import Vue from 'vue';
import crypto from 'crypto';

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;

var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache;
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol$1 = _root.Symbol;

var _Symbol = Symbol$1;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];

var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var Map$1 = _getNative(_root, 'Map');

var _Map = Map$1;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');

var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? (data[key] !== undefined) : hasOwnProperty$3.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (_nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;

var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash,
    'map': new (_Map || _ListCache),
    'string': new _Hash
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;

var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;

var _Stack = Stack;

var defineProperty = (function() {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

/**
 * This function is like `assignValue` except that it doesn't assign
 * `undefined` values.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignMergeValue(object, key, value) {
  if ((value !== undefined && !eq_1(object[key], value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignMergeValue = assignMergeValue;

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

var _createBaseFor = createBaseFor;

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = _createBaseFor();

var _baseFor = baseFor;

var _cloneBuffer = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

module.exports = cloneBuffer;
});

/** Built-in value references. */
var Uint8Array$1 = _root.Uint8Array;

var _Uint8Array = Uint8Array$1;

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new _Uint8Array(result).set(new _Uint8Array(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray;

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject_1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/** Built-in value references. */
var getPrototype = _overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$5;

  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !_isPrototype(object))
    ? _baseCreate(_getPrototype(object))
    : {};
}

var _initCloneObject = initCloneObject;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function() { return arguments; }()) ? _baseIsArguments : function(value) {
  return isObjectLike_1(value) && hasOwnProperty$4.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

var isArguments_1 = isArguments;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

var isLength_1 = isLength;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject(value) {
  return isObjectLike_1(value) && isArrayLike_1(value);
}

var isArrayLikeObject_1 = isArrayLikeObject;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse_1;

module.exports = isBuffer;
});

/** `Object#toString` result references. */
var objectTag = '[object Object]';

/** Used for built-in method references. */
var funcProto$2 = Function.prototype,
    objectProto$7 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString$2.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject(value) {
  if (!isObjectLike_1(value) || _baseGetTag(value) != objectTag) {
    return false;
  }
  var proto = _getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$5.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString$2.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    objectTag$1 = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
typedArrayTags[errorTag] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag] = typedArrayTags[numberTag] =
typedArrayTags[objectTag$1] = typedArrayTags[regexpTag] =
typedArrayTags[setTag] = typedArrayTags[stringTag] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) &&
    isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && _freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;

var isTypedArray_1 = isTypedArray;

/**
 * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function safeGet(object, key) {
  if (key === 'constructor' && typeof object[key] === 'function') {
    return;
  }

  if (key == '__proto__') {
    return;
  }

  return object[key];
}

var _safeGet = safeGet;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$8.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$6.call(object, key) && eq_1(objValue, value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      _baseAssignValue(object, key, newValue);
    } else {
      _assignValue(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$7.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           _isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject_1(object)) {
    return _nativeKeysIn(object);
  }
  var isProto = _isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$8.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object, true) : _baseKeysIn(object);
}

var keysIn_1 = keysIn;

/**
 * Converts `value` to a plain object flattening inherited enumerable string
 * keyed properties of `value` to own properties of the plain object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {Object} Returns the converted plain object.
 * @example
 *
 * function Foo() {
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.assign({ 'a': 1 }, new Foo);
 * // => { 'a': 1, 'b': 2 }
 *
 * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
 * // => { 'a': 1, 'b': 2, 'c': 3 }
 */
function toPlainObject(value) {
  return _copyObject(value, keysIn_1(value));
}

var toPlainObject_1 = toPlainObject;

/**
 * A specialized version of `baseMerge` for arrays and objects which performs
 * deep merges and tracks traversed objects enabling objects with circular
 * references to be merged.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {string} key The key of the value to merge.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} mergeFunc The function to merge values.
 * @param {Function} [customizer] The function to customize assigned values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
  var objValue = _safeGet(object, key),
      srcValue = _safeGet(source, key),
      stacked = stack.get(srcValue);

  if (stacked) {
    _assignMergeValue(object, key, stacked);
    return;
  }
  var newValue = customizer
    ? customizer(objValue, srcValue, (key + ''), object, source, stack)
    : undefined;

  var isCommon = newValue === undefined;

  if (isCommon) {
    var isArr = isArray_1(srcValue),
        isBuff = !isArr && isBuffer_1(srcValue),
        isTyped = !isArr && !isBuff && isTypedArray_1(srcValue);

    newValue = srcValue;
    if (isArr || isBuff || isTyped) {
      if (isArray_1(objValue)) {
        newValue = objValue;
      }
      else if (isArrayLikeObject_1(objValue)) {
        newValue = _copyArray(objValue);
      }
      else if (isBuff) {
        isCommon = false;
        newValue = _cloneBuffer(srcValue, true);
      }
      else if (isTyped) {
        isCommon = false;
        newValue = _cloneTypedArray(srcValue, true);
      }
      else {
        newValue = [];
      }
    }
    else if (isPlainObject_1(srcValue) || isArguments_1(srcValue)) {
      newValue = objValue;
      if (isArguments_1(objValue)) {
        newValue = toPlainObject_1(objValue);
      }
      else if (!isObject_1(objValue) || isFunction_1(objValue)) {
        newValue = _initCloneObject(srcValue);
      }
    }
    else {
      isCommon = false;
    }
  }
  if (isCommon) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, newValue);
    mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
    stack['delete'](srcValue);
  }
  _assignMergeValue(object, key, newValue);
}

var _baseMergeDeep = baseMergeDeep;

/**
 * The base implementation of `_.merge` without support for multiple sources.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} [customizer] The function to customize merged values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMerge(object, source, srcIndex, customizer, stack) {
  if (object === source) {
    return;
  }
  _baseFor(source, function(srcValue, key) {
    stack || (stack = new _Stack);
    if (isObject_1(srcValue)) {
      _baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
    }
    else {
      var newValue = customizer
        ? customizer(_safeGet(object, key), srcValue, (key + ''), object, source, stack)
        : undefined;

      if (newValue === undefined) {
        newValue = srcValue;
      }
      _assignMergeValue(object, key, newValue);
    }
  }, keysIn_1);
}

var _baseMerge = baseMerge;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

var identity_1 = identity;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

var _apply = apply;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return _apply(func, this, otherArgs);
  };
}

var _overRest = overRest;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

var constant_1 = constant;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !_defineProperty ? identity_1 : function(func, string) {
  return _defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant_1(string),
    'writable': true
  });
};

var _baseSetToString = baseSetToString;

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = _shortOut(_baseSetToString);

var _setToString = setToString;

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return _setToString(_overRest(func, start, identity_1), func + '');
}

var _baseRest = baseRest;

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject_1(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike_1(object) && _isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq_1(object[index], value);
  }
  return false;
}

var _isIterateeCall = isIterateeCall;

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner(assigner) {
  return _baseRest(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && _isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

var _createAssigner = createAssigner;

/**
 * This method is like `_.assign` except that it recursively merges own and
 * inherited enumerable string keyed properties of source objects into the
 * destination object. Source properties that resolve to `undefined` are
 * skipped if a destination value exists. Array and plain object properties
 * are merged recursively. Other objects and value types are overridden by
 * assignment. Source objects are applied from left to right. Subsequent
 * sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 0.5.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = {
 *   'a': [{ 'b': 2 }, { 'd': 4 }]
 * };
 *
 * var other = {
 *   'a': [{ 'c': 3 }, { 'e': 5 }]
 * };
 *
 * _.merge(object, other);
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 */
var merge = _createAssigner(function(object, source, srcIndex) {
  _baseMerge(object, source, srcIndex);
});

var merge_1 = merge;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || _MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = _MapCache;

var memoize_1 = memoize;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize_1(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = _memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}

var toString_1 = toString;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}

var _castPath = castPath;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : _baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get;

/**
 * The base implementation of `_.values` and `_.valuesIn` which creates an
 * array of `object` property values corresponding to the property names
 * of `props`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} props The property names to get values for.
 * @returns {Object} Returns the array of property values.
 */
function baseValues(object, props) {
  return _arrayMap(props, function(key) {
    return object[key];
  });
}

var _baseValues = baseValues;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);

var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$b.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$9.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * Creates an array of the own enumerable string keyed property values of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property values.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.values(new Foo);
 * // => [1, 2] (iteration order is not guaranteed)
 *
 * _.values('hi');
 * // => ['h', 'i']
 */
function values(object) {
  return object == null ? [] : _baseValues(object, keys_1(object));
}

var values_1 = values;

const config = {
  locale: 'en-US'
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

const iconTypes = {
  regular: 'me-icon',
  solid: 'me-icon-s',
  light: 'me-icon-l',
  thin: 'me-icon-t'
};

const variants = ['primary', 'success', 'danger', 'warning'];

var script = {
  name: 'MeIcon',
  props: {
    icon: {
      type: String,
      default: ''
    },
    isInfo: {
      type: Boolean,
      default: false
    },
    toggleOnHover: {
      type: Boolean,
      default: false
    },
    hoverColor: {
      type: String,
      default: ''
    },
    hoverIcon: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: ''
    },
    size: {
      type: [Number, String],
      default: ''
    },
    tooltip: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: '',
      validator: (value) => {
        return [
          'regular',
          'solid',
          'light',
          'thin'
        ].includes(value) || value === ''
      }
    },
    variant: {
      type: String,
      default: '',
      validator: (value) => {
        return [
          'primary',
          'success',
          'danger',
          'warning'
        ].includes(value) || value === ''
      }
    }
  },

  data() {
    return {
      isHover: false
    }
  },

  computed: {
    hoverCursor() {
      return !!this.$listeners.click
    },

    iconStyle() {
      const styles = {};
      styles.color = this.currentIconColor;

      if (this.size) {
        styles.fontSize = this.iconSize;
      }

      return styles
    },

    currentIconColor() {
      if (this.color && !this.isHover) return this.color

      if (this.hoverColor && this.isHover) return this.hoverColor

      if (this.isInfo && this.isHover) return 'var(--me-primary-2)'

      if (this.isInfo && !this.isHover) return 'var(--me-gray-5)'

      return this.color
    },

    variantClass() {
      return this.variant && variants.includes(this.variant) ? `icon-${this.variant}` : ''
    },

    currentIcon() {
      if (this.isInfo && !this.icon) return 'me-icon-s icon-circle-info'

      if ((this.toggleOnHover || this.hoverIcon) && this.isHover) return this.currentHoverIcon

      if (this.type) return [iconTypes[this.type], this.iconName]

      return this.icon
    },

    iconName() {
      return this.icon.split(' ').filter(val => !Object.values(iconTypes).includes(val)).join(' ')
    },

    currentHoverIcon() {
      return this.hoverIcon || [iconTypes.solid, this.iconName]
    },

    iconSize() {
      return typeof this.size === 'number' ? `${this.size}px` : this.size
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "i",
    _vm._g(
      _vm._b(
        {
          directives: [
            {
              name: "me-tooltip",
              rawName: "v-me-tooltip.hover",
              value: _vm.tooltip,
              expression: "tooltip",
              modifiers: { hover: true }
            }
          ],
          class: [
            _vm.currentIcon,
            _vm.variantClass,
            {
              "hover-cursor": _vm.hoverCursor
            }
          ],
          style: _vm.iconStyle,
          on: {
            mouseover: function($event) {
              _vm.isHover = true;
            },
            mouseleave: function($event) {
              _vm.isHover = false;
            }
          }
        },
        "i",
        _vm.$attrs,
        false
      ),
      _vm.$listeners
    ),
    [_vm._t("default")],
    2
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = function (inject) {
    if (!inject) return
    inject("data-v-a13a0512_0", { source: ".icon-primary[data-v-a13a0512] {\n  color: var(--me-primary-2);\n}\n.icon-success[data-v-a13a0512] {\n  color: var(--me-success-2);\n}\n.icon-warning[data-v-a13a0512] {\n  color: var(--me-warning-2);\n}\n.icon-danger[data-v-a13a0512] {\n  color: var(--me-danger-2);\n}\n.hover-cursor[data-v-a13a0512] {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=Icon.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Icon.vue","Icon.vue"],"names":[],"mappings":"AA0JA;EACA,0BAAA;ACzJA;ADwJA;EACA,0BAAA;ACrJA;ADoJA;EACA,0BAAA;ACjJA;ADgJA;EACA,yBAAA;AC7IA;ADiJA;EACA,eAAA;AC9IA;;AAEA,mCAAmC","file":"Icon.vue","sourcesContent":["<template>\n  <i\n    :class=\"[currentIcon, variantClass, {\n      'hover-cursor': hoverCursor\n    }]\"\n    v-bind=\"$attrs\"\n    v-on=\"$listeners\"\n    v-me-tooltip.hover=\"tooltip\"\n    :style=\"iconStyle\"\n    @mouseover=\"isHover = true\"\n    @mouseleave=\"isHover = false\"\n  >\n    <slot />\n  </i>\n</template>\n\n<script>\nconst iconTypes = {\n  regular: 'me-icon',\n  solid: 'me-icon-s',\n  light: 'me-icon-l',\n  thin: 'me-icon-t'\n}\n\nconst variants = ['primary', 'success', 'danger', 'warning']\n\nexport default {\n  name: 'MeIcon',\n  props: {\n    icon: {\n      type: String,\n      default: ''\n    },\n    isInfo: {\n      type: Boolean,\n      default: false\n    },\n    toggleOnHover: {\n      type: Boolean,\n      default: false\n    },\n    hoverColor: {\n      type: String,\n      default: ''\n    },\n    hoverIcon: {\n      type: String,\n      default: ''\n    },\n    color: {\n      type: String,\n      default: ''\n    },\n    size: {\n      type: [Number, String],\n      default: ''\n    },\n    tooltip: {\n      type: String,\n      default: null\n    },\n    type: {\n      type: String,\n      default: '',\n      validator: (value) => {\n        return [\n          'regular',\n          'solid',\n          'light',\n          'thin'\n        ].includes(value) || value === ''\n      }\n    },\n    variant: {\n      type: String,\n      default: '',\n      validator: (value) => {\n        return [\n          'primary',\n          'success',\n          'danger',\n          'warning'\n        ].includes(value) || value === ''\n      }\n    }\n  },\n\n  data() {\n    return {\n      isHover: false\n    }\n  },\n\n  computed: {\n    hoverCursor() {\n      return !!this.$listeners.click\n    },\n\n    iconStyle() {\n      const styles = {}\n      styles.color = this.currentIconColor\n\n      if (this.size) {\n        styles.fontSize = this.iconSize\n      }\n\n      return styles\n    },\n\n    currentIconColor() {\n      if (this.color && !this.isHover) return this.color\n\n      if (this.hoverColor && this.isHover) return this.hoverColor\n\n      if (this.isInfo && this.isHover) return 'var(--me-primary-2)'\n\n      if (this.isInfo && !this.isHover) return 'var(--me-gray-5)'\n\n      return this.color\n    },\n\n    variantClass() {\n      return this.variant && variants.includes(this.variant) ? `icon-${this.variant}` : ''\n    },\n\n    currentIcon() {\n      if (this.isInfo && !this.icon) return 'me-icon-s icon-circle-info'\n\n      if ((this.toggleOnHover || this.hoverIcon) && this.isHover) return this.currentHoverIcon\n\n      if (this.type) return [iconTypes[this.type], this.iconName]\n\n      return this.icon\n    },\n\n    iconName() {\n      return this.icon.split(' ').filter(val => !Object.values(iconTypes).includes(val)).join(' ')\n    },\n\n    currentHoverIcon() {\n      return this.hoverIcon || [iconTypes.solid, this.iconName]\n    },\n\n    iconSize() {\n      return typeof this.size === 'number' ? `${this.size}px` : this.size\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $variantList: 'primary', 'success', 'warning', 'danger';\n\n  @each $variantName in $variantList {\n      .icon-#{$variantName} {\n        color: var(--me-#{$variantName}-2);\n      }\n    }\n\n  .hover-cursor {\n    cursor: pointer;\n  }\n\n</style>\n",".icon-primary {\n  color: var(--me-primary-2);\n}\n\n.icon-success {\n  color: var(--me-success-2);\n}\n\n.icon-warning {\n  color: var(--me-warning-2);\n}\n\n.icon-danger {\n  color: var(--me-danger-2);\n}\n\n.hover-cursor {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=Icon.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__ = "data-v-a13a0512";
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    createInjector,
    undefined,
    undefined
  );

var e=function(){return (e=Object.assign||function(e){for(var t,r=1,s=arguments.length;r<s;r++)for(var a in t=arguments[r])Object.prototype.hasOwnProperty.call(t,a)&&(e[a]=t[a]);return e}).apply(this,arguments)},t={kebab:/-(\w)/g,styleProp:/:(.*)/,styleList:/;(?![^(]*\))/g};function r(e,t){return t?t.toUpperCase():""}function s(e){for(var s,a={},c=0,o=e.split(t.styleList);c<o.length;c++){var n=o[c].split(t.styleProp),i=n[0],l=n[1];(i=i.trim())&&("string"==typeof l&&(l=l.trim()),a[(s=i,s.replace(t.kebab,r))]=l);}return a}function a(){for(var t,r,a={},c=arguments.length;c--;)for(var o=0,n=Object.keys(arguments[c]);o<n.length;o++)switch(t=n[o]){case"class":case"style":case"directives":if(Array.isArray(a[t])||(a[t]=[]),"style"===t){var i=void 0;i=Array.isArray(arguments[c].style)?arguments[c].style:[arguments[c].style];for(var l=0;l<i.length;l++){var y=i[l];"string"==typeof y&&(i[l]=s(y));}arguments[c].style=i;}a[t]=a[t].concat(arguments[c][t]);break;case"staticClass":if(!arguments[c][t])break;void 0===a[t]&&(a[t]=""),a[t]&&(a[t]+=" "),a[t]+=arguments[c][t].trim();break;case"on":case"nativeOn":a[t]||(a[t]={});for(var p=0,f=Object.keys(arguments[c][t]||{});p<f.length;p++)r=f[p],a[t][r]?a[t][r]=[].concat(a[t][r],arguments[c][t][r]):a[t][r]=arguments[c][t][r];break;case"attrs":case"props":case"domProps":case"scopedSlots":case"staticStyle":case"hook":case"transition":a[t]||(a[t]={}),a[t]=e({},arguments[c][t],a[t]);break;case"slot":case"key":case"ref":case"tag":case"show":case"keepAlive":default:a[t]||(a[t]=arguments[c][t]);}return a}

/**
 * Utilities to get information about the current environment
 */
// --- Constants ---
var hasWindowSupport = typeof window !== 'undefined';
var hasDocumentSupport = typeof document !== 'undefined';
var hasNavigatorSupport = typeof navigator !== 'undefined';
var hasPromiseSupport = typeof Promise !== 'undefined';
/* istanbul ignore next: JSDOM always returns false */

var hasMutationObserverSupport = typeof MutationObserver !== 'undefined' || typeof WebKitMutationObserver !== 'undefined' || typeof MozMutationObserver !== 'undefined';
var isBrowser = hasWindowSupport && hasDocumentSupport && hasNavigatorSupport; // Browser type sniffing

var userAgent = isBrowser ? window.navigator.userAgent.toLowerCase() : '';
var isJSDOM = userAgent.indexOf('jsdom') > 0;
var isIE = /msie|trident/.test(userAgent); // Determine if the browser supports the option passive for events

var hasPassiveEventSupport = function () {
  var passiveEventSupported = false;

  if (isBrowser) {
    try {
      var options = {
        get passive() {
          // This function will be called when the browser
          // attempts to access the passive property.

          /* istanbul ignore next: will never be called in JSDOM */
          passiveEventSupported = true;
        }

      };
      window.addEventListener('test', options, options);
      window.removeEventListener('test', options, options);
    } catch (err) {
      /* istanbul ignore next: will never be called in JSDOM */
      passiveEventSupported = false;
    }
  }

  return passiveEventSupported;
}();
var hasTouchSupport = isBrowser && ('ontouchstart' in document.documentElement || navigator.maxTouchPoints > 0);
var hasPointerEventSupport = isBrowser && Boolean(window.PointerEvent || window.MSPointerEvent);

var getEnv = function getEnv(key) {
  var fallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var env = typeof process !== 'undefined' && process ? process.env || {} : {};

  if (!key) {
    /* istanbul ignore next */
    return env;
  }

  return env[key] || fallback;
};
var getNoWarn = function getNoWarn() {
  return getEnv('BOOTSTRAP_VUE_NO_WARN') || getEnv('NODE_ENV') === 'production';
};

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf(subClass, superClass); }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized(self); }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _wrapNativeSuper(Class) { var _cache = typeof Map === "function" ? new Map() : undefined; _wrapNativeSuper = function _wrapNativeSuper(Class) { if (Class === null || !_isNativeFunction(Class)) return Class; if (typeof Class !== "function") { throw new TypeError("Super expression must either be null or a function"); } if (typeof _cache !== "undefined") { if (_cache.has(Class)) return _cache.get(Class); _cache.set(Class, Wrapper); } function Wrapper() { return _construct(Class, arguments, _getPrototypeOf(this).constructor); } Wrapper.prototype = Object.create(Class.prototype, { constructor: { value: Wrapper, enumerable: false, writable: true, configurable: true } }); return _setPrototypeOf(Wrapper, Class); }; return _wrapNativeSuper(Class); }

function _construct(Parent, args, Class) { if (_isNativeReflectConstruct()) { _construct = Reflect.construct; } else { _construct = function _construct(Parent, args, Class) { var a = [null]; a.push.apply(a, args); var Constructor = Function.bind.apply(Parent, a); var instance = new Constructor(); if (Class) _setPrototypeOf(instance, Class.prototype); return instance; }; } return _construct.apply(null, arguments); }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

function _isNativeFunction(fn) { return Function.toString.call(fn).indexOf("[native code]") !== -1; }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }
var w = hasWindowSupport ? window : {};
var Element$1 = hasWindowSupport ? w.Element : /*#__PURE__*/function (_Object) {
  _inherits(Element, _Object);

  var _super = _createSuper(Element);

  function Element() {
    _classCallCheck(this, Element);

    return _super.apply(this, arguments);
  }

  return Element;
}( /*#__PURE__*/_wrapNativeSuper(Object));
var HTMLElement = hasWindowSupport ? w.HTMLElement : /*#__PURE__*/function (_Element) {
  _inherits(HTMLElement, _Element);

  var _super2 = _createSuper(HTMLElement);

  function HTMLElement() {
    _classCallCheck(this, HTMLElement);

    return _super2.apply(this, arguments);
  }

  return HTMLElement;
}(Element$1);
var SVGElement = hasWindowSupport ? w.SVGElement : /*#__PURE__*/function (_Element2) {
  _inherits(SVGElement, _Element2);

  var _super3 = _createSuper(SVGElement);

  function SVGElement() {
    _classCallCheck(this, SVGElement);

    return _super3.apply(this, arguments);
  }

  return SVGElement;
}(Element$1);
var File$1 = hasWindowSupport ? w.File : /*#__PURE__*/function (_Object2) {
  _inherits(File, _Object2);

  var _super4 = _createSuper(File);

  function File() {
    _classCallCheck(this, File);

    return _super4.apply(this, arguments);
  }

  return File;
}( /*#__PURE__*/_wrapNativeSuper(Object));

function _typeof$1(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$1 = function _typeof(obj) { return typeof obj; }; } else { _typeof$1 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$1(obj); }

var toType = function toType(val) {
  return _typeof$1(val);
};
var toRawType = function toRawType(val) {
  return Object.prototype.toString.call(val).slice(8, -1);
};
var isUndefined = function isUndefined(val) {
  return val === undefined;
};
var isNull = function isNull(val) {
  return val === null;
};
var isUndefinedOrNull = function isUndefinedOrNull(val) {
  return isUndefined(val) || isNull(val);
};
var isFunction$1 = function isFunction(val) {
  return toType(val) === 'function';
};
var isBoolean = function isBoolean(val) {
  return toType(val) === 'boolean';
};
var isString = function isString(val) {
  return toType(val) === 'string';
};
var isNumber = function isNumber(val) {
  return toType(val) === 'number';
}; // Is a value number like (i.e. a number or a number as string)

var isNumeric = function isNumeric(value) {
  return !isNaN(parseInt(value, 10));
};
var isArray$1 = function isArray(val) {
  return Array.isArray(val);
}; // Quick object check
// This is primarily used to tell Objects from primitive values
// when we know the value is a JSON-compliant type
// Note object could be a complex type like array, Date, etc.

var isObject$1 = function isObject(obj) {
  return obj !== null && _typeof$1(obj) === 'object';
}; // Strict object type check
// Only returns true for plain JavaScript objects

var isPlainObject$1 = function isPlainObject(obj) {
  return Object.prototype.toString.call(obj) === '[object Object]';
};
var isDate = function isDate(val) {
  return val instanceof Date;
};
var isEvent = function isEvent(val) {
  return val instanceof Event;
};
var isFile = function isFile(val) {
  return val instanceof File$1;
};
var isRegExp = function isRegExp(val) {
  return toRawType(val) === 'RegExp';
};
var isPromise = function isPromise(val) {
  return !isUndefinedOrNull(val) && isFunction$1(val.then) && isFunction$1(val.catch);
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var assign = function assign() {
  return Object.assign.apply(Object, arguments);
};
var create = function create(proto, optionalProps) {
  return Object.create(proto, optionalProps);
};
var defineProperties = function defineProperties(obj, props) {
  return Object.defineProperties(obj, props);
};
var defineProperty$1 = function defineProperty(obj, prop, descriptor) {
  return Object.defineProperty(obj, prop, descriptor);
};
var freeze = function freeze(obj) {
  return Object.freeze(obj);
};
var keys$1 = function keys(obj) {
  return Object.keys(obj);
}; // --- "Instance" ---

var hasOwnProperty$a = function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
};

/**
 * Shallow copy an object. If the passed in object
 * is null or undefined, returns an empty object
 */

var clone = function clone(obj) {
  return _objectSpread({}, obj);
};
/**
 * Return a shallow copy of object with the specified properties only
 * @link https://gist.github.com/bisubus/2da8af7e801ffd813fab7ac221aa7afc
 */

var pick = function pick(obj, props) {
  return keys$1(obj).filter(function (key) {
    return props.indexOf(key) !== -1;
  }).reduce(function (result, key) {
    return _objectSpread(_objectSpread({}, result), {}, _defineProperty$1({}, key, obj[key]));
  }, {});
};
/**
 * Return a shallow copy of object with the specified properties omitted
 * @link https://gist.github.com/bisubus/2da8af7e801ffd813fab7ac221aa7afc
 */

var omit = function omit(obj, props) {
  return keys$1(obj).filter(function (key) {
    return props.indexOf(key) === -1;
  }).reduce(function (result, key) {
    return _objectSpread(_objectSpread({}, result), {}, _defineProperty$1({}, key, obj[key]));
  }, {});
};
/**
 * Merges two object deeply together
 * @link https://gist.github.com/Salakar/1d7137de9cb8b704e48a
 */

var mergeDeep = function mergeDeep(target, source) {
  if (isObject$1(target) && isObject$1(source)) {
    keys$1(source).forEach(function (key) {
      if (isObject$1(source[key])) {
        if (!target[key] || !isObject$1(target[key])) {
          target[key] = source[key];
        }

        mergeDeep(target[key], source[key]);
      } else {
        assign(target, _defineProperty$1({}, key, source[key]));
      }
    });
  }

  return target;
};
/**
 * Convenience method to create a read-only descriptor
 */

var readonlyDescriptor = function readonlyDescriptor() {
  return {
    enumerable: true,
    configurable: false,
    writable: false
  };
};
/**
 * Deep-freezes and object, making it immutable / read-only
 * Returns the same object passed-in, but frozen
 * Freezes inner object/array/values first
 * Note: This method will not work for property values using `Symbol()` as a key
 * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
 */

var deepFreeze = function deepFreeze(obj) {
  // Retrieve the property names defined on object/array
  // Note: `keys` will ignore properties that are keyed by a `Symbol()`
  var props = keys$1(obj); // Iterate over each prop and recursively freeze it

  props.forEach(function (prop) {
    var value = obj[prop]; // If value is a plain object or array, we deepFreeze it

    obj[prop] = value && (isPlainObject$1(value) || isArray$1(value)) ? deepFreeze(value) : value;
  });
  return freeze(obj);
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$2(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
var cloneDeep = function cloneDeep(obj) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : obj;

  if (isArray$1(obj)) {
    return obj.reduce(function (result, val) {
      return [].concat(_toConsumableArray(result), [cloneDeep(val, val)]);
    }, []);
  }

  if (isPlainObject$1(obj)) {
    return keys$1(obj).reduce(function (result, key) {
      return _objectSpread$1(_objectSpread$1({}, result), {}, _defineProperty$2({}, key, cloneDeep(obj[key], obj[key])));
    }, {});
  }

  return defaultValue;
};

// Loose YYYY-MM-DD matching, ignores any appended time inforation
var RX_ARRAY_NOTATION = /\[(\d+)]/g;
var RX_DIGITS = /^\d+$/;
var RX_EXTENSION = /^\..+/;
var RX_HASH = /^#/;
var RX_HASH_ID = /^#[A-Za-z]+[\w\-:.]*$/;
var RX_HTML_TAGS = /(<([^>]+)>)/gi;
var RX_HYPHENATE = /\B([A-Z])/g;
var RX_LOWER_UPPER = /([a-z])([A-Z])/g;
var RX_NUMBER = /^[0-9]*\.?[0-9]+$/;
var RX_REGEXP_REPLACE = /[-/\\^$*+?.()|[\]{}]/g;
var RX_SPACES = /[\s\uFEFF\xA0]+/g;
var RX_SPACE_SPLIT = /\s+/;
var RX_STAR = /\/\*$/;
var RX_START_SPACE_WORD = /(\s|^)(\w)/g;
var RX_TRIM_LEFT = /^\s+/;
var RX_UNDERSCORE = /_/g;
var RX_UN_KEBAB = /-(\w)/g; // Aspect

var RX_COL_CLASS = /^col-/; // Icon

var RX_ICON_PREFIX = /^BIcon/; // Locale

var identity$1 = function identity(x) {
  return x;
};

/**
 * Get property defined by dot/array notation in string, returns undefined if not found
 *
 * @link https://gist.github.com/jeneg/9767afdcca45601ea44930ea03e0febf#gistcomment-1935901
 *
 * @param {Object} obj
 * @param {string|Array} path
 * @return {*}
 */

var getRaw = function getRaw(obj, path) {
  var defaultValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : undefined;
  // Handle array of path values
  path = isArray$1(path) ? path.join('.') : path; // If no path or no object passed

  if (!path || !isObject$1(obj)) {
    return defaultValue;
  } // Handle edge case where user has dot(s) in top-level item field key
  // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2762
  // Switched to `in` operator vs `hasOwnProperty` to handle obj.prototype getters
  // https://github.com/bootstrap-vue/bootstrap-vue/issues/3463


  if (path in obj) {
    return obj[path];
  } // Handle string array notation (numeric indices only)


  path = String(path).replace(RX_ARRAY_NOTATION, '.$1');
  var steps = path.split('.').filter(identity$1); // Handle case where someone passes a string of only dots

  if (steps.length === 0) {
    return defaultValue;
  } // Traverse path in object to find result
  // Switched to `in` operator vs `hasOwnProperty` to handle obj.prototype getters
  // https://github.com/bootstrap-vue/bootstrap-vue/issues/3463


  return steps.every(function (step) {
    return isObject$1(obj) && step in obj && !isUndefinedOrNull(obj = obj[step]);
  }) ? obj : isNull(obj) ? null : defaultValue;
};
/**
 * Get property defined by dot/array notation in string.
 *
 * @link https://gist.github.com/jeneg/9767afdcca45601ea44930ea03e0febf#gistcomment-1935901
 *
 * @param {Object} obj
 * @param {string|Array} path
 * @param {*} defaultValue (optional)
 * @return {*}
 */

var get$1 = function get(obj, path) {
  var defaultValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var val = getRaw(obj, path);
  return isUndefinedOrNull(val) ? defaultValue : val;
};

/**
 * Log a warning message to the console with BootstrapVue formatting
 * @param {string} message
 */

var warn = function warn(message)
/* istanbul ignore next */
{
  var source = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

  if (!getNoWarn()) {
    console.warn("[BootstrapVue warn]: ".concat(source ? "".concat(source, " - ") : '').concat(message));
  }
};
/**
 * Warn when no MutationObserver support is given
 * @param {string} source
 * @returns {boolean} warned
 */

var warnNoMutationObserverSupport = function warnNoMutationObserverSupport(source) {
  /* istanbul ignore else */
  if (hasMutationObserverSupport) {
    return false;
  } else {
    warn("".concat(source, ": Requires MutationObserver support."));
    return true;
  }
};

var NAME_ALERT = 'BAlert';
var NAME_AVATAR = 'BAvatar';
var NAME_BADGE = 'BBadge';
var NAME_BUTTON = 'BButton';
var NAME_BUTTON_CLOSE = 'BButtonClose';
var NAME_CALENDAR = 'BCalendar';
var NAME_CARD_SUB_TITLE = 'BCardSubTitle';
var NAME_CAROUSEL = 'BCarousel';
var NAME_CAROUSEL_SLIDE = 'BCarouselSlide';
var NAME_COL = 'BCol';
var NAME_DROPDOWN = 'BDropdown';
var NAME_DROPDOWN_DIVIDER = 'BDropdownDivider';
var NAME_DROPDOWN_GROUP = 'BDropdownGroup';
var NAME_DROPDOWN_ITEM = 'BDropdownItem';
var NAME_FORM_CHECKBOX = 'BFormCheckbox';
var NAME_FORM_DATEPICKER = 'BFormDatepicker';
var NAME_FORM_FILE = 'BFormFile';
var NAME_FORM_GROUP = 'BFormGroup';
var NAME_FORM_INPUT = 'BFormInput';
var NAME_FORM_INVALID_FEEDBACK = 'BFormInvalidFeedback';
var NAME_FORM_RADIO = 'BFormRadio';
var NAME_FORM_RADIO_GROUP = 'BFormRadioGroup';
var NAME_FORM_RATING = 'BFormRating';
var NAME_FORM_ROW = 'BFormRow';
var NAME_FORM_SPINBUTTON = 'BFormSpinbutton';
var NAME_FORM_TAG = 'BFormTag';
var NAME_FORM_TAGS = 'BFormTags';
var NAME_FORM_TEXT = 'BFormText';
var NAME_FORM_TEXTAREA = 'BFormTextarea';
var NAME_FORM_TIMEPICKER = 'BFormTimepicker';
var NAME_FORM_VALID_FEEDBACK = 'BFormValidFeedback';
var NAME_ICON = 'BIcon';
var NAME_ICON_BASE = 'BIconBase';
var NAME_IMG = 'BImg';
var NAME_IMG_LAZY = 'BImgLazy';
var NAME_INPUT_GROUP = 'BInputGroup';
var NAME_INPUT_GROUP_ADDON = 'BInputGroupAddon';
var NAME_INPUT_GROUP_APPEND = 'BInputGroupAppend';
var NAME_INPUT_GROUP_PREPEND = 'BInputGroupPrepend';
var NAME_INPUT_GROUP_TEXT = 'BInputGroupText';
var NAME_JUMBOTRON = 'BJumbotron';
var NAME_LINK = 'BLink';
var NAME_LIST_GROUP_ITEM = 'BListGroupItem';
var NAME_MODAL = 'BModal';
var NAME_NAV = 'BNav';
var NAME_NAVBAR = 'BNavbar';
var NAME_NAVBAR_TOGGLE = 'BNavbarToggle';
var NAME_OVERLAY = 'BOverlay';
var NAME_PAGINATION = 'BPagination';
var NAME_PAGINATION_NAV = 'BPaginationNav';
var NAME_POPOVER = 'BPopover';
var NAME_POPOVER_HELPER = 'BVPopover';
var NAME_POPOVER_TEMPLATE = 'BVPopoverTemplate';
var NAME_POPPER = 'BVPopper';
var NAME_PROGRESS = 'BProgress';
var NAME_PROGRESS_BAR = 'BProgressBar';
var NAME_SIDEBAR = 'BSidebar';
var NAME_SKELETON = 'BSkeleton';
var NAME_SKELETON_ICON = 'BSkeletonIcon';
var NAME_SKELETON_TABLE = 'BSkeletonTable';
var NAME_SPINNER = 'BSpinner';
var NAME_TAB = 'BTab';
var NAME_TABLE = 'BTable';
var NAME_TABLE_CELL = 'BTableCell';
var NAME_TABLE_SIMPLE = 'BTableSimple';
var NAME_TABS = 'BTabs';
var NAME_TAB_BUTTON_HELPER = 'BVTabButton';
var NAME_TBODY = 'BTbody';
var NAME_TFOOT = 'BTfoot';
var NAME_TH = 'BTh';
var NAME_THEAD = 'BThead';
var NAME_TIME = 'BTime';
var NAME_TOAST = 'BToast';
var NAME_TOASTER = 'BToaster';
var NAME_TOOLTIP = 'BTooltip';
var NAME_TOOLTIP_HELPER = 'BVTooltip';
var NAME_TOOLTIP_TEMPLATE = 'BVTooltipTemplate';
var NAME_TR = 'BTr';
var NAME_TRANSITION = 'BVTransition';
var NAME_TRANSPORTER_SINGLE = 'BTransporterSingle';
var NAME_TRANSPORTER_TARGET_SINGLE = 'BTransporterTargetSingle';

var _deepFreeze;

function _defineProperty$3(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// NOTES
//
// The global config SHALL NOT be used to set defaults for Boolean props, as the props
// would loose their semantic meaning, and force people writing 3rd party components to
// explicitly set a true or false value using the v-bind syntax on boolean props
//
// Supported config values (depending on the prop's supported type(s)):
// `String`, `Array`, `Object`, `null` or `undefined`
// BREAKPOINT DEFINITIONS
//
// Some components (`<b-col>` and `<b-form-group>`) generate props based on breakpoints,
// and this occurs when the component is first loaded (evaluated), which may happen
// before the config is created/modified
//
// To get around this we make these components' props async (lazy evaluation)
// The component definition is only called/executed when the first access to the
// component is used (and cached on subsequent uses)
// PROP DEFAULTS
//
// For default values on props, we use the default value factory function approach so
// that the default values are pulled in at each component instantiation
//
//  props: {
//    variant: {
//      type: String,
//      default: () => getConfigComponent('BAlert', 'variant')
//    }
//  }
//
// We also provide a cached getter for breakpoints, which are "frozen" on first access
// prettier-ignore

var DEFAULTS = deepFreeze((_deepFreeze = {
  // Breakpoints
  breakpoints: ['xs', 'sm', 'md', 'lg', 'xl'],
  // Form controls
  formControls: {
    size: undefined
  }
}, _defineProperty$3(_deepFreeze, NAME_ALERT, {
  dismissLabel: 'Close',
  variant: 'info'
}), _defineProperty$3(_deepFreeze, NAME_AVATAR, {
  variant: 'secondary',
  badgeVariant: 'primary'
}), _defineProperty$3(_deepFreeze, NAME_BADGE, {
  variant: 'secondary'
}), _defineProperty$3(_deepFreeze, NAME_BUTTON, {
  size: undefined,
  variant: 'secondary'
}), _defineProperty$3(_deepFreeze, NAME_BUTTON_CLOSE, {
  content: '&times;',
  // `textVariant` is `undefined` to inherit the current text color
  textVariant: undefined,
  ariaLabel: 'Close'
}), _defineProperty$3(_deepFreeze, NAME_CALENDAR, {
  selectedVariant: 'primary',
  // Defaults to `selectedVariant`
  todayVariant: undefined,
  navButtonVariant: 'secondary',
  // BFormDate will choose these first if not provided in BFormDate section
  labelPrevDecade: 'Previous decade',
  labelPrevYear: 'Previous year',
  labelPrevMonth: 'Previous month',
  labelCurrentMonth: 'Current month',
  labelNextMonth: 'Next month',
  labelNextYear: 'Next year',
  labelNextDecade: 'Next decade',
  labelToday: 'Today',
  labelSelected: 'Selected date',
  labelNoDateSelected: 'No date selected',
  labelCalendar: 'Calendar',
  labelNav: 'Calendar navigation',
  labelHelp: 'Use cursor keys to navigate calendar dates'
}), _defineProperty$3(_deepFreeze, NAME_CARD_SUB_TITLE, {
  // `<b-card>` and `<b-card-body>` also inherit this prop
  subTitleTextVariant: 'muted'
}), _defineProperty$3(_deepFreeze, NAME_CAROUSEL, {
  labelPrev: 'Previous Slide',
  labelNext: 'Next Slide',
  labelGotoSlide: 'Goto Slide',
  labelIndicators: 'Select a slide to display'
}), _defineProperty$3(_deepFreeze, NAME_DROPDOWN, {
  toggleText: 'Toggle Dropdown',
  size: undefined,
  variant: 'secondary',
  splitVariant: undefined
}), _defineProperty$3(_deepFreeze, NAME_FORM_DATEPICKER, {
  // BFormDatepicker will choose from BCalendar first if not provided here
  selectedVariant: undefined,
  todayVariant: undefined,
  navButtonVariant: undefined,
  labelPrevDecade: undefined,
  labelPrevYear: undefined,
  labelPrevMonth: undefined,
  labelCurrentMonth: undefined,
  labelNextMonth: undefined,
  labelNextYear: undefined,
  labelNextDecade: undefined,
  labelToday: undefined,
  labelSelected: undefined,
  labelNoDateSelected: undefined,
  labelCalendar: undefined,
  labelNav: undefined,
  labelHelp: undefined,
  // These props are specific to BFormDatepicker
  labelTodayButton: 'Select today',
  labelResetButton: 'Reset',
  labelCloseButton: 'Close'
}), _defineProperty$3(_deepFreeze, NAME_FORM_FILE, {
  browseText: 'Browse',
  // Chrome default file prompt
  placeholder: 'No file chosen',
  dropPlaceholder: 'Drop files here',
  noDropPlaceholder: 'Not allowed'
}), _defineProperty$3(_deepFreeze, NAME_FORM_RATING, {
  variant: null,
  color: null
}), _defineProperty$3(_deepFreeze, NAME_FORM_TAG, {
  removeLabel: 'Remove tag',
  variant: 'secondary'
}), _defineProperty$3(_deepFreeze, NAME_FORM_TAGS, {
  addButtonText: 'Add',
  addButtonVariant: 'outline-secondary',
  duplicateTagText: 'Duplicate tag(s)',
  invalidTagText: 'Invalid tag(s)',
  limitTagsText: 'Tag limit reached',
  placeholder: 'Add tag...',
  tagRemoveLabel: 'Remove tag',
  tagRemovedLabel: 'Tag removed',
  tagVariant: 'secondary'
}), _defineProperty$3(_deepFreeze, NAME_FORM_TEXT, {
  textVariant: 'muted'
}), _defineProperty$3(_deepFreeze, NAME_FORM_TIMEPICKER, {
  // Fallback to BTime
  labelNoTimeSelected: undefined,
  labelSelected: undefined,
  labelHours: undefined,
  labelMinutes: undefined,
  labelSeconds: undefined,
  labelAmpm: undefined,
  labelAm: undefined,
  labelPm: undefined,
  // Fallback to BTime then BFormSpinbutton
  labelDecrement: undefined,
  labelIncrement: undefined,
  // These props are specific to BFormTimepicker
  labelNowButton: 'Select now',
  labelResetButton: 'Reset',
  labelCloseButton: 'Close'
}), _defineProperty$3(_deepFreeze, NAME_FORM_SPINBUTTON, {
  labelDecrement: 'Decrement',
  labelIncrement: 'Increment'
}), _defineProperty$3(_deepFreeze, NAME_IMG, {
  blankColor: 'transparent'
}), _defineProperty$3(_deepFreeze, NAME_IMG_LAZY, {
  blankColor: 'transparent'
}), _defineProperty$3(_deepFreeze, NAME_INPUT_GROUP, {
  size: undefined
}), _defineProperty$3(_deepFreeze, NAME_JUMBOTRON, {
  bgVariant: undefined,
  borderVariant: undefined,
  textVariant: undefined
}), _defineProperty$3(_deepFreeze, NAME_LINK, {
  routerComponentName: undefined
}), _defineProperty$3(_deepFreeze, NAME_LIST_GROUP_ITEM, {
  variant: undefined
}), _defineProperty$3(_deepFreeze, NAME_MODAL, {
  titleTag: 'h5',
  size: 'md',
  headerBgVariant: undefined,
  headerBorderVariant: undefined,
  headerTextVariant: undefined,
  headerCloseVariant: undefined,
  bodyBgVariant: undefined,
  bodyTextVariant: undefined,
  footerBgVariant: undefined,
  footerBorderVariant: undefined,
  footerTextVariant: undefined,
  cancelTitle: 'Cancel',
  cancelVariant: 'secondary',
  okTitle: 'OK',
  okVariant: 'primary',
  headerCloseContent: '&times;',
  headerCloseLabel: 'Close'
}), _defineProperty$3(_deepFreeze, NAME_NAVBAR, {
  variant: null
}), _defineProperty$3(_deepFreeze, NAME_NAVBAR_TOGGLE, {
  label: 'Toggle navigation'
}), _defineProperty$3(_deepFreeze, NAME_PAGINATION, {
  size: undefined
}), _defineProperty$3(_deepFreeze, NAME_PAGINATION_NAV, {
  size: undefined
}), _defineProperty$3(_deepFreeze, NAME_POPOVER, {
  boundary: 'scrollParent',
  boundaryPadding: 5,
  customClass: undefined,
  delay: 50,
  variant: undefined
}), _defineProperty$3(_deepFreeze, NAME_PROGRESS, {
  variant: undefined
}), _defineProperty$3(_deepFreeze, NAME_PROGRESS_BAR, {
  variant: undefined
}), _defineProperty$3(_deepFreeze, NAME_SPINNER, {
  variant: undefined
}), _defineProperty$3(_deepFreeze, NAME_SKELETON, {
  animation: 'wave'
}), _defineProperty$3(_deepFreeze, NAME_SKELETON_ICON, {
  animation: 'wave'
}), _defineProperty$3(_deepFreeze, NAME_SIDEBAR, {
  bgVariant: 'light',
  textVariant: 'dark',
  shadow: false,
  width: undefined,
  tag: 'div',
  backdropVariant: 'dark'
}), _defineProperty$3(_deepFreeze, NAME_TABLE, {
  selectedVariant: 'active',
  headVariant: undefined,
  footVariant: undefined
}), _defineProperty$3(_deepFreeze, NAME_TIME, {
  labelNoTimeSelected: 'No time selected',
  labelSelected: 'Selected time',
  labelHours: 'Hours',
  labelMinutes: 'Minutes',
  labelSeconds: 'Seconds',
  labelAmpm: 'AM/PM',
  // It would be nice to be able to get these from Intl.DateTimeFormat somehow
  labelAm: 'AM',
  labelPm: 'PM',
  // The following inherit from BFormSpinbutton if not provided
  labelIncrement: undefined,
  labelDecrement: undefined
}), _defineProperty$3(_deepFreeze, NAME_TOAST, {
  toaster: 'b-toaster-top-right',
  autoHideDelay: 5000,
  variant: undefined,
  toastClass: undefined,
  headerClass: undefined,
  bodyClass: undefined
}), _defineProperty$3(_deepFreeze, NAME_TOASTER, {
  ariaLive: undefined,
  ariaAtomic: undefined,
  role: undefined
}), _defineProperty$3(_deepFreeze, NAME_TOOLTIP, {
  boundary: 'scrollParent',
  boundaryPadding: 5,
  customClass: undefined,
  delay: 50,
  variant: undefined
}), _deepFreeze));

var memoize$1 = function memoize(fn) {
  var cache = create(null);
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    var argsKey = JSON.stringify(args);
    return cache[argsKey] = cache[argsKey] || fn.apply(null, args);
  };
};

var PROP_NAME = '$bvConfig';
var VueProto = Vue.prototype; // --- Getter methods ---

var getConfigValue = function getConfigValue(key) {
  return VueProto[PROP_NAME] ? VueProto[PROP_NAME].getConfigValue(key) : cloneDeep(getRaw(DEFAULTS, key));
}; // Method to grab a config value for a particular component

var getComponentConfig = function getComponentConfig(cmpName) {
  var key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  // Return the particular config value for key for if specified,
  // otherwise we return the full config (or an empty object if not found)
  return key ? getConfigValue("".concat(cmpName, ".").concat(key)) : getConfigValue(cmpName) || {};
}; // Convenience method for getting all breakpoint names

var getBreakpoints = function getBreakpoints() {
  return getConfigValue('breakpoints');
}; // Private function for caching / locking-in breakpoint names

var _getBreakpointsCached = memoize$1(function () {
  return getBreakpoints();
}); // Convenience method for getting all breakpoint names.
// Caches the results after first access.


var getBreakpointsCached = function getBreakpointsCached() {
  return cloneDeep(_getBreakpointsCached());
}; // Convenience method for getting breakpoints with
// the smallest breakpoint set as ''.
// Useful for components that create breakpoint specific props.
// Caches the results after first access.

var getBreakpointsUpCached = memoize$1(function () {
  var breakpoints = getBreakpointsCached();
  breakpoints[0] = '';
  return breakpoints;
}); // Convenience method for getting breakpoints with

// --- Static ---
var from = function from() {
  return Array.from.apply(Array, arguments);
}; // --- Instance ---

var arrayIncludes = function arrayIncludes(array, value) {
  return array.indexOf(value) !== -1;
};
var concat = function concat() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return Array.prototype.concat.apply([], args);
}; // --- Utilities ---

var createAndFillArray = function createAndFillArray(size, value) {
  return Array(size).fill(value);
};
var flatten = function flatten(array) {
  return array.reduce(function (result, item) {
    return result.concat(item);
  }, []);
};
var flattenDeep = function flattenDeep(array) {
  return array.reduce(function (result, item) {
    return result.concat(Array.isArray(item) ? flattenDeep(item) : item);
  }, []);
};

// Number utilities
// Converts a value (string, number, etc.) to an integer number
// Assumes radix base 10
var toInteger = function toInteger(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : NaN;
  var integer = parseInt(value, 10);
  return isNaN(integer) ? defaultValue : integer;
}; // Converts a value (string, number, etc.) to a number

var toFloat = function toFloat(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : NaN;
  var float = parseFloat(value);
  return isNaN(float) ? defaultValue : float;
}; // Converts a value (string, number, etc.) to a string

// String utilities
// Converts PascalCase or camelCase to kebab-case

var kebabCase = function kebabCase(str) {
  return str.replace(RX_HYPHENATE, '-$1').toLowerCase();
}; // Converts a kebab-case or camelCase string to PascalCase

var pascalCase = function pascalCase(str) {
  str = kebabCase(str).replace(RX_UN_KEBAB, function (_, c) {
    return c ? c.toUpperCase() : '';
  });
  return str.charAt(0).toUpperCase() + str.slice(1);
}; // Converts a string, including strings in camelCase or snake_case, into Start Case
// It keeps original single quote and hyphen in the word
// https://github.com/UrbanCompass/to-start-case

var startCase = function startCase(str) {
  return str.replace(RX_UNDERSCORE, ' ').replace(RX_LOWER_UPPER, function (str, $1, $2) {
    return $1 + ' ' + $2;
  }).replace(RX_START_SPACE_WORD, function (str, $1, $2) {
    return $1 + $2.toUpperCase();
  });
}; // Lowercases the first letter of a string and returns a new string

var lowerFirst = function lowerFirst(str) {
  str = isString(str) ? str.trim() : String(str);
  return str.charAt(0).toLowerCase() + str.slice(1);
}; // Uppercases the first letter of a string and returns a new string

var upperFirst = function upperFirst(str) {
  str = isString(str) ? str.trim() : String(str);
  return str.charAt(0).toUpperCase() + str.slice(1);
}; // Escape characters to be used in building a regular expression

var escapeRegExp = function escapeRegExp(str) {
  return str.replace(RX_REGEXP_REPLACE, '\\$&');
}; // Convert a value to a string that can be rendered
// `undefined`/`null` will be converted to `''`
// Plain objects and arrays will be JSON stringified

var toString$1 = function toString(val) {
  var spaces = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
  return isUndefinedOrNull(val) ? '' : isArray$1(val) || isPlainObject$1(val) && val.toString === Object.prototype.toString ? JSON.stringify(val, null, spaces) : String(val);
}; // Remove leading white space from a string

var trimLeft = function trimLeft(str) {
  return toString$1(str).replace(RX_TRIM_LEFT, '');
}; // Remove Trailing white space from a string

var trim = function trim(str) {
  return toString$1(str).trim();
}; // Lower case a string

var lowerCase = function lowerCase(str) {
  return toString$1(str).toLowerCase();
}; // Upper case a string

var TABABLE_SELECTOR = ['button', '[href]:not(.disabled)', 'input', 'select', 'textarea', '[tabindex]', '[contenteditable]'].map(function (s) {
  return "".concat(s, ":not(:disabled):not([disabled])");
}).join(', ');
var w$1 = hasWindowSupport ? window : {};
var d = hasDocumentSupport ? document : {};
var elProto = typeof Element !== 'undefined' ? Element.prototype : {}; // --- Normalization utils ---
// See: https://developer.mozilla.org/en-US/docs/Web/API/Element/matches#Polyfill

/* istanbul ignore next */

var matchesEl = elProto.matches || elProto.msMatchesSelector || elProto.webkitMatchesSelector; // See: https://developer.mozilla.org/en-US/docs/Web/API/Element/closest

/* istanbul ignore next */

var closestEl = elProto.closest || function (sel)
/* istanbul ignore next */
{
  var el = this;

  do {
    // Use our "patched" matches function
    if (matches(el, sel)) {
      return el;
    }

    el = el.parentElement || el.parentNode;
  } while (!isNull(el) && el.nodeType === Node.ELEMENT_NODE);

  return null;
}; // `requestAnimationFrame()` convenience method

/* istanbul ignore next: JSDOM always returns the first option */

var requestAF = w$1.requestAnimationFrame || w$1.webkitRequestAnimationFrame || w$1.mozRequestAnimationFrame || w$1.msRequestAnimationFrame || w$1.oRequestAnimationFrame || // Fallback, but not a true polyfill
// Only needed for Opera Mini

/* istanbul ignore next */
function (cb) {
  return setTimeout(cb, 16);
};
var MutationObs = w$1.MutationObserver || w$1.WebKitMutationObserver || w$1.MozMutationObserver || null; // --- Utils ---
// Remove a node from DOM

var removeNode = function removeNode(el) {
  return el && el.parentNode && el.parentNode.removeChild(el);
}; // Determine if an element is an HTML element

var isElement = function isElement(el) {
  return !!(el && el.nodeType === Node.ELEMENT_NODE);
}; // Get the currently active HTML element

var getActiveElement = function getActiveElement() {
  var excludes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var activeElement = d.activeElement;
  return activeElement && !excludes.some(function (el) {
    return el === activeElement;
  }) ? activeElement : null;
}; // Returns `true` if a tag's name equals `name`

var isTag = function isTag(tag, name) {
  return toString$1(tag).toLowerCase() === toString$1(name).toLowerCase();
}; // Determine if an HTML element is the currently active element

var isActiveElement = function isActiveElement(el) {
  return isElement(el) && el === getActiveElement();
}; // Determine if an HTML element is visible - Faster than CSS check

var isVisible = function isVisible(el) {
  if (!isElement(el) || !el.parentNode || !contains(d.body, el)) {
    // Note this can fail for shadow dom elements since they
    // are not a direct descendant of document.body
    return false;
  }

  if (getStyle(el, 'display') === 'none') {
    // We do this check to help with vue-test-utils when using v-show

    /* istanbul ignore next */
    return false;
  } // All browsers support getBoundingClientRect(), except JSDOM as it returns all 0's for values :(
  // So any tests that need isVisible will fail in JSDOM
  // Except when we override the getBCR prototype in some tests


  var bcr = getBCR(el);
  return !!(bcr && bcr.height > 0 && bcr.width > 0);
}; // Determine if an element is disabled

var isDisabled = function isDisabled(el) {
  return !isElement(el) || el.disabled || hasAttr(el, 'disabled') || hasClass(el, 'disabled');
}; // Cause/wait-for an element to reflow its content (adjusting its height/width)

var reflow = function reflow(el) {
  // Requesting an elements offsetHight will trigger a reflow of the element content

  /* istanbul ignore next: reflow doesn't happen in JSDOM */
  return isElement(el) && el.offsetHeight;
}; // Select all elements matching selector. Returns `[]` if none found

var selectAll = function selectAll(selector, root) {
  return from((isElement(root) ? root : d).querySelectorAll(selector));
}; // Select a single element, returns `null` if not found

var select = function select(selector, root) {
  return (isElement(root) ? root : d).querySelector(selector) || null;
}; // Determine if an element matches a selector

var matches = function matches(el, selector) {
  return isElement(el) ? matchesEl.call(el, selector) : false;
}; // Finds closest element matching selector. Returns `null` if not found

var closest = function closest(selector, root) {
  var includeRoot = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  if (!isElement(root)) {
    return null;
  }

  var el = closestEl.call(root, selector); // Native closest behaviour when `includeRoot` is truthy,
  // else emulate jQuery closest and return `null` if match is
  // the passed in root element when `includeRoot` is falsey

  return includeRoot ? el : el === root ? null : el;
}; // Returns true if the parent element contains the child element

var contains = function contains(parent, child) {
  return parent && isFunction$1(parent.contains) ? parent.contains(child) : false;
}; // Get an element given an ID

var getById = function getById(id) {
  return d.getElementById(/^#/.test(id) ? id.slice(1) : id) || null;
}; // Add a class to an element

var addClass = function addClass(el, className) {
  // We are checking for `el.classList` existence here since IE 11
  // returns `undefined` for some elements (e.g. SVG elements)
  // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2713
  if (className && isElement(el) && el.classList) {
    el.classList.add(className);
  }
}; // Remove a class from an element

var removeClass = function removeClass(el, className) {
  // We are checking for `el.classList` existence here since IE 11
  // returns `undefined` for some elements (e.g. SVG elements)
  // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2713
  if (className && isElement(el) && el.classList) {
    el.classList.remove(className);
  }
}; // Test if an element has a class

var hasClass = function hasClass(el, className) {
  // We are checking for `el.classList` existence here since IE 11
  // returns `undefined` for some elements (e.g. SVG elements)
  // See https://github.com/bootstrap-vue/bootstrap-vue/issues/2713
  if (className && isElement(el) && el.classList) {
    return el.classList.contains(className);
  }

  return false;
}; // Set an attribute on an element

var setAttr = function setAttr(el, attr, value) {
  if (attr && isElement(el)) {
    el.setAttribute(attr, value);
  }
}; // Remove an attribute from an element

var removeAttr = function removeAttr(el, attr) {
  if (attr && isElement(el)) {
    el.removeAttribute(attr);
  }
}; // Get an attribute value from an element
// Returns `null` if not found

var getAttr = function getAttr(el, attr) {
  return attr && isElement(el) ? el.getAttribute(attr) : null;
}; // Determine if an attribute exists on an element
// Returns `true` or `false`, or `null` if element not found

var hasAttr = function hasAttr(el, attr) {
  return attr && isElement(el) ? el.hasAttribute(attr) : null;
}; // Set an style property on an element

var setStyle = function setStyle(el, prop, value) {
  if (prop && isElement(el)) {
    el.style[prop] = value;
  }
}; // Remove an style property from an element

var removeStyle = function removeStyle(el, prop) {
  if (prop && isElement(el)) {
    el.style[prop] = '';
  }
}; // Get an style property value from an element
// Returns `null` if not found

var getStyle = function getStyle(el, prop) {
  return prop && isElement(el) ? el.style[prop] || null : null;
}; // Return the Bounding Client Rect of an element
// Returns `null` if not an element

/* istanbul ignore next: getBoundingClientRect() doesn't work in JSDOM */

var getBCR = function getBCR(el) {
  return isElement(el) ? el.getBoundingClientRect() : null;
}; // Get computed style object for an element

/* istanbul ignore next: getComputedStyle() doesn't work in JSDOM */

var getCS = function getCS(el) {
  return hasWindowSupport && isElement(el) ? w$1.getComputedStyle(el) : {};
}; // Returns a `Selection` object representing the range of text selected
// Returns `null` if no window support is given

/* istanbul ignore next: getSelection() doesn't work in JSDOM */

var getSel = function getSel() {
  return hasWindowSupport && w$1.getSelection ? w$1.getSelection() : null;
}; // Return an element's offset with respect to document element
// Assumes users have not used `tabindex` > `0` on elements

var getTabables = function getTabables() {
  var rootEl = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  return selectAll(TABABLE_SELECTOR, rootEl).filter(isVisible).filter(function (el) {
    return el.tabIndex > -1 && !el.disabled;
  });
}; // Attempt to focus an element, and return `true` if successful

var attemptFocus = function attemptFocus(el) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  try {
    el.focus(options);
  } catch (_unused) {}

  return isActiveElement(el);
}; // Attempt to blur an element, and return `true` if successful

var attemptBlur = function attemptBlur(el) {
  try {
    el.blur();
  } catch (_unused2) {}

  return !isActiveElement(el);
};

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty$4(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$4(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var NO_FADE_PROPS = {
  name: '',
  enterClass: '',
  enterActiveClass: '',
  enterToClass: 'show',
  leaveClass: 'show',
  leaveActiveClass: '',
  leaveToClass: ''
};

var FADE_PROPS = _objectSpread$2(_objectSpread$2({}, NO_FADE_PROPS), {}, {
  enterActiveClass: 'fade',
  leaveActiveClass: 'fade'
}); // @vue/component


var BVTransition = /*#__PURE__*/Vue.extend({
  name: NAME_TRANSITION,
  functional: true,
  props: {
    noFade: {
      // Only applicable to the built in transition
      // Has no effect if `trans-props` provided
      type: Boolean,
      default: false
    },
    appear: {
      // Has no effect if `trans-props` provided
      type: Boolean,
      default: false
    },
    mode: {
      // Can be overridden by user supplied trans-props
      type: String // default: undefined

    },
    // For user supplied transitions (if needed)
    transProps: {
      type: Object,
      default: null
    }
  },
  render: function render(h, _ref) {
    var children = _ref.children,
        data = _ref.data,
        props = _ref.props;
    var transProps = props.transProps;

    if (!isPlainObject$1(transProps)) {
      transProps = props.noFade ? NO_FADE_PROPS : FADE_PROPS;

      if (props.appear) {
        // Default the appear classes to equal the enter classes
        transProps = _objectSpread$2(_objectSpread$2({}, transProps), {}, {
          appear: true,
          appearClass: transProps.enterClass,
          appearActiveClass: transProps.enterActiveClass,
          appearToClass: transProps.enterToClass
        });
      }
    }

    transProps = _objectSpread$2(_objectSpread$2({
      mode: props.mode
    }, transProps), {}, {
      // We always need `css` true
      css: true
    });
    return h('transition', // Any transition event listeners will get merged here
    a(data, {
      props: transProps
    }), children);
  }
});

var SLOT_NAME_APPEND = 'append';
var SLOT_NAME_DEFAULT = 'default';
var SLOT_NAME_DESCRIPTION = 'description';
var SLOT_NAME_FIRST = 'first';
var SLOT_NAME_FOOTER = 'footer';
var SLOT_NAME_HEADER = 'header';
var SLOT_NAME_LABEL = 'label';
var SLOT_NAME_PREPEND = 'prepend';
var SLOT_NAME_TITLE = 'title';

// In functional components, `slots` is a function so it must be called
// first before passing to the below methods. `scopedSlots` is always an
// object and may be undefined (for Vue < 2.6.x)

/**
 * Returns true if either scoped or unscoped named slot exists
 *
 * @param {String, Array} name or name[]
 * @param {Object} scopedSlots
 * @param {Object} slots
 * @returns {Array|undefined} VNodes
 */

var hasNormalizedSlot = function hasNormalizedSlot(names) {
  var $scopedSlots = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var $slots = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // Ensure names is an array
  names = concat(names).filter(identity$1); // Returns true if the either a $scopedSlot or $slot exists with the specified name

  return names.some(function (name) {
    return $scopedSlots[name] || $slots[name];
  });
};
/**
 * Returns VNodes for named slot either scoped or unscoped
 *
 * @param {String, Array} name or name[]
 * @param {String} scope
 * @param {Object} scopedSlots
 * @param {Object} slots
 * @returns {Array|undefined} VNodes
 */


var normalizeSlot = function normalizeSlot(names) {
  var scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var $scopedSlots = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var $slots = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  // Ensure names is an array
  names = concat(names).filter(identity$1);
  var slot;

  for (var i = 0; i < names.length && !slot; i++) {
    var name = names[i];
    slot = $scopedSlots[name] || $slots[name];
  } // Note: in Vue 2.6.x, all named slots are also scoped slots


  return isFunction$1(slot) ? slot(scope) : slot;
}; // Named exports

var normalizeSlotMixin = {
  methods: {
    hasNormalizedSlot: function hasNormalizedSlot$1() {
      var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : SLOT_NAME_DEFAULT;
      // Returns true if the either a $scopedSlot or $slot exists with the specified name
      // `name` can be a string name or an array of names
      return hasNormalizedSlot(name, this.$scopedSlots, this.$slots);
    },
    normalizeSlot: function normalizeSlot$1() {
      var name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : SLOT_NAME_DEFAULT;
      var scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      // Returns an array of rendered VNodes if slot found.
      // Returns undefined if not found.
      // `name` can be a string name or an array of names
      var vNodes = normalizeSlot(name, scope, this.$scopedSlots, this.$slots);

      return vNodes ? concat(vNodes) : vNodes;
    }
  }
};

// Normalize event options based on support of passive option
// Exported only for testing purposes

var parseEventOptions = function parseEventOptions(options) {
  /* istanbul ignore else: can't test in JSDOM, as it supports passive */
  if (hasPassiveEventSupport) {
    return isObject$1(options) ? options : {
      capture: !!options || false
    };
  } else {
    // Need to translate to actual Boolean value
    return !!(isObject$1(options) ? options.capture : options);
  }
}; // Attach an event listener to an element

var eventOn = function eventOn(el, evtName, handler, options) {
  if (el && el.addEventListener) {
    el.addEventListener(evtName, handler, parseEventOptions(options));
  }
}; // Remove an event listener from an element

var eventOff = function eventOff(el, evtName, handler, options) {
  if (el && el.removeEventListener) {
    el.removeEventListener(evtName, handler, parseEventOptions(options));
  }
}; // Utility method to add/remove a event listener based on first argument (boolean)
// It passes all other arguments to the `eventOn()` or `eventOff` method

var eventOnOff = function eventOnOff(on) {
  var method = on ? eventOn : eventOff;

  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  method.apply(void 0, args);
}; // Utility method to prevent the default event handling and propagation

var stopEvent = function stopEvent(evt) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$preventDefault = _ref.preventDefault,
      preventDefault = _ref$preventDefault === void 0 ? true : _ref$preventDefault,
      _ref$propagation = _ref.propagation,
      propagation = _ref$propagation === void 0 ? true : _ref$propagation,
      _ref$immediatePropaga = _ref.immediatePropagation,
      immediatePropagation = _ref$immediatePropaga === void 0 ? false : _ref$immediatePropaga;

  if (preventDefault) {
    evt.preventDefault();
  }

  if (propagation) {
    evt.stopPropagation();
  }

  if (immediatePropagation) {
    evt.stopImmediatePropagation();
  }
};

function _defineProperty$5(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props = {
  content: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_BUTTON_CLOSE, 'content');
    }
  },
  disabled: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_BUTTON_CLOSE, 'ariaLabel');
    }
  },
  textVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_BUTTON_CLOSE, 'textVariant');
    }
  }
}; // @vue/component

var BButtonClose = /*#__PURE__*/Vue.extend({
  name: NAME_BUTTON_CLOSE,
  functional: true,
  props: props,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        slots = _ref.slots,
        scopedSlots = _ref.scopedSlots;
    var $slots = slots();
    var $scopedSlots = scopedSlots || {};
    var componentData = {
      staticClass: 'close',
      class: _defineProperty$5({}, "text-".concat(props.textVariant), props.textVariant),
      attrs: {
        type: 'button',
        disabled: props.disabled,
        'aria-label': props.ariaLabel ? String(props.ariaLabel) : null
      },
      on: {
        click: function click(evt) {
          // Ensure click on button HTML content is also disabled

          /* istanbul ignore if: bug in JSDOM still emits click on inner element */
          if (props.disabled && isEvent(evt)) {
            stopEvent(evt);
          }
        }
      }
    }; // Careful not to override the default slot with innerHTML

    if (!hasNormalizedSlot(SLOT_NAME_DEFAULT, $scopedSlots, $slots)) {
      componentData.domProps = {
        innerHTML: props.content
      };
    }

    return h('button', a(data, componentData), normalizeSlot(SLOT_NAME_DEFAULT, {}, $scopedSlots, $slots));
  }
});

function _defineProperty$6(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var parseCountDown = function parseCountDown(show) {
  if (show === '' || isBoolean(show)) {
    return 0;
  }

  show = toInteger(show, 0);
  return show > 0 ? show : 0;
}; // Convert `show` value to a boolean


var parseShow = function parseShow(show) {
  if (show === '' || show === true) {
    return true;
  }

  if (toInteger(show, 0) < 1) {
    // Boolean will always return false for the above comparison
    return false;
  }

  return !!show;
}; // @vue/component


var BAlert = /*#__PURE__*/Vue.extend({
  name: NAME_ALERT,
  mixins: [normalizeSlotMixin],
  model: {
    prop: 'show',
    event: 'input'
  },
  props: {
    variant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_ALERT, 'variant');
      }
    },
    dismissible: {
      type: Boolean,
      default: false
    },
    dismissLabel: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_ALERT, 'dismissLabel');
      }
    },
    show: {
      type: [Boolean, Number, String],
      default: false
    },
    fade: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      countDown: 0,
      countDownTimeout: null,
      // If initially shown, we need to set these for SSR
      localShow: parseShow(this.show)
    };
  },
  watch: {
    show: function show(newVal) {
      this.countDown = parseCountDown(newVal);
      this.localShow = parseShow(newVal);
    },
    countDown: function countDown(newVal) {
      var _this = this;

      this.clearCountDownInterval();

      if (isNumeric(this.show)) {
        // Ignore if this.show transitions to a boolean value.
        this.$emit('dismiss-count-down', newVal);

        if (this.show !== newVal) {
          // Update the v-model if needed
          this.$emit('input', newVal);
        }

        if (newVal > 0) {
          this.localShow = true;
          this.countDownTimeout = setTimeout(function () {
            _this.countDown--;
          }, 1000);
        } else {
          // Slightly delay the hide to allow any UI updates
          this.$nextTick(function () {
            requestAF(function () {
              _this.localShow = false;
            });
          });
        }
      }
    },
    localShow: function localShow(newVal) {
      if (!newVal && (this.dismissible || isNumeric(this.show))) {
        // Only emit dismissed events for dismissible or auto dismissing alerts
        this.$emit('dismissed');
      }

      if (!isNumeric(this.show) && this.show !== newVal) {
        // Only emit booleans if we weren't passed a number via `this.show`
        this.$emit('input', newVal);
      }
    }
  },
  created: function created() {
    this.countDown = parseCountDown(this.show);
    this.localShow = parseShow(this.show);
  },
  mounted: function mounted() {
    this.countDown = parseCountDown(this.show);
    this.localShow = parseShow(this.show);
  },
  beforeDestroy: function beforeDestroy() {
    this.clearCountDownInterval();
  },
  methods: {
    dismiss: function dismiss() {
      this.clearCountDownInterval();
      this.countDown = 0;
      this.localShow = false;
    },
    clearCountDownInterval: function clearCountDownInterval() {
      if (this.countDownTimeout) {
        clearTimeout(this.countDownTimeout);
        this.countDownTimeout = null;
      }
    }
  },
  render: function render(h) {
    var $alert; // undefined

    if (this.localShow) {
      var $dismissBtn = h();

      if (this.dismissible) {
        // Add dismiss button
        $dismissBtn = h(BButtonClose, {
          attrs: {
            'aria-label': this.dismissLabel
          },
          on: {
            click: this.dismiss
          }
        }, [this.normalizeSlot('dismiss')]);
      }

      $alert = h('div', {
        key: this._uid,
        staticClass: 'alert',
        class: _defineProperty$6({
          'alert-dismissible': this.dismissible
        }, "alert-".concat(this.variant), this.variant),
        attrs: {
          role: 'alert',
          'aria-live': 'polite',
          'aria-atomic': true
        }
      }, [$dismissBtn, this.normalizeSlot()]);
      $alert = [$alert];
    }

    return h(BVTransition, {
      props: {
        noFade: !this.fade
      }
    }, $alert);
  }
});

// Math utilty functions
var mathMin = Math.min;
var mathMax = Math.max;
var mathAbs = Math.abs;
var mathCeil = Math.ceil;
var mathFloor = Math.floor;

var unprefixPropName = function unprefixPropName(prefix, value) {
  return lowerFirst(value.replace(prefix, ''));
}; // Suffix can be a falsey value so nothing is appended to string
// (helps when looping over props & some shouldn't change)
// Use data last parameters to allow for currying

var suffixPropName = function suffixPropName(suffix, str) {
  return str + (suffix ? upperFirst(suffix) : '');
}; // Copies props from one array/object to a new array/object
// plucks all the values off the target object, returning a new object
// that has props that reference the original prop values

var pluckProps = function pluckProps(keysToPluck, objToPluck) {
  var transformFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : identity$1;
  return (isArray$1(keysToPluck) ? keysToPluck.slice() : keys$1(keysToPluck)).reduce(function (memo, prop) {
    memo[transformFn(prop)] = objToPluck[prop];
    return memo;
  }, {});
};

var ANCHOR_TAG = 'a'; // Precompile RegExp

var commaRE = /%2C/g;
var encodeReserveRE = /[!'()*]/g;

var encodeReserveReplacer = function encodeReserveReplacer(c) {
  return '%' + c.charCodeAt(0).toString(16);
}; // Fixed encodeURIComponent which is more conformant to RFC3986:
// - escapes [!'()*]
// - preserve commas


var encode = function encode(str) {
  return encodeURIComponent(toString$1(str)).replace(encodeReserveRE, encodeReserveReplacer).replace(commaRE, ',');
};
// See: https://github.com/vuejs/vue-router/blob/dev/src/util/query.js

var stringifyQueryObj = function stringifyQueryObj(obj) {
  if (!isPlainObject$1(obj)) {
    return '';
  }

  var query = keys$1(obj).map(function (key) {
    var val = obj[key];

    if (isUndefined(val)) {
      return '';
    } else if (isNull(val)) {
      return encode(key);
    } else if (isArray$1(val)) {
      return val.reduce(function (results, val2) {
        if (isNull(val2)) {
          results.push(encode(key));
        } else if (!isUndefined(val2)) {
          // Faster than string interpolation
          results.push(encode(key) + '=' + encode(val2));
        }

        return results;
      }, []).join('&');
    } // Faster than string interpolation


    return encode(key) + '=' + encode(val);
  })
  /* must check for length, as we only want to filter empty strings, not things that look falsey! */
  .filter(function (x) {
    return x.length > 0;
  }).join('&');
  return query ? "?".concat(query) : '';
};
var isLink = function isLink(props) {
  return !!(props.href || props.to);
};
var isRouterLink = function isRouterLink(tag) {
  return !isTag(tag, ANCHOR_TAG);
};
var computeTag = function computeTag() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      to = _ref.to,
      disabled = _ref.disabled,
      routerComponentName = _ref.routerComponentName;

  var thisOrParent = arguments.length > 1 ? arguments[1] : undefined;
  var hasRouter = thisOrParent.$router;

  if (!hasRouter || hasRouter && disabled || hasRouter && !to) {
    return ANCHOR_TAG;
  } // TODO:
  //   Check registered components for existence of user supplied router link component name
  //   We would need to check PascalCase, kebab-case, and camelCase versions of name:
  //   const name = routerComponentName
  //   const names = [name, PascalCase(name), KebabCase(name), CamelCase(name)]
  //   exists = names.some(name => !!thisOrParent.$options.components[name])
  //   And may want to cache the result for performance or we just let the render fail
  //   if the component is not registered


  return routerComponentName || (thisOrParent.$nuxt ? 'nuxt-link' : 'router-link');
};
var computeRel = function computeRel() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      target = _ref2.target,
      rel = _ref2.rel;

  if (target === '_blank' && isNull(rel)) {
    return 'noopener';
  }

  return rel || null;
};
var computeHref = function computeHref() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      href = _ref3.href,
      to = _ref3.to;

  var tag = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ANCHOR_TAG;
  var fallback = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '#';
  var toFallback = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '/';

  // We've already checked the $router in computeTag(), so isRouterLink() indicates a live router.
  // When deferring to Vue Router's router-link, don't use the href attribute at all.
  // We return null, and then remove href from the attributes passed to router-link
  if (isRouterLink(tag)) {
    return null;
  } // Return `href` when explicitly provided


  if (href) {
    return href;
  } // Reconstruct `href` when `to` used, but no router


  if (to) {
    // Fallback to `to` prop (if `to` is a string)
    if (isString(to)) {
      return to || toFallback;
    } // Fallback to `to.path + to.query + to.hash` prop (if `to` is an object)


    if (isPlainObject$1(to) && (to.path || to.query || to.hash)) {
      var path = toString$1(to.path);
      var query = stringifyQueryObj(to.query);
      var hash = toString$1(to.hash);
      hash = !hash || hash.charAt(0) === '#' ? hash : "#".concat(hash);
      return "".concat(path).concat(query).concat(hash) || toFallback;
    }
  } // If nothing is provided return the fallback


  return fallback;
};

var CODE_BACKSPACE = 8;
var CODE_DELETE = 46;
var CODE_DOWN = 40;
var CODE_END = 35;
var CODE_ENTER = 13;
var CODE_ESC = 27;
var CODE_HOME = 36;
var CODE_LEFT = 37;
var CODE_RIGHT = 39;
var CODE_SPACE = 32;
var CODE_UP = 38;

// Handles when arrays are "sparse" (array.every(...) doesn't handle sparse)

var compareArrays = function compareArrays(a, b) {
  if (a.length !== b.length) {
    return false;
  }

  var equal = true;

  for (var i = 0; equal && i < a.length; i++) {
    equal = looseEqual(a[i], b[i]);
  }

  return equal;
};
/**
 * Check if two values are loosely equal - that is,
 * if they are plain objects, do they have the same shape?
 * Returns boolean true or false
 */


var looseEqual = function looseEqual(a, b) {
  if (a === b) {
    return true;
  }

  var aValidType = isDate(a);
  var bValidType = isDate(b);

  if (aValidType || bValidType) {
    return aValidType && bValidType ? a.getTime() === b.getTime() : false;
  }

  aValidType = isArray$1(a);
  bValidType = isArray$1(b);

  if (aValidType || bValidType) {
    return aValidType && bValidType ? compareArrays(a, b) : false;
  }

  aValidType = isObject$1(a);
  bValidType = isObject$1(b);

  if (aValidType || bValidType) {
    /* istanbul ignore if: this if will probably never be called */
    if (!aValidType || !bValidType) {
      return false;
    }

    var aKeysCount = keys$1(a).length;
    var bKeysCount = keys$1(b).length;

    if (aKeysCount !== bKeysCount) {
      return false;
    }

    for (var key in a) {
      var aHasKey = hasOwnProperty$a(a, key);
      var bHasKey = hasOwnProperty$a(b, key);

      if (aHasKey && !bHasKey || !aHasKey && bHasKey || !looseEqual(a[key], b[key])) {
        return false;
      }
    }
  }

  return String(a) === String(b);
};

function _defineProperty$7(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var isEmpty = function isEmpty(value) {
  return !value || keys$1(value).length === 0;
};

var makePropWatcher = function makePropWatcher(propName) {
  return {
    handler: function handler(newValue, oldValue) {
      if (looseEqual(newValue, oldValue)) {
        return;
      }

      if (isEmpty(newValue) || isEmpty(oldValue)) {
        this[propName] = cloneDeep(newValue);
        return;
      }

      for (var key in oldValue) {
        if (!hasOwnProperty$a(newValue, key)) {
          this.$delete(this.$data[propName], key);
        }
      }

      for (var _key in newValue) {
        this.$set(this.$data[propName], _key, newValue[_key]);
      }
    }
  };
};
var makePropCacheMixin = function makePropCacheMixin(propName, proxyPropName) {
  return {
    data: function data() {
      return _defineProperty$7({}, proxyPropName, cloneDeep(this[propName]));
    },
    watch: _defineProperty$7({}, propName, makePropWatcher(proxyPropName))
  };
};

var attrsMixin = makePropCacheMixin('$attrs', 'bvAttrs');

var listenersMixin = makePropCacheMixin('$listeners', 'bvListeners');

function _toConsumableArray$1(arr) { return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$1(arr) || _nonIterableSpread$1(); }

function _nonIterableSpread$1() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$1(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$1(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen); }

function _iterableToArray$1(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles$1(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$1(arr); }

function _arrayLikeToArray$1(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$3(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$3(Object(source), true).forEach(function (key) { _defineProperty$8(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$3(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$8(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// <router-link> specific props

var routerLinkProps = {
  to: {
    type: [String, Object],
    default: null
  },
  append: {
    type: Boolean,
    default: false
  },
  replace: {
    type: Boolean,
    default: false
  },
  event: {
    type: [String, Array],
    default: 'click'
  },
  activeClass: {
    type: String // default: undefined

  },
  exact: {
    type: Boolean,
    default: false
  },
  exactActiveClass: {
    type: String // default: undefined

  },
  routerTag: {
    type: String,
    default: 'a'
  }
}; // <nuxt-link> specific props

var nuxtLinkProps = {
  prefetch: {
    type: Boolean,
    // Must be `null` to fall back to the value defined in the
    // `nuxt.config.js` configuration file for `router.prefetchLinks`
    // We convert `null` to `undefined`, so that Nuxt.js will use the
    // compiled default. Vue treats `undefined` as default of `false`
    // for Boolean props, so we must set it as `null` here to be a
    // true tri-state prop
    default: null
  },
  noPrefetch: {
    type: Boolean,
    default: false
  }
};
var props$1 = _objectSpread$3(_objectSpread$3(_objectSpread$3({
  href: {
    type: String,
    default: null
  },
  rel: {
    type: String,
    // Must be `null` if no value provided
    default: null
  },
  target: {
    type: String,
    default: '_self'
  },
  active: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
}, routerLinkProps), nuxtLinkProps), {}, {
  // To support 3rd party router links based on `<router-link>` (i.e. `g-link` for Gridsome)
  // Default is to auto choose between `<router-link>` and `<nuxt-link>`
  // Gridsome doesn't provide a mechanism to auto detect and has caveats
  // such as not supporting FQDN URLs or hash only URLs
  routerComponentName: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_LINK, 'routerComponentName');
    }
  }
}); // --- Main component ---
// @vue/component

var BLink = /*#__PURE__*/Vue.extend({
  name: NAME_LINK,
  // Mixin order is important!
  mixins: [attrsMixin, listenersMixin, normalizeSlotMixin],
  inheritAttrs: false,
  props: props$1,
  computed: {
    computedTag: function computedTag() {
      // We don't pass `this` as the first arg as we need reactivity of the props
      var to = this.to,
          disabled = this.disabled,
          routerComponentName = this.routerComponentName;
      return computeTag({
        to: to,
        disabled: disabled,
        routerComponentName: routerComponentName
      }, this);
    },
    isRouterLink: function isRouterLink$1() {
      return isRouterLink(this.computedTag);
    },
    computedRel: function computedRel() {
      // We don't pass `this` as the first arg as we need reactivity of the props
      return computeRel({
        target: this.target,
        rel: this.rel
      });
    },
    computedHref: function computedHref() {
      // We don't pass `this` as the first arg as we need reactivity of the props
      return computeHref({
        to: this.to,
        href: this.href
      }, this.computedTag);
    },
    computedProps: function computedProps() {
      var prefetch = this.prefetch;
      return this.isRouterLink ? _objectSpread$3(_objectSpread$3({}, pluckProps(_objectSpread$3(_objectSpread$3({}, routerLinkProps), nuxtLinkProps), this)), {}, {
        // Coerce `prefetch` value `null` to be `undefined`
        prefetch: isBoolean(prefetch) ? prefetch : undefined,
        // Pass `router-tag` as `tag` prop
        tag: this.routerTag
      }) : {};
    },
    computedAttrs: function computedAttrs() {
      var bvAttrs = this.bvAttrs,
          href = this.computedHref,
          rel = this.computedRel,
          disabled = this.disabled,
          target = this.target,
          routerTag = this.routerTag,
          isRouterLink = this.isRouterLink;
      return _objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3({}, bvAttrs), href ? {
        href: href
      } : {}), isRouterLink && routerTag !== 'a' && routerTag !== 'area' ? {} : {
        rel: rel,
        target: target
      }), {}, {
        tabindex: disabled ? '-1' : isUndefined(bvAttrs.tabindex) ? null : bvAttrs.tabindex,
        'aria-disabled': disabled ? 'true' : null
      });
    },
    computedListeners: function computedListeners() {
      return _objectSpread$3(_objectSpread$3({}, this.bvListeners), {}, {
        // We want to overwrite any click handler since our callback
        // will invoke the user supplied handler(s) if `!this.disabled`
        click: this.onClick
      });
    }
  },
  methods: {
    onClick: function onClick(evt) {
      var _arguments = arguments;
      var evtIsEvent = isEvent(evt);
      var isRouterLink = this.isRouterLink;
      var suppliedHandler = this.bvListeners.click;

      if (evtIsEvent && this.disabled) {
        // Stop event from bubbling up
        // Kill the event loop attached to this specific `EventTarget`
        // Needed to prevent `vue-router` for doing its thing
        stopEvent(evt, {
          immediatePropagation: true
        });
      } else {
        /* istanbul ignore next: difficult to test, but we know it works */
        if (isRouterLink && evt.currentTarget.__vue__) {
          // Router links do not emit instance `click` events, so we
          // add in an `$emit('click', evt)` on its Vue instance
          evt.currentTarget.__vue__.$emit('click', evt);
        } // Call the suppliedHandler(s), if any provided


        concat(suppliedHandler).filter(function (h) {
          return isFunction$1(h);
        }).forEach(function (handler) {
          handler.apply(void 0, _toConsumableArray$1(_arguments));
        }); // Emit the global `$root` click event

        this.$root.$emit('clicked::link', evt);
      } // Stop scroll-to-top behavior or navigation on
      // regular links when href is just '#'


      if (evtIsEvent && !isRouterLink && this.computedHref === '#') {
        stopEvent(evt, {
          propagation: false
        });
      }
    },
    focus: function focus() {
      attemptFocus(this.$el);
    },
    blur: function blur() {
      attemptBlur(this.$el);
    }
  },
  render: function render(h) {
    var active = this.active,
        disabled = this.disabled;
    return h(this.computedTag, _defineProperty$8({
      class: {
        active: active,
        disabled: disabled
      },
      attrs: this.computedAttrs,
      props: this.computedProps
    }, this.isRouterLink ? 'nativeOn' : 'on', this.computedListeners), this.normalizeSlot());
  }
});

function ownKeys$4(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$4(Object(source), true).forEach(function (key) { _defineProperty$9(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$4(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$9(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var linkProps = omit(props$1, ['event', 'routerTag']);
delete linkProps.href.default;
delete linkProps.to.default;
var btnProps = {
  block: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_BUTTON, 'size');
    }
  },
  variant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_BUTTON, 'variant');
    }
  },
  type: {
    type: String,
    default: 'button'
  },
  tag: {
    type: String,
    default: 'button'
  },
  pill: {
    type: Boolean,
    default: false
  },
  squared: {
    type: Boolean,
    default: false
  },
  pressed: {
    // Tri-state: `true`, `false` or `null`
    // => On, off, not a toggle
    type: Boolean,
    default: null
  }
};
var props$2 = _objectSpread$4(_objectSpread$4({}, btnProps), linkProps); // --- Helper methods ---
// Focus handler for toggle buttons
// Needs class of 'focus' when focused

var handleFocus = function handleFocus(evt) {
  if (evt.type === 'focusin') {
    addClass(evt.target, 'focus');
  } else if (evt.type === 'focusout') {
    removeClass(evt.target, 'focus');
  }
}; // Is the requested button a link?
// If tag prop is set to `a`, we use a <b-link> to get proper disabled handling


var isLink$1 = function isLink$1(props) {
  return isLink(props) || isTag(props.tag, 'a');
}; // Is the button to be a toggle button?


var isToggle = function isToggle(props) {
  return isBoolean(props.pressed);
}; // Is the button "really" a button?


var isButton = function isButton(props) {
  return !(isLink$1(props) || props.tag && !isTag(props.tag, 'button'));
}; // Is the requested tag not a button or link?


var isNonStandardTag = function isNonStandardTag(props) {
  return !isLink$1(props) && !isButton(props);
}; // Compute required classes (non static classes)


var computeClass = function computeClass(props) {
  var _ref;

  return ["btn-".concat(props.variant || getComponentConfig(NAME_BUTTON, 'variant')), (_ref = {}, _defineProperty$9(_ref, "btn-".concat(props.size), props.size), _defineProperty$9(_ref, 'btn-block', props.block), _defineProperty$9(_ref, 'rounded-pill', props.pill), _defineProperty$9(_ref, 'rounded-0', props.squared && !props.pill), _defineProperty$9(_ref, "disabled", props.disabled), _defineProperty$9(_ref, "active", props.pressed), _ref)];
}; // Compute the link props to pass to b-link (if required)


var computeLinkProps = function computeLinkProps(props) {
  return isLink$1(props) ? pluckProps(linkProps, props) : {};
}; // Compute the attributes for a button


var computeAttrs = function computeAttrs(props, data) {
  var button = isButton(props);
  var link = isLink$1(props);
  var toggle = isToggle(props);
  var nonStandardTag = isNonStandardTag(props);
  var hashLink = link && props.href === '#';
  var role = data.attrs && data.attrs.role ? data.attrs.role : null;
  var tabindex = data.attrs ? data.attrs.tabindex : null;

  if (nonStandardTag || hashLink) {
    tabindex = '0';
  }

  return {
    // Type only used for "real" buttons
    type: button && !link ? props.type : null,
    // Disabled only set on "real" buttons
    disabled: button ? props.disabled : null,
    // We add a role of button when the tag is not a link or button for ARIA
    // Don't bork any role provided in `data.attrs` when `isLink` or `isButton`
    // Except when link has `href` of `#`
    role: nonStandardTag || hashLink ? 'button' : role,
    // We set the `aria-disabled` state for non-standard tags
    'aria-disabled': nonStandardTag ? String(props.disabled) : null,
    // For toggles, we need to set the pressed state for ARIA
    'aria-pressed': toggle ? String(props.pressed) : null,
    // `autocomplete="off"` is needed in toggle mode to prevent some browsers
    // from remembering the previous setting when using the back button
    autocomplete: toggle ? 'off' : null,
    // `tabindex` is used when the component is not a button
    // Links are tabbable, but don't allow disabled, while non buttons or links
    // are not tabbable, so we mimic that functionality by disabling tabbing
    // when disabled, and adding a `tabindex="0"` to non buttons or non links
    tabindex: props.disabled && !button ? '-1' : tabindex
  };
}; // --- Main component ---
// @vue/component


var BButton = /*#__PURE__*/Vue.extend({
  name: NAME_BUTTON,
  functional: true,
  props: props$2,
  render: function render(h, _ref2) {
    var props = _ref2.props,
        data = _ref2.data,
        listeners = _ref2.listeners,
        children = _ref2.children;
    var toggle = isToggle(props);
    var link = isLink$1(props);
    var nonStandardTag = isNonStandardTag(props);
    var hashLink = link && props.href === '#';
    var on = {
      keydown: function keydown(evt) {
        // When the link is a `href="#"` or a non-standard tag (has `role="button"`),
        // we add a keydown handlers for CODE_SPACE/CODE_ENTER

        /* istanbul ignore next */
        if (props.disabled || !(nonStandardTag || hashLink)) {
          return;
        }

        var keyCode = evt.keyCode; // Add CODE_SPACE handler for `href="#"` and CODE_ENTER handler for non-standard tags

        if (keyCode === CODE_SPACE || keyCode === CODE_ENTER && nonStandardTag) {
          var target = evt.currentTarget || evt.target;
          stopEvent(evt, {
            propagation: false
          });
          target.click();
        }
      },
      click: function click(evt) {
        /* istanbul ignore if: blink/button disabled should handle this */
        if (props.disabled && isEvent(evt)) {
          stopEvent(evt);
        } else if (toggle && listeners && listeners['update:pressed']) {
          // Send `.sync` updates to any "pressed" prop (if `.sync` listeners)
          // `concat()` will normalize the value to an array without
          // double wrapping an array value in an array
          concat(listeners['update:pressed']).forEach(function (fn) {
            if (isFunction$1(fn)) {
              fn(!props.pressed);
            }
          });
        }
      }
    };

    if (toggle) {
      on.focusin = handleFocus;
      on.focusout = handleFocus;
    }

    var componentData = {
      staticClass: 'btn',
      class: computeClass(props),
      props: computeLinkProps(props),
      attrs: computeAttrs(props, data),
      on: on
    };
    return h(link ? BLink : props.tag, a(data, componentData), children);
  }
});

function ownKeys$5(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$5(Object(source), true).forEach(function (key) { _defineProperty$a(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$5(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$a(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var commonIconProps = {
  title: {
    type: String // default: null

  },
  variant: {
    type: String,
    default: null
  },
  fontScale: {
    type: [Number, String],
    default: 1
  },
  scale: {
    type: [Number, String],
    default: 1
  },
  rotate: {
    type: [Number, String],
    default: 0
  },
  flipH: {
    type: Boolean,
    default: false
  },
  flipV: {
    type: Boolean,
    default: false
  },
  shiftH: {
    type: [Number, String],
    default: 0
  },
  shiftV: {
    type: [Number, String],
    default: 0
  },
  animation: {
    type: String,
    default: null
  }
}; // Base attributes needed on all icons

var baseAttrs = {
  viewBox: '0 0 16 16',
  width: '1em',
  height: '1em',
  focusable: 'false',
  role: 'img',
  'aria-label': 'icon'
}; // Attributes that are nulled out when stacked

var stackedAttrs = {
  width: null,
  height: null,
  focusable: null,
  role: null,
  'aria-label': null
}; // Shared private base component to reduce bundle/runtime size
// @vue/component

var BVIconBase = /*#__PURE__*/Vue.extend({
  name: NAME_ICON_BASE,
  functional: true,
  props: _objectSpread$5({
    content: {
      type: String
    },
    stacked: {
      type: Boolean,
      default: false
    }
  }, commonIconProps),
  render: function render(h, _ref) {
    var _class;

    var data = _ref.data,
        props = _ref.props,
        children = _ref.children;
    var fontScale = mathMax(toFloat(props.fontScale, 1), 0) || 1;
    var scale = mathMax(toFloat(props.scale, 1), 0) || 1;
    var rotate = toFloat(props.rotate, 0);
    var shiftH = toFloat(props.shiftH, 0);
    var shiftV = toFloat(props.shiftV, 0);
    var flipH = props.flipH;
    var flipV = props.flipV;
    var animation = props.animation; // Compute the transforms
    // Note that order is important as SVG transforms are applied in order from
    // left to right and we want flipping/scale to occur before rotation
    // Note shifting is applied separately
    // Assumes that the viewbox is `0 0 16 16` (`8 8` is the center)

    var hasScale = flipH || flipV || scale !== 1;
    var hasTransforms = hasScale || rotate;
    var hasShift = shiftH || shiftV;
    var transforms = [hasTransforms ? 'translate(8 8)' : null, hasScale ? "scale(".concat((flipH ? -1 : 1) * scale, " ").concat((flipV ? -1 : 1) * scale, ")") : null, rotate ? "rotate(".concat(rotate, ")") : null, hasTransforms ? 'translate(-8 -8)' : null].filter(identity$1); // Handling stacked icons

    var isStacked = props.stacked;
    var hasContent = !isUndefinedOrNull(props.content); // We wrap the content in a `<g>` for handling the transforms (except shift)

    var $inner = h('g', {
      attrs: {
        transform: transforms.join(' ') || null
      },
      domProps: hasContent ? {
        innerHTML: props.content || ''
      } : {}
    }, children); // If needed, we wrap in an additional `<g>` in order to handle the shifting

    if (hasShift) {
      $inner = h('g', {
        attrs: {
          transform: "translate(".concat(16 * shiftH / 16, " ").concat(-16 * shiftV / 16, ")")
        }
      }, [$inner]);
    }

    if (isStacked) {
      // Wrap in an additional `<g>` for proper
      // animation handling if stacked
      $inner = h('g', {}, [$inner]);
    }

    var $title = props.title ? h('title', props.title) : null;
    return h('svg', a({
      staticClass: 'b-icon bi',
      class: (_class = {}, _defineProperty$a(_class, "text-".concat(props.variant), !!props.variant), _defineProperty$a(_class, "b-icon-animation-".concat(animation), !!animation), _class),
      attrs: baseAttrs,
      style: isStacked ? {} : {
        fontSize: fontScale === 1 ? null : "".concat(fontScale * 100, "%")
      }
    }, // Merge in user supplied data
    data, // If icon is stacked, null out some attrs
    isStacked ? {
      attrs: stackedAttrs
    } : {}, // These cannot be overridden by users
    {
      attrs: {
        xmlns: isStacked ? null : 'http://www.w3.org/2000/svg',
        fill: 'currentColor'
      }
    }), [$title, $inner]);
  }
});

function ownKeys$6(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$6(Object(source), true).forEach(function (key) { _defineProperty$b(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$6(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$b(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
/**
 * Icon component generator function
 *
 * @param {string} icon name (minus the leading `BIcon`)
 * @param {string} raw `innerHTML` for SVG
 * @return {VueComponent}
 */

var makeIcon = function makeIcon(name, content) {
  // For performance reason we pre-compute some values, so that
  // they are not computed on each render of the icon component
  var kebabName = kebabCase(name);
  var iconName = "BIcon".concat(pascalCase(name));
  var iconNameClass = "bi-".concat(kebabName);
  var iconTitle = kebabName.replace(/-/g, ' ');
  var svgContent = trim(content || ''); // Return the icon component definition

  return /*#__PURE__*/Vue.extend({
    name: iconName,
    functional: true,
    props: _objectSpread$6(_objectSpread$6({}, commonIconProps), {}, {
      stacked: {
        type: Boolean,
        default: false
      }
    }),
    render: function render(h, _ref) {
      var data = _ref.data,
          props = _ref.props;
      return h(BVIconBase, a( // Defaults
      {
        props: {
          title: iconTitle
        },
        attrs: {
          'aria-label': iconTitle
        }
      }, // User data
      data, // Required data
      {
        staticClass: iconNameClass,
        props: _objectSpread$6(_objectSpread$6({}, props), {}, {
          content: svgContent
        })
      }));
    }
  });
};

// --- BEGIN AUTO-GENERATED FILE ---
var BIconBlank=/*#__PURE__*/makeIcon('Blank','');// --- Bootstrap Icons ---
var BIconPersonFill=/*#__PURE__*/makeIcon('PersonFill','<path fill-rule="evenodd" d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>');// eslint-disable-next-line
var BIconX=/*#__PURE__*/makeIcon('X','<path fill-rule="evenodd" d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>');// eslint-disable-next-line
// --- END AUTO-GENERATED FILE ---

function ownKeys$7(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$7(Object(source), true).forEach(function (key) { _defineProperty$c(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$7(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$c(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Requires the requested icon component to be installed

var BIcon = /*#__PURE__*/Vue.extend({
  name: NAME_ICON,
  functional: true,
  props: _objectSpread$7(_objectSpread$7({
    icon: {
      type: String,
      default: null
    }
  }, commonIconProps), {}, {
    stacked: {
      type: Boolean,
      default: false
    }
  }),
  render: function render(h, _ref) {
    var data = _ref.data,
        props = _ref.props,
        parent = _ref.parent;
    var icon = pascalCase(trim(props.icon || '')).replace(RX_ICON_PREFIX, '');
    var iconName = "BIcon".concat(icon); // If parent context exists, we check to see if the icon has been registered
    // Either locally in the parent component, or globally at the `$root` level
    // If not registered, we render a blank icon

    var components = ((parent || {}).$options || {}).components;
    var componentRefOrName = icon && components ? components[iconName] || BIconBlank : icon ? iconName : BIconBlank;
    return h(componentRefOrName, a(data, {
      props: _objectSpread$7(_objectSpread$7({}, props), {}, {
        icon: null
      })
    }));
  }
});

function ownKeys$8(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$8(Object(source), true).forEach(function (key) { _defineProperty$d(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$8(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$d(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var CLASS_NAME = 'b-avatar';
var SIZES = ['sm', null, 'lg'];
var FONT_SIZE_SCALE = 0.4;
var BADGE_FONT_SIZE_SCALE = FONT_SIZE_SCALE * 0.7; // --- Props ---

var linkProps$1 = omit(props$1, ['active', 'event', 'routerTag']);

var props$3 = _objectSpread$8(_objectSpread$8({
  src: {
    type: String // default: null

  },
  text: {
    type: String // default: null

  },
  icon: {
    type: String // default: null

  },
  alt: {
    type: String,
    default: 'avatar'
  },
  variant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_AVATAR, 'variant');
    }
  },
  size: {
    type: [Number, String],
    default: null
  },
  square: {
    type: Boolean,
    default: false
  },
  rounded: {
    type: [Boolean, String],
    default: false
  },
  button: {
    type: Boolean,
    default: false
  },
  buttonType: {
    type: String,
    default: 'button'
  },
  badge: {
    type: [Boolean, String],
    default: false
  },
  badgeVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_AVATAR, 'badgeVariant');
    }
  },
  badgeTop: {
    type: Boolean,
    default: false
  },
  badgeLeft: {
    type: Boolean,
    default: false
  },
  badgeOffset: {
    type: String,
    default: '0px'
  }
}, linkProps$1), {}, {
  ariaLabel: {
    type: String // default: null

  }
}); // --- Utility methods ---


var computeSize = function computeSize(value) {
  // Parse to number when value is a float-like string
  value = isString(value) && RX_NUMBER.test(value) ? toFloat(value, 0) : value; // Convert all numbers to pixel values

  return isNumber(value) ? "".concat(value, "px") : value || null;
}; // --- Main component ---
// @vue/component

var BAvatar = /*#__PURE__*/Vue.extend({
  name: NAME_AVATAR,
  mixins: [normalizeSlotMixin],
  inject: {
    bvAvatarGroup: {
      default: null
    }
  },
  props: props$3,
  data: function data() {
    return {
      localSrc: this.src || null
    };
  },
  computed: {
    computedSize: function computedSize() {
      // Always use the avatar group size
      var bvAvatarGroup = this.bvAvatarGroup;
      return computeSize(bvAvatarGroup ? bvAvatarGroup.size : this.size);
    },
    computedVariant: function computedVariant() {
      var bvAvatarGroup = this.bvAvatarGroup;
      return bvAvatarGroup && bvAvatarGroup.variant ? bvAvatarGroup.variant : this.variant;
    },
    computedRounded: function computedRounded() {
      var bvAvatarGroup = this.bvAvatarGroup;
      var square = bvAvatarGroup && bvAvatarGroup.square ? true : this.square;
      var rounded = bvAvatarGroup && bvAvatarGroup.rounded ? bvAvatarGroup.rounded : this.rounded;
      return square ? '0' : rounded === '' ? true : rounded || 'circle';
    },
    fontStyle: function fontStyle() {
      var size = this.computedSize;
      var fontSize = SIZES.indexOf(size) === -1 ? "calc(".concat(size, " * ").concat(FONT_SIZE_SCALE, ")") : null;
      return fontSize ? {
        fontSize: fontSize
      } : {};
    },
    marginStyle: function marginStyle() {
      var size = this.computedSize,
          bvAvatarGroup = this.bvAvatarGroup;
      var overlapScale = bvAvatarGroup ? bvAvatarGroup.overlapScale : 0;
      var value = size && overlapScale ? "calc(".concat(size, " * -").concat(overlapScale, ")") : null;
      return value ? {
        marginLeft: value,
        marginRight: value
      } : {};
    },
    badgeStyle: function badgeStyle() {
      var size = this.computedSize,
          badgeTop = this.badgeTop,
          badgeLeft = this.badgeLeft,
          badgeOffset = this.badgeOffset;
      var offset = badgeOffset || '0px';
      return {
        fontSize: SIZES.indexOf(size) === -1 ? "calc(".concat(size, " * ").concat(BADGE_FONT_SIZE_SCALE, " )") : null,
        top: badgeTop ? offset : null,
        bottom: badgeTop ? null : offset,
        left: badgeLeft ? offset : null,
        right: badgeLeft ? null : offset
      };
    }
  },
  watch: {
    src: function src(newSrc, oldSrc) {
      if (newSrc !== oldSrc) {
        this.localSrc = newSrc || null;
      }
    }
  },
  methods: {
    onImgError: function onImgError(evt) {
      this.localSrc = null;
      this.$emit('img-error', evt);
    },
    onClick: function onClick(evt) {
      this.$emit('click', evt);
    }
  },
  render: function render(h) {
    var _class2;

    var variant = this.computedVariant,
        disabled = this.disabled,
        rounded = this.computedRounded,
        icon = this.icon,
        src = this.localSrc,
        text = this.text,
        fontStyle = this.fontStyle,
        marginStyle = this.marginStyle,
        size = this.computedSize,
        button = this.button,
        type = this.buttonType,
        badge = this.badge,
        badgeVariant = this.badgeVariant,
        badgeStyle = this.badgeStyle;
    var link = !button && isLink(this);
    var tag = button ? BButton : link ? BLink : 'span';
    var alt = this.alt;
    var ariaLabel = this.ariaLabel || null;
    var $content = null;

    if (this.hasNormalizedSlot()) {
      // Default slot overrides props
      $content = h('span', {
        staticClass: 'b-avatar-custom'
      }, [this.normalizeSlot()]);
    } else if (src) {
      $content = h('img', {
        style: variant ? {} : {
          width: '100%',
          height: '100%'
        },
        attrs: {
          src: src,
          alt: alt
        },
        on: {
          error: this.onImgError
        }
      });
      $content = h('span', {
        staticClass: 'b-avatar-img'
      }, [$content]);
    } else if (icon) {
      $content = h(BIcon, {
        props: {
          icon: icon
        },
        attrs: {
          'aria-hidden': 'true',
          alt: alt
        }
      });
    } else if (text) {
      $content = h('span', {
        staticClass: 'b-avatar-text',
        style: fontStyle
      }, [h('span', text)]);
    } else {
      // Fallback default avatar content
      $content = h(BIconPersonFill, {
        attrs: {
          'aria-hidden': 'true',
          alt: alt
        }
      });
    }

    var $badge = h();
    var hasBadgeSlot = this.hasNormalizedSlot('badge');

    if (badge || badge === '' || hasBadgeSlot) {
      var badgeText = badge === true ? '' : badge;
      $badge = h('span', {
        staticClass: 'b-avatar-badge',
        class: _defineProperty$d({}, "badge-".concat(badgeVariant), !!badgeVariant),
        style: badgeStyle
      }, [hasBadgeSlot ? this.normalizeSlot('badge') : badgeText]);
    }

    var componentData = {
      staticClass: CLASS_NAME,
      class: (_class2 = {}, _defineProperty$d(_class2, "".concat(CLASS_NAME, "-").concat(size), size && SIZES.indexOf(size) !== -1), _defineProperty$d(_class2, "badge-".concat(variant), !button && variant), _defineProperty$d(_class2, "rounded", rounded === true), _defineProperty$d(_class2, "rounded-".concat(rounded), rounded && rounded !== true), _defineProperty$d(_class2, "disabled", disabled), _class2),
      style: _objectSpread$8(_objectSpread$8({}, marginStyle), {}, {
        width: size,
        height: size
      }),
      attrs: {
        'aria-label': ariaLabel || null
      },
      props: button ? {
        variant: variant,
        disabled: disabled,
        type: type
      } : link ? pluckProps(linkProps$1, this) : {},
      on: button || link ? {
        click: this.onClick
      } : {}
    };
    return h(tag, componentData, [$content, $badge]);
  }
});

function ownKeys$9(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$9(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$9(Object(source), true).forEach(function (key) { _defineProperty$e(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$9(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$e(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var linkProps$2 = omit(props$1, ['event', 'routerTag']);
delete linkProps$2.href.default;
delete linkProps$2.to.default;
var props$4 = _objectSpread$9({
  tag: {
    type: String,
    default: 'span'
  },
  variant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_BADGE, 'variant');
    }
  },
  pill: {
    type: Boolean,
    default: false
  }
}, linkProps$2); // --- Main component ---
// @vue/component

var BBadge = /*#__PURE__*/Vue.extend({
  name: NAME_BADGE,
  functional: true,
  props: props$4,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    var link = isLink(props);
    var tag = link ? BLink : props.tag;
    var componentData = {
      staticClass: 'badge',
      class: [props.variant ? "badge-".concat(props.variant) : 'badge-secondary', {
        'badge-pill': props.pill,
        active: props.active,
        disabled: props.disabled
      }],
      props: link ? pluckProps(linkProps$2, props) : {}
    };
    return h(tag, a(data, componentData), children);
  }
});

var stripTags = function stripTags() {
  var text = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return String(text).replace(RX_HTML_TAGS, '');
}; // Generate a `domProps` object for either `innerHTML`, `textContent` or an empty object

var htmlOrText = function htmlOrText(innerHTML, textContent) {
  return innerHTML ? {
    innerHTML: innerHTML
  } : textContent ? {
    textContent: textContent
  } : {};
};

// SSR safe client-side ID attribute generation
// ID's can only be generated client-side, after mount
// `this._uid` is not synched between server and client
// @vue/component
var idMixin = {
  props: {
    id: {
      type: String // default: null

    }
  },
  data: function data() {
    return {
      localId_: null
    };
  },
  computed: {
    safeId: function safeId() {
      // Computed property that returns a dynamic function for creating the ID
      // Reacts to changes in both `.id` and `.localId_` and regenerates a new function
      var id = this.id || this.localId_; // We return a function that accepts an optional suffix string
      // So this computed prop looks and works like a method
      // but benefits from Vue's computed prop caching

      var fn = function fn(suffix) {
        if (!id) {
          return null;
        }

        suffix = String(suffix || '').replace(/\s+/g, '_');
        return suffix ? id + '_' + suffix : id;
      };

      return fn;
    }
  },
  mounted: function mounted() {
    var _this = this;

    // `mounted()` only occurs client-side
    this.$nextTick(function () {
      // Update DOM with auto-generated ID after mount
      // to prevent SSR hydration errors
      _this.localId_ = "__BVID__".concat(_this._uid);
    });
  }
};

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }
var OBSERVER_PROP_NAME = '__bv__visibility_observer';

var VisibilityObserver = /*#__PURE__*/function () {
  function VisibilityObserver(el, options, vnode) {
    _classCallCheck$1(this, VisibilityObserver);

    this.el = el;
    this.callback = options.callback;
    this.margin = options.margin || 0;
    this.once = options.once || false;
    this.observer = null;
    this.visible = undefined;
    this.doneOnce = false; // Create the observer instance (if possible)

    this.createObserver(vnode);
  }

  _createClass(VisibilityObserver, [{
    key: "createObserver",
    value: function createObserver(vnode) {
      var _this = this;

      // Remove any previous observer
      if (this.observer) {
        /* istanbul ignore next */
        this.stop();
      } // Should only be called once and `callback` prop should be a function


      if (this.doneOnce || !isFunction$1(this.callback)) {
        /* istanbul ignore next */
        return;
      } // Create the observer instance


      try {
        // Future: Possibly add in other modifiers for left/right/top/bottom
        // offsets, root element reference, and thresholds
        this.observer = new IntersectionObserver(this.handler.bind(this), {
          // `null` = 'viewport'
          root: null,
          // Pixels away from view port to consider "visible"
          rootMargin: this.margin,
          // Intersection ratio of el and root (as a value from 0 to 1)
          threshold: 0
        });
      } catch (_unused) {
        // No IntersectionObserver support, so just stop trying to observe
        this.doneOnce = true;
        this.observer = undefined;
        this.callback(null);
        return;
      } // Start observing in a `$nextTick()` (to allow DOM to complete rendering)

      /* istanbul ignore next: IntersectionObserver not supported in JSDOM */


      vnode.context.$nextTick(function () {
        requestAF(function () {
          // Placed in an `if` just in case we were destroyed before
          // this `requestAnimationFrame` runs
          if (_this.observer) {
            _this.observer.observe(_this.el);
          }
        });
      });
    }
    /* istanbul ignore next */

  }, {
    key: "handler",
    value: function handler(entries)
    /* istanbul ignore next: IntersectionObserver not supported in JSDOM */
    {
      var entry = entries ? entries[0] : {};
      var isIntersecting = Boolean(entry.isIntersecting || entry.intersectionRatio > 0.0);

      if (isIntersecting !== this.visible) {
        this.visible = isIntersecting;
        this.callback(isIntersecting);

        if (this.once && this.visible) {
          this.doneOnce = true;
          this.stop();
        }
      }
    }
  }, {
    key: "stop",
    value: function stop() {
      /* istanbul ignore next */
      this.observer && this.observer.disconnect();
      this.observer = null;
    }
  }]);

  return VisibilityObserver;
}();

var destroy = function destroy(el) {
  var observer = el[OBSERVER_PROP_NAME];

  if (observer && observer.stop) {
    observer.stop();
  }

  delete el[OBSERVER_PROP_NAME];
};

var bind = function bind(el, _ref, vnode) {
  var value = _ref.value,
      modifiers = _ref.modifiers;
  // `value` is the callback function
  var options = {
    margin: '0px',
    once: false,
    callback: value
  }; // Parse modifiers

  keys$1(modifiers).forEach(function (mod) {
    /* istanbul ignore else: Until <b-img-lazy> is switched to use this directive */
    if (RX_DIGITS.test(mod)) {
      options.margin = "".concat(mod, "px");
    } else if (mod.toLowerCase() === 'once') {
      options.once = true;
    }
  }); // Destroy any previous observer

  destroy(el); // Create new observer

  el[OBSERVER_PROP_NAME] = new VisibilityObserver(el, options, vnode); // Store the current modifiers on the object (cloned)

  el[OBSERVER_PROP_NAME]._prevModifiers = clone(modifiers);
}; // When the directive options may have been updated (or element)


var componentUpdated = function componentUpdated(el, _ref2, vnode) {
  var value = _ref2.value,
      oldValue = _ref2.oldValue,
      modifiers = _ref2.modifiers;
  // Compare value/oldValue and modifiers to see if anything has changed
  // and if so, destroy old observer and create new observer

  /* istanbul ignore next */
  modifiers = clone(modifiers);
  /* istanbul ignore next */

  if (el && (value !== oldValue || !el[OBSERVER_PROP_NAME] || !looseEqual(modifiers, el[OBSERVER_PROP_NAME]._prevModifiers))) {
    // Re-bind on element
    bind(el, {
      value: value,
      modifiers: modifiers
    }, vnode);
  }
}; // When directive un-binds from element


var unbind = function unbind(el) {
  // Remove the observer
  destroy(el);
}; // Export the directive


var VBVisible = {
  bind: bind,
  componentUpdated: componentUpdated,
  unbind: unbind
};

function _defineProperty$f(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Blank image with fill template

var BLANK_TEMPLATE = '<svg width="%{w}" height="%{h}" ' + 'xmlns="http://www.w3.org/2000/svg" ' + 'viewBox="0 0 %{w} %{h}" preserveAspectRatio="none">' + '<rect width="100%" height="100%" style="fill:%{f};"></rect>' + '</svg>';
var props$5 = {
  src: {
    type: String // default: null

  },
  srcset: {
    type: [String, Array] // default: null

  },
  sizes: {
    type: [String, Array] // default: null

  },
  alt: {
    type: String,
    default: null
  },
  width: {
    type: [Number, String] // default: null

  },
  height: {
    type: [Number, String] // default: null

  },
  block: {
    type: Boolean,
    default: false
  },
  fluid: {
    type: Boolean,
    default: false
  },
  fluidGrow: {
    // Gives fluid images class `w-100` to make them grow to fit container
    type: Boolean,
    default: false
  },
  rounded: {
    // rounded can be:
    //   false: no rounding of corners
    //   true: slightly rounded corners
    //   'top': top corners rounded
    //   'right': right corners rounded
    //   'bottom': bottom corners rounded
    //   'left': left corners rounded
    //   'circle': circle/oval
    //   '0': force rounding off
    type: [Boolean, String],
    default: false
  },
  thumbnail: {
    type: Boolean,
    default: false
  },
  left: {
    type: Boolean,
    default: false
  },
  right: {
    type: Boolean,
    default: false
  },
  center: {
    type: Boolean,
    default: false
  },
  blank: {
    type: Boolean,
    default: false
  },
  blankColor: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_IMG, 'blankColor');
    }
  }
}; // --- Helper methods ---

var makeBlankImgSrc = function makeBlankImgSrc(width, height, color) {
  var src = encodeURIComponent(BLANK_TEMPLATE.replace('%{w}', toString$1(width)).replace('%{h}', toString$1(height)).replace('%{f}', color));
  return "data:image/svg+xml;charset=UTF-8,".concat(src);
}; // @vue/component


var BImg = /*#__PURE__*/Vue.extend({
  name: NAME_IMG,
  functional: true,
  props: props$5,
  render: function render(h, _ref) {
    var _class;

    var props = _ref.props,
        data = _ref.data;
    var src = props.src;
    var width = toInteger(props.width) || null;
    var height = toInteger(props.height) || null;
    var align = null;
    var block = props.block;
    var srcset = concat(props.srcset).filter(identity$1).join(',');
    var sizes = concat(props.sizes).filter(identity$1).join(',');

    if (props.blank) {
      if (!height && width) {
        height = width;
      } else if (!width && height) {
        width = height;
      }

      if (!width && !height) {
        width = 1;
        height = 1;
      } // Make a blank SVG image


      src = makeBlankImgSrc(width, height, props.blankColor || 'transparent'); // Disable srcset and sizes

      srcset = null;
      sizes = null;
    }

    if (props.left) {
      align = 'float-left';
    } else if (props.right) {
      align = 'float-right';
    } else if (props.center) {
      align = 'mx-auto';
      block = true;
    }

    return h('img', a(data, {
      attrs: {
        src: src,
        alt: props.alt,
        width: width ? toString$1(width) : null,
        height: height ? toString$1(height) : null,
        srcset: srcset || null,
        sizes: sizes || null
      },
      class: (_class = {
        'img-thumbnail': props.thumbnail,
        'img-fluid': props.fluid || props.fluidGrow,
        'w-100': props.fluidGrow,
        rounded: props.rounded === '' || props.rounded === true
      }, _defineProperty$f(_class, "rounded-".concat(props.rounded), isString(props.rounded) && props.rounded !== ''), _defineProperty$f(_class, align, align), _defineProperty$f(_class, 'd-block', block), _class)
    }));
  }
});

var EVENT_OPTIONS_PASSIVE = {
  passive: true
};
var EVENT_OPTIONS_NO_CAPTURE = {
  passive: true,
  capture: false
};

var noop = function noop() {};

function ownKeys$a(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$a(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$a(Object(source), true).forEach(function (key) { _defineProperty$g(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$a(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$g(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
/**
 * Observe a DOM element changes, falls back to eventListener mode
 * @param {Element} el The DOM element to observe
 * @param {Function} callback callback to be called on change
 * @param {object} [options={childList: true, subtree: true}] observe options
 * @see https://stackoverflow.com/questions/3219758
 */

var observeDom = function observeDom(el, callback, options)
/* istanbul ignore next: difficult to test in JSDOM */
{
  // Handle cases where we might be passed a Vue instance
  el = el ? el.$el || el : null; // Early exit when we have no element

  /* istanbul ignore next: difficult to test in JSDOM */

  if (!isElement(el)) {
    return null;
  } // Exit and throw a warning when `MutationObserver` isn't available


  if (warnNoMutationObserverSupport('observeDom')) {
    return null;
  } // Define a new observer


  var obs = new MutationObs(function (mutations) {
    var changed = false; // A mutation can contain several change records, so we loop
    // through them to see what has changed
    // We break out of the loop early if any "significant" change
    // has been detected

    for (var i = 0; i < mutations.length && !changed; i++) {
      // The mutation record
      var mutation = mutations[i]; // Mutation type

      var type = mutation.type; // DOM node (could be any DOM node type - HTMLElement, Text, comment, etc.)

      var target = mutation.target; // Detect whether a change happened based on type and target

      if (type === 'characterData' && target.nodeType === Node.TEXT_NODE) {
        // We ignore nodes that are not TEXT (i.e. comments, etc.)
        // as they don't change layout
        changed = true;
      } else if (type === 'attributes') {
        changed = true;
      } else if (type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
        // This includes HTMLElement and text nodes being
        // added/removed/re-arranged
        changed = true;
      }
    } // We only call the callback if a change that could affect
    // layout/size truly happened


    if (changed) {
      callback();
    }
  }); // Have the observer observe foo for changes in children, etc

  obs.observe(el, _objectSpread$a({
    childList: true,
    subtree: true
  }, options)); // We return a reference to the observer so that `obs.disconnect()`
  // can be called if necessary
  // To reduce overhead when the root element is hidden

  return obs;
};

var DIRECTION = {
  next: {
    dirClass: 'carousel-item-left',
    overlayClass: 'carousel-item-next'
  },
  prev: {
    dirClass: 'carousel-item-right',
    overlayClass: 'carousel-item-prev'
  }
}; // Fallback Transition duration (with a little buffer) in ms

var TRANS_DURATION = 600 + 50; // Time for mouse compat events to fire after touch

var TOUCH_EVENT_COMPAT_WAIT = 500; // Number of pixels to consider touch move a swipe

var SWIPE_THRESHOLD = 40; // PointerEvent pointer types

var PointerType = {
  TOUCH: 'touch',
  PEN: 'pen'
}; // Transition Event names

var TransitionEndEvents = {
  WebkitTransition: 'webkitTransitionEnd',
  MozTransition: 'transitionend',
  OTransition: 'otransitionend oTransitionEnd',
  transition: 'transitionend'
}; // Return the browser specific transitionEnd event name

var getTransitionEndEvent = function getTransitionEndEvent(el) {
  for (var name in TransitionEndEvents) {
    if (!isUndefined(el.style[name])) {
      return TransitionEndEvents[name];
    }
  } // Fallback

  /* istanbul ignore next */


  return null;
}; // @vue/component


var BCarousel = /*#__PURE__*/Vue.extend({
  name: NAME_CAROUSEL,
  mixins: [idMixin, normalizeSlotMixin],
  provide: function provide() {
    return {
      bvCarousel: this
    };
  },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    labelPrev: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_CAROUSEL, 'labelPrev');
      }
    },
    labelNext: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_CAROUSEL, 'labelNext');
      }
    },
    labelGotoSlide: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_CAROUSEL, 'labelGotoSlide');
      }
    },
    labelIndicators: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_CAROUSEL, 'labelIndicators');
      }
    },
    interval: {
      type: Number,
      default: 5000
    },
    indicators: {
      type: Boolean,
      default: false
    },
    controls: {
      type: Boolean,
      default: false
    },
    noAnimation: {
      // Disable slide/fade animation
      type: Boolean,
      default: false
    },
    fade: {
      // Enable cross-fade animation instead of slide animation
      type: Boolean,
      default: false
    },
    noWrap: {
      // Disable wrapping/looping when start/end is reached
      type: Boolean,
      default: false
    },
    noTouch: {
      // Sniffed by carousel-slide
      type: Boolean,
      default: false
    },
    noHoverPause: {
      // Disable pause on hover
      type: Boolean,
      default: false
    },
    imgWidth: {
      // Sniffed by carousel-slide
      type: [Number, String] // default: undefined

    },
    imgHeight: {
      // Sniffed by carousel-slide
      type: [Number, String] // default: undefined

    },
    background: {
      type: String // default: undefined

    },
    value: {
      type: Number,
      default: 0
    }
  },
  data: function data() {
    return {
      index: this.value || 0,
      isSliding: false,
      transitionEndEvent: null,
      slides: [],
      direction: null,
      isPaused: !(toInteger(this.interval, 0) > 0),
      // Touch event handling values
      touchStartX: 0,
      touchDeltaX: 0
    };
  },
  computed: {
    numSlides: function numSlides() {
      return this.slides.length;
    }
  },
  watch: {
    value: function value(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setSlide(toInteger(newVal, 0));
      }
    },
    interval: function interval(newVal, oldVal) {
      if (newVal === oldVal) {
        /* istanbul ignore next */
        return;
      }

      if (!newVal) {
        // Pausing slide show
        this.pause(false);
      } else {
        // Restarting or Changing interval
        this.pause(true);
        this.start(false);
      }
    },
    isPaused: function isPaused(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit(newVal ? 'paused' : 'unpaused');
      }
    },
    index: function index(to, from) {
      if (to === from || this.isSliding) {
        /* istanbul ignore next */
        return;
      }

      this.doSlide(to, from);
    }
  },
  created: function created() {
    // Create private non-reactive props
    this.$_interval = null;
    this.$_animationTimeout = null;
    this.$_touchTimeout = null;
    this.$_observer = null; // Set initial paused state

    this.isPaused = !(toInteger(this.interval, 0) > 0);
  },
  mounted: function mounted() {
    // Cache current browser transitionend event name
    this.transitionEndEvent = getTransitionEndEvent(this.$el) || null; // Get all slides

    this.updateSlides(); // Observe child changes so we can update slide list

    this.setObserver(true);
  },
  beforeDestroy: function beforeDestroy() {
    this.clearInterval();
    this.clearAnimationTimeout();
    this.clearTouchTimeout();
    this.setObserver(false);
  },
  methods: {
    clearInterval: function (_clearInterval) {
      function clearInterval() {
        return _clearInterval.apply(this, arguments);
      }

      clearInterval.toString = function () {
        return _clearInterval.toString();
      };

      return clearInterval;
    }(function () {
      clearInterval(this.$_interval);
      this.$_interval = null;
    }),
    clearAnimationTimeout: function clearAnimationTimeout() {
      clearTimeout(this.$_animationTimeout);
      this.$_animationTimeout = null;
    },
    clearTouchTimeout: function clearTouchTimeout() {
      clearTimeout(this.$_touchTimeout);
      this.$_touchTimeout = null;
    },
    setObserver: function setObserver() {
      var on = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      this.$_observer && this.$_observer.disconnect();
      this.$_observer = null;

      if (on) {
        this.$_observer = observeDom(this.$refs.inner, this.updateSlides.bind(this), {
          subtree: false,
          childList: true,
          attributes: true,
          attributeFilter: ['id']
        });
      }
    },
    // Set slide
    setSlide: function setSlide(slide) {
      var _this = this;

      var direction = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

      // Don't animate when page is not visible

      /* istanbul ignore if: difficult to test */
      if (isBrowser && document.visibilityState && document.hidden) {
        return;
      }

      var noWrap = this.noWrap;
      var numSlides = this.numSlides; // Make sure we have an integer (you never know!)

      slide = mathFloor(slide); // Don't do anything if nothing to slide to

      if (numSlides === 0) {
        return;
      } // Don't change slide while transitioning, wait until transition is done


      if (this.isSliding) {
        // Schedule slide after sliding complete
        this.$once('sliding-end', function () {
          return _this.setSlide(slide, direction);
        });
        return;
      }

      this.direction = direction; // Set new slide index
      // Wrap around if necessary (if no-wrap not enabled)

      this.index = slide >= numSlides ? noWrap ? numSlides - 1 : 0 : slide < 0 ? noWrap ? 0 : numSlides - 1 : slide; // Ensure the v-model is synched up if no-wrap is enabled
      // and user tried to slide pass either ends

      if (noWrap && this.index !== slide && this.index !== this.value) {
        this.$emit('input', this.index);
      }
    },
    // Previous slide
    prev: function prev() {
      this.setSlide(this.index - 1, 'prev');
    },
    // Next slide
    next: function next() {
      this.setSlide(this.index + 1, 'next');
    },
    // Pause auto rotation
    pause: function pause(evt) {
      if (!evt) {
        this.isPaused = true;
      }

      this.clearInterval();
    },
    // Start auto rotate slides
    start: function start(evt) {
      if (!evt) {
        this.isPaused = false;
      }
      /* istanbul ignore next: most likely will never happen, but just in case */


      this.clearInterval(); // Don't start if no interval, or less than 2 slides

      if (this.interval && this.numSlides > 1) {
        this.$_interval = setInterval(this.next, mathMax(1000, this.interval));
      }
    },
    // Restart auto rotate slides when focus/hover leaves the carousel

    /* istanbul ignore next */
    restart: function restart()
    /* istanbul ignore next: difficult to test */
    {
      if (!this.$el.contains(getActiveElement())) {
        this.start();
      }
    },
    doSlide: function doSlide(to, from) {
      var _this2 = this;

      var isCycling = Boolean(this.interval); // Determine sliding direction

      var direction = this.calcDirection(this.direction, from, to);
      var overlayClass = direction.overlayClass;
      var dirClass = direction.dirClass; // Determine current and next slides

      var currentSlide = this.slides[from];
      var nextSlide = this.slides[to]; // Don't do anything if there aren't any slides to slide to

      if (!currentSlide || !nextSlide) {
        /* istanbul ignore next */
        return;
      } // Start animating


      this.isSliding = true;

      if (isCycling) {
        this.pause(false);
      }

      this.$emit('sliding-start', to); // Update v-model

      this.$emit('input', this.index);

      if (this.noAnimation) {
        addClass(nextSlide, 'active');
        removeClass(currentSlide, 'active');
        this.isSliding = false; // Notify ourselves that we're done sliding (slid)

        this.$nextTick(function () {
          return _this2.$emit('sliding-end', to);
        });
      } else {
        addClass(nextSlide, overlayClass); // Trigger a reflow of next slide

        reflow(nextSlide);
        addClass(currentSlide, dirClass);
        addClass(nextSlide, dirClass); // Transition End handler

        var called = false;
        /* istanbul ignore next: difficult to test */

        var onceTransEnd = function onceTransEnd() {
          if (called) {
            return;
          }

          called = true;
          /* istanbul ignore if: transition events cant be tested in JSDOM */

          if (_this2.transitionEndEvent) {
            var events = _this2.transitionEndEvent.split(/\s+/);

            events.forEach(function (evt) {
              return eventOff(nextSlide, evt, onceTransEnd, EVENT_OPTIONS_NO_CAPTURE);
            });
          }

          _this2.clearAnimationTimeout();

          removeClass(nextSlide, dirClass);
          removeClass(nextSlide, overlayClass);
          addClass(nextSlide, 'active');
          removeClass(currentSlide, 'active');
          removeClass(currentSlide, dirClass);
          removeClass(currentSlide, overlayClass);
          setAttr(currentSlide, 'aria-current', 'false');
          setAttr(nextSlide, 'aria-current', 'true');
          setAttr(currentSlide, 'aria-hidden', 'true');
          setAttr(nextSlide, 'aria-hidden', 'false');
          _this2.isSliding = false;
          _this2.direction = null; // Notify ourselves that we're done sliding (slid)

          _this2.$nextTick(function () {
            return _this2.$emit('sliding-end', to);
          });
        }; // Set up transitionend handler

        /* istanbul ignore if: transition events cant be tested in JSDOM */


        if (this.transitionEndEvent) {
          var events = this.transitionEndEvent.split(/\s+/);
          events.forEach(function (event) {
            return eventOn(nextSlide, event, onceTransEnd, EVENT_OPTIONS_NO_CAPTURE);
          });
        } // Fallback to setTimeout()


        this.$_animationTimeout = setTimeout(onceTransEnd, TRANS_DURATION);
      }

      if (isCycling) {
        this.start(false);
      }
    },
    // Update slide list
    updateSlides: function updateSlides() {
      this.pause(true); // Get all slides as DOM elements

      this.slides = selectAll('.carousel-item', this.$refs.inner);
      var numSlides = this.slides.length; // Keep slide number in range

      var index = mathMax(0, mathMin(mathFloor(this.index), numSlides - 1));
      this.slides.forEach(function (slide, idx) {
        var n = idx + 1;

        if (idx === index) {
          addClass(slide, 'active');
          setAttr(slide, 'aria-current', 'true');
        } else {
          removeClass(slide, 'active');
          setAttr(slide, 'aria-current', 'false');
        }

        setAttr(slide, 'aria-posinset', String(n));
        setAttr(slide, 'aria-setsize', String(numSlides));
      }); // Set slide as active

      this.setSlide(index);
      this.start(this.isPaused);
    },
    calcDirection: function calcDirection() {
      var direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      var curIndex = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      var nextIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

      if (!direction) {
        return nextIndex > curIndex ? DIRECTION.next : DIRECTION.prev;
      }

      return DIRECTION[direction];
    },
    handleClick: function handleClick(evt, fn) {
      var keyCode = evt.keyCode;

      if (evt.type === 'click' || keyCode === CODE_SPACE || keyCode === CODE_ENTER) {
        stopEvent(evt);
        fn();
      }
    },

    /* istanbul ignore next */
    handleSwipe: function handleSwipe()
    /* istanbul ignore next: JSDOM doesn't support touch events */
    {
      var absDeltaX = mathAbs(this.touchDeltaX);

      if (absDeltaX <= SWIPE_THRESHOLD) {
        return;
      }

      var direction = absDeltaX / this.touchDeltaX; // Reset touch delta X
      // https://github.com/twbs/bootstrap/pull/28558

      this.touchDeltaX = 0;

      if (direction > 0) {
        // Swipe left
        this.prev();
      } else if (direction < 0) {
        // Swipe right
        this.next();
      }
    },

    /* istanbul ignore next */
    touchStart: function touchStart(evt)
    /* istanbul ignore next: JSDOM doesn't support touch events */
    {
      if (hasPointerEventSupport && PointerType[evt.pointerType.toUpperCase()]) {
        this.touchStartX = evt.clientX;
      } else if (!hasPointerEventSupport) {
        this.touchStartX = evt.touches[0].clientX;
      }
    },

    /* istanbul ignore next */
    touchMove: function touchMove(evt)
    /* istanbul ignore next: JSDOM doesn't support touch events */
    {
      // Ensure swiping with one touch and not pinching
      if (evt.touches && evt.touches.length > 1) {
        this.touchDeltaX = 0;
      } else {
        this.touchDeltaX = evt.touches[0].clientX - this.touchStartX;
      }
    },

    /* istanbul ignore next */
    touchEnd: function touchEnd(evt)
    /* istanbul ignore next: JSDOM doesn't support touch events */
    {
      if (hasPointerEventSupport && PointerType[evt.pointerType.toUpperCase()]) {
        this.touchDeltaX = evt.clientX - this.touchStartX;
      }

      this.handleSwipe(); // If it's a touch-enabled device, mouseenter/leave are fired as
      // part of the mouse compatibility events on first tap - the carousel
      // would stop cycling until user tapped out of it;
      // here, we listen for touchend, explicitly pause the carousel
      // (as if it's the second time we tap on it, mouseenter compat event
      // is NOT fired) and after a timeout (to allow for mouse compatibility
      // events to fire) we explicitly restart cycling

      this.pause(false);
      this.clearTouchTimeout();
      this.$_touchTimeout = setTimeout(this.start, TOUCH_EVENT_COMPAT_WAIT + mathMax(1000, this.interval));
    }
  },
  render: function render(h) {
    var _this3 = this;

    // Wrapper for slides
    var inner = h('div', {
      ref: 'inner',
      class: ['carousel-inner'],
      attrs: {
        id: this.safeId('__BV_inner_'),
        role: 'list'
      }
    }, [this.normalizeSlot()]); // Prev and next controls

    var controls = h();

    if (this.controls) {
      var prevHandler = function prevHandler(evt) {
        /* istanbul ignore next */
        if (!_this3.isSliding) {
          _this3.handleClick(evt, _this3.prev);
        } else {
          stopEvent(evt, {
            propagation: false
          });
        }
      };

      var nextHandler = function nextHandler(evt) {
        /* istanbul ignore next */
        if (!_this3.isSliding) {
          _this3.handleClick(evt, _this3.next);
        } else {
          stopEvent(evt, {
            propagation: false
          });
        }
      };

      controls = [h('a', {
        class: ['carousel-control-prev'],
        attrs: {
          href: '#',
          role: 'button',
          'aria-controls': this.safeId('__BV_inner_'),
          'aria-disabled': this.isSliding ? 'true' : null
        },
        on: {
          click: prevHandler,
          keydown: prevHandler
        }
      }, [h('span', {
        class: ['carousel-control-prev-icon'],
        attrs: {
          'aria-hidden': 'true'
        }
      }), h('span', {
        class: ['sr-only']
      }, [this.labelPrev])]), h('a', {
        class: ['carousel-control-next'],
        attrs: {
          href: '#',
          role: 'button',
          'aria-controls': this.safeId('__BV_inner_'),
          'aria-disabled': this.isSliding ? 'true' : null
        },
        on: {
          click: nextHandler,
          keydown: nextHandler
        }
      }, [h('span', {
        class: ['carousel-control-next-icon'],
        attrs: {
          'aria-hidden': 'true'
        }
      }), h('span', {
        class: ['sr-only']
      }, [this.labelNext])])];
    } // Indicators


    var indicators = h('ol', {
      class: ['carousel-indicators'],
      directives: [{
        name: 'show',
        rawName: 'v-show',
        value: this.indicators,
        expression: 'indicators'
      }],
      attrs: {
        id: this.safeId('__BV_indicators_'),
        'aria-hidden': this.indicators ? 'false' : 'true',
        'aria-label': this.labelIndicators,
        'aria-owns': this.safeId('__BV_inner_')
      }
    }, this.slides.map(function (slide, n) {
      return h('li', {
        key: "slide_".concat(n),
        class: {
          active: n === _this3.index
        },
        attrs: {
          role: 'button',
          id: _this3.safeId("__BV_indicator_".concat(n + 1, "_")),
          tabindex: _this3.indicators ? '0' : '-1',
          'aria-current': n === _this3.index ? 'true' : 'false',
          'aria-label': "".concat(_this3.labelGotoSlide, " ").concat(n + 1),
          'aria-describedby': _this3.slides[n].id || null,
          'aria-controls': _this3.safeId('__BV_inner_')
        },
        on: {
          click: function click(evt) {
            _this3.handleClick(evt, function () {
              _this3.setSlide(n);
            });
          },
          keydown: function keydown(evt) {
            _this3.handleClick(evt, function () {
              _this3.setSlide(n);
            });
          }
        }
      });
    }));
    var on = {
      mouseenter: this.noHoverPause ? noop : this.pause,
      mouseleave: this.noHoverPause ? noop : this.restart,
      focusin: this.pause,
      focusout: this.restart,
      keydown: function keydown(evt) {
        if (/input|textarea/i.test(evt.target.tagName)) {
          /* istanbul ignore next */
          return;
        }

        var keyCode = evt.keyCode;

        if (keyCode === CODE_LEFT || keyCode === CODE_RIGHT) {
          stopEvent(evt);

          _this3[keyCode === CODE_LEFT ? 'prev' : 'next']();
        }
      }
    }; // Touch support event handlers for environment

    if (!this.noTouch && hasTouchSupport) {
      // Attach appropriate listeners (prepend event name with '&' for passive mode)

      /* istanbul ignore next: JSDOM doesn't support touch events */
      if (hasPointerEventSupport) {
        on['&pointerdown'] = this.touchStart;
        on['&pointerup'] = this.touchEnd;
      } else {
        on['&touchstart'] = this.touchStart;
        on['&touchmove'] = this.touchMove;
        on['&touchend'] = this.touchEnd;
      }
    } // Return the carousel


    return h('div', {
      staticClass: 'carousel',
      class: {
        slide: !this.noAnimation,
        'carousel-fade': !this.noAnimation && this.fade,
        'pointer-event': !this.noTouch && hasTouchSupport && hasPointerEventSupport
      },
      style: {
        background: this.background
      },
      attrs: {
        role: 'region',
        id: this.safeId(),
        'aria-busy': this.isSliding ? 'true' : 'false'
      },
      on: on
    }, [inner, controls, indicators]);
  }
});

function ownKeys$b(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$b(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$b(Object(source), true).forEach(function (key) { _defineProperty$h(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$b(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$h(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var imgProps = {
  imgSrc: {
    type: String // default: undefined

  },
  imgAlt: {
    type: String // default: undefined

  },
  imgWidth: {
    type: [Number, String] // default: undefined

  },
  imgHeight: {
    type: [Number, String] // default: undefined

  },
  imgBlank: {
    type: Boolean,
    default: false
  },
  imgBlankColor: {
    type: String,
    default: 'transparent'
  }
};
var props$6 = _objectSpread$b(_objectSpread$b({}, imgProps), {}, {
  contentVisibleUp: {
    type: String
  },
  contentTag: {
    type: String,
    default: 'div'
  },
  caption: {
    type: String
  },
  captionHtml: {
    type: String
  },
  captionTag: {
    type: String,
    default: 'h3'
  },
  text: {
    type: String
  },
  textHtml: {
    type: String
  },
  textTag: {
    type: String,
    default: 'p'
  },
  background: {
    type: String
  }
}); // --- Main component ---
// @vue/component

var BCarouselSlide = /*#__PURE__*/Vue.extend({
  name: NAME_CAROUSEL_SLIDE,
  mixins: [idMixin, normalizeSlotMixin],
  inject: {
    bvCarousel: {
      default: function _default() {
        return {
          // Explicitly disable touch if not a child of carousel
          noTouch: true
        };
      }
    }
  },
  props: props$6,
  computed: {
    contentClasses: function contentClasses() {
      return [this.contentVisibleUp ? 'd-none' : '', this.contentVisibleUp ? "d-".concat(this.contentVisibleUp, "-block") : ''];
    },
    computedWidth: function computedWidth() {
      // Use local width, or try parent width
      return this.imgWidth || this.bvCarousel.imgWidth || null;
    },
    computedHeight: function computedHeight() {
      // Use local height, or try parent height
      return this.imgHeight || this.bvCarousel.imgHeight || null;
    }
  },
  render: function render(h) {
    var $img = this.normalizeSlot('img');

    if (!$img && (this.imgSrc || this.imgBlank)) {
      var on = {}; // Touch support event handler

      /* istanbul ignore if: difficult to test in JSDOM */

      if (!this.bvCarousel.noTouch && hasTouchSupport) {
        on.dragstart = function (evt) {
          return stopEvent(evt, {
            propagation: false
          });
        };
      }

      $img = h(BImg, {
        props: _objectSpread$b(_objectSpread$b({}, pluckProps(imgProps, this.$props, unprefixPropName.bind(null, 'img'))), {}, {
          width: this.computedWidth,
          height: this.computedHeight,
          fluidGrow: true,
          block: true
        }),
        on: on
      });
    }

    var $contentChildren = [// Caption
    this.caption || this.captionHtml ? h(this.captionTag, {
      domProps: htmlOrText(this.captionHtml, this.caption)
    }) : false, // Text
    this.text || this.textHtml ? h(this.textTag, {
      domProps: htmlOrText(this.textHtml, this.text)
    }) : false, // Children
    this.normalizeSlot() || false];
    var $content = h();

    if ($contentChildren.some(Boolean)) {
      $content = h(this.contentTag, {
        staticClass: 'carousel-caption',
        class: this.contentClasses
      }, $contentChildren.map(function ($child) {
        return $child || h();
      }));
    }

    return h('div', {
      staticClass: 'carousel-item',
      style: {
        background: this.background || this.bvCarousel.background || null
      },
      attrs: {
        id: this.safeId(),
        role: 'listitem'
      }
    }, [$img, $content]);
  }
});

// @vue/component
var listenOnRootMixin = {
  methods: {
    /**
     * Safely register event listeners on the root Vue node
     * While Vue automatically removes listeners for individual components,
     * when a component registers a listener on root and is destroyed,
     * this orphans a callback because the node is gone,
     * but the root does not clear the callback
     *
     * When registering a `$root` listener, it also registers a listener on
     * the component's `beforeDestroy()` hook to automatically remove the
     * event listener from the `$root` instance
     *
     * @param {string} event
     * @param {function} callback
     */
    listenOnRoot: function listenOnRoot(event, callback) {
      var _this = this;

      this.$root.$on(event, callback);
      this.$on('hook:beforeDestroy', function () {
        _this.$root.$off(event, callback);
      });
    },

    /**
     * Safely register a `$once()` event listener on the root Vue node
     * While Vue automatically removes listeners for individual components,
     * when a component registers a listener on root and is destroyed,
     * this orphans a callback because the node is gone,
     * but the root does not clear the callback
     *
     * When registering a $root listener, it also registers a listener on
     * the component's `beforeDestroy` hook to automatically remove the
     * event listener from the $root instance.
     *
     * @param {string} event
     * @param {function} callback
     */
    listenOnRootOnce: function listenOnRootOnce(event, callback) {
      var _this2 = this;

      this.$root.$once(event, callback);
      this.$on('hook:beforeDestroy', function () {
        _this2.$root.$off(event, callback);
      });
    },

    /**
     * Convenience method for calling `vm.$emit()` on `vm.$root`
     *
     * @param {string} event
     * @param {*} args
     */
    emitOnRoot: function emitOnRoot(event) {
      var _this$$root;

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      (_this$$root = this.$root).$emit.apply(_this$$root, [event].concat(args));
    }
  }
};

// Classes to apply to trigger element

var CLASS_BV_TOGGLE_COLLAPSED = 'collapsed';
var CLASS_BV_TOGGLE_NOT_COLLAPSED = 'not-collapsed'; // Property key for handler storage

var BV_BASE = '__BV_toggle'; // Root event listener property (Function)

var BV_TOGGLE_ROOT_HANDLER = "".concat(BV_BASE, "_HANDLER__"); // Trigger element click handler property (Function)

var BV_TOGGLE_CLICK_HANDLER = "".concat(BV_BASE, "_CLICK__"); // Target visibility state property (Boolean)

var BV_TOGGLE_STATE = "".concat(BV_BASE, "_STATE__"); // Target ID list property (Array)

var BV_TOGGLE_TARGETS = "".concat(BV_BASE, "_TARGETS__"); // Commonly used strings

var STRING_FALSE = 'false';
var STRING_TRUE = 'true'; // Commonly used attribute names

var ATTR_ARIA_CONTROLS = 'aria-controls';
var ATTR_ARIA_EXPANDED = 'aria-expanded';
var ATTR_ROLE = 'role';
var ATTR_TABINDEX = 'tabindex'; // Commonly used style properties

var STYLE_OVERFLOW_ANCHOR = 'overflow-anchor'; // Emitted control event for collapse (emitted to collapse)

var EVENT_TOGGLE = 'bv::toggle::collapse'; // Listen to event for toggle state update (emitted by collapse)

var EVENT_STATE = 'bv::collapse::state'; // Private event emitted on `$root` to ensure the toggle state is always synced
// Gets emitted even if the state of b-collapse has not changed
// This event is NOT to be documented as people should not be using it

var EVENT_STATE_SYNC = 'bv::collapse::sync::state'; // Private event we send to collapse to request state update sync event

var EVENT_STATE_REQUEST = 'bv::request::collapse::state';
var KEYDOWN_KEY_CODES = [CODE_ENTER, CODE_SPACE]; // --- Helper methods ---

var isNonStandardTag$1 = function isNonStandardTag(el) {
  return !arrayIncludes(['button', 'a'], el.tagName.toLowerCase());
};

var getTargets = function getTargets(_ref, el) {
  var modifiers = _ref.modifiers,
      arg = _ref.arg,
      value = _ref.value;
  // Any modifiers are considered target IDs
  var targets = keys$1(modifiers || {}); // If value is a string, split out individual targets (if space delimited)

  value = isString(value) ? value.split(RX_SPACE_SPLIT) : value; // Support target ID as link href (`href="#id"`)

  if (isTag(el.tagName, 'a')) {
    var href = getAttr(el, 'href') || '';

    if (RX_HASH_ID.test(href)) {
      targets.push(href.replace(RX_HASH, ''));
    }
  } // Add ID from `arg` (if provided), and support value
  // as a single string ID or an array of string IDs
  // If `value` is not an array or string, then it gets filtered out


  concat(arg, value).forEach(function (t) {
    return isString(t) && targets.push(t);
  }); // Return only unique and truthy target IDs

  return targets.filter(function (t, index, arr) {
    return t && arr.indexOf(t) === index;
  });
};

var removeClickListener = function removeClickListener(el) {
  var handler = el[BV_TOGGLE_CLICK_HANDLER];

  if (handler) {
    eventOff(el, 'click', handler, EVENT_OPTIONS_PASSIVE);
    eventOff(el, 'keydown', handler, EVENT_OPTIONS_PASSIVE);
  }

  el[BV_TOGGLE_CLICK_HANDLER] = null;
};

var addClickListener = function addClickListener(el, vnode) {
  removeClickListener(el);

  if (vnode.context) {
    var handler = function handler(evt) {
      if (!(evt.type === 'keydown' && !arrayIncludes(KEYDOWN_KEY_CODES, evt.keyCode)) && !isDisabled(el)) {
        var targets = el[BV_TOGGLE_TARGETS] || [];
        targets.forEach(function (target) {
          vnode.context.$root.$emit(EVENT_TOGGLE, target);
        });
      }
    };

    el[BV_TOGGLE_CLICK_HANDLER] = handler;
    eventOn(el, 'click', handler, EVENT_OPTIONS_PASSIVE);

    if (isNonStandardTag$1(el)) {
      eventOn(el, 'keydown', handler, EVENT_OPTIONS_PASSIVE);
    }
  }
};

var removeRootListeners = function removeRootListeners(el, vnode) {
  if (el[BV_TOGGLE_ROOT_HANDLER] && vnode.context) {
    vnode.context.$root.$off([EVENT_STATE, EVENT_STATE_SYNC], el[BV_TOGGLE_ROOT_HANDLER]);
  }

  el[BV_TOGGLE_ROOT_HANDLER] = null;
};

var addRootListeners = function addRootListeners(el, vnode) {
  removeRootListeners(el, vnode);

  if (vnode.context) {
    var handler = function handler(id, state) {
      // `state` will be `true` if target is expanded
      if (arrayIncludes(el[BV_TOGGLE_TARGETS] || [], id)) {
        // Set/Clear 'collapsed' visibility class state
        el[BV_TOGGLE_STATE] = state; // Set `aria-expanded` and class state on trigger element

        setToggleState(el, state);
      }
    };

    el[BV_TOGGLE_ROOT_HANDLER] = handler; // Listen for toggle state changes (public) and sync (private)

    vnode.context.$root.$on([EVENT_STATE, EVENT_STATE_SYNC], handler);
  }
};

var setToggleState = function setToggleState(el, state) {
  // State refers to the visibility of the collapse/sidebar
  if (state) {
    removeClass(el, CLASS_BV_TOGGLE_COLLAPSED);
    addClass(el, CLASS_BV_TOGGLE_NOT_COLLAPSED);
    setAttr(el, ATTR_ARIA_EXPANDED, STRING_TRUE);
  } else {
    removeClass(el, CLASS_BV_TOGGLE_NOT_COLLAPSED);
    addClass(el, CLASS_BV_TOGGLE_COLLAPSED);
    setAttr(el, ATTR_ARIA_EXPANDED, STRING_FALSE);
  }
}; // Reset and remove a property from the provided element


var resetProp = function resetProp(el, prop) {
  el[prop] = null;
  delete el[prop];
}; // Handle directive updates


var handleUpdate = function handleUpdate(el, binding, vnode) {
  /* istanbul ignore next: should never happen */
  if (!isBrowser || !vnode.context) {
    return;
  } // If element is not a button or link, we add `role="button"`
  // and `tabindex="0"` for accessibility reasons


  if (isNonStandardTag$1(el)) {
    if (!hasAttr(el, ATTR_ROLE)) {
      setAttr(el, ATTR_ROLE, 'button');
    }

    if (!hasAttr(el, ATTR_TABINDEX)) {
      setAttr(el, ATTR_TABINDEX, '0');
    }
  } // Ensure the collapse class and `aria-*` attributes persist
  // after element is updated (either by parent re-rendering
  // or changes to this element or its contents)


  setToggleState(el, el[BV_TOGGLE_STATE]); // Parse list of target IDs

  var targets = getTargets(binding, el); // Ensure the `aria-controls` hasn't been overwritten
  // or removed when vnode updates
  // Also ensure to set `overflow-anchor` to `none` to prevent
  // the browser's scroll anchoring behavior

  /* istanbul ignore else */

  if (targets.length > 0) {
    setAttr(el, ATTR_ARIA_CONTROLS, targets.join(' '));
    setStyle(el, STYLE_OVERFLOW_ANCHOR, 'none');
  } else {
    removeAttr(el, ATTR_ARIA_CONTROLS);
    removeStyle(el, STYLE_OVERFLOW_ANCHOR);
  } // Add/Update our click listener(s)
  // Wrap in a `requestAF()` to allow any previous
  // click handling to occur first


  requestAF(function () {
    addClickListener(el, vnode);
  }); // If targets array has changed, update

  if (!looseEqual(targets, el[BV_TOGGLE_TARGETS])) {
    // Update targets array to element storage
    el[BV_TOGGLE_TARGETS] = targets; // Ensure `aria-controls` is up to date
    // Request a state update from targets so that we can
    // ensure expanded state is correct (in most cases)

    targets.forEach(function (target) {
      vnode.context.$root.$emit(EVENT_STATE_REQUEST, target);
    });
  }
};
/*
 * Export our directive
 */


var VBToggle = {
  bind: function bind(el, binding, vnode) {
    // State is initially collapsed until we receive a state event
    el[BV_TOGGLE_STATE] = false; // Assume no targets initially

    el[BV_TOGGLE_TARGETS] = []; // Add our root listeners

    addRootListeners(el, vnode); // Initial update of trigger

    handleUpdate(el, binding, vnode);
  },
  componentUpdated: handleUpdate,
  updated: handleUpdate,
  unbind: function unbind(el, binding, vnode) {
    removeClickListener(el); // Remove our $root listener

    removeRootListeners(el, vnode); // Reset custom props

    resetProp(el, BV_TOGGLE_ROOT_HANDLER);
    resetProp(el, BV_TOGGLE_CLICK_HANDLER);
    resetProp(el, BV_TOGGLE_STATE);
    resetProp(el, BV_TOGGLE_TARGETS); // Reset classes/attrs/styles

    removeClass(el, CLASS_BV_TOGGLE_COLLAPSED);
    removeClass(el, CLASS_BV_TOGGLE_NOT_COLLAPSED);
    removeAttr(el, ATTR_ARIA_EXPANDED);
    removeAttr(el, ATTR_ARIA_CONTROLS);
    removeAttr(el, ATTR_ROLE);
    removeStyle(el, STYLE_OVERFLOW_ANCHOR);
  }
};

/**!
 * @fileOverview Kickass library to create and place poppers near their reference elements.
 * @version 1.16.1
 * @license
 * Copyright (c) 2016 Federico Zivolo and contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
var isBrowser$1 = typeof window !== 'undefined' && typeof document !== 'undefined' && typeof navigator !== 'undefined';

var timeoutDuration = function () {
  var longerTimeoutBrowsers = ['Edge', 'Trident', 'Firefox'];
  for (var i = 0; i < longerTimeoutBrowsers.length; i += 1) {
    if (isBrowser$1 && navigator.userAgent.indexOf(longerTimeoutBrowsers[i]) >= 0) {
      return 1;
    }
  }
  return 0;
}();

function microtaskDebounce(fn) {
  var called = false;
  return function () {
    if (called) {
      return;
    }
    called = true;
    window.Promise.resolve().then(function () {
      called = false;
      fn();
    });
  };
}

function taskDebounce(fn) {
  var scheduled = false;
  return function () {
    if (!scheduled) {
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        fn();
      }, timeoutDuration);
    }
  };
}

var supportsMicroTasks = isBrowser$1 && window.Promise;

/**
* Create a debounced version of a method, that's asynchronously deferred
* but called in the minimum time possible.
*
* @method
* @memberof Popper.Utils
* @argument {Function} fn
* @returns {Function}
*/
var debounce = supportsMicroTasks ? microtaskDebounce : taskDebounce;

/**
 * Check if the given variable is a function
 * @method
 * @memberof Popper.Utils
 * @argument {Any} functionToCheck - variable to check
 * @returns {Boolean} answer to: is a function?
 */
function isFunction$2(functionToCheck) {
  var getType = {};
  return functionToCheck && getType.toString.call(functionToCheck) === '[object Function]';
}

/**
 * Get CSS computed property of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Eement} element
 * @argument {String} property
 */
function getStyleComputedProperty(element, property) {
  if (element.nodeType !== 1) {
    return [];
  }
  // NOTE: 1 DOM access here
  var window = element.ownerDocument.defaultView;
  var css = window.getComputedStyle(element, null);
  return property ? css[property] : css;
}

/**
 * Returns the parentNode or the host of the element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} parent
 */
function getParentNode(element) {
  if (element.nodeName === 'HTML') {
    return element;
  }
  return element.parentNode || element.host;
}

/**
 * Returns the scrolling parent of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} scroll parent
 */
function getScrollParent(element) {
  // Return body, `getScroll` will take care to get the correct `scrollTop` from it
  if (!element) {
    return document.body;
  }

  switch (element.nodeName) {
    case 'HTML':
    case 'BODY':
      return element.ownerDocument.body;
    case '#document':
      return element.body;
  }

  // Firefox want us to check `-x` and `-y` variations as well

  var _getStyleComputedProp = getStyleComputedProperty(element),
      overflow = _getStyleComputedProp.overflow,
      overflowX = _getStyleComputedProp.overflowX,
      overflowY = _getStyleComputedProp.overflowY;

  if (/(auto|scroll|overlay)/.test(overflow + overflowY + overflowX)) {
    return element;
  }

  return getScrollParent(getParentNode(element));
}

/**
 * Returns the reference node of the reference object, or the reference object itself.
 * @method
 * @memberof Popper.Utils
 * @param {Element|Object} reference - the reference element (the popper will be relative to this)
 * @returns {Element} parent
 */
function getReferenceNode(reference) {
  return reference && reference.referenceNode ? reference.referenceNode : reference;
}

var isIE11 = isBrowser$1 && !!(window.MSInputMethodContext && document.documentMode);
var isIE10 = isBrowser$1 && /MSIE 10/.test(navigator.userAgent);

/**
 * Determines if the browser is Internet Explorer
 * @method
 * @memberof Popper.Utils
 * @param {Number} version to check
 * @returns {Boolean} isIE
 */
function isIE$1(version) {
  if (version === 11) {
    return isIE11;
  }
  if (version === 10) {
    return isIE10;
  }
  return isIE11 || isIE10;
}

/**
 * Returns the offset parent of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} offset parent
 */
function getOffsetParent(element) {
  if (!element) {
    return document.documentElement;
  }

  var noOffsetParent = isIE$1(10) ? document.body : null;

  // NOTE: 1 DOM access here
  var offsetParent = element.offsetParent || null;
  // Skip hidden elements which don't have an offsetParent
  while (offsetParent === noOffsetParent && element.nextElementSibling) {
    offsetParent = (element = element.nextElementSibling).offsetParent;
  }

  var nodeName = offsetParent && offsetParent.nodeName;

  if (!nodeName || nodeName === 'BODY' || nodeName === 'HTML') {
    return element ? element.ownerDocument.documentElement : document.documentElement;
  }

  // .offsetParent will return the closest TH, TD or TABLE in case
  // no offsetParent is present, I hate this job...
  if (['TH', 'TD', 'TABLE'].indexOf(offsetParent.nodeName) !== -1 && getStyleComputedProperty(offsetParent, 'position') === 'static') {
    return getOffsetParent(offsetParent);
  }

  return offsetParent;
}

function isOffsetContainer(element) {
  var nodeName = element.nodeName;

  if (nodeName === 'BODY') {
    return false;
  }
  return nodeName === 'HTML' || getOffsetParent(element.firstElementChild) === element;
}

/**
 * Finds the root node (document, shadowDOM root) of the given element
 * @method
 * @memberof Popper.Utils
 * @argument {Element} node
 * @returns {Element} root node
 */
function getRoot(node) {
  if (node.parentNode !== null) {
    return getRoot(node.parentNode);
  }

  return node;
}

/**
 * Finds the offset parent common to the two provided nodes
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element1
 * @argument {Element} element2
 * @returns {Element} common offset parent
 */
function findCommonOffsetParent(element1, element2) {
  // This check is needed to avoid errors in case one of the elements isn't defined for any reason
  if (!element1 || !element1.nodeType || !element2 || !element2.nodeType) {
    return document.documentElement;
  }

  // Here we make sure to give as "start" the element that comes first in the DOM
  var order = element1.compareDocumentPosition(element2) & Node.DOCUMENT_POSITION_FOLLOWING;
  var start = order ? element1 : element2;
  var end = order ? element2 : element1;

  // Get common ancestor container
  var range = document.createRange();
  range.setStart(start, 0);
  range.setEnd(end, 0);
  var commonAncestorContainer = range.commonAncestorContainer;

  // Both nodes are inside #document

  if (element1 !== commonAncestorContainer && element2 !== commonAncestorContainer || start.contains(end)) {
    if (isOffsetContainer(commonAncestorContainer)) {
      return commonAncestorContainer;
    }

    return getOffsetParent(commonAncestorContainer);
  }

  // one of the nodes is inside shadowDOM, find which one
  var element1root = getRoot(element1);
  if (element1root.host) {
    return findCommonOffsetParent(element1root.host, element2);
  } else {
    return findCommonOffsetParent(element1, getRoot(element2).host);
  }
}

/**
 * Gets the scroll value of the given element in the given side (top and left)
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @argument {String} side `top` or `left`
 * @returns {number} amount of scrolled pixels
 */
function getScroll(element) {
  var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'top';

  var upperSide = side === 'top' ? 'scrollTop' : 'scrollLeft';
  var nodeName = element.nodeName;

  if (nodeName === 'BODY' || nodeName === 'HTML') {
    var html = element.ownerDocument.documentElement;
    var scrollingElement = element.ownerDocument.scrollingElement || html;
    return scrollingElement[upperSide];
  }

  return element[upperSide];
}

/*
 * Sum or subtract the element scroll values (left and top) from a given rect object
 * @method
 * @memberof Popper.Utils
 * @param {Object} rect - Rect object you want to change
 * @param {HTMLElement} element - The element from the function reads the scroll values
 * @param {Boolean} subtract - set to true if you want to subtract the scroll values
 * @return {Object} rect - The modifier rect object
 */
function includeScroll(rect, element) {
  var subtract = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var scrollTop = getScroll(element, 'top');
  var scrollLeft = getScroll(element, 'left');
  var modifier = subtract ? -1 : 1;
  rect.top += scrollTop * modifier;
  rect.bottom += scrollTop * modifier;
  rect.left += scrollLeft * modifier;
  rect.right += scrollLeft * modifier;
  return rect;
}

/*
 * Helper to detect borders of a given element
 * @method
 * @memberof Popper.Utils
 * @param {CSSStyleDeclaration} styles
 * Result of `getStyleComputedProperty` on the given element
 * @param {String} axis - `x` or `y`
 * @return {number} borders - The borders size of the given axis
 */

function getBordersSize(styles, axis) {
  var sideA = axis === 'x' ? 'Left' : 'Top';
  var sideB = sideA === 'Left' ? 'Right' : 'Bottom';

  return parseFloat(styles['border' + sideA + 'Width']) + parseFloat(styles['border' + sideB + 'Width']);
}

function getSize(axis, body, html, computedStyle) {
  return Math.max(body['offset' + axis], body['scroll' + axis], html['client' + axis], html['offset' + axis], html['scroll' + axis], isIE$1(10) ? parseInt(html['offset' + axis]) + parseInt(computedStyle['margin' + (axis === 'Height' ? 'Top' : 'Left')]) + parseInt(computedStyle['margin' + (axis === 'Height' ? 'Bottom' : 'Right')]) : 0);
}

function getWindowSizes(document) {
  var body = document.body;
  var html = document.documentElement;
  var computedStyle = isIE$1(10) && getComputedStyle(html);

  return {
    height: getSize('Height', body, html, computedStyle),
    width: getSize('Width', body, html, computedStyle)
  };
}

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();





var defineProperty$2 = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

/**
 * Given element offsets, generate an output similar to getBoundingClientRect
 * @method
 * @memberof Popper.Utils
 * @argument {Object} offsets
 * @returns {Object} ClientRect like output
 */
function getClientRect(offsets) {
  return _extends({}, offsets, {
    right: offsets.left + offsets.width,
    bottom: offsets.top + offsets.height
  });
}

/**
 * Get bounding client rect of given element
 * @method
 * @memberof Popper.Utils
 * @param {HTMLElement} element
 * @return {Object} client rect
 */
function getBoundingClientRect(element) {
  var rect = {};

  // IE10 10 FIX: Please, don't ask, the element isn't
  // considered in DOM in some circumstances...
  // This isn't reproducible in IE10 compatibility mode of IE11
  try {
    if (isIE$1(10)) {
      rect = element.getBoundingClientRect();
      var scrollTop = getScroll(element, 'top');
      var scrollLeft = getScroll(element, 'left');
      rect.top += scrollTop;
      rect.left += scrollLeft;
      rect.bottom += scrollTop;
      rect.right += scrollLeft;
    } else {
      rect = element.getBoundingClientRect();
    }
  } catch (e) {}

  var result = {
    left: rect.left,
    top: rect.top,
    width: rect.right - rect.left,
    height: rect.bottom - rect.top
  };

  // subtract scrollbar size from sizes
  var sizes = element.nodeName === 'HTML' ? getWindowSizes(element.ownerDocument) : {};
  var width = sizes.width || element.clientWidth || result.width;
  var height = sizes.height || element.clientHeight || result.height;

  var horizScrollbar = element.offsetWidth - width;
  var vertScrollbar = element.offsetHeight - height;

  // if an hypothetical scrollbar is detected, we must be sure it's not a `border`
  // we make this check conditional for performance reasons
  if (horizScrollbar || vertScrollbar) {
    var styles = getStyleComputedProperty(element);
    horizScrollbar -= getBordersSize(styles, 'x');
    vertScrollbar -= getBordersSize(styles, 'y');

    result.width -= horizScrollbar;
    result.height -= vertScrollbar;
  }

  return getClientRect(result);
}

function getOffsetRectRelativeToArbitraryNode(children, parent) {
  var fixedPosition = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  var isIE10 = isIE$1(10);
  var isHTML = parent.nodeName === 'HTML';
  var childrenRect = getBoundingClientRect(children);
  var parentRect = getBoundingClientRect(parent);
  var scrollParent = getScrollParent(children);

  var styles = getStyleComputedProperty(parent);
  var borderTopWidth = parseFloat(styles.borderTopWidth);
  var borderLeftWidth = parseFloat(styles.borderLeftWidth);

  // In cases where the parent is fixed, we must ignore negative scroll in offset calc
  if (fixedPosition && isHTML) {
    parentRect.top = Math.max(parentRect.top, 0);
    parentRect.left = Math.max(parentRect.left, 0);
  }
  var offsets = getClientRect({
    top: childrenRect.top - parentRect.top - borderTopWidth,
    left: childrenRect.left - parentRect.left - borderLeftWidth,
    width: childrenRect.width,
    height: childrenRect.height
  });
  offsets.marginTop = 0;
  offsets.marginLeft = 0;

  // Subtract margins of documentElement in case it's being used as parent
  // we do this only on HTML because it's the only element that behaves
  // differently when margins are applied to it. The margins are included in
  // the box of the documentElement, in the other cases not.
  if (!isIE10 && isHTML) {
    var marginTop = parseFloat(styles.marginTop);
    var marginLeft = parseFloat(styles.marginLeft);

    offsets.top -= borderTopWidth - marginTop;
    offsets.bottom -= borderTopWidth - marginTop;
    offsets.left -= borderLeftWidth - marginLeft;
    offsets.right -= borderLeftWidth - marginLeft;

    // Attach marginTop and marginLeft because in some circumstances we may need them
    offsets.marginTop = marginTop;
    offsets.marginLeft = marginLeft;
  }

  if (isIE10 && !fixedPosition ? parent.contains(scrollParent) : parent === scrollParent && scrollParent.nodeName !== 'BODY') {
    offsets = includeScroll(offsets, parent);
  }

  return offsets;
}

function getViewportOffsetRectRelativeToArtbitraryNode(element) {
  var excludeScroll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  var html = element.ownerDocument.documentElement;
  var relativeOffset = getOffsetRectRelativeToArbitraryNode(element, html);
  var width = Math.max(html.clientWidth, window.innerWidth || 0);
  var height = Math.max(html.clientHeight, window.innerHeight || 0);

  var scrollTop = !excludeScroll ? getScroll(html) : 0;
  var scrollLeft = !excludeScroll ? getScroll(html, 'left') : 0;

  var offset = {
    top: scrollTop - relativeOffset.top + relativeOffset.marginTop,
    left: scrollLeft - relativeOffset.left + relativeOffset.marginLeft,
    width: width,
    height: height
  };

  return getClientRect(offset);
}

/**
 * Check if the given element is fixed or is inside a fixed parent
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @argument {Element} customContainer
 * @returns {Boolean} answer to "isFixed?"
 */
function isFixed(element) {
  var nodeName = element.nodeName;
  if (nodeName === 'BODY' || nodeName === 'HTML') {
    return false;
  }
  if (getStyleComputedProperty(element, 'position') === 'fixed') {
    return true;
  }
  var parentNode = getParentNode(element);
  if (!parentNode) {
    return false;
  }
  return isFixed(parentNode);
}

/**
 * Finds the first parent of an element that has a transformed property defined
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Element} first transformed parent or documentElement
 */

function getFixedPositionOffsetParent(element) {
  // This check is needed to avoid errors in case one of the elements isn't defined for any reason
  if (!element || !element.parentElement || isIE$1()) {
    return document.documentElement;
  }
  var el = element.parentElement;
  while (el && getStyleComputedProperty(el, 'transform') === 'none') {
    el = el.parentElement;
  }
  return el || document.documentElement;
}

/**
 * Computed the boundaries limits and return them
 * @method
 * @memberof Popper.Utils
 * @param {HTMLElement} popper
 * @param {HTMLElement} reference
 * @param {number} padding
 * @param {HTMLElement} boundariesElement - Element used to define the boundaries
 * @param {Boolean} fixedPosition - Is in fixed position mode
 * @returns {Object} Coordinates of the boundaries
 */
function getBoundaries(popper, reference, padding, boundariesElement) {
  var fixedPosition = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;

  // NOTE: 1 DOM access here

  var boundaries = { top: 0, left: 0 };
  var offsetParent = fixedPosition ? getFixedPositionOffsetParent(popper) : findCommonOffsetParent(popper, getReferenceNode(reference));

  // Handle viewport case
  if (boundariesElement === 'viewport') {
    boundaries = getViewportOffsetRectRelativeToArtbitraryNode(offsetParent, fixedPosition);
  } else {
    // Handle other cases based on DOM element used as boundaries
    var boundariesNode = void 0;
    if (boundariesElement === 'scrollParent') {
      boundariesNode = getScrollParent(getParentNode(reference));
      if (boundariesNode.nodeName === 'BODY') {
        boundariesNode = popper.ownerDocument.documentElement;
      }
    } else if (boundariesElement === 'window') {
      boundariesNode = popper.ownerDocument.documentElement;
    } else {
      boundariesNode = boundariesElement;
    }

    var offsets = getOffsetRectRelativeToArbitraryNode(boundariesNode, offsetParent, fixedPosition);

    // In case of HTML, we need a different computation
    if (boundariesNode.nodeName === 'HTML' && !isFixed(offsetParent)) {
      var _getWindowSizes = getWindowSizes(popper.ownerDocument),
          height = _getWindowSizes.height,
          width = _getWindowSizes.width;

      boundaries.top += offsets.top - offsets.marginTop;
      boundaries.bottom = height + offsets.top;
      boundaries.left += offsets.left - offsets.marginLeft;
      boundaries.right = width + offsets.left;
    } else {
      // for all the other DOM elements, this one is good
      boundaries = offsets;
    }
  }

  // Add paddings
  padding = padding || 0;
  var isPaddingNumber = typeof padding === 'number';
  boundaries.left += isPaddingNumber ? padding : padding.left || 0;
  boundaries.top += isPaddingNumber ? padding : padding.top || 0;
  boundaries.right -= isPaddingNumber ? padding : padding.right || 0;
  boundaries.bottom -= isPaddingNumber ? padding : padding.bottom || 0;

  return boundaries;
}

function getArea(_ref) {
  var width = _ref.width,
      height = _ref.height;

  return width * height;
}

/**
 * Utility used to transform the `auto` placement to the placement with more
 * available space.
 * @method
 * @memberof Popper.Utils
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function computeAutoPlacement(placement, refRect, popper, reference, boundariesElement) {
  var padding = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;

  if (placement.indexOf('auto') === -1) {
    return placement;
  }

  var boundaries = getBoundaries(popper, reference, padding, boundariesElement);

  var rects = {
    top: {
      width: boundaries.width,
      height: refRect.top - boundaries.top
    },
    right: {
      width: boundaries.right - refRect.right,
      height: boundaries.height
    },
    bottom: {
      width: boundaries.width,
      height: boundaries.bottom - refRect.bottom
    },
    left: {
      width: refRect.left - boundaries.left,
      height: boundaries.height
    }
  };

  var sortedAreas = Object.keys(rects).map(function (key) {
    return _extends({
      key: key
    }, rects[key], {
      area: getArea(rects[key])
    });
  }).sort(function (a, b) {
    return b.area - a.area;
  });

  var filteredAreas = sortedAreas.filter(function (_ref2) {
    var width = _ref2.width,
        height = _ref2.height;
    return width >= popper.clientWidth && height >= popper.clientHeight;
  });

  var computedPlacement = filteredAreas.length > 0 ? filteredAreas[0].key : sortedAreas[0].key;

  var variation = placement.split('-')[1];

  return computedPlacement + (variation ? '-' + variation : '');
}

/**
 * Get offsets to the reference element
 * @method
 * @memberof Popper.Utils
 * @param {Object} state
 * @param {Element} popper - the popper element
 * @param {Element} reference - the reference element (the popper will be relative to this)
 * @param {Element} fixedPosition - is in fixed position mode
 * @returns {Object} An object containing the offsets which will be applied to the popper
 */
function getReferenceOffsets(state, popper, reference) {
  var fixedPosition = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;

  var commonOffsetParent = fixedPosition ? getFixedPositionOffsetParent(popper) : findCommonOffsetParent(popper, getReferenceNode(reference));
  return getOffsetRectRelativeToArbitraryNode(reference, commonOffsetParent, fixedPosition);
}

/**
 * Get the outer sizes of the given element (offset size + margins)
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element
 * @returns {Object} object containing width and height properties
 */
function getOuterSizes(element) {
  var window = element.ownerDocument.defaultView;
  var styles = window.getComputedStyle(element);
  var x = parseFloat(styles.marginTop || 0) + parseFloat(styles.marginBottom || 0);
  var y = parseFloat(styles.marginLeft || 0) + parseFloat(styles.marginRight || 0);
  var result = {
    width: element.offsetWidth + y,
    height: element.offsetHeight + x
  };
  return result;
}

/**
 * Get the opposite placement of the given one
 * @method
 * @memberof Popper.Utils
 * @argument {String} placement
 * @returns {String} flipped placement
 */
function getOppositePlacement(placement) {
  var hash = { left: 'right', right: 'left', bottom: 'top', top: 'bottom' };
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash[matched];
  });
}

/**
 * Get offsets to the popper
 * @method
 * @memberof Popper.Utils
 * @param {Object} position - CSS position the Popper will get applied
 * @param {HTMLElement} popper - the popper element
 * @param {Object} referenceOffsets - the reference offsets (the popper will be relative to this)
 * @param {String} placement - one of the valid placement options
 * @returns {Object} popperOffsets - An object containing the offsets which will be applied to the popper
 */
function getPopperOffsets(popper, referenceOffsets, placement) {
  placement = placement.split('-')[0];

  // Get popper node sizes
  var popperRect = getOuterSizes(popper);

  // Add position, width and height to our offsets object
  var popperOffsets = {
    width: popperRect.width,
    height: popperRect.height
  };

  // depending by the popper placement we have to compute its offsets slightly differently
  var isHoriz = ['right', 'left'].indexOf(placement) !== -1;
  var mainSide = isHoriz ? 'top' : 'left';
  var secondarySide = isHoriz ? 'left' : 'top';
  var measurement = isHoriz ? 'height' : 'width';
  var secondaryMeasurement = !isHoriz ? 'height' : 'width';

  popperOffsets[mainSide] = referenceOffsets[mainSide] + referenceOffsets[measurement] / 2 - popperRect[measurement] / 2;
  if (placement === secondarySide) {
    popperOffsets[secondarySide] = referenceOffsets[secondarySide] - popperRect[secondaryMeasurement];
  } else {
    popperOffsets[secondarySide] = referenceOffsets[getOppositePlacement(secondarySide)];
  }

  return popperOffsets;
}

/**
 * Mimics the `find` method of Array
 * @method
 * @memberof Popper.Utils
 * @argument {Array} arr
 * @argument prop
 * @argument value
 * @returns index or -1
 */
function find(arr, check) {
  // use native find if supported
  if (Array.prototype.find) {
    return arr.find(check);
  }

  // use `filter` to obtain the same behavior of `find`
  return arr.filter(check)[0];
}

/**
 * Return the index of the matching object
 * @method
 * @memberof Popper.Utils
 * @argument {Array} arr
 * @argument prop
 * @argument value
 * @returns index or -1
 */
function findIndex(arr, prop, value) {
  // use native findIndex if supported
  if (Array.prototype.findIndex) {
    return arr.findIndex(function (cur) {
      return cur[prop] === value;
    });
  }

  // use `find` + `indexOf` if `findIndex` isn't supported
  var match = find(arr, function (obj) {
    return obj[prop] === value;
  });
  return arr.indexOf(match);
}

/**
 * Loop trough the list of modifiers and run them in order,
 * each of them will then edit the data object.
 * @method
 * @memberof Popper.Utils
 * @param {dataObject} data
 * @param {Array} modifiers
 * @param {String} ends - Optional modifier name used as stopper
 * @returns {dataObject}
 */
function runModifiers(modifiers, data, ends) {
  var modifiersToRun = ends === undefined ? modifiers : modifiers.slice(0, findIndex(modifiers, 'name', ends));

  modifiersToRun.forEach(function (modifier) {
    if (modifier['function']) {
      // eslint-disable-line dot-notation
      console.warn('`modifier.function` is deprecated, use `modifier.fn`!');
    }
    var fn = modifier['function'] || modifier.fn; // eslint-disable-line dot-notation
    if (modifier.enabled && isFunction$2(fn)) {
      // Add properties to offsets to make them a complete clientRect object
      // we do this before each modifier to make sure the previous one doesn't
      // mess with these values
      data.offsets.popper = getClientRect(data.offsets.popper);
      data.offsets.reference = getClientRect(data.offsets.reference);

      data = fn(data, modifier);
    }
  });

  return data;
}

/**
 * Updates the position of the popper, computing the new offsets and applying
 * the new style.<br />
 * Prefer `scheduleUpdate` over `update` because of performance reasons.
 * @method
 * @memberof Popper
 */
function update() {
  // if popper is destroyed, don't perform any further update
  if (this.state.isDestroyed) {
    return;
  }

  var data = {
    instance: this,
    styles: {},
    arrowStyles: {},
    attributes: {},
    flipped: false,
    offsets: {}
  };

  // compute reference element offsets
  data.offsets.reference = getReferenceOffsets(this.state, this.popper, this.reference, this.options.positionFixed);

  // compute auto placement, store placement inside the data object,
  // modifiers will be able to edit `placement` if needed
  // and refer to originalPlacement to know the original value
  data.placement = computeAutoPlacement(this.options.placement, data.offsets.reference, this.popper, this.reference, this.options.modifiers.flip.boundariesElement, this.options.modifiers.flip.padding);

  // store the computed placement inside `originalPlacement`
  data.originalPlacement = data.placement;

  data.positionFixed = this.options.positionFixed;

  // compute the popper offsets
  data.offsets.popper = getPopperOffsets(this.popper, data.offsets.reference, data.placement);

  data.offsets.popper.position = this.options.positionFixed ? 'fixed' : 'absolute';

  // run the modifiers
  data = runModifiers(this.modifiers, data);

  // the first `update` will call `onCreate` callback
  // the other ones will call `onUpdate` callback
  if (!this.state.isCreated) {
    this.state.isCreated = true;
    this.options.onCreate(data);
  } else {
    this.options.onUpdate(data);
  }
}

/**
 * Helper used to know if the given modifier is enabled.
 * @method
 * @memberof Popper.Utils
 * @returns {Boolean}
 */
function isModifierEnabled(modifiers, modifierName) {
  return modifiers.some(function (_ref) {
    var name = _ref.name,
        enabled = _ref.enabled;
    return enabled && name === modifierName;
  });
}

/**
 * Get the prefixed supported property name
 * @method
 * @memberof Popper.Utils
 * @argument {String} property (camelCase)
 * @returns {String} prefixed property (camelCase or PascalCase, depending on the vendor prefix)
 */
function getSupportedPropertyName(property) {
  var prefixes = [false, 'ms', 'Webkit', 'Moz', 'O'];
  var upperProp = property.charAt(0).toUpperCase() + property.slice(1);

  for (var i = 0; i < prefixes.length; i++) {
    var prefix = prefixes[i];
    var toCheck = prefix ? '' + prefix + upperProp : property;
    if (typeof document.body.style[toCheck] !== 'undefined') {
      return toCheck;
    }
  }
  return null;
}

/**
 * Destroys the popper.
 * @method
 * @memberof Popper
 */
function destroy$1() {
  this.state.isDestroyed = true;

  // touch DOM only if `applyStyle` modifier is enabled
  if (isModifierEnabled(this.modifiers, 'applyStyle')) {
    this.popper.removeAttribute('x-placement');
    this.popper.style.position = '';
    this.popper.style.top = '';
    this.popper.style.left = '';
    this.popper.style.right = '';
    this.popper.style.bottom = '';
    this.popper.style.willChange = '';
    this.popper.style[getSupportedPropertyName('transform')] = '';
  }

  this.disableEventListeners();

  // remove the popper if user explicitly asked for the deletion on destroy
  // do not use `remove` because IE11 doesn't support it
  if (this.options.removeOnDestroy) {
    this.popper.parentNode.removeChild(this.popper);
  }
  return this;
}

/**
 * Get the window associated with the element
 * @argument {Element} element
 * @returns {Window}
 */
function getWindow(element) {
  var ownerDocument = element.ownerDocument;
  return ownerDocument ? ownerDocument.defaultView : window;
}

function attachToScrollParents(scrollParent, event, callback, scrollParents) {
  var isBody = scrollParent.nodeName === 'BODY';
  var target = isBody ? scrollParent.ownerDocument.defaultView : scrollParent;
  target.addEventListener(event, callback, { passive: true });

  if (!isBody) {
    attachToScrollParents(getScrollParent(target.parentNode), event, callback, scrollParents);
  }
  scrollParents.push(target);
}

/**
 * Setup needed event listeners used to update the popper position
 * @method
 * @memberof Popper.Utils
 * @private
 */
function setupEventListeners(reference, options, state, updateBound) {
  // Resize event listener on window
  state.updateBound = updateBound;
  getWindow(reference).addEventListener('resize', state.updateBound, { passive: true });

  // Scroll event listener on scroll parents
  var scrollElement = getScrollParent(reference);
  attachToScrollParents(scrollElement, 'scroll', state.updateBound, state.scrollParents);
  state.scrollElement = scrollElement;
  state.eventsEnabled = true;

  return state;
}

/**
 * It will add resize/scroll events and start recalculating
 * position of the popper element when they are triggered.
 * @method
 * @memberof Popper
 */
function enableEventListeners() {
  if (!this.state.eventsEnabled) {
    this.state = setupEventListeners(this.reference, this.options, this.state, this.scheduleUpdate);
  }
}

/**
 * Remove event listeners used to update the popper position
 * @method
 * @memberof Popper.Utils
 * @private
 */
function removeEventListeners(reference, state) {
  // Remove resize event listener on window
  getWindow(reference).removeEventListener('resize', state.updateBound);

  // Remove scroll event listener on scroll parents
  state.scrollParents.forEach(function (target) {
    target.removeEventListener('scroll', state.updateBound);
  });

  // Reset state
  state.updateBound = null;
  state.scrollParents = [];
  state.scrollElement = null;
  state.eventsEnabled = false;
  return state;
}

/**
 * It will remove resize/scroll events and won't recalculate popper position
 * when they are triggered. It also won't trigger `onUpdate` callback anymore,
 * unless you call `update` method manually.
 * @method
 * @memberof Popper
 */
function disableEventListeners() {
  if (this.state.eventsEnabled) {
    cancelAnimationFrame(this.scheduleUpdate);
    this.state = removeEventListeners(this.reference, this.state);
  }
}

/**
 * Tells if a given input is a number
 * @method
 * @memberof Popper.Utils
 * @param {*} input to check
 * @return {Boolean}
 */
function isNumeric$1(n) {
  return n !== '' && !isNaN(parseFloat(n)) && isFinite(n);
}

/**
 * Set the style to the given popper
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element - Element to apply the style to
 * @argument {Object} styles
 * Object with a list of properties and values which will be applied to the element
 */
function setStyles(element, styles) {
  Object.keys(styles).forEach(function (prop) {
    var unit = '';
    // add unit if the value is numeric and is one of the following
    if (['width', 'height', 'top', 'right', 'bottom', 'left'].indexOf(prop) !== -1 && isNumeric$1(styles[prop])) {
      unit = 'px';
    }
    element.style[prop] = styles[prop] + unit;
  });
}

/**
 * Set the attributes to the given popper
 * @method
 * @memberof Popper.Utils
 * @argument {Element} element - Element to apply the attributes to
 * @argument {Object} styles
 * Object with a list of properties and values which will be applied to the element
 */
function setAttributes(element, attributes) {
  Object.keys(attributes).forEach(function (prop) {
    var value = attributes[prop];
    if (value !== false) {
      element.setAttribute(prop, attributes[prop]);
    } else {
      element.removeAttribute(prop);
    }
  });
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} data.styles - List of style properties - values to apply to popper element
 * @argument {Object} data.attributes - List of attribute properties - values to apply to popper element
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The same data object
 */
function applyStyle(data) {
  // any property present in `data.styles` will be applied to the popper,
  // in this way we can make the 3rd party modifiers add custom styles to it
  // Be aware, modifiers could override the properties defined in the previous
  // lines of this modifier!
  setStyles(data.instance.popper, data.styles);

  // any property present in `data.attributes` will be applied to the popper,
  // they will be set as HTML attributes of the element
  setAttributes(data.instance.popper, data.attributes);

  // if arrowElement is defined and arrowStyles has some properties
  if (data.arrowElement && Object.keys(data.arrowStyles).length) {
    setStyles(data.arrowElement, data.arrowStyles);
  }

  return data;
}

/**
 * Set the x-placement attribute before everything else because it could be used
 * to add margins to the popper margins needs to be calculated to get the
 * correct popper offsets.
 * @method
 * @memberof Popper.modifiers
 * @param {HTMLElement} reference - The reference element used to position the popper
 * @param {HTMLElement} popper - The HTML element used as popper
 * @param {Object} options - Popper.js options
 */
function applyStyleOnLoad(reference, popper, options, modifierOptions, state) {
  // compute reference element offsets
  var referenceOffsets = getReferenceOffsets(state, popper, reference, options.positionFixed);

  // compute auto placement, store placement inside the data object,
  // modifiers will be able to edit `placement` if needed
  // and refer to originalPlacement to know the original value
  var placement = computeAutoPlacement(options.placement, referenceOffsets, popper, reference, options.modifiers.flip.boundariesElement, options.modifiers.flip.padding);

  popper.setAttribute('x-placement', placement);

  // Apply `position` to popper before anything else because
  // without the position applied we can't guarantee correct computations
  setStyles(popper, { position: options.positionFixed ? 'fixed' : 'absolute' });

  return options;
}

/**
 * @function
 * @memberof Popper.Utils
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Boolean} shouldRound - If the offsets should be rounded at all
 * @returns {Object} The popper's position offsets rounded
 *
 * The tale of pixel-perfect positioning. It's still not 100% perfect, but as
 * good as it can be within reason.
 * Discussion here: https://github.com/FezVrasta/popper.js/pull/715
 *
 * Low DPI screens cause a popper to be blurry if not using full pixels (Safari
 * as well on High DPI screens).
 *
 * Firefox prefers no rounding for positioning and does not have blurriness on
 * high DPI screens.
 *
 * Only horizontal placement and left/right values need to be considered.
 */
function getRoundedOffsets(data, shouldRound) {
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;
  var round = Math.round,
      floor = Math.floor;

  var noRound = function noRound(v) {
    return v;
  };

  var referenceWidth = round(reference.width);
  var popperWidth = round(popper.width);

  var isVertical = ['left', 'right'].indexOf(data.placement) !== -1;
  var isVariation = data.placement.indexOf('-') !== -1;
  var sameWidthParity = referenceWidth % 2 === popperWidth % 2;
  var bothOddWidth = referenceWidth % 2 === 1 && popperWidth % 2 === 1;

  var horizontalToInteger = !shouldRound ? noRound : isVertical || isVariation || sameWidthParity ? round : floor;
  var verticalToInteger = !shouldRound ? noRound : round;

  return {
    left: horizontalToInteger(bothOddWidth && !isVariation && shouldRound ? popper.left - 1 : popper.left),
    top: verticalToInteger(popper.top),
    bottom: verticalToInteger(popper.bottom),
    right: horizontalToInteger(popper.right)
  };
}

var isFirefox = isBrowser$1 && /Firefox/i.test(navigator.userAgent);

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function computeStyle(data, options) {
  var x = options.x,
      y = options.y;
  var popper = data.offsets.popper;

  // Remove this legacy support in Popper.js v2

  var legacyGpuAccelerationOption = find(data.instance.modifiers, function (modifier) {
    return modifier.name === 'applyStyle';
  }).gpuAcceleration;
  if (legacyGpuAccelerationOption !== undefined) {
    console.warn('WARNING: `gpuAcceleration` option moved to `computeStyle` modifier and will not be supported in future versions of Popper.js!');
  }
  var gpuAcceleration = legacyGpuAccelerationOption !== undefined ? legacyGpuAccelerationOption : options.gpuAcceleration;

  var offsetParent = getOffsetParent(data.instance.popper);
  var offsetParentRect = getBoundingClientRect(offsetParent);

  // Styles
  var styles = {
    position: popper.position
  };

  var offsets = getRoundedOffsets(data, window.devicePixelRatio < 2 || !isFirefox);

  var sideA = x === 'bottom' ? 'top' : 'bottom';
  var sideB = y === 'right' ? 'left' : 'right';

  // if gpuAcceleration is set to `true` and transform is supported,
  //  we use `translate3d` to apply the position to the popper we
  // automatically use the supported prefixed version if needed
  var prefixedProperty = getSupportedPropertyName('transform');

  // now, let's make a step back and look at this code closely (wtf?)
  // If the content of the popper grows once it's been positioned, it
  // may happen that the popper gets misplaced because of the new content
  // overflowing its reference element
  // To avoid this problem, we provide two options (x and y), which allow
  // the consumer to define the offset origin.
  // If we position a popper on top of a reference element, we can set
  // `x` to `top` to make the popper grow towards its top instead of
  // its bottom.
  var left = void 0,
      top = void 0;
  if (sideA === 'bottom') {
    // when offsetParent is <html> the positioning is relative to the bottom of the screen (excluding the scrollbar)
    // and not the bottom of the html element
    if (offsetParent.nodeName === 'HTML') {
      top = -offsetParent.clientHeight + offsets.bottom;
    } else {
      top = -offsetParentRect.height + offsets.bottom;
    }
  } else {
    top = offsets.top;
  }
  if (sideB === 'right') {
    if (offsetParent.nodeName === 'HTML') {
      left = -offsetParent.clientWidth + offsets.right;
    } else {
      left = -offsetParentRect.width + offsets.right;
    }
  } else {
    left = offsets.left;
  }
  if (gpuAcceleration && prefixedProperty) {
    styles[prefixedProperty] = 'translate3d(' + left + 'px, ' + top + 'px, 0)';
    styles[sideA] = 0;
    styles[sideB] = 0;
    styles.willChange = 'transform';
  } else {
    // othwerise, we use the standard `top`, `left`, `bottom` and `right` properties
    var invertTop = sideA === 'bottom' ? -1 : 1;
    var invertLeft = sideB === 'right' ? -1 : 1;
    styles[sideA] = top * invertTop;
    styles[sideB] = left * invertLeft;
    styles.willChange = sideA + ', ' + sideB;
  }

  // Attributes
  var attributes = {
    'x-placement': data.placement
  };

  // Update `data` attributes, styles and arrowStyles
  data.attributes = _extends({}, attributes, data.attributes);
  data.styles = _extends({}, styles, data.styles);
  data.arrowStyles = _extends({}, data.offsets.arrow, data.arrowStyles);

  return data;
}

/**
 * Helper used to know if the given modifier depends from another one.<br />
 * It checks if the needed modifier is listed and enabled.
 * @method
 * @memberof Popper.Utils
 * @param {Array} modifiers - list of modifiers
 * @param {String} requestingName - name of requesting modifier
 * @param {String} requestedName - name of requested modifier
 * @returns {Boolean}
 */
function isModifierRequired(modifiers, requestingName, requestedName) {
  var requesting = find(modifiers, function (_ref) {
    var name = _ref.name;
    return name === requestingName;
  });

  var isRequired = !!requesting && modifiers.some(function (modifier) {
    return modifier.name === requestedName && modifier.enabled && modifier.order < requesting.order;
  });

  if (!isRequired) {
    var _requesting = '`' + requestingName + '`';
    var requested = '`' + requestedName + '`';
    console.warn(requested + ' modifier is required by ' + _requesting + ' modifier in order to work, be sure to include it before ' + _requesting + '!');
  }
  return isRequired;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function arrow(data, options) {
  var _data$offsets$arrow;

  // arrow depends on keepTogether in order to work
  if (!isModifierRequired(data.instance.modifiers, 'arrow', 'keepTogether')) {
    return data;
  }

  var arrowElement = options.element;

  // if arrowElement is a string, suppose it's a CSS selector
  if (typeof arrowElement === 'string') {
    arrowElement = data.instance.popper.querySelector(arrowElement);

    // if arrowElement is not found, don't run the modifier
    if (!arrowElement) {
      return data;
    }
  } else {
    // if the arrowElement isn't a query selector we must check that the
    // provided DOM node is child of its popper node
    if (!data.instance.popper.contains(arrowElement)) {
      console.warn('WARNING: `arrow.element` must be child of its popper element!');
      return data;
    }
  }

  var placement = data.placement.split('-')[0];
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var isVertical = ['left', 'right'].indexOf(placement) !== -1;

  var len = isVertical ? 'height' : 'width';
  var sideCapitalized = isVertical ? 'Top' : 'Left';
  var side = sideCapitalized.toLowerCase();
  var altSide = isVertical ? 'left' : 'top';
  var opSide = isVertical ? 'bottom' : 'right';
  var arrowElementSize = getOuterSizes(arrowElement)[len];

  //
  // extends keepTogether behavior making sure the popper and its
  // reference have enough pixels in conjunction
  //

  // top/left side
  if (reference[opSide] - arrowElementSize < popper[side]) {
    data.offsets.popper[side] -= popper[side] - (reference[opSide] - arrowElementSize);
  }
  // bottom/right side
  if (reference[side] + arrowElementSize > popper[opSide]) {
    data.offsets.popper[side] += reference[side] + arrowElementSize - popper[opSide];
  }
  data.offsets.popper = getClientRect(data.offsets.popper);

  // compute center of the popper
  var center = reference[side] + reference[len] / 2 - arrowElementSize / 2;

  // Compute the sideValue using the updated popper offsets
  // take popper margin in account because we don't have this info available
  var css = getStyleComputedProperty(data.instance.popper);
  var popperMarginSide = parseFloat(css['margin' + sideCapitalized]);
  var popperBorderSide = parseFloat(css['border' + sideCapitalized + 'Width']);
  var sideValue = center - data.offsets.popper[side] - popperMarginSide - popperBorderSide;

  // prevent arrowElement from being placed not contiguously to its popper
  sideValue = Math.max(Math.min(popper[len] - arrowElementSize, sideValue), 0);

  data.arrowElement = arrowElement;
  data.offsets.arrow = (_data$offsets$arrow = {}, defineProperty$2(_data$offsets$arrow, side, Math.round(sideValue)), defineProperty$2(_data$offsets$arrow, altSide, ''), _data$offsets$arrow);

  return data;
}

/**
 * Get the opposite placement variation of the given one
 * @method
 * @memberof Popper.Utils
 * @argument {String} placement variation
 * @returns {String} flipped placement variation
 */
function getOppositeVariation(variation) {
  if (variation === 'end') {
    return 'start';
  } else if (variation === 'start') {
    return 'end';
  }
  return variation;
}

/**
 * List of accepted placements to use as values of the `placement` option.<br />
 * Valid placements are:
 * - `auto`
 * - `top`
 * - `right`
 * - `bottom`
 * - `left`
 *
 * Each placement can have a variation from this list:
 * - `-start`
 * - `-end`
 *
 * Variations are interpreted easily if you think of them as the left to right
 * written languages. Horizontally (`top` and `bottom`), `start` is left and `end`
 * is right.<br />
 * Vertically (`left` and `right`), `start` is top and `end` is bottom.
 *
 * Some valid examples are:
 * - `top-end` (on top of reference, right aligned)
 * - `right-start` (on right of reference, top aligned)
 * - `bottom` (on bottom, centered)
 * - `auto-end` (on the side with more space available, alignment depends by placement)
 *
 * @static
 * @type {Array}
 * @enum {String}
 * @readonly
 * @method placements
 * @memberof Popper
 */
var placements = ['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'right-start', 'right', 'right-end', 'bottom-end', 'bottom', 'bottom-start', 'left-end', 'left', 'left-start'];

// Get rid of `auto` `auto-start` and `auto-end`
var validPlacements = placements.slice(3);

/**
 * Given an initial placement, returns all the subsequent placements
 * clockwise (or counter-clockwise).
 *
 * @method
 * @memberof Popper.Utils
 * @argument {String} placement - A valid placement (it accepts variations)
 * @argument {Boolean} counter - Set to true to walk the placements counterclockwise
 * @returns {Array} placements including their variations
 */
function clockwise(placement) {
  var counter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  var index = validPlacements.indexOf(placement);
  var arr = validPlacements.slice(index + 1).concat(validPlacements.slice(0, index));
  return counter ? arr.reverse() : arr;
}

var BEHAVIORS = {
  FLIP: 'flip',
  CLOCKWISE: 'clockwise',
  COUNTERCLOCKWISE: 'counterclockwise'
};

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function flip(data, options) {
  // if `inner` modifier is enabled, we can't use the `flip` modifier
  if (isModifierEnabled(data.instance.modifiers, 'inner')) {
    return data;
  }

  if (data.flipped && data.placement === data.originalPlacement) {
    // seems like flip is trying to loop, probably there's not enough space on any of the flippable sides
    return data;
  }

  var boundaries = getBoundaries(data.instance.popper, data.instance.reference, options.padding, options.boundariesElement, data.positionFixed);

  var placement = data.placement.split('-')[0];
  var placementOpposite = getOppositePlacement(placement);
  var variation = data.placement.split('-')[1] || '';

  var flipOrder = [];

  switch (options.behavior) {
    case BEHAVIORS.FLIP:
      flipOrder = [placement, placementOpposite];
      break;
    case BEHAVIORS.CLOCKWISE:
      flipOrder = clockwise(placement);
      break;
    case BEHAVIORS.COUNTERCLOCKWISE:
      flipOrder = clockwise(placement, true);
      break;
    default:
      flipOrder = options.behavior;
  }

  flipOrder.forEach(function (step, index) {
    if (placement !== step || flipOrder.length === index + 1) {
      return data;
    }

    placement = data.placement.split('-')[0];
    placementOpposite = getOppositePlacement(placement);

    var popperOffsets = data.offsets.popper;
    var refOffsets = data.offsets.reference;

    // using floor because the reference offsets may contain decimals we are not going to consider here
    var floor = Math.floor;
    var overlapsRef = placement === 'left' && floor(popperOffsets.right) > floor(refOffsets.left) || placement === 'right' && floor(popperOffsets.left) < floor(refOffsets.right) || placement === 'top' && floor(popperOffsets.bottom) > floor(refOffsets.top) || placement === 'bottom' && floor(popperOffsets.top) < floor(refOffsets.bottom);

    var overflowsLeft = floor(popperOffsets.left) < floor(boundaries.left);
    var overflowsRight = floor(popperOffsets.right) > floor(boundaries.right);
    var overflowsTop = floor(popperOffsets.top) < floor(boundaries.top);
    var overflowsBottom = floor(popperOffsets.bottom) > floor(boundaries.bottom);

    var overflowsBoundaries = placement === 'left' && overflowsLeft || placement === 'right' && overflowsRight || placement === 'top' && overflowsTop || placement === 'bottom' && overflowsBottom;

    // flip the variation if required
    var isVertical = ['top', 'bottom'].indexOf(placement) !== -1;

    // flips variation if reference element overflows boundaries
    var flippedVariationByRef = !!options.flipVariations && (isVertical && variation === 'start' && overflowsLeft || isVertical && variation === 'end' && overflowsRight || !isVertical && variation === 'start' && overflowsTop || !isVertical && variation === 'end' && overflowsBottom);

    // flips variation if popper content overflows boundaries
    var flippedVariationByContent = !!options.flipVariationsByContent && (isVertical && variation === 'start' && overflowsRight || isVertical && variation === 'end' && overflowsLeft || !isVertical && variation === 'start' && overflowsBottom || !isVertical && variation === 'end' && overflowsTop);

    var flippedVariation = flippedVariationByRef || flippedVariationByContent;

    if (overlapsRef || overflowsBoundaries || flippedVariation) {
      // this boolean to detect any flip loop
      data.flipped = true;

      if (overlapsRef || overflowsBoundaries) {
        placement = flipOrder[index + 1];
      }

      if (flippedVariation) {
        variation = getOppositeVariation(variation);
      }

      data.placement = placement + (variation ? '-' + variation : '');

      // this object contains `position`, we want to preserve it along with
      // any additional property we may add in the future
      data.offsets.popper = _extends({}, data.offsets.popper, getPopperOffsets(data.instance.popper, data.offsets.reference, data.placement));

      data = runModifiers(data.instance.modifiers, data, 'flip');
    }
  });
  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function keepTogether(data) {
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var placement = data.placement.split('-')[0];
  var floor = Math.floor;
  var isVertical = ['top', 'bottom'].indexOf(placement) !== -1;
  var side = isVertical ? 'right' : 'bottom';
  var opSide = isVertical ? 'left' : 'top';
  var measurement = isVertical ? 'width' : 'height';

  if (popper[side] < floor(reference[opSide])) {
    data.offsets.popper[opSide] = floor(reference[opSide]) - popper[measurement];
  }
  if (popper[opSide] > floor(reference[side])) {
    data.offsets.popper[opSide] = floor(reference[side]);
  }

  return data;
}

/**
 * Converts a string containing value + unit into a px value number
 * @function
 * @memberof {modifiers~offset}
 * @private
 * @argument {String} str - Value + unit string
 * @argument {String} measurement - `height` or `width`
 * @argument {Object} popperOffsets
 * @argument {Object} referenceOffsets
 * @returns {Number|String}
 * Value in pixels, or original string if no values were extracted
 */
function toValue(str, measurement, popperOffsets, referenceOffsets) {
  // separate value from unit
  var split = str.match(/((?:\-|\+)?\d*\.?\d*)(.*)/);
  var value = +split[1];
  var unit = split[2];

  // If it's not a number it's an operator, I guess
  if (!value) {
    return str;
  }

  if (unit.indexOf('%') === 0) {
    var element = void 0;
    switch (unit) {
      case '%p':
        element = popperOffsets;
        break;
      case '%':
      case '%r':
      default:
        element = referenceOffsets;
    }

    var rect = getClientRect(element);
    return rect[measurement] / 100 * value;
  } else if (unit === 'vh' || unit === 'vw') {
    // if is a vh or vw, we calculate the size based on the viewport
    var size = void 0;
    if (unit === 'vh') {
      size = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    } else {
      size = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    }
    return size / 100 * value;
  } else {
    // if is an explicit pixel unit, we get rid of the unit and keep the value
    // if is an implicit unit, it's px, and we return just the value
    return value;
  }
}

/**
 * Parse an `offset` string to extrapolate `x` and `y` numeric offsets.
 * @function
 * @memberof {modifiers~offset}
 * @private
 * @argument {String} offset
 * @argument {Object} popperOffsets
 * @argument {Object} referenceOffsets
 * @argument {String} basePlacement
 * @returns {Array} a two cells array with x and y offsets in numbers
 */
function parseOffset(offset, popperOffsets, referenceOffsets, basePlacement) {
  var offsets = [0, 0];

  // Use height if placement is left or right and index is 0 otherwise use width
  // in this way the first offset will use an axis and the second one
  // will use the other one
  var useHeight = ['right', 'left'].indexOf(basePlacement) !== -1;

  // Split the offset string to obtain a list of values and operands
  // The regex addresses values with the plus or minus sign in front (+10, -20, etc)
  var fragments = offset.split(/(\+|\-)/).map(function (frag) {
    return frag.trim();
  });

  // Detect if the offset string contains a pair of values or a single one
  // they could be separated by comma or space
  var divider = fragments.indexOf(find(fragments, function (frag) {
    return frag.search(/,|\s/) !== -1;
  }));

  if (fragments[divider] && fragments[divider].indexOf(',') === -1) {
    console.warn('Offsets separated by white space(s) are deprecated, use a comma (,) instead.');
  }

  // If divider is found, we divide the list of values and operands to divide
  // them by ofset X and Y.
  var splitRegex = /\s*,\s*|\s+/;
  var ops = divider !== -1 ? [fragments.slice(0, divider).concat([fragments[divider].split(splitRegex)[0]]), [fragments[divider].split(splitRegex)[1]].concat(fragments.slice(divider + 1))] : [fragments];

  // Convert the values with units to absolute pixels to allow our computations
  ops = ops.map(function (op, index) {
    // Most of the units rely on the orientation of the popper
    var measurement = (index === 1 ? !useHeight : useHeight) ? 'height' : 'width';
    var mergeWithPrevious = false;
    return op
    // This aggregates any `+` or `-` sign that aren't considered operators
    // e.g.: 10 + +5 => [10, +, +5]
    .reduce(function (a, b) {
      if (a[a.length - 1] === '' && ['+', '-'].indexOf(b) !== -1) {
        a[a.length - 1] = b;
        mergeWithPrevious = true;
        return a;
      } else if (mergeWithPrevious) {
        a[a.length - 1] += b;
        mergeWithPrevious = false;
        return a;
      } else {
        return a.concat(b);
      }
    }, [])
    // Here we convert the string values into number values (in px)
    .map(function (str) {
      return toValue(str, measurement, popperOffsets, referenceOffsets);
    });
  });

  // Loop trough the offsets arrays and execute the operations
  ops.forEach(function (op, index) {
    op.forEach(function (frag, index2) {
      if (isNumeric$1(frag)) {
        offsets[index] += frag * (op[index2 - 1] === '-' ? -1 : 1);
      }
    });
  });
  return offsets;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @argument {Number|String} options.offset=0
 * The offset value as described in the modifier description
 * @returns {Object} The data object, properly modified
 */
function offset(data, _ref) {
  var offset = _ref.offset;
  var placement = data.placement,
      _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var basePlacement = placement.split('-')[0];

  var offsets = void 0;
  if (isNumeric$1(+offset)) {
    offsets = [+offset, 0];
  } else {
    offsets = parseOffset(offset, popper, reference, basePlacement);
  }

  if (basePlacement === 'left') {
    popper.top += offsets[0];
    popper.left -= offsets[1];
  } else if (basePlacement === 'right') {
    popper.top += offsets[0];
    popper.left += offsets[1];
  } else if (basePlacement === 'top') {
    popper.left += offsets[0];
    popper.top -= offsets[1];
  } else if (basePlacement === 'bottom') {
    popper.left += offsets[0];
    popper.top += offsets[1];
  }

  data.popper = popper;
  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function preventOverflow(data, options) {
  var boundariesElement = options.boundariesElement || getOffsetParent(data.instance.popper);

  // If offsetParent is the reference element, we really want to
  // go one step up and use the next offsetParent as reference to
  // avoid to make this modifier completely useless and look like broken
  if (data.instance.reference === boundariesElement) {
    boundariesElement = getOffsetParent(boundariesElement);
  }

  // NOTE: DOM access here
  // resets the popper's position so that the document size can be calculated excluding
  // the size of the popper element itself
  var transformProp = getSupportedPropertyName('transform');
  var popperStyles = data.instance.popper.style; // assignment to help minification
  var top = popperStyles.top,
      left = popperStyles.left,
      transform = popperStyles[transformProp];

  popperStyles.top = '';
  popperStyles.left = '';
  popperStyles[transformProp] = '';

  var boundaries = getBoundaries(data.instance.popper, data.instance.reference, options.padding, boundariesElement, data.positionFixed);

  // NOTE: DOM access here
  // restores the original style properties after the offsets have been computed
  popperStyles.top = top;
  popperStyles.left = left;
  popperStyles[transformProp] = transform;

  options.boundaries = boundaries;

  var order = options.priority;
  var popper = data.offsets.popper;

  var check = {
    primary: function primary(placement) {
      var value = popper[placement];
      if (popper[placement] < boundaries[placement] && !options.escapeWithReference) {
        value = Math.max(popper[placement], boundaries[placement]);
      }
      return defineProperty$2({}, placement, value);
    },
    secondary: function secondary(placement) {
      var mainSide = placement === 'right' ? 'left' : 'top';
      var value = popper[mainSide];
      if (popper[placement] > boundaries[placement] && !options.escapeWithReference) {
        value = Math.min(popper[mainSide], boundaries[placement] - (placement === 'right' ? popper.width : popper.height));
      }
      return defineProperty$2({}, mainSide, value);
    }
  };

  order.forEach(function (placement) {
    var side = ['left', 'top'].indexOf(placement) !== -1 ? 'primary' : 'secondary';
    popper = _extends({}, popper, check[side](placement));
  });

  data.offsets.popper = popper;

  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function shift(data) {
  var placement = data.placement;
  var basePlacement = placement.split('-')[0];
  var shiftvariation = placement.split('-')[1];

  // if shift shiftvariation is specified, run the modifier
  if (shiftvariation) {
    var _data$offsets = data.offsets,
        reference = _data$offsets.reference,
        popper = _data$offsets.popper;

    var isVertical = ['bottom', 'top'].indexOf(basePlacement) !== -1;
    var side = isVertical ? 'left' : 'top';
    var measurement = isVertical ? 'width' : 'height';

    var shiftOffsets = {
      start: defineProperty$2({}, side, reference[side]),
      end: defineProperty$2({}, side, reference[side] + reference[measurement] - popper[measurement])
    };

    data.offsets.popper = _extends({}, popper, shiftOffsets[shiftvariation]);
  }

  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by update method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function hide(data) {
  if (!isModifierRequired(data.instance.modifiers, 'hide', 'preventOverflow')) {
    return data;
  }

  var refRect = data.offsets.reference;
  var bound = find(data.instance.modifiers, function (modifier) {
    return modifier.name === 'preventOverflow';
  }).boundaries;

  if (refRect.bottom < bound.top || refRect.left > bound.right || refRect.top > bound.bottom || refRect.right < bound.left) {
    // Avoid unnecessary DOM access if visibility hasn't changed
    if (data.hide === true) {
      return data;
    }

    data.hide = true;
    data.attributes['x-out-of-boundaries'] = '';
  } else {
    // Avoid unnecessary DOM access if visibility hasn't changed
    if (data.hide === false) {
      return data;
    }

    data.hide = false;
    data.attributes['x-out-of-boundaries'] = false;
  }

  return data;
}

/**
 * @function
 * @memberof Modifiers
 * @argument {Object} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {Object} The data object, properly modified
 */
function inner(data) {
  var placement = data.placement;
  var basePlacement = placement.split('-')[0];
  var _data$offsets = data.offsets,
      popper = _data$offsets.popper,
      reference = _data$offsets.reference;

  var isHoriz = ['left', 'right'].indexOf(basePlacement) !== -1;

  var subtractLength = ['top', 'left'].indexOf(basePlacement) === -1;

  popper[isHoriz ? 'left' : 'top'] = reference[basePlacement] - (subtractLength ? popper[isHoriz ? 'width' : 'height'] : 0);

  data.placement = getOppositePlacement(placement);
  data.offsets.popper = getClientRect(popper);

  return data;
}

/**
 * Modifier function, each modifier can have a function of this type assigned
 * to its `fn` property.<br />
 * These functions will be called on each update, this means that you must
 * make sure they are performant enough to avoid performance bottlenecks.
 *
 * @function ModifierFn
 * @argument {dataObject} data - The data object generated by `update` method
 * @argument {Object} options - Modifiers configuration and options
 * @returns {dataObject} The data object, properly modified
 */

/**
 * Modifiers are plugins used to alter the behavior of your poppers.<br />
 * Popper.js uses a set of 9 modifiers to provide all the basic functionalities
 * needed by the library.
 *
 * Usually you don't want to override the `order`, `fn` and `onLoad` props.
 * All the other properties are configurations that could be tweaked.
 * @namespace modifiers
 */
var modifiers = {
  /**
   * Modifier used to shift the popper on the start or end of its reference
   * element.<br />
   * It will read the variation of the `placement` property.<br />
   * It can be one either `-end` or `-start`.
   * @memberof modifiers
   * @inner
   */
  shift: {
    /** @prop {number} order=100 - Index used to define the order of execution */
    order: 100,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: shift
  },

  /**
   * The `offset` modifier can shift your popper on both its axis.
   *
   * It accepts the following units:
   * - `px` or unit-less, interpreted as pixels
   * - `%` or `%r`, percentage relative to the length of the reference element
   * - `%p`, percentage relative to the length of the popper element
   * - `vw`, CSS viewport width unit
   * - `vh`, CSS viewport height unit
   *
   * For length is intended the main axis relative to the placement of the popper.<br />
   * This means that if the placement is `top` or `bottom`, the length will be the
   * `width`. In case of `left` or `right`, it will be the `height`.
   *
   * You can provide a single value (as `Number` or `String`), or a pair of values
   * as `String` divided by a comma or one (or more) white spaces.<br />
   * The latter is a deprecated method because it leads to confusion and will be
   * removed in v2.<br />
   * Additionally, it accepts additions and subtractions between different units.
   * Note that multiplications and divisions aren't supported.
   *
   * Valid examples are:
   * ```
   * 10
   * '10%'
   * '10, 10'
   * '10%, 10'
   * '10 + 10%'
   * '10 - 5vh + 3%'
   * '-10px + 5vh, 5px - 6%'
   * ```
   * > **NB**: If you desire to apply offsets to your poppers in a way that may make them overlap
   * > with their reference element, unfortunately, you will have to disable the `flip` modifier.
   * > You can read more on this at this [issue](https://github.com/FezVrasta/popper.js/issues/373).
   *
   * @memberof modifiers
   * @inner
   */
  offset: {
    /** @prop {number} order=200 - Index used to define the order of execution */
    order: 200,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: offset,
    /** @prop {Number|String} offset=0
     * The offset value as described in the modifier description
     */
    offset: 0
  },

  /**
   * Modifier used to prevent the popper from being positioned outside the boundary.
   *
   * A scenario exists where the reference itself is not within the boundaries.<br />
   * We can say it has "escaped the boundaries" — or just "escaped".<br />
   * In this case we need to decide whether the popper should either:
   *
   * - detach from the reference and remain "trapped" in the boundaries, or
   * - if it should ignore the boundary and "escape with its reference"
   *
   * When `escapeWithReference` is set to`true` and reference is completely
   * outside its boundaries, the popper will overflow (or completely leave)
   * the boundaries in order to remain attached to the edge of the reference.
   *
   * @memberof modifiers
   * @inner
   */
  preventOverflow: {
    /** @prop {number} order=300 - Index used to define the order of execution */
    order: 300,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: preventOverflow,
    /**
     * @prop {Array} [priority=['left','right','top','bottom']]
     * Popper will try to prevent overflow following these priorities by default,
     * then, it could overflow on the left and on top of the `boundariesElement`
     */
    priority: ['left', 'right', 'top', 'bottom'],
    /**
     * @prop {number} padding=5
     * Amount of pixel used to define a minimum distance between the boundaries
     * and the popper. This makes sure the popper always has a little padding
     * between the edges of its container
     */
    padding: 5,
    /**
     * @prop {String|HTMLElement} boundariesElement='scrollParent'
     * Boundaries used by the modifier. Can be `scrollParent`, `window`,
     * `viewport` or any DOM element.
     */
    boundariesElement: 'scrollParent'
  },

  /**
   * Modifier used to make sure the reference and its popper stay near each other
   * without leaving any gap between the two. Especially useful when the arrow is
   * enabled and you want to ensure that it points to its reference element.
   * It cares only about the first axis. You can still have poppers with margin
   * between the popper and its reference element.
   * @memberof modifiers
   * @inner
   */
  keepTogether: {
    /** @prop {number} order=400 - Index used to define the order of execution */
    order: 400,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: keepTogether
  },

  /**
   * This modifier is used to move the `arrowElement` of the popper to make
   * sure it is positioned between the reference element and its popper element.
   * It will read the outer size of the `arrowElement` node to detect how many
   * pixels of conjunction are needed.
   *
   * It has no effect if no `arrowElement` is provided.
   * @memberof modifiers
   * @inner
   */
  arrow: {
    /** @prop {number} order=500 - Index used to define the order of execution */
    order: 500,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: arrow,
    /** @prop {String|HTMLElement} element='[x-arrow]' - Selector or node used as arrow */
    element: '[x-arrow]'
  },

  /**
   * Modifier used to flip the popper's placement when it starts to overlap its
   * reference element.
   *
   * Requires the `preventOverflow` modifier before it in order to work.
   *
   * **NOTE:** this modifier will interrupt the current update cycle and will
   * restart it if it detects the need to flip the placement.
   * @memberof modifiers
   * @inner
   */
  flip: {
    /** @prop {number} order=600 - Index used to define the order of execution */
    order: 600,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: flip,
    /**
     * @prop {String|Array} behavior='flip'
     * The behavior used to change the popper's placement. It can be one of
     * `flip`, `clockwise`, `counterclockwise` or an array with a list of valid
     * placements (with optional variations)
     */
    behavior: 'flip',
    /**
     * @prop {number} padding=5
     * The popper will flip if it hits the edges of the `boundariesElement`
     */
    padding: 5,
    /**
     * @prop {String|HTMLElement} boundariesElement='viewport'
     * The element which will define the boundaries of the popper position.
     * The popper will never be placed outside of the defined boundaries
     * (except if `keepTogether` is enabled)
     */
    boundariesElement: 'viewport',
    /**
     * @prop {Boolean} flipVariations=false
     * The popper will switch placement variation between `-start` and `-end` when
     * the reference element overlaps its boundaries.
     *
     * The original placement should have a set variation.
     */
    flipVariations: false,
    /**
     * @prop {Boolean} flipVariationsByContent=false
     * The popper will switch placement variation between `-start` and `-end` when
     * the popper element overlaps its reference boundaries.
     *
     * The original placement should have a set variation.
     */
    flipVariationsByContent: false
  },

  /**
   * Modifier used to make the popper flow toward the inner of the reference element.
   * By default, when this modifier is disabled, the popper will be placed outside
   * the reference element.
   * @memberof modifiers
   * @inner
   */
  inner: {
    /** @prop {number} order=700 - Index used to define the order of execution */
    order: 700,
    /** @prop {Boolean} enabled=false - Whether the modifier is enabled or not */
    enabled: false,
    /** @prop {ModifierFn} */
    fn: inner
  },

  /**
   * Modifier used to hide the popper when its reference element is outside of the
   * popper boundaries. It will set a `x-out-of-boundaries` attribute which can
   * be used to hide with a CSS selector the popper when its reference is
   * out of boundaries.
   *
   * Requires the `preventOverflow` modifier before it in order to work.
   * @memberof modifiers
   * @inner
   */
  hide: {
    /** @prop {number} order=800 - Index used to define the order of execution */
    order: 800,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: hide
  },

  /**
   * Computes the style that will be applied to the popper element to gets
   * properly positioned.
   *
   * Note that this modifier will not touch the DOM, it just prepares the styles
   * so that `applyStyle` modifier can apply it. This separation is useful
   * in case you need to replace `applyStyle` with a custom implementation.
   *
   * This modifier has `850` as `order` value to maintain backward compatibility
   * with previous versions of Popper.js. Expect the modifiers ordering method
   * to change in future major versions of the library.
   *
   * @memberof modifiers
   * @inner
   */
  computeStyle: {
    /** @prop {number} order=850 - Index used to define the order of execution */
    order: 850,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: computeStyle,
    /**
     * @prop {Boolean} gpuAcceleration=true
     * If true, it uses the CSS 3D transformation to position the popper.
     * Otherwise, it will use the `top` and `left` properties
     */
    gpuAcceleration: true,
    /**
     * @prop {string} [x='bottom']
     * Where to anchor the X axis (`bottom` or `top`). AKA X offset origin.
     * Change this if your popper should grow in a direction different from `bottom`
     */
    x: 'bottom',
    /**
     * @prop {string} [x='left']
     * Where to anchor the Y axis (`left` or `right`). AKA Y offset origin.
     * Change this if your popper should grow in a direction different from `right`
     */
    y: 'right'
  },

  /**
   * Applies the computed styles to the popper element.
   *
   * All the DOM manipulations are limited to this modifier. This is useful in case
   * you want to integrate Popper.js inside a framework or view library and you
   * want to delegate all the DOM manipulations to it.
   *
   * Note that if you disable this modifier, you must make sure the popper element
   * has its position set to `absolute` before Popper.js can do its work!
   *
   * Just disable this modifier and define your own to achieve the desired effect.
   *
   * @memberof modifiers
   * @inner
   */
  applyStyle: {
    /** @prop {number} order=900 - Index used to define the order of execution */
    order: 900,
    /** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
    enabled: true,
    /** @prop {ModifierFn} */
    fn: applyStyle,
    /** @prop {Function} */
    onLoad: applyStyleOnLoad,
    /**
     * @deprecated since version 1.10.0, the property moved to `computeStyle` modifier
     * @prop {Boolean} gpuAcceleration=true
     * If true, it uses the CSS 3D transformation to position the popper.
     * Otherwise, it will use the `top` and `left` properties
     */
    gpuAcceleration: undefined
  }
};

/**
 * The `dataObject` is an object containing all the information used by Popper.js.
 * This object is passed to modifiers and to the `onCreate` and `onUpdate` callbacks.
 * @name dataObject
 * @property {Object} data.instance The Popper.js instance
 * @property {String} data.placement Placement applied to popper
 * @property {String} data.originalPlacement Placement originally defined on init
 * @property {Boolean} data.flipped True if popper has been flipped by flip modifier
 * @property {Boolean} data.hide True if the reference element is out of boundaries, useful to know when to hide the popper
 * @property {HTMLElement} data.arrowElement Node used as arrow by arrow modifier
 * @property {Object} data.styles Any CSS property defined here will be applied to the popper. It expects the JavaScript nomenclature (eg. `marginBottom`)
 * @property {Object} data.arrowStyles Any CSS property defined here will be applied to the popper arrow. It expects the JavaScript nomenclature (eg. `marginBottom`)
 * @property {Object} data.boundaries Offsets of the popper boundaries
 * @property {Object} data.offsets The measurements of popper, reference and arrow elements
 * @property {Object} data.offsets.popper `top`, `left`, `width`, `height` values
 * @property {Object} data.offsets.reference `top`, `left`, `width`, `height` values
 * @property {Object} data.offsets.arrow] `top` and `left` offsets, only one of them will be different from 0
 */

/**
 * Default options provided to Popper.js constructor.<br />
 * These can be overridden using the `options` argument of Popper.js.<br />
 * To override an option, simply pass an object with the same
 * structure of the `options` object, as the 3rd argument. For example:
 * ```
 * new Popper(ref, pop, {
 *   modifiers: {
 *     preventOverflow: { enabled: false }
 *   }
 * })
 * ```
 * @type {Object}
 * @static
 * @memberof Popper
 */
var Defaults = {
  /**
   * Popper's placement.
   * @prop {Popper.placements} placement='bottom'
   */
  placement: 'bottom',

  /**
   * Set this to true if you want popper to position it self in 'fixed' mode
   * @prop {Boolean} positionFixed=false
   */
  positionFixed: false,

  /**
   * Whether events (resize, scroll) are initially enabled.
   * @prop {Boolean} eventsEnabled=true
   */
  eventsEnabled: true,

  /**
   * Set to true if you want to automatically remove the popper when
   * you call the `destroy` method.
   * @prop {Boolean} removeOnDestroy=false
   */
  removeOnDestroy: false,

  /**
   * Callback called when the popper is created.<br />
   * By default, it is set to no-op.<br />
   * Access Popper.js instance with `data.instance`.
   * @prop {onCreate}
   */
  onCreate: function onCreate() {},

  /**
   * Callback called when the popper is updated. This callback is not called
   * on the initialization/creation of the popper, but only on subsequent
   * updates.<br />
   * By default, it is set to no-op.<br />
   * Access Popper.js instance with `data.instance`.
   * @prop {onUpdate}
   */
  onUpdate: function onUpdate() {},

  /**
   * List of modifiers used to modify the offsets before they are applied to the popper.
   * They provide most of the functionalities of Popper.js.
   * @prop {modifiers}
   */
  modifiers: modifiers
};

/**
 * @callback onCreate
 * @param {dataObject} data
 */

/**
 * @callback onUpdate
 * @param {dataObject} data
 */

// Utils
// Methods
var Popper = function () {
  /**
   * Creates a new Popper.js instance.
   * @class Popper
   * @param {Element|referenceObject} reference - The reference element used to position the popper
   * @param {Element} popper - The HTML / XML element used as the popper
   * @param {Object} options - Your custom options to override the ones defined in [Defaults](#defaults)
   * @return {Object} instance - The generated Popper.js instance
   */
  function Popper(reference, popper) {
    var _this = this;

    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    classCallCheck(this, Popper);

    this.scheduleUpdate = function () {
      return requestAnimationFrame(_this.update);
    };

    // make update() debounced, so that it only runs at most once-per-tick
    this.update = debounce(this.update.bind(this));

    // with {} we create a new object with the options inside it
    this.options = _extends({}, Popper.Defaults, options);

    // init state
    this.state = {
      isDestroyed: false,
      isCreated: false,
      scrollParents: []
    };

    // get reference and popper elements (allow jQuery wrappers)
    this.reference = reference && reference.jquery ? reference[0] : reference;
    this.popper = popper && popper.jquery ? popper[0] : popper;

    // Deep merge modifiers options
    this.options.modifiers = {};
    Object.keys(_extends({}, Popper.Defaults.modifiers, options.modifiers)).forEach(function (name) {
      _this.options.modifiers[name] = _extends({}, Popper.Defaults.modifiers[name] || {}, options.modifiers ? options.modifiers[name] : {});
    });

    // Refactoring modifiers' list (Object => Array)
    this.modifiers = Object.keys(this.options.modifiers).map(function (name) {
      return _extends({
        name: name
      }, _this.options.modifiers[name]);
    })
    // sort the modifiers by order
    .sort(function (a, b) {
      return a.order - b.order;
    });

    // modifiers have the ability to execute arbitrary code when Popper.js get inited
    // such code is executed in the same order of its modifier
    // they could add new properties to their options configuration
    // BE AWARE: don't add options to `options.modifiers.name` but to `modifierOptions`!
    this.modifiers.forEach(function (modifierOptions) {
      if (modifierOptions.enabled && isFunction$2(modifierOptions.onLoad)) {
        modifierOptions.onLoad(_this.reference, _this.popper, _this.options, modifierOptions, _this.state);
      }
    });

    // fire the first update to position the popper in the right place
    this.update();

    var eventsEnabled = this.options.eventsEnabled;
    if (eventsEnabled) {
      // setup event listeners, they will take care of update the position in specific situations
      this.enableEventListeners();
    }

    this.state.eventsEnabled = eventsEnabled;
  }

  // We can't use class properties because they don't get listed in the
  // class prototype and break stuff like Sinon stubs


  createClass(Popper, [{
    key: 'update',
    value: function update$$1() {
      return update.call(this);
    }
  }, {
    key: 'destroy',
    value: function destroy$$1() {
      return destroy$1.call(this);
    }
  }, {
    key: 'enableEventListeners',
    value: function enableEventListeners$$1() {
      return enableEventListeners.call(this);
    }
  }, {
    key: 'disableEventListeners',
    value: function disableEventListeners$$1() {
      return disableEventListeners.call(this);
    }

    /**
     * Schedules an update. It will run on the next UI update available.
     * @method scheduleUpdate
     * @memberof Popper
     */


    /**
     * Collection of utilities useful when writing custom modifiers.
     * Starting from version 1.7, this method is available only if you
     * include `popper-utils.js` before `popper.js`.
     *
     * **DEPRECATION**: This way to access PopperUtils is deprecated
     * and will be removed in v2! Use the PopperUtils module directly instead.
     * Due to the high instability of the methods contained in Utils, we can't
     * guarantee them to follow semver. Use them at your own risk!
     * @static
     * @private
     * @type {Object}
     * @deprecated since version 1.8
     * @member Utils
     * @memberof Popper
     */

  }]);
  return Popper;
}();

/**
 * The `referenceObject` is an object that provides an interface compatible with Popper.js
 * and lets you use it as replacement of a real DOM node.<br />
 * You can use this method to position a popper relatively to a set of coordinates
 * in case you don't have a DOM node to use as reference.
 *
 * ```
 * new Popper(referenceObject, popperNode);
 * ```
 *
 * NB: This feature isn't supported in Internet Explorer 10.
 * @name referenceObject
 * @property {Function} data.getBoundingClientRect
 * A function that returns a set of coordinates compatible with the native `getBoundingClientRect` method.
 * @property {number} data.clientWidth
 * An ES6 getter that will return the width of the virtual reference element.
 * @property {number} data.clientHeight
 * An ES6 getter that will return the height of the virtual reference element.
 */


Popper.Utils = (typeof window !== 'undefined' ? window : global).PopperUtils;
Popper.placements = placements;
Popper.Defaults = Defaults;

var PLACEMENT_TOP_START = 'top-start';
var PLACEMENT_TOP_END = 'top-end';
var PLACEMENT_BOTTOM_START = 'bottom-start';
var PLACEMENT_BOTTOM_END = 'bottom-end';
var PLACEMENT_RIGHT_START = 'right-start';
var PLACEMENT_LEFT_START = 'left-start';

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$1(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$1(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$1(Constructor.prototype, protoProps); if (staticProps) _defineProperties$1(Constructor, staticProps); return Constructor; }

var BvEvent = /*#__PURE__*/function () {
  function BvEvent(type) {
    var eventInit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck$2(this, BvEvent);

    // Start by emulating native Event constructor
    if (!type) {
      /* istanbul ignore next */
      throw new TypeError("Failed to construct '".concat(this.constructor.name, "'. 1 argument required, ").concat(arguments.length, " given."));
    } // Merge defaults first, the eventInit, and the type last
    // so it can't be overwritten


    assign(this, BvEvent.Defaults, this.constructor.Defaults, eventInit, {
      type: type
    }); // Freeze some props as readonly, but leave them enumerable

    defineProperties(this, {
      type: readonlyDescriptor(),
      cancelable: readonlyDescriptor(),
      nativeEvent: readonlyDescriptor(),
      target: readonlyDescriptor(),
      relatedTarget: readonlyDescriptor(),
      vueTarget: readonlyDescriptor(),
      componentId: readonlyDescriptor()
    }); // Create a private variable using closure scoping

    var defaultPrevented = false; // Recreate preventDefault method. One way setter

    this.preventDefault = function preventDefault() {
      if (this.cancelable) {
        defaultPrevented = true;
      }
    }; // Create `defaultPrevented` publicly accessible prop that
    // can only be altered by the preventDefault method


    defineProperty$1(this, 'defaultPrevented', {
      enumerable: true,
      get: function get() {
        return defaultPrevented;
      }
    });
  }

  _createClass$1(BvEvent, null, [{
    key: "Defaults",
    get: function get() {
      return {
        type: '',
        cancelable: true,
        nativeEvent: null,
        target: null,
        relatedTarget: null,
        vueTarget: null,
        componentId: null
      };
    }
  }]);

  return BvEvent;
}(); // Named Exports

var clickOutMixin = {
  data: function data() {
    return {
      listenForClickOut: false
    };
  },
  watch: {
    listenForClickOut: function listenForClickOut(newValue, oldValue) {
      if (newValue !== oldValue) {
        eventOff(this.clickOutElement, this.clickOutEventName, this._clickOutHandler, EVENT_OPTIONS_NO_CAPTURE);

        if (newValue) {
          eventOn(this.clickOutElement, this.clickOutEventName, this._clickOutHandler, EVENT_OPTIONS_NO_CAPTURE);
        }
      }
    }
  },
  beforeCreate: function beforeCreate() {
    // Declare non-reactive properties
    this.clickOutElement = null;
    this.clickOutEventName = null;
  },
  mounted: function mounted() {
    if (!this.clickOutElement) {
      this.clickOutElement = document;
    }

    if (!this.clickOutEventName) {
      this.clickOutEventName = 'click';
    }

    if (this.listenForClickOut) {
      eventOn(this.clickOutElement, this.clickOutEventName, this._clickOutHandler, EVENT_OPTIONS_NO_CAPTURE);
    }
  },
  beforeDestroy: function beforeDestroy()
  /* istanbul ignore next */
  {
    eventOff(this.clickOutElement, this.clickOutEventName, this._clickOutHandler, EVENT_OPTIONS_NO_CAPTURE);
  },
  methods: {
    isClickOut: function isClickOut(evt) {
      return !contains(this.$el, evt.target);
    },
    _clickOutHandler: function _clickOutHandler(evt) {
      if (this.clickOutHandler && this.isClickOut(evt)) {
        this.clickOutHandler(evt);
      }
    }
  }
};

var focusInMixin = {
  data: function data() {
    return {
      listenForFocusIn: false
    };
  },
  watch: {
    listenForFocusIn: function listenForFocusIn(newValue, oldValue) {
      if (newValue !== oldValue) {
        eventOff(this.focusInElement, 'focusin', this._focusInHandler, EVENT_OPTIONS_NO_CAPTURE);

        if (newValue) {
          eventOn(this.focusInElement, 'focusin', this._focusInHandler, EVENT_OPTIONS_NO_CAPTURE);
        }
      }
    }
  },
  beforeCreate: function beforeCreate() {
    // Declare non-reactive properties
    this.focusInElement = null;
  },
  mounted: function mounted() {
    if (!this.focusInElement) {
      this.focusInElement = document;
    }

    if (this.listenForFocusIn) {
      eventOn(this.focusInElement, 'focusin', this._focusInHandler, EVENT_OPTIONS_NO_CAPTURE);
    }
  },
  beforeDestroy: function beforeDestroy()
  /* istanbul ignore next */
  {
    eventOff(this.focusInElement, 'focusin', this._focusInHandler, EVENT_OPTIONS_NO_CAPTURE);
  },
  methods: {
    _focusInHandler: function _focusInHandler(evt) {
      if (this.focusInHandler) {
        this.focusInHandler(evt);
      }
    }
  }
};

function ownKeys$c(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$c(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$c(Object(source), true).forEach(function (key) { _defineProperty$i(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$c(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$i(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Root dropdown event names

var ROOT_EVENT_PREFIX = 'bv::dropdown::';
var ROOT_EVENT_SHOWN = "".concat(ROOT_EVENT_PREFIX, "shown");
var ROOT_EVENT_HIDDEN = "".concat(ROOT_EVENT_PREFIX, "hidden"); // CSS selectors

var SELECTOR_FORM_CHILD = '.dropdown form';
var SELECTOR_ITEM = ['.dropdown-item', '.b-dropdown-form'].map(function (selector) {
  return "".concat(selector, ":not(.disabled):not([disabled])");
}).join(', '); // --- Utility methods ---
// Return an array of visible items

var filterVisibles = function filterVisibles(els) {
  return (els || []).filter(isVisible);
}; // --- Props ---


var commonProps = {
  dropup: {
    // place on top if possible
    type: Boolean,
    default: false
  },
  dropright: {
    // place right if possible
    type: Boolean,
    default: false
  },
  dropleft: {
    // place left if possible
    type: Boolean,
    default: false
  },
  right: {
    // Right align menu (default is left align)
    type: Boolean,
    default: false
  },
  offset: {
    // Number of pixels to offset menu, or a CSS unit value (i.e. `1px`, `1rem`, etc.)
    type: [Number, String],
    default: 0
  },
  noFlip: {
    // Disable auto-flipping of menu from bottom <=> top
    type: Boolean,
    default: false
  },
  popperOpts: {
    type: Object,
    default: function _default() {}
  },
  boundary: {
    // String: `scrollParent`, `window` or `viewport`
    // HTMLElement: HTML Element reference
    type: [String, HTMLElement],
    default: 'scrollParent'
  }
}; // @vue/component

var dropdownMixin = {
  mixins: [idMixin, clickOutMixin, focusInMixin],
  provide: function provide() {
    return {
      bvDropdown: this
    };
  },
  inject: {
    bvNavbar: {
      default: null
    }
  },
  props: _objectSpread$c(_objectSpread$c({}, commonProps), {}, {
    disabled: {
      type: Boolean,
      default: false
    }
  }),
  data: function data() {
    return {
      visible: false,
      visibleChangePrevented: false
    };
  },
  computed: {
    inNavbar: function inNavbar() {
      return !isNull(this.bvNavbar);
    },
    toggler: function toggler() {
      var toggle = this.$refs.toggle;
      return toggle ? toggle.$el || toggle : null;
    },
    directionClass: function directionClass() {
      if (this.dropup) {
        return 'dropup';
      } else if (this.dropright) {
        return 'dropright';
      } else if (this.dropleft) {
        return 'dropleft';
      }

      return '';
    },
    boundaryClass: function boundaryClass() {
      // Position `static` is needed to allow menu to "breakout" of the `scrollParent`
      // boundaries when boundary is anything other than `scrollParent`
      // See: https://github.com/twbs/bootstrap/issues/24251#issuecomment-341413786
      return this.boundary !== 'scrollParent' && !this.inNavbar ? 'position-static' : '';
    }
  },
  watch: {
    visible: function visible(newValue, oldValue) {
      if (this.visibleChangePrevented) {
        this.visibleChangePrevented = false;
        return;
      }

      if (newValue !== oldValue) {
        var evtName = newValue ? 'show' : 'hide';
        var bvEvt = new BvEvent(evtName, {
          cancelable: true,
          vueTarget: this,
          target: this.$refs.menu,
          relatedTarget: null,
          componentId: this.safeId ? this.safeId() : this.id || null
        });
        this.emitEvent(bvEvt);

        if (bvEvt.defaultPrevented) {
          // Reset value and exit if canceled
          this.visibleChangePrevented = true;
          this.visible = oldValue; // Just in case a child element triggered `this.hide(true)`

          this.$off('hidden', this.focusToggler);
          return;
        }

        if (evtName === 'show') {
          this.showMenu();
        } else {
          this.hideMenu();
        }
      }
    },
    disabled: function disabled(newValue, oldValue) {
      if (newValue !== oldValue && newValue && this.visible) {
        // Hide dropdown if disabled changes to true
        this.visible = false;
      }
    }
  },
  created: function created() {
    // Create private non-reactive props
    this.$_popper = null;
  },

  /* istanbul ignore next */
  deactivated: function deactivated()
  /* istanbul ignore next: not easy to test */
  {
    // In case we are inside a `<keep-alive>`
    this.visible = false;
    this.whileOpenListen(false);
    this.destroyPopper();
  },
  beforeDestroy: function beforeDestroy() {
    this.visible = false;
    this.whileOpenListen(false);
    this.destroyPopper();
  },
  methods: {
    // Event emitter
    emitEvent: function emitEvent(bvEvt) {
      var type = bvEvt.type;
      this.$emit(type, bvEvt);
      this.$root.$emit("".concat(ROOT_EVENT_PREFIX).concat(type), bvEvt);
    },
    showMenu: function showMenu() {
      var _this = this;

      if (this.disabled) {
        /* istanbul ignore next */
        return;
      } // Only instantiate Popper.js when dropdown is not in `<b-navbar>`


      if (!this.inNavbar) {
        if (typeof Popper === 'undefined') {
          /* istanbul ignore next */
          warn('Popper.js not found. Falling back to CSS positioning', NAME_DROPDOWN);
        } else {
          // For dropup with alignment we use the parent element as popper container
          var el = this.dropup && this.right || this.split ? this.$el : this.$refs.toggle; // Make sure we have a reference to an element, not a component!

          el = el.$el || el; // Instantiate Popper.js

          this.createPopper(el);
        }
      } // Ensure other menus are closed


      this.$root.$emit(ROOT_EVENT_SHOWN, this); // Enable listeners

      this.whileOpenListen(true); // Wrap in `$nextTick()` to ensure menu is fully rendered/shown

      this.$nextTick(function () {
        // Focus on the menu container on show
        _this.focusMenu(); // Emit the shown event


        _this.$emit('shown');
      });
    },
    hideMenu: function hideMenu() {
      this.whileOpenListen(false);
      this.$root.$emit(ROOT_EVENT_HIDDEN, this);
      this.$emit('hidden');
      this.destroyPopper();
    },
    createPopper: function createPopper(element) {
      this.destroyPopper();
      this.$_popper = new Popper(element, this.$refs.menu, this.getPopperConfig());
    },
    // Ensure popper event listeners are removed cleanly
    destroyPopper: function destroyPopper() {
      this.$_popper && this.$_popper.destroy();
      this.$_popper = null;
    },
    // Instructs popper to re-computes the dropdown position
    // useful if the content changes size
    updatePopper: function updatePopper()
    /* istanbul ignore next: not easy to test */
    {
      try {
        this.$_popper.scheduleUpdate();
      } catch (_unused) {}
    },
    getPopperConfig: function getPopperConfig() {
      var placement = PLACEMENT_BOTTOM_START;

      if (this.dropup) {
        placement = this.right ? PLACEMENT_TOP_END : PLACEMENT_TOP_START;
      } else if (this.dropright) {
        placement = PLACEMENT_RIGHT_START;
      } else if (this.dropleft) {
        placement = PLACEMENT_LEFT_START;
      } else if (this.right) {
        placement = PLACEMENT_BOTTOM_END;
      }

      var popperConfig = {
        placement: placement,
        modifiers: {
          offset: {
            offset: this.offset || 0
          },
          flip: {
            enabled: !this.noFlip
          }
        }
      };
      var boundariesElement = this.boundary;

      if (boundariesElement) {
        popperConfig.modifiers.preventOverflow = {
          boundariesElement: boundariesElement
        };
      }

      return mergeDeep(popperConfig, this.popperOpts || {});
    },
    // Turn listeners on/off while open
    whileOpenListen: function whileOpenListen(isOpen) {
      // Hide the dropdown when clicked outside
      this.listenForClickOut = isOpen; // Hide the dropdown when it loses focus

      this.listenForFocusIn = isOpen; // Hide the dropdown when another dropdown is opened

      var method = isOpen ? '$on' : '$off';
      this.$root[method](ROOT_EVENT_SHOWN, this.rootCloseListener);
    },
    rootCloseListener: function rootCloseListener(vm) {
      if (vm !== this) {
        this.visible = false;
      }
    },
    show: function show() {
      var _this2 = this;

      // Public method to show dropdown
      if (this.disabled) {
        return;
      } // Wrap in a `requestAF()` to allow any previous
      // click handling to occur first


      requestAF(function () {
        _this2.visible = true;
      });
    },
    hide: function hide() {
      var refocus = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      // Public method to hide dropdown
      if (this.disabled) {
        /* istanbul ignore next */
        return;
      }

      this.visible = false;

      if (refocus) {
        // Child element is closing the dropdown on click
        this.$once('hidden', this.focusToggler);
      }
    },
    // Called only by a button that toggles the menu
    toggle: function toggle(evt) {
      evt = evt || {}; // Early exit when not a click event or ENTER, SPACE or DOWN were pressed

      var _evt = evt,
          type = _evt.type,
          keyCode = _evt.keyCode;

      if (type !== 'click' && !(type === 'keydown' && [CODE_ENTER, CODE_SPACE, CODE_DOWN].indexOf(keyCode) !== -1)) {
        /* istanbul ignore next */
        return;
      }
      /* istanbul ignore next */


      if (this.disabled) {
        this.visible = false;
        return;
      }

      this.$emit('toggle', evt);
      stopEvent(evt); // Toggle visibility

      if (this.visible) {
        this.hide(true);
      } else {
        this.show();
      }
    },
    // Mousedown handler for the toggle

    /* istanbul ignore next */
    onMousedown: function onMousedown(evt)
    /* istanbul ignore next */
    {
      // We prevent the 'mousedown' event for the toggle to stop the
      // 'focusin' event from being fired
      // The event would otherwise be picked up by the global 'focusin'
      // listener and there is no cross-browser solution to detect it
      // relates to the toggle click
      // The 'click' event will still be fired and we handle closing
      // other dropdowns there too
      // See https://github.com/bootstrap-vue/bootstrap-vue/issues/4328
      stopEvent(evt, {
        propagation: false
      });
    },
    // Called from dropdown menu context
    onKeydown: function onKeydown(evt) {
      var keyCode = evt.keyCode;

      if (keyCode === CODE_ESC) {
        // Close on ESC
        this.onEsc(evt);
      } else if (keyCode === CODE_DOWN) {
        // Down Arrow
        this.focusNext(evt, false);
      } else if (keyCode === CODE_UP) {
        // Up Arrow
        this.focusNext(evt, true);
      }
    },
    // If user presses ESC, close the menu
    onEsc: function onEsc(evt) {
      if (this.visible) {
        this.visible = false;
        stopEvent(evt); // Return focus to original trigger button

        this.$once('hidden', this.focusToggler);
      }
    },
    // Called only in split button mode, for the split button
    onSplitClick: function onSplitClick(evt) {
      /* istanbul ignore next */
      if (this.disabled) {
        this.visible = false;
        return;
      }

      this.$emit('click', evt);
    },
    // Shared hide handler between click-out and focus-in events
    hideHandler: function hideHandler(evt) {
      var target = evt.target;

      if (this.visible && !contains(this.$refs.menu, target) && !contains(this.toggler, target)) {
        this.hide();
      }
    },
    // Document click-out listener
    clickOutHandler: function clickOutHandler(evt) {
      this.hideHandler(evt);
    },
    // Document focus-in listener
    focusInHandler: function focusInHandler(evt) {
      this.hideHandler(evt);
    },
    // Keyboard nav
    focusNext: function focusNext(evt, up) {
      var _this3 = this;

      // Ignore key up/down on form elements
      var target = evt.target;

      if (!this.visible || evt && closest(SELECTOR_FORM_CHILD, target)) {
        /* istanbul ignore next: should never happen */
        return;
      }

      stopEvent(evt);
      this.$nextTick(function () {
        var items = _this3.getItems();

        if (items.length < 1) {
          /* istanbul ignore next: should never happen */
          return;
        }

        var index = items.indexOf(target);

        if (up && index > 0) {
          index--;
        } else if (!up && index < items.length - 1) {
          index++;
        }

        if (index < 0) {
          /* istanbul ignore next: should never happen */
          index = 0;
        }

        _this3.focusItem(index, items);
      });
    },
    focusItem: function focusItem(index, items) {
      var el = items.find(function (el, i) {
        return i === index;
      });
      attemptFocus(el);
    },
    getItems: function getItems() {
      // Get all items
      return filterVisibles(selectAll(SELECTOR_ITEM, this.$refs.menu));
    },
    focusMenu: function focusMenu() {
      attemptFocus(this.$refs.menu);
    },
    focusToggler: function focusToggler() {
      var _this4 = this;

      this.$nextTick(function () {
        attemptFocus(_this4.toggler);
      });
    }
  }
};

function ownKeys$d(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$d(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$d(Object(source), true).forEach(function (key) { _defineProperty$j(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$d(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$j(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var props$7 = {
  text: {
    type: String // default: null

  },
  html: {
    type: String // default: null

  },
  variant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_DROPDOWN, 'variant');
    }
  },
  size: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_DROPDOWN, 'size');
    }
  },
  block: {
    type: Boolean,
    default: false
  },
  menuClass: {
    type: [String, Array, Object] // default: null

  },
  toggleTag: {
    type: String,
    default: 'button'
  },
  toggleText: {
    // This really should be toggleLabel
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_DROPDOWN, 'toggleText');
    }
  },
  toggleClass: {
    type: [String, Array, Object] // default: null

  },
  noCaret: {
    type: Boolean,
    default: false
  },
  split: {
    type: Boolean,
    default: false
  },
  splitHref: {
    type: String // default: undefined

  },
  splitTo: {
    type: [String, Object] // default: undefined

  },
  splitVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_DROPDOWN, 'splitVariant');
    }
  },
  splitClass: {
    type: [String, Array, Object] // default: null

  },
  splitButtonType: {
    type: String,
    default: 'button',
    validator: function validator(value) {
      return arrayIncludes(['button', 'submit', 'reset'], value);
    }
  },
  lazy: {
    // If true, only render menu contents when open
    type: Boolean,
    default: false
  },
  role: {
    type: String,
    default: 'menu'
  }
}; // --- Main component ---
// @vue/component

var BDropdown = /*#__PURE__*/Vue.extend({
  name: NAME_DROPDOWN,
  mixins: [idMixin, dropdownMixin, normalizeSlotMixin],
  props: props$7,
  computed: {
    dropdownClasses: function dropdownClasses() {
      var block = this.block,
          split = this.split;
      return [this.directionClass, this.boundaryClass, {
        show: this.visible,
        // The 'btn-group' class is required in `split` mode for button alignment
        // It needs also to be applied when `block` is disabled to allow multiple
        // dropdowns to be aligned one line
        'btn-group': split || !block,
        // When `block` is enabled and we are in `split` mode the 'd-flex' class
        // needs to be applied to allow the buttons to stretch to full width
        'd-flex': block && split
      }];
    },
    menuClasses: function menuClasses() {
      return [this.menuClass, {
        'dropdown-menu-right': this.right,
        show: this.visible
      }];
    },
    toggleClasses: function toggleClasses() {
      var split = this.split;
      return [this.toggleClass, {
        'dropdown-toggle-split': split,
        'dropdown-toggle-no-caret': this.noCaret && !split
      }];
    }
  },
  render: function render(h) {
    var visible = this.visible,
        variant = this.variant,
        size = this.size,
        block = this.block,
        disabled = this.disabled,
        split = this.split,
        role = this.role,
        hide = this.hide,
        toggle = this.toggle;
    var commonProps = {
      variant: variant,
      size: size,
      block: block,
      disabled: disabled
    };
    var buttonContentSlotName = 'button-content';
    var $buttonChildren = this.normalizeSlot(buttonContentSlotName);
    var buttonContentDomProps = this.hasNormalizedSlot(buttonContentSlotName) ? {} : htmlOrText(this.html, this.text);
    var $split = h();

    if (split) {
      var splitTo = this.splitTo,
          splitHref = this.splitHref,
          splitButtonType = this.splitButtonType;

      var btnProps = _objectSpread$d(_objectSpread$d({}, commonProps), {}, {
        variant: this.splitVariant || variant
      }); // We add these as needed due to <router-link> issues with
      // defined property with `undefined`/`null` values


      if (splitTo) {
        btnProps.to = splitTo;
      } else if (splitHref) {
        btnProps.href = splitHref;
      } else if (splitButtonType) {
        btnProps.type = splitButtonType;
      }

      $split = h(BButton, {
        class: this.splitClass,
        attrs: {
          id: this.safeId('_BV_button_')
        },
        props: btnProps,
        domProps: buttonContentDomProps,
        on: {
          click: this.onSplitClick
        },
        ref: 'button'
      }, $buttonChildren); // Overwrite button content for the toggle when in `split` mode

      $buttonChildren = [h('span', {
        class: ['sr-only']
      }, [this.toggleText])];
      buttonContentDomProps = {};
    }

    var $toggle = h(BButton, {
      staticClass: 'dropdown-toggle',
      class: this.toggleClasses,
      attrs: {
        id: this.safeId('_BV_toggle_'),
        'aria-haspopup': 'true',
        'aria-expanded': toString$1(visible)
      },
      props: _objectSpread$d(_objectSpread$d({}, commonProps), {}, {
        tag: this.toggleTag,
        block: block && !split
      }),
      domProps: buttonContentDomProps,
      on: {
        mousedown: this.onMousedown,
        click: toggle,
        keydown: toggle // Handle ENTER, SPACE and DOWN

      },
      ref: 'toggle'
    }, $buttonChildren);
    var $menu = h('ul', {
      staticClass: 'dropdown-menu',
      class: this.menuClasses,
      attrs: {
        role: role,
        tabindex: '-1',
        'aria-labelledby': this.safeId(split ? '_BV_button_' : '_BV_toggle_')
      },
      on: {
        keydown: this.onKeydown // Handle UP, DOWN and ESC

      },
      ref: 'menu'
    }, [!this.lazy || visible ? this.normalizeSlot(SLOT_NAME_DEFAULT, {
      hide: hide
    }) : h()]);
    return h('div', {
      staticClass: 'dropdown b-dropdown',
      class: this.dropdownClasses,
      attrs: {
        id: this.safeId()
      }
    }, [$split, $toggle, $menu]);
  }
});

function ownKeys$e(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$e(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$e(Object(source), true).forEach(function (key) { _defineProperty$k(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$e(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$k(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$8 = omit(props$1, ['event', 'routerTag']); // @vue/component

var BDropdownItem = /*#__PURE__*/Vue.extend({
  name: NAME_DROPDOWN_ITEM,
  mixins: [attrsMixin, normalizeSlotMixin],
  inheritAttrs: false,
  inject: {
    bvDropdown: {
      default: null
    }
  },
  props: _objectSpread$e(_objectSpread$e({}, props$8), {}, {
    linkClass: {
      type: [String, Array, Object] // default: null

    },
    variant: {
      type: String // default: null

    }
  }),
  computed: {
    computedAttrs: function computedAttrs() {
      return _objectSpread$e(_objectSpread$e({}, this.bvAttrs), {}, {
        role: 'menuitem'
      });
    }
  },
  methods: {
    closeDropdown: function closeDropdown() {
      var _this = this;

      // Close on next animation frame to allow <b-link> time to process
      requestAF(function () {
        if (_this.bvDropdown) {
          _this.bvDropdown.hide(true);
        }
      });
    },
    onClick: function onClick(evt) {
      this.$emit('click', evt);
      this.closeDropdown();
    }
  },
  render: function render(h) {
    var linkClass = this.linkClass,
        variant = this.variant,
        active = this.active,
        disabled = this.disabled,
        onClick = this.onClick;
    return h('li', {
      attrs: {
        role: 'presentation'
      }
    }, [h(BLink, {
      staticClass: 'dropdown-item',
      class: [linkClass, _defineProperty$k({}, "text-".concat(variant), variant && !(active || disabled))],
      props: this.$props,
      attrs: this.computedAttrs,
      on: {
        click: onClick
      },
      ref: 'item'
    }, this.normalizeSlot())]);
  }
});

function ownKeys$f(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$f(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$f(Object(source), true).forEach(function (key) { _defineProperty$l(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$f(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$l(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$9 = {
  tag: {
    type: String,
    default: 'hr'
  }
}; // @vue/component

var BDropdownDivider = /*#__PURE__*/Vue.extend({
  name: NAME_DROPDOWN_DIVIDER,
  functional: true,
  props: props$9,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data;
    var $attrs = data.attrs || {};
    data.attrs = {};
    return h('li', a(data, {
      attrs: {
        role: 'presentation'
      }
    }), [h(props.tag, {
      staticClass: 'dropdown-divider',
      attrs: _objectSpread$f(_objectSpread$f({}, $attrs), {}, {
        role: 'separator',
        'aria-orientation': 'horizontal'
      }),
      ref: 'divider'
    })]);
  }
});

function ownKeys$g(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$g(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$g(Object(source), true).forEach(function (key) { _defineProperty$m(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$g(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$m(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$a = {
  id: {
    type: String // default: null

  },
  header: {
    type: String // default: null

  },
  headerTag: {
    type: String,
    default: 'header'
  },
  headerVariant: {
    type: String // default: null

  },
  headerClasses: {
    type: [String, Array, Object] // default: null

  },
  ariaDescribedby: {
    type: String // default: null

  }
}; // @vue/component

var BDropdownGroup = /*#__PURE__*/Vue.extend({
  name: NAME_DROPDOWN_GROUP,
  functional: true,
  props: props$a,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        slots = _ref.slots,
        scopedSlots = _ref.scopedSlots;
    var $slots = slots();
    var $scopedSlots = scopedSlots || {};
    var $attrs = data.attrs || {};
    data.attrs = {};
    var header;
    var headerId = null;

    if (hasNormalizedSlot(SLOT_NAME_HEADER, $scopedSlots, $slots) || props.header) {
      headerId = props.id ? "_bv_".concat(props.id, "_group_dd_header") : null;
      header = h(props.headerTag, {
        staticClass: 'dropdown-header',
        class: [props.headerClasses, _defineProperty$m({}, "text-".concat(props.variant), props.variant)],
        attrs: {
          id: headerId,
          role: 'heading'
        }
      }, normalizeSlot(SLOT_NAME_HEADER, {}, $scopedSlots, $slots) || props.header);
    }

    var adb = [headerId, props.ariaDescribedBy].filter(identity$1).join(' ').trim();
    return h('li', a(data, {
      attrs: {
        role: 'presentation'
      }
    }), [header || h(), h('ul', {
      staticClass: 'list-unstyled',
      attrs: _objectSpread$g(_objectSpread$g({}, $attrs), {}, {
        id: props.id || null,
        role: 'group',
        'aria-describedby': adb || null
      })
    }, normalizeSlot(SLOT_NAME_DEFAULT, {}, $scopedSlots, $slots))]);
  }
});

var OPTIONS_OBJECT_DEPRECATED_MSG = 'Setting prop "options" to an object is deprecated. Use the array format instead.'; // @vue/component

var formOptionsMixin = {
  props: {
    options: {
      type: [Array, Object],
      default: function _default() {
        return [];
      }
    },
    valueField: {
      type: String,
      default: 'value'
    },
    textField: {
      type: String,
      default: 'text'
    },
    htmlField: {
      type: String,
      default: 'html'
    },
    disabledField: {
      type: String,
      default: 'disabled'
    }
  },
  computed: {
    formOptions: function formOptions() {
      return this.normalizeOptions(this.options);
    }
  },
  methods: {
    normalizeOption: function normalizeOption(option) {
      var key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

      // When the option is an object, normalize it
      if (isPlainObject$1(option)) {
        var value = get$1(option, this.valueField);
        var text = get$1(option, this.textField);
        return {
          value: isUndefined(value) ? key || text : value,
          text: stripTags(String(isUndefined(text) ? key : text)),
          html: get$1(option, this.htmlField),
          disabled: Boolean(get$1(option, this.disabledField))
        };
      } // Otherwise create an `<option>` object from the given value


      return {
        value: key || option,
        text: stripTags(String(option)),
        disabled: false
      };
    },
    normalizeOptions: function normalizeOptions(options) {
      var _this = this;

      // Normalize the given options array
      if (isArray$1(options)) {
        return options.map(function (option) {
          return _this.normalizeOption(option);
        });
      } else if (isPlainObject$1(options)) {
        // Deprecate the object options format
        warn(OPTIONS_OBJECT_DEPRECATED_MSG, this.$options.name); // Normalize a `options` object to an array of options

        return keys$1(options).map(function (key) {
          return _this.normalizeOption(options[key] || {}, key);
        });
      } // If not an array or object, return an empty array

      /* istanbul ignore next */


      return [];
    }
  }
};

function _defineProperty$n(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$b = {
  id: {
    type: String // default: null

  },
  tag: {
    type: String,
    default: 'small'
  },
  textVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_FORM_TEXT, 'textVariant');
    }
  },
  inline: {
    type: Boolean,
    default: false
  }
}; // @vue/component

var BFormText = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_TEXT,
  functional: true,
  props: props$b,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    return h(props.tag, a(data, {
      class: _defineProperty$n({
        'form-text': !props.inline
      }, "text-".concat(props.textVariant), props.textVariant),
      attrs: {
        id: props.id
      }
    }), children);
  }
});

var props$c = {
  id: {
    type: String // default: null

  },
  tag: {
    type: String,
    default: 'div'
  },
  tooltip: {
    type: Boolean,
    default: false
  },
  forceShow: {
    type: Boolean,
    default: false
  },
  state: {
    // Tri-state prop: `true`, `false`, or `null`
    type: Boolean,
    default: null
  },
  ariaLive: {
    type: String // default: null

  },
  role: {
    type: String // default: null

  }
}; // @vue/component

var BFormInvalidFeedback = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_INVALID_FEEDBACK,
  functional: true,
  props: props$c,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    var show = props.forceShow === true || props.state === false;
    return h(props.tag, a(data, {
      class: {
        'invalid-feedback': !props.tooltip,
        'invalid-tooltip': props.tooltip,
        'd-block': show
      },
      attrs: {
        id: props.id || null,
        role: props.role || null,
        'aria-live': props.ariaLive || null,
        'aria-atomic': props.ariaLive ? 'true' : null
      }
    }), children);
  }
});

var props$d = {
  id: {
    type: String // default: null

  },
  tag: {
    type: String,
    default: 'div'
  },
  tooltip: {
    type: Boolean,
    default: false
  },
  forceShow: {
    type: Boolean,
    default: false
  },
  state: {
    // Tri-state prop: `true`, `false`, or `null`
    type: Boolean,
    default: null
  },
  ariaLive: {
    type: String // default: null

  },
  role: {
    type: String // default: null

  }
}; // @vue/component

var BFormValidFeedback = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_VALID_FEEDBACK,
  functional: true,
  props: props$d,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    var show = props.forceShow === true || props.state === true;
    return h(props.tag, a(data, {
      class: {
        'valid-feedback': !props.tooltip,
        'valid-tooltip': props.tooltip,
        'd-block': show
      },
      attrs: {
        id: props.id || null,
        role: props.role || null,
        'aria-live': props.ariaLive || null,
        'aria-atomic': props.ariaLive ? 'true' : null
      }
    }), children);
  }
});

var props$e = {
  tag: {
    type: String,
    default: 'div'
  }
}; // @vue/component

var BFormRow = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_ROW,
  functional: true,
  props: props$e,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    return h(props.tag, a(data, {
      staticClass: 'form-row'
    }), children);
  }
});

var looseIndexOf = function looseIndexOf(arr, val) {
  // Assumes that the first argument is an array
  for (var i = 0; i < arr.length; i++) {
    if (looseEqual(arr[i], val)) {
      return i;
    }
  }

  return -1;
};

var SELECTOR = 'input, textarea, select'; // @vue/component

var formMixin = {
  props: {
    name: {
      type: String // default: undefined

    },
    id: {
      type: String // default: undefined

    },
    disabled: {
      type: Boolean
    },
    required: {
      type: Boolean,
      default: false
    },
    form: {
      type: String // default: null

    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  mounted: function mounted() {
    this.handleAutofocus();
  },

  /* istanbul ignore next */
  activated: function activated() {
    this.handleAutofocus();
  },
  methods: {
    handleAutofocus: function handleAutofocus() {
      var _this = this;

      this.$nextTick(function () {
        requestAF(function () {
          var el = _this.$el;

          if (_this.autofocus && isVisible(el)) {
            if (!matches(el, SELECTOR)) {
              el = select(SELECTOR, el);
            }

            attemptFocus(el);
          }
        });
      });
    }
  }
};

function ownKeys$h(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$h(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$h(Object(source), true).forEach(function (key) { _defineProperty$o(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$h(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$o(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var formRadioCheckMixin = {
  mixins: [attrsMixin, normalizeSlotMixin],
  inheritAttrs: false,
  model: {
    prop: 'checked',
    event: 'input'
  },
  props: {
    value: {// Value when checked
      // type: Object,
      // default: undefined
    },
    checked: {// This is the v-model
      // type: Object,
      // default: undefined
    },
    inline: {
      type: Boolean,
      default: false
    },
    plain: {
      type: Boolean,
      default: false
    },
    button: {
      // Only applicable in standalone mode (non group)
      type: Boolean,
      default: false
    },
    buttonVariant: {
      // Only applicable when rendered with button style
      type: String // default: null

    },
    ariaLabel: {
      // Placed on the input if present.
      type: String // default: null

    },
    ariaLabelledby: {
      // Placed on the input if present.
      type: String // default: null

    }
  },
  data: function data() {
    return {
      localChecked: this.isGroup ? this.bvGroup.checked : this.checked,
      hasFocus: false
    };
  },
  computed: {
    computedLocalChecked: {
      get: function get() {
        return this.isGroup ? this.bvGroup.localChecked : this.localChecked;
      },
      set: function set(val) {
        if (this.isGroup) {
          this.bvGroup.localChecked = val;
        } else {
          this.localChecked = val;
        }
      }
    },
    isGroup: function isGroup() {
      // Is this check/radio a child of check-group or radio-group?
      return Boolean(this.bvGroup);
    },
    isBtnMode: function isBtnMode() {
      // Support button style in single input mode
      return this.isGroup ? this.bvGroup.buttons : this.button;
    },
    isPlain: function isPlain() {
      return this.isBtnMode ? false : this.isGroup ? this.bvGroup.plain : this.plain;
    },
    isCustom: function isCustom() {
      return this.isBtnMode ? false : !this.isPlain;
    },
    isSwitch: function isSwitch() {
      // Custom switch styling (checkboxes only)
      return this.isBtnMode || this.isRadio || this.isPlain ? false : this.isGroup ? this.bvGroup.switches : this.switch;
    },
    isInline: function isInline() {
      return this.isGroup ? this.bvGroup.inline : this.inline;
    },
    isDisabled: function isDisabled() {
      // Child can be disabled while parent isn't, but is always disabled if group is
      return this.isGroup ? this.bvGroup.disabled || this.disabled : this.disabled;
    },
    isRequired: function isRequired() {
      // Required only works when a name is provided for the input(s)
      // Child can only be required when parent is
      // Groups will always have a name (either user supplied or auto generated)
      return this.getName && (this.isGroup ? this.bvGroup.required : this.required);
    },
    getName: function getName() {
      // Group name preferred over local name
      return (this.isGroup ? this.bvGroup.groupName : this.name) || null;
    },
    getForm: function getForm() {
      return (this.isGroup ? this.bvGroup.form : this.form) || null;
    },
    getSize: function getSize() {
      return (this.isGroup ? this.bvGroup.size : this.size) || '';
    },
    getState: function getState() {
      return this.isGroup ? this.bvGroup.computedState : this.computedState;
    },
    getButtonVariant: function getButtonVariant() {
      // Local variant preferred over group variant
      if (this.buttonVariant) {
        return this.buttonVariant;
      } else if (this.isGroup && this.bvGroup.buttonVariant) {
        return this.bvGroup.buttonVariant;
      } // default variant


      return 'secondary';
    },
    buttonClasses: function buttonClasses() {
      var _ref;

      // Same for radio & check
      return ['btn', "btn-".concat(this.getButtonVariant), (_ref = {}, _defineProperty$o(_ref, "btn-".concat(this.getSize), this.getSize), _defineProperty$o(_ref, "disabled", this.isDisabled), _defineProperty$o(_ref, "active", this.isChecked), _defineProperty$o(_ref, "focus", this.hasFocus), _ref)];
    },
    computedAttrs: function computedAttrs() {
      return _objectSpread$h(_objectSpread$h({}, this.bvAttrs), {}, {
        id: this.safeId(),
        type: this.isRadio ? 'radio' : 'checkbox',
        name: this.getName,
        form: this.getForm,
        disabled: this.isDisabled,
        required: this.isRequired,
        'aria-required': this.isRequired || null,
        'aria-label': this.ariaLabel || null,
        'aria-labelledby': this.ariaLabelledby || null
      });
    }
  },
  watch: {
    checked: function checked(newValue) {
      if (!looseEqual(newValue, this.computedLocalChecked)) {
        this.computedLocalChecked = newValue;
      }
    }
  },
  methods: {
    handleFocus: function handleFocus(evt) {
      // When in buttons mode, we need to add 'focus' class to label when input focused
      // As it is the hidden input which has actual focus
      if (evt.target) {
        if (evt.type === 'focus') {
          this.hasFocus = true;
        } else if (evt.type === 'blur') {
          this.hasFocus = false;
        }
      }
    },
    // Convenience methods for focusing the input
    focus: function focus() {
      if (!this.isDisabled) {
        attemptFocus(this.$refs.input);
      }
    },
    blur: function blur() {
      if (!this.isDisabled) {
        attemptBlur(this.$refs.input);
      }
    }
  },
  render: function render(h) {
    var defaultSlot = this.normalizeSlot(); // Generate the input element

    var on = {
      change: this.handleChange
    };

    if (this.isBtnMode) {
      // Handlers for focus styling when in button mode
      on.focus = on.blur = this.handleFocus;
    }

    var input = h('input', {
      ref: 'input',
      key: 'input',
      on: on,
      class: {
        'form-check-input': this.isPlain,
        'custom-control-input': this.isCustom,
        'is-valid': this.getState === true && !this.isBtnMode,
        'is-invalid': this.getState === false && !this.isBtnMode,
        // https://github.com/bootstrap-vue/bootstrap-vue/issues/2911
        'position-static': this.isPlain && !defaultSlot
      },
      directives: [{
        name: 'model',
        rawName: 'v-model',
        value: this.computedLocalChecked,
        expression: 'computedLocalChecked'
      }],
      attrs: this.computedAttrs,
      domProps: {
        value: this.value,
        checked: this.isChecked
      }
    });

    if (this.isBtnMode) {
      // Button mode
      var button = h('label', {
        class: this.buttonClasses
      }, [input, defaultSlot]);

      if (!this.isGroup) {
        // Standalone button mode, so wrap in 'btn-group-toggle'
        // and flag it as inline-block to mimic regular buttons
        button = h('div', {
          class: ['btn-group-toggle', 'd-inline-block']
        }, [button]);
      }

      return button;
    } else {
      // Not button mode
      var label = h(); // If no label content in plain mode we dont render the label
      // https://github.com/bootstrap-vue/bootstrap-vue/issues/2911

      if (!(this.isPlain && !defaultSlot)) {
        label = h('label', {
          class: {
            'form-check-label': this.isPlain,
            'custom-control-label': this.isCustom
          },
          attrs: {
            for: this.safeId()
          }
        }, defaultSlot);
      } // Wrap it in a div


      return h('div', {
        class: _defineProperty$o({
          'form-check': this.isPlain,
          'form-check-inline': this.isPlain && this.isInline,
          'custom-control': this.isCustom,
          'custom-control-inline': this.isCustom && this.isInline,
          'custom-checkbox': this.isCustom && this.isCheck && !this.isSwitch,
          'custom-switch': this.isSwitch,
          'custom-radio': this.isCustom && this.isRadio
        }, "b-custom-control-".concat(this.getSize), Boolean(this.getSize && !this.isBtnMode))
      }, [input, label]);
    }
  }
};

var formSizeMixin = {
  props: {
    size: {
      type: String,
      default: function _default() {
        return getComponentConfig('formControls', 'size');
      }
    }
  },
  computed: {
    sizeFormClass: function sizeFormClass() {
      return [this.size ? "form-control-".concat(this.size) : null];
    },

    /* istanbul ignore next: don't think this is used */
    sizeBtnClass: function sizeBtnClass() {
      return [this.size ? "btn-".concat(this.size) : null];
    }
  }
};

/* Form control contextual state class computation
 *
 * Returned class is either 'is-valid' or 'is-invalid' based on the 'state' prop
 * state can be one of five values:
 *  - true for is-valid
 *  - false for is-invalid
 *  - null for no contextual state
 */

var formStateMixin = {
  props: {
    state: {
      // Tri-state prop: true, false, null (or undefined)
      type: Boolean,
      default: null
    }
  },
  computed: {
    computedState: function computedState() {
      // If not a boolean, ensure that value is null
      return isBoolean(this.state) ? this.state : null;
    },
    stateClass: function stateClass() {
      var state = this.computedState;
      return state === true ? 'is-valid' : state === false ? 'is-invalid' : null;
    }
  }
};

var BFormCheckbox = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_CHECKBOX,
  mixins: [formRadioCheckMixin, // Includes shared render function
  idMixin, formMixin, formSizeMixin, formStateMixin],
  inject: {
    bvGroup: {
      from: 'bvCheckGroup',
      default: false
    }
  },
  props: {
    value: {
      // type: [String, Number, Boolean, Object],
      default: true
    },
    uncheckedValue: {
      // type: [String, Number, Boolean, Object],
      // Not applicable in multi-check mode
      default: false
    },
    indeterminate: {
      // Not applicable in multi-check mode
      type: Boolean,
      default: false
    },
    switch: {
      // Custom switch styling
      type: Boolean,
      default: false
    },
    checked: {
      // v-model (Array when multiple checkboxes have same name)
      // type: [String, Number, Boolean, Object, Array],
      default: null
    }
  },
  computed: {
    isChecked: function isChecked() {
      var value = this.value,
          checked = this.computedLocalChecked;
      return isArray$1(checked) ? looseIndexOf(checked, value) > -1 : looseEqual(checked, value);
    },
    isRadio: function isRadio() {
      return false;
    },
    isCheck: function isCheck() {
      return true;
    }
  },
  watch: {
    computedLocalChecked: function computedLocalChecked(newValue, oldValue) {
      if (!looseEqual(newValue, oldValue)) {
        this.$emit('input', newValue);
        var $input = this.$refs.input;

        if ($input) {
          this.$emit('update:indeterminate', $input.indeterminate);
        }
      }
    },
    indeterminate: function indeterminate(newVal) {
      this.setIndeterminate(newVal);
    }
  },
  mounted: function mounted() {
    // Set initial indeterminate state
    this.setIndeterminate(this.indeterminate);
  },
  methods: {
    handleChange: function handleChange(_ref) {
      var _ref$target = _ref.target,
          checked = _ref$target.checked,
          indeterminate = _ref$target.indeterminate;
      var value = this.value,
          uncheckedValue = this.uncheckedValue; // Update `computedLocalChecked`

      var localChecked = this.computedLocalChecked;

      if (isArray$1(localChecked)) {
        var index = looseIndexOf(localChecked, value);

        if (checked && index < 0) {
          // Add value to array
          localChecked = localChecked.concat(value);
        } else if (!checked && index > -1) {
          // Remove value from array
          localChecked = localChecked.slice(0, index).concat(localChecked.slice(index + 1));
        }
      } else {
        localChecked = checked ? value : uncheckedValue;
      }

      this.computedLocalChecked = localChecked; // Change is only emitted on user interaction

      this.$emit('change', localChecked); // If this is a child of `<form-checkbox-group>`,
      // we emit a change event on it as well

      if (this.isGroup) {
        this.bvGroup.$emit('change', localChecked);
      }

      this.$emit('update:indeterminate', indeterminate);
    },
    setIndeterminate: function setIndeterminate(state) {
      // Indeterminate only supported in single checkbox mode
      if (isArray$1(this.computedLocalChecked)) {
        state = false;
      }

      var $input = this.$refs.input;

      if ($input) {
        $input.indeterminate = state; // Emit update event to prop

        this.$emit('update:indeterminate', state);
      }
    }
  }
});

var BFormRadio = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_RADIO,
  mixins: [idMixin, formRadioCheckMixin, // Includes shared render function
  formMixin, formSizeMixin, formStateMixin],
  inject: {
    bvGroup: {
      from: 'bvRadioGroup',
      default: false
    }
  },
  props: {
    checked: {
      // v-model
      // type: [String, Number, Boolean, Object],
      default: null
    }
  },
  computed: {
    // Radio Groups can only have a single value, so determining if checked is simple
    isChecked: function isChecked() {
      return looseEqual(this.value, this.computedLocalChecked);
    },
    // Flags for form-radio-check mixin
    isRadio: function isRadio() {
      return true;
    },
    isCheck: function isCheck() {
      return false;
    }
  },
  watch: {
    // Radio Groups can only have a single value, so our watchers are simple
    computedLocalChecked: function computedLocalChecked() {
      this.$emit('input', this.computedLocalChecked);
    }
  },
  methods: {
    handleChange: function handleChange(_ref) {
      var checked = _ref.target.checked;
      var value = this.value;
      this.computedLocalChecked = value; // Change is only emitted on user interaction

      this.$emit('change', checked ? value : null); // If this is a child of form-radio-group, we emit a change event on it as well

      if (this.isGroup) {
        this.bvGroup.$emit('change', checked ? value : null);
      }
    }
  }
});

var formRadioCheckGroupMixin = {
  mixins: [normalizeSlotMixin],
  model: {
    prop: 'checked',
    event: 'input'
  },
  props: {
    validated: {
      type: Boolean,
      default: false
    },
    ariaInvalid: {
      type: [Boolean, String],
      default: false
    },
    stacked: {
      type: Boolean,
      default: false
    },
    plain: {
      type: Boolean,
      default: false
    },
    buttons: {
      // Render as button style
      type: Boolean,
      default: false
    },
    buttonVariant: {
      // Only applicable when rendered with button style
      type: String,
      default: 'secondary'
    }
  },
  computed: {
    inline: function inline() {
      return !this.stacked;
    },
    groupName: function groupName() {
      // Checks/Radios tied to the same model must have the same name,
      // especially for ARIA accessibility.
      return this.name || this.safeId();
    },
    groupClasses: function groupClasses() {
      if (this.buttons) {
        return ['btn-group-toggle', this.inline ? 'btn-group' : 'btn-group-vertical', this.size ? "btn-group-".concat(this.size) : '', this.validated ? "was-validated" : ''];
      }

      return [this.validated ? "was-validated" : ''];
    },
    computedAriaInvalid: function computedAriaInvalid() {
      var ariaInvalid = this.ariaInvalid;

      if (ariaInvalid === true || ariaInvalid === 'true' || ariaInvalid === '') {
        return 'true';
      }

      return this.computedState === false ? 'true' : null;
    }
  },
  watch: {
    checked: function checked(newVal) {
      if (!looseEqual(newVal, this.localChecked)) {
        this.localChecked = newVal;
      }
    },
    localChecked: function localChecked(newValue, oldValue) {
      if (!looseEqual(newValue, oldValue)) {
        this.$emit('input', newValue);
      }
    }
  },
  render: function render(h) {
    var _this = this;

    var $inputs = this.formOptions.map(function (option, index) {
      var key = "BV_option_".concat(index);
      return h(_this.isRadioGroup ? BFormRadio : BFormCheckbox, {
        props: {
          id: _this.safeId(key),
          value: option.value,
          // Individual radios or checks can be disabled in a group
          disabled: option.disabled || false // We don't need to include these, since the input's will know they are inside here
          // name: this.groupName,
          // form: this.form || null,
          // required: Boolean(this.name && this.required)

        },
        key: key
      }, [h('span', {
        domProps: htmlOrText(option.html, option.text)
      })]);
    });
    return h('div', {
      class: [this.groupClasses, 'bv-no-focus-ring'],
      attrs: {
        id: this.safeId(),
        role: this.isRadioGroup ? 'radiogroup' : 'group',
        // Add `tabindex="-1"` to allow group to be focused if needed by screen readers
        tabindex: '-1',
        'aria-required': this.required ? 'true' : null,
        'aria-invalid': this.computedAriaInvalid
      }
    }, [this.normalizeSlot(SLOT_NAME_FIRST), $inputs, this.normalizeSlot()]);
  }
};

// @vue/component
var formCustomMixin = {
  props: {
    plain: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    custom: function custom() {
      return !this.plain;
    }
  }
};

function ownKeys$i(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$i(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$i(Object(source), true).forEach(function (key) { _defineProperty$p(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$i(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$p(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var VALUE_EMPTY_DEPRECATED_MSG = 'Setting "value"/"v-model" to an empty string for reset is deprecated. Set to "null" instead.'; // --- Helper methods ---

var isValidValue = function isValidValue(value) {
  return isFile(value) || isArray$1(value) && value.every(function (v) {
    return isValidValue(v);
  });
}; // Helper method to "safely" get the entry from a data-transfer item

/* istanbul ignore next: not supported in JSDOM */


var getDataTransferItemEntry = function getDataTransferItemEntry(item) {
  return isFunction$1(item.getAsEntry) ? item.getAsEntry() : isFunction$1(item.webkitGetAsEntry) ? item.webkitGetAsEntry() : null;
}; // Drop handler function to get all files

/* istanbul ignore next: not supported in JSDOM */


var getAllFileEntries = function getAllFileEntries(dataTransferItemList) {
  var traverseDirectories = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  return Promise.all(from(dataTransferItemList).filter(function (item) {
    return item.kind === 'file';
  }).map(function (item) {
    var entry = getDataTransferItemEntry(item);

    if (entry) {
      if (entry.isDirectory && traverseDirectories) {
        return getAllFileEntriesInDirectory(entry.createReader(), "".concat(entry.name, "/"));
      } else if (entry.isFile) {
        return new Promise(function (resolve) {
          entry.file(function (file) {
            file.$path = '';
            resolve(file);
          });
        });
      }
    }

    return null;
  }).filter(identity$1));
}; // Get all the file entries (recursive) in a directory

/* istanbul ignore next: not supported in JSDOM */


var getAllFileEntriesInDirectory = function getAllFileEntriesInDirectory(directoryReader) {
  var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return new Promise(function (resolve) {
    var entryPromises = [];

    var readDirectoryEntries = function readDirectoryEntries() {
      directoryReader.readEntries(function (entries) {
        if (entries.length === 0) {
          resolve(Promise.all(entryPromises).then(function (entries) {
            return flatten(entries);
          }));
        } else {
          entryPromises.push(Promise.all(entries.map(function (entry) {
            if (entry) {
              if (entry.isDirectory) {
                return getAllFileEntriesInDirectory(entry.createReader(), "".concat(path).concat(entry.name, "/"));
              } else if (entry.isFile) {
                return new Promise(function (resolve) {
                  entry.file(function (file) {
                    file.$path = "".concat(path).concat(file.name);
                    resolve(file);
                  });
                });
              }
            }

            return null;
          }).filter(identity$1)));
          readDirectoryEntries();
        }
      });
    };

    readDirectoryEntries();
  });
}; // @vue/component


var BFormFile = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_FILE,
  mixins: [attrsMixin, idMixin, formMixin, formStateMixin, formCustomMixin, normalizeSlotMixin],
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    size: {
      type: String,
      default: function _default() {
        return getComponentConfig('BFormControl', 'size');
      }
    },
    value: {
      type: [File$1, Array],
      default: null,
      validator: function validator(value) {
        /* istanbul ignore next */
        if (value === '') {
          warn(VALUE_EMPTY_DEPRECATED_MSG, NAME_FORM_FILE);
          return true;
        }

        return isUndefinedOrNull(value) || isValidValue(value);
      }
    },
    accept: {
      type: String,
      default: ''
    },
    // Instruct input to capture from camera
    capture: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_FILE, 'placeholder');
      }
    },
    browseText: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_FILE, 'browseText');
      }
    },
    dropPlaceholder: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_FILE, 'dropPlaceholder');
      }
    },
    noDropPlaceholder: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_FILE, 'noDropPlaceholder');
      }
    },
    multiple: {
      type: Boolean,
      default: false
    },
    directory: {
      type: Boolean,
      default: false
    },
    // TODO:
    //   Should we deprecate this and only support flat file structures?
    //   Nested file structures are only supported when files are dropped
    //   A Chromium "bug" prevents `webkitEntries` from being populated
    //   on the file input's `change` event and is marked as "WontFix"
    //   Mozilla implemented the behavior the same way as Chromium
    //   See: https://bugs.chromium.org/p/chromium/issues/detail?id=138987
    //   See: https://bugzilla.mozilla.org/show_bug.cgi?id=1326031
    noTraverse: {
      type: Boolean,
      default: false
    },
    noDrop: {
      type: Boolean,
      default: false
    },
    fileNameFormatter: {
      type: Function,
      default: null
    }
  },
  data: function data() {
    return {
      files: [],
      dragging: false,
      // IE 11 doesn't respect setting `evt.dataTransfer.dropEffect`,
      // so we handle it ourselves as well
      // https://stackoverflow.com/a/46915971/2744776
      dropAllowed: !this.noDrop,
      hasFocus: false
    };
  },
  computed: {
    // Convert `accept` to an array of `[{ RegExpr, isMime }, ...]`
    computedAccept: function computedAccept() {
      var accept = this.accept;
      accept = (accept || '').trim().split(/[,\s]+/).filter(Boolean); // Allow any file type/extension

      if (accept.length === 0) {
        return null;
      }

      return accept.map(function (extOrType) {
        var prop = 'name';
        var startMatch = '^';
        var endMatch = '$';

        if (RX_EXTENSION.test(extOrType)) {
          // File extension /\.ext$/
          startMatch = '';
        } else {
          // MIME type /^mime\/.+$/ or /^mime\/type$/
          prop = 'type';

          if (RX_STAR.test(extOrType)) {
            endMatch = '.+$'; // Remove trailing `*`

            extOrType = extOrType.slice(0, -1);
          }
        } // Escape all RegExp special chars


        extOrType = escapeRegExp(extOrType);
        var rx = new RegExp("".concat(startMatch).concat(extOrType).concat(endMatch));
        return {
          rx: rx,
          prop: prop
        };
      });
    },
    computedCapture: function computedCapture() {
      var capture = this.capture;
      return capture === true || capture === '' ? true : capture || null;
    },
    computedAttrs: function computedAttrs() {
      var name = this.name,
          disabled = this.disabled,
          required = this.required,
          form = this.form,
          computedCapture = this.computedCapture,
          accept = this.accept,
          multiple = this.multiple,
          directory = this.directory;
      return _objectSpread$i(_objectSpread$i({}, this.bvAttrs), {}, {
        type: 'file',
        id: this.safeId(),
        name: name,
        disabled: disabled,
        required: required,
        form: form || null,
        capture: computedCapture,
        accept: accept || null,
        multiple: multiple,
        directory: directory,
        webkitdirectory: directory,
        'aria-required': required ? 'true' : null
      });
    },
    clonedFiles: function clonedFiles() {
      return cloneDeep(this.files);
    },
    flattenedFiles: function flattenedFiles() {
      return flattenDeep(this.files);
    },
    fileNames: function fileNames() {
      return this.flattenedFiles.map(function (file) {
        return file.name;
      });
    },
    labelContent: function labelContent() {
      var h = this.$createElement; // Draging active

      /* istanbul ignore next: used by drag/drop which can't be tested easily */

      if (this.dragging && !this.noDrop) {
        return (// TODO: Add additional scope with file count, and other not-allowed reasons
          this.normalizeSlot('drop-placeholder', {
            allowed: this.dropAllowed
          }) || (this.dropAllowed ? this.dropPlaceholder : h('span', {
            staticClass: 'text-danger'
          }, this.noDropPlaceholder))
        );
      } // No file chosen


      if (this.files.length === 0) {
        return this.normalizeSlot('placeholder') || this.placeholder;
      } // There is a slot for formatting the files/names


      if (this.hasNormalizedSlot('file-name')) {
        return this.normalizeSlot('file-name', {
          files: this.flattenedFiles,
          filesTraversed: this.clonedFiles,
          names: this.fileNames
        });
      } // Use the user supplied formatter, or the built in one


      return isFunction$1(this.fileNameFormatter) ? String(this.fileNameFormatter(this.flattenedFiles, this.clonedFiles)) : this.fileNames.join(', ');
    }
  },
  watch: {
    value: function value(newValue) {
      if (!newValue || isArray$1(newValue) && newValue.length === 0) {
        this.reset();
      }
    },
    files: function files(newValue, oldValue) {
      if (!looseEqual(newValue, oldValue)) {
        var multiple = this.multiple,
            noTraverse = this.noTraverse;
        var files = !multiple || noTraverse ? flattenDeep(newValue) : newValue;
        this.$emit('input', multiple ? files : files[0] || null);
      }
    }
  },
  mounted: function mounted() {
    var _this = this;

    // Listen for form reset events, to reset the file input
    var $form = closest('form', this.$el);

    if ($form) {
      eventOn($form, 'reset', this.reset, EVENT_OPTIONS_PASSIVE);
      this.$on('hook:beforeDestroy', function () {
        eventOff($form, 'reset', _this.reset, EVENT_OPTIONS_PASSIVE);
      });
    }
  },
  methods: {
    isFileValid: function isFileValid(file) {
      if (!file) {
        return false;
      }

      var accept = this.computedAccept;
      return accept ? accept.some(function (a) {
        return a.rx.test(file[a.prop]);
      }) : true;
    },
    isFilesArrayValid: function isFilesArrayValid(files) {
      var _this2 = this;

      return isArray$1(files) ? files.every(function (file) {
        return _this2.isFileValid(file);
      }) : this.isFileValid(files);
    },
    setFiles: function setFiles(files) {
      // Reset the dragging flags
      this.dropAllowed = !this.noDrop;
      this.dragging = false; // Set the selected files

      this.files = this.multiple ? this.directory ? files : flattenDeep(files) : flattenDeep(files).slice(0, 1);
    },
    setInputFiles: function setInputFiles(files)
    /* istanbul ignore next: used by Drag/Drop */
    {
      // Try an set the file input files array so that `required`
      // constraint works for dropped files (will fail in IE11 though)
      // To be used only when dropping files
      try {
        // Firefox < 62 workaround exploiting https://bugzilla.mozilla.org/show_bug.cgi?id=1422655
        var dataTransfer = new ClipboardEvent('').clipboardData || new DataTransfer(); // Add flattened files to temp `dataTransfer` object to get a true `FileList` array

        flattenDeep(cloneDeep(files)).forEach(function (file) {
          // Make sure to remove the custom `$path` attribute
          delete file.$path;
          dataTransfer.items.add(file);
        });
        this.$refs.input.files = dataTransfer.files;
      } catch (_unused) {}
    },
    reset: function reset() {
      // IE 11 doesn't support setting `$input.value` to `''` or `null`
      // So we use this little extra hack to reset the value, just in case
      // This also appears to work on modern browsers as well
      // Wrapped in try in case IE 11 or mobile Safari crap out
      try {
        var $input = this.$refs.input;
        $input.value = '';
        $input.type = '';
        $input.type = 'file';
      } catch (_unused2) {}

      this.files = [];
    },
    handleFiles: function handleFiles(files) {
      var isDrop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      if (isDrop) {
        // When dropped, make sure to filter files with the internal `accept` logic
        var filteredFiles = files.filter(this.isFilesArrayValid); // Only update files when we have any after filtering

        if (filteredFiles.length > 0) {
          this.setFiles(filteredFiles); // Try an set the file input's files array so that `required`
          // constraint works for dropped files (will fail in IE 11 though)

          this.setInputFiles(filteredFiles);
        }
      } else {
        // We always update the files from the `change` event
        this.setFiles(files);
      }
    },
    focusHandler: function focusHandler(evt) {
      // Bootstrap v4 doesn't have focus styling for custom file input
      // Firefox has a `[type=file]:focus ~ sibling` selector issue,
      // so we add a `focus` class to get around these bugs
      if (this.plain || evt.type === 'focusout') {
        this.hasFocus = false;
      } else {
        // Add focus styling for custom file input
        this.hasFocus = true;
      }
    },
    onChange: function onChange(evt) {
      var _this3 = this;

      var type = evt.type,
          target = evt.target,
          _evt$dataTransfer = evt.dataTransfer,
          dataTransfer = _evt$dataTransfer === void 0 ? {} : _evt$dataTransfer;
      var isDrop = type === 'drop'; // Always emit original event

      this.$emit('change', evt);
      var items = from(dataTransfer.items || []);

      if (hasPromiseSupport && items.length > 0 && !isNull(getDataTransferItemEntry(items[0]))) {
        // Drop handling for modern browsers
        // Supports nested directory structures in `directory` mode

        /* istanbul ignore next: not supported in JSDOM */
        getAllFileEntries(items, this.directory).then(function (files) {
          return _this3.handleFiles(files, isDrop);
        });
      } else {
        // Standard file input handling (native file input change event),
        // or fallback drop mode (IE 11 / Opera) which don't support `directory` mode
        var files = from(target.files || dataTransfer.files || []).map(function (file) {
          // Add custom `$path` property to each file (to be consistent with drop mode)
          file.$path = file.webkitRelativePath || '';
          return file;
        });
        this.handleFiles(files, isDrop);
      }
    },
    onDragenter: function onDragenter(evt) {
      stopEvent(evt);
      this.dragging = true;
      var _evt$dataTransfer2 = evt.dataTransfer,
          dataTransfer = _evt$dataTransfer2 === void 0 ? {} : _evt$dataTransfer2; // Early exit when the input or dropping is disabled

      if (this.noDrop || this.disabled || !this.dropAllowed) {
        // Show deny feedback

        /* istanbul ignore next: not supported in JSDOM */
        dataTransfer.dropEffect = 'none';
        this.dropAllowed = false;
        return;
      }
      /* istanbul ignore next: not supported in JSDOM */


      dataTransfer.dropEffect = 'copy';
    },
    // Note this event fires repeatedly while the mouse is over the dropzone at
    // intervals in the milliseconds, so avoid doing much processing in here
    onDragover: function onDragover(evt) {
      stopEvent(evt);
      this.dragging = true;
      var _evt$dataTransfer3 = evt.dataTransfer,
          dataTransfer = _evt$dataTransfer3 === void 0 ? {} : _evt$dataTransfer3; // Early exit when the input or dropping is disabled

      if (this.noDrop || this.disabled || !this.dropAllowed) {
        // Show deny feedback

        /* istanbul ignore next: not supported in JSDOM */
        dataTransfer.dropEffect = 'none';
        this.dropAllowed = false;
        return;
      }
      /* istanbul ignore next: not supported in JSDOM */


      dataTransfer.dropEffect = 'copy';
    },
    onDragleave: function onDragleave(evt) {
      var _this4 = this;

      stopEvent(evt);
      this.$nextTick(function () {
        _this4.dragging = false; // Reset `dropAllowed` to default

        _this4.dropAllowed = !_this4.noDrop;
      });
    },
    // Triggered by a file drop onto drop target
    onDrop: function onDrop(evt) {
      var _this5 = this;

      stopEvent(evt);
      this.dragging = false; // Early exit when the input or dropping is disabled

      if (this.noDrop || this.disabled || !this.dropAllowed) {
        this.$nextTick(function () {
          // Reset `dropAllowed` to default
          _this5.dropAllowed = !_this5.noDrop;
        });
        return;
      }

      this.onChange(evt);
    }
  },
  render: function render(h) {
    var custom = this.custom,
        plain = this.plain,
        size = this.size,
        dragging = this.dragging,
        stateClass = this.stateClass; // Form Input

    var $input = h('input', {
      ref: 'input',
      class: [{
        'form-control-file': plain,
        'custom-file-input': custom,
        focus: custom && this.hasFocus
      }, stateClass],
      // With IE 11, the input gets in the "way" of the drop events,
      // so we move it out of the way by putting it behind the label
      // Bootstrap v4 has it in front
      style: custom ? {
        zIndex: -5
      } : {},
      attrs: this.computedAttrs,
      on: {
        change: this.onChange,
        focusin: this.focusHandler,
        focusout: this.focusHandler,
        reset: this.reset
      }
    });

    if (plain) {
      return $input;
    } // Overlay label


    var $label = h('label', {
      staticClass: 'custom-file-label',
      class: {
        dragging: dragging
      },
      attrs: {
        for: this.safeId(),
        // This goes away in Bootstrap v5
        'data-browse': this.browseText || null
      }
    }, [h('span', {
      staticClass: 'd-block form-file-text',
      // `pointer-events: none` is used to make sure
      // the drag events fire only on the label
      style: {
        pointerEvents: 'none'
      }
    }, [this.labelContent])]); // Return rendered custom file input

    return h('div', {
      staticClass: 'custom-file b-form-file',
      class: [_defineProperty$p({}, "b-custom-control-".concat(size), size), stateClass],
      attrs: {
        id: this.safeId('_BV_file_outer_')
      },
      on: {
        dragenter: this.onDragenter,
        dragover: this.onDragover,
        dragleave: this.onDragleave,
        drop: this.onDrop
      }
    }, [$input, $label]);
  }
});

var escapeChar = function escapeChar(value) {
  return '\\' + value;
}; // The `cssEscape()` util is based on this `CSS.escape()` polyfill:
// https://github.com/mathiasbynens/CSS.escape


var cssEscape = function cssEscape(value) {
  value = toString$1(value);
  var length = value.length;
  var firstCharCode = value.charCodeAt(0);
  return value.split('').reduce(function (result, char, index) {
    var charCode = value.charCodeAt(index); // If the character is NULL (U+0000), use (U+FFFD) as replacement

    if (charCode === 0x0000) {
      return result + "\uFFFD";
    } // If the character ...


    if ( // ... is U+007F OR
    charCode === 0x007f || // ... is in the range [\1-\1F] (U+0001 to U+001F) OR ...
    charCode >= 0x0001 && charCode <= 0x001f || // ... is the first character and is in the range [0-9] (U+0030 to U+0039) OR ...
    index === 0 && charCode >= 0x0030 && charCode <= 0x0039 || // ... is the second character and is in the range [0-9] (U+0030 to U+0039)
    // and the first character is a `-` (U+002D) ...
    index === 1 && charCode >= 0x0030 && charCode <= 0x0039 && firstCharCode === 0x002d) {
      // ... https://drafts.csswg.org/cssom/#escape-a-character-as-code-point
      return result + escapeChar("".concat(charCode.toString(16), " "));
    } // If the character ...


    if ( // ... is the first character AND ...
    index === 0 && // ... is a `-` (U+002D) AND ...
    charCode === 0x002d && // ... there is no second character ...
    length === 1) {
      // ... use the escaped character
      return result + escapeChar(char);
    } // If the character ...


    if ( // ... is greater than or equal to U+0080 OR ...
    charCode >= 0x0080 || // ... is `-` (U+002D) OR ...
    charCode === 0x002d || // ... is `_` (U+005F) OR ...
    charCode === 0x005f || // ... is in the range [0-9] (U+0030 to U+0039) OR ...
    charCode >= 0x0030 && charCode <= 0x0039 || // ... is in the range [A-Z] (U+0041 to U+005A) OR ...
    charCode >= 0x0041 && charCode <= 0x005a || // ... is in the range [a-z] (U+0061 to U+007A) ...
    charCode >= 0x0061 && charCode <= 0x007a) {
      // ... use the character itself
      return result + char;
    } // Otherwise use the escaped character
    // See: https://drafts.csswg.org/cssom/#escape-a-character


    return result + escapeChar(char);
  }, '');
};

function ownKeys$j(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$j(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$j(Object(source), true).forEach(function (key) { _defineProperty$q(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$j(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$q(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var boolStrNum = function boolStrNum() {
  return {
    type: [Boolean, String, Number],
    default: false
  };
}; // Generates a prop object with a type of `[String, Number]`


var strNum = function strNum() {
  return {
    type: [String, Number],
    default: null
  };
}; // Compute a breakpoint class name


var computeBreakpoint = function computeBreakpoint(type, breakpoint, val) {
  var className = type;

  if (isUndefinedOrNull(val) || val === false) {
    return undefined;
  }

  if (breakpoint) {
    className += "-".concat(breakpoint);
  } // Handling the boolean style prop when accepting [Boolean, String, Number]
  // means Vue will not convert <b-col sm></b-col> to sm: true for us.
  // Since the default is false, an empty string indicates the prop's presence.


  if (type === 'col' && (val === '' || val === true)) {
    // .col-md
    return lowerCase(className);
  } // .order-md-6


  className += "-".concat(val);
  return lowerCase(className);
}; // Memoized function for better performance on generating class names


var computeBreakpointClass = memoize$1(computeBreakpoint); // Cached copy of the breakpoint prop names

var breakpointPropMap = create(null); // Lazy evaled props factory for BCol

var generateProps = function generateProps() {
  // Grab the breakpoints from the cached config (exclude the '' (xs) breakpoint)
  var breakpoints = getBreakpointsUpCached().filter(identity$1); // Supports classes like: .col-sm, .col-md-6, .col-lg-auto

  var breakpointCol = breakpoints.reduce(function (propMap, breakpoint) {
    if (breakpoint) {
      // We filter out the '' breakpoint (xs), as making a prop name ''
      // would not work. The `cols` prop is used for `xs`
      propMap[breakpoint] = boolStrNum();
    }

    return propMap;
  }, create(null)); // Supports classes like: .offset-md-1, .offset-lg-12

  var breakpointOffset = breakpoints.reduce(function (propMap, breakpoint) {
    propMap[suffixPropName(breakpoint, 'offset')] = strNum();
    return propMap;
  }, create(null)); // Supports classes like: .order-md-1, .order-lg-12

  var breakpointOrder = breakpoints.reduce(function (propMap, breakpoint) {
    propMap[suffixPropName(breakpoint, 'order')] = strNum();
    return propMap;
  }, create(null)); // For loop doesn't need to check hasOwnProperty
  // when using an object created from null

  breakpointPropMap = assign(create(null), {
    col: keys$1(breakpointCol),
    offset: keys$1(breakpointOffset),
    order: keys$1(breakpointOrder)
  }); // Return the generated props

  return _objectSpread$j(_objectSpread$j(_objectSpread$j(_objectSpread$j({
    // Generic flexbox .col (xs)
    col: {
      type: Boolean,
      default: false
    },
    // .col-[1-12]|auto  (xs)
    cols: strNum()
  }, breakpointCol), {}, {
    offset: strNum()
  }, breakpointOffset), {}, {
    order: strNum()
  }, breakpointOrder), {}, {
    // Flex alignment
    alignSelf: {
      type: String,
      default: null,
      validator: function validator(str) {
        return arrayIncludes(['auto', 'start', 'end', 'center', 'baseline', 'stretch'], str);
      }
    },
    tag: {
      type: String,
      default: 'div'
    }
  });
}; // We do not use Vue.extend here as that would evaluate the props
// immediately, which we do not want to happen
// @vue/component


var BCol = {
  name: NAME_COL,
  functional: true,

  get props() {
    // Allow props to be lazy evaled on first access and
    // then they become a non-getter afterwards.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/get#Smart_self-overwriting_lazy_getters
    delete this.props; // eslint-disable-next-line no-return-assign

    return this.props = generateProps();
  },

  render: function render(h, _ref) {
    var _classList$push;

    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    var classList = []; // Loop through `col`, `offset`, `order` breakpoint props

    for (var type in breakpointPropMap) {
      // Returns colSm, offset, offsetSm, orderMd, etc.
      var _keys = breakpointPropMap[type];

      for (var i = 0; i < _keys.length; i++) {
        // computeBreakpoint(col, colSm => Sm, value=[String, Number, Boolean])
        var c = computeBreakpointClass(type, _keys[i].replace(type, ''), props[_keys[i]]); // If a class is returned, push it onto the array.

        if (c) {
          classList.push(c);
        }
      }
    }

    var hasColClasses = classList.some(function (className) {
      return RX_COL_CLASS.test(className);
    });
    classList.push((_classList$push = {
      // Default to .col if no other col-{bp}-* classes generated nor `cols` specified.
      col: props.col || !hasColClasses && !props.cols
    }, _defineProperty$q(_classList$push, "col-".concat(props.cols), props.cols), _defineProperty$q(_classList$push, "offset-".concat(props.offset), props.offset), _defineProperty$q(_classList$push, "order-".concat(props.order), props.order), _defineProperty$q(_classList$push, "align-self-".concat(props.alignSelf), props.alignSelf), _classList$push));
    return h(props.tag, a(data, {
      class: classList
    }), children);
  }
};

function ownKeys$k(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$k(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$k(Object(source), true).forEach(function (key) { _defineProperty$r(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$k(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$r(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Selector for finding first input in the form-group

var SELECTOR$1 = 'input:not([disabled]),textarea:not([disabled]),select:not([disabled])'; // --- Render methods ---

var renderInvalidFeedback = function renderInvalidFeedback(h, ctx) {
  var content = ctx.normalizeSlot('invalid-feedback') || ctx.invalidFeedback;
  var invalidFeedback = h();

  if (content) {
    invalidFeedback = h(BFormInvalidFeedback, {
      props: {
        id: ctx.invalidFeedbackId,
        // If state is explicitly false, always show the feedback
        state: ctx.computedState,
        tooltip: ctx.tooltip,
        ariaLive: ctx.feedbackAriaLive,
        role: ctx.feedbackAriaLive ? 'alert' : null
      },
      attrs: {
        tabindex: content ? '-1' : null
      }
    }, [content]);
  }

  return invalidFeedback;
};

var renderValidFeedback = function renderValidFeedback(h, ctx) {
  var content = ctx.normalizeSlot('valid-feedback') || ctx.validFeedback;
  var validFeedback = h();

  if (content) {
    validFeedback = h(BFormValidFeedback, {
      props: {
        id: ctx.validFeedbackId,
        // If state is explicitly true, always show the feedback
        state: ctx.computedState,
        tooltip: ctx.tooltip,
        ariaLive: ctx.feedbackAriaLive,
        role: ctx.feedbackAriaLive ? 'alert' : null
      },
      attrs: {
        tabindex: content ? '-1' : null
      }
    }, [content]);
  }

  return validFeedback;
};

var renderHelpText = function renderHelpText(h, ctx) {
  // Form help text (description)
  var content = ctx.normalizeSlot(SLOT_NAME_DESCRIPTION) || ctx.description;
  var description = h();

  if (content) {
    description = h(BFormText, {
      attrs: {
        id: ctx.descriptionId,
        tabindex: content ? '-1' : null
      }
    }, [content]);
  }

  return description;
};

var renderLabel = function renderLabel(h, ctx) {
  // Render label/legend inside b-col if necessary
  var content = ctx.normalizeSlot(SLOT_NAME_LABEL) || ctx.label;
  var labelFor = ctx.labelFor;
  var isLegend = !labelFor;
  var isHorizontal = ctx.isHorizontal;
  var labelTag = isLegend ? 'legend' : 'label';

  if (!content && !isHorizontal) {
    return h();
  } else if (ctx.labelSrOnly) {
    var label = h();

    if (content) {
      label = h(labelTag, {
        class: 'sr-only',
        attrs: {
          id: ctx.labelId,
          for: labelFor || null
        }
      }, [content]);
    }

    return h(isHorizontal ? BCol : 'div', {
      props: isHorizontal ? ctx.labelColProps : {}
    }, [label]);
  } else {
    return h(isHorizontal ? BCol : labelTag, {
      on: isLegend ? {
        click: ctx.legendClick
      } : {},
      props: isHorizontal ? _objectSpread$k({
        tag: labelTag
      }, ctx.labelColProps) : {},
      attrs: {
        id: ctx.labelId,
        for: labelFor || null,
        // We add a tab index to legend so that screen readers
        // will properly read the aria-labelledby in IE.
        tabindex: isLegend ? '-1' : null
      },
      class: [// Hide the focus ring on the legend
      isLegend ? 'bv-no-focus-ring' : '', // When horizontal or if a legend is rendered, add col-form-label
      // for correct sizing as Bootstrap has inconsistent font styling
      // for legend in non-horizontal form-groups.
      // See: https://github.com/twbs/bootstrap/issues/27805
      isHorizontal || isLegend ? 'col-form-label' : '', // Emulate label padding top of 0 on legend when not horizontal
      !isHorizontal && isLegend ? 'pt-0' : '', // If not horizontal and not a legend, we add d-block to label
      // so that label-align works
      !isHorizontal && !isLegend ? 'd-block' : '', ctx.labelSize ? "col-form-label-".concat(ctx.labelSize) : '', ctx.labelAlignClasses, ctx.labelClass]
    }, [content]);
  }
}; // -- BFormGroup Prop factory -- used for lazy generation of props
// Memoize this function to return cached values to
// save time in computed functions


var makePropName = memoize$1(function () {
  var breakpoint = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var prefix = arguments.length > 1 ? arguments[1] : undefined;
  return "".concat(prefix).concat(upperFirst(breakpoint));
}); // BFormGroup prop generator for lazy generation of props

var generateProps$1 = function generateProps() {
  var CODE_BREAKPOINTS = getBreakpointsUpCached(); // Generate the labelCol breakpoint props

  var bpLabelColProps = CODE_BREAKPOINTS.reduce(function (props, breakpoint) {
    // i.e. label-cols, label-cols-sm, label-cols-md, ...
    props[makePropName(breakpoint, 'labelCols')] = {
      type: [Number, String, Boolean],
      default: breakpoint ? false : null
    };
    return props;
  }, create(null)); // Generate the labelAlign breakpoint props

  var bpLabelAlignProps = CODE_BREAKPOINTS.reduce(function (props, breakpoint) {
    // label-align, label-align-sm, label-align-md, ...
    props[makePropName(breakpoint, 'labelAlign')] = {
      type: String // left, right, center
      // default: null

    };
    return props;
  }, create(null));
  return _objectSpread$k(_objectSpread$k(_objectSpread$k({
    label: {
      type: String // default: null

    },
    labelFor: {
      type: String // default: null

    },
    labelSize: {
      type: String // default: null

    },
    labelSrOnly: {
      type: Boolean,
      default: false
    }
  }, bpLabelColProps), bpLabelAlignProps), {}, {
    labelClass: {
      type: [String, Array, Object] // default: null

    },
    description: {
      type: String // default: null

    },
    invalidFeedback: {
      type: String // default: null

    },
    validFeedback: {
      type: String // default: null

    },
    tooltip: {
      // Enable tooltip style feedback
      type: Boolean,
      default: false
    },
    feedbackAriaLive: {
      type: String,
      default: 'assertive'
    },
    validated: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  });
}; // We do not use Vue.extend here as that would evaluate the props
// immediately, which we do not want to happen
// @vue/component


var BFormGroup = {
  name: NAME_FORM_GROUP,
  mixins: [idMixin, formStateMixin, normalizeSlotMixin],

  get props() {
    // Allow props to be lazy evaled on first access and
    // then they become a non-getter afterwards.
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/get#Smart_self-overwriting_lazy_getters
    delete this.props; // eslint-disable-next-line no-return-assign

    return this.props = generateProps$1();
  },

  computed: {
    labelColProps: function labelColProps() {
      var _this = this;

      var props = {};
      getBreakpointsUpCached().forEach(function (breakpoint) {
        // Grab the value if the label column breakpoint prop
        var propVal = _this[makePropName(breakpoint, 'labelCols')]; // Handle case where the prop's value is an empty string,
        // which represents true


        propVal = propVal === '' ? true : propVal || false;

        if (!isBoolean(propVal) && propVal !== 'auto') {
          // Convert to column size to number
          propVal = toInteger(propVal, 0); // Ensure column size is greater than 0

          propVal = propVal > 0 ? propVal : false;
        }

        if (propVal) {
          // Add the prop to the list of props to give to b-col
          // If breakpoint is '' (labelCols=true), then we use the
          // col prop to make equal width at xs
          var bColPropName = breakpoint || (isBoolean(propVal) ? 'col' : 'cols'); // Add it to the props

          props[bColPropName] = propVal;
        }
      });
      return props;
    },
    labelAlignClasses: function labelAlignClasses() {
      var _this2 = this;

      var classes = [];
      getBreakpointsUpCached().forEach(function (breakpoint) {
        // Assemble the label column breakpoint align classes
        var propVal = _this2[makePropName(breakpoint, 'labelAlign')] || null;

        if (propVal) {
          var className = breakpoint ? "text-".concat(breakpoint, "-").concat(propVal) : "text-".concat(propVal);
          classes.push(className);
        }
      });
      return classes;
    },
    isHorizontal: function isHorizontal() {
      // Determine if the resultant form-group will be rendered
      // horizontal (meaning it has label-col breakpoints)
      return keys$1(this.labelColProps).length > 0;
    },
    labelId: function labelId() {
      return this.hasNormalizedSlot(SLOT_NAME_LABEL) || this.label ? this.safeId('_BV_label_') : null;
    },
    descriptionId: function descriptionId() {
      return this.hasNormalizedSlot(SLOT_NAME_DESCRIPTION) || this.description ? this.safeId('_BV_description_') : null;
    },
    hasInvalidFeedback: function hasInvalidFeedback() {
      // Used for computing aria-describedby
      return this.computedState === false && (this.hasNormalizedSlot('invalid-feedback') || this.invalidFeedback);
    },
    invalidFeedbackId: function invalidFeedbackId() {
      return this.hasInvalidFeedback ? this.safeId('_BV_feedback_invalid_') : null;
    },
    hasValidFeedback: function hasValidFeedback() {
      // Used for computing aria-describedby
      return this.computedState === true && (this.hasNormalizedSlot('valid-feedback') || this.validFeedback);
    },
    validFeedbackId: function validFeedbackId() {
      return this.hasValidFeedback ? this.safeId('_BV_feedback_valid_') : null;
    },
    describedByIds: function describedByIds() {
      // Screen readers will read out any content linked to by aria-describedby
      // even if the content is hidden with `display: none;`, hence we only include
      // feedback IDs if the form-group's state is explicitly valid or invalid.
      return [this.descriptionId, this.invalidFeedbackId, this.validFeedbackId].filter(Boolean).join(' ') || null;
    }
  },
  watch: {
    describedByIds: function describedByIds(add, remove) {
      if (add !== remove) {
        this.setInputDescribedBy(add, remove);
      }
    }
  },
  mounted: function mounted() {
    var _this3 = this;

    this.$nextTick(function () {
      // Set the aria-describedby IDs on the input specified by label-for
      // We do this in a nextTick to ensure the children have finished rendering
      _this3.setInputDescribedBy(_this3.describedByIds);
    });
  },
  methods: {
    legendClick: function legendClick(evt) {
      if (this.labelFor) {
        // Don't do anything if labelFor is set

        /* istanbul ignore next: clicking a label will focus the input, so no need to test */
        return;
      }

      var tagName = evt.target ? evt.target.tagName : '';

      if (/^(input|select|textarea|label|button|a)$/i.test(tagName)) {
        // If clicked an interactive element inside legend,
        // we just let the default happen

        /* istanbul ignore next */
        return;
      }

      var inputs = selectAll(SELECTOR$1, this.$refs.content).filter(isVisible); // If only a single input, focus it, emulating label behaviour

      if (inputs && inputs.length === 1) {
        attemptFocus(inputs[0]);
      }
    },
    setInputDescribedBy: function setInputDescribedBy(add, remove) {
      // Sets the `aria-describedby` attribute on the input if label-for is set.
      // Optionally accepts a string of IDs to remove as the second parameter.
      // Preserves any aria-describedby value(s) user may have on input.
      if (this.labelFor && isBrowser) {
        // We need to escape `labelFor` since it can be user-provided
        var input = select("#".concat(cssEscape(this.labelFor)), this.$refs.content);

        if (input) {
          var adb = 'aria-describedby';
          var ids = (getAttr(input, adb) || '').split(/\s+/);
          add = (add || '').split(/\s+/);
          remove = (remove || '').split(/\s+/); // Update ID list, preserving any original IDs
          // and ensuring the ID's are unique

          ids = ids.filter(function (id) {
            return !arrayIncludes(remove, id);
          }).concat(add).filter(Boolean);
          ids = keys$1(ids.reduce(function (memo, id) {
            return _objectSpread$k(_objectSpread$k({}, memo), {}, _defineProperty$r({}, id, true));
          }, {})).join(' ').trim();

          if (ids) {
            setAttr(input, adb, ids);
          } else {
            // No IDs, so remove the attribute
            removeAttr(input, adb);
          }
        }
      }
    }
  },
  render: function render(h) {
    var isFieldset = !this.labelFor;
    var isHorizontal = this.isHorizontal; // Generate the label

    var label = renderLabel(h, this); // Generate the content

    var content = h(isHorizontal ? BCol : 'div', {
      ref: 'content',
      // Hide focus ring
      staticClass: 'bv-no-focus-ring',
      attrs: {
        tabindex: isFieldset ? '-1' : null,
        role: isFieldset ? 'group' : null
      }
    }, [this.normalizeSlot() || h(), renderInvalidFeedback(h, this), renderValidFeedback(h, this), renderHelpText(h, this)]); // Create the form-group

    var data = {
      staticClass: 'form-group',
      class: [this.validated ? 'was-validated' : null, this.stateClass],
      attrs: {
        id: this.safeId(),
        disabled: isFieldset ? this.disabled : null,
        role: isFieldset ? null : 'group',
        'aria-invalid': this.computedState === false ? 'true' : null,
        // Only apply aria-labelledby if we are a horizontal fieldset
        // as the legend is no longer a direct child of fieldset
        'aria-labelledby': isFieldset && isHorizontal ? this.labelId : null,
        // Only apply aria-describedby IDs if we are a fieldset
        // as the input will have the IDs when not a fieldset
        'aria-describedby': isFieldset ? this.describedByIds : null
      }
    }; // Return it wrapped in a form-group
    // Note: Fieldsets do not support adding `row` or `form-row` directly
    // to them due to browser specific render issues, so we move the `form-row`
    // to an inner wrapper div when horizontal and using a fieldset

    return h(isFieldset ? 'fieldset' : isHorizontal ? BFormRow : 'div', data, isHorizontal && isFieldset ? [h(BFormRow, [label, content])] : [label, content]);
  }
};

// @vue/component
var formSelectionMixin = {
  computed: {
    selectionStart: {
      // Expose selectionStart for formatters, etc
      cache: false,

      /* istanbul ignore next */
      get: function get() {
        return this.$refs.input.selectionStart;
      },

      /* istanbul ignore next */
      set: function set(val) {
        this.$refs.input.selectionStart = val;
      }
    },
    selectionEnd: {
      // Expose selectionEnd for formatters, etc
      cache: false,

      /* istanbul ignore next */
      get: function get() {
        return this.$refs.input.selectionEnd;
      },

      /* istanbul ignore next */
      set: function set(val) {
        this.$refs.input.selectionEnd = val;
      }
    },
    selectionDirection: {
      // Expose selectionDirection for formatters, etc
      cache: false,

      /* istanbul ignore next */
      get: function get() {
        return this.$refs.input.selectionDirection;
      },

      /* istanbul ignore next */
      set: function set(val) {
        this.$refs.input.selectionDirection = val;
      }
    }
  },
  methods: {
    /* istanbul ignore next */
    select: function select() {
      var _this$$refs$input;

      // For external handler that may want a select() method
      (_this$$refs$input = this.$refs.input).select.apply(_this$$refs$input, arguments);
    },

    /* istanbul ignore next */
    setSelectionRange: function setSelectionRange() {
      var _this$$refs$input2;

      // For external handler that may want a setSelectionRange(a,b,c) method
      (_this$$refs$input2 = this.$refs.input).setSelectionRange.apply(_this$$refs$input2, arguments);
    },

    /* istanbul ignore next */
    setRangeText: function setRangeText() {
      var _this$$refs$input3;

      // For external handler that may want a setRangeText(a,b,c) method
      (_this$$refs$input3 = this.$refs.input).setRangeText.apply(_this$$refs$input3, arguments);
    }
  }
};

var formTextMixin = {
  model: {
    prop: 'value',
    event: 'update'
  },
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    ariaInvalid: {
      type: [Boolean, String],
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    plaintext: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String // default: null

    },
    placeholder: {
      type: String // default: null

    },
    formatter: {
      type: Function // default: null

    },
    lazyFormatter: {
      type: Boolean,
      default: false
    },
    trim: {
      type: Boolean,
      default: false
    },
    number: {
      type: Boolean,
      default: false
    },
    lazy: {
      // Only update the `v-model` on blur/change events
      type: Boolean,
      default: false
    },
    debounce: {
      // Debounce timeout (in ms). Not applicable with `lazy` prop
      type: [Number, String],
      default: 0
    }
  },
  data: function data() {
    return {
      localValue: toString$1(this.value),
      vModelValue: this.value
    };
  },
  computed: {
    computedClass: function computedClass() {
      return [{
        // Range input needs class `custom-range`
        'custom-range': this.type === 'range',
        // `plaintext` not supported by `type="range"` or `type="color"`
        'form-control-plaintext': this.plaintext && this.type !== 'range' && this.type !== 'color',
        // `form-control` not used by `type="range"` or `plaintext`
        // Always used by `type="color"`
        'form-control': !this.plaintext && this.type !== 'range' || this.type === 'color'
      }, this.sizeFormClass, this.stateClass];
    },
    computedAriaInvalid: function computedAriaInvalid() {
      if (!this.ariaInvalid || this.ariaInvalid === 'false') {
        // `this.ariaInvalid` is `null` or `false` or 'false'
        return this.computedState === false ? 'true' : null;
      }

      if (this.ariaInvalid === true) {
        // User wants explicit `:aria-invalid="true"`
        return 'true';
      } // Most likely a string value (which could be the string 'true')


      return this.ariaInvalid;
    },
    computedDebounce: function computedDebounce() {
      // Ensure we have a positive number equal to or greater than 0
      return mathMax(toInteger(this.debounce, 0), 0);
    },
    hasFormatter: function hasFormatter() {
      return isFunction$1(this.formatter);
    }
  },
  watch: {
    value: function value(newVal) {
      var stringifyValue = toString$1(newVal);

      if (stringifyValue !== this.localValue && newVal !== this.vModelValue) {
        // Clear any pending debounce timeout, as we are overwriting the user input
        this.clearDebounce(); // Update the local values

        this.localValue = stringifyValue;
        this.vModelValue = newVal;
      }
    }
  },
  created: function created() {
    // Create private non-reactive props
    this.$_inputDebounceTimer = null;
  },
  mounted: function mounted() {
    // Set up destroy handler
    this.$on('hook:beforeDestroy', this.clearDebounce); // Preset the internal state

    var value = this.value;
    var stringifyValue = toString$1(value);
    /* istanbul ignore next */

    if (stringifyValue !== this.localValue && value !== this.vModelValue) {
      this.localValue = stringifyValue;
      this.vModelValue = value;
    }
  },
  methods: {
    clearDebounce: function clearDebounce() {
      clearTimeout(this.$_inputDebounceTimer);
      this.$_inputDebounceTimer = null;
    },
    formatValue: function formatValue(value, evt) {
      var force = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      value = toString$1(value);

      if (this.hasFormatter && (!this.lazyFormatter || force)) {
        value = this.formatter(value, evt);
      }

      return value;
    },
    modifyValue: function modifyValue(value) {
      // Emulate `.trim` modifier behaviour
      if (this.trim) {
        value = value.trim();
      } // Emulate `.number` modifier behaviour


      if (this.number) {
        value = toFloat(value, value);
      }

      return value;
    },
    updateValue: function updateValue(value) {
      var _this = this;

      var force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var lazy = this.lazy;

      if (lazy && !force) {
        return;
      } // Make sure to always clear the debounce when `updateValue()`
      // is called, even when the v-model hasn't changed


      this.clearDebounce(); // Define the shared update logic in a method to be able to use
      // it for immediate and debounced value changes

      var doUpdate = function doUpdate() {
        value = _this.modifyValue(value);

        if (value !== _this.vModelValue) {
          _this.vModelValue = value;

          _this.$emit('update', value);
        } else if (_this.hasFormatter) {
          // When the `vModelValue` hasn't changed but the actual input value
          // is out of sync, make sure to change it to the given one
          // Usually caused by browser autocomplete and how it triggers the
          // change or input event, or depending on the formatter function
          // https://github.com/bootstrap-vue/bootstrap-vue/issues/2657
          // https://github.com/bootstrap-vue/bootstrap-vue/issues/3498

          /* istanbul ignore next: hard to test */
          var $input = _this.$refs.input;
          /* istanbul ignore if: hard to test out of sync value */

          if ($input && value !== $input.value) {
            $input.value = value;
          }
        }
      }; // Only debounce the value update when a value greater than `0`
      // is set and we are not in lazy mode or this is a forced update


      var debounce = this.computedDebounce;

      if (debounce > 0 && !lazy && !force) {
        this.$_inputDebounceTimer = setTimeout(doUpdate, debounce);
      } else {
        // Immediately update the v-model
        doUpdate();
      }
    },
    onInput: function onInput(evt) {
      // `evt.target.composing` is set by Vue
      // https://github.com/vuejs/vue/blob/dev/src/platforms/web/runtime/directives/model.js
      // TODO: Is this needed now with the latest Vue?

      /* istanbul ignore if: hard to test composition events */
      if (evt.target.composing) {
        return;
      }

      var value = evt.target.value;
      var formattedValue = this.formatValue(value, evt); // Exit when the `formatter` function strictly returned `false`
      // or prevented the input event

      /* istanbul ignore next */

      if (formattedValue === false || evt.defaultPrevented) {
        stopEvent(evt, {
          propagation: false
        });
        return;
      }

      this.localValue = formattedValue;
      this.updateValue(formattedValue);
      this.$emit('input', formattedValue);
    },
    onChange: function onChange(evt) {
      var value = evt.target.value;
      var formattedValue = this.formatValue(value, evt); // Exit when the `formatter` function strictly returned `false`
      // or prevented the input event

      /* istanbul ignore next */

      if (formattedValue === false || evt.defaultPrevented) {
        stopEvent(evt, {
          propagation: false
        });
        return;
      }

      this.localValue = formattedValue;
      this.updateValue(formattedValue, true);
      this.$emit('change', formattedValue);
    },
    onBlur: function onBlur(evt) {
      // Apply the `localValue` on blur to prevent cursor jumps
      // on mobile browsers (e.g. caused by autocomplete)
      var value = evt.target.value;
      var formattedValue = this.formatValue(value, evt, true);

      if (formattedValue !== false) {
        // We need to use the modified value here to apply the
        // `.trim` and `.number` modifiers properly
        this.localValue = toString$1(this.modifyValue(formattedValue)); // We pass the formatted value here since the `updateValue` method
        // handles the modifiers itself

        this.updateValue(formattedValue, true);
      } // Emit native blur event


      this.$emit('blur', evt);
    },
    focus: function focus() {
      // For external handler that may want a focus method
      if (!this.disabled) {
        attemptFocus(this.$el);
      }
    },
    blur: function blur() {
      // For external handler that may want a blur method
      if (!this.disabled) {
        attemptBlur(this.$el);
      }
    }
  }
};

// @vue/component
var formValidityMixin = {
  computed: {
    validity: {
      // Expose validity property
      cache: false,

      /* istanbul ignore next */
      get: function get() {
        return this.$refs.input.validity;
      }
    },
    validationMessage: {
      // Expose validationMessage property
      cache: false,

      /* istanbul ignore next */
      get: function get() {
        return this.$refs.input.validationMessage;
      }
    },
    willValidate: {
      // Expose willValidate property
      cache: false,

      /* istanbul ignore next */
      get: function get() {
        return this.$refs.input.willValidate;
      }
    }
  },
  methods: {
    /* istanbul ignore next */
    setCustomValidity: function setCustomValidity() {
      var _this$$refs$input;

      // For external handler that may want a setCustomValidity(...) method
      return (_this$$refs$input = this.$refs.input).setCustomValidity.apply(_this$$refs$input, arguments);
    },

    /* istanbul ignore next */
    checkValidity: function checkValidity() {
      var _this$$refs$input2;

      // For external handler that may want a checkValidity(...) method
      return (_this$$refs$input2 = this.$refs.input).checkValidity.apply(_this$$refs$input2, arguments);
    },

    /* istanbul ignore next */
    reportValidity: function reportValidity() {
      var _this$$refs$input3;

      // For external handler that may want a reportValidity(...) method
      return (_this$$refs$input3 = this.$refs.input).reportValidity.apply(_this$$refs$input3, arguments);
    }
  }
};

function ownKeys$l(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$l(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$l(Object(source), true).forEach(function (key) { _defineProperty$s(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$l(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$s(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var TYPES = ['text', 'password', 'email', 'number', 'url', 'tel', 'search', 'range', 'color', 'date', 'time', 'datetime', 'datetime-local', 'month', 'week']; // @vue/component

var BFormInput = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_INPUT,
  // Mixin order is important!
  mixins: [listenersMixin, idMixin, formMixin, formSizeMixin, formStateMixin, formTextMixin, formSelectionMixin, formValidityMixin],
  props: {
    // `value` prop is defined in form-text mixin
    type: {
      type: String,
      default: 'text',
      validator: function validator(type) {
        return arrayIncludes(TYPES, type);
      }
    },
    noWheel: {
      // Disable mousewheel to prevent wheel from
      // changing values (i.e. number/date)
      type: Boolean,
      default: false
    },
    min: {
      type: [String, Number] // default: null

    },
    max: {
      type: [String, Number] // default: null

    },
    step: {
      type: [String, Number] // default: null

    },
    list: {
      type: String // default: null

    }
  },
  computed: {
    localType: function localType() {
      // We only allow certain types
      return arrayIncludes(TYPES, this.type) ? this.type : 'text';
    },
    computedAttrs: function computedAttrs() {
      var type = this.localType,
          disabled = this.disabled,
          placeholder = this.placeholder,
          required = this.required,
          min = this.min,
          max = this.max,
          step = this.step;
      return {
        id: this.safeId(),
        name: this.name || null,
        form: this.form || null,
        type: type,
        disabled: disabled,
        placeholder: placeholder,
        required: required,
        autocomplete: this.autocomplete || null,
        readonly: this.readonly || this.plaintext,
        min: min,
        max: max,
        step: step,
        list: type !== 'password' ? this.list : null,
        'aria-required': required ? 'true' : null,
        'aria-invalid': this.computedAriaInvalid
      };
    },
    computedListeners: function computedListeners() {
      return _objectSpread$l(_objectSpread$l({}, this.bvListeners), {}, {
        input: this.onInput,
        change: this.onChange,
        blur: this.onBlur
      });
    }
  },
  watch: {
    noWheel: function noWheel(newVal) {
      this.setWheelStopper(newVal);
    }
  },
  mounted: function mounted() {
    this.setWheelStopper(this.noWheel);
  },

  /* istanbul ignore next */
  deactivated: function deactivated() {
    // Turn off listeners when keep-alive component deactivated

    /* istanbul ignore next */
    this.setWheelStopper(false);
  },

  /* istanbul ignore next */
  activated: function activated() {
    // Turn on listeners (if no-wheel) when keep-alive component activated

    /* istanbul ignore next */
    this.setWheelStopper(this.noWheel);
  },
  beforeDestroy: function beforeDestroy() {
    /* istanbul ignore next */
    this.setWheelStopper(false);
  },
  methods: {
    setWheelStopper: function setWheelStopper(on) {
      var input = this.$el; // We use native events, so that we don't interfere with propagation

      eventOnOff(on, input, 'focus', this.onWheelFocus);
      eventOnOff(on, input, 'blur', this.onWheelBlur);

      if (!on) {
        eventOff(document, 'wheel', this.stopWheel);
      }
    },
    onWheelFocus: function onWheelFocus() {
      eventOn(document, 'wheel', this.stopWheel);
    },
    onWheelBlur: function onWheelBlur() {
      eventOff(document, 'wheel', this.stopWheel);
    },
    stopWheel: function stopWheel(evt) {
      stopEvent(evt, {
        propagation: false
      });
      attemptBlur(this.$el);
    }
  },
  render: function render(h) {
    return h('input', {
      ref: 'input',
      class: this.computedClass,
      attrs: this.computedAttrs,
      domProps: {
        value: this.localValue
      },
      on: this.computedListeners
    });
  }
});

var props$f = {
  checked: {
    // type: [String, Number, Boolean, Object],
    default: null
  }
}; // @vue/component

var BFormRadioGroup = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_RADIO_GROUP,
  mixins: [idMixin, formMixin, formRadioCheckGroupMixin, // Includes render function
  formOptionsMixin, formSizeMixin, formStateMixin],
  provide: function provide() {
    return {
      bvRadioGroup: this
    };
  },
  props: props$f,
  data: function data() {
    return {
      localChecked: this.checked
    };
  },
  computed: {
    isRadioGroup: function isRadioGroup() {
      return true;
    }
  }
});

var BFormTag = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_TAG,
  mixins: [idMixin, normalizeSlotMixin],
  props: {
    variant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAG, 'variant');
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    title: {
      type: String // default: null

    },
    pill: {
      type: Boolean,
      default: false
    },
    removeLabel: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAG, 'removeLabel');
      }
    },
    tag: {
      type: String,
      default: 'span'
    }
  },
  methods: {
    onDelete: function onDelete(evt) {
      var type = evt.type,
          keyCode = evt.keyCode;

      if (!this.disabled && (type === 'click' || type === 'keydown' && keyCode === CODE_DELETE)) {
        this.$emit('remove');
      }
    }
  },
  render: function render(h) {
    var tagId = this.safeId();
    var tagLabelId = this.safeId('_taglabel_');
    var $remove = h();

    if (!this.disabled) {
      $remove = h(BButtonClose, {
        staticClass: 'b-form-tag-remove',
        props: {
          ariaLabel: this.removeLabel
        },
        attrs: {
          'aria-controls': tagId,
          'aria-describedby': tagLabelId,
          'aria-keyshortcuts': 'Delete'
        },
        on: {
          click: this.onDelete,
          keydown: this.onDelete
        }
      });
    }

    var $tag = h('span', {
      staticClass: 'b-form-tag-content flex-grow-1 text-truncate',
      attrs: {
        id: tagLabelId
      }
    }, this.normalizeSlot() || this.title || [h()]);
    return h(BBadge, {
      staticClass: 'b-form-tag d-inline-flex align-items-baseline mw-100',
      class: {
        disabled: this.disabled
      },
      attrs: {
        id: tagId,
        title: this.title || null,
        'aria-labelledby': tagLabelId
      },
      props: {
        tag: this.tag,
        variant: this.variant,
        pill: this.pill
      }
    }, [$tag, $remove]);
  }
});

function _toConsumableArray$2(arr) { return _arrayWithoutHoles$2(arr) || _iterableToArray$2(arr) || _unsupportedIterableToArray$2(arr) || _nonIterableSpread$2(); }

function _nonIterableSpread$2() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$2(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$2(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen); }

function _iterableToArray$2(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles$2(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$2(arr); }

function _arrayLikeToArray$2(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$m(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$m(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$m(Object(source), true).forEach(function (key) { _defineProperty$t(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$m(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$t(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Supported input types (for built in input)

var TYPES$1 = ['text', 'email', 'tel', 'url', 'number']; // --- Utility methods ---
// Escape special chars in string and replace
// contiguous spaces with a whitespace match

var escapeRegExpChars = function escapeRegExpChars(str) {
  return escapeRegExp(str).replace(RX_SPACES, '\\s');
}; // Remove leading/trailing spaces from array of tags and remove duplicates


var cleanTags = function cleanTags(tags) {
  return concat(tags).map(function (tag) {
    return trim(toString$1(tag));
  }).filter(function (tag, index, arr) {
    return tag.length > 0 && arr.indexOf(tag) === index;
  });
}; // Processes an input/change event, normalizing string or event argument


var processEventValue = function processEventValue(evt) {
  return isString(evt) ? evt : isEvent(evt) ? evt.target.value || '' : '';
}; // Returns a fresh empty `tagsState` object


var cleanTagsState = function cleanTagsState() {
  return {
    all: [],
    valid: [],
    invalid: [],
    duplicate: []
  };
}; // @vue/component


var BFormTags = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_TAGS,
  mixins: [idMixin, normalizeSlotMixin],
  model: {
    // Even though this is the default that Vue assumes, we need
    // to add it for the docs to reflect that this is the model
    prop: 'value',
    event: 'input'
  },
  props: {
    inputId: {
      type: String // default: null

    },
    placeholder: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'placeholder');
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    name: {
      type: String // default: null

    },
    form: {
      type: String // default: null

    },
    autofocus: {
      type: Boolean,
      default: false
    },
    state: {
      // Tri-state: `true`, `false`, `null`
      type: Boolean,
      default: null
    },
    size: {
      type: String // default: null

    },
    inputType: {
      type: String,
      default: 'text',
      validator: function validator(type) {
        return arrayIncludes(TYPES$1, type);
      }
    },
    inputClass: {
      type: [String, Array, Object] // default: null

    },
    inputAttrs: {
      // Additional attributes to add to the input element
      type: Object,
      default: function _default() {
        return {};
      }
    },
    addButtonText: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'addButtonText');
      }
    },
    addButtonVariant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'addButtonVariant');
      }
    },
    tagVariant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'tagVariant');
      }
    },
    tagClass: {
      type: [String, Array, Object] // default: null

    },
    tagPills: {
      type: Boolean,
      default: false
    },
    tagRemoveLabel: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'tagRemoveLabel');
      }
    },
    tagRemovedLabel: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'tagRemovedLabel');
      }
    },
    tagValidator: {
      type: Function // default: null

    },
    duplicateTagText: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'duplicateTagText');
      }
    },
    invalidTagText: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'invalidTagText');
      }
    },
    limitTagsText: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_FORM_TAGS, 'limitTagsText');
      }
    },
    limit: {
      type: Number // default: null

    },
    separator: {
      // Character (or characters) that trigger adding tags
      type: [String, Array] // default: null

    },
    removeOnDelete: {
      // Enable deleting last tag in list when CODE_BACKSPACE is
      // pressed and input is empty
      type: Boolean,
      default: false
    },
    addOnChange: {
      // Enable change event triggering tag addition
      // Handy if using <select> as the input
      type: Boolean,
      default: false
    },
    noAddOnEnter: {
      // Disable ENTER key from triggering tag addition
      type: Boolean,
      default: false
    },
    noOuterFocus: {
      // Disable the focus ring on the root element
      type: Boolean,
      default: false
    },
    ignoreInputFocusSelector: {
      // Disable the input focus behavior when clicking
      // on element matching the selector (or selectors)
      type: [Array, String],
      default: function _default() {
        return ['.b-form-tag', 'button', 'input', 'select'];
      }
    },
    value: {
      // The v-model prop
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  data: function data() {
    return {
      hasFocus: false,
      newTag: '',
      tags: [],
      // Tags that were removed
      removedTags: [],
      // Populated when tags are parsed
      tagsState: cleanTagsState()
    };
  },
  computed: {
    computedInputId: function computedInputId() {
      return this.inputId || this.safeId('__input__');
    },
    computedInputType: function computedInputType() {
      // We only allow certain types
      return arrayIncludes(TYPES$1, this.inputType) ? this.inputType : 'text';
    },
    computedInputAttrs: function computedInputAttrs() {
      return _objectSpread$m(_objectSpread$m({}, this.inputAttrs), {}, {
        // Must have attributes
        id: this.computedInputId,
        value: this.newTag,
        disabled: this.disabled || null,
        form: this.form || null
      });
    },
    computedInputHandlers: function computedInputHandlers() {
      return {
        input: this.onInputInput,
        change: this.onInputChange,
        keydown: this.onInputKeydown
      };
    },
    computedSeparator: function computedSeparator() {
      // Merge the array into a string
      return concat(this.separator).filter(isString).filter(identity$1).join('');
    },
    computedSeparatorRegExp: function computedSeparatorRegExp() {
      // We use a computed prop here to precompile the RegExp
      // The RegExp is a character class RE in the form of `/[abc]+/`
      // where a, b, and c are the valid separator characters
      // -> `tags = str.split(/[abc]+/).filter(t => t)`
      var separator = this.computedSeparator;
      return separator ? new RegExp("[".concat(escapeRegExpChars(separator), "]+")) : null;
    },
    computedJoiner: function computedJoiner() {
      // When tag(s) are invalid or duplicate, we leave them
      // in the input so that the user can see them
      // If there are more than one tag in the input, we use the
      // first separator character as the separator in the input
      // We append a space if the first separator is not a space
      var joiner = this.computedSeparator.charAt(0);
      return joiner !== ' ' ? "".concat(joiner, " ") : joiner;
    },
    computeIgnoreInputFocusSelector: function computeIgnoreInputFocusSelector() {
      // Normalize to an single selector with selectors separated by `,`
      return concat(this.ignoreInputFocusSelector).filter(identity$1).join(',').trim();
    },
    disableAddButton: function disableAddButton() {
      var _this = this;

      // If 'Add' button should be disabled
      // If the input contains at least one tag that can
      // be added, then the 'Add' button should be enabled
      var newTag = trim(this.newTag);
      return newTag === '' || !this.splitTags(newTag).some(function (t) {
        return !arrayIncludes(_this.tags, t) && _this.validateTag(t);
      });
    },
    duplicateTags: function duplicateTags() {
      return this.tagsState.duplicate;
    },
    hasDuplicateTags: function hasDuplicateTags() {
      return this.duplicateTags.length > 0;
    },
    invalidTags: function invalidTags() {
      return this.tagsState.invalid;
    },
    hasInvalidTags: function hasInvalidTags() {
      return this.invalidTags.length > 0;
    },
    isLimitReached: function isLimitReached() {
      var limit = this.limit;
      return isNumber(limit) && limit >= 0 && this.tags.length >= limit;
    }
  },
  watch: {
    value: function value(newVal) {
      this.tags = cleanTags(newVal);
    },
    tags: function tags(newVal, oldVal) {
      // Update the `v-model` (if it differs from the value prop)
      if (!looseEqual(newVal, this.value)) {
        this.$emit('input', newVal);
      }

      if (!looseEqual(newVal, oldVal)) {
        newVal = concat(newVal).filter(identity$1);
        oldVal = concat(oldVal).filter(identity$1);
        this.removedTags = oldVal.filter(function (old) {
          return !arrayIncludes(newVal, old);
        });
      }
    },
    tagsState: function tagsState(newVal, oldVal) {
      // Emit a tag-state event when the `tagsState` object changes
      if (!looseEqual(newVal, oldVal)) {
        this.$emit('tag-state', newVal.valid, newVal.invalid, newVal.duplicate);
      }
    }
  },
  created: function created() {
    // We do this in created to make sure an input event emits
    // if the cleaned tags are not equal to the value prop
    this.tags = cleanTags(this.value);
  },
  mounted: function mounted() {
    this.handleAutofocus();
  },

  /* istanbul ignore next */
  activated: function activated()
  /* istanbul ignore next */
  {
    this.handleAutofocus();
  },
  methods: {
    addTag: function addTag(newTag) {
      newTag = isString(newTag) ? newTag : this.newTag;
      /* istanbul ignore next */

      if (this.disabled || trim(newTag) === '' || this.isLimitReached) {
        // Early exit
        return;
      }

      var parsed = this.parseTags(newTag); // Add any new tags to the `tags` array, or if the
      // array of `allTags` is empty, we clear the input

      if (parsed.valid.length > 0 || parsed.all.length === 0) {
        // Clear the user input element (and leave in any invalid/duplicate tag(s)

        /* istanbul ignore if: full testing to be added later */
        if (matches(this.getInput(), 'select')) {
          // The following is needed to properly
          // work with `<select>` elements
          this.newTag = '';
        } else {
          var invalidAndDuplicates = [].concat(_toConsumableArray$2(parsed.invalid), _toConsumableArray$2(parsed.duplicate));
          this.newTag = parsed.all.filter(function (tag) {
            return arrayIncludes(invalidAndDuplicates, tag);
          }).join(this.computedJoiner).concat(invalidAndDuplicates.length > 0 ? this.computedJoiner.charAt(0) : '');
        }
      }

      if (parsed.valid.length > 0) {
        // We add the new tags in one atomic operation
        // to trigger reactivity once (instead of once per tag)
        // We do this after we update the new tag input value
        // `concat()` can be faster than array spread, when both args are arrays
        this.tags = concat(this.tags, parsed.valid);
      }

      this.tagsState = parsed; // Attempt to re-focus the input (specifically for when using the Add
      // button, as the button disappears after successfully adding a tag

      this.focus();
    },
    removeTag: function removeTag(tag) {
      var _this2 = this;

      /* istanbul ignore next */
      if (this.disabled) {
        return;
      } // TODO:
      //   Add `onRemoveTag(tag)` user method, which if returns `false`
      //   will prevent the tag from being removed (i.e. confirmation)
      //   Or emit cancelable `BvEvent`


      this.tags = this.tags.filter(function (t) {
        return t !== tag;
      }); // Return focus to the input (if possible)

      this.$nextTick(function () {
        _this2.focus();
      });
    },
    // --- Input element event handlers ---
    onInputInput: function onInputInput(evt) {
      /* istanbul ignore next: hard to test composition events */
      if (this.disabled || isEvent(evt) && evt.target.composing) {
        // `evt.target.composing` is set by Vue (`v-model` directive)
        // https://github.com/vuejs/vue/blob/dev/src/platforms/web/runtime/directives/model.js
        return;
      }

      var newTag = processEventValue(evt);
      var separatorRe = this.computedSeparatorRegExp;

      if (this.newTag !== newTag) {
        this.newTag = newTag;
      } // We ignore leading whitespace for the following


      newTag = trimLeft(newTag);

      if (separatorRe && separatorRe.test(newTag.slice(-1))) {
        // A trailing separator character was entered, so add the tag(s)
        // Note: More than one tag on input event is possible via copy/paste
        this.addTag();
      } else {
        // Validate (parse tags) on input event
        this.tagsState = newTag === '' ? cleanTagsState() : this.parseTags(newTag);
      }
    },
    onInputChange: function onInputChange(evt) {
      // Change is triggered on `<input>` blur, or `<select>` selected
      // This event is opt-in
      if (!this.disabled && this.addOnChange) {
        var newTag = processEventValue(evt);
        /* istanbul ignore next */

        if (this.newTag !== newTag) {
          this.newTag = newTag;
        }

        this.addTag();
      }
    },
    onInputKeydown: function onInputKeydown(evt) {
      // Early exit

      /* istanbul ignore next */
      if (this.disabled || !isEvent(evt)) {
        return;
      }

      var keyCode = evt.keyCode;
      var value = evt.target.value || '';
      /* istanbul ignore else: testing to be added later */

      if (!this.noAddOnEnter && keyCode === CODE_ENTER) {
        // Attempt to add the tag when user presses enter
        stopEvent(evt, {
          propagation: false
        });
        this.addTag();
      } else if (this.removeOnDelete && (keyCode === CODE_BACKSPACE || keyCode === CODE_DELETE) && value === '') {
        // Remove the last tag if the user pressed backspace/delete and the input is empty
        stopEvent(evt, {
          propagation: false
        });
        this.tags = this.tags.slice(0, -1);
      }
    },
    // --- Wrapper event handlers ---
    onClick: function onClick(evt) {
      var _this3 = this;

      var ignoreFocusSelector = this.computeIgnoreInputFocusSelector;
      var target = evt.target;

      if (!this.disabled && !isActiveElement(target) && (!ignoreFocusSelector || !closest(ignoreFocusSelector, target, true))) {
        this.$nextTick(function () {
          _this3.focus();
        });
      }
    },
    onFocusin: function onFocusin() {
      this.hasFocus = true;
    },
    onFocusout: function onFocusout() {
      this.hasFocus = false;
    },
    handleAutofocus: function handleAutofocus() {
      var _this4 = this;

      this.$nextTick(function () {
        requestAF(function () {
          if (_this4.autofocus && !_this4.disabled) {
            _this4.focus();
          }
        });
      });
    },
    // --- Public methods ---
    focus: function focus() {
      if (!this.disabled) {
        attemptFocus(this.getInput());
      }
    },
    blur: function blur() {
      if (!this.disabled) {
        attemptBlur(this.getInput());
      }
    },
    // --- Private methods ---
    splitTags: function splitTags(newTag) {
      // Split the input into an array of raw tags
      newTag = toString$1(newTag);
      var separatorRe = this.computedSeparatorRegExp; // Split the tag(s) via the optional separator
      // Normally only a single tag is provided, but copy/paste
      // can enter multiple tags in a single operation

      return (separatorRe ? newTag.split(separatorRe) : [newTag]).map(trim).filter(identity$1);
    },
    parseTags: function parseTags(newTag) {
      var _this5 = this;

      // Takes `newTag` value and parses it into `validTags`,
      // `invalidTags`, and duplicate tags as an object
      // Split the input into raw tags
      var tags = this.splitTags(newTag); // Base results

      var parsed = {
        all: tags,
        valid: [],
        invalid: [],
        duplicate: []
      }; // Parse the unique tags

      tags.forEach(function (tag) {
        if (arrayIncludes(_this5.tags, tag) || arrayIncludes(parsed.valid, tag)) {
          // Unique duplicate tags
          if (!arrayIncludes(parsed.duplicate, tag)) {
            parsed.duplicate.push(tag);
          }
        } else if (_this5.validateTag(tag)) {
          // We only add unique/valid tags
          parsed.valid.push(tag);
        } else {
          // Unique invalid tags
          if (!arrayIncludes(parsed.invalid, tag)) {
            parsed.invalid.push(tag);
          }
        }
      });
      return parsed;
    },
    validateTag: function validateTag(tag) {
      // Call the user supplied tag validator
      var validator = this.tagValidator;
      return isFunction$1(validator) ? validator(tag) : true;
    },
    getInput: function getInput() {
      // Returns the input element reference (or null if not found)
      // We need to escape `computedInputId` since it can be user-provided
      return select("#".concat(cssEscape(this.computedInputId)), this.$el);
    },
    // Default User Interface render
    defaultRender: function defaultRender(_ref) {
      var tags = _ref.tags,
          inputAttrs = _ref.inputAttrs,
          inputType = _ref.inputType,
          inputHandlers = _ref.inputHandlers,
          removeTag = _ref.removeTag,
          addTag = _ref.addTag,
          isInvalid = _ref.isInvalid,
          isDuplicate = _ref.isDuplicate,
          isLimitReached = _ref.isLimitReached,
          disableAddButton = _ref.disableAddButton,
          disabled = _ref.disabled,
          placeholder = _ref.placeholder,
          inputClass = _ref.inputClass,
          tagRemoveLabel = _ref.tagRemoveLabel,
          tagVariant = _ref.tagVariant,
          tagPills = _ref.tagPills,
          tagClass = _ref.tagClass,
          addButtonText = _ref.addButtonText,
          addButtonVariant = _ref.addButtonVariant,
          invalidTagText = _ref.invalidTagText,
          duplicateTagText = _ref.duplicateTagText,
          limitTagsText = _ref.limitTagsText;
      var h = this.$createElement; // Make the list of tags

      var $tags = tags.map(function (tag) {
        tag = toString$1(tag);
        return h(BFormTag, {
          class: tagClass,
          props: {
            // `BFormTag` will auto generate an ID
            // so we do not need to set the ID prop
            tag: 'li',
            title: tag,
            disabled: disabled,
            variant: tagVariant,
            pill: tagPills,
            removeLabel: tagRemoveLabel
          },
          on: {
            remove: function remove() {
              return removeTag(tag);
            }
          },
          key: "tags_".concat(tag)
        }, tag);
      }); // Feedback IDs if needed

      var invalidFeedbackId = invalidTagText && isInvalid ? this.safeId('__invalid_feedback__') : null;
      var duplicateFeedbackId = duplicateTagText && isDuplicate ? this.safeId('__duplicate_feedback__') : null;
      var limitFeedbackId = limitTagsText && isLimitReached ? this.safeId('__limit_feedback__') : null; // Compute the `aria-describedby` attribute value

      var ariaDescribedby = [inputAttrs['aria-describedby'], invalidFeedbackId, duplicateFeedbackId, limitFeedbackId].filter(identity$1).join(' '); // Input

      var $input = h('input', {
        ref: 'input',
        // Directive needed to get `evt.target.composing` set (if needed)
        directives: [{
          name: 'model',
          value: inputAttrs.value
        }],
        staticClass: 'b-form-tags-input w-100 flex-grow-1 p-0 m-0 bg-transparent border-0',
        class: inputClass,
        style: {
          outline: 0,
          minWidth: '5rem'
        },
        attrs: _objectSpread$m(_objectSpread$m({}, inputAttrs), {}, {
          'aria-describedby': ariaDescribedby || null,
          type: inputType,
          placeholder: placeholder || null
        }),
        domProps: {
          value: inputAttrs.value
        },
        on: inputHandlers
      }); // Add button

      var $button = h(BButton, {
        ref: 'button',
        staticClass: 'b-form-tags-button py-0',
        class: {
          // Only show the button if the tag can be added
          // We use the `invisible` class instead of not rendering
          // the button, so that we maintain layout to prevent
          // the user input from jumping around
          invisible: disableAddButton
        },
        style: {
          fontSize: '90%'
        },
        props: {
          variant: addButtonVariant,
          disabled: disableAddButton || isLimitReached
        },
        on: {
          click: function click() {
            return addTag();
          }
        }
      }, [this.normalizeSlot('add-button-text') || addButtonText]); // ID of the tags + input `<ul>` list
      // Note we could concatenate `inputAttrs.id` with '__tag_list__'
      // but `inputId` may be `null` until after mount
      // `safeId()` returns `null`, if no user provided ID,
      // until after mount when a unique ID is generated

      var tagListId = this.safeId('__tag_list__');
      var $field = h('li', {
        staticClass: 'b-from-tags-field flex-grow-1',
        attrs: {
          role: 'none',
          'aria-live': 'off',
          'aria-controls': tagListId
        },
        key: 'tags_field'
      }, [h('div', {
        staticClass: 'd-flex',
        attrs: {
          role: 'group'
        }
      }, [$input, $button])]); // Wrap in an unordered list element (we use a list for accessibility)

      var $ul = h('ul', {
        staticClass: 'b-form-tags-list list-unstyled mb-0 d-flex flex-wrap align-items-center',
        attrs: {
          id: tagListId
        },
        key: 'tags_list'
      }, [$tags, $field]); // Assemble the feedback

      var $feedback = h();

      if (invalidTagText || duplicateTagText || limitTagsText) {
        // Add an aria live region for the invalid/duplicate tag
        // messages if the user has not disabled the messages
        var joiner = this.computedJoiner; // Invalid tag feedback if needed (error)

        var $invalid = h();

        if (invalidFeedbackId) {
          $invalid = h(BFormInvalidFeedback, {
            props: {
              id: invalidFeedbackId,
              forceShow: true
            },
            key: 'tags_invalid_feedback'
          }, [this.invalidTagText, ': ', this.invalidTags.join(joiner)]);
        } // Duplicate tag feedback if needed (warning, not error)


        var $duplicate = h();

        if (duplicateFeedbackId) {
          $duplicate = h(BFormText, {
            props: {
              id: duplicateFeedbackId
            },
            key: 'tags_duplicate_feedback'
          }, [this.duplicateTagText, ': ', this.duplicateTags.join(joiner)]);
        } // Limit tags feedback if needed (warning, not error)


        var $limit = h();

        if (limitFeedbackId) {
          $limit = h(BFormText, {
            props: {
              id: limitFeedbackId
            },
            key: 'tags_limit_feedback'
          }, [limitTagsText]);
        }

        $feedback = h('div', {
          attrs: {
            'aria-live': 'polite',
            'aria-atomic': 'true'
          },
          key: 'tags_feedback'
        }, [$invalid, $duplicate, $limit]);
      } // Return the content


      return [$ul, $feedback];
    }
  },
  render: function render(h) {
    var _this6 = this;

    // Scoped slot properties
    var scope = {
      // Array of tags (shallow copy to prevent mutations)
      tags: this.tags.slice(),
      // <input> v-bind:inputAttrs
      inputAttrs: this.computedInputAttrs,
      // We don't include this in the attrs, as users may want to override this
      inputType: this.computedInputType,
      // <input> v-on:inputHandlers
      inputHandlers: this.computedInputHandlers,
      // Methods
      removeTag: this.removeTag,
      addTag: this.addTag,
      // <input> :id="inputId"
      inputId: this.computedInputId,
      // Invalid/Duplicate state information
      isInvalid: this.hasInvalidTags,
      invalidTags: this.invalidTags.slice(),
      isDuplicate: this.hasDuplicateTags,
      duplicateTags: this.duplicateTags.slice(),
      isLimitReached: this.isLimitReached,
      // If the 'Add' button should be disabled
      disableAddButton: this.disableAddButton,
      // Pass-though values
      disabled: this.disabled,
      state: this.state,
      size: this.size,
      limit: this.limit,
      separator: this.separator,
      placeholder: this.placeholder,
      inputClass: this.inputClass,
      tagRemoveLabel: this.tagRemoveLabel,
      tagVariant: this.tagVariant,
      tagPills: this.tagPills,
      tagClass: this.tagClass,
      addButtonText: this.addButtonText,
      addButtonVariant: this.addButtonVariant,
      invalidTagText: this.invalidTagText,
      duplicateTagText: this.duplicateTagText,
      limitTagsText: this.limitTagsText
    }; // Generate the user interface

    var $content = this.normalizeSlot(SLOT_NAME_DEFAULT, scope) || this.defaultRender(scope); // Generate the `aria-live` region for the current value(s)

    var $output = h('output', {
      staticClass: 'sr-only',
      attrs: {
        id: this.safeId('__selected_tags__'),
        role: 'status',
        for: this.computedInputId,
        'aria-live': this.hasFocus ? 'polite' : 'off',
        'aria-atomic': 'true',
        'aria-relevant': 'additions text'
      }
    }, this.tags.join(', ')); // Removed tag live region

    var $removed = h('div', {
      staticClass: 'sr-only',
      attrs: {
        id: this.safeId('__removed_tags__'),
        role: 'status',
        'aria-live': this.hasFocus ? 'assertive' : 'off',
        'aria-atomic': 'true'
      }
    }, this.removedTags.length > 0 ? "(".concat(this.tagRemovedLabel, ") ").concat(this.removedTags.join(', ')) : ''); // Add hidden inputs for form submission

    var $hidden = h();

    if (this.name && !this.disabled) {
      // We add hidden inputs for each tag if a name is provided
      // for native submission of forms
      $hidden = this.tags.map(function (tag) {
        return h('input', {
          attrs: {
            type: 'hidden',
            value: tag,
            name: _this6.name,
            form: _this6.form || null
          },
          key: "tag_input_".concat(tag)
        });
      });
    } // Return the rendered output


    return h('div', {
      staticClass: 'b-form-tags form-control h-auto',
      class: _defineProperty$t({
        focus: this.hasFocus && !this.noOuterFocus && !this.disabled,
        disabled: this.disabled,
        'is-valid': this.state === true,
        'is-invalid': this.state === false
      }, "form-control-".concat(this.size), this.size),
      attrs: {
        id: this.safeId(),
        role: 'group',
        tabindex: this.disabled || this.noOuterFocus ? null : '-1',
        'aria-describedby': this.safeId('__selected_tags__')
      },
      on: {
        click: this.onClick,
        focusin: this.onFocusin,
        focusout: this.onFocusout
      }
    }, [$output, $removed, $content, $hidden]);
  }
});

function ownKeys$n(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$n(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$n(Object(source), true).forEach(function (key) { _defineProperty$u(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$n(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$u(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BFormTextarea = /*#__PURE__*/Vue.extend({
  name: NAME_FORM_TEXTAREA,
  directives: {
    'b-visible': VBVisible
  },
  // Mixin order is important!
  mixins: [listenersMixin, idMixin, listenOnRootMixin, formMixin, formSizeMixin, formStateMixin, formTextMixin, formSelectionMixin, formValidityMixin],
  props: {
    rows: {
      type: [Number, String],
      default: 2
    },
    maxRows: {
      type: [Number, String] // default: null

    },
    wrap: {
      // 'soft', 'hard' or 'off'. Browser default is 'soft'
      type: String,
      default: 'soft'
    },
    noResize: {
      // Disable the resize handle of textarea
      type: Boolean,
      default: false
    },
    noAutoShrink: {
      // When in auto resize mode, disable shrinking to content height
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      heightInPx: null
    };
  },
  computed: {
    computedStyle: function computedStyle() {
      var styles = {
        // Setting `noResize` to true will disable the ability for the user to
        // manually resize the textarea. We also disable when in auto height mode
        resize: !this.computedRows || this.noResize ? 'none' : null
      };

      if (!this.computedRows) {
        // Conditionally set the computed CSS height when auto rows/height is enabled
        // We avoid setting the style to `null`, which can override user manual resize handle
        styles.height = this.heightInPx; // We always add a vertical scrollbar to the textarea when auto-height is
        // enabled so that the computed height calculation returns a stable value

        styles.overflowY = 'scroll';
      }

      return styles;
    },
    computedMinRows: function computedMinRows() {
      // Ensure rows is at least 2 and positive (2 is the native textarea value)
      // A value of 1 can cause issues in some browsers, and most browsers
      // only support 2 as the smallest value
      return mathMax(toInteger(this.rows, 2), 2);
    },
    computedMaxRows: function computedMaxRows() {
      return mathMax(this.computedMinRows, toInteger(this.maxRows, 0));
    },
    computedRows: function computedRows() {
      // This is used to set the attribute 'rows' on the textarea
      // If auto-height is enabled, then we return `null` as we use CSS to control height
      return this.computedMinRows === this.computedMaxRows ? this.computedMinRows : null;
    },
    computedAttrs: function computedAttrs() {
      var disabled = this.disabled,
          required = this.required;
      return {
        id: this.safeId(),
        name: this.name || null,
        form: this.form || null,
        disabled: disabled,
        placeholder: this.placeholder || null,
        required: required,
        autocomplete: this.autocomplete || null,
        readonly: this.readonly || this.plaintext,
        rows: this.computedRows,
        wrap: this.wrap || null,
        'aria-required': this.required ? 'true' : null,
        'aria-invalid': this.computedAriaInvalid
      };
    },
    computedListeners: function computedListeners() {
      return _objectSpread$n(_objectSpread$n({}, this.bvListeners), {}, {
        input: this.onInput,
        change: this.onChange,
        blur: this.onBlur
      });
    }
  },
  watch: {
    localValue: function localValue() {
      this.setHeight();
    }
  },
  mounted: function mounted() {
    this.setHeight();
  },
  methods: {
    // Called by intersection observer directive
    visibleCallback: function visibleCallback(visible)
    /* istanbul ignore next */
    {
      if (visible) {
        // We use a `$nextTick()` here just to make sure any
        // transitions or portalling have completed
        this.$nextTick(this.setHeight);
      }
    },
    setHeight: function setHeight() {
      var _this = this;

      this.$nextTick(function () {
        requestAF(function () {
          _this.heightInPx = _this.computeHeight();
        });
      });
    },
    computeHeight: function computeHeight()
    /* istanbul ignore next: can't test getComputedStyle in JSDOM */
    {
      if (this.$isServer || !isNull(this.computedRows)) {
        return null;
      }

      var el = this.$el; // Element must be visible (not hidden) and in document
      // Must be checked after above checks

      if (!isVisible(el)) {
        return null;
      } // Get current computed styles


      var computedStyle = getCS(el); // Height of one line of text in px

      var lineHeight = toFloat(computedStyle.lineHeight, 1); // Calculate height of border and padding

      var border = toFloat(computedStyle.borderTopWidth, 0) + toFloat(computedStyle.borderBottomWidth, 0);
      var padding = toFloat(computedStyle.paddingTop, 0) + toFloat(computedStyle.paddingBottom, 0); // Calculate offset

      var offset = border + padding; // Minimum height for min rows (which must be 2 rows or greater for cross-browser support)

      var minHeight = lineHeight * this.computedMinRows + offset; // Get the current style height (with `px` units)

      var oldHeight = getStyle(el, 'height') || computedStyle.height; // Probe scrollHeight by temporarily changing the height to `auto`

      setStyle(el, 'height', 'auto');
      var scrollHeight = el.scrollHeight; // Place the original old height back on the element, just in case `computedProp`
      // returns the same value as before

      setStyle(el, 'height', oldHeight); // Calculate content height in 'rows' (scrollHeight includes padding but not border)

      var contentRows = mathMax((scrollHeight - padding) / lineHeight, 2); // Calculate number of rows to display (limited within min/max rows)

      var rows = mathMin(mathMax(contentRows, this.computedMinRows), this.computedMaxRows); // Calculate the required height of the textarea including border and padding (in pixels)

      var height = mathMax(mathCeil(rows * lineHeight + offset), minHeight); // Computed height remains the larger of `oldHeight` and new `height`,
      // when height is in `sticky` mode (prop `no-auto-shrink` is true)

      if (this.noAutoShrink && toFloat(oldHeight, 0) > height) {
        return oldHeight;
      } // Return the new computed CSS height in px units


      return "".concat(height, "px");
    }
  },
  render: function render(h) {
    return h('textarea', {
      ref: 'input',
      class: this.computedClass,
      style: this.computedStyle,
      directives: [{
        name: 'b-visible',
        value: this.visibleCallback,
        // If textarea is within 640px of viewport, consider it visible
        modifiers: {
          '640': true
        }
      }],
      attrs: this.computedAttrs,
      domProps: {
        value: this.localValue
      },
      on: this.computedListeners
    });
  }
});

var props$g = {
  tag: {
    type: String,
    default: 'div'
  }
}; // @vue/component

var BInputGroupText = /*#__PURE__*/Vue.extend({
  name: NAME_INPUT_GROUP_TEXT,
  functional: true,
  props: props$g,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    return h(props.tag, a(data, {
      staticClass: 'input-group-text'
    }), children);
  }
});

function ownKeys$o(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$o(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$o(Object(source), true).forEach(function (key) { _defineProperty$v(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$o(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$v(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var commonProps$1 = {
  id: {
    type: String,
    default: null
  },
  tag: {
    type: String,
    default: 'div'
  },
  isText: {
    type: Boolean,
    default: false
  }
}; // @vue/component

var BInputGroupAddon = /*#__PURE__*/Vue.extend({
  name: NAME_INPUT_GROUP_ADDON,
  functional: true,
  props: _objectSpread$o(_objectSpread$o({}, commonProps$1), {}, {
    append: {
      type: Boolean,
      default: false
    }
  }),
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    return h(props.tag, a(data, {
      class: {
        'input-group-append': props.append,
        'input-group-prepend': !props.append
      },
      attrs: {
        id: props.id
      }
    }), props.isText ? [h(BInputGroupText, children)] : children);
  }
});

function ownKeys$p(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$p(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$p(Object(source), true).forEach(function (key) { _defineProperty$w(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$p(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$w(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BInputGroupAppend = /*#__PURE__*/Vue.extend({
  name: NAME_INPUT_GROUP_APPEND,
  functional: true,
  props: commonProps$1,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    // Pass all our data down to child, and set `append` to `true`
    return h(BInputGroupAddon, a(data, {
      props: _objectSpread$p(_objectSpread$p({}, props), {}, {
        append: true
      })
    }), children);
  }
});

function ownKeys$q(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$q(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$q(Object(source), true).forEach(function (key) { _defineProperty$x(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$q(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$x(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BInputGroupPrepend = /*#__PURE__*/Vue.extend({
  name: NAME_INPUT_GROUP_PREPEND,
  functional: true,
  props: commonProps$1,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    // pass all our props/attrs down to child, and set`append` to false
    return h(BInputGroupAddon, a(data, {
      props: _objectSpread$q(_objectSpread$q({}, props), {}, {
        append: false
      })
    }), children);
  }
});

function _defineProperty$y(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var props$h = {
  id: {
    type: String
  },
  size: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_INPUT_GROUP, 'size');
    }
  },
  prepend: {
    type: String
  },
  prependHtml: {
    type: String
  },
  append: {
    type: String
  },
  appendHtml: {
    type: String
  },
  tag: {
    type: String,
    default: 'div'
  }
}; // --- Main component ---
// @vue/component

var BInputGroup = /*#__PURE__*/Vue.extend({
  name: NAME_INPUT_GROUP,
  functional: true,
  props: props$h,
  render: function render(h, _ref) {
    var props = _ref.props,
        data = _ref.data,
        slots = _ref.slots,
        scopedSlots = _ref.scopedSlots;
    var prepend = props.prepend,
        prependHtml = props.prependHtml,
        append = props.append,
        appendHtml = props.appendHtml,
        size = props.size;
    var $scopedSlots = scopedSlots || {};
    var $slots = slots();
    var slotScope = {};
    var $prepend = h();
    var hasPrependSlot = hasNormalizedSlot(SLOT_NAME_PREPEND, $scopedSlots, $slots);

    if (hasPrependSlot || prepend || prependHtml) {
      $prepend = h(BInputGroupPrepend, [hasPrependSlot ? normalizeSlot(SLOT_NAME_PREPEND, slotScope, $scopedSlots, $slots) : h(BInputGroupText, {
        domProps: htmlOrText(prependHtml, prepend)
      })]);
    }

    var $append = h();
    var hasAppendSlot = hasNormalizedSlot(SLOT_NAME_APPEND, $scopedSlots, $slots);

    if (hasAppendSlot || append || appendHtml) {
      $append = h(BInputGroupAppend, [hasAppendSlot ? normalizeSlot(SLOT_NAME_APPEND, slotScope, $scopedSlots, $slots) : h(BInputGroupText, {
        domProps: htmlOrText(appendHtml, append)
      })]);
    }

    return h(props.tag, a(data, {
      staticClass: 'input-group',
      class: _defineProperty$y({}, "input-group-".concat(size), size),
      attrs: {
        id: props.id || null,
        role: 'group'
      }
    }), [$prepend, normalizeSlot(SLOT_NAME_DEFAULT, slotScope, $scopedSlots, $slots), $append]);
  }
});

//
// Single root node portaling of content, which retains parent/child hierarchy
// Unlike Portal-Vue where portaled content is no longer a descendent of its
// intended parent components
//
// Private components for use by Tooltips, Popovers and Modals
//
// Based on vue-simple-portal
// https://github.com/LinusBorg/vue-simple-portal
// Transporter target used by BTransporterSingle
// Supports only a single root element
// @vue/component

var BTransporterTargetSingle = /*#__PURE__*/Vue.extend({
  // As an abstract component, it doesn't appear in the $parent chain of
  // components, which means the next parent of any component rendered inside
  // of this one will be the parent from which is was portal'd
  abstract: true,
  name: NAME_TRANSPORTER_TARGET_SINGLE,
  props: {
    nodes: {
      // Even though we only support a single root element,
      // VNodes are always passed as an array
      type: [Array, Function] // default: undefined

    }
  },
  data: function data(vm) {
    return {
      updatedNodes: vm.nodes
    };
  },
  destroyed: function destroyed() {
    removeNode(this.$el);
  },
  render: function render(h) {
    var nodes = isFunction$1(this.updatedNodes) ? this.updatedNodes({}) : this.updatedNodes;
    nodes = concat(nodes).filter(Boolean);
    /* istanbul ignore else */

    if (nodes && nodes.length > 0 && !nodes[0].text) {
      return nodes[0];
    } else {
      /* istanbul ignore next */
      return h();
    }
  }
}); // This component has no root element, so only a single VNode is allowed
// @vue/component

var BTransporterSingle = /*#__PURE__*/Vue.extend({
  name: NAME_TRANSPORTER_SINGLE,
  mixins: [normalizeSlotMixin],
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    container: {
      // String: CSS selector,
      // HTMLElement: Element reference
      // Mainly needed for tooltips/popovers inside modals
      type: [String, HTMLElement],
      default: 'body'
    },
    tag: {
      // This should be set to match the root element type
      type: String,
      default: 'div'
    }
  },
  watch: {
    disabled: {
      immediate: true,
      handler: function handler(disabled) {
        disabled ? this.unmountTarget() : this.$nextTick(this.mountTarget);
      }
    }
  },
  created: function created() {
    // Create private non-reactive props
    this.$_defaultFn = null;
    this.$_target = null;
  },
  beforeMount: function beforeMount() {
    this.mountTarget();
  },
  updated: function updated() {
    // We need to make sure that all children have completed updating
    // before rendering in the target
    // `vue-simple-portal` has the this in a `$nextTick()`,
    // while `portal-vue` doesn't
    // Just trying to see if the `$nextTick()` delay is required or not
    // Since all slots in Vue 2.6.x are always functions
    this.updateTarget();
  },
  beforeDestroy: function beforeDestroy() {
    this.unmountTarget();
    this.$_defaultFn = null;
  },
  methods: {
    // Get the element which the target should be appended to
    getContainer: function getContainer() {
      /* istanbul ignore else */
      if (isBrowser) {
        var container = this.container;
        return isString(container) ? select(container) : container;
      } else {
        return null;
      }
    },
    // Mount the target
    mountTarget: function mountTarget() {
      if (!this.$_target) {
        var container = this.getContainer();

        if (container) {
          var el = document.createElement('div');
          container.appendChild(el);
          this.$_target = new BTransporterTargetSingle({
            el: el,
            parent: this,
            propsData: {
              // Initial nodes to be rendered
              nodes: concat(this.normalizeSlot())
            }
          });
        }
      }
    },
    // Update the content of the target
    updateTarget: function updateTarget() {
      if (isBrowser && this.$_target) {
        var defaultFn = this.$scopedSlots.default;

        if (!this.disabled) {
          /* istanbul ignore else: only applicable in Vue 2.5.x */
          if (defaultFn && this.$_defaultFn !== defaultFn) {
            // We only update the target component if the scoped slot
            // function is a fresh one. The new slot syntax (since Vue 2.6)
            // can cache unchanged slot functions and we want to respect that here
            this.$_target.updatedNodes = defaultFn;
          } else if (!defaultFn) {
            // We also need to be back compatible with non-scoped default slot (i.e. 2.5.x)
            this.$_target.updatedNodes = this.$slots.default;
          }
        } // Update the scoped slot function cache


        this.$_defaultFn = defaultFn;
      }
    },
    // Unmount the target
    unmountTarget: function unmountTarget() {
      this.$_target && this.$_target.$destroy();
      this.$_target = null;
    }
  },
  render: function render(h) {
    if (this.disabled) {
      var nodes = concat(this.normalizeSlot()).filter(identity$1);

      if (nodes.length > 0 && !nodes[0].text) {
        return nodes[0];
      }
    }

    return h();
  }
});

var PROP = '$_bv_documentHandlers_'; // @vue/component

var listenOnDocumentMixin = {
  created: function created() {
    var _this = this;

    /* istanbul ignore next */
    if (!isBrowser) {
      return;
    } // Declare non-reactive property
    // Object of arrays, keyed by event name,
    // where value is an array of handlers
    // Prop will be defined on client only


    this[PROP] = {}; // Set up our beforeDestroy handler (client only)

    this.$once('hook:beforeDestroy', function () {
      var items = _this[PROP] || {}; // Immediately delete this[PROP] to prevent the
      // listenOn/Off methods from running (which may occur
      // due to requestAnimationFrame/transition delays)

      delete _this[PROP]; // Remove all registered event handlers

      keys$1(items).forEach(function (evtName) {
        var handlers = items[evtName] || [];
        handlers.forEach(function (handler) {
          return eventOff(document, evtName, handler, EVENT_OPTIONS_NO_CAPTURE);
        });
      });
    });
  },
  methods: {
    listenDocument: function listenDocument(on, evtName, handler) {
      on ? this.listenOnDocument(evtName, handler) : this.listenOffDocument(evtName, handler);
    },
    listenOnDocument: function listenOnDocument(evtName, handler) {
      if (this[PROP] && isString(evtName) && isFunction$1(handler)) {
        this[PROP][evtName] = this[PROP][evtName] || [];

        if (!arrayIncludes(this[PROP][evtName], handler)) {
          this[PROP][evtName].push(handler);
          eventOn(document, evtName, handler, EVENT_OPTIONS_NO_CAPTURE);
        }
      }
    },
    listenOffDocument: function listenOffDocument(evtName, handler) {
      if (this[PROP] && isString(evtName) && isFunction$1(handler)) {
        eventOff(document, evtName, handler, EVENT_OPTIONS_NO_CAPTURE);
        this[PROP][evtName] = (this[PROP][evtName] || []).filter(function (h) {
          return h !== handler;
        });
      }
    }
  }
};

var PROP$1 = '$_bv_windowHandlers_'; // @vue/component

var listenOnWindowMixin = {
  beforeCreate: function beforeCreate() {
    // Declare non-reactive property
    // Object of arrays, keyed by event name,
    // where value is an array of handlers
    this[PROP$1] = {};
  },
  beforeDestroy: function beforeDestroy() {
    if (isBrowser) {
      var items = this[PROP$1]; // Immediately delete this[PROP] to prevent the
      // listenOn/Off methods from running (which may occur
      // due to requestAnimationFrame delays)

      delete this[PROP$1]; // Remove all registered event handlers

      keys$1(items).forEach(function (evtName) {
        var handlers = items[evtName] || [];
        handlers.forEach(function (handler) {
          return eventOff(window, evtName, handler, EVENT_OPTIONS_NO_CAPTURE);
        });
      });
    }
  },
  methods: {
    listenWindow: function listenWindow(on, evtName, handler) {
      on ? this.listenOnWindow(evtName, handler) : this.listenOffWindow(evtName, handler);
    },
    listenOnWindow: function listenOnWindow(evtName, handler) {
      if (isBrowser && this[PROP$1] && isString(evtName) && isFunction$1(handler)) {
        this[PROP$1][evtName] = this[PROP$1][evtName] || [];

        if (!arrayIncludes(this[PROP$1][evtName], handler)) {
          this[PROP$1][evtName].push(handler);
          eventOn(window, evtName, handler, EVENT_OPTIONS_NO_CAPTURE);
        }
      }
    },
    listenOffWindow: function listenOffWindow(evtName, handler) {
      if (isBrowser && this[PROP$1] && isString(evtName) && isFunction$1(handler)) {
        eventOff(window, evtName, handler, EVENT_OPTIONS_NO_CAPTURE);
        this[PROP$1][evtName] = (this[PROP$1][evtName] || []).filter(function (h) {
          return h !== handler;
        });
      }
    }
  }
};

// This method returns a component's scoped style attribute name: `data-v-xxxxxxx`
// The `_scopeId` options property is added by vue-loader when using scoped styles
// and will be `undefined` if no scoped styles are in use
var getScopeId = function getScopeId(vm) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return vm ? vm.$options._scopeId || defaultValue : defaultValue;
};

function _defineProperty$z(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var scopedStyleAttrsMixin = {
  computed: {
    scopedStyleAttrs: function scopedStyleAttrs() {
      var scopeId = getScopeId(this.$parent);
      return scopeId ? _defineProperty$z({}, scopeId, '') : {};
    }
  }
};

/**
 * Private ModalManager helper
 * Handles controlling modal stacking zIndexes and body adjustments/classes
 */
// Default modal backdrop z-index

var DEFAULT_ZINDEX = 1040; // Selectors for padding/margin adjustments

var Selector = {
  FIXED_CONTENT: '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top',
  STICKY_CONTENT: '.sticky-top',
  NAVBAR_TOGGLER: '.navbar-toggler'
}; // @vue/component

var ModalManager = /*#__PURE__*/Vue.extend({
  data: function data() {
    return {
      modals: [],
      baseZIndex: null,
      scrollbarWidth: null,
      isBodyOverflowing: false
    };
  },
  computed: {
    modalCount: function modalCount() {
      return this.modals.length;
    },
    modalsAreOpen: function modalsAreOpen() {
      return this.modalCount > 0;
    }
  },
  watch: {
    modalCount: function modalCount(newCount, oldCount) {
      if (isBrowser) {
        this.getScrollbarWidth();

        if (newCount > 0 && oldCount === 0) {
          // Transitioning to modal(s) open
          this.checkScrollbar();
          this.setScrollbar();
          addClass(document.body, 'modal-open');
        } else if (newCount === 0 && oldCount > 0) {
          // Transitioning to modal(s) closed
          this.resetScrollbar();
          removeClass(document.body, 'modal-open');
        }

        setAttr(document.body, 'data-modal-open-count', String(newCount));
      }
    },
    modals: function modals(newVal) {
      var _this = this;

      this.checkScrollbar();
      requestAF(function () {
        _this.updateModals(newVal || []);
      });
    }
  },
  methods: {
    // Public methods
    registerModal: function registerModal(modal) {
      var _this2 = this;

      // Register the modal if not already registered
      if (modal && this.modals.indexOf(modal) === -1) {
        // Add modal to modals array
        this.modals.push(modal);
        modal.$once('hook:beforeDestroy', function () {
          _this2.unregisterModal(modal);
        });
      }
    },
    unregisterModal: function unregisterModal(modal) {
      var index = this.modals.indexOf(modal);

      if (index > -1) {
        // Remove modal from modals array
        this.modals.splice(index, 1); // Reset the modal's data

        if (!(modal._isBeingDestroyed || modal._isDestroyed)) {
          this.resetModal(modal);
        }
      }
    },
    getBaseZIndex: function getBaseZIndex() {
      if (isNull(this.baseZIndex) && isBrowser) {
        // Create a temporary `div.modal-backdrop` to get computed z-index
        var div = document.createElement('div');
        addClass(div, 'modal-backdrop');
        addClass(div, 'd-none');
        setStyle(div, 'display', 'none');
        document.body.appendChild(div);
        this.baseZIndex = toInteger(getCS(div).zIndex, DEFAULT_ZINDEX);
        document.body.removeChild(div);
      }

      return this.baseZIndex || DEFAULT_ZINDEX;
    },
    getScrollbarWidth: function getScrollbarWidth() {
      if (isNull(this.scrollbarWidth) && isBrowser) {
        // Create a temporary `div.measure-scrollbar` to get computed z-index
        var div = document.createElement('div');
        addClass(div, 'modal-scrollbar-measure');
        document.body.appendChild(div);
        this.scrollbarWidth = getBCR(div).width - div.clientWidth;
        document.body.removeChild(div);
      }

      return this.scrollbarWidth || 0;
    },
    // Private methods
    updateModals: function updateModals(modals) {
      var _this3 = this;

      var baseZIndex = this.getBaseZIndex();
      var scrollbarWidth = this.getScrollbarWidth();
      modals.forEach(function (modal, index) {
        // We update data values on each modal
        modal.zIndex = baseZIndex + index;
        modal.scrollbarWidth = scrollbarWidth;
        modal.isTop = index === _this3.modals.length - 1;
        modal.isBodyOverflowing = _this3.isBodyOverflowing;
      });
    },
    resetModal: function resetModal(modal) {
      if (modal) {
        modal.zIndex = this.getBaseZIndex();
        modal.isTop = true;
        modal.isBodyOverflowing = false;
      }
    },
    checkScrollbar: function checkScrollbar() {
      // Determine if the body element is overflowing
      var _getBCR = getBCR(document.body),
          left = _getBCR.left,
          right = _getBCR.right;

      this.isBodyOverflowing = left + right < window.innerWidth;
    },
    setScrollbar: function setScrollbar() {
      var body = document.body; // Storage place to cache changes to margins and padding
      // Note: This assumes the following element types are not added to the
      // document after the modal has opened.

      body._paddingChangedForModal = body._paddingChangedForModal || [];
      body._marginChangedForModal = body._marginChangedForModal || [];

      if (this.isBodyOverflowing) {
        var scrollbarWidth = this.scrollbarWidth; // Adjust fixed content padding

        /* istanbul ignore next: difficult to test in JSDOM */

        selectAll(Selector.FIXED_CONTENT).forEach(function (el) {
          var actualPadding = getStyle(el, 'paddingRight') || '';
          setAttr(el, 'data-padding-right', actualPadding);
          setStyle(el, 'paddingRight', "".concat(toFloat(getCS(el).paddingRight, 0) + scrollbarWidth, "px"));

          body._paddingChangedForModal.push(el);
        }); // Adjust sticky content margin

        /* istanbul ignore next: difficult to test in JSDOM */

        selectAll(Selector.STICKY_CONTENT).forEach(function (el)
        /* istanbul ignore next */
        {
          var actualMargin = getStyle(el, 'marginRight') || '';
          setAttr(el, 'data-margin-right', actualMargin);
          setStyle(el, 'marginRight', "".concat(toFloat(getCS(el).marginRight, 0) - scrollbarWidth, "px"));

          body._marginChangedForModal.push(el);
        }); // Adjust <b-navbar-toggler> margin

        /* istanbul ignore next: difficult to test in JSDOM */

        selectAll(Selector.NAVBAR_TOGGLER).forEach(function (el)
        /* istanbul ignore next */
        {
          var actualMargin = getStyle(el, 'marginRight') || '';
          setAttr(el, 'data-margin-right', actualMargin);
          setStyle(el, 'marginRight', "".concat(toFloat(getCS(el).marginRight, 0) + scrollbarWidth, "px"));

          body._marginChangedForModal.push(el);
        }); // Adjust body padding

        var actualPadding = getStyle(body, 'paddingRight') || '';
        setAttr(body, 'data-padding-right', actualPadding);
        setStyle(body, 'paddingRight', "".concat(toFloat(getCS(body).paddingRight, 0) + scrollbarWidth, "px"));
      }
    },
    resetScrollbar: function resetScrollbar() {
      var body = document.body;

      if (body._paddingChangedForModal) {
        // Restore fixed content padding
        body._paddingChangedForModal.forEach(function (el) {
          /* istanbul ignore next: difficult to test in JSDOM */
          if (hasAttr(el, 'data-padding-right')) {
            setStyle(el, 'paddingRight', getAttr(el, 'data-padding-right') || '');
            removeAttr(el, 'data-padding-right');
          }
        });
      }

      if (body._marginChangedForModal) {
        // Restore sticky content and navbar-toggler margin
        body._marginChangedForModal.forEach(function (el) {
          /* istanbul ignore next: difficult to test in JSDOM */
          if (hasAttr(el, 'data-margin-right')) {
            setStyle(el, 'marginRight', getAttr(el, 'data-margin-right') || '');
            removeAttr(el, 'data-margin-right');
          }
        });
      }

      body._paddingChangedForModal = null;
      body._marginChangedForModal = null; // Restore body padding

      if (hasAttr(body, 'data-padding-right')) {
        setStyle(body, 'paddingRight', getAttr(body, 'data-padding-right') || '');
        removeAttr(body, 'data-padding-right');
      }
    }
  }
}); // Create and export our modal manager instance

var modalManager = new ModalManager();

function _typeof$2(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$2 = function _typeof(obj) { return typeof obj; }; } else { _typeof$2 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$2(obj); }

function ownKeys$r(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$r(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$r(Object(source), true).forEach(function (key) { _defineProperty$A(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$r(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$A(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$2(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$2(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$2(Constructor.prototype, protoProps); if (staticProps) _defineProperties$2(Constructor, staticProps); return Constructor; }

function _get(target, property, receiver) { if (typeof Reflect !== "undefined" && Reflect.get) { _get = Reflect.get; } else { _get = function _get(target, property, receiver) { var base = _superPropBase(target, property); if (!base) return; var desc = Object.getOwnPropertyDescriptor(base, property); if (desc.get) { return desc.get.call(receiver); } return desc.value; }; } return _get(target, property, receiver || target); }

function _superPropBase(object, property) { while (!Object.prototype.hasOwnProperty.call(object, property)) { object = _getPrototypeOf$1(object); if (object === null) break; } return object; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$1(subClass, superClass); }

function _setPrototypeOf$1(o, p) { _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$1(o, p); }

function _createSuper$1(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$1(); return function _createSuperInternal() { var Super = _getPrototypeOf$1(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$1(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$1(this, result); }; }

function _possibleConstructorReturn$1(self, call) { if (call && (_typeof$2(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$1(self); }

function _assertThisInitialized$1(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$1() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$1(o) { _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$1(o); }

var BvModalEvent = /*#__PURE__*/function (_BvEvent) {
  _inherits$1(BvModalEvent, _BvEvent);

  var _super = _createSuper$1(BvModalEvent);

  function BvModalEvent(type) {
    var _this;

    var eventInit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck$3(this, BvModalEvent);

    _this = _super.call(this, type, eventInit); // Freeze our new props as readonly, but leave them enumerable

    defineProperties(_assertThisInitialized$1(_this), {
      trigger: readonlyDescriptor()
    });
    return _this;
  }

  _createClass$2(BvModalEvent, null, [{
    key: "Defaults",
    get: function get() {
      return _objectSpread$r(_objectSpread$r({}, _get(_getPrototypeOf$1(BvModalEvent), "Defaults", this)), {}, {
        trigger: null
      });
    }
  }]);

  return BvModalEvent;
}(BvEvent); // Named exports

function ownKeys$s(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$s(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$s(Object(source), true).forEach(function (key) { _defineProperty$B(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$s(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$B(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// ObserveDom config to detect changes in modal content
// so that we can adjust the modal padding if needed

var OBSERVER_CONFIG = {
  subtree: true,
  childList: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['style', 'class']
}; // --- Props ---

var props$i = {
  size: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'size');
    }
  },
  centered: {
    type: Boolean,
    default: false
  },
  scrollable: {
    type: Boolean,
    default: false
  },
  buttonSize: {
    type: String // default: ''

  },
  noStacking: {
    type: Boolean,
    default: false
  },
  noFade: {
    type: Boolean,
    default: false
  },
  noCloseOnBackdrop: {
    type: Boolean,
    default: false
  },
  noCloseOnEsc: {
    type: Boolean,
    default: false
  },
  noEnforceFocus: {
    type: Boolean,
    default: false
  },
  ignoreEnforceFocusSelector: {
    type: [Array, String],
    default: ''
  },
  title: {
    type: String,
    default: ''
  },
  titleHtml: {
    type: String
  },
  titleTag: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'titleTag');
    }
  },
  titleClass: {
    type: [String, Array, Object] // default: null

  },
  titleSrOnly: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String // default: null

  },
  headerBgVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'headerBgVariant');
    }
  },
  headerBorderVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'headerBorderVariant');
    }
  },
  headerTextVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'headerTextVariant');
    }
  },
  headerCloseVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'headerCloseVariant');
    }
  },
  headerClass: {
    type: [String, Array, Object] // default: null

  },
  bodyBgVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'bodyBgVariant');
    }
  },
  bodyTextVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'bodyTextVariant');
    }
  },
  modalClass: {
    type: [String, Array, Object] // default: null

  },
  dialogClass: {
    type: [String, Array, Object] // default: null

  },
  contentClass: {
    type: [String, Array, Object] // default: null

  },
  bodyClass: {
    type: [String, Array, Object] // default: null

  },
  footerBgVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'footerBgVariant');
    }
  },
  footerBorderVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'footerBorderVariant');
    }
  },
  footerTextVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'footerTextVariant');
    }
  },
  footerClass: {
    type: [String, Array, Object] // default: null

  },
  // TODO: Rename to `noHeader` and deprecate `hideHeader`
  hideHeader: {
    type: Boolean,
    default: false
  },
  // TODO: Rename to `noFooter` and deprecate `hideFooter`
  hideFooter: {
    type: Boolean,
    default: false
  },
  // TODO: Rename to `noHeaderClose` and deprecate `hideHeaderClose`
  hideHeaderClose: {
    type: Boolean,
    default: false
  },
  // TODO: Rename to `noBackdrop` and deprecate `hideBackdrop`
  hideBackdrop: {
    type: Boolean,
    default: false
  },
  okOnly: {
    type: Boolean,
    default: false
  },
  okDisabled: {
    type: Boolean,
    default: false
  },
  cancelDisabled: {
    type: Boolean,
    default: false
  },
  visible: {
    type: Boolean,
    default: false
  },
  returnFocus: {
    // HTML Element, CSS selector string or Vue component instance
    type: [HTMLElement, String, Object],
    default: null
  },
  headerCloseContent: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'headerCloseContent');
    }
  },
  headerCloseLabel: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'headerCloseLabel');
    }
  },
  cancelTitle: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'cancelTitle');
    }
  },
  cancelTitleHtml: {
    type: String
  },
  okTitle: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'okTitle');
    }
  },
  okTitleHtml: {
    type: String
  },
  cancelVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'cancelVariant');
    }
  },
  okVariant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_MODAL, 'okVariant');
    }
  },
  lazy: {
    type: Boolean,
    default: false
  },
  busy: {
    type: Boolean,
    default: false
  },
  static: {
    type: Boolean,
    default: false
  },
  autoFocusButton: {
    type: String,
    default: null,
    validator
    /* istanbul ignore next */
    : function validator(val) {
      /* istanbul ignore next */
      return isUndefinedOrNull(val) || arrayIncludes(['ok', 'cancel', 'close'], val);
    }
  }
}; // @vue/component

var BModal = /*#__PURE__*/Vue.extend({
  name: NAME_MODAL,
  mixins: [attrsMixin, idMixin, listenOnDocumentMixin, listenOnRootMixin, listenOnWindowMixin, normalizeSlotMixin, scopedStyleAttrsMixin],
  inheritAttrs: false,
  model: {
    prop: 'visible',
    event: 'change'
  },
  props: props$i,
  data: function data() {
    return {
      isHidden: true,
      // If modal should not be in document
      isVisible: false,
      // Controls modal visible state
      isTransitioning: false,
      // Used for style control
      isShow: false,
      // Used for style control
      isBlock: false,
      // Used for style control
      isOpening: false,
      // To signal that the modal is in the process of opening
      isClosing: false,
      // To signal that the modal is in the process of closing
      ignoreBackdropClick: false,
      // Used to signify if click out listener should ignore the click
      isModalOverflowing: false,
      return_focus: this.returnFocus || null,
      // The following items are controlled by the modalManager instance
      scrollbarWidth: 0,
      zIndex: modalManager.getBaseZIndex(),
      isTop: true,
      isBodyOverflowing: false
    };
  },
  computed: {
    modalId: function modalId() {
      return this.safeId();
    },
    modalOuterId: function modalOuterId() {
      return this.safeId('__BV_modal_outer_');
    },
    modalHeaderId: function modalHeaderId() {
      return this.safeId('__BV_modal_header_');
    },
    modalBodyId: function modalBodyId() {
      return this.safeId('__BV_modal_body_');
    },
    modalTitleId: function modalTitleId() {
      return this.safeId('__BV_modal_title_');
    },
    modalContentId: function modalContentId() {
      return this.safeId('__BV_modal_content_');
    },
    modalFooterId: function modalFooterId() {
      return this.safeId('__BV_modal_footer_');
    },
    modalBackdropId: function modalBackdropId() {
      return this.safeId('__BV_modal_backdrop_');
    },
    modalClasses: function modalClasses() {
      return [{
        fade: !this.noFade,
        show: this.isShow
      }, this.modalClass];
    },
    modalStyles: function modalStyles() {
      var sbWidth = "".concat(this.scrollbarWidth, "px");
      return {
        paddingLeft: !this.isBodyOverflowing && this.isModalOverflowing ? sbWidth : '',
        paddingRight: this.isBodyOverflowing && !this.isModalOverflowing ? sbWidth : '',
        // Needed to fix issue https://github.com/bootstrap-vue/bootstrap-vue/issues/3457
        // Even though we are using v-show, we must ensure 'none' is restored in the styles
        display: this.isBlock ? 'block' : 'none'
      };
    },
    dialogClasses: function dialogClasses() {
      var _ref;

      return [(_ref = {}, _defineProperty$B(_ref, "modal-".concat(this.size), this.size), _defineProperty$B(_ref, 'modal-dialog-centered', this.centered), _defineProperty$B(_ref, 'modal-dialog-scrollable', this.scrollable), _ref), this.dialogClass];
    },
    headerClasses: function headerClasses() {
      var _ref2;

      return [(_ref2 = {}, _defineProperty$B(_ref2, "bg-".concat(this.headerBgVariant), this.headerBgVariant), _defineProperty$B(_ref2, "text-".concat(this.headerTextVariant), this.headerTextVariant), _defineProperty$B(_ref2, "border-".concat(this.headerBorderVariant), this.headerBorderVariant), _ref2), this.headerClass];
    },
    titleClasses: function titleClasses() {
      return [{
        'sr-only': this.titleSrOnly
      }, this.titleClass];
    },
    bodyClasses: function bodyClasses() {
      var _ref3;

      return [(_ref3 = {}, _defineProperty$B(_ref3, "bg-".concat(this.bodyBgVariant), this.bodyBgVariant), _defineProperty$B(_ref3, "text-".concat(this.bodyTextVariant), this.bodyTextVariant), _ref3), this.bodyClass];
    },
    footerClasses: function footerClasses() {
      var _ref4;

      return [(_ref4 = {}, _defineProperty$B(_ref4, "bg-".concat(this.footerBgVariant), this.footerBgVariant), _defineProperty$B(_ref4, "text-".concat(this.footerTextVariant), this.footerTextVariant), _defineProperty$B(_ref4, "border-".concat(this.footerBorderVariant), this.footerBorderVariant), _ref4), this.footerClass];
    },
    modalOuterStyle: function modalOuterStyle() {
      // Styles needed for proper stacking of modals
      return {
        position: 'absolute',
        zIndex: this.zIndex
      };
    },
    slotScope: function slotScope() {
      return {
        ok: this.onOk,
        cancel: this.onCancel,
        close: this.onClose,
        hide: this.hide,
        visible: this.isVisible
      };
    },
    computeIgnoreEnforceFocusSelector: function computeIgnoreEnforceFocusSelector() {
      // Normalize to an single selector with selectors separated by `,`
      return concat(this.ignoreEnforceFocusSelector).filter(identity$1).join(',').trim();
    },
    computedAttrs: function computedAttrs() {
      // If the parent has a scoped style attribute, and the modal
      // is portalled, add the scoped attribute to the modal wrapper
      var scopedStyleAttrs = !this.static ? this.scopedStyleAttrs : {};
      return _objectSpread$s(_objectSpread$s(_objectSpread$s({}, scopedStyleAttrs), this.bvAttrs), {}, {
        id: this.modalOuterId
      });
    },
    computedModalAttrs: function computedModalAttrs() {
      var isVisible = this.isVisible,
          ariaLabel = this.ariaLabel;
      return {
        id: this.modalId,
        role: 'dialog',
        'aria-hidden': isVisible ? null : 'true',
        'aria-modal': isVisible ? 'true' : null,
        'aria-label': ariaLabel,
        'aria-labelledby': this.hideHeader || ariaLabel || // TODO: Rename slot to `title` and deprecate `modal-title`
        !(this.hasNormalizedSlot('modal-title') || this.titleHtml || this.title) ? null : this.modalTitleId,
        'aria-describedby': this.modalBodyId
      };
    }
  },
  watch: {
    visible: function visible(newVal, oldVal) {
      if (newVal !== oldVal) {
        this[newVal ? 'show' : 'hide']();
      }
    }
  },
  created: function created() {
    // Define non-reactive properties
    this.$_observer = null;
  },
  mounted: function mounted() {
    // Set initial z-index as queried from the DOM
    this.zIndex = modalManager.getBaseZIndex(); // Listen for events from others to either open or close ourselves
    // and listen to all modals to enable/disable enforce focus

    this.listenOnRoot('bv::show::modal', this.showHandler);
    this.listenOnRoot('bv::hide::modal', this.hideHandler);
    this.listenOnRoot('bv::toggle::modal', this.toggleHandler); // Listen for `bv:modal::show events`, and close ourselves if the
    // opening modal not us

    this.listenOnRoot('bv::modal::show', this.modalListener); // Initially show modal?

    if (this.visible === true) {
      this.$nextTick(this.show);
    }
  },
  beforeDestroy: function beforeDestroy() {
    // Ensure everything is back to normal
    this.setObserver(false);

    if (this.isVisible) {
      this.isVisible = false;
      this.isShow = false;
      this.isTransitioning = false;
    }
  },
  methods: {
    setObserver: function setObserver() {
      var on = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      this.$_observer && this.$_observer.disconnect();
      this.$_observer = null;

      if (on) {
        this.$_observer = observeDom(this.$refs.content, this.checkModalOverflow.bind(this), OBSERVER_CONFIG);
      }
    },
    // Private method to update the v-model
    updateModel: function updateModel(val) {
      if (val !== this.visible) {
        this.$emit('change', val);
      }
    },
    // Private method to create a BvModalEvent object
    buildEvent: function buildEvent(type) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return new BvModalEvent(type, _objectSpread$s(_objectSpread$s({
        // Default options
        cancelable: false,
        target: this.$refs.modal || this.$el || null,
        relatedTarget: null,
        trigger: null
      }, options), {}, {
        // Options that can't be overridden
        vueTarget: this,
        componentId: this.modalId
      }));
    },
    // Public method to show modal
    show: function show() {
      if (this.isVisible || this.isOpening) {
        // If already open, or in the process of opening, do nothing

        /* istanbul ignore next */
        return;
      }
      /* istanbul ignore next */


      if (this.isClosing) {
        // If we are in the process of closing, wait until hidden before re-opening

        /* istanbul ignore next */
        this.$once('hidden', this.show);
        /* istanbul ignore next */

        return;
      }

      this.isOpening = true; // Set the element to return focus to when closed

      this.return_focus = this.return_focus || this.getActiveElement();
      var showEvt = this.buildEvent('show', {
        cancelable: true
      });
      this.emitEvent(showEvt); // Don't show if canceled

      if (showEvt.defaultPrevented || this.isVisible) {
        this.isOpening = false; // Ensure the v-model reflects the current state

        this.updateModel(false);
        return;
      } // Show the modal


      this.doShow();
    },
    // Public method to hide modal
    hide: function hide() {
      var trigger = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

      if (!this.isVisible || this.isClosing) {
        /* istanbul ignore next */
        return;
      }

      this.isClosing = true;
      var hideEvt = this.buildEvent('hide', {
        cancelable: trigger !== 'FORCE',
        trigger: trigger || null
      }); // We emit specific event for one of the three built-in buttons

      if (trigger === 'ok') {
        this.$emit('ok', hideEvt);
      } else if (trigger === 'cancel') {
        this.$emit('cancel', hideEvt);
      } else if (trigger === 'headerclose') {
        this.$emit('close', hideEvt);
      }

      this.emitEvent(hideEvt); // Hide if not canceled

      if (hideEvt.defaultPrevented || !this.isVisible) {
        this.isClosing = false; // Ensure v-model reflects current state

        this.updateModel(true);
        return;
      } // Stop observing for content changes


      this.setObserver(false); // Trigger the hide transition

      this.isVisible = false; // Update the v-model

      this.updateModel(false);
    },
    // Public method to toggle modal visibility
    toggle: function toggle(triggerEl) {
      if (triggerEl) {
        this.return_focus = triggerEl;
      }

      if (this.isVisible) {
        this.hide('toggle');
      } else {
        this.show();
      }
    },
    // Private method to get the current document active element
    getActiveElement: function getActiveElement$1() {
      // Returning focus to `document.body` may cause unwanted scrolls,
      // so we exclude setting focus on body
      var activeElement = getActiveElement(isBrowser ? [document.body] : []); // Preset the fallback return focus value if it is not set
      // `document.activeElement` should be the trigger element that was clicked or
      // in the case of using the v-model, which ever element has current focus
      // Will be overridden by some commands such as toggle, etc.
      // Note: On IE 11, `document.activeElement` may be `null`
      // So we test it for truthiness first
      // https://github.com/bootstrap-vue/bootstrap-vue/issues/3206


      return activeElement && activeElement.focus ? activeElement : null;
    },
    // Private method to finish showing modal
    doShow: function doShow() {
      var _this = this;

      /* istanbul ignore next: commenting out for now until we can test stacking */
      if (modalManager.modalsAreOpen && this.noStacking) {
        // If another modal(s) is already open, wait for it(them) to close
        this.listenOnRootOnce('bv::modal::hidden', this.doShow);
        return;
      }

      modalManager.registerModal(this); // Place modal in DOM

      this.isHidden = false;
      this.$nextTick(function () {
        // We do this in `$nextTick()` to ensure the modal is in DOM first
        // before we show it
        _this.isVisible = true;
        _this.isOpening = false; // Update the v-model

        _this.updateModel(true);

        _this.$nextTick(function () {
          // Observe changes in modal content and adjust if necessary
          // In a `$nextTick()` in case modal content is lazy
          _this.setObserver(true);
        });
      });
    },
    // Transition handlers
    onBeforeEnter: function onBeforeEnter() {
      this.isTransitioning = true;
      this.setResizeEvent(true);
    },
    onEnter: function onEnter() {
      var _this2 = this;

      this.isBlock = true; // We add the `show` class 1 frame later
      // `requestAF()` runs the callback before the next repaint, so we need
      // two calls to guarantee the next frame has been rendered

      requestAF(function () {
        requestAF(function () {
          _this2.isShow = true;
        });
      });
    },
    onAfterEnter: function onAfterEnter() {
      var _this3 = this;

      this.checkModalOverflow();
      this.isTransitioning = false; // We use `requestAF()` to allow transition hooks to complete
      // before passing control over to the other handlers
      // This will allow users to not have to use `$nextTick()` or `requestAF()`
      // when trying to pre-focus an element

      requestAF(function () {
        _this3.emitEvent(_this3.buildEvent('shown'));

        _this3.setEnforceFocus(true);

        _this3.$nextTick(function () {
          // Delayed in a `$nextTick()` to allow users time to pre-focus
          // an element if the wish
          _this3.focusFirst();
        });
      });
    },
    onBeforeLeave: function onBeforeLeave() {
      this.isTransitioning = true;
      this.setResizeEvent(false);
      this.setEnforceFocus(false);
    },
    onLeave: function onLeave() {
      // Remove the 'show' class
      this.isShow = false;
    },
    onAfterLeave: function onAfterLeave() {
      var _this4 = this;

      this.isBlock = false;
      this.isTransitioning = false;
      this.isModalOverflowing = false;
      this.isHidden = true;
      this.$nextTick(function () {
        _this4.isClosing = false;
        modalManager.unregisterModal(_this4);

        _this4.returnFocusTo(); // TODO: Need to find a way to pass the `trigger` property
        //       to the `hidden` event, not just only the `hide` event


        _this4.emitEvent(_this4.buildEvent('hidden'));
      });
    },
    // Event emitter
    emitEvent: function emitEvent(bvModalEvt) {
      var type = bvModalEvt.type; // We emit on root first incase a global listener wants to cancel
      // the event first before the instance emits its event

      this.emitOnRoot("bv::modal::".concat(type), bvModalEvt, bvModalEvt.componentId);
      this.$emit(type, bvModalEvt);
    },
    // UI event handlers
    onDialogMousedown: function onDialogMousedown() {
      var _this5 = this;

      // Watch to see if the matching mouseup event occurs outside the dialog
      // And if it does, cancel the clickOut handler
      var modal = this.$refs.modal;

      var onceModalMouseup = function onceModalMouseup(evt) {
        eventOff(modal, 'mouseup', onceModalMouseup, EVENT_OPTIONS_NO_CAPTURE);

        if (evt.target === modal) {
          _this5.ignoreBackdropClick = true;
        }
      };

      eventOn(modal, 'mouseup', onceModalMouseup, EVENT_OPTIONS_NO_CAPTURE);
    },
    onClickOut: function onClickOut(evt) {
      if (this.ignoreBackdropClick) {
        // Click was initiated inside the modal content, but finished outside.
        // Set by the above onDialogMousedown handler
        this.ignoreBackdropClick = false;
        return;
      } // Do nothing if not visible, backdrop click disabled, or element
      // that generated click event is no longer in document body


      if (!this.isVisible || this.noCloseOnBackdrop || !contains(document.body, evt.target)) {
        return;
      } // If backdrop clicked, hide modal


      if (!contains(this.$refs.content, evt.target)) {
        this.hide('backdrop');
      }
    },
    onOk: function onOk() {
      this.hide('ok');
    },
    onCancel: function onCancel() {
      this.hide('cancel');
    },
    onClose: function onClose() {
      this.hide('headerclose');
    },
    onEsc: function onEsc(evt) {
      // If ESC pressed, hide modal
      if (evt.keyCode === CODE_ESC && this.isVisible && !this.noCloseOnEsc) {
        this.hide('esc');
      }
    },
    // Document focusin listener
    focusHandler: function focusHandler(evt) {
      // If focus leaves modal content, bring it back
      var content = this.$refs.content;
      var target = evt.target;

      if (this.noEnforceFocus || !this.isTop || !this.isVisible || !content || document === target || contains(content, target) || this.computeIgnoreEnforceFocusSelector && closest(this.computeIgnoreEnforceFocusSelector, target, true)) {
        return;
      }

      var tabables = getTabables(this.$refs.content);
      var _this$$refs = this.$refs,
          bottomTrap = _this$$refs.bottomTrap,
          topTrap = _this$$refs.topTrap;

      if (bottomTrap && target === bottomTrap) {
        // If user pressed TAB out of modal into our bottom trab trap element
        // Find the first tabable element in the modal content and focus it
        if (attemptFocus(tabables[0])) {
          // Focus was successful
          return;
        }
      } else if (topTrap && target === topTrap) {
        // If user pressed CTRL-TAB out of modal and into our top tab trap element
        // Find the last tabable element in the modal content and focus it
        if (attemptFocus(tabables[tabables.length - 1])) {
          // Focus was successful
          return;
        }
      } // Otherwise focus the modal content container


      attemptFocus(content, {
        preventScroll: true
      });
    },
    // Turn on/off focusin listener
    setEnforceFocus: function setEnforceFocus(on) {
      this.listenDocument(on, 'focusin', this.focusHandler);
    },
    // Resize listener
    setResizeEvent: function setResizeEvent(on) {
      this.listenWindow(on, 'resize', this.checkModalOverflow);
      this.listenWindow(on, 'orientationchange', this.checkModalOverflow);
    },
    // Root listener handlers
    showHandler: function showHandler(id, triggerEl) {
      if (id === this.modalId) {
        this.return_focus = triggerEl || this.getActiveElement();
        this.show();
      }
    },
    hideHandler: function hideHandler(id) {
      if (id === this.modalId) {
        this.hide('event');
      }
    },
    toggleHandler: function toggleHandler(id, triggerEl) {
      if (id === this.modalId) {
        this.toggle(triggerEl);
      }
    },
    modalListener: function modalListener(bvEvt) {
      // If another modal opens, close this one if stacking not permitted
      if (this.noStacking && bvEvt.vueTarget !== this) {
        this.hide();
      }
    },
    // Focus control handlers
    focusFirst: function focusFirst() {
      var _this6 = this;

      // Don't try and focus if we are SSR
      if (isBrowser) {
        requestAF(function () {
          var modal = _this6.$refs.modal;
          var content = _this6.$refs.content;

          var activeElement = _this6.getActiveElement(); // If the modal contains the activeElement, we don't do anything


          if (modal && content && !(activeElement && contains(content, activeElement))) {
            var ok = _this6.$refs['ok-button'];
            var cancel = _this6.$refs['cancel-button'];
            var close = _this6.$refs['close-button']; // Focus the appropriate button or modal content wrapper

            var autoFocus = _this6.autoFocusButton;
            /* istanbul ignore next */

            var el = autoFocus === 'ok' && ok ? ok.$el || ok : autoFocus === 'cancel' && cancel ? cancel.$el || cancel : autoFocus === 'close' && close ? close.$el || close : content; // Focus the element

            attemptFocus(el);

            if (el === content) {
              // Make sure top of modal is showing (if longer than the viewport)
              _this6.$nextTick(function () {
                modal.scrollTop = 0;
              });
            }
          }
        });
      }
    },
    returnFocusTo: function returnFocusTo() {
      // Prefer `returnFocus` prop over event specified
      // `return_focus` value
      var el = this.returnFocus || this.return_focus || null;
      this.return_focus = null;
      this.$nextTick(function () {
        // Is el a string CSS selector?
        el = isString(el) ? select(el) : el;

        if (el) {
          // Possibly could be a component reference
          el = el.$el || el;
          attemptFocus(el);
        }
      });
    },
    checkModalOverflow: function checkModalOverflow() {
      if (this.isVisible) {
        var modal = this.$refs.modal;
        this.isModalOverflowing = modal.scrollHeight > document.documentElement.clientHeight;
      }
    },
    makeModal: function makeModal(h) {
      // Modal header
      var $header = h();

      if (!this.hideHeader) {
        // TODO: Rename slot to `header` and deprecate `modal-header`
        var $modalHeader = this.normalizeSlot('modal-header', this.slotScope);

        if (!$modalHeader) {
          var $closeButton = h();

          if (!this.hideHeaderClose) {
            $closeButton = h(BButtonClose, {
              props: {
                content: this.headerCloseContent,
                disabled: this.isTransitioning,
                ariaLabel: this.headerCloseLabel,
                textVariant: this.headerCloseVariant || this.headerTextVariant
              },
              on: {
                click: this.onClose
              },
              ref: 'close-button'
            }, // TODO: Rename slot to `header-close` and deprecate `modal-header-close`
            [this.normalizeSlot('modal-header-close')]);
          }

          $modalHeader = [h(this.titleTag, {
            staticClass: 'modal-title',
            class: this.titleClasses,
            attrs: {
              id: this.modalTitleId
            },
            // TODO: Rename slot to `title` and deprecate `modal-title`
            domProps: this.hasNormalizedSlot('modal-title') ? {} : htmlOrText(this.titleHtml, this.title)
          }, // TODO: Rename slot to `title` and deprecate `modal-title`
          this.normalizeSlot('modal-title', this.slotScope)), $closeButton];
        }

        $header = h('header', {
          staticClass: 'modal-header',
          class: this.headerClasses,
          attrs: {
            id: this.modalHeaderId
          },
          ref: 'header'
        }, [$modalHeader]);
      } // Modal body


      var $body = h('div', {
        staticClass: 'modal-body',
        class: this.bodyClasses,
        attrs: {
          id: this.modalBodyId
        },
        ref: 'body'
      }, this.normalizeSlot(SLOT_NAME_DEFAULT, this.slotScope)); // Modal footer

      var $footer = h();

      if (!this.hideFooter) {
        // TODO: Rename slot to `footer` and deprecate `modal-footer`
        var $modalFooter = this.normalizeSlot('modal-footer', this.slotScope);

        if (!$modalFooter) {
          var $cancelButton = h();

          if (!this.okOnly) {
            $cancelButton = h(BButton, {
              props: {
                variant: this.cancelVariant,
                size: this.buttonSize,
                disabled: this.cancelDisabled || this.busy || this.isTransitioning
              },
              // TODO: Rename slot to `cancel-button` and deprecate `modal-cancel`
              domProps: this.hasNormalizedSlot('modal-cancel') ? {} : htmlOrText(this.cancelTitleHtml, this.cancelTitle),
              on: {
                click: this.onCancel
              },
              ref: 'cancel-button'
            }, // TODO: Rename slot to `cancel-button` and deprecate `modal-cancel`
            this.normalizeSlot('modal-cancel'));
          }

          var $okButton = h(BButton, {
            props: {
              variant: this.okVariant,
              size: this.buttonSize,
              disabled: this.okDisabled || this.busy || this.isTransitioning
            },
            // TODO: Rename slot to `ok-button` and deprecate `modal-ok`
            domProps: this.hasNormalizedSlot('modal-ok') ? {} : htmlOrText(this.okTitleHtml, this.okTitle),
            on: {
              click: this.onOk
            },
            ref: 'ok-button'
          }, // TODO: Rename slot to `ok-button` and deprecate `modal-ok`
          this.normalizeSlot('modal-ok'));
          $modalFooter = [$cancelButton, $okButton];
        }

        $footer = h('footer', {
          staticClass: 'modal-footer',
          class: this.footerClasses,
          attrs: {
            id: this.modalFooterId
          },
          ref: 'footer'
        }, [$modalFooter]);
      } // Assemble modal content


      var $modalContent = h('div', {
        staticClass: 'modal-content',
        class: this.contentClass,
        attrs: {
          id: this.modalContentId,
          tabindex: '-1'
        },
        ref: 'content'
      }, [$header, $body, $footer]); // Tab traps to prevent page from scrolling to next element in
      // tab index during enforce-focus tab cycle

      var $tabTrapTop = h();
      var $tabTrapBottom = h();

      if (this.isVisible && !this.noEnforceFocus) {
        $tabTrapTop = h('span', {
          ref: 'topTrap',
          attrs: {
            tabindex: '0'
          }
        });
        $tabTrapBottom = h('span', {
          ref: 'bottomTrap',
          attrs: {
            tabindex: '0'
          }
        });
      } // Modal dialog wrapper


      var $modalDialog = h('div', {
        staticClass: 'modal-dialog',
        class: this.dialogClasses,
        on: {
          mousedown: this.onDialogMousedown
        },
        ref: 'dialog'
      }, [$tabTrapTop, $modalContent, $tabTrapBottom]); // Modal

      var $modal = h('div', {
        staticClass: 'modal',
        class: this.modalClasses,
        style: this.modalStyles,
        attrs: this.computedModalAttrs,
        on: {
          keydown: this.onEsc,
          click: this.onClickOut
        },
        directives: [{
          name: 'show',
          value: this.isVisible
        }],
        ref: 'modal'
      }, [$modalDialog]); // Wrap modal in transition
      // Sadly, we can't use `BVTransition` here due to the differences in
      // transition durations for `.modal` and `.modal-dialog`
      // At least until https://github.com/vuejs/vue/issues/9986 is resolved

      $modal = h('transition', {
        props: {
          enterClass: '',
          enterToClass: '',
          enterActiveClass: '',
          leaveClass: '',
          leaveActiveClass: '',
          leaveToClass: ''
        },
        on: {
          beforeEnter: this.onBeforeEnter,
          enter: this.onEnter,
          afterEnter: this.onAfterEnter,
          beforeLeave: this.onBeforeLeave,
          leave: this.onLeave,
          afterLeave: this.onAfterLeave
        }
      }, [$modal]); // Modal backdrop

      var $backdrop = h();

      if (!this.hideBackdrop && this.isVisible) {
        $backdrop = h('div', {
          staticClass: 'modal-backdrop',
          attrs: {
            id: this.modalBackdropId
          }
        }, // TODO: Rename slot to `backdrop` and deprecate `modal-backdrop`
        this.normalizeSlot('modal-backdrop'));
      }

      $backdrop = h(BVTransition, {
        props: {
          noFade: this.noFade
        }
      }, [$backdrop]); // Assemble modal and backdrop in an outer <div>

      return h('div', {
        style: this.modalOuterStyle,
        attrs: this.computedAttrs,
        key: "modal-outer-".concat(this._uid)
      }, [$modal, $backdrop]);
    }
  },
  render: function render(h) {
    if (this.static) {
      return this.lazy && this.isHidden ? h() : this.makeModal(h);
    } else {
      return this.isHidden ? h() : h(BTransporterSingle, [this.makeModal(h)]);
    }
  }
});

var EVENT_SHOW = 'bv::show::modal'; // Prop name we use to store info on root element

var PROPERTY = '__bv_modal_directive__';

var getTarget = function getTarget(_ref) {
  var _ref$modifiers = _ref.modifiers,
      modifiers = _ref$modifiers === void 0 ? {} : _ref$modifiers,
      arg = _ref.arg,
      value = _ref.value;
  // Try value, then arg, otherwise pick last modifier
  return isString(value) ? value : isString(arg) ? arg : keys$1(modifiers).reverse()[0];
};

var getTriggerElement = function getTriggerElement(el) {
  // If root element is a dropdown-item or nav-item, we
  // need to target the inner link or button instead
  return el && matches(el, '.dropdown-menu > li, li.nav-item') ? select('a, button', el) || el : el;
};

var setRole = function setRole(trigger) {
  // Ensure accessibility on non button elements
  if (trigger && trigger.tagName !== 'BUTTON') {
    // Only set a role if the trigger element doesn't have one
    if (!hasAttr(trigger, 'role')) {
      setAttr(trigger, 'role', 'button');
    } // Add a tabindex is not a button or link, and tabindex is not provided


    if (trigger.tagName !== 'A' && !hasAttr(trigger, 'tabindex')) {
      setAttr(trigger, 'tabindex', '0');
    }
  }
};

var bind$1 = function bind(el, binding, vnode) {
  var target = getTarget(binding);
  var trigger = getTriggerElement(el);

  if (target && trigger) {
    var handler = function handler(evt) {
      // `currentTarget` is the element with the listener on it
      var currentTarget = evt.currentTarget;

      if (!isDisabled(currentTarget)) {
        var type = evt.type;
        var key = evt.keyCode; // Open modal only if trigger is not disabled

        if (type === 'click' || type === 'keydown' && (key === CODE_ENTER || key === CODE_SPACE)) {
          vnode.context.$root.$emit(EVENT_SHOW, target, currentTarget);
        }
      }
    };

    el[PROPERTY] = {
      handler: handler,
      target: target,
      trigger: trigger
    }; // If element is not a button, we add `role="button"` for accessibility

    setRole(trigger); // Listen for click events

    eventOn(trigger, 'click', handler, EVENT_OPTIONS_PASSIVE);

    if (trigger.tagName !== 'BUTTON' && getAttr(trigger, 'role') === 'button') {
      // If trigger isn't a button but has role button,
      // we also listen for `keydown.space` && `keydown.enter`
      eventOn(trigger, 'keydown', handler, EVENT_OPTIONS_PASSIVE);
    }
  }
};

var unbind$1 = function unbind(el) {
  var oldProp = el[PROPERTY] || {};
  var trigger = oldProp.trigger;
  var handler = oldProp.handler;

  if (trigger && handler) {
    eventOff(trigger, 'click', handler, EVENT_OPTIONS_PASSIVE);
    eventOff(trigger, 'keydown', handler, EVENT_OPTIONS_PASSIVE);
    eventOff(el, 'click', handler, EVENT_OPTIONS_PASSIVE);
    eventOff(el, 'keydown', handler, EVENT_OPTIONS_PASSIVE);
  }

  delete el[PROPERTY];
};

var componentUpdated$1 = function componentUpdated(el, binding, vnode) {
  var oldProp = el[PROPERTY] || {};
  var target = getTarget(binding);
  var trigger = getTriggerElement(el);

  if (target !== oldProp.target || trigger !== oldProp.trigger) {
    // We bind and rebind if the target or trigger changes
    unbind$1(el);
    bind$1(el, binding, vnode);
  } // If trigger element is not a button, ensure `role="button"`
  // is still set for accessibility


  setRole(trigger);
};

var updated = function updated() {};
/*
 * Export our directive
 */


var VBModal = {
  inserted: componentUpdated$1,
  updated: updated,
  componentUpdated: componentUpdated$1,
  unbind: unbind$1
};

function _defineProperty$C(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var props$j = {
  tag: {
    type: String,
    default: 'ul'
  },
  fill: {
    type: Boolean,
    default: false
  },
  justified: {
    type: Boolean,
    default: false
  },
  align: {
    type: String // default: null

  },
  tabs: {
    type: Boolean,
    default: false
  },
  pills: {
    type: Boolean,
    default: false
  },
  vertical: {
    type: Boolean,
    default: false
  },
  small: {
    type: Boolean,
    default: false
  },
  cardHeader: {
    // Set to true if placing in a card header
    type: Boolean,
    default: false
  }
}; // -- Utils --

var computeJustifyContent = function computeJustifyContent(value) {
  // Normalize value
  value = value === 'left' ? 'start' : value === 'right' ? 'end' : value;
  return "justify-content-".concat(value);
}; // @vue/component


var BNav = /*#__PURE__*/Vue.extend({
  name: NAME_NAV,
  functional: true,
  props: props$j,
  render: function render(h, _ref) {
    var _class;

    var props = _ref.props,
        data = _ref.data,
        children = _ref.children;
    return h(props.tag, a(data, {
      staticClass: 'nav',
      class: (_class = {
        'nav-tabs': props.tabs,
        'nav-pills': props.pills && !props.tabs,
        'card-header-tabs': !props.vertical && props.cardHeader && props.tabs,
        'card-header-pills': !props.vertical && props.cardHeader && props.pills && !props.tabs,
        'flex-column': props.vertical,
        'nav-fill': !props.vertical && props.fill,
        'nav-justified': !props.vertical && props.justified
      }, _defineProperty$C(_class, computeJustifyContent(props.align), !props.vertical && props.align), _defineProperty$C(_class, "small", props.small), _class)
    }), children);
  }
});

function _defineProperty$D(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BSpinner = /*#__PURE__*/Vue.extend({
  name: NAME_SPINNER,
  functional: true,
  props: {
    type: {
      type: String,
      default: 'border' // SCSS currently supports 'border' or 'grow'

    },
    label: {
      type: String // default: null

    },
    variant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SPINNER, 'variant');
      }
    },
    small: {
      type: Boolean,
      default: false
    },
    role: {
      type: String,
      default: 'status'
    },
    tag: {
      type: String,
      default: 'span'
    }
  },
  render: function render(h, _ref) {
    var _class;

    var props = _ref.props,
        data = _ref.data,
        slots = _ref.slots,
        scopedSlots = _ref.scopedSlots;
    var $slots = slots();
    var $scopedSlots = scopedSlots || {};
    var label = normalizeSlot(SLOT_NAME_LABEL, {}, $scopedSlots, $slots) || props.label;

    if (label) {
      label = h('span', {
        staticClass: 'sr-only'
      }, label);
    }

    return h(props.tag, a(data, {
      attrs: {
        role: label ? props.role || 'status' : null,
        'aria-hidden': label ? null : 'true'
      },
      class: (_class = {}, _defineProperty$D(_class, "spinner-".concat(props.type), props.type), _defineProperty$D(_class, "spinner-".concat(props.type, "-sm"), props.small), _defineProperty$D(_class, "text-".concat(props.variant), props.variant), _class)
    }), [label || h()]);
  }
});

function ownKeys$t(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$t(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$t(Object(source), true).forEach(function (key) { _defineProperty$E(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$t(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$E(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var positionCover = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 0
};
var BOverlay = /*#__PURE__*/Vue.extend({
  name: NAME_OVERLAY,
  mixins: [normalizeSlotMixin],
  props: {
    show: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'light'
    },
    bgColor: {
      // Alternative to variant, allowing a specific
      // CSS color to be applied to the overlay
      type: String // default: null

    },
    opacity: {
      type: [Number, String],
      default: 0.85,
      validator: function validator(value) {
        var number = toFloat(value, 0);
        return number >= 0 && number <= 1;
      }
    },
    blur: {
      type: String,
      default: '2px'
    },
    rounded: {
      type: [Boolean, String],
      default: false
    },
    noCenter: {
      type: Boolean,
      default: false
    },
    noFade: {
      type: Boolean,
      default: false
    },
    spinnerType: {
      type: String,
      default: 'border'
    },
    spinnerVariant: {
      type: String // default: null

    },
    spinnerSmall: {
      type: Boolean,
      default: false
    },
    overlayTag: {
      type: String,
      default: 'div'
    },
    wrapTag: {
      type: String,
      default: 'div'
    },
    noWrap: {
      // If set, does not render the default slot
      // and switches to absolute positioning
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    },
    zIndex: {
      type: [Number, String],
      default: 10
    }
  },
  computed: {
    computedRounded: function computedRounded() {
      var rounded = this.rounded;
      return rounded === true || rounded === '' ? 'rounded' : !rounded ? '' : "rounded-".concat(rounded);
    },
    computedVariant: function computedVariant() {
      return this.variant && !this.bgColor ? "bg-".concat(this.variant) : '';
    },
    overlayScope: function overlayScope() {
      return {
        spinnerType: this.spinnerType || null,
        spinnerVariant: this.spinnerVariant || null,
        spinnerSmall: this.spinnerSmall
      };
    }
  },
  methods: {
    defaultOverlayFn: function defaultOverlayFn(_ref) {
      var spinnerType = _ref.spinnerType,
          spinnerVariant = _ref.spinnerVariant,
          spinnerSmall = _ref.spinnerSmall;
      return this.$createElement(BSpinner, {
        props: {
          type: spinnerType,
          variant: spinnerVariant,
          small: spinnerSmall
        }
      });
    }
  },
  render: function render(h) {
    var _this = this;

    var $overlay = h();

    if (this.show) {
      var scope = this.overlayScope; // Overlay backdrop

      var $background = h('div', {
        staticClass: 'position-absolute',
        class: [this.computedVariant, this.computedRounded],
        style: _objectSpread$t(_objectSpread$t({}, positionCover), {}, {
          opacity: this.opacity,
          backgroundColor: this.bgColor || null,
          backdropFilter: this.blur ? "blur(".concat(this.blur, ")") : null
        })
      }); // Overlay content

      var $content = h('div', {
        staticClass: 'position-absolute',
        style: this.noCenter ?
        /* istanbul ignore next */
        _objectSpread$t({}, positionCover) : {
          top: '50%',
          left: '50%',
          transform: 'translateX(-50%) translateY(-50%)'
        }
      }, [this.normalizeSlot('overlay', scope) || this.defaultOverlayFn(scope)]); // Overlay positioning

      $overlay = h(this.overlayTag, {
        key: 'overlay',
        staticClass: 'b-overlay',
        class: {
          'position-absolute': !this.noWrap || this.noWrap && !this.fixed,
          'position-fixed': this.noWrap && this.fixed
        },
        style: _objectSpread$t(_objectSpread$t({}, positionCover), {}, {
          zIndex: this.zIndex || 10
        }),
        on: {
          click: function click(evt) {
            return _this.$emit('click', evt);
          }
        }
      }, [$background, $content]);
    } // Wrap in a fade transition


    $overlay = h(BVTransition, {
      props: {
        noFade: this.noFade,
        appear: true
      },
      on: {
        'after-enter': function afterEnter() {
          return _this.$emit('shown');
        },
        'after-leave': function afterLeave() {
          return _this.$emit('hidden');
        }
      }
    }, [$overlay]);

    if (this.noWrap) {
      return $overlay;
    }

    return h(this.wrapTag, {
      staticClass: 'b-overlay-wrap position-relative',
      attrs: {
        'aria-busy': this.show ? 'true' : null
      }
    }, this.noWrap ? [$overlay] : [this.normalizeSlot(), $overlay]);
  }
});

/**
 * @param {number} length
 * @return {Array}
 */
var range = function range(length) {
  return Array.apply(null, {
    length: length
  });
};

// Base on-demand component for tooltip / popover templates
var AttachmentMap = {
  AUTO: 'auto',
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left',
  TOPLEFT: 'top',
  TOPRIGHT: 'top',
  RIGHTTOP: 'right',
  RIGHTBOTTOM: 'right',
  BOTTOMLEFT: 'bottom',
  BOTTOMRIGHT: 'bottom',
  LEFTTOP: 'left',
  LEFTBOTTOM: 'left'
};
var OffsetMap = {
  AUTO: 0,
  TOPLEFT: -1,
  TOP: 0,
  TOPRIGHT: +1,
  RIGHTTOP: -1,
  RIGHT: 0,
  RIGHTBOTTOM: +1,
  BOTTOMLEFT: -1,
  BOTTOM: 0,
  BOTTOMRIGHT: +1,
  LEFTTOP: -1,
  LEFT: 0,
  LEFTBOTTOM: +1
}; // @vue/component

var BVPopper = /*#__PURE__*/Vue.extend({
  name: NAME_POPPER,
  props: {
    target: {
      // Element that the tooltip/popover is positioned relative to
      type: [HTMLElement, SVGElement] // default: null

    },
    placement: {
      type: String,
      default: 'top'
    },
    fallbackPlacement: {
      type: [String, Array],
      default: 'flip'
    },
    offset: {
      type: Number,
      default: 0
    },
    boundary: {
      // 'scrollParent', 'viewport', 'window', or Element
      type: [String, HTMLElement],
      default: 'scrollParent'
    },
    boundaryPadding: {
      // Tooltip/popover will try and stay away from
      // boundary edge by this many pixels
      type: Number,
      default: 5
    },
    arrowPadding: {
      // The minimum distance (in `px`) from the edge of the
      // tooltip/popover that the arrow can be positioned
      type: Number,
      default: 6
    }
  },
  data: function data() {
    return {
      // reactive props set by parent
      noFade: false,
      // State related data
      localShow: true,
      attachment: this.getAttachment(this.placement)
    };
  },
  computed: {
    /* istanbul ignore next */
    templateType: function templateType()
    /* istanbul ignore next */
    {
      // Overridden by template component
      return 'unknown';
    },
    popperConfig: function popperConfig() {
      var _this = this;

      var placement = this.placement;
      return {
        placement: this.getAttachment(placement),
        modifiers: {
          offset: {
            offset: this.getOffset(placement)
          },
          flip: {
            behavior: this.fallbackPlacement
          },
          // `arrow.element` can also be a reference to an HTML Element
          // maybe we should make this a `$ref` in the templates?
          arrow: {
            element: '.arrow'
          },
          preventOverflow: {
            padding: this.boundaryPadding,
            boundariesElement: this.boundary
          }
        },
        onCreate: function onCreate(data) {
          // Handle flipping arrow classes
          if (data.originalPlacement !== data.placement) {
            /* istanbul ignore next: can't test in JSDOM */
            _this.popperPlacementChange(data);
          }
        },
        onUpdate: function onUpdate(data) {
          // Handle flipping arrow classes
          _this.popperPlacementChange(data);
        }
      };
    }
  },
  created: function created() {
    var _this2 = this;

    // Note: We are created on-demand, and should be guaranteed that
    // DOM is rendered/ready by the time the created hook runs
    this.$_popper = null; // Ensure we show as we mount

    this.localShow = true; // Create popper instance before shown

    this.$on('show', function (el) {
      _this2.popperCreate(el);
    }); // Self destruct handler

    var handleDestroy = function handleDestroy() {
      _this2.$nextTick(function () {
        // In a `requestAF()` to release control back to application
        requestAF(function () {
          _this2.$destroy();
        });
      });
    }; // Self destruct if parent destroyed


    this.$parent.$once('hook:destroyed', handleDestroy); // Self destruct after hidden

    this.$once('hidden', handleDestroy);
  },
  beforeMount: function beforeMount() {
    // Ensure that the attachment position is correct before mounting
    // as our propsData is added after `new Template({...})`
    this.attachment = this.getAttachment(this.placement);
  },
  mounted: function mounted() {// TBD
  },
  updated: function updated() {
    // Update popper if needed
    // TODO: Should this be a watcher on `this.popperConfig` instead?
    this.updatePopper();
  },
  beforeDestroy: function beforeDestroy() {
    this.destroyPopper();
  },
  destroyed: function destroyed() {
    // Make sure template is removed from DOM
    var el = this.$el;
    el && el.parentNode && el.parentNode.removeChild(el);
  },
  methods: {
    // "Public" method to trigger hide template
    hide: function hide() {
      this.localShow = false;
    },
    // Private
    getAttachment: function getAttachment(placement) {
      return AttachmentMap[String(placement).toUpperCase()] || 'auto';
    },
    getOffset: function getOffset(placement) {
      if (!this.offset) {
        // Could set a ref for the arrow element
        var arrow = this.$refs.arrow || select('.arrow', this.$el);
        var arrowOffset = toFloat(getCS(arrow).width, 0) + toFloat(this.arrowPadding, 0);

        switch (OffsetMap[String(placement).toUpperCase()] || 0) {
          /* istanbul ignore next: can't test in JSDOM */
          case +1:
            /* istanbul ignore next: can't test in JSDOM */
            return "+50%p - ".concat(arrowOffset, "px");

          /* istanbul ignore next: can't test in JSDOM */

          case -1:
            /* istanbul ignore next: can't test in JSDOM */
            return "-50%p + ".concat(arrowOffset, "px");

          default:
            return 0;
        }
      }
      /* istanbul ignore next */


      return this.offset;
    },
    popperCreate: function popperCreate(el) {
      this.destroyPopper(); // We use `el` rather than `this.$el` just in case the original
      // mountpoint root element type was changed by the template

      this.$_popper = new Popper(this.target, el, this.popperConfig);
    },
    destroyPopper: function destroyPopper() {
      this.$_popper && this.$_popper.destroy();
      this.$_popper = null;
    },
    updatePopper: function updatePopper() {
      this.$_popper && this.$_popper.scheduleUpdate();
    },
    popperPlacementChange: function popperPlacementChange(data) {
      // Callback used by popper to adjust the arrow placement
      this.attachment = this.getAttachment(data.placement);
    },

    /* istanbul ignore next */
    renderTemplate: function renderTemplate(h)
    /* istanbul ignore next */
    {
      // Will be overridden by templates
      return h('div');
    }
  },
  render: function render(h) {
    var _this3 = this;

    // Note: `show` and 'fade' classes are only appled during transition
    return h(BVTransition, {
      // Transitions as soon as mounted
      props: {
        appear: true,
        noFade: this.noFade
      },
      on: {
        // Events used by parent component/instance
        beforeEnter: function beforeEnter(el) {
          return _this3.$emit('show', el);
        },
        afterEnter: function afterEnter(el) {
          return _this3.$emit('shown', el);
        },
        beforeLeave: function beforeLeave(el) {
          return _this3.$emit('hide', el);
        },
        afterLeave: function afterLeave(el) {
          return _this3.$emit('hidden', el);
        }
      }
    }, [this.localShow ? this.renderTemplate(h) : h()]);
  }
});

function ownKeys$u(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$u(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$u(Object(source), true).forEach(function (key) { _defineProperty$F(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$u(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$F(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BVTooltipTemplate = /*#__PURE__*/Vue.extend({
  name: NAME_TOOLTIP_TEMPLATE,
  extends: BVPopper,
  mixins: [scopedStyleAttrsMixin],
  props: {
    // Other non-reactive (while open) props are pulled in from BVPopper
    id: {
      type: String // default: null

    },
    html: {
      // Used only by the directive versions
      type: Boolean // default: false

    }
  },
  data: function data() {
    // We use data, rather than props to ensure reactivity
    // Parent component will directly set this data
    return {
      title: '',
      content: '',
      variant: null,
      customClass: null,
      interactive: true
    };
  },
  computed: {
    templateType: function templateType() {
      return 'tooltip';
    },
    templateClasses: function templateClasses() {
      var _ref;

      return [(_ref = {
        // Disables pointer events to hide the tooltip when the user
        // hovers over its content
        noninteractive: !this.interactive
      }, _defineProperty$F(_ref, "b-".concat(this.templateType, "-").concat(this.variant), this.variant), _defineProperty$F(_ref, "bs-".concat(this.templateType, "-").concat(this.attachment), this.attachment), _ref), this.customClass];
    },
    templateAttributes: function templateAttributes() {
      return _objectSpread$u({
        id: this.id,
        role: 'tooltip',
        tabindex: '-1'
      }, this.scopedStyleAttrs);
    },
    templateListeners: function templateListeners() {
      var _this = this;

      // Used for hover/focus trigger listeners
      return {
        mouseenter
        /* istanbul ignore next */
        : function mouseenter(evt) {
          /* istanbul ignore next: difficult to test in JSDOM */
          _this.$emit('mouseenter', evt);
        },
        mouseleave
        /* istanbul ignore next */
        : function mouseleave(evt) {
          /* istanbul ignore next: difficult to test in JSDOM */
          _this.$emit('mouseleave', evt);
        },
        focusin
        /* istanbul ignore next */
        : function focusin(evt) {
          /* istanbul ignore next: difficult to test in JSDOM */
          _this.$emit('focusin', evt);
        },
        focusout
        /* istanbul ignore next */
        : function focusout(evt) {
          /* istanbul ignore next: difficult to test in JSDOM */
          _this.$emit('focusout', evt);
        }
      };
    }
  },
  methods: {
    renderTemplate: function renderTemplate(h) {
      // Title can be a scoped slot function
      var $title = isFunction$1(this.title) ? this.title({}) : isUndefinedOrNull(this.title) ?
      /* istanbul ignore next */
      h() : this.title; // Directive versions only

      var domProps = this.html && !isFunction$1(this.title) ? {
        innerHTML: this.title
      } : {};
      return h('div', {
        staticClass: 'tooltip b-tooltip',
        class: this.templateClasses,
        attrs: this.templateAttributes,
        on: this.templateListeners
      }, [h('div', {
        ref: 'arrow',
        staticClass: 'arrow'
      }), h('div', {
        staticClass: 'tooltip-inner',
        domProps: domProps
      }, [$title])]);
    }
  }
});

function ownKeys$v(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$v(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$v(Object(source), true).forEach(function (key) { _defineProperty$G(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$v(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$G(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var MODAL_SELECTOR = '.modal-content'; // Modal `$root` hidden event

var MODAL_CLOSE_EVENT = 'bv::modal::hidden'; // Sidebar container selector for appending tooltip/popover

var SIDEBAR_SELECTOR = '.b-sidebar'; // For finding the container to append to

var CONTAINER_SELECTOR = [MODAL_SELECTOR, SIDEBAR_SELECTOR].join(', '); // For dropdown sniffing

var DROPDOWN_CLASS = 'dropdown';
var DROPDOWN_OPEN_SELECTOR = '.dropdown-menu.show'; // Data attribute to temporary store the `title` attribute's value

var DATA_TITLE_ATTR = 'data-original-title'; // Data specific to popper and template
// We don't use props, as we need reactivity (we can't pass reactive props)

var templateData = {
  // Text string or Scoped slot function
  title: '',
  // Text string or Scoped slot function
  content: '',
  // String
  variant: null,
  // String, Array, Object
  customClass: null,
  // String or array of Strings (overwritten by BVPopper)
  triggers: '',
  // String (overwritten by BVPopper)
  placement: 'auto',
  // String or array of strings
  fallbackPlacement: 'flip',
  // Element or Component reference (or function that returns element) of
  // the element that will have the trigger events bound, and is also
  // default element for positioning
  target: null,
  // HTML ID, Element or Component reference
  container: null,
  // 'body'
  // Boolean
  noFade: false,
  // 'scrollParent', 'viewport', 'window', Element, or Component reference
  boundary: 'scrollParent',
  // Tooltip/popover will try and stay away from
  // boundary edge by this many pixels (Number)
  boundaryPadding: 5,
  // Arrow offset (Number)
  offset: 0,
  // Hover/focus delay (Number or Object)
  delay: 0,
  // Arrow of Tooltip/popover will try and stay away from
  // the edge of tooltip/popover edge by this many pixels
  arrowPadding: 6,
  // Interactive state (Boolean)
  interactive: true,
  // Disabled state (Boolean)
  disabled: false,
  // ID to use for tooltip/popover
  id: null,
  // Flag used by directives only, for HTML content
  html: false
}; // @vue/component

var BVTooltip = /*#__PURE__*/Vue.extend({
  name: NAME_TOOLTIP_HELPER,
  props: {// None
  },
  data: function data() {
    return _objectSpread$v(_objectSpread$v({}, templateData), {}, {
      // State management data
      activeTrigger: {
        // manual: false,
        hover: false,
        click: false,
        focus: false
      },
      localShow: false
    });
  },
  computed: {
    templateType: function templateType() {
      // Overwritten by BVPopover
      return 'tooltip';
    },
    computedId: function computedId() {
      return this.id || "__bv_".concat(this.templateType, "_").concat(this._uid, "__");
    },
    computedDelay: function computedDelay() {
      // Normalizes delay into object form
      var delay = {
        show: 0,
        hide: 0
      };

      if (isPlainObject$1(this.delay)) {
        delay.show = mathMax(toInteger(this.delay.show, 0), 0);
        delay.hide = mathMax(toInteger(this.delay.hide, 0), 0);
      } else if (isNumber(this.delay) || isString(this.delay)) {
        delay.show = delay.hide = mathMax(toInteger(this.delay, 0), 0);
      }

      return delay;
    },
    computedTriggers: function computedTriggers() {
      // Returns the triggers in sorted array form
      // TODO: Switch this to object form for easier lookup
      return concat(this.triggers).filter(Boolean).join(' ').trim().toLowerCase().split(/\s+/).sort();
    },
    isWithActiveTrigger: function isWithActiveTrigger() {
      for (var trigger in this.activeTrigger) {
        if (this.activeTrigger[trigger]) {
          return true;
        }
      }

      return false;
    },
    computedTemplateData: function computedTemplateData() {
      return {
        title: this.title,
        content: this.content,
        variant: this.variant,
        customClass: this.customClass,
        noFade: this.noFade,
        interactive: this.interactive
      };
    }
  },
  watch: {
    computedTriggers: function computedTriggers(newTriggers, oldTriggers) {
      var _this = this;

      // Triggers have changed, so re-register them

      /* istanbul ignore next */
      if (!looseEqual(newTriggers, oldTriggers)) {
        this.$nextTick(function () {
          // Disable trigger listeners
          _this.unListen(); // Clear any active triggers that are no longer in the list of triggers


          oldTriggers.forEach(function (trigger) {
            if (!arrayIncludes(newTriggers, trigger)) {
              if (_this.activeTrigger[trigger]) {
                _this.activeTrigger[trigger] = false;
              }
            }
          }); // Re-enable the trigger listeners

          _this.listen();
        });
      }
    },
    computedTemplateData: function computedTemplateData() {
      // If any of the while open reactive "props" change,
      // ensure that the template updates accordingly
      this.handleTemplateUpdate();
    },
    title: function title(newValue, oldValue) {
      // Make sure to hide the tooltip when the title is set empty
      if (newValue !== oldValue && !newValue) {
        this.hide();
      }
    },
    disabled: function disabled(newValue) {
      if (newValue) {
        this.disable();
      } else {
        this.enable();
      }
    }
  },
  created: function created() {
    var _this2 = this;

    // Create non-reactive properties
    this.$_tip = null;
    this.$_hoverTimeout = null;
    this.$_hoverState = '';
    this.$_visibleInterval = null;
    this.$_enabled = !this.disabled;
    this.$_noop = noop.bind(this); // Destroy ourselves when the parent is destroyed

    if (this.$parent) {
      this.$parent.$once('hook:beforeDestroy', function () {
        _this2.$nextTick(function () {
          // In a `requestAF()` to release control back to application
          requestAF(function () {
            _this2.$destroy();
          });
        });
      });
    }

    this.$nextTick(function () {
      var target = _this2.getTarget();

      if (target && contains(document.body, target)) {
        // Copy the parent's scoped style attribute
        _this2.scopeId = getScopeId(_this2.$parent); // Set up all trigger handlers and listeners

        _this2.listen();
      } else {
        /* istanbul ignore next */
        warn(isString(_this2.target) ? "Unable to find target element by ID \"#".concat(_this2.target, "\" in document.") : 'The provided target is no valid HTML element.', _this2.templateType);
      }
    });
  },

  /* istanbul ignore next */
  updated: function updated()
  /* istanbul ignore next */
  {
    // Usually called when the slots/data changes
    this.$nextTick(this.handleTemplateUpdate);
  },

  /* istanbul ignore next */
  deactivated: function deactivated()
  /* istanbul ignore next */
  {
    // In a keepalive that has been deactivated, so hide
    // the tooltip/popover if it is showing
    this.forceHide();
  },
  beforeDestroy: function beforeDestroy() {
    // Remove all handler/listeners
    this.unListen();
    this.setWhileOpenListeners(false); // Clear any timeouts/intervals

    this.clearHoverTimeout();
    this.clearVisibilityInterval(); // Destroy the template

    this.destroyTemplate(); // Remove any other private properties created during create

    this.$_noop = null;
  },
  methods: {
    // --- Methods for creating and destroying the template ---
    getTemplate: function getTemplate() {
      // Overridden by BVPopover
      return BVTooltipTemplate;
    },
    updateData: function updateData() {
      var _this3 = this;

      var data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      // Method for updating popper/template data
      // We only update data if it exists, and has not changed
      var titleUpdated = false;
      keys$1(templateData).forEach(function (prop) {
        if (!isUndefined(data[prop]) && _this3[prop] !== data[prop]) {
          _this3[prop] = data[prop];

          if (prop === 'title') {
            titleUpdated = true;
          }
        }
      }); // If the title has updated, we may need to handle the `title`
      // attribute on the trigger target
      // We only do this while the template is open

      if (titleUpdated && this.localShow) {
        this.fixTitle();
      }
    },
    createTemplateAndShow: function createTemplateAndShow() {
      // Creates the template instance and show it
      var container = this.getContainer();
      var Template = this.getTemplate();
      var $tip = this.$_tip = new Template({
        parent: this,
        // The following is not reactive to changes in the props data
        propsData: {
          // These values cannot be changed while template is showing
          id: this.computedId,
          html: this.html,
          placement: this.placement,
          fallbackPlacement: this.fallbackPlacement,
          target: this.getPlacementTarget(),
          boundary: this.getBoundary(),
          // Ensure the following are integers
          offset: toInteger(this.offset, 0),
          arrowPadding: toInteger(this.arrowPadding, 0),
          boundaryPadding: toInteger(this.boundaryPadding, 0)
        }
      }); // We set the initial reactive data (values that can be changed while open)

      this.handleTemplateUpdate(); // Template transition phase events (handled once only)
      // When the template has mounted, but not visibly shown yet

      $tip.$once('show', this.onTemplateShow); // When the template has completed showing

      $tip.$once('shown', this.onTemplateShown); // When the template has started to hide

      $tip.$once('hide', this.onTemplateHide); // When the template has completed hiding

      $tip.$once('hidden', this.onTemplateHidden); // When the template gets destroyed for any reason

      $tip.$once('hook:destroyed', this.destroyTemplate); // Convenience events from template
      // To save us from manually adding/removing DOM
      // listeners to tip element when it is open

      $tip.$on('focusin', this.handleEvent);
      $tip.$on('focusout', this.handleEvent);
      $tip.$on('mouseenter', this.handleEvent);
      $tip.$on('mouseleave', this.handleEvent); // Mount (which triggers the `show`)

      $tip.$mount(container.appendChild(document.createElement('div'))); // Template will automatically remove its markup from DOM when hidden
    },
    hideTemplate: function hideTemplate() {
      // Trigger the template to start hiding
      // The template will emit the `hide` event after this and
      // then emit the `hidden` event once it is fully hidden
      // The `hook:destroyed` will also be called (safety measure)
      this.$_tip && this.$_tip.hide(); // Clear out any stragging active triggers

      this.clearActiveTriggers(); // Reset the hover state

      this.$_hoverState = '';
    },
    // Destroy the template instance and reset state
    destroyTemplate: function destroyTemplate() {
      this.setWhileOpenListeners(false);
      this.clearHoverTimeout();
      this.$_hoverState = '';
      this.clearActiveTriggers();
      this.localPlacementTarget = null;

      try {
        this.$_tip.$destroy();
      } catch (_unused) {}

      this.$_tip = null;
      this.removeAriaDescribedby();
      this.restoreTitle();
      this.localShow = false;
    },
    getTemplateElement: function getTemplateElement() {
      return this.$_tip ? this.$_tip.$el : null;
    },
    handleTemplateUpdate: function handleTemplateUpdate() {
      var _this4 = this;

      // Update our template title/content "props"
      // So that the template updates accordingly
      var $tip = this.$_tip;

      if ($tip) {
        var props = ['title', 'content', 'variant', 'customClass', 'noFade', 'interactive']; // Only update the values if they have changed

        props.forEach(function (prop) {
          if ($tip[prop] !== _this4[prop]) {
            $tip[prop] = _this4[prop];
          }
        });
      }
    },
    // --- Show/Hide handlers ---
    // Show the tooltip
    show: function show() {
      var target = this.getTarget();

      if (!target || !contains(document.body, target) || !isVisible(target) || this.dropdownOpen() || (isUndefinedOrNull(this.title) || this.title === '') && (isUndefinedOrNull(this.content) || this.content === '')) {
        // If trigger element isn't in the DOM or is not visible, or
        // is on an open dropdown toggle, or has no content, then
        // we exit without showing
        return;
      } // If tip already exists, exit early


      if (this.$_tip || this.localShow) {
        /* istanbul ignore next */
        return;
      } // In the process of showing


      this.localShow = true; // Create a cancelable BvEvent

      var showEvt = this.buildEvent('show', {
        cancelable: true
      });
      this.emitEvent(showEvt); // Don't show if event cancelled

      /* istanbul ignore if */

      if (showEvt.defaultPrevented) {
        // Destroy the template (if for some reason it was created)
        this.destroyTemplate();
        return;
      } // Fix the title attribute on target


      this.fixTitle(); // Set aria-describedby on target

      this.addAriaDescribedby(); // Create and show the tooltip

      this.createTemplateAndShow();
    },
    hide: function hide() {
      var force = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      // Hide the tooltip
      var tip = this.getTemplateElement();
      /* istanbul ignore if */

      if (!tip || !this.localShow) {
        this.restoreTitle();
        return;
      } // Emit cancelable BvEvent 'hide'
      // We disable cancelling if `force` is true


      var hideEvt = this.buildEvent('hide', {
        cancelable: !force
      });
      this.emitEvent(hideEvt);
      /* istanbul ignore if: ignore for now */

      if (hideEvt.defaultPrevented) {
        // Don't hide if event cancelled
        return;
      } // Tell the template to hide


      this.hideTemplate();
    },
    forceHide: function forceHide() {
      // Forcefully hides/destroys the template, regardless of any active triggers
      var tip = this.getTemplateElement();

      if (!tip || !this.localShow) {
        /* istanbul ignore next */
        return;
      } // Disable while open listeners/watchers
      // This is also done in the template `hide` evt handler


      this.setWhileOpenListeners(false); // Clear any hover enter/leave event

      this.clearHoverTimeout();
      this.$_hoverState = '';
      this.clearActiveTriggers(); // Disable the fade animation on the template

      if (this.$_tip) {
        this.$_tip.noFade = true;
      } // Hide the tip (with force = true)


      this.hide(true);
    },
    enable: function enable() {
      this.$_enabled = true; // Create a non-cancelable BvEvent

      this.emitEvent(this.buildEvent('enabled'));
    },
    disable: function disable() {
      this.$_enabled = false; // Create a non-cancelable BvEvent

      this.emitEvent(this.buildEvent('disabled'));
    },
    // --- Handlers for template events ---
    // When template is inserted into DOM, but not yet shown
    onTemplateShow: function onTemplateShow() {
      // Enable while open listeners/watchers
      this.setWhileOpenListeners(true);
    },
    // When template show transition completes
    onTemplateShown: function onTemplateShown() {
      var prevHoverState = this.$_hoverState;
      this.$_hoverState = '';
      /* istanbul ignore next: occasional Node 10 coverage error */

      if (prevHoverState === 'out') {
        this.leave(null);
      } // Emit a non-cancelable BvEvent 'shown'


      this.emitEvent(this.buildEvent('shown'));
    },
    // When template is starting to hide
    onTemplateHide: function onTemplateHide() {
      // Disable while open listeners/watchers
      this.setWhileOpenListeners(false);
    },
    // When template has completed closing (just before it self destructs)
    onTemplateHidden: function onTemplateHidden() {
      // Destroy the template
      this.destroyTemplate(); // Emit a non-cancelable BvEvent 'shown'

      this.emitEvent(this.buildEvent('hidden'));
    },
    // --- Utility methods ---
    getTarget: function getTarget() {
      var target = this.target;

      if (isString(target)) {
        target = getById(target.replace(/^#/, ''));
      } else if (isFunction$1(target)) {
        target = target();
      } else if (target) {
        target = target.$el || target;
      }

      return isElement(target) ? target : null;
    },
    getPlacementTarget: function getPlacementTarget() {
      // This is the target that the tooltip will be placed on, which may not
      // necessarily be the same element that has the trigger event listeners
      // For now, this is the same as target
      // TODO:
      //   Add in child selector support
      //   Add in visibility checks for this element
      //   Fallback to target if not found
      return this.getTarget();
    },
    getTargetId: function getTargetId() {
      // Returns the ID of the trigger element
      var target = this.getTarget();
      return target && target.id ? target.id : null;
    },
    getContainer: function getContainer() {
      // Handle case where container may be a component ref
      var container = this.container ? this.container.$el || this.container : false;
      var body = document.body;
      var target = this.getTarget(); // If we are in a modal, we append to the modal, If we
      // are in a sidebar, we append to the sidebar, else append
      // to body, unless a container is specified
      // TODO:
      //   Template should periodically check to see if it is in dom
      //   And if not, self destruct (if container got v-if'ed out of DOM)
      //   Or this could possibly be part of the visibility check

      return container === false ? closest(CONTAINER_SELECTOR, target) || body :
      /*istanbul ignore next */
      isString(container) ?
      /*istanbul ignore next */
      getById(container.replace(/^#/, '')) || body :
      /*istanbul ignore next */
      body;
    },
    getBoundary: function getBoundary() {
      return this.boundary ? this.boundary.$el || this.boundary : 'scrollParent';
    },
    isInModal: function isInModal() {
      var target = this.getTarget();
      return target && closest(MODAL_SELECTOR, target);
    },
    isDropdown: function isDropdown() {
      // Returns true if trigger is a dropdown
      var target = this.getTarget();
      return target && hasClass(target, DROPDOWN_CLASS);
    },
    dropdownOpen: function dropdownOpen() {
      // Returns true if trigger is a dropdown and the dropdown menu is open
      var target = this.getTarget();
      return this.isDropdown() && target && select(DROPDOWN_OPEN_SELECTOR, target);
    },
    clearHoverTimeout: function clearHoverTimeout() {
      clearTimeout(this.$_hoverTimeout);
      this.$_hoverTimeout = null;
    },
    clearVisibilityInterval: function clearVisibilityInterval() {
      clearInterval(this.$_visibleInterval);
      this.$_visibleInterval = null;
    },
    clearActiveTriggers: function clearActiveTriggers() {
      for (var trigger in this.activeTrigger) {
        this.activeTrigger[trigger] = false;
      }
    },
    addAriaDescribedby: function addAriaDescribedby() {
      // Add aria-describedby on trigger element, without removing any other IDs
      var target = this.getTarget();
      var desc = getAttr(target, 'aria-describedby') || '';
      desc = desc.split(/\s+/).concat(this.computedId).join(' ').trim(); // Update/add aria-described by

      setAttr(target, 'aria-describedby', desc);
    },
    removeAriaDescribedby: function removeAriaDescribedby() {
      var _this5 = this;

      // Remove aria-describedby on trigger element, without removing any other IDs
      var target = this.getTarget();
      var desc = getAttr(target, 'aria-describedby') || '';
      desc = desc.split(/\s+/).filter(function (d) {
        return d !== _this5.computedId;
      }).join(' ').trim(); // Update or remove aria-describedby

      if (desc) {
        /* istanbul ignore next */
        setAttr(target, 'aria-describedby', desc);
      } else {
        removeAttr(target, 'aria-describedby');
      }
    },
    fixTitle: function fixTitle() {
      // If the target has a `title` attribute,
      // remove it and store it on a data attribute
      var target = this.getTarget();

      if (hasAttr(target, 'title')) {
        // Get `title` attribute value and remove it from target
        var title = getAttr(target, 'title');
        setAttr(target, 'title', ''); // Only set the data attribute when the value is truthy

        if (title) {
          setAttr(target, DATA_TITLE_ATTR, title);
        }
      }
    },
    restoreTitle: function restoreTitle() {
      // If the target had a `title` attribute,
      // restore it and remove the data attribute
      var target = this.getTarget();

      if (hasAttr(target, DATA_TITLE_ATTR)) {
        // Get data attribute value and remove it from target
        var title = getAttr(target, DATA_TITLE_ATTR);
        removeAttr(target, DATA_TITLE_ATTR); // Only restore the `title` attribute when the value is truthy

        if (title) {
          setAttr(target, 'title', title);
        }
      }
    },
    // --- BvEvent helpers ---
    buildEvent: function buildEvent(type) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      // Defaults to a non-cancellable event
      return new BvEvent(type, _objectSpread$v({
        cancelable: false,
        target: this.getTarget(),
        relatedTarget: this.getTemplateElement() || null,
        componentId: this.computedId,
        vueTarget: this
      }, options));
    },
    emitEvent: function emitEvent(bvEvt) {
      // Emits a BvEvent on $root and this instance
      var evtName = bvEvt.type;
      var $root = this.$root;

      if ($root && $root.$emit) {
        // Emit an event on $root
        $root.$emit("bv::".concat(this.templateType, "::").concat(evtName), bvEvt);
      }

      this.$emit(evtName, bvEvt);
    },
    // --- Event handler setup methods ---
    listen: function listen() {
      var _this6 = this;

      // Enable trigger event handlers
      var el = this.getTarget();

      if (!el) {
        /* istanbul ignore next */
        return;
      } // Listen for global show/hide events


      this.setRootListener(true); // Set up our listeners on the target trigger element

      this.computedTriggers.forEach(function (trigger) {
        if (trigger === 'click') {
          eventOn(el, 'click', _this6.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
        } else if (trigger === 'focus') {
          eventOn(el, 'focusin', _this6.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
          eventOn(el, 'focusout', _this6.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
        } else if (trigger === 'blur') {
          // Used to close $tip when element looses focus

          /* istanbul ignore next */
          eventOn(el, 'focusout', _this6.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
        } else if (trigger === 'hover') {
          eventOn(el, 'mouseenter', _this6.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
          eventOn(el, 'mouseleave', _this6.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
        }
      }, this);
    },
    unListen: function unListen()
    /* istanbul ignore next */
    {
      var _this7 = this;

      // Remove trigger event handlers
      var events = ['click', 'focusin', 'focusout', 'mouseenter', 'mouseleave'];
      var target = this.getTarget(); // Stop listening for global show/hide/enable/disable events

      this.setRootListener(false); // Clear out any active target listeners

      events.forEach(function (evt) {
        target && eventOff(target, evt, _this7.handleEvent, EVENT_OPTIONS_NO_CAPTURE);
      }, this);
    },
    setRootListener: function setRootListener(on) {
      // Listen for global `bv::{hide|show}::{tooltip|popover}` hide request event
      var $root = this.$root;

      if ($root) {
        var method = on ? '$on' : '$off';
        var type = this.templateType;
        $root[method]("bv::hide::".concat(type), this.doHide);
        $root[method]("bv::show::".concat(type), this.doShow);
        $root[method]("bv::disable::".concat(type), this.doDisable);
        $root[method]("bv::enable::".concat(type), this.doEnable);
      }
    },
    setWhileOpenListeners: function setWhileOpenListeners(on) {
      // Events that are only registered when the template is showing
      // Modal close events
      this.setModalListener(on); // Dropdown open events (if we are attached to a dropdown)

      this.setDropdownListener(on); // Periodic $element visibility check
      // For handling when tip target is in <keepalive>, tabs, carousel, etc

      this.visibleCheck(on); // On-touch start listeners

      this.setOnTouchStartListener(on);
    },
    // Handler for periodic visibility check
    visibleCheck: function visibleCheck(on) {
      var _this8 = this;

      this.clearVisibilityInterval();
      var target = this.getTarget();
      var tip = this.getTemplateElement();

      if (on) {
        this.$_visibleInterval = setInterval(function () {
          if (tip && _this8.localShow && (!target.parentNode || !isVisible(target))) {
            // Target element is no longer visible or not in DOM, so force-hide the tooltip
            _this8.forceHide();
          }
        }, 100);
      }
    },
    setModalListener: function setModalListener(on) {
      // Handle case where tooltip/target is in a modal
      if (this.isInModal()) {
        // We can listen for modal hidden events on `$root`
        this.$root[on ? '$on' : '$off'](MODAL_CLOSE_EVENT, this.forceHide);
      }
    },
    setOnTouchStartListener: function setOnTouchStartListener(on)
    /* istanbul ignore next: JSDOM doesn't support `ontouchstart` */
    {
      var _this9 = this;

      // If this is a touch-enabled device we add extra empty
      // `mouseover` listeners to the body's immediate children
      // Only needed because of broken event delegation on iOS
      // https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
      if ('ontouchstart' in document.documentElement) {
        from(document.body.children).forEach(function (el) {
          eventOnOff(on, el, 'mouseover', _this9.$_noop);
        });
      }
    },
    setDropdownListener: function setDropdownListener(on) {
      var target = this.getTarget();

      if (!target || !this.$root || !this.isDropdown) {
        return;
      } // We can listen for dropdown shown events on its instance
      // TODO:
      //   We could grab the ID from the dropdown, and listen for
      //   $root events for that particular dropdown id
      //   Dropdown shown and hidden events will need to emit
      //   Note: Dropdown auto-ID happens in a `$nextTick()` after mount
      //         So the ID lookup would need to be done in a `$nextTick()`


      if (target.__vue__) {
        target.__vue__[on ? '$on' : '$off']('shown', this.forceHide);
      }
    },
    // --- Event handlers ---
    handleEvent: function handleEvent(evt) {
      // General trigger event handler
      // target is the trigger element
      var target = this.getTarget();

      if (!target || isDisabled(target) || !this.$_enabled || this.dropdownOpen()) {
        // If disabled or not enabled, or if a dropdown that is open, don't do anything
        // If tip is shown before element gets disabled, then tip will not
        // close until no longer disabled or forcefully closed
        return;
      }

      var type = evt.type;
      var triggers = this.computedTriggers;

      if (type === 'click' && arrayIncludes(triggers, 'click')) {
        this.click(evt);
      } else if (type === 'mouseenter' && arrayIncludes(triggers, 'hover')) {
        // `mouseenter` is a non-bubbling event
        this.enter(evt);
      } else if (type === 'focusin' && arrayIncludes(triggers, 'focus')) {
        // `focusin` is a bubbling event
        // `evt` includes `relatedTarget` (element losing focus)
        this.enter(evt);
      } else if (type === 'focusout' && (arrayIncludes(triggers, 'focus') || arrayIncludes(triggers, 'blur')) || type === 'mouseleave' && arrayIncludes(triggers, 'hover')) {
        // `focusout` is a bubbling event
        // `mouseleave` is a non-bubbling event
        // `tip` is the template (will be null if not open)
        var tip = this.getTemplateElement(); // `evtTarget` is the element which is losing focus/hover and

        var evtTarget = evt.target; // `relatedTarget` is the element gaining focus/hover

        var relatedTarget = evt.relatedTarget;
        /* istanbul ignore next */

        if ( // From tip to target
        tip && contains(tip, evtTarget) && contains(target, relatedTarget) || // From target to tip
        tip && contains(target, evtTarget) && contains(tip, relatedTarget) || // Within tip
        tip && contains(tip, evtTarget) && contains(tip, relatedTarget) || // Within target
        contains(target, evtTarget) && contains(target, relatedTarget)) {
          // If focus/hover moves within `tip` and `target`, don't trigger a leave
          return;
        } // Otherwise trigger a leave


        this.leave(evt);
      }
    },
    doHide: function doHide(id) {
      // Programmatically hide tooltip or popover
      if (!id || this.getTargetId() === id || this.computedId === id) {
        // Close all tooltips or popovers, or this specific tip (with ID)
        this.forceHide();
      }
    },
    doShow: function doShow(id) {
      // Programmatically show tooltip or popover
      if (!id || this.getTargetId() === id || this.computedId === id) {
        // Open all tooltips or popovers, or this specific tip (with ID)
        this.show();
      }
    },

    /*istanbul ignore next: ignore for now */
    doDisable: function doDisable(id)
    /*istanbul ignore next: ignore for now */
    {
      // Programmatically disable tooltip or popover
      if (!id || this.getTargetId() === id || this.computedId === id) {
        // Disable all tooltips or popovers (no ID), or this specific tip (with ID)
        this.disable();
      }
    },

    /*istanbul ignore next: ignore for now */
    doEnable: function doEnable(id)
    /*istanbul ignore next: ignore for now */
    {
      // Programmatically enable tooltip or popover
      if (!id || this.getTargetId() === id || this.computedId === id) {
        // Enable all tooltips or popovers (no ID), or this specific tip (with ID)
        this.enable();
      }
    },
    click: function click(evt) {
      if (!this.$_enabled || this.dropdownOpen()) {
        /* istanbul ignore next */
        return;
      } // Get around a WebKit bug where `click` does not trigger focus events
      // On most browsers, `click` triggers a `focusin`/`focus` event first
      // Needed so that trigger 'click blur' works on iOS
      // https://github.com/bootstrap-vue/bootstrap-vue/issues/5099
      // We use `currentTarget` rather than `target` to trigger on the
      // element, not the inner content


      attemptFocus(evt.currentTarget);
      this.activeTrigger.click = !this.activeTrigger.click;

      if (this.isWithActiveTrigger) {
        this.enter(null);
      } else {
        /* istanbul ignore next */
        this.leave(null);
      }
    },

    /* istanbul ignore next */
    toggle: function toggle()
    /* istanbul ignore next */
    {
      // Manual toggle handler
      if (!this.$_enabled || this.dropdownOpen()) {
        /* istanbul ignore next */
        return;
      } // Should we register as an active trigger?
      // this.activeTrigger.manual = !this.activeTrigger.manual


      if (this.localShow) {
        this.leave(null);
      } else {
        this.enter(null);
      }
    },
    enter: function enter() {
      var _this10 = this;

      var evt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

      // Opening trigger handler
      // Note: Click events are sent with evt === null
      if (evt) {
        this.activeTrigger[evt.type === 'focusin' ? 'focus' : 'hover'] = true;
      }
      /* istanbul ignore next */


      if (this.localShow || this.$_hoverState === 'in') {
        this.$_hoverState = 'in';
        return;
      }

      this.clearHoverTimeout();
      this.$_hoverState = 'in';

      if (!this.computedDelay.show) {
        this.show();
      } else {
        // Hide any title attribute while enter delay is active
        this.fixTitle();
        this.$_hoverTimeout = setTimeout(function () {
          /* istanbul ignore else */
          if (_this10.$_hoverState === 'in') {
            _this10.show();
          } else if (!_this10.localShow) {
            _this10.restoreTitle();
          }
        }, this.computedDelay.show);
      }
    },
    leave: function leave() {
      var _this11 = this;

      var evt = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

      // Closing trigger handler
      // Note: Click events are sent with evt === null
      if (evt) {
        this.activeTrigger[evt.type === 'focusout' ? 'focus' : 'hover'] = false;
        /* istanbul ignore next */

        if (evt.type === 'focusout' && arrayIncludes(this.computedTriggers, 'blur')) {
          // Special case for `blur`: we clear out the other triggers
          this.activeTrigger.click = false;
          this.activeTrigger.hover = false;
        }
      }
      /* istanbul ignore next: ignore for now */


      if (this.isWithActiveTrigger) {
        return;
      }

      this.clearHoverTimeout();
      this.$_hoverState = 'out';

      if (!this.computedDelay.hide) {
        this.hide();
      } else {
        this.$_hoverTimeout = setTimeout(function () {
          if (_this11.$_hoverState === 'out') {
            _this11.hide();
          }
        }, this.computedDelay.hide);
      }
    }
  }
});

var BTooltip = /*#__PURE__*/Vue.extend({
  name: NAME_TOOLTIP,
  props: {
    title: {
      type: String // default: undefined

    },
    // Added in by BPopover
    // content: {
    //   type: String,
    //   default: undefined
    // },
    target: {
      // String ID of element, or element/component reference
      // Or function that returns one of the above
      type: [String, HTMLElement, SVGElement, Function, Object],
      required: true
    },
    triggers: {
      type: [String, Array],
      default: 'hover focus'
    },
    placement: {
      type: String,
      default: 'top'
    },
    fallbackPlacement: {
      type: [String, Array],
      default: 'flip',
      validator: function validator(value) {
        return isArray$1(value) && value.every(function (v) {
          return isString(v);
        }) || arrayIncludes(['flip', 'clockwise', 'counterclockwise'], value);
      }
    },
    variant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_TOOLTIP, 'variant');
      }
    },
    customClass: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_TOOLTIP, 'customClass');
      }
    },
    delay: {
      type: [Number, Object, String],
      default: function _default() {
        return getComponentConfig(NAME_TOOLTIP, 'delay');
      }
    },
    boundary: {
      // String: scrollParent, window, or viewport
      // Element: element reference
      // Object: Vue component
      type: [String, HTMLElement, Object],
      default: function _default() {
        return getComponentConfig(NAME_TOOLTIP, 'boundary');
      }
    },
    boundaryPadding: {
      type: [Number, String],
      default: function _default() {
        return getComponentConfig(NAME_TOOLTIP, 'boundaryPadding');
      }
    },
    offset: {
      type: [Number, String],
      default: 0
    },
    noFade: {
      type: Boolean,
      default: false
    },
    container: {
      // String: HTML ID of container, if null body is used (default)
      // HTMLElement: element reference reference
      // Object: Vue Component
      type: [String, HTMLElement, Object] // default: undefined

    },
    show: {
      type: Boolean,
      default: false
    },
    noninteractive: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    id: {
      // ID to use for tooltip element
      // If not provided on will automatically be generated
      type: String // default: null

    }
  },
  data: function data() {
    return {
      localShow: this.show,
      localTitle: '',
      localContent: ''
    };
  },
  computed: {
    templateData: function templateData() {
      // Data that will be passed to the template and popper
      return {
        // We use massaged versions of the title and content props/slots
        title: this.localTitle,
        content: this.localContent,
        // Pass these props as is
        target: this.target,
        triggers: this.triggers,
        placement: this.placement,
        fallbackPlacement: this.fallbackPlacement,
        variant: this.variant,
        customClass: this.customClass,
        container: this.container,
        boundary: this.boundary,
        boundaryPadding: this.boundaryPadding,
        delay: this.delay,
        offset: this.offset,
        noFade: this.noFade,
        interactive: !this.noninteractive,
        disabled: this.disabled,
        id: this.id
      };
    },
    templateTitleContent: function templateTitleContent() {
      // Used to watch for changes to the title and content props
      return {
        title: this.title,
        content: this.content
      };
    }
  },
  watch: {
    show: function show(_show, oldVal) {
      if (_show !== oldVal && _show !== this.localShow && this.$_toolpop) {
        if (_show) {
          this.$_toolpop.show();
        } else {
          // We use `forceHide()` to override any active triggers
          this.$_toolpop.forceHide();
        }
      }
    },
    disabled: function disabled(newVal) {
      if (newVal) {
        this.doDisable();
      } else {
        this.doEnable();
      }
    },
    localShow: function localShow(newVal) {
      // TODO: May need to be done in a `$nextTick()`
      this.$emit('update:show', newVal);
    },
    templateData: function templateData() {
      var _this = this;

      this.$nextTick(function () {
        if (_this.$_toolpop) {
          _this.$_toolpop.updateData(_this.templateData);
        }
      });
    },
    // Watchers for title/content props (prop changes do not trigger the `updated()` hook)
    templateTitleContent: function templateTitleContent() {
      this.$nextTick(this.updateContent);
    }
  },
  created: function created() {
    // Create private non-reactive props
    this.$_toolpop = null;
  },
  updated: function updated() {
    // Update the `propData` object
    // Done in a `$nextTick()` to ensure slot(s) have updated
    this.$nextTick(this.updateContent);
  },
  beforeDestroy: function beforeDestroy() {
    // Shutdown our local event listeners
    this.$off('open', this.doOpen);
    this.$off('close', this.doClose);
    this.$off('disable', this.doDisable);
    this.$off('enable', this.doEnable); // Destroy the tip instance

    if (this.$_toolpop) {
      this.$_toolpop.$destroy();
      this.$_toolpop = null;
    }
  },
  mounted: function mounted() {
    var _this2 = this;

    // Instantiate a new BVTooltip instance
    // Done in a `$nextTick()` to ensure DOM has completed rendering
    // so that target can be found
    this.$nextTick(function () {
      // Load the on demand child instance
      var Component = _this2.getComponent(); // Ensure we have initial content


      _this2.updateContent(); // Pass down the scoped style attribute if available


      var scopeId = getScopeId(_this2) || getScopeId(_this2.$parent); // Create the instance

      var $toolpop = _this2.$_toolpop = new Component({
        parent: _this2,
        // Pass down the scoped style ID
        _scopeId: scopeId || undefined
      }); // Set the initial data

      $toolpop.updateData(_this2.templateData); // Set listeners

      $toolpop.$on('show', _this2.onShow);
      $toolpop.$on('shown', _this2.onShown);
      $toolpop.$on('hide', _this2.onHide);
      $toolpop.$on('hidden', _this2.onHidden);
      $toolpop.$on('disabled', _this2.onDisabled);
      $toolpop.$on('enabled', _this2.onEnabled); // Initially disabled?

      if (_this2.disabled) {
        // Initially disabled
        _this2.doDisable();
      } // Listen to open signals from others


      _this2.$on('open', _this2.doOpen); // Listen to close signals from others


      _this2.$on('close', _this2.doClose); // Listen to disable signals from others


      _this2.$on('disable', _this2.doDisable); // Listen to enable signals from others


      _this2.$on('enable', _this2.doEnable); // Initially show tooltip?


      if (_this2.localShow) {
        $toolpop.show();
      }
    });
  },
  methods: {
    getComponent: function getComponent() {
      // Overridden by BPopover
      return BVTooltip;
    },
    updateContent: function updateContent() {
      // Overridden by BPopover
      // Tooltip: Default slot is `title`
      // Popover: Default slot is `content`, `title` slot is title
      // We pass a scoped slot function reference by default (Vue v2.6x)
      // And pass the title prop as a fallback
      this.setTitle(this.$scopedSlots.default || this.title);
    },
    // Helper methods for `updateContent()`
    setTitle: function setTitle(val) {
      val = isUndefinedOrNull(val) ? '' : val; // We only update the value if it has changed

      if (this.localTitle !== val) {
        this.localTitle = val;
      }
    },
    setContent: function setContent(val) {
      val = isUndefinedOrNull(val) ? '' : val; // We only update the value if it has changed

      if (this.localContent !== val) {
        this.localContent = val;
      }
    },
    // --- Template event handlers ---
    onShow: function onShow(bvEvt) {
      // Placeholder
      this.$emit('show', bvEvt);

      if (bvEvt) {
        this.localShow = !bvEvt.defaultPrevented;
      }
    },
    onShown: function onShown(bvEvt) {
      // Tip is now showing
      this.localShow = true;
      this.$emit('shown', bvEvt);
    },
    onHide: function onHide(bvEvt) {
      this.$emit('hide', bvEvt);
    },
    onHidden: function onHidden(bvEvt) {
      // Tip is no longer showing
      this.$emit('hidden', bvEvt);
      this.localShow = false;
    },
    onDisabled: function onDisabled(bvEvt) {
      // Prevent possible endless loop if user mistakenly
      // fires `disabled` instead of `disable`
      if (bvEvt && bvEvt.type === 'disabled') {
        this.$emit('update:disabled', true);
        this.$emit('disabled', bvEvt);
      }
    },
    onEnabled: function onEnabled(bvEvt) {
      // Prevent possible endless loop if user mistakenly
      // fires `enabled` instead of `enable`
      if (bvEvt && bvEvt.type === 'enabled') {
        this.$emit('update:disabled', false);
        this.$emit('enabled', bvEvt);
      }
    },
    // --- Local event listeners ---
    doOpen: function doOpen() {
      !this.localShow && this.$_toolpop && this.$_toolpop.show();
    },
    doClose: function doClose() {
      this.localShow && this.$_toolpop && this.$_toolpop.hide();
    },
    doDisable: function doDisable() {
      this.$_toolpop && this.$_toolpop.disable();
    },
    doEnable: function doEnable() {
      this.$_toolpop && this.$_toolpop.enable();
    }
  },
  render: function render(h) {
    // Always renders a comment node
    // TODO:
    //   Future: Possibly render a target slot (single root element)
    //   which we can apply the listeners to (pass `this.$el` to BVTooltip)
    return h();
  }
});

var BVPopoverTemplate = /*#__PURE__*/Vue.extend({
  name: NAME_POPOVER_TEMPLATE,
  extends: BVTooltipTemplate,
  computed: {
    templateType: function templateType() {
      return 'popover';
    }
  },
  methods: {
    renderTemplate: function renderTemplate(h) {
      // Title and content could be a scoped slot function
      var $title = isFunction$1(this.title) ? this.title({}) : this.title;
      var $content = isFunction$1(this.content) ? this.content({}) : this.content; // Directive usage only

      var titleDomProps = this.html && !isFunction$1(this.title) ? {
        innerHTML: this.title
      } : {};
      var contentDomProps = this.html && !isFunction$1(this.content) ? {
        innerHTML: this.content
      } : {};
      return h('div', {
        staticClass: 'popover b-popover',
        class: this.templateClasses,
        attrs: this.templateAttributes,
        on: this.templateListeners
      }, [h('div', {
        ref: 'arrow',
        staticClass: 'arrow'
      }), isUndefinedOrNull($title) || $title === '' ?
      /* istanbul ignore next */
      h() : h('h3', {
        staticClass: 'popover-header',
        domProps: titleDomProps
      }, [$title]), isUndefinedOrNull($content) || $content === '' ?
      /* istanbul ignore next */
      h() : h('div', {
        staticClass: 'popover-body',
        domProps: contentDomProps
      }, [$content])]);
    }
  }
});

// Popover "Class" (Built as a renderless Vue instance)

var BVPopover = /*#__PURE__*/Vue.extend({
  name: NAME_POPOVER_HELPER,
  extends: BVTooltip,
  computed: {
    // Overwrites BVTooltip
    templateType: function templateType() {
      return 'popover';
    }
  },
  methods: {
    getTemplate: function getTemplate() {
      // Overwrites BVTooltip
      return BVPopoverTemplate;
    }
  }
});

var BPopover = /*#__PURE__*/Vue.extend({
  name: NAME_POPOVER,
  extends: BTooltip,
  inheritAttrs: false,
  props: {
    title: {
      type: String // default: undefined

    },
    content: {
      type: String // default: undefined

    },
    triggers: {
      type: [String, Array],
      default: 'click'
    },
    placement: {
      type: String,
      default: 'right'
    },
    variant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_POPOVER, 'variant');
      }
    },
    customClass: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_POPOVER, 'customClass');
      }
    },
    delay: {
      type: [Number, Object, String],
      default: function _default() {
        return getComponentConfig(NAME_POPOVER, 'delay');
      }
    },
    boundary: {
      // String: scrollParent, window, or viewport
      // Element: element reference
      // Object: Vue component
      type: [String, HTMLElement, Object],
      default: function _default() {
        return getComponentConfig(NAME_POPOVER, 'boundary');
      }
    },
    boundaryPadding: {
      type: [Number, String],
      default: function _default() {
        return getComponentConfig(NAME_POPOVER, 'boundaryPadding');
      }
    }
  },
  methods: {
    getComponent: function getComponent() {
      // Overridden by BPopover
      return BVPopover;
    },
    updateContent: function updateContent() {
      // Tooltip: Default slot is `title`
      // Popover: Default slot is `content`, `title` slot is title
      // We pass a scoped slot function references by default (Vue v2.6x)
      // And pass the title prop as a fallback
      this.setContent(this.$scopedSlots.default || this.content);
      this.setTitle(this.$scopedSlots.title || this.title);
    }
  } // Render function provided by BTooltip

});

function ownKeys$w(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$w(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$w(Object(source), true).forEach(function (key) { _defineProperty$H(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$w(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$H(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var CLASS_NAME$1 = 'b-sidebar'; // --- Render methods ---

var renderHeaderTitle = function renderHeaderTitle(h, ctx) {
  // Render a empty `<span>` when to title was provided
  var title = ctx.computedTile;

  if (!title) {
    return h('span');
  }

  return h('strong', {
    attrs: {
      id: ctx.safeId('__title__')
    }
  }, [title]);
};

var renderHeaderClose = function renderHeaderClose(h, ctx) {
  if (ctx.noHeaderClose) {
    return h();
  }

  var closeLabel = ctx.closeLabel,
      textVariant = ctx.textVariant,
      hide = ctx.hide;
  return h(BButtonClose, {
    ref: 'close-button',
    props: {
      ariaLabel: closeLabel,
      textVariant: textVariant
    },
    on: {
      click: hide
    }
  }, [ctx.normalizeSlot('header-close') || h(BIconX)]);
};

var renderHeader = function renderHeader(h, ctx) {
  if (ctx.noHeader) {
    return h();
  }

  var $title = renderHeaderTitle(h, ctx);
  var $close = renderHeaderClose(h, ctx);
  return h('header', {
    key: 'header',
    staticClass: "".concat(CLASS_NAME$1, "-header"),
    class: ctx.headerClass
  }, ctx.right ? [$close, $title] : [$title, $close]);
};

var renderBody = function renderBody(h, ctx) {
  return h('div', {
    key: 'body',
    staticClass: "".concat(CLASS_NAME$1, "-body"),
    class: ctx.bodyClass
  }, [ctx.normalizeSlot(SLOT_NAME_DEFAULT, ctx.slotScope)]);
};

var renderFooter = function renderFooter(h, ctx) {
  var $footer = ctx.normalizeSlot(SLOT_NAME_FOOTER, ctx.slotScope);

  if (!$footer) {
    return h();
  }

  return h('footer', {
    key: 'footer',
    staticClass: "".concat(CLASS_NAME$1, "-footer"),
    class: ctx.footerClass
  }, [$footer]);
};

var renderContent = function renderContent(h, ctx) {
  // We render the header even if `lazy` is enabled as it
  // acts as the accessible label for the sidebar
  var $header = renderHeader(h, ctx);

  if (ctx.lazy && !ctx.isOpen) {
    return $header;
  }

  return [$header, renderBody(h, ctx), renderFooter(h, ctx)];
};

var renderBackdrop = function renderBackdrop(h, ctx) {
  if (!ctx.backdrop) {
    return h();
  }

  var backdropVariant = ctx.backdropVariant;
  return h('div', {
    directives: [{
      name: 'show',
      value: ctx.localShow
    }],
    staticClass: 'b-sidebar-backdrop',
    class: _defineProperty$H({}, "bg-".concat(backdropVariant), !!backdropVariant),
    on: {
      click: ctx.onBackdropClick
    }
  });
}; // --- Main component ---
// @vue/component


var BSidebar = /*#__PURE__*/Vue.extend({
  name: NAME_SIDEBAR,
  // Mixin order is important!
  mixins: [attrsMixin, idMixin, listenOnRootMixin, normalizeSlotMixin],
  inheritAttrs: false,
  model: {
    prop: 'visible',
    event: 'change'
  },
  props: {
    title: {
      type: String // default: null

    },
    right: {
      type: Boolean,
      default: false
    },
    bgVariant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SIDEBAR, 'bgVariant');
      }
    },
    textVariant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SIDEBAR, 'textVariant');
      }
    },
    shadow: {
      type: [Boolean, String],
      default: function _default() {
        return getComponentConfig(NAME_SIDEBAR, 'shadow');
      }
    },
    width: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SIDEBAR, 'width');
      }
    },
    zIndex: {
      type: [Number, String] // default: null

    },
    ariaLabel: {
      type: String // default: null

    },
    ariaLabelledby: {
      type: String // default: null

    },
    closeLabel: {
      // `aria-label` for close button
      // Defaults to 'Close'
      type: String // default: undefined

    },
    tag: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SIDEBAR, 'tag');
      }
    },
    sidebarClass: {
      type: [String, Array, Object] // default: null

    },
    headerClass: {
      type: [String, Array, Object] // default: null

    },
    bodyClass: {
      type: [String, Array, Object] // default: null

    },
    footerClass: {
      type: [String, Array, Object] // default: null

    },
    backdrop: {
      // If `true`, shows a basic backdrop
      type: Boolean,
      default: false
    },
    backdropVariant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SIDEBAR, 'backdropVariant');
      }
    },
    noSlide: {
      type: Boolean,
      default: false
    },
    noHeader: {
      type: Boolean,
      default: false
    },
    noHeaderClose: {
      type: Boolean,
      default: false
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false
    },
    noCloseOnRouteChange: {
      type: Boolean,
      default: false
    },
    noEnforceFocus: {
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      // Internal `v-model` state
      localShow: !!this.visible,
      // For lazy render triggering
      isOpen: !!this.visible
    };
  },
  computed: {
    transitionProps: function transitionProps() {
      return this.noSlide ?
      /* istanbul ignore next */
      {
        css: true
      } : {
        css: true,
        enterClass: '',
        enterActiveClass: 'slide',
        enterToClass: 'show',
        leaveClass: 'show',
        leaveActiveClass: 'slide',
        leaveToClass: ''
      };
    },
    slotScope: function slotScope() {
      return {
        visible: this.localShow,
        right: this.right,
        hide: this.hide
      };
    },
    computedTile: function computedTile() {
      return this.normalizeSlot(SLOT_NAME_TITLE, this.slotScope) || toString$1(this.title) || null;
    },
    titleId: function titleId() {
      return this.computedTile ? this.safeId('__title__') : null;
    },
    computedAttrs: function computedAttrs() {
      return _objectSpread$w(_objectSpread$w({}, this.bvAttrs), {}, {
        id: this.safeId(),
        tabindex: '-1',
        role: 'dialog',
        'aria-modal': this.backdrop ? 'true' : 'false',
        'aria-hidden': this.localShow ? null : 'true',
        'aria-label': this.ariaLabel || null,
        'aria-labelledby': this.ariaLabelledby || this.titleId || null
      });
    }
  },
  watch: {
    visible: function visible(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.localShow = newVal;
      }
    },
    localShow: function localShow(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.emitState(newVal);
        this.$emit('change', newVal);
      }
    },

    /* istanbul ignore next */
    $route: function $route()
    /* istanbul ignore next: pain to mock */
    {
      var newVal = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var oldVal = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (!this.noCloseOnRouteChange && newVal.fullPath !== oldVal.fullPath) {
        this.hide();
      }
    }
  },
  created: function created() {
    // Define non-reactive properties
    this.$_returnFocusEl = null;
  },
  mounted: function mounted() {
    var _this = this;

    // Add `$root` listeners
    this.listenOnRoot(EVENT_TOGGLE, this.handleToggle);
    this.listenOnRoot(EVENT_STATE_REQUEST, this.handleSync); // Send out a gratuitous state event to ensure toggle button is synced

    this.$nextTick(function () {
      _this.emitState(_this.localShow);
    });
  },

  /* istanbul ignore next */
  activated: function activated()
  /* istanbul ignore next */
  {
    this.emitSync();
  },
  beforeDestroy: function beforeDestroy() {
    this.localShow = false;
    this.$_returnFocusEl = null;
  },
  methods: {
    hide: function hide() {
      this.localShow = false;
    },
    emitState: function emitState() {
      var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.localShow;
      this.emitOnRoot(EVENT_STATE, this.safeId(), state);
    },
    emitSync: function emitSync() {
      var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.localShow;
      this.emitOnRoot(EVENT_STATE_SYNC, this.safeId(), state);
    },
    handleToggle: function handleToggle(id) {
      // Note `safeId()` can be null until after mount
      if (id && id === this.safeId()) {
        this.localShow = !this.localShow;
      }
    },
    handleSync: function handleSync(id) {
      var _this2 = this;

      // Note `safeId()` can be null until after mount
      if (id && id === this.safeId()) {
        this.$nextTick(function () {
          _this2.emitSync(_this2.localShow);
        });
      }
    },
    onKeydown: function onKeydown(evt) {
      var keyCode = evt.keyCode;

      if (!this.noCloseOnEsc && keyCode === CODE_ESC && this.localShow) {
        this.hide();
      }
    },
    onBackdropClick: function onBackdropClick() {
      if (this.localShow && !this.noCloseOnBackdrop) {
        this.hide();
      }
    },

    /* istanbul ignore next */
    onTopTrapFocus: function onTopTrapFocus()
    /* istanbul ignore next */
    {
      var tabables = getTabables(this.$refs.content);
      this.enforceFocus(tabables.reverse()[0]);
    },

    /* istanbul ignore next */
    onBottomTrapFocus: function onBottomTrapFocus()
    /* istanbul ignore next */
    {
      var tabables = getTabables(this.$refs.content);
      this.enforceFocus(tabables[0]);
    },
    onBeforeEnter: function onBeforeEnter() {
      // Returning focus to `document.body` may cause unwanted scrolls,
      // so we exclude setting focus on body
      this.$_returnFocusEl = getActiveElement(isBrowser ? [document.body] : []); // Trigger lazy render

      this.isOpen = true;
    },
    onAfterEnter: function onAfterEnter(el) {
      if (!contains(el, getActiveElement())) {
        this.enforceFocus(el);
      }

      this.$emit('shown');
    },
    onAfterLeave: function onAfterLeave() {
      this.enforceFocus(this.$_returnFocusEl);
      this.$_returnFocusEl = null; // Trigger lazy render

      this.isOpen = false;
      this.$emit('hidden');
    },
    enforceFocus: function enforceFocus(el) {
      if (!this.noEnforceFocus) {
        attemptFocus(el);
      }
    }
  },
  render: function render(h) {
    var _ref;

    var localShow = this.localShow;
    var shadow = this.shadow === '' ? true : this.shadow;
    var $sidebar = h(this.tag, {
      ref: 'content',
      directives: [{
        name: 'show',
        value: localShow
      }],
      staticClass: CLASS_NAME$1,
      class: [(_ref = {
        shadow: shadow === true
      }, _defineProperty$H(_ref, "shadow-".concat(shadow), shadow && shadow !== true), _defineProperty$H(_ref, "".concat(CLASS_NAME$1, "-right"), this.right), _defineProperty$H(_ref, "bg-".concat(this.bgVariant), !!this.bgVariant), _defineProperty$H(_ref, "text-".concat(this.textVariant), !!this.textVariant), _ref), this.sidebarClass],
      attrs: this.computedAttrs,
      style: {
        width: this.width
      }
    }, [renderContent(h, this)]);
    $sidebar = h('transition', {
      props: this.transitionProps,
      on: {
        beforeEnter: this.onBeforeEnter,
        afterEnter: this.onAfterEnter,
        afterLeave: this.onAfterLeave
      }
    }, [$sidebar]);
    var $backdrop = h(BVTransition, {
      props: {
        noFade: this.noSlide
      }
    }, [renderBackdrop(h, this)]);
    var $tabTrapTop = h();
    var $tabTrapBottom = h();

    if (this.backdrop && this.localShow) {
      $tabTrapTop = h('div', {
        attrs: {
          tabindex: '0'
        },
        on: {
          focus: this.onTopTrapFocus
        }
      });
      $tabTrapBottom = h('div', {
        attrs: {
          tabindex: '0'
        },
        on: {
          focus: this.onBottomTrapFocus
        }
      });
    }

    return h('div', {
      staticClass: 'b-sidebar-outer',
      style: {
        zIndex: this.zIndex
      },
      attrs: {
        tabindex: '-1'
      },
      on: {
        keydown: this.onKeydown
      }
    }, [$tabTrapTop, $sidebar, $tabTrapBottom, $backdrop]);
  }
});

function _defineProperty$I(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BSkeleton = /*#__PURE__*/Vue.extend({
  name: NAME_SKELETON,
  functional: true,
  props: {
    animation: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_SKELETON, 'animation');
      }
    },
    type: {
      type: String,
      default: 'text'
    },
    width: {
      type: String
    },
    height: {
      type: String
    },
    size: {
      type: String
    },
    variant: {
      type: String
    }
  },
  render: function render(h, _ref) {
    var _class;

    var data = _ref.data,
        props = _ref.props;
    var size = props.size,
        animation = props.animation,
        variant = props.variant;
    return h('div', a(data, {
      staticClass: 'b-skeleton',
      style: {
        width: size || props.width,
        height: size || props.height
      },
      class: (_class = {}, _defineProperty$I(_class, "b-skeleton-".concat(props.type), true), _defineProperty$I(_class, "b-skeleton-animate-".concat(animation), animation), _defineProperty$I(_class, "bg-".concat(variant), variant), _class)
    }));
  }
});

// Mixin to determine if an event listener has been registered

var hasListenerMixin = {
  methods: {
    hasListener: function hasListener(name) {
      // Only includes listeners registered via `v-on:name`
      var $listeners = this.$listeners || {}; // Includes `v-on:name` and `this.$on('name')` registered listeners
      // Note this property is not part of the public Vue API, but it is
      // the only way to determine if a listener was added via `vm.$on`

      var $events = this._events || {}; // Registered listeners in `this._events` are always an array,
      // but might be zero length

      return !isUndefined($listeners[name]) || isArray$1($events[name]) && $events[name].length > 0;
    }
  }
};

function ownKeys$x(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$x(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$x(Object(source), true).forEach(function (key) { _defineProperty$J(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$x(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$J(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$k = {
  variant: {
    type: String,
    default: null
  }
};
var LIGHT = 'light';
var DARK = 'dark'; // TODO:
//   In Bootstrap v5, we won't need "sniffing" as table element variants properly inherit
//   to the child elements, so this can be converted to a functional component
// @vue/component

var BTr = /*#__PURE__*/Vue.extend({
  name: NAME_TR,
  // Mixin order is important!
  mixins: [attrsMixin, listenersMixin, normalizeSlotMixin],
  inheritAttrs: false,
  provide: function provide() {
    return {
      bvTableTr: this
    };
  },
  inject: {
    bvTableRowGroup: {
      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return {};
      }
    }
  },
  props: props$k,
  computed: {
    inTbody: function inTbody() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.isTbody;
    },
    inThead: function inThead() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.isThead;
    },
    inTfoot: function inTfoot() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.isTfoot;
    },
    isDark: function isDark() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.isDark;
    },
    isStacked: function isStacked() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.isStacked;
    },
    isResponsive: function isResponsive() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.isResponsive;
    },
    isStickyHeader: function isStickyHeader() {
      // Sniffed by <b-td> / <b-th>
      // Sticky headers are only supported in thead
      return this.bvTableRowGroup.isStickyHeader;
    },
    hasStickyHeader: function hasStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      return !this.isStacked && this.bvTableRowGroup.hasStickyHeader;
    },
    tableVariant: function tableVariant() {
      // Sniffed by <b-td> / <b-th>
      return this.bvTableRowGroup.tableVariant;
    },
    headVariant: function headVariant() {
      // Sniffed by <b-td> / <b-th>
      return this.inThead ? this.bvTableRowGroup.headVariant : null;
    },
    footVariant: function footVariant() {
      // Sniffed by <b-td> / <b-th>
      return this.inTfoot ? this.bvTableRowGroup.footVariant : null;
    },
    isRowDark: function isRowDark() {
      return this.headVariant === LIGHT || this.footVariant === LIGHT ?
      /* istanbul ignore next */
      false : this.headVariant === DARK || this.footVariant === DARK ?
      /* istanbul ignore next */
      true : this.isDark;
    },
    trClasses: function trClasses() {
      return [this.variant ? "".concat(this.isRowDark ? 'bg' : 'table', "-").concat(this.variant) : null];
    },
    trAttrs: function trAttrs() {
      return _objectSpread$x({
        role: 'row'
      }, this.bvAttrs);
    }
  },
  render: function render(h) {
    return h('tr', {
      class: this.trClasses,
      attrs: this.trAttrs,
      // Pass native listeners to child
      on: this.bvListeners
    }, this.normalizeSlot());
  }
});

var slotName = 'bottom-row';
var bottomRowMixin = {
  methods: {
    renderBottomRow: function renderBottomRow() {
      var h = this.$createElement; // Static bottom row slot (hidden in visibly stacked mode as we can't control the data-label)
      // If in *always* stacked mode, we don't bother rendering the row

      if (!this.hasNormalizedSlot(slotName) || this.stacked === true || this.stacked === '') {
        return h();
      }

      var fields = this.computedFields;
      return h(BTr, {
        key: 'b-bottom-row',
        staticClass: 'b-table-bottom-row',
        class: [isFunction$1(this.tbodyTrClass) ?
        /* istanbul ignore next */
        this.tbodyTrClass(null, 'row-bottom') : this.tbodyTrClass],
        attrs: isFunction$1(this.tbodyTrAttr) ?
        /* istanbul ignore next */
        this.tbodyTrAttr(null, 'row-bottom') : this.tbodyTrAttr
      }, this.normalizeSlot(slotName, {
        columns: fields.length,
        fields: fields
      }));
    }
  }
};

function ownKeys$y(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$y(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$y(Object(source), true).forEach(function (key) { _defineProperty$K(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$y(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$K(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Parse a rowspan or colspan into a digit (or `null` if < `1` )

var parseSpan = function parseSpan(value) {
  value = toInteger(value, 0);
  return value > 0 ? value : null;
};
/* istanbul ignore next */


var spanValidator = function spanValidator(val) {
  return isUndefinedOrNull(val) || parseSpan(val) > 0;
}; // --- Props ---


var props$l = {
  variant: {
    type: String,
    default: null
  },
  colspan: {
    type: [Number, String],
    default: null,
    validator: spanValidator
  },
  rowspan: {
    type: [Number, String],
    default: null,
    validator: spanValidator
  },
  stackedHeading: {
    type: String,
    default: null
  },
  stickyColumn: {
    type: Boolean,
    default: false
  }
}; // --- Main component ---
// TODO:
//   In Bootstrap v5, we won't need "sniffing" as table element variants properly inherit
//   to the child elements, so this can be converted to a functional component
// @vue/component

var BTd = /*#__PURE__*/Vue.extend({
  name: NAME_TABLE_CELL,
  // Mixin order is important!
  mixins: [attrsMixin, listenersMixin, normalizeSlotMixin],
  inheritAttrs: false,
  inject: {
    bvTableTr: {
      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return {};
      }
    }
  },
  props: props$l,
  computed: {
    tag: function tag() {
      // Overridden by <b-th>
      return 'td';
    },
    inTbody: function inTbody() {
      return this.bvTableTr.inTbody;
    },
    inThead: function inThead() {
      return this.bvTableTr.inThead;
    },
    inTfoot: function inTfoot() {
      return this.bvTableTr.inTfoot;
    },
    isDark: function isDark() {
      return this.bvTableTr.isDark;
    },
    isStacked: function isStacked() {
      return this.bvTableTr.isStacked;
    },
    isStackedCell: function isStackedCell() {
      // We only support stacked-heading in tbody in stacked mode
      return this.inTbody && this.isStacked;
    },
    isResponsive: function isResponsive() {
      return this.bvTableTr.isResponsive;
    },
    isStickyHeader: function isStickyHeader() {
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      // Sticky headers only apply to cells in table `thead`
      return this.bvTableTr.isStickyHeader;
    },
    hasStickyHeader: function hasStickyHeader() {
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      return this.bvTableTr.hasStickyHeader;
    },
    isStickyColumn: function isStickyColumn() {
      // Needed to handle background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      // Sticky column cells are only available in responsive
      // mode (horizontal scrolling) or when sticky header mode
      // Applies to cells in `thead`, `tbody` and `tfoot`
      return !this.isStacked && (this.isResponsive || this.hasStickyHeader) && this.stickyColumn;
    },
    rowVariant: function rowVariant() {
      return this.bvTableTr.variant;
    },
    headVariant: function headVariant() {
      return this.bvTableTr.headVariant;
    },
    footVariant: function footVariant() {
      return this.bvTableTr.footVariant;
    },
    tableVariant: function tableVariant() {
      return this.bvTableTr.tableVariant;
    },
    computedColspan: function computedColspan() {
      return parseSpan(this.colspan);
    },
    computedRowspan: function computedRowspan() {
      return parseSpan(this.rowspan);
    },
    cellClasses: function cellClasses() {
      // We use computed props here for improved performance by caching
      // the results of the string interpolation
      var variant = this.variant;

      if (!variant && this.isStickyHeader && !this.headVariant || !variant && this.isStickyColumn && this.inTfoot && !this.footVariant || !variant && this.isStickyColumn && this.inThead && !this.headVariant || !variant && this.isStickyColumn && this.inTbody) {
        // Needed for sticky-header mode as Bootstrap v4 table cells do
        // not inherit parent's background-color. Boo!
        variant = this.rowVariant || this.tableVariant || 'b-table-default';
      }

      return [variant ? "".concat(this.isDark ? 'bg' : 'table', "-").concat(variant) : null, this.isStickyColumn ? 'b-table-sticky-column' : null];
    },
    cellAttrs: function cellAttrs() {
      // We use computed props here for improved performance by caching
      // the results of the object spread (Object.assign)
      var headOrFoot = this.inThead || this.inTfoot; // Make sure col/rowspan's are > 0 or null

      var colspan = this.computedColspan;
      var rowspan = this.computedRowspan; // Default role and scope

      var role = 'cell';
      var scope = null; // Compute role and scope
      // We only add scopes with an explicit span of 1 or greater

      if (headOrFoot) {
        // Header or footer cells
        role = 'columnheader';
        scope = colspan > 0 ? 'colspan' : 'col';
      } else if (isTag(this.tag, 'th')) {
        // th's in tbody
        role = 'rowheader';
        scope = rowspan > 0 ? 'rowgroup' : 'row';
      }

      return _objectSpread$y(_objectSpread$y({
        colspan: colspan,
        rowspan: rowspan,
        role: role,
        scope: scope
      }, this.bvAttrs), {}, {
        // Add in the stacked cell label data-attribute if in
        // stacked mode (if a stacked heading label is provided)
        'data-label': this.isStackedCell && !isUndefinedOrNull(this.stackedHeading) ?
        /* istanbul ignore next */
        toString$1(this.stackedHeading) : null
      });
    }
  },
  render: function render(h) {
    var content = [this.normalizeSlot()];
    return h(this.tag, {
      class: this.cellClasses,
      attrs: this.cellAttrs,
      // Transfer any native listeners
      on: this.bvListeners
    }, [this.isStackedCell ? h('div', [content]) : content]);
  }
});

var busySlotName = 'table-busy';
var busyMixin = {
  props: {
    busy: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      localBusy: false
    };
  },
  computed: {
    computedBusy: function computedBusy() {
      return this.busy || this.localBusy;
    }
  },
  watch: {
    localBusy: function localBusy(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('update:busy', newVal);
      }
    }
  },
  methods: {
    // Event handler helper
    stopIfBusy: function stopIfBusy(evt) {
      if (this.computedBusy) {
        // If table is busy (via provider) then don't propagate
        stopEvent(evt);
        return true;
      }

      return false;
    },
    // Render the busy indicator or return `null` if not busy
    renderBusy: function renderBusy() {
      var h = this.$createElement; // Return a busy indicator row, or `null` if not busy

      if (this.computedBusy && this.hasNormalizedSlot(busySlotName)) {
        // Show the busy slot
        return h(BTr, {
          key: 'table-busy-slot',
          staticClass: 'b-table-busy-slot',
          class: [isFunction$1(this.tbodyTrClass) ?
          /* istanbul ignore next */
          this.tbodyTrClass(null, busySlotName) : this.tbodyTrClass],
          attrs: isFunction$1(this.tbodyTrAttr) ?
          /* istanbul ignore next */
          this.tbodyTrAttr(null, busySlotName) : this.tbodyTrAttr
        }, [h(BTd, {
          props: {
            colspan: this.computedFields.length || null
          }
        }, [this.normalizeSlot(busySlotName)])]);
      } else {
        // We return `null` here so that we can determine if we need to
        // render the table items rows or not
        return null;
      }
    }
  }
};

var captionMixin = {
  props: {
    // `caption-top` is part of table-render mixin (styling)
    // captionTop: {
    //   type: Boolean,
    //   default: false
    // },
    caption: {
      type: String // default: null

    },
    captionHtml: {
      type: String
    }
  },
  computed: {
    captionId: function captionId() {
      // Even though `this.safeId` looks like a method, it is a computed prop
      // that returns a new function if the underlying ID changes
      return this.isStacked ? this.safeId('_caption_') : null;
    }
  },
  methods: {
    renderCaption: function renderCaption() {
      var caption = this.caption,
          captionHtml = this.captionHtml;
      var h = this.$createElement;
      var $caption = h();
      var hasCaptionSlot = this.hasNormalizedSlot('table-caption');

      if (hasCaptionSlot || caption || captionHtml) {
        $caption = h('caption', {
          key: 'caption',
          attrs: {
            id: this.captionId
          },
          domProps: hasCaptionSlot ? {} : htmlOrText(captionHtml, caption)
        }, this.normalizeSlot('table-caption'));
      }

      return $caption;
    }
  }
};

var colgroupMixin = {
  methods: {
    renderColgroup: function renderColgroup() {
      var h = this.$createElement;
      var fields = this.computedFields;
      var $colgroup = h();

      if (this.hasNormalizedSlot('table-colgroup')) {
        $colgroup = h('colgroup', {
          key: 'colgroup'
        }, [this.normalizeSlot('table-colgroup', {
          columns: fields.length,
          fields: fields
        })]);
      }

      return $colgroup;
    }
  }
};

var emptyMixin = {
  props: {
    showEmpty: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: 'There are no records to show'
    },
    emptyHtml: {
      type: String
    },
    emptyFilteredText: {
      type: String,
      default: 'There are no records matching your request'
    },
    emptyFilteredHtml: {
      type: String
    }
  },
  methods: {
    renderEmpty: function renderEmpty() {
      var h = this.$createElement;
      var items = this.computedItems;
      var $empty = h();

      if (this.showEmpty && (!items || items.length === 0) && !(this.computedBusy && this.hasNormalizedSlot('table-busy'))) {
        var isFiltered = this.isFiltered,
            emptyText = this.emptyText,
            emptyHtml = this.emptyHtml,
            emptyFilteredText = this.emptyFilteredText,
            emptyFilteredHtml = this.emptyFilteredHtml,
            computedFields = this.computedFields,
            tbodyTrClass = this.tbodyTrClass,
            tbodyTrAttr = this.tbodyTrAttr;
        $empty = this.normalizeSlot(this.isFiltered ? 'emptyfiltered' : 'empty', {
          emptyFilteredHtml: emptyFilteredHtml,
          emptyFilteredText: emptyFilteredText,
          emptyHtml: emptyHtml,
          emptyText: emptyText,
          fields: computedFields,
          // Not sure why this is included, as it will always be an empty array
          items: this.computedItems
        });

        if (!$empty) {
          $empty = h('div', {
            class: ['text-center', 'my-2'],
            domProps: isFiltered ? htmlOrText(emptyFilteredHtml, emptyFilteredText) : htmlOrText(emptyHtml, emptyText)
          });
        }

        $empty = h(BTd, {
          props: {
            colspan: computedFields.length || null
          }
        }, [h('div', {
          attrs: {
            role: 'alert',
            'aria-live': 'polite'
          }
        }, [$empty])]);
        $empty = h(BTr, {
          staticClass: 'b-table-empty-row',
          class: [isFunction$1(tbodyTrClass) ?
          /* istanbul ignore next */
          this.tbodyTrClass(null, 'row-empty') : tbodyTrClass],
          attrs: isFunction$1(tbodyTrAttr) ?
          /* istanbul ignore next */
          this.tbodyTrAttr(null, 'row-empty') : tbodyTrAttr,
          key: isFiltered ? 'b-empty-filtered-row' : 'b-empty-row'
        }, [$empty]);
      }

      return $empty;
    }
  }
};

// Constants used by table helpers
// Object of item keys that should be ignored for headers and
// stringification and filter events
var IGNORED_FIELD_KEYS = {
  _rowVariant: true,
  _cellVariants: true,
  _showDetails: true
}; // Filter CSS selector for click/dblclick/etc. events
// If any of these selectors match the clicked element, we ignore the event

var EVENT_FILTER = ['a', 'a *', // Include content inside links
'button', 'button *', // Include content inside buttons
'input:not(.disabled):not([disabled])', 'select:not(.disabled):not([disabled])', 'textarea:not(.disabled):not([disabled])', '[role="link"]', '[role="link"] *', '[role="button"]', '[role="button"] *', '[tabindex]:not(.disabled):not([disabled])'].join(',');

var sanitizeRow = function sanitizeRow(row, ignoreFields, includeFields) {
  var fieldsObj = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  // We first need to format the row based on the field configurations
  // This ensures that we add formatted values for keys that may not
  // exist in the row itself
  var formattedRow = keys$1(fieldsObj).reduce(function (result, key) {
    var field = fieldsObj[key];
    var filterByFormatted = field.filterByFormatted;
    var formatter = isFunction$1(filterByFormatted) ?
    /* istanbul ignore next */
    filterByFormatted : filterByFormatted ?
    /* istanbul ignore next */
    field.formatter : null;

    if (isFunction$1(formatter)) {
      result[key] = formatter(row[key], key, row);
    }

    return result;
  }, clone(row)); // Determine the allowed keys:
  //   - Ignore special fields that start with `_`
  //   - Ignore fields in the `ignoreFields` array
  //   - Include only fields in the `includeFields` array

  var allowedKeys = keys$1(formattedRow).filter(function (key) {
    return !IGNORED_FIELD_KEYS[key] && !(isArray$1(ignoreFields) && ignoreFields.length > 0 && arrayIncludes(ignoreFields, key)) && !(isArray$1(includeFields) && includeFields.length > 0 && !arrayIncludes(includeFields, key));
  });
  return pick(formattedRow, allowedKeys);
};

// SSR safe deterministic way (keys are sorted before stringification)
//
//   ex:
//     { b: 3, c: { z: 'zzz', d: null, e: 2 }, d: [10, 12, 11], a: 'one' }
//   becomes
//     'one 3 2 zzz 10 12 11'
//
// Primitives (numbers/strings) are returned as-is
// Null and undefined values are filtered out
// Dates are converted to their native string format

var stringifyObjectValues = function stringifyObjectValues(val) {
  if (isUndefinedOrNull(val)) {
    /* istanbul ignore next */
    return '';
  } // Arrays are also object, and keys just returns the array indexes
  // Date objects we convert to strings


  if (isObject$1(val) && !isDate(val)) {
    return keys$1(val).sort() // Sort to prevent SSR issues on pre-rendered sorted tables
    .filter(function (v) {
      return !isUndefinedOrNull(v);
    }) // Ignore undefined/null values
    .map(function (k) {
      return stringifyObjectValues(val[k]);
    }).join(' ');
  }

  return toString$1(val);
};

// TODO: Add option to stringify `scopedSlot` items

var stringifyRecordValues = function stringifyRecordValues(row, ignoreFields, includeFields, fieldsObj) {
  return isObject$1(row) ? stringifyObjectValues(sanitizeRow(row, ignoreFields, includeFields, fieldsObj)) :
  /* istanbul ignore next */
  '';
};

var DEBOUNCE_DEPRECATED_MSG = 'Prop "filter-debounce" is deprecated. Use the debounce feature of "<b-form-input>" instead.';
var filteringMixin = {
  props: {
    filter: {
      type: [String, RegExp, Object, Array],
      default: null
    },
    filterFunction: {
      type: Function // default: null

    },
    filterIgnoredFields: {
      type: Array // default: undefined

    },
    filterIncludedFields: {
      type: Array // default: undefined

    },
    filterDebounce: {
      type: [Number, String],
      deprecated: DEBOUNCE_DEPRECATED_MSG,
      default: 0,
      validator: function validator(val) {
        return /^\d+/.test(String(val));
      }
    }
  },
  data: function data() {
    return {
      // Flag for displaying which empty slot to show and some event triggering
      isFiltered: false,
      // Where we store the copy of the filter criteria after debouncing
      // We pre-set it with the sanitized filter value
      localFilter: this.filterSanitize(this.filter)
    };
  },
  computed: {
    computedFilterIgnored: function computedFilterIgnored() {
      return concat(this.filterIgnoredFields || []).filter(identity$1);
    },
    computedFilterIncluded: function computedFilterIncluded() {
      return concat(this.filterIncludedFields || []).filter(identity$1);
    },
    computedFilterDebounce: function computedFilterDebounce() {
      var ms = toInteger(this.filterDebounce, 0);
      /* istanbul ignore next */

      if (ms > 0) {
        warn(DEBOUNCE_DEPRECATED_MSG, NAME_TABLE);
      }

      return ms;
    },
    localFiltering: function localFiltering() {
      return this.hasProvider ? !!this.noProviderFiltering : true;
    },
    // For watching changes to `filteredItems` vs `localItems`
    filteredCheck: function filteredCheck() {
      return {
        filteredItems: this.filteredItems,
        localItems: this.localItems,
        localFilter: this.localFilter
      };
    },
    // Sanitized/normalize filter-function prop
    localFilterFn: function localFilterFn() {
      // Return `null` to signal to use internal filter function
      return isFunction$1(this.filterFunction) ? this.filterFunction : null;
    },
    // Returns the records in `localItems` that match the filter criteria
    // Returns the original `localItems` array if not sorting
    filteredItems: function filteredItems() {
      var items = this.localItems || []; // Note the criteria is debounced and sanitized

      var criteria = this.localFilter; // Resolve the filtering function, when requested
      // We prefer the provided filtering function and fallback to the internal one
      // When no filtering criteria is specified the filtering factories will return `null`

      var filterFn = this.localFiltering ? this.filterFnFactory(this.localFilterFn, criteria) || this.defaultFilterFnFactory(criteria) : null; // We only do local filtering when requested and there are records to filter

      return filterFn && items.length > 0 ? items.filter(filterFn) : items;
    }
  },
  watch: {
    // Watch for debounce being set to 0
    computedFilterDebounce: function computedFilterDebounce(newVal) {
      if (!newVal && this.$_filterTimer) {
        this.clearFilterTimer();
        this.localFilter = this.filterSanitize(this.filter);
      }
    },
    // Watch for changes to the filter criteria, and debounce if necessary
    filter: {
      // We need a deep watcher in case the user passes
      // an object when using `filter-function`
      deep: true,
      handler: function handler(newCriteria) {
        var _this = this;

        var timeout = this.computedFilterDebounce;
        this.clearFilterTimer();

        if (timeout && timeout > 0) {
          // If we have a debounce time, delay the update of `localFilter`
          this.$_filterTimer = setTimeout(function () {
            _this.localFilter = _this.filterSanitize(newCriteria);
          }, timeout);
        } else {
          // Otherwise, immediately update `localFilter` with `newFilter` value
          this.localFilter = this.filterSanitize(newCriteria);
        }
      }
    },
    // Watch for changes to the filter criteria and filtered items vs `localItems`
    // Set visual state and emit events as required
    filteredCheck: function filteredCheck(_ref) {
      var filteredItems = _ref.filteredItems,
          localFilter = _ref.localFilter;
      // Determine if the dataset is filtered or not
      var isFiltered = false;

      if (!localFilter) {
        // If filter criteria is falsey
        isFiltered = false;
      } else if (looseEqual(localFilter, []) || looseEqual(localFilter, {})) {
        // If filter criteria is an empty array or object
        isFiltered = false;
      } else if (localFilter) {
        // If filter criteria is truthy
        isFiltered = true;
      }

      if (isFiltered) {
        this.$emit('filtered', filteredItems, filteredItems.length);
      }

      this.isFiltered = isFiltered;
    },
    isFiltered: function isFiltered(newVal, oldVal) {
      if (newVal === false && oldVal === true) {
        // We need to emit a filtered event if isFiltered transitions from true to
        // false so that users can update their pagination controls.
        this.$emit('filtered', this.localItems, this.localItems.length);
      }
    }
  },
  created: function created() {
    var _this2 = this;

    // Create private non-reactive props
    this.$_filterTimer = null; // If filter is "pre-set", set the criteria
    // This will trigger any watchers/dependents
    // this.localFilter = this.filterSanitize(this.filter)
    // Set the initial filtered state in a `$nextTick()` so that
    // we trigger a filtered event if needed

    this.$nextTick(function () {
      _this2.isFiltered = Boolean(_this2.localFilter);
    });
  },
  beforeDestroy: function beforeDestroy()
  /* istanbul ignore next */
  {
    this.clearFilterTimer();
  },
  methods: {
    clearFilterTimer: function clearFilterTimer() {
      clearTimeout(this.$_filterTimer);
      this.$_filterTimer = null;
    },
    filterSanitize: function filterSanitize(criteria) {
      // Sanitizes filter criteria based on internal or external filtering
      if (this.localFiltering && !this.localFilterFn && !(isString(criteria) || isRegExp(criteria))) {
        // If using internal filter function, which only accepts string or RegExp,
        // return '' to signify no filter
        return '';
      } // Could be a string, object or array, as needed by external filter function
      // We use `cloneDeep` to ensure we have a new copy of an object or array
      // without Vue's reactive observers


      return cloneDeep(criteria);
    },
    // Filter Function factories
    filterFnFactory: function filterFnFactory(filterFn, criteria) {
      // Wrapper factory for external filter functions
      // Wrap the provided filter-function and return a new function
      // Returns `null` if no filter-function defined or if criteria is falsey
      // Rather than directly grabbing `this.computedLocalFilterFn` or `this.filterFunction`
      // we have it passed, so that the caller computed prop will be reactive to changes
      // in the original filter-function (as this routine is a method)
      if (!filterFn || !isFunction$1(filterFn) || !criteria || looseEqual(criteria, []) || looseEqual(criteria, {})) {
        return null;
      } // Build the wrapped filter test function, passing the criteria to the provided function


      var fn = function fn(item) {
        // Generated function returns true if the criteria matches part
        // of the serialized data, otherwise false
        return filterFn(item, criteria);
      }; // Return the wrapped function


      return fn;
    },
    defaultFilterFnFactory: function defaultFilterFnFactory(criteria) {
      var _this3 = this;

      // Generates the default filter function, using the given filter criteria
      // Returns `null` if no criteria or criteria format not supported
      if (!criteria || !(isString(criteria) || isRegExp(criteria))) {
        // Built in filter can only support strings or RegExp criteria (at the moment)
        return null;
      } // Build the RegExp needed for filtering


      var regExp = criteria;

      if (isString(regExp)) {
        // Escape special RegExp characters in the string and convert contiguous
        // whitespace to \s+ matches
        var pattern = escapeRegExp(criteria).replace(RX_SPACES, '\\s+'); // Build the RegExp (no need for global flag, as we only need
        // to find the value once in the string)

        regExp = new RegExp(".*".concat(pattern, ".*"), 'i');
      } // Generate the wrapped filter test function to use


      var fn = function fn(item) {
        // This searches all row values (and sub property values) in the entire (excluding
        // special `_` prefixed keys), because we convert the record to a space-separated
        // string containing all the value properties (recursively), even ones that are
        // not visible (not specified in this.fields)
        // Users can ignore filtering on specific fields, or on only certain fields,
        // and can optionall specify searching results of fields with formatter
        //
        // TODO: Enable searching on scoped slots (optional, as it will be SLOW)
        //
        // Generated function returns true if the criteria matches part of
        // the serialized data, otherwise false
        //
        // We set `lastIndex = 0` on the `RegExp` in case someone specifies the `/g` global flag
        regExp.lastIndex = 0;
        return regExp.test(stringifyRecordValues(item, _this3.computedFilterIgnored, _this3.computedFilterIncluded, _this3.computedFieldsObj));
      }; // Return the generated function


      return fn;
    }
  }
};

var processField = function processField(key, value) {
  var field = null;

  if (isString(value)) {
    // Label shortcut
    field = {
      key: key,
      label: value
    };
  } else if (isFunction$1(value)) {
    // Formatter shortcut
    field = {
      key: key,
      formatter: value
    };
  } else if (isObject$1(value)) {
    field = clone(value);
    field.key = field.key || key;
  } else if (value !== false) {
    // Fallback to just key

    /* istanbul ignore next */
    field = {
      key: key
    };
  }

  return field;
}; // We normalize fields into an array of objects
// [ { key:..., label:..., ...}, {...}, ..., {..}]


var normalizeFields = function normalizeFields(origFields, items) {
  var fields = [];

  if (isArray$1(origFields)) {
    // Normalize array Form
    origFields.filter(identity$1).forEach(function (f) {
      if (isString(f)) {
        fields.push({
          key: f,
          label: startCase(f)
        });
      } else if (isObject$1(f) && f.key && isString(f.key)) {
        // Full object definition. We use assign so that we don't mutate the original
        fields.push(clone(f));
      } else if (isObject$1(f) && keys$1(f).length === 1) {
        // Shortcut object (i.e. { 'foo_bar': 'This is Foo Bar' }
        var key = keys$1(f)[0];
        var field = processField(key, f[key]);

        if (field) {
          fields.push(field);
        }
      }
    });
  } // If no field provided, take a sample from first record (if exits)


  if (fields.length === 0 && isArray$1(items) && items.length > 0) {
    var sample = items[0];
    keys$1(sample).forEach(function (k) {
      if (!IGNORED_FIELD_KEYS[k]) {
        fields.push({
          key: k,
          label: startCase(k)
        });
      }
    });
  } // Ensure we have a unique array of fields and that they have String labels


  var memo = {};
  return fields.filter(function (f) {
    if (!memo[f.key]) {
      memo[f.key] = true;
      f.label = isString(f.label) ? f.label : startCase(f.key);
      return true;
    }

    return false;
  });
};

var itemsMixin = {
  props: {
    items: {
      // Provider mixin adds in `Function` type
      type: Array,

      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return [];
      }
    },
    fields: {
      type: Array,
      default: null
    },
    primaryKey: {
      // Primary key for record
      // If provided the value in each row must be unique!
      type: String // default: null

    },
    value: {
      // `v-model` for retrieving the current displayed rows
      type: Array,
      default: function _default() {
        return [];
      }
    }
  },
  data: function data() {
    return {
      // Our local copy of the items
      // Must be an array
      localItems: isArray$1(this.items) ? this.items.slice() : []
    };
  },
  computed: {
    computedFields: function computedFields() {
      // We normalize fields into an array of objects
      // `[ { key:..., label:..., ...}, {...}, ..., {..}]`
      return normalizeFields(this.fields, this.localItems);
    },
    computedFieldsObj: function computedFieldsObj() {
      // Fields as a simple lookup hash object
      // Mainly for formatter lookup and use in `scopedSlots` for convenience
      // If the field has a formatter, it normalizes formatter to a
      // function ref or `undefined` if no formatter
      var parent = this.$parent;
      return this.computedFields.reduce(function (obj, f) {
        // We use object spread here so we don't mutate the original field object
        obj[f.key] = clone(f);

        if (f.formatter) {
          // Normalize formatter to a function ref or `undefined`
          var formatter = f.formatter;

          if (isString(formatter) && isFunction$1(parent[formatter])) {
            formatter = parent[formatter];
          } else if (!isFunction$1(formatter)) {
            /* istanbul ignore next */
            formatter = undefined;
          } // Return formatter function or `undefined` if none


          obj[f.key].formatter = formatter;
        }

        return obj;
      }, {});
    },
    computedItems: function computedItems() {
      // Fallback if various mixins not provided
      return (this.paginatedItems || this.sortedItems || this.filteredItems || this.localItems ||
      /* istanbul ignore next */
      []).slice();
    },
    context: function context() {
      // Current state of sorting, filtering and pagination props/values
      return {
        filter: this.localFilter,
        sortBy: this.localSortBy,
        sortDesc: this.localSortDesc,
        perPage: mathMax(toInteger(this.perPage, 0), 0),
        currentPage: mathMax(toInteger(this.currentPage, 0), 1),
        apiUrl: this.apiUrl
      };
    }
  },
  watch: {
    items: function items(newItems) {
      /* istanbul ignore else */
      if (isArray$1(newItems)) {
        // Set `localItems`/`filteredItems` to a copy of the provided array
        this.localItems = newItems.slice();
      } else if (isUndefinedOrNull(newItems)) {
        /* istanbul ignore next */
        this.localItems = [];
      }
    },
    // Watch for changes on `computedItems` and update the `v-model`
    computedItems: function computedItems(newVal, oldVal) {
      if (!looseEqual(newVal, oldVal)) {
        this.$emit('input', newVal);
      }
    },
    // Watch for context changes
    context: function context(newVal, oldVal) {
      // Emit context information for external paging/filtering/sorting handling
      if (!looseEqual(newVal, oldVal)) {
        this.$emit('context-changed', newVal);
      }
    }
  },
  mounted: function mounted() {
    // Initially update the `v-model` of displayed items
    this.$emit('input', this.computedItems);
  },
  methods: {
    // Method to get the formatter method for a given field key
    getFieldFormatter: function getFieldFormatter(key) {
      var field = this.computedFieldsObj[key]; // `this.computedFieldsObj` has pre-normalized the formatter to a
      // function ref if present, otherwise `undefined`

      return field ? field.formatter : undefined;
    }
  }
};

var paginationMixin = {
  props: {
    perPage: {
      type: [Number, String],
      default: 0
    },
    currentPage: {
      type: [Number, String],
      default: 1
    }
  },
  computed: {
    localPaging: function localPaging() {
      return this.hasProvider ? !!this.noProviderPaging : true;
    },
    paginatedItems: function paginatedItems() {
      var items = this.sortedItems || this.filteredItems || this.localItems || [];
      var currentPage = mathMax(toInteger(this.currentPage, 1), 1);
      var perPage = mathMax(toInteger(this.perPage, 0), 0); // Apply local pagination

      if (this.localPaging && !!perPage) {
        // Grab the current page of data (which may be past filtered items limit)
        items = items.slice((currentPage - 1) * perPage, currentPage * perPage);
      } // Return the items to display in the table


      return items;
    }
  }
};

var providerMixin = {
  mixins: [listenOnRootMixin],
  props: {
    // Prop override(s)
    items: {
      // Adds in 'Function' support
      type: [Array, Function],

      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return [];
      }
    },
    // Additional props
    noProviderPaging: {
      type: Boolean,
      default: false
    },
    noProviderSorting: {
      type: Boolean,
      default: false
    },
    noProviderFiltering: {
      type: Boolean,
      default: false
    },
    apiUrl: {
      // Passthrough prop. Passed to the context object. Not used by b-table directly
      type: String,
      default: ''
    }
  },
  computed: {
    hasProvider: function hasProvider() {
      return isFunction$1(this.items);
    },
    providerTriggerContext: function providerTriggerContext() {
      // Used to trigger the provider function via a watcher. Only the fields that
      // are needed for triggering a provider update are included. Note that the
      // regular this.context is sent to the provider during fetches though, as they
      // may need all the prop info.
      var ctx = {
        apiUrl: this.apiUrl,
        filter: null,
        sortBy: null,
        sortDesc: null,
        perPage: null,
        currentPage: null
      };

      if (!this.noProviderFiltering) {
        // Either a string, or could be an object or array.
        ctx.filter = this.localFilter;
      }

      if (!this.noProviderSorting) {
        ctx.sortBy = this.localSortBy;
        ctx.sortDesc = this.localSortDesc;
      }

      if (!this.noProviderPaging) {
        ctx.perPage = this.perPage;
        ctx.currentPage = this.currentPage;
      }

      return clone(ctx);
    }
  },
  watch: {
    // Provider update triggering
    items: function items(newVal) {
      // If a new provider has been specified, trigger an update
      if (this.hasProvider || isFunction$1(newVal)) {
        this.$nextTick(this._providerUpdate);
      }
    },
    providerTriggerContext: function providerTriggerContext(newVal, oldVal) {
      // Trigger the provider to update as the relevant context values have changed.
      if (!looseEqual(newVal, oldVal)) {
        this.$nextTick(this._providerUpdate);
      }
    }
  },
  mounted: function mounted() {
    var _this = this;

    // Call the items provider if necessary
    if (this.hasProvider && (!this.localItems || this.localItems.length === 0)) {
      // Fetch on mount if localItems is empty
      this._providerUpdate();
    } // Listen for global messages to tell us to force refresh the table


    this.listenOnRoot('bv::refresh::table', function (id) {
      if (id === _this.id || id === _this) {
        _this.refresh();
      }
    });
  },
  methods: {
    refresh: function refresh() {
      // Public Method: Force a refresh of the provider function
      this.$off('refreshed', this.refresh);

      if (this.computedBusy) {
        // Can't force an update when forced busy by user (busy prop === true)
        if (this.localBusy && this.hasProvider) {
          // But if provider running (localBusy), re-schedule refresh once `refreshed` emitted
          this.$on('refreshed', this.refresh);
        }
      } else {
        this.clearSelected();

        if (this.hasProvider) {
          this.$nextTick(this._providerUpdate);
        } else {
          /* istanbul ignore next */
          this.localItems = isArray$1(this.items) ? this.items.slice() : [];
        }
      }
    },
    // Provider related methods
    _providerSetLocal: function _providerSetLocal(items) {
      this.localItems = isArray$1(items) ? items.slice() : [];
      this.localBusy = false;
      this.$emit('refreshed'); // New root emit

      if (this.id) {
        this.emitOnRoot('bv::table::refreshed', this.id);
      }
    },
    _providerUpdate: function _providerUpdate() {
      var _this2 = this;

      // Refresh the provider function items.
      if (!this.hasProvider) {
        // Do nothing if no provider
        return;
      } // If table is busy, wait until refreshed before calling again


      if (this.computedBusy) {
        // Schedule a new refresh once `refreshed` is emitted
        this.$nextTick(this.refresh);
        return;
      } // Set internal busy state


      this.localBusy = true; // Call provider function with context and optional callback after DOM is fully updated

      this.$nextTick(function () {
        try {
          // Call provider function passing it the context and optional callback
          var data = _this2.items(_this2.context, _this2._providerSetLocal);

          if (isPromise(data)) {
            // Provider returned Promise
            data.then(function (items) {
              // Provider resolved with items
              _this2._providerSetLocal(items);
            });
          } else if (isArray$1(data)) {
            // Provider returned Array data
            _this2._providerSetLocal(data);
          } else {
            /* istanbul ignore if */
            if (_this2.items.length !== 2) {
              // Check number of arguments provider function requested
              // Provider not using callback (didn't request second argument), so we clear
              // busy state as most likely there was an error in the provider function

              /* istanbul ignore next */
              warn("Provider function didn't request callback and did not return a promise or data.", NAME_TABLE);
              _this2.localBusy = false;
            }
          }
        } catch (e)
        /* istanbul ignore next */
        {
          // Provider function borked on us, so we spew out a warning
          // and clear the busy state
          warn("Provider function error [".concat(e.name, "] ").concat(e.message, "."), NAME_TABLE);
          _this2.localBusy = false;

          _this2.$off('refreshed', _this2.refresh);
        }
      });
    }
  }
};

function _defineProperty$L(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var selectableMixin = {
  props: {
    selectable: {
      type: Boolean,
      default: false
    },
    selectMode: {
      type: String,
      default: 'multi',
      validator: function validator(val) {
        return arrayIncludes(['range', 'multi', 'single'], val);
      }
    },
    selectedVariant: {
      type: String,
      default: function _default() {
        return getComponentConfig(NAME_TABLE, 'selectedVariant');
      }
    },
    noSelectOnClick: {
      // Disable use of click handlers for row selection
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      selectedRows: [],
      selectedLastRow: -1
    };
  },
  computed: {
    isSelectable: function isSelectable() {
      return this.selectable && this.selectMode;
    },
    hasSelectableRowClick: function hasSelectableRowClick() {
      return this.isSelectable && !this.noSelectOnClick;
    },
    supportsSelectableRows: function supportsSelectableRows() {
      return true;
    },
    selectableHasSelection: function selectableHasSelection() {
      return this.isSelectable && this.selectedRows && this.selectedRows.length > 0 && this.selectedRows.some(identity$1);
    },
    selectableIsMultiSelect: function selectableIsMultiSelect() {
      return this.isSelectable && arrayIncludes(['range', 'multi'], this.selectMode);
    },
    selectableTableClasses: function selectableTableClasses() {
      var _ref;

      return _ref = {
        'b-table-selectable': this.isSelectable
      }, _defineProperty$L(_ref, "b-table-select-".concat(this.selectMode), this.isSelectable), _defineProperty$L(_ref, 'b-table-selecting', this.selectableHasSelection), _defineProperty$L(_ref, 'b-table-selectable-no-click', this.isSelectable && !this.hasSelectableRowClick), _ref;
    },
    selectableTableAttrs: function selectableTableAttrs() {
      return {
        // TODO:
        //   Should this attribute not be included when no-select-on-click is set
        //   since this attribute implies keyboard navigation?
        'aria-multiselectable': !this.isSelectable ? null : this.selectableIsMultiSelect ? 'true' : 'false'
      };
    }
  },
  watch: {
    computedItems: function computedItems(newVal, oldVal) {
      // Reset for selectable
      var equal = false;

      if (this.isSelectable && this.selectedRows.length > 0) {
        // Quick check against array length
        equal = isArray$1(newVal) && isArray$1(oldVal) && newVal.length === oldVal.length;

        for (var i = 0; equal && i < newVal.length; i++) {
          // Look for the first non-loosely equal row, after ignoring reserved fields
          equal = looseEqual(sanitizeRow(newVal[i]), sanitizeRow(oldVal[i]));
        }
      }

      if (!equal) {
        this.clearSelected();
      }
    },
    selectable: function selectable(newVal) {
      this.clearSelected();
      this.setSelectionHandlers(newVal);
    },
    selectMode: function selectMode() {
      this.clearSelected();
    },
    hasSelectableRowClick: function hasSelectableRowClick(newVal) {
      this.clearSelected();
      this.setSelectionHandlers(!newVal);
    },
    selectedRows: function selectedRows(_selectedRows, oldVal) {
      var _this = this;

      if (this.isSelectable && !looseEqual(_selectedRows, oldVal)) {
        var items = []; // `.forEach()` skips over non-existent indices (on sparse arrays)

        _selectedRows.forEach(function (v, idx) {
          if (v) {
            items.push(_this.computedItems[idx]);
          }
        });

        this.$emit('row-selected', items);
      }
    }
  },
  beforeMount: function beforeMount() {
    // Set up handlers if needed
    if (this.isSelectable) {
      this.setSelectionHandlers(true);
    }
  },
  methods: {
    // Public methods
    selectRow: function selectRow(index) {
      // Select a particular row (indexed based on computedItems)
      if (this.isSelectable && isNumber(index) && index >= 0 && index < this.computedItems.length && !this.isRowSelected(index)) {
        var selectedRows = this.selectableIsMultiSelect ? this.selectedRows.slice() : [];
        selectedRows[index] = true;
        this.selectedLastClicked = -1;
        this.selectedRows = selectedRows;
      }
    },
    unselectRow: function unselectRow(index) {
      // Un-select a particular row (indexed based on `computedItems`)
      if (this.isSelectable && isNumber(index) && this.isRowSelected(index)) {
        var selectedRows = this.selectedRows.slice();
        selectedRows[index] = false;
        this.selectedLastClicked = -1;
        this.selectedRows = selectedRows;
      }
    },
    selectAllRows: function selectAllRows() {
      var length = this.computedItems.length;

      if (this.isSelectable && length > 0) {
        this.selectedLastClicked = -1;
        this.selectedRows = this.selectableIsMultiSelect ? range(length).map(function () {
          return true;
        }) : [true];
      }
    },
    isRowSelected: function isRowSelected(index) {
      // Determine if a row is selected (indexed based on `computedItems`)
      return !!(isNumber(index) && this.selectedRows[index]);
    },
    clearSelected: function clearSelected() {
      // Clear any active selected row(s)
      this.selectedLastClicked = -1;
      this.selectedRows = [];
    },
    // Internal private methods
    selectableRowClasses: function selectableRowClasses(index) {
      if (this.isSelectable && this.isRowSelected(index)) {
        var variant = this.selectedVariant;
        return _defineProperty$L({
          'b-table-row-selected': true
        }, "".concat(this.dark ? 'bg' : 'table', "-").concat(variant), variant);
      } else {
        return {};
      }
    },
    selectableRowAttrs: function selectableRowAttrs(index) {
      return {
        'aria-selected': !this.isSelectable ? null : this.isRowSelected(index) ? 'true' : 'false'
      };
    },
    setSelectionHandlers: function setSelectionHandlers(on) {
      var method = on && !this.noSelectOnClick ? '$on' : '$off'; // Handle row-clicked event

      this[method]('row-clicked', this.selectionHandler); // Clear selection on filter, pagination, and sort changes

      this[method]('filtered', this.clearSelected);
      this[method]('context-changed', this.clearSelected);
    },
    selectionHandler: function selectionHandler(item, index, evt) {
      /* istanbul ignore if: should never happen */
      if (!this.isSelectable || this.noSelectOnClick) {
        // Don't do anything if table is not in selectable mode
        this.clearSelected();
        return;
      }

      var selectMode = this.selectMode;
      var selectedRows = this.selectedRows.slice();
      var selected = !selectedRows[index]; // Note 'multi' mode needs no special event handling

      if (selectMode === 'single') {
        selectedRows = [];
      } else if (selectMode === 'range') {
        if (this.selectedLastRow > -1 && evt.shiftKey) {
          // range
          for (var idx = mathMin(this.selectedLastRow, index); idx <= mathMax(this.selectedLastRow, index); idx++) {
            selectedRows[idx] = true;
          }

          selected = true;
        } else {
          if (!(evt.ctrlKey || evt.metaKey)) {
            // Clear range selection if any
            selectedRows = [];
            selected = true;
          }

          this.selectedLastRow = selected ? index : -1;
        }
      }

      selectedRows[index] = selected;
      this.selectedRows = selectedRows;
    }
  }
};

/*
 * Consistent and stable sort function across JavaScript platforms
 *
 * Inconsistent sorts can cause SSR problems between client and server
 * such as in <b-table> if sortBy is applied to the data on server side render.
 * Chrome and V8 native sorts are inconsistent/unstable
 *
 * This function uses native sort with fallback to index compare when the a and b
 * compare returns 0
 *
 * Algorithm based on:
 * https://stackoverflow.com/questions/1427608/fast-stable-sorting-algorithm-implementation-in-javascript/45422645#45422645
 *
 * @param {array} array to sort
 * @param {function} sort compare function
 * @return {array}
 */
var stableSort = function stableSort(array, compareFn) {
  // Using `.bind(compareFn)` on the wrapped anonymous function improves
  // performance by avoiding the function call setup. We don't use an arrow
  // function here as it binds `this` to the `stableSort` context rather than
  // the `compareFn` context, which wouldn't give us the performance increase.
  return array.map(function (a, index) {
    return [index, a];
  }).sort(function (a, b) {
    return this(a[1], b[1]) || a[0] - b[0];
  }.bind(compareFn)).map(function (e) {
    return e[1];
  });
};

//
// TODO: Add option to sort by multiple columns (tri-state per column,
//       plus order of columns in sort)  where sortBy could be an array
//       of objects `[ {key: 'foo', sortDir: 'asc'}, {key:'bar', sortDir: 'desc'} ...]`
//       or an array of arrays `[ ['foo','asc'], ['bar','desc'] ]`
//       Multisort will most likely be handled in mixin-sort.js by
//       calling this method for each sortBy

var defaultSortCompare = function defaultSortCompare(a, b, sortBy, sortDesc, formatter, localeOpts, locale, nullLast) {
  var aa = get$1(a, sortBy, null);
  var bb = get$1(b, sortBy, null);

  if (isFunction$1(formatter)) {
    aa = formatter(aa, sortBy, a);
    bb = formatter(bb, sortBy, b);
  }

  aa = isUndefinedOrNull(aa) ? '' : aa;
  bb = isUndefinedOrNull(bb) ? '' : bb;

  if (isDate(aa) && isDate(bb) || isNumber(aa) && isNumber(bb)) {
    // Special case for comparing dates and numbers
    // Internally dates are compared via their epoch number values
    return aa < bb ? -1 : aa > bb ? 1 : 0;
  } else if (nullLast && aa === '' && bb !== '') {
    // Special case when sorting null/undefined/empty string last
    return 1;
  } else if (nullLast && aa !== '' && bb === '') {
    // Special case when sorting null/undefined/empty string last
    return -1;
  } // Do localized string comparison


  return stringifyObjectValues(aa).localeCompare(stringifyObjectValues(bb), locale, localeOpts);
};

function ownKeys$z(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$z(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$z(Object(source), true).forEach(function (key) { _defineProperty$M(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$z(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$M(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var sortingMixin = {
  props: {
    sortBy: {
      type: String,
      default: ''
    },
    sortDesc: {
      // TODO: Make this tri-state: true, false, null
      type: Boolean,
      default: false
    },
    sortDirection: {
      // This prop is named incorrectly
      // It should be `initialSortDirection` as it is a bit misleading
      // (not to mention it screws up the ARIA label on the headers)
      type: String,
      default: 'asc',
      validator: function validator(direction) {
        return arrayIncludes(['asc', 'desc', 'last'], direction);
      }
    },
    sortCompare: {
      type: Function // default: null

    },
    sortCompareOptions: {
      // Supported localCompare options, see `options` section of:
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
      type: Object,
      default: function _default() {
        return {
          numeric: true
        };
      }
    },
    sortCompareLocale: {
      // String: locale code
      // Array: array of Locale strings
      type: [String, Array] // default: undefined

    },
    sortNullLast: {
      // Sort null and undefined to appear last
      type: Boolean,
      default: false
    },
    noSortReset: {
      // Another prop that should have had a better name.
      // It should be noSortClear (on non-sortable headers).
      // We will need to make sure the documentation is clear on what
      // this prop does (as well as in the code for future reference)
      type: Boolean,
      default: false
    },
    labelSortAsc: {
      type: String,
      default: 'Click to sort Ascending'
    },
    labelSortDesc: {
      type: String,
      default: 'Click to sort Descending'
    },
    labelSortClear: {
      type: String,
      default: 'Click to clear sorting'
    },
    noLocalSorting: {
      type: Boolean,
      default: false
    },
    noFooterSorting: {
      type: Boolean,
      default: false
    },
    sortIconLeft: {
      // Place the sorting icon on the left of the header cells
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      localSortBy: this.sortBy || '',
      localSortDesc: this.sortDesc || false
    };
  },
  computed: {
    localSorting: function localSorting() {
      return this.hasProvider ? !!this.noProviderSorting : !this.noLocalSorting;
    },
    isSortable: function isSortable() {
      return this.computedFields.some(function (f) {
        return f.sortable;
      });
    },
    sortedItems: function sortedItems() {
      // Sorts the filtered items and returns a new array of the sorted items
      // or the original items array if not sorted.
      var items = (this.filteredItems || this.localItems || []).slice();
      var sortBy = this.localSortBy;
      var sortDesc = this.localSortDesc;
      var sortCompare = this.sortCompare;
      var localSorting = this.localSorting;

      var sortOptions = _objectSpread$z(_objectSpread$z({}, this.sortCompareOptions), {}, {
        usage: 'sort'
      });

      var sortLocale = this.sortCompareLocale || undefined;
      var nullLast = this.sortNullLast;

      if (sortBy && localSorting) {
        var field = this.computedFieldsObj[sortBy] || {};
        var sortByFormatted = field.sortByFormatted;
        var formatter = isFunction$1(sortByFormatted) ?
        /* istanbul ignore next */
        sortByFormatted : sortByFormatted ? this.getFieldFormatter(sortBy) : undefined; // `stableSort` returns a new array, and leaves the original array intact

        return stableSort(items, function (a, b) {
          var result = null;

          if (isFunction$1(sortCompare)) {
            // Call user provided sortCompare routine
            result = sortCompare(a, b, sortBy, sortDesc, formatter, sortOptions, sortLocale);
          }

          if (isUndefinedOrNull(result) || result === false) {
            // Fallback to built-in defaultSortCompare if sortCompare
            // is not defined or returns null/false
            result = defaultSortCompare(a, b, sortBy, sortDesc, formatter, sortOptions, sortLocale, nullLast);
          } // Negate result if sorting in descending order


          return (result || 0) * (sortDesc ? -1 : 1);
        });
      }

      return items;
    }
  },
  watch: {
    /* istanbul ignore next: pain in the butt to test */
    isSortable: function isSortable(newVal)
    /* istanbul ignore next: pain in the butt to test */
    {
      if (newVal) {
        if (this.isSortable) {
          this.$on('head-clicked', this.handleSort);
        }
      } else {
        this.$off('head-clicked', this.handleSort);
      }
    },
    sortDesc: function sortDesc(newVal) {
      if (newVal === this.localSortDesc) {
        /* istanbul ignore next */
        return;
      }

      this.localSortDesc = newVal || false;
    },
    sortBy: function sortBy(newVal) {
      if (newVal === this.localSortBy) {
        /* istanbul ignore next */
        return;
      }

      this.localSortBy = newVal || '';
    },
    // Update .sync props
    localSortDesc: function localSortDesc(newVal, oldVal) {
      // Emit update to sort-desc.sync
      if (newVal !== oldVal) {
        this.$emit('update:sortDesc', newVal);
      }
    },
    localSortBy: function localSortBy(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$emit('update:sortBy', newVal);
      }
    }
  },
  created: function created() {
    if (this.isSortable) {
      this.$on('head-clicked', this.handleSort);
    }
  },
  methods: {
    // Handlers
    // Need to move from thead-mixin
    handleSort: function handleSort(key, field, evt, isFoot) {
      var _this = this;

      if (!this.isSortable) {
        /* istanbul ignore next */
        return;
      }

      if (isFoot && this.noFooterSorting) {
        return;
      } // TODO: make this tri-state sorting
      // cycle desc => asc => none => desc => ...


      var sortChanged = false;

      var toggleLocalSortDesc = function toggleLocalSortDesc() {
        var sortDirection = field.sortDirection || _this.sortDirection;

        if (sortDirection === 'asc') {
          _this.localSortDesc = false;
        } else if (sortDirection === 'desc') {
          _this.localSortDesc = true;
        }
      };

      if (field.sortable) {
        var sortKey = !this.localSorting && field.sortKey ? field.sortKey : key;

        if (this.localSortBy === sortKey) {
          // Change sorting direction on current column
          this.localSortDesc = !this.localSortDesc;
        } else {
          // Start sorting this column ascending
          this.localSortBy = sortKey; // this.localSortDesc = false

          toggleLocalSortDesc();
        }

        sortChanged = true;
      } else if (this.localSortBy && !this.noSortReset) {
        this.localSortBy = '';
        toggleLocalSortDesc();
        sortChanged = true;
      }

      if (sortChanged) {
        // Sorting parameters changed
        this.$emit('sort-changed', this.context);
      }
    },
    // methods to compute classes and attrs for thead>th cells
    sortTheadThClasses: function sortTheadThClasses(key, field, isFoot) {
      return {
        // If sortable and sortIconLeft are true, then place sort icon on the left
        'b-table-sort-icon-left': field.sortable && this.sortIconLeft && !(isFoot && this.noFooterSorting)
      };
    },
    sortTheadThAttrs: function sortTheadThAttrs(key, field, isFoot) {
      if (!this.isSortable || isFoot && this.noFooterSorting) {
        // No attributes if not a sortable table
        return {};
      }

      var sortable = field.sortable; // Assemble the aria-sort attribute value

      var ariaSort = sortable && this.localSortBy === key ? this.localSortDesc ? 'descending' : 'ascending' : sortable ? 'none' : null; // Return the attribute

      return {
        'aria-sort': ariaSort
      };
    },
    sortTheadThLabel: function sortTheadThLabel(key, field, isFoot) {
      // A label to be placed in an `.sr-only` element in the header cell
      if (!this.isSortable || isFoot && this.noFooterSorting) {
        // No label if not a sortable table
        return null;
      }

      var sortable = field.sortable; // The correctness of these labels is very important for screen-reader users.

      var labelSorting = '';

      if (sortable) {
        if (this.localSortBy === key) {
          // currently sorted sortable column.
          labelSorting = this.localSortDesc ? this.labelSortAsc : this.labelSortDesc;
        } else {
          // Not currently sorted sortable column.
          // Not using nested ternary's here for clarity/readability
          // Default for ariaLabel
          labelSorting = this.localSortDesc ? this.labelSortDesc : this.labelSortAsc; // Handle sortDirection setting

          var sortDirection = this.sortDirection || field.sortDirection;

          if (sortDirection === 'asc') {
            labelSorting = this.labelSortAsc;
          } else if (sortDirection === 'desc') {
            labelSorting = this.labelSortDesc;
          }
        }
      } else if (!this.noSortReset) {
        // Non sortable column
        labelSorting = this.localSortBy ? this.labelSortClear : '';
      } // Return the sr-only sort label or null if no label


      return trim(labelSorting) || null;
    }
  }
};

function _defineProperty$N(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

// Mixin for providing stacked tables
var stackedMixin = {
  props: {
    stacked: {
      type: [Boolean, String],
      default: false
    }
  },
  computed: {
    isStacked: function isStacked() {
      // `true` when always stacked, or returns breakpoint specified
      return this.stacked === '' ? true : this.stacked;
    },
    isStackedAlways: function isStackedAlways() {
      return this.isStacked === true;
    },
    stackedTableClasses: function stackedTableClasses() {
      return _defineProperty$N({
        'b-table-stacked': this.isStackedAlways
      }, "b-table-stacked-".concat(this.stacked), !this.isStackedAlways && this.isStacked);
    }
  }
};

function ownKeys$A(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$A(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$A(Object(source), true).forEach(function (key) { _defineProperty$O(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$A(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$O(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Includes all main table styling options

var tableRendererMixin = {
  // Don't place attributes on root element automatically,
  // as table could be wrapped in responsive `<div>`
  inheritAttrs: false,
  // Mixin order is important!
  mixins: [attrsMixin],
  provide: function provide() {
    return {
      bvTable: this
    };
  },
  props: {
    striped: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    borderless: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    hover: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    },
    responsive: {
      type: [Boolean, String],
      default: false
    },
    stickyHeader: {
      // If a string, it is assumed to be the table `max-height` value
      type: [Boolean, String],
      default: false
    },
    noBorderCollapse: {
      type: Boolean,
      default: false
    },
    captionTop: {
      type: Boolean,
      default: false
    },
    tableVariant: {
      type: String // default: null

    },
    tableClass: {
      type: [String, Array, Object] // default: null

    }
  },
  computed: {
    // Layout related computed props
    isResponsive: function isResponsive() {
      var responsive = this.responsive === '' ? true : this.responsive;
      return this.isStacked ? false : responsive;
    },
    isStickyHeader: function isStickyHeader() {
      var stickyHeader = this.stickyHeader === '' ? true : this.stickyHeader;
      return this.isStacked ? false : stickyHeader;
    },
    wrapperClasses: function wrapperClasses() {
      return [this.isStickyHeader ? 'b-table-sticky-header' : '', this.isResponsive === true ? 'table-responsive' : this.isResponsive ? "table-responsive-".concat(this.responsive) : ''].filter(identity$1);
    },
    wrapperStyles: function wrapperStyles() {
      return this.isStickyHeader && !isBoolean(this.isStickyHeader) ? {
        maxHeight: this.isStickyHeader
      } : {};
    },
    tableClasses: function tableClasses() {
      var hover = this.isTableSimple ? this.hover : this.hover && this.computedItems.length > 0 && !this.computedBusy;
      return [// User supplied classes
      this.tableClass, // Styling classes
      {
        'table-striped': this.striped,
        'table-hover': hover,
        'table-dark': this.dark,
        'table-bordered': this.bordered,
        'table-borderless': this.borderless,
        'table-sm': this.small,
        // The following are b-table custom styles
        border: this.outlined,
        'b-table-fixed': this.fixed,
        'b-table-caption-top': this.captionTop,
        'b-table-no-border-collapse': this.noBorderCollapse
      }, this.tableVariant ? "".concat(this.dark ? 'bg' : 'table', "-").concat(this.tableVariant) : '', // Stacked table classes
      this.stackedTableClasses, // Selectable classes
      this.selectableTableClasses];
    },
    tableAttrs: function tableAttrs() {
      // Preserve user supplied aria-describedby, if provided in `$attrs`
      var adb = [(this.bvAttrs || {})['aria-describedby'], this.captionId].filter(identity$1).join(' ') || null;
      var items = this.computedItems;
      var filteredItems = this.filteredItems;
      var fields = this.computedFields;
      var selectableAttrs = this.selectableTableAttrs || {};
      var ariaAttrs = this.isTableSimple ? {} : {
        'aria-busy': this.computedBusy ? 'true' : 'false',
        'aria-colcount': toString$1(fields.length),
        'aria-describedby': adb
      };
      var rowCount = items && filteredItems && filteredItems.length > items.length ? toString$1(filteredItems.length) : null;
      return _objectSpread$A(_objectSpread$A(_objectSpread$A({
        // We set `aria-rowcount` before merging in `$attrs`,
        // in case user has supplied their own
        'aria-rowcount': rowCount
      }, this.bvAttrs), {}, {
        // Now we can override any `$attrs` here
        id: this.safeId(),
        role: 'table'
      }, ariaAttrs), selectableAttrs);
    }
  },
  render: function render(h) {
    var $content = [];

    if (this.isTableSimple) {
      $content.push(this.normalizeSlot());
    } else {
      // Build the `<caption>` (from caption mixin)
      $content.push(this.renderCaption ? this.renderCaption() : null); // Build the `<colgroup>`

      $content.push(this.renderColgroup ? this.renderColgroup() : null); // Build the `<thead>`

      $content.push(this.renderThead ? this.renderThead() : null); // Build the `<tbody>`

      $content.push(this.renderTbody ? this.renderTbody() : null); // Build the `<tfoot>`

      $content.push(this.renderTfoot ? this.renderTfoot() : null);
    } // Assemble `<table>`


    var $table = h('table', {
      key: 'b-table',
      staticClass: 'table b-table',
      class: this.tableClasses,
      attrs: this.tableAttrs
    }, $content.filter(identity$1)); // Add responsive/sticky wrapper if needed and return table

    return this.wrapperClasses.length > 0 ? h('div', {
      key: 'wrap',
      class: this.wrapperClasses,
      style: this.wrapperStyles
    }, [$table]) : $table;
  }
};

function ownKeys$B(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$B(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$B(Object(source), true).forEach(function (key) { _defineProperty$P(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$B(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$P(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$m = {
  tbodyTransitionProps: {
    type: Object // default: undefined

  },
  tbodyTransitionHandlers: {
    type: Object // default: undefined

  }
}; // TODO:
//   In Bootstrap v5, we won't need "sniffing" as table element variants properly inherit
//   to the child elements, so this can be converted to a functional component
// @vue/component

var BTbody = /*#__PURE__*/Vue.extend({
  name: NAME_TBODY,
  // Mixin order is important!
  mixins: [attrsMixin, listenersMixin, normalizeSlotMixin],
  inheritAttrs: false,
  provide: function provide() {
    return {
      bvTableRowGroup: this
    };
  },
  inject: {
    bvTable: {
      // Sniffed by <b-tr> / <b-td> / <b-th>

      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return {};
      }
    }
  },
  props: props$m,
  computed: {
    isTbody: function isTbody() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return true;
    },
    isDark: function isDark() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.dark;
    },
    isStacked: function isStacked() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.isStacked;
    },
    isResponsive: function isResponsive() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.isResponsive;
    },
    isStickyHeader: function isStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Sticky headers are only supported in thead
      return false;
    },
    hasStickyHeader: function hasStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      return !this.isStacked && this.bvTable.stickyHeader;
    },
    tableVariant: function tableVariant() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.tableVariant;
    },
    isTransitionGroup: function isTransitionGroup() {
      return this.tbodyTransitionProps || this.tbodyTransitionHandlers;
    },
    tbodyAttrs: function tbodyAttrs() {
      return _objectSpread$B({
        role: 'rowgroup'
      }, this.bvAttrs);
    },
    tbodyProps: function tbodyProps() {
      return this.tbodyTransitionProps ? _objectSpread$B(_objectSpread$B({}, this.tbodyTransitionProps), {}, {
        tag: 'tbody'
      }) : {};
    }
  },
  render: function render(h) {
    var data = {
      props: this.tbodyProps,
      attrs: this.tbodyAttrs
    };

    if (this.isTransitionGroup) {
      // We use native listeners if a transition group for any delegated events
      data.on = this.tbodyTransitionHandlers || {};
      data.nativeOn = this.bvListeners;
    } else {
      // Otherwise we place any listeners on the tbody element
      data.on = this.bvListeners;
    }

    return h(this.isTransitionGroup ? 'transition-group' : 'tbody', data, this.normalizeSlot());
  }
});

var TABLE_TAG_NAMES = ['TD', 'TH', 'TR']; // Returns `true` if we should ignore the click/double-click/keypress event
// Avoids having the user need to use `@click.stop` on the form control

var filterEvent = function filterEvent(evt) {
  // Exit early when we don't have a target element
  if (!evt || !evt.target) {
    /* istanbul ignore next */
    return false;
  }

  var el = evt.target; // Exit early when element is disabled or a table element

  if (el.disabled || TABLE_TAG_NAMES.indexOf(el.tagName) !== -1) {
    return false;
  } // Ignore the click when it was inside a dropdown menu


  if (closest('.dropdown-menu', el)) {
    return true;
  }

  var label = el.tagName === 'LABEL' ? el : closest('label', el); // If the label's form control is not disabled then we don't propagate event
  // Modern browsers have `label.control` that references the associated input, but IE 11
  // does not have this property on the label element, so we resort to DOM lookups

  if (label) {
    var labelFor = getAttr(label, 'for');
    var input = labelFor ? getById(labelFor) : select('input, select, textarea', label);

    if (input && !input.disabled) {
      return true;
    }
  } // Otherwise check if the event target matches one of the selectors in the
  // event filter (i.e. anchors, non disabled inputs, etc.)
  // Return `true` if we should ignore the event


  return matches(el, EVENT_FILTER);
};

// Used to filter out click events caused by the mouse up at end of selection
//
// Accepts an element as only argument to test to see if selection overlaps or is
// contained within the element

var textSelectionActive = function textSelectionActive() {
  var el = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  var sel = getSel();
  return sel && sel.toString().trim() !== '' && sel.containsNode && isElement(el) ?
  /* istanbul ignore next */
  sel.containsNode(el, true) : false;
};

//   In Bootstrap v5, we won't need "sniffing" as table element variants properly inherit
//   to the child elements, so this can be converted to a functional component
// @vue/component

var BTh = /*#__PURE__*/Vue.extend({
  name: NAME_TH,
  extends: BTd,
  computed: {
    tag: function tag() {
      return 'th';
    }
  }
});

function ownKeys$C(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$C(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$C(Object(source), true).forEach(function (key) { _defineProperty$Q(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$C(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$Q(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var detailsSlotName = 'row-details';
var tbodyRowMixin = {
  props: {
    tbodyTrClass: {
      type: [String, Array, Object, Function] // default: null

    },
    tbodyTrAttr: {
      type: [Object, Function] // default: null

    },
    detailsTdClass: {
      type: [String, Array, Object] // default: null

    }
  },
  methods: {
    // Methods for computing classes, attributes and styles for table cells
    getTdValues: function getTdValues(item, key, tdValue, defValue) {
      var parent = this.$parent;

      if (tdValue) {
        var value = get$1(item, key, '');

        if (isFunction$1(tdValue)) {
          return tdValue(value, key, item);
        } else if (isString(tdValue) && isFunction$1(parent[tdValue])) {
          return parent[tdValue](value, key, item);
        }

        return tdValue;
      }

      return defValue;
    },
    getThValues: function getThValues(item, key, thValue, type, defValue) {
      var parent = this.$parent;

      if (thValue) {
        var value = get$1(item, key, '');

        if (isFunction$1(thValue)) {
          return thValue(value, key, item, type);
        } else if (isString(thValue) && isFunction$1(parent[thValue])) {
          return parent[thValue](value, key, item, type);
        }

        return thValue;
      }

      return defValue;
    },
    // Method to get the value for a field
    getFormattedValue: function getFormattedValue(item, field) {
      var key = field.key;
      var formatter = this.getFieldFormatter(key);
      var value = get$1(item, key, null);

      if (isFunction$1(formatter)) {
        value = formatter(value, key, item);
      }

      return isUndefinedOrNull(value) ? '' : value;
    },
    // Factory function methods
    toggleDetailsFactory: function toggleDetailsFactory(hasDetailsSlot, item) {
      var _this = this;

      // Returns a function to toggle a row's details slot
      return function () {
        if (hasDetailsSlot) {
          _this.$set(item, '_showDetails', !item._showDetails);
        }
      };
    },
    // Row event handlers
    rowHovered: function rowHovered(evt) {
      // `mouseenter` handler (non-bubbling)
      // `this.tbodyRowEvtStopped` from tbody mixin
      if (!this.tbodyRowEvtStopped(evt)) {
        // `this.emitTbodyRowEvent` from tbody mixin
        this.emitTbodyRowEvent('row-hovered', evt);
      }
    },
    rowUnhovered: function rowUnhovered(evt) {
      // `mouseleave` handler (non-bubbling)
      // `this.tbodyRowEvtStopped` from tbody mixin
      if (!this.tbodyRowEvtStopped(evt)) {
        // `this.emitTbodyRowEvent` from tbody mixin
        this.emitTbodyRowEvent('row-unhovered', evt);
      }
    },
    // Render helpers
    renderTbodyRowCell: function renderTbodyRowCell(field, colIndex, item, rowIndex) {
      var _this2 = this;

      // Renders a TD or TH for a row's field
      var h = this.$createElement;
      var hasDetailsSlot = this.hasNormalizedSlot(detailsSlotName);
      var formatted = this.getFormattedValue(item, field);
      var key = field.key;
      var stickyColumn = !this.isStacked && (this.isResponsive || this.stickyHeader) && field.stickyColumn; // We only uses the helper components for sticky columns to
      // improve performance of BTable/BTableLite by reducing the
      // total number of vue instances created during render

      var cellTag = stickyColumn ? field.isRowHeader ? BTh : BTd : field.isRowHeader ? 'th' : 'td';
      var cellVariant = item._cellVariants && item._cellVariants[key] ? item._cellVariants[key] : field.variant || null;
      var data = {
        // For the Vue key, we concatenate the column index and
        // field key (as field keys could be duplicated)
        // TODO: Although we do prevent duplicate field keys...
        //   So we could change this to: `row-${rowIndex}-cell-${key}`
        key: "row-".concat(rowIndex, "-cell-").concat(colIndex, "-").concat(key),
        class: [field.class ? field.class : '', this.getTdValues(item, key, field.tdClass, '')],
        props: {},
        attrs: _objectSpread$C({
          'aria-colindex': String(colIndex + 1)
        }, field.isRowHeader ? this.getThValues(item, key, field.thAttr, 'row', {}) : this.getTdValues(item, key, field.tdAttr, {}))
      };

      if (stickyColumn) {
        // We are using the helper BTd or BTh
        data.props = {
          stackedHeading: this.isStacked ? field.label : null,
          stickyColumn: true,
          variant: cellVariant
        };
      } else {
        // Using native TD or TH element, so we need to
        // add in the attributes and variant class
        data.attrs['data-label'] = this.isStacked && !isUndefinedOrNull(field.label) ? toString$1(field.label) : null;
        data.attrs.role = field.isRowHeader ? 'rowheader' : 'cell';
        data.attrs.scope = field.isRowHeader ? 'row' : null; // Add in the variant class

        if (cellVariant) {
          data.class.push("".concat(this.dark ? 'bg' : 'table', "-").concat(cellVariant));
        }
      }

      var slotScope = {
        item: item,
        index: rowIndex,
        field: field,
        unformatted: get$1(item, key, ''),
        value: formatted,
        toggleDetails: this.toggleDetailsFactory(hasDetailsSlot, item),
        detailsShowing: Boolean(item._showDetails)
      }; // If table supports selectable mode, then add in the following scope
      // this.supportsSelectableRows will be undefined if mixin isn't loaded

      if (this.supportsSelectableRows) {
        slotScope.rowSelected = this.isRowSelected(rowIndex);

        slotScope.selectRow = function () {
          return _this2.selectRow(rowIndex);
        };

        slotScope.unselectRow = function () {
          return _this2.unselectRow(rowIndex);
        };
      } // The new `v-slot` syntax doesn't like a slot name starting with
      // a square bracket and if using in-document HTML templates, the
      // v-slot attributes are lower-cased by the browser.
      // Switched to round bracket syntax to prevent confusion with
      // dynamic slot name syntax.
      // We look for slots in this order: `cell(${key})`, `cell(${key.toLowerCase()})`, 'cell()'
      // Slot names are now cached by mixin tbody in `this.$_bodyFieldSlotNameCache`
      // Will be `null` if no slot (or fallback slot) exists


      var slotName = this.$_bodyFieldSlotNameCache[key];
      var $childNodes = slotName ? this.normalizeSlot(slotName, slotScope) : toString$1(formatted);

      if (this.isStacked) {
        // We wrap in a DIV to ensure rendered as a single cell when visually stacked!
        $childNodes = [h('div', [$childNodes])];
      } // Render either a td or th cell


      return h(cellTag, data, [$childNodes]);
    },
    renderTbodyRow: function renderTbodyRow(item, rowIndex) {
      var _this3 = this;

      // Renders an item's row (or rows if details supported)
      var h = this.$createElement;
      var fields = this.computedFields;
      var tableStriped = this.striped;
      var hasDetailsSlot = this.hasNormalizedSlot(detailsSlotName);
      var rowShowDetails = item._showDetails && hasDetailsSlot;
      var hasRowClickHandler = this.$listeners['row-clicked'] || this.hasSelectableRowClick; // We can return more than one TR if rowDetails enabled

      var $rows = []; // Details ID needed for `aria-details` when details showing
      // We set it to `null` when not showing so that attribute
      // does not appear on the element

      var detailsId = rowShowDetails ? this.safeId("_details_".concat(rowIndex, "_")) : null; // For each item data field in row

      var $tds = fields.map(function (field, colIndex) {
        return _this3.renderTbodyRowCell(field, colIndex, item, rowIndex);
      }); // Calculate the row number in the dataset (indexed from 1)

      var ariaRowIndex = null;

      if (this.currentPage && this.perPage && this.perPage > 0) {
        ariaRowIndex = String((this.currentPage - 1) * this.perPage + rowIndex + 1);
      } // Create a unique :key to help ensure that sub components are re-rendered rather than
      // re-used, which can cause issues. If a primary key is not provided we use the rendered
      // rows index within the tbody.
      // See: https://github.com/bootstrap-vue/bootstrap-vue/issues/2410


      var primaryKey = this.primaryKey;
      var primaryKeyValue = toString$1(get$1(item, primaryKey)) || null;
      var rowKey = primaryKeyValue || toString$1(rowIndex); // If primary key is provided, use it to generate a unique ID on each tbody > tr
      // In the format of '{tableId}__row_{primaryKeyValue}'

      var rowId = primaryKeyValue ? this.safeId("_row_".concat(primaryKeyValue)) : null; // Selectable classes and attributes

      var selectableClasses = this.selectableRowClasses ? this.selectableRowClasses(rowIndex) : {};
      var selectableAttrs = this.selectableRowAttrs ? this.selectableRowAttrs(rowIndex) : {}; // Additional classes and attributes

      var userTrClasses = isFunction$1(this.tbodyTrClass) ? this.tbodyTrClass(item, 'row') : this.tbodyTrClass;
      var userTrAttrs = isFunction$1(this.tbodyTrAttr) ?
      /* istanbul ignore next */
      this.tbodyTrAttr(item, 'row') : this.tbodyTrAttr; // Add the item row

      $rows.push(h(BTr, {
        key: "__b-table-row-".concat(rowKey, "__"),
        ref: 'itemRows',
        refInFor: true,
        class: [userTrClasses, selectableClasses, rowShowDetails ? 'b-table-has-details' : ''],
        props: {
          variant: item._rowVariant || null
        },
        attrs: _objectSpread$C(_objectSpread$C({
          id: rowId
        }, userTrAttrs), {}, {
          // Users cannot override the following attributes
          tabindex: hasRowClickHandler ? '0' : null,
          'data-pk': primaryKeyValue || null,
          'aria-details': detailsId,
          'aria-owns': detailsId,
          'aria-rowindex': ariaRowIndex
        }, selectableAttrs),
        on: {
          // Note: These events are not A11Y friendly!
          mouseenter: this.rowHovered,
          mouseleave: this.rowUnhovered
        }
      }, $tds)); // Row Details slot

      if (rowShowDetails) {
        var detailsScope = {
          item: item,
          index: rowIndex,
          fields: fields,
          toggleDetails: this.toggleDetailsFactory(hasDetailsSlot, item)
        }; // If table supports selectable mode, then add in the following scope
        // this.supportsSelectableRows will be undefined if mixin isn't loaded

        if (this.supportsSelectableRows) {
          detailsScope.rowSelected = this.isRowSelected(rowIndex);

          detailsScope.selectRow = function () {
            return _this3.selectRow(rowIndex);
          };

          detailsScope.unselectRow = function () {
            return _this3.unselectRow(rowIndex);
          };
        } // Render the details slot in a TD


        var $details = h(BTd, {
          props: {
            colspan: fields.length
          },
          class: this.detailsTdClass
        }, [this.normalizeSlot(detailsSlotName, detailsScope)]); // Add a hidden row to keep table row striping consistent when details showing
        // Only added if the table is striped

        if (tableStriped) {
          $rows.push( // We don't use `BTr` here as we don't need the extra functionality
          h('tr', {
            key: "__b-table-details-stripe__".concat(rowKey),
            staticClass: 'd-none',
            attrs: {
              'aria-hidden': 'true',
              role: 'presentation'
            }
          }));
        } // Add the actual details row


        var userDetailsTrClasses = isFunction$1(this.tbodyTrClass) ?
        /* istanbul ignore next */
        this.tbodyTrClass(item, detailsSlotName) : this.tbodyTrClass;
        var userDetailsTrAttrs = isFunction$1(this.tbodyTrAttr) ?
        /* istanbul ignore next */
        this.tbodyTrAttr(item, detailsSlotName) : this.tbodyTrAttr;
        $rows.push(h(BTr, {
          key: "__b-table-details__".concat(rowKey),
          staticClass: 'b-table-details',
          class: [userDetailsTrClasses],
          props: {
            variant: item._rowVariant || null
          },
          attrs: _objectSpread$C(_objectSpread$C({}, userDetailsTrAttrs), {}, {
            // Users cannot override the following attributes
            id: detailsId,
            tabindex: '-1'
          })
        }, [$details]));
      } else if (hasDetailsSlot) {
        // Only add the placeholder if a the table has a row-details slot defined (but not shown)
        $rows.push(h());

        if (tableStriped) {
          // Add extra placeholder if table is striped
          $rows.push(h());
        }
      } // Return the row(s)


      return $rows;
    }
  }
};

function ownKeys$D(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$D(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$D(Object(source), true).forEach(function (key) { _defineProperty$R(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$D(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$R(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var props$n = _objectSpread$D(_objectSpread$D({}, props$m), {}, {
  tbodyClass: {
    type: [String, Array, Object] // default: undefined

  }
});

var tbodyMixin = {
  mixins: [tbodyRowMixin],
  props: props$n,
  beforeDestroy: function beforeDestroy() {
    this.$_bodyFieldSlotNameCache = null;
  },
  methods: {
    // Helper methods
    getTbodyTrs: function getTbodyTrs() {
      // Returns all the item TR elements (excludes detail and spacer rows)
      // `this.$refs.itemRows` is an array of item TR components/elements
      // Rows should all be B-TR components, but we map to TR elements
      // Also note that `this.$refs.itemRows` may not always be in document order
      var refs = this.$refs || {};
      var tbody = refs.tbody ? refs.tbody.$el || refs.tbody : null;
      var trs = (refs.itemRows || []).map(function (tr) {
        return tr.$el || tr;
      });
      return tbody && tbody.children && tbody.children.length > 0 && trs && trs.length > 0 ? from(tbody.children).filter(function (tr) {
        return arrayIncludes(trs, tr);
      }) :
      /* istanbul ignore next */
      [];
    },
    getTbodyTrIndex: function getTbodyTrIndex(el) {
      // Returns index of a particular TBODY item TR
      // We set `true` on closest to include self in result

      /* istanbul ignore next: should not normally happen */
      if (!isElement(el)) {
        return -1;
      }

      var tr = el.tagName === 'TR' ? el : closest('tr', el, true);
      return tr ? this.getTbodyTrs().indexOf(tr) : -1;
    },
    emitTbodyRowEvent: function emitTbodyRowEvent(type, evt) {
      // Emits a row event, with the item object, row index and original event
      if (type && this.hasListener(type) && evt && evt.target) {
        var rowIndex = this.getTbodyTrIndex(evt.target);

        if (rowIndex > -1) {
          // The array of TRs correlate to the `computedItems` array
          var item = this.computedItems[rowIndex];
          this.$emit(type, item, rowIndex, evt);
        }
      }
    },
    tbodyRowEvtStopped: function tbodyRowEvtStopped(evt) {
      return this.stopIfBusy && this.stopIfBusy(evt);
    },
    // Delegated row event handlers
    onTbodyRowKeydown: function onTbodyRowKeydown(evt) {
      // Keyboard navigation and row click emulation
      var target = evt.target;

      if (this.tbodyRowEvtStopped(evt) || target.tagName !== 'TR' || !isActiveElement(target) || target.tabIndex !== 0) {
        // Early exit if not an item row TR
        return;
      }

      var keyCode = evt.keyCode;

      if (arrayIncludes([CODE_ENTER, CODE_SPACE], keyCode)) {
        // Emulated click for keyboard users, transfer to click handler
        stopEvent(evt);
        this.onTBodyRowClicked(evt);
      } else if (arrayIncludes([CODE_UP, CODE_DOWN, CODE_HOME, CODE_END], keyCode)) {
        // Keyboard navigation
        var rowIndex = this.getTbodyTrIndex(target);

        if (rowIndex > -1) {
          stopEvent(evt);
          var trs = this.getTbodyTrs();
          var shift = evt.shiftKey;

          if (keyCode === CODE_HOME || shift && keyCode === CODE_UP) {
            // Focus first row
            attemptFocus(trs[0]);
          } else if (keyCode === CODE_END || shift && keyCode === CODE_DOWN) {
            // Focus last row
            attemptFocus(trs[trs.length - 1]);
          } else if (keyCode === CODE_UP && rowIndex > 0) {
            // Focus previous row
            attemptFocus(trs[rowIndex - 1]);
          } else if (keyCode === CODE_DOWN && rowIndex < trs.length - 1) {
            // Focus next row
            attemptFocus(trs[rowIndex + 1]);
          }
        }
      }
    },
    onTBodyRowClicked: function onTBodyRowClicked(evt) {
      if (this.tbodyRowEvtStopped(evt)) {
        // If table is busy, then don't propagate
        return;
      } else if (filterEvent(evt) || textSelectionActive(this.$el)) {
        // Clicked on a non-disabled control so ignore
        // Or user is selecting text, so ignore
        return;
      }

      this.emitTbodyRowEvent('row-clicked', evt);
    },
    onTbodyRowMiddleMouseRowClicked: function onTbodyRowMiddleMouseRowClicked(evt) {
      if (!this.tbodyRowEvtStopped(evt) && evt.which === 2) {
        this.emitTbodyRowEvent('row-middle-clicked', evt);
      }
    },
    onTbodyRowContextmenu: function onTbodyRowContextmenu(evt) {
      if (!this.tbodyRowEvtStopped(evt)) {
        this.emitTbodyRowEvent('row-contextmenu', evt);
      }
    },
    onTbodyRowDblClicked: function onTbodyRowDblClicked(evt) {
      if (!this.tbodyRowEvtStopped(evt) && !filterEvent(evt)) {
        this.emitTbodyRowEvent('row-dblclicked', evt);
      }
    },
    // Note: Row hover handlers are handled by the tbody-row mixin
    // As mouseenter/mouseleave events do not bubble
    //
    // Render Helper
    renderTbody: function renderTbody() {
      var _this = this;

      // Render the tbody element and children
      var items = this.computedItems; // Shortcut to `createElement` (could use `this._c()` instead)

      var h = this.$createElement;
      var hasRowClickHandler = this.hasListener('row-clicked') || this.hasSelectableRowClick; // Prepare the tbody rows

      var $rows = []; // Add the item data rows or the busy slot

      var $busy = this.renderBusy ? this.renderBusy() : null;

      if ($busy) {
        // If table is busy and a busy slot, then return only the busy "row" indicator
        $rows.push($busy);
      } else {
        // Table isn't busy, or we don't have a busy slot
        // Create a slot cache for improved performance when looking up cell slot names
        // Values will be keyed by the field's `key` and will store the slot's name
        // Slots could be dynamic (i.e. `v-if`), so we must compute on each render
        // Used by tbody-row mixin render helper
        var cache = {};
        var defaultSlotName = this.hasNormalizedSlot('cell()') ? 'cell()' : null;
        this.computedFields.forEach(function (field) {
          var key = field.key;
          var fullName = "cell(".concat(key, ")");
          var lowerName = "cell(".concat(key.toLowerCase(), ")");
          cache[key] = _this.hasNormalizedSlot(fullName) ? fullName : _this.hasNormalizedSlot(lowerName) ?
          /* istanbul ignore next */
          lowerName : defaultSlotName;
        }); // Created as a non-reactive property so to not trigger component updates
        // Must be a fresh object each render

        this.$_bodyFieldSlotNameCache = cache; // Add static top row slot (hidden in visibly stacked mode
        // as we can't control `data-label` attr)

        $rows.push(this.renderTopRow ? this.renderTopRow() : h()); // Render the rows

        items.forEach(function (item, rowIndex) {
          // Render the individual item row (rows if details slot)
          $rows.push(_this.renderTbodyRow(item, rowIndex));
        }); // Empty items / empty filtered row slot (only shows if `items.length < 1`)

        $rows.push(this.renderEmpty ? this.renderEmpty() : h()); // Static bottom row slot (hidden in visibly stacked mode
        // as we can't control `data-label` attr)

        $rows.push(this.renderBottomRow ? this.renderBottomRow() : h());
      } // Note: these events will only emit if a listener is registered


      var handlers = {
        auxclick: this.onTbodyRowMiddleMouseRowClicked,
        // TODO:
        //   Perhaps we do want to automatically prevent the
        //   default context menu from showing if there is a
        //   `row-contextmenu` listener registered
        contextmenu: this.onTbodyRowContextmenu,
        // The following event(s) is not considered A11Y friendly
        dblclick: this.onTbodyRowDblClicked // Hover events (`mouseenter`/`mouseleave`) are handled by `tbody-row` mixin

      }; // Add in click/keydown listeners if needed

      if (hasRowClickHandler) {
        handlers.click = this.onTBodyRowClicked;
        handlers.keydown = this.onTbodyRowKeydown;
      } // Assemble rows into the tbody


      var $tbody = h(BTbody, {
        ref: 'tbody',
        class: this.tbodyClass || null,
        props: {
          tbodyTransitionProps: this.tbodyTransitionProps,
          tbodyTransitionHandlers: this.tbodyTransitionHandlers
        },
        // BTbody transfers all native event listeners to the root element
        // TODO: Only set the handlers if the table is not busy
        on: handlers
      }, $rows); // Return the assembled tbody

      return $tbody;
    }
  }
};

function ownKeys$E(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$E(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$E(Object(source), true).forEach(function (key) { _defineProperty$S(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$E(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$S(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$o = {
  footVariant: {
    type: String,
    // Supported values: 'lite', 'dark', or null
    default: null
  }
}; // TODO:
//   In Bootstrap v5, we won't need "sniffing" as table element variants properly inherit
//   to the child elements, so this can be converted to a functional component
// @vue/component

var BTfoot = /*#__PURE__*/Vue.extend({
  name: NAME_TFOOT,
  // Mixin order is important!
  mixins: [attrsMixin, listenersMixin, normalizeSlotMixin],
  inheritAttrs: false,
  provide: function provide() {
    return {
      bvTableRowGroup: this
    };
  },
  inject: {
    bvTable: {
      // Sniffed by <b-tr> / <b-td> / <b-th>

      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return {};
      }
    }
  },
  props: props$o,
  computed: {
    isTfoot: function isTfoot() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return true;
    },
    isDark: function isDark() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.dark;
    },
    isStacked: function isStacked() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.isStacked;
    },
    isResponsive: function isResponsive() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.isResponsive;
    },
    isStickyHeader: function isStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Sticky headers are only supported in thead
      return false;
    },
    hasStickyHeader: function hasStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      return !this.isStacked && this.bvTable.stickyHeader;
    },
    tableVariant: function tableVariant() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.tableVariant;
    },
    tfootClasses: function tfootClasses() {
      return [this.footVariant ? "thead-".concat(this.footVariant) : null];
    },
    tfootAttrs: function tfootAttrs() {
      return _objectSpread$E({
        role: 'rowgroup'
      }, this.bvAttrs);
    }
  },
  render: function render(h) {
    return h('tfoot', {
      class: this.tfootClasses,
      attrs: this.tfootAttrs,
      // Pass down any native listeners
      on: this.bvListeners
    }, this.normalizeSlot());
  }
});

var tfootMixin = {
  props: {
    footClone: {
      type: Boolean,
      default: false
    },
    footVariant: {
      type: String,
      // 'dark', 'light', or `null` (or custom)
      default: function _default() {
        return getComponentConfig(NAME_TABLE, 'footVariant');
      }
    },
    footRowVariant: {
      // Any Bootstrap theme variant (or custom). Falls back to `headRowVariant`
      type: String // default: null

    },
    tfootClass: {
      type: [String, Array, Object] // default: null

    },
    tfootTrClass: {
      type: [String, Array, Object] // default: null

    }
  },
  methods: {
    renderTFootCustom: function renderTFootCustom() {
      var h = this.$createElement;

      if (this.hasNormalizedSlot('custom-foot')) {
        return h(BTfoot, {
          key: 'bv-tfoot-custom',
          class: this.tfootClass || null,
          props: {
            footVariant: this.footVariant || this.headVariant || null
          }
        }, this.normalizeSlot('custom-foot', {
          items: this.computedItems.slice(),
          fields: this.computedFields.slice(),
          columns: this.computedFields.length
        }));
      } else {
        return h();
      }
    },
    renderTfoot: function renderTfoot() {
      // Passing true to renderThead will make it render a tfoot
      return this.footClone ? this.renderThead(true) : this.renderTFootCustom();
    }
  }
};

function ownKeys$F(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$F(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$F(Object(source), true).forEach(function (key) { _defineProperty$T(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$F(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$T(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var props$p = {
  headVariant: {
    // Also sniffed by <b-tr> / <b-td> / <b-th>
    type: String,
    // Supported values: 'lite', 'dark', or null
    default: null
  }
}; // TODO:
//   In Bootstrap v5, we won't need "sniffing" as table element variants properly inherit
//   to the child elements, so this can be converted to a functional component
// @vue/component

var BThead = /*#__PURE__*/Vue.extend({
  name: NAME_THEAD,
  // Mixin order is important!
  mixins: [attrsMixin, listenersMixin, normalizeSlotMixin],
  inheritAttrs: false,
  provide: function provide() {
    return {
      bvTableRowGroup: this
    };
  },
  inject: {
    bvTable: {
      // Sniffed by <b-tr> / <b-td> / <b-th>

      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return {};
      }
    }
  },
  props: props$p,
  computed: {
    isThead: function isThead() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return true;
    },
    isDark: function isDark() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.dark;
    },
    isStacked: function isStacked() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.isStacked;
    },
    isResponsive: function isResponsive() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.isResponsive;
    },
    isStickyHeader: function isStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      // Sticky headers only apply to cells in table `thead`
      return !this.isStacked && this.bvTable.stickyHeader;
    },
    hasStickyHeader: function hasStickyHeader() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      // Needed to handle header background classes, due to lack of
      // background color inheritance with Bootstrap v4 table CSS
      return !this.isStacked && this.bvTable.stickyHeader;
    },
    tableVariant: function tableVariant() {
      // Sniffed by <b-tr> / <b-td> / <b-th>
      return this.bvTable.tableVariant;
    },
    theadClasses: function theadClasses() {
      return [this.headVariant ? "thead-".concat(this.headVariant) : null];
    },
    theadAttrs: function theadAttrs() {
      return _objectSpread$F({
        role: 'rowgroup'
      }, this.bvAttrs);
    }
  },
  render: function render(h) {
    return h('thead', {
      class: this.theadClasses,
      attrs: this.theadAttrs,
      // Pass down any native listeners
      on: this.bvListeners
    }, this.normalizeSlot());
  }
});

function _toConsumableArray$3(arr) { return _arrayWithoutHoles$3(arr) || _iterableToArray$3(arr) || _unsupportedIterableToArray$3(arr) || _nonIterableSpread$3(); }

function _nonIterableSpread$3() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray$3(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray$3(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$3(o, minLen); }

function _iterableToArray$3(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles$3(arr) { if (Array.isArray(arr)) return _arrayLikeToArray$3(arr); }

function _arrayLikeToArray$3(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys$G(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$G(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$G(Object(source), true).forEach(function (key) { _defineProperty$U(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$G(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$U(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var theadMixin = {
  props: {
    headVariant: {
      type: String,
      // 'light', 'dark' or `null` (or custom)
      default: function _default() {
        return getComponentConfig(NAME_TABLE, 'headVariant');
      }
    },
    headRowVariant: {
      // Any Bootstrap theme variant (or custom)
      type: String // default: null

    },
    theadClass: {
      type: [String, Array, Object] // default: undefined

    },
    theadTrClass: {
      type: [String, Array, Object] // default: undefined

    }
  },
  methods: {
    fieldClasses: function fieldClasses(field) {
      // Header field (<th>) classes
      return [field.class ? field.class : '', field.thClass ? field.thClass : ''];
    },
    headClicked: function headClicked(evt, field, isFoot) {
      if (this.stopIfBusy && this.stopIfBusy(evt)) {
        // If table is busy (via provider) then don't propagate
        return;
      } else if (filterEvent(evt)) {
        // Clicked on a non-disabled control so ignore
        return;
      } else if (textSelectionActive(this.$el)) {
        // User is selecting text, so ignore

        /* istanbul ignore next: JSDOM doesn't support getSelection() */
        return;
      }

      stopEvent(evt);
      this.$emit('head-clicked', field.key, field, evt, isFoot);
    },
    renderThead: function renderThead() {
      var _this = this;

      var isFoot = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      var h = this.$createElement;
      var fields = this.computedFields || []; // In always stacked mode, we don't bother rendering the head/foot
      // Or if no field headings (empty table)

      if (this.isStackedAlways || fields.length === 0) {
        return h();
      }

      var isSortable = this.isSortable,
          isSelectable = this.isSelectable,
          headVariant = this.headVariant,
          footVariant = this.footVariant,
          headRowVariant = this.headRowVariant,
          footRowVariant = this.footRowVariant;
      var hasHeadClickListener = isSortable || this.hasListener('head-clicked'); // Reference to `selectAllRows` and `clearSelected()`, if table is selectable

      var selectAllRows = isSelectable ? this.selectAllRows : noop;
      var clearSelected = isSelectable ? this.clearSelected : noop; // Helper function to generate a field <th> cell

      var makeCell = function makeCell(field, colIndex) {
        var label = field.label,
            labelHtml = field.labelHtml,
            variant = field.variant,
            stickyColumn = field.stickyColumn,
            key = field.key;
        var ariaLabel = null;

        if (!field.label.trim() && !field.headerTitle) {
          // In case field's label and title are empty/blank
          // We need to add a hint about what the column is about for non-sighted users

          /* istanbul ignore next */
          ariaLabel = startCase(field.key);
        }

        var on = {};

        if (hasHeadClickListener) {
          on.click = function (evt) {
            _this.headClicked(evt, field, isFoot);
          };

          on.keydown = function (evt) {
            var keyCode = evt.keyCode;

            if (keyCode === CODE_ENTER || keyCode === CODE_SPACE) {
              _this.headClicked(evt, field, isFoot);
            }
          };
        }

        var sortAttrs = isSortable ? _this.sortTheadThAttrs(key, field, isFoot) : {};
        var sortClass = isSortable ? _this.sortTheadThClasses(key, field, isFoot) : null;
        var sortLabel = isSortable ? _this.sortTheadThLabel(key, field, isFoot) : null;
        var data = {
          class: [_this.fieldClasses(field), sortClass],
          props: {
            variant: variant,
            stickyColumn: stickyColumn
          },
          style: field.thStyle || {},
          attrs: _objectSpread$G(_objectSpread$G({
            // We only add a tabindex of 0 if there is a head-clicked listener
            tabindex: hasHeadClickListener ? '0' : null,
            abbr: field.headerAbbr || null,
            title: field.headerTitle || null,
            'aria-colindex': colIndex + 1,
            'aria-label': ariaLabel
          }, _this.getThValues(null, key, field.thAttr, isFoot ? 'foot' : 'head', {})), sortAttrs),
          on: on,
          key: key
        }; // Handle edge case where in-document templates are used with new
        // `v-slot:name` syntax where the browser lower-cases the v-slot's
        // name (attributes become lower cased when parsed by the browser)
        // We have replaced the square bracket syntax with round brackets
        // to prevent confusion with dynamic slot names

        var slotNames = ["head(".concat(key, ")"), "head(".concat(key.toLowerCase(), ")"), 'head()']; // Footer will fallback to header slot names

        if (isFoot) {
          slotNames = ["foot(".concat(key, ")"), "foot(".concat(key.toLowerCase(), ")"), 'foot()'].concat(_toConsumableArray$3(slotNames));
        }

        var scope = {
          label: label,
          column: key,
          field: field,
          isFoot: isFoot,
          // Add in row select methods
          selectAllRows: selectAllRows,
          clearSelected: clearSelected
        };
        var $content = _this.normalizeSlot(slotNames, scope) || h('div', {
          domProps: htmlOrText(labelHtml, label)
        });
        var $srLabel = sortLabel ? h('span', {
          staticClass: 'sr-only'
        }, " (".concat(sortLabel, ")")) : null; // Return the header cell

        return h(BTh, data, [$content, $srLabel].filter(identity$1));
      }; // Generate the array of <th> cells


      var $cells = fields.map(makeCell).filter(identity$1); // Generate the row(s)

      var $trs = [];

      if (isFoot) {
        $trs.push(h(BTr, {
          class: this.tfootTrClass,
          props: {
            variant: isUndefinedOrNull(footRowVariant) ? headRowVariant :
            /* istanbul ignore next */
            footRowVariant
          }
        }, $cells));
      } else {
        var scope = {
          columns: fields.length,
          fields: fields,
          // Add in row select methods
          selectAllRows: selectAllRows,
          clearSelected: clearSelected
        };
        $trs.push(this.normalizeSlot('thead-top', scope) || h());
        $trs.push(h(BTr, {
          class: this.theadTrClass,
          props: {
            variant: headRowVariant
          }
        }, $cells));
      }

      return h(isFoot ? BTfoot : BThead, {
        key: isFoot ? 'bv-tfoot' : 'bv-thead',
        class: (isFoot ? this.tfootClass : this.theadClass) || null,
        props: isFoot ? {
          footVariant: footVariant || headVariant || null
        } : {
          headVariant: headVariant || null
        }
      }, $trs);
    }
  }
};

var slotName$1 = 'top-row';
var topRowMixin = {
  methods: {
    renderTopRow: function renderTopRow() {
      var h = this.$createElement; // Add static Top Row slot (hidden in visibly stacked mode as we can't control the data-label)
      // If in *always* stacked mode, we don't bother rendering the row

      if (!this.hasNormalizedSlot(slotName$1) || this.stacked === true || this.stacked === '') {
        return h();
      }

      var fields = this.computedFields;
      return h(BTr, {
        key: 'b-top-row',
        staticClass: 'b-table-top-row',
        class: [isFunction$1(this.tbodyTrClass) ? this.tbodyTrClass(null, 'row-top') : this.tbodyTrClass],
        attrs: isFunction$1(this.tbodyTrAttr) ? this.tbodyTrAttr(null, 'row-top') : this.tbodyTrAttr
      }, [this.normalizeSlot(slotName$1, {
        columns: fields.length,
        fields: fields
      })]);
    }
  }
};

// @vue/component

var BTable = /*#__PURE__*/Vue.extend({
  name: NAME_TABLE,
  // Order of mixins is important!
  // They are merged from first to last, followed by this component
  mixins: [// General mixins
  attrsMixin, hasListenerMixin, idMixin, normalizeSlotMixin, // Required table mixins
  itemsMixin, tableRendererMixin, stackedMixin, theadMixin, tfootMixin, tbodyMixin, // Table features mixins
  stackedMixin, filteringMixin, sortingMixin, paginationMixin, captionMixin, colgroupMixin, selectableMixin, emptyMixin, topRowMixin, bottomRowMixin, busyMixin, providerMixin] // Render function is provided by table-renderer mixin

});

// @vue/component

var BTableSimple = /*#__PURE__*/Vue.extend({
  name: NAME_TABLE_SIMPLE,
  // Order of mixins is important!
  // They are merged from first to last, followed by this component.
  mixins: [// Required mixins
  idMixin, normalizeSlotMixin, tableRendererMixin, // feature mixin
  // Stacked requires extra handling by users via
  // the table cell `stacked-heading` prop
  stackedMixin],
  computed: {
    isTableSimple: function isTableSimple() {
      return true;
    }
  } // render function provided by table-renderer mixin

});

function ownKeys$H(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$H(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$H(Object(source), true).forEach(function (key) { _defineProperty$V(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$H(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$V(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BSkeletonTable = /*#__PURE__*/Vue.extend({
  name: NAME_SKELETON_TABLE,
  functional: true,
  props: {
    animation: {
      type: String
    },
    rows: {
      type: Number,
      default: 3,
      validator: function validator(value) {
        return value > 0;
      }
    },
    columns: {
      type: Number,
      default: 5,
      validator: function validator(value) {
        return value > 0;
      }
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    tableProps: {
      type: Object,
      default: function _default() {}
    }
  },
  render: function render(h, _ref) {
    var props = _ref.props;
    var animation = props.animation,
        columns = props.columns;
    var $th = h('th', [h(BSkeleton, {
      props: {
        animation: animation
      }
    })]);
    var $thTr = h('tr', createAndFillArray(columns, $th));
    var $td = h('td', [h(BSkeleton, {
      props: {
        width: '75%',
        animation: animation
      }
    })]);
    var $tdTr = h('tr', createAndFillArray(columns, $td));
    var $tbody = h('tbody', createAndFillArray(props.rows, $tdTr));
    var $thead = !props.hideHeader ? h('thead', [$thTr]) : h();
    var $tfoot = props.showFooter ? h('tfoot', [$thTr]) : h();
    return h(BTableSimple, {
      props: _objectSpread$H({}, props.tableProps)
    }, [$thead, $tbody, $tfoot]);
  }
});

function ownKeys$I(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$I(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$I(Object(source), true).forEach(function (key) { _defineProperty$W(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$I(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$W(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var navProps = omit(props$j, ['tabs', 'isNavBar', 'cardHeader']); // --- Helper methods ---
// Filter function to filter out disabled tabs

var notDisabled = function notDisabled(tab) {
  return !tab.disabled;
}; // --- Helper components ---
// @vue/component


var BVTabButton = /*#__PURE__*/Vue.extend({
  name: NAME_TAB_BUTTON_HELPER,
  inject: {
    bvTabs: {
      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return {};
      }
    }
  },
  props: {
    // Reference to the child <b-tab> instance
    tab: {
      default: null
    },
    tabs: {
      type: Array,

      /* istanbul ignore next */
      default: function _default()
      /* istanbul ignore next */
      {
        return [];
      }
    },
    id: {
      type: String,
      default: null
    },
    controls: {
      type: String,
      default: null
    },
    tabIndex: {
      type: Number,
      default: null
    },
    posInSet: {
      type: Number,
      default: null
    },
    setSize: {
      type: Number,
      default: null
    },
    noKeyNav: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    focus: function focus() {
      attemptFocus(this.$refs.link);
    },
    handleEvt: function handleEvt(evt) {
      if (this.tab.disabled) {
        /* istanbul ignore next */
        return;
      }

      var type = evt.type,
          keyCode = evt.keyCode,
          shiftKey = evt.shiftKey;

      if (type === 'click') {
        stopEvent(evt);
        this.$emit('click', evt);
      } else if (type === 'keydown' && keyCode === CODE_SPACE) {
        // For ARIA tabs the SPACE key will also trigger a click/select
        // Even with keyboard navigation disabled, SPACE should "click" the button
        // See: https://github.com/bootstrap-vue/bootstrap-vue/issues/4323
        stopEvent(evt);
        this.$emit('click', evt);
      } else if (type === 'keydown' && !this.noKeyNav) {
        // For keyboard navigation
        if ([CODE_UP, CODE_LEFT, CODE_HOME].indexOf(keyCode) !== -1) {
          stopEvent(evt);

          if (shiftKey || keyCode === CODE_HOME) {
            this.$emit('first', evt);
          } else {
            this.$emit('prev', evt);
          }
        } else if ([CODE_DOWN, CODE_RIGHT, CODE_END].indexOf(keyCode) !== -1) {
          stopEvent(evt);

          if (shiftKey || keyCode === CODE_END) {
            this.$emit('last', evt);
          } else {
            this.$emit('next', evt);
          }
        }
      }
    }
  },
  render: function render(h) {
    var id = this.id,
        tabIndex = this.tabIndex,
        setSize = this.setSize,
        posInSet = this.posInSet,
        controls = this.controls,
        handleEvt = this.handleEvt;
    var _this$tab = this.tab,
        title = _this$tab.title,
        localActive = _this$tab.localActive,
        disabled = _this$tab.disabled,
        titleItemClass = _this$tab.titleItemClass,
        titleLinkClass = _this$tab.titleLinkClass,
        titleLinkAttributes = _this$tab.titleLinkAttributes;
    var $link = h(BLink, {
      ref: 'link',
      staticClass: 'nav-link',
      class: [{
        active: localActive && !disabled,
        disabled: disabled
      }, titleLinkClass, // Apply <b-tabs> `activeNavItemClass` styles when the tab is active
      localActive ? this.bvTabs.activeNavItemClass : null],
      props: {
        disabled: disabled
      },
      attrs: _objectSpread$I(_objectSpread$I({}, titleLinkAttributes), {}, {
        role: 'tab',
        id: id,
        // Roving tab index when keynav enabled
        tabindex: tabIndex,
        'aria-selected': localActive && !disabled ? 'true' : 'false',
        'aria-setsize': setSize,
        'aria-posinset': posInSet,
        'aria-controls': controls
      }),
      on: {
        click: handleEvt,
        keydown: handleEvt
      }
    }, [this.tab.normalizeSlot(SLOT_NAME_TITLE) || title]);
    return h('li', {
      staticClass: 'nav-item',
      class: [titleItemClass],
      attrs: {
        role: 'presentation'
      }
    }, [$link]);
  }
}); // @vue/component

var BTabs = /*#__PURE__*/Vue.extend({
  name: NAME_TABS,
  mixins: [idMixin, normalizeSlotMixin],
  provide: function provide() {
    return {
      bvTabs: this
    };
  },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: _objectSpread$I(_objectSpread$I({}, navProps), {}, {
    tag: {
      type: String,
      default: 'div'
    },
    card: {
      type: Boolean,
      default: false
    },
    end: {
      // Synonym for 'bottom'
      type: Boolean,
      default: false
    },
    noFade: {
      type: Boolean,
      default: false
    },
    noNavStyle: {
      type: Boolean,
      default: false
    },
    noKeyNav: {
      type: Boolean,
      default: false
    },
    lazy: {
      // This prop is sniffed by the <b-tab> child
      type: Boolean,
      default: false
    },
    contentClass: {
      type: [String, Array, Object] // default: null

    },
    navClass: {
      type: [String, Array, Object] // default: null

    },
    navWrapperClass: {
      type: [String, Array, Object] // default: null

    },
    activeNavItemClass: {
      // Only applied to the currently active <b-nav-item>
      type: [String, Array, Object] // default: null

    },
    activeTabClass: {
      // Only applied to the currently active <b-tab>
      // This prop is sniffed by the <b-tab> child
      type: [String, Array, Object] // default: null

    },
    value: {
      // v-model
      type: Number,
      default: null
    }
  }),
  data: function data() {
    return {
      // Index of current tab
      currentTab: toInteger(this.value, -1),
      // Array of direct child <b-tab> instances, in DOM order
      tabs: [],
      // Array of child instances registered (for triggering reactive updates)
      registeredTabs: [],
      // Flag to know if we are mounted or not
      isMounted: false
    };
  },
  computed: {
    fade: function fade() {
      // This computed prop is sniffed by the tab child
      return !this.noFade;
    },
    localNavClass: function localNavClass() {
      var classes = [];

      if (this.card && this.vertical) {
        classes.push('card-header', 'h-100', 'border-bottom-0', 'rounded-0');
      }

      return [].concat(classes, [this.navClass]);
    }
  },
  watch: {
    currentTab: function currentTab(newVal) {
      var index = -1; // Ensure only one tab is active at most

      this.tabs.forEach(function (tab, idx) {
        if (newVal === idx && !tab.disabled) {
          tab.localActive = true;
          index = idx;
        } else {
          tab.localActive = false;
        }
      }); // Update the v-model

      this.$emit('input', index);
    },
    value: function value(newVal, oldVal) {
      if (newVal !== oldVal) {
        newVal = toInteger(newVal, -1);
        oldVal = toInteger(oldVal, 0);
        var tabs = this.tabs;

        if (tabs[newVal] && !tabs[newVal].disabled) {
          this.activateTab(tabs[newVal]);
        } else {
          // Try next or prev tabs
          if (newVal < oldVal) {
            this.previousTab();
          } else {
            this.nextTab();
          }
        }
      }
    },
    registeredTabs: function registeredTabs() {
      var _this = this;

      // Each b-tab will register/unregister itself.
      // We use this to detect when tabs are added/removed
      // to trigger the update of the tabs.
      this.$nextTick(function () {
        requestAF(function () {
          _this.updateTabs();
        });
      });
    },
    tabs: function tabs(newVal, oldVal) {
      var _this2 = this;

      // If tabs added, removed, or re-ordered, we emit a `changed` event.
      // We use `tab._uid` instead of `tab.safeId()`, as the later is changed
      // in a nextTick if no explicit ID is provided, causing duplicate emits.
      if (!looseEqual(newVal.map(function (t) {
        return t._uid;
      }), oldVal.map(function (t) {
        return t._uid;
      }))) {
        // In a nextTick to ensure currentTab has been set first.
        this.$nextTick(function () {
          // We emit shallow copies of the new and old arrays of tabs, to
          // prevent users from potentially mutating the internal arrays.
          _this2.$emit('changed', newVal.slice(), oldVal.slice());
        });
      }
    },
    isMounted: function isMounted(newVal) {
      var _this3 = this;

      // Trigger an update after mounted.  Needed for tabs inside lazy modals.
      if (newVal) {
        requestAF(function () {
          _this3.updateTabs();
        });
      } // Enable or disable the observer


      this.setObserver(newVal);
    }
  },
  created: function created() {
    var _this4 = this;

    // Create private non-reactive props
    this.$_observer = null;
    this.currentTab = toInteger(this.value, -1); // For SSR and to make sure only a single tab is shown on mount
    // We wrap this in a `$nextTick()` to ensure the child tabs have been created

    this.$nextTick(function () {
      _this4.updateTabs();
    });
  },
  mounted: function mounted() {
    var _this5 = this;

    // Call `updateTabs()` just in case...
    this.updateTabs();
    this.$nextTick(function () {
      // Flag we are now mounted and to switch to DOM for tab probing.
      // As this.$slots.default appears to lie about component instances
      // after b-tabs is destroyed and re-instantiated.
      // And this.$children does not respect DOM order.
      _this5.isMounted = true;
    });
  },

  /* istanbul ignore next */
  deactivated: function deactivated()
  /* istanbul ignore next */
  {
    this.isMounted = false;
  },

  /* istanbul ignore next */
  activated: function activated()
  /* istanbul ignore next */
  {
    var _this6 = this;

    this.currentTab = toInteger(this.value, -1);
    this.$nextTick(function () {
      _this6.updateTabs();

      _this6.isMounted = true;
    });
  },
  beforeDestroy: function beforeDestroy() {
    this.isMounted = false;
  },
  destroyed: function destroyed() {
    // Ensure no references to child instances exist
    this.tabs = [];
  },
  methods: {
    registerTab: function registerTab(tab) {
      var _this7 = this;

      if (!arrayIncludes(this.registeredTabs, tab)) {
        this.registeredTabs.push(tab);
        tab.$once('hook:destroyed', function () {
          _this7.unregisterTab(tab);
        });
      }
    },
    unregisterTab: function unregisterTab(tab) {
      this.registeredTabs = this.registeredTabs.slice().filter(function (t) {
        return t !== tab;
      });
    },
    // DOM observer is needed to detect changes in order of tabs
    setObserver: function setObserver(on) {
      this.$_observer && this.$_observer.disconnect();
      this.$_observer = null;

      if (on) {
        var self = this;
        /* istanbul ignore next: difficult to test mutation observer in JSDOM */

        var handler = function handler() {
          // We delay the update to ensure that `tab.safeId()` has
          // updated with the final ID value.
          self.$nextTick(function () {
            requestAF(function () {
              self.updateTabs();
            });
          });
        }; // Watch for changes to <b-tab> sub components


        this.$_observer = observeDom(this.$refs.tabsContainer, handler, {
          childList: true,
          subtree: false,
          attributes: true,
          attributeFilter: ['id']
        });
      }
    },
    getTabs: function getTabs() {
      // We use `registeredTabs` as the source of truth for child tab components
      // We also filter out any `<b-tab>` components that are extended
      // `<b-tab>` with a root child `<b-tab>`
      // See: https://github.com/bootstrap-vue/bootstrap-vue/issues/3260
      var tabs = this.registeredTabs.filter(function (tab) {
        return tab.$children.filter(function (t) {
          return t._isTab;
        }).length === 0;
      }); // DOM Order of Tabs

      var order = [];

      if (this.isMounted && tabs.length > 0) {
        // We rely on the DOM when mounted to get the 'true' order of the `<b-tab>` children
        // `querySelectorAll()` always returns elements in document order, regardless of
        // order specified in the selector
        var selector = tabs.map(function (tab) {
          return "#".concat(tab.safeId());
        }).join(', ');
        order = selectAll(selector, this.$el).map(function (el) {
          return el.id;
        }).filter(identity$1);
      } // Stable sort keeps the original order if not found in the `order` array,
      // which will be an empty array before mount


      return stableSort(tabs, function (a, b) {
        return order.indexOf(a.safeId()) - order.indexOf(b.safeId());
      });
    },
    // Update list of `<b-tab>` children
    updateTabs: function updateTabs() {
      // Probe tabs
      var tabs = this.getTabs(); // Find *last* active non-disabled tab in current tabs
      // We trust tab state over `currentTab`, in case tabs were added/removed/re-ordered

      var tabIndex = tabs.indexOf(tabs.slice().reverse().find(function (tab) {
        return tab.localActive && !tab.disabled;
      })); // Else try setting to `currentTab`

      if (tabIndex < 0) {
        var currentTab = this.currentTab;

        if (currentTab >= tabs.length) {
          // Handle last tab being removed, so find the last non-disabled tab
          tabIndex = tabs.indexOf(tabs.slice().reverse().find(notDisabled));
        } else if (tabs[currentTab] && !tabs[currentTab].disabled) {
          // Current tab is not disabled
          tabIndex = currentTab;
        }
      } // Else find *first* non-disabled tab in current tabs


      if (tabIndex < 0) {
        tabIndex = tabs.indexOf(tabs.find(notDisabled));
      } // Set the current tab state to active


      tabs.forEach(function (tab) {
        // tab.localActive = idx === tabIndex && !tab.disabled
        tab.localActive = false;
      });

      if (tabs[tabIndex]) {
        tabs[tabIndex].localActive = true;
      } // Update the array of tab children


      this.tabs = tabs; // Set the currentTab index (can be -1 if no non-disabled tabs)

      this.currentTab = tabIndex;
    },
    // Find a button that controls a tab, given the tab reference
    // Returns the button vm instance
    getButtonForTab: function getButtonForTab(tab) {
      return (this.$refs.buttons || []).find(function (btn) {
        return btn.tab === tab;
      });
    },
    // Force a button to re-render its content, given a <b-tab> instance
    // Called by <b-tab> on `update()`
    updateButton: function updateButton(tab) {
      var button = this.getButtonForTab(tab);

      if (button && button.$forceUpdate) {
        button.$forceUpdate();
      }
    },
    // Activate a tab given a `<b-tab>` instance
    // Also accessed by `<b-tab>`
    activateTab: function activateTab(tab) {
      var result = false;

      if (tab) {
        var index = this.tabs.indexOf(tab);

        if (!tab.disabled && index > -1 && index !== this.currentTab) {
          var tabEvt = new BvEvent('activate-tab', {
            cancelable: true,
            vueTarget: this,
            componentId: this.safeId()
          });
          this.$emit(tabEvt.type, index, this.currentTab, tabEvt);

          if (!tabEvt.defaultPrevented) {
            result = true;
            this.currentTab = index;
          }
        }
      } // Couldn't set tab, so ensure v-model is set to `this.currentTab`

      /* istanbul ignore next: should rarely happen */


      if (!result && this.currentTab !== this.value) {
        this.$emit('input', this.currentTab);
      }

      return result;
    },
    // Deactivate a tab given a <b-tab> instance
    // Accessed by <b-tab>
    deactivateTab: function deactivateTab(tab) {
      if (tab) {
        // Find first non-disabled tab that isn't the one being deactivated
        // If no tabs are available, then don't deactivate current tab
        return this.activateTab(this.tabs.filter(function (t) {
          return t !== tab;
        }).find(notDisabled));
      }
      /* istanbul ignore next: should never/rarely happen */


      return false;
    },
    // Focus a tab button given its <b-tab> instance
    focusButton: function focusButton(tab) {
      var _this8 = this;

      // Wrap in `$nextTick()` to ensure DOM has completed rendering/updating before focusing
      this.$nextTick(function () {
        attemptFocus(_this8.getButtonForTab(tab));
      });
    },
    // Emit a click event on a specified <b-tab> component instance
    emitTabClick: function emitTabClick(tab, evt) {
      if (isEvent(evt) && tab && tab.$emit && !tab.disabled) {
        tab.$emit('click', evt);
      }
    },
    // Click handler
    clickTab: function clickTab(tab, evt) {
      this.activateTab(tab);
      this.emitTabClick(tab, evt);
    },
    // Move to first non-disabled tab
    firstTab: function firstTab(focus) {
      var tab = this.tabs.find(notDisabled);

      if (this.activateTab(tab) && focus) {
        this.focusButton(tab);
        this.emitTabClick(tab, focus);
      }
    },
    // Move to previous non-disabled tab
    previousTab: function previousTab(focus) {
      var currentIndex = mathMax(this.currentTab, 0);
      var tab = this.tabs.slice(0, currentIndex).reverse().find(notDisabled);

      if (this.activateTab(tab) && focus) {
        this.focusButton(tab);
        this.emitTabClick(tab, focus);
      }
    },
    // Move to next non-disabled tab
    nextTab: function nextTab(focus) {
      var currentIndex = mathMax(this.currentTab, -1);
      var tab = this.tabs.slice(currentIndex + 1).find(notDisabled);

      if (this.activateTab(tab) && focus) {
        this.focusButton(tab);
        this.emitTabClick(tab, focus);
      }
    },
    // Move to last non-disabled tab
    lastTab: function lastTab(focus) {
      var tab = this.tabs.slice().reverse().find(notDisabled);

      if (this.activateTab(tab) && focus) {
        this.focusButton(tab);
        this.emitTabClick(tab, focus);
      }
    }
  },
  render: function render(h) {
    var _this9 = this;

    var tabs = this.tabs,
        noKeyNav = this.noKeyNav,
        firstTab = this.firstTab,
        previousTab = this.previousTab,
        nextTab = this.nextTab,
        lastTab = this.lastTab; // Currently active tab

    var activeTab = tabs.find(function (tab) {
      return tab.localActive && !tab.disabled;
    }); // Tab button to allow focusing when no active tab found (keynav only)

    var fallbackTab = tabs.find(function (tab) {
      return !tab.disabled;
    }); // For each `<b-tab>` found create the tab buttons

    var buttons = tabs.map(function (tab, index) {
      var tabIndex = null; // Ensure at least one tab button is focusable when keynav enabled (if possible)

      if (!noKeyNav) {
        // Buttons are not in tab index unless active, or a fallback tab
        tabIndex = -1;

        if (activeTab === tab || !activeTab && fallbackTab === tab) {
          // Place tab button in tab sequence
          tabIndex = null;
        }
      }

      return h(BVTabButton, {
        key: tab._uid || index,
        ref: 'buttons',
        // Needed to make `this.$refs.buttons` an array
        refInFor: true,
        props: {
          tab: tab,
          tabs: tabs,
          id: tab.controlledBy || (tab.safeId ? tab.safeId("_BV_tab_button_") : null),
          controls: tab.safeId ? tab.safeId() : null,
          tabIndex: tabIndex,
          setSize: tabs.length,
          posInSet: index + 1,
          noKeyNav: noKeyNav
        },
        on: {
          click: function click(evt) {
            _this9.clickTab(tab, evt);
          },
          first: firstTab,
          prev: previousTab,
          next: nextTab,
          last: lastTab
        }
      });
    }); // Nav

    var nav = h(BNav, {
      ref: 'nav',
      class: this.localNavClass,
      attrs: {
        role: 'tablist',
        id: this.safeId('_BV_tab_controls_')
      },
      props: {
        fill: this.fill,
        justified: this.justified,
        align: this.align,
        tabs: !this.noNavStyle && !this.pills,
        pills: !this.noNavStyle && this.pills,
        vertical: this.vertical,
        small: this.small,
        cardHeader: this.card && !this.vertical
      }
    }, [this.normalizeSlot('tabs-start') || h(), buttons, this.normalizeSlot('tabs-end') || h()]);
    nav = h('div', {
      key: 'bv-tabs-nav',
      class: [{
        'card-header': this.card && !this.vertical && !this.end,
        'card-footer': this.card && !this.vertical && this.end,
        'col-auto': this.vertical
      }, this.navWrapperClass]
    }, [nav]);
    var empty = h();

    if (!tabs || tabs.length === 0) {
      empty = h('div', {
        key: 'bv-empty-tab',
        class: ['tab-pane', 'active', {
          'card-body': this.card
        }]
      }, this.normalizeSlot('empty'));
    } // Main content section


    var content = h('div', {
      ref: 'tabsContainer',
      key: 'bv-tabs-container',
      staticClass: 'tab-content',
      class: [{
        col: this.vertical
      }, this.contentClass],
      attrs: {
        id: this.safeId('_BV_tab_container_')
      }
    }, concat(this.normalizeSlot(), empty)); // Render final output

    return h(this.tag, {
      staticClass: 'tabs',
      class: {
        row: this.vertical,
        'no-gutters': this.vertical && this.card
      },
      attrs: {
        id: this.safeId()
      }
    }, [this.end ? content : h(), [nav], this.end ? h() : content]);
  }
});

var BTab = /*#__PURE__*/Vue.extend({
  name: NAME_TAB,
  mixins: [idMixin, normalizeSlotMixin],
  inject: {
    bvTabs: {
      default: function _default() {
        return {};
      }
    }
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    tag: {
      type: String,
      default: 'div'
    },
    buttonId: {
      type: String // default: ''

    },
    title: {
      type: String,
      default: ''
    },
    titleItemClass: {
      // Sniffed by `<b-tabs>` and added to nav `li.nav-item`
      type: [String, Array, Object] // default: null

    },
    titleLinkClass: {
      // Sniffed by `<b-tabs>` and added to nav `a.nav-link`
      type: [String, Array, Object] // default: null

    },
    titleLinkAttributes: {
      type: Object // default: null

    },
    disabled: {
      type: Boolean,
      default: false
    },
    noBody: {
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    return {
      localActive: this.active && !this.disabled,
      show: false
    };
  },
  computed: {
    tabClasses: function tabClasses() {
      return [{
        active: this.localActive,
        disabled: this.disabled,
        'card-body': this.bvTabs.card && !this.noBody
      }, // Apply <b-tabs> `activeTabClass` styles when this tab is active
      this.localActive ? this.bvTabs.activeTabClass : null];
    },
    controlledBy: function controlledBy() {
      return this.buttonId || this.safeId('__BV_tab_button__');
    },
    computedNoFade: function computedNoFade() {
      return !(this.bvTabs.fade || false);
    },
    computedLazy: function computedLazy() {
      return this.bvTabs.lazy || this.lazy;
    },
    // For parent sniffing of child
    _isTab: function _isTab() {
      return true;
    }
  },
  watch: {
    localActive: function localActive(newValue) {
      // Make `active` prop work with `.sync` modifier
      this.$emit('update:active', newValue);
    },
    active: function active(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          // If activated post mount
          this.activate();
        } else {
          /* istanbul ignore next */
          if (!this.deactivate()) {
            // Tab couldn't be deactivated, so we reset the synced active prop
            // Deactivation will fail if no other tabs to activate
            this.$emit('update:active', this.localActive);
          }
        }
      }
    },
    disabled: function disabled(newValue, oldValue) {
      if (newValue !== oldValue) {
        var firstTab = this.bvTabs.firstTab;

        if (newValue && this.localActive && firstTab) {
          this.localActive = false;
          firstTab();
        }
      }
    }
  },
  mounted: function mounted() {
    // Inform b-tabs of our presence
    this.registerTab(); // Initially show on mount if active and not disabled

    this.show = this.localActive;
  },
  updated: function updated() {
    // Force the tab button content to update (since slots are not reactive)
    // Only done if we have a title slot, as the title prop is reactive
    var updateButton = this.bvTabs.updateButton;

    if (updateButton && this.hasNormalizedSlot(SLOT_NAME_TITLE)) {
      updateButton(this);
    }
  },
  destroyed: function destroyed() {
    // inform b-tabs of our departure
    this.unregisterTab();
  },
  methods: {
    // Private methods
    registerTab: function registerTab() {
      // Inform `<b-tabs>` of our presence
      var registerTab = this.bvTabs.registerTab;

      if (registerTab) {
        registerTab(this);
      }
    },
    unregisterTab: function unregisterTab() {
      // Inform `<b-tabs>` of our departure
      var unregisterTab = this.bvTabs.unregisterTab;

      if (unregisterTab) {
        unregisterTab(this);
      }
    },
    // Public methods
    activate: function activate() {
      // Not inside a `<b-tabs>` component or tab is disabled
      var activateTab = this.bvTabs.activateTab;
      return activateTab && !this.disabled ? activateTab(this) : false;
    },
    deactivate: function deactivate() {
      // Not inside a `<b-tabs>` component or not active to begin with
      var deactivateTab = this.bvTabs.deactivateTab;
      return deactivateTab && this.localActive ? deactivateTab(this) : false;
    }
  },
  render: function render(h) {
    var localActive = this.localActive;
    var $content = h(this.tag, {
      ref: 'panel',
      staticClass: 'tab-pane',
      class: this.tabClasses,
      directives: [{
        name: 'show',
        rawName: 'v-show',
        value: localActive,
        expression: 'localActive'
      }],
      attrs: {
        role: 'tabpanel',
        id: this.safeId(),
        'aria-hidden': localActive ? 'false' : 'true',
        'aria-labelledby': this.controlledBy || null
      }
    }, // Render content lazily if requested
    [localActive || !this.computedLazy ? this.normalizeSlot() : h()]);
    return h(BVTransition, {
      props: {
        mode: 'out-in',
        noFade: this.computedNoFade
      }
    }, [$content]);
  }
});

function _typeof$3(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof$3 = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof$3 = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof$3(obj);
}

function _toConsumableArray$4(arr) {
  return _arrayWithoutHoles$4(arr) || _iterableToArray$4(arr) || _nonIterableSpread$4();
}

function _arrayWithoutHoles$4(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _iterableToArray$4(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _nonIterableSpread$4() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

var inBrowser = typeof window !== 'undefined';
function freeze$1(item) {
  if (Array.isArray(item) || _typeof$3(item) === 'object') {
    return Object.freeze(item);
  }

  return item;
}
function combinePassengers(transports) {
  var slotProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return transports.reduce(function (passengers, transport) {
    var temp = transport.passengers[0];
    var newPassengers = typeof temp === 'function' ? temp(slotProps) : transport.passengers;
    return passengers.concat(newPassengers);
  }, []);
}
function stableSort$1(array, compareFn) {
  return array.map(function (v, idx) {
    return [idx, v];
  }).sort(function (a, b) {
    return compareFn(a[1], b[1]) || a[0] - b[0];
  }).map(function (c) {
    return c[1];
  });
}
function pick$1(obj, keys) {
  return keys.reduce(function (acc, key) {
    if (obj.hasOwnProperty(key)) {
      acc[key] = obj[key];
    }

    return acc;
  }, {});
}

var transports = {};
var targets = {};
var sources = {};
var Wormhole = Vue.extend({
  data: function data() {
    return {
      transports: transports,
      targets: targets,
      sources: sources,
      trackInstances: inBrowser
    };
  },
  methods: {
    open: function open(transport) {
      if (!inBrowser) return;
      var to = transport.to,
          from = transport.from,
          passengers = transport.passengers,
          _transport$order = transport.order,
          order = _transport$order === void 0 ? Infinity : _transport$order;
      if (!to || !from || !passengers) return;
      var newTransport = {
        to: to,
        from: from,
        passengers: freeze$1(passengers),
        order: order
      };
      var keys = Object.keys(this.transports);

      if (keys.indexOf(to) === -1) {
        Vue.set(this.transports, to, []);
      }

      var currentIndex = this.$_getTransportIndex(newTransport); // Copying the array here so that the PortalTarget change event will actually contain two distinct arrays

      var newTransports = this.transports[to].slice(0);

      if (currentIndex === -1) {
        newTransports.push(newTransport);
      } else {
        newTransports[currentIndex] = newTransport;
      }

      this.transports[to] = stableSort$1(newTransports, function (a, b) {
        return a.order - b.order;
      });
    },
    close: function close(transport) {
      var force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var to = transport.to,
          from = transport.from;
      if (!to || !from && force === false) return;

      if (!this.transports[to]) {
        return;
      }

      if (force) {
        this.transports[to] = [];
      } else {
        var index = this.$_getTransportIndex(transport);

        if (index >= 0) {
          // Copying the array here so that the PortalTarget change event will actually contain two distinct arrays
          var newTransports = this.transports[to].slice(0);
          newTransports.splice(index, 1);
          this.transports[to] = newTransports;
        }
      }
    },
    registerTarget: function registerTarget(target, vm, force) {
      if (!inBrowser) return;

      if (this.trackInstances && !force && this.targets[target]) {
        console.warn("[portal-vue]: Target ".concat(target, " already exists"));
      }

      this.$set(this.targets, target, Object.freeze([vm]));
    },
    unregisterTarget: function unregisterTarget(target) {
      this.$delete(this.targets, target);
    },
    registerSource: function registerSource(source, vm, force) {
      if (!inBrowser) return;

      if (this.trackInstances && !force && this.sources[source]) {
        console.warn("[portal-vue]: source ".concat(source, " already exists"));
      }

      this.$set(this.sources, source, Object.freeze([vm]));
    },
    unregisterSource: function unregisterSource(source) {
      this.$delete(this.sources, source);
    },
    hasTarget: function hasTarget(to) {
      return !!(this.targets[to] && this.targets[to][0]);
    },
    hasSource: function hasSource(to) {
      return !!(this.sources[to] && this.sources[to][0]);
    },
    hasContentFor: function hasContentFor(to) {
      return !!this.transports[to] && !!this.transports[to].length;
    },
    // Internal
    $_getTransportIndex: function $_getTransportIndex(_ref) {
      var to = _ref.to,
          from = _ref.from;

      for (var i in this.transports[to]) {
        if (this.transports[to][i].from === from) {
          return +i;
        }
      }

      return -1;
    }
  }
});
var wormhole = new Wormhole(transports);

var _id = 1;
var Portal = Vue.extend({
  name: 'portal',
  props: {
    disabled: {
      type: Boolean
    },
    name: {
      type: String,
      default: function _default() {
        return String(_id++);
      }
    },
    order: {
      type: Number,
      default: 0
    },
    slim: {
      type: Boolean
    },
    slotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    tag: {
      type: String,
      default: 'DIV'
    },
    to: {
      type: String,
      default: function _default() {
        return String(Math.round(Math.random() * 10000000));
      }
    }
  },
  created: function created() {
    var _this = this;

    this.$nextTick(function () {
      wormhole.registerSource(_this.name, _this);
    });
  },
  mounted: function mounted() {
    if (!this.disabled) {
      this.sendUpdate();
    }
  },
  updated: function updated() {
    if (this.disabled) {
      this.clear();
    } else {
      this.sendUpdate();
    }
  },
  beforeDestroy: function beforeDestroy() {
    wormhole.unregisterSource(this.name);
    this.clear();
  },
  watch: {
    to: function to(newValue, oldValue) {
      oldValue && oldValue !== newValue && this.clear(oldValue);
      this.sendUpdate();
    }
  },
  methods: {
    clear: function clear(target) {
      var closer = {
        from: this.name,
        to: target || this.to
      };
      wormhole.close(closer);
    },
    normalizeSlots: function normalizeSlots() {
      return this.$scopedSlots.default ? [this.$scopedSlots.default] : this.$slots.default;
    },
    normalizeOwnChildren: function normalizeOwnChildren(children) {
      return typeof children === 'function' ? children(this.slotProps) : children;
    },
    sendUpdate: function sendUpdate() {
      var slotContent = this.normalizeSlots();

      if (slotContent) {
        var transport = {
          from: this.name,
          to: this.to,
          passengers: _toConsumableArray$4(slotContent),
          order: this.order
        };
        wormhole.open(transport);
      } else {
        this.clear();
      }
    }
  },
  render: function render(h) {
    var children = this.$slots.default || this.$scopedSlots.default || [];
    var Tag = this.tag;

    if (children && this.disabled) {
      return children.length <= 1 && this.slim ? this.normalizeOwnChildren(children)[0] : h(Tag, [this.normalizeOwnChildren(children)]);
    } else {
      return this.slim ? h() : h(Tag, {
        class: {
          'v-portal': true
        },
        style: {
          display: 'none'
        },
        key: 'v-portal-placeholder'
      });
    }
  }
});

var PortalTarget = Vue.extend({
  name: 'portalTarget',
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    slim: {
      type: Boolean,
      default: false
    },
    slotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    tag: {
      type: String,
      default: 'div'
    },
    transition: {
      type: [String, Object, Function]
    }
  },
  data: function data() {
    return {
      transports: wormhole.transports,
      firstRender: true
    };
  },
  created: function created() {
    var _this = this;

    this.$nextTick(function () {
      wormhole.registerTarget(_this.name, _this);
    });
  },
  watch: {
    ownTransports: function ownTransports() {
      this.$emit('change', this.children().length > 0);
    },
    name: function name(newVal, oldVal) {
      /**
       * TODO
       * This should warn as well ...
       */
      wormhole.unregisterTarget(oldVal);
      wormhole.registerTarget(newVal, this);
    }
  },
  mounted: function mounted() {
    var _this2 = this;

    if (this.transition) {
      this.$nextTick(function () {
        // only when we have a transition, because it causes a re-render
        _this2.firstRender = false;
      });
    }
  },
  beforeDestroy: function beforeDestroy() {
    wormhole.unregisterTarget(this.name);
  },
  computed: {
    ownTransports: function ownTransports() {
      var transports = this.transports[this.name] || [];

      if (this.multiple) {
        return transports;
      }

      return transports.length === 0 ? [] : [transports[transports.length - 1]];
    },
    passengers: function passengers() {
      return combinePassengers(this.ownTransports, this.slotProps);
    }
  },
  methods: {
    // can't be a computed prop because it has to "react" to $slot changes.
    children: function children() {
      return this.passengers.length !== 0 ? this.passengers : this.$scopedSlots.default ? this.$scopedSlots.default(this.slotProps) : this.$slots.default || [];
    },
    // can't be a computed prop because it has to "react" to this.children().
    noWrapper: function noWrapper() {
      var noWrapper = this.slim && !this.transition;

      if (noWrapper && this.children().length > 1) {
        console.warn('[portal-vue]: PortalTarget with `slim` option received more than one child element.');
      }

      return noWrapper;
    }
  },
  render: function render(h) {
    var noWrapper = this.noWrapper();
    var children = this.children();
    var Tag = this.transition || this.tag;
    return noWrapper ? children[0] : this.slim && !Tag ? h() : h(Tag, {
      props: {
        // if we have a transition component, pass the tag if it exists
        tag: this.transition && this.tag ? this.tag : undefined
      },
      class: {
        'vue-portal-target': true
      }
    }, children);
  }
});

var _id$1 = 0;
var portalProps = ['disabled', 'name', 'order', 'slim', 'slotProps', 'tag', 'to'];
var targetProps = ['multiple', 'transition'];
var MountingPortal = Vue.extend({
  name: 'MountingPortal',
  inheritAttrs: false,
  props: {
    append: {
      type: [Boolean, String]
    },
    bail: {
      type: Boolean
    },
    mountTo: {
      type: String,
      required: true
    },
    // Portal
    disabled: {
      type: Boolean
    },
    // name for the portal
    name: {
      type: String,
      default: function _default() {
        return 'mounted_' + String(_id$1++);
      }
    },
    order: {
      type: Number,
      default: 0
    },
    slim: {
      type: Boolean
    },
    slotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    tag: {
      type: String,
      default: 'DIV'
    },
    // name for the target
    to: {
      type: String,
      default: function _default() {
        return String(Math.round(Math.random() * 10000000));
      }
    },
    // Target
    multiple: {
      type: Boolean,
      default: false
    },
    targetSlim: {
      type: Boolean
    },
    targetSlotProps: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    targetTag: {
      type: String,
      default: 'div'
    },
    transition: {
      type: [String, Object, Function]
    }
  },
  created: function created() {
    if (typeof document === 'undefined') return;
    var el = document.querySelector(this.mountTo);

    if (!el) {
      console.error("[portal-vue]: Mount Point '".concat(this.mountTo, "' not found in document"));
      return;
    }

    var props = this.$props; // Target already exists

    if (wormhole.targets[props.name]) {
      if (props.bail) {
        console.warn("[portal-vue]: Target ".concat(props.name, " is already mounted.\n        Aborting because 'bail: true' is set"));
      } else {
        this.portalTarget = wormhole.targets[props.name];
      }

      return;
    }

    var append = props.append;

    if (append) {
      var type = typeof append === 'string' ? append : 'DIV';
      var mountEl = document.createElement(type);
      el.appendChild(mountEl);
      el = mountEl;
    } // get props for target from $props
    // we have to rename a few of them


    var _props = pick$1(this.$props, targetProps);

    _props.slim = this.targetSlim;
    _props.tag = this.targetTag;
    _props.slotProps = this.targetSlotProps;
    _props.name = this.to;
    this.portalTarget = new PortalTarget({
      el: el,
      parent: this.$parent || this,
      propsData: _props
    });
  },
  beforeDestroy: function beforeDestroy() {
    var target = this.portalTarget;

    if (this.append) {
      var el = target.$el;
      el.parentNode.removeChild(el);
    }

    target.$destroy();
  },
  render: function render(h) {
    if (!this.portalTarget) {
      console.warn("[portal-vue] Target wasn't mounted");
      return h();
    } // if there's no "manual" scoped slot, so we create a <Portal> ourselves


    if (!this.$scopedSlots.manual) {
      var props = pick$1(this.$props, portalProps);
      return h(Portal, {
        props: props,
        attrs: this.$attrs,
        on: this.$listeners,
        scopedSlots: this.$scopedSlots
      }, this.$slots.default);
    } // else, we render the scoped slot


    var content = this.$scopedSlots.manual({
      to: this.to
    }); // if user used <template> for the scoped slot
    // content will be an array

    if (Array.isArray(content)) {
      content = content[0];
    }

    if (!content) return h();
    return content;
  }
});

var props$q = {
  name: {
    type: String,
    required: true
  },
  ariaLive: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_TOASTER, 'ariaLive');
    }
  },
  ariaAtomic: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_TOASTER, 'ariaAtomic');
    } // Allowed: 'true' or 'false' or null

  },
  role: {
    // Aria role
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_TOASTER, 'role');
    }
  }
  /*
  transition: {
    type: [Boolean, String, Object],
    default: false
  }
  */

}; // @vue/component

var DefaultTransition = /*#__PURE__*/Vue.extend({
  data: function data() {
    return {
      // Transition classes base name
      name: 'b-toaster'
    };
  },
  methods: {
    onAfterEnter: function onAfterEnter(el) {
      var _this = this;

      // Work around a Vue.js bug where `*-enter-to` class is not removed
      // See: https://github.com/vuejs/vue/pull/7901
      // The `*-move` class is also stuck on elements that moved,
      // but there are no JavaScript hooks to handle after move
      // See: https://github.com/vuejs/vue/pull/7906
      requestAF(function () {
        removeClass(el, "".concat(_this.name, "-enter-to"));
      });
    }
  },
  render: function render(h) {
    return h('transition-group', {
      props: {
        tag: 'div',
        name: this.name
      },
      on: {
        afterEnter: this.onAfterEnter
      }
    }, this.$slots.default);
  }
}); // @vue/component

var BToaster = /*#__PURE__*/Vue.extend({
  name: NAME_TOASTER,
  props: props$q,
  data: function data() {
    return {
      // We don't render on SSR or if a an existing target found
      doRender: false,
      dead: false,
      // Toaster names cannot change once created
      staticName: this.name
    };
  },
  beforeMount: function beforeMount() {
    var _this2 = this;

    this.staticName = this.name;
    /* istanbul ignore if */

    if (wormhole.hasTarget(this.staticName)) {
      warn("A \"<portal-target>\" with name \"".concat(this.name, "\" already exists in the document."), NAME_TOASTER);
      this.dead = true;
    } else {
      this.doRender = true;
      this.$once('hook:beforeDestroy', function () {
        // Let toasts made with `this.$bvToast.toast()` know that this toaster
        // is being destroyed and should should also destroy/hide themselves
        _this2.$root.$emit('bv::toaster::destroyed', _this2.staticName);
      });
    }
  },
  destroyed: function destroyed() {
    // Remove from DOM if needed

    /* istanbul ignore next: difficult to test */
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  render: function render(h) {
    var $toaster = h('div', {
      class: ['d-none', {
        'b-dead-toaster': this.dead
      }]
    });

    if (this.doRender) {
      var $target = h(PortalTarget, {
        staticClass: 'b-toaster-slot',
        props: {
          name: this.staticName,
          multiple: true,
          tag: 'div',
          slim: false,
          // transition: this.transition || DefaultTransition
          transition: DefaultTransition
        }
      });
      $toaster = h('div', {
        staticClass: 'b-toaster',
        class: [this.staticName],
        attrs: {
          id: this.staticName,
          role: this.role || null,
          // Fallback to null to make sure attribute doesn't exist
          'aria-live': this.ariaLive,
          'aria-atomic': this.ariaAtomic
        }
      }, [$target]);
    }

    return $toaster;
  }
});

function ownKeys$J(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$J(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$J(Object(source), true).forEach(function (key) { _defineProperty$X(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$J(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$X(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var MIN_DURATION = 1000; // --- Props ---

var linkProps$3 = pick(props$1, ['href', 'to']);
var props$r = _objectSpread$J({
  id: {
    // Even though the ID prop is provided by idMixin, we
    // add it here for $bvToast props filtering
    type: String // default: null

  },
  title: {
    type: String // default: null

  },
  toaster: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_TOAST, 'toaster');
    }
  },
  visible: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: function _default() {
      return getComponentConfig(NAME_TOAST, 'variant');
    }
  },
  isStatus: {
    // Switches role to 'status' and aria-live to 'polite'
    type: Boolean,
    default: false
  },
  appendToast: {
    type: Boolean,
    default: false
  },
  noAutoHide: {
    type: Boolean,
    default: false
  },
  autoHideDelay: {
    type: [Number, String],
    default: function _default() {
      return getComponentConfig(NAME_TOAST, 'autoHideDelay');
    }
  },
  noCloseButton: {
    type: Boolean,
    default: false
  },
  noFade: {
    type: Boolean,
    default: false
  },
  noHoverPause: {
    type: Boolean,
    default: false
  },
  solid: {
    type: Boolean,
    default: false
  },
  toastClass: {
    type: [String, Object, Array],
    default: function _default() {
      return getComponentConfig(NAME_TOAST, 'toastClass');
    }
  },
  headerClass: {
    type: [String, Object, Array],
    default: function _default() {
      return getComponentConfig(NAME_TOAST, 'headerClass');
    }
  },
  bodyClass: {
    type: [String, Object, Array],
    default: function _default() {
      return getComponentConfig(NAME_TOAST, 'bodyClass');
    }
  },
  static: {
    // Render the toast in place, rather than in a portal-target
    type: Boolean,
    default: false
  }
}, linkProps$3); // @vue/component

var BToast = /*#__PURE__*/Vue.extend({
  name: NAME_TOAST,
  mixins: [attrsMixin, idMixin, listenOnRootMixin, normalizeSlotMixin, scopedStyleAttrsMixin],
  inheritAttrs: false,
  model: {
    prop: 'visible',
    event: 'change'
  },
  props: props$r,
  data: function data() {
    return {
      isMounted: false,
      doRender: false,
      localShow: false,
      isTransitioning: false,
      isHiding: false,
      order: 0,
      timer: null,
      dismissStarted: 0,
      resumeDismiss: 0
    };
  },
  computed: {
    bToastClasses: function bToastClasses() {
      return _defineProperty$X({
        'b-toast-solid': this.solid,
        'b-toast-append': this.appendToast,
        'b-toast-prepend': !this.appendToast
      }, "b-toast-".concat(this.variant), this.variant);
    },
    slotScope: function slotScope() {
      return {
        hide: this.hide
      };
    },
    computedDuration: function computedDuration() {
      // Minimum supported duration is 1 second
      return mathMax(toInteger(this.autoHideDelay, 0), MIN_DURATION);
    },
    computedToaster: function computedToaster() {
      return String(this.toaster);
    },
    transitionHandlers: function transitionHandlers() {
      return {
        beforeEnter: this.onBeforeEnter,
        afterEnter: this.onAfterEnter,
        beforeLeave: this.onBeforeLeave,
        afterLeave: this.onAfterLeave
      };
    },
    computedAttrs: function computedAttrs() {
      return _objectSpread$J(_objectSpread$J({}, this.bvAttrs), {}, {
        id: this.safeId(),
        tabindex: '0'
      });
    }
  },
  watch: {
    visible: function visible(newVal) {
      newVal ? this.show() : this.hide();
    },
    localShow: function localShow(newVal) {
      if (newVal !== this.visible) {
        this.$emit('change', newVal);
      }
    },

    /* istanbul ignore next */
    toaster: function toaster()
    /* istanbul ignore next */
    {
      // If toaster target changed, make sure toaster exists
      this.$nextTick(this.ensureToaster);
    },

    /* istanbul ignore next */
    static: function _static(newVal)
    /* istanbul ignore next */
    {
      // If static changes to true, and the toast is showing,
      // ensure the toaster target exists
      if (newVal && this.localShow) {
        this.ensureToaster();
      }
    }
  },
  mounted: function mounted() {
    var _this = this;

    this.isMounted = true;
    this.$nextTick(function () {
      if (_this.visible) {
        requestAF(function () {
          _this.show();
        });
      }
    }); // Listen for global $root show events

    this.listenOnRoot('bv::show::toast', function (id) {
      if (id === _this.safeId()) {
        _this.show();
      }
    }); // Listen for global $root hide events

    this.listenOnRoot('bv::hide::toast', function (id) {
      if (!id || id === _this.safeId()) {
        _this.hide();
      }
    }); // Make sure we hide when toaster is destroyed

    /* istanbul ignore next: difficult to test */

    this.listenOnRoot('bv::toaster::destroyed', function (toaster) {
      /* istanbul ignore next */
      if (toaster === _this.computedToaster) {
        /* istanbul ignore next */
        _this.hide();
      }
    });
  },
  beforeDestroy: function beforeDestroy() {
    this.clearDismissTimer();
  },
  methods: {
    show: function show() {
      var _this2 = this;

      if (!this.localShow) {
        this.ensureToaster();
        var showEvt = this.buildEvent('show');
        this.emitEvent(showEvt);
        this.dismissStarted = this.resumeDismiss = 0;
        this.order = Date.now() * (this.appendToast ? 1 : -1);
        this.isHiding = false;
        this.doRender = true;
        this.$nextTick(function () {
          // We show the toast after we have rendered the portal and b-toast wrapper
          // so that screen readers will properly announce the toast
          requestAF(function () {
            _this2.localShow = true;
          });
        });
      }
    },
    hide: function hide() {
      var _this3 = this;

      if (this.localShow) {
        var hideEvt = this.buildEvent('hide');
        this.emitEvent(hideEvt);
        this.setHoverHandler(false);
        this.dismissStarted = this.resumeDismiss = 0;
        this.clearDismissTimer();
        this.isHiding = true;
        requestAF(function () {
          _this3.localShow = false;
        });
      }
    },
    buildEvent: function buildEvent(type) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      return new BvEvent(type, _objectSpread$J(_objectSpread$J({
        cancelable: false,
        target: this.$el || null,
        relatedTarget: null
      }, options), {}, {
        vueTarget: this,
        componentId: this.safeId()
      }));
    },
    emitEvent: function emitEvent(bvEvt) {
      var type = bvEvt.type;
      this.emitOnRoot("bv::toast:".concat(type), bvEvt);
      this.$emit(type, bvEvt);
    },
    ensureToaster: function ensureToaster() {
      if (this.static) {
        return;
      }

      if (!wormhole.hasTarget(this.computedToaster)) {
        var div = document.createElement('div');
        document.body.appendChild(div);
        var toaster = new BToaster({
          parent: this.$root,
          propsData: {
            name: this.computedToaster
          }
        });
        toaster.$mount(div);
      }
    },
    startDismissTimer: function startDismissTimer() {
      this.clearDismissTimer();

      if (!this.noAutoHide) {
        this.timer = setTimeout(this.hide, this.resumeDismiss || this.computedDuration);
        this.dismissStarted = Date.now();
        this.resumeDismiss = 0;
      }
    },
    clearDismissTimer: function clearDismissTimer() {
      clearTimeout(this.timer);
      this.timer = null;
    },
    setHoverHandler: function setHoverHandler(on) {
      var el = this.$refs['b-toast'];
      eventOnOff(on, el, 'mouseenter', this.onPause, EVENT_OPTIONS_NO_CAPTURE);
      eventOnOff(on, el, 'mouseleave', this.onUnPause, EVENT_OPTIONS_NO_CAPTURE);
    },
    onPause: function onPause() {
      // Determine time remaining, and then pause timer
      if (this.noAutoHide || this.noHoverPause || !this.timer || this.resumeDismiss) {
        return;
      }

      var passed = Date.now() - this.dismissStarted;

      if (passed > 0) {
        this.clearDismissTimer();
        this.resumeDismiss = mathMax(this.computedDuration - passed, MIN_DURATION);
      }
    },
    onUnPause: function onUnPause() {
      // Restart timer with max of time remaining or 1 second
      if (this.noAutoHide || this.noHoverPause || !this.resumeDismiss) {
        this.resumeDismiss = this.dismissStarted = 0;
        return;
      }

      this.startDismissTimer();
    },
    onLinkClick: function onLinkClick() {
      var _this4 = this;

      // We delay the close to allow time for the
      // browser to process the link click
      this.$nextTick(function () {
        requestAF(function () {
          _this4.hide();
        });
      });
    },
    onBeforeEnter: function onBeforeEnter() {
      this.isTransitioning = true;
    },
    onAfterEnter: function onAfterEnter() {
      this.isTransitioning = false;
      var hiddenEvt = this.buildEvent('shown');
      this.emitEvent(hiddenEvt);
      this.startDismissTimer();
      this.setHoverHandler(true);
    },
    onBeforeLeave: function onBeforeLeave() {
      this.isTransitioning = true;
    },
    onAfterLeave: function onAfterLeave() {
      this.isTransitioning = false;
      this.order = 0;
      this.resumeDismiss = this.dismissStarted = 0;
      var hiddenEvt = this.buildEvent('hidden');
      this.emitEvent(hiddenEvt);
      this.doRender = false;
    },
    makeToast: function makeToast(h) {
      var _this5 = this;

      // Render helper for generating the toast
      // Assemble the header content
      var $headerContent = [];
      var $title = this.normalizeSlot('toast-title', this.slotScope);

      if ($title) {
        $headerContent.push($title);
      } else if (this.title) {
        $headerContent.push(h('strong', {
          staticClass: 'mr-2'
        }, this.title));
      }

      if (!this.noCloseButton) {
        $headerContent.push(h(BButtonClose, {
          staticClass: 'ml-auto mb-1',
          on: {
            click: function click() {
              _this5.hide();
            }
          }
        }));
      } // Assemble the header (if needed)


      var $header = h();

      if ($headerContent.length > 0) {
        $header = h('header', {
          staticClass: 'toast-header',
          class: this.headerClass
        }, $headerContent);
      } // Toast body


      var link = isLink(this);
      var $body = h(link ? BLink : 'div', {
        staticClass: 'toast-body',
        class: this.bodyClass,
        props: link ? pluckProps(linkProps$3, this) : {},
        on: link ? {
          click: this.onLinkClick
        } : {}
      }, [this.normalizeSlot(SLOT_NAME_DEFAULT, this.slotScope) || h()]); // Build the toast

      var $toast = h('div', {
        key: "toast-".concat(this._uid),
        ref: 'toast',
        staticClass: 'toast',
        class: this.toastClass,
        attrs: this.computedAttrs
      }, [$header, $body]);
      return $toast;
    }
  },
  render: function render(h) {
    if (!this.doRender || !this.isMounted) {
      return h();
    }

    var name = "b-toast-".concat(this._uid); // If scoped styles are applied and the toast is not static,
    // make sure the scoped style data attribute is applied

    var scopedStyleAttrs = !this.static ? this.scopedStyleAttrs : {};
    return h(Portal, {
      props: {
        name: name,
        to: this.computedToaster,
        order: this.order,
        slim: true,
        disabled: this.static
      }
    }, [h('div', {
      key: name,
      ref: 'b-toast',
      staticClass: 'b-toast',
      class: this.bToastClasses,
      attrs: _objectSpread$J(_objectSpread$J({}, scopedStyleAttrs), {}, {
        id: this.safeId('_toast_outer'),
        role: this.isHiding ? null : this.isStatus ? 'status' : 'alert',
        'aria-live': this.isHiding ? null : this.isStatus ? 'polite' : 'assertive',
        'aria-atomic': this.isHiding ? null : 'true'
      })
    }, [h(BVTransition, {
      props: {
        noFade: this.noFade
      },
      on: this.transitionHandlers
    }, [this.localShow ? this.makeToast(h) : h()])])]);
  }
});

function ownKeys$K(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$K(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$K(Object(source), true).forEach(function (key) { _defineProperty$Y(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$K(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$Y(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var BV_TOOLTIP = '__BV_Tooltip__'; // Default trigger

var DefaultTrigger = 'hover focus'; // Valid event triggers

var validTriggers = {
  focus: true,
  hover: true,
  click: true,
  blur: true,
  manual: true
}; // Directive modifier test regular expressions. Pre-compile for performance

var htmlRE = /^html$/i;
var noninteractiveRE = /^noninteractive$/i;
var noFadeRE = /^nofade$/i;
var placementRE = /^(auto|top(left|right)?|bottom(left|right)?|left(top|bottom)?|right(top|bottom)?)$/i;
var boundaryRE = /^(window|viewport|scrollParent)$/i;
var delayRE = /^d\d+$/i;
var delayShowRE = /^ds\d+$/i;
var delayHideRE = /^dh\d+$/i;
var offsetRE = /^o-?\d+$/i;
var variantRE = /^v-.+$/i;
var spacesRE = /\s+/; // Build a Tooltip config based on bindings (if any)
// Arguments and modifiers take precedence over passed value config object

var parseBindings = function parseBindings(bindings, vnode)
/* istanbul ignore next: not easy to test */
{
  // We start out with a basic config
  var config = {
    title: undefined,
    trigger: '',
    // Default set below if needed
    placement: 'top',
    fallbackPlacement: 'flip',
    container: false,
    // Default of body
    animation: true,
    offset: 0,
    id: null,
    html: false,
    interactive: true,
    disabled: false,
    delay: getComponentConfig(NAME_TOOLTIP, 'delay'),
    boundary: String(getComponentConfig(NAME_TOOLTIP, 'boundary')),
    boundaryPadding: toInteger(getComponentConfig(NAME_TOOLTIP, 'boundaryPadding'), 0),
    variant: getComponentConfig(NAME_TOOLTIP, 'variant'),
    customClass: getComponentConfig(NAME_TOOLTIP, 'customClass')
  }; // Process `bindings.value`

  if (isString(bindings.value) || isNumber(bindings.value)) {
    // Value is tooltip content (HTML optionally supported)
    config.title = bindings.value;
  } else if (isFunction$1(bindings.value)) {
    // Title generator function
    config.title = bindings.value;
  } else if (isPlainObject$1(bindings.value)) {
    // Value is config object, so merge
    config = _objectSpread$K(_objectSpread$K({}, config), bindings.value);
  } // If title is not provided, try title attribute


  if (isUndefined(config.title)) {
    // Try attribute
    var data = vnode.data || {};
    config.title = data.attrs && !isUndefinedOrNull(data.attrs.title) ? data.attrs.title : undefined;
  } // Normalize delay


  if (!isPlainObject$1(config.delay)) {
    config.delay = {
      show: toInteger(config.delay, 0),
      hide: toInteger(config.delay, 0)
    };
  } // If argument, assume element ID of container element


  if (bindings.arg) {
    // Element ID specified as arg
    // We must prepend '#' to become a CSS selector
    config.container = "#".concat(bindings.arg);
  } // Process modifiers


  keys$1(bindings.modifiers).forEach(function (mod) {
    if (htmlRE.test(mod)) {
      // Title allows HTML
      config.html = true;
    } else if (noninteractiveRE.test(mod)) {
      // Noninteractive
      config.interactive = false;
    } else if (noFadeRE.test(mod)) {
      // No animation
      config.animation = false;
    } else if (placementRE.test(mod)) {
      // Placement of tooltip
      config.placement = mod;
    } else if (boundaryRE.test(mod)) {
      // Boundary of tooltip
      mod = mod === 'scrollparent' ? 'scrollParent' : mod;
      config.boundary = mod;
    } else if (delayRE.test(mod)) {
      // Delay value
      var delay = toInteger(mod.slice(1), 0);
      config.delay.show = delay;
      config.delay.hide = delay;
    } else if (delayShowRE.test(mod)) {
      // Delay show value
      config.delay.show = toInteger(mod.slice(2), 0);
    } else if (delayHideRE.test(mod)) {
      // Delay hide value
      config.delay.hide = toInteger(mod.slice(2), 0);
    } else if (offsetRE.test(mod)) {
      // Offset value, negative allowed
      config.offset = toInteger(mod.slice(1), 0);
    } else if (variantRE.test(mod)) {
      // Variant
      config.variant = mod.slice(2) || null;
    }
  }); // Special handling of event trigger modifiers trigger is
  // a space separated list

  var selectedTriggers = {}; // Parse current config object trigger

  concat(config.trigger || '').filter(identity$1).join(' ').trim().toLowerCase().split(spacesRE).forEach(function (trigger) {
    if (validTriggers[trigger]) {
      selectedTriggers[trigger] = true;
    }
  }); // Parse modifiers for triggers

  keys$1(bindings.modifiers).forEach(function (mod) {
    mod = mod.toLowerCase();

    if (validTriggers[mod]) {
      // If modifier is a valid trigger
      selectedTriggers[mod] = true;
    }
  }); // Sanitize triggers

  config.trigger = keys$1(selectedTriggers).join(' ');

  if (config.trigger === 'blur') {
    // Blur by itself is useless, so convert it to 'focus'
    config.trigger = 'focus';
  }

  if (!config.trigger) {
    // Use default trigger
    config.trigger = DefaultTrigger;
  } // Return the config


  return config;
}; // Add/update Tooltip on our element


var applyTooltip = function applyTooltip(el, bindings, vnode) {
  if (!isBrowser) {
    /* istanbul ignore next */
    return;
  }

  var config = parseBindings(bindings, vnode);

  if (!el[BV_TOOLTIP]) {
    var $parent = vnode.context;
    el[BV_TOOLTIP] = new BVTooltip({
      parent: $parent,
      // Add the parent's scoped style attribute data
      _scopeId: getScopeId($parent, undefined)
    });
    el[BV_TOOLTIP].__bv_prev_data__ = {};
    el[BV_TOOLTIP].$on('show', function ()
    /* istanbul ignore next: for now */
    {
      // Before showing the tooltip, we update the title if it is a function
      if (isFunction$1(config.title)) {
        el[BV_TOOLTIP].updateData({
          title: config.title(el)
        });
      }
    });
  }

  var data = {
    title: config.title,
    triggers: config.trigger,
    placement: config.placement,
    fallbackPlacement: config.fallbackPlacement,
    variant: config.variant,
    customClass: config.customClass,
    container: config.container,
    boundary: config.boundary,
    delay: config.delay,
    offset: config.offset,
    noFade: !config.animation,
    id: config.id,
    interactive: config.interactive,
    disabled: config.disabled,
    html: config.html
  };
  var oldData = el[BV_TOOLTIP].__bv_prev_data__;
  el[BV_TOOLTIP].__bv_prev_data__ = data;

  if (!looseEqual(data, oldData)) {
    // We only update the instance if data has changed
    var newData = {
      target: el
    };
    keys$1(data).forEach(function (prop) {
      // We only pass data properties that have changed
      if (data[prop] !== oldData[prop]) {
        // if title is a function, we execute it here
        newData[prop] = prop === 'title' && isFunction$1(data[prop]) ? data[prop](el) : data[prop];
      }
    });
    el[BV_TOOLTIP].updateData(newData);
  }
}; // Remove Tooltip on our element


var removeTooltip = function removeTooltip(el) {
  if (el[BV_TOOLTIP]) {
    el[BV_TOOLTIP].$destroy();
    el[BV_TOOLTIP] = null;
  }

  delete el[BV_TOOLTIP];
}; // Export our directive


var VBTooltip = {
  bind: function bind(el, bindings, vnode) {
    applyTooltip(el, bindings, vnode);
  },
  // We use `componentUpdated` here instead of `update`, as the former
  // waits until the containing component and children have finished updating
  componentUpdated: function componentUpdated(el, bindings, vnode) {
    // Performed in a `$nextTick()` to prevent render update loops
    vnode.context.$nextTick(function () {
      applyTooltip(el, bindings, vnode);
    });
  },
  unbind: function unbind(el) {
    removeTooltip(el);
  }
};

//

var script$1 = {
  name: 'MePopover',
  components: {
    BPopover
  },
  props: {
    target: {
      type: [String, Function],
      required: true
    },
    title: {
      type: String,
      default: undefined
    },
    content: {
      type: String,
      default: undefined
    },
    placement: {
      type: String,
      default: 'right'
    },
    triggers: {
      type: [String, Array],
      default: 'click'
    },
    show: {
      type: Boolean,
      default: false
    },
    container: {
      type: String,
      default: undefined
    },
    noArrow: {
      type: Boolean,
      default: false
    },
    offset: {
      type: Number,
      default: 0
    },
    customClass: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    boundary: {
      type: String,
      default: 'scrollParent'
    },
    action: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      model: this.show
    }
  },
  computed: {
    noArrowClass() {
      return this.noArrow ? 'no-arrow' : ''
    }
  },
  watch: {
    show(val) {
      this.model = val;
    }
  }
};

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BPopover",
    {
      attrs: {
        target: _vm.target,
        title: _vm.title,
        content: _vm.content,
        placement: _vm.placement,
        triggers: _vm.triggers,
        show: _vm.model,
        container: _vm.container,
        boundary: _vm.boundary,
        disabled: _vm.disabled,
        "custom-class":
          "me-popover " + _vm.noArrowClass + " " + _vm.customClass,
        offset: _vm.offset
      },
      on: {
        "update:show": function($event) {
          return _vm.$emit("update:show", $event)
        }
      },
      scopedSlots: _vm._u(
        [
          _vm.title
            ? {
                key: "title",
                fn: function() {
                  return [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]
                },
                proxy: true
              }
            : null,
          _vm._l(_vm.$scopedSlots, function(_, slot) {
            return {
              key: slot,
              fn: function(scope) {
                return [_vm._t(slot, null, null, scope)]
              }
            }
          })
        ],
        null,
        true
      )
    },
    [
      _vm._v(" "),
      [
        _c("div", [_vm._v("\n      " + _vm._s(_vm.content) + "\n    ")]),
        _vm._v(" "),
        _vm.action
          ? _c(
              "a",
              {
                staticClass: "popover-body__action",
                attrs: { href: _vm.action.href },
                on: {
                  click: function($event) {
                    _vm.action.click ? _vm.action.click() : null;
                  }
                }
              },
              [_vm._v("\n      " + _vm._s(_vm.action.label) + "\n    ")]
            )
          : _vm._e()
      ]
    ],
    2
  )
};
var __vue_staticRenderFns__$1 = [];
__vue_render__$1._withStripped = true;

  /* style */
  const __vue_inject_styles__$1 = function (inject) {
    if (!inject) return
    inject("data-v-8f01230a_0", { source: "div.me-popover[data-v-8f01230a] {\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n  width: 328px;\n  max-width: 328px;\n  border-radius: 8px;\n  padding: 16px;\n  border: 1px solid var(--me-gray-6);\n  box-shadow: var(--me-shadow-level-1);\n  z-index: 1110;\n}\ndiv.me-popover.bs-popover-bottom[data-v-8f01230a], div.me-popover.bs-popover-top[data-v-8f01230a], div.me-popover.bs-popover-right[data-v-8f01230a] {\n  margin-top: 12px;\n}\ndiv.me-popover[data-v-8f01230a]  .popover-header {\n  display: flex;\n  align-items: center;\n  height: 22px;\n  padding: 0px;\n  border: 0px;\n  background: var(--me-white);\n  font-weight: 700;\n  font-size: 14px;\n  line-height: 22px;\n  color: var(--me-gray-2);\n}\ndiv.me-popover[data-v-8f01230a]  .popover-body {\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n  padding: 0px;\n  color: var(--me-gray-2);\n}\ndiv.me-popover[data-v-8f01230a]  .popover-body__action {\n  height: 22px;\n  display: flex;\n  align-items: center;\n}\ndiv.me-popover.no-arrow[data-v-8f01230a]  .arrow {\n  display: none;\n}\n\n/*# sourceMappingURL=Popover.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Popover.vue","Popover.vue"],"names":[],"mappings":"AA0HA;EACA,aAAA;EACA,sBAAA;EACA,QAAA;EACA,YAAA;EACA,gBAAA;EACA,kBAAA;EACA,aAAA;EACA,kCAAA;EACA,oCAAA;EACA,aAAA;ACzHA;AD2HA;EACA,gBAAA;ACzHA;AD4HA;EACA,aAAA;EACA,mBAAA;EACA,YAAA;EACA,YAAA;EACA,WAAA;EACA,2BAAA;EACA,gBAAA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;AC1HA;AD6HA;EACA,aAAA;EACA,sBAAA;EACA,QAAA;EACA,YAAA;EACA,uBAAA;AC3HA;AD6HA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;AC3HA;AD+HA;EACA,aAAA;AC7HA;;AAEA,sCAAsC","file":"Popover.vue","sourcesContent":["<template>\n  <BPopover\n    :target=\"target\"\n    :title=\"title\"\n    :content=\"content\"\n    :placement=\"placement\"\n    :triggers=\"triggers\"\n    :show=\"model\"\n    :container=\"container\"\n    :boundary=\"boundary\"\n    :disabled=\"disabled\"\n    :custom-class=\"`me-popover ${noArrowClass} ${customClass}`\"\n    :offset=\"offset\"\n    @update:show=\"$emit('update:show', $event)\"\n  >\n    <template v-if=\"title\" v-slot:title>\n      {{ title }}\n    </template>\n\n    <template>\n      <div>\n        {{ content }}\n      </div>\n\n      <a\n        v-if=\"action\"\n        :href=\"action.href\"\n        @click=\"action.click ? action.click() : null\"\n        class=\"popover-body__action\"\n      >\n        {{ action.label }}\n      </a>\n    </template>\n\n    <template v-for=\"(_, slot) of $scopedSlots\" v-slot:[slot]=\"scope\">\n      <slot :name=\"slot\" v-bind=\"scope\" />\n    </template>\n  </BPopover>\n</template>\n\n<script>\nimport { BPopover } from 'bootstrap-vue'\n\nexport default {\n  name: 'MePopover',\n  components: {\n    BPopover\n  },\n  props: {\n    target: {\n      type: [String, Function],\n      required: true\n    },\n    title: {\n      type: String,\n      default: undefined\n    },\n    content: {\n      type: String,\n      default: undefined\n    },\n    placement: {\n      type: String,\n      default: 'right'\n    },\n    triggers: {\n      type: [String, Array],\n      default: 'click'\n    },\n    show: {\n      type: Boolean,\n      default: false\n    },\n    container: {\n      type: String,\n      default: undefined\n    },\n    noArrow: {\n      type: Boolean,\n      default: false\n    },\n    offset: {\n      type: Number,\n      default: 0\n    },\n    customClass: {\n      type: String,\n      default: ''\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    boundary: {\n      type: String,\n      default: 'scrollParent'\n    },\n    action: {\n      type: Object,\n      default: null\n    }\n  },\n  data() {\n    return {\n      model: this.show\n    }\n  },\n  computed: {\n    noArrowClass() {\n      return this.noArrow ? 'no-arrow' : ''\n    }\n  },\n  watch: {\n    show(val) {\n      this.model = val\n    }\n  }\n}\n\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-popover {\n    display: flex;\n    flex-direction: column;\n    gap: 8px;\n    width:  328px;\n    max-width:  328px;\n    border-radius: 8px;\n    padding: 16px;\n    border: 1px solid var(--me-gray-6);\n    box-shadow: var(--me-shadow-level-1);\n    z-index: 1110;\n\n    &.bs-popover-bottom, &.bs-popover-top, &.bs-popover-right {\n      margin-top: 12px;\n    }\n\n    ::v-deep .popover-header {\n      display: flex;\n      align-items: center;\n      height: 22px;\n      padding: 0px;\n      border: 0px;\n      background: var(--me-white);\n      font-weight: 700;\n      font-size: 14px;\n      line-height: 22px;\n      color: var(--me-gray-2);\n    }\n\n    ::v-deep .popover-body {\n      display: flex;\n      flex-direction: column;\n      gap: 8px;\n      padding: 0px;\n      color: var(--me-gray-2);\n\n      &__action {\n        height: 22px;\n        display: flex;\n        align-items: center;\n      }\n    }\n\n    &.no-arrow ::v-deep .arrow {\n      display: none;\n    }\n  }\n</style>\n","div.me-popover {\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n  width: 328px;\n  max-width: 328px;\n  border-radius: 8px;\n  padding: 16px;\n  border: 1px solid var(--me-gray-6);\n  box-shadow: var(--me-shadow-level-1);\n  z-index: 1110;\n}\ndiv.me-popover.bs-popover-bottom, div.me-popover.bs-popover-top, div.me-popover.bs-popover-right {\n  margin-top: 12px;\n}\ndiv.me-popover ::v-deep .popover-header {\n  display: flex;\n  align-items: center;\n  height: 22px;\n  padding: 0px;\n  border: 0px;\n  background: var(--me-white);\n  font-weight: 700;\n  font-size: 14px;\n  line-height: 22px;\n  color: var(--me-gray-2);\n}\ndiv.me-popover ::v-deep .popover-body {\n  display: flex;\n  flex-direction: column;\n  gap: 8px;\n  padding: 0px;\n  color: var(--me-gray-2);\n}\ndiv.me-popover ::v-deep .popover-body__action {\n  height: 22px;\n  display: flex;\n  align-items: center;\n}\ndiv.me-popover.no-arrow ::v-deep .arrow {\n  display: none;\n}\n\n/*# sourceMappingURL=Popover.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1 = "data-v-8f01230a";
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$2 = {
  name: 'MeDropdownItem',
  components: {
    BDropdownItem,
    MePopover: __vue_component__$1,
    MeIcon: __vue_component__
  },
  props: {
    subItems: {
      type: Array,
      default: null
    },
    active: {
      type: Boolean,
      default: null
    },
    description: {
      type: String,
      default: ''
    },
    descriptionHTML: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: ''
    },
    itemClazz: {
      type: String,
      default: ''
    },
    id: {
      type: [String, Number],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placement: {
      type: String,
      default: 'top'
    },
    pill: {
      type: Boolean,
      default: false
    },
    popover: {
      type: Object,
      default: null
    },
    target: {
      type: String,
      default: '_self'
    }
  },

  data() {
    return {
      expanded: false
    }
  },

  methods: {
    handleClick(event) {
      if (this.disabled) return

      if (!this.subItems) {
        this.$emit('click', { label: this.label });
        return
      }

      this.expanded = !this.expanded;
      event.preventDefault && event.preventDefault();
      event.stopPropagation && event.stopPropagation();
    },

    handleSubitemClick(subItem) {
      if (this.disabled) return

      if (subItem && subItem.click) {
        subItem.click(subItem);
      }
    },

    hide() {
      this.expanded = false;
    }
  }
};

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-dropdown-item-wrapper" },
    [
      _c(
        "BDropdownItem",
        {
          class: ["me-dropdown-item", { pill: _vm.pill }],
          attrs: {
            href: _vm.href,
            target: _vm.target,
            active: _vm.active,
            disabled: _vm.disabled,
            "data-cy": "dropdownItem"
          },
          nativeOn: {
            "!click": function($event) {
              return _vm.handleClick($event)
            }
          }
        },
        [
          _vm._t("dropdown-item", [
            _c(
              "span",
              {
                directives: [
                  {
                    name: "me-tooltip",
                    rawName: "v-me-tooltip",
                    value: {
                      title: _vm.description,
                      boundary: "viewport",
                      placement: _vm.placement,
                      customClass: "dropdown-tooltip",
                      html: _vm.descriptionHTML
                    },
                    expression:
                      "{\n          title: description,\n          boundary: 'viewport',\n          placement: placement,\n          customClass: 'dropdown-tooltip',\n          html: descriptionHTML\n        }"
                  }
                ],
                class: ["item", _vm.itemClazz],
                attrs: { id: _vm.id || (_vm.popover && _vm.popover.id) || null }
              },
              [
                _vm._t("item-label", [
                  _vm.icon
                    ? _c("me-icon", {
                        staticClass: "item-icon",
                        attrs: { icon: _vm.icon }
                      })
                    : _vm._e(),
                  _vm._v("\n\n          " + _vm._s(_vm.label) + "\n        ")
                ])
              ],
              2
            ),
            _vm._v(" "),
            _vm.subItems
              ? _c("me-icon", {
                  staticClass: "expand-icon",
                  attrs: { icon: "me-icon-l icon-chevron-right" }
                })
              : _vm._e()
          ]),
          _vm._v(" "),
          _vm.popover && _vm.popover.id
            ? _c(
                "me-popover",
                {
                  attrs: {
                    disabled: _vm.popover.disabled,
                    target: _vm.popover.id,
                    placement: _vm.popover.placement || "top",
                    triggers: _vm.popover.triggers || "hover",
                    boundary: "viewport"
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm.popover.title
                        ? {
                            key: "title",
                            fn: function() {
                              return [
                                _c(
                                  "div",
                                  {
                                    staticClass:
                                      "d-flex justify-content-between"
                                  },
                                  [
                                    _c("span", { staticClass: "mr-2" }, [
                                      _vm._v(
                                        " " + _vm._s(_vm.popover.title) + " "
                                      )
                                    ])
                                  ]
                                )
                              ]
                            },
                            proxy: true
                          }
                        : null
                    ],
                    null,
                    true
                  )
                },
                [
                  _vm._v(" "),
                  _vm.popover.content
                    ? _c("div", [
                        _vm._v(
                          "\n        " +
                            _vm._s(_vm.popover.content) +
                            "\n      "
                        )
                      ])
                    : _vm._e()
                ]
              )
            : _vm._e()
        ],
        2
      ),
      _vm._v(" "),
      _vm.subItems
        ? _c(
            "ul",
            { class: ["dropdown-menu", "sub-menu", { show: _vm.expanded }] },
            _vm._l(_vm.subItems, function(subItem) {
              return _c("MeDropdownItem", {
                key: subItem.id || subItem.label,
                attrs: {
                  active: subItem.active,
                  disabled: subItem.disabled,
                  href: subItem.href,
                  target: subItem.target,
                  label: subItem.label,
                  itemClazz: subItem.class,
                  description: subItem.description,
                  descriptionHTML: subItem.descriptionHTML,
                  placement: subItem.placement,
                  popover: subItem.popover,
                  id:
                    subItem.id ||
                    (subItem.popover && subItem.popover.id) ||
                    null,
                  icon: subItem.icon,
                  pill: _vm.pill,
                  subItems: subItem.subItems
                },
                on: {
                  click: function($event) {
                    return _vm.handleSubitemClick(subItem)
                  }
                }
              })
            }),
            1
          )
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$2 = [];
__vue_render__$2._withStripped = true;

  /* style */
  const __vue_inject_styles__$2 = function (inject) {
    if (!inject) return
    inject("data-v-121b6e32_0", { source: ".me-dropdown-item-wrapper[data-v-121b6e32] {\n  width: 100%;\n  position: relative;\n}\n.me-dropdown-item-wrapper .sub-menu[data-v-121b6e32] {\n  left: 100%;\n  top: -0.625rem;\n}\n.me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  width: 100%;\n  color: var(--me-gray-2);\n  padding: 0;\n  text-decoration: none;\n  min-height: 40px;\n  font-size: 14px;\n}\n.me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item.disabled, .me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item.disabled:hover, .me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item.disabled:focus {\n  cursor: default;\n  pointer-events: all;\n  background-color: transparent;\n  color: var(--me-gray-4);\n}\n.me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item:hover, .me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item:focus {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-2);\n}\n.me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item.active {\n  background-color: transparent;\n  color: var(--me-primary-2);\n}\n.me-dropdown-item-wrapper .me-dropdown-item[data-v-121b6e32]  .dropdown-item.active:hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-2);\n}\n.me-dropdown-item-wrapper .me-dropdown-item.pill[data-v-121b6e32] {\n  margin-bottom: 8px;\n}\n.me-dropdown-item-wrapper .me-dropdown-item.pill[data-v-121b6e32]  .dropdown-item {\n  border-radius: 50rem;\n  background-color: var(--me-white);\n}\n.me-dropdown-item-wrapper .me-dropdown-item.pill[data-v-121b6e32]  .dropdown-item:hover {\n  background-color: var(--me-primary-7);\n}\n.me-dropdown-item-wrapper .me-dropdown-item .item[data-v-121b6e32] {\n  display: flex;\n  align-items: center;\n  padding: 0 16px;\n}\n.me-dropdown-item-wrapper .me-dropdown-item .item .item-icon[data-v-121b6e32] {\n  margin: 4px 8px 0 0;\n}\n.me-dropdown-item-wrapper .me-dropdown-item .expand-icon[data-v-121b6e32] {\n  margin: 4px 14.4px 0 0;\n}\n.dropdown-tooltip[data-v-121b6e32] {\n  z-index: 13000;\n}\n\n/*# sourceMappingURL=DropdownItem.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dropdown/DropdownItem.vue","DropdownItem.vue"],"names":[],"mappings":"AAuMA;EACA,WAAA;EACA,kBAAA;ACtMA;ADwMA;EACA,UAAA;EACA,cAAA;ACtMA;AD0MA;EACA,aAAA;EACA,mBAAA;EACA,8BAAA;EACA,WAAA;EACA,uBAAA;EACA,UAAA;EACA,qBAAA;EACA,gBAAA;EACA,eAAA;ACxMA;AD0MA;EACA,eAAA;EACA,mBAAA;EACA,6BAAA;EACA,uBAAA;ACxMA;AD2MA;EACA,qCAAA;EACA,0BAAA;ACzMA;AD4MA;EACA,6BAAA;EACA,0BAAA;AC1MA;AD4MA;EACA,qCAAA;EACA,0BAAA;AC1MA;AD+MA;EACA,kBAAA;AC7MA;AD+MA;EACA,oBAAA;EACA,iCAAA;AC7MA;AD+MA;EACA,qCAAA;AC7MA;ADmNA;EACA,aAAA;EACA,mBAAA;EACA,eAAA;ACjNA;ADmNA;EACA,mBAAA;ACjNA;ADqNA;EACA,sBAAA;ACnNA;ADwNA;EACA,cAAA;ACrNA;;AAEA,2CAA2C","file":"DropdownItem.vue","sourcesContent":["<template>\n  <div class=\"me-dropdown-item-wrapper\">\n    <BDropdownItem\n      @click.native.capture=\"handleClick\"\n      :class=\"['me-dropdown-item', {'pill': pill}]\"\n      :href=\"href\"\n      :target=\"target\"\n      :active=\"active\"\n      :disabled=\"disabled\"\n      data-cy=\"dropdownItem\"\n    >\n      <slot name=\"dropdown-item\">\n        <span\n          v-me-tooltip=\"{\n            title: description,\n            boundary: 'viewport',\n            placement: placement,\n            customClass: 'dropdown-tooltip',\n            html: descriptionHTML\n          }\"\n          :id=\"id || (popover && popover.id) || null\"\n          :class=\"['item', itemClazz]\"\n        >\n          <slot name=\"item-label\">\n            <me-icon\n              v-if=\"icon\"\n              :icon=\"icon\"\n              class=\"item-icon\"\n            />\n\n            {{ label }}\n          </slot>\n        </span>\n\n        <me-icon\n          v-if=\"subItems\"\n          icon=\"me-icon-l icon-chevron-right\"\n          class=\"expand-icon\"\n        />\n      </slot>\n\n      <me-popover\n        v-if=\"popover && popover.id\"\n        :disabled=\"popover.disabled\"\n        :target=\"popover.id\"\n        :placement=\"popover.placement || 'top'\"\n        :triggers=\"popover.triggers || 'hover'\"\n        boundary=\"viewport\"\n      >\n        <template v-slot:title v-if=\"popover.title\">\n          <div class=\"d-flex justify-content-between\">\n            <span class=\"mr-2\"> {{ popover.title }} </span>\n          </div>\n        </template>\n\n        <div v-if=\"popover.content\">\n          {{ popover.content }}\n        </div>\n      </me-popover>\n    </BDropdownItem>\n\n    <ul\n      v-if=\"subItems\"\n      :class=\"[\n        'dropdown-menu',\n        'sub-menu',\n        { 'show': expanded }\n      ]\"\n    >\n      <MeDropdownItem\n        v-for=\"subItem in subItems\"\n        :active=\"subItem.active\"\n        :disabled=\"subItem.disabled\"\n        :key=\"subItem.id || subItem.label\"\n        :href=\"subItem.href\"\n        :target=\"subItem.target\"\n        :label=\"subItem.label\"\n        :itemClazz=\"subItem.class\"\n        :description=\"subItem.description\"\n        :descriptionHTML=\"subItem.descriptionHTML\"\n        :placement=\"subItem.placement\"\n        :popover=\"subItem.popover\"\n        :id=\"subItem.id || (subItem.popover && subItem.popover.id) || null\"\n        :icon=\"subItem.icon\"\n        :pill=\"pill\"\n        :subItems=\"subItem.subItems\"\n        @click=\"handleSubitemClick(subItem)\"\n      />\n    </ul>\n  </div>\n</template>\n\n<script>\nimport { BDropdownItem } from 'bootstrap-vue'\nimport MePopover from '../Popover.vue'\nimport MeIcon from '../Icon.vue'\n\nexport default {\n  name: 'MeDropdownItem',\n  components: {\n    BDropdownItem,\n    MePopover,\n    MeIcon\n  },\n  props: {\n    subItems: {\n      type: Array,\n      default: null\n    },\n    active: {\n      type: Boolean,\n      default: null\n    },\n    description: {\n      type: String,\n      default: ''\n    },\n    descriptionHTML: {\n      type: Boolean,\n      default: false\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    href: {\n      type: String,\n      default: null\n    },\n    icon: {\n      type: String,\n      default: ''\n    },\n    itemClazz: {\n      type: String,\n      default: ''\n    },\n    id: {\n      type: [String, Number],\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    placement: {\n      type: String,\n      default: 'top'\n    },\n    pill: {\n      type: Boolean,\n      default: false\n    },\n    popover: {\n      type: Object,\n      default: null\n    },\n    target: {\n      type: String,\n      default: '_self'\n    }\n  },\n\n  data() {\n    return {\n      expanded: false\n    }\n  },\n\n  methods: {\n    handleClick(event) {\n      if (this.disabled) return\n\n      if (!this.subItems) {\n        this.$emit('click', { label: this.label })\n        return\n      }\n\n      this.expanded = !this.expanded\n      event.preventDefault && event.preventDefault()\n      event.stopPropagation && event.stopPropagation()\n    },\n\n    handleSubitemClick(subItem) {\n      if (this.disabled) return\n\n      if (subItem && subItem.click) {\n        subItem.click(subItem)\n      }\n    },\n\n    hide() {\n      this.expanded = false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-dropdown-item-wrapper {\n  width: 100%;\n  position: relative;\n\n  .sub-menu {\n      left: 100%;\n      top: -0.625rem;\n    }\n  .me-dropdown-item {\n\n    ::v-deep .dropdown-item {\n      display: flex;\n      align-items: center;\n      justify-content: space-between;\n      width: 100%;\n      color: var(--me-gray-2);\n      padding: 0;\n      text-decoration: none;\n      min-height: 40px;\n      font-size: 14px;\n\n      &.disabled, &.disabled:hover, &.disabled:focus {\n        cursor: default;\n        pointer-events: all;\n        background-color: transparent;\n        color: var(--me-gray-4);\n      }\n\n      &:hover, &:focus {\n        background-color: var(--me-primary-7);\n        color: var(--me-primary-2);\n      }\n\n      &.active {\n        background-color: transparent;\n        color: var(--me-primary-2);\n\n        &:hover {\n          background-color: var(--me-primary-7);\n          color: var(--me-primary-2);\n        }\n      }\n    }\n\n    &.pill {\n      margin-bottom: 8px;\n      ::v-deep {\n        .dropdown-item {\n          border-radius: 50rem;\n          background-color: var(--me-white);\n\n          &:hover {\n            background-color: var(--me-primary-7);\n          }\n        }\n      }\n    }\n\n    .item {\n      display: flex;\n      align-items: center;\n      padding: 0 16px;\n\n      .item-icon {\n        margin: 4px 8px 0 0;\n      }\n    }\n\n    .expand-icon {\n      margin: 4px 14.4px 0 0;\n    }\n  }\n}\n\n.dropdown-tooltip {\n  z-index: 13000;\n}\n</style>\n",".me-dropdown-item-wrapper {\n  width: 100%;\n  position: relative;\n}\n.me-dropdown-item-wrapper .sub-menu {\n  left: 100%;\n  top: -0.625rem;\n}\n.me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  width: 100%;\n  color: var(--me-gray-2);\n  padding: 0;\n  text-decoration: none;\n  min-height: 40px;\n  font-size: 14px;\n}\n.me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item.disabled, .me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item.disabled:hover, .me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item.disabled:focus {\n  cursor: default;\n  pointer-events: all;\n  background-color: transparent;\n  color: var(--me-gray-4);\n}\n.me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item:hover, .me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item:focus {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-2);\n}\n.me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item.active {\n  background-color: transparent;\n  color: var(--me-primary-2);\n}\n.me-dropdown-item-wrapper .me-dropdown-item ::v-deep .dropdown-item.active:hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-2);\n}\n.me-dropdown-item-wrapper .me-dropdown-item.pill {\n  margin-bottom: 8px;\n}\n.me-dropdown-item-wrapper .me-dropdown-item.pill ::v-deep .dropdown-item {\n  border-radius: 50rem;\n  background-color: var(--me-white);\n}\n.me-dropdown-item-wrapper .me-dropdown-item.pill ::v-deep .dropdown-item:hover {\n  background-color: var(--me-primary-7);\n}\n.me-dropdown-item-wrapper .me-dropdown-item .item {\n  display: flex;\n  align-items: center;\n  padding: 0 16px;\n}\n.me-dropdown-item-wrapper .me-dropdown-item .item .item-icon {\n  margin: 4px 8px 0 0;\n}\n.me-dropdown-item-wrapper .me-dropdown-item .expand-icon {\n  margin: 4px 14.4px 0 0;\n}\n\n.dropdown-tooltip {\n  z-index: 13000;\n}\n\n/*# sourceMappingURL=DropdownItem.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2 = "data-v-121b6e32";
  /* module identifier */
  const __vue_module_identifier__$2 = undefined;
  /* functional template */
  const __vue_is_functional_template__$2 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2, staticRenderFns: __vue_staticRenderFns__$2 },
    __vue_inject_styles__$2,
    __vue_script__$2,
    __vue_scope_id__$2,
    __vue_is_functional_template__$2,
    __vue_module_identifier__$2,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$3 = {
  name: 'MeAccordionList',
  components: {
    Icon: __vue_component__,
    DropdownItem: __vue_component__$2
  },

  props: {
    click: {
      type: Function,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      required: true
    },
    opened: {
      type: Boolean,
      default: false
    },
    caret: {
      type: String,
      default: 'me-icon-l icon-chevron-down'
    },
    left: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      localOpened: false
    }
  },

  watch: {
    opened: {
      immediate: true,
      handler(newVal) {
        this.localOpened = newVal;
      }
    }
  },

  methods: {
    toggle() {
      this.localOpened = !this.localOpened;
    },

    linkClick() {
      if (this.click) {
        this.click();
      } else {
        this.toggle();
      }
    },

    caretClick() {
      this.toggle();
    }
  }
};

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-accordion-list",
      class: { opened: _vm.localOpened, left: _vm.left },
      attrs: { "data-cy": "accordionList" }
    },
    [
      _c(
        "span",
        {
          staticClass: "accordion-item",
          class: { active: _vm.active },
          attrs: { "data-cy": "accordionLink" },
          on: { click: _vm.linkClick }
        },
        [
          _vm.left
            ? _c("Icon", {
                staticClass: "accordion-caret accordion-caret--left",
                attrs: { icon: _vm.caret, "data-cy": "accordionCaret" },
                on: {
                  click: function($event) {
                    $event.stopPropagation();
                    return _vm.caretClick($event)
                  }
                }
              })
            : _vm._e(),
          _vm._v(
            "\n\n    " + _vm._s(_vm.label || _vm.description) + "\n\n    "
          ),
          !_vm.left
            ? _c("Icon", {
                staticClass: "accordion-caret accordion-caret--right",
                attrs: { icon: _vm.caret, "data-cy": "accordionCaret" },
                on: {
                  click: function($event) {
                    $event.stopPropagation();
                    return _vm.caretClick($event)
                  }
                }
              })
            : _vm._e()
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "ul",
        { staticClass: "me-list-group", class: { active: _vm.active } },
        _vm._l(_vm.items, function(item) {
          return _c("DropdownItem", {
            key: item.key,
            attrs: {
              active: item.active,
              href: item.url,
              label: item.label || item.description,
              "data-cy": "accordionListItem"
            },
            on: {
              click: function($event) {
                item.click ? item.click() : null;
              }
            }
          })
        }),
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$3 = [];
__vue_render__$3._withStripped = true;

  /* style */
  const __vue_inject_styles__$3 = function (inject) {
    if (!inject) return
    inject("data-v-784973de_0", { source: "div.me-accordion-list[data-v-784973de] {\n  width: 100%;\n  font-size: 14px;\n}\ndiv.me-accordion-list > .accordion-item[data-v-784973de] {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 40px;\n  padding: 0 16px;\n  cursor: pointer;\n  position: relative;\n}\ndiv.me-accordion-list > .accordion-item[data-v-784973de]:hover {\n  background-color: var(--me-primary-7);\n}\ndiv.me-accordion-list > .accordion-item > .accordion-caret[data-v-784973de] {\n  transition-duration: 0.2s;\n  transition: transform;\n}\ndiv.me-accordion-list > .accordion-item > .accordion-caret--left[data-v-784973de] {\n  margin-right: 4px;\n}\ndiv.me-accordion-list > .accordion-item > .accordion-caret--right[data-v-784973de] {\n  margin-left: auto;\n}\ndiv.me-accordion-list > .accordion-item.active[data-v-784973de], div.me-accordion-list > .accordion-item.active[data-v-784973de]:hover {\n  color: var(--me-primary-2);\n}\ndiv.me-accordion-list > ul[data-v-784973de] {\n  display: none;\n  list-style: none;\n  padding-left: 0;\n}\ndiv.me-accordion-list.opened > .accordion-item > .accordion-caret[data-v-784973de] {\n  transform: rotate(180deg);\n}\ndiv.me-accordion-list.opened > ul[data-v-784973de] {\n  display: block;\n}\n\n/*# sourceMappingURL=AccordionList.vue.map */", map: {"version":3,"sources":["/app/build/src/components/AccordionList.vue","AccordionList.vue"],"names":[],"mappings":"AAkIA;EACA,WAAA;EACA,eAAA;ACjIA;ADmIA;EACA,aAAA;EACA,mBAAA;EACA,WAAA;EACA,YAAA;EACA,eAAA;EACA,eAAA;EACA,kBAAA;ACjIA;ADmIA;EACA,qCAAA;ACjIA;ADoIA;EACA,yBAAA;EACA,qBAAA;AClIA;ADoIA;EACA,iBAAA;AClIA;ADqIA;EACA,iBAAA;ACnIA;ADuIA;EAEA,0BAAA;ACtIA;AD0IA;EACA,aAAA;EACA,gBAAA;EACA,eAAA;ACxIA;AD6IA;EACA,yBAAA;AC3IA;AD+IA;EACA,cAAA;AC7IA;;AAEA,4CAA4C","file":"AccordionList.vue","sourcesContent":["<template>\n  <div\n    :class=\"{ opened: localOpened, left: left }\"\n    class=\"me-accordion-list\"\n    data-cy=\"accordionList\"\n  >\n    <span\n      :class=\"{ active: active }\"\n      @click=\"linkClick\"\n      data-cy=\"accordionLink\"\n      class=\"accordion-item\"\n    >\n      <Icon\n        v-if=\"left\"\n        :icon=\"caret\"\n        @click.stop=\"caretClick\"\n        class=\"accordion-caret accordion-caret--left\"\n        data-cy=\"accordionCaret\"\n      />\n\n      {{ label || description }}\n\n      <Icon\n        v-if=\"!left\"\n        :icon=\"caret\"\n        @click.stop=\"caretClick\"\n        class=\"accordion-caret accordion-caret--right\"\n        data-cy=\"accordionCaret\"\n      />\n    </span>\n\n    <ul\n      :class=\"{ 'active': active }\"\n      class=\"me-list-group\"\n    >\n      <DropdownItem\n        v-for=\"item in items\"\n        :key=\"item.key\"\n        :active=\"item.active\"\n        :href=\"item.url\"\n        :label=\"item.label || item.description\"\n        @click=\"item.click ? item.click() : null\"\n        data-cy=\"accordionListItem\"\n      />\n    </ul>\n  </div>\n</template>\n\n<script>\nimport Icon from './Icon.vue'\nimport DropdownItem from './dropdown/DropdownItem.vue'\n\nexport default {\n  name: 'MeAccordionList',\n  components: {\n    Icon,\n    DropdownItem\n  },\n\n  props: {\n    click: {\n      type: Function,\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    description: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      required: true\n    },\n    opened: {\n      type: Boolean,\n      default: false\n    },\n    caret: {\n      type: String,\n      default: 'me-icon-l icon-chevron-down'\n    },\n    left: {\n      type: Boolean,\n      default: false\n    },\n    active: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      localOpened: false\n    }\n  },\n\n  watch: {\n    opened: {\n      immediate: true,\n      handler(newVal) {\n        this.localOpened = newVal\n      }\n    }\n  },\n\n  methods: {\n    toggle() {\n      this.localOpened = !this.localOpened\n    },\n\n    linkClick() {\n      if (this.click) {\n        this.click()\n      } else {\n        this.toggle()\n      }\n    },\n\n    caretClick() {\n      this.toggle()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-accordion-list {\n    width: 100%;\n    font-size: 14px;\n\n    > .accordion-item {\n      display: flex;\n      align-items: center;\n      width: 100%;\n      height: 40px;\n      padding: 0 16px;\n      cursor: pointer;\n      position: relative;\n\n      &:hover {\n        background-color: var(--me-primary-7);\n      }\n\n      > .accordion-caret {\n        transition-duration: 0.2s;\n        transition: transform;\n\n        &--left {\n          margin-right: 4px;\n        }\n\n        &--right {\n          margin-left: auto;\n        }\n      }\n\n      &.active,\n      &.active:hover {\n        color: var(--me-primary-2);\n      }\n    }\n\n    > ul {\n      display: none;\n      list-style: none;\n      padding-left: 0;\n    }\n\n    &.opened {\n      > .accordion-item {\n        > .accordion-caret {\n          transform: rotate(180deg);\n        }\n      }\n\n      > ul {\n        display: block;\n      }\n    }\n  }\n</style>\n","div.me-accordion-list {\n  width: 100%;\n  font-size: 14px;\n}\ndiv.me-accordion-list > .accordion-item {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 40px;\n  padding: 0 16px;\n  cursor: pointer;\n  position: relative;\n}\ndiv.me-accordion-list > .accordion-item:hover {\n  background-color: var(--me-primary-7);\n}\ndiv.me-accordion-list > .accordion-item > .accordion-caret {\n  transition-duration: 0.2s;\n  transition: transform;\n}\ndiv.me-accordion-list > .accordion-item > .accordion-caret--left {\n  margin-right: 4px;\n}\ndiv.me-accordion-list > .accordion-item > .accordion-caret--right {\n  margin-left: auto;\n}\ndiv.me-accordion-list > .accordion-item.active, div.me-accordion-list > .accordion-item.active:hover {\n  color: var(--me-primary-2);\n}\ndiv.me-accordion-list > ul {\n  display: none;\n  list-style: none;\n  padding-left: 0;\n}\ndiv.me-accordion-list.opened > .accordion-item > .accordion-caret {\n  transform: rotate(180deg);\n}\ndiv.me-accordion-list.opened > ul {\n  display: block;\n}\n\n/*# sourceMappingURL=AccordionList.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$3 = "data-v-784973de";
  /* module identifier */
  const __vue_module_identifier__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$3 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3, staticRenderFns: __vue_staticRenderFns__$3 },
    __vue_inject_styles__$3,
    __vue_script__$3,
    __vue_scope_id__$3,
    __vue_is_functional_template__$3,
    __vue_module_identifier__$3,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const alertIcons = {
  primary: {
    icon: 'me-icon-l icon-circle-info',
    color: 'var(--me-primary-2)'
  },
  success: {
    icon: 'me-icon-l icon-check-circle',
    color: 'var(--me-success-0)'
  },
  danger: {
    icon: 'me-icon-l icon-triangle-exclamation',
    color: 'var(--me-danger-2)'
  },
  warning: {
    icon: 'me-icon-l icon-circle-exclamation',
    color: 'var(--me-warning-0)'
  },
  default: {
    icon: 'me-icon-l icon-circle-info',
    color: 'var(--me-gray-2)'
  }
};

var script$4 = {
  name: 'MeAlert',
  components: {
    BAlert,
    MeIcon: __vue_component__
  },
  model: {
    prop: 'show'
  },
  props: {
    dismissible: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    show: {
      type: [Boolean, Number],
      default: false
    },
    variant: {
      type: String,
      default: 'primary'
    },
    withIcon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      current: this.show
    }
  },
  computed: {
    alertIcon() {
      return alertIcons[this.variant] || alertIcons.default
    }
  },
  watch: {
    show(val) {
      this.current = val;
    }
  }
};

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BAlert",
    {
      class: { "rounded-borders": _vm.rounded },
      attrs: {
        variant: _vm.variant,
        dismissible: _vm.dismissible,
        "data-cy": "alert",
        fade: ""
      },
      on: {
        dismissed: function($event) {
          return _vm.$emit("dismissed")
        },
        input: function($event) {
          return _vm.$emit("input", _vm.current)
        }
      },
      scopedSlots: _vm._u([
        {
          key: "dismiss",
          fn: function() {
            return [
              _c("MeIcon", {
                attrs: {
                  size: 16,
                  icon: "me-icon-l icon-xmark",
                  color: "var(--me-gray-2)",
                  "data-cy": "alert-dismiss"
                }
              })
            ]
          },
          proxy: true
        }
      ]),
      model: {
        value: _vm.current,
        callback: function($$v) {
          _vm.current = $$v;
        },
        expression: "current"
      }
    },
    [
      _c(
        "div",
        { staticClass: "alert-content" },
        [
          _vm.withIcon
            ? _c("MeIcon", {
                attrs: {
                  icon: _vm.alertIcon.icon,
                  color: _vm.alertIcon.color,
                  size: 16
                }
              })
            : _vm._e(),
          _vm._v(" "),
          _c("div", [_vm._t("default")], 2)
        ],
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$4 = [];
__vue_render__$4._withStripped = true;

  /* style */
  const __vue_inject_styles__$4 = function (inject) {
    if (!inject) return
    inject("data-v-30b72e24_0", { source: "div.alert[data-v-30b72e24] {\n  border: none;\n  color: var(--me-gray-2);\n  border-radius: 0;\n  font-size: 14px;\n  font-weight: 600;\n  padding: 16px;\n}\ndiv.alert-danger[data-v-30b72e24] {\n  background-color: var(--me-danger-7);\n}\ndiv.alert-primary[data-v-30b72e24] {\n  background-color: var(--me-primary-7);\n}\ndiv.alert-success[data-v-30b72e24] {\n  background-color: var(--me-success-7);\n}\ndiv.alert-warning[data-v-30b72e24] {\n  background-color: var(--me-warning-7);\n}\ndiv.alert-light[data-v-30b72e24], div.alert-info[data-v-30b72e24], div.alert-dark[data-v-30b72e24], div.alert-secondary[data-v-30b72e24] {\n  background-color: var(--me-bg-gray);\n}\ndiv.alert-dismissible[data-v-30b72e24] {\n  padding-right: 48px;\n}\ndiv.alert.rounded-borders[data-v-30b72e24] {\n  border-radius: 8px;\n}\ndiv.alert .alert-content[data-v-30b72e24] {\n  display: flex;\n  gap: 8px;\n}\ndiv.alert[data-v-30b72e24]  button.close {\n  width: 24px;\n  height: 24px;\n  padding: 0;\n  right: 15px;\n  top: 16px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  opacity: 1;\n  text-shadow: none;\n  font-weight: 400;\n}\ndiv.alert[data-v-30b72e24]  button.close:focus {\n  opacity: 1;\n  outline: none;\n}\ndiv.alert[data-v-30b72e24]  button.close:not(:disabled):hover {\n  opacity: 1;\n}\n\n/*# sourceMappingURL=Alert.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Alert.vue","Alert.vue"],"names":[],"mappings":"AAgHA;EACA,YAAA;EACA,uBAAA;EACA,gBAAA;EACA,eAAA;EACA,gBAAA;EACA,aAAA;AC/GA;ADiHA;EACA,oCAAA;AC/GA;ADkHA;EACA,qCAAA;AChHA;ADmHA;EACA,qCAAA;ACjHA;ADoHA;EACA,qCAAA;AClHA;ADqHA;EACA,mCAAA;ACnHA;ADsHA;EACA,mBAAA;ACpHA;ADuHA;EACA,kBAAA;ACrHA;ADwHA;EACA,aAAA;EACA,QAAA;ACtHA;ADyHA;EACA,WAAA;EACA,YAAA;EACA,UAAA;EACA,WAAA;EACA,SAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,UAAA;EACA,iBAAA;EACA,gBAAA;ACvHA;ADyHA;EACA,UAAA;EACA,aAAA;ACvHA;AD0HA;EACA,UAAA;ACxHA;;AAEA,oCAAoC","file":"Alert.vue","sourcesContent":["<template>\n  <BAlert\n    v-model=\"current\"\n    :variant=\"variant\"\n    :dismissible=\"dismissible\"\n    :class=\"{ 'rounded-borders': rounded }\"\n    @dismissed=\"$emit('dismissed')\"\n    @input=\"$emit('input', current)\"\n    data-cy=\"alert\"\n    fade\n  >\n    <div class=\"alert-content\">\n      <MeIcon\n        v-if=\"withIcon\"\n        :icon=\"alertIcon.icon\"\n        :color=\"alertIcon.color\"\n        :size=\"16\"\n      />\n\n      <div>\n        <slot />\n      </div>\n    </div>\n\n    <template v-slot:dismiss>\n      <MeIcon\n        :size=\"16\"\n        icon=\"me-icon-l icon-xmark\"\n        color=\"var(--me-gray-2)\"\n        data-cy=\"alert-dismiss\"\n      />\n    </template>\n  </BAlert>\n</template>\n\n<script>\nimport { BAlert } from 'bootstrap-vue'\nimport MeIcon from './Icon.vue'\n\nconst alertIcons = {\n  primary: {\n    icon: 'me-icon-l icon-circle-info',\n    color: 'var(--me-primary-2)'\n  },\n  success: {\n    icon: 'me-icon-l icon-check-circle',\n    color: 'var(--me-success-0)'\n  },\n  danger: {\n    icon: 'me-icon-l icon-triangle-exclamation',\n    color: 'var(--me-danger-2)'\n  },\n  warning: {\n    icon: 'me-icon-l icon-circle-exclamation',\n    color: 'var(--me-warning-0)'\n  },\n  default: {\n    icon: 'me-icon-l icon-circle-info',\n    color: 'var(--me-gray-2)'\n  }\n}\n\nexport default {\n  name: 'MeAlert',\n  components: {\n    BAlert,\n    MeIcon\n  },\n  model: {\n    prop: 'show'\n  },\n  props: {\n    dismissible: {\n      type: Boolean,\n      default: false\n    },\n    rounded: {\n      type: Boolean,\n      default: false\n    },\n    show: {\n      type: [Boolean, Number],\n      default: false\n    },\n    variant: {\n      type: String,\n      default: 'primary'\n    },\n    withIcon: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      current: this.show\n    }\n  },\n  computed: {\n    alertIcon() {\n      return alertIcons[this.variant] || alertIcons.default\n    }\n  },\n  watch: {\n    show(val) {\n      this.current = val\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.alert {\n    border: none;\n    color: var(--me-gray-2);\n    border-radius: 0;\n    font-size: 14px;\n    font-weight: 600;\n    padding: 16px;\n\n    &-danger {\n      background-color: var(--me-danger-7);\n    }\n\n    &-primary {\n      background-color: var(--me-primary-7);\n    }\n\n    &-success {\n      background-color: var(--me-success-7);\n    }\n\n    &-warning {\n      background-color: var(--me-warning-7);\n    }\n\n    &-light, &-info, &-dark, &-secondary {\n      background-color: var(--me-bg-gray);\n    }\n\n    &-dismissible {\n      padding-right: 48px;\n    }\n\n    &.rounded-borders {\n      border-radius: 8px;\n    }\n\n    .alert-content {\n      display: flex;\n      gap: 8px;\n    }\n\n    ::v-deep button.close {\n      width: 24px;\n      height: 24px;\n      padding: 0;\n      right: 15px;\n      top: 16px;\n      display: flex;\n      align-items: center;\n      justify-content: center;\n      opacity: 1;\n      text-shadow: none;\n      font-weight: 400;\n\n      &:focus {\n       opacity: 1;\n       outline: none;\n      }\n\n      &:not(:disabled):hover {\n        opacity: 1;\n      }\n    }\n  }\n</style>\n","div.alert {\n  border: none;\n  color: var(--me-gray-2);\n  border-radius: 0;\n  font-size: 14px;\n  font-weight: 600;\n  padding: 16px;\n}\ndiv.alert-danger {\n  background-color: var(--me-danger-7);\n}\ndiv.alert-primary {\n  background-color: var(--me-primary-7);\n}\ndiv.alert-success {\n  background-color: var(--me-success-7);\n}\ndiv.alert-warning {\n  background-color: var(--me-warning-7);\n}\ndiv.alert-light, div.alert-info, div.alert-dark, div.alert-secondary {\n  background-color: var(--me-bg-gray);\n}\ndiv.alert-dismissible {\n  padding-right: 48px;\n}\ndiv.alert.rounded-borders {\n  border-radius: 8px;\n}\ndiv.alert .alert-content {\n  display: flex;\n  gap: 8px;\n}\ndiv.alert ::v-deep button.close {\n  width: 24px;\n  height: 24px;\n  padding: 0;\n  right: 15px;\n  top: 16px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  opacity: 1;\n  text-shadow: none;\n  font-weight: 400;\n}\ndiv.alert ::v-deep button.close:focus {\n  opacity: 1;\n  outline: none;\n}\ndiv.alert ::v-deep button.close:not(:disabled):hover {\n  opacity: 1;\n}\n\n/*# sourceMappingURL=Alert.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$4 = "data-v-30b72e24";
  /* module identifier */
  const __vue_module_identifier__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$4 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4, staticRenderFns: __vue_staticRenderFns__$4 },
    __vue_inject_styles__$4,
    __vue_script__$4,
    __vue_scope_id__$4,
    __vue_is_functional_template__$4,
    __vue_module_identifier__$4,
    false,
    createInjector,
    undefined,
    undefined
  );

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */
function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

var _arrayEach = arrayEach;

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign(object, source) {
  return object && _copyObject(source, keys_1(source), object);
}

var _baseAssign = baseAssign;

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn(object, source) {
  return object && _copyObject(source, keysIn_1(source), object);
}

var _baseAssignIn = baseAssignIn;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$c.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};

var _getSymbols = getSymbols;

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols(source, object) {
  return _copyObject(source, _getSymbols(source), object);
}

var _copySymbols = copySymbols;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray_1 : function(object) {
  var result = [];
  while (object) {
    _arrayPush(result, _getSymbols(object));
    object = _getPrototype(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn;

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn(source, object) {
  return _copyObject(source, _getSymbolsIn(source), object);
}

var _copySymbolsIn = copySymbolsIn;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn(object) {
  return _baseGetAllKeys(object, keysIn_1, _getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');

var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise$1 = _getNative(_root, 'Promise');

var _Promise = Promise$1;

/* Built-in method references that are verified to be native. */
var Set$1 = _getNative(_root, 'Set');

var _Set = Set$1;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');

var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$1 = '[object Map]',
    objectTag$2 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$1 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$1 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$1) ||
    (_Map && getTag(new _Map) != mapTag$1) ||
    (_Promise && getTag(_Promise.resolve()) != promiseTag) ||
    (_Set && getTag(new _Set) != setTag$1) ||
    (_WeakMap && getTag(new _WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$2 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$1;
        case mapCtorString: return mapTag$1;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$1;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag;

/** Used for built-in method references. */
var objectProto$d = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$b = objectProto$d.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$b.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

var _initCloneArray = initCloneArray;

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView(dataView, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView;

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto$1 ? symbolProto$1.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol;

/** `Object#toString` result references. */
var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag$1 = '[object Symbol]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return _cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$2:
      return _cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return _cloneTypedArray(object, isDeep);

    case mapTag$2:
      return new Ctor;

    case numberTag$1:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return _cloneRegExp(object);

    case setTag$2:
      return new Ctor;

    case symbolTag$1:
      return _cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag;

/** `Object#toString` result references. */
var mapTag$3 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap(value) {
  return isObjectLike_1(value) && _getTag(value) == mapTag$3;
}

var _baseIsMap = baseIsMap;

/* Node.js helper references. */
var nodeIsMap = _nodeUtil && _nodeUtil.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap = nodeIsMap ? _baseUnary(nodeIsMap) : _baseIsMap;

var isMap_1 = isMap;

/** `Object#toString` result references. */
var setTag$3 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet(value) {
  return isObjectLike_1(value) && _getTag(value) == setTag$3;
}

var _baseIsSet = baseIsSet;

/* Node.js helper references. */
var nodeIsSet = _nodeUtil && _nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet = nodeIsSet ? _baseUnary(nodeIsSet) : _baseIsSet;

var isSet_1 = isSet;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    mapTag$4 = '[object Map]',
    numberTag$2 = '[object Number]',
    objectTag$3 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$2 = '[object Symbol]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag$2] = cloneableTags[arrayTag$1] =
cloneableTags[arrayBufferTag$2] = cloneableTags[dataViewTag$3] =
cloneableTags[boolTag$2] = cloneableTags[dateTag$2] =
cloneableTags[float32Tag$2] = cloneableTags[float64Tag$2] =
cloneableTags[int8Tag$2] = cloneableTags[int16Tag$2] =
cloneableTags[int32Tag$2] = cloneableTags[mapTag$4] =
cloneableTags[numberTag$2] = cloneableTags[objectTag$3] =
cloneableTags[regexpTag$2] = cloneableTags[setTag$4] =
cloneableTags[stringTag$2] = cloneableTags[symbolTag$2] =
cloneableTags[uint8Tag$2] = cloneableTags[uint8ClampedTag$2] =
cloneableTags[uint16Tag$2] = cloneableTags[uint32Tag$2] = true;
cloneableTags[errorTag$1] = cloneableTags[funcTag$2] =
cloneableTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject_1(value)) {
    return value;
  }
  var isArr = isArray_1(value);
  if (isArr) {
    result = _initCloneArray(value);
    if (!isDeep) {
      return _copyArray(value, result);
    }
  } else {
    var tag = _getTag(value),
        isFunc = tag == funcTag$2 || tag == genTag$1;

    if (isBuffer_1(value)) {
      return _cloneBuffer(value, isDeep);
    }
    if (tag == objectTag$3 || tag == argsTag$2 || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : _initCloneObject(value);
      if (!isDeep) {
        return isFlat
          ? _copySymbolsIn(value, _baseAssignIn(result, value))
          : _copySymbols(value, _baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = _initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new _Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet_1(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap_1(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? _getAllKeysIn : _getAllKeys)
    : (isFlat ? keysIn_1 : keys_1);

  var props = isArr ? undefined : keysFunc(value);
  _arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    _assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1,
    CLONE_SYMBOLS_FLAG$1 = 4;

/**
 * This method is like `_.clone` except that it recursively clones `value`.
 *
 * @static
 * @memberOf _
 * @since 1.0.0
 * @category Lang
 * @param {*} value The value to recursively clone.
 * @returns {*} Returns the deep cloned value.
 * @see _.clone
 * @example
 *
 * var objects = [{ 'a': 1 }, { 'b': 2 }];
 *
 * var deep = _.cloneDeep(objects);
 * console.log(deep[0] === objects[0]);
 * // => false
 */
function cloneDeep$1(value) {
  return _baseClone(value, CLONE_DEEP_FLAG$1 | CLONE_SYMBOLS_FLAG$1);
}

var cloneDeep_1 = cloneDeep$1;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED$2);
  return this;
}

var _setCacheAdd = setCacheAdd;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new _MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = _setCacheAdd;
SetCache.prototype.has = _setCacheHas;

var _SetCache = SetCache;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

var _arraySome = arraySome;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new _SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!_arraySome(other, function(othValue, othIndex) {
            if (!_cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/** `Object#toString` result references. */
var boolTag$3 = '[object Boolean]',
    dateTag$3 = '[object Date]',
    errorTag$2 = '[object Error]',
    mapTag$5 = '[object Map]',
    numberTag$3 = '[object Number]',
    regexpTag$3 = '[object RegExp]',
    setTag$5 = '[object Set]',
    stringTag$3 = '[object String]',
    symbolTag$3 = '[object Symbol]';

var arrayBufferTag$3 = '[object ArrayBuffer]',
    dataViewTag$4 = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto$2 = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf$1 = symbolProto$2 ? symbolProto$2.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag$4:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag$3:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new _Uint8Array(object), new _Uint8Array(other))) {
        return false;
      }
      return true;

    case boolTag$3:
    case dateTag$3:
    case numberTag$3:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq_1(+object, +other);

    case errorTag$2:
      return object.name == other.name && object.message == other.message;

    case regexpTag$3:
    case stringTag$3:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag$5:
      var convert = _mapToArray;

    case setTag$5:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1;
      convert || (convert = _setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$1;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = _equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$3:
      if (symbolValueOf$1) {
        return symbolValueOf$1.call(object) == symbolValueOf$1.call(other);
      }
  }
  return false;
}

var _equalByTag = equalByTag;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** Used for built-in method references. */
var objectProto$e = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$c = objectProto$e.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2,
      objProps = _getAllKeys(object),
      objLength = objProps.length,
      othProps = _getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$c.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** `Object#toString` result references. */
var argsTag$3 = '[object Arguments]',
    arrayTag$2 = '[object Array]',
    objectTag$4 = '[object Object]';

/** Used for built-in method references. */
var objectProto$f = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$d = objectProto$f.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray_1(object),
      othIsArr = isArray_1(other),
      objTag = objIsArr ? arrayTag$2 : _getTag(object),
      othTag = othIsArr ? arrayTag$2 : _getTag(other);

  objTag = objTag == argsTag$3 ? objectTag$4 : objTag;
  othTag = othTag == argsTag$3 ? objectTag$4 : othTag;

  var objIsObj = objTag == objectTag$4,
      othIsObj = othTag == objectTag$4,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer_1(object)) {
    if (!isBuffer_1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new _Stack);
    return (objIsArr || isTypedArray_1(object))
      ? _equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : _equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$3)) {
    var objIsWrapped = objIsObj && hasOwnProperty$d.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$d.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new _Stack);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new _Stack);
  return _equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike_1(value) && !isObjectLike_1(other))) {
    return value !== value && other !== other;
  }
  return _baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

var _baseIsEqual = baseIsEqual;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new _Stack;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$4 | COMPARE_UNORDERED_FLAG$2, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

var _baseIsMatch = baseIsMatch;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject_1(value);
}

var _isStrictComparable = isStrictComparable;

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys_1(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, _isStrictComparable(value)];
  }
  return result;
}

var _getMatchData = getMatchData;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

var _matchesStrictComparable = matchesStrictComparable;

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = _getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return _matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || _baseIsMatch(object, source, matchData);
  };
}

var _baseMatches = baseMatches;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}

var _baseHasIn = baseHasIn;

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = _castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = _toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength_1(length) && _isIndex(key, length) &&
    (isArray_1(object) || isArguments_1(object));
}

var _hasPath = hasPath;

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && _hasPath(object, path, _baseHasIn);
}

var hasIn_1 = hasIn;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (_isKey(path) && _isStrictComparable(srcValue)) {
    return _matchesStrictComparable(_toKey(path), srcValue);
  }
  return function(object) {
    var objValue = get_1(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn_1(object, path)
      : _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$5 | COMPARE_UNORDERED_FLAG$3);
  };
}

var _baseMatchesProperty = baseMatchesProperty;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

var _baseProperty = baseProperty;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function(object) {
    return _baseGet(object, path);
  };
}

var _basePropertyDeep = basePropertyDeep;

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return _isKey(path) ? _baseProperty(_toKey(path)) : _basePropertyDeep(path);
}

var property_1 = property;

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity_1;
  }
  if (typeof value == 'object') {
    return isArray_1(value)
      ? _baseMatchesProperty(value[0], value[1])
      : _baseMatches(value);
  }
  return property_1(value);
}

var _baseIteratee = baseIteratee;

/**
 * Creates a `_.find` or `_.findLast` function.
 *
 * @private
 * @param {Function} findIndexFunc The function to find the collection index.
 * @returns {Function} Returns the new find function.
 */
function createFind(findIndexFunc) {
  return function(collection, predicate, fromIndex) {
    var iterable = Object(collection);
    if (!isArrayLike_1(collection)) {
      var iteratee = _baseIteratee(predicate);
      collection = keys_1(collection);
      predicate = function(key) { return iteratee(iterable[key], key, iterable); };
    }
    var index = findIndexFunc(collection, predicate, fromIndex);
    return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined;
  };
}

var _createFind = createFind;

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseFindIndex(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

var _baseFindIndex = baseFindIndex;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to match leading and trailing whitespace. */
var reTrim = /^\s+|\s+$/g;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, '');
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber;

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber_1(value);
  if (value === INFINITY$2 || value === -INFINITY$2) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite;

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger$1(value) {
  var result = toFinite_1(value),
      remainder = result % 1;

  return result === result ? (remainder ? result - remainder : result) : 0;
}

var toInteger_1 = toInteger$1;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$1 = Math.max;

/**
 * This method is like `_.find` except that it returns the index of the first
 * element `predicate` returns truthy for instead of the element itself.
 *
 * @static
 * @memberOf _
 * @since 1.1.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {number} Returns the index of the found element, else `-1`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'active': false },
 *   { 'user': 'fred',    'active': false },
 *   { 'user': 'pebbles', 'active': true }
 * ];
 *
 * _.findIndex(users, function(o) { return o.user == 'barney'; });
 * // => 0
 *
 * // The `_.matches` iteratee shorthand.
 * _.findIndex(users, { 'user': 'fred', 'active': false });
 * // => 1
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.findIndex(users, ['active', false]);
 * // => 0
 *
 * // The `_.property` iteratee shorthand.
 * _.findIndex(users, 'active');
 * // => 2
 */
function findIndex$1(array, predicate, fromIndex) {
  var length = array == null ? 0 : array.length;
  if (!length) {
    return -1;
  }
  var index = fromIndex == null ? 0 : toInteger_1(fromIndex);
  if (index < 0) {
    index = nativeMax$1(length + index, 0);
  }
  return _baseFindIndex(array, _baseIteratee(predicate), index);
}

var findIndex_1 = findIndex$1;

/**
 * Iterates over elements of `collection`, returning the first element
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {*} Returns the matched element, else `undefined`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'age': 36, 'active': true },
 *   { 'user': 'fred',    'age': 40, 'active': false },
 *   { 'user': 'pebbles', 'age': 1,  'active': true }
 * ];
 *
 * _.find(users, function(o) { return o.age < 40; });
 * // => object for 'barney'
 *
 * // The `_.matches` iteratee shorthand.
 * _.find(users, { 'age': 1, 'active': true });
 * // => object for 'pebbles'
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.find(users, ['active', false]);
 * // => object for 'fred'
 *
 * // The `_.property` iteratee shorthand.
 * _.find(users, 'active');
 * // => object for 'barney'
 */
var find$1 = _createFind(findIndex_1);

var find_1 = find$1;

/** `Object#toString` result references. */
var mapTag$6 = '[object Map]',
    setTag$6 = '[object Set]';

/** Used for built-in method references. */
var objectProto$g = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$e = objectProto$g.hasOwnProperty;

/**
 * Checks if `value` is an empty object, collection, map, or set.
 *
 * Objects are considered empty if they have no own enumerable string keyed
 * properties.
 *
 * Array-like values such as `arguments` objects, arrays, buffers, strings, or
 * jQuery-like collections are considered empty if they have a `length` of `0`.
 * Similarly, maps and sets are considered empty if they have a `size` of `0`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 * @example
 *
 * _.isEmpty(null);
 * // => true
 *
 * _.isEmpty(true);
 * // => true
 *
 * _.isEmpty(1);
 * // => true
 *
 * _.isEmpty([1, 2, 3]);
 * // => false
 *
 * _.isEmpty({ 'a': 1 });
 * // => false
 */
function isEmpty$1(value) {
  if (value == null) {
    return true;
  }
  if (isArrayLike_1(value) &&
      (isArray_1(value) || typeof value == 'string' || typeof value.splice == 'function' ||
        isBuffer_1(value) || isTypedArray_1(value) || isArguments_1(value))) {
    return !value.length;
  }
  var tag = _getTag(value);
  if (tag == mapTag$6 || tag == setTag$6) {
    return !value.size;
  }
  if (_isPrototype(value)) {
    return !_baseKeys(value).length;
  }
  for (var key in value) {
    if (hasOwnProperty$e.call(value, key)) {
      return false;
    }
  }
  return true;
}

var isEmpty_1 = isEmpty$1;

//

var script$5 = {
  name: 'MeSpinner',
  components: {
    BSpinner
  },
  props: {
    type: {
      type: String,
      default: 'border'
    },
    variant: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    small: {
      type: Boolean,
      default: false
    },
    fluid: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("BSpinner", {
    staticClass: "me-spinner",
    class: { "fluid-spinner": _vm.fluid },
    attrs: {
      type: _vm.type,
      variant: _vm.variant,
      label: _vm.label,
      small: _vm.small
    }
  })
};
var __vue_staticRenderFns__$5 = [];
__vue_render__$5._withStripped = true;

  /* style */
  const __vue_inject_styles__$5 = function (inject) {
    if (!inject) return
    inject("data-v-8fb65f60_0", { source: ".me-spinner.fluid-spinner[data-v-8fb65f60] {\n  width: 100%;\n  height: 100%;\n}\n.me-spinner.text-success[data-v-8fb65f60] {\n  color: var(--me-success-2) !important;\n}\n\n/*# sourceMappingURL=Spinner.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Spinner.vue","Spinner.vue"],"names":[],"mappings":"AA6CA;EACA,WAAA;EACA,YAAA;AC5CA;AD8CA;EACA,qCAAA;AC3CA;;AAEA,sCAAsC","file":"Spinner.vue","sourcesContent":["<template>\n  <BSpinner\n    :type=\"type\"\n    :variant=\"variant\"\n    :label=\"label\"\n    :small=\"small\"\n    :class=\"{'fluid-spinner': fluid}\"\n    class=\"me-spinner\"\n  />\n</template>\n\n<script>\nimport { BSpinner } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSpinner',\n  components: {\n    BSpinner\n  },\n  props: {\n    type: {\n      type: String,\n      default: 'border'\n    },\n    variant: {\n      type: String,\n      default: null\n    },\n    label: {\n      type: String,\n      default: null\n    },\n    small: {\n      type: Boolean,\n      default: false\n    },\n    fluid: {\n      type: Boolean,\n      default: false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-spinner.fluid-spinner {\n  width: 100%;\n  height: 100%;\n}\n.me-spinner.text-success{\n  color: var(--me-success-2) !important;\n}\n\n</style>\n",".me-spinner.fluid-spinner {\n  width: 100%;\n  height: 100%;\n}\n\n.me-spinner.text-success {\n  color: var(--me-success-2) !important;\n}\n\n/*# sourceMappingURL=Spinner.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$5 = "data-v-8fb65f60";
  /* module identifier */
  const __vue_module_identifier__$5 = undefined;
  /* functional template */
  const __vue_is_functional_template__$5 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5, staticRenderFns: __vue_staticRenderFns__$5 },
    __vue_inject_styles__$5,
    __vue_script__$5,
    __vue_scope_id__$5,
    __vue_is_functional_template__$5,
    __vue_module_identifier__$5,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$6 = {
  name: 'MeButton',
  components: {
    BButton,
    Spinner: __vue_component__$5,
    MeIcon: __vue_component__
  },
  props: {
    appendIcon: {
      type: String,
      default: null
    },
    block: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    },
    customVariant: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: null
    },
    pill: {
      type: Boolean,
      default: false
    },
    prependIcon: {
      type: String,
      default: null
    },
    size: {
      type: String,
      default: 'md'
    },
    target: {
      type: String,
      default: '_self'
    },
    type: {
      type: String,
      default: 'button'
    },
    variant: {
      type: String,
      default: 'secondary'
    }
  }
};

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BButton",
    {
      staticClass: "me-button",
      class: [
        _vm.customVariant,
        {
          busy: _vm.busy,
          "btn-width": !_vm.icon && _vm.variant !== "link",
          "btn-padding": !_vm.icon,
          "icon-width": _vm.icon,
          "prepend-padding": _vm.prependIcon,
          "append-padding": _vm.appendIcon
        }
      ],
      attrs: {
        block: _vm.block,
        disabled: _vm.disabled || _vm.busy,
        href: _vm.href,
        pill: _vm.pill,
        size: _vm.size,
        target: _vm.target,
        type: _vm.type,
        variant: _vm.variant,
        "data-cy": "b-button"
      },
      on: {
        click: function($event) {
          return _vm.$emit("click", $event)
        }
      }
    },
    [
      _vm.icon
        ? _c("MeIcon", { attrs: { icon: _vm.icon } })
        : [
            _vm.prependIcon
              ? _c("MeIcon", {
                  staticClass: "prepend-icon",
                  attrs: { icon: _vm.prependIcon, "data-cy": "prependIcon" }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm._t("default"),
            _vm._v(" "),
            _vm.appendIcon
              ? _c("MeIcon", {
                  staticClass: "append-icon",
                  attrs: { icon: _vm.appendIcon, "data-cy": "appendIcon" }
                })
              : _vm._e()
          ],
      _vm._v(" "),
      _vm.busy
        ? _c("Spinner", { attrs: { small: "", type: "grow" } })
        : _vm._e()
    ],
    2
  )
};
var __vue_staticRenderFns__$6 = [];
__vue_render__$6._withStripped = true;

  /* style */
  const __vue_inject_styles__$6 = function (inject) {
    if (!inject) return
    inject("data-v-ecf1c0a0_0", { source: ".btn[data-v-ecf1c0a0] {\n  font-size: 14px;\n}\n.btn[data-v-ecf1c0a0]:focus {\n  box-shadow: none;\n}\n.btn[data-v-ecf1c0a0]:focus:active {\n  box-shadow: none !important;\n}\n.btn-secondary[data-v-ecf1c0a0] {\n  background-color: var(--me-gray-2);\n  color: var(--me-white);\n  border-color: var(--me-gray-2);\n}\n.btn-secondary[data-v-ecf1c0a0]:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-primary[data-v-ecf1c0a0] {\n  background-color: var(--me-primary-2);\n  color: var(--me-white);\n  border-color: var(--me-primary-2);\n}\n.btn-primary[data-v-ecf1c0a0]:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-success[data-v-ecf1c0a0] {\n  background-color: var(--me-success-2);\n  color: var(--me-white);\n  border-color: var(--me-success-2);\n}\n.btn-success[data-v-ecf1c0a0]:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-danger[data-v-ecf1c0a0] {\n  background-color: var(--me-danger-2);\n  color: var(--me-white);\n  border-color: var(--me-danger-2);\n}\n.btn-danger[data-v-ecf1c0a0]:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-warning[data-v-ecf1c0a0] {\n  background-color: var(--me-warning-2);\n  color: var(--me-white);\n  border-color: var(--me-warning-2);\n}\n.btn-warning[data-v-ecf1c0a0]:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-secondary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.btn-secondary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.btn-primary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.btn-primary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.btn-success[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.btn-success[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.btn-danger[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.btn-danger[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.btn-warning[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.btn-warning[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.btn-light-secondary[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: transparent;\n}\n.btn-light-secondary[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-primary[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: transparent;\n}\n.btn-light-primary[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-success[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: transparent;\n}\n.btn-light-success[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-danger[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: transparent;\n}\n.btn-light-danger[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-warning[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: transparent;\n}\n.btn-light-warning[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-secondary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.btn-light-secondary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.btn-light-primary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.btn-light-primary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.btn-light-success[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.btn-light-success[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.btn-light-danger[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.btn-light-danger[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.btn-light-warning[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.btn-light-warning[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.btn-outline-secondary[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-2);\n}\n.btn-outline-secondary[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-primary[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: var(--me-primary-2);\n}\n.btn-outline-primary[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-success[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: var(--me-success-2);\n}\n.btn-outline-success[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-danger[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.btn-outline-danger[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-warning[data-v-ecf1c0a0] {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: var(--me-warning-2);\n}\n.btn-outline-warning[data-v-ecf1c0a0]:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-secondary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.btn-outline-secondary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.btn-outline-primary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.btn-outline-primary[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.btn-outline-success[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.btn-outline-success[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.btn-outline-danger[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.btn-outline-danger[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.btn-outline-warning[data-v-ecf1c0a0]:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.btn-outline-warning[data-v-ecf1c0a0]:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-button.btn-padding[data-v-ecf1c0a0] {\n  padding-left: 16px;\n  padding-right: 16px;\n}\n.me-button.append-padding[data-v-ecf1c0a0] {\n  padding-right: 11px;\n}\n.me-button.append-padding .append-icon[data-v-ecf1c0a0] {\n  margin-left: 4px;\n}\n.me-button.prepend-padding[data-v-ecf1c0a0] {\n  padding-left: 11px;\n}\n.me-button.prepend-padding .prepend-icon[data-v-ecf1c0a0] {\n  margin-right: 4px;\n}\n.me-button.btn[data-v-ecf1c0a0] {\n  border-radius: 8px;\n}\n.me-button.btn-sm[data-v-ecf1c0a0] {\n  height: 32px;\n  font-size: 12px;\n  line-height: 22px;\n}\n.me-button.btn-sm.btn-width[data-v-ecf1c0a0] {\n  min-width: 104px;\n}\n.me-button.btn-sm.icon-width[data-v-ecf1c0a0] {\n  width: 32px;\n}\n.me-button.btn-md[data-v-ecf1c0a0] {\n  height: 40px;\n  font-size: 14px;\n  line-height: 26px;\n}\n.me-button.btn-md.btn-width[data-v-ecf1c0a0] {\n  min-width: 120px;\n}\n.me-button.btn-md.icon-width[data-v-ecf1c0a0] {\n  width: 40px;\n}\n.me-button.btn-lg[data-v-ecf1c0a0] {\n  height: 48px;\n  font-size: 16px;\n  line-height: 30px;\n}\n.me-button.btn-lg.btn-width[data-v-ecf1c0a0] {\n  min-width: 140px;\n}\n.me-button.btn-lg.icon-width[data-v-ecf1c0a0] {\n  width: 48px;\n}\n.me-button.busy .me-spinner[data-v-ecf1c0a0] {\n  position: relative;\n  top: -2px;\n}\n.me-button.busy.btn-sm .me-spinner[data-v-ecf1c0a0] {\n  top: -1px;\n}\n.me-button.busy.btn-lg .me-spinner[data-v-ecf1c0a0] {\n  top: -4px;\n}\n@media print {\n.me-button[data-v-ecf1c0a0] {\n    display: none;\n}\n}\n\n/*# sourceMappingURL=Button.vue.map */", map: {"version":3,"sources":["Button.vue","/app/build/src/components/Button.vue"],"names":[],"mappings":"AAAA;EACE,eAAe;AACjB;AACA;EACE,gBAAgB;AAClB;AACA;EACE,2BAA2B;AAC7B;AACA;EACE,kCAAkC;EAClC,sBAAsB;EACtB,8BAA8B;AAChC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,mCAAmC;EACnC,uBAAuB;EACvB,+BAA+B;AACjC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,yBAAyB;EACzB,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,mCAAmC;EACnC,uBAAuB;EACvB,+BAA+B;AACjC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,mCAAmC;EACnC,uBAAuB;EACvB,+BAA+B;AACjC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;ACrMA;EACA,kBAAA;EACA,mBAAA;ADwMA;ACrMA;EACA,mBAAA;ADuMA;ACrMA;EACA,gBAAA;ADuMA;ACnMA;EACA,kBAAA;ADqMA;ACnMA;EACA,iBAAA;ADqMA;ACjMA;EACA,kBAAA;ADmMA;ACjMA;EACA,YAAA;EACA,eAAA;EACA,iBAAA;ADmMA;ACjMA;EACA,gBAAA;ADmMA;AChMA;EACA,WAAA;ADkMA;AC9LA;EACA,YAAA;EACA,eAAA;EACA,iBAAA;ADgMA;AC9LA;EACA,gBAAA;ADgMA;AC7LA;EACA,WAAA;AD+LA;AC3LA;EACA,YAAA;EACA,eAAA;EACA,iBAAA;AD6LA;AC3LA;EACA,gBAAA;AD6LA;AC1LA;EACA,WAAA;AD4LA;ACtLA;EACA,kBAAA;EACA,SAAA;ADwLA;ACpLA;EACA,SAAA;ADsLA;ACjLA;EACA,SAAA;ADmLA;AC9KA;AAxFA;IAyFA,aAAA;ADiLE;AACF;;AAEA,qCAAqC","file":"Button.vue","sourcesContent":[".btn {\n  font-size: 14px;\n}\n.btn:focus {\n  box-shadow: none;\n}\n.btn:focus:active {\n  box-shadow: none !important;\n}\n.btn-secondary {\n  background-color: var(--me-gray-2);\n  color: var(--me-white);\n  border-color: var(--me-gray-2);\n}\n.btn-secondary:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-primary {\n  background-color: var(--me-primary-2);\n  color: var(--me-white);\n  border-color: var(--me-primary-2);\n}\n.btn-primary:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-success {\n  background-color: var(--me-success-2);\n  color: var(--me-white);\n  border-color: var(--me-success-2);\n}\n.btn-success:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-danger {\n  background-color: var(--me-danger-2);\n  color: var(--me-white);\n  border-color: var(--me-danger-2);\n}\n.btn-danger:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-warning {\n  background-color: var(--me-warning-2);\n  color: var(--me-white);\n  border-color: var(--me-warning-2);\n}\n.btn-warning:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.btn-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.btn-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.btn-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.btn-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.btn-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.btn-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.btn-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.btn-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.btn-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.btn-light-secondary {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: transparent;\n}\n.btn-light-secondary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-primary {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: transparent;\n}\n.btn-light-primary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-success {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: transparent;\n}\n.btn-light-success:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-danger {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: transparent;\n}\n.btn-light-danger:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-warning {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: transparent;\n}\n.btn-light-warning:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.btn-light-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.btn-light-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.btn-light-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.btn-light-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.btn-light-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.btn-light-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.btn-light-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.btn-light-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.btn-light-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.btn-light-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.btn-outline-secondary {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-2);\n}\n.btn-outline-secondary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-primary {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: var(--me-primary-2);\n}\n.btn-outline-primary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-success {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: var(--me-success-2);\n}\n.btn-outline-success:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-danger {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.btn-outline-danger:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-warning {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: var(--me-warning-2);\n}\n.btn-outline-warning:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.btn-outline-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.btn-outline-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.btn-outline-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.btn-outline-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.btn-outline-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.btn-outline-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.btn-outline-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.btn-outline-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.btn-outline-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.btn-outline-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n\n.me-button.btn-padding {\n  padding-left: 16px;\n  padding-right: 16px;\n}\n.me-button.append-padding {\n  padding-right: 11px;\n}\n.me-button.append-padding .append-icon {\n  margin-left: 4px;\n}\n.me-button.prepend-padding {\n  padding-left: 11px;\n}\n.me-button.prepend-padding .prepend-icon {\n  margin-right: 4px;\n}\n.me-button.btn {\n  border-radius: 8px;\n}\n.me-button.btn-sm {\n  height: 32px;\n  font-size: 12px;\n  line-height: 22px;\n}\n.me-button.btn-sm.btn-width {\n  min-width: 104px;\n}\n.me-button.btn-sm.icon-width {\n  width: 32px;\n}\n.me-button.btn-md {\n  height: 40px;\n  font-size: 14px;\n  line-height: 26px;\n}\n.me-button.btn-md.btn-width {\n  min-width: 120px;\n}\n.me-button.btn-md.icon-width {\n  width: 40px;\n}\n.me-button.btn-lg {\n  height: 48px;\n  font-size: 16px;\n  line-height: 30px;\n}\n.me-button.btn-lg.btn-width {\n  min-width: 140px;\n}\n.me-button.btn-lg.icon-width {\n  width: 48px;\n}\n.me-button.busy .me-spinner {\n  position: relative;\n  top: -2px;\n}\n.me-button.busy.btn-sm .me-spinner {\n  top: -1px;\n}\n.me-button.busy.btn-lg .me-spinner {\n  top: -4px;\n}\n@media print {\n  .me-button {\n    display: none;\n  }\n}\n\n/*# sourceMappingURL=Button.vue.map */","<template>\n  <BButton\n    :block=\"block\"\n    :class=\"[\n      customVariant,\n      {\n        busy: busy,\n        'btn-width': !icon && variant !== 'link',\n        'btn-padding': !icon,\n        'icon-width': icon,\n        'prepend-padding': prependIcon,\n        'append-padding': appendIcon,\n      }\n    ]\"\n    :disabled=\"disabled || busy\"\n    :href=\"href\"\n    :pill=\"pill\"\n    :size=\"size\"\n    :target=\"target\"\n    :type=\"type\"\n    :variant=\"variant\"\n    @click=\"$emit('click', $event)\"\n    class=\"me-button\"\n    data-cy=\"b-button\"\n  >\n    <MeIcon\n      v-if=\"icon\"\n      :icon=\"icon\"\n    />\n\n    <template v-else>\n      <MeIcon\n        v-if=\"prependIcon\"\n        :icon=\"prependIcon\"\n        class=\"prepend-icon\"\n        data-cy=\"prependIcon\"\n      />\n\n      <slot />\n\n      <MeIcon\n        v-if=\"appendIcon\"\n        :icon=\"appendIcon\"\n        class=\"append-icon\"\n        data-cy=\"appendIcon\"\n      />\n    </template>\n\n    <Spinner v-if=\"busy\" small type=\"grow\" />\n  </BButton>\n</template>\n\n<script>\nimport { BButton } from 'bootstrap-vue'\nimport Spinner from './Spinner.vue'\nimport MeIcon from './Icon.vue'\n\nexport default {\n  name: 'MeButton',\n  components: {\n    BButton,\n    Spinner,\n    MeIcon\n  },\n  props: {\n    appendIcon: {\n      type: String,\n      default: null\n    },\n    block: {\n      type: Boolean,\n      default: false\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    customVariant: {\n      type: String,\n      default: null\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    href: {\n      type: String,\n      default: ''\n    },\n    icon: {\n      type: String,\n      default: null\n    },\n    pill: {\n      type: Boolean,\n      default: false\n    },\n    prependIcon: {\n      type: String,\n      default: null\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    target: {\n      type: String,\n      default: '_self'\n    },\n    type: {\n      type: String,\n      default: 'button'\n    },\n    variant: {\n      type: String,\n      default: 'secondary'\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/button.scss\";\n\n  .me-button {\n\n    &.btn-padding {\n      padding-left: 16px;\n      padding-right: 16px;\n    }\n\n    &.append-padding {\n      padding-right: 11px;\n\n      .append-icon {\n        margin-left: 4px;\n      }\n    }\n\n    &.prepend-padding {\n      padding-left: 11px;\n\n      .prepend-icon {\n        margin-right: 4px;\n      }\n    }\n\n    &.btn {\n      border-radius: 8px;\n\n      &-sm {\n        height: 32px;\n        font-size: 12px;\n        line-height: 22px;\n\n        &.btn-width {\n          min-width: 104px;\n        }\n\n        &.icon-width {\n          width: 32px;\n        }\n      }\n\n      &-md {\n        height: 40px;\n        font-size: 14px;\n        line-height: 26px;\n\n        &.btn-width {\n          min-width: 120px;\n        }\n\n        &.icon-width {\n          width: 40px;\n        }\n      }\n\n      &-lg {\n        height: 48px;\n        font-size: 16px;\n        line-height: 30px;\n\n        &.btn-width {\n          min-width: 140px;\n        }\n\n        &.icon-width {\n          width: 48px;\n        }\n      }\n    }\n\n    &.busy {\n      .me-spinner {\n        position: relative;\n        top: -2px;\n      }\n\n      &.btn-sm {\n        .me-spinner {\n          top: -1px;\n        }\n      }\n\n      &.btn-lg {\n        .me-spinner {\n          top: -4px;\n        }\n      }\n    }\n\n    @media print {\n      display: none;\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$6 = "data-v-ecf1c0a0";
  /* module identifier */
  const __vue_module_identifier__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$6 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$6 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$6, staticRenderFns: __vue_staticRenderFns__$6 },
    __vue_inject_styles__$6,
    __vue_script__$6,
    __vue_scope_id__$6,
    __vue_is_functional_template__$6,
    __vue_module_identifier__$6,
    false,
    createInjector,
    undefined,
    undefined
  );

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now = function() {
  return _root.Date.now();
};

var now_1 = now;

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$2 = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce$1(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  wait = toNumber_1(wait) || 0;
  if (isObject_1(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax$2(toNumber_1(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now_1();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now_1());
  }

  function debounced() {
    var time = now_1(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce$1;

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$7 = {
  name: 'MeDropdownGroup',
  props: {
    header: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("li", { staticClass: "me-dropdown-group" }, [
    _vm.header
      ? _c(
          "header",
          { staticClass: "group-header", attrs: { "data-cy": "group-header" } },
          [_vm._v("\n    " + _vm._s(_vm.header) + "\n  ")]
        )
      : _vm._e(),
    _vm._v(" "),
    _c(
      "ul",
      { staticClass: "group-items", attrs: { "data-cy": "group-items" } },
      [_vm._t("default")],
      2
    )
  ])
};
var __vue_staticRenderFns__$7 = [];
__vue_render__$7._withStripped = true;

  /* style */
  const __vue_inject_styles__$7 = function (inject) {
    if (!inject) return
    inject("data-v-c3b5c4c8_0", { source: ".me-dropdown-group .group-header[data-v-c3b5c4c8] {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  padding: 8px 16px;\n  line-height: 16px;\n}\n.me-dropdown-group .group-items[data-v-c3b5c4c8] {\n  list-style-type: none;\n  padding: 0;\n  margin: 0;\n}\n\n/*# sourceMappingURL=DropdownGroup.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dropdown/DropdownGroup.vue","DropdownGroup.vue"],"names":[],"mappings":"AAiCA;EACA,eAAA;EACA,uBAAA;EACA,iBAAA;EACA,iBAAA;AChCA;ADmCA;EACA,qBAAA;EACA,UAAA;EACA,SAAA;ACjCA;;AAEA,4CAA4C","file":"DropdownGroup.vue","sourcesContent":["<template>\n  <li class=\"me-dropdown-group\">\n    <header\n      v-if=\"header\"\n      class=\"group-header\"\n      data-cy=\"group-header\"\n    >\n      {{ header }}\n    </header>\n\n    <ul\n      class=\"group-items\"\n      data-cy=\"group-items\"\n    >\n      <slot />\n    </ul>\n  </li>\n</template>\n\n<script>\nexport default {\n  name: 'MeDropdownGroup',\n  props: {\n    header: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-dropdown-group {\n  .group-header {\n    font-size: 12px;\n    color: var(--me-gray-4);\n    padding: 8px 16px;\n    line-height: 16px;\n  }\n\n  .group-items {\n    list-style-type: none;\n    padding: 0;\n    margin: 0;\n  }\n}\n</style>\n",".me-dropdown-group .group-header {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  padding: 8px 16px;\n  line-height: 16px;\n}\n.me-dropdown-group .group-items {\n  list-style-type: none;\n  padding: 0;\n  margin: 0;\n}\n\n/*# sourceMappingURL=DropdownGroup.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$7 = "data-v-c3b5c4c8";
  /* module identifier */
  const __vue_module_identifier__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$7 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$7 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$7, staticRenderFns: __vue_staticRenderFns__$7 },
    __vue_inject_styles__$7,
    __vue_script__$7,
    __vue_scope_id__$7,
    __vue_is_functional_template__$7,
    __vue_module_identifier__$7,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$8 = {
  name: 'MeDropDown',
  components: {
    BDropdown,
    BDropdownDivider,
    MeDropdownItem: __vue_component__$2,
    MeDropdownGroup: __vue_component__$7
  },
  props: {
    backdrop: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    boundary: {
      type: String,
      default: 'scrollParent'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dropleft: {
      type: Boolean,
      default: false
    },
    dropright: {
      type: Boolean,
      default: false
    },
    dropup: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default() {
        return []
      }
    },
    menuRight: {
      type: Boolean,
      default: false
    },
    noCaret: {
      type: Boolean,
      default: false
    },
    pill: {
      type: Boolean,
      default: false
    },
    pillItems: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    },
    split: {
      type: Boolean,
      default: false
    },
    splitHref: {
      type: String,
      default: ''
    },
    splitVariant: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'secondary'
    },
    noFlip: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      opened: false
    }
  },
  computed: {
    expanded() {
      return this.items.some((item) => {
        if (item.group) {
          const groupSubItems = item.group.items.some((groupItem) => {
            Object.hasOwn(groupItem, 'subItems');
          });

          return groupSubItems
        }
        return Object.hasOwn(item, 'subItems')
      })
    }
  },

  methods: {
    handleClick(item, itemObj) {
      this.hideSubItems(itemObj.label);

      if (item.click && !item.subItems) item.click(item);
      if (!item.subItems) this.hide();
    },

    show() {
      this.$refs['b-dropdown'].show();
    },

    hide(event) {
      this.hideSubItems();
      this.$refs['b-dropdown'].hide();
      this.$emit('hide', event);
    },

    hideSubItems(label) {
      if (this.$refs['dropdown-item']) {
        this.$refs['dropdown-item'].filter(item => item.label !== label)
          .forEach(item => item.hide());
      }
    }
  }
};

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        "me-dropdown",
        {
          pill: _vm.pill,
          "pill-items": _vm.pillItems,
          opened: _vm.opened,
          block: _vm.block,
          "btn-min-width": _vm.text,
          expanded: _vm.expanded
        }
      ]
    },
    [
      _c(
        "BDropdown",
        {
          ref: "b-dropdown",
          attrs: {
            block: _vm.block,
            boundary: _vm.boundary,
            disabled: _vm.disabled,
            dropleft: _vm.dropleft,
            dropright: _vm.dropright,
            dropup: _vm.dropup,
            "no-caret": _vm.noCaret,
            right: _vm.menuRight,
            size: _vm.size,
            "split-href": _vm.splitHref,
            "split-variant": _vm.splitVariant,
            split: _vm.split,
            text: _vm.text,
            variant: _vm.variant,
            "no-flip": _vm.noFlip,
            "data-cy": "b-dropdown"
          },
          on: {
            click: function($event) {
              return _vm.$emit("click", $event)
            },
            hidden: function($event) {
              _vm.opened = false;
            },
            hide: _vm.hide,
            show: function($event) {
              return _vm.$emit("show", $event)
            },
            shown: function($event) {
              _vm.opened = true;
            }
          },
          scopedSlots: _vm._u(
            [
              _vm._l(_vm.$scopedSlots, function(_, slot) {
                return {
                  key: slot,
                  fn: function(scope) {
                    return [_vm._t(slot, null, null, scope)]
                  }
                }
              })
            ],
            null,
            true
          )
        },
        [
          _vm._v(" "),
          _vm._l(_vm.items, function(item, index) {
            return [
              item.group && item.group.items
                ? _c(
                    "MeDropdownGroup",
                    {
                      key: item.group.label || index,
                      attrs: { header: item.group.label }
                    },
                    _vm._l(item.group.items, function(groupItem) {
                      return _c("MeDropdownItem", {
                        key: groupItem.id || groupItem.label || index,
                        ref: "dropdown-item",
                        refInFor: true,
                        attrs: {
                          active: groupItem.active,
                          disabled: groupItem.disabled,
                          href: groupItem.href,
                          target: groupItem.target,
                          label: groupItem.label,
                          itemClazz: groupItem.class,
                          description: groupItem.description,
                          descriptionHTML: groupItem.descriptionHTML,
                          placement: groupItem.placement,
                          popover: groupItem.popover,
                          id:
                            groupItem.id ||
                            (groupItem.popover && groupItem.popover.id) ||
                            null,
                          icon: groupItem.icon,
                          pill: _vm.pillItems,
                          subItems: groupItem.subItems
                        },
                        on: {
                          click: function(itemOBj) {
                            return _vm.handleClick(groupItem, itemOBj)
                          }
                        }
                      })
                    }),
                    1
                  )
                : _c("MeDropdownItem", {
                    key: item.id || item.label || index,
                    ref: "dropdown-item",
                    refInFor: true,
                    attrs: {
                      active: item.active,
                      disabled: item.disabled,
                      href: item.href,
                      target: item.target,
                      label: item.label,
                      itemClazz: item.class,
                      description: item.description,
                      descriptionHTML: item.descriptionHTML,
                      placement: item.placement,
                      popover: item.popover,
                      id: item.id || (item.popover && item.popover.id) || null,
                      icon: item.icon,
                      pill: _vm.pillItems,
                      subItems: item.subItems
                    },
                    on: {
                      click: function(itemOBj) {
                        return _vm.handleClick(item, itemOBj)
                      }
                    }
                  }),
              _vm._v(" "),
              item.divider
                ? _c("BDropdownDivider", {
                    key:
                      "divider" +
                      (item.label || (item.group && item.group.label))
                  })
                : _vm._e()
            ]
          })
        ],
        2
      ),
      _vm._v(" "),
      _vm.backdrop ? _c("div", { staticClass: "backdrop" }) : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$8 = [];
__vue_render__$8._withStripped = true;

  /* style */
  const __vue_inject_styles__$8 = function (inject) {
    if (!inject) return
    inject("data-v-0e45c672_0", { source: "@charset \"UTF-8\";\n.me-dropdown[data-v-0e45c672] {\n  display: inline-block;\n}\n.me-dropdown .dropdown-divider[data-v-0e45c672] {\n  margin: 0;\n}\n.me-dropdown.expanded[data-v-0e45c672]  .dropdown-menu {\n  overflow-y: unset !important;\n}\n.me-dropdown.pill[data-v-0e45c672]  .dropdown-toggle {\n  border-radius: 50rem !important;\n}\n.me-dropdown.block[data-v-0e45c672] {\n  width: 100%;\n}\n.me-dropdown.pill-items[data-v-0e45c672]  .dropdown-menu {\n  background-color: transparent !important;\n  border: none !important;\n  box-shadow: none !important;\n}\n.me-dropdown.btn-min-width[data-v-0e45c672]  .dropdown .btn-sm.dropdown-toggle:first-child {\n  min-width: 104px;\n}\n.me-dropdown.btn-min-width[data-v-0e45c672]  .dropdown .btn-sm:not(.dropdown-toggle) {\n  min-width: 80px;\n}\n.me-dropdown.btn-min-width[data-v-0e45c672]  .dropdown .btn-md.dropdown-toggle:first-child {\n  min-width: 120px;\n}\n.me-dropdown.btn-min-width[data-v-0e45c672]  .dropdown .btn-md:not(.dropdown-toggle) {\n  min-width: 90px;\n}\n.me-dropdown.btn-min-width[data-v-0e45c672]  .dropdown .btn-lg.dropdown-toggle:first-child {\n  min-width: 140px;\n}\n.me-dropdown.btn-min-width[data-v-0e45c672]  .dropdown .btn-lg:not(.dropdown-toggle) {\n  min-width: 104px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn {\n  font-size: 14px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn:focus {\n  box-shadow: none;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn:focus:active {\n  box-shadow: none !important;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-secondary {\n  background-color: var(--me-gray-2);\n  color: var(--me-white);\n  border-color: var(--me-gray-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-secondary:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-primary {\n  background-color: var(--me-primary-2);\n  color: var(--me-white);\n  border-color: var(--me-primary-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-primary:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-success {\n  background-color: var(--me-success-2);\n  color: var(--me-white);\n  border-color: var(--me-success-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-success:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-danger {\n  background-color: var(--me-danger-2);\n  color: var(--me-white);\n  border-color: var(--me-danger-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-danger:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-warning {\n  background-color: var(--me-warning-2);\n  color: var(--me-white);\n  border-color: var(--me-warning-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-warning:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-secondary {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: transparent;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-secondary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-primary {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: transparent;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-primary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-success {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: transparent;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-success:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-danger {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: transparent;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-danger:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-warning {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: transparent;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-warning:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-light-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-secondary {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-secondary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-primary {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: var(--me-primary-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-primary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-success {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: var(--me-success-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-success:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-danger {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-danger:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-warning {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: var(--me-warning-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-warning:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-outline-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 8px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-sm {\n  height: 32px;\n  font-size: 12px;\n  line-height: 22px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-md {\n  height: 40px;\n  font-size: 14px;\n  line-height: 26px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .btn-lg {\n  height: 48px;\n  font-size: 16px;\n  line-height: 30px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu {\n  background: var(--me-white);\n  border-radius: 8px;\n  border: none;\n  box-shadow: var(--me-shadow-level-1);\n  max-height: 70vh;\n  overflow-y: auto;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu::-webkit-scrollbar-track {\n  display: none;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu:visited, .me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu:focus {\n  border: none;\n  outline: 0 !important;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-menu .dropdown-header {\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-4);\n  height: 32px;\n  padding: 8px 16px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.dropup .dropdown-toggle:after {\n  content: \"\";\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.dropup .dropdown-toggle:not(:empty):after {\n  margin-left: 8px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.dropright .dropdown-toggle:after {\n  content: \"\";\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.dropright .dropdown-toggle:not(:empty):after {\n  margin-left: 8px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.dropleft .dropdown-toggle:before {\n  border: none !important;\n  content: \"\";\n  font-family: \"ME Icons\";\n  font-size: 12px;\n  vertical-align: middle;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.dropleft .dropdown-toggle:not(:empty):before {\n  margin-right: 8px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-toggle:not(:empty):after {\n  margin-left: 8px;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-toggle::after {\n  border: none !important;\n  content: \"\";\n  font-family: \"ME Icons Light\";\n  font-size: 12px;\n  vertical-align: middle;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown .dropdown-toggle-no-caret::after, .me-dropdown[data-v-0e45c672]  .dropdown .dropdown-toggle-no-caret::before {\n  display: none;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.btn-group .btn:not(:last-child):not(.dropdown-toggle) {\n  border-radius: 8px 0 0 8px;\n  vertical-align: middle;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.btn-group .btn:not(:first-child) {\n  border-radius: 0 8px 8px 0;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split {\n  vertical-align: middle;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split.btn-primary, .me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split.btn-secondary, .me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split.btn-success, .me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split.btn-danger, .me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split.btn-warning {\n  border-left: 1px solid rgba(255, 255, 255, 0.3);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.btn-group .dropdown-toggle-split:after {\n  margin-left: 0 !important;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn {\n  border-color: transparent;\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-secondary:not(:disabled):not(.disabled) {\n  background: var(--me-gray-5);\n  color: var(--me-gray-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-primary:not(:disabled):not(.disabled) {\n  background: var(--me-primary-1);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-success:not(:disabled):not(.disabled) {\n  background: var(--me-success-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-danger:not(:disabled):not(.disabled) {\n  background: var(--me-danger-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-warning:not(:disabled):not(.disabled) {\n  background: var(--me-warning-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-light-secondary:not(:disabled):not(.disabled) {\n  background: var(--me-gray-5);\n  color: var(--me-gray-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-light-primary:not(:disabled):not(.disabled) {\n  background: var(--me-primary-1);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-light-success:not(:disabled):not(.disabled) {\n  background: var(--me-success-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-light-danger:not(:disabled):not(.disabled) {\n  background: var(--me-danger-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-light-warning:not(:disabled):not(.disabled) {\n  background: var(--me-warning-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-outline-secondary:not(:disabled):not(.disabled) {\n  background: var(--me-gray-5);\n  color: var(--me-gray-2);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-outline-primary:not(:disabled):not(.disabled) {\n  background: var(--me-primary-1);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-outline-success:not(:disabled):not(.disabled) {\n  background: var(--me-success-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-outline-danger:not(:disabled):not(.disabled) {\n  background: var(--me-danger-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle.btn-outline-warning:not(:disabled):not(.disabled) {\n  background: var(--me-warning-0);\n  color: var(--me-white);\n}\n.me-dropdown[data-v-0e45c672]  .dropdown.show .dropdown-toggle-split::after {\n  content: \"\";\n}\n.me-dropdown > div.backdrop[data-v-0e45c672] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 1100;\n}\n.me-dropdown.opened > div.backdrop[data-v-0e45c672] {\n  display: block;\n  opacity: 0.4;\n}\n.me-dropdown.opened .dropdown[data-v-0e45c672] {\n  z-index: 1102;\n}\n.me-dropdown.opened .dropdown[data-v-0e45c672]  .dropdown-toggle::after {\n  content: \"\";\n}\n.me-dropdown.opened .dropup[data-v-0e45c672]  .dropdown-toggle::after {\n  content: \"\";\n}\n.me-dropdown.opened .dropright[data-v-0e45c672]  .dropdown-toggle::after {\n  content: \"\";\n}\n.me-dropdown.opened .dropleft[data-v-0e45c672]  .dropdown-toggle::before {\n  content: \"\";\n}\n.dropdown-tooltip[data-v-0e45c672] {\n  z-index: 1300;\n}\n\n/*# sourceMappingURL=DropDown.vue.map */", map: {"version":3,"sources":["DropDown.vue","/app/build/src/components/dropdown/DropDown.vue"],"names":[],"mappings":"AAAA,gBAAgB;AC8OhB;EACA,qBAAA;AD5OA;AC8OA;EACA,SAAA;AD5OA;ACgPA;EACA,4BAAA;AD9OA;ACmPA;EACA,+BAAA;ADjPA;ACqPA;EACA,WAAA;ADnPA;ACuPA;EACA,wCAAA;EACA,uBAAA;EACA,2BAAA;ADrPA;AC4PA;EACA,gBAAA;AD1PA;AC6PA;EACA,eAAA;AD3PA;ACgQA;EACA,gBAAA;AD9PA;ACiQA;EACA,eAAA;AD/PA;ACoQA;EACA,gBAAA;ADlQA;ACqQA;EACA,gBAAA;ADnQA;AACA;EACE,eAAe;AACjB;AACA;EACE,gBAAgB;AAClB;AACA;EACE,2BAA2B;AAC7B;AACA;EACE,kCAAkC;EAClC,sBAAsB;EACtB,8BAA8B;AAChC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,mCAAmC;EACnC,uBAAuB;EACvB,+BAA+B;AACjC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,yBAAyB;EACzB,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,yBAAyB;AAC3B;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,yBAAyB;EACzB,UAAU;AACZ;AACA;EACE,mCAAmC;EACnC,uBAAuB;EACvB,+BAA+B;AACjC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,6BAA6B;EAC7B,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,6BAA6B;EAC7B,uBAAuB;EACvB,8BAA8B;EAC9B,UAAU;AACZ;AACA;EACE,mCAAmC;EACnC,uBAAuB;EACvB,+BAA+B;AACjC;AACA;EACE,kCAAkC;EAClC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;AACA;EACE,oCAAoC;EACpC,yBAAyB;EACzB,gCAAgC;AAClC;AACA;EACE,oCAAoC;EACpC,sBAAsB;EACtB,gCAAgC;AAClC;AACA;EACE,qCAAqC;EACrC,0BAA0B;EAC1B,iCAAiC;AACnC;AACA;EACE,qCAAqC;EACrC,sBAAsB;EACtB,iCAAiC;AACnC;ACxDA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,kBAAA;AD0DA;ACxDA;EACA,YAAA;EACA,eAAA;EACA,iBAAA;AD0DA;ACvDA;EACA,YAAA;EACA,eAAA;EACA,iBAAA;ADyDA;ACtDA;EACA,YAAA;EACA,eAAA;EACA,iBAAA;ADwDA;ACpDA;EACA,2BAAA;EACA,kBAAA;EACA,YAAA;EACA,oCAAA;EACA,gBAAA;EACA,gBAAA;ADsDA;ACpDA;EACA,UAAA;EACA,WAAA;ADsDA;ACpDA;EACA,aAAA;ADsDA;ACnDA;EACA,+BAAA;EACA,mBAAA;ADqDA;ACnDA;EACA,+BAAA;ADqDA;AChDA;EAEA,YAAA;EACA,qBAAA;ADiDA;AC9CA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;EACA,YAAA;EACA,iBAAA;ADgDA;AC1CA;EACA,YAAA;AD4CA;ACzCA;EACA,gBAAA;AD2CA;ACpCA;EACA,YAAA;ADsCA;ACnCA;EACA,gBAAA;ADqCA;AC9BA;EACA,uBAAA;EACA,YAAA;EACA,uBAAA;EACA,eAAA;EACA,sBAAA;ADgCA;AC7BA;EACA,iBAAA;AD+BA;ACzBA;EACA,gBAAA;AD2BA;ACxBA;EACA,uBAAA;EACA,YAAA;EACA,6BAAA;EACA,eAAA;EACA,sBAAA;AD0BA;ACtBA;EACA,aAAA;ADwBA;AClBA;EACA,0BAAA;EACA,sBAAA;ADoBA;ACjBA;EACA,0BAAA;ADmBA;ACfA;EACA,sBAAA;ADiBA;ACfA;EAKA,+CAAA;ADaA;ACTA;EACA,yBAAA;ADWA;ACHA;EACA,yBAAA;ADKA;ACFA;EACA,4BAAA;EACA,uBAAA;ADIA;ACDA;EACA,+BAAA;EACA,sBAAA;ADGA;ACAA;EACA,+BAAA;EACA,sBAAA;ADEA;ACCA;EACA,8BAAA;EACA,sBAAA;ADCA;ACEA;EACA,+BAAA;EACA,sBAAA;ADAA;ACtBA;EACA,4BAAA;EACA,uBAAA;ADwBA;ACrBA;EACA,+BAAA;EACA,sBAAA;ADuBA;ACpBA;EACA,+BAAA;EACA,sBAAA;ADsBA;ACnBA;EACA,8BAAA;EACA,sBAAA;ADqBA;AClBA;EACA,+BAAA;EACA,sBAAA;ADoBA;AC1CA;EACA,4BAAA;EACA,uBAAA;AD4CA;ACzCA;EACA,+BAAA;EACA,sBAAA;AD2CA;ACxCA;EACA,+BAAA;EACA,sBAAA;AD0CA;ACvCA;EACA,8BAAA;EACA,sBAAA;ADyCA;ACtCA;EACA,+BAAA;EACA,sBAAA;ADwCA;ACxBA;EACA,YAAA;AD0BA;ACnBA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,aAAA;ADqBA;ACjBA;EACA,cAAA;EACA,YAAA;ADmBA;AChBA;EACA,aAAA;ADkBA;ACjBA;EACA,YAAA;ADmBA;ACdA;EACA,YAAA;ADgBA;ACXA;EACA,YAAA;ADaA;ACRA;EACA,YAAA;ADUA;ACJA;EACA,aAAA;ADOA;;AAEA,uCAAuC","file":"DropDown.vue","sourcesContent":["@charset \"UTF-8\";\n.me-dropdown {\n  display: inline-block;\n}\n.me-dropdown .dropdown-divider {\n  margin: 0;\n}\n.me-dropdown.expanded ::v-deep .dropdown-menu {\n  overflow-y: unset !important;\n}\n.me-dropdown.pill ::v-deep .dropdown-toggle {\n  border-radius: 50rem !important;\n}\n.me-dropdown.block {\n  width: 100%;\n}\n.me-dropdown.pill-items ::v-deep .dropdown-menu {\n  background-color: transparent !important;\n  border: none !important;\n  box-shadow: none !important;\n}\n.me-dropdown.btn-min-width ::v-deep .dropdown .btn-sm.dropdown-toggle:first-child {\n  min-width: 104px;\n}\n.me-dropdown.btn-min-width ::v-deep .dropdown .btn-sm:not(.dropdown-toggle) {\n  min-width: 80px;\n}\n.me-dropdown.btn-min-width ::v-deep .dropdown .btn-md.dropdown-toggle:first-child {\n  min-width: 120px;\n}\n.me-dropdown.btn-min-width ::v-deep .dropdown .btn-md:not(.dropdown-toggle) {\n  min-width: 90px;\n}\n.me-dropdown.btn-min-width ::v-deep .dropdown .btn-lg.dropdown-toggle:first-child {\n  min-width: 140px;\n}\n.me-dropdown.btn-min-width ::v-deep .dropdown .btn-lg:not(.dropdown-toggle) {\n  min-width: 104px;\n}\n.me-dropdown ::v-deep .dropdown .btn {\n  font-size: 14px;\n}\n.me-dropdown ::v-deep .dropdown .btn:focus {\n  box-shadow: none;\n}\n.me-dropdown ::v-deep .dropdown .btn:focus:active {\n  box-shadow: none !important;\n}\n.me-dropdown ::v-deep .dropdown .btn-secondary {\n  background-color: var(--me-gray-2);\n  color: var(--me-white);\n  border-color: var(--me-gray-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-secondary:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-primary {\n  background-color: var(--me-primary-2);\n  color: var(--me-white);\n  border-color: var(--me-primary-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-primary:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-success {\n  background-color: var(--me-success-2);\n  color: var(--me-white);\n  border-color: var(--me-success-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-success:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-danger {\n  background-color: var(--me-danger-2);\n  color: var(--me-white);\n  border-color: var(--me-danger-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-danger:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-warning {\n  background-color: var(--me-warning-2);\n  color: var(--me-white);\n  border-color: var(--me-warning-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-warning:disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.me-dropdown ::v-deep .dropdown .btn-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.me-dropdown ::v-deep .dropdown .btn-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.me-dropdown ::v-deep .dropdown .btn-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-secondary {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: transparent;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-secondary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-primary {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: transparent;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-primary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-success {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: transparent;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-success:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-danger {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: transparent;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-danger:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-warning {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: transparent;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-warning:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: transparent;\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-light-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-light-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-secondary {\n  background-color: transparent;\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-secondary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-primary {\n  background-color: transparent;\n  color: var(--me-primary-2);\n  border-color: var(--me-primary-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-primary:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-success {\n  background-color: transparent;\n  color: var(--me-success-2);\n  border-color: var(--me-success-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-success:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-danger {\n  background-color: transparent;\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-danger:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-warning {\n  background-color: transparent;\n  color: var(--me-warning-2);\n  border-color: var(--me-warning-2);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-warning:disabled {\n  background-color: transparent;\n  color: var(--me-gray-4);\n  border-color: var(--me-gray-6);\n  opacity: 1;\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-secondary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-0);\n  border-color: var(--me-bg-gray);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-secondary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-gray-5);\n  color: var(--me-gray-2);\n  border-color: var(--me-gray-5);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-primary:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n  border-color: var(--me-primary-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-primary:not(:disabled):not(.disabled):active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n  border-color: var(--me-primary-1);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-success:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n  border-color: var(--me-success-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-success:not(:disabled):not(.disabled):active {\n  background-color: var(--me-success-0);\n  color: var(--me-white);\n  border-color: var(--me-success-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-danger:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n  border-color: var(--me-danger-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-danger:not(:disabled):not(.disabled):active {\n  background-color: var(--me-danger-0);\n  color: var(--me-white);\n  border-color: var(--me-danger-0);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-warning:not(:disabled):not(.disabled):hover {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n  border-color: var(--me-warning-7);\n}\n.me-dropdown ::v-deep .dropdown .btn-outline-warning:not(:disabled):not(.disabled):active {\n  background-color: var(--me-warning-0);\n  color: var(--me-white);\n  border-color: var(--me-warning-0);\n}\n.me-dropdown ::v-deep .dropdown .btn {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  border-radius: 8px;\n}\n.me-dropdown ::v-deep .dropdown .btn-sm {\n  height: 32px;\n  font-size: 12px;\n  line-height: 22px;\n}\n.me-dropdown ::v-deep .dropdown .btn-md {\n  height: 40px;\n  font-size: 14px;\n  line-height: 26px;\n}\n.me-dropdown ::v-deep .dropdown .btn-lg {\n  height: 48px;\n  font-size: 16px;\n  line-height: 30px;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu {\n  background: var(--me-white);\n  border-radius: 8px;\n  border: none;\n  box-shadow: var(--me-shadow-level-1);\n  max-height: 70vh;\n  overflow-y: auto;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu::-webkit-scrollbar-track {\n  display: none;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu:visited, .me-dropdown ::v-deep .dropdown .dropdown-menu:focus {\n  border: none;\n  outline: 0 !important;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-menu .dropdown-header {\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-4);\n  height: 32px;\n  padding: 8px 16px;\n}\n.me-dropdown ::v-deep .dropdown.dropup .dropdown-toggle:after {\n  content: \"\";\n}\n.me-dropdown ::v-deep .dropdown.dropup .dropdown-toggle:not(:empty):after {\n  margin-left: 8px;\n}\n.me-dropdown ::v-deep .dropdown.dropright .dropdown-toggle:after {\n  content: \"\";\n}\n.me-dropdown ::v-deep .dropdown.dropright .dropdown-toggle:not(:empty):after {\n  margin-left: 8px;\n}\n.me-dropdown ::v-deep .dropdown.dropleft .dropdown-toggle:before {\n  border: none !important;\n  content: \"\";\n  font-family: \"ME Icons\";\n  font-size: 12px;\n  vertical-align: middle;\n}\n.me-dropdown ::v-deep .dropdown.dropleft .dropdown-toggle:not(:empty):before {\n  margin-right: 8px;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-toggle:not(:empty):after {\n  margin-left: 8px;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-toggle::after {\n  border: none !important;\n  content: \"\";\n  font-family: \"ME Icons Light\";\n  font-size: 12px;\n  vertical-align: middle;\n}\n.me-dropdown ::v-deep .dropdown .dropdown-toggle-no-caret::after, .me-dropdown ::v-deep .dropdown .dropdown-toggle-no-caret::before {\n  display: none;\n}\n.me-dropdown ::v-deep .dropdown.btn-group .btn:not(:last-child):not(.dropdown-toggle) {\n  border-radius: 8px 0 0 8px;\n  vertical-align: middle;\n}\n.me-dropdown ::v-deep .dropdown.btn-group .btn:not(:first-child) {\n  border-radius: 0 8px 8px 0;\n}\n.me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split {\n  vertical-align: middle;\n}\n.me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split.btn-primary, .me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split.btn-secondary, .me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split.btn-success, .me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split.btn-danger, .me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split.btn-warning {\n  border-left: 1px solid rgba(255, 255, 255, 0.3);\n}\n.me-dropdown ::v-deep .dropdown.btn-group .dropdown-toggle-split:after {\n  margin-left: 0 !important;\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn {\n  border-color: transparent;\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-secondary:not(:disabled):not(.disabled) {\n  background: var(--me-gray-5);\n  color: var(--me-gray-2);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-primary:not(:disabled):not(.disabled) {\n  background: var(--me-primary-1);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-success:not(:disabled):not(.disabled) {\n  background: var(--me-success-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-danger:not(:disabled):not(.disabled) {\n  background: var(--me-danger-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-warning:not(:disabled):not(.disabled) {\n  background: var(--me-warning-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-light-secondary:not(:disabled):not(.disabled) {\n  background: var(--me-gray-5);\n  color: var(--me-gray-2);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-light-primary:not(:disabled):not(.disabled) {\n  background: var(--me-primary-1);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-light-success:not(:disabled):not(.disabled) {\n  background: var(--me-success-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-light-danger:not(:disabled):not(.disabled) {\n  background: var(--me-danger-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-light-warning:not(:disabled):not(.disabled) {\n  background: var(--me-warning-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-outline-secondary:not(:disabled):not(.disabled) {\n  background: var(--me-gray-5);\n  color: var(--me-gray-2);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-outline-primary:not(:disabled):not(.disabled) {\n  background: var(--me-primary-1);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-outline-success:not(:disabled):not(.disabled) {\n  background: var(--me-success-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-outline-danger:not(:disabled):not(.disabled) {\n  background: var(--me-danger-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle.btn-outline-warning:not(:disabled):not(.disabled) {\n  background: var(--me-warning-0);\n  color: var(--me-white);\n}\n.me-dropdown ::v-deep .dropdown.show .dropdown-toggle-split::after {\n  content: \"\";\n}\n.me-dropdown > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 1100;\n}\n.me-dropdown.opened > div.backdrop {\n  display: block;\n  opacity: 0.4;\n}\n.me-dropdown.opened .dropdown {\n  z-index: 1102;\n}\n.me-dropdown.opened .dropdown ::v-deep .dropdown-toggle::after {\n  content: \"\";\n}\n.me-dropdown.opened .dropup ::v-deep .dropdown-toggle::after {\n  content: \"\";\n}\n.me-dropdown.opened .dropright ::v-deep .dropdown-toggle::after {\n  content: \"\";\n}\n.me-dropdown.opened .dropleft ::v-deep .dropdown-toggle::before {\n  content: \"\";\n}\n\n.dropdown-tooltip {\n  z-index: 1300;\n}\n\n/*# sourceMappingURL=DropDown.vue.map */","<template>\n  <div\n    :class=\"['me-dropdown', {\n      'pill': pill,\n      'pill-items': pillItems,\n      'opened': opened,\n      'block': block,\n      'btn-min-width': text,\n      'expanded' : expanded\n    }]\"\n  >\n    <BDropdown\n      ref=\"b-dropdown\"\n      :block=\"block\"\n      :boundary=\"boundary\"\n      :disabled=\"disabled\"\n      :dropleft=\"dropleft\"\n      :dropright=\"dropright\"\n      :dropup=\"dropup\"\n      :no-caret=\"noCaret\"\n      :right=\"menuRight\"\n      :size=\"size\"\n      :split-href=\"splitHref\"\n      :split-variant=\"splitVariant\"\n      :split=\"split\"\n      :text=\"text\"\n      :variant=\"variant\"\n      :no-flip=\"noFlip\"\n      @click=\"$emit('click', $event)\"\n      @hidden=\"opened = false\"\n      @hide=\"hide\"\n      @show=\"$emit('show', $event)\"\n      @shown=\"opened = true\"\n      data-cy=\"b-dropdown\"\n    >\n      <template v-for=\"(_, slot) of $scopedSlots\" v-slot:[slot]=\"scope\">\n        <slot :name=\"slot\" v-bind=\"scope\" />\n      </template>\n\n      <template v-for=\"(item, index) in items\">\n        <MeDropdownGroup\n          v-if=\"item.group && item.group.items\"\n          :key=\"item.group.label || index\"\n          :header=\"item.group.label\"\n        >\n          <MeDropdownItem\n            ref=\"dropdown-item\"\n            v-for=\"(groupItem) in item.group.items\"\n            :active=\"groupItem.active\"\n            :disabled=\"groupItem.disabled\"\n            :key=\"groupItem.id || groupItem.label || index\"\n            :href=\"groupItem.href\"\n            :target=\"groupItem.target\"\n            :label=\"groupItem.label\"\n            :itemClazz=\"groupItem.class\"\n            :description=\"groupItem.description\"\n            :descriptionHTML=\"groupItem.descriptionHTML\"\n            :placement=\"groupItem.placement\"\n            :popover=\"groupItem.popover\"\n            :id=\"groupItem.id || (groupItem.popover && groupItem.popover.id) || null\"\n            :icon=\"groupItem.icon\"\n            :pill=\"pillItems\"\n            :subItems=\"groupItem.subItems\"\n            @click=\"(itemOBj) => handleClick(groupItem, itemOBj)\"\n          />\n        </MeDropdownGroup>\n\n        <MeDropdownItem\n          ref=\"dropdown-item\"\n          v-else\n          :active=\"item.active\"\n          :disabled=\"item.disabled\"\n          :key=\"item.id || item.label || index\"\n          :href=\"item.href\"\n          :target=\"item.target\"\n          :label=\"item.label\"\n          :itemClazz=\"item.class\"\n          :description=\"item.description\"\n          :descriptionHTML=\"item.descriptionHTML\"\n          :placement=\"item.placement\"\n          :popover=\"item.popover\"\n          :id=\"item.id || (item.popover && item.popover.id) || null\"\n          :icon=\"item.icon\"\n          :pill=\"pillItems\"\n          :subItems=\"item.subItems\"\n          @click=\"(itemOBj) => handleClick(item, itemOBj)\"\n        />\n\n        <BDropdownDivider v-if=\"item.divider\" :key=\"`divider${item.label || (item.group && item.group.label)}`\" />\n      </template>\n    </BDropdown>\n\n    <div v-if=\"backdrop\" class=\"backdrop\" />\n  </div>\n</template>\n\n<script>\nimport { BDropdown, BDropdownDivider } from 'bootstrap-vue'\nimport MeDropdownItem from './DropdownItem.vue'\nimport MeDropdownGroup from './DropdownGroup.vue'\n\nexport default {\n  name: 'MeDropDown',\n  components: {\n    BDropdown,\n    BDropdownDivider,\n    MeDropdownItem,\n    MeDropdownGroup\n  },\n  props: {\n    backdrop: {\n      type: Boolean,\n      default: false\n    },\n    block: {\n      type: Boolean,\n      default: false\n    },\n    boundary: {\n      type: String,\n      default: 'scrollParent'\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    dropleft: {\n      type: Boolean,\n      default: false\n    },\n    dropright: {\n      type: Boolean,\n      default: false\n    },\n    dropup: {\n      type: Boolean,\n      default: false\n    },\n    items: {\n      type: Array,\n      default() {\n        return []\n      }\n    },\n    menuRight: {\n      type: Boolean,\n      default: false\n    },\n    noCaret: {\n      type: Boolean,\n      default: false\n    },\n    pill: {\n      type: Boolean,\n      default: false\n    },\n    pillItems: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    split: {\n      type: Boolean,\n      default: false\n    },\n    splitHref: {\n      type: String,\n      default: ''\n    },\n    splitVariant: {\n      type: String,\n      default: ''\n    },\n    text: {\n      type: String,\n      default: ''\n    },\n    variant: {\n      type: String,\n      default: 'secondary'\n    },\n    noFlip: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      opened: false\n    }\n  },\n  computed: {\n    expanded() {\n      return this.items.some((item) => {\n        if (item.group) {\n          const groupSubItems = item.group.items.some((groupItem) => {\n            Object.hasOwn(groupItem, 'subItems')\n          })\n\n          return groupSubItems\n        }\n        return Object.hasOwn(item, 'subItems')\n      })\n    }\n  },\n\n  methods: {\n    handleClick(item, itemObj) {\n      this.hideSubItems(itemObj.label)\n\n      if (item.click && !item.subItems) item.click(item)\n      if (!item.subItems) this.hide()\n    },\n\n    show() {\n      this.$refs['b-dropdown'].show()\n    },\n\n    hide(event) {\n      this.hideSubItems()\n      this.$refs['b-dropdown'].hide()\n      this.$emit('hide', event)\n    },\n\n    hideSubItems(label) {\n      if (this.$refs['dropdown-item']) {\n        this.$refs['dropdown-item'].filter(item => item.label !== label)\n          .forEach(item => item.hide())\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-dropdown {\n    display: inline-block;\n\n    .dropdown-divider {\n      margin: 0;\n    }\n\n    &.expanded {\n      ::v-deep .dropdown-menu {\n        overflow-y: unset !important;\n      }\n    }\n\n    &.pill {\n      ::v-deep .dropdown-toggle {\n        border-radius: 50rem !important;\n      }\n    }\n\n    &.block {\n      width: 100%;\n    }\n\n    &.pill-items {\n      ::v-deep .dropdown-menu {\n        background-color: transparent !important;\n        border: none!important;\n        box-shadow: none!important;\n      }\n    }\n\n    &.btn-min-width {\n      ::v-deep .dropdown {\n        .btn-sm {\n          &.dropdown-toggle:first-child {\n            min-width: 104px;\n          }\n\n          &:not(.dropdown-toggle) {\n            min-width: 80px;\n          }\n        }\n\n        .btn-md {\n          &.dropdown-toggle:first-child {\n            min-width: 120px;\n          }\n\n          &:not(.dropdown-toggle) {\n            min-width: 90px;\n          }\n        }\n\n        .btn-lg {\n          &.dropdown-toggle:first-child {\n            min-width: 140px;\n          }\n\n          &:not(.dropdown-toggle) {\n            min-width: 104px;\n          }\n        }\n      }\n    }\n\n    ::v-deep .dropdown {\n      @import \"../../scss/button.scss\";\n\n      .btn {\n        display: flex;\n        align-items: center;\n        justify-content: center;\n        border-radius: 8px;\n\n        &-sm {\n          height: 32px;\n          font-size: 12px;\n          line-height: 22px;\n        }\n\n        &-md {\n          height: 40px;\n          font-size: 14px;\n          line-height: 26px;\n        }\n\n        &-lg {\n          height: 48px;\n          font-size: 16px;\n          line-height: 30px;\n        }\n      }\n\n      .dropdown-menu {\n        background: var(--me-white);\n        border-radius: 8px;\n        border: none;\n        box-shadow: var(--me-shadow-level-1);\n        max-height: 70vh;\n        overflow-y: auto;\n\n        &::-webkit-scrollbar {\n          width: 6px;\n          height: 6px;\n\n          &-track {\n            display: none;\n          }\n\n          &-thumb {\n            background: rgba(0,0,0,.25);\n            border-radius: 15px;\n\n            &:hover {\n              background: rgba(0,0,0,.45)\n            }\n          }\n        }\n\n        &:visited,\n        &:focus {\n          border: none;\n          outline: 0 !important;\n        }\n\n        .dropdown-header {\n          font-size: 12px;\n          line-height: 16px;\n          color: var(--me-gray-4);\n          height: 32px;\n          padding: 8px 16px;\n        }\n      }\n\n      &.dropup {\n        .dropdown-toggle {\n          &:after {\n            content: \"\\f2ab\";\n          }\n\n          &:not(:empty):after {\n            margin-left: 8px;\n          }\n        }\n      }\n\n      &.dropright {\n        .dropdown-toggle {\n          &:after {\n            content: \"\\f2a6\";\n          }\n\n          &:not(:empty):after {\n            margin-left: 8px;\n          }\n        }\n      }\n\n      &.dropleft {\n        .dropdown-toggle {\n          &:before {\n            border: none !important;\n            content: \"\\f2a5\";\n            font-family: 'ME Icons';\n            font-size: 12px;\n            vertical-align: middle;\n          }\n\n          &:not(:empty):before {\n            margin-right: 8px;\n          }\n        }\n      }\n\n      .dropdown-toggle {\n        &:not(:empty):after {\n          margin-left: 8px;\n        }\n\n        &::after {\n          border: none !important;\n          content: \"\\f2a4\";\n          font-family: 'ME Icons Light';\n          font-size: 12px;\n          vertical-align: middle;\n        }\n\n        &-no-caret {\n          &::after, &::before {\n            display: none;\n          }\n        }\n      }\n\n      &.btn-group {\n        .btn:not(:last-child):not(.dropdown-toggle) {\n          border-radius: 8px 0 0 8px;\n          vertical-align: middle;\n        }\n\n        .btn:not(:first-child) {\n          border-radius: 0 8px 8px 0;\n        }\n\n        .dropdown-toggle {\n          &-split {\n            vertical-align: middle;\n            &.btn {\n              &-primary,\n              &-secondary,\n              &-success,\n              &-danger,\n              &-warning {\n                border-left: 1px solid rgba(255,255,255, 0.3);\n              }\n            }\n\n            &:after {\n              margin-left: 0 !important;\n            }\n          }\n        }\n      }\n\n      &.show {\n        .dropdown-toggle {\n          &.btn {\n            border-color: transparent;\n\n            @mixin pressed() {\n              &-secondary:not(:disabled):not(.disabled) {\n                background: var(--me-gray-5);\n                color: var(--me-gray-2);\n              }\n\n              &-primary:not(:disabled):not(.disabled) {\n                background: var(--me-primary-1);\n                color: var(--me-white);\n              }\n\n              &-success:not(:disabled):not(.disabled) {\n                background: var(--me-success-0);\n                color: var(--me-white);\n              }\n\n              &-danger:not(:disabled):not(.disabled) {\n                background: var(--me-danger-0);\n                color: var(--me-white);\n              }\n\n              &-warning:not(:disabled):not(.disabled) {\n                background: var(--me-warning-0);\n                color: var(--me-white);\n              }\n            }\n\n            @include pressed();\n\n            &-light {\n              @include pressed();\n            }\n\n            &-outline {\n              @include pressed();\n            }\n          }\n\n          &-split {\n            &::after {\n              content: \"\\f2ab\";\n            }\n          }\n        }\n      }\n    }\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      z-index: 1100;\n    }\n\n    &.opened {\n      > div.backdrop {\n        display: block;\n        opacity: 0.4;\n      }\n\n      .dropdown {\n         z-index: 1102;\n        ::v-deep .dropdown-toggle::after {\n          content: \"\\f2ab\";\n        }\n      }\n\n      .dropup  {\n        ::v-deep .dropdown-toggle::after {\n          content: \"\\f2a4\";\n        }\n      }\n\n      .dropright {\n        ::v-deep .dropdown-toggle::after {\n          content: \"\\f2a5\";\n        }\n      }\n\n      .dropleft {\n        ::v-deep .dropdown-toggle::before {\n          content: \"\\f2a6\";\n        }\n      }\n    }\n  }\n\n  .dropdown-tooltip {\n    z-index: 1300;\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$8 = "data-v-0e45c672";
  /* module identifier */
  const __vue_module_identifier__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$8 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$8 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$8, staticRenderFns: __vue_staticRenderFns__$8 },
    __vue_inject_styles__$8,
    __vue_script__$8,
    __vue_scope_id__$8,
    __vue_is_functional_template__$8,
    __vue_module_identifier__$8,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$9 = {
  name: 'MeBreadcrumbItem',

  components: {
    MeIcon: __vue_component__
  },

  props: {
    href: {
      type: String,
      default: ''
    },
    to: {
      type: String,
      default: ''
    },
    targetBlank: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "li",
    { staticClass: "me-breadcrumb-item" },
    [
      _vm._t("default", [
        _vm.to
          ? _c(
              "NuxtLink",
              {
                staticClass: "item-text",
                attrs: { to: _vm.to, "data-cy": "item-nuxt" }
              },
              [
                _vm._t("item-text", [
                  _vm._v("\n        " + _vm._s(_vm.text) + "\n      ")
                ])
              ],
              2
            )
          : _vm.href
          ? _c(
              "a",
              {
                staticClass: "item-text",
                attrs: {
                  href: _vm.href,
                  target: _vm.targetBlank ? "_blank" : "_self",
                  "data-cy": "item-anchor"
                }
              },
              [
                _vm._t("item-text", [
                  _vm._v("\n        " + _vm._s(_vm.text) + "\n      ")
                ])
              ],
              2
            )
          : _c(
              "span",
              { staticClass: "item-text" },
              [
                _vm._t("item-text", [
                  _vm._v("\n        " + _vm._s(_vm.text) + "\n      ")
                ])
              ],
              2
            )
      ]),
      _vm._v(" "),
      _c("MeIcon", {
        attrs: {
          size: 13,
          icon: "me-icon-l icon-chevron-right",
          color: "var(--me-gray-3)"
        }
      })
    ],
    2
  )
};
var __vue_staticRenderFns__$9 = [];
__vue_render__$9._withStripped = true;

  /* style */
  const __vue_inject_styles__$9 = function (inject) {
    if (!inject) return
    inject("data-v-66ea0c6a_0", { source: ".me-breadcrumb-item[data-v-66ea0c6a] {\n  display: flex;\n  flex-wrap: nowrap;\n  align-items: center;\n}\n.me-breadcrumb-item .item-text[data-v-66ea0c6a] {\n  color: var(--me-gray-3);\n}\n.me-breadcrumb-item .icon-chevron-right[data-v-66ea0c6a] {\n  margin: 0 12px;\n}\n\n/*# sourceMappingURL=BreadcrumbItem.vue.map */", map: {"version":3,"sources":["/app/build/src/components/breadcrumb/BreadcrumbItem.vue","BreadcrumbItem.vue"],"names":[],"mappings":"AAyEA;EACA,aAAA;EACA,iBAAA;EACA,mBAAA;ACxEA;ADyEA;EACA,uBAAA;ACvEA;ADyEA;EACA,cAAA;ACvEA;;AAEA,6CAA6C","file":"BreadcrumbItem.vue","sourcesContent":["<template>\n  <li class=\"me-breadcrumb-item\">\n    <slot>\n      <NuxtLink\n        v-if=\"to\"\n        :to=\"to\"\n        class=\"item-text\"\n        data-cy=\"item-nuxt\"\n      >\n        <slot name=\"item-text\">\n          {{ text }}\n        </slot>\n      </NuxtLink>\n\n      <a\n        v-else-if=\"href\"\n        :href=\"href\"\n        :target=\"targetBlank ? '_blank': '_self'\"\n        class=\"item-text\"\n        data-cy=\"item-anchor\"\n      >\n        <slot name=\"item-text\">\n          {{ text }}\n        </slot>\n      </a>\n\n      <span v-else class=\"item-text\">\n        <slot name=\"item-text\">\n          {{ text }}\n        </slot>\n      </span>\n    </slot>\n\n    <MeIcon\n      :size=\"13\"\n      icon=\"me-icon-l icon-chevron-right\"\n      color=\"var(--me-gray-3)\"\n    />\n  </li>\n</template>\n\n<script>\nimport MeIcon from '../Icon.vue'\n\nexport default {\n  name: 'MeBreadcrumbItem',\n\n  components: {\n    MeIcon\n  },\n\n  props: {\n    href: {\n      type: String,\n      default: ''\n    },\n    to: {\n      type: String,\n      default: ''\n    },\n    targetBlank: {\n      type: Boolean,\n      default: false\n    },\n    text: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-breadcrumb-item {\n  display: flex;\n  flex-wrap: nowrap;\n  align-items: center;\n  .item-text {\n    color: var(--me-gray-3);\n  }\n  .icon-chevron-right {\n    margin: 0 12px;\n  }\n}\n</style>\n",".me-breadcrumb-item {\n  display: flex;\n  flex-wrap: nowrap;\n  align-items: center;\n}\n.me-breadcrumb-item .item-text {\n  color: var(--me-gray-3);\n}\n.me-breadcrumb-item .icon-chevron-right {\n  margin: 0 12px;\n}\n\n/*# sourceMappingURL=BreadcrumbItem.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$9 = "data-v-66ea0c6a";
  /* module identifier */
  const __vue_module_identifier__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$9 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$9 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$9, staticRenderFns: __vue_staticRenderFns__$9 },
    __vue_inject_styles__$9,
    __vue_script__$9,
    __vue_scope_id__$9,
    __vue_is_functional_template__$9,
    __vue_module_identifier__$9,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$a = {
  name: 'MeBreadcrumb',

  components: {
    MeDropdown: __vue_component__$8,
    MeIcon: __vue_component__,
    MeBreadcrumbItem: __vue_component__$9
  },

  props: {
    items: {
      type: Array,
      default: function () { return [] }
    }
  },

  data() {
    return {
      truncate: false,
      originalWidth: 0
    }
  },

  computed: {
    dropdownItems() {
      const items = this.truncateItems.truncate.map((item) => {
        return {
          label: item.text,
          href: item.href || item.to,
          target: item.targetBlank ? '_blank' : '_self'
        }
      });

      return items
    },

    truncateItems() {
      if (this.items.length < 3) return

      if (this.items.length === 3) {
        return { truncate: [this.items[1]], preserve: [this.items[2]] }
      }

      if (this.items.length === 4) {
        return {
          truncate: this.items.slice(1, this.items.length - 1),
          preserve: [this.items[3]]
        }
      }

      return {
        truncate: this.items.slice(1, this.items.length - 2),
        preserve: this.items.slice(this.items.length - 2)
      }
    }
  },

  mounted() {
    const liItems = this.$refs.breadcrumb?.querySelectorAll('[data-cy="itemList"]');
    this.originalWidth = Array.from(liItems).reduce((sum, li) => sum + li.offsetWidth, 0);

    this.setResponsiveness();
    window.addEventListener(
      'resize',
      debounce_1(this.setResponsiveness, 500)
    );
  },

  beforeDestroy() {
    window.removeEventListener('resize', debounce_1);
  },

  methods: {
    setResponsiveness() {
      this.$nextTick(() => {
        this.truncate = this.originalWidth > this.$refs.breadcrumb?.offsetWidth &&
        this.items.length >= 3;
      });
    }
  }
};

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "ul",
    {
      ref: "breadcrumb",
      staticClass: "me-breadcrumb",
      attrs: { "data-cy": "breadcrumb" }
    },
    [
      _vm._t("default", [
        !_vm.truncate
          ? _vm._l(_vm.items, function(item) {
              return _c("MeBreadcrumbItem", {
                key: item.text,
                attrs: {
                  href: item.href,
                  to: item.to,
                  targetBlank: item.targetBlank,
                  text: item.text,
                  "data-cy": "itemList"
                }
              })
            })
          : [
              _c("MeBreadcrumbItem", {
                attrs: {
                  href: _vm.items[0].href,
                  to: _vm.items[0].to,
                  targetBlank: _vm.items[0].targetBlank,
                  text: _vm.items[0].text,
                  "data-cy": "itemList"
                }
              }),
              _vm._v(" "),
              _c(
                "MeBreadcrumbItem",
                { attrs: { "data-cy": "itemList" } },
                [
                  _c("MeDropdown", {
                    attrs: {
                      items: _vm.dropdownItems,
                      variant: "light-secondary",
                      size: "sm",
                      "no-caret": "",
                      pill: ""
                    },
                    scopedSlots: _vm._u([
                      {
                        key: "button-content",
                        fn: function() {
                          return [
                            _c("MeIcon", {
                              attrs: { icon: "me-icon-s icon-ellipsis-h" }
                            })
                          ]
                        },
                        proxy: true
                      }
                    ])
                  })
                ],
                1
              ),
              _vm._v(" "),
              _vm._l(_vm.truncateItems.preserve, function(item) {
                return _c("MeBreadcrumbItem", {
                  key: item.text,
                  attrs: {
                    href: item.href,
                    to: item.to,
                    targetBlank: item.targetBlank,
                    text: item.text,
                    "data-cy": "itemList"
                  }
                })
              })
            ]
      ])
    ],
    2
  )
};
var __vue_staticRenderFns__$a = [];
__vue_render__$a._withStripped = true;

  /* style */
  const __vue_inject_styles__$a = function (inject) {
    if (!inject) return
    inject("data-v-e376cab8_0", { source: ".me-breadcrumb[data-v-e376cab8] {\n  list-style-type: none;\n  margin: 0;\n  display: flex;\n  color: var(--me-gray-2);\n  padding: 0;\n  font-size: 14px;\n  line-height: 22px;\n  align-items: center;\n  height: 48px;\n}\n.me-breadcrumb[data-v-e376cab8]  .me-breadcrumb-item:last-child .item-text {\n  color: var(--me-gray-2);\n  font-weight: 600;\n}\n.me-breadcrumb[data-v-e376cab8]  .me-breadcrumb-item:last-child .icon-chevron-right {\n  display: none;\n}\n\n/*# sourceMappingURL=Breadcrumb.vue.map */", map: {"version":3,"sources":["/app/build/src/components/breadcrumb/Breadcrumb.vue","Breadcrumb.vue"],"names":[],"mappings":"AAkJA;EACA,qBAAA;EACA,SAAA;EACA,aAAA;EACA,uBAAA;EACA,UAAA;EACA,eAAA;EACA,iBAAA;EACA,mBAAA;EACA,YAAA;ACjJA;ADoJA;EACA,uBAAA;EACA,gBAAA;AClJA;ADoJA;EACA,aAAA;AClJA;;AAEA,yCAAyC","file":"Breadcrumb.vue","sourcesContent":["<template>\n  <ul\n    ref=\"breadcrumb\"\n    class=\"me-breadcrumb\"\n    data-cy=\"breadcrumb\"\n  >\n    <slot>\n      <template v-if=\"!truncate\">\n        <MeBreadcrumbItem\n          v-for=\"item in items\"\n          :key=\"item.text\"\n          :href=\"item.href\"\n          :to=\"item.to\"\n          :targetBlank=\"item.targetBlank\"\n          :text=\"item.text\"\n          data-cy=\"itemList\"\n        />\n      </template>\n\n      <template v-else>\n        <MeBreadcrumbItem\n          :href=\"items[0].href\"\n          :to=\"items[0].to\"\n          :targetBlank=\"items[0].targetBlank\"\n          :text=\"items[0].text\"\n          data-cy=\"itemList\"\n        />\n\n        <MeBreadcrumbItem data-cy=\"itemList\">\n          <MeDropdown\n            :items=\"dropdownItems\"\n            variant=\"light-secondary\"\n            size=\"sm\"\n            no-caret\n            pill\n          >\n            <template v-slot:button-content>\n              <MeIcon icon=\"me-icon-s icon-ellipsis-h\" />\n            </template>\n          </MeDropdown>\n        </MeBreadcrumbItem>\n\n        <MeBreadcrumbItem\n          v-for=\"item in truncateItems.preserve\"\n          :key=\"item.text\"\n          :href=\"item.href\"\n          :to=\"item.to\"\n          :targetBlank=\"item.targetBlank\"\n          :text=\"item.text\"\n          data-cy=\"itemList\"\n        />\n      </template>\n    </slot>\n  </ul>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport MeDropdown from '../dropdown/DropDown.vue'\nimport MeIcon from '../Icon.vue'\nimport MeBreadcrumbItem from './BreadcrumbItem.vue'\n\nexport default {\n  name: 'MeBreadcrumb',\n\n  components: {\n    MeDropdown,\n    MeIcon,\n    MeBreadcrumbItem\n  },\n\n  props: {\n    items: {\n      type: Array,\n      default: function () { return [] }\n    }\n  },\n\n  data() {\n    return {\n      truncate: false,\n      originalWidth: 0\n    }\n  },\n\n  computed: {\n    dropdownItems() {\n      const items = this.truncateItems.truncate.map((item) => {\n        return {\n          label: item.text,\n          href: item.href || item.to,\n          target: item.targetBlank ? '_blank' : '_self'\n        }\n      })\n\n      return items\n    },\n\n    truncateItems() {\n      if (this.items.length < 3) return\n\n      if (this.items.length === 3) {\n        return { truncate: [this.items[1]], preserve: [this.items[2]] }\n      }\n\n      if (this.items.length === 4) {\n        return {\n          truncate: this.items.slice(1, this.items.length - 1),\n          preserve: [this.items[3]]\n        }\n      }\n\n      return {\n        truncate: this.items.slice(1, this.items.length - 2),\n        preserve: this.items.slice(this.items.length - 2)\n      }\n    }\n  },\n\n  mounted() {\n    const liItems = this.$refs.breadcrumb?.querySelectorAll('[data-cy=\"itemList\"]')\n    this.originalWidth = Array.from(liItems).reduce((sum, li) => sum + li.offsetWidth, 0)\n\n    this.setResponsiveness()\n    window.addEventListener(\n      'resize',\n      debounce(this.setResponsiveness, 500)\n    )\n  },\n\n  beforeDestroy() {\n    window.removeEventListener('resize', debounce)\n  },\n\n  methods: {\n    setResponsiveness() {\n      this.$nextTick(() => {\n        this.truncate = this.originalWidth > this.$refs.breadcrumb?.offsetWidth &&\n        this.items.length >= 3\n      })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-breadcrumb {\n    list-style-type: none;\n    margin: 0;\n    display: flex;\n    color: var(--me-gray-2);\n    padding: 0;\n    font-size: 14px;\n    line-height: 22px;\n    align-items: center;\n    height: 48px;\n\n    ::v-deep .me-breadcrumb-item:last-child {\n      .item-text {\n        color: var(--me-gray-2);\n        font-weight: 600;\n      }\n      .icon-chevron-right {\n        display: none;\n      }\n    }\n  }\n</style>\n",".me-breadcrumb {\n  list-style-type: none;\n  margin: 0;\n  display: flex;\n  color: var(--me-gray-2);\n  padding: 0;\n  font-size: 14px;\n  line-height: 22px;\n  align-items: center;\n  height: 48px;\n}\n.me-breadcrumb ::v-deep .me-breadcrumb-item:last-child .item-text {\n  color: var(--me-gray-2);\n  font-weight: 600;\n}\n.me-breadcrumb ::v-deep .me-breadcrumb-item:last-child .icon-chevron-right {\n  display: none;\n}\n\n/*# sourceMappingURL=Breadcrumb.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$a = "data-v-e376cab8";
  /* module identifier */
  const __vue_module_identifier__$a = undefined;
  /* functional template */
  const __vue_is_functional_template__$a = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$a, staticRenderFns: __vue_staticRenderFns__$a },
    __vue_inject_styles__$a,
    __vue_script__$a,
    __vue_scope_id__$a,
    __vue_is_functional_template__$a,
    __vue_module_identifier__$a,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$b = {
  name: 'MeModalBreadcrumb',
  components: {
    MeBreadcrumb: __vue_component__$a
  },
  props: {
    close: {
      type: Function,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    actions: {
      type: Array,
      default: null
    },
    expandUrl: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      currentActions: []
    }
  },

  watch: {
    expandUrl() {
      this.currentActions = this.getActions();
    },
    actions() {
      this.currentActions = this.getActions();
    }
  },

  beforeMount() {
    this.currentActions = this.getActions();
  },

  methods: {
    getActions() {
      if (this.expandUrl) {
        this.currentActions = [
          {
            name: 'me-icon-l icon-arrow-up-right-from-square',
            href: this.expandUrl
          }
        ];
      }

      if (this.actions) {
        return [...this.currentActions, ...this.actions]
      }

      return [
        ...this.currentActions,
        {
          name: 'me-icon-l icon-xmark',
          click: this.close
        }
      ]
    }
  }
};

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "modal-breadcrumb" },
    [
      _c("me-breadcrumb", {
        staticClass: "me-breadcrumb",
        attrs: { items: _vm.items, "data-cy": "breadcrumb" }
      }),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "modal-breadcrumb-actions" },
        [
          _vm._l(_vm.currentActions, function(action) {
            return [
              _c("me-button", {
                key: action.name,
                attrs: {
                  icon: action.name,
                  href: action.href,
                  target: "_blank",
                  pill: "",
                  variant: "light-secondary",
                  "data-cy": "action"
                },
                on: {
                  click: function($event) {
                    action.click ? action.click(_vm.close) : null;
                  }
                }
              })
            ]
          })
        ],
        2
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$b = [];
__vue_render__$b._withStripped = true;

  /* style */
  const __vue_inject_styles__$b = function (inject) {
    if (!inject) return
    inject("data-v-3b5ae9cb_0", { source: ".modal-breadcrumb[data-v-3b5ae9cb] {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  height: 48px;\n  border-radius: 4px 4px 0px 0px;\n  background: var(--me-gray-6);\n  padding: 4px, 0px, 4px, 0px;\n}\n.modal-breadcrumb .me-breadcrumb[data-v-3b5ae9cb] {\n  align-items: center;\n  padding: 0px 32px;\n}\n.modal-breadcrumb-actions[data-v-3b5ae9cb] {\n  display: flex;\n  gap: 8px;\n}\n\n/*# sourceMappingURL=ModalBreadcrumb.vue.map */", map: {"version":3,"sources":["/app/build/src/components/modal/ModalBreadcrumb.vue","ModalBreadcrumb.vue"],"names":[],"mappings":"AAkGA;EACA,aAAA;EACA,8BAAA;EACA,mBAAA;EACA,YAAA;EACA,8BAAA;EACA,4BAAA;EACA,2BAAA;ACjGA;ADmGA;EACA,mBAAA;EACA,iBAAA;ACjGA;ADoGA;EACA,aAAA;EACA,QAAA;AClGA;;AAEA,8CAA8C","file":"ModalBreadcrumb.vue","sourcesContent":["<template>\n  <div class=\"modal-breadcrumb\">\n    <me-breadcrumb\n      :items=\"items\"\n      class=\"me-breadcrumb\"\n      data-cy=\"breadcrumb\"\n    />\n    <div class=\"modal-breadcrumb-actions\">\n      <template v-for=\"action in currentActions\">\n        <me-button\n          :key=\"action.name\"\n          :icon=\"action.name\"\n          @click=\"action.click ? action.click(close) : null\"\n          :href=\"action.href\"\n          target=\"_blank\"\n          pill\n          variant=\"light-secondary\"\n          data-cy=\"action\"\n        />\n      </template>\n    </div>\n  </div>\n</template>\n\n<script>\nimport MeBreadcrumb from './../../components/breadcrumb/Breadcrumb.vue'\n\nexport default {\n  name: 'MeModalBreadcrumb',\n  components: {\n    MeBreadcrumb\n  },\n  props: {\n    close: {\n      type: Function,\n      required: true\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    actions: {\n      type: Array,\n      default: null\n    },\n    expandUrl: {\n      type: String,\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      currentActions: []\n    }\n  },\n\n  watch: {\n    expandUrl() {\n      this.currentActions = this.getActions()\n    },\n    actions() {\n      this.currentActions = this.getActions()\n    }\n  },\n\n  beforeMount() {\n    this.currentActions = this.getActions()\n  },\n\n  methods: {\n    getActions() {\n      if (this.expandUrl) {\n        this.currentActions = [\n          {\n            name: 'me-icon-l icon-arrow-up-right-from-square',\n            href: this.expandUrl\n          }\n        ]\n      }\n\n      if (this.actions) {\n        return [...this.currentActions, ...this.actions]\n      }\n\n      return [\n        ...this.currentActions,\n        {\n          name: 'me-icon-l icon-xmark',\n          click: this.close\n        }\n      ]\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.modal-breadcrumb {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  height: 48px;\n  border-radius: 4px 4px 0px 0px;\n  background: var(--me-gray-6);\n  padding: 4px, 0px, 4px, 0px;\n\n  .me-breadcrumb {\n    align-items: center;\n    padding: 0px 32px;\n  }\n\n  &-actions {\n    display: flex;\n    gap: 8px;\n  }\n}\n </style>\n",".modal-breadcrumb {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  height: 48px;\n  border-radius: 4px 4px 0px 0px;\n  background: var(--me-gray-6);\n  padding: 4px, 0px, 4px, 0px;\n}\n.modal-breadcrumb .me-breadcrumb {\n  align-items: center;\n  padding: 0px 32px;\n}\n.modal-breadcrumb-actions {\n  display: flex;\n  gap: 8px;\n}\n\n/*# sourceMappingURL=ModalBreadcrumb.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$b = "data-v-3b5ae9cb";
  /* module identifier */
  const __vue_module_identifier__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$b = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$b, staticRenderFns: __vue_staticRenderFns__$b },
    __vue_inject_styles__$b,
    __vue_script__$b,
    __vue_scope_id__$b,
    __vue_is_functional_template__$b,
    __vue_module_identifier__$b,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$c = {
  name: 'MeModalHeaderContent',
  props: {
    headerIcon: {
      type: String,
      default: ''
    },
    hideHeaderClose: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    close: {
      type: Function,
      required: true
    }
  }
};

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-modal-header-content" }, [
    _vm.headerIcon
      ? _c("div", { staticClass: "d-flex justify-content-center w-100" }, [
          _c("img", {
            attrs: { src: _vm.headerIcon, alt: "modal-header-image" }
          })
        ])
      : _vm._e(),
    _vm._v(" "),
    _c("div", { staticClass: "w-100" }, [
      _c(
        "h5",
        { staticClass: "modal-title", class: { "pr-4": !_vm.hideHeaderClose } },
        [
          _vm._t("modal-title", [
            _vm._v("\n        " + _vm._s(_vm.title) + "\n      ")
          ])
        ],
        2
      ),
      _vm._v(" "),
      _vm.subtitle
        ? _c("h6", { staticClass: "modal-subtitle" }, [
            _vm._v("\n      " + _vm._s(_vm.subtitle) + "\n    ")
          ])
        : _vm._e(),
      _vm._v(" "),
      !_vm.hideHeaderClose
        ? _c(
            "button",
            {
              staticClass: "close modal-close-icon",
              attrs: { type: "button", "aria-label": "Close" },
              on: {
                click: function($event) {
                  return _vm.close()
                }
              }
            },
            [_vm._v("\n      ×\n    ")]
          )
        : _vm._e()
    ])
  ])
};
var __vue_staticRenderFns__$c = [];
__vue_render__$c._withStripped = true;

  /* style */
  const __vue_inject_styles__$c = function (inject) {
    if (!inject) return
    inject("data-v-a9016546_0", { source: ".me-modal-header[data-v-a9016546] {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  position: relative;\n}\n.me-modal-header .modal-title[data-v-a9016546] {\n  font-weight: 600;\n  font-size: 20px;\n}\n.me-modal-header .modal-subtitle[data-v-a9016546] {\n  color: var(--me-gray-3);\n  font-size: 14px;\n  font-weight: normal;\n  margin: 0;\n}\n.me-modal-header .modal-close-icon[data-v-a9016546] {\n  position: absolute;\n  top: 0px;\n  right: 5px;\n}\n.me-modal-header .modal-close-icon[data-v-a9016546]:focus {\n  outline: none;\n}\n\n/*# sourceMappingURL=ModalHeaderContent.vue.map */", map: {"version":3,"sources":["/app/build/src/components/modal/ModalHeaderContent.vue","ModalHeaderContent.vue"],"names":[],"mappings":"AAoEA;EACA,aAAA;EACA,sBAAA;EACA,WAAA;EACA,kBAAA;ACnEA;ADqEA;EACA,gBAAA;EACA,eAAA;ACnEA;ADsEA;EACA,uBAAA;EACA,eAAA;EACA,mBAAA;EACA,SAAA;ACpEA;ADuEA;EACA,kBAAA;EACA,QAAA;EACA,UAAA;ACrEA;ADuEA;EACA,aAAA;ACrEA;;AAEA,iDAAiD","file":"ModalHeaderContent.vue","sourcesContent":["<template>\n  <div class=\"me-modal-header-content\">\n    <div\n      v-if=\"headerIcon\"\n      class=\"d-flex justify-content-center w-100\"\n    >\n      <img :src=\"headerIcon\" alt=\"modal-header-image\">\n    </div>\n\n    <div class=\"w-100\">\n      <h5\n        :class=\"{'pr-4': !hideHeaderClose}\"\n        class=\"modal-title\"\n      >\n        <slot name=\"modal-title\">\n          {{ title }}\n        </slot>\n      </h5>\n\n      <h6\n        v-if=\"subtitle\"\n        class=\"modal-subtitle\"\n      >\n        {{ subtitle }}\n      </h6>\n\n      <button\n        v-if=\"!hideHeaderClose\"\n        @click=\"close()\"\n        type=\"button\"\n        aria-label=\"Close\"\n        class=\"close modal-close-icon\"\n      >\n        &times;\n      </button>\n    </div>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeModalHeaderContent',\n  props: {\n    headerIcon: {\n      type: String,\n      default: ''\n    },\n    hideHeaderClose: {\n      type: Boolean,\n      default: true\n    },\n    title: {\n      type: String,\n      default: ''\n    },\n    subtitle: {\n      type: String,\n      default: ''\n    },\n    close: {\n      type: Function,\n      required: true\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-modal-header {\n    display: flex;\n    flex-direction: column;\n    width: 100%;\n    position: relative;\n\n    .modal-title {\n      font-weight: 600;\n      font-size: 20px;\n    }\n\n    .modal-subtitle {\n      color: var(--me-gray-3);\n      font-size: 14px;\n      font-weight: normal;\n      margin: 0;\n    }\n\n    .modal-close-icon {\n      position: absolute;\n      top: 0px;\n      right: 5px;\n\n      &:focus {\n        outline: none;\n      }\n    }\n  }\n</style>\n",".me-modal-header {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  position: relative;\n}\n.me-modal-header .modal-title {\n  font-weight: 600;\n  font-size: 20px;\n}\n.me-modal-header .modal-subtitle {\n  color: var(--me-gray-3);\n  font-size: 14px;\n  font-weight: normal;\n  margin: 0;\n}\n.me-modal-header .modal-close-icon {\n  position: absolute;\n  top: 0px;\n  right: 5px;\n}\n.me-modal-header .modal-close-icon:focus {\n  outline: none;\n}\n\n/*# sourceMappingURL=ModalHeaderContent.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$c = "data-v-a9016546";
  /* module identifier */
  const __vue_module_identifier__$c = undefined;
  /* functional template */
  const __vue_is_functional_template__$c = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$c, staticRenderFns: __vue_staticRenderFns__$c },
    __vue_inject_styles__$c,
    __vue_script__$c,
    __vue_scope_id__$c,
    __vue_is_functional_template__$c,
    __vue_module_identifier__$c,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$d = {
  name: 'MeModalHeader',
  components: {
    ModalBreadcrumb: __vue_component__$b,
    ModalHeaderContent: __vue_component__$c
  },
  props: {
    headerIcon: {
      type: String,
      default: ''
    },
    hideHeaderClose: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    close: {
      type: Function,
      required: true
    },
    breadcrumb: {
      type: Object,
      default: () => null
    }
  },

  computed: {
    hasBreadCrumb() {
      return this.breadcrumb?.items?.length
    }
  }

};

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$d = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-modal-header" }, [
    _vm.breadcrumb && _vm.breadcrumb.items && _vm.breadcrumb.items.length
      ? _c(
          "div",
          { staticClass: "modal-header-breadcrumb" },
          [
            _c("ModalBreadcrumb", {
              attrs: {
                items: _vm.breadcrumb.items,
                actions: _vm.breadcrumb.actions,
                expandUrl: _vm.breadcrumb.expandUrl,
                close: _vm.close
              }
            })
          ],
          1
        )
      : _vm._e(),
    _vm._v(" "),
    _c(
      "div",
      {
        staticClass: "modal-header-content",
        class: { "modal-header-content__with-breadcrumb": _vm.hasBreadCrumb }
      },
      [
        _c(
          "ModalHeaderContent",
          {
            attrs: {
              headerIcon: _vm.headerIcon,
              hideHeaderClose: _vm.hideHeaderClose || !!_vm.hasBreadCrumb,
              title: _vm.title,
              subtitle: _vm.subtitle,
              close: _vm.close
            }
          },
          [_c("template", { slot: "modal-title" }, [_vm._t("modal-title")], 2)],
          2
        )
      ],
      1
    )
  ])
};
var __vue_staticRenderFns__$d = [];
__vue_render__$d._withStripped = true;

  /* style */
  const __vue_inject_styles__$d = function (inject) {
    if (!inject) return
    inject("data-v-201f6170_0", { source: ".me-modal-header[data-v-201f6170] {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  position: relative;\n}\n.me-modal-header .modal-header-content__with-breadcrumb[data-v-201f6170] {\n  padding-right: 24px;\n  padding-left: 24px;\n}\n.me-modal-header .modal-header-breadcrumb[data-v-201f6170] {\n  width: 100%;\n  min-width: 100%;\n}\n\n/*# sourceMappingURL=ModalHeader.vue.map */", map: {"version":3,"sources":["/app/build/src/components/modal/ModalHeader.vue","ModalHeader.vue"],"names":[],"mappings":"AAgFA;EACA,aAAA;EACA,sBAAA;EACA,WAAA;EACA,kBAAA;AC/EA;ADkFA;EACA,mBAAA;EACA,kBAAA;AChFA;ADoFA;EACA,WAAA;EACA,eAAA;AClFA;;AAEA,0CAA0C","file":"ModalHeader.vue","sourcesContent":["<template>\n  <div class=\"me-modal-header\">\n    <div\n      v-if=\"breadcrumb && breadcrumb.items && breadcrumb.items.length\"\n      class=\"modal-header-breadcrumb\"\n    >\n      <ModalBreadcrumb\n        :items=\"breadcrumb.items\"\n        :actions=\"breadcrumb.actions\"\n        :expandUrl=\"breadcrumb.expandUrl\"\n        :close=\"close\"\n      />\n    </div>\n\n    <div\n      :class=\"{'modal-header-content__with-breadcrumb': hasBreadCrumb}\"\n      class=\"modal-header-content\"\n    >\n      <ModalHeaderContent\n        :headerIcon=\"headerIcon\"\n        :hideHeaderClose=\"hideHeaderClose || !!hasBreadCrumb\"\n        :title=\"title\"\n        :subtitle=\"subtitle\"\n        :close=\"close\"\n      >\n        <template slot=\"modal-title\">\n          <slot name=\"modal-title\" />\n        </template>\n      </ModalHeaderContent>\n    </div>\n  </div>\n</template>\n\n<script>\nimport ModalBreadcrumb from './ModalBreadcrumb.vue'\nimport ModalHeaderContent from './ModalHeaderContent.vue'\n\nexport default {\n  name: 'MeModalHeader',\n  components: {\n    ModalBreadcrumb,\n    ModalHeaderContent\n  },\n  props: {\n    headerIcon: {\n      type: String,\n      default: ''\n    },\n    hideHeaderClose: {\n      type: Boolean,\n      default: true\n    },\n    title: {\n      type: String,\n      default: ''\n    },\n    subtitle: {\n      type: String,\n      default: ''\n    },\n    close: {\n      type: Function,\n      required: true\n    },\n    breadcrumb: {\n      type: Object,\n      default: () => null\n    }\n  },\n\n  computed: {\n    hasBreadCrumb() {\n      return this.breadcrumb?.items?.length\n    }\n  }\n\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-modal-header {\n    display: flex;\n    flex-direction: column;\n    width: 100%;\n    position: relative;\n\n    .modal-header-content {\n      &__with-breadcrumb {\n        padding-right: 24px;\n        padding-left: 24px;\n        }\n    }\n\n    .modal-header-breadcrumb {\n      width: 100%;\n      min-width: 100%;\n    }\n  }\n</style>\n",".me-modal-header {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  position: relative;\n}\n.me-modal-header .modal-header-content__with-breadcrumb {\n  padding-right: 24px;\n  padding-left: 24px;\n}\n.me-modal-header .modal-header-breadcrumb {\n  width: 100%;\n  min-width: 100%;\n}\n\n/*# sourceMappingURL=ModalHeader.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$d = "data-v-201f6170";
  /* module identifier */
  const __vue_module_identifier__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$d = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$d, staticRenderFns: __vue_staticRenderFns__$d },
    __vue_inject_styles__$d,
    __vue_script__$d,
    __vue_scope_id__$d,
    __vue_is_functional_template__$d,
    __vue_module_identifier__$d,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$e = {
  name: 'MeModalFooter',
  components: {
    Button: __vue_component__$6
  },

  props: {
    buttons: {
      type: Array,
      default: () => []
    },
    close: {
      type: Function,
      required: true
    },
    busy: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isWidthSmall: window.innerWidth <= 574 || this.size === 'sm'
    }
  },

  mounted() {
    window.addEventListener('resize', this.setIsWidthSmall);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setIsWidthSmall);
  },

  methods: {
    getButtonsVariant(order) {
      if (order === 0) return 'primary'
      if (order === 1) return 'light-primary'
      return 'outline-primary'
    },

    getButtonsClass(order) {
      if (order === 0) return ''
      if (order === 2) return 'mr-auto'
      return 'mr-2'
    },

    isButtonDisabled(button) {
      if (button.disabled) return true
      if (button.busy) return false
      return this.busy
    },

    setIsWidthSmall: debounce_1(function () {
      this.isWidthSmall = window.innerWidth <= 574 || this.size === 'sm';
    }, 500)
  }
};

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$e = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-modal-footer",
      class: { "me-modal-footer--extra-action": _vm.buttons.length > 2 },
      style: _vm.isWidthSmall ? "width: 100%" : ""
    },
    [
      _vm.buttons.length === 0
        ? _c(
            "Button",
            {
              attrs: { block: _vm.isWidthSmall, variant: "primary" },
              on: {
                click: function($event) {
                  return _vm.close()
                }
              }
            },
            [_vm._v("\n    " + _vm._s(_vm.$toolkit.t("modal.close")) + "\n  ")]
          )
        : _c(
            "div",
            {
              class: _vm.isWidthSmall ? "footer-sm" : "d-flex flex-row-reverse"
            },
            _vm._l(_vm.buttons, function(button, index) {
              return _c(
                "Button",
                {
                  key: button.key || button.label,
                  class: _vm.getButtonsClass(index),
                  attrs: {
                    disabled: _vm.isButtonDisabled(button),
                    busy: button.busy,
                    variant: button.variant || _vm.getButtonsVariant(index),
                    prependIcon:
                      button.icon && button.icon.prepend && button.icon.name,
                    appendIcon:
                      button.icon && button.icon.append && button.icon.name,
                    block: _vm.isWidthSmall
                  },
                  on: {
                    click: function() {
                      return button.click && button.click(_vm.close)
                    }
                  }
                },
                [_c("span", [_vm._v(" " + _vm._s(button.label))])]
              )
            }),
            1
          )
    ],
    1
  )
};
var __vue_staticRenderFns__$e = [];
__vue_render__$e._withStripped = true;

  /* style */
  const __vue_inject_styles__$e = function (inject) {
    if (!inject) return
    inject("data-v-746606b0_0", { source: ".me-modal-footer--extra-action[data-v-746606b0] {\n  justify-content: space-between;\n  width: 100%;\n}\n.me-modal-footer .footer-sm[data-v-746606b0] {\n  display: flex;\n  flex-direction: column;\n  gap: 16px;\n}\n.me-modal-footer .footer-sm .btn-block[data-v-746606b0] {\n  margin-top: 0;\n}\n.me-modal-footer .btn-footer[data-v-746606b0] {\n  margin-right: 36px;\n}\n.me-modal-footer .btn-footer[data-v-746606b0]:last-of-type {\n  margin-right: 0;\n}\n\n/*# sourceMappingURL=ModalFooter.vue.map */", map: {"version":3,"sources":["/app/build/src/components/modal/ModalFooter.vue","ModalFooter.vue"],"names":[],"mappings":"AA4GA;EACA,8BAAA;EACA,WAAA;AC3GA;AD8GA;EACA,aAAA;EACA,sBAAA;EACA,SAAA;AC5GA;AD8GA;EACA,aAAA;AC5GA;ADgHA;EACA,kBAAA;AC9GA;ADgHA;EACA,eAAA;AC9GA;;AAEA,0CAA0C","file":"ModalFooter.vue","sourcesContent":["<template>\n  <div\n    :class=\"{ 'me-modal-footer--extra-action': buttons.length > 2 }\"\n    :style=\"isWidthSmall ? 'width: 100%' : ''\"\n    class=\"me-modal-footer\"\n  >\n    <Button\n      v-if=\"buttons.length === 0\"\n      @click=\"close()\"\n      :block=\"isWidthSmall\"\n      variant=\"primary\"\n    >\n      {{ $toolkit.t('modal.close') }}\n    </Button>\n\n    <div\n      v-else\n      :class=\"isWidthSmall ? 'footer-sm' : 'd-flex flex-row-reverse'\"\n    >\n      <Button\n        v-for=\"(button, index) in buttons\"\n        :key=\"button.key || button.label\"\n        :disabled=\"isButtonDisabled(button)\"\n        :busy=\"button.busy\"\n        :variant=\"button.variant || getButtonsVariant(index)\"\n        @click=\"() => button.click && button.click(close)\"\n        :class=\"getButtonsClass(index)\"\n        :prependIcon=\"button.icon && button.icon.prepend && button.icon.name\"\n        :appendIcon=\"button.icon && button.icon.append && button.icon.name\"\n        :block=\"isWidthSmall\"\n      >\n        <span> {{ button.label }}</span>\n      </Button>\n    </div>\n  </div>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport Button from './../Button.vue'\n\nexport default {\n  name: 'MeModalFooter',\n  components: {\n    Button\n  },\n\n  props: {\n    buttons: {\n      type: Array,\n      default: () => []\n    },\n    close: {\n      type: Function,\n      required: true\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: ''\n    }\n  },\n\n  data() {\n    return {\n      isWidthSmall: window.innerWidth <= 574 || this.size === 'sm'\n    }\n  },\n\n  mounted() {\n    window.addEventListener('resize', this.setIsWidthSmall)\n  },\n\n  beforeDestroy() {\n    window.removeEventListener('resize', this.setIsWidthSmall)\n  },\n\n  methods: {\n    getButtonsVariant(order) {\n      if (order === 0) return 'primary'\n      if (order === 1) return 'light-primary'\n      return 'outline-primary'\n    },\n\n    getButtonsClass(order) {\n      if (order === 0) return ''\n      if (order === 2) return 'mr-auto'\n      return 'mr-2'\n    },\n\n    isButtonDisabled(button) {\n      if (button.disabled) return true\n      if (button.busy) return false\n      return this.busy\n    },\n\n    setIsWidthSmall: debounce(function () {\n      this.isWidthSmall = window.innerWidth <= 574 || this.size === 'sm'\n    }, 500)\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-modal-footer {\n    &--extra-action {\n      justify-content: space-between;\n      width: 100%;\n    }\n\n    .footer-sm {\n      display: flex;\n      flex-direction: column;\n      gap: 16px;\n\n      .btn-block {\n        margin-top: 0;\n      }\n    }\n\n    .btn-footer {\n      margin-right: 36px;\n\n      &:last-of-type {\n        margin-right: 0;\n      }\n    }\n  }\n</style>\n",".me-modal-footer--extra-action {\n  justify-content: space-between;\n  width: 100%;\n}\n.me-modal-footer .footer-sm {\n  display: flex;\n  flex-direction: column;\n  gap: 16px;\n}\n.me-modal-footer .footer-sm .btn-block {\n  margin-top: 0;\n}\n.me-modal-footer .btn-footer {\n  margin-right: 36px;\n}\n.me-modal-footer .btn-footer:last-of-type {\n  margin-right: 0;\n}\n\n/*# sourceMappingURL=ModalFooter.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$e = "data-v-746606b0";
  /* module identifier */
  const __vue_module_identifier__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$e = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$e, staticRenderFns: __vue_staticRenderFns__$e },
    __vue_inject_styles__$e,
    __vue_script__$e,
    __vue_scope_id__$e,
    __vue_is_functional_template__$e,
    __vue_module_identifier__$e,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$f = {
  name: 'MeModal',
  components: {
    BModal,
    ModalHeader: __vue_component__$d,
    ModalFooter: __vue_component__$e
  },

  model: {
    prop: 'visible'
  },

  props: {
    bodyClass: {
      type: String,
      default: ''
    },
    busy: {
      type: Boolean,
      default: false
    },
    buttons: {
      type: Array,
      default: () => []
    },
    centered: {
      type: Boolean,
      default: true
    },
    contentClass: {
      type: String,
      default: ''
    },
    footerClass: {
      type: String,
      default: ''
    },
    headerClass: {
      type: String,
      default: ''
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    hideHeaderClose: {
      type: Boolean,
      default: true
    },
    hideFooter: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: null
    },
    noHeaderBorder: {
      type: Boolean,
      default: true
    },
    noFooterBorder: {
      type: Boolean,
      default: true
    },
    noBackdrop: {
      type: Boolean,
      default: false
    },
    noKeyboard: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    },
    headerIcon: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: false
    },
    breadcrumb: {
      type: Object,
      default: () => null
    }
  },

  data() {
    return {
      model: this.visible,
      headerClasses: `${this.headerClass} ${this.noHeaderBorder ? 'no-border' : ''} ${this.breadcrumb?.items?.length ? 'no-padding' : ''}`,
      footerClasses: `${this.footerClass} ${this.noFooterBorder ? 'no-border' : ''}`,
      bodyClasses: `${this.bodyClass} ${this.noHeaderBorder ? 'no-header-border' : ''} ${this.noFooterBorder ? 'no-footer-border' : ''}`
    }
  },

  watch: {
    visible(val) {
      this.model = val;
    }
  },

  methods: {
    show() {
      this.model = true;
    },

    hide() {
      this.model = false;
    },

    onShow(event) {
      this.$emit('show', event);
      this.$emit('update:visible', true);
    },

    onShown() {
      this.$emit('shown');
    },

    onHide(event) {
      this.$emit('hide', event);
      this.$emit('update:visible', false);
    },

    onHidden() {
      this.$emit('hidden');
    }
  }
};

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$f = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "b-modal",
    _vm._b(
      {
        attrs: {
          "body-class": _vm.bodyClasses,
          "footer-class": _vm.footerClasses,
          "header-class": _vm.headerClasses,
          "no-close-on-backdrop": _vm.noBackdrop,
          "no-close-on-esc": _vm.noKeyboard
        },
        on: {
          show: _vm.onShow,
          shown: _vm.onShown,
          hide: _vm.onHide,
          hidden: _vm.onHidden
        },
        scopedSlots: _vm._u(
          [
            {
              key: "modal-header",
              fn: function(ref) {
                var close = ref.close;
                return [
                  _c(
                    "ModalHeader",
                    {
                      staticClass: "modal-header-content",
                      attrs: {
                        headerIcon: _vm.headerIcon,
                        hideHeaderClose: _vm.hideHeaderClose,
                        title: _vm.title,
                        subtitle: _vm.subtitle,
                        breadcrumb: _vm.breadcrumb,
                        close: close
                      }
                    },
                    [
                      _c(
                        "template",
                        { slot: "modal-title" },
                        [_vm._t("modal-title")],
                        2
                      )
                    ],
                    2
                  )
                ]
              }
            },
            {
              key: "modal-footer",
              fn: function(ref) {
                var close = ref.close;
                return [
                  _c("ModalFooter", {
                    attrs: {
                      buttons: _vm.buttons,
                      close: close,
                      busy: _vm.busy,
                      size: _vm.size
                    }
                  })
                ]
              }
            },
            _vm._l(_vm.$scopedSlots, function(_, slot) {
              return {
                key: slot,
                fn: function(scope) {
                  return [_vm._t(slot, null, null, scope)]
                }
              }
            })
          ],
          null,
          true
        ),
        model: {
          value: _vm.model,
          callback: function($$v) {
            _vm.model = $$v;
          },
          expression: "model"
        }
      },
      "b-modal",
      _vm.$props,
      false
    )
  )
};
var __vue_staticRenderFns__$f = [];
__vue_render__$f._withStripped = true;

  /* style */
  const __vue_inject_styles__$f = function (inject) {
    if (!inject) return
    inject("data-v-46e25ade_0", { source: "[data-v-46e25ade] .modal-dialog.modal-sm {\n  min-width: 328px;\n}\n@media (max-width: 575px) {\n[data-v-46e25ade] .modal-dialog.modal-sm {\n    max-width: 328px;\n    margin: 0.5rem auto;\n}\n}\n[data-v-46e25ade] .modal-dialog.modal-md {\n  min-width: 656px;\n}\n@media (max-width: 656px) {\n[data-v-46e25ade] .modal-dialog.modal-md {\n    min-width: 328px;\n}\n}\n@media (max-width: 575px) {\n[data-v-46e25ade] .modal-dialog.modal-md {\n    max-width: 328px;\n    margin: 0.5rem auto;\n}\n}\n[data-v-46e25ade] .modal-dialog.modal-lg {\n  min-width: 984px;\n}\n@media (max-width: 984px) {\n[data-v-46e25ade] .modal-dialog.modal-lg {\n    min-width: 656px;\n}\n}\n@media (max-width: 656px) {\n[data-v-46e25ade] .modal-dialog.modal-lg {\n    min-width: 328px;\n}\n}\n@media (max-width: 575px) {\n[data-v-46e25ade] .modal-dialog.modal-lg {\n    max-width: 328px;\n    margin: 0.5rem auto;\n}\n}\n[data-v-46e25ade] .modal-dialog.modal-xl {\n  min-width: 1312px;\n}\n@media (max-width: 1312px) {\n[data-v-46e25ade] .modal-dialog.modal-xl {\n    min-width: 984px;\n}\n}\n@media (max-width: 984px) {\n[data-v-46e25ade] .modal-dialog.modal-xl {\n    min-width: 656px;\n}\n}\n@media (max-width: 656px) {\n[data-v-46e25ade] .modal-dialog.modal-xl {\n    min-width: 328px;\n}\n}\n@media (max-width: 575px) {\n[data-v-46e25ade] .modal-dialog.modal-xl {\n    max-width: 328px;\n    margin: 0.5rem auto;\n}\n}\n[data-v-46e25ade] .modal-content {\n  border: 0px;\n}\n[data-v-46e25ade] .modal-body {\n  font-size: 14px;\n  padding: 24px;\n}\n[data-v-46e25ade] .modal-header {\n  padding: 24px;\n  flex-wrap: wrap;\n}\n[data-v-46e25ade] .modal-footer {\n  padding: 24px;\n}\n[data-v-46e25ade] .modal-body.no-header-border {\n  padding-top: 12px;\n}\n[data-v-46e25ade] .modal-body.no-footer-border {\n  padding-bottom: 12px;\n}\n[data-v-46e25ade] .modal-header.no-border {\n  padding-bottom: 12px;\n}\n[data-v-46e25ade] .modal-footer.no-border {\n  padding-top: 12px;\n}\n[data-v-46e25ade] .modal-header.no-padding {\n  padding: 0px;\n  padding-bottom: 24px;\n}\n[data-v-46e25ade] .modal-header.no-padding.no-border {\n  padding-bottom: 12px;\n}\n[data-v-46e25ade] .modal-header.no-border,[data-v-46e25ade] .modal-footer.no-border {\n  border: none;\n}\n\n/*# sourceMappingURL=Modal.vue.map */", map: {"version":3,"sources":["/app/build/src/components/modal/Modal.vue","Modal.vue"],"names":[],"mappings":"AAyMA;EACA,gBAAA;ACxMA;AD0MA;AAHA;IAIA,gBAAA;IACA,mBAAA;ACvME;AACF;AD0MA;EACA,gBAAA;ACxMA;AD0MA;AAHA;IAIA,gBAAA;ACvME;AACF;ADyMA;AAPA;IAQA,gBAAA;IACA,mBAAA;ACtME;AACF;ADyMA;EACA,gBAAA;ACvMA;ADyMA;AAHA;IAIA,gBAAA;ACtME;AACF;ADwMA;AAPA;IAQA,gBAAA;ACrME;AACF;ADuMA;AAXA;IAYA,gBAAA;IACA,mBAAA;ACpME;AACF;ADuMA;EACA,iBAAA;ACrMA;ADuMA;AAHA;IAIA,gBAAA;ACpME;AACF;ADsMA;AAPA;IAQA,gBAAA;ACnME;AACF;ADqMA;AAXA;IAYA,gBAAA;AClME;AACF;ADoMA;AAfA;IAgBA,gBAAA;IACA,mBAAA;ACjME;AACF;ADsMA;EACA,WAAA;ACnMA;ADsMA;EACA,eAAA;EACA,aAAA;ACnMA;ADsMA;EACA,aAAA;EACA,eAAA;ACnMA;ADsMA;EACA,aAAA;ACnMA;ADsMA;EACA,iBAAA;ACnMA;ADsMA;EACA,oBAAA;ACnMA;ADsMA;EACA,oBAAA;ACnMA;ADsMA;EACA,iBAAA;ACnMA;ADsMA;EACA,YAAA;EACA,oBAAA;ACnMA;ADqMA;EACA,oBAAA;ACnMA;ADuMA;EAEA,YAAA;ACpMA;;AAEA,oCAAoC","file":"Modal.vue","sourcesContent":["<template>\n  <b-modal\n    v-model=\"model\"\n    :body-class=\"bodyClasses\"\n    :footer-class=\"footerClasses\"\n    :header-class=\"headerClasses\"\n    :no-close-on-backdrop=\"noBackdrop\"\n    :no-close-on-esc=\"noKeyboard\"\n    v-bind=\"$props\"\n    @show=\"onShow\"\n    @shown=\"onShown\"\n    @hide=\"onHide\"\n    @hidden=\"onHidden\"\n  >\n    <template slot=\"modal-header\" slot-scope=\"{ close }\">\n      <ModalHeader\n        :headerIcon=\"headerIcon\"\n        :hideHeaderClose=\"hideHeaderClose\"\n        :title=\"title\"\n        :subtitle=\"subtitle\"\n        :breadcrumb=\"breadcrumb\"\n        :close=\"close\"\n        class=\"modal-header-content\"\n      >\n        <template slot=\"modal-title\">\n          <slot name=\"modal-title\" />\n        </template>\n      </ModalHeader>\n    </template>\n\n    <template slot=\"modal-footer\" slot-scope=\"{ close }\">\n      <ModalFooter\n        :buttons=\"buttons\"\n        :close=\"close\"\n        :busy=\"busy\"\n        :size=\"size\"\n      />\n    </template>\n\n    <template v-for=\"(_, slot) of $scopedSlots\" v-slot:[slot]=\"scope\">\n      <slot :name=\"slot\" v-bind=\"scope\" />\n    </template>\n  </b-modal>\n</template>\n\n<script>\nimport { BModal } from 'bootstrap-vue'\nimport ModalHeader from './ModalHeader.vue'\nimport ModalFooter from './ModalFooter.vue'\n\nexport default {\n  name: 'MeModal',\n  components: {\n    BModal,\n    ModalHeader,\n    ModalFooter\n  },\n\n  model: {\n    prop: 'visible'\n  },\n\n  props: {\n    bodyClass: {\n      type: String,\n      default: ''\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    buttons: {\n      type: Array,\n      default: () => []\n    },\n    centered: {\n      type: Boolean,\n      default: true\n    },\n    contentClass: {\n      type: String,\n      default: ''\n    },\n    footerClass: {\n      type: String,\n      default: ''\n    },\n    headerClass: {\n      type: String,\n      default: ''\n    },\n    hideHeader: {\n      type: Boolean,\n      default: false\n    },\n    hideHeaderClose: {\n      type: Boolean,\n      default: true\n    },\n    hideFooter: {\n      type: Boolean,\n      default: false\n    },\n    id: {\n      type: String,\n      default: null\n    },\n    noHeaderBorder: {\n      type: Boolean,\n      default: true\n    },\n    noFooterBorder: {\n      type: Boolean,\n      default: true\n    },\n    noBackdrop: {\n      type: Boolean,\n      default: false\n    },\n    noKeyboard: {\n      type: Boolean,\n      default: false\n    },\n    scrollable: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    headerIcon: {\n      type: String,\n      default: ''\n    },\n    title: {\n      type: String,\n      default: ''\n    },\n    subtitle: {\n      type: String,\n      default: ''\n    },\n    visible: {\n      type: Boolean,\n      default: false\n    },\n    breadcrumb: {\n      type: Object,\n      default: () => null\n    }\n  },\n\n  data() {\n    return {\n      model: this.visible,\n      headerClasses: `${this.headerClass} ${this.noHeaderBorder ? 'no-border' : ''} ${this.breadcrumb?.items?.length ? 'no-padding' : ''}`,\n      footerClasses: `${this.footerClass} ${this.noFooterBorder ? 'no-border' : ''}`,\n      bodyClasses: `${this.bodyClass} ${this.noHeaderBorder ? 'no-header-border' : ''} ${this.noFooterBorder ? 'no-footer-border' : ''}`\n    }\n  },\n\n  watch: {\n    visible(val) {\n      this.model = val\n    }\n  },\n\n  methods: {\n    show() {\n      this.model = true\n    },\n\n    hide() {\n      this.model = false\n    },\n\n    onShow(event) {\n      this.$emit('show', event)\n      this.$emit('update:visible', true)\n    },\n\n    onShown() {\n      this.$emit('shown')\n    },\n\n    onHide(event) {\n      this.$emit('hide', event)\n      this.$emit('update:visible', false)\n    },\n\n    onHidden() {\n      this.$emit('hidden')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  ::v-deep .modal-dialog {\n    &.modal {\n      &-sm {\n        min-width: 328px;\n\n        @media (max-width: 575px) {\n          max-width: 328px;\n          margin: 0.5rem auto;\n        }\n      }\n\n      &-md {\n        min-width: 656px;\n\n        @media (max-width: 656px) {\n          min-width: 328px;\n        }\n\n        @media (max-width: 575px) {\n          max-width: 328px;\n          margin: 0.5rem auto;\n        }\n      }\n\n      &-lg {\n        min-width: 984px;\n\n        @media (max-width: 984px) {\n          min-width: 656px;\n        }\n\n        @media (max-width: 656px) {\n          min-width: 328px;\n        }\n\n        @media (max-width: 575px) {\n          max-width: 328px;\n          margin: 0.5rem auto;\n        }\n      }\n\n      &-xl {\n        min-width: 1312px;\n\n        @media (max-width: 1312px) {\n          min-width: 984px;\n        }\n\n        @media (max-width: 984px) {\n          min-width: 656px;\n        }\n\n        @media (max-width: 656px) {\n          min-width: 328px;\n        }\n\n        @media (max-width: 575px) {\n          max-width: 328px;\n          margin: 0.5rem auto;\n        }\n      }\n    }\n  }\n\n  ::v-deep .modal-content {\n    border: 0px;\n  }\n\n  ::v-deep .modal-body {\n    font-size: 14px;\n    padding: 24px;\n  }\n\n  ::v-deep .modal-header {\n    padding: 24px;\n    flex-wrap: wrap;\n  }\n\n  ::v-deep .modal-footer {\n    padding: 24px;\n  }\n\n  ::v-deep .modal-body.no-header-border {\n    padding-top: 12px;\n  }\n\n  ::v-deep .modal-body.no-footer-border {\n    padding-bottom: 12px;\n  }\n\n  ::v-deep .modal-header.no-border {\n    padding-bottom: 12px;\n  }\n\n  ::v-deep .modal-footer.no-border {\n    padding-top: 12px;\n  }\n\n  ::v-deep .modal-header.no-padding {\n    padding: 0px;\n    padding-bottom: 24px;\n\n    &.no-border {\n      padding-bottom: 12px;\n    }\n  }\n\n  ::v-deep .modal-header.no-border,\n  ::v-deep .modal-footer.no-border {\n    border: none;\n  }\n</style>\n","::v-deep .modal-dialog.modal-sm {\n  min-width: 328px;\n}\n@media (max-width: 575px) {\n  ::v-deep .modal-dialog.modal-sm {\n    max-width: 328px;\n    margin: 0.5rem auto;\n  }\n}\n::v-deep .modal-dialog.modal-md {\n  min-width: 656px;\n}\n@media (max-width: 656px) {\n  ::v-deep .modal-dialog.modal-md {\n    min-width: 328px;\n  }\n}\n@media (max-width: 575px) {\n  ::v-deep .modal-dialog.modal-md {\n    max-width: 328px;\n    margin: 0.5rem auto;\n  }\n}\n::v-deep .modal-dialog.modal-lg {\n  min-width: 984px;\n}\n@media (max-width: 984px) {\n  ::v-deep .modal-dialog.modal-lg {\n    min-width: 656px;\n  }\n}\n@media (max-width: 656px) {\n  ::v-deep .modal-dialog.modal-lg {\n    min-width: 328px;\n  }\n}\n@media (max-width: 575px) {\n  ::v-deep .modal-dialog.modal-lg {\n    max-width: 328px;\n    margin: 0.5rem auto;\n  }\n}\n::v-deep .modal-dialog.modal-xl {\n  min-width: 1312px;\n}\n@media (max-width: 1312px) {\n  ::v-deep .modal-dialog.modal-xl {\n    min-width: 984px;\n  }\n}\n@media (max-width: 984px) {\n  ::v-deep .modal-dialog.modal-xl {\n    min-width: 656px;\n  }\n}\n@media (max-width: 656px) {\n  ::v-deep .modal-dialog.modal-xl {\n    min-width: 328px;\n  }\n}\n@media (max-width: 575px) {\n  ::v-deep .modal-dialog.modal-xl {\n    max-width: 328px;\n    margin: 0.5rem auto;\n  }\n}\n\n::v-deep .modal-content {\n  border: 0px;\n}\n\n::v-deep .modal-body {\n  font-size: 14px;\n  padding: 24px;\n}\n\n::v-deep .modal-header {\n  padding: 24px;\n  flex-wrap: wrap;\n}\n\n::v-deep .modal-footer {\n  padding: 24px;\n}\n\n::v-deep .modal-body.no-header-border {\n  padding-top: 12px;\n}\n\n::v-deep .modal-body.no-footer-border {\n  padding-bottom: 12px;\n}\n\n::v-deep .modal-header.no-border {\n  padding-bottom: 12px;\n}\n\n::v-deep .modal-footer.no-border {\n  padding-top: 12px;\n}\n\n::v-deep .modal-header.no-padding {\n  padding: 0px;\n  padding-bottom: 24px;\n}\n::v-deep .modal-header.no-padding.no-border {\n  padding-bottom: 12px;\n}\n\n::v-deep .modal-header.no-border,\n::v-deep .modal-footer.no-border {\n  border: none;\n}\n\n/*# sourceMappingURL=Modal.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$f = "data-v-46e25ade";
  /* module identifier */
  const __vue_module_identifier__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$f = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$f, staticRenderFns: __vue_staticRenderFns__$f },
    __vue_inject_styles__$f,
    __vue_script__$f,
    __vue_scope_id__$f,
    __vue_is_functional_template__$f,
    __vue_module_identifier__$f,
    false,
    createInjector,
    undefined,
    undefined
  );

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */
function baseSlice(array, start, end) {
  var index = -1,
      length = array.length;

  if (start < 0) {
    start = -start > length ? 0 : (length + start);
  }
  end = end > length ? length : end;
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : ((end - start) >>> 0);
  start >>>= 0;

  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}

var _baseSlice = baseSlice;

/**
 * Casts `array` to a slice if it's needed.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {number} start The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the cast slice.
 */
function castSlice(array, start, end) {
  var length = array.length;
  end = end === undefined ? length : end;
  return (!start && end >= length) ? array : _baseSlice(array, start, end);
}

var _castSlice = castSlice;

/** Used to compose unicode character classes. */
var rsAstralRange = '\\ud800-\\udfff',
    rsComboMarksRange = '\\u0300-\\u036f',
    reComboHalfMarksRange = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange = '\\u20d0-\\u20ff',
    rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
    rsVarRange = '\\ufe0e\\ufe0f';

/** Used to compose unicode capture groups. */
var rsZWJ = '\\u200d';

/** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

/**
 * Checks if `string` contains Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a symbol is found, else `false`.
 */
function hasUnicode(string) {
  return reHasUnicode.test(string);
}

var _hasUnicode = hasUnicode;

/** `Object#toString` result references. */
var regexpTag$4 = '[object RegExp]';

/**
 * The base implementation of `_.isRegExp` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
 */
function baseIsRegExp(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == regexpTag$4;
}

var _baseIsRegExp = baseIsRegExp;

/* Node.js helper references. */
var nodeIsRegExp = _nodeUtil && _nodeUtil.isRegExp;

/**
 * Checks if `value` is classified as a `RegExp` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
 * @example
 *
 * _.isRegExp(/abc/);
 * // => true
 *
 * _.isRegExp('/abc/');
 * // => false
 */
var isRegExp$1 = nodeIsRegExp ? _baseUnary(nodeIsRegExp) : _baseIsRegExp;

var isRegExp_1 = isRegExp$1;

/**
 * Gets the size of an ASCII `string`.
 *
 * @private
 * @param {string} string The string inspect.
 * @returns {number} Returns the string size.
 */
var asciiSize = _baseProperty('length');

var _asciiSize = asciiSize;

/** Used to compose unicode character classes. */
var rsAstralRange$1 = '\\ud800-\\udfff',
    rsComboMarksRange$1 = '\\u0300-\\u036f',
    reComboHalfMarksRange$1 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$1 = '\\u20d0-\\u20ff',
    rsComboRange$1 = rsComboMarksRange$1 + reComboHalfMarksRange$1 + rsComboSymbolsRange$1,
    rsVarRange$1 = '\\ufe0e\\ufe0f';

/** Used to compose unicode capture groups. */
var rsAstral = '[' + rsAstralRange$1 + ']',
    rsCombo = '[' + rsComboRange$1 + ']',
    rsFitz = '\\ud83c[\\udffb-\\udfff]',
    rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
    rsNonAstral = '[^' + rsAstralRange$1 + ']',
    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsZWJ$1 = '\\u200d';

/** Used to compose unicode regexes. */
var reOptMod = rsModifier + '?',
    rsOptVar = '[' + rsVarRange$1 + ']?',
    rsOptJoin = '(?:' + rsZWJ$1 + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
    rsSeq = rsOptVar + reOptMod + rsOptJoin,
    rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

/**
 * Gets the size of a Unicode `string`.
 *
 * @private
 * @param {string} string The string inspect.
 * @returns {number} Returns the string size.
 */
function unicodeSize(string) {
  var result = reUnicode.lastIndex = 0;
  while (reUnicode.test(string)) {
    ++result;
  }
  return result;
}

var _unicodeSize = unicodeSize;

/**
 * Gets the number of symbols in `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the string size.
 */
function stringSize(string) {
  return _hasUnicode(string)
    ? _unicodeSize(string)
    : _asciiSize(string);
}

var _stringSize = stringSize;

/**
 * Converts an ASCII `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function asciiToArray(string) {
  return string.split('');
}

var _asciiToArray = asciiToArray;

/** Used to compose unicode character classes. */
var rsAstralRange$2 = '\\ud800-\\udfff',
    rsComboMarksRange$2 = '\\u0300-\\u036f',
    reComboHalfMarksRange$2 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$2 = '\\u20d0-\\u20ff',
    rsComboRange$2 = rsComboMarksRange$2 + reComboHalfMarksRange$2 + rsComboSymbolsRange$2,
    rsVarRange$2 = '\\ufe0e\\ufe0f';

/** Used to compose unicode capture groups. */
var rsAstral$1 = '[' + rsAstralRange$2 + ']',
    rsCombo$1 = '[' + rsComboRange$2 + ']',
    rsFitz$1 = '\\ud83c[\\udffb-\\udfff]',
    rsModifier$1 = '(?:' + rsCombo$1 + '|' + rsFitz$1 + ')',
    rsNonAstral$1 = '[^' + rsAstralRange$2 + ']',
    rsRegional$1 = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair$1 = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsZWJ$2 = '\\u200d';

/** Used to compose unicode regexes. */
var reOptMod$1 = rsModifier$1 + '?',
    rsOptVar$1 = '[' + rsVarRange$2 + ']?',
    rsOptJoin$1 = '(?:' + rsZWJ$2 + '(?:' + [rsNonAstral$1, rsRegional$1, rsSurrPair$1].join('|') + ')' + rsOptVar$1 + reOptMod$1 + ')*',
    rsSeq$1 = rsOptVar$1 + reOptMod$1 + rsOptJoin$1,
    rsSymbol$1 = '(?:' + [rsNonAstral$1 + rsCombo$1 + '?', rsCombo$1, rsRegional$1, rsSurrPair$1, rsAstral$1].join('|') + ')';

/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
var reUnicode$1 = RegExp(rsFitz$1 + '(?=' + rsFitz$1 + ')|' + rsSymbol$1 + rsSeq$1, 'g');

/**
 * Converts a Unicode `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function unicodeToArray(string) {
  return string.match(reUnicode$1) || [];
}

var _unicodeToArray = unicodeToArray;

/**
 * Converts `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */
function stringToArray(string) {
  return _hasUnicode(string)
    ? _unicodeToArray(string)
    : _asciiToArray(string);
}

var _stringToArray = stringToArray;

/** Used as default options for `_.truncate`. */
var DEFAULT_TRUNC_LENGTH = 30,
    DEFAULT_TRUNC_OMISSION = '...';

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags$1 = /\w*$/;

/**
 * Truncates `string` if it's longer than the given maximum string length.
 * The last characters of the truncated string are replaced with the omission
 * string which defaults to "...".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category String
 * @param {string} [string=''] The string to truncate.
 * @param {Object} [options={}] The options object.
 * @param {number} [options.length=30] The maximum string length.
 * @param {string} [options.omission='...'] The string to indicate text is omitted.
 * @param {RegExp|string} [options.separator] The separator pattern to truncate to.
 * @returns {string} Returns the truncated string.
 * @example
 *
 * _.truncate('hi-diddly-ho there, neighborino');
 * // => 'hi-diddly-ho there, neighbo...'
 *
 * _.truncate('hi-diddly-ho there, neighborino', {
 *   'length': 24,
 *   'separator': ' '
 * });
 * // => 'hi-diddly-ho there,...'
 *
 * _.truncate('hi-diddly-ho there, neighborino', {
 *   'length': 24,
 *   'separator': /,? +/
 * });
 * // => 'hi-diddly-ho there...'
 *
 * _.truncate('hi-diddly-ho there, neighborino', {
 *   'omission': ' [...]'
 * });
 * // => 'hi-diddly-ho there, neig [...]'
 */
function truncate(string, options) {
  var length = DEFAULT_TRUNC_LENGTH,
      omission = DEFAULT_TRUNC_OMISSION;

  if (isObject_1(options)) {
    var separator = 'separator' in options ? options.separator : separator;
    length = 'length' in options ? toInteger_1(options.length) : length;
    omission = 'omission' in options ? _baseToString(options.omission) : omission;
  }
  string = toString_1(string);

  var strLength = string.length;
  if (_hasUnicode(string)) {
    var strSymbols = _stringToArray(string);
    strLength = strSymbols.length;
  }
  if (length >= strLength) {
    return string;
  }
  var end = length - _stringSize(omission);
  if (end < 1) {
    return omission;
  }
  var result = strSymbols
    ? _castSlice(strSymbols, 0, end).join('')
    : string.slice(0, end);

  if (separator === undefined) {
    return result + omission;
  }
  if (strSymbols) {
    end += (result.length - end);
  }
  if (isRegExp_1(separator)) {
    if (string.slice(end).search(separator)) {
      var match,
          substring = result;

      if (!separator.global) {
        separator = RegExp(separator.source, toString_1(reFlags$1.exec(separator)) + 'g');
      }
      separator.lastIndex = 0;
      while ((match = separator.exec(substring))) {
        var newEnd = match.index;
      }
      result = result.slice(0, newEnd === undefined ? end : newEnd);
    }
  } else if (string.indexOf(_baseToString(separator), end) != end) {
    var index = result.lastIndexOf(separator);
    if (index > -1) {
      result = result.slice(0, index);
    }
  }
  return result + omission;
}

var truncate_1 = truncate;

var apexcharts_min = createCommonjsModule(function (module, exports) {
/*!
 * ApexCharts v3.22.0
 * (c) 2018-2020 Juned Chhipa
 * Released under the MIT License.
 */
!function(t,e){module.exports=e();}(commonjsGlobal,(function(){function t(e){return (t="function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t})(e)}function e(t,e){if(!(t instanceof e))throw new TypeError("Cannot call a class as a function")}function i(t,e){for(var i=0;i<e.length;i++){var a=e[i];a.enumerable=a.enumerable||!1,a.configurable=!0,"value"in a&&(a.writable=!0),Object.defineProperty(t,a.key,a);}}function a(t,e,a){return e&&i(t.prototype,e),a&&i(t,a),t}function s(t,e,i){return e in t?Object.defineProperty(t,e,{value:i,enumerable:!0,configurable:!0,writable:!0}):t[e]=i,t}function r(t,e){var i=Object.keys(t);if(Object.getOwnPropertySymbols){var a=Object.getOwnPropertySymbols(t);e&&(a=a.filter((function(e){return Object.getOwnPropertyDescriptor(t,e).enumerable}))),i.push.apply(i,a);}return i}function n(t){for(var e=1;e<arguments.length;e++){var i=null!=arguments[e]?arguments[e]:{};e%2?r(Object(i),!0).forEach((function(e){s(t,e,i[e]);})):Object.getOwnPropertyDescriptors?Object.defineProperties(t,Object.getOwnPropertyDescriptors(i)):r(Object(i)).forEach((function(e){Object.defineProperty(t,e,Object.getOwnPropertyDescriptor(i,e));}));}return t}function o(t,e){if("function"!=typeof e&&null!==e)throw new TypeError("Super expression must either be null or a function");t.prototype=Object.create(e&&e.prototype,{constructor:{value:t,writable:!0,configurable:!0}}),e&&h(t,e);}function l(t){return (l=Object.setPrototypeOf?Object.getPrototypeOf:function(t){return t.__proto__||Object.getPrototypeOf(t)})(t)}function h(t,e){return (h=Object.setPrototypeOf||function(t,e){return t.__proto__=e,t})(t,e)}function c(t,e){return !e||"object"!=typeof e&&"function"!=typeof e?function(t){if(void 0===t)throw new ReferenceError("this hasn't been initialised - super() hasn't been called");return t}(t):e}function d(t){var e=function(){if("undefined"==typeof Reflect||!Reflect.construct)return !1;if(Reflect.construct.sham)return !1;if("function"==typeof Proxy)return !0;try{return Date.prototype.toString.call(Reflect.construct(Date,[],(function(){}))),!0}catch(t){return !1}}();return function(){var i,a=l(t);if(e){var s=l(this).constructor;i=Reflect.construct(a,arguments,s);}else i=a.apply(this,arguments);return c(this,i)}}function g(t){return function(t){if(Array.isArray(t))return u(t)}(t)||function(t){if("undefined"!=typeof Symbol&&Symbol.iterator in Object(t))return Array.from(t)}(t)||function(t,e){if(!t)return;if("string"==typeof t)return u(t,e);var i=Object.prototype.toString.call(t).slice(8,-1);"Object"===i&&t.constructor&&(i=t.constructor.name);if("Map"===i||"Set"===i)return Array.from(t);if("Arguments"===i||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(i))return u(t,e)}(t)||function(){throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}()}function u(t,e){(null==e||e>t.length)&&(e=t.length);for(var i=0,a=new Array(e);i<e;i++)a[i]=t[i];return a}var f=function(){function i(){e(this,i);}return a(i,[{key:"shadeRGBColor",value:function(t,e){var i=e.split(","),a=t<0?0:255,s=t<0?-1*t:t,r=parseInt(i[0].slice(4),10),n=parseInt(i[1],10),o=parseInt(i[2],10);return "rgb("+(Math.round((a-r)*s)+r)+","+(Math.round((a-n)*s)+n)+","+(Math.round((a-o)*s)+o)+")"}},{key:"shadeHexColor",value:function(t,e){var i=parseInt(e.slice(1),16),a=t<0?0:255,s=t<0?-1*t:t,r=i>>16,n=i>>8&255,o=255&i;return "#"+(16777216+65536*(Math.round((a-r)*s)+r)+256*(Math.round((a-n)*s)+n)+(Math.round((a-o)*s)+o)).toString(16).slice(1)}},{key:"shadeColor",value:function(t,e){return i.isColorHex(e)?this.shadeHexColor(t,e):this.shadeRGBColor(t,e)}}],[{key:"bind",value:function(t,e){return function(){return t.apply(e,arguments)}}},{key:"isObject",value:function(e){return e&&"object"===t(e)&&!Array.isArray(e)&&null!=e}},{key:"listToArray",value:function(t){var e,i=[];for(e=0;e<t.length;e++)i[e]=t[e];return i}},{key:"extend",value:function(t,e){var i=this;"function"!=typeof Object.assign&&(Object.assign=function(t){if(null==t)throw new TypeError("Cannot convert undefined or null to object");for(var e=Object(t),i=1;i<arguments.length;i++){var a=arguments[i];if(null!=a)for(var s in a)a.hasOwnProperty(s)&&(e[s]=a[s]);}return e});var a=Object.assign({},t);return this.isObject(t)&&this.isObject(e)&&Object.keys(e).forEach((function(r){i.isObject(e[r])&&r in t?a[r]=i.extend(t[r],e[r]):Object.assign(a,s({},r,e[r]));})),a}},{key:"extendArray",value:function(t,e){var a=[];return t.map((function(t){a.push(i.extend(e,t));})),t=a}},{key:"monthMod",value:function(t){return t%12}},{key:"clone",value:function(e){if("[object Array]"===Object.prototype.toString.call(e)){for(var i=[],a=0;a<e.length;a++)i[a]=this.clone(e[a]);return i}if("[object Null]"===Object.prototype.toString.call(e))return null;if("[object Date]"===Object.prototype.toString.call(e))return e;if("object"===t(e)){var s={};for(var r in e)e.hasOwnProperty(r)&&(s[r]=this.clone(e[r]));return s}return e}},{key:"log10",value:function(t){return Math.log(t)/Math.LN10}},{key:"roundToBase10",value:function(t){return Math.pow(10,Math.floor(Math.log10(t)))}},{key:"roundToBase",value:function(t,e){return Math.pow(e,Math.floor(Math.log(t)/Math.log(e)))}},{key:"parseNumber",value:function(t){return null===t?t:parseFloat(t)}},{key:"randomId",value:function(){return (Math.random()+1).toString(36).substring(4)}},{key:"noExponents",value:function(t){var e=String(t).split(/[eE]/);if(1===e.length)return e[0];var i="",a=t<0?"-":"",s=e[0].replace(".",""),r=Number(e[1])+1;if(r<0){for(i=a+"0.";r++;)i+="0";return i+s.replace(/^-/,"")}for(r-=s.length;r--;)i+="0";return s+i}},{key:"getDimensions",value:function(t){var e=getComputedStyle(t),i=[],a=t.clientHeight,s=t.clientWidth;return a-=parseFloat(e.paddingTop)+parseFloat(e.paddingBottom),s-=parseFloat(e.paddingLeft)+parseFloat(e.paddingRight),i.push(s),i.push(a),i}},{key:"getBoundingClientRect",value:function(t){var e=t.getBoundingClientRect();return {top:e.top,right:e.right,bottom:e.bottom,left:e.left,width:t.clientWidth,height:t.clientHeight,x:e.left,y:e.top}}},{key:"getLargestStringFromArr",value:function(t){return t.reduce((function(t,e){return Array.isArray(e)&&(e=e.reduce((function(t,e){return t.length>e.length?t:e}))),t.length>e.length?t:e}),0)}},{key:"hexToRgba",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:"#999999",e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:.6;"#"!==t.substring(0,1)&&(t="#999999");var i=t.replace("#","");i=i.match(new RegExp("(.{"+i.length/3+"})","g"));for(var a=0;a<i.length;a++)i[a]=parseInt(1===i[a].length?i[a]+i[a]:i[a],16);return void 0!==e&&i.push(e),"rgba("+i.join(",")+")"}},{key:"getOpacityFromRGBA",value:function(t){return parseFloat(t.replace(/^.*,(.+)\)/,"$1"))}},{key:"rgb2hex",value:function(t){return (t=t.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i))&&4===t.length?"#"+("0"+parseInt(t[1],10).toString(16)).slice(-2)+("0"+parseInt(t[2],10).toString(16)).slice(-2)+("0"+parseInt(t[3],10).toString(16)).slice(-2):""}},{key:"isColorHex",value:function(t){return /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)|(^#[0-9A-F]{8}$)/i.test(t)}},{key:"polarToCartesian",value:function(t,e,i,a){var s=(a-90)*Math.PI/180;return {x:t+i*Math.cos(s),y:e+i*Math.sin(s)}}},{key:"escapeString",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:"x",i=t.toString().slice();return i=i.replace(/[` ~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi,e)}},{key:"negToZero",value:function(t){return t<0?0:t}},{key:"moveIndexInArray",value:function(t,e,i){if(i>=t.length)for(var a=i-t.length+1;a--;)t.push(void 0);return t.splice(i,0,t.splice(e,1)[0]),t}},{key:"extractNumber",value:function(t){return parseFloat(t.replace(/[^\d.]*/g,""))}},{key:"findAncestor",value:function(t,e){for(;(t=t.parentElement)&&!t.classList.contains(e););return t}},{key:"setELstyles",value:function(t,e){for(var i in e)e.hasOwnProperty(i)&&(t.style.key=e[i]);}},{key:"isNumber",value:function(t){return !isNaN(t)&&parseFloat(Number(t))===t&&!isNaN(parseInt(t,10))}},{key:"isFloat",value:function(t){return Number(t)===t&&t%1!=0}},{key:"isSafari",value:function(){return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)}},{key:"isFirefox",value:function(){return navigator.userAgent.toLowerCase().indexOf("firefox")>-1}},{key:"isIE11",value:function(){if(-1!==window.navigator.userAgent.indexOf("MSIE")||window.navigator.appVersion.indexOf("Trident/")>-1)return !0}},{key:"isIE",value:function(){var t=window.navigator.userAgent,e=t.indexOf("MSIE ");if(e>0)return parseInt(t.substring(e+5,t.indexOf(".",e)),10);if(t.indexOf("Trident/")>0){var i=t.indexOf("rv:");return parseInt(t.substring(i+3,t.indexOf(".",i)),10)}var a=t.indexOf("Edge/");return a>0&&parseInt(t.substring(a+5,t.indexOf(".",a)),10)}}]),i}(),p=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"getDefaultFilter",value:function(t,e){var i=this.w;t.unfilter(!0),(new window.SVG.Filter).size("120%","180%","-5%","-40%"),"none"!==i.config.states.normal.filter?this.applyFilter(t,e,i.config.states.normal.filter.type,i.config.states.normal.filter.value):i.config.chart.dropShadow.enabled&&this.dropShadow(t,i.config.chart.dropShadow,e);}},{key:"addNormalFilter",value:function(t,e){var i=this.w;i.config.chart.dropShadow.enabled&&!t.node.classList.contains("apexcharts-marker")&&this.dropShadow(t,i.config.chart.dropShadow,e);}},{key:"addLightenFilter",value:function(t,e,i){var a=this,s=this.w,r=i.intensity;t.unfilter(!0);new window.SVG.Filter;t.filter((function(t){var i=s.config.chart.dropShadow;(i.enabled?a.addShadow(t,e,i):t).componentTransfer({rgb:{type:"linear",slope:1.5,intercept:r}});})),t.filterer.node.setAttribute("filterUnits","userSpaceOnUse"),this._scaleFilterSize(t.filterer.node);}},{key:"addDarkenFilter",value:function(t,e,i){var a=this,s=this.w,r=i.intensity;t.unfilter(!0);new window.SVG.Filter;t.filter((function(t){var i=s.config.chart.dropShadow;(i.enabled?a.addShadow(t,e,i):t).componentTransfer({rgb:{type:"linear",slope:r}});})),t.filterer.node.setAttribute("filterUnits","userSpaceOnUse"),this._scaleFilterSize(t.filterer.node);}},{key:"applyFilter",value:function(t,e,i){var a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:.5;switch(i){case"none":this.addNormalFilter(t,e);break;case"lighten":this.addLightenFilter(t,e,{intensity:a});break;case"darken":this.addDarkenFilter(t,e,{intensity:a});}}},{key:"addShadow",value:function(t,e,i){var a=i.blur,s=i.top,r=i.left,n=i.color,o=i.opacity,l=t.flood(Array.isArray(n)?n[e]:n,o).composite(t.sourceAlpha,"in").offset(r,s).gaussianBlur(a).merge(t.source);return t.blend(t.source,l)}},{key:"dropShadow",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:0,a=e.top,s=e.left,r=e.blur,n=e.color,o=e.opacity,l=e.noUserSpaceOnUse,h=this.w;return t.unfilter(!0),f.isIE()&&"radialBar"===h.config.chart.type||(n=Array.isArray(n)?n[i]:n,t.filter((function(t){var e=null;e=f.isSafari()||f.isFirefox()||f.isIE()?t.flood(n,o).composite(t.sourceAlpha,"in").offset(s,a).gaussianBlur(r):t.flood(n,o).composite(t.sourceAlpha,"in").offset(s,a).gaussianBlur(r).merge(t.source),t.blend(t.source,e);})),l||t.filterer.node.setAttribute("filterUnits","userSpaceOnUse"),this._scaleFilterSize(t.filterer.node)),t}},{key:"setSelectionFilter",value:function(t,e,i){var a=this.w;if(void 0!==a.globals.selectedDataPoints[e]&&a.globals.selectedDataPoints[e].indexOf(i)>-1){t.node.setAttribute("selected",!0);var s=a.config.states.active.filter;"none"!==s&&this.applyFilter(t,e,s.type,s.value);}}},{key:"_scaleFilterSize",value:function(t){!function(e){for(var i in e)e.hasOwnProperty(i)&&t.setAttribute(i,e[i]);}({width:"200%",height:"200%",x:"-50%",y:"-50%"});}}]),t}(),x=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.setEasingFunctions();}return a(t,[{key:"setEasingFunctions",value:function(){var t;if(!this.w.globals.easing){switch(this.w.config.chart.animations.easing){case"linear":t="-";break;case"easein":t="<";break;case"easeout":t=">";break;case"easeinout":t="<>";break;case"swing":t=function(t){var e=1.70158;return (t-=1)*t*((e+1)*t+e)+1};break;case"bounce":t=function(t){return t<1/2.75?7.5625*t*t:t<2/2.75?7.5625*(t-=1.5/2.75)*t+.75:t<2.5/2.75?7.5625*(t-=2.25/2.75)*t+.9375:7.5625*(t-=2.625/2.75)*t+.984375};break;case"elastic":t=function(t){return t===!!t?t:Math.pow(2,-10*t)*Math.sin((t-.075)*(2*Math.PI)/.3)+1};break;default:t="<>";}this.w.globals.easing=t;}}},{key:"animateLine",value:function(t,e,i,a){t.attr(e).animate(a).attr(i);}},{key:"animateCircleRadius",value:function(t,e,i,a,s,r){e||(e=0),t.attr({r:e}).animate(a,s).attr({r:i}).afterAll((function(){r();}));}},{key:"animateCircle",value:function(t,e,i,a,s){t.attr({r:e.r,cx:e.cx,cy:e.cy}).animate(a,s).attr({r:i.r,cx:i.cx,cy:i.cy});}},{key:"animateRect",value:function(t,e,i,a,s){t.attr(e).animate(a).attr(i).afterAll((function(){return s()}));}},{key:"animatePathsGradually",value:function(t){var e=t.el,i=t.realIndex,a=t.j,s=t.fill,r=t.pathFrom,n=t.pathTo,o=t.speed,l=t.delay,h=this.w,c=0;h.config.chart.animations.animateGradually.enabled&&(c=h.config.chart.animations.animateGradually.delay),h.config.chart.animations.dynamicAnimation.enabled&&h.globals.dataChanged&&"bar"!==h.config.chart.type&&(c=0),this.morphSVG(e,i,a,"line"!==h.config.chart.type||h.globals.comboCharts?s:"stroke",r,n,o,l*c);}},{key:"showDelayedElements",value:function(){this.w.globals.delayedElements.forEach((function(t){t.el.classList.remove("apexcharts-element-hidden");}));}},{key:"animationCompleted",value:function(t){var e=this.w;e.globals.animationEnded||(e.globals.animationEnded=!0,this.showDelayedElements(),"function"==typeof e.config.chart.events.animationEnd&&e.config.chart.events.animationEnd(this.ctx,{el:t,w:e}));}},{key:"morphSVG",value:function(t,e,i,a,s,r,n,o){var l=this,h=this.w;s||(s=t.attr("pathFrom")),r||(r=t.attr("pathTo"));var c=function(t){return "radar"===h.config.chart.type&&(n=1),"M 0 ".concat(h.globals.gridHeight)};(!s||s.indexOf("undefined")>-1||s.indexOf("NaN")>-1)&&(s=c()),(!r||r.indexOf("undefined")>-1||r.indexOf("NaN")>-1)&&(r=c()),h.globals.shouldAnimate||(n=1),t.plot(s).animate(1,h.globals.easing,o).plot(s).animate(n,h.globals.easing,o).plot(r).afterAll((function(){f.isNumber(i)?i===h.globals.series[h.globals.maxValsInArrayIndex].length-2&&h.globals.shouldAnimate&&l.animationCompleted(t):"none"!==a&&h.globals.shouldAnimate&&(!h.globals.comboCharts&&e===h.globals.series.length-1||h.globals.comboCharts)&&l.animationCompleted(t),l.showDelayedElements();}));}}]),t}(),b=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"drawLine",value:function(t,e,i,a){var s=arguments.length>4&&void 0!==arguments[4]?arguments[4]:"#a8a8a8",r=arguments.length>5&&void 0!==arguments[5]?arguments[5]:0,n=arguments.length>6&&void 0!==arguments[6]?arguments[6]:null,o=this.w,l=o.globals.dom.Paper.line().attr({x1:t,y1:e,x2:i,y2:a,stroke:s,"stroke-dasharray":r,"stroke-width":n});return l}},{key:"drawRect",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:0,e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:0,i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:0,a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:0,s=arguments.length>4&&void 0!==arguments[4]?arguments[4]:0,r=arguments.length>5&&void 0!==arguments[5]?arguments[5]:"#fefefe",n=arguments.length>6&&void 0!==arguments[6]?arguments[6]:1,o=arguments.length>7&&void 0!==arguments[7]?arguments[7]:null,l=arguments.length>8&&void 0!==arguments[8]?arguments[8]:null,h=arguments.length>9&&void 0!==arguments[9]?arguments[9]:0,c=this.w,d=c.globals.dom.Paper.rect();return d.attr({x:t,y:e,width:i>0?i:0,height:a>0?a:0,rx:s,ry:s,opacity:n,"stroke-width":null!==o?o:0,stroke:null!==l?l:"none","stroke-dasharray":h}),d.node.setAttribute("fill",r),d}},{key:"drawPolygon",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:"#e1e1e1",i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:1,a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:"none",s=this.w,r=s.globals.dom.Paper.polygon(t).attr({fill:a,stroke:e,"stroke-width":i});return r}},{key:"drawCircle",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:null,i=this.w,a=i.globals.dom.Paper.circle(2*t);return null!==e&&a.attr(e),a}},{key:"drawPath",value:function(t){var e=t.d,i=void 0===e?"":e,a=t.stroke,s=void 0===a?"#a8a8a8":a,r=t.strokeWidth,n=void 0===r?1:r,o=t.fill,l=t.fillOpacity,h=void 0===l?1:l,c=t.strokeOpacity,d=void 0===c?1:c,g=t.classes,u=t.strokeLinecap,f=void 0===u?null:u,p=t.strokeDashArray,x=void 0===p?0:p,b=this.w;return null===f&&(f=b.config.stroke.lineCap),(i.indexOf("undefined")>-1||i.indexOf("NaN")>-1)&&(i="M 0 ".concat(b.globals.gridHeight)),b.globals.dom.Paper.path(i).attr({fill:o,"fill-opacity":h,stroke:s,"stroke-opacity":d,"stroke-linecap":f,"stroke-width":n,"stroke-dasharray":x,class:g})}},{key:"group",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:null,e=this.w,i=e.globals.dom.Paper.group();return null!==t&&i.attr(t),i}},{key:"move",value:function(t,e){var i=["M",t,e].join(" ");return i}},{key:"line",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:null,a=null;return null===i?a=["L",t,e].join(" "):"H"===i?a=["H",t].join(" "):"V"===i&&(a=["V",e].join(" ")),a}},{key:"curve",value:function(t,e,i,a,s,r){var n=["C",t,e,i,a,s,r].join(" ");return n}},{key:"quadraticCurve",value:function(t,e,i,a){return ["Q",t,e,i,a].join(" ")}},{key:"arc",value:function(t,e,i,a,s,r,n){var o=arguments.length>7&&void 0!==arguments[7]&&arguments[7],l="A";o&&(l="a");var h=[l,t,e,i,a,s,r,n].join(" ");return h}},{key:"renderPaths",value:function(t){var e,i=t.j,a=t.realIndex,s=t.pathFrom,r=t.pathTo,o=t.stroke,l=t.strokeWidth,h=t.strokeLinecap,c=t.fill,d=t.animationDelay,g=t.initialSpeed,u=t.dataChangeSpeed,f=t.className,b=t.shouldClipToGrid,m=void 0===b||b,v=t.bindEventsOnPaths,y=void 0===v||v,w=t.drawShadow,k=void 0===w||w,A=this.w,S=new p(this.ctx),C=new x(this.ctx),L=this.w.config.chart.animations.enabled,P=L&&this.w.config.chart.animations.dynamicAnimation.enabled,T=!!(L&&!A.globals.resized||P&&A.globals.dataChanged&&A.globals.shouldAnimate);T?e=s:(e=r,A.globals.animationEnded=!0);var z=A.config.stroke.dashArray,I=0;I=Array.isArray(z)?z[a]:A.config.stroke.dashArray;var M=this.drawPath({d:e,stroke:o,strokeWidth:l,fill:c,fillOpacity:1,classes:f,strokeLinecap:h,strokeDashArray:I});if(M.attr("index",a),m&&M.attr({"clip-path":"url(#gridRectMask".concat(A.globals.cuid,")")}),"none"!==A.config.states.normal.filter.type)S.getDefaultFilter(M,a);else if(A.config.chart.dropShadow.enabled&&k&&(!A.config.chart.dropShadow.enabledOnSeries||A.config.chart.dropShadow.enabledOnSeries&&-1!==A.config.chart.dropShadow.enabledOnSeries.indexOf(a))){var E=A.config.chart.dropShadow;S.dropShadow(M,E,a);}y&&(M.node.addEventListener("mouseenter",this.pathMouseEnter.bind(this,M)),M.node.addEventListener("mouseleave",this.pathMouseLeave.bind(this,M)),M.node.addEventListener("mousedown",this.pathMouseDown.bind(this,M))),M.attr({pathTo:r,pathFrom:s});var X={el:M,j:i,realIndex:a,pathFrom:s,pathTo:r,fill:c,strokeWidth:l,delay:d};return !L||A.globals.resized||A.globals.dataChanged?!A.globals.resized&&A.globals.dataChanged||C.showDelayedElements():C.animatePathsGradually(n(n({},X),{},{speed:g})),A.globals.dataChanged&&P&&T&&C.animatePathsGradually(n(n({},X),{},{speed:u})),M}},{key:"drawPattern",value:function(t,e,i){var a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:"#a8a8a8",s=arguments.length>4&&void 0!==arguments[4]?arguments[4]:0,r=this.w,n=r.globals.dom.Paper.pattern(e,i,(function(r){"horizontalLines"===t?r.line(0,0,i,0).stroke({color:a,width:s+1}):"verticalLines"===t?r.line(0,0,0,e).stroke({color:a,width:s+1}):"slantedLines"===t?r.line(0,0,e,i).stroke({color:a,width:s}):"squares"===t?r.rect(e,i).fill("none").stroke({color:a,width:s}):"circles"===t&&r.circle(e).fill("none").stroke({color:a,width:s});}));return n}},{key:"drawGradient",value:function(t,e,i,a,s){var r,n=arguments.length>5&&void 0!==arguments[5]?arguments[5]:null,o=arguments.length>6&&void 0!==arguments[6]?arguments[6]:null,l=arguments.length>7&&void 0!==arguments[7]?arguments[7]:null,h=arguments.length>8&&void 0!==arguments[8]?arguments[8]:0,c=this.w;e.length<9&&0===e.indexOf("#")&&(e=f.hexToRgba(e,a)),i.length<9&&0===i.indexOf("#")&&(i=f.hexToRgba(i,s));var d=0,g=1,u=1,p=null;null!==o&&(d=void 0!==o[0]?o[0]/100:0,g=void 0!==o[1]?o[1]/100:1,u=void 0!==o[2]?o[2]/100:1,p=void 0!==o[3]?o[3]/100:null);var x=!("donut"!==c.config.chart.type&&"pie"!==c.config.chart.type&&"polarArea"!==c.config.chart.type&&"bubble"!==c.config.chart.type);if(r=null===l||0===l.length?c.globals.dom.Paper.gradient(x?"radial":"linear",(function(t){t.at(d,e,a),t.at(g,i,s),t.at(u,i,s),null!==p&&t.at(p,e,a);})):c.globals.dom.Paper.gradient(x?"radial":"linear",(function(t){(Array.isArray(l[h])?l[h]:l).forEach((function(e){t.at(e.offset/100,e.color,e.opacity);}));})),x){var b=c.globals.gridWidth/2,m=c.globals.gridHeight/2;"bubble"!==c.config.chart.type?r.attr({gradientUnits:"userSpaceOnUse",cx:b,cy:m,r:n}):r.attr({cx:.5,cy:.5,r:.8,fx:.2,fy:.2});}else "vertical"===t?r.from(0,0).to(0,1):"diagonal"===t?r.from(0,0).to(1,1):"horizontal"===t?r.from(0,1).to(1,1):"diagonal2"===t&&r.from(1,0).to(0,1);return r}},{key:"drawText",value:function(t){var e,i=t.x,a=t.y,s=t.text,r=t.textAnchor,n=t.fontSize,o=t.fontFamily,l=t.fontWeight,h=t.foreColor,c=t.opacity,d=t.cssClass,g=void 0===d?"":d,u=t.isPlainText,f=void 0===u||u,p=this.w;return void 0===s&&(s=""),r||(r="start"),h&&h.length||(h=p.config.chart.foreColor),o=o||p.config.chart.fontFamily,l=l||"regular",(e=Array.isArray(s)?p.globals.dom.Paper.text((function(t){for(var e=0;e<s.length;e++)0===e?t.tspan(s[e]):t.tspan(s[e]).newLine();})):f?p.globals.dom.Paper.plain(s):p.globals.dom.Paper.text((function(t){return t.tspan(s)}))).attr({x:i,y:a,"text-anchor":r,"dominant-baseline":"auto","font-size":n,"font-family":o,"font-weight":l,fill:h,class:"apexcharts-text "+g}),e.node.style.fontFamily=o,e.node.style.opacity=c,e}},{key:"drawMarker",value:function(t,e,i){t=t||0;var a=i.pSize||0,s=null;if("square"===i.shape){var r=void 0===i.pRadius?a/2:i.pRadius;null!==e&&a||(a=0,r=0);var n=1.2*a+r,o=this.drawRect(n,n,n,n,r);o.attr({x:t-n/2,y:e-n/2,cx:t,cy:e,class:i.class?i.class:"",fill:i.pointFillColor,"fill-opacity":i.pointFillOpacity?i.pointFillOpacity:1,stroke:i.pointStrokeColor,"stroke-width":i.pWidth?i.pWidth:0,"stroke-opacity":i.pointStrokeOpacity?i.pointStrokeOpacity:1}),s=o;}else "circle"!==i.shape&&i.shape||(f.isNumber(e)||(a=0,e=0),s=this.drawCircle(a,{cx:t,cy:e,class:i.class?i.class:"",stroke:i.pointStrokeColor,fill:i.pointFillColor,"fill-opacity":i.pointFillOpacity?i.pointFillOpacity:1,"stroke-width":i.pWidth?i.pWidth:0,"stroke-opacity":i.pointStrokeOpacity?i.pointStrokeOpacity:1}));return s}},{key:"pathMouseEnter",value:function(t,e){var i=this.w,a=new p(this.ctx),s=parseInt(t.node.getAttribute("index"),10),r=parseInt(t.node.getAttribute("j"),10);if("function"==typeof i.config.chart.events.dataPointMouseEnter&&i.config.chart.events.dataPointMouseEnter(e,this.ctx,{seriesIndex:s,dataPointIndex:r,w:i}),this.ctx.events.fireEvent("dataPointMouseEnter",[e,this.ctx,{seriesIndex:s,dataPointIndex:r,w:i}]),("none"===i.config.states.active.filter.type||"true"!==t.node.getAttribute("selected"))&&"none"!==i.config.states.hover.filter.type&&"none"!==i.config.states.active.filter.type&&!i.globals.isTouchDevice){var n=i.config.states.hover.filter;a.applyFilter(t,s,n.type,n.value);}}},{key:"pathMouseLeave",value:function(t,e){var i=this.w,a=new p(this.ctx),s=parseInt(t.node.getAttribute("index"),10),r=parseInt(t.node.getAttribute("j"),10);"function"==typeof i.config.chart.events.dataPointMouseLeave&&i.config.chart.events.dataPointMouseLeave(e,this.ctx,{seriesIndex:s,dataPointIndex:r,w:i}),this.ctx.events.fireEvent("dataPointMouseLeave",[e,this.ctx,{seriesIndex:s,dataPointIndex:r,w:i}]),"none"!==i.config.states.active.filter.type&&"true"===t.node.getAttribute("selected")||"none"!==i.config.states.hover.filter.type&&a.getDefaultFilter(t,s);}},{key:"pathMouseDown",value:function(t,e){var i=this.w,a=new p(this.ctx),s=parseInt(t.node.getAttribute("index"),10),r=parseInt(t.node.getAttribute("j"),10),n="false";if("true"===t.node.getAttribute("selected")){if(t.node.setAttribute("selected","false"),i.globals.selectedDataPoints[s].indexOf(r)>-1){var o=i.globals.selectedDataPoints[s].indexOf(r);i.globals.selectedDataPoints[s].splice(o,1);}}else {if(!i.config.states.active.allowMultipleDataPointsSelection&&i.globals.selectedDataPoints.length>0){i.globals.selectedDataPoints=[];var l=i.globals.dom.Paper.select(".apexcharts-series path").members,h=i.globals.dom.Paper.select(".apexcharts-series circle, .apexcharts-series rect").members,c=function(t){Array.prototype.forEach.call(t,(function(t){t.node.setAttribute("selected","false"),a.getDefaultFilter(t,s);}));};c(l),c(h);}t.node.setAttribute("selected","true"),n="true",void 0===i.globals.selectedDataPoints[s]&&(i.globals.selectedDataPoints[s]=[]),i.globals.selectedDataPoints[s].push(r);}if("true"===n){var d=i.config.states.active.filter;"none"!==d&&a.applyFilter(t,s,d.type,d.value);}else "none"!==i.config.states.active.filter.type&&a.getDefaultFilter(t,s);"function"==typeof i.config.chart.events.dataPointSelection&&i.config.chart.events.dataPointSelection(e,this.ctx,{selectedDataPoints:i.globals.selectedDataPoints,seriesIndex:s,dataPointIndex:r,w:i}),e&&this.ctx.events.fireEvent("dataPointSelection",[e,this.ctx,{selectedDataPoints:i.globals.selectedDataPoints,seriesIndex:s,dataPointIndex:r,w:i}]);}},{key:"rotateAroundCenter",value:function(t){var e=t.getBBox();return {x:e.x+e.width/2,y:e.y+e.height/2}}},{key:"getTextRects",value:function(t,e,i,a){var s=!(arguments.length>4&&void 0!==arguments[4])||arguments[4],r=this.w,n=this.drawText({x:-200,y:-200,text:t,textAnchor:"start",fontSize:e,fontFamily:i,foreColor:"#fff",opacity:0});a&&n.attr("transform",a),r.globals.dom.Paper.add(n);var o=n.bbox();return s||(o=n.node.getBoundingClientRect()),n.remove(),{width:o.width,height:o.height}}},{key:"placeTextWithEllipsis",value:function(t,e,i){if("function"==typeof t.getComputedTextLength&&(t.textContent=e,e.length>0&&t.getComputedTextLength()>=i/.8)){for(var a=e.length-3;a>0;a-=3)if(t.getSubStringLength(0,a)<=i/.8)return void(t.textContent=e.substring(0,a)+"...");t.textContent=".";}}}],[{key:"setAttrs",value:function(t,e){for(var i in e)e.hasOwnProperty(i)&&t.setAttribute(i,e[i]);}}]),t}(),m=function(){function t(i){e(this,t),this.w=i.w,this.annoCtx=i;}return a(t,[{key:"setOrientations",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:null,i=this.w;if("vertical"===t.label.orientation){var a=null!==e?e:0,s=i.globals.dom.baseEl.querySelector(".apexcharts-xaxis-annotations .apexcharts-xaxis-annotation-label[rel='".concat(a,"']"));if(null!==s){var r=s.getBoundingClientRect();s.setAttribute("x",parseFloat(s.getAttribute("x"))-r.height+4),"top"===t.label.position?s.setAttribute("y",parseFloat(s.getAttribute("y"))+r.width):s.setAttribute("y",parseFloat(s.getAttribute("y"))-r.width);var n=this.annoCtx.graphics.rotateAroundCenter(s),o=n.x,l=n.y;s.setAttribute("transform","rotate(-90 ".concat(o," ").concat(l,")"));}}}},{key:"addBackgroundToAnno",value:function(t,e){var i=this.w;if(!t||!e.label.text||e.label.text&&!e.label.text.trim())return null;var a=i.globals.dom.baseEl.querySelector(".apexcharts-grid").getBoundingClientRect(),s=t.getBoundingClientRect(),r=e.label.style.padding.left,n=e.label.style.padding.right,o=e.label.style.padding.top,l=e.label.style.padding.bottom;"vertical"===e.label.orientation&&(o=e.label.style.padding.left,l=e.label.style.padding.right,r=e.label.style.padding.top,n=e.label.style.padding.bottom);var h=s.left-a.left-r,c=s.top-a.top-o,d=this.annoCtx.graphics.drawRect(h-i.globals.barPadForNumericAxis,c,s.width+r+n,s.height+o+l,e.label.borderRadius,e.label.style.background,1,e.label.borderWidth,e.label.borderColor,0);return e.id&&d.node.classList.add(e.id),d}},{key:"annotationsBackground",value:function(){var t=this,e=this.w,i=function(i,a,s){var r=e.globals.dom.baseEl.querySelector(".apexcharts-".concat(s,"-annotations .apexcharts-").concat(s,"-annotation-label[rel='").concat(a,"']"));if(r){var n=r.parentNode,o=t.addBackgroundToAnno(r,i);o&&n.insertBefore(o.node,r);}};e.config.annotations.xaxis.map((function(t,e){i(t,e,"xaxis");})),e.config.annotations.yaxis.map((function(t,e){i(t,e,"yaxis");})),e.config.annotations.points.map((function(t,e){i(t,e,"point");}));}},{key:"getStringX",value:function(t){var e=this.w,i=t;e.config.xaxis.convertedCatToNumeric&&e.globals.categoryLabels.length&&(t=e.globals.categoryLabels.indexOf(t)+1);var a=e.globals.labels.indexOf(t),s=e.globals.dom.baseEl.querySelector(".apexcharts-xaxis-texts-g text:nth-child("+(a+1)+")");return s&&(i=parseFloat(s.getAttribute("x"))),i}}]),t}(),v=function(){function t(i){e(this,t),this.w=i.w,this.annoCtx=i,this.invertAxis=this.annoCtx.invertAxis;}return a(t,[{key:"addXaxisAnnotation",value:function(t,e,i){var a=this.w,s=this.invertAxis?a.globals.minY:a.globals.minX,r=this.invertAxis?a.globals.maxY:a.globals.maxX,n=this.invertAxis?a.globals.yRange[0]:a.globals.xRange,o=(t.x-s)/(n/a.globals.gridWidth);this.annoCtx.inversedReversedAxis&&(o=(r-t.x)/(n/a.globals.gridWidth));var l=t.label.text;"category"!==a.config.xaxis.type&&!a.config.xaxis.convertedCatToNumeric||this.invertAxis||a.globals.dataFormatXNumeric||(o=this.annoCtx.helpers.getStringX(t.x));var h=t.strokeDashArray;if(f.isNumber(o)){if(null===t.x2||void 0===t.x2){var c=this.annoCtx.graphics.drawLine(o+t.offsetX,0+t.offsetY,o+t.offsetX,a.globals.gridHeight+t.offsetY,t.borderColor,h,t.borderWidth);e.appendChild(c.node),t.id&&c.node.classList.add(t.id);}else {var d=(t.x2-s)/(n/a.globals.gridWidth);if(this.annoCtx.inversedReversedAxis&&(d=(r-t.x2)/(n/a.globals.gridWidth)),"category"!==a.config.xaxis.type&&!a.config.xaxis.convertedCatToNumeric||this.invertAxis||a.globals.dataFormatXNumeric||(d=this.annoCtx.helpers.getStringX(t.x2)),d<o){var g=o;o=d,d=g;}var u=this.annoCtx.graphics.drawRect(o+t.offsetX,0+t.offsetY,d-o,a.globals.gridHeight+t.offsetY,0,t.fillColor,t.opacity,1,t.borderColor,h);u.node.classList.add("apexcharts-annotation-rect"),u.attr("clip-path","url(#gridRectMask".concat(a.globals.cuid,")")),e.appendChild(u.node),t.id&&u.node.classList.add(t.id);}var p="top"===t.label.position?4:a.globals.gridHeight,x=this.annoCtx.graphics.getTextRects(l,parseFloat(t.label.style.fontSize)),b=this.annoCtx.graphics.drawText({x:o+t.label.offsetX,y:p+t.label.offsetY-("vertical"===t.label.orientation?"top"===t.label.position?x.width/2-12:-x.width/2:0),text:l,textAnchor:t.label.textAnchor,fontSize:t.label.style.fontSize,fontFamily:t.label.style.fontFamily,fontWeight:t.label.style.fontWeight,foreColor:t.label.style.color,cssClass:"apexcharts-xaxis-annotation-label ".concat(t.label.style.cssClass," ").concat(t.id?t.id:"")});b.attr({rel:i}),e.appendChild(b.node),this.annoCtx.helpers.setOrientations(t,i);}}},{key:"drawXAxisAnnotations",value:function(){var t=this,e=this.w,i=this.annoCtx.graphics.group({class:"apexcharts-xaxis-annotations"});return e.config.annotations.xaxis.map((function(e,a){t.addXaxisAnnotation(e,i.node,a);})),i}}]),t}(),y=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.months31=[1,3,5,7,8,10,12],this.months30=[2,4,6,9,11],this.daysCntOfYear=[0,31,59,90,120,151,181,212,243,273,304,334];}return a(t,[{key:"isValidDate",value:function(t){return !isNaN(this.parseDate(t))}},{key:"getTimeStamp",value:function(t){return Date.parse(t)?this.w.config.xaxis.labels.datetimeUTC?new Date(new Date(t).toISOString().substr(0,25)).getTime():new Date(t).getTime():t}},{key:"getDate",value:function(t){return this.w.config.xaxis.labels.datetimeUTC?new Date(new Date(t).toUTCString()):new Date(t)}},{key:"parseDate",value:function(t){var e=Date.parse(t);if(!isNaN(e))return this.getTimeStamp(t);var i=Date.parse(t.replace(/-/g,"/").replace(/[a-z]+/gi," "));return i=this.getTimeStamp(i)}},{key:"parseDateWithTimezone",value:function(t){return Date.parse(t.replace(/-/g,"/").replace(/[a-z]+/gi," "))}},{key:"formatDate",value:function(t,e){var i=this.w.globals.locale,a=this.w.config.xaxis.labels.datetimeUTC,s=["\0"].concat(g(i.months)),r=["\x01"].concat(g(i.shortMonths)),n=["\x02"].concat(g(i.days)),o=["\x03"].concat(g(i.shortDays));function l(t,e){var i=t+"";for(e=e||2;i.length<e;)i="0"+i;return i}var h=a?t.getUTCFullYear():t.getFullYear();e=(e=(e=e.replace(/(^|[^\\])yyyy+/g,"$1"+h)).replace(/(^|[^\\])yy/g,"$1"+h.toString().substr(2,2))).replace(/(^|[^\\])y/g,"$1"+h);var c=(a?t.getUTCMonth():t.getMonth())+1;e=(e=(e=(e=e.replace(/(^|[^\\])MMMM+/g,"$1"+s[0])).replace(/(^|[^\\])MMM/g,"$1"+r[0])).replace(/(^|[^\\])MM/g,"$1"+l(c))).replace(/(^|[^\\])M/g,"$1"+c);var d=a?t.getUTCDate():t.getDate();e=(e=(e=(e=e.replace(/(^|[^\\])dddd+/g,"$1"+n[0])).replace(/(^|[^\\])ddd/g,"$1"+o[0])).replace(/(^|[^\\])dd/g,"$1"+l(d))).replace(/(^|[^\\])d/g,"$1"+d);var u=a?t.getUTCHours():t.getHours(),f=u>12?u-12:0===u?12:u;e=(e=(e=(e=e.replace(/(^|[^\\])HH+/g,"$1"+l(u))).replace(/(^|[^\\])H/g,"$1"+u)).replace(/(^|[^\\])hh+/g,"$1"+l(f))).replace(/(^|[^\\])h/g,"$1"+f);var p=a?t.getUTCMinutes():t.getMinutes();e=(e=e.replace(/(^|[^\\])mm+/g,"$1"+l(p))).replace(/(^|[^\\])m/g,"$1"+p);var x=a?t.getUTCSeconds():t.getSeconds();e=(e=e.replace(/(^|[^\\])ss+/g,"$1"+l(x))).replace(/(^|[^\\])s/g,"$1"+x);var b=a?t.getUTCMilliseconds():t.getMilliseconds();e=e.replace(/(^|[^\\])fff+/g,"$1"+l(b,3)),b=Math.round(b/10),e=e.replace(/(^|[^\\])ff/g,"$1"+l(b)),b=Math.round(b/10);var m=u<12?"AM":"PM";e=(e=(e=e.replace(/(^|[^\\])f/g,"$1"+b)).replace(/(^|[^\\])TT+/g,"$1"+m)).replace(/(^|[^\\])T/g,"$1"+m.charAt(0));var v=m.toLowerCase();e=(e=e.replace(/(^|[^\\])tt+/g,"$1"+v)).replace(/(^|[^\\])t/g,"$1"+v.charAt(0));var y=-t.getTimezoneOffset(),w=a||!y?"Z":y>0?"+":"-";if(!a){var k=(y=Math.abs(y))%60;w+=l(Math.floor(y/60))+":"+l(k);}e=e.replace(/(^|[^\\])K/g,"$1"+w);var A=(a?t.getUTCDay():t.getDay())+1;return e=(e=(e=(e=(e=e.replace(new RegExp(n[0],"g"),n[A])).replace(new RegExp(o[0],"g"),o[A])).replace(new RegExp(s[0],"g"),s[c])).replace(new RegExp(r[0],"g"),r[c])).replace(/\\(.)/g,"$1")}},{key:"getTimeUnitsfromTimestamp",value:function(t,e,i){var a=this.w;void 0!==a.config.xaxis.min&&(t=a.config.xaxis.min),void 0!==a.config.xaxis.max&&(e=a.config.xaxis.max);var s=this.getDate(t),r=this.getDate(e),n=this.formatDate(s,"yyyy MM dd HH mm").split(" "),o=this.formatDate(r,"yyyy MM dd HH mm").split(" ");return {minMinute:parseInt(n[4],10),maxMinute:parseInt(o[4],10),minHour:parseInt(n[3],10),maxHour:parseInt(o[3],10),minDate:parseInt(n[2],10),maxDate:parseInt(o[2],10),minMonth:parseInt(n[1],10)-1,maxMonth:parseInt(o[1],10)-1,minYear:parseInt(n[0],10),maxYear:parseInt(o[0],10)}}},{key:"isLeapYear",value:function(t){return t%4==0&&t%100!=0||t%400==0}},{key:"calculcateLastDaysOfMonth",value:function(t,e,i){return this.determineDaysOfMonths(t,e)-i}},{key:"determineDaysOfYear",value:function(t){var e=365;return this.isLeapYear(t)&&(e=366),e}},{key:"determineRemainingDaysOfYear",value:function(t,e,i){var a=this.daysCntOfYear[e]+i;return e>1&&this.isLeapYear()&&a++,a}},{key:"determineDaysOfMonths",value:function(t,e){var i=30;switch(t=f.monthMod(t),!0){case this.months30.indexOf(t)>-1:2===t&&(i=this.isLeapYear(e)?29:28);break;case this.months31.indexOf(t)>-1:default:i=31;}return i}}]),t}(),w=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"getStackedSeriesTotals",value:function(){var t=this.w,e=[];if(0===t.globals.series.length)return e;for(var i=0;i<t.globals.series[t.globals.maxValsInArrayIndex].length;i++){for(var a=0,s=0;s<t.globals.series.length;s++)void 0!==t.globals.series[s][i]&&(a+=t.globals.series[s][i]);e.push(a);}return t.globals.stackedSeriesTotals=e,e}},{key:"getSeriesTotalByIndex",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:null;return null===t?this.w.config.series.reduce((function(t,e){return t+e}),0):this.w.globals.series[t].reduce((function(t,e){return t+e}),0)}},{key:"isSeriesNull",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:null;return 0===(null===t?this.w.config.series.filter((function(t){return null!==t})):this.w.config.series[t].data.filter((function(t){return null!==t}))).length}},{key:"seriesHaveSameValues",value:function(t){return this.w.globals.series[t].every((function(t,e,i){return t===i[0]}))}},{key:"getCategoryLabels",value:function(t){var e=this,i=this.w,a=t.slice();return i.config.xaxis.convertedCatToNumeric&&(a=t.map((function(t,a){return i.config.xaxis.labels.formatter(t-i.globals.minX+1,{i:a,dateFormatter:new y(e.ctx).formatDate,w:i})}))),a}},{key:"getLargestSeries",value:function(){var t=this.w;t.globals.maxValsInArrayIndex=t.globals.series.map((function(t){return t.length})).indexOf(Math.max.apply(Math,t.globals.series.map((function(t){return t.length}))));}},{key:"getLargestMarkerSize",value:function(){var t=this.w,e=0;return t.globals.markers.size.forEach((function(t){e=Math.max(e,t);})),t.globals.markers.largestSize=e,e}},{key:"getSeriesTotals",value:function(){var t=this.w;t.globals.seriesTotals=t.globals.series.map((function(t,e){var i=0;if(Array.isArray(t))for(var a=0;a<t.length;a++)i+=t[a];else i+=t;return i}));}},{key:"getSeriesTotalsXRange",value:function(t,e){var i=this.w;return i.globals.series.map((function(a,s){for(var r=0,n=0;n<a.length;n++)i.globals.seriesX[s][n]>t&&i.globals.seriesX[s][n]<e&&(r+=a[n]);return r}))}},{key:"getPercentSeries",value:function(){var t=this.w;t.globals.seriesPercent=t.globals.series.map((function(e,i){var a=[];if(Array.isArray(e))for(var s=0;s<e.length;s++){var r=t.globals.stackedSeriesTotals[s],n=0;r&&(n=100*e[s]/r),a.push(n);}else {var o=100*e/t.globals.seriesTotals.reduce((function(t,e){return t+e}),0);a.push(o);}return a}));}},{key:"getCalculatedRatios",value:function(){var t,e,i,a,s=this.w.globals,r=[],n=0,o=[],l=.1,h=0;if(s.yRange=[],s.isMultipleYAxis)for(var c=0;c<s.minYArr.length;c++)s.yRange.push(Math.abs(s.minYArr[c]-s.maxYArr[c])),o.push(0);else s.yRange.push(Math.abs(s.minY-s.maxY));s.xRange=Math.abs(s.maxX-s.minX),s.zRange=Math.abs(s.maxZ-s.minZ);for(var d=0;d<s.yRange.length;d++)r.push(s.yRange[d]/s.gridHeight);if(e=s.xRange/s.gridWidth,i=Math.abs(s.initialMaxX-s.initialMinX)/s.gridWidth,t=s.yRange/s.gridWidth,a=s.xRange/s.gridHeight,(n=s.zRange/s.gridHeight*16)||(n=1),s.minY!==Number.MIN_VALUE&&0!==Math.abs(s.minY)&&(s.hasNegs=!0),s.isMultipleYAxis){o=[];for(var g=0;g<r.length;g++)o.push(-s.minYArr[g]/r[g]);}else o.push(-s.minY/r[0]),s.minY!==Number.MIN_VALUE&&0!==Math.abs(s.minY)&&(l=-s.minY/t,h=s.minX/e);return {yRatio:r,invertedYRatio:t,zRatio:n,xRatio:e,initialXRatio:i,invertedXRatio:a,baseLineInvertedY:l,baseLineY:o,baseLineX:h}}},{key:"getLogSeries",value:function(t){var e=this,i=this.w;return i.globals.seriesLog=t.map((function(t,a){return i.config.yaxis[a]&&i.config.yaxis[a].logarithmic?t.map((function(t){return null===t?null:e.getLogVal(t,a)})):t})),i.globals.invalidLogScale?t:i.globals.seriesLog}},{key:"getLogVal",value:function(t,e){var i=this.w;return (Math.log(t)-Math.log(i.globals.minYArr[e]))/(Math.log(i.globals.maxYArr[e])-Math.log(i.globals.minYArr[e]))}},{key:"getLogYRatios",value:function(t){var e=this,i=this.w,a=this.w.globals;return a.yLogRatio=t.slice(),a.logYRange=a.yRange.map((function(t,s){if(i.config.yaxis[s]&&e.w.config.yaxis[s].logarithmic){var r,n=-Number.MAX_VALUE,o=Number.MIN_VALUE;return a.seriesLog.forEach((function(t,e){t.forEach((function(t){i.config.yaxis[e]&&i.config.yaxis[e].logarithmic&&(n=Math.max(t,n),o=Math.min(t,o));}));})),r=Math.pow(a.yRange[s],Math.abs(o-n)/a.yRange[s]),a.yLogRatio[s]=r/a.gridHeight,r}})),a.invalidLogScale?t.slice():a.yLogRatio}}],[{key:"checkComboSeries",value:function(t){var e=!1,i=0;return t.length&&void 0!==t[0].type&&(e=!0,t.forEach((function(t){"bar"!==t.type&&"column"!==t.type&&"candlestick"!==t.type||i++;}))),{comboBarCount:i,comboCharts:e}}},{key:"extendArrayProps",value:function(t,e,i){return e.yaxis&&(e=t.extendYAxis(e,i)),e.annotations&&(e.annotations.yaxis&&(e=t.extendYAxisAnnotations(e)),e.annotations.xaxis&&(e=t.extendXAxisAnnotations(e)),e.annotations.points&&(e=t.extendPointAnnotations(e))),e}}]),t}(),k=function(){function t(i){e(this,t),this.w=i.w,this.annoCtx=i;}return a(t,[{key:"addYaxisAnnotation",value:function(t,e,i){var a,s=this.w,r=t.strokeDashArray,n=this._getY1Y2("y1",t),o=t.label.text;if(null===t.y2||void 0===t.y2){var l=this.annoCtx.graphics.drawLine(0+t.offsetX,n+t.offsetY,s.globals.gridWidth+t.offsetX,n+t.offsetY,t.borderColor,r,t.borderWidth);e.appendChild(l.node),t.id&&l.node.classList.add(t.id);}else {if((a=this._getY1Y2("y2",t))>n){var h=n;n=a,a=h;}var c=this.annoCtx.graphics.drawRect(0+t.offsetX,a+t.offsetY,s.globals.gridWidth+t.offsetX,n-a,0,t.fillColor,t.opacity,1,t.borderColor,r);c.node.classList.add("apexcharts-annotation-rect"),c.attr("clip-path","url(#gridRectMask".concat(s.globals.cuid,")")),e.appendChild(c.node),t.id&&c.node.classList.add(t.id);}var d="right"===t.label.position?s.globals.gridWidth:0,g=this.annoCtx.graphics.drawText({x:d+t.label.offsetX,y:(a||n)+t.label.offsetY-3,text:o,textAnchor:t.label.textAnchor,fontSize:t.label.style.fontSize,fontFamily:t.label.style.fontFamily,fontWeight:t.label.style.fontWeight,foreColor:t.label.style.color,cssClass:"apexcharts-yaxis-annotation-label ".concat(t.label.style.cssClass," ").concat(t.id?t.id:"")});g.attr({rel:i}),e.appendChild(g.node);}},{key:"_getY1Y2",value:function(t,e){var i,a="y1"===t?e.y:e.y2,s=this.w;if(this.annoCtx.invertAxis){var r=s.globals.labels.indexOf(a);s.config.xaxis.convertedCatToNumeric&&(r=s.globals.categoryLabels.indexOf(a));var n=s.globals.dom.baseEl.querySelector(".apexcharts-yaxis-texts-g text:nth-child("+(r+1)+")");n&&(i=parseFloat(n.getAttribute("y")));}else {var o;if(s.config.yaxis[e.yAxisIndex].logarithmic)o=(a=new w(this.annoCtx.ctx).getLogVal(a,e.yAxisIndex))/s.globals.yLogRatio[e.yAxisIndex];else o=(a-s.globals.minYArr[e.yAxisIndex])/(s.globals.yRange[e.yAxisIndex]/s.globals.gridHeight);i=s.globals.gridHeight-o,s.config.yaxis[e.yAxisIndex]&&s.config.yaxis[e.yAxisIndex].reversed&&(i=o);}return i}},{key:"drawYAxisAnnotations",value:function(){var t=this,e=this.w,i=this.annoCtx.graphics.group({class:"apexcharts-yaxis-annotations"});return e.config.annotations.yaxis.map((function(e,a){t.addYaxisAnnotation(e,i.node,a);})),i}}]),t}(),A=function(){function t(i){e(this,t),this.w=i.w,this.annoCtx=i;}return a(t,[{key:"addPointAnnotation",value:function(t,e,i){var a=this.w,s=0,r=0,n=0;this.annoCtx.invertAxis&&console.warn("Point annotation is not supported in horizontal bar charts.");var o,l=parseFloat(t.y);if("string"==typeof t.x){var h=a.globals.labels.indexOf(t.x);a.config.xaxis.convertedCatToNumeric&&(h=a.globals.categoryLabels.indexOf(t.x)),s=this.annoCtx.helpers.getStringX(t.x),null===t.y&&(l=a.globals.series[t.seriesIndex][h]);}else s=(t.x-a.globals.minX)/(a.globals.xRange/a.globals.gridWidth);a.config.yaxis[t.yAxisIndex].logarithmic?o=(l=new w(this.annoCtx.ctx).getLogVal(l,t.yAxisIndex))/a.globals.yLogRatio[t.yAxisIndex]:o=(l-a.globals.minYArr[t.yAxisIndex])/(a.globals.yRange[t.yAxisIndex]/a.globals.gridHeight);if(r=a.globals.gridHeight-o-parseFloat(t.label.style.fontSize)-t.marker.size,n=a.globals.gridHeight-o,a.config.yaxis[t.yAxisIndex]&&a.config.yaxis[t.yAxisIndex].reversed&&(r=o+parseFloat(t.label.style.fontSize)+t.marker.size,n=o),f.isNumber(s)){var c={pSize:t.marker.size,pWidth:t.marker.strokeWidth,pointFillColor:t.marker.fillColor,pointStrokeColor:t.marker.strokeColor,shape:t.marker.shape,pRadius:t.marker.radius,class:"apexcharts-point-annotation-marker ".concat(t.marker.cssClass," ").concat(t.id?t.id:"")},d=this.annoCtx.graphics.drawMarker(s+t.marker.offsetX,n+t.marker.offsetY,c);e.appendChild(d.node);var g=t.label.text?t.label.text:"",u=this.annoCtx.graphics.drawText({x:s+t.label.offsetX,y:r+t.label.offsetY,text:g,textAnchor:t.label.textAnchor,fontSize:t.label.style.fontSize,fontFamily:t.label.style.fontFamily,fontWeight:t.label.style.fontWeight,foreColor:t.label.style.color,cssClass:"apexcharts-point-annotation-label ".concat(t.label.style.cssClass," ").concat(t.id?t.id:"")});if(u.attr({rel:i}),e.appendChild(u.node),t.customSVG.SVG){var p=this.annoCtx.graphics.group({class:"apexcharts-point-annotations-custom-svg "+t.customSVG.cssClass});p.attr({transform:"translate(".concat(s+t.customSVG.offsetX,", ").concat(r+t.customSVG.offsetY,")")}),p.node.innerHTML=t.customSVG.SVG,e.appendChild(p.node);}if(t.image.path){var x=t.image.width?t.image.width:20,b=t.image.height?t.image.height:20;this.annoCtx.addImage({x:s+t.image.offsetX-x/2,y:r+t.image.offsetY-b/2,width:x,height:b,path:t.image.path,appendTo:".apexcharts-point-annotations"});}}}},{key:"drawPointAnnotations",value:function(){var t=this,e=this.w,i=this.annoCtx.graphics.group({class:"apexcharts-point-annotations"});return e.config.annotations.points.map((function(e,a){t.addPointAnnotation(e,i.node,a);})),i}}]),t}();var S={name:"en",options:{months:["January","February","March","April","May","June","July","August","September","October","November","December"],shortMonths:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],days:["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],shortDays:["Sun","Mon","Tue","Wed","Thu","Fri","Sat"],toolbar:{exportToSVG:"Download SVG",exportToPNG:"Download PNG",exportToCSV:"Download CSV",menu:"Menu",selection:"Selection",selectionZoom:"Selection Zoom",zoomIn:"Zoom In",zoomOut:"Zoom Out",pan:"Panning",reset:"Reset Zoom"}}},C=function(){function t(){e(this,t),this.yAxis={show:!0,showAlways:!1,showForNullSeries:!0,seriesName:void 0,opposite:!1,reversed:!1,logarithmic:!1,tickAmount:void 0,forceNiceScale:!1,max:void 0,min:void 0,floating:!1,decimalsInFloat:void 0,labels:{show:!0,minWidth:0,maxWidth:160,offsetX:0,offsetY:0,align:void 0,rotate:0,padding:20,style:{colors:[],fontSize:"11px",fontWeight:400,fontFamily:void 0,cssClass:""},formatter:void 0},axisBorder:{show:!1,color:"#e0e0e0",width:1,offsetX:0,offsetY:0},axisTicks:{show:!1,color:"#e0e0e0",width:6,offsetX:0,offsetY:0},title:{text:void 0,rotate:-90,offsetY:0,offsetX:0,style:{color:void 0,fontSize:"11px",fontWeight:900,fontFamily:void 0,cssClass:""}},tooltip:{enabled:!1,offsetX:0},crosshairs:{show:!0,position:"front",stroke:{color:"#b6b6b6",width:1,dashArray:0}}},this.pointAnnotation={x:0,y:null,yAxisIndex:0,seriesIndex:0,marker:{size:4,fillColor:"#fff",strokeWidth:2,strokeColor:"#333",shape:"circle",offsetX:0,offsetY:0,radius:2,cssClass:""},label:{borderColor:"#c2c2c2",borderWidth:1,borderRadius:2,text:void 0,textAnchor:"middle",offsetX:0,offsetY:0,style:{background:"#fff",color:void 0,fontSize:"11px",fontFamily:void 0,fontWeight:400,cssClass:"",padding:{left:5,right:5,top:2,bottom:2}}},customSVG:{SVG:void 0,cssClass:void 0,offsetX:0,offsetY:0},image:{path:void 0,width:20,height:20,offsetX:0,offsetY:0}},this.yAxisAnnotation={y:0,y2:null,strokeDashArray:1,fillColor:"#c2c2c2",borderColor:"#c2c2c2",borderWidth:1,opacity:.3,offsetX:0,offsetY:0,yAxisIndex:0,label:{borderColor:"#c2c2c2",borderWidth:1,borderRadius:2,text:void 0,textAnchor:"end",position:"right",offsetX:0,offsetY:-3,style:{background:"#fff",color:void 0,fontSize:"11px",fontFamily:void 0,fontWeight:400,cssClass:"",padding:{left:5,right:5,top:2,bottom:2}}}},this.xAxisAnnotation={x:0,x2:null,strokeDashArray:1,fillColor:"#c2c2c2",borderColor:"#c2c2c2",borderWidth:1,opacity:.3,offsetX:0,offsetY:0,label:{borderColor:"#c2c2c2",borderWidth:1,borderRadius:2,text:void 0,textAnchor:"middle",orientation:"vertical",position:"top",offsetX:0,offsetY:0,style:{background:"#fff",color:void 0,fontSize:"11px",fontFamily:void 0,fontWeight:400,cssClass:"",padding:{left:5,right:5,top:2,bottom:2}}}},this.text={x:0,y:0,text:"",textAnchor:"start",foreColor:void 0,fontSize:"13px",fontFamily:void 0,fontWeight:400,appendTo:".apexcharts-annotations",backgroundColor:"transparent",borderColor:"#c2c2c2",borderRadius:0,borderWidth:0,paddingLeft:4,paddingRight:4,paddingTop:2,paddingBottom:2};}return a(t,[{key:"init",value:function(){return {annotations:{position:"front",yaxis:[this.yAxisAnnotation],xaxis:[this.xAxisAnnotation],points:[this.pointAnnotation],texts:[],images:[],shapes:[]},chart:{animations:{enabled:!0,easing:"easeinout",speed:800,animateGradually:{delay:150,enabled:!0},dynamicAnimation:{enabled:!0,speed:350}},background:"transparent",locales:[S],defaultLocale:"en",dropShadow:{enabled:!1,enabledOnSeries:void 0,top:2,left:2,blur:4,color:"#000",opacity:.35},events:{animationEnd:void 0,beforeMount:void 0,mounted:void 0,updated:void 0,click:void 0,mouseMove:void 0,legendClick:void 0,markerClick:void 0,selection:void 0,dataPointSelection:void 0,dataPointMouseEnter:void 0,dataPointMouseLeave:void 0,beforeZoom:void 0,beforeResetZoom:void 0,zoomed:void 0,scrolled:void 0,brushScrolled:void 0},foreColor:"#373d3f",fontFamily:"Helvetica, Arial, sans-serif",height:"auto",parentHeightOffset:15,redrawOnParentResize:!0,id:void 0,group:void 0,offsetX:0,offsetY:0,selection:{enabled:!1,type:"x",fill:{color:"#24292e",opacity:.1},stroke:{width:1,color:"#24292e",opacity:.4,dashArray:3},xaxis:{min:void 0,max:void 0},yaxis:{min:void 0,max:void 0}},sparkline:{enabled:!1},brush:{enabled:!1,autoScaleYaxis:!0,target:void 0},stacked:!1,stackType:"normal",toolbar:{show:!0,offsetX:0,offsetY:0,tools:{download:!0,selection:!0,zoom:!0,zoomin:!0,zoomout:!0,pan:!0,reset:!0,customIcons:[]},export:{csv:{filename:void 0,columnDelimiter:",",headerCategory:"category",headerValue:"value",dateFormatter:function(t){return new Date(t).toDateString()}}},autoSelected:"zoom"},type:"line",width:"100%",zoom:{enabled:!0,type:"x",autoScaleYaxis:!1,zoomedArea:{fill:{color:"#90CAF9",opacity:.4},stroke:{color:"#0D47A1",opacity:.4,width:1}}}},plotOptions:{area:{fillTo:"origin"},bar:{horizontal:!1,columnWidth:"70%",barHeight:"70%",distributed:!1,startingShape:"flat",endingShape:"flat",rangeBarOverlap:!0,rangeBarGroupRows:!1,colors:{ranges:[],backgroundBarColors:[],backgroundBarOpacity:1,backgroundBarRadius:0},dataLabels:{position:"top",maxItems:100,hideOverflowingLabels:!0,orientation:"horizontal"}},bubble:{minBubbleRadius:void 0,maxBubbleRadius:void 0},candlestick:{colors:{upward:"#00B746",downward:"#EF403C"},wick:{useFillColor:!0}},heatmap:{radius:2,enableShades:!0,shadeIntensity:.5,reverseNegativeShade:!1,distributed:!1,useFillColorAsStroke:!1,colorScale:{inverse:!1,ranges:[],min:void 0,max:void 0}},treemap:{enableShades:!0,shadeIntensity:.5,distributed:!1,useFillColorAsStroke:!1,colorScale:{inverse:!1,ranges:[],min:void 0,max:void 0}},radialBar:{inverseOrder:!1,startAngle:0,endAngle:360,offsetX:0,offsetY:0,hollow:{margin:5,size:"50%",background:"transparent",image:void 0,imageWidth:150,imageHeight:150,imageOffsetX:0,imageOffsetY:0,imageClipped:!0,position:"front",dropShadow:{enabled:!1,top:0,left:0,blur:3,color:"#000",opacity:.5}},track:{show:!0,startAngle:void 0,endAngle:void 0,background:"#f2f2f2",strokeWidth:"97%",opacity:1,margin:5,dropShadow:{enabled:!1,top:0,left:0,blur:3,color:"#000",opacity:.5}},dataLabels:{show:!0,name:{show:!0,fontSize:"16px",fontFamily:void 0,fontWeight:600,color:void 0,offsetY:0,formatter:function(t){return t}},value:{show:!0,fontSize:"14px",fontFamily:void 0,fontWeight:400,color:void 0,offsetY:16,formatter:function(t){return t+"%"}},total:{show:!1,label:"Total",fontSize:"16px",fontWeight:600,fontFamily:void 0,color:void 0,formatter:function(t){return t.globals.seriesTotals.reduce((function(t,e){return t+e}),0)/t.globals.series.length+"%"}}}},pie:{customScale:1,offsetX:0,offsetY:0,startAngle:0,expandOnClick:!0,dataLabels:{offset:0,minAngleToShowLabel:10},donut:{size:"65%",background:"transparent",labels:{show:!1,name:{show:!0,fontSize:"16px",fontFamily:void 0,fontWeight:600,color:void 0,offsetY:-10,formatter:function(t){return t}},value:{show:!0,fontSize:"20px",fontFamily:void 0,fontWeight:400,color:void 0,offsetY:10,formatter:function(t){return t}},total:{show:!1,showAlways:!1,label:"Total",fontSize:"16px",fontWeight:400,fontFamily:void 0,color:void 0,formatter:function(t){return t.globals.seriesTotals.reduce((function(t,e){return t+e}),0)}}}}},polarArea:{rings:{strokeWidth:1,strokeColor:"#e8e8e8"}},radar:{size:void 0,offsetX:0,offsetY:0,polygons:{strokeWidth:1,strokeColors:"#e8e8e8",connectorColors:"#e8e8e8",fill:{colors:void 0}}}},colors:void 0,dataLabels:{enabled:!0,enabledOnSeries:void 0,formatter:function(t){return null!==t?t:""},textAnchor:"middle",distributed:!1,offsetX:0,offsetY:0,style:{fontSize:"12px",fontFamily:void 0,fontWeight:600,colors:void 0},background:{enabled:!0,foreColor:"#fff",borderRadius:2,padding:4,opacity:.9,borderWidth:1,borderColor:"#fff",dropShadow:{enabled:!1,top:1,left:1,blur:1,color:"#000",opacity:.45}},dropShadow:{enabled:!1,top:1,left:1,blur:1,color:"#000",opacity:.45}},fill:{type:"solid",colors:void 0,opacity:.85,gradient:{shade:"dark",type:"horizontal",shadeIntensity:.5,gradientToColors:void 0,inverseColors:!0,opacityFrom:1,opacityTo:1,stops:[0,50,100],colorStops:[]},image:{src:[],width:void 0,height:void 0},pattern:{style:"squares",width:6,height:6,strokeWidth:2}},grid:{show:!0,borderColor:"#e0e0e0",strokeDashArray:0,position:"back",xaxis:{lines:{show:!1}},yaxis:{lines:{show:!0}},row:{colors:void 0,opacity:.5},column:{colors:void 0,opacity:.5},padding:{top:0,right:10,bottom:0,left:12}},labels:[],legend:{show:!0,showForSingleSeries:!1,showForNullSeries:!0,showForZeroSeries:!0,floating:!1,position:"bottom",horizontalAlign:"center",inverseOrder:!1,fontSize:"12px",fontFamily:void 0,fontWeight:400,width:void 0,height:void 0,formatter:void 0,tooltipHoverFormatter:void 0,offsetX:-20,offsetY:4,labels:{colors:void 0,useSeriesColors:!1},markers:{width:12,height:12,strokeWidth:0,fillColors:void 0,strokeColor:"#fff",radius:12,customHTML:void 0,offsetX:0,offsetY:0,onClick:void 0},itemMargin:{horizontal:5,vertical:2},onItemClick:{toggleDataSeries:!0},onItemHover:{highlightDataSeries:!0}},markers:{discrete:[],size:0,colors:void 0,strokeColors:"#fff",strokeWidth:2,strokeOpacity:.9,strokeDashArray:0,fillOpacity:1,shape:"circle",radius:2,offsetX:0,offsetY:0,onClick:void 0,onDblClick:void 0,showNullDataPoints:!0,hover:{size:void 0,sizeOffset:3}},noData:{text:void 0,align:"center",verticalAlign:"middle",offsetX:0,offsetY:0,style:{color:void 0,fontSize:"14px",fontFamily:void 0}},responsive:[],series:void 0,states:{normal:{filter:{type:"none",value:0}},hover:{filter:{type:"lighten",value:.1}},active:{allowMultipleDataPointsSelection:!1,filter:{type:"darken",value:.5}}},title:{text:void 0,align:"left",margin:5,offsetX:0,offsetY:0,floating:!1,style:{fontSize:"14px",fontWeight:900,fontFamily:void 0,color:void 0}},subtitle:{text:void 0,align:"left",margin:5,offsetX:0,offsetY:30,floating:!1,style:{fontSize:"12px",fontWeight:400,fontFamily:void 0,color:void 0}},stroke:{show:!0,curve:"smooth",lineCap:"butt",width:2,colors:void 0,dashArray:0},tooltip:{enabled:!0,enabledOnSeries:void 0,shared:!0,followCursor:!1,intersect:!1,inverseOrder:!1,custom:void 0,fillSeriesColor:!1,theme:"light",style:{fontSize:"12px",fontFamily:void 0},onDatasetHover:{highlightDataSeries:!1},x:{show:!0,format:"dd MMM",formatter:void 0},y:{formatter:void 0,title:{formatter:function(t){return t+": "}}},z:{formatter:void 0,title:"Size: "},marker:{show:!0,fillColors:void 0},items:{display:"flex"},fixed:{enabled:!1,position:"topRight",offsetX:0,offsetY:0}},xaxis:{type:"category",categories:[],convertedCatToNumeric:!1,sorted:!1,offsetX:0,offsetY:0,labels:{show:!0,rotate:-45,rotateAlways:!1,hideOverlappingLabels:!0,trim:!1,minHeight:void 0,maxHeight:120,showDuplicates:!0,style:{colors:[],fontSize:"12px",fontWeight:400,fontFamily:void 0,cssClass:""},offsetX:0,offsetY:0,format:void 0,formatter:void 0,datetimeUTC:!0,datetimeFormatter:{year:"yyyy",month:"MMM 'yy",day:"dd MMM",hour:"HH:mm",minute:"HH:mm:ss"}},axisBorder:{show:!0,color:"#e0e0e0",width:"100%",height:1,offsetX:0,offsetY:0},axisTicks:{show:!0,color:"#e0e0e0",height:6,offsetX:0,offsetY:0},tickAmount:void 0,tickPlacement:"on",min:void 0,max:void 0,range:void 0,floating:!1,position:"bottom",title:{text:void 0,offsetX:0,offsetY:0,style:{color:void 0,fontSize:"12px",fontWeight:900,fontFamily:void 0,cssClass:""}},crosshairs:{show:!0,width:1,position:"back",opacity:.9,stroke:{color:"#b6b6b6",width:1,dashArray:3},fill:{type:"solid",color:"#B1B9C4",gradient:{colorFrom:"#D8E3F0",colorTo:"#BED1E6",stops:[0,100],opacityFrom:.4,opacityTo:.5}},dropShadow:{enabled:!1,left:0,top:0,blur:1,opacity:.4}},tooltip:{enabled:!0,offsetY:0,formatter:void 0,style:{fontSize:"12px",fontFamily:void 0}}},yaxis:this.yAxis,theme:{mode:"light",palette:"palette1",monochrome:{enabled:!1,color:"#008FFB",shadeTo:"light",shadeIntensity:.65}}}}}]),t}(),L=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.graphics=new b(this.ctx),this.w.globals.isBarHorizontal&&(this.invertAxis=!0),this.helpers=new m(this),this.xAxisAnnotations=new v(this),this.yAxisAnnotations=new k(this),this.pointsAnnotations=new A(this),this.w.globals.isBarHorizontal&&this.w.config.yaxis[0].reversed&&(this.inversedReversedAxis=!0),this.xDivision=this.w.globals.gridWidth/this.w.globals.dataPoints;}return a(t,[{key:"drawAxesAnnotations",value:function(){var t=this.w;if(t.globals.axisCharts){for(var e=this.yAxisAnnotations.drawYAxisAnnotations(),i=this.xAxisAnnotations.drawXAxisAnnotations(),a=this.pointsAnnotations.drawPointAnnotations(),s=t.config.chart.animations.enabled,r=[e,i,a],n=[i.node,e.node,a.node],o=0;o<3;o++)t.globals.dom.elGraphical.add(r[o]),!s||t.globals.resized||t.globals.dataChanged||"scatter"!==t.config.chart.type&&"bubble"!==t.config.chart.type&&t.globals.dataPoints>1&&n[o].classList.add("apexcharts-element-hidden"),t.globals.delayedElements.push({el:n[o],index:0});this.helpers.annotationsBackground();}}},{key:"drawImageAnnos",value:function(){var t=this;this.w.config.annotations.images.map((function(e,i){t.addImage(e,i);}));}},{key:"drawTextAnnos",value:function(){var t=this;this.w.config.annotations.texts.map((function(e,i){t.addText(e,i);}));}},{key:"addXaxisAnnotation",value:function(t,e,i){this.xAxisAnnotations.addXaxisAnnotation(t,e,i);}},{key:"addYaxisAnnotation",value:function(t,e,i){this.yAxisAnnotations.addYaxisAnnotation(t,e,i);}},{key:"addPointAnnotation",value:function(t,e,i){this.pointsAnnotations.addPointAnnotation(t,e,i);}},{key:"addText",value:function(t,e){var i=t.x,a=t.y,s=t.text,r=t.textAnchor,n=t.foreColor,o=t.fontSize,l=t.fontFamily,h=t.fontWeight,c=t.cssClass,d=t.backgroundColor,g=t.borderWidth,u=t.strokeDashArray,f=t.borderRadius,p=t.borderColor,x=t.appendTo,b=void 0===x?".apexcharts-annotations":x,m=t.paddingLeft,v=void 0===m?4:m,y=t.paddingRight,w=void 0===y?4:y,k=t.paddingBottom,A=void 0===k?2:k,S=t.paddingTop,C=void 0===S?2:S,L=this.w,P=this.graphics.drawText({x:i,y:a,text:s,textAnchor:r||"start",fontSize:o||"12px",fontWeight:h||"regular",fontFamily:l||L.config.chart.fontFamily,foreColor:n||L.config.chart.foreColor,cssClass:c}),T=L.globals.dom.baseEl.querySelector(b);T&&T.appendChild(P.node);var z=P.bbox();if(s){var I=this.graphics.drawRect(z.x-v,z.y-C,z.width+v+w,z.height+A+C,f,d||"transparent",1,g,p,u);T.insertBefore(I.node,P.node);}}},{key:"addImage",value:function(t,e){var i=this.w,a=t.path,s=t.x,r=void 0===s?0:s,n=t.y,o=void 0===n?0:n,l=t.width,h=void 0===l?20:l,c=t.height,d=void 0===c?20:c,g=t.appendTo,u=void 0===g?".apexcharts-annotations":g,f=i.globals.dom.Paper.image(a);f.size(h,d).move(r,o);var p=i.globals.dom.baseEl.querySelector(u);p&&p.appendChild(f.node);}},{key:"addXaxisAnnotationExternal",value:function(t,e,i){return this.addAnnotationExternal({params:t,pushToMemory:e,context:i,type:"xaxis",contextMethod:i.addXaxisAnnotation}),i}},{key:"addYaxisAnnotationExternal",value:function(t,e,i){return this.addAnnotationExternal({params:t,pushToMemory:e,context:i,type:"yaxis",contextMethod:i.addYaxisAnnotation}),i}},{key:"addPointAnnotationExternal",value:function(t,e,i){return void 0===this.invertAxis&&(this.invertAxis=i.w.globals.isBarHorizontal),this.addAnnotationExternal({params:t,pushToMemory:e,context:i,type:"point",contextMethod:i.addPointAnnotation}),i}},{key:"addAnnotationExternal",value:function(t){var e=t.params,i=t.pushToMemory,a=t.context,s=t.type,r=t.contextMethod,n=a,o=n.w,l=o.globals.dom.baseEl.querySelector(".apexcharts-".concat(s,"-annotations")),h=l.childNodes.length+1,c=new C,d=Object.assign({},"xaxis"===s?c.xAxisAnnotation:"yaxis"===s?c.yAxisAnnotation:c.pointAnnotation),g=f.extend(d,e);switch(s){case"xaxis":this.addXaxisAnnotation(g,l,h);break;case"yaxis":this.addYaxisAnnotation(g,l,h);break;case"point":this.addPointAnnotation(g,l,h);}var u=o.globals.dom.baseEl.querySelector(".apexcharts-".concat(s,"-annotations .apexcharts-").concat(s,"-annotation-label[rel='").concat(h,"']")),p=this.helpers.addBackgroundToAnno(u,g);return p&&l.insertBefore(p.node,u),i&&o.globals.memory.methodsToExec.push({context:n,id:g.id?g.id:f.randomId(),method:r,label:"addAnnotation",params:e}),a}},{key:"clearAnnotations",value:function(t){var e=t.w,i=e.globals.dom.baseEl.querySelectorAll(".apexcharts-yaxis-annotations, .apexcharts-xaxis-annotations, .apexcharts-point-annotations");e.globals.memory.methodsToExec.map((function(t,i){"addText"!==t.label&&"addAnnotation"!==t.label||e.globals.memory.methodsToExec.splice(i,1);})),i=f.listToArray(i),Array.prototype.forEach.call(i,(function(t){for(;t.firstChild;)t.removeChild(t.firstChild);}));}},{key:"removeAnnotation",value:function(t,e){var i=t.w,a=i.globals.dom.baseEl.querySelectorAll(".".concat(e));a&&(i.globals.memory.methodsToExec.map((function(t,a){t.id===e&&i.globals.memory.methodsToExec.splice(a,1);})),Array.prototype.forEach.call(a,(function(t){t.parentElement.removeChild(t);})));}}]),t}(),P=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.opts=null,this.seriesIndex=0;}return a(t,[{key:"clippedImgArea",value:function(t){var e=this.w,i=e.config,a=parseInt(e.globals.gridWidth,10),s=parseInt(e.globals.gridHeight,10),r=a>s?a:s,n=t.image,o=0,l=0;void 0===t.width&&void 0===t.height?void 0!==i.fill.image.width&&void 0!==i.fill.image.height?(o=i.fill.image.width+1,l=i.fill.image.height):(o=r+1,l=r):(o=t.width,l=t.height);var h=document.createElementNS(e.globals.SVGNS,"pattern");b.setAttrs(h,{id:t.patternID,patternUnits:t.patternUnits?t.patternUnits:"userSpaceOnUse",width:o+"px",height:l+"px"});var c=document.createElementNS(e.globals.SVGNS,"image");h.appendChild(c),c.setAttributeNS(window.SVG.xlink,"href",n),b.setAttrs(c,{x:0,y:0,preserveAspectRatio:"none",width:o+"px",height:l+"px"}),c.style.opacity=t.opacity,e.globals.dom.elDefs.node.appendChild(h);}},{key:"getSeriesIndex",value:function(t){var e=this.w;return ("bar"===e.config.chart.type||"rangeBar"===e.config.chart.type)&&e.config.plotOptions.bar.distributed||"heatmap"===e.config.chart.type||"treemap"===e.config.chart.type?this.seriesIndex=t.seriesNumber:this.seriesIndex=t.seriesNumber%e.globals.series.length,this.seriesIndex}},{key:"fillPath",value:function(t){var e=this.w;this.opts=t;var i,a,s,r=this.w.config;this.seriesIndex=this.getSeriesIndex(t);var n=this.getFillColors()[this.seriesIndex];void 0!==e.globals.seriesColors[this.seriesIndex]&&(n=e.globals.seriesColors[this.seriesIndex]),"function"==typeof n&&(n=n({seriesIndex:this.seriesIndex,dataPointIndex:t.dataPointIndex,value:t.value,w:e}));var o=this.getFillType(this.seriesIndex),l=Array.isArray(r.fill.opacity)?r.fill.opacity[this.seriesIndex]:r.fill.opacity;t.color&&(n=t.color);var h=n;if(-1===n.indexOf("rgb")?n.length<9&&(h=f.hexToRgba(n,l)):n.indexOf("rgba")>-1&&(l=f.getOpacityFromRGBA(n)),t.opacity&&(l=t.opacity),"pattern"===o&&(a=this.handlePatternFill(a,n,l,h)),"gradient"===o&&(s=this.handleGradientFill(n,l,this.seriesIndex)),"image"===o){var c=r.fill.image.src,d=t.patternID?t.patternID:"";this.clippedImgArea({opacity:l,image:Array.isArray(c)?t.seriesNumber<c.length?c[t.seriesNumber]:c[0]:c,width:t.width?t.width:void 0,height:t.height?t.height:void 0,patternUnits:t.patternUnits,patternID:"pattern".concat(e.globals.cuid).concat(t.seriesNumber+1).concat(d)}),i="url(#pattern".concat(e.globals.cuid).concat(t.seriesNumber+1).concat(d,")");}else i="gradient"===o?s:"pattern"===o?a:h;return t.solid&&(i=h),i}},{key:"getFillType",value:function(t){var e=this.w;return Array.isArray(e.config.fill.type)?e.config.fill.type[t]:e.config.fill.type}},{key:"getFillColors",value:function(){var t=this.w,e=t.config,i=this.opts,a=[];return t.globals.comboCharts?"line"===t.config.series[this.seriesIndex].type?Array.isArray(t.globals.stroke.colors)?a=t.globals.stroke.colors:a.push(t.globals.stroke.colors):Array.isArray(t.globals.fill.colors)?a=t.globals.fill.colors:a.push(t.globals.fill.colors):"line"===e.chart.type?Array.isArray(t.globals.stroke.colors)?a=t.globals.stroke.colors:a.push(t.globals.stroke.colors):Array.isArray(t.globals.fill.colors)?a=t.globals.fill.colors:a.push(t.globals.fill.colors),void 0!==i.fillColors&&(a=[],Array.isArray(i.fillColors)?a=i.fillColors.slice():a.push(i.fillColors)),a}},{key:"handlePatternFill",value:function(t,e,i,a){var s=this.w.config,r=this.opts,n=new b(this.ctx),o=void 0===s.fill.pattern.strokeWidth?Array.isArray(s.stroke.width)?s.stroke.width[this.seriesIndex]:s.stroke.width:Array.isArray(s.fill.pattern.strokeWidth)?s.fill.pattern.strokeWidth[this.seriesIndex]:s.fill.pattern.strokeWidth,l=e;Array.isArray(s.fill.pattern.style)?t=void 0!==s.fill.pattern.style[r.seriesNumber]?n.drawPattern(s.fill.pattern.style[r.seriesNumber],s.fill.pattern.width,s.fill.pattern.height,l,o,i):a:t=n.drawPattern(s.fill.pattern.style,s.fill.pattern.width,s.fill.pattern.height,l,o,i);return t}},{key:"handleGradientFill",value:function(t,e,i){var a,s=this.w.config,r=this.opts,n=new b(this.ctx),o=new f,l=s.fill.gradient.type,h=t,c=void 0===s.fill.gradient.opacityFrom?e:Array.isArray(s.fill.gradient.opacityFrom)?s.fill.gradient.opacityFrom[i]:s.fill.gradient.opacityFrom;h.indexOf("rgba")>-1&&(c=f.getOpacityFromRGBA(h));var d=void 0===s.fill.gradient.opacityTo?e:Array.isArray(s.fill.gradient.opacityTo)?s.fill.gradient.opacityTo[i]:s.fill.gradient.opacityTo;if(void 0===s.fill.gradient.gradientToColors||0===s.fill.gradient.gradientToColors.length)a="dark"===s.fill.gradient.shade?o.shadeColor(-1*parseFloat(s.fill.gradient.shadeIntensity),t.indexOf("rgb")>-1?f.rgb2hex(t):t):o.shadeColor(parseFloat(s.fill.gradient.shadeIntensity),t.indexOf("rgb")>-1?f.rgb2hex(t):t);else {var g=s.fill.gradient.gradientToColors[r.seriesNumber];a=g,g.indexOf("rgba")>-1&&(d=f.getOpacityFromRGBA(g));}if(s.fill.gradient.inverseColors){var u=h;h=a,a=u;}return h.indexOf("rgb")>-1&&(h=f.rgb2hex(h)),a.indexOf("rgb")>-1&&(a=f.rgb2hex(a)),n.drawGradient(l,h,a,c,d,r.size,s.fill.gradient.stops,s.fill.gradient.colorStops,i)}}]),t}(),T=function(){function t(i,a){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"setGlobalMarkerSize",value:function(){var t=this.w;if(t.globals.markers.size=Array.isArray(t.config.markers.size)?t.config.markers.size:[t.config.markers.size],t.globals.markers.size.length>0){if(t.globals.markers.size.length<t.globals.series.length+1)for(var e=0;e<=t.globals.series.length;e++)void 0===t.globals.markers.size[e]&&t.globals.markers.size.push(t.globals.markers.size[0]);}else t.globals.markers.size=t.config.series.map((function(e){return t.config.markers.size}));}},{key:"plotChartMarkers",value:function(t,e,i,a){var s,r=arguments.length>4&&void 0!==arguments[4]&&arguments[4],n=this.w,o=e,l=t,h=null,c=new b(this.ctx);if((n.globals.markers.size[e]>0||r)&&(h=c.group({class:r?"":"apexcharts-series-markers"})).attr("clip-path","url(#gridRectMarkerMask".concat(n.globals.cuid,")")),Array.isArray(l.x))for(var d=0;d<l.x.length;d++){var g=i;1===i&&0===d&&(g=0),1===i&&1===d&&(g=1);var u="apexcharts-marker";"line"!==n.config.chart.type&&"area"!==n.config.chart.type||n.globals.comboCharts||n.config.tooltip.intersect||(u+=" no-pointer-events");var x=Array.isArray(n.config.markers.size)?n.globals.markers.size[e]>0:n.config.markers.size>0;if(x||r){f.isNumber(l.y[d])?u+=" w".concat(f.randomId()):u="apexcharts-nullpoint";var m=this.getMarkerConfig(u,e,g);n.config.series[o].data[g]&&(n.config.series[o].data[g].fillColor&&(m.pointFillColor=n.config.series[o].data[g].fillColor),n.config.series[o].data[g].strokeColor&&(m.pointStrokeColor=n.config.series[o].data[g].strokeColor)),a&&(m.pSize=a),(s=c.drawMarker(l.x[d],l.y[d],m)).attr("rel",g),s.attr("j",g),s.attr("index",e),s.node.setAttribute("default-marker-size",m.pSize);var v=new p(this.ctx);v.setSelectionFilter(s,e,g),this.addEvents(s),h&&h.add(s);}else void 0===n.globals.pointsArray[e]&&(n.globals.pointsArray[e]=[]),n.globals.pointsArray[e].push([l.x[d],l.y[d]]);}return h}},{key:"getMarkerConfig",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:null,a=this.w,s=this.getMarkerStyle(e),r=a.globals.markers.size[e],n=a.config.markers;return null!==i&&n.discrete.length&&n.discrete.map((function(t){t.seriesIndex===e&&t.dataPointIndex===i&&(s.pointStrokeColor=t.strokeColor,s.pointFillColor=t.fillColor,r=t.size);})),{pSize:r,pRadius:n.radius,pWidth:Array.isArray(n.strokeWidth)?n.strokeWidth[e]:n.strokeWidth,pointStrokeColor:s.pointStrokeColor,pointFillColor:s.pointFillColor,shape:Array.isArray(n.shape)?n.shape[e]:n.shape,class:t,pointStrokeOpacity:Array.isArray(n.strokeOpacity)?n.strokeOpacity[e]:n.strokeOpacity,pointStrokeDashArray:Array.isArray(n.strokeDashArray)?n.strokeDashArray[e]:n.strokeDashArray,pointFillOpacity:Array.isArray(n.fillOpacity)?n.fillOpacity[e]:n.fillOpacity,seriesIndex:e}}},{key:"addEvents",value:function(t){var e=this.w,i=new b(this.ctx);t.node.addEventListener("mouseenter",i.pathMouseEnter.bind(this.ctx,t)),t.node.addEventListener("mouseleave",i.pathMouseLeave.bind(this.ctx,t)),t.node.addEventListener("mousedown",i.pathMouseDown.bind(this.ctx,t)),t.node.addEventListener("click",e.config.markers.onClick),t.node.addEventListener("dblclick",e.config.markers.onDblClick),t.node.addEventListener("touchstart",i.pathMouseDown.bind(this.ctx,t),{passive:!0});}},{key:"getMarkerStyle",value:function(t){var e=this.w,i=e.globals.markers.colors,a=e.config.markers.strokeColor||e.config.markers.strokeColors;return {pointStrokeColor:Array.isArray(a)?a[t]:a,pointFillColor:Array.isArray(i)?i[t]:i}}}]),t}(),z=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.initialAnim=this.w.config.chart.animations.enabled,this.dynamicAnim=this.initialAnim&&this.w.config.chart.animations.dynamicAnimation.enabled;}return a(t,[{key:"draw",value:function(t,e,i){var a=this.w,s=new b(this.ctx),r=i.realIndex,n=i.pointsPos,o=i.zRatio,l=i.elParent,h=s.group({class:"apexcharts-series-markers apexcharts-series-".concat(a.config.chart.type)});if(h.attr("clip-path","url(#gridRectMarkerMask".concat(a.globals.cuid,")")),Array.isArray(n.x))for(var c=0;c<n.x.length;c++){var d=e+1,g=!0;0===e&&0===c&&(d=0),0===e&&1===c&&(d=1);var u=0,f=a.globals.markers.size[r];if(o!==1/0){f=a.globals.seriesZ[r][d]/o;var p=a.config.plotOptions.bubble;p.minBubbleRadius&&f<p.minBubbleRadius&&(f=p.minBubbleRadius),p.maxBubbleRadius&&f>p.maxBubbleRadius&&(f=p.maxBubbleRadius);}a.config.chart.animations.enabled||(u=f);var x=n.x[c],m=n.y[c];if(u=u||0,null!==m&&void 0!==a.globals.series[r][d]||(g=!1),g){var v=this.drawPoint(x,m,u,f,r,d,e);h.add(v);}l.add(h);}}},{key:"drawPoint",value:function(t,e,i,a,s,r,n){var o=this.w,l=s,h=new x(this.ctx),c=new p(this.ctx),d=new P(this.ctx),g=new T(this.ctx),u=new b(this.ctx),f=g.getMarkerConfig("apexcharts-marker",l),m=d.fillPath({seriesNumber:s,dataPointIndex:r,patternUnits:"objectBoundingBox",value:o.globals.series[s][n]}),v=u.drawCircle(i);if(o.config.series[l].data[r]&&o.config.series[l].data[r].fillColor&&(m=o.config.series[l].data[r].fillColor),v.attr({cx:t,cy:e,fill:m,stroke:f.pointStrokeColor,r:a,"stroke-width":f.pWidth,"stroke-dasharray":f.pointStrokeDashArray,"stroke-opacity":f.pointStrokeOpacity}),o.config.chart.dropShadow.enabled){var y=o.config.chart.dropShadow;c.dropShadow(v,y,s);}if(this.initialAnim&&!o.globals.dataChanged&&!o.globals.resized){var w=o.config.chart.animations.speed;h.animateCircleRadius(v,0,a,w,o.globals.easing,(function(){window.setTimeout((function(){h.animationCompleted(v);}),100);}));}if(o.globals.dataChanged)if(this.dynamicAnim){var k,A,S,C,L=o.config.chart.animations.dynamicAnimation.speed;null!=(C=o.globals.previousPaths[s]&&o.globals.previousPaths[s][n])&&(k=C.x,A=C.y,S=void 0!==C.r?C.r:a);for(var z=0;z<o.globals.collapsedSeries.length;z++)o.globals.collapsedSeries[z].index===s&&(L=1,a=0);0===t&&0===e&&(a=0),h.animateCircle(v,{cx:k,cy:A,r:S},{cx:t,cy:e,r:a},L,o.globals.easing);}else v.attr({r:a});return v.attr({rel:r,j:r,index:s,"default-marker-size":a}),c.setSelectionFilter(v,s,r),g.addEvents(v),v.node.classList.add("apexcharts-marker"),v}},{key:"centerTextInBubble",value:function(t){var e=this.w;return {y:t+=parseInt(e.config.dataLabels.style.fontSize,10)/4}}}]),t}(),I=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"dataLabelsCorrection",value:function(t,e,i,a,s,r,n){var o=this.w,l=!1,h=new b(this.ctx).getTextRects(i,n),c=h.width,d=h.height;void 0===o.globals.dataLabelsRects[a]&&(o.globals.dataLabelsRects[a]=[]),o.globals.dataLabelsRects[a].push({x:t,y:e,width:c,height:d});var g=o.globals.dataLabelsRects[a].length-2,u=void 0!==o.globals.lastDrawnDataLabelsIndexes[a]?o.globals.lastDrawnDataLabelsIndexes[a][o.globals.lastDrawnDataLabelsIndexes[a].length-1]:0;if(void 0!==o.globals.dataLabelsRects[a][g]){var f=o.globals.dataLabelsRects[a][u];(t>f.x+f.width+2||e>f.y+f.height+2||t+c<f.x)&&(l=!0);}return (0===s||r)&&(l=!0),{x:t,y:e,textRects:h,drawnextLabel:l}}},{key:"drawDataLabel",value:function(t,e,i){var a=this,s=arguments.length>4&&void 0!==arguments[4]?arguments[4]:2,r=this.w,n=new b(this.ctx),o=r.config.dataLabels,l=0,h=0,c=i,d=null;if(!o.enabled||!Array.isArray(t.x))return d;d=n.group({class:"apexcharts-data-labels"});for(var g=0;g<t.x.length;g++)if(l=t.x[g]+o.offsetX,h=t.y[g]+o.offsetY+s,!isNaN(l)){1===i&&0===g&&(c=0),1===i&&1===g&&(c=1);var u=r.globals.series[e][c],f="",p=function(t){return r.config.dataLabels.formatter(t,{ctx:a.ctx,seriesIndex:e,dataPointIndex:c,w:r})};if("bubble"===r.config.chart.type){f=p(u=r.globals.seriesZ[e][c]),h=t.y[g];var x=new z(this.ctx),m=x.centerTextInBubble(h,e,c);h=m.y;}else void 0!==u&&(f=p(u));this.plotDataLabelsText({x:l,y:h,text:f,i:e,j:c,parent:d,offsetCorrection:!0,dataLabelsConfig:r.config.dataLabels});}return d}},{key:"plotDataLabelsText",value:function(t){var e=this.w,i=new b(this.ctx),a=t.x,s=t.y,r=t.i,n=t.j,o=t.text,l=t.textAnchor,h=t.fontSize,c=t.parent,d=t.dataLabelsConfig,g=t.color,u=t.alwaysDrawDataLabel,f=t.offsetCorrection;if(!(Array.isArray(e.config.dataLabels.enabledOnSeries)&&e.config.dataLabels.enabledOnSeries.indexOf(r)<0)){var x={x:a,y:s,drawnextLabel:!0};f&&(x=this.dataLabelsCorrection(a,s,o,r,n,u,parseInt(d.style.fontSize,10))),e.globals.zoomed||(a=x.x,s=x.y),x.textRects&&(a+x.textRects.width<-20||a>e.globals.gridWidth+20)&&(o="");var m=e.globals.dataLabels.style.colors[r];(("bar"===e.config.chart.type||"rangeBar"===e.config.chart.type)&&e.config.plotOptions.bar.distributed||e.config.dataLabels.distributed)&&(m=e.globals.dataLabels.style.colors[n]),g&&(m=g);var v=d.offsetX,y=d.offsetY;if("bar"!==e.config.chart.type&&"rangeBar"!==e.config.chart.type||(v=0,y=0),x.drawnextLabel){var w=i.drawText({width:100,height:parseInt(d.style.fontSize,10),x:a+v,y:s+y,foreColor:m,textAnchor:l||d.textAnchor,text:o,fontSize:h||d.style.fontSize,fontFamily:d.style.fontFamily,fontWeight:d.style.fontWeight||"normal"});if(w.attr({class:"apexcharts-datalabel",cx:a,cy:s}),d.dropShadow.enabled){var k=d.dropShadow;new p(this.ctx).dropShadow(w,k);}c.add(w),void 0===e.globals.lastDrawnDataLabelsIndexes[r]&&(e.globals.lastDrawnDataLabelsIndexes[r]=[]),e.globals.lastDrawnDataLabelsIndexes[r].push(n);}}}},{key:"addBackgroundToDataLabel",value:function(t,e){var i=this.w,a=i.config.dataLabels.background,s=a.padding,r=a.padding/2,n=e.width,o=e.height,l=new b(this.ctx).drawRect(e.x-s,e.y-r/2,n+2*s,o+r,a.borderRadius,"transparent"===i.config.chart.background?"#fff":i.config.chart.background,a.opacity,a.borderWidth,a.borderColor);a.dropShadow.enabled&&new p(this.ctx).dropShadow(l,a.dropShadow);return l}},{key:"dataLabelsBackground",value:function(){var t=this.w;if("bubble"!==t.config.chart.type)for(var e=t.globals.dom.baseEl.querySelectorAll(".apexcharts-datalabels text"),i=0;i<e.length;i++){var a=e[i],s=a.getBBox(),r=null;if(s.width&&s.height&&(r=this.addBackgroundToDataLabel(a,s)),r){a.parentNode.insertBefore(r.node,a);var n=a.getAttribute("fill");t.config.chart.animations.enabled&&!t.globals.resized&&!t.globals.dataChanged?r.animate().attr({fill:n}):r.attr({fill:n}),a.setAttribute("fill",t.config.dataLabels.background.foreColor);}}}},{key:"bringForward",value:function(){for(var t=this.w,e=t.globals.dom.baseEl.querySelectorAll(".apexcharts-datalabels"),i=t.globals.dom.baseEl.querySelector(".apexcharts-plot-series:last-child"),a=0;a<e.length;a++)i&&i.insertBefore(e[a],i.nextSibling);}}]),t}(),M=function(){function t(i){e(this,t),this.w=i.w,this.barCtx=i;}return a(t,[{key:"handleBarDataLabels",value:function(t){var e=t.x,i=t.y,a=t.y1,s=t.y2,r=t.i,n=t.j,o=t.realIndex,l=t.series,h=t.barHeight,c=t.barWidth,d=t.barYPosition,g=t.visibleSeries,u=t.renderedPath,f=this.w,p=new b(this.barCtx.ctx),x=Array.isArray(this.barCtx.strokeWidth)?this.barCtx.strokeWidth[o]:this.barCtx.strokeWidth,m=e+parseFloat(c*g),v=i+parseFloat(h*g);f.globals.isXNumeric&&!f.globals.isBarHorizontal&&(m=e+parseFloat(c*(g+1)),v=i+parseFloat(h*(g+1))-x);var y=e,w=i,k={},A=f.config.dataLabels,S=this.barCtx.barOptions.dataLabels;void 0!==d&&this.barCtx.isTimelineBar&&(v=d,w=d);var C=A.offsetX,L=A.offsetY,P={width:0,height:0};if(f.config.dataLabels.enabled){var T=String(f.globals.minY).length>String(f.globals.maxY).length?f.globals.minY:f.globals.maxY;P=p.getTextRects(f.globals.yLabelFormatters[0](T),parseFloat(A.style.fontSize));}var z={x:e,y:i,i:r,j:n,renderedPath:u,bcx:m,bcy:v,barHeight:h,barWidth:c,textRects:P,strokeWidth:x,dataLabelsX:y,dataLabelsY:w,barDataLabelsConfig:S,offX:C,offY:L};return k=this.barCtx.isHorizontal?this.calculateBarsDataLabelsPosition(z):this.calculateColumnsDataLabelsPosition(z),u.attr({cy:k.bcy,cx:k.bcx,j:n,val:l[r][n],barHeight:h,barWidth:c}),this.drawCalculatedDataLabels({x:k.dataLabelsX,y:k.dataLabelsY,val:this.barCtx.isTimelineBar?[a,s]:l[r][n],i:o,j:n,barWidth:c,barHeight:h,textRects:P,dataLabelsConfig:A})}},{key:"calculateColumnsDataLabelsPosition",value:function(t){var e,i=this.w,a=t.i,s=t.j,r=t.y,n=t.bcx,o=t.barWidth,l=t.barHeight,h=t.textRects,c=t.dataLabelsY,d=t.barDataLabelsConfig,g=t.strokeWidth,u=t.offX,f=t.offY;l=Math.abs(l);var p="vertical"===i.config.plotOptions.bar.dataLabels.orientation;n-=g/2;var x=i.globals.gridWidth/i.globals.dataPoints;if(e=i.globals.isXNumeric?n-o/2+u:n-x+o/2+u,p){e=e+h.height/2-g/2-2;}var b=this.barCtx.series[a][s]<0,m=r;switch(this.barCtx.isReversed&&(m=r-l+(b?2*l:0),r-=l),d.position){case"center":c=p?b?m+l/2+f:m+l/2-f:b?m-l/2+h.height/2+f:m+l/2+h.height/2-f;break;case"bottom":c=p?b?m+l+f:m+l-f:b?m-l+h.height+g+f:m+l-h.height/2+g-f;break;case"top":c=p?b?m+f:m-f:b?m-h.height/2-f:m+h.height+f;}return i.config.chart.stacked||(c<0?c=0+g:c+h.height/3>i.globals.gridHeight&&(c=i.globals.gridHeight-g)),{bcx:n,bcy:r,dataLabelsX:e,dataLabelsY:c}}},{key:"calculateBarsDataLabelsPosition",value:function(t){var e=this.w,i=t.x,a=t.i,s=t.j,r=t.bcy,n=t.barHeight,o=t.barWidth,l=t.textRects,h=t.dataLabelsX,c=t.strokeWidth,d=t.barDataLabelsConfig,g=t.offX,u=t.offY,f=e.globals.gridHeight/e.globals.dataPoints;o=Math.abs(o);var p=r-(this.barCtx.isTimelineBar?0:f)+n/2+l.height/2+u-3,x=this.barCtx.series[a][s]<0,b=i;switch(this.barCtx.isReversed&&(b=i+o-(x?2*o:0),i=e.globals.gridWidth-o),d.position){case"center":h=x?b+o/2-g:b-o/2+g;break;case"bottom":h=x?b+o-c-Math.round(l.width/2)-g:b-o+c+Math.round(l.width/2)+g;break;case"top":h=x?b-c+Math.round(l.width/2)-g:b-c-Math.round(l.width/2)+g;}return e.config.chart.stacked||(h<0?h=h+l.width+c:h+l.width/2>e.globals.gridWidth&&(h=e.globals.gridWidth-l.width-c)),{bcx:i,bcy:r,dataLabelsX:h,dataLabelsY:p}}},{key:"drawCalculatedDataLabels",value:function(t){var e=t.x,i=t.y,a=t.val,s=t.i,r=t.j,o=t.textRects,l=t.barHeight,h=t.barWidth,c=t.dataLabelsConfig,d=this.w,g="rotate(0)";"vertical"===d.config.plotOptions.bar.dataLabels.orientation&&(g="rotate(-90, ".concat(e,", ").concat(i,")"));var u=new I(this.barCtx.ctx),f=new b(this.barCtx.ctx),p=c.formatter,x=null,m=d.globals.collapsedSeriesIndices.indexOf(s)>-1;if(c.enabled&&!m){x=f.group({class:"apexcharts-data-labels",transform:g});var v="";void 0!==a&&(v=p(a,{seriesIndex:s,dataPointIndex:r,w:d})),0===a&&d.config.chart.stacked&&(v="");var y=d.globals.series[s][r]<=0,w=d.config.plotOptions.bar.dataLabels.position;if("vertical"===d.config.plotOptions.bar.dataLabels.orientation&&("top"===w&&(c.textAnchor=y?"end":"start"),"center"===w&&(c.textAnchor="middle"),"bottom"===w&&(c.textAnchor=y?"end":"start")),this.barCtx.isTimelineBar&&this.barCtx.barOptions.dataLabels.hideOverflowingLabels)h<f.getTextRects(v,parseFloat(c.style.fontSize)).width&&(v="");d.config.chart.stacked&&this.barCtx.barOptions.dataLabels.hideOverflowingLabels&&(this.barCtx.isHorizontal?(h>0&&o.width/1.6>h||h<0&&o.width/1.6<h)&&(v=""):o.height/1.6>l&&(v=""));var k=n({},c);this.barCtx.isHorizontal&&a<0&&("start"===c.textAnchor?k.textAnchor="end":"end"===c.textAnchor&&(k.textAnchor="start")),u.plotDataLabelsText({x:e,y:i,text:v,i:s,j:r,parent:x,dataLabelsConfig:k,alwaysDrawDataLabel:!0,offsetCorrection:!0});}return x}}]),t}(),E=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.legendInactiveClass="legend-mouseover-inactive";}return a(t,[{key:"getAllSeriesEls",value:function(){return this.w.globals.dom.baseEl.getElementsByClassName("apexcharts-series")}},{key:"getSeriesByName",value:function(t){return this.w.globals.dom.baseEl.querySelector(".apexcharts-inner .apexcharts-series[seriesName='".concat(f.escapeString(t),"']"))}},{key:"isSeriesHidden",value:function(t){var e=this.getSeriesByName(t),i=parseInt(e.getAttribute("data:realIndex"),10);return {isHidden:e.classList.contains("apexcharts-series-collapsed"),realIndex:i}}},{key:"addCollapsedClassToSeries",value:function(t,e){var i=this.w;function a(i){for(var a=0;a<i.length;a++)i[a].index===e&&t.node.classList.add("apexcharts-series-collapsed");}a(i.globals.collapsedSeries),a(i.globals.ancillaryCollapsedSeries);}},{key:"toggleSeries",value:function(t){var e=this.isSeriesHidden(t);return this.ctx.legend.legendHelpers.toggleDataSeries(e.realIndex,e.isHidden),e.isHidden}},{key:"showSeries",value:function(t){var e=this.isSeriesHidden(t);e.isHidden&&this.ctx.legend.legendHelpers.toggleDataSeries(e.realIndex,!0);}},{key:"hideSeries",value:function(t){var e=this.isSeriesHidden(t);e.isHidden||this.ctx.legend.legendHelpers.toggleDataSeries(e.realIndex,!1);}},{key:"resetSeries",value:function(){var t=!(arguments.length>0&&void 0!==arguments[0])||arguments[0],e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],a=this.w,s=f.clone(a.globals.initialSeries);a.globals.previousPaths=[],i?(a.globals.collapsedSeries=[],a.globals.ancillaryCollapsedSeries=[],a.globals.collapsedSeriesIndices=[],a.globals.ancillaryCollapsedSeriesIndices=[]):s=this.emptyCollapsedSeries(s),a.config.series=s,t&&(e&&(a.globals.zoomed=!1,this.ctx.updateHelpers.revertDefaultAxisMinMax()),this.ctx.updateHelpers._updateSeries(s,a.config.chart.animations.dynamicAnimation.enabled));}},{key:"emptyCollapsedSeries",value:function(t){for(var e=this.w,i=0;i<t.length;i++)e.globals.collapsedSeriesIndices.indexOf(i)>-1&&(t[i].data=[]);return t}},{key:"toggleSeriesOnHover",value:function(t,e){var i=this.w,a=i.globals.dom.baseEl.querySelectorAll(".apexcharts-series, .apexcharts-datalabels");if("mousemove"===t.type){var s=parseInt(e.getAttribute("rel"),10)-1,r=null,n=null;i.globals.axisCharts||"radialBar"===i.config.chart.type?i.globals.axisCharts?(r=i.globals.dom.baseEl.querySelector(".apexcharts-series[data\\:realIndex='".concat(s,"']")),n=i.globals.dom.baseEl.querySelector(".apexcharts-datalabels[data\\:realIndex='".concat(s,"']"))):r=i.globals.dom.baseEl.querySelector(".apexcharts-series[rel='".concat(s+1,"']")):r=i.globals.dom.baseEl.querySelector(".apexcharts-series[rel='".concat(s+1,"'] path"));for(var o=0;o<a.length;o++)a[o].classList.add(this.legendInactiveClass);null!==r&&(i.globals.axisCharts||r.parentNode.classList.remove(this.legendInactiveClass),r.classList.remove(this.legendInactiveClass),null!==n&&n.classList.remove(this.legendInactiveClass));}else if("mouseout"===t.type)for(var l=0;l<a.length;l++)a[l].classList.remove(this.legendInactiveClass);}},{key:"highlightRangeInSeries",value:function(t,e){var i=this,a=this.w,s=a.globals.dom.baseEl.getElementsByClassName("apexcharts-heatmap-rect"),r=function(t){for(var e=0;e<s.length;e++)s[e].classList[t](i.legendInactiveClass);};if("mousemove"===t.type){var n=parseInt(e.getAttribute("rel"),10)-1;r("add"),function(t){for(var e=0;e<s.length;e++){var a=parseInt(s[e].getAttribute("val"),10);a>=t.from&&a<=t.to&&s[e].classList.remove(i.legendInactiveClass);}}(a.config.plotOptions.heatmap.colorScale.ranges[n]);}else "mouseout"===t.type&&r("remove");}},{key:"getActiveConfigSeriesIndex",value:function(){var t=arguments.length>0&&void 0!==arguments[0]&&arguments[0],e=this.w,i=0;if(e.config.series.length>1)for(var a=e.config.series.map((function(i,a){var s=!1;return t&&(s="bar"===e.config.series[a].type||"column"===e.config.series[a].type),i.data&&i.data.length>0&&!s?a:-1})),s=0;s<a.length;s++)if(-1!==a[s]){i=a[s];break}return i}},{key:"getPreviousPaths",value:function(){var t=this.w;function e(e,i,a){for(var s=e[i].childNodes,r={type:a,paths:[],realIndex:e[i].getAttribute("data:realIndex")},n=0;n<s.length;n++)if(s[n].hasAttribute("pathTo")){var o=s[n].getAttribute("pathTo");r.paths.push({d:o});}t.globals.previousPaths.push(r);}t.globals.previousPaths=[];["line","area","bar","candlestick","radar"].forEach((function(i){for(var a,s=(a=i,t.globals.dom.baseEl.querySelectorAll(".apexcharts-".concat(a,"-series .apexcharts-series"))),r=0;r<s.length;r++)e(s,r,i);})),this.handlePrevBubbleScatterPaths("bubble"),this.handlePrevBubbleScatterPaths("scatter");var i=t.globals.dom.baseEl.querySelectorAll(".apexcharts-".concat(t.config.chart.type," .apexcharts-series"));if(i.length>0)for(var a=function(e){for(var i=t.globals.dom.baseEl.querySelectorAll(".apexcharts-".concat(t.config.chart.type," .apexcharts-series[data\\:realIndex='").concat(e,"'] rect")),a=[],s=function(t){var e=function(e){return i[t].getAttribute(e)},s={x:parseFloat(e("x")),y:parseFloat(e("y")),width:parseFloat(e("width")),height:parseFloat(e("height"))};a.push({rect:s,color:i[t].getAttribute("color")});},r=0;r<i.length;r++)s(r);t.globals.previousPaths.push(a);},s=0;s<i.length;s++)a(s);t.globals.axisCharts||(t.globals.previousPaths=t.globals.series);}},{key:"handlePrevBubbleScatterPaths",value:function(t){var e=this.w,i=e.globals.dom.baseEl.querySelectorAll(".apexcharts-".concat(t,"-series .apexcharts-series"));if(i.length>0)for(var a=0;a<i.length;a++){for(var s=e.globals.dom.baseEl.querySelectorAll(".apexcharts-".concat(t,"-series .apexcharts-series[data\\:realIndex='").concat(a,"'] circle")),r=[],n=0;n<s.length;n++)r.push({x:s[n].getAttribute("cx"),y:s[n].getAttribute("cy"),r:s[n].getAttribute("r")});e.globals.previousPaths.push(r);}}},{key:"clearPreviousPaths",value:function(){var t=this.w;t.globals.previousPaths=[],t.globals.allSeriesCollapsed=!1;}},{key:"handleNoData",value:function(){var t=this.w,e=t.config.noData,i=new b(this.ctx),a=t.globals.svgWidth/2,s=t.globals.svgHeight/2,r="middle";if(t.globals.noData=!0,t.globals.animationEnded=!0,"left"===e.align?(a=10,r="start"):"right"===e.align&&(a=t.globals.svgWidth-10,r="end"),"top"===e.verticalAlign?s=50:"bottom"===e.verticalAlign&&(s=t.globals.svgHeight-50),a+=e.offsetX,s=s+parseInt(e.style.fontSize,10)+2+e.offsetY,void 0!==e.text&&""!==e.text){var n=i.drawText({x:a,y:s,text:e.text,textAnchor:r,fontSize:e.style.fontSize,fontFamily:e.style.fontFamily,foreColor:e.style.color,opacity:1,class:"apexcharts-text-nodata"});t.globals.dom.Paper.add(n);}}},{key:"setNullSeriesToZeroValues",value:function(t){for(var e=this.w,i=0;i<t.length;i++)if(0===t[i].length)for(var a=0;a<t[e.globals.maxValsInArrayIndex].length;a++)t[i].push(0);return t}},{key:"hasAllSeriesEqualX",value:function(){for(var t=!0,e=this.w,i=this.filteredSeriesX(),a=0;a<i.length-1;a++)if(i[a][0]!==i[a+1][0]){t=!1;break}return e.globals.allSeriesHasEqualX=t,t}},{key:"filteredSeriesX",value:function(){var t=this.w.globals.seriesX.map((function(t){return t.length>0?t:[]}));return t}}]),t}(),X=function(){function t(i){e(this,t),this.w=i.w,this.barCtx=i;}return a(t,[{key:"initVariables",value:function(t){var e=this.w;this.barCtx.series=t,this.barCtx.totalItems=0,this.barCtx.seriesLen=0,this.barCtx.visibleI=-1,this.barCtx.visibleItems=1;for(var i=0;i<t.length;i++)if(t[i].length>0&&(this.barCtx.seriesLen=this.barCtx.seriesLen+1,this.barCtx.totalItems+=t[i].length),e.globals.isXNumeric)for(var a=0;a<t[i].length;a++)e.globals.seriesX[i][a]>e.globals.minX&&e.globals.seriesX[i][a]<e.globals.maxX&&this.barCtx.visibleItems++;else this.barCtx.visibleItems=e.globals.dataPoints;0===this.barCtx.seriesLen&&(this.barCtx.seriesLen=1);}},{key:"initialPositions",value:function(){var t,e,i,a,s,r,n,o,l=this.w,h=l.globals.dataPoints;this.barCtx.isTimelineBar&&(h=l.globals.labels.length);var c=this.barCtx.seriesLen;if(l.config.plotOptions.bar.rangeBarGroupRows&&(c=1),this.barCtx.isHorizontal)s=(i=l.globals.gridHeight/h)/c,l.globals.isXNumeric&&(s=(i=l.globals.gridHeight/this.barCtx.totalItems)/this.barCtx.seriesLen),s=s*parseInt(this.barCtx.barOptions.barHeight,10)/100,o=this.barCtx.baseLineInvertedY+l.globals.padHorizontal+(this.barCtx.isReversed?l.globals.gridWidth:0)-(this.barCtx.isReversed?2*this.barCtx.baseLineInvertedY:0),e=(i-s*this.barCtx.seriesLen)/2;else {if(a=l.globals.gridWidth/this.barCtx.visibleItems,l.config.xaxis.convertedCatToNumeric&&(a=l.globals.gridWidth/l.globals.dataPoints),r=a/this.barCtx.seriesLen*parseInt(this.barCtx.barOptions.columnWidth,10)/100,l.globals.isXNumeric){var d=this.barCtx.xRatio;l.config.xaxis.convertedCatToNumeric&&(d=this.barCtx.initialXRatio),l.globals.minXDiff&&.5!==l.globals.minXDiff&&l.globals.minXDiff/d>0&&(a=l.globals.minXDiff/d),(r=a/this.barCtx.seriesLen*parseInt(this.barCtx.barOptions.columnWidth,10)/100)<1&&(r=1);}n=l.globals.gridHeight-this.barCtx.baseLineY[this.barCtx.yaxisIndex]-(this.barCtx.isReversed?l.globals.gridHeight:0)+(this.barCtx.isReversed?2*this.barCtx.baseLineY[this.barCtx.yaxisIndex]:0),t=l.globals.padHorizontal+(a-r*this.barCtx.seriesLen)/2;}return {x:t,y:e,yDivision:i,xDivision:a,barHeight:s,barWidth:r,zeroH:n,zeroW:o}}},{key:"getPathFillColor",value:function(t,e,i,a){var s=this.w,r=new P(this.barCtx.ctx),n=null,o=this.barCtx.barOptions.distributed?i:e;this.barCtx.barOptions.colors.ranges.length>0&&this.barCtx.barOptions.colors.ranges.map((function(a){t[e][i]>=a.from&&t[e][i]<=a.to&&(n=a.color);}));return s.config.series[e].data[i]&&s.config.series[e].data[i].fillColor&&(n=s.config.series[e].data[i].fillColor),r.fillPath({seriesNumber:this.barCtx.barOptions.distributed?o:a,dataPointIndex:i,color:n,value:t[e][i]})}},{key:"getStrokeWidth",value:function(t,e,i){var a=0,s=this.w;return void 0===this.barCtx.series[t][e]||null===this.barCtx.series[t][e]?this.barCtx.isNullValue=!0:this.barCtx.isNullValue=!1,s.config.stroke.show&&(this.barCtx.isNullValue||(a=Array.isArray(this.barCtx.strokeWidth)?this.barCtx.strokeWidth[i]:this.barCtx.strokeWidth)),a}},{key:"barBackground",value:function(t){var e=t.j,i=t.i,a=t.x1,s=t.x2,r=t.y1,n=t.y2,o=t.elSeries,l=this.w,h=new b(this.barCtx.ctx),c=new E(this.barCtx.ctx).getActiveConfigSeriesIndex();if(this.barCtx.barOptions.colors.backgroundBarColors.length>0&&c===i){e>=this.barCtx.barOptions.colors.backgroundBarColors.length&&(e-=this.barCtx.barOptions.colors.backgroundBarColors.length);var d=this.barCtx.barOptions.colors.backgroundBarColors[e],g=h.drawRect(void 0!==a?a:0,void 0!==r?r:0,void 0!==s?s:l.globals.gridWidth,void 0!==n?n:l.globals.gridHeight,this.barCtx.barOptions.colors.backgroundBarRadius,d,this.barCtx.barOptions.colors.backgroundBarOpacity);o.add(g),g.node.classList.add("apexcharts-backgroundBar");}}},{key:"getColumnPaths",value:function(t){var e=t.barWidth,i=t.barXPosition,a=t.yRatio,s=t.y1,r=t.y2,n=t.strokeWidth,o=t.series,l=t.realIndex,h=t.i,c=t.j,d=t.w,g=new b(this.barCtx.ctx);(n=Array.isArray(n)?n[l]:n)||(n=0);var u={barWidth:e,strokeWidth:n,yRatio:a,barXPosition:i,y1:s,y2:r},f=this.getRoundedBars(d,u,o,h,c),p=i,x=i+e,m=g.move(p,f.y1),v=g.move(p,f.y1);return d.globals.previousPaths.length>0&&(v=this.barCtx.getPreviousPath(l,c,!1)),{pathTo:m=m+g.line(p,f.y2)+f.endingPath+g.line(x-n,f.y2)+g.line(x-n,f.y1)+f.startingPath+"z",pathFrom:v=v+g.line(p,s)+g.line(x-n,s)+g.line(x-n,s)+g.line(x-n,s)+g.line(p,s)}}},{key:"getBarpaths",value:function(t){var e=t.barYPosition,i=t.barHeight,a=t.x1,s=t.x2,r=t.strokeWidth,n=t.series,o=t.realIndex,l=t.i,h=t.j,c=t.w,d=new b(this.barCtx.ctx);(r=Array.isArray(r)?r[o]:r)||(r=0);var g={barHeight:i,strokeWidth:r,barYPosition:e,x2:s,x1:a},u=this.getRoundedBars(c,g,n,l,h),f=d.move(u.x1,e),p=d.move(u.x1,e);c.globals.previousPaths.length>0&&(p=this.barCtx.getPreviousPath(o,h,!1));var x=e,m=e+i;return {pathTo:f=f+d.line(u.x2,x)+u.endingPath+d.line(u.x2,m-r)+d.line(u.x1,m-r)+u.startingPath+"z",pathFrom:p=p+d.line(a,x)+d.line(a,m-r)+d.line(a,m-r)+d.line(a,m-r)+d.line(a,x)}}},{key:"getRoundedBars",value:function(t,e,i,a,s){var r=new b(this.barCtx.ctx),n=Array.isArray(e.strokeWidth)?e.strokeWidth[a]:e.strokeWidth;if(n||(n=0),this.barCtx.isHorizontal){var o=null,l="",h=e.x2,c=e.x1;if(void 0!==i[a][s]||null!==i[a][s]){var d=i[a][s]<0,g=e.barHeight/2-n;switch(d&&(g=-e.barHeight/2-n),g>Math.abs(h-c)&&(g=Math.abs(h-c)),"rounded"===this.barCtx.barOptions.endingShape&&(h=e.x2-g/2),"rounded"===this.barCtx.barOptions.startingShape&&(c=e.x1+g/2),this.barCtx.barOptions.endingShape){case"flat":o=r.line(h,e.barYPosition+e.barHeight-n);break;case"rounded":o=r.quadraticCurve(h+g,e.barYPosition+(e.barHeight-n)/2,h,e.barYPosition+e.barHeight-n);}switch(this.barCtx.barOptions.startingShape){case"flat":l=r.line(c,e.barYPosition+e.barHeight-n);break;case"rounded":l=r.quadraticCurve(c-g,e.barYPosition+e.barHeight/2,c,e.barYPosition);}}return {endingPath:o,startingPath:l,x2:h,x1:c}}var u=null,f="",p=e.y2,x=e.y1;if(void 0!==i[a][s]||null!==i[a][s]){var m=i[a][s]<0,v=e.barWidth/2-n;switch(m&&(v=-e.barWidth/2-n),v>Math.abs(p-x)&&(v=Math.abs(p-x)),"rounded"===this.barCtx.barOptions.endingShape&&(p+=v/2),"rounded"===this.barCtx.barOptions.startingShape&&(x-=v/2),this.barCtx.barOptions.endingShape){case"flat":u=r.line(e.barXPosition+e.barWidth-n,p);break;case"rounded":u=r.quadraticCurve(e.barXPosition+(e.barWidth-n)/2,p-v,e.barXPosition+e.barWidth-n,p);}switch(this.barCtx.barOptions.startingShape){case"flat":f=r.line(e.barXPosition+e.barWidth-n,x);break;case"rounded":f=r.quadraticCurve(e.barXPosition+(e.barWidth-n)/2,x+v,e.barXPosition,x);}}return {endingPath:u,startingPath:f,y2:p,y1:x}}}]),t}(),Y=function(){function t(i,a){e(this,t),this.ctx=i,this.w=i.w;var s=this.w;this.barOptions=s.config.plotOptions.bar,this.isHorizontal=this.barOptions.horizontal,this.strokeWidth=s.config.stroke.width,this.isNullValue=!1,this.isTimelineBar="datetime"===s.config.xaxis.type&&s.globals.seriesRangeBarTimeline.length,this.xyRatios=a,null!==this.xyRatios&&(this.xRatio=a.xRatio,this.initialXRatio=a.initialXRatio,this.yRatio=a.yRatio,this.invertedXRatio=a.invertedXRatio,this.invertedYRatio=a.invertedYRatio,this.baseLineY=a.baseLineY,this.baseLineInvertedY=a.baseLineInvertedY),this.yaxisIndex=0,this.seriesLen=0,this.barHelpers=new X(this);}return a(t,[{key:"draw",value:function(t,e){var i=this.w,a=new b(this.ctx),s=new w(this.ctx,i);t=s.getLogSeries(t),this.series=t,this.yRatio=s.getLogYRatios(this.yRatio),this.barHelpers.initVariables(t);var r=a.group({class:"apexcharts-bar-series apexcharts-plot-series"});i.config.dataLabels.enabled&&this.totalItems>this.barOptions.dataLabels.maxItems&&console.warn("WARNING: DataLabels are enabled but there are too many to display. This may cause performance issue when rendering.");for(var o=0,l=0;o<t.length;o++,l++){var h,c,d,g,u=void 0,p=void 0,x=[],m=[],v=i.globals.comboCharts?e[o]:o,y=a.group({class:"apexcharts-series",rel:o+1,seriesName:f.escapeString(i.globals.seriesNames[v]),"data:realIndex":v});this.ctx.series.addCollapsedClassToSeries(y,v),t[o].length>0&&(this.visibleI=this.visibleI+1);var k=0,A=0;this.yRatio.length>1&&(this.yaxisIndex=v),this.isReversed=i.config.yaxis[this.yaxisIndex]&&i.config.yaxis[this.yaxisIndex].reversed;var S=this.barHelpers.initialPositions();p=S.y,k=S.barHeight,c=S.yDivision,g=S.zeroW,u=S.x,A=S.barWidth,h=S.xDivision,d=S.zeroH,this.horizontal||m.push(u+A/2);for(var C=a.group({class:"apexcharts-datalabels","data:realIndex":v}),L=0;L<i.globals.dataPoints;L++){var P=this.barHelpers.getStrokeWidth(o,L,v),T=null,z={indexes:{i:o,j:L,realIndex:v,bc:l},x:u,y:p,strokeWidth:P,elSeries:y};this.isHorizontal?(T=this.drawBarPaths(n(n({},z),{},{barHeight:k,zeroW:g,yDivision:c})),A=this.series[o][L]/this.invertedYRatio):(T=this.drawColumnPaths(n(n({},z),{},{xDivision:h,barWidth:A,zeroH:d})),k=this.series[o][L]/this.yRatio[this.yaxisIndex]),p=T.y,u=T.x,L>0&&m.push(u+A/2),x.push(p);var I=this.barHelpers.getPathFillColor(t,o,L,v);this.renderSeries({realIndex:v,pathFill:I,j:L,i:o,pathFrom:T.pathFrom,pathTo:T.pathTo,strokeWidth:P,elSeries:y,x:u,y:p,series:t,barHeight:k,barWidth:A,elDataLabelsWrap:C,visibleSeries:this.visibleI,type:"bar"});}i.globals.seriesXvalues[v]=m,i.globals.seriesYvalues[v]=x,r.add(y);}return r}},{key:"renderSeries",value:function(t){var e=t.realIndex,i=t.pathFill,a=t.lineFill,s=t.j,r=t.i,n=t.pathFrom,o=t.pathTo,l=t.strokeWidth,h=t.elSeries,c=t.x,d=t.y,g=t.y1,u=t.y2,f=t.series,x=t.barHeight,m=t.barWidth,v=t.barYPosition,y=t.elDataLabelsWrap,w=t.visibleSeries,k=t.type,A=this.w,S=new b(this.ctx);a||(a=this.barOptions.distributed?A.globals.stroke.colors[s]:A.globals.stroke.colors[e]),A.config.series[r].data[s]&&A.config.series[r].data[s].strokeColor&&(a=A.config.series[r].data[s].strokeColor),this.isNullValue&&(i="none");var C=s/A.config.chart.animations.animateGradually.delay*(A.config.chart.animations.speed/A.globals.dataPoints)/2.4,L=S.renderPaths({i:r,j:s,realIndex:e,pathFrom:n,pathTo:o,stroke:a,strokeWidth:l,strokeLineCap:A.config.stroke.lineCap,fill:i,animationDelay:C,initialSpeed:A.config.chart.animations.speed,dataChangeSpeed:A.config.chart.animations.dynamicAnimation.speed,className:"apexcharts-".concat(k,"-area")});L.attr("clip-path","url(#gridRectMask".concat(A.globals.cuid,")")),void 0!==g&&void 0!==u&&(L.attr("data-range-y1",g),L.attr("data-range-y2",u)),new p(this.ctx).setSelectionFilter(L,e,s),h.add(L);var P=new M(this).handleBarDataLabels({x:c,y:d,y1:g,y2:u,i:r,j:s,series:f,realIndex:e,barHeight:x,barWidth:m,barYPosition:v,renderedPath:L,visibleSeries:w});return null!==P&&y.add(P),h.add(y),h}},{key:"drawBarPaths",value:function(t){var e=t.indexes,i=t.barHeight,a=t.strokeWidth,s=t.zeroW,r=t.x,n=t.y,o=t.yDivision,l=t.elSeries,h=this.w,c=e.i,d=e.j;h.globals.isXNumeric&&(n=(h.globals.seriesX[c][d]-h.globals.minX)/this.invertedXRatio-i);var g=n+i*this.visibleI;r=void 0===this.series[c][d]||null===this.series[c][d]?s:s+this.series[c][d]/this.invertedYRatio-2*(this.isReversed?this.series[c][d]/this.invertedYRatio:0);var u=this.barHelpers.getBarpaths({barYPosition:g,barHeight:i,x1:s,x2:r,strokeWidth:a,series:this.series,realIndex:e.realIndex,i:c,j:d,w:h});return h.globals.isXNumeric||(n+=o),this.barHelpers.barBackground({j:d,i:c,y1:g-i*this.visibleI,y2:i*this.seriesLen,elSeries:l}),{pathTo:u.pathTo,pathFrom:u.pathFrom,x:r,y:n,barYPosition:g}}},{key:"drawColumnPaths",value:function(t){var e=t.indexes,i=t.x,a=t.y,s=t.xDivision,r=t.barWidth,n=t.zeroH,o=t.strokeWidth,l=t.elSeries,h=this.w,c=e.i,d=e.j,g=e.bc;if(h.globals.isXNumeric){var u=c;h.globals.seriesX[c].length||(u=h.globals.maxValsInArrayIndex),i=(h.globals.seriesX[u][d]-h.globals.minX)/this.xRatio-r*this.seriesLen/2;}var f=i+r*this.visibleI;a=void 0===this.series[c][d]||null===this.series[c][d]?n:n-this.series[c][d]/this.yRatio[this.yaxisIndex]+2*(this.isReversed?this.series[c][d]/this.yRatio[this.yaxisIndex]:0);var p=this.barHelpers.getColumnPaths({barXPosition:f,barWidth:r,y1:n,y2:a,strokeWidth:o,series:this.series,realIndex:e.realIndex,i:c,j:d,w:h});return h.globals.isXNumeric||(i+=s),this.barHelpers.barBackground({bc:g,j:d,i:c,x1:f-o/2-r*this.visibleI,x2:r*this.seriesLen+o/2,elSeries:l}),{pathTo:p.pathTo,pathFrom:p.pathFrom,x:i,y:a,barXPosition:f}}},{key:"getPreviousPath",value:function(t,e){for(var i,a=this.w,s=0;s<a.globals.previousPaths.length;s++){var r=a.globals.previousPaths[s];r.paths&&r.paths.length>0&&parseInt(r.realIndex,10)===parseInt(t,10)&&void 0!==a.globals.previousPaths[s].paths[e]&&(i=a.globals.previousPaths[s].paths[e].d);}return i}}]),t}(),F=function(t){o(s,t);var i=d(s);function s(){return e(this,s),i.apply(this,arguments)}return a(s,[{key:"draw",value:function(t,e){var i=this.w,a=new b(this.ctx);this.rangeBarOptions=this.w.config.plotOptions.rangeBar,this.series=t,this.seriesRangeStart=i.globals.seriesRangeStart,this.seriesRangeEnd=i.globals.seriesRangeEnd,this.barHelpers.initVariables(t);for(var s=a.group({class:"apexcharts-rangebar-series apexcharts-plot-series"}),r=0;r<t.length;r++){var o,l,h,c=void 0,d=void 0,g=void 0,u=i.globals.comboCharts?e[r]:r,p=a.group({class:"apexcharts-series",seriesName:f.escapeString(i.globals.seriesNames[u]),rel:r+1,"data:realIndex":u});t[r].length>0&&(this.visibleI=this.visibleI+1);var x=0,m=0;this.yRatio.length>1&&(this.yaxisIndex=u);var v=this.barHelpers.initialPositions();d=v.y,h=v.zeroW,c=v.x,m=v.barWidth,o=v.xDivision,l=v.zeroH;for(var y=a.group({class:"apexcharts-datalabels","data:realIndex":u}),w=0;w<i.globals.dataPoints;w++){var k=this.barHelpers.getStrokeWidth(r,w,u),A=this.seriesRangeStart[r][w],S=this.seriesRangeEnd[r][w],C=null,L=null,P={x:c,y:d,strokeWidth:k,elSeries:p};if(g=v.yDivision,x=v.barHeight,this.isHorizontal){L=d+x*this.visibleI;var T=this.seriesLen;i.config.plotOptions.bar.rangeBarGroupRows&&(T=1);var z=(g-x*T)/2;if(void 0===i.config.series[r].data[w])break;if(this.isTimelineBar&&i.config.series[r].data[w].x){var I=this.detectOverlappingBars({i:r,j:w,barYPosition:L,srty:z,barHeight:x,yDivision:g,initPositions:v});x=I.barHeight,L=I.barYPosition;}m=(C=this.drawRangeBarPaths(n({indexes:{i:r,j:w,realIndex:u},barHeight:x,barYPosition:L,zeroW:h,yDivision:g,y1:A,y2:S},P))).barWidth;}else x=(C=this.drawRangeColumnPaths(n({indexes:{i:r,j:w,realIndex:u},zeroH:l,barWidth:m,xDivision:o},P))).barHeight;d=C.y,c=C.x;var M=this.barHelpers.getPathFillColor(t,r,w,u),E=i.globals.stroke.colors[u];this.renderSeries({realIndex:u,pathFill:M,lineFill:E,j:w,i:r,x:c,y:d,y1:A,y2:S,pathFrom:C.pathFrom,pathTo:C.pathTo,strokeWidth:k,elSeries:p,series:t,barHeight:x,barYPosition:L,barWidth:m,elDataLabelsWrap:y,visibleSeries:this.visibleI,type:"rangebar"});}s.add(p);}return s}},{key:"detectOverlappingBars",value:function(t){var e=t.i,i=t.j,a=t.barYPosition,s=t.srty,r=t.barHeight,n=t.yDivision,o=t.initPositions,l=this.w,h=[],c=l.config.series[e].data[i].rangeName,d=l.config.series[e].data[i].x,g=l.globals.labels.indexOf(d),u=l.globals.seriesRangeBarTimeline[e].findIndex((function(t){return t.x===d&&t.overlaps.length>0}));return a=l.config.plotOptions.bar.rangeBarGroupRows?s+n*g:s+r*this.visibleI+n*g,u>-1&&!l.config.plotOptions.bar.rangeBarOverlap&&(h=l.globals.seriesRangeBarTimeline[e][u].overlaps).indexOf(c)>-1&&(a=(r=o.barHeight/h.length)*this.visibleI+n*(100-parseInt(this.barOptions.barHeight,10))/100/2+r*(this.visibleI+h.indexOf(c))+n*g),{barYPosition:a,barHeight:r}}},{key:"drawRangeColumnPaths",value:function(t){var e=t.indexes,i=t.x,a=(t.strokeWidth,t.xDivision),s=t.barWidth,r=t.zeroH,n=this.w,o=e.i,l=e.j,h=this.yRatio[this.yaxisIndex],c=e.realIndex,d=this.getRangeValue(c,l),g=Math.min(d.start,d.end),u=Math.max(d.start,d.end);n.globals.isXNumeric&&(i=(n.globals.seriesX[o][l]-n.globals.minX)/this.xRatio-s/2);var f=i+s*this.visibleI;void 0===this.series[o][l]||null===this.series[o][l]?g=r:(g=r-g/h,u=r-u/h);var p=Math.abs(u-g),x=this.barHelpers.getColumnPaths({barXPosition:f,barWidth:s,y1:g,y2:u,strokeWidth:this.strokeWidth,series:this.seriesRangeEnd,i:c,j:l,w:n});return n.globals.isXNumeric||(i+=a),{pathTo:x.pathTo,pathFrom:x.pathFrom,barHeight:p,x:i,y:u,barXPosition:f}}},{key:"drawRangeBarPaths",value:function(t){var e=t.indexes,i=t.y,a=t.y1,s=t.y2,r=t.yDivision,n=t.barHeight,o=t.barYPosition,l=t.zeroW,h=this.w,c=l+a/this.invertedYRatio,d=l+s/this.invertedYRatio,g=Math.abs(d-c),u=this.barHelpers.getBarpaths({barYPosition:o,barHeight:n,x1:c,x2:d,strokeWidth:this.strokeWidth,series:this.seriesRangeEnd,i:e.realIndex,j:e.j,w:h});return h.globals.isXNumeric||(i+=r),{pathTo:u.pathTo,pathFrom:u.pathFrom,barWidth:g,x:d,y:i}}},{key:"getRangeValue",value:function(t,e){var i=this.w;return {start:i.globals.seriesRangeStart[t][e],end:i.globals.seriesRangeEnd[t][e]}}},{key:"getTooltipValues",value:function(t){var e=t.ctx,i=t.seriesIndex,a=t.dataPointIndex,s=t.y1,r=t.y2,n=t.w,o=n.globals.seriesRangeStart[i][a],l=n.globals.seriesRangeEnd[i][a],h=n.globals.labels[a],c=n.config.series[i].name,d=n.config.tooltip.y.formatter,g=n.config.tooltip.y.title.formatter,u={w:n,seriesIndex:i,dataPointIndex:a};"function"==typeof g&&(c=g(c,u)),s&&r&&(o=s,l=r,n.config.series[i].data[a].x&&(h=n.config.series[i].data[a].x+":"),"function"==typeof d&&(h=d(h,u)));var f="",p="",x=n.globals.colors[i];if(void 0===n.config.tooltip.x.formatter)if("datetime"===n.config.xaxis.type){var b=new y(e);f=b.formatDate(b.getDate(o),n.config.tooltip.x.format),p=b.formatDate(b.getDate(l),n.config.tooltip.x.format);}else f=o,p=l;else f=n.config.tooltip.x.formatter(o),p=n.config.tooltip.x.formatter(l);return {start:o,end:l,startVal:f,endVal:p,ylabel:h,color:x,seriesName:c}}},{key:"buildCustomTooltipHTML",value:function(t){var e=t.color,i=t.seriesName;return '<div class="apexcharts-tooltip-rangebar"><div> <span class="series-name" style="color: '+e+'">'+(i||"")+'</span></div><div> <span class="category">'+t.ylabel+' </span> <span class="value start-value">'+t.start+'</span> <span class="separator">-</span> <span class="value end-value">'+t.end+"</span></div></div>"}}]),s}(Y),D=function(){function t(i){e(this,t),this.opts=i;}return a(t,[{key:"line",value:function(){return {chart:{animations:{easing:"swing"}},dataLabels:{enabled:!1},stroke:{width:5,curve:"straight"},markers:{size:0,hover:{sizeOffset:6}},xaxis:{crosshairs:{width:1}}}}},{key:"sparkline",value:function(t){this.opts.yaxis[0].show=!1,this.opts.yaxis[0].title.text="",this.opts.yaxis[0].axisBorder.show=!1,this.opts.yaxis[0].axisTicks.show=!1,this.opts.yaxis[0].floating=!0;return f.extend(t,{grid:{show:!1,padding:{left:0,right:0,top:0,bottom:0}},legend:{show:!1},xaxis:{labels:{show:!1},tooltip:{enabled:!1},axisBorder:{show:!1},axisTicks:{show:!1}},chart:{toolbar:{show:!1},zoom:{enabled:!1}},dataLabels:{enabled:!1}})}},{key:"bar",value:function(){return {chart:{stacked:!1,animations:{easing:"swing"}},plotOptions:{bar:{dataLabels:{position:"center"}}},dataLabels:{style:{colors:["#fff"]},background:{enabled:!1}},stroke:{width:0,lineCap:"square"},fill:{opacity:.85},legend:{markers:{shape:"square",radius:2,size:8}},tooltip:{shared:!1},xaxis:{tooltip:{enabled:!1},tickPlacement:"between",crosshairs:{width:"barWidth",position:"back",fill:{type:"gradient"},dropShadow:{enabled:!1},stroke:{width:0}}}}}},{key:"candlestick",value:function(){return {stroke:{width:1,colors:["#333"]},fill:{opacity:1},dataLabels:{enabled:!1},tooltip:{shared:!0,custom:function(t){var e=t.seriesIndex,i=t.dataPointIndex,a=t.w;return '<div class="apexcharts-tooltip-candlestick"><div>Open: <span class="value">'+a.globals.seriesCandleO[e][i]+'</span></div><div>High: <span class="value">'+a.globals.seriesCandleH[e][i]+'</span></div><div>Low: <span class="value">'+a.globals.seriesCandleL[e][i]+'</span></div><div>Close: <span class="value">'+a.globals.seriesCandleC[e][i]+"</span></div></div>"}},states:{active:{filter:{type:"none"}}},xaxis:{crosshairs:{width:1}}}}},{key:"rangeBar",value:function(){return {stroke:{width:0,lineCap:"square"},plotOptions:{bar:{dataLabels:{position:"center"}}},dataLabels:{enabled:!1,formatter:function(t,e){e.ctx;var i=e.seriesIndex,a=e.dataPointIndex,s=e.w,r=s.globals.seriesRangeStart[i][a];return s.globals.seriesRangeEnd[i][a]-r},background:{enabled:!1},style:{colors:["#fff"]}},tooltip:{shared:!1,followCursor:!0,custom:function(t){return t.w.config.plotOptions&&t.w.config.plotOptions.bar&&t.w.config.plotOptions.bar.horizontal?function(t){var e=new F(t.ctx,null),i=e.getTooltipValues(t),a=i.color,s=i.seriesName,r=i.ylabel,n=i.startVal,o=i.endVal;return e.buildCustomTooltipHTML({color:a,seriesName:s,ylabel:r,start:n,end:o})}(t):function(t){var e=new F(t.ctx,null),i=e.getTooltipValues(t),a=i.color,s=i.seriesName,r=i.ylabel,n=i.start,o=i.end;return e.buildCustomTooltipHTML({color:a,seriesName:s,ylabel:r,start:n,end:o})}(t)}},xaxis:{tickPlacement:"between",tooltip:{enabled:!1},crosshairs:{stroke:{width:0}}}}}},{key:"area",value:function(){return {stroke:{width:4},fill:{type:"gradient",gradient:{inverseColors:!1,shade:"light",type:"vertical",opacityFrom:.65,opacityTo:.5,stops:[0,100,100]}},markers:{size:0,hover:{sizeOffset:6}},tooltip:{followCursor:!1}}}},{key:"brush",value:function(t){return f.extend(t,{chart:{toolbar:{autoSelected:"selection",show:!1},zoom:{enabled:!1}},dataLabels:{enabled:!1},stroke:{width:1},tooltip:{enabled:!1},xaxis:{tooltip:{enabled:!1}}})}},{key:"stacked100",value:function(t){t.dataLabels=t.dataLabels||{},t.dataLabels.formatter=t.dataLabels.formatter||void 0;var e=t.dataLabels.formatter;return t.yaxis.forEach((function(e,i){t.yaxis[i].min=0,t.yaxis[i].max=100;})),"bar"===t.chart.type&&(t.dataLabels.formatter=e||function(t){return "number"==typeof t&&t?t.toFixed(0)+"%":t}),t}},{key:"convertCatToNumeric",value:function(t){return t.xaxis.convertedCatToNumeric=!0,t}},{key:"convertCatToNumericXaxis",value:function(t,e,i){t.xaxis.type="numeric",t.xaxis.labels=t.xaxis.labels||{},t.xaxis.labels.formatter=t.xaxis.labels.formatter||function(t){return f.isNumber(t)?Math.floor(t):t};var a=t.xaxis.labels.formatter,s=t.xaxis.categories&&t.xaxis.categories.length?t.xaxis.categories:t.labels;return i&&i.length&&(s=i.map((function(t){return t.toString()}))),s&&s.length&&(t.xaxis.labels.formatter=function(t){return f.isNumber(t)?a(s[Math.floor(t)-1]):a(t)}),t.xaxis.categories=[],t.labels=[],t.xaxis.tickAmount=t.xaxis.tickAmount||"dataPoints",t}},{key:"bubble",value:function(){return {dataLabels:{style:{colors:["#fff"]}},tooltip:{shared:!1,intersect:!0},xaxis:{crosshairs:{width:0}},fill:{type:"solid",gradient:{shade:"light",inverse:!0,shadeIntensity:.55,opacityFrom:.4,opacityTo:.8}}}}},{key:"scatter",value:function(){return {dataLabels:{enabled:!1},tooltip:{shared:!1,intersect:!0},markers:{size:6,strokeWidth:1,hover:{sizeOffset:2}}}}},{key:"heatmap",value:function(){return {chart:{stacked:!1},fill:{opacity:1},dataLabels:{style:{colors:["#fff"]}},stroke:{colors:["#fff"]},tooltip:{followCursor:!0,marker:{show:!1},x:{show:!1}},legend:{position:"top",markers:{shape:"square",size:10,offsetY:2}},grid:{padding:{right:20}}}}},{key:"treemap",value:function(){return {chart:{zoom:{enabled:!1}},dataLabels:{style:{fontSize:14,fontWeight:600,colors:["#fff"]}},stroke:{show:!0,width:2,colors:["#fff"]},legend:{show:!1},fill:{gradient:{stops:[0,100]}},tooltip:{followCursor:!0,x:{show:!1}},grid:{padding:{left:0,right:0}},xaxis:{crosshairs:{show:!1},tooltip:{enabled:!1}}}}},{key:"pie",value:function(){return {chart:{toolbar:{show:!1}},plotOptions:{pie:{donut:{labels:{show:!1}}}},dataLabels:{formatter:function(t){return t.toFixed(1)+"%"},style:{colors:["#fff"]},background:{enabled:!1},dropShadow:{enabled:!0}},stroke:{colors:["#fff"]},fill:{opacity:1,gradient:{shade:"light",stops:[0,100]}},tooltip:{theme:"dark",fillSeriesColor:!0},legend:{position:"right"}}}},{key:"donut",value:function(){return {chart:{toolbar:{show:!1}},dataLabels:{formatter:function(t){return t.toFixed(1)+"%"},style:{colors:["#fff"]},background:{enabled:!1},dropShadow:{enabled:!0}},stroke:{colors:["#fff"]},fill:{opacity:1,gradient:{shade:"light",shadeIntensity:.35,stops:[80,100],opacityFrom:1,opacityTo:1}},tooltip:{theme:"dark",fillSeriesColor:!0},legend:{position:"right"}}}},{key:"polarArea",value:function(){return this.opts.yaxis[0].tickAmount=this.opts.yaxis[0].tickAmount?this.opts.yaxis[0].tickAmount:6,{chart:{toolbar:{show:!1}},dataLabels:{formatter:function(t){return t.toFixed(1)+"%"},enabled:!1},stroke:{show:!0,width:2},fill:{opacity:.7},tooltip:{theme:"dark",fillSeriesColor:!0},legend:{position:"right"}}}},{key:"radar",value:function(){return this.opts.yaxis[0].labels.offsetY=this.opts.yaxis[0].labels.offsetY?this.opts.yaxis[0].labels.offsetY:6,{dataLabels:{enabled:!1,style:{fontSize:"11px"}},stroke:{width:2},markers:{size:3,strokeWidth:1,strokeOpacity:1},fill:{opacity:.2},tooltip:{shared:!1,intersect:!0,followCursor:!0},grid:{show:!1},xaxis:{labels:{formatter:function(t){return t},style:{colors:["#a8a8a8"],fontSize:"11px"}},tooltip:{enabled:!1},crosshairs:{show:!1}}}}},{key:"radialBar",value:function(){return {chart:{animations:{dynamicAnimation:{enabled:!0,speed:800}},toolbar:{show:!1}},fill:{gradient:{shade:"dark",shadeIntensity:.4,inverseColors:!1,type:"diagonal2",opacityFrom:1,opacityTo:1,stops:[70,98,100]}},legend:{show:!1,position:"right"},tooltip:{enabled:!1,fillSeriesColor:!0}}}}]),t}(),R=function(){function i(t){e(this,i),this.opts=t;}return a(i,[{key:"init",value:function(e){var i=e.responsiveOverride,a=this.opts,s=new C,r=new D(a);this.chartType=a.chart.type,"histogram"===this.chartType&&(a.chart.type="bar",a=f.extend({plotOptions:{bar:{columnWidth:"99.99%"}}},a)),a=this.extendYAxis(a),a=this.extendAnnotations(a);var n=s.init(),o={};if(a&&"object"===t(a)){var l={};l=-1!==["line","area","bar","candlestick","rangeBar","histogram","bubble","scatter","heatmap","treemap","pie","polarArea","donut","radar","radialBar"].indexOf(a.chart.type)?r[a.chart.type]():r.line(),a.chart.brush&&a.chart.brush.enabled&&(l=r.brush(l)),a.chart.stacked&&"100%"===a.chart.stackType&&(a=r.stacked100(a)),this.checkForDarkTheme(window.Apex),this.checkForDarkTheme(a),a.xaxis=a.xaxis||window.Apex.xaxis||{},i||(a.xaxis.convertedCatToNumeric=!1),((a=this.checkForCatToNumericXAxis(this.chartType,l,a)).chart.sparkline&&a.chart.sparkline.enabled||window.Apex.chart&&window.Apex.chart.sparkline&&window.Apex.chart.sparkline.enabled)&&(l=r.sparkline(l)),o=f.extend(n,l);}var h=f.extend(o,window.Apex);return n=f.extend(h,a),n=this.handleUserInputErrors(n)}},{key:"checkForCatToNumericXAxis",value:function(t,e,i){var a=new D(i),s="bar"===t&&i.plotOptions&&i.plotOptions.bar&&i.plotOptions.bar.horizontal,r="pie"===t||"polarArea"===t||"donut"===t||"radar"===t||"radialBar"===t||"heatmap"===t,n="datetime"!==i.xaxis.type&&"numeric"!==i.xaxis.type,o=i.xaxis.tickPlacement?i.xaxis.tickPlacement:e.xaxis&&e.xaxis.tickPlacement;return s||r||!n||"between"===o||(i=a.convertCatToNumeric(i)),i}},{key:"extendYAxis",value:function(t,e){var i=new C;(void 0===t.yaxis||!t.yaxis||Array.isArray(t.yaxis)&&0===t.yaxis.length)&&(t.yaxis={}),t.yaxis.constructor!==Array&&window.Apex.yaxis&&window.Apex.yaxis.constructor!==Array&&(t.yaxis=f.extend(t.yaxis,window.Apex.yaxis)),t.yaxis.constructor!==Array?t.yaxis=[f.extend(i.yAxis,t.yaxis)]:t.yaxis=f.extendArray(t.yaxis,i.yAxis);var a=!1;t.yaxis.forEach((function(t){t.logarithmic&&(a=!0);}));var s=t.series;return e&&!s&&(s=e.config.series),a&&s.length!==t.yaxis.length&&s.length&&(t.yaxis=s.map((function(e,a){if(e.name||(s[a].name="series-".concat(a+1)),t.yaxis[a])return t.yaxis[a].seriesName=s[a].name,t.yaxis[a];var r=f.extend(i.yAxis,t.yaxis[0]);return r.show=!1,r}))),a&&s.length>1&&s.length!==t.yaxis.length&&console.warn("A multi-series logarithmic chart should have equal number of series and y-axes. Please make sure to equalize both."),t}},{key:"extendAnnotations",value:function(t){return void 0===t.annotations&&(t.annotations={},t.annotations.yaxis=[],t.annotations.xaxis=[],t.annotations.points=[]),t=this.extendYAxisAnnotations(t),t=this.extendXAxisAnnotations(t),t=this.extendPointAnnotations(t)}},{key:"extendYAxisAnnotations",value:function(t){var e=new C;return t.annotations.yaxis=f.extendArray(void 0!==t.annotations.yaxis?t.annotations.yaxis:[],e.yAxisAnnotation),t}},{key:"extendXAxisAnnotations",value:function(t){var e=new C;return t.annotations.xaxis=f.extendArray(void 0!==t.annotations.xaxis?t.annotations.xaxis:[],e.xAxisAnnotation),t}},{key:"extendPointAnnotations",value:function(t){var e=new C;return t.annotations.points=f.extendArray(void 0!==t.annotations.points?t.annotations.points:[],e.pointAnnotation),t}},{key:"checkForDarkTheme",value:function(t){t.theme&&"dark"===t.theme.mode&&(t.tooltip||(t.tooltip={}),"light"!==t.tooltip.theme&&(t.tooltip.theme="dark"),t.chart.foreColor||(t.chart.foreColor="#f6f7f8"),t.chart.background||(t.chart.background="#424242"),t.theme.palette||(t.theme.palette="palette4"));}},{key:"handleUserInputErrors",value:function(t){var e=t;if(e.tooltip.shared&&e.tooltip.intersect)throw new Error("tooltip.shared cannot be enabled when tooltip.intersect is true. Turn off any other option by setting it to false.");if(("bar"===e.chart.type||"rangeBar"===e.chart.type)&&e.plotOptions.bar.horizontal){if(e.yaxis.length>1)throw new Error("Multiple Y Axis for bars are not supported. Switch to column chart by setting plotOptions.bar.horizontal=false");e.yaxis[0].reversed&&(e.yaxis[0].opposite=!0),e.xaxis.tooltip.enabled=!1,e.yaxis[0].tooltip.enabled=!1,e.chart.zoom.enabled=!1;}return "bar"!==e.chart.type&&"rangeBar"!==e.chart.type||e.tooltip.shared&&("barWidth"===e.xaxis.crosshairs.width&&e.series.length>1&&(console.warn('crosshairs.width = "barWidth" is only supported in single series, not in a multi-series barChart.'),e.xaxis.crosshairs.width="tickWidth"),e.plotOptions.bar.horizontal&&(e.states.hover.type="none",e.tooltip.shared=!1),e.tooltip.followCursor||(console.warn("followCursor option in shared columns cannot be turned off. Please set %ctooltip.followCursor: true","color: blue;"),e.tooltip.followCursor=!0)),"candlestick"===e.chart.type&&e.yaxis[0].reversed&&(console.warn("Reversed y-axis in candlestick chart is not supported."),e.yaxis[0].reversed=!1),e.chart.group&&0===e.yaxis[0].labels.minWidth&&console.warn("It looks like you have multiple charts in synchronization. You must provide yaxis.labels.minWidth which must be EQUAL for all grouped charts to prevent incorrect behaviour."),Array.isArray(e.stroke.width)&&"line"!==e.chart.type&&"area"!==e.chart.type&&(console.warn("stroke.width option accepts array only for line and area charts. Reverted back to Number"),e.stroke.width=e.stroke.width[0]),e}}]),i}(),H=function(){function t(){e(this,t);}return a(t,[{key:"initGlobalVars",value:function(t){t.series=[],t.seriesCandleO=[],t.seriesCandleH=[],t.seriesCandleL=[],t.seriesCandleC=[],t.seriesRangeStart=[],t.seriesRangeEnd=[],t.seriesRangeBarTimeline=[],t.seriesPercent=[],t.seriesX=[],t.seriesZ=[],t.seriesNames=[],t.seriesTotals=[],t.seriesLog=[],t.seriesColors=[],t.stackedSeriesTotals=[],t.seriesXvalues=[],t.seriesYvalues=[],t.labels=[],t.categoryLabels=[],t.timescaleLabels=[],t.noLabelsProvided=!1,t.resizeTimer=null,t.selectionResizeTimer=null,t.delayedElements=[],t.pointsArray=[],t.dataLabelsRects=[],t.isXNumeric=!1,t.xaxisLabelsCount=0,t.skipLastTimelinelabel=!1,t.skipFirstTimelinelabel=!1,t.isDataXYZ=!1,t.isMultiLineX=!1,t.isMultipleYAxis=!1,t.maxY=-Number.MAX_VALUE,t.minY=Number.MIN_VALUE,t.minYArr=[],t.maxYArr=[],t.maxX=-Number.MAX_VALUE,t.minX=Number.MAX_VALUE,t.initialMaxX=-Number.MAX_VALUE,t.initialMinX=Number.MAX_VALUE,t.maxDate=0,t.minDate=Number.MAX_VALUE,t.minZ=Number.MAX_VALUE,t.maxZ=-Number.MAX_VALUE,t.minXDiff=Number.MAX_VALUE,t.yAxisScale=[],t.xAxisScale=null,t.xAxisTicksPositions=[],t.yLabelsCoords=[],t.yTitleCoords=[],t.barPadForNumericAxis=0,t.padHorizontal=0,t.xRange=0,t.yRange=[],t.zRange=0,t.dataPoints=0,t.xTickAmount=0;}},{key:"globalVars",value:function(t){return {chartID:null,cuid:null,events:{beforeMount:[],mounted:[],updated:[],clicked:[],selection:[],dataPointSelection:[],zoomed:[],scrolled:[]},colors:[],clientX:null,clientY:null,fill:{colors:[]},stroke:{colors:[]},dataLabels:{style:{colors:[]}},radarPolygons:{fill:{colors:[]}},markers:{colors:[],size:t.markers.size,largestSize:0},animationEnded:!1,isTouchDevice:"ontouchstart"in window||navigator.msMaxTouchPoints,isDirty:!1,isExecCalled:!1,initialConfig:null,initialSeries:[],lastXAxis:[],lastYAxis:[],columnSeries:null,labels:[],timescaleLabels:[],noLabelsProvided:!1,allSeriesCollapsed:!1,collapsedSeries:[],collapsedSeriesIndices:[],ancillaryCollapsedSeries:[],ancillaryCollapsedSeriesIndices:[],risingSeries:[],dataFormatXNumeric:!1,capturedSeriesIndex:-1,capturedDataPointIndex:-1,selectedDataPoints:[],goldenPadding:35,invalidLogScale:!1,ignoreYAxisIndexes:[],yAxisSameScaleIndices:[],maxValsInArrayIndex:0,radialSize:0,selection:void 0,zoomEnabled:"zoom"===t.chart.toolbar.autoSelected&&t.chart.toolbar.tools.zoom&&t.chart.zoom.enabled,panEnabled:"pan"===t.chart.toolbar.autoSelected&&t.chart.toolbar.tools.pan,selectionEnabled:"selection"===t.chart.toolbar.autoSelected&&t.chart.toolbar.tools.selection,yaxis:null,mousedown:!1,lastClientPosition:{},visibleXRange:void 0,yValueDecimal:0,total:0,SVGNS:"http://www.w3.org/2000/svg",svgWidth:0,svgHeight:0,noData:!1,locale:{},dom:{},memory:{methodsToExec:[]},shouldAnimate:!0,skipLastTimelinelabel:!1,skipFirstTimelinelabel:!1,delayedElements:[],axisCharts:!0,isDataXYZ:!1,resized:!1,resizeTimer:null,comboCharts:!1,dataChanged:!1,previousPaths:[],allSeriesHasEqualX:!0,pointsArray:[],dataLabelsRects:[],lastDrawnDataLabelsIndexes:[],hasNullValues:!1,easing:null,zoomed:!1,gridWidth:0,gridHeight:0,rotateXLabels:!1,defaultLabels:!1,xLabelFormatter:void 0,yLabelFormatters:[],xaxisTooltipFormatter:void 0,ttKeyFormatter:void 0,ttVal:void 0,ttZFormatter:void 0,LINE_HEIGHT_RATIO:1.618,xAxisLabelsHeight:0,xAxisLabelsWidth:0,yAxisLabelsWidth:0,scaleX:1,scaleY:1,translateX:0,translateY:0,translateYAxisX:[],yAxisWidths:[],translateXAxisY:0,translateXAxisX:0,tooltip:null}}},{key:"init",value:function(t){var e=this.globalVars(t);return this.initGlobalVars(e),e.initialConfig=f.extend({},t),e.initialSeries=f.clone(t.series),e.lastXAxis=f.clone(e.initialConfig.xaxis),e.lastYAxis=f.clone(e.initialConfig.yaxis),e}}]),t}(),N=function(){function t(i){e(this,t),this.opts=i;}return a(t,[{key:"init",value:function(){var t=new R(this.opts).init({responsiveOverride:!1});return {config:t,globals:(new H).init(t)}}}]),t}(),O=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.twoDSeries=[],this.threeDSeries=[],this.twoDSeriesX=[],this.coreUtils=new w(this.ctx);}return a(t,[{key:"isMultiFormat",value:function(){return this.isFormatXY()||this.isFormat2DArray()}},{key:"isFormatXY",value:function(){var t=this.w.config.series.slice(),e=new E(this.ctx);if(this.activeSeriesIndex=e.getActiveConfigSeriesIndex(),void 0!==t[this.activeSeriesIndex].data&&t[this.activeSeriesIndex].data.length>0&&null!==t[this.activeSeriesIndex].data[0]&&void 0!==t[this.activeSeriesIndex].data[0].x&&null!==t[this.activeSeriesIndex].data[0])return !0}},{key:"isFormat2DArray",value:function(){var t=this.w.config.series.slice(),e=new E(this.ctx);if(this.activeSeriesIndex=e.getActiveConfigSeriesIndex(),void 0!==t[this.activeSeriesIndex].data&&t[this.activeSeriesIndex].data.length>0&&void 0!==t[this.activeSeriesIndex].data[0]&&null!==t[this.activeSeriesIndex].data[0]&&t[this.activeSeriesIndex].data[0].constructor===Array)return !0}},{key:"handleFormat2DArray",value:function(t,e){var i=this.w.config,a=this.w.globals;i.xaxis.sorted&&("datetime"===i.xaxis.type?t[e].data.sort((function(t,e){return new Date(t[0]).getTime()-new Date(e[0]).getTime()})):"numeric"===i.xaxis.type&&t[e].data.sort((function(t,e){return t[0]-e[0]})));for(var s=0;s<t[e].data.length;s++)if(void 0!==t[e].data[s][1]&&(Array.isArray(t[e].data[s][1])&&4===t[e].data[s][1].length?this.twoDSeries.push(f.parseNumber(t[e].data[s][1][3])):5===t[e].data[s].length?this.twoDSeries.push(f.parseNumber(t[e].data[s][4])):this.twoDSeries.push(f.parseNumber(t[e].data[s][1])),a.dataFormatXNumeric=!0),"datetime"===i.xaxis.type){var r=new Date(t[e].data[s][0]);r=new Date(r).getTime(),this.twoDSeriesX.push(r);}else this.twoDSeriesX.push(t[e].data[s][0]);for(var n=0;n<t[e].data.length;n++)void 0!==t[e].data[n][2]&&(this.threeDSeries.push(t[e].data[n][2]),a.isDataXYZ=!0);}},{key:"handleFormatXY",value:function(t,e){var i=this.w.config,a=this.w.globals,s=new y(this.ctx),r=e;a.collapsedSeriesIndices.indexOf(e)>-1&&(r=this.activeSeriesIndex),i.xaxis.sorted&&("datetime"===i.xaxis.type?t[e].data.sort((function(t,e){return new Date(t.x).getTime()-new Date(e.x).getTime()})):"numeric"===i.xaxis.type&&t[e].data.sort((function(t,e){return t.x-e.x})));for(var n=0;n<t[e].data.length;n++)void 0!==t[e].data[n].y&&(Array.isArray(t[e].data[n].y)?this.twoDSeries.push(f.parseNumber(t[e].data[n].y[t[e].data[n].y.length-1])):this.twoDSeries.push(f.parseNumber(t[e].data[n].y)));for(var o=0;o<t[r].data.length;o++){var l="string"==typeof t[r].data[o].x,h=Array.isArray(t[r].data[o].x),c=!h&&!!s.isValidDate(t[r].data[o].x.toString());if(l||c)if(l||i.xaxis.convertedCatToNumeric){var d=a.isBarHorizontal&&a.isRangeData;"datetime"!==i.xaxis.type||d?(this.fallbackToCategory=!0,this.twoDSeriesX.push(t[r].data[o].x)):this.twoDSeriesX.push(s.parseDate(t[r].data[o].x));}else "datetime"===i.xaxis.type?this.twoDSeriesX.push(s.parseDate(t[r].data[o].x.toString())):(a.dataFormatXNumeric=!0,a.isXNumeric=!0,this.twoDSeriesX.push(parseFloat(t[r].data[o].x)));else h?(this.fallbackToCategory=!0,this.twoDSeriesX.push(t[r].data[o].x)):(a.isXNumeric=!0,a.dataFormatXNumeric=!0,this.twoDSeriesX.push(t[r].data[o].x));}if(t[e].data[0]&&void 0!==t[e].data[0].z){for(var g=0;g<t[e].data.length;g++)this.threeDSeries.push(t[e].data[g].z);a.isDataXYZ=!0;}}},{key:"handleRangeData",value:function(t,e){var i=this.w.config,a=this.w.globals,s={};return this.isFormat2DArray()?s=this.handleRangeDataFormat("array",t,e):this.isFormatXY()&&(s=this.handleRangeDataFormat("xy",t,e)),a.seriesRangeStart.push(s.start),a.seriesRangeEnd.push(s.end),"datetime"===i.xaxis.type&&a.seriesRangeBarTimeline.push(s.rangeUniques),a.seriesRangeBarTimeline.forEach((function(t,e){t&&t.forEach((function(t,e){t.y.forEach((function(e,i){for(var a=0;a<t.y.length;a++)if(i!==a){var s=e.y1,r=e.y2,n=t.y[a].y1;s<=t.y[a].y2&&n<=r&&(t.overlaps.indexOf(e.rangeName)<0&&t.overlaps.push(e.rangeName),t.overlaps.indexOf(t.y[a].rangeName)<0&&t.overlaps.push(t.y[a].rangeName));}}));}));})),s}},{key:"handleCandleStickData",value:function(t,e){var i=this.w.globals,a={};return this.isFormat2DArray()?a=this.handleCandleStickDataFormat("array",t,e):this.isFormatXY()&&(a=this.handleCandleStickDataFormat("xy",t,e)),i.seriesCandleO[e]=a.o,i.seriesCandleH[e]=a.h,i.seriesCandleL[e]=a.l,i.seriesCandleC[e]=a.c,a}},{key:"handleRangeDataFormat",value:function(t,e,i){var a=[],s=[],r=e[i].data.filter((function(t,e,i){return e===i.findIndex((function(e){return e.x===t.x}))})).map((function(t,e){return {x:t.x,overlaps:[],y:[]}})),n="Please provide [Start, End] values in valid format. Read more https://apexcharts.com/docs/series/#rangecharts",o=new E(this.ctx).getActiveConfigSeriesIndex();if("array"===t){if(2!==e[o].data[0][1].length)throw new Error(n);for(var l=0;l<e[i].data.length;l++)a.push(e[i].data[l][1][0]),s.push(e[i].data[l][1][1]);}else if("xy"===t){if(2!==e[o].data[0].y.length)throw new Error(n);for(var h=function(t){var n=f.randomId(),o=e[i].data[t].x,l={y1:e[i].data[t].y[0],y2:e[i].data[t].y[1],rangeName:n};e[i].data[t].rangeName=n;var h=r.findIndex((function(t){return t.x===o}));r[h].y.push(l),a.push(l.y1),s.push(l.y2);},c=0;c<e[i].data.length;c++)h(c);}return {start:a,end:s,rangeUniques:r}}},{key:"handleCandleStickDataFormat",value:function(t,e,i){var a=this.w,s=[],r=[],n=[],o=[],l="Please provide [Open, High, Low and Close] values in valid format. Read more https://apexcharts.com/docs/series/#candlestick";if("array"===t){if(!Array.isArray(e[i].data[0][1])&&5!==e[i].data[0].length||Array.isArray(e[i].data[0][1])&&4!==e[i].data[0][1].length)throw new Error(l);if(5===e[i].data[0].length)for(var h=0;h<e[i].data.length;h++)s.push(e[i].data[h][1]),r.push(e[i].data[h][2]),n.push(e[i].data[h][3]),o.push(e[i].data[h][4]);else for(var c=0;c<e[i].data.length;c++)s.push(e[i].data[c][1][0]),r.push(e[i].data[c][1][1]),n.push(e[i].data[c][1][2]),o.push(e[i].data[c][1][3]);}else if("xy"===t){if(!a.globals.comboCharts&&4!==e[i].data[0].y.length||a.globals.comboCharts&&"candlestick"===e[i].type&&e[i].data.length&&4!==e[i].data[0].y.length)throw new Error(l);for(var d=0;d<e[i].data.length;d++)s.push(e[i].data[d].y[0]),r.push(e[i].data[d].y[1]),n.push(e[i].data[d].y[2]),o.push(e[i].data[d].y[3]);}return {o:s,h:r,l:n,c:o}}},{key:"parseDataAxisCharts",value:function(t){for(var e=this,i=arguments.length>1&&void 0!==arguments[1]?arguments[1]:this.ctx,a=this.w.config,s=this.w.globals,r=new y(i),n=a.labels.length>0?a.labels.slice():a.xaxis.categories.slice(),o=function(){for(var t=0;t<n.length;t++)if("string"==typeof n[t]){if(!r.isValidDate(n[t]))throw new Error("You have provided invalid Date format. Please provide a valid JavaScript Date");e.twoDSeriesX.push(r.parseDate(n[t]));}else e.twoDSeriesX.push(n[t]);},l=0;l<t.length;l++){if(this.twoDSeries=[],this.twoDSeriesX=[],this.threeDSeries=[],void 0===t[l].data)return void console.error("It is a possibility that you may have not included 'data' property in series.");if("rangeBar"!==a.chart.type&&"rangeArea"!==a.chart.type&&"rangeBar"!==t[l].type&&"rangeArea"!==t[l].type||(s.isRangeData=!0,this.handleRangeData(t,l)),this.isMultiFormat())this.isFormat2DArray()?this.handleFormat2DArray(t,l):this.isFormatXY()&&this.handleFormatXY(t,l),"candlestick"!==a.chart.type&&"candlestick"!==t[l].type||this.handleCandleStickData(t,l),s.series.push(this.twoDSeries),s.labels.push(this.twoDSeriesX),s.seriesX.push(this.twoDSeriesX),l!==this.activeSeriesIndex||this.fallbackToCategory||(s.isXNumeric=!0);else {"datetime"===a.xaxis.type?(s.isXNumeric=!0,o(),s.seriesX.push(this.twoDSeriesX)):"numeric"===a.xaxis.type&&(s.isXNumeric=!0,n.length>0&&(this.twoDSeriesX=n,s.seriesX.push(this.twoDSeriesX))),s.labels.push(this.twoDSeriesX);var h=t[l].data.map((function(t){return f.parseNumber(t)}));s.series.push(h);}s.seriesZ.push(this.threeDSeries),void 0!==t[l].name?s.seriesNames.push(t[l].name):s.seriesNames.push("series-"+parseInt(l+1,10)),void 0!==t[l].color?s.seriesColors.push(t[l].color):s.seriesColors.push(void 0);}return this.w}},{key:"parseDataNonAxisCharts",value:function(t){var e=this.w.globals,i=this.w.config;e.series=t.slice(),e.seriesNames=i.labels.slice();for(var a=0;a<e.series.length;a++)void 0===e.seriesNames[a]&&e.seriesNames.push("series-"+(a+1));return this.w}},{key:"handleExternalLabelsData",value:function(t){var e=this.w.config,i=this.w.globals;if(e.xaxis.categories.length>0)i.labels=e.xaxis.categories;else if(e.labels.length>0)i.labels=e.labels.slice();else if(this.fallbackToCategory){if(i.labels=i.labels[0],i.seriesRangeBarTimeline.length&&(i.seriesRangeBarTimeline.map((function(t){t.forEach((function(t){i.labels.indexOf(t.x)<0&&t.x&&i.labels.push(t.x);}));})),i.labels=i.labels.filter((function(t,e,i){return i.indexOf(t)===e}))),e.xaxis.convertedCatToNumeric)new D(e).convertCatToNumericXaxis(e,this.ctx,i.seriesX[0]),this._generateExternalLabels(t);}else this._generateExternalLabels(t);}},{key:"_generateExternalLabels",value:function(t){var e=this.w.globals,i=this.w.config,a=[];if(e.axisCharts){if(e.series.length>0)for(var s=0;s<e.series[e.maxValsInArrayIndex].length;s++)a.push(s+1);e.seriesX=[];for(var r=0;r<t.length;r++)e.seriesX.push(a);e.isXNumeric=!0;}if(0===a.length){a=e.axisCharts?[]:e.series.map((function(t,e){return e+1}));for(var n=0;n<t.length;n++)e.seriesX.push(a);}e.labels=a,i.xaxis.convertedCatToNumeric&&(e.categoryLabels=a.map((function(t){return i.xaxis.labels.formatter(t)}))),e.noLabelsProvided=!0;}},{key:"parseData",value:function(t){var e=this.w,i=e.config,a=e.globals;if(this.excludeCollapsedSeriesInYAxis(),this.fallbackToCategory=!1,this.ctx.core.resetGlobals(),this.ctx.core.isMultipleY(),a.axisCharts?this.parseDataAxisCharts(t):this.parseDataNonAxisCharts(t),this.coreUtils.getLargestSeries(),"bar"===i.chart.type&&i.chart.stacked){var s=new E(this.ctx);a.series=s.setNullSeriesToZeroValues(a.series);}this.coreUtils.getSeriesTotals(),a.axisCharts&&this.coreUtils.getStackedSeriesTotals(),this.coreUtils.getPercentSeries(),a.dataFormatXNumeric||a.isXNumeric&&("numeric"!==i.xaxis.type||0!==i.labels.length||0!==i.xaxis.categories.length)||this.handleExternalLabelsData(t);for(var r=this.coreUtils.getCategoryLabels(a.labels),n=0;n<r.length;n++)if(Array.isArray(r[n])){a.isMultiLineX=!0;break}}},{key:"excludeCollapsedSeriesInYAxis",value:function(){var t=this,e=this.w;e.globals.ignoreYAxisIndexes=e.globals.collapsedSeries.map((function(i,a){if(t.w.globals.isMultipleYAxis&&!e.config.chart.stacked)return i.index}));}}]),t}(),W=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.tooltipKeyFormat="dd MMM";}return a(t,[{key:"xLabelFormat",value:function(t,e,i,a){var s=this.w;if("datetime"===s.config.xaxis.type&&void 0===s.config.xaxis.labels.formatter&&void 0===s.config.tooltip.x.formatter){var r=new y(this.ctx);return r.formatDate(r.getDate(e),s.config.tooltip.x.format)}return t(e,i,a)}},{key:"defaultGeneralFormatter",value:function(t){return Array.isArray(t)?t.map((function(t){return t})):t}},{key:"defaultYFormatter",value:function(t,e,i){var a=this.w;return f.isNumber(t)&&(t=0!==a.globals.yValueDecimal?t.toFixed(void 0!==e.decimalsInFloat?e.decimalsInFloat:a.globals.yValueDecimal):a.globals.maxYArr[i]-a.globals.minYArr[i]<10?t.toFixed(1):t.toFixed(0)),t}},{key:"setLabelFormatters",value:function(){var t=this,e=this.w;return e.globals.xLabelFormatter=function(e){return t.defaultGeneralFormatter(e)},e.globals.xaxisTooltipFormatter=function(e){return t.defaultGeneralFormatter(e)},e.globals.ttKeyFormatter=function(e){return t.defaultGeneralFormatter(e)},e.globals.ttZFormatter=function(t){return t},e.globals.legendFormatter=function(e){return t.defaultGeneralFormatter(e)},void 0!==e.config.xaxis.labels.formatter?e.globals.xLabelFormatter=e.config.xaxis.labels.formatter:e.globals.xLabelFormatter=function(t){if(f.isNumber(t)){if(!e.config.xaxis.convertedCatToNumeric&&"numeric"===e.config.xaxis.type&&e.globals.dataPoints<50)return t.toFixed(1);if(e.globals.isBarHorizontal)if(e.globals.maxY-e.globals.minYArr<4)return t.toFixed(1);return t.toFixed(0)}return t},"function"==typeof e.config.tooltip.x.formatter?e.globals.ttKeyFormatter=e.config.tooltip.x.formatter:e.globals.ttKeyFormatter=e.globals.xLabelFormatter,"function"==typeof e.config.xaxis.tooltip.formatter&&(e.globals.xaxisTooltipFormatter=e.config.xaxis.tooltip.formatter),(Array.isArray(e.config.tooltip.y)||void 0!==e.config.tooltip.y.formatter)&&(e.globals.ttVal=e.config.tooltip.y),void 0!==e.config.tooltip.z.formatter&&(e.globals.ttZFormatter=e.config.tooltip.z.formatter),void 0!==e.config.legend.formatter&&(e.globals.legendFormatter=e.config.legend.formatter),e.config.yaxis.forEach((function(i,a){void 0!==i.labels.formatter?e.globals.yLabelFormatters[a]=i.labels.formatter:e.globals.yLabelFormatters[a]=function(s){return e.globals.xyCharts?Array.isArray(s)?s.map((function(e){return t.defaultYFormatter(e,i,a)})):t.defaultYFormatter(s,i,a):s};})),e.globals}},{key:"heatmapLabelFormatters",value:function(){var t=this.w;if("heatmap"===t.config.chart.type){t.globals.yAxisScale[0].result=t.globals.seriesNames.slice();var e=t.globals.seriesNames.reduce((function(t,e){return t.length>e.length?t:e}),0);t.globals.yAxisScale[0].niceMax=e,t.globals.yAxisScale[0].niceMin=e;}}}]),t}(),B=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"getLabel",value:function(t,e,i,a){var s=arguments.length>4&&void 0!==arguments[4]?arguments[4]:[],r=arguments.length>5&&void 0!==arguments[5]?arguments[5]:"12px",n=this.w,o=void 0===t[a]?"":t[a],l=o,h=n.globals.xLabelFormatter,c=n.config.xaxis.labels.formatter,d=!1,g=new W(this.ctx),u=o;l=g.xLabelFormat(h,o,u,{i:a,dateFormatter:new y(this.ctx).formatDate,w:n}),void 0!==c&&(l=c(o,t[a],{i:a,dateFormatter:new y(this.ctx).formatDate,w:n}));var f=function(t){var i=null;return e.forEach((function(t){"month"===t.unit?i="year":"day"===t.unit?i="month":"hour"===t.unit?i="day":"minute"===t.unit&&(i="hour");})),i===t};e.length>0?(d=f(e[a].unit),i=e[a].position,l=e[a].value):"datetime"===n.config.xaxis.type&&void 0===c&&(l=""),void 0===l&&(l=""),l=Array.isArray(l)?l:l.toString();var p=new b(this.ctx),x={};x=n.globals.rotateXLabels?p.getTextRects(l,parseInt(r,10),null,"rotate(".concat(n.config.xaxis.labels.rotate," 0 0)"),!1):p.getTextRects(l,parseInt(r,10));var m=!n.config.xaxis.labels.showDuplicates&&this.ctx.timeScale&&"hours"!==this.ctx.timeScale.tickInterval&&"minutes"!==this.ctx.timeScale.tickInterval;return !Array.isArray(l)&&(0===l.indexOf("NaN")||0===l.toLowerCase().indexOf("invalid")||l.toLowerCase().indexOf("infinity")>=0||s.indexOf(l)>=0&&m)&&(l=""),{x:i,text:l,textRect:x,isBold:d}}},{key:"checkLabelBasedOnTickamount",value:function(t,e,i){var a=this.w,s=a.config.xaxis.tickAmount;return "dataPoints"===s&&(s=Math.round(a.globals.gridWidth/120)),s>i||t%Math.round(i/(s+1))==0||(e.text=""),e}},{key:"checkForOverflowingLabels",value:function(t,e,i,a,s){var r=this.w;if(0===t&&r.globals.skipFirstTimelinelabel&&(e.text=""),t===i-1&&r.globals.skipLastTimelinelabel&&(e.text=""),r.config.xaxis.labels.hideOverlappingLabels&&a.length>0){var n=s[s.length-1];e.x<n.textRect.width/(r.globals.rotateXLabels?Math.abs(r.config.xaxis.labels.rotate)/12:1.01)+n.x&&(e.text="");}return e}},{key:"checkForReversedLabels",value:function(t,e){var i=this.w;return i.config.yaxis[t]&&i.config.yaxis[t].reversed&&e.reverse(),e}},{key:"isYAxisHidden",value:function(t){var e=this.w,i=new w(this.ctx);return !e.config.yaxis[t].show||!e.config.yaxis[t].showForNullSeries&&i.isSeriesNull(t)&&-1===e.globals.collapsedSeriesIndices.indexOf(t)}},{key:"getYAxisForeColor",value:function(t,e){var i=this.w;return Array.isArray(t)&&i.globals.yAxisScale[e]&&this.ctx.theme.pushExtraColors(t,i.globals.yAxisScale[e].result.length,!1),t}},{key:"drawYAxisTicks",value:function(t,e,i,a,s,r,n){var o=this.w,l=new b(this.ctx),h=o.globals.translateY;if(a.show&&e>0){!0===o.config.yaxis[s].opposite&&(t+=a.width);for(var c=e;c>=0;c--){var d=h+e/10+o.config.yaxis[s].labels.offsetY-1;o.globals.isBarHorizontal&&(d=r*c),"heatmap"===o.config.chart.type&&(d+=r/2);var g=l.drawLine(t+i.offsetX-a.width+a.offsetX,d+a.offsetY,t+i.offsetX+a.offsetX,d+a.offsetY,a.color);n.add(g),h+=r;}}}}]),t}(),V=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"fixSvgStringForIe11",value:function(t){if(!f.isIE11())return t;var e=0,i=t.replace(/xmlns="http:\/\/www.w3.org\/2000\/svg"/g,(function(t){return 2===++e?'xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:svgjs="http://svgjs.com/svgjs"':t}));return i=(i=i.replace(/xmlns:NS\d+=""/g,"")).replace(/NS\d+:(\w+:\w+=")/g,"$1")}},{key:"getSvgString",value:function(){var t=this.w.globals.dom.Paper.svg();return this.fixSvgStringForIe11(t)}},{key:"cleanup",value:function(){var t=this.w,e=t.globals.dom.baseEl.getElementsByClassName("apexcharts-xcrosshairs"),i=t.globals.dom.baseEl.getElementsByClassName("apexcharts-ycrosshairs"),a=t.globals.dom.baseEl.querySelectorAll(".apexcharts-zoom-rect, .apexcharts-selection-rect");Array.prototype.forEach.call(a,(function(t){t.setAttribute("width",0);})),e&&e[0]&&(e[0].setAttribute("x",-500),e[0].setAttribute("x1",-500),e[0].setAttribute("x2",-500)),i&&i[0]&&(i[0].setAttribute("y",-100),i[0].setAttribute("y1",-100),i[0].setAttribute("y2",-100));}},{key:"svgUrl",value:function(){this.cleanup();var t=this.getSvgString(),e=new Blob([t],{type:"image/svg+xml;charset=utf-8"});return URL.createObjectURL(e)}},{key:"dataURI",value:function(){var t=this;return new Promise((function(e){var i=t.w;t.cleanup();var a=document.createElement("canvas");a.width=i.globals.svgWidth,a.height=parseInt(i.globals.dom.elWrap.style.height,10);var s="transparent"===i.config.chart.background?"#fff":i.config.chart.background,r=a.getContext("2d");r.fillStyle=s,r.fillRect(0,0,a.width,a.height);var n=t.getSvgString();if(window.canvg&&f.isIE11()){var o=window.canvg.Canvg.fromString(r,n,{ignoreClear:!0,ignoreDimensions:!0});o.start();var l=a.msToBlob();o.stop(),e({blob:l});}else {var h="data:image/svg+xml,"+encodeURIComponent(n),c=new Image;c.crossOrigin="anonymous",c.onload=function(){if(r.drawImage(c,0,0),a.msToBlob){var t=a.msToBlob();e({blob:t});}else {var i=a.toDataURL("image/png");e({imgURI:i});}},c.src=h;}}))}},{key:"exportToSVG",value:function(){this.triggerDownload(this.svgUrl(),null,".svg");}},{key:"exportToPng",value:function(){var t=this;this.dataURI().then((function(e){var i=e.imgURI,a=e.blob;a?navigator.msSaveOrOpenBlob(a,t.w.globals.chartID+".png"):t.triggerDownload(i,null,".png");}));}},{key:"exportToCSV",value:function(t){var e=this,i=t.series,a=t.columnDelimiter,s=t.lineDelimiter,r=void 0===s?"\n":s,n=this.w,o=[],l=[],h="data:text/csv;charset=utf-8,",c=new O(this.ctx),d=new B(this.ctx),g=function(t){var i="";if(n.globals.axisCharts){if("category"===n.config.xaxis.type||n.config.xaxis.convertedCatToNumeric)if(n.globals.isBarHorizontal){var a=n.globals.yLabelFormatters[0],s=new E(e.ctx).getActiveConfigSeriesIndex();i=a(n.globals.labels[t],{seriesIndex:s,dataPointIndex:t,w:n});}else i=d.getLabel(n.globals.labels,n.globals.timescaleLabels,0,t).text;"datetime"===n.config.xaxis.type&&(n.config.xaxis.categories.length?i=n.config.xaxis.categories[t]:n.config.labels.length&&(i=n.config.labels[t]));}else i=n.config.labels[t];return i};o.push(n.config.chart.toolbar.export.csv.headerCategory),i.map((function(t,e){n.globals.axisCharts&&o.push(t.name?t.name:"series-".concat(e));})),n.globals.axisCharts||(o.push(n.config.chart.toolbar.export.csv.headerValue),l.push(o.join(a))),i.map((function(t,e){n.globals.axisCharts?function(t,e){if(o.length&&0===e&&l.push(o.join(a)),t.data&&t.data.length)for(var s=0;s<t.data.length;s++){o=[];var r=g(s);if(r||(c.isFormatXY()?r=i[e].data[s].x:c.isFormat2DArray()&&(r=i[e].data[s]?i[e].data[s][0]:"")),0===e){o.push((d=r,"datetime"===n.config.xaxis.type&&String(d).length>=10?n.config.chart.toolbar.export.csv.dateFormatter(r):r));for(var h=0;h<n.globals.series.length;h++)o.push(n.globals.series[h][s]);}("candlestick"===n.config.chart.type||t.type&&"candlestick"===t.type)&&(o.pop(),o.push(n.globals.seriesCandleO[e][s]),o.push(n.globals.seriesCandleH[e][s]),o.push(n.globals.seriesCandleL[e][s]),o.push(n.globals.seriesCandleC[e][s])),"rangeBar"===n.config.chart.type&&(o.pop(),o.push(n.globals.seriesRangeStart[e][s]),o.push(n.globals.seriesRangeEnd[e][s])),o.length&&l.push(o.join(a));}var d;}(t,e):((o=[]).push(n.globals.labels[e]),o.push(n.globals.series[e]),l.push(o.join(a)));})),h+=l.join(r),this.triggerDownload(encodeURI(h),n.config.chart.toolbar.export.csv.filename,".csv");}},{key:"triggerDownload",value:function(t,e,i){var a=document.createElement("a");a.href=t,a.download=(e||this.w.globals.chartID)+i,document.body.appendChild(a),a.click(),document.body.removeChild(a);}}]),t}(),G=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;var a=this.w;this.axesUtils=new B(i),this.xaxisLabels=a.globals.labels.slice(),a.globals.timescaleLabels.length>0&&!a.globals.isBarHorizontal&&(this.xaxisLabels=a.globals.timescaleLabels.slice()),this.drawnLabels=[],this.drawnLabelsRects=[],"top"===a.config.xaxis.position?this.offY=0:this.offY=a.globals.gridHeight+1,this.offY=this.offY+a.config.xaxis.axisBorder.offsetY,this.isCategoryBarHorizontal="bar"===a.config.chart.type&&a.config.plotOptions.bar.horizontal,this.xaxisFontSize=a.config.xaxis.labels.style.fontSize,this.xaxisFontFamily=a.config.xaxis.labels.style.fontFamily,this.xaxisForeColors=a.config.xaxis.labels.style.colors,this.xaxisBorderWidth=a.config.xaxis.axisBorder.width,this.isCategoryBarHorizontal&&(this.xaxisBorderWidth=a.config.yaxis[0].axisBorder.width.toString()),this.xaxisBorderWidth.indexOf("%")>-1?this.xaxisBorderWidth=a.globals.gridWidth*parseInt(this.xaxisBorderWidth,10)/100:this.xaxisBorderWidth=parseInt(this.xaxisBorderWidth,10),this.xaxisBorderHeight=a.config.xaxis.axisBorder.height,this.yaxis=a.config.yaxis[0];}return a(t,[{key:"drawXaxis",value:function(){var t,e=this,i=this.w,a=new b(this.ctx),s=a.group({class:"apexcharts-xaxis",transform:"translate(".concat(i.config.xaxis.offsetX,", ").concat(i.config.xaxis.offsetY,")")}),r=a.group({class:"apexcharts-xaxis-texts-g",transform:"translate(".concat(i.globals.translateXAxisX,", ").concat(i.globals.translateXAxisY,")")});s.add(r);for(var n=i.globals.padHorizontal,o=[],l=0;l<this.xaxisLabels.length;l++)o.push(this.xaxisLabels[l]);var h=o.length;if(i.globals.isXNumeric){var c=h>1?h-1:h;t=i.globals.gridWidth/c,n=n+t/2+i.config.xaxis.labels.offsetX;}else t=i.globals.gridWidth/o.length,n=n+t+i.config.xaxis.labels.offsetX;if(i.config.xaxis.labels.show)for(var d=function(s){var l=n-t/2+i.config.xaxis.labels.offsetX;0===s&&1===h&&t/2===n&&1===i.globals.dataPoints&&(l=i.globals.gridWidth/2);var c=e.axesUtils.getLabel(o,i.globals.timescaleLabels,l,s,e.drawnLabels,e.xaxisFontSize),d=28;i.globals.rotateXLabels&&(d=22);(c=void 0!==i.config.xaxis.tickAmount&&"dataPoints"!==i.config.xaxis.tickAmount&&"datetime"!==i.config.xaxis.type?e.axesUtils.checkLabelBasedOnTickamount(s,c,h):e.axesUtils.checkForOverflowingLabels(s,c,h,e.drawnLabels,e.drawnLabelsRects)).text&&i.globals.xaxisLabelsCount++;var g=a.drawText({x:c.x,y:e.offY+i.config.xaxis.labels.offsetY+d-("top"===i.config.xaxis.position?i.globals.xAxisHeight+i.config.xaxis.axisTicks.height-2:0),text:c.text,textAnchor:"middle",fontWeight:c.isBold?600:i.config.xaxis.labels.style.fontWeight,fontSize:e.xaxisFontSize,fontFamily:e.xaxisFontFamily,foreColor:Array.isArray(e.xaxisForeColors)?i.config.xaxis.convertedCatToNumeric?e.xaxisForeColors[i.globals.minX+s-1]:e.xaxisForeColors[s]:e.xaxisForeColors,isPlainText:!1,cssClass:"apexcharts-xaxis-label "+i.config.xaxis.labels.style.cssClass});r.add(g);var u=document.createElementNS(i.globals.SVGNS,"title");u.textContent=Array.isArray(c.text)?c.text.join(" "):c.text,g.node.appendChild(u),""!==c.text&&(e.drawnLabels.push(c.text),e.drawnLabelsRects.push(c)),n+=t;},g=0;g<=h-1;g++)d(g);if(void 0!==i.config.xaxis.title.text){var u=a.group({class:"apexcharts-xaxis-title"}),f=a.drawText({x:i.globals.gridWidth/2+i.config.xaxis.title.offsetX,y:this.offY-parseFloat(this.xaxisFontSize)+i.globals.xAxisLabelsHeight+i.config.xaxis.title.offsetY,text:i.config.xaxis.title.text,textAnchor:"middle",fontSize:i.config.xaxis.title.style.fontSize,fontFamily:i.config.xaxis.title.style.fontFamily,fontWeight:i.config.xaxis.title.style.fontWeight,foreColor:i.config.xaxis.title.style.color,cssClass:"apexcharts-xaxis-title-text "+i.config.xaxis.title.style.cssClass});u.add(f),s.add(u);}if(i.config.xaxis.axisBorder.show){var p=i.globals.barPadForNumericAxis,x=a.drawLine(i.globals.padHorizontal+i.config.xaxis.axisBorder.offsetX-p,this.offY,this.xaxisBorderWidth+p,this.offY,i.config.xaxis.axisBorder.color,0,this.xaxisBorderHeight);s.add(x);}return s}},{key:"drawXaxisInversed",value:function(t){var e,i,a=this,s=this.w,r=new b(this.ctx),n=s.config.yaxis[0].opposite?s.globals.translateYAxisX[t]:0,o=r.group({class:"apexcharts-yaxis apexcharts-xaxis-inversed",rel:t}),l=r.group({class:"apexcharts-yaxis-texts-g apexcharts-xaxis-inversed-texts-g",transform:"translate("+n+", 0)"});o.add(l);var h=[];if(s.config.yaxis[t].show)for(var c=0;c<this.xaxisLabels.length;c++)h.push(this.xaxisLabels[c]);e=s.globals.gridHeight/h.length,i=-e/2.2;var d=s.globals.yLabelFormatters[0],g=s.config.yaxis[0].labels;if(g.show)for(var u=function(n){var o=void 0===h[n]?"":h[n];o=d(o,{seriesIndex:t,dataPointIndex:n,w:s});var c=a.axesUtils.getYAxisForeColor(g.style.colors,t),u=0;Array.isArray(o)&&(u=o.length/2*parseInt(g.style.fontSize,10));var f=r.drawText({x:g.offsetX-15,y:i+e+g.offsetY-u,text:o,textAnchor:a.yaxis.opposite?"start":"end",foreColor:Array.isArray(c)?c[n]:c,fontSize:g.style.fontSize,fontFamily:g.style.fontFamily,fontWeight:g.style.fontWeight,isPlainText:!1,cssClass:"apexcharts-yaxis-label "+g.style.cssClass});l.add(f);var p=document.createElementNS(s.globals.SVGNS,"title");if(p.textContent=o.text,f.node.appendChild(p),0!==s.config.yaxis[t].labels.rotate){var x=r.rotateAroundCenter(f.node);f.node.setAttribute("transform","rotate(".concat(s.config.yaxis[t].labels.rotate," 0 ").concat(x.y,")"));}i+=e;},f=0;f<=h.length-1;f++)u(f);if(void 0!==s.config.yaxis[0].title.text){var p=r.group({class:"apexcharts-yaxis-title apexcharts-xaxis-title-inversed",transform:"translate("+n+", 0)"}),x=r.drawText({x:0,y:s.globals.gridHeight/2,text:s.config.yaxis[0].title.text,textAnchor:"middle",foreColor:s.config.yaxis[0].title.style.color,fontSize:s.config.yaxis[0].title.style.fontSize,fontWeight:s.config.yaxis[0].title.style.fontWeight,fontFamily:s.config.yaxis[0].title.style.fontFamily,cssClass:"apexcharts-yaxis-title-text "+s.config.yaxis[0].title.style.cssClass});p.add(x),o.add(p);}var m=0;this.isCategoryBarHorizontal&&s.config.yaxis[0].opposite&&(m=s.globals.gridWidth);var v=s.config.xaxis.axisBorder;if(v.show){var y=r.drawLine(s.globals.padHorizontal+v.offsetX+m,1+v.offsetY,s.globals.padHorizontal+v.offsetX+m,s.globals.gridHeight+v.offsetY,v.color,0);o.add(y);}return s.config.yaxis[0].axisTicks.show&&this.axesUtils.drawYAxisTicks(m,h.length,s.config.yaxis[0].axisBorder,s.config.yaxis[0].axisTicks,0,e,o),o}},{key:"drawXaxisTicks",value:function(t,e){var i=this.w,a=t;if(!(t<0||t-2>i.globals.gridWidth)){var s=this.offY+i.config.xaxis.axisTicks.offsetY,r=s+i.config.xaxis.axisTicks.height;if("top"===i.config.xaxis.position&&(r=s-i.config.xaxis.axisTicks.height),i.config.xaxis.axisTicks.show){var n=new b(this.ctx).drawLine(t+i.config.xaxis.axisTicks.offsetX,s+i.config.xaxis.offsetY,a+i.config.xaxis.axisTicks.offsetX,r+i.config.xaxis.offsetY,i.config.xaxis.axisTicks.color);e.add(n),n.node.classList.add("apexcharts-xaxis-tick");}}}},{key:"getXAxisTicksPositions",value:function(){var t=this.w,e=[],i=this.xaxisLabels.length,a=t.globals.padHorizontal;if(t.globals.timescaleLabels.length>0)for(var s=0;s<i;s++)a=this.xaxisLabels[s].position,e.push(a);else for(var r=i,n=0;n<r;n++){var o=r;t.globals.isXNumeric&&"bar"!==t.config.chart.type&&(o-=1),a+=t.globals.gridWidth/o,e.push(a);}return e}},{key:"xAxisLabelCorrections",value:function(){var t=this.w,e=new b(this.ctx),i=t.globals.dom.baseEl.querySelector(".apexcharts-xaxis-texts-g"),a=t.globals.dom.baseEl.querySelectorAll(".apexcharts-xaxis-texts-g text"),s=t.globals.dom.baseEl.querySelectorAll(".apexcharts-yaxis-inversed text"),r=t.globals.dom.baseEl.querySelectorAll(".apexcharts-xaxis-inversed-texts-g text tspan");if(t.globals.rotateXLabels||t.config.xaxis.labels.rotateAlways)for(var n=0;n<a.length;n++){var o=e.rotateAroundCenter(a[n]);o.y=o.y-1,o.x=o.x+1,a[n].setAttribute("transform","rotate(".concat(t.config.xaxis.labels.rotate," ").concat(o.x," ").concat(o.y,")")),a[n].setAttribute("text-anchor","end");i.setAttribute("transform","translate(0, ".concat(-10,")"));var l=a[n].childNodes;t.config.xaxis.labels.trim&&Array.prototype.forEach.call(l,(function(i){e.placeTextWithEllipsis(i,i.textContent,t.config.xaxis.labels.maxHeight-("bottom"===t.config.legend.position?20:10));}));}else !function(){for(var i=t.globals.gridWidth/(t.globals.labels.length+1),s=0;s<a.length;s++){var r=a[s].childNodes;t.config.xaxis.labels.trim&&"datetime"!==t.config.xaxis.type&&Array.prototype.forEach.call(r,(function(t){e.placeTextWithEllipsis(t,t.textContent,i);}));}}();if(s.length>0){var h=s[s.length-1].getBBox(),c=s[0].getBBox();h.x<-20&&s[s.length-1].parentNode.removeChild(s[s.length-1]),c.x+c.width>t.globals.gridWidth&&!t.globals.isBarHorizontal&&s[0].parentNode.removeChild(s[0]);for(var d=0;d<r.length;d++)e.placeTextWithEllipsis(r[d],r[d].textContent,t.config.yaxis[0].labels.maxWidth-2*parseFloat(t.config.yaxis[0].title.style.fontSize)-20);}}}]),t}(),_=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;var a=this.w;this.xaxisLabels=a.globals.labels.slice(),this.axesUtils=new B(i),this.isTimelineBar="datetime"===a.config.xaxis.type&&a.globals.seriesRangeBarTimeline.length,a.globals.timescaleLabels.length>0&&(this.xaxisLabels=a.globals.timescaleLabels.slice());}return a(t,[{key:"drawGridArea",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:null,e=this.w,i=new b(this.ctx);null===t&&(t=i.group({class:"apexcharts-grid"}));var a=i.drawLine(e.globals.padHorizontal,1,e.globals.padHorizontal,e.globals.gridHeight,"transparent"),s=i.drawLine(e.globals.padHorizontal,e.globals.gridHeight,e.globals.gridWidth,e.globals.gridHeight,"transparent");return t.add(s),t.add(a),t}},{key:"drawGrid",value:function(){var t=null;return this.w.globals.axisCharts&&(t=this.renderGrid(),this.drawGridArea(t.el)),t}},{key:"createGridMask",value:function(){var t=this.w,e=t.globals,i=new b(this.ctx),a=Array.isArray(t.config.stroke.width)?0:t.config.stroke.width;if(Array.isArray(t.config.stroke.width)){var s=0;t.config.stroke.width.forEach((function(t){s=Math.max(s,t);})),a=s;}e.dom.elGridRectMask=document.createElementNS(e.SVGNS,"clipPath"),e.dom.elGridRectMask.setAttribute("id","gridRectMask".concat(e.cuid)),e.dom.elGridRectMarkerMask=document.createElementNS(e.SVGNS,"clipPath"),e.dom.elGridRectMarkerMask.setAttribute("id","gridRectMarkerMask".concat(e.cuid));var r=t.config.chart.type,n=0,o=0;("bar"===r||"rangeBar"===r||t.globals.comboBarCount>0)&&t.globals.isXNumeric&&!t.globals.isBarHorizontal&&(n=t.config.grid.padding.left,o=t.config.grid.padding.right,e.barPadForNumericAxis>n&&(n=e.barPadForNumericAxis,o=e.barPadForNumericAxis)),e.dom.elGridRect=i.drawRect(-a/2-n-2,-a/2,e.gridWidth+a+o+n+4,e.gridHeight+a,0,"#fff"),new w(this).getLargestMarkerSize();var l=t.globals.markers.largestSize+1;e.dom.elGridRectMarker=i.drawRect(2*-l,2*-l,e.gridWidth+4*l,e.gridHeight+4*l,0,"#fff"),e.dom.elGridRectMask.appendChild(e.dom.elGridRect.node),e.dom.elGridRectMarkerMask.appendChild(e.dom.elGridRectMarker.node);var h=e.dom.baseEl.querySelector("defs");h.appendChild(e.dom.elGridRectMask),h.appendChild(e.dom.elGridRectMarkerMask);}},{key:"_drawGridLines",value:function(t){var e=t.i,i=t.x1,a=t.y1,s=t.x2,r=t.y2,n=t.xCount,o=t.parent,l=this.w;0===e&&l.globals.skipFirstTimelinelabel||e===n-1&&l.globals.skipLastTimelinelabel&&!l.config.xaxis.labels.formatter||"radar"===l.config.chart.type||(l.config.grid.xaxis.lines.show&&this._drawGridLine({x1:i,y1:a,x2:s,y2:r,parent:o}),new G(this.ctx).drawXaxisTicks(i,this.elg));}},{key:"_drawGridLine",value:function(t){var e=t.x1,i=t.y1,a=t.x2,s=t.y2,r=t.parent,n=this.w,o=r.node.classList.contains("apexcharts-gridlines-horizontal"),l=n.config.grid.strokeDashArray,h=n.globals.barPadForNumericAxis,c=new b(this).drawLine(e-(o?h:0),i,a+(o?h:0),s,n.config.grid.borderColor,l);c.node.classList.add("apexcharts-gridline"),r.add(c);}},{key:"_drawGridBandRect",value:function(t){var e=t.c,i=t.x1,a=t.y1,s=t.x2,r=t.y2,n=t.type,o=this.w,l=new b(this.ctx),h=o.globals.barPadForNumericAxis;if("column"!==n||"datetime"!==o.config.xaxis.type){var c=o.config.grid[n].colors[e],d=l.drawRect(i-("row"===n?h:0),a,s+("row"===n?2*h:0),r,0,c,o.config.grid[n].opacity);this.elg.add(d),d.attr("clip-path","url(#gridRectMask".concat(o.globals.cuid,")")),d.node.classList.add("apexcharts-grid-".concat(n));}}},{key:"_drawXYLines",value:function(t){var e=this,i=t.xCount,a=t.tickAmount,s=this.w;if(s.config.grid.xaxis.lines.show||s.config.xaxis.axisTicks.show){var r=s.globals.padHorizontal,n=s.globals.gridHeight;s.globals.timescaleLabels.length?function(t){for(var a=t.xC,s=t.x1,r=t.y1,n=t.x2,o=t.y2,l=0;l<a;l++)s=e.xaxisLabels[l].position,n=e.xaxisLabels[l].position,e._drawGridLines({i:l,x1:s,y1:r,x2:n,y2:o,xCount:i,parent:e.elgridLinesV});}({xC:i,x1:r,y1:0,x2:void 0,y2:n}):(s.globals.isXNumeric&&(i=s.globals.xAxisScale.result.length),s.config.xaxis.convertedCatToNumeric&&(i=s.globals.xaxisLabelsCount),function(t){var a=t.xC,r=t.x1,n=t.y1,o=t.x2,l=t.y2;if(void 0!==s.config.xaxis.tickAmount&&"dataPoints"!==s.config.xaxis.tickAmount)s.globals.dom.baseEl.querySelectorAll(".apexcharts-text.apexcharts-xaxis-label tspan:not(:empty)").forEach((function(t,a){var s=t.getBBox();e._drawGridLines({i:a,x1:s.x+s.width/2,y1:n,x2:s.x+s.width/2,y2:l,xCount:i,parent:e.elgridLinesV});}));else for(var h=0;h<a+(s.globals.isXNumeric?0:1);h++)0===h&&1===a&&1===s.globals.dataPoints&&(o=r=s.globals.gridWidth/2),e._drawGridLines({i:h,x1:r,y1:n,x2:o,y2:l,xCount:i,parent:e.elgridLinesV}),o=r+=s.globals.gridWidth/(s.globals.isXNumeric?a-1:a);}({xC:i,x1:r,y1:0,x2:void 0,y2:n}));}if(s.config.grid.yaxis.lines.show){var o=0,l=0,h=s.globals.gridWidth,c=a+1;this.isTimelineBar&&(c=s.globals.labels.length);for(var d=0;d<c+(this.isTimelineBar?1:0);d++)this._drawGridLine({x1:0,y1:o,x2:h,y2:l,parent:this.elgridLinesH}),l=o+=s.globals.gridHeight/(this.isTimelineBar?c:a);}}},{key:"_drawInvertedXYLines",value:function(t){var e=t.xCount,i=this.w;if(i.config.grid.xaxis.lines.show||i.config.xaxis.axisTicks.show)for(var a,s=i.globals.padHorizontal,r=i.globals.gridHeight,n=0;n<e+1;n++){i.config.grid.xaxis.lines.show&&this._drawGridLine({x1:s,y1:0,x2:a,y2:r,parent:this.elgridLinesV}),new G(this.ctx).drawXaxisTicks(s,this.elg),a=s=s+i.globals.gridWidth/e+.3;}if(i.config.grid.yaxis.lines.show)for(var o=0,l=0,h=i.globals.gridWidth,c=0;c<i.globals.dataPoints+1;c++)this._drawGridLine({x1:0,y1:o,x2:h,y2:l,parent:this.elgridLinesH}),l=o+=i.globals.gridHeight/i.globals.dataPoints;}},{key:"renderGrid",value:function(){var t=this.w,e=new b(this.ctx);this.elg=e.group({class:"apexcharts-grid"}),this.elgridLinesH=e.group({class:"apexcharts-gridlines-horizontal"}),this.elgridLinesV=e.group({class:"apexcharts-gridlines-vertical"}),this.elg.add(this.elgridLinesH),this.elg.add(this.elgridLinesV),t.config.grid.show||(this.elgridLinesV.hide(),this.elgridLinesH.hide());for(var i,a=t.globals.yAxisScale.length?t.globals.yAxisScale[0].result.length-1:5,s=0;s<t.globals.series.length&&(void 0!==t.globals.yAxisScale[s]&&(a=t.globals.yAxisScale[s].result.length-1),!(a>2));s++);return !t.globals.isBarHorizontal||this.isTimelineBar?(i=this.xaxisLabels.length,this.isTimelineBar&&(a=t.globals.labels.length,t.config.xaxis.tickAmount&&t.config.xaxis.labels.formatter&&(i=t.config.xaxis.tickAmount)),this._drawXYLines({xCount:i,tickAmount:a})):(i=a,a=t.globals.xTickAmount,this._drawInvertedXYLines({xCount:i,tickAmount:a})),this.drawGridBands(i,a),{el:this.elg,xAxisTickWidth:t.globals.gridWidth/i}}},{key:"drawGridBands",value:function(t,e){var i=this.w;if(void 0!==i.config.grid.row.colors&&i.config.grid.row.colors.length>0)for(var a=0,s=i.globals.gridHeight/e,r=i.globals.gridWidth,n=0,o=0;n<e;n++,o++)o>=i.config.grid.row.colors.length&&(o=0),this._drawGridBandRect({c:o,x1:0,y1:a,x2:r,y2:s,type:"row"}),a+=i.globals.gridHeight/e;if(void 0!==i.config.grid.column.colors&&i.config.grid.column.colors.length>0)for(var l=i.globals.isBarHorizontal||"category"!==i.config.xaxis.type&&!i.config.xaxis.convertedCatToNumeric?t:t-1,h=i.globals.padHorizontal,c=i.globals.padHorizontal+i.globals.gridWidth/l,d=i.globals.gridHeight,g=0,u=0;g<t;g++,u++)u>=i.config.grid.column.colors.length&&(u=0),this._drawGridBandRect({c:u,x1:h,y1:0,x2:c,y2:d,type:"column"}),h+=i.globals.gridWidth/l;}}]),t}(),j=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"niceScale",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:10,a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:0,s=arguments.length>4?arguments[4]:void 0,r=this.w;if("dataPoints"===i&&(i=r.globals.dataPoints-1),t===Number.MIN_VALUE&&0===e||!f.isNumber(t)&&!f.isNumber(e)||t===Number.MIN_VALUE&&e===-Number.MAX_VALUE){t=0,e=i;var n=this.linearScale(t,e,i);return n}t>e?(console.warn("axis.min cannot be greater than axis.max"),e=t+.1):t===e&&(t=0===t?0:t-.5,e=0===e?2:e+.5);var o=[],l=Math.abs(e-t);l<1&&s&&("candlestick"===r.config.chart.type||"candlestick"===r.config.series[a].type||r.globals.isRangeData)&&(e*=1.01);var h=i+1;h<2?h=2:h>2&&(h-=2);var c=l/h,d=Math.floor(f.log10(c)),g=Math.pow(10,d),u=Math.round(c/g);u<1&&(u=1);var p=u*g,x=p*Math.floor(t/p),b=p*Math.ceil(e/p),m=x;if(s&&l>2){for(;o.push(m),!((m+=p)>b););return {result:o,niceMin:o[0],niceMax:o[o.length-1]}}var v=t;(o=[]).push(v);for(var y=Math.abs(e-t)/i,w=0;w<=i;w++)v+=y,o.push(v);return o[o.length-2]>=e&&o.pop(),{result:o,niceMin:o[0],niceMax:o[o.length-1]}}},{key:"linearScale",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:10,a=Math.abs(e-t),s=a/i;i===Number.MAX_VALUE&&(i=10,s=1);for(var r=[],n=t;i>=0;)r.push(n),n+=s,i-=1;return {result:r,niceMin:r[0],niceMax:r[r.length-1]}}},{key:"logarithmicScale",value:function(t,e,i,a){(e<=0||e===Number.MIN_VALUE)&&(e=.01);for(var s=Math.log(e)/Math.log(10),r=Math.log(i)/Math.log(10),n=Math.abs(i-e)/a,o=[],l=e;a>=0;)o.push(l),l+=n,a-=1;var h=o.map((function(t,a){t<=0&&(t=.01);var n=(r-s)/(i-e),o=Math.pow(10,s+n*(t-s));return Math.round(o/f.roundToBase(o,10))*f.roundToBase(o,10)}));return 0===h[0]&&(h[0]=1),{result:h,niceMin:h[0],niceMax:h[h.length-1]}}},{key:"setYScaleForIndex",value:function(t,e,i){var a=this.w.globals,s=this.w.config,r=a.isBarHorizontal?s.xaxis:s.yaxis[t];void 0===a.yAxisScale[t]&&(a.yAxisScale[t]=[]);var n=Math.abs(i-e);if(r.logarithmic&&n<=5&&(a.invalidLogScale=!0),r.logarithmic&&n>5)a.allSeriesCollapsed=!1,a.yAxisScale[t]=this.logarithmicScale(t,e,i,r.tickAmount?r.tickAmount:Math.floor(Math.log10(i)));else if(i!==-Number.MAX_VALUE&&f.isNumber(i))if(a.allSeriesCollapsed=!1,void 0===r.min&&void 0===r.max||r.forceNiceScale){var o=void 0===s.yaxis[t].max&&void 0===s.yaxis[t].min||s.yaxis[t].forceNiceScale;a.yAxisScale[t]=this.niceScale(e,i,r.tickAmount?r.tickAmount:n<5&&n>1?n+1:5,t,o);}else a.yAxisScale[t]=this.linearScale(e,i,r.tickAmount);else a.yAxisScale[t]=this.linearScale(0,5,5);}},{key:"setXScale",value:function(t,e){var i=this.w,a=i.globals,s=i.config.xaxis,r=Math.abs(e-t);return e!==-Number.MAX_VALUE&&f.isNumber(e)?a.xAxisScale=this.niceScale(t,e,s.tickAmount?s.tickAmount:r<5&&r>1?r+1:5,0):a.xAxisScale=this.linearScale(0,5,5),a.xAxisScale}},{key:"setMultipleYScales",value:function(){var t=this,e=this.w.globals,i=this.w.config,a=e.minYArr.concat([]),s=e.maxYArr.concat([]),r=[];i.yaxis.forEach((function(e,n){var o=n;i.series.forEach((function(t,i){t.name===e.seriesName&&(o=i,n!==i?r.push({index:i,similarIndex:n,alreadyExists:!0}):r.push({index:i}));}));var l=a[o],h=s[o];t.setYScaleForIndex(n,l,h);})),this.sameScaleInMultipleAxes(a,s,r);}},{key:"sameScaleInMultipleAxes",value:function(t,e,i){var a=this,s=this.w.config,r=this.w.globals,n=[];i.forEach((function(t){t.alreadyExists&&(void 0===n[t.index]&&(n[t.index]=[]),n[t.index].push(t.index),n[t.index].push(t.similarIndex));})),r.yAxisSameScaleIndices=n,n.forEach((function(t,e){n.forEach((function(i,a){var s,r;e!==a&&(s=t,r=i,s.filter((function(t){return -1!==r.indexOf(t)}))).length>0&&(n[e]=n[e].concat(n[a]));}));}));var o=n.map((function(t){return t.filter((function(e,i){return t.indexOf(e)===i}))})).map((function(t){return t.sort()}));n=n.filter((function(t){return !!t}));var l=o.slice(),h=l.map((function(t){return JSON.stringify(t)}));l=l.filter((function(t,e){return h.indexOf(JSON.stringify(t))===e}));var c=[],d=[];t.forEach((function(t,i){l.forEach((function(a,s){a.indexOf(i)>-1&&(void 0===c[s]&&(c[s]=[],d[s]=[]),c[s].push({key:i,value:t}),d[s].push({key:i,value:e[i]}));}));}));var g=Array.apply(null,Array(l.length)).map(Number.prototype.valueOf,Number.MIN_VALUE),u=Array.apply(null,Array(l.length)).map(Number.prototype.valueOf,-Number.MAX_VALUE);c.forEach((function(t,e){t.forEach((function(t,i){g[e]=Math.min(t.value,g[e]);}));})),d.forEach((function(t,e){t.forEach((function(t,i){u[e]=Math.max(t.value,u[e]);}));})),t.forEach((function(t,e){d.forEach((function(t,i){var n=g[i],o=u[i];s.chart.stacked&&(o=0,t.forEach((function(t,e){t.value!==-Number.MAX_VALUE&&(o+=t.value),n!==Number.MIN_VALUE&&(n+=c[i][e].value);}))),t.forEach((function(i,l){t[l].key===e&&(void 0!==s.yaxis[e].min&&(n="function"==typeof s.yaxis[e].min?s.yaxis[e].min(r.minY):s.yaxis[e].min),void 0!==s.yaxis[e].max&&(o="function"==typeof s.yaxis[e].max?s.yaxis[e].max(r.maxY):s.yaxis[e].max),a.setYScaleForIndex(e,n,o));}));}));}));}},{key:"autoScaleY",value:function(t,e,i){t||(t=this);var a=t.w;if(a.globals.isMultipleYAxis||a.globals.collapsedSeries.length)return console.warn("autoScaleYaxis is not supported in a multi-yaxis chart."),e;var s=a.globals.seriesX[0],r=a.config.chart.stacked;return e.forEach((function(t,n){for(var o=0,l=0;l<s.length;l++)if(s[l]>=i.xaxis.min){o=l;break}var h,c,d=a.globals.minYArr[n],g=a.globals.maxYArr[n],u=a.globals.stackedSeriesTotals;a.globals.series.forEach((function(n,l){var f=n[o];r?(f=u[o],h=c=f,u.forEach((function(t,e){s[e]<=i.xaxis.max&&s[e]>=i.xaxis.min&&(t>c&&null!==t&&(c=t),n[e]<h&&null!==n[e]&&(h=n[e]));}))):(h=c=f,n.forEach((function(t,e){if(s[e]<=i.xaxis.max&&s[e]>=i.xaxis.min){var r=t,n=t;a.globals.series.forEach((function(i,a){null!==t&&(r=Math.min(i[e],r),n=Math.max(i[e],n));})),n>c&&null!==n&&(c=n),r<h&&null!==r&&(h=r);}}))),void 0===h&&void 0===c&&(h=d,c=g),(c*=c<0?.9:1.1)<0&&c<g&&(c=g),(h*=h<0?1.1:.9)<0&&h>d&&(h=d),e.length>1?(e[l].min=void 0===t.min?h:t.min,e[l].max=void 0===t.max?c:t.max):(e[0].min=void 0===t.min?h:t.min,e[0].max=void 0===t.max?c:t.max);}));})),e}}]),t}(),U=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.scales=new j(i);}return a(t,[{key:"init",value:function(){this.setYRange(),this.setXRange(),this.setZRange();}},{key:"getMinYMaxY",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:Number.MAX_VALUE,i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:-Number.MAX_VALUE,a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:null,s=this.w.config,r=this.w.globals,n=-Number.MAX_VALUE,o=Number.MIN_VALUE;null===a&&(a=t+1);var l=r.series,h=l,c=l;"candlestick"===s.chart.type?(h=r.seriesCandleL,c=r.seriesCandleH):r.isRangeData&&(h=r.seriesRangeStart,c=r.seriesRangeEnd);for(var d=t;d<a;d++){r.dataPoints=Math.max(r.dataPoints,l[d].length);for(var g=0;g<r.series[d].length;g++){var u=l[d][g];null!==u&&f.isNumber(u)?(n=Math.max(n,c[d][g]),e=Math.min(e,h[d][g]),i=Math.max(i,h[d][g]),"candlestick"===this.w.config.chart.type&&(n=Math.max(n,r.seriesCandleO[d][g]),n=Math.max(n,r.seriesCandleH[d][g]),n=Math.max(n,r.seriesCandleL[d][g]),i=n=Math.max(n,r.seriesCandleC[d][g])),f.isFloat(u)&&(u=f.noExponents(u),r.yValueDecimal=Math.max(r.yValueDecimal,u.toString().split(".")[1].length)),o>h[d][g]&&h[d][g]<0&&(o=h[d][g])):r.hasNullValues=!0;}}return "rangeBar"===s.chart.type&&r.seriesRangeStart.length&&r.isBarHorizontal&&"datetime"===s.xaxis.type&&(o=e),"bar"===s.chart.type&&(o<0&&n<0&&(n=0),o===Number.MIN_VALUE&&(o=0)),{minY:o,maxY:n,lowestY:e,highestY:i}}},{key:"setYRange",value:function(){var t=this.w.globals,e=this.w.config;t.maxY=-Number.MAX_VALUE,t.minY=Number.MIN_VALUE;var i=Number.MAX_VALUE;if(t.isMultipleYAxis)for(var a=0;a<t.series.length;a++){var s=this.getMinYMaxY(a,i,null,a+1);t.minYArr.push(s.minY),t.maxYArr.push(s.maxY),i=s.lowestY;}var r=this.getMinYMaxY(0,i,null,t.series.length);if(t.minY=r.minY,t.maxY=r.maxY,i=r.lowestY,e.chart.stacked&&this._setStackedMinMax(),("line"===e.chart.type||"area"===e.chart.type||"candlestick"===e.chart.type||"rangeBar"===e.chart.type&&!t.isBarHorizontal)&&t.minY===Number.MIN_VALUE&&i!==-Number.MAX_VALUE&&i!==t.maxY){var n=t.maxY-i;i>=0&&i<=10&&(n=0),t.minY=i-5*n/100,i>0&&t.minY<0&&(t.minY=0),t.maxY=t.maxY+5*n/100;}if(e.yaxis.forEach((function(e,i){void 0!==e.max&&("number"==typeof e.max?t.maxYArr[i]=e.max:"function"==typeof e.max&&(t.maxYArr[i]=e.max(t.maxY)),t.maxY=t.maxYArr[i]),void 0!==e.min&&("number"==typeof e.min?t.minYArr[i]=e.min:"function"==typeof e.min&&(t.minYArr[i]=e.min(t.minY)),t.minY=t.minYArr[i]);})),t.isBarHorizontal){["min","max"].forEach((function(i){void 0!==e.xaxis[i]&&"number"==typeof e.xaxis[i]&&("min"===i?t.minY=e.xaxis[i]:t.maxY=e.xaxis[i]);}));}return t.isMultipleYAxis?(this.scales.setMultipleYScales(),t.minY=i,t.yAxisScale.forEach((function(e,i){t.minYArr[i]=e.niceMin,t.maxYArr[i]=e.niceMax;}))):(this.scales.setYScaleForIndex(0,t.minY,t.maxY),t.minY=t.yAxisScale[0].niceMin,t.maxY=t.yAxisScale[0].niceMax,t.minYArr[0]=t.yAxisScale[0].niceMin,t.maxYArr[0]=t.yAxisScale[0].niceMax),{minY:t.minY,maxY:t.maxY,minYArr:t.minYArr,maxYArr:t.maxYArr}}},{key:"setXRange",value:function(){var t=this.w.globals,e=this.w.config,i="numeric"===e.xaxis.type||"datetime"===e.xaxis.type||"category"===e.xaxis.type&&!t.noLabelsProvided||t.noLabelsProvided||t.isXNumeric;if(t.isXNumeric&&function(){for(var e=0;e<t.series.length;e++)if(t.labels[e])for(var i=0;i<t.labels[e].length;i++)null!==t.labels[e][i]&&f.isNumber(t.labels[e][i])&&(t.maxX=Math.max(t.maxX,t.labels[e][i]),t.initialMaxX=Math.max(t.maxX,t.labels[e][i]),t.minX=Math.min(t.minX,t.labels[e][i]),t.initialMinX=Math.min(t.minX,t.labels[e][i]));}(),t.noLabelsProvided&&0===e.xaxis.categories.length&&(t.maxX=t.labels[t.labels.length-1],t.initialMaxX=t.labels[t.labels.length-1],t.minX=1,t.initialMinX=1),t.isXNumeric||t.noLabelsProvided||t.dataFormatXNumeric){var a;if(void 0===e.xaxis.tickAmount?(a=Math.round(t.svgWidth/150),"numeric"===e.xaxis.type&&t.dataPoints<30&&(a=t.dataPoints-1),a>t.dataPoints&&0!==t.dataPoints&&(a=t.dataPoints-1)):"dataPoints"===e.xaxis.tickAmount?(t.series.length>1&&(a=t.series[t.maxValsInArrayIndex].length-1),t.isXNumeric&&(a=t.maxX-t.minX-1)):a=e.xaxis.tickAmount,t.xTickAmount=a,void 0!==e.xaxis.max&&"number"==typeof e.xaxis.max&&(t.maxX=e.xaxis.max),void 0!==e.xaxis.min&&"number"==typeof e.xaxis.min&&(t.minX=e.xaxis.min),void 0!==e.xaxis.range&&(t.minX=t.maxX-e.xaxis.range),t.minX!==Number.MAX_VALUE&&t.maxX!==-Number.MAX_VALUE)if(e.xaxis.convertedCatToNumeric&&!t.dataFormatXNumeric){for(var s=[],r=t.minX-1;r<t.maxX;r++)s.push(r+1);t.xAxisScale={result:s,niceMin:s[0],niceMax:s[s.length-1]};}else t.xAxisScale=this.scales.setXScale(t.minX,t.maxX);else t.xAxisScale=this.scales.linearScale(1,a,a),t.noLabelsProvided&&t.labels.length>0&&(t.xAxisScale=this.scales.linearScale(1,t.labels.length,a-1),t.seriesX=t.labels.slice());i&&(t.labels=t.xAxisScale.result.slice());}return t.isBarHorizontal&&t.labels.length&&(t.xTickAmount=t.labels.length),this._handleSingleDataPoint(),this._getMinXDiff(),{minX:t.minX,maxX:t.maxX}}},{key:"setZRange",value:function(){var t=this.w.globals;if(t.isDataXYZ)for(var e=0;e<t.series.length;e++)if(void 0!==t.seriesZ[e])for(var i=0;i<t.seriesZ[e].length;i++)null!==t.seriesZ[e][i]&&f.isNumber(t.seriesZ[e][i])&&(t.maxZ=Math.max(t.maxZ,t.seriesZ[e][i]),t.minZ=Math.min(t.minZ,t.seriesZ[e][i]));}},{key:"_handleSingleDataPoint",value:function(){var t=this.w.globals,e=this.w.config;if(t.minX===t.maxX){var i=new y(this.ctx);if("datetime"===e.xaxis.type){var a=i.getDate(t.minX);a.setUTCDate(a.getDate()-2),t.minX=new Date(a).getTime();var s=i.getDate(t.maxX);s.setUTCDate(s.getDate()+2),t.maxX=new Date(s).getTime();}else ("numeric"===e.xaxis.type||"category"===e.xaxis.type&&!t.noLabelsProvided)&&(t.minX=t.minX-2,t.initialMinX=t.minX,t.maxX=t.maxX+2,t.initialMaxX=t.maxX);}}},{key:"_getMinXDiff",value:function(){var t=this.w.globals;t.isXNumeric&&t.seriesX.forEach((function(e,i){1===e.length&&e.push(t.seriesX[t.maxValsInArrayIndex][t.seriesX[t.maxValsInArrayIndex].length-1]);var a=e.slice();a.sort((function(t,e){return t-e})),a.forEach((function(e,a){if(a>0){var s=e-t.seriesX[i][a-1];s>0&&(t.minXDiff=Math.min(s,t.minXDiff));}})),1===t.dataPoints&&t.minXDiff===Number.MAX_VALUE&&(t.minXDiff=.5);}));}},{key:"_setStackedMinMax",value:function(){var t=this.w.globals,e=[],i=[];if(t.series.length)for(var a=0;a<t.series[t.maxValsInArrayIndex].length;a++)for(var s=0,r=0,n=0;n<t.series.length;n++)null!==t.series[n][a]&&f.isNumber(t.series[n][a])&&(t.series[n][a]>0?s=s+parseFloat(t.series[n][a])+1e-4:r+=parseFloat(t.series[n][a])),n===t.series.length-1&&(e.push(s),i.push(r));for(var o=0;o<e.length;o++)t.maxY=Math.max(t.maxY,e[o]),t.minY=Math.min(t.minY,i[o]);}}]),t}(),q=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;var a=this.w;this.xaxisFontSize=a.config.xaxis.labels.style.fontSize,this.axisFontFamily=a.config.xaxis.labels.style.fontFamily,this.xaxisForeColors=a.config.xaxis.labels.style.colors,this.isCategoryBarHorizontal="bar"===a.config.chart.type&&a.config.plotOptions.bar.horizontal,this.xAxisoffX=0,"bottom"===a.config.xaxis.position&&(this.xAxisoffX=a.globals.gridHeight),this.drawnLabels=[],this.axesUtils=new B(i);}return a(t,[{key:"drawYaxis",value:function(t){var e=this,i=this.w,a=new b(this.ctx),s=i.config.yaxis[t].labels.style,r=s.fontSize,n=s.fontFamily,o=s.fontWeight,l=a.group({class:"apexcharts-yaxis",rel:t,transform:"translate("+i.globals.translateYAxisX[t]+", 0)"});if(this.axesUtils.isYAxisHidden(t))return l;var h=a.group({class:"apexcharts-yaxis-texts-g"});l.add(h);var c=i.globals.yAxisScale[t].result.length-1,d=i.globals.gridHeight/c,g=i.globals.translateY,u=i.globals.yLabelFormatters[t],f=i.globals.yAxisScale[t].result.slice();f=this.axesUtils.checkForReversedLabels(t,f);var p="";if(i.config.yaxis[t].labels.show)for(var x=function(l){var x=f[l];x=u(x,l);var b=i.config.yaxis[t].labels.padding;i.config.yaxis[t].opposite&&0!==i.config.yaxis.length&&(b*=-1);var m=e.axesUtils.getYAxisForeColor(s.colors,t),v=a.drawText({x:b,y:g+c/10+i.config.yaxis[t].labels.offsetY+1,text:x,textAnchor:i.config.yaxis[t].opposite?"start":"end",fontSize:r,fontFamily:n,fontWeight:o,foreColor:Array.isArray(m)?m[l]:m,isPlainText:!1,cssClass:"apexcharts-yaxis-label "+s.cssClass});if(l===c&&(p=v),h.add(v),0!==i.config.yaxis[t].labels.rotate){var y=a.rotateAroundCenter(p.node),w=a.rotateAroundCenter(v.node);v.node.setAttribute("transform","rotate(".concat(i.config.yaxis[t].labels.rotate," ").concat(y.x," ").concat(w.y,")"));}g+=d;},m=c;m>=0;m--)x(m);if(void 0!==i.config.yaxis[t].title.text){var v=a.group({class:"apexcharts-yaxis-title"}),y=0;i.config.yaxis[t].opposite&&(y=i.globals.translateYAxisX[t]);var w=a.drawText({x:y,y:i.globals.gridHeight/2+i.globals.translateY+i.config.yaxis[t].title.offsetY,text:i.config.yaxis[t].title.text,textAnchor:"end",foreColor:i.config.yaxis[t].title.style.color,fontSize:i.config.yaxis[t].title.style.fontSize,fontWeight:i.config.yaxis[t].title.style.fontWeight,fontFamily:i.config.yaxis[t].title.style.fontFamily,cssClass:"apexcharts-yaxis-title-text "+i.config.yaxis[t].title.style.cssClass});v.add(w),l.add(v);}var k=i.config.yaxis[t].axisBorder,A=31+k.offsetX;if(i.config.yaxis[t].opposite&&(A=-31-k.offsetX),k.show){var S=a.drawLine(A,i.globals.translateY+k.offsetY-2,A,i.globals.gridHeight+i.globals.translateY+k.offsetY+2,k.color,0,k.width);l.add(S);}return i.config.yaxis[t].axisTicks.show&&this.axesUtils.drawYAxisTicks(A,c,k,i.config.yaxis[t].axisTicks,t,d,l),l}},{key:"drawYaxisInversed",value:function(t){var e=this.w,i=new b(this.ctx),a=i.group({class:"apexcharts-xaxis apexcharts-yaxis-inversed"}),s=i.group({class:"apexcharts-xaxis-texts-g",transform:"translate(".concat(e.globals.translateXAxisX,", ").concat(e.globals.translateXAxisY,")")});a.add(s);var r=e.globals.yAxisScale[t].result.length-1,n=e.globals.gridWidth/r+.1,o=n+e.config.xaxis.labels.offsetX,l=e.globals.xLabelFormatter,h=e.globals.yAxisScale[t].result.slice(),c=e.globals.timescaleLabels;c.length>0&&(this.xaxisLabels=c.slice(),r=(h=c.slice()).length),h=this.axesUtils.checkForReversedLabels(t,h);var d=c.length;if(e.config.xaxis.labels.show)for(var g=d?0:r;d?g<d:g>=0;d?g++:g--){var u=h[g];u=l(u,g);var f=e.globals.gridWidth+e.globals.padHorizontal-(o-n+e.config.xaxis.labels.offsetX);if(c.length){var p=this.axesUtils.getLabel(h,c,f,g,this.drawnLabels,this.xaxisFontSize);f=p.x,u=p.text,this.drawnLabels.push(p.text),0===g&&e.globals.skipFirstTimelinelabel&&(u=""),g===h.length-1&&e.globals.skipLastTimelinelabel&&(u="");}var x=i.drawText({x:f,y:this.xAxisoffX+e.config.xaxis.labels.offsetY+30-("top"===e.config.xaxis.position?e.globals.xAxisHeight+e.config.xaxis.axisTicks.height-2:0),text:u,textAnchor:"middle",foreColor:Array.isArray(this.xaxisForeColors)?this.xaxisForeColors[t]:this.xaxisForeColors,fontSize:this.xaxisFontSize,fontFamily:this.xaxisFontFamily,fontWeight:e.config.xaxis.labels.style.fontWeight,isPlainText:!1,cssClass:"apexcharts-xaxis-label "+e.config.xaxis.labels.style.cssClass});s.add(x),x.tspan(u);var m=document.createElementNS(e.globals.SVGNS,"title");m.textContent=u,x.node.appendChild(m),o+=n;}return this.inversedYAxisTitleText(a),this.inversedYAxisBorder(a),a}},{key:"inversedYAxisBorder",value:function(t){var e=this.w,i=new b(this.ctx),a=e.config.xaxis.axisBorder;if(a.show){var s=0;"bar"===e.config.chart.type&&e.globals.isXNumeric&&(s-=15);var r=i.drawLine(e.globals.padHorizontal+s+a.offsetX,this.xAxisoffX,e.globals.gridWidth,this.xAxisoffX,a.color,0,a.height);t.add(r);}}},{key:"inversedYAxisTitleText",value:function(t){var e=this.w,i=new b(this.ctx);if(void 0!==e.config.xaxis.title.text){var a=i.group({class:"apexcharts-xaxis-title apexcharts-yaxis-title-inversed"}),s=i.drawText({x:e.globals.gridWidth/2+e.config.xaxis.title.offsetX,y:this.xAxisoffX+parseFloat(this.xaxisFontSize)+parseFloat(e.config.xaxis.title.style.fontSize)+e.config.xaxis.title.offsetY+20,text:e.config.xaxis.title.text,textAnchor:"middle",fontSize:e.config.xaxis.title.style.fontSize,fontFamily:e.config.xaxis.title.style.fontFamily,fontWeight:e.config.xaxis.title.style.fontWeight,foreColor:e.config.xaxis.title.style.color,cssClass:"apexcharts-xaxis-title-text "+e.config.xaxis.title.style.cssClass});a.add(s),t.add(a);}}},{key:"yAxisTitleRotate",value:function(t,e){var i=this.w,a=new b(this.ctx),s={width:0,height:0},r={width:0,height:0},n=i.globals.dom.baseEl.querySelector(" .apexcharts-yaxis[rel='".concat(t,"'] .apexcharts-yaxis-texts-g"));null!==n&&(s=n.getBoundingClientRect());var o=i.globals.dom.baseEl.querySelector(".apexcharts-yaxis[rel='".concat(t,"'] .apexcharts-yaxis-title text"));if(null!==o&&(r=o.getBoundingClientRect()),null!==o){var l=this.xPaddingForYAxisTitle(t,s,r,e);o.setAttribute("x",l.xPos-(e?10:0));}if(null!==o){var h=a.rotateAroundCenter(o);o.setAttribute("transform","rotate(".concat(e?-1*i.config.yaxis[t].title.rotate:i.config.yaxis[t].title.rotate," ").concat(h.x," ").concat(h.y,")"));}}},{key:"xPaddingForYAxisTitle",value:function(t,e,i,a){var s=this.w,r=0,n=0,o=10;return void 0===s.config.yaxis[t].title.text||t<0?{xPos:n,padd:0}:(a?(n=e.width+s.config.yaxis[t].title.offsetX+i.width/2+o/2,0===(r+=1)&&(n-=o/2)):(n=-1*e.width+s.config.yaxis[t].title.offsetX+o/2+i.width/2,s.globals.isBarHorizontal&&(o=25,n=-1*e.width-s.config.yaxis[t].title.offsetX-o)),{xPos:n,padd:o})}},{key:"setYAxisXPosition",value:function(t,e){var i=this.w,a=0,s=0,r=18,n=1;i.config.yaxis.length>1&&(this.multipleYs=!0),i.config.yaxis.map((function(o,l){var h=i.globals.ignoreYAxisIndexes.indexOf(l)>-1||!o.show||o.floating||0===t[l].width,c=t[l].width+e[l].width;o.opposite?i.globals.isBarHorizontal?(s=i.globals.gridWidth+i.globals.translateX-1,i.globals.translateYAxisX[l]=s-o.labels.offsetX):(s=i.globals.gridWidth+i.globals.translateX+n,h||(n=n+c+20),i.globals.translateYAxisX[l]=s-o.labels.offsetX+20):(a=i.globals.translateX-r,h||(r=r+c+20),i.globals.translateYAxisX[l]=a+o.labels.offsetX);}));}},{key:"setYAxisTextAlignments",value:function(){var t=this.w,e=t.globals.dom.baseEl.getElementsByClassName("apexcharts-yaxis");(e=f.listToArray(e)).forEach((function(e,i){var a=t.config.yaxis[i];if(a&&void 0!==a.labels.align){var s=t.globals.dom.baseEl.querySelector(".apexcharts-yaxis[rel='".concat(i,"'] .apexcharts-yaxis-texts-g")),r=t.globals.dom.baseEl.querySelectorAll(".apexcharts-yaxis[rel='".concat(i,"'] .apexcharts-yaxis-label"));r=f.listToArray(r);var n=s.getBoundingClientRect();"left"===a.labels.align?(r.forEach((function(t,e){t.setAttribute("text-anchor","start");})),a.opposite||s.setAttribute("transform","translate(-".concat(n.width,", 0)"))):"center"===a.labels.align?(r.forEach((function(t,e){t.setAttribute("text-anchor","middle");})),s.setAttribute("transform","translate(".concat(n.width/2*(a.opposite?1:-1),", 0)"))):"right"===a.labels.align&&(r.forEach((function(t,e){t.setAttribute("text-anchor","end");})),a.opposite&&s.setAttribute("transform","translate(".concat(n.width,", 0)")));}}));}}]),t}(),Z=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.documentEvent=f.bind(this.documentEvent,this);}return a(t,[{key:"addEventListener",value:function(t,e){var i=this.w;i.globals.events.hasOwnProperty(t)?i.globals.events[t].push(e):i.globals.events[t]=[e];}},{key:"removeEventListener",value:function(t,e){var i=this.w;if(i.globals.events.hasOwnProperty(t)){var a=i.globals.events[t].indexOf(e);-1!==a&&i.globals.events[t].splice(a,1);}}},{key:"fireEvent",value:function(t,e){var i=this.w;if(i.globals.events.hasOwnProperty(t)){e&&e.length||(e=[]);for(var a=i.globals.events[t],s=a.length,r=0;r<s;r++)a[r].apply(null,e);}}},{key:"setupEventHandlers",value:function(){var t=this,e=this.w,i=this.ctx,a=e.globals.dom.baseEl.querySelector(e.globals.chartClass);this.ctx.eventList.forEach((function(t){a.addEventListener(t,(function(t){var a=Object.assign({},e,{seriesIndex:e.globals.capturedSeriesIndex,dataPointIndex:e.globals.capturedDataPointIndex});"mousemove"===t.type||"touchmove"===t.type?"function"==typeof e.config.chart.events.mouseMove&&e.config.chart.events.mouseMove(t,i,a):("mouseup"===t.type&&1===t.which||"touchend"===t.type)&&("function"==typeof e.config.chart.events.click&&e.config.chart.events.click(t,i,a),i.ctx.events.fireEvent("click",[t,i,a]));}),{capture:!1,passive:!0});})),this.ctx.eventList.forEach((function(i){e.globals.dom.baseEl.addEventListener(i,t.documentEvent);})),this.ctx.core.setupBrushHandler();}},{key:"documentEvent",value:function(t){var e=this.w,i=t.target.className;if("click"===t.type){var a=e.globals.dom.baseEl.querySelector(".apexcharts-menu");a&&a.classList.contains("apexcharts-menu-open")&&"apexcharts-menu-icon"!==i&&a.classList.remove("apexcharts-menu-open");}e.globals.clientX="touchmove"===t.type?t.touches[0].clientX:t.clientX,e.globals.clientY="touchmove"===t.type?t.touches[0].clientY:t.clientY;}}]),t}(),$=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"setCurrentLocaleValues",value:function(t){var e=this.w.config.chart.locales;window.Apex.chart&&window.Apex.chart.locales&&window.Apex.chart.locales.length>0&&(e=this.w.config.chart.locales.concat(window.Apex.chart.locales));var i=e.filter((function(e){return e.name===t}))[0];if(!i)throw new Error("Wrong locale name provided. Please make sure you set the correct locale name in options");var a=f.extend(S,i);this.w.globals.locale=a.options;}}]),t}(),J=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"drawAxis",value:function(t,e){var i,a,s=this.w.globals,r=this.w.config,n=new G(this.ctx),o=new q(this.ctx);s.axisCharts&&"radar"!==t&&(s.isBarHorizontal?(a=o.drawYaxisInversed(0),i=n.drawXaxisInversed(0),s.dom.elGraphical.add(i),s.dom.elGraphical.add(a)):(i=n.drawXaxis(),s.dom.elGraphical.add(i),r.yaxis.map((function(t,e){-1===s.ignoreYAxisIndexes.indexOf(e)&&(a=o.drawYaxis(e),s.dom.Paper.add(a));}))));r.yaxis.map((function(t,e){-1===s.ignoreYAxisIndexes.indexOf(e)&&o.yAxisTitleRotate(e,t.opposite);}));}}]),t}(),Q=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"drawXCrosshairs",value:function(){var t=this.w,e=new b(this.ctx),i=new p(this.ctx),a=t.config.xaxis.crosshairs.fill.gradient,s=t.config.xaxis.crosshairs.dropShadow,r=t.config.xaxis.crosshairs.fill.type,n=a.colorFrom,o=a.colorTo,l=a.opacityFrom,h=a.opacityTo,c=a.stops,d=s.enabled,g=s.left,u=s.top,x=s.blur,m=s.color,v=s.opacity,y=t.config.xaxis.crosshairs.fill.color;if(t.config.xaxis.crosshairs.show){"gradient"===r&&(y=e.drawGradient("vertical",n,o,l,h,null,c,null));var w=e.drawRect();1===t.config.xaxis.crosshairs.width&&(w=e.drawLine()),w.attr({class:"apexcharts-xcrosshairs",x:0,y:0,y2:t.globals.gridHeight,width:f.isNumber(t.config.xaxis.crosshairs.width)?t.config.xaxis.crosshairs.width:0,height:t.globals.gridHeight,fill:y,filter:"none","fill-opacity":t.config.xaxis.crosshairs.opacity,stroke:t.config.xaxis.crosshairs.stroke.color,"stroke-width":t.config.xaxis.crosshairs.stroke.width,"stroke-dasharray":t.config.xaxis.crosshairs.stroke.dashArray}),d&&(w=i.dropShadow(w,{left:g,top:u,blur:x,color:m,opacity:v})),t.globals.dom.elGraphical.add(w);}}},{key:"drawYCrosshairs",value:function(){var t=this.w,e=new b(this.ctx),i=t.config.yaxis[0].crosshairs,a=t.globals.barPadForNumericAxis;if(t.config.yaxis[0].crosshairs.show){var s=e.drawLine(-a,0,t.globals.gridWidth+a,0,i.stroke.color,i.stroke.dashArray,i.stroke.width);s.attr({class:"apexcharts-ycrosshairs"}),t.globals.dom.elGraphical.add(s);}var r=e.drawLine(-a,0,t.globals.gridWidth+a,0,i.stroke.color,0,0);r.attr({class:"apexcharts-ycrosshairs-hidden"}),t.globals.dom.elGraphical.add(r);}}]),t}(),K=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"checkResponsiveConfig",value:function(t){var e=this,i=this.w,a=i.config;if(0!==a.responsive.length){var s=a.responsive.slice();s.sort((function(t,e){return t.breakpoint>e.breakpoint?1:e.breakpoint>t.breakpoint?-1:0})).reverse();var r=new R({}),n=function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:{},a=s[0].breakpoint,n=window.innerWidth>0?window.innerWidth:screen.width;if(n>a){var o=w.extendArrayProps(r,i.globals.initialConfig,i);t=f.extend(o,t),t=f.extend(i.config,t),e.overrideResponsiveOptions(t);}else for(var l=0;l<s.length;l++)n<s[l].breakpoint&&(t=w.extendArrayProps(r,s[l].options,i),t=f.extend(i.config,t),e.overrideResponsiveOptions(t));};if(t){var o=w.extendArrayProps(r,t,i);o=f.extend(i.config,o),n(o=f.extend(o,t));}else n({});}}},{key:"overrideResponsiveOptions",value:function(t){var e=new R(t).init({responsiveOverride:!0});this.w.config=e;}}]),t}(),tt=function(){function t(i){e(this,t),this.ctx=i,this.colors=[],this.w=i.w;var a=this.w;this.isColorFn=!1,this.isHeatmapDistributed="treemap"===a.config.chart.type&&a.config.plotOptions.treemap.distributed||"heatmap"===a.config.chart.type&&a.config.plotOptions.heatmap.distributed,this.isBarDistributed=a.config.plotOptions.bar.distributed&&("bar"===a.config.chart.type||"rangeBar"===a.config.chart.type);}return a(t,[{key:"init",value:function(){this.setDefaultColors();}},{key:"setDefaultColors",value:function(){var t=this,e=this.w,i=new f;if(e.globals.dom.elWrap.classList.add("apexcharts-theme-".concat(e.config.theme.mode)),void 0===e.config.colors?e.globals.colors=this.predefined():(e.globals.colors=e.config.colors,Array.isArray(e.config.colors)&&e.config.colors.length>0&&"function"==typeof e.config.colors[0]&&(e.globals.colors=e.config.series.map((function(i,a){var s=e.config.colors[a];return s||(s=e.config.colors[0]),"function"==typeof s?(t.isColorFn=!0,s({value:e.globals.axisCharts?e.globals.series[a][0]?e.globals.series[a][0]:0:e.globals.series[a],seriesIndex:a,dataPointIndex:a,w:e})):s})))),e.globals.seriesColors.map((function(t,i){t&&(e.globals.colors[i]=t);})),e.config.theme.monochrome.enabled){var a=[],s=e.globals.series.length;(this.isBarDistributed||this.isHeatmapDistributed)&&(s=e.globals.series[0].length*e.globals.series.length);for(var r=e.config.theme.monochrome.color,n=1/(s/e.config.theme.monochrome.shadeIntensity),o=e.config.theme.monochrome.shadeTo,l=0,h=0;h<s;h++){var c=void 0;"dark"===o?(c=i.shadeColor(-1*l,r),l+=n):(c=i.shadeColor(l,r),l+=n),a.push(c);}e.globals.colors=a.slice();}var d=e.globals.colors.slice();this.pushExtraColors(e.globals.colors);["fill","stroke"].forEach((function(i){void 0===e.config[i].colors?e.globals[i].colors=t.isColorFn?e.config.colors:d:e.globals[i].colors=e.config[i].colors.slice(),t.pushExtraColors(e.globals[i].colors);})),void 0===e.config.dataLabels.style.colors?e.globals.dataLabels.style.colors=d:e.globals.dataLabels.style.colors=e.config.dataLabels.style.colors.slice(),this.pushExtraColors(e.globals.dataLabels.style.colors,50),void 0===e.config.plotOptions.radar.polygons.fill.colors?e.globals.radarPolygons.fill.colors=["dark"===e.config.theme.mode?"#424242":"#fff"]:e.globals.radarPolygons.fill.colors=e.config.plotOptions.radar.polygons.fill.colors.slice(),this.pushExtraColors(e.globals.radarPolygons.fill.colors,20),void 0===e.config.markers.colors?e.globals.markers.colors=d:e.globals.markers.colors=e.config.markers.colors.slice(),this.pushExtraColors(e.globals.markers.colors);}},{key:"pushExtraColors",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:null,a=this.w,s=e||a.globals.series.length;if(null===i&&(i=this.isBarDistributed||this.isHeatmapDistributed||"heatmap"===a.config.chart.type&&a.config.plotOptions.heatmap.colorScale.inverse),i&&a.globals.series.length&&(s=a.globals.series[a.globals.maxValsInArrayIndex].length*a.globals.series.length),t.length<s)for(var r=s-t.length,n=0;n<r;n++)t.push(t[n]);}},{key:"updateThemeOptions",value:function(t){t.chart=t.chart||{},t.tooltip=t.tooltip||{};var e=t.theme.mode||"light",i=t.theme.palette?t.theme.palette:"dark"===e?"palette4":"palette1",a=t.chart.foreColor?t.chart.foreColor:"dark"===e?"#f6f7f8":"#373d3f";return t.tooltip.theme=e,t.chart.foreColor=a,t.theme.palette=i,t}},{key:"predefined",value:function(){switch(this.w.config.theme.palette){case"palette1":this.colors=["#008FFB","#00E396","#FEB019","#FF4560","#775DD0"];break;case"palette2":this.colors=["#3f51b5","#03a9f4","#4caf50","#f9ce1d","#FF9800"];break;case"palette3":this.colors=["#33b2df","#546E7A","#d4526e","#13d8aa","#A5978B"];break;case"palette4":this.colors=["#4ecdc4","#c7f464","#81D4FA","#fd6a6a","#546E7A"];break;case"palette5":this.colors=["#2b908f","#f9a3a4","#90ee7e","#fa4443","#69d2e7"];break;case"palette6":this.colors=["#449DD1","#F86624","#EA3546","#662E9B","#C5D86D"];break;case"palette7":this.colors=["#D7263D","#1B998B","#2E294E","#F46036","#E2C044"];break;case"palette8":this.colors=["#662E9B","#F86624","#F9C80E","#EA3546","#43BCCD"];break;case"palette9":this.colors=["#5C4742","#A5978B","#8D5B4C","#5A2A27","#C4BBAF"];break;case"palette10":this.colors=["#A300D6","#7D02EB","#5653FE","#2983FF","#00B1F2"];break;default:this.colors=["#008FFB","#00E396","#FEB019","#FF4560","#775DD0"];}return this.colors}}]),t}(),et=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"draw",value:function(){this.drawTitleSubtitle("title"),this.drawTitleSubtitle("subtitle");}},{key:"drawTitleSubtitle",value:function(t){var e=this.w,i="title"===t?e.config.title:e.config.subtitle,a=e.globals.svgWidth/2,s=i.offsetY,r="middle";if("left"===i.align?(a=10,r="start"):"right"===i.align&&(a=e.globals.svgWidth-10,r="end"),a+=i.offsetX,s=s+parseInt(i.style.fontSize,10)+i.margin/2,void 0!==i.text){var n=new b(this.ctx).drawText({x:a,y:s,text:i.text,textAnchor:r,fontSize:i.style.fontSize,fontFamily:i.style.fontFamily,fontWeight:i.style.fontWeight,foreColor:i.style.color,opacity:1});n.node.setAttribute("class","apexcharts-".concat(t,"-text")),e.globals.dom.Paper.add(n);}}}]),t}(),it=function(){function t(i){e(this,t),this.w=i.w,this.dCtx=i;}return a(t,[{key:"getTitleSubtitleCoords",value:function(t){var e=this.w,i=0,a=0,s="title"===t?e.config.title.floating:e.config.subtitle.floating,r=e.globals.dom.baseEl.querySelector(".apexcharts-".concat(t,"-text"));if(null!==r&&!s){var n=r.getBoundingClientRect();i=n.width,a=e.globals.axisCharts?n.height+5:n.height;}return {width:i,height:a}}},{key:"getLegendsRect",value:function(){var t=this.w,e=t.globals.dom.baseEl.querySelector(".apexcharts-legend");t.config.legend.height||"top"!==t.config.legend.position&&"bottom"!==t.config.legend.position||(e.style.maxHeight=t.globals.svgHeight/2+"px");var i=Object.assign({},f.getBoundingClientRect(e));return null!==e&&!t.config.legend.floating&&t.config.legend.show?this.dCtx.lgRect={x:i.x,y:i.y,height:i.height,width:0===i.height?0:i.width}:this.dCtx.lgRect={x:0,y:0,height:0,width:0},"left"!==t.config.legend.position&&"right"!==t.config.legend.position||1.5*this.dCtx.lgRect.width>t.globals.svgWidth&&(this.dCtx.lgRect.width=t.globals.svgWidth/1.5),this.dCtx.lgRect}},{key:"getLargestStringFromMultiArr",value:function(t,e){var i=t;if(this.w.globals.isMultiLineX){var a=e.map((function(t,e){return Array.isArray(t)?t.length:1})),s=Math.max.apply(Math,g(a));i=e[a.indexOf(s)];}return i}}]),t}(),at=function(){function t(i){e(this,t),this.w=i.w,this.dCtx=i;}return a(t,[{key:"getxAxisLabelsCoords",value:function(){var t,e=this.w,i=e.globals.labels.slice();if(e.config.xaxis.convertedCatToNumeric&&0===i.length&&(i=e.globals.categoryLabels),e.globals.timescaleLabels.length>0){var a=this.getxAxisTimeScaleLabelsCoords();t={width:a.width,height:a.height},e.globals.rotateXLabels=!1;}else {this.dCtx.lgWidthForSideLegends="left"!==e.config.legend.position&&"right"!==e.config.legend.position||e.config.legend.floating?0:this.dCtx.lgRect.width;var s=e.globals.xLabelFormatter,r=f.getLargestStringFromArr(i),n=this.dCtx.dimHelpers.getLargestStringFromMultiArr(r,i);e.globals.isBarHorizontal&&(n=r=e.globals.yAxisScale[0].result.reduce((function(t,e){return t.length>e.length?t:e}),0));var o=new W(this.dCtx.ctx),l=r;r=o.xLabelFormat(s,r,l,{i:void 0,dateFormatter:new y(this.dCtx.ctx).formatDate,w:e}),n=o.xLabelFormat(s,n,l,{i:void 0,dateFormatter:new y(this.dCtx.ctx).formatDate,w:e}),(e.config.xaxis.convertedCatToNumeric&&void 0===r||""===String(r).trim())&&(n=r="1");var h=new b(this.dCtx.ctx),c=h.getTextRects(r,e.config.xaxis.labels.style.fontSize),d=c;if(r!==n&&(d=h.getTextRects(n,e.config.xaxis.labels.style.fontSize)),(t={width:c.width>=d.width?c.width:d.width,height:c.height>=d.height?c.height:d.height}).width*i.length>e.globals.svgWidth-this.dCtx.lgWidthForSideLegends-this.dCtx.yAxisWidth-this.dCtx.gridPad.left-this.dCtx.gridPad.right&&0!==e.config.xaxis.labels.rotate||e.config.xaxis.labels.rotateAlways){if(!e.globals.isBarHorizontal){e.globals.rotateXLabels=!0;var g=function(t){return h.getTextRects(t,e.config.xaxis.labels.style.fontSize,e.config.xaxis.labels.style.fontFamily,"rotate(".concat(e.config.xaxis.labels.rotate," 0 0)"),!1)};c=g(r),r!==n&&(d=g(n)),t.height=(c.height>d.height?c.height:d.height)/1.5,t.width=c.width>d.width?c.width:d.width;}}else e.globals.rotateXLabels=!1;}return e.config.xaxis.labels.show||(t={width:0,height:0}),{width:t.width,height:t.height}}},{key:"getxAxisTitleCoords",value:function(){var t=this.w,e=0,i=0;if(void 0!==t.config.xaxis.title.text){var a=new b(this.dCtx.ctx).getTextRects(t.config.xaxis.title.text,t.config.xaxis.title.style.fontSize);e=a.width,i=a.height;}return {width:e,height:i}}},{key:"getxAxisTimeScaleLabelsCoords",value:function(){var t,e=this.w;this.dCtx.timescaleLabels=e.globals.timescaleLabels.slice();var i=this.dCtx.timescaleLabels.map((function(t){return t.value})),a=i.reduce((function(t,e){return void 0===t?(console.error("You have possibly supplied invalid Date format. Please supply a valid JavaScript Date"),0):t.length>e.length?t:e}),0);return 1.05*(t=new b(this.dCtx.ctx).getTextRects(a,e.config.xaxis.labels.style.fontSize)).width*i.length>e.globals.gridWidth&&0!==e.config.xaxis.labels.rotate&&(e.globals.overlappingXLabels=!0),t}},{key:"additionalPaddingXLabels",value:function(t){var e=this,i=this.w,a=i.globals,s=i.config,r=s.xaxis.type,n=t.width;a.skipLastTimelinelabel=!1,a.skipFirstTimelinelabel=!1;var o=i.config.yaxis[0].opposite&&i.globals.isBarHorizontal,l=function(t,o){(function(t){return -1!==a.collapsedSeriesIndices.indexOf(t)})(o)||("datetime"!==r&&e.dCtx.gridPad.left<n/2-e.dCtx.yAxisWidthLeft&&!a.rotateXLabels&&!s.xaxis.labels.trim&&(e.dCtx.xPadLeft=n/2+1),function(t){if(e.dCtx.timescaleLabels&&e.dCtx.timescaleLabels.length){var s=e.dCtx.timescaleLabels[0],o=e.dCtx.timescaleLabels[e.dCtx.timescaleLabels.length-1].position+n/1.75-e.dCtx.yAxisWidthRight,l=s.position-n/1.75+e.dCtx.yAxisWidthLeft;o>a.svgWidth-a.translateX&&(a.skipLastTimelinelabel=!0),l<0&&(a.skipFirstTimelinelabel=!0);}else "datetime"===r?e.dCtx.gridPad.right<n&&!a.rotateXLabels&&(a.skipLastTimelinelabel=!0):"datetime"!==r&&e.dCtx.gridPad.right<n/2-e.dCtx.yAxisWidthRight&&!a.rotateXLabels&&("between"!==i.config.xaxis.tickPlacement||i.globals.isBarHorizontal)&&(e.dCtx.xPadRight=n/2+1);}());};s.yaxis.forEach((function(t,i){o?(e.dCtx.gridPad.left<n&&(e.dCtx.xPadLeft=n/2+1),e.dCtx.xPadRight=n/2+1):l(0,i);}));}}]),t}(),st=function(){function t(i){e(this,t),this.w=i.w,this.dCtx=i;}return a(t,[{key:"getyAxisLabelsCoords",value:function(){var t=this,e=this.w,i=[],a=10,s=new B(this.dCtx.ctx);return e.config.yaxis.map((function(r,n){var o=e.globals.yAxisScale[n];if(!s.isYAxisHidden(n)&&r.labels.show&&o.result.length){var l=e.globals.yLabelFormatters[n],h=String(o.niceMin).length>String(o.niceMax).length?o.niceMin:o.niceMax,c=l(h,{seriesIndex:n,dataPointIndex:-1,w:e}),d=c;if(void 0!==c&&0!==c.length||(c=h),e.globals.isBarHorizontal){a=0;var g=e.globals.labels.slice();c=l(c=f.getLargestStringFromArr(g),{seriesIndex:n,dataPointIndex:-1,w:e}),d=t.dCtx.dimHelpers.getLargestStringFromMultiArr(c,g);}var u=new b(t.dCtx.ctx),p=u.getTextRects(c,r.labels.style.fontSize),x=p;c!==d&&(x=u.getTextRects(d,r.labels.style.fontSize)),i.push({width:(x.width>p.width?x.width:p.width)+a,height:x.height>p.height?x.height:p.height});}else i.push({width:0,height:0});})),i}},{key:"getyAxisTitleCoords",value:function(){var t=this,e=this.w,i=[];return e.config.yaxis.map((function(e,a){if(e.show&&void 0!==e.title.text){var s=new b(t.dCtx.ctx).getTextRects(e.title.text,e.title.style.fontSize,e.title.style.fontFamily,"rotate(-90 0 0)",!1);i.push({width:s.width,height:s.height});}else i.push({width:0,height:0});})),i}},{key:"getTotalYAxisWidth",value:function(){var t=this.w,e=0,i=0,a=0,s=t.globals.yAxisScale.length>1?10:0,r=new B(this.dCtx.ctx),n=function(n,o){var l=t.config.yaxis[o].floating,h=0;n.width>0&&!l?(h=n.width+s,function(e){return t.globals.ignoreYAxisIndexes.indexOf(e)>-1}(o)&&(h=h-n.width-s)):h=l||r.isYAxisHidden(o)?0:5,t.config.yaxis[o].opposite?a+=h:i+=h,e+=h;};return t.globals.yLabelsCoords.map((function(t,e){n(t,e);})),t.globals.yTitleCoords.map((function(t,e){n(t,e);})),t.globals.isBarHorizontal&&(e=t.globals.yLabelsCoords[0].width+t.globals.yTitleCoords[0].width+15),this.dCtx.yAxisWidthLeft=i,this.dCtx.yAxisWidthRight=a,e}}]),t}(),rt=function(){function t(i){e(this,t),this.w=i.w,this.dCtx=i;}return a(t,[{key:"gridPadForColumnsInNumericAxis",value:function(t){var e=this.w;if(e.globals.noData||e.globals.allSeriesCollapsed)return 0;var i=e.config.chart.type,a=0,s="bar"===i||"rangeBar"===i?e.config.series.length:1;if(e.globals.comboBarCount>0&&(s=e.globals.comboBarCount),e.globals.collapsedSeries.forEach((function(t){"bar"!==t.type&&"rangeBar"!==t.type||(s-=1);})),e.config.chart.stacked&&(s=1),("bar"===i||"rangeBar"===i||e.globals.comboBarCount>0)&&e.globals.isXNumeric&&!e.globals.isBarHorizontal&&s>0){var r,n,o=Math.abs(e.globals.initialMaxX-e.globals.initialMinX);o<=3&&(o=e.globals.dataPoints),r=o/t,e.globals.minXDiff&&e.globals.minXDiff/r>0&&(n=e.globals.minXDiff/r),n>t/2&&(n/=2),(a=n/s*parseInt(e.config.plotOptions.bar.columnWidth,10)/100)<1&&(a=1),a=a/(s>1?1:1.5)+5,e.globals.barPadForNumericAxis=a;}return a}},{key:"gridPadFortitleSubtitle",value:function(){var t=this,e=this.w,i=e.globals,a=this.dCtx.isSparkline||!e.globals.axisCharts?0:10;["title","subtitle"].forEach((function(i){void 0!==e.config[i].text?a+=e.config[i].margin:a+=t.dCtx.isSparkline||!e.globals.axisCharts?0:5;})),!e.config.legend.show||"bottom"!==e.config.legend.position||e.config.legend.floating||e.globals.axisCharts||(a+=10);var s=this.dCtx.dimHelpers.getTitleSubtitleCoords("title"),r=this.dCtx.dimHelpers.getTitleSubtitleCoords("subtitle");i.gridHeight=i.gridHeight-s.height-r.height-a,i.translateY=i.translateY+s.height+r.height+a;}},{key:"setGridXPosForDualYAxis",value:function(t,e){var i=this.w,a=new B(this.dCtx.ctx);i.config.yaxis.map((function(s,r){-1!==i.globals.ignoreYAxisIndexes.indexOf(r)||s.floating||a.isYAxisHidden(r)||(s.opposite&&(i.globals.translateX=i.globals.translateX-(e[r].width+t[r].width)-parseInt(i.config.yaxis[r].labels.style.fontSize,10)/1.2-12),i.globals.translateX<2&&(i.globals.translateX=2));}));}}]),t}(),nt=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.lgRect={},this.yAxisWidth=0,this.yAxisWidthLeft=0,this.yAxisWidthRight=0,this.xAxisHeight=0,this.isSparkline=this.w.config.chart.sparkline.enabled,this.dimHelpers=new it(this),this.dimYAxis=new st(this),this.dimXAxis=new at(this),this.dimGrid=new rt(this),this.lgWidthForSideLegends=0,this.gridPad=this.w.config.grid.padding,this.xPadRight=0,this.xPadLeft=0;}return a(t,[{key:"plotCoords",value:function(){var t=this.w.globals;this.lgRect=this.dimHelpers.getLegendsRect(),t.axisCharts?this.setDimensionsForAxisCharts():this.setDimensionsForNonAxisCharts(),this.dimGrid.gridPadFortitleSubtitle(),t.gridHeight=t.gridHeight-this.gridPad.top-this.gridPad.bottom,t.gridWidth=t.gridWidth-this.gridPad.left-this.gridPad.right-this.xPadRight-this.xPadLeft;var e=this.dimGrid.gridPadForColumnsInNumericAxis(t.gridWidth);t.gridWidth=t.gridWidth-2*e,t.translateX=t.translateX+this.gridPad.left+this.xPadLeft+(e>0?e+4:0),t.translateY=t.translateY+this.gridPad.top;}},{key:"setDimensionsForAxisCharts",value:function(){var t=this,e=this.w,i=e.globals,a=this.dimYAxis.getyAxisLabelsCoords(),s=this.dimYAxis.getyAxisTitleCoords();e.globals.yLabelsCoords=[],e.globals.yTitleCoords=[],e.config.yaxis.map((function(t,i){e.globals.yLabelsCoords.push({width:a[i].width,index:i}),e.globals.yTitleCoords.push({width:s[i].width,index:i});})),this.yAxisWidth=this.dimYAxis.getTotalYAxisWidth();var r=this.dimXAxis.getxAxisLabelsCoords(),n=this.dimXAxis.getxAxisTitleCoords();this.conditionalChecksForAxisCoords(r,n),i.translateXAxisY=e.globals.rotateXLabels?this.xAxisHeight/8:-4,i.translateXAxisX=e.globals.rotateXLabels&&e.globals.isXNumeric&&e.config.xaxis.labels.rotate<=-45?-this.xAxisWidth/4:0,e.globals.isBarHorizontal&&(i.rotateXLabels=!1,i.translateXAxisY=parseInt(e.config.xaxis.labels.style.fontSize,10)/1.5*-1),i.translateXAxisY=i.translateXAxisY+e.config.xaxis.labels.offsetY,i.translateXAxisX=i.translateXAxisX+e.config.xaxis.labels.offsetX;var o=this.yAxisWidth,l=this.xAxisHeight;i.xAxisLabelsHeight=this.xAxisHeight,i.xAxisLabelsWidth=this.xAxisWidth,i.xAxisHeight=this.xAxisHeight;var h=10;("radar"===e.config.chart.type||this.isSparkline)&&(o=0,l=i.goldenPadding),this.isSparkline&&(this.lgRect={height:0,width:0}),(this.isSparkline||"treemap"===e.config.chart.type)&&(o=0,l=0,h=0),this.isSparkline||this.dimXAxis.additionalPaddingXLabels(r);var c=function(){i.translateX=o,i.gridHeight=i.svgHeight-t.lgRect.height-l-(t.isSparkline||"treemap"===e.config.chart.type?0:e.globals.rotateXLabels?10:15),i.gridWidth=i.svgWidth-o;};switch("top"===e.config.xaxis.position&&(h=i.xAxisHeight-e.config.xaxis.axisTicks.height-5),e.config.legend.position){case"bottom":i.translateY=h,c();break;case"top":i.translateY=this.lgRect.height+h,c();break;case"left":i.translateY=h,i.translateX=this.lgRect.width+o,i.gridHeight=i.svgHeight-l-12,i.gridWidth=i.svgWidth-this.lgRect.width-o;break;case"right":i.translateY=h,i.translateX=o,i.gridHeight=i.svgHeight-l-12,i.gridWidth=i.svgWidth-this.lgRect.width-o-5;break;default:throw new Error("Legend position not supported")}this.dimGrid.setGridXPosForDualYAxis(s,a),new q(this.ctx).setYAxisXPosition(a,s);}},{key:"setDimensionsForNonAxisCharts",value:function(){var t=this.w,e=t.globals,i=t.config,a=0;t.config.legend.show&&!t.config.legend.floating&&(a=20);var s="pie"===i.chart.type||"polarArea"===i.chart.type||"donut"===i.chart.type?"pie":"radialBar",r=i.plotOptions[s].offsetY,n=i.plotOptions[s].offsetX;if(!i.legend.show||i.legend.floating)return e.gridHeight=e.svgHeight-i.grid.padding.left+i.grid.padding.right,e.gridWidth=e.gridHeight,e.translateY=r,void(e.translateX=n+(e.svgWidth-e.gridWidth)/2);switch(i.legend.position){case"bottom":e.gridHeight=e.svgHeight-this.lgRect.height-e.goldenPadding,e.gridWidth=e.gridHeight,e.translateY=r-10,e.translateX=n+(e.svgWidth-e.gridWidth)/2;break;case"top":e.gridHeight=e.svgHeight-this.lgRect.height-e.goldenPadding,e.gridWidth=e.gridHeight,e.translateY=this.lgRect.height+r+10,e.translateX=n+(e.svgWidth-e.gridWidth)/2;break;case"left":e.gridWidth=e.svgWidth-this.lgRect.width-a,e.gridHeight="auto"!==i.chart.height?e.svgHeight:e.gridWidth,e.translateY=r,e.translateX=n+this.lgRect.width+a;break;case"right":e.gridWidth=e.svgWidth-this.lgRect.width-a-5,e.gridHeight="auto"!==i.chart.height?e.svgHeight:e.gridWidth,e.translateY=r,e.translateX=n+10;break;default:throw new Error("Legend position not supported")}}},{key:"conditionalChecksForAxisCoords",value:function(t,e){var i=this.w;this.xAxisHeight=(t.height+e.height)*(i.globals.isMultiLineX?1.2:i.globals.LINE_HEIGHT_RATIO)+(i.globals.rotateXLabels?22:10),this.xAxisWidth=t.width,this.xAxisHeight-e.height>i.config.xaxis.labels.maxHeight&&(this.xAxisHeight=i.config.xaxis.labels.maxHeight),i.config.xaxis.labels.minHeight&&this.xAxisHeight<i.config.xaxis.labels.minHeight&&(this.xAxisHeight=i.config.xaxis.labels.minHeight),i.config.xaxis.floating&&(this.xAxisHeight=0);var a=0,s=0;i.config.yaxis.forEach((function(t){a+=t.labels.minWidth,s+=t.labels.maxWidth;})),this.yAxisWidth<a&&(this.yAxisWidth=a),this.yAxisWidth>s&&(this.yAxisWidth=s);}}]),t}(),ot=function(){function t(i){e(this,t),this.w=i.w,this.lgCtx=i;}return a(t,[{key:"getLegendStyles",value:function(){var t=document.createElement("style");t.setAttribute("type","text/css");var e=document.createTextNode("\t\n    \t\n      .apexcharts-legend {\t\n        display: flex;\t\n        overflow: auto;\t\n        padding: 0 10px;\t\n      }\t\n      .apexcharts-legend.position-bottom, .apexcharts-legend.position-top {\t\n        flex-wrap: wrap\t\n      }\t\n      .apexcharts-legend.position-right, .apexcharts-legend.position-left {\t\n        flex-direction: column;\t\n        bottom: 0;\t\n      }\t\n      .apexcharts-legend.position-bottom.apexcharts-align-left, .apexcharts-legend.position-top.apexcharts-align-left, .apexcharts-legend.position-right, .apexcharts-legend.position-left {\t\n        justify-content: flex-start;\t\n      }\t\n      .apexcharts-legend.position-bottom.apexcharts-align-center, .apexcharts-legend.position-top.apexcharts-align-center {\t\n        justify-content: center;  \t\n      }\t\n      .apexcharts-legend.position-bottom.apexcharts-align-right, .apexcharts-legend.position-top.apexcharts-align-right {\t\n        justify-content: flex-end;\t\n      }\t\n      .apexcharts-legend-series {\t\n        cursor: pointer;\t\n        line-height: normal;\t\n      }\t\n      .apexcharts-legend.position-bottom .apexcharts-legend-series, .apexcharts-legend.position-top .apexcharts-legend-series{\t\n        display: flex;\t\n        align-items: center;\t\n      }\t\n      .apexcharts-legend-text {\t\n        position: relative;\t\n        font-size: 14px;\t\n      }\t\n      .apexcharts-legend-text *, .apexcharts-legend-marker * {\t\n        pointer-events: none;\t\n      }\t\n      .apexcharts-legend-marker {\t\n        position: relative;\t\n        display: inline-block;\t\n        cursor: pointer;\t\n        margin-right: 3px;\t\n        border-style: solid;\n      }\t\n      \t\n      .apexcharts-legend.apexcharts-align-right .apexcharts-legend-series, .apexcharts-legend.apexcharts-align-left .apexcharts-legend-series{\t\n        display: inline-block;\t\n      }\t\n      .apexcharts-legend-series.apexcharts-no-click {\t\n        cursor: auto;\t\n      }\t\n      .apexcharts-legend .apexcharts-hidden-zero-series, .apexcharts-legend .apexcharts-hidden-null-series {\t\n        display: none !important;\t\n      }\t\n      .apexcharts-inactive-legend {\t\n        opacity: 0.45;\t\n      }");return t.appendChild(e),t}},{key:"getLegendBBox",value:function(){var t=this.w.globals.dom.baseEl.querySelector(".apexcharts-legend").getBoundingClientRect(),e=t.width;return {clwh:t.height,clww:e}}},{key:"appendToForeignObject",value:function(){var t=this.w.globals;t.dom.elLegendForeign=document.createElementNS(t.SVGNS,"foreignObject");var e=t.dom.elLegendForeign;e.setAttribute("x",0),e.setAttribute("y",0),e.setAttribute("width",t.svgWidth),e.setAttribute("height",t.svgHeight),t.dom.elLegendWrap.setAttribute("xmlns","http://www.w3.org/1999/xhtml"),e.appendChild(t.dom.elLegendWrap),e.appendChild(this.getLegendStyles()),t.dom.Paper.node.insertBefore(e,t.dom.elGraphical.node);}},{key:"toggleDataSeries",value:function(t,e){var i=this,a=this.w;if(a.globals.axisCharts||"radialBar"===a.config.chart.type){a.globals.resized=!0;var s=null,r=null;if(a.globals.risingSeries=[],a.globals.axisCharts?(s=a.globals.dom.baseEl.querySelector(".apexcharts-series[data\\:realIndex='".concat(t,"']")),r=parseInt(s.getAttribute("data:realIndex"),10)):(s=a.globals.dom.baseEl.querySelector(".apexcharts-series[rel='".concat(t+1,"']")),r=parseInt(s.getAttribute("rel"),10)-1),e)[{cs:a.globals.collapsedSeries,csi:a.globals.collapsedSeriesIndices},{cs:a.globals.ancillaryCollapsedSeries,csi:a.globals.ancillaryCollapsedSeriesIndices}].forEach((function(t){i.riseCollapsedSeries(t.cs,t.csi,r);}));else this.hideSeries({seriesEl:s,realIndex:r});}else {var n=a.globals.dom.Paper.select(" .apexcharts-series[rel='".concat(t+1,"'] path")),o=a.config.chart.type;if("pie"===o||"polarArea"===o||"donut"===o){var l=a.config.plotOptions.pie.donut.labels;new b(this.lgCtx.ctx).pathMouseDown(n.members[0],null),this.lgCtx.ctx.pie.printDataLabelsInner(n.members[0].node,l);}n.fire("click");}}},{key:"hideSeries",value:function(t){var e=t.seriesEl,i=t.realIndex,a=this.w,s=f.clone(a.config.series);if(a.globals.axisCharts){var r=!1;if(a.config.yaxis[i]&&a.config.yaxis[i].show&&a.config.yaxis[i].showAlways&&(r=!0,a.globals.ancillaryCollapsedSeriesIndices.indexOf(i)<0&&(a.globals.ancillaryCollapsedSeries.push({index:i,data:s[i].data.slice(),type:e.parentNode.className.baseVal.split("-")[1]}),a.globals.ancillaryCollapsedSeriesIndices.push(i))),!r){a.globals.collapsedSeries.push({index:i,data:s[i].data.slice(),type:e.parentNode.className.baseVal.split("-")[1]}),a.globals.collapsedSeriesIndices.push(i);var n=a.globals.risingSeries.indexOf(i);a.globals.risingSeries.splice(n,1);}}else a.globals.collapsedSeries.push({index:i,data:s[i]}),a.globals.collapsedSeriesIndices.push(i);for(var o=e.childNodes,l=0;l<o.length;l++)o[l].classList.contains("apexcharts-series-markers-wrap")&&(o[l].classList.contains("apexcharts-hide")?o[l].classList.remove("apexcharts-hide"):o[l].classList.add("apexcharts-hide"));a.globals.allSeriesCollapsed=a.globals.collapsedSeries.length===a.config.series.length,s=this._getSeriesBasedOnCollapsedState(s),this.lgCtx.ctx.updateHelpers._updateSeries(s,a.config.chart.animations.dynamicAnimation.enabled);}},{key:"riseCollapsedSeries",value:function(t,e,i){var a=this.w,s=f.clone(a.config.series);if(t.length>0){for(var r=0;r<t.length;r++)t[r].index===i&&(a.globals.axisCharts?(s[i].data=t[r].data.slice(),t.splice(r,1),e.splice(r,1),a.globals.risingSeries.push(i)):(s[i]=t[r].data,t.splice(r,1),e.splice(r,1),a.globals.risingSeries.push(i)));s=this._getSeriesBasedOnCollapsedState(s),this.lgCtx.ctx.updateHelpers._updateSeries(s,a.config.chart.animations.dynamicAnimation.enabled);}}},{key:"_getSeriesBasedOnCollapsedState",value:function(t){var e=this.w;return e.globals.axisCharts?t.forEach((function(i,a){e.globals.collapsedSeriesIndices.indexOf(a)>-1&&(t[a].data=[]);})):t.forEach((function(i,a){e.globals.collapsedSeriesIndices.indexOf(a)>-1&&(t[a]=0);})),t}}]),t}(),lt=function(){function t(i,a){e(this,t),this.ctx=i,this.w=i.w,this.onLegendClick=this.onLegendClick.bind(this),this.onLegendHovered=this.onLegendHovered.bind(this),this.isBarsDistributed="bar"===this.w.config.chart.type&&this.w.config.plotOptions.bar.distributed&&1===this.w.config.series.length,this.legendHelpers=new ot(this);}return a(t,[{key:"init",value:function(){var t=this.w,e=t.globals,i=t.config;if((i.legend.showForSingleSeries&&1===e.series.length||this.isBarsDistributed||e.series.length>1||!e.axisCharts)&&i.legend.show){for(;e.dom.elLegendWrap.firstChild;)e.dom.elLegendWrap.removeChild(e.dom.elLegendWrap.firstChild);this.drawLegends(),f.isIE11()?document.getElementsByTagName("head")[0].appendChild(this.legendHelpers.getLegendStyles()):this.legendHelpers.appendToForeignObject(),"bottom"===i.legend.position||"top"===i.legend.position?this.legendAlignHorizontal():"right"!==i.legend.position&&"left"!==i.legend.position||this.legendAlignVertical();}}},{key:"drawLegends",value:function(){var t=this.w,e=t.config.legend.fontFamily,i=t.globals.seriesNames,a=t.globals.colors.slice();if("heatmap"===t.config.chart.type){var s=t.config.plotOptions.heatmap.colorScale.ranges;i=s.map((function(t){return t.name?t.name:t.from+" - "+t.to})),a=s.map((function(t){return t.color}));}else this.isBarsDistributed&&(i=t.globals.labels.slice());for(var r=t.globals.legendFormatter,n=t.config.legend.inverseOrder,o=n?i.length-1:0;n?o>=0:o<=i.length-1;n?o--:o++){var l=r(i[o],{seriesIndex:o,w:t}),h=!1,c=!1;if(t.globals.collapsedSeries.length>0)for(var d=0;d<t.globals.collapsedSeries.length;d++)t.globals.collapsedSeries[d].index===o&&(h=!0);if(t.globals.ancillaryCollapsedSeriesIndices.length>0)for(var g=0;g<t.globals.ancillaryCollapsedSeriesIndices.length;g++)t.globals.ancillaryCollapsedSeriesIndices[g]===o&&(c=!0);var u=document.createElement("span");u.classList.add("apexcharts-legend-marker");var p=t.config.legend.markers.offsetX,x=t.config.legend.markers.offsetY,m=t.config.legend.markers.height,v=t.config.legend.markers.width,y=t.config.legend.markers.strokeWidth,k=t.config.legend.markers.strokeColor,A=t.config.legend.markers.radius,S=u.style;S.background=a[o],S.color=a[o],S.setProperty("background",a[o],"important"),t.config.legend.markers.fillColors&&t.config.legend.markers.fillColors[o]&&(S.background=t.config.legend.markers.fillColors[o]),void 0!==t.globals.seriesColors[o]&&(S.background=t.globals.seriesColors[o],S.color=t.globals.seriesColors[o]),S.height=Array.isArray(m)?parseFloat(m[o])+"px":parseFloat(m)+"px",S.width=Array.isArray(v)?parseFloat(v[o])+"px":parseFloat(v)+"px",S.left=Array.isArray(p)?p[o]:p,S.top=Array.isArray(x)?x[o]:x,S.borderWidth=Array.isArray(y)?y[o]:y,S.borderColor=Array.isArray(k)?k[o]:k,S.borderRadius=Array.isArray(A)?parseFloat(A[o])+"px":parseFloat(A)+"px",t.config.legend.markers.customHTML&&(Array.isArray(t.config.legend.markers.customHTML)?t.config.legend.markers.customHTML[o]&&(u.innerHTML=t.config.legend.markers.customHTML[o]()):u.innerHTML=t.config.legend.markers.customHTML()),b.setAttrs(u,{rel:o+1,"data:collapsed":h||c}),(h||c)&&u.classList.add("apexcharts-inactive-legend");var C=document.createElement("div"),L=document.createElement("span");L.classList.add("apexcharts-legend-text"),L.innerHTML=Array.isArray(l)?l.join(" "):l;var P=t.config.legend.labels.useSeriesColors?t.globals.colors[o]:t.config.legend.labels.colors;P||(P=t.config.chart.foreColor),L.style.color=P,L.style.fontSize=parseFloat(t.config.legend.fontSize)+"px",L.style.fontWeight=t.config.legend.fontWeight,L.style.fontFamily=e||t.config.chart.fontFamily,b.setAttrs(L,{rel:o+1,i:o,"data:default-text":encodeURIComponent(l),"data:collapsed":h||c}),C.appendChild(u),C.appendChild(L);var T=new w(this.ctx);if(!t.config.legend.showForZeroSeries)0===T.getSeriesTotalByIndex(o)&&T.seriesHaveSameValues(o)&&!T.isSeriesNull(o)&&-1===t.globals.collapsedSeriesIndices.indexOf(o)&&-1===t.globals.ancillaryCollapsedSeriesIndices.indexOf(o)&&C.classList.add("apexcharts-hidden-zero-series");t.config.legend.showForNullSeries||T.isSeriesNull(o)&&-1===t.globals.collapsedSeriesIndices.indexOf(o)&&-1===t.globals.ancillaryCollapsedSeriesIndices.indexOf(o)&&C.classList.add("apexcharts-hidden-null-series"),t.globals.dom.elLegendWrap.appendChild(C),t.globals.dom.elLegendWrap.classList.add("apexcharts-align-".concat(t.config.legend.horizontalAlign)),t.globals.dom.elLegendWrap.classList.add("position-"+t.config.legend.position),C.classList.add("apexcharts-legend-series"),C.style.margin="".concat(t.config.legend.itemMargin.vertical,"px ").concat(t.config.legend.itemMargin.horizontal,"px"),t.globals.dom.elLegendWrap.style.width=t.config.legend.width?t.config.legend.width+"px":"",t.globals.dom.elLegendWrap.style.height=t.config.legend.height?t.config.legend.height+"px":"",b.setAttrs(C,{rel:o+1,seriesName:f.escapeString(i[o]),"data:collapsed":h||c}),(h||c)&&C.classList.add("apexcharts-inactive-legend"),t.config.legend.onItemClick.toggleDataSeries||C.classList.add("apexcharts-no-click");}"treemap"!==t.config.chart.type&&"heatmap"!==t.config.chart.type&&!this.isBarsDistributed&&t.config.legend.onItemClick.toggleDataSeries&&t.globals.dom.elWrap.addEventListener("click",this.onLegendClick,!0),t.config.legend.onItemHover.highlightDataSeries&&(t.globals.dom.elWrap.addEventListener("mousemove",this.onLegendHovered,!0),t.globals.dom.elWrap.addEventListener("mouseout",this.onLegendHovered,!0));}},{key:"setLegendWrapXY",value:function(t,e){var i=this.w,a=i.globals.dom.baseEl.querySelector(".apexcharts-legend"),s=a.getBoundingClientRect(),r=0,n=0;if("bottom"===i.config.legend.position)n+=i.globals.svgHeight-s.height/2;else if("top"===i.config.legend.position){var o=new nt(this.ctx),l=o.dimHelpers.getTitleSubtitleCoords("title").height,h=o.dimHelpers.getTitleSubtitleCoords("subtitle").height;n=n+(l>0?l-10:0)+(h>0?h-10:0);}a.style.position="absolute",r=r+t+i.config.legend.offsetX,n=n+e+i.config.legend.offsetY,a.style.left=r+"px",a.style.top=n+"px","bottom"===i.config.legend.position?(a.style.top="auto",a.style.bottom=5-i.config.legend.offsetY+"px"):"right"===i.config.legend.position&&(a.style.left="auto",a.style.right=25+i.config.legend.offsetX+"px");["width","height"].forEach((function(t){a.style[t]&&(a.style[t]=parseInt(i.config.legend[t],10)+"px");}));}},{key:"legendAlignHorizontal",value:function(){var t=this.w;t.globals.dom.baseEl.querySelector(".apexcharts-legend").style.right=0;var e=this.legendHelpers.getLegendBBox(),i=new nt(this.ctx),a=i.dimHelpers.getTitleSubtitleCoords("title"),s=i.dimHelpers.getTitleSubtitleCoords("subtitle"),r=0;"bottom"===t.config.legend.position?r=-e.clwh/1.8:"top"===t.config.legend.position&&(r=a.height+s.height+t.config.title.margin+t.config.subtitle.margin-10),this.setLegendWrapXY(20,r);}},{key:"legendAlignVertical",value:function(){var t=this.w,e=this.legendHelpers.getLegendBBox(),i=0;"left"===t.config.legend.position&&(i=20),"right"===t.config.legend.position&&(i=t.globals.svgWidth-e.clww-10),this.setLegendWrapXY(i,20);}},{key:"onLegendHovered",value:function(t){var e=this.w,i=t.target.classList.contains("apexcharts-legend-text")||t.target.classList.contains("apexcharts-legend-marker");if("heatmap"===e.config.chart.type||this.isBarsDistributed){if(i){var a=parseInt(t.target.getAttribute("rel"),10)-1;this.ctx.events.fireEvent("legendHover",[this.ctx,a,this.w]),new E(this.ctx).highlightRangeInSeries(t,t.target);}}else !t.target.classList.contains("apexcharts-inactive-legend")&&i&&new E(this.ctx).toggleSeriesOnHover(t,t.target);}},{key:"onLegendClick",value:function(t){if(t.target.classList.contains("apexcharts-legend-text")||t.target.classList.contains("apexcharts-legend-marker")){var e=parseInt(t.target.getAttribute("rel"),10)-1,i="true"===t.target.getAttribute("data:collapsed"),a=this.w.config.chart.events.legendClick;"function"==typeof a&&a(this.ctx,e,this.w),this.ctx.events.fireEvent("legendClick",[this.ctx,e,this.w]);var s=this.w.config.legend.markers.onClick;"function"==typeof s&&t.target.classList.contains("apexcharts-legend-marker")&&(s(this.ctx,e,this.w),this.ctx.events.fireEvent("legendMarkerClick",[this.ctx,e,this.w])),this.legendHelpers.toggleDataSeries(e,i);}}}]),t}(),ht=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.ev=this.w.config.chart.events,this.selectedClass="apexcharts-selected",this.localeValues=this.w.globals.locale.toolbar;}return a(t,[{key:"createToolbar",value:function(){var t=this,e=this.w,i=function(){return document.createElement("div")},a=i();if(a.setAttribute("class","apexcharts-toolbar"),a.style.top=e.config.chart.toolbar.offsetY+"px",a.style.right=3-e.config.chart.toolbar.offsetX+"px",e.globals.dom.elWrap.appendChild(a),this.elZoom=i(),this.elZoomIn=i(),this.elZoomOut=i(),this.elPan=i(),this.elSelection=i(),this.elZoomReset=i(),this.elMenuIcon=i(),this.elMenu=i(),this.elCustomIcons=[],this.t=e.config.chart.toolbar.tools,Array.isArray(this.t.customIcons))for(var s=0;s<this.t.customIcons.length;s++)this.elCustomIcons.push(i());var r=[],n=function(i,a,s){var n=i.toLowerCase();t.t[n]&&e.config.chart.zoom.enabled&&r.push({el:a,icon:"string"==typeof t.t[n]?t.t[n]:s,title:t.localeValues[i],class:"apexcharts-".concat(n,"-icon")});};n("zoomIn",this.elZoomIn,'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">\n    <path d="M0 0h24v24H0z" fill="none"/>\n    <path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>\n</svg>\n'),n("zoomOut",this.elZoomOut,'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">\n    <path d="M0 0h24v24H0z" fill="none"/>\n    <path d="M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>\n</svg>\n');var o=function(i){t.t[i]&&e.config.chart[i].enabled&&r.push({el:"zoom"===i?t.elZoom:t.elSelection,icon:"string"==typeof t.t[i]?t.t[i]:"zoom"===i?'<svg xmlns="http://www.w3.org/2000/svg" fill="#000000" height="24" viewBox="0 0 24 24" width="24">\n    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>\n    <path d="M0 0h24v24H0V0z" fill="none"/>\n    <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/>\n</svg>':'<svg fill="#6E8192" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">\n    <path d="M0 0h24v24H0z" fill="none"/>\n    <path d="M3 5h2V3c-1.1 0-2 .9-2 2zm0 8h2v-2H3v2zm4 8h2v-2H7v2zM3 9h2V7H3v2zm10-6h-2v2h2V3zm6 0v2h2c0-1.1-.9-2-2-2zM5 21v-2H3c0 1.1.9 2 2 2zm-2-4h2v-2H3v2zM9 3H7v2h2V3zm2 18h2v-2h-2v2zm8-8h2v-2h-2v2zm0 8c1.1 0 2-.9 2-2h-2v2zm0-12h2V7h-2v2zm0 8h2v-2h-2v2zm-4 4h2v-2h-2v2zm0-16h2V3h-2v2z"/>\n</svg>',title:t.localeValues["zoom"===i?"selectionZoom":"selection"],class:e.globals.isTouchDevice?"apexcharts-element-hidden":"apexcharts-".concat(i,"-icon")});};o("zoom"),o("selection"),this.t.pan&&e.config.chart.zoom.enabled&&r.push({el:this.elPan,icon:"string"==typeof this.t.pan?this.t.pan:'<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" fill="#000000" height="24" viewBox="0 0 24 24" width="24">\n    <defs>\n        <path d="M0 0h24v24H0z" id="a"/>\n    </defs>\n    <clipPath id="b">\n        <use overflow="visible" xlink:href="#a"/>\n    </clipPath>\n    <path clip-path="url(#b)" d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z"/>\n</svg>',title:this.localeValues.pan,class:e.globals.isTouchDevice?"apexcharts-element-hidden":"apexcharts-pan-icon"}),n("reset",this.elZoomReset,'<svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">\n    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>\n    <path d="M0 0h24v24H0z" fill="none"/>\n</svg>'),this.t.download&&r.push({el:this.elMenuIcon,icon:"string"==typeof this.t.download?this.t.download:'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" d="M0 0h24v24H0V0z"/><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>',title:this.localeValues.menu,class:"apexcharts-menu-icon"});for(var l=0;l<this.elCustomIcons.length;l++)r.push({el:this.elCustomIcons[l],icon:this.t.customIcons[l].icon,title:this.t.customIcons[l].title,index:this.t.customIcons[l].index,class:"apexcharts-toolbar-custom-icon "+this.t.customIcons[l].class});r.forEach((function(t,e){t.index&&f.moveIndexInArray(r,e,t.index);}));for(var h=0;h<r.length;h++)b.setAttrs(r[h].el,{class:r[h].class,title:r[h].title}),r[h].el.innerHTML=r[h].icon,a.appendChild(r[h].el);this._createHamburgerMenu(a),e.globals.zoomEnabled?this.elZoom.classList.add(this.selectedClass):e.globals.panEnabled?this.elPan.classList.add(this.selectedClass):e.globals.selectionEnabled&&this.elSelection.classList.add(this.selectedClass),this.addToolbarEventListeners();}},{key:"_createHamburgerMenu",value:function(t){this.elMenuItems=[],t.appendChild(this.elMenu),b.setAttrs(this.elMenu,{class:"apexcharts-menu"});var e=[{name:"exportSVG",title:this.localeValues.exportToSVG},{name:"exportPNG",title:this.localeValues.exportToPNG},{name:"exportCSV",title:this.localeValues.exportToCSV}];this.w.globals.allSeriesHasEqualX||e.splice(2,1);for(var i=0;i<e.length;i++)this.elMenuItems.push(document.createElement("div")),this.elMenuItems[i].innerHTML=e[i].title,b.setAttrs(this.elMenuItems[i],{class:"apexcharts-menu-item ".concat(e[i].name),title:e[i].title}),this.elMenu.appendChild(this.elMenuItems[i]);}},{key:"addToolbarEventListeners",value:function(){var t=this;this.elZoomReset.addEventListener("click",this.handleZoomReset.bind(this)),this.elSelection.addEventListener("click",this.toggleZoomSelection.bind(this,"selection")),this.elZoom.addEventListener("click",this.toggleZoomSelection.bind(this,"zoom")),this.elZoomIn.addEventListener("click",this.handleZoomIn.bind(this)),this.elZoomOut.addEventListener("click",this.handleZoomOut.bind(this)),this.elPan.addEventListener("click",this.togglePanning.bind(this)),this.elMenuIcon.addEventListener("click",this.toggleMenu.bind(this)),this.elMenuItems.forEach((function(e){e.classList.contains("exportSVG")?e.addEventListener("click",t.handleDownload.bind(t,"svg")):e.classList.contains("exportPNG")?e.addEventListener("click",t.handleDownload.bind(t,"png")):e.classList.contains("exportCSV")&&e.addEventListener("click",t.handleDownload.bind(t,"csv"));}));for(var e=0;e<this.t.customIcons.length;e++)this.elCustomIcons[e].addEventListener("click",this.t.customIcons[e].click.bind(this,this.ctx,this.ctx.w));}},{key:"toggleZoomSelection",value:function(t){this.ctx.getSyncedCharts().forEach((function(e){e.ctx.toolbar.toggleOtherControls();var i="selection"===t?e.ctx.toolbar.elSelection:e.ctx.toolbar.elZoom,a="selection"===t?"selectionEnabled":"zoomEnabled";e.w.globals[a]=!e.w.globals[a],i.classList.contains(e.ctx.toolbar.selectedClass)?i.classList.remove(e.ctx.toolbar.selectedClass):i.classList.add(e.ctx.toolbar.selectedClass);}));}},{key:"getToolbarIconsReference",value:function(){var t=this.w;this.elZoom||(this.elZoom=t.globals.dom.baseEl.querySelector(".apexcharts-zoom-icon")),this.elPan||(this.elPan=t.globals.dom.baseEl.querySelector(".apexcharts-pan-icon")),this.elSelection||(this.elSelection=t.globals.dom.baseEl.querySelector(".apexcharts-selection-icon"));}},{key:"enableZoomPanFromToolbar",value:function(t){this.toggleOtherControls(),"pan"===t?this.w.globals.panEnabled=!0:this.w.globals.zoomEnabled=!0;var e="pan"===t?this.elPan:this.elZoom,i="pan"===t?this.elZoom:this.elPan;e&&e.classList.add(this.selectedClass),i&&i.classList.remove(this.selectedClass);}},{key:"togglePanning",value:function(){this.ctx.getSyncedCharts().forEach((function(t){t.ctx.toolbar.toggleOtherControls(),t.w.globals.panEnabled=!t.w.globals.panEnabled,t.ctx.toolbar.elPan.classList.contains(t.ctx.toolbar.selectedClass)?t.ctx.toolbar.elPan.classList.remove(t.ctx.toolbar.selectedClass):t.ctx.toolbar.elPan.classList.add(t.ctx.toolbar.selectedClass);}));}},{key:"toggleOtherControls",value:function(){var t=this,e=this.w;e.globals.panEnabled=!1,e.globals.zoomEnabled=!1,e.globals.selectionEnabled=!1,this.getToolbarIconsReference(),[this.elPan,this.elSelection,this.elZoom].forEach((function(e){e&&e.classList.remove(t.selectedClass);}));}},{key:"handleZoomIn",value:function(){var t=this.w,e=(t.globals.minX+t.globals.maxX)/2,i=(t.globals.minX+e)/2,a=(t.globals.maxX+e)/2,s=this._getNewMinXMaxX(i,a);t.globals.disableZoomIn||this.zoomUpdateOptions(s.minX,s.maxX);}},{key:"handleZoomOut",value:function(){var t=this.w;if(!("datetime"===t.config.xaxis.type&&new Date(t.globals.minX).getUTCFullYear()<1e3)){var e=(t.globals.minX+t.globals.maxX)/2,i=t.globals.minX-(e-t.globals.minX),a=t.globals.maxX-(e-t.globals.maxX),s=this._getNewMinXMaxX(i,a);t.globals.disableZoomOut||this.zoomUpdateOptions(s.minX,s.maxX);}}},{key:"_getNewMinXMaxX",value:function(t,e){var i=this.w.config.xaxis.convertedCatToNumeric;return {minX:i?Math.floor(t):t,maxX:i?Math.floor(e):e}}},{key:"zoomUpdateOptions",value:function(t,e){var i=this.w;if(void 0!==t||void 0!==e){if(!(i.config.xaxis.convertedCatToNumeric&&(t<1&&(t=1,e=i.globals.dataPoints),e-t<2))){var a={min:t,max:e},s=this.getBeforeZoomRange(a);s&&(a=s.xaxis);var r={xaxis:a},n=f.clone(i.globals.initialConfig.yaxis);if(i.config.chart.zoom.autoScaleYaxis)n=new j(this.ctx).autoScaleY(this.ctx,n,{xaxis:a});i.config.chart.group||(r.yaxis=n),this.w.globals.zoomed=!0,this.ctx.updateHelpers._updateOptions(r,!1,this.w.config.chart.animations.dynamicAnimation.enabled),this.zoomCallback(a,n);}}else this.handleZoomReset();}},{key:"zoomCallback",value:function(t,e){"function"==typeof this.ev.zoomed&&this.ev.zoomed(this.ctx,{xaxis:t,yaxis:e});}},{key:"getBeforeZoomRange",value:function(t,e){var i=null;return "function"==typeof this.ev.beforeZoom&&(i=this.ev.beforeZoom(this,{xaxis:t,yaxis:e})),i}},{key:"toggleMenu",value:function(){var t=this;window.setTimeout((function(){t.elMenu.classList.contains("apexcharts-menu-open")?t.elMenu.classList.remove("apexcharts-menu-open"):t.elMenu.classList.add("apexcharts-menu-open");}),0);}},{key:"handleDownload",value:function(t){var e=this.w,i=new V(this.ctx);switch(t){case"svg":i.exportToSVG(this.ctx);break;case"png":i.exportToPng(this.ctx);break;case"csv":i.exportToCSV({series:e.config.series,columnDelimiter:e.config.chart.toolbar.export.csv.columnDelimiter});}}},{key:"handleZoomReset",value:function(t){this.ctx.getSyncedCharts().forEach((function(t){var e=t.w;if(e.globals.lastXAxis.min=void 0,e.globals.lastXAxis.max=void 0,t.updateHelpers.revertDefaultAxisMinMax(),"function"==typeof e.config.chart.events.beforeResetZoom){var i=e.config.chart.events.beforeResetZoom(t,e);i&&t.updateHelpers.revertDefaultAxisMinMax(i);}"function"==typeof e.config.chart.events.zoomed&&t.ctx.toolbar.zoomCallback({min:e.config.xaxis.min,max:e.config.xaxis.max}),e.globals.zoomed=!1;var a=t.ctx.series.emptyCollapsedSeries(f.clone(e.globals.initialSeries));t.updateHelpers._updateSeries(a,e.config.chart.animations.dynamicAnimation.enabled);}));}},{key:"destroy",value:function(){this.elZoom=null,this.elZoomIn=null,this.elZoomOut=null,this.elPan=null,this.elSelection=null,this.elZoomReset=null,this.elMenuIcon=null;}}]),t}(),ct=function(t){o(s,t);var i=d(s);function s(t){var a;return e(this,s),(a=i.call(this,t)).ctx=t,a.w=t.w,a.dragged=!1,a.graphics=new b(a.ctx),a.eventList=["mousedown","mouseleave","mousemove","touchstart","touchmove","mouseup","touchend"],a.clientX=0,a.clientY=0,a.startX=0,a.endX=0,a.dragX=0,a.startY=0,a.endY=0,a.dragY=0,a.moveDirection="none",a}return a(s,[{key:"init",value:function(t){var e=this,i=t.xyRatios,a=this.w,s=this;this.xyRatios=i,this.zoomRect=this.graphics.drawRect(0,0,0,0),this.selectionRect=this.graphics.drawRect(0,0,0,0),this.gridRect=a.globals.dom.baseEl.querySelector(".apexcharts-grid"),this.zoomRect.node.classList.add("apexcharts-zoom-rect"),this.selectionRect.node.classList.add("apexcharts-selection-rect"),a.globals.dom.elGraphical.add(this.zoomRect),a.globals.dom.elGraphical.add(this.selectionRect),"x"===a.config.chart.selection.type?this.slDraggableRect=this.selectionRect.draggable({minX:0,minY:0,maxX:a.globals.gridWidth,maxY:a.globals.gridHeight}).on("dragmove",this.selectionDragging.bind(this,"dragging")):"y"===a.config.chart.selection.type?this.slDraggableRect=this.selectionRect.draggable({minX:0,maxX:a.globals.gridWidth}).on("dragmove",this.selectionDragging.bind(this,"dragging")):this.slDraggableRect=this.selectionRect.draggable().on("dragmove",this.selectionDragging.bind(this,"dragging")),this.preselectedSelection(),this.hoverArea=a.globals.dom.baseEl.querySelector("".concat(a.globals.chartClass," .apexcharts-svg")),this.hoverArea.classList.add("apexcharts-zoomable"),this.eventList.forEach((function(t){e.hoverArea.addEventListener(t,s.svgMouseEvents.bind(s,i),{capture:!1,passive:!0});}));}},{key:"destroy",value:function(){this.slDraggableRect&&(this.slDraggableRect.draggable(!1),this.slDraggableRect.off(),this.selectionRect.off()),this.selectionRect=null,this.zoomRect=null,this.gridRect=null;}},{key:"svgMouseEvents",value:function(t,e){var i=this.w,a=this,s=this.ctx.toolbar,r=i.globals.zoomEnabled?i.config.chart.zoom.type:i.config.chart.selection.type,n=i.config.chart.toolbar.autoSelected;e.shiftKey?(this.shiftWasPressed=!0,s.enableZoomPanFromToolbar("pan"===n?"zoom":"pan")):this.shiftWasPressed&&(s.enableZoomPanFromToolbar(n),this.shiftWasPressed=!1);var o=e.target.classList;if(!(o.contains("apexcharts-selection-rect")||o.contains("apexcharts-legend-marker")||o.contains("apexcharts-legend-text")||e.target.parentNode.classList.contains("apexcharts-toolbar"))){if(a.clientX="touchmove"===e.type||"touchstart"===e.type?e.touches[0].clientX:"touchend"===e.type?e.changedTouches[0].clientX:e.clientX,a.clientY="touchmove"===e.type||"touchstart"===e.type?e.touches[0].clientY:"touchend"===e.type?e.changedTouches[0].clientY:e.clientY,"mousedown"===e.type&&1===e.which){var l=a.gridRect.getBoundingClientRect();a.startX=a.clientX-l.left,a.startY=a.clientY-l.top,a.dragged=!1,a.w.globals.mousedown=!0;}if(("mousemove"===e.type&&1===e.which||"touchmove"===e.type)&&(a.dragged=!0,i.globals.panEnabled?(i.globals.selection=null,a.w.globals.mousedown&&a.panDragging({context:a,zoomtype:r,xyRatios:t})):(a.w.globals.mousedown&&i.globals.zoomEnabled||a.w.globals.mousedown&&i.globals.selectionEnabled)&&(a.selection=a.selectionDrawing({context:a,zoomtype:r}))),"mouseup"===e.type||"touchend"===e.type||"mouseleave"===e.type){var h=a.gridRect.getBoundingClientRect();a.w.globals.mousedown&&(a.endX=a.clientX-h.left,a.endY=a.clientY-h.top,a.dragX=Math.abs(a.endX-a.startX),a.dragY=Math.abs(a.endY-a.startY),(i.globals.zoomEnabled||i.globals.selectionEnabled)&&a.selectionDrawn({context:a,zoomtype:r}),i.globals.panEnabled&&i.config.xaxis.convertedCatToNumeric&&a.delayedPanScrolled()),i.globals.zoomEnabled&&a.hideSelectionRect(this.selectionRect),a.dragged=!1,a.w.globals.mousedown=!1;}this.makeSelectionRectDraggable();}}},{key:"makeSelectionRectDraggable",value:function(){var t=this.w;if(this.selectionRect){var e=this.selectionRect.node.getBoundingClientRect();e.width>0&&e.height>0&&this.slDraggableRect.selectize({points:"l, r",pointSize:8,pointType:"rect"}).resize({constraint:{minX:0,minY:0,maxX:t.globals.gridWidth,maxY:t.globals.gridHeight}}).on("resizing",this.selectionDragging.bind(this,"resizing"));}}},{key:"preselectedSelection",value:function(){var t=this.w,e=this.xyRatios;if(!t.globals.zoomEnabled)if(void 0!==t.globals.selection&&null!==t.globals.selection)this.drawSelectionRect(t.globals.selection);else if(void 0!==t.config.chart.selection.xaxis.min&&void 0!==t.config.chart.selection.xaxis.max){var i=(t.config.chart.selection.xaxis.min-t.globals.minX)/e.xRatio,a={x:i,y:0,width:t.globals.gridWidth-(t.globals.maxX-t.config.chart.selection.xaxis.max)/e.xRatio-i,height:t.globals.gridHeight,translateX:0,translateY:0,selectionEnabled:!0};this.drawSelectionRect(a),this.makeSelectionRectDraggable(),"function"==typeof t.config.chart.events.selection&&t.config.chart.events.selection(this.ctx,{xaxis:{min:t.config.chart.selection.xaxis.min,max:t.config.chart.selection.xaxis.max},yaxis:{}});}}},{key:"drawSelectionRect",value:function(t){var e=t.x,i=t.y,a=t.width,s=t.height,r=t.translateX,n=void 0===r?0:r,o=t.translateY,l=void 0===o?0:o,h=this.w,c=this.zoomRect,d=this.selectionRect;if(this.dragged||null!==h.globals.selection){var g={transform:"translate("+n+", "+l+")"};h.globals.zoomEnabled&&this.dragged&&(a<0&&(a=1),c.attr({x:e,y:i,width:a,height:s,fill:h.config.chart.zoom.zoomedArea.fill.color,"fill-opacity":h.config.chart.zoom.zoomedArea.fill.opacity,stroke:h.config.chart.zoom.zoomedArea.stroke.color,"stroke-width":h.config.chart.zoom.zoomedArea.stroke.width,"stroke-opacity":h.config.chart.zoom.zoomedArea.stroke.opacity}),b.setAttrs(c.node,g)),h.globals.selectionEnabled&&(d.attr({x:e,y:i,width:a>0?a:0,height:s>0?s:0,fill:h.config.chart.selection.fill.color,"fill-opacity":h.config.chart.selection.fill.opacity,stroke:h.config.chart.selection.stroke.color,"stroke-width":h.config.chart.selection.stroke.width,"stroke-dasharray":h.config.chart.selection.stroke.dashArray,"stroke-opacity":h.config.chart.selection.stroke.opacity}),b.setAttrs(d.node,g));}}},{key:"hideSelectionRect",value:function(t){t&&t.attr({x:0,y:0,width:0,height:0});}},{key:"selectionDrawing",value:function(t){var e=t.context,i=t.zoomtype,a=this.w,s=e,r=this.gridRect.getBoundingClientRect(),n=s.startX-1,o=s.startY,l=!1,h=!1,c=s.clientX-r.left-n,d=s.clientY-r.top-o,g={};return Math.abs(c+n)>a.globals.gridWidth?c=a.globals.gridWidth-n:s.clientX-r.left<0&&(c=n),n>s.clientX-r.left&&(l=!0,c=Math.abs(c)),o>s.clientY-r.top&&(h=!0,d=Math.abs(d)),g="x"===i?{x:l?n-c:n,y:0,width:c,height:a.globals.gridHeight}:"y"===i?{x:0,y:h?o-d:o,width:a.globals.gridWidth,height:d}:{x:l?n-c:n,y:h?o-d:o,width:c,height:d},s.drawSelectionRect(g),s.selectionDragging("resizing"),g}},{key:"selectionDragging",value:function(t,e){var i=this,a=this.w,s=this.xyRatios,r=this.selectionRect,n=0;"resizing"===t&&(n=30);var o=function(t){return parseFloat(r.node.getAttribute(t))},l={x:o("x"),y:o("y"),width:o("width"),height:o("height")};a.globals.selection=l,"function"==typeof a.config.chart.events.selection&&a.globals.selectionEnabled&&(clearTimeout(this.w.globals.selectionResizeTimer),this.w.globals.selectionResizeTimer=window.setTimeout((function(){var t=i.gridRect.getBoundingClientRect(),e=r.node.getBoundingClientRect(),n={xaxis:{min:a.globals.xAxisScale.niceMin+(e.left-t.left)*s.xRatio,max:a.globals.xAxisScale.niceMin+(e.right-t.left)*s.xRatio},yaxis:{min:a.globals.yAxisScale[0].niceMin+(t.bottom-e.bottom)*s.yRatio[0],max:a.globals.yAxisScale[0].niceMax-(e.top-t.top)*s.yRatio[0]}};a.config.chart.events.selection(i.ctx,n),a.config.chart.brush.enabled&&void 0!==a.config.chart.events.brushScrolled&&a.config.chart.events.brushScrolled(i.ctx,n);}),n));}},{key:"selectionDrawn",value:function(t){var e=t.context,i=t.zoomtype,a=this.w,s=e,r=this.xyRatios,n=this.ctx.toolbar;if(s.startX>s.endX){var o=s.startX;s.startX=s.endX,s.endX=o;}if(s.startY>s.endY){var l=s.startY;s.startY=s.endY,s.endY=l;}var h=a.globals.xAxisScale.niceMin+s.startX*r.xRatio,c=a.globals.xAxisScale.niceMin+s.endX*r.xRatio,d=[],g=[];if(a.config.yaxis.forEach((function(t,e){d.push(a.globals.yAxisScale[e].niceMax-r.yRatio[e]*s.startY),g.push(a.globals.yAxisScale[e].niceMax-r.yRatio[e]*s.endY);})),s.dragged&&(s.dragX>10||s.dragY>10)&&h!==c)if(a.globals.zoomEnabled){var u=f.clone(a.globals.initialConfig.yaxis),p=f.clone(a.globals.initialConfig.xaxis);if(a.globals.zoomed=!0,a.config.xaxis.convertedCatToNumeric&&(h=Math.floor(h),c=Math.floor(c),h<1&&(h=1,c=a.globals.dataPoints),c-h<2&&(c=h+1)),"xy"!==i&&"x"!==i||(p={min:h,max:c}),"xy"!==i&&"y"!==i||u.forEach((function(t,e){u[e].min=g[e],u[e].max=d[e];})),a.config.chart.zoom.autoScaleYaxis){var x=new j(s.ctx);u=x.autoScaleY(s.ctx,u,{xaxis:p});}if(n){var b=n.getBeforeZoomRange(p,u);b&&(p=b.xaxis?b.xaxis:p,u=b.yaxis?b.yaxis:u);}var m={xaxis:p};a.config.chart.group||(m.yaxis=u),s.ctx.updateHelpers._updateOptions(m,!1,s.w.config.chart.animations.dynamicAnimation.enabled),"function"==typeof a.config.chart.events.zoomed&&n.zoomCallback(p,u);}else if(a.globals.selectionEnabled){var v,y=null;v={min:h,max:c},"xy"!==i&&"y"!==i||(y=f.clone(a.config.yaxis)).forEach((function(t,e){y[e].min=g[e],y[e].max=d[e];})),a.globals.selection=s.selection,"function"==typeof a.config.chart.events.selection&&a.config.chart.events.selection(s.ctx,{xaxis:v,yaxis:y});}}},{key:"panDragging",value:function(t){var e=t.context,i=this.w,a=e;if(void 0!==i.globals.lastClientPosition.x){var s=i.globals.lastClientPosition.x-a.clientX,r=i.globals.lastClientPosition.y-a.clientY;Math.abs(s)>Math.abs(r)&&s>0?this.moveDirection="left":Math.abs(s)>Math.abs(r)&&s<0?this.moveDirection="right":Math.abs(r)>Math.abs(s)&&r>0?this.moveDirection="up":Math.abs(r)>Math.abs(s)&&r<0&&(this.moveDirection="down");}i.globals.lastClientPosition={x:a.clientX,y:a.clientY};var n=i.globals.minX,o=i.globals.maxX;i.config.xaxis.convertedCatToNumeric||a.panScrolled(n,o);}},{key:"delayedPanScrolled",value:function(){var t=this.w,e=t.globals.minX,i=t.globals.maxX,a=(t.globals.maxX-t.globals.minX)/2;"left"===this.moveDirection?(e=t.globals.minX+a,i=t.globals.maxX+a):"right"===this.moveDirection&&(e=t.globals.minX-a,i=t.globals.maxX-a),e=Math.floor(e),i=Math.floor(i),this.updateScrolledChart({xaxis:{min:e,max:i}},e,i);}},{key:"panScrolled",value:function(t,e){var i=this.w,a=this.xyRatios,s=f.clone(i.globals.initialConfig.yaxis);"left"===this.moveDirection?(t=i.globals.minX+i.globals.gridWidth/15*a.xRatio,e=i.globals.maxX+i.globals.gridWidth/15*a.xRatio):"right"===this.moveDirection&&(t=i.globals.minX-i.globals.gridWidth/15*a.xRatio,e=i.globals.maxX-i.globals.gridWidth/15*a.xRatio),(t<i.globals.initialMinX||e>i.globals.initialMaxX)&&(t=i.globals.minX,e=i.globals.maxX);var r={min:t,max:e};i.config.chart.zoom.autoScaleYaxis&&(s=new j(this.ctx).autoScaleY(this.ctx,s,{xaxis:r}));var n={xaxis:{min:t,max:e}};i.config.chart.group||(n.yaxis=s),this.updateScrolledChart(n,t,e);}},{key:"updateScrolledChart",value:function(t,e,i){var a=this.w;this.ctx.updateHelpers._updateOptions(t,!1,!1),"function"==typeof a.config.chart.events.scrolled&&a.config.chart.events.scrolled(this.ctx,{xaxis:{min:e,max:i}});}}]),s}(ht),dt=function(){function t(i){e(this,t),this.w=i.w,this.ttCtx=i,this.ctx=i.ctx;}return a(t,[{key:"getNearestValues",value:function(t){var e=t.hoverArea,i=t.elGrid,a=t.clientX,s=t.clientY,r=this.w,n=r.globals.gridWidth,o=n/(r.globals.dataPoints-1),l=i.getBoundingClientRect(),h=this.hasBars();!r.globals.comboCharts&&!h||r.config.xaxis.convertedCatToNumeric||(o=n/r.globals.dataPoints);var c=a-l.left-r.globals.barPadForNumericAxis,d=s-l.top;c<0||d<0||c>r.globals.gridWidth||d>r.globals.gridHeight?(e.classList.remove("hovering-zoom"),e.classList.remove("hovering-pan")):r.globals.zoomEnabled?(e.classList.remove("hovering-pan"),e.classList.add("hovering-zoom")):r.globals.panEnabled&&(e.classList.remove("hovering-zoom"),e.classList.add("hovering-pan"));var g=Math.round(c/o);h&&!r.config.xaxis.convertedCatToNumeric&&(g=Math.ceil(c/o),g-=1);for(var u,p=null,x=null,b=[],m=0;m<r.globals.seriesXvalues.length;m++)b.push([r.globals.seriesXvalues[m][0]-1e-6].concat(r.globals.seriesXvalues[m]));return b=b.map((function(t){return t.filter((function(t){return t}))})),u=r.globals.seriesYvalues.map((function(t){return t.filter((function(t){return f.isNumber(t)}))})),r.globals.isXNumeric&&(p=(x=this.closestInMultiArray(c,d,b,u)).index,g=x.j,null!==p&&(b=r.globals.seriesXvalues[p],g=(x=this.closestInArray(c,b)).index)),r.globals.capturedSeriesIndex=null===p?-1:p,(!g||g<1)&&(g=0),r.globals.capturedDataPointIndex=g,{capturedSeries:p,j:g,hoverX:c,hoverY:d}}},{key:"closestInMultiArray",value:function(t,e,i,a){var s=this.w,r=0,n=null,o=-1;s.globals.series.length>1?r=this.getFirstActiveXArray(i):n=0;var l=a[r][0],h=i[r][0],c=Math.abs(t-h),d=Math.abs(e-l),g=d+c;return a.map((function(s,r){s.map((function(s,l){var h=Math.abs(e-a[r][l]),u=Math.abs(t-i[r][l]),f=u+h;f<g&&(g=f,c=u,d=h,n=r,o=l);}));})),{index:n,j:o}}},{key:"getFirstActiveXArray",value:function(t){for(var e=0,i=t.map((function(t,e){return t.length>0?e:-1})),a=0;a<i.length;a++)if(-1!==i[a]){e=i[a];break}return e}},{key:"closestInArray",value:function(t,e){for(var i=e[0],a=null,s=Math.abs(t-i),r=0;r<e.length;r++){var n=Math.abs(t-e[r]);n<s&&(s=n,a=r);}return {index:a}}},{key:"isXoverlap",value:function(t){var e=[],i=this.w.globals.seriesX.filter((function(t){return void 0!==t[0]}));if(i.length>0)for(var a=0;a<i.length-1;a++)void 0!==i[a][t]&&void 0!==i[a+1][t]&&i[a][t]!==i[a+1][t]&&e.push("unEqual");return 0===e.length}},{key:"isInitialSeriesSameLen",value:function(){for(var t=!0,e=this.w.globals.initialSeries,i=0;i<e.length-1;i++)if(e[i].data.length!==e[i+1].data.length){t=!1;break}return t}},{key:"getBarsHeight",value:function(t){return g(t).reduce((function(t,e){return t+e.getBBox().height}),0)}},{key:"getElMarkers",value:function(){return this.w.globals.dom.baseEl.querySelectorAll(" .apexcharts-series-markers")}},{key:"getAllMarkers",value:function(){var t=this.w.globals.dom.baseEl.querySelectorAll(".apexcharts-series-markers-wrap");(t=g(t)).sort((function(t,e){return Number(e.getAttribute("data:realIndex"))<Number(t.getAttribute("data:realIndex"))?0:-1}));var e=[];return t.forEach((function(t){e.push(t.querySelector(".apexcharts-marker"));})),e}},{key:"hasMarkers",value:function(){return this.getElMarkers().length>0}},{key:"getElBars",value:function(){return this.w.globals.dom.baseEl.querySelectorAll(".apexcharts-bar-series,  .apexcharts-candlestick-series, .apexcharts-rangebar-series")}},{key:"hasBars",value:function(){return this.getElBars().length>0}},{key:"getHoverMarkerSize",value:function(t){var e=this.w,i=e.config.markers.hover.size;return void 0===i&&(i=e.globals.markers.size[t]+e.config.markers.hover.sizeOffset),i}},{key:"toggleAllTooltipSeriesGroups",value:function(t){var e=this.w,i=this.ttCtx;0===i.allTooltipSeriesGroups.length&&(i.allTooltipSeriesGroups=e.globals.dom.baseEl.querySelectorAll(".apexcharts-tooltip-series-group"));for(var a=i.allTooltipSeriesGroups,s=0;s<a.length;s++)"enable"===t?(a[s].classList.add("apexcharts-active"),a[s].style.display=e.config.tooltip.items.display):(a[s].classList.remove("apexcharts-active"),a[s].style.display="none");}}]),t}(),gt=function(){function t(i){e(this,t),this.w=i.w,this.ctx=i.ctx,this.ttCtx=i,this.tooltipUtil=new dt(i);}return a(t,[{key:"drawSeriesTexts",value:function(t){var e=t.shared,i=void 0===e||e,a=t.ttItems,s=t.i,r=void 0===s?0:s,n=t.j,o=void 0===n?null:n,l=t.y1,h=t.y2,c=t.e,d=this.w;void 0!==d.config.tooltip.custom?this.handleCustomTooltip({i:r,j:o,y1:l,y2:h,w:d}):this.toggleActiveInactiveSeries(i);var g=this.getValuesToPrint({i:r,j:o});this.printLabels({i:r,j:o,values:g,ttItems:a,shared:i,e:c});var u=this.ttCtx.getElTooltip();this.ttCtx.tooltipRect.ttWidth=u.getBoundingClientRect().width,this.ttCtx.tooltipRect.ttHeight=u.getBoundingClientRect().height;}},{key:"printLabels",value:function(t){var e,i=this,a=t.i,s=t.j,r=t.values,n=t.ttItems,o=t.shared,l=t.e,h=this.w,c=r.xVal,d=r.zVal,g=r.xAxisTTVal,u="",f=h.globals.colors[a];null!==s&&h.config.plotOptions.bar.distributed&&(f=h.globals.colors[s]);for(var p=function(t,r){var p=i.getFormatters(a);u=i.getSeriesName({fn:p.yLbTitleFormatter,index:a,seriesIndex:a,j:s}),"treemap"===h.config.chart.type&&(u=p.yLbTitleFormatter(String(h.config.series[a].data[s].x),{series:h.globals.series,seriesIndex:a,dataPointIndex:s,w:h}));var x=h.config.tooltip.inverseOrder?r:t;if(h.globals.axisCharts){var b=function(t){return p.yLbFormatter(h.globals.series[t][s],{series:h.globals.series,seriesIndex:t,dataPointIndex:s,w:h})};o?(p=i.getFormatters(x),u=i.getSeriesName({fn:p.yLbTitleFormatter,index:x,seriesIndex:a,j:s}),f=h.globals.colors[x],e=b(x)):(l&&l.target&&l.target.getAttribute("fill")&&(f=l.target.getAttribute("fill")),e=b(a));}null===s&&(e=p.yLbFormatter(h.globals.series[a],h)),i.DOMHandling({i:a,t:x,j:s,ttItems:n,values:{val:e,xVal:c,xAxisTTVal:g,zVal:d},seriesName:u,shared:o,pColor:f});},x=0,b=h.globals.series.length-1;x<h.globals.series.length;x++,b--)p(x,b);}},{key:"getFormatters",value:function(t){var e,i=this.w,a=i.globals.yLabelFormatters[t];return void 0!==i.globals.ttVal?Array.isArray(i.globals.ttVal)?(a=i.globals.ttVal[t]&&i.globals.ttVal[t].formatter,e=i.globals.ttVal[t]&&i.globals.ttVal[t].title&&i.globals.ttVal[t].title.formatter):(a=i.globals.ttVal.formatter,"function"==typeof i.globals.ttVal.title.formatter&&(e=i.globals.ttVal.title.formatter)):e=i.config.tooltip.y.title.formatter,"function"!=typeof a&&(a=i.globals.yLabelFormatters[0]?i.globals.yLabelFormatters[0]:function(t){return t}),"function"!=typeof e&&(e=function(t){return t}),{yLbFormatter:a,yLbTitleFormatter:e}}},{key:"getSeriesName",value:function(t){var e=t.fn,i=t.index,a=t.seriesIndex,s=t.j,r=this.w;return e(String(r.globals.seriesNames[i]),{series:r.globals.series,seriesIndex:a,dataPointIndex:s,w:r})}},{key:"DOMHandling",value:function(t){t.i;var e=t.t,i=(t.j,t.ttItems),a=t.values,s=t.seriesName,r=t.shared,n=t.pColor,o=this.w,l=this.ttCtx,h=a.val,c=a.xVal,d=a.xAxisTTVal,g=a.zVal,u=null;u=i[e].children,o.config.tooltip.fillSeriesColor&&(i[e].style.backgroundColor=n,u[0].style.display="none"),l.showTooltipTitle&&(null===l.tooltipTitle&&(l.tooltipTitle=o.globals.dom.baseEl.querySelector(".apexcharts-tooltip-title")),l.tooltipTitle.innerHTML=c),l.blxaxisTooltip&&(l.xaxisTooltipText.innerHTML=""!==d?d:c);var f=i[e].querySelector(".apexcharts-tooltip-text-label");f&&(f.innerHTML=s||"");var p=i[e].querySelector(".apexcharts-tooltip-text-value");(p&&(p.innerHTML=void 0!==h?h:""),u[0]&&u[0].classList.contains("apexcharts-tooltip-marker")&&(o.config.tooltip.marker.fillColors&&Array.isArray(o.config.tooltip.marker.fillColors)&&(n=o.config.tooltip.marker.fillColors[e]),u[0].style.backgroundColor=n),o.config.tooltip.marker.show||(u[0].style.display="none"),null!==g)&&(i[e].querySelector(".apexcharts-tooltip-text-z-label").innerHTML=o.config.tooltip.z.title,i[e].querySelector(".apexcharts-tooltip-text-z-value").innerHTML=void 0!==g?g:"");r&&u[0]&&(null==h||o.globals.collapsedSeriesIndices.indexOf(e)>-1?u[0].parentNode.style.display="none":u[0].parentNode.style.display=o.config.tooltip.items.display);}},{key:"toggleActiveInactiveSeries",value:function(t){var e=this.w;if(t)this.tooltipUtil.toggleAllTooltipSeriesGroups("enable");else {this.tooltipUtil.toggleAllTooltipSeriesGroups("disable");var i=e.globals.dom.baseEl.querySelector(".apexcharts-tooltip-series-group");i&&(i.classList.add("apexcharts-active"),i.style.display=e.config.tooltip.items.display);}}},{key:"getValuesToPrint",value:function(t){var e=t.i,i=t.j,a=this.w,s=this.ctx.series.filteredSeriesX(),r="",n="",o=null,l=null,h={series:a.globals.series,seriesIndex:e,dataPointIndex:i,w:a},c=a.globals.ttZFormatter;null===i?l=a.globals.series[e]:a.globals.isXNumeric&&"treemap"!==a.config.chart.type?(r=s[e][i],0===s[e].length&&(r=s[this.tooltipUtil.getFirstActiveXArray(s)][i])):r=void 0!==a.globals.labels[i]?a.globals.labels[i]:"";var d=r;a.globals.isXNumeric&&"datetime"===a.config.xaxis.type?r=new W(this.ctx).xLabelFormat(a.globals.ttKeyFormatter,d,d,{i:void 0,dateFormatter:new y(this.ctx).formatDate,w:this.w}):a.globals.isBarHorizontal||(r=a.globals.xLabelFormatter(d,h));return void 0!==a.config.tooltip.x.formatter&&(r=a.globals.ttKeyFormatter(d,h)),a.globals.seriesZ.length>0&&a.globals.seriesZ[0].length>0&&(o=c(a.globals.seriesZ[e][i],a)),n="function"==typeof a.config.xaxis.tooltip.formatter?a.globals.xaxisTooltipFormatter(d,h):r,{val:Array.isArray(l)?l.join(" "):l,xVal:Array.isArray(r)?r.join(" "):r,xAxisTTVal:Array.isArray(n)?n.join(" "):n,zVal:o}}},{key:"handleCustomTooltip",value:function(t){var e=t.i,i=t.j,a=t.y1,s=t.y2,r=t.w,n=this.ttCtx.getElTooltip(),o=r.config.tooltip.custom;Array.isArray(o)&&o[e]&&(o=o[e]),n.innerHTML=o({ctx:this.ctx,series:r.globals.series,seriesIndex:e,dataPointIndex:i,y1:a,y2:s,w:r});}}]),t}(),ut=function(){function t(i){e(this,t),this.ttCtx=i,this.ctx=i.ctx,this.w=i.w;}return a(t,[{key:"moveXCrosshairs",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:null,i=this.ttCtx,a=this.w,s=i.getElXCrosshairs(),r=t-i.xcrosshairsWidth/2,n=a.globals.labels.slice().length;if(null!==e&&(r=a.globals.gridWidth/n*e),null!==s&&(s.setAttribute("x",r),s.setAttribute("x1",r),s.setAttribute("x2",r),s.setAttribute("y2",a.globals.gridHeight),s.classList.add("apexcharts-active")),r<0&&(r=0),r>a.globals.gridWidth&&(r=a.globals.gridWidth),i.blxaxisTooltip){var o=r;"tickWidth"!==a.config.xaxis.crosshairs.width&&"barWidth"!==a.config.xaxis.crosshairs.width||(o=r+i.xcrosshairsWidth/2),this.moveXAxisTooltip(o);}}},{key:"moveYCrosshairs",value:function(t){var e=this.ttCtx;null!==e.ycrosshairs&&b.setAttrs(e.ycrosshairs,{y1:t,y2:t}),null!==e.ycrosshairsHidden&&b.setAttrs(e.ycrosshairsHidden,{y1:t,y2:t});}},{key:"moveXAxisTooltip",value:function(t){var e=this.w,i=this.ttCtx;if(null!==i.xaxisTooltip){i.xaxisTooltip.classList.add("apexcharts-active");var a=i.xaxisOffY+e.config.xaxis.tooltip.offsetY+e.globals.translateY+1+e.config.xaxis.offsetY;if(t-=i.xaxisTooltip.getBoundingClientRect().width/2,!isNaN(t)){t+=e.globals.translateX;var s;s=new b(this.ctx).getTextRects(i.xaxisTooltipText.innerHTML),i.xaxisTooltipText.style.minWidth=s.width+"px",i.xaxisTooltip.style.left=t+"px",i.xaxisTooltip.style.top=a+"px";}}}},{key:"moveYAxisTooltip",value:function(t){var e=this.w,i=this.ttCtx;null===i.yaxisTTEls&&(i.yaxisTTEls=e.globals.dom.baseEl.querySelectorAll(".apexcharts-yaxistooltip"));var a=parseInt(i.ycrosshairsHidden.getAttribute("y1"),10),s=e.globals.translateY+a,r=i.yaxisTTEls[t].getBoundingClientRect().height,n=e.globals.translateYAxisX[t]-2;e.config.yaxis[t].opposite&&(n-=26),s-=r/2,-1===e.globals.ignoreYAxisIndexes.indexOf(t)?(i.yaxisTTEls[t].classList.add("apexcharts-active"),i.yaxisTTEls[t].style.top=s+"px",i.yaxisTTEls[t].style.left=n+e.config.yaxis[t].tooltip.offsetX+"px"):i.yaxisTTEls[t].classList.remove("apexcharts-active");}},{key:"moveTooltip",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:null,a=this.w,s=this.ttCtx,r=s.getElTooltip(),n=s.tooltipRect,o=null!==i?parseFloat(i):1,l=parseFloat(t)+o+5,h=parseFloat(e)+o/2;if(l>a.globals.gridWidth/2&&(l=l-n.ttWidth-o-15),l>a.globals.gridWidth-n.ttWidth-10&&(l=a.globals.gridWidth-n.ttWidth),l<-20&&(l=-20),a.config.tooltip.followCursor){var c=s.getElGrid(),d=c.getBoundingClientRect();h=s.e.clientY+a.globals.translateY-d.top-n.ttHeight/2;}if(!a.config.tooltip.followCursor){var g=this.positionChecks(n,l,h);l=g.x,h=g.y;}isNaN(l)||(l+=a.globals.translateX,r.style.left=l+"px",r.style.top=h+"px");}},{key:"positionChecks",value:function(t,e,i){var a=this.w;return t.ttHeight/2+i>a.globals.gridHeight&&(i=a.globals.gridHeight-t.ttHeight+a.globals.translateY),i<0&&(i=0),{x:e,y:i}}},{key:"moveMarkers",value:function(t,e){var i=this.w,a=this.ttCtx;if(i.globals.markers.size[t]>0)for(var s=i.globals.dom.baseEl.querySelectorAll(" .apexcharts-series[data\\:realIndex='".concat(t,"'] .apexcharts-marker")),r=0;r<s.length;r++)parseInt(s[r].getAttribute("rel"),10)===e&&(a.marker.resetPointsSize(),a.marker.enlargeCurrentPoint(e,s[r]));else a.marker.resetPointsSize(),this.moveDynamicPointOnHover(e,t);}},{key:"moveDynamicPointOnHover",value:function(t,e){var i,a,s=this.w,r=this.ttCtx,n=s.globals.pointsArray,o=r.tooltipUtil.getHoverMarkerSize(e),l=s.config.series[e].type;if(!l||"column"!==l&&"candlestick"!==l){i=n[e][t][0],a=n[e][t][1]?n[e][t][1]:0;var h=s.globals.dom.baseEl.querySelector(".apexcharts-series[data\\:realIndex='".concat(e,"'] .apexcharts-series-markers circle"));h&&a<s.globals.gridHeight&&a>0&&(h.setAttribute("r",o),h.setAttribute("cx",i),h.setAttribute("cy",a)),this.moveXCrosshairs(i),r.fixedTooltip||this.moveTooltip(i,a,o);}}},{key:"moveDynamicPointsOnHover",value:function(t){var e,i=this.ttCtx,a=i.w,s=0,r=0,n=a.globals.pointsArray;e=new E(this.ctx).getActiveConfigSeriesIndex(!0);var o=i.tooltipUtil.getHoverMarkerSize(e);n[e]&&(s=n[e][t][0],r=n[e][t][1]);var l=i.tooltipUtil.getAllMarkers();if(null!==l)for(var h=0;h<a.globals.series.length;h++){var c=n[h];if(a.globals.comboCharts&&void 0===c&&l.splice(h,0,null),c&&c.length){var d=n[h][t][1];l[h].setAttribute("cx",s),null!==d&&!isNaN(d)&&d<a.globals.gridHeight&&d>0?(l[h]&&l[h].setAttribute("r",o),l[h]&&l[h].setAttribute("cy",d)):l[h]&&l[h].setAttribute("r",0);}}if(this.moveXCrosshairs(s),!i.fixedTooltip){var g=r||a.globals.gridHeight;this.moveTooltip(s,g,o);}}},{key:"moveStickyTooltipOverBars",value:function(t){var e,i=this.w,a=this.ttCtx,s=i.globals.columnSeries?i.globals.columnSeries.length:i.globals.series.length,r=s>=2&&s%2==0?Math.floor(s/2):Math.floor(s/2)+1,n=i.globals.dom.baseEl.querySelector(".apexcharts-bar-series .apexcharts-series[rel='".concat(r,"'] path[j='").concat(t,"'], .apexcharts-candlestick-series .apexcharts-series[rel='").concat(r,"'] path[j='").concat(t,"'], .apexcharts-rangebar-series .apexcharts-series[rel='").concat(r,"'] path[j='").concat(t,"']")),o=n?parseFloat(n.getAttribute("cx")):0,l=n?parseFloat(n.getAttribute("barWidth")):0;i.globals.isXNumeric?o-=s%2!=0?l/2:0:(o=a.xAxisTicksPositions[t-1]+a.dataPointsDividedWidth/2,isNaN(o)&&(o=a.xAxisTicksPositions[t]-a.dataPointsDividedWidth/2));var h=a.getElGrid().getBoundingClientRect();if(e=a.e.clientY-h.top-a.tooltipRect.ttHeight/2,this.moveXCrosshairs(o),!a.fixedTooltip){var c=e||i.globals.gridHeight;this.moveTooltip(o,c);}}}]),t}(),ft=function(){function t(i){e(this,t),this.w=i.w,this.ttCtx=i,this.ctx=i.ctx,this.tooltipPosition=new ut(i);}return a(t,[{key:"drawDynamicPoints",value:function(){var t=this.w,e=new b(this.ctx),i=new T(this.ctx),a=t.globals.dom.baseEl.querySelectorAll(".apexcharts-series");a=g(a),t.config.chart.stacked&&a.sort((function(t,e){return parseFloat(t.getAttribute("data:realIndex"))-parseFloat(e.getAttribute("data:realIndex"))}));for(var s=0;s<a.length;s++){var r=a[s].querySelector(".apexcharts-series-markers-wrap");if(null!==r){var n=void 0,o="apexcharts-marker w".concat((Math.random()+1).toString(36).substring(4));"line"!==t.config.chart.type&&"area"!==t.config.chart.type||t.globals.comboCharts||t.config.tooltip.intersect||(o+=" no-pointer-events");var l=i.getMarkerConfig(o,s);(n=e.drawMarker(0,0,l)).node.setAttribute("default-marker-size",0);var h=document.createElementNS(t.globals.SVGNS,"g");h.classList.add("apexcharts-series-markers"),h.appendChild(n.node),r.appendChild(h);}}}},{key:"enlargeCurrentPoint",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:null,a=arguments.length>3&&void 0!==arguments[3]?arguments[3]:null,s=this.w;"bubble"!==s.config.chart.type&&this.newPointSize(t,e);var r=e.getAttribute("cx"),n=e.getAttribute("cy");if(null!==i&&null!==a&&(r=i,n=a),this.tooltipPosition.moveXCrosshairs(r),!this.fixedTooltip){if("radar"===s.config.chart.type){var o=this.ttCtx.getElGrid(),l=o.getBoundingClientRect();r=this.ttCtx.e.clientX-l.left;}this.tooltipPosition.moveTooltip(r,n,s.config.markers.hover.size);}}},{key:"enlargePoints",value:function(t){for(var e=this.w,i=this.ttCtx,a=t,s=e.globals.dom.baseEl.querySelectorAll(".apexcharts-series:not(.apexcharts-series-collapsed) .apexcharts-marker"),r=e.config.markers.hover.size,n=0;n<s.length;n++){var o=s[n].getAttribute("rel"),l=s[n].getAttribute("index");if(void 0===r&&(r=e.globals.markers.size[l]+e.config.markers.hover.sizeOffset),a===parseInt(o,10)){this.newPointSize(a,s[n]);var h=s[n].getAttribute("cx"),c=s[n].getAttribute("cy");this.tooltipPosition.moveXCrosshairs(h),i.fixedTooltip||this.tooltipPosition.moveTooltip(h,c,r);}else this.oldPointSize(s[n]);}}},{key:"newPointSize",value:function(t,e){var i=this.w,a=i.config.markers.hover.size,s=0===t?e.parentNode.firstChild:e.parentNode.lastChild;if("0"!==s.getAttribute("default-marker-size")){var r=parseInt(s.getAttribute("index"),10);void 0===a&&(a=i.globals.markers.size[r]+i.config.markers.hover.sizeOffset),s.setAttribute("r",a);}}},{key:"oldPointSize",value:function(t){var e=parseFloat(t.getAttribute("default-marker-size"));t.setAttribute("r",e);}},{key:"resetPointsSize",value:function(){for(var t=this.w.globals.dom.baseEl.querySelectorAll(".apexcharts-series:not(.apexcharts-series-collapsed) .apexcharts-marker"),e=0;e<t.length;e++){var i=parseFloat(t[e].getAttribute("default-marker-size"));f.isNumber(i)?t[e].setAttribute("r",i):t[e].setAttribute("r",0);}}}]),t}(),pt=function(){function t(i){e(this,t),this.w=i.w,this.ttCtx=i;}return a(t,[{key:"getAttr",value:function(t,e){return parseFloat(t.target.getAttribute(e))}},{key:"handleHeatTreeTooltip",value:function(t){var e=t.e,i=t.opt,a=t.x,s=t.y,r=t.type,n=this.ttCtx,o=this.w;if(e.target.classList.contains("apexcharts-".concat(r,"-rect"))){var l=this.getAttr(e,"i"),h=this.getAttr(e,"j"),c=this.getAttr(e,"cx"),d=this.getAttr(e,"cy"),g=this.getAttr(e,"width"),u=this.getAttr(e,"height");if(n.tooltipLabels.drawSeriesTexts({ttItems:i.ttItems,i:l,j:h,shared:!1,e:e}),o.globals.capturedSeriesIndex=l,o.globals.capturedDataPointIndex=h,a=c+n.tooltipRect.ttWidth/2+g,s=d+n.tooltipRect.ttHeight/2-u/2,n.tooltipPosition.moveXCrosshairs(c+g/2),a>o.globals.gridWidth/2&&(a=c-n.tooltipRect.ttWidth/2+g),n.w.config.tooltip.followCursor){var f=n.getElGrid().getBoundingClientRect();(a=n.e.clientX-f.left+10)>o.globals.gridWidth/2&&(a=a-n.tooltipRect.ttWidth-10),s=n.e.clientY-f.top+o.globals.translateY/2-10;}}return {x:a,y:s}}},{key:"handleMarkerTooltip",value:function(t){var e,i,a=t.e,s=t.opt,r=t.x,n=t.y,o=this.w,l=this.ttCtx;if(a.target.classList.contains("apexcharts-marker")){var h=parseInt(s.paths.getAttribute("cx"),10),c=parseInt(s.paths.getAttribute("cy"),10),d=parseFloat(s.paths.getAttribute("val"));if(i=parseInt(s.paths.getAttribute("rel"),10),e=parseInt(s.paths.parentNode.parentNode.parentNode.getAttribute("rel"),10)-1,l.intersect){var g=f.findAncestor(s.paths,"apexcharts-series");g&&(e=parseInt(g.getAttribute("data:realIndex"),10));}if(l.tooltipLabels.drawSeriesTexts({ttItems:s.ttItems,i:e,j:i,shared:!l.showOnIntersect&&o.config.tooltip.shared,e:a}),"mouseup"===a.type&&l.markerClick(a,e,i),o.globals.capturedSeriesIndex=e,o.globals.capturedDataPointIndex=i,r=h,n=c+o.globals.translateY-1.4*l.tooltipRect.ttHeight,l.w.config.tooltip.followCursor){var u=l.getElGrid().getBoundingClientRect();n=l.e.clientY+o.globals.translateY-u.top;}d<0&&(n=c),l.marker.enlargeCurrentPoint(i,s.paths,r,n);}return {x:r,y:n}}},{key:"handleBarTooltip",value:function(t){var e,i,a=t.e,s=t.opt,r=this.w,n=this.ttCtx,o=n.getElTooltip(),l=0,h=0,c=0,d=this.getBarTooltipXY({e:a,opt:s});e=d.i;var g=d.barHeight,u=d.j;if(r.globals.capturedSeriesIndex=e,r.globals.capturedDataPointIndex=u,r.globals.isBarHorizontal&&n.tooltipUtil.hasBars()||!r.config.tooltip.shared?(h=d.x,c=d.y,i=Array.isArray(r.config.stroke.width)?r.config.stroke.width[e]:r.config.stroke.width,l=h):r.globals.comboCharts||r.config.tooltip.shared||(l/=2),isNaN(c)?c=r.globals.svgHeight-n.tooltipRect.ttHeight:c<0&&(c=0),h+n.tooltipRect.ttWidth>r.globals.gridWidth?h-=n.tooltipRect.ttWidth:h<0&&(h=0),n.w.config.tooltip.followCursor){var f=n.getElGrid().getBoundingClientRect();c=n.e.clientY-f.top;}if(null===n.tooltip&&(n.tooltip=r.globals.dom.baseEl.querySelector(".apexcharts-tooltip")),r.config.tooltip.shared||(r.globals.comboBarCount>0?n.tooltipPosition.moveXCrosshairs(l+i/2):n.tooltipPosition.moveXCrosshairs(l)),!n.fixedTooltip&&(!r.config.tooltip.shared||r.globals.isBarHorizontal&&n.tooltipUtil.hasBars())){var p=r.globals.isMultipleYAxis?r.config.yaxis[x]&&r.config.yaxis[x].reversed:r.config.yaxis[0].reversed;p&&(h-=n.tooltipRect.ttWidth)<0&&(h=0),o.style.left=h+r.globals.translateX+"px";var x=parseInt(s.paths.parentNode.getAttribute("data:realIndex"),10);!p||r.globals.isBarHorizontal&&n.tooltipUtil.hasBars()||(c=c+g-2*(r.globals.series[e][u]<0?g:0)),n.tooltipRect.ttHeight+c>r.globals.gridHeight?(c=r.globals.gridHeight-n.tooltipRect.ttHeight+r.globals.translateY,o.style.top=c+"px"):o.style.top=c+r.globals.translateY-n.tooltipRect.ttHeight/2+"px";}}},{key:"getBarTooltipXY",value:function(t){var e=t.e,i=t.opt,a=this.w,s=null,r=this.ttCtx,n=0,o=0,l=0,h=0,c=0,d=e.target.classList;if(d.contains("apexcharts-bar-area")||d.contains("apexcharts-candlestick-area")||d.contains("apexcharts-rangebar-area")){var g=e.target,u=g.getBoundingClientRect(),f=i.elGrid.getBoundingClientRect(),p=u.height;c=u.height;var x=u.width,b=parseInt(g.getAttribute("cx"),10),m=parseInt(g.getAttribute("cy"),10);h=parseFloat(g.getAttribute("barWidth"));var v="touchmove"===e.type?e.touches[0].clientX:e.clientX;s=parseInt(g.getAttribute("j"),10),n=parseInt(g.parentNode.getAttribute("rel"),10)-1;var y=g.getAttribute("data-range-y1"),w=g.getAttribute("data-range-y2");a.globals.comboCharts&&(n=parseInt(g.parentNode.getAttribute("data:realIndex"),10)),r.tooltipLabels.drawSeriesTexts({ttItems:i.ttItems,i:n,j:s,y1:y?parseInt(y,10):null,y2:w?parseInt(w,10):null,shared:!r.showOnIntersect&&a.config.tooltip.shared,e:e}),a.config.tooltip.followCursor?a.globals.isBarHorizontal?(o=v-f.left+15,l=m-r.dataPointsDividedHeight+p/2-r.tooltipRect.ttHeight/2):(o=a.globals.isXNumeric?b-x/2:b-r.dataPointsDividedWidth+x/2,l=e.clientY-f.top-r.tooltipRect.ttHeight/2-15):a.globals.isBarHorizontal?((o=b)<r.xyRatios.baseLineInvertedY&&(o=b-r.tooltipRect.ttWidth),l=m-r.dataPointsDividedHeight+p/2-r.tooltipRect.ttHeight/2):(o=a.globals.isXNumeric?b-x/2:b-r.dataPointsDividedWidth+x/2,l=m);}return {x:o,y:l,barHeight:c,barWidth:h,i:n,j:s}}}]),t}(),xt=function(){function t(i){e(this,t),this.w=i.w,this.ttCtx=i;}return a(t,[{key:"drawXaxisTooltip",value:function(){var t=this.w,e=this.ttCtx,i="bottom"===t.config.xaxis.position;e.xaxisOffY=i?t.globals.gridHeight+1:-t.globals.xAxisHeight-t.config.xaxis.axisTicks.height+3;var a=i?"apexcharts-xaxistooltip apexcharts-xaxistooltip-bottom":"apexcharts-xaxistooltip apexcharts-xaxistooltip-top",s=t.globals.dom.elWrap;e.blxaxisTooltip&&(null===t.globals.dom.baseEl.querySelector(".apexcharts-xaxistooltip")&&(e.xaxisTooltip=document.createElement("div"),e.xaxisTooltip.setAttribute("class",a+" apexcharts-theme-"+t.config.tooltip.theme),s.appendChild(e.xaxisTooltip),e.xaxisTooltipText=document.createElement("div"),e.xaxisTooltipText.classList.add("apexcharts-xaxistooltip-text"),e.xaxisTooltipText.style.fontFamily=t.config.xaxis.tooltip.style.fontFamily||t.config.chart.fontFamily,e.xaxisTooltipText.style.fontSize=t.config.xaxis.tooltip.style.fontSize,e.xaxisTooltip.appendChild(e.xaxisTooltipText)));}},{key:"drawYaxisTooltip",value:function(){for(var t=this.w,e=this.ttCtx,i=function(i){var a=t.config.yaxis[i].opposite||t.config.yaxis[i].crosshairs.opposite;e.yaxisOffX=a?t.globals.gridWidth+1:1;var s="apexcharts-yaxistooltip apexcharts-yaxistooltip-".concat(i,a?" apexcharts-yaxistooltip-right":" apexcharts-yaxistooltip-left");t.globals.yAxisSameScaleIndices.map((function(e,a){e.map((function(e,a){a===i&&(s+=t.config.yaxis[a].show?" ":" apexcharts-yaxistooltip-hidden");}));}));var r=t.globals.dom.elWrap;null===t.globals.dom.baseEl.querySelector(".apexcharts-yaxistooltip apexcharts-yaxistooltip-".concat(i))&&(e.yaxisTooltip=document.createElement("div"),e.yaxisTooltip.setAttribute("class",s+" apexcharts-theme-"+t.config.tooltip.theme),r.appendChild(e.yaxisTooltip),0===i&&(e.yaxisTooltipText=[]),e.yaxisTooltipText[i]=document.createElement("div"),e.yaxisTooltipText[i].classList.add("apexcharts-yaxistooltip-text"),e.yaxisTooltip.appendChild(e.yaxisTooltipText[i]));},a=0;a<t.config.yaxis.length;a++)i(a);}},{key:"setXCrosshairWidth",value:function(){var t=this.w,e=this.ttCtx,i=e.getElXCrosshairs();if(e.xcrosshairsWidth=parseInt(t.config.xaxis.crosshairs.width,10),t.globals.comboCharts){var a=t.globals.dom.baseEl.querySelector(".apexcharts-bar-area");if(null!==a&&"barWidth"===t.config.xaxis.crosshairs.width){var s=parseFloat(a.getAttribute("barWidth"));e.xcrosshairsWidth=s;}else if("tickWidth"===t.config.xaxis.crosshairs.width){var r=t.globals.labels.length;e.xcrosshairsWidth=t.globals.gridWidth/r;}}else if("tickWidth"===t.config.xaxis.crosshairs.width){var n=t.globals.labels.length;e.xcrosshairsWidth=t.globals.gridWidth/n;}else if("barWidth"===t.config.xaxis.crosshairs.width){var o=t.globals.dom.baseEl.querySelector(".apexcharts-bar-area");if(null!==o){var l=parseFloat(o.getAttribute("barWidth"));e.xcrosshairsWidth=l;}else e.xcrosshairsWidth=1;}t.globals.isBarHorizontal&&(e.xcrosshairsWidth=0),null!==i&&e.xcrosshairsWidth>0&&i.setAttribute("width",e.xcrosshairsWidth);}},{key:"handleYCrosshair",value:function(){var t=this.w,e=this.ttCtx;e.ycrosshairs=t.globals.dom.baseEl.querySelector(".apexcharts-ycrosshairs"),e.ycrosshairsHidden=t.globals.dom.baseEl.querySelector(".apexcharts-ycrosshairs-hidden");}},{key:"drawYaxisTooltipText",value:function(t,e,i){var a=this.ttCtx,s=this.w,r=s.globals.yLabelFormatters[t];if(a.yaxisTooltips[t]){var n=a.getElGrid().getBoundingClientRect(),o=(e-n.top)*i.yRatio[t],l=s.globals.maxYArr[t]-s.globals.minYArr[t],h=s.globals.minYArr[t]+(l-o);a.tooltipPosition.moveYCrosshairs(e-n.top),a.yaxisTooltipText[t].innerHTML=r(h),a.tooltipPosition.moveYAxisTooltip(t);}}}]),t}(),bt=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;var a=this.w;this.tConfig=a.config.tooltip,this.tooltipUtil=new dt(this),this.tooltipLabels=new gt(this),this.tooltipPosition=new ut(this),this.marker=new ft(this),this.intersect=new pt(this),this.axesTooltip=new xt(this),this.showOnIntersect=this.tConfig.intersect,this.showTooltipTitle=this.tConfig.x.show,this.fixedTooltip=this.tConfig.fixed.enabled,this.xaxisTooltip=null,this.yaxisTTEls=null,this.isBarShared=!a.globals.isBarHorizontal&&this.tConfig.shared;}return a(t,[{key:"getElTooltip",value:function(t){return t||(t=this),t.w.globals.dom.baseEl.querySelector(".apexcharts-tooltip")}},{key:"getElXCrosshairs",value:function(){return this.w.globals.dom.baseEl.querySelector(".apexcharts-xcrosshairs")}},{key:"getElGrid",value:function(){return this.w.globals.dom.baseEl.querySelector(".apexcharts-grid")}},{key:"drawTooltip",value:function(t){var e=this.w;this.xyRatios=t,this.blxaxisTooltip=e.config.xaxis.tooltip.enabled&&e.globals.axisCharts,this.yaxisTooltips=e.config.yaxis.map((function(t,i){return !!(t.show&&t.tooltip.enabled&&e.globals.axisCharts)})),this.allTooltipSeriesGroups=[],e.globals.axisCharts||(this.showTooltipTitle=!1);var i=document.createElement("div");if(i.classList.add("apexcharts-tooltip"),i.classList.add("apexcharts-theme-".concat(this.tConfig.theme)),e.globals.dom.elWrap.appendChild(i),e.globals.axisCharts){this.axesTooltip.drawXaxisTooltip(),this.axesTooltip.drawYaxisTooltip(),this.axesTooltip.setXCrosshairWidth(),this.axesTooltip.handleYCrosshair();var a=new G(this.ctx);this.xAxisTicksPositions=a.getXAxisTicksPositions();}if(!e.globals.comboCharts&&!this.tConfig.intersect&&"bar"!==e.config.chart.type&&"rangeBar"!==e.config.chart.type||this.tConfig.shared||(this.showOnIntersect=!0),0!==e.config.markers.size&&0!==e.globals.markers.largestSize||this.marker.drawDynamicPoints(this),e.globals.collapsedSeries.length!==e.globals.series.length){this.dataPointsDividedHeight=e.globals.gridHeight/e.globals.dataPoints,this.dataPointsDividedWidth=e.globals.gridWidth/e.globals.dataPoints,this.showTooltipTitle&&(this.tooltipTitle=document.createElement("div"),this.tooltipTitle.classList.add("apexcharts-tooltip-title"),this.tooltipTitle.style.fontFamily=this.tConfig.style.fontFamily||e.config.chart.fontFamily,this.tooltipTitle.style.fontSize=this.tConfig.style.fontSize,i.appendChild(this.tooltipTitle));var s=e.globals.series.length;(e.globals.xyCharts||e.globals.comboCharts)&&this.tConfig.shared&&(s=this.showOnIntersect?1:e.globals.series.length),this.legendLabels=e.globals.dom.baseEl.querySelectorAll(".apexcharts-legend-text"),this.ttItems=this.createTTElements(s),this.addSVGEvents();}}},{key:"createTTElements",value:function(t){for(var e=this.w,i=[],a=this.getElTooltip(),s=0;s<t;s++){var r=document.createElement("div");r.classList.add("apexcharts-tooltip-series-group"),r.style.order=e.config.tooltip.inverseOrder?t-s:s+1,this.tConfig.shared&&this.tConfig.enabledOnSeries&&Array.isArray(this.tConfig.enabledOnSeries)&&this.tConfig.enabledOnSeries.indexOf(s)<0&&r.classList.add("apexcharts-tooltip-series-group-hidden");var n=document.createElement("span");n.classList.add("apexcharts-tooltip-marker"),n.style.backgroundColor=e.globals.colors[s],r.appendChild(n);var o=document.createElement("div");o.classList.add("apexcharts-tooltip-text"),o.style.fontFamily=this.tConfig.style.fontFamily||e.config.chart.fontFamily,o.style.fontSize=this.tConfig.style.fontSize;var l=document.createElement("div");l.classList.add("apexcharts-tooltip-y-group");var h=document.createElement("span");h.classList.add("apexcharts-tooltip-text-label"),l.appendChild(h);var c=document.createElement("span");c.classList.add("apexcharts-tooltip-text-value"),l.appendChild(c);var d=document.createElement("div");d.classList.add("apexcharts-tooltip-z-group");var g=document.createElement("span");g.classList.add("apexcharts-tooltip-text-z-label"),d.appendChild(g);var u=document.createElement("span");u.classList.add("apexcharts-tooltip-text-z-value"),d.appendChild(u),o.appendChild(l),o.appendChild(d),r.appendChild(o),a.appendChild(r),i.push(r);}return i}},{key:"addSVGEvents",value:function(){var t=this.w,e=t.config.chart.type,i=this.getElTooltip(),a=!("bar"!==e&&"candlestick"!==e&&"rangeBar"!==e),s="area"===e||"line"===e||"scatter"===e||"bubble"===e||"radar"===e,r=t.globals.dom.Paper.node,n=this.getElGrid();n&&(this.seriesBound=n.getBoundingClientRect());var o,l=[],h=[],c={hoverArea:r,elGrid:n,tooltipEl:i,tooltipY:l,tooltipX:h,ttItems:this.ttItems};if(t.globals.axisCharts&&(s?o=t.globals.dom.baseEl.querySelectorAll(".apexcharts-series[data\\:longestSeries='true'] .apexcharts-marker"):a?o=t.globals.dom.baseEl.querySelectorAll(".apexcharts-series .apexcharts-bar-area, .apexcharts-series .apexcharts-candlestick-area, .apexcharts-series .apexcharts-rangebar-area"):"heatmap"!==e&&"treemap"!==e||(o=t.globals.dom.baseEl.querySelectorAll(".apexcharts-series .apexcharts-heatmap, .apexcharts-series .apexcharts-treemap")),o&&o.length))for(var d=0;d<o.length;d++)l.push(o[d].getAttribute("cy")),h.push(o[d].getAttribute("cx"));if(t.globals.xyCharts&&!this.showOnIntersect||t.globals.comboCharts&&!this.showOnIntersect||a&&this.tooltipUtil.hasBars()&&this.tConfig.shared)this.addPathsEventListeners([r],c);else if(a&&!t.globals.comboCharts||s&&this.showOnIntersect)this.addDatapointEventsListeners(c);else if(!t.globals.axisCharts||"heatmap"===e||"treemap"===e){var g=t.globals.dom.baseEl.querySelectorAll(".apexcharts-series");this.addPathsEventListeners(g,c);}if(this.showOnIntersect){var u=t.globals.dom.baseEl.querySelectorAll(".apexcharts-line-series .apexcharts-marker, .apexcharts-area-series .apexcharts-marker");u.length>0&&this.addPathsEventListeners(u,c),this.tooltipUtil.hasBars()&&!this.tConfig.shared&&this.addDatapointEventsListeners(c);}}},{key:"drawFixedTooltipRect",value:function(){var t=this.w,e=this.getElTooltip(),i=e.getBoundingClientRect(),a=i.width+10,s=i.height+10,r=this.tConfig.fixed.offsetX,n=this.tConfig.fixed.offsetY,o=this.tConfig.fixed.position.toLowerCase();return o.indexOf("right")>-1&&(r=r+t.globals.svgWidth-a+10),o.indexOf("bottom")>-1&&(n=n+t.globals.svgHeight-s-10),e.style.left=r+"px",e.style.top=n+"px",{x:r,y:n,ttWidth:a,ttHeight:s}}},{key:"addDatapointEventsListeners",value:function(t){var e=this.w.globals.dom.baseEl.querySelectorAll(".apexcharts-series-markers .apexcharts-marker, .apexcharts-bar-area, .apexcharts-candlestick-area, .apexcharts-rangebar-area");this.addPathsEventListeners(e,t);}},{key:"addPathsEventListeners",value:function(t,e){for(var i=this,a=function(a){var s={paths:t[a],tooltipEl:e.tooltipEl,tooltipY:e.tooltipY,tooltipX:e.tooltipX,elGrid:e.elGrid,hoverArea:e.hoverArea,ttItems:e.ttItems};["mousemove","mouseup","touchmove","mouseout","touchend"].map((function(e){return t[a].addEventListener(e,i.seriesHover.bind(i,s),{capture:!1,passive:!0})}));},s=0;s<t.length;s++)a(s);}},{key:"seriesHover",value:function(t,e){var i=this,a=[],s=this.w;s.config.chart.group&&(a=this.ctx.getGroupedCharts()),s.globals.axisCharts&&(s.globals.minX===-1/0&&s.globals.maxX===1/0||0===s.globals.dataPoints)||(a.length?a.forEach((function(a){var s=i.getElTooltip(a),r={paths:t.paths,tooltipEl:s,tooltipY:t.tooltipY,tooltipX:t.tooltipX,elGrid:t.elGrid,hoverArea:t.hoverArea,ttItems:a.w.globals.tooltip.ttItems};a.w.globals.minX===i.w.globals.minX&&a.w.globals.maxX===i.w.globals.maxX&&a.w.globals.tooltip.seriesHoverByContext({chartCtx:a,ttCtx:a.w.globals.tooltip,opt:r,e:e});})):this.seriesHoverByContext({chartCtx:this.ctx,ttCtx:this.w.globals.tooltip,opt:t,e:e}));}},{key:"seriesHoverByContext",value:function(t){var e=t.chartCtx,i=t.ttCtx,a=t.opt,s=t.e,r=e.w,n=this.getElTooltip();(i.tooltipRect={x:0,y:0,ttWidth:n.getBoundingClientRect().width,ttHeight:n.getBoundingClientRect().height},i.e=s,!i.tooltipUtil.hasBars()||r.globals.comboCharts||i.isBarShared)||this.tConfig.onDatasetHover.highlightDataSeries&&new E(e).toggleSeriesOnHover(s,s.target.parentNode);i.fixedTooltip&&i.drawFixedTooltipRect(),r.globals.axisCharts?i.axisChartsTooltips({e:s,opt:a,tooltipRect:i.tooltipRect}):i.nonAxisChartsTooltips({e:s,opt:a,tooltipRect:i.tooltipRect});}},{key:"axisChartsTooltips",value:function(t){var e,i,a=t.e,s=t.opt,r=this.w,n=s.elGrid.getBoundingClientRect(),o="touchmove"===a.type?a.touches[0].clientX:a.clientX,l="touchmove"===a.type?a.touches[0].clientY:a.clientY;if(this.clientY=l,this.clientX=o,r.globals.capturedSeriesIndex=-1,r.globals.capturedDataPointIndex=-1,l<n.top||l>n.top+n.height)this.handleMouseOut(s);else {if(Array.isArray(this.tConfig.enabledOnSeries)&&!r.config.tooltip.shared){var h=parseInt(s.paths.getAttribute("index"),10);if(this.tConfig.enabledOnSeries.indexOf(h)<0)return void this.handleMouseOut(s)}var c=this.getElTooltip(),d=this.getElXCrosshairs(),g=r.globals.xyCharts||"bar"===r.config.chart.type&&!r.globals.isBarHorizontal&&this.tooltipUtil.hasBars()&&this.tConfig.shared||r.globals.comboCharts&&this.tooltipUtil.hasBars();if(r.globals.isBarHorizontal&&this.tooltipUtil.hasBars()&&(g=!1),"mousemove"===a.type||"touchmove"===a.type||"mouseup"===a.type){null!==d&&d.classList.add("apexcharts-active");var u=this.yaxisTooltips.filter((function(t){return !0===t}));if(null!==this.ycrosshairs&&u.length&&this.ycrosshairs.classList.add("apexcharts-active"),g&&!this.showOnIntersect)this.handleStickyTooltip(a,o,l,s);else if("heatmap"===r.config.chart.type||"treemap"===r.config.chart.type){var f=this.intersect.handleHeatTreeTooltip({e:a,opt:s,x:e,y:i,type:r.config.chart.type});e=f.x,i=f.y,c.style.left=e+"px",c.style.top=i+"px";}else this.tooltipUtil.hasBars()&&this.intersect.handleBarTooltip({e:a,opt:s}),this.tooltipUtil.hasMarkers()&&this.intersect.handleMarkerTooltip({e:a,opt:s,x:e,y:i});if(this.yaxisTooltips.length)for(var p=0;p<r.config.yaxis.length;p++)this.axesTooltip.drawYaxisTooltipText(p,l,this.xyRatios);s.tooltipEl.classList.add("apexcharts-active");}else "mouseout"!==a.type&&"touchend"!==a.type||this.handleMouseOut(s);}}},{key:"nonAxisChartsTooltips",value:function(t){var e=t.e,i=t.opt,a=t.tooltipRect,s=this.w,r=i.paths.getAttribute("rel"),n=this.getElTooltip(),o=s.globals.dom.elWrap.getBoundingClientRect();if("mousemove"===e.type||"touchmove"===e.type){n.classList.add("apexcharts-active"),this.tooltipLabels.drawSeriesTexts({ttItems:i.ttItems,i:parseInt(r,10)-1,shared:!1});var l=s.globals.clientX-o.left-a.ttWidth/2,h=s.globals.clientY-o.top-a.ttHeight-10;n.style.left=l+"px",n.style.top=h+"px";}else "mouseout"!==e.type&&"touchend"!==e.type||n.classList.remove("apexcharts-active");}},{key:"handleStickyTooltip",value:function(t,e,i,a){var s=this.w,r=this.tooltipUtil.getNearestValues({context:this,hoverArea:a.hoverArea,elGrid:a.elGrid,clientX:e,clientY:i}),n=r.j,o=r.capturedSeries;r.hoverX<0||r.hoverX>s.globals.gridWidth?this.handleMouseOut(a):null!==o?this.handleStickyCapturedSeries(t,o,a,n):this.tooltipUtil.isXoverlap(n)&&this.create(t,this,0,n,a.ttItems);}},{key:"handleStickyCapturedSeries",value:function(t,e,i,a){var s=this.w;null===s.globals.series[e][a]?this.handleMouseOut(i):void 0!==s.globals.series[e][a]?this.tConfig.shared&&this.tooltipUtil.isXoverlap(a)&&this.tooltipUtil.isInitialSeriesSameLen()?this.create(t,this,e,a,i.ttItems):this.create(t,this,e,a,i.ttItems,!1):this.tooltipUtil.isXoverlap(a)&&this.create(t,this,0,a,i.ttItems);}},{key:"deactivateHoverFilter",value:function(){for(var t=this.w,e=new b(this.ctx),i=t.globals.dom.Paper.select(".apexcharts-bar-area"),a=0;a<i.length;a++)e.pathMouseLeave(i[a]);}},{key:"handleMouseOut",value:function(t){var e=this.w,i=this.getElXCrosshairs();if(t.tooltipEl.classList.remove("apexcharts-active"),this.deactivateHoverFilter(),"bubble"!==e.config.chart.type&&this.marker.resetPointsSize(),null!==i&&i.classList.remove("apexcharts-active"),null!==this.ycrosshairs&&this.ycrosshairs.classList.remove("apexcharts-active"),this.blxaxisTooltip&&this.xaxisTooltip.classList.remove("apexcharts-active"),this.yaxisTooltips.length){null===this.yaxisTTEls&&(this.yaxisTTEls=e.globals.dom.baseEl.querySelectorAll(".apexcharts-yaxistooltip"));for(var a=0;a<this.yaxisTTEls.length;a++)this.yaxisTTEls[a].classList.remove("apexcharts-active");}e.config.legend.tooltipHoverFormatter&&this.legendLabels.forEach((function(t){var e=t.getAttribute("data:default-text");t.innerHTML=decodeURIComponent(e);}));}},{key:"markerClick",value:function(t,e,i){var a=this.w;"function"==typeof a.config.chart.events.markerClick&&a.config.chart.events.markerClick(t,this.ctx,{seriesIndex:e,dataPointIndex:i,w:a}),this.ctx.events.fireEvent("markerClick",[t,this.ctx,{seriesIndex:e,dataPointIndex:i,w:a}]);}},{key:"create",value:function(t,e,i,a,s){var r=arguments.length>5&&void 0!==arguments[5]?arguments[5]:null,n=this.w,o=e;"mouseup"===t.type&&this.markerClick(t,i,a),null===r&&(r=this.tConfig.shared);var l=this.tooltipUtil.hasMarkers(),h=this.tooltipUtil.getElBars();if(n.config.legend.tooltipHoverFormatter){var c=n.config.legend.tooltipHoverFormatter,d=Array.from(this.legendLabels);d.forEach((function(t){var e=t.getAttribute("data:default-text");t.innerHTML=decodeURIComponent(e);}));for(var g=0;g<d.length;g++){var u=d[g],f=parseInt(u.getAttribute("i"),10),p=decodeURIComponent(u.getAttribute("data:default-text")),x=c(p,{seriesIndex:r?f:i,dataPointIndex:a,w:n});if(r)u.innerHTML=n.globals.collapsedSeriesIndices.indexOf(f)<0?x:p;else if(u.innerHTML=f===i?x:p,i===f)break}}if(r){if(o.tooltipLabels.drawSeriesTexts({ttItems:s,i:i,j:a,shared:!this.showOnIntersect&&this.tConfig.shared}),l&&(n.globals.markers.largestSize>0?o.marker.enlargePoints(a):o.tooltipPosition.moveDynamicPointsOnHover(a)),this.tooltipUtil.hasBars()&&(this.barSeriesHeight=this.tooltipUtil.getBarsHeight(h),this.barSeriesHeight>0)){var m=new b(this.ctx),v=n.globals.dom.Paper.select(".apexcharts-bar-area[j='".concat(a,"']"));this.deactivateHoverFilter(),this.tooltipPosition.moveStickyTooltipOverBars(a);for(var y=0;y<v.length;y++)m.pathMouseEnter(v[y]);}}else o.tooltipLabels.drawSeriesTexts({shared:!1,ttItems:s,i:i,j:a}),this.tooltipUtil.hasBars()&&o.tooltipPosition.moveStickyTooltipOverBars(a),l&&o.tooltipPosition.moveMarkers(i,a);}}]),t}(),mt=function(t){o(s,t);var i=d(s);function s(){return e(this,s),i.apply(this,arguments)}return a(s,[{key:"draw",value:function(t,e){var i=this,a=this.w;this.graphics=new b(this.ctx),this.bar=new Y(this.ctx,this.xyRatios);var s=new w(this.ctx,a);t=s.getLogSeries(t),this.yRatio=s.getLogYRatios(this.yRatio),this.barHelpers.initVariables(t),"100%"===a.config.chart.stackType&&(t=a.globals.seriesPercent.slice()),this.series=t,this.totalItems=0,this.prevY=[],this.prevX=[],this.prevYF=[],this.prevXF=[],this.prevYVal=[],this.prevXVal=[],this.xArrj=[],this.xArrjF=[],this.xArrjVal=[],this.yArrj=[],this.yArrjF=[],this.yArrjVal=[];for(var r=0;r<t.length;r++)t[r].length>0&&(this.totalItems+=t[r].length);for(var o=this.graphics.group({class:"apexcharts-bar-series apexcharts-plot-series"}),l=0,h=0,c=function(s,r){var c=void 0,d=void 0,g=void 0,u=void 0,p=[],x=[],b=a.globals.comboCharts?e[s]:s;i.yRatio.length>1&&(i.yaxisIndex=b),i.isReversed=a.config.yaxis[i.yaxisIndex]&&a.config.yaxis[i.yaxisIndex].reversed;var m=i.graphics.group({class:"apexcharts-series",seriesName:f.escapeString(a.globals.seriesNames[b]),rel:s+1,"data:realIndex":b});i.ctx.series.addCollapsedClassToSeries(m,b);var v=i.graphics.group({class:"apexcharts-datalabels","data:realIndex":b}),y=0,w=0,k=i.initialPositions(l,h,c,d,g,u);h=k.y,y=k.barHeight,d=k.yDivision,u=k.zeroW,l=k.x,w=k.barWidth,c=k.xDivision,g=k.zeroH,i.yArrj=[],i.yArrjF=[],i.yArrjVal=[],i.xArrj=[],i.xArrjF=[],i.xArrjVal=[],1===i.prevY.length&&i.prevY[0].every((function(t){return isNaN(t)}))&&(i.prevY[0]=i.prevY[0].map((function(t){return g})),i.prevYF[0]=i.prevYF[0].map((function(t){return 0})));for(var A=0;A<a.globals.dataPoints;A++){var S=i.barHelpers.getStrokeWidth(s,A,b),C={indexes:{i:s,j:A,realIndex:b,bc:r},strokeWidth:S,x:l,y:h,elSeries:m},L=null;i.isHorizontal?(L=i.drawStackedBarPaths(n(n({},C),{},{zeroW:u,barHeight:y,yDivision:d})),w=i.series[s][A]/i.invertedYRatio):(L=i.drawStackedColumnPaths(n(n({},C),{},{xDivision:c,barWidth:w,zeroH:g})),y=i.series[s][A]/i.yRatio[i.yaxisIndex]),h=L.y,l=L.x,p.push(l),x.push(h);var P=i.barHelpers.getPathFillColor(t,s,A,b);m=i.renderSeries({realIndex:b,pathFill:P,j:A,i:s,pathFrom:L.pathFrom,pathTo:L.pathTo,strokeWidth:S,elSeries:m,x:l,y:h,series:t,barHeight:y,barWidth:w,elDataLabelsWrap:v,type:"bar",visibleSeries:0});}a.globals.seriesXvalues[b]=p,a.globals.seriesYvalues[b]=x,i.prevY.push(i.yArrj),i.prevYF.push(i.yArrjF),i.prevYVal.push(i.yArrjVal),i.prevX.push(i.xArrj),i.prevXF.push(i.xArrjF),i.prevXVal.push(i.xArrjVal),o.add(m);},d=0,g=0;d<t.length;d++,g++)c(d,g);return o}},{key:"initialPositions",value:function(t,e,i,a,s,r){var n,o,l=this.w;return this.isHorizontal?(n=(n=a=l.globals.gridHeight/l.globals.dataPoints)*parseInt(l.config.plotOptions.bar.barHeight,10)/100,r=this.baseLineInvertedY+l.globals.padHorizontal+(this.isReversed?l.globals.gridWidth:0)-(this.isReversed?2*this.baseLineInvertedY:0),e=(a-n)/2):(o=i=l.globals.gridWidth/l.globals.dataPoints,o=l.globals.isXNumeric&&l.globals.dataPoints>1?(i=l.globals.minXDiff/this.xRatio)*parseInt(this.barOptions.columnWidth,10)/100:o*parseInt(l.config.plotOptions.bar.columnWidth,10)/100,s=this.baseLineY[this.yaxisIndex]+(this.isReversed?l.globals.gridHeight:0)-(this.isReversed?2*this.baseLineY[this.yaxisIndex]:0),t=l.globals.padHorizontal+(i-o)/2),{x:t,y:e,yDivision:a,xDivision:i,barHeight:n,barWidth:o,zeroH:s,zeroW:r}}},{key:"drawStackedBarPaths",value:function(t){for(var e,i=t.indexes,a=t.barHeight,s=t.strokeWidth,r=t.zeroW,n=t.x,o=t.y,l=t.yDivision,h=t.elSeries,c=this.w,d=o,g=i.i,u=i.j,f=0,p=0;p<this.prevXF.length;p++)f+=this.prevXF[p][u];if(g>0){var x=r;this.prevXVal[g-1][u]<0?x=this.series[g][u]>=0?this.prevX[g-1][u]+f-2*(this.isReversed?f:0):this.prevX[g-1][u]:this.prevXVal[g-1][u]>=0&&(x=this.series[g][u]>=0?this.prevX[g-1][u]:this.prevX[g-1][u]-f+2*(this.isReversed?f:0)),e=x;}else e=r;n=null===this.series[g][u]?e:e+this.series[g][u]/this.invertedYRatio-2*(this.isReversed?this.series[g][u]/this.invertedYRatio:0),this.xArrj.push(n),this.xArrjF.push(Math.abs(e-n)),this.xArrjVal.push(this.series[g][u]);var b=this.barHelpers.getBarpaths({barYPosition:d,barHeight:a,x1:e,x2:n,strokeWidth:s,series:this.series,realIndex:i.realIndex,i:g,j:u,w:c});return this.barHelpers.barBackground({j:u,i:g,y1:d,y2:a,elSeries:h}),o+=l,{pathTo:b.pathTo,pathFrom:b.pathFrom,x:n,y:o}}},{key:"drawStackedColumnPaths",value:function(t){var e=t.indexes,i=t.x,a=t.y,s=t.xDivision,r=t.barWidth,n=t.zeroH,o=(t.strokeWidth,t.elSeries),l=this.w,h=e.i,c=e.j,d=e.bc;if(l.globals.isXNumeric){var g=l.globals.seriesX[h][c];g||(g=0),i=(g-l.globals.minX)/this.xRatio-r/2;}for(var u,f=i,p=0,x=0;x<this.prevYF.length;x++)p+=isNaN(this.prevYF[x][c])?0:this.prevYF[x][c];if(h>0&&!l.globals.isXNumeric||h>0&&l.globals.isXNumeric&&l.globals.seriesX[h-1][c]===l.globals.seriesX[h][c]){var b,m,v=Math.min(this.yRatio.length+1,h+1);if(void 0!==this.prevY[h-1])for(var y=1;y<v;y++)if(!isNaN(this.prevY[h-y][c])){m=this.prevY[h-y][c];break}for(var w=1;w<v;w++){if(this.prevYVal[h-w][c]<0){b=this.series[h][c]>=0?m-p+2*(this.isReversed?p:0):m;break}if(this.prevYVal[h-w][c]>=0){b=this.series[h][c]>=0?m:m+p-2*(this.isReversed?p:0);break}}void 0===b&&(b=l.globals.gridHeight),u=this.prevYF[0].every((function(t){return 0===t}))&&this.prevYF.slice(1,h).every((function(t){return t.every((function(t){return isNaN(t)}))}))?l.globals.gridHeight-n:b;}else u=l.globals.gridHeight-n;a=u-this.series[h][c]/this.yRatio[this.yaxisIndex]+2*(this.isReversed?this.series[h][c]/this.yRatio[this.yaxisIndex]:0),this.yArrj.push(a),this.yArrjF.push(Math.abs(u-a)),this.yArrjVal.push(this.series[h][c]);var k=this.barHelpers.getColumnPaths({barXPosition:f,barWidth:r,y1:u,y2:a,yRatio:this.yRatio[this.yaxisIndex],strokeWidth:this.strokeWidth,series:this.series,realIndex:e.realIndex,i:h,j:c,w:l});return this.barHelpers.barBackground({bc:d,j:c,i:h,x1:f,x2:r,elSeries:o}),i+=s,{pathTo:k.pathTo,pathFrom:k.pathFrom,x:l.globals.isXNumeric?i-s:i,y:a}}}]),s}(Y),vt=function(t){o(s,t);var i=d(s);function s(){return e(this,s),i.apply(this,arguments)}return a(s,[{key:"draw",value:function(t,e){var i=this.w,a=new b(this.ctx),s=new P(this.ctx);this.candlestickOptions=this.w.config.plotOptions.candlestick;var r=new w(this.ctx,i);t=r.getLogSeries(t),this.series=t,this.yRatio=r.getLogYRatios(this.yRatio),this.barHelpers.initVariables(t);for(var n=a.group({class:"apexcharts-candlestick-series apexcharts-plot-series"}),o=0;o<t.length;o++){var l,h,c=void 0,d=void 0,g=[],u=[],p=i.globals.comboCharts?e[o]:o,x=a.group({class:"apexcharts-series",seriesName:f.escapeString(i.globals.seriesNames[p]),rel:o+1,"data:realIndex":p});t[o].length>0&&(this.visibleI=this.visibleI+1);var m,v;this.yRatio.length>1&&(this.yaxisIndex=p);var y=this.barHelpers.initialPositions();d=y.y,m=y.barHeight,c=y.x,v=y.barWidth,l=y.xDivision,h=y.zeroH,u.push(c+v/2);for(var k=a.group({class:"apexcharts-datalabels","data:realIndex":p}),A=0;A<i.globals.dataPoints;A++){var S,C=this.barHelpers.getStrokeWidth(o,A,p),L=this.drawCandleStickPaths({indexes:{i:o,j:A,realIndex:p},x:c,y:d,xDivision:l,barWidth:v,zeroH:h,strokeWidth:C,elSeries:x});d=L.y,c=L.x,S=L.color,A>0&&u.push(c+v/2),g.push(d);var T=s.fillPath({seriesNumber:p,dataPointIndex:A,color:S,value:t[o][A]}),z=this.candlestickOptions.wick.useFillColor?S:void 0;this.renderSeries({realIndex:p,pathFill:T,lineFill:z,j:A,i:o,pathFrom:L.pathFrom,pathTo:L.pathTo,strokeWidth:C,elSeries:x,x:c,y:d,series:t,barHeight:m,barWidth:v,elDataLabelsWrap:k,visibleSeries:this.visibleI,type:"candlestick"});}i.globals.seriesXvalues[p]=u,i.globals.seriesYvalues[p]=g,n.add(x);}return n}},{key:"drawCandleStickPaths",value:function(t){var e=t.indexes,i=t.x,a=(t.y,t.xDivision),s=t.barWidth,r=t.zeroH,n=t.strokeWidth,o=this.w,l=new b(this.ctx),h=e.i,c=e.j,d=!0,g=o.config.plotOptions.candlestick.colors.upward,u=o.config.plotOptions.candlestick.colors.downward,f=this.yRatio[this.yaxisIndex],p=e.realIndex,x=this.getOHLCValue(p,c),m=r,v=r;x.o>x.c&&(d=!1);var y=Math.min(x.o,x.c),w=Math.max(x.o,x.c);o.globals.isXNumeric&&(i=(o.globals.seriesX[p][c]-o.globals.minX)/this.xRatio-s/2);var k=i+s*this.visibleI;void 0===this.series[h][c]||null===this.series[h][c]?y=r:(y=r-y/f,w=r-w/f,m=r-x.h/f,v=r-x.l/f);var A=l.move(k,r),S=l.move(k,y);return o.globals.previousPaths.length>0&&(S=this.getPreviousPath(p,c,!0)),A=l.move(k,w)+l.line(k+s/2,w)+l.line(k+s/2,m)+l.line(k+s/2,w)+l.line(k+s,w)+l.line(k+s,y)+l.line(k+s/2,y)+l.line(k+s/2,v)+l.line(k+s/2,y)+l.line(k,y)+l.line(k,w-n/2),S+=l.move(k,y),o.globals.isXNumeric||(i+=a),{pathTo:A,pathFrom:S,x:i,y:w,barXPosition:k,color:d?g:u}}},{key:"getOHLCValue",value:function(t,e){var i=this.w;return {o:i.globals.seriesCandleO[t][e],h:i.globals.seriesCandleH[t][e],l:i.globals.seriesCandleL[t][e],c:i.globals.seriesCandleC[t][e]}}}]),s}(Y),yt=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"checkColorRange",value:function(){var t=this.w,e=!1,i=t.config.plotOptions[t.config.chart.type];return i.colorScale.ranges.length>0&&i.colorScale.ranges.map((function(t,i){t.from<=0&&(e=!0);})),e}},{key:"getShadeColor",value:function(t,e,i,a){var s=this.w,r=1,n=s.config.plotOptions[t].shadeIntensity,o=this.determineColor(t,e,i);s.globals.hasNegs||a?r=s.config.plotOptions[t].reverseNegativeShade?o.percent<0?o.percent/100*(1.25*n):(1-o.percent/100)*(1.25*n):o.percent<=0?1-(1+o.percent/100)*n:(1-o.percent/100)*n:(r=1-o.percent/100,"treemap"===t&&(r=(1-o.percent/100)*(1.25*n)));var l=o.color,h=new f;return s.config.plotOptions[t].enableShades&&(r<0&&(r=0),l="dark"===this.w.config.theme.mode?f.hexToRgba(h.shadeColor(-1*r,o.color),s.config.fill.opacity):f.hexToRgba(h.shadeColor(r,o.color),s.config.fill.opacity)),{color:l,colorProps:o}}},{key:"determineColor",value:function(t,e,i){var a=this.w,s=a.globals.series[e][i],r=a.config.plotOptions[t],n=r.colorScale.inverse?i:e;a.config.plotOptions[t].distributed&&(n=i);var o=a.globals.colors[n],l=null,h=Math.min.apply(Math,g(a.globals.series[e])),c=Math.max.apply(Math,g(a.globals.series[e]));r.distributed||"heatmap"!==t||(h=a.globals.minY,c=a.globals.maxY),void 0!==r.colorScale.min&&(h=r.colorScale.min<a.globals.minY?r.colorScale.min:a.globals.minY,c=r.colorScale.max>a.globals.maxY?r.colorScale.max:a.globals.maxY);var d=Math.abs(c)+Math.abs(h),u=100*s/(0===d?d-1e-6:d);r.colorScale.ranges.length>0&&r.colorScale.ranges.map((function(t,e){if(s>=t.from&&s<=t.to){o=t.color,l=t.foreColor?t.foreColor:null,h=t.from,c=t.to;var i=Math.abs(c)+Math.abs(h);u=100*s/(0===i?i-1e-6:i);}}));return {color:o,foreColor:l,percent:u}}},{key:"calculateDataLabels",value:function(t){var e=t.text,i=t.x,a=t.y,s=t.i,r=t.j,n=t.colorProps,o=t.fontSize,l=this.w.config.dataLabels,h=new b(this.ctx),c=new I(this.ctx),d=null;if(l.enabled){d=h.group({class:"apexcharts-data-labels"});var g=l.offsetX,u=l.offsetY,f=i+g,p=a+parseFloat(l.style.fontSize)/3+u;c.plotDataLabelsText({x:f,y:p,text:e,i:s,j:r,color:n.foreColor,parent:d,fontSize:o,dataLabelsConfig:l});}return d}},{key:"addListeners",value:function(t){var e=new b(this.ctx);t.node.addEventListener("mouseenter",e.pathMouseEnter.bind(this,t)),t.node.addEventListener("mouseleave",e.pathMouseLeave.bind(this,t)),t.node.addEventListener("mousedown",e.pathMouseDown.bind(this,t));}}]),t}(),wt=function(){function t(i,a){e(this,t),this.ctx=i,this.w=i.w,this.xRatio=a.xRatio,this.yRatio=a.yRatio,this.dynamicAnim=this.w.config.chart.animations.dynamicAnimation,this.helpers=new yt(i),this.rectRadius=this.w.config.plotOptions.heatmap.radius,this.strokeWidth=this.w.config.stroke.show?this.w.config.stroke.width:0;}return a(t,[{key:"draw",value:function(t){var e=this.w,i=new b(this.ctx),a=i.group({class:"apexcharts-heatmap"});a.attr("clip-path","url(#gridRectMask".concat(e.globals.cuid,")"));var s=e.globals.gridWidth/e.globals.dataPoints,r=e.globals.gridHeight/e.globals.series.length,n=0,o=!1;this.negRange=this.helpers.checkColorRange();var l=t.slice();e.config.yaxis[0].reversed&&(o=!0,l.reverse());for(var h=o?0:l.length-1;o?h<l.length:h>=0;o?h++:h--){var c=i.group({class:"apexcharts-series apexcharts-heatmap-series",seriesName:f.escapeString(e.globals.seriesNames[h]),rel:h+1,"data:realIndex":h});if(this.ctx.series.addCollapsedClassToSeries(c,h),e.config.chart.dropShadow.enabled){var d=e.config.chart.dropShadow;new p(this.ctx).dropShadow(c,d,h);}for(var g=0,u=e.config.plotOptions.heatmap.shadeIntensity,x=0;x<l[h].length;x++){var m=this.helpers.getShadeColor(e.config.chart.type,h,x,this.negRange),v=m.color,y=m.colorProps;if("image"===e.config.fill.type)v=new P(this.ctx).fillPath({seriesNumber:h,dataPointIndex:x,opacity:e.globals.hasNegs?y.percent<0?1-(1+y.percent/100):u+y.percent/100:y.percent/100,patternID:f.randomId(),width:e.config.fill.image.width?e.config.fill.image.width:s,height:e.config.fill.image.height?e.config.fill.image.height:r});var w=this.rectRadius,k=i.drawRect(g,n,s,r,w);if(k.attr({cx:g,cy:n}),k.node.classList.add("apexcharts-heatmap-rect"),c.add(k),k.attr({fill:v,i:h,index:h,j:x,val:l[h][x],"stroke-width":this.strokeWidth,stroke:e.config.plotOptions.heatmap.useFillColorAsStroke?v:e.globals.stroke.colors[0],color:v}),this.helpers.addListeners(k),e.config.chart.animations.enabled&&!e.globals.dataChanged){var A=1;e.globals.resized||(A=e.config.chart.animations.speed),this.animateHeatMap(k,g,n,s,r,A);}if(e.globals.dataChanged){var S=1;if(this.dynamicAnim.enabled&&e.globals.shouldAnimate){S=this.dynamicAnim.speed;var C=e.globals.previousPaths[h]&&e.globals.previousPaths[h][x]&&e.globals.previousPaths[h][x].color;C||(C="rgba(255, 255, 255, 0)"),this.animateHeatColor(k,f.isColorHex(C)?C:f.rgb2hex(C),f.isColorHex(v)?v:f.rgb2hex(v),S);}}var L=(0, e.config.dataLabels.formatter)(e.globals.series[h][x],{value:e.globals.series[h][x],seriesIndex:h,dataPointIndex:x,w:e}),T=this.helpers.calculateDataLabels({text:L,x:g+s/2,y:n+r/2,i:h,j:x,colorProps:y,series:l});null!==T&&c.add(T),g+=s;}n+=r,a.add(c);}var z=e.globals.yAxisScale[0].result.slice();e.config.yaxis[0].reversed?z.unshift(""):z.push(""),e.globals.yAxisScale[0].result=z;var I=e.globals.gridHeight/e.globals.series.length;return e.config.yaxis[0].labels.offsetY=-I/2,a}},{key:"animateHeatMap",value:function(t,e,i,a,s,r){var n=new x(this.ctx);n.animateRect(t,{x:e+a/2,y:i+s/2,width:0,height:0},{x:e,y:i,width:a,height:s},r,(function(){n.animationCompleted(t);}));}},{key:"animateHeatColor",value:function(t,e,i,a){t.attr({fill:e}).animate(a).attr({fill:i});}}]),t}(),kt=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"drawYAxisTexts",value:function(t,e,i,a){var s=this.w,r=s.config.yaxis[0],n=s.globals.yLabelFormatters[0];return new b(this.ctx).drawText({x:t+r.labels.offsetX,y:e+r.labels.offsetY,text:n(a,i),textAnchor:"middle",fontSize:r.labels.style.fontSize,fontFamily:r.labels.style.fontFamily,foreColor:Array.isArray(r.labels.style.colors)?r.labels.style.colors[i]:r.labels.style.colors})}}]),t}(),At=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;var a=this.w;this.chartType=this.w.config.chart.type,this.initialAnim=this.w.config.chart.animations.enabled,this.dynamicAnim=this.initialAnim&&this.w.config.chart.animations.dynamicAnimation.enabled,this.animBeginArr=[0],this.animDur=0,this.donutDataLabels=this.w.config.plotOptions.pie.donut.labels,this.lineColorArr=void 0!==a.globals.stroke.colors?a.globals.stroke.colors:a.globals.colors,this.defaultSize=a.globals.svgHeight<a.globals.svgWidth?a.globals.gridHeight:a.globals.gridWidth,this.centerY=this.defaultSize/2,this.centerX=a.globals.gridWidth/2,this.fullAngle=360,a.globals.radialSize=this.defaultSize/2.05-a.config.stroke.width-(a.config.chart.sparkline.enabled?0:a.config.chart.dropShadow.blur),this.donutSize=a.globals.radialSize*parseInt(a.config.plotOptions.pie.donut.size,10)/100,this.maxY=0,this.sliceLabels=[],this.sliceSizes=[],this.prevSectorAngleArr=[];}return a(t,[{key:"draw",value:function(t){var e=this,i=this.w,a=new b(this.ctx);if(this.ret=a.group({class:"apexcharts-pie"}),i.globals.noData)return this.ret;for(var s=0,r=0;r<t.length;r++)s+=f.negToZero(t[r]);var n=[],o=a.group();0===s&&(s=1e-5),t.forEach((function(t){e.maxY=Math.max(e.maxY,t);})),"polarArea"===this.chartType&&this.drawPolarElements();for(var l=0;l<t.length;l++){var h=this.fullAngle*f.negToZero(t[l])/s;n.push(h),"polarArea"===this.chartType?(n[l]=this.fullAngle/t.length,this.sliceSizes.push(i.globals.radialSize*t[l]/this.maxY)):this.sliceSizes.push(i.globals.radialSize);}if(i.globals.dataChanged){for(var c,d=0,g=0;g<i.globals.previousPaths.length;g++)d+=f.negToZero(i.globals.previousPaths[g]);for(var u=0;u<i.globals.previousPaths.length;u++)c=this.fullAngle*f.negToZero(i.globals.previousPaths[u])/d,this.prevSectorAngleArr.push(c);}this.donutSize<0&&(this.donutSize=0);var p=i.config.plotOptions.pie.customScale,x=i.globals.gridWidth/2,m=i.globals.gridHeight/2,v=x-i.globals.gridWidth/2*p,y=m-i.globals.gridHeight/2*p;if("donut"===this.chartType){var w=a.drawCircle(this.donutSize);w.attr({cx:this.centerX,cy:this.centerY,fill:i.config.plotOptions.pie.donut.background?i.config.plotOptions.pie.donut.background:"transparent"}),o.add(w);}var k=this.drawArcs(n,t);if(this.sliceLabels.forEach((function(t){k.add(t);})),o.attr({transform:"translate(".concat(v,", ").concat(y,") scale(").concat(p,")")}),o.add(k),this.ret.add(o),this.donutDataLabels.show){var A=this.renderInnerDataLabels(this.donutDataLabels,{hollowSize:this.donutSize,centerX:this.centerX,centerY:this.centerY,opacity:this.donutDataLabels.show,translateX:v,translateY:y});this.ret.add(A);}return this.ret}},{key:"drawArcs",value:function(t,e){var i=this.w,a=new p(this.ctx),s=new b(this.ctx),r=new P(this.ctx),n=s.group({class:"apexcharts-slices"}),o=i.config.plotOptions.pie.startAngle%this.fullAngle,l=o,h=o,c=o,d=o;this.strokeWidth=i.config.stroke.show?i.config.stroke.width:0;for(var g=0;g<t.length;g++){var u=s.group({class:"apexcharts-series apexcharts-pie-series",seriesName:f.escapeString(i.globals.seriesNames[g]),rel:g+1,"data:realIndex":g});n.add(u),h=d,c=(l=c)+t[g],d=h+this.prevSectorAngleArr[g];var x=c<l?this.fullAngle+c-l:c-l,m=r.fillPath({seriesNumber:g,size:this.sliceSizes[g],value:e[g]}),v=this.getChangedPath(h,d),y=s.drawPath({d:v,stroke:Array.isArray(this.lineColorArr)?this.lineColorArr[g]:this.lineColorArr,strokeWidth:0,fill:m,fillOpacity:i.config.fill.opacity,classes:"apexcharts-pie-area apexcharts-".concat(this.chartType.toLowerCase(),"-slice-").concat(g)});if(y.attr({index:0,j:g}),i.config.chart.dropShadow.enabled){var w=i.config.chart.dropShadow;a.dropShadow(y,w,g);}this.addListeners(y,this.donutDataLabels),b.setAttrs(y.node,{"data:angle":x,"data:startAngle":l,"data:strokeWidth":this.strokeWidth,"data:value":e[g]});var k={x:0,y:0};"pie"===this.chartType||"polarArea"===this.chartType?k=f.polarToCartesian(this.centerX,this.centerY,i.globals.radialSize/1.25+i.config.plotOptions.pie.dataLabels.offset,(l+x/2)%this.fullAngle):"donut"===this.chartType&&(k=f.polarToCartesian(this.centerX,this.centerY,(i.globals.radialSize+this.donutSize)/2+i.config.plotOptions.pie.dataLabels.offset,(l+x/2)%this.fullAngle)),u.add(y);var A=0;if(!this.initialAnim||i.globals.resized||i.globals.dataChanged?this.animBeginArr.push(0):(0===(A=x/this.fullAngle*i.config.chart.animations.speed)&&(A=1),this.animDur=A+this.animDur,this.animBeginArr.push(this.animDur)),this.dynamicAnim&&i.globals.dataChanged?this.animatePaths(y,{size:this.sliceSizes[g],endAngle:c,startAngle:l,prevStartAngle:h,prevEndAngle:d,animateStartingPos:!0,i:g,animBeginArr:this.animBeginArr,shouldSetPrevPaths:!0,dur:i.config.chart.animations.dynamicAnimation.speed}):this.animatePaths(y,{size:this.sliceSizes[g],endAngle:c,startAngle:l,i:g,totalItems:t.length-1,animBeginArr:this.animBeginArr,dur:A}),i.config.plotOptions.pie.expandOnClick&&"polarArea"!==this.chartType&&y.click(this.pieClicked.bind(this,g)),i.config.dataLabels.enabled){var S=k.x,C=k.y,L=100*x/this.fullAngle+"%";if(0!==x&&i.config.plotOptions.pie.dataLabels.minAngleToShowLabel<t[g]){var T=i.config.dataLabels.formatter;void 0!==T&&(L=T(i.globals.seriesPercent[g][0],{seriesIndex:g,w:i}));var z=i.globals.dataLabels.style.colors[g],I=s.group({class:"apexcharts-datalabels"}),M=s.drawText({x:S,y:C,text:L,textAnchor:"middle",fontSize:i.config.dataLabels.style.fontSize,fontFamily:i.config.dataLabels.style.fontFamily,fontWeight:i.config.dataLabels.style.fontWeight,foreColor:z});if(I.add(M),i.config.dataLabels.dropShadow.enabled){var E=i.config.dataLabels.dropShadow;a.dropShadow(M,E);}M.node.classList.add("apexcharts-pie-label"),i.config.chart.animations.animate&&!1===i.globals.resized&&(M.node.classList.add("apexcharts-pie-label-delay"),M.node.style.animationDelay=i.config.chart.animations.speed/940+"s"),this.sliceLabels.push(I);}}}return n}},{key:"addListeners",value:function(t,e){var i=new b(this.ctx);t.node.addEventListener("mouseenter",i.pathMouseEnter.bind(this,t)),t.node.addEventListener("mouseleave",i.pathMouseLeave.bind(this,t)),t.node.addEventListener("mouseleave",this.revertDataLabelsInner.bind(this,t.node,e)),t.node.addEventListener("mousedown",i.pathMouseDown.bind(this,t)),this.donutDataLabels.total.showAlways||(t.node.addEventListener("mouseenter",this.printDataLabelsInner.bind(this,t.node,e)),t.node.addEventListener("mousedown",this.printDataLabelsInner.bind(this,t.node,e)));}},{key:"animatePaths",value:function(t,e){var i=this.w,a=e.endAngle<e.startAngle?this.fullAngle+e.endAngle-e.startAngle:e.endAngle-e.startAngle,s=a,r=e.startAngle,n=e.startAngle;void 0!==e.prevStartAngle&&void 0!==e.prevEndAngle&&(r=e.prevEndAngle,s=e.prevEndAngle<e.prevStartAngle?this.fullAngle+e.prevEndAngle-e.prevStartAngle:e.prevEndAngle-e.prevStartAngle),e.i===i.config.series.length-1&&(a+n>this.fullAngle?e.endAngle=e.endAngle-(a+n):a+n<this.fullAngle&&(e.endAngle=e.endAngle+(this.fullAngle-(a+n)))),a===this.fullAngle&&(a=this.fullAngle-.01),this.animateArc(t,r,n,a,s,e);}},{key:"animateArc",value:function(t,e,i,a,s,r){var n,o=this,l=this.w,h=new x(this.ctx),c=r.size;(isNaN(e)||isNaN(s))&&(e=i,s=a,r.dur=0);var d=a,g=i,u=e<i?this.fullAngle+e-i:e-i;l.globals.dataChanged&&r.shouldSetPrevPaths&&r.prevEndAngle&&(n=o.getPiePath({me:o,startAngle:r.prevStartAngle,angle:r.prevEndAngle<r.prevStartAngle?this.fullAngle+r.prevEndAngle-r.prevStartAngle:r.prevEndAngle-r.prevStartAngle,size:c}),t.attr({d:n})),0!==r.dur?t.animate(r.dur,l.globals.easing,r.animBeginArr[r.i]).afterAll((function(){"pie"!==o.chartType&&"donut"!==o.chartType&&"polarArea"!==o.chartType||this.animate(l.config.chart.animations.dynamicAnimation.speed).attr({"stroke-width":o.strokeWidth}),r.i===l.config.series.length-1&&h.animationCompleted(t);})).during((function(l){d=u+(a-u)*l,r.animateStartingPos&&(d=s+(a-s)*l,g=e-s+(i-(e-s))*l),n=o.getPiePath({me:o,startAngle:g,angle:d,size:c}),t.node.setAttribute("data:pathOrig",n),t.attr({d:n});})):(n=o.getPiePath({me:o,startAngle:g,angle:a,size:c}),r.isTrack||(l.globals.animationEnded=!0),t.node.setAttribute("data:pathOrig",n),t.attr({d:n,"stroke-width":o.strokeWidth}));}},{key:"pieClicked",value:function(t){var e,i=this.w,a=this.sliceSizes[t]+(i.config.plotOptions.pie.expandOnClick?4:0),s=i.globals.dom.Paper.select(".apexcharts-".concat(this.chartType.toLowerCase(),"-slice-").concat(t)).members[0];if("true"!==s.attr("data:pieClicked")){var r=i.globals.dom.baseEl.getElementsByClassName("apexcharts-pie-area");Array.prototype.forEach.call(r,(function(t){t.setAttribute("data:pieClicked","false");var e=t.getAttribute("data:pathOrig");t.setAttribute("d",e);})),s.attr("data:pieClicked","true");var n=parseInt(s.attr("data:startAngle"),10),o=parseInt(s.attr("data:angle"),10);e=this.getPiePath({me:this,startAngle:n,angle:o,size:a}),360!==o&&s.plot(e);}else {s.attr({"data:pieClicked":"false"}),this.revertDataLabelsInner(s.node,this.donutDataLabels);var l=s.attr("data:pathOrig");s.attr({d:l});}}},{key:"getChangedPath",value:function(t,e){var i="";return this.dynamicAnim&&this.w.globals.dataChanged&&(i=this.getPiePath({me:this,startAngle:t,angle:e-t,size:this.size})),i}},{key:"getPiePath",value:function(t){var e=t.me,i=t.startAngle,a=t.angle,s=t.size,r=i,n=Math.PI*(r-90)/180,o=a+i;Math.ceil(o)>=this.fullAngle+this.w.config.plotOptions.pie.startAngle%this.fullAngle&&(o=this.fullAngle+this.w.config.plotOptions.pie.startAngle%this.fullAngle-.01),Math.ceil(o)>this.fullAngle&&(o-=this.fullAngle);var l=Math.PI*(o-90)/180,h=e.centerX+s*Math.cos(n),c=e.centerY+s*Math.sin(n),d=e.centerX+s*Math.cos(l),g=e.centerY+s*Math.sin(l),u=f.polarToCartesian(e.centerX,e.centerY,e.donutSize,o),p=f.polarToCartesian(e.centerX,e.centerY,e.donutSize,r),x=a>180?1:0,b=["M",h,c,"A",s,s,0,x,1,d,g];return "donut"===e.chartType?[].concat(b,["L",u.x,u.y,"A",e.donutSize,e.donutSize,0,x,0,p.x,p.y,"L",h,c,"z"]).join(" "):"pie"===e.chartType||"polarArea"===e.chartType?[].concat(b,["L",e.centerX,e.centerY,"L",h,c]).join(" "):[].concat(b).join(" ")}},{key:"drawPolarElements",value:function(){var t=this.w,e=new j(this.ctx),i=new b(this.ctx),a=new kt(this.ctx),s=i.group(),r=i.group(),n=void 0===t.config.yaxis[0].max&&void 0===t.config.yaxis[0].min,o=e.niceScale(0,Math.ceil(this.maxY),t.config.yaxis[0].tickAmount,0,n),l=o.result.reverse(),h=o.result.length;this.maxY=o.niceMax;for(var c=t.globals.radialSize,d=c/(h-1),g=0;g<h-1;g++){var u=i.drawCircle(c);if(u.attr({cx:this.centerX,cy:this.centerY,fill:"none","stroke-width":t.config.plotOptions.polarArea.rings.strokeWidth,stroke:t.config.plotOptions.polarArea.rings.strokeColor}),t.config.yaxis[0].show){var f=a.drawYAxisTexts(this.centerX,this.centerY-c+parseInt(t.config.yaxis[0].labels.style.fontSize,10)/2,g,l[g]);r.add(f);}s.add(u),c-=d;}this.ret.add(s),this.ret.add(r);}},{key:"renderInnerDataLabels",value:function(t,e){var i=this.w,a=new b(this.ctx),s=a.group({class:"apexcharts-datalabels-group",transform:"translate(".concat(e.translateX?e.translateX:0,", ").concat(e.translateY?e.translateY:0,") scale(").concat(i.config.plotOptions.pie.customScale,")")}),r=t.total.show;s.node.style.opacity=e.opacity;var n,o,l=e.centerX,h=e.centerY;n=void 0===t.name.color?i.globals.colors[0]:t.name.color;var c=t.name.fontSize,d=t.name.fontFamily,g=t.value.fontWeight;o=void 0===t.value.color?i.config.chart.foreColor:t.value.color;var u=t.value.formatter,f="",p="";if(r?(n=t.total.color,c=t.total.fontSize,d=t.total.fontFamily,g=t.total.fontWeight,p=t.total.label,f=t.total.formatter(i)):1===i.globals.series.length&&(f=u(i.globals.series[0],i),p=i.globals.seriesNames[0]),p&&(p=t.name.formatter(p,t.total.show,i)),t.name.show){var x=a.drawText({x:l,y:h+parseFloat(t.name.offsetY),text:p,textAnchor:"middle",foreColor:n,fontSize:c,fontWeight:g,fontFamily:d});x.node.classList.add("apexcharts-datalabel-label"),s.add(x);}if(t.value.show){var m=t.name.show?parseFloat(t.value.offsetY)+16:t.value.offsetY,v=a.drawText({x:l,y:h+m,text:f,textAnchor:"middle",foreColor:o,fontWeight:t.value.fontWeight,fontSize:t.value.fontSize,fontFamily:t.value.fontFamily});v.node.classList.add("apexcharts-datalabel-value"),s.add(v);}return s}},{key:"printInnerLabels",value:function(t,e,i,a){var s,r=this.w;a?s=void 0===t.name.color?r.globals.colors[parseInt(a.parentNode.getAttribute("rel"),10)-1]:t.name.color:r.globals.series.length>1&&t.total.show&&(s=t.total.color);var n=r.globals.dom.baseEl.querySelector(".apexcharts-datalabel-label"),o=r.globals.dom.baseEl.querySelector(".apexcharts-datalabel-value");i=(0, t.value.formatter)(i,r),a||"function"!=typeof t.total.formatter||(i=t.total.formatter(r));var l=e===t.total.label;e=t.name.formatter(e,l,r),null!==n&&(n.textContent=e),null!==o&&(o.textContent=i),null!==n&&(n.style.fill=s);}},{key:"printDataLabelsInner",value:function(t,e){var i=this.w,a=t.getAttribute("data:value"),s=i.globals.seriesNames[parseInt(t.parentNode.getAttribute("rel"),10)-1];i.globals.series.length>1&&this.printInnerLabels(e,s,a,t);var r=i.globals.dom.baseEl.querySelector(".apexcharts-datalabels-group");null!==r&&(r.style.opacity=1);}},{key:"revertDataLabelsInner",value:function(t,e,i){var a=this,s=this.w,r=s.globals.dom.baseEl.querySelector(".apexcharts-datalabels-group"),n=!1,o=s.globals.dom.baseEl.getElementsByClassName("apexcharts-pie-area"),l=function(t){var i=t.makeSliceOut,s=t.printLabel;Array.prototype.forEach.call(o,(function(t){"true"===t.getAttribute("data:pieClicked")&&(i&&(n=!0),s&&a.printDataLabelsInner(t,e));}));};if(l({makeSliceOut:!0,printLabel:!1}),e.total.show&&s.globals.series.length>1)n&&!e.total.showAlways?l({makeSliceOut:!1,printLabel:!0}):this.printInnerLabels(e,e.total.label,e.total.formatter(s));else if(l({makeSliceOut:!1,printLabel:!0}),!n)if(s.globals.selectedDataPoints.length&&s.globals.series.length>1)if(s.globals.selectedDataPoints[0].length>0){var h=s.globals.selectedDataPoints[0],c=s.globals.dom.baseEl.querySelector(".apexcharts-".concat(this.chartType.toLowerCase(),"-slice-").concat(h));this.printDataLabelsInner(c,e);}else r&&s.globals.selectedDataPoints.length&&0===s.globals.selectedDataPoints[0].length&&(r.style.opacity=0);else r&&s.globals.series.length>1&&(r.style.opacity=0);}}]),t}(),St=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.chartType=this.w.config.chart.type,this.initialAnim=this.w.config.chart.animations.enabled,this.dynamicAnim=this.initialAnim&&this.w.config.chart.animations.dynamicAnimation.enabled,this.animDur=0;var a=this.w;this.graphics=new b(this.ctx),this.lineColorArr=void 0!==a.globals.stroke.colors?a.globals.stroke.colors:a.globals.colors,this.defaultSize=a.globals.svgHeight<a.globals.svgWidth?a.globals.gridHeight+1.5*a.globals.goldenPadding:a.globals.gridWidth,this.maxValue=this.w.globals.maxY,this.minValue=this.w.globals.minY,this.polygons=a.config.plotOptions.radar.polygons,this.strokeWidth=a.config.stroke.show?a.config.stroke.width:0,this.size=this.defaultSize/2.1-this.strokeWidth-a.config.chart.dropShadow.blur,a.config.xaxis.labels.show&&(this.size=this.size-a.globals.xAxisLabelsWidth/1.75),void 0!==a.config.plotOptions.radar.size&&(this.size=a.config.plotOptions.radar.size),this.dataRadiusOfPercent=[],this.dataRadius=[],this.angleArr=[],this.yaxisLabelsTextsPos=[];}return a(t,[{key:"draw",value:function(t){var e=this,i=this.w,a=new P(this.ctx),s=[],r=new I(this.ctx);t.length&&(this.dataPointsLen=t[i.globals.maxValsInArrayIndex].length),this.disAngle=2*Math.PI/this.dataPointsLen;var o=i.globals.gridWidth/2,l=i.globals.gridHeight/2,h=o+i.config.plotOptions.radar.offsetX,c=l+i.config.plotOptions.radar.offsetY,d=this.graphics.group({class:"apexcharts-radar-series apexcharts-plot-series",transform:"translate(".concat(h||0,", ").concat(c||0,")")}),g=[],u=null,x=null;if(this.yaxisLabels=this.graphics.group({class:"apexcharts-yaxis"}),t.forEach((function(t,o){var l=t.length===i.globals.dataPoints,h=e.graphics.group().attr({class:"apexcharts-series","data:longestSeries":l,seriesName:f.escapeString(i.globals.seriesNames[o]),rel:o+1,"data:realIndex":o});e.dataRadiusOfPercent[o]=[],e.dataRadius[o]=[],e.angleArr[o]=[],t.forEach((function(t,i){var a=Math.abs(e.maxValue-e.minValue);t+=Math.abs(e.minValue),e.dataRadiusOfPercent[o][i]=t/a,e.dataRadius[o][i]=e.dataRadiusOfPercent[o][i]*e.size,e.angleArr[o][i]=i*e.disAngle;})),g=e.getDataPointsPos(e.dataRadius[o],e.angleArr[o]);var c=e.createPaths(g,{x:0,y:0});u=e.graphics.group({class:"apexcharts-series-markers-wrap apexcharts-element-hidden"}),x=e.graphics.group({class:"apexcharts-datalabels","data:realIndex":o}),i.globals.delayedElements.push({el:u.node,index:o});var d={i:o,realIndex:o,animationDelay:o,initialSpeed:i.config.chart.animations.speed,dataChangeSpeed:i.config.chart.animations.dynamicAnimation.speed,className:"apexcharts-radar",shouldClipToGrid:!1,bindEventsOnPaths:!1,stroke:i.globals.stroke.colors[o],strokeLineCap:i.config.stroke.lineCap},b=null;i.globals.previousPaths.length>0&&(b=e.getPreviousPath(o));for(var m=0;m<c.linePathsTo.length;m++){var v=e.graphics.renderPaths(n(n({},d),{},{pathFrom:null===b?c.linePathsFrom[m]:b,pathTo:c.linePathsTo[m],strokeWidth:Array.isArray(e.strokeWidth)?e.strokeWidth[o]:e.strokeWidth,fill:"none",drawShadow:!1}));h.add(v);var y=a.fillPath({seriesNumber:o}),w=e.graphics.renderPaths(n(n({},d),{},{pathFrom:null===b?c.areaPathsFrom[m]:b,pathTo:c.areaPathsTo[m],strokeWidth:0,fill:y,drawShadow:!1}));if(i.config.chart.dropShadow.enabled){var k=new p(e.ctx),A=i.config.chart.dropShadow;k.dropShadow(w,Object.assign({},A,{noUserSpaceOnUse:!0}),o);}h.add(w);}t.forEach((function(t,a){var s=new T(e.ctx).getMarkerConfig("apexcharts-marker",o,a),l=e.graphics.drawMarker(g[a].x,g[a].y,s);l.attr("rel",a),l.attr("j",a),l.attr("index",o),l.node.setAttribute("default-marker-size",s.pSize);var c=e.graphics.group({class:"apexcharts-series-markers"});c&&c.add(l),u.add(c),h.add(u);var d=i.config.dataLabels;if(d.enabled){var f=d.formatter(i.globals.series[o][a],{seriesIndex:o,dataPointIndex:a,w:i});r.plotDataLabelsText({x:g[a].x,y:g[a].y,text:f,textAnchor:"middle",i:o,j:o,parent:x,offsetCorrection:!1,dataLabelsConfig:n({},d)});}h.add(x);})),s.push(h);})),this.drawPolygons({parent:d}),i.config.xaxis.labels.show){var b=this.drawXAxisTexts();d.add(b);}return d.add(this.yaxisLabels),s.forEach((function(t){d.add(t);})),d}},{key:"drawPolygons",value:function(t){for(var e=this,i=this.w,a=t.parent,s=new kt(this.ctx),r=i.globals.yAxisScale[0].result.reverse(),n=r.length,o=[],l=this.size/(n-1),h=0;h<n;h++)o[h]=l*h;o.reverse();var c=[],d=[];o.forEach((function(t,i){var a=e.getPolygonPos(t),s="";a.forEach((function(t,a){if(0===i){var r=e.graphics.drawLine(t.x,t.y,0,0,Array.isArray(e.polygons.connectorColors)?e.polygons.connectorColors[a]:e.polygons.connectorColors);d.push(r);}0===a&&e.yaxisLabelsTextsPos.push({x:t.x,y:t.y}),s+=t.x+","+t.y+" ";})),c.push(s);})),c.forEach((function(t,s){var r=e.polygons.strokeColors,n=e.polygons.strokeWidth,o=e.graphics.drawPolygon(t,Array.isArray(r)?r[s]:r,Array.isArray(n)?n[s]:n,i.globals.radarPolygons.fill.colors[s]);a.add(o);})),d.forEach((function(t){a.add(t);})),i.config.yaxis[0].show&&this.yaxisLabelsTextsPos.forEach((function(t,i){var a=s.drawYAxisTexts(t.x,t.y,i,r[i]);e.yaxisLabels.add(a);}));}},{key:"drawXAxisTexts",value:function(){var t=this,e=this.w,i=e.config.xaxis.labels,a=this.graphics.group({class:"apexcharts-xaxis"}),s=this.getPolygonPos(this.size);return e.globals.labels.forEach((function(r,o){var l=e.config.xaxis.labels.formatter,h=new I(t.ctx);if(s[o]){var c=t.getTextPos(s[o],t.size),d=l(r,{seriesIndex:-1,dataPointIndex:o,w:e});h.plotDataLabelsText({x:c.newX,y:c.newY,text:d,textAnchor:c.textAnchor,i:o,j:o,parent:a,color:Array.isArray(i.style.colors)&&i.style.colors[o]?i.style.colors[o]:"#a8a8a8",dataLabelsConfig:n({textAnchor:c.textAnchor,dropShadow:{enabled:!1}},i),offsetCorrection:!1});}})),a}},{key:"createPaths",value:function(t,e){var i=this,a=[],s=[],r=[],n=[];if(t.length){s=[this.graphics.move(e.x,e.y)],n=[this.graphics.move(e.x,e.y)];var o=this.graphics.move(t[0].x,t[0].y),l=this.graphics.move(t[0].x,t[0].y);t.forEach((function(e,a){o+=i.graphics.line(e.x,e.y),l+=i.graphics.line(e.x,e.y),a===t.length-1&&(o+="Z",l+="Z");})),a.push(o),r.push(l);}return {linePathsFrom:s,linePathsTo:a,areaPathsFrom:n,areaPathsTo:r}}},{key:"getTextPos",value:function(t,e){var i="middle",a=t.x,s=t.y;return Math.abs(t.x)>=10?t.x>0?(i="start",a+=10):t.x<0&&(i="end",a-=10):i="middle",Math.abs(t.y)>=e-10&&(t.y<0?s-=10:t.y>0&&(s+=10)),{textAnchor:i,newX:a,newY:s}}},{key:"getPreviousPath",value:function(t){for(var e=this.w,i=null,a=0;a<e.globals.previousPaths.length;a++){var s=e.globals.previousPaths[a];s.paths.length>0&&parseInt(s.realIndex,10)===parseInt(t,10)&&void 0!==e.globals.previousPaths[a].paths[0]&&(i=e.globals.previousPaths[a].paths[0].d);}return i}},{key:"getDataPointsPos",value:function(t,e){var i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:this.dataPointsLen;t=t||[],e=e||[];for(var a=[],s=0;s<i;s++){var r={};r.x=t[s]*Math.sin(e[s]),r.y=-t[s]*Math.cos(e[s]),a.push(r);}return a}},{key:"getPolygonPos",value:function(t){for(var e=[],i=2*Math.PI/this.dataPointsLen,a=0;a<this.dataPointsLen;a++){var s={};s.x=t*Math.sin(a*i),s.y=-t*Math.cos(a*i),e.push(s);}return e}}]),t}(),Ct=function(t){o(s,t);var i=d(s);function s(t){var a;e(this,s),(a=i.call(this,t)).ctx=t,a.w=t.w,a.animBeginArr=[0],a.animDur=0;var r=a.w;return a.startAngle=r.config.plotOptions.radialBar.startAngle,a.endAngle=r.config.plotOptions.radialBar.endAngle,a.totalAngle=Math.abs(r.config.plotOptions.radialBar.endAngle-r.config.plotOptions.radialBar.startAngle),a.trackStartAngle=r.config.plotOptions.radialBar.track.startAngle,a.trackEndAngle=r.config.plotOptions.radialBar.track.endAngle,a.radialDataLabels=r.config.plotOptions.radialBar.dataLabels,a.trackStartAngle||(a.trackStartAngle=a.startAngle),a.trackEndAngle||(a.trackEndAngle=a.endAngle),360===a.endAngle&&(a.endAngle=359.99),a.margin=parseInt(r.config.plotOptions.radialBar.track.margin,10),a}return a(s,[{key:"draw",value:function(t){var e=this.w,i=new b(this.ctx),a=i.group({class:"apexcharts-radialbar"});if(e.globals.noData)return a;var s=i.group(),r=this.defaultSize/2,n=e.globals.gridWidth/2,o=this.defaultSize/2.05;e.config.chart.sparkline.enabled||(o=o-e.config.stroke.width-e.config.chart.dropShadow.blur);var l=e.globals.fill.colors;if(e.config.plotOptions.radialBar.track.show){var h=this.drawTracks({size:o,centerX:n,centerY:r,colorArr:l,series:t});s.add(h);}var c=this.drawArcs({size:o,centerX:n,centerY:r,colorArr:l,series:t}),d=360;e.config.plotOptions.radialBar.startAngle<0&&(d=this.totalAngle);var g=(360-d)/360;if(e.globals.radialSize=o-o*g,this.radialDataLabels.value.show){var u=Math.max(this.radialDataLabels.value.offsetY,this.radialDataLabels.name.offsetY);e.globals.radialSize+=u*g;}return s.add(c.g),"front"===e.config.plotOptions.radialBar.hollow.position&&(c.g.add(c.elHollow),c.dataLabels&&c.g.add(c.dataLabels)),a.add(s),a}},{key:"drawTracks",value:function(t){var e=this.w,i=new b(this.ctx),a=i.group({class:"apexcharts-tracks"}),s=new p(this.ctx),r=new P(this.ctx),n=this.getStrokeWidth(t);t.size=t.size-n/2;for(var o=0;o<t.series.length;o++){var l=i.group({class:"apexcharts-radialbar-track apexcharts-track"});a.add(l),l.attr({rel:o+1}),t.size=t.size-n-this.margin;var h=e.config.plotOptions.radialBar.track,c=r.fillPath({seriesNumber:0,size:t.size,fillColors:Array.isArray(h.background)?h.background[o]:h.background,solid:!0}),d=this.trackStartAngle,g=this.trackEndAngle;Math.abs(g)+Math.abs(d)>=360&&(g=360-Math.abs(this.startAngle)-.1);var u=i.drawPath({d:"",stroke:c,strokeWidth:n*parseInt(h.strokeWidth,10)/100,fill:"none",strokeOpacity:h.opacity,classes:"apexcharts-radialbar-area"});if(h.dropShadow.enabled){var f=h.dropShadow;s.dropShadow(u,f);}l.add(u),u.attr("id","apexcharts-radialbarTrack-"+o),this.animatePaths(u,{centerX:t.centerX,centerY:t.centerY,endAngle:g,startAngle:d,size:t.size,i:o,totalItems:2,animBeginArr:0,dur:0,isTrack:!0,easing:e.globals.easing});}return a}},{key:"drawArcs",value:function(t){var e=this.w,i=new b(this.ctx),a=new P(this.ctx),s=new p(this.ctx),r=i.group(),n=this.getStrokeWidth(t);t.size=t.size-n/2;var o=e.config.plotOptions.radialBar.hollow.background,l=t.size-n*t.series.length-this.margin*t.series.length-n*parseInt(e.config.plotOptions.radialBar.track.strokeWidth,10)/100/2,h=l-e.config.plotOptions.radialBar.hollow.margin;void 0!==e.config.plotOptions.radialBar.hollow.image&&(o=this.drawHollowImage(t,r,l,o));var c=this.drawHollow({size:h,centerX:t.centerX,centerY:t.centerY,fill:o||"transparent"});if(e.config.plotOptions.radialBar.hollow.dropShadow.enabled){var d=e.config.plotOptions.radialBar.hollow.dropShadow;s.dropShadow(c,d);}var g=1;!this.radialDataLabels.total.show&&e.globals.series.length>1&&(g=0);var u=null;this.radialDataLabels.show&&(u=this.renderInnerDataLabels(this.radialDataLabels,{hollowSize:l,centerX:t.centerX,centerY:t.centerY,opacity:g})),"back"===e.config.plotOptions.radialBar.hollow.position&&(r.add(c),u&&r.add(u));var x=!1;e.config.plotOptions.radialBar.inverseOrder&&(x=!0);for(var m=x?t.series.length-1:0;x?m>=0:m<t.series.length;x?m--:m++){var v=i.group({class:"apexcharts-series apexcharts-radial-series",seriesName:f.escapeString(e.globals.seriesNames[m])});r.add(v),v.attr({rel:m+1,"data:realIndex":m}),this.ctx.series.addCollapsedClassToSeries(v,m),t.size=t.size-n-this.margin;var y=a.fillPath({seriesNumber:m,size:t.size,value:t.series[m]}),w=this.startAngle,k=void 0,A=f.negToZero(t.series[m]>100?100:t.series[m])/100,S=Math.round(this.totalAngle*A)+this.startAngle,C=void 0;e.globals.dataChanged&&(k=this.startAngle,C=Math.round(this.totalAngle*f.negToZero(e.globals.previousPaths[m])/100)+k),Math.abs(S)+Math.abs(w)>=360&&(S-=.01),Math.abs(C)+Math.abs(k)>=360&&(C-=.01);var L=S-w,T=Array.isArray(e.config.stroke.dashArray)?e.config.stroke.dashArray[m]:e.config.stroke.dashArray,z=i.drawPath({d:"",stroke:y,strokeWidth:n,fill:"none",fillOpacity:e.config.fill.opacity,classes:"apexcharts-radialbar-area apexcharts-radialbar-slice-"+m,strokeDashArray:T});if(b.setAttrs(z.node,{"data:angle":L,"data:value":t.series[m]}),e.config.chart.dropShadow.enabled){var I=e.config.chart.dropShadow;s.dropShadow(z,I,m);}this.addListeners(z,this.radialDataLabels),v.add(z),z.attr({index:0,j:m});var M=0;!this.initialAnim||e.globals.resized||e.globals.dataChanged||(M=(S-w)/360*e.config.chart.animations.speed,this.animDur=M/(1.2*t.series.length)+this.animDur,this.animBeginArr.push(this.animDur)),e.globals.dataChanged&&(M=(S-w)/360*e.config.chart.animations.dynamicAnimation.speed,this.animDur=M/(1.2*t.series.length)+this.animDur,this.animBeginArr.push(this.animDur)),this.animatePaths(z,{centerX:t.centerX,centerY:t.centerY,endAngle:S,startAngle:w,prevEndAngle:C,prevStartAngle:k,size:t.size,i:m,totalItems:2,animBeginArr:this.animBeginArr,dur:M,shouldSetPrevPaths:!0,easing:e.globals.easing});}return {g:r,elHollow:c,dataLabels:u}}},{key:"drawHollow",value:function(t){var e=new b(this.ctx).drawCircle(2*t.size);return e.attr({class:"apexcharts-radialbar-hollow",cx:t.centerX,cy:t.centerY,r:t.size,fill:t.fill}),e}},{key:"drawHollowImage",value:function(t,e,i,a){var s=this.w,r=new P(this.ctx),n=f.randomId(),o=s.config.plotOptions.radialBar.hollow.image;if(s.config.plotOptions.radialBar.hollow.imageClipped)r.clippedImgArea({width:i,height:i,image:o,patternID:"pattern".concat(s.globals.cuid).concat(n)}),a="url(#pattern".concat(s.globals.cuid).concat(n,")");else {var l=s.config.plotOptions.radialBar.hollow.imageWidth,h=s.config.plotOptions.radialBar.hollow.imageHeight;if(void 0===l&&void 0===h){var c=s.globals.dom.Paper.image(o).loaded((function(e){this.move(t.centerX-e.width/2+s.config.plotOptions.radialBar.hollow.imageOffsetX,t.centerY-e.height/2+s.config.plotOptions.radialBar.hollow.imageOffsetY);}));e.add(c);}else {var d=s.globals.dom.Paper.image(o).loaded((function(e){this.move(t.centerX-l/2+s.config.plotOptions.radialBar.hollow.imageOffsetX,t.centerY-h/2+s.config.plotOptions.radialBar.hollow.imageOffsetY),this.size(l,h);}));e.add(d);}}return a}},{key:"getStrokeWidth",value:function(t){var e=this.w;return t.size*(100-parseInt(e.config.plotOptions.radialBar.hollow.size,10))/100/(t.series.length+1)-this.margin}}]),s}(At),Lt=function(){function t(i){e(this,t),this.w=i.w,this.lineCtx=i;}return a(t,[{key:"sameValueSeriesFix",value:function(t,e){var i=this.w;if("line"===i.config.chart.type&&("gradient"===i.config.fill.type||"gradient"===i.config.fill.type[t])&&new w(this.lineCtx.ctx,i).seriesHaveSameValues(t)){var a=e[t].slice();a[a.length-1]=a[a.length-1]+1e-6,e[t]=a;}return e}},{key:"calculatePoints",value:function(t){var e=t.series,i=t.realIndex,a=t.x,s=t.y,r=t.i,n=t.j,o=t.prevY,l=this.w,h=[],c=[];if(0===n){var d=this.lineCtx.categoryAxisCorrection+l.config.markers.offsetX;l.globals.isXNumeric&&(d=(l.globals.seriesX[i][0]-l.globals.minX)/this.lineCtx.xRatio+l.config.markers.offsetX),h.push(d),c.push(f.isNumber(e[r][0])?o+l.config.markers.offsetY:null),h.push(a+l.config.markers.offsetX),c.push(f.isNumber(e[r][n+1])?s+l.config.markers.offsetY:null);}else h.push(a+l.config.markers.offsetX),c.push(f.isNumber(e[r][n+1])?s+l.config.markers.offsetY:null);return {x:h,y:c}}},{key:"checkPreviousPaths",value:function(t){for(var e=t.pathFromLine,i=t.pathFromArea,a=t.realIndex,s=this.w,r=0;r<s.globals.previousPaths.length;r++){var n=s.globals.previousPaths[r];("line"===n.type||"area"===n.type)&&n.paths.length>0&&parseInt(n.realIndex,10)===parseInt(a,10)&&("line"===n.type?(this.lineCtx.appendPathFrom=!1,e=s.globals.previousPaths[r].paths[0].d):"area"===n.type&&(this.lineCtx.appendPathFrom=!1,i=s.globals.previousPaths[r].paths[0].d,s.config.stroke.show&&s.globals.previousPaths[r].paths[1]&&(e=s.globals.previousPaths[r].paths[1].d)));}return {pathFromLine:e,pathFromArea:i}}},{key:"determineFirstPrevY",value:function(t){var e=t.i,i=t.series,a=t.prevY,s=t.lineYPosition,r=this.w;if(void 0!==i[e][0])a=(s=r.config.chart.stacked&&e>0?this.lineCtx.prevSeriesY[e-1][0]:this.lineCtx.zeroY)-i[e][0]/this.lineCtx.yRatio[this.lineCtx.yaxisIndex]+2*(this.lineCtx.isReversed?i[e][0]/this.lineCtx.yRatio[this.lineCtx.yaxisIndex]:0);else if(r.config.chart.stacked&&e>0&&void 0===i[e][0])for(var n=e-1;n>=0;n--)if(null!==i[n][0]&&void 0!==i[n][0]){a=s=this.lineCtx.prevSeriesY[n][0];break}return {prevY:a,lineYPosition:s}}}]),t}(),Pt=function(){function t(i,a,s){e(this,t),this.ctx=i,this.w=i.w,this.xyRatios=a,this.pointsChart=!("bubble"!==this.w.config.chart.type&&"scatter"!==this.w.config.chart.type)||s,this.scatter=new z(this.ctx),this.noNegatives=this.w.globals.minX===Number.MAX_VALUE,this.lineHelpers=new Lt(this),this.markers=new T(this.ctx),this.prevSeriesY=[],this.categoryAxisCorrection=0,this.yaxisIndex=0;}return a(t,[{key:"draw",value:function(t,e,i){var a=this.w,s=new b(this.ctx),r=a.globals.comboCharts?e:a.config.chart.type,n=s.group({class:"apexcharts-".concat(r,"-series apexcharts-plot-series")}),o=new w(this.ctx,a);this.yRatio=this.xyRatios.yRatio,this.zRatio=this.xyRatios.zRatio,this.xRatio=this.xyRatios.xRatio,this.baseLineY=this.xyRatios.baseLineY,t=o.getLogSeries(t),this.yRatio=o.getLogYRatios(this.yRatio);for(var l=[],h=0;h<t.length;h++){t=this.lineHelpers.sameValueSeriesFix(h,t);var c=a.globals.comboCharts?i[h]:h;this._initSerieVariables(t,h,c);var d=[],g=[],u=a.globals.padHorizontal+this.categoryAxisCorrection;this.ctx.series.addCollapsedClassToSeries(this.elSeries,c),a.globals.isXNumeric&&a.globals.seriesX.length>0&&(u=(a.globals.seriesX[c][0]-a.globals.minX)/this.xRatio),g.push(u);var f,p=u,x=p,m=this.zeroY;m=this.lineHelpers.determineFirstPrevY({i:h,series:t,prevY:m,lineYPosition:0}).prevY,d.push(m),f=m;var v=this._calculatePathsFrom({series:t,i:h,realIndex:c,prevX:x,prevY:m}),y=this._iterateOverDataPoints({series:t,realIndex:c,i:h,x:u,y:1,pX:p,pY:f,pathsFrom:v,linePaths:[],areaPaths:[],seriesIndex:i,lineYPosition:0,xArrj:g,yArrj:d});this._handlePaths({type:r,realIndex:c,i:h,paths:y}),this.elSeries.add(this.elPointsMain),this.elSeries.add(this.elDataLabelsWrap),l.push(this.elSeries);}if(a.config.chart.stacked)for(var k=l.length;k>0;k--)n.add(l[k-1]);else for(var A=0;A<l.length;A++)n.add(l[A]);return n}},{key:"_initSerieVariables",value:function(t,e,i){var a=this.w,s=new b(this.ctx);this.xDivision=a.globals.gridWidth/(a.globals.dataPoints-("on"===a.config.xaxis.tickPlacement?1:0)),this.strokeWidth=Array.isArray(a.config.stroke.width)?a.config.stroke.width[i]:a.config.stroke.width,this.yRatio.length>1&&(this.yaxisIndex=i),this.isReversed=a.config.yaxis[this.yaxisIndex]&&a.config.yaxis[this.yaxisIndex].reversed,this.zeroY=a.globals.gridHeight-this.baseLineY[this.yaxisIndex]-(this.isReversed?a.globals.gridHeight:0)+(this.isReversed?2*this.baseLineY[this.yaxisIndex]:0),this.areaBottomY=this.zeroY,(this.zeroY>a.globals.gridHeight||"end"===a.config.plotOptions.area.fillTo)&&(this.areaBottomY=a.globals.gridHeight),this.categoryAxisCorrection=this.xDivision/2,this.elSeries=s.group({class:"apexcharts-series",seriesName:f.escapeString(a.globals.seriesNames[i])}),this.elPointsMain=s.group({class:"apexcharts-series-markers-wrap","data:realIndex":i}),this.elDataLabelsWrap=s.group({class:"apexcharts-datalabels","data:realIndex":i});var r=t[e].length===a.globals.dataPoints;this.elSeries.attr({"data:longestSeries":r,rel:e+1,"data:realIndex":i}),this.appendPathFrom=!0;}},{key:"_calculatePathsFrom",value:function(t){var e,i,a,s,r=t.series,n=t.i,o=t.realIndex,l=t.prevX,h=t.prevY,c=this.w,d=new b(this.ctx);if(null===r[n][0]){for(var g=0;g<r[n].length;g++)if(null!==r[n][g]){l=this.xDivision*g,h=this.zeroY-r[n][g]/this.yRatio[this.yaxisIndex],e=d.move(l,h),i=d.move(l,this.areaBottomY);break}}else e=d.move(l,h),i=d.move(l,this.areaBottomY)+d.line(l,h);if(a=d.move(-1,this.zeroY)+d.line(-1,this.zeroY),s=d.move(-1,this.zeroY)+d.line(-1,this.zeroY),c.globals.previousPaths.length>0){var u=this.lineHelpers.checkPreviousPaths({pathFromLine:a,pathFromArea:s,realIndex:o});a=u.pathFromLine,s=u.pathFromArea;}return {prevX:l,prevY:h,linePath:e,areaPath:i,pathFromLine:a,pathFromArea:s}}},{key:"_handlePaths",value:function(t){var e=t.type,i=t.realIndex,a=t.i,s=t.paths,r=this.w,o=new b(this.ctx),l=new P(this.ctx);this.prevSeriesY.push(s.yArrj),r.globals.seriesXvalues[i]=s.xArrj,r.globals.seriesYvalues[i]=s.yArrj,this.pointsChart||r.globals.delayedElements.push({el:this.elPointsMain.node,index:i});var h={i:a,realIndex:i,animationDelay:a,initialSpeed:r.config.chart.animations.speed,dataChangeSpeed:r.config.chart.animations.dynamicAnimation.speed,className:"apexcharts-".concat(e)};if("area"===e)for(var c=l.fillPath({seriesNumber:i}),d=0;d<s.areaPaths.length;d++){var g=o.renderPaths(n(n({},h),{},{pathFrom:s.pathFromArea,pathTo:s.areaPaths[d],stroke:"none",strokeWidth:0,strokeLineCap:null,fill:c}));this.elSeries.add(g);}if(r.config.stroke.show&&!this.pointsChart){var u=null;u="line"===e?l.fillPath({seriesNumber:i,i:a}):r.globals.stroke.colors[i];for(var f=0;f<s.linePaths.length;f++){var p=o.renderPaths(n(n({},h),{},{pathFrom:s.pathFromLine,pathTo:s.linePaths[f],stroke:u,strokeWidth:this.strokeWidth,strokeLineCap:r.config.stroke.lineCap,fill:"none"}));this.elSeries.add(p);}}}},{key:"_iterateOverDataPoints",value:function(t){for(var e=t.series,i=t.realIndex,a=t.i,s=t.x,r=t.y,n=t.pX,o=t.pY,l=t.pathsFrom,h=t.linePaths,c=t.areaPaths,d=t.seriesIndex,g=t.lineYPosition,u=t.xArrj,p=t.yArrj,x=this.w,m=new b(this.ctx),v=this.yRatio,y=l.prevY,w=l.linePath,k=l.areaPath,A=l.pathFromLine,S=l.pathFromArea,C=f.isNumber(x.globals.minYArr[i])?x.globals.minYArr[i]:x.globals.minY,L=x.globals.dataPoints>1?x.globals.dataPoints-1:x.globals.dataPoints,P=0;P<L;P++){var T=void 0===e[a][P+1]||null===e[a][P+1];if(x.globals.isXNumeric){var z=x.globals.seriesX[i][P+1];void 0===x.globals.seriesX[i][P+1]&&(z=x.globals.seriesX[i][L-1]),s=(z-x.globals.minX)/this.xRatio;}else s+=this.xDivision;if(x.config.chart.stacked)if(a>0&&x.globals.collapsedSeries.length<x.config.series.length-1){g=this.prevSeriesY[function(t){for(var e=t,i=0;i<x.globals.series.length;i++)if(x.globals.collapsedSeriesIndices.indexOf(t)>-1){e--;break}return e>=0?e:0}(a-1)][P+1];}else g=this.zeroY;else g=this.zeroY;r=T?g-C/v[this.yaxisIndex]+2*(this.isReversed?C/v[this.yaxisIndex]:0):g-e[a][P+1]/v[this.yaxisIndex]+2*(this.isReversed?e[a][P+1]/v[this.yaxisIndex]:0),u.push(s),p.push(r);var I=this.lineHelpers.calculatePoints({series:e,x:s,y:r,realIndex:i,i:a,j:P,prevY:y}),M=this._createPaths({series:e,i:a,realIndex:i,j:P,x:s,y:r,pX:n,pY:o,linePath:w,areaPath:k,linePaths:h,areaPaths:c,seriesIndex:d});c=M.areaPaths,h=M.linePaths,n=M.pX,o=M.pY,k=M.areaPath,w=M.linePath,this.appendPathFrom&&(A+=m.line(s,this.zeroY),S+=m.line(s,this.zeroY)),this.handleNullDataPoints(e,I,a,P,i),this._handleMarkersAndLabels({pointsPos:I,series:e,x:s,y:r,prevY:y,i:a,j:P,realIndex:i});}return {yArrj:p,xArrj:u,pathFromArea:S,areaPaths:c,pathFromLine:A,linePaths:h}}},{key:"_handleMarkersAndLabels",value:function(t){var e=t.pointsPos,i=(t.series,t.x,t.y,t.prevY,t.i),a=t.j,s=t.realIndex,r=this.w,n=new I(this.ctx);if(this.pointsChart)this.scatter.draw(this.elSeries,a,{realIndex:s,pointsPos:e,zRatio:this.zRatio,elParent:this.elPointsMain});else {r.globals.series[i].length>1&&this.elPointsMain.node.classList.add("apexcharts-element-hidden");var o=this.markers.plotChartMarkers(e,s,a+1);null!==o&&this.elPointsMain.add(o);}var l=n.drawDataLabel(e,s,a+1,null);null!==l&&this.elDataLabelsWrap.add(l);}},{key:"_createPaths",value:function(t){var e=t.series,i=t.i,a=t.realIndex,s=t.j,r=t.x,n=t.y,o=t.pX,l=t.pY,h=t.linePath,c=t.areaPath,d=t.linePaths,g=t.areaPaths,u=t.seriesIndex,f=this.w,p=new b(this.ctx),x=f.config.stroke.curve,m=this.areaBottomY;if(Array.isArray(f.config.stroke.curve)&&(x=Array.isArray(u)?f.config.stroke.curve[u[i]]:f.config.stroke.curve[i]),"smooth"===x){var v=.35*(r-o);f.globals.hasNullValues?(null!==e[i][s]&&(null!==e[i][s+1]?(h=p.move(o,l)+p.curve(o+v,l,r-v,n,r+1,n),c=p.move(o+1,l)+p.curve(o+v,l,r-v,n,r+1,n)+p.line(r,m)+p.line(o,m)+"z"):(h=p.move(o,l),c=p.move(o,l)+"z")),d.push(h),g.push(c)):(h+=p.curve(o+v,l,r-v,n,r,n),c+=p.curve(o+v,l,r-v,n,r,n)),o=r,l=n,s===e[i].length-2&&(c=c+p.curve(o,l,r,n,r,m)+p.move(r,n)+"z",f.globals.hasNullValues||(d.push(h),g.push(c)));}else {if(null===e[i][s+1]){h+=p.move(r,n);var y=f.globals.isXNumeric?(f.globals.seriesX[a][s]-f.globals.minX)/this.xRatio:r-this.xDivision;c=c+p.line(y,m)+p.move(r,n)+"z";}null===e[i][s]&&(h+=p.move(r,n),c+=p.move(r,m)),"stepline"===x?(h=h+p.line(r,null,"H")+p.line(null,n,"V"),c=c+p.line(r,null,"H")+p.line(null,n,"V")):"straight"===x&&(h+=p.line(r,n),c+=p.line(r,n)),s===e[i].length-2&&(c=c+p.line(r,m)+p.move(r,n)+"z",d.push(h),g.push(c));}return {linePaths:d,areaPaths:g,pX:o,pY:l,linePath:h,areaPath:c}}},{key:"handleNullDataPoints",value:function(t,e,i,a,s){var r=this.w;if(null===t[i][a]&&r.config.markers.showNullDataPoints||1===t[i].length){var n=this.markers.plotChartMarkers(e,s,a+1,this.strokeWidth-r.config.markers.strokeWidth/2,!0);null!==n&&this.elPointsMain.add(n);}}}]),t}();window.TreemapSquared={},window.TreemapSquared.generate=function(){function t(e,i,a,r){this.xoffset=e,this.yoffset=i,this.height=r,this.width=a,this.shortestEdge=function(){return Math.min(this.height,this.width)},this.getCoordinates=function(t){var e,i=[],a=this.xoffset,r=this.yoffset,n=s(t)/this.height,o=s(t)/this.width;if(this.width>=this.height)for(e=0;e<t.length;e++)i.push([a,r,a+n,r+t[e]/n]),r+=t[e]/n;else for(e=0;e<t.length;e++)i.push([a,r,a+t[e]/o,r+o]),a+=t[e]/o;return i},this.cutArea=function(e){var i;if(this.width>=this.height){var a=e/this.height,s=this.width-a;i=new t(this.xoffset+a,this.yoffset,s,this.height);}else {var r=e/this.width,n=this.height-r;i=new t(this.xoffset,this.yoffset+r,this.width,n);}return i};}function e(e,a,r,n,o){return n=void 0===n?0:n,o=void 0===o?0:o,function(t){var e,i,a=[];for(e=0;e<t.length;e++)for(i=0;i<t[e].length;i++)a.push(t[e][i]);return a}(function t(e,a,r,n){var o,l,h;if(0!==e.length)return o=r.shortestEdge(),l=e[0],function(t,e,a){var s;if(0===t.length)return !0;(s=t.slice()).push(e);var r=i(t,a),n=i(s,a);return r>=n}(a,l,o)?(a.push(l),t(e.slice(1),a,r,n)):(h=r.cutArea(s(a),n),n.push(r.getCoordinates(a)),t(e,[],h,n)),n;n.push(r.getCoordinates(a));}(function(t,e){var i,a=[],r=e/s(t);for(i=0;i<t.length;i++)a[i]=t[i]*r;return a}(e,a*r),[],new t(n,o,a,r),[]))}function i(t,e){var i=Math.min.apply(Math,t),a=Math.max.apply(Math,t),r=s(t);return Math.max(Math.pow(e,2)*a/Math.pow(r,2),Math.pow(r,2)/(Math.pow(e,2)*i))}function a(t){return t&&t.constructor===Array}function s(t){var e,i=0;for(e=0;e<t.length;e++)i+=t[e];return i}function r(t){var e,i=0;if(a(t[0]))for(e=0;e<t.length;e++)i+=r(t[e]);else i=s(t);return i}return function t(i,s,n,o,l){o=void 0===o?0:o,l=void 0===l?0:l;var h,c,d=[],g=[];if(a(i[0])){for(c=0;c<i.length;c++)d[c]=r(i[c]);for(h=e(d,s,n,o,l),c=0;c<i.length;c++)g.push(t(i[c],h[c][2]-h[c][0],h[c][3]-h[c][1],h[c][0],h[c][1]));}else g=e(i,s,n,o,l);return g}}();var Tt,zt,It=function(){function t(i,a){e(this,t),this.ctx=i,this.w=i.w,this.strokeWidth=this.w.config.stroke.width,this.helpers=new yt(i),this.dynamicAnim=this.w.config.chart.animations.dynamicAnimation,this.labels=[];}return a(t,[{key:"draw",value:function(t){var e=this,i=this.w,a=new b(this.ctx),s=new P(this.ctx),r=a.group({class:"apexcharts-treemap"}),n=[];return t.forEach((function(t){var e=t.map((function(t){return Math.abs(t)}));n.push(e);})),this.negRange=this.helpers.checkColorRange(),i.config.series.forEach((function(t,i){t.data.forEach((function(t){Array.isArray(e.labels[i])||(e.labels[i]=[]),e.labels[i].push(t.x);}));})),window.TreemapSquared.generate(n,i.globals.gridWidth,i.globals.gridHeight).forEach((function(n,o){var l=a.group({class:"apexcharts-series apexcharts-treemap-series",seriesName:f.escapeString(i.globals.seriesNames[o]),rel:o+1,"data:realIndex":o});if(i.config.chart.dropShadow.enabled){var h=i.config.chart.dropShadow;new p(e.ctx).dropShadow(r,h,o);}var c=a.group({class:"apexcharts-data-labels"});n.forEach((function(r,n){var h=r[0],c=r[1],d=r[2],g=r[3],u=a.drawRect(h,c,d-h,g-c,0,"#fff",1,e.strokeWidth,i.config.plotOptions.treemap.useFillColorAsStroke?p:i.globals.stroke.colors[o]);u.attr({cx:h,cy:c,i:o,j:n,width:d-h,height:g-c});var f=e.helpers.getShadeColor(i.config.chart.type,o,n,e.negRange),p=f.color;void 0!==i.config.series[o].data[n]&&i.config.series[o].data[n].fillColor&&(p=i.config.series[o].data[n].fillColor);var x=s.fillPath({color:p,seriesNumber:o,dataPointIndex:n});u.node.classList.add("apexcharts-treemap-rect"),u.attr({fill:x}),e.helpers.addListeners(u);var b={x:h+(d-h)/2,y:c+(g-c)/2,width:0,height:0},m={x:h,y:c,width:d-h,height:g-c};if(i.config.chart.animations.enabled&&!i.globals.dataChanged){var v=1;i.globals.resized||(v=i.config.chart.animations.speed),e.animateTreemap(u,b,m,v);}if(i.globals.dataChanged){var y=1;e.dynamicAnim.enabled&&i.globals.shouldAnimate&&(y=e.dynamicAnim.speed,i.globals.previousPaths[o][n]&&i.globals.previousPaths[o][n].rect&&(b=i.globals.previousPaths[o][n].rect),e.animateTreemap(u,b,m,y));}var w=e.getFontSize(r),k=i.config.dataLabels.formatter(e.labels[o][n],{value:i.globals.series[o][n],seriesIndex:o,dataPointIndex:n,w:i}),A=e.helpers.calculateDataLabels({text:k,x:(h+d)/2,y:(c+g)/2+e.strokeWidth/2+w/3,i:o,j:n,colorProps:f,fontSize:w,series:t});i.config.dataLabels.enabled&&A&&e.rotateToFitLabel(A,k,h,c,d,g),l.add(u),null!==A&&l.add(A);})),l.add(c),r.add(l);})),r}},{key:"getFontSize",value:function(t){var e=this.w;var i,a,s,r,n=function t(e){var i,a=0;if(Array.isArray(e[0]))for(i=0;i<e.length;i++)a+=t(e[i]);else for(i=0;i<e.length;i++)a+=e[i].length;return a}(this.labels)/function t(e){var i,a=0;if(Array.isArray(e[0]))for(i=0;i<e.length;i++)a+=t(e[i]);else for(i=0;i<e.length;i++)a+=1;return a}(this.labels);return i=t[2]-t[0],a=t[3]-t[1],s=i*a,r=Math.pow(s,.5),Math.min(r/n,parseInt(e.config.dataLabels.style.fontSize,10))}},{key:"rotateToFitLabel",value:function(t,e,i,a,s,r){var n=new b(this.ctx),o=n.getTextRects(e);if(o.width+5>s-i&&o.width<=r-a){var l=n.rotateAroundCenter(t.node);t.node.setAttribute("transform","rotate(-90 ".concat(l.x," ").concat(l.y,")"));}}},{key:"animateTreemap",value:function(t,e,i,a){var s=new x(this.ctx);s.animateRect(t,{x:e.x,y:e.y,width:e.width,height:e.height},{x:i.x,y:i.y,width:i.width,height:i.height},a,(function(){s.animationCompleted(t);}));}}]),t}(),Mt=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w,this.timeScaleArray=[],this.utc=this.w.config.xaxis.labels.datetimeUTC;}return a(t,[{key:"calculateTimeScaleTicks",value:function(t,e){var i=this,a=this.w;if(a.globals.allSeriesCollapsed)return a.globals.labels=[],a.globals.timescaleLabels=[],[];var s=new y(this.ctx),r=(e-t)/864e5;this.determineInterval(r),a.globals.disableZoomIn=!1,a.globals.disableZoomOut=!1,r<.005?a.globals.disableZoomIn=!0:r>5e4&&(a.globals.disableZoomOut=!0);var o=s.getTimeUnitsfromTimestamp(t,e,this.utc),l=a.globals.gridWidth/r,h=l/24,c=h/60,d=Math.floor(24*r),g=Math.floor(24*r*60),u=Math.floor(r),f=Math.floor(r/30),p=Math.floor(r/365),x={minMinute:o.minMinute,minHour:o.minHour,minDate:o.minDate,minMonth:o.minMonth,minYear:o.minYear},b={firstVal:x,currentMinute:x.minMinute,currentHour:x.minHour,currentMonthDate:x.minDate,currentDate:x.minDate,currentMonth:x.minMonth,currentYear:x.minYear,daysWidthOnXAxis:l,hoursWidthOnXAxis:h,minutesWidthOnXAxis:c,numberOfMinutes:g,numberOfHours:d,numberOfDays:u,numberOfMonths:f,numberOfYears:p};switch(this.tickInterval){case"years":this.generateYearScale(b);break;case"months":case"half_year":this.generateMonthScale(b);break;case"months_days":case"months_fortnight":case"days":case"week_days":this.generateDayScale(b);break;case"hours":this.generateHourScale(b);break;case"minutes":this.generateMinuteScale(b);}var m=this.timeScaleArray.map((function(t){var e={position:t.position,unit:t.unit,year:t.year,day:t.day?t.day:1,hour:t.hour?t.hour:0,month:t.month+1};return "month"===t.unit?n(n({},e),{},{day:1,value:t.value+1}):"day"===t.unit||"hour"===t.unit?n(n({},e),{},{value:t.value}):"minute"===t.unit?n(n({},e),{},{value:t.value,minute:t.value}):t}));return m.filter((function(t){var e=1,s=Math.ceil(a.globals.gridWidth/120),r=t.value;void 0!==a.config.xaxis.tickAmount&&(s=a.config.xaxis.tickAmount),m.length>s&&(e=Math.floor(m.length/s));var n=!1,o=!1;switch(i.tickInterval){case"years":"year"===t.unit&&(n=!0);break;case"half_year":e=7,"year"===t.unit&&(n=!0);break;case"months":e=1,"year"===t.unit&&(n=!0);break;case"months_fortnight":e=15,"year"!==t.unit&&"month"!==t.unit||(n=!0),30===r&&(o=!0);break;case"months_days":e=10,"month"===t.unit&&(n=!0),30===r&&(o=!0);break;case"week_days":e=8,"month"===t.unit&&(n=!0);break;case"days":e=1,"month"===t.unit&&(n=!0);break;case"hours":"day"===t.unit&&(n=!0);break;case"minutes":r%5!=0&&(o=!0);}if("minutes"===i.tickInterval||"hours"===i.tickInterval){if(!o)return !0}else if((r%e==0||n)&&!o)return !0}))}},{key:"recalcDimensionsBasedOnFormat",value:function(t,e){var i=this.w,a=this.formatDates(t),s=this.removeOverlappingTS(a);i.globals.timescaleLabels=s.slice(),new nt(this.ctx).plotCoords();}},{key:"determineInterval",value:function(t){switch(!0){case t>1825:this.tickInterval="years";break;case t>800&&t<=1825:this.tickInterval="half_year";break;case t>180&&t<=800:this.tickInterval="months";break;case t>90&&t<=180:this.tickInterval="months_fortnight";break;case t>60&&t<=90:this.tickInterval="months_days";break;case t>30&&t<=60:this.tickInterval="week_days";break;case t>2&&t<=30:this.tickInterval="days";break;case t>.1&&t<=2:this.tickInterval="hours";break;case t<.1:this.tickInterval="minutes";break;default:this.tickInterval="days";}}},{key:"generateYearScale",value:function(t){var e=t.firstVal,i=t.currentMonth,a=t.currentYear,s=t.daysWidthOnXAxis,r=t.numberOfYears,n=e.minYear,o=0,l=new y(this.ctx);if(e.minDate>1||e.minMonth>0){var h=l.determineRemainingDaysOfYear(e.minYear,e.minMonth,e.minDate);o=(l.determineDaysOfYear(e.minYear)-h+1)*s,n=e.minYear+1,this.timeScaleArray.push({position:o,value:n,unit:"year",year:n,month:f.monthMod(i+1)});}else 1===e.minDate&&0===e.minMonth&&this.timeScaleArray.push({position:o,value:n,unit:"year",year:a,month:f.monthMod(i+1)});for(var c=n,d=o,g=0;g<r;g++)c++,d=l.determineDaysOfYear(c-1)*s+d,this.timeScaleArray.push({position:d,value:c,unit:"year",year:c,month:1});}},{key:"generateMonthScale",value:function(t){var e=t.firstVal,i=t.currentMonthDate,a=t.currentMonth,s=t.currentYear,r=t.daysWidthOnXAxis,n=t.numberOfMonths,o=a,l=0,h=new y(this.ctx),c="month",d=0;if(e.minDate>1){l=(h.determineDaysOfMonths(a+1,e.minYear)-i+1)*r,o=f.monthMod(a+1);var g=s+d,u=f.monthMod(o),p=o;0===o&&(c="year",p=g,u=1,g+=d+=1),this.timeScaleArray.push({position:l,value:p,unit:c,year:g,month:u});}else this.timeScaleArray.push({position:l,value:o,unit:c,year:s,month:f.monthMod(a)});for(var x=o+1,b=l,m=0,v=1;m<n;m++,v++){0===(x=f.monthMod(x))?(c="year",d+=1):c="month";var w=this._getYear(s,x,d);b=h.determineDaysOfMonths(x,w)*r+b;var k=0===x?w:x;this.timeScaleArray.push({position:b,value:k,unit:c,year:w,month:0===x?1:x}),x++;}}},{key:"generateDayScale",value:function(t){var e=t.firstVal,i=t.currentMonth,a=t.currentYear,s=t.hoursWidthOnXAxis,r=t.numberOfDays,n=new y(this.ctx),o="day",l=e.minDate+1,h=l,c=function(t,e,i){return t>n.determineDaysOfMonths(e+1,i)?(h=1,o="month",g=e+=1,e):e},d=(24-e.minHour)*s,g=l,u=c(h,i,a);0===e.minHour&&1===e.minDate&&(d=0,g=f.monthMod(e.minMonth),o="month",h=e.minDate,r++),this.timeScaleArray.push({position:d,value:g,unit:o,year:this._getYear(a,u,0),month:f.monthMod(u),day:h});for(var p=d,x=0;x<r;x++){o="day",u=c(h+=1,u,this._getYear(a,u,0));var b=this._getYear(a,u,0);p=24*s+p;var m=1===h?f.monthMod(u):h;this.timeScaleArray.push({position:p,value:m,unit:o,year:b,month:f.monthMod(u),day:m});}}},{key:"generateHourScale",value:function(t){var e=t.firstVal,i=t.currentDate,a=t.currentMonth,s=t.currentYear,r=t.minutesWidthOnXAxis,n=t.numberOfHours,o=new y(this.ctx),l="hour",h=function(t,e){return t>o.determineDaysOfMonths(e+1,s)&&(x=1,e+=1),{month:e,date:x}},c=function(t,e){return t>o.determineDaysOfMonths(e+1,s)?e+=1:e},d=60-e.minMinute,g=d*r,u=e.minHour+1,p=u+1;60===d&&(g=0,p=(u=e.minHour)+1);var x=i,b=c(x,a);this.timeScaleArray.push({position:g,value:u,unit:l,day:x,hour:p,year:s,month:f.monthMod(b)});for(var m=g,v=0;v<n;v++){if(l="hour",p>=24)p=0,l="day",b=h(x+=1,b).month,b=c(x,b);var w=this._getYear(s,b,0);m=0===p&&0===v?d*r:60*r+m;var k=0===p?x:p;this.timeScaleArray.push({position:m,value:k,unit:l,hour:p,day:x,year:w,month:f.monthMod(b)}),p++;}}},{key:"generateMinuteScale",value:function(t){var e=t.firstVal,i=t.currentMinute,a=t.currentHour,s=t.currentDate,r=t.currentMonth,n=t.currentYear,o=t.minutesWidthOnXAxis,l=t.numberOfMinutes,h=o-(i-e.minMinute),c=e.minMinute+1,d=c+1,g=s,u=r,p=n,x=a;this.timeScaleArray.push({position:h,value:c,unit:"minute",day:g,hour:x,minute:d,year:p,month:f.monthMod(u)});for(var b=h,m=0;m<l;m++)d>=60&&(d=0,24===(x+=1)&&(x=0)),b=o+b,this.timeScaleArray.push({position:b,value:d,unit:"minute",hour:x,minute:d,day:g,year:this._getYear(n,u,0),month:f.monthMod(u)}),d++;}},{key:"createRawDateString",value:function(t,e){var i=t.year;return i+="-"+("0"+t.month.toString()).slice(-2),"day"===t.unit?i+="day"===t.unit?"-"+("0"+e).slice(-2):"-01":i+="-"+("0"+(t.day?t.day:"1")).slice(-2),"hour"===t.unit?i+="hour"===t.unit?"T"+("0"+e).slice(-2):"T00":i+="T"+("0"+(t.hour?t.hour:"0")).slice(-2),i+="minute"===t.unit?":"+("0"+e).slice(-2)+":00":":00:00",this.utc&&(i+=".000Z"),i}},{key:"formatDates",value:function(t){var e=this,i=this.w;return t.map((function(t){var a=t.value.toString(),s=new y(e.ctx),r=e.createRawDateString(t,a),n=s.getDate(s.parseDate(r));if(e.utc||(n=s.getDate(s.parseDateWithTimezone(r))),void 0===i.config.xaxis.labels.format){var o="dd MMM",l=i.config.xaxis.labels.datetimeFormatter;"year"===t.unit&&(o=l.year),"month"===t.unit&&(o=l.month),"day"===t.unit&&(o=l.day),"hour"===t.unit&&(o=l.hour),"minute"===t.unit&&(o=l.minute),a=s.formatDate(n,o);}else a=s.formatDate(n,i.config.xaxis.labels.format);return {dateString:r,position:t.position,value:a,unit:t.unit,year:t.year,month:t.month}}))}},{key:"removeOverlappingTS",value:function(t){var e,i=this,a=new b(this.ctx),s=!1;t.length>0&&t[0].value&&t.every((function(e){return e.value.length===t[0].value.length}))&&(s=!0,e=a.getTextRects(t[0].value).width);var r=0,n=t.map((function(n,o){if(o>0&&i.w.config.xaxis.labels.hideOverlappingLabels){var l=s?e:a.getTextRects(t[r].value).width,h=t[r].position;return n.position>h+l+10?(r=o,n):null}return n}));return n=n.filter((function(t){return null!==t}))}},{key:"_getYear",value:function(t,e,i){return t+Math.floor(e/12)+i}}]),t}(),Et=function(){function t(i,a){e(this,t),this.ctx=a,this.w=a.w,this.el=i;}return a(t,[{key:"setupElements",value:function(){var t=this.w.globals,e=this.w.config,i=e.chart.type;t.axisCharts=["line","area","bar","rangeBar","candlestick","scatter","bubble","radar","heatmap","treemap"].indexOf(i)>-1,t.xyCharts=["line","area","bar","rangeBar","candlestick","scatter","bubble"].indexOf(i)>-1,t.isBarHorizontal=("bar"===e.chart.type||"rangeBar"===e.chart.type)&&e.plotOptions.bar.horizontal,t.chartClass=".apexcharts"+t.chartID,t.dom.baseEl=this.el,t.dom.elWrap=document.createElement("div"),b.setAttrs(t.dom.elWrap,{id:t.chartClass.substring(1),class:"apexcharts-canvas "+t.chartClass.substring(1)}),this.el.appendChild(t.dom.elWrap),t.dom.Paper=new window.SVG.Doc(t.dom.elWrap),t.dom.Paper.attr({class:"apexcharts-svg","xmlns:data":"ApexChartsNS",transform:"translate(".concat(e.chart.offsetX,", ").concat(e.chart.offsetY,")")}),t.dom.Paper.node.style.background=e.chart.background,this.setSVGDimensions(),t.dom.elGraphical=t.dom.Paper.group().attr({class:"apexcharts-inner apexcharts-graphical"}),t.dom.elAnnotations=t.dom.Paper.group().attr({class:"apexcharts-annotations"}),t.dom.elDefs=t.dom.Paper.defs(),t.dom.elLegendWrap=document.createElement("div"),t.dom.elLegendWrap.classList.add("apexcharts-legend"),t.dom.elWrap.appendChild(t.dom.elLegendWrap),t.dom.Paper.add(t.dom.elGraphical),t.dom.elGraphical.add(t.dom.elDefs);}},{key:"plotChartType",value:function(t,e){var i=this.w,a=i.config,s=i.globals,r={series:[],i:[]},n={series:[],i:[]},o={series:[],i:[]},l={series:[],i:[]},h={series:[],i:[]},c={series:[],i:[]};s.series.map((function(e,d){void 0!==t[d].type?("column"===t[d].type||"bar"===t[d].type?(s.series.length>1&&a.plotOptions.bar.horizontal&&console.warn("Horizontal bars are not supported in a mixed/combo chart. Please turn off `plotOptions.bar.horizontal`"),h.series.push(e),h.i.push(d),i.globals.columnSeries=h.series):"area"===t[d].type?(n.series.push(e),n.i.push(d)):"line"===t[d].type?(r.series.push(e),r.i.push(d)):"scatter"===t[d].type?(o.series.push(e),o.i.push(d)):"bubble"===t[d].type?(l.series.push(e),l.i.push(d)):"candlestick"===t[d].type?(c.series.push(e),c.i.push(d)):console.warn("You have specified an unrecognized chart type. Available types for this propery are line/area/column/bar/scatter/bubble"),s.comboCharts=!0):(r.series.push(e),r.i.push(d));}));var d=new Pt(this.ctx,e),g=new vt(this.ctx,e);this.ctx.pie=new At(this.ctx);var u=new Ct(this.ctx);this.ctx.rangeBar=new F(this.ctx,e);var f=new St(this.ctx),p=[];if(s.comboCharts){if(n.series.length>0&&p.push(d.draw(n.series,"area",n.i)),h.series.length>0)if(i.config.chart.stacked){var x=new mt(this.ctx,e);p.push(x.draw(h.series,h.i));}else {var b=new Y(this.ctx,e);p.push(b.draw(h.series,h.i));}if(r.series.length>0&&p.push(d.draw(r.series,"line",r.i)),c.series.length>0&&p.push(g.draw(c.series,c.i)),o.series.length>0){var m=new Pt(this.ctx,e,!0);p.push(m.draw(o.series,"scatter",o.i));}if(l.series.length>0){var v=new Pt(this.ctx,e,!0);p.push(v.draw(l.series,"bubble",l.i));}}else switch(a.chart.type){case"line":p=d.draw(s.series,"line");break;case"area":p=d.draw(s.series,"area");break;case"bar":if(a.chart.stacked)p=new mt(this.ctx,e).draw(s.series);else p=new Y(this.ctx,e).draw(s.series);break;case"candlestick":p=new vt(this.ctx,e).draw(s.series);break;case"rangeBar":p=this.ctx.rangeBar.draw(s.series);break;case"heatmap":p=new wt(this.ctx,e).draw(s.series);break;case"treemap":p=new It(this.ctx,e).draw(s.series);break;case"pie":case"donut":case"polarArea":p=this.ctx.pie.draw(s.series);break;case"radialBar":p=u.draw(s.series);break;case"radar":p=f.draw(s.series);break;default:p=d.draw(s.series);}return p}},{key:"setSVGDimensions",value:function(){var t=this.w.globals,e=this.w.config;t.svgWidth=e.chart.width,t.svgHeight=e.chart.height;var i=f.getDimensions(this.el),a=e.chart.width.toString().split(/[0-9]+/g).pop();if("%"===a?f.isNumber(i[0])&&(0===i[0].width&&(i=f.getDimensions(this.el.parentNode)),t.svgWidth=i[0]*parseInt(e.chart.width,10)/100):"px"!==a&&""!==a||(t.svgWidth=parseInt(e.chart.width,10)),"auto"!==t.svgHeight&&""!==t.svgHeight)if("%"===e.chart.height.toString().split(/[0-9]+/g).pop()){var s=f.getDimensions(this.el.parentNode);t.svgHeight=s[1]*parseInt(e.chart.height,10)/100;}else t.svgHeight=parseInt(e.chart.height,10);else t.axisCharts?t.svgHeight=t.svgWidth/1.61:t.svgHeight=t.svgWidth/1.2;t.svgWidth<0&&(t.svgWidth=0),t.svgHeight<0&&(t.svgHeight=0),b.setAttrs(t.dom.Paper.node,{width:t.svgWidth,height:t.svgHeight});var r=e.chart.sparkline.enabled?0:t.axisCharts?e.chart.parentHeightOffset:0;t.dom.Paper.node.parentNode.parentNode.style.minHeight=t.svgHeight+r+"px",t.dom.elWrap.style.width=t.svgWidth+"px",t.dom.elWrap.style.height=t.svgHeight+"px";}},{key:"shiftGraphPosition",value:function(){var t=this.w.globals,e=t.translateY,i={transform:"translate("+t.translateX+", "+e+")"};b.setAttrs(t.dom.elGraphical.node,i);}},{key:"resizeNonAxisCharts",value:function(){var t=this.w,e=t.globals,i=0,a=t.config.chart.sparkline.enabled?1:15;a+=t.config.grid.padding.bottom,"top"!==t.config.legend.position&&"bottom"!==t.config.legend.position||!t.config.legend.show||t.config.legend.floating||(i=new lt(this.ctx).legendHelpers.getLegendBBox().clwh+10);var s=t.globals.dom.baseEl.querySelector(".apexcharts-radialbar"),r=2.05*t.globals.radialSize;if(s&&!t.config.chart.sparkline.enabled){var n=f.getBoundingClientRect(s);r=n.bottom;var o=n.bottom-n.top;r=Math.max(2.05*t.globals.radialSize,o);}var l=r+e.translateY+i+a;e.dom.elLegendForeign&&e.dom.elLegendForeign.setAttribute("height",l),e.dom.elWrap.style.height=l+"px",b.setAttrs(e.dom.Paper.node,{height:l}),e.dom.Paper.node.parentNode.parentNode.style.minHeight=l+"px";}},{key:"coreCalculations",value:function(){new U(this.ctx).init();}},{key:"resetGlobals",value:function(){var t=this,e=function(){return t.w.config.series.map((function(t){return []}))},i=new H,a=this.w.globals;i.initGlobalVars(a),a.seriesXvalues=e(),a.seriesYvalues=e();}},{key:"isMultipleY",value:function(){if(this.w.config.yaxis.constructor===Array&&this.w.config.yaxis.length>1)return this.w.globals.isMultipleYAxis=!0,!0}},{key:"xySettings",value:function(){var t=null,e=this.w;if(e.globals.axisCharts){if("back"===e.config.xaxis.crosshairs.position)new Q(this.ctx).drawXCrosshairs();if("back"===e.config.yaxis[0].crosshairs.position)new Q(this.ctx).drawYCrosshairs();if("datetime"===e.config.xaxis.type&&void 0===e.config.xaxis.labels.formatter){this.ctx.timeScale=new Mt(this.ctx);var i=[];isFinite(e.globals.minX)&&isFinite(e.globals.maxX)&&!e.globals.isBarHorizontal?i=this.ctx.timeScale.calculateTimeScaleTicks(e.globals.minX,e.globals.maxX):e.globals.isBarHorizontal&&(i=this.ctx.timeScale.calculateTimeScaleTicks(e.globals.minY,e.globals.maxY)),this.ctx.timeScale.recalcDimensionsBasedOnFormat(i);}t=new w(this.ctx).getCalculatedRatios();}return t}},{key:"updateSourceChart",value:function(t){this.ctx.w.globals.selection=void 0,this.ctx.updateHelpers._updateOptions({chart:{selection:{xaxis:{min:t.w.globals.minX,max:t.w.globals.maxX}}}},!1,!1);}},{key:"setupBrushHandler",value:function(){var t=this,e=this.w;if(e.config.chart.brush.enabled&&"function"!=typeof e.config.chart.events.selection){var i=e.config.chart.brush.targets||[e.config.chart.brush.target];i.forEach((function(e){var i=ApexCharts.getChartByID(e);i.w.globals.brushSource=t.ctx,"function"!=typeof i.w.config.chart.events.zoomed&&(i.w.config.chart.events.zoomed=function(){t.updateSourceChart(i);}),"function"!=typeof i.w.config.chart.events.scrolled&&(i.w.config.chart.events.scrolled=function(){t.updateSourceChart(i);});})),e.config.chart.events.selection=function(t,a){i.forEach((function(t){var i=ApexCharts.getChartByID(t),s=f.clone(e.config.yaxis);if(e.config.chart.brush.autoScaleYaxis&&1===i.w.globals.series.length){var r=new j(i);s=r.autoScaleY(i,s,a);}var o=i.w.config.yaxis.reduce((function(t,e,a){return [].concat(g(t),[n(n({},i.w.config.yaxis[a]),{},{min:s[0].min,max:s[0].max})])}),[]);i.ctx.updateHelpers._updateOptions({xaxis:{min:a.xaxis.min,max:a.xaxis.max},yaxis:o},!1,!1,!1,!1);}));};}}}]),t}(),Xt=function(){function i(t){e(this,i),this.ctx=t,this.w=t.w;}return a(i,[{key:"_updateOptions",value:function(e){var i=this,a=arguments.length>1&&void 0!==arguments[1]&&arguments[1],s=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],r=!(arguments.length>3&&void 0!==arguments[3])||arguments[3],n=arguments.length>4&&void 0!==arguments[4]&&arguments[4],o=[this.ctx];r&&(o=this.ctx.getSyncedCharts()),this.ctx.w.globals.isExecCalled&&(o=[this.ctx],this.ctx.w.globals.isExecCalled=!1),o.forEach((function(r){var o=r.w;return o.globals.shouldAnimate=s,a||(o.globals.resized=!0,o.globals.dataChanged=!0,s&&r.series.getPreviousPaths()),e&&"object"===t(e)&&(r.config=new R(e),e=w.extendArrayProps(r.config,e,o),r.w.globals.chartID!==i.ctx.w.globals.chartID&&delete e.series,o.config=f.extend(o.config,e),n&&(o.globals.lastXAxis=e.xaxis?f.clone(e.xaxis):[],o.globals.lastYAxis=e.yaxis?f.clone(e.yaxis):[],o.globals.initialConfig=f.extend({},o.config),o.globals.initialSeries=f.clone(o.config.series))),r.update(e)}));}},{key:"_updateSeries",value:function(t,e){var i,a=this,s=arguments.length>2&&void 0!==arguments[2]&&arguments[2],r=this.w;return r.globals.shouldAnimate=e,r.globals.dataChanged=!0,e&&this.ctx.series.getPreviousPaths(),r.globals.axisCharts?(0===(i=t.map((function(t,e){return a._extendSeries(t,e)}))).length&&(i=[{data:[]}]),r.config.series=i):r.config.series=t.slice(),s&&(r.globals.initialSeries=f.clone(r.config.series)),this.ctx.update()}},{key:"_extendSeries",value:function(t,e){var i=this.w,a=i.config.series[e];return n(n({},i.config.series[e]),{},{name:t.name?t.name:a&&a.name,color:t.color?t.color:a&&a.color,type:t.type?t.type:a&&a.type,data:t.data?t.data:a&&a.data})}},{key:"toggleDataPointSelection",value:function(t,e){var i=this.w,a=null,s=".apexcharts-series[data\\:realIndex='".concat(t,"']");return i.globals.axisCharts?a=i.globals.dom.Paper.select("".concat(s," path[j='").concat(e,"'], ").concat(s," circle[j='").concat(e,"'], ").concat(s," rect[j='").concat(e,"']")).members[0]:void 0===e&&(a=i.globals.dom.Paper.select("".concat(s," path[j='").concat(t,"']")).members[0],"pie"!==i.config.chart.type&&"polarArea"!==i.config.chart.type&&"donut"!==i.config.chart.type||this.ctx.pie.pieClicked(t)),a?(new b(this.ctx).pathMouseDown(a,null),a.node?a.node:null):(console.warn("toggleDataPointSelection: Element not found"),null)}},{key:"forceXAxisUpdate",value:function(t){var e=this.w;if(["min","max"].forEach((function(i){void 0!==t.xaxis[i]&&(e.config.xaxis[i]=t.xaxis[i],e.globals.lastXAxis[i]=t.xaxis[i]);})),t.xaxis.categories&&t.xaxis.categories.length&&(e.config.xaxis.categories=t.xaxis.categories),e.config.xaxis.convertedCatToNumeric){var i=new D(t);t=i.convertCatToNumericXaxis(t,this.ctx);}return t}},{key:"forceYAxisUpdate",value:function(t){var e=this.w;return e.config.chart.stacked&&"100%"===e.config.chart.stackType&&(Array.isArray(t.yaxis)?t.yaxis.forEach((function(e,i){t.yaxis[i].min=0,t.yaxis[i].max=100;})):(t.yaxis.min=0,t.yaxis.max=100)),t}},{key:"revertDefaultAxisMinMax",value:function(t){var e=this,i=this.w,a=i.globals.lastXAxis,s=i.globals.lastYAxis;t&&t.xaxis&&(a=t.xaxis),t&&t.yaxis&&(s=t.yaxis),i.config.xaxis.min=a.min,i.config.xaxis.max=a.max;var r=function(t){void 0!==s[t]&&(i.config.yaxis[t].min=s[t].min,i.config.yaxis[t].max=s[t].max);};i.config.yaxis.map((function(t,a){i.globals.zoomed||void 0!==s[a]?r(a):void 0!==e.ctx.opts.yaxis[a]&&(t.min=e.ctx.opts.yaxis[a].min,t.max=e.ctx.opts.yaxis[a].max);}));}}]),i}();Tt="undefined"!=typeof window?window:void 0,zt=function(e,i){var a=(void 0!==this?this:e).SVG=function(t){if(a.supported)return t=new a.Doc(t),a.parser.draw||a.prepare(),t};if(a.ns="http://www.w3.org/2000/svg",a.xmlns="http://www.w3.org/2000/xmlns/",a.xlink="http://www.w3.org/1999/xlink",a.svgjs="http://svgjs.com/svgjs",a.supported=!0,!a.supported)return !1;a.did=1e3,a.eid=function(t){return "Svgjs"+d(t)+a.did++},a.create=function(t){var e=i.createElementNS(this.ns,t);return e.setAttribute("id",this.eid(t)),e},a.extend=function(){var t,e;e=(t=[].slice.call(arguments)).pop();for(var i=t.length-1;i>=0;i--)if(t[i])for(var s in e)t[i].prototype[s]=e[s];a.Set&&a.Set.inherit&&a.Set.inherit();},a.invent=function(t){var e="function"==typeof t.create?t.create:function(){this.constructor.call(this,a.create(t.create));};return t.inherit&&(e.prototype=new t.inherit),t.extend&&a.extend(e,t.extend),t.construct&&a.extend(t.parent||a.Container,t.construct),e},a.adopt=function(t){return t?t.instance?t.instance:((i="svg"==t.nodeName?t.parentNode instanceof e.SVGElement?new a.Nested:new a.Doc:"linearGradient"==t.nodeName?new a.Gradient("linear"):"radialGradient"==t.nodeName?new a.Gradient("radial"):a[d(t.nodeName)]?new(a[d(t.nodeName)]):new a.Element(t)).type=t.nodeName,i.node=t,t.instance=i,i instanceof a.Doc&&i.namespace().defs(),i.setData(JSON.parse(t.getAttribute("svgjs:data"))||{}),i):null;var i;},a.prepare=function(){var t=i.getElementsByTagName("body")[0],e=(t?new a.Doc(t):a.adopt(i.documentElement).nested()).size(2,0);a.parser={body:t||i.documentElement,draw:e.style("opacity:0;position:absolute;left:-100%;top:-100%;overflow:hidden").node,poly:e.polyline().node,path:e.path().node,native:a.create("svg")};},a.parser={native:a.create("svg")},i.addEventListener("DOMContentLoaded",(function(){a.parser.draw||a.prepare();}),!1),a.regex={numberAndUnit:/^([+-]?(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?)([a-z%]*)$/i,hex:/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i,rgb:/rgb\((\d+),(\d+),(\d+)\)/,reference:/#([a-z0-9\-_]+)/i,transforms:/\)\s*,?\s*/,whitespace:/\s/g,isHex:/^#[a-f0-9]{3,6}$/i,isRgb:/^rgb\(/,isCss:/[^:]+:[^;]+;?/,isBlank:/^(\s+)?$/,isNumber:/^[+-]?(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?$/i,isPercent:/^-?[\d\.]+%$/,isImage:/\.(jpg|jpeg|png|gif|svg)(\?[^=]+.*)?/i,delimiter:/[\s,]+/,hyphen:/([^e])\-/gi,pathLetters:/[MLHVCSQTAZ]/gi,isPathLetter:/[MLHVCSQTAZ]/i,numbersWithDots:/((\d?\.\d+(?:e[+-]?\d+)?)((?:\.\d+(?:e[+-]?\d+)?)+))+/gi,dots:/\./g},a.utils={map:function(t,e){for(var i=t.length,a=[],s=0;s<i;s++)a.push(e(t[s]));return a},filter:function(t,e){for(var i=t.length,a=[],s=0;s<i;s++)e(t[s])&&a.push(t[s]);return a},filterSVGElements:function(t){return this.filter(t,(function(t){return t instanceof e.SVGElement}))}},a.defaults={attrs:{"fill-opacity":1,"stroke-opacity":1,"stroke-width":0,"stroke-linejoin":"miter","stroke-linecap":"butt",fill:"#000000",stroke:"#000000",opacity:1,x:0,y:0,cx:0,cy:0,width:0,height:0,r:0,rx:0,ry:0,offset:0,"stop-opacity":1,"stop-color":"#000000","font-size":16,"font-family":"Helvetica, Arial, sans-serif","text-anchor":"start"}},a.Color=function(e){var i,s;this.r=0,this.g=0,this.b=0,e&&("string"==typeof e?a.regex.isRgb.test(e)?(i=a.regex.rgb.exec(e.replace(a.regex.whitespace,"")),this.r=parseInt(i[1]),this.g=parseInt(i[2]),this.b=parseInt(i[3])):a.regex.isHex.test(e)&&(i=a.regex.hex.exec(4==(s=e).length?["#",s.substring(1,2),s.substring(1,2),s.substring(2,3),s.substring(2,3),s.substring(3,4),s.substring(3,4)].join(""):s),this.r=parseInt(i[1],16),this.g=parseInt(i[2],16),this.b=parseInt(i[3],16)):"object"===t(e)&&(this.r=e.r,this.g=e.g,this.b=e.b));},a.extend(a.Color,{toString:function(){return this.toHex()},toHex:function(){return "#"+g(this.r)+g(this.g)+g(this.b)},toRgb:function(){return "rgb("+[this.r,this.g,this.b].join()+")"},brightness:function(){return this.r/255*.3+this.g/255*.59+this.b/255*.11},morph:function(t){return this.destination=new a.Color(t),this},at:function(t){return this.destination?(t=t<0?0:t>1?1:t,new a.Color({r:~~(this.r+(this.destination.r-this.r)*t),g:~~(this.g+(this.destination.g-this.g)*t),b:~~(this.b+(this.destination.b-this.b)*t)})):this}}),a.Color.test=function(t){return t+="",a.regex.isHex.test(t)||a.regex.isRgb.test(t)},a.Color.isRgb=function(t){return t&&"number"==typeof t.r&&"number"==typeof t.g&&"number"==typeof t.b},a.Color.isColor=function(t){return a.Color.isRgb(t)||a.Color.test(t)},a.Array=function(t,e){0==(t=(t||[]).valueOf()).length&&e&&(t=e.valueOf()),this.value=this.parse(t);},a.extend(a.Array,{toString:function(){return this.value.join(" ")},valueOf:function(){return this.value},parse:function(t){return t=t.valueOf(),Array.isArray(t)?t:this.split(t)}}),a.PointArray=function(t,e){a.Array.call(this,t,e||[[0,0]]);},a.PointArray.prototype=new a.Array,a.PointArray.prototype.constructor=a.PointArray;for(var s={M:function(t,e,i){return e.x=i.x=t[0],e.y=i.y=t[1],["M",e.x,e.y]},L:function(t,e){return e.x=t[0],e.y=t[1],["L",t[0],t[1]]},H:function(t,e){return e.x=t[0],["H",t[0]]},V:function(t,e){return e.y=t[0],["V",t[0]]},C:function(t,e){return e.x=t[4],e.y=t[5],["C",t[0],t[1],t[2],t[3],t[4],t[5]]},Q:function(t,e){return e.x=t[2],e.y=t[3],["Q",t[0],t[1],t[2],t[3]]},Z:function(t,e,i){return e.x=i.x,e.y=i.y,["Z"]}},r="mlhvqtcsaz".split(""),n=0,o=r.length;n<o;++n)s[r[n]]=function(t){return function(e,i,a){if("H"==t)e[0]=e[0]+i.x;else if("V"==t)e[0]=e[0]+i.y;else if("A"==t)e[5]=e[5]+i.x,e[6]=e[6]+i.y;else for(var r=0,n=e.length;r<n;++r)e[r]=e[r]+(r%2?i.y:i.x);return s[t](e,i,a)}}(r[n].toUpperCase());a.PathArray=function(t,e){a.Array.call(this,t,e||[["M",0,0]]);},a.PathArray.prototype=new a.Array,a.PathArray.prototype.constructor=a.PathArray,a.extend(a.PathArray,{toString:function(){return function(t){for(var e=0,i=t.length,a="";e<i;e++)a+=t[e][0],null!=t[e][1]&&(a+=t[e][1],null!=t[e][2]&&(a+=" ",a+=t[e][2],null!=t[e][3]&&(a+=" ",a+=t[e][3],a+=" ",a+=t[e][4],null!=t[e][5]&&(a+=" ",a+=t[e][5],a+=" ",a+=t[e][6],null!=t[e][7]&&(a+=" ",a+=t[e][7])))));return a+" "}(this.value)},move:function(t,e){var i=this.bbox();return i.x,i.y,this},at:function(t){if(!this.destination)return this;for(var e=this.value,i=this.destination.value,s=[],r=new a.PathArray,n=0,o=e.length;n<o;n++){s[n]=[e[n][0]];for(var l=1,h=e[n].length;l<h;l++)s[n][l]=e[n][l]+(i[n][l]-e[n][l])*t;"A"===s[n][0]&&(s[n][4]=+(0!=s[n][4]),s[n][5]=+(0!=s[n][5]));}return r.value=s,r},parse:function(t){if(t instanceof a.PathArray)return t.valueOf();var e,i={M:2,L:2,H:1,V:1,C:6,S:4,Q:4,T:2,A:7,Z:0};t="string"==typeof t?t.replace(a.regex.numbersWithDots,h).replace(a.regex.pathLetters," $& ").replace(a.regex.hyphen,"$1 -").trim().split(a.regex.delimiter):t.reduce((function(t,e){return [].concat.call(t,e)}),[]);var r=[],n=new a.Point,o=new a.Point,l=0,c=t.length;do{a.regex.isPathLetter.test(t[l])?(e=t[l],++l):"M"==e?e="L":"m"==e&&(e="l"),r.push(s[e].call(null,t.slice(l,l+=i[e.toUpperCase()]).map(parseFloat),n,o));}while(c>l);return r},bbox:function(){return a.parser.draw||a.prepare(),a.parser.path.setAttribute("d",this.toString()),a.parser.path.getBBox()}}),a.Number=a.invent({create:function(t,e){this.value=0,this.unit=e||"","number"==typeof t?this.value=isNaN(t)?0:isFinite(t)?t:t<0?-34e37:34e37:"string"==typeof t?(e=t.match(a.regex.numberAndUnit))&&(this.value=parseFloat(e[1]),"%"==e[5]?this.value/=100:"s"==e[5]&&(this.value*=1e3),this.unit=e[5]):t instanceof a.Number&&(this.value=t.valueOf(),this.unit=t.unit);},extend:{toString:function(){return ("%"==this.unit?~~(1e8*this.value)/1e6:"s"==this.unit?this.value/1e3:this.value)+this.unit},toJSON:function(){return this.toString()},valueOf:function(){return this.value},plus:function(t){return t=new a.Number(t),new a.Number(this+t,this.unit||t.unit)},minus:function(t){return t=new a.Number(t),new a.Number(this-t,this.unit||t.unit)},times:function(t){return t=new a.Number(t),new a.Number(this*t,this.unit||t.unit)},divide:function(t){return t=new a.Number(t),new a.Number(this/t,this.unit||t.unit)},to:function(t){var e=new a.Number(this);return "string"==typeof t&&(e.unit=t),e},morph:function(t){return this.destination=new a.Number(t),t.relative&&(this.destination.value+=this.value),this},at:function(t){return this.destination?new a.Number(this.destination).minus(this).times(t).plus(this):this}}}),a.Element=a.invent({create:function(t){this._stroke=a.defaults.attrs.stroke,this._event=null,this.dom={},(this.node=t)&&(this.type=t.nodeName,this.node.instance=this,this._stroke=t.getAttribute("stroke")||this._stroke);},extend:{x:function(t){return this.attr("x",t)},y:function(t){return this.attr("y",t)},cx:function(t){return null==t?this.x()+this.width()/2:this.x(t-this.width()/2)},cy:function(t){return null==t?this.y()+this.height()/2:this.y(t-this.height()/2)},move:function(t,e){return this.x(t).y(e)},center:function(t,e){return this.cx(t).cy(e)},width:function(t){return this.attr("width",t)},height:function(t){return this.attr("height",t)},size:function(t,e){var i=u(this,t,e);return this.width(new a.Number(i.width)).height(new a.Number(i.height))},clone:function(t){this.writeDataToDom();var e=x(this.node.cloneNode(!0));return t?t.add(e):this.after(e),e},remove:function(){return this.parent()&&this.parent().removeElement(this),this},replace:function(t){return this.after(t).remove(),t},addTo:function(t){return t.put(this)},putIn:function(t){return t.add(this)},id:function(t){return this.attr("id",t)},show:function(){return this.style("display","")},hide:function(){return this.style("display","none")},visible:function(){return "none"!=this.style("display")},toString:function(){return this.attr("id")},classes:function(){var t=this.attr("class");return null==t?[]:t.trim().split(a.regex.delimiter)},hasClass:function(t){return -1!=this.classes().indexOf(t)},addClass:function(t){if(!this.hasClass(t)){var e=this.classes();e.push(t),this.attr("class",e.join(" "));}return this},removeClass:function(t){return this.hasClass(t)&&this.attr("class",this.classes().filter((function(e){return e!=t})).join(" ")),this},toggleClass:function(t){return this.hasClass(t)?this.removeClass(t):this.addClass(t)},reference:function(t){return a.get(this.attr(t))},parent:function(t){var i=this;if(!i.node.parentNode)return null;if(i=a.adopt(i.node.parentNode),!t)return i;for(;i&&i.node instanceof e.SVGElement;){if("string"==typeof t?i.matches(t):i instanceof t)return i;if(!i.node.parentNode||"#document"==i.node.parentNode.nodeName)return null;i=a.adopt(i.node.parentNode);}},doc:function(){return this instanceof a.Doc?this:this.parent(a.Doc)},parents:function(t){var e=[],i=this;do{if(!(i=i.parent(t))||!i.node)break;e.push(i);}while(i.parent);return e},matches:function(t){return function(t,e){return (t.matches||t.matchesSelector||t.msMatchesSelector||t.mozMatchesSelector||t.webkitMatchesSelector||t.oMatchesSelector).call(t,e)}(this.node,t)},native:function(){return this.node},svg:function(t){var e=i.createElement("svg");if(!(t&&this instanceof a.Parent))return e.appendChild(t=i.createElement("svg")),this.writeDataToDom(),t.appendChild(this.node.cloneNode(!0)),e.innerHTML.replace(/^<svg>/,"").replace(/<\/svg>$/,"");e.innerHTML="<svg>"+t.replace(/\n/,"").replace(/<([\w:-]+)([^<]+?)\/>/g,"<$1$2></$1>")+"</svg>";for(var s=0,r=e.firstChild.childNodes.length;s<r;s++)this.node.appendChild(e.firstChild.firstChild);return this},writeDataToDom:function(){return (this.each||this.lines)&&(this.each?this:this.lines()).each((function(){this.writeDataToDom();})),this.node.removeAttribute("svgjs:data"),Object.keys(this.dom).length&&this.node.setAttribute("svgjs:data",JSON.stringify(this.dom)),this},setData:function(t){return this.dom=t,this},is:function(t){return function(t,e){return t instanceof e}(this,t)}}}),a.easing={"-":function(t){return t},"<>":function(t){return -Math.cos(t*Math.PI)/2+.5},">":function(t){return Math.sin(t*Math.PI/2)},"<":function(t){return 1-Math.cos(t*Math.PI/2)}},a.morph=function(t){return function(e,i){return new a.MorphObj(e,i).at(t)}},a.Situation=a.invent({create:function(t){this.init=!1,this.reversed=!1,this.reversing=!1,this.duration=new a.Number(t.duration).valueOf(),this.delay=new a.Number(t.delay).valueOf(),this.start=+new Date+this.delay,this.finish=this.start+this.duration,this.ease=t.ease,this.loop=0,this.loops=!1,this.animations={},this.attrs={},this.styles={},this.transforms=[],this.once={};}}),a.FX=a.invent({create:function(t){this._target=t,this.situations=[],this.active=!1,this.situation=null,this.paused=!1,this.lastPos=0,this.pos=0,this.absPos=0,this._speed=1;},extend:{animate:function(e,i,s){"object"===t(e)&&(i=e.ease,s=e.delay,e=e.duration);var r=new a.Situation({duration:e||1e3,delay:s||0,ease:a.easing[i||"-"]||i});return this.queue(r),this},target:function(t){return t&&t instanceof a.Element?(this._target=t,this):this._target},timeToAbsPos:function(t){return (t-this.situation.start)/(this.situation.duration/this._speed)},absPosToTime:function(t){return this.situation.duration/this._speed*t+this.situation.start},startAnimFrame:function(){this.stopAnimFrame(),this.animationFrame=e.requestAnimationFrame(function(){this.step();}.bind(this));},stopAnimFrame:function(){e.cancelAnimationFrame(this.animationFrame);},start:function(){return !this.active&&this.situation&&(this.active=!0,this.startCurrent()),this},startCurrent:function(){return this.situation.start=+new Date+this.situation.delay/this._speed,this.situation.finish=this.situation.start+this.situation.duration/this._speed,this.initAnimations().step()},queue:function(t){return ("function"==typeof t||t instanceof a.Situation)&&this.situations.push(t),this.situation||(this.situation=this.situations.shift()),this},dequeue:function(){return this.stop(),this.situation=this.situations.shift(),this.situation&&(this.situation instanceof a.Situation?this.start():this.situation.call(this)),this},initAnimations:function(){var t,e=this.situation;if(e.init)return this;for(var i in e.animations){t=this.target()[i](),Array.isArray(t)||(t=[t]),Array.isArray(e.animations[i])||(e.animations[i]=[e.animations[i]]);for(var s=t.length;s--;)e.animations[i][s]instanceof a.Number&&(t[s]=new a.Number(t[s])),e.animations[i][s]=t[s].morph(e.animations[i][s]);}for(var i in e.attrs)e.attrs[i]=new a.MorphObj(this.target().attr(i),e.attrs[i]);for(var i in e.styles)e.styles[i]=new a.MorphObj(this.target().style(i),e.styles[i]);return e.initialTransformation=this.target().matrixify(),e.init=!0,this},clearQueue:function(){return this.situations=[],this},clearCurrent:function(){return this.situation=null,this},stop:function(t,e){var i=this.active;return this.active=!1,e&&this.clearQueue(),t&&this.situation&&(!i&&this.startCurrent(),this.atEnd()),this.stopAnimFrame(),this.clearCurrent()},after:function(t){var e=this.last();return this.target().on("finished.fx",(function i(a){a.detail.situation==e&&(t.call(this,e),this.off("finished.fx",i));})),this._callStart()},during:function(t){var e=this.last(),i=function(i){i.detail.situation==e&&t.call(this,i.detail.pos,a.morph(i.detail.pos),i.detail.eased,e);};return this.target().off("during.fx",i).on("during.fx",i),this.after((function(){this.off("during.fx",i);})),this._callStart()},afterAll:function(t){var e=function e(i){t.call(this),this.off("allfinished.fx",e);};return this.target().off("allfinished.fx",e).on("allfinished.fx",e),this._callStart()},last:function(){return this.situations.length?this.situations[this.situations.length-1]:this.situation},add:function(t,e,i){return this.last()[i||"animations"][t]=e,this._callStart()},step:function(t){var e,i,a;t||(this.absPos=this.timeToAbsPos(+new Date)),!1!==this.situation.loops?(e=Math.max(this.absPos,0),i=Math.floor(e),!0===this.situation.loops||i<this.situation.loops?(this.pos=e-i,a=this.situation.loop,this.situation.loop=i):(this.absPos=this.situation.loops,this.pos=1,a=this.situation.loop-1,this.situation.loop=this.situation.loops),this.situation.reversing&&(this.situation.reversed=this.situation.reversed!=Boolean((this.situation.loop-a)%2))):(this.absPos=Math.min(this.absPos,1),this.pos=this.absPos),this.pos<0&&(this.pos=0),this.situation.reversed&&(this.pos=1-this.pos);var s=this.situation.ease(this.pos);for(var r in this.situation.once)r>this.lastPos&&r<=s&&(this.situation.once[r].call(this.target(),this.pos,s),delete this.situation.once[r]);return this.active&&this.target().fire("during",{pos:this.pos,eased:s,fx:this,situation:this.situation}),this.situation?(this.eachAt(),1==this.pos&&!this.situation.reversed||this.situation.reversed&&0==this.pos?(this.stopAnimFrame(),this.target().fire("finished",{fx:this,situation:this.situation}),this.situations.length||(this.target().fire("allfinished"),this.situations.length||(this.target().off(".fx"),this.active=!1)),this.active?this.dequeue():this.clearCurrent()):!this.paused&&this.active&&this.startAnimFrame(),this.lastPos=s,this):this},eachAt:function(){var t,e=this,i=this.target(),s=this.situation;for(var r in s.animations)t=[].concat(s.animations[r]).map((function(t){return "string"!=typeof t&&t.at?t.at(s.ease(e.pos),e.pos):t})),i[r].apply(i,t);for(var r in s.attrs)t=[r].concat(s.attrs[r]).map((function(t){return "string"!=typeof t&&t.at?t.at(s.ease(e.pos),e.pos):t})),i.attr.apply(i,t);for(var r in s.styles)t=[r].concat(s.styles[r]).map((function(t){return "string"!=typeof t&&t.at?t.at(s.ease(e.pos),e.pos):t})),i.style.apply(i,t);if(s.transforms.length){t=s.initialTransformation,r=0;for(var n=s.transforms.length;r<n;r++){var o=s.transforms[r];o instanceof a.Matrix?t=o.relative?t.multiply((new a.Matrix).morph(o).at(s.ease(this.pos))):t.morph(o).at(s.ease(this.pos)):(o.relative||o.undo(t.extract()),t=t.multiply(o.at(s.ease(this.pos))));}i.matrix(t);}return this},once:function(t,e,i){var a=this.last();return i||(t=a.ease(t)),a.once[t]=e,this},_callStart:function(){return setTimeout(function(){this.start();}.bind(this),0),this}},parent:a.Element,construct:{animate:function(t,e,i){return (this.fx||(this.fx=new a.FX(this))).animate(t,e,i)},delay:function(t){return (this.fx||(this.fx=new a.FX(this))).delay(t)},stop:function(t,e){return this.fx&&this.fx.stop(t,e),this},finish:function(){return this.fx&&this.fx.finish(),this}}}),a.MorphObj=a.invent({create:function(t,e){return a.Color.isColor(e)?new a.Color(t).morph(e):a.regex.delimiter.test(t)?a.regex.pathLetters.test(t)?new a.PathArray(t).morph(e):new a.Array(t).morph(e):a.regex.numberAndUnit.test(e)?new a.Number(t).morph(e):(this.value=t,void(this.destination=e))},extend:{at:function(t,e){return e<1?this.value:this.destination},valueOf:function(){return this.value}}}),a.extend(a.FX,{attr:function(e,i,a){if("object"===t(e))for(var s in e)this.attr(s,e[s]);else this.add(e,i,"attrs");return this},plot:function(t,e,i,a){return 4==arguments.length?this.plot([t,e,i,a]):this.add("plot",new(this.target().morphArray)(t))}}),a.Box=a.invent({create:function(e,i,s,r){if(!("object"!==t(e)||e instanceof a.Element))return a.Box.call(this,null!=e.left?e.left:e.x,null!=e.top?e.top:e.y,e.width,e.height);4==arguments.length&&(this.x=e,this.y=i,this.width=s,this.height=r),b(this);}}),a.BBox=a.invent({create:function(t){if(a.Box.apply(this,[].slice.call(arguments)),t instanceof a.Element){var e;try{if(!i.documentElement.contains){for(var s=t.node;s.parentNode;)s=s.parentNode;if(s!=i)throw new Error("Element not in the dom")}e=t.node.getBBox();}catch(i){if(t instanceof a.Shape){a.parser.draw||a.prepare();var r=t.clone(a.parser.draw.instance).show();e=r.node.getBBox(),r.remove();}else e={x:t.node.clientLeft,y:t.node.clientTop,width:t.node.clientWidth,height:t.node.clientHeight};}a.Box.call(this,e);}},inherit:a.Box,parent:a.Element,construct:{bbox:function(){return new a.BBox(this)}}}),a.BBox.prototype.constructor=a.BBox,a.Matrix=a.invent({create:function(e){var i=p([1,0,0,1,0,0]);e=e instanceof a.Element?e.matrixify():"string"==typeof e?p(e.split(a.regex.delimiter).map(parseFloat)):6==arguments.length?p([].slice.call(arguments)):Array.isArray(e)?p(e):"object"===t(e)?e:i;for(var s=v.length-1;s>=0;--s)this[v[s]]=null!=e[v[s]]?e[v[s]]:i[v[s]];},extend:{extract:function(){var t=f(this,0,1),e=(f(this,1,0),180/Math.PI*Math.atan2(t.y,t.x)-90);return {x:this.e,y:this.f,transformedX:(this.e*Math.cos(e*Math.PI/180)+this.f*Math.sin(e*Math.PI/180))/Math.sqrt(this.a*this.a+this.b*this.b),transformedY:(this.f*Math.cos(e*Math.PI/180)+this.e*Math.sin(-e*Math.PI/180))/Math.sqrt(this.c*this.c+this.d*this.d),rotation:e,a:this.a,b:this.b,c:this.c,d:this.d,e:this.e,f:this.f,matrix:new a.Matrix(this)}},clone:function(){return new a.Matrix(this)},morph:function(t){return this.destination=new a.Matrix(t),this},multiply:function(t){return new a.Matrix(this.native().multiply(function(t){return t instanceof a.Matrix||(t=new a.Matrix(t)),t}(t).native()))},inverse:function(){return new a.Matrix(this.native().inverse())},translate:function(t,e){return new a.Matrix(this.native().translate(t||0,e||0))},native:function(){for(var t=a.parser.native.createSVGMatrix(),e=v.length-1;e>=0;e--)t[v[e]]=this[v[e]];return t},toString:function(){return "matrix("+m(this.a)+","+m(this.b)+","+m(this.c)+","+m(this.d)+","+m(this.e)+","+m(this.f)+")"}},parent:a.Element,construct:{ctm:function(){return new a.Matrix(this.node.getCTM())},screenCTM:function(){if(this instanceof a.Nested){var t=this.rect(1,1),e=t.node.getScreenCTM();return t.remove(),new a.Matrix(e)}return new a.Matrix(this.node.getScreenCTM())}}}),a.Point=a.invent({create:function(e,i){var a;a=Array.isArray(e)?{x:e[0],y:e[1]}:"object"===t(e)?{x:e.x,y:e.y}:null!=e?{x:e,y:null!=i?i:e}:{x:0,y:0},this.x=a.x,this.y=a.y;},extend:{clone:function(){return new a.Point(this)},morph:function(t,e){return this.destination=new a.Point(t,e),this}}}),a.extend(a.Element,{point:function(t,e){return new a.Point(t,e).transform(this.screenCTM().inverse())}}),a.extend(a.Element,{attr:function(e,i,s){if(null==e){for(e={},s=(i=this.node.attributes).length-1;s>=0;s--)e[i[s].nodeName]=a.regex.isNumber.test(i[s].nodeValue)?parseFloat(i[s].nodeValue):i[s].nodeValue;return e}if("object"===t(e))for(var r in e)this.attr(r,e[r]);else if(null===i)this.node.removeAttribute(e);else {if(null==i)return null==(i=this.node.getAttribute(e))?a.defaults.attrs[e]:a.regex.isNumber.test(i)?parseFloat(i):i;"stroke-width"==e?this.attr("stroke",parseFloat(i)>0?this._stroke:null):"stroke"==e&&(this._stroke=i),"fill"!=e&&"stroke"!=e||(a.regex.isImage.test(i)&&(i=this.doc().defs().image(i,0,0)),i instanceof a.Image&&(i=this.doc().defs().pattern(0,0,(function(){this.add(i);})))),"number"==typeof i?i=new a.Number(i):a.Color.isColor(i)?i=new a.Color(i):Array.isArray(i)&&(i=new a.Array(i)),"leading"==e?this.leading&&this.leading(i):"string"==typeof s?this.node.setAttributeNS(s,e,i.toString()):this.node.setAttribute(e,i.toString()),!this.rebuild||"font-size"!=e&&"x"!=e||this.rebuild(e,i);}return this}}),a.extend(a.Element,{transform:function(e,i){var s;return "object"!==t(e)?(s=new a.Matrix(this).extract(),"string"==typeof e?s[e]:s):(s=new a.Matrix(this),i=!!i||!!e.relative,null!=e.a&&(s=i?s.multiply(new a.Matrix(e)):new a.Matrix(e)),this.attr("transform",s))}}),a.extend(a.Element,{untransform:function(){return this.attr("transform",null)},matrixify:function(){return (this.attr("transform")||"").split(a.regex.transforms).slice(0,-1).map((function(t){var e=t.trim().split("(");return [e[0],e[1].split(a.regex.delimiter).map((function(t){return parseFloat(t)}))]})).reduce((function(t,e){return "matrix"==e[0]?t.multiply(p(e[1])):t[e[0]].apply(t,e[1])}),new a.Matrix)},toParent:function(t){if(this==t)return this;var e=this.screenCTM(),i=t.screenCTM().inverse();return this.addTo(t).untransform().transform(i.multiply(e)),this},toDoc:function(){return this.toParent(this.doc())}}),a.Transformation=a.invent({create:function(e,i){if(arguments.length>1&&"boolean"!=typeof i)return this.constructor.call(this,[].slice.call(arguments));if(Array.isArray(e))for(var a=0,s=this.arguments.length;a<s;++a)this[this.arguments[a]]=e[a];else if("object"===t(e))for(a=0,s=this.arguments.length;a<s;++a)this[this.arguments[a]]=e[this.arguments[a]];this.inversed=!1,!0===i&&(this.inversed=!0);}}),a.Translate=a.invent({parent:a.Matrix,inherit:a.Transformation,create:function(t,e){this.constructor.apply(this,[].slice.call(arguments));},extend:{arguments:["transformedX","transformedY"],method:"translate"}}),a.extend(a.Element,{style:function(e,i){if(0==arguments.length)return this.node.style.cssText||"";if(arguments.length<2)if("object"===t(e))for(var s in e)this.style(s,e[s]);else {if(!a.regex.isCss.test(e))return this.node.style[c(e)];for(e=e.split(/\s*;\s*/).filter((function(t){return !!t})).map((function(t){return t.split(/\s*:\s*/)}));i=e.pop();)this.style(i[0],i[1]);}else this.node.style[c(e)]=null===i||a.regex.isBlank.test(i)?"":i;return this}}),a.Parent=a.invent({create:function(t){this.constructor.call(this,t);},inherit:a.Element,extend:{children:function(){return a.utils.map(a.utils.filterSVGElements(this.node.childNodes),(function(t){return a.adopt(t)}))},add:function(t,e){return null==e?this.node.appendChild(t.node):t.node!=this.node.childNodes[e]&&this.node.insertBefore(t.node,this.node.childNodes[e]),this},put:function(t,e){return this.add(t,e),t},has:function(t){return this.index(t)>=0},index:function(t){return [].slice.call(this.node.childNodes).indexOf(t.node)},get:function(t){return a.adopt(this.node.childNodes[t])},first:function(){return this.get(0)},last:function(){return this.get(this.node.childNodes.length-1)},each:function(t,e){for(var i=this.children(),s=0,r=i.length;s<r;s++)i[s]instanceof a.Element&&t.apply(i[s],[s,i]),e&&i[s]instanceof a.Container&&i[s].each(t,e);return this},removeElement:function(t){return this.node.removeChild(t.node),this},clear:function(){for(;this.node.hasChildNodes();)this.node.removeChild(this.node.lastChild);return delete this._defs,this},defs:function(){return this.doc().defs()}}}),a.extend(a.Parent,{ungroup:function(t,e){return 0===e||this instanceof a.Defs||this.node==a.parser.draw||(t=t||(this instanceof a.Doc?this:this.parent(a.Parent)),e=e||1/0,this.each((function(){return this instanceof a.Defs?this:this instanceof a.Parent?this.ungroup(t,e-1):this.toParent(t)})),this.node.firstChild||this.remove()),this},flatten:function(t,e){return this.ungroup(t,e)}}),a.Container=a.invent({create:function(t){this.constructor.call(this,t);},inherit:a.Parent}),a.ViewBox=a.invent({parent:a.Container,construct:{}}),["click","dblclick","mousedown","mouseup","mouseover","mouseout","mousemove","touchstart","touchmove","touchleave","touchend","touchcancel"].forEach((function(t){a.Element.prototype[t]=function(e){return a.on(this.node,t,e),this};})),a.listeners=[],a.handlerMap=[],a.listenerId=0,a.on=function(t,e,i,s,r){var n=i.bind(s||t.instance||t),o=(a.handlerMap.indexOf(t)+1||a.handlerMap.push(t))-1,l=e.split(".")[0],h=e.split(".")[1]||"*";a.listeners[o]=a.listeners[o]||{},a.listeners[o][l]=a.listeners[o][l]||{},a.listeners[o][l][h]=a.listeners[o][l][h]||{},i._svgjsListenerId||(i._svgjsListenerId=++a.listenerId),a.listeners[o][l][h][i._svgjsListenerId]=n,t.addEventListener(l,n,r||!1);},a.off=function(t,e,i){var s=a.handlerMap.indexOf(t),r=e&&e.split(".")[0],n=e&&e.split(".")[1],o="";if(-1!=s)if(i){if("function"==typeof i&&(i=i._svgjsListenerId),!i)return;a.listeners[s][r]&&a.listeners[s][r][n||"*"]&&(t.removeEventListener(r,a.listeners[s][r][n||"*"][i],!1),delete a.listeners[s][r][n||"*"][i]);}else if(n&&r){if(a.listeners[s][r]&&a.listeners[s][r][n]){for(var l in a.listeners[s][r][n])a.off(t,[r,n].join("."),l);delete a.listeners[s][r][n];}}else if(n)for(var h in a.listeners[s])for(var o in a.listeners[s][h])n===o&&a.off(t,[h,n].join("."));else if(r){if(a.listeners[s][r]){for(var o in a.listeners[s][r])a.off(t,[r,o].join("."));delete a.listeners[s][r];}}else {for(var h in a.listeners[s])a.off(t,h);delete a.listeners[s],delete a.handlerMap[s];}},a.extend(a.Element,{on:function(t,e,i,s){return a.on(this.node,t,e,i,s),this},off:function(t,e){return a.off(this.node,t,e),this},fire:function(t,i){return t instanceof e.Event?this.node.dispatchEvent(t):this.node.dispatchEvent(t=new a.CustomEvent(t,{detail:i,cancelable:!0})),this._event=t,this},event:function(){return this._event}}),a.Defs=a.invent({create:"defs",inherit:a.Container}),a.G=a.invent({create:"g",inherit:a.Container,extend:{x:function(t){return null==t?this.transform("x"):this.transform({x:t-this.x()},!0)}},construct:{group:function(){return this.put(new a.G)}}}),a.Doc=a.invent({create:function(t){t&&("svg"==(t="string"==typeof t?i.getElementById(t):t).nodeName?this.constructor.call(this,t):(this.constructor.call(this,a.create("svg")),t.appendChild(this.node),this.size("100%","100%")),this.namespace().defs());},inherit:a.Container,extend:{namespace:function(){return this.attr({xmlns:a.ns,version:"1.1"}).attr("xmlns:xlink",a.xlink,a.xmlns).attr("xmlns:svgjs",a.svgjs,a.xmlns)},defs:function(){var t;return this._defs||((t=this.node.getElementsByTagName("defs")[0])?this._defs=a.adopt(t):this._defs=new a.Defs,this.node.appendChild(this._defs.node)),this._defs},parent:function(){return this.node.parentNode&&"#document"!=this.node.parentNode.nodeName?this.node.parentNode:null},remove:function(){return this.parent()&&this.parent().removeChild(this.node),this},clear:function(){for(;this.node.hasChildNodes();)this.node.removeChild(this.node.lastChild);return delete this._defs,a.parser.draw&&!a.parser.draw.parentNode&&this.node.appendChild(a.parser.draw),this},clone:function(t){this.writeDataToDom();var e=this.node,i=x(e.cloneNode(!0));return t?(t.node||t).appendChild(i.node):e.parentNode.insertBefore(i.node,e.nextSibling),i}}}),a.extend(a.Element,{}),a.Gradient=a.invent({create:function(t){this.constructor.call(this,a.create(t+"Gradient")),this.type=t;},inherit:a.Container,extend:{at:function(t,e,i){return this.put(new a.Stop).update(t,e,i)},update:function(t){return this.clear(),"function"==typeof t&&t.call(this,this),this},fill:function(){return "url(#"+this.id()+")"},toString:function(){return this.fill()},attr:function(t,e,i){return "transform"==t&&(t="gradientTransform"),a.Container.prototype.attr.call(this,t,e,i)}},construct:{gradient:function(t,e){return this.defs().gradient(t,e)}}}),a.extend(a.Gradient,a.FX,{from:function(t,e){return "radial"==(this._target||this).type?this.attr({fx:new a.Number(t),fy:new a.Number(e)}):this.attr({x1:new a.Number(t),y1:new a.Number(e)})},to:function(t,e){return "radial"==(this._target||this).type?this.attr({cx:new a.Number(t),cy:new a.Number(e)}):this.attr({x2:new a.Number(t),y2:new a.Number(e)})}}),a.extend(a.Defs,{gradient:function(t,e){return this.put(new a.Gradient(t)).update(e)}}),a.Stop=a.invent({create:"stop",inherit:a.Element,extend:{update:function(t){return ("number"==typeof t||t instanceof a.Number)&&(t={offset:arguments[0],color:arguments[1],opacity:arguments[2]}),null!=t.opacity&&this.attr("stop-opacity",t.opacity),null!=t.color&&this.attr("stop-color",t.color),null!=t.offset&&this.attr("offset",new a.Number(t.offset)),this}}}),a.Pattern=a.invent({create:"pattern",inherit:a.Container,extend:{fill:function(){return "url(#"+this.id()+")"},update:function(t){return this.clear(),"function"==typeof t&&t.call(this,this),this},toString:function(){return this.fill()},attr:function(t,e,i){return "transform"==t&&(t="patternTransform"),a.Container.prototype.attr.call(this,t,e,i)}},construct:{pattern:function(t,e,i){return this.defs().pattern(t,e,i)}}}),a.extend(a.Defs,{pattern:function(t,e,i){return this.put(new a.Pattern).update(i).attr({x:0,y:0,width:t,height:e,patternUnits:"userSpaceOnUse"})}}),a.Shape=a.invent({create:function(t){this.constructor.call(this,t);},inherit:a.Element}),a.Symbol=a.invent({create:"symbol",inherit:a.Container,construct:{symbol:function(){return this.put(new a.Symbol)}}}),a.Use=a.invent({create:"use",inherit:a.Shape,extend:{element:function(t,e){return this.attr("href",(e||"")+"#"+t,a.xlink)}},construct:{use:function(t,e){return this.put(new a.Use).element(t,e)}}}),a.Rect=a.invent({create:"rect",inherit:a.Shape,construct:{rect:function(t,e){return this.put(new a.Rect).size(t,e)}}}),a.Circle=a.invent({create:"circle",inherit:a.Shape,construct:{circle:function(t){return this.put(new a.Circle).rx(new a.Number(t).divide(2)).move(0,0)}}}),a.extend(a.Circle,a.FX,{rx:function(t){return this.attr("r",t)},ry:function(t){return this.rx(t)}}),a.Ellipse=a.invent({create:"ellipse",inherit:a.Shape,construct:{ellipse:function(t,e){return this.put(new a.Ellipse).size(t,e).move(0,0)}}}),a.extend(a.Ellipse,a.Rect,a.FX,{rx:function(t){return this.attr("rx",t)},ry:function(t){return this.attr("ry",t)}}),a.extend(a.Circle,a.Ellipse,{x:function(t){return null==t?this.cx()-this.rx():this.cx(t+this.rx())},y:function(t){return null==t?this.cy()-this.ry():this.cy(t+this.ry())},cx:function(t){return null==t?this.attr("cx"):this.attr("cx",t)},cy:function(t){return null==t?this.attr("cy"):this.attr("cy",t)},width:function(t){return null==t?2*this.rx():this.rx(new a.Number(t).divide(2))},height:function(t){return null==t?2*this.ry():this.ry(new a.Number(t).divide(2))},size:function(t,e){var i=u(this,t,e);return this.rx(new a.Number(i.width).divide(2)).ry(new a.Number(i.height).divide(2))}}),a.Line=a.invent({create:"line",inherit:a.Shape,extend:{array:function(){return new a.PointArray([[this.attr("x1"),this.attr("y1")],[this.attr("x2"),this.attr("y2")]])},plot:function(t,e,i,s){return null==t?this.array():(t=void 0!==e?{x1:t,y1:e,x2:i,y2:s}:new a.PointArray(t).toLine(),this.attr(t))},move:function(t,e){return this.attr(this.array().move(t,e).toLine())},size:function(t,e){var i=u(this,t,e);return this.attr(this.array().size(i.width,i.height).toLine())}},construct:{line:function(t,e,i,s){return a.Line.prototype.plot.apply(this.put(new a.Line),null!=t?[t,e,i,s]:[0,0,0,0])}}}),a.Polyline=a.invent({create:"polyline",inherit:a.Shape,construct:{polyline:function(t){return this.put(new a.Polyline).plot(t||new a.PointArray)}}}),a.Polygon=a.invent({create:"polygon",inherit:a.Shape,construct:{polygon:function(t){return this.put(new a.Polygon).plot(t||new a.PointArray)}}}),a.extend(a.Polyline,a.Polygon,{array:function(){return this._array||(this._array=new a.PointArray(this.attr("points")))},plot:function(t){return null==t?this.array():this.clear().attr("points","string"==typeof t?t:this._array=new a.PointArray(t))},clear:function(){return delete this._array,this},move:function(t,e){return this.attr("points",this.array().move(t,e))},size:function(t,e){var i=u(this,t,e);return this.attr("points",this.array().size(i.width,i.height))}}),a.extend(a.Line,a.Polyline,a.Polygon,{morphArray:a.PointArray,x:function(t){return null==t?this.bbox().x:this.move(t,this.bbox().y)},y:function(t){return null==t?this.bbox().y:this.move(this.bbox().x,t)},width:function(t){var e=this.bbox();return null==t?e.width:this.size(t,e.height)},height:function(t){var e=this.bbox();return null==t?e.height:this.size(e.width,t)}}),a.Path=a.invent({create:"path",inherit:a.Shape,extend:{morphArray:a.PathArray,array:function(){return this._array||(this._array=new a.PathArray(this.attr("d")))},plot:function(t){return null==t?this.array():this.clear().attr("d","string"==typeof t?t:this._array=new a.PathArray(t))},clear:function(){return delete this._array,this}},construct:{path:function(t){return this.put(new a.Path).plot(t||new a.PathArray)}}}),a.Image=a.invent({create:"image",inherit:a.Shape,extend:{load:function(t){if(!t)return this;var i=this,s=new e.Image;return a.on(s,"load",(function(){a.off(s);var e=i.parent(a.Pattern);null!==e&&(0==i.width()&&0==i.height()&&i.size(s.width,s.height),e&&0==e.width()&&0==e.height()&&e.size(i.width(),i.height()),"function"==typeof i._loaded&&i._loaded.call(i,{width:s.width,height:s.height,ratio:s.width/s.height,url:t}));})),a.on(s,"error",(function(t){a.off(s),"function"==typeof i._error&&i._error.call(i,t);})),this.attr("href",s.src=this.src=t,a.xlink)},loaded:function(t){return this._loaded=t,this},error:function(t){return this._error=t,this}},construct:{image:function(t,e,i){return this.put(new a.Image).load(t).size(e||0,i||e||0)}}}),a.Text=a.invent({create:function(){this.constructor.call(this,a.create("text")),this.dom.leading=new a.Number(1.3),this._rebuild=!0,this._build=!1,this.attr("font-family",a.defaults.attrs["font-family"]);},inherit:a.Shape,extend:{x:function(t){return null==t?this.attr("x"):this.attr("x",t)},text:function(t){if(void 0===t){t="";for(var e=this.node.childNodes,i=0,s=e.length;i<s;++i)0!=i&&3!=e[i].nodeType&&1==a.adopt(e[i]).dom.newLined&&(t+="\n"),t+=e[i].textContent;return t}if(this.clear().build(!0),"function"==typeof t)t.call(this,this);else {i=0;for(var r=(t=t.split("\n")).length;i<r;i++)this.tspan(t[i]).newLine();}return this.build(!1).rebuild()},size:function(t){return this.attr("font-size",t).rebuild()},leading:function(t){return null==t?this.dom.leading:(this.dom.leading=new a.Number(t),this.rebuild())},lines:function(){var t=(this.textPath&&this.textPath()||this).node,e=a.utils.map(a.utils.filterSVGElements(t.childNodes),(function(t){return a.adopt(t)}));return new a.Set(e)},rebuild:function(t){if("boolean"==typeof t&&(this._rebuild=t),this._rebuild){var e=this,i=0,s=this.dom.leading*new a.Number(this.attr("font-size"));this.lines().each((function(){this.dom.newLined&&(e.textPath()||this.attr("x",e.attr("x")),"\n"==this.text()?i+=s:(this.attr("dy",s+i),i=0));})),this.fire("rebuild");}return this},build:function(t){return this._build=!!t,this},setData:function(t){return this.dom=t,this.dom.leading=new a.Number(t.leading||1.3),this}},construct:{text:function(t){return this.put(new a.Text).text(t)},plain:function(t){return this.put(new a.Text).plain(t)}}}),a.Tspan=a.invent({create:"tspan",inherit:a.Shape,extend:{text:function(t){return null==t?this.node.textContent+(this.dom.newLined?"\n":""):("function"==typeof t?t.call(this,this):this.plain(t),this)},dx:function(t){return this.attr("dx",t)},dy:function(t){return this.attr("dy",t)},newLine:function(){var t=this.parent(a.Text);return this.dom.newLined=!0,this.dy(t.dom.leading*t.attr("font-size")).attr("x",t.x())}}}),a.extend(a.Text,a.Tspan,{plain:function(t){return !1===this._build&&this.clear(),this.node.appendChild(i.createTextNode(t)),this},tspan:function(t){var e=(this.textPath&&this.textPath()||this).node,i=new a.Tspan;return !1===this._build&&this.clear(),e.appendChild(i.node),i.text(t)},clear:function(){for(var t=(this.textPath&&this.textPath()||this).node;t.hasChildNodes();)t.removeChild(t.lastChild);return this},length:function(){return this.node.getComputedTextLength()}}),a.TextPath=a.invent({create:"textPath",inherit:a.Parent,parent:a.Text,construct:{morphArray:a.PathArray,array:function(){var t=this.track();return t?t.array():null},plot:function(t){var e=this.track(),i=null;return e&&(i=e.plot(t)),null==t?i:this},track:function(){var t=this.textPath();if(t)return t.reference("href")},textPath:function(){if(this.node.firstChild&&"textPath"==this.node.firstChild.nodeName)return a.adopt(this.node.firstChild)}}}),a.Nested=a.invent({create:function(){this.constructor.call(this,a.create("svg")),this.style("overflow","visible");},inherit:a.Container,construct:{nested:function(){return this.put(new a.Nested)}}});var l={stroke:["color","width","opacity","linecap","linejoin","miterlimit","dasharray","dashoffset"],fill:["color","opacity","rule"],prefix:function(t,e){return "color"==e?t:t+"-"+e}};function h(t,e,i,s){return i+s.replace(a.regex.dots," .")}function c(t){return t.toLowerCase().replace(/-(.)/g,(function(t,e){return e.toUpperCase()}))}function d(t){return t.charAt(0).toUpperCase()+t.slice(1)}function g(t){var e=t.toString(16);return 1==e.length?"0"+e:e}function u(t,e,i){if(null==e||null==i){var a=t.bbox();null==e?e=a.width/a.height*i:null==i&&(i=a.height/a.width*e);}return {width:e,height:i}}function f(t,e,i){return {x:e*t.a+i*t.c+0,y:e*t.b+i*t.d+0}}function p(t){return {a:t[0],b:t[1],c:t[2],d:t[3],e:t[4],f:t[5]}}function x(t){for(var i=t.childNodes.length-1;i>=0;i--)t.childNodes[i]instanceof e.SVGElement&&x(t.childNodes[i]);return a.adopt(t).id(a.eid(t.nodeName))}function b(t){return null==t.x&&(t.x=0,t.y=0,t.width=0,t.height=0),t.w=t.width,t.h=t.height,t.x2=t.x+t.width,t.y2=t.y+t.height,t.cx=t.x+t.width/2,t.cy=t.y+t.height/2,t}function m(t){return Math.abs(t)>1e-37?t:0}["fill","stroke"].forEach((function(t){var e={};e[t]=function(e){if(void 0===e)return this;if("string"==typeof e||a.Color.isRgb(e)||e&&"function"==typeof e.fill)this.attr(t,e);else for(var i=l[t].length-1;i>=0;i--)null!=e[l[t][i]]&&this.attr(l.prefix(t,l[t][i]),e[l[t][i]]);return this},a.extend(a.Element,a.FX,e);})),a.extend(a.Element,a.FX,{translate:function(t,e){return this.transform({x:t,y:e})},matrix:function(t){return this.attr("transform",new a.Matrix(6==arguments.length?[].slice.call(arguments):t))},opacity:function(t){return this.attr("opacity",t)},dx:function(t){return this.x(new a.Number(t).plus(this instanceof a.FX?0:this.x()),!0)},dy:function(t){return this.y(new a.Number(t).plus(this instanceof a.FX?0:this.y()),!0)}}),a.extend(a.Path,{length:function(){return this.node.getTotalLength()},pointAt:function(t){return this.node.getPointAtLength(t)}}),a.Set=a.invent({create:function(t){Array.isArray(t)?this.members=t:this.clear();},extend:{add:function(){for(var t=[].slice.call(arguments),e=0,i=t.length;e<i;e++)this.members.push(t[e]);return this},remove:function(t){var e=this.index(t);return e>-1&&this.members.splice(e,1),this},each:function(t){for(var e=0,i=this.members.length;e<i;e++)t.apply(this.members[e],[e,this.members]);return this},clear:function(){return this.members=[],this},length:function(){return this.members.length},has:function(t){return this.index(t)>=0},index:function(t){return this.members.indexOf(t)},get:function(t){return this.members[t]},first:function(){return this.get(0)},last:function(){return this.get(this.members.length-1)},valueOf:function(){return this.members}},construct:{set:function(t){return new a.Set(t)}}}),a.FX.Set=a.invent({create:function(t){this.set=t;}}),a.Set.inherit=function(){var t=[];for(var e in a.Shape.prototype)"function"==typeof a.Shape.prototype[e]&&"function"!=typeof a.Set.prototype[e]&&t.push(e);for(var e in t.forEach((function(t){a.Set.prototype[t]=function(){for(var e=0,i=this.members.length;e<i;e++)this.members[e]&&"function"==typeof this.members[e][t]&&this.members[e][t].apply(this.members[e],arguments);return "animate"==t?this.fx||(this.fx=new a.FX.Set(this)):this};})),t=[],a.FX.prototype)"function"==typeof a.FX.prototype[e]&&"function"!=typeof a.FX.Set.prototype[e]&&t.push(e);t.forEach((function(t){a.FX.Set.prototype[t]=function(){for(var e=0,i=this.set.members.length;e<i;e++)this.set.members[e].fx[t].apply(this.set.members[e].fx,arguments);return this};}));},a.extend(a.Element,{}),a.extend(a.Element,{remember:function(e,i){if("object"===t(arguments[0]))for(var a in e)this.remember(a,e[a]);else {if(1==arguments.length)return this.memory()[e];this.memory()[e]=i;}return this},forget:function(){if(0==arguments.length)this._memory={};else for(var t=arguments.length-1;t>=0;t--)delete this.memory()[arguments[t]];return this},memory:function(){return this._memory||(this._memory={})}}),a.get=function(t){var e=i.getElementById(function(t){var e=(t||"").toString().match(a.regex.reference);if(e)return e[1]}(t)||t);return a.adopt(e)},a.select=function(t,e){return new a.Set(a.utils.map((e||i).querySelectorAll(t),(function(t){return a.adopt(t)})))},a.extend(a.Parent,{select:function(t){return a.select(t,this.node)}});var v="abcdef".split("");if("function"!=typeof e.CustomEvent){var y=function(t,e){e=e||{bubbles:!1,cancelable:!1,detail:void 0};var a=i.createEvent("CustomEvent");return a.initCustomEvent(t,e.bubbles,e.cancelable,e.detail),a};y.prototype=e.Event.prototype,a.CustomEvent=y;}else a.CustomEvent=e.CustomEvent;return a},"object"===(t(exports))&&"undefined"!='object'?module.exports=Tt.document?zt(Tt,Tt.document):function(t){return zt(t,t.document)}:Tt.SVG=zt(Tt,Tt.document),
/*! svg.filter.js - v2.0.2 - 2016-02-24
  * https://github.com/wout/svg.filter.js
  * Copyright (c) 2016 Wout Fierens; Licensed MIT */
function(){SVG.Filter=SVG.invent({create:"filter",inherit:SVG.Parent,extend:{source:"SourceGraphic",sourceAlpha:"SourceAlpha",background:"BackgroundImage",backgroundAlpha:"BackgroundAlpha",fill:"FillPaint",stroke:"StrokePaint",autoSetIn:!0,put:function(t,e){return this.add(t,e),!t.attr("in")&&this.autoSetIn&&t.attr("in",this.source),t.attr("result")||t.attr("result",t),t},blend:function(t,e,i){return this.put(new SVG.BlendEffect(t,e,i))},colorMatrix:function(t,e){return this.put(new SVG.ColorMatrixEffect(t,e))},convolveMatrix:function(t){return this.put(new SVG.ConvolveMatrixEffect(t))},componentTransfer:function(t){return this.put(new SVG.ComponentTransferEffect(t))},composite:function(t,e,i){return this.put(new SVG.CompositeEffect(t,e,i))},flood:function(t,e){return this.put(new SVG.FloodEffect(t,e))},offset:function(t,e){return this.put(new SVG.OffsetEffect(t,e))},image:function(t){return this.put(new SVG.ImageEffect(t))},merge:function(){var t=[void 0];for(var e in arguments)t.push(arguments[e]);return this.put(new(SVG.MergeEffect.bind.apply(SVG.MergeEffect,t)))},gaussianBlur:function(t,e){return this.put(new SVG.GaussianBlurEffect(t,e))},morphology:function(t,e){return this.put(new SVG.MorphologyEffect(t,e))},diffuseLighting:function(t,e,i){return this.put(new SVG.DiffuseLightingEffect(t,e,i))},displacementMap:function(t,e,i,a,s){return this.put(new SVG.DisplacementMapEffect(t,e,i,a,s))},specularLighting:function(t,e,i,a){return this.put(new SVG.SpecularLightingEffect(t,e,i,a))},tile:function(){return this.put(new SVG.TileEffect)},turbulence:function(t,e,i,a,s){return this.put(new SVG.TurbulenceEffect(t,e,i,a,s))},toString:function(){return "url(#"+this.attr("id")+")"}}}),SVG.extend(SVG.Defs,{filter:function(t){var e=this.put(new SVG.Filter);return "function"==typeof t&&t.call(e,e),e}}),SVG.extend(SVG.Container,{filter:function(t){return this.defs().filter(t)}}),SVG.extend(SVG.Element,SVG.G,SVG.Nested,{filter:function(t){return this.filterer=t instanceof SVG.Element?t:this.doc().filter(t),this.doc()&&this.filterer.doc()!==this.doc()&&this.doc().defs().add(this.filterer),this.attr("filter",this.filterer),this.filterer},unfilter:function(t){return this.filterer&&!0===t&&this.filterer.remove(),delete this.filterer,this.attr("filter",null)}}),SVG.Effect=SVG.invent({create:function(){this.constructor.call(this);},inherit:SVG.Element,extend:{in:function(t){return null==t?this.parent()&&this.parent().select('[result="'+this.attr("in")+'"]').get(0)||this.attr("in"):this.attr("in",t)},result:function(t){return null==t?this.attr("result"):this.attr("result",t)},toString:function(){return this.result()}}}),SVG.ParentEffect=SVG.invent({create:function(){this.constructor.call(this);},inherit:SVG.Parent,extend:{in:function(t){return null==t?this.parent()&&this.parent().select('[result="'+this.attr("in")+'"]').get(0)||this.attr("in"):this.attr("in",t)},result:function(t){return null==t?this.attr("result"):this.attr("result",t)},toString:function(){return this.result()}}});var t={blend:function(t,e){return this.parent()&&this.parent().blend(this,t,e)},colorMatrix:function(t,e){return this.parent()&&this.parent().colorMatrix(t,e).in(this)},convolveMatrix:function(t){return this.parent()&&this.parent().convolveMatrix(t).in(this)},componentTransfer:function(t){return this.parent()&&this.parent().componentTransfer(t).in(this)},composite:function(t,e){return this.parent()&&this.parent().composite(this,t,e)},flood:function(t,e){return this.parent()&&this.parent().flood(t,e)},offset:function(t,e){return this.parent()&&this.parent().offset(t,e).in(this)},image:function(t){return this.parent()&&this.parent().image(t)},merge:function(){return this.parent()&&this.parent().merge.apply(this.parent(),[this].concat(arguments))},gaussianBlur:function(t,e){return this.parent()&&this.parent().gaussianBlur(t,e).in(this)},morphology:function(t,e){return this.parent()&&this.parent().morphology(t,e).in(this)},diffuseLighting:function(t,e,i){return this.parent()&&this.parent().diffuseLighting(t,e,i).in(this)},displacementMap:function(t,e,i,a){return this.parent()&&this.parent().displacementMap(this,t,e,i,a)},specularLighting:function(t,e,i,a){return this.parent()&&this.parent().specularLighting(t,e,i,a).in(this)},tile:function(){return this.parent()&&this.parent().tile().in(this)},turbulence:function(t,e,i,a,s){return this.parent()&&this.parent().turbulence(t,e,i,a,s).in(this)}};SVG.extend(SVG.Effect,t),SVG.extend(SVG.ParentEffect,t),SVG.ChildEffect=SVG.invent({create:function(){this.constructor.call(this);},inherit:SVG.Element,extend:{in:function(t){this.attr("in",t);}}});var e={blend:function(t,e,i){this.attr({in:t,in2:e,mode:i||"normal"});},colorMatrix:function(t,e){"matrix"==t&&(e=s(e)),this.attr({type:t,values:void 0===e?null:e});},convolveMatrix:function(t){t=s(t),this.attr({order:Math.sqrt(t.split(" ").length),kernelMatrix:t});},composite:function(t,e,i){this.attr({in:t,in2:e,operator:i});},flood:function(t,e){this.attr("flood-color",t),null!=e&&this.attr("flood-opacity",e);},offset:function(t,e){this.attr({dx:t,dy:e});},image:function(t){this.attr("href",t,SVG.xlink);},displacementMap:function(t,e,i,a,s){this.attr({in:t,in2:e,scale:i,xChannelSelector:a,yChannelSelector:s});},gaussianBlur:function(t,e){null!=t||null!=e?this.attr("stdDeviation",r(Array.prototype.slice.call(arguments))):this.attr("stdDeviation","0 0");},morphology:function(t,e){this.attr({operator:t,radius:e});},tile:function(){},turbulence:function(t,e,i,a,s){this.attr({numOctaves:e,seed:i,stitchTiles:a,baseFrequency:t,type:s});}},i={merge:function(){var t;if(arguments[0]instanceof SVG.Set){var e=this;arguments[0].each((function(t){this instanceof SVG.MergeNode?e.put(this):(this instanceof SVG.Effect||this instanceof SVG.ParentEffect)&&e.put(new SVG.MergeNode(this));}));}else {t=Array.isArray(arguments[0])?arguments[0]:arguments;for(var i=0;i<t.length;i++)t[i]instanceof SVG.MergeNode?this.put(t[i]):this.put(new SVG.MergeNode(t[i]));}},componentTransfer:function(t){if(this.rgb=new SVG.Set,["r","g","b","a"].forEach(function(t){this[t]=new(SVG["Func"+t.toUpperCase()])("identity"),this.rgb.add(this[t]),this.node.appendChild(this[t].node);}.bind(this)),t)for(var e in t.rgb&&(["r","g","b"].forEach(function(e){this[e].attr(t.rgb);}.bind(this)),delete t.rgb),t)this[e].attr(t[e]);},diffuseLighting:function(t,e,i){this.attr({surfaceScale:t,diffuseConstant:e,kernelUnitLength:i});},specularLighting:function(t,e,i,a){this.attr({surfaceScale:t,diffuseConstant:e,specularExponent:i,kernelUnitLength:a});}},a={distantLight:function(t,e){this.attr({azimuth:t,elevation:e});},pointLight:function(t,e,i){this.attr({x:t,y:e,z:i});},spotLight:function(t,e,i,a,s,r){this.attr({x:t,y:e,z:i,pointsAtX:a,pointsAtY:s,pointsAtZ:r});},mergeNode:function(t){this.attr("in",t);}};function s(t){return Array.isArray(t)&&(t=new SVG.Array(t)),t.toString().replace(/^\s+/,"").replace(/\s+$/,"").replace(/\s+/g," ")}function r(t){if(!Array.isArray(t))return t;for(var e=0,i=t.length,a=[];e<i;e++)a.push(t[e]);return a.join(" ")}function n(){var t=function(){};for(var e in "function"==typeof arguments[arguments.length-1]&&(t=arguments[arguments.length-1],Array.prototype.splice.call(arguments,arguments.length-1,1)),arguments)for(var i in arguments[e])t(arguments[e][i],i,arguments[e]);}["r","g","b","a"].forEach((function(t){a["Func"+t.toUpperCase()]=function(t){switch(this.attr("type",t),t){case"table":this.attr("tableValues",arguments[1]);break;case"linear":this.attr("slope",arguments[1]),this.attr("intercept",arguments[2]);break;case"gamma":this.attr("amplitude",arguments[1]),this.attr("exponent",arguments[2]),this.attr("offset",arguments[2]);}};})),n(e,(function(t,e){var i=e.charAt(0).toUpperCase()+e.slice(1);SVG[i+"Effect"]=SVG.invent({create:function(){this.constructor.call(this,SVG.create("fe"+i)),t.apply(this,arguments),this.result(this.attr("id")+"Out");},inherit:SVG.Effect,extend:{}});})),n(i,(function(t,e){var i=e.charAt(0).toUpperCase()+e.slice(1);SVG[i+"Effect"]=SVG.invent({create:function(){this.constructor.call(this,SVG.create("fe"+i)),t.apply(this,arguments),this.result(this.attr("id")+"Out");},inherit:SVG.ParentEffect,extend:{}});})),n(a,(function(t,e){var i=e.charAt(0).toUpperCase()+e.slice(1);SVG[i]=SVG.invent({create:function(){this.constructor.call(this,SVG.create("fe"+i)),t.apply(this,arguments);},inherit:SVG.ChildEffect,extend:{}});})),SVG.extend(SVG.MergeEffect,{in:function(t){return t instanceof SVG.MergeNode?this.add(t,0):this.add(new SVG.MergeNode(t),0),this}}),SVG.extend(SVG.CompositeEffect,SVG.BlendEffect,SVG.DisplacementMapEffect,{in2:function(t){return null==t?this.parent()&&this.parent().select('[result="'+this.attr("in2")+'"]').get(0)||this.attr("in2"):this.attr("in2",t)}}),SVG.filter={sepiatone:[.343,.669,.119,0,0,.249,.626,.13,0,0,.172,.334,.111,0,0,0,0,0,1,0]};}.call(void 0),function(){function t(t,s,r,n,o,l,h){for(var c=t.slice(s,r||h),d=n.slice(o,l||h),g=0,u={pos:[0,0],start:[0,0]},f={pos:[0,0],start:[0,0]};;){if(c[g]=e.call(u,c[g]),d[g]=e.call(f,d[g]),c[g][0]!=d[g][0]||"M"==c[g][0]||"A"==c[g][0]&&(c[g][4]!=d[g][4]||c[g][5]!=d[g][5])?(Array.prototype.splice.apply(c,[g,1].concat(a.call(u,c[g]))),Array.prototype.splice.apply(d,[g,1].concat(a.call(f,d[g])))):(c[g]=i.call(u,c[g]),d[g]=i.call(f,d[g])),++g==c.length&&g==d.length)break;g==c.length&&c.push(["C",u.pos[0],u.pos[1],u.pos[0],u.pos[1],u.pos[0],u.pos[1]]),g==d.length&&d.push(["C",f.pos[0],f.pos[1],f.pos[0],f.pos[1],f.pos[0],f.pos[1]]);}return {start:c,dest:d}}function e(t){switch(t[0]){case"z":case"Z":t[0]="L",t[1]=this.start[0],t[2]=this.start[1];break;case"H":t[0]="L",t[2]=this.pos[1];break;case"V":t[0]="L",t[2]=t[1],t[1]=this.pos[0];break;case"T":t[0]="Q",t[3]=t[1],t[4]=t[2],t[1]=this.reflection[1],t[2]=this.reflection[0];break;case"S":t[0]="C",t[6]=t[4],t[5]=t[3],t[4]=t[2],t[3]=t[1],t[2]=this.reflection[1],t[1]=this.reflection[0];}return t}function i(t){var e=t.length;return this.pos=[t[e-2],t[e-1]],-1!="SCQT".indexOf(t[0])&&(this.reflection=[2*this.pos[0]-t[e-4],2*this.pos[1]-t[e-3]]),t}function a(t){var e=[t];switch(t[0]){case"M":return this.pos=this.start=[t[1],t[2]],e;case"L":t[5]=t[3]=t[1],t[6]=t[4]=t[2],t[1]=this.pos[0],t[2]=this.pos[1];break;case"Q":t[6]=t[4],t[5]=t[3],t[4]=1*t[4]/3+2*t[2]/3,t[3]=1*t[3]/3+2*t[1]/3,t[2]=1*this.pos[1]/3+2*t[2]/3,t[1]=1*this.pos[0]/3+2*t[1]/3;break;case"A":t=(e=function(t,e){var i,a,s,r,n,o,l,h,c,d,g,u,f,p,x,b,m,v,y,w,k,A,S,C,L,P,T=Math.abs(e[1]),z=Math.abs(e[2]),I=e[3]%360,M=e[4],E=e[5],X=e[6],Y=e[7],F=new SVG.Point(t),D=new SVG.Point(X,Y),R=[];if(0===T||0===z||F.x===D.x&&F.y===D.y)return [["C",F.x,F.y,D.x,D.y,D.x,D.y]];i=new SVG.Point((F.x-D.x)/2,(F.y-D.y)/2).transform((new SVG.Matrix).rotate(I)),(a=i.x*i.x/(T*T)+i.y*i.y/(z*z))>1&&(a=Math.sqrt(a),T*=a,z*=a);s=(new SVG.Matrix).rotate(I).scale(1/T,1/z).rotate(-I),F=F.transform(s),D=D.transform(s),r=[D.x-F.x,D.y-F.y],o=r[0]*r[0]+r[1]*r[1],n=Math.sqrt(o),r[0]/=n,r[1]/=n,l=o<4?Math.sqrt(1-o/4):0,M===E&&(l*=-1);h=new SVG.Point((D.x+F.x)/2+l*-r[1],(D.y+F.y)/2+l*r[0]),c=new SVG.Point(F.x-h.x,F.y-h.y),d=new SVG.Point(D.x-h.x,D.y-h.y),g=Math.acos(c.x/Math.sqrt(c.x*c.x+c.y*c.y)),c.y<0&&(g*=-1);u=Math.acos(d.x/Math.sqrt(d.x*d.x+d.y*d.y)),d.y<0&&(u*=-1);E&&g>u&&(u+=2*Math.PI);!E&&g<u&&(u-=2*Math.PI);for(p=Math.ceil(2*Math.abs(g-u)/Math.PI),b=[],m=g,f=(u-g)/p,x=4*Math.tan(f/4)/3,k=0;k<=p;k++)y=Math.cos(m),v=Math.sin(m),w=new SVG.Point(h.x+y,h.y+v),b[k]=[new SVG.Point(w.x+x*v,w.y-x*y),w,new SVG.Point(w.x-x*v,w.y+x*y)],m+=f;for(b[0][0]=b[0][1].clone(),b[b.length-1][2]=b[b.length-1][1].clone(),s=(new SVG.Matrix).rotate(I).scale(T,z).rotate(-I),k=0,A=b.length;k<A;k++)b[k][0]=b[k][0].transform(s),b[k][1]=b[k][1].transform(s),b[k][2]=b[k][2].transform(s);for(k=1,A=b.length;k<A;k++)w=b[k-1][2],S=w.x,C=w.y,w=b[k][0],L=w.x,P=w.y,w=b[k][1],X=w.x,Y=w.y,R.push(["C",S,C,L,P,X,Y]);return R}(this.pos,t))[0];}return t[0]="C",this.pos=[t[5],t[6]],this.reflection=[2*t[5]-t[3],2*t[6]-t[4]],e}function s(t,e){if(!1===e)return !1;for(var i=e,a=t.length;i<a;++i)if("M"==t[i][0])return i;return !1}SVG.extend(SVG.PathArray,{morph:function(e){for(var i=this.value,a=this.parse(e),r=0,n=0,o=!1,l=!1;!1!==r||!1!==n;){var h;o=s(i,!1!==r&&r+1),l=s(a,!1!==n&&n+1),!1===r&&(r=0==(h=new SVG.PathArray(c.start).bbox()).height||0==h.width?i.push(i[0])-1:i.push(["M",h.x+h.width/2,h.y+h.height/2])-1),!1===n&&(n=0==(h=new SVG.PathArray(c.dest).bbox()).height||0==h.width?a.push(a[0])-1:a.push(["M",h.x+h.width/2,h.y+h.height/2])-1);var c=t(i,r,o,a,n,l);i=i.slice(0,r).concat(c.start,!1===o?[]:i.slice(o)),a=a.slice(0,n).concat(c.dest,!1===l?[]:a.slice(l)),r=!1!==o&&r+c.start.length,n=!1!==l&&n+c.dest.length;}return this.value=i,this.destination=new SVG.PathArray,this.destination.value=a,this}});}(),
/*! svg.draggable.js - v2.2.2 - 2019-01-08
  * https://github.com/svgdotjs/svg.draggable.js
  * Copyright (c) 2019 Wout Fierens; Licensed MIT */
function(){function t(t){t.remember("_draggable",this),this.el=t;}t.prototype.init=function(t,e){var i=this;this.constraint=t,this.value=e,this.el.on("mousedown.drag",(function(t){i.start(t);})),this.el.on("touchstart.drag",(function(t){i.start(t);}));},t.prototype.transformPoint=function(t,e){var i=(t=t||window.event).changedTouches&&t.changedTouches[0]||t;return this.p.x=i.clientX-(e||0),this.p.y=i.clientY,this.p.matrixTransform(this.m)},t.prototype.getBBox=function(){var t=this.el.bbox();return this.el instanceof SVG.Nested&&(t=this.el.rbox()),(this.el instanceof SVG.G||this.el instanceof SVG.Use||this.el instanceof SVG.Nested)&&(t.x=this.el.x(),t.y=this.el.y()),t},t.prototype.start=function(t){if("click"!=t.type&&"mousedown"!=t.type&&"mousemove"!=t.type||1==(t.which||t.buttons)){var e=this;if(this.el.fire("beforedrag",{event:t,handler:this}),!this.el.event().defaultPrevented){t.preventDefault(),t.stopPropagation(),this.parent=this.parent||this.el.parent(SVG.Nested)||this.el.parent(SVG.Doc),this.p=this.parent.node.createSVGPoint(),this.m=this.el.node.getScreenCTM().inverse();var i,a=this.getBBox();if(this.el instanceof SVG.Text)switch(i=this.el.node.getComputedTextLength(),this.el.attr("text-anchor")){case"middle":i/=2;break;case"start":i=0;}this.startPoints={point:this.transformPoint(t,i),box:a,transform:this.el.transform()},SVG.on(window,"mousemove.drag",(function(t){e.drag(t);})),SVG.on(window,"touchmove.drag",(function(t){e.drag(t);})),SVG.on(window,"mouseup.drag",(function(t){e.end(t);})),SVG.on(window,"touchend.drag",(function(t){e.end(t);})),this.el.fire("dragstart",{event:t,p:this.startPoints.point,m:this.m,handler:this});}}},t.prototype.drag=function(t){var e=this.getBBox(),i=this.transformPoint(t),a=this.startPoints.box.x+i.x-this.startPoints.point.x,s=this.startPoints.box.y+i.y-this.startPoints.point.y,r=this.constraint,n=i.x-this.startPoints.point.x,o=i.y-this.startPoints.point.y;if(this.el.fire("dragmove",{event:t,p:i,m:this.m,handler:this}),this.el.event().defaultPrevented)return i;if("function"==typeof r){var l=r.call(this.el,a,s,this.m);"boolean"==typeof l&&(l={x:l,y:l}),!0===l.x?this.el.x(a):!1!==l.x&&this.el.x(l.x),!0===l.y?this.el.y(s):!1!==l.y&&this.el.y(l.y);}else "object"==typeof r&&(null!=r.minX&&a<r.minX?n=(a=r.minX)-this.startPoints.box.x:null!=r.maxX&&a>r.maxX-e.width&&(n=(a=r.maxX-e.width)-this.startPoints.box.x),null!=r.minY&&s<r.minY?o=(s=r.minY)-this.startPoints.box.y:null!=r.maxY&&s>r.maxY-e.height&&(o=(s=r.maxY-e.height)-this.startPoints.box.y),null!=r.snapToGrid&&(a-=a%r.snapToGrid,s-=s%r.snapToGrid,n-=n%r.snapToGrid,o-=o%r.snapToGrid),this.el instanceof SVG.G?this.el.matrix(this.startPoints.transform).transform({x:n,y:o},!0):this.el.move(a,s));return i},t.prototype.end=function(t){var e=this.drag(t);this.el.fire("dragend",{event:t,p:e,m:this.m,handler:this}),SVG.off(window,"mousemove.drag"),SVG.off(window,"touchmove.drag"),SVG.off(window,"mouseup.drag"),SVG.off(window,"touchend.drag");},SVG.extend(SVG.Element,{draggable:function(e,i){"function"!=typeof e&&"object"!=typeof e||(i=e,e=!0);var a=this.remember("_draggable")||new t(this);return (e=void 0===e||e)?a.init(i||{},e):(this.off("mousedown.drag"),this.off("touchstart.drag")),this}});}.call(void 0),function(){function t(t){this.el=t,t.remember("_selectHandler",this),this.pointSelection={isSelected:!1},this.rectSelection={isSelected:!1},this.pointsList={lt:[0,0],rt:["width",0],rb:["width","height"],lb:[0,"height"],t:["width",0],r:["width","height"],b:["width","height"],l:[0,"height"]},this.pointCoord=function(t,e,i){var a="string"!=typeof t?t:e[t];return i?a/2:a},this.pointCoords=function(t,e){var i=this.pointsList[t];return {x:this.pointCoord(i[0],e,"t"===t||"b"===t),y:this.pointCoord(i[1],e,"r"===t||"l"===t)}};}t.prototype.init=function(t,e){var i=this.el.bbox();this.options={};var a=this.el.selectize.defaults.points;for(var s in this.el.selectize.defaults)this.options[s]=this.el.selectize.defaults[s],void 0!==e[s]&&(this.options[s]=e[s]);var r=["points","pointsExclude"];for(var s in r){var n=this.options[r[s]];"string"==typeof n?n=n.length>0?n.split(/\s*,\s*/i):[]:"boolean"==typeof n&&"points"===r[s]&&(n=n?a:[]),this.options[r[s]]=n;}this.options.points=[a,this.options.points].reduce((function(t,e){return t.filter((function(t){return e.indexOf(t)>-1}))})),this.options.points=[this.options.points,this.options.pointsExclude].reduce((function(t,e){return t.filter((function(t){return e.indexOf(t)<0}))})),this.parent=this.el.parent(),this.nested=this.nested||this.parent.group(),this.nested.matrix(new SVG.Matrix(this.el).translate(i.x,i.y)),this.options.deepSelect&&-1!==["line","polyline","polygon"].indexOf(this.el.type)?this.selectPoints(t):this.selectRect(t),this.observe(),this.cleanup();},t.prototype.selectPoints=function(t){return this.pointSelection.isSelected=t,this.pointSelection.set||(this.pointSelection.set=this.parent.set(),this.drawPoints()),this},t.prototype.getPointArray=function(){var t=this.el.bbox();return this.el.array().valueOf().map((function(e){return [e[0]-t.x,e[1]-t.y]}))},t.prototype.drawPoints=function(){for(var t=this,e=this.getPointArray(),i=0,a=e.length;i<a;++i){var s=function(e){return function(i){(i=i||window.event).preventDefault?i.preventDefault():i.returnValue=!1,i.stopPropagation();var a=i.pageX||i.touches[0].pageX,s=i.pageY||i.touches[0].pageY;t.el.fire("point",{x:a,y:s,i:e,event:i});}}(i),r=this.drawPoint(e[i][0],e[i][1]).addClass(this.options.classPoints).addClass(this.options.classPoints+"_point").on("touchstart",s).on("mousedown",s);this.pointSelection.set.add(r);}},t.prototype.drawPoint=function(t,e){var i=this.options.pointType;switch(i){case"circle":return this.drawCircle(t,e);case"rect":return this.drawRect(t,e);default:if("function"==typeof i)return i.call(this,t,e);throw new Error("Unknown "+i+" point type!")}},t.prototype.drawCircle=function(t,e){return this.nested.circle(this.options.pointSize).center(t,e)},t.prototype.drawRect=function(t,e){return this.nested.rect(this.options.pointSize,this.options.pointSize).center(t,e)},t.prototype.updatePointSelection=function(){var t=this.getPointArray();this.pointSelection.set.each((function(e){this.cx()===t[e][0]&&this.cy()===t[e][1]||this.center(t[e][0],t[e][1]);}));},t.prototype.updateRectSelection=function(){var t=this,e=this.el.bbox();if(this.rectSelection.set.get(0).attr({width:e.width,height:e.height}),this.options.points.length&&this.options.points.map((function(i,a){var s=t.pointCoords(i,e);t.rectSelection.set.get(a+1).center(s.x,s.y);})),this.options.rotationPoint){var i=this.rectSelection.set.length();this.rectSelection.set.get(i-1).center(e.width/2,20);}},t.prototype.selectRect=function(t){var e=this,i=this.el.bbox();function a(t){return function(i){(i=i||window.event).preventDefault?i.preventDefault():i.returnValue=!1,i.stopPropagation();var a=i.pageX||i.touches[0].pageX,s=i.pageY||i.touches[0].pageY;e.el.fire(t,{x:a,y:s,event:i});}}if(this.rectSelection.isSelected=t,this.rectSelection.set=this.rectSelection.set||this.parent.set(),this.rectSelection.set.get(0)||this.rectSelection.set.add(this.nested.rect(i.width,i.height).addClass(this.options.classRect)),this.options.points.length&&this.rectSelection.set.length()<2){this.options.points.map((function(t,s){var r=e.pointCoords(t,i),n=e.drawPoint(r.x,r.y).attr("class",e.options.classPoints+"_"+t).on("mousedown",a(t)).on("touchstart",a(t));e.rectSelection.set.add(n);})),this.rectSelection.set.each((function(){this.addClass(e.options.classPoints);}));}if(this.options.rotationPoint&&(this.options.points&&!this.rectSelection.set.get(9)||!this.options.points&&!this.rectSelection.set.get(1))){var s=function(t){(t=t||window.event).preventDefault?t.preventDefault():t.returnValue=!1,t.stopPropagation();var i=t.pageX||t.touches[0].pageX,a=t.pageY||t.touches[0].pageY;e.el.fire("rot",{x:i,y:a,event:t});},r=this.drawPoint(i.width/2,20).attr("class",this.options.classPoints+"_rot").on("touchstart",s).on("mousedown",s);this.rectSelection.set.add(r);}},t.prototype.handler=function(){var t=this.el.bbox();this.nested.matrix(new SVG.Matrix(this.el).translate(t.x,t.y)),this.rectSelection.isSelected&&this.updateRectSelection(),this.pointSelection.isSelected&&this.updatePointSelection();},t.prototype.observe=function(){var t=this;if(MutationObserver)if(this.rectSelection.isSelected||this.pointSelection.isSelected)this.observerInst=this.observerInst||new MutationObserver((function(){t.handler();})),this.observerInst.observe(this.el.node,{attributes:!0});else try{this.observerInst.disconnect(),delete this.observerInst;}catch(t){}else this.el.off("DOMAttrModified.select"),(this.rectSelection.isSelected||this.pointSelection.isSelected)&&this.el.on("DOMAttrModified.select",(function(){t.handler();}));},t.prototype.cleanup=function(){!this.rectSelection.isSelected&&this.rectSelection.set&&(this.rectSelection.set.each((function(){this.remove();})),this.rectSelection.set.clear(),delete this.rectSelection.set),!this.pointSelection.isSelected&&this.pointSelection.set&&(this.pointSelection.set.each((function(){this.remove();})),this.pointSelection.set.clear(),delete this.pointSelection.set),this.pointSelection.isSelected||this.rectSelection.isSelected||(this.nested.remove(),delete this.nested);},SVG.extend(SVG.Element,{selectize:function(e,i){return "object"==typeof e&&(i=e,e=!0),(this.remember("_selectHandler")||new t(this)).init(void 0===e||e,i||{}),this}}),SVG.Element.prototype.selectize.defaults={points:["lt","rt","rb","lb","t","r","b","l"],pointsExclude:[],classRect:"svg_select_boundingRect",classPoints:"svg_select_points",pointSize:7,rotationPoint:!0,deepSelect:!1,pointType:"circle"};}(),function(){(function(){function t(t){t.remember("_resizeHandler",this),this.el=t,this.parameters={},this.lastUpdateCall=null,this.p=t.doc().node.createSVGPoint();}t.prototype.transformPoint=function(t,e,i){return this.p.x=t-(this.offset.x-window.pageXOffset),this.p.y=e-(this.offset.y-window.pageYOffset),this.p.matrixTransform(i||this.m)},t.prototype._extractPosition=function(t){return {x:null!=t.clientX?t.clientX:t.touches[0].clientX,y:null!=t.clientY?t.clientY:t.touches[0].clientY}},t.prototype.init=function(t){var e=this;if(this.stop(),"stop"!==t){for(var i in this.options={},this.el.resize.defaults)this.options[i]=this.el.resize.defaults[i],void 0!==t[i]&&(this.options[i]=t[i]);this.el.on("lt.resize",(function(t){e.resize(t||window.event);})),this.el.on("rt.resize",(function(t){e.resize(t||window.event);})),this.el.on("rb.resize",(function(t){e.resize(t||window.event);})),this.el.on("lb.resize",(function(t){e.resize(t||window.event);})),this.el.on("t.resize",(function(t){e.resize(t||window.event);})),this.el.on("r.resize",(function(t){e.resize(t||window.event);})),this.el.on("b.resize",(function(t){e.resize(t||window.event);})),this.el.on("l.resize",(function(t){e.resize(t||window.event);})),this.el.on("rot.resize",(function(t){e.resize(t||window.event);})),this.el.on("point.resize",(function(t){e.resize(t||window.event);})),this.update();}},t.prototype.stop=function(){return this.el.off("lt.resize"),this.el.off("rt.resize"),this.el.off("rb.resize"),this.el.off("lb.resize"),this.el.off("t.resize"),this.el.off("r.resize"),this.el.off("b.resize"),this.el.off("l.resize"),this.el.off("rot.resize"),this.el.off("point.resize"),this},t.prototype.resize=function(t){var e=this;this.m=this.el.node.getScreenCTM().inverse(),this.offset={x:window.pageXOffset,y:window.pageYOffset};var i=this._extractPosition(t.detail.event);if(this.parameters={type:this.el.type,p:this.transformPoint(i.x,i.y),x:t.detail.x,y:t.detail.y,box:this.el.bbox(),rotation:this.el.transform().rotation},"text"===this.el.type&&(this.parameters.fontSize=this.el.attr()["font-size"]),void 0!==t.detail.i){var a=this.el.array().valueOf();this.parameters.i=t.detail.i,this.parameters.pointCoords=[a[t.detail.i][0],a[t.detail.i][1]];}switch(t.type){case"lt":this.calc=function(t,e){var i=this.snapToGrid(t,e);if(this.parameters.box.width-i[0]>0&&this.parameters.box.height-i[1]>0){if("text"===this.parameters.type)return this.el.move(this.parameters.box.x+i[0],this.parameters.box.y),void this.el.attr("font-size",this.parameters.fontSize-i[0]);i=this.checkAspectRatio(i),this.el.move(this.parameters.box.x+i[0],this.parameters.box.y+i[1]).size(this.parameters.box.width-i[0],this.parameters.box.height-i[1]);}};break;case"rt":this.calc=function(t,e){var i=this.snapToGrid(t,e,2);if(this.parameters.box.width+i[0]>0&&this.parameters.box.height-i[1]>0){if("text"===this.parameters.type)return this.el.move(this.parameters.box.x-i[0],this.parameters.box.y),void this.el.attr("font-size",this.parameters.fontSize+i[0]);i=this.checkAspectRatio(i,!0),this.el.move(this.parameters.box.x,this.parameters.box.y+i[1]).size(this.parameters.box.width+i[0],this.parameters.box.height-i[1]);}};break;case"rb":this.calc=function(t,e){var i=this.snapToGrid(t,e,0);if(this.parameters.box.width+i[0]>0&&this.parameters.box.height+i[1]>0){if("text"===this.parameters.type)return this.el.move(this.parameters.box.x-i[0],this.parameters.box.y),void this.el.attr("font-size",this.parameters.fontSize+i[0]);i=this.checkAspectRatio(i),this.el.move(this.parameters.box.x,this.parameters.box.y).size(this.parameters.box.width+i[0],this.parameters.box.height+i[1]);}};break;case"lb":this.calc=function(t,e){var i=this.snapToGrid(t,e,1);if(this.parameters.box.width-i[0]>0&&this.parameters.box.height+i[1]>0){if("text"===this.parameters.type)return this.el.move(this.parameters.box.x+i[0],this.parameters.box.y),void this.el.attr("font-size",this.parameters.fontSize-i[0]);i=this.checkAspectRatio(i,!0),this.el.move(this.parameters.box.x+i[0],this.parameters.box.y).size(this.parameters.box.width-i[0],this.parameters.box.height+i[1]);}};break;case"t":this.calc=function(t,e){var i=this.snapToGrid(t,e,2);if(this.parameters.box.height-i[1]>0){if("text"===this.parameters.type)return;this.el.move(this.parameters.box.x,this.parameters.box.y+i[1]).height(this.parameters.box.height-i[1]);}};break;case"r":this.calc=function(t,e){var i=this.snapToGrid(t,e,0);if(this.parameters.box.width+i[0]>0){if("text"===this.parameters.type)return;this.el.move(this.parameters.box.x,this.parameters.box.y).width(this.parameters.box.width+i[0]);}};break;case"b":this.calc=function(t,e){var i=this.snapToGrid(t,e,0);if(this.parameters.box.height+i[1]>0){if("text"===this.parameters.type)return;this.el.move(this.parameters.box.x,this.parameters.box.y).height(this.parameters.box.height+i[1]);}};break;case"l":this.calc=function(t,e){var i=this.snapToGrid(t,e,1);if(this.parameters.box.width-i[0]>0){if("text"===this.parameters.type)return;this.el.move(this.parameters.box.x+i[0],this.parameters.box.y).width(this.parameters.box.width-i[0]);}};break;case"rot":this.calc=function(t,e){var i=t+this.parameters.p.x,a=e+this.parameters.p.y,s=Math.atan2(this.parameters.p.y-this.parameters.box.y-this.parameters.box.height/2,this.parameters.p.x-this.parameters.box.x-this.parameters.box.width/2),r=Math.atan2(a-this.parameters.box.y-this.parameters.box.height/2,i-this.parameters.box.x-this.parameters.box.width/2),n=this.parameters.rotation+180*(r-s)/Math.PI+this.options.snapToAngle/2;this.el.center(this.parameters.box.cx,this.parameters.box.cy).rotate(n-n%this.options.snapToAngle,this.parameters.box.cx,this.parameters.box.cy);};break;case"point":this.calc=function(t,e){var i=this.snapToGrid(t,e,this.parameters.pointCoords[0],this.parameters.pointCoords[1]),a=this.el.array().valueOf();a[this.parameters.i][0]=this.parameters.pointCoords[0]+i[0],a[this.parameters.i][1]=this.parameters.pointCoords[1]+i[1],this.el.plot(a);};}this.el.fire("resizestart",{dx:this.parameters.x,dy:this.parameters.y,event:t}),SVG.on(window,"touchmove.resize",(function(t){e.update(t||window.event);})),SVG.on(window,"touchend.resize",(function(){e.done();})),SVG.on(window,"mousemove.resize",(function(t){e.update(t||window.event);})),SVG.on(window,"mouseup.resize",(function(){e.done();}));},t.prototype.update=function(t){if(t){var e=this._extractPosition(t),i=this.transformPoint(e.x,e.y),a=i.x-this.parameters.p.x,s=i.y-this.parameters.p.y;this.lastUpdateCall=[a,s],this.calc(a,s),this.el.fire("resizing",{dx:a,dy:s,event:t});}else this.lastUpdateCall&&this.calc(this.lastUpdateCall[0],this.lastUpdateCall[1]);},t.prototype.done=function(){this.lastUpdateCall=null,SVG.off(window,"mousemove.resize"),SVG.off(window,"mouseup.resize"),SVG.off(window,"touchmove.resize"),SVG.off(window,"touchend.resize"),this.el.fire("resizedone");},t.prototype.snapToGrid=function(t,e,i,a){var s;return void 0!==a?s=[(i+t)%this.options.snapToGrid,(a+e)%this.options.snapToGrid]:(i=null==i?3:i,s=[(this.parameters.box.x+t+(1&i?0:this.parameters.box.width))%this.options.snapToGrid,(this.parameters.box.y+e+(2&i?0:this.parameters.box.height))%this.options.snapToGrid]),t<0&&(s[0]-=this.options.snapToGrid),e<0&&(s[1]-=this.options.snapToGrid),t-=Math.abs(s[0])<this.options.snapToGrid/2?s[0]:s[0]-(t<0?-this.options.snapToGrid:this.options.snapToGrid),e-=Math.abs(s[1])<this.options.snapToGrid/2?s[1]:s[1]-(e<0?-this.options.snapToGrid:this.options.snapToGrid),this.constraintToBox(t,e,i,a)},t.prototype.constraintToBox=function(t,e,i,a){var s,r,n=this.options.constraint||{};return void 0!==a?(s=i,r=a):(s=this.parameters.box.x+(1&i?0:this.parameters.box.width),r=this.parameters.box.y+(2&i?0:this.parameters.box.height)),void 0!==n.minX&&s+t<n.minX&&(t=n.minX-s),void 0!==n.maxX&&s+t>n.maxX&&(t=n.maxX-s),void 0!==n.minY&&r+e<n.minY&&(e=n.minY-r),void 0!==n.maxY&&r+e>n.maxY&&(e=n.maxY-r),[t,e]},t.prototype.checkAspectRatio=function(t,e){if(!this.options.saveAspectRatio)return t;var i=t.slice(),a=this.parameters.box.width/this.parameters.box.height,s=this.parameters.box.width+t[0],r=this.parameters.box.height-t[1],n=s/r;return n<a?(i[1]=s/a-this.parameters.box.height,e&&(i[1]=-i[1])):n>a&&(i[0]=this.parameters.box.width-r*a,e&&(i[0]=-i[0])),i},SVG.extend(SVG.Element,{resize:function(e){return (this.remember("_resizeHandler")||new t(this)).init(e||{}),this}}),SVG.Element.prototype.resize.defaults={snapToAngle:.1,snapToGrid:1,constraint:{},saveAspectRatio:!1};}).call(this);}();!function(t,e){void 0===e&&(e={});var i=e.insertAt;if(t&&"undefined"!=typeof document){var a=document.head||document.getElementsByTagName("head")[0],s=document.createElement("style");s.type="text/css","top"===i&&a.firstChild?a.insertBefore(s,a.firstChild):a.appendChild(s),s.styleSheet?s.styleSheet.cssText=t:s.appendChild(document.createTextNode(t));}}('.apexcharts-canvas {\n  position: relative;\n  user-select: none;\n  /* cannot give overflow: hidden as it will crop tooltips which overflow outside chart area */\n}\n\n\n/* scrollbar is not visible by default for legend, hence forcing the visibility */\n.apexcharts-canvas ::-webkit-scrollbar {\n  -webkit-appearance: none;\n  width: 6px;\n}\n\n.apexcharts-canvas ::-webkit-scrollbar-thumb {\n  border-radius: 4px;\n  background-color: rgba(0, 0, 0, .5);\n  box-shadow: 0 0 1px rgba(255, 255, 255, .5);\n  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, .5);\n}\n\n\n.apexcharts-inner {\n  position: relative;\n}\n\n.apexcharts-text tspan {\n  font-family: inherit;\n}\n\n.legend-mouseover-inactive {\n  transition: 0.15s ease all;\n  opacity: 0.20;\n}\n\n.apexcharts-series-collapsed {\n  opacity: 0;\n}\n\n.apexcharts-tooltip {\n  border-radius: 5px;\n  box-shadow: 2px 2px 6px -4px #999;\n  cursor: default;\n  font-size: 14px;\n  left: 62px;\n  opacity: 0;\n  pointer-events: none;\n  position: absolute;\n  top: 20px;\n  display: flex;\n  flex-direction: column;\n  overflow: hidden;\n  white-space: nowrap;\n  z-index: 12;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-tooltip.apexcharts-active {\n  opacity: 1;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-tooltip.apexcharts-theme-light {\n  border: 1px solid #e3e3e3;\n  background: rgba(255, 255, 255, 0.96);\n}\n\n.apexcharts-tooltip.apexcharts-theme-dark {\n  color: #fff;\n  background: rgba(30, 30, 30, 0.8);\n}\n\n.apexcharts-tooltip * {\n  font-family: inherit;\n}\n\n\n.apexcharts-tooltip-title {\n  padding: 6px;\n  font-size: 15px;\n  margin-bottom: 4px;\n}\n\n.apexcharts-tooltip.apexcharts-theme-light .apexcharts-tooltip-title {\n  background: #ECEFF1;\n  border-bottom: 1px solid #ddd;\n}\n\n.apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {\n  background: rgba(0, 0, 0, 0.7);\n  border-bottom: 1px solid #333;\n}\n\n.apexcharts-tooltip-text-value,\n.apexcharts-tooltip-text-z-value {\n  display: inline-block;\n  font-weight: 600;\n  margin-left: 5px;\n}\n\n.apexcharts-tooltip-text-z-label:empty,\n.apexcharts-tooltip-text-z-value:empty {\n  display: none;\n}\n\n.apexcharts-tooltip-text-value,\n.apexcharts-tooltip-text-z-value {\n  font-weight: 600;\n}\n\n.apexcharts-tooltip-marker {\n  width: 12px;\n  height: 12px;\n  position: relative;\n  top: 0px;\n  margin-right: 10px;\n  border-radius: 50%;\n}\n\n.apexcharts-tooltip-series-group {\n  padding: 0 10px;\n  display: none;\n  text-align: left;\n  justify-content: left;\n  align-items: center;\n}\n\n.apexcharts-tooltip-series-group.apexcharts-active .apexcharts-tooltip-marker {\n  opacity: 1;\n}\n\n.apexcharts-tooltip-series-group.apexcharts-active,\n.apexcharts-tooltip-series-group:last-child {\n  padding-bottom: 4px;\n}\n\n.apexcharts-tooltip-series-group-hidden {\n  opacity: 0;\n  height: 0;\n  line-height: 0;\n  padding: 0 !important;\n}\n\n.apexcharts-tooltip-y-group {\n  padding: 6px 0 5px;\n}\n\n.apexcharts-tooltip-candlestick {\n  padding: 4px 8px;\n}\n\n.apexcharts-tooltip-candlestick>div {\n  margin: 4px 0;\n}\n\n.apexcharts-tooltip-candlestick span.value {\n  font-weight: bold;\n}\n\n.apexcharts-tooltip-rangebar {\n  padding: 5px 8px;\n}\n\n.apexcharts-tooltip-rangebar .category {\n  font-weight: 600;\n  color: #777;\n}\n\n.apexcharts-tooltip-rangebar .series-name {\n  font-weight: bold;\n  display: block;\n  margin-bottom: 5px;\n}\n\n.apexcharts-xaxistooltip {\n  opacity: 0;\n  padding: 9px 10px;\n  pointer-events: none;\n  color: #373d3f;\n  font-size: 13px;\n  text-align: center;\n  border-radius: 2px;\n  position: absolute;\n  z-index: 10;\n  background: #ECEFF1;\n  border: 1px solid #90A4AE;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-xaxistooltip.apexcharts-theme-dark {\n  background: rgba(0, 0, 0, 0.7);\n  border: 1px solid rgba(0, 0, 0, 0.5);\n  color: #fff;\n}\n\n.apexcharts-xaxistooltip:after,\n.apexcharts-xaxistooltip:before {\n  left: 50%;\n  border: solid transparent;\n  content: " ";\n  height: 0;\n  width: 0;\n  position: absolute;\n  pointer-events: none;\n}\n\n.apexcharts-xaxistooltip:after {\n  border-color: rgba(236, 239, 241, 0);\n  border-width: 6px;\n  margin-left: -6px;\n}\n\n.apexcharts-xaxistooltip:before {\n  border-color: rgba(144, 164, 174, 0);\n  border-width: 7px;\n  margin-left: -7px;\n}\n\n.apexcharts-xaxistooltip-bottom:after,\n.apexcharts-xaxistooltip-bottom:before {\n  bottom: 100%;\n}\n\n.apexcharts-xaxistooltip-top:after,\n.apexcharts-xaxistooltip-top:before {\n  top: 100%;\n}\n\n.apexcharts-xaxistooltip-bottom:after {\n  border-bottom-color: #ECEFF1;\n}\n\n.apexcharts-xaxistooltip-bottom:before {\n  border-bottom-color: #90A4AE;\n}\n\n.apexcharts-xaxistooltip-bottom.apexcharts-theme-dark:after {\n  border-bottom-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-xaxistooltip-bottom.apexcharts-theme-dark:before {\n  border-bottom-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-xaxistooltip-top:after {\n  border-top-color: #ECEFF1\n}\n\n.apexcharts-xaxistooltip-top:before {\n  border-top-color: #90A4AE;\n}\n\n.apexcharts-xaxistooltip-top.apexcharts-theme-dark:after {\n  border-top-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-xaxistooltip-top.apexcharts-theme-dark:before {\n  border-top-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-xaxistooltip.apexcharts-active {\n  opacity: 1;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-yaxistooltip {\n  opacity: 0;\n  padding: 4px 10px;\n  pointer-events: none;\n  color: #373d3f;\n  font-size: 13px;\n  text-align: center;\n  border-radius: 2px;\n  position: absolute;\n  z-index: 10;\n  background: #ECEFF1;\n  border: 1px solid #90A4AE;\n}\n\n.apexcharts-yaxistooltip.apexcharts-theme-dark {\n  background: rgba(0, 0, 0, 0.7);\n  border: 1px solid rgba(0, 0, 0, 0.5);\n  color: #fff;\n}\n\n.apexcharts-yaxistooltip:after,\n.apexcharts-yaxistooltip:before {\n  top: 50%;\n  border: solid transparent;\n  content: " ";\n  height: 0;\n  width: 0;\n  position: absolute;\n  pointer-events: none;\n}\n\n.apexcharts-yaxistooltip:after {\n  border-color: rgba(236, 239, 241, 0);\n  border-width: 6px;\n  margin-top: -6px;\n}\n\n.apexcharts-yaxistooltip:before {\n  border-color: rgba(144, 164, 174, 0);\n  border-width: 7px;\n  margin-top: -7px;\n}\n\n.apexcharts-yaxistooltip-left:after,\n.apexcharts-yaxistooltip-left:before {\n  left: 100%;\n}\n\n.apexcharts-yaxistooltip-right:after,\n.apexcharts-yaxistooltip-right:before {\n  right: 100%;\n}\n\n.apexcharts-yaxistooltip-left:after {\n  border-left-color: #ECEFF1;\n}\n\n.apexcharts-yaxistooltip-left:before {\n  border-left-color: #90A4AE;\n}\n\n.apexcharts-yaxistooltip-left.apexcharts-theme-dark:after {\n  border-left-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-yaxistooltip-left.apexcharts-theme-dark:before {\n  border-left-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-yaxistooltip-right:after {\n  border-right-color: #ECEFF1;\n}\n\n.apexcharts-yaxistooltip-right:before {\n  border-right-color: #90A4AE;\n}\n\n.apexcharts-yaxistooltip-right.apexcharts-theme-dark:after {\n  border-right-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-yaxistooltip-right.apexcharts-theme-dark:before {\n  border-right-color: rgba(0, 0, 0, 0.5);\n}\n\n.apexcharts-yaxistooltip.apexcharts-active {\n  opacity: 1;\n}\n\n.apexcharts-yaxistooltip-hidden {\n  display: none;\n}\n\n.apexcharts-xcrosshairs,\n.apexcharts-ycrosshairs {\n  pointer-events: none;\n  opacity: 0;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-xcrosshairs.apexcharts-active,\n.apexcharts-ycrosshairs.apexcharts-active {\n  opacity: 1;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-ycrosshairs-hidden {\n  opacity: 0;\n}\n\n.apexcharts-selection-rect {\n  cursor: move;\n}\n\n.svg_select_boundingRect, .svg_select_points_rot {\n  pointer-events: none;\n  opacity: 0;\n  visibility: hidden;\n}\n.apexcharts-selection-rect + g .svg_select_boundingRect,\n.apexcharts-selection-rect + g .svg_select_points_rot {\n  opacity: 0;\n  visibility: hidden;\n}\n\n.apexcharts-selection-rect + g .svg_select_points_l,\n.apexcharts-selection-rect + g .svg_select_points_r {\n  cursor: ew-resize;\n  opacity: 1;\n  visibility: visible;\n}\n\n.svg_select_points {\n  fill: #efefef;\n  stroke: #333;\n  rx: 2;\n}\n\n.apexcharts-svg.apexcharts-zoomable.hovering-zoom {\n  cursor: crosshair\n}\n\n.apexcharts-svg.apexcharts-zoomable.hovering-pan {\n  cursor: move\n}\n\n.apexcharts-zoom-icon,\n.apexcharts-zoomin-icon,\n.apexcharts-zoomout-icon,\n.apexcharts-reset-icon,\n.apexcharts-pan-icon,\n.apexcharts-selection-icon,\n.apexcharts-menu-icon,\n.apexcharts-toolbar-custom-icon {\n  cursor: pointer;\n  width: 20px;\n  height: 20px;\n  line-height: 24px;\n  color: #6E8192;\n  text-align: center;\n}\n\n.apexcharts-zoom-icon svg,\n.apexcharts-zoomin-icon svg,\n.apexcharts-zoomout-icon svg,\n.apexcharts-reset-icon svg,\n.apexcharts-menu-icon svg {\n  fill: #6E8192;\n}\n\n.apexcharts-selection-icon svg {\n  fill: #444;\n  transform: scale(0.76)\n}\n\n.apexcharts-theme-dark .apexcharts-zoom-icon svg,\n.apexcharts-theme-dark .apexcharts-zoomin-icon svg,\n.apexcharts-theme-dark .apexcharts-zoomout-icon svg,\n.apexcharts-theme-dark .apexcharts-reset-icon svg,\n.apexcharts-theme-dark .apexcharts-pan-icon svg,\n.apexcharts-theme-dark .apexcharts-selection-icon svg,\n.apexcharts-theme-dark .apexcharts-menu-icon svg,\n.apexcharts-theme-dark .apexcharts-toolbar-custom-icon svg {\n  fill: #f3f4f5;\n}\n\n.apexcharts-canvas .apexcharts-zoom-icon.apexcharts-selected svg,\n.apexcharts-canvas .apexcharts-selection-icon.apexcharts-selected svg,\n.apexcharts-canvas .apexcharts-reset-zoom-icon.apexcharts-selected svg {\n  fill: #008FFB;\n}\n\n.apexcharts-theme-light .apexcharts-selection-icon:not(.apexcharts-selected):hover svg,\n.apexcharts-theme-light .apexcharts-zoom-icon:not(.apexcharts-selected):hover svg,\n.apexcharts-theme-light .apexcharts-zoomin-icon:hover svg,\n.apexcharts-theme-light .apexcharts-zoomout-icon:hover svg,\n.apexcharts-theme-light .apexcharts-reset-icon:hover svg,\n.apexcharts-theme-light .apexcharts-menu-icon:hover svg {\n  fill: #333;\n}\n\n.apexcharts-selection-icon,\n.apexcharts-menu-icon {\n  position: relative;\n}\n\n.apexcharts-reset-icon {\n  margin-left: 5px;\n}\n\n.apexcharts-zoom-icon,\n.apexcharts-reset-icon,\n.apexcharts-menu-icon {\n  transform: scale(0.85);\n}\n\n.apexcharts-zoomin-icon,\n.apexcharts-zoomout-icon {\n  transform: scale(0.7)\n}\n\n.apexcharts-zoomout-icon {\n  margin-right: 3px;\n}\n\n.apexcharts-pan-icon {\n  transform: scale(0.62);\n  position: relative;\n  left: 1px;\n  top: 0px;\n}\n\n.apexcharts-pan-icon svg {\n  fill: #fff;\n  stroke: #6E8192;\n  stroke-width: 2;\n}\n\n.apexcharts-pan-icon.apexcharts-selected svg {\n  stroke: #008FFB;\n}\n\n.apexcharts-pan-icon:not(.apexcharts-selected):hover svg {\n  stroke: #333;\n}\n\n.apexcharts-toolbar {\n  position: absolute;\n  z-index: 11;\n  max-width: 176px;\n  text-align: right;\n  border-radius: 3px;\n  padding: 0px 6px 2px 6px;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.apexcharts-menu {\n  background: #fff;\n  position: absolute;\n  top: 100%;\n  border: 1px solid #ddd;\n  border-radius: 3px;\n  padding: 3px;\n  right: 10px;\n  opacity: 0;\n  min-width: 110px;\n  transition: 0.15s ease all;\n  pointer-events: none;\n}\n\n.apexcharts-menu.apexcharts-menu-open {\n  opacity: 1;\n  pointer-events: all;\n  transition: 0.15s ease all;\n}\n\n.apexcharts-menu-item {\n  padding: 6px 7px;\n  font-size: 12px;\n  cursor: pointer;\n}\n\n.apexcharts-theme-light .apexcharts-menu-item:hover {\n  background: #eee;\n}\n\n.apexcharts-theme-dark .apexcharts-menu {\n  background: rgba(0, 0, 0, 0.7);\n  color: #fff;\n}\n\n@media screen and (min-width: 768px) {\n  .apexcharts-canvas:hover .apexcharts-toolbar {\n    opacity: 1;\n  }\n}\n\n.apexcharts-datalabel.apexcharts-element-hidden {\n  opacity: 0;\n}\n\n.apexcharts-pie-label,\n.apexcharts-datalabels,\n.apexcharts-datalabel,\n.apexcharts-datalabel-label,\n.apexcharts-datalabel-value {\n  cursor: default;\n  pointer-events: none;\n}\n\n.apexcharts-pie-label-delay {\n  opacity: 0;\n  animation-name: opaque;\n  animation-duration: 0.3s;\n  animation-fill-mode: forwards;\n  animation-timing-function: ease;\n}\n\n.apexcharts-canvas .apexcharts-element-hidden {\n  opacity: 0;\n}\n\n.apexcharts-hide .apexcharts-series-points {\n  opacity: 0;\n}\n\n.apexcharts-gridline,\n.apexcharts-annotation-rect,\n.apexcharts-tooltip .apexcharts-marker,\n.apexcharts-area-series .apexcharts-area,\n.apexcharts-line,\n.apexcharts-zoom-rect,\n.apexcharts-toolbar svg,\n.apexcharts-area-series .apexcharts-series-markers .apexcharts-marker.no-pointer-events,\n.apexcharts-line-series .apexcharts-series-markers .apexcharts-marker.no-pointer-events,\n.apexcharts-radar-series path,\n.apexcharts-radar-series polygon {\n  pointer-events: none;\n}\n\n\n/* markers */\n\n.apexcharts-marker {\n  transition: 0.15s ease all;\n}\n\n@keyframes opaque {\n  0% {\n    opacity: 0;\n  }\n  100% {\n    opacity: 1;\n  }\n}\n\n\n/* Resize generated styles */\n\n@keyframes resizeanim {\n  from {\n    opacity: 0;\n  }\n  to {\n    opacity: 0;\n  }\n}\n\n.resize-triggers {\n  animation: 1ms resizeanim;\n  visibility: hidden;\n  opacity: 0;\n}\n\n.resize-triggers,\n.resize-triggers>div,\n.contract-trigger:before {\n  content: " ";\n  display: block;\n  position: absolute;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100%;\n  overflow: hidden;\n}\n\n.resize-triggers>div {\n  background: #eee;\n  overflow: auto;\n}\n\n.contract-trigger:before {\n  width: 200%;\n  height: 200%;\n}'),function(){function t(t){var e=t.__resizeTriggers__,i=e.firstElementChild,a=e.lastElementChild,s=i?i.firstElementChild:null;a&&(a.scrollLeft=a.scrollWidth,a.scrollTop=a.scrollHeight),s&&(s.style.width=i.offsetWidth+1+"px",s.style.height=i.offsetHeight+1+"px"),i&&(i.scrollLeft=i.scrollWidth,i.scrollTop=i.scrollHeight);}function e(e){var i=this;t(this),this.__resizeRAF__&&r(this.__resizeRAF__),this.__resizeRAF__=s((function(){(function(t){return t.offsetWidth!=t.__resizeLast__.width||t.offsetHeight!=t.__resizeLast__.height})(i)&&(i.__resizeLast__.width=i.offsetWidth,i.__resizeLast__.height=i.offsetHeight,i.__resizeListeners__.forEach((function(t){t.call(e);})));}));}var i,a,s=(i=window.requestAnimationFrame||window.mozRequestAnimationFrame||window.webkitRequestAnimationFrame||function(t){return window.setTimeout(t,20)},function(t){return i(t)}),r=(a=window.cancelAnimationFrame||window.mozCancelAnimationFrame||window.webkitCancelAnimationFrame||window.clearTimeout,function(t){return a(t)}),n=!1,o="animationstart",l="Webkit Moz O ms".split(" "),h="webkitAnimationStart animationstart oAnimationStart MSAnimationStart".split(" "),c=document.createElement("fakeelement");if(void 0!==c.style.animationName&&(n=!0),!1===n)for(var d=0;d<l.length;d++)if(void 0!==c.style[l[d]+"AnimationName"]){o=h[d];break}window.addResizeListener=function(i,a){i.__resizeTriggers__||("static"==getComputedStyle(i).position&&(i.style.position="relative"),i.__resizeLast__={},i.__resizeListeners__=[],(i.__resizeTriggers__=document.createElement("div")).className="resize-triggers",i.__resizeTriggers__.innerHTML='<div class="expand-trigger"><div></div></div><div class="contract-trigger"></div>',i.appendChild(i.__resizeTriggers__),t(i),i.addEventListener("scroll",e,!0),o&&i.__resizeTriggers__.addEventListener(o,(function(e){"resizeanim"==e.animationName&&t(i);}))),i.__resizeListeners__.push(a);},window.removeResizeListener=function(t,i){t&&(t.__resizeListeners__.splice(t.__resizeListeners__.indexOf(i),1),t.__resizeListeners__.length||(t.removeEventListener("scroll",e),t.__resizeTriggers__.parentNode&&(t.__resizeTriggers__=!t.removeChild(t.__resizeTriggers__))));};}(),void 0===window.Apex&&(window.Apex={});var Yt=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"initModules",value:function(){this.ctx.publicMethods=["updateOptions","updateSeries","appendData","appendSeries","toggleSeries","showSeries","hideSeries","setLocale","resetSeries","zoomX","toggleDataPointSelection","dataURI","addXaxisAnnotation","addYaxisAnnotation","addPointAnnotation","clearAnnotations","removeAnnotation","paper","destroy"],this.ctx.eventList=["click","mousedown","mousemove","touchstart","touchmove","mouseup","touchend"],this.ctx.animations=new x(this.ctx),this.ctx.axes=new J(this.ctx),this.ctx.core=new Et(this.ctx.el,this.ctx),this.ctx.config=new R({}),this.ctx.data=new O(this.ctx),this.ctx.grid=new _(this.ctx),this.ctx.graphics=new b(this.ctx),this.ctx.coreUtils=new w(this.ctx),this.ctx.crosshairs=new Q(this.ctx),this.ctx.events=new Z(this.ctx),this.ctx.exports=new V(this.ctx),this.ctx.localization=new $(this.ctx),this.ctx.options=new C,this.ctx.responsive=new K(this.ctx),this.ctx.series=new E(this.ctx),this.ctx.theme=new tt(this.ctx),this.ctx.formatters=new W(this.ctx),this.ctx.titleSubtitle=new et(this.ctx),this.ctx.legend=new lt(this.ctx),this.ctx.toolbar=new ht(this.ctx),this.ctx.dimensions=new nt(this.ctx),this.ctx.updateHelpers=new Xt(this.ctx),this.ctx.zoomPanSelection=new ct(this.ctx),this.ctx.w.globals.tooltip=new bt(this.ctx);}}]),t}(),Ft=function(){function t(i){e(this,t),this.ctx=i,this.w=i.w;}return a(t,[{key:"clear",value:function(t){var e=t.isUpdating;this.ctx.zoomPanSelection&&this.ctx.zoomPanSelection.destroy(),this.ctx.toolbar&&this.ctx.toolbar.destroy(),this.ctx.animations=null,this.ctx.axes=null,this.ctx.annotations=null,this.ctx.core=null,this.ctx.data=null,this.ctx.grid=null,this.ctx.series=null,this.ctx.responsive=null,this.ctx.theme=null,this.ctx.formatters=null,this.ctx.titleSubtitle=null,this.ctx.legend=null,this.ctx.dimensions=null,this.ctx.options=null,this.ctx.crosshairs=null,this.ctx.zoomPanSelection=null,this.ctx.updateHelpers=null,this.ctx.toolbar=null,this.ctx.localization=null,this.ctx.w.globals.tooltip=null,this.clearDomElements({isUpdating:e});}},{key:"killSVG",value:function(t){t.each((function(t,e){this.removeClass("*"),this.off(),this.stop();}),!0),t.ungroup(),t.clear();}},{key:"clearDomElements",value:function(t){var e=this,i=t.isUpdating,a=this.w.globals.dom.Paper.node;a.parentNode&&a.parentNode.parentNode&&!i&&(a.parentNode.parentNode.style.minHeight="unset");var s=this.w.globals.dom.baseEl;s&&this.ctx.eventList.forEach((function(t){s.removeEventListener(t,e.ctx.events.documentEvent);}));var r=this.w.globals.dom;if(null!==this.ctx.el)for(;this.ctx.el.firstChild;)this.ctx.el.removeChild(this.ctx.el.firstChild);this.killSVG(r.Paper),r.Paper.remove(),r.elWrap=null,r.elGraphical=null,r.elAnnotations=null,r.elLegendWrap=null,r.baseEl=null,r.elGridRect=null,r.elGridRectMask=null,r.elGridRectMarkerMask=null,r.elDefs=null;}}]),t}();return function(){function t(i,a){e(this,t),this.opts=a,this.ctx=this,this.w=new N(a).init(),this.el=i,this.w.globals.cuid=f.randomId(),this.w.globals.chartID=this.w.config.chart.id?f.escapeString(this.w.config.chart.id):this.w.globals.cuid,new Yt(this).initModules(),this.create=f.bind(this.create,this),this.windowResizeHandler=this._windowResize.bind(this);}return a(t,[{key:"render",value:function(){var t=this;return new Promise((function(e,i){if(null!==t.el){void 0===Apex._chartInstances&&(Apex._chartInstances=[]),t.w.config.chart.id&&Apex._chartInstances.push({id:t.w.globals.chartID,group:t.w.config.chart.group,chart:t}),t.setLocale(t.w.config.chart.defaultLocale);var a=t.w.config.chart.events.beforeMount;"function"==typeof a&&a(t,t.w),t.events.fireEvent("beforeMount",[t,t.w]),window.addEventListener("resize",t.windowResizeHandler),window.addResizeListener(t.el.parentNode,t._parentResizeCallback.bind(t));var s=t.create(t.w.config.series,{});if(!s)return e(t);t.mount(s).then((function(){"function"==typeof t.w.config.chart.events.mounted&&t.w.config.chart.events.mounted(t,t.w),t.events.fireEvent("mounted",[t,t.w]),e(s);})).catch((function(t){i(t);}));}else i(new Error("Element not found"));}))}},{key:"create",value:function(t,e){var i=this.w;new Yt(this).initModules();var a=this.w.globals;(a.noData=!1,a.animationEnded=!1,this.responsive.checkResponsiveConfig(e),i.config.xaxis.convertedCatToNumeric)&&new D(i.config).convertCatToNumericXaxis(i.config,this.ctx);if(null===this.el)return a.animationEnded=!0,null;if(this.core.setupElements(),"treemap"===i.config.chart.type&&(i.config.grid.show=!1,i.config.yaxis[0].show=!1),0===a.svgWidth)return a.animationEnded=!0,null;var s=w.checkComboSeries(t);a.comboCharts=s.comboCharts,a.comboBarCount=s.comboBarCount;var r=t.every((function(t){return t.data&&0===t.data.length}));(0===t.length||r)&&this.series.handleNoData(),this.events.setupEventHandlers(),this.data.parseData(t),this.theme.init(),new T(this).setGlobalMarkerSize(),this.formatters.setLabelFormatters(),this.titleSubtitle.draw(),a.noData&&a.collapsedSeries.length!==a.series.length&&!i.config.legend.showForSingleSeries||this.legend.init(),this.series.hasAllSeriesEqualX(),a.axisCharts&&(this.core.coreCalculations(),"category"!==i.config.xaxis.type&&this.formatters.setLabelFormatters()),this.formatters.heatmapLabelFormatters(),this.dimensions.plotCoords();var n=this.core.xySettings();this.grid.createGridMask();var o=this.core.plotChartType(t,n),l=new I(this);l.bringForward(),i.config.dataLabels.background.enabled&&l.dataLabelsBackground(),this.core.shiftGraphPosition();var h={plot:{left:i.globals.translateX,top:i.globals.translateY,width:i.globals.gridWidth,height:i.globals.gridHeight}};return {elGraph:o,xyRatios:n,elInner:i.globals.dom.elGraphical,dimensions:h}}},{key:"mount",value:function(){var t=this,e=arguments.length>0&&void 0!==arguments[0]?arguments[0]:null,i=this,a=i.w;return new Promise((function(s,r){if(null===i.el)return r(new Error("Not enough data to display or target element not found"));(null===e||a.globals.allSeriesCollapsed)&&i.series.handleNoData(),"treemap"!==a.config.chart.type&&i.axes.drawAxis(a.config.chart.type,e.xyRatios),i.grid=new _(i);var n=i.grid.drawGrid();i.annotations=new L(i),i.annotations.drawImageAnnos(),i.annotations.drawTextAnnos(),"back"===a.config.grid.position&&n&&a.globals.dom.elGraphical.add(n.el);var o=new G(t.ctx),l=new q(t.ctx);if(null!==n&&(o.xAxisLabelCorrections(n.xAxisTickWidth),l.setYAxisTextAlignments()),"back"===a.config.annotations.position&&(a.globals.dom.Paper.add(a.globals.dom.elAnnotations),i.annotations.drawAxesAnnotations()),Array.isArray(e.elGraph))for(var h=0;h<e.elGraph.length;h++)a.globals.dom.elGraphical.add(e.elGraph[h]);else a.globals.dom.elGraphical.add(e.elGraph);if("front"===a.config.grid.position&&n&&a.globals.dom.elGraphical.add(n.el),"front"===a.config.xaxis.crosshairs.position&&i.crosshairs.drawXCrosshairs(),"front"===a.config.yaxis[0].crosshairs.position&&i.crosshairs.drawYCrosshairs(),"front"===a.config.annotations.position&&(a.globals.dom.Paper.add(a.globals.dom.elAnnotations),i.annotations.drawAxesAnnotations()),!a.globals.noData){if(a.config.tooltip.enabled&&!a.globals.noData&&i.w.globals.tooltip.drawTooltip(e.xyRatios),a.globals.axisCharts&&(a.globals.isXNumeric||a.config.xaxis.convertedCatToNumeric))(a.config.chart.zoom.enabled||a.config.chart.selection&&a.config.chart.selection.enabled||a.config.chart.pan&&a.config.chart.pan.enabled)&&i.zoomPanSelection.init({xyRatios:e.xyRatios});else {var c=a.config.chart.toolbar.tools;["zoom","zoomin","zoomout","selection","pan","reset"].forEach((function(t){c[t]=!1;}));}a.config.chart.toolbar.show&&!a.globals.allSeriesCollapsed&&i.toolbar.createToolbar();}a.globals.memory.methodsToExec.length>0&&a.globals.memory.methodsToExec.forEach((function(t){t.method(t.params,!1,t.context);})),a.globals.axisCharts||a.globals.noData||i.core.resizeNonAxisCharts(),s(i);}))}},{key:"destroy",value:function(){window.removeEventListener("resize",this.windowResizeHandler),window.removeResizeListener(this.el.parentNode,this._parentResizeCallback.bind(this));var t=this.w.config.chart.id;t&&Apex._chartInstances.forEach((function(e,i){e.id===f.escapeString(t)&&Apex._chartInstances.splice(i,1);})),new Ft(this.ctx).clear({isUpdating:!1});}},{key:"updateOptions",value:function(t){var e=this,i=arguments.length>1&&void 0!==arguments[1]&&arguments[1],a=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],s=!(arguments.length>3&&void 0!==arguments[3])||arguments[3],r=!(arguments.length>4&&void 0!==arguments[4])||arguments[4],n=this.w;return n.globals.selection=void 0,t.series&&(this.series.resetSeries(!1,!0,!1),t.series.length&&t.series[0].data&&(t.series=t.series.map((function(t,i){return e.updateHelpers._extendSeries(t,i)}))),this.updateHelpers.revertDefaultAxisMinMax()),t.xaxis&&(t=this.updateHelpers.forceXAxisUpdate(t)),t.yaxis&&(t=this.updateHelpers.forceYAxisUpdate(t)),n.globals.collapsedSeriesIndices.length>0&&this.series.clearPreviousPaths(),t.theme&&(t=this.theme.updateThemeOptions(t)),this.updateHelpers._updateOptions(t,i,a,s,r)}},{key:"updateSeries",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:[],e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=!(arguments.length>2&&void 0!==arguments[2])||arguments[2];return this.series.resetSeries(!1),this.updateHelpers.revertDefaultAxisMinMax(),this.updateHelpers._updateSeries(t,e,i)}},{key:"appendSeries",value:function(t){var e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],a=this.w.config.series.slice();return a.push(t),this.series.resetSeries(!1),this.updateHelpers.revertDefaultAxisMinMax(),this.updateHelpers._updateSeries(a,e,i)}},{key:"appendData",value:function(t){var e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=this;i.w.globals.dataChanged=!0,i.series.getPreviousPaths();for(var a=i.w.config.series.slice(),s=0;s<a.length;s++)if(null!==t[s]&&void 0!==t[s])for(var r=0;r<t[s].data.length;r++)a[s].data.push(t[s].data[r]);return i.w.config.series=a,e&&(i.w.globals.initialSeries=f.clone(i.w.config.series)),this.update()}},{key:"update",value:function(t){var e=this;return new Promise((function(i,a){new Ft(e.ctx).clear({isUpdating:!0});var s=e.create(e.w.config.series,t);if(!s)return i(e);e.mount(s).then((function(){"function"==typeof e.w.config.chart.events.updated&&e.w.config.chart.events.updated(e,e.w),e.events.fireEvent("updated",[e,e.w]),e.w.globals.isDirty=!0,i(e);})).catch((function(t){a(t);}));}))}},{key:"getSyncedCharts",value:function(){var t=this.getGroupedCharts(),e=[this];return t.length&&(e=[],t.forEach((function(t){e.push(t);}))),e}},{key:"getGroupedCharts",value:function(){var t=this;return Apex._chartInstances.filter((function(t){if(t.group)return !0})).map((function(e){return t.w.config.chart.group===e.group?e.chart:t}))}},{key:"toggleSeries",value:function(t){return this.series.toggleSeries(t)}},{key:"showSeries",value:function(t){this.series.showSeries(t);}},{key:"hideSeries",value:function(t){this.series.hideSeries(t);}},{key:"resetSeries",value:function(){var t=!(arguments.length>0&&void 0!==arguments[0])||arguments[0],e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1];this.series.resetSeries(t,e);}},{key:"addEventListener",value:function(t,e){this.events.addEventListener(t,e);}},{key:"removeEventListener",value:function(t,e){this.events.removeEventListener(t,e);}},{key:"addXaxisAnnotation",value:function(t){var e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:void 0,a=this;i&&(a=i),a.annotations.addXaxisAnnotationExternal(t,e,a);}},{key:"addYaxisAnnotation",value:function(t){var e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:void 0,a=this;i&&(a=i),a.annotations.addYaxisAnnotationExternal(t,e,a);}},{key:"addPointAnnotation",value:function(t){var e=!(arguments.length>1&&void 0!==arguments[1])||arguments[1],i=arguments.length>2&&void 0!==arguments[2]?arguments[2]:void 0,a=this;i&&(a=i),a.annotations.addPointAnnotationExternal(t,e,a);}},{key:"clearAnnotations",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:void 0,e=this;t&&(e=t),e.annotations.clearAnnotations(e);}},{key:"removeAnnotation",value:function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:void 0,i=this;e&&(i=e),i.annotations.removeAnnotation(i,t);}},{key:"getChartArea",value:function(){return this.w.globals.dom.baseEl.querySelector(".apexcharts-inner")}},{key:"getSeriesTotalXRange",value:function(t,e){return this.coreUtils.getSeriesTotalsXRange(t,e)}},{key:"getHighestValueInSeries",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:0,e=new U(this.ctx);return e.getMinYMaxY(t).highestY}},{key:"getLowestValueInSeries",value:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:0,e=new U(this.ctx);return e.getMinYMaxY(t).lowestY}},{key:"getSeriesTotal",value:function(){return this.w.globals.seriesTotals}},{key:"toggleDataPointSelection",value:function(t,e){return this.updateHelpers.toggleDataPointSelection(t,e)}},{key:"zoomX",value:function(t,e){this.ctx.toolbar.zoomUpdateOptions(t,e);}},{key:"setLocale",value:function(t){this.localization.setCurrentLocaleValues(t);}},{key:"dataURI",value:function(){return new V(this.ctx).dataURI()}},{key:"paper",value:function(){return this.w.globals.dom.Paper}},{key:"_parentResizeCallback",value:function(){!this.w.globals.noData&&this.w.globals.animationEnded&&this.w.config.chart.redrawOnParentResize&&this._windowResize();}},{key:"_windowResize",value:function(){var t=this;clearTimeout(this.w.globals.resizeTimer),this.w.globals.resizeTimer=window.setTimeout((function(){t.w.globals.resized=!0,t.w.globals.dataChanged=!1,t.ctx.update();}),150);}}],[{key:"getChartByID",value:function(t){var e=f.escapeString(t),i=Apex._chartInstances.filter((function(t){return t.id===e}))[0];return i&&i.chart}},{key:"initOnLoad",value:function(){for(var e=document.querySelectorAll("[data-apexcharts]"),i=0;i<e.length;i++){new t(e[i],JSON.parse(e[i].getAttribute("data-options"))).render();}}},{key:"exec",value:function(t,e){var i=this.getChartByID(t);if(i){i.w.globals.isExecCalled=!0;var a=null;if(-1!==i.publicMethods.indexOf(e)){for(var s=arguments.length,r=new Array(s>2?s-2:0),n=2;n<s;n++)r[n-2]=arguments[n];a=i[e].apply(i,r);}return a}}},{key:"merge",value:function(t,e){return f.extend(t,e)}}]),t}()}));
});

var vueApexcharts = createCommonjsModule(function (module, exports) {
(function (global, factory) {
   module.exports = factory(apexcharts_min) ;
}(commonjsGlobal, (function (ApexCharts) {
  ApexCharts = ApexCharts && ApexCharts.hasOwnProperty('default') ? ApexCharts['default'] : ApexCharts;

  function _typeof(obj) {
    if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
      _typeof = function (obj) {
        return typeof obj;
      };
    } else {
      _typeof = function (obj) {
        return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
      };
    }

    return _typeof(obj);
  }

  function _defineProperty(obj, key, value) {
    if (key in obj) {
      Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
      });
    } else {
      obj[key] = value;
    }

    return obj;
  }

  var ApexChartsComponent = {
    props: {
      options: {
        type: Object
      },
      type: {
        type: String
      },
      series: {
        type: Array,
        required: true,
        default: function _default() {
          return [];
        }
      },
      width: {
        default: "100%"
      },
      height: {
        default: "auto"
      }
    },
    data: function data() {
      return {
        chart: null
      };
    },
    beforeMount: function beforeMount() {
      window.ApexCharts = ApexCharts;
    },
    mounted: function mounted() {
      this.init();
    },
    created: function created() {
      var _this = this;

      this.$watch("options", function (options) {
        if (!_this.chart && options) {
          _this.init();
        } else {
          _this.chart.updateOptions(_this.options);
        }
      });
      this.$watch("series", function (series) {
        if (!_this.chart && series) {
          _this.init();
        } else {
          _this.chart.updateSeries(_this.series);
        }
      });
      var watched = ["type", "width", "height"];
      watched.forEach(function (prop) {
        _this.$watch(prop, function () {
          _this.refresh();
        });
      });
    },
    beforeDestroy: function beforeDestroy() {
      if (!this.chart) {
        return;
      }

      this.destroy();
    },
    render: function render(createElement) {
      return createElement("div");
    },
    methods: {
      init: function init() {
        var _this2 = this;

        var newOptions = {
          chart: {
            type: this.type || this.options.chart.type || "line",
            height: this.height,
            width: this.width,
            events: {}
          },
          series: this.series
        };
        Object.keys(this.$listeners).forEach(function (evt) {
          newOptions.chart.events[evt] = _this2.$listeners[evt];
        });
        var config = this.extend(this.options, newOptions);
        this.chart = new ApexCharts(this.$el, config);
        return this.chart.render();
      },
      isObject: function isObject(item) {
        return item && _typeof(item) === "object" && !Array.isArray(item) && item != null;
      },
      extend: function extend(target, source) {
        var _this3 = this;

        if (typeof Object.assign !== "function") {
          (function () {
            Object.assign = function (target) {
              // We must check against these specific cases.
              if (target === undefined || target === null) {
                throw new TypeError("Cannot convert undefined or null to object");
              }

              var output = Object(target);

              for (var index = 1; index < arguments.length; index++) {
                var _source = arguments[index];

                if (_source !== undefined && _source !== null) {
                  for (var nextKey in _source) {
                    if (_source.hasOwnProperty(nextKey)) {
                      output[nextKey] = _source[nextKey];
                    }
                  }
                }
              }

              return output;
            };
          })();
        }

        var output = Object.assign({}, target);

        if (this.isObject(target) && this.isObject(source)) {
          Object.keys(source).forEach(function (key) {
            if (_this3.isObject(source[key])) {
              if (!(key in target)) {
                Object.assign(output, _defineProperty({}, key, source[key]));
              } else {
                output[key] = _this3.extend(target[key], source[key]);
              }
            } else {
              Object.assign(output, _defineProperty({}, key, source[key]));
            }
          });
        }

        return output;
      },
      refresh: function refresh() {
        this.destroy();
        return this.init();
      },
      destroy: function destroy() {
        this.chart.destroy();
      },
      updateSeries: function updateSeries(newSeries, animate) {
        return this.chart.updateSeries(newSeries, animate);
      },
      updateOptions: function updateOptions(newOptions, redrawPaths, animate, updateSyncedCharts) {
        return this.chart.updateOptions(newOptions, redrawPaths, animate, updateSyncedCharts);
      },
      toggleSeries: function toggleSeries(seriesName) {
        return this.chart.toggleSeries(seriesName);
      },
      showSeries: function showSeries(seriesName) {
        this.chart.showSeries(seriesName);
      },
      hideSeries: function hideSeries(seriesName) {
        this.chart.hideSeries(seriesName);
      },
      appendSeries: function appendSeries(newSeries, animate) {
        return this.chart.appendSeries(newSeries, animate);
      },
      resetSeries: function resetSeries() {
        this.chart.resetSeries();
      },
      zoomX: function zoomX(min, max) {
        this.chart.zoomX(min, max);
      },
      toggleDataPointSelection: function toggleDataPointSelection(seriesIndex, dataPointIndex) {
        this.chart.toggleDataPointSelection(seriesIndex, dataPointIndex);
      },
      appendData: function appendData(newData) {
        return this.chart.appendData(newData);
      },
      addText: function addText(options) {
        this.chart.addText(options);
      },
      addImage: function addImage(options) {
        this.chart.addImage(options);
      },
      addShape: function addShape(options) {
        this.chart.addShape(options);
      },
      dataURI: function dataURI() {
        return this.chart.dataURI();
      },
      setLocale: function setLocale(localeName) {
        return this.chart.setLocale(localeName);
      },
      addXaxisAnnotation: function addXaxisAnnotation(options, pushToMemory) {
        this.chart.addXaxisAnnotation(options, pushToMemory);
      },
      addYaxisAnnotation: function addYaxisAnnotation(options, pushToMemory) {
        this.chart.addYaxisAnnotation(options, pushToMemory);
      },
      addPointAnnotation: function addPointAnnotation(options, pushToMemory) {
        this.chart.addPointAnnotation(options, pushToMemory);
      },
      removeAnnotation: function removeAnnotation(id, options) {
        this.chart.removeAnnotation(id, options);
      },
      clearAnnotations: function clearAnnotations() {
        this.chart.clearAnnotations();
      }
    }
  };

  var VueApexCharts = ApexChartsComponent;
  window.ApexCharts = ApexCharts;

  VueApexCharts.install = function (Vue) {
    //adding a global method or property
    Vue.ApexCharts = ApexCharts;
    window.ApexCharts = ApexCharts; // add the instance method

    Object.defineProperty(Vue.prototype, '$apexcharts', {
      get: function get() {
        return ApexCharts;
      }
    });
  };

  return VueApexCharts;

})));
});

//

var script$g = {
  name: 'MeChart',
  components: {
    vueApexcharts
  },
  props: {
    type: {
      type: String,
      default: 'bar'
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    series: {
      type: Array,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    categoryType: {
      type: String,
      default: 'category'
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    showDataLabel: {
      type: Boolean,
      default: true
    },
    showTooltip: {
      type: Boolean,
      default: true
    },
    partial: {
      type: Boolean,
      default: false
    },
    stacking: {
      type: String,
      default: null
    },
    formatter: {
      type: Function,
      default: value => value
    }
  },
  data() {
    return {
      selection: [],
      stackable: {
        bar: true,
        column: true
      }
    }
  },
  computed: {
    formattedCategories() {
      return this.categories.map(category => this.formatter(category) || this.$toolkit.t('analytics.empty'))
    },
    chartType() {
      if (this.type === 'column') {
        return 'bar'
      } else {
        return this.type
      }
    },
    chartOptions() {
      if (this.type === 'pie' || this.type === 'donut') {
        return {
          chart: {
            events: {
              dataPointSelection: this.dataPointSelection
            }
          },
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  show: this.type === 'donut',
                  total: {
                    show: true
                  }
                }
              }
            }
          },
          labels: this.formattedCategories,
          title: {
            text: this.title || undefined,
            align: 'center',
            style: {
              fontSize: '16px'
            }
          },
          subtitle: {
            text: this.subtitle || undefined,
            align: 'center',
            style: {
              fontSize: '12px'
            }
          },
          dataLabels: {
            enabled: this.showDataLabel
          },
          legend: {
            show: this.showLegend,
            formatter(seriesName) {
              return truncate_1(seriesName, { length: 15 })
            }
          },
          tooltip: {
            enabled: this.showTooltip,
            followCursor: true
          }
        }
      } else {
        return {
          chart: {
            stacked: this.stackable[this.type] && this.stacking,
            stackType: this.stackable[this.type] && this.stacking === 'percentage' ? '100%' : undefined,
            toolbar: {
              tools: {
                download: false
              }
            },
            sparkline: {
              enabled: this.partial ? true : undefined
            },
            events: {
              dataPointSelection: this.dataPointSelection
            }
          },
          stroke: {
            curve: this.partial ? 'straight' : 'smooth'
          },
          title: {
            text: this.title || undefined,
            align: 'center',
            style: {
              fontSize: '16px'
            }
          },
          subtitle: {
            text: this.subtitle || undefined,
            align: 'center',
            style: {
              fontSize: '12px'
            }
          },
          dataLabels: {
            enabled: this.showDataLabel
          },
          legend: {
            show: this.showLegend
          },
          tooltip: {
            enabled: this.showTooltip,
            followCursor: true,
            x: {
              show: true
            }
          },
          plotOptions: {
            bar: {
              horizontal: this.type === 'bar',
              distributed: this.series.length === 1
            }
          },
          xaxis: {
            type: this.categoryType,
            categories: this.formattedCategories,
            labels: {
              trim: true,
              hideOverlappingLabels: false
            }
          }
        }
      }
    },
    chartSeries() {
      if (this.type === 'pie' || this.type === 'donut') {
        return this.series[0].data
      } else {
        if (this.categoryType === 'datetime') {
          const series = [];
          this.series.forEach((item) => {
            const serie = {
              name: item.name,
              data: []
            };
            item.data.forEach((value, index) => {
              serie.data.push([this.formattedCategories[index], value]);
            });
            series.push(serie);
          });
          return series
        }

        return this.series
      }
    }
  },
  mounted() {
    window.addEventListener('keyup', this.onKeyUp);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.onKeyUp);
  },
  methods: {
    download() {
      this.$refs.chart.dataURI().then((uri) => {
        const download = document.createElement('a');
        download.href = uri;
        download.download = 'chart.png';
        download.click();
      });
    },
    dataPointSelection(event, _chartContext, config) {
      const category = this.categories[config.dataPointIndex];
      const index = this.selection.indexOf(category);
      if (index < 0) {
        this.selection.push(category);
      } else {
        this.selection.splice(index, 1);
      }
      if (!event || (!event.ctrlKey && !event.metaKey)) {
        this.dispatchSelection();
      }
    },
    onKeyUp(e) {
      if (e.keyCode === 17 || e.keyCode === 91) {
        this.dispatchSelection();
      }
    },
    dispatchSelection() {
      if (this.selection.length > 0) {
        this.$emit('selection', {
          categories: this.selection
        });
        this.selection = [];
      }
    }
  }
};

/* script */
const __vue_script__$g = script$g;

/* template */
var __vue_render__$g = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("vue-apexcharts", {
    ref: "chart",
    attrs: {
      type: _vm.chartType,
      width: _vm.width,
      height: _vm.height,
      options: _vm.chartOptions,
      series: _vm.chartSeries
    }
  })
};
var __vue_staticRenderFns__$g = [];
__vue_render__$g._withStripped = true;

  /* style */
  const __vue_inject_styles__$g = undefined;
  /* scoped */
  const __vue_scope_id__$g = undefined;
  /* module identifier */
  const __vue_module_identifier__$g = undefined;
  /* functional template */
  const __vue_is_functional_template__$g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$g, staticRenderFns: __vue_staticRenderFns__$g },
    __vue_inject_styles__$g,
    __vue_script__$g,
    __vue_scope_id__$g,
    __vue_is_functional_template__$g,
    __vue_module_identifier__$g,
    false,
    undefined,
    undefined,
    undefined
  );

var withParamsBrowser = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.withParams = void 0;

function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var root = typeof window !== 'undefined' ? window : typeof commonjsGlobal !== 'undefined' ? commonjsGlobal : {};

var fakeWithParams = function fakeWithParams(paramsOrClosure, maybeValidator) {
  if (_typeof(paramsOrClosure) === 'object' && maybeValidator !== undefined) {
    return maybeValidator;
  }

  return paramsOrClosure(function () {});
};

var withParams = root.vuelidate ? root.vuelidate.withParams : fakeWithParams;
exports.withParams = withParams;
});

unwrapExports(withParamsBrowser);
var withParamsBrowser_1 = withParamsBrowser.withParams;

var params = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.pushParams = pushParams;
exports.popParams = popParams;
exports.withParams = withParams;
exports._setTarget = exports.target = void 0;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var stack = [];
var target = null;
exports.target = target;

var _setTarget = function _setTarget(x) {
  exports.target = target = x;
};

exports._setTarget = _setTarget;

function pushParams() {
  if (target !== null) {
    stack.push(target);
  }

  exports.target = target = {};
}

function popParams() {
  var lastTarget = target;
  var newTarget = exports.target = target = stack.pop() || null;

  if (newTarget) {
    if (!Array.isArray(newTarget.$sub)) {
      newTarget.$sub = [];
    }

    newTarget.$sub.push(lastTarget);
  }

  return lastTarget;
}

function addParams(params) {
  if (_typeof(params) === 'object' && !Array.isArray(params)) {
    exports.target = target = _objectSpread({}, target, {}, params);
  } else {
    throw new Error('params must be an object');
  }
}

function withParamsDirect(params, validator) {
  return withParamsClosure(function (add) {
    return function () {
      add(params);

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return validator.apply(this, args);
    };
  });
}

function withParamsClosure(closure) {
  var validator = closure(addParams);
  return function () {
    pushParams();

    try {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      return validator.apply(this, args);
    } finally {
      popParams();
    }
  };
}

function withParams(paramsOrClosure, maybeValidator) {
  if (_typeof(paramsOrClosure) === 'object' && maybeValidator !== undefined) {
    return withParamsDirect(paramsOrClosure, maybeValidator);
  }

  return withParamsClosure(paramsOrClosure);
}
});

unwrapExports(params);
var params_1 = params.pushParams;
var params_2 = params.popParams;
var params_3 = params.withParams;
var params_4 = params._setTarget;
var params_5 = params.target;

var withParams_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var withParams = process.env.BUILD === 'web' ? withParamsBrowser.withParams : params.withParams;
var _default = withParams;
exports.default = _default;
});

unwrapExports(withParams_1);

var common = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
Object.defineProperty(exports, "withParams", {
  enumerable: true,
  get: function get() {
    return _withParams.default;
  }
});
exports.regex = exports.ref = exports.len = exports.req = void 0;

var _withParams = _interopRequireDefault(withParams_1);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var req = function req(value) {
  if (Array.isArray(value)) return !!value.length;

  if (value === undefined || value === null) {
    return false;
  }

  if (value === false) {
    return true;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }

  if (_typeof(value) === 'object') {
    for (var _ in value) {
      return true;
    }

    return false;
  }

  return !!String(value).length;
};

exports.req = req;

var len = function len(value) {
  if (Array.isArray(value)) return value.length;

  if (_typeof(value) === 'object') {
    return Object.keys(value).length;
  }

  return String(value).length;
};

exports.len = len;

var ref = function ref(reference, vm, parentVm) {
  return typeof reference === 'function' ? reference.call(vm, parentVm) : parentVm[reference];
};

exports.ref = ref;

var regex = function regex(type, expr) {
  return (0, _withParams.default)({
    type: type
  }, function (value) {
    return !req(value) || expr.test(value);
  });
};

exports.regex = regex;
});

unwrapExports(common);
var common_1 = common.regex;
var common_2 = common.ref;
var common_3 = common.len;
var common_4 = common.req;

var required = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;



var _default = (0, common.withParams)({
  type: 'required'
}, function (value) {
  if (typeof value === 'string') {
    return (0, common.req)(value.trim());
  }

  return (0, common.req)(value);
});

exports.default = _default;
});

var required$1 = unwrapExports(required);

var requiredIf = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;



var _default = function _default(prop) {
  return (0, common.withParams)({
    type: 'requiredIf',
    prop: prop
  }, function (value, parentVm) {
    return (0, common.ref)(prop, this, parentVm) ? (0, common.req)(value) : true;
  });
};

exports.default = _default;
});

var requiredIf$1 = unwrapExports(requiredIf);

//

var script$h = {
  name: 'MeForm',
  components: {
    Button: __vue_component__$6
  },
  props: {
    busy: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onSubmit() {
      this.$emit('submit');
    }
  }
};

/* script */
const __vue_script__$h = script$h;

/* template */
var __vue_render__$h = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "form",
    {
      staticClass: "me-form",
      on: {
        submit: function($event) {
          $event.preventDefault();
          return _vm.onSubmit($event)
        }
      }
    },
    [
      _vm._t("default"),
      _vm._v(" "),
      _vm._t("form-actions", [
        _c(
          "div",
          { staticClass: "button-bar" },
          [
            _c(
              "Button",
              { attrs: { busy: _vm.busy, type: "submit", variant: "primary" } },
              [
                _vm._v(
                  "\n        " +
                    _vm._s(_vm.$toolkit.t("analytics.actions.save")) +
                    "\n      "
                )
              ]
            )
          ],
          1
        )
      ])
    ],
    2
  )
};
var __vue_staticRenderFns__$h = [];
__vue_render__$h._withStripped = true;

  /* style */
  const __vue_inject_styles__$h = function (inject) {
    if (!inject) return
    inject("data-v-172ac67c_0", { source: "form.me-form > div.button-bar[data-v-172ac67c] {\n  border-top: 1px solid #eee;\n  padding-top: 10px;\n  text-align: right;\n}\n\n/*# sourceMappingURL=Form.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Form.vue","Form.vue"],"names":[],"mappings":"AAsCA;EACA,0BAAA;EACA,iBAAA;EACA,iBAAA;ACrCA;;AAEA,mCAAmC","file":"Form.vue","sourcesContent":["<template>\n  <form @submit.prevent=\"onSubmit\" class=\"me-form\">\n    <slot />\n\n    <slot name=\"form-actions\">\n      <div class=\"button-bar\">\n        <Button :busy=\"busy\" type=\"submit\" variant=\"primary\">\n          {{ $toolkit.t('analytics.actions.save') }}\n        </Button>\n      </div>\n    </slot>\n  </form>\n</template>\n\n<script>\nimport Button from './Button.vue'\n\nexport default {\n  name: 'MeForm',\n  components: {\n    Button\n  },\n  props: {\n    busy: {\n      type: Boolean,\n      default: false\n    }\n  },\n  methods: {\n    onSubmit() {\n      this.$emit('submit')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  form.me-form {\n    > div.button-bar {\n        border-top: 1px solid #eee;\n        padding-top: 10px;\n        text-align: right;\n    }\n  }\n</style>\n","form.me-form > div.button-bar {\n  border-top: 1px solid #eee;\n  padding-top: 10px;\n  text-align: right;\n}\n\n/*# sourceMappingURL=Form.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$h = "data-v-172ac67c";
  /* module identifier */
  const __vue_module_identifier__$h = undefined;
  /* functional template */
  const __vue_is_functional_template__$h = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$h, staticRenderFns: __vue_staticRenderFns__$h },
    __vue_inject_styles__$h,
    __vue_script__$h,
    __vue_scope_id__$h,
    __vue_is_functional_template__$h,
    __vue_module_identifier__$h,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$i = {
  name: 'MeFormGroup',
  components: {
    BFormInvalidFeedback,
    BFormValidFeedback
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: null
    },
    state: {
      type: Boolean,
      default: null
    },
    invalidFeedback: {
      type: String,
      default: ''
    },
    validFeedback: {
      type: String,
      default: ''
    },
    cols: {
      type: Number,
      default: null
    },
    colsSm: {
      type: Number,
      default: null
    },
    colsMd: {
      type: Number,
      default: null
    },
    colsLg: {
      type: Number,
      default: null
    },
    colsXl: {
      type: Number,
      default: null
    },
    labelCols: {
      type: Number,
      default: null
    },
    labelColsSm: {
      type: Number,
      default: null
    },
    labelColsMd: {
      type: Number,
      default: null
    },
    labelColsLg: {
      type: Number,
      default: null
    },
    labelColsXl: {
      type: Number,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    tooltip: {
      type: String,
      default: null
    },
    markRequired: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    clazz() {
      let clazz = 'form-group';
      if (this.labelClazz) {
        clazz += ' row';
      }
      if (this.cols) {
        clazz += ` col-${this.cols}`;
      }
      if (this.colsSm) {
        clazz += ` col-sm-${this.colsSm}`;
      }
      if (this.colsMd) {
        clazz += ` col-md-${this.colsMd}`;
      }
      if (this.colsLg) {
        clazz += ` col-lg-${this.colsLg}`;
      }
      if (this.colsXl) {
        clazz += ` col-xl-${this.colsXl}`;
      }
      return clazz
    },
    labelClazz() {
      let clazz = '';
      if (this.labelCols) {
        clazz += ` col-${this.labelCols}`;
      }
      if (this.labelColsSm) {
        clazz += ` col-sm-${this.labelColsSm}`;
      }
      if (this.labelColsMd) {
        clazz += ` col-md-${this.labelColsMd}`;
      }
      if (this.labelColsLg) {
        clazz += ` col-lg-${this.labelColsLg}`;
      }
      if (this.labelColsXl) {
        clazz += ` col-xl-${this.labelColsXl}`;
      }
      return clazz
    },
    colClazz() {
      let clazz = '';
      if (this.labelCols) {
        clazz += ` col-${12 - this.labelCols}`;
      }
      if (this.labelColsSm) {
        clazz += ` col-sm-${12 - this.labelColsSm}`;
      }
      if (this.labelColsMd) {
        clazz += ` col-md-${12 - this.labelColsMd}`;
      }
      if (this.labelColsLg) {
        clazz += ` col-lg-${12 - this.labelColsLg}`;
      }
      if (this.labelColsXl) {
        clazz += ` col-xl-${12 - this.labelColsXl}`;
      }
      return clazz
    }
  }
};

/* script */
const __vue_script__$i = script$i;

/* template */
var __vue_render__$i = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "form-group",
      class: _vm.clazz,
      attrs: { "data-cy": "me-form-group" }
    },
    [
      _c("div", { staticClass: "icon-label", class: _vm.labelClazz }, [
        _vm.label
          ? _c("label", [
              _vm._v("\n      " + _vm._s(_vm.label) + "\n      "),
              _vm.markRequired
                ? _c("span", [_vm._v("\n        *\n      ")])
                : _vm._e(),
              _vm._v(" "),
              _vm.icon
                ? _c("i", {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value: _vm.tooltip,
                        expression: "tooltip"
                      }
                    ],
                    class: _vm.icon
                  })
                : _vm._e()
            ])
          : _vm._e()
      ]),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "form-group__invalid-feedback", class: _vm.colClazz },
        [
          _vm._t("default"),
          _vm._v(" "),
          _c("b-form-invalid-feedback", { attrs: { state: _vm.state } }, [
            _vm._v("\n      " + _vm._s(_vm.invalidFeedback) + "\n    ")
          ]),
          _vm._v(" "),
          _c("b-form-valid-feedback", { attrs: { state: _vm.state } }, [
            _vm._v("\n      " + _vm._s(_vm.validFeedback) + "\n    ")
          ]),
          _vm._v(" "),
          _vm.description
            ? _c("small", { staticClass: "form-text text-muted" }, [
                _vm._v("\n      " + _vm._s(_vm.description) + "\n    ")
              ])
            : _vm._e()
        ],
        2
      )
    ]
  )
};
var __vue_staticRenderFns__$i = [];
__vue_render__$i._withStripped = true;

  /* style */
  const __vue_inject_styles__$i = function (inject) {
    if (!inject) return
    inject("data-v-ddcdbd90_0", { source: "label[data-v-ddcdbd90] {\n  margin-bottom: 4px;\n  font-size: 12px;\n}\n.form-group .invalid-feedback[data-v-ddcdbd90],\n.form-group .valid-feedback[data-v-ddcdbd90],\n.form-group .text-muted[data-v-ddcdbd90] {\n  margin-top: 4px;\n  font-size: 12px;\n}\n.icon-label[data-v-ddcdbd90] {\n  display: flex;\n  font-size: 14px;\n}\n.icon-label i[data-v-ddcdbd90] {\n  margin-left: 5px;\n}\n\n/*# sourceMappingURL=FormGroup.vue.map */", map: {"version":3,"sources":["/app/build/src/components/FormGroup.vue","FormGroup.vue"],"names":[],"mappings":"AA2LA;EACA,kBAAA;EACA,eAAA;AC1LA;AD8LA;;;EAGA,eAAA;EACA,eAAA;AC3LA;AD+LA;EACA,aAAA;EACA,eAAA;AC5LA;AD8LA;EACA,gBAAA;AC5LA;;AAEA,wCAAwC","file":"FormGroup.vue","sourcesContent":["<template>\n  <div\n    :class=\"clazz\"\n    class=\"form-group\"\n    data-cy=\"me-form-group\"\n  >\n    <div :class=\"labelClazz\" class=\"icon-label\">\n      <label v-if=\"label\">\n        {{ label }}\n        <span v-if=\"markRequired\">\n          *\n        </span>\n        <i\n          v-if=\"icon\"\n          :class=\"icon\"\n          v-me-tooltip=\"tooltip\"\n        />\n      </label>\n    </div>\n\n    <div :class=\"colClazz\" class=\"form-group__invalid-feedback\">\n      <slot />\n      <b-form-invalid-feedback :state=\"state\">\n        {{ invalidFeedback }}\n      </b-form-invalid-feedback>\n\n      <b-form-valid-feedback :state=\"state\">\n        {{ validFeedback }}\n      </b-form-valid-feedback>\n\n      <small v-if=\"description\" class=\"form-text text-muted\">\n        {{ description }}\n      </small>\n    </div>\n  </div>\n</template>\n\n<script>\nimport { BFormInvalidFeedback, BFormValidFeedback } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeFormGroup',\n  components: {\n    BFormInvalidFeedback,\n    BFormValidFeedback\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    description: {\n      type: String,\n      default: null\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    invalidFeedback: {\n      type: String,\n      default: ''\n    },\n    validFeedback: {\n      type: String,\n      default: ''\n    },\n    cols: {\n      type: Number,\n      default: null\n    },\n    colsSm: {\n      type: Number,\n      default: null\n    },\n    colsMd: {\n      type: Number,\n      default: null\n    },\n    colsLg: {\n      type: Number,\n      default: null\n    },\n    colsXl: {\n      type: Number,\n      default: null\n    },\n    labelCols: {\n      type: Number,\n      default: null\n    },\n    labelColsSm: {\n      type: Number,\n      default: null\n    },\n    labelColsMd: {\n      type: Number,\n      default: null\n    },\n    labelColsLg: {\n      type: Number,\n      default: null\n    },\n    labelColsXl: {\n      type: Number,\n      default: null\n    },\n    icon: {\n      type: String,\n      default: null\n    },\n    tooltip: {\n      type: String,\n      default: null\n    },\n    markRequired: {\n      type: Boolean,\n      default: false\n    }\n  },\n  computed: {\n    clazz() {\n      let clazz = 'form-group'\n      if (this.labelClazz) {\n        clazz += ' row'\n      }\n      if (this.cols) {\n        clazz += ` col-${this.cols}`\n      }\n      if (this.colsSm) {\n        clazz += ` col-sm-${this.colsSm}`\n      }\n      if (this.colsMd) {\n        clazz += ` col-md-${this.colsMd}`\n      }\n      if (this.colsLg) {\n        clazz += ` col-lg-${this.colsLg}`\n      }\n      if (this.colsXl) {\n        clazz += ` col-xl-${this.colsXl}`\n      }\n      return clazz\n    },\n    labelClazz() {\n      let clazz = ''\n      if (this.labelCols) {\n        clazz += ` col-${this.labelCols}`\n      }\n      if (this.labelColsSm) {\n        clazz += ` col-sm-${this.labelColsSm}`\n      }\n      if (this.labelColsMd) {\n        clazz += ` col-md-${this.labelColsMd}`\n      }\n      if (this.labelColsLg) {\n        clazz += ` col-lg-${this.labelColsLg}`\n      }\n      if (this.labelColsXl) {\n        clazz += ` col-xl-${this.labelColsXl}`\n      }\n      return clazz\n    },\n    colClazz() {\n      let clazz = ''\n      if (this.labelCols) {\n        clazz += ` col-${12 - this.labelCols}`\n      }\n      if (this.labelColsSm) {\n        clazz += ` col-sm-${12 - this.labelColsSm}`\n      }\n      if (this.labelColsMd) {\n        clazz += ` col-md-${12 - this.labelColsMd}`\n      }\n      if (this.labelColsLg) {\n        clazz += ` col-lg-${12 - this.labelColsLg}`\n      }\n      if (this.labelColsXl) {\n        clazz += ` col-xl-${12 - this.labelColsXl}`\n      }\n      return clazz\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  label {\n    margin-bottom: 4px;\n    font-size: 12px;\n  }\n\n  .form-group {\n    .invalid-feedback,\n    .valid-feedback,\n    .text-muted {\n      margin-top: 4px;\n      font-size: 12px;\n    }\n  }\n\n  .icon-label {\n    display: flex;\n    font-size: 14px;\n\n    i {\n      margin-left: 5px;\n    }\n  }\n</style>\n","label {\n  margin-bottom: 4px;\n  font-size: 12px;\n}\n\n.form-group .invalid-feedback,\n.form-group .valid-feedback,\n.form-group .text-muted {\n  margin-top: 4px;\n  font-size: 12px;\n}\n\n.icon-label {\n  display: flex;\n  font-size: 14px;\n}\n.icon-label i {\n  margin-left: 5px;\n}\n\n/*# sourceMappingURL=FormGroup.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$i = "data-v-ddcdbd90";
  /* module identifier */
  const __vue_module_identifier__$i = undefined;
  /* functional template */
  const __vue_is_functional_template__$i = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$i, staticRenderFns: __vue_staticRenderFns__$i },
    __vue_inject_styles__$i,
    __vue_script__$i,
    __vue_scope_id__$i,
    __vue_is_functional_template__$i,
    __vue_module_identifier__$i,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$j = {
  name: 'MeFormRow'
};

/* script */
const __vue_script__$j = script$j;

/* template */
var __vue_render__$j = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "form-row" }, [_vm._t("default")], 2)
};
var __vue_staticRenderFns__$j = [];
__vue_render__$j._withStripped = true;

  /* style */
  const __vue_inject_styles__$j = undefined;
  /* scoped */
  const __vue_scope_id__$j = undefined;
  /* module identifier */
  const __vue_module_identifier__$j = undefined;
  /* functional template */
  const __vue_is_functional_template__$j = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$j, staticRenderFns: __vue_staticRenderFns__$j },
    __vue_inject_styles__$j,
    __vue_script__$j,
    __vue_scope_id__$j,
    __vue_is_functional_template__$j,
    __vue_module_identifier__$j,
    false,
    undefined,
    undefined,
    undefined
  );

/**
 * Performs a deep comparison between two values to determine if they are
 * equivalent.
 *
 * **Note:** This method supports comparing arrays, array buffers, booleans,
 * date objects, error objects, maps, numbers, `Object` objects, regexes,
 * sets, strings, symbols, and typed arrays. `Object` objects are compared
 * by their own, not inherited, enumerable properties. Functions and DOM
 * nodes are compared by strict equality, i.e. `===`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.isEqual(object, other);
 * // => true
 *
 * object === other;
 * // => false
 */
function isEqual(value, other) {
  return _baseIsEqual(value, other);
}

var isEqual_1 = isEqual;

//

var script$k = {
  name: 'MeSwitchButton',
  components: {
    Button: __vue_component__$6
  },
  props: {
    checked: {
      type: [Array, Boolean, String, Number, Object],
      default: null
    },
    value: {
      type: [Boolean, String, Number, Array, Object],
      default: true
    }
  },

  computed: {
    isActive() {
      if (this.isArray) {
        return this.checked.some(val => isEqual_1(val, this.value))
      }
      return isEqual_1(this.checked, this.value)
    },

    isArray() {
      return Array.isArray(this.checked)
    }
  },

  methods: {
    onEnabledClick() {
      if (this.isArray && !this.checked.some(val => isEqual_1(val, this.value))) {
        this.$emit('change', [...this.checked, this.value]);
      } else if (!this.isArray) this.$emit('change', this.value);
    },

    onDisabledClick() {
      if (this.isArray) this.$emit('change', this.checked.filter(val => !isEqual_1(val, this.value)));
      else this.$emit('change', false);
    }
  }
};

/* script */
const __vue_script__$k = script$k;

/* template */
var __vue_render__$k = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-switch-button",
      attrs: { "data-cy": "me-switch-button" }
    },
    [
      _c(
        "Button",
        {
          class: { "not-active": !_vm.isActive },
          attrs: {
            size: "sm",
            variant: "primary",
            "prepend-icon": "me-icon-s icon-circle-check"
          },
          on: { click: _vm.onEnabledClick }
        },
        [
          _vm._v(
            "\n    " +
              _vm._s("" + _vm.$toolkit.t("switchButton.enabled")) +
              "\n  "
          )
        ]
      ),
      _vm._v(" "),
      _c(
        "Button",
        {
          class: { "not-active": _vm.isActive },
          attrs: { size: "sm", "prepend-icon": "me-icon-s icon-circle-xmark" },
          on: { click: _vm.onDisabledClick }
        },
        [
          _vm._v(
            "\n    " +
              _vm._s("" + _vm.$toolkit.t("switchButton.disabled")) +
              "\n  "
          )
        ]
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$k = [];
__vue_render__$k._withStripped = true;

  /* style */
  const __vue_inject_styles__$k = function (inject) {
    if (!inject) return
    inject("data-v-aa2900f4_0", { source: ".me-switch-button[data-v-aa2900f4] {\n  display: flex;\n  gap: 16px;\n  padding: 8px;\n  background-color: var(--me-bg-gray);\n  width: fit-content;\n  border-radius: 8px;\n}\n.me-switch-button .me-button[data-v-aa2900f4] {\n  width: 284px;\n}\n.me-switch-button .me-button.not-active[data-v-aa2900f4] {\n  background-color: var(--me-bg-gray);\n  border: none;\n  color: var(--me-gray-4);\n}\n\n/*# sourceMappingURL=SwitchButton.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SwitchButton.vue","SwitchButton.vue"],"names":[],"mappings":"AA2EA;EACA,aAAA;EACA,SAAA;EACA,YAAA;EACA,mCAAA;EACA,kBAAA;EACA,kBAAA;AC1EA;AD4EA;EACA,YAAA;AC1EA;AD4EA;EACA,mCAAA;EACA,YAAA;EACA,uBAAA;AC1EA;;AAEA,2CAA2C","file":"SwitchButton.vue","sourcesContent":["<template>\n  <div\n    class=\"me-switch-button\"\n    data-cy=\"me-switch-button\"\n  >\n    <Button\n      @click=\"onEnabledClick\"\n      :class=\"{ 'not-active': !isActive }\"\n      size=\"sm\"\n      variant=\"primary\"\n      prepend-icon=\"me-icon-s icon-circle-check\"\n    >\n      {{ `${$toolkit.t('switchButton.enabled')}` }}\n    </Button>\n\n    <Button\n      @click=\"onDisabledClick\"\n      :class=\"{ 'not-active': isActive }\"\n      size=\"sm\"\n      prepend-icon=\"me-icon-s icon-circle-xmark\"\n    >\n      {{ `${$toolkit.t('switchButton.disabled')}` }}\n    </Button>\n  </div>\n</template>\n\n<script>\nimport isEqual from 'lodash/isEqual'\nimport Button from './Button.vue'\n\nexport default {\n  name: 'MeSwitchButton',\n  components: {\n    Button\n  },\n  props: {\n    checked: {\n      type: [Array, Boolean, String, Number, Object],\n      default: null\n    },\n    value: {\n      type: [Boolean, String, Number, Array, Object],\n      default: true\n    }\n  },\n\n  computed: {\n    isActive() {\n      if (this.isArray) {\n        return this.checked.some(val => isEqual(val, this.value))\n      }\n      return isEqual(this.checked, this.value)\n    },\n\n    isArray() {\n      return Array.isArray(this.checked)\n    }\n  },\n\n  methods: {\n    onEnabledClick() {\n      if (this.isArray && !this.checked.some(val => isEqual(val, this.value))) {\n        this.$emit('change', [...this.checked, this.value])\n      } else if (!this.isArray) this.$emit('change', this.value)\n    },\n\n    onDisabledClick() {\n      if (this.isArray) this.$emit('change', this.checked.filter(val => !isEqual(val, this.value)))\n      else this.$emit('change', false)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-switch-button {\n  display: flex;\n  gap: 16px;\n  padding: 8px;\n  background-color: var(--me-bg-gray);\n  width: fit-content;\n  border-radius: 8px;\n\n  .me-button {\n    width: 284px;\n\n    &.not-active {\n        background-color: var(--me-bg-gray);\n        border: none;\n        color: var(--me-gray-4);\n    }\n  }\n}\n</style>\n",".me-switch-button {\n  display: flex;\n  gap: 16px;\n  padding: 8px;\n  background-color: var(--me-bg-gray);\n  width: fit-content;\n  border-radius: 8px;\n}\n.me-switch-button .me-button {\n  width: 284px;\n}\n.me-switch-button .me-button.not-active {\n  background-color: var(--me-bg-gray);\n  border: none;\n  color: var(--me-gray-4);\n}\n\n/*# sourceMappingURL=SwitchButton.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$k = "data-v-aa2900f4";
  /* module identifier */
  const __vue_module_identifier__$k = undefined;
  /* functional template */
  const __vue_is_functional_template__$k = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$k, staticRenderFns: __vue_staticRenderFns__$k },
    __vue_inject_styles__$k,
    __vue_script__$k,
    __vue_scope_id__$k,
    __vue_is_functional_template__$k,
    __vue_module_identifier__$k,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$l = {
  name: 'MeCheckbox',
  components: {
    BFormCheckbox,
    SwitchButton: __vue_component__$k
  },
  model: {
    prop: 'checked'
  },
  props: {
    checked: {
      type: [Array, Boolean, String, Number, Object],
      default: null
    },
    value: {
      type: [Boolean, String, Number, Array, Object],
      default: true
    },
    uncheckedValue: {
      type: [Boolean, String, Number, Array, Object],
      default: false
    },
    toggle: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    indeterminate: {
      type: Boolean,
      default: false
    },
    switchButton: {
      type: Boolean,
      default: false
    },
    state: {
      type: Boolean,
      default: null
    },
    reverse: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'success', 'warning', 'danger'].includes(value)
    }
  },
  data() {
    return {
      model: this.checked,
      key: this.value
    }
  },
  watch: {
    checked(val) {
      this.model = val;
    },
    value(val) {
      this.key = val;
    }
  },

  methods: {
    onSwitchButtonChange(value) {
      this.model = value;
      this.$emit('input', value);
      this.$emit('change', value);
    }
  }
};

/* script */
const __vue_script__$l = script$l;

/* template */
var __vue_render__$l = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      !_vm.switchButton
        ? _c(
            "BFormCheckbox",
            {
              class: [
                "me-checkbox",
                "me-checkbox--" + _vm.variant,
                { reverse: _vm.reverse }
              ],
              attrs: {
                disabled: _vm.disabled,
                indeterminate: _vm.indeterminate,
                state: _vm.state,
                switch: _vm.toggle,
                value: _vm.key,
                "unchecked-value": _vm.uncheckedValue
              },
              on: {
                input: function($event) {
                  return _vm.$emit("input", $event)
                },
                change: function($event) {
                  return _vm.$emit("change", $event)
                }
              },
              model: {
                value: _vm.model,
                callback: function($$v) {
                  _vm.model = $$v;
                },
                expression: "model"
              }
            },
            [_vm._t("default")],
            2
          )
        : _c("SwitchButton", {
            attrs: { value: _vm.key, checked: _vm.model },
            on: { change: _vm.onSwitchButtonChange }
          })
    ],
    1
  )
};
var __vue_staticRenderFns__$l = [];
__vue_render__$l._withStripped = true;

  /* style */
  const __vue_inject_styles__$l = function (inject) {
    if (!inject) return
    inject("data-v-45bacc5c_0", { source: ".me-checkbox--primary[data-v-45bacc5c]  .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-primary-2);\n  border-color: var(--me-primary-2);\n}\n.me-checkbox--success[data-v-45bacc5c]  .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-success-2);\n  border-color: var(--me-success-2);\n}\n.me-checkbox--warning[data-v-45bacc5c]  .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-warning-2);\n  border-color: var(--me-warning-2);\n}\n.me-checkbox--danger[data-v-45bacc5c]  .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-checkbox[data-v-45bacc5c]  .custom-control-input[disabled] ~ .custom-control-label::before,\n.me-checkbox[data-v-45bacc5c]  .custom-control-input:disabled ~ .custom-control-label::before {\n  background-color: var(--me-gray-6);\n  border-color: var(--me-gray-4);\n}\n.me-checkbox[data-v-45bacc5c]  .custom-control-input:disabled:checked ~ .custom-control-label::before {\n  background-color: var(--me-gray-4);\n}\n.me-checkbox[data-v-45bacc5c]  .custom-control-label {\n  font-size: 14px;\n}\n.me-checkbox.reverse[data-v-45bacc5c] {\n  padding: 0;\n}\n.me-checkbox.reverse[data-v-45bacc5c]  label {\n  padding-right: 1.5rem;\n}\n.me-checkbox.reverse[data-v-45bacc5c]  label::before {\n  right: 0;\n  left: initial !important;\n}\n.me-checkbox.reverse[data-v-45bacc5c]  label::after {\n  right: 0;\n  left: initial !important;\n}\n.me-checkbox.custom-switch[data-v-45bacc5c]  .custom-control-input[disabled] ~ .custom-control-label::before,\n.me-checkbox.custom-switch[data-v-45bacc5c]  .custom-control-input:disabled ~ .custom-control-label::before {\n  background-color: var(--me-gray-6);\n  border-color: var(--me-gray-6);\n}\n.me-checkbox.custom-switch.reverse[data-v-45bacc5c] {\n  padding: 0;\n}\n.me-checkbox.custom-switch.reverse[data-v-45bacc5c]  label {\n  padding-right: 2.25rem;\n}\n.me-checkbox.custom-switch.reverse[data-v-45bacc5c]  label::before {\n  right: 0;\n  left: initial !important;\n}\n.me-checkbox.custom-switch.reverse[data-v-45bacc5c]  label::after {\n  right: calc(0.75rem + 2px);\n  left: initial !important;\n}\n.me-checkbox.custom-checkbox[data-v-45bacc5c]  .custom-control-input:not(:indeterminate):not(:checked) ~ .custom-control-label::before {\n  border: var(--me-gray-2) solid 1px;\n}\n\n/*# sourceMappingURL=Checkbox.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Checkbox.vue","Checkbox.vue"],"names":[],"mappings":"AAmHA;EACA,qCAAA;EACA,iCAAA;AClHA;ADgHA;EACA,qCAAA;EACA,iCAAA;AC9GA;AD4GA;EACA,qCAAA;EACA,iCAAA;AC1GA;ADwGA;EACA,oCAAA;EACA,gCAAA;ACtGA;ADiHA;;EAEA,kCAAA;EACA,8BAAA;AC/GA;ADkHA;EACA,kCAAA;AChHA;ADqHA;EACA,eAAA;ACnHA;ADuHA;EACA,UAAA;ACrHA;ADwHA;EACA,qBAAA;ACtHA;ADwHA;EACA,QAAA;EACA,wBAAA;ACtHA;ADyHA;EACA,QAAA;EACA,wBAAA;ACvHA;AD+HA;;EAEA,kCAAA;EACA,8BAAA;AC7HA;ADiIA;EACA,UAAA;AC/HA;ADkIA;EACA,sBAAA;AChIA;ADkIA;EACA,QAAA;EACA,wBAAA;AChIA;ADmIA;EACA,0BAAA;EACA,wBAAA;ACjIA;ADyIA;EACA,kCAAA;ACvIA;;AAEA,uCAAuC","file":"Checkbox.vue","sourcesContent":["<template>\n  <div>\n    <BFormCheckbox\n      v-if=\"!switchButton\"\n      v-model=\"model\"\n      :disabled=\"disabled\"\n      :indeterminate=\"indeterminate\"\n      :state=\"state\"\n      :switch=\"toggle\"\n      :value=\"key\"\n      :unchecked-value=\"uncheckedValue\"\n      @input=\"$emit('input', $event)\"\n      @change=\"$emit('change', $event)\"\n      :class=\"['me-checkbox', `me-checkbox--${variant}`, {reverse}]\"\n    >\n      <slot />\n    </BFormCheckbox>\n\n    <SwitchButton\n      v-else\n      @change=\"onSwitchButtonChange\"\n      :value=\"key\"\n      :checked=\"model\"\n    />\n  </div>\n</template>\n\n<script>\nimport { BFormCheckbox } from 'bootstrap-vue'\nimport SwitchButton from './SwitchButton.vue'\n\nexport default {\n  name: 'MeCheckbox',\n  components: {\n    BFormCheckbox,\n    SwitchButton\n  },\n  model: {\n    prop: 'checked'\n  },\n  props: {\n    checked: {\n      type: [Array, Boolean, String, Number, Object],\n      default: null\n    },\n    value: {\n      type: [Boolean, String, Number, Array, Object],\n      default: true\n    },\n    uncheckedValue: {\n      type: [Boolean, String, Number, Array, Object],\n      default: false\n    },\n    toggle: {\n      type: Boolean,\n      default: false\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    indeterminate: {\n      type: Boolean,\n      default: false\n    },\n    switchButton: {\n      type: Boolean,\n      default: false\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    reverse: {\n      type: Boolean,\n      default: false\n    },\n    variant: {\n      type: String,\n      default: 'primary',\n      validator: value => ['primary', 'success', 'warning', 'danger'].includes(value)\n    }\n  },\n  data() {\n    return {\n      model: this.checked,\n      key: this.value\n    }\n  },\n  watch: {\n    checked(val) {\n      this.model = val\n    },\n    value(val) {\n      this.key = val\n    }\n  },\n\n  methods: {\n    onSwitchButtonChange(value) {\n      this.model = value\n      this.$emit('input', value)\n      this.$emit('change', value)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $variantList: 'primary', 'success', 'warning', 'danger';\n\n  @mixin variant() {\n    @each $variantName in $variantList {\n      &--#{$variantName} {\n        ::v-deep {\n          .custom-control-input:checked ~ .custom-control-label::before {\n            background-color: var(--me-#{$variantName}-2);\n            border-color: var(--me-#{$variantName}-2);\n          }\n        }\n      }\n    }\n  }\n\n  .me-checkbox {\n    @include variant();\n\n    ::v-deep {\n      .custom-control-input[disabled] ~ .custom-control-label::before,\n      .custom-control-input:disabled ~ .custom-control-label::before {\n        background-color: var(--me-gray-6);\n        border-color: var(--me-gray-4);\n      }\n\n      .custom-control-input:disabled:checked ~ .custom-control-label::before {\n        background-color: var(--me-gray-4);\n      }\n    }\n\n    ::v-deep {\n      .custom-control-label {\n        font-size: 14px;\n      }\n    }\n\n    &.reverse {\n      padding: 0;\n\n      ::v-deep {\n        label {\n          padding-right: 1.5rem;\n\n          &::before {\n            right: 0;\n            left: initial !important;\n          }\n\n          &::after {\n            right: 0;\n            left: initial !important;\n          }\n        }\n      }\n    }\n\n    &.custom-switch {\n      ::v-deep {\n        .custom-control-input[disabled] ~ .custom-control-label::before,\n        .custom-control-input:disabled ~ .custom-control-label::before {\n          background-color: var(--me-gray-6);\n          border-color: var(--me-gray-6);\n        }\n      }\n\n      &.reverse {\n        padding: 0;\n\n        ::v-deep {\n          label {\n            padding-right: 2.25rem;\n\n            &::before {\n              right: 0;\n              left: initial !important;\n            }\n\n            &::after {\n              right: calc(0.75rem + 2px);\n              left: initial !important;\n            }\n          }\n        }\n      }\n    }\n\n    &.custom-checkbox {\n      ::v-deep .custom-control-input:not(:indeterminate):not(:checked) ~ .custom-control-label::before  {\n        border: var(--me-gray-2) solid 1px;\n      }\n    }\n  }\n\n</style>\n",".me-checkbox--primary ::v-deep .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-primary-2);\n  border-color: var(--me-primary-2);\n}\n.me-checkbox--success ::v-deep .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-success-2);\n  border-color: var(--me-success-2);\n}\n.me-checkbox--warning ::v-deep .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-warning-2);\n  border-color: var(--me-warning-2);\n}\n.me-checkbox--danger ::v-deep .custom-control-input:checked ~ .custom-control-label::before {\n  background-color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-checkbox ::v-deep .custom-control-input[disabled] ~ .custom-control-label::before,\n.me-checkbox ::v-deep .custom-control-input:disabled ~ .custom-control-label::before {\n  background-color: var(--me-gray-6);\n  border-color: var(--me-gray-4);\n}\n.me-checkbox ::v-deep .custom-control-input:disabled:checked ~ .custom-control-label::before {\n  background-color: var(--me-gray-4);\n}\n.me-checkbox ::v-deep .custom-control-label {\n  font-size: 14px;\n}\n.me-checkbox.reverse {\n  padding: 0;\n}\n.me-checkbox.reverse ::v-deep label {\n  padding-right: 1.5rem;\n}\n.me-checkbox.reverse ::v-deep label::before {\n  right: 0;\n  left: initial !important;\n}\n.me-checkbox.reverse ::v-deep label::after {\n  right: 0;\n  left: initial !important;\n}\n.me-checkbox.custom-switch ::v-deep .custom-control-input[disabled] ~ .custom-control-label::before,\n.me-checkbox.custom-switch ::v-deep .custom-control-input:disabled ~ .custom-control-label::before {\n  background-color: var(--me-gray-6);\n  border-color: var(--me-gray-6);\n}\n.me-checkbox.custom-switch.reverse {\n  padding: 0;\n}\n.me-checkbox.custom-switch.reverse ::v-deep label {\n  padding-right: 2.25rem;\n}\n.me-checkbox.custom-switch.reverse ::v-deep label::before {\n  right: 0;\n  left: initial !important;\n}\n.me-checkbox.custom-switch.reverse ::v-deep label::after {\n  right: calc(0.75rem + 2px);\n  left: initial !important;\n}\n.me-checkbox.custom-checkbox ::v-deep .custom-control-input:not(:indeterminate):not(:checked) ~ .custom-control-label::before {\n  border: var(--me-gray-2) solid 1px;\n}\n\n/*# sourceMappingURL=Checkbox.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$l = "data-v-45bacc5c";
  /* module identifier */
  const __vue_module_identifier__$l = undefined;
  /* functional template */
  const __vue_is_functional_template__$l = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$l, staticRenderFns: __vue_staticRenderFns__$l },
    __vue_inject_styles__$l,
    __vue_script__$l,
    __vue_scope_id__$l,
    __vue_is_functional_template__$l,
    __vue_module_identifier__$l,
    false,
    createInjector,
    undefined,
    undefined
  );

var vueSelect = createCommonjsModule(function (module, exports) {
!function(t,e){module.exports=e();}("undefined"!=typeof self?self:commonjsGlobal,(function(){return function(t){var e={};function n(o){if(e[o])return e[o].exports;var i=e[o]={i:o,l:!1,exports:{}};return t[o].call(i.exports,i,i.exports,n),i.l=!0,i.exports}return n.m=t,n.c=e,n.d=function(t,e,o){n.o(t,e)||Object.defineProperty(t,e,{enumerable:!0,get:o});},n.r=function(t){"undefined"!=typeof Symbol&&Symbol.toStringTag&&Object.defineProperty(t,Symbol.toStringTag,{value:"Module"}),Object.defineProperty(t,"__esModule",{value:!0});},n.t=function(t,e){if(1&e&&(t=n(t)),8&e)return t;if(4&e&&"object"==typeof t&&t&&t.__esModule)return t;var o=Object.create(null);if(n.r(o),Object.defineProperty(o,"default",{enumerable:!0,value:t}),2&e&&"string"!=typeof t)for(var i in t)n.d(o,i,function(e){return t[e]}.bind(null,i));return o},n.n=function(t){var e=t&&t.__esModule?function(){return t.default}:function(){return t};return n.d(e,"a",e),e},n.o=function(t,e){return Object.prototype.hasOwnProperty.call(t,e)},n.p="/",n(n.s=8)}([function(t,e,n){var o=n(4),i=n(5),s=n(6);t.exports=function(t){return o(t)||i(t)||s()};},function(t,e){function n(e){return "function"==typeof Symbol&&"symbol"==typeof Symbol.iterator?t.exports=n=function(t){return typeof t}:t.exports=n=function(t){return t&&"function"==typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},n(e)}t.exports=n;},function(t,e,n){},function(t,e){t.exports=function(t,e,n){return e in t?Object.defineProperty(t,e,{value:n,enumerable:!0,configurable:!0,writable:!0}):t[e]=n,t};},function(t,e){t.exports=function(t){if(Array.isArray(t)){for(var e=0,n=new Array(t.length);e<t.length;e++)n[e]=t[e];return n}};},function(t,e){t.exports=function(t){if(Symbol.iterator in Object(t)||"[object Arguments]"===Object.prototype.toString.call(t))return Array.from(t)};},function(t,e){t.exports=function(){throw new TypeError("Invalid attempt to spread non-iterable instance")};},function(t,e,n){var o=n(2);n.n(o).a;},function(t,e,n){n.r(e);var o=n(0),i=n.n(o),s=n(1),r=n.n(s),a=n(3),l=n.n(a),c={props:{autoscroll:{type:Boolean,default:!0}},watch:{typeAheadPointer:function(){this.autoscroll&&this.maybeAdjustScroll();}},methods:{maybeAdjustScroll:function(){var t,e=(null===(t=this.$refs.dropdownMenu)||void 0===t?void 0:t.children[this.typeAheadPointer])||!1;if(e){var n=this.getDropdownViewport(),o=e.getBoundingClientRect(),i=o.top,s=o.bottom,r=o.height;if(i<n.top)return this.$refs.dropdownMenu.scrollTop=e.offsetTop;if(s>n.bottom)return this.$refs.dropdownMenu.scrollTop=e.offsetTop-(n.height-r)}},getDropdownViewport:function(){return this.$refs.dropdownMenu?this.$refs.dropdownMenu.getBoundingClientRect():{height:0,top:0,bottom:0}}}},u={data:function(){return {typeAheadPointer:-1}},watch:{filteredOptions:function(){for(var t=0;t<this.filteredOptions.length;t++)if(this.selectable(this.filteredOptions[t])){this.typeAheadPointer=t;break}}},methods:{typeAheadUp:function(){for(var t=this.typeAheadPointer-1;t>=0;t--)if(this.selectable(this.filteredOptions[t])){this.typeAheadPointer=t;break}},typeAheadDown:function(){for(var t=this.typeAheadPointer+1;t<this.filteredOptions.length;t++)if(this.selectable(this.filteredOptions[t])){this.typeAheadPointer=t;break}},typeAheadSelect:function(){var t=this.filteredOptions[this.typeAheadPointer];t&&this.select(t);}}},p={props:{loading:{type:Boolean,default:!1}},data:function(){return {mutableLoading:!1}},watch:{search:function(){this.$emit("search",this.search,this.toggleLoading);},loading:function(t){this.mutableLoading=t;}},methods:{toggleLoading:function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:null;return this.mutableLoading=null==t?!this.mutableLoading:t}}};function h(t,e,n,o,i,s,r,a){var l,c="function"==typeof t?t.options:t;if(e&&(c.render=e,c.staticRenderFns=n,c._compiled=!0),o&&(c.functional=!0),s&&(c._scopeId="data-v-"+s),r?(l=function(t){(t=t||this.$vnode&&this.$vnode.ssrContext||this.parent&&this.parent.$vnode&&this.parent.$vnode.ssrContext)||"undefined"==typeof __VUE_SSR_CONTEXT__||(t=__VUE_SSR_CONTEXT__),i&&i.call(this,t),t&&t._registeredComponents&&t._registeredComponents.add(r);},c._ssrRegister=l):i&&(l=a?function(){i.call(this,this.$root.$options.shadowRoot);}:i),l)if(c.functional){c._injectStyles=l;var u=c.render;c.render=function(t,e){return l.call(e),u(t,e)};}else {var p=c.beforeCreate;c.beforeCreate=p?[].concat(p,l):[l];}return {exports:t,options:c}}var d={Deselect:h({},(function(){var t=this.$createElement,e=this._self._c||t;return e("svg",{attrs:{xmlns:"http://www.w3.org/2000/svg",width:"10",height:"10"}},[e("path",{attrs:{d:"M6.895455 5l2.842897-2.842898c.348864-.348863.348864-.914488 0-1.263636L9.106534.261648c-.348864-.348864-.914489-.348864-1.263636 0L5 3.104545 2.157102.261648c-.348863-.348864-.914488-.348864-1.263636 0L.261648.893466c-.348864.348864-.348864.914489 0 1.263636L3.104545 5 .261648 7.842898c-.348864.348863-.348864.914488 0 1.263636l.631818.631818c.348864.348864.914773.348864 1.263636 0L5 6.895455l2.842898 2.842897c.348863.348864.914772.348864 1.263636 0l.631818-.631818c.348864-.348864.348864-.914489 0-1.263636L6.895455 5z"}})])}),[],!1,null,null,null).exports,OpenIndicator:h({},(function(){var t=this.$createElement,e=this._self._c||t;return e("svg",{attrs:{xmlns:"http://www.w3.org/2000/svg",width:"14",height:"10"}},[e("path",{attrs:{d:"M9.211364 7.59931l4.48338-4.867229c.407008-.441854.407008-1.158247 0-1.60046l-.73712-.80023c-.407008-.441854-1.066904-.441854-1.474243 0L7 5.198617 2.51662.33139c-.407008-.441853-1.066904-.441853-1.474243 0l-.737121.80023c-.407008.441854-.407008 1.158248 0 1.600461l4.48338 4.867228L7 10l2.211364-2.40069z"}})])}),[],!1,null,null,null).exports},f={inserted:function(t,e,n){var o=n.context;if(o.appendToBody){var i=o.$refs.toggle.getBoundingClientRect(),s=i.height,r=i.top,a=i.left,l=i.width,c=window.scrollX||window.pageXOffset,u=window.scrollY||window.pageYOffset;t.unbindPosition=o.calculatePosition(t,o,{width:l+"px",left:c+a+"px",top:u+r+s+"px"}),document.body.appendChild(t);}},unbind:function(t,e,n){n.context.appendToBody&&(t.unbindPosition&&"function"==typeof t.unbindPosition&&t.unbindPosition(),t.parentNode&&t.parentNode.removeChild(t));}};var y=function(t){var e={};return Object.keys(t).sort().forEach((function(n){e[n]=t[n];})),JSON.stringify(e)},b=0;var g=function(){return ++b};function v(t,e){var n=Object.keys(t);if(Object.getOwnPropertySymbols){var o=Object.getOwnPropertySymbols(t);e&&(o=o.filter((function(e){return Object.getOwnPropertyDescriptor(t,e).enumerable}))),n.push.apply(n,o);}return n}function m(t){for(var e=1;e<arguments.length;e++){var n=null!=arguments[e]?arguments[e]:{};e%2?v(Object(n),!0).forEach((function(e){l()(t,e,n[e]);})):Object.getOwnPropertyDescriptors?Object.defineProperties(t,Object.getOwnPropertyDescriptors(n)):v(Object(n)).forEach((function(e){Object.defineProperty(t,e,Object.getOwnPropertyDescriptor(n,e));}));}return t}var _={components:m({},d),directives:{appendToBody:f},mixins:[c,u,p],props:{value:{},components:{type:Object,default:function(){return {}}},options:{type:Array,default:function(){return []}},disabled:{type:Boolean,default:!1},clearable:{type:Boolean,default:!0},searchable:{type:Boolean,default:!0},multiple:{type:Boolean,default:!1},placeholder:{type:String,default:""},transition:{type:String,default:"vs__fade"},clearSearchOnSelect:{type:Boolean,default:!0},closeOnSelect:{type:Boolean,default:!0},label:{type:String,default:"label"},autocomplete:{type:String,default:"off"},reduce:{type:Function,default:function(t){return t}},selectable:{type:Function,default:function(t){return !0}},getOptionLabel:{type:Function,default:function(t){return "object"===r()(t)?t.hasOwnProperty(this.label)?t[this.label]:console.warn('[vue-select warn]: Label key "option.'.concat(this.label,'" does not')+" exist in options object ".concat(JSON.stringify(t),".\n")+"https://vue-select.org/api/props.html#getoptionlabel"):t}},getOptionKey:{type:Function,default:function(t){if("object"!==r()(t))return t;try{return t.hasOwnProperty("id")?t.id:y(t)}catch(e){return console.warn("[vue-select warn]: Could not stringify this option to generate unique key. Please provide'getOptionKey' prop to return a unique key for each option.\nhttps://vue-select.org/api/props.html#getoptionkey",t,e)}}},onTab:{type:Function,default:function(){this.selectOnTab&&!this.isComposing&&this.typeAheadSelect();}},taggable:{type:Boolean,default:!1},tabindex:{type:Number,default:null},pushTags:{type:Boolean,default:!1},filterable:{type:Boolean,default:!0},filterBy:{type:Function,default:function(t,e,n){return (e||"").toLowerCase().indexOf(n.toLowerCase())>-1}},filter:{type:Function,default:function(t,e){var n=this;return t.filter((function(t){var o=n.getOptionLabel(t);return "number"==typeof o&&(o=o.toString()),n.filterBy(t,o,e)}))}},createOption:{type:Function,default:function(t){return "object"===r()(this.optionList[0])?l()({},this.label,t):t}},resetOnOptionsChange:{default:!1,validator:function(t){return ["function","boolean"].includes(r()(t))}},clearSearchOnBlur:{type:Function,default:function(t){var e=t.clearSearchOnSelect,n=t.multiple;return e&&!n}},noDrop:{type:Boolean,default:!1},inputId:{type:String},dir:{type:String,default:"auto"},selectOnTab:{type:Boolean,default:!1},selectOnKeyCodes:{type:Array,default:function(){return [13]}},searchInputQuerySelector:{type:String,default:"[type=search]"},mapKeydown:{type:Function,default:function(t,e){return t}},appendToBody:{type:Boolean,default:!1},calculatePosition:{type:Function,default:function(t,e,n){var o=n.width,i=n.top,s=n.left;t.style.top=i,t.style.left=s,t.style.width=o;}},dropdownShouldOpen:{type:Function,default:function(t){var e=t.noDrop,n=t.open,o=t.mutableLoading;return !e&&(n&&!o)}}},data:function(){return {uid:g(),search:"",open:!1,isComposing:!1,pushedTags:[],_value:[]}},computed:{isTrackingValues:function(){return void 0===this.value||this.$options.propsData.hasOwnProperty("reduce")},selectedValue:function(){var t=this.value;return this.isTrackingValues&&(t=this.$data._value),t?[].concat(t):[]},optionList:function(){return this.options.concat(this.pushTags?this.pushedTags:[])},searchEl:function(){return this.$scopedSlots.search?this.$refs.selectedOptions.querySelector(this.searchInputQuerySelector):this.$refs.search},scope:function(){var t=this,e={search:this.search,loading:this.loading,searching:this.searching,filteredOptions:this.filteredOptions};return {search:{attributes:m({disabled:this.disabled,placeholder:this.searchPlaceholder,tabindex:this.tabindex,readonly:!this.searchable,id:this.inputId,"aria-autocomplete":"list","aria-labelledby":"vs".concat(this.uid,"__combobox"),"aria-controls":"vs".concat(this.uid,"__listbox"),ref:"search",type:"search",autocomplete:this.autocomplete,value:this.search},this.dropdownOpen&&this.filteredOptions[this.typeAheadPointer]?{"aria-activedescendant":"vs".concat(this.uid,"__option-").concat(this.typeAheadPointer)}:{}),events:{compositionstart:function(){return t.isComposing=!0},compositionend:function(){return t.isComposing=!1},keydown:this.onSearchKeyDown,blur:this.onSearchBlur,focus:this.onSearchFocus,input:function(e){return t.search=e.target.value}}},spinner:{loading:this.mutableLoading},noOptions:{search:this.search,loading:this.mutableLoading,searching:this.searching},openIndicator:{attributes:{ref:"openIndicator",role:"presentation",class:"vs__open-indicator"}},listHeader:e,listFooter:e,header:m({},e,{deselect:this.deselect}),footer:m({},e,{deselect:this.deselect})}},childComponents:function(){return m({},d,{},this.components)},stateClasses:function(){return {"vs--open":this.dropdownOpen,"vs--single":!this.multiple,"vs--multiple":this.multiple,"vs--searching":this.searching&&!this.noDrop,"vs--searchable":this.searchable&&!this.noDrop,"vs--unsearchable":!this.searchable,"vs--loading":this.mutableLoading,"vs--disabled":this.disabled}},searching:function(){return !!this.search},dropdownOpen:function(){return this.dropdownShouldOpen(this)},searchPlaceholder:function(){return this.isValueEmpty&&this.placeholder?this.placeholder:void 0},filteredOptions:function(){var t=[].concat(this.optionList);if(!this.filterable&&!this.taggable)return t;var e=this.search.length?this.filter(t,this.search,this):t;if(this.taggable&&this.search.length){var n=this.createOption(this.search);this.optionExists(n)||e.unshift(n);}return e},isValueEmpty:function(){return 0===this.selectedValue.length},showClearButton:function(){return !this.multiple&&this.clearable&&!this.open&&!this.isValueEmpty}},watch:{options:function(t,e){var n=this;!this.taggable&&("function"==typeof n.resetOnOptionsChange?n.resetOnOptionsChange(t,e,n.selectedValue):n.resetOnOptionsChange)&&this.clearSelection(),this.value&&this.isTrackingValues&&this.setInternalValueFromOptions(this.value);},value:function(t){this.isTrackingValues&&this.setInternalValueFromOptions(t);},multiple:function(){this.clearSelection();},open:function(t){this.$emit(t?"open":"close");}},created:function(){this.mutableLoading=this.loading,void 0!==this.value&&this.isTrackingValues&&this.setInternalValueFromOptions(this.value),this.$on("option:created",this.pushTag);},methods:{setInternalValueFromOptions:function(t){var e=this;Array.isArray(t)?this.$data._value=t.map((function(t){return e.findOptionFromReducedValue(t)})):this.$data._value=this.findOptionFromReducedValue(t);},select:function(t){this.$emit("option:selecting",t),this.isOptionSelected(t)||(this.taggable&&!this.optionExists(t)&&this.$emit("option:created",t),this.multiple&&(t=this.selectedValue.concat(t)),this.updateValue(t),this.$emit("option:selected",t)),this.onAfterSelect(t);},deselect:function(t){var e=this;this.$emit("option:deselecting",t),this.updateValue(this.selectedValue.filter((function(n){return !e.optionComparator(n,t)}))),this.$emit("option:deselected",t);},clearSelection:function(){this.updateValue(this.multiple?[]:null);},onAfterSelect:function(t){this.closeOnSelect&&(this.open=!this.open,this.searchEl.blur()),this.clearSearchOnSelect&&(this.search="");},updateValue:function(t){var e=this;void 0===this.value&&(this.$data._value=t),null!==t&&(t=Array.isArray(t)?t.map((function(t){return e.reduce(t)})):this.reduce(t)),this.$emit("input",t);},toggleDropdown:function(t){var e=t.target!==this.searchEl;e&&t.preventDefault();var n=[].concat(i()(this.$refs.deselectButtons||[]),i()([this.$refs.clearButton]||!1));void 0===this.searchEl||n.filter(Boolean).some((function(e){return e.contains(t.target)||e===t.target}))?t.preventDefault():this.open&&e?this.searchEl.blur():this.disabled||(this.open=!0,this.searchEl.focus());},isOptionSelected:function(t){var e=this;return this.selectedValue.some((function(n){return e.optionComparator(n,t)}))},optionComparator:function(t,e){return this.getOptionKey(t)===this.getOptionKey(e)},findOptionFromReducedValue:function(t){var e=this,n=[].concat(i()(this.options),i()(this.pushedTags)).filter((function(n){return JSON.stringify(e.reduce(n))===JSON.stringify(t)}));return 1===n.length?n[0]:n.find((function(t){return e.optionComparator(t,e.$data._value)}))||t},closeSearchOptions:function(){this.open=!1,this.$emit("search:blur");},maybeDeleteValue:function(){if(!this.searchEl.value.length&&this.selectedValue&&this.selectedValue.length&&this.clearable){var t=null;this.multiple&&(t=i()(this.selectedValue.slice(0,this.selectedValue.length-1))),this.updateValue(t);}},optionExists:function(t){var e=this;return this.optionList.some((function(n){return e.optionComparator(n,t)}))},normalizeOptionForSlot:function(t){return "object"===r()(t)?t:l()({},this.label,t)},pushTag:function(t){this.pushedTags.push(t);},onEscape:function(){this.search.length?this.search="":this.searchEl.blur();},onSearchBlur:function(){if(!this.mousedown||this.searching){var t=this.clearSearchOnSelect,e=this.multiple;return this.clearSearchOnBlur({clearSearchOnSelect:t,multiple:e})&&(this.search=""),void this.closeSearchOptions()}this.mousedown=!1,0!==this.search.length||0!==this.options.length||this.closeSearchOptions();},onSearchFocus:function(){this.open=!0,this.$emit("search:focus");},onMousedown:function(){this.mousedown=!0;},onMouseUp:function(){this.mousedown=!1;},onSearchKeyDown:function(t){var e=this,n=function(t){return t.preventDefault(),!e.isComposing&&e.typeAheadSelect()},o={8:function(t){return e.maybeDeleteValue()},9:function(t){return e.onTab()},27:function(t){return e.onEscape()},38:function(t){return t.preventDefault(),e.typeAheadUp()},40:function(t){return t.preventDefault(),e.typeAheadDown()}};this.selectOnKeyCodes.forEach((function(t){return o[t]=n}));var i=this.mapKeydown(o,this);if("function"==typeof i[t.keyCode])return i[t.keyCode](t)}}},O=(n(7),h(_,(function(){var t=this,e=t.$createElement,n=t._self._c||e;return n("div",{staticClass:"v-select",class:t.stateClasses,attrs:{dir:t.dir}},[t._t("header",null,null,t.scope.header),t._v(" "),n("div",{ref:"toggle",staticClass:"vs__dropdown-toggle",attrs:{id:"vs"+t.uid+"__combobox",role:"combobox","aria-expanded":t.dropdownOpen.toString(),"aria-owns":"vs"+t.uid+"__listbox","aria-label":"Search for option"},on:{mousedown:function(e){return t.toggleDropdown(e)}}},[n("div",{ref:"selectedOptions",staticClass:"vs__selected-options"},[t._l(t.selectedValue,(function(e){return t._t("selected-option-container",[n("span",{key:t.getOptionKey(e),staticClass:"vs__selected"},[t._t("selected-option",[t._v("\n            "+t._s(t.getOptionLabel(e))+"\n          ")],null,t.normalizeOptionForSlot(e)),t._v(" "),t.multiple?n("button",{ref:"deselectButtons",refInFor:!0,staticClass:"vs__deselect",attrs:{disabled:t.disabled,type:"button",title:"Deselect "+t.getOptionLabel(e),"aria-label":"Deselect "+t.getOptionLabel(e)},on:{click:function(n){return t.deselect(e)}}},[n(t.childComponents.Deselect,{tag:"component"})],1):t._e()],2)],{option:t.normalizeOptionForSlot(e),deselect:t.deselect,multiple:t.multiple,disabled:t.disabled})})),t._v(" "),t._t("search",[n("input",t._g(t._b({staticClass:"vs__search"},"input",t.scope.search.attributes,!1),t.scope.search.events))],null,t.scope.search)],2),t._v(" "),n("div",{ref:"actions",staticClass:"vs__actions"},[n("button",{directives:[{name:"show",rawName:"v-show",value:t.showClearButton,expression:"showClearButton"}],ref:"clearButton",staticClass:"vs__clear",attrs:{disabled:t.disabled,type:"button",title:"Clear Selected","aria-label":"Clear Selected"},on:{click:t.clearSelection}},[n(t.childComponents.Deselect,{tag:"component"})],1),t._v(" "),t._t("open-indicator",[t.noDrop?t._e():n(t.childComponents.OpenIndicator,t._b({tag:"component"},"component",t.scope.openIndicator.attributes,!1))],null,t.scope.openIndicator),t._v(" "),t._t("spinner",[n("div",{directives:[{name:"show",rawName:"v-show",value:t.mutableLoading,expression:"mutableLoading"}],staticClass:"vs__spinner"},[t._v("Loading...")])],null,t.scope.spinner)],2)]),t._v(" "),n("transition",{attrs:{name:t.transition}},[t.dropdownOpen?n("ul",{directives:[{name:"append-to-body",rawName:"v-append-to-body"}],key:"vs"+t.uid+"__listbox",ref:"dropdownMenu",staticClass:"vs__dropdown-menu",attrs:{id:"vs"+t.uid+"__listbox",role:"listbox",tabindex:"-1"},on:{mousedown:function(e){return e.preventDefault(),t.onMousedown(e)},mouseup:t.onMouseUp}},[t._t("list-header",null,null,t.scope.listHeader),t._v(" "),t._l(t.filteredOptions,(function(e,o){return n("li",{key:t.getOptionKey(e),staticClass:"vs__dropdown-option",class:{"vs__dropdown-option--selected":t.isOptionSelected(e),"vs__dropdown-option--highlight":o===t.typeAheadPointer,"vs__dropdown-option--disabled":!t.selectable(e)},attrs:{id:"vs"+t.uid+"__option-"+o,role:"option","aria-selected":o===t.typeAheadPointer||null},on:{mouseover:function(n){t.selectable(e)&&(t.typeAheadPointer=o);},click:function(n){n.preventDefault(),n.stopPropagation(),t.selectable(e)&&t.select(e);}}},[t._t("option",[t._v("\n          "+t._s(t.getOptionLabel(e))+"\n        ")],null,t.normalizeOptionForSlot(e))],2)})),t._v(" "),0===t.filteredOptions.length?n("li",{staticClass:"vs__no-options"},[t._t("no-options",[t._v("Sorry, no matching options.")],null,t.scope.noOptions)],2):t._e(),t._v(" "),t._t("list-footer",null,null,t.scope.listFooter)],2):n("ul",{staticStyle:{display:"none",visibility:"hidden"},attrs:{id:"vs"+t.uid+"__listbox",role:"listbox"}})]),t._v(" "),t._t("footer",null,null,t.scope.footer)],2)}),[],!1,null,null,null).exports),w={ajax:p,pointer:u,pointerScroll:c};n.d(e,"VueSelect",(function(){return O})),n.d(e,"mixins",(function(){return w}));e.default=O;}])}));

});

var vueSelect$1 = unwrapExports(vueSelect);
var vueSelect_1 = vueSelect.VueSelect;

//

var script$m = {
  name: 'MeSelect',
  components: {
    vueSelect: vueSelect$1,
    Spinner: __vue_component__$5,
    Icon: __vue_component__,
    Modal: __vue_component__$f
  },
  props: {
    value: {
      type: [Array, Object, Number, String],
      default: null
    },
    autoSelect: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default() {
        return []
      }
    },
    headerOptions: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: 'name'
    },
    max: {
      type: Number,
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    showMore: {
      type: Boolean,
      default: false
    },
    showMoreTitle: {
      type: String,
      default: ''
    },
    showMoreSubtitle: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    highlightSearch: {
      type: Boolean,
      default: null
    },
    state: {
      type: Boolean,
      default: null
    },
    size: {
      type: String,
      default: 'md'
    },
    provider: {
      type: Function,
      default: null
    },
    context: {
      type: Object,
      default: null
    },
    autoFlip: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    components: {
      type: Object,
      default: null
    },
    selectable: {
      type: Function,
      default: () => true
    },
    resetValue: {
      type: Boolean,
      default: false
    },
    showOptionAll: {
      type: Boolean,
      default: false
    },
    removeSelectedOption: {
      type: Boolean,
      default: false
    },
    filterBy: {
      type: Function,
      default: null
    },
    appendToBody: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      busy: false,
      currentOptions: [],
      Deselect: {
        render: createElement => createElement('span', { class: 'me-icon-l icon-xmark' })
      },
      direction: '',
      feedback: '',
      reload: true,
      searchedTerm: '',
      selected: this.value || null,
      showMoreVisible: false,
      selectWidthAvailable: 0,
      hiddenSelectedOptionsQnt: 0,
      canOpenDropdown: true,
      showMoreBtnSpace: 130
    }
  },
  computed: {
    needCustomOptionSlot() {
      return this.highlightSearch || this.shouldShowOptionAll
    },

    clazz() {
      return `me-select ${this.size || 'md'} ${this.stateClazz} ${this.direction} ${this.multiple && this.showMore ? 'show-more-option' : ''}`
    },

    stateClazz() {
      if (this.state === null) {
        return ''
      }
      return this.state ? 'is-valid' : 'is-invalid'
    },

    emptyFeedback() {
      return this.feedback || this.$toolkit.t('grid.result.empty')
    },

    shouldShowOptionAll() {
      return this.options.length && this.multiple && this.showOptionAll && !this.max
    },

    optionAll() {
      if (Array.isArray(this.selected) && this.selected.length === this.options.length) {
        return {
          [this.label]: this.$toolkit.t('select.removeAll'),
          isRemoveAll: true
        }
      }
      return {
        [this.label]: this.$toolkit.t('select.addAll'),
        isAddAll: true
      }
    },

    isAutoSelectEnabled() {
      return this.autoSelect && this.options.length === 1 && !this.disabled
    }
  },

  watch: {
    value(val) {
      this.selected = val;
    },

    options() {
      this.setCurrentOptions();
    },

    selected: {
      handler() {
        if (this.multiple && this.showMore) {
          this.$nextTick(() => {
            this.setShowMoreOptionsButton();
          });
        }

        this.setCurrentOptions();
      },
      immediate: true
    }
  },

  created() {
    if (this.isAutoSelectEnabled) {
      this.selected = this.getInitialSelectedValue();
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setSelectWidthAvailable);
  },

  beforeMount() {
    this.setCurrentOptions();
  },

  mounted() {
    if (this.isAutoSelectEnabled) this.onChange();

    if (this.multiple && this.showMore) {
      this.setShowMoreOptionsButton();
      if (this.$refs.select?.$el) {
        this.selectWidthAvailable = this.$refs.select.$el.clientWidth - this.showMoreBtnSpace;
      }
      window.addEventListener('resize', this.setSelectWidthAvailable);
    }
  },
  methods: {
    getInitialSelectedValue() {
      if (this.isAutoSelectEnabled && this.multiple) return this.options
      if (this.isAutoSelectEnabled) return this.options[0]
      return this.value
    },

    dropdownShouldOpen({ noDrop, open, mutableLoading }) {
      if (!this.canOpenDropdown) {
        this.canOpenDropdown = true;
        return false
      }
      return noDrop ? false : open && !mutableLoading
    },

    customDeselectOption(option, cb) {
      this.canOpenDropdown = false;
      if (this.disabled) return

      cb(option);
    },

    setSelectWidthAvailable() {
      debounce_1(() => {
        this.selectWidthAvailable = this.$refs.select.$el.clientWidth - this.showMoreBtnSpace;
        this.setShowMoreOptionsButton();
      }, 500)();
    },

    setShowMoreOptionsButton() {
      this.hiddenSelectedOptionsQnt = 0;
      const allSelectedNodeList = this.$refs.select?.$el.querySelectorAll('.vs__selected');
      const selectedContainerEl = this.$refs.select?.$el.querySelector('.vs__selected-options');

      if (!allSelectedNodeList?.length || !selectedContainerEl) {
        this.showMoreVisible = false;
        return
      }

      const allSelected = [...allSelectedNodeList];
      if (!allSelected.length) {
        this.showMoreVisible = false;
        return
      }

      let totalSelectionWidth = 0;
      let isFirstElementEllipsed = false;

      allSelected.forEach((selection, index) => {
        if (index === 0) {
          allSelected[index].style.width = 'auto';
        }

        totalSelectionWidth = totalSelectionWidth + selection.clientWidth;

        const shouldHideOption = this.selectWidthAvailable < totalSelectionWidth;
        if (shouldHideOption && index === 0) {
          isFirstElementEllipsed = true;
          allSelected[index].style.width = `${this.selectWidthAvailable - 20}px`;
        } else if (shouldHideOption) {
          this.hiddenSelectedOptionsQnt = this.hiddenSelectedOptionsQnt + 1;
          allSelected[index].style.visibility = 'hidden';
          allSelected[index].style.position = 'absolute';
        } else {
          allSelected[index].style.visibility = 'visible';
          allSelected[index].style.position = 'relative';
        }
      });

      this.showMoreVisible = this.hiddenSelectedOptionsQnt > 0 || isFirstElementEllipsed;
    },

    showMoreOptionsModal() {
      this.$emit('showMoreOptionsModal');
      if (this.$refs.selectShowMoreModal) {
        this.$refs.selectShowMoreModal.show();
      }
    },

    setCurrentOptions() {
      if (this.provider !== null) return

      let filteredOptions = cloneDeep_1(this.options);
      if (this.removeSelectedOption) {
        filteredOptions = this.getFilteredOptions();
      }

      if (this.shouldShowOptionAll) {
        this.currentOptions = cloneDeep_1([this.optionAll, ...filteredOptions]);
        return
      }

      this.currentOptions = cloneDeep_1(filteredOptions);
    },

    getFilteredOptions() {
      if (Array.isArray(this.selected)) {
        const selectedOptionsLabels = this.selected.map(selectedOption => selectedOption[this.label]);
        return this.options.filter(option => !selectedOptionsLabels.includes(option[this.label]))
      }
      return this.options.filter(option => option[this.label] !== (this.selected && this.selected[this.label]))
    },

    focus() {
      this.search();
    },

    search(term) {
      this.searchedTerm = term;
      if (this.autoFlip) {
        this.changeDropdownDirection();
      }

      if (this.provider !== null && this.reload) {
        this.busy = true;
        this.currentOptions = [];
        this.provider({ term, context: this.context }, ({ result, feedback, hits }) => {
          this.currentOptions = result;
          this.feedback = feedback;
          this.busy = false;
          this.reload = result.length !== hits;
        });
      }
    },

    async changeDropdownDirection() {
      this.direction = '';
      await this.$nextTick();
      const dropdownRect = this.$refs.select.$refs.dropdownMenu?.getBoundingClientRect();

      if (dropdownRect?.y >= document.documentElement.clientHeight - dropdownRect?.height) {
        this.direction = 'dropup';
      }
    },

    onChange() {
      if (this.shouldShowOptionAll) {
        this.handleOptionAll();
      }

      this.$emit('input', this.selected);
      if (this.resetValue) {
        this.selected = '';
      }
    },

    handleOptionAll() {
      const isRemoveAll = this.selected.some(optionSelected => optionSelected.isRemoveAll);
      const isAddAll = this.selected.some(optionSelected => optionSelected.isAddAll);
      if (isRemoveAll) {
        this.selected = [];
      } else if (isAddAll) {
        this.selected = this.options;
      }
    },

    currentFilterBy(option, label, search) {
      if (this.filterBy) return this.filterBy(option, label, search)
      return this.filterByNormalized(option, label, search)
    },

    filterByNormalized(option, label, search) {
      const normalizedLabel = this.normalizeString(label);
      const normalizedSearch = this.normalizeString(search);

      const hasSearchAccents = search !== normalizedSearch;
      const labelToCompare = hasSearchAccents ? label : normalizedLabel;
      return (labelToCompare || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1
    },

    normalizeString(text) {
      return text.normalize('NFD').replace(/[^a-zA-Z\s]/g, '')
    }

  }
};

/* script */
const __vue_script__$m = script$m;

/* template */
var __vue_render__$m = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        _vm.clazz,
        {
          max: _vm.multiple && _vm.max && _vm.selected.length === _vm.max
        }
      ]
    },
    [
      _c("vue-select", {
        ref: "select",
        attrs: {
          filterBy: _vm.currentFilterBy,
          dropdownShouldOpen: _vm.dropdownShouldOpen,
          clearable: _vm.clearable,
          appendToBody: _vm.appendToBody,
          components: _vm.components || { Deselect: _vm.Deselect },
          disabled: _vm.disabled,
          "highlight-search": _vm.highlightSearch,
          label: _vm.label,
          multiple: _vm.multiple,
          options: _vm.currentOptions,
          placeholder: _vm.placeholder || _vm.$toolkit.t("select.placeholder"),
          selectable:
            _vm.multiple && _vm.max
              ? function() {
                  return _vm.selected.length < _vm.max
                }
              : _vm.selectable,
          "data-cy": "meSelect"
        },
        on: {
          close: function($event) {
            return _vm.$emit("close")
          },
          input: _vm.onChange,
          open: function($event) {
            return _vm.$emit("open")
          },
          "search:focus": _vm.focus,
          search: _vm.search
        },
        scopedSlots: _vm._u(
          [
            _vm.busy
              ? {
                  key: "spinner",
                  fn: function() {
                    return [
                      _c("Spinner", {
                        attrs: { small: "", type: "grow", variant: "secondary" }
                      })
                    ]
                  },
                  proxy: true
                }
              : null,
            {
              key: "no-options",
              fn: function() {
                return [
                  _c("div", { staticClass: "my-2 ml-2 text-left" }, [
                    _vm.busy
                      ? _c(
                          "div",
                          [
                            _c("Spinner", {
                              staticClass: "align-middle",
                              attrs: { small: "" }
                            }),
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.$toolkit.t("spinner.loading")) +
                                "\n        "
                            )
                          ],
                          1
                        )
                      : _c(
                          "div",
                          [
                            _c("Icon", {
                              attrs: { icon: "me-icon-l icon-info-circle" }
                            }),
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.emptyFeedback) +
                                "\n        "
                            )
                          ],
                          1
                        )
                  ])
                ]
              },
              proxy: true
            },
            {
              key: "list-header",
              fn: function() {
                return [
                  _vm._t("list-header", [
                    _vm.headerOptions.length
                      ? _c(
                          "li",
                          {
                            staticClass: "me-vs__header-actions",
                            attrs: { "data-cy": "headerOptions" }
                          },
                          _vm._l(_vm.headerOptions, function(headerOption) {
                            return _c(
                              "div",
                              {
                                key: headerOption[_vm.label],
                                staticClass: "me-vs__header-actions__item",
                                attrs: { "data-cy": "headerOptionsItem" },
                                on: {
                                  click: function($event) {
                                    headerOption.click
                                      ? headerOption.click(headerOption)
                                      : null;
                                  }
                                }
                              },
                              [
                                headerOption.icon
                                  ? _c("me-icon", {
                                      attrs: {
                                        icon: headerOption.icon,
                                        variant: "primary",
                                        size: "12px"
                                      }
                                    })
                                  : _vm._e(),
                                _vm._v(" "),
                                _c(
                                  "span",
                                  {
                                    staticClass:
                                      "me-vs__header-actions__item__label"
                                  },
                                  [
                                    _vm._v(
                                      "\n              " +
                                        _vm._s(headerOption[_vm.label]) +
                                        "\n            "
                                    )
                                  ]
                                )
                              ],
                              1
                            )
                          }),
                          0
                        )
                      : _vm._e()
                  ])
                ]
              },
              proxy: true
            },
            _vm.needCustomOptionSlot
              ? {
                  key: "option",
                  fn: function(option) {
                    return [
                      _vm.highlightSearch && _vm.shouldShowOptionAll
                        ? [
                            option.isRemoveAll || option.isAddAll
                              ? [
                                  _c("span", { staticClass: "option-all" }, [
                                    _vm._v(
                                      "\n            " +
                                        _vm._s(_vm.optionAll[_vm.label]) +
                                        "\n          "
                                    )
                                  ])
                                ]
                              : _c("span", {
                                  directives: [
                                    {
                                      name: "me-safe-html",
                                      rawName: "v-me-safe-html",
                                      value: _vm.$options.filters.highlightText(
                                        option.name,
                                        _vm.searchedTerm
                                      ),
                                      expression:
                                        "$options.filters.highlightText(option.name, searchedTerm)"
                                    }
                                  ]
                                })
                          ]
                        : _vm.highlightSearch
                        ? [
                            _c("span", {
                              directives: [
                                {
                                  name: "me-safe-html",
                                  rawName: "v-me-safe-html",
                                  value: _vm.$options.filters.highlightText(
                                    option.name,
                                    _vm.searchedTerm
                                  ),
                                  expression:
                                    "$options.filters.highlightText(option.name, searchedTerm)"
                                }
                              ]
                            })
                          ]
                        : [
                            option.isRemoveAll || option.isAddAll
                              ? [
                                  _c("span", { staticClass: "option-all" }, [
                                    _vm._v(
                                      "\n            " +
                                        _vm._s(_vm.optionAll[_vm.label]) +
                                        "\n          "
                                    )
                                  ])
                                ]
                              : [
                                  _vm._v(
                                    "\n          " +
                                      _vm._s(option[_vm.label]) +
                                      "\n        "
                                  )
                                ]
                          ]
                    ]
                  }
                }
              : null,
            !_vm.multiple
              ? {
                  key: "selected-option-container",
                  fn: function(ref) {
                    var option = ref.option;
                    return [
                      _c(
                        "div",
                        { staticClass: "selected-option" },
                        [
                          _vm._t(
                            "selected-option-container",
                            [
                              _c(
                                "div",
                                { staticClass: "selected-option__truncate" },
                                [
                                  _vm._v(
                                    "\n            " +
                                      _vm._s(option[_vm.label]) +
                                      "\n          "
                                  )
                                ]
                              )
                            ],
                            null,
                            { option: option }
                          )
                        ],
                        2
                      )
                    ]
                  }
                }
              : _vm.multiple && _vm.showMore
              ? {
                  key: "selected-option-container",
                  fn: function(ref) {
                    var option = ref.option;
                    var deselect = ref.deselect;
                    return [
                      _c("span", { staticClass: "vs__selected" }, [
                        _vm._v(
                          "\n        " +
                            _vm._s(option[_vm.label]) +
                            "\n        "
                        ),
                        _c(
                          "button",
                          {
                            staticClass: "vs__deselect",
                            on: {
                              mousedown: function($event) {
                                $event.stopPropagation();
                                _vm.canOpenDropdown = false;
                              },
                              mouseleave: function($event) {
                                _vm.canOpenDropdown = true;
                              },
                              click: function($event) {
                                $event.stopPropagation();
                                return _vm.customDeselectOption(
                                  option,
                                  deselect
                                )
                              }
                            }
                          },
                          [
                            _c("span", {
                              staticClass:
                                "me-icon-l icon-xmark deselect-button--show-more"
                            })
                          ]
                        )
                      ])
                    ]
                  }
                }
              : null,
            {
              key: "open-indicator",
              fn: function(ref) {
                var attributes = ref.attributes;
                return [
                  _vm._t(
                    "open-indicator",
                    [
                      _vm.showMoreVisible
                        ? _c(
                            "span",
                            {
                              staticClass: "show-more-action",
                              on: {
                                click: _vm.showMoreOptionsModal,
                                mousedown: function($event) {
                                  $event.stopPropagation();
                                  _vm.canOpenDropdown = false;
                                },
                                mouseleave: function($event) {
                                  _vm.canOpenDropdown = true;
                                }
                              }
                            },
                            [
                              _vm._v(
                                "\n          " +
                                  _vm._s(_vm.$toolkit.t("select.showMore")) +
                                  " "
                              ),
                              _vm.hiddenSelectedOptionsQnt > 0
                                ? [
                                    _vm._v(
                                      "(" +
                                        _vm._s(_vm.hiddenSelectedOptionsQnt) +
                                        ")"
                                    )
                                  ]
                                : _vm._e()
                            ],
                            2
                          )
                        : _c(
                            "span",
                            _vm._b(
                              { staticClass: "select-caret" },
                              "span",
                              attributes,
                              false
                            ),
                            [
                              _c("i", {
                                staticClass: "me-icon-l icon-chevron-down"
                              })
                            ]
                          )
                    ],
                    null,
                    { attributes: attributes }
                  )
                ]
              }
            },
            _vm._l(_vm.$scopedSlots, function(_, slot) {
              return {
                key: slot,
                fn: function(scope) {
                  return [_vm._t(slot, null, null, scope)]
                }
              }
            })
          ],
          null,
          true
        ),
        model: {
          value: _vm.selected,
          callback: function($$v) {
            _vm.selected = $$v;
          },
          expression: "selected"
        }
      }),
      _vm._v(" "),
      _vm._t(
        "show-more-container",
        [
          _vm.multiple && _vm.showMore
            ? _c(
                "Modal",
                {
                  ref: "selectShowMoreModal",
                  attrs: {
                    title:
                      _vm.showMoreTitle ||
                      _vm.$toolkit.t("select.showMoreModal.title") +
                        " (" +
                        (_vm.selected && _vm.selected.length) +
                        ")",
                    subtitle: _vm.showMoreSubtitle,
                    size: "lg"
                  }
                },
                [
                  _c(
                    "div",
                    { staticClass: "more-options-modal-body" },
                    [
                      _c("vue-select", {
                        attrs: {
                          filterBy: _vm.currentFilterBy,
                          clearable: _vm.clearable,
                          components: _vm.components || {
                            Deselect: _vm.Deselect
                          },
                          disabled: _vm.disabled,
                          "highlight-search": _vm.highlightSearch,
                          label: _vm.label,
                          options: _vm.currentOptions,
                          selectable: _vm.max
                            ? function() {
                                return _vm.selected.length < _vm.max
                              }
                            : _vm.selectable,
                          multiple: ""
                        },
                        on: {
                          close: function($event) {
                            return _vm.$emit("close")
                          },
                          input: _vm.onChange,
                          open: function($event) {
                            return _vm.$emit("open")
                          },
                          "search:focus": _vm.focus,
                          search: _vm.search
                        },
                        scopedSlots: _vm._u(
                          [
                            {
                              key: "open-indicator",
                              fn: function(ref) {
                                var attributes = ref.attributes;
                                return [
                                  _c(
                                    "span",
                                    _vm._b(
                                      { staticClass: "select-caret" },
                                      "span",
                                      attributes,
                                      false
                                    ),
                                    [
                                      _c("i", {
                                        staticClass:
                                          "me-icon-l icon-chevron-down"
                                      })
                                    ]
                                  )
                                ]
                              }
                            }
                          ],
                          null,
                          false,
                          2094951248
                        ),
                        model: {
                          value: _vm.selected,
                          callback: function($$v) {
                            _vm.selected = $$v;
                          },
                          expression: "selected"
                        }
                      })
                    ],
                    1
                  )
                ]
              )
            : _vm._e()
        ],
        null,
        { options: _vm.currentOptions, value: _vm.selected }
      )
    ],
    2
  )
};
var __vue_staticRenderFns__$m = [];
__vue_render__$m._withStripped = true;

  /* style */
  const __vue_inject_styles__$m = function (inject) {
    if (!inject) return
    inject("data-v-8f2e878a_0", { source: ".more-options-modal-body[data-v-8f2e878a]  .vs--multiple .vs__selected-options {\n  flex-wrap: wrap;\n  max-height: 50vh;\n  overflow-y: auto;\n  padding-left: 8px;\n}\n.more-options-modal-body[data-v-8f2e878a]  .vs--multiple .vs__selected {\n  background-color: var(--me-primary-7);\n  border-radius: 50px;\n  border: none;\n  color: var(--me-gray-2);\n  font-size: 12px;\n  padding: 4px 8px;\n  margin-left: 0;\n  margin-right: 4px;\n}\n.more-options-modal-body[data-v-8f2e878a]  .vs--multiple .vs__selected .vs__deselect {\n  color: var(--me-gray-2);\n  margin-left: 12px;\n  margin-right: 4px;\n}\ndiv.me-select[data-v-8f2e878a] {\n  background-color: var(--me-white);\n  color: var(--me-gray-0);\n  border-radius: 8px;\n}\ndiv.me-select[data-v-8f2e878a] ::placeholder {\n  color: var(--me-gray-4);\n}\ndiv.me-select.max[data-v-8f2e878a]  .vs__dropdown-option {\n  background-color: transparent !important;\n  color: var(--me-gray-4) !important;\n  cursor: not-allowed;\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple .vs__selected-options {\n  flex-wrap: nowrap;\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple .vs__selected-options .vs__selected {\n  position: relative;\n  white-space: nowrap;\n  padding-right: 15px;\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple .vs__selected-options .vs__selected .deselect-button--show-more {\n  position: absolute;\n  top: 4px;\n  right: 12px;\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple .vs__selected-options .vs__selected:first-of-type {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  display: inline-block;\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple .vs__selected-options .vs__selected .vs__deselect:focus {\n  outline: none;\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple .vs__actions .show-more-action {\n  white-space: nowrap;\n  cursor: pointer;\n  color: var(--me-primary-2);\n}\ndiv.me-select.show-more-option[data-v-8f2e878a]  .vs--multiple.vs--open .vs__selected-options {\n  flex-wrap: wrap;\n}\ndiv.me-select[data-v-8f2e878a]  .vs--multiple.vs--disabled .vs__deselect {\n  cursor: not-allowed;\n}\ndiv.me-select[data-v-8f2e878a]  .vs--multiple .vs__selected-options {\n  flex-wrap: wrap;\n}\ndiv.me-select[data-v-8f2e878a]  .vs--multiple .vs__selected {\n  background-color: var(--me-primary-7);\n  border-radius: 50px;\n  border: none;\n  color: var(--me-gray-2);\n  font-size: 12px;\n  padding: 4px 8px;\n  margin-left: 0;\n  margin-right: 4px;\n}\ndiv.me-select[data-v-8f2e878a]  .vs--multiple .vs__selected .vs__deselect {\n  color: var(--me-gray-2);\n  margin-left: 12px;\n  margin-right: 4px;\n}\ndiv.me-select.sm[data-v-8f2e878a]  div.vs__dropdown-toggle {\n  font-size: 12px;\n  min-height: 32px;\n}\ndiv.me-select.sm .select-caret[data-v-8f2e878a] {\n  line-height: 14px;\n  padding-left: 8px;\n  margin-top: 2px;\n}\ndiv.me-select.md[data-v-8f2e878a]  div.vs__dropdown-toggle {\n  font-size: 14px;\n  min-height: 40px;\n}\ndiv.me-select.md .select-caret[data-v-8f2e878a] {\n  line-height: 16px;\n  padding-left: 8px;\n  margin-top: 2px;\n}\ndiv.me-select.lg[data-v-8f2e878a]  div.vs__dropdown-toggle {\n  font-size: 16px;\n  min-height: 48px;\n}\ndiv.me-select.lg .select-caret[data-v-8f2e878a] {\n  line-height: 18px;\n  padding-left: 8px;\n  margin-top: 2px;\n}\ndiv.me-select .select-caret[data-v-8f2e878a] {\n  color: var(--me-gray-2);\n  cursor: pointer;\n  padding-right: 8px;\n  margin-top: -1px !important;\n}\ndiv.me-select.is-invalid[data-v-8f2e878a]  div.v-select:not(.vs--open) > div.vs__dropdown-toggle {\n  border-color: var(--me-danger-2);\n}\ndiv.me-select.is-invalid + .invalid-feedback[data-v-8f2e878a] {\n  display: block;\n}\ndiv.me-select.dropup[data-v-8f2e878a]  ul.vs__dropdown-menu {\n  bottom: calc(100%);\n  top: unset;\n  margin-bottom: 2px;\n}\ndiv.me-select .selected-option[data-v-8f2e878a] {\n  height: 100%;\n  margin-top: 4px;\n  padding: 0px;\n  text-align: left;\n  width: 100%;\n}\ndiv.me-select .selected-option__truncate[data-v-8f2e878a] {\n  -webkit-box-orient: vertical;\n  -webkit-line-clamp: 1;\n  display: -webkit-box;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n/*# sourceMappingURL=Select.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Select.vue","Select.vue"],"names":[],"mappings":"AAknBA;EACA,eAAA;EACA,gBAAA;EACA,gBAAA;EACA,iBAAA;ACjnBA;ADonBA;EAnBA,qCAAA;EACA,mBAAA;EACA,YAAA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,cAAA;EACA,iBAAA;AC9lBA;AD6mBA;EACA,uBAAA;EACA,iBAAA;EACA,iBAAA;AC3mBA;ADinBA;EACA,iCAAA;EACA,uBAAA;EACA,kBAAA;AC9mBA;ADgnBA;EACA,uBAAA;AC9mBA;ADknBA;EACA,wCAAA;EACA,kCAAA;EACA,mBAAA;AChnBA;ADsnBA;EACA,iBAAA;ACpnBA;ADsnBA;EACA,kBAAA;EACA,mBAAA;EACA,mBAAA;ACpnBA;ADunBA;EACA,kBAAA;EACA,QAAA;EACA,WAAA;ACrnBA;ADynBA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,qBAAA;ACvnBA;AD2nBA;EACA,aAAA;ACznBA;ADgoBA;EACA,mBAAA;EACA,eAAA;EACA,0BAAA;AC9nBA;ADooBA;EACA,eAAA;ACloBA;AD0oBA;EACA,mBAAA;ACxoBA;AD4oBA;EACA,eAAA;AC1oBA;AD6oBA;EA7GA,qCAAA;EACA,mBAAA;EACA,YAAA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,cAAA;EACA,iBAAA;AC7hBA;ADsoBA;EACA,uBAAA;EACA,iBAAA;EACA,iBAAA;ACpoBA;AD0oBA;EACA,eAAA;EACA,gBAAA;ACxoBA;AD2oBA;EACA,iBAAA;EACA,iBAAA;EACA,eAAA;ACzoBA;AD8oBA;EACA,eAAA;EACA,gBAAA;AC5oBA;AD+oBA;EACA,iBAAA;EACA,iBAAA;EACA,eAAA;AC7oBA;ADkpBA;EACA,eAAA;EACA,gBAAA;AChpBA;ADmpBA;EACA,iBAAA;EACA,iBAAA;EACA,eAAA;ACjpBA;ADqpBA;EACA,uBAAA;EACA,eAAA;EACA,kBAAA;EACA,2BAAA;ACnpBA;ADypBA;EACA,gCAAA;ACvpBA;AD4pBA;EACA,cAAA;AC1pBA;AD+pBA;EACA,kBAAA;EACA,UAAA;EACA,kBAAA;AC7pBA;ADiqBA;EACA,YAAA;EACA,eAAA;EACA,YAAA;EACA,gBAAA;EACA,WAAA;AC/pBA;ADiqBA;EACA,4BAAA;EACA,qBAAA;EACA,oBAAA;EACA,gBAAA;EACA,uBAAA;AC/pBA;;AAEA,qCAAqC","file":"Select.vue","sourcesContent":["<template>\n  <div\n    :class=\"[clazz, {\n      'max': multiple && max && selected.length === max\n    }]\"\n  >\n    <vue-select\n      ref=\"select\"\n      v-model=\"selected\"\n      :filterBy=\"currentFilterBy\"\n      :dropdownShouldOpen=\"dropdownShouldOpen\"\n      :clearable=\"clearable\"\n      :appendToBody=\"appendToBody\"\n      :components=\"components || {Deselect}\"\n      :disabled=\"disabled\"\n      :highlight-search=\"highlightSearch\"\n      :label=\"label\"\n      :multiple=\"multiple\"\n      :options=\"currentOptions\"\n      :placeholder=\"placeholder || $toolkit.t('select.placeholder')\"\n      :selectable=\"multiple && max\n        ? () => selected.length < max : selectable\"\n      @close=\"$emit('close')\"\n      @input=\"onChange\"\n      @open=\"$emit('open')\"\n      @search:focus=\"focus\"\n      @search=\"search\"\n      data-cy=\"meSelect\"\n    >\n      <template\n        v-if=\"busy\"\n        v-slot:spinner\n      >\n        <Spinner small type=\"grow\" variant=\"secondary\" />\n      </template>\n\n      <template v-slot:no-options>\n        <div class=\"my-2 ml-2 text-left\">\n          <div v-if=\"busy\">\n            <Spinner small class=\"align-middle\" />\n            {{ $toolkit.t('spinner.loading') }}\n          </div>\n\n          <div v-else>\n            <Icon icon=\"me-icon-l icon-info-circle\" />\n            {{ emptyFeedback }}\n          </div>\n        </div>\n      </template>\n\n      <template v-slot:list-header>\n        <slot name=\"list-header\">\n          <li\n            v-if=\"headerOptions.length\"\n            class=\"me-vs__header-actions\"\n            data-cy=\"headerOptions\"\n          >\n            <div\n              v-for=\"headerOption in headerOptions\"\n              :key=\"headerOption[label]\"\n              @click=\"headerOption.click ? headerOption.click(headerOption) : null\"\n              class=\"me-vs__header-actions__item\"\n              data-cy=\"headerOptionsItem\"\n            >\n              <me-icon\n                v-if=\"headerOption.icon\"\n                :icon=\"headerOption.icon\"\n                variant=\"primary\"\n                size=\"12px\"\n              />\n\n              <span class=\"me-vs__header-actions__item__label\">\n                {{ headerOption[label] }}\n              </span>\n            </div>\n          </li>\n        </slot>\n      </template>\n\n      <template\n        v-if=\"needCustomOptionSlot\"\n        v-slot:option=\"option\"\n      >\n        <template v-if=\"highlightSearch && shouldShowOptionAll\">\n          <template v-if=\"option.isRemoveAll || option.isAddAll\">\n            <span class=\"option-all\">\n              {{ optionAll[label] }}\n            </span>\n          </template>\n\n          <span\n            v-else\n            v-me-safe-html=\"$options.filters.highlightText(option.name, searchedTerm)\"\n          />\n        </template>\n\n        <template v-else-if=\"highlightSearch\">\n          <span\n            v-me-safe-html=\"$options.filters.highlightText(option.name, searchedTerm)\"\n          />\n        </template>\n\n        <template v-else>\n          <template v-if=\"option.isRemoveAll || option.isAddAll\">\n            <span class=\"option-all\">\n              {{ optionAll[label] }}\n            </span>\n          </template>\n\n          <template v-else>\n            {{ option[label] }}\n          </template>\n        </template>\n      </template>\n\n      <template\n        v-if=\"!multiple\"\n        v-slot:selected-option-container=\"{ option }\"\n      >\n        <div class=\"selected-option\">\n          <slot v-bind=\"{ option }\" name=\"selected-option-container\">\n            <div class=\"selected-option__truncate\">\n              {{ option[label] }}\n            </div>\n          </slot>\n        </div>\n      </template>\n\n      <template\n        v-else-if=\"multiple && showMore\"\n        v-slot:selected-option-container=\"{ option, deselect }\"\n      >\n        <span class=\"vs__selected\">\n          {{ option[label] }}\n          <button\n            @mousedown.stop=\"canOpenDropdown = false\"\n            @mouseleave=\"canOpenDropdown = true\"\n            @click.stop=\"customDeselectOption(option, deselect)\"\n            class=\"vs__deselect\"\n          >\n            <span class=\"me-icon-l icon-xmark deselect-button--show-more\" />\n          </button>\n        </span>\n      </template>\n\n      <template v-slot:open-indicator=\"{ attributes }\">\n        <slot v-bind=\"{ attributes }\" name=\"open-indicator\">\n          <span\n            v-if=\"showMoreVisible\"\n            @click=\"showMoreOptionsModal\"\n            @mousedown.stop=\"canOpenDropdown = false\"\n            @mouseleave=\"canOpenDropdown = true\"\n            class=\"show-more-action\"\n          >\n            {{ $toolkit.t('select.showMore') }} <template v-if=\"hiddenSelectedOptionsQnt > 0\">({{ hiddenSelectedOptionsQnt }})</template>\n          </span>\n          <span v-else v-bind=\"attributes\" class=\"select-caret\"><i class=\"me-icon-l icon-chevron-down\" /></span>\n        </slot>\n      </template>\n\n      <template v-for=\"(_, slot) of $scopedSlots\" v-slot:[slot]=\"scope\">\n        <slot :name=\"slot\" v-bind=\"scope\" />\n      </template>\n    </vue-select>\n\n    <slot v-bind=\"{ options: currentOptions, value: selected }\" name=\"show-more-container\">\n      <Modal\n        ref=\"selectShowMoreModal\"\n        v-if=\"multiple && showMore\"\n        :title=\"showMoreTitle || `${$toolkit.t('select.showMoreModal.title')} (${selected && selected.length})`\"\n        :subtitle=\"showMoreSubtitle\"\n        size=\"lg\"\n      >\n        <div class=\"more-options-modal-body\">\n          <vue-select\n            v-model=\"selected\"\n            :filterBy=\"currentFilterBy\"\n            :clearable=\"clearable\"\n            :components=\"components || {Deselect}\"\n            :disabled=\"disabled\"\n            :highlight-search=\"highlightSearch\"\n            :label=\"label\"\n            :options=\"currentOptions\"\n            :selectable=\"max ? () => selected.length < max : selectable\"\n            @close=\"$emit('close')\"\n            @input=\"onChange\"\n            @open=\"$emit('open')\"\n            @search:focus=\"focus\"\n            @search=\"search\"\n            multiple\n          >\n            <template v-slot:open-indicator=\"{ attributes }\">\n              <span v-bind=\"attributes\" class=\"select-caret\"><i class=\"me-icon-l icon-chevron-down\" /></span>\n            </template>\n          </vue-select>\n        </div>\n      </Modal>\n    </slot>\n  </div>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport cloneDeep from 'lodash/cloneDeep'\nimport vueSelect from 'vue-select'\nimport Spinner from './Spinner.vue'\nimport Modal from './modal/Modal.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeSelect',\n  components: {\n    vueSelect,\n    Spinner,\n    Icon,\n    Modal\n  },\n  props: {\n    value: {\n      type: [Array, Object, Number, String],\n      default: null\n    },\n    autoSelect: {\n      type: Boolean,\n      default: false\n    },\n    options: {\n      type: Array,\n      default() {\n        return []\n      }\n    },\n    headerOptions: {\n      type: Array,\n      default: () => []\n    },\n    label: {\n      type: String,\n      default: 'name'\n    },\n    max: {\n      type: Number,\n      default: null\n    },\n    multiple: {\n      type: Boolean,\n      default: false\n    },\n    showMore: {\n      type: Boolean,\n      default: false\n    },\n    showMoreTitle: {\n      type: String,\n      default: ''\n    },\n    showMoreSubtitle: {\n      type: String,\n      default: ''\n    },\n    clearable: {\n      type: Boolean,\n      default: true\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    highlightSearch: {\n      type: Boolean,\n      default: null\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    provider: {\n      type: Function,\n      default: null\n    },\n    context: {\n      type: Object,\n      default: null\n    },\n    autoFlip: {\n      type: Boolean,\n      default: true\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    components: {\n      type: Object,\n      default: null\n    },\n    selectable: {\n      type: Function,\n      default: () => true\n    },\n    resetValue: {\n      type: Boolean,\n      default: false\n    },\n    showOptionAll: {\n      type: Boolean,\n      default: false\n    },\n    removeSelectedOption: {\n      type: Boolean,\n      default: false\n    },\n    filterBy: {\n      type: Function,\n      default: null\n    },\n    appendToBody: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      busy: false,\n      currentOptions: [],\n      Deselect: {\n        render: createElement => createElement('span', { class: 'me-icon-l icon-xmark' })\n      },\n      direction: '',\n      feedback: '',\n      reload: true,\n      searchedTerm: '',\n      selected: this.value || null,\n      showMoreVisible: false,\n      selectWidthAvailable: 0,\n      hiddenSelectedOptionsQnt: 0,\n      canOpenDropdown: true,\n      showMoreBtnSpace: 130\n    }\n  },\n  computed: {\n    needCustomOptionSlot() {\n      return this.highlightSearch || this.shouldShowOptionAll\n    },\n\n    clazz() {\n      return `me-select ${this.size || 'md'} ${this.stateClazz} ${this.direction} ${this.multiple && this.showMore ? 'show-more-option' : ''}`\n    },\n\n    stateClazz() {\n      if (this.state === null) {\n        return ''\n      }\n      return this.state ? 'is-valid' : 'is-invalid'\n    },\n\n    emptyFeedback() {\n      return this.feedback || this.$toolkit.t('grid.result.empty')\n    },\n\n    shouldShowOptionAll() {\n      return this.options.length && this.multiple && this.showOptionAll && !this.max\n    },\n\n    optionAll() {\n      if (Array.isArray(this.selected) && this.selected.length === this.options.length) {\n        return {\n          [this.label]: this.$toolkit.t('select.removeAll'),\n          isRemoveAll: true\n        }\n      }\n      return {\n        [this.label]: this.$toolkit.t('select.addAll'),\n        isAddAll: true\n      }\n    },\n\n    isAutoSelectEnabled() {\n      return this.autoSelect && this.options.length === 1 && !this.disabled\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.selected = val\n    },\n\n    options() {\n      this.setCurrentOptions()\n    },\n\n    selected: {\n      handler() {\n        if (this.multiple && this.showMore) {\n          this.$nextTick(() => {\n            this.setShowMoreOptionsButton()\n          })\n        }\n\n        this.setCurrentOptions()\n      },\n      immediate: true\n    }\n  },\n\n  created() {\n    if (this.isAutoSelectEnabled) {\n      this.selected = this.getInitialSelectedValue()\n    }\n  },\n\n  beforeDestroy() {\n    window.removeEventListener('resize', this.setSelectWidthAvailable)\n  },\n\n  beforeMount() {\n    this.setCurrentOptions()\n  },\n\n  mounted() {\n    if (this.isAutoSelectEnabled) this.onChange()\n\n    if (this.multiple && this.showMore) {\n      this.setShowMoreOptionsButton()\n      if (this.$refs.select?.$el) {\n        this.selectWidthAvailable = this.$refs.select.$el.clientWidth - this.showMoreBtnSpace\n      }\n      window.addEventListener('resize', this.setSelectWidthAvailable)\n    }\n  },\n  methods: {\n    getInitialSelectedValue() {\n      if (this.isAutoSelectEnabled && this.multiple) return this.options\n      if (this.isAutoSelectEnabled) return this.options[0]\n      return this.value\n    },\n\n    dropdownShouldOpen({ noDrop, open, mutableLoading }) {\n      if (!this.canOpenDropdown) {\n        this.canOpenDropdown = true\n        return false\n      }\n      return noDrop ? false : open && !mutableLoading\n    },\n\n    customDeselectOption(option, cb) {\n      this.canOpenDropdown = false\n      if (this.disabled) return\n\n      cb(option)\n    },\n\n    setSelectWidthAvailable() {\n      debounce(() => {\n        this.selectWidthAvailable = this.$refs.select.$el.clientWidth - this.showMoreBtnSpace\n        this.setShowMoreOptionsButton()\n      }, 500)()\n    },\n\n    setShowMoreOptionsButton() {\n      this.hiddenSelectedOptionsQnt = 0\n      const allSelectedNodeList = this.$refs.select?.$el.querySelectorAll('.vs__selected')\n      const selectedContainerEl = this.$refs.select?.$el.querySelector('.vs__selected-options')\n\n      if (!allSelectedNodeList?.length || !selectedContainerEl) {\n        this.showMoreVisible = false\n        return\n      }\n\n      const allSelected = [...allSelectedNodeList]\n      if (!allSelected.length) {\n        this.showMoreVisible = false\n        return\n      }\n\n      let totalSelectionWidth = 0\n      let isFirstElementEllipsed = false\n\n      allSelected.forEach((selection, index) => {\n        if (index === 0) {\n          allSelected[index].style.width = 'auto'\n        }\n\n        totalSelectionWidth = totalSelectionWidth + selection.clientWidth\n\n        const shouldHideOption = this.selectWidthAvailable < totalSelectionWidth\n        if (shouldHideOption && index === 0) {\n          isFirstElementEllipsed = true\n          allSelected[index].style.width = `${this.selectWidthAvailable - 20}px`\n        } else if (shouldHideOption) {\n          this.hiddenSelectedOptionsQnt = this.hiddenSelectedOptionsQnt + 1\n          allSelected[index].style.visibility = 'hidden'\n          allSelected[index].style.position = 'absolute'\n        } else {\n          allSelected[index].style.visibility = 'visible'\n          allSelected[index].style.position = 'relative'\n        }\n      })\n\n      this.showMoreVisible = this.hiddenSelectedOptionsQnt > 0 || isFirstElementEllipsed\n    },\n\n    showMoreOptionsModal() {\n      this.$emit('showMoreOptionsModal')\n      if (this.$refs.selectShowMoreModal) {\n        this.$refs.selectShowMoreModal.show()\n      }\n    },\n\n    setCurrentOptions() {\n      if (this.provider !== null) return\n\n      let filteredOptions = cloneDeep(this.options)\n      if (this.removeSelectedOption) {\n        filteredOptions = this.getFilteredOptions()\n      }\n\n      if (this.shouldShowOptionAll) {\n        this.currentOptions = cloneDeep([this.optionAll, ...filteredOptions])\n        return\n      }\n\n      this.currentOptions = cloneDeep(filteredOptions)\n    },\n\n    getFilteredOptions() {\n      if (Array.isArray(this.selected)) {\n        const selectedOptionsLabels = this.selected.map(selectedOption => selectedOption[this.label])\n        return this.options.filter(option => !selectedOptionsLabels.includes(option[this.label]))\n      }\n      return this.options.filter(option => option[this.label] !== (this.selected && this.selected[this.label]))\n    },\n\n    focus() {\n      this.search()\n    },\n\n    search(term) {\n      this.searchedTerm = term\n      if (this.autoFlip) {\n        this.changeDropdownDirection()\n      }\n\n      if (this.provider !== null && this.reload) {\n        this.busy = true\n        this.currentOptions = []\n        this.provider({ term, context: this.context }, ({ result, feedback, hits }) => {\n          this.currentOptions = result\n          this.feedback = feedback\n          this.busy = false\n          this.reload = result.length !== hits\n        })\n      }\n    },\n\n    async changeDropdownDirection() {\n      this.direction = ''\n      await this.$nextTick()\n      const dropdownRect = this.$refs.select.$refs.dropdownMenu?.getBoundingClientRect()\n\n      if (dropdownRect?.y >= document.documentElement.clientHeight - dropdownRect?.height) {\n        this.direction = 'dropup'\n      }\n    },\n\n    onChange() {\n      if (this.shouldShowOptionAll) {\n        this.handleOptionAll()\n      }\n\n      this.$emit('input', this.selected)\n      if (this.resetValue) {\n        this.selected = ''\n      }\n    },\n\n    handleOptionAll() {\n      const isRemoveAll = this.selected.some(optionSelected => optionSelected.isRemoveAll)\n      const isAddAll = this.selected.some(optionSelected => optionSelected.isAddAll)\n      if (isRemoveAll) {\n        this.selected = []\n      } else if (isAddAll) {\n        this.selected = this.options\n      }\n    },\n\n    currentFilterBy(option, label, search) {\n      if (this.filterBy) return this.filterBy(option, label, search)\n      return this.filterByNormalized(option, label, search)\n    },\n\n    filterByNormalized(option, label, search) {\n      const normalizedLabel = this.normalizeString(label)\n      const normalizedSearch = this.normalizeString(search)\n\n      const hasSearchAccents = search !== normalizedSearch\n      const labelToCompare = hasSearchAccents ? label : normalizedLabel\n      return (labelToCompare || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1\n    },\n\n    normalizeString(text) {\n      return text.normalize('NFD').replace(/[^a-zA-Z\\s]/g, '')\n    }\n\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @mixin selected-multiple {\n    background-color: var(--me-primary-7);\n    border-radius: 50px;\n    border: none;\n    color: var(--me-gray-2);\n    font-size: 12px;\n    padding: 4px 8px;\n    margin-left: 0;\n    margin-right: 4px;\n  }\n\n  .more-options-modal-body {\n     ::v-deep .vs--multiple {\n      .vs__selected-options {\n        flex-wrap: wrap;\n        max-height: 50vh;\n        overflow-y: auto;\n        padding-left: 8px;\n      }\n\n      .vs__selected {\n         @include selected-multiple;\n\n        .vs__deselect  {\n          color: var(--me-gray-2);\n          margin-left: 12px;\n          margin-right: 4px;\n        }\n      }\n    }\n  }\n\n  div.me-select {\n    background-color: var(--me-white);\n    color: var(--me-gray-0);\n    border-radius: 8px;\n\n    ::placeholder {\n      color: var(--me-gray-4);\n    }\n\n    &.max {\n      ::v-deep .vs__dropdown-option {\n        background-color: transparent !important;\n        color: var(--me-gray-4) !important;\n        cursor: not-allowed;\n      }\n    }\n\n    &.show-more-option {\n      ::v-deep .vs--multiple {\n        .vs__selected-options {\n          flex-wrap: nowrap;\n\n          .vs__selected {\n            position: relative;\n            white-space: nowrap;\n            padding-right: 15px;\n\n            .deselect-button {\n              &--show-more {\n                position: absolute;\n                top: 4px;\n                right: 12px;\n              }\n            }\n\n            &:first-of-type {\n              white-space: nowrap;\n              overflow: hidden;\n              text-overflow: ellipsis;\n              display: inline-block;\n            }\n\n            .vs__deselect {\n              &:focus {\n                outline: none;\n              }\n            }\n          }\n        }\n\n        .vs__actions {\n          .show-more-action {\n            white-space: nowrap;\n            cursor: pointer;\n            color: var(--me-primary-2);\n          }\n        }\n      }\n\n      ::v-deep .vs--multiple.vs--open {\n        .vs__selected-options {\n          flex-wrap: wrap;\n        }\n      }\n    }\n\n    ::v-deep .vs--multiple {\n\n      &.vs--disabled {\n        .vs__deselect  {\n          cursor: not-allowed;\n        }\n      }\n\n      .vs__selected-options {\n        flex-wrap: wrap;\n      }\n\n      .vs__selected {\n        @include selected-multiple;\n\n        .vs__deselect  {\n          color: var(--me-gray-2);\n          margin-left: 12px;\n          margin-right: 4px;\n        }\n      }\n    }\n\n    &.sm {\n      ::v-deep div.vs__dropdown-toggle {\n        font-size: 12px;\n        min-height: 32px;\n      }\n\n      .select-caret {\n        line-height: 14px;\n        padding-left: 8px;\n        margin-top: 2px;\n      }\n    }\n\n    &.md {\n      ::v-deep div.vs__dropdown-toggle {\n        font-size: 14px;\n        min-height: 40px;\n      }\n\n      .select-caret {\n        line-height: 16px;\n        padding-left: 8px;\n        margin-top: 2px;\n      }\n    }\n\n    &.lg {\n      ::v-deep div.vs__dropdown-toggle {\n        font-size: 16px;\n        min-height: 48px;\n      }\n\n      .select-caret {\n        line-height: 18px;\n        padding-left: 8px;\n        margin-top: 2px;\n      }\n    }\n\n    .select-caret {\n      color: var(--me-gray-2);\n      cursor: pointer;\n      padding-right: 8px;\n      margin-top: -1px !important;\n    }\n\n    &.is-invalid {\n      ::v-deep div.v-select {\n        &:not(.vs--open) {\n          > div.vs__dropdown-toggle {\n            border-color: var(--me-danger-2)\n          }\n        }\n      }\n\n      + .invalid-feedback {\n        display: block;\n      }\n    }\n\n    &.dropup {\n      ::v-deep ul.vs__dropdown-menu {\n        bottom: calc(100%);\n        top: unset;\n        margin-bottom: 2px;\n      }\n    }\n\n    .selected-option {\n      height: 100%;\n      margin-top: 4px;\n      padding: 0px;\n      text-align: left;\n      width: 100%;\n\n      &__truncate {\n        -webkit-box-orient: vertical;\n        -webkit-line-clamp: 1;\n        display: -webkit-box;\n        overflow: hidden;\n        text-overflow: ellipsis;\n      }\n    }\n  }\n</style>\n",".more-options-modal-body ::v-deep .vs--multiple .vs__selected-options {\n  flex-wrap: wrap;\n  max-height: 50vh;\n  overflow-y: auto;\n  padding-left: 8px;\n}\n.more-options-modal-body ::v-deep .vs--multiple .vs__selected {\n  background-color: var(--me-primary-7);\n  border-radius: 50px;\n  border: none;\n  color: var(--me-gray-2);\n  font-size: 12px;\n  padding: 4px 8px;\n  margin-left: 0;\n  margin-right: 4px;\n}\n.more-options-modal-body ::v-deep .vs--multiple .vs__selected .vs__deselect {\n  color: var(--me-gray-2);\n  margin-left: 12px;\n  margin-right: 4px;\n}\n\ndiv.me-select {\n  background-color: var(--me-white);\n  color: var(--me-gray-0);\n  border-radius: 8px;\n}\ndiv.me-select ::placeholder {\n  color: var(--me-gray-4);\n}\ndiv.me-select.max ::v-deep .vs__dropdown-option {\n  background-color: transparent !important;\n  color: var(--me-gray-4) !important;\n  cursor: not-allowed;\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple .vs__selected-options {\n  flex-wrap: nowrap;\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple .vs__selected-options .vs__selected {\n  position: relative;\n  white-space: nowrap;\n  padding-right: 15px;\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple .vs__selected-options .vs__selected .deselect-button--show-more {\n  position: absolute;\n  top: 4px;\n  right: 12px;\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple .vs__selected-options .vs__selected:first-of-type {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  display: inline-block;\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple .vs__selected-options .vs__selected .vs__deselect:focus {\n  outline: none;\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple .vs__actions .show-more-action {\n  white-space: nowrap;\n  cursor: pointer;\n  color: var(--me-primary-2);\n}\ndiv.me-select.show-more-option ::v-deep .vs--multiple.vs--open .vs__selected-options {\n  flex-wrap: wrap;\n}\ndiv.me-select ::v-deep .vs--multiple.vs--disabled .vs__deselect {\n  cursor: not-allowed;\n}\ndiv.me-select ::v-deep .vs--multiple .vs__selected-options {\n  flex-wrap: wrap;\n}\ndiv.me-select ::v-deep .vs--multiple .vs__selected {\n  background-color: var(--me-primary-7);\n  border-radius: 50px;\n  border: none;\n  color: var(--me-gray-2);\n  font-size: 12px;\n  padding: 4px 8px;\n  margin-left: 0;\n  margin-right: 4px;\n}\ndiv.me-select ::v-deep .vs--multiple .vs__selected .vs__deselect {\n  color: var(--me-gray-2);\n  margin-left: 12px;\n  margin-right: 4px;\n}\ndiv.me-select.sm ::v-deep div.vs__dropdown-toggle {\n  font-size: 12px;\n  min-height: 32px;\n}\ndiv.me-select.sm .select-caret {\n  line-height: 14px;\n  padding-left: 8px;\n  margin-top: 2px;\n}\ndiv.me-select.md ::v-deep div.vs__dropdown-toggle {\n  font-size: 14px;\n  min-height: 40px;\n}\ndiv.me-select.md .select-caret {\n  line-height: 16px;\n  padding-left: 8px;\n  margin-top: 2px;\n}\ndiv.me-select.lg ::v-deep div.vs__dropdown-toggle {\n  font-size: 16px;\n  min-height: 48px;\n}\ndiv.me-select.lg .select-caret {\n  line-height: 18px;\n  padding-left: 8px;\n  margin-top: 2px;\n}\ndiv.me-select .select-caret {\n  color: var(--me-gray-2);\n  cursor: pointer;\n  padding-right: 8px;\n  margin-top: -1px !important;\n}\ndiv.me-select.is-invalid ::v-deep div.v-select:not(.vs--open) > div.vs__dropdown-toggle {\n  border-color: var(--me-danger-2);\n}\ndiv.me-select.is-invalid + .invalid-feedback {\n  display: block;\n}\ndiv.me-select.dropup ::v-deep ul.vs__dropdown-menu {\n  bottom: calc(100%);\n  top: unset;\n  margin-bottom: 2px;\n}\ndiv.me-select .selected-option {\n  height: 100%;\n  margin-top: 4px;\n  padding: 0px;\n  text-align: left;\n  width: 100%;\n}\ndiv.me-select .selected-option__truncate {\n  -webkit-box-orient: vertical;\n  -webkit-line-clamp: 1;\n  display: -webkit-box;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n/*# sourceMappingURL=Select.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$m = "data-v-8f2e878a";
  /* module identifier */
  const __vue_module_identifier__$m = undefined;
  /* functional template */
  const __vue_is_functional_template__$m = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$m, staticRenderFns: __vue_staticRenderFns__$m },
    __vue_inject_styles__$m,
    __vue_script__$m,
    __vue_scope_id__$m,
    __vue_is_functional_template__$m,
    __vue_module_identifier__$m,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$n = {
  name: 'MeCollapse',
  components: {
    Button: __vue_component__$6
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    opened: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'primary-hover'
    },
    optionalButton: {
      type: Boolean,
      default: false
    },
    showTooltip: {
      type: Boolean,
      default: true
    },
    bordered: {
      type: Boolean,
      default: false
    },
    titleVariant: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isOpen: this.opened
    }
  },

  computed: {
    toggleBtnProps() {
      return {
        variant: this.variant,
        icon: this.toggleIcon,
        size: 'sm',
        pill: true
      }
    },

    toggleIcon() {
      if (this.variant !== 'secondary') {
        return 'me-icon-l icon-chevron-down'
      }

      if (this.isOpen) {
        return 'me-icon-l icon-minus'
      }

      return 'me-icon-l icon-plus'
    },

    renderCollapseTooltip() {
      if (this.showTooltip) {
        return this.isOpen ? this.$toolkit.t('collapse.hide') : this.$toolkit.t('collapse.expand')
      }

      return ''
    }
  },

  watch: {
    opened(value) {
      this.isOpen = value;
    }
  },

  methods: {
    toggleCollase() {
      this.isOpen = !this.isOpen;

      this.$emit('update:opened', this.isOpen);
      this.$emit('click', this.isOpen);
    }
  }
};

/* script */
const __vue_script__$n = script$n;

/* template */
var __vue_render__$n = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    {
      staticClass: "me-collapse",
      class: { opened: _vm.isOpen, bordered: _vm.bordered }
    },
    [
      _vm.$slots.header
        ? _c(
            "section",
            {
              staticClass: "collapse-header collapse-header--custom",
              on: { click: _vm.toggleCollase }
            },
            [
              _c(
                "Button",
                _vm._b(
                  {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip.hover",
                        value: _vm.renderCollapseTooltip,
                        expression: "renderCollapseTooltip",
                        modifiers: { hover: true }
                      }
                    ]
                  },
                  "Button",
                  _vm.toggleBtnProps,
                  false
                )
              ),
              _vm._v(" "),
              _vm._t("header")
            ],
            2
          )
        : _c(
            "h5",
            {
              class: [{ iconbutton: _vm.optionalButton }, _vm.titleVariant],
              on: { click: _vm.toggleCollase }
            },
            [
              _c(
                "Button",
                _vm._b(
                  {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip.hover",
                        value: _vm.renderCollapseTooltip,
                        expression: "renderCollapseTooltip",
                        modifiers: { hover: true }
                      }
                    ]
                  },
                  "Button",
                  _vm.toggleBtnProps,
                  false
                )
              ),
              _vm._v("\n\n    " + _vm._s(_vm.title) + "\n    "),
              _vm._t("collapse-info-title")
            ],
            2
          ),
      _vm._v(" "),
      _vm.optionalButton ? _c("div", [_vm._t("optionalButton")], 2) : _vm._e(),
      _vm._v(" "),
      _c("transition", { attrs: { name: "content-transition" } }, [
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isOpen,
                expression: "isOpen"
              }
            ],
            staticClass: "collapse-body"
          },
          [_vm._t("default")],
          2
        )
      ])
    ],
    1
  )
};
var __vue_staticRenderFns__$n = [];
__vue_render__$n._withStripped = true;

  /* style */
  const __vue_inject_styles__$n = function (inject) {
    if (!inject) return
    inject("data-v-7d7a674e_0", { source: "section.me-collapse.bordered[data-v-7d7a674e] {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\nsection.me-collapse.bordered h5[data-v-7d7a674e],\nsection.me-collapse.bordered .collapse-header[data-v-7d7a674e] {\n  padding-left: 10px;\n}\nsection.me-collapse.bordered div.collapse-body[data-v-7d7a674e] {\n  border-top: 1px solid var(--me-gray-6);\n  padding: 10px;\n}\nsection.me-collapse > h5[data-v-7d7a674e],\nsection.me-collapse .collapse-header[data-v-7d7a674e] {\n  align-items: center;\n  color: var(--me-gray-2);\n  cursor: pointer;\n  display: flex;\n  font-size: 16px;\n  font-weight: 600;\n  margin: 15px 0;\n}\nsection.me-collapse > h5--custom[data-v-7d7a674e],\nsection.me-collapse .collapse-header--custom[data-v-7d7a674e] {\n  font-size: unset;\n  font-weight: unset;\n}\nsection.me-collapse > h5.iconbutton[data-v-7d7a674e],\nsection.me-collapse .collapse-header.iconbutton[data-v-7d7a674e] {\n  display: inline-block;\n}\nsection.me-collapse > h5.iconbutton + div[data-v-7d7a674e],\nsection.me-collapse .collapse-header.iconbutton + div[data-v-7d7a674e] {\n  display: inline-block;\n}\nsection.me-collapse > h5.iconbutton + div i[data-v-7d7a674e],\nsection.me-collapse .collapse-header.iconbutton + div i[data-v-7d7a674e] {\n  color: var(--me-primary-2);\n  cursor: pointer;\n  font-size: 20px;\n  margin-left: 7px;\n}\nsection.me-collapse > h5 > button[data-v-7d7a674e],\nsection.me-collapse .collapse-header > button[data-v-7d7a674e] {\n  align-items: center;\n  display: flex;\n  float: left;\n  justify-content: center;\n  margin-right: 10px;\n  height: 20px !important;\n  width: 20px !important;\n}\nsection.me-collapse > h5 > button[data-v-7d7a674e]:focus,\nsection.me-collapse .collapse-header > button[data-v-7d7a674e]:focus {\n  box-shadow: none;\n}\nsection.me-collapse > h5 > button[variant=secondary][data-v-7d7a674e],\nsection.me-collapse .collapse-header > button[variant=secondary][data-v-7d7a674e] {\n  background-color: rgba(0, 0, 0, 0.24);\n  border: none;\n  box-shadow: none;\n}\nsection.me-collapse > h5 > button.btn-primary-hover[data-v-7d7a674e],\nsection.me-collapse .collapse-header > button.btn-primary-hover[data-v-7d7a674e] {\n  background-color: var(--me-primary-7);\n  border-color: rgba(10, 140, 204, 0.12);\n  color: var(--me-primary-2);\n}\nsection.me-collapse > h5 > button.btn-primary-hover[data-v-7d7a674e]:hover,\nsection.me-collapse .collapse-header > button.btn-primary-hover[data-v-7d7a674e]:hover {\n  background: var(--me-primary-7);\n  border-color: rgba(10, 140, 204, 0.12);\n}\nsection.me-collapse > h5 > button[variant=square][data-v-7d7a674e],\nsection.me-collapse .collapse-header > button[variant=square][data-v-7d7a674e] {\n  background-color: rgba(0, 0, 0, 0.24);\n  border-radius: 2px !important;\n  color: var(--me-white);\n}\nsection.me-collapse > h5 > button[data-v-7d7a674e]  i,\nsection.me-collapse .collapse-header > button[data-v-7d7a674e]  i {\n  transition-duration: 0.3s;\n  transition-property: transform;\n}\nsection.me-collapse > h5 > button[data-v-7d7a674e]  i.icon-chevron-down,\nsection.me-collapse .collapse-header > button[data-v-7d7a674e]  i.icon-chevron-down {\n  transition-duration: 0.3s;\n  transition-property: transform;\n}\nsection.me-collapse.opened > h5 > button:not(.btn-secondary)[variant=square][data-v-7d7a674e],\nsection.me-collapse.opened .collapse-header > button:not(.btn-secondary)[variant=square][data-v-7d7a674e] {\n  background-color: rgba(12, 139, 203, 0.07);\n  color: var(--me-primary-2);\n}\nsection.me-collapse.opened > h5 > button[data-v-7d7a674e]:not(.btn-secondary)  i.icon-chevron-down,\nsection.me-collapse.opened .collapse-header > button[data-v-7d7a674e]:not(.btn-secondary)  i.icon-chevron-down {\n  transform: rotate(-180deg);\n}\nsection.me-collapse.opened > div[data-v-7d7a674e] {\n  display: block;\n}\n@media print {\nsection.me-collapse .me-collapse > h5 > button[variant=square][data-v-7d7a674e],\nsection.me-collapse .me-collapse .collapse-header > button[variant=square][data-v-7d7a674e] {\n    background-color: rgba(12, 139, 203, 0.07) !important;\n    color: var(--me-primary-2) !important;\n}\nsection.me-collapse .me-collapse > h5 > button > i[data-v-7d7a674e],\nsection.me-collapse .me-collapse .collapse-header > button > i[data-v-7d7a674e] {\n    transform: rotate(180deg) !important;\n    color: var(--me-gray-2);\n}\nsection.me-collapse .me-collapse > div[data-v-7d7a674e] {\n    display: block !important;\n}\n}\n.content-transition-enter-active[data-v-7d7a674e], .content-transition-leave-active[data-v-7d7a674e] {\n  transition: all 0.3s;\n}\n.content-transition-enter[data-v-7d7a674e], .content-transition-leave-to[data-v-7d7a674e] {\n  transform: translateY(-10px);\n  opacity: 0;\n}\n\n/*# sourceMappingURL=Collapse.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Collapse.vue","Collapse.vue"],"names":[],"mappings":"AA2IA;EACA,kCAAA;EACA,kBAAA;AC1IA;AD4IA;;EAEA,kBAAA;AC1IA;AD6IA;EACA,sCAAA;EACA,aAAA;AC3IA;AD8IA;;EAEA,mBAAA;EACA,uBAAA;EACA,eAAA;EACA,aAAA;EACA,eAAA;EACA,gBAAA;EACA,cAAA;AC5IA;AD8IA;;EACA,gBAAA;EACA,kBAAA;AC3IA;AD8IA;;EACA,qBAAA;AC3IA;AD6IA;;EACA,qBAAA;AC1IA;AD4IA;;EACA,0BAAA;EACA,eAAA;EACA,eAAA;EACA,gBAAA;ACzIA;AD8IA;;EACA,mBAAA;EACA,aAAA;EACA,WAAA;EACA,uBAAA;EACA,kBAAA;EACA,uBAAA;EACA,sBAAA;AC3IA;AD6IA;;EACA,gBAAA;AC1IA;AD6IA;;EACA,qCAAA;EACA,YAAA;EACA,gBAAA;AC1IA;AD6IA;;EACA,qCAAA;EACA,sCAAA;EACA,0BAAA;AC1IA;AD4IA;;EACA,+BAAA;EACA,sCAAA;ACzIA;AD6IA;;EACA,qCAAA;EACA,6BAAA;EACA,sBAAA;AC1IA;AD8IA;;EACA,yBAAA;EACA,8BAAA;AC3IA;AD8IA;;EACA,yBAAA;EACA,8BAAA;AC3IA;ADqJA;;EACA,0CAAA;EACA,0BAAA;AClJA;ADsJA;;EACA,0BAAA;ACnJA;ADyJA;EACA,cAAA;ACvJA;AD2JA;AAKA;;IACA,qDAAA;IACA,qCAAA;AC5JE;AD+JF;;IACA,oCAAA;IACA,uBAAA;AC5JE;ADiKF;IACA,yBAAA;AC/JE;AACF;ADoKA;EACA,oBAAA;ACjKA;ADoKA;EACA,4BAAA;EACA,UAAA;ACjKA;;AAEA,uCAAuC","file":"Collapse.vue","sourcesContent":["<template>\n  <section\n    :class=\"{opened: isOpen, bordered }\"\n    class=\"me-collapse\"\n  >\n    <section\n      v-if=\"$slots.header\"\n      @click=\"toggleCollase\"\n      class=\"collapse-header collapse-header--custom\"\n    >\n      <!-- TODO nao usar button já que não respeita os tamanhos e cores padrao - usar o me-icon no lugar -->\n      <Button\n        v-me-tooltip.hover=\"renderCollapseTooltip\"\n        v-bind=\"toggleBtnProps\"\n      />\n      <slot name=\"header\" />\n    </section>\n\n    <h5\n      v-else\n      :class=\"[{iconbutton: optionalButton }, titleVariant]\"\n      @click=\"toggleCollase\"\n    >\n      <Button\n        v-me-tooltip.hover=\"renderCollapseTooltip\"\n        v-bind=\"toggleBtnProps\"\n      />\n\n      {{ title }}\n      <slot name=\"collapse-info-title\" />\n    </h5>\n\n    <div v-if=\"optionalButton\">\n      <slot name=\"optionalButton\" />\n    </div>\n\n    <transition name=\"content-transition\">\n      <div v-show=\"isOpen\" class=\"collapse-body\">\n        <slot />\n      </div>\n    </transition>\n  </section>\n</template>\n\n<script>\nimport Button from './Button.vue'\n\nexport default {\n  name: 'MeCollapse',\n  components: {\n    Button\n  },\n  props: {\n    title: {\n      type: String,\n      default: ''\n    },\n    opened: {\n      type: Boolean,\n      default: false\n    },\n    variant: {\n      type: String,\n      default: 'primary-hover'\n    },\n    optionalButton: {\n      type: Boolean,\n      default: false\n    },\n    showTooltip: {\n      type: Boolean,\n      default: true\n    },\n    bordered: {\n      type: Boolean,\n      default: false\n    },\n    titleVariant: {\n      type: String,\n      default: ''\n    }\n  },\n\n  data() {\n    return {\n      isOpen: this.opened\n    }\n  },\n\n  computed: {\n    toggleBtnProps() {\n      return {\n        variant: this.variant,\n        icon: this.toggleIcon,\n        size: 'sm',\n        pill: true\n      }\n    },\n\n    toggleIcon() {\n      if (this.variant !== 'secondary') {\n        return 'me-icon-l icon-chevron-down'\n      }\n\n      if (this.isOpen) {\n        return 'me-icon-l icon-minus'\n      }\n\n      return 'me-icon-l icon-plus'\n    },\n\n    renderCollapseTooltip() {\n      if (this.showTooltip) {\n        return this.isOpen ? this.$toolkit.t('collapse.hide') : this.$toolkit.t('collapse.expand')\n      }\n\n      return ''\n    }\n  },\n\n  watch: {\n    opened(value) {\n      this.isOpen = value\n    }\n  },\n\n  methods: {\n    toggleCollase() {\n      this.isOpen = !this.isOpen\n\n      this.$emit('update:opened', this.isOpen)\n      this.$emit('click', this.isOpen)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  section.me-collapse {\n    &.bordered {\n      border: 1px solid var(--me-gray-6);\n      border-radius: 8px;\n\n      h5,\n      .collapse-header {\n        padding-left: 10px;\n      }\n\n      div.collapse-body {\n        border-top: 1px solid var(--me-gray-6);\n        padding: 10px;\n      }\n    }\n    > h5,\n      .collapse-header {\n      align-items: center;\n      color: var(--me-gray-2);\n      cursor: pointer;\n      display: flex;\n      font-size: 16px;\n      font-weight: 600;\n      margin: 15px 0;\n\n      &--custom {\n        font-size: unset;\n        font-weight: unset;\n      }\n\n      &.iconbutton {\n        display: inline-block;\n\n        & + div {\n          display: inline-block;\n\n          i {\n            color: var(--me-primary-2);\n            cursor: pointer;\n            font-size: 20px;\n            margin-left: 7px;\n          }\n        }\n      }\n\n      > button {\n        align-items: center;\n        display: flex;\n        float: left;\n        justify-content: center;\n        margin-right: 10px;\n        height: 20px !important;\n        width: 20px !important;\n\n        &:focus {\n          box-shadow: none;\n        }\n\n        &[variant=\"secondary\"] {\n          background-color: rgba(0, 0, 0, 0.24);\n          border: none;\n          box-shadow: none;\n        }\n\n        &.btn-primary-hover {\n          background-color: var(--me-primary-7);\n          border-color: rgba(10, 140, 204, 0.12);\n          color: var(--me-primary-2);\n\n          &:hover {\n            background: var(--me-primary-7);\n            border-color: rgba(10, 140, 204, 0.12);\n          }\n        }\n\n        &[variant=\"square\"] {\n          background-color: rgba(0, 0, 0, 0.24);\n          border-radius: 2px !important;\n          color: var(--me-white);\n        }\n\n        ::v-deep {\n          i {\n            transition-duration: .3s;\n            transition-property: transform;\n          }\n\n          i.icon-chevron-down {\n            transition-duration: .3s;\n            transition-property: transform;\n          }\n        }\n      }\n    }\n\n    &.opened {\n      > h5,\n        .collapse-header {\n        > button:not(.btn-secondary) {\n          &[variant=\"square\"] {\n            background-color: rgba(12, 139, 203, 0.07);\n            color: var(--me-primary-2);\n          }\n\n          ::v-deep {\n            i.icon-chevron-down {\n              transform: rotate(-180deg);\n            }\n          }\n        }\n      }\n\n      > div {\n        display: block;\n      }\n    }\n\n    @media print {\n      .me-collapse {\n        > h5,\n          .collapse-header {\n          > button {\n            &[variant=\"square\"] {\n              background-color: rgba(12, 139, 203, 0.07) !important;\n              color: var(--me-primary-2) !important;\n            }\n\n            > i {\n              transform: rotate(180deg) !important;\n              color: var(--me-gray-2);\n            }\n          }\n        }\n\n        > div {\n          display: block !important;\n        }\n      }\n    }\n  }\n\n  .content-transition-enter-active, .content-transition-leave-active {\n      transition: all .3s;\n  }\n\n  .content-transition-enter, .content-transition-leave-to {\n    transform: translateY(-10px);\n    opacity: 0;\n  }\n</style>\n","section.me-collapse.bordered {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\nsection.me-collapse.bordered h5,\nsection.me-collapse.bordered .collapse-header {\n  padding-left: 10px;\n}\nsection.me-collapse.bordered div.collapse-body {\n  border-top: 1px solid var(--me-gray-6);\n  padding: 10px;\n}\nsection.me-collapse > h5,\nsection.me-collapse .collapse-header {\n  align-items: center;\n  color: var(--me-gray-2);\n  cursor: pointer;\n  display: flex;\n  font-size: 16px;\n  font-weight: 600;\n  margin: 15px 0;\n}\nsection.me-collapse > h5--custom,\nsection.me-collapse .collapse-header--custom {\n  font-size: unset;\n  font-weight: unset;\n}\nsection.me-collapse > h5.iconbutton,\nsection.me-collapse .collapse-header.iconbutton {\n  display: inline-block;\n}\nsection.me-collapse > h5.iconbutton + div,\nsection.me-collapse .collapse-header.iconbutton + div {\n  display: inline-block;\n}\nsection.me-collapse > h5.iconbutton + div i,\nsection.me-collapse .collapse-header.iconbutton + div i {\n  color: var(--me-primary-2);\n  cursor: pointer;\n  font-size: 20px;\n  margin-left: 7px;\n}\nsection.me-collapse > h5 > button,\nsection.me-collapse .collapse-header > button {\n  align-items: center;\n  display: flex;\n  float: left;\n  justify-content: center;\n  margin-right: 10px;\n  height: 20px !important;\n  width: 20px !important;\n}\nsection.me-collapse > h5 > button:focus,\nsection.me-collapse .collapse-header > button:focus {\n  box-shadow: none;\n}\nsection.me-collapse > h5 > button[variant=secondary],\nsection.me-collapse .collapse-header > button[variant=secondary] {\n  background-color: rgba(0, 0, 0, 0.24);\n  border: none;\n  box-shadow: none;\n}\nsection.me-collapse > h5 > button.btn-primary-hover,\nsection.me-collapse .collapse-header > button.btn-primary-hover {\n  background-color: var(--me-primary-7);\n  border-color: rgba(10, 140, 204, 0.12);\n  color: var(--me-primary-2);\n}\nsection.me-collapse > h5 > button.btn-primary-hover:hover,\nsection.me-collapse .collapse-header > button.btn-primary-hover:hover {\n  background: var(--me-primary-7);\n  border-color: rgba(10, 140, 204, 0.12);\n}\nsection.me-collapse > h5 > button[variant=square],\nsection.me-collapse .collapse-header > button[variant=square] {\n  background-color: rgba(0, 0, 0, 0.24);\n  border-radius: 2px !important;\n  color: var(--me-white);\n}\nsection.me-collapse > h5 > button ::v-deep i,\nsection.me-collapse .collapse-header > button ::v-deep i {\n  transition-duration: 0.3s;\n  transition-property: transform;\n}\nsection.me-collapse > h5 > button ::v-deep i.icon-chevron-down,\nsection.me-collapse .collapse-header > button ::v-deep i.icon-chevron-down {\n  transition-duration: 0.3s;\n  transition-property: transform;\n}\nsection.me-collapse.opened > h5 > button:not(.btn-secondary)[variant=square],\nsection.me-collapse.opened .collapse-header > button:not(.btn-secondary)[variant=square] {\n  background-color: rgba(12, 139, 203, 0.07);\n  color: var(--me-primary-2);\n}\nsection.me-collapse.opened > h5 > button:not(.btn-secondary) ::v-deep i.icon-chevron-down,\nsection.me-collapse.opened .collapse-header > button:not(.btn-secondary) ::v-deep i.icon-chevron-down {\n  transform: rotate(-180deg);\n}\nsection.me-collapse.opened > div {\n  display: block;\n}\n@media print {\n  section.me-collapse .me-collapse > h5 > button[variant=square],\nsection.me-collapse .me-collapse .collapse-header > button[variant=square] {\n    background-color: rgba(12, 139, 203, 0.07) !important;\n    color: var(--me-primary-2) !important;\n  }\n  section.me-collapse .me-collapse > h5 > button > i,\nsection.me-collapse .me-collapse .collapse-header > button > i {\n    transform: rotate(180deg) !important;\n    color: var(--me-gray-2);\n  }\n  section.me-collapse .me-collapse > div {\n    display: block !important;\n  }\n}\n\n.content-transition-enter-active, .content-transition-leave-active {\n  transition: all 0.3s;\n}\n\n.content-transition-enter, .content-transition-leave-to {\n  transform: translateY(-10px);\n  opacity: 0;\n}\n\n/*# sourceMappingURL=Collapse.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$n = "data-v-7d7a674e";
  /* module identifier */
  const __vue_module_identifier__$n = undefined;
  /* functional template */
  const __vue_is_functional_template__$n = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$n, staticRenderFns: __vue_staticRenderFns__$n },
    __vue_inject_styles__$n,
    __vue_script__$n,
    __vue_scope_id__$n,
    __vue_is_functional_template__$n,
    __vue_module_identifier__$n,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$o = {
  name: 'MeInputIcons',
  components: {
    Icon: __vue_component__
  },

  props: {
    type: {
      type: String,
      default: 'text'
    },
    hasAppend: {
      type: Boolean,
      default: false
    },
    state: {
      type: Boolean,
      default: null,
      validator: prop => typeof prop === typeof true || prop === null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isPasswordVisible: false
    }
  },

  methods: {
    togglePasswordType() {
      this.isPasswordVisible = !this.isPasswordVisible;
      this.$emit('togglePasswordType');
    }
  }
};

/* script */
const __vue_script__$o = script$o;

/* template */
var __vue_render__$o = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "input-icons" }, [
    _vm.type !== "range" && _vm.state !== null
      ? _c(
          "div",
          {
            staticClass: "state-icon-container",
            class: [
              "state-icon-container--" + _vm.type,
              { "state-icon-container--append": _vm.hasAppend }
            ]
          },
          [
            _vm.state
              ? _c("Icon", {
                  staticClass: "state-valid",
                  attrs: { icon: "me-icon-s icon-check-circle" }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.state === false
              ? _c("Icon", {
                  staticClass: "state-invalid",
                  attrs: { icon: "me-icon-s icon-circle-exclamation" }
                })
              : _vm._e()
          ],
          1
        )
      : _vm._e(),
    _vm._v(" "),
    _vm.type === "password"
      ? _c(
          "div",
          {
            staticClass: "password-icon-container",
            class: {
              disabled: _vm.disabled,
              "password-icon-container--append": _vm.hasAppend
            },
            on: { click: _vm.togglePasswordType }
          },
          [
            _vm.isPasswordVisible
              ? _c("Icon", {
                  staticClass: "password-icon me-icon-s icon-eye",
                  attrs: { icon: "me-icon-s icon-eye" }
                })
              : _c("Icon", {
                  staticClass: "password-icon",
                  attrs: { icon: "me-icon-s icon-eye-slash" }
                })
          ],
          1
        )
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$o = [];
__vue_render__$o._withStripped = true;

  /* style */
  const __vue_inject_styles__$o = function (inject) {
    if (!inject) return
    inject("data-v-f9596496_0", { source: ".input-icons[data-v-f9596496] {\n  position: absolute;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  z-index: 2;\n  font-size: 12px;\n  right: 0;\n  top: 0;\n}\n.input-icons .state-icon-container[data-v-f9596496] {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n}\n.input-icons .state-icon-container .state-valid[data-v-f9596496] {\n  color: var(--me-success-2);\n}\n.input-icons .state-icon-container .state-invalid[data-v-f9596496] {\n  color: var(--me-danger-2);\n}\n.input-icons .state-icon-container--append[data-v-f9596496] {\n  padding-right: 40px;\n}\n.input-icons .password-icon-container[data-v-f9596496] {\n  color: var(--me-gray-3);\n  width: 32px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 100%;\n  cursor: pointer;\n}\n.input-icons .password-icon-container--append[data-v-f9596496] {\n  padding-right: 40px;\n}\n.input-icons .password-icon-container.disabled[data-v-f9596496] {\n  pointer-events: none;\n}\n\n/*# sourceMappingURL=InputIcons.vue.map */", map: {"version":3,"sources":["/app/build/src/components/InputIcons.vue","InputIcons.vue"],"names":[],"mappings":"AAsFA;EACA,kBAAA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;ACrFA;ADuFA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,WAAA;ACrFA;ADuFA;EACA,0BAAA;ACrFA;ADwFA;EACA,yBAAA;ACtFA;ADyFA;EACA,mBAAA;ACvFA;AD2FA;EACA,uBAAA;EACA,WAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,YAAA;EACA,eAAA;ACzFA;AD2FA;EACA,mBAAA;ACzFA;AD4FA;EACA,oBAAA;AC1FA;;AAEA,yCAAyC","file":"InputIcons.vue","sourcesContent":["<template>\n  <div class=\"input-icons\">\n    <div\n      v-if=\"type !== 'range' && state !== null\"\n      :class=\"[`state-icon-container--${type}`, {'state-icon-container--append': hasAppend}]\"\n      class=\"state-icon-container\"\n    >\n      <Icon\n        v-if=\"state\"\n        class=\"state-valid\"\n        icon=\"me-icon-s icon-check-circle\"\n      />\n\n      <Icon\n        v-if=\"state === false\"\n        class=\"state-invalid\"\n        icon=\"me-icon-s icon-circle-exclamation\"\n      />\n    </div>\n\n    <div\n      v-if=\"type === 'password'\"\n      @click=\"togglePasswordType\"\n      :class=\"{disabled: disabled, 'password-icon-container--append': hasAppend}\"\n      class=\"password-icon-container\"\n    >\n      <Icon\n        v-if=\"isPasswordVisible\"\n        class=\"password-icon me-icon-s icon-eye\"\n        icon=\"me-icon-s icon-eye\"\n      />\n\n      <Icon\n        v-else\n        class=\"password-icon\"\n        icon=\"me-icon-s icon-eye-slash\"\n      />\n    </div>\n  </div>\n</template>\n\n<script>\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeInputIcons',\n  components: {\n    Icon\n  },\n\n  props: {\n    type: {\n      type: String,\n      default: 'text'\n    },\n    hasAppend: {\n      type: Boolean,\n      default: false\n    },\n    state: {\n      type: Boolean,\n      default: null,\n      validator: prop => typeof prop === typeof true || prop === null\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      isPasswordVisible: false\n    }\n  },\n\n  methods: {\n    togglePasswordType() {\n      this.isPasswordVisible = !this.isPasswordVisible\n      this.$emit('togglePasswordType')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .input-icons {\n    position: absolute;\n    height: 100%;\n    display: flex;\n    align-items: center;\n    z-index: 2;\n    font-size: 12px;\n    right: 0;\n    top: 0;\n\n    .state-icon-container {\n      display: flex;\n      align-items: center;\n      justify-content: center;\n      width: 32px;\n\n      .state-valid {\n        color: var(--me-success-2);\n      }\n\n      .state-invalid {\n        color: var(--me-danger-2);\n      }\n\n      &--append {\n        padding-right: 40px;\n      }\n    }\n\n    .password-icon-container {\n      color: var(--me-gray-3);\n      width: 32px;\n      display: flex;\n      align-items: center;\n      justify-content: center;\n      height: 100%;\n      cursor: pointer;\n\n      &--append {\n        padding-right: 40px;\n      }\n\n      &.disabled {\n        pointer-events: none;\n      }\n    }\n  }\n</style>\n",".input-icons {\n  position: absolute;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  z-index: 2;\n  font-size: 12px;\n  right: 0;\n  top: 0;\n}\n.input-icons .state-icon-container {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n}\n.input-icons .state-icon-container .state-valid {\n  color: var(--me-success-2);\n}\n.input-icons .state-icon-container .state-invalid {\n  color: var(--me-danger-2);\n}\n.input-icons .state-icon-container--append {\n  padding-right: 40px;\n}\n.input-icons .password-icon-container {\n  color: var(--me-gray-3);\n  width: 32px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 100%;\n  cursor: pointer;\n}\n.input-icons .password-icon-container--append {\n  padding-right: 40px;\n}\n.input-icons .password-icon-container.disabled {\n  pointer-events: none;\n}\n\n/*# sourceMappingURL=InputIcons.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$o = "data-v-f9596496";
  /* module identifier */
  const __vue_module_identifier__$o = undefined;
  /* functional template */
  const __vue_is_functional_template__$o = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$o, staticRenderFns: __vue_staticRenderFns__$o },
    __vue_inject_styles__$o,
    __vue_script__$o,
    __vue_scope_id__$o,
    __vue_is_functional_template__$o,
    __vue_module_identifier__$o,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$p = {
  name: 'MeTextInput',
  components: {
    InputIcons: __vue_component__$o,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    MeIcon: __vue_component__
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      type: Boolean,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    maxlength: {
      type: [Number, String],
      default: null
    },
    max: {
      type: [Number, String],
      default: null
    },
    min: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    return {
      current: this.value,
      isPasswordVisible: false,
      focused: false
    }
  },

  computed: {
    clazz() {
      return `me-text-input ${this.size} ${this.stateClazz}`
    },

    stateClazz() {
      if (this.state === null) return ''

      return this.state ? 'is-valid' : 'is-invalid'
    },

    searchIconsClazz() {
      return `search-type-icons ${this.size}`
    },

    hasPrepend() {
      return this.$slots.prepend
    },

    hasAppend() {
      return this.$slots.append
    }
  },

  watch: {
    value(val) {
      this.current = val;
    }
  },

  methods: {
    onFocus() {
      this.focused = true;
      this.$emit('focus');
    },

    onBlur() {
      this.focused = false;
      this.$emit('blur', this.current);
    },

    clearTerm() {
      this.current = '';
      this.$emit('input', this.current);
    }
  }
};

/* script */
const __vue_script__$p = script$p;

/* template */
var __vue_render__$p = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BInputGroup",
    {
      class: [
        _vm.clazz,
        {
          "search-type": _vm.type === "search"
        }
      ],
      attrs: { size: _vm.size, "data-cy": "b-text-input" }
    },
    [
      _vm.$slots.prepend
        ? _c(
            "BInputGroupPrepend",
            { attrs: { "is-text": "" } },
            [_vm._t("prepend")],
            2
          )
        : _vm._e(),
      _vm._v(" "),
      _c("InputIcons", {
        attrs: {
          state: _vm.state,
          type: _vm.type,
          "has-append": !!_vm.$slots.append,
          disabled: _vm.disabled
        },
        on: {
          togglePasswordType: function($event) {
            _vm.isPasswordVisible = !_vm.isPasswordVisible;
          }
        }
      }),
      _vm._v(" "),
      _vm.type === "search"
        ? _c("MeIcon", {
            class: _vm.searchIconsClazz,
            attrs: {
              color: _vm.focused ? "var(--me-gray-4)" : "var(--me-gray-3)",
              icon: "me-icon-l icon-magnifying-glass",
              size: "14px"
            }
          })
        : _vm._e(),
      _vm._v(" "),
      _c("BFormInput", {
        class: {
          "password-type": _vm.type === "password",
          "search-prepend": _vm.type === "search" && _vm.hasPrepend,
          "search-append": _vm.type === "search" && _vm.hasAppend
        },
        attrs: {
          disabled: _vm.disabled,
          max: _vm.max,
          maxlength: _vm.maxlength,
          min: _vm.min,
          placeholder: _vm.placeholder,
          size: _vm.size,
          state: _vm.state,
          type:
            _vm.isPasswordVisible || _vm.type === "search" ? "text" : _vm.type,
          autocomplete: "off"
        },
        on: {
          input: function($event) {
            return _vm.$emit("input", _vm.current)
          },
          keyup: function($event) {
            if (
              !$event.type.indexOf("key") &&
              _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")
            ) {
              return null
            }
            return _vm.$emit("enter", _vm.current)
          },
          blur: _vm.onBlur,
          focus: _vm.onFocus
        },
        model: {
          value: _vm.current,
          callback: function($$v) {
            _vm.current = $$v;
          },
          expression: "current"
        }
      }),
      _vm._v(" "),
      _vm.$slots.append
        ? _c(
            "BInputGroupAppend",
            { attrs: { "is-text": "" } },
            [_vm._t("append")],
            2
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.current && _vm.type === "search"
        ? _c("MeIcon", {
            class: _vm.searchIconsClazz,
            attrs: {
              color: "var(--me-gray-3)",
              icon: "me-icon-l icon-xmark",
              size: "14px"
            },
            on: { click: _vm.clearTerm }
          })
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$p = [];
__vue_render__$p._withStripped = true;

  /* style */
  const __vue_inject_styles__$p = function (inject) {
    if (!inject) return
    inject("data-v-28d218ca_0", { source: ".me-text-input > input.form-control[data-v-28d218ca] {\n  color: var(--me-gray-0);\n  border-radius: 8px;\n  padding-left: 16px;\n  box-shadow: none;\n  z-index: 1;\n}\n.me-text-input > input.form-control[data-v-28d218ca]:not(.is-invalid) {\n  border-color: var(--me-gray-6);\n}\n.me-text-input > input.form-control[data-v-28d218ca]:focus {\n  outline: none;\n  box-shadow: none;\n}\n.me-text-input > input.form-control[data-v-28d218ca]:focus:not(.is-invalid) {\n  border-color: var(--me-primary-2);\n}\n.search-type > input.form-control[data-v-28d218ca] {\n  padding-left: 40px !important;\n  padding-right: 40px !important;\n}\n.search-type > input.form-control.search-append[data-v-28d218ca]:not(.search-prepend) {\n  border-top-right-radius: 0 !important;\n  border-bottom-right-radius: 0 !important;\n  border-top-left-radius: 8px !important;\n  border-bottom-left-radius: 8px !important;\n}\n.search-type > input.form-control.search-prepend[data-v-28d218ca]:not(.search-append) {\n  border-top-left-radius: 0 !important;\n  border-bottom-left-radius: 0 !important;\n  border-top-right-radius: 8px !important;\n  border-bottom-right-radius: 8px !important;\n}\n.search-type > input.form-control[data-v-28d218ca]:not(.search-append):not(.search-prepend) {\n  border-radius: 8px !important;\n}\n.search-type .search-type-icons[data-v-28d218ca] {\n  position: absolute;\n  z-index: 2;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n}\n.search-type .search-type-icons.icon-xmark[data-v-28d218ca] {\n  right: 0;\n  cursor: pointer;\n}\n.search-type .search-type-icons.sm[data-v-28d218ca] {\n  height: 32px;\n}\n.search-type .search-type-icons.md[data-v-28d218ca] {\n  height: 40px;\n}\n.search-type .search-type-icons.lg[data-v-28d218ca] {\n  height: 48px;\n}\n.search-type div.input-group-prepend ~ i.icon-magnifying-glass.sm[data-v-28d218ca] {\n  left: 32px;\n}\n.search-type div.input-group-prepend ~ i.icon-magnifying-glass.md[data-v-28d218ca] {\n  left: 41.6px;\n}\n.search-type div.input-group-prepend ~ i.icon-magnifying-glass.lg[data-v-28d218ca] {\n  left: 53.6px;\n}\n.search-type div.input-group-append ~ i.icon-xmark.sm[data-v-28d218ca] {\n  right: 32px;\n}\n.search-type div.input-group-append ~ i.icon-xmark.md[data-v-28d218ca] {\n  right: 41.6px;\n}\n.search-type div.input-group-append ~ i.icon-xmark.lg[data-v-28d218ca] {\n  right: 53.6px;\n}\n.me-text-input > input.form-control[data-v-28d218ca] {\n  padding-left: 8px;\n  padding-right: 8px;\n  position: relative;\n}\n.me-text-input > input.form-control[type=date][data-v-28d218ca]::-webkit-calendar-picker-indicator, .me-text-input > input.form-control[type=time][data-v-28d218ca]::-webkit-calendar-picker-indicator {\n  background-image: none;\n}\n.me-text-input > input.form-control[data-v-28d218ca]:not(:first-child) {\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n}\n.me-text-input > input.form-control[data-v-28d218ca]:not(:last-child) {\n  border-top-right-radius: 0;\n  border-bottom-right-radius: 0;\n}\n.me-text-input > input.form-control.is-invalid[data-v-28d218ca], .me-text-input > input.form-control.is-valid[data-v-28d218ca] {\n  background-image: none;\n  padding-right: 40px;\n}\n.me-text-input > input.form-control.is-invalid[data-v-28d218ca] {\n  border-color: var(--me-danger-2);\n}\n.me-text-input > input.form-control.is-valid[data-v-28d218ca] {\n  border-color: var(--me-gray-6);\n}\n.me-text-input > input.form-control.password-type[data-v-28d218ca] {\n  padding-right: 40px;\n}\n.me-text-input > input.form-control.password-type.is-valid[data-v-28d218ca], .me-text-input > input.form-control.password-type.is-invalid[data-v-28d218ca] {\n  padding-right: 72px;\n}\n.me-text-input > input.form-control[data-v-28d218ca]:disabled {\n  background: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border: 1px solid var(--me-gray-4);\n}\n.me-text-input.sm input[data-v-28d218ca] {\n  font-size: 12px;\n  height: 32px;\n}\n.me-text-input.md input[data-v-28d218ca] {\n  font-size: 14px;\n  height: 40px;\n}\n.me-text-input.lg > input[data-v-28d218ca] {\n  font-size: 16px;\n  height: 48px;\n}\n.me-text-input .input-icons:first-child + input.form-control[data-v-28d218ca] {\n  border-top-left-radius: 8px;\n  border-bottom-left-radius: 8px;\n}\n\n/*# sourceMappingURL=TextInput.vue.map */", map: {"version":3,"sources":["TextInput.vue","/app/build/src/components/TextInput.vue"],"names":[],"mappings":"AAAA;EACE,uBAAuB;EACvB,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,UAAU;AACZ;AACA;EACE,8BAA8B;AAChC;AACA;EACE,aAAa;EACb,gBAAgB;AAClB;AACA;EACE,iCAAiC;AACnC;ACgLA;EACA,6BAAA;EACA,8BAAA;AD7KA;AC+KA;EACA,qCAAA;EACA,wCAAA;EACA,sCAAA;EACA,yCAAA;AD7KA;ACgLA;EACA,oCAAA;EACA,uCAAA;EACA,uCAAA;EACA,0CAAA;AD9KA;ACiLA;EACA,6BAAA;AD/KA;ACmLA;EACA,kBAAA;EACA,UAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,WAAA;ADjLA;ACmLA;EACA,QAAA;EACA,eAAA;ADjLA;ACoLA;EACA,YAAA;ADlLA;ACqLA;EACA,YAAA;ADnLA;ACsLA;EACA,YAAA;ADpLA;AC0LA;EACA,UAAA;ADxLA;AC2LA;EACA,YAAA;ADzLA;AC4LA;EACA,YAAA;AD1LA;ACiMA;EACA,WAAA;AD/LA;ACkMA;EACA,aAAA;ADhMA;ACmMA;EACA,aAAA;ADjMA;ACwMA;EACA,iBAAA;EACA,kBAAA;EACA,kBAAA;ADrMA;ACwMA;EAEA,sBAAA;ADvMA;AC0MA;EACA,yBAAA;EACA,4BAAA;ADxMA;AC2MA;EACA,0BAAA;EACA,6BAAA;ADzMA;AC4MA;EAEA,sBAAA;EACA,mBA/GA;AD5FA;AC8MA;EACA,gCAAA;AD5MA;AC+MA;EACA,8BAAA;AD7MA;ACgNA;EACA,mBA3HA;ADnFA;ACgNA;EAEA,mBA9HA;ADjFA;ACmNA;EACA,4BAAA;EACA,uBAAA;EACA,kCAAA;ADjNA;ACsNA;EACA,eAAA;EACA,YAAA;ADpNA;ACyNA;EACA,eAAA;EACA,YAAA;ADvNA;AC4NA;EACA,eAAA;EACA,YAAA;AD1NA;AC+NA;EACA,2BAAA;EACA,8BAAA;AD7NA;;AAEA,wCAAwC","file":"TextInput.vue","sourcesContent":[".me-text-input > input.form-control {\n  color: var(--me-gray-0);\n  border-radius: 8px;\n  padding-left: 16px;\n  box-shadow: none;\n  z-index: 1;\n}\n.me-text-input > input.form-control:not(.is-invalid) {\n  border-color: var(--me-gray-6);\n}\n.me-text-input > input.form-control:focus {\n  outline: none;\n  box-shadow: none;\n}\n.me-text-input > input.form-control:focus:not(.is-invalid) {\n  border-color: var(--me-primary-2);\n}\n\n.search-type > input.form-control {\n  padding-left: 40px !important;\n  padding-right: 40px !important;\n}\n.search-type > input.form-control.search-append:not(.search-prepend) {\n  border-top-right-radius: 0 !important;\n  border-bottom-right-radius: 0 !important;\n  border-top-left-radius: 8px !important;\n  border-bottom-left-radius: 8px !important;\n}\n.search-type > input.form-control.search-prepend:not(.search-append) {\n  border-top-left-radius: 0 !important;\n  border-bottom-left-radius: 0 !important;\n  border-top-right-radius: 8px !important;\n  border-bottom-right-radius: 8px !important;\n}\n.search-type > input.form-control:not(.search-append):not(.search-prepend) {\n  border-radius: 8px !important;\n}\n.search-type .search-type-icons {\n  position: absolute;\n  z-index: 2;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 32px;\n}\n.search-type .search-type-icons.icon-xmark {\n  right: 0;\n  cursor: pointer;\n}\n.search-type .search-type-icons.sm {\n  height: 32px;\n}\n.search-type .search-type-icons.md {\n  height: 40px;\n}\n.search-type .search-type-icons.lg {\n  height: 48px;\n}\n.search-type div.input-group-prepend ~ i.icon-magnifying-glass.sm {\n  left: 32px;\n}\n.search-type div.input-group-prepend ~ i.icon-magnifying-glass.md {\n  left: 41.6px;\n}\n.search-type div.input-group-prepend ~ i.icon-magnifying-glass.lg {\n  left: 53.6px;\n}\n.search-type div.input-group-append ~ i.icon-xmark.sm {\n  right: 32px;\n}\n.search-type div.input-group-append ~ i.icon-xmark.md {\n  right: 41.6px;\n}\n.search-type div.input-group-append ~ i.icon-xmark.lg {\n  right: 53.6px;\n}\n\n.me-text-input > input.form-control {\n  padding-left: 8px;\n  padding-right: 8px;\n  position: relative;\n}\n.me-text-input > input.form-control[type=date]::-webkit-calendar-picker-indicator, .me-text-input > input.form-control[type=time]::-webkit-calendar-picker-indicator {\n  background-image: none;\n}\n.me-text-input > input.form-control:not(:first-child) {\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n}\n.me-text-input > input.form-control:not(:last-child) {\n  border-top-right-radius: 0;\n  border-bottom-right-radius: 0;\n}\n.me-text-input > input.form-control.is-invalid, .me-text-input > input.form-control.is-valid {\n  background-image: none;\n  padding-right: 40px;\n}\n.me-text-input > input.form-control.is-invalid {\n  border-color: var(--me-danger-2);\n}\n.me-text-input > input.form-control.is-valid {\n  border-color: var(--me-gray-6);\n}\n.me-text-input > input.form-control.password-type {\n  padding-right: 40px;\n}\n.me-text-input > input.form-control.password-type.is-valid, .me-text-input > input.form-control.password-type.is-invalid {\n  padding-right: 72px;\n}\n.me-text-input > input.form-control:disabled {\n  background: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border: 1px solid var(--me-gray-4);\n}\n.me-text-input.sm input {\n  font-size: 12px;\n  height: 32px;\n}\n.me-text-input.md input {\n  font-size: 14px;\n  height: 40px;\n}\n.me-text-input.lg > input {\n  font-size: 16px;\n  height: 48px;\n}\n.me-text-input .input-icons:first-child + input.form-control {\n  border-top-left-radius: 8px;\n  border-bottom-left-radius: 8px;\n}\n\n/*# sourceMappingURL=TextInput.vue.map */","<template>\n  <BInputGroup\n    :class=\"[clazz, {\n      'search-type': type === 'search'\n    }]\"\n    :size=\"size\"\n    data-cy=\"b-text-input\"\n  >\n    <BInputGroupPrepend\n      v-if=\"$slots.prepend\"\n      is-text\n    >\n      <slot name=\"prepend\" />\n    </BInputGroupPrepend>\n\n    <InputIcons\n      :state=\"state\"\n      :type=\"type\"\n      :has-append=\"!!$slots.append\"\n      :disabled=\"disabled\"\n      @togglePasswordType=\"isPasswordVisible = !isPasswordVisible\"\n    />\n\n    <MeIcon\n      v-if=\"type === 'search'\"\n      :class=\"searchIconsClazz\"\n      :color=\"focused\n        ? 'var(--me-gray-4)' : 'var(--me-gray-3)'\"\n      icon=\"me-icon-l icon-magnifying-glass\"\n      size=\"14px\"\n    />\n\n    <BFormInput\n      v-model=\"current\"\n      :disabled=\"disabled\"\n      :max=\"max\"\n      :maxlength=\"maxlength\"\n      :min=\"min\"\n      :placeholder=\"placeholder\"\n      :size=\"size\"\n      :state=\"state\"\n      :type=\"isPasswordVisible || type === 'search'\n        ? 'text' : type\"\n      :class=\"{\n        'password-type': type === 'password',\n        'search-prepend': type === 'search' && hasPrepend,\n        'search-append': type === 'search' && hasAppend\n      }\"\n      @input=\"$emit('input', current)\"\n      @keyup.enter=\"$emit('enter', current)\"\n      @blur=\"onBlur\"\n      @focus=\"onFocus\"\n      autocomplete=\"off\"\n    />\n\n    <BInputGroupAppend\n      v-if=\"$slots.append\"\n      is-text\n    >\n      <slot name=\"append\" />\n    </BInputGroupAppend>\n\n    <MeIcon\n      v-if=\"current && type === 'search'\"\n      :class=\"searchIconsClazz\"\n      :color=\"'var(--me-gray-3)'\"\n      @click=\"clearTerm\"\n      icon=\"me-icon-l icon-xmark\"\n      size=\"14px\"\n    />\n  </BInputGroup>\n</template>\n\n<script>\nimport { BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend } from 'bootstrap-vue'\nimport InputIcons from './InputIcons.vue'\nimport MeIcon from './Icon.vue'\n\nexport default {\n  name: 'MeTextInput',\n  components: {\n    InputIcons,\n    BFormInput,\n    BInputGroup,\n    BInputGroupPrepend,\n    BInputGroupAppend,\n    MeIcon\n  },\n\n  props: {\n    value: {\n      type: String,\n      default: ''\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    type: {\n      type: String,\n      default: 'text'\n    },\n    maxlength: {\n      type: [Number, String],\n      default: null\n    },\n    max: {\n      type: [Number, String],\n      default: null\n    },\n    min: {\n      type: [Number, String],\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      current: this.value,\n      isPasswordVisible: false,\n      focused: false\n    }\n  },\n\n  computed: {\n    clazz() {\n      return `me-text-input ${this.size} ${this.stateClazz}`\n    },\n\n    stateClazz() {\n      if (this.state === null) return ''\n\n      return this.state ? 'is-valid' : 'is-invalid'\n    },\n\n    searchIconsClazz() {\n      return `search-type-icons ${this.size}`\n    },\n\n    hasPrepend() {\n      return this.$slots.prepend\n    },\n\n    hasAppend() {\n      return this.$slots.append\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    }\n  },\n\n  methods: {\n    onFocus() {\n      this.focused = true\n      this.$emit('focus')\n    },\n\n    onBlur() {\n      this.focused = false\n      this.$emit('blur', this.current)\n    },\n\n    clearTerm() {\n      this.current = ''\n      this.$emit('input', this.current)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/input.scss\";\n\n  $padding-one-icon: 40px;\n  $padding-two-icons: 72px;\n\n  .search-type {\n    & > input.form-control {\n      padding-left: $padding-one-icon !important;\n      padding-right: $padding-one-icon !important;\n\n      &.search-append:not(.search-prepend) {\n        border-top-right-radius: 0 !important;\n        border-bottom-right-radius: 0 !important;\n        border-top-left-radius: 8px !important;\n        border-bottom-left-radius: 8px !important;\n      }\n\n      &.search-prepend:not(.search-append) {\n        border-top-left-radius: 0 !important;\n        border-bottom-left-radius: 0 !important;\n        border-top-right-radius: 8px !important;\n        border-bottom-right-radius: 8px !important;\n      }\n\n      &:not(.search-append):not(.search-prepend) {\n        border-radius: 8px !important;\n      }\n    }\n\n    .search-type-icons {\n      position: absolute;\n      z-index: 2;\n      display: flex;\n      align-items: center;\n      justify-content: center;\n      width: 32px;\n\n      &.icon-xmark {\n        right: 0;\n        cursor: pointer;\n      }\n\n      &.sm {\n        height: 32px;\n      }\n\n      &.md {\n        height: 40px;\n      }\n\n      &.lg {\n        height: 48px;\n      }\n    }\n\n    div.input-group-prepend {\n      & ~ i.icon-magnifying-glass {\n        &.sm {\n          left: 32px;\n        }\n\n        &.md {\n          left: 41.6px;\n        }\n\n        &.lg {\n          left: 53.6px;\n        }\n      }\n    }\n\n    div.input-group-append {\n      & ~ i.icon-xmark {\n        &.sm {\n          right: 32px;\n        }\n\n        &.md {\n          right: 41.6px;\n        }\n\n        &.lg {\n          right: 53.6px;\n        }\n      }\n    }\n  }\n\n  .me-text-input {\n    > input.form-control {\n      padding-left: 8px;\n      padding-right: 8px;\n      position: relative;\n      @extend %input-style;\n\n      &[type=\"date\"]::-webkit-calendar-picker-indicator,\n      &[type=\"time\"]::-webkit-calendar-picker-indicator {\n        background-image: none;\n      }\n\n      &:not(:first-child) {\n        border-top-left-radius: 0;\n        border-bottom-left-radius: 0;\n      }\n\n      &:not(:last-child) {\n        border-top-right-radius: 0;\n        border-bottom-right-radius: 0;\n      }\n\n      &.is-invalid,\n      &.is-valid {\n        background-image: none;\n        padding-right: $padding-one-icon;\n      }\n\n      &.is-invalid {\n        border-color: var(--me-danger-2);\n      }\n\n      &.is-valid {\n        border-color: var(--me-gray-6);\n      }\n\n      &.password-type {\n        padding-right: $padding-one-icon;\n\n        &.is-valid,\n        &.is-invalid {\n          padding-right: $padding-two-icons;\n        }\n      }\n\n      &:disabled {\n        background: var(--me-gray-6);\n        color: var(--me-gray-4);\n        border: 1px solid var(--me-gray-4);\n      }\n    }\n\n    &.sm {\n      input {\n        font-size: 12px;\n        height: 32px;\n      }\n    }\n\n    &.md {\n      input {\n        font-size: 14px;\n        height: 40px;\n      }\n    }\n\n    &.lg {\n      > input {\n        font-size: 16px;\n        height: 48px;\n      }\n    }\n\n    .input-icons:first-child {\n      + input.form-control {\n        border-top-left-radius: 8px;\n        border-bottom-left-radius: 8px;\n      }\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$p = "data-v-28d218ca";
  /* module identifier */
  const __vue_module_identifier__$p = undefined;
  /* functional template */
  const __vue_is_functional_template__$p = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$p, staticRenderFns: __vue_staticRenderFns__$p },
    __vue_inject_styles__$p,
    __vue_script__$p,
    __vue_scope_id__$p,
    __vue_is_functional_template__$p,
    __vue_module_identifier__$p,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$q = {
  name: 'MeAnalyticsForm',
  components: {
    Button: __vue_component__$6,
    Select: __vue_component__$m,
    Collapse: __vue_component__$n,
    Form: __vue_component__$h,
    FormGroup: __vue_component__$i,
    FormRow: __vue_component__$j,
    Checkbox: __vue_component__$l,
    TextInput: __vue_component__$p,
    Icon: __vue_component__
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    disabledSeries: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      model: cloneDeep_1(merge_1({
        type: {},
        field: {},
        operator: {},
        series: [],
        options: {}
      }, this.item)),
      limits: [5, 10, 15, 20, 25, 50, 100],
      stackTypes: [
        { name: 'normal', label: this.$toolkit.t('analytics.stacking.normal') },
        { name: 'percentage', label: this.$toolkit.t('analytics.stacking.percentage') }
      ],
      calendar: [
        { name: 'day', label: this.$toolkit.t('analytics.calendar.day') },
        { name: 'month', label: this.$toolkit.t('analytics.calendar.month') },
        { name: 'year', label: this.$toolkit.t('analytics.calendar.year') }
      ],
      types: [
        { name: 'area', label: this.$toolkit.t('analytics.types.area'), icon: 'me-icon icon-chart-area', stackable: false },
        { name: 'bar', label: this.$toolkit.t('analytics.types.bar'), icon: 'me-icon icon-bars', stackable: true },
        { name: 'line', label: this.$toolkit.t('analytics.types.line'), icon: 'me-icon icon-chart-line', stackable: false },
        { name: 'column', label: this.$toolkit.t('analytics.types.column'), icon: 'me-icon icon-chart-bar', stackable: true },
        { name: 'pie', label: this.$toolkit.t('analytics.types.pie'), icon: 'me-icon icon-chart-pie', stackable: false },
        { name: 'donut', label: this.$toolkit.t('analytics.types.donut'), icon: 'me-icon icon-dot-circle', stackable: false },
        { name: 'metric', label: this.$toolkit.t('analytics.types.metric'), icon: 'me-icon icon-calculator', stackable: false }
      ],
      operators: {
        number: [
          { name: 'count', label: this.$toolkit.t('analytics.operators.count') },
          { name: 'sum', label: this.$toolkit.t('analytics.operators.sum') },
          { name: 'max', label: this.$toolkit.t('analytics.operators.max') },
          { name: 'min', label: this.$toolkit.t('analytics.operators.min') },
          { name: 'avg', label: this.$toolkit.t('analytics.operators.avg') }
        ],
        string: [
          { name: 'count', label: this.$toolkit.t('analytics.operators.count') },
          { name: 'cardinality', label: this.$toolkit.t('analytics.operators.cardinality') }
        ],
        date: [
          { name: 'count', label: this.$toolkit.t('analytics.operators.count') }
        ],
        boolean: [
          { name: 'count', label: this.$toolkit.t('analytics.operators.count') }
        ]
      }
    }
  },
  validations: {
    model: {
      title: {
        required: required$1
      },
      type: {
        required: required$1
      },
      field: {
        required: required$1
      },
      operator: {
        required: requiredIf$1(({ type }) => {
          return type && type.name === 'metric'
        })
      },
      series: {
        $each: {
          field: {
            required: required$1
          },
          operator: {
            required: required$1
          }
        }
      }
    }
  },
  computed: {
    fieldLabel() {
      if (this.model.type.name === 'metric') {
        return this.$toolkit.t('analytics.properties.field')
      } else {
        return this.$toolkit.t('analytics.properties.groupBy')
      }
    },
    fixedSerie() {
      return this.model.type.name === 'pie' || this.model.type.name === 'donut'
    },
    isStackable() {
      return this.model.type.stackable
    },
    isDate() {
      return this.model.field && this.model.field.type === 'date'
    }
  },
  methods: {
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false
      }
      this.$emit('submit', this.model);
    },
    addSerie() {
      this.model.series.push({
        key: new Date().getTime(),
        field: {},
        operator: {}
      });
    },
    removeSerie(index) {
      this.model.series.splice(index, 1);
    },
    onTypeChange(value) {
      if (!value) {
        return
      }
      if (value.name === 'metric') {
        this.model.field = {};
        this.model.operator = {};
        this.model.series = [];
      } else {
        this.model.operator = {
          name: 'terms'
        };
        if (isEmpty_1(this.model.series)) {
          this.addSerie();
        }
        if (this.model.series.length > 1 && this.fixedSerie) {
          this.model.series = this.model.series.slice(0, 1);
        }
      }
    },
    onDateGroupingChange(value) {
      if (!value) {
        this.model.operator = {
          name: 'histogram'
        };
      } else {
        this.model.operator = {
          name: 'terms'
        };
      }
    }
  }
};

/* script */
const __vue_script__$q = script$q;

/* template */
var __vue_render__$q = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-analytics-form" },
    [
      _c(
        "Form",
        { on: { submit: _vm.submit } },
        [
          _c(
            "FormGroup",
            {
              attrs: {
                label: _vm.$toolkit.t("analytics.properties.title"),
                state: !_vm.$v.model.title.$anyError,
                "invalid-feedback": _vm.$toolkit.t("feedback.required")
              }
            },
            [
              _c("TextInput", {
                attrs: { state: !_vm.$v.model.title.$anyError ? null : false },
                model: {
                  value: _vm.model.title,
                  callback: function($$v) {
                    _vm.$set(_vm.model, "title", $$v);
                  },
                  expression: "model.title"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "FormRow",
            [
              _c(
                "FormGroup",
                {
                  attrs: {
                    cols: 6,
                    label: _vm.$toolkit.t("analytics.properties.type"),
                    state: !_vm.$v.model.type.$anyError,
                    "invalid-feedback": _vm.$toolkit.t("feedback.required")
                  }
                },
                [
                  _c("Select", {
                    attrs: {
                      options: _vm.types,
                      clearable: false,
                      state: !_vm.$v.model.type.$anyError ? null : false,
                      label: "label"
                    },
                    on: { input: _vm.onTypeChange },
                    scopedSlots: _vm._u([
                      {
                        key: "option",
                        fn: function(option) {
                          return [
                            _c("Icon", { attrs: { icon: option.icon } }),
                            _vm._v(" " + _vm._s(option.label) + "\n          ")
                          ]
                        }
                      }
                    ]),
                    model: {
                      value: _vm.model.type,
                      callback: function($$v) {
                        _vm.$set(_vm.model, "type", $$v);
                      },
                      expression: "model.type"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "FormGroup",
                {
                  attrs: {
                    cols: 6,
                    label: _vm.fieldLabel,
                    state: !_vm.$v.model.field.$anyError,
                    "invalid-feedback": _vm.$toolkit.t("feedback.required")
                  }
                },
                [
                  _c("Select", {
                    attrs: {
                      options: _vm.fields,
                      clearable: false,
                      state: !_vm.$v.model.field.$anyError ? null : false,
                      label: "label"
                    },
                    model: {
                      value: _vm.model.field,
                      callback: function($$v) {
                        _vm.$set(_vm.model, "field", $$v);
                      },
                      expression: "model.field"
                    }
                  })
                ],
                1
              )
            ],
            1
          ),
          _vm._v(" "),
          _vm.model.type.name === "metric"
            ? _c(
                "FormGroup",
                {
                  attrs: {
                    label: _vm.$toolkit.t("analytics.properties.operator"),
                    state: !_vm.$v.model.operator.$anyError,
                    "invalid-feedback": _vm.$toolkit.t("feedback.required")
                  }
                },
                [
                  _c("Select", {
                    attrs: {
                      options: _vm.operators[_vm.model.field.type] || [],
                      clearable: false,
                      state: !_vm.$v.model.operator.$anyError ? null : false,
                      label: "label"
                    },
                    model: {
                      value: _vm.model.operator,
                      callback: function($$v) {
                        _vm.$set(_vm.model, "operator", $$v);
                      },
                      expression: "model.operator"
                    }
                  })
                ],
                1
              )
            : _vm._e(),
          _vm._v(" "),
          !_vm.disabledSeries && _vm.model.series.length > 0
            ? _c(
                "div",
                { staticClass: "series" },
                _vm._l(_vm.model.series, function(serie, index) {
                  return _c(
                    "div",
                    { key: serie.key, staticClass: "item" },
                    [
                      _c("div", { staticClass: "row" }, [
                        _c(
                          "div",
                          { staticClass: "col-sm-4" },
                          [
                            _c(
                              "FormGroup",
                              {
                                attrs: {
                                  label: _vm.$toolkit.t(
                                    "analytics.properties.field"
                                  ),
                                  state: !_vm.$v.model.series.$each[index].field
                                    .$anyError,
                                  "invalid-feedback": _vm.$toolkit.t(
                                    "feedback.required"
                                  )
                                }
                              },
                              [
                                _c("Select", {
                                  attrs: {
                                    options: _vm.fields,
                                    clearable: false,
                                    state: !_vm.$v.model.series.$each[index]
                                      .field.$anyError
                                      ? null
                                      : false,
                                    label: "label"
                                  },
                                  model: {
                                    value: serie.field,
                                    callback: function($$v) {
                                      _vm.$set(serie, "field", $$v);
                                    },
                                    expression: "serie.field"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          { staticClass: "col-sm-4" },
                          [
                            _c(
                              "FormGroup",
                              {
                                attrs: {
                                  label: _vm.$toolkit.t(
                                    "analytics.properties.operator"
                                  ),
                                  state: !_vm.$v.model.series.$each[index]
                                    .operator.$anyError,
                                  "invalid-feedback": _vm.$toolkit.t(
                                    "feedback.required"
                                  )
                                }
                              },
                              [
                                _c("Select", {
                                  attrs: {
                                    options:
                                      _vm.operators[serie.field.type] || [],
                                    clearable: false,
                                    state: !_vm.$v.model.series.$each[index]
                                      .operator.$anyError
                                      ? null
                                      : false,
                                    label: "label"
                                  },
                                  model: {
                                    value: serie.operator,
                                    callback: function($$v) {
                                      _vm.$set(serie, "operator", $$v);
                                    },
                                    expression: "serie.operator"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "div",
                          { staticClass: "col-sm-4" },
                          [
                            _c(
                              "FormGroup",
                              {
                                attrs: {
                                  label: _vm.$toolkit.t(
                                    "analytics.properties.label"
                                  )
                                }
                              },
                              [
                                _c("TextInput", {
                                  attrs: {
                                    placeholder: _vm.$toolkit.t(
                                      "analytics.properties.label"
                                    )
                                  },
                                  model: {
                                    value: serie.label,
                                    callback: function($$v) {
                                      _vm.$set(serie, "label", $$v);
                                    },
                                    expression: "serie.label"
                                  }
                                })
                              ],
                              1
                            )
                          ],
                          1
                        )
                      ]),
                      _vm._v(" "),
                      index === 0 && !_vm.fixedSerie
                        ? _c(
                            "Button",
                            {
                              attrs: { pill: "", variant: "light", size: "sm" },
                              on: { click: _vm.addSerie }
                            },
                            [
                              _c("Icon", {
                                attrs: { icon: "me-icon icon-plus" }
                              })
                            ],
                            1
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      index !== 0 && !_vm.fixedSerie
                        ? _c(
                            "Button",
                            {
                              attrs: { pill: "", variant: "light", size: "sm" },
                              on: {
                                click: function($event) {
                                  return _vm.removeSerie(index)
                                }
                              }
                            },
                            [
                              _c("Icon", {
                                attrs: { icon: "me-icon icon-trash" }
                              })
                            ],
                            1
                          )
                        : _vm._e()
                    ],
                    1
                  )
                }),
                0
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.model.type.name !== "metric"
            ? _c(
                "Collapse",
                {
                  attrs: {
                    title: _vm.$toolkit.t("analytics.properties.advanced")
                  }
                },
                [
                  _vm.isDate
                    ? _c(
                        "FormGroup",
                        {
                          attrs: {
                            label: _vm.$toolkit.t(
                              "analytics.properties.options.dateGrouping"
                            )
                          }
                        },
                        [
                          _c("Select", {
                            attrs: { options: _vm.calendar, label: "label" },
                            on: { input: _vm.onDateGroupingChange },
                            model: {
                              value: _vm.model.options.dateGrouping,
                              callback: function($$v) {
                                _vm.$set(_vm.model.options, "dateGrouping", $$v);
                              },
                              expression: "model.options.dateGrouping"
                            }
                          })
                        ],
                        1
                      )
                    : _vm._e(),
                  _vm._v(" "),
                  _c(
                    "FormGroup",
                    {
                      attrs: {
                        label: _vm.$toolkit.t(
                          "analytics.properties.options.limit"
                        )
                      }
                    },
                    [
                      _c("Select", {
                        attrs: { options: _vm.limits, clearable: false },
                        model: {
                          value: _vm.model.options.limit,
                          callback: function($$v) {
                            _vm.$set(_vm.model.options, "limit", $$v);
                          },
                          expression: "model.options.limit"
                        }
                      })
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _vm.isStackable
                    ? _c(
                        "FormGroup",
                        {
                          attrs: {
                            label: _vm.$toolkit.t(
                              "analytics.properties.options.stacking"
                            )
                          }
                        },
                        [
                          _c("Select", {
                            attrs: { options: _vm.stackTypes, label: "label" },
                            model: {
                              value: _vm.model.options.stacking,
                              callback: function($$v) {
                                _vm.$set(_vm.model.options, "stacking", $$v);
                              },
                              expression: "model.options.stacking"
                            }
                          })
                        ],
                        1
                      )
                    : _vm._e(),
                  _vm._v(" "),
                  _c(
                    "FormGroup",
                    [
                      _c(
                        "Checkbox",
                        {
                          attrs: { toggle: "" },
                          model: {
                            value: _vm.model.options.showLegend,
                            callback: function($$v) {
                              _vm.$set(_vm.model.options, "showLegend", $$v);
                            },
                            expression: "model.options.showLegend"
                          }
                        },
                        [
                          _vm._v(
                            "\n          " +
                              _vm._s(
                                _vm.$toolkit.t(
                                  "analytics.properties.options.showLegend"
                                )
                              ) +
                              "\n        "
                          )
                        ]
                      )
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c(
                    "FormGroup",
                    [
                      _c(
                        "Checkbox",
                        {
                          attrs: { toggle: "" },
                          model: {
                            value: _vm.model.options.showDataLabel,
                            callback: function($$v) {
                              _vm.$set(_vm.model.options, "showDataLabel", $$v);
                            },
                            expression: "model.options.showDataLabel"
                          }
                        },
                        [
                          _vm._v(
                            "\n          " +
                              _vm._s(
                                _vm.$toolkit.t(
                                  "analytics.properties.options.showDataLabel"
                                )
                              ) +
                              "\n        "
                          )
                        ]
                      )
                    ],
                    1
                  )
                ],
                1
              )
            : _vm._e()
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$q = [];
__vue_render__$q._withStripped = true;

  /* style */
  const __vue_inject_styles__$q = function (inject) {
    if (!inject) return
    inject("data-v-39b36b24_0", { source: "div.me-analytics-form div.series[data-v-39b36b24] {\n  background-color: #f5f5f5;\n  border: 1px solid #e3e3e3;\n  padding: 10px;\n}\ndiv.me-analytics-form div.series > div.item[data-v-39b36b24] {\n  padding-right: 35px;\n  position: relative;\n}\ndiv.me-analytics-form div.series > div.item > button[data-v-39b36b24] {\n  position: absolute;\n  right: 0;\n  top: 35px;\n}\n\n/*# sourceMappingURL=AnalyticsForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/AnalyticsForm.vue","AnalyticsForm.vue"],"names":[],"mappings":"AAiRA;EACA,yBAAA;EACA,yBAAA;EACA,aAAA;AChRA;ADkRA;EACA,mBAAA;EACA,kBAAA;AChRA;ADkRA;EACA,kBAAA;EACA,QAAA;EACA,SAAA;AChRA;;AAEA,4CAA4C","file":"AnalyticsForm.vue","sourcesContent":["<template>\n  <div class=\"me-analytics-form\">\n    <Form @submit=\"submit\">\n      <FormGroup :label=\"$toolkit.t('analytics.properties.title')\" :state=\"!$v.model.title.$anyError\" :invalid-feedback=\"$toolkit.t('feedback.required')\">\n        <TextInput v-model=\"model.title\" :state=\"!$v.model.title.$anyError ? null : false\" />\n      </FormGroup>\n      <FormRow>\n        <FormGroup :cols=\"6\" :label=\"$toolkit.t('analytics.properties.type')\" :state=\"!$v.model.type.$anyError\" :invalid-feedback=\"$toolkit.t('feedback.required')\">\n          <Select\n            v-model=\"model.type\"\n            :options=\"types\"\n            :clearable=\"false\"\n            :state=\"!$v.model.type.$anyError ? null : false\"\n            @input=\"onTypeChange\"\n            label=\"label\"\n          >\n            <template slot=\"option\" slot-scope=\"option\">\n              <Icon :icon=\"option.icon\" /> {{ option.label }}\n            </template>\n          </Select>\n        </FormGroup>\n        <FormGroup :cols=\"6\" :label=\"fieldLabel\" :state=\"!$v.model.field.$anyError\" :invalid-feedback=\"$toolkit.t('feedback.required')\">\n          <Select v-model=\"model.field\" :options=\"fields\" :clearable=\"false\" :state=\"!$v.model.field.$anyError ? null : false\" label=\"label\" />\n        </FormGroup>\n      </FormRow>\n      <FormGroup v-if=\"model.type.name === 'metric'\" :label=\"$toolkit.t('analytics.properties.operator')\" :state=\"!$v.model.operator.$anyError\" :invalid-feedback=\"$toolkit.t('feedback.required')\">\n        <Select v-model=\"model.operator\" :options=\"operators[model.field.type] || []\" :clearable=\"false\" :state=\"!$v.model.operator.$anyError ? null : false\" label=\"label\" />\n      </FormGroup>\n      <div v-if=\"!disabledSeries && model.series.length > 0\" class=\"series\">\n        <div v-for=\"(serie, index) in model.series\" :key=\"serie.key\" class=\"item\">\n          <div class=\"row\">\n            <div class=\"col-sm-4\">\n              <FormGroup :label=\"$toolkit.t('analytics.properties.field')\" :state=\"!$v.model.series.$each[index].field.$anyError\" :invalid-feedback=\"$toolkit.t('feedback.required')\">\n                <Select v-model=\"serie.field\" :options=\"fields\" :clearable=\"false\" :state=\"!$v.model.series.$each[index].field.$anyError ? null : false\" label=\"label\" />\n              </FormGroup>\n            </div>\n            <div class=\"col-sm-4\">\n              <FormGroup :label=\"$toolkit.t('analytics.properties.operator')\" :state=\"!$v.model.series.$each[index].operator.$anyError\" :invalid-feedback=\"$toolkit.t('feedback.required')\">\n                <Select v-model=\"serie.operator\" :options=\"operators[serie.field.type] || []\" :clearable=\"false\" :state=\"!$v.model.series.$each[index].operator.$anyError ? null : false\" label=\"label\" />\n              </FormGroup>\n            </div>\n            <div class=\"col-sm-4\">\n              <FormGroup :label=\"$toolkit.t('analytics.properties.label')\">\n                <TextInput v-model=\"serie.label\" :placeholder=\"$toolkit.t('analytics.properties.label')\" />\n              </FormGroup>\n            </div>\n          </div>\n          <Button v-if=\"index === 0 && !fixedSerie\" @click=\"addSerie\" pill variant=\"light\" size=\"sm\">\n            <Icon icon=\"me-icon icon-plus\" />\n          </Button>\n          <Button v-if=\"index !== 0 && !fixedSerie\" @click=\"removeSerie(index)\" pill variant=\"light\" size=\"sm\">\n            <Icon icon=\"me-icon icon-trash\" />\n          </Button>\n        </div>\n      </div>\n      <Collapse v-if=\"model.type.name !== 'metric'\" :title=\"$toolkit.t('analytics.properties.advanced')\">\n        <FormGroup v-if=\"isDate\" :label=\"$toolkit.t('analytics.properties.options.dateGrouping')\">\n          <Select v-model=\"model.options.dateGrouping\" :options=\"calendar\" @input=\"onDateGroupingChange\" label=\"label\" />\n        </FormGroup>\n        <FormGroup :label=\"$toolkit.t('analytics.properties.options.limit')\">\n          <Select v-model=\"model.options.limit\" :options=\"limits\" :clearable=\"false\" />\n        </FormGroup>\n        <FormGroup v-if=\"isStackable\" :label=\"$toolkit.t('analytics.properties.options.stacking')\">\n          <Select v-model=\"model.options.stacking\" :options=\"stackTypes\" label=\"label\" />\n        </FormGroup>\n        <FormGroup>\n          <Checkbox v-model=\"model.options.showLegend\" toggle>\n            {{ $toolkit.t('analytics.properties.options.showLegend') }}\n          </Checkbox>\n        </FormGroup>\n        <FormGroup>\n          <Checkbox v-model=\"model.options.showDataLabel\" toggle>\n            {{ $toolkit.t('analytics.properties.options.showDataLabel') }}\n          </Checkbox>\n        </FormGroup>\n      </Collapse>\n    </Form>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport merge from 'lodash/merge'\nimport isEmpty from 'lodash/isEmpty'\nimport required from 'vuelidate/lib/validators/required'\nimport requiredIf from 'vuelidate/lib/validators/requiredIf'\nimport Button from './Button.vue'\nimport Form from './Form.vue'\nimport FormGroup from './FormGroup.vue'\nimport FormRow from './FormRow.vue'\nimport Checkbox from './Checkbox.vue'\nimport Select from './Select.vue'\nimport Collapse from './Collapse.vue'\nimport TextInput from './TextInput.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeAnalyticsForm',\n  components: {\n    Button,\n    Select,\n    Collapse,\n    Form,\n    FormGroup,\n    FormRow,\n    Checkbox,\n    TextInput,\n    Icon\n  },\n  props: {\n    item: {\n      type: Object,\n      required: true\n    },\n    fields: {\n      type: Array,\n      required: true\n    },\n    disabledSeries: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      model: cloneDeep(merge({\n        type: {},\n        field: {},\n        operator: {},\n        series: [],\n        options: {}\n      }, this.item)),\n      limits: [5, 10, 15, 20, 25, 50, 100],\n      stackTypes: [\n        { name: 'normal', label: this.$toolkit.t('analytics.stacking.normal') },\n        { name: 'percentage', label: this.$toolkit.t('analytics.stacking.percentage') }\n      ],\n      calendar: [\n        { name: 'day', label: this.$toolkit.t('analytics.calendar.day') },\n        { name: 'month', label: this.$toolkit.t('analytics.calendar.month') },\n        { name: 'year', label: this.$toolkit.t('analytics.calendar.year') }\n      ],\n      types: [\n        { name: 'area', label: this.$toolkit.t('analytics.types.area'), icon: 'me-icon icon-chart-area', stackable: false },\n        { name: 'bar', label: this.$toolkit.t('analytics.types.bar'), icon: 'me-icon icon-bars', stackable: true },\n        { name: 'line', label: this.$toolkit.t('analytics.types.line'), icon: 'me-icon icon-chart-line', stackable: false },\n        { name: 'column', label: this.$toolkit.t('analytics.types.column'), icon: 'me-icon icon-chart-bar', stackable: true },\n        { name: 'pie', label: this.$toolkit.t('analytics.types.pie'), icon: 'me-icon icon-chart-pie', stackable: false },\n        { name: 'donut', label: this.$toolkit.t('analytics.types.donut'), icon: 'me-icon icon-dot-circle', stackable: false },\n        { name: 'metric', label: this.$toolkit.t('analytics.types.metric'), icon: 'me-icon icon-calculator', stackable: false }\n      ],\n      operators: {\n        number: [\n          { name: 'count', label: this.$toolkit.t('analytics.operators.count') },\n          { name: 'sum', label: this.$toolkit.t('analytics.operators.sum') },\n          { name: 'max', label: this.$toolkit.t('analytics.operators.max') },\n          { name: 'min', label: this.$toolkit.t('analytics.operators.min') },\n          { name: 'avg', label: this.$toolkit.t('analytics.operators.avg') }\n        ],\n        string: [\n          { name: 'count', label: this.$toolkit.t('analytics.operators.count') },\n          { name: 'cardinality', label: this.$toolkit.t('analytics.operators.cardinality') }\n        ],\n        date: [\n          { name: 'count', label: this.$toolkit.t('analytics.operators.count') }\n        ],\n        boolean: [\n          { name: 'count', label: this.$toolkit.t('analytics.operators.count') }\n        ]\n      }\n    }\n  },\n  validations: {\n    model: {\n      title: {\n        required\n      },\n      type: {\n        required\n      },\n      field: {\n        required\n      },\n      operator: {\n        required: requiredIf(({ type }) => {\n          return type && type.name === 'metric'\n        })\n      },\n      series: {\n        $each: {\n          field: {\n            required\n          },\n          operator: {\n            required\n          }\n        }\n      }\n    }\n  },\n  computed: {\n    fieldLabel() {\n      if (this.model.type.name === 'metric') {\n        return this.$toolkit.t('analytics.properties.field')\n      } else {\n        return this.$toolkit.t('analytics.properties.groupBy')\n      }\n    },\n    fixedSerie() {\n      return this.model.type.name === 'pie' || this.model.type.name === 'donut'\n    },\n    isStackable() {\n      return this.model.type.stackable\n    },\n    isDate() {\n      return this.model.field && this.model.field.type === 'date'\n    }\n  },\n  methods: {\n    submit() {\n      this.$v.$touch()\n      if (this.$v.$invalid) {\n        return false\n      }\n      this.$emit('submit', this.model)\n    },\n    addSerie() {\n      this.model.series.push({\n        key: new Date().getTime(),\n        field: {},\n        operator: {}\n      })\n    },\n    removeSerie(index) {\n      this.model.series.splice(index, 1)\n    },\n    onTypeChange(value) {\n      if (!value) {\n        return\n      }\n      if (value.name === 'metric') {\n        this.model.field = {}\n        this.model.operator = {}\n        this.model.series = []\n      } else {\n        this.model.operator = {\n          name: 'terms'\n        }\n        if (isEmpty(this.model.series)) {\n          this.addSerie()\n        }\n        if (this.model.series.length > 1 && this.fixedSerie) {\n          this.model.series = this.model.series.slice(0, 1)\n        }\n      }\n    },\n    onDateGroupingChange(value) {\n      if (!value) {\n        this.model.operator = {\n          name: 'histogram'\n        }\n      } else {\n        this.model.operator = {\n          name: 'terms'\n        }\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-analytics-form {\n    div.series {\n      background-color: #f5f5f5;\n      border: 1px solid #e3e3e3;\n      padding: 10px;\n\n      > div.item {\n        padding-right: 35px;\n        position: relative;\n\n        > button {\n          position: absolute;\n          right: 0;\n          top: 35px;\n        }\n      }\n    }\n  }\n</style>\n","div.me-analytics-form div.series {\n  background-color: #f5f5f5;\n  border: 1px solid #e3e3e3;\n  padding: 10px;\n}\ndiv.me-analytics-form div.series > div.item {\n  padding-right: 35px;\n  position: relative;\n}\ndiv.me-analytics-form div.series > div.item > button {\n  position: absolute;\n  right: 0;\n  top: 35px;\n}\n\n/*# sourceMappingURL=AnalyticsForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$q = "data-v-39b36b24";
  /* module identifier */
  const __vue_module_identifier__$q = undefined;
  /* functional template */
  const __vue_is_functional_template__$q = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$q, staticRenderFns: __vue_staticRenderFns__$q },
    __vue_inject_styles__$q,
    __vue_script__$q,
    __vue_scope_id__$q,
    __vue_is_functional_template__$q,
    __vue_module_identifier__$q,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$r = {
  name: 'MeAnalyticsCard',
  components: {
    BDropdown,
    BDropdownItem,
    BDropdownGroup,
    BDropdownDivider,
    Chart: __vue_component__$g,
    Modal: __vue_component__$f,
    AnalyticsForm: __vue_component__$q,
    Spinner: __vue_component__$5,
    Icon: __vue_component__
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      default: null
    },
    fields: {
      type: Array,
      required: true
    },
    busy: {
      type: Boolean,
      default: false
    },
    disabledSeries: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    formatter: {
      type: Function,
      default: value => value
    }
  },
  data() {
    return {
      model: cloneDeep_1(merge_1({
        size: {
          width: 1,
          height: 2
        },
        options: {
        }
      }, this.item))
    }
  },
  computed: {
    height() {
      if (this.model.size.height === 1) {
        return '180px'
      } else {
        return '435px'
      }
    },
    clazz() {
      if (this.model.size.width === 3) {
        return 'col-lg-12'
      } else if (this.model.size.width === 2) {
        return 'col-lg-8'
      } else {
        return 'col-lg-4'
      }
    },
    categories() {
      return this.data.items.map(item => item.value)
    },
    series() {
      const series = [];
      this.item.series.forEach((serie, index) => {
        const data = this.data.items.map((category) => {
          return Number(category.items[index].value) || 0
        });
        series.push({
          name: serie.label || `${this.$toolkit.t('analytics.serie')} ${index + 1}`,
          data: data
        });
      });
      return series
    },
    resultValue() {
      return this.data.value
    },
    hasResult() {
      return this.data && (this.data.value || !isEmpty_1(this.data.items))
    },
    isShort() {
      return this.model.size.height === 1
    },
    stacking() {
      return this.model.options.stacking ? this.model.options.stacking.name : null
    },
    categoryType() {
      return this.model.operator && this.model.operator.name === 'histogram' &&
         this.model.field && this.model.field.type === 'date' ? 'datetime' : null
    }
  },
  methods: {
    changeWidth(width) {
      this.model.size.width = width;
      this.$emit('resize', this.model);
      window.dispatchEvent(new Event('resize'));
    },
    changeHeight(height) {
      this.model.size.height = height;
      this.$emit('resize', this.model);
    },
    edit() {
      this.$refs.modal.show();
    },
    remove() {
      this.$emit('remove', this.model);
    },
    download() {
      this.$refs.chart.download();
    },
    hide() {
      this.$refs.modal.hide();
    },
    submit(model) {
      this.hide();
      this.change(model);
    },
    change(model) {
      this.$emit('change', model);
    },
    selection({ categories }) {
      this.$emit('selection', cloneDeep_1({
        key: this.model.key,
        field: this.model.field,
        categories
      }));
    }
  }
};

/* script */
const __vue_script__$r = script$r;

/* template */
var __vue_render__$r = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { class: _vm.clazz },
    [
      _c("div", { staticClass: "me-card", class: { short: _vm.isShort } }, [
        _c(
          "div",
          { staticClass: "header" },
          [
            _c("label", [_vm._v(_vm._s(_vm.model.title))]),
            _vm._v(" "),
            _c(
              "BDropdown",
              {
                attrs: {
                  variant: "link",
                  right: "",
                  "no-caret": "",
                  "data-cy": "analitycsCardOptions"
                }
              },
              [
                _c(
                  "template",
                  { slot: "button-content" },
                  [_c("Icon", { attrs: { icon: "me-icon icon-bars" } })],
                  1
                ),
                _vm._v(" "),
                _vm.editable
                  ? _c(
                      "BDropdownItem",
                      { on: { click: _vm.edit } },
                      [
                        _c("Icon", { attrs: { icon: "me-icon icon-pencil" } }),
                        _vm._v(
                          " " +
                            _vm._s(_vm.$toolkit.t("analytics.actions.edit")) +
                            "\n        "
                        )
                      ],
                      1
                    )
                  : _vm._e(),
                _vm._v(" "),
                _c(
                  "BDropdownItem",
                  { on: { click: _vm.download } },
                  [
                    _c("Icon", { attrs: { icon: "me-icon icon-download" } }),
                    _vm._v(
                      " " +
                        _vm._s(_vm.$toolkit.t("analytics.actions.download")) +
                        "\n        "
                    )
                  ],
                  1
                ),
                _vm._v(" "),
                _c("BDropdownDivider"),
                _vm._v(" "),
                _c(
                  "BDropdownGroup",
                  {
                    attrs: {
                      header: _vm.$toolkit.t("analytics.properties.width")
                    }
                  },
                  [
                    _c(
                      "BDropdownItem",
                      {
                        attrs: {
                          title: "1/3",
                          "data-cy": "analitycsCardWidth"
                        },
                        on: {
                          click: function($event) {
                            return _vm.changeWidth(1)
                          }
                        }
                      },
                      [
                        _c("Icon", {
                          attrs: { icon: "me-icon icon-layout-left" }
                        })
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "BDropdownItem",
                      {
                        attrs: {
                          title: "2/3",
                          "data-cy": "analitycsCardWidth"
                        },
                        on: {
                          click: function($event) {
                            return _vm.changeWidth(2)
                          }
                        }
                      },
                      [
                        _c("Icon", {
                          attrs: { icon: "me-icon icon-layout-splitted" }
                        })
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "BDropdownItem",
                      {
                        attrs: {
                          title: "3/3",
                          "data-cy": "analitycsCardWidth"
                        },
                        on: {
                          click: function($event) {
                            return _vm.changeWidth(3)
                          }
                        }
                      },
                      [
                        _c("Icon", {
                          attrs: { icon: "me-icon icon-layout-full" }
                        })
                      ],
                      1
                    )
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "BDropdownGroup",
                  {
                    attrs: {
                      header: _vm.$toolkit.t("analytics.properties.height")
                    }
                  },
                  [
                    _c(
                      "BDropdownItem",
                      {
                        attrs: {
                          title: "1/2",
                          "data-cy": "analitycsCardHeight"
                        },
                        on: {
                          click: function($event) {
                            return _vm.changeHeight(1)
                          }
                        }
                      },
                      [
                        _c("Icon", {
                          attrs: { icon: "me-icon icon-layout-splitted-vert" }
                        })
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c(
                      "BDropdownItem",
                      {
                        attrs: {
                          title: "2/2",
                          "data-cy": "analitycsCardHeight"
                        },
                        on: {
                          click: function($event) {
                            return _vm.changeHeight(2)
                          }
                        }
                      },
                      [
                        _c("Icon", {
                          attrs: { icon: "me-icon icon-layout-full" }
                        })
                      ],
                      1
                    )
                  ],
                  1
                ),
                _vm._v(" "),
                _c("BDropdownDivider"),
                _vm._v(" "),
                _c(
                  "BDropdownItem",
                  { on: { click: _vm.remove } },
                  [
                    _c("Icon", { attrs: { icon: "me-icon icon-trash" } }),
                    _vm._v(
                      " " +
                        _vm._s(_vm.$toolkit.t("analytics.actions.remove")) +
                        "\n        "
                    )
                  ],
                  1
                )
              ],
              2
            )
          ],
          1
        ),
        _vm._v(" "),
        _c("div", { staticClass: "content" }, [
          _vm.busy
            ? _c("div", [
                _c(
                  "div",
                  { staticClass: "feedback" },
                  [
                    _c("Spinner", {
                      staticClass: "align-middle",
                      attrs: { variant: "primary" }
                    }),
                    _vm._v(
                      " " +
                        _vm._s(_vm.$toolkit.t("spinner.loading")) +
                        "\n        "
                    )
                  ],
                  1
                )
              ])
            : _vm.hasResult
            ? _c(
                "div",
                [
                  _vm.model.type.name === "metric"
                    ? _c("div", [
                        _c("h1", [
                          _vm._v(
                            "\n            " +
                              _vm._s(_vm._f("me-number")(_vm.resultValue)) +
                              "\n          "
                          )
                        ])
                      ])
                    : _c("Chart", {
                        ref: "chart",
                        attrs: {
                          type: _vm.model.type.name,
                          "show-data-label": _vm.model.options.showDataLabel,
                          "show-legend": _vm.model.options.showLegend,
                          height: _vm.height,
                          categories: _vm.categories,
                          "category-type": _vm.categoryType,
                          series: _vm.series,
                          stacking: _vm.stacking,
                          formatter: _vm.formatter
                        },
                        on: { selection: _vm.selection }
                      })
                ],
                1
              )
            : _c("div", [
                _c(
                  "div",
                  { staticClass: "feedback" },
                  [
                    _c("Icon", { attrs: { icon: "me-icon icon-sad-tear" } }),
                    _vm._v(
                      " " +
                        _vm._s(_vm.$toolkit.t("analytics.noResults")) +
                        "\n        "
                    )
                  ],
                  1
                )
              ])
        ])
      ]),
      _vm._v(" "),
      _c(
        "Modal",
        {
          ref: "modal",
          attrs: {
            title: _vm.$toolkit.t("analytics.edition"),
            size: "lg",
            "hide-footer": ""
          }
        },
        [
          _c("AnalyticsForm", {
            attrs: {
              item: _vm.model,
              fields: _vm.fields,
              "disabled-series": _vm.disabledSeries
            },
            on: { submit: _vm.submit }
          })
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$r = [];
__vue_render__$r._withStripped = true;

  /* style */
  const __vue_inject_styles__$r = function (inject) {
    if (!inject) return
    inject("data-v-9bb5e298_0", { source: ".me-card[data-v-9bb5e298] {\n  border: 0;\n  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.24);\n  background-color: white;\n  margin-bottom: 1em;\n}\n.me-card > div[data-v-9bb5e298] {\n  border-top: 1px solid rgba(0, 0, 0, 0.1);\n  padding: 15px 30px;\n  position: relative;\n}\n.me-card > div[data-v-9bb5e298]:first-child {\n  border: 0;\n}\ndiv.me-card > div.header[data-v-9bb5e298] {\n  padding: 10px 50px 10px 10px;\n}\ndiv.me-card > div.header > label[data-v-9bb5e298] {\n  display: list-item;\n  line-height: 22px;\n  margin: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\ndiv.me-card > div.header div.dropdown[data-v-9bb5e298] {\n  position: absolute;\n  right: 5px;\n  top: 3px;\n}\ndiv.me-card > div.header div.dropdown[data-v-9bb5e298]  button {\n  color: var(--me-gray-4);\n}\ndiv.me-card > div.header div.dropdown[data-v-9bb5e298]  button:after {\n  content: none;\n}\ndiv.me-card > div.header div.dropdown[data-v-9bb5e298]  ul > li > ul {\n  display: table-cell;\n  line-height: 20px;\n  padding-left: 15px;\n}\ndiv.me-card > div.header div.dropdown[data-v-9bb5e298]  ul > li > ul > li {\n  float: left;\n}\ndiv.me-card > div.header div.dropdown[data-v-9bb5e298]  ul > li > ul > li > a {\n  font-size: 18px;\n  padding: 5px;\n}\ndiv.me-card > div.content[data-v-9bb5e298] {\n  height: 450px;\n  padding: 0;\n}\ndiv.me-card > div.content > div[data-v-9bb5e298] {\n  display: table;\n  height: 100% !important;\n  width: 100%;\n}\ndiv.me-card > div.content > div > div[data-v-9bb5e298] {\n  display: table-cell;\n  vertical-align: middle;\n}\ndiv.me-card > div.content > div > div.feedback[data-v-9bb5e298], div.me-card > div.content > div > div > h1[data-v-9bb5e298] {\n  text-align: center;\n}\ndiv.me-card.short > div.content[data-v-9bb5e298] {\n  height: 195px;\n}\n\n/*# sourceMappingURL=AnalyticsCard.vue.map */", map: {"version":3,"sources":["AnalyticsCard.vue","/app/build/src/components/AnalyticsCard.vue"],"names":[],"mappings":"AAAA;EACE,SAAS;EACT,2CAA2C;EAC3C,uBAAuB;EACvB,kBAAkB;AACpB;AACA;EACE,wCAAwC;EACxC,kBAAkB;EAClB,kBAAkB;AACpB;AACA;EACE,SAAS;AACX;ACyOA;EACA,4BAAA;ADtOA;ACwOA;EACA,kBAAA;EACA,iBAAA;EACA,SAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;ADtOA;ACyOA;EACA,kBAAA;EACA,UAAA;EACA,QAAA;ADvOA;ACyOA;EACA,uBAAA;ADvOA;ACyOA;EACA,aAAA;ADvOA;AC6OA;EACA,mBAAA;EACA,iBAAA;EACA,kBAAA;AD3OA;AC6OA;EACA,WAAA;AD3OA;AC4OA;EACA,eAAA;EACA,YAAA;AD1OA;ACmPA;EACA,aAAA;EACA,UAAA;ADjPA;ACmPA;EACA,cAAA;EACA,uBAAA;EACA,WAAA;ADjPA;ACmPA;EACA,mBAAA;EACA,sBAAA;ADjPA;ACmPA;EACA,kBAAA;ADjPA;ACwPA;EACA,aAAA;ADtPA;;AAEA,4CAA4C","file":"AnalyticsCard.vue","sourcesContent":[".me-card {\n  border: 0;\n  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.24);\n  background-color: white;\n  margin-bottom: 1em;\n}\n.me-card > div {\n  border-top: 1px solid rgba(0, 0, 0, 0.1);\n  padding: 15px 30px;\n  position: relative;\n}\n.me-card > div:first-child {\n  border: 0;\n}\n\ndiv.me-card > div.header {\n  padding: 10px 50px 10px 10px;\n}\ndiv.me-card > div.header > label {\n  display: list-item;\n  line-height: 22px;\n  margin: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\ndiv.me-card > div.header div.dropdown {\n  position: absolute;\n  right: 5px;\n  top: 3px;\n}\ndiv.me-card > div.header div.dropdown ::v-deep button {\n  color: var(--me-gray-4);\n}\ndiv.me-card > div.header div.dropdown ::v-deep button:after {\n  content: none;\n}\ndiv.me-card > div.header div.dropdown ::v-deep ul > li > ul {\n  display: table-cell;\n  line-height: 20px;\n  padding-left: 15px;\n}\ndiv.me-card > div.header div.dropdown ::v-deep ul > li > ul > li {\n  float: left;\n}\ndiv.me-card > div.header div.dropdown ::v-deep ul > li > ul > li > a {\n  font-size: 18px;\n  padding: 5px;\n}\ndiv.me-card > div.content {\n  height: 450px;\n  padding: 0;\n}\ndiv.me-card > div.content > div {\n  display: table;\n  height: 100% !important;\n  width: 100%;\n}\ndiv.me-card > div.content > div > div {\n  display: table-cell;\n  vertical-align: middle;\n}\ndiv.me-card > div.content > div > div.feedback, div.me-card > div.content > div > div > h1 {\n  text-align: center;\n}\ndiv.me-card.short > div.content {\n  height: 195px;\n}\n\n/*# sourceMappingURL=AnalyticsCard.vue.map */","<template>\n  <div :class=\"clazz\">\n    <div :class=\"{ short: isShort }\" class=\"me-card\">\n      <div class=\"header\">\n        <label>{{ model.title }}</label>\n        <BDropdown variant=\"link\" right no-caret data-cy=\"analitycsCardOptions\">\n          <template slot=\"button-content\">\n            <Icon icon=\"me-icon icon-bars\" />\n          </template>\n          <BDropdownItem v-if=\"editable\" @click=\"edit\">\n            <Icon icon=\"me-icon icon-pencil\" /> {{ $toolkit.t('analytics.actions.edit') }}\n          </BDropdownItem>\n          <BDropdownItem @click=\"download\">\n            <Icon icon=\"me-icon icon-download\" /> {{ $toolkit.t('analytics.actions.download') }}\n          </BDropdownItem>\n          <BDropdownDivider />\n          <BDropdownGroup :header=\"$toolkit.t('analytics.properties.width')\">\n            <BDropdownItem @click=\"changeWidth(1)\" title=\"1/3\" data-cy=\"analitycsCardWidth\">\n              <Icon icon=\"me-icon icon-layout-left\" />\n            </BDropdownItem>\n            <BDropdownItem @click=\"changeWidth(2)\" title=\"2/3\" data-cy=\"analitycsCardWidth\">\n              <Icon icon=\"me-icon icon-layout-splitted\" />\n            </BDropdownItem>\n            <BDropdownItem @click=\"changeWidth(3)\" title=\"3/3\" data-cy=\"analitycsCardWidth\">\n              <Icon icon=\"me-icon icon-layout-full\" />\n            </BDropdownItem>\n          </BDropdownGroup>\n          <BDropdownGroup :header=\"$toolkit.t('analytics.properties.height')\">\n            <BDropdownItem @click=\"changeHeight(1)\" title=\"1/2\" data-cy=\"analitycsCardHeight\">\n              <Icon icon=\"me-icon icon-layout-splitted-vert\" />\n            </BDropdownItem>\n            <BDropdownItem @click=\"changeHeight(2)\" title=\"2/2\" data-cy=\"analitycsCardHeight\">\n              <Icon icon=\"me-icon icon-layout-full\" />\n            </BDropdownItem>\n          </BDropdownGroup>\n          <BDropdownDivider />\n          <BDropdownItem @click=\"remove\">\n            <Icon icon=\"me-icon icon-trash\" /> {{ $toolkit.t('analytics.actions.remove') }}\n          </BDropdownItem>\n        </BDropdown>\n      </div>\n      <div class=\"content\">\n        <div v-if=\"busy\">\n          <div class=\"feedback\">\n            <Spinner variant=\"primary\" class=\"align-middle\" /> {{ $toolkit.t('spinner.loading') }}\n          </div>\n        </div>\n        <div v-else-if=\"hasResult\">\n          <div v-if=\"model.type.name === 'metric'\">\n            <h1>\n              {{ resultValue | me-number }}\n            </h1>\n          </div>\n          <Chart\n            ref=\"chart\"\n            v-else\n            :type=\"model.type.name\"\n            :show-data-label=\"model.options.showDataLabel\"\n            :show-legend=\"model.options.showLegend\"\n            :height=\"height\"\n            :categories=\"categories\"\n            :category-type=\"categoryType\"\n            :series=\"series\"\n            :stacking=\"stacking\"\n            :formatter=\"formatter\"\n            @selection=\"selection\"\n          />\n        </div>\n        <div v-else>\n          <div class=\"feedback\">\n            <Icon icon=\"me-icon icon-sad-tear\" /> {{ $toolkit.t('analytics.noResults') }}\n          </div>\n        </div>\n      </div>\n    </div>\n    <Modal\n      ref=\"modal\"\n      :title=\"$toolkit.t('analytics.edition')\"\n      size=\"lg\"\n      hide-footer\n    >\n      <AnalyticsForm :item=\"model\" :fields=\"fields\" :disabled-series=\"disabledSeries\" @submit=\"submit\" />\n    </Modal>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport merge from 'lodash/merge'\nimport isEmpty from 'lodash/isEmpty'\nimport { BDropdown, BDropdownItem, BDropdownGroup, BDropdownDivider } from 'bootstrap-vue'\nimport Chart from './Chart.vue'\nimport Modal from './modal/Modal.vue'\nimport Spinner from './Spinner.vue'\nimport AnalyticsForm from './AnalyticsForm.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeAnalyticsCard',\n  components: {\n    BDropdown,\n    BDropdownItem,\n    BDropdownGroup,\n    BDropdownDivider,\n    Chart,\n    Modal,\n    AnalyticsForm,\n    Spinner,\n    Icon\n  },\n  props: {\n    item: {\n      type: Object,\n      required: true\n    },\n    data: {\n      type: Object,\n      default: null\n    },\n    fields: {\n      type: Array,\n      required: true\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    disabledSeries: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    formatter: {\n      type: Function,\n      default: value => value\n    }\n  },\n  data() {\n    return {\n      model: cloneDeep(merge({\n        size: {\n          width: 1,\n          height: 2\n        },\n        options: {\n        }\n      }, this.item))\n    }\n  },\n  computed: {\n    height() {\n      if (this.model.size.height === 1) {\n        return '180px'\n      } else {\n        return '435px'\n      }\n    },\n    clazz() {\n      if (this.model.size.width === 3) {\n        return 'col-lg-12'\n      } else if (this.model.size.width === 2) {\n        return 'col-lg-8'\n      } else {\n        return 'col-lg-4'\n      }\n    },\n    categories() {\n      return this.data.items.map(item => item.value)\n    },\n    series() {\n      const series = []\n      this.item.series.forEach((serie, index) => {\n        const data = this.data.items.map((category) => {\n          return Number(category.items[index].value) || 0\n        })\n        series.push({\n          name: serie.label || `${this.$toolkit.t('analytics.serie')} ${index + 1}`,\n          data: data\n        })\n      })\n      return series\n    },\n    resultValue() {\n      return this.data.value\n    },\n    hasResult() {\n      return this.data && (this.data.value || !isEmpty(this.data.items))\n    },\n    isShort() {\n      return this.model.size.height === 1\n    },\n    stacking() {\n      return this.model.options.stacking ? this.model.options.stacking.name : null\n    },\n    categoryType() {\n      return this.model.operator && this.model.operator.name === 'histogram' &&\n         this.model.field && this.model.field.type === 'date' ? 'datetime' : null\n    }\n  },\n  methods: {\n    changeWidth(width) {\n      this.model.size.width = width\n      this.$emit('resize', this.model)\n      window.dispatchEvent(new Event('resize'))\n    },\n    changeHeight(height) {\n      this.model.size.height = height\n      this.$emit('resize', this.model)\n    },\n    edit() {\n      this.$refs.modal.show()\n    },\n    remove() {\n      this.$emit('remove', this.model)\n    },\n    download() {\n      this.$refs.chart.download()\n    },\n    hide() {\n      this.$refs.modal.hide()\n    },\n    submit(model) {\n      this.hide()\n      this.change(model)\n    },\n    change(model) {\n      this.$emit('change', model)\n    },\n    selection({ categories }) {\n      this.$emit('selection', cloneDeep({\n        key: this.model.key,\n        field: this.model.field,\n        categories\n      }))\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/card.scss\";\n\n  div.me-card {\n    > div.header {\n      padding: 10px 50px 10px 10px;\n\n      > label {\n        display: list-item;\n        line-height: 22px;\n        margin: 0;\n        overflow: hidden;\n        text-overflow: ellipsis;\n        white-space: nowrap;\n      }\n\n      div.dropdown {\n        position: absolute;\n        right: 5px;\n        top: 3px;\n\n        ::v-deep button {\n          color: var(--me-gray-4);\n\n          &:after {\n            content: none;\n          }\n        }\n\n        ::v-deep ul {\n          > li {\n            > ul {\n              display: table-cell;\n              line-height: 20px;\n              padding-left: 15px;\n\n              > li {\n                float: left;\n                > a {\n                  font-size: 18px;\n                  padding: 5px;\n                }\n              }\n            }\n          }\n        }\n      }\n    }\n\n    > div.content {\n      height: 450px;\n      padding: 0;\n\n      > div {\n        display: table;\n        height: 100% !important;\n        width: 100%;\n\n        > div {\n          display: table-cell;\n          vertical-align: middle;\n\n          &.feedback, > h1 {\n            text-align: center;\n          }\n        }\n      }\n    }\n\n    &.short {\n      > div.content {\n        height: 195px;\n      }\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$r = "data-v-9bb5e298";
  /* module identifier */
  const __vue_module_identifier__$r = undefined;
  /* functional template */
  const __vue_is_functional_template__$r = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$r, staticRenderFns: __vue_staticRenderFns__$r },
    __vue_inject_styles__$r,
    __vue_script__$r,
    __vue_scope_id__$r,
    __vue_is_functional_template__$r,
    __vue_module_identifier__$r,
    false,
    createInjector,
    undefined,
    undefined
  );

/**!
 * Sortable 1.10.2
 * @author	RubaXa   <trash@rubaxa.org>
 * @author	owenm    <owen23355@gmail.com>
 * @license MIT
 */
function _typeof$4(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof$4 = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof$4 = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof$4(obj);
}

function _defineProperty$Z(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function _objectSpread$L(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty$Z(target, key, source[key]);
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var version = "1.10.2";

function userAgent$1(pattern) {
  if (typeof window !== 'undefined' && window.navigator) {
    return !!
    /*@__PURE__*/
    navigator.userAgent.match(pattern);
  }
}

var IE11OrLess = userAgent$1(/(?:Trident.*rv[ :]?11\.|msie|iemobile|Windows Phone)/i);
var Edge = userAgent$1(/Edge/i);
var FireFox = userAgent$1(/firefox/i);
var Safari = userAgent$1(/safari/i) && !userAgent$1(/chrome/i) && !userAgent$1(/android/i);
var IOS = userAgent$1(/iP(ad|od|hone)/i);
var ChromeForAndroid = userAgent$1(/chrome/i) && userAgent$1(/android/i);

var captureMode = {
  capture: false,
  passive: false
};

function on(el, event, fn) {
  el.addEventListener(event, fn, !IE11OrLess && captureMode);
}

function off(el, event, fn) {
  el.removeEventListener(event, fn, !IE11OrLess && captureMode);
}

function matches$1(
/**HTMLElement*/
el,
/**String*/
selector) {
  if (!selector) return;
  selector[0] === '>' && (selector = selector.substring(1));

  if (el) {
    try {
      if (el.matches) {
        return el.matches(selector);
      } else if (el.msMatchesSelector) {
        return el.msMatchesSelector(selector);
      } else if (el.webkitMatchesSelector) {
        return el.webkitMatchesSelector(selector);
      }
    } catch (_) {
      return false;
    }
  }

  return false;
}

function getParentOrHost(el) {
  return el.host && el !== document && el.host.nodeType ? el.host : el.parentNode;
}

function closest$1(
/**HTMLElement*/
el,
/**String*/
selector,
/**HTMLElement*/
ctx, includeCTX) {
  if (el) {
    ctx = ctx || document;

    do {
      if (selector != null && (selector[0] === '>' ? el.parentNode === ctx && matches$1(el, selector) : matches$1(el, selector)) || includeCTX && el === ctx) {
        return el;
      }

      if (el === ctx) break;
      /* jshint boss:true */
    } while (el = getParentOrHost(el));
  }

  return null;
}

var R_SPACE = /\s+/g;

function toggleClass(el, name, state) {
  if (el && name) {
    if (el.classList) {
      el.classList[state ? 'add' : 'remove'](name);
    } else {
      var className = (' ' + el.className + ' ').replace(R_SPACE, ' ').replace(' ' + name + ' ', ' ');
      el.className = (className + (state ? ' ' + name : '')).replace(R_SPACE, ' ');
    }
  }
}

function css(el, prop, val) {
  var style = el && el.style;

  if (style) {
    if (val === void 0) {
      if (document.defaultView && document.defaultView.getComputedStyle) {
        val = document.defaultView.getComputedStyle(el, '');
      } else if (el.currentStyle) {
        val = el.currentStyle;
      }

      return prop === void 0 ? val : val[prop];
    } else {
      if (!(prop in style) && prop.indexOf('webkit') === -1) {
        prop = '-webkit-' + prop;
      }

      style[prop] = val + (typeof val === 'string' ? '' : 'px');
    }
  }
}

function matrix(el, selfOnly) {
  var appliedTransforms = '';

  if (typeof el === 'string') {
    appliedTransforms = el;
  } else {
    do {
      var transform = css(el, 'transform');

      if (transform && transform !== 'none') {
        appliedTransforms = transform + ' ' + appliedTransforms;
      }
      /* jshint boss:true */

    } while (!selfOnly && (el = el.parentNode));
  }

  var matrixFn = window.DOMMatrix || window.WebKitCSSMatrix || window.CSSMatrix || window.MSCSSMatrix;
  /*jshint -W056 */

  return matrixFn && new matrixFn(appliedTransforms);
}

function find$2(ctx, tagName, iterator) {
  if (ctx) {
    var list = ctx.getElementsByTagName(tagName),
        i = 0,
        n = list.length;

    if (iterator) {
      for (; i < n; i++) {
        iterator(list[i], i);
      }
    }

    return list;
  }

  return [];
}

function getWindowScrollingElement() {
  var scrollingElement = document.scrollingElement;

  if (scrollingElement) {
    return scrollingElement;
  } else {
    return document.documentElement;
  }
}
/**
 * Returns the "bounding client rect" of given element
 * @param  {HTMLElement} el                       The element whose boundingClientRect is wanted
 * @param  {[Boolean]} relativeToContainingBlock  Whether the rect should be relative to the containing block of (including) the container
 * @param  {[Boolean]} relativeToNonStaticParent  Whether the rect should be relative to the relative parent of (including) the contaienr
 * @param  {[Boolean]} undoScale                  Whether the container's scale() should be undone
 * @param  {[HTMLElement]} container              The parent the element will be placed in
 * @return {Object}                               The boundingClientRect of el, with specified adjustments
 */


function getRect(el, relativeToContainingBlock, relativeToNonStaticParent, undoScale, container) {
  if (!el.getBoundingClientRect && el !== window) return;
  var elRect, top, left, bottom, right, height, width;

  if (el !== window && el !== getWindowScrollingElement()) {
    elRect = el.getBoundingClientRect();
    top = elRect.top;
    left = elRect.left;
    bottom = elRect.bottom;
    right = elRect.right;
    height = elRect.height;
    width = elRect.width;
  } else {
    top = 0;
    left = 0;
    bottom = window.innerHeight;
    right = window.innerWidth;
    height = window.innerHeight;
    width = window.innerWidth;
  }

  if ((relativeToContainingBlock || relativeToNonStaticParent) && el !== window) {
    // Adjust for translate()
    container = container || el.parentNode; // solves #1123 (see: https://stackoverflow.com/a/37953806/6088312)
    // Not needed on <= IE11

    if (!IE11OrLess) {
      do {
        if (container && container.getBoundingClientRect && (css(container, 'transform') !== 'none' || relativeToNonStaticParent && css(container, 'position') !== 'static')) {
          var containerRect = container.getBoundingClientRect(); // Set relative to edges of padding box of container

          top -= containerRect.top + parseInt(css(container, 'border-top-width'));
          left -= containerRect.left + parseInt(css(container, 'border-left-width'));
          bottom = top + elRect.height;
          right = left + elRect.width;
          break;
        }
        /* jshint boss:true */

      } while (container = container.parentNode);
    }
  }

  if (undoScale && el !== window) {
    // Adjust for scale()
    var elMatrix = matrix(container || el),
        scaleX = elMatrix && elMatrix.a,
        scaleY = elMatrix && elMatrix.d;

    if (elMatrix) {
      top /= scaleY;
      left /= scaleX;
      width /= scaleX;
      height /= scaleY;
      bottom = top + height;
      right = left + width;
    }
  }

  return {
    top: top,
    left: left,
    bottom: bottom,
    right: right,
    width: width,
    height: height
  };
}
/**
 * Checks if a side of an element is scrolled past a side of its parents
 * @param  {HTMLElement}  el           The element who's side being scrolled out of view is in question
 * @param  {String}       elSide       Side of the element in question ('top', 'left', 'right', 'bottom')
 * @param  {String}       parentSide   Side of the parent in question ('top', 'left', 'right', 'bottom')
 * @return {HTMLElement}               The parent scroll element that the el's side is scrolled past, or null if there is no such element
 */


function isScrolledPast(el, elSide, parentSide) {
  var parent = getParentAutoScrollElement(el, true),
      elSideVal = getRect(el)[elSide];
  /* jshint boss:true */

  while (parent) {
    var parentSideVal = getRect(parent)[parentSide],
        visible = void 0;

    if (parentSide === 'top' || parentSide === 'left') {
      visible = elSideVal >= parentSideVal;
    } else {
      visible = elSideVal <= parentSideVal;
    }

    if (!visible) return parent;
    if (parent === getWindowScrollingElement()) break;
    parent = getParentAutoScrollElement(parent, false);
  }

  return false;
}
/**
 * Gets nth child of el, ignoring hidden children, sortable's elements (does not ignore clone if it's visible)
 * and non-draggable elements
 * @param  {HTMLElement} el       The parent element
 * @param  {Number} childNum      The index of the child
 * @param  {Object} options       Parent Sortable's options
 * @return {HTMLElement}          The child at index childNum, or null if not found
 */


function getChild(el, childNum, options) {
  var currentChild = 0,
      i = 0,
      children = el.children;

  while (i < children.length) {
    if (children[i].style.display !== 'none' && children[i] !== Sortable.ghost && children[i] !== Sortable.dragged && closest$1(children[i], options.draggable, el, false)) {
      if (currentChild === childNum) {
        return children[i];
      }

      currentChild++;
    }

    i++;
  }

  return null;
}
/**
 * Gets the last child in the el, ignoring ghostEl or invisible elements (clones)
 * @param  {HTMLElement} el       Parent element
 * @param  {selector} selector    Any other elements that should be ignored
 * @return {HTMLElement}          The last child, ignoring ghostEl
 */


function lastChild(el, selector) {
  var last = el.lastElementChild;

  while (last && (last === Sortable.ghost || css(last, 'display') === 'none' || selector && !matches$1(last, selector))) {
    last = last.previousElementSibling;
  }

  return last || null;
}
/**
 * Returns the index of an element within its parent for a selected set of
 * elements
 * @param  {HTMLElement} el
 * @param  {selector} selector
 * @return {number}
 */


function index(el, selector) {
  var index = 0;

  if (!el || !el.parentNode) {
    return -1;
  }
  /* jshint boss:true */


  while (el = el.previousElementSibling) {
    if (el.nodeName.toUpperCase() !== 'TEMPLATE' && el !== Sortable.clone && (!selector || matches$1(el, selector))) {
      index++;
    }
  }

  return index;
}
/**
 * Returns the scroll offset of the given element, added with all the scroll offsets of parent elements.
 * The value is returned in real pixels.
 * @param  {HTMLElement} el
 * @return {Array}             Offsets in the format of [left, top]
 */


function getRelativeScrollOffset(el) {
  var offsetLeft = 0,
      offsetTop = 0,
      winScroller = getWindowScrollingElement();

  if (el) {
    do {
      var elMatrix = matrix(el),
          scaleX = elMatrix.a,
          scaleY = elMatrix.d;
      offsetLeft += el.scrollLeft * scaleX;
      offsetTop += el.scrollTop * scaleY;
    } while (el !== winScroller && (el = el.parentNode));
  }

  return [offsetLeft, offsetTop];
}
/**
 * Returns the index of the object within the given array
 * @param  {Array} arr   Array that may or may not hold the object
 * @param  {Object} obj  An object that has a key-value pair unique to and identical to a key-value pair in the object you want to find
 * @return {Number}      The index of the object in the array, or -1
 */


function indexOfObject(arr, obj) {
  for (var i in arr) {
    if (!arr.hasOwnProperty(i)) continue;

    for (var key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] === arr[i][key]) return Number(i);
    }
  }

  return -1;
}

function getParentAutoScrollElement(el, includeSelf) {
  // skip to window
  if (!el || !el.getBoundingClientRect) return getWindowScrollingElement();
  var elem = el;
  var gotSelf = false;

  do {
    // we don't need to get elem css if it isn't even overflowing in the first place (performance)
    if (elem.clientWidth < elem.scrollWidth || elem.clientHeight < elem.scrollHeight) {
      var elemCSS = css(elem);

      if (elem.clientWidth < elem.scrollWidth && (elemCSS.overflowX == 'auto' || elemCSS.overflowX == 'scroll') || elem.clientHeight < elem.scrollHeight && (elemCSS.overflowY == 'auto' || elemCSS.overflowY == 'scroll')) {
        if (!elem.getBoundingClientRect || elem === document.body) return getWindowScrollingElement();
        if (gotSelf || includeSelf) return elem;
        gotSelf = true;
      }
    }
    /* jshint boss:true */

  } while (elem = elem.parentNode);

  return getWindowScrollingElement();
}

function extend(dst, src) {
  if (dst && src) {
    for (var key in src) {
      if (src.hasOwnProperty(key)) {
        dst[key] = src[key];
      }
    }
  }

  return dst;
}

function isRectEqual(rect1, rect2) {
  return Math.round(rect1.top) === Math.round(rect2.top) && Math.round(rect1.left) === Math.round(rect2.left) && Math.round(rect1.height) === Math.round(rect2.height) && Math.round(rect1.width) === Math.round(rect2.width);
}

var _throttleTimeout;

function throttle(callback, ms) {
  return function () {
    if (!_throttleTimeout) {
      var args = arguments,
          _this = this;

      if (args.length === 1) {
        callback.call(_this, args[0]);
      } else {
        callback.apply(_this, args);
      }

      _throttleTimeout = setTimeout(function () {
        _throttleTimeout = void 0;
      }, ms);
    }
  };
}

function cancelThrottle() {
  clearTimeout(_throttleTimeout);
  _throttleTimeout = void 0;
}

function scrollBy(el, x, y) {
  el.scrollLeft += x;
  el.scrollTop += y;
}

function clone$1(el) {
  var Polymer = window.Polymer;
  var $ = window.jQuery || window.Zepto;

  if (Polymer && Polymer.dom) {
    return Polymer.dom(el).cloneNode(true);
  } else if ($) {
    return $(el).clone(true)[0];
  } else {
    return el.cloneNode(true);
  }
}

var expando = 'Sortable' + new Date().getTime();

function AnimationStateManager() {
  var animationStates = [],
      animationCallbackId;
  return {
    captureAnimationState: function captureAnimationState() {
      animationStates = [];
      if (!this.options.animation) return;
      var children = [].slice.call(this.el.children);
      children.forEach(function (child) {
        if (css(child, 'display') === 'none' || child === Sortable.ghost) return;
        animationStates.push({
          target: child,
          rect: getRect(child)
        });

        var fromRect = _objectSpread$L({}, animationStates[animationStates.length - 1].rect); // If animating: compensate for current animation


        if (child.thisAnimationDuration) {
          var childMatrix = matrix(child, true);

          if (childMatrix) {
            fromRect.top -= childMatrix.f;
            fromRect.left -= childMatrix.e;
          }
        }

        child.fromRect = fromRect;
      });
    },
    addAnimationState: function addAnimationState(state) {
      animationStates.push(state);
    },
    removeAnimationState: function removeAnimationState(target) {
      animationStates.splice(indexOfObject(animationStates, {
        target: target
      }), 1);
    },
    animateAll: function animateAll(callback) {
      var _this = this;

      if (!this.options.animation) {
        clearTimeout(animationCallbackId);
        if (typeof callback === 'function') callback();
        return;
      }

      var animating = false,
          animationTime = 0;
      animationStates.forEach(function (state) {
        var time = 0,
            target = state.target,
            fromRect = target.fromRect,
            toRect = getRect(target),
            prevFromRect = target.prevFromRect,
            prevToRect = target.prevToRect,
            animatingRect = state.rect,
            targetMatrix = matrix(target, true);

        if (targetMatrix) {
          // Compensate for current animation
          toRect.top -= targetMatrix.f;
          toRect.left -= targetMatrix.e;
        }

        target.toRect = toRect;

        if (target.thisAnimationDuration) {
          // Could also check if animatingRect is between fromRect and toRect
          if (isRectEqual(prevFromRect, toRect) && !isRectEqual(fromRect, toRect) && // Make sure animatingRect is on line between toRect & fromRect
          (animatingRect.top - toRect.top) / (animatingRect.left - toRect.left) === (fromRect.top - toRect.top) / (fromRect.left - toRect.left)) {
            // If returning to same place as started from animation and on same axis
            time = calculateRealTime(animatingRect, prevFromRect, prevToRect, _this.options);
          }
        } // if fromRect != toRect: animate


        if (!isRectEqual(toRect, fromRect)) {
          target.prevFromRect = fromRect;
          target.prevToRect = toRect;

          if (!time) {
            time = _this.options.animation;
          }

          _this.animate(target, animatingRect, toRect, time);
        }

        if (time) {
          animating = true;
          animationTime = Math.max(animationTime, time);
          clearTimeout(target.animationResetTimer);
          target.animationResetTimer = setTimeout(function () {
            target.animationTime = 0;
            target.prevFromRect = null;
            target.fromRect = null;
            target.prevToRect = null;
            target.thisAnimationDuration = null;
          }, time);
          target.thisAnimationDuration = time;
        }
      });
      clearTimeout(animationCallbackId);

      if (!animating) {
        if (typeof callback === 'function') callback();
      } else {
        animationCallbackId = setTimeout(function () {
          if (typeof callback === 'function') callback();
        }, animationTime);
      }

      animationStates = [];
    },
    animate: function animate(target, currentRect, toRect, duration) {
      if (duration) {
        css(target, 'transition', '');
        css(target, 'transform', '');
        var elMatrix = matrix(this.el),
            scaleX = elMatrix && elMatrix.a,
            scaleY = elMatrix && elMatrix.d,
            translateX = (currentRect.left - toRect.left) / (scaleX || 1),
            translateY = (currentRect.top - toRect.top) / (scaleY || 1);
        target.animatingX = !!translateX;
        target.animatingY = !!translateY;
        css(target, 'transform', 'translate3d(' + translateX + 'px,' + translateY + 'px,0)');
        repaint(target); // repaint

        css(target, 'transition', 'transform ' + duration + 'ms' + (this.options.easing ? ' ' + this.options.easing : ''));
        css(target, 'transform', 'translate3d(0,0,0)');
        typeof target.animated === 'number' && clearTimeout(target.animated);
        target.animated = setTimeout(function () {
          css(target, 'transition', '');
          css(target, 'transform', '');
          target.animated = false;
          target.animatingX = false;
          target.animatingY = false;
        }, duration);
      }
    }
  };
}

function repaint(target) {
  return target.offsetWidth;
}

function calculateRealTime(animatingRect, fromRect, toRect, options) {
  return Math.sqrt(Math.pow(fromRect.top - animatingRect.top, 2) + Math.pow(fromRect.left - animatingRect.left, 2)) / Math.sqrt(Math.pow(fromRect.top - toRect.top, 2) + Math.pow(fromRect.left - toRect.left, 2)) * options.animation;
}

var plugins = [];
var defaults = {
  initializeByDefault: true
};
var PluginManager = {
  mount: function mount(plugin) {
    // Set default static properties
    for (var option in defaults) {
      if (defaults.hasOwnProperty(option) && !(option in plugin)) {
        plugin[option] = defaults[option];
      }
    }

    plugins.push(plugin);
  },
  pluginEvent: function pluginEvent(eventName, sortable, evt) {
    var _this = this;

    this.eventCanceled = false;

    evt.cancel = function () {
      _this.eventCanceled = true;
    };

    var eventNameGlobal = eventName + 'Global';
    plugins.forEach(function (plugin) {
      if (!sortable[plugin.pluginName]) return; // Fire global events if it exists in this sortable

      if (sortable[plugin.pluginName][eventNameGlobal]) {
        sortable[plugin.pluginName][eventNameGlobal](_objectSpread$L({
          sortable: sortable
        }, evt));
      } // Only fire plugin event if plugin is enabled in this sortable,
      // and plugin has event defined


      if (sortable.options[plugin.pluginName] && sortable[plugin.pluginName][eventName]) {
        sortable[plugin.pluginName][eventName](_objectSpread$L({
          sortable: sortable
        }, evt));
      }
    });
  },
  initializePlugins: function initializePlugins(sortable, el, defaults, options) {
    plugins.forEach(function (plugin) {
      var pluginName = plugin.pluginName;
      if (!sortable.options[pluginName] && !plugin.initializeByDefault) return;
      var initialized = new plugin(sortable, el, sortable.options);
      initialized.sortable = sortable;
      initialized.options = sortable.options;
      sortable[pluginName] = initialized; // Add default options from plugin

      _extends$1(defaults, initialized.defaults);
    });

    for (var option in sortable.options) {
      if (!sortable.options.hasOwnProperty(option)) continue;
      var modified = this.modifyOption(sortable, option, sortable.options[option]);

      if (typeof modified !== 'undefined') {
        sortable.options[option] = modified;
      }
    }
  },
  getEventProperties: function getEventProperties(name, sortable) {
    var eventProperties = {};
    plugins.forEach(function (plugin) {
      if (typeof plugin.eventProperties !== 'function') return;

      _extends$1(eventProperties, plugin.eventProperties.call(sortable[plugin.pluginName], name));
    });
    return eventProperties;
  },
  modifyOption: function modifyOption(sortable, name, value) {
    var modifiedValue;
    plugins.forEach(function (plugin) {
      // Plugin must exist on the Sortable
      if (!sortable[plugin.pluginName]) return; // If static option listener exists for this option, call in the context of the Sortable's instance of this plugin

      if (plugin.optionListeners && typeof plugin.optionListeners[name] === 'function') {
        modifiedValue = plugin.optionListeners[name].call(sortable[plugin.pluginName], value);
      }
    });
    return modifiedValue;
  }
};

function dispatchEvent(_ref) {
  var sortable = _ref.sortable,
      rootEl = _ref.rootEl,
      name = _ref.name,
      targetEl = _ref.targetEl,
      cloneEl = _ref.cloneEl,
      toEl = _ref.toEl,
      fromEl = _ref.fromEl,
      oldIndex = _ref.oldIndex,
      newIndex = _ref.newIndex,
      oldDraggableIndex = _ref.oldDraggableIndex,
      newDraggableIndex = _ref.newDraggableIndex,
      originalEvent = _ref.originalEvent,
      putSortable = _ref.putSortable,
      extraEventProperties = _ref.extraEventProperties;
  sortable = sortable || rootEl && rootEl[expando];
  if (!sortable) return;
  var evt,
      options = sortable.options,
      onName = 'on' + name.charAt(0).toUpperCase() + name.substr(1); // Support for new CustomEvent feature

  if (window.CustomEvent && !IE11OrLess && !Edge) {
    evt = new CustomEvent(name, {
      bubbles: true,
      cancelable: true
    });
  } else {
    evt = document.createEvent('Event');
    evt.initEvent(name, true, true);
  }

  evt.to = toEl || rootEl;
  evt.from = fromEl || rootEl;
  evt.item = targetEl || rootEl;
  evt.clone = cloneEl;
  evt.oldIndex = oldIndex;
  evt.newIndex = newIndex;
  evt.oldDraggableIndex = oldDraggableIndex;
  evt.newDraggableIndex = newDraggableIndex;
  evt.originalEvent = originalEvent;
  evt.pullMode = putSortable ? putSortable.lastPutMode : undefined;

  var allEventProperties = _objectSpread$L({}, extraEventProperties, PluginManager.getEventProperties(name, sortable));

  for (var option in allEventProperties) {
    evt[option] = allEventProperties[option];
  }

  if (rootEl) {
    rootEl.dispatchEvent(evt);
  }

  if (options[onName]) {
    options[onName].call(sortable, evt);
  }
}

var pluginEvent = function pluginEvent(eventName, sortable) {
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      originalEvent = _ref.evt,
      data = _objectWithoutProperties(_ref, ["evt"]);

  PluginManager.pluginEvent.bind(Sortable)(eventName, sortable, _objectSpread$L({
    dragEl: dragEl,
    parentEl: parentEl,
    ghostEl: ghostEl,
    rootEl: rootEl,
    nextEl: nextEl,
    lastDownEl: lastDownEl,
    cloneEl: cloneEl,
    cloneHidden: cloneHidden,
    dragStarted: moved,
    putSortable: putSortable,
    activeSortable: Sortable.active,
    originalEvent: originalEvent,
    oldIndex: oldIndex,
    oldDraggableIndex: oldDraggableIndex,
    newIndex: newIndex,
    newDraggableIndex: newDraggableIndex,
    hideGhostForTarget: _hideGhostForTarget,
    unhideGhostForTarget: _unhideGhostForTarget,
    cloneNowHidden: function cloneNowHidden() {
      cloneHidden = true;
    },
    cloneNowShown: function cloneNowShown() {
      cloneHidden = false;
    },
    dispatchSortableEvent: function dispatchSortableEvent(name) {
      _dispatchEvent({
        sortable: sortable,
        name: name,
        originalEvent: originalEvent
      });
    }
  }, data));
};

function _dispatchEvent(info) {
  dispatchEvent(_objectSpread$L({
    putSortable: putSortable,
    cloneEl: cloneEl,
    targetEl: dragEl,
    rootEl: rootEl,
    oldIndex: oldIndex,
    oldDraggableIndex: oldDraggableIndex,
    newIndex: newIndex,
    newDraggableIndex: newDraggableIndex
  }, info));
}

var dragEl,
    parentEl,
    ghostEl,
    rootEl,
    nextEl,
    lastDownEl,
    cloneEl,
    cloneHidden,
    oldIndex,
    newIndex,
    oldDraggableIndex,
    newDraggableIndex,
    activeGroup,
    putSortable,
    awaitingDragStarted = false,
    ignoreNextClick = false,
    sortables = [],
    tapEvt,
    touchEvt,
    lastDx,
    lastDy,
    tapDistanceLeft,
    tapDistanceTop,
    moved,
    lastTarget,
    lastDirection,
    pastFirstInvertThresh = false,
    isCircumstantialInvert = false,
    targetMoveDistance,
    // For positioning ghost absolutely
ghostRelativeParent,
    ghostRelativeParentInitialScroll = [],
    // (left, top)
_silent = false,
    savedInputChecked = [];
/** @const */

var documentExists = typeof document !== 'undefined',
    PositionGhostAbsolutely = IOS,
    CSSFloatProperty = Edge || IE11OrLess ? 'cssFloat' : 'float',
    // This will not pass for IE9, because IE9 DnD only works on anchors
supportDraggable = documentExists && !ChromeForAndroid && !IOS && 'draggable' in document.createElement('div'),
    supportCssPointerEvents = function () {
  if (!documentExists) return; // false when <= IE11

  if (IE11OrLess) {
    return false;
  }

  var el = document.createElement('x');
  el.style.cssText = 'pointer-events:auto';
  return el.style.pointerEvents === 'auto';
}(),
    _detectDirection = function _detectDirection(el, options) {
  var elCSS = css(el),
      elWidth = parseInt(elCSS.width) - parseInt(elCSS.paddingLeft) - parseInt(elCSS.paddingRight) - parseInt(elCSS.borderLeftWidth) - parseInt(elCSS.borderRightWidth),
      child1 = getChild(el, 0, options),
      child2 = getChild(el, 1, options),
      firstChildCSS = child1 && css(child1),
      secondChildCSS = child2 && css(child2),
      firstChildWidth = firstChildCSS && parseInt(firstChildCSS.marginLeft) + parseInt(firstChildCSS.marginRight) + getRect(child1).width,
      secondChildWidth = secondChildCSS && parseInt(secondChildCSS.marginLeft) + parseInt(secondChildCSS.marginRight) + getRect(child2).width;

  if (elCSS.display === 'flex') {
    return elCSS.flexDirection === 'column' || elCSS.flexDirection === 'column-reverse' ? 'vertical' : 'horizontal';
  }

  if (elCSS.display === 'grid') {
    return elCSS.gridTemplateColumns.split(' ').length <= 1 ? 'vertical' : 'horizontal';
  }

  if (child1 && firstChildCSS["float"] && firstChildCSS["float"] !== 'none') {
    var touchingSideChild2 = firstChildCSS["float"] === 'left' ? 'left' : 'right';
    return child2 && (secondChildCSS.clear === 'both' || secondChildCSS.clear === touchingSideChild2) ? 'vertical' : 'horizontal';
  }

  return child1 && (firstChildCSS.display === 'block' || firstChildCSS.display === 'flex' || firstChildCSS.display === 'table' || firstChildCSS.display === 'grid' || firstChildWidth >= elWidth && elCSS[CSSFloatProperty] === 'none' || child2 && elCSS[CSSFloatProperty] === 'none' && firstChildWidth + secondChildWidth > elWidth) ? 'vertical' : 'horizontal';
},
    _dragElInRowColumn = function _dragElInRowColumn(dragRect, targetRect, vertical) {
  var dragElS1Opp = vertical ? dragRect.left : dragRect.top,
      dragElS2Opp = vertical ? dragRect.right : dragRect.bottom,
      dragElOppLength = vertical ? dragRect.width : dragRect.height,
      targetS1Opp = vertical ? targetRect.left : targetRect.top,
      targetS2Opp = vertical ? targetRect.right : targetRect.bottom,
      targetOppLength = vertical ? targetRect.width : targetRect.height;
  return dragElS1Opp === targetS1Opp || dragElS2Opp === targetS2Opp || dragElS1Opp + dragElOppLength / 2 === targetS1Opp + targetOppLength / 2;
},

/**
 * Detects first nearest empty sortable to X and Y position using emptyInsertThreshold.
 * @param  {Number} x      X position
 * @param  {Number} y      Y position
 * @return {HTMLElement}   Element of the first found nearest Sortable
 */
_detectNearestEmptySortable = function _detectNearestEmptySortable(x, y) {
  var ret;
  sortables.some(function (sortable) {
    if (lastChild(sortable)) return;
    var rect = getRect(sortable),
        threshold = sortable[expando].options.emptyInsertThreshold,
        insideHorizontally = x >= rect.left - threshold && x <= rect.right + threshold,
        insideVertically = y >= rect.top - threshold && y <= rect.bottom + threshold;

    if (threshold && insideHorizontally && insideVertically) {
      return ret = sortable;
    }
  });
  return ret;
},
    _prepareGroup = function _prepareGroup(options) {
  function toFn(value, pull) {
    return function (to, from, dragEl, evt) {
      var sameGroup = to.options.group.name && from.options.group.name && to.options.group.name === from.options.group.name;

      if (value == null && (pull || sameGroup)) {
        // Default pull value
        // Default pull and put value if same group
        return true;
      } else if (value == null || value === false) {
        return false;
      } else if (pull && value === 'clone') {
        return value;
      } else if (typeof value === 'function') {
        return toFn(value(to, from, dragEl, evt), pull)(to, from, dragEl, evt);
      } else {
        var otherGroup = (pull ? to : from).options.group.name;
        return value === true || typeof value === 'string' && value === otherGroup || value.join && value.indexOf(otherGroup) > -1;
      }
    };
  }

  var group = {};
  var originalGroup = options.group;

  if (!originalGroup || _typeof$4(originalGroup) != 'object') {
    originalGroup = {
      name: originalGroup
    };
  }

  group.name = originalGroup.name;
  group.checkPull = toFn(originalGroup.pull, true);
  group.checkPut = toFn(originalGroup.put);
  group.revertClone = originalGroup.revertClone;
  options.group = group;
},
    _hideGhostForTarget = function _hideGhostForTarget() {
  if (!supportCssPointerEvents && ghostEl) {
    css(ghostEl, 'display', 'none');
  }
},
    _unhideGhostForTarget = function _unhideGhostForTarget() {
  if (!supportCssPointerEvents && ghostEl) {
    css(ghostEl, 'display', '');
  }
}; // #1184 fix - Prevent click event on fallback if dragged but item not changed position


if (documentExists) {
  document.addEventListener('click', function (evt) {
    if (ignoreNextClick) {
      evt.preventDefault();
      evt.stopPropagation && evt.stopPropagation();
      evt.stopImmediatePropagation && evt.stopImmediatePropagation();
      ignoreNextClick = false;
      return false;
    }
  }, true);
}

var nearestEmptyInsertDetectEvent = function nearestEmptyInsertDetectEvent(evt) {
  if (dragEl) {
    evt = evt.touches ? evt.touches[0] : evt;

    var nearest = _detectNearestEmptySortable(evt.clientX, evt.clientY);

    if (nearest) {
      // Create imitation event
      var event = {};

      for (var i in evt) {
        if (evt.hasOwnProperty(i)) {
          event[i] = evt[i];
        }
      }

      event.target = event.rootEl = nearest;
      event.preventDefault = void 0;
      event.stopPropagation = void 0;

      nearest[expando]._onDragOver(event);
    }
  }
};

var _checkOutsideTargetEl = function _checkOutsideTargetEl(evt) {
  if (dragEl) {
    dragEl.parentNode[expando]._isOutsideThisEl(evt.target);
  }
};
/**
 * @class  Sortable
 * @param  {HTMLElement}  el
 * @param  {Object}       [options]
 */


function Sortable(el, options) {
  if (!(el && el.nodeType && el.nodeType === 1)) {
    throw "Sortable: `el` must be an HTMLElement, not ".concat({}.toString.call(el));
  }

  this.el = el; // root element

  this.options = options = _extends$1({}, options); // Export instance

  el[expando] = this;
  var defaults = {
    group: null,
    sort: true,
    disabled: false,
    store: null,
    handle: null,
    draggable: /^[uo]l$/i.test(el.nodeName) ? '>li' : '>*',
    swapThreshold: 1,
    // percentage; 0 <= x <= 1
    invertSwap: false,
    // invert always
    invertedSwapThreshold: null,
    // will be set to same as swapThreshold if default
    removeCloneOnHide: true,
    direction: function direction() {
      return _detectDirection(el, this.options);
    },
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    ignore: 'a, img',
    filter: null,
    preventOnFilter: true,
    animation: 0,
    easing: null,
    setData: function setData(dataTransfer, dragEl) {
      dataTransfer.setData('Text', dragEl.textContent);
    },
    dropBubble: false,
    dragoverBubble: false,
    dataIdAttr: 'data-id',
    delay: 0,
    delayOnTouchOnly: false,
    touchStartThreshold: (Number.parseInt ? Number : window).parseInt(window.devicePixelRatio, 10) || 1,
    forceFallback: false,
    fallbackClass: 'sortable-fallback',
    fallbackOnBody: false,
    fallbackTolerance: 0,
    fallbackOffset: {
      x: 0,
      y: 0
    },
    supportPointer: Sortable.supportPointer !== false && 'PointerEvent' in window,
    emptyInsertThreshold: 5
  };
  PluginManager.initializePlugins(this, el, defaults); // Set default options

  for (var name in defaults) {
    !(name in options) && (options[name] = defaults[name]);
  }

  _prepareGroup(options); // Bind all private methods


  for (var fn in this) {
    if (fn.charAt(0) === '_' && typeof this[fn] === 'function') {
      this[fn] = this[fn].bind(this);
    }
  } // Setup drag mode


  this.nativeDraggable = options.forceFallback ? false : supportDraggable;

  if (this.nativeDraggable) {
    // Touch start threshold cannot be greater than the native dragstart threshold
    this.options.touchStartThreshold = 1;
  } // Bind events


  if (options.supportPointer) {
    on(el, 'pointerdown', this._onTapStart);
  } else {
    on(el, 'mousedown', this._onTapStart);
    on(el, 'touchstart', this._onTapStart);
  }

  if (this.nativeDraggable) {
    on(el, 'dragover', this);
    on(el, 'dragenter', this);
  }

  sortables.push(this.el); // Restore sorting

  options.store && options.store.get && this.sort(options.store.get(this) || []); // Add animation state manager

  _extends$1(this, AnimationStateManager());
}

Sortable.prototype =
/** @lends Sortable.prototype */
{
  constructor: Sortable,
  _isOutsideThisEl: function _isOutsideThisEl(target) {
    if (!this.el.contains(target) && target !== this.el) {
      lastTarget = null;
    }
  },
  _getDirection: function _getDirection(evt, target) {
    return typeof this.options.direction === 'function' ? this.options.direction.call(this, evt, target, dragEl) : this.options.direction;
  },
  _onTapStart: function _onTapStart(
  /** Event|TouchEvent */
  evt) {
    if (!evt.cancelable) return;

    var _this = this,
        el = this.el,
        options = this.options,
        preventOnFilter = options.preventOnFilter,
        type = evt.type,
        touch = evt.touches && evt.touches[0] || evt.pointerType && evt.pointerType === 'touch' && evt,
        target = (touch || evt).target,
        originalTarget = evt.target.shadowRoot && (evt.path && evt.path[0] || evt.composedPath && evt.composedPath()[0]) || target,
        filter = options.filter;

    _saveInputCheckedState(el); // Don't trigger start event when an element is been dragged, otherwise the evt.oldindex always wrong when set option.group.


    if (dragEl) {
      return;
    }

    if (/mousedown|pointerdown/.test(type) && evt.button !== 0 || options.disabled) {
      return; // only left button and enabled
    } // cancel dnd if original target is content editable


    if (originalTarget.isContentEditable) {
      return;
    }

    target = closest$1(target, options.draggable, el, false);

    if (target && target.animated) {
      return;
    }

    if (lastDownEl === target) {
      // Ignoring duplicate `down`
      return;
    } // Get the index of the dragged element within its parent


    oldIndex = index(target);
    oldDraggableIndex = index(target, options.draggable); // Check filter

    if (typeof filter === 'function') {
      if (filter.call(this, evt, target, this)) {
        _dispatchEvent({
          sortable: _this,
          rootEl: originalTarget,
          name: 'filter',
          targetEl: target,
          toEl: el,
          fromEl: el
        });

        pluginEvent('filter', _this, {
          evt: evt
        });
        preventOnFilter && evt.cancelable && evt.preventDefault();
        return; // cancel dnd
      }
    } else if (filter) {
      filter = filter.split(',').some(function (criteria) {
        criteria = closest$1(originalTarget, criteria.trim(), el, false);

        if (criteria) {
          _dispatchEvent({
            sortable: _this,
            rootEl: criteria,
            name: 'filter',
            targetEl: target,
            fromEl: el,
            toEl: el
          });

          pluginEvent('filter', _this, {
            evt: evt
          });
          return true;
        }
      });

      if (filter) {
        preventOnFilter && evt.cancelable && evt.preventDefault();
        return; // cancel dnd
      }
    }

    if (options.handle && !closest$1(originalTarget, options.handle, el, false)) {
      return;
    } // Prepare `dragstart`


    this._prepareDragStart(evt, touch, target);
  },
  _prepareDragStart: function _prepareDragStart(
  /** Event */
  evt,
  /** Touch */
  touch,
  /** HTMLElement */
  target) {
    var _this = this,
        el = _this.el,
        options = _this.options,
        ownerDocument = el.ownerDocument,
        dragStartFn;

    if (target && !dragEl && target.parentNode === el) {
      var dragRect = getRect(target);
      rootEl = el;
      dragEl = target;
      parentEl = dragEl.parentNode;
      nextEl = dragEl.nextSibling;
      lastDownEl = target;
      activeGroup = options.group;
      Sortable.dragged = dragEl;
      tapEvt = {
        target: dragEl,
        clientX: (touch || evt).clientX,
        clientY: (touch || evt).clientY
      };
      tapDistanceLeft = tapEvt.clientX - dragRect.left;
      tapDistanceTop = tapEvt.clientY - dragRect.top;
      this._lastX = (touch || evt).clientX;
      this._lastY = (touch || evt).clientY;
      dragEl.style['will-change'] = 'all';

      dragStartFn = function dragStartFn() {
        pluginEvent('delayEnded', _this, {
          evt: evt
        });

        if (Sortable.eventCanceled) {
          _this._onDrop();

          return;
        } // Delayed drag has been triggered
        // we can re-enable the events: touchmove/mousemove


        _this._disableDelayedDragEvents();

        if (!FireFox && _this.nativeDraggable) {
          dragEl.draggable = true;
        } // Bind the events: dragstart/dragend


        _this._triggerDragStart(evt, touch); // Drag start event


        _dispatchEvent({
          sortable: _this,
          name: 'choose',
          originalEvent: evt
        }); // Chosen item


        toggleClass(dragEl, options.chosenClass, true);
      }; // Disable "draggable"


      options.ignore.split(',').forEach(function (criteria) {
        find$2(dragEl, criteria.trim(), _disableDraggable);
      });
      on(ownerDocument, 'dragover', nearestEmptyInsertDetectEvent);
      on(ownerDocument, 'mousemove', nearestEmptyInsertDetectEvent);
      on(ownerDocument, 'touchmove', nearestEmptyInsertDetectEvent);
      on(ownerDocument, 'mouseup', _this._onDrop);
      on(ownerDocument, 'touchend', _this._onDrop);
      on(ownerDocument, 'touchcancel', _this._onDrop); // Make dragEl draggable (must be before delay for FireFox)

      if (FireFox && this.nativeDraggable) {
        this.options.touchStartThreshold = 4;
        dragEl.draggable = true;
      }

      pluginEvent('delayStart', this, {
        evt: evt
      }); // Delay is impossible for native DnD in Edge or IE

      if (options.delay && (!options.delayOnTouchOnly || touch) && (!this.nativeDraggable || !(Edge || IE11OrLess))) {
        if (Sortable.eventCanceled) {
          this._onDrop();

          return;
        } // If the user moves the pointer or let go the click or touch
        // before the delay has been reached:
        // disable the delayed drag


        on(ownerDocument, 'mouseup', _this._disableDelayedDrag);
        on(ownerDocument, 'touchend', _this._disableDelayedDrag);
        on(ownerDocument, 'touchcancel', _this._disableDelayedDrag);
        on(ownerDocument, 'mousemove', _this._delayedDragTouchMoveHandler);
        on(ownerDocument, 'touchmove', _this._delayedDragTouchMoveHandler);
        options.supportPointer && on(ownerDocument, 'pointermove', _this._delayedDragTouchMoveHandler);
        _this._dragStartTimer = setTimeout(dragStartFn, options.delay);
      } else {
        dragStartFn();
      }
    }
  },
  _delayedDragTouchMoveHandler: function _delayedDragTouchMoveHandler(
  /** TouchEvent|PointerEvent **/
  e) {
    var touch = e.touches ? e.touches[0] : e;

    if (Math.max(Math.abs(touch.clientX - this._lastX), Math.abs(touch.clientY - this._lastY)) >= Math.floor(this.options.touchStartThreshold / (this.nativeDraggable && window.devicePixelRatio || 1))) {
      this._disableDelayedDrag();
    }
  },
  _disableDelayedDrag: function _disableDelayedDrag() {
    dragEl && _disableDraggable(dragEl);
    clearTimeout(this._dragStartTimer);

    this._disableDelayedDragEvents();
  },
  _disableDelayedDragEvents: function _disableDelayedDragEvents() {
    var ownerDocument = this.el.ownerDocument;
    off(ownerDocument, 'mouseup', this._disableDelayedDrag);
    off(ownerDocument, 'touchend', this._disableDelayedDrag);
    off(ownerDocument, 'touchcancel', this._disableDelayedDrag);
    off(ownerDocument, 'mousemove', this._delayedDragTouchMoveHandler);
    off(ownerDocument, 'touchmove', this._delayedDragTouchMoveHandler);
    off(ownerDocument, 'pointermove', this._delayedDragTouchMoveHandler);
  },
  _triggerDragStart: function _triggerDragStart(
  /** Event */
  evt,
  /** Touch */
  touch) {
    touch = touch || evt.pointerType == 'touch' && evt;

    if (!this.nativeDraggable || touch) {
      if (this.options.supportPointer) {
        on(document, 'pointermove', this._onTouchMove);
      } else if (touch) {
        on(document, 'touchmove', this._onTouchMove);
      } else {
        on(document, 'mousemove', this._onTouchMove);
      }
    } else {
      on(dragEl, 'dragend', this);
      on(rootEl, 'dragstart', this._onDragStart);
    }

    try {
      if (document.selection) {
        // Timeout neccessary for IE9
        _nextTick(function () {
          document.selection.empty();
        });
      } else {
        window.getSelection().removeAllRanges();
      }
    } catch (err) {}
  },
  _dragStarted: function _dragStarted(fallback, evt) {

    awaitingDragStarted = false;

    if (rootEl && dragEl) {
      pluginEvent('dragStarted', this, {
        evt: evt
      });

      if (this.nativeDraggable) {
        on(document, 'dragover', _checkOutsideTargetEl);
      }

      var options = this.options; // Apply effect

      !fallback && toggleClass(dragEl, options.dragClass, false);
      toggleClass(dragEl, options.ghostClass, true);
      Sortable.active = this;
      fallback && this._appendGhost(); // Drag start event

      _dispatchEvent({
        sortable: this,
        name: 'start',
        originalEvent: evt
      });
    } else {
      this._nulling();
    }
  },
  _emulateDragOver: function _emulateDragOver() {
    if (touchEvt) {
      this._lastX = touchEvt.clientX;
      this._lastY = touchEvt.clientY;

      _hideGhostForTarget();

      var target = document.elementFromPoint(touchEvt.clientX, touchEvt.clientY);
      var parent = target;

      while (target && target.shadowRoot) {
        target = target.shadowRoot.elementFromPoint(touchEvt.clientX, touchEvt.clientY);
        if (target === parent) break;
        parent = target;
      }

      dragEl.parentNode[expando]._isOutsideThisEl(target);

      if (parent) {
        do {
          if (parent[expando]) {
            var inserted = void 0;
            inserted = parent[expando]._onDragOver({
              clientX: touchEvt.clientX,
              clientY: touchEvt.clientY,
              target: target,
              rootEl: parent
            });

            if (inserted && !this.options.dragoverBubble) {
              break;
            }
          }

          target = parent; // store last element
        }
        /* jshint boss:true */
        while (parent = parent.parentNode);
      }

      _unhideGhostForTarget();
    }
  },
  _onTouchMove: function _onTouchMove(
  /**TouchEvent*/
  evt) {
    if (tapEvt) {
      var options = this.options,
          fallbackTolerance = options.fallbackTolerance,
          fallbackOffset = options.fallbackOffset,
          touch = evt.touches ? evt.touches[0] : evt,
          ghostMatrix = ghostEl && matrix(ghostEl, true),
          scaleX = ghostEl && ghostMatrix && ghostMatrix.a,
          scaleY = ghostEl && ghostMatrix && ghostMatrix.d,
          relativeScrollOffset = PositionGhostAbsolutely && ghostRelativeParent && getRelativeScrollOffset(ghostRelativeParent),
          dx = (touch.clientX - tapEvt.clientX + fallbackOffset.x) / (scaleX || 1) + (relativeScrollOffset ? relativeScrollOffset[0] - ghostRelativeParentInitialScroll[0] : 0) / (scaleX || 1),
          dy = (touch.clientY - tapEvt.clientY + fallbackOffset.y) / (scaleY || 1) + (relativeScrollOffset ? relativeScrollOffset[1] - ghostRelativeParentInitialScroll[1] : 0) / (scaleY || 1); // only set the status to dragging, when we are actually dragging

      if (!Sortable.active && !awaitingDragStarted) {
        if (fallbackTolerance && Math.max(Math.abs(touch.clientX - this._lastX), Math.abs(touch.clientY - this._lastY)) < fallbackTolerance) {
          return;
        }

        this._onDragStart(evt, true);
      }

      if (ghostEl) {
        if (ghostMatrix) {
          ghostMatrix.e += dx - (lastDx || 0);
          ghostMatrix.f += dy - (lastDy || 0);
        } else {
          ghostMatrix = {
            a: 1,
            b: 0,
            c: 0,
            d: 1,
            e: dx,
            f: dy
          };
        }

        var cssMatrix = "matrix(".concat(ghostMatrix.a, ",").concat(ghostMatrix.b, ",").concat(ghostMatrix.c, ",").concat(ghostMatrix.d, ",").concat(ghostMatrix.e, ",").concat(ghostMatrix.f, ")");
        css(ghostEl, 'webkitTransform', cssMatrix);
        css(ghostEl, 'mozTransform', cssMatrix);
        css(ghostEl, 'msTransform', cssMatrix);
        css(ghostEl, 'transform', cssMatrix);
        lastDx = dx;
        lastDy = dy;
        touchEvt = touch;
      }

      evt.cancelable && evt.preventDefault();
    }
  },
  _appendGhost: function _appendGhost() {
    // Bug if using scale(): https://stackoverflow.com/questions/2637058
    // Not being adjusted for
    if (!ghostEl) {
      var container = this.options.fallbackOnBody ? document.body : rootEl,
          rect = getRect(dragEl, true, PositionGhostAbsolutely, true, container),
          options = this.options; // Position absolutely

      if (PositionGhostAbsolutely) {
        // Get relatively positioned parent
        ghostRelativeParent = container;

        while (css(ghostRelativeParent, 'position') === 'static' && css(ghostRelativeParent, 'transform') === 'none' && ghostRelativeParent !== document) {
          ghostRelativeParent = ghostRelativeParent.parentNode;
        }

        if (ghostRelativeParent !== document.body && ghostRelativeParent !== document.documentElement) {
          if (ghostRelativeParent === document) ghostRelativeParent = getWindowScrollingElement();
          rect.top += ghostRelativeParent.scrollTop;
          rect.left += ghostRelativeParent.scrollLeft;
        } else {
          ghostRelativeParent = getWindowScrollingElement();
        }

        ghostRelativeParentInitialScroll = getRelativeScrollOffset(ghostRelativeParent);
      }

      ghostEl = dragEl.cloneNode(true);
      toggleClass(ghostEl, options.ghostClass, false);
      toggleClass(ghostEl, options.fallbackClass, true);
      toggleClass(ghostEl, options.dragClass, true);
      css(ghostEl, 'transition', '');
      css(ghostEl, 'transform', '');
      css(ghostEl, 'box-sizing', 'border-box');
      css(ghostEl, 'margin', 0);
      css(ghostEl, 'top', rect.top);
      css(ghostEl, 'left', rect.left);
      css(ghostEl, 'width', rect.width);
      css(ghostEl, 'height', rect.height);
      css(ghostEl, 'opacity', '0.8');
      css(ghostEl, 'position', PositionGhostAbsolutely ? 'absolute' : 'fixed');
      css(ghostEl, 'zIndex', '100000');
      css(ghostEl, 'pointerEvents', 'none');
      Sortable.ghost = ghostEl;
      container.appendChild(ghostEl); // Set transform-origin

      css(ghostEl, 'transform-origin', tapDistanceLeft / parseInt(ghostEl.style.width) * 100 + '% ' + tapDistanceTop / parseInt(ghostEl.style.height) * 100 + '%');
    }
  },
  _onDragStart: function _onDragStart(
  /**Event*/
  evt,
  /**boolean*/
  fallback) {
    var _this = this;

    var dataTransfer = evt.dataTransfer;
    var options = _this.options;
    pluginEvent('dragStart', this, {
      evt: evt
    });

    if (Sortable.eventCanceled) {
      this._onDrop();

      return;
    }

    pluginEvent('setupClone', this);

    if (!Sortable.eventCanceled) {
      cloneEl = clone$1(dragEl);
      cloneEl.draggable = false;
      cloneEl.style['will-change'] = '';

      this._hideClone();

      toggleClass(cloneEl, this.options.chosenClass, false);
      Sortable.clone = cloneEl;
    } // #1143: IFrame support workaround


    _this.cloneId = _nextTick(function () {
      pluginEvent('clone', _this);
      if (Sortable.eventCanceled) return;

      if (!_this.options.removeCloneOnHide) {
        rootEl.insertBefore(cloneEl, dragEl);
      }

      _this._hideClone();

      _dispatchEvent({
        sortable: _this,
        name: 'clone'
      });
    });
    !fallback && toggleClass(dragEl, options.dragClass, true); // Set proper drop events

    if (fallback) {
      ignoreNextClick = true;
      _this._loopId = setInterval(_this._emulateDragOver, 50);
    } else {
      // Undo what was set in _prepareDragStart before drag started
      off(document, 'mouseup', _this._onDrop);
      off(document, 'touchend', _this._onDrop);
      off(document, 'touchcancel', _this._onDrop);

      if (dataTransfer) {
        dataTransfer.effectAllowed = 'move';
        options.setData && options.setData.call(_this, dataTransfer, dragEl);
      }

      on(document, 'drop', _this); // #1276 fix:

      css(dragEl, 'transform', 'translateZ(0)');
    }

    awaitingDragStarted = true;
    _this._dragStartId = _nextTick(_this._dragStarted.bind(_this, fallback, evt));
    on(document, 'selectstart', _this);
    moved = true;

    if (Safari) {
      css(document.body, 'user-select', 'none');
    }
  },
  // Returns true - if no further action is needed (either inserted or another condition)
  _onDragOver: function _onDragOver(
  /**Event*/
  evt) {
    var el = this.el,
        target = evt.target,
        dragRect,
        targetRect,
        revert,
        options = this.options,
        group = options.group,
        activeSortable = Sortable.active,
        isOwner = activeGroup === group,
        canSort = options.sort,
        fromSortable = putSortable || activeSortable,
        vertical,
        _this = this,
        completedFired = false;

    if (_silent) return;

    function dragOverEvent(name, extra) {
      pluginEvent(name, _this, _objectSpread$L({
        evt: evt,
        isOwner: isOwner,
        axis: vertical ? 'vertical' : 'horizontal',
        revert: revert,
        dragRect: dragRect,
        targetRect: targetRect,
        canSort: canSort,
        fromSortable: fromSortable,
        target: target,
        completed: completed,
        onMove: function onMove(target, after) {
          return _onMove(rootEl, el, dragEl, dragRect, target, getRect(target), evt, after);
        },
        changed: changed
      }, extra));
    } // Capture animation state


    function capture() {
      dragOverEvent('dragOverAnimationCapture');

      _this.captureAnimationState();

      if (_this !== fromSortable) {
        fromSortable.captureAnimationState();
      }
    } // Return invocation when dragEl is inserted (or completed)


    function completed(insertion) {
      dragOverEvent('dragOverCompleted', {
        insertion: insertion
      });

      if (insertion) {
        // Clones must be hidden before folding animation to capture dragRectAbsolute properly
        if (isOwner) {
          activeSortable._hideClone();
        } else {
          activeSortable._showClone(_this);
        }

        if (_this !== fromSortable) {
          // Set ghost class to new sortable's ghost class
          toggleClass(dragEl, putSortable ? putSortable.options.ghostClass : activeSortable.options.ghostClass, false);
          toggleClass(dragEl, options.ghostClass, true);
        }

        if (putSortable !== _this && _this !== Sortable.active) {
          putSortable = _this;
        } else if (_this === Sortable.active && putSortable) {
          putSortable = null;
        } // Animation


        if (fromSortable === _this) {
          _this._ignoreWhileAnimating = target;
        }

        _this.animateAll(function () {
          dragOverEvent('dragOverAnimationComplete');
          _this._ignoreWhileAnimating = null;
        });

        if (_this !== fromSortable) {
          fromSortable.animateAll();
          fromSortable._ignoreWhileAnimating = null;
        }
      } // Null lastTarget if it is not inside a previously swapped element


      if (target === dragEl && !dragEl.animated || target === el && !target.animated) {
        lastTarget = null;
      } // no bubbling and not fallback


      if (!options.dragoverBubble && !evt.rootEl && target !== document) {
        dragEl.parentNode[expando]._isOutsideThisEl(evt.target); // Do not detect for empty insert if already inserted


        !insertion && nearestEmptyInsertDetectEvent(evt);
      }

      !options.dragoverBubble && evt.stopPropagation && evt.stopPropagation();
      return completedFired = true;
    } // Call when dragEl has been inserted


    function changed() {
      newIndex = index(dragEl);
      newDraggableIndex = index(dragEl, options.draggable);

      _dispatchEvent({
        sortable: _this,
        name: 'change',
        toEl: el,
        newIndex: newIndex,
        newDraggableIndex: newDraggableIndex,
        originalEvent: evt
      });
    }

    if (evt.preventDefault !== void 0) {
      evt.cancelable && evt.preventDefault();
    }

    target = closest$1(target, options.draggable, el, true);
    dragOverEvent('dragOver');
    if (Sortable.eventCanceled) return completedFired;

    if (dragEl.contains(evt.target) || target.animated && target.animatingX && target.animatingY || _this._ignoreWhileAnimating === target) {
      return completed(false);
    }

    ignoreNextClick = false;

    if (activeSortable && !options.disabled && (isOwner ? canSort || (revert = !rootEl.contains(dragEl)) // Reverting item into the original list
    : putSortable === this || (this.lastPutMode = activeGroup.checkPull(this, activeSortable, dragEl, evt)) && group.checkPut(this, activeSortable, dragEl, evt))) {
      vertical = this._getDirection(evt, target) === 'vertical';
      dragRect = getRect(dragEl);
      dragOverEvent('dragOverValid');
      if (Sortable.eventCanceled) return completedFired;

      if (revert) {
        parentEl = rootEl; // actualization

        capture();

        this._hideClone();

        dragOverEvent('revert');

        if (!Sortable.eventCanceled) {
          if (nextEl) {
            rootEl.insertBefore(dragEl, nextEl);
          } else {
            rootEl.appendChild(dragEl);
          }
        }

        return completed(true);
      }

      var elLastChild = lastChild(el, options.draggable);

      if (!elLastChild || _ghostIsLast(evt, vertical, this) && !elLastChild.animated) {
        // If already at end of list: Do not insert
        if (elLastChild === dragEl) {
          return completed(false);
        } // assign target only if condition is true


        if (elLastChild && el === evt.target) {
          target = elLastChild;
        }

        if (target) {
          targetRect = getRect(target);
        }

        if (_onMove(rootEl, el, dragEl, dragRect, target, targetRect, evt, !!target) !== false) {
          capture();
          el.appendChild(dragEl);
          parentEl = el; // actualization

          changed();
          return completed(true);
        }
      } else if (target.parentNode === el) {
        targetRect = getRect(target);
        var direction = 0,
            targetBeforeFirstSwap,
            differentLevel = dragEl.parentNode !== el,
            differentRowCol = !_dragElInRowColumn(dragEl.animated && dragEl.toRect || dragRect, target.animated && target.toRect || targetRect, vertical),
            side1 = vertical ? 'top' : 'left',
            scrolledPastTop = isScrolledPast(target, 'top', 'top') || isScrolledPast(dragEl, 'top', 'top'),
            scrollBefore = scrolledPastTop ? scrolledPastTop.scrollTop : void 0;

        if (lastTarget !== target) {
          targetBeforeFirstSwap = targetRect[side1];
          pastFirstInvertThresh = false;
          isCircumstantialInvert = !differentRowCol && options.invertSwap || differentLevel;
        }

        direction = _getSwapDirection(evt, target, targetRect, vertical, differentRowCol ? 1 : options.swapThreshold, options.invertedSwapThreshold == null ? options.swapThreshold : options.invertedSwapThreshold, isCircumstantialInvert, lastTarget === target);
        var sibling;

        if (direction !== 0) {
          // Check if target is beside dragEl in respective direction (ignoring hidden elements)
          var dragIndex = index(dragEl);

          do {
            dragIndex -= direction;
            sibling = parentEl.children[dragIndex];
          } while (sibling && (css(sibling, 'display') === 'none' || sibling === ghostEl));
        } // If dragEl is already beside target: Do not insert


        if (direction === 0 || sibling === target) {
          return completed(false);
        }

        lastTarget = target;
        lastDirection = direction;
        var nextSibling = target.nextElementSibling,
            after = false;
        after = direction === 1;

        var moveVector = _onMove(rootEl, el, dragEl, dragRect, target, targetRect, evt, after);

        if (moveVector !== false) {
          if (moveVector === 1 || moveVector === -1) {
            after = moveVector === 1;
          }

          _silent = true;
          setTimeout(_unsilent, 30);
          capture();

          if (after && !nextSibling) {
            el.appendChild(dragEl);
          } else {
            target.parentNode.insertBefore(dragEl, after ? nextSibling : target);
          } // Undo chrome's scroll adjustment (has no effect on other browsers)


          if (scrolledPastTop) {
            scrollBy(scrolledPastTop, 0, scrollBefore - scrolledPastTop.scrollTop);
          }

          parentEl = dragEl.parentNode; // actualization
          // must be done before animation

          if (targetBeforeFirstSwap !== undefined && !isCircumstantialInvert) {
            targetMoveDistance = Math.abs(targetBeforeFirstSwap - getRect(target)[side1]);
          }

          changed();
          return completed(true);
        }
      }

      if (el.contains(dragEl)) {
        return completed(false);
      }
    }

    return false;
  },
  _ignoreWhileAnimating: null,
  _offMoveEvents: function _offMoveEvents() {
    off(document, 'mousemove', this._onTouchMove);
    off(document, 'touchmove', this._onTouchMove);
    off(document, 'pointermove', this._onTouchMove);
    off(document, 'dragover', nearestEmptyInsertDetectEvent);
    off(document, 'mousemove', nearestEmptyInsertDetectEvent);
    off(document, 'touchmove', nearestEmptyInsertDetectEvent);
  },
  _offUpEvents: function _offUpEvents() {
    var ownerDocument = this.el.ownerDocument;
    off(ownerDocument, 'mouseup', this._onDrop);
    off(ownerDocument, 'touchend', this._onDrop);
    off(ownerDocument, 'pointerup', this._onDrop);
    off(ownerDocument, 'touchcancel', this._onDrop);
    off(document, 'selectstart', this);
  },
  _onDrop: function _onDrop(
  /**Event*/
  evt) {
    var el = this.el,
        options = this.options; // Get the index of the dragged element within its parent

    newIndex = index(dragEl);
    newDraggableIndex = index(dragEl, options.draggable);
    pluginEvent('drop', this, {
      evt: evt
    });
    parentEl = dragEl && dragEl.parentNode; // Get again after plugin event

    newIndex = index(dragEl);
    newDraggableIndex = index(dragEl, options.draggable);

    if (Sortable.eventCanceled) {
      this._nulling();

      return;
    }

    awaitingDragStarted = false;
    isCircumstantialInvert = false;
    pastFirstInvertThresh = false;
    clearInterval(this._loopId);
    clearTimeout(this._dragStartTimer);

    _cancelNextTick(this.cloneId);

    _cancelNextTick(this._dragStartId); // Unbind events


    if (this.nativeDraggable) {
      off(document, 'drop', this);
      off(el, 'dragstart', this._onDragStart);
    }

    this._offMoveEvents();

    this._offUpEvents();

    if (Safari) {
      css(document.body, 'user-select', '');
    }

    css(dragEl, 'transform', '');

    if (evt) {
      if (moved) {
        evt.cancelable && evt.preventDefault();
        !options.dropBubble && evt.stopPropagation();
      }

      ghostEl && ghostEl.parentNode && ghostEl.parentNode.removeChild(ghostEl);

      if (rootEl === parentEl || putSortable && putSortable.lastPutMode !== 'clone') {
        // Remove clone(s)
        cloneEl && cloneEl.parentNode && cloneEl.parentNode.removeChild(cloneEl);
      }

      if (dragEl) {
        if (this.nativeDraggable) {
          off(dragEl, 'dragend', this);
        }

        _disableDraggable(dragEl);

        dragEl.style['will-change'] = ''; // Remove classes
        // ghostClass is added in dragStarted

        if (moved && !awaitingDragStarted) {
          toggleClass(dragEl, putSortable ? putSortable.options.ghostClass : this.options.ghostClass, false);
        }

        toggleClass(dragEl, this.options.chosenClass, false); // Drag stop event

        _dispatchEvent({
          sortable: this,
          name: 'unchoose',
          toEl: parentEl,
          newIndex: null,
          newDraggableIndex: null,
          originalEvent: evt
        });

        if (rootEl !== parentEl) {
          if (newIndex >= 0) {
            // Add event
            _dispatchEvent({
              rootEl: parentEl,
              name: 'add',
              toEl: parentEl,
              fromEl: rootEl,
              originalEvent: evt
            }); // Remove event


            _dispatchEvent({
              sortable: this,
              name: 'remove',
              toEl: parentEl,
              originalEvent: evt
            }); // drag from one list and drop into another


            _dispatchEvent({
              rootEl: parentEl,
              name: 'sort',
              toEl: parentEl,
              fromEl: rootEl,
              originalEvent: evt
            });

            _dispatchEvent({
              sortable: this,
              name: 'sort',
              toEl: parentEl,
              originalEvent: evt
            });
          }

          putSortable && putSortable.save();
        } else {
          if (newIndex !== oldIndex) {
            if (newIndex >= 0) {
              // drag & drop within the same list
              _dispatchEvent({
                sortable: this,
                name: 'update',
                toEl: parentEl,
                originalEvent: evt
              });

              _dispatchEvent({
                sortable: this,
                name: 'sort',
                toEl: parentEl,
                originalEvent: evt
              });
            }
          }
        }

        if (Sortable.active) {
          /* jshint eqnull:true */
          if (newIndex == null || newIndex === -1) {
            newIndex = oldIndex;
            newDraggableIndex = oldDraggableIndex;
          }

          _dispatchEvent({
            sortable: this,
            name: 'end',
            toEl: parentEl,
            originalEvent: evt
          }); // Save sorting


          this.save();
        }
      }
    }

    this._nulling();
  },
  _nulling: function _nulling() {
    pluginEvent('nulling', this);
    rootEl = dragEl = parentEl = ghostEl = nextEl = cloneEl = lastDownEl = cloneHidden = tapEvt = touchEvt = moved = newIndex = newDraggableIndex = oldIndex = oldDraggableIndex = lastTarget = lastDirection = putSortable = activeGroup = Sortable.dragged = Sortable.ghost = Sortable.clone = Sortable.active = null;
    savedInputChecked.forEach(function (el) {
      el.checked = true;
    });
    savedInputChecked.length = lastDx = lastDy = 0;
  },
  handleEvent: function handleEvent(
  /**Event*/
  evt) {
    switch (evt.type) {
      case 'drop':
      case 'dragend':
        this._onDrop(evt);

        break;

      case 'dragenter':
      case 'dragover':
        if (dragEl) {
          this._onDragOver(evt);

          _globalDragOver(evt);
        }

        break;

      case 'selectstart':
        evt.preventDefault();
        break;
    }
  },

  /**
   * Serializes the item into an array of string.
   * @returns {String[]}
   */
  toArray: function toArray() {
    var order = [],
        el,
        children = this.el.children,
        i = 0,
        n = children.length,
        options = this.options;

    for (; i < n; i++) {
      el = children[i];

      if (closest$1(el, options.draggable, this.el, false)) {
        order.push(el.getAttribute(options.dataIdAttr) || _generateId(el));
      }
    }

    return order;
  },

  /**
   * Sorts the elements according to the array.
   * @param  {String[]}  order  order of the items
   */
  sort: function sort(order) {
    var items = {},
        rootEl = this.el;
    this.toArray().forEach(function (id, i) {
      var el = rootEl.children[i];

      if (closest$1(el, this.options.draggable, rootEl, false)) {
        items[id] = el;
      }
    }, this);
    order.forEach(function (id) {
      if (items[id]) {
        rootEl.removeChild(items[id]);
        rootEl.appendChild(items[id]);
      }
    });
  },

  /**
   * Save the current sorting
   */
  save: function save() {
    var store = this.options.store;
    store && store.set && store.set(this);
  },

  /**
   * For each element in the set, get the first element that matches the selector by testing the element itself and traversing up through its ancestors in the DOM tree.
   * @param   {HTMLElement}  el
   * @param   {String}       [selector]  default: `options.draggable`
   * @returns {HTMLElement|null}
   */
  closest: function closest$1$1(el, selector) {
    return closest$1(el, selector || this.options.draggable, this.el, false);
  },

  /**
   * Set/get option
   * @param   {string} name
   * @param   {*}      [value]
   * @returns {*}
   */
  option: function option(name, value) {
    var options = this.options;

    if (value === void 0) {
      return options[name];
    } else {
      var modifiedValue = PluginManager.modifyOption(this, name, value);

      if (typeof modifiedValue !== 'undefined') {
        options[name] = modifiedValue;
      } else {
        options[name] = value;
      }

      if (name === 'group') {
        _prepareGroup(options);
      }
    }
  },

  /**
   * Destroy
   */
  destroy: function destroy() {
    pluginEvent('destroy', this);
    var el = this.el;
    el[expando] = null;
    off(el, 'mousedown', this._onTapStart);
    off(el, 'touchstart', this._onTapStart);
    off(el, 'pointerdown', this._onTapStart);

    if (this.nativeDraggable) {
      off(el, 'dragover', this);
      off(el, 'dragenter', this);
    } // Remove draggable attributes


    Array.prototype.forEach.call(el.querySelectorAll('[draggable]'), function (el) {
      el.removeAttribute('draggable');
    });

    this._onDrop();

    this._disableDelayedDragEvents();

    sortables.splice(sortables.indexOf(this.el), 1);
    this.el = el = null;
  },
  _hideClone: function _hideClone() {
    if (!cloneHidden) {
      pluginEvent('hideClone', this);
      if (Sortable.eventCanceled) return;
      css(cloneEl, 'display', 'none');

      if (this.options.removeCloneOnHide && cloneEl.parentNode) {
        cloneEl.parentNode.removeChild(cloneEl);
      }

      cloneHidden = true;
    }
  },
  _showClone: function _showClone(putSortable) {
    if (putSortable.lastPutMode !== 'clone') {
      this._hideClone();

      return;
    }

    if (cloneHidden) {
      pluginEvent('showClone', this);
      if (Sortable.eventCanceled) return; // show clone at dragEl or original position

      if (rootEl.contains(dragEl) && !this.options.group.revertClone) {
        rootEl.insertBefore(cloneEl, dragEl);
      } else if (nextEl) {
        rootEl.insertBefore(cloneEl, nextEl);
      } else {
        rootEl.appendChild(cloneEl);
      }

      if (this.options.group.revertClone) {
        this.animate(dragEl, cloneEl);
      }

      css(cloneEl, 'display', '');
      cloneHidden = false;
    }
  }
};

function _globalDragOver(
/**Event*/
evt) {
  if (evt.dataTransfer) {
    evt.dataTransfer.dropEffect = 'move';
  }

  evt.cancelable && evt.preventDefault();
}

function _onMove(fromEl, toEl, dragEl, dragRect, targetEl, targetRect, originalEvent, willInsertAfter) {
  var evt,
      sortable = fromEl[expando],
      onMoveFn = sortable.options.onMove,
      retVal; // Support for new CustomEvent feature

  if (window.CustomEvent && !IE11OrLess && !Edge) {
    evt = new CustomEvent('move', {
      bubbles: true,
      cancelable: true
    });
  } else {
    evt = document.createEvent('Event');
    evt.initEvent('move', true, true);
  }

  evt.to = toEl;
  evt.from = fromEl;
  evt.dragged = dragEl;
  evt.draggedRect = dragRect;
  evt.related = targetEl || toEl;
  evt.relatedRect = targetRect || getRect(toEl);
  evt.willInsertAfter = willInsertAfter;
  evt.originalEvent = originalEvent;
  fromEl.dispatchEvent(evt);

  if (onMoveFn) {
    retVal = onMoveFn.call(sortable, evt, originalEvent);
  }

  return retVal;
}

function _disableDraggable(el) {
  el.draggable = false;
}

function _unsilent() {
  _silent = false;
}

function _ghostIsLast(evt, vertical, sortable) {
  var rect = getRect(lastChild(sortable.el, sortable.options.draggable));
  var spacer = 10;
  return vertical ? evt.clientX > rect.right + spacer || evt.clientX <= rect.right && evt.clientY > rect.bottom && evt.clientX >= rect.left : evt.clientX > rect.right && evt.clientY > rect.top || evt.clientX <= rect.right && evt.clientY > rect.bottom + spacer;
}

function _getSwapDirection(evt, target, targetRect, vertical, swapThreshold, invertedSwapThreshold, invertSwap, isLastTarget) {
  var mouseOnAxis = vertical ? evt.clientY : evt.clientX,
      targetLength = vertical ? targetRect.height : targetRect.width,
      targetS1 = vertical ? targetRect.top : targetRect.left,
      targetS2 = vertical ? targetRect.bottom : targetRect.right,
      invert = false;

  if (!invertSwap) {
    // Never invert or create dragEl shadow when target movemenet causes mouse to move past the end of regular swapThreshold
    if (isLastTarget && targetMoveDistance < targetLength * swapThreshold) {
      // multiplied only by swapThreshold because mouse will already be inside target by (1 - threshold) * targetLength / 2
      // check if past first invert threshold on side opposite of lastDirection
      if (!pastFirstInvertThresh && (lastDirection === 1 ? mouseOnAxis > targetS1 + targetLength * invertedSwapThreshold / 2 : mouseOnAxis < targetS2 - targetLength * invertedSwapThreshold / 2)) {
        // past first invert threshold, do not restrict inverted threshold to dragEl shadow
        pastFirstInvertThresh = true;
      }

      if (!pastFirstInvertThresh) {
        // dragEl shadow (target move distance shadow)
        if (lastDirection === 1 ? mouseOnAxis < targetS1 + targetMoveDistance // over dragEl shadow
        : mouseOnAxis > targetS2 - targetMoveDistance) {
          return -lastDirection;
        }
      } else {
        invert = true;
      }
    } else {
      // Regular
      if (mouseOnAxis > targetS1 + targetLength * (1 - swapThreshold) / 2 && mouseOnAxis < targetS2 - targetLength * (1 - swapThreshold) / 2) {
        return _getInsertDirection(target);
      }
    }
  }

  invert = invert || invertSwap;

  if (invert) {
    // Invert of regular
    if (mouseOnAxis < targetS1 + targetLength * invertedSwapThreshold / 2 || mouseOnAxis > targetS2 - targetLength * invertedSwapThreshold / 2) {
      return mouseOnAxis > targetS1 + targetLength / 2 ? 1 : -1;
    }
  }

  return 0;
}
/**
 * Gets the direction dragEl must be swapped relative to target in order to make it
 * seem that dragEl has been "inserted" into that element's position
 * @param  {HTMLElement} target       The target whose position dragEl is being inserted at
 * @return {Number}                   Direction dragEl must be swapped
 */


function _getInsertDirection(target) {
  if (index(dragEl) < index(target)) {
    return 1;
  } else {
    return -1;
  }
}
/**
 * Generate id
 * @param   {HTMLElement} el
 * @returns {String}
 * @private
 */


function _generateId(el) {
  var str = el.tagName + el.className + el.src + el.href + el.textContent,
      i = str.length,
      sum = 0;

  while (i--) {
    sum += str.charCodeAt(i);
  }

  return sum.toString(36);
}

function _saveInputCheckedState(root) {
  savedInputChecked.length = 0;
  var inputs = root.getElementsByTagName('input');
  var idx = inputs.length;

  while (idx--) {
    var el = inputs[idx];
    el.checked && savedInputChecked.push(el);
  }
}

function _nextTick(fn) {
  return setTimeout(fn, 0);
}

function _cancelNextTick(id) {
  return clearTimeout(id);
} // Fixed #973:


if (documentExists) {
  on(document, 'touchmove', function (evt) {
    if ((Sortable.active || awaitingDragStarted) && evt.cancelable) {
      evt.preventDefault();
    }
  });
} // Export utils


Sortable.utils = {
  on: on,
  off: off,
  css: css,
  find: find$2,
  is: function is(el, selector) {
    return !!closest$1(el, selector, el, false);
  },
  extend: extend,
  throttle: throttle,
  closest: closest$1,
  toggleClass: toggleClass,
  clone: clone$1,
  index: index,
  nextTick: _nextTick,
  cancelNextTick: _cancelNextTick,
  detectDirection: _detectDirection,
  getChild: getChild
};
/**
 * Get the Sortable instance of an element
 * @param  {HTMLElement} element The element
 * @return {Sortable|undefined}         The instance of Sortable
 */

Sortable.get = function (element) {
  return element[expando];
};
/**
 * Mount a plugin to Sortable
 * @param  {...SortablePlugin|SortablePlugin[]} plugins       Plugins being mounted
 */


Sortable.mount = function () {
  for (var _len = arguments.length, plugins = new Array(_len), _key = 0; _key < _len; _key++) {
    plugins[_key] = arguments[_key];
  }

  if (plugins[0].constructor === Array) plugins = plugins[0];
  plugins.forEach(function (plugin) {
    if (!plugin.prototype || !plugin.prototype.constructor) {
      throw "Sortable: Mounted plugin must be a constructor function, not ".concat({}.toString.call(plugin));
    }

    if (plugin.utils) Sortable.utils = _objectSpread$L({}, Sortable.utils, plugin.utils);
    PluginManager.mount(plugin);
  });
};
/**
 * Create sortable instance
 * @param {HTMLElement}  el
 * @param {Object}      [options]
 */


Sortable.create = function (el, options) {
  return new Sortable(el, options);
}; // Export


Sortable.version = version;

var autoScrolls = [],
    scrollEl,
    scrollRootEl,
    scrolling = false,
    lastAutoScrollX,
    lastAutoScrollY,
    touchEvt$1,
    pointerElemChangedInterval;

function AutoScrollPlugin() {
  function AutoScroll() {
    this.defaults = {
      scroll: true,
      scrollSensitivity: 30,
      scrollSpeed: 10,
      bubbleScroll: true
    }; // Bind all private methods

    for (var fn in this) {
      if (fn.charAt(0) === '_' && typeof this[fn] === 'function') {
        this[fn] = this[fn].bind(this);
      }
    }
  }

  AutoScroll.prototype = {
    dragStarted: function dragStarted(_ref) {
      var originalEvent = _ref.originalEvent;

      if (this.sortable.nativeDraggable) {
        on(document, 'dragover', this._handleAutoScroll);
      } else {
        if (this.options.supportPointer) {
          on(document, 'pointermove', this._handleFallbackAutoScroll);
        } else if (originalEvent.touches) {
          on(document, 'touchmove', this._handleFallbackAutoScroll);
        } else {
          on(document, 'mousemove', this._handleFallbackAutoScroll);
        }
      }
    },
    dragOverCompleted: function dragOverCompleted(_ref2) {
      var originalEvent = _ref2.originalEvent;

      // For when bubbling is canceled and using fallback (fallback 'touchmove' always reached)
      if (!this.options.dragOverBubble && !originalEvent.rootEl) {
        this._handleAutoScroll(originalEvent);
      }
    },
    drop: function drop() {
      if (this.sortable.nativeDraggable) {
        off(document, 'dragover', this._handleAutoScroll);
      } else {
        off(document, 'pointermove', this._handleFallbackAutoScroll);
        off(document, 'touchmove', this._handleFallbackAutoScroll);
        off(document, 'mousemove', this._handleFallbackAutoScroll);
      }

      clearPointerElemChangedInterval();
      clearAutoScrolls();
      cancelThrottle();
    },
    nulling: function nulling() {
      touchEvt$1 = scrollRootEl = scrollEl = scrolling = pointerElemChangedInterval = lastAutoScrollX = lastAutoScrollY = null;
      autoScrolls.length = 0;
    },
    _handleFallbackAutoScroll: function _handleFallbackAutoScroll(evt) {
      this._handleAutoScroll(evt, true);
    },
    _handleAutoScroll: function _handleAutoScroll(evt, fallback) {
      var _this = this;

      var x = (evt.touches ? evt.touches[0] : evt).clientX,
          y = (evt.touches ? evt.touches[0] : evt).clientY,
          elem = document.elementFromPoint(x, y);
      touchEvt$1 = evt; // IE does not seem to have native autoscroll,
      // Edge's autoscroll seems too conditional,
      // MACOS Safari does not have autoscroll,
      // Firefox and Chrome are good

      if (fallback || Edge || IE11OrLess || Safari) {
        autoScroll(evt, this.options, elem, fallback); // Listener for pointer element change

        var ogElemScroller = getParentAutoScrollElement(elem, true);

        if (scrolling && (!pointerElemChangedInterval || x !== lastAutoScrollX || y !== lastAutoScrollY)) {
          pointerElemChangedInterval && clearPointerElemChangedInterval(); // Detect for pointer elem change, emulating native DnD behaviour

          pointerElemChangedInterval = setInterval(function () {
            var newElem = getParentAutoScrollElement(document.elementFromPoint(x, y), true);

            if (newElem !== ogElemScroller) {
              ogElemScroller = newElem;
              clearAutoScrolls();
            }

            autoScroll(evt, _this.options, newElem, fallback);
          }, 10);
          lastAutoScrollX = x;
          lastAutoScrollY = y;
        }
      } else {
        // if DnD is enabled (and browser has good autoscrolling), first autoscroll will already scroll, so get parent autoscroll of first autoscroll
        if (!this.options.bubbleScroll || getParentAutoScrollElement(elem, true) === getWindowScrollingElement()) {
          clearAutoScrolls();
          return;
        }

        autoScroll(evt, this.options, getParentAutoScrollElement(elem, false), false);
      }
    }
  };
  return _extends$1(AutoScroll, {
    pluginName: 'scroll',
    initializeByDefault: true
  });
}

function clearAutoScrolls() {
  autoScrolls.forEach(function (autoScroll) {
    clearInterval(autoScroll.pid);
  });
  autoScrolls = [];
}

function clearPointerElemChangedInterval() {
  clearInterval(pointerElemChangedInterval);
}

var autoScroll = throttle(function (evt, options, rootEl, isFallback) {
  // Bug: https://bugzilla.mozilla.org/show_bug.cgi?id=505521
  if (!options.scroll) return;
  var x = (evt.touches ? evt.touches[0] : evt).clientX,
      y = (evt.touches ? evt.touches[0] : evt).clientY,
      sens = options.scrollSensitivity,
      speed = options.scrollSpeed,
      winScroller = getWindowScrollingElement();
  var scrollThisInstance = false,
      scrollCustomFn; // New scroll root, set scrollEl

  if (scrollRootEl !== rootEl) {
    scrollRootEl = rootEl;
    clearAutoScrolls();
    scrollEl = options.scroll;
    scrollCustomFn = options.scrollFn;

    if (scrollEl === true) {
      scrollEl = getParentAutoScrollElement(rootEl, true);
    }
  }

  var layersOut = 0;
  var currentParent = scrollEl;

  do {
    var el = currentParent,
        rect = getRect(el),
        top = rect.top,
        bottom = rect.bottom,
        left = rect.left,
        right = rect.right,
        width = rect.width,
        height = rect.height,
        canScrollX = void 0,
        canScrollY = void 0,
        scrollWidth = el.scrollWidth,
        scrollHeight = el.scrollHeight,
        elCSS = css(el),
        scrollPosX = el.scrollLeft,
        scrollPosY = el.scrollTop;

    if (el === winScroller) {
      canScrollX = width < scrollWidth && (elCSS.overflowX === 'auto' || elCSS.overflowX === 'scroll' || elCSS.overflowX === 'visible');
      canScrollY = height < scrollHeight && (elCSS.overflowY === 'auto' || elCSS.overflowY === 'scroll' || elCSS.overflowY === 'visible');
    } else {
      canScrollX = width < scrollWidth && (elCSS.overflowX === 'auto' || elCSS.overflowX === 'scroll');
      canScrollY = height < scrollHeight && (elCSS.overflowY === 'auto' || elCSS.overflowY === 'scroll');
    }

    var vx = canScrollX && (Math.abs(right - x) <= sens && scrollPosX + width < scrollWidth) - (Math.abs(left - x) <= sens && !!scrollPosX);
    var vy = canScrollY && (Math.abs(bottom - y) <= sens && scrollPosY + height < scrollHeight) - (Math.abs(top - y) <= sens && !!scrollPosY);

    if (!autoScrolls[layersOut]) {
      for (var i = 0; i <= layersOut; i++) {
        if (!autoScrolls[i]) {
          autoScrolls[i] = {};
        }
      }
    }

    if (autoScrolls[layersOut].vx != vx || autoScrolls[layersOut].vy != vy || autoScrolls[layersOut].el !== el) {
      autoScrolls[layersOut].el = el;
      autoScrolls[layersOut].vx = vx;
      autoScrolls[layersOut].vy = vy;
      clearInterval(autoScrolls[layersOut].pid);

      if (vx != 0 || vy != 0) {
        scrollThisInstance = true;
        /* jshint loopfunc:true */

        autoScrolls[layersOut].pid = setInterval(function () {
          // emulate drag over during autoscroll (fallback), emulating native DnD behaviour
          if (isFallback && this.layer === 0) {
            Sortable.active._onTouchMove(touchEvt$1); // To move ghost if it is positioned absolutely

          }

          var scrollOffsetY = autoScrolls[this.layer].vy ? autoScrolls[this.layer].vy * speed : 0;
          var scrollOffsetX = autoScrolls[this.layer].vx ? autoScrolls[this.layer].vx * speed : 0;

          if (typeof scrollCustomFn === 'function') {
            if (scrollCustomFn.call(Sortable.dragged.parentNode[expando], scrollOffsetX, scrollOffsetY, evt, touchEvt$1, autoScrolls[this.layer].el) !== 'continue') {
              return;
            }
          }

          scrollBy(autoScrolls[this.layer].el, scrollOffsetX, scrollOffsetY);
        }.bind({
          layer: layersOut
        }), 24);
      }
    }

    layersOut++;
  } while (options.bubbleScroll && currentParent !== winScroller && (currentParent = getParentAutoScrollElement(currentParent, false)));

  scrolling = scrollThisInstance; // in case another function catches scrolling as false in between when it is not
}, 30);

var drop = function drop(_ref) {
  var originalEvent = _ref.originalEvent,
      putSortable = _ref.putSortable,
      dragEl = _ref.dragEl,
      activeSortable = _ref.activeSortable,
      dispatchSortableEvent = _ref.dispatchSortableEvent,
      hideGhostForTarget = _ref.hideGhostForTarget,
      unhideGhostForTarget = _ref.unhideGhostForTarget;
  if (!originalEvent) return;
  var toSortable = putSortable || activeSortable;
  hideGhostForTarget();
  var touch = originalEvent.changedTouches && originalEvent.changedTouches.length ? originalEvent.changedTouches[0] : originalEvent;
  var target = document.elementFromPoint(touch.clientX, touch.clientY);
  unhideGhostForTarget();

  if (toSortable && !toSortable.el.contains(target)) {
    dispatchSortableEvent('spill');
    this.onSpill({
      dragEl: dragEl,
      putSortable: putSortable
    });
  }
};

function Revert() {}

Revert.prototype = {
  startIndex: null,
  dragStart: function dragStart(_ref2) {
    var oldDraggableIndex = _ref2.oldDraggableIndex;
    this.startIndex = oldDraggableIndex;
  },
  onSpill: function onSpill(_ref3) {
    var dragEl = _ref3.dragEl,
        putSortable = _ref3.putSortable;
    this.sortable.captureAnimationState();

    if (putSortable) {
      putSortable.captureAnimationState();
    }

    var nextSibling = getChild(this.sortable.el, this.startIndex, this.options);

    if (nextSibling) {
      this.sortable.el.insertBefore(dragEl, nextSibling);
    } else {
      this.sortable.el.appendChild(dragEl);
    }

    this.sortable.animateAll();

    if (putSortable) {
      putSortable.animateAll();
    }
  },
  drop: drop
};

_extends$1(Revert, {
  pluginName: 'revertOnSpill'
});

function Remove() {}

Remove.prototype = {
  onSpill: function onSpill(_ref4) {
    var dragEl = _ref4.dragEl,
        putSortable = _ref4.putSortable;
    var parentSortable = putSortable || this.sortable;
    parentSortable.captureAnimationState();
    dragEl.parentNode && dragEl.parentNode.removeChild(dragEl);
    parentSortable.animateAll();
  },
  drop: drop
};

_extends$1(Remove, {
  pluginName: 'removeOnSpill'
});

Sortable.mount(new AutoScrollPlugin());
Sortable.mount(Remove, Revert);

var vuedraggable_common = createCommonjsModule(function (module) {
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, { enumerable: true, get: getter });
/******/ 		}
/******/ 	};
/******/
/******/ 	// define __esModule on exports
/******/ 	__webpack_require__.r = function(exports) {
/******/ 		if(typeof Symbol !== 'undefined' && Symbol.toStringTag) {
/******/ 			Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });
/******/ 		}
/******/ 		Object.defineProperty(exports, '__esModule', { value: true });
/******/ 	};
/******/
/******/ 	// create a fake namespace object
/******/ 	// mode & 1: value is a module id, require it
/******/ 	// mode & 2: merge all properties of value into the ns
/******/ 	// mode & 4: return value when already ns object
/******/ 	// mode & 8|1: behave like require
/******/ 	__webpack_require__.t = function(value, mode) {
/******/ 		if(mode & 1) value = __webpack_require__(value);
/******/ 		if(mode & 8) return value;
/******/ 		if((mode & 4) && typeof value === 'object' && value && value.__esModule) return value;
/******/ 		var ns = Object.create(null);
/******/ 		__webpack_require__.r(ns);
/******/ 		Object.defineProperty(ns, 'default', { enumerable: true, value: value });
/******/ 		if(mode & 2 && typeof value != 'string') for(var key in value) __webpack_require__.d(ns, key, function(key) { return value[key]; }.bind(null, key));
/******/ 		return ns;
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = "fb15");
/******/ })
/************************************************************************/
/******/ ({

/***/ "01f9":
/***/ (function(module, exports, __webpack_require__) {

var LIBRARY = __webpack_require__("2d00");
var $export = __webpack_require__("5ca1");
var redefine = __webpack_require__("2aba");
var hide = __webpack_require__("32e9");
var Iterators = __webpack_require__("84f2");
var $iterCreate = __webpack_require__("41a0");
var setToStringTag = __webpack_require__("7f20");
var getPrototypeOf = __webpack_require__("38fd");
var ITERATOR = __webpack_require__("2b4c")('iterator');
var BUGGY = !([].keys && 'next' in [].keys()); // Safari has buggy iterators w/o `next`
var FF_ITERATOR = '@@iterator';
var KEYS = 'keys';
var VALUES = 'values';

var returnThis = function () { return this; };

module.exports = function (Base, NAME, Constructor, next, DEFAULT, IS_SET, FORCED) {
  $iterCreate(Constructor, NAME, next);
  var getMethod = function (kind) {
    if (!BUGGY && kind in proto) return proto[kind];
    switch (kind) {
      case KEYS: return function keys() { return new Constructor(this, kind); };
      case VALUES: return function values() { return new Constructor(this, kind); };
    } return function entries() { return new Constructor(this, kind); };
  };
  var TAG = NAME + ' Iterator';
  var DEF_VALUES = DEFAULT == VALUES;
  var VALUES_BUG = false;
  var proto = Base.prototype;
  var $native = proto[ITERATOR] || proto[FF_ITERATOR] || DEFAULT && proto[DEFAULT];
  var $default = $native || getMethod(DEFAULT);
  var $entries = DEFAULT ? !DEF_VALUES ? $default : getMethod('entries') : undefined;
  var $anyNative = NAME == 'Array' ? proto.entries || $native : $native;
  var methods, key, IteratorPrototype;
  // Fix native
  if ($anyNative) {
    IteratorPrototype = getPrototypeOf($anyNative.call(new Base()));
    if (IteratorPrototype !== Object.prototype && IteratorPrototype.next) {
      // Set @@toStringTag to native iterators
      setToStringTag(IteratorPrototype, TAG, true);
      // fix for some old engines
      if (!LIBRARY && typeof IteratorPrototype[ITERATOR] != 'function') hide(IteratorPrototype, ITERATOR, returnThis);
    }
  }
  // fix Array#{values, @@iterator}.name in V8 / FF
  if (DEF_VALUES && $native && $native.name !== VALUES) {
    VALUES_BUG = true;
    $default = function values() { return $native.call(this); };
  }
  // Define iterator
  if ((!LIBRARY || FORCED) && (BUGGY || VALUES_BUG || !proto[ITERATOR])) {
    hide(proto, ITERATOR, $default);
  }
  // Plug for library
  Iterators[NAME] = $default;
  Iterators[TAG] = returnThis;
  if (DEFAULT) {
    methods = {
      values: DEF_VALUES ? $default : getMethod(VALUES),
      keys: IS_SET ? $default : getMethod(KEYS),
      entries: $entries
    };
    if (FORCED) for (key in methods) {
      if (!(key in proto)) redefine(proto, key, methods[key]);
    } else $export($export.P + $export.F * (BUGGY || VALUES_BUG), NAME, methods);
  }
  return methods;
};


/***/ }),

/***/ "02f4":
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__("4588");
var defined = __webpack_require__("be13");
// true  -> String#at
// false -> String#codePointAt
module.exports = function (TO_STRING) {
  return function (that, pos) {
    var s = String(defined(that));
    var i = toInteger(pos);
    var l = s.length;
    var a, b;
    if (i < 0 || i >= l) return TO_STRING ? '' : undefined;
    a = s.charCodeAt(i);
    return a < 0xd800 || a > 0xdbff || i + 1 === l || (b = s.charCodeAt(i + 1)) < 0xdc00 || b > 0xdfff
      ? TO_STRING ? s.charAt(i) : a
      : TO_STRING ? s.slice(i, i + 2) : (a - 0xd800 << 10) + (b - 0xdc00) + 0x10000;
  };
};


/***/ }),

/***/ "0390":
/***/ (function(module, exports, __webpack_require__) {

var at = __webpack_require__("02f4")(true);

 // `AdvanceStringIndex` abstract operation
// https://tc39.github.io/ecma262/#sec-advancestringindex
module.exports = function (S, index, unicode) {
  return index + (unicode ? at(S, index).length : 1);
};


/***/ }),

/***/ "0bfb":
/***/ (function(module, exports, __webpack_require__) {

// 21.2.5.3 get RegExp.prototype.flags
var anObject = __webpack_require__("cb7c");
module.exports = function () {
  var that = anObject(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};


/***/ }),

/***/ "0d58":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.14 / 15.2.3.14 Object.keys(O)
var $keys = __webpack_require__("ce10");
var enumBugKeys = __webpack_require__("e11e");

module.exports = Object.keys || function keys(O) {
  return $keys(O, enumBugKeys);
};


/***/ }),

/***/ "1495":
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__("86cc");
var anObject = __webpack_require__("cb7c");
var getKeys = __webpack_require__("0d58");

module.exports = __webpack_require__("9e1e") ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject(O);
  var keys = getKeys(Properties);
  var length = keys.length;
  var i = 0;
  var P;
  while (length > i) dP.f(O, P = keys[i++], Properties[P]);
  return O;
};


/***/ }),

/***/ "214f":
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__("b0c5");
var redefine = __webpack_require__("2aba");
var hide = __webpack_require__("32e9");
var fails = __webpack_require__("79e5");
var defined = __webpack_require__("be13");
var wks = __webpack_require__("2b4c");
var regexpExec = __webpack_require__("520a");

var SPECIES = wks('species');

var REPLACE_SUPPORTS_NAMED_GROUPS = !fails(function () {
  // #replace needs built-in support for named groups.
  // #match works fine because it just return the exec results, even if it has
  // a "grops" property.
  var re = /./;
  re.exec = function () {
    var result = [];
    result.groups = { a: '7' };
    return result;
  };
  return ''.replace(re, '$<a>') !== '7';
});

var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC = (function () {
  // Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
  var re = /(?:)/;
  var originalExec = re.exec;
  re.exec = function () { return originalExec.apply(this, arguments); };
  var result = 'ab'.split(re);
  return result.length === 2 && result[0] === 'a' && result[1] === 'b';
})();

module.exports = function (KEY, length, exec) {
  var SYMBOL = wks(KEY);

  var DELEGATES_TO_SYMBOL = !fails(function () {
    // String methods call symbol-named RegEp methods
    var O = {};
    O[SYMBOL] = function () { return 7; };
    return ''[KEY](O) != 7;
  });

  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL ? !fails(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;
    re.exec = function () { execCalled = true; return null; };
    if (KEY === 'split') {
      // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.
      re.constructor = {};
      re.constructor[SPECIES] = function () { return re; };
    }
    re[SYMBOL]('');
    return !execCalled;
  }) : undefined;

  if (
    !DELEGATES_TO_SYMBOL ||
    !DELEGATES_TO_EXEC ||
    (KEY === 'replace' && !REPLACE_SUPPORTS_NAMED_GROUPS) ||
    (KEY === 'split' && !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC)
  ) {
    var nativeRegExpMethod = /./[SYMBOL];
    var fns = exec(
      defined,
      SYMBOL,
      ''[KEY],
      function maybeCallNative(nativeMethod, regexp, str, arg2, forceStringMethod) {
        if (regexp.exec === regexpExec) {
          if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
            // The native String method already delegates to @@method (this
            // polyfilled function), leasing to infinite recursion.
            // We avoid it by directly calling the native @@method method.
            return { done: true, value: nativeRegExpMethod.call(regexp, str, arg2) };
          }
          return { done: true, value: nativeMethod.call(str, regexp, arg2) };
        }
        return { done: false };
      }
    );
    var strfn = fns[0];
    var rxfn = fns[1];

    redefine(String.prototype, KEY, strfn);
    hide(RegExp.prototype, SYMBOL, length == 2
      // 21.2.5.8 RegExp.prototype[@@replace](string, replaceValue)
      // 21.2.5.11 RegExp.prototype[@@split](string, limit)
      ? function (string, arg) { return rxfn.call(string, this, arg); }
      // 21.2.5.6 RegExp.prototype[@@match](string)
      // 21.2.5.9 RegExp.prototype[@@search](string)
      : function (string) { return rxfn.call(string, this); }
    );
  }
};


/***/ }),

/***/ "230e":
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__("d3f4");
var document = __webpack_require__("7726").document;
// typeof document.createElement is 'object' in old IE
var is = isObject(document) && isObject(document.createElement);
module.exports = function (it) {
  return is ? document.createElement(it) : {};
};


/***/ }),

/***/ "23c6":
/***/ (function(module, exports, __webpack_require__) {

// getting tag from 19.1.3.6 Object.prototype.toString()
var cof = __webpack_require__("2d95");
var TAG = __webpack_require__("2b4c")('toStringTag');
// ES3 wrong here
var ARG = cof(function () { return arguments; }()) == 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (e) { /* empty */ }
};

module.exports = function (it) {
  var O, T, B;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (T = tryGet(O = Object(it), TAG)) == 'string' ? T
    // builtinTag case
    : ARG ? cof(O)
    // ES3 arguments fallback
    : (B = cof(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : B;
};


/***/ }),

/***/ "2621":
/***/ (function(module, exports) {

exports.f = Object.getOwnPropertySymbols;


/***/ }),

/***/ "2aba":
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__("7726");
var hide = __webpack_require__("32e9");
var has = __webpack_require__("69a8");
var SRC = __webpack_require__("ca5a")('src');
var $toString = __webpack_require__("fa5b");
var TO_STRING = 'toString';
var TPL = ('' + $toString).split(TO_STRING);

__webpack_require__("8378").inspectSource = function (it) {
  return $toString.call(it);
};

(module.exports = function (O, key, val, safe) {
  var isFunction = typeof val == 'function';
  if (isFunction) has(val, 'name') || hide(val, 'name', key);
  if (O[key] === val) return;
  if (isFunction) has(val, SRC) || hide(val, SRC, O[key] ? '' + O[key] : TPL.join(String(key)));
  if (O === global) {
    O[key] = val;
  } else if (!safe) {
    delete O[key];
    hide(O, key, val);
  } else if (O[key]) {
    O[key] = val;
  } else {
    hide(O, key, val);
  }
// add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, TO_STRING, function toString() {
  return typeof this == 'function' && this[SRC] || $toString.call(this);
});


/***/ }),

/***/ "2aeb":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.2 / 15.2.3.5 Object.create(O [, Properties])
var anObject = __webpack_require__("cb7c");
var dPs = __webpack_require__("1495");
var enumBugKeys = __webpack_require__("e11e");
var IE_PROTO = __webpack_require__("613b")('IE_PROTO');
var Empty = function () { /* empty */ };
var PROTOTYPE = 'prototype';

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var createDict = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = __webpack_require__("230e")('iframe');
  var i = enumBugKeys.length;
  var lt = '<';
  var gt = '>';
  var iframeDocument;
  iframe.style.display = 'none';
  __webpack_require__("fab2").appendChild(iframe);
  iframe.src = 'javascript:'; // eslint-disable-line no-script-url
  // createDict = iframe.contentWindow.Object;
  // html.removeChild(iframe);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(lt + 'script' + gt + 'document.F=Object' + lt + '/script' + gt);
  iframeDocument.close();
  createDict = iframeDocument.F;
  while (i--) delete createDict[PROTOTYPE][enumBugKeys[i]];
  return createDict();
};

module.exports = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    Empty[PROTOTYPE] = anObject(O);
    result = new Empty();
    Empty[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO] = O;
  } else result = createDict();
  return Properties === undefined ? result : dPs(result, Properties);
};


/***/ }),

/***/ "2b4c":
/***/ (function(module, exports, __webpack_require__) {

var store = __webpack_require__("5537")('wks');
var uid = __webpack_require__("ca5a");
var Symbol = __webpack_require__("7726").Symbol;
var USE_SYMBOL = typeof Symbol == 'function';

var $exports = module.exports = function (name) {
  return store[name] || (store[name] =
    USE_SYMBOL && Symbol[name] || (USE_SYMBOL ? Symbol : uid)('Symbol.' + name));
};

$exports.store = store;


/***/ }),

/***/ "2d00":
/***/ (function(module, exports) {

module.exports = false;


/***/ }),

/***/ "2d95":
/***/ (function(module, exports) {

var toString = {}.toString;

module.exports = function (it) {
  return toString.call(it).slice(8, -1);
};


/***/ }),

/***/ "2fdb":
/***/ (function(module, exports, __webpack_require__) {
// 21.1.3.7 String.prototype.includes(searchString, position = 0)

var $export = __webpack_require__("5ca1");
var context = __webpack_require__("d2c8");
var INCLUDES = 'includes';

$export($export.P + $export.F * __webpack_require__("5147")(INCLUDES), 'String', {
  includes: function includes(searchString /* , position = 0 */) {
    return !!~context(this, searchString, INCLUDES)
      .indexOf(searchString, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),

/***/ "32e9":
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__("86cc");
var createDesc = __webpack_require__("4630");
module.exports = __webpack_require__("9e1e") ? function (object, key, value) {
  return dP.f(object, key, createDesc(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};


/***/ }),

/***/ "38fd":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.9 / 15.2.3.2 Object.getPrototypeOf(O)
var has = __webpack_require__("69a8");
var toObject = __webpack_require__("4bf8");
var IE_PROTO = __webpack_require__("613b")('IE_PROTO');
var ObjectProto = Object.prototype;

module.exports = Object.getPrototypeOf || function (O) {
  O = toObject(O);
  if (has(O, IE_PROTO)) return O[IE_PROTO];
  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  } return O instanceof Object ? ObjectProto : null;
};


/***/ }),

/***/ "41a0":
/***/ (function(module, exports, __webpack_require__) {

var create = __webpack_require__("2aeb");
var descriptor = __webpack_require__("4630");
var setToStringTag = __webpack_require__("7f20");
var IteratorPrototype = {};

// 25.1.2.1.1 %IteratorPrototype%[@@iterator]()
__webpack_require__("32e9")(IteratorPrototype, __webpack_require__("2b4c")('iterator'), function () { return this; });

module.exports = function (Constructor, NAME, next) {
  Constructor.prototype = create(IteratorPrototype, { next: descriptor(1, next) });
  setToStringTag(Constructor, NAME + ' Iterator');
};


/***/ }),

/***/ "456d":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.14 Object.keys(O)
var toObject = __webpack_require__("4bf8");
var $keys = __webpack_require__("0d58");

__webpack_require__("5eda")('keys', function () {
  return function keys(it) {
    return $keys(toObject(it));
  };
});


/***/ }),

/***/ "4588":
/***/ (function(module, exports) {

// 7.1.4 ToInteger
var ceil = Math.ceil;
var floor = Math.floor;
module.exports = function (it) {
  return isNaN(it = +it) ? 0 : (it > 0 ? floor : ceil)(it);
};


/***/ }),

/***/ "4630":
/***/ (function(module, exports) {

module.exports = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};


/***/ }),

/***/ "4bf8":
/***/ (function(module, exports, __webpack_require__) {

// 7.1.13 ToObject(argument)
var defined = __webpack_require__("be13");
module.exports = function (it) {
  return Object(defined(it));
};


/***/ }),

/***/ "5147":
/***/ (function(module, exports, __webpack_require__) {

var MATCH = __webpack_require__("2b4c")('match');
module.exports = function (KEY) {
  var re = /./;
  try {
    '/./'[KEY](re);
  } catch (e) {
    try {
      re[MATCH] = false;
      return !'/./'[KEY](re);
    } catch (f) { /* empty */ }
  } return true;
};


/***/ }),

/***/ "520a":
/***/ (function(module, exports, __webpack_require__) {


var regexpFlags = __webpack_require__("0bfb");

var nativeExec = RegExp.prototype.exec;
// This always refers to the native implementation, because the
// String#replace polyfill uses ./fix-regexp-well-known-symbol-logic.js,
// which loads this file before patching the method.
var nativeReplace = String.prototype.replace;

var patchedExec = nativeExec;

var LAST_INDEX = 'lastIndex';

var UPDATES_LAST_INDEX_WRONG = (function () {
  var re1 = /a/,
      re2 = /b*/g;
  nativeExec.call(re1, 'a');
  nativeExec.call(re2, 'a');
  return re1[LAST_INDEX] !== 0 || re2[LAST_INDEX] !== 0;
})();

// nonparticipating capturing group, copied from es5-shim's String#split patch.
var NPCG_INCLUDED = /()??/.exec('')[1] !== undefined;

var PATCH = UPDATES_LAST_INDEX_WRONG || NPCG_INCLUDED;

if (PATCH) {
  patchedExec = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;

    if (NPCG_INCLUDED) {
      reCopy = new RegExp('^' + re.source + '$(?!\\s)', regexpFlags.call(re));
    }
    if (UPDATES_LAST_INDEX_WRONG) lastIndex = re[LAST_INDEX];

    match = nativeExec.call(re, str);

    if (UPDATES_LAST_INDEX_WRONG && match) {
      re[LAST_INDEX] = re.global ? match.index + match[0].length : lastIndex;
    }
    if (NPCG_INCLUDED && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      // eslint-disable-next-line no-loop-func
      nativeReplace.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

module.exports = patchedExec;


/***/ }),

/***/ "52a7":
/***/ (function(module, exports) {

exports.f = {}.propertyIsEnumerable;


/***/ }),

/***/ "5537":
/***/ (function(module, exports, __webpack_require__) {

var core = __webpack_require__("8378");
var global = __webpack_require__("7726");
var SHARED = '__core-js_shared__';
var store = global[SHARED] || (global[SHARED] = {});

(module.exports = function (key, value) {
  return store[key] || (store[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: core.version,
  mode: __webpack_require__("2d00") ? 'pure' : 'global',
  copyright: '© 2019 Denis Pushkarev (zloirock.ru)'
});


/***/ }),

/***/ "5ca1":
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__("7726");
var core = __webpack_require__("8378");
var hide = __webpack_require__("32e9");
var redefine = __webpack_require__("2aba");
var ctx = __webpack_require__("9b43");
var PROTOTYPE = 'prototype';

var $export = function (type, name, source) {
  var IS_FORCED = type & $export.F;
  var IS_GLOBAL = type & $export.G;
  var IS_STATIC = type & $export.S;
  var IS_PROTO = type & $export.P;
  var IS_BIND = type & $export.B;
  var target = IS_GLOBAL ? global : IS_STATIC ? global[name] || (global[name] = {}) : (global[name] || {})[PROTOTYPE];
  var exports = IS_GLOBAL ? core : core[name] || (core[name] = {});
  var expProto = exports[PROTOTYPE] || (exports[PROTOTYPE] = {});
  var key, own, out, exp;
  if (IS_GLOBAL) source = name;
  for (key in source) {
    // contains in native
    own = !IS_FORCED && target && target[key] !== undefined;
    // export native or passed
    out = (own ? target : source)[key];
    // bind timers to global for call from export context
    exp = IS_BIND && own ? ctx(out, global) : IS_PROTO && typeof out == 'function' ? ctx(Function.call, out) : out;
    // extend global
    if (target) redefine(target, key, out, type & $export.U);
    // export
    if (exports[key] != out) hide(exports, key, exp);
    if (IS_PROTO && expProto[key] != out) expProto[key] = out;
  }
};
global.core = core;
// type bitmap
$export.F = 1;   // forced
$export.G = 2;   // global
$export.S = 4;   // static
$export.P = 8;   // proto
$export.B = 16;  // bind
$export.W = 32;  // wrap
$export.U = 64;  // safe
$export.R = 128; // real proto method for `library`
module.exports = $export;


/***/ }),

/***/ "5eda":
/***/ (function(module, exports, __webpack_require__) {

// most Object methods by ES6 should accept primitives
var $export = __webpack_require__("5ca1");
var core = __webpack_require__("8378");
var fails = __webpack_require__("79e5");
module.exports = function (KEY, exec) {
  var fn = (core.Object || {})[KEY] || Object[KEY];
  var exp = {};
  exp[KEY] = exec(fn);
  $export($export.S + $export.F * fails(function () { fn(1); }), 'Object', exp);
};


/***/ }),

/***/ "5f1b":
/***/ (function(module, exports, __webpack_require__) {


var classof = __webpack_require__("23c6");
var builtinExec = RegExp.prototype.exec;

 // `RegExpExec` abstract operation
// https://tc39.github.io/ecma262/#sec-regexpexec
module.exports = function (R, S) {
  var exec = R.exec;
  if (typeof exec === 'function') {
    var result = exec.call(R, S);
    if (typeof result !== 'object') {
      throw new TypeError('RegExp exec method returned something other than an Object or null');
    }
    return result;
  }
  if (classof(R) !== 'RegExp') {
    throw new TypeError('RegExp#exec called on incompatible receiver');
  }
  return builtinExec.call(R, S);
};


/***/ }),

/***/ "613b":
/***/ (function(module, exports, __webpack_require__) {

var shared = __webpack_require__("5537")('keys');
var uid = __webpack_require__("ca5a");
module.exports = function (key) {
  return shared[key] || (shared[key] = uid(key));
};


/***/ }),

/***/ "626a":
/***/ (function(module, exports, __webpack_require__) {

// fallback for non-array-like ES3 and non-enumerable old V8 strings
var cof = __webpack_require__("2d95");
// eslint-disable-next-line no-prototype-builtins
module.exports = Object('z').propertyIsEnumerable(0) ? Object : function (it) {
  return cof(it) == 'String' ? it.split('') : Object(it);
};


/***/ }),

/***/ "6762":
/***/ (function(module, exports, __webpack_require__) {

// https://github.com/tc39/Array.prototype.includes
var $export = __webpack_require__("5ca1");
var $includes = __webpack_require__("c366")(true);

$export($export.P, 'Array', {
  includes: function includes(el /* , fromIndex = 0 */) {
    return $includes(this, el, arguments.length > 1 ? arguments[1] : undefined);
  }
});

__webpack_require__("9c6c")('includes');


/***/ }),

/***/ "6821":
/***/ (function(module, exports, __webpack_require__) {

// to indexed object, toObject with fallback for non-array-like ES3 strings
var IObject = __webpack_require__("626a");
var defined = __webpack_require__("be13");
module.exports = function (it) {
  return IObject(defined(it));
};


/***/ }),

/***/ "69a8":
/***/ (function(module, exports) {

var hasOwnProperty = {}.hasOwnProperty;
module.exports = function (it, key) {
  return hasOwnProperty.call(it, key);
};


/***/ }),

/***/ "6a99":
/***/ (function(module, exports, __webpack_require__) {

// 7.1.1 ToPrimitive(input [, PreferredType])
var isObject = __webpack_require__("d3f4");
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string
module.exports = function (it, S) {
  if (!isObject(it)) return it;
  var fn, val;
  if (S && typeof (fn = it.toString) == 'function' && !isObject(val = fn.call(it))) return val;
  if (typeof (fn = it.valueOf) == 'function' && !isObject(val = fn.call(it))) return val;
  if (!S && typeof (fn = it.toString) == 'function' && !isObject(val = fn.call(it))) return val;
  throw TypeError("Can't convert object to primitive value");
};


/***/ }),

/***/ "7333":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.1 Object.assign(target, source, ...)
var DESCRIPTORS = __webpack_require__("9e1e");
var getKeys = __webpack_require__("0d58");
var gOPS = __webpack_require__("2621");
var pIE = __webpack_require__("52a7");
var toObject = __webpack_require__("4bf8");
var IObject = __webpack_require__("626a");
var $assign = Object.assign;

// should work with symbols and should have deterministic property order (V8 bug)
module.exports = !$assign || __webpack_require__("79e5")(function () {
  var A = {};
  var B = {};
  // eslint-disable-next-line no-undef
  var S = Symbol();
  var K = 'abcdefghijklmnopqrst';
  A[S] = 7;
  K.split('').forEach(function (k) { B[k] = k; });
  return $assign({}, A)[S] != 7 || Object.keys($assign({}, B)).join('') != K;
}) ? function assign(target, source) { // eslint-disable-line no-unused-vars
  var T = toObject(target);
  var aLen = arguments.length;
  var index = 1;
  var getSymbols = gOPS.f;
  var isEnum = pIE.f;
  while (aLen > index) {
    var S = IObject(arguments[index++]);
    var keys = getSymbols ? getKeys(S).concat(getSymbols(S)) : getKeys(S);
    var length = keys.length;
    var j = 0;
    var key;
    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS || isEnum.call(S, key)) T[key] = S[key];
    }
  } return T;
} : $assign;


/***/ }),

/***/ "7726":
/***/ (function(module, exports) {

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
var global = module.exports = typeof window != 'undefined' && window.Math == Math
  ? window : typeof self != 'undefined' && self.Math == Math ? self
  // eslint-disable-next-line no-new-func
  : Function('return this')();
if (typeof __g == 'number') __g = global; // eslint-disable-line no-undef


/***/ }),

/***/ "77f1":
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__("4588");
var max = Math.max;
var min = Math.min;
module.exports = function (index, length) {
  index = toInteger(index);
  return index < 0 ? max(index + length, 0) : min(index, length);
};


/***/ }),

/***/ "79e5":
/***/ (function(module, exports) {

module.exports = function (exec) {
  try {
    return !!exec();
  } catch (e) {
    return true;
  }
};


/***/ }),

/***/ "7f20":
/***/ (function(module, exports, __webpack_require__) {

var def = __webpack_require__("86cc").f;
var has = __webpack_require__("69a8");
var TAG = __webpack_require__("2b4c")('toStringTag');

module.exports = function (it, tag, stat) {
  if (it && !has(it = stat ? it : it.prototype, TAG)) def(it, TAG, { configurable: true, value: tag });
};


/***/ }),

/***/ "8378":
/***/ (function(module, exports) {

var core = module.exports = { version: '2.6.11' };
if (typeof __e == 'number') __e = core; // eslint-disable-line no-undef


/***/ }),

/***/ "84f2":
/***/ (function(module, exports) {

module.exports = {};


/***/ }),

/***/ "86cc":
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__("cb7c");
var IE8_DOM_DEFINE = __webpack_require__("c69a");
var toPrimitive = __webpack_require__("6a99");
var dP = Object.defineProperty;

exports.f = __webpack_require__("9e1e") ? Object.defineProperty : function defineProperty(O, P, Attributes) {
  anObject(O);
  P = toPrimitive(P, true);
  anObject(Attributes);
  if (IE8_DOM_DEFINE) try {
    return dP(O, P, Attributes);
  } catch (e) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported!');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};


/***/ }),

/***/ "9b43":
/***/ (function(module, exports, __webpack_require__) {

// optional / simple context binding
var aFunction = __webpack_require__("d8e8");
module.exports = function (fn, that, length) {
  aFunction(fn);
  if (that === undefined) return fn;
  switch (length) {
    case 1: return function (a) {
      return fn.call(that, a);
    };
    case 2: return function (a, b) {
      return fn.call(that, a, b);
    };
    case 3: return function (a, b, c) {
      return fn.call(that, a, b, c);
    };
  }
  return function (/* ...args */) {
    return fn.apply(that, arguments);
  };
};


/***/ }),

/***/ "9c6c":
/***/ (function(module, exports, __webpack_require__) {

// 22.1.3.31 Array.prototype[@@unscopables]
var UNSCOPABLES = __webpack_require__("2b4c")('unscopables');
var ArrayProto = Array.prototype;
if (ArrayProto[UNSCOPABLES] == undefined) __webpack_require__("32e9")(ArrayProto, UNSCOPABLES, {});
module.exports = function (key) {
  ArrayProto[UNSCOPABLES][key] = true;
};


/***/ }),

/***/ "9def":
/***/ (function(module, exports, __webpack_require__) {

// 7.1.15 ToLength
var toInteger = __webpack_require__("4588");
var min = Math.min;
module.exports = function (it) {
  return it > 0 ? min(toInteger(it), 0x1fffffffffffff) : 0; // pow(2, 53) - 1 == 9007199254740991
};


/***/ }),

/***/ "9e1e":
/***/ (function(module, exports, __webpack_require__) {

// Thank's IE8 for his funny defineProperty
module.exports = !__webpack_require__("79e5")(function () {
  return Object.defineProperty({}, 'a', { get: function () { return 7; } }).a != 7;
});


/***/ }),

/***/ "a352":
/***/ (function(module, exports) {

module.exports = Sortable;

/***/ }),

/***/ "a481":
/***/ (function(module, exports, __webpack_require__) {


var anObject = __webpack_require__("cb7c");
var toObject = __webpack_require__("4bf8");
var toLength = __webpack_require__("9def");
var toInteger = __webpack_require__("4588");
var advanceStringIndex = __webpack_require__("0390");
var regExpExec = __webpack_require__("5f1b");
var max = Math.max;
var min = Math.min;
var floor = Math.floor;
var SUBSTITUTION_SYMBOLS = /\$([$&`']|\d\d?|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED = /\$([$&`']|\d\d?)/g;

var maybeToString = function (it) {
  return it === undefined ? it : String(it);
};

// @@replace logic
__webpack_require__("214f")('replace', 2, function (defined, REPLACE, $replace, maybeCallNative) {
  return [
    // `String.prototype.replace` method
    // https://tc39.github.io/ecma262/#sec-string.prototype.replace
    function replace(searchValue, replaceValue) {
      var O = defined(this);
      var fn = searchValue == undefined ? undefined : searchValue[REPLACE];
      return fn !== undefined
        ? fn.call(searchValue, O, replaceValue)
        : $replace.call(String(O), searchValue, replaceValue);
    },
    // `RegExp.prototype[@@replace]` method
    // https://tc39.github.io/ecma262/#sec-regexp.prototype-@@replace
    function (regexp, replaceValue) {
      var res = maybeCallNative($replace, regexp, this, replaceValue);
      if (res.done) return res.value;

      var rx = anObject(regexp);
      var S = String(this);
      var functionalReplace = typeof replaceValue === 'function';
      if (!functionalReplace) replaceValue = String(replaceValue);
      var global = rx.global;
      if (global) {
        var fullUnicode = rx.unicode;
        rx.lastIndex = 0;
      }
      var results = [];
      while (true) {
        var result = regExpExec(rx, S);
        if (result === null) break;
        results.push(result);
        if (!global) break;
        var matchStr = String(result[0]);
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
      }
      var accumulatedResult = '';
      var nextSourcePosition = 0;
      for (var i = 0; i < results.length; i++) {
        result = results[i];
        var matched = String(result[0]);
        var position = max(min(toInteger(result.index), S.length), 0);
        var captures = [];
        // NOTE: This is equivalent to
        //   captures = result.slice(1).map(maybeToString)
        // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
        // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
        // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.
        for (var j = 1; j < result.length; j++) captures.push(maybeToString(result[j]));
        var namedCaptures = result.groups;
        if (functionalReplace) {
          var replacerArgs = [matched].concat(captures, position, S);
          if (namedCaptures !== undefined) replacerArgs.push(namedCaptures);
          var replacement = String(replaceValue.apply(undefined, replacerArgs));
        } else {
          replacement = getSubstitution(matched, S, position, captures, namedCaptures, replaceValue);
        }
        if (position >= nextSourcePosition) {
          accumulatedResult += S.slice(nextSourcePosition, position) + replacement;
          nextSourcePosition = position + matched.length;
        }
      }
      return accumulatedResult + S.slice(nextSourcePosition);
    }
  ];

    // https://tc39.github.io/ecma262/#sec-getsubstitution
  function getSubstitution(matched, str, position, captures, namedCaptures, replacement) {
    var tailPos = position + matched.length;
    var m = captures.length;
    var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED;
    if (namedCaptures !== undefined) {
      namedCaptures = toObject(namedCaptures);
      symbols = SUBSTITUTION_SYMBOLS;
    }
    return $replace.call(replacement, symbols, function (match, ch) {
      var capture;
      switch (ch.charAt(0)) {
        case '$': return '$';
        case '&': return matched;
        case '`': return str.slice(0, position);
        case "'": return str.slice(tailPos);
        case '<':
          capture = namedCaptures[ch.slice(1, -1)];
          break;
        default: // \d\d?
          var n = +ch;
          if (n === 0) return match;
          if (n > m) {
            var f = floor(n / 10);
            if (f === 0) return match;
            if (f <= m) return captures[f - 1] === undefined ? ch.charAt(1) : captures[f - 1] + ch.charAt(1);
            return match;
          }
          capture = captures[n - 1];
      }
      return capture === undefined ? '' : capture;
    });
  }
});


/***/ }),

/***/ "aae3":
/***/ (function(module, exports, __webpack_require__) {

// 7.2.8 IsRegExp(argument)
var isObject = __webpack_require__("d3f4");
var cof = __webpack_require__("2d95");
var MATCH = __webpack_require__("2b4c")('match');
module.exports = function (it) {
  var isRegExp;
  return isObject(it) && ((isRegExp = it[MATCH]) !== undefined ? !!isRegExp : cof(it) == 'RegExp');
};


/***/ }),

/***/ "ac6a":
/***/ (function(module, exports, __webpack_require__) {

var $iterators = __webpack_require__("cadf");
var getKeys = __webpack_require__("0d58");
var redefine = __webpack_require__("2aba");
var global = __webpack_require__("7726");
var hide = __webpack_require__("32e9");
var Iterators = __webpack_require__("84f2");
var wks = __webpack_require__("2b4c");
var ITERATOR = wks('iterator');
var TO_STRING_TAG = wks('toStringTag');
var ArrayValues = Iterators.Array;

var DOMIterables = {
  CSSRuleList: true, // TODO: Not spec compliant, should be false.
  CSSStyleDeclaration: false,
  CSSValueList: false,
  ClientRectList: false,
  DOMRectList: false,
  DOMStringList: false,
  DOMTokenList: true,
  DataTransferItemList: false,
  FileList: false,
  HTMLAllCollection: false,
  HTMLCollection: false,
  HTMLFormElement: false,
  HTMLSelectElement: false,
  MediaList: true, // TODO: Not spec compliant, should be false.
  MimeTypeArray: false,
  NamedNodeMap: false,
  NodeList: true,
  PaintRequestList: false,
  Plugin: false,
  PluginArray: false,
  SVGLengthList: false,
  SVGNumberList: false,
  SVGPathSegList: false,
  SVGPointList: false,
  SVGStringList: false,
  SVGTransformList: false,
  SourceBufferList: false,
  StyleSheetList: true, // TODO: Not spec compliant, should be false.
  TextTrackCueList: false,
  TextTrackList: false,
  TouchList: false
};

for (var collections = getKeys(DOMIterables), i = 0; i < collections.length; i++) {
  var NAME = collections[i];
  var explicit = DOMIterables[NAME];
  var Collection = global[NAME];
  var proto = Collection && Collection.prototype;
  var key;
  if (proto) {
    if (!proto[ITERATOR]) hide(proto, ITERATOR, ArrayValues);
    if (!proto[TO_STRING_TAG]) hide(proto, TO_STRING_TAG, NAME);
    Iterators[NAME] = ArrayValues;
    if (explicit) for (key in $iterators) if (!proto[key]) redefine(proto, key, $iterators[key], true);
  }
}


/***/ }),

/***/ "b0c5":
/***/ (function(module, exports, __webpack_require__) {

var regexpExec = __webpack_require__("520a");
__webpack_require__("5ca1")({
  target: 'RegExp',
  proto: true,
  forced: regexpExec !== /./.exec
}, {
  exec: regexpExec
});


/***/ }),

/***/ "be13":
/***/ (function(module, exports) {

// 7.2.1 RequireObjectCoercible(argument)
module.exports = function (it) {
  if (it == undefined) throw TypeError("Can't call method on  " + it);
  return it;
};


/***/ }),

/***/ "c366":
/***/ (function(module, exports, __webpack_require__) {

// false -> Array#indexOf
// true  -> Array#includes
var toIObject = __webpack_require__("6821");
var toLength = __webpack_require__("9def");
var toAbsoluteIndex = __webpack_require__("77f1");
module.exports = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIObject($this);
    var length = toLength(O.length);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare
    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare
      if (value != value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) if (IS_INCLUDES || index in O) {
      if (O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};


/***/ }),

/***/ "c649":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* WEBPACK VAR INJECTION */(function(global) {/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "c", function() { return insertNodeAt; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return camelize; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return console; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "d", function() { return removeNode; });
/* harmony import */ var core_js_modules_es6_regexp_replace__WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__("a481");


function getConsole() {
  if (typeof window !== "undefined") {
    return window.console;
  }

  return global.console;
}

var console = getConsole();

function cached(fn) {
  var cache = Object.create(null);
  return function cachedFn(str) {
    var hit = cache[str];
    return hit || (cache[str] = fn(str));
  };
}

var regex = /-(\w)/g;
var camelize = cached(function (str) {
  return str.replace(regex, function (_, c) {
    return c ? c.toUpperCase() : "";
  });
});

function removeNode(node) {
  if (node.parentElement !== null) {
    node.parentElement.removeChild(node);
  }
}

function insertNodeAt(fatherNode, node, position) {
  var refNode = position === 0 ? fatherNode.children[0] : fatherNode.children[position - 1].nextSibling;
  fatherNode.insertBefore(node, refNode);
}


/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__("c8ba")));

/***/ }),

/***/ "c69a":
/***/ (function(module, exports, __webpack_require__) {

module.exports = !__webpack_require__("9e1e") && !__webpack_require__("79e5")(function () {
  return Object.defineProperty(__webpack_require__("230e")('div'), 'a', { get: function () { return 7; } }).a != 7;
});


/***/ }),

/***/ "c8ba":
/***/ (function(module, exports) {

var g;

// This works in non-strict mode
g = (function() {
	return this;
})();

try {
	// This works if eval is allowed (see CSP)
	g = g || new Function("return this")();
} catch (e) {
	// This works if the window reference is available
	if (typeof window === "object") g = window;
}

// g can still be undefined, but nothing to do about it...
// We return undefined, instead of nothing here, so it's
// easier to handle this case. if(!global) { ...}

module.exports = g;


/***/ }),

/***/ "ca5a":
/***/ (function(module, exports) {

var id = 0;
var px = Math.random();
module.exports = function (key) {
  return 'Symbol('.concat(key === undefined ? '' : key, ')_', (++id + px).toString(36));
};


/***/ }),

/***/ "cadf":
/***/ (function(module, exports, __webpack_require__) {

var addToUnscopables = __webpack_require__("9c6c");
var step = __webpack_require__("d53b");
var Iterators = __webpack_require__("84f2");
var toIObject = __webpack_require__("6821");

// 22.1.3.4 Array.prototype.entries()
// 22.1.3.13 Array.prototype.keys()
// 22.1.3.29 Array.prototype.values()
// 22.1.3.30 Array.prototype[@@iterator]()
module.exports = __webpack_require__("01f9")(Array, 'Array', function (iterated, kind) {
  this._t = toIObject(iterated); // target
  this._i = 0;                   // next index
  this._k = kind;                // kind
// 22.1.5.2.1 %ArrayIteratorPrototype%.next()
}, function () {
  var O = this._t;
  var kind = this._k;
  var index = this._i++;
  if (!O || index >= O.length) {
    this._t = undefined;
    return step(1);
  }
  if (kind == 'keys') return step(0, index);
  if (kind == 'values') return step(0, O[index]);
  return step(0, [index, O[index]]);
}, 'values');

// argumentsList[@@iterator] is %ArrayProto_values% (9.4.4.6, 9.4.4.7)
Iterators.Arguments = Iterators.Array;

addToUnscopables('keys');
addToUnscopables('values');
addToUnscopables('entries');


/***/ }),

/***/ "cb7c":
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__("d3f4");
module.exports = function (it) {
  if (!isObject(it)) throw TypeError(it + ' is not an object!');
  return it;
};


/***/ }),

/***/ "ce10":
/***/ (function(module, exports, __webpack_require__) {

var has = __webpack_require__("69a8");
var toIObject = __webpack_require__("6821");
var arrayIndexOf = __webpack_require__("c366")(false);
var IE_PROTO = __webpack_require__("613b")('IE_PROTO');

module.exports = function (object, names) {
  var O = toIObject(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) if (key != IE_PROTO) has(O, key) && result.push(key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (has(O, key = names[i++])) {
    ~arrayIndexOf(result, key) || result.push(key);
  }
  return result;
};


/***/ }),

/***/ "d2c8":
/***/ (function(module, exports, __webpack_require__) {

// helper for String#{startsWith, endsWith, includes}
var isRegExp = __webpack_require__("aae3");
var defined = __webpack_require__("be13");

module.exports = function (that, searchString, NAME) {
  if (isRegExp(searchString)) throw TypeError('String#' + NAME + " doesn't accept regex!");
  return String(defined(that));
};


/***/ }),

/***/ "d3f4":
/***/ (function(module, exports) {

module.exports = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};


/***/ }),

/***/ "d53b":
/***/ (function(module, exports) {

module.exports = function (done, value) {
  return { value: value, done: !!done };
};


/***/ }),

/***/ "d8e8":
/***/ (function(module, exports) {

module.exports = function (it) {
  if (typeof it != 'function') throw TypeError(it + ' is not a function!');
  return it;
};


/***/ }),

/***/ "e11e":
/***/ (function(module, exports) {

// IE 8- don't enum bug keys
module.exports = (
  'constructor,hasOwnProperty,isPrototypeOf,propertyIsEnumerable,toLocaleString,toString,valueOf'
).split(',');


/***/ }),

/***/ "f559":
/***/ (function(module, exports, __webpack_require__) {
// 21.1.3.18 String.prototype.startsWith(searchString [, position ])

var $export = __webpack_require__("5ca1");
var toLength = __webpack_require__("9def");
var context = __webpack_require__("d2c8");
var STARTS_WITH = 'startsWith';
var $startsWith = ''[STARTS_WITH];

$export($export.P + $export.F * __webpack_require__("5147")(STARTS_WITH), 'String', {
  startsWith: function startsWith(searchString /* , position = 0 */) {
    var that = context(this, searchString, STARTS_WITH);
    var index = toLength(Math.min(arguments.length > 1 ? arguments[1] : undefined, that.length));
    var search = String(searchString);
    return $startsWith
      ? $startsWith.call(that, search, index)
      : that.slice(index, index + search.length) === search;
  }
});


/***/ }),

/***/ "f6fd":
/***/ (function(module, exports) {

// document.currentScript polyfill by Adam Miller

// MIT license

(function(document){
  var currentScript = "currentScript",
      scripts = document.getElementsByTagName('script'); // Live NodeList collection

  // If browser needs currentScript polyfill, add get currentScript() to the document object
  if (!(currentScript in document)) {
    Object.defineProperty(document, currentScript, {
      get: function(){

        // IE 6-10 supports script readyState
        // IE 10+ support stack trace
        try { throw new Error(); }
        catch (err) {

          // Find the second match for the "at" string to get file src url from stack.
          // Specifically works with the format of stack traces in IE.
          var i, res = ((/.*at [^\(]*\((.*):.+:.+\)$/ig).exec(err.stack) || [false])[1];

          // For all scripts on the page, if src matches or if ready state is interactive, return the script tag
          for(i in scripts){
            if(scripts[i].src == res || scripts[i].readyState == "interactive"){
              return scripts[i];
            }
          }

          // If no match, return null
          return null;
        }
      }
    });
  }
})(document);


/***/ }),

/***/ "f751":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.3.1 Object.assign(target, source)
var $export = __webpack_require__("5ca1");

$export($export.S + $export.F, 'Object', { assign: __webpack_require__("7333") });


/***/ }),

/***/ "fa5b":
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__("5537")('native-function-to-string', Function.toString);


/***/ }),

/***/ "fab2":
/***/ (function(module, exports, __webpack_require__) {

var document = __webpack_require__("7726").document;
module.exports = document && document.documentElement;


/***/ }),

/***/ "fb15":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
// ESM COMPAT FLAG
__webpack_require__.r(__webpack_exports__);

// CONCATENATED MODULE: ./node_modules/@vue/cli-service/lib/commands/build/setPublicPath.js
// This file is imported into lib/wc client bundles.

if (typeof window !== 'undefined') {
  {
    __webpack_require__("f6fd");
  }

  var setPublicPath_i;
  if ((setPublicPath_i = window.document.currentScript) && (setPublicPath_i = setPublicPath_i.src.match(/(.+\/)[^/]+\.js(\?.*)?$/))) {
    __webpack_require__.p = setPublicPath_i[1]; // eslint-disable-line
  }
}

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.object.assign.js
var es6_object_assign = __webpack_require__("f751");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.string.starts-with.js
var es6_string_starts_with = __webpack_require__("f559");

// EXTERNAL MODULE: ./node_modules/core-js/modules/web.dom.iterable.js
var web_dom_iterable = __webpack_require__("ac6a");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.array.iterator.js
var es6_array_iterator = __webpack_require__("cadf");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.object.keys.js
var es6_object_keys = __webpack_require__("456d");

// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/arrayWithHoles.js
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/iterableToArrayLimit.js
function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/arrayLikeToArray.js
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/unsupportedIterableToArray.js

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/nonIterableRest.js
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/slicedToArray.js




function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
// EXTERNAL MODULE: ./node_modules/core-js/modules/es7.array.includes.js
var es7_array_includes = __webpack_require__("6762");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.string.includes.js
var es6_string_includes = __webpack_require__("2fdb");

// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/arrayWithoutHoles.js

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/iterableToArray.js
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter);
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/nonIterableSpread.js
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
// CONCATENATED MODULE: ./node_modules/@babel/runtime/helpers/esm/toConsumableArray.js




function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
// EXTERNAL MODULE: external {"commonjs":"sortablejs","commonjs2":"sortablejs","amd":"sortablejs","root":"Sortable"}
var external_commonjs_sortablejs_commonjs2_sortablejs_amd_sortablejs_root_Sortable_ = __webpack_require__("a352");
var external_commonjs_sortablejs_commonjs2_sortablejs_amd_sortablejs_root_Sortable_default = /*#__PURE__*/__webpack_require__.n(external_commonjs_sortablejs_commonjs2_sortablejs_amd_sortablejs_root_Sortable_);

// EXTERNAL MODULE: ./src/util/helper.js
var helper = __webpack_require__("c649");

// CONCATENATED MODULE: ./src/vuedraggable.js












function buildAttribute(object, propName, value) {
  if (value === undefined) {
    return object;
  }

  object = object || {};
  object[propName] = value;
  return object;
}

function computeVmIndex(vnodes, element) {
  return vnodes.map(function (elt) {
    return elt.elm;
  }).indexOf(element);
}

function _computeIndexes(slots, children, isTransition, footerOffset) {
  if (!slots) {
    return [];
  }

  var elmFromNodes = slots.map(function (elt) {
    return elt.elm;
  });
  var footerIndex = children.length - footerOffset;

  var rawIndexes = _toConsumableArray(children).map(function (elt, idx) {
    return idx >= footerIndex ? elmFromNodes.length : elmFromNodes.indexOf(elt);
  });

  return isTransition ? rawIndexes.filter(function (ind) {
    return ind !== -1;
  }) : rawIndexes;
}

function emit(evtName, evtData) {
  var _this = this;

  this.$nextTick(function () {
    return _this.$emit(evtName.toLowerCase(), evtData);
  });
}

function delegateAndEmit(evtName) {
  var _this2 = this;

  return function (evtData) {
    if (_this2.realList !== null) {
      _this2["onDrag" + evtName](evtData);
    }

    emit.call(_this2, evtName, evtData);
  };
}

function isTransitionName(name) {
  return ["transition-group", "TransitionGroup"].includes(name);
}

function vuedraggable_isTransition(slots) {
  if (!slots || slots.length !== 1) {
    return false;
  }

  var _slots = _slicedToArray(slots, 1),
      componentOptions = _slots[0].componentOptions;

  if (!componentOptions) {
    return false;
  }

  return isTransitionName(componentOptions.tag);
}

function getSlot(slot, scopedSlot, key) {
  return slot[key] || (scopedSlot[key] ? scopedSlot[key]() : undefined);
}

function computeChildrenAndOffsets(children, slot, scopedSlot) {
  var headerOffset = 0;
  var footerOffset = 0;
  var header = getSlot(slot, scopedSlot, "header");

  if (header) {
    headerOffset = header.length;
    children = children ? [].concat(_toConsumableArray(header), _toConsumableArray(children)) : _toConsumableArray(header);
  }

  var footer = getSlot(slot, scopedSlot, "footer");

  if (footer) {
    footerOffset = footer.length;
    children = children ? [].concat(_toConsumableArray(children), _toConsumableArray(footer)) : _toConsumableArray(footer);
  }

  return {
    children: children,
    headerOffset: headerOffset,
    footerOffset: footerOffset
  };
}

function getComponentAttributes($attrs, componentData) {
  var attributes = null;

  var update = function update(name, value) {
    attributes = buildAttribute(attributes, name, value);
  };

  var attrs = Object.keys($attrs).filter(function (key) {
    return key === "id" || key.startsWith("data-");
  }).reduce(function (res, key) {
    res[key] = $attrs[key];
    return res;
  }, {});
  update("attrs", attrs);

  if (!componentData) {
    return attributes;
  }

  var on = componentData.on,
      props = componentData.props,
      componentDataAttrs = componentData.attrs;
  update("on", on);
  update("props", props);
  Object.assign(attributes.attrs, componentDataAttrs);
  return attributes;
}

var eventsListened = ["Start", "Add", "Remove", "Update", "End"];
var eventsToEmit = ["Choose", "Unchoose", "Sort", "Filter", "Clone"];
var readonlyProperties = ["Move"].concat(eventsListened, eventsToEmit).map(function (evt) {
  return "on" + evt;
});
var draggingElement = null;
var props = {
  options: Object,
  list: {
    type: Array,
    required: false,
    default: null
  },
  value: {
    type: Array,
    required: false,
    default: null
  },
  noTransitionOnDrag: {
    type: Boolean,
    default: false
  },
  clone: {
    type: Function,
    default: function _default(original) {
      return original;
    }
  },
  element: {
    type: String,
    default: "div"
  },
  tag: {
    type: String,
    default: null
  },
  move: {
    type: Function,
    default: null
  },
  componentData: {
    type: Object,
    required: false,
    default: null
  }
};
var draggableComponent = {
  name: "draggable",
  inheritAttrs: false,
  props: props,
  data: function data() {
    return {
      transitionMode: false,
      noneFunctionalComponentMode: false
    };
  },
  render: function render(h) {
    var slots = this.$slots.default;
    this.transitionMode = vuedraggable_isTransition(slots);

    var _computeChildrenAndOf = computeChildrenAndOffsets(slots, this.$slots, this.$scopedSlots),
        children = _computeChildrenAndOf.children,
        headerOffset = _computeChildrenAndOf.headerOffset,
        footerOffset = _computeChildrenAndOf.footerOffset;

    this.headerOffset = headerOffset;
    this.footerOffset = footerOffset;
    var attributes = getComponentAttributes(this.$attrs, this.componentData);
    return h(this.getTag(), attributes, children);
  },
  created: function created() {
    if (this.list !== null && this.value !== null) {
      helper["b" /* console */].error("Value and list props are mutually exclusive! Please set one or another.");
    }

    if (this.element !== "div") {
      helper["b" /* console */].warn("Element props is deprecated please use tag props instead. See https://github.com/SortableJS/Vue.Draggable/blob/master/documentation/migrate.md#element-props");
    }

    if (this.options !== undefined) {
      helper["b" /* console */].warn("Options props is deprecated, add sortable options directly as vue.draggable item, or use v-bind. See https://github.com/SortableJS/Vue.Draggable/blob/master/documentation/migrate.md#options-props");
    }
  },
  mounted: function mounted() {
    var _this3 = this;

    this.noneFunctionalComponentMode = this.getTag().toLowerCase() !== this.$el.nodeName.toLowerCase() && !this.getIsFunctional();

    if (this.noneFunctionalComponentMode && this.transitionMode) {
      throw new Error("Transition-group inside component is not supported. Please alter tag value or remove transition-group. Current tag value: ".concat(this.getTag()));
    }

    var optionsAdded = {};
    eventsListened.forEach(function (elt) {
      optionsAdded["on" + elt] = delegateAndEmit.call(_this3, elt);
    });
    eventsToEmit.forEach(function (elt) {
      optionsAdded["on" + elt] = emit.bind(_this3, elt);
    });
    var attributes = Object.keys(this.$attrs).reduce(function (res, key) {
      res[Object(helper["a" /* camelize */])(key)] = _this3.$attrs[key];
      return res;
    }, {});
    var options = Object.assign({}, this.options, attributes, optionsAdded, {
      onMove: function onMove(evt, originalEvent) {
        return _this3.onDragMove(evt, originalEvent);
      }
    });
    !("draggable" in options) && (options.draggable = ">*");
    this._sortable = new external_commonjs_sortablejs_commonjs2_sortablejs_amd_sortablejs_root_Sortable_default.a(this.rootContainer, options);
    this.computeIndexes();
  },
  beforeDestroy: function beforeDestroy() {
    if (this._sortable !== undefined) this._sortable.destroy();
  },
  computed: {
    rootContainer: function rootContainer() {
      return this.transitionMode ? this.$el.children[0] : this.$el;
    },
    realList: function realList() {
      return this.list ? this.list : this.value;
    }
  },
  watch: {
    options: {
      handler: function handler(newOptionValue) {
        this.updateOptions(newOptionValue);
      },
      deep: true
    },
    $attrs: {
      handler: function handler(newOptionValue) {
        this.updateOptions(newOptionValue);
      },
      deep: true
    },
    realList: function realList() {
      this.computeIndexes();
    }
  },
  methods: {
    getIsFunctional: function getIsFunctional() {
      var fnOptions = this._vnode.fnOptions;
      return fnOptions && fnOptions.functional;
    },
    getTag: function getTag() {
      return this.tag || this.element;
    },
    updateOptions: function updateOptions(newOptionValue) {
      for (var property in newOptionValue) {
        var value = Object(helper["a" /* camelize */])(property);

        if (readonlyProperties.indexOf(value) === -1) {
          this._sortable.option(value, newOptionValue[property]);
        }
      }
    },
    getChildrenNodes: function getChildrenNodes() {
      if (this.noneFunctionalComponentMode) {
        return this.$children[0].$slots.default;
      }

      var rawNodes = this.$slots.default;
      return this.transitionMode ? rawNodes[0].child.$slots.default : rawNodes;
    },
    computeIndexes: function computeIndexes() {
      var _this4 = this;

      this.$nextTick(function () {
        _this4.visibleIndexes = _computeIndexes(_this4.getChildrenNodes(), _this4.rootContainer.children, _this4.transitionMode, _this4.footerOffset);
      });
    },
    getUnderlyingVm: function getUnderlyingVm(htmlElt) {
      var index = computeVmIndex(this.getChildrenNodes() || [], htmlElt);

      if (index === -1) {
        //Edge case during move callback: related element might be
        //an element different from collection
        return null;
      }

      var element = this.realList[index];
      return {
        index: index,
        element: element
      };
    },
    getUnderlyingPotencialDraggableComponent: function getUnderlyingPotencialDraggableComponent(_ref) {
      var vue = _ref.__vue__;

      if (!vue || !vue.$options || !isTransitionName(vue.$options._componentTag)) {
        if (!("realList" in vue) && vue.$children.length === 1 && "realList" in vue.$children[0]) return vue.$children[0];
        return vue;
      }

      return vue.$parent;
    },
    emitChanges: function emitChanges(evt) {
      var _this5 = this;

      this.$nextTick(function () {
        _this5.$emit("change", evt);
      });
    },
    alterList: function alterList(onList) {
      if (this.list) {
        onList(this.list);
        return;
      }

      var newList = _toConsumableArray(this.value);

      onList(newList);
      this.$emit("input", newList);
    },
    spliceList: function spliceList() {
      var _arguments = arguments;

      var spliceList = function spliceList(list) {
        return list.splice.apply(list, _toConsumableArray(_arguments));
      };

      this.alterList(spliceList);
    },
    updatePosition: function updatePosition(oldIndex, newIndex) {
      var updatePosition = function updatePosition(list) {
        return list.splice(newIndex, 0, list.splice(oldIndex, 1)[0]);
      };

      this.alterList(updatePosition);
    },
    getRelatedContextFromMoveEvent: function getRelatedContextFromMoveEvent(_ref2) {
      var to = _ref2.to,
          related = _ref2.related;
      var component = this.getUnderlyingPotencialDraggableComponent(to);

      if (!component) {
        return {
          component: component
        };
      }

      var list = component.realList;
      var context = {
        list: list,
        component: component
      };

      if (to !== related && list && component.getUnderlyingVm) {
        var destination = component.getUnderlyingVm(related);

        if (destination) {
          return Object.assign(destination, context);
        }
      }

      return context;
    },
    getVmIndex: function getVmIndex(domIndex) {
      var indexes = this.visibleIndexes;
      var numberIndexes = indexes.length;
      return domIndex > numberIndexes - 1 ? numberIndexes : indexes[domIndex];
    },
    getComponent: function getComponent() {
      return this.$slots.default[0].componentInstance;
    },
    resetTransitionData: function resetTransitionData(index) {
      if (!this.noTransitionOnDrag || !this.transitionMode) {
        return;
      }

      var nodes = this.getChildrenNodes();
      nodes[index].data = null;
      var transitionContainer = this.getComponent();
      transitionContainer.children = [];
      transitionContainer.kept = undefined;
    },
    onDragStart: function onDragStart(evt) {
      this.context = this.getUnderlyingVm(evt.item);
      evt.item._underlying_vm_ = this.clone(this.context.element);
      draggingElement = evt.item;
    },
    onDragAdd: function onDragAdd(evt) {
      var element = evt.item._underlying_vm_;

      if (element === undefined) {
        return;
      }

      Object(helper["d" /* removeNode */])(evt.item);
      var newIndex = this.getVmIndex(evt.newIndex);
      this.spliceList(newIndex, 0, element);
      this.computeIndexes();
      var added = {
        element: element,
        newIndex: newIndex
      };
      this.emitChanges({
        added: added
      });
    },
    onDragRemove: function onDragRemove(evt) {
      Object(helper["c" /* insertNodeAt */])(this.rootContainer, evt.item, evt.oldIndex);

      if (evt.pullMode === "clone") {
        Object(helper["d" /* removeNode */])(evt.clone);
        return;
      }

      var oldIndex = this.context.index;
      this.spliceList(oldIndex, 1);
      var removed = {
        element: this.context.element,
        oldIndex: oldIndex
      };
      this.resetTransitionData(oldIndex);
      this.emitChanges({
        removed: removed
      });
    },
    onDragUpdate: function onDragUpdate(evt) {
      Object(helper["d" /* removeNode */])(evt.item);
      Object(helper["c" /* insertNodeAt */])(evt.from, evt.item, evt.oldIndex);
      var oldIndex = this.context.index;
      var newIndex = this.getVmIndex(evt.newIndex);
      this.updatePosition(oldIndex, newIndex);
      var moved = {
        element: this.context.element,
        oldIndex: oldIndex,
        newIndex: newIndex
      };
      this.emitChanges({
        moved: moved
      });
    },
    updateProperty: function updateProperty(evt, propertyName) {
      evt.hasOwnProperty(propertyName) && (evt[propertyName] += this.headerOffset);
    },
    computeFutureIndex: function computeFutureIndex(relatedContext, evt) {
      if (!relatedContext.element) {
        return 0;
      }

      var domChildren = _toConsumableArray(evt.to.children).filter(function (el) {
        return el.style["display"] !== "none";
      });

      var currentDOMIndex = domChildren.indexOf(evt.related);
      var currentIndex = relatedContext.component.getVmIndex(currentDOMIndex);
      var draggedInList = domChildren.indexOf(draggingElement) !== -1;
      return draggedInList || !evt.willInsertAfter ? currentIndex : currentIndex + 1;
    },
    onDragMove: function onDragMove(evt, originalEvent) {
      var onMove = this.move;

      if (!onMove || !this.realList) {
        return true;
      }

      var relatedContext = this.getRelatedContextFromMoveEvent(evt);
      var draggedContext = this.context;
      var futureIndex = this.computeFutureIndex(relatedContext, evt);
      Object.assign(draggedContext, {
        futureIndex: futureIndex
      });
      var sendEvt = Object.assign({}, evt, {
        relatedContext: relatedContext,
        draggedContext: draggedContext
      });
      return onMove(sendEvt, originalEvent);
    },
    onDragEnd: function onDragEnd() {
      this.computeIndexes();
      draggingElement = null;
    }
  }
};

if (typeof window !== "undefined" && "Vue" in window) {
  window.Vue.component("draggable", draggableComponent);
}

/* harmony default export */ var vuedraggable = (draggableComponent);
// CONCATENATED MODULE: ./node_modules/@vue/cli-service/lib/commands/build/entry-lib.js


/* harmony default export */ var entry_lib = __webpack_exports__["default"] = (vuedraggable);



/***/ })

/******/ })["default"];

});

var Draggable = unwrapExports(vuedraggable_common);

//

var script$s = {
  name: 'MeDraggable',
  components: {
    Draggable
  }
};

/* script */
const __vue_script__$s = script$s;

/* template */
var __vue_render__$s = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Draggable",
    _vm._g(_vm._b({}, "Draggable", _vm.$attrs, false), _vm.$listeners),
    [_vm._t("default")],
    2
  )
};
var __vue_staticRenderFns__$s = [];
__vue_render__$s._withStripped = true;

  /* style */
  const __vue_inject_styles__$s = undefined;
  /* scoped */
  const __vue_scope_id__$s = undefined;
  /* module identifier */
  const __vue_module_identifier__$s = undefined;
  /* functional template */
  const __vue_is_functional_template__$s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$s, staticRenderFns: __vue_staticRenderFns__$s },
    __vue_inject_styles__$s,
    __vue_script__$s,
    __vue_scope_id__$s,
    __vue_is_functional_template__$s,
    __vue_module_identifier__$s,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$t = {
  name: 'MeAnalytics',
  components: {
    Button: __vue_component__$6,
    Modal: __vue_component__$f,
    AnalyticsCard: __vue_component__$r,
    AnalyticsForm: __vue_component__$q,
    Spinner: __vue_component__$5,
    Draggable: __vue_component__$s,
    Icon: __vue_component__
  },
  props: {
    fields: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    provider: {
      type: Function,
      default: null
    },
    disabledSeries: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    formatter: {
      type: Function,
      default: value => value
    }
  },
  data() {
    return {
      busy: false,
      model: cloneDeep_1(this.items),
      source: cloneDeep_1(this.data)
    }
  },
  computed: {
    empty() {
      return isEmpty_1(this.model)
    },
    cards() {
      return this.model.map((item) => {
        const data = find_1(this.source, { key: item.key });
        return {
          key: item.key,
          item: item,
          data: data || {},
          busy: !data
        }
      })
    }
  },
  watch: {
    items(val) {
      this.model = cloneDeep_1(val);
    },
    data(val) {
      this.source = cloneDeep_1(val);
    }
  },
  created() {
    if (this.empty) {
      return
    }
    this.busy = true;
    this.dataProvider(() => {
      this.busy = false;
    });
  },
  methods: {
    create() {
      this.$refs.modal.show();
    },
    submit(item) {
      this.$refs.modal.hide();
      this.add(item);
    },
    add(item) {
      const key = new Date().getTime().toString();
      this.model.push({
        ...item,
        key: key
      });
      this.dataProvider();
      this.dispatchChange('add');
    },
    clear() {
      this.model = [];
    },
    remove(item) {
      this.model = this.model.filter((i) => {
        return i.key !== item.key
      });
      this.dispatchChange('remove');
    },
    resize({ key, size }) {
      const item = find_1(this.model, { key: key });
      if (item) {
        item.size = size;
        this.dispatchChange('resize');
      }
    },
    change(item) {
      const index = findIndex_1(this.model, {
        key: item.key
      });
      if (index < 0) {
        return
      }
      const key = new Date().getTime().toString();
      this.model.splice(index, 1, {
        ...item,
        key: key
      });
      this.dataProvider();
      this.dispatchChange();
    },
    dataProvider(cb) {
      if (!this.provider) {
        if (cb) {
          cb();
        }
        return
      }
      this.provider({
        items: cloneDeep_1(this.model)
      }, (data) => {
        this.source = data;
        if (cb) {
          cb();
        }
      });
    },
    selection({ key, field, categories }) {
      this.$emit('selection', {
        key: key,
        field: field,
        operator: {
          label: this.$toolkit.t('criteria.operators.in'),
          value: 'in'
        },
        value: categories
      });
      this.dataProvider();
    },
    refresh() {
      this.busy = true;
      this.dataProvider(() => {
        this.busy = false;
      });
    },
    removeTransformStyleFromCard() {
      this.$el.querySelectorAll('.me-analytics .content.row > div').forEach((element) => {
        element.style.transform = 'none';
      });
    },
    dispatchChange(type = 'change') {
      this.removeTransformStyleFromCard();
      this.$emit('change', cloneDeep_1(this.model), { type });
    }
  }
};

/* script */
const __vue_script__$t = script$t;

/* template */
var __vue_render__$t = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c("div", { staticClass: "me-analytics" }, [
        _vm.busy
          ? _c(
              "div",
              { staticClass: "loading", attrs: { "data-cy": "analyticsBusy" } },
              [
                _c("Spinner", {
                  staticClass: "align-middle",
                  attrs: { variant: "primary" }
                }),
                _vm._v(
                  " " + _vm._s(_vm.$toolkit.t("spinner.loading")) + "\n    "
                )
              ],
              1
            )
          : _vm.empty
          ? _c(
              "div",
              {
                staticClass: "empty-content",
                attrs: { "data-cy": "analyticsEmpty" }
              },
              [
                _c(
                  "div",
                  [
                    _c("Icon", { attrs: { icon: "me-icon icon-chart-line" } }),
                    _vm._v(" "),
                    _c("Icon", { attrs: { icon: "me-icon icon-chart-pie" } }),
                    _vm._v(" "),
                    _c("Icon", { attrs: { icon: "me-icon icon-chart-area" } }),
                    _vm._v(" "),
                    _c("Icon", { attrs: { icon: "me-icon icon-chart-bar" } })
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "Button",
                  {
                    attrs: {
                      variant: "outline-secondary",
                      "data-cy": "analyticsCreateItem"
                    },
                    on: { click: _vm.create }
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.$toolkit.t("analytics.start")) +
                        "\n      "
                    )
                  ]
                )
              ],
              1
            )
          : _c(
              "div",
              { attrs: { "data-cy": "analyticsContainer" } },
              [
                _c(
                  "Draggable",
                  {
                    staticClass: "content row",
                    attrs: { handle: ".header" },
                    on: {
                      change: function($event) {
                        return _vm.dispatchChange("drag")
                      }
                    },
                    model: {
                      value: _vm.model,
                      callback: function($$v) {
                        _vm.model = $$v;
                      },
                      expression: "model"
                    }
                  },
                  _vm._l(_vm.cards, function(item) {
                    return _c("AnalyticsCard", {
                      key: item.key,
                      attrs: {
                        item: item.item,
                        fields: _vm.fields,
                        data: item.data,
                        busy: item.busy,
                        "disabled-series": _vm.disabledSeries,
                        editable: _vm.editable,
                        formatter: _vm.formatter,
                        "data-cy": "analyticsCard"
                      },
                      on: {
                        resize: _vm.resize,
                        remove: _vm.remove,
                        change: _vm.change,
                        selection: _vm.selection
                      }
                    })
                  }),
                  1
                )
              ],
              1
            )
      ]),
      _vm._v(" "),
      _c(
        "Modal",
        {
          ref: "modal",
          attrs: {
            title: _vm.$toolkit.t("analytics.creation"),
            size: "sm",
            "hide-footer": ""
          }
        },
        [
          _c("AnalyticsForm", {
            attrs: {
              item: {},
              fields: _vm.fields,
              "disabled-series": _vm.disabledSeries
            },
            on: { submit: _vm.submit }
          })
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$t = [];
__vue_render__$t._withStripped = true;

  /* style */
  const __vue_inject_styles__$t = function (inject) {
    if (!inject) return
    inject("data-v-aa6ba622_0", { source: "div.me-analytics[data-v-aa6ba622] {\n  margin-bottom: 1rem;\n}\ndiv.me-analytics > div.empty-content[data-v-aa6ba622], div.me-analytics > div.loading[data-v-aa6ba622] {\n  background: #f9f9f9;\n  border-radius: 10px;\n  padding: 20px;\n  text-align: center;\n}\ndiv.me-analytics > div.empty-content > div > i[data-v-aa6ba622], div.me-analytics > div.loading > div > i[data-v-aa6ba622] {\n  color: #ececec;\n  font-size: 100px;\n  margin: 15px;\n}\n\n/*# sourceMappingURL=Analytics.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Analytics.vue","Analytics.vue"],"names":[],"mappings":"AAoPA;EACA,mBAAA;ACnPA;ADqPA;EACA,mBAAA;EACA,mBAAA;EACA,aAAA;EACA,kBAAA;ACnPA;ADqPA;EACA,cAAA;EACA,gBAAA;EACA,YAAA;ACnPA;;AAEA,wCAAwC","file":"Analytics.vue","sourcesContent":["<template>\n  <div>\n    <div class=\"me-analytics\">\n      <div v-if=\"busy\" class=\"loading\" data-cy=\"analyticsBusy\">\n        <Spinner variant=\"primary\" class=\"align-middle\" /> {{ $toolkit.t('spinner.loading') }}\n      </div>\n      <div v-else-if=\"empty\" class=\"empty-content\" data-cy=\"analyticsEmpty\">\n        <div>\n          <Icon icon=\"me-icon icon-chart-line\" />\n          <Icon icon=\"me-icon icon-chart-pie\" />\n          <Icon icon=\"me-icon icon-chart-area\" />\n          <Icon icon=\"me-icon icon-chart-bar\" />\n        </div>\n        <Button @click=\"create\" variant=\"outline-secondary\" data-cy=\"analyticsCreateItem\">\n          {{ $toolkit.t('analytics.start') }}\n        </Button>\n      </div>\n      <div v-else data-cy=\"analyticsContainer\">\n        <Draggable\n          v-model=\"model\"\n          @change=\"($event) => dispatchChange('drag')\"\n          handle=\".header\"\n          class=\"content row\"\n        >\n          <AnalyticsCard\n            v-for=\"item in cards\"\n            :key=\"item.key\"\n            :item=\"item.item\"\n            :fields=\"fields\"\n            :data=\"item.data\"\n            :busy=\"item.busy\"\n            :disabled-series=\"disabledSeries\"\n            :editable=\"editable\"\n            :formatter=\"formatter\"\n            @resize=\"resize\"\n            @remove=\"remove\"\n            @change=\"change\"\n            @selection=\"selection\"\n            data-cy=\"analyticsCard\"\n          />\n        </Draggable>\n      </div>\n    </div>\n    <Modal\n      ref=\"modal\"\n      :title=\"$toolkit.t('analytics.creation')\"\n      size=\"sm\"\n      hide-footer\n    >\n      <AnalyticsForm :item=\"{}\" :fields=\"fields\" :disabled-series=\"disabledSeries\" @submit=\"submit\" />\n    </Modal>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport find from 'lodash/find'\nimport findIndex from 'lodash/findIndex'\nimport isEmpty from 'lodash/isEmpty'\nimport Button from './Button.vue'\nimport Modal from './modal/Modal.vue'\nimport Spinner from './Spinner.vue'\nimport AnalyticsCard from './AnalyticsCard.vue'\nimport AnalyticsForm from './AnalyticsForm.vue'\nimport Draggable from './Draggable.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeAnalytics',\n  components: {\n    Button,\n    Modal,\n    AnalyticsCard,\n    AnalyticsForm,\n    Spinner,\n    Draggable,\n    Icon\n  },\n  props: {\n    fields: {\n      type: Array,\n      required: true\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    data: {\n      type: Array,\n      default: () => []\n    },\n    provider: {\n      type: Function,\n      default: null\n    },\n    disabledSeries: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    formatter: {\n      type: Function,\n      default: value => value\n    }\n  },\n  data() {\n    return {\n      busy: false,\n      model: cloneDeep(this.items),\n      source: cloneDeep(this.data)\n    }\n  },\n  computed: {\n    empty() {\n      return isEmpty(this.model)\n    },\n    cards() {\n      return this.model.map((item) => {\n        const data = find(this.source, { key: item.key })\n        return {\n          key: item.key,\n          item: item,\n          data: data || {},\n          busy: !data\n        }\n      })\n    }\n  },\n  watch: {\n    items(val) {\n      this.model = cloneDeep(val)\n    },\n    data(val) {\n      this.source = cloneDeep(val)\n    }\n  },\n  created() {\n    if (this.empty) {\n      return\n    }\n    this.busy = true\n    this.dataProvider(() => {\n      this.busy = false\n    })\n  },\n  methods: {\n    create() {\n      this.$refs.modal.show()\n    },\n    submit(item) {\n      this.$refs.modal.hide()\n      this.add(item)\n    },\n    add(item) {\n      const key = new Date().getTime().toString()\n      this.model.push({\n        ...item,\n        key: key\n      })\n      this.dataProvider()\n      this.dispatchChange('add')\n    },\n    clear() {\n      this.model = []\n    },\n    remove(item) {\n      this.model = this.model.filter((i) => {\n        return i.key !== item.key\n      })\n      this.dispatchChange('remove')\n    },\n    resize({ key, size }) {\n      const item = find(this.model, { key: key })\n      if (item) {\n        item.size = size\n        this.dispatchChange('resize')\n      }\n    },\n    change(item) {\n      const index = findIndex(this.model, {\n        key: item.key\n      })\n      if (index < 0) {\n        return\n      }\n      const key = new Date().getTime().toString()\n      this.model.splice(index, 1, {\n        ...item,\n        key: key\n      })\n      this.dataProvider()\n      this.dispatchChange()\n    },\n    dataProvider(cb) {\n      if (!this.provider) {\n        if (cb) {\n          cb()\n        }\n        return\n      }\n      this.provider({\n        items: cloneDeep(this.model)\n      }, (data) => {\n        this.source = data\n        if (cb) {\n          cb()\n        }\n      })\n    },\n    selection({ key, field, categories }) {\n      this.$emit('selection', {\n        key: key,\n        field: field,\n        operator: {\n          label: this.$toolkit.t('criteria.operators.in'),\n          value: 'in'\n        },\n        value: categories\n      })\n      this.dataProvider()\n    },\n    refresh() {\n      this.busy = true\n      this.dataProvider(() => {\n        this.busy = false\n      })\n    },\n    removeTransformStyleFromCard() {\n      this.$el.querySelectorAll('.me-analytics .content.row > div').forEach((element) => {\n        element.style.transform = 'none'\n      })\n    },\n    dispatchChange(type = 'change') {\n      this.removeTransformStyleFromCard()\n      this.$emit('change', cloneDeep(this.model), { type })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-analytics {\n    margin-bottom: 1rem;\n\n    > div.empty-content, > div.loading {\n      background: #f9f9f9;\n      border-radius: 10px;\n      padding: 20px;\n      text-align: center;\n\n      > div > i {\n        color: #ececec;\n        font-size: 100px;\n        margin: 15px;\n      }\n    }\n  }\n</style>\n","div.me-analytics {\n  margin-bottom: 1rem;\n}\ndiv.me-analytics > div.empty-content, div.me-analytics > div.loading {\n  background: #f9f9f9;\n  border-radius: 10px;\n  padding: 20px;\n  text-align: center;\n}\ndiv.me-analytics > div.empty-content > div > i, div.me-analytics > div.loading > div > i {\n  color: #ececec;\n  font-size: 100px;\n  margin: 15px;\n}\n\n/*# sourceMappingURL=Analytics.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$t = "data-v-aa6ba622";
  /* module identifier */
  const __vue_module_identifier__$t = undefined;
  /* functional template */
  const __vue_is_functional_template__$t = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$t, staticRenderFns: __vue_staticRenderFns__$t },
    __vue_inject_styles__$t,
    __vue_script__$t,
    __vue_scope_id__$t,
    __vue_is_functional_template__$t,
    __vue_module_identifier__$t,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//

var script$u = {
  name: 'MeAnchor',
  props: {
    tabs: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      elements: []
    }
  },
  mounted() {
    this.tabs.map((element) => {
      element.class = '';
    });
    this.elements = this.tabs;
  },
  methods: {
    redefineAnchorClasses(index) {
      this.elements.forEach((element) => {
        element.class = '';
      });

      this.elements[index].class = 'selected ';
    }
  }
};

/* script */
const __vue_script__$u = script$u;

/* template */
var __vue_render__$u = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-anchor" }, [
    _c(
      "ul",
      _vm._l(_vm.elements, function(tab, index) {
        return _c("li", { key: index, class: tab.class }, [
          _c(
            "a",
            {
              attrs: { href: "#" + tab.contentId },
              on: {
                click: function($event) {
                  return _vm.redefineAnchorClasses(index)
                }
              }
            },
            [_vm._v("\n        " + _vm._s(tab.title) + "\n      ")]
          )
        ])
      }),
      0
    )
  ])
};
var __vue_staticRenderFns__$u = [];
__vue_render__$u._withStripped = true;

  /* style */
  const __vue_inject_styles__$u = function (inject) {
    if (!inject) return
    inject("data-v-9d21bc44_0", { source: "div.me-anchor[data-v-9d21bc44] {\n  margin-bottom: 14px;\n}\ndiv.me-anchor > ul[data-v-9d21bc44] {\n  display: flex;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\ndiv.me-anchor > ul > li[data-v-9d21bc44] {\n  border-bottom: 4px solid transparent;\n  transform: translateY(2px);\n  transition: 0.2s all ease-in-out;\n}\ndiv.me-anchor > ul > li > a[data-v-9d21bc44] {\n  border-bottom: 4px solid transparent;\n  color: rgba(0, 0, 0, 0.36);\n  display: block;\n  padding: 0;\n  text-decoration: none;\n}\ndiv.me-anchor > ul > li[data-v-9d21bc44]:hover, div.me-anchor > ul > li.selected[data-v-9d21bc44] {\n  border-bottom: 4px solid var(--me-primary-2);\n  color: rgba(0, 0, 0, 0.76);\n  text-decoration: none;\n}\ndiv.me-anchor > ul > li.selected > a[data-v-9d21bc44] {\n  color: rgba(0, 0, 0, 0.76);\n}\ndiv.me-anchor > ul > li[data-v-9d21bc44]:not(:last-child) {\n  margin-right: 32px;\n}\n\n/*# sourceMappingURL=Anchor.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Anchor.vue","Anchor.vue"],"names":[],"mappings":"AA6CA;EACA,mBAAA;AC5CA;AD8CA;EACA,aAAA;EACA,gBAAA;EACA,SAAA;EACA,UAAA;AC5CA;AD8CA;EACA,oCAAA;EACA,0BAAA;EACA,gCAAA;AC5CA;AD8CA;EACA,oCAAA;EACA,0BAAA;EACA,cAAA;EACA,UAAA;EACA,qBAAA;AC5CA;AD+CA;EAEA,4CAAA;EACA,0BAAA;EACA,qBAAA;AC9CA;ADkDA;EACA,0BAAA;AChDA;ADoDA;EACA,kBAAA;AClDA;;AAEA,qCAAqC","file":"Anchor.vue","sourcesContent":["<template>\n  <div class=\"me-anchor\">\n    <ul>\n      <li v-for=\"(tab, index) in elements\" :key=\"index\" :class=\"tab.class\">\n        <a :href=\"('#' + tab.contentId)\" @click=\"redefineAnchorClasses(index)\">\n          {{ tab.title }}\n        </a>\n      </li>\n    </ul>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeAnchor',\n  props: {\n    tabs: {\n      type: Array,\n      required: true\n    }\n  },\n  data() {\n    return {\n      elements: []\n    }\n  },\n  mounted() {\n    this.tabs.map((element) => {\n      element.class = ''\n    })\n    this.elements = this.tabs\n  },\n  methods: {\n    redefineAnchorClasses(index) {\n      this.elements.forEach((element) => {\n        element.class = ''\n      })\n\n      this.elements[index].class = 'selected '\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-anchor {\n    margin-bottom: 14px;\n\n    > ul {\n      display: flex;\n      list-style: none;\n      margin: 0;\n      padding: 0;\n\n      > li {\n        border-bottom: 4px solid transparent;\n        transform: translateY(2px);\n        transition: 0.2s all ease-in-out;\n\n        > a {\n          border-bottom: 4px solid transparent;\n          color: rgba(0, 0, 0, 0.36);\n          display: block;\n          padding: 0;\n          text-decoration: none;\n        }\n\n        &:hover,\n        &.selected {\n          border-bottom: 4px solid var(--me-primary-2);\n          color: rgba(0, 0, 0, 0.76);\n          text-decoration: none;\n        }\n\n        &.selected {\n          > a {\n            color: rgba(0, 0, 0, 0.76);\n          }\n        }\n\n        &:not(:last-child) {\n          margin-right: 32px;\n        }\n      }\n    }\n  }\n</style>\n","div.me-anchor {\n  margin-bottom: 14px;\n}\ndiv.me-anchor > ul {\n  display: flex;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\ndiv.me-anchor > ul > li {\n  border-bottom: 4px solid transparent;\n  transform: translateY(2px);\n  transition: 0.2s all ease-in-out;\n}\ndiv.me-anchor > ul > li > a {\n  border-bottom: 4px solid transparent;\n  color: rgba(0, 0, 0, 0.36);\n  display: block;\n  padding: 0;\n  text-decoration: none;\n}\ndiv.me-anchor > ul > li:hover, div.me-anchor > ul > li.selected {\n  border-bottom: 4px solid var(--me-primary-2);\n  color: rgba(0, 0, 0, 0.76);\n  text-decoration: none;\n}\ndiv.me-anchor > ul > li.selected > a {\n  color: rgba(0, 0, 0, 0.76);\n}\ndiv.me-anchor > ul > li:not(:last-child) {\n  margin-right: 32px;\n}\n\n/*# sourceMappingURL=Anchor.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$u = "data-v-9d21bc44";
  /* module identifier */
  const __vue_module_identifier__$u = undefined;
  /* functional template */
  const __vue_is_functional_template__$u = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$u, staticRenderFns: __vue_staticRenderFns__$u },
    __vue_inject_styles__$u,
    __vue_script__$u,
    __vue_scope_id__$u,
    __vue_is_functional_template__$u,
    __vue_module_identifier__$u,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$v = {
  name: 'MeAttachment',

  components: {
    Spinner: __vue_component__$5,
    Button: __vue_component__$6,
    MeCheckbox: __vue_component__$l,
    MeIcon: __vue_component__
  },

  props: {
    deleteDisabled: {
      type: Boolean,
      default: false
    },
    downloadDisabled: {
      type: Boolean,
      default: false
    },
    fileId: {
      type: [Number, String],
      default: 0
    },
    fileIcon: {
      type: String,
      default: ''
    },
    fileUrl: {
      type: String,
      default: ''
    },
    fileName: {
      type: String,
      required: true
    },
    fileSize: {
      type: [String, Number],
      default: null
    },
    fileExtension: {
      type: String,
      default: ''
    },
    fileDate: {
      type: String,
      default: ''
    },
    canDeleteFile: {
      type: Boolean,
      default: true
    },
    busy: {
      type: Boolean,
      default: false
    },
    feedback: {
      type: String,
      default: ''
    },
    hasCheckbox: {
      type: Boolean,
      default: false
    },
    isChecked: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      checkboxIsChecked: this.isChecked
    }
  },

  computed: {
    fileSizeFormat() {
      if (!this.fileSize || typeof this.fileSize === 'string') {
        return this.fileSize || ''
      }
      const size = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const _fileSize = parseInt(Math.floor(Math.log(this.fileSize) / Math.log(1024)));
      return Math.round(this.fileSize / Math.pow(1024, _fileSize), 2) + ' ' + size[_fileSize]
    },

    iconClazz() {
      const icons = {
        bmp: 'icon-file-image',
        doc: 'icon-file-word',
        docx: 'icon-file-word',
        jpeg: 'icon-file-image',
        jpg: 'icon-file-image',
        pdf: 'icon-file-pdf',
        png: 'icon-file-image',
        ppt: 'icon-file-powerpoint',
        pptx: 'icon-file-powerpoint',
        rar: 'icon-file-archive',
        txt: 'icon-file-alt',
        xls: 'icon-file-excel',
        xlsx: 'icon-file-excel',
        xml: 'icon-file-code',
        zip: 'icon-file-archive'
      };
      return `me-icon-l ${this.fileIcon ||
        icons[this.fileExtension.toLocaleLowerCase()] || 'icon-file'}`
    }
  },

  watch: {
    checkboxIsChecked(val) {
      if (val) {
        this.checkboxIsChecked = val;
      }
    }
  },

  methods: {
    deleteFile() {
      this.$emit('delete-file', this.fileId);
    },

    changeCheckboxState() {
      this.checkboxIsChecked = !this.checkboxIsChecked;
      this.$emit('change', this.checkboxIsChecked);
    }
  }
};

/* script */
const __vue_script__$v = script$v;

/* template */
var __vue_render__$v = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me__card-attachment",
      style:
        _vm.fileUrl || _vm.canDeleteFile
          ? { padding: "16px 13px 9px 16px" }
          : { padding: "16px" }
    },
    [
      _c("div", { staticClass: "me-attachment-card" }, [
        !_vm.hasCheckbox
          ? _c(
              "div",
              { staticClass: "icon-checkbox" },
              [_c("MeIcon", { attrs: { icon: _vm.iconClazz, size: 35 } })],
              1
            )
          : _c(
              "div",
              { staticClass: "icon-checkbox" },
              [
                _c("MeCheckbox", {
                  on: { change: _vm.changeCheckboxState },
                  model: {
                    value: _vm.checkboxIsChecked,
                    callback: function($$v) {
                      _vm.checkboxIsChecked = $$v;
                    },
                    expression: "checkboxIsChecked"
                  }
                })
              ],
              1
            ),
        _vm._v(" "),
        _c("div", { staticClass: "wrapper-right" }, [
          _c(
            "span",
            {
              directives: [
                {
                  name: "me-tooltip",
                  rawName: "v-me-tooltip",
                  value: _vm.fileName,
                  expression: "fileName"
                }
              ],
              staticClass: "attachment__title",
              style: _vm.feedback || _vm.busy ? { "margin-right": "30px" } : ""
            },
            [_vm._v("\n        " + _vm._s(_vm.fileName) + "\n      ")]
          ),
          _vm._v(" "),
          _c("span", { staticClass: "attachment__file-size" }, [
            _vm._v("\n        " + _vm._s(_vm.fileSizeFormat) + "\n      ")
          ]),
          _vm._v(" "),
          _c("div", { staticClass: "file-info" }, [_vm._t("file-info")], 2),
          _vm._v(" "),
          _vm.busy
            ? _c(
                "div",
                { staticClass: "busy" },
                [
                  _c("Spinner", {
                    staticClass: "align-middle",
                    attrs: { variant: "primary" }
                  })
                ],
                1
              )
            : _c(
                "div",
                { staticClass: "footer" },
                [
                  _vm.fileDate
                    ? _c(
                        "span",
                        {
                          staticClass: "attachment__date",
                          style:
                            _vm.fileUrl || _vm.canDeleteFile
                              ? { "margin-bottom": "-7px" }
                              : ""
                        },
                        [
                          _vm._v(
                            "\n          " +
                              _vm._s(_vm._f("me-date")(_vm.fileDate)) +
                              "\n        "
                          )
                        ]
                      )
                    : _vm._e(),
                  _vm._v(" "),
                  _vm.canDeleteFile
                    ? _c("Button", {
                        staticClass: "delete-button",
                        attrs: {
                          disabled: _vm.deleteDisabled,
                          icon: "me-icon-l icon-trash"
                        },
                        on: { click: _vm.deleteFile }
                      })
                    : _vm._e(),
                  _vm._v(" "),
                  _vm.fileUrl && !_vm.feedback
                    ? _c("Button", {
                        staticClass: "download-button",
                        attrs: {
                          disabled: _vm.downloadDisabled,
                          href: _vm.fileUrl,
                          icon: "me-icon-l icon-arrow-to-bottom",
                          target: "_blank"
                        }
                      })
                    : _vm._e()
                ],
                1
              ),
          _vm._v(" "),
          _vm.feedback
            ? _c(
                "div",
                { staticClass: "feedback" },
                [
                  _c("MeIcon", {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value: _vm.feedback,
                        expression: "feedback"
                      }
                    ],
                    attrs: {
                      size: 12,
                      icon: "me-icon-l icon-exclamation-triangle"
                    }
                  })
                ],
                1
              )
            : _vm._e()
        ])
      ])
    ]
  )
};
var __vue_staticRenderFns__$v = [];
__vue_render__$v._withStripped = true;

  /* style */
  const __vue_inject_styles__$v = function (inject) {
    if (!inject) return
    inject("data-v-3da930e0_0", { source: ".me__card-attachment[data-v-3da930e0] {\n  background-color: var(--me-white);\n  border-radius: 8px;\n  border: 1px solid var(--me-gray-6);\n  box-shadow: var(--me-shadow-level-1);\n  display: flex;\n  transition: 0.2s all ease-in-out;\n  min-height: 123px;\n  width: 100%;\n  position: relative;\n}\n.me__card-attachment[data-v-3da930e0]:hover {\n  border: 1px solid var(--me-primary-2);\n}\n.me__card-attachment .me-attachment-card[data-v-3da930e0] {\n  display: flex;\n  width: -webkit-fill-available;\n}\n.me__card-attachment .me-attachment-card .icon-checkbox .me-icon-l[data-v-3da930e0] {\n  color: var(--me-gray-3);\n  margin-right: 16.2px;\n}\n.me__card-attachment .me-attachment-card .icon-checkbox[data-v-3da930e0]  .me-checkbox {\n  margin-right: 11.6px;\n}\n.me__card-attachment .me-attachment-card .wrapper-right[data-v-3da930e0] {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  overflow: hidden;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .file-info[data-v-3da930e0] {\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-3);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer[data-v-3da930e0] {\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  margin: 0 3px 7px 0;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .download-button[data-v-3da930e0] {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  width: 32px;\n  height: 32px;\n  border-radius: 50%;\n  background-color: transparent;\n  color: var(--me-gray-1);\n  border: none;\n  margin-left: 26px;\n  font-size: 12px;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .download-button[data-v-3da930e0]:hover {\n  color: var(--me-primary-0);\n  background-color: var(--me-primary-6);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .download-button[data-v-3da930e0]:focus {\n  box-shadow: 0px 4px 8px var(--me-primary-6);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .delete-button[data-v-3da930e0] {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  width: 32px;\n  height: 32px;\n  border-radius: 50%;\n  background-color: transparent;\n  border: none;\n  color: var(--me-danger-2);\n  font-size: 12px;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .delete-button[data-v-3da930e0]:hover {\n  color: var(--me-danger-0);\n  background-color: var(--me-danger-7);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .delete-button[data-v-3da930e0]:focus {\n  box-shadow: 0px 4px 8px var(--me-danger-7);\n}\n.me__card-attachment .me-attachment-card .busy[data-v-3da930e0] {\n  position: absolute;\n  right: 16px;\n  top: 12px;\n}\n.me__card-attachment .me-attachment-card .busy .me-spinner[data-v-3da930e0] {\n  width: 14px;\n  height: 14px;\n  font-size: 12px;\n}\n.me__card-attachment .me-attachment-card .feedback[data-v-3da930e0] {\n  position: absolute;\n  right: 16px;\n  top: 10px;\n}\n.me__card-attachment .me-attachment-card .feedback i[data-v-3da930e0] {\n  color: var(--me-danger-2);\n}\n.me__card-attachment .me-attachment-card .attachment__file-size[data-v-3da930e0] {\n  font-size: 12px;\n  line-height: 16px;\n  font-weight: 600;\n  color: var(--me-gray-4);\n  margin-bottom: auto;\n}\n.me__card-attachment .me-attachment-card .attachment__date[data-v-3da930e0] {\n  margin-right: auto;\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-3);\n}\n.me__card-attachment .me-attachment-card .attachment__title[data-v-3da930e0] {\n  font-size: 14px;\n  font-weight: 600;\n  line-height: 22px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  color: var(--me-gray-3);\n}\n\n/*# sourceMappingURL=Attachment.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Attachment.vue","Attachment.vue"],"names":[],"mappings":"AAuNA;EACA,iCAAA;EACA,kBAAA;EACA,kCAAA;EACA,oCAAA;EACA,aAAA;EACA,gCAAA;EACA,iBAAA;EACA,WAAA;EACA,kBAAA;ACtNA;ADwNA;EACA,qCAAA;ACtNA;ADyNA;EACA,aAAA;EACA,6BAAA;ACvNA;AD0NA;EACA,uBAAA;EACA,oBAAA;ACxNA;AD2NA;EACA,oBAAA;ACzNA;AD6NA;EACA,aAAA;EACA,sBAAA;EACA,WAAA;EACA,gBAAA;AC3NA;AD6NA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;AC3NA;AD8NA;EACA,aAAA;EACA,yBAAA;EACA,mBAAA;EACA,mBAAA;AC5NA;AD8NA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,WAAA;EACA,YAAA;EACA,kBAAA;EACA,6BAAA;EACA,uBAAA;EACA,YAAA;EACA,iBAAA;EACA,eAAA;AC5NA;AD8NA;EACA,0BAAA;EACA,qCAAA;AC5NA;AD+NA;EACA,2CAAA;AC7NA;ADiOA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,WAAA;EACA,YAAA;EACA,kBAAA;EACA,6BAAA;EACA,YAAA;EACA,yBAAA;EACA,eAAA;AC/NA;ADiOA;EACA,yBAAA;EACA,oCAAA;AC/NA;ADkOA;EACA,0CAAA;AChOA;ADsOA;EACA,kBAAA;EACA,WAAA;EACA,SAAA;ACpOA;ADsOA;EACA,WAAA;EACA,YAAA;EACA,eAAA;ACpOA;ADwOA;EACA,kBAAA;EACA,WAAA;EACA,SAAA;ACtOA;ADwOA;EACA,yBAAA;ACtOA;AD2OA;EACA,eAAA;EACA,iBAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;ACzOA;AD4OA;EACA,kBAAA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;AC1OA;AD6OA;EACA,eAAA;EACA,gBAAA;EACA,iBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,uBAAA;AC3OA;;AAEA,yCAAyC","file":"Attachment.vue","sourcesContent":["<template>\n  <div\n    :style=\"fileUrl || canDeleteFile\n      ? {'padding': '16px 13px 9px 16px'} : {'padding': '16px'}\"\n    class=\"me__card-attachment\"\n  >\n    <div class=\"me-attachment-card\">\n      <div v-if=\"!hasCheckbox\" class=\"icon-checkbox\">\n        <MeIcon :icon=\"iconClazz\" :size=\"35\" />\n      </div>\n\n      <div v-else class=\"icon-checkbox\">\n        <MeCheckbox\n          v-model=\"checkboxIsChecked\"\n          @change=\"changeCheckboxState\"\n        />\n      </div>\n\n      <div class=\"wrapper-right\">\n        <span\n          v-me-tooltip=\"fileName\"\n          :style=\"feedback || busy ? { 'margin-right': '30px' } : ''\"\n          class=\"attachment__title\"\n        >\n          {{ fileName }}\n        </span>\n\n        <span class=\"attachment__file-size\">\n          {{ fileSizeFormat }}\n        </span>\n\n        <div class=\"file-info\">\n          <slot name=\"file-info\" />\n        </div>\n\n        <div v-if=\"busy\" class=\"busy\">\n          <Spinner variant=\"primary\" class=\"align-middle\" />\n        </div>\n\n        <div v-else class=\"footer\">\n          <span\n            v-if=\"fileDate\"\n            :style=\"fileUrl || canDeleteFile\n              ? {'margin-bottom': '-7px'} : ''\"\n            class=\"attachment__date\"\n          >\n            {{ fileDate | me-date }}\n          </span>\n\n          <Button\n            :disabled=\"deleteDisabled\"\n            @click=\"deleteFile\"\n            v-if=\"canDeleteFile\"\n            class=\"delete-button\"\n            icon=\"me-icon-l icon-trash\"\n          />\n\n          <Button\n            :disabled=\"downloadDisabled\"\n            :href=\"fileUrl\"\n            v-if=\"fileUrl && !feedback\"\n            class=\"download-button\"\n            icon=\"me-icon-l icon-arrow-to-bottom\"\n            target=\"_blank\"\n          />\n        </div>\n\n        <div v-if=\"feedback\" class=\"feedback\">\n          <MeIcon\n            v-me-tooltip=\"feedback\"\n            :size=\"12\"\n            icon=\"me-icon-l icon-exclamation-triangle\"\n          />\n        </div>\n      </div>\n    </div>\n  </div>\n</template>\n\n<script>\nimport Spinner from './Spinner.vue'\nimport Button from './Button.vue'\nimport MeCheckbox from './Checkbox.vue'\nimport MeIcon from './Icon.vue'\n\nexport default {\n  name: 'MeAttachment',\n\n  components: {\n    Spinner,\n    Button,\n    MeCheckbox,\n    MeIcon\n  },\n\n  props: {\n    deleteDisabled: {\n      type: Boolean,\n      default: false\n    },\n    downloadDisabled: {\n      type: Boolean,\n      default: false\n    },\n    fileId: {\n      type: [Number, String],\n      default: 0\n    },\n    fileIcon: {\n      type: String,\n      default: ''\n    },\n    fileUrl: {\n      type: String,\n      default: ''\n    },\n    fileName: {\n      type: String,\n      required: true\n    },\n    fileSize: {\n      type: [String, Number],\n      default: null\n    },\n    fileExtension: {\n      type: String,\n      default: ''\n    },\n    fileDate: {\n      type: String,\n      default: ''\n    },\n    canDeleteFile: {\n      type: Boolean,\n      default: true\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    feedback: {\n      type: String,\n      default: ''\n    },\n    hasCheckbox: {\n      type: Boolean,\n      default: false\n    },\n    isChecked: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      checkboxIsChecked: this.isChecked\n    }\n  },\n\n  computed: {\n    fileSizeFormat() {\n      if (!this.fileSize || typeof this.fileSize === 'string') {\n        return this.fileSize || ''\n      }\n      const size = ['Bytes', 'KB', 'MB', 'GB', 'TB']\n      const _fileSize = parseInt(Math.floor(Math.log(this.fileSize) / Math.log(1024)))\n      return Math.round(this.fileSize / Math.pow(1024, _fileSize), 2) + ' ' + size[_fileSize]\n    },\n\n    iconClazz() {\n      const icons = {\n        bmp: 'icon-file-image',\n        doc: 'icon-file-word',\n        docx: 'icon-file-word',\n        jpeg: 'icon-file-image',\n        jpg: 'icon-file-image',\n        pdf: 'icon-file-pdf',\n        png: 'icon-file-image',\n        ppt: 'icon-file-powerpoint',\n        pptx: 'icon-file-powerpoint',\n        rar: 'icon-file-archive',\n        txt: 'icon-file-alt',\n        xls: 'icon-file-excel',\n        xlsx: 'icon-file-excel',\n        xml: 'icon-file-code',\n        zip: 'icon-file-archive'\n      }\n      return `me-icon-l ${this.fileIcon ||\n        icons[this.fileExtension.toLocaleLowerCase()] || 'icon-file'}`\n    }\n  },\n\n  watch: {\n    checkboxIsChecked(val) {\n      if (val) {\n        this.checkboxIsChecked = val\n      }\n    }\n  },\n\n  methods: {\n    deleteFile() {\n      this.$emit('delete-file', this.fileId)\n    },\n\n    changeCheckboxState() {\n      this.checkboxIsChecked = !this.checkboxIsChecked\n      this.$emit('change', this.checkboxIsChecked)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me__card-attachment {\n    background-color: var(--me-white);\n    border-radius: 8px;\n    border: 1px solid var(--me-gray-6);\n    box-shadow: var(--me-shadow-level-1);\n    display: flex;\n    transition: .2s all ease-in-out;\n    min-height: 123px;\n    width: 100%;\n    position: relative;\n\n    &:hover {\n      border: 1px solid var(--me-primary-2);\n    }\n\n    .me-attachment-card {\n      display: flex;\n      width: -webkit-fill-available;\n\n      .icon-checkbox {\n        .me-icon-l {\n          color: var(--me-gray-3);\n          margin-right: 16.2px\n        }\n\n        ::v-deep .me-checkbox {\n          margin-right: 11.6px;\n        }\n      }\n\n      .wrapper-right {\n        display: flex;\n        flex-direction: column;\n        width: 100%;\n        overflow: hidden;\n\n        .file-info {\n          font-size: 12px;\n          line-height: 16px;\n          color: var(--me-gray-3);\n        }\n\n        .footer {\n          display: flex;\n          justify-content: flex-end;\n          align-items: center;\n          margin: 0 3px 7px 0;\n\n        .download-button {\n          display: flex;\n          justify-content: center;\n          align-items: center;\n          width: 32px;\n          height: 32px;\n          border-radius: 50%;\n          background-color: transparent;\n          color: var(--me-gray-1);\n          border: none;\n          margin-left: 26px;\n          font-size: 12px;\n\n          &:hover {\n            color: var(--me-primary-0);\n            background-color: var(--me-primary-6);\n          }\n\n          &:focus {\n            box-shadow: 0px 4px 8px var(--me-primary-6);\n          }\n        }\n\n        .delete-button {\n          display: flex;\n          justify-content: center;\n          align-items: center;\n          width: 32px;\n          height: 32px;\n          border-radius: 50%;\n          background-color: transparent;\n          border: none;\n          color: var(--me-danger-2);\n          font-size: 12px;\n\n          &:hover {\n            color: var(--me-danger-0);\n            background-color:  var(--me-danger-7);\n          }\n\n          &:focus {\n            box-shadow: 0px 4px 8px var(--me-danger-7);\n          }\n        }\n      }\n      }\n\n      .busy {\n        position: absolute;\n        right: 16px;\n        top: 12px;\n\n        .me-spinner {\n          width: 14px;\n          height: 14px;\n          font-size: 12px;\n        }\n      }\n\n      .feedback {\n        position: absolute;\n        right: 16px;\n        top: 10px;\n\n        i {\n          color: var(--me-danger-2);\n        }\n      }\n\n      .attachment {\n        &__file-size {\n          font-size: 12px;\n          line-height: 16px;\n          font-weight: 600;\n          color: var(--me-gray-4);\n          margin-bottom: auto;\n        }\n\n        &__date {\n          margin-right: auto;\n          font-size: 12px;\n          line-height: 16px;\n          color: var(--me-gray-3);\n        }\n\n        &__title {\n          font-size: 14px;\n          font-weight: 600;\n          line-height: 22px;\n          white-space: nowrap;\n          overflow: hidden;\n          text-overflow: ellipsis;\n          color: var(--me-gray-3);\n        }\n      }\n    }\n  }\n</style>\n",".me__card-attachment {\n  background-color: var(--me-white);\n  border-radius: 8px;\n  border: 1px solid var(--me-gray-6);\n  box-shadow: var(--me-shadow-level-1);\n  display: flex;\n  transition: 0.2s all ease-in-out;\n  min-height: 123px;\n  width: 100%;\n  position: relative;\n}\n.me__card-attachment:hover {\n  border: 1px solid var(--me-primary-2);\n}\n.me__card-attachment .me-attachment-card {\n  display: flex;\n  width: -webkit-fill-available;\n}\n.me__card-attachment .me-attachment-card .icon-checkbox .me-icon-l {\n  color: var(--me-gray-3);\n  margin-right: 16.2px;\n}\n.me__card-attachment .me-attachment-card .icon-checkbox ::v-deep .me-checkbox {\n  margin-right: 11.6px;\n}\n.me__card-attachment .me-attachment-card .wrapper-right {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  overflow: hidden;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .file-info {\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-3);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer {\n  display: flex;\n  justify-content: flex-end;\n  align-items: center;\n  margin: 0 3px 7px 0;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .download-button {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  width: 32px;\n  height: 32px;\n  border-radius: 50%;\n  background-color: transparent;\n  color: var(--me-gray-1);\n  border: none;\n  margin-left: 26px;\n  font-size: 12px;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .download-button:hover {\n  color: var(--me-primary-0);\n  background-color: var(--me-primary-6);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .download-button:focus {\n  box-shadow: 0px 4px 8px var(--me-primary-6);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .delete-button {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  width: 32px;\n  height: 32px;\n  border-radius: 50%;\n  background-color: transparent;\n  border: none;\n  color: var(--me-danger-2);\n  font-size: 12px;\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .delete-button:hover {\n  color: var(--me-danger-0);\n  background-color: var(--me-danger-7);\n}\n.me__card-attachment .me-attachment-card .wrapper-right .footer .delete-button:focus {\n  box-shadow: 0px 4px 8px var(--me-danger-7);\n}\n.me__card-attachment .me-attachment-card .busy {\n  position: absolute;\n  right: 16px;\n  top: 12px;\n}\n.me__card-attachment .me-attachment-card .busy .me-spinner {\n  width: 14px;\n  height: 14px;\n  font-size: 12px;\n}\n.me__card-attachment .me-attachment-card .feedback {\n  position: absolute;\n  right: 16px;\n  top: 10px;\n}\n.me__card-attachment .me-attachment-card .feedback i {\n  color: var(--me-danger-2);\n}\n.me__card-attachment .me-attachment-card .attachment__file-size {\n  font-size: 12px;\n  line-height: 16px;\n  font-weight: 600;\n  color: var(--me-gray-4);\n  margin-bottom: auto;\n}\n.me__card-attachment .me-attachment-card .attachment__date {\n  margin-right: auto;\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-3);\n}\n.me__card-attachment .me-attachment-card .attachment__title {\n  font-size: 14px;\n  font-weight: 600;\n  line-height: 22px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  color: var(--me-gray-3);\n}\n\n/*# sourceMappingURL=Attachment.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$v = "data-v-3da930e0";
  /* module identifier */
  const __vue_module_identifier__$v = undefined;
  /* functional template */
  const __vue_is_functional_template__$v = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$v, staticRenderFns: __vue_staticRenderFns__$v },
    __vue_inject_styles__$v,
    __vue_script__$v,
    __vue_scope_id__$v,
    __vue_is_functional_template__$v,
    __vue_module_identifier__$v,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$w = {
  name: 'MeAvatar',
  components: {
    BAvatar,
    Icon: __vue_component__
  },
  props: {
    badgeVariant: {
      type: String,
      default: ''
    },
    bordered: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    rounded: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: '40px'
    },
    square: {
      type: Boolean,
      default: false
    },
    src: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'secondary'
    }
  }
};

/* script */
const __vue_script__$w = script$w;

/* template */
var __vue_render__$w = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BAvatar",
    {
      class: "me-avatar " + _vm.bordered,
      attrs: {
        "badge-variant": _vm.badgeVariant,
        bordered: _vm.bordered,
        icon: _vm.icon,
        rounded: _vm.rounded,
        size: _vm.size,
        square: _vm.square,
        src: _vm.src,
        text: _vm.text,
        variant: _vm.variant
      },
      scopedSlots: _vm._u(
        [
          {
            key: "badge",
            fn: function() {
              return [_vm._t("avatar-badge-content")]
            },
            proxy: true
          }
        ],
        null,
        true
      )
    },
    [
      _vm._t("avatar-content", [
        _vm.icon ? _c("Icon", { attrs: { icon: _vm.icon } }) : _vm._e()
      ])
    ],
    2
  )
};
var __vue_staticRenderFns__$w = [];
__vue_render__$w._withStripped = true;

  /* style */
  const __vue_inject_styles__$w = function (inject) {
    if (!inject) return
    inject("data-v-187bec30_0", { source: ".me-avatar.b-avatar[data-v-187bec30] {\n  align-items: center;\n  display: inline-flex;\n  justify-content: center;\n  line-height: 1;\n  position: relative;\n  text-align: center;\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-img {\n  border-radius: inherit;\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-img img {\n  border-radius: inherit;\n  height: 100%;\n  width: 100%;\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-badge {\n  border-radius: 10em;\n  font-size: 70%;\n  min-height: 1.5em;\n  min-width: 1.5em;\n  padding: 0.25em;\n  position: absolute;\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-badge.badge-light {\n  color: var(--me-white);\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-badge.badge-light-danger {\n  background: var(--me-danger-2);\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-badge.badge-light-warning {\n  background: var(--me-warning-2);\n}\n.me-avatar.b-avatar[data-v-187bec30]  .b-avatar-badge.badge-light-success {\n  background: var(--me-success-2);\n}\n.me-avatar.badge-light-danger[data-v-187bec30] {\n  background: #FFDADA;\n}\n.me-avatar.badge-light-warning[data-v-187bec30] {\n  background: #FFEFDD;\n}\n.me-avatar.badge-light-success[data-v-187bec30] {\n  background: #D6F6E5;\n}\n.me-avatar.border-2[data-v-187bec30] {\n  border-width: 2px !important;\n}\n.me-avatar.border-3[data-v-187bec30] {\n  border-width: 3px !important;\n}\n.me-avatar.border-4[data-v-187bec30] {\n  border-width: 4px !important;\n}\n.me-avatar.border-5[data-v-187bec30] {\n  border-width: 5px !important;\n}\n.me-avatar.border-light-danger[data-v-187bec30] {\n  border-color: #FFA0A0 !important;\n}\n.me-avatar.border-light-warning[data-v-187bec30] {\n  border-color: #FFE3C3 !important;\n}\n.me-avatar.border-light-success[data-v-187bec30] {\n  border-color: #B3ECCC !important;\n}\n\n/*# sourceMappingURL=Avatar.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Avatar.vue","Avatar.vue"],"names":[],"mappings":"AA2EA;EACA,mBAAA;EACA,oBAAA;EACA,uBAAA;EACA,cAAA;EACA,kBAAA;EACA,kBAAA;AC1EA;AD4EA;EACA,sBAAA;AC1EA;AD4EA;EACA,sBAAA;EACA,YAAA;EACA,WAAA;AC1EA;AD8EA;EACA,mBAAA;EACA,cAAA;EACA,iBAAA;EACA,gBAAA;EACA,eAAA;EACA,kBAAA;AC5EA;AD8EA;EACA,sBAAA;AC5EA;AD8EA;EACA,8BAAA;AC5EA;AD+EA;EACA,+BAAA;AC7EA;ADgFA;EACA,+BAAA;AC9EA;ADqFA;EACA,mBAAA;ACnFA;ADsFA;EACA,mBAAA;ACpFA;ADuFA;EACA,mBAAA;ACrFA;AD0FA;EACA,4BAAA;ACxFA;AD2FA;EACA,4BAAA;ACzFA;AD4FA;EACA,4BAAA;AC1FA;AD6FA;EACA,4BAAA;AC3FA;AD+FA;EACA,gCAAA;AC7FA;ADgGA;EACA,gCAAA;AC9FA;ADiGA;EACA,gCAAA;AC/FA;;AAEA,qCAAqC","file":"Avatar.vue","sourcesContent":["<template>\n  <BAvatar\n    :badge-variant=\"badgeVariant\"\n    :bordered=\"bordered\"\n    :icon=\"icon\"\n    :rounded=\"rounded\"\n    :size=\"size\"\n    :square=\"square\"\n    :src=\"src\"\n    :text=\"text\"\n    :variant=\"variant\"\n    :class=\"`me-avatar ${bordered}`\"\n  >\n    <slot name=\"avatar-content\">\n      <Icon v-if=\"icon\" :icon=\"icon\" />\n    </slot>\n    <template #badge>\n      <slot name=\"avatar-badge-content\" />\n    </template>\n  </BAvatar>\n</template>\n\n<script>\nimport { BAvatar } from 'bootstrap-vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeAvatar',\n  components: {\n    BAvatar,\n    Icon\n  },\n  props: {\n    badgeVariant: {\n      type: String,\n      default: ''\n    },\n    bordered: {\n      type: String,\n      default: ''\n    },\n    icon: {\n      type: String,\n      default: ''\n    },\n    rounded: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: '40px'\n    },\n    square: {\n      type: Boolean,\n      default: false\n    },\n    src: {\n      type: String,\n      default: ''\n    },\n    text: {\n      type: String,\n      default: ''\n    },\n    variant: {\n      type: String,\n      default: 'secondary'\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-avatar {\n  &.b-avatar {\n    align-items: center;\n    display: inline-flex;\n    justify-content: center;\n    line-height: 1;\n    position: relative;\n    text-align: center;\n\n    ::v-deep .b-avatar-img {\n      border-radius: inherit;\n\n      img {\n        border-radius: inherit;\n        height: 100%;\n        width: 100%;\n      }\n    }\n\n    ::v-deep .b-avatar-badge {\n      border-radius: 10em;\n      font-size: 70%;\n      min-height: 1.5em;\n      min-width: 1.5em;\n      padding: .25em;\n      position: absolute;\n\n      &.badge-light {\n        color: var(--me-white);\n\n        &-danger {\n          background: var(--me-danger-2);\n        }\n\n        &-warning {\n          background: var(--me-warning-2);\n        }\n\n        &-success {\n          background: var(--me-success-2);\n        }\n      }\n    }\n  }\n\n  &.badge-light {\n    &-danger {\n      background: #FFDADA;\n    }\n\n    &-warning {\n      background: #FFEFDD;\n    }\n\n    &-success {\n      background: #D6F6E5;\n    }\n  }\n\n  &.border {\n    &-2 {\n      border-width: 2px !important;\n    }\n\n    &-3 {\n      border-width: 3px !important;\n    }\n\n    &-4 {\n      border-width: 4px !important;\n    }\n\n    &-5 {\n      border-width: 5px !important;\n    }\n\n    &-light {\n      &-danger {\n        border-color: #FFA0A0 !important;\n      }\n\n      &-warning {\n        border-color: #FFE3C3 !important;\n      }\n\n      &-success {\n        border-color: #B3ECCC !important;\n      }\n    }\n  }\n}\n</style>\n",".me-avatar.b-avatar {\n  align-items: center;\n  display: inline-flex;\n  justify-content: center;\n  line-height: 1;\n  position: relative;\n  text-align: center;\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-img {\n  border-radius: inherit;\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-img img {\n  border-radius: inherit;\n  height: 100%;\n  width: 100%;\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-badge {\n  border-radius: 10em;\n  font-size: 70%;\n  min-height: 1.5em;\n  min-width: 1.5em;\n  padding: 0.25em;\n  position: absolute;\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-badge.badge-light {\n  color: var(--me-white);\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-badge.badge-light-danger {\n  background: var(--me-danger-2);\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-badge.badge-light-warning {\n  background: var(--me-warning-2);\n}\n.me-avatar.b-avatar ::v-deep .b-avatar-badge.badge-light-success {\n  background: var(--me-success-2);\n}\n.me-avatar.badge-light-danger {\n  background: #FFDADA;\n}\n.me-avatar.badge-light-warning {\n  background: #FFEFDD;\n}\n.me-avatar.badge-light-success {\n  background: #D6F6E5;\n}\n.me-avatar.border-2 {\n  border-width: 2px !important;\n}\n.me-avatar.border-3 {\n  border-width: 3px !important;\n}\n.me-avatar.border-4 {\n  border-width: 4px !important;\n}\n.me-avatar.border-5 {\n  border-width: 5px !important;\n}\n.me-avatar.border-light-danger {\n  border-color: #FFA0A0 !important;\n}\n.me-avatar.border-light-warning {\n  border-color: #FFE3C3 !important;\n}\n.me-avatar.border-light-success {\n  border-color: #B3ECCC !important;\n}\n\n/*# sourceMappingURL=Avatar.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$w = "data-v-187bec30";
  /* module identifier */
  const __vue_module_identifier__$w = undefined;
  /* functional template */
  const __vue_is_functional_template__$w = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$w, staticRenderFns: __vue_staticRenderFns__$w },
    __vue_inject_styles__$w,
    __vue_script__$w,
    __vue_scope_id__$w,
    __vue_is_functional_template__$w,
    __vue_module_identifier__$w,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$x = {
  name: 'MeBadge',
  components: {
    MeIcon: __vue_component__
  },
  props: {
    appearance: {
      type: String,
      required: true,
      validator: (value) => {
        return [
          'primary',
          'secondary',
          'success',
          'warning',
          'danger',
          'light',
          'info',
          'dark',
          'light-primary',
          'light-secondary',
          'light-success',
          'light-warning',
          'light-danger',
          'light-default'
        ].includes(value)
      }
    },
    href: {
      type: String,
      default: 'javascript:;'
    },
    icon: {
      type: String,
      default: ''
    },
    text: {
      type: String,
      default: 'font-weight-normal'
    },
    target: {
      type: String,
      default: '_self'
    },
    variant: {
      type: String,
      default: 'none',
      validator: (value) => {
        return value === 'link' || value === 'none'
      }
    }
  },
  computed: {
    clazz() {
      if (this.icon && this.appearance === 'light-secondary') {
        return `badge badge-${this.appearance} icon ${this.text}`
      }
      return `badge badge-${this.appearance} ${this.text}`
    }
  }
};

/* script */
const __vue_script__$x = script$x;

/* template */
var __vue_render__$x = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-badge", attrs: { "data-cy": "me-badge" } },
    [
      _vm.variant === "none"
        ? _c(
            "span",
            { class: _vm.clazz },
            [
              _vm.icon
                ? _c("MeIcon", {
                    staticClass: "badge-icon",
                    attrs: { icon: _vm.icon }
                  })
                : _vm._e(),
              _vm._v(" "),
              _vm._t("default")
            ],
            2
          )
        : _vm.variant === "link"
        ? _c(
            "a",
            {
              class: _vm.clazz,
              attrs: { href: _vm.href, target: _vm.target },
              on: {
                click: function($event) {
                  return _vm.$emit("click", $event)
                }
              }
            },
            [
              _vm.icon
                ? _c("MeIcon", {
                    staticClass: "badge-icon",
                    attrs: { icon: _vm.icon }
                  })
                : _vm._e(),
              _vm._v(" "),
              _vm._t("default")
            ],
            2
          )
        : _vm._e()
    ]
  )
};
var __vue_staticRenderFns__$x = [];
__vue_render__$x._withStripped = true;

  /* style */
  const __vue_inject_styles__$x = function (inject) {
    if (!inject) return
    inject("data-v-f5475010_0", { source: "div.me-badge[data-v-f5475010] {\n  display: inline-block;\n}\ndiv.me-badge > .badge[data-v-f5475010] {\n  border-radius: 50px;\n  padding: 4px 8px;\n  color: var(--me-white);\n  font-size: 12px;\n  line-height: 16px;\n  font-weight: 400;\n}\ndiv.me-badge > .badge .badge-icon[data-v-f5475010] {\n  margin-right: 4px;\n}\ndiv.me-badge > .badge.font-weight-bold[data-v-f5475010] {\n  font-weight: 600 !important;\n}\ndiv.me-badge > .badge-primary[data-v-f5475010] {\n  background-color: var(--me-primary-2);\n}\ndiv.me-badge > .badge-secondary[data-v-f5475010] {\n  background-color: var(--me-gray-1);\n}\ndiv.me-badge > .badge-success[data-v-f5475010] {\n  background-color: var(--me-success-2);\n}\ndiv.me-badge > .badge-warning[data-v-f5475010] {\n  background-color: var(--me-warning-2);\n}\ndiv.me-badge > .badge-danger[data-v-f5475010] {\n  background-color: var(--me-danger-2);\n}\ndiv.me-badge > .badge-light[data-v-f5475010] {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-2);\n}\ndiv.me-badge > .badge-light-primary[data-v-f5475010] {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-0);\n}\ndiv.me-badge > .badge-light-default[data-v-f5475010] {\n  background-color: var(--me-primary-7);\n  color: var(--me-gray-2);\n}\ndiv.me-badge > .badge-light-secondary[data-v-f5475010] {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-0);\n}\ndiv.me-badge > .badge-light-secondary.icon[data-v-f5475010] {\n  color: var(--me-gray-3);\n}\ndiv.me-badge > .badge-light-success[data-v-f5475010] {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n}\ndiv.me-badge > .badge-light-warning[data-v-f5475010] {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n}\ndiv.me-badge > .badge-light-danger[data-v-f5475010] {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n}\n\n/*# sourceMappingURL=Badge.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Badge.vue","Badge.vue"],"names":[],"mappings":"AAoGA;EACA,qBAAA;ACnGA;ADqGA;EACA,mBAAA;EACA,gBAAA;EACA,sBAAA;EACA,eAAA;EACA,iBAAA;EACA,gBAAA;ACnGA;ADqGA;EACA,iBAAA;ACnGA;ADsGA;EACA,2BAAA;ACpGA;ADuGA;EACA,qCAAA;ACrGA;ADwGA;EACA,kCAAA;ACtGA;ADyGA;EACA,qCAAA;ACvGA;AD0GA;EACA,qCAAA;ACxGA;AD2GA;EACA,oCAAA;ACzGA;AD4GA;EACA,mCAAA;EACA,uBAAA;AC1GA;AD6GA;EACA,qCAAA;EACA,0BAAA;AC3GA;AD8GA;EACA,qCAAA;EACA,uBAAA;AC5GA;AD+GA;EACA,kCAAA;EACA,uBAAA;AC7GA;AD+GA;EACA,uBAAA;AC7GA;ADiHA;EACA,qCAAA;EACA,0BAAA;AC/GA;ADkHA;EACA,qCAAA;EACA,0BAAA;AChHA;ADmHA;EACA,oCAAA;EACA,yBAAA;ACjHA;;AAEA,oCAAoC","file":"Badge.vue","sourcesContent":["<template>\n  <div class=\"me-badge\" data-cy=\"me-badge\">\n    <span\n      v-if=\"variant === 'none'\"\n      :class=\"clazz\"\n    >\n      <MeIcon\n        v-if=\"icon\"\n        :icon=\"icon\"\n        class=\"badge-icon\"\n      />\n\n      <slot />\n    </span>\n\n    <a\n      v-else-if=\"variant === 'link'\"\n      :class=\"clazz\"\n      :href=\"href\"\n      :target=\"target\"\n      @click=\"$emit('click', $event)\"\n    >\n      <MeIcon\n        v-if=\"icon\"\n        :icon=\"icon\"\n        class=\"badge-icon\"\n      />\n\n      <slot />\n    </a>\n  </div>\n</template>\n\n<script>\nimport MeIcon from './Icon.vue'\n\nexport default {\n  name: 'MeBadge',\n  components: {\n    MeIcon\n  },\n  props: {\n    appearance: {\n      type: String,\n      required: true,\n      validator: (value) => {\n        return [\n          'primary',\n          'secondary',\n          'success',\n          'warning',\n          'danger',\n          'light',\n          'info',\n          'dark',\n          'light-primary',\n          'light-secondary',\n          'light-success',\n          'light-warning',\n          'light-danger',\n          'light-default'\n        ].includes(value)\n      }\n    },\n    href: {\n      type: String,\n      default: 'javascript:;'\n    },\n    icon: {\n      type: String,\n      default: ''\n    },\n    text: {\n      type: String,\n      default: 'font-weight-normal'\n    },\n    target: {\n      type: String,\n      default: '_self'\n    },\n    variant: {\n      type: String,\n      default: 'none',\n      validator: (value) => {\n        return value === 'link' || value === 'none'\n      }\n    }\n  },\n  computed: {\n    clazz() {\n      if (this.icon && this.appearance === 'light-secondary') {\n        return `badge badge-${this.appearance} icon ${this.text}`\n      }\n      return `badge badge-${this.appearance} ${this.text}`\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-badge {\n    display: inline-block;\n\n    > .badge {\n      border-radius: 50px;\n      padding: 4px 8px;\n      color: var(--me-white);\n      font-size: 12px;\n      line-height: 16px;\n      font-weight: 400;\n\n      .badge-icon {\n        margin-right: 4px;\n      }\n\n      &.font-weight-bold {\n        font-weight: 600 !important;\n      }\n\n      &-primary {\n        background-color: var(--me-primary-2);\n      }\n\n      &-secondary {\n        background-color: var(--me-gray-1);\n      }\n\n      &-success {\n        background-color: var(--me-success-2);\n      }\n\n      &-warning {\n        background-color: var(--me-warning-2);\n      }\n\n      &-danger {\n        background-color: var(--me-danger-2);\n      }\n\n      &-light {\n        background-color: var(--me-bg-gray);\n        color: var(--me-gray-2);\n      }\n\n      &-light-primary {\n        background-color: var(--me-primary-7);\n        color: var(--me-primary-0);\n      }\n\n      &-light-default {\n        background-color: var(--me-primary-7);\n        color: var(--me-gray-2);\n      }\n\n      &-light-secondary {\n        background-color: var(--me-gray-6);\n        color: var(--me-gray-0);\n\n        &.icon {\n          color: var(--me-gray-3)\n        }\n      }\n\n      &-light-success {\n        background-color: var(--me-success-7);\n        color: var(--me-success-0);\n      }\n\n      &-light-warning {\n        background-color: var(--me-warning-7);\n        color: var(--me-warning-0);\n      }\n\n      &-light-danger {\n        background-color: var(--me-danger-7);\n        color: var(--me-danger-0);\n      }\n    }\n  }\n</style>\n","div.me-badge {\n  display: inline-block;\n}\ndiv.me-badge > .badge {\n  border-radius: 50px;\n  padding: 4px 8px;\n  color: var(--me-white);\n  font-size: 12px;\n  line-height: 16px;\n  font-weight: 400;\n}\ndiv.me-badge > .badge .badge-icon {\n  margin-right: 4px;\n}\ndiv.me-badge > .badge.font-weight-bold {\n  font-weight: 600 !important;\n}\ndiv.me-badge > .badge-primary {\n  background-color: var(--me-primary-2);\n}\ndiv.me-badge > .badge-secondary {\n  background-color: var(--me-gray-1);\n}\ndiv.me-badge > .badge-success {\n  background-color: var(--me-success-2);\n}\ndiv.me-badge > .badge-warning {\n  background-color: var(--me-warning-2);\n}\ndiv.me-badge > .badge-danger {\n  background-color: var(--me-danger-2);\n}\ndiv.me-badge > .badge-light {\n  background-color: var(--me-bg-gray);\n  color: var(--me-gray-2);\n}\ndiv.me-badge > .badge-light-primary {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-0);\n}\ndiv.me-badge > .badge-light-default {\n  background-color: var(--me-primary-7);\n  color: var(--me-gray-2);\n}\ndiv.me-badge > .badge-light-secondary {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-0);\n}\ndiv.me-badge > .badge-light-secondary.icon {\n  color: var(--me-gray-3);\n}\ndiv.me-badge > .badge-light-success {\n  background-color: var(--me-success-7);\n  color: var(--me-success-0);\n}\ndiv.me-badge > .badge-light-warning {\n  background-color: var(--me-warning-7);\n  color: var(--me-warning-0);\n}\ndiv.me-badge > .badge-light-danger {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-0);\n}\n\n/*# sourceMappingURL=Badge.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$x = "data-v-f5475010";
  /* module identifier */
  const __vue_module_identifier__$x = undefined;
  /* functional template */
  const __vue_is_functional_template__$x = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$x, staticRenderFns: __vue_staticRenderFns__$x },
    __vue_inject_styles__$x,
    __vue_script__$x,
    __vue_scope_id__$x,
    __vue_is_functional_template__$x,
    __vue_module_identifier__$x,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$y = {
  name: 'MeBoxCard',
  props: {
    effect: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    className() {
      return `box-card ${!this.effect ? '--transition-off' : ''}`
    }
  }
};

/* script */
const __vue_script__$y = script$y;

/* template */
var __vue_render__$y = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { class: _vm.className }, [
    _c("div", { staticClass: "card-header" }, [_vm._t("card-header")], 2),
    _vm._v(" "),
    _c("div", { staticClass: "card-content" }, [_vm._t("card-content")], 2),
    _vm._v(" "),
    _c("div", { staticClass: "card-footer" }, [_vm._t("card-footer")], 2)
  ])
};
var __vue_staticRenderFns__$y = [];
__vue_render__$y._withStripped = true;

  /* style */
  const __vue_inject_styles__$y = function (inject) {
    if (!inject) return
    inject("data-v-066f8a4c_0", { source: ".box-card[data-v-066f8a4c] {\n  display: flex;\n  flex-direction: column;\n  align-content: stretch;\n  justify-content: stretch;\n  padding: 12px;\n  background: var(--me-white);\n  border-radius: 0.3rem;\n  box-shadow: var(--me-shadow-level-1), var(--me-shadow-level-1);\n  transition: box-shadow 0.15s ease-in-out;\n  height: 100%;\n}\n.box-card[data-v-066f8a4c]:hover {\n  box-shadow: var(--me-shadow-level-2);\n}\n.box-card.--transition-off[data-v-066f8a4c]:hover {\n  box-shadow: var(--me-shadow-level-1), var(--me-shadow-level-1) !important;\n  transition: none !important;\n  cursor: auto;\n}\n.box-card .card-header[data-v-066f8a4c] {\n  background: var(--me-white);\n  display: flex;\n  border: none;\n  justify-content: space-between;\n  padding: 0;\n}\n.box-card .card-content[data-v-066f8a4c] {\n  display: flex;\n  flex-direction: column;\n  flex: 1;\n  background: var(--me-white);\n}\n.box-card .card-footer[data-v-066f8a4c] {\n  display: flex;\n  border: none;\n  flex-direction: column;\n  justify-content: flex-end;\n  background: var(--me-white);\n  padding: 0;\n}\n\n/*# sourceMappingURL=BoxCard.vue.map */", map: {"version":3,"sources":["/app/build/src/components/BoxCard.vue","BoxCard.vue"],"names":[],"mappings":"AAkCA;EACA,aAAA;EACA,sBAAA;EACA,sBAAA;EACA,wBAAA;EACA,aAAA;EACA,2BAAA;EACA,qBAAA;EACA,8DAAA;EACA,wCAAA;EACA,YAAA;ACjCA;ADmCA;EACA,oCAAA;ACjCA;ADsCA;EACA,yEAAA;EACA,2BAAA;EACA,YAAA;ACpCA;ADwCA;EACA,2BAAA;EACA,aAAA;EACA,YAAA;EACA,8BAAA;EACA,UAAA;ACtCA;ADyCA;EACA,aAAA;EACA,sBAAA;EACA,OAAA;EACA,2BAAA;ACvCA;AD0CA;EACA,aAAA;EACA,YAAA;EACA,sBAAA;EACA,yBAAA;EACA,2BAAA;EACA,UAAA;ACxCA;;AAEA,sCAAsC","file":"BoxCard.vue","sourcesContent":["<template>\n  <div :class=\"className\">\n    <div class=\"card-header\">\n      <slot name=\"card-header\" />\n    </div>\n\n    <div class=\"card-content\">\n      <slot name=\"card-content\" />\n    </div>\n\n    <div class=\"card-footer\">\n      <slot name=\"card-footer\" />\n    </div>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeBoxCard',\n  props: {\n    effect: {\n      type: Boolean,\n      default: false\n    }\n  },\n  computed: {\n    className() {\n      return `box-card ${!this.effect ? '--transition-off' : ''}`\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .box-card {\n    display: flex;\n    flex-direction: column;\n    align-content: stretch;\n    justify-content: stretch;\n    padding: 12px;\n    background: var(--me-white);\n    border-radius: 0.3rem;\n    box-shadow: var(--me-shadow-level-1), var(--me-shadow-level-1);\n    transition: box-shadow 0.15s ease-in-out;\n    height: 100%;\n\n    &:hover {\n      box-shadow: var(--me-shadow-level-2);\n    }\n\n    // Turn off hover shadow effect\n    &.--transition-off {\n      &:hover {\n        box-shadow: var(--me-shadow-level-1), var(--me-shadow-level-1) !important;\n        transition: none !important;\n        cursor: auto;\n      }\n    }\n\n    .card-header {\n      background: var(--me-white);\n      display: flex;\n      border:none;\n      justify-content: space-between;\n      padding: 0;\n    }\n\n    .card-content {\n      display: flex;\n      flex-direction: column;\n      flex: 1;\n      background: var(--me-white);\n    }\n\n    .card-footer{\n      display: flex;\n      border: none;\n      flex-direction: column;\n      justify-content: flex-end;\n      background: var(--me-white);\n      padding: 0;\n    }\n  }\n</style>\n",".box-card {\n  display: flex;\n  flex-direction: column;\n  align-content: stretch;\n  justify-content: stretch;\n  padding: 12px;\n  background: var(--me-white);\n  border-radius: 0.3rem;\n  box-shadow: var(--me-shadow-level-1), var(--me-shadow-level-1);\n  transition: box-shadow 0.15s ease-in-out;\n  height: 100%;\n}\n.box-card:hover {\n  box-shadow: var(--me-shadow-level-2);\n}\n.box-card.--transition-off:hover {\n  box-shadow: var(--me-shadow-level-1), var(--me-shadow-level-1) !important;\n  transition: none !important;\n  cursor: auto;\n}\n.box-card .card-header {\n  background: var(--me-white);\n  display: flex;\n  border: none;\n  justify-content: space-between;\n  padding: 0;\n}\n.box-card .card-content {\n  display: flex;\n  flex-direction: column;\n  flex: 1;\n  background: var(--me-white);\n}\n.box-card .card-footer {\n  display: flex;\n  border: none;\n  flex-direction: column;\n  justify-content: flex-end;\n  background: var(--me-white);\n  padding: 0;\n}\n\n/*# sourceMappingURL=BoxCard.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$y = "data-v-066f8a4c";
  /* module identifier */
  const __vue_module_identifier__$y = undefined;
  /* functional template */
  const __vue_is_functional_template__$y = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$y, staticRenderFns: __vue_staticRenderFns__$y },
    __vue_inject_styles__$y,
    __vue_script__$y,
    __vue_scope_id__$y,
    __vue_is_functional_template__$y,
    __vue_module_identifier__$y,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$z = {
  name: 'MeBoxCardGrid',

  props: {
    hasSelection: {
      type: Boolean,
      default: false
    },
    cardsHeight: {
      type: Number,
      default: 450
    },
    provider: {
      type: Function,
      default: () => ({})
    }
  },

  methods: {
    refresh() {
      this.$refs.scroll.refresh();
    }
  }
};

/* script */
const __vue_script__$z = script$z;

/* template */
var __vue_render__$z = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-box-card-grid" },
    [
      _c(
        "me-scroll",
        {
          ref: "scroll",
          attrs: {
            provider: _vm.provider,
            pageSize: 10,
            "has-selection": _vm.hasSelection
          },
          scopedSlots: _vm._u(
            [
              {
                key: "checkbox",
                fn: function(ref) {
                  var toggleAll = ref.toggleAll;
                  return [
                    _vm._t("selection", null, null, { toggleAll: toggleAll })
                  ]
                }
              },
              {
                key: "item",
                fn: function(ref) {
                  var item = ref.item;
                  var checkItem = ref.checkItem;
                  return [
                    _c(
                      "div",
                      { style: "height:" + _vm.cardsHeight + "px" },
                      [
                        _vm._t("card", null, {
                          item: item,
                          checkItem: checkItem
                        })
                      ],
                      2
                    )
                  ]
                }
              }
            ],
            null,
            true
          )
        },
        [
          _vm._v(" "),
          _vm._v(" "),
          _c("div"),
          _vm._v(" "),
          _c("div"),
          _vm._v(" "),
          _c("div"),
          _vm._v(" "),
          _c("div")
        ]
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$z = [];
__vue_render__$z._withStripped = true;

  /* style */
  const __vue_inject_styles__$z = function (inject) {
    if (!inject) return
    inject("data-v-3e64ea08_0", { source: ".me-box-card-grid[data-v-3e64ea08]  .me-scroll-content {\n  display: grid;\n  grid-gap: 1rem;\n  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n  padding: 1px 1rem 0px 1px;\n  place-content: flex-start;\n  place-items: center;\n}\n@media only screen and (max-width: 1383px) {\n.me-box-card-grid[data-v-3e64ea08]  .me-scroll-content {\n    grid-template-columns: repeat(4, minmax(260px, 1fr));\n}\n}\n@media only screen and (max-width: 1365px) {\n.me-box-card-grid[data-v-3e64ea08]  .me-scroll-content {\n    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n}\n}\n\n/*# sourceMappingURL=BoxCardGrid.vue.map */", map: {"version":3,"sources":["/app/build/src/components/BoxCardGrid.vue","BoxCardGrid.vue"],"names":[],"mappings":"AAuDA;EACA,aAAA;EACA,cAAA;EACA,2DAAA;EACA,yBAAA;EACA,yBAAA;EACA,mBAAA;ACtDA;ADwDA;AARA;IASA,oDAAA;ACrDE;AACF;ADuDA;AAZA;IAaA,2DAAA;ACpDE;AACF;;AAEA,0CAA0C","file":"BoxCardGrid.vue","sourcesContent":["<template>\n  <div class=\"me-box-card-grid\">\n    <me-scroll\n      ref=\"scroll\"\n      :provider=\"provider\"\n      :pageSize=\"10\"\n      :has-selection=\"hasSelection\"\n    >\n      <template v-slot:checkbox=\"{ toggleAll }\">\n        <slot v-bind=\"{ toggleAll }\" name=\"selection\" />\n      </template>\n\n      <template v-slot:item=\"{ item, checkItem }\">\n        <div :style=\"`height:${cardsHeight}px`\">\n          <slot :item=\"item\" :check-item=\"checkItem\" name=\"card\" />\n        </div>\n      </template>\n      <div />\n      <div />\n      <div />\n      <div />\n    </me-scroll>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeBoxCardGrid',\n\n  props: {\n    hasSelection: {\n      type: Boolean,\n      default: false\n    },\n    cardsHeight: {\n      type: Number,\n      default: 450\n    },\n    provider: {\n      type: Function,\n      default: () => ({})\n    }\n  },\n\n  methods: {\n    refresh() {\n      this.$refs.scroll.refresh()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-box-card-grid {\n  ::v-deep {\n    .me-scroll-content {\n      display: grid;\n      grid-gap: 1rem;\n      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n      padding: 1px 1rem 0px 1px;\n      place-content: flex-start;\n      place-items: center;\n\n      @media only screen and (max-width: 1383px) {\n        grid-template-columns: repeat(4, minmax(260px, 1fr));\n      }\n\n      @media only screen and (max-width: 1365px) {\n        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n      }\n    }\n  }\n}\n</style>\n",".me-box-card-grid ::v-deep .me-scroll-content {\n  display: grid;\n  grid-gap: 1rem;\n  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n  padding: 1px 1rem 0px 1px;\n  place-content: flex-start;\n  place-items: center;\n}\n@media only screen and (max-width: 1383px) {\n  .me-box-card-grid ::v-deep .me-scroll-content {\n    grid-template-columns: repeat(4, minmax(260px, 1fr));\n  }\n}\n@media only screen and (max-width: 1365px) {\n  .me-box-card-grid ::v-deep .me-scroll-content {\n    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));\n  }\n}\n\n/*# sourceMappingURL=BoxCardGrid.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$z = "data-v-3e64ea08";
  /* module identifier */
  const __vue_module_identifier__$z = undefined;
  /* functional template */
  const __vue_is_functional_template__$z = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$z, staticRenderFns: __vue_staticRenderFns__$z },
    __vue_inject_styles__$z,
    __vue_script__$z,
    __vue_scope_id__$z,
    __vue_is_functional_template__$z,
    __vue_module_identifier__$z,
    false,
    createInjector,
    undefined,
    undefined
  );

function isDate$1(value) {
  return value instanceof Date || Object.prototype.toString.call(value) === '[object Date]';
}
function toDate(value) {
  if (isDate$1(value)) {
    return new Date(value.getTime());
  }

  if (value == null) {
    return new Date(NaN);
  }

  return new Date(value);
}
function isValidDate(value) {
  return isDate$1(value) && !isNaN(value.getTime());
}
function startOfWeek(value) {
  var firstDayOfWeek = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

  if (!(firstDayOfWeek >= 0 && firstDayOfWeek <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6');
  }

  var date = toDate(value);
  var day = date.getDay();
  var diff = (day + 7 - firstDayOfWeek) % 7;
  date.setDate(date.getDate() - diff);
  date.setHours(0, 0, 0, 0);
  return date;
}
function startOfWeekYear(value) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$firstDayOfWeek = _ref.firstDayOfWeek,
      firstDayOfWeek = _ref$firstDayOfWeek === void 0 ? 0 : _ref$firstDayOfWeek,
      _ref$firstWeekContain = _ref.firstWeekContainsDate,
      firstWeekContainsDate = _ref$firstWeekContain === void 0 ? 1 : _ref$firstWeekContain;

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7');
  }

  var date = toDate(value);
  var year = date.getFullYear();
  var firstDateOfFirstWeek = new Date(0);

  for (var i = year + 1; i >= year - 1; i--) {
    firstDateOfFirstWeek.setFullYear(i, 0, firstWeekContainsDate);
    firstDateOfFirstWeek.setHours(0, 0, 0, 0);
    firstDateOfFirstWeek = startOfWeek(firstDateOfFirstWeek, firstDayOfWeek);

    if (date.getTime() >= firstDateOfFirstWeek.getTime()) {
      break;
    }
  }

  return firstDateOfFirstWeek;
}
function getWeek(value) {
  var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref2$firstDayOfWeek = _ref2.firstDayOfWeek,
      firstDayOfWeek = _ref2$firstDayOfWeek === void 0 ? 0 : _ref2$firstDayOfWeek,
      _ref2$firstWeekContai = _ref2.firstWeekContainsDate,
      firstWeekContainsDate = _ref2$firstWeekContai === void 0 ? 1 : _ref2$firstWeekContai;

  var date = toDate(value);
  var firstDateOfThisWeek = startOfWeek(date, firstDayOfWeek);
  var firstDateOfFirstWeek = startOfWeekYear(date, {
    firstDayOfWeek: firstDayOfWeek,
    firstWeekContainsDate: firstWeekContainsDate
  });
  var diff = firstDateOfThisWeek.getTime() - firstDateOfFirstWeek.getTime();
  return Math.round(diff / (7 * 24 * 3600 * 1000)) + 1;
}

var locale = {
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  firstDayOfWeek: 0,
  firstWeekContainsDate: 1
};

var REGEX_FORMAT = /\[([^\]]+)]|YYYY|YY?|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|m{1,2}|s{1,2}|Z{1,2}|S{1,3}|w{1,2}|x|X|a|A/g;

function pad(val) {
  var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
  var output = "".concat(Math.abs(val));
  var sign = val < 0 ? '-' : '';

  while (output.length < len) {
    output = "0".concat(output);
  }

  return sign + output;
}

function getOffset(date) {
  return Math.round(date.getTimezoneOffset() / 15) * 15;
}

function formatTimezone(offset) {
  var delimeter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;
  return sign + pad(hours, 2) + delimeter + pad(minutes, 2);
}

var meridiem = function meridiem(h, _, isLowercase) {
  var word = h < 12 ? 'AM' : 'PM';
  return isLowercase ? word.toLocaleLowerCase() : word;
};

var formatFlags = {
  Y: function Y(date) {
    var y = date.getFullYear();
    return y <= 9999 ? "".concat(y) : "+".concat(y);
  },
  // Year: 00, 01, ..., 99
  YY: function YY(date) {
    return pad(date.getFullYear(), 4).substr(2);
  },
  // Year: 1900, 1901, ..., 2099
  YYYY: function YYYY(date) {
    return pad(date.getFullYear(), 4);
  },
  // Month: 1, 2, ..., 12
  M: function M(date) {
    return date.getMonth() + 1;
  },
  // Month: 01, 02, ..., 12
  MM: function MM(date) {
    return pad(date.getMonth() + 1, 2);
  },
  MMM: function MMM(date, locale) {
    return locale.monthsShort[date.getMonth()];
  },
  MMMM: function MMMM(date, locale) {
    return locale.months[date.getMonth()];
  },
  // Day of month: 1, 2, ..., 31
  D: function D(date) {
    return date.getDate();
  },
  // Day of month: 01, 02, ..., 31
  DD: function DD(date) {
    return pad(date.getDate(), 2);
  },
  // Hour: 0, 1, ... 23
  H: function H(date) {
    return date.getHours();
  },
  // Hour: 00, 01, ..., 23
  HH: function HH(date) {
    return pad(date.getHours(), 2);
  },
  // Hour: 1, 2, ..., 12
  h: function h(date) {
    var hours = date.getHours();

    if (hours === 0) {
      return 12;
    }

    if (hours > 12) {
      return hours % 12;
    }

    return hours;
  },
  // Hour: 01, 02, ..., 12
  hh: function hh() {
    var hours = formatFlags.h.apply(formatFlags, arguments);
    return pad(hours, 2);
  },
  // Minute: 0, 1, ..., 59
  m: function m(date) {
    return date.getMinutes();
  },
  // Minute: 00, 01, ..., 59
  mm: function mm(date) {
    return pad(date.getMinutes(), 2);
  },
  // Second: 0, 1, ..., 59
  s: function s(date) {
    return date.getSeconds();
  },
  // Second: 00, 01, ..., 59
  ss: function ss(date) {
    return pad(date.getSeconds(), 2);
  },
  // 1/10 of second: 0, 1, ..., 9
  S: function S(date) {
    return Math.floor(date.getMilliseconds() / 100);
  },
  // 1/100 of second: 00, 01, ..., 99
  SS: function SS(date) {
    return pad(Math.floor(date.getMilliseconds() / 10), 2);
  },
  // Millisecond: 000, 001, ..., 999
  SSS: function SSS(date) {
    return pad(date.getMilliseconds(), 3);
  },
  // Day of week: 0, 1, ..., 6
  d: function d(date) {
    return date.getDay();
  },
  // Day of week: 'Su', 'Mo', ..., 'Sa'
  dd: function dd(date, locale) {
    return locale.weekdaysMin[date.getDay()];
  },
  // Day of week: 'Sun', 'Mon',..., 'Sat'
  ddd: function ddd(date, locale) {
    return locale.weekdaysShort[date.getDay()];
  },
  // Day of week: 'Sunday', 'Monday', ...,'Saturday'
  dddd: function dddd(date, locale) {
    return locale.weekdays[date.getDay()];
  },
  // AM, PM
  A: function A(date, locale) {
    var meridiemFunc = locale.meridiem || meridiem;
    return meridiemFunc(date.getHours(), date.getMinutes(), false);
  },
  // am, pm
  a: function a(date, locale) {
    var meridiemFunc = locale.meridiem || meridiem;
    return meridiemFunc(date.getHours(), date.getMinutes(), true);
  },
  // Timezone: -01:00, +00:00, ... +12:00
  Z: function Z(date) {
    return formatTimezone(getOffset(date), ':');
  },
  // Timezone: -0100, +0000, ... +1200
  ZZ: function ZZ(date) {
    return formatTimezone(getOffset(date));
  },
  // Seconds timestamp: 512969520
  X: function X(date) {
    return Math.floor(date.getTime() / 1000);
  },
  // Milliseconds timestamp: 512969520900
  x: function x(date) {
    return date.getTime();
  },
  w: function w(date, locale) {
    return getWeek(date, {
      firstDayOfWeek: locale.firstDayOfWeek,
      firstWeekContainsDate: locale.firstWeekContainsDate
    });
  },
  ww: function ww(date, locale) {
    return pad(formatFlags.w(date, locale), 2);
  }
};
function format(val, str) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var formatStr = str ? String(str) : 'YYYY-MM-DDTHH:mm:ss.SSSZ';
  var date = toDate(val);

  if (!isValidDate(date)) {
    return 'Invalid Date';
  }

  var locale$1 = options.locale || locale;
  return formatStr.replace(REGEX_FORMAT, function (match, p1) {
    if (p1) {
      return p1;
    }

    if (typeof formatFlags[match] === 'function') {
      return "".concat(formatFlags[match](date, locale$1));
    }

    return match;
  });
}

function _toConsumableArray$5(arr) { return _arrayWithoutHoles$5(arr) || _iterableToArray$5(arr) || _nonIterableSpread$5(); }

function _nonIterableSpread$5() { throw new TypeError("Invalid attempt to spread non-iterable instance"); }

function _iterableToArray$5(iter) { if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter); }

function _arrayWithoutHoles$5(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } }

function ownKeys$L(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$M(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$L(source, true).forEach(function (key) { _defineProperty$_(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$L(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) { return; } var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _defineProperty$_(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var formattingTokens = /(\[[^\[]*\])|(MM?M?M?|Do|DD?|ddd?d?|w[o|w]?|YYYY|YY|a|A|hh?|HH?|mm?|ss?|S{1,3}|x|X|ZZ?|.)/g;
var match1 = /\d/; // 0 - 9

var match2 = /\d\d/; // 00 - 99

var match3 = /\d{3}/; // 000 - 999

var match4 = /\d{4}/; // 0000 - 9999

var match1to2 = /\d\d?/; // 0 - 99

var matchShortOffset = /[+-]\d\d:?\d\d/; // +00:00 -00:00 +0000 or -0000

var matchSigned = /[+-]?\d+/; // -inf - inf

var matchTimestamp = /[+-]?\d+(\.\d{1,3})?/; // 123456789 123456789.123
// const matchWord = /[0-9]{0,256}['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFF07\uFF10-\uFFEF]{1,256}|[\u0600-\u06FF\/]{1,256}(\s*?[\u0600-\u06FF]{1,256}){1,2}/i; // Word

var YEAR = 'year';
var MONTH = 'month';
var DAY = 'day';
var HOUR = 'hour';
var MINUTE = 'minute';
var SECOND = 'second';
var MILLISECOND = 'millisecond';
var parseFlags = {};

var addParseFlag = function addParseFlag(token, regex, callback) {
  var tokens = Array.isArray(token) ? token : [token];
  var func;

  if (typeof callback === 'string') {
    func = function func(input) {
      var value = parseInt(input, 10);
      return _defineProperty$_({}, callback, value);
    };
  } else {
    func = callback;
  }

  tokens.forEach(function (key) {
    parseFlags[key] = [regex, func];
  });
};

var escapeStringRegExp = function escapeStringRegExp(str) {
  return str.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
};

var matchWordRegExp = function matchWordRegExp(localeKey) {
  return function (locale) {
    var array = locale[localeKey];

    if (!Array.isArray(array)) {
      throw new Error("Locale[".concat(localeKey, "] need an array"));
    }

    return new RegExp(array.map(escapeStringRegExp).join('|'));
  };
};

var matchWordCallback = function matchWordCallback(localeKey, key) {
  return function (input, locale) {
    var array = locale[localeKey];

    if (!Array.isArray(array)) {
      throw new Error("Locale[".concat(localeKey, "] need an array"));
    }

    var index = array.indexOf(input);

    if (index < 0) {
      throw new Error('Invalid Word');
    }

    return _defineProperty$_({}, key, index);
  };
};

addParseFlag('Y', matchSigned, YEAR);
addParseFlag('YY', match2, function (input) {
  var year = new Date().getFullYear();
  var cent = Math.floor(year / 100);
  var value = parseInt(input, 10);
  value = (value > 68 ? cent - 1 : cent) * 100 + value;
  return _defineProperty$_({}, YEAR, value);
});
addParseFlag('YYYY', match4, YEAR);
addParseFlag('M', match1to2, function (input) {
  return _defineProperty$_({}, MONTH, parseInt(input, 10) - 1);
});
addParseFlag('MM', match2, function (input) {
  return _defineProperty$_({}, MONTH, parseInt(input, 10) - 1);
});
addParseFlag('MMM', matchWordRegExp('monthsShort'), matchWordCallback('monthsShort', MONTH));
addParseFlag('MMMM', matchWordRegExp('months'), matchWordCallback('months', MONTH));
addParseFlag('D', match1to2, DAY);
addParseFlag('DD', match2, DAY);
addParseFlag(['H', 'h'], match1to2, HOUR);
addParseFlag(['HH', 'hh'], match2, HOUR);
addParseFlag('m', match1to2, MINUTE);
addParseFlag('mm', match2, MINUTE);
addParseFlag('s', match1to2, SECOND);
addParseFlag('ss', match2, SECOND);
addParseFlag('S', match1, function (input) {
  return _defineProperty$_({}, MILLISECOND, parseInt(input, 10) * 100);
});
addParseFlag('SS', match2, function (input) {
  return _defineProperty$_({}, MILLISECOND, parseInt(input, 10) * 10);
});
addParseFlag('SSS', match3, MILLISECOND);

function matchMeridiem(locale) {
  return locale.meridiemParse || /[ap]\.?m?\.?/i;
}

function defaultIsPM(input) {
  return "".concat(input).toLowerCase().charAt(0) === 'p';
}

addParseFlag(['A', 'a'], matchMeridiem, function (input, locale) {
  var isPM = typeof locale.isPM === 'function' ? locale.isPM(input) : defaultIsPM(input);
  return {
    isPM: isPM
  };
});

function offsetFromString(str) {
  var _ref8 = str.match(/([+-]|\d\d)/g) || ['-', '0', '0'],
      _ref9 = _slicedToArray(_ref8, 3),
      symbol = _ref9[0],
      hour = _ref9[1],
      minute = _ref9[2];

  var minutes = parseInt(hour, 10) * 60 + parseInt(minute, 10);

  if (minutes === 0) {
    return 0;
  }

  return symbol === '+' ? -minutes : +minutes;
}

addParseFlag(['Z', 'ZZ'], matchShortOffset, function (input) {
  return {
    offset: offsetFromString(input)
  };
});
addParseFlag('x', matchSigned, function (input) {
  return {
    date: new Date(parseInt(input, 10))
  };
});
addParseFlag('X', matchTimestamp, function (input) {
  return {
    date: new Date(parseFloat(input) * 1000)
  };
});
addParseFlag('d', match1, 'weekday');
addParseFlag('dd', matchWordRegExp('weekdaysMin'), matchWordCallback('weekdaysMin', 'weekday'));
addParseFlag('ddd', matchWordRegExp('weekdaysShort'), matchWordCallback('weekdaysShort', 'weekday'));
addParseFlag('dddd', matchWordRegExp('weekdays'), matchWordCallback('weekdays', 'weekday'));
addParseFlag('w', match1to2, 'week');
addParseFlag('ww', match2, 'week');

function to24hour(hour, isPM) {
  if (hour !== undefined && isPM !== undefined) {
    if (isPM) {
      if (hour < 12) {
        return hour + 12;
      }
    } else if (hour === 12) {
      return 0;
    }
  }

  return hour;
}

function getFullInputArray(input) {
  var backupDate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  var result = [0, 0, 1, 0, 0, 0, 0];
  var backupArr = [backupDate.getFullYear(), backupDate.getMonth(), backupDate.getDate(), backupDate.getHours(), backupDate.getMinutes(), backupDate.getSeconds(), backupDate.getMilliseconds()];
  var useBackup = true;

  for (var i = 0; i < 7; i++) {
    if (input[i] === undefined) {
      result[i] = useBackup ? backupArr[i] : result[i];
    } else {
      result[i] = input[i];
      useBackup = false;
    }
  }

  return result;
}

function createDate(y, m, d, h, M, s, ms) {
  var date;

  if (y < 100 && y >= 0) {
    date = new Date(y + 400, m, d, h, M, s, ms);

    if (isFinite(date.getFullYear())) {
      date.setFullYear(y);
    }
  } else {
    date = new Date(y, m, d, h, M, s, ms);
  }

  return date;
}

function createUTCDate() {
  var date;

  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  var y = args[0];

  if (y < 100 && y >= 0) {
    args[0] += 400;
    date = new Date(Date.UTC.apply(Date, args)); // eslint-disable-next-line no-restricted-globals

    if (isFinite(date.getUTCFullYear())) {
      date.setUTCFullYear(y);
    }
  } else {
    date = new Date(Date.UTC.apply(Date, args));
  }

  return date;
}

function makeParser(dateString, format, locale) {
  var tokens = format.match(formattingTokens);

  if (!tokens) {
    throw new Error();
  }

  var length = tokens.length;
  var mark = {};

  for (var i = 0; i < length; i += 1) {
    var token = tokens[i];
    var parseTo = parseFlags[token];

    if (!parseTo) {
      var word = token.replace(/^\[|\]$/g, '');

      if (dateString.indexOf(word) === 0) {
        dateString = dateString.substr(word.length);
      } else {
        throw new Error('not match');
      }
    } else {
      var regex = typeof parseTo[0] === 'function' ? parseTo[0](locale) : parseTo[0];
      var parser = parseTo[1];
      var value = (regex.exec(dateString) || [])[0];
      var obj = parser(value, locale);
      mark = _objectSpread$M({}, mark, {}, obj);
      dateString = dateString.replace(value, '');
    }
  }

  return mark;
}

function parse(str, format) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  try {
    var _options$locale = options.locale,
        _locale = _options$locale === void 0 ? locale : _options$locale,
        _options$backupDate = options.backupDate,
        backupDate = _options$backupDate === void 0 ? new Date() : _options$backupDate;

    var parseResult = makeParser(str, format, _locale);
    var year = parseResult.year,
        month = parseResult.month,
        day = parseResult.day,
        hour = parseResult.hour,
        minute = parseResult.minute,
        second = parseResult.second,
        millisecond = parseResult.millisecond,
        isPM = parseResult.isPM,
        date = parseResult.date,
        offset = parseResult.offset,
        weekday = parseResult.weekday,
        week = parseResult.week;

    if (date) {
      return date;
    }

    var inputArray = [year, month, day, hour, minute, second, millisecond];
    inputArray[3] = to24hour(inputArray[3], isPM); // check week

    if (week !== undefined && month === undefined && day === undefined) {
      // new Date(year, 3) make sure in current year
      var firstDate = startOfWeekYear(year === undefined ? backupDate : new Date(year, 3), {
        firstDayOfWeek: _locale.firstDayOfWeek,
        firstWeekContainsDate: _locale.firstWeekContainsDate
      });
      return new Date(firstDate.getTime() + (week - 1) * 7 * 24 * 3600 * 1000);
    }

    var parsedDate;
    var result = getFullInputArray(inputArray, backupDate);

    if (offset !== undefined) {
      result[6] += offset * 60 * 1000;
      parsedDate = createUTCDate.apply(void 0, _toConsumableArray$5(result));
    } else {
      parsedDate = createDate.apply(void 0, _toConsumableArray$5(result));
    } // check weekday


    if (weekday !== undefined && parsedDate.getDay() !== weekday) {
      return new Date(NaN);
    }

    return parsedDate;
  } catch (e) {
    return new Date(NaN);
  }
}

function _typeof$5(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof$5 = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof$5 = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof$5(obj);
}

function _defineProperty$$(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$2.apply(this, arguments);
}

function ownKeys$M(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys$M(Object(source), true).forEach(function (key) {
        _defineProperty$$(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys$M(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties$1(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$1(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray$1(arr, i) {
  return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$4(arr, i) || _nonIterableRest$1();
}

function _arrayWithHoles$1(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit$1(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray$4(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$4(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$4(o, minLen);
}

function _arrayLikeToArray$4(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest$1() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _extends$1$1() {
  return _extends$1$1 = Object.assign || function (a) {
    for (var b, c = 1; c < arguments.length; c++) {
      for (var d in b = arguments[c], b) {
        Object.prototype.hasOwnProperty.call(b, d) && (a[d] = b[d]);
      }
    }

    return a;
  }, _extends$1$1.apply(this, arguments);
}

var normalMerge = ["attrs", "props", "domProps"],
    toArrayMerge = ["class", "style", "directives"],
    functionalMerge = ["on", "nativeOn"],
    mergeJsxProps = function mergeJsxProps(a) {
  return a.reduce(function (c, a) {
    for (var b in a) {
      if (!c[b]) c[b] = a[b];else if (-1 !== normalMerge.indexOf(b)) c[b] = _extends$1$1({}, c[b], a[b]);else if (-1 !== toArrayMerge.indexOf(b)) {
        var d = c[b] instanceof Array ? c[b] : [c[b]],
            e = a[b] instanceof Array ? a[b] : [a[b]];
        c[b] = d.concat(e);
      } else if (-1 !== functionalMerge.indexOf(b)) {
        for (var f in a[b]) {
          if (c[b][f]) {
            var g = c[b][f] instanceof Array ? c[b][f] : [c[b][f]],
                h = a[b][f] instanceof Array ? a[b][f] : [a[b][f]];
            c[b][f] = g.concat(h);
          } else c[b][f] = a[b][f];
        }
      } else if ("hook" == b) for (var i in a[b]) {
        c[b][i] = c[b][i] ? mergeFn(c[b][i], a[b][i]) : a[b][i];
      } else c[b] = a[b];
    }

    return c;
  }, {});
},
    mergeFn = function mergeFn(a, b) {
  return function () {
    a && a.apply(this, arguments), b && b.apply(this, arguments);
  };
};

var helper = mergeJsxProps;

// new Date(10, 0, 1) The year from 0 to 99 will be incremented by 1900 automatically.
function createDate$1(y) {
  var M = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var d = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  var h = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  var m = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
  var s = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;
  var ms = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : 0;
  var date = new Date(y, M, d, h, m, s, ms);

  if (y < 100 && y >= 0) {
    date.setFullYear(y);
  }

  return date;
}
function isValidDate$1(date) {
  return date instanceof Date && !isNaN(date);
}
function isValidRangeDate(date) {
  return Array.isArray(date) && date.length === 2 && date.every(isValidDate$1) && date[0] <= date[1];
}
function isValidDates(dates) {
  return Array.isArray(dates) && dates.every(isValidDate$1);
}
function getValidDate(value) {
  var date = new Date(value);

  if (isValidDate$1(date)) {
    return date;
  }

  for (var _len = arguments.length, backup = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    backup[_key - 1] = arguments[_key];
  }

  if (backup.length) {
    return getValidDate.apply(void 0, backup);
  }

  return new Date();
}
function startOfYear(value) {
  var date = new Date(value);
  date.setMonth(0, 1);
  date.setHours(0, 0, 0, 0);
  return date;
}
function startOfMonth(value) {
  var date = new Date(value);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}
function startOfDay(value) {
  var date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}
function getCalendar(_ref) {
  var firstDayOfWeek = _ref.firstDayOfWeek,
      year = _ref.year,
      month = _ref.month;
  var arr = []; // change to the last day of the last month

  var calendar = createDate$1(year, month, 0);
  var lastDayInLastMonth = calendar.getDate(); // getDay() 0 is Sunday, 1 is Monday

  var firstDayInLastMonth = lastDayInLastMonth - (calendar.getDay() + 7 - firstDayOfWeek) % 7;

  for (var i = firstDayInLastMonth; i <= lastDayInLastMonth; i++) {
    arr.push(createDate$1(year, month, i - lastDayInLastMonth));
  } // change to the last day of the current month


  calendar.setMonth(month + 1, 0);
  var lastDayInCurrentMonth = calendar.getDate();

  for (var _i = 1; _i <= lastDayInCurrentMonth; _i++) {
    arr.push(createDate$1(year, month, _i));
  }

  var lastMonthLength = lastDayInLastMonth - firstDayInLastMonth + 1;
  var nextMonthLength = 6 * 7 - lastMonthLength - lastDayInCurrentMonth;

  for (var _i2 = 1; _i2 <= nextMonthLength; _i2++) {
    arr.push(createDate$1(year, month, lastDayInCurrentMonth + _i2));
  }

  return arr;
}
function setMonth(dirtyDate, dirtyMonth) {
  var date = new Date(dirtyDate);
  var month = typeof dirtyMonth === 'function' ? dirtyMonth(date.getMonth()) : Number(dirtyMonth);
  var year = date.getFullYear();
  var daysInMonth = createDate$1(year, month + 1, 0).getDate();
  var day = date.getDate();
  date.setMonth(month, Math.min(day, daysInMonth));
  return date;
}
function setYear(dirtyDate, dirtyYear) {
  var date = new Date(dirtyDate);
  var year = typeof dirtyYear === 'function' ? dirtyYear(date.getFullYear()) : dirtyYear;
  date.setFullYear(year);
  return date;
}
function assignTime(target, source) {
  var date = new Date(target);
  var time = new Date(source);
  date.setHours(time.getHours(), time.getMinutes(), time.getSeconds());
  return date;
}

/**
 * chunk the array
 * @param {Array} arr
 * @param {Number} size
 */
function chunk(arr, size) {
  if (!Array.isArray(arr)) {
    return [];
  }

  var result = [];
  var len = arr.length;
  var i = 0;
  size = size || len;

  while (i < len) {
    result.push(arr.slice(i, i += size));
  }

  return result;
}
/**
 * isObject
 * @param {*} obj
 * @returns {Boolean}
 */

function isObject$2(obj) {
  return Object.prototype.toString.call(obj) === '[object Object]';
}
/**
 * pick object
 * @param {Object} obj
 * @param {Array|String} props
 */

function pick$2(obj, props) {
  if (!isObject$2(obj)) return {};

  if (!Array.isArray(props)) {
    props = [props];
  }

  var res = {};
  props.forEach(function (prop) {
    if (prop in obj) {
      res[prop] = obj[prop];
    }
  });
  return res;
}
/**
 * deep merge two object without merging array
 * @param {object} target
 * @param {object} source
 */

function mergeDeep$1(target, source) {
  if (!isObject$2(target)) {
    return {};
  }

  var result = target;

  if (isObject$2(source)) {
    Object.keys(source).forEach(function (key) {
      var value = source[key];

      if (isObject$2(value) && isObject$2(target[key])) {
        value = mergeDeep$1(target[key], value);
      }

      result = _objectSpread2({}, result, _defineProperty$$({}, key, value));
    });
  }

  return result;
}

function unwrapExports$1 (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule$1(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var en = createCommonjsModule$1(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var locale = {
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  firstDayOfWeek: 0,
  firstWeekContainsDate: 1
};
var _default = locale;
exports["default"] = _default;
module.exports = exports.default;
});

var en$1 = unwrapExports$1(en);

var lang = {
  formatLocale: en$1,
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true
};

var defaultLocale = 'en';
var locales = {};
locales[defaultLocale] = lang;
function locale$1(name, object, isLocal) {
  if (typeof name !== 'string') return locales[defaultLocale];
  var l = defaultLocale;

  if (locales[name]) {
    l = name;
  }

  if (object) {
    locales[name] = object;
    l = name;
  }

  if (!isLocal) {
    defaultLocale = l;
  }

  return locales[name] || locales[defaultLocale];
}
/**
 * get locale object
 * @param {string} name lang
 */

function getLocale(name) {
  return locale$1(name, null, true);
}

/* istanbul ignore file */
function rafThrottle(fn) {
  var isRunning = false;
  return function fnBinfRaf() {
    var _this = this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    if (isRunning) return;
    isRunning = true;
    requestAnimationFrame(function () {
      isRunning = false;
      fn.apply(_this, args);
    });
  };
}

/**
 * get the hidden element width, height
 * @param {HTMLElement} element dom
 */
function getPopupElementSize(element) {
  var originalDisplay = element.style.display;
  var originalVisibility = element.style.visibility;
  element.style.display = 'block';
  element.style.visibility = 'hidden';
  var styles = window.getComputedStyle(element);
  var width = element.offsetWidth + parseInt(styles.marginLeft, 10) + parseInt(styles.marginRight, 10);
  var height = element.offsetHeight + parseInt(styles.marginTop, 10) + parseInt(styles.marginBottom, 10);
  element.style.display = originalDisplay;
  element.style.visibility = originalVisibility;
  return {
    width: width,
    height: height
  };
}
/**
 * get the popup position
 * @param {HTMLElement} el relative element
 * @param {Number} targetWidth target element's width
 * @param {Number} targetHeight target element's height
 * @param {Boolean} fixed
 */

function getRelativePosition(el, targetWidth, targetHeight, fixed) {
  var left = 0;
  var top = 0;
  var offsetX = 0;
  var offsetY = 0;
  var relativeRect = el.getBoundingClientRect();
  var dw = document.documentElement.clientWidth;
  var dh = document.documentElement.clientHeight;

  if (fixed) {
    offsetX = window.pageXOffset + relativeRect.left;
    offsetY = window.pageYOffset + relativeRect.top;
  }

  if (dw - relativeRect.left < targetWidth && relativeRect.right < targetWidth) {
    left = offsetX - relativeRect.left + 1;
  } else if (relativeRect.left + relativeRect.width / 2 <= dw / 2) {
    left = offsetX;
  } else {
    left = offsetX + relativeRect.width - targetWidth;
  }

  if (relativeRect.top <= targetHeight && dh - relativeRect.bottom <= targetHeight) {
    top = offsetY + dh - relativeRect.top - targetHeight;
  } else if (relativeRect.top + relativeRect.height / 2 <= dh / 2) {
    top = offsetY + relativeRect.height;
  } else {
    top = offsetY - targetHeight;
  }

  return {
    left: "".concat(left, "px"),
    top: "".concat(top, "px")
  };
}
function getScrollParent$1(node) {
  var until = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document.body;

  if (!node || node === until) {
    return null;
  }

  var style = function style(value, prop) {
    return getComputedStyle(value, null).getPropertyValue(prop);
  };

  var regex = /(auto|scroll)/;
  var scroll = regex.test(style(node, 'overflow') + style(node, 'overflow-y') + style(node, 'overflow-x'));
  return scroll ? node : getScrollParent$1(node.parentNode, until);
}

//
var script$A = {
  name: 'Popup',
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    appendToBody: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      top: '',
      left: ''
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler: function handler(val) {
        var _this = this;

        this.$nextTick(function () {
          if (val) {
            _this.displayPopup();
          }
        });
      }
    }
  },
  mounted: function mounted() {
    var _this2 = this;

    if (this.appendToBody) {
      document.body.appendChild(this.$el);
    }

    this._clickoutEvent = 'ontouchend' in document ? 'touchstart' : 'mousedown';
    document.addEventListener(this._clickoutEvent, this.handleClickOutside); // change the popup position when resize or scroll

    var relativeElement = this.$parent.$el;
    this._displayPopup = rafThrottle(function () {
      return _this2.displayPopup();
    });
    this._scrollParent = getScrollParent$1(relativeElement) || window;

    this._scrollParent.addEventListener('scroll', this._displayPopup);

    window.addEventListener('resize', this._displayPopup);
  },
  beforeDestroy: function beforeDestroy() {
    if (this.appendToBody && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }

    document.removeEventListener(this._clickoutEvent, this.handleClickOutside);

    this._scrollParent.removeEventListener('scroll', this._displayPopup);

    window.removeEventListener('resize', this._displayPopup);
  },
  methods: {
    handleClickOutside: function handleClickOutside(evt) {
      if (!this.visible) return;
      var target = evt.target;
      var el = this.$el;

      if (el && !el.contains(target)) {
        this.$emit('clickoutside', evt);
      }
    },
    displayPopup: function displayPopup() {
      if (!this.visible) return;
      var popup = this.$el;
      var relativeElement = this.$parent.$el;
      var appendToBody = this.appendToBody;

      if (!this._popupRect) {
        this._popupRect = getPopupElementSize(popup);
      }

      var _this$_popupRect = this._popupRect,
          width = _this$_popupRect.width,
          height = _this$_popupRect.height;

      var _getRelativePosition = getRelativePosition(relativeElement, width, height, appendToBody),
          left = _getRelativePosition.left,
          top = _getRelativePosition.top;

      this.left = left;
      this.top = top;
    }
  }
};

function normalizeComponent$1(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier
/* server only */
, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  } // Vue.extend constructor export interop.


  var options = typeof script === 'function' ? script.options : script; // render functions

  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true; // functional template

    if (isFunctionalTemplate) {
      options.functional = true;
    }
  } // scopedId


  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;

  if (moduleIdentifier) {
    // server build
    hook = function hook(context) {
      // 2.3 injection
      context = context || // cached call
      this.$vnode && this.$vnode.ssrContext || // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true

      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      } // inject component styles


      if (style) {
        style.call(this, createInjectorSSR(context));
      } // register component module identifier for async chunk inference


      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    }; // used by ssr in case component is cached and beforeCreate
    // never gets called


    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function (context) {
      style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }

  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      var originalRender = options.render;

      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }

  return script;
}

/* script */
var __vue_script__$A = script$A;
/* template */

var __vue_render__$A = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('transition', {
    attrs: {
      "name": _vm.prefixClass + "-zoom-in-down"
    }
  }, [_vm.visible ? _c('div', {
    class: _vm.prefixClass + "-datepicker-main " + _vm.prefixClass + "-datepicker-popup",
    style: {
      top: _vm.top,
      left: _vm.left,
      position: 'absolute'
    }
  }, [_vm._t("default")], 2) : _vm._e()]);
};

var __vue_staticRenderFns__$A = [];
/* style */

var __vue_inject_styles__$A = undefined;
/* scoped */

var __vue_scope_id__$A = undefined;
/* module identifier */

var __vue_module_identifier__$A = undefined;
/* functional template */

var __vue_is_functional_template__$A = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$A = normalizeComponent$1({
  render: __vue_render__$A,
  staticRenderFns: __vue_staticRenderFns__$A
}, __vue_inject_styles__$A, __vue_script__$A, __vue_scope_id__$A, __vue_is_functional_template__$A, __vue_module_identifier__$A, false, undefined, undefined, undefined);

/* script */

/* template */
var __vue_render__$1$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "viewBox": "0 0 1024 1024",
      "width": "1em",
      "height": "1em"
    }
  }, [_c('path', {
    attrs: {
      "d": "M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z"
    }
  })]);
};

var __vue_staticRenderFns__$1$1 = [];
/* style */

var __vue_inject_styles__$1$1 = undefined;
/* scoped */

var __vue_scope_id__$1$1 = undefined;
/* module identifier */

var __vue_module_identifier__$1$1 = undefined;
/* functional template */

var __vue_is_functional_template__$1$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$1$1 = normalizeComponent$1({
  render: __vue_render__$1$1,
  staticRenderFns: __vue_staticRenderFns__$1$1
}, __vue_inject_styles__$1$1, {}, __vue_scope_id__$1$1, __vue_is_functional_template__$1$1, __vue_module_identifier__$1$1, false, undefined, undefined, undefined);

/* script */

/* template */
var __vue_render__$2$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "viewBox": "0 0 24 24",
      "width": "1em",
      "height": "1em"
    }
  }, [_c('path', {
    attrs: {
      "d": "M0 0h24v24H0z",
      "fill": "none"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
    }
  }), _vm._v(" "), _c('path', {
    attrs: {
      "d": "M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"
    }
  })]);
};

var __vue_staticRenderFns__$2$1 = [];
/* style */

var __vue_inject_styles__$2$1 = undefined;
/* scoped */

var __vue_scope_id__$2$1 = undefined;
/* module identifier */

var __vue_module_identifier__$2$1 = undefined;
/* functional template */

var __vue_is_functional_template__$2$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$2$1 = normalizeComponent$1({
  render: __vue_render__$2$1,
  staticRenderFns: __vue_staticRenderFns__$2$1
}, __vue_inject_styles__$2$1, {}, __vue_scope_id__$2$1, __vue_is_functional_template__$2$1, __vue_module_identifier__$2$1, false, undefined, undefined, undefined);

/* script */

/* template */
var __vue_render__$3$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('svg', {
    attrs: {
      "xmlns": "http://www.w3.org/2000/svg",
      "viewBox": "0 0 1024 1024",
      "width": "1em",
      "height": "1em"
    }
  }, [_c('path', {
    attrs: {
      "d": "M810.005333 274.005333l-237.994667 237.994667 237.994667 237.994667-60.010667 60.010667-237.994667-237.994667-237.994667 237.994667-60.010667-60.010667 237.994667-237.994667-237.994667-237.994667 60.010667-60.010667 237.994667 237.994667 237.994667-237.994667z"
    }
  })]);
};

var __vue_staticRenderFns__$3$1 = [];
/* style */

var __vue_inject_styles__$3$1 = undefined;
/* scoped */

var __vue_scope_id__$3$1 = undefined;
/* module identifier */

var __vue_module_identifier__$3$1 = undefined;
/* functional template */

var __vue_is_functional_template__$3$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$3$1 = normalizeComponent$1({
  render: __vue_render__$3$1,
  staticRenderFns: __vue_staticRenderFns__$3$1
}, __vue_inject_styles__$3$1, {}, __vue_scope_id__$3$1, __vue_is_functional_template__$3$1, __vue_module_identifier__$3$1, false, undefined, undefined, undefined);

//
//
//
//
//
//
//
//
//
//
var script$1$1 = {
  props: {
    type: String
  },
  inject: {
    prefixClass: {
      default: 'mx'
    }
  }
};

/* script */
var __vue_script__$1$1 = script$1$1;
/* template */

var __vue_render__$4$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('button', _vm._g({
    class: _vm.prefixClass + "-btn " + _vm.prefixClass + "-btn-text " + _vm.prefixClass + "-btn-icon-" + _vm.type,
    attrs: {
      "type": "button"
    }
  }, _vm.$listeners), [_c('i', {
    class: _vm.prefixClass + "-icon-" + _vm.type
  })]);
};

var __vue_staticRenderFns__$4$1 = [];
/* style */

var __vue_inject_styles__$4$1 = undefined;
/* scoped */

var __vue_scope_id__$4$1 = undefined;
/* module identifier */

var __vue_module_identifier__$4$1 = undefined;
/* functional template */

var __vue_is_functional_template__$4$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$4$1 = normalizeComponent$1({
  render: __vue_render__$4$1,
  staticRenderFns: __vue_staticRenderFns__$4$1
}, __vue_inject_styles__$4$1, __vue_script__$1$1, __vue_scope_id__$4$1, __vue_is_functional_template__$4$1, __vue_module_identifier__$4$1, false, undefined, undefined, undefined);

var script$2$1 = {
  name: 'TableDate',
  components: {
    IconButton: __vue_component__$4$1
  },
  inject: {
    getLocale: {
      default: function _default() {
        return getLocale;
      }
    },
    getWeek: {
      default: function _default() {
        return getWeek;
      }
    },
    prefixClass: {
      default: 'mx'
    },
    onDateMouseEnter: {
      default: undefined
    },
    onDateMouseLeave: {
      default: undefined
    }
  },
  props: {
    calendar: {
      type: Date,
      default: function _default() {
        return new Date();
      }
    },
    showWeekNumber: {
      type: Boolean,
      default: false
    },
    titleFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    getRowClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    },
    getCellClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    }
  },
  computed: {
    firstDayOfWeek: function firstDayOfWeek() {
      return this.getLocale().formatLocale.firstDayOfWeek || 0;
    },
    yearMonth: function yearMonth() {
      var _this$getLocale = this.getLocale(),
          yearFormat = _this$getLocale.yearFormat,
          monthBeforeYear = _this$getLocale.monthBeforeYear,
          _this$getLocale$month = _this$getLocale.monthFormat,
          monthFormat = _this$getLocale$month === void 0 ? 'MMM' : _this$getLocale$month;

      var yearLabel = {
        panel: 'year',
        label: this.formatDate(this.calendar, yearFormat)
      };
      var monthLabel = {
        panel: 'month',
        label: this.formatDate(this.calendar, monthFormat)
      };
      return monthBeforeYear ? [monthLabel, yearLabel] : [yearLabel, monthLabel];
    },
    days: function days() {
      var locale = this.getLocale();
      var days = locale.days || locale.formatLocale.weekdaysMin;
      return days.concat(days).slice(this.firstDayOfWeek, this.firstDayOfWeek + 7);
    },
    dates: function dates() {
      var year = this.calendar.getFullYear();
      var month = this.calendar.getMonth();
      var arr = getCalendar({
        firstDayOfWeek: this.firstDayOfWeek,
        year: year,
        month: month
      });
      return chunk(arr, 7);
    }
  },
  methods: {
    handleIconLeftClick: function handleIconLeftClick() {
      this.$emit('changecalendar', setMonth(this.calendar, function (v) {
        return v - 1;
      }), 'last-month');
    },
    handleIconRightClick: function handleIconRightClick() {
      this.$emit('changecalendar', setMonth(this.calendar, function (v) {
        return v + 1;
      }), 'next-month');
    },
    handleIconDoubleLeftClick: function handleIconDoubleLeftClick() {
      this.$emit('changecalendar', setYear(this.calendar, function (v) {
        return v - 1;
      }), 'last-year');
    },
    handleIconDoubleRightClick: function handleIconDoubleRightClick() {
      this.$emit('changecalendar', setYear(this.calendar, function (v) {
        return v + 1;
      }), 'next-year');
    },
    handlePanelChange: function handlePanelChange(panel) {
      this.$emit('changepanel', panel);
    },
    handleMouseEnter: function handleMouseEnter(cell) {
      if (typeof this.onDateMouseEnter === 'function') {
        this.onDateMouseEnter(cell);
      }
    },
    handleMouseLeave: function handleMouseLeave(cell) {
      if (typeof this.onDateMouseLeave === 'function') {
        this.onDateMouseLeave(cell);
      }
    },
    handleCellClick: function handleCellClick(evt) {
      var target = evt.target;

      if (target.tagName.toUpperCase() === 'DIV') {
        target = target.parentNode;
      }

      var index = target.getAttribute('data-row-col');

      if (index) {
        var _index$split$map = index.split(',').map(function (v) {
          return parseInt(v, 10);
        }),
            _index$split$map2 = _slicedToArray$1(_index$split$map, 2),
            row = _index$split$map2[0],
            col = _index$split$map2[1];

        var date = this.dates[row][col];
        this.$emit('select', new Date(date));
      }
    },
    formatDate: function formatDate(date, fmt) {
      return format(date, fmt, {
        locale: this.getLocale().formatLocale
      });
    },
    getCellTitle: function getCellTitle(date) {
      var fmt = this.titleFormat;
      return this.formatDate(date, fmt);
    },
    getWeekNumber: function getWeekNumber(date) {
      return this.getWeek(date, this.getLocale().formatLocale);
    }
  }
};

/* script */
var __vue_script__$2$1 = script$2$1;
/* template */

var __vue_render__$5$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.prefixClass + "-calendar " + _vm.prefixClass + "-calendar-panel-date"
  }, [_c('div', {
    class: _vm.prefixClass + "-calendar-header"
  }, [_c('icon-button', {
    attrs: {
      "type": "double-left"
    },
    on: {
      "click": _vm.handleIconDoubleLeftClick
    }
  }), _vm._v(" "), _c('icon-button', {
    attrs: {
      "type": "left"
    },
    on: {
      "click": _vm.handleIconLeftClick
    }
  }), _vm._v(" "), _c('icon-button', {
    attrs: {
      "type": "double-right"
    },
    on: {
      "click": _vm.handleIconDoubleRightClick
    }
  }), _vm._v(" "), _c('icon-button', {
    attrs: {
      "type": "right"
    },
    on: {
      "click": _vm.handleIconRightClick
    }
  }), _vm._v(" "), _c('span', {
    class: _vm.prefixClass + "-calendar-header-label"
  }, _vm._l(_vm.yearMonth, function (item) {
    return _c('button', {
      key: item.panel,
      class: _vm.prefixClass + "-btn " + _vm.prefixClass + "-btn-text " + _vm.prefixClass + "-btn-current-" + item.panel,
      attrs: {
        "type": "button"
      },
      on: {
        "click": function click($event) {
          return _vm.handlePanelChange(item.panel);
        }
      }
    }, [_vm._v("\n        " + _vm._s(item.label) + "\n      ")]);
  }), 0)], 1), _vm._v(" "), _c('div', {
    class: _vm.prefixClass + "-calendar-content"
  }, [_c('table', {
    class: _vm.prefixClass + "-table " + _vm.prefixClass + "-table-date"
  }, [_c('thead', [_c('tr', [_vm.showWeekNumber ? _c('th', {
    class: _vm.prefixClass + "-week-number-header"
  }) : _vm._e(), _vm._v(" "), _vm._l(_vm.days, function (day) {
    return _c('th', {
      key: day
    }, [_vm._v(_vm._s(day))]);
  })], 2)]), _vm._v(" "), _c('tbody', {
    on: {
      "click": _vm.handleCellClick
    }
  }, _vm._l(_vm.dates, function (row, i) {
    return _c('tr', {
      key: i,
      class: [_vm.prefixClass + "-date-row", _vm.getRowClasses(row)]
    }, [_vm.showWeekNumber ? _c('td', {
      class: _vm.prefixClass + "-week-number",
      attrs: {
        "data-row-col": i + ",0"
      }
    }, [_vm._v("\n            " + _vm._s(_vm.getWeekNumber(row[0])) + "\n          ")]) : _vm._e(), _vm._v(" "), _vm._l(row, function (cell, j) {
      return _c('td', {
        key: j,
        staticClass: "cell",
        class: _vm.getCellClasses(cell),
        attrs: {
          "data-row-col": i + "," + j,
          "title": _vm.getCellTitle(cell)
        },
        on: {
          "mouseenter": function mouseenter($event) {
            return _vm.handleMouseEnter(cell);
          },
          "mouseleave": function mouseleave($event) {
            return _vm.handleMouseLeave(cell);
          }
        }
      }, [_c('div', [_vm._v(_vm._s(cell.getDate()))])]);
    })], 2);
  }), 0)])])]);
};

var __vue_staticRenderFns__$5$1 = [];
/* style */

var __vue_inject_styles__$5$1 = undefined;
/* scoped */

var __vue_scope_id__$5$1 = undefined;
/* module identifier */

var __vue_module_identifier__$5$1 = undefined;
/* functional template */

var __vue_is_functional_template__$5$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$5$1 = normalizeComponent$1({
  render: __vue_render__$5$1,
  staticRenderFns: __vue_staticRenderFns__$5$1
}, __vue_inject_styles__$5$1, __vue_script__$2$1, __vue_scope_id__$5$1, __vue_is_functional_template__$5$1, __vue_module_identifier__$5$1, false, undefined, undefined, undefined);

//
var script$3$1 = {
  name: 'TableMonth',
  components: {
    IconButton: __vue_component__$4$1
  },
  inject: {
    getLocale: {
      default: function _default() {
        return getLocale;
      }
    },
    prefixClass: {
      default: 'mx'
    }
  },
  props: {
    calendar: {
      type: Date,
      default: function _default() {
        return new Date();
      }
    },
    getCellClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    }
  },
  computed: {
    calendarYear: function calendarYear() {
      return this.calendar.getFullYear();
    },
    months: function months() {
      var locale = this.getLocale();
      var monthsLocale = locale.months || locale.formatLocale.monthsShort;
      var months = monthsLocale.map(function (text, month) {
        return {
          text: text,
          month: month
        };
      });
      return chunk(months, 3);
    }
  },
  methods: {
    handleIconDoubleLeftClick: function handleIconDoubleLeftClick() {
      this.$emit('changecalendar', setYear(this.calendar, function (v) {
        return v - 1;
      }), 'last-year');
    },
    handleIconDoubleRightClick: function handleIconDoubleRightClick() {
      this.$emit('changecalendar', setYear(this.calendar, function (v) {
        return v + 1;
      }), 'next-year');
    },
    handlePanelChange: function handlePanelChange() {
      this.$emit('changepanel', 'year');
    },
    handleClick: function handleClick(evt) {
      var target = evt.target;

      if (target.tagName.toUpperCase() === 'DIV') {
        target = target.parentNode;
      }

      var month = target.getAttribute('data-month');

      if (month) {
        this.$emit('select', parseInt(month, 10));
      }
    }
  }
};

/* script */
var __vue_script__$3$1 = script$3$1;
/* template */

var __vue_render__$6$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.prefixClass + "-calendar " + _vm.prefixClass + "-calendar-panel-month"
  }, [_c('div', {
    class: _vm.prefixClass + "-calendar-header"
  }, [_c('icon-button', {
    attrs: {
      "type": "double-left"
    },
    on: {
      "click": _vm.handleIconDoubleLeftClick
    }
  }), _vm._v(" "), _c('icon-button', {
    attrs: {
      "type": "double-right"
    },
    on: {
      "click": _vm.handleIconDoubleRightClick
    }
  }), _vm._v(" "), _c('span', {
    class: _vm.prefixClass + "-calendar-header-label"
  }, [_c('button', {
    class: _vm.prefixClass + "-btn " + _vm.prefixClass + "-btn-text",
    attrs: {
      "type": "button"
    },
    on: {
      "click": _vm.handlePanelChange
    }
  }, [_vm._v("\n        " + _vm._s(_vm.calendarYear) + "\n      ")])])], 1), _vm._v(" "), _c('div', {
    class: _vm.prefixClass + "-calendar-content"
  }, [_c('table', {
    class: _vm.prefixClass + "-table " + _vm.prefixClass + "-table-month",
    on: {
      "click": _vm.handleClick
    }
  }, _vm._l(_vm.months, function (row, i) {
    return _c('tr', {
      key: i
    }, _vm._l(row, function (cell, j) {
      return _c('td', {
        key: j,
        staticClass: "cell",
        class: _vm.getCellClasses(cell.month),
        attrs: {
          "data-month": cell.month
        }
      }, [_c('div', [_vm._v(_vm._s(cell.text))])]);
    }), 0);
  }), 0)])]);
};

var __vue_staticRenderFns__$6$1 = [];
/* style */

var __vue_inject_styles__$6$1 = undefined;
/* scoped */

var __vue_scope_id__$6$1 = undefined;
/* module identifier */

var __vue_module_identifier__$6$1 = undefined;
/* functional template */

var __vue_is_functional_template__$6$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$6$1 = normalizeComponent$1({
  render: __vue_render__$6$1,
  staticRenderFns: __vue_staticRenderFns__$6$1
}, __vue_inject_styles__$6$1, __vue_script__$3$1, __vue_scope_id__$6$1, __vue_is_functional_template__$6$1, __vue_module_identifier__$6$1, false, undefined, undefined, undefined);

//
var script$4$1 = {
  name: 'TableYear',
  components: {
    IconButton: __vue_component__$4$1
  },
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  props: {
    calendar: {
      type: Date,
      default: function _default() {
        return new Date();
      }
    },
    getCellClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    },
    getYearPanel: {
      type: Function
    }
  },
  computed: {
    years: function years() {
      var calendar = new Date(this.calendar);

      if (typeof this.getYearPanel === 'function') {
        return this.getYearPanel(calendar);
      }

      return this.getYears(calendar);
    },
    firstYear: function firstYear() {
      return this.years[0][0];
    },
    lastYear: function lastYear() {
      var last = function last(arr) {
        return arr[arr.length - 1];
      };

      return last(last(this.years));
    }
  },
  methods: {
    getYears: function getYears(calendar) {
      var firstYear = Math.floor(calendar.getFullYear() / 10) * 10;
      var years = [];

      for (var i = 0; i < 10; i++) {
        years.push(firstYear + i);
      }

      return chunk(years, 2);
    },
    handleIconDoubleLeftClick: function handleIconDoubleLeftClick() {
      this.$emit('changecalendar', setYear(this.calendar, function (v) {
        return v - 10;
      }), 'last-decade');
    },
    handleIconDoubleRightClick: function handleIconDoubleRightClick() {
      this.$emit('changecalendar', setYear(this.calendar, function (v) {
        return v + 10;
      }), 'next-decade');
    },
    handleClick: function handleClick(evt) {
      var target = evt.target;

      if (target.tagName.toUpperCase() === 'DIV') {
        target = target.parentNode;
      }

      var year = target.getAttribute('data-year');

      if (year) {
        this.$emit('select', parseInt(year, 10));
      }
    }
  }
};

/* script */
var __vue_script__$4$1 = script$4$1;
/* template */

var __vue_render__$7$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.prefixClass + "-calendar " + _vm.prefixClass + "-calendar-panel-year"
  }, [_c('div', {
    class: _vm.prefixClass + "-calendar-header"
  }, [_c('icon-button', {
    attrs: {
      "type": "double-left"
    },
    on: {
      "click": _vm.handleIconDoubleLeftClick
    }
  }), _vm._v(" "), _c('icon-button', {
    attrs: {
      "type": "double-right"
    },
    on: {
      "click": _vm.handleIconDoubleRightClick
    }
  }), _vm._v(" "), _c('span', {
    class: _vm.prefixClass + "-calendar-header-label"
  }, [_c('span', [_vm._v(_vm._s(_vm.firstYear))]), _vm._v(" "), _c('span', {
    class: _vm.prefixClass + "-calendar-decade-separator"
  }), _vm._v(" "), _c('span', [_vm._v(_vm._s(_vm.lastYear))])])], 1), _vm._v(" "), _c('div', {
    class: _vm.prefixClass + "-calendar-content"
  }, [_c('table', {
    class: _vm.prefixClass + "-table " + _vm.prefixClass + "-table-year",
    on: {
      "click": _vm.handleClick
    }
  }, _vm._l(_vm.years, function (row, i) {
    return _c('tr', {
      key: i
    }, _vm._l(row, function (cell, j) {
      return _c('td', {
        key: j,
        staticClass: "cell",
        class: _vm.getCellClasses(cell),
        attrs: {
          "data-year": cell
        }
      }, [_c('div', [_vm._v(_vm._s(cell))])]);
    }), 0);
  }), 0)])]);
};

var __vue_staticRenderFns__$7$1 = [];
/* style */

var __vue_inject_styles__$7$1 = undefined;
/* scoped */

var __vue_scope_id__$7$1 = undefined;
/* module identifier */

var __vue_module_identifier__$7$1 = undefined;
/* functional template */

var __vue_is_functional_template__$7$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$7$1 = normalizeComponent$1({
  render: __vue_render__$7$1,
  staticRenderFns: __vue_staticRenderFns__$7$1
}, __vue_inject_styles__$7$1, __vue_script__$4$1, __vue_scope_id__$7$1, __vue_is_functional_template__$7$1, __vue_module_identifier__$7$1, false, undefined, undefined, undefined);

var CalendarPanel = {
  name: 'CalendarPanel',
  inject: {
    prefixClass: {
      default: 'mx'
    },
    dispatchDatePicker: {
      default: function _default() {
        return function () {};
      }
    }
  },
  props: {
    value: {},
    defaultValue: {
      default: function _default() {
        var date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
      }
    },
    defaultPanel: {
      type: String
    },
    disabledDate: {
      type: Function,
      default: function _default() {
        return false;
      }
    },
    type: {
      type: String,
      default: 'date'
    },
    getClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    },
    showWeekNumber: {
      type: Boolean,
      default: undefined
    },
    getYearPanel: {
      type: Function
    },
    titleFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    calendar: Date,
    // update date when select year or month
    partialUpdate: {
      type: Boolean,
      default: false
    }
  },
  data: function data() {
    var panels = ['date', 'month', 'year'];
    var index = Math.max(panels.indexOf(this.type), panels.indexOf(this.defaultPanel));
    var panel = index !== -1 ? panels[index] : 'date';
    return {
      panel: panel,
      innerCalendar: new Date()
    };
  },
  computed: {
    innerValue: function innerValue() {
      var value = Array.isArray(this.value) ? this.value : [this.value];
      var map = {
        year: startOfYear,
        month: startOfMonth,
        date: startOfDay
      };
      var start = map[this.type] || map.date;
      return value.filter(isValidDate$1).map(function (v) {
        return start(v);
      });
    },
    calendarYear: function calendarYear() {
      return this.innerCalendar.getFullYear();
    },
    calendarMonth: function calendarMonth() {
      return this.innerCalendar.getMonth();
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: 'initCalendar'
    },
    calendar: {
      handler: 'initCalendar'
    },
    defaultValue: {
      handler: 'initCalendar'
    }
  },
  methods: {
    initCalendar: function initCalendar() {
      var calendarDate = this.calendar;

      if (!isValidDate$1(calendarDate)) {
        var length = this.innerValue.length;
        calendarDate = getValidDate(length > 0 ? this.innerValue[length - 1] : this.defaultValue);
      }

      this.innerCalendar = startOfMonth(calendarDate);
    },
    isDisabled: function isDisabled(date) {
      return this.disabledDate(new Date(date), this.innerValue);
    },
    emitDate: function emitDate(date, type) {
      if (!this.isDisabled(date)) {
        this.$emit('select', date, type, this.innerValue); // someone need get the first selected date to set range value. (#429)

        this.dispatchDatePicker('pick', date, type);
      }
    },
    handleCalendarChange: function handleCalendarChange(calendar, type) {
      var oldCalendar = new Date(this.innerCalendar);
      this.innerCalendar = calendar;
      this.$emit('update:calendar', calendar);
      this.dispatchDatePicker('calendar-change', calendar, oldCalendar, type);
    },
    handelPanelChange: function handelPanelChange(panel) {
      var oldPanel = this.panel;
      this.panel = panel;
      this.dispatchDatePicker('panel-change', panel, oldPanel);
    },
    handleSelectYear: function handleSelectYear(year) {
      if (this.type === 'year') {
        var date = this.getYearCellDate(year);
        this.emitDate(date, 'year');
      } else {
        this.handleCalendarChange(createDate$1(year, this.calendarMonth), 'year');
        this.handelPanelChange('month');

        if (this.partialUpdate && this.innerValue.length === 1) {
          var _date = new Date(this.innerValue[0]);

          _date.setFullYear(year);

          this.emitDate(_date, 'year');
        }
      }
    },
    handleSelectMonth: function handleSelectMonth(month) {
      if (this.type === 'month') {
        var date = this.getMonthCellDate(month);
        this.emitDate(date, 'month');
      } else {
        this.handleCalendarChange(createDate$1(this.calendarYear, month), 'month');
        this.handelPanelChange('date');

        if (this.partialUpdate && this.innerValue.length === 1) {
          var _date2 = new Date(this.innerValue[0]);

          _date2.setFullYear(this.calendarYear);

          this.emitDate(setMonth(_date2, month), 'month');
        }
      }
    },
    handleSelectDate: function handleSelectDate(date) {
      this.emitDate(date, this.type === 'week' ? 'week' : 'date');
    },
    getMonthCellDate: function getMonthCellDate(month) {
      return createDate$1(this.calendarYear, month);
    },
    getYearCellDate: function getYearCellDate(year) {
      return createDate$1(year, 0);
    },
    getDateClasses: function getDateClasses(cellDate) {
      var notCurrentMonth = cellDate.getMonth() !== this.calendarMonth;
      var classes = [];

      if (cellDate.getTime() === new Date().setHours(0, 0, 0, 0)) {
        classes.push('today');
      }

      if (notCurrentMonth) {
        classes.push('not-current-month');
      }

      var state = this.getStateClass(cellDate);

      if (!(state === 'active' && notCurrentMonth)) {
        classes.push(state);
      }

      return classes.concat(this.getClasses(cellDate, this.innerValue, classes.join(' ')));
    },
    getMonthClasses: function getMonthClasses(month) {
      if (this.type !== 'month') {
        return this.calendarMonth === month ? 'active' : '';
      }

      var classes = [];
      var cellDate = this.getMonthCellDate(month);
      classes.push(this.getStateClass(cellDate));
      return classes.concat(this.getClasses(cellDate, this.innerValue, classes.join(' ')));
    },
    getYearClasses: function getYearClasses(year) {
      if (this.type !== 'year') {
        return this.calendarYear === year ? 'active' : '';
      }

      var classes = [];
      var cellDate = this.getYearCellDate(year);
      classes.push(this.getStateClass(cellDate));
      return classes.concat(this.getClasses(cellDate, this.innerValue, classes.join(' ')));
    },
    getStateClass: function getStateClass(cellDate) {
      if (this.isDisabled(cellDate)) {
        return 'disabled';
      }

      if (this.innerValue.some(function (v) {
        return v.getTime() === cellDate.getTime();
      })) {
        return 'active';
      }

      return '';
    },
    getWeekState: function getWeekState(row) {
      if (this.type !== 'week') return '';
      var start = row[0].getTime();
      var end = row[6].getTime();
      var active = this.innerValue.some(function (v) {
        var time = v.getTime();
        return time >= start && time <= end;
      });
      return active ? "".concat(this.prefixClass, "-active-week") : '';
    }
  },
  render: function render() {
    var h = arguments[0];
    var panel = this.panel,
        innerCalendar = this.innerCalendar;

    if (panel === 'year') {
      return h(__vue_component__$7$1, {
        "attrs": {
          "calendar": innerCalendar,
          "getCellClasses": this.getYearClasses,
          "getYearPanel": this.getYearPanel
        },
        "on": {
          "select": this.handleSelectYear,
          "changecalendar": this.handleCalendarChange
        }
      });
    }

    if (panel === 'month') {
      return h(__vue_component__$6$1, {
        "attrs": {
          "calendar": innerCalendar,
          "getCellClasses": this.getMonthClasses
        },
        "on": {
          "select": this.handleSelectMonth,
          "changepanel": this.handelPanelChange,
          "changecalendar": this.handleCalendarChange
        }
      });
    }

    return h(__vue_component__$5$1, {
      "class": _defineProperty$$({}, "".concat(this.prefixClass, "-calendar-week-mode"), this.type === 'week'),
      "attrs": {
        "calendar": innerCalendar,
        "getCellClasses": this.getDateClasses,
        "getRowClasses": this.getWeekState,
        "titleFormat": this.titleFormat,
        "showWeekNumber": typeof this.showWeekNumber === 'boolean' ? this.showWeekNumber : this.type === 'week'
      },
      "on": {
        "select": this.handleSelectDate,
        "changepanel": this.handelPanelChange,
        "changecalendar": this.handleCalendarChange
      }
    });
  }
};

var CalendarRange = {
  name: 'CalendarRange',
  components: {
    CalendarPanel: CalendarPanel
  },
  provide: function provide() {
    return {
      onDateMouseEnter: this.onDateMouseEnter,
      onDateMouseLeave: this.onDateMouseLeave
    };
  },
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  props: _objectSpread2({}, CalendarPanel.props),
  data: function data() {
    return {
      innerValue: [],
      calendars: [],
      hoveredValue: null
    };
  },
  computed: {
    // Minimum difference between start and end calendars
    calendarMinDiff: function calendarMinDiff() {
      var map = {
        date: 1,
        // type:date  min 1 month
        month: 1 * 12,
        // type:month min 1 year
        year: 10 * 12 // type:year  min 10 year

      };
      return map[this.type] || map.date;
    },
    calendarMaxDiff: function calendarMaxDiff() {
      return Infinity;
    },
    defaultValues: function defaultValues() {
      return Array.isArray(this.defaultValue) ? this.defaultValue : [this.defaultValue, this.defaultValue];
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: function handler() {
        var _this = this;

        this.innerValue = isValidRangeDate(this.value) ? this.value : [new Date(NaN), new Date(NaN)];
        var calendars = this.innerValue.map(function (v, i) {
          return startOfMonth(getValidDate(v, _this.defaultValues[i]));
        });
        this.updateCalendars(calendars);
      }
    }
  },
  methods: {
    handleSelect: function handleSelect(date, type) {
      var _this$innerValue = _slicedToArray$1(this.innerValue, 2),
          startValue = _this$innerValue[0],
          endValue = _this$innerValue[1];

      if (isValidDate$1(startValue) && !isValidDate$1(endValue)) {
        if (startValue.getTime() > date.getTime()) {
          this.innerValue = [date, startValue];
        } else {
          this.innerValue = [startValue, date];
        }

        this.emitDate(this.innerValue, type);
      } else {
        this.innerValue = [date, new Date(NaN)];
      }
    },
    onDateMouseEnter: function onDateMouseEnter(cell) {
      this.hoveredValue = cell;
    },
    onDateMouseLeave: function onDateMouseLeave() {
      this.hoveredValue = null;
    },
    emitDate: function emitDate(dates, type) {
      this.$emit('select', dates, type);
    },
    updateStartCalendar: function updateStartCalendar(value) {
      this.updateCalendars([value, this.calendars[1]], 1);
    },
    updateEndCalendar: function updateEndCalendar(value) {
      this.updateCalendars([this.calendars[0], value], 0);
    },
    updateCalendars: function updateCalendars(calendars) {
      var adjustIndex = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      var gap = this.getCalendarGap(calendars);

      if (gap) {
        var calendar = new Date(calendars[adjustIndex]);
        calendar.setMonth(calendar.getMonth() + (adjustIndex === 0 ? -gap : gap));
        calendars[adjustIndex] = calendar;
      }

      this.calendars = calendars;
    },
    getCalendarGap: function getCalendarGap(calendars) {
      var _calendars = _slicedToArray$1(calendars, 2),
          calendarLeft = _calendars[0],
          calendarRight = _calendars[1];

      var yearDiff = calendarRight.getFullYear() - calendarLeft.getFullYear();
      var monthDiff = calendarRight.getMonth() - calendarLeft.getMonth();
      var diff = yearDiff * 12 + monthDiff;
      var min = this.calendarMinDiff;
      var max = this.calendarMaxDiff;

      if (diff < min) {
        return min - diff;
      }

      if (diff > max) {
        return max - diff;
      }

      return 0;
    },
    getRangeClasses: function getRangeClasses(cellDate, currentDates, classnames) {
      var classes = [].concat(this.getClasses(cellDate, currentDates, classnames));
      if (/disabled|active/.test(classnames)) return classes;

      var inRange = function inRange(data, range) {
        var fn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (v) {
          return v.getTime();
        };
        var value = fn(data);

        var _range$map = range.map(fn),
            _range$map2 = _slicedToArray$1(_range$map, 2),
            min = _range$map2[0],
            max = _range$map2[1];

        if (min > max) {
          var _ref = [max, min];
          min = _ref[0];
          max = _ref[1];
        }

        return value > min && value < max;
      };

      if (currentDates.length === 2 && inRange(cellDate, currentDates)) {
        return classes.concat('in-range');
      }

      if (currentDates.length === 1 && this.hoveredValue && inRange(cellDate, [currentDates[0], this.hoveredValue])) {
        return classes.concat('hover-in-range');
      }

      return classes;
    }
  },
  render: function render() {
    var _this2 = this;

    var h = arguments[0];
    var calendarRange = this.calendars.map(function (calendar, index) {
      var props = _objectSpread2({}, _this2.$props, {
        calendar: calendar,
        value: _this2.innerValue,
        defaultValue: _this2.defaultValues[index],
        getClasses: _this2.getRangeClasses,
        // don't update when range is true
        partialUpdate: false
      });

      var on = {
        select: _this2.handleSelect,
        'update:calendar': index === 0 ? _this2.updateStartCalendar : _this2.updateEndCalendar
      };
      return h("calendar-panel", {
        "props": _objectSpread2({}, props),
        "on": _objectSpread2({}, on)
      });
    });
    var prefixClass = this.prefixClass;
    return h("div", {
      "class": "".concat(prefixClass, "-range-wrapper")
    }, [calendarRange]);
  }
};

var scrollBarWidth;
function getScrollbarWidth () {
  if (typeof window === 'undefined') return 0;
  if (scrollBarWidth !== undefined) return scrollBarWidth;
  var outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.width = '100px';
  outer.style.position = 'absolute';
  outer.style.top = '-9999px';
  document.body.appendChild(outer);
  var inner = document.createElement('div');
  inner.style.width = '100%';
  outer.appendChild(inner);
  scrollBarWidth = outer.offsetWidth - inner.offsetWidth;
  outer.parentNode.removeChild(outer);
  return scrollBarWidth;
}

//
var script$5$1 = {
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  data: function data() {
    return {
      scrollbarWidth: 0,
      thumbTop: '',
      thumbHeight: ''
    };
  },
  created: function created() {
    this.scrollbarWidth = getScrollbarWidth();
    document.addEventListener('mouseup', this.handleDragend);
  },
  beforeDestroy: function beforeDestroy() {
    document.addEventListener('mouseup', this.handleDragend);
  },
  mounted: function mounted() {
    this.$nextTick(this.getThumbSize);
  },
  methods: {
    getThumbSize: function getThumbSize() {
      var wrap = this.$refs.wrap;
      if (!wrap) return;
      var heightPercentage = wrap.clientHeight * 100 / wrap.scrollHeight;
      this.thumbHeight = heightPercentage < 100 ? "".concat(heightPercentage, "%") : '';
    },
    handleScroll: function handleScroll(evt) {
      var el = evt.currentTarget;
      var scrollHeight = el.scrollHeight,
          scrollTop = el.scrollTop;
      this.thumbTop = "".concat(scrollTop * 100 / scrollHeight, "%");
    },
    handleDragstart: function handleDragstart(evt) {
      evt.stopImmediatePropagation();
      this._draggable = true;
      var offsetTop = this.$refs.thumb.offsetTop;
      this._prevY = evt.clientY - offsetTop;
      document.addEventListener('mousemove', this.handleDraging);
    },
    handleDraging: function handleDraging(evt) {
      if (!this._draggable) return;
      var clientY = evt.clientY;
      var wrap = this.$refs.wrap;
      var scrollHeight = wrap.scrollHeight,
          clientHeight = wrap.clientHeight;
      var offsetY = clientY - this._prevY;
      var top = offsetY * scrollHeight / clientHeight;
      wrap.scrollTop = top;
    },
    handleDragend: function handleDragend() {
      if (this._draggable) {
        this._draggable = false;
        document.removeEventListener('mousemove', this.handleDraging);
      }
    }
  }
};

/* script */
var __vue_script__$5$1 = script$5$1;
/* template */

var __vue_render__$8$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.prefixClass + "-scrollbar",
    style: {
      position: 'relative',
      overflow: 'hidden'
    }
  }, [_c('div', {
    ref: "wrap",
    class: _vm.prefixClass + "-scrollbar-wrap",
    style: {
      marginRight: "-" + _vm.scrollbarWidth + "px"
    },
    on: {
      "scroll": _vm.handleScroll
    }
  }, [_vm._t("default")], 2), _vm._v(" "), _c('div', {
    class: _vm.prefixClass + "-scrollbar-track"
  }, [_c('div', {
    ref: "thumb",
    class: _vm.prefixClass + "-scrollbar-thumb",
    style: {
      height: _vm.thumbHeight,
      top: _vm.thumbTop
    },
    on: {
      "mousedown": _vm.handleDragstart
    }
  })])]);
};

var __vue_staticRenderFns__$8$1 = [];
/* style */

var __vue_inject_styles__$8$1 = undefined;
/* scoped */

var __vue_scope_id__$8$1 = undefined;
/* module identifier */

var __vue_module_identifier__$8$1 = undefined;
/* functional template */

var __vue_is_functional_template__$8$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$8$1 = normalizeComponent$1({
  render: __vue_render__$8$1,
  staticRenderFns: __vue_staticRenderFns__$8$1
}, __vue_inject_styles__$8$1, __vue_script__$5$1, __vue_scope_id__$8$1, __vue_is_functional_template__$8$1, __vue_module_identifier__$8$1, false, undefined, undefined, undefined);

//

var padNumber = function padNumber(value) {
  value = parseInt(value, 10);
  return value < 10 ? "0".concat(value) : "".concat(value);
};

var generateOptions = function generateOptions(length, step, options) {
  if (Array.isArray(options)) {
    return options.filter(function (v) {
      return v >= 0 && v < length;
    });
  }

  if (step <= 0) {
    step = 1;
  }

  var arr = [];

  for (var i = 0; i < length; i += step) {
    arr.push(i);
  }

  return arr;
};

var scrollTo = function scrollTo(element, to) {
  var duration = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;

  // jump to target if duration zero
  if (duration <= 0) {
    requestAnimationFrame(function () {
      element.scrollTop = to;
    });
    return;
  }

  var difference = to - element.scrollTop;
  var tick = difference / duration * 10;
  requestAnimationFrame(function () {
    var scrollTop = element.scrollTop + tick;

    if (scrollTop >= to) {
      element.scrollTop = to;
      return;
    }

    element.scrollTop = scrollTop;
    scrollTo(element, to, duration - 10);
  });
};

var script$6$1 = {
  name: 'ListColumns',
  components: {
    ScrollbarVertical: __vue_component__$8$1
  },
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  props: {
    date: Date,
    scrollDuration: {
      type: Number,
      default: 100
    },
    getClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    },
    hourOptions: Array,
    minuteOptions: Array,
    secondOptions: Array,
    showHour: {
      type: Boolean,
      default: true
    },
    showMinute: {
      type: Boolean,
      default: true
    },
    showSecond: {
      type: Boolean,
      default: true
    },
    hourStep: {
      type: Number,
      default: 1
    },
    minuteStep: {
      type: Number,
      default: 1
    },
    secondStep: {
      type: Number,
      default: 1
    },
    use12h: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    columns: function columns() {
      var cols = [];
      if (this.showHour) cols.push({
        type: 'hour',
        list: this.getHoursList()
      });
      if (this.showMinute) cols.push({
        type: 'minute',
        list: this.getMinutesList()
      });
      if (this.showSecond) cols.push({
        type: 'second',
        list: this.getSecondsList()
      });
      if (this.use12h) cols.push({
        type: 'ampm',
        list: this.getAMPMList()
      });
      return cols.filter(function (v) {
        return v.list.length > 0;
      });
    }
  },
  watch: {
    date: {
      handler: function handler() {
        var _this = this;

        this.$nextTick(function () {
          _this.scrollToSelected(_this.scrollDuration);
        });
      }
    }
  },
  mounted: function mounted() {
    this.scrollToSelected(0);
  },
  methods: {
    getHoursList: function getHoursList() {
      var _this2 = this;

      return generateOptions(this.use12h ? 12 : 24, this.hourStep, this.hourOptions).map(function (num) {
        var date = new Date(_this2.date);
        var text = padNumber(num);

        if (_this2.use12h) {
          if (num === 0) {
            text = '12';
          }

          if (date.getHours() >= 12) {
            num += 12;
          }
        }

        var value = date.setHours(num);
        return {
          value: value,
          text: text
        };
      });
    },
    getMinutesList: function getMinutesList() {
      var _this3 = this;

      return generateOptions(60, this.minuteStep, this.minuteOptions).map(function (num) {
        var value = new Date(_this3.date).setMinutes(num);
        return {
          value: value,
          text: padNumber(num)
        };
      });
    },
    getSecondsList: function getSecondsList() {
      var _this4 = this;

      return generateOptions(60, this.secondStep, this.secondOptions).map(function (num) {
        var value = new Date(_this4.date).setSeconds(num);
        return {
          value: value,
          text: padNumber(num)
        };
      });
    },
    getAMPMList: function getAMPMList() {
      var _this5 = this;

      return ['AM', 'PM'].map(function (text, i) {
        var date = new Date(_this5.date);
        var value = date.setHours(date.getHours() % 12 + i * 12);
        return {
          text: text,
          value: value
        };
      });
    },
    scrollToSelected: function scrollToSelected(duration) {
      var elements = this.$el.querySelectorAll('.active');

      for (var i = 0; i < elements.length; i++) {
        var element = elements[i];
        var scrollElement = getScrollParent$1(element, this.$el);

        if (scrollElement) {
          var to = element.offsetTop;
          scrollTo(scrollElement, to, duration);
        }
      }
    },
    handleSelect: function handleSelect(evt) {
      var target = evt.target,
          currentTarget = evt.currentTarget;
      if (target.tagName.toUpperCase() !== 'LI') return;
      var type = currentTarget.getAttribute('data-type');
      var colIndex = parseInt(currentTarget.getAttribute('data-index'), 10);
      var cellIndex = parseInt(target.getAttribute('data-index'), 10);
      var value = this.columns[colIndex].list[cellIndex].value;
      this.$emit('select', value, type);
    }
  }
};

/* script */
var __vue_script__$6$1 = script$6$1;
/* template */

var __vue_render__$9$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.prefixClass + "-time-columns"
  }, _vm._l(_vm.columns, function (col, i) {
    return _c('scrollbar-vertical', {
      key: i,
      class: _vm.prefixClass + "-time-column"
    }, [_c('ul', {
      class: _vm.prefixClass + "-time-list",
      attrs: {
        "data-type": col.type,
        "data-index": i
      },
      on: {
        "click": _vm.handleSelect
      }
    }, _vm._l(col.list, function (item, j) {
      return _c('li', {
        key: item.value,
        class: [_vm.prefixClass + "-time-item", _vm.getClasses(item.value, col.type)],
        attrs: {
          "data-index": j
        }
      }, [_vm._v("\n        " + _vm._s(item.text) + "\n      ")]);
    }), 0)]);
  }), 1);
};

var __vue_staticRenderFns__$9$1 = [];
/* style */

var __vue_inject_styles__$9$1 = undefined;
/* scoped */

var __vue_scope_id__$9$1 = undefined;
/* module identifier */

var __vue_module_identifier__$9$1 = undefined;
/* functional template */

var __vue_is_functional_template__$9$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$9$1 = normalizeComponent$1({
  render: __vue_render__$9$1,
  staticRenderFns: __vue_staticRenderFns__$9$1
}, __vue_inject_styles__$9$1, __vue_script__$6$1, __vue_scope_id__$9$1, __vue_is_functional_template__$9$1, __vue_module_identifier__$9$1, false, undefined, undefined, undefined);

//

function parseOption() {
  var time = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var values = time.split(':');

  if (values.length >= 2) {
    var hours = parseInt(values[0], 10);
    var minutes = parseInt(values[1], 10);
    return {
      hours: hours,
      minutes: minutes
    };
  }

  return null;
}

var scrollTo$1 = function scrollTo(element, to) {
  if (element) {
    element.scrollTop = to;
  }
};

var script$7$1 = {
  name: 'ListOptions',
  components: {
    ScrollbarVertical: __vue_component__$8$1
  },
  inject: {
    getLocale: {
      default: function _default() {
        return getLocale;
      }
    },
    prefixClass: {
      default: 'mx'
    }
  },
  props: {
    date: Date,
    options: {
      type: [Object, Function],
      default: function _default() {
        return [];
      }
    },
    format: {
      type: String,
      default: 'HH:mm:ss'
    },
    getClasses: {
      type: Function,
      default: function _default() {
        return [];
      }
    }
  },
  computed: {
    list: function list() {
      var result = [];
      var options = this.options;

      if (typeof options === 'function') {
        return options() || [];
      }

      var start = parseOption(options.start);
      var end = parseOption(options.end);
      var step = parseOption(options.step);
      var fmt = options.format || this.format;

      if (start && end && step) {
        var startMinutes = start.minutes + start.hours * 60;
        var endMinutes = end.minutes + end.hours * 60;
        var stepMinutes = step.minutes + step.hours * 60;
        var len = Math.floor((endMinutes - startMinutes) / stepMinutes);

        for (var i = 0; i <= len; i++) {
          var timeMinutes = startMinutes + i * stepMinutes;
          var hours = Math.floor(timeMinutes / 60);
          var minutes = timeMinutes % 60;
          var value = new Date(this.date).setHours(hours, minutes, 0);
          result.push({
            value: value,
            text: this.formatDate(value, fmt)
          });
        }
      }

      return result;
    }
  },
  mounted: function mounted() {
    this.scrollToSelected();
  },
  methods: {
    formatDate: function formatDate(date, fmt) {
      return format(date, fmt, {
        locale: this.getLocale().formatLocale
      });
    },
    scrollToSelected: function scrollToSelected() {
      var element = this.$el.querySelector('.active');
      if (!element) return;
      var scrollElement = getScrollParent$1(element, this.$el);
      if (!scrollElement) return;
      var to = element.offsetTop;
      scrollTo$1(scrollElement, to);
    },
    handleSelect: function handleSelect(value) {
      this.$emit('select', value, 'time');
    }
  }
};

/* script */
var __vue_script__$7$1 = script$7$1;
/* template */

var __vue_render__$a$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('scrollbar-vertical', _vm._l(_vm.list, function (item) {
    return _c('div', {
      key: item.value,
      class: [_vm.prefixClass + "-time-option", _vm.getClasses(item.value)],
      on: {
        "click": function click($event) {
          return _vm.handleSelect(item.value);
        }
      }
    }, [_vm._v("\n    " + _vm._s(item.text) + "\n  ")]);
  }), 0);
};

var __vue_staticRenderFns__$a$1 = [];
/* style */

var __vue_inject_styles__$a$1 = undefined;
/* scoped */

var __vue_scope_id__$a$1 = undefined;
/* module identifier */

var __vue_module_identifier__$a$1 = undefined;
/* functional template */

var __vue_is_functional_template__$a$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$a$1 = normalizeComponent$1({
  render: __vue_render__$a$1,
  staticRenderFns: __vue_staticRenderFns__$a$1
}, __vue_inject_styles__$a$1, __vue_script__$7$1, __vue_scope_id__$a$1, __vue_is_functional_template__$a$1, __vue_module_identifier__$a$1, false, undefined, undefined, undefined);

//
var script$8$1 = {
  name: 'TimePanel',
  components: {
    ListColumns: __vue_component__$9$1,
    ListOptions: __vue_component__$a$1
  },
  inject: {
    getLocale: {
      default: function _default() {
        return getLocale;
      }
    },
    prefixClass: {
      default: 'mx'
    }
  },
  props: {
    value: {},
    defaultValue: {
      default: function _default() {
        var date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
      }
    },
    format: {
      default: 'HH:mm:ss'
    },
    timeTitleFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    showTimeHeader: {
      type: Boolean,
      default: false
    },
    disabledTime: {
      type: Function,
      default: function _default() {
        return false;
      }
    },
    timePickerOptions: {
      type: [Object, Function],
      default: function _default() {
        return null;
      }
    },
    hourOptions: Array,
    minuteOptions: Array,
    secondOptions: Array,
    hourStep: {
      type: Number,
      default: 1
    },
    minuteStep: {
      type: Number,
      default: 1
    },
    secondStep: {
      type: Number,
      default: 1
    },
    showHour: {
      type: Boolean,
      default: undefined
    },
    showMinute: {
      type: Boolean,
      default: undefined
    },
    showSecond: {
      type: Boolean,
      default: undefined
    },
    use12h: {
      type: Boolean,
      default: undefined
    },
    scrollDuration: {
      type: Number,
      default: 100
    }
  },
  data: function data() {
    return {
      innerValue: getValidDate(this.value, this.defaultValue)
    };
  },
  computed: {
    title: function title() {
      var titleFormat = this.timeTitleFormat;
      var date = new Date(this.innerValue);
      return this.formatDate(date, titleFormat);
    },
    innerForamt: function innerForamt() {
      return typeof this.format === 'string' ? this.format : 'HH:mm:ss';
    },
    ShowHourMinuteSecondAMPM: function ShowHourMinuteSecondAMPM() {
      var _this = this;

      var fmt = this.innerForamt;
      var defaultProps = {
        showHour: /[HhKk]/.test(fmt),
        showMinute: /m/.test(fmt),
        showSecond: /s/.test(fmt),
        use12h: /a/i.test(fmt)
      };
      var obj = {};
      Object.keys(defaultProps).forEach(function (key) {
        obj[key] = typeof _this[key] === 'boolean' ? _this[key] : defaultProps[key];
      });
      return obj;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: function handler() {
        this.innerValue = getValidDate(this.value, this.defaultValue);
      }
    }
  },
  methods: {
    formatDate: function formatDate(date, fmt) {
      return format(date, fmt, {
        locale: this.getLocale().formatLocale
      });
    },
    isDisabledTime: function isDisabledTime(value) {
      return this.disabledTime(new Date(value));
    },
    isDisabledHour: function isDisabledHour(date) {
      var value = new Date(date);
      return this.isDisabledTime(value) && this.isDisabledTime(value.setMinutes(0, 0, 0)) && this.isDisabledTime(value.setMinutes(59, 59, 999));
    },
    isDisabledMinute: function isDisabledMinute(date) {
      var value = new Date(date);
      return this.isDisabledTime(value) && this.isDisabledTime(value.setSeconds(0, 0)) && this.isDisabledTime(value.setSeconds(59, 999));
    },
    isDisabled: function isDisabled(date, type) {
      if (type === 'hour') {
        return this.isDisabledHour(date);
      }

      if (type === 'minute') {
        return this.isDisabledMinute(date);
      }

      return this.isDisabledTime(date);
    },
    handleSelect: function handleSelect(value, type) {
      var date = new Date(value);

      if (!this.isDisabled(value, type)) {
        this.innerValue = date;

        if (!this.isDisabledTime(date)) {
          this.$emit('select', date, type);
        }
      }
    },
    handleClickTitle: function handleClickTitle() {
      this.$emit('clicktitle');
    },
    getClasses: function getClasses(value, type) {
      var cellDate = new Date(value);

      if (this.isDisabled(value, type)) {
        return 'disabled';
      }

      if (cellDate.getTime() === this.innerValue.getTime()) {
        return 'active';
      }

      return '';
    }
  }
};

/* script */
var __vue_script__$8$1 = script$8$1;
/* template */

var __vue_render__$b$1 = function __vue_render__() {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.prefixClass + "-time"
  }, [_vm.showTimeHeader ? _c('div', {
    class: _vm.prefixClass + "-time-header"
  }, [_c('button', {
    class: _vm.prefixClass + "-btn " + _vm.prefixClass + "-btn-text " + _vm.prefixClass + "-time-header-title",
    attrs: {
      "type": "button"
    },
    on: {
      "click": _vm.handleClickTitle
    }
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")])]) : _vm._e(), _vm._v(" "), _c('div', {
    class: _vm.prefixClass + "-time-content"
  }, [_vm.timePickerOptions ? _c('list-options', {
    attrs: {
      "date": _vm.innerValue,
      "get-classes": _vm.getClasses,
      "options": _vm.timePickerOptions,
      "format": _vm.innerForamt
    },
    on: {
      "select": _vm.handleSelect
    }
  }) : _c('list-columns', _vm._b({
    attrs: {
      "date": _vm.innerValue,
      "get-classes": _vm.getClasses,
      "hour-options": _vm.hourOptions,
      "minute-options": _vm.minuteOptions,
      "second-options": _vm.secondOptions,
      "hour-step": _vm.hourStep,
      "minute-step": _vm.minuteStep,
      "second-step": _vm.secondStep,
      "scroll-duration": _vm.scrollDuration
    },
    on: {
      "select": _vm.handleSelect
    }
  }, 'list-columns', _vm.ShowHourMinuteSecondAMPM, false))], 1)]);
};

var __vue_staticRenderFns__$b$1 = [];
/* style */

var __vue_inject_styles__$b$1 = undefined;
/* scoped */

var __vue_scope_id__$b$1 = undefined;
/* module identifier */

var __vue_module_identifier__$b$1 = undefined;
/* functional template */

var __vue_is_functional_template__$b$1 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

var __vue_component__$b$1 = normalizeComponent$1({
  render: __vue_render__$b$1,
  staticRenderFns: __vue_staticRenderFns__$b$1
}, __vue_inject_styles__$b$1, __vue_script__$8$1, __vue_scope_id__$b$1, __vue_is_functional_template__$b$1, __vue_module_identifier__$b$1, false, undefined, undefined, undefined);

var TimeRange = {
  name: 'TimeRange',
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  props: _objectSpread2({}, __vue_component__$b$1.props),
  data: function data() {
    return {
      startValue: new Date(NaN),
      endValue: new Date(NaN)
    };
  },
  watch: {
    value: {
      immediate: true,
      handler: function handler() {
        if (isValidRangeDate(this.value)) {
          var _this$value = _slicedToArray$1(this.value, 2),
              startValue = _this$value[0],
              endValue = _this$value[1];

          this.startValue = startValue;
          this.endValue = endValue;
        } else {
          this.startValue = new Date(NaN);
          this.endValue = new Date(NaN);
        }
      }
    }
  },
  methods: {
    emitChange: function emitChange(type, index) {
      var date = [this.startValue, this.endValue];
      this.$emit('select', date, type === 'time' ? 'time-range' : type, index);
    },
    handleSelectStart: function handleSelectStart(date, type) {
      this.startValue = date; // check the NaN

      if (!(this.endValue.getTime() >= date.getTime())) {
        this.endValue = date;
      }

      this.emitChange(type, 0);
    },
    handleSelectEnd: function handleSelectEnd(date, type) {
      // check the NaN
      this.endValue = date;

      if (!(this.startValue.getTime() <= date.getTime())) {
        this.startValue = date;
      }

      this.emitChange(type, 1);
    },
    disabledStartTime: function disabledStartTime(date) {
      return this.disabledTime(date, 0);
    },
    disabledEndTime: function disabledEndTime(date) {
      return date.getTime() < this.startValue.getTime() || this.disabledTime(date, 1);
    }
  },
  render: function render() {
    var h = arguments[0];
    var defaultValues = Array.isArray(this.defaultValue) ? this.defaultValue : [this.defaultValue, this.defaultValue];
    var prefixClass = this.prefixClass;
    return h("div", {
      "class": "".concat(prefixClass, "-range-wrapper")
    }, [h(__vue_component__$b$1, {
      "props": _objectSpread2({}, _objectSpread2({}, this.$props, {
        value: this.startValue,
        defaultValue: defaultValues[0],
        disabledTime: this.disabledStartTime
      })),
      "on": _objectSpread2({}, _objectSpread2({}, this.$listeners, {
        select: this.handleSelectStart
      }))
    }), h(__vue_component__$b$1, {
      "props": _objectSpread2({}, _objectSpread2({}, this.$props, {
        value: this.endValue,
        defaultValue: defaultValues[1],
        disabledTime: this.disabledEndTime
      })),
      "on": _objectSpread2({}, _objectSpread2({}, this.$listeners, {
        select: this.handleSelectEnd
      }))
    })]);
  }
};

var DatetimePanel = {
  name: 'DatetimePanel',
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  emits: ['select', 'update:show-time-panel'],
  props: _objectSpread2({}, CalendarPanel.props, {}, __vue_component__$b$1.props, {
    showTimePanel: {
      type: Boolean,
      default: undefined
    }
  }),
  data: function data() {
    return {
      defaultTimeVisible: false,
      currentValue: this.value
    };
  },
  computed: {
    timeVisible: function timeVisible() {
      return typeof this.showTimePanel === 'boolean' ? this.showTimePanel : this.defaultTimeVisible;
    }
  },
  watch: {
    value: function value(val) {
      this.currentValue = val;
    }
  },
  methods: {
    closeTimePanel: function closeTimePanel() {
      this.defaultTimeVisible = false;
      this.$emit('update:show-time-panel', false);
    },
    openTimePanel: function openTimePanel() {
      this.defaultTimeVisible = true;
      this.$emit('update:show-time-panel', true);
    },
    emitDate: function emitDate(date, type) {
      this.$emit('select', date, type);
    },
    handleSelect: function handleSelect(date, type) {
      if (type === 'date') {
        this.openTimePanel();
      }

      var datetime = assignTime(date, getValidDate(this.value, this.defaultValue));

      if (this.disabledTime(new Date(datetime))) {
        // set the time of defalutValue;
        datetime = assignTime(date, this.defaultValue);

        if (this.disabledTime(new Date(datetime))) {
          // if disabled don't emit date
          this.currentValue = datetime;
          return;
        }
      }

      this.emitDate(datetime, type);
    }
  },
  render: function render() {
    var h = arguments[0];
    var calendarProps = {
      props: _objectSpread2({}, pick$2(this.$props, Object.keys(CalendarPanel.props)), {
        type: 'date',
        value: this.currentValue
      }),
      on: {
        select: this.handleSelect
      }
    };
    var timeProps = {
      props: _objectSpread2({}, pick$2(this.$props, Object.keys(__vue_component__$b$1.props)), {
        showTimeHeader: true,
        value: this.currentValue
      }),
      on: {
        select: this.emitDate,
        clicktitle: this.closeTimePanel
      }
    };
    var prefixClass = this.prefixClass;
    return h("div", [h(CalendarPanel, helper([{}, calendarProps])), this.timeVisible && h(__vue_component__$b$1, helper([{
      "class": "".concat(prefixClass, "-calendar-time")
    }, timeProps]))]);
  }
};

var DatetimeRange = {
  name: 'DatetimeRange',
  inject: {
    prefixClass: {
      default: 'mx'
    }
  },
  emits: ['select', 'update:show-time-panel'],
  props: _objectSpread2({}, CalendarRange.props, {}, TimeRange.props, {
    showTimePanel: {
      type: Boolean,
      default: undefined
    }
  }),
  data: function data() {
    return {
      defaultTimeVisible: false,
      currentValue: this.value
    };
  },
  computed: {
    timeVisible: function timeVisible() {
      return typeof this.showTimePanel === 'boolean' ? this.showTimePanel : this.defaultTimeVisible;
    }
  },
  watch: {
    value: function value(val) {
      this.currentValue = val;
    }
  },
  methods: {
    closeTimePanel: function closeTimePanel() {
      this.defaultTimeVisible = false;
      this.$emit('update:show-time-panel', false);
    },
    openTimePanel: function openTimePanel() {
      this.defaultTimeVisible = true;
      this.$emit('update:show-time-panel', true);
    },
    emitDate: function emitDate(dates, type) {
      this.$emit('select', dates, type);
    },
    handleSelect: function handleSelect(dates, type) {
      var _this = this;

      if (type === 'date') {
        this.openTimePanel();
      }

      var defaultValues = Array.isArray(this.defaultValue) ? this.defaultValue : [this.defaultValue, this.defaultValue];
      var datetimes = dates.map(function (date, i) {
        var time = isValidRangeDate(_this.value) ? _this.value[i] : defaultValues[i];
        return assignTime(date, time);
      });

      if (datetimes[1].getTime() < datetimes[0].getTime()) {
        datetimes = [datetimes[0], datetimes[0]];
      }

      if (datetimes.some(this.disabledTime)) {
        datetimes = dates.map(function (date, i) {
          return assignTime(date, defaultValues[i]);
        });

        if (datetimes.some(this.disabledTime)) {
          this.currentValue = datetimes;
          return;
        }
      }

      this.emitDate(datetimes, type);
    }
  },
  render: function render() {
    var h = arguments[0];
    var calendarProps = {
      props: _objectSpread2({}, pick$2(this.$props, Object.keys(CalendarRange.props)), {
        type: 'date',
        value: this.currentValue
      }),
      on: {
        select: this.handleSelect
      }
    };
    var timeProps = {
      props: _objectSpread2({}, pick$2(this.$props, Object.keys(TimeRange.props)), {
        value: this.currentValue,
        showTimeHeader: true
      }),
      on: {
        select: this.emitDate,
        clicktitle: this.closeTimePanel
      }
    };
    var prefixClass = this.prefixClass;
    return h("div", [h(CalendarRange, helper([{}, calendarProps])), this.timeVisible && h(TimeRange, helper([{
      "class": "".concat(prefixClass, "-calendar-time")
    }, timeProps]))]);
  }
};

var componentMap = {
  default: CalendarPanel,
  time: __vue_component__$b$1,
  datetime: DatetimePanel
};
var componentRangeMap = {
  default: CalendarRange,
  time: TimeRange,
  datetime: DatetimeRange
};
var DatePicker = {
  name: 'DatePicker',
  provide: function provide() {
    var _this = this;

    return {
      // make locale reactive
      getLocale: function getLocale() {
        return _this.locale;
      },
      getWeek: this.getWeek,
      prefixClass: this.prefixClass,
      dispatchDatePicker: this.$emit.bind(this)
    };
  },
  props: _objectSpread2({}, DatetimePanel.props, {
    value: {},
    valueType: {
      type: String,
      default: 'date' // date, format, timestamp, or token like 'YYYY-MM-DD'

    },
    type: {
      type: String,
      // ['date', 'datetime', 'time', 'year', 'month', 'week']
      default: 'date'
    },
    format: {
      type: String
    },
    formatter: {
      type: Object
    },
    range: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    rangeSeparator: {
      type: String
    },
    lang: {
      type: [String, Object]
    },
    placeholder: {
      type: String,
      default: ''
    },
    editable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    prefixClass: {
      type: String,
      default: 'mx'
    },
    inputClass: {},
    inputAttr: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    open: {
      type: Boolean,
      default: undefined
    },
    popupClass: {},
    popupStyle: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    inline: {
      type: Boolean,
      default: false
    },
    confirm: {
      type: Boolean,
      default: false
    },
    confirmText: {
      type: String,
      default: 'OK'
    },
    renderInputText: {
      type: Function
    },
    shortcuts: {
      type: Array,
      validator: function validator(value) {
        return Array.isArray(value) && value.every(function (v) {
          return isObject$2(v) && typeof v.text === 'string' && typeof v.onClick === 'function';
        });
      },
      default: function _default() {
        return [];
      }
    }
  }),
  data: function data() {
    return {
      // cache the innervalue, wait to confirm
      currentValue: null,
      userInput: null,
      defaultOpen: false
    };
  },
  computed: {
    popupVisible: function popupVisible() {
      return !this.disabled && (typeof this.open === 'boolean' ? this.open : this.defaultOpen);
    },
    innerRangeSeparator: function innerRangeSeparator() {
      return this.rangeSeparator || (this.multiple ? ',' : ' ~ ');
    },
    innerFormat: function innerFormat() {
      var map = {
        date: 'YYYY-MM-DD',
        datetime: 'YYYY-MM-DD HH:mm:ss',
        year: 'YYYY',
        month: 'YYYY-MM',
        time: 'HH:mm:ss',
        week: 'w'
      };
      return this.format || map[this.type] || map.date;
    },
    innerValue: function innerValue() {
      var value = this.value;

      if (this.validMultipleType) {
        value = Array.isArray(value) ? value : [];
        return value.map(this.value2date);
      }

      if (this.range) {
        value = Array.isArray(value) ? value.slice(0, 2) : [null, null];
        return value.map(this.value2date);
      }

      return this.value2date(value);
    },
    text: function text() {
      var _this2 = this;

      if (this.userInput !== null) {
        return this.userInput;
      }

      if (typeof this.renderInputText === 'function') {
        return this.renderInputText(this.innerValue);
      }

      if (!this.isValidValue(this.innerValue)) {
        return '';
      }

      if (Array.isArray(this.innerValue)) {
        return this.innerValue.map(function (v) {
          return _this2.formatDate(v);
        }).join(this.innerRangeSeparator);
      }

      return this.formatDate(this.innerValue);
    },
    showClearIcon: function showClearIcon() {
      return !this.disabled && this.clearable && this.text;
    },
    locale: function locale() {
      if (isObject$2(this.lang)) {
        return mergeDeep$1(getLocale(), this.lang);
      }

      return getLocale(this.lang);
    },
    validMultipleType: function validMultipleType() {
      var types = ['date', 'month', 'year'];
      return this.multiple && !this.range && types.indexOf(this.type) !== -1;
    }
  },
  watch: {
    innerValue: {
      immediate: true,
      handler: function handler(val) {
        this.currentValue = val;
      }
    },
    popupVisible: {
      handler: function handler(val) {
        if (val) {
          this.currentValue = this.innerValue;
        }
      }
    }
  },
  created: function created() {
    if (_typeof$5(this.format) === 'object') {
      console.warn("[vue2-datepicker]: The prop `format` don't support Object any more. You can use the new prop `formatter` to replace it");
    }
  },
  methods: {
    handleClickOutSide: function handleClickOutSide(evt) {
      var target = evt.target;

      if (!this.$el.contains(target)) {
        this.closePopup();
      }
    },
    getFormatter: function getFormatter(key) {
      return isObject$2(this.formatter) && this.formatter[key] || isObject$2(this.format) && this.format[key];
    },
    getWeek: function getWeek$1(date, options) {
      if (typeof this.getFormatter('getWeek') === 'function') {
        return this.getFormatter('getWeek')(date, options);
      }

      return getWeek(date, options);
    },
    parseDate: function parseDate(value, fmt) {
      fmt = fmt || this.innerFormat;

      if (typeof this.getFormatter('parse') === 'function') {
        return this.getFormatter('parse')(value, fmt);
      }

      var backupDate = new Date();
      return parse(value, fmt, {
        locale: this.locale.formatLocale,
        backupDate: backupDate
      });
    },
    formatDate: function formatDate(date, fmt) {
      fmt = fmt || this.innerFormat;

      if (typeof this.getFormatter('stringify') === 'function') {
        return this.getFormatter('stringify')(date, fmt);
      }

      return format(date, fmt, {
        locale: this.locale.formatLocale
      });
    },
    // transform the outer value to inner date
    value2date: function value2date(value) {
      switch (this.valueType) {
        case 'date':
          return value instanceof Date ? new Date(value.getTime()) : new Date(NaN);

        case 'timestamp':
          return typeof value === 'number' ? new Date(value) : new Date(NaN);

        case 'format':
          return typeof value === 'string' ? this.parseDate(value) : new Date(NaN);

        default:
          return typeof value === 'string' ? this.parseDate(value, this.valueType) : new Date(NaN);
      }
    },
    // transform the inner date to outer value
    date2value: function date2value(date) {
      if (!isValidDate$1(date)) return null;

      switch (this.valueType) {
        case 'date':
          return date;

        case 'timestamp':
          return date.getTime();

        case 'format':
          return this.formatDate(date);

        default:
          return this.formatDate(date, this.valueType);
      }
    },
    emitValue: function emitValue(date, type) {
      var close = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
      // fix IE11/10 trigger input event when input is focused. (placeholder !== '')
      this.userInput = null;
      var value = Array.isArray(date) ? date.map(this.date2value) : this.date2value(date);
      this.$emit('input', value);
      this.$emit('change', value, type);

      if (close) {
        this.closePopup();
      }

      return value;
    },
    isValidValue: function isValidValue(value) {
      if (this.validMultipleType) {
        return isValidDates(value);
      }

      if (this.range) {
        return isValidRangeDate(value);
      }

      return isValidDate$1(value);
    },
    isValidValueAndNotDisabled: function isValidValueAndNotDisabled(value) {
      if (!this.isValidValue(value)) {
        return false;
      }

      var disabledDate = typeof this.disabledDate === 'function' ? this.disabledDate : function () {
        return false;
      };
      var disabledTime = typeof this.disabledTime === 'function' ? this.disabledTime : function () {
        return false;
      };

      if (!Array.isArray(value)) {
        value = [value];
      }

      return value.every(function (v) {
        return !disabledDate(v) && !disabledTime(v);
      });
    },
    handleMultipleDates: function handleMultipleDates(date, dates) {
      if (this.validMultipleType && dates) {
        var nextDates = dates.filter(function (v) {
          return v.getTime() !== date.getTime();
        });

        if (nextDates.length === dates.length) {
          nextDates.push(date);
        }

        return nextDates;
      }

      return date;
    },
    handleSelectDate: function handleSelectDate(val, type, dates) {
      val = this.handleMultipleDates(val, dates);

      if (this.confirm) {
        this.currentValue = val;
      } else {
        this.emitValue(val, type, // this.type === 'datetime', click the time should close popup
        !this.validMultipleType && (type === this.type || type === 'time'));
      }
    },
    clear: function clear() {
      this.emitValue(this.range ? [null, null] : null);
      this.$emit('clear');
    },
    handleClear: function handleClear(evt) {
      evt.stopPropagation();
      this.clear();
    },
    handleConfirmDate: function handleConfirmDate() {
      var value = this.emitValue(this.currentValue);
      this.$emit('confirm', value);
    },
    handleSelectShortcut: function handleSelectShortcut(evt) {
      var index = evt.currentTarget.getAttribute('data-index');
      var item = this.shortcuts[parseInt(index, 10)];

      if (isObject$2(item) && typeof item.onClick === 'function') {
        var date = item.onClick(this);

        if (date) {
          this.emitValue(date);
        }
      }
    },
    openPopup: function openPopup(evt) {
      if (this.popupVisible || this.disabled) return;
      this.defaultOpen = true;
      this.$emit('open', evt);
      this.$emit('update:open', true);
    },
    closePopup: function closePopup() {
      if (!this.popupVisible) return;
      this.defaultOpen = false;
      this.$emit('close');
      this.$emit('update:open', false);
    },
    blur: function blur() {
      // when use slot input
      if (this.$refs.input) {
        this.$refs.input.blur();
      }
    },
    focus: function focus() {
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    },
    handleInputChange: function handleInputChange() {
      var _this3 = this;

      if (!this.editable || this.userInput === null) return;
      var text = this.userInput.trim();
      this.userInput = null;

      if (text === '') {
        this.clear();
        return;
      }

      var date;

      if (this.validMultipleType) {
        date = text.split(this.innerRangeSeparator).map(function (v) {
          return _this3.parseDate(v.trim());
        });
      } else if (this.range) {
        var arr = text.split(this.innerRangeSeparator);

        if (arr.length !== 2) {
          // Maybe the separator during the day is the same as the separator for the date
          // eg: 2019-10-09-2020-01-02
          arr = text.split(this.innerRangeSeparator.trim());
        }

        date = arr.map(function (v) {
          return _this3.parseDate(v.trim());
        });
      } else {
        date = this.parseDate(text);
      }

      if (this.isValidValueAndNotDisabled(date)) {
        this.emitValue(date);
        this.blur();
      } else {
        this.$emit('input-error', text);
      }
    },
    handleInputInput: function handleInputInput(evt) {
      // slot input v-model
      this.userInput = typeof evt === 'string' ? evt : evt.target.value;
    },
    handleInputKeydown: function handleInputKeydown(evt) {
      var keyCode = evt.keyCode; // Tab 9 or Enter 13

      if (keyCode === 9) {
        this.closePopup();
      } else if (keyCode === 13) {
        this.handleInputChange();
      }
    },
    handleInputBlur: function handleInputBlur(evt) {
      // tab close
      this.$emit('blur', evt);
    },
    handleInputFocus: function handleInputFocus(evt) {
      this.openPopup(evt);
      this.$emit('focus', evt);
    },
    hasSlot: function hasSlot(name) {
      return !!(this.$slots[name] || this.$scopedSlots[name]);
    },
    renderSlot: function renderSlot(name, fallback, props) {
      var slotFn = this.$scopedSlots[name];

      if (slotFn) {
        return slotFn(props) || fallback;
      }

      return this.$slots[name] || fallback;
    },
    renderInput: function renderInput() {
      var h = this.$createElement;
      var prefixClass = this.prefixClass;

      var props = _objectSpread2({
        name: 'date',
        type: 'text',
        autocomplete: 'off',
        value: this.text,
        class: this.inputClass || "".concat(this.prefixClass, "-input"),
        readonly: !this.editable,
        disabled: this.disabled,
        placeholder: this.placeholder
      }, this.inputAttr);

      var value = props.value,
          className = props.class,
          attrs = _objectWithoutProperties$1(props, ["value", "class"]);

      var events = {
        keydown: this.handleInputKeydown,
        focus: this.handleInputFocus,
        blur: this.handleInputBlur,
        input: this.handleInputInput,
        change: this.handleInputChange
      };
      var input = this.renderSlot('input', h("input", {
        "domProps": {
          "value": value
        },
        "class": className,
        "attrs": _objectSpread2({}, attrs),
        "on": _objectSpread2({}, events),
        "ref": "input"
      }), {
        props: props,
        events: events
      });
      var calendarIcon = this.type === 'time' ? h(__vue_component__$2$1) : h(__vue_component__$1$1);
      return h("div", {
        "class": "".concat(prefixClass, "-input-wrapper"),
        "on": {
          "mousedown": this.openPopup
        }
      }, [input, this.showClearIcon ? h("i", {
        "class": "".concat(prefixClass, "-icon-clear"),
        "on": {
          "mousedown": this.handleClear
        }
      }, [this.renderSlot('icon-clear', h(__vue_component__$3$1))]) : null, h("i", {
        "class": "".concat(prefixClass, "-icon-calendar")
      }, [this.renderSlot('icon-calendar', calendarIcon)])]);
    },
    renderContent: function renderContent() {
      var h = this.$createElement;
      var map = this.range ? componentRangeMap : componentMap;
      var Component = map[this.type] || map.default;

      var props = _objectSpread2({}, pick$2(this.$props, Object.keys(Component.props)), {
        value: this.currentValue
      });

      var on = _objectSpread2({}, pick$2(this.$listeners, Component.emits || []), {
        select: this.handleSelectDate
      });

      var content = h(Component, helper([{}, {
        props: props,
        on: on,
        ref: 'picker'
      }]));
      return h("div", {
        "class": "".concat(this.prefixClass, "-datepicker-body")
      }, [this.renderSlot('content', content, {
        value: this.currentValue,
        emit: this.handleSelectDate
      })]);
    },
    renderSidebar: function renderSidebar() {
      var _this4 = this;

      var h = this.$createElement;
      var prefixClass = this.prefixClass;
      return h("div", {
        "class": "".concat(prefixClass, "-datepicker-sidebar")
      }, [this.renderSlot('sidebar', null, {
        value: this.currentValue,
        emit: this.handleSelectDate
      }), this.shortcuts.map(function (v, i) {
        return h("button", {
          "key": i,
          "attrs": {
            "data-index": i,
            "type": "button"
          },
          "class": "".concat(prefixClass, "-btn ").concat(prefixClass, "-btn-text ").concat(prefixClass, "-btn-shortcut"),
          "on": {
            "click": _this4.handleSelectShortcut
          }
        }, [v.text]);
      })]);
    },
    renderHeader: function renderHeader() {
      var h = this.$createElement;
      return h("div", {
        "class": "".concat(this.prefixClass, "-datepicker-header")
      }, [this.renderSlot('header', null, {
        value: this.currentValue,
        emit: this.handleSelectDate
      })]);
    },
    renderFooter: function renderFooter() {
      var h = this.$createElement;
      var prefixClass = this.prefixClass;
      return h("div", {
        "class": "".concat(prefixClass, "-datepicker-footer")
      }, [this.renderSlot('footer', null, {
        value: this.currentValue,
        emit: this.handleSelectDate
      }), this.confirm ? h("button", {
        "attrs": {
          "type": "button"
        },
        "class": "".concat(prefixClass, "-btn ").concat(prefixClass, "-datepicker-btn-confirm"),
        "on": {
          "click": this.handleConfirmDate
        }
      }, [this.confirmText]) : null]);
    }
  },
  render: function render() {
    var _class;

    var h = arguments[0];
    var prefixClass = this.prefixClass,
        inline = this.inline,
        disabled = this.disabled;
    var sidedar = this.hasSlot('sidebar') || this.shortcuts.length ? this.renderSidebar() : null;
    var content = h("div", {
      "class": "".concat(prefixClass, "-datepicker-content")
    }, [this.hasSlot('header') ? this.renderHeader() : null, this.renderContent(), this.hasSlot('footer') || this.confirm ? this.renderFooter() : null]);
    return h("div", {
      "class": (_class = {}, _defineProperty$$(_class, "".concat(prefixClass, "-datepicker"), true), _defineProperty$$(_class, "".concat(prefixClass, "-datepicker-range"), this.range), _defineProperty$$(_class, "".concat(prefixClass, "-datepicker-inline"), inline), _defineProperty$$(_class, "disabled", disabled), _class)
    }, [!inline ? this.renderInput() : null, !inline ? h(__vue_component__$A, {
      "ref": "popup",
      "class": this.popupClass,
      "style": this.popupStyle,
      "attrs": {
        "visible": this.popupVisible,
        "appendToBody": this.appendToBody
      },
      "on": {
        "clickoutside": this.handleClickOutSide
      }
    }, [sidedar, content]) : h("div", {
      "class": "".concat(prefixClass, "-datepicker-main")
    }, [sidedar, content])]);
  }
};

DatePicker.locale = locale$1;

DatePicker.install = function install(Vue) {
  Vue.component(DatePicker.name, DatePicker);
};

if (typeof window !== 'undefined' && window.Vue) {
  DatePicker.install(window.Vue);
}

_extends$2(DatePicker, {
  CalendarPanel: CalendarPanel,
  CalendarRange: CalendarRange,
  TimePanel: __vue_component__$b$1,
  TimeRange: TimeRange,
  DatetimePanel: DatetimePanel,
  DatetimeRange: DatetimeRange
});

//

var script$B = {
  name: 'MeDatePicker',
  components: {
    DatePicker,
    Icon: __vue_component__
  },
  props: {
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledDate: {
      type: Function,
      default: () => false
    },
    disabledTime: {
      type: Function,
      default: () => false
    },
    editable: {
      type: Boolean,
      default: true
    },
    hourStep: {
      type: Number,
      default: 1
    },
    minuteStep: {
      type: Number,
      default: 5
    },
    placeholder: {
      type: String,
      default: ''
    },
    range: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      validator: prop => typeof prop === typeof true || prop === null,
      default: null
    },
    timePickerOptions: {
      type: Object,
      default: null
    },
    type: {
      type: String,
      default: 'date'
    },
    value: {
      type: [String, Date, Array],
      default: null
    },
    popupClass: {
      type: String,
      default: ''
    },
    popupStyle: {
      type: Object,
      default: () => null
    }
  },

  data() {
    return {
      current: this.value,
      format: {
        date: this.$toolkit.t('datePicker.format.date'),
        month: this.$toolkit.t('datePicker.format.month'),
        year: this.$toolkit.t('datePicker.format.year'),
        datetime: this.$toolkit.t('datePicker.format.datetime'),
        time: this.$toolkit.t('datePicker.format.time')
      },
      lang: {
        formatLocale: {
          weekdaysMin: this.$toolkit.t('datePicker.days').split('_'),
          monthsShort: this.$toolkit.t('datePicker.months').split('_'),
          placeholder: {
            date: this.$toolkit.t('datePicker.placeholder.date')
          }
        }
      }
    }
  },

  computed: {
    clazz() {
      return `me-date-picker ${this.size || ''} ${this.stateClazz}`
    },

    stateClazz() {
      if (this.state === null) {
        return ''
      }
      return this.state ? 'is-valid' : 'is-invalid'
    },

    getIcon() {
      return this.type.toLowerCase() === 'time' ? 'icon-clock-three' : 'icon-calendar'
    },

    iconColor() {
      return this.disabled ? 'var(--me-gray-4)' : 'var(--me-gray-3)'
    }
  },

  watch: {
    value(value) {
      this.current = value;
    }
  },

  methods: {
    clear() {
      this.$refs.meDatePickerRef.clear();
    },

    setFocus() {
      this.$refs.meDatePickerRef.focus();
    }
  }

};

/* script */
const __vue_script__$B = script$B;

/* template */
var __vue_render__$B = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { class: _vm.clazz },
    [
      _c(
        "date-picker",
        {
          ref: "meDatePickerRef",
          attrs: {
            clearable: _vm.clearable,
            disabled: _vm.disabled,
            "disabled-date": _vm.disabledDate,
            "disabled-time": _vm.disabledTime,
            editable: _vm.editable,
            format: _vm.format[_vm.type],
            "hour-step": _vm.hourStep,
            lang: _vm.lang,
            "minute-step": _vm.minuteStep,
            placeholder: _vm.placeholder,
            "popup-class": _vm.popupClass,
            "popup-style": _vm.popupStyle,
            range: _vm.range,
            "time-picker-options": _vm.timePickerOptions,
            type: _vm.type
          },
          on: {
            input: function($event) {
              return _vm.$emit("input", _vm.current)
            },
            focus: function($event) {
              return _vm.$emit("focus")
            },
            blur: function($event) {
              return _vm.$emit("blur")
            },
            close: function($event) {
              return _vm.$emit("close")
            },
            open: function($event) {
              return _vm.$emit("open")
            },
            enter: function($event) {
              return _vm.$emit("enter", _vm.current)
            }
          },
          model: {
            value: _vm.current,
            callback: function($$v) {
              _vm.current = $$v;
            },
            expression: "current"
          }
        },
        [
          _c("template", { slot: "icon-calendar" }, [
            _c(
              "div",
              {
                staticClass: "input-icons-wrapper",
                on: { click: _vm.setFocus }
              },
              [
                _c(
                  "div",
                  { staticClass: "input-icons" },
                  [
                    _vm.state
                      ? _c("Icon", {
                          staticClass: "state-icon state-valid",
                          attrs: {
                            icon: "me-icon-s icon-check-circle",
                            size: "13px"
                          }
                        })
                      : _vm._e(),
                    _vm._v(" "),
                    _vm.state === false
                      ? _c("Icon", {
                          staticClass: "state-icon state-invalid",
                          attrs: {
                            icon: "me-icon-s icon-circle-exclamation",
                            size: "13px"
                          }
                        })
                      : _vm._e()
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "input-icons" },
                  [
                    _c("Icon", {
                      attrs: {
                        icon: "me-icon-l " + _vm.getIcon,
                        color: _vm.iconColor,
                        size: "13px"
                      }
                    })
                  ],
                  1
                )
              ]
            )
          ]),
          _vm._v(" "),
          _c("template", { slot: "icon-clear" }, [
            _c(
              "div",
              { staticClass: "clear-icon" },
              [
                _c("Icon", {
                  attrs: {
                    icon: "me-icon-l icon-times",
                    size: "13px",
                    color: "var(--me-gray-3)"
                  }
                })
              ],
              1
            )
          ])
        ],
        2
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$B = [];
__vue_render__$B._withStripped = true;

  /* style */
  const __vue_inject_styles__$B = function (inject) {
    if (!inject) return
    inject("data-v-5227e9ee_0", { source: ".me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  color: var(--me-gray-0);\n  border-radius: 8px;\n  padding-left: 16px;\n  box-shadow: none;\n  z-index: 1;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input:not(.is-invalid) {\n  border-color: var(--me-gray-6);\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input:focus {\n  outline: none;\n  box-shadow: none;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input:focus:not(.is-invalid) {\n  border-color: var(--me-primary-2);\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee] {\n  width: 100%;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper .clear-icon {\n  width: 32px;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper .input-icons-wrapper {\n  height: 100%;\n  display: flex;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper .input-icons-wrapper .input-icons {\n  width: 32px;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  padding-left: 8px;\n  padding-right: 40px;\n}\n.me-date-picker > .mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > i {\n  right: 0;\n}\n.me-date-picker > .mx-datepicker.disabled[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  background: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border: 1px solid var(--me-gray-4);\n}\n.me-date-picker > .mx-datepicker.disabled[data-v-5227e9ee]  div.mx-input-wrapper i {\n  color: var(--me-gray-4);\n}\n.me-date-picker.sm > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  font-size: 12px;\n  height: 32px;\n}\n.me-date-picker.md > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  font-size: 14px;\n  height: 40px;\n}\n.me-date-picker.lg > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  font-size: 16px;\n  height: 48px;\n}\n.me-date-picker.is-invalid > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  padding-right: 72px;\n  border-color: var(--me-danger-2);\n}\n.me-date-picker.is-invalid > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input:focus {\n  border-color: var(--me-danger-2);\n}\n.me-date-picker.is-invalid > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper i.state-icon {\n  color: var(--me-danger-2);\n}\n.me-date-picker.is-invalid + .invalid-feedback[data-v-5227e9ee] {\n  display: block;\n}\n.me-date-picker.is-valid > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper > input {\n  padding-right: 72px;\n}\n.me-date-picker.is-valid > div.mx-datepicker[data-v-5227e9ee]  div.mx-input-wrapper i.state-icon {\n  color: var(--me-success-2);\n}\n\n/*# sourceMappingURL=DatePicker.vue.map */", map: {"version":3,"sources":["DatePicker.vue","/app/build/src/components/DatePicker.vue"],"names":[],"mappings":"AAAA;EACE,uBAAuB;EACvB,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,UAAU;AACZ;AACA;EACE,8BAA8B;AAChC;AACA;EACE,aAAa;EACb,gBAAgB;AAClB;AACA;EACE,iCAAiC;AACnC;ACmMA;EACA,WAAA;ADhMA;ACmMA;EACA,WAAA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;ADjMA;ACoMA;EACA,YAAA;EACA,aAAA;ADlMA;ACoMA;EACA,WAAA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;ADlMA;ACsMA;EACA,iBAAA;EACA,mBAAA;ADpMA;ACwMA;EACA,QAAA;ADtMA;AC4MA;EACA,4BAAA;EACA,uBAAA;EACA,kCAAA;AD1MA;AC6MA;EACA,uBAAA;AD3MA;ACkNA;EACA,eAAA;EACA,YAAA;ADhNA;ACqNA;EACA,eAAA;EACA,YAAA;ADnNA;ACwNA;EACA,eAAA;EACA,YAAA;ADtNA;AC4NA;EACA,mBAAA;EACA,gCAAA;AD1NA;AC4NA;EACA,gCAAA;AD1NA;AC8NA;EACA,yBAAA;AD5NA;ACgOA;EACA,cAAA;AD9NA;ACoOA;EACA,mBAAA;ADlOA;ACqOA;EACA,0BAAA;ADnOA;;AAEA,yCAAyC","file":"DatePicker.vue","sourcesContent":[".me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  color: var(--me-gray-0);\n  border-radius: 8px;\n  padding-left: 16px;\n  box-shadow: none;\n  z-index: 1;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper > input:not(.is-invalid) {\n  border-color: var(--me-gray-6);\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper > input:focus {\n  outline: none;\n  box-shadow: none;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper > input:focus:not(.is-invalid) {\n  border-color: var(--me-primary-2);\n}\n\n.me-date-picker > .mx-datepicker {\n  width: 100%;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper .clear-icon {\n  width: 32px;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper .input-icons-wrapper {\n  height: 100%;\n  display: flex;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper .input-icons-wrapper .input-icons {\n  width: 32px;\n  height: 100%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  padding-left: 8px;\n  padding-right: 40px;\n}\n.me-date-picker > .mx-datepicker ::v-deep div.mx-input-wrapper > i {\n  right: 0;\n}\n.me-date-picker > .mx-datepicker.disabled ::v-deep div.mx-input-wrapper > input {\n  background: var(--me-gray-6);\n  color: var(--me-gray-4);\n  border: 1px solid var(--me-gray-4);\n}\n.me-date-picker > .mx-datepicker.disabled ::v-deep div.mx-input-wrapper i {\n  color: var(--me-gray-4);\n}\n.me-date-picker.sm > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  font-size: 12px;\n  height: 32px;\n}\n.me-date-picker.md > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  font-size: 14px;\n  height: 40px;\n}\n.me-date-picker.lg > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  font-size: 16px;\n  height: 48px;\n}\n.me-date-picker.is-invalid > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  padding-right: 72px;\n  border-color: var(--me-danger-2);\n}\n.me-date-picker.is-invalid > div.mx-datepicker ::v-deep div.mx-input-wrapper > input:focus {\n  border-color: var(--me-danger-2);\n}\n.me-date-picker.is-invalid > div.mx-datepicker ::v-deep div.mx-input-wrapper i.state-icon {\n  color: var(--me-danger-2);\n}\n.me-date-picker.is-invalid + .invalid-feedback {\n  display: block;\n}\n.me-date-picker.is-valid > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n  padding-right: 72px;\n}\n.me-date-picker.is-valid > div.mx-datepicker ::v-deep div.mx-input-wrapper i.state-icon {\n  color: var(--me-success-2);\n}\n\n/*# sourceMappingURL=DatePicker.vue.map */","<template>\n  <div :class=\"clazz\">\n    <date-picker\n      ref=\"meDatePickerRef\"\n      v-model=\"current\"\n      :clearable=\"clearable\"\n      :disabled=\"disabled\"\n      :disabled-date=\"disabledDate\"\n      :disabled-time=\"disabledTime\"\n      :editable=\"editable\"\n      :format=\"format[type]\"\n      :hour-step=\"hourStep\"\n      :lang=\"lang\"\n      :minute-step=\"minuteStep\"\n      :placeholder=\"placeholder\"\n      :popup-class=\"popupClass\"\n      :popup-style=\"popupStyle\"\n      :range=\"range\"\n      :time-picker-options=\"timePickerOptions\"\n      :type=\"type\"\n      @input=\"$emit('input', current)\"\n      @focus=\"$emit('focus')\"\n      @blur=\"$emit('blur')\"\n      @close=\"$emit('close')\"\n      @open=\"$emit('open')\"\n      @enter=\"$emit('enter', current)\"\n    >\n      <template slot=\"icon-calendar\">\n        <div @click=\"setFocus\" class=\"input-icons-wrapper\">\n          <div class=\"input-icons\">\n            <Icon\n              v-if=\"state\"\n              class=\"state-icon state-valid\"\n              icon=\"me-icon-s icon-check-circle\"\n              size=\"13px\"\n            />\n\n            <Icon\n              v-if=\"state === false\"\n              class=\"state-icon state-invalid\"\n              icon=\"me-icon-s icon-circle-exclamation\"\n              size=\"13px\"\n            />\n          </div>\n\n          <div class=\"input-icons\">\n            <Icon\n              :icon=\"`me-icon-l ${getIcon}`\"\n              :color=\"iconColor\"\n              size=\"13px\"\n            />\n          </div>\n        </div>\n      </template>\n\n      <template slot=\"icon-clear\">\n        <div class=\"clear-icon\">\n          <Icon\n            icon=\"me-icon-l icon-times\"\n            size=\"13px\"\n            color=\"var(--me-gray-3)\"\n          />\n        </div>\n      </template>\n    </date-picker>\n  </div>\n</template>\n\n<script>\nimport DatePicker from 'vue2-datepicker'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeDatePicker',\n  components: {\n    DatePicker,\n    Icon\n  },\n  props: {\n    clearable: {\n      type: Boolean,\n      default: true\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    disabledDate: {\n      type: Function,\n      default: () => false\n    },\n    disabledTime: {\n      type: Function,\n      default: () => false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    hourStep: {\n      type: Number,\n      default: 1\n    },\n    minuteStep: {\n      type: Number,\n      default: 5\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    range: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      validator: prop => typeof prop === typeof true || prop === null,\n      default: null\n    },\n    timePickerOptions: {\n      type: Object,\n      default: null\n    },\n    type: {\n      type: String,\n      default: 'date'\n    },\n    value: {\n      type: [String, Date, Array],\n      default: null\n    },\n    popupClass: {\n      type: String,\n      default: ''\n    },\n    popupStyle: {\n      type: Object,\n      default: () => null\n    }\n  },\n\n  data() {\n    return {\n      current: this.value,\n      format: {\n        date: this.$toolkit.t('datePicker.format.date'),\n        month: this.$toolkit.t('datePicker.format.month'),\n        year: this.$toolkit.t('datePicker.format.year'),\n        datetime: this.$toolkit.t('datePicker.format.datetime'),\n        time: this.$toolkit.t('datePicker.format.time')\n      },\n      lang: {\n        formatLocale: {\n          weekdaysMin: this.$toolkit.t('datePicker.days').split('_'),\n          monthsShort: this.$toolkit.t('datePicker.months').split('_'),\n          placeholder: {\n            date: this.$toolkit.t('datePicker.placeholder.date')\n          }\n        }\n      }\n    }\n  },\n\n  computed: {\n    clazz() {\n      return `me-date-picker ${this.size || ''} ${this.stateClazz}`\n    },\n\n    stateClazz() {\n      if (this.state === null) {\n        return ''\n      }\n      return this.state ? 'is-valid' : 'is-invalid'\n    },\n\n    getIcon() {\n      return this.type.toLowerCase() === 'time' ? 'icon-clock-three' : 'icon-calendar'\n    },\n\n    iconColor() {\n      return this.disabled ? 'var(--me-gray-4)' : 'var(--me-gray-3)'\n    }\n  },\n\n  watch: {\n    value(value) {\n      this.current = value\n    }\n  },\n\n  methods: {\n    clear() {\n      this.$refs.meDatePickerRef.clear()\n    },\n\n    setFocus() {\n      this.$refs.meDatePickerRef.focus()\n    }\n  }\n\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/input.scss\";\n\n  .me-date-picker {\n    > .mx-datepicker {\n      width: 100%;\n\n      ::v-deep div.mx-input-wrapper {\n        .clear-icon {\n          width: 32px;\n          height: 100%;\n          display: flex;\n          align-items: center;\n          justify-content: center;\n        }\n\n        .input-icons-wrapper {\n          height: 100%;\n          display: flex;\n\n          .input-icons {\n            width: 32px;\n            height: 100%;\n            display: flex;\n            align-items: center;\n            justify-content: center;\n          }\n        }\n\n        > input {\n          padding-left: 8px;\n          padding-right: 40px;\n          @extend %input-style;\n        }\n\n        > i {\n          right: 0;\n        }\n      }\n\n      &.disabled {\n        ::v-deep div.mx-input-wrapper {\n          > input {\n            background: var(--me-gray-6);\n            color: var(--me-gray-4);\n            border: 1px solid var(--me-gray-4);\n          }\n\n          i {\n            color: var(--me-gray-4);\n          }\n        }\n      }\n    }\n\n    &.sm {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n        font-size: 12px;\n        height: 32px;\n      }\n    }\n\n    &.md {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n        font-size: 14px;\n        height: 40px;\n      }\n    }\n\n    &.lg {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n        font-size: 16px;\n        height: 48px;\n      }\n    }\n\n    &.is-invalid {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper {\n        > input {\n          padding-right: 72px;\n          border-color: var(--me-danger-2);\n\n          &:focus {\n            border-color: var(--me-danger-2);\n          }\n        }\n\n        i.state-icon {\n          color: var(--me-danger-2);\n        }\n      }\n\n      + .invalid-feedback {\n        display: block;\n      }\n    }\n\n    &.is-valid {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper {\n        > input {\n          padding-right: 72px;\n        }\n\n        i.state-icon {\n          color: var(--me-success-2);\n        }\n      }\n    }\n  }\n</style>\n\n<style lang=\"scss\">\n  .mx-datepicker-popup {\n    z-index: 1061;\n  }\n</style>\n"]}, media: undefined })
,inject("data-v-5227e9ee_1", { source: ".mx-datepicker-popup {\n  z-index: 1061;\n}\n\n/*# sourceMappingURL=DatePicker.vue.map */", map: {"version":3,"sources":["/app/build/src/components/DatePicker.vue","DatePicker.vue"],"names":[],"mappings":"AA+TA;EACA,aAAA;AC9TA;;AAEA,yCAAyC","file":"DatePicker.vue","sourcesContent":["<template>\n  <div :class=\"clazz\">\n    <date-picker\n      ref=\"meDatePickerRef\"\n      v-model=\"current\"\n      :clearable=\"clearable\"\n      :disabled=\"disabled\"\n      :disabled-date=\"disabledDate\"\n      :disabled-time=\"disabledTime\"\n      :editable=\"editable\"\n      :format=\"format[type]\"\n      :hour-step=\"hourStep\"\n      :lang=\"lang\"\n      :minute-step=\"minuteStep\"\n      :placeholder=\"placeholder\"\n      :popup-class=\"popupClass\"\n      :popup-style=\"popupStyle\"\n      :range=\"range\"\n      :time-picker-options=\"timePickerOptions\"\n      :type=\"type\"\n      @input=\"$emit('input', current)\"\n      @focus=\"$emit('focus')\"\n      @blur=\"$emit('blur')\"\n      @close=\"$emit('close')\"\n      @open=\"$emit('open')\"\n      @enter=\"$emit('enter', current)\"\n    >\n      <template slot=\"icon-calendar\">\n        <div @click=\"setFocus\" class=\"input-icons-wrapper\">\n          <div class=\"input-icons\">\n            <Icon\n              v-if=\"state\"\n              class=\"state-icon state-valid\"\n              icon=\"me-icon-s icon-check-circle\"\n              size=\"13px\"\n            />\n\n            <Icon\n              v-if=\"state === false\"\n              class=\"state-icon state-invalid\"\n              icon=\"me-icon-s icon-circle-exclamation\"\n              size=\"13px\"\n            />\n          </div>\n\n          <div class=\"input-icons\">\n            <Icon\n              :icon=\"`me-icon-l ${getIcon}`\"\n              :color=\"iconColor\"\n              size=\"13px\"\n            />\n          </div>\n        </div>\n      </template>\n\n      <template slot=\"icon-clear\">\n        <div class=\"clear-icon\">\n          <Icon\n            icon=\"me-icon-l icon-times\"\n            size=\"13px\"\n            color=\"var(--me-gray-3)\"\n          />\n        </div>\n      </template>\n    </date-picker>\n  </div>\n</template>\n\n<script>\nimport DatePicker from 'vue2-datepicker'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeDatePicker',\n  components: {\n    DatePicker,\n    Icon\n  },\n  props: {\n    clearable: {\n      type: Boolean,\n      default: true\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    disabledDate: {\n      type: Function,\n      default: () => false\n    },\n    disabledTime: {\n      type: Function,\n      default: () => false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    hourStep: {\n      type: Number,\n      default: 1\n    },\n    minuteStep: {\n      type: Number,\n      default: 5\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    range: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      validator: prop => typeof prop === typeof true || prop === null,\n      default: null\n    },\n    timePickerOptions: {\n      type: Object,\n      default: null\n    },\n    type: {\n      type: String,\n      default: 'date'\n    },\n    value: {\n      type: [String, Date, Array],\n      default: null\n    },\n    popupClass: {\n      type: String,\n      default: ''\n    },\n    popupStyle: {\n      type: Object,\n      default: () => null\n    }\n  },\n\n  data() {\n    return {\n      current: this.value,\n      format: {\n        date: this.$toolkit.t('datePicker.format.date'),\n        month: this.$toolkit.t('datePicker.format.month'),\n        year: this.$toolkit.t('datePicker.format.year'),\n        datetime: this.$toolkit.t('datePicker.format.datetime'),\n        time: this.$toolkit.t('datePicker.format.time')\n      },\n      lang: {\n        formatLocale: {\n          weekdaysMin: this.$toolkit.t('datePicker.days').split('_'),\n          monthsShort: this.$toolkit.t('datePicker.months').split('_'),\n          placeholder: {\n            date: this.$toolkit.t('datePicker.placeholder.date')\n          }\n        }\n      }\n    }\n  },\n\n  computed: {\n    clazz() {\n      return `me-date-picker ${this.size || ''} ${this.stateClazz}`\n    },\n\n    stateClazz() {\n      if (this.state === null) {\n        return ''\n      }\n      return this.state ? 'is-valid' : 'is-invalid'\n    },\n\n    getIcon() {\n      return this.type.toLowerCase() === 'time' ? 'icon-clock-three' : 'icon-calendar'\n    },\n\n    iconColor() {\n      return this.disabled ? 'var(--me-gray-4)' : 'var(--me-gray-3)'\n    }\n  },\n\n  watch: {\n    value(value) {\n      this.current = value\n    }\n  },\n\n  methods: {\n    clear() {\n      this.$refs.meDatePickerRef.clear()\n    },\n\n    setFocus() {\n      this.$refs.meDatePickerRef.focus()\n    }\n  }\n\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/input.scss\";\n\n  .me-date-picker {\n    > .mx-datepicker {\n      width: 100%;\n\n      ::v-deep div.mx-input-wrapper {\n        .clear-icon {\n          width: 32px;\n          height: 100%;\n          display: flex;\n          align-items: center;\n          justify-content: center;\n        }\n\n        .input-icons-wrapper {\n          height: 100%;\n          display: flex;\n\n          .input-icons {\n            width: 32px;\n            height: 100%;\n            display: flex;\n            align-items: center;\n            justify-content: center;\n          }\n        }\n\n        > input {\n          padding-left: 8px;\n          padding-right: 40px;\n          @extend %input-style;\n        }\n\n        > i {\n          right: 0;\n        }\n      }\n\n      &.disabled {\n        ::v-deep div.mx-input-wrapper {\n          > input {\n            background: var(--me-gray-6);\n            color: var(--me-gray-4);\n            border: 1px solid var(--me-gray-4);\n          }\n\n          i {\n            color: var(--me-gray-4);\n          }\n        }\n      }\n    }\n\n    &.sm {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n        font-size: 12px;\n        height: 32px;\n      }\n    }\n\n    &.md {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n        font-size: 14px;\n        height: 40px;\n      }\n    }\n\n    &.lg {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper > input {\n        font-size: 16px;\n        height: 48px;\n      }\n    }\n\n    &.is-invalid {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper {\n        > input {\n          padding-right: 72px;\n          border-color: var(--me-danger-2);\n\n          &:focus {\n            border-color: var(--me-danger-2);\n          }\n        }\n\n        i.state-icon {\n          color: var(--me-danger-2);\n        }\n      }\n\n      + .invalid-feedback {\n        display: block;\n      }\n    }\n\n    &.is-valid {\n      > div.mx-datepicker ::v-deep div.mx-input-wrapper {\n        > input {\n          padding-right: 72px;\n        }\n\n        i.state-icon {\n          color: var(--me-success-2);\n        }\n      }\n    }\n  }\n</style>\n\n<style lang=\"scss\">\n  .mx-datepicker-popup {\n    z-index: 1061;\n  }\n</style>\n",".mx-datepicker-popup {\n  z-index: 1061;\n}\n\n/*# sourceMappingURL=DatePicker.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$B = "data-v-5227e9ee";
  /* module identifier */
  const __vue_module_identifier__$B = undefined;
  /* functional template */
  const __vue_is_functional_template__$B = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$B, staticRenderFns: __vue_staticRenderFns__$B },
    __vue_inject_styles__$B,
    __vue_script__$B,
    __vue_scope_id__$B,
    __vue_is_functional_template__$B,
    __vue_module_identifier__$B,
    false,
    createInjector,
    undefined,
    undefined
  );

var accounting_umd = createCommonjsModule(function (module, exports) {
(function (global, factory) {
	 factory(exports) ;
}(commonjsGlobal, function (exports) {
	function __commonjs(fn, module) { return module = { exports: {} }, fn(module, module.exports), module.exports; }

	/**
	 * The library's settings configuration object.
	 *
	 * Contains default parameters for currency and number formatting
	 */
	var settings = {
	  symbol: '$', // default currency symbol is '$'
	  format: '%s%v', // controls output: %s = symbol, %v = value (can be object, see docs)
	  decimal: '.', // decimal point separator
	  thousand: ',', // thousands separator
	  precision: 2, // decimal places
	  grouping: 3, // digit grouping (not implemented yet)
	  stripZeros: false, // strip insignificant zeros from decimal part
	  fallback: 0 // value returned on unformat() failure
	};

	/**
	 * Takes a string/array of strings, removes all formatting/cruft and returns the raw float value
	 * Alias: `accounting.parse(string)`
	 *
	 * Decimal must be included in the regular expression to match floats (defaults to
	 * accounting.settings.decimal), so if the number uses a non-standard decimal
	 * separator, provide it as the second argument.
	 *
	 * Also matches bracketed negatives (eg. '$ (1.99)' => -1.99)
	 *
	 * Doesn't throw any errors (`NaN`s become 0) but this may change in future
	 *
	 * ```js
	 *  accounting.unformat("£ 12,345,678.90 GBP"); // 12345678.9
	 * ```
	 *
	 * @method unformat
	 * @for accounting
	 * @param {String|Array<String>} value The string or array of strings containing the number/s to parse.
	 * @param {Number}               decimal Number of decimal digits of the resultant number
	 * @return {Float} The parsed number
	 */
	function unformat(value) {
	  var decimal = arguments.length <= 1 || arguments[1] === undefined ? settings.decimal : arguments[1];
	  var fallback = arguments.length <= 2 || arguments[2] === undefined ? settings.fallback : arguments[2];

	  // Recursively unformat arrays:
	  if (Array.isArray(value)) {
	    return value.map(function (val) {
	      return unformat(val, decimal, fallback);
	    });
	  }

	  // Return the value as-is if it's already a number:
	  if (typeof value === 'number') return value;

	  // Build regex to strip out everything except digits, decimal point and minus sign:
	  var regex = new RegExp('[^0-9-(-)-' + decimal + ']', ['g']);
	  var unformattedValueString = ('' + value).replace(regex, '') // strip out any cruft
	  .replace(decimal, '.') // make sure decimal point is standard
	  .replace(/\(([-]*\d*[^)]?\d+)\)/g, '-$1') // replace bracketed values with negatives
	  .replace(/\((.*)\)/, ''); // remove any brackets that do not have numeric value

	  /**
	   * Handling -ve number and bracket, eg.
	   * (-100) = 100, -(100) = 100, --100 = 100
	   */
	  var negative = (unformattedValueString.match(/-/g) || 2).length % 2,
	      absUnformatted = parseFloat(unformattedValueString.replace(/-/g, '')),
	      unformatted = absUnformatted * (negative ? -1 : 1);

	  // This will fail silently which may cause trouble, let's wait and see:
	  return !isNaN(unformatted) ? unformatted : fallback;
	}

	/**
	 * Check and normalise the value of precision (must be positive integer)
	 */
	function _checkPrecision(val, base) {
	  val = Math.round(Math.abs(val));
	  return isNaN(val) ? base : val;
	}

	/**
	 * Implementation of toFixed() that treats floats more like decimals
	 *
	 * Fixes binary rounding issues (eg. (0.615).toFixed(2) === '0.61') that present
	 * problems for accounting- and finance-related software.
	 *
	 * ```js
	 *  (0.615).toFixed(2);           // "0.61" (native toFixed has rounding issues)
	 *  accounting.toFixed(0.615, 2); // "0.62"
	 * ```
	 *
	 * @method toFixed
	 * @for accounting
	 * @param {Float}   value         The float to be treated as a decimal number.
	 * @param {Number} [precision=2] The number of decimal digits to keep.
	 * @return {String} The given number transformed into a string with the given precission
	 */
	function toFixed(value, precision) {
	  precision = _checkPrecision(precision, settings.precision);
	  var power = Math.pow(10, precision);

	  // Multiply up by precision, round accurately, then divide and use native toFixed():
	  return (Math.round((value + 1e-8) * power) / power).toFixed(precision);
	}

	var index = __commonjs(function (module) {
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var propIsEnumerable = Object.prototype.propertyIsEnumerable;

	function toObject(val) {
		if (val === null || val === undefined) {
			throw new TypeError('Object.assign cannot be called with null or undefined');
		}

		return Object(val);
	}

	module.exports = Object.assign || function (target, source) {
		var from;
		var to = toObject(target);
		var symbols;

		for (var s = 1; s < arguments.length; s++) {
			from = Object(arguments[s]);

			for (var key in from) {
				if (hasOwnProperty.call(from, key)) {
					to[key] = from[key];
				}
			}

			if (Object.getOwnPropertySymbols) {
				symbols = Object.getOwnPropertySymbols(from);
				for (var i = 0; i < symbols.length; i++) {
					if (propIsEnumerable.call(from, symbols[i])) {
						to[symbols[i]] = from[symbols[i]];
					}
				}
			}
		}

		return to;
	};
	});

	var objectAssign = (index && typeof index === 'object' && 'default' in index ? index['default'] : index);

	function _stripInsignificantZeros(str, decimal) {
	  var parts = str.split(decimal);
	  var integerPart = parts[0];
	  var decimalPart = parts[1].replace(/0+$/, '');

	  if (decimalPart.length > 0) {
	    return integerPart + decimal + decimalPart;
	  }

	  return integerPart;
	}

	/**
	 * Format a number, with comma-separated thousands and custom precision/decimal places
	 * Alias: `accounting.format()`
	 *
	 * Localise by overriding the precision and thousand / decimal separators
	 *
	 * ```js
	 * accounting.formatNumber(5318008);              // 5,318,008
	 * accounting.formatNumber(9876543.21, { precision: 3, thousand: " " }); // 9 876 543.210
	 * ```
	 *
	 * @method formatNumber
	 * @for accounting
	 * @param {Number}        number The number to be formatted.
	 * @param {Object}        [opts={}] Object containing all the options of the method.
	 * @return {String} The given number properly formatted.
	  */
	function formatNumber(number) {
	  var opts = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	  // Resursively format arrays:
	  if (Array.isArray(number)) {
	    return number.map(function (val) {
	      return formatNumber(val, opts);
	    });
	  }

	  // Build options object from second param (if object) or all params, extending defaults:
	  opts = objectAssign({}, settings, opts);

	  // Do some calc:
	  var negative = number < 0 ? '-' : '';
	  var base = parseInt(toFixed(Math.abs(number), opts.precision), 10) + '';
	  var mod = base.length > 3 ? base.length % 3 : 0;

	  // Format the number:
	  var formatted = negative + (mod ? base.substr(0, mod) + opts.thousand : '') + base.substr(mod).replace(/(\d{3})(?=\d)/g, '$1' + opts.thousand) + (opts.precision > 0 ? opts.decimal + toFixed(Math.abs(number), opts.precision).split('.')[1] : '');

	  return opts.stripZeros ? _stripInsignificantZeros(formatted, opts.decimal) : formatted;
	}

	var index$1 = __commonjs(function (module) {

	var strValue = String.prototype.valueOf;
	var tryStringObject = function tryStringObject(value) {
		try {
			strValue.call(value);
			return true;
		} catch (e) {
			return false;
		}
	};
	var toStr = Object.prototype.toString;
	var strClass = '[object String]';
	var hasToStringTag = typeof Symbol === 'function' && typeof Symbol.toStringTag === 'symbol';

	module.exports = function isString(value) {
		if (typeof value === 'string') { return true; }
		if (typeof value !== 'object') { return false; }
		return hasToStringTag ? tryStringObject(value) : toStr.call(value) === strClass;
	};
	});

	var isString = (index$1 && typeof index$1 === 'object' && 'default' in index$1 ? index$1['default'] : index$1);

	/**
	 * Parses a format string or object and returns format obj for use in rendering
	 *
	 * `format` is either a string with the default (positive) format, or object
	 * containing `pos` (required), `neg` and `zero` values
	 *
	 * Either string or format.pos must contain "%v" (value) to be valid
	 *
	 * @method _checkCurrencyFormat
	 * @for accounting
	 * @param {String}        [format="%s%v"] String with the format to apply, where %s is the currency symbol and %v is the value.
	 * @return {Object} object represnting format (with pos, neg and zero attributes)
	 */
	function _checkCurrencyFormat(format) {
	  // Format should be a string, in which case `value` ('%v') must be present:
	  if (isString(format) && format.match('%v')) {
	    // Create and return positive, negative and zero formats:
	    return {
	      pos: format,
	      neg: format.replace('-', '').replace('%v', '-%v'),
	      zero: format
	    };
	  }

	  // Otherwise, assume format was fine:
	  return format;
	}

	/**
	 * Format a number into currency
	 *
	 * Usage: accounting.formatMoney(number, symbol, precision, thousandsSep, decimalSep, format)
	 * defaults: (0, '$', 2, ',', '.', '%s%v')
	 *
	 * Localise by overriding the symbol, precision, thousand / decimal separators and format
	 *
	 * ```js
	 * // Default usage:
	 * accounting.formatMoney(12345678); // $12,345,678.00
	 *
	 * // European formatting (custom symbol and separators), can also use options object as second parameter:
	 * accounting.formatMoney(4999.99, { symbol: "€", precision: 2, thousand: ".", decimal: "," }); // €4.999,99
	 *
	 * // Negative values can be formatted nicely:
	 * accounting.formatMoney(-500000, { symbol: "£ ", precision: 0 }); // £ -500,000
	 *
	 * // Simple `format` string allows control of symbol position (%v = value, %s = symbol):
	 * accounting.formatMoney(5318008, { symbol: "GBP",  format: "%v %s" }); // 5,318,008.00 GBP
	 * ```
	 *
	 * @method formatMoney
	 * @for accounting
	 * @param {Number}        number Number to be formatted.
	 * @param {Object}        [opts={}] Object containing all the options of the method.
	 * @return {String} The given number properly formatted as money.
	 */
	function formatMoney(number) {
	  var opts = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	  // Resursively format arrays:
	  if (Array.isArray(number)) {
	    return number.map(function (val) {
	      return formatMoney(val, opts);
	    });
	  }

	  // Build options object from second param (if object) or all params, extending defaults:
	  opts = objectAssign({}, settings, opts);

	  // Check format (returns object with pos, neg and zero):
	  var formats = _checkCurrencyFormat(opts.format);

	  // Choose which format to use for this value:
	  var useFormat = undefined;

	  if (number > 0) {
	    useFormat = formats.pos;
	  } else if (number < 0) {
	    useFormat = formats.neg;
	  } else {
	    useFormat = formats.zero;
	  }

	  // Return with currency symbol added:
	  return useFormat.replace('%s', opts.symbol).replace('%v', formatNumber(Math.abs(number), opts));
	}

	/**
	 * Format a list of numbers into an accounting column, padding with whitespace
	 * to line up currency symbols, thousand separators and decimals places
	 *
	 * List should be an array of numbers
	 *
	 * Returns array of accouting-formatted number strings of same length
	 *
	 * NB: `white-space:pre` CSS rule is required on the list container to prevent
	 * browsers from collapsing the whitespace in the output strings.
	 *
	 * ```js
	 * accounting.formatColumn([123.5, 3456.49, 777888.99, 12345678, -5432], { symbol: "$ " });
	 * ```
	 *
	 * @method formatColumn
	 * @for accounting
	 * @param {Array<Number>} list An array of numbers to format
	 * @param {Object}        [opts={}] Object containing all the options of the method.
	 * @param {Object|String} [symbol="$"] String with the currency symbol. For conveniency if can be an object containing all the options of the method.
	 * @param {Integer}       [precision=2] Number of decimal digits
	 * @param {String}        [thousand=','] String with the thousands separator.
	 * @param {String}        [decimal="."] String with the decimal separator.
	 * @param {String}        [format="%s%v"] String with the format to apply, where %s is the currency symbol and %v is the value.
	 * @return {Array<String>} array of accouting-formatted number strings of same length
	 */
	function formatColumn(list) {
	  var opts = arguments.length <= 1 || arguments[1] === undefined ? {} : arguments[1];

	  if (!list) return [];

	  // Build options object from second param (if object) or all params, extending defaults:
	  opts = objectAssign({}, settings, opts);

	  // Check format (returns object with pos, neg and zero), only need pos for now:
	  var formats = _checkCurrencyFormat(opts.format);

	  // Whether to pad at start of string or after currency symbol:
	  var padAfterSymbol = formats.pos.indexOf('%s') < formats.pos.indexOf('%v');

	  // Store value for the length of the longest string in the column:
	  var maxLength = 0;

	  // Format the list according to options, store the length of the longest string:
	  var formatted = list.map(function (val) {
	    if (Array.isArray(val)) {
	      // Recursively format columns if list is a multi-dimensional array:
	      return formatColumn(val, opts);
	    }
	    // Clean up the value
	    val = unformat(val, opts.decimal);

	    // Choose which format to use for this value (pos, neg or zero):
	    var useFormat = undefined;

	    if (val > 0) {
	      useFormat = formats.pos;
	    } else if (val < 0) {
	      useFormat = formats.neg;
	    } else {
	      useFormat = formats.zero;
	    }

	    // Format this value, push into formatted list and save the length:
	    var fVal = useFormat.replace('%s', opts.symbol).replace('%v', formatNumber(Math.abs(val), opts));

	    if (fVal.length > maxLength) {
	      maxLength = fVal.length;
	    }

	    return fVal;
	  });

	  // Pad each number in the list and send back the column of numbers:
	  return formatted.map(function (val) {
	    // Only if this is a string (not a nested array, which would have already been padded):
	    if (isString(val) && val.length < maxLength) {
	      // Depending on symbol position, pad after symbol or at index 0:
	      return padAfterSymbol ? val.replace(opts.symbol, opts.symbol + new Array(maxLength - val.length + 1).join(' ')) : new Array(maxLength - val.length + 1).join(' ') + val;
	    }
	    return val;
	  });
	}

	exports.settings = settings;
	exports.unformat = unformat;
	exports.toFixed = toFixed;
	exports.formatMoney = formatMoney;
	exports.formatNumber = formatNumber;
	exports.formatColumn = formatColumn;
	exports.format = formatMoney;
	exports.parse = unformat;

}));

});

var vueNumeric_min = createCommonjsModule(function (module, exports) {
!function(e,t){module.exports=t(accounting_umd);}("undefined"!=typeof self?self:commonjsGlobal,function(e){return function(e){function t(n){if(r[n])return r[n].exports;var i=r[n]={i:n,l:!1,exports:{}};return e[n].call(i.exports,i,i.exports,t),i.l=!0,i.exports}var r={};return t.m=e,t.c=r,t.d=function(e,r,n){t.o(e,r)||Object.defineProperty(e,r,{configurable:!1,enumerable:!0,get:n});},t.n=function(e){var r=e&&e.__esModule?function(){return e.default}:function(){return e};return t.d(r,"a",r),r},t.o=function(e,t){return Object.prototype.hasOwnProperty.call(e,t)},t.p="",t(t.s=1)}([function(e,t,r){var n=r(4),i=r.n(n);t.a={name:"VueNumeric",props:{currency:{type:String,default:"",required:!1},max:{type:Number,default:Number.MAX_SAFE_INTEGER||9007199254740991,required:!1},min:{type:Number,default:Number.MIN_SAFE_INTEGER||-9007199254740991,required:!1},minus:{type:Boolean,default:!1,required:!1},placeholder:{type:String,default:"",required:!1},emptyValue:{type:[Number,String],default:"",required:!1},precision:{type:Number,default:0,required:!1},separator:{type:String,default:",",required:!1},thousandSeparator:{default:void 0,required:!1,type:String},decimalSeparator:{default:void 0,required:!1,type:String},outputType:{required:!1,type:String,default:"Number"},value:{type:[Number,String],default:0,required:!0},readOnly:{type:Boolean,default:!1,required:!1},readOnlyClass:{type:String,default:"",required:!1},currencySymbolPosition:{type:String,default:"prefix",required:!1}},data:function(){return {amount:""}},computed:{amountNumber:function(){return this.unformat(this.amount)},valueNumber:function(){return this.unformat(this.value)},decimalSeparatorSymbol:function(){return void 0!==this.decimalSeparator?this.decimalSeparator:","===this.separator?".":","},thousandSeparatorSymbol:function(){return void 0!==this.thousandSeparator?this.thousandSeparator:"."===this.separator?".":"space"===this.separator?" ":","},symbolPosition:function(){return this.currency?"suffix"===this.currencySymbolPosition?"%v %s":"%s %v":"%v"}},watch:{valueNumber:function(e){this.$refs.numeric!==document.activeElement&&(this.amount=this.format(e));},readOnly:function(e,t){var r=this;!1===t&&!0===e&&this.$nextTick(function(){r.$refs.readOnly.className=r.readOnlyClass;});},separator:function(){this.process(this.valueNumber),this.amount=this.format(this.valueNumber);},currency:function(){this.process(this.valueNumber),this.amount=this.format(this.valueNumber);},precision:function(){this.process(this.valueNumber),this.amount=this.format(this.valueNumber);}},mounted:function(){var e=this;this.placeholder||(this.process(this.valueNumber),this.amount=this.format(this.valueNumber),setTimeout(function(){e.process(e.valueNumber),e.amount=e.format(e.valueNumber);},500)),this.readOnly&&(this.$refs.readOnly.className=this.readOnlyClass);},methods:{onBlurHandler:function(e){this.$emit("blur",e),this.amount=this.format(this.valueNumber);},onFocusHandler:function(e){this.$emit("focus",e),0===this.valueNumber?this.amount=null:this.amount=i.a.formatMoney(this.valueNumber,{symbol:"",format:"%v",thousand:"",decimal:this.decimalSeparatorSymbol,precision:Number(this.precision)});},onInputHandler:function(){this.process(this.amountNumber);},process:function(e){e>=this.max&&this.update(this.max),e<=this.min&&this.update(this.min),e>this.min&&e<this.max&&this.update(e),!this.minus&&e<0&&(this.min>=0?this.update(this.min):this.update(0));},update:function(e){var t=i.a.toFixed(e,this.precision),r="string"===this.outputType.toLowerCase()?t:Number(t);this.$emit("input",r);},format:function(e){return i.a.formatMoney(e,{symbol:this.currency,format:this.symbolPosition,precision:Number(this.precision),decimal:this.decimalSeparatorSymbol,thousand:this.thousandSeparatorSymbol})},unformat:function(e){var t="string"==typeof e&&""===e?this.emptyValue:e;return i.a.unformat(t,this.decimalSeparatorSymbol)}}};},function(e,t,r){Object.defineProperty(t,"__esModule",{value:!0});var n=r(2),i={install:function(e){e.component(n.a.name,n.a);}};n.a.install=i.install,t.default=n.a;},function(e,t,r){var n=r(0),i=r(5),u=r(3),o=u(n.a,i.a,!1,null,null,null);t.a=o.exports;},function(e,t){e.exports=function(e,t,r,n,i,u){var o,a=e=e||{},s=typeof e.default;"object"!==s&&"function"!==s||(o=e,a=e.default);var c="function"==typeof a?a.options:a;t&&(c.render=t.render,c.staticRenderFns=t.staticRenderFns,c._compiled=!0),r&&(c.functional=!0),i&&(c._scopeId=i);var l;if(u?(l=function(e){e=e||this.$vnode&&this.$vnode.ssrContext||this.parent&&this.parent.$vnode&&this.parent.$vnode.ssrContext,e||"undefined"==typeof __VUE_SSR_CONTEXT__||(e=__VUE_SSR_CONTEXT__),n&&n.call(this,e),e&&e._registeredComponents&&e._registeredComponents.add(u);},c._ssrRegister=l):n&&(l=n),l){var m=c.functional,d=m?c.render:c.beforeCreate;m?(c._injectStyles=l,c.render=function(e,t){return l.call(t),d(e,t)}):c.beforeCreate=d?[].concat(d,l):[l];}return {esModule:o,exports:a,options:c}};},function(t,r){t.exports=e;},function(e,t,r){var n=function(){var e=this,t=e.$createElement,r=e._self._c||t;return e.readOnly?r("span",{ref:"readOnly"},[e._v(e._s(e.amount))]):r("input",{directives:[{name:"model",rawName:"v-model",value:e.amount,expression:"amount"}],ref:"numeric",attrs:{placeholder:e.placeholder,type:"tel"},domProps:{value:e.amount},on:{blur:e.onBlurHandler,input:[function(t){t.target.composing||(e.amount=t.target.value);},e.onInputHandler],focus:e.onFocusHandler}})},i=[],u={render:n,staticRenderFns:i};t.a=u;}])});
});

var Numeric = unwrapExports(vueNumeric_min);
var vueNumeric_min_1 = vueNumeric_min.VueNumeric;

//

var script$C = {
  name: 'MeNumberInput',
  components: {
    Numeric,
    InputIcons: __vue_component__$o
  },

  props: {
    value: {
      type: Number,
      default: null
    },
    precision: {
      type: Number,
      default: 2
    },
    decimalSeparator: {
      type: String,
      default: ','
    },
    thousandSeparator: {
      type: String,
      default: '.'
    },
    minus: {
      type: Boolean,
      default: true
    },
    min: {
      type: Number,
      default: -999999999
    },
    max: {
      type: Number,
      default: 999999999
    },
    size: {
      type: String,
      default: 'md'
    },
    placeholder: {
      type: String,
      default: ''
    },
    state: {
      type: Boolean,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      default: ''
    },
    useLocale: {
      type: [Boolean, String],
      default: false
    }
  },

  data() {
    return {
      current: this.value || 0,
      currentDecimalSeparator: this.decimalSeparator,
      currentThousandSeparator: this.thousandSeparator
    }
  },

  computed: {
    clazz() {
      return `form-control ${this.size} ${this.stateClazz}`
    },

    stateClazz() {
      if (this.state === null) {
        return ''
      }
      return this.state ? 'is-valid' : 'is-invalid'
    }
  },

  watch: {
    value(value) {
      this.current = value;
    }
  },

  created() {
    if (this.useLocale) {
      this.setLocaleConfig();
    }
  },

  methods: {
    setLocaleConfig() {
      const locale = this.getLocaleKey();

      this.currentThousandSeparator = this.getThousandSeparator(locale);
      this.currentDecimalSeparator = this.getDecimalSeparator(locale);
    },

    getThousandSeparator(locale) {
      const separator = new Intl.NumberFormat(locale).format(1000).charAt(1);
      const hasCustomSeparator = separator !== '0';

      if (hasCustomSeparator) return separator

      return this.thousandSeparator
    },

    getDecimalSeparator(locale) {
      return new Intl.NumberFormat(locale).format(1.1).charAt(1)
    },

    getLocaleKey() {
      if (typeof this.useLocale === 'boolean') return this.$toolkit.locale

      return this.useLocale
    }
  }
};

/* script */
const __vue_script__$C = script$C;

/* template */
var __vue_render__$C = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-number-input" },
    [
      _c("Numeric", {
        class: _vm.clazz,
        attrs: {
          currency: _vm.currency,
          "decimal-separator": _vm.currentDecimalSeparator,
          max: _vm.max,
          min: _vm.min,
          minus: _vm.minus,
          placeholder: _vm.placeholder,
          disabled: _vm.disabled,
          precision: _vm.precision,
          "thousand-separator": _vm.currentThousandSeparator,
          "data-cy": "me-number-input"
        },
        on: {
          input: function($event) {
            return _vm.$emit("input", _vm.current)
          },
          blur: function($event) {
            return _vm.$emit("blur", _vm.current)
          },
          focus: function($event) {
            return _vm.$emit("focus", _vm.current)
          }
        },
        model: {
          value: _vm.current,
          callback: function($$v) {
            _vm.current = $$v;
          },
          expression: "current"
        }
      }),
      _vm._v(" "),
      _c("InputIcons", { attrs: { state: _vm.state } })
    ],
    1
  )
};
var __vue_staticRenderFns__$C = [];
__vue_render__$C._withStripped = true;

  /* style */
  const __vue_inject_styles__$C = function (inject) {
    if (!inject) return
    inject("data-v-1f6c511e_0", { source: ".me-number-input .form-control[data-v-1f6c511e] {\n  color: var(--me-gray-0);\n  border-radius: 8px;\n  padding-left: 16px;\n  box-shadow: none;\n  z-index: 1;\n}\n.me-number-input .form-control[data-v-1f6c511e]:not(.is-invalid) {\n  border-color: var(--me-gray-6);\n}\n.me-number-input .form-control[data-v-1f6c511e]:focus {\n  outline: none;\n  box-shadow: none;\n}\n.me-number-input .form-control[data-v-1f6c511e]:focus:not(.is-invalid) {\n  border-color: var(--me-primary-2);\n}\n.me-number-input[data-v-1f6c511e] {\n  position: relative;\n}\n.me-number-input .form-control[data-v-1f6c511e] {\n  padding-left: 8px;\n  padding-right: 8px;\n  caret-color: var(--me-primary-5);\n  color: var(--me-gray-2);\n}\n.me-number-input .form-control.is-invalid[data-v-1f6c511e], .me-number-input .form-control.is-valid[data-v-1f6c511e] {\n  background-image: none;\n  padding-right: 40px;\n}\n.me-number-input .form-control.is-invalid[data-v-1f6c511e] {\n  caret-color: auto;\n}\n.me-number-input .form-control.sm[data-v-1f6c511e] {\n  font-size: 12px;\n  height: 32px;\n}\n.me-number-input .form-control.md[data-v-1f6c511e] {\n  font-size: 14px;\n  height: 40px;\n}\n.me-number-input .form-control.lg[data-v-1f6c511e] {\n  font-size: 16px;\n  height: 48px;\n}\n.me-number-input .form-control[data-v-1f6c511e]:disabled {\n  background: var(--me-gray-6);\n  border: 1px solid var(--me-gray-4);\n  color: var(--me-gray-4);\n}\n.me-number-input .form-control[data-v-1f6c511e]::placeholder {\n  color: var(--me-gray-4);\n}\n\n/*# sourceMappingURL=NumberInput.vue.map */", map: {"version":3,"sources":["NumberInput.vue","/app/build/src/components/NumberInput.vue"],"names":[],"mappings":"AAAA;EACE,uBAAuB;EACvB,kBAAkB;EAClB,kBAAkB;EAClB,gBAAgB;EAChB,UAAU;AACZ;AACA;EACE,8BAA8B;AAChC;AACA;EACE,aAAa;EACb,gBAAgB;AAClB;AACA;EACE,iCAAiC;AACnC;AC4IA;EACA,kBAAA;ADzIA;AC2IA;EAEA,iBAAA;EACA,kBAAA;EACA,gCAAA;EACA,uBAAA;AD1IA;AC4IA;EAEA,sBAAA;EACA,mBAAA;AD3IA;AC8IA;EACA,iBAAA;AD5IA;AC+IA;EACA,eAAA;EACA,YAAA;AD7IA;ACgJA;EACA,eAAA;EACA,YAAA;AD9IA;ACiJA;EACA,eAAA;EACA,YAAA;AD/IA;ACkJA;EACA,4BAAA;EACA,kCAAA;EACA,uBAAA;ADhJA;ACmJA;EACA,uBAAA;ADjJA;;AAEA,0CAA0C","file":"NumberInput.vue","sourcesContent":[".me-number-input .form-control {\n  color: var(--me-gray-0);\n  border-radius: 8px;\n  padding-left: 16px;\n  box-shadow: none;\n  z-index: 1;\n}\n.me-number-input .form-control:not(.is-invalid) {\n  border-color: var(--me-gray-6);\n}\n.me-number-input .form-control:focus {\n  outline: none;\n  box-shadow: none;\n}\n.me-number-input .form-control:focus:not(.is-invalid) {\n  border-color: var(--me-primary-2);\n}\n\n.me-number-input {\n  position: relative;\n}\n.me-number-input .form-control {\n  padding-left: 8px;\n  padding-right: 8px;\n  caret-color: var(--me-primary-5);\n  color: var(--me-gray-2);\n}\n.me-number-input .form-control.is-invalid, .me-number-input .form-control.is-valid {\n  background-image: none;\n  padding-right: 40px;\n}\n.me-number-input .form-control.is-invalid {\n  caret-color: auto;\n}\n.me-number-input .form-control.sm {\n  font-size: 12px;\n  height: 32px;\n}\n.me-number-input .form-control.md {\n  font-size: 14px;\n  height: 40px;\n}\n.me-number-input .form-control.lg {\n  font-size: 16px;\n  height: 48px;\n}\n.me-number-input .form-control:disabled {\n  background: var(--me-gray-6);\n  border: 1px solid var(--me-gray-4);\n  color: var(--me-gray-4);\n}\n.me-number-input .form-control::placeholder {\n  color: var(--me-gray-4);\n}\n\n/*# sourceMappingURL=NumberInput.vue.map */","<template>\n  <div class=\"me-number-input\">\n    <Numeric\n      v-model=\"current\"\n      :class=\"clazz\"\n      :currency=\"currency\"\n      :decimal-separator=\"currentDecimalSeparator\"\n      :max=\"max\"\n      :min=\"min\"\n      :minus=\"minus\"\n      :placeholder=\"placeholder\"\n      :disabled=\"disabled\"\n      :precision=\"precision\"\n      :thousand-separator=\"currentThousandSeparator\"\n      @input=\"$emit('input', current)\"\n      @blur=\"$emit('blur', current)\"\n      @focus=\"$emit('focus', current)\"\n      data-cy=\"me-number-input\"\n    />\n\n    <InputIcons :state=\"state\" />\n  </div>\n</template>\n\n<script>\nimport Numeric from 'vue-numeric'\nimport InputIcons from './InputIcons.vue'\n\nexport default {\n  name: 'MeNumberInput',\n  components: {\n    Numeric,\n    InputIcons\n  },\n\n  props: {\n    value: {\n      type: Number,\n      default: null\n    },\n    precision: {\n      type: Number,\n      default: 2\n    },\n    decimalSeparator: {\n      type: String,\n      default: ','\n    },\n    thousandSeparator: {\n      type: String,\n      default: '.'\n    },\n    minus: {\n      type: Boolean,\n      default: true\n    },\n    min: {\n      type: Number,\n      default: -999999999\n    },\n    max: {\n      type: Number,\n      default: 999999999\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    currency: {\n      type: String,\n      default: ''\n    },\n    useLocale: {\n      type: [Boolean, String],\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      current: this.value || 0,\n      currentDecimalSeparator: this.decimalSeparator,\n      currentThousandSeparator: this.thousandSeparator\n    }\n  },\n\n  computed: {\n    clazz() {\n      return `form-control ${this.size} ${this.stateClazz}`\n    },\n\n    stateClazz() {\n      if (this.state === null) {\n        return ''\n      }\n      return this.state ? 'is-valid' : 'is-invalid'\n    }\n  },\n\n  watch: {\n    value(value) {\n      this.current = value\n    }\n  },\n\n  created() {\n    if (this.useLocale) {\n      this.setLocaleConfig()\n    }\n  },\n\n  methods: {\n    setLocaleConfig() {\n      const locale = this.getLocaleKey()\n\n      this.currentThousandSeparator = this.getThousandSeparator(locale)\n      this.currentDecimalSeparator = this.getDecimalSeparator(locale)\n    },\n\n    getThousandSeparator(locale) {\n      const separator = new Intl.NumberFormat(locale).format(1000).charAt(1)\n      const hasCustomSeparator = separator !== '0'\n\n      if (hasCustomSeparator) return separator\n\n      return this.thousandSeparator\n    },\n\n    getDecimalSeparator(locale) {\n      return new Intl.NumberFormat(locale).format(1.1).charAt(1)\n    },\n\n    getLocaleKey() {\n      if (typeof this.useLocale === 'boolean') return this.$toolkit.locale\n\n      return this.useLocale\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/input.scss\";\n\n  .me-number-input {\n    position: relative;\n\n    .form-control {\n      @extend %input-style;\n      padding-left: 8px;\n      padding-right: 8px;\n      caret-color: var(--me-primary-5);\n      color: var(--me-gray-2);\n\n      &.is-invalid,\n      &.is-valid {\n        background-image: none;\n        padding-right: 40px;\n      }\n\n      &.is-invalid {\n        caret-color: auto;\n      }\n\n      &.sm {\n        font-size: 12px;\n        height: 32px;\n      }\n\n      &.md {\n        font-size: 14px;\n        height: 40px;\n      }\n\n      &.lg {\n        font-size: 16px;\n        height: 48px;\n      }\n\n      &:disabled {\n        background: var(--me-gray-6);\n        border: 1px solid var(--me-gray-4);\n        color: var(--me-gray-4);\n      }\n\n      &::placeholder {\n        color: var(--me-gray-4);\n      }\n    }\n\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$C = "data-v-1f6c511e";
  /* module identifier */
  const __vue_module_identifier__$C = undefined;
  /* functional template */
  const __vue_is_functional_template__$C = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$C, staticRenderFns: __vue_staticRenderFns__$C },
    __vue_inject_styles__$C,
    __vue_script__$C,
    __vue_scope_id__$C,
    __vue_is_functional_template__$C,
    __vue_module_identifier__$C,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$D = {
  name: 'MeTextArea',
  components: {
    BFormTextarea,
    InputIcons: __vue_component__$o
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    rows: {
      type: Number,
      default: 3
    },
    maxRows: {
      type: Number,
      default: 5
    },
    size: {
      type: String,
      default: 'md'
    },
    placeholder: {
      type: String,
      default: ''
    },
    state: {
      type: Boolean,
      default: null
    },
    maxLength: {
      type: Number,
      default: null,
      validator: value => value === null || value > 0
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      current: this.maxLength && this.value.length > this.maxLength
        ? this.value.substring(0, this.maxLength) : this.value
    }
  },

  watch: {
    value(val) {
      this.current = val;
    }
  }
};

/* script */
const __vue_script__$D = script$D;

/* template */
var __vue_render__$D = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "me-textarea" },
    [
      _c("InputIcons", {
        staticClass: "state-icon",
        attrs: { state: _vm.state }
      }),
      _vm._v(" "),
      _c("BFormTextarea", {
        class: [
          "me-textarea-input",
          _vm.disabled ? "disabled" : "",
          _vm.maxLength ? "max-length-input" : ""
        ],
        attrs: {
          "max-rows": _vm.maxRows,
          placeholder: _vm.placeholder,
          rows: _vm.rows,
          size: _vm.size,
          state: _vm.state,
          maxlength: _vm.maxLength,
          disabled: _vm.disabled
        },
        on: {
          input: function($event) {
            return _vm.$emit("input", _vm.current)
          }
        },
        model: {
          value: _vm.current,
          callback: function($$v) {
            _vm.current = $$v;
          },
          expression: "current"
        }
      }),
      _vm._v(" "),
      _vm.maxLength
        ? _c(
            "div",
            {
              class: [
                "char-count char-count-" + _vm.size,
                _vm.current.length === 0 || _vm.disabled ? "empty-disabled" : ""
              ]
            },
            [
              _vm._v(
                "\n    " +
                  _vm._s(_vm.current.length) +
                  "/" +
                  _vm._s(_vm.maxLength) +
                  "\n  "
              )
            ]
          )
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$D = [];
__vue_render__$D._withStripped = true;

  /* style */
  const __vue_inject_styles__$D = function (inject) {
    if (!inject) return
    inject("data-v-1a93e08a_0", { source: ".me-textarea[data-v-1a93e08a] {\n  position: relative;\n}\n.me-textarea .state-icon[data-v-1a93e08a] {\n  align-items: flex-start;\n  padding-top: 14px;\n}\n.me-textarea .char-count[data-v-1a93e08a] {\n  position: absolute;\n  text-align: right;\n  bottom: 0.5rem;\n  right: 8px;\n}\n.me-textarea .char-count.empty-disabled[data-v-1a93e08a] {\n  color: var(--me-gray-4);\n}\n.me-textarea .char-count-sm[data-v-1a93e08a] {\n  font-size: 12px;\n}\n.me-textarea .char-count-md[data-v-1a93e08a] {\n  font-size: 14px;\n}\n.me-textarea .char-count-lg[data-v-1a93e08a] {\n  font-size: 16px;\n}\n.me-textarea .me-textarea-input[data-v-1a93e08a] {\n  color: var(--me-gray-2);\n  outline: 1px solid var(--me-gray-6);\n  border: none;\n  padding-left: 8px;\n  padding-right: 8px;\n  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n  scrollbar-width: thin;\n}\n.me-textarea .me-textarea-input.max-length-input[data-v-1a93e08a] {\n  border-bottom: 2rem solid var(--me-white);\n}\n.me-textarea .me-textarea-input.disabled[data-v-1a93e08a] {\n  outline-color: var(--me-gray-4);\n  color: var(--me-gray-4);\n  border-bottom-color: var(--me-gray-6);\n  background-color: var(--me-gray-6);\n}\n.me-textarea .me-textarea-input.form-control[data-v-1a93e08a]::placeholder {\n  color: var(--me-gray-4);\n}\n.me-textarea .me-textarea-input.form-control[data-v-1a93e08a]:focus {\n  outline-color: var(--me-primary-2);\n  caret-color: var(--me-primary-2);\n  box-shadow: none;\n}\n.me-textarea .me-textarea-input.form-control.is-invalid[data-v-1a93e08a] {\n  outline-color: var(--me-danger-2);\n  caret-color: auto;\n  background-image: none;\n  padding-right: 32px;\n}\n.me-textarea .me-textarea-input.form-control.is-valid[data-v-1a93e08a] {\n  background-image: none;\n  padding-right: 32px;\n}\n.me-textarea .me-textarea-input.form-control-sm[data-v-1a93e08a] {\n  font-size: 12px;\n}\n.me-textarea .me-textarea-input.form-control-md[data-v-1a93e08a] {\n  font-size: 14px;\n}\n.me-textarea .me-textarea-input.form-control-lg[data-v-1a93e08a] {\n  font-size: 16px;\n}\n.me-textarea .me-textarea-input[data-v-1a93e08a]::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n.me-textarea .me-textarea-input[data-v-1a93e08a]::-webkit-scrollbar-track {\n  border-radius: 15px;\n}\n.me-textarea .me-textarea-input[data-v-1a93e08a]::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\n.me-textarea .me-textarea-input[data-v-1a93e08a]::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\n\n/*# sourceMappingURL=TextArea.vue.map */", map: {"version":3,"sources":["/app/build/src/components/TextArea.vue","TextArea.vue"],"names":[],"mappings":"AA4FA;EACA,kBAAA;AC3FA;AD6FA;EACA,uBAAA;EACA,iBAAA;AC3FA;AD8FA;EACA,kBAAA;EACA,iBAAA;EACA,cAAA;EACA,UAAA;AC5FA;AD8FA;EACA,uBAAA;AC5FA;AD+FA;EACA,eAAA;AC7FA;ADgGA;EACA,eAAA;AC9FA;ADiGA;EACA,eAAA;AC/FA;ADmGA;EACA,uBAAA;EACA,mCAAA;EACA,YAAA;EACA,iBAAA;EACA,kBAAA;EACA,gDAAA;EACA,qBAAA;ACjGA;ADmGA;EACA,yCAAA;ACjGA;ADoGA;EACA,+BAAA;EACA,uBAAA;EACA,qCAAA;EACA,kCAAA;AClGA;ADsGA;EACA,uBAAA;ACpGA;ADuGA;EACA,kCAAA;EACA,gCAAA;EACA,gBAAA;ACrGA;ADwGA;EACA,iCAAA;EACA,iBAAA;EACA,sBAAA;EACA,mBAAA;ACtGA;ADyGA;EACA,sBAAA;EACA,mBAAA;ACvGA;AD0GA;EACA,eAAA;ACxGA;AD2GA;EACA,eAAA;ACzGA;AD4GA;EACA,eAAA;AC1GA;AD8GA;EACA,UAAA;EACA,WAAA;AC5GA;AD8GA;EACA,mBAAA;AC5GA;AD+GA;EACA,+BAAA;EACA,mBAAA;AC7GA;AD+GA;EACA,+BAAA;AC7GA;;AAEA,uCAAuC","file":"TextArea.vue","sourcesContent":["<template>\n  <section class=\"me-textarea\">\n    <InputIcons\n      :state=\"state\"\n      class=\"state-icon\"\n    />\n\n    <BFormTextarea\n      v-model=\"current\"\n      :max-rows=\"maxRows\"\n      :placeholder=\"placeholder\"\n      :rows=\"rows\"\n      :size=\"size\"\n      :state=\"state\"\n      @input=\"$emit('input', current)\"\n      :maxlength=\"maxLength\"\n      :disabled=\"disabled\"\n      :class=\"['me-textarea-input', disabled ? 'disabled' : '',\n               maxLength ? 'max-length-input' : '']\"\n    />\n\n    <div\n      v-if=\"maxLength\"\n      :class=\"[`char-count char-count-${size}`,\n               current.length === 0 || disabled ? 'empty-disabled' : '']\"\n    >\n      {{ current.length }}/{{ maxLength }}\n    </div>\n  </section>\n</template>\n\n<script>\nimport { BFormTextarea } from 'bootstrap-vue'\nimport InputIcons from './InputIcons.vue'\n\nexport default {\n  name: 'MeTextArea',\n  components: {\n    BFormTextarea,\n    InputIcons\n  },\n  props: {\n    value: {\n      type: String,\n      default: ''\n    },\n    rows: {\n      type: Number,\n      default: 3\n    },\n    maxRows: {\n      type: Number,\n      default: 5\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    maxLength: {\n      type: Number,\n      default: null,\n      validator: value => value === null || value > 0\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      current: this.maxLength && this.value.length > this.maxLength\n        ? this.value.substring(0, this.maxLength) : this.value\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-textarea {\n    position: relative;\n\n    .state-icon {\n      align-items: flex-start;\n      padding-top: 14px;\n    }\n\n    .char-count {\n      position: absolute;\n      text-align: right;\n      bottom: 0.5rem;\n      right: 8px;\n\n      &.empty-disabled {\n        color: var(--me-gray-4);\n      }\n\n      &-sm {\n        font-size: 12px;\n      }\n\n      &-md {\n        font-size: 14px;\n      }\n\n      &-lg {\n        font-size: 16px;\n      }\n    }\n\n    .me-textarea-input {\n      color: var(--me-gray-2);\n      outline: 1px solid var(--me-gray-6);\n      border: none;\n      padding-left: 8px;\n      padding-right: 8px;\n      scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n      scrollbar-width: thin;\n\n      &.max-length-input {\n        border-bottom: 2rem solid var(--me-white);\n      }\n\n      &.disabled {\n        outline-color: var(--me-gray-4);\n        color: var(--me-gray-4);\n        border-bottom-color: var(--me-gray-6);\n        background-color: var(--me-gray-6);\n      }\n\n      &.form-control {\n        &::placeholder {\n          color: var(--me-gray-4);\n        }\n\n        &:focus {\n          outline-color: var(--me-primary-2);\n          caret-color: var(--me-primary-2);\n          box-shadow: none;\n        }\n\n        &.is-invalid {\n          outline-color: var(--me-danger-2);\n          caret-color: auto;\n          background-image: none;\n          padding-right: 32px;\n        }\n\n        &.is-valid {\n          background-image: none;\n          padding-right: 32px;\n        }\n\n        &-sm {\n          font-size: 12px;\n        }\n\n        &-md {\n          font-size: 14px;\n        }\n\n        &-lg {\n          font-size: 16px;\n        }\n      }\n\n      &::-webkit-scrollbar {\n        width: 6px;\n        height: 6px;\n\n        &-track {\n          border-radius: 15px;\n        }\n\n        &-thumb {\n          background: rgba(0, 0, 0, 0.25);\n          border-radius: 15px;\n\n          &:hover {\n            background: rgba(0, 0, 0, 0.45);\n          }\n        }\n      }\n    }\n\n  }\n</style>\n",".me-textarea {\n  position: relative;\n}\n.me-textarea .state-icon {\n  align-items: flex-start;\n  padding-top: 14px;\n}\n.me-textarea .char-count {\n  position: absolute;\n  text-align: right;\n  bottom: 0.5rem;\n  right: 8px;\n}\n.me-textarea .char-count.empty-disabled {\n  color: var(--me-gray-4);\n}\n.me-textarea .char-count-sm {\n  font-size: 12px;\n}\n.me-textarea .char-count-md {\n  font-size: 14px;\n}\n.me-textarea .char-count-lg {\n  font-size: 16px;\n}\n.me-textarea .me-textarea-input {\n  color: var(--me-gray-2);\n  outline: 1px solid var(--me-gray-6);\n  border: none;\n  padding-left: 8px;\n  padding-right: 8px;\n  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n  scrollbar-width: thin;\n}\n.me-textarea .me-textarea-input.max-length-input {\n  border-bottom: 2rem solid var(--me-white);\n}\n.me-textarea .me-textarea-input.disabled {\n  outline-color: var(--me-gray-4);\n  color: var(--me-gray-4);\n  border-bottom-color: var(--me-gray-6);\n  background-color: var(--me-gray-6);\n}\n.me-textarea .me-textarea-input.form-control::placeholder {\n  color: var(--me-gray-4);\n}\n.me-textarea .me-textarea-input.form-control:focus {\n  outline-color: var(--me-primary-2);\n  caret-color: var(--me-primary-2);\n  box-shadow: none;\n}\n.me-textarea .me-textarea-input.form-control.is-invalid {\n  outline-color: var(--me-danger-2);\n  caret-color: auto;\n  background-image: none;\n  padding-right: 32px;\n}\n.me-textarea .me-textarea-input.form-control.is-valid {\n  background-image: none;\n  padding-right: 32px;\n}\n.me-textarea .me-textarea-input.form-control-sm {\n  font-size: 12px;\n}\n.me-textarea .me-textarea-input.form-control-md {\n  font-size: 14px;\n}\n.me-textarea .me-textarea-input.form-control-lg {\n  font-size: 16px;\n}\n.me-textarea .me-textarea-input::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n.me-textarea .me-textarea-input::-webkit-scrollbar-track {\n  border-radius: 15px;\n}\n.me-textarea .me-textarea-input::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\n.me-textarea .me-textarea-input::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\n\n/*# sourceMappingURL=TextArea.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$D = "data-v-1a93e08a";
  /* module identifier */
  const __vue_module_identifier__$D = undefined;
  /* functional template */
  const __vue_is_functional_template__$D = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$D, staticRenderFns: __vue_staticRenderFns__$D },
    __vue_inject_styles__$D,
    __vue_script__$D,
    __vue_scope_id__$D,
    __vue_is_functional_template__$D,
    __vue_module_identifier__$D,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$E = {
  name: 'MeInput',
  components: {
    Checkbox: __vue_component__$l,
    DatePicker: __vue_component__$B,
    NumberInput: __vue_component__$C,
    Select: __vue_component__$m,
    TextArea: __vue_component__$D,
    TextInput: __vue_component__$p
  },

  props: {
    type: {
      type: String,
      default: 'string'
    },
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: null
    },
    options: {
      type: Object,
      default() {
        return { }
      }
    },
    size: {
      type: String,
      default: 'md'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    state: {
      type: Boolean,
      default: null
    },
    context: {
      type: Object,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    rows: {
      type: Number,
      default: 3
    },
    maxRows: {
      type: Number,
      default: 5
    },
    autoFlip: {
      type: Boolean,
      default: true
    },
    selectable: {
      type: Function,
      default: () => true
    }
  },

  data() {
    return {
      current: this.value
    }
  },

  watch: {
    value(val) {
      this.current = val;
    }
  }
};

/* script */
const __vue_script__$E = script$E;

/* template */
var __vue_render__$E = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-input" },
    [
      _vm.type === "string" || _vm.type === "text"
        ? _c("TextInput", {
            attrs: {
              placeholder: _vm.placeholder,
              disabled: _vm.options.disabled,
              size: _vm.size,
              state: _vm.state
            },
            on: {
              input: function($event) {
                return _vm.$emit("input", _vm.current)
              }
            },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          })
        : _vm.type === "number" || _vm.type === "numeric"
        ? _c("NumberInput", {
            attrs: {
              "use-locale": _vm.options.useLocale,
              placeholder: _vm.placeholder,
              size: _vm.size,
              state: _vm.state,
              disabled: _vm.options.disabled,
              currency: _vm.options.currency,
              minus: _vm.options.minus,
              max: _vm.options.max,
              min: _vm.options.min,
              precision: _vm.options.precision || 0,
              thousandSeparator: _vm.options.thousandSeparator || ""
            },
            on: {
              input: function($event) {
                return _vm.$emit("input", _vm.current)
              }
            },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          })
        : _vm.type === "date"
        ? _c("DatePicker", {
            attrs: {
              placeholder: _vm.placeholder,
              size: _vm.size,
              state: _vm.state,
              disabled: _vm.options.disabled,
              type: _vm.options.type
            },
            on: {
              input: function($event) {
                return _vm.$emit("input", _vm.current)
              }
            },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          })
        : _vm.type === "boolean"
        ? _c("Checkbox", {
            attrs: { state: _vm.state },
            on: {
              input: function($event) {
                return _vm.$emit("input", _vm.current)
              }
            },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          })
        : _vm.type === "object" || _vm.type === "select"
        ? _c("Select", {
            attrs: {
              autoFlip: _vm.autoFlip,
              context: _vm.context,
              label: _vm.options.label,
              multiple: _vm.options.multiple || _vm.multiple,
              options: _vm.options.options,
              placeholder: _vm.placeholder,
              provider: _vm.options.provider,
              clearable: _vm.options.clearable,
              highlightSearch: _vm.options.highlightSearch,
              showMore: _vm.options.showMore,
              showMoreTitle: _vm.options.showMoreTitle,
              showMoreSubtitle: _vm.options.showMoreSubtitle,
              removeSelectedOption: _vm.options.removeSelectedOption,
              showOptionAll: _vm.options.showOptionAll,
              selectable: _vm.selectable,
              size: _vm.size,
              state: _vm.state,
              disabled: _vm.options.disabled
            },
            on: {
              input: function($event) {
                return _vm.$emit("input", _vm.current)
              }
            },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          })
        : _vm.type === "textarea"
        ? _c("TextArea", {
            attrs: {
              maxRows: _vm.maxRows,
              placeholder: _vm.placeholder,
              rows: _vm.rows,
              size: _vm.size,
              state: _vm.state
            },
            on: {
              input: function($event) {
                return _vm.$emit("input", _vm.current)
              }
            },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          })
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$E = [];
__vue_render__$E._withStripped = true;

  /* style */
  const __vue_inject_styles__$E = function (inject) {
    if (!inject) return
    inject("data-v-7d6c827e_0", { source: ".me-input[data-v-7d6c827e] {\n  display: inline-block;\n}\n\n/*# sourceMappingURL=Input.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Input.vue","Input.vue"],"names":[],"mappings":"AAsKA;EACA,qBAAA;ACrKA;;AAEA,oCAAoC","file":"Input.vue","sourcesContent":["<template>\n  <div class=\"me-input\">\n    <TextInput\n      v-if=\"type === 'string' || type === 'text'\"\n      v-model=\"current\"\n      :placeholder=\"placeholder\"\n      :disabled=\"options.disabled\"\n      :size=\"size\"\n      :state=\"state\"\n      @input=\"$emit('input', current)\"\n    />\n    <NumberInput\n      v-else-if=\"type === 'number' || type === 'numeric'\"\n      v-model=\"current\"\n      :use-locale=\"options.useLocale\"\n      :placeholder=\"placeholder\"\n      :size=\"size\"\n      :state=\"state\"\n      :disabled=\"options.disabled\"\n      :currency=\"options.currency\"\n      :minus=\"options.minus\"\n      :max=\"options.max\"\n      :min=\"options.min\"\n      :precision=\"options.precision || 0\"\n      :thousandSeparator=\"options.thousandSeparator || ''\"\n      @input=\"$emit('input', current)\"\n    />\n    <DatePicker\n      v-else-if=\"type === 'date'\"\n      v-model=\"current\"\n      :placeholder=\"placeholder\"\n      :size=\"size\"\n      :state=\"state\"\n      :disabled=\"options.disabled\"\n      :type=\"options.type\"\n      @input=\"$emit('input', current)\"\n    />\n    <Checkbox\n      v-else-if=\"type === 'boolean'\"\n      v-model=\"current\"\n      :state=\"state\"\n      @input=\"$emit('input', current)\"\n    />\n    <Select\n      v-else-if=\"type === 'object' || type === 'select'\"\n      v-model=\"current\"\n      :autoFlip=\"autoFlip\"\n      :context=\"context\"\n      :label=\"options.label\"\n      :multiple=\"options.multiple || multiple\"\n      :options=\"options.options\"\n      :placeholder=\"placeholder\"\n      :provider=\"options.provider\"\n      :clearable=\"options.clearable\"\n      :highlightSearch=\"options.highlightSearch\"\n      :showMore=\"options.showMore\"\n      :showMoreTitle=\"options.showMoreTitle\"\n      :showMoreSubtitle=\"options.showMoreSubtitle\"\n      :removeSelectedOption=\"options.removeSelectedOption\"\n      :showOptionAll=\"options.showOptionAll\"\n      :selectable=\"selectable\"\n      :size=\"size\"\n      :state=\"state\"\n      :disabled=\"options.disabled\"\n      @input=\"$emit('input', current)\"\n    />\n    <TextArea\n      v-else-if=\"type === 'textarea'\"\n      v-model=\"current\"\n      :maxRows=\"maxRows\"\n      :placeholder=\"placeholder\"\n      :rows=\"rows\"\n      :size=\"size\"\n      :state=\"state\"\n      @input=\"$emit('input', current)\"\n    />\n  </div>\n</template>\n\n<script>\nimport Checkbox from './Checkbox.vue'\nimport DatePicker from './DatePicker.vue'\nimport NumberInput from './NumberInput.vue'\nimport Select from './Select.vue'\nimport TextArea from './TextArea.vue'\nimport TextInput from './TextInput.vue'\n\nexport default {\n  name: 'MeInput',\n  components: {\n    Checkbox,\n    DatePicker,\n    NumberInput,\n    Select,\n    TextArea,\n    TextInput\n  },\n\n  props: {\n    type: {\n      type: String,\n      default: 'string'\n    },\n    value: {\n      type: [String, Number, Date, Boolean, Object, Array],\n      default: null\n    },\n    options: {\n      type: Object,\n      default() {\n        return { }\n      }\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    multiple: {\n      type: Boolean,\n      default: false\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    context: {\n      type: Object,\n      default: null\n    },\n    placeholder: {\n      type: String,\n      default: null\n    },\n    rows: {\n      type: Number,\n      default: 3\n    },\n    maxRows: {\n      type: Number,\n      default: 5\n    },\n    autoFlip: {\n      type: Boolean,\n      default: true\n    },\n    selectable: {\n      type: Function,\n      default: () => true\n    }\n  },\n\n  data() {\n    return {\n      current: this.value\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-input {\n    display: inline-block;\n  }\n</style>\n",".me-input {\n  display: inline-block;\n}\n\n/*# sourceMappingURL=Input.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$E = "data-v-7d6c827e";
  /* module identifier */
  const __vue_module_identifier__$E = undefined;
  /* functional template */
  const __vue_is_functional_template__$E = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$E, staticRenderFns: __vue_staticRenderFns__$E },
    __vue_inject_styles__$E,
    __vue_script__$E,
    __vue_scope_id__$E,
    __vue_is_functional_template__$E,
    __vue_module_identifier__$E,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$F = {
  name: 'MeButtonBarForm',
  components: {
    Form: __vue_component__$h,
    FormGroup: __vue_component__$i,
    Input: __vue_component__$E
  },
  props: {
    formFields: {
      type: Array,
      required: true
    },
    url: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      model: this.formFields.reduce(
        (obj, item) => Object.assign(obj, { [item.name]: item.type === 'boolean' ? true : null }), {}
      ),
      busy: false
    }
  },
  validations() {
    const model = this.formFields.reduce(
      (obj, item) => Object.assign(obj, {
        [item.name]: {
          required: this.isItemValidationRequired(item)
        }
      }), {});
    return { model }
  },
  computed: {
    clazz() {
      return `me-button-bar-form__${this.busy ? 'busy' : ''}`
    }
  },
  methods: {
    isItemValidationRequired(item) {
      if (item.requiredIf) return this.setRequiredIf(item.requiredIf.any[0])
      if (item.required) return required$1
      return false
    },
    setRequiredIf({ field, equalsTo }) {
      return requiredIf$1(obj => obj[field] && obj[field].value === equalsTo)
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false
      }
      const form = {};
      for (const [key, value] of Object.entries(this.model)) {
        const field = this.formFields.find(field => field.name === key);
        if (field.type === 'select') {
          form[key] = value && value[field.options.value];
        } else {
          form[key] = (value && value.value) || value;
        }
      }
      try {
        this.busy = true;
        const res = await fetch(this.url, {
          method: 'POST',
          body: JSON.stringify(form)
        });
        this.$emit('submit', res);
      } catch (error) {
        this.$emit('submit', error);
      } finally {
        this.busy = false;
      }
    }
  }
};

/* script */
const __vue_script__$F = script$F;

/* template */
var __vue_render__$F = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-button-bar-form" },
    [
      _c(
        "Form",
        { class: _vm.clazz, on: { submit: _vm.submit } },
        [
          _vm.busy
            ? _c("div", { staticClass: "me-button-bar-form__spinner" }, [
                _c(
                  "div",
                  { staticClass: "me-button-bar-form__spinner__content" },
                  [
                    _c("me-spinner", {
                      attrs: { fluid: "", variant: "primary" }
                    })
                  ],
                  1
                )
              ])
            : _vm._e(),
          _vm._v(" "),
          _vm._l(_vm.formFields, function(field) {
            return _c(
              "FormGroup",
              {
                key: field.label,
                attrs: {
                  label: field.label,
                  "invalid-feedback": _vm.$toolkit.t("feedback.required"),
                  state: !_vm.$v.model[field.name].$anyError ? null : false
                }
              },
              [
                _c("Input", {
                  ref: field.name,
                  refInFor: true,
                  attrs: {
                    type: field.type,
                    state: !_vm.$v.model[field.name].$anyError ? null : false,
                    options: field.options
                  },
                  model: {
                    value: _vm.$v.model[field.name].$model,
                    callback: function($$v) {
                      _vm.$set(_vm.$v.model[field.name], "$model", $$v);
                    },
                    expression: "$v.model[field.name].$model"
                  }
                })
              ],
              1
            )
          })
        ],
        2
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$F = [];
__vue_render__$F._withStripped = true;

  /* style */
  const __vue_inject_styles__$F = function (inject) {
    if (!inject) return
    inject("data-v-828f023c_0", { source: ".me-button-bar-form__spinner[data-v-828f023c] {\n  align-items: center;\n  background-color: rgba(0, 0, 0, 0.05);\n  display: flex;\n  height: 100%;\n  justify-content: center;\n  left: 0;\n  position: absolute;\n  top: 0;\n  width: 100%;\n  z-index: 9999;\n}\n.me-button-bar-form__spinner__content[data-v-828f023c] {\n  height: 64px;\n  width: 64px;\n}\n.me-button-bar-form__busy[data-v-828f023c]  .button-bar button {\n  background-color: lightgray;\n  border: none;\n}\n.me-button-bar-form .me-form[data-v-828f023c]  .invalid-feedback {\n  display: block;\n}\n.me-button-bar-form .me-form .me-input[data-v-828f023c] {\n  width: 100%;\n}\n.me-button-bar-form .me-form .me-input[data-v-828f023c] textarea.form-control.is-invalid {\n  background-position: top calc(0.375em + 0.1875rem) right calc(1.375em + 0.1875rem);\n}\n\n/*# sourceMappingURL=ButtonBarForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/ButtonBarForm.vue","ButtonBarForm.vue"],"names":[],"mappings":"AAmHA;EACA,mBAAA;EACA,qCAAA;EACA,aAAA;EACA,YAAA;EACA,uBAAA;EACA,OAAA;EACA,kBAAA;EACA,MAAA;EACA,WAAA;EACA,aAAA;AClHA;ADoHA;EACA,YAAA;EACA,WAAA;AClHA;ADwHA;EACA,2BAAA;EACA,YAAA;ACtHA;AD4HA;EACA,cAAA;AC1HA;AD6HA;EACA,WAAA;AC3HA;AD6HA;EACA,kFAAA;AC3HA;;AAEA,4CAA4C","file":"ButtonBarForm.vue","sourcesContent":["<template>\n  <div class=\"me-button-bar-form\">\n    <Form @submit=\"submit\" :class=\"clazz\">\n      <div v-if=\"busy\" class=\"me-button-bar-form__spinner\">\n        <div class=\"me-button-bar-form__spinner__content\">\n          <me-spinner fluid variant=\"primary\" />\n        </div>\n      </div>\n      <FormGroup\n        v-for=\"field in formFields\"\n        :label=\"field.label\"\n        :invalid-feedback=\"$toolkit.t('feedback.required')\"\n        :key=\"field.label \"\n        :state=\"!$v.model[field.name].$anyError ? null : false\"\n      >\n        <Input\n          :type=\"field.type\"\n          v-model=\"$v.model[field.name].$model\"\n          :ref=\"field.name\"\n          :state=\"!$v.model[field.name].$anyError ? null : false\"\n          :options=\"field.options\"\n        />\n      </FormGroup>\n    </Form>\n  </div>\n</template>\n\n<script>\nimport required from 'vuelidate/lib/validators/required'\nimport requiredIf from 'vuelidate/lib/validators/requiredIf'\nimport Form from './Form.vue'\nimport FormGroup from './FormGroup.vue'\nimport Input from './Input.vue'\n\nexport default {\n  name: 'MeButtonBarForm',\n  components: {\n    Form,\n    FormGroup,\n    Input\n  },\n  props: {\n    formFields: {\n      type: Array,\n      required: true\n    },\n    url: {\n      type: String,\n      required: true\n    }\n  },\n  data() {\n    return {\n      model: this.formFields.reduce(\n        (obj, item) => Object.assign(obj, { [item.name]: item.type === 'boolean' ? true : null }), {}\n      ),\n      busy: false\n    }\n  },\n  validations() {\n    const model = this.formFields.reduce(\n      (obj, item) => Object.assign(obj, {\n        [item.name]: {\n          required: this.isItemValidationRequired(item)\n        }\n      }), {})\n    return { model }\n  },\n  computed: {\n    clazz() {\n      return `me-button-bar-form__${this.busy ? 'busy' : ''}`\n    }\n  },\n  methods: {\n    isItemValidationRequired(item) {\n      if (item.requiredIf) return this.setRequiredIf(item.requiredIf.any[0])\n      if (item.required) return required\n      return false\n    },\n    setRequiredIf({ field, equalsTo }) {\n      return requiredIf(obj => obj[field] && obj[field].value === equalsTo)\n    },\n    async submit() {\n      this.$v.$touch()\n      if (this.$v.$invalid) {\n        return false\n      }\n      const form = {}\n      for (const [key, value] of Object.entries(this.model)) {\n        const field = this.formFields.find(field => field.name === key)\n        if (field.type === 'select') {\n          form[key] = value && value[field.options.value]\n        } else {\n          form[key] = (value && value.value) || value\n        }\n      }\n      try {\n        this.busy = true\n        const res = await fetch(this.url, {\n          method: 'POST',\n          body: JSON.stringify(form)\n        })\n        this.$emit('submit', res)\n      } catch (error) {\n        this.$emit('submit', error)\n      } finally {\n        this.busy = false\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-button-bar-form {\n    &__spinner {\n      align-items: center;\n      background-color: rgba(0, 0, 0, 0.05);\n      display: flex;\n      height: 100%;\n      justify-content: center;\n      left: 0;\n      position: absolute;\n      top: 0;\n      width: 100%;\n      z-index: 9999;\n\n      &__content {\n        height: 64px;\n        width: 64px;\n      }\n    }\n\n    &__busy {\n      ::v-deep .button-bar {\n        & button {\n          background-color: lightgray;\n          border: none;\n        }\n      }\n    }\n\n    .me-form {\n      ::v-deep .invalid-feedback {\n        display: block;\n      }\n\n      .me-input {\n        width: 100%;\n\n        &::v-deep textarea.form-control.is-invalid {\n          background-position: top calc(0.375em + 0.1875rem) right calc(1.375em + 0.1875rem)\n        }\n      }\n    }\n  }\n</style>\n",".me-button-bar-form__spinner {\n  align-items: center;\n  background-color: rgba(0, 0, 0, 0.05);\n  display: flex;\n  height: 100%;\n  justify-content: center;\n  left: 0;\n  position: absolute;\n  top: 0;\n  width: 100%;\n  z-index: 9999;\n}\n.me-button-bar-form__spinner__content {\n  height: 64px;\n  width: 64px;\n}\n.me-button-bar-form__busy ::v-deep .button-bar button {\n  background-color: lightgray;\n  border: none;\n}\n.me-button-bar-form .me-form ::v-deep .invalid-feedback {\n  display: block;\n}\n.me-button-bar-form .me-form .me-input {\n  width: 100%;\n}\n.me-button-bar-form .me-form .me-input::v-deep textarea.form-control.is-invalid {\n  background-position: top calc(0.375em + 0.1875rem) right calc(1.375em + 0.1875rem);\n}\n\n/*# sourceMappingURL=ButtonBarForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$F = "data-v-828f023c";
  /* module identifier */
  const __vue_module_identifier__$F = undefined;
  /* functional template */
  const __vue_is_functional_template__$F = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$F, staticRenderFns: __vue_staticRenderFns__$F },
    __vue_inject_styles__$F,
    __vue_script__$F,
    __vue_scope_id__$F,
    __vue_is_functional_template__$F,
    __vue_module_identifier__$F,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const maxVisibleActions = 4;

var script$G = {
  name: 'MeButtonBar',
  components: {
    ButtonBarForm: __vue_component__$F,
    MeIcon: __vue_component__
  },
  props: {
    actions: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    size: {
      type: Number,
      default: 0
    },
    maxSize: {
      type: Number,
      default: null
    },
    apiUrl: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      dropdownVisibleIndex: null,
      currentWidth: 0,
      responsiveSize: maxVisibleActions,
      apiActions: []
    }
  },

  computed: {
    allActions() {
      return [...this.apiActions, ...this.actions]
    },

    visibleActions() {
      return this.allActions.slice(0, this.size || this.responsiveSize)
    },

    moreActions() {
      const actionsFiltered = this.allActions.slice(this.size || this.responsiveSize, this.allActions.length);

      return this.mapActionsToDropdownGroup(actionsFiltered)
    },

    clazz() {
      return actionIndex => `mr-0 ${(this.allActions[actionIndex] && this.allActions[actionIndex].style) || 'text-decoration-none'}`
    }
  },

  watch: {
    visibleActions() {
      this.dropdownVisibleIndex = null;
    }
  },

  async mounted() {
    this.currentWidth = this.$refs.buttonBarNav?.offsetWidth;
    this.setResponsiveSize();
    window.addEventListener('resize', debounce_1(this.setResponsiveSize, 500));
    this.apiActions = this.parseApiActions(await this.getApiActions(this.apiUrl));
  },

  beforeDestroy() {
    window.removeEventListener('resize', debounce_1);
  },

  methods: {
    mapActionsToDropdownGroup(actions) {
      return actions.flatMap((action, index, actionsSelf) => {
        if (action.options) {
          const { label, options } = action;

          const groups = options.filter(option => option.group)
            .map((group, groupsIndex, groupsSelf) => ({
              ...group,
              divider: !groupsSelf[groupsIndex + 1]?.group && !actionsSelf[index + 1]?.options
            }));

          const actionOptions = {
            group: {
              label,
              items: options.filter(option => !option.group)
            },
            divider: !groups.length && !actionsSelf[index + 1]?.options
          };

          return [actionOptions, ...groups]
        }

        return action
      })
    },

    async getApiActions(apiUrl) {
      if (!apiUrl) return []

      try {
        const res = await fetch(apiUrl);
        return await res.json()
      } catch (error) {
        this.$meToast.error({
          content: this.$toolkit.t('buttonBar.apiActionsError')
        });
        return []
      }
    },

    parseApiActions(actions) {
      return actions.map((action) => {
        if (!action.form) return action

        return {
          ...action,
          click: () => {
            if (this.$refs[action.label] && this.$refs[action.label][0]) {
              this.$refs[action.label][0].show();
            }
          }
        }
      })
    },

    setResponsiveSize(_event, size = maxVisibleActions) {
      const currentSize = Math.min(this.allActions?.length, size);

      if (currentSize < 0) {
        this.responsiveSize = 0;
        return
      }

      if (currentSize === maxVisibleActions) {
        this.responsiveSize = this.getMaxResponsiveSize();
      }

      this.$nextTick(() => {
        if (this.$refs.buttonBarNav?.offsetWidth < this.$refs.buttonBarNav?.scrollWidth) {
          this.responsiveSize = this.getMaxSizeOrSize(this.maxSize, currentSize);
          this.setResponsiveSize(null, currentSize - 1);
        }
      });
    },

    getMaxResponsiveSize() {
      if (this.actions.length === maxVisibleActions) return this.getMaxSizeOrSize(this.maxSize, maxVisibleActions)

      if (this.actions.length > maxVisibleActions) return this.getMaxSizeOrSize(this.maxSize, maxVisibleActions - 1)
    },

    getMaxSizeOrSize(maxSize, size) {
      const hasMaxSize = maxSize || maxSize === 0;
      if (!hasMaxSize || maxSize > size) return size
      return maxSize
    },

    async formSubmited($event, modalRef) {
      if ($event.status === 200) {
        this.$refs[modalRef][0].hide();
        this.$meToast.success({
          content: this.$toolkit.t('buttonBar.success')
        });
        this.apiActions = this.parseApiActions(await this.getApiActions(this.apiUrl));
      }

      if ($event.status !== 200) {
        this.$meToast.error({
          content: this.$toolkit.t('buttonBar.error')
        });
      }
    }
  }
};

/* script */
const __vue_script__$G = script$G;

/* template */
var __vue_render__$G = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "nav",
    { ref: "buttonBarNav", staticClass: "me-button-bar d-flex" },
    [
      _vm._l(_vm.visibleActions, function(action, index) {
        return [
          action.options && action.options.length
            ? [
                _c(
                  "div",
                  {
                    key: action.label + action.href,
                    staticClass: "me-button-bar__action",
                    attrs: { "data-cy": "buttonBarAction" }
                  },
                  [
                    _c("me-drop-down", {
                      directives: [
                        {
                          name: "me-tooltip",
                          rawName: "v-me-tooltip",
                          value: {
                            hover: true,
                            placement: action.placement || "bottom",
                            html: action.descriptionHTML,
                            title:
                              _vm.dropdownVisibleIndex === index
                                ? ""
                                : action.description
                          },
                          expression:
                            "{\n            hover: true,\n            placement: action.placement || 'bottom',\n            html: action.descriptionHTML,\n            title: dropdownVisibleIndex === index ? '' : action.description\n          }"
                        }
                      ],
                      attrs: {
                        id: (action.popover && action.popover.id) || null,
                        items: action.options,
                        disabled: action.disabled || false,
                        block: "",
                        variant: "light-primary"
                      },
                      on: {
                        show: function($event) {
                          _vm.dropdownVisibleIndex = index;
                        },
                        hide: function($event) {
                          _vm.dropdownVisibleIndex = null;
                        }
                      },
                      scopedSlots: _vm._u(
                        [
                          {
                            key: "button-content",
                            fn: function() {
                              return [
                                action.icon
                                  ? _c("MeIcon", {
                                      staticStyle: { "margin-right": "8px" },
                                      attrs: { icon: action.icon }
                                    })
                                  : _vm._e(),
                                _vm._v(
                                  "\n            " +
                                    _vm._s(action.label) +
                                    "\n          "
                                )
                              ]
                            },
                            proxy: true
                          }
                        ],
                        null,
                        true
                      )
                    })
                  ],
                  1
                )
              ]
            : [
                _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value: {
                          hover: true,
                          placement: action.placement || "bottom",
                          html: action.descriptionHTML,
                          title:
                            _vm.dropdownVisibleIndex === index
                              ? ""
                              : action.description
                        },
                        expression:
                          "{\n          hover: true,\n          placement: action.placement || 'bottom',\n          html: action.descriptionHTML,\n          title: dropdownVisibleIndex === index ? '' : action.description\n        }"
                      }
                    ],
                    key: action.label + action.href,
                    staticClass: "me-button-bar__action",
                    attrs: {
                      id: (action.popover && action.popover.id) || null,
                      "data-jest": "button-bar-action-btn",
                      "data-cy": "buttonBarAction"
                    }
                  },
                  [
                    _c(
                      "me-button",
                      {
                        class: _vm.clazz(index),
                        attrs: {
                          href: action.href || action.location,
                          target: action.location ? "_blank" : "_self",
                          variant: action.style || "light-primary",
                          disabled: action.disabled || false,
                          busy: action.busy,
                          prependIcon: action.icon
                        },
                        on: {
                          click: function($event) {
                            action.click ? action.click() : null;
                          }
                        }
                      },
                      [
                        action.label
                          ? _c("span", [
                              _vm._v(" " + _vm._s(action.label) + " ")
                            ])
                          : _vm._e()
                      ]
                    )
                  ],
                  1
                )
              ],
          _vm._v(" "),
          _vm.dropdownVisibleIndex !== index
            ? [
                action && action.popover && action.popover.id
                  ? _c(
                      "me-popover",
                      {
                        key: action.popover.id,
                        attrs: {
                          disabled: action.popover.disabled,
                          target: action.popover.id,
                          placement: action.popover.placement || "bottom",
                          triggers: action.popover.triggers || "hover"
                        },
                        scopedSlots: _vm._u(
                          [
                            {
                              key: "title",
                              fn: function() {
                                return [
                                  _vm._t(
                                    "popover-title",
                                    [
                                      action.popover.title
                                        ? _c(
                                            "div",
                                            {
                                              staticClass:
                                                "d-flex justify-content-between"
                                            },
                                            [
                                              _c(
                                                "span",
                                                { staticClass: "mr-2" },
                                                [
                                                  _vm._v(
                                                    _vm._s(action.popover.title)
                                                  )
                                                ]
                                              )
                                            ]
                                          )
                                        : _vm._e()
                                    ],
                                    { popover: action.popover }
                                  )
                                ]
                              },
                              proxy: true
                            }
                          ],
                          null,
                          true
                        )
                      },
                      [
                        _vm._v(" "),
                        _vm._t(
                          "popover-content",
                          [
                            action.popover.content
                              ? _c("div", [
                                  _vm._v(
                                    "\n            " +
                                      _vm._s(action.popover.content) +
                                      "\n          "
                                  )
                                ])
                              : _vm._e()
                          ],
                          { popover: action.popover }
                        )
                      ],
                      2
                    )
                  : _vm._e()
              ]
            : _vm._e()
        ]
      }),
      _vm._v(" "),
      _vm.moreActions.length
        ? _c(
            "div",
            {
              staticClass: "me-button-bar__action",
              attrs: { "data-cy": "buttonBarMoreActions" }
            },
            [
              _c("me-drop-down", {
                attrs: {
                  items: _vm.moreActions,
                  variant: "light-primary",
                  block: ""
                },
                scopedSlots: _vm._u(
                  [
                    {
                      key: "button-content",
                      fn: function() {
                        return [
                          _c("MeIcon", {
                            staticStyle: { "margin-right": "8px" },
                            attrs: { icon: "me-icon icon-ellipsis" }
                          }),
                          _vm._v(
                            "\n        " +
                              _vm._s(_vm.$toolkit.t("buttonBar.moreOptions")) +
                              "\n      "
                          )
                        ]
                      },
                      proxy: true
                    }
                  ],
                  null,
                  false,
                  3616335089
                )
              })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.options.length > 0
        ? _c(
            "div",
            {
              staticClass: "me-button-bar__options",
              attrs: { "data-cy": "buttonBarOptions" }
            },
            [
              _c("me-drop-down", {
                attrs: {
                  items: _vm.options,
                  text: _vm.$toolkit.t("buttonBar.options"),
                  variant: "light-primary",
                  "menu-right": ""
                }
              })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _vm._l(_vm.allActions, function(action) {
        return [
          action.form
            ? _c(
                "me-modal",
                {
                  key: action.label,
                  ref: action.label,
                  refInFor: true,
                  attrs: {
                    id: action.label,
                    title: action.confirmation || action.label,
                    "hide-footer": "",
                    "no-backdrop": "",
                    "no-keyboard": "",
                    centered: ""
                  }
                },
                [
                  _c(
                    "template",
                    { slot: "default" },
                    [
                      _c("ButtonBarForm", {
                        attrs: { formFields: action.form, url: action.url },
                        on: {
                          submit: function($event) {
                            return _vm.formSubmited($event, action.label)
                          }
                        }
                      })
                    ],
                    1
                  )
                ],
                2
              )
            : _vm._e()
        ]
      })
    ],
    2
  )
};
var __vue_staticRenderFns__$G = [];
__vue_render__$G._withStripped = true;

  /* style */
  const __vue_inject_styles__$G = function (inject) {
    if (!inject) return
    inject("data-v-a348864a_0", { source: ".me-button-bar[data-v-a348864a] {\n  height: 100%;\n  display: flex;\n  align-items: center;\n}\n.me-button-bar__action[data-v-a348864a] {\n  white-space: nowrap;\n  margin-left: 16px;\n  min-width: 120px;\n}\n.me-button-bar__action[data-v-a348864a]:first-of-type {\n  margin-left: 0;\n}\n.me-button-bar__options[data-v-a348864a] {\n  margin-left: auto;\n}\n\n/*# sourceMappingURL=ButtonBar.vue.map */", map: {"version":3,"sources":["/app/build/src/components/ButtonBar.vue","ButtonBar.vue"],"names":[],"mappings":"AAqVA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;ACpVA;ADsVA;EACA,mBAAA;EACA,iBAAA;EACA,gBAAA;ACpVA;ADsVA;EACA,cAAA;ACpVA;ADwVA;EACA,iBAAA;ACtVA;;AAEA,wCAAwC","file":"ButtonBar.vue","sourcesContent":["<template>\n  <nav ref=\"buttonBarNav\" class=\"me-button-bar d-flex\">\n    <template v-for=\"(action, index) in visibleActions\">\n      <template v-if=\"action.options && action.options.length\">\n        <div\n          :key=\"action.label + action.href\"\n          class=\"me-button-bar__action\"\n          data-cy=\"buttonBarAction\"\n        >\n          <me-drop-down\n            :id=\"(action.popover && action.popover.id) || null\"\n            :items=\"action.options\"\n            :disabled=\"action.disabled || false\"\n            @show=\"dropdownVisibleIndex = index\"\n            @hide=\"dropdownVisibleIndex = null\"\n            v-me-tooltip=\"{\n              hover: true,\n              placement: action.placement || 'bottom',\n              html: action.descriptionHTML,\n              title: dropdownVisibleIndex === index ? '' : action.description\n            }\"\n            block\n            variant=\"light-primary\"\n          >\n            <template v-slot:button-content>\n              <MeIcon\n                :icon=\"action.icon\"\n                v-if=\"action.icon\"\n                style=\"margin-right: 8px\"\n              />\n              {{ action.label }}\n            </template>\n          </me-drop-down>\n        </div>\n      </template>\n\n      <template v-else>\n        <div\n          :key=\"action.label + action.href\"\n          :id=\"(action.popover && action.popover.id) || null\"\n          v-me-tooltip=\"{\n            hover: true,\n            placement: action.placement || 'bottom',\n            html: action.descriptionHTML,\n            title: dropdownVisibleIndex === index ? '' : action.description\n          }\"\n          data-jest=\"button-bar-action-btn\"\n          data-cy=\"buttonBarAction\"\n          class=\"me-button-bar__action\"\n        >\n          <me-button\n            :href=\"action.href || action.location\"\n            :target=\"action.location ? '_blank' : '_self'\"\n            @click=\"action.click ? action.click() : null\"\n            :class=\"clazz(index)\"\n            :variant=\"action.style || 'light-primary'\"\n            :disabled=\"action.disabled || false\"\n            :busy=\"action.busy\"\n            :prependIcon=\"action.icon\"\n          >\n            <span v-if=\"action.label\"> {{ action.label }} </span>\n          </me-button>\n        </div>\n      </template>\n\n      <template v-if=\"dropdownVisibleIndex !== index\">\n        <me-popover\n          v-if=\"action && action.popover && action.popover.id\"\n          :key=\"action.popover.id\"\n          :disabled=\"action.popover.disabled\"\n          :target=\"action.popover.id\"\n          :placement=\"action.popover.placement || 'bottom'\"\n          :triggers=\"action.popover.triggers || 'hover'\"\n        >\n          <template v-slot:title>\n            <slot :popover=\"action.popover\" name=\"popover-title\">\n              <div\n                v-if=\"action.popover.title\"\n                class=\"d-flex justify-content-between\"\n              >\n                <span class=\"mr-2\">{{ action.popover.title }}</span>\n              </div>\n            </slot>\n          </template>\n\n          <slot :popover=\"action.popover\" name=\"popover-content\">\n            <div v-if=\"action.popover.content\">\n              {{ action.popover.content }}\n            </div>\n          </slot>\n        </me-popover>\n      </template>\n    </template>\n\n    <div\n      v-if=\"moreActions.length\"\n      class=\"me-button-bar__action\"\n      data-cy=\"buttonBarMoreActions\"\n    >\n      <me-drop-down\n        :items=\"moreActions\"\n        variant=\"light-primary\"\n        block\n      >\n        <template #button-content>\n          <MeIcon\n            icon=\"me-icon icon-ellipsis\"\n            style=\"margin-right: 8px\"\n          />\n          {{ $toolkit.t('buttonBar.moreOptions') }}\n        </template>\n      </me-drop-down>\n    </div>\n\n    <div\n      v-if=\"options.length > 0\"\n      class=\"me-button-bar__options\"\n      data-cy=\"buttonBarOptions\"\n    >\n      <me-drop-down\n        :items=\"options\"\n        :text=\"$toolkit.t('buttonBar.options')\"\n        variant=\"light-primary\"\n        menu-right\n      />\n    </div>\n\n    <template v-for=\"action in allActions\">\n      <me-modal\n        :ref=\"action.label\"\n        v-if=\"action.form\"\n        :id=\"action.label\"\n        :key=\"action.label\"\n        :title=\"action.confirmation || action.label\"\n        hide-footer\n        no-backdrop\n        no-keyboard\n        centered\n      >\n        <template slot=\"default\">\n          <ButtonBarForm\n            :formFields=\"action.form\"\n            :url=\"action.url\"\n            @submit=\"formSubmited($event, action.label)\"\n          />\n        </template>\n      </me-modal>\n    </template>\n  </nav>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport ButtonBarForm from './ButtonBarForm.vue'\nimport MeIcon from './Icon.vue'\n\nconst maxVisibleActions = 4\n\nexport default {\n  name: 'MeButtonBar',\n  components: {\n    ButtonBarForm,\n    MeIcon\n  },\n  props: {\n    actions: {\n      type: Array,\n      default: () => []\n    },\n    options: {\n      type: Array,\n      default: () => []\n    },\n    size: {\n      type: Number,\n      default: 0\n    },\n    maxSize: {\n      type: Number,\n      default: null\n    },\n    apiUrl: {\n      type: String,\n      default: ''\n    }\n  },\n\n  data() {\n    return {\n      dropdownVisibleIndex: null,\n      currentWidth: 0,\n      responsiveSize: maxVisibleActions,\n      apiActions: []\n    }\n  },\n\n  computed: {\n    allActions() {\n      return [...this.apiActions, ...this.actions]\n    },\n\n    visibleActions() {\n      return this.allActions.slice(0, this.size || this.responsiveSize)\n    },\n\n    moreActions() {\n      const actionsFiltered = this.allActions.slice(this.size || this.responsiveSize, this.allActions.length)\n\n      return this.mapActionsToDropdownGroup(actionsFiltered)\n    },\n\n    clazz() {\n      return actionIndex => `mr-0 ${(this.allActions[actionIndex] && this.allActions[actionIndex].style) || 'text-decoration-none'}`\n    }\n  },\n\n  watch: {\n    visibleActions() {\n      this.dropdownVisibleIndex = null\n    }\n  },\n\n  async mounted() {\n    this.currentWidth = this.$refs.buttonBarNav?.offsetWidth\n    this.setResponsiveSize()\n    window.addEventListener('resize', debounce(this.setResponsiveSize, 500))\n    this.apiActions = this.parseApiActions(await this.getApiActions(this.apiUrl))\n  },\n\n  beforeDestroy() {\n    window.removeEventListener('resize', debounce)\n  },\n\n  methods: {\n    mapActionsToDropdownGroup(actions) {\n      return actions.flatMap((action, index, actionsSelf) => {\n        if (action.options) {\n          const { label, options } = action\n\n          const groups = options.filter(option => option.group)\n            .map((group, groupsIndex, groupsSelf) => ({\n              ...group,\n              divider: !groupsSelf[groupsIndex + 1]?.group && !actionsSelf[index + 1]?.options\n            }))\n\n          const actionOptions = {\n            group: {\n              label,\n              items: options.filter(option => !option.group)\n            },\n            divider: !groups.length && !actionsSelf[index + 1]?.options\n          }\n\n          return [actionOptions, ...groups]\n        }\n\n        return action\n      })\n    },\n\n    async getApiActions(apiUrl) {\n      if (!apiUrl) return []\n\n      try {\n        const res = await fetch(apiUrl)\n        return await res.json()\n      } catch (error) {\n        this.$meToast.error({\n          content: this.$toolkit.t('buttonBar.apiActionsError')\n        })\n        return []\n      }\n    },\n\n    parseApiActions(actions) {\n      return actions.map((action) => {\n        if (!action.form) return action\n\n        return {\n          ...action,\n          click: () => {\n            if (this.$refs[action.label] && this.$refs[action.label][0]) {\n              this.$refs[action.label][0].show()\n            }\n          }\n        }\n      })\n    },\n\n    setResponsiveSize(_event, size = maxVisibleActions) {\n      const currentSize = Math.min(this.allActions?.length, size)\n\n      if (currentSize < 0) {\n        this.responsiveSize = 0\n        return\n      }\n\n      if (currentSize === maxVisibleActions) {\n        this.responsiveSize = this.getMaxResponsiveSize()\n      }\n\n      this.$nextTick(() => {\n        if (this.$refs.buttonBarNav?.offsetWidth < this.$refs.buttonBarNav?.scrollWidth) {\n          this.responsiveSize = this.getMaxSizeOrSize(this.maxSize, currentSize)\n          this.setResponsiveSize(null, currentSize - 1)\n        }\n      })\n    },\n\n    getMaxResponsiveSize() {\n      if (this.actions.length === maxVisibleActions) return this.getMaxSizeOrSize(this.maxSize, maxVisibleActions)\n\n      if (this.actions.length > maxVisibleActions) return this.getMaxSizeOrSize(this.maxSize, maxVisibleActions - 1)\n    },\n\n    getMaxSizeOrSize(maxSize, size) {\n      const hasMaxSize = maxSize || maxSize === 0\n      if (!hasMaxSize || maxSize > size) return size\n      return maxSize\n    },\n\n    async formSubmited($event, modalRef) {\n      if ($event.status === 200) {\n        this.$refs[modalRef][0].hide()\n        this.$meToast.success({\n          content: this.$toolkit.t('buttonBar.success')\n        })\n        this.apiActions = this.parseApiActions(await this.getApiActions(this.apiUrl))\n      }\n\n      if ($event.status !== 200) {\n        this.$meToast.error({\n          content: this.$toolkit.t('buttonBar.error')\n        })\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-button-bar {\n    height: 100%;\n    display: flex;\n    align-items: center;\n\n    &__action {\n      white-space: nowrap;\n      margin-left: 16px;\n      min-width: 120px;\n\n      &:first-of-type {\n        margin-left: 0;\n      }\n    }\n\n    &__options {\n      margin-left: auto;\n    }\n  }\n</style>\n",".me-button-bar {\n  height: 100%;\n  display: flex;\n  align-items: center;\n}\n.me-button-bar__action {\n  white-space: nowrap;\n  margin-left: 16px;\n  min-width: 120px;\n}\n.me-button-bar__action:first-of-type {\n  margin-left: 0;\n}\n.me-button-bar__options {\n  margin-left: auto;\n}\n\n/*# sourceMappingURL=ButtonBar.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$G = "data-v-a348864a";
  /* module identifier */
  const __vue_module_identifier__$G = undefined;
  /* functional template */
  const __vue_is_functional_template__$G = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$G, staticRenderFns: __vue_staticRenderFns__$G },
    __vue_inject_styles__$G,
    __vue_script__$G,
    __vue_scope_id__$G,
    __vue_is_functional_template__$G,
    __vue_module_identifier__$G,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$H = {
  name: 'MeCard',
  props: {
    appearance: {
      type: String,
      default: 'default'
    },
    href: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      link: this.href
    }
  },
  computed: {
    clazz() {
      const classPrefix = 'me__card';
      return `${classPrefix} ${(
        this.appearance && `${classPrefix}--${this.appearance}`
      )} ${(
        this.link && `${classPrefix}--withlink`
      )}`
    }
  }
};

/* script */
const __vue_script__$H = script$H;

/* template */
var __vue_render__$H = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _vm.href
      ? _c(
          "a",
          { class: _vm.clazz, attrs: { href: _vm.link } },
          [_vm._t("default")],
          2
        )
      : _c("div", { class: _vm.clazz }, [_vm._t("default")], 2)
  ])
};
var __vue_staticRenderFns__$H = [];
__vue_render__$H._withStripped = true;

  /* style */
  const __vue_inject_styles__$H = function (inject) {
    if (!inject) return
    inject("data-v-62ccfc3d_0", { source: ".me__card[data-v-62ccfc3d] {\n  background-color: var(--me-white);\n  border-radius: 4px;\n  box-shadow: var(--me-shadow-level-1);\n  box-sizing: border-box;\n  display: inline-flex;\n  margin: 10px;\n  padding: 6px;\n  position: relative;\n  transition: 0.2s all ease-in-out;\n  width: -webkit-fill-available;\n}\n.me__card.me__card--left-border[data-v-62ccfc3d] {\n  padding-left: 14px;\n}\n.me__card.me__card--left-border[data-v-62ccfc3d]::before {\n  background-color: var(--me-primary-2);\n  content: \"\";\n  display: block;\n  height: 60%;\n  left: 0;\n  position: absolute;\n  top: 20%;\n  width: 4px;\n}\n.me__card.me__card--withlink[data-v-62ccfc3d] {\n  border: 1px solid rgba(0, 0, 0, 0.12);\n  color: var(--me-gray-0);\n  text-decoration: none;\n}\n.me__card.me__card--withlink[data-v-62ccfc3d]:hover {\n  border: solid 1px rgba(10, 140, 204, 0.74);\n  box-shadow: var(--me-shadow-level-2);\n  text-decoration: none;\n}\n\n/*# sourceMappingURL=Card.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Card.vue","Card.vue"],"names":[],"mappings":"AAkDA;EACA,iCAAA;EACA,kBAAA;EACA,oCAAA;EACA,sBAAA;EACA,oBAAA;EACA,YAAA;EACA,YAAA;EACA,kBAAA;EACA,gCAAA;EACA,6BAAA;ACjDA;ADmDA;EACA,kBAAA;ACjDA;ADmDA;EACA,qCAAA;EACA,WAAA;EACA,cAAA;EACA,WAAA;EACA,OAAA;EACA,kBAAA;EACA,QAAA;EACA,UAAA;ACjDA;ADqDA;EACA,qCAAA;EACA,uBAAA;EACA,qBAAA;ACnDA;ADqDA;EACA,0CAAA;EACA,oCAAA;EACA,qBAAA;ACnDA;;AAEA,mCAAmC","file":"Card.vue","sourcesContent":["<template>\n  <div>\n    <a\n      v-if=\"href\"\n      :class=\"clazz\"\n      :href=\"link\"\n    >\n      <slot />\n    </a>\n    <div\n      v-else\n      :class=\"clazz\"\n    >\n      <slot />\n    </div>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeCard',\n  props: {\n    appearance: {\n      type: String,\n      default: 'default'\n    },\n    href: {\n      type: String,\n      default: ''\n    }\n  },\n  data() {\n    return {\n      link: this.href\n    }\n  },\n  computed: {\n    clazz() {\n      const classPrefix = 'me__card'\n      return `${classPrefix} ${(\n        this.appearance && `${classPrefix}--${this.appearance}`\n      )} ${(\n        this.link && `${classPrefix}--withlink`\n      )}`\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me__card {\n    background-color: var(--me-white);\n    border-radius: 4px;\n    box-shadow: var(--me-shadow-level-1);\n    box-sizing: border-box;\n    display: inline-flex;\n    margin: 10px;\n    padding: 6px;\n    position: relative;\n    transition: .2s all ease-in-out;\n    width: -webkit-fill-available;\n\n    &.me__card--left-border {\n      padding-left: 14px;\n\n      &::before {\n        background-color: var(--me-primary-2);\n        content: '';\n        display: block;\n        height: 60%;\n        left: 0;\n        position: absolute;\n        top: 20%;\n        width: 4px;\n      }\n    }\n\n    &.me__card--withlink {\n      border: 1px solid rgba(0, 0, 0, 0.12);\n      color: var(--me-gray-0);\n      text-decoration: none;\n\n      &:hover {\n        border: solid 1px rgba(10, 140, 204, 0.74);\n        box-shadow: var(--me-shadow-level-2);\n        text-decoration: none;\n      }\n    }\n  }\n</style>\n",".me__card {\n  background-color: var(--me-white);\n  border-radius: 4px;\n  box-shadow: var(--me-shadow-level-1);\n  box-sizing: border-box;\n  display: inline-flex;\n  margin: 10px;\n  padding: 6px;\n  position: relative;\n  transition: 0.2s all ease-in-out;\n  width: -webkit-fill-available;\n}\n.me__card.me__card--left-border {\n  padding-left: 14px;\n}\n.me__card.me__card--left-border::before {\n  background-color: var(--me-primary-2);\n  content: \"\";\n  display: block;\n  height: 60%;\n  left: 0;\n  position: absolute;\n  top: 20%;\n  width: 4px;\n}\n.me__card.me__card--withlink {\n  border: 1px solid rgba(0, 0, 0, 0.12);\n  color: var(--me-gray-0);\n  text-decoration: none;\n}\n.me__card.me__card--withlink:hover {\n  border: solid 1px rgba(10, 140, 204, 0.74);\n  box-shadow: var(--me-shadow-level-2);\n  text-decoration: none;\n}\n\n/*# sourceMappingURL=Card.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$H = "data-v-62ccfc3d";
  /* module identifier */
  const __vue_module_identifier__$H = undefined;
  /* functional template */
  const __vue_is_functional_template__$H = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$H, staticRenderFns: __vue_staticRenderFns__$H },
    __vue_inject_styles__$H,
    __vue_script__$H,
    __vue_scope_id__$H,
    __vue_is_functional_template__$H,
    __vue_module_identifier__$H,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$I = {
  name: 'MeCarousel',
  components: {
    BCarousel,
    BCarouselSlide
  },
  props: {
    imageList: {
      type: Array,
      required: true
    },
    controls: {
      type: [Boolean, String],
      default: false
    },
    indicators: {
      type: [Boolean, String],
      default: false
    },
    interval: {
      type: Number,
      default: 5000
    },
    background: {
      type: String,
      default: 'var(--me-white)'
    },
    externalArrows: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    showControls() {
      return this.controls === true || this.controls === 'hover'
    },

    showIndicators() {
      return this.indicators === true || this.indicators === 'hover'
    }
  },

  methods: {
    onSlideStart(slide) {
      this.$emit('slide-start', slide);
    },
    onSlideEnd(slide) {
      this.$emit('slide-end', slide);
    }
  }
};

/* script */
const __vue_script__$I = script$I;

/* template */
var __vue_render__$I = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-caroussel",
      class: {
        "external-arrows": _vm.externalArrows,
        "controls-hover": _vm.controls === "hover",
        "indicators-hover": _vm.indicators === "hover"
      },
      attrs: { "data-cy": "me-carousel" }
    },
    [
      _c(
        "b-carousel",
        {
          attrs: {
            id: "carousel-1",
            interval: _vm.interval,
            controls: _vm.showControls,
            indicators: _vm.showIndicators,
            background: _vm.background
          },
          on: {
            "sliding-start": _vm.onSlideStart,
            "sliding-end": _vm.onSlideEnd
          }
        },
        _vm._l(_vm.imageList, function(image, index) {
          return _c("b-carousel-slide", {
            key: index,
            attrs: { "img-src": image.url }
          })
        }),
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$I = [];
__vue_render__$I._withStripped = true;

  /* style */
  const __vue_inject_styles__$I = function (inject) {
    if (!inject) return
    inject("data-v-a802231a_0", { source: "@charset \"UTF-8\";\n.me-caroussel[data-v-a802231a] {\n  height: 100%;\n  width: 100%;\n}\n.me-caroussel .carousel[data-v-a802231a] {\n  height: 100%;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-inner {\n  height: 100%;\n  display: flex;\n  align-items: center;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-item {\n  height: 100%;\n  text-align: center;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-item img {\n  display: unset !important;\n  object-fit: contain;\n  height: 100%;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-prev, .me-caroussel .carousel[data-v-a802231a]  .carousel-control-next {\n  z-index: unset;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-next {\n  right: 5px;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-prev {\n  left: 5px;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-prev,\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-next {\n  width: 27px;\n  opacity: unset;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-next .carousel-control-next-icon,\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-prev .carousel-control-prev-icon {\n  background: rgba(255, 255, 255, 0.9);\n  font-family: \"ME Icons\";\n  font-size: 20px;\n  color: #999999;\n  height: 27px;\n  width: 27px;\n  border-radius: 100%;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-prev .carousel-control-prev-icon::after {\n  content: \"\";\n  position: absolute;\n  right: 8px;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-control-next .carousel-control-next-icon::before {\n  content: \"\";\n  position: absolute;\n  left: 8px;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-indicators {\n  bottom: -36px;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-indicators li {\n  border-radius: 100%;\n  width: 8px;\n  height: 8px;\n  opacity: 1;\n  background-color: #eeeeee;\n}\n.me-caroussel .carousel[data-v-a802231a]  .carousel-indicators li.active {\n  background-color: #0a8ccc;\n}\n.me-caroussel.external-arrows[data-v-a802231a]  .carousel-inner {\n  margin: 0 auto;\n  width: calc(100% - 64px);\n  border: 1px solid #F9F9F9;\n}\n.me-caroussel.controls-hover[data-v-a802231a]  .carousel .carousel-control-prev, .me-caroussel.controls-hover[data-v-a802231a]  .carousel .carousel-control-next {\n  opacity: 0;\n  transition: all 0.3s ease-out;\n}\n.me-caroussel.controls-hover[data-v-a802231a]:hover  .carousel .carousel-control-prev, .me-caroussel.controls-hover[data-v-a802231a]:hover  .carousel .carousel-control-next {\n  opacity: 1;\n  transition: all 0.2s ease-in;\n}\n.me-caroussel.indicators-hover[data-v-a802231a]  .carousel .carousel-indicators {\n  opacity: 0;\n  transition: all 0.3s ease-out;\n}\n.me-caroussel.indicators-hover[data-v-a802231a]:hover  .carousel .carousel-indicators {\n  opacity: 1;\n  transition: all 0.2s ease-in;\n}\n\n/*# sourceMappingURL=Carousel.vue.map */", map: {"version":3,"sources":["Carousel.vue","/app/build/src/components/Carousel.vue"],"names":[],"mappings":"AAAA,gBAAgB;ACsFhB;EACA,YAAA;EACA,WAAA;ADpFA;ACsFA;EACA,YAAA;ADpFA;ACuFA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;ADrFA;ACwFA;EACA,YAAA;EACA,kBAAA;ADtFA;ACwFA;EACA,yBAAA;EACA,mBAAA;EACA,YAAA;ADtFA;AC0FA;EACA,cAAA;ADxFA;AC2FA;EACA,UAAA;ADzFA;AC4FA;EACA,SAAA;AD1FA;AC6FA;;EAEA,WAAA;EACA,cAAA;AD3FA;AC8FA;;EAEA,oCAAA;EACA,uBAAA;EACA,eAAA;EACA,cAAA;EACA,YAAA;EACA,WAAA;EACA,mBAAA;AD5FA;ACgGA;EACA,YAAA;EACA,kBAAA;EACA,UAAA;AD9FA;ACmGA;EACA,YAAA;EACA,kBAAA;EACA,SAAA;ADjGA;ACqGA;EACA,aAAA;ADnGA;ACqGA;EACA,mBAAA;EACA,UAAA;EACA,WAAA;EACA,UAAA;EACA,yBAAA;ADnGA;ACqGA;EACA,yBAAA;ADnGA;AC4GA;EACA,cAAA;EACA,wBAAA;EACA,yBAAA;ADzGA;ACiHA;EACA,UAAA;EACA,6BAAA;AD9GA;ACsHA;EACA,UAAA;EACA,4BAAA;ADpHA;AC8HA;EACA,UAAA;EACA,6BAAA;AD3HA;ACmIA;EACA,UAAA;EACA,4BAAA;ADjIA;;AAEA,uCAAuC","file":"Carousel.vue","sourcesContent":["@charset \"UTF-8\";\n.me-caroussel {\n  height: 100%;\n  width: 100%;\n}\n.me-caroussel .carousel {\n  height: 100%;\n}\n.me-caroussel .carousel ::v-deep .carousel-inner {\n  height: 100%;\n  display: flex;\n  align-items: center;\n}\n.me-caroussel .carousel ::v-deep .carousel-item {\n  height: 100%;\n  text-align: center;\n}\n.me-caroussel .carousel ::v-deep .carousel-item img {\n  display: unset !important;\n  object-fit: contain;\n  height: 100%;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-prev, .me-caroussel .carousel ::v-deep .carousel-control-next {\n  z-index: unset;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-next {\n  right: 5px;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-prev {\n  left: 5px;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-prev,\n.me-caroussel .carousel ::v-deep .carousel-control-next {\n  width: 27px;\n  opacity: unset;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-next .carousel-control-next-icon,\n.me-caroussel .carousel ::v-deep .carousel-control-prev .carousel-control-prev-icon {\n  background: rgba(255, 255, 255, 0.9);\n  font-family: \"ME Icons\";\n  font-size: 20px;\n  color: #999999;\n  height: 27px;\n  width: 27px;\n  border-radius: 100%;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-prev .carousel-control-prev-icon::after {\n  content: \"\";\n  position: absolute;\n  right: 8px;\n}\n.me-caroussel .carousel ::v-deep .carousel-control-next .carousel-control-next-icon::before {\n  content: \"\";\n  position: absolute;\n  left: 8px;\n}\n.me-caroussel .carousel ::v-deep .carousel-indicators {\n  bottom: -36px;\n}\n.me-caroussel .carousel ::v-deep .carousel-indicators li {\n  border-radius: 100%;\n  width: 8px;\n  height: 8px;\n  opacity: 1;\n  background-color: #eeeeee;\n}\n.me-caroussel .carousel ::v-deep .carousel-indicators li.active {\n  background-color: #0a8ccc;\n}\n\n.me-caroussel.external-arrows ::v-deep .carousel-inner {\n  margin: 0 auto;\n  width: calc(100% - 64px);\n  border: 1px solid #F9F9F9;\n}\n\n.me-caroussel.controls-hover ::v-deep .carousel .carousel-control-prev, .me-caroussel.controls-hover ::v-deep .carousel .carousel-control-next {\n  opacity: 0;\n  transition: all 0.3s ease-out;\n}\n.me-caroussel.controls-hover:hover ::v-deep .carousel .carousel-control-prev, .me-caroussel.controls-hover:hover ::v-deep .carousel .carousel-control-next {\n  opacity: 1;\n  transition: all 0.2s ease-in;\n}\n\n.me-caroussel.indicators-hover ::v-deep .carousel .carousel-indicators {\n  opacity: 0;\n  transition: all 0.3s ease-out;\n}\n.me-caroussel.indicators-hover:hover ::v-deep .carousel .carousel-indicators {\n  opacity: 1;\n  transition: all 0.2s ease-in;\n}\n\n/*# sourceMappingURL=Carousel.vue.map */","<template>\n  <div\n    :class=\"{\n      'external-arrows': externalArrows,\n      'controls-hover': controls === 'hover',\n      'indicators-hover': indicators === 'hover'\n    }\"\n    class=\"me-caroussel\"\n    data-cy=\"me-carousel\"\n  >\n    <b-carousel\n      id=\"carousel-1\"\n      :interval=\"interval\"\n      :controls=\"showControls\"\n      :indicators=\"showIndicators\"\n      :background=\"background\"\n      @sliding-start=\"onSlideStart\"\n      @sliding-end=\"onSlideEnd\"\n    >\n      <b-carousel-slide\n        v-for=\"(image, index) in imageList\"\n        :key=\"index\"\n        :img-src=\"image.url\"\n      />\n    </b-carousel>\n  </div>\n</template>\n\n<script>\nimport { BCarousel, BCarouselSlide } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeCarousel',\n  components: {\n    BCarousel,\n    BCarouselSlide\n  },\n  props: {\n    imageList: {\n      type: Array,\n      required: true\n    },\n    controls: {\n      type: [Boolean, String],\n      default: false\n    },\n    indicators: {\n      type: [Boolean, String],\n      default: false\n    },\n    interval: {\n      type: Number,\n      default: 5000\n    },\n    background: {\n      type: String,\n      default: 'var(--me-white)'\n    },\n    externalArrows: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  computed: {\n    showControls() {\n      return this.controls === true || this.controls === 'hover'\n    },\n\n    showIndicators() {\n      return this.indicators === true || this.indicators === 'hover'\n    }\n  },\n\n  methods: {\n    onSlideStart(slide) {\n      this.$emit('slide-start', slide)\n    },\n    onSlideEnd(slide) {\n      this.$emit('slide-end', slide)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-caroussel {\n    height: 100%;\n    width: 100%;\n\n    .carousel {\n      height: 100%;\n\n      ::v-deep {\n        .carousel-inner {\n          height: 100%;\n          display: flex;\n          align-items: center;\n        }\n\n        .carousel-item {\n          height: 100%;\n          text-align: center;\n\n          img {\n            display: unset !important;\n            object-fit: contain;\n            height: 100%;\n          }\n        }\n\n        .carousel-control-prev, .carousel-control-next {\n          z-index: unset;\n        }\n\n        .carousel-control-next {\n          right: 5px;\n        }\n\n        .carousel-control-prev {\n          left: 5px;\n        }\n\n        .carousel-control-prev,\n        .carousel-control-next {\n          width: 27px;\n          opacity: unset;\n        }\n\n        .carousel-control-next .carousel-control-next-icon,\n        .carousel-control-prev .carousel-control-prev-icon {\n          background: rgba(255, 255, 255, .9);\n          font-family: 'ME Icons';\n          font-size: 20px;\n          color: rgba(153, 153, 153, 1);\n          height: 27px;\n          width: 27px;\n          border-radius: 100%;\n        }\n\n        .carousel-control-prev .carousel-control-prev-icon {\n          &::after {\n            content: \"\\f2a5\";\n            position: absolute;\n            right: 8px;\n          }\n        }\n\n        .carousel-control-next .carousel-control-next-icon {\n          &::before {\n            content: \"\\f2a6\";\n            position: absolute;\n            left: 8px;\n          }\n        }\n\n        .carousel-indicators {\n          bottom: -36px;\n\n          li {\n            border-radius: 100%;\n            width: 8px;\n            height: 8px;\n            opacity: 1;\n            background-color: rgba(238, 238, 238, 1);\n\n            &.active {\n              background-color: rgba(10, 140, 204, 1);\n            }\n          }\n        }\n      }\n    }\n  }\n  .me-caroussel.external-arrows {\n    ::v-deep {\n      .carousel-inner {\n        margin: 0 auto;\n        width: calc(100% - 64px);\n        border: 1px solid #F9F9F9;\n      }\n    }\n  }\n\n  .me-caroussel.controls-hover {\n    ::v-deep {\n      .carousel {\n        .carousel-control-prev, .carousel-control-next {\n          opacity: 0;\n          transition: all .3s ease-out;\n        }\n      }\n    }\n\n    &:hover {\n      ::v-deep {\n        .carousel {\n          .carousel-control-prev, .carousel-control-next {\n            opacity: 1;\n            transition: all .2s ease-in;\n          }\n        }\n      }\n    }\n  }\n\n  .me-caroussel.indicators-hover {\n    ::v-deep {\n      .carousel {\n        .carousel-indicators {\n          opacity: 0;\n          transition: all .3s ease-out;\n        }\n      }\n    }\n\n    &:hover {\n      ::v-deep {\n        .carousel {\n          .carousel-indicators {\n            opacity: 1;\n            transition: all .2s ease-in;\n          }\n        }\n      }\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$I = "data-v-a802231a";
  /* module identifier */
  const __vue_module_identifier__$I = undefined;
  /* functional template */
  const __vue_is_functional_template__$I = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$I, staticRenderFns: __vue_staticRenderFns__$I },
    __vue_inject_styles__$I,
    __vue_script__$I,
    __vue_scope_id__$I,
    __vue_is_functional_template__$I,
    __vue_module_identifier__$I,
    false,
    createInjector,
    undefined,
    undefined
  );

/** `Object#toString` result references. */
var boolTag$4 = '[object Boolean]';

/**
 * Checks if `value` is classified as a boolean primitive or object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a boolean, else `false`.
 * @example
 *
 * _.isBoolean(false);
 * // => true
 *
 * _.isBoolean(null);
 * // => false
 */
function isBoolean$1(value) {
  return value === true || value === false ||
    (isObjectLike_1(value) && _baseGetTag(value) == boolTag$4);
}

var isBoolean_1 = isBoolean$1;

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$J = {
  name: 'MeInputMultipleContainer',
  props: {
    bordered: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      type: Boolean,
      default: null
    },
    focused: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    clazz() {
      return `input-multiple-container input-multiple-container-${this.size}`
    }
  }
};

/* script */
const __vue_script__$J = script$J;

/* template */
var __vue_render__$J = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      class: [
        _vm.clazz,
        {
          bordered: _vm.bordered,
          "is-valid": _vm.state,
          "is-invalid": _vm.state === false,
          focused: _vm.focused,
          disabled: _vm.disabled
        }
      ]
    },
    [_vm._t("content")],
    2
  )
};
var __vue_staticRenderFns__$J = [];
__vue_render__$J._withStripped = true;

  /* style */
  const __vue_inject_styles__$J = function (inject) {
    if (!inject) return
    inject("data-v-1f928dbb_0", { source: ".input-multiple-container[data-v-1f928dbb] {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  width: 100%;\n  display: flex;\n  background-color: #FFF;\n}\n.input-multiple-container.focused[data-v-1f928dbb] {\n  border-color: var(--me-primary-2);\n}\n.input-multiple-container.is-invalid[data-v-1f928dbb] {\n  border-color: var(--me-danger-2);\n}\n.input-multiple-container.disabled[data-v-1f928dbb] {\n  background-color: var(--me-gray-6);\n  pointer-events: none;\n  border: 1px solid var(--me-gray-4);\n}\n.input-multiple-container-sm[data-v-1f928dbb] {\n  min-height: 32px;\n}\n.input-multiple-container-md[data-v-1f928dbb] {\n  min-height: 40px;\n}\n.input-multiple-container-lg[data-v-1f928dbb] {\n  min-height: 48px;\n}\n.input-multiple-container[data-v-1f928dbb]:not(.bordered) {\n  border: none;\n}\n\n/*# sourceMappingURL=InputMultipleContainer.vue.map */", map: {"version":3,"sources":["/app/build/src/components/inputMultiple/InputMultipleContainer.vue","InputMultipleContainer.vue"],"names":[],"mappings":"AAoDA;EACA,kCAAA;EACA,kBAAA;EACA,WAAA;EACA,aAAA;EACA,sBAAA;ACnDA;ADqDA;EACA,iCAAA;ACnDA;ADsDA;EACA,gCAAA;ACpDA;ADuDA;EACA,kCAAA;EACA,oBAAA;EACA,kCAAA;ACrDA;ADwDA;EACA,gBAAA;ACtDA;ADyDA;EACA,gBAAA;ACvDA;AD0DA;EACA,gBAAA;ACxDA;AD2DA;EACA,YAAA;ACzDA;;AAEA,qDAAqD","file":"InputMultipleContainer.vue","sourcesContent":["<template>\n  <div\n    :class=\"[\n      clazz,\n      {\n        'bordered': bordered,\n        'is-valid': state,\n        'is-invalid': state === false,\n        'focused': focused,\n        'disabled': disabled\n      }\n    ]\"\n  >\n    <slot name=\"content\" />\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeInputMultipleContainer',\n  props: {\n    bordered: {\n      type: Boolean,\n      default: true\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    focused: {\n      type: Boolean,\n      default: false\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  computed: {\n    clazz() {\n      return `input-multiple-container input-multiple-container-${this.size}`\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .input-multiple-container {\n    border: 1px solid var(--me-gray-6);\n    border-radius: 8px;\n    width: 100%;\n    display: flex;\n    background-color: #FFF;\n\n    &.focused {\n      border-color: var(--me-primary-2);\n    }\n\n    &.is-invalid {\n      border-color: var(--me-danger-2 );\n    }\n\n    &.disabled {\n      background-color: var(--me-gray-6);\n      pointer-events: none;\n      border: 1px solid var(--me-gray-4);\n    }\n\n    &-sm {\n      min-height: 32px;\n    }\n\n    &-md {\n      min-height: 40px;\n    }\n\n    &-lg {\n      min-height: 48px;\n    }\n\n    &:not(.bordered) {\n      border: none;\n    }\n  }\n\n</style>\n",".input-multiple-container {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  width: 100%;\n  display: flex;\n  background-color: #FFF;\n}\n.input-multiple-container.focused {\n  border-color: var(--me-primary-2);\n}\n.input-multiple-container.is-invalid {\n  border-color: var(--me-danger-2);\n}\n.input-multiple-container.disabled {\n  background-color: var(--me-gray-6);\n  pointer-events: none;\n  border: 1px solid var(--me-gray-4);\n}\n.input-multiple-container-sm {\n  min-height: 32px;\n}\n.input-multiple-container-md {\n  min-height: 40px;\n}\n.input-multiple-container-lg {\n  min-height: 48px;\n}\n.input-multiple-container:not(.bordered) {\n  border: none;\n}\n\n/*# sourceMappingURL=InputMultipleContainer.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$J = "data-v-1f928dbb";
  /* module identifier */
  const __vue_module_identifier__$J = undefined;
  /* functional template */
  const __vue_is_functional_template__$J = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$J, staticRenderFns: __vue_staticRenderFns__$J },
    __vue_inject_styles__$J,
    __vue_script__$J,
    __vue_scope_id__$J,
    __vue_is_functional_template__$J,
    __vue_module_identifier__$J,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$K = {
  name: 'MeBadgesMultiple',
  components: {
    Badge: __vue_component__$x
  },

  props: {
    badge: {
      type: [String, Date],
      default: ''
    },

    variant: {
      type: String,
      default: 'light-default'
    },

    type: {
      type: String,
      default: 'text'
    },

    maxItemLength: {
      type: Number,
      default: 50
    }
  }
};

/* script */
const __vue_script__$K = script$K;

/* template */
var __vue_render__$K = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Badge",
    {
      staticClass: "badge-multiple",
      attrs: { appearance: _vm.variant, "data-jest": "badge-multiple" }
    },
    [
      _c(
        "span",
        { staticClass: "label" },
        [
          _vm.type === "date"
            ? [
                _vm._v(
                  "\n      " + _vm._s(_vm._f("me-date")(_vm.badge)) + "\n    "
                )
              ]
            : [
                _vm._v(
                  "\n      " +
                    _vm._s(
                      _vm._f("me-cut-text")(_vm.badge, _vm.maxItemLength)
                    ) +
                    "\n    "
                )
              ]
        ],
        2
      ),
      _vm._v(" "),
      _c("i", {
        staticClass: "me-icon-l icon-xmark",
        attrs: {
          "data-jest": "remove-badge-multiple",
          "data-cy": "remove-badge-multiple"
        },
        on: {
          click: function($event) {
            return _vm.$emit("removeItem", _vm.badge)
          }
        }
      })
    ]
  )
};
var __vue_staticRenderFns__$K = [];
__vue_render__$K._withStripped = true;

  /* style */
  const __vue_inject_styles__$K = function (inject) {
    if (!inject) return
    inject("data-v-114a946e_0", { source: ".badge-multiple[data-v-114a946e] {\n  margin-right: 8px;\n}\n.badge-multiple .label[data-v-114a946e] {\n  padding-right: 12px;\n}\n.badge-multiple i[data-v-114a946e] {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=BadgesMultiple.vue.map */", map: {"version":3,"sources":["/app/build/src/components/inputMultiple/BadgesMultiple.vue","BadgesMultiple.vue"],"names":[],"mappings":"AA4DA;EACA,iBAAA;AC3DA;AD6DA;EACA,mBAAA;AC3DA;AD8DA;EACA,eAAA;AC5DA;;AAEA,6CAA6C","file":"BadgesMultiple.vue","sourcesContent":["<template>\n  <Badge\n    :appearance=\"variant\"\n    class=\"badge-multiple\"\n    data-jest=\"badge-multiple\"\n  >\n    <span class=\"label\">\n      <template v-if=\"type === 'date'\">\n        {{ badge | me-date }}\n      </template>\n\n      <template v-else>\n        {{ badge | me-cut-text(maxItemLength) }}\n      </template>\n\n    </span>\n\n    <i\n      @click=\"$emit('removeItem', badge)\"\n      class=\"me-icon-l icon-xmark\"\n      data-jest=\"remove-badge-multiple\"\n      data-cy=\"remove-badge-multiple\"\n    />\n  </Badge>\n</template>\n\n<script>\nimport Badge from './../Badge.vue'\n\nexport default {\n  name: 'MeBadgesMultiple',\n  components: {\n    Badge\n  },\n\n  props: {\n    badge: {\n      type: [String, Date],\n      default: ''\n    },\n\n    variant: {\n      type: String,\n      default: 'light-default'\n    },\n\n    type: {\n      type: String,\n      default: 'text'\n    },\n\n    maxItemLength: {\n      type: Number,\n      default: 50\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .badge-multiple {\n    margin-right: 8px;\n\n    .label {\n      padding-right: 12px;\n    }\n\n    i {\n      cursor: pointer;\n    }\n  }\n</style>\n",".badge-multiple {\n  margin-right: 8px;\n}\n.badge-multiple .label {\n  padding-right: 12px;\n}\n.badge-multiple i {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=BadgesMultiple.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$K = "data-v-114a946e";
  /* module identifier */
  const __vue_module_identifier__$K = undefined;
  /* functional template */
  const __vue_is_functional_template__$K = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$K, staticRenderFns: __vue_staticRenderFns__$K },
    __vue_inject_styles__$K,
    __vue_script__$K,
    __vue_scope_id__$K,
    __vue_is_functional_template__$K,
    __vue_module_identifier__$K,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$L = {
  name: 'MeTextInputMultiple',
  components: {
    InputMultipleContainer: __vue_component__$J,
    BadgesMultiple: __vue_component__$K,
    TextInput: __vue_component__$p
  },

  props: {
    initialValues: {
      type: [String, Array],
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    values: {
      type: Array,
      default: () => ([])
    },
    maxItems: {
      type: Number,
      default: 0
    },
    maxItemLength: {
      type: Number,
      default: 50
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      type: Boolean,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'light-default'
    },
    type: {
      type: String,
      default: 'text'
    },
    splitDelimiter: {
      type: [String, RegExp],
      default: null
    }
  },

  data() {
    return {
      isFocused: false,
      current: this.value,
      items: this.value ? [...new Set([...this.values, this.value])] : this.values
    }
  },

  computed: {
    shouldShowInput() {
      if (!this.maxItems) return true
      return this.items?.length < this.maxItems
    }
  },

  watch: {
    value(val) {
      this.current = val;
    },

    values(val) {
      this.items = [...new Set([...val])];
    }
  },

  mounted() {
    if (typeof this.initialValues === 'string') {
      this.current = this.initialValues;
    }

    if (this.initialValues?.length) {
      this.items = this.initialValues;
    }
  },

  methods: {
    handleFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },

    handleBlur() {
      this.isFocused = false;
      this.$emit('blur');
    },

    addItem(newItem) {
      if (!newItem) return

      let inserted = newItem.trim();

      if (this.splitDelimiter) {
        inserted = newItem.split(this.splitDelimiter).filter(val => val);
      }
      this.items = [...new Set([...this.items.concat(inserted)])];
      this.$emit('itemAdded', { items: this.items, inserted });
      this.current = null;
    },

    removeItem(removedItem) {
      this.items = this.items.filter(item => item !== removedItem);
      this.$emit('itemRemoved', { items: this.items, removed: removedItem });
    }
  }
};

/* script */
const __vue_script__$L = script$L;

/* template */
var __vue_render__$L = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "InputMultipleContainer",
    {
      attrs: {
        bordered: _vm.bordered,
        size: _vm.size,
        state: _vm.state,
        disabled: _vm.disabled,
        focused: _vm.isFocused
      }
    },
    [
      _c("template", { slot: "content" }, [
        _c(
          "div",
          {
            staticClass: "text-input-multiple",
            class: { "with-badge": _vm.items && _vm.items.length }
          },
          [
            _vm._l(_vm.items, function(item) {
              return [
                _vm._t(
                  "items",
                  [
                    _c("BadgesMultiple", {
                      key: item,
                      attrs: {
                        badge: item,
                        variant: _vm.disabled ? "light" : _vm.variant,
                        type: _vm.type,
                        maxItemLength: _vm.maxItemLength
                      },
                      on: { removeItem: _vm.removeItem }
                    })
                  ],
                  { item: item }
                )
              ]
            }),
            _vm._v(" "),
            _vm._t("input", [
              _c("TextInput", {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.shouldShowInput,
                    expression: "shouldShowInput"
                  }
                ],
                attrs: {
                  disabled: _vm.disabled,
                  placeholder: _vm.placeholder,
                  size: _vm.size,
                  state: _vm.state,
                  type: _vm.type
                },
                on: {
                  enter: function($event) {
                    return _vm.addItem(_vm.current)
                  },
                  input: function($event) {
                    return _vm.$emit("input", _vm.current, _vm.items)
                  },
                  focus: _vm.handleFocus,
                  blur: _vm.handleBlur
                },
                model: {
                  value: _vm.current,
                  callback: function($$v) {
                    _vm.current = $$v;
                  },
                  expression: "current"
                }
              })
            ])
          ],
          2
        )
      ])
    ],
    2
  )
};
var __vue_staticRenderFns__$L = [];
__vue_render__$L._withStripped = true;

  /* style */
  const __vue_inject_styles__$L = function (inject) {
    if (!inject) return
    inject("data-v-76f2dc58_0", { source: ".text-input-multiple[data-v-76f2dc58] {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  align-items: center;\n}\n.text-input-multiple.with-badge[data-v-76f2dc58] {\n  margin-left: 8px;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group {\n  flex: 1;\n  min-width: 120px;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group-sm {\n  min-height: 30px;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group-md {\n  min-height: 38px;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group-lg {\n  min-height: 46px;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group .form-control {\n  border: none;\n  height: auto;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group .form-control:focus {\n  box-shadow: none;\n}\n.text-input-multiple[data-v-76f2dc58]  .input-group .form-control:disabled {\n  border: none;\n}\n\n/*# sourceMappingURL=TextInputMultiple.vue.map */", map: {"version":3,"sources":["/app/build/src/components/inputMultiple/TextInputMultiple.vue","TextInputMultiple.vue"],"names":[],"mappings":"AAsLA;EACA,aAAA;EACA,eAAA;EACA,WAAA;EACA,mBAAA;ACrLA;ADuLA;EACA,gBAAA;ACrLA;ADwLA;EACA,OAAA;EACA,gBAAA;ACtLA;ADwLA;EACA,gBAAA;ACtLA;ADyLA;EACA,gBAAA;ACvLA;AD0LA;EACA,gBAAA;ACxLA;AD2LA;EACA,YAAA;EACA,YAAA;ACzLA;AD2LA;EACA,gBAAA;ACzLA;AD4LA;EACA,YAAA;AC1LA;;AAEA,gDAAgD","file":"TextInputMultiple.vue","sourcesContent":["<template>\n  <InputMultipleContainer\n    :bordered=\"bordered\"\n    :size=\"size\"\n    :state=\"state\"\n    :disabled=\"disabled\"\n    :focused=\"isFocused\"\n  >\n    <template slot=\"content\">\n      <div\n        :class=\"{'with-badge': items && items.length}\"\n        class=\"text-input-multiple\"\n      >\n        <template v-for=\"item in items\">\n          <slot :item=\"item\" name=\"items\">\n            <BadgesMultiple\n              :key=\"item\"\n              :badge=\"item\"\n              :variant=\"disabled ? 'light' : variant\"\n              :type=\"type\"\n              :maxItemLength=\"maxItemLength\"\n              @removeItem=\"removeItem\"\n            />\n          </slot>\n        </template>\n\n        <slot name=\"input\">\n          <TextInput\n            v-model=\"current\"\n            v-show=\"shouldShowInput\"\n            :disabled=\"disabled\"\n            :placeholder=\"placeholder\"\n            :size=\"size\"\n            :state=\"state\"\n            :type=\"type\"\n            @enter=\"addItem(current)\"\n            @input=\"$emit('input', current, items)\"\n            @focus=\"handleFocus\"\n            @blur=\"handleBlur\"\n          />\n        </slot>\n      </div>\n    </template>\n  </InputMultipleContainer>\n</template>\n\n<script>\nimport InputMultipleContainer from './InputMultipleContainer.vue'\nimport BadgesMultiple from './BadgesMultiple.vue'\nimport TextInput from './../TextInput.vue'\n\nexport default {\n  name: 'MeTextInputMultiple',\n  components: {\n    InputMultipleContainer,\n    BadgesMultiple,\n    TextInput\n  },\n\n  props: {\n    initialValues: {\n      type: [String, Array],\n      default: ''\n    },\n    value: {\n      type: String,\n      default: ''\n    },\n    values: {\n      type: Array,\n      default: () => ([])\n    },\n    maxItems: {\n      type: Number,\n      default: 0\n    },\n    maxItemLength: {\n      type: Number,\n      default: 50\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    bordered: {\n      type: Boolean,\n      default: true\n    },\n    variant: {\n      type: String,\n      default: 'light-default'\n    },\n    type: {\n      type: String,\n      default: 'text'\n    },\n    splitDelimiter: {\n      type: [String, RegExp],\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      isFocused: false,\n      current: this.value,\n      items: this.value ? [...new Set([...this.values, this.value])] : this.values\n    }\n  },\n\n  computed: {\n    shouldShowInput() {\n      if (!this.maxItems) return true\n      return this.items?.length < this.maxItems\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    },\n\n    values(val) {\n      this.items = [...new Set([...val])]\n    }\n  },\n\n  mounted() {\n    if (typeof this.initialValues === 'string') {\n      this.current = this.initialValues\n    }\n\n    if (this.initialValues?.length) {\n      this.items = this.initialValues\n    }\n  },\n\n  methods: {\n    handleFocus() {\n      this.isFocused = true\n      this.$emit('focus')\n    },\n\n    handleBlur() {\n      this.isFocused = false\n      this.$emit('blur')\n    },\n\n    addItem(newItem) {\n      if (!newItem) return\n\n      let inserted = newItem.trim()\n\n      if (this.splitDelimiter) {\n        inserted = newItem.split(this.splitDelimiter).filter(val => val)\n      }\n      this.items = [...new Set([...this.items.concat(inserted)])]\n      this.$emit('itemAdded', { items: this.items, inserted })\n      this.current = null\n    },\n\n    removeItem(removedItem) {\n      this.items = this.items.filter(item => item !== removedItem)\n      this.$emit('itemRemoved', { items: this.items, removed: removedItem })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .text-input-multiple {\n    display: flex;\n    flex-wrap: wrap;\n    width: 100%;\n    align-items: center;\n\n    &.with-badge {\n      margin-left: 8px;\n    }\n\n    ::v-deep .input-group {\n      flex: 1;\n      min-width: 120px;\n\n      &-sm {\n        min-height: 30px;\n      }\n\n      &-md {\n        min-height: 38px;\n      }\n\n      &-lg {\n        min-height: 46px;\n      }\n\n      .form-control {\n        border: none;\n        height: auto;\n\n        &:focus {\n          box-shadow: none;\n        }\n\n        &:disabled {\n          border: none;\n        }\n      }\n    }\n  }\n</style>\n",".text-input-multiple {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  align-items: center;\n}\n.text-input-multiple.with-badge {\n  margin-left: 8px;\n}\n.text-input-multiple ::v-deep .input-group {\n  flex: 1;\n  min-width: 120px;\n}\n.text-input-multiple ::v-deep .input-group-sm {\n  min-height: 30px;\n}\n.text-input-multiple ::v-deep .input-group-md {\n  min-height: 38px;\n}\n.text-input-multiple ::v-deep .input-group-lg {\n  min-height: 46px;\n}\n.text-input-multiple ::v-deep .input-group .form-control {\n  border: none;\n  height: auto;\n}\n.text-input-multiple ::v-deep .input-group .form-control:focus {\n  box-shadow: none;\n}\n.text-input-multiple ::v-deep .input-group .form-control:disabled {\n  border: none;\n}\n\n/*# sourceMappingURL=TextInputMultiple.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$L = "data-v-76f2dc58";
  /* module identifier */
  const __vue_module_identifier__$L = undefined;
  /* functional template */
  const __vue_is_functional_template__$L = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$L, staticRenderFns: __vue_staticRenderFns__$L },
    __vue_inject_styles__$L,
    __vue_script__$L,
    __vue_scope_id__$L,
    __vue_is_functional_template__$L,
    __vue_module_identifier__$L,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$M = {
  name: 'MeDatePickerMultiple',
  components: {
    InputMultipleContainer: __vue_component__$J,
    BadgesMultiple: __vue_component__$K,
    DatePicker: __vue_component__$B
  },

  props: {
    initialValues: {
      type: [String, Date, Array],
      default: ''
    },
    value: {
      type: [String, Date],
      default: ''
    },
    values: {
      type: Array,
      default: () => ([])
    },
    maxItems: {
      type: Number,
      default: 0
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      type: Boolean,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'light-default'
    }
  },

  data() {
    return {
      isFocused: false,
      current: this.value,
      items: this.value ? [...this.values, this.value] : this.values
    }
  },

  computed: {
    itemsString() {
      return [...new Set(this.items.map(item => new Date(item).toString()))]
    },

    itemsDate() {
      return this.itemsString.map(item => new Date(item))
    },

    shouldShowInput() {
      if (!this.maxItems) return true
      return this.itemsString?.length < this.maxItems
    }
  },

  watch: {
    value(val) {
      this.current = val;
    },

    values(val) {
      this.items = val;
    }
  },

  mounted() {
    if (!Array.isArray(this.initialValues)) {
      this.current = this.initialValues;
    }

    if (this.initialValues?.length) {
      this.items = this.initialValues;
    }
  },

  methods: {
    handleFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },

    handleBlur() {
      this.isFocused = false;
      this.$emit('blur');
    },

    addItem(newItem) {
      if (!newItem) return

      this.items = [...this.items, newItem];
      this.$emit('itemAdded', { items: this.itemsDate, inserted: newItem });

      this.$refs.meDatePickerRef.clear();
    },

    removeItem(removedItem) {
      this.items = this.itemsString.filter(item => item !== removedItem.toString());
      this.$emit('itemRemoved', { items: this.itemsDate, removed: removedItem });
    }
  }
};

/* script */
const __vue_script__$M = script$M;

/* template */
var __vue_render__$M = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "InputMultipleContainer",
    {
      attrs: {
        bordered: _vm.bordered,
        size: _vm.size,
        state: _vm.state,
        focused: _vm.isFocused,
        disabled: _vm.disabled
      }
    },
    [
      _c("template", { slot: "content" }, [
        _c(
          "div",
          {
            staticClass: "date-picker-multiple",
            class: { "with-badge": _vm.itemsDate && _vm.itemsDate.length }
          },
          [
            _vm._l(_vm.itemsDate, function(item) {
              return [
                _vm._t(
                  "items",
                  [
                    _c("BadgesMultiple", {
                      key: item.toString(),
                      attrs: {
                        badge: item,
                        variant: _vm.disabled ? "light" : _vm.variant,
                        type: "date"
                      },
                      on: { removeItem: _vm.removeItem }
                    })
                  ],
                  { item: item }
                )
              ]
            }),
            _vm._v(" "),
            _vm._t("ipnut", [
              _c("DatePicker", {
                directives: [
                  {
                    name: "show",
                    rawName: "v-show",
                    value: _vm.shouldShowInput,
                    expression: "shouldShowInput"
                  }
                ],
                ref: "meDatePickerRef",
                attrs: {
                  disabled: _vm.disabled,
                  placeholder: _vm.placeholder,
                  size: _vm.size,
                  state: _vm.state,
                  type: "date"
                },
                on: {
                  input: _vm.addItem,
                  enter: _vm.addItem,
                  focus: _vm.handleFocus,
                  blur: _vm.handleBlur
                },
                model: {
                  value: _vm.current,
                  callback: function($$v) {
                    _vm.current = $$v;
                  },
                  expression: "current"
                }
              })
            ])
          ],
          2
        )
      ])
    ],
    2
  )
};
var __vue_staticRenderFns__$M = [];
__vue_render__$M._withStripped = true;

  /* style */
  const __vue_inject_styles__$M = function (inject) {
    if (!inject) return
    inject("data-v-a80fa048_0", { source: ".date-picker-multiple[data-v-a80fa048] {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  align-items: center;\n}\n.date-picker-multiple.with-badge[data-v-a80fa048] {\n  margin-left: 8px;\n}\n.date-picker-multiple .me-date-picker[data-v-a80fa048] {\n  flex: 1;\n}\n.date-picker-multiple .me-date-picker.sm[data-v-a80fa048]  .mx-datepicker input.mx-input, .date-picker-multiple .me-date-picker.md[data-v-a80fa048]  .mx-datepicker input.mx-input, .date-picker-multiple .me-date-picker.lg[data-v-a80fa048]  .mx-datepicker input.mx-input {\n  height: auto;\n}\n.date-picker-multiple .me-date-picker[data-v-a80fa048]  .mx-datepicker {\n  min-width: 120px;\n}\n.date-picker-multiple .me-date-picker[data-v-a80fa048]  .mx-datepicker input.mx-input {\n  border: none;\n  box-shadow: none;\n}\n.date-picker-multiple .me-date-picker[data-v-a80fa048]  .mx-datepicker input.mx-input:focus {\n  box-shadow: none;\n}\n.date-picker-multiple .me-date-picker[data-v-a80fa048]  .mx-datepicker input.mx-input:disabled {\n  border: none;\n}\n\n/*# sourceMappingURL=DatePickerMultiple.vue.map */", map: {"version":3,"sources":["/app/build/src/components/inputMultiple/DatePickerMultiple.vue","DatePickerMultiple.vue"],"names":[],"mappings":"AA8KA;EACA,aAAA;EACA,eAAA;EACA,WAAA;EACA,mBAAA;AC7KA;AD+KA;EACA,gBAAA;AC7KA;ADgLA;EACA,OAAA;AC9KA;ADoLA;EACA,YAAA;AClLA;ADuLA;EACA,gBAAA;ACrLA;ADuLA;EACA,YAAA;EACA,gBAAA;ACrLA;ADuLA;EACA,gBAAA;ACrLA;ADwLA;EACA,YAAA;ACtLA;;AAEA,iDAAiD","file":"DatePickerMultiple.vue","sourcesContent":["<template>\n  <InputMultipleContainer\n    :bordered=\"bordered\"\n    :size=\"size\"\n    :state=\"state\"\n    :focused=\"isFocused\"\n    :disabled=\"disabled\"\n  >\n    <template slot=\"content\">\n      <div\n        :class=\"{'with-badge': itemsDate && itemsDate.length}\"\n        class=\"date-picker-multiple\"\n      >\n        <template v-for=\"item in itemsDate\">\n          <slot :item=\"item\" name=\"items\">\n            <BadgesMultiple\n              :key=\"item.toString()\"\n              :badge=\"item\"\n              :variant=\"disabled ? 'light' : variant\"\n              @removeItem=\"removeItem\"\n              type=\"date\"\n            />\n          </slot>\n        </template>\n\n        <slot name=\"ipnut\">\n          <DatePicker\n            ref=\"meDatePickerRef\"\n            v-model=\"current\"\n            v-show=\"shouldShowInput\"\n            :disabled=\"disabled\"\n            :placeholder=\"placeholder\"\n            :size=\"size\"\n            :state=\"state\"\n            @input=\"addItem\"\n            @enter=\"addItem\"\n            @focus=\"handleFocus\"\n            @blur=\"handleBlur\"\n            type=\"date\"\n          />\n        </slot>\n      </div>\n    </template>\n  </InputMultipleContainer>\n</template>\n\n<script>\nimport InputMultipleContainer from './InputMultipleContainer.vue'\nimport BadgesMultiple from './BadgesMultiple.vue'\nimport DatePicker from './../DatePicker.vue'\n\nexport default {\n  name: 'MeDatePickerMultiple',\n  components: {\n    InputMultipleContainer,\n    BadgesMultiple,\n    DatePicker\n  },\n\n  props: {\n    initialValues: {\n      type: [String, Date, Array],\n      default: ''\n    },\n    value: {\n      type: [String, Date],\n      default: ''\n    },\n    values: {\n      type: Array,\n      default: () => ([])\n    },\n    maxItems: {\n      type: Number,\n      default: 0\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    bordered: {\n      type: Boolean,\n      default: true\n    },\n    variant: {\n      type: String,\n      default: 'light-default'\n    }\n  },\n\n  data() {\n    return {\n      isFocused: false,\n      current: this.value,\n      items: this.value ? [...this.values, this.value] : this.values\n    }\n  },\n\n  computed: {\n    itemsString() {\n      return [...new Set(this.items.map(item => new Date(item).toString()))]\n    },\n\n    itemsDate() {\n      return this.itemsString.map(item => new Date(item))\n    },\n\n    shouldShowInput() {\n      if (!this.maxItems) return true\n      return this.itemsString?.length < this.maxItems\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    },\n\n    values(val) {\n      this.items = val\n    }\n  },\n\n  mounted() {\n    if (!Array.isArray(this.initialValues)) {\n      this.current = this.initialValues\n    }\n\n    if (this.initialValues?.length) {\n      this.items = this.initialValues\n    }\n  },\n\n  methods: {\n    handleFocus() {\n      this.isFocused = true\n      this.$emit('focus')\n    },\n\n    handleBlur() {\n      this.isFocused = false\n      this.$emit('blur')\n    },\n\n    addItem(newItem) {\n      if (!newItem) return\n\n      this.items = [...this.items, newItem]\n      this.$emit('itemAdded', { items: this.itemsDate, inserted: newItem })\n\n      this.$refs.meDatePickerRef.clear()\n    },\n\n    removeItem(removedItem) {\n      this.items = this.itemsString.filter(item => item !== removedItem.toString())\n      this.$emit('itemRemoved', { items: this.itemsDate, removed: removedItem })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .date-picker-multiple {\n    display: flex;\n    flex-wrap: wrap;\n    width: 100%;\n    align-items: center;\n\n    &.with-badge {\n      margin-left: 8px\n    }\n\n    .me-date-picker {\n      flex: 1;\n\n      &.sm,\n      &.md,\n      &.lg {\n        ::v-deep .mx-datepicker {\n          input.mx-input {\n            height: auto;\n          }\n        }\n      }\n\n      ::v-deep .mx-datepicker {\n        min-width: 120px;\n\n        input.mx-input {\n          border: none;\n          box-shadow: none;\n\n          &:focus {\n            box-shadow: none;\n          }\n\n          &:disabled {\n            border: none;\n          }\n        }\n      }\n    }\n  }\n</style>\n",".date-picker-multiple {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  align-items: center;\n}\n.date-picker-multiple.with-badge {\n  margin-left: 8px;\n}\n.date-picker-multiple .me-date-picker {\n  flex: 1;\n}\n.date-picker-multiple .me-date-picker.sm ::v-deep .mx-datepicker input.mx-input, .date-picker-multiple .me-date-picker.md ::v-deep .mx-datepicker input.mx-input, .date-picker-multiple .me-date-picker.lg ::v-deep .mx-datepicker input.mx-input {\n  height: auto;\n}\n.date-picker-multiple .me-date-picker ::v-deep .mx-datepicker {\n  min-width: 120px;\n}\n.date-picker-multiple .me-date-picker ::v-deep .mx-datepicker input.mx-input {\n  border: none;\n  box-shadow: none;\n}\n.date-picker-multiple .me-date-picker ::v-deep .mx-datepicker input.mx-input:focus {\n  box-shadow: none;\n}\n.date-picker-multiple .me-date-picker ::v-deep .mx-datepicker input.mx-input:disabled {\n  border: none;\n}\n\n/*# sourceMappingURL=DatePickerMultiple.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$M = "data-v-a80fa048";
  /* module identifier */
  const __vue_module_identifier__$M = undefined;
  /* functional template */
  const __vue_is_functional_template__$M = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$M, staticRenderFns: __vue_staticRenderFns__$M },
    __vue_inject_styles__$M,
    __vue_script__$M,
    __vue_scope_id__$M,
    __vue_is_functional_template__$M,
    __vue_module_identifier__$M,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$N = {
  name: 'MeInputMultiple',
  components: {
    TextInputMultiple: __vue_component__$L,
    DatePickerMultiple: __vue_component__$M
  },

  props: {
    initialValues: {
      type: [String, Array, Date],
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    values: {
      type: Array,
      default: () => ([])
    },
    maxItems: {
      type: Number,
      default: 0
    },
    maxItemLength: {
      type: Number,
      default: 50
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      type: Boolean,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'light-default'
    },
    type: {
      type: String,
      default: 'text'
    },
    splitDelimiter: {
      type: [String, RegExp],
      default: null
    }
  },

  data() {
    return {
      onEvents: {
        input: (val, list) => this.$emit('input', val, list),
        itemAdded: val => this.$emit('itemAdded', val),
        itemRemoved: val => this.$emit('itemRemoved', val)
      }
    }
  }
};

/* script */
const __vue_script__$N = script$N;

/* template */
var __vue_render__$N = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-input-multiple w-100" },
    [
      _vm.type === "date"
        ? _c(
            "DatePickerMultiple",
            _vm._g(
              _vm._b({}, "DatePickerMultiple", _vm.$props, false),
              _vm.onEvents
            )
          )
        : _c(
            "TextInputMultiple",
            _vm._g(
              _vm._b(
                {
                  nativeOn: {
                    focus: function($event) {
                      return _vm.$emit("focus")
                    }
                  }
                },
                "TextInputMultiple",
                _vm.$props,
                false
              ),
              _vm.onEvents
            )
          )
    ],
    1
  )
};
var __vue_staticRenderFns__$N = [];
__vue_render__$N._withStripped = true;

  /* style */
  const __vue_inject_styles__$N = undefined;
  /* scoped */
  const __vue_scope_id__$N = undefined;
  /* module identifier */
  const __vue_module_identifier__$N = undefined;
  /* functional template */
  const __vue_is_functional_template__$N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$N, staticRenderFns: __vue_staticRenderFns__$N },
    __vue_inject_styles__$N,
    __vue_script__$N,
    __vue_scope_id__$N,
    __vue_is_functional_template__$N,
    __vue_module_identifier__$N,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$O = {
  name: 'MeCriteria',
  components: {
    Button: __vue_component__$6,
    Input: __vue_component__$E,
    Select: __vue_component__$m,
    TextInput: __vue_component__$p,
    InputMultiple: __vue_component__$N,
    Icon: __vue_component__,
    MeDropdownItem: __vue_component__$2
  },
  model: {
    prop: 'criteria',
    event: 'change'
  },
  props: {
    beforeRemoveItem: {
      type: Function,
      default: null
    },
    condensed: {
      type: Boolean,
      default: true
    },
    fields: {
      type: Array,
      required: true
    },
    criteria: {
      type: Array,
      required: true
    },
    opened: {
      type: Boolean,
      default: false
    },
    max: {
      type: Number,
      default: null
    },
    hideOperators: {
      type: Boolean,
      default: false
    },
    operatorsClass: {
      type: String,
      default: 'col-auto'
    },
    hideRemove: {
      type: Boolean,
      default: false
    },
    context: {
      type: Object,
      required: false,
      default() {
        return { }
      }
    },
    autoFlipSelect: {
      type: Boolean,
      default: true
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    textSelect: {
      type: String,
      default: ''
    },
    oneKeydown: {
      type: Function,
      default: null
    },
    maxItems: {
      type: Number,
      default: 0
    },
    inline: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    }
  },
  data() {
    return {
      currentCriteria: [],
      showInput: this.opened,
      multipleTags: false,
      operatorNumber: [
        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },
        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },
        { label: this.$toolkit.t('criteria.operators.greaterThan'), value: 'greaterThan', symbol: '>', icon: 'me-icon-l icon-greater-than' },
        { label: this.$toolkit.t('criteria.operators.lesserThan'), value: 'lesserThan', symbol: '<', icon: 'me-icon-l icon-less-than' },
        { label: this.$toolkit.t('criteria.operators.greaterThanEqual'), value: 'greaterThanEqual', symbol: '>=', icon: 'me-icon-l icon-greater-than-equal' },
        { label: this.$toolkit.t('criteria.operators.lesserThanEqual'), value: 'lesserThanEqual', symbol: '<=', icon: 'me-icon-l icon-less-than-equal' },
        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },
        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' }
      ],
      operatorText: [
        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },
        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },
        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },
        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' },
        { label: this.$toolkit.t('criteria.operators.contains'), value: 'contains', symbol: '=?', icon: 'me-icon-l icon-circle-plus' },
        { label: this.$toolkit.t('criteria.operators.doesntContains'), value: 'doesntContains', symbol: '!=?', icon: 'me-icon-l icon-minus-circle' }
      ],
      operatorDate: [
        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },
        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },
        { label: this.$toolkit.t('criteria.operators.greaterThan'), value: 'greaterThan', symbol: '>', icon: 'me-icon-l icon-greater-than' },
        { label: this.$toolkit.t('criteria.operators.lesserThan'), value: 'lesserThan', symbol: '<', icon: 'me-icon-l icon-less-than' },
        { label: this.$toolkit.t('criteria.operators.greaterThanEqual'), value: 'greaterThanEqual', symbol: '>=', icon: 'me-icon-l icon-greater-than-equal' },
        { label: this.$toolkit.t('criteria.operators.lesserThanEqual'), value: 'lesserThanEqual', symbol: '<=', icon: 'me-icon-l icon-less-than-equal' },
        { label: this.$toolkit.t('criteria.operators.today'), value: 'today', input: false, icon: 'me-icon-l icon-calendar-days' },
        { label: this.$toolkit.t('criteria.operators.thisWeek'), value: 'thisWeek', input: false, icon: 'me-icon-l icon-calendar-week' },
        { label: this.$toolkit.t('criteria.operators.thisMonth'), value: 'thisMonth', input: false, icon: 'me-icon-l icon-calendar-range' },
        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },
        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' }
      ],
      operatorBoolean: [
        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },
        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },
        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },
        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' }
      ],
      operatorObject: [
        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },
        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },
        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },
        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' },
        { label: this.$toolkit.t('criteria.operators.in'), value: 'in', multiple: true, icon: 'me-icon-l icon-circle-plus' },
        { label: this.$toolkit.t('criteria.operators.notIn'), value: 'notIn', multiple: true, icon: 'me-icon-l icon-minus-circle' }
      ],
      defaultOperator: {
        label: this.$toolkit.t('criteria.operators.is'),
        value: 'is',
        symbol: '=',
        icon: 'me-icon icon-equals'
      },
      operatorWidth: null,
      inlineLabelHasEllipsis: false,
      labelTooltip: null
    }
  },
  computed: {
    operators() {
      return {
        number: this.operatorNumber,
        numeric: this.operatorNumber,
        string: this.operatorText,
        text: this.operatorText,
        date: this.operatorDate,
        boolean: this.operatorBoolean,
        object: this.operatorObject
      }
    },
    clazz() {
      if (this.condensed) {
        return 'col-lg-12'
      } else {
        return 'col-md-6 col-lg-4'
      }
    },
    canAdd() {
      return this.max == null || this.currentCriteria.length < this.max
    },
    criteriaContext() {
      return {
        ...this.context,
        criteria: this.context.criteria != null ? this.currentCriteria.concat(this.context.criteria) : this.currentCriteria
      }
    },
    filteredFields() {
      const criteriaFieldsNames = this.currentCriteria.map(({ field }) => field?.name);
      return this.fields.filter((field) => {
        return !criteriaFieldsNames.includes(field?.name)
      })
    }
  },
  watch: {
    criteria(value) {
      this.currentCriteria = this.prepareModel(value);
    }
  },
  created() {
    this.currentCriteria = this.prepareModel(cloneDeep_1(this.criteria));
    this.setOperatorWidth();
    this.setMaxSize();
  },
  methods: {
    getLabelTooltip(item) {
      if (item.field && item.field.tooltip) return item.field.tooltip
      if (this.inlineLabelHasEllipsis) return item.field.label
      return ''
    },

    inputOptions(item) {
      if (item.field.type === 'boolean') {
        return {
          ...(item.field.options || {}),
          options: item.field.options?.options?.length ? item.field.options.options : [
            {
              name: this.$toolkit.t('criteria.boolean.yes'),
              value: true
            },
            {
              name: this.$toolkit.t('criteria.boolean.no'),
              value: false
            }
          ]
        }
      }
      return item.field.options || {}
    },
    prepareModel(criteria) {
      return (criteria || []).map((criterion) => {
        let field = null;

        if (criterion.field?.name) {
          field = this.fields.find(({ name }) => name === criterion.field.name);
        }

        const operator = this.getCriterionOperator(criterion, field);
        const operators = this.getCriterionOperators(criterion, field);

        return {
          field: field || criterion.field,
          operator,
          operators,
          value: criterion.value,
          tooltip: criterion.tooltip,
          custom: criterion.custom || {}
        }
      })
    },
    getCriterionOperator(criterion, field) {
      let criterionOperator = null;
      let filedTypeOperator = null;

      if (criterion.operators?.length) {
        criterionOperator = criterion.operators.find(({ value }) => value === criterion.operator.value);
      }

      if (!criterionOperator && field && criterion.operator) {
        filedTypeOperator = this.operators[field.type]?.find(({ value }) => value === criterion.operator.value);
      }

      return criterionOperator || filedTypeOperator || this.defaultOperator
    },

    getCriterionOperators(criterion, field) {
      if (criterion.operators) return criterion.operators
      if (field?.type) return this.operators[field.type] || []
      return []
    },

    addItem() {
      this.showInput = true;
    },
    onAdd(field) {
      this.currentCriteria.push({
        field: field,
        operator: this.defaultOperator
      });
      this.showInput = false;
      this.dispatchChange();
      this.setOperatorWidth();

      this.$nextTick(() => {
        this.scrollToNewCriterionField();
      });
    },
    scrollToNewCriterionField() {
      if (!this.$refs.criteriaFieldsRef?.scrollTo) return

      this.$refs.criteriaFieldsRef.scrollTo(0, this.$refs.criteriaFieldsRef.scrollHeight || 0);
    },
    removeItem(index) {
      this.resetDependents(this.currentCriteria[index]);
      this.currentCriteria.splice(index, 1);
      this.dispatchChange();
    },
    handleRemoveItem(index, item) {
      if (!this.beforeRemoveItem) {
        this.removeItem(index);
        return
      }

      new Promise((resolve) => {
        this.beforeRemoveItem({ ok: resolve }, item);
      }).then(() => {
        this.removeItem(index);
      });
    },
    changeOperator(item, operator) {
      const itemIndex = this.currentCriteria.findIndex(({ field: { name } }) => name === item?.field?.name);
      this.currentCriteria[itemIndex].operator = operator;

      if (isBoolean_1(operator.input) && !operator.input) {
        this.currentCriteria[itemIndex].value = null;
      }

      this.dispatchChange();
      this.setOperatorWidth();
    },
    onCriterionChange(criterion) {
      this.resetDependents(criterion);
      this.dispatchChange();
    },
    onCriterionMultipleChange(criterion, currentValue, currentItems) {
      if (!criterion.value?.length) {
        criterion.value = [currentValue];
      } else {
        criterion.value = [...currentItems, currentValue].filter(val => val);
      }

      this.resetDependents(criterion);
      this.dispatchChange();
    },
    resetDependents(criterion) {
      this.currentCriteria = this.currentCriteria.map((currentCriteria) => {
        const { value: currentCriteriaValue } = currentCriteria;

        return {
          ...currentCriteria,
          value: currentCriteria?.field?.parent === criterion?.field?.name ? null : currentCriteriaValue
        }
      });
    },
    dispatchChange() {
      this.$emit('change', cloneDeep_1(this.currentCriteria));
      this.setMaxSize();
    },
    addValue(currentItem, itemAdded) {
      currentItem.value = itemAdded.items;
      this.$emit('change', cloneDeep_1(this.currentCriteria));
    },

    removeValue(item, field) {
      if (field.type === 'date') {
        return this.handleRemoveDateMultiple(item, field)
      }
      this.handleRemoveItemMultiple(item);
    },

    handleRemoveDateMultiple({ items }, field) {
      this.currentCriteria = this.currentCriteria.map(criterion => criterion.field.name === field.name ? ({
        ...criterion,
        value: items
      }) : criterion);

      this.$emit('change', cloneDeep_1(this.currentCriteria));
    },

    handleRemoveItemMultiple(item) {
      const criteria = this.currentCriteria.find(criterion => criterion.field.multiple && criterion.value.includes(item.removed));
      const index = criteria.value.indexOf(item.removed);
      criteria.value.splice(index, 1);
      this.$emit('change', cloneDeep_1(this.currentCriteria));
    },

    setMaxSize() {
      this.$nextTick(() => {
        const criteriaDiv = document.querySelector('div.me-criteria');
        const criteriaFieldsContainer = document.querySelector('div.me-criteria__fields');
        /* istanbul ignore next */
        if (criteriaDiv && criteriaFieldsContainer) {
          const dropdownRect = criteriaDiv.getBoundingClientRect();
          if (dropdownRect.height >= document.documentElement.clientHeight * 0.8 - dropdownRect.y) {
            criteriaFieldsContainer.style.maxHeight = `calc(85vh - ${dropdownRect.y}px)`;
            criteriaFieldsContainer.style.minHeight = this.currentCriteria.length ? '48px' : 'unset';
            criteriaFieldsContainer.style.overflowY = 'auto';
            criteriaFieldsContainer.style.overflowX = 'hidden';
            criteriaFieldsContainer.style.paddingRight = '10px';
          } else {
            criteriaFieldsContainer.style.maxHeight = 'unset';
            criteriaFieldsContainer.style.minHeight = 'unset';
            criteriaFieldsContainer.style.overflowY = 'unset';
            criteriaFieldsContainer.style.overflowX = 'unset';
            criteriaFieldsContainer.style.paddingRight = 'initial';
          }
        }
      });
    },
    setOperatorWidth() {
      if (this.operatorsClass !== 'col-auto') return

      this.$nextTick(() => {
        this.operatorWidth = this.currentCriteria.reduce((acc, criterion) => ({ ...acc, [criterion.field.name]: this.getOperatorWidth(criterion) }), {});
      });
    },
    getOperatorWidth(criterion) {
      const operatorEls = this.$refs[`operator_${criterion.field.name}`];
      if (!operatorEls?.length) return 'auto'

      return `${operatorEls[0].$el.clientWidth}px`
    },
    isSelectableDisabled(index) {
      if (!this.maxItems) return false
      return this.currentCriteria[index]?.value?.length >= this.maxItems
    },

    setInlineLabelHasEllipsis(event, item) {
      this.inlineLabelHasEllipsis = event.target.offsetHeight < event.target.scrollHeight;
      this.labelTooltip = this.getLabelTooltip(item);
    }
  }
};

/* script */
const __vue_script__$O = script$O;

/* template */
var __vue_render__$O = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-criteria" }, [
    _c(
      "div",
      { ref: "criteriaFieldsRef", staticClass: "me-criteria__fields" },
      [
        _c(
          "div",
          { staticClass: "row" },
          _vm._l(_vm.currentCriteria, function(item, index) {
            return _c(
              "div",
              {
                key: item.field.name,
                staticClass: "me-criteria__fields__input-group col-12",
                class: _vm.clazz
              },
              [
                item.field.label && !_vm.inline
                  ? _c(
                      "div",
                      {
                        staticClass:
                          "row me-criteria__fields__input-group__label"
                      },
                      [
                        _c("div", { staticClass: "col-12" }, [
                          _c(
                            "label",
                            {
                              directives: [
                                {
                                  name: "me-tooltip",
                                  rawName: "v-me-tooltip.hover",
                                  value: _vm.labelTooltip,
                                  expression: "labelTooltip",
                                  modifiers: { hover: true }
                                }
                              ],
                              on: {
                                mouseenter: function(e) {
                                  return _vm.setInlineLabelHasEllipsis(e, item)
                                }
                              }
                            },
                            [
                              _vm._v(
                                "\n              " +
                                  _vm._s(item.field.label) +
                                  "\n            "
                              )
                            ]
                          )
                        ])
                      ]
                    )
                  : _vm._e(),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass:
                      "row no-gutters align-items-stretch flex-nowrap"
                  },
                  [
                    _vm.inline && item.field.label
                      ? _c(
                          "div",
                          {
                            staticClass:
                              "me-criteria__fields__input-group__label me-criteria__fields__input-group__label--inline"
                          },
                          [
                            _c(
                              "label",
                              {
                                directives: [
                                  {
                                    name: "me-tooltip",
                                    rawName: "v-me-tooltip.hover",
                                    value: _vm.labelTooltip,
                                    expression: "labelTooltip",
                                    modifiers: { hover: true }
                                  }
                                ],
                                on: {
                                  mouseenter: function(e) {
                                    return _vm.setInlineLabelHasEllipsis(
                                      e,
                                      item
                                    )
                                  }
                                }
                              },
                              [
                                _vm._v(
                                  "\n              " +
                                    _vm._s(item.field.label) +
                                    "\n            "
                                )
                              ]
                            )
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    !_vm.hideOperators
                      ? _c(
                          "div",
                          {
                            staticClass:
                              "me-criteria__fields__input-group__field-condition",
                            class: _vm.operatorsClass
                          },
                          [
                            _c(
                              "me-drop-down",
                              {
                                ref: "operator_" + item.field.name,
                                refInFor: true,
                                attrs: {
                                  disabled: item.operator.disabled,
                                  size: _vm.size,
                                  block: "",
                                  variant: "outline-secondary"
                                }
                              },
                              [
                                _c("template", { slot: "button-content" }, [
                                  _c(
                                    "div",
                                    { staticClass: "dropdown-icon" },
                                    [
                                      item.operator.icon
                                        ? _c("Icon", {
                                            class: item.operator.icon
                                          })
                                        : _vm._e()
                                    ],
                                    1
                                  ),
                                  _vm._v(" "),
                                  _c("div", { staticClass: "flex-grow-1" }, [
                                    _vm._v(
                                      "\n                  " +
                                        _vm._s(item.operator.label) +
                                        "\n                "
                                    )
                                  ])
                                ]),
                                _vm._v(" "),
                                _vm._l(item.operators, function(operator) {
                                  return _c("MeDropdownItem", {
                                    key: operator.symbol,
                                    attrs: { label: operator.label },
                                    on: {
                                      click: function($event) {
                                        return _vm.changeOperator(
                                          item,
                                          operator
                                        )
                                      }
                                    }
                                  })
                                })
                              ],
                              2
                            )
                          ],
                          1
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass:
                          "col d-flex me-criteria__fields__input-group__input",
                        class: {
                          "me-criteria__fields__input-group__input--with-operators": !_vm.hideOperators,
                          "me-criteria__fields__input-group__input--with-remove": !_vm.hideRemove
                        },
                        attrs: {
                          state: item.field.state,
                          "invalid-feedback": item.field.invalidFeedBack
                        }
                      },
                      [
                        (item.operator.input === undefined ||
                          item.operator.input) &&
                        item.field.multiple && item.field.type !== "object"
                          ? _c("InputMultiple", {
                              staticClass:
                                "me-criteria__fields__input-group__input__type",
                              attrs: {
                                maxItems: _vm.maxItems,
                                splitDelimiter:
                                  item.field.options &&
                                  item.field.options.splitDelimiter,
                                type:
                                  item.field.type === "string"
                                    ? "text"
                                    : item.field.type,
                                placeholder: item.field.placeholder || "",
                                initialValues: item.value,
                                disabled:
                                  item.field.options &&
                                  item.field.options.disabled
                                    ? item.field.options.disabled
                                    : false,
                                size: _vm.size
                              },
                              on: {
                                input: function(currentValue, currentItems) {
                                  return _vm.onCriterionMultipleChange(
                                    item,
                                    currentValue,
                                    currentItems
                                  )
                                },
                                itemAdded: function(itemAdded) {
                                  return _vm.addValue(item, itemAdded)
                                },
                                itemRemoved: function(itemRemoved) {
                                  return _vm.removeValue(
                                    itemRemoved,
                                    item.field
                                  )
                                }
                              }
                            })
                          : item.operator.input === undefined ||
                            item.operator.input
                          ? _c("Input", {
                              staticClass:
                                "me-criteria__fields__input-group__input__type",
                              attrs: {
                                type:
                                  item.field.type === "boolean"
                                    ? "object"
                                    : item.field.type,
                                multiple:
                                  item.field.multiple || item.operator.multiple,
                                options: _vm.inputOptions(item),
                                selectable: function() {
                                  return !_vm.isSelectableDisabled(index)
                                },
                                context: _vm.criteriaContext,
                                autoFlip: _vm.autoFlipSelect,
                                state: item.field.state,
                                size: _vm.size
                              },
                              on: {
                                input: function($event) {
                                  return _vm.onCriterionChange(item)
                                }
                              },
                              model: {
                                value: item.value,
                                callback: function($$v) {
                                  _vm.$set(item, "value", $$v);
                                },
                                expression: "item.value"
                              }
                            })
                          : _c("TextInput", {
                              staticClass:
                                "me-criteria__fields__input-group__input__type",
                              attrs: {
                                state: item.field.state,
                                size: _vm.size,
                                disabled: ""
                              }
                            })
                      ],
                      1
                    ),
                    _vm._v(" "),
                    _c("div", { staticClass: "col-auto" }, [
                      !_vm.hideRemove
                        ? _c(
                            "div",
                            {
                              class: "remove " + _vm.size,
                              on: {
                                click: function($event) {
                                  return _vm.handleRemoveItem(index, item)
                                }
                              }
                            },
                            [
                              _c("Icon", {
                                attrs: {
                                  size: 13,
                                  icon: "me-icon-l icon-trash",
                                  color: "var(--me-danger-2)"
                                }
                              })
                            ],
                            1
                          )
                        : _vm._e()
                    ])
                  ]
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "row no-gutters" },
                  [
                    _vm.inline && item.field.label
                      ? _c("div", {
                          staticClass:
                            "me-criteria__fields__input-group__label me-criteria__input-group__label--inline"
                        })
                      : _vm._e(),
                    _vm._v(" "),
                    !_vm.hideOperators && item.field
                      ? _c("div", {
                          class: [
                            _vm.operatorsClass !== "col-auto" &&
                              _vm.operatorsClass
                          ],
                          style: {
                            width:
                              _vm.operatorsClass === "col-auto" &&
                              _vm.operatorWidth &&
                              _vm.operatorWidth[item.field.name]
                          }
                        })
                      : _vm._e(),
                    _vm._v(" "),
                    _c("me-form-group", {
                      class: ["col offset-auto", { inline: _vm.inline }],
                      attrs: {
                        state: item.field.state,
                        "invalid-feedback": item.field.invalidFeedBack,
                        description: item.field.description
                      }
                    })
                  ],
                  1
                )
              ]
            )
          }),
          0
        )
      ]
    ),
    _vm._v(" "),
    _c("div", { staticClass: "row" }, [
      _vm.canAdd
        ? _c(
            "div",
            { staticClass: "me-criteria__add-criterion", class: _vm.clazz },
            [
              _vm._t(
                "criteria-add",
                [
                  _vm.hideButton
                    ? _c("span", [_vm._v(_vm._s(_vm.textSelect))])
                    : _vm._e(),
                  _vm._v(" "),
                  _c(
                    "div",
                    [
                      _vm.showInput || _vm.hideButton
                        ? _c("Select", {
                            attrs: {
                              resetValue: _vm.hideButton,
                              options: _vm.filteredFields,
                              placeholder: _vm.$toolkit.t(
                                "criteria.field.placeholder"
                              ),
                              clearable: false,
                              autoFlip: _vm.autoFlipSelect,
                              label: "label"
                            },
                            on: { input: _vm.onAdd }
                          })
                        : _c(
                            "Button",
                            {
                              attrs: { size: "md", variant: "light" },
                              on: { click: _vm.addItem }
                            },
                            [
                              _c("Icon", {
                                attrs: { icon: "me-icon icon-plus" }
                              }),
                              _vm._v(
                                " " +
                                  _vm._s(_vm.$toolkit.t("criteria.condition")) +
                                  "\n          "
                              )
                            ],
                            1
                          )
                    ],
                    1
                  )
                ],
                { onAdd: _vm.onAdd, fields: _vm.filteredFields }
              )
            ],
            2
          )
        : _vm._e()
    ])
  ])
};
var __vue_staticRenderFns__$O = [];
__vue_render__$O._withStripped = true;

  /* style */
  const __vue_inject_styles__$O = function (inject) {
    if (!inject) return
    inject("data-v-29fff4fd_0", { source: ".me-criteria__fields__input-group .me-input[data-v-29fff4fd] {\n  width: 100%;\n}\n.me-criteria__fields__input-group .remove[data-v-29fff4fd] {\n  border-radius: 100%;\n  cursor: pointer;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-left: 4px;\n}\n.me-criteria__fields__input-group .remove.sm[data-v-29fff4fd] {\n  width: 32px;\n  height: 32px;\n}\n.me-criteria__fields__input-group .remove.md[data-v-29fff4fd] {\n  width: 40px;\n  height: 40px;\n}\n.me-criteria__fields__input-group .remove.lg[data-v-29fff4fd] {\n  width: 48px;\n  height: 48px;\n}\n.me-criteria__fields__input-group .remove[data-v-29fff4fd]:hover {\n  background-color: var(--me-danger-7);\n}\n.me-criteria__fields__input-group__label[data-v-29fff4fd] {\n  font-size: 12px;\n  color: var(--me-gray-2);\n}\n.me-criteria__fields__input-group__label label[data-v-29fff4fd] {\n  margin-bottom: 4px;\n}\n.me-criteria__fields__input-group__label--inline[data-v-29fff4fd] {\n  display: flex;\n  align-items: center;\n  width: 72px;\n  min-width: 72px;\n  margin-right: 8px;\n}\n.me-criteria__fields__input-group__label--inline label[data-v-29fff4fd] {\n  margin-bottom: 0;\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd] {\n  white-space: nowrap;\n}\n.me-criteria__fields__input-group__field-condition .dropdown-icon[data-v-29fff4fd] {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-right: 4px;\n  width: 20px;\n  height: 20px;\n  border-radius: 50%;\n  background-color: var(--me-gray-6);\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd]  .form-group__invalid-feedback {\n  height: 100%;\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd]  .dropdown {\n  width: 100%;\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd]  .dropdown.show .dropdown-toggle {\n  background-color: var(--me-gray-6) !important;\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd]  .dropdown button:first-child {\n  display: flex;\n  align-items: center;\n  border-right: none;\n  border-radius: 8px 0 0 8px;\n  border-color: var(--me-gray-6) !important;\n  background-color: var(--me-bg-gray);\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd]  .dropdown button:first-child span {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 24px;\n  height: 24px;\n  margin-right: 8px;\n  background-color: var(--me-gray-6);\n  border-radius: 50%;\n  font-size: 12px;\n}\n.me-criteria__fields__input-group__field-condition[data-v-29fff4fd]  .dropdown button:first-child:not(:disabled):active {\n  background-color: var(--me-gray-6) !important;\n}\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type[data-v-29fff4fd]  .me-date-picker .mx-datepicker .mx-input-wrapper input,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type[data-v-29fff4fd]  .me-select .vs__dropdown-toggle,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type[data-v-29fff4fd]  .me-text-input input,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type[data-v-29fff4fd]  .input-multiple-container,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type[data-v-29fff4fd]  input {\n  border-top-left-radius: 0px !important;\n  border-bottom-left-radius: 0px !important;\n}\n.me-criteria__fields__input-group[data-v-29fff4fd]  .input-multiple-container.hideOperators {\n  border-top-left-radius: 8px;\n  border-bottom-left-radius: 8px;\n}\n.me-criteria__fields__input-group[data-v-29fff4fd]  .form-group.inline {\n  margin-left: 82px;\n}\n.me-criteria__add-criterion[data-v-29fff4fd] {\n  display: flex;\n  flex-direction: column;\n}\n\n/*# sourceMappingURL=Criteria.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Criteria.vue","Criteria.vue"],"names":[],"mappings":"AA+mBA;EACA,WAAA;AC9mBA;ADinBA;EACA,mBAAA;EACA,eAAA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,gBAAA;AC/mBA;ADinBA;EACA,WAAA;EACA,YAAA;AC/mBA;ADknBA;EACA,WAAA;EACA,YAAA;AChnBA;ADmnBA;EACA,WAAA;EACA,YAAA;ACjnBA;ADonBA;EACA,oCAAA;AClnBA;ADsnBA;EACA,eAAA;EACA,uBAAA;ACpnBA;ADsnBA;EACA,kBAAA;ACpnBA;ADunBA;EACA,aAAA;EACA,mBAAA;EACA,WAAA;EACA,eAAA;EACA,iBAAA;ACrnBA;ADunBA;EACA,gBAAA;EACA,gBAAA;EACA,oBAAA;EACA,qBAAA;EACA,4BAAA;EACA,uBAAA;ACrnBA;AD0nBA;EACA,mBAAA;ACxnBA;AD0nBA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,iBAAA;EACA,WAAA;EACA,YAAA;EACA,kBAAA;EACA,kCAAA;ACxnBA;AD2nBA;EACA,YAAA;ACznBA;AD4nBA;EACA,WAAA;AC1nBA;AD6nBA;EACA,6CAAA;AC3nBA;AD+nBA;EACA,aAAA;EACA,mBAAA;EACA,kBAAA;EACA,0BAAA;EACA,yCAAA;EACA,mCAAA;AC7nBA;AD+nBA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,WAAA;EACA,YAAA;EACA,iBAAA;EACA,kCAAA;EACA,kBAAA;EACA,eAAA;AC7nBA;ADgoBA;EACA,6CAAA;AC9nBA;ADqoBA;;;;;EAKA,sCAAA;EACA,yCAAA;ACnoBA;ADwoBA;EACA,2BAAA;EACA,8BAAA;ACtoBA;AD0oBA;EACA,iBAAA;ACxoBA;AD6oBA;EACA,aAAA;EACA,sBAAA;AC3oBA;;AAEA,uCAAuC","file":"Criteria.vue","sourcesContent":["<template>\n  <div class=\"me-criteria\">\n    <div ref=\"criteriaFieldsRef\" class=\"me-criteria__fields\">\n      <div class=\"row\">\n        <div\n          v-for=\"(item, index) in currentCriteria\"\n          :key=\"item.field.name\"\n          :class=\"clazz\"\n          class=\"me-criteria__fields__input-group col-12\"\n        >\n          <div\n            v-if=\"item.field.label && !inline\"\n            class=\"row me-criteria__fields__input-group__label\"\n          >\n            <div class=\"col-12\">\n              <label\n                v-me-tooltip.hover=\"labelTooltip\"\n                @mouseenter=\"(e) => setInlineLabelHasEllipsis(e, item)\"\n              >\n                {{ item.field.label }}\n              </label>\n            </div>\n          </div>\n\n          <div class=\"row no-gutters align-items-stretch flex-nowrap\">\n            <div\n              v-if=\"inline && item.field.label\"\n              class=\"me-criteria__fields__input-group__label me-criteria__fields__input-group__label--inline\"\n            >\n              <label\n                v-me-tooltip.hover=\"labelTooltip\"\n                @mouseenter=\"(e) => setInlineLabelHasEllipsis(e, item)\"\n              >\n                {{ item.field.label }}\n              </label>\n            </div>\n\n            <div\n              v-if=\"!hideOperators\"\n              :class=\"operatorsClass\"\n              class=\"me-criteria__fields__input-group__field-condition\"\n            >\n              <me-drop-down\n                :ref=\"`operator_${item.field.name}`\"\n                :disabled=\"item.operator.disabled\"\n                :size=\"size\"\n                block\n                variant=\"outline-secondary\"\n              >\n                <template slot=\"button-content\">\n                  <div class=\"dropdown-icon\">\n                    <Icon\n                      v-if=\"item.operator.icon\"\n                      :class=\"item.operator.icon\"\n                    />\n                  </div>\n\n                  <div class=\"flex-grow-1\">\n                    {{ item.operator.label }}\n                  </div>\n                </template>\n\n                <MeDropdownItem\n                  v-for=\"operator in item.operators\"\n                  :key=\"operator.symbol\"\n                  :label=\"operator.label\"\n                  @click=\"changeOperator(item, operator)\"\n                />\n              </me-drop-down>\n            </div>\n\n            <div\n              :state=\"item.field.state\"\n              :invalid-feedback=\"item.field.invalidFeedBack\"\n              :class=\"{\n                'me-criteria__fields__input-group__input--with-operators': !hideOperators,\n                'me-criteria__fields__input-group__input--with-remove': !hideRemove\n              }\"\n              class=\"col d-flex me-criteria__fields__input-group__input\"\n            >\n              <InputMultiple\n                v-if=\"(item.operator.input === undefined || item.operator.input) && (item.field.multiple && item.field.type !== 'object')\"\n                :maxItems=\"maxItems\"\n                :splitDelimiter=\"item.field.options && item.field.options.splitDelimiter\"\n                :type=\"item.field.type === 'string' ? 'text' : item.field.type\"\n                :placeholder=\"item.field.placeholder || ''\"\n                :initialValues=\"item.value\"\n                :disabled=\"item.field.options && item.field.options.disabled ? item.field.options.disabled : false\"\n                :size=\"size\"\n                @input=\"(currentValue, currentItems) => onCriterionMultipleChange(item, currentValue, currentItems)\"\n                @itemAdded=\"(itemAdded) => addValue(item, itemAdded)\"\n                @itemRemoved=\"(itemRemoved) => removeValue(itemRemoved, item.field)\"\n                class=\"me-criteria__fields__input-group__input__type\"\n              />\n\n              <Input\n                v-else-if=\"item.operator.input === undefined || item.operator.input\"\n                v-model=\"item.value\"\n                :type=\"item.field.type === 'boolean' ? 'object' : item.field.type\"\n                :multiple=\"item.field.multiple || item.operator.multiple\"\n                :options=\"inputOptions(item)\"\n                :selectable=\"() => !isSelectableDisabled(index)\"\n                :context=\"criteriaContext\"\n                :autoFlip=\"autoFlipSelect\"\n                :state=\"item.field.state\"\n                :size=\"size\"\n                @input=\"onCriterionChange(item)\"\n                class=\"me-criteria__fields__input-group__input__type\"\n              />\n\n              <TextInput\n                v-else\n                :state=\"item.field.state\"\n                :size=\"size\"\n                disabled\n                class=\"me-criteria__fields__input-group__input__type\"\n              />\n            </div>\n\n            <div class=\"col-auto\">\n              <div\n                v-if=\"!hideRemove\"\n                @click=\"handleRemoveItem(index, item)\"\n                :class=\"`remove ${size}`\"\n              >\n                <Icon\n                  :size=\"13\"\n                  icon=\"me-icon-l icon-trash\"\n                  color=\"var(--me-danger-2)\"\n                />\n              </div>\n            </div>\n          </div>\n\n          <div class=\"row no-gutters\">\n            <div\n              v-if=\"inline && item.field.label\"\n              class=\"me-criteria__fields__input-group__label me-criteria__input-group__label--inline\"\n            />\n\n            <div\n              v-if=\"!hideOperators && item.field\"\n              :style=\"{ width: operatorsClass === 'col-auto' && operatorWidth && operatorWidth[item.field.name] }\"\n              :class=\"[operatorsClass !== 'col-auto' && operatorsClass]\"\n            />\n\n            <me-form-group\n              :state=\"item.field.state\"\n              :invalid-feedback=\"item.field.invalidFeedBack\"\n              :description=\"item.field.description\"\n              :class=\"['col offset-auto', { 'inline': inline }]\"\n            />\n          </div>\n        </div>\n      </div>\n    </div>\n\n    <div class=\"row\">\n      <div v-if=\"canAdd\" :class=\"clazz\" class=\"me-criteria__add-criterion\">\n        <slot\n          :onAdd=\"onAdd\"\n          :fields=\"filteredFields\"\n          name=\"criteria-add\"\n        >\n          <span v-if=\"hideButton\">{{ textSelect }}</span>\n\n          <div>\n            <Select\n              v-if=\"showInput || hideButton\"\n              :resetValue=\"hideButton\"\n              :options=\"filteredFields\"\n              :placeholder=\"$toolkit.t('criteria.field.placeholder')\"\n              :clearable=\"false\"\n              :autoFlip=\"autoFlipSelect\"\n              @input=\"onAdd\"\n              label=\"label\"\n            />\n\n            <Button\n              @click=\"addItem\"\n              v-else\n              size=\"md\"\n              variant=\"light\"\n            >\n              <Icon icon=\"me-icon icon-plus\" /> {{ $toolkit.t('criteria.condition') }}\n            </Button>\n          </div>\n        </slot>\n      </div>\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport isBoolean from 'lodash/isBoolean'\nimport Button from './Button.vue'\nimport Input from './Input.vue'\nimport Select from './Select.vue'\nimport TextInput from './TextInput.vue'\nimport Icon from './Icon.vue'\nimport InputMultiple from './inputMultiple/InputMultiple.vue'\nimport MeDropdownItem from './dropdown/DropdownItem.vue'\n\nexport default {\n  name: 'MeCriteria',\n  components: {\n    Button,\n    Input,\n    Select,\n    TextInput,\n    InputMultiple,\n    Icon,\n    MeDropdownItem\n  },\n  model: {\n    prop: 'criteria',\n    event: 'change'\n  },\n  props: {\n    beforeRemoveItem: {\n      type: Function,\n      default: null\n    },\n    condensed: {\n      type: Boolean,\n      default: true\n    },\n    fields: {\n      type: Array,\n      required: true\n    },\n    criteria: {\n      type: Array,\n      required: true\n    },\n    opened: {\n      type: Boolean,\n      default: false\n    },\n    max: {\n      type: Number,\n      default: null\n    },\n    hideOperators: {\n      type: Boolean,\n      default: false\n    },\n    operatorsClass: {\n      type: String,\n      default: 'col-auto'\n    },\n    hideRemove: {\n      type: Boolean,\n      default: false\n    },\n    context: {\n      type: Object,\n      required: false,\n      default() {\n        return { }\n      }\n    },\n    autoFlipSelect: {\n      type: Boolean,\n      default: true\n    },\n    hideButton: {\n      type: Boolean,\n      default: false\n    },\n    textSelect: {\n      type: String,\n      default: ''\n    },\n    oneKeydown: {\n      type: Function,\n      default: null\n    },\n    maxItems: {\n      type: Number,\n      default: 0\n    },\n    inline: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: 'md'\n    }\n  },\n  data() {\n    return {\n      currentCriteria: [],\n      showInput: this.opened,\n      multipleTags: false,\n      operatorNumber: [\n        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },\n        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },\n        { label: this.$toolkit.t('criteria.operators.greaterThan'), value: 'greaterThan', symbol: '>', icon: 'me-icon-l icon-greater-than' },\n        { label: this.$toolkit.t('criteria.operators.lesserThan'), value: 'lesserThan', symbol: '<', icon: 'me-icon-l icon-less-than' },\n        { label: this.$toolkit.t('criteria.operators.greaterThanEqual'), value: 'greaterThanEqual', symbol: '>=', icon: 'me-icon-l icon-greater-than-equal' },\n        { label: this.$toolkit.t('criteria.operators.lesserThanEqual'), value: 'lesserThanEqual', symbol: '<=', icon: 'me-icon-l icon-less-than-equal' },\n        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },\n        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' }\n      ],\n      operatorText: [\n        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },\n        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },\n        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },\n        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' },\n        { label: this.$toolkit.t('criteria.operators.contains'), value: 'contains', symbol: '=?', icon: 'me-icon-l icon-circle-plus' },\n        { label: this.$toolkit.t('criteria.operators.doesntContains'), value: 'doesntContains', symbol: '!=?', icon: 'me-icon-l icon-minus-circle' }\n      ],\n      operatorDate: [\n        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },\n        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },\n        { label: this.$toolkit.t('criteria.operators.greaterThan'), value: 'greaterThan', symbol: '>', icon: 'me-icon-l icon-greater-than' },\n        { label: this.$toolkit.t('criteria.operators.lesserThan'), value: 'lesserThan', symbol: '<', icon: 'me-icon-l icon-less-than' },\n        { label: this.$toolkit.t('criteria.operators.greaterThanEqual'), value: 'greaterThanEqual', symbol: '>=', icon: 'me-icon-l icon-greater-than-equal' },\n        { label: this.$toolkit.t('criteria.operators.lesserThanEqual'), value: 'lesserThanEqual', symbol: '<=', icon: 'me-icon-l icon-less-than-equal' },\n        { label: this.$toolkit.t('criteria.operators.today'), value: 'today', input: false, icon: 'me-icon-l icon-calendar-days' },\n        { label: this.$toolkit.t('criteria.operators.thisWeek'), value: 'thisWeek', input: false, icon: 'me-icon-l icon-calendar-week' },\n        { label: this.$toolkit.t('criteria.operators.thisMonth'), value: 'thisMonth', input: false, icon: 'me-icon-l icon-calendar-range' },\n        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },\n        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' }\n      ],\n      operatorBoolean: [\n        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },\n        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },\n        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },\n        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' }\n      ],\n      operatorObject: [\n        { label: this.$toolkit.t('criteria.operators.is'), value: 'is', symbol: '=', icon: 'me-icon-l icon-equals' },\n        { label: this.$toolkit.t('criteria.operators.isNot'), value: 'isNot', symbol: '!=', icon: 'me-icon-l icon-not-equal' },\n        { label: this.$toolkit.t('criteria.operators.isNull'), value: 'isNull', input: false, icon: 'me-icon-l icon-empty-set' },\n        { label: this.$toolkit.t('criteria.operators.isNotNull'), value: 'isNotNull', input: false, icon: 'me-icon-l icon-cubes' },\n        { label: this.$toolkit.t('criteria.operators.in'), value: 'in', multiple: true, icon: 'me-icon-l icon-circle-plus' },\n        { label: this.$toolkit.t('criteria.operators.notIn'), value: 'notIn', multiple: true, icon: 'me-icon-l icon-minus-circle' }\n      ],\n      defaultOperator: {\n        label: this.$toolkit.t('criteria.operators.is'),\n        value: 'is',\n        symbol: '=',\n        icon: 'me-icon icon-equals'\n      },\n      operatorWidth: null,\n      inlineLabelHasEllipsis: false,\n      labelTooltip: null\n    }\n  },\n  computed: {\n    operators() {\n      return {\n        number: this.operatorNumber,\n        numeric: this.operatorNumber,\n        string: this.operatorText,\n        text: this.operatorText,\n        date: this.operatorDate,\n        boolean: this.operatorBoolean,\n        object: this.operatorObject\n      }\n    },\n    clazz() {\n      if (this.condensed) {\n        return 'col-lg-12'\n      } else {\n        return 'col-md-6 col-lg-4'\n      }\n    },\n    canAdd() {\n      return this.max == null || this.currentCriteria.length < this.max\n    },\n    criteriaContext() {\n      return {\n        ...this.context,\n        criteria: this.context.criteria != null ? this.currentCriteria.concat(this.context.criteria) : this.currentCriteria\n      }\n    },\n    filteredFields() {\n      const criteriaFieldsNames = this.currentCriteria.map(({ field }) => field?.name)\n      return this.fields.filter((field) => {\n        return !criteriaFieldsNames.includes(field?.name)\n      })\n    }\n  },\n  watch: {\n    criteria(value) {\n      this.currentCriteria = this.prepareModel(value)\n    }\n  },\n  created() {\n    this.currentCriteria = this.prepareModel(cloneDeep(this.criteria))\n    this.setOperatorWidth()\n    this.setMaxSize()\n  },\n  methods: {\n    getLabelTooltip(item) {\n      if (item.field && item.field.tooltip) return item.field.tooltip\n      if (this.inlineLabelHasEllipsis) return item.field.label\n      return ''\n    },\n\n    inputOptions(item) {\n      if (item.field.type === 'boolean') {\n        return {\n          ...(item.field.options || {}),\n          options: item.field.options?.options?.length ? item.field.options.options : [\n            {\n              name: this.$toolkit.t('criteria.boolean.yes'),\n              value: true\n            },\n            {\n              name: this.$toolkit.t('criteria.boolean.no'),\n              value: false\n            }\n          ]\n        }\n      }\n      return item.field.options || {}\n    },\n    prepareModel(criteria) {\n      return (criteria || []).map((criterion) => {\n        let field = null\n\n        if (criterion.field?.name) {\n          field = this.fields.find(({ name }) => name === criterion.field.name)\n        }\n\n        const operator = this.getCriterionOperator(criterion, field)\n        const operators = this.getCriterionOperators(criterion, field)\n\n        return {\n          field: field || criterion.field,\n          operator,\n          operators,\n          value: criterion.value,\n          tooltip: criterion.tooltip,\n          custom: criterion.custom || {}\n        }\n      })\n    },\n    getCriterionOperator(criterion, field) {\n      let criterionOperator = null\n      let filedTypeOperator = null\n\n      if (criterion.operators?.length) {\n        criterionOperator = criterion.operators.find(({ value }) => value === criterion.operator.value)\n      }\n\n      if (!criterionOperator && field && criterion.operator) {\n        filedTypeOperator = this.operators[field.type]?.find(({ value }) => value === criterion.operator.value)\n      }\n\n      return criterionOperator || filedTypeOperator || this.defaultOperator\n    },\n\n    getCriterionOperators(criterion, field) {\n      if (criterion.operators) return criterion.operators\n      if (field?.type) return this.operators[field.type] || []\n      return []\n    },\n\n    addItem() {\n      this.showInput = true\n    },\n    onAdd(field) {\n      this.currentCriteria.push({\n        field: field,\n        operator: this.defaultOperator\n      })\n      this.showInput = false\n      this.dispatchChange()\n      this.setOperatorWidth()\n\n      this.$nextTick(() => {\n        this.scrollToNewCriterionField()\n      })\n    },\n    scrollToNewCriterionField() {\n      if (!this.$refs.criteriaFieldsRef?.scrollTo) return\n\n      this.$refs.criteriaFieldsRef.scrollTo(0, this.$refs.criteriaFieldsRef.scrollHeight || 0)\n    },\n    removeItem(index) {\n      this.resetDependents(this.currentCriteria[index])\n      this.currentCriteria.splice(index, 1)\n      this.dispatchChange()\n    },\n    handleRemoveItem(index, item) {\n      if (!this.beforeRemoveItem) {\n        this.removeItem(index)\n        return\n      }\n\n      new Promise((resolve) => {\n        this.beforeRemoveItem({ ok: resolve }, item)\n      }).then(() => {\n        this.removeItem(index)\n      })\n    },\n    changeOperator(item, operator) {\n      const itemIndex = this.currentCriteria.findIndex(({ field: { name } }) => name === item?.field?.name)\n      this.currentCriteria[itemIndex].operator = operator\n\n      if (isBoolean(operator.input) && !operator.input) {\n        this.currentCriteria[itemIndex].value = null\n      }\n\n      this.dispatchChange()\n      this.setOperatorWidth()\n    },\n    onCriterionChange(criterion) {\n      this.resetDependents(criterion)\n      this.dispatchChange()\n    },\n    onCriterionMultipleChange(criterion, currentValue, currentItems) {\n      if (!criterion.value?.length) {\n        criterion.value = [currentValue]\n      } else {\n        criterion.value = [...currentItems, currentValue].filter(val => val)\n      }\n\n      this.resetDependents(criterion)\n      this.dispatchChange()\n    },\n    resetDependents(criterion) {\n      this.currentCriteria = this.currentCriteria.map((currentCriteria) => {\n        const { value: currentCriteriaValue } = currentCriteria\n\n        return {\n          ...currentCriteria,\n          value: currentCriteria?.field?.parent === criterion?.field?.name ? null : currentCriteriaValue\n        }\n      })\n    },\n    dispatchChange() {\n      this.$emit('change', cloneDeep(this.currentCriteria))\n      this.setMaxSize()\n    },\n    addValue(currentItem, itemAdded) {\n      currentItem.value = itemAdded.items\n      this.$emit('change', cloneDeep(this.currentCriteria))\n    },\n\n    removeValue(item, field) {\n      if (field.type === 'date') {\n        return this.handleRemoveDateMultiple(item, field)\n      }\n      this.handleRemoveItemMultiple(item)\n    },\n\n    handleRemoveDateMultiple({ items }, field) {\n      this.currentCriteria = this.currentCriteria.map(criterion => criterion.field.name === field.name ? ({\n        ...criterion,\n        value: items\n      }) : criterion)\n\n      this.$emit('change', cloneDeep(this.currentCriteria))\n    },\n\n    handleRemoveItemMultiple(item) {\n      const criteria = this.currentCriteria.find(criterion => criterion.field.multiple && criterion.value.includes(item.removed))\n      const index = criteria.value.indexOf(item.removed)\n      criteria.value.splice(index, 1)\n      this.$emit('change', cloneDeep(this.currentCriteria))\n    },\n\n    setMaxSize() {\n      this.$nextTick(() => {\n        const criteriaDiv = document.querySelector('div.me-criteria')\n        const criteriaFieldsContainer = document.querySelector('div.me-criteria__fields')\n        /* istanbul ignore next */\n        if (criteriaDiv && criteriaFieldsContainer) {\n          const dropdownRect = criteriaDiv.getBoundingClientRect()\n          if (dropdownRect.height >= document.documentElement.clientHeight * 0.8 - dropdownRect.y) {\n            criteriaFieldsContainer.style.maxHeight = `calc(85vh - ${dropdownRect.y}px)`\n            criteriaFieldsContainer.style.minHeight = this.currentCriteria.length ? '48px' : 'unset'\n            criteriaFieldsContainer.style.overflowY = 'auto'\n            criteriaFieldsContainer.style.overflowX = 'hidden'\n            criteriaFieldsContainer.style.paddingRight = '10px'\n          } else {\n            criteriaFieldsContainer.style.maxHeight = 'unset'\n            criteriaFieldsContainer.style.minHeight = 'unset'\n            criteriaFieldsContainer.style.overflowY = 'unset'\n            criteriaFieldsContainer.style.overflowX = 'unset'\n            criteriaFieldsContainer.style.paddingRight = 'initial'\n          }\n        }\n      })\n    },\n    setOperatorWidth() {\n      if (this.operatorsClass !== 'col-auto') return\n\n      this.$nextTick(() => {\n        this.operatorWidth = this.currentCriteria.reduce((acc, criterion) => ({ ...acc, [criterion.field.name]: this.getOperatorWidth(criterion) }), {})\n      })\n    },\n    getOperatorWidth(criterion) {\n      const operatorEls = this.$refs[`operator_${criterion.field.name}`]\n      if (!operatorEls?.length) return 'auto'\n\n      return `${operatorEls[0].$el.clientWidth}px`\n    },\n    isSelectableDisabled(index) {\n      if (!this.maxItems) return false\n      return this.currentCriteria[index]?.value?.length >= this.maxItems\n    },\n\n    setInlineLabelHasEllipsis(event, item) {\n      this.inlineLabelHasEllipsis = event.target.offsetHeight < event.target.scrollHeight\n      this.labelTooltip = this.getLabelTooltip(item)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-criteria {\n  &__fields {\n    &__input-group {\n      .me-input {\n        width: 100%;\n      }\n\n      .remove {\n        border-radius: 100%;\n        cursor: pointer;\n        display: flex;\n        justify-content: center;\n        align-items: center;\n        margin-left: 4px;\n\n        &.sm {\n          width: 32px;\n          height: 32px;\n        }\n\n        &.md {\n          width: 40px;\n          height: 40px;\n        }\n\n        &.lg {\n          width: 48px;\n          height: 48px;\n        }\n\n        &:hover {\n          background-color: var(--me-danger-7);\n        }\n      }\n\n      &__label {\n        font-size: 12px;\n        color: var(--me-gray-2);\n\n        label {\n          margin-bottom: 4px;\n        }\n\n        &--inline {\n          display: flex;\n          align-items: center;\n          width: 72px;\n          min-width: 72px;\n          margin-right: 8px;\n\n          label {\n            margin-bottom: 0;\n            overflow: hidden;\n            display: -webkit-box;\n            -webkit-line-clamp: 2;\n            -webkit-box-orient: vertical;\n            text-overflow: ellipsis;\n          }\n        }\n      }\n\n      &__field-condition {\n        white-space: nowrap;\n\n        .dropdown-icon {\n          display: flex;\n          justify-content: center;\n          align-items: center;\n          margin-right: 4px;\n          width: 20px;\n          height: 20px;\n          border-radius: 50%;\n          background-color: var(--me-gray-6);\n        }\n\n        ::v-deep .form-group__invalid-feedback {\n          height: 100%;\n        }\n\n        ::v-deep .dropdown {\n          width: 100%;\n\n          &.show {\n            .dropdown-toggle {\n              background-color: var(--me-gray-6) !important;\n            }\n          }\n\n          button:first-child {\n            display: flex;\n            align-items: center;\n            border-right: none;\n            border-radius: 8px 0 0 8px;\n            border-color: var(--me-gray-6) !important;\n            background-color: var(--me-bg-gray);\n\n            span {\n              display: flex;\n              align-items: center;\n              justify-content: center;\n              width: 24px;\n              height: 24px;\n              margin-right: 8px;\n              background-color: var(--me-gray-6);\n              border-radius: 50%;\n              font-size: 12px;\n            }\n\n            &:not(:disabled):active {\n              background-color: var(--me-gray-6) !important;\n            }\n          }\n        }\n      }\n\n      &__input--with-operators &__input__type {\n        ::v-deep .me-date-picker .mx-datepicker .mx-input-wrapper input,\n        ::v-deep .me-select .vs__dropdown-toggle,\n        ::v-deep .me-text-input input,\n        ::v-deep .input-multiple-container,\n        ::v-deep input {\n          border-top-left-radius: 0px !important;\n          border-bottom-left-radius: 0px !important;\n        }\n      }\n\n      ::v-deep .input-multiple-container {\n        &.hideOperators {\n          border-top-left-radius: 8px;\n          border-bottom-left-radius: 8px;\n        }\n      }\n\n      ::v-deep .form-group.inline {\n        margin-left: 82px;\n      }\n    }\n  }\n\n  &__add-criterion {\n    display: flex;\n    flex-direction: column;\n  }\n}\n</style>\n",".me-criteria__fields__input-group .me-input {\n  width: 100%;\n}\n.me-criteria__fields__input-group .remove {\n  border-radius: 100%;\n  cursor: pointer;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-left: 4px;\n}\n.me-criteria__fields__input-group .remove.sm {\n  width: 32px;\n  height: 32px;\n}\n.me-criteria__fields__input-group .remove.md {\n  width: 40px;\n  height: 40px;\n}\n.me-criteria__fields__input-group .remove.lg {\n  width: 48px;\n  height: 48px;\n}\n.me-criteria__fields__input-group .remove:hover {\n  background-color: var(--me-danger-7);\n}\n.me-criteria__fields__input-group__label {\n  font-size: 12px;\n  color: var(--me-gray-2);\n}\n.me-criteria__fields__input-group__label label {\n  margin-bottom: 4px;\n}\n.me-criteria__fields__input-group__label--inline {\n  display: flex;\n  align-items: center;\n  width: 72px;\n  min-width: 72px;\n  margin-right: 8px;\n}\n.me-criteria__fields__input-group__label--inline label {\n  margin-bottom: 0;\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n}\n.me-criteria__fields__input-group__field-condition {\n  white-space: nowrap;\n}\n.me-criteria__fields__input-group__field-condition .dropdown-icon {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-right: 4px;\n  width: 20px;\n  height: 20px;\n  border-radius: 50%;\n  background-color: var(--me-gray-6);\n}\n.me-criteria__fields__input-group__field-condition ::v-deep .form-group__invalid-feedback {\n  height: 100%;\n}\n.me-criteria__fields__input-group__field-condition ::v-deep .dropdown {\n  width: 100%;\n}\n.me-criteria__fields__input-group__field-condition ::v-deep .dropdown.show .dropdown-toggle {\n  background-color: var(--me-gray-6) !important;\n}\n.me-criteria__fields__input-group__field-condition ::v-deep .dropdown button:first-child {\n  display: flex;\n  align-items: center;\n  border-right: none;\n  border-radius: 8px 0 0 8px;\n  border-color: var(--me-gray-6) !important;\n  background-color: var(--me-bg-gray);\n}\n.me-criteria__fields__input-group__field-condition ::v-deep .dropdown button:first-child span {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 24px;\n  height: 24px;\n  margin-right: 8px;\n  background-color: var(--me-gray-6);\n  border-radius: 50%;\n  font-size: 12px;\n}\n.me-criteria__fields__input-group__field-condition ::v-deep .dropdown button:first-child:not(:disabled):active {\n  background-color: var(--me-gray-6) !important;\n}\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type ::v-deep .me-date-picker .mx-datepicker .mx-input-wrapper input,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type ::v-deep .me-select .vs__dropdown-toggle,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type ::v-deep .me-text-input input,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type ::v-deep .input-multiple-container,\n.me-criteria__fields__input-group__input--with-operators .me-criteria__fields__input-group__input__type ::v-deep input {\n  border-top-left-radius: 0px !important;\n  border-bottom-left-radius: 0px !important;\n}\n.me-criteria__fields__input-group ::v-deep .input-multiple-container.hideOperators {\n  border-top-left-radius: 8px;\n  border-bottom-left-radius: 8px;\n}\n.me-criteria__fields__input-group ::v-deep .form-group.inline {\n  margin-left: 82px;\n}\n.me-criteria__add-criterion {\n  display: flex;\n  flex-direction: column;\n}\n\n/*# sourceMappingURL=Criteria.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$O = "data-v-29fff4fd";
  /* module identifier */
  const __vue_module_identifier__$O = undefined;
  /* functional template */
  const __vue_is_functional_template__$O = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$O, staticRenderFns: __vue_staticRenderFns__$O },
    __vue_inject_styles__$O,
    __vue_script__$O,
    __vue_scope_id__$O,
    __vue_is_functional_template__$O,
    __vue_module_identifier__$O,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$P = {
  name: 'MeFilterActions',
  components: {
    Button: __vue_component__$6,
    Modal: __vue_component__$f,
    TextInput: __vue_component__$p,
    FormGroup: __vue_component__$i
  },

  props: {
    filterName: {
      type: String,
      default: ''
    },
    hideOperators: {
      type: Boolean,
      default: false
    },
    validateFiltersName: {
      type: Boolean,
      default: false
    },
    criteria: {
      type: Array,
      required: true
    },
    busy: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      filtersName: this.filterName,
      filtersNameWasTouched: false
    }
  },

  computed: {
    modalActions() {
      return [
        {
          label: this.$toolkit.t('filter.save'),
          disabled: this.shouldDisableSaveButton,
          click: close => this.emitSave(close)
        },
        {
          label: this.$toolkit.t('filter.cancel'),
          click: close => close()
        }
      ]
    },

    isEmptyFiltersName() {
      return this.filtersName.trim() === ''
    },

    getFiltersNameState() {
      if (!this.filtersNameWasTouched) return null

      if (this.validateFiltersName) return !this.isEmptyFiltersName

      return null
    },

    shouldDisableSaveButton() {
      return this.validateFiltersName && this.isEmptyFiltersName && !this.getFiltersNameState
    }
  },

  watch: {
    filterName(value) {
      this.filtersName = value;
    }
  },

  methods: {
    updateFiltersNameTouched() {
      if (this.filtersName) {
        this.filtersNameWasTouched = true;
      }
    },

    emitSave(cb) {
      cb();
      this.$emit('save', { filtersName: this.filtersName, criteria: this.criteria });
    }
  }
};

/* script */
const __vue_script__$P = script$P;

/* template */
var __vue_render__$P = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticStyle: { display: "inline" } },
    [
      _c(
        "modal",
        {
          attrs: {
            id: "customFilter",
            busy: _vm.busy,
            title: _vm.$toolkit.t("filterActions.modalTitle"),
            buttons: _vm.modalActions
          },
          on: {
            shown: function($event) {
              return _vm.$refs.filtersNameRef.$el.focus()
            }
          }
        },
        [
          _c(
            "form-group",
            { attrs: { label: _vm.$toolkit.t("filterActions.inputLabel") } },
            [
              _c("text-input", {
                ref: "filtersNameRef",
                attrs: { state: _vm.getFiltersNameState },
                on: { blur: _vm.updateFiltersNameTouched },
                model: {
                  value: _vm.filtersName,
                  callback: function($$v) {
                    _vm.filtersName = $$v;
                  },
                  expression: "filtersName"
                }
              })
            ],
            1
          )
        ],
        1
      ),
      _vm._v(" "),
      _c("Button", {
        directives: [
          {
            name: "me-modal",
            rawName: "v-me-modal.customFilter",
            modifiers: { customFilter: true }
          },
          {
            name: "me-tooltip",
            rawName: "v-me-tooltip.hover",
            value: _vm.$toolkit.t("criteria.save"),
            expression: "$toolkit.t('criteria.save')",
            modifiers: { hover: true }
          }
        ],
        attrs: {
          variant: "light-secondary",
          size: "sm",
          icon: "me-icon icon-save"
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$P = [];
__vue_render__$P._withStripped = true;

  /* style */
  const __vue_inject_styles__$P = undefined;
  /* scoped */
  const __vue_scope_id__$P = undefined;
  /* module identifier */
  const __vue_module_identifier__$P = undefined;
  /* functional template */
  const __vue_is_functional_template__$P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$P, staticRenderFns: __vue_staticRenderFns__$P },
    __vue_inject_styles__$P,
    __vue_script__$P,
    __vue_scope_id__$P,
    __vue_is_functional_template__$P,
    __vue_module_identifier__$P,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$Q = {
  name: 'MeCriteriaAdd',
  components: {
    TextInput: __vue_component__$p,
    DropDown: __vue_component__$8,
    DropdownItem: __vue_component__$2,
    Icon: __vue_component__
  },
  props: {
    onAdd: {
      type: Function,
      required: true
    },
    fields: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      currentFieldSearch: ''
    }
  },

  methods: {
    getFilterFieldsOptions(fieldsList, searchLabel) {
      if (!searchLabel) return fieldsList

      return fieldsList.filter(field => this.filterFields(field.label, searchLabel))
    },

    filterFields(fieldLabel, search) {
      const normalizedLabel = this.normalizeString(fieldLabel);
      const normalizedSearch = this.normalizeString(search);

      const hasSearchAccents = search !== normalizedSearch;
      const labelToCompare = hasSearchAccents ? fieldLabel : normalizedLabel;
      return (labelToCompare || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1
    },

    normalizeString(text) {
      return text.normalize('NFD').replace(/[^a-zA-Z\s]/g, '')
    }
  }
};

/* script */
const __vue_script__$Q = script$Q;

/* template */
var __vue_render__$Q = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "criteria-add", attrs: { "data-cy": "criteriaAdd" } },
    [
      _vm.fields.length
        ? _c(
            "DropDown",
            {
              attrs: {
                block: "",
                size: "sm",
                variant: "outline-primary",
                "no-caret": "",
                "no-flip": "",
                "data-cy": "criteriaAddDropdown"
              },
              on: {
                hide: function() {
                  return (_vm.currentFieldSearch = "")
                }
              },
              scopedSlots: _vm._u(
                [
                  {
                    key: "button-content",
                    fn: function() {
                      return [
                        _c("Icon", {
                          staticStyle: { "margin-right": "7px" },
                          attrs: { icon: "me-icon icon-plus" }
                        }),
                        _vm._v(
                          _vm._s(_vm.$toolkit.t("search.addFilter")) + "\n    "
                        )
                      ]
                    },
                    proxy: true
                  }
                ],
                null,
                false,
                784163437
              )
            },
            [
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "criteria-add__field-search" },
                [
                  _c("TextInput", {
                    attrs: {
                      placeholder: _vm.$toolkit.t("search.searchFilter"),
                      type: "search",
                      "data-cy": "criteriaAddTextInput"
                    },
                    nativeOn: {
                      click: function($event) {
                        $event.stopPropagation();
                      }
                    },
                    model: {
                      value: _vm.currentFieldSearch,
                      callback: function($$v) {
                        _vm.currentFieldSearch = $$v;
                      },
                      expression: "currentFieldSearch"
                    }
                  })
                ],
                1
              ),
              _vm._v(" "),
              _vm._l(
                _vm.getFilterFieldsOptions(_vm.fields, _vm.currentFieldSearch),
                function(filteredField) {
                  return _c("DropdownItem", {
                    key: filteredField.name || filteredField.label,
                    attrs: {
                      label: filteredField.label,
                      "data-cy": "criteriaAddItem"
                    },
                    on: {
                      click: function() {
                        return _vm.onAdd(filteredField)
                      }
                    }
                  })
                }
              )
            ],
            2
          )
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$Q = [];
__vue_render__$Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$Q = function (inject) {
    if (!inject) return
    inject("data-v-2413e7d0_0", { source: ".criteria-add__field-search[data-v-2413e7d0] {\n  width: 100%;\n  padding: 8px 16px 4px;\n  position: sticky;\n  top: -8px;\n  background-color: var(--me-white);\n  z-index: 1;\n}\n.criteria-add[data-v-2413e7d0]  .dropdown-menu {\n  width: 100%;\n  max-height: 300px !important;\n}\n\n/*# sourceMappingURL=CriteriaAdd.vue.map */", map: {"version":3,"sources":["/app/build/src/components/CriteriaAdd.vue","CriteriaAdd.vue"],"names":[],"mappings":"AA6FA;EACA,WAAA;EACA,qBAAA;EACA,gBAAA;EACA,SAAA;EACA,iCAAA;EACA,UAAA;AC5FA;AD+FA;EACA,WAAA;EACA,4BAAA;AC7FA;;AAEA,0CAA0C","file":"CriteriaAdd.vue","sourcesContent":["<template>\n  <div class=\"criteria-add\" data-cy=\"criteriaAdd\">\n    <DropDown\n      v-if=\"fields.length\"\n      @hide=\"() => (currentFieldSearch = '')\"\n      block\n      size=\"sm\"\n      variant=\"outline-primary\"\n      no-caret\n      no-flip\n      data-cy=\"criteriaAddDropdown\"\n    >\n      <template v-slot:button-content>\n        <Icon icon=\"me-icon icon-plus\" style=\"margin-right: 7px\" />{{ $toolkit.t('search.addFilter') }}\n      </template>\n\n      <div class=\"criteria-add__field-search\">\n        <TextInput\n          v-model=\"currentFieldSearch\"\n          :placeholder=\"$toolkit.t('search.searchFilter')\"\n          @click.native.stop\n          type=\"search\"\n          data-cy=\"criteriaAddTextInput\"\n        />\n      </div>\n\n      <DropdownItem\n        v-for=\"filteredField in getFilterFieldsOptions(fields, currentFieldSearch)\"\n        :key=\"filteredField.name || filteredField.label\"\n        :label=\"filteredField.label\"\n        @click=\"() => onAdd(filteredField)\"\n        data-cy=\"criteriaAddItem\"\n      />\n    </DropDown>\n  </div>\n</template>\n\n<script>\nimport TextInput from './TextInput.vue'\nimport DropDown from './dropdown/DropDown.vue'\nimport DropdownItem from './dropdown/DropdownItem.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeCriteriaAdd',\n  components: {\n    TextInput,\n    DropDown,\n    DropdownItem,\n    Icon\n  },\n  props: {\n    onAdd: {\n      type: Function,\n      required: true\n    },\n    fields: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      currentFieldSearch: ''\n    }\n  },\n\n  methods: {\n    getFilterFieldsOptions(fieldsList, searchLabel) {\n      if (!searchLabel) return fieldsList\n\n      return fieldsList.filter(field => this.filterFields(field.label, searchLabel))\n    },\n\n    filterFields(fieldLabel, search) {\n      const normalizedLabel = this.normalizeString(fieldLabel)\n      const normalizedSearch = this.normalizeString(search)\n\n      const hasSearchAccents = search !== normalizedSearch\n      const labelToCompare = hasSearchAccents ? fieldLabel : normalizedLabel\n      return (labelToCompare || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1\n    },\n\n    normalizeString(text) {\n      return text.normalize('NFD').replace(/[^a-zA-Z\\s]/g, '')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .criteria-add {\n    &__field-search {\n      width: 100%;\n      padding: 8px 16px 4px;\n      position: sticky;\n      top: -8px;\n      background-color: var(--me-white);\n      z-index: 1;\n    }\n\n    ::v-deep .dropdown-menu {\n      width: 100%;\n      max-height: 300px !important;\n    }\n  }\n</style>\n",".criteria-add__field-search {\n  width: 100%;\n  padding: 8px 16px 4px;\n  position: sticky;\n  top: -8px;\n  background-color: var(--me-white);\n  z-index: 1;\n}\n.criteria-add ::v-deep .dropdown-menu {\n  width: 100%;\n  max-height: 300px !important;\n}\n\n/*# sourceMappingURL=CriteriaAdd.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$Q = "data-v-2413e7d0";
  /* module identifier */
  const __vue_module_identifier__$Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$Q = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Q, staticRenderFns: __vue_staticRenderFns__$Q },
    __vue_inject_styles__$Q,
    __vue_script__$Q,
    __vue_scope_id__$Q,
    __vue_is_functional_template__$Q,
    __vue_module_identifier__$Q,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$R = {
  name: 'MeCriteriaBarForm',
  components: {
    Button: __vue_component__$6,
    Criteria: __vue_component__$O,
    CriteriaAdd: __vue_component__$Q
  },

  props: {
    fields: {
      type: Array,
      required: true
    },
    criterion: {
      type: Object,
      default: null
    },
    hideOperators: {
      type: Boolean,
      default: false
    },
    context: {
      type: Object,
      default: null
    },
    autoFlipSelect: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      opened: false,
      model: []
    }
  },

  computed: {
    title() {
      return this.criterion === null ? this.$toolkit.t('criteria.add') : this.$toolkit.t('criteria.edit')
    },

    confirmTitle() {
      return this.criterion === null ? this.$toolkit.t('criteria.add') : this.$toolkit.t('criteria.save')
    },

    max() {
      return this.criterion === null ? null : 1
    }
  },

  watch: {
    criterion(val) {
      this.model = [];
      if (val) {
        this.model.push(cloneDeep_1(val));
      }
    }
  },

  methods: {
    show() {
      if (this.criterion !== null) {
        this.model.push(cloneDeep_1(this.criterion));
      }

      this.opened = true;
    },

    close() {
      this.model = [];
      this.opened = false;
    },

    cancel() {
      this.close();
    },

    confirm() {
      this.$emit('change', {
        criterion: this.criterion,
        items: cloneDeep_1(this.model)
      });

      this.close();
    },

    onCriteriaChange(newVal) {
      this.$emit('onCriteriaChange', newVal);
    }
  }
};

/* script */
const __vue_script__$R = script$R;

/* template */
var __vue_render__$R = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-criteria-bar-form", class: { opened: _vm.opened } },
    [
      _vm.opened
        ? _c("div", { staticClass: "form" }, [
            _c(
              "div",
              { staticClass: "header" },
              [
                _vm._v("\n      " + _vm._s(_vm.title) + "\n      "),
                _c("Button", {
                  attrs: {
                    variant: "link",
                    size: "sm",
                    icon: "me-icon icon-times"
                  },
                  on: { click: _vm.close }
                })
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              [
                _c("label", { staticClass: "label-title" }, [
                  _vm._v(
                    "\n        " +
                      _vm._s(_vm.$toolkit.t("search.filters")) +
                      "\n      "
                  )
                ]),
                _vm._v(" "),
                _c("Criteria", {
                  attrs: {
                    fields: _vm.fields,
                    max: _vm.max,
                    context: _vm.context,
                    "hide-remove": _vm.criterion !== null,
                    "hide-operators": _vm.hideOperators,
                    autoFlipSelect: _vm.autoFlipSelect,
                    size: "sm",
                    "show-input": "",
                    opened: "",
                    inline: ""
                  },
                  on: { change: _vm.onCriteriaChange },
                  scopedSlots: _vm._u(
                    [
                      {
                        key: "criteria-add",
                        fn: function(ref) {
                          var filteredFields = ref.fields;
                          var onAdd = ref.onAdd;
                          return [
                            _c("CriteriaAdd", {
                              attrs: { fields: filteredFields, onAdd: onAdd }
                            })
                          ]
                        }
                      }
                    ],
                    null,
                    false,
                    672401155
                  ),
                  model: {
                    value: _vm.model,
                    callback: function($$v) {
                      _vm.model = $$v;
                    },
                    expression: "model"
                  }
                })
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "footer" },
              [
                _c(
                  "Button",
                  {
                    attrs: { variant: "light-primary", size: "sm" },
                    on: { click: _vm.cancel }
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.$toolkit.t("criteria.cancel")) +
                        "\n      "
                    )
                  ]
                ),
                _vm._v(" "),
                _c(
                  "Button",
                  {
                    attrs: { variant: "primary", size: "sm" },
                    on: { click: _vm.confirm }
                  },
                  [_vm._v("\n        " + _vm._s(_vm.confirmTitle) + "\n      ")]
                )
              ],
              1
            )
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("div", { staticClass: "backdrop", on: { click: _vm.close } })
    ]
  )
};
var __vue_staticRenderFns__$R = [];
__vue_render__$R._withStripped = true;

  /* style */
  const __vue_inject_styles__$R = function (inject) {
    if (!inject) return
    inject("data-v-2fdc8591_0", { source: "div.me-criteria-bar-form > div.form[data-v-2fdc8591] {\n  background-color: var(--me-white);\n  border-radius: 0.2rem;\n  box-shadow: var(--me-shadow-level-1);\n  margin-top: -10px;\n  max-width: 500px;\n  min-width: 300px;\n  position: absolute;\n  width: 100%;\n  z-index: 1061;\n}\ndiv.me-criteria-bar-form > div.form > div[data-v-2fdc8591] {\n  padding: 1rem;\n}\ndiv.me-criteria-bar-form > div.form .label-title[data-v-2fdc8591] {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  line-height: 16px;\n}\ndiv.me-criteria-bar-form > div.form .header[data-v-2fdc8591] {\n  border-bottom: 1px solid #eee;\n}\ndiv.me-criteria-bar-form > div.form .header button[data-v-2fdc8591] {\n  color: var(--me-gray-2);\n  position: absolute;\n  right: 1rem;\n  top: 13px;\n}\ndiv.me-criteria-bar-form > div.form .footer[data-v-2fdc8591] {\n  border-top: 1px solid #eee;\n  text-align: right;\n}\ndiv.me-criteria-bar-form > div.backdrop[data-v-2fdc8591] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1050;\n}\ndiv.me-criteria-bar-form.opened > div.backdrop[data-v-2fdc8591] {\n  display: block;\n  opacity: 0.1;\n}\n\n/*# sourceMappingURL=CriteriaBarForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/CriteriaBarForm.vue","CriteriaBarForm.vue"],"names":[],"mappings":"AA4JA;EACA,iCAAA;EACA,qBAAA;EACA,oCAAA;EACA,iBAAA;EACA,gBAAA;EACA,gBAAA;EACA,kBAAA;EACA,WAAA;EACA,aAAA;AC3JA;AD6JA;EACA,aAAA;AC3JA;AD8JA;EACA,uBAAA;EACA,eAAA;EACA,iBAAA;AC5JA;AD+JA;EACA,6BAAA;AC7JA;AD+JA;EACA,uBAAA;EACA,kBAAA;EACA,WAAA;EACA,SAAA;AC7JA;ADiKA;EACA,0BAAA;EACA,iBAAA;AC/JA;ADmKA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,yBAAA;EACA,mBAAA;EACA,aAAA;ACjKA;ADqKA;EACA,cAAA;EACA,YAAA;ACnKA;;AAEA,8CAA8C","file":"CriteriaBarForm.vue","sourcesContent":["<template>\n  <div :class=\"{ opened: opened }\" class=\"me-criteria-bar-form\">\n    <div v-if=\"opened\" class=\"form\">\n      <div class=\"header\">\n        {{ title }}\n        <Button\n          @click=\"close\"\n          variant=\"link\"\n          size=\"sm\"\n          icon=\"me-icon icon-times\"\n        />\n      </div>\n\n      <div>\n        <label class=\"label-title\">\n          {{ $toolkit.t('search.filters') }}\n        </label>\n\n        <Criteria\n          v-model=\"model\"\n          :fields=\"fields\"\n          :max=\"max\"\n          :context=\"context\"\n          :hide-remove=\"criterion !== null\"\n          :hide-operators=\"hideOperators\"\n          :autoFlipSelect=\"autoFlipSelect\"\n          @change=\"onCriteriaChange\"\n          size=\"sm\"\n          show-input\n          opened\n          inline\n        >\n          <template v-slot:criteria-add=\"{fields: filteredFields, onAdd}\">\n            <CriteriaAdd\n              :fields=\"filteredFields\"\n              :onAdd=\"onAdd\"\n            />\n          </template>\n        </Criteria>\n      </div>\n      <div class=\"footer\">\n        <Button @click=\"cancel\" variant=\"light-primary\" size=\"sm\">\n          {{ $toolkit.t('criteria.cancel') }}\n        </Button>\n        <Button @click=\"confirm\" variant=\"primary\" size=\"sm\">\n          {{ confirmTitle }}\n        </Button>\n      </div>\n    </div>\n    <div @click=\"close\" class=\"backdrop\" />\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport Button from './Button.vue'\nimport Criteria from './Criteria.vue'\nimport CriteriaAdd from './CriteriaAdd.vue'\n\nexport default {\n  name: 'MeCriteriaBarForm',\n  components: {\n    Button,\n    Criteria,\n    CriteriaAdd\n  },\n\n  props: {\n    fields: {\n      type: Array,\n      required: true\n    },\n    criterion: {\n      type: Object,\n      default: null\n    },\n    hideOperators: {\n      type: Boolean,\n      default: false\n    },\n    context: {\n      type: Object,\n      default: null\n    },\n    autoFlipSelect: {\n      type: Boolean,\n      default: true\n    }\n  },\n\n  data() {\n    return {\n      opened: false,\n      model: []\n    }\n  },\n\n  computed: {\n    title() {\n      return this.criterion === null ? this.$toolkit.t('criteria.add') : this.$toolkit.t('criteria.edit')\n    },\n\n    confirmTitle() {\n      return this.criterion === null ? this.$toolkit.t('criteria.add') : this.$toolkit.t('criteria.save')\n    },\n\n    max() {\n      return this.criterion === null ? null : 1\n    }\n  },\n\n  watch: {\n    criterion(val) {\n      this.model = []\n      if (val) {\n        this.model.push(cloneDeep(val))\n      }\n    }\n  },\n\n  methods: {\n    show() {\n      if (this.criterion !== null) {\n        this.model.push(cloneDeep(this.criterion))\n      }\n\n      this.opened = true\n    },\n\n    close() {\n      this.model = []\n      this.opened = false\n    },\n\n    cancel() {\n      this.close()\n    },\n\n    confirm() {\n      this.$emit('change', {\n        criterion: this.criterion,\n        items: cloneDeep(this.model)\n      })\n\n      this.close()\n    },\n\n    onCriteriaChange(newVal) {\n      this.$emit('onCriteriaChange', newVal)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-criteria-bar-form {\n    > div.form {\n      background-color: var(--me-white);\n      border-radius: 0.2rem;\n      box-shadow: var(--me-shadow-level-1);\n      margin-top: -10px;\n      max-width: 500px;\n      min-width: 300px;\n      position: absolute;\n      width: 100%;\n      z-index: 1061;\n\n      > div {\n        padding: 1rem;\n      }\n\n      .label-title {\n        color: var(--me-gray-4);\n        font-size: 12px;\n        line-height: 16px;\n      }\n\n      .header {\n        border-bottom: 1px solid #eee;\n\n        button {\n          color: var(--me-gray-2);\n          position: absolute;\n          right: 1rem;\n          top: 13px;\n        }\n      }\n\n      .footer {\n        border-top: 1px solid #eee;\n        text-align: right;\n      }\n    }\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      transition-duration: 0.2s;\n      transition: opacity;\n      z-index: 1050;\n    }\n\n    &.opened {\n      > div.backdrop {\n        display: block;\n        opacity: 0.1;\n      }\n    }\n  }\n</style>\n","div.me-criteria-bar-form > div.form {\n  background-color: var(--me-white);\n  border-radius: 0.2rem;\n  box-shadow: var(--me-shadow-level-1);\n  margin-top: -10px;\n  max-width: 500px;\n  min-width: 300px;\n  position: absolute;\n  width: 100%;\n  z-index: 1061;\n}\ndiv.me-criteria-bar-form > div.form > div {\n  padding: 1rem;\n}\ndiv.me-criteria-bar-form > div.form .label-title {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  line-height: 16px;\n}\ndiv.me-criteria-bar-form > div.form .header {\n  border-bottom: 1px solid #eee;\n}\ndiv.me-criteria-bar-form > div.form .header button {\n  color: var(--me-gray-2);\n  position: absolute;\n  right: 1rem;\n  top: 13px;\n}\ndiv.me-criteria-bar-form > div.form .footer {\n  border-top: 1px solid #eee;\n  text-align: right;\n}\ndiv.me-criteria-bar-form > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1050;\n}\ndiv.me-criteria-bar-form.opened > div.backdrop {\n  display: block;\n  opacity: 0.1;\n}\n\n/*# sourceMappingURL=CriteriaBarForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$R = "data-v-2fdc8591";
  /* module identifier */
  const __vue_module_identifier__$R = undefined;
  /* functional template */
  const __vue_is_functional_template__$R = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$R, staticRenderFns: __vue_staticRenderFns__$R },
    __vue_inject_styles__$R,
    __vue_script__$R,
    __vue_scope_id__$R,
    __vue_is_functional_template__$R,
    __vue_module_identifier__$R,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$S = {
  name: 'MeCriteriaBar',
  components: {
    Button: __vue_component__$6,
    Icon: __vue_component__,
    FilterActions: __vue_component__$P,
    CriteriaBarForm: __vue_component__$R
  },
  props: {
    filterName: {
      type: String,
      default: ''
    },
    fields: {
      type: Array,
      required: true
    },
    busy: {
      type: Boolean,
      default: false
    },
    term: {
      type: String,
      default: ''
    },
    criteria: {
      type: Array,
      required: true
    },
    showSave: {
      type: Boolean,
      default: false
    },
    showRefine: {
      type: Boolean,
      default: false
    },
    showAdd: {
      type: Boolean,
      default: true
    },
    hideOperators: {
      type: Boolean,
      default: false
    },
    autoFlipSelect: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentTerm: this.term,
      currentCriteria: cloneDeep_1(this.criteria),
      criterion: null
    }
  },
  computed: {
    isEmpty() {
      return !this.currentTerm && isEmpty_1(this.currentCriteria)
    },
    selectableFields() {
      if (this.criterion) {
        return this.fields.filter(field => this.criterion.field.name === field.name)
      }

      return this.fields.filter(field => field.isFixed || !this.currentCriteria.map(criteria => criteria.field.name).includes(field.name))
    },
    showActions() {
      return this.showSave && this.currentCriteria.length > 0
    },
    context() {
      return {
        criteria: this.currentCriteria
      }
    }
  },
  watch: {
    term(val) {
      this.currentTerm = val;
    },
    criteria(val) {
      this.currentCriteria = cloneDeep_1(val || []);
    },
    currentCriteria(val) {
      this.currentCriteria = val;
      this.$emit('onCriteriaChange', { newCriteria: val });
    }
  },
  methods: {
    add() {
      this.criterion = null;
      this.$refs.form.show();
    },
    editItem(index) {
      this.criterion = this.currentCriteria[index];
      this.criterion._index = index;
      this.$refs.form.show();
    },
    onChange({ items, criterion }) {
      if (items[0]?.field.isFixed) {
        criterion = {
          _index: this.currentCriteria.findIndex(criteria => criteria.field.name === items[0].field.name)
        };
      }
      if (items.length === 0) {
        return
      }
      if (criterion && criterion._index > -1) {
        this.resetDependents(items[0]);
        this.currentCriteria.splice(criterion._index, 1, items[0]);
      } else {
        items.forEach(item => this.currentCriteria.push(item));
      }
      this.dispatchChange();
    },
    resetDependents(criterion) {
      this.currentCriteria.filter(d => d.field.parent === criterion.field.name).forEach((item) => {
        item.value = null;
      });
    },
    refine() {
      this.$emit('refine');
    },
    removeItem(index) {
      this.resetDependents(this.currentCriteria[index]);
      this.currentCriteria.splice(index, 1);
      this.dispatchChange();
    },
    clearTerm() {
      this.currentTerm = '';
      this.dispatchChange();
    },
    reset() {
      this.currentTerm = '';
      this.currentCriteria = [];
      this.dispatchChange();
      this.$emit('reset');
    },
    dispatchChange() {
      this.$emit('change', {
        term: this.currentTerm,
        criteria: this.currentCriteria
      });
    },
    formatItemValue(item) {
      if (isArray_1(item.value)) {
        return item.value.map((value) => {
          return this.formatItemValue({ ...item, value })
        }).join(', ')
      }

      if (item.field.type === 'object' && item.value) {
        return item.value[item.field.options.label]
      }

      if (item.field.type === 'boolean' && item.value?.name) {
        return item.value.name
      }

      if (item.field.type === 'date' && item.value) {
        return this.$options.filters['me-date'](item.value, this.$toolkit.locale)
      }

      return item.value
    },
    saveFields({ filtersName, criteria }) {
      this.$emit('saveFilters', {
        filtersName,
        criteria
      });
    },
    onCriteriaChange(newVal) {
      this.$emit('onCriteriaChange', { newCriteria: newVal });
    }
  }
};

/* script */
const __vue_script__$S = script$S;

/* template */
var __vue_render__$S = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c("div", { staticClass: "me-criteria-bar" }, [
        _c("div", { staticClass: "items" }, [
          _c(
            "ul",
            [
              _vm.currentTerm
                ? _c("li", [
                    _c(
                      "span",
                      { staticClass: "badge badge-secondary item" },
                      [
                        _c("Icon", { attrs: { icon: "me-icon icon-search" } }),
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.currentTerm) +
                            "\n            "
                        ),
                        _c(
                          "div",
                          { staticClass: "actions" },
                          [
                            _c("Icon", {
                              directives: [
                                {
                                  name: "me-tooltip",
                                  rawName: "v-me-tooltip.hover",
                                  value: _vm.$toolkit.t("criteria.remove"),
                                  expression: "$toolkit.t('criteria.remove')",
                                  modifiers: { hover: true }
                                }
                              ],
                              attrs: { icon: "me-icon icon-trash" },
                              on: {
                                click: function($event) {
                                  return _vm.clearTerm()
                                }
                              }
                            })
                          ],
                          1
                        )
                      ],
                      1
                    )
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm._l(_vm.currentCriteria, function(item, index) {
                return _c(
                  "li",
                  { key: item.key },
                  [
                    item.field && !item.field.isCustom
                      ? _c(
                          "span",
                          { staticClass: "badge badge-secondary item" },
                          [
                            item.field.icon
                              ? _c("i", { class: item.field.icon })
                              : _vm._e(),
                            _vm._v(
                              "\n            " +
                                _vm._s(item.field.label) +
                                " " +
                                _vm._s(
                                  item.operator.symbol || item.operator.label
                                ) +
                                " " +
                                _vm._s(_vm.formatItemValue(item)) +
                                "\n            "
                            ),
                            _c(
                              "div",
                              { staticClass: "actions" },
                              [
                                !item.notEditable
                                  ? _c("Icon", {
                                      directives: [
                                        {
                                          name: "me-tooltip",
                                          rawName: "v-me-tooltip.hover",
                                          value: _vm.$toolkit.t(
                                            "criteria.edit"
                                          ),
                                          expression:
                                            "$toolkit.t('criteria.edit')",
                                          modifiers: { hover: true }
                                        }
                                      ],
                                      attrs: { icon: "me-icon icon-edit" },
                                      on: {
                                        click: function($event) {
                                          return _vm.editItem(index)
                                        }
                                      }
                                    })
                                  : _vm._e(),
                                _vm._v(" "),
                                _c("Icon", {
                                  directives: [
                                    {
                                      name: "me-tooltip",
                                      rawName: "v-me-tooltip",
                                      value: _vm.$toolkit.t("criteria.remove"),
                                      expression:
                                        "$toolkit.t('criteria.remove')"
                                    }
                                  ],
                                  attrs: { icon: "me-icon icon-trash" },
                                  on: {
                                    click: function($event) {
                                      return _vm.removeItem(index)
                                    }
                                  }
                                })
                              ],
                              1
                            )
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    item.field && item.field.isCustom
                      ? [_vm._t(item.field.name, null, { item: item })]
                      : _vm._e()
                  ],
                  2
                )
              }),
              _vm._v(" "),
              _vm.isEmpty
                ? _c("li", [
                    _c("span", { staticClass: "badge badge-secondary" }, [
                      _vm._v(
                        "\n            " +
                          _vm._s(_vm.$toolkit.t("criteria.empty")) +
                          "\n          "
                      )
                    ])
                  ])
                : _vm._e()
            ],
            2
          )
        ]),
        _vm._v(" "),
        _c(
          "div",
          { staticClass: "actions" },
          [
            _vm.showAdd
              ? _c("Button", {
                  directives: [
                    {
                      name: "me-tooltip",
                      rawName: "v-me-tooltip.hover",
                      value: _vm.$toolkit.t("criteria.add"),
                      expression: "$toolkit.t('criteria.add')",
                      modifiers: { hover: true }
                    }
                  ],
                  attrs: {
                    variant: "light-secondary",
                    size: "sm",
                    icon: "me-icon icon-plus"
                  },
                  on: { click: _vm.add }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.showRefine
              ? _c("Button", {
                  directives: [
                    {
                      name: "me-tooltip",
                      rawName: "v-me-tooltip.hover",
                      value: _vm.$toolkit.t("criteria.refine"),
                      expression: "$toolkit.t('criteria.refine')",
                      modifiers: { hover: true }
                    }
                  ],
                  attrs: {
                    variant: "light",
                    size: "sm",
                    icon: "me-icon icon-filter"
                  },
                  on: { click: _vm.refine }
                })
              : _vm._e(),
            _vm._v(" "),
            !_vm.isEmpty
              ? _c("Button", {
                  directives: [
                    {
                      name: "me-tooltip",
                      rawName: "v-me-tooltip.hover",
                      value: _vm.$toolkit.t("criteria.reset"),
                      expression: "$toolkit.t('criteria.reset')",
                      modifiers: { hover: true }
                    }
                  ],
                  attrs: {
                    variant: "light-secondary",
                    size: "sm",
                    icon: "me-icon icon-trash"
                  },
                  on: { click: _vm.reset }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.showActions
              ? _c("FilterActions", {
                  attrs: {
                    "filter-name": _vm.filterName,
                    criteria: _vm.criteria,
                    busy: _vm.busy,
                    "hide-operators": _vm.hideOperators,
                    "validate-filters-name": ""
                  },
                  on: {
                    save: function(name, criteria) {
                      return _vm.saveFields(name, criteria)
                    }
                  }
                })
              : _vm._e()
          ],
          1
        )
      ]),
      _vm._v(" "),
      _c("CriteriaBarForm", {
        ref: "form",
        staticClass: "me-criteria-bar-form",
        attrs: {
          context: _vm.context,
          criterion: _vm.criterion,
          fields: _vm.selectableFields,
          "hide-operators": _vm.hideOperators,
          autoFlipSelect: _vm.autoFlipSelect
        },
        on: { change: _vm.onChange, onCriteriaChange: _vm.onCriteriaChange }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$S = [];
__vue_render__$S._withStripped = true;

  /* style */
  const __vue_inject_styles__$S = function (inject) {
    if (!inject) return
    inject("data-v-3316876b_0", { source: "div.me-criteria-bar[data-v-3316876b] {\n  display: flex;\n}\ndiv.me-criteria-bar > div.items[data-v-3316876b] {\n  margin: -4px;\n}\ndiv.me-criteria-bar > div.items .badge[data-v-3316876b] {\n  font-weight: 600 !important;\n}\ndiv.me-criteria-bar > div.actions[data-v-3316876b] {\n  margin-left: 6px;\n  white-space: nowrap;\n}\ndiv.me-criteria-bar > div > ul[data-v-3316876b] {\n  display: inline;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\ndiv.me-criteria-bar > div > ul > li[data-v-3316876b] {\n  float: left;\n  margin: 4px;\n}\ndiv.me-criteria-bar > div > ul > li > .badge[data-v-3316876b] {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-2);\n  height: 31px;\n  padding: 0.594rem;\n  position: relative;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item[data-v-3316876b]:hover {\n  color: transparent;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item:hover > .actions[data-v-3316876b] {\n  opacity: 1;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item > .actions[data-v-3316876b] {\n  color: var(--me-gray-2);\n  height: 100%;\n  left: 0;\n  line-height: 32px;\n  opacity: 0;\n  position: absolute;\n  top: 0;\n  width: 100%;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item > .actions > i[data-v-3316876b] {\n  cursor: pointer;\n  position: relative;\n  width: 30px;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item > .actions > i[data-v-3316876b]:not(:last-child)::after {\n  border-right: 1px solid rgba(0, 0, 0, 0.12);\n  content: \"\";\n  height: 20px;\n  position: absolute;\n  right: -2px;\n  top: 5px;\n}\n\n/*# sourceMappingURL=CriteriaBar.vue.map */", map: {"version":3,"sources":["/app/build/src/components/CriteriaBar.vue","CriteriaBar.vue"],"names":[],"mappings":"AAkSA;EACA,aAAA;ACjSA;ADoSA;EACA,YAAA;AClSA;ADoSA;EACA,2BAAA;AClSA;ADsSA;EACA,gBAAA;EACA,mBAAA;ACpSA;ADuSA;EACA,eAAA;EACA,gBAAA;EACA,SAAA;EACA,UAAA;ACrSA;ADuSA;EACA,WAAA;EACA,WAAA;ACrSA;ADuSA;EACA,kCAAA;EACA,uBAAA;EACA,YAAA;EACA,iBAAA;EACA,kBAAA;ACrSA;ADwSA;EACA,kBAAA;ACtSA;ADuSA;EACA,UAAA;ACrSA;ADySA;EACA,uBAAA;EACA,YAAA;EACA,OAAA;EACA,iBAAA;EACA,UAAA;EACA,kBAAA;EACA,MAAA;EACA,WAAA;ACvSA;ADySA;EACA,eAAA;EACA,kBAAA;EACA,WAAA;ACvSA;AD0SA;EACA,2CAAA;EACA,WAAA;EACA,YAAA;EACA,kBAAA;EACA,WAAA;EACA,QAAA;ACxSA;;AAEA,0CAA0C","file":"CriteriaBar.vue","sourcesContent":["<template>\n  <div>\n    <div class=\"me-criteria-bar\">\n      <div class=\"items\">\n        <ul>\n          <li v-if=\"currentTerm\">\n            <span class=\"badge badge-secondary item\">\n              <Icon icon=\"me-icon icon-search\" />\n              {{ currentTerm }}\n              <div class=\"actions\">\n                <Icon\n                  v-me-tooltip.hover=\"$toolkit.t('criteria.remove')\"\n                  @click=\"clearTerm()\"\n                  icon=\"me-icon icon-trash\"\n                />\n              </div>\n            </span>\n          </li>\n          <li v-for=\"(item, index) in currentCriteria\" :key=\"item.key\">\n            <span v-if=\"item.field && !item.field.isCustom\" class=\"badge badge-secondary item\">\n              <i v-if=\"item.field.icon\" :class=\"item.field.icon\" />\n              {{ item.field.label }} {{ item.operator.symbol || item.operator.label }} {{ formatItemValue(item) }}\n              <div class=\"actions\">\n                <Icon\n                  v-if=\"!item.notEditable\"\n                  v-me-tooltip.hover=\"$toolkit.t('criteria.edit')\"\n                  @click=\"editItem(index)\"\n                  icon=\"me-icon icon-edit\"\n                />\n                <Icon\n                  v-me-tooltip=\"$toolkit.t('criteria.remove')\"\n                  @click=\"removeItem(index)\"\n                  icon=\"me-icon icon-trash\"\n                />\n              </div>\n            </span>\n            <template v-if=\"item.field && item.field.isCustom\">\n              <slot :item=\"item\" :name=\"item.field.name\" />\n            </template>\n          </li>\n          <li v-if=\"isEmpty\">\n            <span class=\"badge badge-secondary\">\n              {{ $toolkit.t('criteria.empty') }}\n            </span>\n          </li>\n        </ul>\n      </div>\n      <div class=\"actions\">\n        <Button\n          v-if=\"showAdd\"\n          v-me-tooltip.hover=\"$toolkit.t('criteria.add')\"\n          @click=\"add\"\n          variant=\"light-secondary\"\n          size=\"sm\"\n          icon=\"me-icon icon-plus\"\n        />\n\n        <Button\n          v-if=\"showRefine\"\n          v-me-tooltip.hover=\"$toolkit.t('criteria.refine')\"\n          @click=\"refine\"\n          variant=\"light\"\n          size=\"sm\"\n          icon=\"me-icon icon-filter\"\n        />\n\n        <Button\n          v-if=\"!isEmpty\"\n          v-me-tooltip.hover=\"$toolkit.t('criteria.reset')\"\n          @click=\"reset\"\n          variant=\"light-secondary\"\n          size=\"sm\"\n          icon=\"me-icon icon-trash\"\n        />\n\n        <FilterActions\n          v-if=\"showActions\"\n          :filter-name=\"filterName\"\n          :criteria=\"criteria\"\n          :busy=\"busy\"\n          @save=\"(name, criteria) => saveFields(name, criteria)\"\n          :hide-operators=\"hideOperators\"\n          validate-filters-name\n        />\n      </div>\n    </div>\n\n    <CriteriaBarForm\n      ref=\"form\"\n      :context=\"context\"\n      :criterion=\"criterion\"\n      :fields=\"selectableFields\"\n      :hide-operators=\"hideOperators\"\n      :autoFlipSelect=\"autoFlipSelect\"\n      @change=\"onChange\"\n      @onCriteriaChange=\"onCriteriaChange\"\n      class=\"me-criteria-bar-form\"\n    />\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport isArray from 'lodash/isArray'\nimport isEmpty from 'lodash/isEmpty'\nimport Button from './Button.vue'\nimport Icon from './Icon.vue'\nimport FilterActions from './FilterActions.vue'\nimport CriteriaBarForm from './CriteriaBarForm.vue'\n\nexport default {\n  name: 'MeCriteriaBar',\n  components: {\n    Button,\n    Icon,\n    FilterActions,\n    CriteriaBarForm\n  },\n  props: {\n    filterName: {\n      type: String,\n      default: ''\n    },\n    fields: {\n      type: Array,\n      required: true\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    term: {\n      type: String,\n      default: ''\n    },\n    criteria: {\n      type: Array,\n      required: true\n    },\n    showSave: {\n      type: Boolean,\n      default: false\n    },\n    showRefine: {\n      type: Boolean,\n      default: false\n    },\n    showAdd: {\n      type: Boolean,\n      default: true\n    },\n    hideOperators: {\n      type: Boolean,\n      default: false\n    },\n    autoFlipSelect: {\n      type: Boolean,\n      default: true\n    }\n  },\n  data() {\n    return {\n      currentTerm: this.term,\n      currentCriteria: cloneDeep(this.criteria),\n      criterion: null\n    }\n  },\n  computed: {\n    isEmpty() {\n      return !this.currentTerm && isEmpty(this.currentCriteria)\n    },\n    selectableFields() {\n      if (this.criterion) {\n        return this.fields.filter(field => this.criterion.field.name === field.name)\n      }\n\n      return this.fields.filter(field => field.isFixed || !this.currentCriteria.map(criteria => criteria.field.name).includes(field.name))\n    },\n    showActions() {\n      return this.showSave && this.currentCriteria.length > 0\n    },\n    context() {\n      return {\n        criteria: this.currentCriteria\n      }\n    }\n  },\n  watch: {\n    term(val) {\n      this.currentTerm = val\n    },\n    criteria(val) {\n      this.currentCriteria = cloneDeep(val || [])\n    },\n    currentCriteria(val) {\n      this.currentCriteria = val\n      this.$emit('onCriteriaChange', { newCriteria: val })\n    }\n  },\n  methods: {\n    add() {\n      this.criterion = null\n      this.$refs.form.show()\n    },\n    editItem(index) {\n      this.criterion = this.currentCriteria[index]\n      this.criterion._index = index\n      this.$refs.form.show()\n    },\n    onChange({ items, criterion }) {\n      if (items[0]?.field.isFixed) {\n        criterion = {\n          _index: this.currentCriteria.findIndex(criteria => criteria.field.name === items[0].field.name)\n        }\n      }\n      if (items.length === 0) {\n        return\n      }\n      if (criterion && criterion._index > -1) {\n        this.resetDependents(items[0])\n        this.currentCriteria.splice(criterion._index, 1, items[0])\n      } else {\n        items.forEach(item => this.currentCriteria.push(item))\n      }\n      this.dispatchChange()\n    },\n    resetDependents(criterion) {\n      this.currentCriteria.filter(d => d.field.parent === criterion.field.name).forEach((item) => {\n        item.value = null\n      })\n    },\n    refine() {\n      this.$emit('refine')\n    },\n    removeItem(index) {\n      this.resetDependents(this.currentCriteria[index])\n      this.currentCriteria.splice(index, 1)\n      this.dispatchChange()\n    },\n    clearTerm() {\n      this.currentTerm = ''\n      this.dispatchChange()\n    },\n    reset() {\n      this.currentTerm = ''\n      this.currentCriteria = []\n      this.dispatchChange()\n      this.$emit('reset')\n    },\n    dispatchChange() {\n      this.$emit('change', {\n        term: this.currentTerm,\n        criteria: this.currentCriteria\n      })\n    },\n    formatItemValue(item) {\n      if (isArray(item.value)) {\n        return item.value.map((value) => {\n          return this.formatItemValue({ ...item, value })\n        }).join(', ')\n      }\n\n      if (item.field.type === 'object' && item.value) {\n        return item.value[item.field.options.label]\n      }\n\n      if (item.field.type === 'boolean' && item.value?.name) {\n        return item.value.name\n      }\n\n      if (item.field.type === 'date' && item.value) {\n        return this.$options.filters['me-date'](item.value, this.$toolkit.locale)\n      }\n\n      return item.value\n    },\n    saveFields({ filtersName, criteria }) {\n      this.$emit('saveFilters', {\n        filtersName,\n        criteria\n      })\n    },\n    onCriteriaChange(newVal) {\n      this.$emit('onCriteriaChange', { newCriteria: newVal })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-criteria-bar {\n    display: flex;\n\n    > div {\n      &.items {\n        margin: -4px;\n\n        .badge {\n          font-weight: 600 !important;\n        }\n      }\n\n      &.actions {\n        margin-left: 6px;\n        white-space: nowrap;\n      }\n\n      > ul {\n        display: inline;\n        list-style: none;\n        margin: 0;\n        padding: 0;\n\n        > li {\n          float: left;\n          margin: 4px;\n\n          > .badge {\n            background-color: var(--me-gray-6);\n            color: var(--me-gray-2);\n            height: 31px;\n            padding: .594rem;\n            position: relative;\n\n            &.item {\n              &:hover {\n                color: transparent;\n                > .actions {\n                  opacity: 1;\n                }\n              }\n\n              > .actions {\n                color: var(--me-gray-2);\n                height: 100%;\n                left: 0;\n                line-height: 32px;\n                opacity: 0;\n                position: absolute;\n                top: 0;\n                width: 100%;\n\n                > i {\n                  cursor: pointer;\n                  position: relative;\n                  width: 30px;\n\n                  &:not(:last-child) {\n                    &::after {\n                      border-right: 1px solid rgba(0, 0, 0, 0.12);\n                      content: \"\";\n                      height: 20px;\n                      position: absolute;\n                      right: -2px;\n                      top: 5px;\n                    }\n                  }\n                }\n              }\n            }\n          }\n        }\n      }\n    }\n  }\n</style>\n","div.me-criteria-bar {\n  display: flex;\n}\ndiv.me-criteria-bar > div.items {\n  margin: -4px;\n}\ndiv.me-criteria-bar > div.items .badge {\n  font-weight: 600 !important;\n}\ndiv.me-criteria-bar > div.actions {\n  margin-left: 6px;\n  white-space: nowrap;\n}\ndiv.me-criteria-bar > div > ul {\n  display: inline;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\ndiv.me-criteria-bar > div > ul > li {\n  float: left;\n  margin: 4px;\n}\ndiv.me-criteria-bar > div > ul > li > .badge {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-2);\n  height: 31px;\n  padding: 0.594rem;\n  position: relative;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item:hover {\n  color: transparent;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item:hover > .actions {\n  opacity: 1;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item > .actions {\n  color: var(--me-gray-2);\n  height: 100%;\n  left: 0;\n  line-height: 32px;\n  opacity: 0;\n  position: absolute;\n  top: 0;\n  width: 100%;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item > .actions > i {\n  cursor: pointer;\n  position: relative;\n  width: 30px;\n}\ndiv.me-criteria-bar > div > ul > li > .badge.item > .actions > i:not(:last-child)::after {\n  border-right: 1px solid rgba(0, 0, 0, 0.12);\n  content: \"\";\n  height: 20px;\n  position: absolute;\n  right: -2px;\n  top: 5px;\n}\n\n/*# sourceMappingURL=CriteriaBar.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$S = "data-v-3316876b";
  /* module identifier */
  const __vue_module_identifier__$S = undefined;
  /* functional template */
  const __vue_is_functional_template__$S = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$S, staticRenderFns: __vue_staticRenderFns__$S },
    __vue_inject_styles__$S,
    __vue_script__$S,
    __vue_scope_id__$S,
    __vue_is_functional_template__$S,
    __vue_module_identifier__$S,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$T = {
  name: 'MeCriteriaInput',
  components: {
    Criteria: __vue_component__$O
  },

  props: {
    beforeRemoveItem: {
      type: Function,
      default: null
    },
    value: {
      type: Array,
      default: null
    },
    fields: {
      type: Array,
      required: true
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    hideRemove: {
      type: Boolean,
      default: false
    },
    hideOperators: {
      type: Boolean,
      default: false
    },
    textSelect: {
      type: String,
      default: ''
    },
    oneKeydown: {
      type: Function,
      default: null
    },
    maxItems: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: null
    },
    operatorsClass: {
      type: String,
      default: 'col-auto'
    },
    inline: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      criteria: cloneDeep_1(this.value)
    }
  },

  watch: {
    value(newValue) {
      this.criteria = cloneDeep_1(newValue);
    }
  },

  methods: {
    onChange(criteria) {
      this.$emit('input', cloneDeep_1(criteria));
    }
  }
};

/* script */
const __vue_script__$T = script$T;

/* template */
var __vue_render__$T = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c("Criteria", {
        attrs: {
          fields: _vm.fields,
          criteria: _vm.criteria,
          hideButton: _vm.hideButton,
          hideRemove: _vm.hideRemove,
          hideOperators: _vm.hideOperators,
          textSelect: _vm.textSelect,
          oneKeydown: _vm.oneKeydown,
          maxItems: _vm.maxItems,
          max: _vm.max,
          inline: _vm.inline,
          "before-remove-item": _vm.beforeRemoveItem,
          operatorsClass: _vm.operatorsClass
        },
        on: { change: _vm.onChange }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$T = [];
__vue_render__$T._withStripped = true;

  /* style */
  const __vue_inject_styles__$T = undefined;
  /* scoped */
  const __vue_scope_id__$T = undefined;
  /* module identifier */
  const __vue_module_identifier__$T = undefined;
  /* functional template */
  const __vue_is_functional_template__$T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$T, staticRenderFns: __vue_staticRenderFns__$T },
    __vue_inject_styles__$T,
    __vue_script__$T,
    __vue_scope_id__$T,
    __vue_is_functional_template__$T,
    __vue_module_identifier__$T,
    false,
    undefined,
    undefined,
    undefined
  );

var vueGridLayout_common = createCommonjsModule(function (module) {
/*! vue-grid-layout - 2.4.0 | (c) 2015, 2022  Gustavo Santos (JBay Solutions) <gustavo.santos@jbaysolutions.com> (http://www.jbaysolutions.com) | https://github.com/jbaysolutions/vue-grid-layout */
module.exports =
/******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
/******/ 		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function(exports, name, getter) {
/******/ 		if(!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, { enumerable: true, get: getter });
/******/ 		}
/******/ 	};
/******/
/******/ 	// define __esModule on exports
/******/ 	__webpack_require__.r = function(exports) {
/******/ 		if(typeof Symbol !== 'undefined' && Symbol.toStringTag) {
/******/ 			Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });
/******/ 		}
/******/ 		Object.defineProperty(exports, '__esModule', { value: true });
/******/ 	};
/******/
/******/ 	// create a fake namespace object
/******/ 	// mode & 1: value is a module id, require it
/******/ 	// mode & 2: merge all properties of value into the ns
/******/ 	// mode & 4: return value when already ns object
/******/ 	// mode & 8|1: behave like require
/******/ 	__webpack_require__.t = function(value, mode) {
/******/ 		if(mode & 1) value = __webpack_require__(value);
/******/ 		if(mode & 8) return value;
/******/ 		if((mode & 4) && typeof value === 'object' && value && value.__esModule) return value;
/******/ 		var ns = Object.create(null);
/******/ 		__webpack_require__.r(ns);
/******/ 		Object.defineProperty(ns, 'default', { enumerable: true, value: value });
/******/ 		if(mode & 2 && typeof value != 'string') for(var key in value) __webpack_require__.d(ns, key, function(key) { return value[key]; }.bind(null, key));
/******/ 		return ns;
/******/ 	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function(module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
/******/ 	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function(object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = "fb15");
/******/ })
/************************************************************************/
/******/ ({

/***/ "01f9":
/***/ (function(module, exports, __webpack_require__) {

var LIBRARY = __webpack_require__("2d00");
var $export = __webpack_require__("5ca1");
var redefine = __webpack_require__("2aba");
var hide = __webpack_require__("32e9");
var Iterators = __webpack_require__("84f2");
var $iterCreate = __webpack_require__("41a0");
var setToStringTag = __webpack_require__("7f20");
var getPrototypeOf = __webpack_require__("38fd");
var ITERATOR = __webpack_require__("2b4c")('iterator');
var BUGGY = !([].keys && 'next' in [].keys()); // Safari has buggy iterators w/o `next`
var FF_ITERATOR = '@@iterator';
var KEYS = 'keys';
var VALUES = 'values';

var returnThis = function () { return this; };

module.exports = function (Base, NAME, Constructor, next, DEFAULT, IS_SET, FORCED) {
  $iterCreate(Constructor, NAME, next);
  var getMethod = function (kind) {
    if (!BUGGY && kind in proto) return proto[kind];
    switch (kind) {
      case KEYS: return function keys() { return new Constructor(this, kind); };
      case VALUES: return function values() { return new Constructor(this, kind); };
    } return function entries() { return new Constructor(this, kind); };
  };
  var TAG = NAME + ' Iterator';
  var DEF_VALUES = DEFAULT == VALUES;
  var VALUES_BUG = false;
  var proto = Base.prototype;
  var $native = proto[ITERATOR] || proto[FF_ITERATOR] || DEFAULT && proto[DEFAULT];
  var $default = $native || getMethod(DEFAULT);
  var $entries = DEFAULT ? !DEF_VALUES ? $default : getMethod('entries') : undefined;
  var $anyNative = NAME == 'Array' ? proto.entries || $native : $native;
  var methods, key, IteratorPrototype;
  // Fix native
  if ($anyNative) {
    IteratorPrototype = getPrototypeOf($anyNative.call(new Base()));
    if (IteratorPrototype !== Object.prototype && IteratorPrototype.next) {
      // Set @@toStringTag to native iterators
      setToStringTag(IteratorPrototype, TAG, true);
      // fix for some old engines
      if (!LIBRARY && typeof IteratorPrototype[ITERATOR] != 'function') hide(IteratorPrototype, ITERATOR, returnThis);
    }
  }
  // fix Array#{values, @@iterator}.name in V8 / FF
  if (DEF_VALUES && $native && $native.name !== VALUES) {
    VALUES_BUG = true;
    $default = function values() { return $native.call(this); };
  }
  // Define iterator
  if ((!LIBRARY || FORCED) && (BUGGY || VALUES_BUG || !proto[ITERATOR])) {
    hide(proto, ITERATOR, $default);
  }
  // Plug for library
  Iterators[NAME] = $default;
  Iterators[TAG] = returnThis;
  if (DEFAULT) {
    methods = {
      values: DEF_VALUES ? $default : getMethod(VALUES),
      keys: IS_SET ? $default : getMethod(KEYS),
      entries: $entries
    };
    if (FORCED) for (key in methods) {
      if (!(key in proto)) redefine(proto, key, methods[key]);
    } else $export($export.P + $export.F * (BUGGY || VALUES_BUG), NAME, methods);
  }
  return methods;
};


/***/ }),

/***/ "02f4":
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__("4588");
var defined = __webpack_require__("be13");
// true  -> String#at
// false -> String#codePointAt
module.exports = function (TO_STRING) {
  return function (that, pos) {
    var s = String(defined(that));
    var i = toInteger(pos);
    var l = s.length;
    var a, b;
    if (i < 0 || i >= l) return TO_STRING ? '' : undefined;
    a = s.charCodeAt(i);
    return a < 0xd800 || a > 0xdbff || i + 1 === l || (b = s.charCodeAt(i + 1)) < 0xdc00 || b > 0xdfff
      ? TO_STRING ? s.charAt(i) : a
      : TO_STRING ? s.slice(i, i + 2) : (a - 0xd800 << 10) + (b - 0xdc00) + 0x10000;
  };
};


/***/ }),

/***/ "0390":
/***/ (function(module, exports, __webpack_require__) {

var at = __webpack_require__("02f4")(true);

 // `AdvanceStringIndex` abstract operation
// https://tc39.github.io/ecma262/#sec-advancestringindex
module.exports = function (S, index, unicode) {
  return index + (unicode ? at(S, index).length : 1);
};


/***/ }),

/***/ "0bfb":
/***/ (function(module, exports, __webpack_require__) {

// 21.2.5.3 get RegExp.prototype.flags
var anObject = __webpack_require__("cb7c");
module.exports = function () {
  var that = anObject(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};


/***/ }),

/***/ "0d58":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.14 / 15.2.3.14 Object.keys(O)
var $keys = __webpack_require__("ce10");
var enumBugKeys = __webpack_require__("e11e");

module.exports = Object.keys || function keys(O) {
  return $keys(O, enumBugKeys);
};


/***/ }),

/***/ "1156":
/***/ (function(module, exports, __webpack_require__) {

// style-loader: Adds some css to the DOM by adding a <style> tag

// load the styles
var content = __webpack_require__("ad20");
if(typeof content === 'string') content = [[module.i, content, '']];
if(content.locals) module.exports = content.locals;
// add the styles to the DOM
var add = __webpack_require__("499e").default;
var update = add("c1ec597e", content, true, {"sourceMap":false,"shadowMode":false});

/***/ }),

/***/ "11e9":
/***/ (function(module, exports, __webpack_require__) {

var pIE = __webpack_require__("52a7");
var createDesc = __webpack_require__("4630");
var toIObject = __webpack_require__("6821");
var toPrimitive = __webpack_require__("6a99");
var has = __webpack_require__("69a8");
var IE8_DOM_DEFINE = __webpack_require__("c69a");
var gOPD = Object.getOwnPropertyDescriptor;

exports.f = __webpack_require__("9e1e") ? gOPD : function getOwnPropertyDescriptor(O, P) {
  O = toIObject(O);
  P = toPrimitive(P, true);
  if (IE8_DOM_DEFINE) try {
    return gOPD(O, P);
  } catch (e) { /* empty */ }
  if (has(O, P)) return createDesc(!pIE.f.call(O, P), O[P]);
};


/***/ }),

/***/ "1495":
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__("86cc");
var anObject = __webpack_require__("cb7c");
var getKeys = __webpack_require__("0d58");

module.exports = __webpack_require__("9e1e") ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject(O);
  var keys = getKeys(Properties);
  var length = keys.length;
  var i = 0;
  var P;
  while (length > i) dP.f(O, P = keys[i++], Properties[P]);
  return O;
};


/***/ }),

/***/ "18d2":
/***/ (function(module, exports, __webpack_require__) {
/**
 * Resize detection strategy that injects objects to elements in order to detect resize events.
 * Heavily inspired by: http://www.backalleycoder.com/2013/03/18/cross-browser-event-based-element-resize-detection/
 */



var browserDetector = __webpack_require__("18e9");

module.exports = function(options) {
    options             = options || {};
    var reporter        = options.reporter;
    var batchProcessor  = options.batchProcessor;
    var getState        = options.stateHandler.getState;

    if(!reporter) {
        throw new Error("Missing required dependency: reporter.");
    }

    /**
     * Adds a resize event listener to the element.
     * @public
     * @param {element} element The element that should have the listener added.
     * @param {function} listener The listener callback to be called for each resize event of the element. The element will be given as a parameter to the listener callback.
     */
    function addListener(element, listener) {
        function listenerProxy() {
            listener(element);
        }

        if(browserDetector.isIE(8)) {
            //IE 8 does not support object, but supports the resize event directly on elements.
            getState(element).object = {
                proxy: listenerProxy
            };
            element.attachEvent("onresize", listenerProxy);
        } else {
            var object = getObject(element);

            if(!object) {
                throw new Error("Element is not detectable by this strategy.");
            }

            object.contentDocument.defaultView.addEventListener("resize", listenerProxy);
        }
    }

    function buildCssTextString(rules) {
        var seperator = options.important ? " !important; " : "; ";

        return (rules.join(seperator) + seperator).trim();
    }

    /**
     * Makes an element detectable and ready to be listened for resize events. Will call the callback when the element is ready to be listened for resize changes.
     * @private
     * @param {object} options Optional options object.
     * @param {element} element The element to make detectable
     * @param {function} callback The callback to be called when the element is ready to be listened for resize changes. Will be called with the element as first parameter.
     */
    function makeDetectable(options, element, callback) {
        if (!callback) {
            callback = element;
            element = options;
            options = null;
        }

        options = options || {};
        var debug = options.debug;

        function injectObject(element, callback) {
            var OBJECT_STYLE = buildCssTextString(["display: block", "position: absolute", "top: 0", "left: 0", "width: 100%", "height: 100%", "border: none", "padding: 0", "margin: 0", "opacity: 0", "z-index: -1000", "pointer-events: none"]);

            //The target element needs to be positioned (everything except static) so the absolute positioned object will be positioned relative to the target element.

            // Position altering may be performed directly or on object load, depending on if style resolution is possible directly or not.
            var positionCheckPerformed = false;

            // The element may not yet be attached to the DOM, and therefore the style object may be empty in some browsers.
            // Since the style object is a reference, it will be updated as soon as the element is attached to the DOM.
            var style = window.getComputedStyle(element);
            var width = element.offsetWidth;
            var height = element.offsetHeight;

            getState(element).startSize = {
                width: width,
                height: height
            };

            function mutateDom() {
                function alterPositionStyles() {
                    if(style.position === "static") {
                        element.style.setProperty("position", "relative", options.important ? "important" : "");

                        var removeRelativeStyles = function(reporter, element, style, property) {
                            function getNumericalValue(value) {
                                return value.replace(/[^-\d\.]/g, "");
                            }

                            var value = style[property];

                            if(value !== "auto" && getNumericalValue(value) !== "0") {
                                reporter.warn("An element that is positioned static has style." + property + "=" + value + " which is ignored due to the static positioning. The element will need to be positioned relative, so the style." + property + " will be set to 0. Element: ", element);
                                element.style.setProperty(property, "0", options.important ? "important" : "");
                            }
                        };

                        //Check so that there are no accidental styles that will make the element styled differently now that is is relative.
                        //If there are any, set them to 0 (this should be okay with the user since the style properties did nothing before [since the element was positioned static] anyway).
                        removeRelativeStyles(reporter, element, style, "top");
                        removeRelativeStyles(reporter, element, style, "right");
                        removeRelativeStyles(reporter, element, style, "bottom");
                        removeRelativeStyles(reporter, element, style, "left");
                    }
                }

                function onObjectLoad() {
                    // The object has been loaded, which means that the element now is guaranteed to be attached to the DOM.
                    if (!positionCheckPerformed) {
                        alterPositionStyles();
                    }

                    /*jshint validthis: true */

                    function getDocument(element, callback) {
                        //Opera 12 seem to call the object.onload before the actual document has been created.
                        //So if it is not present, poll it with an timeout until it is present.
                        //TODO: Could maybe be handled better with object.onreadystatechange or similar.
                        if(!element.contentDocument) {
                            var state = getState(element);
                            if (state.checkForObjectDocumentTimeoutId) {
                                window.clearTimeout(state.checkForObjectDocumentTimeoutId);
                            }
                            state.checkForObjectDocumentTimeoutId = setTimeout(function checkForObjectDocument() {
                                state.checkForObjectDocumentTimeoutId = 0;
                                getDocument(element, callback);
                            }, 100);

                            return;
                        }

                        callback(element.contentDocument);
                    }

                    //Mutating the object element here seems to fire another load event.
                    //Mutating the inner document of the object element is fine though.
                    var objectElement = this;

                    //Create the style element to be added to the object.
                    getDocument(objectElement, function onObjectDocumentReady(objectDocument) {
                        //Notify that the element is ready to be listened to.
                        callback(element);
                    });
                }

                // The element may be detached from the DOM, and some browsers does not support style resolving of detached elements.
                // The alterPositionStyles needs to be delayed until we know the element has been attached to the DOM (which we are sure of when the onObjectLoad has been fired), if style resolution is not possible.
                if (style.position !== "") {
                    alterPositionStyles();
                    positionCheckPerformed = true;
                }

                //Add an object element as a child to the target element that will be listened to for resize events.
                var object = document.createElement("object");
                object.style.cssText = OBJECT_STYLE;
                object.tabIndex = -1;
                object.type = "text/html";
                object.setAttribute("aria-hidden", "true");
                object.onload = onObjectLoad;

                //Safari: This must occur before adding the object to the DOM.
                //IE: Does not like that this happens before, even if it is also added after.
                if(!browserDetector.isIE()) {
                    object.data = "about:blank";
                }

                if (!getState(element)) {
                    // The element has been uninstalled before the actual loading happened.
                    return;
                }

                element.appendChild(object);
                getState(element).object = object;

                //IE: This must occur after adding the object to the DOM.
                if(browserDetector.isIE()) {
                    object.data = "about:blank";
                }
            }

            if(batchProcessor) {
                batchProcessor.add(mutateDom);
            } else {
                mutateDom();
            }
        }

        if(browserDetector.isIE(8)) {
            //IE 8 does not support objects properly. Luckily they do support the resize event.
            //So do not inject the object and notify that the element is already ready to be listened to.
            //The event handler for the resize event is attached in the utils.addListener instead.
            callback(element);
        } else {
            injectObject(element, callback);
        }
    }

    /**
     * Returns the child object of the target element.
     * @private
     * @param {element} element The target element.
     * @returns The object element of the target.
     */
    function getObject(element) {
        return getState(element).object;
    }

    function uninstall(element) {
        if (!getState(element)) {
            return;
        }

        var object = getObject(element);

        if (!object) {
            return;
        }

        if (browserDetector.isIE(8)) {
            element.detachEvent("onresize", object.proxy);
        } else {
            element.removeChild(object);
        }

        if (getState(element).checkForObjectDocumentTimeoutId) {
            window.clearTimeout(getState(element).checkForObjectDocumentTimeoutId);
        }

        delete getState(element).object;
    }

    return {
        makeDetectable: makeDetectable,
        addListener: addListener,
        uninstall: uninstall
    };
};


/***/ }),

/***/ "18e9":
/***/ (function(module, exports, __webpack_require__) {


var detector = module.exports = {};

detector.isIE = function(version) {
    function isAnyIeVersion() {
        var agent = navigator.userAgent.toLowerCase();
        return agent.indexOf("msie") !== -1 || agent.indexOf("trident") !== -1 || agent.indexOf(" edge/") !== -1;
    }

    if(!isAnyIeVersion()) {
        return false;
    }

    if(!version) {
        return true;
    }

    //Shamelessly stolen from https://gist.github.com/padolsey/527683
    var ieVersion = (function(){
        var undef,
            v = 3,
            div = document.createElement("div"),
            all = div.getElementsByTagName("i");

        do {
            div.innerHTML = "<!--[if gt IE " + (++v) + "]><i></i><![endif]-->";
        }
        while (all[0]);

        return v > 4 ? v : undef;
    }());

    return version === ieVersion;
};

detector.isLegacyOpera = function() {
    return !!window.opera;
};


/***/ }),

/***/ "1ca7":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return getDocumentDir; });
/* unused harmony export setDocumentDir */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return addWindowEventListener; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "c", function() { return removeWindowEventListener; });
var currentDir
/*: "ltr" | "rtl" | "auto"*/
= "auto"; // let currentDir = "auto";

function hasDocument() {
  return typeof document !== "undefined";
}

function hasWindow() {
  return typeof window !== "undefined";
}

function getDocumentDir() {
  if (!hasDocument()) {
    return currentDir;
  }

  var direction = typeof document.dir !== "undefined" ? document.dir : document.getElementsByTagName("html")[0].getAttribute("dir");
  return direction;
}
function addWindowEventListener(event
/*:string*/
, callback
/*: () => mixed*/
) {
  if (!hasWindow) {
    callback();
    return;
  }

  window.addEventListener(event, callback);
}
function removeWindowEventListener(event
/*:string*/
, callback
/*: () => mixed*/
) {
  if (!hasWindow) {
    return;
  }

  window.removeEventListener(event, callback);
}

/***/ }),

/***/ "214f":
/***/ (function(module, exports, __webpack_require__) {

__webpack_require__("b0c5");
var redefine = __webpack_require__("2aba");
var hide = __webpack_require__("32e9");
var fails = __webpack_require__("79e5");
var defined = __webpack_require__("be13");
var wks = __webpack_require__("2b4c");
var regexpExec = __webpack_require__("520a");

var SPECIES = wks('species');

var REPLACE_SUPPORTS_NAMED_GROUPS = !fails(function () {
  // #replace needs built-in support for named groups.
  // #match works fine because it just return the exec results, even if it has
  // a "grops" property.
  var re = /./;
  re.exec = function () {
    var result = [];
    result.groups = { a: '7' };
    return result;
  };
  return ''.replace(re, '$<a>') !== '7';
});

var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC = (function () {
  // Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
  var re = /(?:)/;
  var originalExec = re.exec;
  re.exec = function () { return originalExec.apply(this, arguments); };
  var result = 'ab'.split(re);
  return result.length === 2 && result[0] === 'a' && result[1] === 'b';
})();

module.exports = function (KEY, length, exec) {
  var SYMBOL = wks(KEY);

  var DELEGATES_TO_SYMBOL = !fails(function () {
    // String methods call symbol-named RegEp methods
    var O = {};
    O[SYMBOL] = function () { return 7; };
    return ''[KEY](O) != 7;
  });

  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL ? !fails(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;
    re.exec = function () { execCalled = true; return null; };
    if (KEY === 'split') {
      // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.
      re.constructor = {};
      re.constructor[SPECIES] = function () { return re; };
    }
    re[SYMBOL]('');
    return !execCalled;
  }) : undefined;

  if (
    !DELEGATES_TO_SYMBOL ||
    !DELEGATES_TO_EXEC ||
    (KEY === 'replace' && !REPLACE_SUPPORTS_NAMED_GROUPS) ||
    (KEY === 'split' && !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC)
  ) {
    var nativeRegExpMethod = /./[SYMBOL];
    var fns = exec(
      defined,
      SYMBOL,
      ''[KEY],
      function maybeCallNative(nativeMethod, regexp, str, arg2, forceStringMethod) {
        if (regexp.exec === regexpExec) {
          if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
            // The native String method already delegates to @@method (this
            // polyfilled function), leasing to infinite recursion.
            // We avoid it by directly calling the native @@method method.
            return { done: true, value: nativeRegExpMethod.call(regexp, str, arg2) };
          }
          return { done: true, value: nativeMethod.call(str, regexp, arg2) };
        }
        return { done: false };
      }
    );
    var strfn = fns[0];
    var rxfn = fns[1];

    redefine(String.prototype, KEY, strfn);
    hide(RegExp.prototype, SYMBOL, length == 2
      // 21.2.5.8 RegExp.prototype[@@replace](string, replaceValue)
      // 21.2.5.11 RegExp.prototype[@@split](string, limit)
      ? function (string, arg) { return rxfn.call(string, this, arg); }
      // 21.2.5.6 RegExp.prototype[@@match](string)
      // 21.2.5.9 RegExp.prototype[@@search](string)
      : function (string) { return rxfn.call(string, this); }
    );
  }
};


/***/ }),

/***/ "230e":
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__("d3f4");
var document = __webpack_require__("7726").document;
// typeof document.createElement is 'object' in old IE
var is = isObject(document) && isObject(document.createElement);
module.exports = function (it) {
  return is ? document.createElement(it) : {};
};


/***/ }),

/***/ "2350":
/***/ (function(module, exports) {

/*
	MIT License http://www.opensource.org/licenses/mit-license.php
	Author Tobias Koppers @sokra
*/
// css base code, injected by the css-loader
module.exports = function(useSourceMap) {
	var list = [];

	// return the list of modules as css string
	list.toString = function toString() {
		return this.map(function (item) {
			var content = cssWithMappingToString(item, useSourceMap);
			if(item[2]) {
				return "@media " + item[2] + "{" + content + "}";
			} else {
				return content;
			}
		}).join("");
	};

	// import a list of modules into the list
	list.i = function(modules, mediaQuery) {
		if(typeof modules === "string")
			modules = [[null, modules, ""]];
		var alreadyImportedModules = {};
		for(var i = 0; i < this.length; i++) {
			var id = this[i][0];
			if(typeof id === "number")
				alreadyImportedModules[id] = true;
		}
		for(i = 0; i < modules.length; i++) {
			var item = modules[i];
			// skip already imported module
			// this implementation is not 100% perfect for weird media query combinations
			//  when a module is imported multiple times with different media queries.
			//  I hope this will never occur (Hey this way we have smaller bundles)
			if(typeof item[0] !== "number" || !alreadyImportedModules[item[0]]) {
				if(mediaQuery && !item[2]) {
					item[2] = mediaQuery;
				} else if(mediaQuery) {
					item[2] = "(" + item[2] + ") and (" + mediaQuery + ")";
				}
				list.push(item);
			}
		}
	};
	return list;
};

function cssWithMappingToString(item, useSourceMap) {
	var content = item[1] || '';
	var cssMapping = item[3];
	if (!cssMapping) {
		return content;
	}

	if (useSourceMap && typeof btoa === 'function') {
		var sourceMapping = toComment(cssMapping);
		var sourceURLs = cssMapping.sources.map(function (source) {
			return '/*# sourceURL=' + cssMapping.sourceRoot + source + ' */'
		});

		return [content].concat(sourceURLs).concat([sourceMapping]).join('\n');
	}

	return [content].join('\n');
}

// Adapted from convert-source-map (MIT)
function toComment(sourceMap) {
	// eslint-disable-next-line no-undef
	var base64 = btoa(unescape(encodeURIComponent(JSON.stringify(sourceMap))));
	var data = 'sourceMappingURL=data:application/json;charset=utf-8;base64,' + base64;

	return '/*# ' + data + ' */';
}


/***/ }),

/***/ "23c6":
/***/ (function(module, exports, __webpack_require__) {

// getting tag from 19.1.3.6 Object.prototype.toString()
var cof = __webpack_require__("2d95");
var TAG = __webpack_require__("2b4c")('toStringTag');
// ES3 wrong here
var ARG = cof(function () { return arguments; }()) == 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (e) { /* empty */ }
};

module.exports = function (it) {
  var O, T, B;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (T = tryGet(O = Object(it), TAG)) == 'string' ? T
    // builtinTag case
    : ARG ? cof(O)
    // ES3 arguments fallback
    : (B = cof(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : B;
};


/***/ }),

/***/ "2621":
/***/ (function(module, exports) {

exports.f = Object.getOwnPropertySymbols;


/***/ }),

/***/ "2877":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return normalizeComponent; });
/* globals __VUE_SSR_CONTEXT__ */

// IMPORTANT: Do NOT use ES2015 features in this file (except for modules).
// This module is a runtime utility for cleaner component module output and will
// be included in the final webpack user bundle.

function normalizeComponent (
  scriptExports,
  render,
  staticRenderFns,
  functionalTemplate,
  injectStyles,
  scopeId,
  moduleIdentifier, /* server only */
  shadowMode /* vue-cli only */
) {
  // Vue.extend constructor export interop
  var options = typeof scriptExports === 'function'
    ? scriptExports.options
    : scriptExports;

  // render functions
  if (render) {
    options.render = render;
    options.staticRenderFns = staticRenderFns;
    options._compiled = true;
  }

  // functional template
  if (functionalTemplate) {
    options.functional = true;
  }

  // scopedId
  if (scopeId) {
    options._scopeId = 'data-v-' + scopeId;
  }

  var hook;
  if (moduleIdentifier) { // server build
    hook = function (context) {
      // 2.3 injection
      context =
        context || // cached call
        (this.$vnode && this.$vnode.ssrContext) || // stateful
        (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
      // 2.2 with runInNewContext: true
      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      }
      // inject component styles
      if (injectStyles) {
        injectStyles.call(this, context);
      }
      // register component module identifier for async chunk inferrence
      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    };
    // used by ssr in case component is cached and beforeCreate
    // never gets called
    options._ssrRegister = hook;
  } else if (injectStyles) {
    hook = shadowMode
      ? function () {
        injectStyles.call(
          this,
          (options.functional ? this.parent : this).$root.$options.shadowRoot
        );
      }
      : injectStyles;
  }

  if (hook) {
    if (options.functional) {
      // for template-only hot-reload because in that case the render fn doesn't
      // go through the normalizer
      options._injectStyles = hook;
      // register for functional component in vue file
      var originalRender = options.render;
      options.render = function renderWithStyleInjection (h, context) {
        hook.call(context);
        return originalRender(h, context)
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing
        ? [].concat(existing, hook)
        : [hook];
    }
  }

  return {
    exports: scriptExports,
    options: options
  }
}


/***/ }),

/***/ "2aba":
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__("7726");
var hide = __webpack_require__("32e9");
var has = __webpack_require__("69a8");
var SRC = __webpack_require__("ca5a")('src');
var $toString = __webpack_require__("fa5b");
var TO_STRING = 'toString';
var TPL = ('' + $toString).split(TO_STRING);

__webpack_require__("8378").inspectSource = function (it) {
  return $toString.call(it);
};

(module.exports = function (O, key, val, safe) {
  var isFunction = typeof val == 'function';
  if (isFunction) has(val, 'name') || hide(val, 'name', key);
  if (O[key] === val) return;
  if (isFunction) has(val, SRC) || hide(val, SRC, O[key] ? '' + O[key] : TPL.join(String(key)));
  if (O === global) {
    O[key] = val;
  } else if (!safe) {
    delete O[key];
    hide(O, key, val);
  } else if (O[key]) {
    O[key] = val;
  } else {
    hide(O, key, val);
  }
// add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, TO_STRING, function toString() {
  return typeof this == 'function' && this[SRC] || $toString.call(this);
});


/***/ }),

/***/ "2aeb":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.2 / 15.2.3.5 Object.create(O [, Properties])
var anObject = __webpack_require__("cb7c");
var dPs = __webpack_require__("1495");
var enumBugKeys = __webpack_require__("e11e");
var IE_PROTO = __webpack_require__("613b")('IE_PROTO');
var Empty = function () { /* empty */ };
var PROTOTYPE = 'prototype';

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var createDict = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = __webpack_require__("230e")('iframe');
  var i = enumBugKeys.length;
  var lt = '<';
  var gt = '>';
  var iframeDocument;
  iframe.style.display = 'none';
  __webpack_require__("fab2").appendChild(iframe);
  iframe.src = 'javascript:'; // eslint-disable-line no-script-url
  // createDict = iframe.contentWindow.Object;
  // html.removeChild(iframe);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(lt + 'script' + gt + 'document.F=Object' + lt + '/script' + gt);
  iframeDocument.close();
  createDict = iframeDocument.F;
  while (i--) delete createDict[PROTOTYPE][enumBugKeys[i]];
  return createDict();
};

module.exports = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    Empty[PROTOTYPE] = anObject(O);
    result = new Empty();
    Empty[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO] = O;
  } else result = createDict();
  return Properties === undefined ? result : dPs(result, Properties);
};


/***/ }),

/***/ "2af9":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* WEBPACK VAR INJECTION */(function(global) {/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "d", function() { return install; });
/* harmony import */ var core_js_modules_es6_function_name__WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__("7f7f");
/* harmony import */ var core_js_modules_es6_array_iterator__WEBPACK_IMPORTED_MODULE_1__ = __webpack_require__("cadf");
/* harmony import */ var core_js_modules_es6_object_keys__WEBPACK_IMPORTED_MODULE_2__ = __webpack_require__("456d");
/* harmony import */ var core_js_modules_web_dom_iterable__WEBPACK_IMPORTED_MODULE_3__ = __webpack_require__("ac6a");
/* harmony import */ var _GridItem_vue__WEBPACK_IMPORTED_MODULE_4__ = __webpack_require__("bc21");
/* harmony reexport (safe) */ __webpack_require__.d(__webpack_exports__, "a", function() { return _GridItem_vue__WEBPACK_IMPORTED_MODULE_4__["a"]; });

/* harmony import */ var _GridLayout_vue__WEBPACK_IMPORTED_MODULE_5__ = __webpack_require__("37c8");
/* harmony reexport (safe) */ __webpack_require__.d(__webpack_exports__, "b", function() { return _GridLayout_vue__WEBPACK_IMPORTED_MODULE_5__["a"]; });






 // import ResponsiveGridLayout from './ResponsiveGridLayout.vue';

var VueGridLayout = {
  // ResponsiveGridLayout,
  GridLayout: _GridLayout_vue__WEBPACK_IMPORTED_MODULE_5__[/* default */ "a"],
  GridItem: _GridItem_vue__WEBPACK_IMPORTED_MODULE_4__[/* default */ "a"]
};
function install(Vue) {
  if (install.installed) return;
  install.installed = true;
  Object.keys(VueGridLayout).forEach(function (name) {
    Vue.component(name, VueGridLayout[name]);
  });
}
var plugin = {
  install: install
};
var GlobalVue = null;

if (typeof window !== 'undefined') {
  GlobalVue = window.Vue;
} else if (typeof global !== 'undefined') {
  GlobalVue = global.Vue;
}

if (GlobalVue) {
  GlobalVue.use(plugin);
}

/* harmony default export */ __webpack_exports__["c"] = (VueGridLayout);

/* WEBPACK VAR INJECTION */}.call(this, __webpack_require__("c8ba")));

/***/ }),

/***/ "2b4c":
/***/ (function(module, exports, __webpack_require__) {

var store = __webpack_require__("5537")('wks');
var uid = __webpack_require__("ca5a");
var Symbol = __webpack_require__("7726").Symbol;
var USE_SYMBOL = typeof Symbol == 'function';

var $exports = module.exports = function (name) {
  return store[name] || (store[name] =
    USE_SYMBOL && Symbol[name] || (USE_SYMBOL ? Symbol : uid)('Symbol.' + name));
};

$exports.store = store;


/***/ }),

/***/ "2cef":
/***/ (function(module, exports, __webpack_require__) {


module.exports = function() {
    var idCount = 1;

    /**
     * Generates a new unique id in the context.
     * @public
     * @returns {number} A unique id in the context.
     */
    function generate() {
        return idCount++;
    }

    return {
        generate: generate
    };
};


/***/ }),

/***/ "2d00":
/***/ (function(module, exports) {

module.exports = false;


/***/ }),

/***/ "2d95":
/***/ (function(module, exports) {

var toString = {}.toString;

module.exports = function (it) {
  return toString.call(it).slice(8, -1);
};


/***/ }),

/***/ "2f21":
/***/ (function(module, exports, __webpack_require__) {

var fails = __webpack_require__("79e5");

module.exports = function (method, arg) {
  return !!method && fails(function () {
    // eslint-disable-next-line no-useless-call
    arg ? method.call(null, function () { /* empty */ }, 1) : method.call(null);
  });
};


/***/ }),

/***/ "32e9":
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__("86cc");
var createDesc = __webpack_require__("4630");
module.exports = __webpack_require__("9e1e") ? function (object, key, value) {
  return dP.f(object, key, createDesc(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};


/***/ }),

/***/ "37c8":
/***/ (function(module, __webpack_exports__, __webpack_require__) {

// CONCATENATED MODULE: ./node_modules/cache-loader/dist/cjs.js?{"cacheDirectory":"node_modules/.cache/vue-loader","cacheIdentifier":"1705dc22-vue-loader-template"}!./node_modules/vue-loader/lib/loaders/templateLoader.js??vue-loader-options!./node_modules/cache-loader/dist/cjs.js??ref--0-0!./node_modules/vue-loader/lib??vue-loader-options!./src/components/GridLayout.vue?vue&type=template&id=361da5e4&
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"item",staticClass:"vue-grid-layout",style:(_vm.mergedStyle)},[_vm._t("default"),_c('grid-item',{directives:[{name:"show",rawName:"v-show",value:(_vm.isDragging),expression:"isDragging"}],staticClass:"vue-grid-placeholder",attrs:{"x":_vm.placeholder.x,"y":_vm.placeholder.y,"w":_vm.placeholder.w,"h":_vm.placeholder.h,"i":_vm.placeholder.i}})],2)};
var staticRenderFns = [];


// CONCATENATED MODULE: ./src/components/GridLayout.vue?vue&type=template&id=361da5e4&

// EXTERNAL MODULE: ./node_modules/core-js/modules/es7.object.get-own-property-descriptors.js
var es7_object_get_own_property_descriptors = __webpack_require__("8e6e");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.array.iterator.js
var es6_array_iterator = __webpack_require__("cadf");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.object.keys.js
var es6_object_keys = __webpack_require__("456d");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.object.assign.js
var es6_object_assign = __webpack_require__("f751");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.number.is-finite.js
var es6_number_is_finite = __webpack_require__("fca0");

// EXTERNAL MODULE: ./node_modules/core-js/modules/web.dom.iterable.js
var web_dom_iterable = __webpack_require__("ac6a");

// EXTERNAL MODULE: ./node_modules/@babel/runtime/helpers/esm/defineProperty.js
var defineProperty = __webpack_require__("ade3");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.number.constructor.js
var es6_number_constructor = __webpack_require__("c5f6");

// EXTERNAL MODULE: external {"commonjs":"vue","commonjs2":"vue","root":"Vue"}
var external_commonjs_vue_commonjs2_vue_root_Vue_ = __webpack_require__("8bbf");
var external_commonjs_vue_commonjs2_vue_root_Vue_default = /*#__PURE__*/__webpack_require__.n(external_commonjs_vue_commonjs2_vue_root_Vue_);

// EXTERNAL MODULE: ./src/helpers/utils.js
var utils = __webpack_require__("a2b6");

// EXTERNAL MODULE: ./src/helpers/responsiveUtils.js
var responsiveUtils = __webpack_require__("97a7");

// EXTERNAL MODULE: ./src/components/GridItem.vue + 71 modules
var GridItem = __webpack_require__("bc21");

// EXTERNAL MODULE: ./src/helpers/DOM.js
var DOM = __webpack_require__("1ca7");

// CONCATENATED MODULE: ./node_modules/cache-loader/dist/cjs.js??ref--12-0!./node_modules/thread-loader/dist/cjs.js!./node_modules/babel-loader/lib!./node_modules/cache-loader/dist/cjs.js??ref--0-0!./node_modules/vue-loader/lib??vue-loader-options!./src/components/GridLayout.vue?vue&type=script&lang=js&









function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { Object(defineProperty["a" /* default */])(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//


var elementResizeDetectorMaker = __webpack_require__("eec4");


 //var eventBus = require('./eventBus');



/* harmony default export */ var GridLayoutvue_type_script_lang_js_ = ({
  name: "GridLayout",
  provide: function provide() {
    return {
      eventBus: null,
      layout: this
    };
  },
  components: {
    GridItem: GridItem["a" /* default */]
  },
  props: {
    // If true, the container height swells and contracts to fit contents
    autoSize: {
      type: Boolean,
      default: true
    },
    colNum: {
      type: Number,
      default: 12
    },
    rowHeight: {
      type: Number,
      default: 150
    },
    maxRows: {
      type: Number,
      default: Infinity
    },
    margin: {
      type: Array,
      default: function _default() {
        return [10, 10];
      }
    },
    isDraggable: {
      type: Boolean,
      default: true
    },
    isResizable: {
      type: Boolean,
      default: true
    },
    isMirrored: {
      type: Boolean,
      default: false
    },
    isBounded: {
      type: Boolean,
      default: false
    },
    useCssTransforms: {
      type: Boolean,
      default: true
    },
    verticalCompact: {
      type: Boolean,
      default: true
    },
    restoreOnDrag: {
      type: Boolean,
      default: false
    },
    layout: {
      type: Array,
      required: true
    },
    responsive: {
      type: Boolean,
      default: false
    },
    responsiveLayouts: {
      type: Object,
      default: function _default() {
        return {};
      }
    },
    transformScale: {
      type: Number,
      default: 1
    },
    breakpoints: {
      type: Object,
      default: function _default() {
        return {
          lg: 1200,
          md: 996,
          sm: 768,
          xs: 480,
          xxs: 0
        };
      }
    },
    cols: {
      type: Object,
      default: function _default() {
        return {
          lg: 12,
          md: 10,
          sm: 6,
          xs: 4,
          xxs: 2
        };
      }
    },
    preventCollision: {
      type: Boolean,
      default: false
    },
    useStyleCursor: {
      type: Boolean,
      default: true
    }
  },
  data: function data() {
    return {
      width: null,
      mergedStyle: {},
      lastLayoutLength: 0,
      isDragging: false,
      placeholder: {
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        i: -1
      },
      layouts: {},
      // array to store all layouts from different breakpoints
      lastBreakpoint: null,
      // store last active breakpoint
      originalLayout: null // store original Layout

    };
  },
  created: function created() {
    var self = this; // Accessible refernces of functions for removing in beforeDestroy

    self.resizeEventHandler = function (eventType, i, x, y, h, w) {
      self.resizeEvent(eventType, i, x, y, h, w);
    };

    self.dragEventHandler = function (eventType, i, x, y, h, w) {
      self.dragEvent(eventType, i, x, y, h, w);
    };

    self._provided.eventBus = new external_commonjs_vue_commonjs2_vue_root_Vue_default.a();
    self.eventBus = self._provided.eventBus;
    self.eventBus.$on('resizeEvent', self.resizeEventHandler);
    self.eventBus.$on('dragEvent', self.dragEventHandler);
    self.$emit('layout-created', self.layout);
  },
  beforeDestroy: function beforeDestroy() {
    //Remove listeners
    this.eventBus.$off('resizeEvent', this.resizeEventHandler);
    this.eventBus.$off('dragEvent', this.dragEventHandler);
    this.eventBus.$destroy();
    Object(DOM["c" /* removeWindowEventListener */])("resize", this.onWindowResize);

    if (this.erd) {
      this.erd.uninstall(this.$refs.item);
    }
  },
  beforeMount: function beforeMount() {
    this.$emit('layout-before-mount', this.layout);
  },
  mounted: function mounted() {
    this.$emit('layout-mounted', this.layout);
    this.$nextTick(function () {
      Object(utils["l" /* validateLayout */])(this.layout);
      this.originalLayout = this.layout;
      var self = this;
      this.$nextTick(function () {
        self.initResponsiveFeatures();
        self.onWindowResize(); //self.width = self.$el.offsetWidth;

        Object(DOM["a" /* addWindowEventListener */])('resize', self.onWindowResize);
        Object(utils["c" /* compact */])(self.layout, self.verticalCompact);
        self.$emit('layout-updated', self.layout);
        self.updateHeight();
        self.$nextTick(function () {
          this.erd = elementResizeDetectorMaker({
            strategy: "scroll",
            //<- For ultra performance.
            // See https://github.com/wnr/element-resize-detector/issues/110 about callOnAdd.
            callOnAdd: false
          });
          this.erd.listenTo(self.$refs.item, function () {
            self.onWindowResize();
          });
        });
      });
    });
  },
  watch: {
    width: function width(newval, oldval) {
      var self = this;
      this.$nextTick(function () {
        var _this = this;

        //this.$broadcast("updateWidth", this.width);
        this.eventBus.$emit("updateWidth", this.width);

        if (oldval === null) {
          /*
              If oldval == null is when the width has never been
              set before. That only occurs when mouting is
              finished, and onWindowResize has been called and
              this.width has been changed the first time after it
              got set to null in the constructor. It is now time
              to issue layout-ready events as the GridItems have
              their sizes configured properly.
                The reason for emitting the layout-ready events on
              the next tick is to allow for the newly-emitted
              updateWidth event (above) to have reached the
              children GridItem-s and had their effect, so we're
              sure that they have the final size before we emit
              layout-ready (for this GridLayout) and
              item-layout-ready (for the GridItem-s).
                This way any client event handlers can reliably
              invistigate stable sizes of GridItem-s.
          */
          this.$nextTick(function () {
            _this.$emit('layout-ready', self.layout);
          });
        }

        this.updateHeight();
      });
    },
    layout: function layout() {
      this.layoutUpdate();
    },
    colNum: function colNum(val) {
      this.eventBus.$emit("setColNum", val);
    },
    rowHeight: function rowHeight() {
      this.eventBus.$emit("setRowHeight", this.rowHeight);
    },
    isDraggable: function isDraggable() {
      this.eventBus.$emit("setDraggable", this.isDraggable);
    },
    isResizable: function isResizable() {
      this.eventBus.$emit("setResizable", this.isResizable);
    },
    isBounded: function isBounded() {
      this.eventBus.$emit("setBounded", this.isBounded);
    },
    transformScale: function transformScale() {
      this.eventBus.$emit("setTransformScale", this.transformScale);
    },
    responsive: function responsive() {
      if (!this.responsive) {
        this.$emit('update:layout', this.originalLayout);
        this.eventBus.$emit("setColNum", this.colNum);
      }

      this.onWindowResize();
    },
    maxRows: function maxRows() {
      this.eventBus.$emit("setMaxRows", this.maxRows);
    },
    margin: function margin() {
      this.updateHeight();
    }
  },
  methods: {
    layoutUpdate: function layoutUpdate() {
      if (this.layout !== undefined && this.originalLayout !== null) {
        if (this.layout.length !== this.originalLayout.length) {
          // console.log("### LAYOUT UPDATE!", this.layout.length, this.originalLayout.length);
          var diff = this.findDifference(this.layout, this.originalLayout);

          if (diff.length > 0) {
            // console.log(diff);
            if (this.layout.length > this.originalLayout.length) {
              this.originalLayout = this.originalLayout.concat(diff);
            } else {
              this.originalLayout = this.originalLayout.filter(function (obj) {
                return !diff.some(function (obj2) {
                  return obj.i === obj2.i;
                });
              });
            }
          }

          this.lastLayoutLength = this.layout.length;
          this.initResponsiveFeatures();
        }

        Object(utils["c" /* compact */])(this.layout, this.verticalCompact);
        this.eventBus.$emit("updateWidth", this.width);
        this.updateHeight();
        this.$emit('layout-updated', this.layout);
      }
    },
    updateHeight: function updateHeight() {
      this.mergedStyle = {
        height: this.containerHeight()
      };
    },
    onWindowResize: function onWindowResize() {
      if (this.$refs !== null && this.$refs.item !== null && this.$refs.item !== undefined) {
        this.width = this.$refs.item.offsetWidth;
      }

      this.eventBus.$emit("resizeEvent");
    },
    containerHeight: function containerHeight() {
      if (!this.autoSize) return; // console.log("bottom: " + bottom(this.layout))
      // console.log("rowHeight + margins: " + (this.rowHeight + this.margin[1]) + this.margin[1])

      var containerHeight = Object(utils["a" /* bottom */])(this.layout) * (this.rowHeight + this.margin[1]) + this.margin[1] + 'px';
      return containerHeight;
    },
    dragEvent: function dragEvent(eventName, id, x, y, h, w) {
      //console.log(eventName + " id=" + id + ", x=" + x + ", y=" + y);
      var l = Object(utils["f" /* getLayoutItem */])(this.layout, id); //GetLayoutItem sometimes returns null object

      if (l === undefined || l === null) {
        l = {
          x: 0,
          y: 0
        };
      }

      if (eventName === "dragstart" && !this.verticalCompact) {
        this.positionsBeforeDrag = this.layout.reduce(function (result, _ref) {
          var i = _ref.i,
              x = _ref.x,
              y = _ref.y;
          return _objectSpread(_objectSpread({}, result), {}, Object(defineProperty["a" /* default */])({}, i, {
            x: x,
            y: y
          }));
        }, {});
      }

      if (eventName === "dragmove" || eventName === "dragstart") {
        this.placeholder.i = id;
        this.placeholder.x = l.x;
        this.placeholder.y = l.y;
        this.placeholder.w = w;
        this.placeholder.h = h;
        this.$nextTick(function () {
          this.isDragging = true;
        }); //this.$broadcast("updateWidth", this.width);

        this.eventBus.$emit("updateWidth", this.width);
      } else {
        this.$nextTick(function () {
          this.isDragging = false;
        });
      } // Move the element to the dragged location.


      this.layout = Object(utils["g" /* moveElement */])(this.layout, l, x, y, true, this.preventCollision);

      if (this.restoreOnDrag) {
        // Do not compact items more than in layout before drag
        // Set moved item as static to avoid to compact it
        l.static = true;
        Object(utils["c" /* compact */])(this.layout, this.verticalCompact, this.positionsBeforeDrag);
        l.static = false;
      } else {
        Object(utils["c" /* compact */])(this.layout, this.verticalCompact);
      } // needed because vue can't detect changes on array element properties


      this.eventBus.$emit("compact");
      this.updateHeight();

      if (eventName === 'dragend') {
        delete this.positionsBeforeDrag;
        this.$emit('layout-updated', this.layout);
      }
    },
    resizeEvent: function resizeEvent(eventName, id, x, y, h, w) {
      var l = Object(utils["f" /* getLayoutItem */])(this.layout, id); //GetLayoutItem sometimes return null object

      if (l === undefined || l === null) {
        l = {
          h: 0,
          w: 0
        };
      }

      var hasCollisions;

      if (this.preventCollision) {
        var collisions = Object(utils["e" /* getAllCollisions */])(this.layout, _objectSpread(_objectSpread({}, l), {}, {
          w: w,
          h: h
        })).filter(function (layoutItem) {
          return layoutItem.i !== l.i;
        });
        hasCollisions = collisions.length > 0; // If we're colliding, we need adjust the placeholder.

        if (hasCollisions) {
          // adjust w && h to maximum allowed space
          var leastX = Infinity,
              leastY = Infinity;
          collisions.forEach(function (layoutItem) {
            if (layoutItem.x > l.x) leastX = Math.min(leastX, layoutItem.x);
            if (layoutItem.y > l.y) leastY = Math.min(leastY, layoutItem.y);
          });
          if (Number.isFinite(leastX)) l.w = leastX - l.x;
          if (Number.isFinite(leastY)) l.h = leastY - l.y;
        }
      }

      if (!hasCollisions) {
        // Set new width and height.
        l.w = w;
        l.h = h;
      }

      if (eventName === "resizestart" || eventName === "resizemove") {
        this.placeholder.i = id;
        this.placeholder.x = x;
        this.placeholder.y = y;
        this.placeholder.w = l.w;
        this.placeholder.h = l.h;
        this.$nextTick(function () {
          this.isDragging = true;
        }); //this.$broadcast("updateWidth", this.width);

        this.eventBus.$emit("updateWidth", this.width);
      } else {
        this.$nextTick(function () {
          this.isDragging = false;
        });
      }

      if (this.responsive) this.responsiveGridLayout();
      Object(utils["c" /* compact */])(this.layout, this.verticalCompact);
      this.eventBus.$emit("compact");
      this.updateHeight();
      if (eventName === 'resizeend') this.$emit('layout-updated', this.layout);
    },
    // finds or generates new layouts for set breakpoints
    responsiveGridLayout: function responsiveGridLayout() {
      var newBreakpoint = Object(responsiveUtils["b" /* getBreakpointFromWidth */])(this.breakpoints, this.width);
      var newCols = Object(responsiveUtils["c" /* getColsFromBreakpoint */])(newBreakpoint, this.cols); // save actual layout in layouts

      if (this.lastBreakpoint != null && !this.layouts[this.lastBreakpoint]) this.layouts[this.lastBreakpoint] = Object(utils["b" /* cloneLayout */])(this.layout); // Find or generate a new layout.

      var layout = Object(responsiveUtils["a" /* findOrGenerateResponsiveLayout */])(this.originalLayout, this.layouts, this.breakpoints, newBreakpoint, this.lastBreakpoint, newCols, this.verticalCompact); // Store the new layout.

      this.layouts[newBreakpoint] = layout;

      if (this.lastBreakpoint !== newBreakpoint) {
        this.$emit('breakpoint-changed', newBreakpoint, layout);
      } // new prop sync


      this.$emit('update:layout', layout);
      this.lastBreakpoint = newBreakpoint;
      this.eventBus.$emit("setColNum", Object(responsiveUtils["c" /* getColsFromBreakpoint */])(newBreakpoint, this.cols));
    },
    // clear all responsive layouts
    initResponsiveFeatures: function initResponsiveFeatures() {
      // clear layouts
      this.layouts = Object.assign({}, this.responsiveLayouts);
    },
    // find difference in layouts
    findDifference: function findDifference(layout, originalLayout) {
      //Find values that are in result1 but not in result2
      var uniqueResultOne = layout.filter(function (obj) {
        return !originalLayout.some(function (obj2) {
          return obj.i === obj2.i;
        });
      }); //Find values that are in result2 but not in result1

      var uniqueResultTwo = originalLayout.filter(function (obj) {
        return !layout.some(function (obj2) {
          return obj.i === obj2.i;
        });
      }); //Combine the two arrays of unique entries#

      return uniqueResultOne.concat(uniqueResultTwo);
    }
  }
});
// CONCATENATED MODULE: ./src/components/GridLayout.vue?vue&type=script&lang=js&
 /* harmony default export */ var components_GridLayoutvue_type_script_lang_js_ = (GridLayoutvue_type_script_lang_js_); 
// EXTERNAL MODULE: ./src/components/GridLayout.vue?vue&type=style&index=0&lang=css&
var GridLayoutvue_type_style_index_0_lang_css_ = __webpack_require__("e279");

// EXTERNAL MODULE: ./node_modules/vue-loader/lib/runtime/componentNormalizer.js
var componentNormalizer = __webpack_require__("2877");

// CONCATENATED MODULE: ./src/components/GridLayout.vue






/* normalize component */

var component = Object(componentNormalizer["a" /* default */])(
  components_GridLayoutvue_type_script_lang_js_,
  render,
  staticRenderFns,
  false,
  null,
  null,
  null
  
);

/* harmony default export */ var GridLayout = __webpack_exports__["a"] = (component.exports);

/***/ }),

/***/ "38fd":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.9 / 15.2.3.2 Object.getPrototypeOf(O)
var has = __webpack_require__("69a8");
var toObject = __webpack_require__("4bf8");
var IE_PROTO = __webpack_require__("613b")('IE_PROTO');
var ObjectProto = Object.prototype;

module.exports = Object.getPrototypeOf || function (O) {
  O = toObject(O);
  if (has(O, IE_PROTO)) return O[IE_PROTO];
  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  } return O instanceof Object ? ObjectProto : null;
};


/***/ }),

/***/ "41a0":
/***/ (function(module, exports, __webpack_require__) {

var create = __webpack_require__("2aeb");
var descriptor = __webpack_require__("4630");
var setToStringTag = __webpack_require__("7f20");
var IteratorPrototype = {};

// 25.1.2.1.1 %IteratorPrototype%[@@iterator]()
__webpack_require__("32e9")(IteratorPrototype, __webpack_require__("2b4c")('iterator'), function () { return this; });

module.exports = function (Constructor, NAME, next) {
  Constructor.prototype = create(IteratorPrototype, { next: descriptor(1, next) });
  setToStringTag(Constructor, NAME + ' Iterator');
};


/***/ }),

/***/ "456d":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.14 Object.keys(O)
var toObject = __webpack_require__("4bf8");
var $keys = __webpack_require__("0d58");

__webpack_require__("5eda")('keys', function () {
  return function keys(it) {
    return $keys(toObject(it));
  };
});


/***/ }),

/***/ "4588":
/***/ (function(module, exports) {

// 7.1.4 ToInteger
var ceil = Math.ceil;
var floor = Math.floor;
module.exports = function (it) {
  return isNaN(it = +it) ? 0 : (it > 0 ? floor : ceil)(it);
};


/***/ }),

/***/ "4630":
/***/ (function(module, exports) {

module.exports = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};


/***/ }),

/***/ "4917":
/***/ (function(module, exports, __webpack_require__) {


var anObject = __webpack_require__("cb7c");
var toLength = __webpack_require__("9def");
var advanceStringIndex = __webpack_require__("0390");
var regExpExec = __webpack_require__("5f1b");

// @@match logic
__webpack_require__("214f")('match', 1, function (defined, MATCH, $match, maybeCallNative) {
  return [
    // `String.prototype.match` method
    // https://tc39.github.io/ecma262/#sec-string.prototype.match
    function match(regexp) {
      var O = defined(this);
      var fn = regexp == undefined ? undefined : regexp[MATCH];
      return fn !== undefined ? fn.call(regexp, O) : new RegExp(regexp)[MATCH](String(O));
    },
    // `RegExp.prototype[@@match]` method
    // https://tc39.github.io/ecma262/#sec-regexp.prototype-@@match
    function (regexp) {
      var res = maybeCallNative($match, regexp, this);
      if (res.done) return res.value;
      var rx = anObject(regexp);
      var S = String(this);
      if (!rx.global) return regExpExec(rx, S);
      var fullUnicode = rx.unicode;
      rx.lastIndex = 0;
      var A = [];
      var n = 0;
      var result;
      while ((result = regExpExec(rx, S)) !== null) {
        var matchStr = String(result[0]);
        A[n] = matchStr;
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
        n++;
      }
      return n === 0 ? null : A;
    }
  ];
});


/***/ }),

/***/ "499e":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
// ESM COMPAT FLAG
__webpack_require__.r(__webpack_exports__);

// EXPORTS
__webpack_require__.d(__webpack_exports__, "default", function() { return /* binding */ addStylesClient; });

// CONCATENATED MODULE: ./node_modules/vue-style-loader/lib/listToStyles.js
/**
 * Translates the list format produced by css-loader into something
 * easier to manipulate.
 */
function listToStyles (parentId, list) {
  var styles = [];
  var newStyles = {};
  for (var i = 0; i < list.length; i++) {
    var item = list[i];
    var id = item[0];
    var css = item[1];
    var media = item[2];
    var sourceMap = item[3];
    var part = {
      id: parentId + ':' + i,
      css: css,
      media: media,
      sourceMap: sourceMap
    };
    if (!newStyles[id]) {
      styles.push(newStyles[id] = { id: id, parts: [part] });
    } else {
      newStyles[id].parts.push(part);
    }
  }
  return styles
}

// CONCATENATED MODULE: ./node_modules/vue-style-loader/lib/addStylesClient.js
/*
  MIT License http://www.opensource.org/licenses/mit-license.php
  Author Tobias Koppers @sokra
  Modified by Evan You @yyx990803
*/



var hasDocument = typeof document !== 'undefined';

if (typeof DEBUG !== 'undefined' && DEBUG) {
  if (!hasDocument) {
    throw new Error(
    'vue-style-loader cannot be used in a non-browser environment. ' +
    "Use { target: 'node' } in your Webpack config to indicate a server-rendering environment."
  ) }
}

/*
type StyleObject = {
  id: number;
  parts: Array<StyleObjectPart>
}

type StyleObjectPart = {
  css: string;
  media: string;
  sourceMap: ?string
}
*/

var stylesInDom = {/*
  [id: number]: {
    id: number,
    refs: number,
    parts: Array<(obj?: StyleObjectPart) => void>
  }
*/};

var head = hasDocument && (document.head || document.getElementsByTagName('head')[0]);
var singletonElement = null;
var singletonCounter = 0;
var isProduction = false;
var noop = function () {};
var options = null;
var ssrIdKey = 'data-vue-ssr-id';

// Force single-tag solution on IE6-9, which has a hard limit on the # of <style>
// tags it will allow on a page
var isOldIE = typeof navigator !== 'undefined' && /msie [6-9]\b/.test(navigator.userAgent.toLowerCase());

function addStylesClient (parentId, list, _isProduction, _options) {
  isProduction = _isProduction;

  options = _options || {};

  var styles = listToStyles(parentId, list);
  addStylesToDom(styles);

  return function update (newList) {
    var mayRemove = [];
    for (var i = 0; i < styles.length; i++) {
      var item = styles[i];
      var domStyle = stylesInDom[item.id];
      domStyle.refs--;
      mayRemove.push(domStyle);
    }
    if (newList) {
      styles = listToStyles(parentId, newList);
      addStylesToDom(styles);
    } else {
      styles = [];
    }
    for (var i = 0; i < mayRemove.length; i++) {
      var domStyle = mayRemove[i];
      if (domStyle.refs === 0) {
        for (var j = 0; j < domStyle.parts.length; j++) {
          domStyle.parts[j]();
        }
        delete stylesInDom[domStyle.id];
      }
    }
  }
}

function addStylesToDom (styles /* Array<StyleObject> */) {
  for (var i = 0; i < styles.length; i++) {
    var item = styles[i];
    var domStyle = stylesInDom[item.id];
    if (domStyle) {
      domStyle.refs++;
      for (var j = 0; j < domStyle.parts.length; j++) {
        domStyle.parts[j](item.parts[j]);
      }
      for (; j < item.parts.length; j++) {
        domStyle.parts.push(addStyle(item.parts[j]));
      }
      if (domStyle.parts.length > item.parts.length) {
        domStyle.parts.length = item.parts.length;
      }
    } else {
      var parts = [];
      for (var j = 0; j < item.parts.length; j++) {
        parts.push(addStyle(item.parts[j]));
      }
      stylesInDom[item.id] = { id: item.id, refs: 1, parts: parts };
    }
  }
}

function createStyleElement () {
  var styleElement = document.createElement('style');
  styleElement.type = 'text/css';
  head.appendChild(styleElement);
  return styleElement
}

function addStyle (obj /* StyleObjectPart */) {
  var update, remove;
  var styleElement = document.querySelector('style[' + ssrIdKey + '~="' + obj.id + '"]');

  if (styleElement) {
    if (isProduction) {
      // has SSR styles and in production mode.
      // simply do nothing.
      return noop
    } else {
      // has SSR styles but in dev mode.
      // for some reason Chrome can't handle source map in server-rendered
      // style tags - source maps in <style> only works if the style tag is
      // created and inserted dynamically. So we remove the server rendered
      // styles and inject new ones.
      styleElement.parentNode.removeChild(styleElement);
    }
  }

  if (isOldIE) {
    // use singleton mode for IE9.
    var styleIndex = singletonCounter++;
    styleElement = singletonElement || (singletonElement = createStyleElement());
    update = applyToSingletonTag.bind(null, styleElement, styleIndex, false);
    remove = applyToSingletonTag.bind(null, styleElement, styleIndex, true);
  } else {
    // use multi-style-tag mode in all other cases
    styleElement = createStyleElement();
    update = applyToTag.bind(null, styleElement);
    remove = function () {
      styleElement.parentNode.removeChild(styleElement);
    };
  }

  update(obj);

  return function updateStyle (newObj /* StyleObjectPart */) {
    if (newObj) {
      if (newObj.css === obj.css &&
          newObj.media === obj.media &&
          newObj.sourceMap === obj.sourceMap) {
        return
      }
      update(obj = newObj);
    } else {
      remove();
    }
  }
}

var replaceText = (function () {
  var textStore = [];

  return function (index, replacement) {
    textStore[index] = replacement;
    return textStore.filter(Boolean).join('\n')
  }
})();

function applyToSingletonTag (styleElement, index, remove, obj) {
  var css = remove ? '' : obj.css;

  if (styleElement.styleSheet) {
    styleElement.styleSheet.cssText = replaceText(index, css);
  } else {
    var cssNode = document.createTextNode(css);
    var childNodes = styleElement.childNodes;
    if (childNodes[index]) styleElement.removeChild(childNodes[index]);
    if (childNodes.length) {
      styleElement.insertBefore(cssNode, childNodes[index]);
    } else {
      styleElement.appendChild(cssNode);
    }
  }
}

function applyToTag (styleElement, obj) {
  var css = obj.css;
  var media = obj.media;
  var sourceMap = obj.sourceMap;

  if (media) {
    styleElement.setAttribute('media', media);
  }
  if (options.ssrId) {
    styleElement.setAttribute(ssrIdKey, obj.id);
  }

  if (sourceMap) {
    // https://developer.chrome.com/devtools/docs/javascript-debugging
    // this makes source maps inside style tags work properly in Chrome
    css += '\n/*# sourceURL=' + sourceMap.sources[0] + ' */';
    // http://stackoverflow.com/a/26603875
    css += '\n/*# sourceMappingURL=data:application/json;base64,' + btoa(unescape(encodeURIComponent(JSON.stringify(sourceMap)))) + ' */';
  }

  if (styleElement.styleSheet) {
    styleElement.styleSheet.cssText = css;
  } else {
    while (styleElement.firstChild) {
      styleElement.removeChild(styleElement.firstChild);
    }
    styleElement.appendChild(document.createTextNode(css));
  }
}


/***/ }),

/***/ "49ad":
/***/ (function(module, exports, __webpack_require__) {


module.exports = function(idHandler) {
    var eventListeners = {};

    /**
     * Gets all listeners for the given element.
     * @public
     * @param {element} element The element to get all listeners for.
     * @returns All listeners for the given element.
     */
    function getListeners(element) {
        var id = idHandler.get(element);

        if (id === undefined) {
            return [];
        }

        return eventListeners[id] || [];
    }

    /**
     * Stores the given listener for the given element. Will not actually add the listener to the element.
     * @public
     * @param {element} element The element that should have the listener added.
     * @param {function} listener The callback that the element has added.
     */
    function addListener(element, listener) {
        var id = idHandler.get(element);

        if(!eventListeners[id]) {
            eventListeners[id] = [];
        }

        eventListeners[id].push(listener);
    }

    function removeListener(element, listener) {
        var listeners = getListeners(element);
        for (var i = 0, len = listeners.length; i < len; ++i) {
            if (listeners[i] === listener) {
              listeners.splice(i, 1);
              break;
            }
        }
    }

    function removeAllListeners(element) {
      var listeners = getListeners(element);
      if (!listeners) { return; }
      listeners.length = 0;
    }

    return {
        get: getListeners,
        add: addListener,
        removeListener: removeListener,
        removeAllListeners: removeAllListeners
    };
};


/***/ }),

/***/ "4bf8":
/***/ (function(module, exports, __webpack_require__) {

// 7.1.13 ToObject(argument)
var defined = __webpack_require__("be13");
module.exports = function (it) {
  return Object(defined(it));
};


/***/ }),

/***/ "5058":
/***/ (function(module, exports, __webpack_require__) {


module.exports = function(options) {
    var idGenerator     = options.idGenerator;
    var getState        = options.stateHandler.getState;

    /**
     * Gets the resize detector id of the element.
     * @public
     * @param {element} element The target element to get the id of.
     * @returns {string|number|null} The id of the element. Null if it has no id.
     */
    function getId(element) {
        var state = getState(element);

        if (state && state.id !== undefined) {
            return state.id;
        }

        return null;
    }

    /**
     * Sets the resize detector id of the element. Requires the element to have a resize detector state initialized.
     * @public
     * @param {element} element The target element to set the id of.
     * @returns {string|number|null} The id of the element.
     */
    function setId(element) {
        var state = getState(element);

        if (!state) {
            throw new Error("setId required the element to have a resize detection state.");
        }

        var id = idGenerator.generate();

        state.id = id;

        return id;
    }

    return {
        get: getId,
        set: setId
    };
};


/***/ }),

/***/ "50bf":
/***/ (function(module, exports, __webpack_require__) {


var utils = module.exports = {};

utils.getOption = getOption;

function getOption(options, name, defaultValue) {
    var value = options[name];

    if((value === undefined || value === null) && defaultValue !== undefined) {
        return defaultValue;
    }

    return value;
}


/***/ }),

/***/ "520a":
/***/ (function(module, exports, __webpack_require__) {


var regexpFlags = __webpack_require__("0bfb");

var nativeExec = RegExp.prototype.exec;
// This always refers to the native implementation, because the
// String#replace polyfill uses ./fix-regexp-well-known-symbol-logic.js,
// which loads this file before patching the method.
var nativeReplace = String.prototype.replace;

var patchedExec = nativeExec;

var LAST_INDEX = 'lastIndex';

var UPDATES_LAST_INDEX_WRONG = (function () {
  var re1 = /a/,
      re2 = /b*/g;
  nativeExec.call(re1, 'a');
  nativeExec.call(re2, 'a');
  return re1[LAST_INDEX] !== 0 || re2[LAST_INDEX] !== 0;
})();

// nonparticipating capturing group, copied from es5-shim's String#split patch.
var NPCG_INCLUDED = /()??/.exec('')[1] !== undefined;

var PATCH = UPDATES_LAST_INDEX_WRONG || NPCG_INCLUDED;

if (PATCH) {
  patchedExec = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;

    if (NPCG_INCLUDED) {
      reCopy = new RegExp('^' + re.source + '$(?!\\s)', regexpFlags.call(re));
    }
    if (UPDATES_LAST_INDEX_WRONG) lastIndex = re[LAST_INDEX];

    match = nativeExec.call(re, str);

    if (UPDATES_LAST_INDEX_WRONG && match) {
      re[LAST_INDEX] = re.global ? match.index + match[0].length : lastIndex;
    }
    if (NPCG_INCLUDED && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      // eslint-disable-next-line no-loop-func
      nativeReplace.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

module.exports = patchedExec;


/***/ }),

/***/ "52a7":
/***/ (function(module, exports) {

exports.f = {}.propertyIsEnumerable;


/***/ }),

/***/ "5537":
/***/ (function(module, exports, __webpack_require__) {

var core = __webpack_require__("8378");
var global = __webpack_require__("7726");
var SHARED = '__core-js_shared__';
var store = global[SHARED] || (global[SHARED] = {});

(module.exports = function (key, value) {
  return store[key] || (store[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: core.version,
  mode: __webpack_require__("2d00") ? 'pure' : 'global',
  copyright: '© 2020 Denis Pushkarev (zloirock.ru)'
});


/***/ }),

/***/ "55dd":
/***/ (function(module, exports, __webpack_require__) {

var $export = __webpack_require__("5ca1");
var aFunction = __webpack_require__("d8e8");
var toObject = __webpack_require__("4bf8");
var fails = __webpack_require__("79e5");
var $sort = [].sort;
var test = [1, 2, 3];

$export($export.P + $export.F * (fails(function () {
  // IE8-
  test.sort(undefined);
}) || !fails(function () {
  // V8 bug
  test.sort(null);
  // Old WebKit
}) || !__webpack_require__("2f21")($sort)), 'Array', {
  // 22.1.3.25 Array.prototype.sort(comparefn)
  sort: function sort(comparefn) {
    return comparefn === undefined
      ? $sort.call(toObject(this))
      : $sort.call(toObject(this), aFunction(comparefn));
  }
});


/***/ }),

/***/ "5be5":
/***/ (function(module, exports, __webpack_require__) {


module.exports = function(options) {
    var getState = options.stateHandler.getState;

    /**
     * Tells if the element has been made detectable and ready to be listened for resize events.
     * @public
     * @param {element} The element to check.
     * @returns {boolean} True or false depending on if the element is detectable or not.
     */
    function isDetectable(element) {
        var state = getState(element);
        return state && !!state.isDetectable;
    }

    /**
     * Marks the element that it has been made detectable and ready to be listened for resize events.
     * @public
     * @param {element} The element to mark.
     */
    function markAsDetectable(element) {
        getState(element).isDetectable = true;
    }

    /**
     * Tells if the element is busy or not.
     * @public
     * @param {element} The element to check.
     * @returns {boolean} True or false depending on if the element is busy or not.
     */
    function isBusy(element) {
        return !!getState(element).busy;
    }

    /**
     * Marks the object is busy and should not be made detectable.
     * @public
     * @param {element} element The element to mark.
     * @param {boolean} busy If the element is busy or not.
     */
    function markBusy(element, busy) {
        getState(element).busy = !!busy;
    }

    return {
        isDetectable: isDetectable,
        markAsDetectable: markAsDetectable,
        isBusy: isBusy,
        markBusy: markBusy
    };
};


/***/ }),

/***/ "5ca1":
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__("7726");
var core = __webpack_require__("8378");
var hide = __webpack_require__("32e9");
var redefine = __webpack_require__("2aba");
var ctx = __webpack_require__("9b43");
var PROTOTYPE = 'prototype';

var $export = function (type, name, source) {
  var IS_FORCED = type & $export.F;
  var IS_GLOBAL = type & $export.G;
  var IS_STATIC = type & $export.S;
  var IS_PROTO = type & $export.P;
  var IS_BIND = type & $export.B;
  var target = IS_GLOBAL ? global : IS_STATIC ? global[name] || (global[name] = {}) : (global[name] || {})[PROTOTYPE];
  var exports = IS_GLOBAL ? core : core[name] || (core[name] = {});
  var expProto = exports[PROTOTYPE] || (exports[PROTOTYPE] = {});
  var key, own, out, exp;
  if (IS_GLOBAL) source = name;
  for (key in source) {
    // contains in native
    own = !IS_FORCED && target && target[key] !== undefined;
    // export native or passed
    out = (own ? target : source)[key];
    // bind timers to global for call from export context
    exp = IS_BIND && own ? ctx(out, global) : IS_PROTO && typeof out == 'function' ? ctx(Function.call, out) : out;
    // extend global
    if (target) redefine(target, key, out, type & $export.U);
    // export
    if (exports[key] != out) hide(exports, key, exp);
    if (IS_PROTO && expProto[key] != out) expProto[key] = out;
  }
};
global.core = core;
// type bitmap
$export.F = 1;   // forced
$export.G = 2;   // global
$export.S = 4;   // static
$export.P = 8;   // proto
$export.B = 16;  // bind
$export.W = 32;  // wrap
$export.U = 64;  // safe
$export.R = 128; // real proto method for `library`
module.exports = $export;


/***/ }),

/***/ "5dbc":
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__("d3f4");
var setPrototypeOf = __webpack_require__("8b97").set;
module.exports = function (that, target, C) {
  var S = target.constructor;
  var P;
  if (S !== C && typeof S == 'function' && (P = S.prototype) !== C.prototype && isObject(P) && setPrototypeOf) {
    setPrototypeOf(that, P);
  } return that;
};


/***/ }),

/***/ "5ed4":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony import */ var _node_modules_vue_style_loader_index_js_ref_6_oneOf_1_0_node_modules_css_loader_index_js_ref_6_oneOf_1_1_node_modules_vue_loader_lib_loaders_stylePostLoader_js_node_modules_postcss_loader_src_index_js_ref_6_oneOf_1_2_node_modules_postcss_loader_src_index_js_ref_6_oneOf_1_3_node_modules_cache_loader_dist_cjs_js_ref_0_0_node_modules_vue_loader_lib_index_js_vue_loader_options_GridItem_vue_vue_type_style_index_0_lang_css___WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__("6e21");
/* unused harmony reexport * */


/***/ }),

/***/ "5eda":
/***/ (function(module, exports, __webpack_require__) {

// most Object methods by ES6 should accept primitives
var $export = __webpack_require__("5ca1");
var core = __webpack_require__("8378");
var fails = __webpack_require__("79e5");
module.exports = function (KEY, exec) {
  var fn = (core.Object || {})[KEY] || Object[KEY];
  var exp = {};
  exp[KEY] = exec(fn);
  $export($export.S + $export.F * fails(function () { fn(1); }), 'Object', exp);
};


/***/ }),

/***/ "5f1b":
/***/ (function(module, exports, __webpack_require__) {


var classof = __webpack_require__("23c6");
var builtinExec = RegExp.prototype.exec;

 // `RegExpExec` abstract operation
// https://tc39.github.io/ecma262/#sec-regexpexec
module.exports = function (R, S) {
  var exec = R.exec;
  if (typeof exec === 'function') {
    var result = exec.call(R, S);
    if (typeof result !== 'object') {
      throw new TypeError('RegExp exec method returned something other than an Object or null');
    }
    return result;
  }
  if (classof(R) !== 'RegExp') {
    throw new TypeError('RegExp#exec called on incompatible receiver');
  }
  return builtinExec.call(R, S);
};


/***/ }),

/***/ "613b":
/***/ (function(module, exports, __webpack_require__) {

var shared = __webpack_require__("5537")('keys');
var uid = __webpack_require__("ca5a");
module.exports = function (key) {
  return shared[key] || (shared[key] = uid(key));
};


/***/ }),

/***/ "626a":
/***/ (function(module, exports, __webpack_require__) {

// fallback for non-array-like ES3 and non-enumerable old V8 strings
var cof = __webpack_require__("2d95");
// eslint-disable-next-line no-prototype-builtins
module.exports = Object('z').propertyIsEnumerable(0) ? Object : function (it) {
  return cof(it) == 'String' ? it.split('') : Object(it);
};


/***/ }),

/***/ "6821":
/***/ (function(module, exports, __webpack_require__) {

// to indexed object, toObject with fallback for non-array-like ES3 strings
var IObject = __webpack_require__("626a");
var defined = __webpack_require__("be13");
module.exports = function (it) {
  return IObject(defined(it));
};


/***/ }),

/***/ "69a8":
/***/ (function(module, exports) {

var hasOwnProperty = {}.hasOwnProperty;
module.exports = function (it, key) {
  return hasOwnProperty.call(it, key);
};


/***/ }),

/***/ "6a99":
/***/ (function(module, exports, __webpack_require__) {

// 7.1.1 ToPrimitive(input [, PreferredType])
var isObject = __webpack_require__("d3f4");
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string
module.exports = function (it, S) {
  if (!isObject(it)) return it;
  var fn, val;
  if (S && typeof (fn = it.toString) == 'function' && !isObject(val = fn.call(it))) return val;
  if (typeof (fn = it.valueOf) == 'function' && !isObject(val = fn.call(it))) return val;
  if (!S && typeof (fn = it.toString) == 'function' && !isObject(val = fn.call(it))) return val;
  throw TypeError("Can't convert object to primitive value");
};


/***/ }),

/***/ "6e21":
/***/ (function(module, exports, __webpack_require__) {

// style-loader: Adds some css to the DOM by adding a <style> tag

// load the styles
var content = __webpack_require__("9cbe");
if(typeof content === 'string') content = [[module.i, content, '']];
if(content.locals) module.exports = content.locals;
// add the styles to the DOM
var add = __webpack_require__("499e").default;
var update = add("3cbd0c21", content, true, {"sourceMap":false,"shadowMode":false});

/***/ }),

/***/ "7333":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.1 Object.assign(target, source, ...)
var DESCRIPTORS = __webpack_require__("9e1e");
var getKeys = __webpack_require__("0d58");
var gOPS = __webpack_require__("2621");
var pIE = __webpack_require__("52a7");
var toObject = __webpack_require__("4bf8");
var IObject = __webpack_require__("626a");
var $assign = Object.assign;

// should work with symbols and should have deterministic property order (V8 bug)
module.exports = !$assign || __webpack_require__("79e5")(function () {
  var A = {};
  var B = {};
  // eslint-disable-next-line no-undef
  var S = Symbol();
  var K = 'abcdefghijklmnopqrst';
  A[S] = 7;
  K.split('').forEach(function (k) { B[k] = k; });
  return $assign({}, A)[S] != 7 || Object.keys($assign({}, B)).join('') != K;
}) ? function assign(target, source) { // eslint-disable-line no-unused-vars
  var T = toObject(target);
  var aLen = arguments.length;
  var index = 1;
  var getSymbols = gOPS.f;
  var isEnum = pIE.f;
  while (aLen > index) {
    var S = IObject(arguments[index++]);
    var keys = getSymbols ? getKeys(S).concat(getSymbols(S)) : getKeys(S);
    var length = keys.length;
    var j = 0;
    var key;
    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS || isEnum.call(S, key)) T[key] = S[key];
    }
  } return T;
} : $assign;


/***/ }),

/***/ "7726":
/***/ (function(module, exports) {

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
var global = module.exports = typeof window != 'undefined' && window.Math == Math
  ? window : typeof self != 'undefined' && self.Math == Math ? self
  // eslint-disable-next-line no-new-func
  : Function('return this')();
if (typeof __g == 'number') __g = global; // eslint-disable-line no-undef


/***/ }),

/***/ "77f1":
/***/ (function(module, exports, __webpack_require__) {

var toInteger = __webpack_require__("4588");
var max = Math.max;
var min = Math.min;
module.exports = function (index, length) {
  index = toInteger(index);
  return index < 0 ? max(index + length, 0) : min(index, length);
};


/***/ }),

/***/ "79e5":
/***/ (function(module, exports) {

module.exports = function (exec) {
  try {
    return !!exec();
  } catch (e) {
    return true;
  }
};


/***/ }),

/***/ "7f20":
/***/ (function(module, exports, __webpack_require__) {

var def = __webpack_require__("86cc").f;
var has = __webpack_require__("69a8");
var TAG = __webpack_require__("2b4c")('toStringTag');

module.exports = function (it, tag, stat) {
  if (it && !has(it = stat ? it : it.prototype, TAG)) def(it, TAG, { configurable: true, value: tag });
};


/***/ }),

/***/ "7f7f":
/***/ (function(module, exports, __webpack_require__) {

var dP = __webpack_require__("86cc").f;
var FProto = Function.prototype;
var nameRE = /^\s*function ([^ (]*)/;
var NAME = 'name';

// 19.2.4.2 name
NAME in FProto || __webpack_require__("9e1e") && dP(FProto, NAME, {
  configurable: true,
  get: function () {
    try {
      return ('' + this).match(nameRE)[1];
    } catch (e) {
      return '';
    }
  }
});


/***/ }),

/***/ "8378":
/***/ (function(module, exports) {

var core = module.exports = { version: '2.6.12' };
if (typeof __e == 'number') __e = core; // eslint-disable-line no-undef


/***/ }),

/***/ "84f2":
/***/ (function(module, exports) {

module.exports = {};


/***/ }),

/***/ "86cc":
/***/ (function(module, exports, __webpack_require__) {

var anObject = __webpack_require__("cb7c");
var IE8_DOM_DEFINE = __webpack_require__("c69a");
var toPrimitive = __webpack_require__("6a99");
var dP = Object.defineProperty;

exports.f = __webpack_require__("9e1e") ? Object.defineProperty : function defineProperty(O, P, Attributes) {
  anObject(O);
  P = toPrimitive(P, true);
  anObject(Attributes);
  if (IE8_DOM_DEFINE) try {
    return dP(O, P, Attributes);
  } catch (e) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported!');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};


/***/ }),

/***/ "8b97":
/***/ (function(module, exports, __webpack_require__) {

// Works with __proto__ only. Old v8 can't work with null proto objects.
/* eslint-disable no-proto */
var isObject = __webpack_require__("d3f4");
var anObject = __webpack_require__("cb7c");
var check = function (O, proto) {
  anObject(O);
  if (!isObject(proto) && proto !== null) throw TypeError(proto + ": can't set as prototype!");
};
module.exports = {
  set: Object.setPrototypeOf || ('__proto__' in {} ? // eslint-disable-line
    function (test, buggy, set) {
      try {
        set = __webpack_require__("9b43")(Function.call, __webpack_require__("11e9").f(Object.prototype, '__proto__').set, 2);
        set(test, []);
        buggy = !(test instanceof Array);
      } catch (e) { buggy = true; }
      return function setPrototypeOf(O, proto) {
        check(O, proto);
        if (buggy) O.__proto__ = proto;
        else set(O, proto);
        return O;
      };
    }({}, false) : undefined),
  check: check
};


/***/ }),

/***/ "8bbf":
/***/ (function(module, exports) {

module.exports = Vue;

/***/ }),

/***/ "8e6e":
/***/ (function(module, exports, __webpack_require__) {

// https://github.com/tc39/proposal-object-getownpropertydescriptors
var $export = __webpack_require__("5ca1");
var ownKeys = __webpack_require__("990b");
var toIObject = __webpack_require__("6821");
var gOPD = __webpack_require__("11e9");
var createProperty = __webpack_require__("f1ae");

$export($export.S, 'Object', {
  getOwnPropertyDescriptors: function getOwnPropertyDescriptors(object) {
    var O = toIObject(object);
    var getDesc = gOPD.f;
    var keys = ownKeys(O);
    var result = {};
    var i = 0;
    var key, desc;
    while (keys.length > i) {
      desc = getDesc(O, key = keys[i++]);
      if (desc !== undefined) createProperty(result, key, desc);
    }
    return result;
  }
});


/***/ }),

/***/ "9093":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.2.7 / 15.2.3.4 Object.getOwnPropertyNames(O)
var $keys = __webpack_require__("ce10");
var hiddenKeys = __webpack_require__("e11e").concat('length', 'prototype');

exports.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return $keys(O, hiddenKeys);
};


/***/ }),

/***/ "97a7":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return getBreakpointFromWidth; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "c", function() { return getColsFromBreakpoint; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return findOrGenerateResponsiveLayout; });
/* unused harmony export generateResponsiveLayout */
/* unused harmony export sortBreakpoints */
/* harmony import */ var core_js_modules_es6_array_sort__WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__("55dd");
/* harmony import */ var core_js_modules_web_dom_iterable__WEBPACK_IMPORTED_MODULE_1__ = __webpack_require__("ac6a");
/* harmony import */ var core_js_modules_es6_array_iterator__WEBPACK_IMPORTED_MODULE_2__ = __webpack_require__("cadf");
/* harmony import */ var core_js_modules_es6_object_keys__WEBPACK_IMPORTED_MODULE_3__ = __webpack_require__("456d");
/* harmony import */ var _utils__WEBPACK_IMPORTED_MODULE_4__ = __webpack_require__("a2b6");




// @flow


/*:: import type {Layout} from './utils';*/

/*:: export type ResponsiveLayout = {lg?: Layout, md?: Layout, sm?: Layout, xs?: Layout, xxs?: Layout};*/

/*:: type Breakpoint = string;*/

/**
 * Given a width, find the highest breakpoint that matches is valid for it (width > breakpoint).
 *
 * @param  {Object} breakpoints Breakpoints object (e.g. {lg: 1200, md: 960, ...})
 * @param  {Number} width Screen width.
 * @return {String}       Highest breakpoint that is less than width.
 */

/*:: type Breakpoints = {lg?: number, md?: number, sm?: number, xs?: number, xxs?: number};*/

function getBreakpointFromWidth(breakpoints
/*: Breakpoints*/
, width
/*: number*/
)
/*: Breakpoint*/
{
  var sorted = sortBreakpoints(breakpoints);
  var matching = sorted[0];

  for (var i = 1, len = sorted.length; i < len; i++) {
    var breakpointName = sorted[i];
    if (width > breakpoints[breakpointName]) matching = breakpointName;
  }

  return matching;
}
/**
 * Given a breakpoint, get the # of cols set for it.
 * @param  {String} breakpoint Breakpoint name.
 * @param  {Object} cols       Map of breakpoints to cols.
 * @return {Number}            Number of cols.
 */

function getColsFromBreakpoint(breakpoint
/*: Breakpoint*/
, cols
/*: Breakpoints*/
)
/*: number*/
{
  if (!cols[breakpoint]) {
    throw new Error("ResponsiveGridLayout: `cols` entry for breakpoint " + breakpoint + " is missing!");
  }

  return cols[breakpoint];
}
/**
 * Given existing layouts and a new breakpoint, find or generate a new layout.
 *
 * This finds the layout above the new one and generates from it, if it exists.
 *
 * @param  {Array} orgLayout     Original layout.
 * @param  {Object} layouts     Existing layouts.
 * @param  {Array} breakpoints All breakpoints.
 * @param  {String} breakpoint New breakpoint.
 * @param  {String} breakpoint Last breakpoint (for fallback).
 * @param  {Number} cols       Column count at new breakpoint.
 * @param  {Boolean} verticalCompact Whether or not to compact the layout
 *   vertically.
 * @return {Array}             New layout.
 */

function findOrGenerateResponsiveLayout(orgLayout
/*: Layout*/
, layouts
/*: ResponsiveLayout*/
, breakpoints
/*: Breakpoints*/
, breakpoint
/*: Breakpoint*/
, lastBreakpoint
/*: Breakpoint*/
, cols
/*: number*/
, verticalCompact
/*: boolean*/
)
/*: Layout*/
{
  // If it already exists, just return it.
  if (layouts[breakpoint]) return Object(_utils__WEBPACK_IMPORTED_MODULE_4__[/* cloneLayout */ "b"])(layouts[breakpoint]); // Find or generate the next layout

  var layout = orgLayout;
  var breakpointsSorted = sortBreakpoints(breakpoints);
  var breakpointsAbove = breakpointsSorted.slice(breakpointsSorted.indexOf(breakpoint));

  for (var i = 0, len = breakpointsAbove.length; i < len; i++) {
    var b = breakpointsAbove[i];

    if (layouts[b]) {
      layout = layouts[b];
      break;
    }
  }

  layout = Object(_utils__WEBPACK_IMPORTED_MODULE_4__[/* cloneLayout */ "b"])(layout || []); // clone layout so we don't modify existing items

  return Object(_utils__WEBPACK_IMPORTED_MODULE_4__[/* compact */ "c"])(Object(_utils__WEBPACK_IMPORTED_MODULE_4__[/* correctBounds */ "d"])(layout, {
    cols: cols
  }), verticalCompact);
}
/**
 * Given breakpoints, return an array of breakpoints sorted by width. This is usually
 * e.g. ['xxs', 'xs', 'sm', ...]
 *
 * @param  {Object} breakpoints Key/value pair of breakpoint names to widths.
 * @return {Array}              Sorted breakpoints.
 */

function sortBreakpoints(breakpoints
/*: Breakpoints*/
)
/*: Array<Breakpoint>*/
{
  var keys
  /*: Array<string>*/
  = Object.keys(breakpoints);
  return keys.sort(function (a, b) {
    return breakpoints[a] - breakpoints[b];
  });
}

/***/ }),

/***/ "990b":
/***/ (function(module, exports, __webpack_require__) {

// all object keys, includes non-enumerable and symbols
var gOPN = __webpack_require__("9093");
var gOPS = __webpack_require__("2621");
var anObject = __webpack_require__("cb7c");
var Reflect = __webpack_require__("7726").Reflect;
module.exports = Reflect && Reflect.ownKeys || function ownKeys(it) {
  var keys = gOPN.f(anObject(it));
  var getSymbols = gOPS.f;
  return getSymbols ? keys.concat(getSymbols(it)) : keys;
};


/***/ }),

/***/ "9b43":
/***/ (function(module, exports, __webpack_require__) {

// optional / simple context binding
var aFunction = __webpack_require__("d8e8");
module.exports = function (fn, that, length) {
  aFunction(fn);
  if (that === undefined) return fn;
  switch (length) {
    case 1: return function (a) {
      return fn.call(that, a);
    };
    case 2: return function (a, b) {
      return fn.call(that, a, b);
    };
    case 3: return function (a, b, c) {
      return fn.call(that, a, b, c);
    };
  }
  return function (/* ...args */) {
    return fn.apply(that, arguments);
  };
};


/***/ }),

/***/ "9c6c":
/***/ (function(module, exports, __webpack_require__) {

// 22.1.3.31 Array.prototype[@@unscopables]
var UNSCOPABLES = __webpack_require__("2b4c")('unscopables');
var ArrayProto = Array.prototype;
if (ArrayProto[UNSCOPABLES] == undefined) __webpack_require__("32e9")(ArrayProto, UNSCOPABLES, {});
module.exports = function (key) {
  ArrayProto[UNSCOPABLES][key] = true;
};


/***/ }),

/***/ "9cbe":
/***/ (function(module, exports, __webpack_require__) {

exports = module.exports = __webpack_require__("2350")(false);
// imports


// module
exports.push([module.i, ".vue-grid-item{-webkit-transition:all .2s ease;transition:all .2s ease;-webkit-transition-property:left,top,right;transition-property:left,top,right}.vue-grid-item.no-touch{-ms-touch-action:none;touch-action:none}.vue-grid-item.cssTransforms{-webkit-transition-property:-webkit-transform;transition-property:-webkit-transform;transition-property:transform;transition-property:transform,-webkit-transform;left:0;right:auto}.vue-grid-item.cssTransforms.render-rtl{left:auto;right:0}.vue-grid-item.resizing{opacity:.6;z-index:3}.vue-grid-item.vue-draggable-dragging{-webkit-transition:none;transition:none;z-index:3}.vue-grid-item.vue-grid-placeholder{background:red;opacity:.2;-webkit-transition-duration:.1s;transition-duration:.1s;z-index:2;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;-o-user-select:none;user-select:none}.vue-grid-item>.vue-resizable-handle{position:absolute;width:20px;height:20px;bottom:0;right:0;background:url(\"data:image/svg+xml;base64,PHN2ZyBzdHlsZT0iYmFja2dyb3VuZC1jb2xvcjojZmZmZmZmMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgd2lkdGg9IjYiIGhlaWdodD0iNiI+PHBhdGggZD0iTTYgNkgwVjQuMmg0LjJWMEg2djZ6IiBvcGFjaXR5PSIuMzAyIi8+PC9zdmc+\");background-position:100% 100%;padding:0 3px 3px 0;background-repeat:no-repeat;background-origin:content-box;-webkit-box-sizing:border-box;box-sizing:border-box;cursor:se-resize}.vue-grid-item>.vue-rtl-resizable-handle{bottom:0;left:0;background:url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0ibm9uZSIgZD0iTS0xLTFoMTJ2MTJILTF6Ii8+PGc+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InVuZGVmaW5lZCIgc3Ryb2tlLWxpbmVqb2luPSJ1bmRlZmluZWQiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2U9IiMwMDAiIGZpbGw9Im5vbmUiIGQ9Ik0xNDQuODIxLTM4LjM5M2wtMjAuMzU3LTMxLjc4NSIvPjxwYXRoIHN0cm9rZT0iIzY2NiIgc3Ryb2tlLWxpbmVjYXA9InVuZGVmaW5lZCIgc3Ryb2tlLWxpbmVqb2luPSJ1bmRlZmluZWQiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIgZD0iTS45NDctLjAxOHY5LjEyNU0tLjY1NiA5aDEwLjczIi8+PC9nPjwvc3ZnPg==);background-position:0 100%;padding-left:3px;background-repeat:no-repeat;background-origin:content-box;cursor:sw-resize;right:auto}.vue-grid-item.disable-userselect{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}", ""]);

// exports


/***/ }),

/***/ "9def":
/***/ (function(module, exports, __webpack_require__) {

// 7.1.15 ToLength
var toInteger = __webpack_require__("4588");
var min = Math.min;
module.exports = function (it) {
  return it > 0 ? min(toInteger(it), 0x1fffffffffffff) : 0; // pow(2, 53) - 1 == 9007199254740991
};


/***/ }),

/***/ "9e1e":
/***/ (function(module, exports, __webpack_require__) {

// Thank's IE8 for his funny defineProperty
module.exports = !__webpack_require__("79e5")(function () {
  return Object.defineProperty({}, 'a', { get: function () { return 7; } }).a != 7;
});


/***/ }),

/***/ "a2b6":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return bottom; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "b", function() { return cloneLayout; });
/* unused harmony export cloneLayoutItem */
/* unused harmony export collides */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "c", function() { return compact; });
/* unused harmony export compactItem */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "d", function() { return correctBounds; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "f", function() { return getLayoutItem; });
/* unused harmony export getFirstCollision */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "e", function() { return getAllCollisions; });
/* unused harmony export getStatics */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "g", function() { return moveElement; });
/* unused harmony export moveElementAwayFromCollision */
/* unused harmony export perc */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "j", function() { return setTransform; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "k", function() { return setTransformRtl; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "h", function() { return setTopLeft; });
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "i", function() { return setTopRight; });
/* unused harmony export sortLayoutItemsByRowCol */
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "l", function() { return validateLayout; });
/* unused harmony export autoBindHandlers */
/* unused harmony export createMarkup */
/* unused harmony export IS_UNITLESS */
/* unused harmony export addPx */
/* unused harmony export hyphenateRE */
/* unused harmony export hyphenate */
/* unused harmony export findItemInArray */
/* unused harmony export findAndRemove */
/* harmony import */ var core_js_modules_es6_regexp_replace__WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__("a481");
/* harmony import */ var core_js_modules_es6_array_iterator__WEBPACK_IMPORTED_MODULE_1__ = __webpack_require__("cadf");
/* harmony import */ var core_js_modules_es6_object_keys__WEBPACK_IMPORTED_MODULE_2__ = __webpack_require__("456d");
/* harmony import */ var core_js_modules_web_dom_iterable__WEBPACK_IMPORTED_MODULE_3__ = __webpack_require__("ac6a");
/* harmony import */ var core_js_modules_es6_array_sort__WEBPACK_IMPORTED_MODULE_4__ = __webpack_require__("55dd");





// @flow

/*:: export type LayoutItemRequired = {w: number, h: number, x: number, y: number, i: string};*/

/*:: export type LayoutItem = LayoutItemRequired &
                         {minW?: number, minH?: number, maxW?: number, maxH?: number,
                          moved?: boolean, static?: boolean,
                          isDraggable?: ?boolean, isResizable?: ?boolean};*/

// export type Position = {left: number, top: number, width: number, height: number};

/*
export type DragCallbackData = {
  node: HTMLElement,
  x: number, y: number,
  deltaX: number, deltaY: number,
  lastX: number, lastY: number
};
*/
// export type DragEvent = {e: Event} & DragCallbackData;

/*:: export type Layout = Array<LayoutItem>;*/

// export type ResizeEvent = {e: Event, node: HTMLElement, size: Size};
// const isProduction = process.env.NODE_ENV === 'production';

/**
 * Return the bottom coordinate of the layout.
 *
 * @param  {Array} layout Layout array.
 * @return {Number}       Bottom coordinate.
 */

/*:: export type Size = {width: number, height: number};*/

function bottom(layout
/*: Layout*/
)
/*: number*/
{
  var max = 0,
      bottomY;

  for (var i = 0, len = layout.length; i < len; i++) {
    bottomY = layout[i].y + layout[i].h;
    if (bottomY > max) max = bottomY;
  }

  return max;
}
function cloneLayout(layout
/*: Layout*/
)
/*: Layout*/
{
  var newLayout = Array(layout.length);

  for (var i = 0, len = layout.length; i < len; i++) {
    newLayout[i] = cloneLayoutItem(layout[i]);
  }

  return newLayout;
} // Fast path to cloning, since this is monomorphic

function cloneLayoutItem(layoutItem
/*: LayoutItem*/
)
/*: LayoutItem*/
{
  /*return {
    w: layoutItem.w, h: layoutItem.h, x: layoutItem.x, y: layoutItem.y, i: layoutItem.i,
    minW: layoutItem.minW, maxW: layoutItem.maxW, minH: layoutItem.minH, maxH: layoutItem.maxH,
    moved: Boolean(layoutItem.moved), static: Boolean(layoutItem.static),
    // These can be null
    isDraggable: layoutItem.isDraggable, isResizable: layoutItem.isResizable
  };*/
  return JSON.parse(JSON.stringify(layoutItem));
}
/**
 * Given two layoutitems, check if they collide.
 *
 * @return {Boolean}   True if colliding.
 */

function collides(l1
/*: LayoutItem*/
, l2
/*: LayoutItem*/
)
/*: boolean*/
{
  if (l1 === l2) return false; // same element

  if (l1.x + l1.w <= l2.x) return false; // l1 is left of l2

  if (l1.x >= l2.x + l2.w) return false; // l1 is right of l2

  if (l1.y + l1.h <= l2.y) return false; // l1 is above l2

  if (l1.y >= l2.y + l2.h) return false; // l1 is below l2

  return true; // boxes overlap
}
/**
 * Given a layout, compact it. This involves going down each y coordinate and removing gaps
 * between items.
 *
 * @param  {Array} layout Layout.
 * @param  {Boolean} verticalCompact Whether or not to compact the layout
 *   vertically.
 * @param {Object} minPositions
 * @return {Array}       Compacted Layout.
 */

function compact(layout
/*: Layout*/
, verticalCompact
/*: Boolean*/
, minPositions)
/*: Layout*/
{
  // Statics go in the compareWith array right away so items flow around them.
  var compareWith = getStatics(layout); // We go through the items by row and column.

  var sorted = sortLayoutItemsByRowCol(layout); // Holding for new items.

  var out = Array(layout.length);

  for (var i = 0, len = sorted.length; i < len; i++) {
    var l = sorted[i]; // Don't move static elements

    if (!l.static) {
      l = compactItem(compareWith, l, verticalCompact, minPositions); // Add to comparison array. We only collide with items before this one.
      // Statics are already in this array.

      compareWith.push(l);
    } // Add to output array to make sure they still come out in the right order.


    out[layout.indexOf(l)] = l; // Clear moved flag, if it exists.

    l.moved = false;
  }

  return out;
}
/**
 * Compact an item in the layout.
 */

function compactItem(compareWith
/*: Layout*/
, l
/*: LayoutItem*/
, verticalCompact
/*: boolean*/
, minPositions)
/*: LayoutItem*/
{
  if (verticalCompact) {
    // Move the element up as far as it can go without colliding.
    while (l.y > 0 && !getFirstCollision(compareWith, l)) {
      l.y--;
    }
  } else if (minPositions) {
    var minY = minPositions[l.i].y;

    while (l.y > minY && !getFirstCollision(compareWith, l)) {
      l.y--;
    }
  } // Move it down, and keep moving it down if it's colliding.


  var collides;

  while (collides = getFirstCollision(compareWith, l)) {
    l.y = collides.y + collides.h;
  }

  return l;
}
/**
 * Given a layout, make sure all elements fit within its bounds.
 *
 * @param  {Array} layout Layout array.
 * @param  {Number} bounds Number of columns.
 */

function correctBounds(layout
/*: Layout*/
, bounds
/*: {cols: number}*/
)
/*: Layout*/
{
  var collidesWith = getStatics(layout);

  for (var i = 0, len = layout.length; i < len; i++) {
    var l = layout[i]; // Overflows right

    if (l.x + l.w > bounds.cols) l.x = bounds.cols - l.w; // Overflows left

    if (l.x < 0) {
      l.x = 0;
      l.w = bounds.cols;
    }

    if (!l.static) collidesWith.push(l);else {
      // If this is static and collides with other statics, we must move it down.
      // We have to do something nicer than just letting them overlap.
      while (getFirstCollision(collidesWith, l)) {
        l.y++;
      }
    }
  }

  return layout;
}
/**
 * Get a layout item by ID. Used so we can override later on if necessary.
 *
 * @param  {Array}  layout Layout array.
 * @param  {String} id     ID
 * @return {LayoutItem}    Item at ID.
 */

function getLayoutItem(layout
/*: Layout*/
, id
/*: string*/
)
/*: ?LayoutItem*/
{
  for (var i = 0, len = layout.length; i < len; i++) {
    if (layout[i].i === id) return layout[i];
  }
}
/**
 * Returns the first item this layout collides with.
 * It doesn't appear to matter which order we approach this from, although
 * perhaps that is the wrong thing to do.
 *
 * @param  {Object} layoutItem Layout item.
 * @return {Object|undefined}  A colliding layout item, or undefined.
 */

function getFirstCollision(layout
/*: Layout*/
, layoutItem
/*: LayoutItem*/
)
/*: ?LayoutItem*/
{
  for (var i = 0, len = layout.length; i < len; i++) {
    if (collides(layout[i], layoutItem)) return layout[i];
  }
}
function getAllCollisions(layout
/*: Layout*/
, layoutItem
/*: LayoutItem*/
)
/*: Array<LayoutItem>*/
{
  return layout.filter(function (l) {
    return collides(l, layoutItem);
  });
}
/**
 * Get all static elements.
 * @param  {Array} layout Array of layout objects.
 * @return {Array}        Array of static layout items..
 */

function getStatics(layout
/*: Layout*/
)
/*: Array<LayoutItem>*/
{
  //return [];
  return layout.filter(function (l) {
    return l.static;
  });
}
/**
 * Move an element. Responsible for doing cascading movements of other elements.
 *
 * @param  {Array}      layout Full layout to modify.
 * @param  {LayoutItem} l      element to move.
 * @param  {Number}     [x]    X position in grid units.
 * @param  {Number}     [y]    Y position in grid units.
 * @param  {Boolean}    [isUserAction] If true, designates that the item we're moving is
 *                                     being dragged/resized by th euser.
 */

function moveElement(layout
/*: Layout*/
, l
/*: LayoutItem*/
, x
/*: Number*/
, y
/*: Number*/
, isUserAction
/*: Boolean*/
, preventCollision
/*: Boolean*/
)
/*: Layout*/
{
  if (l.static) return layout; // Short-circuit if nothing to do.
  //if (l.y === y && l.x === x) return layout;

  var oldX = l.x;
  var oldY = l.y;
  var movingUp = y && l.y > y; // This is quite a bit faster than extending the object

  if (typeof x === 'number') l.x = x;
  if (typeof y === 'number') l.y = y;
  l.moved = true; // If this collides with anything, move it.
  // When doing this comparison, we have to sort the items we compare with
  // to ensure, in the case of multiple collisions, that we're getting the
  // nearest collision.

  var sorted = sortLayoutItemsByRowCol(layout);
  if (movingUp) sorted = sorted.reverse();
  var collisions = getAllCollisions(sorted, l);

  if (preventCollision && collisions.length) {
    l.x = oldX;
    l.y = oldY;
    l.moved = false;
    return layout;
  } // Move each item that collides away from this element.


  for (var i = 0, len = collisions.length; i < len; i++) {
    var collision = collisions[i]; // console.log('resolving collision between', l.i, 'at', l.y, 'and', collision.i, 'at', collision.y);
    // Short circuit so we can't infinite loop

    if (collision.moved) continue; // This makes it feel a bit more precise by waiting to swap for just a bit when moving up.

    if (l.y > collision.y && l.y - collision.y > collision.h / 4) continue; // Don't move static items - we have to move *this* element away

    if (collision.static) {
      layout = moveElementAwayFromCollision(layout, collision, l, isUserAction);
    } else {
      layout = moveElementAwayFromCollision(layout, l, collision, isUserAction);
    }
  }

  return layout;
}
/**
 * This is where the magic needs to happen - given a collision, move an element away from the collision.
 * We attempt to move it up if there's room, otherwise it goes below.
 *
 * @param  {Array} layout            Full layout to modify.
 * @param  {LayoutItem} collidesWith Layout item we're colliding with.
 * @param  {LayoutItem} itemToMove   Layout item we're moving.
 * @param  {Boolean} [isUserAction]  If true, designates that the item we're moving is being dragged/resized
 *                                   by the user.
 */

function moveElementAwayFromCollision(layout
/*: Layout*/
, collidesWith
/*: LayoutItem*/
, itemToMove
/*: LayoutItem*/
, isUserAction
/*: ?boolean*/
)
/*: Layout*/
{
  var preventCollision = false; // we're already colliding
  // If there is enough space above the collision to put this element, move it there.
  // We only do this on the main collision as this can get funky in cascades and cause
  // unwanted swapping behavior.

  if (isUserAction) {
    // Make a mock item so we don't modify the item here, only modify in moveElement.
    var fakeItem
    /*: LayoutItem*/
    = {
      x: itemToMove.x,
      y: itemToMove.y,
      w: itemToMove.w,
      h: itemToMove.h,
      i: '-1'
    };
    fakeItem.y = Math.max(collidesWith.y - itemToMove.h, 0);

    if (!getFirstCollision(layout, fakeItem)) {
      return moveElement(layout, itemToMove, undefined, fakeItem.y, preventCollision);
    }
  } // Previously this was optimized to move below the collision directly, but this can cause problems
  // with cascading moves, as an item may actually leapflog a collision and cause a reversal in order.


  return moveElement(layout, itemToMove, undefined, itemToMove.y + 1, preventCollision);
}
function setTransform(top, left, width, height)
/*: Object*/
{
  // Replace unitless items with px
  var translate = "translate3d(" + left + "px," + top + "px, 0)";
  return {
    transform: translate,
    WebkitTransform: translate,
    MozTransform: translate,
    msTransform: translate,
    OTransform: translate,
    width: width + "px",
    height: height + "px",
    position: 'absolute'
  };
}
/**
 * Just like the setTransform method, but instead it will return a negative value of right.
 *
 * @param top
 * @param right
 * @param width
 * @param height
 * @returns {{transform: string, WebkitTransform: string, MozTransform: string, msTransform: string, OTransform: string, width: string, height: string, position: string}}
 */

function setTransformRtl(top, right, width, height)
/*: Object*/
{
  // Replace unitless items with px
  var translate = "translate3d(" + right * -1 + "px," + top + "px, 0)";
  return {
    transform: translate,
    WebkitTransform: translate,
    MozTransform: translate,
    msTransform: translate,
    OTransform: translate,
    width: width + "px",
    height: height + "px",
    position: 'absolute'
  };
}
function setTopLeft(top, left, width, height)
/*: Object*/
{
  return {
    top: top + "px",
    left: left + "px",
    width: width + "px",
    height: height + "px",
    position: 'absolute'
  };
}
/**
 * Just like the setTopLeft method, but instead, it will return a right property instead of left.
 *
 * @param top
 * @param right
 * @param width
 * @param height
 * @returns {{top: string, right: string, width: string, height: string, position: string}}
 */

function setTopRight(top, right, width, height)
/*: Object*/
{
  return {
    top: top + "px",
    right: right + "px",
    width: width + "px",
    height: height + "px",
    position: 'absolute'
  };
}
/**
 * Get layout items sorted from top left to right and down.
 *
 * @return {Array} Array of layout objects.
 * @return {Array}        Layout, sorted static items first.
 */

function sortLayoutItemsByRowCol(layout
/*: Layout*/
)
/*: Layout*/
{
  return [].concat(layout).sort(function (a, b) {
    if (a.y === b.y && a.x === b.x) {
      return 0;
    }

    if (a.y > b.y || a.y === b.y && a.x > b.x) {
      return 1;
    }

    return -1;
  });
}
/**
 * Generate a layout using the initialLayout and children as a template.
 * Missing entries will be added, extraneous ones will be truncated.
 *
 * @param  {Array}  initialLayout Layout passed in through props.
 * @param  {String} breakpoint    Current responsive breakpoint.
 * @param  {Boolean} verticalCompact Whether or not to compact the layout vertically.
 * @return {Array}                Working layout.
 */

/*
export function synchronizeLayoutWithChildren(initialLayout: Layout, children: Array<React.Element>|React.Element,
                                              cols: number, verticalCompact: boolean): Layout {
  // ensure 'children' is always an array
  if (!Array.isArray(children)) {
    children = [children];
  }
  initialLayout = initialLayout || [];

  // Generate one layout item per child.
  let layout: Layout = [];
  for (let i = 0, len = children.length; i < len; i++) {
    let newItem;
    const child = children[i];

    // Don't overwrite if it already exists.
    const exists = getLayoutItem(initialLayout, child.key || "1" /!* FIXME satisfies Flow *!/);
    if (exists) {
      newItem = exists;
    } else {
      const g = child.props._grid;

      // Hey, this item has a _grid property, use it.
      if (g) {
        if (!isProduction) {
          validateLayout([g], 'ReactGridLayout.children');
        }
        // Validated; add it to the layout. Bottom 'y' possible is the bottom of the layout.
        // This allows you to do nice stuff like specify {y: Infinity}
        if (verticalCompact) {
          newItem = cloneLayoutItem({...g, y: Math.min(bottom(layout), g.y), i: child.key});
        } else {
          newItem = cloneLayoutItem({...g, y: g.y, i: child.key});
        }
      }
      // Nothing provided: ensure this is added to the bottom
      else {
        newItem = cloneLayoutItem({w: 1, h: 1, x: 0, y: bottom(layout), i: child.key || "1"});
      }
    }
    layout[i] = newItem;
  }

  // Correct the layout.
  layout = correctBounds(layout, {cols: cols});
  layout = compact(layout, verticalCompact);

  return layout;
}
*/

/**
 * Validate a layout. Throws errors.
 *
 * @param  {Array}  layout        Array of layout items.
 * @param  {String} [contextName] Context name for errors.
 * @throw  {Error}                Validation error.
 */

function validateLayout(layout
/*: Layout*/
, contextName
/*: string*/
)
/*: void*/
{
  contextName = contextName || "Layout";
  var subProps = ['x', 'y', 'w', 'h'];
  var keyArr = [];
  if (!Array.isArray(layout)) throw new Error(contextName + " must be an array!");

  for (var i = 0, len = layout.length; i < len; i++) {
    var item = layout[i];

    for (var j = 0; j < subProps.length; j++) {
      if (typeof item[subProps[j]] !== 'number') {
        throw new Error('VueGridLayout: ' + contextName + '[' + i + '].' + subProps[j] + ' must be a number!');
      }
    }

    if (item.i === undefined || item.i === null) {
      throw new Error('VueGridLayout: ' + contextName + '[' + i + '].i cannot be null!');
    }

    if (typeof item.i !== 'number' && typeof item.i !== 'string') {
      throw new Error('VueGridLayout: ' + contextName + '[' + i + '].i must be a string or number!');
    }

    if (keyArr.indexOf(item.i) >= 0) {
      throw new Error('VueGridLayout: ' + contextName + '[' + i + '].i must be unique!');
    }

    keyArr.push(item.i);

    if (item.static !== undefined && typeof item.static !== 'boolean') {
      throw new Error('VueGridLayout: ' + contextName + '[' + i + '].static must be a boolean!');
    }
  }
} // Flow can't really figure this out, so we just use Object

/***/ }),

/***/ "a481":
/***/ (function(module, exports, __webpack_require__) {


var anObject = __webpack_require__("cb7c");
var toObject = __webpack_require__("4bf8");
var toLength = __webpack_require__("9def");
var toInteger = __webpack_require__("4588");
var advanceStringIndex = __webpack_require__("0390");
var regExpExec = __webpack_require__("5f1b");
var max = Math.max;
var min = Math.min;
var floor = Math.floor;
var SUBSTITUTION_SYMBOLS = /\$([$&`']|\d\d?|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED = /\$([$&`']|\d\d?)/g;

var maybeToString = function (it) {
  return it === undefined ? it : String(it);
};

// @@replace logic
__webpack_require__("214f")('replace', 2, function (defined, REPLACE, $replace, maybeCallNative) {
  return [
    // `String.prototype.replace` method
    // https://tc39.github.io/ecma262/#sec-string.prototype.replace
    function replace(searchValue, replaceValue) {
      var O = defined(this);
      var fn = searchValue == undefined ? undefined : searchValue[REPLACE];
      return fn !== undefined
        ? fn.call(searchValue, O, replaceValue)
        : $replace.call(String(O), searchValue, replaceValue);
    },
    // `RegExp.prototype[@@replace]` method
    // https://tc39.github.io/ecma262/#sec-regexp.prototype-@@replace
    function (regexp, replaceValue) {
      var res = maybeCallNative($replace, regexp, this, replaceValue);
      if (res.done) return res.value;

      var rx = anObject(regexp);
      var S = String(this);
      var functionalReplace = typeof replaceValue === 'function';
      if (!functionalReplace) replaceValue = String(replaceValue);
      var global = rx.global;
      if (global) {
        var fullUnicode = rx.unicode;
        rx.lastIndex = 0;
      }
      var results = [];
      while (true) {
        var result = regExpExec(rx, S);
        if (result === null) break;
        results.push(result);
        if (!global) break;
        var matchStr = String(result[0]);
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
      }
      var accumulatedResult = '';
      var nextSourcePosition = 0;
      for (var i = 0; i < results.length; i++) {
        result = results[i];
        var matched = String(result[0]);
        var position = max(min(toInteger(result.index), S.length), 0);
        var captures = [];
        // NOTE: This is equivalent to
        //   captures = result.slice(1).map(maybeToString)
        // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
        // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
        // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.
        for (var j = 1; j < result.length; j++) captures.push(maybeToString(result[j]));
        var namedCaptures = result.groups;
        if (functionalReplace) {
          var replacerArgs = [matched].concat(captures, position, S);
          if (namedCaptures !== undefined) replacerArgs.push(namedCaptures);
          var replacement = String(replaceValue.apply(undefined, replacerArgs));
        } else {
          replacement = getSubstitution(matched, S, position, captures, namedCaptures, replaceValue);
        }
        if (position >= nextSourcePosition) {
          accumulatedResult += S.slice(nextSourcePosition, position) + replacement;
          nextSourcePosition = position + matched.length;
        }
      }
      return accumulatedResult + S.slice(nextSourcePosition);
    }
  ];

    // https://tc39.github.io/ecma262/#sec-getsubstitution
  function getSubstitution(matched, str, position, captures, namedCaptures, replacement) {
    var tailPos = position + matched.length;
    var m = captures.length;
    var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED;
    if (namedCaptures !== undefined) {
      namedCaptures = toObject(namedCaptures);
      symbols = SUBSTITUTION_SYMBOLS;
    }
    return $replace.call(replacement, symbols, function (match, ch) {
      var capture;
      switch (ch.charAt(0)) {
        case '$': return '$';
        case '&': return matched;
        case '`': return str.slice(0, position);
        case "'": return str.slice(tailPos);
        case '<':
          capture = namedCaptures[ch.slice(1, -1)];
          break;
        default: // \d\d?
          var n = +ch;
          if (n === 0) return match;
          if (n > m) {
            var f = floor(n / 10);
            if (f === 0) return match;
            if (f <= m) return captures[f - 1] === undefined ? ch.charAt(1) : captures[f - 1] + ch.charAt(1);
            return match;
          }
          capture = captures[n - 1];
      }
      return capture === undefined ? '' : capture;
    });
  }
});


/***/ }),

/***/ "aa77":
/***/ (function(module, exports, __webpack_require__) {

var $export = __webpack_require__("5ca1");
var defined = __webpack_require__("be13");
var fails = __webpack_require__("79e5");
var spaces = __webpack_require__("fdef");
var space = '[' + spaces + ']';
var non = '\u200b\u0085';
var ltrim = RegExp('^' + space + space + '*');
var rtrim = RegExp(space + space + '*$');

var exporter = function (KEY, exec, ALIAS) {
  var exp = {};
  var FORCE = fails(function () {
    return !!spaces[KEY]() || non[KEY]() != non;
  });
  var fn = exp[KEY] = FORCE ? exec(trim) : spaces[KEY];
  if (ALIAS) exp[ALIAS] = fn;
  $export($export.P + $export.F * FORCE, 'String', exp);
};

// 1 -> String#trimLeft
// 2 -> String#trimRight
// 3 -> String#trim
var trim = exporter.trim = function (string, TYPE) {
  string = String(defined(string));
  if (TYPE & 1) string = string.replace(ltrim, '');
  if (TYPE & 2) string = string.replace(rtrim, '');
  return string;
};

module.exports = exporter;


/***/ }),

/***/ "abb4":
/***/ (function(module, exports, __webpack_require__) {


/* global console: false */

/**
 * Reporter that handles the reporting of logs, warnings and errors.
 * @public
 * @param {boolean} quiet Tells if the reporter should be quiet or not.
 */
module.exports = function(quiet) {
    function noop() {
        //Does nothing.
    }

    var reporter = {
        log: noop,
        warn: noop,
        error: noop
    };

    if(!quiet && window.console) {
        var attachFunction = function(reporter, name) {
            //The proxy is needed to be able to call the method with the console context,
            //since we cannot use bind.
            reporter[name] = function reporterProxy() {
                var f = console[name];
                if (f.apply) { //IE9 does not support console.log.apply :)
                    f.apply(console, arguments);
                } else {
                    for (var i = 0; i < arguments.length; i++) {
                        f(arguments[i]);
                    }
                }
            };
        };

        attachFunction(reporter, "log");
        attachFunction(reporter, "warn");
        attachFunction(reporter, "error");
    }

    return reporter;
};

/***/ }),

/***/ "ac6a":
/***/ (function(module, exports, __webpack_require__) {

var $iterators = __webpack_require__("cadf");
var getKeys = __webpack_require__("0d58");
var redefine = __webpack_require__("2aba");
var global = __webpack_require__("7726");
var hide = __webpack_require__("32e9");
var Iterators = __webpack_require__("84f2");
var wks = __webpack_require__("2b4c");
var ITERATOR = wks('iterator');
var TO_STRING_TAG = wks('toStringTag');
var ArrayValues = Iterators.Array;

var DOMIterables = {
  CSSRuleList: true, // TODO: Not spec compliant, should be false.
  CSSStyleDeclaration: false,
  CSSValueList: false,
  ClientRectList: false,
  DOMRectList: false,
  DOMStringList: false,
  DOMTokenList: true,
  DataTransferItemList: false,
  FileList: false,
  HTMLAllCollection: false,
  HTMLCollection: false,
  HTMLFormElement: false,
  HTMLSelectElement: false,
  MediaList: true, // TODO: Not spec compliant, should be false.
  MimeTypeArray: false,
  NamedNodeMap: false,
  NodeList: true,
  PaintRequestList: false,
  Plugin: false,
  PluginArray: false,
  SVGLengthList: false,
  SVGNumberList: false,
  SVGPathSegList: false,
  SVGPointList: false,
  SVGStringList: false,
  SVGTransformList: false,
  SourceBufferList: false,
  StyleSheetList: true, // TODO: Not spec compliant, should be false.
  TextTrackCueList: false,
  TextTrackList: false,
  TouchList: false
};

for (var collections = getKeys(DOMIterables), i = 0; i < collections.length; i++) {
  var NAME = collections[i];
  var explicit = DOMIterables[NAME];
  var Collection = global[NAME];
  var proto = Collection && Collection.prototype;
  var key;
  if (proto) {
    if (!proto[ITERATOR]) hide(proto, ITERATOR, ArrayValues);
    if (!proto[TO_STRING_TAG]) hide(proto, TO_STRING_TAG, NAME);
    Iterators[NAME] = ArrayValues;
    if (explicit) for (key in $iterators) if (!proto[key]) redefine(proto, key, $iterators[key], true);
  }
}


/***/ }),

/***/ "ad20":
/***/ (function(module, exports, __webpack_require__) {

exports = module.exports = __webpack_require__("2350")(false);
// imports


// module
exports.push([module.i, ".vue-grid-layout{position:relative;-webkit-transition:height .2s ease;transition:height .2s ease}", ""]);

// exports


/***/ }),

/***/ "ade3":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony export (binding) */ __webpack_require__.d(__webpack_exports__, "a", function() { return _defineProperty; });
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

/***/ }),

/***/ "b0c5":
/***/ (function(module, exports, __webpack_require__) {

var regexpExec = __webpack_require__("520a");
__webpack_require__("5ca1")({
  target: 'RegExp',
  proto: true,
  forced: regexpExec !== /./.exec
}, {
  exec: regexpExec
});


/***/ }),

/***/ "b770":
/***/ (function(module, exports, __webpack_require__) {


var utils = module.exports = {};

/**
 * Loops through the collection and calls the callback for each element. if the callback returns truthy, the loop is broken and returns the same value.
 * @public
 * @param {*} collection The collection to loop through. Needs to have a length property set and have indices set from 0 to length - 1.
 * @param {function} callback The callback to be called for each element. The element will be given as a parameter to the callback. If this callback returns truthy, the loop is broken and the same value is returned.
 * @returns {*} The value that a callback has returned (if truthy). Otherwise nothing.
 */
utils.forEach = function(collection, callback) {
    for(var i = 0; i < collection.length; i++) {
        var result = callback(collection[i]);
        if(result) {
            return result;
        }
    }
};


/***/ }),

/***/ "bc21":
/***/ (function(module, __webpack_exports__, __webpack_require__) {

// NAMESPACE OBJECT: ./node_modules/@interactjs/snappers/all.js
var all_namespaceObject = {};
__webpack_require__.r(all_namespaceObject);
__webpack_require__.d(all_namespaceObject, "edgeTarget", function() { return edgeTarget; });
__webpack_require__.d(all_namespaceObject, "elements", function() { return snappers_elements; });
__webpack_require__.d(all_namespaceObject, "grid", function() { return grid; });

// CONCATENATED MODULE: ./node_modules/cache-loader/dist/cjs.js?{"cacheDirectory":"node_modules/.cache/vue-loader","cacheIdentifier":"1705dc22-vue-loader-template"}!./node_modules/vue-loader/lib/loaders/templateLoader.js??vue-loader-options!./node_modules/cache-loader/dist/cjs.js??ref--0-0!./node_modules/vue-loader/lib??vue-loader-options!./src/components/GridItem.vue?vue&type=template&id=e7489122&
var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"item",staticClass:"vue-grid-item",class:_vm.classObj,style:(_vm.style)},[_vm._t("default"),(_vm.resizableAndNotStatic)?_c('span',{ref:"handle",class:_vm.resizableHandleClass}):_vm._e()],2)};
var staticRenderFns = [];


// CONCATENATED MODULE: ./src/components/GridItem.vue?vue&type=template&id=e7489122&

// EXTERNAL MODULE: ./node_modules/core-js/modules/es7.object.get-own-property-descriptors.js
var es7_object_get_own_property_descriptors = __webpack_require__("8e6e");

// EXTERNAL MODULE: ./node_modules/core-js/modules/web.dom.iterable.js
var web_dom_iterable = __webpack_require__("ac6a");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.array.iterator.js
var es6_array_iterator = __webpack_require__("cadf");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.object.keys.js
var es6_object_keys = __webpack_require__("456d");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.regexp.replace.js
var es6_regexp_replace = __webpack_require__("a481");

// EXTERNAL MODULE: ./node_modules/@babel/runtime/helpers/esm/defineProperty.js
var defineProperty = __webpack_require__("ade3");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.number.is-finite.js
var es6_number_is_finite = __webpack_require__("fca0");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.regexp.match.js
var es6_regexp_match = __webpack_require__("4917");

// EXTERNAL MODULE: ./node_modules/core-js/modules/es6.number.constructor.js
var es6_number_constructor = __webpack_require__("c5f6");

// EXTERNAL MODULE: ./src/helpers/utils.js
var utils = __webpack_require__("a2b6");

// CONCATENATED MODULE: ./src/helpers/draggableUtils.js
// Get {x, y} positions from event.
function getControlPosition(e) {
  return offsetXYFromParentOf(e);
} // Get from offsetParent

function offsetXYFromParentOf(evt) {
  var offsetParent = evt.target.offsetParent || document.body;
  var offsetParentRect = evt.offsetParent === document.body ? {
    left: 0,
    top: 0
  } : offsetParent.getBoundingClientRect();
  var x = evt.clientX + offsetParent.scrollLeft - offsetParentRect.left;
  var y = evt.clientY + offsetParent.scrollTop - offsetParentRect.top;
  /*const x = Math.round(evt.clientX + offsetParent.scrollLeft - offsetParentRect.left);
  const y = Math.round(evt.clientY + offsetParent.scrollTop - offsetParentRect.top);*/

  return {
    x: x,
    y: y
  };
} // Create an data object exposed by <DraggableCore>'s events

function createCoreData(lastX, lastY, x, y) {
  // State changes are often (but not always!) async. We want the latest value.
  var isStart = !isNum(lastX);

  if (isStart) {
    // If this is our first move, use the x and y as last coords.
    return {
      deltaX: 0,
      deltaY: 0,
      lastX: x,
      lastY: y,
      x: x,
      y: y
    };
  } else {
    // Otherwise calculate proper values.
    return {
      deltaX: x - lastX,
      deltaY: y - lastY,
      lastX: lastX,
      lastY: lastY,
      x: x,
      y: y
    };
  }
}

function isNum(num) {
  return typeof num === 'number' && !isNaN(num);
}
// EXTERNAL MODULE: ./src/helpers/responsiveUtils.js
var responsiveUtils = __webpack_require__("97a7");

// EXTERNAL MODULE: ./src/helpers/DOM.js
var DOM = __webpack_require__("1ca7");

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/domObjects.js
const domObjects = {
  init,
  document: null,
  DocumentFragment: null,
  SVGElement: null,
  SVGSVGElement: null,
  SVGElementInstance: null,
  Element: null,
  HTMLElement: null,
  Event: null,
  Touch: null,
  PointerEvent: null
};

function blank() {}

/* harmony default export */ var utils_domObjects = (domObjects);

function init(window) {
  const win = window;
  domObjects.document = win.document;
  domObjects.DocumentFragment = win.DocumentFragment || blank;
  domObjects.SVGElement = win.SVGElement || blank;
  domObjects.SVGSVGElement = win.SVGSVGElement || blank;
  domObjects.SVGElementInstance = win.SVGElementInstance || blank;
  domObjects.Element = win.Element || blank;
  domObjects.HTMLElement = win.HTMLElement || domObjects.Element;
  domObjects.Event = win.Event;
  domObjects.Touch = win.Touch || blank;
  domObjects.PointerEvent = win.PointerEvent || win.MSPointerEvent;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/isWindow.js
/* harmony default export */ var isWindow = (thing => !!(thing && thing.Window) && thing instanceof thing.Window);

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/window.js

let realWindow = undefined;
let win = undefined;

function window_init(window) {
  // get wrapped window if using Shadow DOM polyfill
  realWindow = window; // create a TextNode

  const el = window.document.createTextNode(''); // check if it's wrapped by a polyfill

  if (el.ownerDocument !== window.document && typeof window.wrap === 'function' && window.wrap(el) === el) {
    // use wrapped window
    window = window.wrap(window);
  }

  win = window;
}

if (typeof window !== 'undefined' && !!window) {
  window_init(window);
}

function getWindow(node) {
  if (isWindow(node)) {
    return node;
  }

  const rootNode = node.ownerDocument || node;
  return rootNode.defaultView || win.window;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/is.js



const is_window = thing => thing === win || isWindow(thing);

const docFrag = thing => object(thing) && thing.nodeType === 11;

const object = thing => !!thing && typeof thing === 'object';

const func = thing => typeof thing === 'function';

const number = thing => typeof thing === 'number';

const bool = thing => typeof thing === 'boolean';

const string = thing => typeof thing === 'string';

const is_element = thing => {
  if (!thing || typeof thing !== 'object') {
    return false;
  } // eslint-disable-next-line import/no-named-as-default-member


  const _window = getWindow(thing) || win;

  return /object|function/.test(typeof _window.Element) ? thing instanceof _window.Element // DOM2
  : thing.nodeType === 1 && typeof thing.nodeName === 'string';
};

const plainObject = thing => object(thing) && !!thing.constructor && /function Object\b/.test(thing.constructor.toString());

const array = thing => object(thing) && typeof thing.length !== 'undefined' && func(thing.splice);

/* harmony default export */ var is = ({
  window: is_window,
  docFrag,
  object,
  func,
  number,
  bool,
  string,
  element: is_element,
  plainObject,
  array
});

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/browser.js



const browser = {
  init: browser_init,
  supportsTouch: null,
  supportsPointerEvent: null,
  isIOS7: null,
  isIOS: null,
  isIe9: null,
  isOperaMobile: null,
  prefixedMatchesSelector: null,
  pEventTypes: null,
  wheelEvent: null
};

function browser_init(window) {
  const Element = utils_domObjects.Element;
  const navigator = win.navigator; // Does the browser support touch input?

  browser.supportsTouch = 'ontouchstart' in window || is.func(window.DocumentTouch) && utils_domObjects.document instanceof window.DocumentTouch; // Does the browser support PointerEvents

  browser.supportsPointerEvent = navigator.pointerEnabled !== false && !!utils_domObjects.PointerEvent;
  browser.isIOS = /iP(hone|od|ad)/.test(navigator.platform); // scrolling doesn't change the result of getClientRects on iOS 7

  browser.isIOS7 = /iP(hone|od|ad)/.test(navigator.platform) && /OS 7[^\d]/.test(navigator.appVersion);
  browser.isIe9 = /MSIE 9/.test(navigator.userAgent); // Opera Mobile must be handled differently

  browser.isOperaMobile = navigator.appName === 'Opera' && browser.supportsTouch && /Presto/.test(navigator.userAgent); // prefix matchesSelector

  browser.prefixedMatchesSelector = 'matches' in Element.prototype ? 'matches' : 'webkitMatchesSelector' in Element.prototype ? 'webkitMatchesSelector' : 'mozMatchesSelector' in Element.prototype ? 'mozMatchesSelector' : 'oMatchesSelector' in Element.prototype ? 'oMatchesSelector' : 'msMatchesSelector';
  browser.pEventTypes = browser.supportsPointerEvent ? utils_domObjects.PointerEvent === window.MSPointerEvent ? {
    up: 'MSPointerUp',
    down: 'MSPointerDown',
    over: 'mouseover',
    out: 'mouseout',
    move: 'MSPointerMove',
    cancel: 'MSPointerCancel'
  } : {
    up: 'pointerup',
    down: 'pointerdown',
    over: 'pointerover',
    out: 'pointerout',
    move: 'pointermove',
    cancel: 'pointercancel'
  } : null; // because Webkit and Opera still use 'mousewheel' event type

  browser.wheelEvent = 'onmousewheel' in utils_domObjects.document ? 'mousewheel' : 'wheel';
}

/* harmony default export */ var utils_browser = (browser);

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/arr.js
const contains = (array, target) => array.indexOf(target) !== -1;
const merge = (target, source) => {
  for (const item of source) {
    target.push(item);
  }

  return target;
};
const from = source => merge([], source);
const findIndex = (array, func) => {
  for (let i = 0; i < array.length; i++) {
    if (func(array[i], i, array)) {
      return i;
    }
  }

  return -1;
};
const find = (array, func) => array[findIndex(array, func)];

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/clone.js

 // tslint:disable-next-line ban-types

function clone(source) {
  const dest = {};

  for (const prop in source) {
    const value = source[prop];

    if (is.plainObject(value)) {
      dest[prop] = clone(value);
    } else if (is.array(value)) {
      dest[prop] = from(value);
    } else {
      dest[prop] = value;
    }
  }

  return dest;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/extend.js
function extend(dest, source) {
  for (const prop in source) {
    dest[prop] = source[prop];
  }

  const ret = dest;
  return ret;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/raf.js
let lastTime = 0;
let request;
let cancel;

function raf_init(window) {
  request = window.requestAnimationFrame;
  cancel = window.cancelAnimationFrame;

  if (!request) {
    const vendors = ['ms', 'moz', 'webkit', 'o'];

    for (const vendor of vendors) {
      request = window[`${vendor}RequestAnimationFrame`];
      cancel = window[`${vendor}CancelAnimationFrame`] || window[`${vendor}CancelRequestAnimationFrame`];
    }
  }

  request = request && request.bind(window);
  cancel = cancel && cancel.bind(window);

  if (!request) {
    request = callback => {
      const currTime = Date.now();
      const timeToCall = Math.max(0, 16 - (currTime - lastTime)); // eslint-disable-next-line node/no-callback-literal

      const token = window.setTimeout(() => {
        callback(currTime + timeToCall);
      }, timeToCall);
      lastTime = currTime + timeToCall;
      return token;
    };

    cancel = token => clearTimeout(token);
  }
}

/* harmony default export */ var raf = ({
  request: callback => request(callback),
  cancel: token => cancel(token),
  init: raf_init
});

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/normalizeListeners.js


function normalize(type, listeners, result) {
  result = result || {};

  if (is.string(type) && type.search(' ') !== -1) {
    type = split(type);
  }

  if (is.array(type)) {
    return type.reduce((acc, t) => extend(acc, normalize(t, listeners, result)), result);
  } // ({ type: fn }) -> ('', { type: fn })


  if (is.object(type)) {
    listeners = type;
    type = '';
  }

  if (is.func(listeners)) {
    result[type] = result[type] || [];
    result[type].push(listeners);
  } else if (is.array(listeners)) {
    for (const l of listeners) {
      normalize(type, l, result);
    }
  } else if (is.object(listeners)) {
    for (const prefix in listeners) {
      const combinedTypes = split(prefix).map(p => `${type}${p}`);
      normalize(combinedTypes, listeners[prefix], result);
    }
  }

  return result;
}

function split(type) {
  return type.trim().split(/ +/);
}

// CONCATENATED MODULE: ./node_modules/@interactjs/core/Eventable.js




function fireUntilImmediateStopped(event, listeners) {
  for (const listener of listeners) {
    if (event.immediatePropagationStopped) {
      break;
    }

    listener(event);
  }
}

class Eventable_Eventable {
  constructor(options) {
    this.options = void 0;
    this.types = {};
    this.propagationStopped = false;
    this.immediatePropagationStopped = false;
    this.global = void 0;
    this.options = extend({}, options || {});
  }

  fire(event) {
    let listeners;
    const global = this.global; // Interactable#on() listeners
    // tslint:disable no-conditional-assignment

    if (listeners = this.types[event.type]) {
      fireUntilImmediateStopped(event, listeners);
    } // interact.on() listeners


    if (!event.propagationStopped && global && (listeners = global[event.type])) {
      fireUntilImmediateStopped(event, listeners);
    }
  }

  on(type, listener) {
    const listeners = normalize(type, listener);

    for (type in listeners) {
      this.types[type] = merge(this.types[type] || [], listeners[type]);
    }
  }

  off(type, listener) {
    const listeners = normalize(type, listener);

    for (type in listeners) {
      const eventList = this.types[type];

      if (!eventList || !eventList.length) {
        continue;
      }

      for (const subListener of listeners[type]) {
        const index = eventList.indexOf(subListener);

        if (index !== -1) {
          eventList.splice(index, 1);
        }
      }
    }
  }

  getRect(_element) {
    return null;
  }

}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/domUtils.js




function nodeContains(parent, child) {
  if (parent.contains) {
    return parent.contains(child);
  }

  while (child) {
    if (child === parent) {
      return true;
    }

    child = child.parentNode;
  }

  return false;
}
function domUtils_closest(element, selector) {
  while (is.element(element)) {
    if (matchesSelector(element, selector)) {
      return element;
    }

    element = parentNode(element);
  }

  return null;
}
function parentNode(node) {
  let parent = node.parentNode;

  if (is.docFrag(parent)) {
    // skip past #shado-root fragments
    // tslint:disable-next-line
    while ((parent = parent.host) && is.docFrag(parent)) {
      continue;
    }

    return parent;
  }

  return parent;
}
function matchesSelector(element, selector) {
  // remove /deep/ from selectors if shadowDOM polyfill is used
  if (win !== realWindow) {
    selector = selector.replace(/\/deep\//g, ' ');
  }

  return element[utils_browser.prefixedMatchesSelector](selector);
}

function matchesUpTo(element, selector, limit) {
  while (is.element(element)) {
    if (matchesSelector(element, selector)) {
      return true;
    }

    element = parentNode(element);

    if (element === limit) {
      return matchesSelector(element, selector);
    }
  }

  return false;
}
function getActualElement(element) {
  return element.correspondingUseElement || element;
}
function getScrollXY(relevantWindow) {
  relevantWindow = relevantWindow || win;
  return {
    x: relevantWindow.scrollX || relevantWindow.document.documentElement.scrollLeft,
    y: relevantWindow.scrollY || relevantWindow.document.documentElement.scrollTop
  };
}
function getElementClientRect(element) {
  const clientRect = element instanceof utils_domObjects.SVGElement ? element.getBoundingClientRect() : element.getClientRects()[0];
  return clientRect && {
    left: clientRect.left,
    right: clientRect.right,
    top: clientRect.top,
    bottom: clientRect.bottom,
    width: clientRect.width || clientRect.right - clientRect.left,
    height: clientRect.height || clientRect.bottom - clientRect.top
  };
}
function getElementRect(element) {
  const clientRect = getElementClientRect(element);

  if (!utils_browser.isIOS7 && clientRect) {
    const scroll = getScrollXY(getWindow(element));
    clientRect.left += scroll.x;
    clientRect.right += scroll.x;
    clientRect.top += scroll.y;
    clientRect.bottom += scroll.y;
  }

  return clientRect;
}
function trySelector(value) {
  if (!is.string(value)) {
    return false;
  } // an exception will be raised if it is invalid


  utils_domObjects.document.querySelector(value);
  return true;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/rect.js



function getStringOptionResult(value, target, element) {
  if (value === 'parent') {
    return parentNode(element);
  }

  if (value === 'self') {
    return target.getRect(element);
  }

  return domUtils_closest(element, value);
}
function resolveRectLike(value, target, element, functionArgs) {
  let returnValue = value;

  if (is.string(returnValue)) {
    returnValue = getStringOptionResult(returnValue, target, element);
  } else if (is.func(returnValue)) {
    returnValue = returnValue(...functionArgs);
  }

  if (is.element(returnValue)) {
    returnValue = getElementRect(returnValue);
  }

  return returnValue;
}
function rectToXY(rect) {
  return rect && {
    x: 'x' in rect ? rect.x : rect.left,
    y: 'y' in rect ? rect.y : rect.top
  };
}
function xywhToTlbr(rect) {
  if (rect && !('left' in rect && 'top' in rect)) {
    rect = extend({}, rect);
    rect.left = rect.x || 0;
    rect.top = rect.y || 0;
    rect.right = rect.right || rect.left + rect.width;
    rect.bottom = rect.bottom || rect.top + rect.height;
  }

  return rect;
}
function tlbrToXywh(rect) {
  if (rect && !('x' in rect && 'y' in rect)) {
    rect = extend({}, rect);
    rect.x = rect.left || 0;
    rect.y = rect.top || 0;
    rect.width = rect.width || (rect.right || 0) - rect.x;
    rect.height = rect.height || (rect.bottom || 0) - rect.y;
  }

  return rect;
}
function addEdges(edges, rect, delta) {
  if (edges.left) {
    rect.left += delta.x;
  }

  if (edges.right) {
    rect.right += delta.x;
  }

  if (edges.top) {
    rect.top += delta.y;
  }

  if (edges.bottom) {
    rect.bottom += delta.y;
  }

  rect.width = rect.right - rect.left;
  rect.height = rect.bottom - rect.top;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/getOriginXY.js

/* harmony default export */ var getOriginXY = (function (target, element, actionName) {
  const actionOptions = target.options[actionName];
  const actionOrigin = actionOptions && actionOptions.origin;
  const origin = actionOrigin || target.options.origin;
  const originRect = resolveRectLike(origin, target, element, [target && element]);
  return rectToXY(originRect) || {
    x: 0,
    y: 0
  };
});

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/hypot.js
/* harmony default export */ var hypot = ((x, y) => Math.sqrt(x * x + y * y));

// CONCATENATED MODULE: ./node_modules/@interactjs/core/BaseEvent.js
class BaseEvent {
  constructor(interaction) {
    this.type = void 0;
    this.target = void 0;
    this.currentTarget = void 0;
    this.interactable = void 0;
    this._interaction = void 0;
    this.timeStamp = void 0;
    this.immediatePropagationStopped = false;
    this.propagationStopped = false;
    this._interaction = interaction;
  }

  preventDefault() {}
  /**
   * Don't call any other listeners (even on the current target)
   */


  stopPropagation() {
    this.propagationStopped = true;
  }
  /**
   * Don't call listeners on the remaining targets
   */


  stopImmediatePropagation() {
    this.immediatePropagationStopped = this.propagationStopped = true;
  }

} // defined outside of class definition to avoid assignment of undefined during
// construction

// getters and setters defined here to support typescript 3.6 and below which
// don't support getter and setters in .d.ts files
Object.defineProperty(BaseEvent.prototype, 'interaction', {
  get() {
    return this._interaction._proxy;
  },

  set() {}

});

// CONCATENATED MODULE: ./node_modules/@interactjs/core/defaultOptions.js
// eslint-disable-next-line @typescript-eslint/no-empty-interface
// export interface Options extends BaseDefaults, PerActionDefaults {}
const defaultOptions_defaults = {
  base: {
    preventDefault: 'auto',
    deltaSource: 'page'
  },
  perAction: {
    enabled: false,
    origin: {
      x: 0,
      y: 0
    }
  },
  actions: {}
};

// CONCATENATED MODULE: ./node_modules/@interactjs/core/InteractEvent.js





class InteractEvent_InteractEvent extends BaseEvent {
  // resize

  /** */
  constructor(interaction, event, actionName, phase, element, preEnd, type) {
    super(interaction);
    this.target = void 0;
    this.currentTarget = void 0;
    this.relatedTarget = null;
    this.screenX = void 0;
    this.screenY = void 0;
    this.button = void 0;
    this.buttons = void 0;
    this.ctrlKey = void 0;
    this.shiftKey = void 0;
    this.altKey = void 0;
    this.metaKey = void 0;
    this.page = void 0;
    this.client = void 0;
    this.delta = void 0;
    this.rect = void 0;
    this.x0 = void 0;
    this.y0 = void 0;
    this.t0 = void 0;
    this.dt = void 0;
    this.duration = void 0;
    this.clientX0 = void 0;
    this.clientY0 = void 0;
    this.velocity = void 0;
    this.speed = void 0;
    this.swipe = void 0;
    this.timeStamp = void 0;
    this.axes = void 0;
    this.preEnd = void 0;
    element = element || interaction.element;
    const target = interaction.interactable;
    const deltaSource = (target && target.options || defaultOptions_defaults).deltaSource;
    const origin = getOriginXY(target, element, actionName);
    const starting = phase === 'start';
    const ending = phase === 'end';
    const prevEvent = starting ? this : interaction.prevEvent;
    const coords = starting ? interaction.coords.start : ending ? {
      page: prevEvent.page,
      client: prevEvent.client,
      timeStamp: interaction.coords.cur.timeStamp
    } : interaction.coords.cur;
    this.page = extend({}, coords.page);
    this.client = extend({}, coords.client);
    this.rect = extend({}, interaction.rect);
    this.timeStamp = coords.timeStamp;

    if (!ending) {
      this.page.x -= origin.x;
      this.page.y -= origin.y;
      this.client.x -= origin.x;
      this.client.y -= origin.y;
    }

    this.ctrlKey = event.ctrlKey;
    this.altKey = event.altKey;
    this.shiftKey = event.shiftKey;
    this.metaKey = event.metaKey;
    this.button = event.button;
    this.buttons = event.buttons;
    this.target = element;
    this.currentTarget = element;
    this.preEnd = preEnd;
    this.type = type || actionName + (phase || '');
    this.interactable = target;
    this.t0 = starting ? interaction.pointers[interaction.pointers.length - 1].downTime : prevEvent.t0;
    this.x0 = interaction.coords.start.page.x - origin.x;
    this.y0 = interaction.coords.start.page.y - origin.y;
    this.clientX0 = interaction.coords.start.client.x - origin.x;
    this.clientY0 = interaction.coords.start.client.y - origin.y;

    if (starting || ending) {
      this.delta = {
        x: 0,
        y: 0
      };
    } else {
      this.delta = {
        x: this[deltaSource].x - prevEvent[deltaSource].x,
        y: this[deltaSource].y - prevEvent[deltaSource].y
      };
    }

    this.dt = interaction.coords.delta.timeStamp;
    this.duration = this.timeStamp - this.t0; // velocity and speed in pixels per second

    this.velocity = extend({}, interaction.coords.velocity[deltaSource]);
    this.speed = hypot(this.velocity.x, this.velocity.y);
    this.swipe = ending || phase === 'inertiastart' ? this.getSwipe() : null;
  }

  getSwipe() {
    const interaction = this._interaction;

    if (interaction.prevEvent.speed < 600 || this.timeStamp - interaction.prevEvent.timeStamp > 150) {
      return null;
    }

    let angle = 180 * Math.atan2(interaction.prevEvent.velocityY, interaction.prevEvent.velocityX) / Math.PI;
    const overlap = 22.5;

    if (angle < 0) {
      angle += 360;
    }

    const left = 135 - overlap <= angle && angle < 225 + overlap;
    const up = 225 - overlap <= angle && angle < 315 + overlap;
    const right = !left && (315 - overlap <= angle || angle < 45 + overlap);
    const down = !up && 45 - overlap <= angle && angle < 135 + overlap;
    return {
      up,
      down,
      left,
      right,
      angle,
      speed: interaction.prevEvent.speed,
      velocity: {
        x: interaction.prevEvent.velocityX,
        y: interaction.prevEvent.velocityY
      }
    };
  }

  preventDefault() {}
  /**
   * Don't call listeners on the remaining targets
   */


  stopImmediatePropagation() {
    this.immediatePropagationStopped = this.propagationStopped = true;
  }
  /**
   * Don't call any other listeners (even on the current target)
   */


  stopPropagation() {
    this.propagationStopped = true;
  }

} // getters and setters defined here to support typescript 3.6 and below which
// don't support getter and setters in .d.ts files

Object.defineProperties(InteractEvent_InteractEvent.prototype, {
  pageX: {
    get() {
      return this.page.x;
    },

    set(value) {
      this.page.x = value;
    }

  },
  pageY: {
    get() {
      return this.page.y;
    },

    set(value) {
      this.page.y = value;
    }

  },
  clientX: {
    get() {
      return this.client.x;
    },

    set(value) {
      this.client.x = value;
    }

  },
  clientY: {
    get() {
      return this.client.y;
    },

    set(value) {
      this.client.y = value;
    }

  },
  dx: {
    get() {
      return this.delta.x;
    },

    set(value) {
      this.delta.x = value;
    }

  },
  dy: {
    get() {
      return this.delta.y;
    },

    set(value) {
      this.delta.y = value;
    }

  },
  velocityX: {
    get() {
      return this.velocity.x;
    },

    set(value) {
      this.velocity.x = value;
    }

  },
  velocityY: {
    get() {
      return this.velocity.y;
    },

    set(value) {
      this.velocity.y = value;
    }

  }
});

// CONCATENATED MODULE: ./node_modules/@interactjs/core/isNonNativeEvent.js
function isNonNativeEvent(type, actions) {
  if (actions.phaselessTypes[type]) {
    return true;
  }

  for (const name in actions.map) {
    if (type.indexOf(name) === 0 && type.substr(name.length) in actions.phases) {
      return true;
    }
  }

  return false;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/core/Interactable.js
/* eslint-disable no-dupe-class-members */











/** */
class Interactable_Interactable {
  /** @internal */
  get _defaults() {
    return {
      base: {},
      perAction: {},
      actions: {}
    };
  }

  /** */
  constructor(target, options, defaultContext, scopeEvents) {
    this.options = void 0;
    this._actions = void 0;
    this.target = void 0;
    this.events = new Eventable_Eventable();
    this._context = void 0;
    this._win = void 0;
    this._doc = void 0;
    this._scopeEvents = void 0;
    this._rectChecker = void 0;
    this._actions = options.actions;
    this.target = target;
    this._context = options.context || defaultContext;
    this._win = getWindow(trySelector(target) ? this._context : target);
    this._doc = this._win.document;
    this._scopeEvents = scopeEvents;
    this.set(options);
  }

  setOnEvents(actionName, phases) {
    if (is.func(phases.onstart)) {
      this.on(`${actionName}start`, phases.onstart);
    }

    if (is.func(phases.onmove)) {
      this.on(`${actionName}move`, phases.onmove);
    }

    if (is.func(phases.onend)) {
      this.on(`${actionName}end`, phases.onend);
    }

    if (is.func(phases.oninertiastart)) {
      this.on(`${actionName}inertiastart`, phases.oninertiastart);
    }

    return this;
  }

  updatePerActionListeners(actionName, prev, cur) {
    if (is.array(prev) || is.object(prev)) {
      this.off(actionName, prev);
    }

    if (is.array(cur) || is.object(cur)) {
      this.on(actionName, cur);
    }
  }

  setPerAction(actionName, options) {
    const defaults = this._defaults; // for all the default per-action options

    for (const optionName_ in options) {
      const optionName = optionName_;
      const actionOptions = this.options[actionName];
      const optionValue = options[optionName]; // remove old event listeners and add new ones

      if (optionName === 'listeners') {
        this.updatePerActionListeners(actionName, actionOptions.listeners, optionValue);
      } // if the option value is an array


      if (is.array(optionValue)) {
        actionOptions[optionName] = from(optionValue);
      } // if the option value is an object
      else if (is.plainObject(optionValue)) {
          // copy the object
          actionOptions[optionName] = extend(actionOptions[optionName] || {}, clone(optionValue)); // set anabled field to true if it exists in the defaults

          if (is.object(defaults.perAction[optionName]) && 'enabled' in defaults.perAction[optionName]) {
            actionOptions[optionName].enabled = optionValue.enabled !== false;
          }
        } // if the option value is a boolean and the default is an object
        else if (is.bool(optionValue) && is.object(defaults.perAction[optionName])) {
            actionOptions[optionName].enabled = optionValue;
          } // if it's anything else, do a plain assignment
          else {
              actionOptions[optionName] = optionValue;
            }
    }
  }
  /**
   * The default function to get an Interactables bounding rect. Can be
   * overridden using {@link Interactable.rectChecker}.
   *
   * @param {Element} [element] The element to measure.
   * @return {Rect} The object's bounding rectangle.
   */


  getRect(element) {
    element = element || (is.element(this.target) ? this.target : null);

    if (is.string(this.target)) {
      element = element || this._context.querySelector(this.target);
    }

    return getElementRect(element);
  }
  /**
   * Returns or sets the function used to calculate the interactable's
   * element's rectangle
   *
   * @param {function} [checker] A function which returns this Interactable's
   * bounding rectangle. See {@link Interactable.getRect}
   * @return {function | object} The checker function or this Interactable
   */


  rectChecker(checker) {
    if (is.func(checker)) {
      this._rectChecker = checker;

      this.getRect = element => {
        const rect = extend({}, this._rectChecker(element));

        if (!('width' in rect)) {
          rect.width = rect.right - rect.left;
          rect.height = rect.bottom - rect.top;
        }

        return rect;
      };

      return this;
    }

    if (checker === null) {
      delete this.getRect;
      delete this._rectChecker;
      return this;
    }

    return this.getRect;
  }

  _backCompatOption(optionName, newValue) {
    if (trySelector(newValue) || is.object(newValue)) {
      this.options[optionName] = newValue;

      for (const action in this._actions.map) {
        this.options[action][optionName] = newValue;
      }

      return this;
    }

    return this.options[optionName];
  }
  /**
   * Gets or sets the origin of the Interactable's element.  The x and y
   * of the origin will be subtracted from action event coordinates.
   *
   * @param {Element | object | string} [origin] An HTML or SVG Element whose
   * rect will be used, an object eg. { x: 0, y: 0 } or string 'parent', 'self'
   * or any CSS selector
   *
   * @return {object} The current origin or this Interactable
   */


  origin(newValue) {
    return this._backCompatOption('origin', newValue);
  }
  /**
   * Returns or sets the mouse coordinate types used to calculate the
   * movement of the pointer.
   *
   * @param {string} [newValue] Use 'client' if you will be scrolling while
   * interacting; Use 'page' if you want autoScroll to work
   * @return {string | object} The current deltaSource or this Interactable
   */


  deltaSource(newValue) {
    if (newValue === 'page' || newValue === 'client') {
      this.options.deltaSource = newValue;
      return this;
    }

    return this.options.deltaSource;
  }
  /**
   * Gets the selector context Node of the Interactable. The default is
   * `window.document`.
   *
   * @return {Node} The context Node of this Interactable
   */


  context() {
    return this._context;
  }

  inContext(element) {
    return this._context === element.ownerDocument || nodeContains(this._context, element);
  }

  testIgnoreAllow(options, targetNode, eventTarget) {
    return !this.testIgnore(options.ignoreFrom, targetNode, eventTarget) && this.testAllow(options.allowFrom, targetNode, eventTarget);
  }

  testAllow(allowFrom, targetNode, element) {
    if (!allowFrom) {
      return true;
    }

    if (!is.element(element)) {
      return false;
    }

    if (is.string(allowFrom)) {
      return matchesUpTo(element, allowFrom, targetNode);
    } else if (is.element(allowFrom)) {
      return nodeContains(allowFrom, element);
    }

    return false;
  }

  testIgnore(ignoreFrom, targetNode, element) {
    if (!ignoreFrom || !is.element(element)) {
      return false;
    }

    if (is.string(ignoreFrom)) {
      return matchesUpTo(element, ignoreFrom, targetNode);
    } else if (is.element(ignoreFrom)) {
      return nodeContains(ignoreFrom, element);
    }

    return false;
  }
  /**
   * Calls listeners for the given InteractEvent type bound globally
   * and directly to this Interactable
   *
   * @param {InteractEvent} iEvent The InteractEvent object to be fired on this
   * Interactable
   * @return {Interactable} this Interactable
   */


  fire(iEvent) {
    this.events.fire(iEvent);
    return this;
  }

  _onOff(method, typeArg, listenerArg, options) {
    if (is.object(typeArg) && !is.array(typeArg)) {
      options = listenerArg;
      listenerArg = null;
    }

    const addRemove = method === 'on' ? 'add' : 'remove';
    const listeners = normalize(typeArg, listenerArg);

    for (let type in listeners) {
      if (type === 'wheel') {
        type = utils_browser.wheelEvent;
      }

      for (const listener of listeners[type]) {
        // if it is an action event type
        if (isNonNativeEvent(type, this._actions)) {
          this.events[method](type, listener);
        } // delegated event
        else if (is.string(this.target)) {
            this._scopeEvents[`${addRemove}Delegate`](this.target, this._context, type, listener, options);
          } // remove listener from this Interactable's element
          else {
              this._scopeEvents[addRemove](this.target, type, listener, options);
            }
      }
    }

    return this;
  }
  /**
   * Binds a listener for an InteractEvent, pointerEvent or DOM event.
   *
   * @param {string | array | object} types The types of events to listen
   * for
   * @param {function | array | object} [listener] The event listener function(s)
   * @param {object | boolean} [options] options object or useCapture flag for
   * addEventListener
   * @return {Interactable} This Interactable
   */


  on(types, listener, options) {
    return this._onOff('on', types, listener, options);
  }
  /**
   * Removes an InteractEvent, pointerEvent or DOM event listener.
   *
   * @param {string | array | object} types The types of events that were
   * listened for
   * @param {function | array | object} [listener] The event listener function(s)
   * @param {object | boolean} [options] options object or useCapture flag for
   * removeEventListener
   * @return {Interactable} This Interactable
   */


  off(types, listener, options) {
    return this._onOff('off', types, listener, options);
  }
  /**
   * Reset the options of this Interactable
   *
   * @param {object} options The new settings to apply
   * @return {object} This Interactable
   */


  set(options) {
    const defaults = this._defaults;

    if (!is.object(options)) {
      options = {};
    }

    this.options = clone(defaults.base);

    for (const actionName_ in this._actions.methodDict) {
      const actionName = actionName_;
      const methodName = this._actions.methodDict[actionName];
      this.options[actionName] = {};
      this.setPerAction(actionName, extend(extend({}, defaults.perAction), defaults.actions[actionName]));
      this[methodName](options[actionName]);
    }

    for (const setting in options) {
      if (is.func(this[setting])) {
        this[setting](options[setting]);
      }
    }

    return this;
  }
  /**
   * Remove this interactable from the list of interactables and remove it's
   * action capabilities and event listeners
   */


  unset() {
    if (is.string(this.target)) {
      // remove delegated events
      for (const type in this._scopeEvents.delegatedEvents) {
        const delegated = this._scopeEvents.delegatedEvents[type];

        for (let i = delegated.length - 1; i >= 0; i--) {
          const {
            selector,
            context,
            listeners
          } = delegated[i];

          if (selector === this.target && context === this._context) {
            delegated.splice(i, 1);
          }

          for (let l = listeners.length - 1; l >= 0; l--) {
            this._scopeEvents.removeDelegate(this.target, this._context, type, listeners[l][0], listeners[l][1]);
          }
        }
      }
    } else {
      this._scopeEvents.remove(this.target, 'all');
    }
  }

}

// CONCATENATED MODULE: ./node_modules/@interactjs/core/InteractableSet.js




class InteractableSet_InteractableSet {
  // all set interactables
  constructor(scope) {
    this.list = [];
    this.selectorMap = {};
    this.scope = void 0;
    this.scope = scope;
    scope.addListeners({
      'interactable:unset': ({
        interactable
      }) => {
        const {
          target,
          _context: context
        } = interactable;
        const targetMappings = is.string(target) ? this.selectorMap[target] : target[this.scope.id];
        const targetIndex = findIndex(targetMappings, m => m.context === context);

        if (targetMappings[targetIndex]) {
          // Destroying mappingInfo's context and interactable
          targetMappings[targetIndex].context = null;
          targetMappings[targetIndex].interactable = null;
        }

        targetMappings.splice(targetIndex, 1);
      }
    });
  }

  new(target, options) {
    options = extend(options || {}, {
      actions: this.scope.actions
    });
    const interactable = new this.scope.Interactable(target, options, this.scope.document, this.scope.events);
    const mappingInfo = {
      context: interactable._context,
      interactable
    };
    this.scope.addDocument(interactable._doc);
    this.list.push(interactable);

    if (is.string(target)) {
      if (!this.selectorMap[target]) {
        this.selectorMap[target] = [];
      }

      this.selectorMap[target].push(mappingInfo);
    } else {
      if (!interactable.target[this.scope.id]) {
        Object.defineProperty(target, this.scope.id, {
          value: [],
          configurable: true
        });
      }

      target[this.scope.id].push(mappingInfo);
    }

    this.scope.fire('interactable:new', {
      target,
      options,
      interactable,
      win: this.scope._win
    });
    return interactable;
  }

  get(target, options) {
    const context = options && options.context || this.scope.document;
    const isSelector = is.string(target);
    const targetMappings = isSelector ? this.selectorMap[target] : target[this.scope.id];

    if (!targetMappings) {
      return null;
    }

    const found = find(targetMappings, m => m.context === context && (isSelector || m.interactable.inContext(target)));
    return found && found.interactable;
  }

  forEachMatch(node, callback) {
    for (const interactable of this.list) {
      let ret;

      if ((is.string(interactable.target) // target is a selector and the element matches
      ? is.element(node) && matchesSelector(node, interactable.target) : // target is the element
      node === interactable.target) && // the element is in context
      interactable.inContext(node)) {
        ret = callback(interactable);
      }

      if (ret !== undefined) {
        return ret;
      }
    }
  }

}

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/pointerExtend.js
function pointerExtend(dest, source) {
  for (const prop in source) {
    const prefixedPropREs = pointerExtend.prefixedPropREs;
    let deprecated = false; // skip deprecated prefixed properties

    for (const vendor in prefixedPropREs) {
      if (prop.indexOf(vendor) === 0 && prefixedPropREs[vendor].test(prop)) {
        deprecated = true;
        break;
      }
    }

    if (!deprecated && typeof source[prop] !== 'function') {
      dest[prop] = source[prop];
    }
  }

  return dest;
}

pointerExtend.prefixedPropREs = {
  webkit: /(Movement[XY]|Radius[XY]|RotationAngle|Force)$/,
  moz: /(Pressure)$/
};
/* harmony default export */ var utils_pointerExtend = (pointerExtend);

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/pointerUtils.js






function copyCoords(dest, src) {
  dest.page = dest.page || {};
  dest.page.x = src.page.x;
  dest.page.y = src.page.y;
  dest.client = dest.client || {};
  dest.client.x = src.client.x;
  dest.client.y = src.client.y;
  dest.timeStamp = src.timeStamp;
}
function setCoordDeltas(targetObj, prev, cur) {
  targetObj.page.x = cur.page.x - prev.page.x;
  targetObj.page.y = cur.page.y - prev.page.y;
  targetObj.client.x = cur.client.x - prev.client.x;
  targetObj.client.y = cur.client.y - prev.client.y;
  targetObj.timeStamp = cur.timeStamp - prev.timeStamp;
}
function setCoordVelocity(targetObj, delta) {
  const dt = Math.max(delta.timeStamp / 1000, 0.001);
  targetObj.page.x = delta.page.x / dt;
  targetObj.page.y = delta.page.y / dt;
  targetObj.client.x = delta.client.x / dt;
  targetObj.client.y = delta.client.y / dt;
  targetObj.timeStamp = dt;
}
function setZeroCoords(targetObj) {
  targetObj.page.x = 0;
  targetObj.page.y = 0;
  targetObj.client.x = 0;
  targetObj.client.y = 0;
}
function isNativePointer(pointer) {
  return pointer instanceof utils_domObjects.Event || pointer instanceof utils_domObjects.Touch;
} // Get specified X/Y coords for mouse or event.touches[0]

function getXY(type, pointer, xy) {
  xy = xy || {};
  type = type || 'page';
  xy.x = pointer[type + 'X'];
  xy.y = pointer[type + 'Y'];
  return xy;
}
function getPageXY(pointer, page) {
  page = page || {
    x: 0,
    y: 0
  }; // Opera Mobile handles the viewport and scrolling oddly

  if (utils_browser.isOperaMobile && isNativePointer(pointer)) {
    getXY('screen', pointer, page);
    page.x += window.scrollX;
    page.y += window.scrollY;
  } else {
    getXY('page', pointer, page);
  }

  return page;
}
function getClientXY(pointer, client) {
  client = client || {};

  if (utils_browser.isOperaMobile && isNativePointer(pointer)) {
    // Opera Mobile handles the viewport and scrolling oddly
    getXY('screen', pointer, client);
  } else {
    getXY('client', pointer, client);
  }

  return client;
}
function getPointerId(pointer) {
  return is.number(pointer.pointerId) ? pointer.pointerId : pointer.identifier;
}
function setCoords(dest, pointers, timeStamp) {
  const pointer = pointers.length > 1 ? pointerAverage(pointers) : pointers[0];
  getPageXY(pointer, dest.page);
  getClientXY(pointer, dest.client);
  dest.timeStamp = timeStamp;
}
function getTouchPair(event) {
  const touches = []; // array of touches is supplied

  if (is.array(event)) {
    touches[0] = event[0];
    touches[1] = event[1];
  } // an event
  else {
      if (event.type === 'touchend') {
        if (event.touches.length === 1) {
          touches[0] = event.touches[0];
          touches[1] = event.changedTouches[0];
        } else if (event.touches.length === 0) {
          touches[0] = event.changedTouches[0];
          touches[1] = event.changedTouches[1];
        }
      } else {
        touches[0] = event.touches[0];
        touches[1] = event.touches[1];
      }
    }

  return touches;
}
function pointerAverage(pointers) {
  const average = {
    pageX: 0,
    pageY: 0,
    clientX: 0,
    clientY: 0,
    screenX: 0,
    screenY: 0
  };

  for (const pointer of pointers) {
    for (const prop in average) {
      average[prop] += pointer[prop];
    }
  }

  for (const prop in average) {
    average[prop] /= pointers.length;
  }

  return average;
}
function touchBBox(event) {
  if (!event.length) {
    return null;
  }

  const touches = getTouchPair(event);
  const minX = Math.min(touches[0].pageX, touches[1].pageX);
  const minY = Math.min(touches[0].pageY, touches[1].pageY);
  const maxX = Math.max(touches[0].pageX, touches[1].pageX);
  const maxY = Math.max(touches[0].pageY, touches[1].pageY);
  return {
    x: minX,
    y: minY,
    left: minX,
    top: minY,
    right: maxX,
    bottom: maxY,
    width: maxX - minX,
    height: maxY - minY
  };
}
function touchDistance(event, deltaSource) {
  const sourceX = deltaSource + 'X';
  const sourceY = deltaSource + 'Y';
  const touches = getTouchPair(event);
  const dx = touches[0][sourceX] - touches[1][sourceX];
  const dy = touches[0][sourceY] - touches[1][sourceY];
  return hypot(dx, dy);
}
function touchAngle(event, deltaSource) {
  const sourceX = deltaSource + 'X';
  const sourceY = deltaSource + 'Y';
  const touches = getTouchPair(event);
  const dx = touches[1][sourceX] - touches[0][sourceX];
  const dy = touches[1][sourceY] - touches[0][sourceY];
  const angle = 180 * Math.atan2(dy, dx) / Math.PI;
  return angle;
}
function getPointerType(pointer) {
  return is.string(pointer.pointerType) ? pointer.pointerType : is.number(pointer.pointerType) ? [undefined, undefined, 'touch', 'pen', 'mouse'][pointer.pointerType] // if the PointerEvent API isn't available, then the "pointer" must
  // be either a MouseEvent, TouchEvent, or Touch object
  : /touch/.test(pointer.type) || pointer instanceof utils_domObjects.Touch ? 'touch' : 'mouse';
} // [ event.target, event.currentTarget ]

function getEventTargets(event) {
  const path = is.func(event.composedPath) ? event.composedPath() : event.path;
  return [getActualElement(path ? path[0] : event.target), getActualElement(event.currentTarget)];
}
function newCoords() {
  return {
    page: {
      x: 0,
      y: 0
    },
    client: {
      x: 0,
      y: 0
    },
    timeStamp: 0
  };
}


// CONCATENATED MODULE: ./node_modules/@interactjs/core/events.js








function install(scope) {
  const targets = [];
  const delegatedEvents = {};
  const documents = [];
  const eventsMethods = {
    add,
    remove,
    addDelegate,
    removeDelegate,
    delegateListener,
    delegateUseCapture,
    delegatedEvents,
    documents,
    targets,
    supportsOptions: false,
    supportsPassive: false
  }; // check if browser supports passive events and options arg

  scope.document.createElement('div').addEventListener('test', null, {
    get capture() {
      return eventsMethods.supportsOptions = true;
    },

    get passive() {
      return eventsMethods.supportsPassive = true;
    }

  });
  scope.events = eventsMethods;

  function add(eventTarget, type, listener, optionalArg) {
    const options = getOptions(optionalArg);
    let target = find(targets, t => t.eventTarget === eventTarget);

    if (!target) {
      target = {
        eventTarget,
        events: {}
      };
      targets.push(target);
    }

    if (!target.events[type]) {
      target.events[type] = [];
    }

    if (eventTarget.addEventListener && !contains(target.events[type], listener)) {
      eventTarget.addEventListener(type, listener, eventsMethods.supportsOptions ? options : options.capture);
      target.events[type].push(listener);
    }
  }

  function remove(eventTarget, type, listener, optionalArg) {
    const options = getOptions(optionalArg);
    const targetIndex = findIndex(targets, t => t.eventTarget === eventTarget);
    const target = targets[targetIndex];

    if (!target || !target.events) {
      return;
    }

    if (type === 'all') {
      for (type in target.events) {
        if (target.events.hasOwnProperty(type)) {
          remove(eventTarget, type, 'all');
        }
      }

      return;
    }

    let typeIsEmpty = false;
    const typeListeners = target.events[type];

    if (typeListeners) {
      if (listener === 'all') {
        for (let i = typeListeners.length - 1; i >= 0; i--) {
          remove(eventTarget, type, typeListeners[i], options);
        }

        return;
      } else {
        for (let i = 0; i < typeListeners.length; i++) {
          if (typeListeners[i] === listener) {
            eventTarget.removeEventListener(type, listener, eventsMethods.supportsOptions ? options : options.capture);
            typeListeners.splice(i, 1);

            if (typeListeners.length === 0) {
              delete target.events[type];
              typeIsEmpty = true;
            }

            break;
          }
        }
      }
    }

    if (typeIsEmpty && !Object.keys(target.events).length) {
      targets.splice(targetIndex, 1);
    }
  }

  function addDelegate(selector, context, type, listener, optionalArg) {
    const options = getOptions(optionalArg);

    if (!delegatedEvents[type]) {
      delegatedEvents[type] = []; // add delegate listener functions

      for (const doc of documents) {
        add(doc, type, delegateListener);
        add(doc, type, delegateUseCapture, true);
      }
    }

    const delegates = delegatedEvents[type];
    let delegate = find(delegates, d => d.selector === selector && d.context === context);

    if (!delegate) {
      delegate = {
        selector,
        context,
        listeners: []
      };
      delegates.push(delegate);
    }

    delegate.listeners.push([listener, options]);
  }

  function removeDelegate(selector, context, type, listener, optionalArg) {
    const options = getOptions(optionalArg);
    const delegates = delegatedEvents[type];
    let matchFound = false;
    let index;

    if (!delegates) {
      return;
    } // count from last index of delegated to 0


    for (index = delegates.length - 1; index >= 0; index--) {
      const cur = delegates[index]; // look for matching selector and context Node

      if (cur.selector === selector && cur.context === context) {
        const {
          listeners
        } = cur; // each item of the listeners array is an array: [function, capture, passive]

        for (let i = listeners.length - 1; i >= 0; i--) {
          const [fn, {
            capture,
            passive
          }] = listeners[i]; // check if the listener functions and capture and passive flags match

          if (fn === listener && capture === options.capture && passive === options.passive) {
            // remove the listener from the array of listeners
            listeners.splice(i, 1); // if all listeners for this target have been removed
            // remove the target from the delegates array

            if (!listeners.length) {
              delegates.splice(index, 1); // remove delegate function from context

              remove(context, type, delegateListener);
              remove(context, type, delegateUseCapture, true);
            } // only remove one listener


            matchFound = true;
            break;
          }
        }

        if (matchFound) {
          break;
        }
      }
    }
  } // bound to the interactable context when a DOM event
  // listener is added to a selector interactable


  function delegateListener(event, optionalArg) {
    const options = getOptions(optionalArg);
    const fakeEvent = new events_FakeEvent(event);
    const delegates = delegatedEvents[event.type];
    const [eventTarget] = getEventTargets(event);
    let element = eventTarget; // climb up document tree looking for selector matches

    while (is.element(element)) {
      for (let i = 0; i < delegates.length; i++) {
        const cur = delegates[i];
        const {
          selector,
          context
        } = cur;

        if (matchesSelector(element, selector) && nodeContains(context, eventTarget) && nodeContains(context, element)) {
          const {
            listeners
          } = cur;
          fakeEvent.currentTarget = element;

          for (const [fn, {
            capture,
            passive
          }] of listeners) {
            if (capture === options.capture && passive === options.passive) {
              fn(fakeEvent);
            }
          }
        }
      }

      element = parentNode(element);
    }
  }

  function delegateUseCapture(event) {
    return delegateListener.call(this, event, true);
  } // for type inferrence


  return eventsMethods;
}

class events_FakeEvent {
  constructor(originalEvent) {
    this.currentTarget = void 0;
    this.originalEvent = void 0;
    this.type = void 0;
    this.originalEvent = originalEvent; // duplicate the event so that currentTarget can be changed

    utils_pointerExtend(this, originalEvent);
  }

  preventOriginalDefault() {
    this.originalEvent.preventDefault();
  }

  stopPropagation() {
    this.originalEvent.stopPropagation();
  }

  stopImmediatePropagation() {
    this.originalEvent.stopImmediatePropagation();
  }

}

function getOptions(param) {
  if (!is.object(param)) {
    return {
      capture: !!param,
      passive: false
    };
  }

  const options = extend({}, param);
  options.capture = !!param.capture;
  options.passive = !!param.passive;
  return options;
}

/* harmony default export */ var events = ({
  id: 'events',
  install
});

// CONCATENATED MODULE: ./node_modules/@interactjs/utils/misc.js

function warnOnce(method, message) {
  let warned = false;
  return function () {
    if (!warned) {
      win.console.warn(message);
      warned = true;
    }

    return method.apply(this, arguments);
  };
}
function copyAction(dest, src) {
  dest.name = src.name;
  dest.axis = src.axis;
  dest.edges = src.edges;
  return dest;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/core/interactStatic.js
/** @module interact */






function createInteractStatic(scope) {
  /**
   * ```js
   * interact('#draggable').draggable(true)
   *
   * var rectables = interact('rect')
   * rectables
   *   .gesturable(true)
   *   .on('gesturemove', function (event) {
   *       // ...
   *   })
   * ```
   *
   * The methods of this variable can be used to set elements as interactables
   * and also to change various default settings.
   *
   * Calling it as a function and passing an element or a valid CSS selector
   * string returns an Interactable object which has various methods to configure
   * it.
   *
   * @global
   *
   * @param {Element | string} target The HTML or SVG Element to interact with
   * or CSS selector
   * @return {Interactable}
   */
  const interact = (target, options) => {
    let interactable = scope.interactables.get(target, options);

    if (!interactable) {
      interactable = scope.interactables.new(target, options);
      interactable.events.global = interact.globalEvents;
    }

    return interactable;
  }; // expose the functions used to calculate multi-touch properties


  interact.getPointerAverage = pointerAverage;
  interact.getTouchBBox = touchBBox;
  interact.getTouchDistance = touchDistance;
  interact.getTouchAngle = touchAngle;
  interact.getElementRect = getElementRect;
  interact.getElementClientRect = getElementClientRect;
  interact.matchesSelector = matchesSelector;
  interact.closest = domUtils_closest;
  interact.globalEvents = {}; // eslint-disable-next-line no-undef

  interact.version = "1.10.2";
  interact.scope = scope;
  /**
  * Use a plugin
  *
  * @alias module:interact.use
  *
   */

  interact.use = function (plugin, options) {
    this.scope.usePlugin(plugin, options);
    return this;
  };
  /**
   * Check if an element or selector has been set with the {@link interact}
   * function
   *
   * @alias module:interact.isSet
   *
   * @param {Target} target The Element or string being searched for
   * @param {object} options
   * @return {boolean} Indicates if the element or CSS selector was previously
   * passed to interact
   */


  interact.isSet = function (target, options) {
    return !!this.scope.interactables.get(target, options && options.context);
  };
  /**
   * @deprecated
   * Add a global listener for an InteractEvent or adds a DOM event to `document`
   *
   * @alias module:interact.on
   *
   * @param {string | array | object} type The types of events to listen for
   * @param {function} listener The function event (s)
   * @param {object | boolean} [options] object or useCapture flag for
   * addEventListener
   * @return {object} interact
   */


  interact.on = warnOnce(function on(type, listener, options) {
    if (is.string(type) && type.search(' ') !== -1) {
      type = type.trim().split(/ +/);
    }

    if (is.array(type)) {
      for (const eventType of type) {
        this.on(eventType, listener, options);
      }

      return this;
    }

    if (is.object(type)) {
      for (const prop in type) {
        this.on(prop, type[prop], listener);
      }

      return this;
    } // if it is an InteractEvent type, add listener to globalEvents


    if (isNonNativeEvent(type, this.scope.actions)) {
      // if this type of event was never bound
      if (!this.globalEvents[type]) {
        this.globalEvents[type] = [listener];
      } else {
        this.globalEvents[type].push(listener);
      }
    } // If non InteractEvent type, addEventListener to document
    else {
        this.scope.events.add(this.scope.document, type, listener, {
          options
        });
      }

    return this;
  }, 'The interact.on() method is being deprecated');
  /**
   * @deprecated
   * Removes a global InteractEvent listener or DOM event from `document`
   *
   * @alias module:interact.off
   *
   * @param {string | array | object} type The types of events that were listened
   * for
   * @param {function} listener The listener function to be removed
   * @param {object | boolean} options [options] object or useCapture flag for
   * removeEventListener
   * @return {object} interact
   */

  interact.off = warnOnce(function off(type, listener, options) {
    if (is.string(type) && type.search(' ') !== -1) {
      type = type.trim().split(/ +/);
    }

    if (is.array(type)) {
      for (const eventType of type) {
        this.off(eventType, listener, options);
      }

      return this;
    }

    if (is.object(type)) {
      for (const prop in type) {
        this.off(prop, type[prop], listener);
      }

      return this;
    }

    if (isNonNativeEvent(type, this.scope.actions)) {
      let index;

      if (type in this.globalEvents && (index = this.globalEvents[type].indexOf(listener)) !== -1) {
        this.globalEvents[type].splice(index, 1);
      }
    } else {
      this.scope.events.remove(this.scope.document, type, listener, options);
    }

    return this;
  }, 'The interact.off() method is being deprecated');

  interact.debug = function () {
    return this.scope;
  };
  /**
   * @alias module:interact.supportsTouch
   *
   * @return {boolean} Whether or not the browser supports touch input
   */


  interact.supportsTouch = function () {
    return utils_browser.supportsTouch;
  };
  /**
   * @alias module:interact.supportsPointerEvent
   *
   * @return {boolean} Whether or not the browser supports PointerEvents
   */


  interact.supportsPointerEvent = function () {
    return utils_browser.supportsPointerEvent;
  };
  /**
   * Cancels all interactions (end events are not fired)
   *
   * @alias module:interact.stop
   *
   * @return {object} interact
   */


  interact.stop = function () {
    for (const interaction of this.scope.interactions.list) {
      interaction.stop();
    }

    return this;
  };
  /**
   * Returns or sets the distance the pointer must be moved before an action
   * sequence occurs. This also affects tolerance for tap events.
   *
   * @alias module:interact.pointerMoveTolerance
   *
   * @param {number} [newValue] The movement from the start position must be greater than this value
   * @return {interact | number}
   */


  interact.pointerMoveTolerance = function (newValue) {
    if (is.number(newValue)) {
      this.scope.interactions.pointerMoveTolerance = newValue;
      return this;
    }

    return this.scope.interactions.pointerMoveTolerance;
  };

  interact.addDocument = function (doc, options) {
    this.scope.addDocument(doc, options);
  };

  interact.removeDocument = function (doc) {
    this.scope.removeDocument(doc);
  };

  return interact;
}

// CONCATENATED MODULE: ./node_modules/@interactjs/core/PointerInfo.js
class PointerInfo {
  constructor(id, pointer, event, downTime, downTarget) {
    this.id = void 0;
    this.pointer = void 0;
    this.event = void 0;
    this.downTime = void 0;
    this.downTarget = void 0;
    this.id = id;
    this.pointer = pointer;
    this.event = event;
    this.downTime = downTime;
    this.downTarget = downTarget;
  }

}

// CONCATENATED MODULE: ./node_modules/@interactjs/core/Interaction.js








let _ProxyValues;

(function (_ProxyValues) {
  _ProxyValues["interactable"] = "";
  _ProxyValues["element"] = "";
  _ProxyValues["prepared"] = "";
  _ProxyValues["pointerIsDown"] = "";
  _ProxyValues["pointerWasMoved"] = "";
  _ProxyValues["_proxy"] = "";
})(_ProxyValues || (_ProxyValues = {}));

let _ProxyMethods;

(function (_ProxyMethods) {
  _ProxyMethods["start"] = "";
  _ProxyMethods["move"] = "";
  _ProxyMethods["end"] = "";
  _ProxyMethods["stop"] = "";
  _ProxyMethods["interacting"] = "";
})(_ProxyMethods || (_ProxyMethods = {}));

let idCounter = 0;
class Interaction_Interaction {
  // current interactable being interacted with
  // the target element of the interactable
  // action that's ready to be fired on next move event
  // keep track of added pointers
  // pointerdown/mousedown/touchstart event
  // previous action event

  /** @internal */
  get pointerMoveTolerance() {
    return 1;
  }
  /**
   * @alias Interaction.prototype.move
   */


  /** */
  constructor({
    pointerType,
    scopeFire
  }) {
    this.interactable = null;
    this.element = null;
    this.rect = void 0;
    this._rects = void 0;
    this.edges = void 0;
    this._scopeFire = void 0;
    this.prepared = {
      name: null,
      axis: null,
      edges: null
    };
    this.pointerType = void 0;
    this.pointers = [];
    this.downEvent = null;
    this.downPointer = {};
    this._latestPointer = {
      pointer: null,
      event: null,
      eventTarget: null
    };
    this.prevEvent = null;
    this.pointerIsDown = false;
    this.pointerWasMoved = false;
    this._interacting = false;
    this._ending = false;
    this._stopped = true;
    this._proxy = null;
    this.simulation = null;
    this.doMove = warnOnce(function (signalArg) {
      this.move(signalArg);
    }, 'The interaction.doMove() method has been renamed to interaction.move()');
    this.coords = {
      // Starting InteractEvent pointer coordinates
      start: newCoords(),
      // Previous native pointer move event coordinates
      prev: newCoords(),
      // current native pointer move event coordinates
      cur: newCoords(),
      // Change in coordinates and time of the pointer
      delta: newCoords(),
      // pointer velocity
      velocity: newCoords()
    };
    this._id = idCounter++;
    this._scopeFire = scopeFire;
    this.pointerType = pointerType;
    const that = this;
    this._proxy = {};

    for (const key in _ProxyValues) {
      Object.defineProperty(this._proxy, key, {
        get() {
          return that[key];
        }

      });
    }

    for (const key in _ProxyMethods) {
      Object.defineProperty(this._proxy, key, {
        value: (...args) => that[key](...args)
      });
    }

    this._scopeFire('interactions:new', {
      interaction: this
    });
  }

  pointerDown(pointer, event, eventTarget) {
    const pointerIndex = this.updatePointer(pointer, event, eventTarget, true);
    const pointerInfo = this.pointers[pointerIndex];

    this._scopeFire('interactions:down', {
      pointer,
      event,
      eventTarget,
      pointerIndex,
      pointerInfo,
      type: 'down',
      interaction: this
    });
  }
  /**
   * ```js
   * interact(target)
   *   .draggable({
   *     // disable the default drag start by down->move
   *     manualStart: true
   *   })
   *   // start dragging after the user holds the pointer down
   *   .on('hold', function (event) {
   *     var interaction = event.interaction
   *
   *     if (!interaction.interacting()) {
   *       interaction.start({ name: 'drag' },
   *                         event.interactable,
   *                         event.currentTarget)
   *     }
   * })
   * ```
   *
   * Start an action with the given Interactable and Element as tartgets. The
   * action must be enabled for the target Interactable and an appropriate
   * number of pointers must be held down - 1 for drag/resize, 2 for gesture.
   *
   * Use it with `interactable.<action>able({ manualStart: false })` to always
   * [start actions manually](https://github.com/taye/interact.js/issues/114)
   *
   * @param {object} action   The action to be performed - drag, resize, etc.
   * @param {Interactable} target  The Interactable to target
   * @param {Element} element The DOM Element to target
   * @return {Boolean} Whether the interaction was successfully started
   */


  start(action, interactable, element) {
    if (this.interacting() || !this.pointerIsDown || this.pointers.length < (action.name === 'gesture' ? 2 : 1) || !interactable.options[action.name].enabled) {
      return false;
    }

    copyAction(this.prepared, action);
    this.interactable = interactable;
    this.element = element;
    this.rect = interactable.getRect(element);
    this.edges = this.prepared.edges ? extend({}, this.prepared.edges) : {
      left: true,
      right: true,
      top: true,
      bottom: true
    };
    this._stopped = false;
    this._interacting = this._doPhase({
      interaction: this,
      event: this.downEvent,
      phase: 'start'
    }) && !this._stopped;
    return this._interacting;
  }

  pointerMove(pointer, event, eventTarget) {
    if (!this.simulation && !(this.modification && this.modification.endResult)) {
      this.updatePointer(pointer, event, eventTarget, false);
    }

    const duplicateMove = this.coords.cur.page.x === this.coords.prev.page.x && this.coords.cur.page.y === this.coords.prev.page.y && this.coords.cur.client.x === this.coords.prev.client.x && this.coords.cur.client.y === this.coords.prev.client.y;
    let dx;
    let dy; // register movement greater than pointerMoveTolerance

    if (this.pointerIsDown && !this.pointerWasMoved) {
      dx = this.coords.cur.client.x - this.coords.start.client.x;
      dy = this.coords.cur.client.y - this.coords.start.client.y;
      this.pointerWasMoved = hypot(dx, dy) > this.pointerMoveTolerance;
    }

    const pointerIndex = this.getPointerIndex(pointer);
    const signalArg = {
      pointer,
      pointerIndex,
      pointerInfo: this.pointers[pointerIndex],
      event,
      type: 'move',
      eventTarget,
      dx,
      dy,
      duplicate: duplicateMove,
      interaction: this
    };

    if (!duplicateMove) {
      // set pointer coordinate, time changes and velocity
      setCoordVelocity(this.coords.velocity, this.coords.delta);
    }

    this._scopeFire('interactions:move', signalArg);

    if (!duplicateMove && !this.simulation) {
      // if interacting, fire an 'action-move' signal etc
      if (this.interacting()) {
        signalArg.type = null;
        this.move(signalArg);
      }

      if (this.pointerWasMoved) {
        copyCoords(this.coords.prev, this.coords.cur);
      }
    }
  }
  /**
   * ```js
   * interact(target)
   *   .draggable(true)
   *   .on('dragmove', function (event) {
   *     if (someCondition) {
   *       // change the snap settings
   *       event.interactable.draggable({ snap: { targets: [] }})
   *       // fire another move event with re-calculated snap
   *       event.interaction.move()
   *     }
   *   })
   * ```
   *
   * Force a move of the current action at the same coordinates. Useful if
   * snap/restrict has been changed and you want a movement with the new
   * settings.
   */


  move(signalArg) {
    if (!signalArg || !signalArg.event) {
      setZeroCoords(this.coords.delta);
    }

    signalArg = extend({
      pointer: this._latestPointer.pointer,
      event: this._latestPointer.event,
      eventTarget: this._latestPointer.eventTarget,
      interaction: this
    }, signalArg || {});
    signalArg.phase = 'move';

    this._doPhase(signalArg);
  } // End interact move events and stop auto-scroll unless simulation is running


  pointerUp(pointer, event, eventTarget, curEventTarget) {
    let pointerIndex = this.getPointerIndex(pointer);

    if (pointerIndex === -1) {
      pointerIndex = this.updatePointer(pointer, event, eventTarget, false);
    }

    const type = /cancel$/i.test(event.type) ? 'cancel' : 'up';

    this._scopeFire(`interactions:${type}`, {
      pointer,
      pointerIndex,
      pointerInfo: this.pointers[pointerIndex],
      event,
      eventTarget,
      type: type,
      curEventTarget,
      interaction: this
    });

    if (!this.simulation) {
      this.end(event);
    }

    this.removePointer(pointer, event);
  }

  documentBlur(event) {
    this.end(event);

    this._scopeFire('interactions:blur', {
      event,
      type: 'blur',
      interaction: this
    });
  }
  /**
   * ```js
   * interact(target)
   *   .draggable(true)
   *   .on('move', function (event) {
   *     if (event.pageX > 1000) {
   *       // end the current action
   *       event.interaction.end()
   *       // stop all further listeners from being called
   *       event.stopImmediatePropagation()
   *     }
   *   })
   * ```
   *
   * @param {PointerEvent} [event]
   */


  end(event) {
    this._ending = true;
    event = event || this._latestPointer.event;
    let endPhaseResult;

    if (this.interacting()) {
      endPhaseResult = this._doPhase({
        event,
        interaction: this,
        phase: 'end'
      });
    }

    this._ending = false;

    if (endPhaseResult === true) {
      this.stop();
    }
  }

  currentAction() {
    return this._interacting ? this.prepared.name : null;
  }

  interacting() {
    return this._interacting;
  }
  /** */


  stop() {
    this._scopeFire('interactions:stop', {
      interaction: this
    });

    this.interactable = this.element = null;
    this._interacting = false;
    this._stopped = true;
    this.prepared.name = this.prevEvent = null;
  }

  getPointerIndex(pointer) {
    const pointerId = getPointerId(pointer); // mouse and pen interactions may have only one pointer

    return this.pointerType === 'mouse' || this.pointerType === 'pen' ? this.pointers.length - 1 : findIndex(this.pointers, curPointer => curPointer.id === pointerId);
  }

  getPointerInfo(pointer) {
    return this.pointers[this.getPointerIndex(pointer)];
  }

  updatePointer(pointer, event, eventTarget, down) {
    const id = getPointerId(pointer);
    let pointerIndex = this.getPointerIndex(pointer);
    let pointerInfo = this.pointers[pointerIndex];
    down = down === false ? false : down || /(down|start)$/i.test(event.type);

    if (!pointerInfo) {
      pointerInfo = new PointerInfo(id, pointer, event, null, null);
      pointerIndex = this.pointers.length;
      this.pointers.push(pointerInfo);
    } else {
      pointerInfo.pointer = pointer;
    }

    setCoords(this.coords.cur, this.pointers.map(p => p.pointer), this._now());
    setCoordDeltas(this.coords.delta, this.coords.prev, this.coords.cur);

    if (down) {
      this.pointerIsDown = true;
      pointerInfo.downTime = this.coords.cur.timeStamp;
      pointerInfo.downTarget = eventTarget;
      utils_pointerExtend(this.downPointer, pointer);

      if (!this.interacting()) {
        copyCoords(this.coords.start, this.coords.cur);
        copyCoords(this.coords.prev, this.coords.cur);
        this.downEvent = event;
        this.pointerWasMoved = false;
      }
    }

    this._updateLatestPointer(pointer, event, eventTarget);

    this._scopeFire('interactions:update-pointer', {
      pointer,
      event,
      eventTarget,
      down,
      pointerInfo,
      pointerIndex,
      interaction: this
    });

    return pointerIndex;
  }

  removePointer(pointer, event) {
    const pointerIndex = this.getPointerIndex(pointer);

    if (pointerIndex === -1) {
      return;
    }

    const pointerInfo = this.pointers[pointerIndex];

    this._scopeFire('interactions:remove-pointer', {
      pointer,
      event,
      eventTarget: null,
      pointerIndex,
      pointerInfo,
      interaction: this
    });

    this.pointers.splice(pointerIndex, 1);
    this.pointerIsDown = false;
  }

  _updateLatestPointer(pointer, event, eventTarget) {
    this._latestPointer.pointer = pointer;
    this._latestPointer.event = event;
    this._latestPointer.eventTarget = eventTarget;
  }

  destroy() {
    this._latestPointer.pointer = null;
    this._latestPointer.event = null;
    this._latestPointer.eventTarget = null;
  }

  _createPreparedEvent(event, phase, preEnd, type) {
    return new InteractEvent_InteractEvent(this, event, this.prepared.name, phase, this.element, preEnd, type);
  }

  _fireEvent(iEvent) {
    this.interactable.fire(iEvent);

    if (!this.prevEvent || iEvent.timeStamp >= this.prevEvent.timeStamp) {
      this.prevEvent = iEvent;
    }
  }

  _doPhase(signalArg) {
    const {
      event,
      phase,
      preEnd,
      type
    } = signalArg;
    const {
      rect
    } = this;

    if (rect && phase === 'move') {
      // update the rect changes due to pointer move
      addEdges(this.edges, rect, this.coords.delta[this.interactable.options.deltaSource]);
      rect.width = rect.right - rect.left;
      rect.height = rect.bottom - rect.top;
    }

    const beforeResult = this._scopeFire(`interactions:before-action-${phase}`, signalArg);

    if (beforeResult === false) {
      return false;
    }

    const iEvent = signalArg.iEvent = this._createPreparedEvent(event, phase, preEnd, type);

    this._scopeFire(`interactions:action-${phase}`, signalArg);

    if (phase === 'start') {
      this.prevEvent = iEvent;
    }

    this._fireEvent(iEvent);

    this._scopeFire(`interactions:after-action-${phase}`, signalArg);

    return true;
  }

  _now() {
    return Date.now();
  }

}
/* harmony default export */ var core_Interaction = (Interaction_Interaction);


// CONCATENATED MODULE: ./node_modules/@interactjs/core/interactablePreventDefault.js





function preventDefault(newValue) {
  if (/^(always|never|auto)$/.test(newValue)) {
    this.options.preventDefault = newValue;
    return this;
  }

  if (is.bool(newValue)) {
    this.options.preventDefault = newValue ? 'always' : 'never';
    return this;
  }

  return this.options.preventDefault;
}

function checkAndPreventDefault(interactable, scope, event) {
  const setting = interactable.options.preventDefault;

  if (setting === 'never') {
    return;
  }

  if (setting === 'always') {
    event.preventDefault();
    return;
  } // setting === 'auto'
  // if the browser supports passive event listeners and isn't running on iOS,
  // don't preventDefault of touch{start,move} events. CSS touch-action and
  // user-select should be used instead of calling event.preventDefault().


  if (scope.events.supportsPassive && /^touch(start|move)$/.test(event.type)) {
    const doc = getWindow(event.target).document;
    const docOptions = scope.getDocOptions(doc);

    if (!(docOptions && docOptions.events) || docOptions.events.passive !== false) {
      return;
    }
  } // don't preventDefault of pointerdown events


  if (/^(mouse|pointer|touch)*(down|start)/i.test(event.type)) {
    return;
  } // don't preventDefault on editable elements


  if (is.element(event.target) && matchesSelector(event.target, 'input,select,textarea,[contenteditable=true],[contenteditable=true] *')) {
    return;
  }

  event.preventDefault();
}

function onInteractionEvent({
  interaction,
  event
}) {
  if (interaction.interactable) {
    interaction.interactable.checkAndPreventDefault(event);
  }
}

function interactablePreventDefault_install(scope) {
  /** @lends Interactable */
  const {
    Interactable
  } = scope;
  /**
   * Returns or sets whether to prevent the browser's default behaviour in
   * response to pointer events. Can be set to:
   *  - `'always'` to always prevent
   *  - `'never'` to never prevent
   *  - `'auto'` to let interact.js try to determine what would be best
   *
   * @param {string} [newValue] `'always'`, `'never'` or `'auto'`
   * @return {string | Interactable} The current setting or this Interactable
   */

  Interactable.prototype.preventDefault = preventDefault;

  Interactable.prototype.checkAndPreventDefault = function (event) {
    return checkAndPreventDefault(this, scope, event);
  }; // prevent native HTML5 drag on interact.js target elements


  scope.interactions.docEvents.push({
    type: 'dragstart',

    listener(event) {
      for (const interaction of scope.interactions.list) {
        if (interaction.element && (interaction.element === event.target || nodeContains(interaction.element, event.target))) {
          interaction.interactable.checkAndPreventDefault(event);
          return;
        }
      }
    }

  });
}
/* harmony default export */ var interactablePreventDefault = ({
  id: 'core/interactablePreventDefault',
  install: interactablePreventDefault_install,
  listeners: ['down', 'move', 'up', 'cancel'].reduce((acc, eventType) => {
    acc[`interactions:${eventType}`] = onInteractionEvent;
    return acc;
  }, {})
});

// CONCATENATED MODULE: ./node_modules/@interactjs/core/interactionFinder.js

const finder = {
  methodOrder: ['simulationResume', 'mouseOrPen', 'hasPointer', 'idle'],

  search(details) {
    for (const method of finder.methodOrder) {
      const interaction = finder[method](details);

      if (interaction) {
        return interaction;
      }
    }

    return null;
  },

  // try to resume simulation with a new pointer
  simulationResume({
    pointerType,
    eventType,
    eventTarget,
    scope
  }) {
    if (!/down|start/i.test(eventType)) {
      return null;
    }

    for (const interaction of scope.interactions.list) {
      let element = eventTarget;

      if (interaction.simulation && interaction.simulation.allowResume && interaction.pointerType === pointerType) {
        while (element) {
          // if the element is the interaction element
          if (element === interaction.element) {
            return interaction;
          }

          element = parentNode(element);
        }
      }
    }

    return null;
  },

  // if it's a mouse or pen interaction
  mouseOrPen({
    pointerId,
    pointerType,
    eventType,
    scope
  }) {
    if (pointerType !== 'mouse' && pointerType !== 'pen') {
      return null;
    }

    let firstNonActive;

    for (const interaction of scope.interactions.list) {
      if (interaction.pointerType === pointerType) {
        // if it's a down event, skip interactions with running simulations
        if (interaction.simulation && !hasPointerId(interaction, pointerId)) {
          continue;
        } // if the interaction is active, return it immediately


        if (interaction.interacting()) {
          return interaction;
        } // otherwise save it and look for another active interaction
        else if (!firstNonActive) {
            firstNonActive = interaction;
          }
      }
    } // if no active mouse interaction was found use the first inactive mouse
    // interaction


    if (firstNonActive) {
      return firstNonActive;
    } // find any mouse or pen interaction.
    // ignore the interaction if the eventType is a *down, and a simulation
    // is active


    for (const interaction of scope.interactions.list) {
      if (interaction.pointerType === pointerType && !(/down/i.test(eventType) && interaction.simulation)) {
        return interaction;
      }
    }

    return null;
  },

  // get interaction that has this pointer
  hasPointer({
    pointerId,
    scope
  }) {
    for (const interaction of scope.interactions.list) {
      if (hasPointerId(interaction, pointerId)) {
        return interaction;
      }
    }

    return null;
  },

  // get first idle interaction with a matching pointerType
  idle({
    pointerType,
    scope
  }) {
    for (const interaction of scope.interactions.list) {
      // if there's already a pointer held down
      if (interaction.pointers.length === 1) {
        const target = interaction.interactable; // don't add this pointer if there is a target interactable and it
        // isn't gesturable

        if (target && !(target.options.gesture && target.options.gesture.enabled)) {
          continue;
        }
      } // maximum of 2 pointers per interaction
      else if (interaction.pointers.length >= 2) {
          continue;
        }

      if (!interaction.interacting() && pointerType === interaction.pointerType) {
        return interaction;
      }
    }

    return null;
  }

};

function hasPointerId(interaction, pointerId) {
  return interaction.pointers.some(({
    id
  }) => id === pointerId);
}

/* harmony default export */ var interactionFinder = (finder);

// CONCATENATED MODULE: ./node_modules/@interactjs/core/interactions.js








const methodNames = ['pointerDown', 'pointerMove', 'pointerUp', 'updatePointer', 'removePointer', 'windowBlur'];

function interactions_install(scope) {
  const listeners = {};

  for (const method of methodNames) {
    listeners[method] = doOnInteractions(method, scope);
  }

  const pEventTypes = utils_browser.pEventTypes;
  let docEvents;

  if (utils_domObjects.PointerEvent) {
    docEvents = [{
      type: pEventTypes.down,
      listener: releasePointersOnRemovedEls
    }, {
      type: pEventTypes.down,
      listener: listeners.pointerDown
    }, {
      type: pEventTypes.move,
      listener: listeners.pointerMove
    }, {
      type: pEventTypes.up,
      listener: listeners.pointerUp
    }, {
      type: pEventTypes.cancel,
      listener: listeners.pointerUp
    }];
  } else {
    docEvents = [{
      type: 'mousedown',
      listener: listeners.pointerDown
    }, {
      type: 'mousemove',
      listener: listeners.pointerMove
    }, {
      type: 'mouseup',
      listener: listeners.pointerUp
    }, {
      type: 'touchstart',
      listener: releasePointersOnRemovedEls
    }, {
      type: 'touchstart',
      listener: listeners.pointerDown
    }, {
      type: 'touchmove',
      listener: listeners.pointerMove
    }, {
      type: 'touchend',
      listener: listeners.pointerUp
    }, {
      type: 'touchcancel',
      listener: listeners.pointerUp
    }];
  }

  docEvents.push({
    type: 'blur',

    listener(event) {
      for (const interaction of scope.interactions.list) {
        interaction.documentBlur(event);
      }
    }

  }); // for ignoring browser's simulated mouse events

  scope.prevTouchTime = 0;
  scope.Interaction = class extends core_Interaction {
    get pointerMoveTolerance() {
      return scope.interactions.pointerMoveTolerance;
    }

    set pointerMoveTolerance(value) {
      scope.interactions.pointerMoveTolerance = value;
    }

    _now() {
      return scope.now();
    }

  };
  scope.interactions = {
    // all active and idle interactions
    list: [],

    new(options) {
      options.scopeFire = (name, arg) => scope.fire(name, arg);

      const interaction = new scope.Interaction(options);
      scope.interactions.list.push(interaction);
      return interaction;
    },

    listeners,
    docEvents,
    pointerMoveTolerance: 1
  };

  function releasePointersOnRemovedEls() {
    // for all inactive touch interactions with pointers down
    for (const interaction of scope.interactions.list) {
      if (!interaction.pointerIsDown || interaction.pointerType !== 'touch' || interaction._interacting) {
        continue;
      } // if a pointer is down on an element that is no longer in the DOM tree


      for (const pointer of interaction.pointers) {
        if (!scope.documents.some(({
          doc
        }) => nodeContains(doc, pointer.downTarget))) {
          // remove the pointer from the interaction
          interaction.removePointer(pointer.pointer, pointer.event);
        }
      }
    }
  }

  scope.usePlugin(interactablePreventDefault);
}

function doOnInteractions(method, scope) {
  return function (event) {
    const interactions = scope.interactions.list;
    const pointerType = getPointerType(event);
    const [eventTarget, curEventTarget] = getEventTargets(event);
    const matches = []; // [ [pointer, interaction], ...]

    if (/^touch/.test(event.type)) {
      scope.prevTouchTime = scope.now(); // @ts-expect-error

      for (const changedTouch of event.changedTouches) {
        const pointer = changedTouch;
        const pointerId = getPointerId(pointer);
        const searchDetails = {
          pointer,
          pointerId,
          pointerType,
          eventType: event.type,
          eventTarget,
          curEventTarget,
          scope
        };
        const interaction = getInteraction(searchDetails);
        matches.push([searchDetails.pointer, searchDetails.eventTarget, searchDetails.curEventTarget, interaction]);
      }
    } else {
      let invalidPointer = false;

      if (!utils_browser.supportsPointerEvent && /mouse/.test(event.type)) {
        // ignore mouse events while touch interactions are active
        for (let i = 0; i < interactions.length && !invalidPointer; i++) {
          invalidPointer = interactions[i].pointerType !== 'mouse' && interactions[i].pointerIsDown;
        } // try to ignore mouse events that are simulated by the browser
        // after a touch event


        invalidPointer = invalidPointer || scope.now() - scope.prevTouchTime < 500 || // on iOS and Firefox Mobile, MouseEvent.timeStamp is zero if simulated
        event.timeStamp === 0;
      }

      if (!invalidPointer) {
        const searchDetails = {
          pointer: event,
          pointerId: getPointerId(event),
          pointerType,
          eventType: event.type,
          curEventTarget,
          eventTarget,
          scope
        };
        const interaction = getInteraction(searchDetails);
        matches.push([searchDetails.pointer, searchDetails.eventTarget, searchDetails.curEventTarget, interaction]);
      }
    } // eslint-disable-next-line no-shadow


    for (const [pointer, eventTarget, curEventTarget, interaction] of matches) {
      interaction[method](pointer, event, eventTarget, curEventTarget);
    }
  };
}

function getInteraction(searchDetails) {
  const {
    pointerType,
    scope
  } = searchDetails;
  const foundInteraction = interactionFinder.search(searchDetails);
  const signalArg = {
    interaction: foundInteraction,
    searchDetails
  };
  scope.fire('interactions:find', signalArg);
  return signalArg.interaction || scope.interactions.new({
    pointerType
  });
}

function onDocSignal({
  doc,
  scope,
  options
}, eventMethodName) {
  const {
    interactions: {
      docEvents
    },
    events
  } = scope;
  const eventMethod = events[eventMethodName];

  if (scope.browser.isIOS && !options.events) {
    options.events = {
      passive: false
    };
  } // delegate event listener


  for (const eventType in events.delegatedEvents) {
    eventMethod(doc, eventType, events.delegateListener);
    eventMethod(doc, eventType, events.delegateUseCapture, true);
  }

  const eventOptions = options && options.events;

  for (const {
    type,
    listener
  } of docEvents) {
    eventMethod(doc, type, listener, eventOptions);
  }
}

const interactions_interactions = {
  id: 'core/interactions',
  install: interactions_install,
  listeners: {
    'scope:add-document': arg => onDocSignal(arg, 'add'),
    'scope:remove-document': arg => onDocSignal(arg, 'remove'),
    'interactable:unset': ({
      interactable
    }, scope) => {
      // Stop and destroy related interactions when an Interactable is unset
      for (let i = scope.interactions.list.length - 1; i >= 0; i--) {
        const interaction = scope.interactions.list[i];

        if (interaction.interactable !== interactable) {
          continue;
        }

        interaction.stop();
        scope.fire('interactions:destroy', {
          interaction
        });
        interaction.destroy();

        if (scope.interactions.list.length > 2) {
          scope.interactions.list.splice(i, 1);
        }
      }
    }
  },
  onDocSignal,
  doOnInteractions,
  methodNames
};
/* harmony default export */ var core_interactions = (interactions_interactions);

// CONCATENATED MODULE: ./node_modules/@interactjs/core/scope.js














class scope_Scope {
  // main window
  // main document
  // main window
  // all documents being listened to
  constructor() {
    this.id = `__interact_scope_${Math.floor(Math.random() * 100)}`;
    this.isInitialized = false;
    this.listenerMaps = [];
    this.browser = utils_browser;
    this.defaults = clone(defaultOptions_defaults);
    this.Eventable = Eventable_Eventable;
    this.actions = {
      map: {},
      phases: {
        start: true,
        move: true,
        end: true
      },
      methodDict: {},
      phaselessTypes: {}
    };
    this.interactStatic = createInteractStatic(this);
    this.InteractEvent = InteractEvent_InteractEvent;
    this.Interactable = void 0;
    this.interactables = new InteractableSet_InteractableSet(this);
    this._win = void 0;
    this.document = void 0;
    this.window = void 0;
    this.documents = [];
    this._plugins = {
      list: [],
      map: {}
    };

    this.onWindowUnload = event => this.removeDocument(event.target);

    const scope = this;
    this.Interactable = class extends Interactable_Interactable {
      get _defaults() {
        return scope.defaults;
      }

      set(options) {
        super.set(options);
        scope.fire('interactable:set', {
          options,
          interactable: this
        });
        return this;
      }

      unset() {
        super.unset();
        scope.interactables.list.splice(scope.interactables.list.indexOf(this), 1);
        scope.fire('interactable:unset', {
          interactable: this
        });
      }

    };
  }

  addListeners(map, id) {
    this.listenerMaps.push({
      id,
      map
    });
  }

  fire(name, arg) {
    for (const {
      map: {
        [name]: listener
      }
    } of this.listenerMaps) {
      if (!!listener && listener(arg, this, name) === false) {
        return false;
      }
    }
  }

  init(window) {
    return this.isInitialized ? this : initScope(this, window);
  }

  pluginIsInstalled(plugin) {
    return this._plugins.map[plugin.id] || this._plugins.list.indexOf(plugin) !== -1;
  }

  usePlugin(plugin, options) {
    if (!this.isInitialized) {
      return this;
    }

    if (this.pluginIsInstalled(plugin)) {
      return this;
    }

    if (plugin.id) {
      this._plugins.map[plugin.id] = plugin;
    }

    this._plugins.list.push(plugin);

    if (plugin.install) {
      plugin.install(this, options);
    }

    if (plugin.listeners && plugin.before) {
      let index = 0;
      const len = this.listenerMaps.length;
      const before = plugin.before.reduce((acc, id) => {
        acc[id] = true;
        acc[pluginIdRoot(id)] = true;
        return acc;
      }, {});

      for (; index < len; index++) {
        const otherId = this.listenerMaps[index].id;

        if (before[otherId] || before[pluginIdRoot(otherId)]) {
          break;
        }
      }

      this.listenerMaps.splice(index, 0, {
        id: plugin.id,
        map: plugin.listeners
      });
    } else if (plugin.listeners) {
      this.listenerMaps.push({
        id: plugin.id,
        map: plugin.listeners
      });
    }

    return this;
  }

  addDocument(doc, options) {
    // do nothing if document is already known
    if (this.getDocIndex(doc) !== -1) {
      return false;
    }

    const window = getWindow(doc);
    options = options ? extend({}, options) : {};
    this.documents.push({
      doc,
      options
    });
    this.events.documents.push(doc); // don't add an unload event for the main document
    // so that the page may be cached in browser history

    if (doc !== this.document) {
      this.events.add(window, 'unload', this.onWindowUnload);
    }

    this.fire('scope:add-document', {
      doc,
      window,
      scope: this,
      options
    });
  }

  removeDocument(doc) {
    const index = this.getDocIndex(doc);
    const window = getWindow(doc);
    const options = this.documents[index].options;
    this.events.remove(window, 'unload', this.onWindowUnload);
    this.documents.splice(index, 1);
    this.events.documents.splice(index, 1);
    this.fire('scope:remove-document', {
      doc,
      window,
      scope: this,
      options
    });
  }

  getDocIndex(doc) {
    for (let i = 0; i < this.documents.length; i++) {
      if (this.documents[i].doc === doc) {
        return i;
      }
    }

    return -1;
  }

  getDocOptions(doc) {
    const docIndex = this.getDocIndex(doc);
    return docIndex === -1 ? null : this.documents[docIndex].options;
  }

  now() {
    return (this.window.Date || Date).now();
  }

}
function initScope(scope, window) {
  scope.isInitialized = true;
  window_init(window);
  utils_domObjects.init(window);
  utils_browser.init(window);
  raf.init(window);
  scope.window = window;
  scope.document = window.document;
  scope.usePlugin(core_interactions);
  scope.usePlugin(events);
  return scope;
}

function pluginIdRoot(id) {
  return id && id.replace(/\/.*$/, '');
}

// CONCATENATED MODULE: ./node_modules/@interactjs/interact/index.js

const interact_scope = new scope_Scope();
const interact_interact = interact_scope.interactStatic;
/* harmony default export */ var _interactjs_interact = (interact_interact);
const interact_init = win => interact_scope.init(win);

if (typeof window === 'object' && !!window) {
  interact_init(window);
}

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-start/InteractableMethods.js




function InteractableMethods_install(scope) {
  const {
    /** @lends Interactable */
    Interactable // tslint:disable-line no-shadowed-variable

  } = scope;

  Interactable.prototype.getAction = function getAction(pointer, event, interaction, element) {
    const action = defaultActionChecker(this, event, interaction, element, scope);

    if (this.options.actionChecker) {
      return this.options.actionChecker(pointer, event, action, this, element, interaction);
    }

    return action;
  };
  /**
   * If the target of the `mousedown`, `pointerdown` or `touchstart` event or any
   * of it's parents match the given CSS selector or Element, no
   * drag/resize/gesture is started.
   *
   * @deprecated
   * Don't use this method. Instead set the `ignoreFrom` option for each action
   * or for `pointerEvents`
   *
   * ```js
   * interact(targett)
   *   .draggable({
   *     ignoreFrom: 'input, textarea, a[href]'',
   *   })
   *   .pointerEvents({
   *     ignoreFrom: '[no-pointer]',
   *   })
   * ```
   *
   * @param {string | Element | null} [newValue] a CSS selector string, an
   * Element or `null` to not ignore any elements
   * @return {string | Element | object} The current ignoreFrom value or this
   * Interactable
   */


  Interactable.prototype.ignoreFrom = warnOnce(function (newValue) {
    return this._backCompatOption('ignoreFrom', newValue);
  }, 'Interactable.ignoreFrom() has been deprecated. Use Interactble.draggable({ignoreFrom: newValue}).');
  /**
   *
   * A drag/resize/gesture is started only If the target of the `mousedown`,
   * `pointerdown` or `touchstart` event or any of it's parents match the given
   * CSS selector or Element.
   *
   * @deprecated
   * Don't use this method. Instead set the `allowFrom` option for each action
   * or for `pointerEvents`
   *
   * ```js
   * interact(targett)
   *   .resizable({
   *     allowFrom: '.resize-handle',
   *   .pointerEvents({
   *     allowFrom: '.handle',,
   *   })
   * ```
   *
   * @param {string | Element | null} [newValue] a CSS selector string, an
   * Element or `null` to allow from any element
   * @return {string | Element | object} The current allowFrom value or this
   * Interactable
   */

  Interactable.prototype.allowFrom = warnOnce(function (newValue) {
    return this._backCompatOption('allowFrom', newValue);
  }, 'Interactable.allowFrom() has been deprecated. Use Interactble.draggable({allowFrom: newValue}).');
  /**
   * ```js
   * interact('.resize-drag')
   *   .resizable(true)
   *   .draggable(true)
   *   .actionChecker(function (pointer, event, action, interactable, element, interaction) {
   *
   *     if (interact.matchesSelector(event.target, '.drag-handle')) {
   *       // force drag with handle target
   *       action.name = drag
   *     }
   *     else {
   *       // resize from the top and right edges
   *       action.name  = 'resize'
   *       action.edges = { top: true, right: true }
   *     }
   *
   *     return action
   * })
   * ```
   *
   * Returns or sets the function used to check action to be performed on
   * pointerDown
   *
   * @param {function | null} [checker] A function which takes a pointer event,
   * defaultAction string, interactable, element and interaction as parameters
   * and returns an object with name property 'drag' 'resize' or 'gesture' and
   * optionally an `edges` object with boolean 'top', 'left', 'bottom' and right
   * props.
   * @return {Function | Interactable} The checker function or this Interactable
   */

  Interactable.prototype.actionChecker = actionChecker;
  /**
   * Returns or sets whether the the cursor should be changed depending on the
   * action that would be performed if the mouse were pressed and dragged.
   *
   * @param {boolean} [newValue]
   * @return {boolean | Interactable} The current setting or this Interactable
   */

  Interactable.prototype.styleCursor = styleCursor;
}

function defaultActionChecker(interactable, event, interaction, element, scope) {
  const rect = interactable.getRect(element);
  const buttons = event.buttons || {
    0: 1,
    1: 4,
    3: 8,
    4: 16
  }[event.button];
  const arg = {
    action: null,
    interactable,
    interaction,
    element,
    rect,
    buttons
  };
  scope.fire('auto-start:check', arg);
  return arg.action;
}

function styleCursor(newValue) {
  if (is.bool(newValue)) {
    this.options.styleCursor = newValue;
    return this;
  }

  if (newValue === null) {
    delete this.options.styleCursor;
    return this;
  }

  return this.options.styleCursor;
}

function actionChecker(checker) {
  if (is.func(checker)) {
    this.options.actionChecker = checker;
    return this;
  }

  if (checker === null) {
    delete this.options.actionChecker;
    return this;
  }

  return this.options.actionChecker;
}

/* harmony default export */ var InteractableMethods = ({
  id: 'auto-start/interactableMethods',
  install: InteractableMethods_install
});

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-start/base.js







function base_install(scope) {
  const {
    interactStatic: interact,
    defaults
  } = scope;
  scope.usePlugin(InteractableMethods);
  defaults.base.actionChecker = null;
  defaults.base.styleCursor = true;
  extend(defaults.perAction, {
    manualStart: false,
    max: Infinity,
    maxPerElement: 1,
    allowFrom: null,
    ignoreFrom: null,
    // only allow left button by default
    // see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons#Return_value
    mouseButtons: 1
  });
  /**
   * Returns or sets the maximum number of concurrent interactions allowed.  By
   * default only 1 interaction is allowed at a time (for backwards
   * compatibility). To allow multiple interactions on the same Interactables and
   * elements, you need to enable it in the draggable, resizable and gesturable
   * `'max'` and `'maxPerElement'` options.
   *
   * @alias module:interact.maxInteractions
   *
   * @param {number} [newValue] Any number. newValue <= 0 means no interactions.
   */

  interact.maxInteractions = newValue => maxInteractions(newValue, scope);

  scope.autoStart = {
    // Allow this many interactions to happen simultaneously
    maxInteractions: Infinity,
    withinInteractionLimit,
    cursorElement: null
  };
}

function prepareOnDown({
  interaction,
  pointer,
  event,
  eventTarget
}, scope) {
  if (interaction.interacting()) {
    return;
  }

  const actionInfo = getActionInfo(interaction, pointer, event, eventTarget, scope);
  prepare(interaction, actionInfo, scope);
}

function prepareOnMove({
  interaction,
  pointer,
  event,
  eventTarget
}, scope) {
  if (interaction.pointerType !== 'mouse' || interaction.pointerIsDown || interaction.interacting()) {
    return;
  }

  const actionInfo = getActionInfo(interaction, pointer, event, eventTarget, scope);
  prepare(interaction, actionInfo, scope);
}

function startOnMove(arg, scope) {
  const {
    interaction
  } = arg;

  if (!interaction.pointerIsDown || interaction.interacting() || !interaction.pointerWasMoved || !interaction.prepared.name) {
    return;
  }

  scope.fire('autoStart:before-start', arg);
  const {
    interactable
  } = interaction;
  const actionName = interaction.prepared.name;

  if (actionName && interactable) {
    // check manualStart and interaction limit
    if (interactable.options[actionName].manualStart || !withinInteractionLimit(interactable, interaction.element, interaction.prepared, scope)) {
      interaction.stop();
    } else {
      interaction.start(interaction.prepared, interactable, interaction.element);
      setInteractionCursor(interaction, scope);
    }
  }
}

function clearCursorOnStop({
  interaction
}, scope) {
  const {
    interactable
  } = interaction;

  if (interactable && interactable.options.styleCursor) {
    setCursor(interaction.element, '', scope);
  }
} // Check if the current interactable supports the action.
// If so, return the validated action. Otherwise, return null


function validateAction(action, interactable, element, eventTarget, scope) {
  if (interactable.testIgnoreAllow(interactable.options[action.name], element, eventTarget) && interactable.options[action.name].enabled && withinInteractionLimit(interactable, element, action, scope)) {
    return action;
  }

  return null;
}

function validateMatches(interaction, pointer, event, matches, matchElements, eventTarget, scope) {
  for (let i = 0, len = matches.length; i < len; i++) {
    const match = matches[i];
    const matchElement = matchElements[i];
    const matchAction = match.getAction(pointer, event, interaction, matchElement);

    if (!matchAction) {
      continue;
    }

    const action = validateAction(matchAction, match, matchElement, eventTarget, scope);

    if (action) {
      return {
        action,
        interactable: match,
        element: matchElement
      };
    }
  }

  return {
    action: null,
    interactable: null,
    element: null
  };
}

function getActionInfo(interaction, pointer, event, eventTarget, scope) {
  let matches = [];
  let matchElements = [];
  let element = eventTarget;

  function pushMatches(interactable) {
    matches.push(interactable);
    matchElements.push(element);
  }

  while (is.element(element)) {
    matches = [];
    matchElements = [];
    scope.interactables.forEachMatch(element, pushMatches);
    const actionInfo = validateMatches(interaction, pointer, event, matches, matchElements, eventTarget, scope);

    if (actionInfo.action && !actionInfo.interactable.options[actionInfo.action.name].manualStart) {
      return actionInfo;
    }

    element = parentNode(element);
  }

  return {
    action: null,
    interactable: null,
    element: null
  };
}

function prepare(interaction, {
  action,
  interactable,
  element
}, scope) {
  action = action || {
    name: null
  };
  interaction.interactable = interactable;
  interaction.element = element;
  copyAction(interaction.prepared, action);
  interaction.rect = interactable && action.name ? interactable.getRect(element) : null;
  setInteractionCursor(interaction, scope);
  scope.fire('autoStart:prepared', {
    interaction
  });
}

function withinInteractionLimit(interactable, element, action, scope) {
  const options = interactable.options;
  const maxActions = options[action.name].max;
  const maxPerElement = options[action.name].maxPerElement;
  const autoStartMax = scope.autoStart.maxInteractions;
  let activeInteractions = 0;
  let interactableCount = 0;
  let elementCount = 0; // no actions if any of these values == 0

  if (!(maxActions && maxPerElement && autoStartMax)) {
    return false;
  }

  for (const interaction of scope.interactions.list) {
    const otherAction = interaction.prepared.name;

    if (!interaction.interacting()) {
      continue;
    }

    activeInteractions++;

    if (activeInteractions >= autoStartMax) {
      return false;
    }

    if (interaction.interactable !== interactable) {
      continue;
    }

    interactableCount += otherAction === action.name ? 1 : 0;

    if (interactableCount >= maxActions) {
      return false;
    }

    if (interaction.element === element) {
      elementCount++;

      if (otherAction === action.name && elementCount >= maxPerElement) {
        return false;
      }
    }
  }

  return autoStartMax > 0;
}

function maxInteractions(newValue, scope) {
  if (is.number(newValue)) {
    scope.autoStart.maxInteractions = newValue;
    return this;
  }

  return scope.autoStart.maxInteractions;
}

function setCursor(element, cursor, scope) {
  const {
    cursorElement: prevCursorElement
  } = scope.autoStart;

  if (prevCursorElement && prevCursorElement !== element) {
    prevCursorElement.style.cursor = '';
  }

  element.ownerDocument.documentElement.style.cursor = cursor;
  element.style.cursor = cursor;
  scope.autoStart.cursorElement = cursor ? element : null;
}

function setInteractionCursor(interaction, scope) {
  const {
    interactable,
    element,
    prepared
  } = interaction;

  if (!(interaction.pointerType === 'mouse' && interactable && interactable.options.styleCursor)) {
    // clear previous target element cursor
    if (scope.autoStart.cursorElement) {
      setCursor(scope.autoStart.cursorElement, '', scope);
    }

    return;
  }

  let cursor = '';

  if (prepared.name) {
    const cursorChecker = interactable.options[prepared.name].cursorChecker;

    if (is.func(cursorChecker)) {
      cursor = cursorChecker(prepared, interactable, element, interaction._interacting);
    } else {
      cursor = scope.actions.map[prepared.name].getCursor(prepared);
    }
  }

  setCursor(interaction.element, cursor || '', scope);
}

const autoStart = {
  id: 'auto-start/base',
  before: ['actions'],
  install: base_install,
  listeners: {
    'interactions:down': prepareOnDown,
    'interactions:move': (arg, scope) => {
      prepareOnMove(arg, scope);
      startOnMove(arg, scope);
    },
    'interactions:stop': clearCursorOnStop
  },
  maxInteractions,
  withinInteractionLimit,
  validateAction
};
/* harmony default export */ var base = (autoStart);

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-start/dragAxis.js




function beforeStart({
  interaction,
  eventTarget,
  dx,
  dy
}, scope) {
  if (interaction.prepared.name !== 'drag') {
    return;
  } // check if a drag is in the correct axis


  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  const targetOptions = interaction.interactable.options.drag;
  const startAxis = targetOptions.startAxis;
  const currentAxis = absX > absY ? 'x' : absX < absY ? 'y' : 'xy';
  interaction.prepared.axis = targetOptions.lockAxis === 'start' ? currentAxis[0] // always lock to one axis even if currentAxis === 'xy'
  : targetOptions.lockAxis; // if the movement isn't in the startAxis of the interactable

  if (currentAxis !== 'xy' && startAxis !== 'xy' && startAxis !== currentAxis) {
    // cancel the prepared action
    interaction.prepared.name = null; // then try to get a drag from another ineractable

    let element = eventTarget;

    const getDraggable = function (interactable) {
      if (interactable === interaction.interactable) {
        return;
      }

      const options = interaction.interactable.options.drag;

      if (!options.manualStart && interactable.testIgnoreAllow(options, element, eventTarget)) {
        const action = interactable.getAction(interaction.downPointer, interaction.downEvent, interaction, element);

        if (action && action.name === 'drag' && checkStartAxis(currentAxis, interactable) && base.validateAction(action, interactable, element, eventTarget, scope)) {
          return interactable;
        }
      }
    }; // check all interactables


    while (is.element(element)) {
      const interactable = scope.interactables.forEachMatch(element, getDraggable);

      if (interactable) {
        interaction.prepared.name = 'drag';
        interaction.interactable = interactable;
        interaction.element = element;
        break;
      }

      element = parentNode(element);
    }
  }
}

function checkStartAxis(startAxis, interactable) {
  if (!interactable) {
    return false;
  }

  const thisAxis = interactable.options.drag.startAxis;
  return startAxis === 'xy' || thisAxis === 'xy' || thisAxis === startAxis;
}

/* harmony default export */ var dragAxis = ({
  id: 'auto-start/dragAxis',
  listeners: {
    'autoStart:before-start': beforeStart
  }
});

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-start/hold.js



function hold_install(scope) {
  const {
    defaults
  } = scope;
  scope.usePlugin(base);
  defaults.perAction.hold = 0;
  defaults.perAction.delay = 0;
}

function getHoldDuration(interaction) {
  const actionName = interaction.prepared && interaction.prepared.name;

  if (!actionName) {
    return null;
  }

  const options = interaction.interactable.options;
  return options[actionName].hold || options[actionName].delay;
}

const hold = {
  id: 'auto-start/hold',
  install: hold_install,
  listeners: {
    'interactions:new': ({
      interaction
    }) => {
      interaction.autoStartHoldTimer = null;
    },
    'autoStart:prepared': ({
      interaction
    }) => {
      const hold = getHoldDuration(interaction);

      if (hold > 0) {
        interaction.autoStartHoldTimer = setTimeout(() => {
          interaction.start(interaction.prepared, interaction.interactable, interaction.element);
        }, hold);
      }
    },
    'interactions:move': ({
      interaction,
      duplicate
    }) => {
      if (interaction.autoStartHoldTimer && interaction.pointerWasMoved && !duplicate) {
        clearTimeout(interaction.autoStartHoldTimer);
        interaction.autoStartHoldTimer = null;
      }
    },
    // prevent regular down->move autoStart
    'autoStart:before-start': ({
      interaction
    }) => {
      const holdDuration = getHoldDuration(interaction);

      if (holdDuration > 0) {
        interaction.prepared.name = null;
      }
    }
  },
  getHoldDuration
};
/* harmony default export */ var auto_start_hold = (hold);

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-start/plugin.js



/* harmony default export */ var auto_start_plugin = ({
  id: 'auto-start',

  install(scope) {
    scope.usePlugin(base);
    scope.usePlugin(auto_start_hold);
    scope.usePlugin(dragAxis);
  }

});

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-start/index.js
/* eslint-disable import/order, no-console, eol-last */



if (typeof window === 'object' && !!window) {
  interact_init(window);
}

_interactjs_interact.use(auto_start_plugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-scroll/plugin.js







function plugin_install(scope) {
  const {
    defaults,
    actions
  } = scope;
  scope.autoScroll = autoScroll;

  autoScroll.now = () => scope.now();

  actions.phaselessTypes.autoscroll = true;
  defaults.perAction.autoScroll = autoScroll.defaults;
}

const autoScroll = {
  defaults: {
    enabled: false,
    margin: 60,
    // the item that is scrolled (Window or HTMLElement)
    container: null,
    // the scroll speed in pixels per second
    speed: 300
  },
  now: Date.now,
  interaction: null,
  i: 0,
  // the handle returned by window.setInterval
  // Direction each pulse is to scroll in
  x: 0,
  y: 0,
  isScrolling: false,
  prevTime: 0,
  margin: 0,
  speed: 0,

  start(interaction) {
    autoScroll.isScrolling = true;
    raf.cancel(autoScroll.i);
    interaction.autoScroll = autoScroll;
    autoScroll.interaction = interaction;
    autoScroll.prevTime = autoScroll.now();
    autoScroll.i = raf.request(autoScroll.scroll);
  },

  stop() {
    autoScroll.isScrolling = false;

    if (autoScroll.interaction) {
      autoScroll.interaction.autoScroll = null;
    }

    raf.cancel(autoScroll.i);
  },

  // scroll the window by the values in scroll.x/y
  scroll() {
    const {
      interaction
    } = autoScroll;
    const {
      interactable,
      element
    } = interaction;
    const actionName = interaction.prepared.name;
    const options = interactable.options[actionName].autoScroll;
    const container = getContainer(options.container, interactable, element);
    const now = autoScroll.now(); // change in time in seconds

    const dt = (now - autoScroll.prevTime) / 1000; // displacement

    const s = options.speed * dt;

    if (s >= 1) {
      const scrollBy = {
        x: autoScroll.x * s,
        y: autoScroll.y * s
      };

      if (scrollBy.x || scrollBy.y) {
        const prevScroll = getScroll(container);

        if (is.window(container)) {
          container.scrollBy(scrollBy.x, scrollBy.y);
        } else if (container) {
          container.scrollLeft += scrollBy.x;
          container.scrollTop += scrollBy.y;
        }

        const curScroll = getScroll(container);
        const delta = {
          x: curScroll.x - prevScroll.x,
          y: curScroll.y - prevScroll.y
        };

        if (delta.x || delta.y) {
          interactable.fire({
            type: 'autoscroll',
            target: element,
            interactable,
            delta,
            interaction,
            container
          });
        }
      }

      autoScroll.prevTime = now;
    }

    if (autoScroll.isScrolling) {
      raf.cancel(autoScroll.i);
      autoScroll.i = raf.request(autoScroll.scroll);
    }
  },

  check(interactable, actionName) {
    var _options$actionName$a;

    const options = interactable.options;
    return (_options$actionName$a = options[actionName].autoScroll) == null ? void 0 : _options$actionName$a.enabled;
  },

  onInteractionMove({
    interaction,
    pointer
  }) {
    if (!(interaction.interacting() && autoScroll.check(interaction.interactable, interaction.prepared.name))) {
      return;
    }

    if (interaction.simulation) {
      autoScroll.x = autoScroll.y = 0;
      return;
    }

    let top;
    let right;
    let bottom;
    let left;
    const {
      interactable,
      element
    } = interaction;
    const actionName = interaction.prepared.name;
    const options = interactable.options[actionName].autoScroll;
    const container = getContainer(options.container, interactable, element);

    if (is.window(container)) {
      left = pointer.clientX < autoScroll.margin;
      top = pointer.clientY < autoScroll.margin;
      right = pointer.clientX > container.innerWidth - autoScroll.margin;
      bottom = pointer.clientY > container.innerHeight - autoScroll.margin;
    } else {
      const rect = getElementClientRect(container);
      left = pointer.clientX < rect.left + autoScroll.margin;
      top = pointer.clientY < rect.top + autoScroll.margin;
      right = pointer.clientX > rect.right - autoScroll.margin;
      bottom = pointer.clientY > rect.bottom - autoScroll.margin;
    }

    autoScroll.x = right ? 1 : left ? -1 : 0;
    autoScroll.y = bottom ? 1 : top ? -1 : 0;

    if (!autoScroll.isScrolling) {
      // set the autoScroll properties to those of the target
      autoScroll.margin = options.margin;
      autoScroll.speed = options.speed;
      autoScroll.start(interaction);
    }
  }

};
function getContainer(value, interactable, element) {
  return (is.string(value) ? getStringOptionResult(value, interactable, element) : value) || getWindow(element);
}
function getScroll(container) {
  if (is.window(container)) {
    container = window.document.body;
  }

  return {
    x: container.scrollLeft,
    y: container.scrollTop
  };
}
const autoScrollPlugin = {
  id: 'auto-scroll',
  install: plugin_install,
  listeners: {
    'interactions:new': ({
      interaction
    }) => {
      interaction.autoScroll = null;
    },
    'interactions:destroy': ({
      interaction
    }) => {
      interaction.autoScroll = null;
      autoScroll.stop();

      if (autoScroll.interaction) {
        autoScroll.interaction = null;
      }
    },
    'interactions:stop': autoScroll.stop,
    'interactions:action-move': arg => autoScroll.onInteractionMove(arg)
  }
};
/* harmony default export */ var auto_scroll_plugin = (autoScrollPlugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/auto-scroll/index.js
/* eslint-disable import/order, no-console, eol-last */



if (typeof window === 'object' && !!window) {
  interact_init(window);
}

_interactjs_interact.use(auto_scroll_plugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/actions/drag/plugin.js



function drag_plugin_install(scope) {
  const {
    actions,
    Interactable,
    defaults
  } = scope;
  Interactable.prototype.draggable = drag.draggable;
  actions.map.drag = drag;
  actions.methodDict.drag = 'draggable';
  defaults.actions.drag = drag.defaults;
}

function beforeMove({
  interaction
}) {
  if (interaction.prepared.name !== 'drag') {
    return;
  }

  const axis = interaction.prepared.axis;

  if (axis === 'x') {
    interaction.coords.cur.page.y = interaction.coords.start.page.y;
    interaction.coords.cur.client.y = interaction.coords.start.client.y;
    interaction.coords.velocity.client.y = 0;
    interaction.coords.velocity.page.y = 0;
  } else if (axis === 'y') {
    interaction.coords.cur.page.x = interaction.coords.start.page.x;
    interaction.coords.cur.client.x = interaction.coords.start.client.x;
    interaction.coords.velocity.client.x = 0;
    interaction.coords.velocity.page.x = 0;
  }
}

function move({
  iEvent,
  interaction
}) {
  if (interaction.prepared.name !== 'drag') {
    return;
  }

  const axis = interaction.prepared.axis;

  if (axis === 'x' || axis === 'y') {
    const opposite = axis === 'x' ? 'y' : 'x';
    iEvent.page[opposite] = interaction.coords.start.page[opposite];
    iEvent.client[opposite] = interaction.coords.start.client[opposite];
    iEvent.delta[opposite] = 0;
  }
}
/**
 * ```js
 * interact(element).draggable({
 *     onstart: function (event) {},
 *     onmove : function (event) {},
 *     onend  : function (event) {},
 *
 *     // the axis in which the first movement must be
 *     // for the drag sequence to start
 *     // 'xy' by default - any direction
 *     startAxis: 'x' || 'y' || 'xy',
 *
 *     // 'xy' by default - don't restrict to one axis (move in any direction)
 *     // 'x' or 'y' to restrict movement to either axis
 *     // 'start' to restrict movement to the axis the drag started in
 *     lockAxis: 'x' || 'y' || 'xy' || 'start',
 *
 *     // max number of drags that can happen concurrently
 *     // with elements of this Interactable. Infinity by default
 *     max: Infinity,
 *
 *     // max number of drags that can target the same element+Interactable
 *     // 1 by default
 *     maxPerElement: 2
 * })
 *
 * var isDraggable = interact('element').draggable(); // true
 * ```
 *
 * Get or set whether drag actions can be performed on the target
 *
 * @alias Interactable.prototype.draggable
 *
 * @param {boolean | object} [options] true/false or An object with event
 * listeners to be fired on drag events (object makes the Interactable
 * draggable)
 * @return {boolean | Interactable} boolean indicating if this can be the
 * target of drag events, or this Interctable
 */


const plugin_draggable = function draggable(options) {
  if (is.object(options)) {
    this.options.drag.enabled = options.enabled !== false;
    this.setPerAction('drag', options);
    this.setOnEvents('drag', options);

    if (/^(xy|x|y|start)$/.test(options.lockAxis)) {
      this.options.drag.lockAxis = options.lockAxis;
    }

    if (/^(xy|x|y)$/.test(options.startAxis)) {
      this.options.drag.startAxis = options.startAxis;
    }

    return this;
  }

  if (is.bool(options)) {
    this.options.drag.enabled = options;
    return this;
  }

  return this.options.drag;
};

const drag = {
  id: 'actions/drag',
  install: drag_plugin_install,
  listeners: {
    'interactions:before-action-move': beforeMove,
    'interactions:action-resume': beforeMove,
    // dragmove
    'interactions:action-move': move,
    'auto-start:check': arg => {
      const {
        interaction,
        interactable,
        buttons
      } = arg;
      const dragOptions = interactable.options.drag;

      if (!(dragOptions && dragOptions.enabled) || // check mouseButton setting if the pointer is down
      interaction.pointerIsDown && /mouse|pointer/.test(interaction.pointerType) && (buttons & interactable.options.drag.mouseButtons) === 0) {
        return undefined;
      }

      arg.action = {
        name: 'drag',
        axis: dragOptions.lockAxis === 'start' ? dragOptions.startAxis : dragOptions.lockAxis
      };
      return false;
    }
  },
  draggable: plugin_draggable,
  beforeMove,
  move,
  defaults: {
    startAxis: 'xy',
    lockAxis: 'xy'
  },

  getCursor() {
    return 'move';
  }

};
/* harmony default export */ var drag_plugin = (drag);

// CONCATENATED MODULE: ./node_modules/@interactjs/actions/drag/index.js
/* eslint-disable import/order, no-console, eol-last */



if (typeof window === 'object' && !!window) {
  interact_init(window);
}

_interactjs_interact.use(drag_plugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/actions/resize/plugin.js






function resize_plugin_install(scope) {
  const {
    actions,
    browser,

    /** @lends Interactable */
    Interactable,
    // tslint:disable-line no-shadowed-variable
    defaults
  } = scope; // Less Precision with touch input

  resize.cursors = initCursors(browser);
  resize.defaultMargin = browser.supportsTouch || browser.supportsPointerEvent ? 20 : 10;
  /**
   * ```js
   * interact(element).resizable({
   *   onstart: function (event) {},
   *   onmove : function (event) {},
   *   onend  : function (event) {},
   *
   *   edges: {
   *     top   : true,       // Use pointer coords to check for resize.
   *     left  : false,      // Disable resizing from left edge.
   *     bottom: '.resize-s',// Resize if pointer target matches selector
   *     right : handleEl    // Resize if pointer target is the given Element
   *   },
   *
   *     // Width and height can be adjusted independently. When `true`, width and
   *     // height are adjusted at a 1:1 ratio.
   *     square: false,
   *
   *     // Width and height can be adjusted independently. When `true`, width and
   *     // height maintain the aspect ratio they had when resizing started.
   *     preserveAspectRatio: false,
   *
   *   // a value of 'none' will limit the resize rect to a minimum of 0x0
   *   // 'negate' will allow the rect to have negative width/height
   *   // 'reposition' will keep the width/height positive by swapping
   *   // the top and bottom edges and/or swapping the left and right edges
   *   invert: 'none' || 'negate' || 'reposition'
   *
   *   // limit multiple resizes.
   *   // See the explanation in the {@link Interactable.draggable} example
   *   max: Infinity,
   *   maxPerElement: 1,
   * })
   *
   * var isResizeable = interact(element).resizable()
   * ```
   *
   * Gets or sets whether resize actions can be performed on the target
   *
   * @param {boolean | object} [options] true/false or An object with event
   * listeners to be fired on resize events (object makes the Interactable
   * resizable)
   * @return {boolean | Interactable} A boolean indicating if this can be the
   * target of resize elements, or this Interactable
   */

  Interactable.prototype.resizable = function (options) {
    return resizable(this, options, scope);
  };

  actions.map.resize = resize;
  actions.methodDict.resize = 'resizable';
  defaults.actions.resize = resize.defaults;
}

function resizeChecker(arg) {
  const {
    interaction,
    interactable,
    element,
    rect,
    buttons
  } = arg;

  if (!rect) {
    return undefined;
  }

  const page = extend({}, interaction.coords.cur.page);
  const resizeOptions = interactable.options.resize;

  if (!(resizeOptions && resizeOptions.enabled) || // check mouseButton setting if the pointer is down
  interaction.pointerIsDown && /mouse|pointer/.test(interaction.pointerType) && (buttons & resizeOptions.mouseButtons) === 0) {
    return undefined;
  } // if using resize.edges


  if (is.object(resizeOptions.edges)) {
    const resizeEdges = {
      left: false,
      right: false,
      top: false,
      bottom: false
    };

    for (const edge in resizeEdges) {
      resizeEdges[edge] = checkResizeEdge(edge, resizeOptions.edges[edge], page, interaction._latestPointer.eventTarget, element, rect, resizeOptions.margin || resize.defaultMargin);
    }

    resizeEdges.left = resizeEdges.left && !resizeEdges.right;
    resizeEdges.top = resizeEdges.top && !resizeEdges.bottom;

    if (resizeEdges.left || resizeEdges.right || resizeEdges.top || resizeEdges.bottom) {
      arg.action = {
        name: 'resize',
        edges: resizeEdges
      };
    }
  } else {
    const right = resizeOptions.axis !== 'y' && page.x > rect.right - resize.defaultMargin;
    const bottom = resizeOptions.axis !== 'x' && page.y > rect.bottom - resize.defaultMargin;

    if (right || bottom) {
      arg.action = {
        name: 'resize',
        axes: (right ? 'x' : '') + (bottom ? 'y' : '')
      };
    }
  }

  return arg.action ? false : undefined;
}

function resizable(interactable, options, scope) {
  if (is.object(options)) {
    interactable.options.resize.enabled = options.enabled !== false;
    interactable.setPerAction('resize', options);
    interactable.setOnEvents('resize', options);

    if (is.string(options.axis) && /^x$|^y$|^xy$/.test(options.axis)) {
      interactable.options.resize.axis = options.axis;
    } else if (options.axis === null) {
      interactable.options.resize.axis = scope.defaults.actions.resize.axis;
    }

    if (is.bool(options.preserveAspectRatio)) {
      interactable.options.resize.preserveAspectRatio = options.preserveAspectRatio;
    } else if (is.bool(options.square)) {
      interactable.options.resize.square = options.square;
    }

    return interactable;
  }

  if (is.bool(options)) {
    interactable.options.resize.enabled = options;
    return interactable;
  }

  return interactable.options.resize;
}

function checkResizeEdge(name, value, page, element, interactableElement, rect, margin) {
  // false, '', undefined, null
  if (!value) {
    return false;
  } // true value, use pointer coords and element rect


  if (value === true) {
    // if dimensions are negative, "switch" edges
    const width = is.number(rect.width) ? rect.width : rect.right - rect.left;
    const height = is.number(rect.height) ? rect.height : rect.bottom - rect.top; // don't use margin greater than half the relevent dimension

    margin = Math.min(margin, Math.abs((name === 'left' || name === 'right' ? width : height) / 2));

    if (width < 0) {
      if (name === 'left') {
        name = 'right';
      } else if (name === 'right') {
        name = 'left';
      }
    }

    if (height < 0) {
      if (name === 'top') {
        name = 'bottom';
      } else if (name === 'bottom') {
        name = 'top';
      }
    }

    if (name === 'left') {
      return page.x < (width >= 0 ? rect.left : rect.right) + margin;
    }

    if (name === 'top') {
      return page.y < (height >= 0 ? rect.top : rect.bottom) + margin;
    }

    if (name === 'right') {
      return page.x > (width >= 0 ? rect.right : rect.left) - margin;
    }

    if (name === 'bottom') {
      return page.y > (height >= 0 ? rect.bottom : rect.top) - margin;
    }
  } // the remaining checks require an element


  if (!is.element(element)) {
    return false;
  }

  return is.element(value) // the value is an element to use as a resize handle
  ? value === element // otherwise check if element matches value as selector
  : matchesUpTo(element, value, interactableElement);
}
/* eslint-disable multiline-ternary */


function initCursors(browser) {
  return browser.isIe9 ? {
    x: 'e-resize',
    y: 's-resize',
    xy: 'se-resize',
    top: 'n-resize',
    left: 'w-resize',
    bottom: 's-resize',
    right: 'e-resize',
    topleft: 'se-resize',
    bottomright: 'se-resize',
    topright: 'ne-resize',
    bottomleft: 'ne-resize'
  } : {
    x: 'ew-resize',
    y: 'ns-resize',
    xy: 'nwse-resize',
    top: 'ns-resize',
    left: 'ew-resize',
    bottom: 'ns-resize',
    right: 'ew-resize',
    topleft: 'nwse-resize',
    bottomright: 'nwse-resize',
    topright: 'nesw-resize',
    bottomleft: 'nesw-resize'
  };
}
/* eslint-enable multiline-ternary */


function start({
  iEvent,
  interaction
}) {
  if (interaction.prepared.name !== 'resize' || !interaction.prepared.edges) {
    return;
  }

  const resizeEvent = iEvent;
  const rect = interaction.rect;
  interaction._rects = {
    start: extend({}, rect),
    corrected: extend({}, rect),
    previous: extend({}, rect),
    delta: {
      left: 0,
      right: 0,
      width: 0,
      top: 0,
      bottom: 0,
      height: 0
    }
  };
  resizeEvent.edges = interaction.prepared.edges;
  resizeEvent.rect = interaction._rects.corrected;
  resizeEvent.deltaRect = interaction._rects.delta;
}

function plugin_move({
  iEvent,
  interaction
}) {
  if (interaction.prepared.name !== 'resize' || !interaction.prepared.edges) {
    return;
  }

  const resizeEvent = iEvent;
  const resizeOptions = interaction.interactable.options.resize;
  const invert = resizeOptions.invert;
  const invertible = invert === 'reposition' || invert === 'negate';
  const current = interaction.rect;
  const {
    start: startRect,
    corrected,
    delta: deltaRect,
    previous
  } = interaction._rects;
  extend(previous, corrected);

  if (invertible) {
    // if invertible, copy the current rect
    extend(corrected, current);

    if (invert === 'reposition') {
      // swap edge values if necessary to keep width/height positive
      if (corrected.top > corrected.bottom) {
        const swap = corrected.top;
        corrected.top = corrected.bottom;
        corrected.bottom = swap;
      }

      if (corrected.left > corrected.right) {
        const swap = corrected.left;
        corrected.left = corrected.right;
        corrected.right = swap;
      }
    }
  } else {
    // if not invertible, restrict to minimum of 0x0 rect
    corrected.top = Math.min(current.top, startRect.bottom);
    corrected.bottom = Math.max(current.bottom, startRect.top);
    corrected.left = Math.min(current.left, startRect.right);
    corrected.right = Math.max(current.right, startRect.left);
  }

  corrected.width = corrected.right - corrected.left;
  corrected.height = corrected.bottom - corrected.top;

  for (const edge in corrected) {
    deltaRect[edge] = corrected[edge] - previous[edge];
  }

  resizeEvent.edges = interaction.prepared.edges;
  resizeEvent.rect = corrected;
  resizeEvent.deltaRect = deltaRect;
}

function end({
  iEvent,
  interaction
}) {
  if (interaction.prepared.name !== 'resize' || !interaction.prepared.edges) {
    return;
  }

  const resizeEvent = iEvent;
  resizeEvent.edges = interaction.prepared.edges;
  resizeEvent.rect = interaction._rects.corrected;
  resizeEvent.deltaRect = interaction._rects.delta;
}

function updateEventAxes({
  iEvent,
  interaction
}) {
  if (interaction.prepared.name !== 'resize' || !interaction.resizeAxes) {
    return;
  }

  const options = interaction.interactable.options;
  const resizeEvent = iEvent;

  if (options.resize.square) {
    if (interaction.resizeAxes === 'y') {
      resizeEvent.delta.x = resizeEvent.delta.y;
    } else {
      resizeEvent.delta.y = resizeEvent.delta.x;
    }

    resizeEvent.axes = 'xy';
  } else {
    resizeEvent.axes = interaction.resizeAxes;

    if (interaction.resizeAxes === 'x') {
      resizeEvent.delta.y = 0;
    } else if (interaction.resizeAxes === 'y') {
      resizeEvent.delta.x = 0;
    }
  }
}

const resize = {
  id: 'actions/resize',
  before: ['actions/drag'],
  install: resize_plugin_install,
  listeners: {
    'interactions:new': ({
      interaction
    }) => {
      interaction.resizeAxes = 'xy';
    },
    'interactions:action-start': arg => {
      start(arg);
      updateEventAxes(arg);
    },
    'interactions:action-move': arg => {
      plugin_move(arg);
      updateEventAxes(arg);
    },
    'interactions:action-end': end,
    'auto-start:check': resizeChecker
  },
  defaults: {
    square: false,
    preserveAspectRatio: false,
    axis: 'xy',
    // use default margin
    margin: NaN,
    // object with props left, right, top, bottom which are
    // true/false values to resize when the pointer is over that edge,
    // CSS selectors to match the handles for each direction
    // or the Elements for each handle
    edges: null,
    // a value of 'none' will limit the resize rect to a minimum of 0x0
    // 'negate' will alow the rect to have negative width/height
    // 'reposition' will keep the width/height positive by swapping
    // the top and bottom edges and/or swapping the left and right edges
    invert: 'none'
  },
  cursors: null,

  getCursor({
    edges,
    axis,
    name
  }) {
    const cursors = resize.cursors;
    let result = null;

    if (axis) {
      result = cursors[name + axis];
    } else if (edges) {
      let cursorKey = '';

      for (const edge of ['top', 'bottom', 'left', 'right']) {
        if (edges[edge]) {
          cursorKey += edge;
        }
      }

      result = cursors[cursorKey];
    }

    return result;
  },

  defaultMargin: null
};
/* harmony default export */ var resize_plugin = (resize);

// CONCATENATED MODULE: ./node_modules/@interactjs/actions/resize/index.js
/* eslint-disable import/order, no-console, eol-last */



if (typeof window === 'object' && !!window) {
  interact_init(window);
}

_interactjs_interact.use(resize_plugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/snappers/edgeTarget.js
/* harmony default export */ var edgeTarget = (() => {});

// CONCATENATED MODULE: ./node_modules/@interactjs/snappers/elements.js
/* harmony default export */ var snappers_elements = (() => {});

// CONCATENATED MODULE: ./node_modules/@interactjs/snappers/grid.js
/* harmony default export */ var grid = (grid => {
  const coordFields = [['x', 'y'], ['left', 'top'], ['right', 'bottom'], ['width', 'height']].filter(([xField, yField]) => xField in grid || yField in grid);

  const gridFunc = (x, y) => {
    const {
      range,
      limits = {
        left: -Infinity,
        right: Infinity,
        top: -Infinity,
        bottom: Infinity
      },
      offset = {
        x: 0,
        y: 0
      }
    } = grid;
    const result = {
      range,
      grid,
      x: null,
      y: null
    };

    for (const [xField, yField] of coordFields) {
      const gridx = Math.round((x - offset.x) / grid[xField]);
      const gridy = Math.round((y - offset.y) / grid[yField]);
      result[xField] = Math.max(limits.left, Math.min(limits.right, gridx * grid[xField] + offset.x));
      result[yField] = Math.max(limits.top, Math.min(limits.bottom, gridy * grid[yField] + offset.y));
    }

    return result;
  };

  gridFunc.grid = grid;
  gridFunc.coordFields = coordFields;
  return gridFunc;
});

// CONCATENATED MODULE: ./node_modules/@interactjs/snappers/all.js




// CONCATENATED MODULE: ./node_modules/@interactjs/snappers/plugin.js


const snappersPlugin = {
  id: 'snappers',

  install(scope) {
    const {
      interactStatic: interact
    } = scope;
    interact.snappers = extend(interact.snappers || {}, all_namespaceObject);
    interact.createSnapGrid = interact.snappers.grid;
  }

};
/* harmony default export */ var snappers_plugin = (snappersPlugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/Modification.js



class Modification_Modification {
  constructor(interaction) {
    this.states = [];
    this.startOffset = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    };
    this.startDelta = null;
    this.result = null;
    this.endResult = null;
    this.edges = void 0;
    this.interaction = void 0;
    this.interaction = interaction;
    this.result = createResult();
  }

  start({
    phase
  }, pageCoords) {
    const {
      interaction
    } = this;
    const modifierList = getModifierList(interaction);
    this.prepareStates(modifierList);
    this.edges = extend({}, interaction.edges);
    this.startOffset = getRectOffset(interaction.rect, pageCoords);
    this.startDelta = {
      x: 0,
      y: 0
    };
    const arg = {
      phase,
      pageCoords,
      preEnd: false
    };
    this.result = createResult();
    this.startAll(arg);
    const result = this.result = this.setAll(arg);
    return result;
  }

  fillArg(arg) {
    const {
      interaction
    } = this;
    arg.interaction = interaction;
    arg.interactable = interaction.interactable;
    arg.element = interaction.element;
    arg.rect = arg.rect || interaction.rect;
    arg.edges = this.edges;
    arg.startOffset = this.startOffset;
  }

  startAll(arg) {
    this.fillArg(arg);

    for (const state of this.states) {
      if (state.methods.start) {
        arg.state = state;
        state.methods.start(arg);
      }
    }
  }

  setAll(arg) {
    this.fillArg(arg);
    const {
      phase,
      preEnd,
      skipModifiers,
      rect: unmodifiedRect
    } = arg;
    arg.coords = extend({}, arg.pageCoords);
    arg.rect = extend({}, unmodifiedRect);
    const states = skipModifiers ? this.states.slice(skipModifiers) : this.states;
    const newResult = createResult(arg.coords, arg.rect);

    for (const state of states) {
      const {
        options
      } = state;
      const lastModifierCoords = extend({}, arg.coords);
      let returnValue = null;

      if (state.methods.set && this.shouldDo(options, preEnd, phase)) {
        arg.state = state;
        returnValue = state.methods.set(arg);
        addEdges(this.interaction.edges, arg.rect, {
          x: arg.coords.x - lastModifierCoords.x,
          y: arg.coords.y - lastModifierCoords.y
        });
      }

      newResult.eventProps.push(returnValue);
    }

    newResult.delta.x = arg.coords.x - arg.pageCoords.x;
    newResult.delta.y = arg.coords.y - arg.pageCoords.y;
    newResult.rectDelta.left = arg.rect.left - unmodifiedRect.left;
    newResult.rectDelta.right = arg.rect.right - unmodifiedRect.right;
    newResult.rectDelta.top = arg.rect.top - unmodifiedRect.top;
    newResult.rectDelta.bottom = arg.rect.bottom - unmodifiedRect.bottom;
    const prevCoords = this.result.coords;
    const prevRect = this.result.rect;

    if (prevCoords && prevRect) {
      const rectChanged = newResult.rect.left !== prevRect.left || newResult.rect.right !== prevRect.right || newResult.rect.top !== prevRect.top || newResult.rect.bottom !== prevRect.bottom;
      newResult.changed = rectChanged || prevCoords.x !== newResult.coords.x || prevCoords.y !== newResult.coords.y;
    }

    return newResult;
  }

  applyToInteraction(arg) {
    const {
      interaction
    } = this;
    const {
      phase
    } = arg;
    const curCoords = interaction.coords.cur;
    const startCoords = interaction.coords.start;
    const {
      result,
      startDelta
    } = this;
    const curDelta = result.delta;

    if (phase === 'start') {
      extend(this.startDelta, result.delta);
    }

    for (const [coordsSet, delta] of [[startCoords, startDelta], [curCoords, curDelta]]) {
      coordsSet.page.x += delta.x;
      coordsSet.page.y += delta.y;
      coordsSet.client.x += delta.x;
      coordsSet.client.y += delta.y;
    }

    const {
      rectDelta
    } = this.result;
    const rect = arg.rect || interaction.rect;
    rect.left += rectDelta.left;
    rect.right += rectDelta.right;
    rect.top += rectDelta.top;
    rect.bottom += rectDelta.bottom;
    rect.width = rect.right - rect.left;
    rect.height = rect.bottom - rect.top;
  }

  setAndApply(arg) {
    const {
      interaction
    } = this;
    const {
      phase,
      preEnd,
      skipModifiers
    } = arg;
    const result = this.setAll({
      preEnd,
      phase,
      pageCoords: arg.modifiedCoords || interaction.coords.cur.page
    });
    this.result = result; // don't fire an action move if a modifier would keep the event in the same
    // cordinates as before

    if (!result.changed && (!skipModifiers || skipModifiers < this.states.length) && interaction.interacting()) {
      return false;
    }

    if (arg.modifiedCoords) {
      const {
        page
      } = interaction.coords.cur;
      const adjustment = {
        x: arg.modifiedCoords.x - page.x,
        y: arg.modifiedCoords.y - page.y
      };
      result.coords.x += adjustment.x;
      result.coords.y += adjustment.y;
      result.delta.x += adjustment.x;
      result.delta.y += adjustment.y;
    }

    this.applyToInteraction(arg);
  }

  beforeEnd(arg) {
    const {
      interaction,
      event
    } = arg;
    const states = this.states;

    if (!states || !states.length) {
      return;
    }

    let doPreend = false;

    for (const state of states) {
      arg.state = state;
      const {
        options,
        methods
      } = state;
      const endPosition = methods.beforeEnd && methods.beforeEnd(arg);

      if (endPosition) {
        this.endResult = endPosition;
        return false;
      }

      doPreend = doPreend || !doPreend && this.shouldDo(options, true, arg.phase, true);
    }

    if (doPreend) {
      // trigger a final modified move before ending
      interaction.move({
        event,
        preEnd: true
      });
    }
  }

  stop(arg) {
    const {
      interaction
    } = arg;

    if (!this.states || !this.states.length) {
      return;
    }

    const modifierArg = extend({
      states: this.states,
      interactable: interaction.interactable,
      element: interaction.element,
      rect: null
    }, arg);
    this.fillArg(modifierArg);

    for (const state of this.states) {
      modifierArg.state = state;

      if (state.methods.stop) {
        state.methods.stop(modifierArg);
      }
    }

    this.states = null;
    this.endResult = null;
  }

  prepareStates(modifierList) {
    this.states = [];

    for (let index = 0; index < modifierList.length; index++) {
      const {
        options,
        methods,
        name
      } = modifierList[index];
      this.states.push({
        options,
        methods,
        index,
        name
      });
    }

    return this.states;
  }

  restoreInteractionCoords({
    interaction: {
      coords,
      rect,
      modification
    }
  }) {
    if (!modification.result) {
      return;
    }

    const {
      startDelta
    } = modification;
    const {
      delta: curDelta,
      rectDelta
    } = modification.result;
    const coordsAndDeltas = [[coords.start, startDelta], [coords.cur, curDelta]];

    for (const [coordsSet, delta] of coordsAndDeltas) {
      coordsSet.page.x -= delta.x;
      coordsSet.page.y -= delta.y;
      coordsSet.client.x -= delta.x;
      coordsSet.client.y -= delta.y;
    }

    rect.left -= rectDelta.left;
    rect.right -= rectDelta.right;
    rect.top -= rectDelta.top;
    rect.bottom -= rectDelta.bottom;
  }

  shouldDo(options, preEnd, phase, requireEndOnly) {
    if ( // ignore disabled modifiers
    !options || options.enabled === false || // check if we require endOnly option to fire move before end
    requireEndOnly && !options.endOnly || // don't apply endOnly modifiers when not ending
    options.endOnly && !preEnd || // check if modifier should run be applied on start
    phase === 'start' && !options.setStart) {
      return false;
    }

    return true;
  }

  copyFrom(other) {
    this.startOffset = other.startOffset;
    this.startDelta = other.startDelta;
    this.edges = other.edges;
    this.states = other.states.map(s => clone(s));
    this.result = createResult(extend({}, other.result.coords), extend({}, other.result.rect));
  }

  destroy() {
    for (const prop in this) {
      this[prop] = null;
    }
  }

}

function createResult(coords, rect) {
  return {
    rect,
    coords,
    delta: {
      x: 0,
      y: 0
    },
    rectDelta: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    },
    eventProps: [],
    changed: true
  };
}

function getModifierList(interaction) {
  const actionOptions = interaction.interactable.options[interaction.prepared.name];
  const actionModifiers = actionOptions.modifiers;

  if (actionModifiers && actionModifiers.length) {
    return actionModifiers;
  }

  return ['snap', 'snapSize', 'snapEdges', 'restrict', 'restrictEdges', 'restrictSize'].map(type => {
    const options = actionOptions[type];
    return options && options.enabled && {
      options,
      methods: options._methods
    };
  }).filter(m => !!m);
}

function getRectOffset(rect, coords) {
  return rect ? {
    left: coords.x - rect.left,
    top: coords.y - rect.top,
    right: rect.right - coords.x,
    bottom: rect.bottom - coords.y
  } : {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  };
}

// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/base.js



function makeModifier(module, name) {
  const {
    defaults
  } = module;
  const methods = {
    start: module.start,
    set: module.set,
    beforeEnd: module.beforeEnd,
    stop: module.stop
  };

  const modifier = _options => {
    const options = _options || {};
    options.enabled = options.enabled !== false; // add missing defaults to options

    for (const prop in defaults) {
      if (!(prop in options)) {
        options[prop] = defaults[prop];
      }
    }

    const m = {
      options,
      methods,
      name,
      enable: () => {
        options.enabled = true;
        return m;
      },
      disable: () => {
        options.enabled = false;
        return m;
      }
    };
    return m;
  };

  if (name && typeof name === 'string') {
    // for backwrads compatibility
    modifier._defaults = defaults;
    modifier._methods = methods;
  }

  return modifier;
}
function addEventModifiers({
  iEvent,
  interaction: {
    modification: {
      result
    }
  }
}) {
  if (result) {
    iEvent.modifiers = result.eventProps;
  }
}
const modifiersBase = {
  id: 'modifiers/base',
  before: ['actions'],
  install: scope => {
    scope.defaults.perAction.modifiers = [];
  },
  listeners: {
    'interactions:new': ({
      interaction
    }) => {
      interaction.modification = new Modification_Modification(interaction);
    },
    'interactions:before-action-start': arg => {
      const {
        modification
      } = arg.interaction;
      modification.start(arg, arg.interaction.coords.start.page);
      arg.interaction.edges = modification.edges;
      modification.applyToInteraction(arg);
    },
    'interactions:before-action-move': arg => arg.interaction.modification.setAndApply(arg),
    'interactions:before-action-end': arg => arg.interaction.modification.beforeEnd(arg),
    'interactions:action-start': addEventModifiers,
    'interactions:action-move': addEventModifiers,
    'interactions:action-end': addEventModifiers,
    'interactions:after-action-start': arg => arg.interaction.modification.restoreInteractionCoords(arg),
    'interactions:after-action-move': arg => arg.interaction.modification.restoreInteractionCoords(arg),
    'interactions:stop': arg => arg.interaction.modification.stop(arg)
  }
};
/* harmony default export */ var modifiers_base = (modifiersBase);

// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/aspectRatio.js
/**
 * @module modifiers/aspectRatio
 *
 * @description
 * This module forces elements to be resized with a specified dx/dy ratio.
 *
 * ```js
 * interact(target).resizable({
 *   modifiers: [
 *     interact.modifiers.snapSize({
 *       targets: [ interact.snappers.grid({ x: 20, y: 20 }) ],
 *     }),
 *     interact.aspectRatio({ ratio: 'preserve' }),
 *   ],
 * });
 * ```
 */




const aspectRatio = {
  start(arg) {
    const {
      state,
      rect,
      edges: originalEdges,
      pageCoords: coords
    } = arg;
    let {
      ratio
    } = state.options;
    const {
      equalDelta,
      modifiers
    } = state.options;

    if (ratio === 'preserve') {
      ratio = rect.width / rect.height;
    }

    state.startCoords = extend({}, coords);
    state.startRect = extend({}, rect);
    state.ratio = ratio;
    state.equalDelta = equalDelta;
    const linkedEdges = state.linkedEdges = {
      top: originalEdges.top || originalEdges.left && !originalEdges.bottom,
      left: originalEdges.left || originalEdges.top && !originalEdges.right,
      bottom: originalEdges.bottom || originalEdges.right && !originalEdges.top,
      right: originalEdges.right || originalEdges.bottom && !originalEdges.left
    };
    state.xIsPrimaryAxis = !!(originalEdges.left || originalEdges.right);

    if (state.equalDelta) {
      state.edgeSign = (linkedEdges.left ? 1 : -1) * (linkedEdges.top ? 1 : -1);
    } else {
      const negativeSecondaryEdge = state.xIsPrimaryAxis ? linkedEdges.top : linkedEdges.left;
      state.edgeSign = negativeSecondaryEdge ? -1 : 1;
    }

    extend(arg.edges, linkedEdges);

    if (!modifiers || !modifiers.length) {
      return;
    }

    const subModification = new Modification_Modification(arg.interaction);
    subModification.copyFrom(arg.interaction.modification);
    subModification.prepareStates(modifiers);
    state.subModification = subModification;
    subModification.startAll({ ...arg
    });
  },

  set(arg) {
    const {
      state,
      rect,
      coords
    } = arg;
    const initialCoords = extend({}, coords);
    const aspectMethod = state.equalDelta ? setEqualDelta : setRatio;
    aspectMethod(state, state.xIsPrimaryAxis, coords, rect);

    if (!state.subModification) {
      return null;
    }

    const correctedRect = extend({}, rect);
    addEdges(state.linkedEdges, correctedRect, {
      x: coords.x - initialCoords.x,
      y: coords.y - initialCoords.y
    });
    const result = state.subModification.setAll({ ...arg,
      rect: correctedRect,
      edges: state.linkedEdges,
      pageCoords: coords,
      prevCoords: coords,
      prevRect: correctedRect
    });
    const {
      delta
    } = result;

    if (result.changed) {
      const xIsCriticalAxis = Math.abs(delta.x) > Math.abs(delta.y); // do aspect modification again with critical edge axis as primary

      aspectMethod(state, xIsCriticalAxis, result.coords, result.rect);
      extend(coords, result.coords);
    }

    return result.eventProps;
  },

  defaults: {
    ratio: 'preserve',
    equalDelta: false,
    modifiers: [],
    enabled: false
  }
};

function setEqualDelta({
  startCoords,
  edgeSign
}, xIsPrimaryAxis, coords) {
  if (xIsPrimaryAxis) {
    coords.y = startCoords.y + (coords.x - startCoords.x) * edgeSign;
  } else {
    coords.x = startCoords.x + (coords.y - startCoords.y) * edgeSign;
  }
}

function setRatio({
  startRect,
  startCoords,
  ratio,
  edgeSign
}, xIsPrimaryAxis, coords, rect) {
  if (xIsPrimaryAxis) {
    const newHeight = rect.width / ratio;
    coords.y = startCoords.y + (newHeight - startRect.height) * edgeSign;
  } else {
    const newWidth = rect.height * ratio;
    coords.x = startCoords.x + (newWidth - startRect.width) * edgeSign;
  }
}

/* harmony default export */ var modifiers_aspectRatio = (makeModifier(aspectRatio, 'aspectRatio'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/noop.js
const noop = () => {};

noop._defaults = {};
/* harmony default export */ var modifiers_noop = (noop);

// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/restrict/pointer.js





function pointer_start({
  rect,
  startOffset,
  state,
  interaction,
  pageCoords
}) {
  const {
    options
  } = state;
  const {
    elementRect
  } = options;
  const offset = extend({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0
  }, options.offset || {});

  if (rect && elementRect) {
    const restriction = getRestrictionRect(options.restriction, interaction, pageCoords);

    if (restriction) {
      const widthDiff = restriction.right - restriction.left - rect.width;
      const heightDiff = restriction.bottom - restriction.top - rect.height;

      if (widthDiff < 0) {
        offset.left += widthDiff;
        offset.right += widthDiff;
      }

      if (heightDiff < 0) {
        offset.top += heightDiff;
        offset.bottom += heightDiff;
      }
    }

    offset.left += startOffset.left - rect.width * elementRect.left;
    offset.top += startOffset.top - rect.height * elementRect.top;
    offset.right += startOffset.right - rect.width * (1 - elementRect.right);
    offset.bottom += startOffset.bottom - rect.height * (1 - elementRect.bottom);
  }

  state.offset = offset;
}

function set({
  coords,
  interaction,
  state
}) {
  const {
    options,
    offset
  } = state;
  const restriction = getRestrictionRect(options.restriction, interaction, coords);

  if (!restriction) {
    return;
  }

  const rect = xywhToTlbr(restriction);
  coords.x = Math.max(Math.min(rect.right - offset.right, coords.x), rect.left + offset.left);
  coords.y = Math.max(Math.min(rect.bottom - offset.bottom, coords.y), rect.top + offset.top);
}

function getRestrictionRect(value, interaction, coords) {
  if (is.func(value)) {
    return resolveRectLike(value, interaction.interactable, interaction.element, [coords.x, coords.y, interaction]);
  } else {
    return resolveRectLike(value, interaction.interactable, interaction.element);
  }
}
const pointer_defaults = {
  restriction: null,
  elementRect: null,
  offset: null,
  endOnly: false,
  enabled: false
};
const restrict = {
  start: pointer_start,
  set,
  defaults: pointer_defaults
};
/* harmony default export */ var restrict_pointer = (makeModifier(restrict, 'restrict'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/restrict/edges.js
// This module adds the options.resize.restrictEdges setting which sets min and
// max for the top, left, bottom and right edges of the target being resized.
//
// interact(target).resize({
//   edges: { top: true, left: true },
//   restrictEdges: {
//     inner: { top: 200, left: 200, right: 400, bottom: 400 },
//     outer: { top:   0, left:   0, right: 600, bottom: 600 },
//   },
// })




const noInner = {
  top: +Infinity,
  left: +Infinity,
  bottom: -Infinity,
  right: -Infinity
};
const noOuter = {
  top: -Infinity,
  left: -Infinity,
  bottom: +Infinity,
  right: +Infinity
};

function edges_start({
  interaction,
  startOffset,
  state
}) {
  const {
    options
  } = state;
  let offset;

  if (options) {
    const offsetRect = getRestrictionRect(options.offset, interaction, interaction.coords.start.page);
    offset = rectToXY(offsetRect);
  }

  offset = offset || {
    x: 0,
    y: 0
  };
  state.offset = {
    top: offset.y + startOffset.top,
    left: offset.x + startOffset.left,
    bottom: offset.y - startOffset.bottom,
    right: offset.x - startOffset.right
  };
}

function edges_set({
  coords,
  edges,
  interaction,
  state
}) {
  const {
    offset,
    options
  } = state;

  if (!edges) {
    return;
  }

  const page = extend({}, coords);
  const inner = getRestrictionRect(options.inner, interaction, page) || {};
  const outer = getRestrictionRect(options.outer, interaction, page) || {};
  fixRect(inner, noInner);
  fixRect(outer, noOuter);

  if (edges.top) {
    coords.y = Math.min(Math.max(outer.top + offset.top, page.y), inner.top + offset.top);
  } else if (edges.bottom) {
    coords.y = Math.max(Math.min(outer.bottom + offset.bottom, page.y), inner.bottom + offset.bottom);
  }

  if (edges.left) {
    coords.x = Math.min(Math.max(outer.left + offset.left, page.x), inner.left + offset.left);
  } else if (edges.right) {
    coords.x = Math.max(Math.min(outer.right + offset.right, page.x), inner.right + offset.right);
  }
}

function fixRect(rect, defaults) {
  for (const edge of ['top', 'left', 'bottom', 'right']) {
    if (!(edge in rect)) {
      rect[edge] = defaults[edge];
    }
  }

  return rect;
}

const edges_defaults = {
  inner: null,
  outer: null,
  offset: null,
  endOnly: false,
  enabled: false
};
const restrictEdges = {
  noInner,
  noOuter,
  start: edges_start,
  set: edges_set,
  defaults: edges_defaults
};
/* harmony default export */ var restrict_edges = (makeModifier(restrictEdges, 'restrictEdges'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/restrict/rect.js



const rect_defaults = extend({
  get elementRect() {
    return {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1
    };
  },

  set elementRect(_) {}

}, restrict.defaults);
const restrictRect = {
  start: restrict.start,
  set: restrict.set,
  defaults: rect_defaults
};
/* harmony default export */ var restrict_rect = (makeModifier(restrictRect, 'restrictRect'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/restrict/size.js





const noMin = {
  width: -Infinity,
  height: -Infinity
};
const noMax = {
  width: +Infinity,
  height: +Infinity
};

function size_start(arg) {
  return restrictEdges.start(arg);
}

function size_set(arg) {
  const {
    interaction,
    state,
    rect,
    edges
  } = arg;
  const {
    options
  } = state;

  if (!edges) {
    return;
  }

  const minSize = tlbrToXywh(getRestrictionRect(options.min, interaction, arg.coords)) || noMin;
  const maxSize = tlbrToXywh(getRestrictionRect(options.max, interaction, arg.coords)) || noMax;
  state.options = {
    endOnly: options.endOnly,
    inner: extend({}, restrictEdges.noInner),
    outer: extend({}, restrictEdges.noOuter)
  };

  if (edges.top) {
    state.options.inner.top = rect.bottom - minSize.height;
    state.options.outer.top = rect.bottom - maxSize.height;
  } else if (edges.bottom) {
    state.options.inner.bottom = rect.top + minSize.height;
    state.options.outer.bottom = rect.top + maxSize.height;
  }

  if (edges.left) {
    state.options.inner.left = rect.right - minSize.width;
    state.options.outer.left = rect.right - maxSize.width;
  } else if (edges.right) {
    state.options.inner.right = rect.left + minSize.width;
    state.options.outer.right = rect.left + maxSize.width;
  }

  restrictEdges.set(arg);
  state.options = options;
}

const size_defaults = {
  min: null,
  max: null,
  endOnly: false,
  enabled: false
};
const restrictSize = {
  start: size_start,
  set: size_set,
  defaults: size_defaults
};
/* harmony default export */ var size = (makeModifier(restrictSize, 'restrictSize'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/snap/pointer.js







function snap_pointer_start(arg) {
  const {
    interaction,
    interactable,
    element,
    rect,
    state,
    startOffset
  } = arg;
  const {
    options
  } = state;
  const origin = options.offsetWithOrigin ? getOrigin(arg) : {
    x: 0,
    y: 0
  };
  let snapOffset;

  if (options.offset === 'startCoords') {
    snapOffset = {
      x: interaction.coords.start.page.x,
      y: interaction.coords.start.page.y
    };
  } else {
    const offsetRect = resolveRectLike(options.offset, interactable, element, [interaction]);
    snapOffset = rectToXY(offsetRect) || {
      x: 0,
      y: 0
    };
    snapOffset.x += origin.x;
    snapOffset.y += origin.y;
  }

  const {
    relativePoints
  } = options;
  state.offsets = rect && relativePoints && relativePoints.length ? relativePoints.map((relativePoint, index) => ({
    index,
    relativePoint,
    x: startOffset.left - rect.width * relativePoint.x + snapOffset.x,
    y: startOffset.top - rect.height * relativePoint.y + snapOffset.y
  })) : [extend({
    index: 0,
    relativePoint: null
  }, snapOffset)];
}

function pointer_set(arg) {
  const {
    interaction,
    coords,
    state
  } = arg;
  const {
    options,
    offsets
  } = state;
  const origin = getOriginXY(interaction.interactable, interaction.element, interaction.prepared.name);
  const page = extend({}, coords);
  const targets = [];

  if (!options.offsetWithOrigin) {
    page.x -= origin.x;
    page.y -= origin.y;
  }

  for (const offset of offsets) {
    const relativeX = page.x - offset.x;
    const relativeY = page.y - offset.y;

    for (let index = 0, len = options.targets.length; index < len; index++) {
      const snapTarget = options.targets[index];
      let target;

      if (is.func(snapTarget)) {
        target = snapTarget(relativeX, relativeY, interaction._proxy, offset, index);
      } else {
        target = snapTarget;
      }

      if (!target) {
        continue;
      }

      targets.push({
        x: (is.number(target.x) ? target.x : relativeX) + offset.x,
        y: (is.number(target.y) ? target.y : relativeY) + offset.y,
        range: is.number(target.range) ? target.range : options.range,
        source: snapTarget,
        index,
        offset
      });
    }
  }

  const closest = {
    target: null,
    inRange: false,
    distance: 0,
    range: 0,
    delta: {
      x: 0,
      y: 0
    }
  };

  for (const target of targets) {
    const range = target.range;
    const dx = target.x - page.x;
    const dy = target.y - page.y;
    const distance = hypot(dx, dy);
    let inRange = distance <= range; // Infinite targets count as being out of range
    // compared to non infinite ones that are in range

    if (range === Infinity && closest.inRange && closest.range !== Infinity) {
      inRange = false;
    }

    if (!closest.target || (inRange // is the closest target in range?
    ? closest.inRange && range !== Infinity // the pointer is relatively deeper in this target
    ? distance / range < closest.distance / closest.range // this target has Infinite range and the closest doesn't
    : range === Infinity && closest.range !== Infinity || // OR this target is closer that the previous closest
    distance < closest.distance : // The other is not in range and the pointer is closer to this target
    !closest.inRange && distance < closest.distance)) {
      closest.target = target;
      closest.distance = distance;
      closest.range = range;
      closest.inRange = inRange;
      closest.delta.x = dx;
      closest.delta.y = dy;
    }
  }

  if (closest.inRange) {
    coords.x = closest.target.x;
    coords.y = closest.target.y;
  }

  state.closest = closest;
  return closest;
}

function getOrigin(arg) {
  const {
    element
  } = arg.interaction;
  const optionsOrigin = rectToXY(resolveRectLike(arg.state.options.origin, null, null, [element]));
  const origin = optionsOrigin || getOriginXY(arg.interactable, element, arg.interaction.prepared.name);
  return origin;
}

const snap_pointer_defaults = {
  range: Infinity,
  targets: null,
  offset: null,
  offsetWithOrigin: true,
  origin: null,
  relativePoints: null,
  endOnly: false,
  enabled: false
};
const snap = {
  start: snap_pointer_start,
  set: pointer_set,
  defaults: snap_pointer_defaults
};
/* harmony default export */ var snap_pointer = (makeModifier(snap, 'snap'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/snap/size.js
// This module allows snapping of the size of targets during resize
// interactions.





function snap_size_start(arg) {
  const {
    state,
    edges
  } = arg;
  const {
    options
  } = state;

  if (!edges) {
    return null;
  }

  arg.state = {
    options: {
      targets: null,
      relativePoints: [{
        x: edges.left ? 0 : 1,
        y: edges.top ? 0 : 1
      }],
      offset: options.offset || 'self',
      origin: {
        x: 0,
        y: 0
      },
      range: options.range
    }
  };
  state.targetFields = state.targetFields || [['width', 'height'], ['x', 'y']];
  snap.start(arg);
  state.offsets = arg.state.offsets;
  arg.state = state;
}

function snap_size_set(arg) {
  const {
    interaction,
    state,
    coords
  } = arg;
  const {
    options,
    offsets
  } = state;
  const relative = {
    x: coords.x - offsets[0].x,
    y: coords.y - offsets[0].y
  };
  state.options = extend({}, options);
  state.options.targets = [];

  for (const snapTarget of options.targets || []) {
    let target;

    if (is.func(snapTarget)) {
      target = snapTarget(relative.x, relative.y, interaction);
    } else {
      target = snapTarget;
    }

    if (!target) {
      continue;
    }

    for (const [xField, yField] of state.targetFields) {
      if (xField in target || yField in target) {
        target.x = target[xField];
        target.y = target[yField];
        break;
      }
    }

    state.options.targets.push(target);
  }

  const returnValue = snap.set(arg);
  state.options = options;
  return returnValue;
}

const snap_size_defaults = {
  range: Infinity,
  targets: null,
  offset: null,
  endOnly: false,
  enabled: false
};
const snapSize = {
  start: snap_size_start,
  set: snap_size_set,
  defaults: snap_size_defaults
};
/* harmony default export */ var snap_size = (makeModifier(snapSize, 'snapSize'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/snap/edges.js
/**
 * @module modifiers/snapEdges
 *
 * @description
 * WOW> This module allows snapping of the edges of targets during resize
 * interactions.
 *
 * ```js
 * interact(target).resizable({
 *   snapEdges: {
 *     targets: [interact.snappers.grid({ x: 100, y: 50 })],
 *   },
 * })
 *
 * interact(target).resizable({
 *   snapEdges: {
 *     targets: [
 *       interact.snappers.grid({
 *        top: 50,
 *        left: 50,
 *        bottom: 100,
 *        right: 100,
 *       }),
 *     ],
 *   },
 * })
 * ```
 */





function snap_edges_start(arg) {
  const {
    edges
  } = arg;

  if (!edges) {
    return null;
  }

  arg.state.targetFields = arg.state.targetFields || [[edges.left ? 'left' : 'right', edges.top ? 'top' : 'bottom']];
  return snapSize.start(arg);
}

const snapEdges = {
  start: snap_edges_start,
  set: snapSize.set,
  defaults: extend(clone(snapSize.defaults), {
    targets: null,
    range: null,
    offset: {
      x: 0,
      y: 0
    }
  })
};
/* harmony default export */ var snap_edges = (makeModifier(snapEdges, 'snapEdges'));


// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/all.js
/* eslint-disable node/no-extraneous-import */












/* harmony default export */ var modifiers_all = ({
  aspectRatio: modifiers_aspectRatio,
  restrictEdges: restrict_edges,
  restrict: restrict_pointer,
  restrictRect: restrict_rect,
  restrictSize: size,
  snapEdges: snap_edges,
  snap: snap_pointer,
  snapSize: snap_size,
  spring: modifiers_noop,
  avoid: modifiers_noop,
  transform: modifiers_noop,
  rubberband: modifiers_noop
});

// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/plugin.js



const plugin_modifiers = {
  id: 'modifiers',

  install(scope) {
    const {
      interactStatic: interact
    } = scope;
    scope.usePlugin(modifiers_base);
    scope.usePlugin(snappers_plugin);
    interact.modifiers = modifiers_all; // for backwrads compatibility

    for (const type in modifiers_all) {
      const {
        _defaults,
        _methods
      } = modifiers_all[type];
      _defaults._methods = _methods;
      scope.defaults.perAction[type] = _defaults;
    }
  }

};
/* harmony default export */ var modifiers_plugin = (plugin_modifiers);

// CONCATENATED MODULE: ./node_modules/@interactjs/modifiers/index.js
/* eslint-disable import/order, no-console, eol-last */



if (typeof window === 'object' && !!window) {
  interact_init(window);
}

_interactjs_interact.use(modifiers_plugin);

// CONCATENATED MODULE: ./node_modules/@interactjs/dev-tools/plugin.js
/* eslint-disable no-console */






var CheckName;

(function (CheckName) {
  CheckName["touchAction"] = "touchAction";
  CheckName["boxSizing"] = "boxSizing";
  CheckName["noListeners"] = "noListeners";
})(CheckName || (CheckName = {}));

const prefix = '[interact.js] ';
const links = {
  touchAction: 'https://developer.mozilla.org/en-US/docs/Web/CSS/touch-action',
  boxSizing: 'https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing'
}; // eslint-disable-next-line no-undef

function dev_tools_plugin_install(scope, {
  logger
} = {}) {
  const {
    Interactable,
    defaults
  } = scope;
  scope.logger = logger || console;
  defaults.base.devTools = {
    ignore: {}
  };

  Interactable.prototype.devTools = function (options) {
    if (options) {
      extend(this.options.devTools, options);
      return this;
    }

    return this.options.devTools;
  };
}

const checks = [{
  name: CheckName.touchAction,

  perform({
    element
  }) {
    return !parentHasStyle(element, 'touchAction', /pan-|pinch|none/);
  },

  getInfo({
    element
  }) {
    return [element, links.touchAction];
  },

  text: 'Consider adding CSS "touch-action: none" to this element\n'
}, {
  name: CheckName.boxSizing,

  perform(interaction) {
    const {
      element
    } = interaction;
    return interaction.prepared.name === 'resize' && element instanceof utils_domObjects.HTMLElement && !hasStyle(element, 'boxSizing', /border-box/);
  },

  text: 'Consider adding CSS "box-sizing: border-box" to this resizable element',

  getInfo({
    element
  }) {
    return [element, links.boxSizing];
  }

}, {
  name: CheckName.noListeners,

  perform(interaction) {
    const actionName = interaction.prepared.name;
    const moveListeners = interaction.interactable.events.types[`${actionName}move`] || [];
    return !moveListeners.length;
  },

  getInfo(interaction) {
    return [interaction.prepared.name, interaction.interactable];
  },

  text: 'There are no listeners set for this action'
}];

function hasStyle(element, prop, styleRe) {
  const value = element.style[prop] || win.getComputedStyle(element)[prop];
  return styleRe.test((value || '').toString());
}

function parentHasStyle(element, prop, styleRe) {
  let parent = element;

  while (is.element(parent)) {
    if (hasStyle(parent, prop, styleRe)) {
      return true;
    }

    parent = parentNode(parent);
  }

  return false;
}

const plugin_id = 'dev-tools';
const defaultExport =  {
  id: plugin_id,
  install: dev_tools_plugin_install,
  listeners: {
    'interactions:action-start': ({
      interaction
    }, scope) => {
      for (const check of checks) {
        const options = interaction.interactable && interaction.interactable.options;

        if (!(options && options.devTools && options.devTools.ignore[check.name]) && check.perform(interaction)) {
          scope.logger.warn(prefix + check.text, ...check.getInfo(interaction));
        }
      }
    }
  },
  checks,
  CheckName,
  links,
  prefix
};
/* harmony default export */ var dev_tools_plugin = (defaultExport);

// CONCATENATED MODULE: ./node_modules/@interactjs/dev-tools/index.js
/* eslint-disable import/order, no-console, eol-last */



if (typeof window === 'object' && !!window) {
  interact_init(window);
}

_interactjs_interact.use(dev_tools_plugin);

// CONCATENATED MODULE: ./node_modules/cache-loader/dist/cjs.js??ref--12-0!./node_modules/thread-loader/dist/cjs.js!./node_modules/babel-loader/lib!./node_modules/cache-loader/dist/cjs.js??ref--0-0!./node_modules/vue-loader/lib??vue-loader-options!./src/components/GridItem.vue?vue&type=script&lang=js&










function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { Object(defineProperty["a" /* default */])(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//



 //    var eventBus = require('./eventBus');








/* harmony default export */ var GridItemvue_type_script_lang_js_ = ({
  name: "GridItem",
  props: {
    /*cols: {
     type: Number,
     required: true
     },*/

    /*containerWidth: {
     type: Number,
     required: true
       },
     rowHeight: {
     type: Number,
     required: true
     },
     margin: {
     type: Array,
     required: true
     },
     maxRows: {
     type: Number,
     required: true
     },*/
    isDraggable: {
      type: Boolean,
      required: false,
      default: null
    },
    isResizable: {
      type: Boolean,
      required: false,
      default: null
    },
    isBounded: {
      type: Boolean,
      required: false,
      default: null
    },

    /*useCssTransforms: {
     type: Boolean,
     required: true
     },
     */
    static: {
      type: Boolean,
      required: false,
      default: false
    },
    minH: {
      type: Number,
      required: false,
      default: 1
    },
    minW: {
      type: Number,
      required: false,
      default: 1
    },
    maxH: {
      type: Number,
      required: false,
      default: Infinity
    },
    maxW: {
      type: Number,
      required: false,
      default: Infinity
    },
    x: {
      type: Number,
      required: true
    },
    y: {
      type: Number,
      required: true
    },
    w: {
      type: Number,
      required: true
    },
    h: {
      type: Number,
      required: true
    },
    i: {
      required: true
    },
    dragIgnoreFrom: {
      type: String,
      required: false,
      default: 'a, button'
    },
    dragAllowFrom: {
      type: String,
      required: false,
      default: null
    },
    resizeIgnoreFrom: {
      type: String,
      required: false,
      default: 'a, button'
    },
    preserveAspectRatio: {
      type: Boolean,
      required: false,
      default: false
    },
    dragOption: {
      type: Object,
      required: false,
      default: function _default() {
        return {};
      }
    },
    resizeOption: {
      type: Object,
      required: false,
      default: function _default() {
        return {};
      }
    }
  },
  inject: ["eventBus", "layout"],
  data: function data() {
    return {
      cols: 1,
      containerWidth: 100,
      rowHeight: 30,
      margin: [10, 10],
      maxRows: Infinity,
      draggable: null,
      resizable: null,
      transformScale: 1,
      useCssTransforms: true,
      useStyleCursor: true,
      isDragging: false,
      dragging: null,
      isResizing: false,
      resizing: null,
      lastX: NaN,
      lastY: NaN,
      lastW: NaN,
      lastH: NaN,
      style: {},
      rtl: false,
      dragEventSet: false,
      resizeEventSet: false,
      previousW: null,
      previousH: null,
      previousX: null,
      previousY: null,
      innerX: this.x,
      innerY: this.y,
      innerW: this.w,
      innerH: this.h
    };
  },
  created: function created() {
    var _this = this;

    var self = this; // Accessible refernces of functions for removing in beforeDestroy

    self.updateWidthHandler = function (width) {
      self.updateWidth(width);
    };

    self.compactHandler = function (layout) {
      self.compact(layout);
    };

    self.setDraggableHandler = function (isDraggable) {
      if (self.isDraggable === null) {
        self.draggable = isDraggable;
      }
    };

    self.setResizableHandler = function (isResizable) {
      if (self.isResizable === null) {
        self.resizable = isResizable;
      }
    };

    self.setBoundedHandler = function (isBounded) {
      if (self.isBounded === null) {
        self.bounded = isBounded;
      }
    };

    self.setTransformScaleHandler = function (transformScale) {
      self.transformScale = transformScale;
    };

    self.setRowHeightHandler = function (rowHeight) {
      self.rowHeight = rowHeight;
    };

    self.setMaxRowsHandler = function (maxRows) {
      self.maxRows = maxRows;
    };

    self.directionchangeHandler = function () {
      _this.rtl = Object(DOM["b" /* getDocumentDir */])() === 'rtl';

      _this.compact();
    };

    self.setColNum = function (colNum) {
      self.cols = parseInt(colNum);
    };

    this.eventBus.$on('updateWidth', self.updateWidthHandler);
    this.eventBus.$on('compact', self.compactHandler);
    this.eventBus.$on('setDraggable', self.setDraggableHandler);
    this.eventBus.$on('setResizable', self.setResizableHandler);
    this.eventBus.$on('setBounded', self.setBoundedHandler);
    this.eventBus.$on('setTransformScale', self.setTransformScaleHandler);
    this.eventBus.$on('setRowHeight', self.setRowHeightHandler);
    this.eventBus.$on('setMaxRows', self.setMaxRowsHandler);
    this.eventBus.$on('directionchange', self.directionchangeHandler);
    this.eventBus.$on('setColNum', self.setColNum);
    this.rtl = Object(DOM["b" /* getDocumentDir */])() === 'rtl';
  },
  beforeDestroy: function beforeDestroy() {
    var self = this; //Remove listeners

    this.eventBus.$off('updateWidth', self.updateWidthHandler);
    this.eventBus.$off('compact', self.compactHandler);
    this.eventBus.$off('setDraggable', self.setDraggableHandler);
    this.eventBus.$off('setResizable', self.setResizableHandler);
    this.eventBus.$off('setBounded', self.setBoundedHandler);
    this.eventBus.$off('setTransformScale', self.setTransformScaleHandler);
    this.eventBus.$off('setRowHeight', self.setRowHeightHandler);
    this.eventBus.$off('setMaxRows', self.setMaxRowsHandler);
    this.eventBus.$off('directionchange', self.directionchangeHandler);
    this.eventBus.$off('setColNum', self.setColNum);

    if (this.interactObj) {
      this.interactObj.unset(); // destroy interact intance
    }
  },
  mounted: function mounted() {
    if (this.layout.responsive && this.layout.lastBreakpoint) {
      this.cols = Object(responsiveUtils["c" /* getColsFromBreakpoint */])(this.layout.lastBreakpoint, this.layout.cols);
    } else {
      this.cols = this.layout.colNum;
    }

    this.rowHeight = this.layout.rowHeight;
    this.containerWidth = this.layout.width !== null ? this.layout.width : 100;
    this.margin = this.layout.margin !== undefined ? this.layout.margin : [10, 10];
    this.maxRows = this.layout.maxRows;

    if (this.isDraggable === null) {
      this.draggable = this.layout.isDraggable;
    } else {
      this.draggable = this.isDraggable;
    }

    if (this.isResizable === null) {
      this.resizable = this.layout.isResizable;
    } else {
      this.resizable = this.isResizable;
    }

    if (this.isBounded === null) {
      this.bounded = this.layout.isBounded;
    } else {
      this.bounded = this.isBounded;
    }

    this.transformScale = this.layout.transformScale;
    this.useCssTransforms = this.layout.useCssTransforms;
    this.useStyleCursor = this.layout.useStyleCursor;
    this.createStyle();
  },
  watch: {
    isDraggable: function isDraggable() {
      this.draggable = this.isDraggable;
    },
    static: function _static() {
      this.tryMakeDraggable();
      this.tryMakeResizable();
    },
    draggable: function draggable() {
      this.tryMakeDraggable();
    },
    isResizable: function isResizable() {
      this.resizable = this.isResizable;
    },
    isBounded: function isBounded() {
      this.bounded = this.isBounded;
    },
    resizable: function resizable() {
      this.tryMakeResizable();
    },
    rowHeight: function rowHeight() {
      this.createStyle();
      this.emitContainerResized();
    },
    cols: function cols() {
      this.tryMakeResizable();
      this.createStyle();
      this.emitContainerResized();
    },
    containerWidth: function containerWidth() {
      this.tryMakeResizable();
      this.createStyle();
      this.emitContainerResized();
    },
    x: function x(newVal) {
      this.innerX = newVal;
      this.createStyle();
    },
    y: function y(newVal) {
      this.innerY = newVal;
      this.createStyle();
    },
    h: function h(newVal) {
      this.innerH = newVal;
      this.createStyle(); // this.emitContainerResized();
    },
    w: function w(newVal) {
      this.innerW = newVal;
      this.createStyle(); // this.emitContainerResized();
    },
    renderRtl: function renderRtl() {
      // console.log("### renderRtl");
      this.tryMakeResizable();
      this.createStyle();
    },
    minH: function minH() {
      this.tryMakeResizable();
    },
    maxH: function maxH() {
      this.tryMakeResizable();
    },
    minW: function minW() {
      this.tryMakeResizable();
    },
    maxW: function maxW() {
      this.tryMakeResizable();
    },
    "$parent.margin": function $parentMargin(margin) {
      if (!margin || margin[0] == this.margin[0] && margin[1] == this.margin[1]) {
        return;
      }

      this.margin = margin.map(function (m) {
        return Number(m);
      });
      this.createStyle();
      this.emitContainerResized();
    }
  },
  computed: {
    classObj: function classObj() {
      return {
        'vue-resizable': this.resizableAndNotStatic,
        'static': this.static,
        'resizing': this.isResizing,
        'vue-draggable-dragging': this.isDragging,
        'cssTransforms': this.useCssTransforms,
        'render-rtl': this.renderRtl,
        'disable-userselect': this.isDragging,
        'no-touch': this.isAndroid && this.draggableOrResizableAndNotStatic
      };
    },
    resizableAndNotStatic: function resizableAndNotStatic() {
      return this.resizable && !this.static;
    },
    draggableOrResizableAndNotStatic: function draggableOrResizableAndNotStatic() {
      return (this.draggable || this.resizable) && !this.static;
    },
    isAndroid: function isAndroid() {
      return navigator.userAgent.toLowerCase().indexOf("android") !== -1;
    },
    renderRtl: function renderRtl() {
      return this.layout.isMirrored ? !this.rtl : this.rtl;
    },
    resizableHandleClass: function resizableHandleClass() {
      if (this.renderRtl) {
        return 'vue-resizable-handle vue-rtl-resizable-handle';
      } else {
        return 'vue-resizable-handle';
      }
    }
  },
  methods: {
    createStyle: function createStyle() {
      if (this.x + this.w > this.cols) {
        this.innerX = 0;
        this.innerW = this.w > this.cols ? this.cols : this.w;
      } else {
        this.innerX = this.x;
        this.innerW = this.w;
      }

      var pos = this.calcPosition(this.innerX, this.innerY, this.innerW, this.innerH);

      if (this.isDragging) {
        pos.top = this.dragging.top; //                    Add rtl support

        if (this.renderRtl) {
          pos.right = this.dragging.left;
        } else {
          pos.left = this.dragging.left;
        }
      }

      if (this.isResizing) {
        pos.width = this.resizing.width;
        pos.height = this.resizing.height;
      }

      var style; // CSS Transforms support (default)

      if (this.useCssTransforms) {
        //                    Add rtl support
        if (this.renderRtl) {
          style = Object(utils["k" /* setTransformRtl */])(pos.top, pos.right, pos.width, pos.height);
        } else {
          style = Object(utils["j" /* setTransform */])(pos.top, pos.left, pos.width, pos.height);
        }
      } else {
        // top,left (slow)
        //                    Add rtl support
        if (this.renderRtl) {
          style = Object(utils["i" /* setTopRight */])(pos.top, pos.right, pos.width, pos.height);
        } else {
          style = Object(utils["h" /* setTopLeft */])(pos.top, pos.left, pos.width, pos.height);
        }
      }

      this.style = style;
    },
    emitContainerResized: function emitContainerResized() {
      // this.style has width and height with trailing 'px'. The
      // resized event is without them
      var styleProps = {};

      for (var _i = 0, _arr = ['width', 'height']; _i < _arr.length; _i++) {
        var prop = _arr[_i];
        var val = this.style[prop];
        var matches = val.match(/^(\d+)px$/);
        if (!matches) return;
        styleProps[prop] = matches[1];
      }

      this.$emit("container-resized", this.i, this.h, this.w, styleProps.height, styleProps.width);
    },
    handleResize: function handleResize(event) {
      if (this.static) return;
      var position = getControlPosition(event); // Get the current drag point from the event. This is used as the offset.

      if (position == null) return; // not possible but satisfies flow

      var x = position.x,
          y = position.y;
      var newSize = {
        width: 0,
        height: 0
      };
      var pos;

      switch (event.type) {
        case "resizestart":
          {
            this.tryMakeResizable();
            this.previousW = this.innerW;
            this.previousH = this.innerH;
            pos = this.calcPosition(this.innerX, this.innerY, this.innerW, this.innerH);
            newSize.width = pos.width;
            newSize.height = pos.height;
            this.resizing = newSize;
            this.isResizing = true;
            break;
          }

        case "resizemove":
          {
            //                        console.log("### resize => " + event.type + ", lastW=" + this.lastW + ", lastH=" + this.lastH);
            var coreEvent = createCoreData(this.lastW, this.lastH, x, y);

            if (this.renderRtl) {
              newSize.width = this.resizing.width - coreEvent.deltaX / this.transformScale;
            } else {
              newSize.width = this.resizing.width + coreEvent.deltaX / this.transformScale;
            }

            newSize.height = this.resizing.height + coreEvent.deltaY / this.transformScale; ///console.log("### resize => " + event.type + ", deltaX=" + coreEvent.deltaX + ", deltaY=" + coreEvent.deltaY);

            this.resizing = newSize;
            break;
          }

        case "resizeend":
          {
            //console.log("### resize end => x=" +this.innerX + " y=" + this.innerY + " w=" + this.innerW + " h=" + this.innerH);
            pos = this.calcPosition(this.innerX, this.innerY, this.innerW, this.innerH);
            newSize.width = pos.width;
            newSize.height = pos.height; //                        console.log("### resize end => " + JSON.stringify(newSize));

            this.resizing = null;
            this.isResizing = false;
            break;
          }
      } // Get new WH


      pos = this.calcWH(newSize.height, newSize.width);

      if (pos.w < this.minW) {
        pos.w = this.minW;
      }

      if (pos.w > this.maxW) {
        pos.w = this.maxW;
      }

      if (pos.h < this.minH) {
        pos.h = this.minH;
      }

      if (pos.h > this.maxH) {
        pos.h = this.maxH;
      }

      if (pos.h < 1) {
        pos.h = 1;
      }

      if (pos.w < 1) {
        pos.w = 1;
      }

      this.lastW = x;
      this.lastH = y;

      if (this.innerW !== pos.w || this.innerH !== pos.h) {
        this.$emit("resize", this.i, pos.h, pos.w, newSize.height, newSize.width);
      }

      if (event.type === "resizeend" && (this.previousW !== this.innerW || this.previousH !== this.innerH)) {
        this.$emit("resized", this.i, pos.h, pos.w, newSize.height, newSize.width);
      }

      this.eventBus.$emit("resizeEvent", event.type, this.i, this.innerX, this.innerY, pos.h, pos.w);
    },
    handleDrag: function handleDrag(event) {
      if (this.static) return;
      if (this.isResizing) return;
      var position = getControlPosition(event); // Get the current drag point from the event. This is used as the offset.

      if (position === null) return; // not possible but satisfies flow

      var x = position.x,
          y = position.y; // let shouldUpdate = false;

      var newPosition = {
        top: 0,
        left: 0
      };

      switch (event.type) {
        case "dragstart":
          {
            this.previousX = this.innerX;
            this.previousY = this.innerY;
            var parentRect = event.target.offsetParent.getBoundingClientRect();
            var clientRect = event.target.getBoundingClientRect();
            var cLeft = clientRect.left / this.transformScale;
            var pLeft = parentRect.left / this.transformScale;
            var cRight = clientRect.right / this.transformScale;
            var pRight = parentRect.right / this.transformScale;
            var cTop = clientRect.top / this.transformScale;
            var pTop = parentRect.top / this.transformScale;

            if (this.renderRtl) {
              newPosition.left = (cRight - pRight) * -1;
            } else {
              newPosition.left = cLeft - pLeft;
            }

            newPosition.top = cTop - pTop;
            this.dragging = newPosition;
            this.isDragging = true;
            break;
          }

        case "dragend":
          {
            if (!this.isDragging) return;

            var _parentRect = event.target.offsetParent.getBoundingClientRect();

            var _clientRect = event.target.getBoundingClientRect();

            var _cLeft = _clientRect.left / this.transformScale;

            var _pLeft = _parentRect.left / this.transformScale;

            var _cRight = _clientRect.right / this.transformScale;

            var _pRight = _parentRect.right / this.transformScale;

            var _cTop = _clientRect.top / this.transformScale;

            var _pTop = _parentRect.top / this.transformScale; //                        Add rtl support


            if (this.renderRtl) {
              newPosition.left = (_cRight - _pRight) * -1;
            } else {
              newPosition.left = _cLeft - _pLeft;
            }

            newPosition.top = _cTop - _pTop; //                        console.log("### drag end => " + JSON.stringify(newPosition));
            //                        console.log("### DROP: " + JSON.stringify(newPosition));

            this.dragging = null;
            this.isDragging = false; // shouldUpdate = true;

            break;
          }

        case "dragmove":
          {
            var coreEvent = createCoreData(this.lastX, this.lastY, x, y); //                        Add rtl support

            if (this.renderRtl) {
              newPosition.left = this.dragging.left - coreEvent.deltaX / this.transformScale;
            } else {
              newPosition.left = this.dragging.left + coreEvent.deltaX / this.transformScale;
            }

            newPosition.top = this.dragging.top + coreEvent.deltaY / this.transformScale;

            if (this.bounded) {
              var bottomBoundary = event.target.offsetParent.clientHeight - this.calcGridItemWHPx(this.h, this.rowHeight, this.margin[1]);
              newPosition.top = this.clamp(newPosition.top, 0, bottomBoundary);
              var colWidth = this.calcColWidth();
              var rightBoundary = this.containerWidth - this.calcGridItemWHPx(this.w, colWidth, this.margin[0]);
              newPosition.left = this.clamp(newPosition.left, 0, rightBoundary);
            } //                        console.log("### drag => " + event.type + ", x=" + x + ", y=" + y);
            //                        console.log("### drag => " + event.type + ", deltaX=" + coreEvent.deltaX + ", deltaY=" + coreEvent.deltaY);
            //                        console.log("### drag end => " + JSON.stringify(newPosition));


            this.dragging = newPosition;
            break;
          }
      } // Get new XY


      var pos;

      if (this.renderRtl) {
        pos = this.calcXY(newPosition.top, newPosition.left);
      } else {
        pos = this.calcXY(newPosition.top, newPosition.left);
      }

      this.lastX = x;
      this.lastY = y;

      if (this.innerX !== pos.x || this.innerY !== pos.y) {
        this.$emit("move", this.i, pos.x, pos.y);
      }

      if (event.type === "dragend" && (this.previousX !== this.innerX || this.previousY !== this.innerY)) {
        this.$emit("moved", this.i, pos.x, pos.y);
      }

      this.eventBus.$emit("dragEvent", event.type, this.i, pos.x, pos.y, this.innerH, this.innerW);
    },
    calcPosition: function calcPosition(x, y, w, h) {
      var colWidth = this.calcColWidth(); // add rtl support

      var out;

      if (this.renderRtl) {
        out = {
          right: Math.round(colWidth * x + (x + 1) * this.margin[0]),
          top: Math.round(this.rowHeight * y + (y + 1) * this.margin[1]),
          // 0 * Infinity === NaN, which causes problems with resize constriants;
          // Fix this if it occurs.
          // Note we do it here rather than later because Math.round(Infinity) causes deopt
          width: w === Infinity ? w : Math.round(colWidth * w + Math.max(0, w - 1) * this.margin[0]),
          height: h === Infinity ? h : Math.round(this.rowHeight * h + Math.max(0, h - 1) * this.margin[1])
        };
      } else {
        out = {
          left: Math.round(colWidth * x + (x + 1) * this.margin[0]),
          top: Math.round(this.rowHeight * y + (y + 1) * this.margin[1]),
          // 0 * Infinity === NaN, which causes problems with resize constriants;
          // Fix this if it occurs.
          // Note we do it here rather than later because Math.round(Infinity) causes deopt
          width: w === Infinity ? w : Math.round(colWidth * w + Math.max(0, w - 1) * this.margin[0]),
          height: h === Infinity ? h : Math.round(this.rowHeight * h + Math.max(0, h - 1) * this.margin[1])
        };
      }

      return out;
    },

    /**
     * Translate x and y coordinates from pixels to grid units.
     * @param  {Number} top  Top position (relative to parent) in pixels.
     * @param  {Number} left Left position (relative to parent) in pixels.
     * @return {Object} x and y in grid units.
     */
    // TODO check if this function needs change in order to support rtl.
    calcXY: function calcXY(top, left) {
      var colWidth = this.calcColWidth(); // left = colWidth * x + margin * (x + 1)
      // l = cx + m(x+1)
      // l = cx + mx + m
      // l - m = cx + mx
      // l - m = x(c + m)
      // (l - m) / (c + m) = x
      // x = (left - margin) / (coldWidth + margin)

      var x = Math.round((left - this.margin[0]) / (colWidth + this.margin[0]));
      var y = Math.round((top - this.margin[1]) / (this.rowHeight + this.margin[1])); // Capping

      x = Math.max(Math.min(x, this.cols - this.innerW), 0);
      y = Math.max(Math.min(y, this.maxRows - this.innerH), 0);
      return {
        x: x,
        y: y
      };
    },
    // Helper for generating column width
    calcColWidth: function calcColWidth() {
      var colWidth = (this.containerWidth - this.margin[0] * (this.cols + 1)) / this.cols; // console.log("### COLS=" + this.cols + " COL WIDTH=" + colWidth + " MARGIN " + this.margin[0]);

      return colWidth;
    },
    // This can either be called:
    // calcGridItemWHPx(w, colWidth, margin[0])
    // or
    // calcGridItemWHPx(h, rowHeight, margin[1])
    calcGridItemWHPx: function calcGridItemWHPx(gridUnits, colOrRowSize, marginPx) {
      // 0 * Infinity === NaN, which causes problems with resize contraints
      if (!Number.isFinite(gridUnits)) return gridUnits;
      return Math.round(colOrRowSize * gridUnits + Math.max(0, gridUnits - 1) * marginPx);
    },
    // Similar to _.clamp
    clamp: function clamp(num, lowerBound, upperBound) {
      return Math.max(Math.min(num, upperBound), lowerBound);
    },

    /**
     * Given a height and width in pixel values, calculate grid units.
     * @param  {Number} height Height in pixels.
     * @param  {Number} width  Width in pixels.
     * @param  {Boolean} autoSizeFlag  function autoSize identifier.
     * @return {Object} w, h as grid units.
     */
    calcWH: function calcWH(height, width) {
      var autoSizeFlag = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      var colWidth = this.calcColWidth(); // width = colWidth * w - (margin * (w - 1))
      // ...
      // w = (width + margin) / (colWidth + margin)

      var w = Math.round((width + this.margin[0]) / (colWidth + this.margin[0]));
      var h = 0;

      if (!autoSizeFlag) {
        h = Math.round((height + this.margin[1]) / (this.rowHeight + this.margin[1]));
      } else {
        h = Math.ceil((height + this.margin[1]) / (this.rowHeight + this.margin[1]));
      } // Capping


      w = Math.max(Math.min(w, this.cols - this.innerX), 0);
      h = Math.max(Math.min(h, this.maxRows - this.innerY), 0);
      return {
        w: w,
        h: h
      };
    },
    updateWidth: function updateWidth(width, colNum) {
      this.containerWidth = width;

      if (colNum !== undefined && colNum !== null) {
        this.cols = colNum;
      }
    },
    compact: function compact() {
      this.createStyle();
    },
    tryMakeDraggable: function tryMakeDraggable() {
      var self = this;

      if (this.interactObj === null || this.interactObj === undefined) {
        this.interactObj = _interactjs_interact(this.$refs.item);

        if (!this.useStyleCursor) {
          this.interactObj.styleCursor(false);
        }
      }

      if (this.draggable && !this.static) {
        var opts = _objectSpread({
          ignoreFrom: this.dragIgnoreFrom,
          allowFrom: this.dragAllowFrom
        }, this.dragOption);

        this.interactObj.draggable(opts);
        /*this.interactObj.draggable({allowFrom: '.vue-draggable-handle'});*/

        if (!this.dragEventSet) {
          this.dragEventSet = true;
          this.interactObj.on('dragstart dragmove dragend', function (event) {
            self.handleDrag(event);
          });
        }
      } else {
        this.interactObj.draggable({
          enabled: false
        });
      }
    },
    tryMakeResizable: function tryMakeResizable() {
      var self = this;

      if (this.interactObj === null || this.interactObj === undefined) {
        this.interactObj = _interactjs_interact(this.$refs.item);

        if (!this.useStyleCursor) {
          this.interactObj.styleCursor(false);
        }
      }

      if (this.resizable && !this.static) {
        var maximum = this.calcPosition(0, 0, this.maxW, this.maxH);
        var minimum = this.calcPosition(0, 0, this.minW, this.minH); // console.log("### MAX " + JSON.stringify(maximum));
        // console.log("### MIN " + JSON.stringify(minimum));

        var opts = _objectSpread({
          // allowFrom: "." + this.resizableHandleClass.trim().replace(" ", "."),
          edges: {
            left: false,
            right: "." + this.resizableHandleClass.trim().replace(" ", "."),
            bottom: "." + this.resizableHandleClass.trim().replace(" ", "."),
            top: false
          },
          ignoreFrom: this.resizeIgnoreFrom,
          restrictSize: {
            min: {
              height: minimum.height * this.transformScale,
              width: minimum.width * this.transformScale
            },
            max: {
              height: maximum.height * this.transformScale,
              width: maximum.width * this.transformScale
            }
          }
        }, this.resizeOption);

        if (this.preserveAspectRatio) {
          opts.modifiers = [_interactjs_interact.modifiers.aspectRatio({
            ratio: 'preserve'
          })];
        }

        this.interactObj.resizable(opts);

        if (!this.resizeEventSet) {
          this.resizeEventSet = true;
          this.interactObj.on('resizestart resizemove resizeend', function (event) {
            self.handleResize(event);
          });
        }
      } else {
        this.interactObj.resizable({
          enabled: false
        });
      }
    },
    autoSize: function autoSize() {
      // ok here we want to calculate if a resize is needed
      this.previousW = this.innerW;
      this.previousH = this.innerH;
      var newSize = this.$slots.default[0].elm.getBoundingClientRect();
      var pos = this.calcWH(newSize.height, newSize.width, true);

      if (pos.w < this.minW) {
        pos.w = this.minW;
      }

      if (pos.w > this.maxW) {
        pos.w = this.maxW;
      }

      if (pos.h < this.minH) {
        pos.h = this.minH;
      }

      if (pos.h > this.maxH) {
        pos.h = this.maxH;
      }

      if (pos.h < 1) {
        pos.h = 1;
      }

      if (pos.w < 1) {
        pos.w = 1;
      } // this.lastW = x; // basically, this is copied from resizehandler, but shouldn't be needed
      // this.lastH = y;


      if (this.innerW !== pos.w || this.innerH !== pos.h) {
        this.$emit("resize", this.i, pos.h, pos.w, newSize.height, newSize.width);
      }

      if (this.previousW !== pos.w || this.previousH !== pos.h) {
        this.$emit("resized", this.i, pos.h, pos.w, newSize.height, newSize.width);
        this.eventBus.$emit("resizeEvent", "resizeend", this.i, this.innerX, this.innerY, pos.h, pos.w);
      }
    }
  }
});
// CONCATENATED MODULE: ./src/components/GridItem.vue?vue&type=script&lang=js&
 /* harmony default export */ var components_GridItemvue_type_script_lang_js_ = (GridItemvue_type_script_lang_js_); 
// EXTERNAL MODULE: ./src/components/GridItem.vue?vue&type=style&index=0&lang=css&
var GridItemvue_type_style_index_0_lang_css_ = __webpack_require__("5ed4");

// EXTERNAL MODULE: ./node_modules/vue-loader/lib/runtime/componentNormalizer.js
var componentNormalizer = __webpack_require__("2877");

// CONCATENATED MODULE: ./src/components/GridItem.vue






/* normalize component */

var component = Object(componentNormalizer["a" /* default */])(
  components_GridItemvue_type_script_lang_js_,
  render,
  staticRenderFns,
  false,
  null,
  null,
  null
  
);

/* harmony default export */ var GridItem = __webpack_exports__["a"] = (component.exports);

/***/ }),

/***/ "be13":
/***/ (function(module, exports) {

// 7.2.1 RequireObjectCoercible(argument)
module.exports = function (it) {
  if (it == undefined) throw TypeError("Can't call method on  " + it);
  return it;
};


/***/ }),

/***/ "c274":
/***/ (function(module, exports, __webpack_require__) {


var utils = __webpack_require__("50bf");

module.exports = function batchProcessorMaker(options) {
    options             = options || {};
    var reporter        = options.reporter;
    var asyncProcess    = utils.getOption(options, "async", true);
    var autoProcess     = utils.getOption(options, "auto", true);

    if(autoProcess && !asyncProcess) {
        reporter && reporter.warn("Invalid options combination. auto=true and async=false is invalid. Setting async=true.");
        asyncProcess = true;
    }

    var batch = Batch();
    var asyncFrameHandler;
    var isProcessing = false;

    function addFunction(level, fn) {
        if(!isProcessing && autoProcess && asyncProcess && batch.size() === 0) {
            // Since this is async, it is guaranteed to be executed after that the fn is added to the batch.
            // This needs to be done before, since we're checking the size of the batch to be 0.
            processBatchAsync();
        }

        batch.add(level, fn);
    }

    function processBatch() {
        // Save the current batch, and create a new batch so that incoming functions are not added into the currently processing batch.
        // Continue processing until the top-level batch is empty (functions may be added to the new batch while processing, and so on).
        isProcessing = true;
        while (batch.size()) {
            var processingBatch = batch;
            batch = Batch();
            processingBatch.process();
        }
        isProcessing = false;
    }

    function forceProcessBatch(localAsyncProcess) {
        if (isProcessing) {
            return;
        }

        if(localAsyncProcess === undefined) {
            localAsyncProcess = asyncProcess;
        }

        if(asyncFrameHandler) {
            cancelFrame(asyncFrameHandler);
            asyncFrameHandler = null;
        }

        if(localAsyncProcess) {
            processBatchAsync();
        } else {
            processBatch();
        }
    }

    function processBatchAsync() {
        asyncFrameHandler = requestFrame(processBatch);
    }

    function cancelFrame(listener) {
        // var cancel = window.cancelAnimationFrame || window.mozCancelAnimationFrame || window.webkitCancelAnimationFrame || window.clearTimeout;
        var cancel = clearTimeout;
        return cancel(listener);
    }

    function requestFrame(callback) {
        // var raf = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || function(fn) { return window.setTimeout(fn, 20); };
        var raf = function(fn) { return setTimeout(fn, 0); };
        return raf(callback);
    }

    return {
        add: addFunction,
        force: forceProcessBatch
    };
};

function Batch() {
    var batch       = {};
    var size        = 0;
    var topLevel    = 0;
    var bottomLevel = 0;

    function add(level, fn) {
        if(!fn) {
            fn = level;
            level = 0;
        }

        if(level > topLevel) {
            topLevel = level;
        } else if(level < bottomLevel) {
            bottomLevel = level;
        }

        if(!batch[level]) {
            batch[level] = [];
        }

        batch[level].push(fn);
        size++;
    }

    function process() {
        for(var level = bottomLevel; level <= topLevel; level++) {
            var fns = batch[level];

            for(var i = 0; i < fns.length; i++) {
                var fn = fns[i];
                fn();
            }
        }
    }

    function getSize() {
        return size;
    }

    return {
        add: add,
        process: process,
        size: getSize
    };
}


/***/ }),

/***/ "c366":
/***/ (function(module, exports, __webpack_require__) {

// false -> Array#indexOf
// true  -> Array#includes
var toIObject = __webpack_require__("6821");
var toLength = __webpack_require__("9def");
var toAbsoluteIndex = __webpack_require__("77f1");
module.exports = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIObject($this);
    var length = toLength(O.length);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare
    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare
      if (value != value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) if (IS_INCLUDES || index in O) {
      if (O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};


/***/ }),

/***/ "c5f6":
/***/ (function(module, exports, __webpack_require__) {

var global = __webpack_require__("7726");
var has = __webpack_require__("69a8");
var cof = __webpack_require__("2d95");
var inheritIfRequired = __webpack_require__("5dbc");
var toPrimitive = __webpack_require__("6a99");
var fails = __webpack_require__("79e5");
var gOPN = __webpack_require__("9093").f;
var gOPD = __webpack_require__("11e9").f;
var dP = __webpack_require__("86cc").f;
var $trim = __webpack_require__("aa77").trim;
var NUMBER = 'Number';
var $Number = global[NUMBER];
var Base = $Number;
var proto = $Number.prototype;
// Opera ~12 has broken Object#toString
var BROKEN_COF = cof(__webpack_require__("2aeb")(proto)) == NUMBER;
var TRIM = 'trim' in String.prototype;

// 7.1.3 ToNumber(argument)
var toNumber = function (argument) {
  var it = toPrimitive(argument, false);
  if (typeof it == 'string' && it.length > 2) {
    it = TRIM ? it.trim() : $trim(it, 3);
    var first = it.charCodeAt(0);
    var third, radix, maxCode;
    if (first === 43 || first === 45) {
      third = it.charCodeAt(2);
      if (third === 88 || third === 120) return NaN; // Number('+0x1') should be NaN, old V8 fix
    } else if (first === 48) {
      switch (it.charCodeAt(1)) {
        case 66: case 98: radix = 2; maxCode = 49; break; // fast equal /^0b[01]+$/i
        case 79: case 111: radix = 8; maxCode = 55; break; // fast equal /^0o[0-7]+$/i
        default: return +it;
      }
      for (var digits = it.slice(2), i = 0, l = digits.length, code; i < l; i++) {
        code = digits.charCodeAt(i);
        // parseInt parses a string to a first unavailable symbol
        // but ToNumber should return NaN if a string contains unavailable symbols
        if (code < 48 || code > maxCode) return NaN;
      } return parseInt(digits, radix);
    }
  } return +it;
};

if (!$Number(' 0o1') || !$Number('0b1') || $Number('+0x1')) {
  $Number = function Number(value) {
    var it = arguments.length < 1 ? 0 : value;
    var that = this;
    return that instanceof $Number
      // check on 1..constructor(foo) case
      && (BROKEN_COF ? fails(function () { proto.valueOf.call(that); }) : cof(that) != NUMBER)
        ? inheritIfRequired(new Base(toNumber(it)), that, $Number) : toNumber(it);
  };
  for (var keys = __webpack_require__("9e1e") ? gOPN(Base) : (
    // ES3:
    'MAX_VALUE,MIN_VALUE,NaN,NEGATIVE_INFINITY,POSITIVE_INFINITY,' +
    // ES6 (in case, if modules with ES6 Number statics required before):
    'EPSILON,isFinite,isInteger,isNaN,isSafeInteger,MAX_SAFE_INTEGER,' +
    'MIN_SAFE_INTEGER,parseFloat,parseInt,isInteger'
  ).split(','), j = 0, key; keys.length > j; j++) {
    if (has(Base, key = keys[j]) && !has($Number, key)) {
      dP($Number, key, gOPD(Base, key));
    }
  }
  $Number.prototype = proto;
  proto.constructor = $Number;
  __webpack_require__("2aba")(global, NUMBER, $Number);
}


/***/ }),

/***/ "c69a":
/***/ (function(module, exports, __webpack_require__) {

module.exports = !__webpack_require__("9e1e") && !__webpack_require__("79e5")(function () {
  return Object.defineProperty(__webpack_require__("230e")('div'), 'a', { get: function () { return 7; } }).a != 7;
});


/***/ }),

/***/ "c8ba":
/***/ (function(module, exports) {

var g;

// This works in non-strict mode
g = (function() {
	return this;
})();

try {
	// This works if eval is allowed (see CSP)
	g = g || new Function("return this")();
} catch (e) {
	// This works if the window reference is available
	if (typeof window === "object") g = window;
}

// g can still be undefined, but nothing to do about it...
// We return undefined, instead of nothing here, so it's
// easier to handle this case. if(!global) { ...}

module.exports = g;


/***/ }),

/***/ "c946":
/***/ (function(module, exports, __webpack_require__) {
/**
 * Resize detection strategy that injects divs to elements in order to detect resize events on scroll events.
 * Heavily inspired by: https://github.com/marcj/css-element-queries/blob/master/src/ResizeSensor.js
 */



var forEach = __webpack_require__("b770").forEach;

module.exports = function(options) {
    options             = options || {};
    var reporter        = options.reporter;
    var batchProcessor  = options.batchProcessor;
    var getState        = options.stateHandler.getState;
    var hasState        = options.stateHandler.hasState;
    var idHandler       = options.idHandler;

    if (!batchProcessor) {
        throw new Error("Missing required dependency: batchProcessor");
    }

    if (!reporter) {
        throw new Error("Missing required dependency: reporter.");
    }

    //TODO: Could this perhaps be done at installation time?
    var scrollbarSizes = getScrollbarSizes();

    var styleId = "erd_scroll_detection_scrollbar_style";
    var detectionContainerClass = "erd_scroll_detection_container";

    function initDocument(targetDocument) {
        // Inject the scrollbar styling that prevents them from appearing sometimes in Chrome.
        // The injected container needs to have a class, so that it may be styled with CSS (pseudo elements).
        injectScrollStyle(targetDocument, styleId, detectionContainerClass);
    }

    initDocument(window.document);

    function buildCssTextString(rules) {
        var seperator = options.important ? " !important; " : "; ";

        return (rules.join(seperator) + seperator).trim();
    }

    function getScrollbarSizes() {
        var width = 500;
        var height = 500;

        var child = document.createElement("div");
        child.style.cssText = buildCssTextString(["position: absolute", "width: " + width*2 + "px", "height: " + height*2 + "px", "visibility: hidden", "margin: 0", "padding: 0"]);

        var container = document.createElement("div");
        container.style.cssText = buildCssTextString(["position: absolute", "width: " + width + "px", "height: " + height + "px", "overflow: scroll", "visibility: none", "top: " + -width*3 + "px", "left: " + -height*3 + "px", "visibility: hidden", "margin: 0", "padding: 0"]);

        container.appendChild(child);

        document.body.insertBefore(container, document.body.firstChild);

        var widthSize = width - container.clientWidth;
        var heightSize = height - container.clientHeight;

        document.body.removeChild(container);

        return {
            width: widthSize,
            height: heightSize
        };
    }

    function injectScrollStyle(targetDocument, styleId, containerClass) {
        function injectStyle(style, method) {
            method = method || function (element) {
                targetDocument.head.appendChild(element);
            };

            var styleElement = targetDocument.createElement("style");
            styleElement.innerHTML = style;
            styleElement.id = styleId;
            method(styleElement);
            return styleElement;
        }

        if (!targetDocument.getElementById(styleId)) {
            var containerAnimationClass = containerClass + "_animation";
            var containerAnimationActiveClass = containerClass + "_animation_active";
            var style = "/* Created by the element-resize-detector library. */\n";
            style += "." + containerClass + " > div::-webkit-scrollbar { " + buildCssTextString(["display: none"]) + " }\n\n";
            style += "." + containerAnimationActiveClass + " { " + buildCssTextString(["-webkit-animation-duration: 0.1s", "animation-duration: 0.1s", "-webkit-animation-name: " + containerAnimationClass, "animation-name: " + containerAnimationClass]) + " }\n";
            style += "@-webkit-keyframes " + containerAnimationClass +  " { 0% { opacity: 1; } 50% { opacity: 0; } 100% { opacity: 1; } }\n";
            style += "@keyframes " + containerAnimationClass +          " { 0% { opacity: 1; } 50% { opacity: 0; } 100% { opacity: 1; } }";
            injectStyle(style);
        }
    }

    function addAnimationClass(element) {
        element.className += " " + detectionContainerClass + "_animation_active";
    }

    function addEvent(el, name, cb) {
        if (el.addEventListener) {
            el.addEventListener(name, cb);
        } else if(el.attachEvent) {
            el.attachEvent("on" + name, cb);
        } else {
            return reporter.error("[scroll] Don't know how to add event listeners.");
        }
    }

    function removeEvent(el, name, cb) {
        if (el.removeEventListener) {
            el.removeEventListener(name, cb);
        } else if(el.detachEvent) {
            el.detachEvent("on" + name, cb);
        } else {
            return reporter.error("[scroll] Don't know how to remove event listeners.");
        }
    }

    function getExpandElement(element) {
        return getState(element).container.childNodes[0].childNodes[0].childNodes[0];
    }

    function getShrinkElement(element) {
        return getState(element).container.childNodes[0].childNodes[0].childNodes[1];
    }

    /**
     * Adds a resize event listener to the element.
     * @public
     * @param {element} element The element that should have the listener added.
     * @param {function} listener The listener callback to be called for each resize event of the element. The element will be given as a parameter to the listener callback.
     */
    function addListener(element, listener) {
        var listeners = getState(element).listeners;

        if (!listeners.push) {
            throw new Error("Cannot add listener to an element that is not detectable.");
        }

        getState(element).listeners.push(listener);
    }

    /**
     * Makes an element detectable and ready to be listened for resize events. Will call the callback when the element is ready to be listened for resize changes.
     * @private
     * @param {object} options Optional options object.
     * @param {element} element The element to make detectable
     * @param {function} callback The callback to be called when the element is ready to be listened for resize changes. Will be called with the element as first parameter.
     */
    function makeDetectable(options, element, callback) {
        if (!callback) {
            callback = element;
            element = options;
            options = null;
        }

        options = options || {};

        function debug() {
            if (options.debug) {
                var args = Array.prototype.slice.call(arguments);
                args.unshift(idHandler.get(element), "Scroll: ");
                if (reporter.log.apply) {
                    reporter.log.apply(null, args);
                } else {
                    for (var i = 0; i < args.length; i++) {
                        reporter.log(args[i]);
                    }
                }
            }
        }

        function isDetached(element) {
            function isInDocument(element) {
                var isInShadowRoot = element.getRootNode && element.getRootNode().contains(element);
                return element === element.ownerDocument.body || element.ownerDocument.body.contains(element) || isInShadowRoot;
            }

            if (!isInDocument(element)) {
                return true;
            }

            // FireFox returns null style in hidden iframes. See https://github.com/wnr/element-resize-detector/issues/68 and https://bugzilla.mozilla.org/show_bug.cgi?id=795520
            if (window.getComputedStyle(element) === null) {
                return true;
            }

            return false;
        }

        function isUnrendered(element) {
            // Check the absolute positioned container since the top level container is display: inline.
            var container = getState(element).container.childNodes[0];
            var style = window.getComputedStyle(container);
            return !style.width || style.width.indexOf("px") === -1; //Can only compute pixel value when rendered.
        }

        function getStyle() {
            // Some browsers only force layouts when actually reading the style properties of the style object, so make sure that they are all read here,
            // so that the user of the function can be sure that it will perform the layout here, instead of later (important for batching).
            var elementStyle            = window.getComputedStyle(element);
            var style                   = {};
            style.position              = elementStyle.position;
            style.width                 = element.offsetWidth;
            style.height                = element.offsetHeight;
            style.top                   = elementStyle.top;
            style.right                 = elementStyle.right;
            style.bottom                = elementStyle.bottom;
            style.left                  = elementStyle.left;
            style.widthCSS              = elementStyle.width;
            style.heightCSS             = elementStyle.height;
            return style;
        }

        function storeStartSize() {
            var style = getStyle();
            getState(element).startSize = {
                width: style.width,
                height: style.height
            };
            debug("Element start size", getState(element).startSize);
        }

        function initListeners() {
            getState(element).listeners = [];
        }

        function storeStyle() {
            debug("storeStyle invoked.");
            if (!getState(element)) {
                debug("Aborting because element has been uninstalled");
                return;
            }

            var style = getStyle();
            getState(element).style = style;
        }

        function storeCurrentSize(element, width, height) {
            getState(element).lastWidth = width;
            getState(element).lastHeight  = height;
        }

        function getExpandChildElement(element) {
            return getExpandElement(element).childNodes[0];
        }

        function getWidthOffset() {
            return 2 * scrollbarSizes.width + 1;
        }

        function getHeightOffset() {
            return 2 * scrollbarSizes.height + 1;
        }

        function getExpandWidth(width) {
            return width + 10 + getWidthOffset();
        }

        function getExpandHeight(height) {
            return height + 10 + getHeightOffset();
        }

        function getShrinkWidth(width) {
            return width * 2 + getWidthOffset();
        }

        function getShrinkHeight(height) {
            return height * 2 + getHeightOffset();
        }

        function positionScrollbars(element, width, height) {
            var expand          = getExpandElement(element);
            var shrink          = getShrinkElement(element);
            var expandWidth     = getExpandWidth(width);
            var expandHeight    = getExpandHeight(height);
            var shrinkWidth     = getShrinkWidth(width);
            var shrinkHeight    = getShrinkHeight(height);
            expand.scrollLeft   = expandWidth;
            expand.scrollTop    = expandHeight;
            shrink.scrollLeft   = shrinkWidth;
            shrink.scrollTop    = shrinkHeight;
        }

        function injectContainerElement() {
            var container = getState(element).container;

            if (!container) {
                container                   = document.createElement("div");
                container.className         = detectionContainerClass;
                container.style.cssText     = buildCssTextString(["visibility: hidden", "display: inline", "width: 0px", "height: 0px", "z-index: -1", "overflow: hidden", "margin: 0", "padding: 0"]);
                getState(element).container = container;
                addAnimationClass(container);
                element.appendChild(container);

                var onAnimationStart = function () {
                    getState(element).onRendered && getState(element).onRendered();
                };

                addEvent(container, "animationstart", onAnimationStart);

                // Store the event handler here so that they may be removed when uninstall is called.
                // See uninstall function for an explanation why it is needed.
                getState(element).onAnimationStart = onAnimationStart;
            }

            return container;
        }

        function injectScrollElements() {
            function alterPositionStyles() {
                var style = getState(element).style;

                if(style.position === "static") {
                    element.style.setProperty("position", "relative",options.important ? "important" : "");

                    var removeRelativeStyles = function(reporter, element, style, property) {
                        function getNumericalValue(value) {
                            return value.replace(/[^-\d\.]/g, "");
                        }

                        var value = style[property];

                        if(value !== "auto" && getNumericalValue(value) !== "0") {
                            reporter.warn("An element that is positioned static has style." + property + "=" + value + " which is ignored due to the static positioning. The element will need to be positioned relative, so the style." + property + " will be set to 0. Element: ", element);
                            element.style[property] = 0;
                        }
                    };

                    //Check so that there are no accidental styles that will make the element styled differently now that is is relative.
                    //If there are any, set them to 0 (this should be okay with the user since the style properties did nothing before [since the element was positioned static] anyway).
                    removeRelativeStyles(reporter, element, style, "top");
                    removeRelativeStyles(reporter, element, style, "right");
                    removeRelativeStyles(reporter, element, style, "bottom");
                    removeRelativeStyles(reporter, element, style, "left");
                }
            }

            function getLeftTopBottomRightCssText(left, top, bottom, right) {
                left = (!left ? "0" : (left + "px"));
                top = (!top ? "0" : (top + "px"));
                bottom = (!bottom ? "0" : (bottom + "px"));
                right = (!right ? "0" : (right + "px"));

                return ["left: " + left, "top: " + top, "right: " + right, "bottom: " + bottom];
            }

            debug("Injecting elements");

            if (!getState(element)) {
                debug("Aborting because element has been uninstalled");
                return;
            }

            alterPositionStyles();

            var rootContainer = getState(element).container;

            if (!rootContainer) {
                rootContainer = injectContainerElement();
            }

            // Due to this WebKit bug https://bugs.webkit.org/show_bug.cgi?id=80808 (currently fixed in Blink, but still present in WebKit browsers such as Safari),
            // we need to inject two containers, one that is width/height 100% and another that is left/top -1px so that the final container always is 1x1 pixels bigger than
            // the targeted element.
            // When the bug is resolved, "containerContainer" may be removed.

            // The outer container can occasionally be less wide than the targeted when inside inline elements element in WebKit (see https://bugs.webkit.org/show_bug.cgi?id=152980).
            // This should be no problem since the inner container either way makes sure the injected scroll elements are at least 1x1 px.

            var scrollbarWidth          = scrollbarSizes.width;
            var scrollbarHeight         = scrollbarSizes.height;
            var containerContainerStyle = buildCssTextString(["position: absolute", "flex: none", "overflow: hidden", "z-index: -1", "visibility: hidden", "width: 100%", "height: 100%", "left: 0px", "top: 0px"]);
            var containerStyle          = buildCssTextString(["position: absolute", "flex: none", "overflow: hidden", "z-index: -1", "visibility: hidden"].concat(getLeftTopBottomRightCssText(-(1 + scrollbarWidth), -(1 + scrollbarHeight), -scrollbarHeight, -scrollbarWidth)));
            var expandStyle             = buildCssTextString(["position: absolute", "flex: none", "overflow: scroll", "z-index: -1", "visibility: hidden", "width: 100%", "height: 100%"]);
            var shrinkStyle             = buildCssTextString(["position: absolute", "flex: none", "overflow: scroll", "z-index: -1", "visibility: hidden", "width: 100%", "height: 100%"]);
            var expandChildStyle        = buildCssTextString(["position: absolute", "left: 0", "top: 0"]);
            var shrinkChildStyle        = buildCssTextString(["position: absolute", "width: 200%", "height: 200%"]);

            var containerContainer      = document.createElement("div");
            var container               = document.createElement("div");
            var expand                  = document.createElement("div");
            var expandChild             = document.createElement("div");
            var shrink                  = document.createElement("div");
            var shrinkChild             = document.createElement("div");

            // Some browsers choke on the resize system being rtl, so force it to ltr. https://github.com/wnr/element-resize-detector/issues/56
            // However, dir should not be set on the top level container as it alters the dimensions of the target element in some browsers.
            containerContainer.dir              = "ltr";

            containerContainer.style.cssText    = containerContainerStyle;
            containerContainer.className        = detectionContainerClass;
            container.className                 = detectionContainerClass;
            container.style.cssText             = containerStyle;
            expand.style.cssText                = expandStyle;
            expandChild.style.cssText           = expandChildStyle;
            shrink.style.cssText                = shrinkStyle;
            shrinkChild.style.cssText           = shrinkChildStyle;

            expand.appendChild(expandChild);
            shrink.appendChild(shrinkChild);
            container.appendChild(expand);
            container.appendChild(shrink);
            containerContainer.appendChild(container);
            rootContainer.appendChild(containerContainer);

            function onExpandScroll() {
                var state = getState(element);
                if (state && state.onExpand) {
                    state.onExpand();
                } else {
                    debug("Aborting expand scroll handler: element has been uninstalled");
                }
            }

            function onShrinkScroll() {
                var state = getState(element);
                if (state && state.onShrink) {
                    state.onShrink();
                } else {
                    debug("Aborting shrink scroll handler: element has been uninstalled");
                }
            }

            addEvent(expand, "scroll", onExpandScroll);
            addEvent(shrink, "scroll", onShrinkScroll);

            // Store the event handlers here so that they may be removed when uninstall is called.
            // See uninstall function for an explanation why it is needed.
            getState(element).onExpandScroll = onExpandScroll;
            getState(element).onShrinkScroll = onShrinkScroll;
        }

        function registerListenersAndPositionElements() {
            function updateChildSizes(element, width, height) {
                var expandChild             = getExpandChildElement(element);
                var expandWidth             = getExpandWidth(width);
                var expandHeight            = getExpandHeight(height);
                expandChild.style.setProperty("width", expandWidth + "px", options.important ? "important" : "");
                expandChild.style.setProperty("height", expandHeight + "px", options.important ? "important" : "");
            }

            function updateDetectorElements(done) {
                var width           = element.offsetWidth;
                var height          = element.offsetHeight;

                // Check whether the size has actually changed since last time the algorithm ran. If not, some steps may be skipped.
                var sizeChanged = width !== getState(element).lastWidth || height !== getState(element).lastHeight;

                debug("Storing current size", width, height);

                // Store the size of the element sync here, so that multiple scroll events may be ignored in the event listeners.
                // Otherwise the if-check in handleScroll is useless.
                storeCurrentSize(element, width, height);

                // Since we delay the processing of the batch, there is a risk that uninstall has been called before the batch gets to execute.
                // Since there is no way to cancel the fn executions, we need to add an uninstall guard to all fns of the batch.

                batchProcessor.add(0, function performUpdateChildSizes() {
                    if (!sizeChanged) {
                        return;
                    }

                    if (!getState(element)) {
                        debug("Aborting because element has been uninstalled");
                        return;
                    }

                    if (!areElementsInjected()) {
                        debug("Aborting because element container has not been initialized");
                        return;
                    }

                    if (options.debug) {
                        var w = element.offsetWidth;
                        var h = element.offsetHeight;

                        if (w !== width || h !== height) {
                            reporter.warn(idHandler.get(element), "Scroll: Size changed before updating detector elements.");
                        }
                    }

                    updateChildSizes(element, width, height);
                });

                batchProcessor.add(1, function updateScrollbars() {
                    // This function needs to be invoked event though the size is unchanged. The element could have been resized very quickly and then
                    // been restored to the original size, which will have changed the scrollbar positions.

                    if (!getState(element)) {
                        debug("Aborting because element has been uninstalled");
                        return;
                    }

                    if (!areElementsInjected()) {
                        debug("Aborting because element container has not been initialized");
                        return;
                    }

                    positionScrollbars(element, width, height);
                });

                if (sizeChanged && done) {
                    batchProcessor.add(2, function () {
                        if (!getState(element)) {
                            debug("Aborting because element has been uninstalled");
                            return;
                        }

                        if (!areElementsInjected()) {
                          debug("Aborting because element container has not been initialized");
                          return;
                        }

                        done();
                    });
                }
            }

            function areElementsInjected() {
                return !!getState(element).container;
            }

            function notifyListenersIfNeeded() {
                function isFirstNotify() {
                    return getState(element).lastNotifiedWidth === undefined;
                }

                debug("notifyListenersIfNeeded invoked");

                var state = getState(element);

                // Don't notify if the current size is the start size, and this is the first notification.
                if (isFirstNotify() && state.lastWidth === state.startSize.width && state.lastHeight === state.startSize.height) {
                    return debug("Not notifying: Size is the same as the start size, and there has been no notification yet.");
                }

                // Don't notify if the size already has been notified.
                if (state.lastWidth === state.lastNotifiedWidth && state.lastHeight === state.lastNotifiedHeight) {
                    return debug("Not notifying: Size already notified");
                }


                debug("Current size not notified, notifying...");
                state.lastNotifiedWidth = state.lastWidth;
                state.lastNotifiedHeight = state.lastHeight;
                forEach(getState(element).listeners, function (listener) {
                    listener(element);
                });
            }

            function handleRender() {
                debug("startanimation triggered.");

                if (isUnrendered(element)) {
                    debug("Ignoring since element is still unrendered...");
                    return;
                }

                debug("Element rendered.");
                var expand = getExpandElement(element);
                var shrink = getShrinkElement(element);
                if (expand.scrollLeft === 0 || expand.scrollTop === 0 || shrink.scrollLeft === 0 || shrink.scrollTop === 0) {
                    debug("Scrollbars out of sync. Updating detector elements...");
                    updateDetectorElements(notifyListenersIfNeeded);
                }
            }

            function handleScroll() {
                debug("Scroll detected.");

                if (isUnrendered(element)) {
                    // Element is still unrendered. Skip this scroll event.
                    debug("Scroll event fired while unrendered. Ignoring...");
                    return;
                }

                updateDetectorElements(notifyListenersIfNeeded);
            }

            debug("registerListenersAndPositionElements invoked.");

            if (!getState(element)) {
                debug("Aborting because element has been uninstalled");
                return;
            }

            getState(element).onRendered = handleRender;
            getState(element).onExpand = handleScroll;
            getState(element).onShrink = handleScroll;

            var style = getState(element).style;
            updateChildSizes(element, style.width, style.height);
        }

        function finalizeDomMutation() {
            debug("finalizeDomMutation invoked.");

            if (!getState(element)) {
                debug("Aborting because element has been uninstalled");
                return;
            }

            var style = getState(element).style;
            storeCurrentSize(element, style.width, style.height);
            positionScrollbars(element, style.width, style.height);
        }

        function ready() {
            callback(element);
        }

        function install() {
            debug("Installing...");
            initListeners();
            storeStartSize();

            batchProcessor.add(0, storeStyle);
            batchProcessor.add(1, injectScrollElements);
            batchProcessor.add(2, registerListenersAndPositionElements);
            batchProcessor.add(3, finalizeDomMutation);
            batchProcessor.add(4, ready);
        }

        debug("Making detectable...");

        if (isDetached(element)) {
            debug("Element is detached");

            injectContainerElement();

            debug("Waiting until element is attached...");

            getState(element).onRendered = function () {
                debug("Element is now attached");
                install();
            };
        } else {
            install();
        }
    }

    function uninstall(element) {
        var state = getState(element);

        if (!state) {
            // Uninstall has been called on a non-erd element.
            return;
        }

        // Uninstall may have been called in the following scenarios:
        // (1) Right between the sync code and async batch (here state.busy = true, but nothing have been registered or injected).
        // (2) In the ready callback of the last level of the batch by another element (here, state.busy = true, but all the stuff has been injected).
        // (3) After the installation process (here, state.busy = false and all the stuff has been injected).
        // So to be on the safe side, let's check for each thing before removing.

        // We need to remove the event listeners, because otherwise the event might fire on an uninstall element which results in an error when trying to get the state of the element.
        state.onExpandScroll && removeEvent(getExpandElement(element), "scroll", state.onExpandScroll);
        state.onShrinkScroll && removeEvent(getShrinkElement(element), "scroll", state.onShrinkScroll);
        state.onAnimationStart && removeEvent(state.container, "animationstart", state.onAnimationStart);

        state.container && element.removeChild(state.container);
    }

    return {
        makeDetectable: makeDetectable,
        addListener: addListener,
        uninstall: uninstall,
        initDocument: initDocument
    };
};


/***/ }),

/***/ "ca5a":
/***/ (function(module, exports) {

var id = 0;
var px = Math.random();
module.exports = function (key) {
  return 'Symbol('.concat(key === undefined ? '' : key, ')_', (++id + px).toString(36));
};


/***/ }),

/***/ "cadf":
/***/ (function(module, exports, __webpack_require__) {

var addToUnscopables = __webpack_require__("9c6c");
var step = __webpack_require__("d53b");
var Iterators = __webpack_require__("84f2");
var toIObject = __webpack_require__("6821");

// 22.1.3.4 Array.prototype.entries()
// 22.1.3.13 Array.prototype.keys()
// 22.1.3.29 Array.prototype.values()
// 22.1.3.30 Array.prototype[@@iterator]()
module.exports = __webpack_require__("01f9")(Array, 'Array', function (iterated, kind) {
  this._t = toIObject(iterated); // target
  this._i = 0;                   // next index
  this._k = kind;                // kind
// 22.1.5.2.1 %ArrayIteratorPrototype%.next()
}, function () {
  var O = this._t;
  var kind = this._k;
  var index = this._i++;
  if (!O || index >= O.length) {
    this._t = undefined;
    return step(1);
  }
  if (kind == 'keys') return step(0, index);
  if (kind == 'values') return step(0, O[index]);
  return step(0, [index, O[index]]);
}, 'values');

// argumentsList[@@iterator] is %ArrayProto_values% (9.4.4.6, 9.4.4.7)
Iterators.Arguments = Iterators.Array;

addToUnscopables('keys');
addToUnscopables('values');
addToUnscopables('entries');


/***/ }),

/***/ "cb7c":
/***/ (function(module, exports, __webpack_require__) {

var isObject = __webpack_require__("d3f4");
module.exports = function (it) {
  if (!isObject(it)) throw TypeError(it + ' is not an object!');
  return it;
};


/***/ }),

/***/ "ce10":
/***/ (function(module, exports, __webpack_require__) {

var has = __webpack_require__("69a8");
var toIObject = __webpack_require__("6821");
var arrayIndexOf = __webpack_require__("c366")(false);
var IE_PROTO = __webpack_require__("613b")('IE_PROTO');

module.exports = function (object, names) {
  var O = toIObject(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) if (key != IE_PROTO) has(O, key) && result.push(key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (has(O, key = names[i++])) {
    ~arrayIndexOf(result, key) || result.push(key);
  }
  return result;
};


/***/ }),

/***/ "d3f4":
/***/ (function(module, exports) {

module.exports = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};


/***/ }),

/***/ "d53b":
/***/ (function(module, exports) {

module.exports = function (done, value) {
  return { value: value, done: !!done };
};


/***/ }),

/***/ "d6eb":
/***/ (function(module, exports, __webpack_require__) {


var prop = "_erd";

function initState(element) {
    element[prop] = {};
    return getState(element);
}

function getState(element) {
    return element[prop];
}

function cleanState(element) {
    delete element[prop];
}

module.exports = {
    initState: initState,
    getState: getState,
    cleanState: cleanState
};


/***/ }),

/***/ "d8e8":
/***/ (function(module, exports) {

module.exports = function (it) {
  if (typeof it != 'function') throw TypeError(it + ' is not a function!');
  return it;
};


/***/ }),

/***/ "e11e":
/***/ (function(module, exports) {

// IE 8- don't enum bug keys
module.exports = (
  'constructor,hasOwnProperty,isPrototypeOf,propertyIsEnumerable,toLocaleString,toString,valueOf'
).split(',');


/***/ }),

/***/ "e279":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
/* harmony import */ var _node_modules_vue_style_loader_index_js_ref_6_oneOf_1_0_node_modules_css_loader_index_js_ref_6_oneOf_1_1_node_modules_vue_loader_lib_loaders_stylePostLoader_js_node_modules_postcss_loader_src_index_js_ref_6_oneOf_1_2_node_modules_postcss_loader_src_index_js_ref_6_oneOf_1_3_node_modules_cache_loader_dist_cjs_js_ref_0_0_node_modules_vue_loader_lib_index_js_vue_loader_options_GridLayout_vue_vue_type_style_index_0_lang_css___WEBPACK_IMPORTED_MODULE_0__ = __webpack_require__("1156");
/* unused harmony reexport * */


/***/ }),

/***/ "eec4":
/***/ (function(module, exports, __webpack_require__) {


var forEach                 = __webpack_require__("b770").forEach;
var elementUtilsMaker       = __webpack_require__("5be5");
var listenerHandlerMaker    = __webpack_require__("49ad");
var idGeneratorMaker        = __webpack_require__("2cef");
var idHandlerMaker          = __webpack_require__("5058");
var reporterMaker           = __webpack_require__("abb4");
var browserDetector         = __webpack_require__("18e9");
var batchProcessorMaker     = __webpack_require__("c274");
var stateHandler            = __webpack_require__("d6eb");

//Detection strategies.
var objectStrategyMaker     = __webpack_require__("18d2");
var scrollStrategyMaker     = __webpack_require__("c946");

function isCollection(obj) {
    return Array.isArray(obj) || obj.length !== undefined;
}

function toArray(collection) {
    if (!Array.isArray(collection)) {
        var array = [];
        forEach(collection, function (obj) {
            array.push(obj);
        });
        return array;
    } else {
        return collection;
    }
}

function isElement(obj) {
    return obj && obj.nodeType === 1;
}

/**
 * @typedef idHandler
 * @type {object}
 * @property {function} get Gets the resize detector id of the element.
 * @property {function} set Generate and sets the resize detector id of the element.
 */

/**
 * @typedef Options
 * @type {object}
 * @property {boolean} callOnAdd    Determines if listeners should be called when they are getting added.
                                    Default is true. If true, the listener is guaranteed to be called when it has been added.
                                    If false, the listener will not be guarenteed to be called when it has been added (does not prevent it from being called).
 * @property {idHandler} idHandler  A custom id handler that is responsible for generating, setting and retrieving id's for elements.
                                    If not provided, a default id handler will be used.
 * @property {reporter} reporter    A custom reporter that handles reporting logs, warnings and errors.
                                    If not provided, a default id handler will be used.
                                    If set to false, then nothing will be reported.
 * @property {boolean} debug        If set to true, the the system will report debug messages as default for the listenTo method.
 */

/**
 * Creates an element resize detector instance.
 * @public
 * @param {Options?} options Optional global options object that will decide how this instance will work.
 */
module.exports = function(options) {
    options = options || {};

    //idHandler is currently not an option to the listenTo function, so it should not be added to globalOptions.
    var idHandler;

    if (options.idHandler) {
        // To maintain compatability with idHandler.get(element, readonly), make sure to wrap the given idHandler
        // so that readonly flag always is true when it's used here. This may be removed next major version bump.
        idHandler = {
            get: function (element) { return options.idHandler.get(element, true); },
            set: options.idHandler.set
        };
    } else {
        var idGenerator = idGeneratorMaker();
        var defaultIdHandler = idHandlerMaker({
            idGenerator: idGenerator,
            stateHandler: stateHandler
        });
        idHandler = defaultIdHandler;
    }

    //reporter is currently not an option to the listenTo function, so it should not be added to globalOptions.
    var reporter = options.reporter;

    if(!reporter) {
        //If options.reporter is false, then the reporter should be quiet.
        var quiet = reporter === false;
        reporter = reporterMaker(quiet);
    }

    //batchProcessor is currently not an option to the listenTo function, so it should not be added to globalOptions.
    var batchProcessor = getOption(options, "batchProcessor", batchProcessorMaker({ reporter: reporter }));

    //Options to be used as default for the listenTo function.
    var globalOptions = {};
    globalOptions.callOnAdd     = !!getOption(options, "callOnAdd", true);
    globalOptions.debug         = !!getOption(options, "debug", false);

    var eventListenerHandler    = listenerHandlerMaker(idHandler);
    var elementUtils            = elementUtilsMaker({
        stateHandler: stateHandler
    });

    //The detection strategy to be used.
    var detectionStrategy;
    var desiredStrategy = getOption(options, "strategy", "object");
    var importantCssRules = getOption(options, "important", false);
    var strategyOptions = {
        reporter: reporter,
        batchProcessor: batchProcessor,
        stateHandler: stateHandler,
        idHandler: idHandler,
        important: importantCssRules
    };

    if(desiredStrategy === "scroll") {
        if (browserDetector.isLegacyOpera()) {
            reporter.warn("Scroll strategy is not supported on legacy Opera. Changing to object strategy.");
            desiredStrategy = "object";
        } else if (browserDetector.isIE(9)) {
            reporter.warn("Scroll strategy is not supported on IE9. Changing to object strategy.");
            desiredStrategy = "object";
        }
    }

    if(desiredStrategy === "scroll") {
        detectionStrategy = scrollStrategyMaker(strategyOptions);
    } else if(desiredStrategy === "object") {
        detectionStrategy = objectStrategyMaker(strategyOptions);
    } else {
        throw new Error("Invalid strategy name: " + desiredStrategy);
    }

    //Calls can be made to listenTo with elements that are still being installed.
    //Also, same elements can occur in the elements list in the listenTo function.
    //With this map, the ready callbacks can be synchronized between the calls
    //so that the ready callback can always be called when an element is ready - even if
    //it wasn't installed from the function itself.
    var onReadyCallbacks = {};

    /**
     * Makes the given elements resize-detectable and starts listening to resize events on the elements. Calls the event callback for each event for each element.
     * @public
     * @param {Options?} options Optional options object. These options will override the global options. Some options may not be overriden, such as idHandler.
     * @param {element[]|element} elements The given array of elements to detect resize events of. Single element is also valid.
     * @param {function} listener The callback to be executed for each resize event for each element.
     */
    function listenTo(options, elements, listener) {
        function onResizeCallback(element) {
            var listeners = eventListenerHandler.get(element);
            forEach(listeners, function callListenerProxy(listener) {
                listener(element);
            });
        }

        function addListener(callOnAdd, element, listener) {
            eventListenerHandler.add(element, listener);

            if(callOnAdd) {
                listener(element);
            }
        }

        //Options object may be omitted.
        if(!listener) {
            listener = elements;
            elements = options;
            options = {};
        }

        if(!elements) {
            throw new Error("At least one element required.");
        }

        if(!listener) {
            throw new Error("Listener required.");
        }

        if (isElement(elements)) {
            // A single element has been passed in.
            elements = [elements];
        } else if (isCollection(elements)) {
            // Convert collection to array for plugins.
            // TODO: May want to check so that all the elements in the collection are valid elements.
            elements = toArray(elements);
        } else {
            return reporter.error("Invalid arguments. Must be a DOM element or a collection of DOM elements.");
        }

        var elementsReady = 0;

        var callOnAdd = getOption(options, "callOnAdd", globalOptions.callOnAdd);
        var onReadyCallback = getOption(options, "onReady", function noop() {});
        var debug = getOption(options, "debug", globalOptions.debug);

        forEach(elements, function attachListenerToElement(element) {
            if (!stateHandler.getState(element)) {
                stateHandler.initState(element);
                idHandler.set(element);
            }

            var id = idHandler.get(element);

            debug && reporter.log("Attaching listener to element", id, element);

            if(!elementUtils.isDetectable(element)) {
                debug && reporter.log(id, "Not detectable.");
                if(elementUtils.isBusy(element)) {
                    debug && reporter.log(id, "System busy making it detectable");

                    //The element is being prepared to be detectable. Do not make it detectable.
                    //Just add the listener, because the element will soon be detectable.
                    addListener(callOnAdd, element, listener);
                    onReadyCallbacks[id] = onReadyCallbacks[id] || [];
                    onReadyCallbacks[id].push(function onReady() {
                        elementsReady++;

                        if(elementsReady === elements.length) {
                            onReadyCallback();
                        }
                    });
                    return;
                }

                debug && reporter.log(id, "Making detectable...");
                //The element is not prepared to be detectable, so do prepare it and add a listener to it.
                elementUtils.markBusy(element, true);
                return detectionStrategy.makeDetectable({ debug: debug, important: importantCssRules }, element, function onElementDetectable(element) {
                    debug && reporter.log(id, "onElementDetectable");

                    if (stateHandler.getState(element)) {
                        elementUtils.markAsDetectable(element);
                        elementUtils.markBusy(element, false);
                        detectionStrategy.addListener(element, onResizeCallback);
                        addListener(callOnAdd, element, listener);

                        // Since the element size might have changed since the call to "listenTo", we need to check for this change,
                        // so that a resize event may be emitted.
                        // Having the startSize object is optional (since it does not make sense in some cases such as unrendered elements), so check for its existance before.
                        // Also, check the state existance before since the element may have been uninstalled in the installation process.
                        var state = stateHandler.getState(element);
                        if (state && state.startSize) {
                            var width = element.offsetWidth;
                            var height = element.offsetHeight;
                            if (state.startSize.width !== width || state.startSize.height !== height) {
                                onResizeCallback(element);
                            }
                        }

                        if(onReadyCallbacks[id]) {
                            forEach(onReadyCallbacks[id], function(callback) {
                                callback();
                            });
                        }
                    } else {
                        // The element has been unisntalled before being detectable.
                        debug && reporter.log(id, "Element uninstalled before being detectable.");
                    }

                    delete onReadyCallbacks[id];

                    elementsReady++;
                    if(elementsReady === elements.length) {
                        onReadyCallback();
                    }
                });
            }

            debug && reporter.log(id, "Already detecable, adding listener.");

            //The element has been prepared to be detectable and is ready to be listened to.
            addListener(callOnAdd, element, listener);
            elementsReady++;
        });

        if(elementsReady === elements.length) {
            onReadyCallback();
        }
    }

    function uninstall(elements) {
        if(!elements) {
            return reporter.error("At least one element is required.");
        }

        if (isElement(elements)) {
            // A single element has been passed in.
            elements = [elements];
        } else if (isCollection(elements)) {
            // Convert collection to array for plugins.
            // TODO: May want to check so that all the elements in the collection are valid elements.
            elements = toArray(elements);
        } else {
            return reporter.error("Invalid arguments. Must be a DOM element or a collection of DOM elements.");
        }

        forEach(elements, function (element) {
            eventListenerHandler.removeAllListeners(element);
            detectionStrategy.uninstall(element);
            stateHandler.cleanState(element);
        });
    }

    function initDocument(targetDocument) {
        detectionStrategy.initDocument && detectionStrategy.initDocument(targetDocument);
    }

    return {
        listenTo: listenTo,
        removeListener: eventListenerHandler.removeListener,
        removeAllListeners: eventListenerHandler.removeAllListeners,
        uninstall: uninstall,
        initDocument: initDocument
    };
};

function getOption(options, name, defaultValue) {
    var value = options[name];

    if((value === undefined || value === null) && defaultValue !== undefined) {
        return defaultValue;
    }

    return value;
}


/***/ }),

/***/ "f1ae":
/***/ (function(module, exports, __webpack_require__) {

var $defineProperty = __webpack_require__("86cc");
var createDesc = __webpack_require__("4630");

module.exports = function (object, index, value) {
  if (index in object) $defineProperty.f(object, index, createDesc(0, value));
  else object[index] = value;
};


/***/ }),

/***/ "f6fd":
/***/ (function(module, exports) {

// document.currentScript polyfill by Adam Miller

// MIT license

(function(document){
  var currentScript = "currentScript",
      scripts = document.getElementsByTagName('script'); // Live NodeList collection

  // If browser needs currentScript polyfill, add get currentScript() to the document object
  if (!(currentScript in document)) {
    Object.defineProperty(document, currentScript, {
      get: function(){

        // IE 6-10 supports script readyState
        // IE 10+ support stack trace
        try { throw new Error(); }
        catch (err) {

          // Find the second match for the "at" string to get file src url from stack.
          // Specifically works with the format of stack traces in IE.
          var i, res = ((/.*at [^\(]*\((.*):.+:.+\)$/ig).exec(err.stack) || [false])[1];

          // For all scripts on the page, if src matches or if ready state is interactive, return the script tag
          for(i in scripts){
            if(scripts[i].src == res || scripts[i].readyState == "interactive"){
              return scripts[i];
            }
          }

          // If no match, return null
          return null;
        }
      }
    });
  }
})(document);


/***/ }),

/***/ "f751":
/***/ (function(module, exports, __webpack_require__) {

// 19.1.3.1 Object.assign(target, source)
var $export = __webpack_require__("5ca1");

$export($export.S + $export.F, 'Object', { assign: __webpack_require__("7333") });


/***/ }),

/***/ "fa5b":
/***/ (function(module, exports, __webpack_require__) {

module.exports = __webpack_require__("5537")('native-function-to-string', Function.toString);


/***/ }),

/***/ "fab2":
/***/ (function(module, exports, __webpack_require__) {

var document = __webpack_require__("7726").document;
module.exports = document && document.documentElement;


/***/ }),

/***/ "fb15":
/***/ (function(module, __webpack_exports__, __webpack_require__) {
// ESM COMPAT FLAG
__webpack_require__.r(__webpack_exports__);

// EXPORTS
__webpack_require__.d(__webpack_exports__, "install", function() { return /* reexport */ components["d" /* install */]; });
__webpack_require__.d(__webpack_exports__, "GridLayout", function() { return /* reexport */ components["b" /* GridLayout */]; });
__webpack_require__.d(__webpack_exports__, "GridItem", function() { return /* reexport */ components["a" /* GridItem */]; });

// CONCATENATED MODULE: ./node_modules/@vue/cli-service/lib/commands/build/setPublicPath.js
// This file is imported into lib/wc client bundles.

if (typeof window !== 'undefined') {
  {
    __webpack_require__("f6fd");
  }

  var i;
  if ((i = window.document.currentScript) && (i = i.src.match(/(.+\/)[^/]+\.js(\?.*)?$/))) {
    __webpack_require__.p = i[1]; // eslint-disable-line
  }
}

// EXTERNAL MODULE: ./src/components/index.js
var components = __webpack_require__("2af9");

// CONCATENATED MODULE: ./node_modules/@vue/cli-service/lib/commands/build/entry-lib.js


/* harmony default export */ var entry_lib = __webpack_exports__["default"] = (components["c" /* default */]);



/***/ }),

/***/ "fca0":
/***/ (function(module, exports, __webpack_require__) {

// 20.1.2.2 Number.isFinite(number)
var $export = __webpack_require__("5ca1");
var _isFinite = __webpack_require__("7726").isFinite;

$export($export.S, 'Number', {
  isFinite: function isFinite(it) {
    return typeof it == 'number' && _isFinite(it);
  }
});


/***/ }),

/***/ "fdef":
/***/ (function(module, exports) {

module.exports = '\x09\x0A\x0B\x0C\x0D\x20\xA0\u1680\u180E\u2000\u2001\u2002\u2003' +
  '\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF';


/***/ })

/******/ })["default"];

});

var VueGridLayout = unwrapExports(vueGridLayout_common);

const widgetTypesEnum = {
  CARD_INDICATOR: 'card-indicator',
  CARD_LINK: 'card-link',
  CARD_BUTTON: 'card-button',
  LIST_CARD: 'list-card',
  LIST_LINK: 'list-link',
  LIST_BUTTON: 'list-button'
};

const widgetSubtypesEnum = {
  INDICATOR: 'indicator',
  LINK: 'link'
};

const widgetButtonTypesEnum = {
  SIMPLE: 'simple',
  MULTIPLE: 'multiple'
};

//

var script$U = {
  name: 'MeWidgetSkeleton',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$U = script$U;

/* template */
var __vue_render__$U = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "widget-skeleton" },
    [
      _c("BSkeleton", {
        attrs: { width: "100%", height: "100%", type: "button" }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$U = [];
__vue_render__$U._withStripped = true;

  /* style */
  const __vue_inject_styles__$U = function (inject) {
    if (!inject) return
    inject("data-v-2ebf239c_0", { source: ".widget-skeleton[data-v-2ebf239c] {\n  border-radius: 16px;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n}\n\n/*# sourceMappingURL=WidgetSkeleton.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/WidgetSkeleton.vue","WidgetSkeleton.vue"],"names":[],"mappings":"AAsBA;EACA,mBAAA;EACA,WAAA;EACA,YAAA;EACA,gBAAA;ACrBA;;AAEA,6CAA6C","file":"WidgetSkeleton.vue","sourcesContent":["<template>\n  <div class=\"widget-skeleton\">\n    <BSkeleton\n      width=\"100%\"\n      height=\"100%\"\n      type=\"button\"\n    />\n  </div>\n</template>\n\n<script>\nimport { BSkeleton } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeWidgetSkeleton',\n  components: {\n    BSkeleton\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-skeleton {\n    border-radius: 16px;\n    width: 100%;\n    height: 100%;\n    overflow: hidden;\n  }\n</style>\n",".widget-skeleton {\n  border-radius: 16px;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n}\n\n/*# sourceMappingURL=WidgetSkeleton.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$U = "data-v-2ebf239c";
  /* module identifier */
  const __vue_module_identifier__$U = undefined;
  /* functional template */
  const __vue_is_functional_template__$U = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$U, staticRenderFns: __vue_staticRenderFns__$U },
    __vue_inject_styles__$U,
    __vue_script__$U,
    __vue_scope_id__$U,
    __vue_is_functional_template__$U,
    __vue_module_identifier__$U,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$V = {
  name: 'MeWidgetError',
  props: {
    widgetLabel: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    image: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$V = script$V;

/* template */
var __vue_render__$V = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "widget-error", attrs: { "data-cy": "widget-error" } },
    [
      _vm.widgetLabel
        ? _c("span", { staticClass: "widget-label" }, [
            _vm._v("\n    " + _vm._s(_vm.widgetLabel) + "\n  ")
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("div", { staticClass: "error-content" }, [
        _vm.image
          ? _c("img", { attrs: { src: _vm.image, alt: _vm.title } })
          : _vm._e(),
        _vm._v(" "),
        _c("p", { staticClass: "error-title" }, [
          _vm._v("\n      " + _vm._s(_vm.title) + "\n    ")
        ]),
        _vm._v(" "),
        _c("p", { staticClass: "error-description" }, [
          _vm._v("\n      " + _vm._s(_vm.description) + "\n    ")
        ])
      ])
    ]
  )
};
var __vue_staticRenderFns__$V = [];
__vue_render__$V._withStripped = true;

  /* style */
  const __vue_inject_styles__$V = function (inject) {
    if (!inject) return
    inject("data-v-2586fa59_0", { source: ".widget-error[data-v-2586fa59] {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  height: 100%;\n  overflow: auto;\n}\n.widget-error .widget-label[data-v-2586fa59] {\n  color: var(--me-gray-3);\n  font-size: 12px;\n  font-weight: 600;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 24px;\n  padding: 8px 8px 16px;\n}\n.widget-error .error-content[data-v-2586fa59] {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  flex-basis: 100%;\n}\n.widget-error .error-content .error-title[data-v-2586fa59] {\n  color: var(--me-gray-2);\n  margin-bottom: initial;\n  font-weight: 600;\n  font-size: 16px;\n  line-height: 22px;\n}\n.widget-error .error-content .error-description[data-v-2586fa59] {\n  color: var(--me-gray-3);\n  text-align: center;\n  font-size: 14px;\n  line-height: 22px;\n}\n\n/*# sourceMappingURL=WidgetError.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/WidgetError.vue","WidgetError.vue"],"names":[],"mappings":"AAgDA;EACA,aAAA;EACA,sBAAA;EACA,WAAA;EACA,YAAA;EACA,cAAA;AC/CA;ADiDA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,YAAA;EACA,qBAAA;AC/CA;ADkDA;EACA,aAAA;EACA,sBAAA;EACA,uBAAA;EACA,mBAAA;EACA,gBAAA;AChDA;ADkDA;EACA,uBAAA;EACA,sBAAA;EACA,gBAAA;EACA,eAAA;EACA,iBAAA;AChDA;ADmDA;EACA,uBAAA;EACA,kBAAA;EACA,eAAA;EACA,iBAAA;ACjDA;;AAEA,0CAA0C","file":"WidgetError.vue","sourcesContent":["<template>\n  <div class=\"widget-error\" data-cy=\"widget-error\">\n    <span v-if=\"widgetLabel\" class=\"widget-label\">\n      {{ widgetLabel }}\n    </span>\n\n    <div class=\"error-content\">\n      <img\n        v-if=\"image\"\n        :src=\"image\"\n        :alt=\"title\"\n      >\n      <p class=\"error-title\">\n        {{ title }}\n      </p>\n\n      <p class=\"error-description\">\n        {{ description }}\n      </p>\n    </div>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeWidgetError',\n  props: {\n    widgetLabel: {\n      type: String,\n      default: ''\n    },\n    title: {\n      type: String,\n      default: ''\n    },\n    description: {\n      type: String,\n      default: ''\n    },\n    image: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-error {\n    display: flex;\n    flex-direction: column;\n    width: 100%;\n    height: 100%;\n    overflow: auto;\n\n    .widget-label {\n      color: var(--me-gray-3);\n      font-size: 12px;\n      font-weight: 600;\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n      height: 24px;\n      padding: 8px 8px 16px;\n    }\n\n    .error-content {\n      display: flex;\n      flex-direction: column;\n      justify-content: center;\n      align-items: center;\n      flex-basis: 100%;\n\n      .error-title {\n        color: var(--me-gray-2);\n        margin-bottom: initial;\n        font-weight: 600;\n        font-size: 16px;\n        line-height: 22px;\n      }\n\n      .error-description {\n        color: var(--me-gray-3);\n        text-align: center;\n        font-size: 14px;\n        line-height: 22px;\n      }\n    }\n  }\n</style>\n",".widget-error {\n  display: flex;\n  flex-direction: column;\n  width: 100%;\n  height: 100%;\n  overflow: auto;\n}\n.widget-error .widget-label {\n  color: var(--me-gray-3);\n  font-size: 12px;\n  font-weight: 600;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 24px;\n  padding: 8px 8px 16px;\n}\n.widget-error .error-content {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  flex-basis: 100%;\n}\n.widget-error .error-content .error-title {\n  color: var(--me-gray-2);\n  margin-bottom: initial;\n  font-weight: 600;\n  font-size: 16px;\n  line-height: 22px;\n}\n.widget-error .error-content .error-description {\n  color: var(--me-gray-3);\n  text-align: center;\n  font-size: 14px;\n  line-height: 22px;\n}\n\n/*# sourceMappingURL=WidgetError.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$V = "data-v-2586fa59";
  /* module identifier */
  const __vue_module_identifier__$V = undefined;
  /* functional template */
  const __vue_is_functional_template__$V = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$V, staticRenderFns: __vue_staticRenderFns__$V },
    __vue_inject_styles__$V,
    __vue_script__$V,
    __vue_scope_id__$V,
    __vue_is_functional_template__$V,
    __vue_module_identifier__$V,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$W = {
  name: 'MeWidgetContent',
  components: {
    WidgetError: __vue_component__$V
  },

  props: {
    disabledClick: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    href: {
      type: String,
      default: ''
    },
    hideEdit: {
      type: Boolean,
      default: false
    },
    hasAction: {
      type: Boolean,
      default: false
    },
    deletable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    error: {
      type: Object,
      default: () => null
    },
    click: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      clicked: false
    }
  },
  computed: {
    widgetActions() {
      let actions = [];
      if (this.editable) {
        actions = [...actions, { label: this.$toolkit.t('dropdownCriterion.actions.edit'), icon: 'me-icon-l icon-pen-to-square', click: this.editWidget }];
      }
      if (this.deletable) {
        actions = [...actions, { label: this.$toolkit.t('dropdownCriterion.actions.delete'), icon: 'me-icon-l icon-trash', click: this.handleDelete }];
      }
      return actions
    }
  },

  methods: {
    editWidget() {
      this.$emit('editWidget');
    },

    handleDelete() {
      this.$emit('deleteWidget');
    },

    handleWidgetClick() {
      if (this.disabledClick) return

      if (this.click && typeof this.click === 'function') {
        this.click();
        return
      }

      if (this.href) {
        window.location.href = this.href;
      }
    }
  }
};

/* script */
const __vue_script__$W = script$W;

/* template */
var __vue_render__$W = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "widget-content",
      class: { "widget-content--link": _vm.href || _vm.hasAction },
      attrs: { "data-cy": "widget-content" },
      on: { click: _vm.handleWidgetClick }
    },
    [
      _vm.error
        ? [_c("WidgetError", _vm._b({}, "WidgetError", _vm.error, false))]
        : [
            _vm._t("default"),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "widget-content__header" },
              [
                _vm._t("header", [
                  _vm._t("title", [
                    _c(
                      "span",
                      { staticClass: "widget-content__header__title" },
                      [
                        _vm._v(
                          "\n            " + _vm._s(_vm.title) + "\n          "
                        )
                      ]
                    )
                  ]),
                  _vm._v(" "),
                  _vm.widgetActions.length
                    ? _c("me-drop-down", {
                        staticClass: "dropdown-button",
                        attrs: {
                          items: _vm.widgetActions,
                          "no-caret": "",
                          dropright: "",
                          variant: "link"
                        },
                        scopedSlots: _vm._u(
                          [
                            {
                              key: "button-content",
                              fn: function() {
                                return [
                                  _c("me-icon", {
                                    attrs: {
                                      icon: "me-icon-s icon-ellipsis-vertical",
                                      color: "var(--me-gray-5 )"
                                    }
                                  })
                                ]
                              },
                              proxy: true
                            }
                          ],
                          null,
                          false,
                          493566480
                        )
                      })
                    : _vm._e()
                ])
              ],
              2
            ),
            _vm._v(" "),
            _vm._t("body")
          ]
    ],
    2
  )
};
var __vue_staticRenderFns__$W = [];
__vue_render__$W._withStripped = true;

  /* style */
  const __vue_inject_styles__$W = function (inject) {
    if (!inject) return
    inject("data-v-3c75ed7f_0", { source: ".widget-content[data-v-3c75ed7f] {\n  height: 100%;\n  width: 100%;\n  padding: 8px 8px 16px 8px;\n  border-radius: 16px;\n  background-color: var(--me-white);\n}\n.widget-content:hover .dropdown-button[data-v-3c75ed7f] {\n  display: inline-block;\n  position: absolute;\n  right: 16px;\n}\n.widget-content:hover .dropdown-button[data-v-3c75ed7f]  button {\n  text-decoration: none;\n}\n.widget-content--link[data-v-3c75ed7f] {\n  cursor: pointer;\n}\n.widget-content .dropdown-button[data-v-3c75ed7f] {\n  display: none;\n}\n.widget-content__header[data-v-3c75ed7f] {\n  display: flex;\n  justify-content: space-between;\n  padding: 8px 8px 16px 8px;\n}\n.widget-content__header__title[data-v-3c75ed7f] {\n  color: var(--me-gray-3);\n  font-size: 12px;\n  font-weight: 600;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 24px;\n}\n\n/*# sourceMappingURL=WidgetContent.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/WidgetContent.vue","WidgetContent.vue"],"names":[],"mappings":"AAqIA;EACA,YAAA;EACA,WAAA;EACA,yBAAA;EACA,mBAAA;EACA,iCAAA;ACpIA;ADuIA;EACA,qBAAA;EACA,kBAAA;EACA,WAAA;ACrIA;ADuIA;EACA,qBAAA;ACrIA;AD0IA;EACA,eAAA;ACxIA;AD2IA;EACA,aAAA;ACzIA;AD4IA;EACA,aAAA;EACA,8BAAA;EACA,yBAAA;AC1IA;AD4IA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,YAAA;AC1IA;;AAEA,4CAA4C","file":"WidgetContent.vue","sourcesContent":["<template>\n  <div\n    @click=\"handleWidgetClick\"\n    :class=\"{'widget-content--link': href || hasAction}\"\n    class=\"widget-content\"\n    data-cy=\"widget-content\"\n  >\n    <template v-if=\"error\">\n      <WidgetError v-bind=\"error\" />\n    </template>\n\n    <template v-else>\n      <slot />\n\n      <div class=\"widget-content__header\">\n        <slot name=\"header\">\n          <slot name=\"title\">\n            <span class=\"widget-content__header__title\">\n              {{ title }}\n            </span>\n          </slot>\n\n          <me-drop-down\n            v-if=\"widgetActions.length\"\n            :items=\"widgetActions\"\n            no-caret\n            dropright\n            class=\"dropdown-button\"\n            variant=\"link\"\n          >\n            <template v-slot:button-content>\n              <me-icon icon=\"me-icon-s icon-ellipsis-vertical\" color=\"var(--me-gray-5 )\" />\n            </template>\n          </me-drop-down>\n        </slot>\n      </div>\n\n      <slot name=\"body\" />\n    </template>\n  </div>\n</template>\n\n<script>\nimport WidgetError from './WidgetError.vue'\n\nexport default {\n  name: 'MeWidgetContent',\n  components: {\n    WidgetError\n  },\n\n  props: {\n    disabledClick: {\n      type: Boolean,\n      default: false\n    },\n    title: {\n      type: String,\n      default: ''\n    },\n    href: {\n      type: String,\n      default: ''\n    },\n    hideEdit: {\n      type: Boolean,\n      default: false\n    },\n    hasAction: {\n      type: Boolean,\n      default: false\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    error: {\n      type: Object,\n      default: () => null\n    },\n    click: {\n      type: Function,\n      default: null\n    }\n  },\n  data() {\n    return {\n      clicked: false\n    }\n  },\n  computed: {\n    widgetActions() {\n      let actions = []\n      if (this.editable) {\n        actions = [...actions, { label: this.$toolkit.t('dropdownCriterion.actions.edit'), icon: 'me-icon-l icon-pen-to-square', click: this.editWidget }]\n      }\n      if (this.deletable) {\n        actions = [...actions, { label: this.$toolkit.t('dropdownCriterion.actions.delete'), icon: 'me-icon-l icon-trash', click: this.handleDelete }]\n      }\n      return actions\n    }\n  },\n\n  methods: {\n    editWidget() {\n      this.$emit('editWidget')\n    },\n\n    handleDelete() {\n      this.$emit('deleteWidget')\n    },\n\n    handleWidgetClick() {\n      if (this.disabledClick) return\n\n      if (this.click && typeof this.click === 'function') {\n        this.click()\n        return\n      }\n\n      if (this.href) {\n        window.location.href = this.href\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-content {\n    height: 100%;\n    width: 100%;\n    padding: 8px 8px 16px 8px;\n    border-radius: 16px;\n    background-color: var(--me-white);\n\n    &:hover {\n      .dropdown-button {\n      display: inline-block;\n      position: absolute;\n      right: 16px;\n\n      ::v-deep button {\n        text-decoration: none;\n      }\n     }\n    }\n\n    &--link {\n      cursor: pointer;\n    }\n\n    .dropdown-button {\n      display: none;\n    }\n\n    &__header {\n      display: flex;\n      justify-content: space-between;\n      padding: 8px 8px 16px 8px;\n\n      &__title {\n        color: var(--me-gray-3);\n        font-size: 12px;\n        font-weight: 600;\n        white-space: nowrap;\n        overflow: hidden;\n        text-overflow: ellipsis;\n        height: 24px;\n      }\n    }\n  }\n</style>\n",".widget-content {\n  height: 100%;\n  width: 100%;\n  padding: 8px 8px 16px 8px;\n  border-radius: 16px;\n  background-color: var(--me-white);\n}\n.widget-content:hover .dropdown-button {\n  display: inline-block;\n  position: absolute;\n  right: 16px;\n}\n.widget-content:hover .dropdown-button ::v-deep button {\n  text-decoration: none;\n}\n.widget-content--link {\n  cursor: pointer;\n}\n.widget-content .dropdown-button {\n  display: none;\n}\n.widget-content__header {\n  display: flex;\n  justify-content: space-between;\n  padding: 8px 8px 16px 8px;\n}\n.widget-content__header__title {\n  color: var(--me-gray-3);\n  font-size: 12px;\n  font-weight: 600;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 24px;\n}\n\n/*# sourceMappingURL=WidgetContent.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$W = "data-v-3c75ed7f";
  /* module identifier */
  const __vue_module_identifier__$W = undefined;
  /* functional template */
  const __vue_is_functional_template__$W = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$W, staticRenderFns: __vue_staticRenderFns__$W },
    __vue_inject_styles__$W,
    __vue_script__$W,
    __vue_scope_id__$W,
    __vue_is_functional_template__$W,
    __vue_module_identifier__$W,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$X = {
  name: 'MeWidgetCardIndicator',
  components: {
    Badge: __vue_component__$x,
    WidgetContent: __vue_component__$W
  },

  props: {
    variant: {
      type: String,
      default: ''
    },
    disabledClick: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      default: null
    },
    click: {
      type: Function,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: Number,
      default: 0
    },
    badgeValue: {
      type: Number,
      default: 0
    },
    deletable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    error: {
      type: Object,
      default: () => null
    }
  }
};

/* script */
const __vue_script__$X = script$X;

/* template */
var __vue_render__$X = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("WidgetContent", {
    staticClass: "widget-card-indicator",
    class: _vm.error ? "" : _vm.variant,
    attrs: {
      editable: _vm.editable,
      deletable: _vm.deletable,
      disabledClick: _vm.disabledClick,
      href: _vm.href,
      click: _vm.click,
      error: _vm.error,
      "has-action": ""
    },
    on: {
      editWidget: function($event) {
        return _vm.$emit("edit")
      },
      deleteWidget: function($event) {
        return _vm.$emit("delete")
      }
    },
    scopedSlots: _vm._u([
      {
        key: "title",
        fn: function() {
          return [
            _c("span", { staticClass: "widget-title", class: _vm.variant }, [
              _vm._v("\n      " + _vm._s(_vm.value) + "\n    ")
            ])
          ]
        },
        proxy: true
      },
      {
        key: "body",
        fn: function() {
          return [
            _c(
              "div",
              { staticClass: "widget-card-body" },
              [
                _c(
                  "span",
                  { staticClass: "widget-label", class: _vm.variant },
                  [_vm._v("\n        " + _vm._s(_vm.label) + "\n      ")]
                ),
                _vm._v(" "),
                _vm.badgeValue
                  ? _c(
                      "Badge",
                      {
                        staticClass: "widget-badge-primary",
                        attrs: {
                          appearance: _vm.variant ? _vm.variant : "primary"
                        }
                      },
                      [
                        _vm._v(
                          "\n        +" + _vm._s(_vm.badgeValue) + "\n      "
                        )
                      ]
                    )
                  : _vm._e(),
                _vm._v(" "),
                _vm.badgeValue
                  ? _c(
                      "Badge",
                      {
                        staticClass: "widget-badge-light-primary",
                        attrs: {
                          appearance: _vm.variant
                            ? _vm.variant
                            : "light-primary"
                        }
                      },
                      [
                        _vm._v(
                          "\n        +" + _vm._s(_vm.badgeValue) + "\n      "
                        )
                      ]
                    )
                  : _vm._e()
              ],
              1
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$X = [];
__vue_render__$X._withStripped = true;

  /* style */
  const __vue_inject_styles__$X = function (inject) {
    if (!inject) return
    inject("data-v-724904d1_0", { source: ".danger[data-v-724904d1] {\n  background-color: var(--me-danger-7) !important;\n  color: var(--me-danger-2) !important;\n}\n.widget-card-indicator[data-v-724904d1]:hover {\n  background-color: var(--me-primary-7);\n}\n.widget-card-indicator:hover .widget-card-body .widget-label[data-v-724904d1] {\n  color: var(--me-primary-2);\n}\n.widget-card-indicator:hover .widget-card-body .widget-badge-primary[data-v-724904d1] {\n  display: inline-block;\n}\n.widget-card-indicator:hover .widget-card-body .widget-badge-light-primary[data-v-724904d1] {\n  display: none;\n}\n.widget-card-indicator .widget-title[data-v-724904d1] {\n  color: var(--me-primary-2);\n  font-weight: 600;\n  font-size: 28px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 38px;\n}\n.widget-card-indicator .widget-card-body[data-v-724904d1] {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: 0 8px;\n}\n.widget-card-indicator .widget-card-body .widget-label[data-v-724904d1] {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding-right: 8px;\n}\n.widget-card-indicator .widget-card-body .widget-badge-primary[data-v-724904d1] {\n  display: none;\n}\n.widget-card-indicator .widget-card-body .widget-badge-light-primary[data-v-724904d1] {\n  display: inline-block;\n}\n\n/*# sourceMappingURL=WidgetCardIndicator.vue.map */", map: {"version":3,"sources":["WidgetCardIndicator.vue","/app/build/src/components/dashboard/widgets/card/WidgetCardIndicator.vue"],"names":[],"mappings":"AAAA;EACE,+CAA+C;EAC/C,oCAAoC;AACtC;ACuGA;EACA,qCAAA;ADpGA;ACuGA;EACA,0BAAA;ADrGA;ACwGA;EACA,qBAAA;ADtGA;ACyGA;EACA,aAAA;ADvGA;AC4GA;EACA,0BAAA;EACA,gBAAA;EACA,eAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,YAAA;AD1GA;AC6GA;EACA,aAAA;EACA,8BAAA;EACA,mBAAA;EACA,cAAA;AD3GA;AC6GA;EACA,uBAAA;EACA,eAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,kBAAA;AD3GA;AC8GA;EACA,aAAA;AD5GA;AC+GA;EACA,qBAAA;AD7GA;;AAEA,kDAAkD","file":"WidgetCardIndicator.vue","sourcesContent":[".danger {\n  background-color: var(--me-danger-7) !important;\n  color: var(--me-danger-2) !important;\n}\n\n.widget-card-indicator:hover {\n  background-color: var(--me-primary-7);\n}\n.widget-card-indicator:hover .widget-card-body .widget-label {\n  color: var(--me-primary-2);\n}\n.widget-card-indicator:hover .widget-card-body .widget-badge-primary {\n  display: inline-block;\n}\n.widget-card-indicator:hover .widget-card-body .widget-badge-light-primary {\n  display: none;\n}\n.widget-card-indicator .widget-title {\n  color: var(--me-primary-2);\n  font-weight: 600;\n  font-size: 28px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 38px;\n}\n.widget-card-indicator .widget-card-body {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  padding: 0 8px;\n}\n.widget-card-indicator .widget-card-body .widget-label {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding-right: 8px;\n}\n.widget-card-indicator .widget-card-body .widget-badge-primary {\n  display: none;\n}\n.widget-card-indicator .widget-card-body .widget-badge-light-primary {\n  display: inline-block;\n}\n\n/*# sourceMappingURL=WidgetCardIndicator.vue.map */","<template>\n  <WidgetContent\n    :editable=\"editable\"\n    :deletable=\"deletable\"\n    :disabledClick=\"disabledClick\"\n    :href=\"href\"\n    :click=\"click\"\n    :error=\"error\"\n    :class=\"error ? '' : variant\"\n    @editWidget=\"$emit('edit')\"\n    @deleteWidget=\"$emit('delete')\"\n    has-action\n    class=\"widget-card-indicator\"\n  >\n    <template v-slot:title>\n      <span :class=\"variant\" class=\"widget-title\">\n        {{ value }}\n      </span>\n    </template>\n\n    <template v-slot:body>\n      <div class=\"widget-card-body\">\n        <span :class=\"variant\" class=\"widget-label\">\n          {{ label }}\n        </span>\n\n        <Badge\n          v-if=\"badgeValue\"\n          :appearance=\"variant ? variant : 'primary'\"\n          class=\"widget-badge-primary\"\n        >\n          +{{ badgeValue }}\n        </Badge>\n\n        <Badge\n          v-if=\"badgeValue\"\n          :appearance=\"variant ? variant : 'light-primary'\"\n          class=\"widget-badge-light-primary\"\n        >\n          +{{ badgeValue }}\n        </Badge>\n      </div>\n    </template>\n  </WidgetContent>\n</template>\n\n<script>\nimport Badge from '../../../Badge.vue'\nimport WidgetContent from '../WidgetContent.vue'\n\nexport default {\n  name: 'MeWidgetCardIndicator',\n  components: {\n    Badge,\n    WidgetContent\n  },\n\n  props: {\n    variant: {\n      type: String,\n      default: ''\n    },\n    disabledClick: {\n      type: Boolean,\n      default: false\n    },\n    href: {\n      type: String,\n      default: null\n    },\n    click: {\n      type: Function,\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    value: {\n      type: Number,\n      default: 0\n    },\n    badgeValue: {\n      type: Number,\n      default: 0\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    error: {\n      type: Object,\n      default: () => null\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n@import \"src/scss/variants.scss\";\n\n  .widget-card-indicator {\n    &:hover {\n      background-color: var(--me-primary-7);\n\n      .widget-card-body {\n        .widget-label {\n          color: var(--me-primary-2);\n        }\n\n        .widget-badge-primary {\n          display: inline-block;\n        }\n\n        .widget-badge-light-primary {\n          display: none;\n        }\n      }\n    }\n\n    .widget-title {\n      color: var(--me-primary-2);\n      font-weight: 600;\n      font-size: 28px;\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n      height: 38px;\n    }\n\n    .widget-card-body {\n      display: flex;\n      justify-content: space-between;\n      align-items: center;\n      padding: 0 8px;\n\n      .widget-label {\n        color: var(--me-gray-4);\n        font-size: 12px;\n        white-space: nowrap;\n        overflow: hidden;\n        text-overflow: ellipsis;\n        padding-right: 8px;\n      }\n\n      .widget-badge-primary {\n        display: none;\n      }\n\n      .widget-badge-light-primary {\n        display: inline-block;\n      }\n    }\n\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$X = "data-v-724904d1";
  /* module identifier */
  const __vue_module_identifier__$X = undefined;
  /* functional template */
  const __vue_is_functional_template__$X = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$X, staticRenderFns: __vue_staticRenderFns__$X },
    __vue_inject_styles__$X,
    __vue_script__$X,
    __vue_scope_id__$X,
    __vue_is_functional_template__$X,
    __vue_module_identifier__$X,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$Y = {
  name: 'MeDashboardEmpty',
  props: {
    width: {
      type: Number,
      default: 200
    },
    height: {
      type: Number,
      default: 200
    }
  }
};

/* script */
const __vue_script__$Y = script$Y;

/* template */
var __vue_render__$Y = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _c(
      "svg",
      {
        attrs: {
          width: _vm.width,
          height: _vm.height,
          viewBox: "0 0 200 200",
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg"
        }
      },
      [
        _c("path", {
          attrs: {
            d: "M23.3723 189.281H19.3232V181.59H23.3723V189.281Z",
            fill: "#7ED4DB"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M41.926 189.281H37.877V181.59H41.926V189.281Z",
            fill: "#7ED4DB"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M61.0466 189.088H48.2242V181.782H61.0466V189.088ZM14.7083 181.782H47.8843V189.088H14.7083V181.782ZM14.3682 181.397V189.474H61.3865V181.397H14.3682Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M15.6174 181.59H11.5684V173.899H15.6174V181.59Z",
            fill: "#F2531B"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M58.8121 181.397H39.3431V174.091H58.8121V181.397ZM6.95317 174.091H39.0032V181.397H6.95317V174.091ZM6.61328 173.705V181.782H59.1523V173.705H6.61328Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M19.2629 173.898H15.2139V166.207H19.2629V173.898Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M10.8322 166.206H6.7832V158.515H10.8322V166.206Z",
            fill: "#5DC695"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M23.3719 173.898H21.3477V166.207H23.3719V173.898Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M62.4576 173.705H42.9883V166.4H62.4576V173.705ZM10.5987 166.4H42.6484V173.705H10.5987V166.4ZM10.2588 166.014V174.091H62.7975V166.014H10.2588Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M54.2962 166.014H34.8272V158.708H54.2962V166.014ZM2.43754 158.708H34.487V166.014H2.43754V158.708ZM2.09766 158.322V166.399H54.6361V158.322H2.09766Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M199.999 85.2012H150.252V29.7343H199.999V85.2012Z",
            fill: "#E0E0E0"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M199.999 29.7344H150.252V25.2229H199.999V29.7344Z",
            fill: "#B6B6B8"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M199.999 122.417H150.252V87.1771H199.999V122.417Z",
            fill: "#E0E0E0"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M194.015 94.2637H163.779V93.8155H194.015V94.2637Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M193.252 57.468C193.252 68.8285 185.137 78.0381 175.126 78.0381C165.115 78.0381 157 68.8285 157 57.468C157 46.1072 165.115 36.8976 175.126 36.8976C185.137 36.8976 193.252 46.1072 193.252 57.468Z",
            fill: "#5DC695"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M188.811 43.987C185.488 39.6461 180.592 36.8974 175.127 36.8974V57.4678L188.811 43.987Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M175.126 57.468L171.82 77.6906C172.893 77.9149 173.996 78.0381 175.126 78.0381C185.136 78.0381 193.252 68.8285 193.252 57.468C193.252 52.3093 191.575 47.5981 188.81 43.9873L175.126 57.468Z",
            fill: "#F8A538"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M161.104 70.5017C163.764 74.1824 167.528 76.7921 171.821 77.6904L175.126 57.4678L161.104 70.5017Z",
            fill: "#F2531B"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M159.934 93.9289C159.934 95.0874 159.106 96.0264 158.086 96.0264C157.065 96.0264 156.237 95.0874 156.237 93.9289C156.237 92.7704 157.065 91.8311 158.086 91.8311C159.106 91.8311 159.934 92.7704 159.934 93.9289Z",
            fill: "#F2531B"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M153.594 27.4785C153.594 27.9284 153.272 28.293 152.876 28.293C152.479 28.293 152.158 27.9284 152.158 27.4785C152.158 27.0287 152.479 26.6638 152.876 26.6638C153.272 26.6638 153.594 27.0287 153.594 27.4785Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M157.218 27.4785C157.218 27.9284 156.897 28.293 156.501 28.293C156.104 28.293 155.783 27.9284 155.783 27.4785C155.783 27.0287 156.104 26.6638 156.501 26.6638C156.897 26.6638 157.218 27.0287 157.218 27.4785Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M160.845 27.4785C160.845 27.9284 160.523 28.293 160.127 28.293C159.731 28.293 159.409 27.9284 159.409 27.4785C159.409 27.0287 159.731 26.6638 160.127 26.6638C160.523 26.6638 160.845 27.0287 160.845 27.4785Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M194.015 101.595H163.779V101.147H194.015V101.595Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M159.934 101.26C159.934 102.418 159.106 103.357 158.086 103.357C157.065 103.357 156.237 102.418 156.237 101.26C156.237 100.101 157.065 99.1622 158.086 99.1622C159.106 99.1622 159.934 100.101 159.934 101.26Z",
            fill: "#5DC695"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M194.015 108.926H163.779V108.478H194.015V108.926Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M159.934 108.591C159.934 109.749 159.106 110.688 158.086 110.688C157.065 110.688 156.237 109.749 156.237 108.591C156.237 107.433 157.065 106.493 158.086 106.493C159.106 106.493 159.934 107.433 159.934 108.591Z",
            fill: "#7ED4DB"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M194.015 116.257H163.779V115.809H194.015V116.257Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M159.934 115.922C159.934 117.081 159.106 118.02 158.086 118.02C157.065 118.02 156.237 117.081 156.237 115.922C156.237 114.763 157.065 113.824 158.086 113.824C159.106 113.824 159.934 114.763 159.934 115.922Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M51.1503 163.343C51.1503 163.343 57.601 154.379 63.3655 145.381C63.1309 149.086 62.9031 152.577 62.6277 155.676C62.2384 160.061 62.0207 164.04 61.9234 167.608C58.6056 166.234 55.0086 164.802 51.1503 163.343ZM155.173 67.114C155.173 67.114 141.764 91.9951 137.489 110.594C137.489 110.594 127.81 104.147 113.77 94.9862C110.936 93.1368 107.715 92.1848 104.364 91.8941C104.15 91.8488 104.031 91.8326 104.031 91.8326C104.031 91.8326 87.337 88.1144 75.7501 94.7623C75.0168 95.1833 74.3311 95.719 73.6886 96.3565C73.6886 96.3565 73.6233 96.4237 73.5058 96.5466C73.4118 96.6439 73.3174 96.7399 73.2254 96.8416C69.234 101.057 40.3823 132.256 34.9003 158.926C31.6609 174.687 42.9925 179.644 52.9166 182.33C55.2467 182.961 58.6509 183.476 62.41 183.893C62.6946 186.607 63.0333 188.418 63.2815 189.281H121.055C122.842 178.744 123.129 173.852 123.459 165.485C123.746 158.194 122.28 140.608 120.296 125.618C122.668 126.871 125.238 128.097 128.029 129.281C146.041 136.922 154.834 120.674 159.87 68.5833L155.173 67.114Z",
            fill: "#8ED4E2"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M60.6543 55.6904H147.149V10.5269H60.6543V55.6904Z",
            fill: "#E0E0E0"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M63.9482 51.998H143.853V14.2186H63.9482V51.998Z",
            fill: "#5DC695"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M63.9482 40.9538L68.4398 44.1494L86.7176 30.2389L99.0336 38.4686L119.602 20.466L137.786 37.9543L143.853 30.2389V51.998H63.9482V40.9538Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M87.6991 26.9591C87.6991 27.5738 87.2598 28.0723 86.7182 28.0723C86.1763 28.0723 85.7373 27.5738 85.7373 26.9591C85.7373 26.3441 86.1763 25.8459 86.7182 25.8459C87.2598 25.8459 87.6991 26.3441 87.6991 26.9591Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M120.583 17.3224C120.583 17.937 120.144 18.4355 119.602 18.4355C119.06 18.4355 118.621 17.937 118.621 17.3224C118.621 16.7074 119.06 16.2092 119.602 16.2092C120.144 16.2092 120.583 16.7074 120.583 17.3224Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M143.853 26.6926L137.435 34.2187L119.711 17.1751L119.607 17.0758L98.7955 34.8931L86.712 26.7319L68.6676 40.4217L63.9482 36.7371V37.2054L68.6649 40.8877L86.7226 27.1877L98.8183 35.357L119.595 17.5692L137.457 34.7461L143.853 27.2472V26.6926Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M5.0293 122.685H48.2764V38.424H5.0293V122.685Z",
            fill: "#E0E0E0"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M112.906 82.04H147.012V62.0138H112.906V82.04Z",
            fill: "#5DC695"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M45.4043 187.924H159.443V189.281H45.4043V187.924Z",
            fill: "#753D25"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M90.9018 185.815L92.2246 185.505L86.8489 155.943C86.7734 155.528 86.4161 155.262 86.0508 155.347C85.6857 155.433 85.4506 155.838 85.5257 156.253L90.9018 185.815Z",
            fill: "#DDE1E8"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M199.91 189.474H0V189.088H199.91V189.474Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M110.766 185.491L151.946 185.474L159.962 147.998L118.782 148.015L110.766 185.491Z",
            fill: "#E0E0E0"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M88.8691 187.923L151.947 187.897L151.947 185.474L88.8682 185.5L88.8691 187.923Z",
            fill: "#E0E0E0"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M110.72 185.491L110.316 187.914L88.8694 187.923L88.8691 187.282L109.581 186.088L110.72 185.491Z",
            fill: "#2C2522"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M110.719 185.492L151.947 185.475L151.947 186.156L110.515 186.703L110.719 185.492Z",
            fill: "#2C2522"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M97.9073 104.476C97.9073 104.899 97.6056 105.241 97.2334 105.241C96.8613 105.241 96.5596 104.899 96.5596 104.476C96.5596 104.054 96.8613 103.712 97.2334 103.712C97.6056 103.712 97.9073 104.054 97.9073 104.476Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M97.9073 112.25C97.9073 112.672 97.6056 113.015 97.2334 113.015C96.8613 113.015 96.5596 112.672 96.5596 112.25C96.5596 111.828 96.8613 111.485 97.2334 111.485C97.6056 111.485 97.9073 111.828 97.9073 112.25Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M70.7904 132.229C70.7904 132.229 67.7311 145.452 62.9863 151.101L63.3644 145.381C63.3644 145.381 68.2695 138.698 70.7904 132.229Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M112.906 97.4684C112.906 97.4684 117.211 100.956 119.602 120.625C119.602 120.625 118.254 102.146 112.906 97.4684Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M61.9229 167.608L61.9264 167.61C78.4926 174.471 88.0691 179.881 88.0691 179.881C88.0691 179.881 75.6783 172.357 61.9483 166.745L61.9229 167.608Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M101.332 75.1456C101.906 74.1352 102.274 72.9277 102.513 71.6178C103.6 65.6634 101.373 57.8812 101.373 57.8812L89.5793 58.1186C89.5793 58.1186 89.5787 58.1206 89.5787 58.1209L87.6989 58.1588L87.7775 63.1C87.7066 63.1698 87.6356 63.2444 87.5657 63.33C86.8241 64.2337 86.8298 66.0455 87.8444 67.3131L87.8684 68.814C87.8621 71.7272 87.6622 81.9966 85.3066 91.4661L97.2331 97.4688L103.901 91.831C103.901 91.831 101.638 85.1942 101.332 75.1456Z",
            fill: "#F68265"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7081 64.1543H9.65039V63.7061H43.7081V64.1543Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M48.4889 19.3828H4.87207V18.9347H48.4889V19.3828Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7081 71.3105H9.65039V70.8624H43.7081V71.3105Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7081 78.4658H9.65039V78.0177H43.7081V78.4658Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7081 85.6221H9.65039V85.1739H43.7081V85.6221Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7081 92.7783H9.65039V92.3302H43.7081V92.7783Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M144.19 66.5674H115.728V66.0705H144.19V66.5674Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M144.19 70.2285H115.728V69.7313H144.19V70.2285Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M144.19 73.8887H115.728V73.3915H144.19V73.8887Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M144.19 77.5488H115.728V77.0517H144.19V77.5488Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M22.926 58.9951H9.65039V43.9295H22.926V58.9951Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7087 58.9951H25.6279V43.9295H43.7087V58.9951Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M43.7081 117.535H9.65039V110.002H43.7081V117.535Z",
            fill: "white"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M13.5971 102.216C13.5971 103.453 12.7135 104.455 11.6237 104.455C10.534 104.455 9.65039 103.453 9.65039 102.216C9.65039 100.979 10.534 99.9765 11.6237 99.9765C12.7135 99.9765 13.5971 100.979 13.5971 102.216Z",
            fill: "#F2531B"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M23.6342 102.216C23.6342 103.453 22.7506 104.455 21.6608 104.455C20.5711 104.455 19.6875 103.453 19.6875 102.216C19.6875 100.979 20.5711 99.9765 21.6608 99.9765C22.7506 99.9765 23.6342 100.979 23.6342 102.216Z",
            fill: "#5DC695"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M33.6713 102.216C33.6713 103.453 32.7877 104.455 31.698 104.455C30.6082 104.455 29.7246 103.453 29.7246 102.216C29.7246 100.979 30.6082 99.9765 31.698 99.9765C32.7877 99.9765 33.6713 100.979 33.6713 102.216Z",
            fill: "#7ED4DB"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M43.7094 102.216C43.7094 103.453 42.8258 104.455 41.736 104.455C40.6463 104.455 39.7627 103.453 39.7627 102.216C39.7627 100.979 40.6463 99.9765 41.736 99.9765C42.8258 99.9765 43.7094 100.979 43.7094 102.216Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M10.0927 19.1591C10.0927 19.6755 9.72409 20.0938 9.26914 20.0938C8.81418 20.0938 8.44531 19.6755 8.44531 19.1591C8.44531 18.6428 8.81418 18.2242 9.26914 18.2242C9.72409 18.2242 10.0927 18.6428 10.0927 19.1591Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M48.4889 26.3574H4.87207V25.9093H48.4889V26.3574Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M19.3124 26.1334C19.3124 26.6497 18.9435 27.0684 18.4886 27.0684C18.0339 27.0684 17.665 26.6497 17.665 26.1334C17.665 25.6175 18.0339 25.1988 18.4886 25.1988C18.9435 25.1988 19.3124 25.6175 19.3124 26.1334Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M48.4889 33.332H4.87207V32.8839H48.4889V33.332Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M39.1991 33.1084C39.1991 33.6243 38.8302 34.043 38.3753 34.043C37.9203 34.043 37.5518 33.6243 37.5518 33.1084C37.5518 32.5921 37.9203 32.1734 38.3753 32.1734C38.8302 32.1734 39.1991 32.5921 39.1991 33.1084Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M66.6427 70.2754H60.6543V63.4795H66.6427V70.2754Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M66.6427 82.3105H60.6543V75.5146H66.6427V82.3105Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M66.6427 94.3447H60.6543V87.5491H66.6427V94.3447Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M63.3396 68.7275L61.9404 67.3545L62.1623 67.0631L63.2704 68.1495L64.9967 65.2097L65.2795 65.4239L63.3396 68.7275Z",
            fill: "#F1EAE5"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M63.3396 80.7627L61.9404 79.39L62.1623 79.0983L63.2704 80.185L64.9967 77.2452L65.2795 77.4594L63.3396 80.7627Z",
            fill: "#F1EAE5"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M63.3396 92.7969L61.9404 91.4242L62.1623 91.1325L63.2704 92.2191L64.9967 89.2797L65.2795 89.4939L63.3396 92.7969Z",
            fill: "#F1EAE5"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M165.923 187.359H175.71C177.966 178.971 179.187 165.12 179.345 163.25H162.288C162.446 165.12 163.667 178.971 165.923 187.359ZM175.96 187.744H165.672L165.635 187.609C163.189 178.625 161.944 163.229 161.932 163.074L161.915 162.864H179.717L179.701 163.074C179.688 163.229 178.444 178.625 175.998 187.609L175.96 187.744Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M164.103 189.088H177.615V187.744H164.103V189.088ZM177.955 189.474H163.763V187.359H177.955V189.474Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M164.312 181.063C160.657 180.153 158.331 176.561 157.374 173.502C156.642 171.166 156.641 168.985 157.37 167.945C159.431 165.003 162.637 167.737 162.67 167.765L162.463 168.071C162.343 167.967 159.489 165.54 157.635 168.185C156.978 169.123 157.001 171.159 157.695 173.373C158.621 176.334 160.866 179.81 164.385 180.687L164.312 181.063Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M179.313 165.692H162.321V165.307H179.313V165.692Z",
            fill: "#2A2A2C"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M177.104 163.057L176.425 164.883H179.311L179.531 163.057H177.104Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M174.555 165.501C174.555 165.501 170.986 180.13 170.816 187.552H175.835C177.702 180.696 178.858 170.211 179.311 165.501H174.555Z",
            fill: "#201621"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M161.691 62.5532C160.8 60.5464 159.479 59.6165 159.479 59.6165C159.479 59.6165 161.834 55.0644 161.685 54.9174C161.535 54.7707 156.895 58.931 156.174 60.9378C156.139 61.0342 156.107 61.1396 156.076 61.2453C155.703 61.4568 155.273 61.7479 155.179 62.0141C155.009 62.5002 155.426 67.1933 155.426 67.1933L159.479 68.4629C162.612 65.9048 161.691 62.5532 161.691 62.5532Z",
            fill: "#F68265"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M88.1589 179.881C88.1589 179.881 91.9337 179.48 93.0998 179.881C94.2662 180.282 99.6724 183.946 99.7937 184.333C99.7937 184.333 99.6117 185.138 98.6397 185.475H90.1355C90.1355 185.475 87.8728 185.58 86.8174 184.699L88.1589 179.881Z",
            fill: "#F68265"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M101.329 75.1569C101.329 75.1569 98.4382 80.5258 92.3555 76.4809C92.3555 76.4809 95.3792 86.3566 102.109 83.7915C102.109 83.7915 101.491 79.9524 101.329 75.1569Z",
            fill: "#CB6A57"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M82.9893 91.9418L85.3072 87.4387L97.2337 97.4687L91.6127 103.448L89.3465 98.2405L82.9893 91.9418Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M103.902 88.5001L97.2334 97.4694L102.49 103.835L103.452 97.8615L106.334 92.1442L103.902 88.5001Z",
            fill: "#3D4BA7"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M85.3066 87.4392H86.1793L86.0583 88.0713L85.3066 87.4392Z",
            fill: "#753D25"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d: "M103.901 88.4996H103.001L103.22 89.415L103.901 88.4996Z",
            fill: "#753D25"
          }
        }),
        _vm._v(" "),
        _c("path", {
          attrs: {
            d:
              "M99.1471 53.0353C96.5801 52.4468 91.8382 54.8964 89.2895 56.7464L87.0411 55.6766C87.0411 55.6766 85.9271 62.1717 87.8348 71.0439C87.8348 71.0439 88.4101 69.9146 89.0585 68.2768C86.9006 67.2327 86.5983 64.5082 87.5656 63.3299C88.7305 61.9062 90.074 62.7726 90.5947 63.207C90.8526 61.8508 90.9538 60.5063 90.7517 59.3501C95.1909 60.9293 102.757 62.965 101.373 57.8811C101.373 57.8811 101.686 59.1078 101.748 59.3834C101.748 59.3834 102.49 53.8017 99.1471 53.0353Z",
            fill: "#201621"
          }
        })
      ]
    )
  ])
};
var __vue_staticRenderFns__$Y = [];
__vue_render__$Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$Y = undefined;
  /* scoped */
  const __vue_scope_id__$Y = undefined;
  /* module identifier */
  const __vue_module_identifier__$Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$Y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Y, staticRenderFns: __vue_staticRenderFns__$Y },
    __vue_inject_styles__$Y,
    __vue_script__$Y,
    __vue_scope_id__$Y,
    __vue_is_functional_template__$Y,
    __vue_module_identifier__$Y,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$Z = {
  name: 'MeWidgetCardIndicator',
  components: {
    WidgetContent: __vue_component__$W,
    DashboardEmpty: __vue_component__$Y
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    deletable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    disabledClick: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: () => null
    },
    click: {
      type: Function,
      default: null
    }
  },
  computed: {
    verifyProcessCreation() {
      return this.items[0].links?.length < 1
    }
  }
};

/* script */
const __vue_script__$Z = script$Z;

/* template */
var __vue_render__$Z = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("WidgetContent", {
    staticClass: "widget-card-button",
    attrs: {
      editable: _vm.editable,
      deletable: _vm.deletable,
      disabledClick: _vm.disabledClick,
      error: _vm.error,
      click: _vm.click,
      "has-action": ""
    },
    on: {
      deleteWidget: function($event) {
        return _vm.$emit("delete")
      }
    },
    scopedSlots: _vm._u([
      {
        key: "title",
        fn: function() {
          return [
            _c("div", { staticClass: "widget-card-text" }, [
              _c("span", { staticClass: "widget-title" }, [
                _vm._v("\n        " + _vm._s(_vm.label) + "\n      ")
              ]),
              _vm._v(" "),
              _c("div", { staticClass: "widget-card-body" }, [
                _c("span", { staticClass: "widget-label" }, [
                  _vm._v(
                    "\n          " + _vm._s(_vm.description) + "\n        "
                  )
                ])
              ]),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "button-list" },
                _vm._l(_vm.items, function(item) {
                  return _c(
                    "div",
                    {
                      key: item.id || item.label,
                      staticClass: "button-item-container"
                    },
                    [
                      _c("me-drop-down", {
                        staticClass: "button-item-container__dropdown",
                        attrs: {
                          items: item.links,
                          disabled: _vm.verifyProcessCreation,
                          boundary: "viewport",
                          variant: "primary",
                          backdrop: "",
                          block: ""
                        },
                        on: {
                          click: function($event) {
                            item.click
                              ? item.click()
                              : function() {
                                  return null
                                };
                          }
                        },
                        scopedSlots: _vm._u(
                          [
                            {
                              key: "button-content",
                              fn: function() {
                                return [
                                  _c("i", {
                                    staticClass: "me-icon-l icon-plus",
                                    staticStyle: { "margin-right": "5px" }
                                  }),
                                  _vm._v(
                                    " " + _vm._s(item.label) + "\n            "
                                  )
                                ]
                              },
                              proxy: true
                            }
                          ],
                          null,
                          true
                        )
                      })
                    ],
                    1
                  )
                }),
                0
              )
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "widget-card-button__image" },
              [_c("DashboardEmpty", { attrs: { width: 100, height: 100 } })],
              1
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$Z = [];
__vue_render__$Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$Z = function (inject) {
    if (!inject) return
    inject("data-v-70078144_0", { source: "[data-v-70078144] .dropdown-menu {\n  z-index: 1200 !important;\n}\n.widget-card-button[data-v-70078144] {\n  display: flex;\n  flex-direction: column;\n  overflow: hidden;\n  background-color: var(--me-primary-7) !important;\n}\n.widget-card-button .button-list[data-v-70078144] {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  padding-top: 16px;\n  padding-right: 24px;\n  padding-bottom: 23px;\n}\n.widget-card-button .button-list .button-item-container[data-v-70078144] {\n  display: flex;\n  flex-grow: 1;\n  min-width: 120px;\n}\n.widget-card-button .button-list .button-item-container__dropdown[data-v-70078144] {\n  width: fit-content;\n  white-space: nowrap;\n  display: block !important;\n  align-items: center;\n}\n.widget-card-button .widget-card-button__image[data-v-70078144] {\n  margin-top: 28px;\n  margin-bottom: 33.68px;\n  margin-left: -7px;\n  transform: rotateY(180deg);\n}\n.widget-card-button .widget-card-text[data-v-70078144] {\n  flex-direction: column;\n}\n.widget-card-button .widget-title[data-v-70078144] {\n  color: var(--me-gray-0);\n  line-height: 22px;\n  font-weight: 600;\n  font-size: 14px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 38px;\n}\n.widget-card-button .widget-card-body[data-v-70078144] {\n  display: flex;\n  justify-content: space-between;\n  padding-top: 8px;\n}\n.widget-card-button .widget-card-body .widget-label[data-v-70078144] {\n  color: var(--me-gray-0);\n  font-size: 12px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding-right: 8px;\n}\n\n/*# sourceMappingURL=WidgetCardButton.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/card/WidgetCardButton.vue","WidgetCardButton.vue"],"names":[],"mappings":"AAwGA;EACA,wBAAA;ACvGA;AD0GA;EACA,aAAA;EACA,sBAAA;EACA,gBAAA;EACA,gDAAA;ACvGA;ADwGA;EACA,aAAA;EACA,eAAA;EACA,WAAA;EACA,iBAAA;EACA,mBAAA;EACA,oBAAA;ACtGA;ADwGA;EACA,aAAA;EACA,YAAA;EACA,gBAAA;ACtGA;ADwGA;EACA,kBAAA;EACA,mBAAA;EACA,yBAAA;EACA,mBAAA;ACtGA;AD0GA;EACA,gBAAA;EACA,sBAAA;EACA,iBAAA;EACA,0BAAA;ACxGA;AD0GA;EACA,sBAAA;ACxGA;AD0GA;EACA,uBAAA;EACA,iBAAA;EACA,gBAAA;EACA,eAAA;EACA,gBAAA;EACA,uBAAA;EACA,YAAA;ACxGA;AD2GA;EACA,aAAA;EACA,8BAAA;EACA,gBAAA;ACzGA;AD2GA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,uBAAA;EACA,kBAAA;ACzGA;;AAEA,+CAA+C","file":"WidgetCardButton.vue","sourcesContent":["<template>\n  <WidgetContent\n    :editable=\"editable\"\n    :deletable=\"deletable\"\n    :disabledClick=\"disabledClick\"\n    :error=\"error\"\n    :click=\"click\"\n    @deleteWidget=\"$emit('delete')\"\n    has-action\n    class=\"widget-card-button\"\n  >\n    <template v-slot:title>\n      <div class=\"widget-card-text\">\n        <span class=\"widget-title\">\n          {{ label }}\n        </span>\n        <div class=\"widget-card-body\">\n          <span class=\"widget-label\">\n            {{ description }}\n          </span>\n        </div>\n        <div class=\"button-list\">\n          <div\n            v-for=\"item in items\"\n            :key=\"item.id || item.label\"\n            class=\"button-item-container\"\n          >\n            <me-drop-down\n              :items=\"item.links\"\n              @click=\"item.click ? item.click() : () => null\"\n              :disabled=\"verifyProcessCreation\"\n              class=\"button-item-container__dropdown\"\n              boundary=\"viewport\"\n              variant=\"primary\"\n              backdrop\n              block\n            >\n              <template v-slot:button-content>\n                <i class=\"me-icon-l icon-plus\" style=\"margin-right: 5px\" /> {{ item.label }}\n              </template>\n            </me-drop-down>\n          </div>\n        </div>\n      </div>\n      <div class=\"widget-card-button__image\">\n        <DashboardEmpty :width=\"100\" :height=\"100\" />\n      </div>\n    </template>\n  </WidgetContent>\n</template>\n<script>\nimport WidgetContent from '../WidgetContent.vue'\nimport DashboardEmpty from '../../../dashboard/DashboardEmpty.vue'\n\nexport default {\n  name: 'MeWidgetCardIndicator',\n  components: {\n    WidgetContent,\n    DashboardEmpty\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    description: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    disabledClick: {\n      type: Boolean,\n      default: false\n    },\n    error: {\n      type: Object,\n      default: () => null\n    },\n    click: {\n      type: Function,\n      default: null\n    }\n  },\n  computed: {\n    verifyProcessCreation() {\n      return this.items[0].links?.length < 1\n    }\n  }\n}\n</script>\n\n  <style lang=\"scss\" scoped>\n    ::v-deep .dropdown-menu {\n      z-index: 1200 !important;\n    }\n\n    .widget-card-button {\n      display: flex;\n      flex-direction: column;\n      overflow: hidden;\n      background-color: var(--me-primary-7) !important;\n      .button-list {\n        display: flex;\n        flex-wrap: wrap;\n        width: 100%;\n        padding-top: 16px;\n        padding-right: 24px;\n        padding-bottom: 23px;\n\n      .button-item-container {\n        display: flex;\n        flex-grow: 1;\n        min-width: 120px;\n\n        &__dropdown {\n          width: fit-content;\n          white-space: nowrap;\n          display: block !important;\n          align-items: center;\n        }\n      }\n    }\n      .widget-card-button__image {\n        margin-top: 28px;\n        margin-bottom: 33.68px;\n        margin-left: -7px;\n        transform: rotateY(180deg);\n      }\n      .widget-card-text {\n        flex-direction: column;\n      }\n      .widget-title {\n        color: var(--me-gray-0);\n        line-height: 22px;\n        font-weight: 600;\n        font-size: 14px;\n        overflow: hidden;\n        text-overflow: ellipsis;\n        height: 38px;\n      }\n\n      .widget-card-body {\n        display: flex;\n        justify-content: space-between;\n        padding-top: 8px;\n\n         .widget-label {\n           color: var(--me-gray-0 );\n           font-size: 12px;\n           overflow: hidden;\n           text-overflow: ellipsis;\n           padding-right: 8px;\n         }\n      }\n\n    }\n  </style>\n","::v-deep .dropdown-menu {\n  z-index: 1200 !important;\n}\n\n.widget-card-button {\n  display: flex;\n  flex-direction: column;\n  overflow: hidden;\n  background-color: var(--me-primary-7) !important;\n}\n.widget-card-button .button-list {\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  padding-top: 16px;\n  padding-right: 24px;\n  padding-bottom: 23px;\n}\n.widget-card-button .button-list .button-item-container {\n  display: flex;\n  flex-grow: 1;\n  min-width: 120px;\n}\n.widget-card-button .button-list .button-item-container__dropdown {\n  width: fit-content;\n  white-space: nowrap;\n  display: block !important;\n  align-items: center;\n}\n.widget-card-button .widget-card-button__image {\n  margin-top: 28px;\n  margin-bottom: 33.68px;\n  margin-left: -7px;\n  transform: rotateY(180deg);\n}\n.widget-card-button .widget-card-text {\n  flex-direction: column;\n}\n.widget-card-button .widget-title {\n  color: var(--me-gray-0);\n  line-height: 22px;\n  font-weight: 600;\n  font-size: 14px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  height: 38px;\n}\n.widget-card-button .widget-card-body {\n  display: flex;\n  justify-content: space-between;\n  padding-top: 8px;\n}\n.widget-card-button .widget-card-body .widget-label {\n  color: var(--me-gray-0);\n  font-size: 12px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding-right: 8px;\n}\n\n/*# sourceMappingURL=WidgetCardButton.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$Z = "data-v-70078144";
  /* module identifier */
  const __vue_module_identifier__$Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$Z = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Z, staticRenderFns: __vue_staticRenderFns__$Z },
    __vue_inject_styles__$Z,
    __vue_script__$Z,
    __vue_scope_id__$Z,
    __vue_is_functional_template__$Z,
    __vue_module_identifier__$Z,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$_ = {
  name: 'MeWidgetCardLink',
  components: {
    Icon: __vue_component__,
    WidgetContent: __vue_component__$W
  },
  props: {
    disabledClick: {
      type: Boolean,
      default: false
    },
    href: {
      type: String,
      default: '#'
    },
    click: {
      type: Function,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    editable: {
      type: Boolean,
      default: true
    },
    icon: {
      type: String,
      default: 'me-icon-s icon-link'
    },
    deletable: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: () => null
    }
  },

  data() {
    return {
      widgetActionsClicked: false
    }
  },

  methods: {
    handleEdit() {
      this.widgetActionsClicked = true;
      this.$emit('edit');
    },
    handleDelete() {
      this.widgetActionsClicked = true;
      this.$emit('delete');
    }
  }
};

/* script */
const __vue_script__$_ = script$_;

/* template */
var __vue_render__$_ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("WidgetContent", {
    staticClass: "widget-card-link",
    attrs: {
      href: _vm.href,
      editable: _vm.editable,
      deletable: _vm.deletable,
      disabledClick: _vm.widgetActionsClicked
        ? _vm.widgetActionsClicked
        : _vm.disabledClick,
      error: _vm.error,
      click: _vm.click
    },
    on: { editWidget: _vm.handleEdit, deleteWidget: _vm.handleDelete },
    nativeOn: {
      click: function($event) {
        _vm.widgetActionsClicked = false;
      }
    },
    scopedSlots: _vm._u([
      {
        key: "title",
        fn: function() {
          return [
            _c("Icon", {
              staticClass: "widget-card-link__icon",
              attrs: { size: 30, icon: _vm.icon }
            })
          ]
        },
        proxy: true
      },
      {
        key: "body",
        fn: function() {
          return [
            _c("span", { staticClass: "widget-label" }, [
              _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")
            ])
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$_ = [];
__vue_render__$_._withStripped = true;

  /* style */
  const __vue_inject_styles__$_ = function (inject) {
    if (!inject) return
    inject("data-v-bf6959f8_0", { source: ".widget-card-link[data-v-bf6959f8]:hover {\n  background-color: var(--me-primary-7);\n}\n.widget-card-link:hover .widget-card-link__icon[data-v-bf6959f8] {\n  color: var(--me-primary-2);\n}\n.widget-card-link:hover .widget-label[data-v-bf6959f8] {\n  color: var(--me-primary-2);\n}\n.widget-card-link__icon[data-v-bf6959f8] {\n  color: var(--me-primary-5);\n  height: 38px;\n}\n.widget-card-link .widget-label[data-v-bf6959f8] {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding: 0 8px;\n}\n\n/*# sourceMappingURL=WidgetCardLink.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/card/WidgetCardLink.vue","WidgetCardLink.vue"],"names":[],"mappings":"AA+FA;EACA,qCAAA;AC9FA;ADgGA;EACA,0BAAA;AC9FA;ADiGA;EACA,0BAAA;AC/FA;ADmGA;EACA,0BAAA;EACA,YAAA;ACjGA;ADoGA;EACA,uBAAA;EACA,eAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,cAAA;AClGA;;AAEA,6CAA6C","file":"WidgetCardLink.vue","sourcesContent":["<template>\n  <WidgetContent\n    :href=\"href\"\n    :editable=\"editable\"\n    :deletable=\"deletable\"\n    :disabledClick=\"widgetActionsClicked ? widgetActionsClicked : disabledClick\"\n    :error=\"error\"\n    :click=\"click\"\n    @editWidget=\"handleEdit\"\n    @deleteWidget=\"handleDelete\"\n    @click.native=\"widgetActionsClicked = false\"\n    class=\"widget-card-link\"\n  >\n    <template v-slot:title>\n      <Icon\n        :size=\"30\"\n        :icon=\"icon\"\n        class=\"widget-card-link__icon\"\n      />\n    </template>\n\n    <template v-slot:body>\n      <span class=\"widget-label\">\n        {{ label }}\n      </span>\n    </template>\n  </WidgetContent>\n</template>\n\n<script>\nimport Icon from '../../../Icon.vue'\nimport WidgetContent from '../WidgetContent.vue'\n\nexport default {\n  name: 'MeWidgetCardLink',\n  components: {\n    Icon,\n    WidgetContent\n  },\n  props: {\n    disabledClick: {\n      type: Boolean,\n      default: false\n    },\n    href: {\n      type: String,\n      default: '#'\n    },\n    click: {\n      type: Function,\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    icon: {\n      type: String,\n      default: 'me-icon-s icon-link'\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    error: {\n      type: Object,\n      default: () => null\n    }\n  },\n\n  data() {\n    return {\n      widgetActionsClicked: false\n    }\n  },\n\n  methods: {\n    handleEdit() {\n      this.widgetActionsClicked = true\n      this.$emit('edit')\n    },\n    handleDelete() {\n      this.widgetActionsClicked = true\n      this.$emit('delete')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-card-link {\n    &:hover {\n      background-color: var(--me-primary-7);\n\n      .widget-card-link__icon {\n        color: var(--me-primary-2);\n      }\n\n      .widget-label {\n        color: var(--me-primary-2);\n      }\n    }\n\n    &__icon {\n      color: var(--me-primary-5);\n      height: 38px;\n    }\n\n    .widget-label {\n      color: var(--me-gray-4);\n      font-size: 12px;\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n      padding: 0 8px;\n    }\n  }\n</style>\n",".widget-card-link:hover {\n  background-color: var(--me-primary-7);\n}\n.widget-card-link:hover .widget-card-link__icon {\n  color: var(--me-primary-2);\n}\n.widget-card-link:hover .widget-label {\n  color: var(--me-primary-2);\n}\n.widget-card-link__icon {\n  color: var(--me-primary-5);\n  height: 38px;\n}\n.widget-card-link .widget-label {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding: 0 8px;\n}\n\n/*# sourceMappingURL=WidgetCardLink.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$_ = "data-v-bf6959f8";
  /* module identifier */
  const __vue_module_identifier__$_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$_ = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$_, staticRenderFns: __vue_staticRenderFns__$_ },
    __vue_inject_styles__$_,
    __vue_script__$_,
    __vue_scope_id__$_,
    __vue_is_functional_template__$_,
    __vue_module_identifier__$_,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$$ = {
  name: 'MeListCardIndicator',
  components: {
    Badge: __vue_component__$x
  },

  props: {
    filter: {
      type: Object,
      default: () => ({})
    },
    href: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: Number,
      default: 0
    },
    badgeValue: {
      type: Number,
      default: 0
    },
    variant: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$$ = script$$;

/* template */
var __vue_render__$$ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "a",
    {
      staticClass: "list-card-indicator",
      class: _vm.variant,
      attrs: { href: _vm.href, "data-cy": "card-indicator-list" }
    },
    [
      _c(
        "div",
        {
          staticClass: "list-card-indicator__header",
          attrs: { "data-cy": "card-indicator-header" }
        },
        [
          _c("span", { staticClass: "widget-value", class: _vm.variant }, [
            _vm._v("\n      " + _vm._s(_vm.value) + "\n    ")
          ]),
          _vm._v(" "),
          _vm.badgeValue
            ? _c(
                "Badge",
                {
                  staticClass: "list-badge-primary",
                  attrs: { appearance: _vm.variant ? _vm.variant : "primary" }
                },
                [_vm._v("\n      +" + _vm._s(_vm.badgeValue) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.badgeValue
            ? _c(
                "Badge",
                {
                  staticClass: "list-badge-light-primary",
                  attrs: {
                    appearance: _vm.variant ? _vm.variant : "light-primary"
                  }
                },
                [_vm._v("\n      +" + _vm._s(_vm.badgeValue) + "\n    ")]
              )
            : _vm._e()
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        {
          staticClass: "list-card-indicator__body",
          attrs: { "data-cy": "card-indicator-body" }
        },
        [
          _c("span", { staticClass: "widget-label", class: _vm.variant }, [
            _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")
          ])
        ]
      )
    ]
  )
};
var __vue_staticRenderFns__$$ = [];
__vue_render__$$._withStripped = true;

  /* style */
  const __vue_inject_styles__$$ = function (inject) {
    if (!inject) return
    inject("data-v-607ba934_0", { source: ".danger[data-v-607ba934] {\n  background-color: var(--me-danger-7) !important;\n  color: var(--me-danger-2) !important;\n}\n.list-card-indicator[data-v-607ba934] {\n  display: block;\n  width: 100%;\n  height: 100%;\n  min-height: 80px;\n  padding: 16px;\n  text-decoration: none;\n  background-color: var(--me-bg-gray);\n  border-radius: 8px;\n}\n.list-card-indicator[data-v-607ba934]:hover {\n  background-color: var(--me-primary-7);\n}\n.list-card-indicator:hover .list-badge-primary[data-v-607ba934] {\n  display: inline-block;\n}\n.list-card-indicator:hover .list-badge-light-primary[data-v-607ba934] {\n  display: none;\n}\n.list-card-indicator:hover .widget-label[data-v-607ba934] {\n  color: var(--me-primary-5);\n}\n.list-card-indicator .list-badge-primary[data-v-607ba934] {\n  display: none;\n}\n.list-card-indicator .list-badge-light-primary[data-v-607ba934] {\n  display: inline-block;\n}\n.list-card-indicator__header[data-v-607ba934], .list-card-indicator__body[data-v-607ba934] {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n.list-card-indicator__header[data-v-607ba934] {\n  padding-bottom: 8px;\n}\n.list-card-indicator__header .widget-value[data-v-607ba934] {\n  color: var(--me-primary-2);\n  font-weight: 600;\n  font-size: 16px;\n}\n.list-card-indicator__body .widget-label[data-v-607ba934] {\n  color: var(--me-gray-4);\n  font-size: 12px;\n}\n\n/*# sourceMappingURL=ListCardIndicator.vue.map */", map: {"version":3,"sources":["ListCardIndicator.vue","/app/build/src/components/dashboard/widgets/list/list-item/ListCardIndicator.vue"],"names":[],"mappings":"AAAA;EACE,+CAA+C;EAC/C,oCAAoC;AACtC;ACiFA;EACA,cAAA;EACA,WAAA;EACA,YAAA;EACA,gBAAA;EACA,aAAA;EACA,qBAAA;EACA,mCAAA;EACA,kBAAA;AD9EA;ACgFA;EACA,qCAAA;AD9EA;ACgFA;EACA,qBAAA;AD9EA;ACiFA;EACA,aAAA;AD/EA;ACkFA;EACA,0BAAA;ADhFA;ACoFA;EACA,aAAA;ADlFA;ACqFA;EACA,qBAAA;ADnFA;ACsFA;EAEA,aAAA;EACA,8BAAA;EACA,mBAAA;ADrFA;ACwFA;EACA,mBAAA;ADtFA;ACwFA;EACA,0BAAA;EACA,gBAAA;EACA,eAAA;ADtFA;AC2FA;EACA,uBAAA;EACA,eAAA;ADzFA;;AAEA,gDAAgD","file":"ListCardIndicator.vue","sourcesContent":[".danger {\n  background-color: var(--me-danger-7) !important;\n  color: var(--me-danger-2) !important;\n}\n\n.list-card-indicator {\n  display: block;\n  width: 100%;\n  height: 100%;\n  min-height: 80px;\n  padding: 16px;\n  text-decoration: none;\n  background-color: var(--me-bg-gray);\n  border-radius: 8px;\n}\n.list-card-indicator:hover {\n  background-color: var(--me-primary-7);\n}\n.list-card-indicator:hover .list-badge-primary {\n  display: inline-block;\n}\n.list-card-indicator:hover .list-badge-light-primary {\n  display: none;\n}\n.list-card-indicator:hover .widget-label {\n  color: var(--me-primary-5);\n}\n.list-card-indicator .list-badge-primary {\n  display: none;\n}\n.list-card-indicator .list-badge-light-primary {\n  display: inline-block;\n}\n.list-card-indicator__header, .list-card-indicator__body {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n.list-card-indicator__header {\n  padding-bottom: 8px;\n}\n.list-card-indicator__header .widget-value {\n  color: var(--me-primary-2);\n  font-weight: 600;\n  font-size: 16px;\n}\n.list-card-indicator__body .widget-label {\n  color: var(--me-gray-4);\n  font-size: 12px;\n}\n\n/*# sourceMappingURL=ListCardIndicator.vue.map */","<template>\n  <a\n    :href=\"href\"\n    :class=\"variant\"\n    class=\"list-card-indicator\"\n    data-cy=\"card-indicator-list\"\n  >\n    <div\n      class=\"list-card-indicator__header\"\n      data-cy=\"card-indicator-header\"\n    >\n      <span :class=\"variant\" class=\"widget-value\">\n        {{ value }}\n      </span>\n\n      <Badge\n        v-if=\"badgeValue\"\n        :appearance=\"variant ? variant : 'primary'\"\n        class=\"list-badge-primary\"\n      >\n        +{{ badgeValue }}\n      </Badge>\n\n      <Badge\n        v-if=\"badgeValue\"\n        :appearance=\"variant ? variant : 'light-primary'\"\n        class=\"list-badge-light-primary\"\n      >\n        +{{ badgeValue }}\n      </Badge>\n    </div>\n\n    <div\n      class=\"list-card-indicator__body\"\n      data-cy=\"card-indicator-body\"\n    >\n      <span :class=\"variant\" class=\"widget-label\">\n        {{ label }}\n      </span>\n    </div>\n  </a>\n</template>\n\n<script>\nimport Badge from '../../../../Badge.vue'\n\nexport default {\n  name: 'MeListCardIndicator',\n  components: {\n    Badge\n  },\n\n  props: {\n    filter: {\n      type: Object,\n      default: () => ({})\n    },\n    href: {\n      type: String,\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    value: {\n      type: Number,\n      default: 0\n    },\n    badgeValue: {\n      type: Number,\n      default: 0\n    },\n    variant: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n@import \"src/scss/variants.scss\";\n\n  .list-card-indicator {\n    display: block;\n    width: 100%;\n    height: 100%;\n    min-height: 80px;\n    padding: 16px;\n    text-decoration: none;\n    background-color: var(--me-bg-gray);\n    border-radius: 8px;\n\n    &:hover {\n      background-color: var(--me-primary-7);\n\n      .list-badge-primary {\n        display: inline-block;\n      }\n\n      .list-badge-light-primary {\n        display: none;\n      }\n\n      .widget-label {\n        color: var(--me-primary-5);\n      }\n    }\n\n    .list-badge-primary {\n      display: none;\n    }\n\n    .list-badge-light-primary {\n      display: inline-block;\n    }\n\n    &__header,\n    &__body {\n      display: flex;\n      justify-content: space-between;\n      align-items: center;\n    }\n\n    &__header {\n      padding-bottom: 8px;\n\n      .widget-value {\n        color: var(--me-primary-2);\n        font-weight: 600;\n        font-size: 16px;\n      }\n    }\n\n    &__body {\n      .widget-label {\n        color: var(--me-gray-4);\n        font-size: 12px;\n      }\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$$ = "data-v-607ba934";
  /* module identifier */
  const __vue_module_identifier__$$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$$ = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$$, staticRenderFns: __vue_staticRenderFns__$$ },
    __vue_inject_styles__$$,
    __vue_script__$$,
    __vue_scope_id__$$,
    __vue_is_functional_template__$$,
    __vue_module_identifier__$$,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$10 = {
  name: 'MeListCardLink',
  components: {
    Icon: __vue_component__
  },

  props: {
    href: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$10 = script$10;

/* template */
var __vue_render__$10 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "a",
    {
      staticClass: "list-card-link",
      attrs: { href: _vm.href, "data-cy": "card-link-list" }
    },
    [
      _c(
        "div",
        { staticClass: "list-card-link__header" },
        [
          _c("Icon", {
            staticClass: "header-icon",
            attrs: { size: 16, icon: "me-icon-s icon-link" }
          })
        ],
        1
      ),
      _vm._v(" "),
      _c("div", { staticClass: "list-card-link__body" }, [
        _c("span", { staticClass: "widget-label" }, [
          _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")
        ])
      ])
    ]
  )
};
var __vue_staticRenderFns__$10 = [];
__vue_render__$10._withStripped = true;

  /* style */
  const __vue_inject_styles__$10 = function (inject) {
    if (!inject) return
    inject("data-v-bfcf2d6e_0", { source: ".list-card-link[data-v-bfcf2d6e] {\n  display: block;\n  width: 100%;\n  height: 100%;\n  min-height: 80px;\n  padding: 16px;\n  text-decoration: none;\n  background-color: var(--me-bg-gray);\n  border-radius: 8px;\n}\n.list-card-link[data-v-bfcf2d6e]:hover {\n  background-color: var(--me-primary-7);\n}\n.list-card-link:hover .header-icon[data-v-bfcf2d6e] {\n  color: var(--me-primary-2);\n}\n.list-card-link:hover .widget-label[data-v-bfcf2d6e] {\n  color: var(--me-primary-5);\n}\n.list-card-link__header[data-v-bfcf2d6e], .list-card-link__body[data-v-bfcf2d6e] {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n.list-card-link__header[data-v-bfcf2d6e] {\n  padding-bottom: 8px;\n}\n.list-card-link__header .header-icon[data-v-bfcf2d6e] {\n  color: var(--me-primary-5);\n}\n.list-card-link__body .widget-label[data-v-bfcf2d6e] {\n  color: var(--me-gray-4);\n  font-size: 12px;\n}\n\n/*# sourceMappingURL=ListCardLink.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/list/list-item/ListCardLink.vue","ListCardLink.vue"],"names":[],"mappings":"AA6CA;EACA,cAAA;EACA,WAAA;EACA,YAAA;EACA,gBAAA;EACA,aAAA;EACA,qBAAA;EACA,mCAAA;EACA,kBAAA;AC5CA;AD8CA;EACA,qCAAA;AC5CA;AD8CA;EACA,0BAAA;AC5CA;AD+CA;EACA,0BAAA;AC7CA;ADiDA;EAEA,aAAA;EACA,8BAAA;EACA,mBAAA;AChDA;ADmDA;EACA,mBAAA;ACjDA;ADmDA;EACA,0BAAA;ACjDA;ADsDA;EACA,uBAAA;EACA,eAAA;ACpDA;;AAEA,2CAA2C","file":"ListCardLink.vue","sourcesContent":["<template>\n  <a\n    :href=\"href\"\n    class=\"list-card-link\"\n    data-cy=\"card-link-list\"\n  >\n    <div class=\"list-card-link__header\">\n      <Icon\n        :size=\"16\"\n        class=\"header-icon\"\n        icon=\"me-icon-s icon-link\"\n      />\n    </div>\n\n    <div class=\"list-card-link__body\">\n      <span class=\"widget-label\">\n        {{ label }}\n      </span>\n    </div>\n  </a>\n</template>\n\n<script>\nimport Icon from '../../../../Icon.vue'\n\nexport default {\n  name: 'MeListCardLink',\n  components: {\n    Icon\n  },\n\n  props: {\n    href: {\n      type: String,\n      default: ''\n    },\n    label: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .list-card-link {\n    display: block;\n    width: 100%;\n    height: 100%;\n    min-height: 80px;\n    padding: 16px;\n    text-decoration: none;\n    background-color: var(--me-bg-gray);\n    border-radius: 8px;\n\n    &:hover {\n      background-color: var(--me-primary-7);\n\n      .header-icon {\n        color: var(--me-primary-2);\n      }\n\n      .widget-label {\n        color: var(--me-primary-5);\n      }\n    }\n\n    &__header,\n    &__body {\n      display: flex;\n      justify-content: space-between;\n      align-items: center;\n    }\n\n    &__header {\n      padding-bottom: 8px;\n\n      .header-icon {\n        color: var(--me-primary-5);\n      }\n    }\n\n    &__body {\n      .widget-label {\n        color: var(--me-gray-4);\n        font-size: 12px;\n      }\n    }\n  }\n</style>\n",".list-card-link {\n  display: block;\n  width: 100%;\n  height: 100%;\n  min-height: 80px;\n  padding: 16px;\n  text-decoration: none;\n  background-color: var(--me-bg-gray);\n  border-radius: 8px;\n}\n.list-card-link:hover {\n  background-color: var(--me-primary-7);\n}\n.list-card-link:hover .header-icon {\n  color: var(--me-primary-2);\n}\n.list-card-link:hover .widget-label {\n  color: var(--me-primary-5);\n}\n.list-card-link__header, .list-card-link__body {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n.list-card-link__header {\n  padding-bottom: 8px;\n}\n.list-card-link__header .header-icon {\n  color: var(--me-primary-5);\n}\n.list-card-link__body .widget-label {\n  color: var(--me-gray-4);\n  font-size: 12px;\n}\n\n/*# sourceMappingURL=ListCardLink.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$10 = "data-v-bfcf2d6e";
  /* module identifier */
  const __vue_module_identifier__$10 = undefined;
  /* functional template */
  const __vue_is_functional_template__$10 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$10 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$10, staticRenderFns: __vue_staticRenderFns__$10 },
    __vue_inject_styles__$10,
    __vue_script__$10,
    __vue_scope_id__$10,
    __vue_is_functional_template__$10,
    __vue_module_identifier__$10,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$11 = {
  name: 'MeWidgetListCard',
  components: {
    ListCardIndicator: __vue_component__$$,
    ListCardLink: __vue_component__$10,
    WidgetContent: __vue_component__$W
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    deletable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    error: {
      type: Object,
      default: () => null
    },
    click: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      widgetSubtypesEnum
    }
  }
};

/* script */
const __vue_script__$11 = script$11;

/* template */
var __vue_render__$11 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("WidgetContent", {
    staticClass: "widget-list-card",
    attrs: {
      editable: _vm.editable,
      deletable: _vm.deletable,
      title: _vm.label,
      error: _vm.error
        ? Object.assign({}, { widgetLabel: _vm.label }, _vm.error)
        : null,
      click: _vm.click
    },
    on: {
      editWidget: function($event) {
        return _vm.$emit("edit")
      },
      deleteWidget: function($event) {
        return _vm.$emit("delete")
      }
    },
    scopedSlots: _vm._u([
      {
        key: "body",
        fn: function() {
          return [
            _c(
              "div",
              { staticClass: "card-list" },
              _vm._l(_vm.items, function(item, index) {
                return _c(
                  "div",
                  { key: item.label + "_" + index, staticClass: "card-item" },
                  [
                    item.type === _vm.widgetSubtypesEnum.INDICATOR
                      ? _c(
                          "ListCardIndicator",
                          _vm._b({}, "ListCardIndicator", item, false)
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    item.type === _vm.widgetSubtypesEnum.LINK
                      ? _c(
                          "ListCardLink",
                          _vm._b({}, "ListCardLink", item, false)
                        )
                      : _vm._e()
                  ],
                  1
                )
              }),
              0
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$11 = [];
__vue_render__$11._withStripped = true;

  /* style */
  const __vue_inject_styles__$11 = function (inject) {
    if (!inject) return
    inject("data-v-280b7b34_0", { source: ".widget-list-card[data-v-280b7b34] {\n  display: flex;\n  flex-direction: column;\n}\n.widget-list-card .card-list[data-v-280b7b34] {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 16px;\n  width: 100%;\n  padding: 0 8px;\n  overflow: auto;\n}\n.widget-list-card .card-list .card-item[data-v-280b7b34] {\n  flex: 1;\n  cursor: pointer;\n  min-width: 87px;\n  min-height: 92px;\n  max-height: 100px;\n  overflow: hidden;\n}\n\n/*# sourceMappingURL=WidgetListCard.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/list/WidgetListCard.vue","WidgetListCard.vue"],"names":[],"mappings":"AAmFA;EACA,aAAA;EACA,sBAAA;AClFA;ADoFA;EACA,aAAA;EACA,eAAA;EACA,SAAA;EACA,WAAA;EACA,cAAA;EACA,cAAA;AClFA;ADoFA;EACA,OAAA;EACA,eAAA;EACA,eAAA;EACA,gBAAA;EACA,iBAAA;EACA,gBAAA;AClFA;;AAEA,6CAA6C","file":"WidgetListCard.vue","sourcesContent":["<template>\n  <WidgetContent\n    :editable=\"editable\"\n    :deletable=\"deletable\"\n    :title=\"label\"\n    :error=\"error ? { widgetLabel: label, ...error } : null\"\n    :click=\"click\"\n    @editWidget=\"$emit('edit')\"\n    @deleteWidget=\"$emit('delete')\"\n    class=\"widget-list-card\"\n  >\n    <template v-slot:body>\n      <div class=\"card-list\">\n        <div\n          v-for=\"(item, index) in items\"\n          :key=\"`${item.label}_${index}`\"\n          class=\"card-item\"\n        >\n          <ListCardIndicator\n            v-if=\"item.type === widgetSubtypesEnum.INDICATOR\"\n            v-bind=\"item\"\n          />\n\n          <ListCardLink\n            v-if=\"item.type === widgetSubtypesEnum.LINK\"\n            v-bind=\"item\"\n          />\n        </div>\n      </div>\n    </template>\n  </WidgetContent>\n</template>\n\n<script>\nimport WidgetContent from '../WidgetContent.vue'\nimport { widgetSubtypesEnum } from '../../enums/widgetTypesEnum.js'\nimport ListCardIndicator from './list-item/ListCardIndicator.vue'\nimport ListCardLink from './list-item/ListCardLink.vue'\n\nexport default {\n  name: 'MeWidgetListCard',\n  components: {\n    ListCardIndicator,\n    ListCardLink,\n    WidgetContent\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    error: {\n      type: Object,\n      default: () => null\n    },\n    click: {\n      type: Function,\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      widgetSubtypesEnum\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-list-card {\n    display: flex;\n    flex-direction: column;\n\n    .card-list {\n      display: flex;\n      flex-wrap: wrap;\n      gap: 16px;\n      width: 100%;\n      padding: 0 8px;\n      overflow: auto;\n\n      .card-item {\n        flex: 1;\n        cursor: pointer;\n        min-width: 87px;\n        min-height: 92px;\n        max-height: 100px;\n        overflow: hidden;\n      }\n    }\n  }\n</style>\n",".widget-list-card {\n  display: flex;\n  flex-direction: column;\n}\n.widget-list-card .card-list {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 16px;\n  width: 100%;\n  padding: 0 8px;\n  overflow: auto;\n}\n.widget-list-card .card-list .card-item {\n  flex: 1;\n  cursor: pointer;\n  min-width: 87px;\n  min-height: 92px;\n  max-height: 100px;\n  overflow: hidden;\n}\n\n/*# sourceMappingURL=WidgetListCard.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$11 = "data-v-280b7b34";
  /* module identifier */
  const __vue_module_identifier__$11 = undefined;
  /* functional template */
  const __vue_is_functional_template__$11 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$11 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$11, staticRenderFns: __vue_staticRenderFns__$11 },
    __vue_inject_styles__$11,
    __vue_script__$11,
    __vue_scope_id__$11,
    __vue_is_functional_template__$11,
    __vue_module_identifier__$11,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$12 = {
  name: 'MeListItemIndicator',
  components: {
    Badge: __vue_component__$x
  },

  props: {
    href: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: Number,
      default: 0
    },
    badgeValue: {
      type: Number,
      default: 0
    }
  }
};

/* script */
const __vue_script__$12 = script$12;

/* template */
var __vue_render__$12 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "a",
    {
      staticClass: "list-item-indicator",
      attrs: { href: _vm.href, "data-cy": "item-indicator-list" }
    },
    [
      _c(
        "span",
        {
          staticClass: "item-label",
          attrs: { "data-cy": "item-indicator-label" }
        },
        [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "d-flex flex-nowrap align-items-center" },
        [
          _vm.badgeValue
            ? _c(
                "Badge",
                {
                  staticClass: "list-badge-primary",
                  attrs: { appearance: "primary" }
                },
                [_vm._v("\n      +" + _vm._s(_vm.badgeValue) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.badgeValue
            ? _c(
                "Badge",
                {
                  staticClass: "list-badge-light-primary",
                  attrs: { appearance: "light-primary" }
                },
                [_vm._v("\n      +" + _vm._s(_vm.badgeValue) + "\n    ")]
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "span",
            {
              staticClass: "item-value",
              attrs: { "data-cy": "item-indicator-value" }
            },
            [_vm._v("\n      " + _vm._s(_vm.value) + "\n    ")]
          )
        ],
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$12 = [];
__vue_render__$12._withStripped = true;

  /* style */
  const __vue_inject_styles__$12 = function (inject) {
    if (!inject) return
    inject("data-v-8352a12c_0", { source: ".list-item-indicator[data-v-8352a12c] {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  border-radius: 8px;\n  text-decoration: none;\n  padding: 8px;\n}\n.list-item-indicator[data-v-8352a12c]:hover {\n  background-color: var(--me-primary-7);\n}\n.list-item-indicator:hover .list-badge-primary[data-v-8352a12c] {\n  display: inline-block;\n}\n.list-item-indicator:hover .list-badge-light-primary[data-v-8352a12c] {\n  display: none;\n}\n.list-item-indicator:hover .item-label[data-v-8352a12c] {\n  color: var(--me-primary-2);\n}\n.list-item-indicator .list-badge-primary[data-v-8352a12c] {\n  display: none;\n}\n.list-item-indicator .list-badge-light-primary[data-v-8352a12c] {\n  display: inline-block;\n}\n.list-item-indicator .item-label[data-v-8352a12c] {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.list-item-indicator .item-value[data-v-8352a12c] {\n  font-size: 14px;\n  color: var(--me-primary-2);\n  font-weight: 600;\n  padding-left: 8px;\n}\n\n/*# sourceMappingURL=ListItemIndicator.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/list/list-item/ListItemIndicator.vue","ListItemIndicator.vue"],"names":[],"mappings":"AAuEA;EACA,aAAA;EACA,8BAAA;EACA,mBAAA;EACA,kBAAA;EACA,qBAAA;EACA,YAAA;ACtEA;ADwEA;EACA,qCAAA;ACtEA;ADwEA;EACA,qBAAA;ACtEA;ADyEA;EACA,aAAA;ACvEA;AD0EA;EACA,0BAAA;ACxEA;AD4EA;EACA,aAAA;AC1EA;AD6EA;EACA,qBAAA;AC3EA;AD8EA;EACA,eAAA;EACA,uBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;AC5EA;AD+EA;EACA,eAAA;EACA,0BAAA;EACA,gBAAA;EACA,iBAAA;AC7EA;;AAEA,gDAAgD","file":"ListItemIndicator.vue","sourcesContent":["<template>\n  <a\n    :href=\"href\"\n    class=\"list-item-indicator\"\n    data-cy=\"item-indicator-list\"\n  >\n    <span\n      class=\"item-label\"\n      data-cy=\"item-indicator-label\"\n    >\n      {{ label }}\n    </span>\n\n    <div class=\"d-flex flex-nowrap align-items-center\">\n      <Badge\n        v-if=\"badgeValue\"\n        class=\"list-badge-primary\"\n        appearance=\"primary\"\n      >\n        +{{ badgeValue }}\n      </Badge>\n\n      <Badge\n        v-if=\"badgeValue\"\n        class=\"list-badge-light-primary\"\n        appearance=\"light-primary\"\n      >\n        +{{ badgeValue }}\n      </Badge>\n\n      <span\n        class=\"item-value\"\n        data-cy=\"item-indicator-value\"\n      >\n        {{ value }}\n      </span>\n    </div>\n  </a>\n</template>\n\n<script>\nimport Badge from '../../../../Badge.vue'\n\nexport default {\n  name: 'MeListItemIndicator',\n  components: {\n    Badge\n  },\n\n  props: {\n    href: {\n      type: String,\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    value: {\n      type: Number,\n      default: 0\n    },\n    badgeValue: {\n      type: Number,\n      default: 0\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .list-item-indicator {\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    border-radius: 8px;\n    text-decoration: none;\n    padding: 8px;\n\n    &:hover {\n      background-color: var(--me-primary-7);\n\n      .list-badge-primary {\n        display: inline-block;\n      }\n\n      .list-badge-light-primary {\n        display: none;\n      }\n\n      .item-label {\n        color: var(--me-primary-2);\n      }\n    }\n\n    .list-badge-primary {\n      display: none;\n    }\n\n    .list-badge-light-primary {\n      display: inline-block;\n    }\n\n    .item-label {\n      font-size: 12px;\n      color: var(--me-gray-4);\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n    }\n\n    .item-value {\n      font-size: 14px;\n      color: var(--me-primary-2);\n      font-weight: 600;\n      padding-left: 8px;\n    }\n  }\n</style>\n",".list-item-indicator {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  border-radius: 8px;\n  text-decoration: none;\n  padding: 8px;\n}\n.list-item-indicator:hover {\n  background-color: var(--me-primary-7);\n}\n.list-item-indicator:hover .list-badge-primary {\n  display: inline-block;\n}\n.list-item-indicator:hover .list-badge-light-primary {\n  display: none;\n}\n.list-item-indicator:hover .item-label {\n  color: var(--me-primary-2);\n}\n.list-item-indicator .list-badge-primary {\n  display: none;\n}\n.list-item-indicator .list-badge-light-primary {\n  display: inline-block;\n}\n.list-item-indicator .item-label {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.list-item-indicator .item-value {\n  font-size: 14px;\n  color: var(--me-primary-2);\n  font-weight: 600;\n  padding-left: 8px;\n}\n\n/*# sourceMappingURL=ListItemIndicator.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$12 = "data-v-8352a12c";
  /* module identifier */
  const __vue_module_identifier__$12 = undefined;
  /* functional template */
  const __vue_is_functional_template__$12 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$12 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$12, staticRenderFns: __vue_staticRenderFns__$12 },
    __vue_inject_styles__$12,
    __vue_script__$12,
    __vue_scope_id__$12,
    __vue_is_functional_template__$12,
    __vue_module_identifier__$12,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$13 = {
  name: 'MeListItemLink',
  components: {
    Icon: __vue_component__
  },

  props: {
    href: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$13 = script$13;

/* template */
var __vue_render__$13 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "a",
    {
      staticClass: "me-list-link",
      attrs: { href: _vm.href, "data-cy": "item-link-list" }
    },
    [
      _c("span", { staticClass: "item-label" }, [_vm._v(_vm._s(_vm.label))]),
      _vm._v(" "),
      _c("Icon", {
        attrs: { icon: "me-icon-s icon-link", color: "var(--me-primary-2)" }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$13 = [];
__vue_render__$13._withStripped = true;

  /* style */
  const __vue_inject_styles__$13 = function (inject) {
    if (!inject) return
    inject("data-v-68721807_0", { source: ".me-list-link[data-v-68721807] {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  border-radius: 8px;\n  padding: 8px;\n  text-decoration: none;\n}\n.me-list-link .item-label[data-v-68721807] {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.me-list-link[data-v-68721807]:hover {\n  background-color: var(--me-primary-7);\n}\n.me-list-link:hover .item-label[data-v-68721807] {\n  color: var(--me-primary-2);\n}\n\n/*# sourceMappingURL=ListItemLink.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/list/list-item/ListItemLink.vue","ListItemLink.vue"],"names":[],"mappings":"AAsCA;EACA,aAAA;EACA,8BAAA;EACA,mBAAA;EACA,kBAAA;EACA,YAAA;EACA,qBAAA;ACrCA;ADuCA;EACA,eAAA;EACA,uBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;ACrCA;ADwCA;EACA,qCAAA;ACtCA;ADwCA;EACA,0BAAA;ACtCA;;AAEA,2CAA2C","file":"ListItemLink.vue","sourcesContent":["<template>\n  <a\n    :href=\"href\"\n    class=\"me-list-link\"\n    data-cy=\"item-link-list\"\n  >\n    <span class=\"item-label\">{{ label }}</span>\n\n    <Icon\n      icon=\"me-icon-s icon-link\"\n      color=\"var(--me-primary-2)\"\n    />\n  </a>\n</template>\n\n<script>\nimport Icon from '../../../../Icon.vue'\n\nexport default {\n  name: 'MeListItemLink',\n  components: {\n    Icon\n  },\n\n  props: {\n    href: {\n      type: String,\n      default: null\n    },\n    label: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-list-link {\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    border-radius: 8px;\n    padding: 8px;\n    text-decoration: none;\n\n    .item-label {\n      font-size: 12px;\n      color: var(--me-gray-4);\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n    }\n\n    &:hover {\n      background-color: var(--me-primary-7);\n\n      .item-label {\n        color: var(--me-primary-2);\n      }\n    }\n  }\n</style>\n",".me-list-link {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  border-radius: 8px;\n  padding: 8px;\n  text-decoration: none;\n}\n.me-list-link .item-label {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.me-list-link:hover {\n  background-color: var(--me-primary-7);\n}\n.me-list-link:hover .item-label {\n  color: var(--me-primary-2);\n}\n\n/*# sourceMappingURL=ListItemLink.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$13 = "data-v-68721807";
  /* module identifier */
  const __vue_module_identifier__$13 = undefined;
  /* functional template */
  const __vue_is_functional_template__$13 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$13 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$13, staticRenderFns: __vue_staticRenderFns__$13 },
    __vue_inject_styles__$13,
    __vue_script__$13,
    __vue_scope_id__$13,
    __vue_is_functional_template__$13,
    __vue_module_identifier__$13,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$14 = {
  name: 'MeWidgetListLink',
  components: {
    ListItemIndicator: __vue_component__$12,
    ListItemLink: __vue_component__$13,
    WidgetContent: __vue_component__$W
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    deletable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    error: {
      type: Object,
      default: () => null
    },
    click: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      widgetSubtypesEnum
    }
  }
};

/* script */
const __vue_script__$14 = script$14;

/* template */
var __vue_render__$14 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("WidgetContent", {
    staticClass: "widget-list-link",
    attrs: {
      editable: _vm.editable,
      deletable: _vm.deletable,
      title: _vm.label,
      error: _vm.error
        ? Object.assign({}, { widgetLabel: _vm.label }, _vm.error)
        : null,
      click: _vm.click
    },
    on: {
      editWidget: function($event) {
        return _vm.$emit("edit")
      },
      deleteWidget: function($event) {
        return _vm.$emit("delete")
      }
    },
    scopedSlots: _vm._u([
      {
        key: "body",
        fn: function() {
          return [
            _c(
              "div",
              { staticClass: "link-list" },
              _vm._l(_vm.items, function(item) {
                return _c(
                  "div",
                  { key: item.id || item.label, staticClass: "link-item" },
                  [
                    item.type === _vm.widgetSubtypesEnum.INDICATOR
                      ? _c(
                          "ListItemIndicator",
                          _vm._b({}, "ListItemIndicator", item, false)
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    item.type === _vm.widgetSubtypesEnum.LINK
                      ? _c(
                          "ListItemLink",
                          _vm._b({}, "ListItemLink", item, false)
                        )
                      : _vm._e()
                  ],
                  1
                )
              }),
              0
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$14 = [];
__vue_render__$14._withStripped = true;

  /* style */
  const __vue_inject_styles__$14 = function (inject) {
    if (!inject) return
    inject("data-v-d1f6a82a_0", { source: ".widget-list-link[data-v-d1f6a82a] {\n  display: flex;\n  flex-direction: column;\n}\n.widget-list-link .link-list[data-v-d1f6a82a] {\n  width: 100%;\n  display: flex;\n  flex-direction: column;\n  overflow-y: auto;\n  gap: 8px;\n}\n.widget-list-link .link-list .link-item[data-v-d1f6a82a] {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=WidgetListLink.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/list/WidgetListLink.vue","WidgetListLink.vue"],"names":[],"mappings":"AAmFA;EACA,aAAA;EACA,sBAAA;AClFA;ADoFA;EACA,WAAA;EACA,aAAA;EACA,sBAAA;EACA,gBAAA;EACA,QAAA;AClFA;ADoFA;EACA,eAAA;AClFA;;AAEA,6CAA6C","file":"WidgetListLink.vue","sourcesContent":["<template>\n  <WidgetContent\n    :editable=\"editable\"\n    :deletable=\"deletable\"\n    :title=\"label\"\n    :error=\"error ? { widgetLabel: label, ...error } : null\"\n    :click=\"click\"\n    @editWidget=\"$emit('edit')\"\n    @deleteWidget=\"$emit('delete')\"\n    class=\"widget-list-link\"\n  >\n    <template v-slot:body>\n      <div class=\"link-list\">\n        <div\n          v-for=\"item in items\"\n          :key=\"item.id || item.label\"\n          class=\"link-item\"\n        >\n          <ListItemIndicator\n            v-if=\"item.type === widgetSubtypesEnum.INDICATOR\"\n            v-bind=\"item\"\n          />\n\n          <ListItemLink\n            v-if=\"item.type === widgetSubtypesEnum.LINK\"\n            v-bind=\"item\"\n          />\n        </div>\n      </div>\n    </template>\n  </WidgetContent>\n</template>\n\n<script>\nimport WidgetContent from '../WidgetContent.vue'\nimport { widgetSubtypesEnum } from '../../enums/widgetTypesEnum.js'\nimport ListItemIndicator from './list-item/ListItemIndicator.vue'\nimport ListItemLink from './list-item/ListItemLink.vue'\n\nexport default {\n  name: 'MeWidgetListLink',\n  components: {\n    ListItemIndicator,\n    ListItemLink,\n    WidgetContent\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    error: {\n      type: Object,\n      default: () => null\n    },\n    click: {\n      type: Function,\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      widgetSubtypesEnum\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-list-link {\n    display: flex;\n    flex-direction: column;\n\n    .link-list {\n      width: 100%;\n      display: flex;\n      flex-direction: column;\n      overflow-y: auto;\n      gap: 8px;\n\n      .link-item {\n        cursor: pointer;\n      }\n    }\n  }\n</style>\n",".widget-list-link {\n  display: flex;\n  flex-direction: column;\n}\n.widget-list-link .link-list {\n  width: 100%;\n  display: flex;\n  flex-direction: column;\n  overflow-y: auto;\n  gap: 8px;\n}\n.widget-list-link .link-list .link-item {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=WidgetListLink.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$14 = "data-v-d1f6a82a";
  /* module identifier */
  const __vue_module_identifier__$14 = undefined;
  /* functional template */
  const __vue_is_functional_template__$14 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$14 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$14, staticRenderFns: __vue_staticRenderFns__$14 },
    __vue_inject_styles__$14,
    __vue_script__$14,
    __vue_scope_id__$14,
    __vue_is_functional_template__$14,
    __vue_module_identifier__$14,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$15 = {
  name: 'MeWidgetListButton',
  components: {
    WidgetContent: __vue_component__$W
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    deletable: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: true
    },
    error: {
      type: Object,
      default: () => null
    },
    click: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      widgetButtonTypesEnum
    }
  }
};

/* script */
const __vue_script__$15 = script$15;

/* template */
var __vue_render__$15 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("WidgetContent", {
    staticClass: "widget-list-button",
    attrs: {
      editable: _vm.editable,
      deletable: _vm.deletable,
      title: _vm.label,
      error: _vm.error
        ? Object.assign({}, { widgetLabel: _vm.label }, _vm.error)
        : null,
      click: _vm.click
    },
    on: {
      editWidget: function($event) {
        return _vm.$emit("edit")
      },
      deleteWidget: function($event) {
        return _vm.$emit("delete")
      }
    },
    scopedSlots: _vm._u([
      {
        key: "body",
        fn: function() {
          return [
            _c(
              "div",
              { staticClass: "button-list" },
              _vm._l(_vm.items, function(item) {
                return _c(
                  "div",
                  {
                    key: item.id || item.label,
                    staticClass: "button-item-container"
                  },
                  [
                    item.type === _vm.widgetButtonTypesEnum.SIMPLE
                      ? _c(
                          "me-button",
                          {
                            staticClass: "button-item-container__button",
                            attrs: {
                              href: item.href,
                              variant: "primary",
                              block: ""
                            }
                          },
                          [
                            _vm._v(
                              "\n          " + _vm._s(item.label) + "\n        "
                            )
                          ]
                        )
                      : _vm._e(),
                    _vm._v(" "),
                    item.type === _vm.widgetButtonTypesEnum.MULTIPLE
                      ? _c("me-drop-down", {
                          staticClass: "button-item-container__dropdown",
                          attrs: {
                            items: item.links,
                            text: item.label,
                            variant: "primary",
                            boundary: "viewport",
                            split: "",
                            block: ""
                          },
                          on: {
                            click: function($event) {
                              item.click
                                ? item.click()
                                : function() {
                                    return null
                                  };
                            }
                          }
                        })
                      : _vm._e()
                  ],
                  1
                )
              }),
              0
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$15 = [];
__vue_render__$15._withStripped = true;

  /* style */
  const __vue_inject_styles__$15 = function (inject) {
    if (!inject) return
    inject("data-v-5bdc64d2_0", { source: ".widget-list-button[data-v-5bdc64d2] {\n  display: flex;\n  flex-direction: column;\n}\n.widget-list-button .button-list[data-v-5bdc64d2] {\n  overflow: auto;\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  gap: 16px;\n  padding: 0 8px;\n}\n.widget-list-button .button-list .button-item-container[data-v-5bdc64d2] {\n  display: flex;\n  flex-grow: 1;\n  cursor: pointer;\n  min-width: 120px;\n}\n.widget-list-button .button-list .button-item-container__dropdown[data-v-5bdc64d2] {\n  padding: 0;\n}\n.widget-list-button .button-list .button-item-container__dropdown[data-v-5bdc64d2]  button:first-of-type {\n  display: block !important;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.widget-list-button .button-list .button-item-container__button[data-v-5bdc64d2] {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n/*# sourceMappingURL=WidgetListButton.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/widgets/list/WidgetListButton.vue","WidgetListButton.vue"],"names":[],"mappings":"AA2FA;EACA,aAAA;EACA,sBAAA;AC1FA;AD4FA;EACA,cAAA;EACA,aAAA;EACA,eAAA;EACA,WAAA;EACA,SAAA;EACA,cAAA;AC1FA;AD4FA;EACA,aAAA;EACA,YAAA;EACA,eAAA;EACA,gBAAA;AC1FA;AD4FA;EACA,UAAA;AC1FA;AD4FA;EACA,yBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;AC1FA;AD8FA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;AC5FA;;AAEA,+CAA+C","file":"WidgetListButton.vue","sourcesContent":["<template>\n  <WidgetContent\n    :editable=\"editable\"\n    :deletable=\"deletable\"\n    :title=\"label\"\n    :error=\"error ? { widgetLabel: label, ...error } : null\"\n    :click=\"click\"\n    @editWidget=\"$emit('edit')\"\n    @deleteWidget=\"$emit('delete')\"\n    class=\"widget-list-button\"\n  >\n    <template v-slot:body>\n      <div class=\"button-list\">\n        <div\n          v-for=\"item in items\"\n          :key=\"item.id || item.label\"\n          class=\"button-item-container\"\n        >\n          <me-button\n            v-if=\"item.type === widgetButtonTypesEnum.SIMPLE\"\n            :href=\"item.href\"\n            variant=\"primary\"\n            class=\"button-item-container__button\"\n            block\n          >\n            {{ item.label }}\n          </me-button>\n\n          <me-drop-down\n            v-if=\"item.type === widgetButtonTypesEnum.MULTIPLE\"\n            :items=\"item.links\"\n            :text=\"item.label\"\n            @click=\"item.click ? item.click() : () => null\"\n            class=\"button-item-container__dropdown\"\n            variant=\"primary\"\n            boundary=\"viewport\"\n            split\n            block\n          />\n        </div>\n      </div>\n    </template>\n  </WidgetContent>\n</template>\n\n<script>\nimport WidgetContent from '../WidgetContent.vue'\nimport { widgetButtonTypesEnum } from '../../enums/widgetTypesEnum'\n\nexport default {\n  name: 'MeWidgetListButton',\n  components: {\n    WidgetContent\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    deletable: {\n      type: Boolean,\n      default: false\n    },\n    editable: {\n      type: Boolean,\n      default: true\n    },\n    error: {\n      type: Object,\n      default: () => null\n    },\n    click: {\n      type: Function,\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      widgetButtonTypesEnum\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .widget-list-button {\n    display: flex;\n    flex-direction: column;\n\n    .button-list {\n      overflow: auto;\n      display: flex;\n      flex-wrap: wrap;\n      width: 100%;\n      gap: 16px;\n      padding: 0 8px;\n\n      .button-item-container {\n        display: flex;\n        flex-grow: 1;\n        cursor: pointer;\n        min-width: 120px;\n\n        &__dropdown {\n          padding: 0;\n\n          ::v-deep button:first-of-type {\n            display: block !important;\n            white-space: nowrap;\n            overflow: hidden;\n            text-overflow: ellipsis;\n          }\n        }\n\n        &__button {\n          white-space: nowrap;\n          overflow: hidden;\n          text-overflow: ellipsis;\n        }\n      }\n    }\n  }\n</style>\n",".widget-list-button {\n  display: flex;\n  flex-direction: column;\n}\n.widget-list-button .button-list {\n  overflow: auto;\n  display: flex;\n  flex-wrap: wrap;\n  width: 100%;\n  gap: 16px;\n  padding: 0 8px;\n}\n.widget-list-button .button-list .button-item-container {\n  display: flex;\n  flex-grow: 1;\n  cursor: pointer;\n  min-width: 120px;\n}\n.widget-list-button .button-list .button-item-container__dropdown {\n  padding: 0;\n}\n.widget-list-button .button-list .button-item-container__dropdown ::v-deep button:first-of-type {\n  display: block !important;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.widget-list-button .button-list .button-item-container__button {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n/*# sourceMappingURL=WidgetListButton.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$15 = "data-v-5bdc64d2";
  /* module identifier */
  const __vue_module_identifier__$15 = undefined;
  /* functional template */
  const __vue_is_functional_template__$15 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$15 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$15, staticRenderFns: __vue_staticRenderFns__$15 },
    __vue_inject_styles__$15,
    __vue_script__$15,
    __vue_scope_id__$15,
    __vue_is_functional_template__$15,
    __vue_module_identifier__$15,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$16 = {
  name: 'MeCardIndicatorForm',
  props: {
    label: {
      type: String,
      default: ''
    },
    filter: {
      type: Object,
      default: null
    },
    indicatorFilters: {
      type: Array,
      default: () => []
    }
  },

  validations: {
    label: {
      required: required$1
    },
    filter: {
      required: required$1
    }
  },

  watch: {
    label: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    },
    filter: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    }
  },

  methods: {
    updateNameField(value) {
      this.$emit('update:label', value);
    },

    updateFilterField(value) {
      this.$emit('update:filter', value);
      this.$v.filter.$touch();
    }
  }
};

/* script */
const __vue_script__$16 = script$16;

/* template */
var __vue_render__$16 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "card-indicator-form" },
    [
      _c(
        "me-form-group",
        {
          staticClass: "card-indicator-form__form-group",
          attrs: {
            state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
            label: _vm.$toolkit.t("dashboard.forms.fields.indicatorName") + "*",
            "invalid-feedback":
              "" + _vm.$toolkit.t("dashboard.forms.feedback.requiredField")
          }
        },
        [
          _c("me-text-input", {
            attrs: {
              state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
              value: _vm.label
            },
            on: {
              input: _vm.updateNameField,
              blur: function($event) {
                return _vm.$v.label.$touch()
              }
            }
          })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "me-form-group",
        {
          staticClass: "card-indicator-form__form-group",
          attrs: {
            state: _vm.$v.filter.$dirty ? !_vm.$v.filter.$anyError : null,
            label: _vm.$toolkit.t("dashboard.forms.fields.filter") + "*",
            "invalid-feedback":
              "" + _vm.$toolkit.t("dashboard.forms.feedback.requiredField")
          }
        },
        [
          _c("me-select", {
            attrs: {
              state:
                _vm.$v.filter.$dirty && _vm.$v.filter.$anyError ? false : null,
              value: _vm.filter,
              options: _vm.indicatorFilters,
              clearable: false,
              "append-to-body": ""
            },
            on: { input: _vm.updateFilterField }
          })
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$16 = [];
__vue_render__$16._withStripped = true;

  /* style */
  const __vue_inject_styles__$16 = function (inject) {
    if (!inject) return
    inject("data-v-494d1629_0", { source: ".card-indicator-form__form-group[data-v-494d1629] {\n  padding: 12px 0;\n}\n\n/*# sourceMappingURL=CardIndicatorForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/config/CardIndicatorForm.vue","CardIndicatorForm.vue"],"names":[],"mappings":"AA6FA;EACA,eAAA;AC5FA;;AAEA,gDAAgD","file":"CardIndicatorForm.vue","sourcesContent":["<template>\n  <div class=\"card-indicator-form\">\n    <me-form-group\n      :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n      :label=\"`${$toolkit.t('dashboard.forms.fields.indicatorName')}*`\"\n      :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n      class=\"card-indicator-form__form-group\"\n    >\n      <me-text-input\n        :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n        :value=\"label\"\n        @input=\"updateNameField\"\n        @blur=\"$v.label.$touch()\"\n      />\n    </me-form-group>\n\n    <me-form-group\n      :state=\"$v.filter.$dirty ? !$v.filter.$anyError : null\"\n      :label=\"`${$toolkit.t('dashboard.forms.fields.filter')}*`\"\n      :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n      class=\"card-indicator-form__form-group\"\n    >\n      <me-select\n        :state=\"$v.filter.$dirty && $v.filter.$anyError ? false : null\"\n        :value=\"filter\"\n        :options=\"indicatorFilters\"\n        :clearable=\"false\"\n        @input=\"updateFilterField\"\n        append-to-body\n      />\n    </me-form-group>\n  </div>\n</template>\n\n<script>\nimport required from 'vuelidate/lib/validators/required'\n\nexport default {\n  name: 'MeCardIndicatorForm',\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    filter: {\n      type: Object,\n      default: null\n    },\n    indicatorFilters: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  validations: {\n    label: {\n      required\n    },\n    filter: {\n      required\n    }\n  },\n\n  watch: {\n    label: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    },\n    filter: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    }\n  },\n\n  methods: {\n    updateNameField(value) {\n      this.$emit('update:label', value)\n    },\n\n    updateFilterField(value) {\n      this.$emit('update:filter', value)\n      this.$v.filter.$touch()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .card-indicator-form {\n    &__form-group {\n      padding: 12px 0;\n    }\n  }\n</style>\n",".card-indicator-form__form-group {\n  padding: 12px 0;\n}\n\n/*# sourceMappingURL=CardIndicatorForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$16 = "data-v-494d1629";
  /* module identifier */
  const __vue_module_identifier__$16 = undefined;
  /* functional template */
  const __vue_is_functional_template__$16 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$16 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$16, staticRenderFns: __vue_staticRenderFns__$16 },
    __vue_inject_styles__$16,
    __vue_script__$16,
    __vue_scope_id__$16,
    __vue_is_functional_template__$16,
    __vue_module_identifier__$16,
    false,
    createInjector,
    undefined,
    undefined
  );

var url = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;



var urlRegex = /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;

var _default = (0, common.regex)('url', urlRegex);

exports.default = _default;
});

var url$1 = unwrapExports(url);

//

var script$17 = {
  name: 'CardLinkForm',
  props: {
    label: {
      type: String,
      default: ''
    },
    href: {
      type: String,
      default: ''
    }
  },

  computed: {
    urlFeedback() {
      if (!this.$v.href.required) return this.$toolkit.t('dashboard.forms.feedback.requiredField')
      if (!this.$v.href.url) return this.$toolkit.t('dashboard.forms.feedback.invalidUrl')
      return ''
    }
  },

  validations: {
    label: {
      required: required$1
    },
    href: {
      required: required$1,
      url: url$1
    }
  },

  watch: {
    label: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    },
    href: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    }
  },

  methods: {
    updateLabelField(value) {
      this.$emit('update:label', value);
    },

    updateHrefField(value) {
      this.$emit('update:href', value);
    }
  }
};

/* script */
const __vue_script__$17 = script$17;

/* template */
var __vue_render__$17 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "card-link-form" },
    [
      _c(
        "me-form-group",
        {
          staticClass: "card-link-form__form-group",
          attrs: {
            state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
            label: _vm.$toolkit.t("dashboard.forms.fields.indicatorName") + "*",
            "invalid-feedback":
              "" + _vm.$toolkit.t("dashboard.forms.feedback.requiredField")
          }
        },
        [
          _c("me-text-input", {
            attrs: {
              state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
              value: _vm.label
            },
            on: {
              input: _vm.updateLabelField,
              blur: function($event) {
                return _vm.$v.label.$touch()
              }
            }
          })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "me-form-group",
        {
          staticClass: "card-link-form__form-group",
          attrs: {
            state: _vm.$v.href.$dirty ? !_vm.$v.href.$anyError : null,
            label: _vm.$toolkit.t("dashboard.forms.fields.url") + "*",
            "invalid-feedback": _vm.urlFeedback
          }
        },
        [
          _c("me-text-input", {
            attrs: {
              state: _vm.$v.href.$dirty ? !_vm.$v.href.$anyError : null,
              value: _vm.href,
              placeholder:
                "" + _vm.$toolkit.t("dashboard.forms.placeholders.url")
            },
            on: {
              input: _vm.updateHrefField,
              blur: function($event) {
                return _vm.$v.href.$touch()
              }
            }
          })
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$17 = [];
__vue_render__$17._withStripped = true;

  /* style */
  const __vue_inject_styles__$17 = function (inject) {
    if (!inject) return
    inject("data-v-d8137084_0", { source: ".card-link-form__form-group[data-v-d8137084] {\n  padding: 12px 0;\n}\n\n/*# sourceMappingURL=CardLinkForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/config/CardLinkForm.vue","CardLinkForm.vue"],"names":[],"mappings":"AAiGA;EACA,eAAA;AChGA;;AAEA,2CAA2C","file":"CardLinkForm.vue","sourcesContent":["<template>\n  <div class=\"card-link-form\">\n    <me-form-group\n      :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n      :label=\"`${$toolkit.t('dashboard.forms.fields.indicatorName')}*`\"\n      :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n      class=\"card-link-form__form-group\"\n    >\n      <me-text-input\n        :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n        :value=\"label\"\n        @input=\"updateLabelField\"\n        @blur=\"$v.label.$touch()\"\n      />\n    </me-form-group>\n\n    <me-form-group\n      :state=\"$v.href.$dirty ? !$v.href.$anyError : null\"\n      :label=\"`${$toolkit.t('dashboard.forms.fields.url')}*`\"\n      :invalid-feedback=\"urlFeedback\"\n      class=\"card-link-form__form-group\"\n    >\n      <me-text-input\n        :state=\"$v.href.$dirty ? !$v.href.$anyError : null\"\n        :value=\"href\"\n        :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.url')}`\"\n        @input=\"updateHrefField\"\n        @blur=\"$v.href.$touch()\"\n      />\n    </me-form-group>\n  </div>\n</template>\n\n<script>\nimport required from 'vuelidate/lib/validators/required'\nimport url from 'vuelidate/lib/validators/url'\n\nexport default {\n  name: 'CardLinkForm',\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    href: {\n      type: String,\n      default: ''\n    }\n  },\n\n  computed: {\n    urlFeedback() {\n      if (!this.$v.href.required) return this.$toolkit.t('dashboard.forms.feedback.requiredField')\n      if (!this.$v.href.url) return this.$toolkit.t('dashboard.forms.feedback.invalidUrl')\n      return ''\n    }\n  },\n\n  validations: {\n    label: {\n      required\n    },\n    href: {\n      required,\n      url\n    }\n  },\n\n  watch: {\n    label: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    },\n    href: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    }\n  },\n\n  methods: {\n    updateLabelField(value) {\n      this.$emit('update:label', value)\n    },\n\n    updateHrefField(value) {\n      this.$emit('update:href', value)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .card-link-form {\n    &__form-group {\n      padding: 12px 0;\n    }\n  }\n</style>\n",".card-link-form__form-group {\n  padding: 12px 0;\n}\n\n/*# sourceMappingURL=CardLinkForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$17 = "data-v-d8137084";
  /* module identifier */
  const __vue_module_identifier__$17 = undefined;
  /* functional template */
  const __vue_is_functional_template__$17 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$17 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$17, staticRenderFns: __vue_staticRenderFns__$17 },
    __vue_inject_styles__$17,
    __vue_script__$17,
    __vue_scope_id__$17,
    __vue_is_functional_template__$17,
    __vue_module_identifier__$17,
    false,
    createInjector,
    undefined,
    undefined
  );

var maxLength = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;



var _default = function _default(length) {
  return (0, common.withParams)({
    type: 'maxLength',
    max: length
  }, function (value) {
    return !(0, common.req)(value) || (0, common.len)(value) <= length;
  });
};

exports.default = _default;
});

var maxLength$1 = unwrapExports(maxLength);

var minLength = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;



var _default = function _default(length) {
  return (0, common.withParams)({
    type: 'minLength',
    min: length
  }, function (value) {
    return !(0, common.req)(value) || (0, common.len)(value) >= length;
  });
};

exports.default = _default;
});

var minLength$1 = unwrapExports(minLength);

//

var script$18 = {
  components: {
    Draggable: __vue_component__$s
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    filterOptions: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      widgetButtonTypesEnum,
      currentItems: cloneDeep_1(this.items).map((item, index) => ({ ...item, id: item.id || index.toString() })),
      listTypeOptions: [
        {
          name: this.$toolkit.t('dashboard.forms.fields.buttonType.simple'),
          value: widgetButtonTypesEnum.SIMPLE
        },
        {
          name: this.$toolkit.t('dashboard.forms.fields.buttonType.multiple'),
          value: widgetButtonTypesEnum.MULTIPLE
        }
      ]
    }
  },

  validations: {
    label: {
      required: required$1,
      maxLength: maxLength$1(180)
    },

    currentItems: {
      required: required$1,
      minLength: minLength$1(1),
      $each: {
        $trackBy: 'id',
        type: {
          required: required$1
        },
        label: {
          required: required$1
        },
        href: {
          url: url$1,
          required: requiredIf$1(({ type }) => {
            return type === widgetButtonTypesEnum.SIMPLE
          })
        },
        links: {
          required: requiredIf$1(({ type }) => {
            return type === widgetButtonTypesEnum.MULTIPLE
          }),
          $each: {
            label: { required: required$1 },
            href: { required: required$1, url: url$1 }
          }

        }
      }
    }
  },

  computed: {
    itemsDrag: {
      get() {
        return this.currentItems
      },
      set(value) {
        this.currentItems = value;
        this.$emit('update:items', value);
      }
    },

    listNameFeedback() {
      if (!this.$v.label.required) return this.$toolkit.t('dashboard.forms.feedback.requiredField')
      if (!this.$v.label.maxLength) {
        const modelLen = this.$v.label.$model.length;
        return `${this.$toolkit.t('dashboard.forms.feedback.charLimit', modelLen)}`
      }
      return ''
    }
  },

  watch: {
    items(value) {
      this.currentItems = cloneDeep_1(value).map((item, index) => ({ ...item, id: item.id || index.toString() }));
    },

    currentItems: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    },

    label: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    }
  },

  methods: {
    addItem() {
      this.currentItems = [{
        id: new Date().getTime().toString(),
        label: '',
        type: widgetButtonTypesEnum.SIMPLE,
        href: ''
      }, ...this.currentItems];
    },

    removeItem(index) {
      this.currentItems[index] = null;
      this.currentItems = [...this.currentItems].filter(item => item);
      this.$emit('update:items', this.currentItems);
    },

    updateListTitle(value) {
      this.$emit('update:label', value);
    },

    updateItem(index, value, prop, v) {
      this.currentItems[index][prop] = value;

      if (prop === 'type' && value === widgetButtonTypesEnum.SIMPLE) {
        delete this.currentItems[index].links;
        this.currentItems[index].href = '';
        v.links.$reset();
      }

      if (prop === 'type' && value === widgetButtonTypesEnum.MULTIPLE) {
        delete this.currentItems[index].href;
        this.currentItems[index].links = [{ label: '', href: '' }];
        v.href.$reset();
      }

      if (prop !== 'href') v[prop].$touch();

      this.$emit('update:items', this.currentItems);
    },

    updateMutipleListLink(index, linkIndex, value, prop, $link) {
      this.currentItems[index].links[linkIndex][prop] = value;
      if (prop !== 'href') $link[prop].$touch();
      this.$emit('update:items', this.currentItems);
    },

    addSubItem(index) {
      this.currentItems = this.currentItems.map((item, indexItem) => indexItem === index
        ? ({
          ...item,
          links: [...item.links, { label: '', href: '' }]
        }) : item);
    },

    removeSubitemItem(index, linkIndex) {
      if (this.currentItems[index].links?.length === 1) return

      this.currentItems[index].links[linkIndex] = null;
      const newLinks = this.currentItems[index].links.filter(link => link);

      this.currentItems = this.currentItems.map((item, indexItem) => indexItem === index
        ? ({ ...item, links: newLinks })
        : item
      );
      this.$emit('update:items', this.currentItems);
    },

    onDragSubItemEnd() {
      this.$emit('update:items', this.currentItems);
    },

    getTypeLabel(type) {
      return this.listTypeOptions.find(typeOption => typeOption.value === type)?.name
    },

    urlFeedback(input) {
      if (!input.required) return `${this.$toolkit.t('dashboard.forms.feedback.requiredField')}`
      if (!input.url) return `${this.$toolkit.t('dashboard.forms.feedback.invalidUrl')}`
    }
  }
};

/* script */
const __vue_script__$18 = script$18;

/* template */
var __vue_render__$18 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "list-link-form" }, [
    _c(
      "div",
      { staticClass: "form-header" },
      [
        _c(
          "me-form-row",
          { staticClass: "form-row" },
          [
            _c(
              "me-form-group",
              {
                staticClass: "form-group col",
                attrs: {
                  state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
                  label:
                    _vm.$toolkit.t("dashboard.forms.fields.listName") + "*",
                  "invalid-feedback": _vm.listNameFeedback
                }
              },
              [
                _c("me-text-input", {
                  attrs: {
                    state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
                    value: _vm.label,
                    placeholder:
                      "" +
                      _vm.$toolkit.t("dashboard.forms.placeholders.listName"),
                    "data-cy": "listButtonLabel"
                  },
                  on: {
                    input: _vm.updateListTitle,
                    blur: function($event) {
                      return _vm.$v.label.$touch()
                    }
                  }
                })
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "me-form-group",
              {
                staticClass: "form-group col-12 col-sm-auto",
                attrs: { label: "" }
              },
              [
                _c("div", { staticClass: "form-action form-action--btn" }, [
                  _c(
                    "div",
                    { staticClass: "form-action--add" },
                    [
                      _c(
                        "me-button",
                        {
                          attrs: {
                            "prepend-icon": "me-icon-l icon-plus",
                            block: "",
                            variant: "outline-primary",
                            "data-cy": "listButtonAddItem"
                          },
                          on: { click: _vm.addItem }
                        },
                        [
                          _vm._v(
                            "\n              " +
                              _vm._s(
                                _vm.$toolkit.t(
                                  "dashboard.forms.actions.addItem"
                                )
                              ) +
                              "\n            "
                          )
                        ]
                      )
                    ],
                    1
                  )
                ])
              ]
            )
          ],
          1
        )
      ],
      1
    ),
    _vm._v(" "),
    _vm.itemsDrag.length
      ? _c(
          "div",
          { staticClass: "form-body row no-gutters" },
          [
            _c(
              "Draggable",
              {
                staticClass: "col-12",
                attrs: {
                  handle: ".form-action--drag",
                  "ghost-class": "ghost-class"
                },
                model: {
                  value: _vm.itemsDrag,
                  callback: function($$v) {
                    _vm.itemsDrag = $$v;
                  },
                  expression: "itemsDrag"
                }
              },
              _vm._l(_vm.$v.currentItems.$each.$iter, function(v, index) {
                return _c(
                  "div",
                  { key: index, staticClass: "form-body__item" },
                  [
                    _c(
                      "me-form-row",
                      { staticClass: "form-row" },
                      [
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-9 col-sm-auto",
                            attrs: { label: "" }
                          },
                          [
                            _c("div", { staticClass: "form-action" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "form-action--drag",
                                  attrs: { "data-cy": "listButtonDragItem" }
                                },
                                [
                                  _c("me-icon", {
                                    attrs: {
                                      size: 16,
                                      color: "var(--me-gray-5)",
                                      icon: "me-icon-s icon-grid-horizontal"
                                    }
                                  })
                                ],
                                1
                              )
                            ])
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass:
                              "form-group col-auto d-sm-none ml-auto trash-padding",
                            attrs: { label: "" }
                          },
                          [
                            _c("div", { staticClass: "form-action" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "form-action--delete",
                                  attrs: { "data-cy": "listButtonRemoveItem" },
                                  on: {
                                    click: function() {
                                      return _vm.removeItem(index)
                                    }
                                  }
                                },
                                [
                                  _c("me-icon", {
                                    attrs: {
                                      size: 13,
                                      icon: "me-icon-l icon-trash",
                                      color: "var(--me-danger-2)"
                                    }
                                  })
                                ],
                                1
                              )
                            ])
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-12 col-sm",
                            attrs: {
                              state: v.type.$dirty ? !v.type.$anyError : null,
                              label:
                                _vm.$toolkit.t("dashboard.forms.fields.type") +
                                "*",
                              "invalid-feedback":
                                "" +
                                _vm.$toolkit.t(
                                  "dashboard.forms.feedback.requiredField"
                                )
                            }
                          },
                          [
                            _c("me-select", {
                              attrs: {
                                state:
                                  v.type.$dirty && v.type.$anyError
                                    ? false
                                    : null,
                                value: _vm.getTypeLabel(v.type.$model),
                                options: _vm.listTypeOptions,
                                clearable: false,
                                "append-to-body": "",
                                "data-cy": "listButtonItemType"
                              },
                              on: {
                                input: function(value) {
                                  return _vm.updateItem(
                                    index,
                                    value.value,
                                    "type",
                                    v
                                  )
                                }
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-12 col-sm",
                            attrs: {
                              state: v.label.$dirty ? !v.label.$anyError : null,
                              cols: 4,
                              label:
                                _vm.$toolkit.t("dashboard.forms.fields.title") +
                                " *",
                              "invalid-feedback":
                                "" +
                                _vm.$toolkit.t(
                                  "dashboard.forms.feedback.requiredField"
                                )
                            }
                          },
                          [
                            _c("me-text-input", {
                              attrs: {
                                state: v.label.$dirty
                                  ? !v.label.$anyError
                                  : null,
                                value: v.label.$model,
                                placeholder:
                                  "" +
                                  _vm.$toolkit.t(
                                    "dashboard.forms.placeholders.title"
                                  ),
                                "data-cy": "listButtonItemLabel"
                              },
                              on: {
                                input: function(value) {
                                  return _vm.updateItem(
                                    index,
                                    value,
                                    "label",
                                    v
                                  )
                                }
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        v.type.$model === _vm.widgetButtonTypesEnum.SIMPLE
                          ? _c(
                              "me-form-group",
                              {
                                staticClass: "form-group col-12 col-sm",
                                attrs: {
                                  state: v.href.$dirty
                                    ? !v.href.$anyError
                                    : null,
                                  cols: 4,
                                  label:
                                    _vm.$toolkit.t(
                                      "dashboard.forms.fields.url"
                                    ) + "*",
                                  "invalid-feedback": _vm.urlFeedback(v.href)
                                }
                              },
                              [
                                _c("me-text-input", {
                                  attrs: {
                                    state: v.href.$dirty
                                      ? !v.href.$anyError
                                      : null,
                                    value: v.href.$model,
                                    placeholder:
                                      "" +
                                      _vm.$toolkit.t(
                                        "dashboard.forms.placeholders.url"
                                      ),
                                    "data-cy": "listButtonItemHref"
                                  },
                                  on: {
                                    blur: function($event) {
                                      return v.href.$touch()
                                    },
                                    input: function(value) {
                                      return _vm.updateItem(
                                        index,
                                        value,
                                        "href",
                                        v
                                      )
                                    }
                                  }
                                })
                              ],
                              1
                            )
                          : _vm._e(),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass:
                              "form-group d-none d-sm-block col-sm-auto delete-icon",
                            attrs: { label: "" }
                          },
                          [
                            _c("div", { staticClass: "form-action" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "form-action--delete",
                                  attrs: { "data-cy": "listButtonRemoveItem" },
                                  on: {
                                    click: function() {
                                      return _vm.removeItem(index)
                                    }
                                  }
                                },
                                [
                                  _c("me-icon", {
                                    attrs: {
                                      size: 13,
                                      icon: "me-icon-l icon-trash",
                                      color: "var(--me-danger-2)"
                                    }
                                  })
                                ],
                                1
                              )
                            ])
                          ]
                        ),
                        _vm._v(" "),
                        _c("div", { staticClass: "w-100" }),
                        _vm._v(" "),
                        v.type.$model === _vm.widgetButtonTypesEnum.MULTIPLE
                          ? _c(
                              "div",
                              { staticClass: "row w-100 justify-content-end" },
                              [
                                _c(
                                  "div",
                                  { staticClass: "col-10 form-multiple" },
                                  [
                                    _c("div", { staticClass: "curve-line" }),
                                    _vm._v(" "),
                                    _c(
                                      "Draggable",
                                      {
                                        attrs: { handle: ".form-action--drag" },
                                        on: { end: _vm.onDragSubItemEnd },
                                        model: {
                                          value: _vm.currentItems[index].links,
                                          callback: function($$v) {
                                            _vm.$set(
                                              _vm.currentItems[index],
                                              "links",
                                              $$v
                                            );
                                          },
                                          expression:
                                            "currentItems[index].links"
                                        }
                                      },
                                      _vm._l(v.links.$each.$iter, function(
                                        link,
                                        linkIndex
                                      ) {
                                        return _c(
                                          "me-form-row",
                                          {
                                            key:
                                              "" +
                                              (linkIndex + link.href ||
                                                "" + link.label ||
                                                ""),
                                            staticClass:
                                              "form-row justify-content-end",
                                            attrs: {
                                              "data-cy": "listButtonSubitem"
                                            }
                                          },
                                          [
                                            _c(
                                              "me-form-group",
                                              {
                                                staticClass:
                                                  "form-group col-9 col-sm-auto",
                                                attrs: { label: "" }
                                              },
                                              [
                                                _c(
                                                  "div",
                                                  {
                                                    staticClass: "form-action"
                                                  },
                                                  [
                                                    _c(
                                                      "div",
                                                      {
                                                        staticClass:
                                                          "form-action--drag",
                                                        attrs: {
                                                          "data-cy":
                                                            "listButtonDragSubItem"
                                                        }
                                                      },
                                                      [
                                                        _c("me-icon", {
                                                          attrs: {
                                                            size: 16,
                                                            color:
                                                              "var(--me-gray-5)",
                                                            icon:
                                                              "me-icon-s icon-grid-horizontal"
                                                          }
                                                        })
                                                      ],
                                                      1
                                                    )
                                                  ]
                                                )
                                              ]
                                            ),
                                            _vm._v(" "),
                                            _c(
                                              "me-form-group",
                                              {
                                                staticClass:
                                                  "form-group col-auto d-sm-none",
                                                attrs: { label: "" }
                                              },
                                              [
                                                _c(
                                                  "div",
                                                  {
                                                    staticClass: "form-action"
                                                  },
                                                  [
                                                    _c(
                                                      "div",
                                                      {
                                                        staticClass:
                                                          "form-action--delete",
                                                        class: {
                                                          "form-action--delete__disabled":
                                                            v.links.$model
                                                              .length === 1
                                                        },
                                                        attrs: {
                                                          "data-cy":
                                                            "listButtonRemoveSubItem"
                                                        },
                                                        on: {
                                                          click: function() {
                                                            return _vm.removeSubitemItem(
                                                              Number(index),
                                                              Number(linkIndex)
                                                            )
                                                          }
                                                        }
                                                      },
                                                      [
                                                        _c("me-icon", {
                                                          attrs: {
                                                            size: 13,
                                                            color:
                                                              v.links.$model
                                                                .length > 1
                                                                ? "var(--me-danger-2)"
                                                                : "var(--me-gray-2)",
                                                            icon:
                                                              "me-icon-l icon-trash"
                                                          }
                                                        })
                                                      ],
                                                      1
                                                    )
                                                  ]
                                                )
                                              ]
                                            ),
                                            _vm._v(" "),
                                            _c(
                                              "me-form-group",
                                              {
                                                staticClass:
                                                  "form-group col-12 col-sm",
                                                attrs: {
                                                  label:
                                                    _vm.$toolkit.t(
                                                      "dashboard.forms.fields.title"
                                                    ) + "*",
                                                  "invalid-feedback":
                                                    "" +
                                                    _vm.$toolkit.t(
                                                      "dashboard.forms.feedback.requiredField"
                                                    )
                                                }
                                              },
                                              [
                                                _c("me-text-input", {
                                                  attrs: {
                                                    state: link.label.$dirty
                                                      ? !link.label.$anyError
                                                      : null,
                                                    value: link.label.$model,
                                                    placeholder:
                                                      "" +
                                                      _vm.$toolkit.t(
                                                        "dashboard.forms.placeholders.title"
                                                      ),
                                                    "data-cy":
                                                      "listButtonSubItemLabel"
                                                  },
                                                  on: {
                                                    input: function(value) {
                                                      return _vm.updateMutipleListLink(
                                                        Number(index),
                                                        Number(linkIndex),
                                                        value,
                                                        "label",
                                                        link
                                                      )
                                                    }
                                                  }
                                                })
                                              ],
                                              1
                                            ),
                                            _vm._v(" "),
                                            _c(
                                              "me-form-group",
                                              {
                                                staticClass:
                                                  "form-group col-12 col-sm",
                                                attrs: {
                                                  label:
                                                    _vm.$toolkit.t(
                                                      "dashboard.forms.fields.url"
                                                    ) + "*",
                                                  "invalid-feedback": _vm.urlFeedback(
                                                    link.href
                                                  )
                                                }
                                              },
                                              [
                                                _c("me-text-input", {
                                                  attrs: {
                                                    state: link.href.$dirty
                                                      ? !link.href.$anyError
                                                      : null,
                                                    value: link.href.$model,
                                                    placeholder:
                                                      "" +
                                                      _vm.$toolkit.t(
                                                        "dashboard.forms.placeholders.url"
                                                      ),
                                                    "data-cy":
                                                      "listButtonSubItemHref"
                                                  },
                                                  on: {
                                                    blur: function($event) {
                                                      return link.href.$touch()
                                                    },
                                                    input: function(value) {
                                                      return _vm.updateMutipleListLink(
                                                        Number(index),
                                                        Number(linkIndex),
                                                        value,
                                                        "href",
                                                        link
                                                      )
                                                    }
                                                  }
                                                })
                                              ],
                                              1
                                            ),
                                            _vm._v(" "),
                                            _c(
                                              "me-form-group",
                                              {
                                                staticClass:
                                                  "form-group d-none d-sm-block col-auto delete-icon",
                                                attrs: { label: "" }
                                              },
                                              [
                                                _c(
                                                  "div",
                                                  {
                                                    staticClass: "form-action"
                                                  },
                                                  [
                                                    _c(
                                                      "div",
                                                      {
                                                        staticClass:
                                                          "form-action--delete",
                                                        class: {
                                                          "form-action--delete__disabled":
                                                            v.links.$model
                                                              .length === 1
                                                        },
                                                        attrs: {
                                                          "data-cy":
                                                            "listButtonRemoveSubItem"
                                                        },
                                                        on: {
                                                          click: function() {
                                                            return _vm.removeSubitemItem(
                                                              Number(index),
                                                              Number(linkIndex)
                                                            )
                                                          }
                                                        }
                                                      },
                                                      [
                                                        _c("me-icon", {
                                                          attrs: {
                                                            size: 13,
                                                            color:
                                                              v.links.$model
                                                                .length > 1
                                                                ? "var(--me-danger-2)"
                                                                : "var(--me-gray-2)",
                                                            icon:
                                                              "me-icon-l icon-trash"
                                                          }
                                                        })
                                                      ],
                                                      1
                                                    )
                                                  ]
                                                )
                                              ]
                                            )
                                          ],
                                          1
                                        )
                                      }),
                                      1
                                    ),
                                    _vm._v(" "),
                                    _c(
                                      "div",
                                      { staticClass: "w-100" },
                                      [
                                        _c(
                                          "me-button",
                                          {
                                            staticClass: "d-none d-sm-block",
                                            attrs: {
                                              variant: "outline-primary",
                                              "prepend-icon":
                                                "me-icon-l icon-plus",
                                              size: "sm",
                                              "data-cy": "listButtonAddSubItem"
                                            },
                                            on: {
                                              click: function($event) {
                                                $event.stopPropagation();
                                                return (function() {
                                                  return _vm.addSubItem(
                                                    Number(index)
                                                  )
                                                })()
                                              }
                                            }
                                          },
                                          [
                                            _vm._v(
                                              "\n                  " +
                                                _vm._s(
                                                  _vm.$toolkit.t(
                                                    "dashboard.forms.actions.addSubItem"
                                                  )
                                                ) +
                                                "\n                "
                                            )
                                          ]
                                        ),
                                        _vm._v(" "),
                                        _c(
                                          "me-button",
                                          {
                                            staticClass: "d-sm-none",
                                            attrs: {
                                              variant: "outline-primary",
                                              "prepend-icon":
                                                "me-icon-l icon-plus",
                                              size: "sm",
                                              "data-cy": "listButtonAddSubItem",
                                              block: ""
                                            },
                                            on: {
                                              click: function($event) {
                                                $event.stopPropagation();
                                                return (function() {
                                                  return _vm.addSubItem(
                                                    Number(index)
                                                  )
                                                })()
                                              }
                                            }
                                          },
                                          [
                                            _vm._v(
                                              "\n                  " +
                                                _vm._s(
                                                  _vm.$toolkit.t(
                                                    "dashboard.forms.actions.addSubItem"
                                                  )
                                                ) +
                                                "\n                "
                                            )
                                          ]
                                        )
                                      ],
                                      1
                                    )
                                  ],
                                  1
                                )
                              ]
                            )
                          : _vm._e()
                      ],
                      1
                    )
                  ],
                  1
                )
              }),
              0
            )
          ],
          1
        )
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$18 = [];
__vue_render__$18._withStripped = true;

  /* style */
  const __vue_inject_styles__$18 = function (inject) {
    if (!inject) return
    inject("data-v-2f6c265c_0", { source: ".list-link-form .form-header[data-v-2f6c265c] {\n  margin-bottom: 16px;\n}\n.list-link-form .form-body[data-v-2f6c265c] {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\n.list-link-form .form-body .ghost-class[data-v-2f6c265c] {\n  background-color: var(--me-primary-7);\n}\n.list-link-form .form-body__item[data-v-2f6c265c] {\n  border-bottom: 1px solid var(--me-gray-6);\n}\n.list-link-form .form-body__item[data-v-2f6c265c]:last-child {\n  border: none;\n}\n.list-link-form .form-body .form-row[data-v-2f6c265c] {\n  padding: 16px;\n}\n.list-link-form .form-body .form-multiple[data-v-2f6c265c] {\n  background-color: var(--me-bg-gray);\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  padding: 16px;\n}\n.list-link-form .form-body .form-multiple .form-row[data-v-2f6c265c] {\n  padding: 0;\n  border-radius: 8px;\n}\n.list-link-form .form-body .form-multiple .form-row.sortable-chosen[data-v-2f6c265c] {\n  background-color: var(--me-primary-7);\n}\n.list-link-form .form-body .form-multiple .curve-line[data-v-2f6c265c] {\n  position: absolute;\n  top: -18px;\n  left: -32px;\n  height: 32px;\n  width: 32px;\n  border-bottom: 1px dashed var(--me-gray-2);\n  border-left: 1px dashed var(--me-gray-2);\n  border-bottom-left-radius: 8px;\n}\n@media screen and (max-width: 768px) {\n.list-link-form .form-body .form-multiple .curve-line[data-v-2f6c265c] {\n    display: none;\n}\n}\n.list-link-form .form-row .form-multiple[data-v-2f6c265c] {\n  position: relative;\n}\n.list-link-form .form-row .form-group.delete-icon[data-v-2f6c265c] {\n  padding-left: 3px;\n}\n.list-link-form .form-row .form-group .form-action[data-v-2f6c265c] {\n  margin-top: 0px;\n}\n@media screen and (min-width: 576px) {\n.list-link-form .form-row .form-group .form-action[data-v-2f6c265c] {\n    margin-top: 22px;\n}\n}\n.list-link-form .form-row .form-group .form-action--btn[data-v-2f6c265c] {\n  margin-top: 0;\n}\n@media screen and (min-width: 576px) {\n.list-link-form .form-row .form-group .form-action--btn[data-v-2f6c265c] {\n    margin-top: 22px;\n}\n}\n.list-link-form .form-row .form-group .form-action--delete[data-v-2f6c265c], .list-link-form .form-row .form-group .form-action--drag[data-v-2f6c265c] {\n  height: 40px;\n  width: 40px;\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.list-link-form .form-row .form-group .form-action--delete[data-v-2f6c265c] {\n  cursor: pointer;\n}\n.list-link-form .form-row .form-group .form-action--delete[data-v-2f6c265c]:hover {\n  background-color: var(--me-danger-7);\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled[data-v-2f6c265c] {\n  cursor: initial;\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled[data-v-2f6c265c]:hover {\n  background-color: transparent;\n}\n.list-link-form .form-row .form-group .form-action--drag[data-v-2f6c265c] {\n  cursor: grab;\n}\n.list-link-form .form-row .form-group .form-action--drag[data-v-2f6c265c]:hover {\n  background-color: var(--me-primary-7);\n}\n\n/*# sourceMappingURL=ListButtonForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/config/ListButtonForm.vue","ListButtonForm.vue"],"names":[],"mappings":"AA8fA;EACA,mBAAA;AC7fA;ADggBA;EACA,kCAAA;EACA,kBAAA;AC9fA;ADggBA;EACA,qCAAA;AC9fA;ADigBA;EACA,yCAAA;AC/fA;ADigBA;EACA,YAAA;AC/fA;ADmgBA;EACA,aAAA;ACjgBA;ADogBA;EACA,mCAAA;EACA,kCAAA;EACA,kBAAA;EACA,aAAA;AClgBA;ADogBA;EACA,UAAA;EACA,kBAAA;AClgBA;ADogBA;EACA,qCAAA;AClgBA;ADsgBA;EACA,kBAAA;EACA,UAAA;EACA,WAAA;EACA,YAAA;EACA,WAAA;EACA,0CAAA;EACA,wCAAA;EACA,8BAAA;ACpgBA;ADsgBA;AAVA;IAWA,aAAA;ACngBE;AACF;AD0gBA;EACA,kBAAA;ACxgBA;AD6gBA;EACA,iBAAA;AC3gBA;AD8gBA;EACA,eAAA;AC5gBA;AD6gBA;AAFA;IAGA,gBAAA;AC1gBE;AACF;AD4gBA;EACA,aAAA;AC1gBA;AD4gBA;AAHA;IAIA,gBAAA;ACzgBE;AACF;AD4gBA;EAEA,YAAA;EACA,WAAA;EACA,kBAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;AC3gBA;AD8gBA;EACA,eAAA;AC5gBA;AD8gBA;EACA,oCAAA;AC5gBA;AD+gBA;EACA,eAAA;AC7gBA;AD8gBA;EACA,6BAAA;AC5gBA;ADihBA;EACA,YAAA;AC/gBA;ADihBA;EACA,qCAAA;AC/gBA;;AAEA,6CAA6C","file":"ListButtonForm.vue","sourcesContent":["<template>\n  <!-- TODO componentizar está praticamente igual ao SimpleListForm.vue -->\n  <div class=\"list-link-form\">\n    <div class=\"form-header\">\n      <me-form-row class=\"form-row\">\n        <me-form-group\n          :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n          :label=\"`${$toolkit.t('dashboard.forms.fields.listName')}*`\"\n          :invalid-feedback=\"listNameFeedback\"\n          class=\"form-group col\"\n        >\n          <me-text-input\n            :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n            :value=\"label\"\n            :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.listName')}`\"\n            @input=\"updateListTitle\"\n            @blur=\"$v.label.$touch()\"\n            data-cy=\"listButtonLabel\"\n          />\n        </me-form-group>\n\n        <me-form-group\n          label=\"\"\n          class=\"form-group col-12 col-sm-auto\"\n        >\n          <div class=\"form-action form-action--btn\">\n            <div class=\"form-action--add\">\n              <me-button\n                @click=\"addItem\"\n                prepend-icon=\"me-icon-l icon-plus\"\n                block\n                variant=\"outline-primary\"\n                data-cy=\"listButtonAddItem\"\n              >\n                {{ $toolkit.t('dashboard.forms.actions.addItem') }}\n              </me-button>\n            </div>\n          </div>\n        </me-form-group>\n      </me-form-row>\n    </div>\n\n    <div v-if=\"itemsDrag.length\" class=\"form-body row no-gutters\">\n      <Draggable\n        v-model=\"itemsDrag\"\n        handle=\".form-action--drag\"\n        ghost-class=\"ghost-class\"\n        class=\"col-12\"\n      >\n        <div\n          v-for=\"(v, index) in $v.currentItems.$each.$iter\"\n          :key=\"index\"\n          class=\"form-body__item\"\n        >\n          <me-form-row\n            class=\"form-row\"\n          >\n            <me-form-group\n              label=\"\"\n              class=\"form-group col-9 col-sm-auto\"\n            >\n              <div class=\"form-action\">\n                <div class=\"form-action--drag\" data-cy=\"listButtonDragItem\">\n                  <me-icon\n                    :size=\"16\"\n                    :color=\"'var(--me-gray-5)'\"\n                    icon=\"me-icon-s icon-grid-horizontal\"\n                  />\n                </div>\n              </div>\n            </me-form-group>\n\n            <me-form-group\n              label=\"\"\n              class=\"form-group col-auto d-sm-none ml-auto trash-padding\"\n            >\n              <div class=\"form-action\">\n                <div\n                  @click=\"() => removeItem(index)\"\n                  class=\"form-action--delete\"\n                  data-cy=\"listButtonRemoveItem\"\n                >\n                  <me-icon\n                    :size=\"13\"\n                    icon=\"me-icon-l icon-trash\"\n                    color=\"var(--me-danger-2)\"\n                  />\n                </div>\n              </div>\n            </me-form-group>\n\n            <me-form-group\n              :state=\"v.type.$dirty ? !v.type.$anyError : null\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.type')}*`\"\n              :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-select\n                :state=\"v.type.$dirty && v.type.$anyError ? false : null\"\n                :value=\"getTypeLabel(v.type.$model)\"\n                :options=\"listTypeOptions\"\n                :clearable=\"false\"\n                @input=\"(value) => updateItem(index, value.value, 'type', v)\"\n                append-to-body\n                data-cy=\"listButtonItemType\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              :state=\"v.label.$dirty ? !v.label.$anyError : null\"\n              :cols=\"4\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.title')} *`\"\n              :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-text-input\n                :state=\"v.label.$dirty ? !v.label.$anyError : null\"\n                :value=\"v.label.$model\"\n                :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.title')}`\"\n                @input=\"(value) => updateItem(index, value, 'label', v)\"\n                data-cy=\"listButtonItemLabel\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              v-if=\"v.type.$model === widgetButtonTypesEnum.SIMPLE\"\n              :state=\"v.href.$dirty ? !v.href.$anyError : null\"\n              :cols=\"4\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.url')}*`\"\n              :invalid-feedback=\"urlFeedback(v.href)\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-text-input\n                :state=\"v.href.$dirty ? !v.href.$anyError : null\"\n                :value=\"v.href.$model\"\n                :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.url')}`\"\n                @blur=\"v.href.$touch()\"\n                @input=\"(value) => updateItem(index, value, 'href', v)\"\n                data-cy=\"listButtonItemHref\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              label=\"\"\n              class=\"form-group d-none d-sm-block col-sm-auto delete-icon\"\n            >\n              <div class=\"form-action\">\n                <div\n                  @click=\"() => removeItem(index)\"\n                  class=\"form-action--delete\"\n                  data-cy=\"listButtonRemoveItem\"\n                >\n                  <me-icon\n                    :size=\"13\"\n                    icon=\"me-icon-l icon-trash\"\n                    color=\"var(--me-danger-2)\"\n                  />\n                </div>\n              </div>\n            </me-form-group>\n\n            <div class=\"w-100\" />\n\n            <div\n              v-if=\"v.type.$model === widgetButtonTypesEnum.MULTIPLE\"\n              class=\"row w-100 justify-content-end\"\n            >\n              <div class=\"col-10 form-multiple\">\n                <div class=\"curve-line\" />\n                <Draggable\n                  v-model=\"currentItems[index].links\"\n                  @end=\"onDragSubItemEnd\"\n                  handle=\".form-action--drag\"\n                >\n                  <me-form-row\n                    v-for=\"(link, linkIndex) in v.links.$each.$iter\"\n                    :key=\"`${linkIndex + link.href || '' + link.label || ''}`\"\n                    class=\"form-row justify-content-end\"\n                    data-cy=\"listButtonSubitem\"\n                  >\n                    <me-form-group\n                      label=\"\"\n                      class=\"form-group col-9 col-sm-auto\"\n                    >\n                      <div class=\"form-action\">\n                        <div class=\"form-action--drag\" data-cy=\"listButtonDragSubItem\">\n                          <me-icon\n                            :size=\"16\"\n                            :color=\"'var(--me-gray-5)'\"\n                            icon=\"me-icon-s icon-grid-horizontal\"\n                          />\n                        </div>\n                      </div>\n                    </me-form-group>\n\n                    <me-form-group\n                      label=\"\"\n                      class=\"form-group col-auto d-sm-none\"\n                    >\n                      <div class=\"form-action\">\n                        <div\n                          @click=\"() => removeSubitemItem(Number(index), Number(linkIndex))\"\n                          :class=\"{'form-action--delete__disabled': v.links.$model.length === 1}\"\n                          class=\"form-action--delete\"\n                          data-cy=\"listButtonRemoveSubItem\"\n                        >\n                          <me-icon\n                            :size=\"13\"\n                            :color=\"v.links.$model.length > 1 ? 'var(--me-danger-2)' : 'var(--me-gray-2)'\"\n                            icon=\"me-icon-l icon-trash\"\n                          />\n                        </div>\n                      </div>\n                    </me-form-group>\n\n                    <me-form-group\n                      :label=\"`${$toolkit.t('dashboard.forms.fields.title')}*`\"\n                      :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n                      class=\"form-group col-12 col-sm\"\n                    >\n                      <me-text-input\n                        :state=\"link.label.$dirty ? !link.label.$anyError : null\"\n                        :value=\"link.label.$model\"\n                        :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.title')}`\"\n                        @input=\"(value) => updateMutipleListLink(Number(index), Number(linkIndex), value, 'label', link)\"\n                        data-cy=\"listButtonSubItemLabel\"\n                      />\n                    </me-form-group>\n\n                    <me-form-group\n                      :label=\"`${$toolkit.t('dashboard.forms.fields.url')}*`\"\n                      :invalid-feedback=\"urlFeedback(link.href)\"\n                      class=\"form-group col-12 col-sm\"\n                    >\n                      <me-text-input\n                        :state=\"link.href.$dirty ? !link.href.$anyError : null\"\n                        :value=\"link.href.$model\"\n                        :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.url')}`\"\n                        @blur=\"link.href.$touch()\"\n                        @input=\"(value) => updateMutipleListLink(Number(index), Number(linkIndex), value, 'href', link)\"\n                        data-cy=\"listButtonSubItemHref\"\n                      />\n                    </me-form-group>\n\n                    <me-form-group\n                      label=\"\"\n                      class=\"form-group d-none d-sm-block col-auto delete-icon\"\n                    >\n                      <div class=\"form-action\">\n                        <div\n                          @click=\"() => removeSubitemItem(Number(index), Number(linkIndex))\"\n                          :class=\"{'form-action--delete__disabled': v.links.$model.length === 1}\"\n                          class=\"form-action--delete\"\n                          data-cy=\"listButtonRemoveSubItem\"\n                        >\n                          <me-icon\n                            :size=\"13\"\n                            :color=\"v.links.$model.length > 1 ? 'var(--me-danger-2)' : 'var(--me-gray-2)'\"\n                            icon=\"me-icon-l icon-trash\"\n                          />\n                        </div>\n                      </div>\n                    </me-form-group>\n                  </me-form-row>\n                </Draggable>\n\n                <div class=\"w-100\">\n                  <me-button\n                    @click.stop=\"() => addSubItem(Number(index))\"\n                    variant=\"outline-primary\"\n                    prepend-icon=\"me-icon-l icon-plus\"\n                    size=\"sm\"\n                    class=\"d-none d-sm-block\"\n                    data-cy=\"listButtonAddSubItem\"\n                  >\n                    {{ $toolkit.t('dashboard.forms.actions.addSubItem') }}\n                  </me-button>\n\n                  <me-button\n                    @click.stop=\"() => addSubItem(Number(index))\"\n                    variant=\"outline-primary\"\n                    prepend-icon=\"me-icon-l icon-plus\"\n                    size=\"sm\"\n                    class=\"d-sm-none\"\n                    data-cy=\"listButtonAddSubItem\"\n                    block\n                  >\n                    {{ $toolkit.t('dashboard.forms.actions.addSubItem') }}\n                  </me-button>\n                </div>\n              </div>\n            </div>\n          </me-form-row>\n        </div>\n      </Draggable>\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport required from 'vuelidate/lib/validators/required'\nimport maxLength from 'vuelidate/lib/validators/maxLength'\nimport minLength from 'vuelidate/lib/validators/minLength'\nimport requiredIf from 'vuelidate/lib/validators/requiredIf'\nimport url from 'vuelidate/lib/validators/url'\nimport { widgetButtonTypesEnum } from '../enums/widgetTypesEnum.js'\nimport Draggable from '../../Draggable.vue'\n\nexport default {\n  components: {\n    Draggable\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    filterOptions: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      widgetButtonTypesEnum,\n      currentItems: cloneDeep(this.items).map((item, index) => ({ ...item, id: item.id || index.toString() })),\n      listTypeOptions: [\n        {\n          name: this.$toolkit.t('dashboard.forms.fields.buttonType.simple'),\n          value: widgetButtonTypesEnum.SIMPLE\n        },\n        {\n          name: this.$toolkit.t('dashboard.forms.fields.buttonType.multiple'),\n          value: widgetButtonTypesEnum.MULTIPLE\n        }\n      ]\n    }\n  },\n\n  validations: {\n    label: {\n      required,\n      maxLength: maxLength(180)\n    },\n\n    currentItems: {\n      required,\n      minLength: minLength(1),\n      $each: {\n        $trackBy: 'id',\n        type: {\n          required\n        },\n        label: {\n          required\n        },\n        href: {\n          url,\n          required: requiredIf(({ type }) => {\n            return type === widgetButtonTypesEnum.SIMPLE\n          })\n        },\n        links: {\n          required: requiredIf(({ type }) => {\n            return type === widgetButtonTypesEnum.MULTIPLE\n          }),\n          $each: {\n            label: { required },\n            href: { required, url }\n          }\n\n        }\n      }\n    }\n  },\n\n  computed: {\n    itemsDrag: {\n      get() {\n        return this.currentItems\n      },\n      set(value) {\n        this.currentItems = value\n        this.$emit('update:items', value)\n      }\n    },\n\n    listNameFeedback() {\n      if (!this.$v.label.required) return this.$toolkit.t('dashboard.forms.feedback.requiredField')\n      if (!this.$v.label.maxLength) {\n        const modelLen = this.$v.label.$model.length\n        return `${this.$toolkit.t('dashboard.forms.feedback.charLimit', modelLen)}`\n      }\n      return ''\n    }\n  },\n\n  watch: {\n    items(value) {\n      this.currentItems = cloneDeep(value).map((item, index) => ({ ...item, id: item.id || index.toString() }))\n    },\n\n    currentItems: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    },\n\n    label: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    }\n  },\n\n  methods: {\n    addItem() {\n      this.currentItems = [{\n        id: new Date().getTime().toString(),\n        label: '',\n        type: widgetButtonTypesEnum.SIMPLE,\n        href: ''\n      }, ...this.currentItems]\n    },\n\n    removeItem(index) {\n      this.currentItems[index] = null\n      this.currentItems = [...this.currentItems].filter(item => item)\n      this.$emit('update:items', this.currentItems)\n    },\n\n    updateListTitle(value) {\n      this.$emit('update:label', value)\n    },\n\n    updateItem(index, value, prop, v) {\n      this.currentItems[index][prop] = value\n\n      if (prop === 'type' && value === widgetButtonTypesEnum.SIMPLE) {\n        delete this.currentItems[index].links\n        this.currentItems[index].href = ''\n        v.links.$reset()\n      }\n\n      if (prop === 'type' && value === widgetButtonTypesEnum.MULTIPLE) {\n        delete this.currentItems[index].href\n        this.currentItems[index].links = [{ label: '', href: '' }]\n        v.href.$reset()\n      }\n\n      if (prop !== 'href') v[prop].$touch()\n\n      this.$emit('update:items', this.currentItems)\n    },\n\n    updateMutipleListLink(index, linkIndex, value, prop, $link) {\n      this.currentItems[index].links[linkIndex][prop] = value\n      if (prop !== 'href') $link[prop].$touch()\n      this.$emit('update:items', this.currentItems)\n    },\n\n    addSubItem(index) {\n      this.currentItems = this.currentItems.map((item, indexItem) => indexItem === index\n        ? ({\n          ...item,\n          links: [...item.links, { label: '', href: '' }]\n        }) : item)\n    },\n\n    removeSubitemItem(index, linkIndex) {\n      if (this.currentItems[index].links?.length === 1) return\n\n      this.currentItems[index].links[linkIndex] = null\n      const newLinks = this.currentItems[index].links.filter(link => link)\n\n      this.currentItems = this.currentItems.map((item, indexItem) => indexItem === index\n        ? ({ ...item, links: newLinks })\n        : item\n      )\n      this.$emit('update:items', this.currentItems)\n    },\n\n    onDragSubItemEnd() {\n      this.$emit('update:items', this.currentItems)\n    },\n\n    getTypeLabel(type) {\n      return this.listTypeOptions.find(typeOption => typeOption.value === type)?.name\n    },\n\n    urlFeedback(input) {\n      if (!input.required) return `${this.$toolkit.t('dashboard.forms.feedback.requiredField')}`\n      if (!input.url) return `${this.$toolkit.t('dashboard.forms.feedback.invalidUrl')}`\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .list-link-form {\n\n    .form-header {\n      margin-bottom: 16px;\n    }\n\n    .form-body {\n      border: 1px solid var(--me-gray-6);\n      border-radius: 8px;\n\n      .ghost-class {\n        background-color: var(--me-primary-7);\n      }\n\n      &__item {\n        border-bottom: 1px solid var(--me-gray-6);\n\n        &:last-child {\n          border: none;\n        }\n      }\n\n      .form-row {\n        padding: 16px;\n      }\n\n      .form-multiple {\n        background-color: var(--me-bg-gray);\n        border: 1px solid var(--me-gray-6);\n        border-radius: 8px;\n        padding: 16px;\n\n        .form-row {\n          padding: 0;\n          border-radius: 8px;\n\n          &.sortable-chosen {\n            background-color: var(--me-primary-7);\n          }\n        }\n\n        .curve-line {\n          position: absolute;\n          top: -18px;\n          left: -32px;\n          height: 32px;\n          width: 32px;\n          border-bottom: 1px dashed var(--me-gray-2);\n          border-left: 1px dashed var(--me-gray-2);\n          border-bottom-left-radius: 8px;\n\n          @media screen and (max-width: 768px) {\n            display: none;\n          }\n        }\n      }\n    }\n\n    .form-row {\n\n      .form-multiple {\n        position: relative;\n      }\n\n      .form-group {\n\n        &.delete-icon {\n          padding-left: 3px;\n        }\n\n        .form-action {\n          margin-top: 0px;\n          @media screen and (min-width: 576px) {\n            margin-top: 22px;\n          }\n\n          &--btn {\n            margin-top: 0;\n\n            @media screen and (min-width: 576px) {\n              margin-top: 22px;\n            }\n          }\n\n          &--delete,\n          &--drag {\n            height: 40px;\n            width: 40px;\n            border-radius: 50%;\n            display: flex;\n            align-items: center;\n            justify-content: center;\n          }\n\n          &--delete {\n            cursor: pointer;\n\n            &:hover {\n              background-color: var(--me-danger-7);\n            }\n\n            &__disabled {\n              cursor: initial;\n              &:hover {\n                background-color: transparent;\n              }\n            }\n          }\n\n          &--drag {\n            cursor: grab;\n\n            &:hover {\n              background-color: var(--me-primary-7);\n            }\n          }\n        }\n      }\n    }\n  }\n</style>\n",".list-link-form .form-header {\n  margin-bottom: 16px;\n}\n.list-link-form .form-body {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\n.list-link-form .form-body .ghost-class {\n  background-color: var(--me-primary-7);\n}\n.list-link-form .form-body__item {\n  border-bottom: 1px solid var(--me-gray-6);\n}\n.list-link-form .form-body__item:last-child {\n  border: none;\n}\n.list-link-form .form-body .form-row {\n  padding: 16px;\n}\n.list-link-form .form-body .form-multiple {\n  background-color: var(--me-bg-gray);\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  padding: 16px;\n}\n.list-link-form .form-body .form-multiple .form-row {\n  padding: 0;\n  border-radius: 8px;\n}\n.list-link-form .form-body .form-multiple .form-row.sortable-chosen {\n  background-color: var(--me-primary-7);\n}\n.list-link-form .form-body .form-multiple .curve-line {\n  position: absolute;\n  top: -18px;\n  left: -32px;\n  height: 32px;\n  width: 32px;\n  border-bottom: 1px dashed var(--me-gray-2);\n  border-left: 1px dashed var(--me-gray-2);\n  border-bottom-left-radius: 8px;\n}\n@media screen and (max-width: 768px) {\n  .list-link-form .form-body .form-multiple .curve-line {\n    display: none;\n  }\n}\n.list-link-form .form-row .form-multiple {\n  position: relative;\n}\n.list-link-form .form-row .form-group.delete-icon {\n  padding-left: 3px;\n}\n.list-link-form .form-row .form-group .form-action {\n  margin-top: 0px;\n}\n@media screen and (min-width: 576px) {\n  .list-link-form .form-row .form-group .form-action {\n    margin-top: 22px;\n  }\n}\n.list-link-form .form-row .form-group .form-action--btn {\n  margin-top: 0;\n}\n@media screen and (min-width: 576px) {\n  .list-link-form .form-row .form-group .form-action--btn {\n    margin-top: 22px;\n  }\n}\n.list-link-form .form-row .form-group .form-action--delete, .list-link-form .form-row .form-group .form-action--drag {\n  height: 40px;\n  width: 40px;\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.list-link-form .form-row .form-group .form-action--delete {\n  cursor: pointer;\n}\n.list-link-form .form-row .form-group .form-action--delete:hover {\n  background-color: var(--me-danger-7);\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled {\n  cursor: initial;\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled:hover {\n  background-color: transparent;\n}\n.list-link-form .form-row .form-group .form-action--drag {\n  cursor: grab;\n}\n.list-link-form .form-row .form-group .form-action--drag:hover {\n  background-color: var(--me-primary-7);\n}\n\n/*# sourceMappingURL=ListButtonForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$18 = "data-v-2f6c265c";
  /* module identifier */
  const __vue_module_identifier__$18 = undefined;
  /* functional template */
  const __vue_is_functional_template__$18 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$18 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$18, staticRenderFns: __vue_staticRenderFns__$18 },
    __vue_inject_styles__$18,
    __vue_script__$18,
    __vue_scope_id__$18,
    __vue_is_functional_template__$18,
    __vue_module_identifier__$18,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$19 = {
  name: 'SimpleListForm',
  components: {
    Draggable: __vue_component__$s
  },

  props: {
    label: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => [{
        type: null,
        label: ''
      }]
    },
    indicatorFilters: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      widgetSubtypesEnum,
      currentItems: cloneDeep_1(this.items).map((item, index) => ({ ...item, id: item.id || index.toString() })),
      listTypeOptions: [
        {
          name: this.$toolkit.t('dashboard.widget.subtype.indicator'),
          value: widgetSubtypesEnum.INDICATOR
        },
        {
          name: this.$toolkit.t('dashboard.widget.subtype.link'),
          value: widgetSubtypesEnum.LINK
        }
      ]
    }
  },

  validations: {
    label: {
      required: required$1,
      maxLength: maxLength$1(180)
    },

    currentItems: {
      required: required$1,
      minLength: minLength$1(1),
      $each: {
        $trackBy: 'id',
        type: {
          required: required$1
        },
        label: {
          required: required$1
        },
        href: {
          url: url$1,
          required: requiredIf$1(({ type }) => {
            return type === widgetSubtypesEnum.LINK
          })
        },
        filter: {
          required: requiredIf$1(({ type }) => {
            return type === widgetSubtypesEnum.INDICATOR
          })
        }
      }
    }
  },

  computed: {
    itemsDrag: {
      get() {
        return this.currentItems
      },
      set(value) {
        this.currentItems = value;
        this.$emit('update:items', value);
      }
    },

    listNameFeedback() {
      if (!this.$v.label.required) return this.$toolkit.t('dashboard.forms.feedback.requiredField')
      if (!this.$v.label.maxLength) {
        const modelLen = this.$v.label.$model.length;
        return `${this.$toolkit.t('dashboard.forms.feedback.charLimit', modelLen)}`
      }
      return ''
    }
  },

  watch: {
    items(value) {
      this.currentItems = cloneDeep_1(value).map((item, index) => ({ ...item, id: item.id || index.toString() }));
    },

    currentItems: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    },

    label: {
      handler() {
        this.$emit('form-validation', !this.$v.$invalid);
      },
      immediate: true
    }
  },

  methods: {
    addItem() {
      this.currentItems = [{
        id: new Date().getTime().toString(),
        label: '',
        type: null,
        href: ''
      }, ...this.currentItems];
    },

    removeItem(index) {
      if (this.currentItems.length === 1) return

      this.currentItems[index] = null;
      this.currentItems = [...this.currentItems].filter(item => item);
      this.$emit('update:items', this.currentItems);
    },

    updateListTitle(value) {
      this.$emit('update:label', value);
    },

    updateItem(index, value, prop, v) {
      this.currentItems[index][prop] = value;
      if (prop === 'type' && value === widgetSubtypesEnum.INDICATOR) {
        delete this.currentItems[index].href;
        this.currentItems[index].filter = {};
        v.filter.$reset();
      }

      if (prop === 'type' && value === widgetSubtypesEnum.LINK) {
        delete this.currentItems[index].filter;
        this.currentItems[index].href = '';
        v.href.$reset();
      }

      if (prop !== 'href') v[prop].$touch();

      this.$emit('update:items', this.currentItems);
    },

    getTypeLabel(type) {
      return this.listTypeOptions.find(typeOption => typeOption.value === type)?.name
    },

    urlFeedback(input) {
      if (!input.required) return `${this.$toolkit.t('dashboard.forms.feedback.requiredField')}`
      if (!input.url) return `${this.$toolkit.t('dashboard.forms.feedback.invalidUrl')}`
    }
  }
};

/* script */
const __vue_script__$19 = script$19;

/* template */
var __vue_render__$19 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "list-link-form" }, [
    _c(
      "div",
      { staticClass: "form-header" },
      [
        _c(
          "me-form-row",
          { staticClass: "form-row" },
          [
            _c(
              "me-form-group",
              {
                staticClass: "form-group col",
                attrs: {
                  state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
                  label:
                    _vm.$toolkit.t("dashboard.forms.fields.listName") + "*",
                  "invalid-feedback": _vm.listNameFeedback
                }
              },
              [
                _c("me-text-input", {
                  attrs: {
                    state: _vm.$v.label.$dirty ? !_vm.$v.label.$anyError : null,
                    value: _vm.label,
                    placeholder:
                      "" +
                      _vm.$toolkit.t("dashboard.forms.placeholders.listName"),
                    "data-cy": "simpleListLabel"
                  },
                  on: {
                    input: _vm.updateListTitle,
                    blur: function($event) {
                      return _vm.$v.label.$touch()
                    }
                  }
                })
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "me-form-group",
              {
                staticClass: "form-group col-12 col-sm-auto",
                attrs: { label: "" }
              },
              [
                _c("div", { staticClass: "form-action form-action--btn" }, [
                  _c(
                    "div",
                    { staticClass: "form-action--add" },
                    [
                      _c(
                        "me-button",
                        {
                          attrs: {
                            "prepend-icon": "me-icon-l icon-plus",
                            block: "",
                            variant: "outline-primary",
                            "data-cy": "simpleListAddItem"
                          },
                          on: { click: _vm.addItem }
                        },
                        [
                          _vm._v(
                            "\n              " +
                              _vm._s(
                                _vm.$toolkit.t(
                                  "dashboard.forms.actions.addItem"
                                )
                              ) +
                              "\n            "
                          )
                        ]
                      )
                    ],
                    1
                  )
                ])
              ]
            )
          ],
          1
        )
      ],
      1
    ),
    _vm._v(" "),
    _vm.itemsDrag.length
      ? _c(
          "div",
          { staticClass: "form-body row no-gutters" },
          [
            _c(
              "Draggable",
              {
                staticClass: "col-12",
                attrs: {
                  "ghost-class": "ghost-class",
                  handle: ".form-action--drag"
                },
                model: {
                  value: _vm.itemsDrag,
                  callback: function($$v) {
                    _vm.itemsDrag = $$v;
                  },
                  expression: "itemsDrag"
                }
              },
              _vm._l(_vm.$v.currentItems.$each.$iter, function(v, index) {
                return _c(
                  "div",
                  { key: index, staticClass: "form-body__item" },
                  [
                    _c(
                      "me-form-row",
                      { staticClass: "form-row" },
                      [
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-10 col-sm-auto",
                            attrs: { label: "" }
                          },
                          [
                            _c("div", { staticClass: "form-action" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "form-action--drag",
                                  attrs: { "data-cy": "simpleListDragItem" }
                                },
                                [
                                  _c("me-icon", {
                                    attrs: {
                                      size: 16,
                                      color: "var(--me-gray-5)",
                                      icon: "me-icon-s icon-grid-horizontal"
                                    }
                                  })
                                ],
                                1
                              )
                            ])
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-auto d-sm-none",
                            attrs: { label: "" }
                          },
                          [
                            _c("div", { staticClass: "form-action" }, [
                              _c(
                                "div",
                                {
                                  class: {
                                    "form-action--delete__disabled":
                                      _vm.itemsDrag.length === 1
                                  },
                                  attrs: { "data-cy": "simpleListRemoveItem" },
                                  on: {
                                    click: function() {
                                      return _vm.removeItem(index)
                                    }
                                  }
                                },
                                [
                                  _c("me-icon", {
                                    attrs: {
                                      size: 13,
                                      color:
                                        _vm.itemsDrag.length > 1
                                          ? "var(--me-danger-2)"
                                          : "var(--me-gray-2)",
                                      icon: "me-icon-l icon-trash"
                                    }
                                  })
                                ],
                                1
                              )
                            ])
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-12 col-sm",
                            attrs: {
                              state: v.type.$dirty ? !v.type.$anyError : null,
                              label:
                                _vm.$toolkit.t("dashboard.forms.fields.type") +
                                "*",
                              "invalid-feedback":
                                "" +
                                _vm.$toolkit.t(
                                  "dashboard.forms.feedback.requiredField"
                                )
                            }
                          },
                          [
                            _c("me-select", {
                              attrs: {
                                state:
                                  v.type.$dirty && v.type.$anyError
                                    ? false
                                    : null,
                                value: _vm.getTypeLabel(v.type.$model),
                                options: _vm.listTypeOptions,
                                clearable: false,
                                "append-to-body": "",
                                "data-cy": "simpleListItemType"
                              },
                              on: {
                                input: function(value) {
                                  return _vm.updateItem(
                                    index,
                                    value.value,
                                    "type",
                                    v
                                  )
                                }
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass: "form-group col-12 col-sm",
                            attrs: {
                              state: v.label.$dirty ? !v.label.$anyError : null,
                              cols: 4,
                              label:
                                _vm.$toolkit.t("dashboard.forms.fields.title") +
                                " *",
                              "invalid-feedback":
                                "" +
                                _vm.$toolkit.t(
                                  "dashboard.forms.feedback.requiredField"
                                )
                            }
                          },
                          [
                            _c("me-text-input", {
                              attrs: {
                                state: v.label.$dirty
                                  ? !v.label.$anyError
                                  : null,
                                value: v.label.$model,
                                placeholder:
                                  "" +
                                  _vm.$toolkit.t(
                                    "dashboard.forms.placeholders.title"
                                  ),
                                "data-cy": "simpleListItemLabel"
                              },
                              on: {
                                input: function(value) {
                                  return _vm.updateItem(
                                    index,
                                    value,
                                    "label",
                                    v
                                  )
                                }
                              }
                            })
                          ],
                          1
                        ),
                        _vm._v(" "),
                        v.type.$model === _vm.widgetSubtypesEnum.LINK
                          ? _c(
                              "me-form-group",
                              {
                                staticClass: "form-group col-12 col-sm",
                                attrs: {
                                  state: v.href.$dirty
                                    ? !v.href.$anyError
                                    : null,
                                  cols: 4,
                                  label:
                                    _vm.$toolkit.t(
                                      "dashboard.forms.fields.url"
                                    ) + "*",
                                  "invalid-feedback": _vm.urlFeedback(v.href)
                                }
                              },
                              [
                                _c("me-text-input", {
                                  attrs: {
                                    state: v.href.$dirty
                                      ? !v.href.$anyError
                                      : null,
                                    value: v.href.$model,
                                    placeholder:
                                      "" +
                                      _vm.$toolkit.t(
                                        "dashboard.forms.placeholders.url"
                                      ),
                                    "data-cy": "simpleListItemHref"
                                  },
                                  on: {
                                    blur: function($event) {
                                      return v.href.$touch()
                                    },
                                    input: function(value) {
                                      return _vm.updateItem(
                                        index,
                                        value,
                                        "href",
                                        v
                                      )
                                    }
                                  }
                                })
                              ],
                              1
                            )
                          : _vm._e(),
                        _vm._v(" "),
                        v.type.$model === _vm.widgetSubtypesEnum.INDICATOR
                          ? _c(
                              "me-form-group",
                              {
                                staticClass: "form-group col-12 col-sm",
                                attrs: {
                                  state: v.filter.$dirty
                                    ? !v.filter.$anyError
                                    : null,
                                  cols: 4,
                                  label:
                                    _vm.$toolkit.t(
                                      "dashboard.forms.fields.filter"
                                    ) + "*",
                                  "invalid-feedback":
                                    "" +
                                    _vm.$toolkit.t(
                                      "dashboard.forms.feedback.requiredField"
                                    )
                                }
                              },
                              [
                                _c("me-select", {
                                  attrs: {
                                    state:
                                      v.filter.$dirty && v.filter.$anyError
                                        ? false
                                        : null,
                                    value: v.filter.$model,
                                    options: _vm.indicatorFilters,
                                    clearable: false,
                                    "append-to-body": "",
                                    "data-cy": "simpleListItemFilter"
                                  },
                                  on: {
                                    input: function(value) {
                                      return _vm.updateItem(
                                        index,
                                        value,
                                        "filter",
                                        v
                                      )
                                    }
                                  }
                                })
                              ],
                              1
                            )
                          : _vm._e(),
                        _vm._v(" "),
                        _c(
                          "me-form-group",
                          {
                            staticClass:
                              "form-group d-none d-sm-block col-sm-auto delete-icon",
                            attrs: { label: "" }
                          },
                          [
                            _c("div", { staticClass: "form-action" }, [
                              _c(
                                "div",
                                {
                                  staticClass: "form-action--delete",
                                  class: {
                                    "form-action--delete__disabled":
                                      _vm.itemsDrag.length === 1
                                  },
                                  attrs: { "data-cy": "simpleListRemoveItem" },
                                  on: {
                                    click: function() {
                                      return _vm.removeItem(index)
                                    }
                                  }
                                },
                                [
                                  _c("me-icon", {
                                    attrs: {
                                      size: 13,
                                      color:
                                        _vm.itemsDrag.length > 1
                                          ? "var(--me-danger-2)"
                                          : "var(--me-gray-2)",
                                      icon: "me-icon-l icon-trash"
                                    }
                                  })
                                ],
                                1
                              )
                            ])
                          ]
                        )
                      ],
                      1
                    )
                  ],
                  1
                )
              }),
              0
            )
          ],
          1
        )
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$19 = [];
__vue_render__$19._withStripped = true;

  /* style */
  const __vue_inject_styles__$19 = function (inject) {
    if (!inject) return
    inject("data-v-3dd4d26a_0", { source: ".list-link-form .form-header[data-v-3dd4d26a] {\n  margin-bottom: 16px;\n}\n.list-link-form .form-body[data-v-3dd4d26a] {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\n.list-link-form .form-body .ghost-class[data-v-3dd4d26a] {\n  background-color: var(--me-primary-7);\n}\n.list-link-form .form-body__item[data-v-3dd4d26a] {\n  border-bottom: 1px solid var(--me-gray-6);\n}\n.list-link-form .form-body__item[data-v-3dd4d26a]:last-child {\n  border: none;\n}\n.list-link-form .form-body .form-row[data-v-3dd4d26a] {\n  padding: 16px;\n}\n.list-link-form .form-row .form-group.delete-icon[data-v-3dd4d26a] {\n  padding-left: 3px;\n}\n.list-link-form .form-row .form-group .form-action[data-v-3dd4d26a] {\n  margin-top: 0px;\n}\n@media screen and (min-width: 576px) {\n.list-link-form .form-row .form-group .form-action[data-v-3dd4d26a] {\n    margin-top: 22px;\n}\n}\n.list-link-form .form-row .form-group .form-action--btn[data-v-3dd4d26a] {\n  margin-top: 0;\n}\n@media screen and (min-width: 576px) {\n.list-link-form .form-row .form-group .form-action--btn[data-v-3dd4d26a] {\n    margin-top: 22px;\n}\n}\n.list-link-form .form-row .form-group .form-action--delete[data-v-3dd4d26a], .list-link-form .form-row .form-group .form-action--drag[data-v-3dd4d26a] {\n  height: 40px;\n  width: 40px;\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.list-link-form .form-row .form-group .form-action--delete[data-v-3dd4d26a] {\n  cursor: pointer;\n}\n.list-link-form .form-row .form-group .form-action--delete[data-v-3dd4d26a]:hover {\n  background-color: var(--me-danger-7);\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled[data-v-3dd4d26a] {\n  cursor: initial;\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled[data-v-3dd4d26a]:hover {\n  background-color: transparent;\n}\n.list-link-form .form-row .form-group .form-action--drag[data-v-3dd4d26a] {\n  cursor: grab;\n}\n.list-link-form .form-row .form-group .form-action--drag[data-v-3dd4d26a]:hover {\n  background-color: var(--me-primary-7);\n}\n\n/*# sourceMappingURL=SimpleListForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/config/SimpleListForm.vue","SimpleListForm.vue"],"names":[],"mappings":"AAqXA;EACA,mBAAA;ACpXA;ADuXA;EACA,kCAAA;EACA,kBAAA;ACrXA;ADuXA;EACA,qCAAA;ACrXA;ADwXA;EACA,yCAAA;ACtXA;ADwXA;EACA,YAAA;ACtXA;AD0XA;EACA,aAAA;ACxXA;AD+XA;EACA,iBAAA;AC7XA;ADgYA;EACA,eAAA;AC9XA;AD+XA;AAFA;IAGA,gBAAA;AC5XE;AACF;AD8XA;EACA,aAAA;AC5XA;AD8XA;AAHA;IAIA,gBAAA;AC3XE;AACF;AD8XA;EAEA,YAAA;EACA,WAAA;EACA,kBAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;AC7XA;ADgYA;EACA,eAAA;AC9XA;ADgYA;EACA,oCAAA;AC9XA;ADiYA;EACA,eAAA;AC/XA;ADgYA;EACA,6BAAA;AC9XA;ADmYA;EACA,YAAA;ACjYA;ADmYA;EACA,qCAAA;ACjYA;;AAEA,6CAA6C","file":"SimpleListForm.vue","sourcesContent":["<template>\n  <!-- TODO componentizar está praticamente igual ao ListButtonForm.vue -->\n  <div class=\"list-link-form\">\n    <div class=\"form-header\">\n      <me-form-row class=\"form-row\">\n        <me-form-group\n          :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n          :label=\"`${$toolkit.t('dashboard.forms.fields.listName')}*`\"\n          :invalid-feedback=\"listNameFeedback\"\n          class=\"form-group col\"\n        >\n          <me-text-input\n            :state=\"$v.label.$dirty ? !$v.label.$anyError : null\"\n            :value=\"label\"\n            :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.listName')}`\"\n            @input=\"updateListTitle\"\n            @blur=\"$v.label.$touch()\"\n            data-cy=\"simpleListLabel\"\n          />\n        </me-form-group>\n\n        <me-form-group\n          label=\"\"\n          class=\"form-group col-12 col-sm-auto\"\n        >\n          <div class=\"form-action form-action--btn\">\n            <div class=\"form-action--add\">\n              <me-button\n                @click=\"addItem\"\n                prepend-icon=\"me-icon-l icon-plus\"\n                block\n                variant=\"outline-primary\"\n                data-cy=\"simpleListAddItem\"\n              >\n                {{ $toolkit.t('dashboard.forms.actions.addItem') }}\n              </me-button>\n            </div>\n          </div>\n        </me-form-group>\n      </me-form-row>\n    </div>\n\n    <div\n      v-if=\"itemsDrag.length\"\n      class=\"form-body row no-gutters\"\n    >\n      <Draggable\n        v-model=\"itemsDrag\"\n        ghost-class=\"ghost-class\"\n        handle=\".form-action--drag\"\n        class=\"col-12\"\n      >\n        <div\n          v-for=\"(v, index) in $v.currentItems.$each.$iter\"\n          :key=\"index\"\n          class=\"form-body__item\"\n        >\n          <me-form-row\n            class=\"form-row\"\n          >\n            <me-form-group\n              label=\"\"\n              class=\"form-group col-10 col-sm-auto\"\n            >\n              <div class=\"form-action\">\n                <div\n                  class=\"form-action--drag\"\n                  data-cy=\"simpleListDragItem\"\n                >\n                  <me-icon\n                    :size=\"16\"\n                    :color=\"'var(--me-gray-5)'\"\n                    icon=\"me-icon-s icon-grid-horizontal\"\n                  />\n                </div>\n              </div>\n            </me-form-group>\n\n            <me-form-group\n              label=\"\"\n              class=\"form-group col-auto d-sm-none\"\n            >\n              <div class=\"form-action\">\n                <div\n                  @click=\"() => removeItem(index)\"\n                  :class=\"{ 'form-action--delete__disabled': itemsDrag.length === 1 }\"\n                  data-cy=\"simpleListRemoveItem\"\n                >\n                  <me-icon\n                    :size=\"13\"\n                    :color=\"itemsDrag.length > 1 ? 'var(--me-danger-2)' : 'var(--me-gray-2)'\"\n                    icon=\"me-icon-l icon-trash\"\n                  />\n                </div>\n              </div>\n            </me-form-group>\n\n            <me-form-group\n              :state=\"v.type.$dirty ? !v.type.$anyError : null\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.type')}*`\"\n              :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-select\n                :state=\"v.type.$dirty && v.type.$anyError ? false : null\"\n                :value=\"getTypeLabel(v.type.$model)\"\n                :options=\"listTypeOptions\"\n                :clearable=\"false\"\n                @input=\"(value) => updateItem(index, value.value, 'type', v)\"\n                append-to-body\n                data-cy=\"simpleListItemType\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              :state=\"v.label.$dirty ? !v.label.$anyError : null\"\n              :cols=\"4\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.title')} *`\"\n              :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-text-input\n                :state=\"v.label.$dirty ? !v.label.$anyError : null\"\n                :value=\"v.label.$model\"\n                :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.title')}`\"\n                @input=\"(value) => updateItem(index, value, 'label', v)\"\n                data-cy=\"simpleListItemLabel\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              v-if=\"v.type.$model === widgetSubtypesEnum.LINK\"\n              :state=\"v.href.$dirty ? !v.href.$anyError : null\"\n              :cols=\"4\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.url')}*`\"\n              :invalid-feedback=\"urlFeedback(v.href)\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-text-input\n                :state=\"v.href.$dirty ? !v.href.$anyError : null\"\n                :value=\"v.href.$model\"\n                :placeholder=\"`${$toolkit.t('dashboard.forms.placeholders.url')}`\"\n                @blur=\"v.href.$touch()\"\n                @input=\"(value) => updateItem(index, value, 'href', v)\"\n                data-cy=\"simpleListItemHref\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              v-if=\"v.type.$model === widgetSubtypesEnum.INDICATOR\"\n              :state=\"v.filter.$dirty ? !v.filter.$anyError : null\"\n              :cols=\"4\"\n              :label=\"`${$toolkit.t('dashboard.forms.fields.filter')}*`\"\n              :invalid-feedback=\"`${$toolkit.t('dashboard.forms.feedback.requiredField')}`\"\n              class=\"form-group col-12 col-sm\"\n            >\n              <me-select\n                :state=\"v.filter.$dirty && v.filter.$anyError ? false : null\"\n                :value=\"v.filter.$model\"\n                :options=\"indicatorFilters\"\n                :clearable=\"false\"\n                @input=\"(value) => updateItem(index, value, 'filter', v)\"\n                append-to-body\n                data-cy=\"simpleListItemFilter\"\n              />\n            </me-form-group>\n\n            <me-form-group\n              label=\"\"\n              class=\"form-group d-none d-sm-block col-sm-auto delete-icon\"\n            >\n              <div class=\"form-action\">\n                <div\n                  :class=\"{ 'form-action--delete__disabled': itemsDrag.length === 1 }\"\n                  @click=\"() => removeItem(index)\"\n                  class=\"form-action--delete\"\n                  data-cy=\"simpleListRemoveItem\"\n                >\n                  <me-icon\n                    :size=\"13\"\n                    :color=\"itemsDrag.length > 1 ? 'var(--me-danger-2)' : 'var(--me-gray-2)'\"\n                    icon=\"me-icon-l icon-trash\"\n                  />\n                </div>\n              </div>\n            </me-form-group>\n          </me-form-row>\n        </div>\n      </Draggable>\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport required from 'vuelidate/lib/validators/required'\nimport maxLength from 'vuelidate/lib/validators/maxLength'\nimport minLength from 'vuelidate/lib/validators/minLength'\nimport requiredIf from 'vuelidate/lib/validators/requiredIf'\nimport url from 'vuelidate/lib/validators/url'\nimport { widgetSubtypesEnum } from '../enums/widgetTypesEnum.js'\nimport Draggable from '../../Draggable.vue'\n\nexport default {\n  name: 'SimpleListForm',\n  components: {\n    Draggable\n  },\n\n  props: {\n    label: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => [{\n        type: null,\n        label: ''\n      }]\n    },\n    indicatorFilters: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      widgetSubtypesEnum,\n      currentItems: cloneDeep(this.items).map((item, index) => ({ ...item, id: item.id || index.toString() })),\n      listTypeOptions: [\n        {\n          name: this.$toolkit.t('dashboard.widget.subtype.indicator'),\n          value: widgetSubtypesEnum.INDICATOR\n        },\n        {\n          name: this.$toolkit.t('dashboard.widget.subtype.link'),\n          value: widgetSubtypesEnum.LINK\n        }\n      ]\n    }\n  },\n\n  validations: {\n    label: {\n      required,\n      maxLength: maxLength(180)\n    },\n\n    currentItems: {\n      required,\n      minLength: minLength(1),\n      $each: {\n        $trackBy: 'id',\n        type: {\n          required\n        },\n        label: {\n          required\n        },\n        href: {\n          url,\n          required: requiredIf(({ type }) => {\n            return type === widgetSubtypesEnum.LINK\n          })\n        },\n        filter: {\n          required: requiredIf(({ type }) => {\n            return type === widgetSubtypesEnum.INDICATOR\n          })\n        }\n      }\n    }\n  },\n\n  computed: {\n    itemsDrag: {\n      get() {\n        return this.currentItems\n      },\n      set(value) {\n        this.currentItems = value\n        this.$emit('update:items', value)\n      }\n    },\n\n    listNameFeedback() {\n      if (!this.$v.label.required) return this.$toolkit.t('dashboard.forms.feedback.requiredField')\n      if (!this.$v.label.maxLength) {\n        const modelLen = this.$v.label.$model.length\n        return `${this.$toolkit.t('dashboard.forms.feedback.charLimit', modelLen)}`\n      }\n      return ''\n    }\n  },\n\n  watch: {\n    items(value) {\n      this.currentItems = cloneDeep(value).map((item, index) => ({ ...item, id: item.id || index.toString() }))\n    },\n\n    currentItems: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    },\n\n    label: {\n      handler() {\n        this.$emit('form-validation', !this.$v.$invalid)\n      },\n      immediate: true\n    }\n  },\n\n  methods: {\n    addItem() {\n      this.currentItems = [{\n        id: new Date().getTime().toString(),\n        label: '',\n        type: null,\n        href: ''\n      }, ...this.currentItems]\n    },\n\n    removeItem(index) {\n      if (this.currentItems.length === 1) return\n\n      this.currentItems[index] = null\n      this.currentItems = [...this.currentItems].filter(item => item)\n      this.$emit('update:items', this.currentItems)\n    },\n\n    updateListTitle(value) {\n      this.$emit('update:label', value)\n    },\n\n    updateItem(index, value, prop, v) {\n      this.currentItems[index][prop] = value\n      if (prop === 'type' && value === widgetSubtypesEnum.INDICATOR) {\n        delete this.currentItems[index].href\n        this.currentItems[index].filter = {}\n        v.filter.$reset()\n      }\n\n      if (prop === 'type' && value === widgetSubtypesEnum.LINK) {\n        delete this.currentItems[index].filter\n        this.currentItems[index].href = ''\n        v.href.$reset()\n      }\n\n      if (prop !== 'href') v[prop].$touch()\n\n      this.$emit('update:items', this.currentItems)\n    },\n\n    getTypeLabel(type) {\n      return this.listTypeOptions.find(typeOption => typeOption.value === type)?.name\n    },\n\n    urlFeedback(input) {\n      if (!input.required) return `${this.$toolkit.t('dashboard.forms.feedback.requiredField')}`\n      if (!input.url) return `${this.$toolkit.t('dashboard.forms.feedback.invalidUrl')}`\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .list-link-form {\n\n    .form-header {\n      margin-bottom: 16px;\n    }\n\n    .form-body {\n      border: 1px solid var(--me-gray-6);\n      border-radius: 8px;\n\n      .ghost-class {\n        background-color: var(--me-primary-7);\n      }\n\n      &__item {\n        border-bottom: 1px solid var(--me-gray-6);\n\n        &:last-child {\n          border: none;\n        }\n      }\n\n      .form-row {\n        padding: 16px;\n      }\n    }\n\n    .form-row {\n      .form-group {\n\n        &.delete-icon {\n          padding-left: 3px;\n        }\n\n        .form-action {\n          margin-top: 0px;\n          @media screen and (min-width: 576px) {\n            margin-top: 22px;\n          }\n\n          &--btn {\n            margin-top: 0;\n\n            @media screen and (min-width: 576px) {\n              margin-top: 22px;\n            }\n          }\n\n          &--delete,\n          &--drag {\n            height: 40px;\n            width: 40px;\n            border-radius: 50%;\n            display: flex;\n            align-items: center;\n            justify-content: center;\n          }\n\n          &--delete {\n            cursor: pointer;\n\n            &:hover {\n              background-color: var(--me-danger-7);\n            }\n\n            &__disabled {\n              cursor: initial;\n              &:hover {\n                background-color: transparent;\n              }\n            }\n          }\n\n          &--drag {\n            cursor: grab;\n\n            &:hover {\n              background-color: var(--me-primary-7);\n            }\n          }\n        }\n      }\n    }\n  }\n</style>\n",".list-link-form .form-header {\n  margin-bottom: 16px;\n}\n.list-link-form .form-body {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\n.list-link-form .form-body .ghost-class {\n  background-color: var(--me-primary-7);\n}\n.list-link-form .form-body__item {\n  border-bottom: 1px solid var(--me-gray-6);\n}\n.list-link-form .form-body__item:last-child {\n  border: none;\n}\n.list-link-form .form-body .form-row {\n  padding: 16px;\n}\n.list-link-form .form-row .form-group.delete-icon {\n  padding-left: 3px;\n}\n.list-link-form .form-row .form-group .form-action {\n  margin-top: 0px;\n}\n@media screen and (min-width: 576px) {\n  .list-link-form .form-row .form-group .form-action {\n    margin-top: 22px;\n  }\n}\n.list-link-form .form-row .form-group .form-action--btn {\n  margin-top: 0;\n}\n@media screen and (min-width: 576px) {\n  .list-link-form .form-row .form-group .form-action--btn {\n    margin-top: 22px;\n  }\n}\n.list-link-form .form-row .form-group .form-action--delete, .list-link-form .form-row .form-group .form-action--drag {\n  height: 40px;\n  width: 40px;\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\n.list-link-form .form-row .form-group .form-action--delete {\n  cursor: pointer;\n}\n.list-link-form .form-row .form-group .form-action--delete:hover {\n  background-color: var(--me-danger-7);\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled {\n  cursor: initial;\n}\n.list-link-form .form-row .form-group .form-action--delete__disabled:hover {\n  background-color: transparent;\n}\n.list-link-form .form-row .form-group .form-action--drag {\n  cursor: grab;\n}\n.list-link-form .form-row .form-group .form-action--drag:hover {\n  background-color: var(--me-primary-7);\n}\n\n/*# sourceMappingURL=SimpleListForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$19 = "data-v-3dd4d26a";
  /* module identifier */
  const __vue_module_identifier__$19 = undefined;
  /* functional template */
  const __vue_is_functional_template__$19 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$19 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$19, staticRenderFns: __vue_staticRenderFns__$19 },
    __vue_inject_styles__$19,
    __vue_script__$19,
    __vue_scope_id__$19,
    __vue_is_functional_template__$19,
    __vue_module_identifier__$19,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1a = {
  components: {
    CardIndicatorForm: __vue_component__$16,
    CardLinkForm: __vue_component__$17,
    ListButtonForm: __vue_component__$18,
    SimpleListForm: __vue_component__$19
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    formModel: {
      type: Object,
      default: () => ({})
    },
    indicatorFilters: {
      type: Array,
      default: () => []
    },
    widgetType: {
      type: String,
      default: null,
      validator: (value) => {
        if (value === null) return true
        return Object.values(widgetTypesEnum).includes(value)
      }
    }
  },

  data() {
    return {
      widgetTypesEnum,
      isFormValid: false,
      isDeleteBusy: false,
      isSaveBusy: false,
      currentFormModel: cloneDeep_1(this.formModel)
    }
  },

  computed: {
    title() {
      const defaultText = this.formModel?.i ? this.$toolkit.t('dashboard.forms.title.editWidget') : this.$toolkit.t('dashboard.forms.title.newWidget');

      const titles = {
        [widgetTypesEnum.CARD_INDICATOR]: `${defaultText} - ${this.$toolkit.t('dashboard.widget.subtype.indicator')}`,
        [widgetTypesEnum.CARD_LINK]: `${defaultText} - ${this.$toolkit.t('dashboard.widget.subtype.link')}`,
        [widgetTypesEnum.LIST_BUTTON]: `${defaultText} - ${this.$toolkit.t('dashboard.widget.type.buttonList')}`,
        [widgetTypesEnum.LIST_CARD]: `${defaultText} - ${this.$toolkit.t('dashboard.widget.type.cardList')}`,
        [widgetTypesEnum.LIST_LINK]: `${defaultText} - ${this.$toolkit.t('dashboard.widget.type.simpleList')}`
      };

      return titles[this.widgetType] || ''
    },

    modalButtons() {
      let defaultButtons = [
        {
          label: this.$toolkit.t('dashboard.forms.actions.save'),
          click: close => this.handleSave(close),
          busy: this.isSaveBusy,
          disabled: !this.isFormValid
        },
        {
          label: this.$toolkit.t('dashboard.forms.actions.cancel'),
          click: close => close()
        }
      ];

      if (this.formModel?.i) {
        const deleteButton = {
          label: this.$toolkit.t('dashboard.forms.actions.delete'),
          click: close => this.handleDelete(close),
          busy: this.isDeleteBusy,
          icon: {
            prepend: true,
            name: 'me-icon-l icon-trash'
          }
        };

        defaultButtons = [...defaultButtons, deleteButton];
      }

      return defaultButtons
    }
  },

  watch: {
    formModel(value) {
      this.currentFormModel = cloneDeep_1(value);
    }
  },

  methods: {
    handleSave(close) {
      this.isSaveBusy = true;
      this.$emit('update:formModel', this.currentFormModel);
      this.$emit('save', this.currentFormModel, close);
    },

    handleDelete(close) {
      this.isDeleteBusy = true;
      this.$emit('delete', close);
    },

    hide() {
      this.$emit('hide');
      this.$emit('update:visible', false);
      this.isDeleteBusy = false;
      this.isSaveBusy = false;
      this.isFormValid = false;
      this.currentFormModel = cloneDeep_1(this.formModel);
    }
  }
};

/* script */
const __vue_script__$1a = script$1a;

/* template */
var __vue_render__$1a = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "me-modal",
    {
      attrs: {
        scrollable: _vm.scrollable,
        noHeaderBorder: false,
        noFooterBorder: false,
        buttons: _vm.modalButtons,
        visible: _vm.visible,
        busy: _vm.busy,
        title: _vm.title,
        size: "lg"
      },
      on: { hide: _vm.hide }
    },
    [
      _vm.currentFormModel
        ? [
            _vm.widgetType === _vm.widgetTypesEnum.CARD_INDICATOR
              ? _c("CardIndicatorForm", {
                  attrs: {
                    label: _vm.currentFormModel.label,
                    filter: _vm.currentFormModel.filter,
                    indicatorFilters: _vm.indicatorFilters
                  },
                  on: {
                    "update:label": function($event) {
                      return _vm.$set(_vm.currentFormModel, "label", $event)
                    },
                    "update:filter": function($event) {
                      return _vm.$set(_vm.currentFormModel, "filter", $event)
                    },
                    "form-validation": function(isValid) {
                      return (_vm.isFormValid = isValid)
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widgetType === _vm.widgetTypesEnum.CARD_LINK
              ? _c("CardLinkForm", {
                  attrs: {
                    label: _vm.currentFormModel.label,
                    href: _vm.currentFormModel.href
                  },
                  on: {
                    "update:label": function($event) {
                      return _vm.$set(_vm.currentFormModel, "label", $event)
                    },
                    "update:href": function($event) {
                      return _vm.$set(_vm.currentFormModel, "href", $event)
                    },
                    "form-validation": function(isValid) {
                      return (_vm.isFormValid = isValid)
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widgetType === _vm.widgetTypesEnum.LIST_BUTTON
              ? _c("ListButtonForm", {
                  attrs: {
                    label: _vm.currentFormModel.label,
                    items: _vm.currentFormModel.items
                  },
                  on: {
                    "update:label": function($event) {
                      return _vm.$set(_vm.currentFormModel, "label", $event)
                    },
                    "update:items": function($event) {
                      return _vm.$set(_vm.currentFormModel, "items", $event)
                    },
                    "form-validation": function(isValid) {
                      return (_vm.isFormValid = isValid)
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widgetType === _vm.widgetTypesEnum.LIST_CARD ||
            _vm.widgetType === _vm.widgetTypesEnum.LIST_LINK
              ? _c("SimpleListForm", {
                  attrs: {
                    label: _vm.currentFormModel.label,
                    items: _vm.currentFormModel.items,
                    indicatorFilters: _vm.indicatorFilters
                  },
                  on: {
                    "update:label": function($event) {
                      return _vm.$set(_vm.currentFormModel, "label", $event)
                    },
                    "update:items": function($event) {
                      return _vm.$set(_vm.currentFormModel, "items", $event)
                    },
                    "form-validation": function(isValid) {
                      return (_vm.isFormValid = isValid)
                    }
                  }
                })
              : _vm._e()
          ]
        : _vm._e()
    ],
    2
  )
};
var __vue_staticRenderFns__$1a = [];
__vue_render__$1a._withStripped = true;

  /* style */
  const __vue_inject_styles__$1a = undefined;
  /* scoped */
  const __vue_scope_id__$1a = undefined;
  /* module identifier */
  const __vue_module_identifier__$1a = undefined;
  /* functional template */
  const __vue_is_functional_template__$1a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1a, staticRenderFns: __vue_staticRenderFns__$1a },
    __vue_inject_styles__$1a,
    __vue_script__$1a,
    __vue_scope_id__$1a,
    __vue_is_functional_template__$1a,
    __vue_module_identifier__$1a,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$1b = {
  name: 'MeWidgetCardIndicator',
  components: {
    WidgetSkeleton: __vue_component__$U,
    WidgetCardIndicator: __vue_component__$X,
    WidgetCardButton: __vue_component__$Z,
    WidgetCardLink: __vue_component__$_,
    WidgetListCard: __vue_component__$11,
    WidgetListLink: __vue_component__$14,
    WidgetListButton: __vue_component__$15,
    ConfigWidgetModal: __vue_component__$1a
  },

  props: {
    widget: {
      type: Object,
      default: () => ({})
    },
    busy: {
      type: Boolean,
      default: false
    },
    isDeletable: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isDragging: {
      type: Boolean,
      default: false
    },
    indicatorFilters: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      widgetTypesEnum,
      isConfigWidgetModalVisible: false,
      formModel: this.getFormModel(this.widget)
    }
  },

  watch: {
    widget(value) {
      this.formModel = this.getFormModel(value);
    }
  },

  methods: {
    onEditWidget() {
      this.isConfigWidgetModalVisible = true;
    },

    getFormModel(widget) {
      if (widget.type === widgetTypesEnum.CARD_INDICATOR) {
        return {
          i: widget.i || null,
          label: widget.label || '',
          filter: widget.filter || {}
        }
      }

      if (widget.type === widgetTypesEnum.CARD_LINK) {
        return {
          i: widget.i || null,
          label: widget.label || '',
          href: widget.href || ''
        }
      }

      const isList = widget.type === widgetTypesEnum.LIST_BUTTON ||
          widget.type === widgetTypesEnum.LIST_CARD ||
          widget.type === widgetTypesEnum.LIST_LINK;

      if (isList) {
        return {
          i: widget.i || null,
          label: widget.label || '',
          items: widget.items || []
        }
      }
    }
  }
};

/* script */
const __vue_script__$1b = script$1b;

/* template */
var __vue_render__$1b = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "dashboard-item",
      class: { "dashboard-item--active": _vm.widget.active }
    },
    [
      _c("ConfigWidgetModal", {
        attrs: {
          visible: _vm.isConfigWidgetModalVisible,
          formModel: _vm.formModel,
          widgetType: _vm.widget.type,
          indicatorFilters: _vm.indicatorFilters,
          busy: _vm.busy,
          scrollable: ""
        },
        on: {
          "update:visible": function($event) {
            _vm.isConfigWidgetModalVisible = $event;
          },
          save: function(form, close) {
            return _vm.$emit("save", form, close)
          },
          delete: function(close) {
            return _vm.$emit("delete", _vm.widget.i, close)
          }
        }
      }),
      _vm._v(" "),
      _vm.widget.busy
        ? _c("WidgetSkeleton")
        : [
            _vm.widget.type === _vm.widgetTypesEnum.CARD_INDICATOR
              ? _c("WidgetCardIndicator", {
                  attrs: {
                    label: _vm.widget.label,
                    disabledClick: _vm.isDragging,
                    value: _vm.widget.value,
                    badgeValue: _vm.widget.badgeValue,
                    variant: _vm.widget.variant,
                    error: _vm.widget.error,
                    editable: _vm.isEditable,
                    deletable: _vm.isDeletable,
                    href: _vm.widget.href,
                    click: _vm.widget.click
                  },
                  on: {
                    edit: _vm.onEditWidget,
                    delete: function() {
                      return _vm.$emit("delete", _vm.widget.i, function() {
                        return null
                      })
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widget.type === _vm.widgetTypesEnum.CARD_BUTTON
              ? _c("WidgetCardButton", {
                  attrs: {
                    label: _vm.widget.label,
                    width: _vm.widget.width,
                    height: _vm.widget.height,
                    items: _vm.widget.items,
                    error: _vm.widget.error,
                    editable: _vm.isEditable,
                    deletable: _vm.isDeletable,
                    description: _vm.widget.description,
                    click: _vm.widget.click
                  },
                  on: {
                    delete: function() {
                      return _vm.$emit("delete", _vm.widget.i, function() {
                        return null
                      })
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widget.type === _vm.widgetTypesEnum.CARD_LINK
              ? _c("WidgetCardLink", {
                  attrs: {
                    label: _vm.widget.label,
                    disabledClick: _vm.isDragging,
                    error: _vm.widget.error,
                    editable: _vm.isEditable,
                    deletable: _vm.isDeletable,
                    icon: _vm.widget.icon,
                    href: _vm.widget.href,
                    click: _vm.widget.click
                  },
                  on: {
                    edit: _vm.onEditWidget,
                    delete: function() {
                      return _vm.$emit("delete", _vm.widget.i, function() {
                        return null
                      })
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widget.type === _vm.widgetTypesEnum.LIST_CARD
              ? _c("WidgetListCard", {
                  attrs: {
                    label: _vm.widget.label,
                    items: _vm.widget.items,
                    deletable: _vm.isDeletable,
                    error: _vm.widget.error,
                    editable: _vm.isEditable,
                    click: _vm.widget.click
                  },
                  on: {
                    edit: _vm.onEditWidget,
                    delete: function() {
                      return _vm.$emit("delete", _vm.widget.i, function() {
                        return null
                      })
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widget.type === _vm.widgetTypesEnum.LIST_LINK
              ? _c("WidgetListLink", {
                  attrs: {
                    label: _vm.widget.label,
                    items: _vm.widget.items,
                    error: _vm.widget.error,
                    editable: _vm.isEditable,
                    deletable: _vm.isDeletable,
                    click: _vm.widget.click
                  },
                  on: {
                    edit: _vm.onEditWidget,
                    delete: function() {
                      return _vm.$emit("delete", _vm.widget.i, function() {
                        return null
                      })
                    }
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.widget.type === _vm.widgetTypesEnum.LIST_BUTTON
              ? _c("WidgetListButton", {
                  attrs: {
                    label: _vm.widget.label,
                    items: _vm.widget.items,
                    error: _vm.widget.error,
                    editable: _vm.isEditable,
                    deletable: _vm.isDeletable,
                    click: _vm.widget.click
                  },
                  on: {
                    edit: _vm.onEditWidget,
                    delete: function() {
                      return _vm.$emit("delete", _vm.widget.i, function() {
                        return null
                      })
                    }
                  }
                })
              : _vm._e()
          ]
    ],
    2
  )
};
var __vue_staticRenderFns__$1b = [];
__vue_render__$1b._withStripped = true;

  /* style */
  const __vue_inject_styles__$1b = function (inject) {
    if (!inject) return
    inject("data-v-04981671_0", { source: "div.dashboard-item[data-v-04981671] {\n  height: 100%;\n  border-radius: 16px;\n}\ndiv.dashboard-item--active[data-v-04981671] {\n  border: 1px solid var(--me-primary-2);\n}\n\n/*# sourceMappingURL=DashboardItem.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/DashboardItem.vue","DashboardItem.vue"],"names":[],"mappings":"AA8MA;EACA,YAAA;EACA,mBAAA;AC7MA;AD+MA;EACA,qCAAA;AC7MA;;AAEA,4CAA4C","file":"DashboardItem.vue","sourcesContent":["<template>\n  <div\n    :class=\"{'dashboard-item--active': widget.active}\"\n    class=\"dashboard-item\"\n  >\n    <ConfigWidgetModal\n      :visible.sync=\"isConfigWidgetModalVisible\"\n      :formModel=\"formModel\"\n      :widgetType=\"widget.type\"\n      :indicatorFilters=\"indicatorFilters\"\n      :busy=\"busy\"\n      @save=\"(form, close) => $emit('save', form, close)\"\n      @delete=\"(close) => $emit('delete', widget.i, close)\"\n      scrollable\n    />\n\n    <WidgetSkeleton v-if=\"widget.busy\" />\n    <template v-else>\n      <WidgetCardIndicator\n        v-if=\"widget.type === widgetTypesEnum.CARD_INDICATOR\"\n        :label=\"widget.label\"\n        :disabledClick=\"isDragging\"\n        :value=\"widget.value\"\n        :badgeValue=\"widget.badgeValue\"\n        :variant=\"widget.variant\"\n        :error=\"widget.error\"\n        :editable=\"isEditable\"\n        :deletable=\"isDeletable\"\n        :href=\"widget.href\"\n        :click=\"widget.click\"\n        @edit=\"onEditWidget\"\n        @delete=\"() => $emit('delete', widget.i, () => null)\"\n      />\n\n      <WidgetCardButton\n        :label=\"widget.label\"\n        v-if=\"widget.type === widgetTypesEnum.CARD_BUTTON\"\n        :width=\"widget.width\"\n        :height=\"widget.height\"\n        :items=\"widget.items\"\n        :error=\"widget.error\"\n        :editable=\"isEditable\"\n        :deletable=\"isDeletable\"\n        :description=\"widget.description\"\n        :click=\"widget.click\"\n        @delete=\"() => $emit('delete', widget.i, () => null)\"\n      />\n\n      <WidgetCardLink\n        v-if=\"widget.type === widgetTypesEnum.CARD_LINK\"\n        :label=\"widget.label\"\n        :disabledClick=\"isDragging\"\n        :error=\"widget.error\"\n        :editable=\"isEditable\"\n        :deletable=\"isDeletable\"\n        :icon=\"widget.icon\"\n        :href=\"widget.href\"\n        :click=\"widget.click\"\n        @edit=\"onEditWidget\"\n        @delete=\"() => $emit('delete', widget.i, () => null)\"\n      />\n\n      <WidgetListCard\n        v-if=\"widget.type === widgetTypesEnum.LIST_CARD\"\n        :label=\"widget.label\"\n        :items=\"widget.items\"\n        :deletable=\"isDeletable\"\n        :error=\"widget.error\"\n        :editable=\"isEditable\"\n        :click=\"widget.click\"\n        @edit=\"onEditWidget\"\n        @delete=\"() => $emit('delete', widget.i, () => null)\"\n      />\n\n      <WidgetListLink\n        v-if=\"widget.type === widgetTypesEnum.LIST_LINK\"\n        :label=\"widget.label\"\n        :items=\"widget.items\"\n        :error=\"widget.error\"\n        :editable=\"isEditable\"\n        :deletable=\"isDeletable\"\n        :click=\"widget.click\"\n        @edit=\"onEditWidget\"\n        @delete=\"() => $emit('delete', widget.i, () => null)\"\n      />\n\n      <WidgetListButton\n        v-if=\"widget.type === widgetTypesEnum.LIST_BUTTON\"\n        :label=\"widget.label\"\n        :items=\"widget.items\"\n        :error=\"widget.error\"\n        :editable=\"isEditable\"\n        :deletable=\"isDeletable\"\n        :click=\"widget.click\"\n        @edit=\"onEditWidget\"\n        @delete=\"() => $emit('delete', widget.i, () => null)\"\n      />\n    </template>\n  </div>\n</template>\n\n<script>\n\nimport { widgetTypesEnum } from './enums/widgetTypesEnum.js'\nimport WidgetSkeleton from './widgets/WidgetSkeleton.vue'\nimport WidgetCardIndicator from './widgets/card/WidgetCardIndicator.vue'\nimport WidgetCardButton from './widgets/card/WidgetCardButton.vue'\nimport WidgetCardLink from './widgets/card/WidgetCardLink.vue'\nimport WidgetListCard from './widgets/list/WidgetListCard.vue'\nimport WidgetListLink from './widgets/list/WidgetListLink.vue'\nimport WidgetListButton from './widgets/list/WidgetListButton.vue'\nimport ConfigWidgetModal from './config/ConfigWidgetModal.vue'\n\nexport default {\n  name: 'MeWidgetCardIndicator',\n  components: {\n    WidgetSkeleton,\n    WidgetCardIndicator,\n    WidgetCardButton,\n    WidgetCardLink,\n    WidgetListCard,\n    WidgetListLink,\n    WidgetListButton,\n    ConfigWidgetModal\n  },\n\n  props: {\n    widget: {\n      type: Object,\n      default: () => ({})\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    isDeletable: {\n      type: Boolean,\n      default: false\n    },\n    isEditable: {\n      type: Boolean,\n      default: false\n    },\n    isDragging: {\n      type: Boolean,\n      default: false\n    },\n    indicatorFilters: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      widgetTypesEnum,\n      isConfigWidgetModalVisible: false,\n      formModel: this.getFormModel(this.widget)\n    }\n  },\n\n  watch: {\n    widget(value) {\n      this.formModel = this.getFormModel(value)\n    }\n  },\n\n  methods: {\n    onEditWidget() {\n      this.isConfigWidgetModalVisible = true\n    },\n\n    getFormModel(widget) {\n      if (widget.type === widgetTypesEnum.CARD_INDICATOR) {\n        return {\n          i: widget.i || null,\n          label: widget.label || '',\n          filter: widget.filter || {}\n        }\n      }\n\n      if (widget.type === widgetTypesEnum.CARD_LINK) {\n        return {\n          i: widget.i || null,\n          label: widget.label || '',\n          href: widget.href || ''\n        }\n      }\n\n      const isList = widget.type === widgetTypesEnum.LIST_BUTTON ||\n          widget.type === widgetTypesEnum.LIST_CARD ||\n          widget.type === widgetTypesEnum.LIST_LINK\n\n      if (isList) {\n        return {\n          i: widget.i || null,\n          label: widget.label || '',\n          items: widget.items || []\n        }\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.dashboard-item {\n    height: 100%;\n    border-radius: 16px;\n\n    &--active {\n      border: 1px solid var(--me-primary-2);\n    }\n\n  }\n</style>\n","div.dashboard-item {\n  height: 100%;\n  border-radius: 16px;\n}\ndiv.dashboard-item--active {\n  border: 1px solid var(--me-primary-2);\n}\n\n/*# sourceMappingURL=DashboardItem.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1b = "data-v-04981671";
  /* module identifier */
  const __vue_module_identifier__$1b = undefined;
  /* functional template */
  const __vue_is_functional_template__$1b = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1b, staticRenderFns: __vue_staticRenderFns__$1b },
    __vue_inject_styles__$1b,
    __vue_script__$1b,
    __vue_scope_id__$1b,
    __vue_is_functional_template__$1b,
    __vue_module_identifier__$1b,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1c = {
  name: 'MeDashboard',
  components: {
    GridLayout: VueGridLayout.GridLayout,
    GridItem: VueGridLayout.GridItem,
    DashboardItem: __vue_component__$1b,
    DashboardEmpty: __vue_component__$Y
  },

  props: {
    widgets: {
      type: Array,
      required: true
    },
    isDraggable: {
      type: Boolean,
      default: false
    },
    isResizable: {
      type: Boolean,
      default: false
    },
    isResponsive: {
      type: Boolean,
      default: false
    },
    colNumber: {
      type: Number,
      default: 24
    },
    indicatorFilters: {
      type: Array,
      default: () => []
    },
    busy: {
      type: Boolean,
      default: false
    },
    isDeletable: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    margin: {
      type: Array,
      default: () => [16, 16]
    },
    breakpoints: {
      type: Object,
      default: () => ({ desktop: 480, mobile: 0 })
    },
    cols: {
      type: Object,
      default: () => ({ desktop: 24, mobile: 4 })
    },
    minW: {
      type: Number,
      default: 2
    },
    minH: {
      type: Number,
      default: 3
    },
    rowHeight: {
      type: Number,
      default: 32
    }
  },

  data() {
    return {
      isDragging: false,
      transitionsActive: false,
      widget: this.mapWidgets(this.widgets),
      layout: this.mapLayout(this.widgets)
    }
  },

  watch: {
    widgets: debounce_1(function (widgets) {
      this.transitionsActive = true;
      this.widget = this.mapWidgets(widgets);

      const newLayout = this.mapLayout(widgets);
      if (this.hasChangeLayout(newLayout)) {
        this.layout = newLayout;
      }
    }, 250)
  },

  methods: {
    onLayoutUpdated: debounce_1(function (newLayout) {
      const widgets = newLayout.map(item => ({
        ...this.widget[item.i],
        ...item
      }));

      this.$emit('widgets-updated', widgets);
    }, 50),

    hasChangeLayout(newLayout) {
      if (newLayout.length !== this.layout.length) return true

      return this.layout.some((layoutItem) => {
        const item = newLayout.find(newLayoutItem => newLayoutItem.i === layoutItem.i);
        if (!item) return true

        return (
          item.x !== layoutItem.x ||
          item.y !== layoutItem.y ||
          item.w !== layoutItem.w ||
          item.h !== layoutItem.h
        )
      })
    },

    mapWidgets: (widgets) => {
      return widgets.reduce((acc, widget) => ({ ...acc, [widget.i]: widget }), {})
    },
    mapLayout: (widgets) => {
      return widgets.map((item) => {
        return {
          static: item.static,
          i: item.i,
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h
        }
      })
    }
  }
};

/* script */
const __vue_script__$1c = script$1c;

/* template */
var __vue_render__$1c = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    {
      ref: "dashboardRef",
      staticClass: "me-dashboard",
      class: { "me-dashboard--transition-active": _vm.transitionsActive }
    },
    [
      _vm._t("empty", [
        _vm.layout.length === 0
          ? [
              _c(
                "div",
                {
                  staticClass:
                    "d-flex flex-column justify-content-center align-items-center w-100 h-100"
                },
                [
                  _c("DashboardEmpty", { attrs: { width: 300, height: 300 } }),
                  _vm._v(" "),
                  _c("span", { staticClass: "empty-state-legend" }, [
                    _vm._v(
                      "\n          " +
                        _vm._s(_vm.$toolkit.t("dashboard.noWidget")) +
                        "\n        "
                    )
                  ])
                ],
                1
              )
            ]
          : _vm._e()
      ]),
      _vm._v(" "),
      _vm.layout.length !== 0
        ? [
            _c(
              "grid-layout",
              {
                attrs: {
                  layout: _vm.layout,
                  "col-num": _vm.colNumber,
                  margin: _vm.margin,
                  cols: _vm.cols,
                  breakpoints: _vm.breakpoints,
                  "row-height": _vm.rowHeight,
                  "is-draggable": _vm.isDraggable,
                  "is-resizable": _vm.isResizable,
                  responsive: _vm.isResponsive,
                  "vertical-compact": true,
                  "use-css-transforms": false
                },
                on: {
                  "update:layout": function($event) {
                    _vm.layout = $event;
                  },
                  "layout-updated": _vm.onLayoutUpdated
                }
              },
              _vm._l(_vm.layout, function(item) {
                return _c(
                  "grid-item",
                  {
                    key: item.i,
                    attrs: {
                      static: item.static,
                      x: item.x,
                      y: item.y,
                      w: item.w,
                      h: item.h,
                      i: item.i,
                      minW: _vm.minW,
                      minH: _vm.minH
                    },
                    on: {
                      move: function($event) {
                        _vm.isDragging = true;
                      }
                    },
                    nativeOn: {
                      click: function($event) {
                        _vm.isDragging = false;
                      }
                    }
                  },
                  [
                    _vm.widget[item.i]
                      ? _c("DashboardItem", {
                          attrs: {
                            widget: _vm.widget[item.i],
                            busy: _vm.busy,
                            isEditable: _vm.isEditable,
                            isDeletable: _vm.isDeletable,
                            isDragging: _vm.isDragging,
                            indicatorFilters: _vm.indicatorFilters
                          },
                          on: {
                            save: function(formData, close) {
                              return _vm.$emit("save", formData, close)
                            },
                            delete: function(widgetId, close) {
                              return _vm.$emit("delete", widgetId, close)
                            }
                          }
                        })
                      : _vm._e()
                  ],
                  1
                )
              }),
              1
            )
          ]
        : _vm._e()
    ],
    2
  )
};
var __vue_staticRenderFns__$1c = [];
__vue_render__$1c._withStripped = true;

  /* style */
  const __vue_inject_styles__$1c = function (inject) {
    if (!inject) return
    inject("data-v-44a306d0_0", { source: ".vue-grid-item[data-v-44a306d0] {\n  transition-property: none;\n  touch-action: none;\n}\n[data-v-44a306d0] .vue-grid-item .vue-resizable-handle {\n  margin-right: 2px;\n  margin-bottom: 2px;\n}\n[data-v-44a306d0] .vue-grid-item.vue-grid-placeholder {\n  background: var(--me-primary-6) !important;\n  border-radius: 16px;\n}\n.vue-grid-item[data-v-44a306d0]:not(.vue-grid-placeholder) {\n  background: var(--me-white);\n  border: 1px solid var(--me-gray-6);\n  border-radius: 16px;\n}\n.vue-grid-item .resizing[data-v-44a306d0] {\n  opacity: 0.9;\n}\n.vue-grid-item .no-drag[data-v-44a306d0] {\n  height: 100%;\n  width: 100%;\n}\n.me-dashboard[data-v-44a306d0] {\n  width: 100%;\n  height: 100%;\n}\n.me-dashboard .empty-state-image[data-v-44a306d0] {\n  height: 200px;\n  width: 200px;\n  margin-bottom: 30px;\n}\n.me-dashboard .empty-state-legend[data-v-44a306d0] {\n  font-size: 20px;\n  font-weight: 600;\n  color: var(--me-gray-5);\n}\n.me-dashboard--transition-active .vue-grid-item[data-v-44a306d0] {\n  transition-property: transform, -webkit-transform;\n}\n\n/*# sourceMappingURL=Dashboard.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/Dashboard.vue","Dashboard.vue"],"names":[],"mappings":"AA6MA;EACA,yBAAA;EACA,kBAAA;AC5MA;AD+MA;EACA,iBAAA;EACA,kBAAA;AC5MA;AD+MA;EACA,0CAAA;EACA,mBAAA;AC5MA;AD+MA;EACA,2BAAA;EACA,kCAAA;EACA,mBAAA;AC5MA;AD+MA;EACA,YAAA;AC5MA;AD+MA;EACA,YAAA;EACA,WAAA;AC5MA;AD+MA;EACA,WAAA;EACA,YAAA;AC5MA;AD8MA;EACA,aAAA;EACA,YAAA;EACA,mBAAA;AC5MA;AD+MA;EACA,eAAA;EACA,gBAAA;EACA,uBAAA;AC7MA;ADiNA;EACA,iDAAA;AC/MA;;AAEA,wCAAwC","file":"Dashboard.vue","sourcesContent":["<template>\n  <section ref=\"dashboardRef\" :class=\"{'me-dashboard--transition-active': transitionsActive}\" class=\"me-dashboard\">\n    <slot name=\"empty\">\n      <template v-if=\"layout.length === 0\">\n        <div class=\"d-flex flex-column justify-content-center align-items-center w-100 h-100\">\n          <DashboardEmpty :width=\"300\" :height=\"300\" />\n          <span class=\"empty-state-legend\">\n            {{ $toolkit.t('dashboard.noWidget') }}\n          </span>\n        </div>\n      </template>\n    </slot>\n\n    <template v-if=\"layout.length !== 0\">\n      <grid-layout\n        :layout.sync=\"layout\"\n        :col-num=\"colNumber\"\n        :margin=\"margin\"\n        :cols=\"cols\"\n        :breakpoints=\"breakpoints\"\n        :row-height=\"rowHeight\"\n        :is-draggable=\"isDraggable\"\n        :is-resizable=\"isResizable\"\n        :responsive=\"isResponsive\"\n        :vertical-compact=\"true\"\n        :use-css-transforms=\"false\"\n        @layout-updated=\"onLayoutUpdated\"\n      >\n        <grid-item\n          v-for=\"item in layout\"\n          :key=\"item.i\"\n          :static=\"item.static\"\n          :x=\"item.x\"\n          :y=\"item.y\"\n          :w=\"item.w\"\n          :h=\"item.h\"\n          :i=\"item.i\"\n          :minW=\"minW\"\n          :minH=\"minH\"\n          @move=\"isDragging = true\"\n          @click.native=\"isDragging = false\"\n        >\n          <DashboardItem\n            v-if=\"widget[item.i]\"\n            :widget=\"widget[item.i]\"\n            :busy=\"busy\"\n            :isEditable=\"isEditable\"\n            :isDeletable=\"isDeletable\"\n            :isDragging=\"isDragging\"\n            :indicatorFilters=\"indicatorFilters\"\n            @save=\"(formData, close) => $emit('save', formData, close)\"\n            @delete=\"(widgetId, close) => $emit('delete', widgetId, close)\"\n          />\n        </grid-item>\n      </grid-layout>\n    </template>\n  </section>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport VueGridLayout from 'vue-grid-layout'\n\nimport DashboardItem from './DashboardItem.vue'\nimport DashboardEmpty from './DashboardEmpty.vue'\n\nexport default {\n  name: 'MeDashboard',\n  components: {\n    GridLayout: VueGridLayout.GridLayout,\n    GridItem: VueGridLayout.GridItem,\n    DashboardItem,\n    DashboardEmpty\n  },\n\n  props: {\n    widgets: {\n      type: Array,\n      required: true\n    },\n    isDraggable: {\n      type: Boolean,\n      default: false\n    },\n    isResizable: {\n      type: Boolean,\n      default: false\n    },\n    isResponsive: {\n      type: Boolean,\n      default: false\n    },\n    colNumber: {\n      type: Number,\n      default: 24\n    },\n    indicatorFilters: {\n      type: Array,\n      default: () => []\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    isDeletable: {\n      type: Boolean,\n      default: false\n    },\n    isEditable: {\n      type: Boolean,\n      default: false\n    },\n    margin: {\n      type: Array,\n      default: () => [16, 16]\n    },\n    breakpoints: {\n      type: Object,\n      default: () => ({ desktop: 480, mobile: 0 })\n    },\n    cols: {\n      type: Object,\n      default: () => ({ desktop: 24, mobile: 4 })\n    },\n    minW: {\n      type: Number,\n      default: 2\n    },\n    minH: {\n      type: Number,\n      default: 3\n    },\n    rowHeight: {\n      type: Number,\n      default: 32\n    }\n  },\n\n  data() {\n    return {\n      isDragging: false,\n      transitionsActive: false,\n      widget: this.mapWidgets(this.widgets),\n      layout: this.mapLayout(this.widgets)\n    }\n  },\n\n  watch: {\n    widgets: debounce(function (widgets) {\n      this.transitionsActive = true\n      this.widget = this.mapWidgets(widgets)\n\n      const newLayout = this.mapLayout(widgets)\n      if (this.hasChangeLayout(newLayout)) {\n        this.layout = newLayout\n      }\n    }, 250)\n  },\n\n  methods: {\n    onLayoutUpdated: debounce(function (newLayout) {\n      const widgets = newLayout.map(item => ({\n        ...this.widget[item.i],\n        ...item\n      }))\n\n      this.$emit('widgets-updated', widgets)\n    }, 50),\n\n    hasChangeLayout(newLayout) {\n      if (newLayout.length !== this.layout.length) return true\n\n      return this.layout.some((layoutItem) => {\n        const item = newLayout.find(newLayoutItem => newLayoutItem.i === layoutItem.i)\n        if (!item) return true\n\n        return (\n          item.x !== layoutItem.x ||\n          item.y !== layoutItem.y ||\n          item.w !== layoutItem.w ||\n          item.h !== layoutItem.h\n        )\n      })\n    },\n\n    mapWidgets: (widgets) => {\n      return widgets.reduce((acc, widget) => ({ ...acc, [widget.i]: widget }), {})\n    },\n    mapLayout: (widgets) => {\n      return widgets.map((item) => {\n        return {\n          static: item.static,\n          i: item.i,\n          x: item.x,\n          y: item.y,\n          w: item.w,\n          h: item.h\n        }\n      })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.vue-grid-item {\n  transition-property: none;\n  touch-action: none\n}\n\n::v-deep .vue-grid-item .vue-resizable-handle {\n  margin-right: 2px;\n  margin-bottom: 2px;\n}\n\n::v-deep .vue-grid-item.vue-grid-placeholder {\n    background: var(--me-primary-6) !important;\n    border-radius: 16px;\n}\n\n.vue-grid-item:not(.vue-grid-placeholder) {\n    background: var(--me-white);\n    border: 1px solid var(--me-gray-6);\n    border-radius: 16px;\n}\n\n.vue-grid-item .resizing {\n    opacity: 0.9;\n}\n\n.vue-grid-item .no-drag {\n    height: 100%;\n    width: 100%;\n}\n\n.me-dashboard {\n  width: 100%;\n  height: 100%;\n\n  .empty-state-image {\n    height: 200px;\n    width: 200px;\n    margin-bottom: 30px;\n  }\n\n  .empty-state-legend {\n    font-size: 20px;\n    font-weight: 600;\n    color: var(--me-gray-5);\n  }\n\n  &--transition-active {\n    .vue-grid-item {\n      transition-property: transform,-webkit-transform;\n    }\n  }\n}\n</style>\n",".vue-grid-item {\n  transition-property: none;\n  touch-action: none;\n}\n\n::v-deep .vue-grid-item .vue-resizable-handle {\n  margin-right: 2px;\n  margin-bottom: 2px;\n}\n\n::v-deep .vue-grid-item.vue-grid-placeholder {\n  background: var(--me-primary-6) !important;\n  border-radius: 16px;\n}\n\n.vue-grid-item:not(.vue-grid-placeholder) {\n  background: var(--me-white);\n  border: 1px solid var(--me-gray-6);\n  border-radius: 16px;\n}\n\n.vue-grid-item .resizing {\n  opacity: 0.9;\n}\n\n.vue-grid-item .no-drag {\n  height: 100%;\n  width: 100%;\n}\n\n.me-dashboard {\n  width: 100%;\n  height: 100%;\n}\n.me-dashboard .empty-state-image {\n  height: 200px;\n  width: 200px;\n  margin-bottom: 30px;\n}\n.me-dashboard .empty-state-legend {\n  font-size: 20px;\n  font-weight: 600;\n  color: var(--me-gray-5);\n}\n.me-dashboard--transition-active .vue-grid-item {\n  transition-property: transform, -webkit-transform;\n}\n\n/*# sourceMappingURL=Dashboard.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1c = "data-v-44a306d0";
  /* module identifier */
  const __vue_module_identifier__$1c = undefined;
  /* functional template */
  const __vue_is_functional_template__$1c = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1c, staticRenderFns: __vue_staticRenderFns__$1c },
    __vue_inject_styles__$1c,
    __vue_script__$1c,
    __vue_scope_id__$1c,
    __vue_is_functional_template__$1c,
    __vue_module_identifier__$1c,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1d = {
  name: 'MeDocHeader',
  props: {
    process: {
      type: String,
      required: true
    },
    number: {
      type: String,
      required: true
    },
    clientNumber: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    labelInfoSup: {
      type: String,
      default: null
    },
    labelInfoBot: {
      type: String,
      default: null
    },
    collectionInfo: {
      type: Array,
      default: null
    },
    collectionBorgList: {
      type: Array,
      default: null
    },
    expandText: {
      type: String,
      required: true,
      default: null
    },
    expandLink: {
      type: String,
      default: ''
    }
  },

  methods: {
    setFontSizeByNumberLenght(length) {
      if (length === 14) {
        return 'font-size-21'
      }

      if (length >= 15) {
        return 'font-size-10'
      }
    }
  }
};

/* script */
const __vue_script__$1d = script$1d;

/* template */
var __vue_render__$1d = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-doc-header" }, [
    _c(
      "section",
      { staticClass: "me-doc-header_badge" },
      [
        _c("h3", [_vm._v(_vm._s(_vm.process))]),
        _vm._v(" "),
        _vm._t("number", [
          _vm.number
            ? _c(
                "p",
                {
                  staticClass: "process-number",
                  class: _vm.setFontSizeByNumberLenght(_vm.number.length)
                },
                [_vm._v("\n        " + _vm._s(_vm.number) + "\n      ")]
              )
            : _vm._e()
        ]),
        _vm._v(" "),
        _vm._t("clientNumber", [
          _vm.clientNumber
            ? _c("p", { staticClass: "client-number" }, [
                _vm._v("\n        " + _vm._s(_vm.clientNumber) + "\n      ")
              ])
            : _vm._e()
        ])
      ],
      2
    ),
    _vm._v(" "),
    _c("section", { staticClass: "me-doc-header_content" }, [
      _c(
        "div",
        { staticClass: "collectionData" },
        [
          _c(
            "span",
            {
              directives: [
                {
                  name: "me-tooltip",
                  rawName: "v-me-tooltip",
                  value: _vm.title,
                  expression: "title"
                }
              ],
              staticClass: "title-purchase"
            },
            [_vm._v(_vm._s(_vm._f("me-cut-text")(_vm.title, 20)))]
          ),
          _vm._v(" "),
          _vm._t("labelInfoSup", [
            _vm.labelInfoSup
              ? _c("div", [_c("i", [_vm._v(_vm._s(_vm.labelInfoSup))])])
              : _vm._e()
          ]),
          _vm._v(" "),
          _vm._t("labelInfoBot", [
            _vm.labelInfoBot
              ? _c(
                  "div",
                  {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value:
                          "Status ERP: Lorem ipsum… - Última Alteração:  —/—/— 00:00 - Categoria: Normal",
                        expression:
                          "'Status ERP: Lorem ipsum… - Última Alteração:  —/—/— 00:00 - Categoria: Normal'"
                      }
                    ]
                  },
                  [_c("i", [_vm._v(_vm._s(_vm.labelInfoBot))])]
                )
              : _vm._e()
          ])
        ],
        2
      ),
      _vm._v(" "),
      _vm.collectionBorgList
        ? _c("div", { staticClass: "collectionBorgList" }, [
            _c(
              "div",
              _vm._l(_vm.collectionBorgList, function(element, index) {
                return _c("div", { key: index }, [
                  _c(
                    "label",
                    {
                      directives: [
                        {
                          name: "me-tooltip",
                          rawName: "v-me-tooltip",
                          value: element.label,
                          expression: "element.label"
                        }
                      ]
                    },
                    [_vm._v(_vm._s(element.label))]
                  ),
                  _vm._v(" "),
                  _c(
                    "span",
                    {
                      directives: [
                        {
                          name: "me-tooltip",
                          rawName: "v-me-tooltip",
                          value: element.value,
                          expression: "element.value"
                        }
                      ]
                    },
                    [_vm._v(_vm._s(element.value))]
                  )
                ])
              }),
              0
            )
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("div", { staticClass: "expand-page" }, [
        _c(
          "span",
          {
            directives: [
              {
                name: "me-tooltip",
                rawName: "v-me-tooltip",
                value: _vm.expandText,
                expression: "expandText"
              }
            ]
          },
          [
            _c("a", { attrs: { href: _vm.expandLink, target: "_blank" } }, [
              _c("i", { staticClass: "me-icon icon-external-link-square" })
            ])
          ]
        )
      ]),
      _vm._v(" "),
      _vm.collectionInfo
        ? _c(
            "div",
            { staticClass: "collectionInfo" },
            _vm._l(_vm.collectionInfo, function(element, index) {
              return _c("div", { key: index }, [
                _c(
                  "span",
                  {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value: element.label,
                        expression: "element.label"
                      }
                    ],
                    style: { color: element.color }
                  },
                  [
                    _c("span", {
                      staticClass: "bulletMobile",
                      style: { background: element.color }
                    }),
                    _vm._v(_vm._s(element.label))
                  ]
                )
              ])
            }),
            0
          )
        : _vm._e()
    ])
  ])
};
var __vue_staticRenderFns__$1d = [];
__vue_render__$1d._withStripped = true;

  /* style */
  const __vue_inject_styles__$1d = function (inject) {
    if (!inject) return
    inject("data-v-4b91fde1_0", { source: ".me-doc-header[data-v-4b91fde1] {\n  display: flex;\n  flex-wrap: nowrap;\n  font-size: 14px;\n  position: relative;\n}\n.me-doc-header i[data-v-4b91fde1] {\n  color: rgba(0, 0, 0, 0.48);\n  font-size: 14px;\n  font-style: italic;\n}\n.me-doc-header_badge[data-v-4b91fde1] {\n  background-color: var(--me-primary-2);\n  border-radius: 0 8px 8px 0;\n  box-shadow: var(--me-shadow-level-2);\n  box-sizing: border-box;\n  color: var(--me-white);\n  height: 90px;\n  margin-top: -4px;\n  padding: 10px 16px;\n  width: 200px;\n}\n.me-doc-header_badge h3[data-v-4b91fde1] {\n  font-size: 18px;\n  font-weight: 600;\n  letter-spacing: 1px;\n  margin: 0;\n}\n.me-doc-header_badge p[data-v-4b91fde1] {\n  font-weight: bold;\n  margin: 0;\n  white-space: nowrap;\n}\n.me-doc-header_badge p.process-number[data-v-4b91fde1] {\n  font-size: 22px;\n}\n.me-doc-header_badge p.client-number[data-v-4b91fde1] {\n  font-size: 12px;\n  font-style: italic;\n}\n.me-doc-header_badge p.font-size-22[data-v-4b91fde1] {\n  font-size: 22px;\n}\n.me-doc-header_badge p.font-size-21[data-v-4b91fde1] {\n  font-size: 21px;\n}\n.me-doc-header_badge p.font-size-10[data-v-4b91fde1] {\n  font-size: 10px;\n}\n.me-doc-header_content[data-v-4b91fde1] {\n  display: flex;\n  flex-wrap: nowrap;\n  justify-content: space-between;\n  padding: 0 0 10px 24px;\n  position: relative;\n  width: 100%;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content[data-v-4b91fde1] {\n    padding: 0 0 0 24px;\n    width: 90%;\n}\n}\n.me-doc-header_content .expand-page[data-v-4b91fde1] {\n  display: none;\n}\n.me-doc-header_content .expand-page a[data-v-4b91fde1] {\n  cursor: pointer;\n}\n.me-doc-header_content .expand-page i[data-v-4b91fde1] {\n  color: var(--me-primary-2);\n  font-size: 18px;\n  font-style: normal;\n  padding-left: 8px;\n  padding-top: 16px;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .expand-page[data-v-4b91fde1] {\n    display: inline-block;\n}\n}\n.me-doc-header_content .collectionData[data-v-4b91fde1] {\n  padding-right: 34px;\n  width: 60%;\n}\n@media (max-width: 850px) {\n.me-doc-header_content .collectionData[data-v-4b91fde1] {\n    width: 40%;\n}\n}\n@media (max-width: 1100px) {\n.me-doc-header_content .collectionData[data-v-4b91fde1] {\n    width: 50%;\n}\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .collectionData[data-v-4b91fde1] {\n    padding-right: 20px;\n}\n}\n.me-doc-header_content .collectionData span.title-purchase[data-v-4b91fde1] {\n  display: block;\n  font-size: 24px;\n  font-weight: 600;\n  line-height: 26px;\n  margin-bottom: 10px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .collectionData span.title-purchase[data-v-4b91fde1] {\n    line-height: 20px;\n    margin-bottom: 7px;\n}\n}\n.me-doc-header_content .collectionData div[data-v-4b91fde1] {\n  display: block;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n.me-doc-header_content .collectionBorgList[data-v-4b91fde1] {\n  margin-top: 10px;\n  padding: 0 80px 0 0;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .collectionBorgList[data-v-4b91fde1] {\n    border-left: none;\n    margin-top: 0;\n    padding: 16px 12px 0 0;\n}\n}\n.me-doc-header_content .collectionBorgList > div[data-v-4b91fde1] {\n  border-left: solid 1px rgba(0, 0, 0, 0.06);\n  padding-left: 34px;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .collectionBorgList > div[data-v-4b91fde1] {\n    padding-right: 24px;\n}\n}\n.me-doc-header_content .collectionBorgList > div > div[data-v-4b91fde1] {\n  display: flex;\n  flex-direction: column;\n  margin-top: 1rem;\n}\n.me-doc-header_content .collectionBorgList > div > div[data-v-4b91fde1]:first-child {\n  margin-top: 0;\n}\n@media (max-width: 11460px) {\n.me-doc-header_content .collectionBorgList > div > div[data-v-4b91fde1] {\n    border-bottom: 1px solid rgba(0, 0, 0, 0.12);\n    flex-direction: inherit;\n    margin-top: 0.6rem;\n}\n}\n.me-doc-header_content .collectionBorgList span[data-v-4b91fde1] {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  width: 120px;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .collectionBorgList span[data-v-4b91fde1] {\n    font-size: 12px;\n}\n}\n.me-doc-header_content .collectionBorgList label[data-v-4b91fde1] {\n  color: #919191;\n  font-size: 12px;\n  margin-right: 8px;\n  margin: 0;\n  text-transform: uppercase;\n}\n@media (max-width: 1460px) {\n.me-doc-header_content .collectionBorgList label[data-v-4b91fde1] {\n    display: block;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    text-transform: capitalize;\n    white-space: nowrap;\n    width: 75px;\n}\n}\n.me-doc-header .collectionInfo[data-v-4b91fde1] {\n  border-left: solid 1px rgba(0, 0, 0, 0.06);\n  font-size: 16px;\n  margin-top: 10px;\n  padding: 0 42px 0 24px;\n}\n@media (max-width: 1460px) {\n.me-doc-header .collectionInfo[data-v-4b91fde1] {\n    border-left: none;\n    bottom: 0;\n    display: flex;\n    font-size: 12px;\n    left: 0;\n    position: absolute;\n}\n}\n.me-doc-header .collectionInfo > div:first-child span[data-v-4b91fde1] {\n  padding-top: 0;\n}\n.me-doc-header .collectionInfo > div span[data-v-4b91fde1] {\n  box-shadow: var(--me-shadow-level-1);\n  display: block;\n  overflow: hidden;\n  padding: 0.95rem 0;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  width: 120px;\n}\n.me-doc-header .collectionInfo > div span span.bulletMobile[data-v-4b91fde1] {\n  display: none;\n}\n@media (max-width: 1460px) {\n.me-doc-header .collectionInfo > div span span.bulletMobile[data-v-4b91fde1] {\n    background-color: var(--me-danger-2);\n    border-radius: 8px;\n    display: inline-block;\n    display: inline-block;\n    height: 6px;\n    margin-bottom: 1px;\n    margin-right: 4px;\n    width: 6px;\n}\n}\n@media (max-width: 1460px) {\n.me-doc-header .collectionInfo > div span[data-v-4b91fde1] {\n    box-shadow: none;\n    padding: 0;\n}\n}\n\n/*# sourceMappingURL=DocHeader.vue.map */", map: {"version":3,"sources":["/app/build/src/components/DocHeader.vue","DocHeader.vue"],"names":[],"mappings":"AAqHA;EACA,aAAA;EACA,iBAAA;EACA,eAAA;EACA,kBAAA;ACpHA;ADsHA;EACA,0BAAA;EACA,eAAA;EACA,kBAAA;ACpHA;ADuHA;EACA,qCAAA;EACA,0BAAA;EACA,oCAAA;EACA,sBAAA;EACA,sBAAA;EACA,YAAA;EACA,gBAAA;EACA,kBAAA;EACA,YAAA;ACrHA;ADuHA;EACA,eAAA;EACA,gBAAA;EACA,mBAAA;EACA,SAAA;ACrHA;ADwHA;EACA,iBAAA;EACA,SAAA;EACA,mBAAA;ACtHA;ADwHA;EACA,eAAA;ACtHA;ADyHA;EACA,eAAA;EACA,kBAAA;ACvHA;AD0HA;EACA,eAAA;ACxHA;AD2HA;EACA,eAAA;ACzHA;AD4HA;EACA,eAAA;AC1HA;ADgIA;EACA,aAAA;EACA,iBAAA;EACA,8BAAA;EACA,sBAAA;EACA,kBAAA;EACA,WAAA;AC9HA;ADgIA;AARA;IASA,mBAAA;IACA,UAAA;AC7HE;AACF;AD+HA;EACA,aAAA;AC7HA;AD+HA;EACA,eAAA;AC7HA;ADgIA;EACA,0BAAA;EACA,eAAA;EACA,kBAAA;EACA,iBAAA;EACA,iBAAA;AC9HA;ADiIA;AAfA;IAgBA,qBAAA;AC9HE;AACF;ADiIA;EACA,mBAAA;EACA,UAAA;AC/HA;ADiIA;AAJA;IAKA,UAAA;AC9HE;AACF;ADgIA;AARA;IASA,UAAA;AC7HE;AACF;AD+HA;AAZA;IAaA,mBAAA;AC5HE;AACF;AD8HA;EACA,cAAA;EACA,eAAA;EACA,gBAAA;EACA,iBAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;AC5HA;AD8HA;AAVA;IAWA,iBAAA;IACA,kBAAA;AC3HE;AACF;AD8HA;EACA,cAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;AC5HA;ADgIA;EACA,gBAAA;EACA,mBAAA;AC9HA;ADgIA;AAJA;IAKA,iBAAA;IACA,aAAA;IACA,sBAAA;AC7HE;AACF;AD+HA;EACA,0CAAA;EACA,kBAAA;AC7HA;AD+HA;AAJA;IAKA,mBAAA;AC5HE;AACF;AD8HA;EACA,aAAA;EACA,sBAAA;EACA,gBAAA;AC5HA;AD8HA;EACA,aAAA;AC5HA;AD+HA;AATA;IAUA,4CAAA;IACA,uBAAA;IACA,kBAAA;AC5HE;AACF;ADgIA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;EACA,YAAA;AC9HA;ADgIA;AANA;IAOA,eAAA;AC7HE;AACF;ADgIA;EACA,cAAA;EACA,eAAA;EACA,iBAAA;EACA,SAAA;EACA,yBAAA;AC9HA;ADgIA;AAPA;IAQA,cAAA;IACA,gBAAA;IACA,uBAAA;IACA,0BAAA;IACA,mBAAA;IACA,WAAA;AC7HE;AACF;ADkIA;EACA,0CAAA;EACA,eAAA;EACA,gBAAA;EACA,sBAAA;AChIA;ADkIA;AANA;IAOA,iBAAA;IACA,SAAA;IACA,aAAA;IACA,eAAA;IACA,OAAA;IACA,kBAAA;AC/HE;AACF;ADmIA;EACA,cAAA;ACjIA;ADqIA;EACA,oCAAA;EACA,cAAA;EACA,gBAAA;EACA,kBAAA;EACA,uBAAA;EACA,mBAAA;EACA,YAAA;ACnIA;ADqIA;EACA,aAAA;ACnIA;ADqIA;AAHA;IAIA,oCAAA;IACA,kBAAA;IACA,qBAAA;IACA,qBAAA;IACA,WAAA;IACA,kBAAA;IACA,iBAAA;IACA,UAAA;AClIE;AACF;ADqIA;AAxBA;IAyBA,gBAAA;IACA,UAAA;AClIE;AACF;;AAEA,wCAAwC","file":"DocHeader.vue","sourcesContent":["<template>\n  <div class=\"me-doc-header\">\n    <section class=\"me-doc-header_badge\">\n      <h3>{{ process }}</h3>\n      <slot name=\"number\">\n        <p\n          v-if=\"number\"\n          :class=\"setFontSizeByNumberLenght(number.length)\"\n          class=\"process-number\"\n        >\n          {{ number }}\n        </p>\n      </slot>\n      <slot name=\"clientNumber\">\n        <p v-if=\"clientNumber\" class=\"client-number\">\n          {{ clientNumber }}\n        </p>\n      </slot>\n    </section>\n    <section class=\"me-doc-header_content\">\n      <div class=\"collectionData\">\n        <span v-me-tooltip=\"title\" class=\"title-purchase\">{{ title | me-cut-text(20) }}</span>\n        <slot name=\"labelInfoSup\">\n          <div v-if=\"labelInfoSup\">\n            <i>{{ labelInfoSup }}</i>\n          </div>\n        </slot>\n        <slot name=\"labelInfoBot\">\n          <div v-if=\"labelInfoBot\" v-me-tooltip=\"'Status ERP: Lorem ipsum… - Última Alteração:  —/—/— 00:00 - Categoria: Normal'\">\n            <i>{{ labelInfoBot }}</i>\n          </div>\n        </slot>\n      </div>\n      <div v-if=\"collectionBorgList\" class=\"collectionBorgList\">\n        <div>\n          <div v-for=\"(element, index) in collectionBorgList\" :key=\"index\">\n            <label v-me-tooltip=\"element.label\">{{ element.label }}</label>\n            <span v-me-tooltip=\"element.value\">{{ element.value }}</span>\n          </div>\n        </div>\n      </div>\n      <div class=\"expand-page\">\n        <span v-me-tooltip=\"expandText\">\n          <a :href=\"expandLink\" target=\"_blank\"><i class=\"me-icon icon-external-link-square\" /></a>\n        </span>\n      </div>\n      <div v-if=\"collectionInfo\" class=\"collectionInfo\">\n        <div v-for=\"(element, index) in collectionInfo\" :key=\"index\">\n          <span v-me-tooltip=\"element.label\" :style=\"{ color: element.color }\"><span :style=\"{ background: element.color }\" class=\"bulletMobile\" />{{ element.label }}</span>\n        </div>\n      </div>\n    </section>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeDocHeader',\n  props: {\n    process: {\n      type: String,\n      required: true\n    },\n    number: {\n      type: String,\n      required: true\n    },\n    clientNumber: {\n      type: String,\n      required: true\n    },\n    title: {\n      type: String,\n      required: true\n    },\n    labelInfoSup: {\n      type: String,\n      default: null\n    },\n    labelInfoBot: {\n      type: String,\n      default: null\n    },\n    collectionInfo: {\n      type: Array,\n      default: null\n    },\n    collectionBorgList: {\n      type: Array,\n      default: null\n    },\n    expandText: {\n      type: String,\n      required: true,\n      default: null\n    },\n    expandLink: {\n      type: String,\n      default: ''\n    }\n  },\n\n  methods: {\n    setFontSizeByNumberLenght(length) {\n      if (length === 14) {\n        return 'font-size-21'\n      }\n\n      if (length >= 15) {\n        return 'font-size-10'\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-doc-header {\n      display: flex;\n      flex-wrap: nowrap;\n      font-size: 14px;\n      position: relative;\n\n      i {\n        color: rgba(0, 0, 0, 0.48);\n        font-size: 14px;\n        font-style: italic;\n      }\n\n      &_badge {\n        background-color: var(--me-primary-2);\n        border-radius: 0 8px 8px 0;\n        box-shadow: var(--me-shadow-level-2);\n        box-sizing: border-box;\n        color: var(--me-white);\n        height: 90px;\n        margin-top: -4px;\n        padding: 10px 16px;\n        width: 200px;\n\n        h3 {\n          font-size: 18px;\n          font-weight: 600;\n          letter-spacing: 1px;\n          margin: 0;\n        }\n\n        p {\n          font-weight: bold;\n          margin: 0;\n          white-space: nowrap;\n\n          &.process-number {\n            font-size: 22px;\n          }\n\n          &.client-number {\n            font-size: 12px;\n            font-style: italic;\n          }\n\n          &.font-size-22 {\n            font-size: 22px;\n          }\n\n          &.font-size-21 {\n            font-size: 21px;\n          }\n\n          &.font-size-10 {\n            font-size: 10px;\n          }\n        }\n\n      }\n\n      &_content {\n        display: flex;\n        flex-wrap: nowrap;\n        justify-content: space-between;\n        padding: 0 0 10px 24px;\n        position: relative;\n        width: 100%;\n\n        @media (max-width: 1460px){\n          padding: 0 0 0 24px;\n          width: 90%\n        }\n\n        .expand-page {\n          display: none;\n\n          a {\n            cursor: pointer;\n          }\n\n          i {\n            color: var(--me-primary-2);\n            font-size: 18px;\n            font-style: normal;\n            padding-left: 8px;\n            padding-top: 16px;\n          }\n\n          @media (max-width: 1460px) {\n            display: inline-block;\n          }\n        }\n\n        .collectionData {\n          padding-right: 34px;\n          width: 60%;\n\n          @media(max-width:850px) {\n            width: 40%;\n          }\n\n          @media(max-width:1100px) {\n            width: 50%;\n          }\n\n          @media (max-width: 1460px) {\n            padding-right: 20px;\n          }\n\n          span.title-purchase {\n            display: block;\n            font-size: 24px;\n            font-weight: 600;\n            line-height: 26px;\n            margin-bottom: 10px;\n            overflow: hidden;\n            text-overflow: ellipsis;\n            white-space: nowrap;\n\n            @media(max-width:1460px) {\n              line-height: 20px;\n              margin-bottom: 7px;\n            }\n          }\n\n          div {\n            display: block;\n            overflow: hidden;\n            text-overflow: ellipsis;\n            white-space: nowrap;\n          }\n        }\n\n        .collectionBorgList {\n          margin-top: 10px;\n          padding: 0 80px 0 0;\n\n          @media (max-width: 1460px){\n            border-left: none;\n            margin-top: 0;\n            padding: 16px 12px 0 0;\n          }\n\n          & > div {\n            border-left: solid 1px rgba(0, 0, 0, 0.06);\n            padding-left: 34px;\n\n            @media (max-width: 1460px){\n              padding-right: 24px;\n            }\n\n            & > div {\n              display: flex;\n              flex-direction: column;\n              margin-top: 1rem;\n\n              &:first-child {\n                margin-top: 0;\n              }\n\n              @media (max-width: 11460px) {\n                border-bottom: 1px solid rgba(0, 0, 0, 0.12);\n                flex-direction: inherit;\n                margin-top: 0.6rem;\n              }\n            }\n          }\n\n          span {\n            overflow: hidden;\n            text-overflow: ellipsis;\n            white-space: nowrap;\n            width: 120px;\n\n            @media(max-width: 1460px) {\n              font-size: 12px;\n            }\n          }\n\n          label {\n            color: #919191;\n            font-size: 12px;\n            margin-right: 8px;\n            margin: 0;\n            text-transform: uppercase;\n\n            @media(max-width: 1460px) {\n              display: block;\n              overflow: hidden;\n              text-overflow: ellipsis;\n              text-transform: capitalize;\n              white-space: nowrap;\n              width: 75px;\n            }\n          }\n        }\n      }\n\n      .collectionInfo {\n        border-left: solid 1px rgba(0, 0, 0, 0.06);\n        font-size: 16px;\n        margin-top: 10px;\n        padding: 0 42px 0 24px;\n\n        @media(max-width: 1460px) {\n          border-left: none;\n          bottom: 0;\n          display: flex;\n          font-size: 12px;\n          left: 0;\n          position: absolute;\n        }\n\n        & > div {\n          &:first-child {\n            span {\n              padding-top: 0;\n            }\n          }\n\n        span {\n          box-shadow: var(--me-shadow-level-1);\n          display: block;\n          overflow: hidden;\n          padding: .95rem 0;\n          text-overflow: ellipsis;\n          white-space: nowrap;\n          width: 120px;\n\n          span.bulletMobile {\n            display: none;\n\n            @media(max-width: 1460px) {\n              background-color: var(--me-danger-2);\n              border-radius: 8px;\n              display: inline-block;\n              display: inline-block;\n              height: 6px;\n              margin-bottom: 1px;\n              margin-right: 4px;\n              width: 6px;\n            }\n          }\n\n          @media(max-width: 1460px) {\n            box-shadow: none;\n            padding: 0;\n          }\n        }\n      }\n    }\n  }\n</style>\n",".me-doc-header {\n  display: flex;\n  flex-wrap: nowrap;\n  font-size: 14px;\n  position: relative;\n}\n.me-doc-header i {\n  color: rgba(0, 0, 0, 0.48);\n  font-size: 14px;\n  font-style: italic;\n}\n.me-doc-header_badge {\n  background-color: var(--me-primary-2);\n  border-radius: 0 8px 8px 0;\n  box-shadow: var(--me-shadow-level-2);\n  box-sizing: border-box;\n  color: var(--me-white);\n  height: 90px;\n  margin-top: -4px;\n  padding: 10px 16px;\n  width: 200px;\n}\n.me-doc-header_badge h3 {\n  font-size: 18px;\n  font-weight: 600;\n  letter-spacing: 1px;\n  margin: 0;\n}\n.me-doc-header_badge p {\n  font-weight: bold;\n  margin: 0;\n  white-space: nowrap;\n}\n.me-doc-header_badge p.process-number {\n  font-size: 22px;\n}\n.me-doc-header_badge p.client-number {\n  font-size: 12px;\n  font-style: italic;\n}\n.me-doc-header_badge p.font-size-22 {\n  font-size: 22px;\n}\n.me-doc-header_badge p.font-size-21 {\n  font-size: 21px;\n}\n.me-doc-header_badge p.font-size-10 {\n  font-size: 10px;\n}\n.me-doc-header_content {\n  display: flex;\n  flex-wrap: nowrap;\n  justify-content: space-between;\n  padding: 0 0 10px 24px;\n  position: relative;\n  width: 100%;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content {\n    padding: 0 0 0 24px;\n    width: 90%;\n  }\n}\n.me-doc-header_content .expand-page {\n  display: none;\n}\n.me-doc-header_content .expand-page a {\n  cursor: pointer;\n}\n.me-doc-header_content .expand-page i {\n  color: var(--me-primary-2);\n  font-size: 18px;\n  font-style: normal;\n  padding-left: 8px;\n  padding-top: 16px;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .expand-page {\n    display: inline-block;\n  }\n}\n.me-doc-header_content .collectionData {\n  padding-right: 34px;\n  width: 60%;\n}\n@media (max-width: 850px) {\n  .me-doc-header_content .collectionData {\n    width: 40%;\n  }\n}\n@media (max-width: 1100px) {\n  .me-doc-header_content .collectionData {\n    width: 50%;\n  }\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .collectionData {\n    padding-right: 20px;\n  }\n}\n.me-doc-header_content .collectionData span.title-purchase {\n  display: block;\n  font-size: 24px;\n  font-weight: 600;\n  line-height: 26px;\n  margin-bottom: 10px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .collectionData span.title-purchase {\n    line-height: 20px;\n    margin-bottom: 7px;\n  }\n}\n.me-doc-header_content .collectionData div {\n  display: block;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n.me-doc-header_content .collectionBorgList {\n  margin-top: 10px;\n  padding: 0 80px 0 0;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .collectionBorgList {\n    border-left: none;\n    margin-top: 0;\n    padding: 16px 12px 0 0;\n  }\n}\n.me-doc-header_content .collectionBorgList > div {\n  border-left: solid 1px rgba(0, 0, 0, 0.06);\n  padding-left: 34px;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .collectionBorgList > div {\n    padding-right: 24px;\n  }\n}\n.me-doc-header_content .collectionBorgList > div > div {\n  display: flex;\n  flex-direction: column;\n  margin-top: 1rem;\n}\n.me-doc-header_content .collectionBorgList > div > div:first-child {\n  margin-top: 0;\n}\n@media (max-width: 11460px) {\n  .me-doc-header_content .collectionBorgList > div > div {\n    border-bottom: 1px solid rgba(0, 0, 0, 0.12);\n    flex-direction: inherit;\n    margin-top: 0.6rem;\n  }\n}\n.me-doc-header_content .collectionBorgList span {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  width: 120px;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .collectionBorgList span {\n    font-size: 12px;\n  }\n}\n.me-doc-header_content .collectionBorgList label {\n  color: #919191;\n  font-size: 12px;\n  margin-right: 8px;\n  margin: 0;\n  text-transform: uppercase;\n}\n@media (max-width: 1460px) {\n  .me-doc-header_content .collectionBorgList label {\n    display: block;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    text-transform: capitalize;\n    white-space: nowrap;\n    width: 75px;\n  }\n}\n.me-doc-header .collectionInfo {\n  border-left: solid 1px rgba(0, 0, 0, 0.06);\n  font-size: 16px;\n  margin-top: 10px;\n  padding: 0 42px 0 24px;\n}\n@media (max-width: 1460px) {\n  .me-doc-header .collectionInfo {\n    border-left: none;\n    bottom: 0;\n    display: flex;\n    font-size: 12px;\n    left: 0;\n    position: absolute;\n  }\n}\n.me-doc-header .collectionInfo > div:first-child span {\n  padding-top: 0;\n}\n.me-doc-header .collectionInfo > div span {\n  box-shadow: var(--me-shadow-level-1);\n  display: block;\n  overflow: hidden;\n  padding: 0.95rem 0;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  width: 120px;\n}\n.me-doc-header .collectionInfo > div span span.bulletMobile {\n  display: none;\n}\n@media (max-width: 1460px) {\n  .me-doc-header .collectionInfo > div span span.bulletMobile {\n    background-color: var(--me-danger-2);\n    border-radius: 8px;\n    display: inline-block;\n    display: inline-block;\n    height: 6px;\n    margin-bottom: 1px;\n    margin-right: 4px;\n    width: 6px;\n  }\n}\n@media (max-width: 1460px) {\n  .me-doc-header .collectionInfo > div span {\n    box-shadow: none;\n    padding: 0;\n  }\n}\n\n/*# sourceMappingURL=DocHeader.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1d = "data-v-4b91fde1";
  /* module identifier */
  const __vue_module_identifier__$1d = undefined;
  /* functional template */
  const __vue_is_functional_template__$1d = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1d, staticRenderFns: __vue_staticRenderFns__$1d },
    __vue_inject_styles__$1d,
    __vue_script__$1d,
    __vue_scope_id__$1d,
    __vue_is_functional_template__$1d,
    __vue_module_identifier__$1d,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1e = {
  name: 'MeDownloadStatus',

  components: {
    Icon: __vue_component__
  },

  props: {
    label: {
      type: String,
      required: true
    },
    error: {
      type: String,
      default: ''
    },
    opened: {
      type: Boolean,
      default: false
    },
    canToggle: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    getStatusIcon() {
      return this.error ? 'circle-exclamation' : 'loader'
    },

    toggleIcon() {
      return this.opened ? 'up' : 'down'
    }
  },

  methods: {
    toggleCollapse() {
      this.$emit('update:opened', !this.opened);
    }
  }
};

/* script */
const __vue_script__$1e = script$1e;

/* template */
var __vue_render__$1e = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-download-status",
      class: _vm.error ? "download-failure" : "download-progress",
      attrs: { "data-jest": "download-status" }
    },
    [
      _c(
        "div",
        { staticClass: "me-download-status__status-load" },
        [
          _c("Icon", {
            attrs: { icon: "me-icon-l icon-" + _vm.getStatusIcon }
          }),
          _vm._v(" "),
          _c(
            "span",
            {
              staticClass: "me-download-status__status-load__title",
              attrs: { "data-jest": "download-status-label" }
            },
            [_vm._v(_vm._s(_vm.error || _vm.label))]
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "me-download-status__actions" },
        [
          _vm.canToggle
            ? _c("Icon", {
                attrs: { icon: "me-icon-l icon-chevron-" + _vm.toggleIcon },
                on: { click: _vm.toggleCollapse }
              })
            : _vm._e(),
          _vm._v(" "),
          _vm.error
            ? _c("Icon", {
                attrs: { icon: "me-icon-l icon-xmark" },
                on: {
                  click: function($event) {
                    return _vm.$emit("hide")
                  }
                }
              })
            : _vm._e()
        ],
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$1e = [];
__vue_render__$1e._withStripped = true;

  /* style */
  const __vue_inject_styles__$1e = function (inject) {
    if (!inject) return
    inject("data-v-921e6fbe_0", { source: ".me-download-status[data-v-921e6fbe] {\n  width: 100%;\n  height: 56px;\n  border-radius: 4px 4px 0 0;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding: 16px;\n}\n.me-download-status__status-load__title[data-v-921e6fbe] {\n  font-weight: 600;\n  font-size: 16px;\n  margin-left: 8px;\n}\n.me-download-status__actions[data-v-921e6fbe] {\n  display: flex;\n  gap: 16px;\n}\n.me-download-status__actions i[data-v-921e6fbe] {\n  cursor: pointer;\n}\n.download-progress[data-v-921e6fbe] {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-2);\n}\n.download-failure[data-v-921e6fbe] {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-2);\n}\n.icon-loader[data-v-921e6fbe] {\n  animation: spinner-data-v-921e6fbe 2s linear infinite;\n}\n@keyframes spinner-data-v-921e6fbe {\nto {\n    transform: rotate(360deg);\n}\n}\n\n/*# sourceMappingURL=DownloadStatus.vue.map */", map: {"version":3,"sources":["/app/build/src/components/download/DownloadStatus.vue","DownloadStatus.vue"],"names":[],"mappings":"AAwEA;EACA,WAAA;EACA,YAAA;EACA,0BAAA;EACA,aAAA;EACA,mBAAA;EACA,8BAAA;EACA,aAAA;ACvEA;AD0EA;EACA,gBAAA;EACA,eAAA;EACA,gBAAA;ACxEA;AD4EA;EACA,aAAA;EACA,SAAA;AC1EA;AD4EA;EACA,eAAA;AC1EA;ADoFA;EAJA,qCAAA;EACA,0BAAA;AC5EA;ADmFA;EARA,oCAAA;EACA,yBAAA;ACvEA;ADkFA;EACA,qDAAA;AC/EA;ADkFA;AACA;IACA,yBAAA;AC/EE;AACF;;AAEA,6CAA6C","file":"DownloadStatus.vue","sourcesContent":["<template>\n  <div :class=\"error ? 'download-failure' : 'download-progress'\" class=\"me-download-status\" data-jest=\"download-status\">\n    <div class=\"me-download-status__status-load\">\n      <Icon :icon=\"`me-icon-l icon-${getStatusIcon}`\" />\n\n      <span class=\"me-download-status__status-load__title\" data-jest=\"download-status-label\">{{ error || label }}</span>\n    </div>\n\n    <div class=\"me-download-status__actions\">\n      <Icon\n        v-if=\"canToggle\"\n        :icon=\"`me-icon-l icon-chevron-${toggleIcon}`\"\n        @click=\"toggleCollapse\"\n      />\n\n      <Icon\n        v-if=\"error\"\n        @click=\"$emit('hide')\"\n        icon=\"me-icon-l icon-xmark\"\n      />\n    </div>\n  </div>\n</template>\n\n<script>\nimport Icon from '../Icon.vue'\n\nexport default {\n  name: 'MeDownloadStatus',\n\n  components: {\n    Icon\n  },\n\n  props: {\n    label: {\n      type: String,\n      required: true\n    },\n    error: {\n      type: String,\n      default: ''\n    },\n    opened: {\n      type: Boolean,\n      default: false\n    },\n    canToggle: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  computed: {\n    getStatusIcon() {\n      return this.error ? 'circle-exclamation' : 'loader'\n    },\n\n    toggleIcon() {\n      return this.opened ? 'up' : 'down'\n    }\n  },\n\n  methods: {\n    toggleCollapse() {\n      this.$emit('update:opened', !this.opened)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-download-status {\n    width: 100%;\n    height: 56px;\n    border-radius: 4px 4px 0 0;\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    padding: 16px;\n\n    &__status-load {\n      &__title {\n        font-weight: 600;\n        font-size: 16px;\n        margin-left: 8px;\n      }\n    }\n\n    &__actions {\n      display: flex;\n      gap: 16px;\n\n      i {\n        cursor: pointer;\n      }\n    }\n  }\n\n  @mixin status($variant) {\n    background-color: var(--me-#{$variant}-7);\n    color: var(--me-#{$variant}-2);\n  }\n\n  .download-progress {\n    @include status('primary');\n  }\n\n  .download-failure {\n    @include status('danger');\n  }\n\n  .icon-loader {\n    animation: spinner 2s linear infinite;\n  }\n\n  @keyframes spinner {\n    to {\n      transform: rotate(360deg);\n    }\n  }\n</style>\n",".me-download-status {\n  width: 100%;\n  height: 56px;\n  border-radius: 4px 4px 0 0;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding: 16px;\n}\n.me-download-status__status-load__title {\n  font-weight: 600;\n  font-size: 16px;\n  margin-left: 8px;\n}\n.me-download-status__actions {\n  display: flex;\n  gap: 16px;\n}\n.me-download-status__actions i {\n  cursor: pointer;\n}\n\n.download-progress {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-2);\n}\n\n.download-failure {\n  background-color: var(--me-danger-7);\n  color: var(--me-danger-2);\n}\n\n.icon-loader {\n  animation: spinner 2s linear infinite;\n}\n\n@keyframes spinner {\n  to {\n    transform: rotate(360deg);\n  }\n}\n\n/*# sourceMappingURL=DownloadStatus.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1e = "data-v-921e6fbe";
  /* module identifier */
  const __vue_module_identifier__$1e = undefined;
  /* functional template */
  const __vue_is_functional_template__$1e = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1e, staticRenderFns: __vue_staticRenderFns__$1e },
    __vue_inject_styles__$1e,
    __vue_script__$1e,
    __vue_scope_id__$1e,
    __vue_is_functional_template__$1e,
    __vue_module_identifier__$1e,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1f = {
  name: 'MeDownload',

  components: {
    DownloadStatus: __vue_component__$1e
  },

  props: {
    label: {
      type: String,
      required: true
    },
    fileName: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      required: false
    },
    error: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isOpened: false
    }
  },

  computed: {
    canToggle() {
      return !!this.fileName
    }
  },

  methods: {
    hide() {
      this.$emit('hide');
      this.$emit('update:visible', !this.visible);
    }
  }
};

/* script */
const __vue_script__$1f = script$1f;

/* template */
var __vue_render__$1f = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.visible
    ? _c(
        "div",
        { staticClass: "me-download" },
        [
          _c("DownloadStatus", {
            attrs: {
              label: _vm.label,
              error: _vm.error,
              opened: _vm.isOpened,
              canToggle: _vm.canToggle
            },
            on: {
              "update:opened": function($event) {
                _vm.isOpened = $event;
              },
              hide: _vm.hide
            }
          }),
          _vm._v(" "),
          _c("transition", { attrs: { name: "content-transition" } }, [
            _vm.isOpened && _vm.fileName
              ? _c(
                  "div",
                  {
                    staticClass: "file-content",
                    attrs: { "data-jest": "download-content" }
                  },
                  [_vm._v("\n      " + _vm._s(_vm.fileName) + "\n    ")]
                )
              : _vm._e()
          ])
        ],
        1
      )
    : _vm._e()
};
var __vue_staticRenderFns__$1f = [];
__vue_render__$1f._withStripped = true;

  /* style */
  const __vue_inject_styles__$1f = function (inject) {
    if (!inject) return
    inject("data-v-79b9c33b_0", { source: ".me-download[data-v-79b9c33b] {\n  width: 328px;\n  background-color: var(--me-white);\n  position: fixed;\n  bottom: 15px;\n  right: 15px;\n  z-index: 999;\n  box-shadow: var(--me-shadow-level-1);\n}\n.me-download .file-content[data-v-79b9c33b] {\n  background-color: var(--me-white);\n  padding: 16px;\n}\n.content-transition-enter-active[data-v-79b9c33b],\n.content-transition-leave-active[data-v-79b9c33b] {\n  transition: all 0.3s;\n}\n.content-transition-enter[data-v-79b9c33b],\n.content-transition-leave-to[data-v-79b9c33b] {\n  transform: translateY(-5px);\n  opacity: 0;\n}\n\n/*# sourceMappingURL=Download.vue.map */", map: {"version":3,"sources":["/app/build/src/components/download/Download.vue","Download.vue"],"names":[],"mappings":"AAqEA;EACA,YAAA;EACA,iCAAA;EACA,eAAA;EACA,YAAA;EACA,WAAA;EACA,YAAA;EACA,oCAAA;ACpEA;ADsEA;EACA,iCAAA;EACA,aAAA;ACpEA;ADwEA;;EAEA,oBAAA;ACrEA;ADwEA;;EAEA,2BAAA;EACA,UAAA;ACrEA;;AAEA,uCAAuC","file":"Download.vue","sourcesContent":["<template>\n  <div v-if=\"visible\" class=\"me-download\">\n    <DownloadStatus\n      :label=\"label\"\n      :error=\"error\"\n      :opened.sync=\"isOpened\"\n      :canToggle=\"canToggle\"\n      @hide=\"hide\"\n    />\n\n    <transition name=\"content-transition\">\n      <div v-if=\"isOpened && fileName\" class=\"file-content\" data-jest=\"download-content\">\n        {{ fileName }}\n      </div>\n    </transition>\n  </div>\n</template>\n\n<script>\nimport DownloadStatus from './DownloadStatus.vue'\n\nexport default {\n  name: 'MeDownload',\n\n  components: {\n    DownloadStatus\n  },\n\n  props: {\n    label: {\n      type: String,\n      required: true\n    },\n    fileName: {\n      type: String,\n      default: ''\n    },\n    visible: {\n      type: Boolean,\n      required: false\n    },\n    error: {\n      type: String,\n      default: ''\n    }\n  },\n\n  data() {\n    return {\n      isOpened: false\n    }\n  },\n\n  computed: {\n    canToggle() {\n      return !!this.fileName\n    }\n  },\n\n  methods: {\n    hide() {\n      this.$emit('hide')\n      this.$emit('update:visible', !this.visible)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-download {\n  width: 328px;\n  background-color: var(--me-white);\n  position: fixed;\n  bottom: 15px;\n  right: 15px;\n  z-index: 999;\n  box-shadow: var(--me-shadow-level-1);\n\n  .file-content {\n    background-color: var(--me-white);\n    padding: 16px;\n  }\n}\n\n.content-transition-enter-active,\n.content-transition-leave-active {\n  transition: all .3s;\n}\n\n.content-transition-enter,\n.content-transition-leave-to {\n  transform: translateY(-5px);\n  opacity: 0;\n}\n\n</style>\n",".me-download {\n  width: 328px;\n  background-color: var(--me-white);\n  position: fixed;\n  bottom: 15px;\n  right: 15px;\n  z-index: 999;\n  box-shadow: var(--me-shadow-level-1);\n}\n.me-download .file-content {\n  background-color: var(--me-white);\n  padding: 16px;\n}\n\n.content-transition-enter-active,\n.content-transition-leave-active {\n  transition: all 0.3s;\n}\n\n.content-transition-enter,\n.content-transition-leave-to {\n  transform: translateY(-5px);\n  opacity: 0;\n}\n\n/*# sourceMappingURL=Download.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1f = "data-v-79b9c33b";
  /* module identifier */
  const __vue_module_identifier__$1f = undefined;
  /* functional template */
  const __vue_is_functional_template__$1f = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1f, staticRenderFns: __vue_staticRenderFns__$1f },
    __vue_inject_styles__$1f,
    __vue_script__$1f,
    __vue_scope_id__$1f,
    __vue_is_functional_template__$1f,
    __vue_module_identifier__$1f,
    false,
    createInjector,
    undefined,
    undefined
  );

/** Used to store function metadata. */
var metaMap = _WeakMap && new _WeakMap;

var _metaMap = metaMap;

/**
 * The base implementation of `setData` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to associate metadata with.
 * @param {*} data The metadata.
 * @returns {Function} Returns `func`.
 */
var baseSetData = !_metaMap ? identity_1 : function(func, data) {
  _metaMap.set(func, data);
  return func;
};

var _baseSetData = baseSetData;

/**
 * Creates a function that produces an instance of `Ctor` regardless of
 * whether it was invoked as part of a `new` expression or by `call` or `apply`.
 *
 * @private
 * @param {Function} Ctor The constructor to wrap.
 * @returns {Function} Returns the new wrapped function.
 */
function createCtor(Ctor) {
  return function() {
    // Use a `switch` statement to work with class constructors. See
    // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
    // for more details.
    var args = arguments;
    switch (args.length) {
      case 0: return new Ctor;
      case 1: return new Ctor(args[0]);
      case 2: return new Ctor(args[0], args[1]);
      case 3: return new Ctor(args[0], args[1], args[2]);
      case 4: return new Ctor(args[0], args[1], args[2], args[3]);
      case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
      case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
      case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
    }
    var thisBinding = _baseCreate(Ctor.prototype),
        result = Ctor.apply(thisBinding, args);

    // Mimic the constructor's `return` behavior.
    // See https://es5.github.io/#x13.2.2 for more details.
    return isObject_1(result) ? result : thisBinding;
  };
}

var _createCtor = createCtor;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG = 1;

/**
 * Creates a function that wraps `func` to invoke it with the optional `this`
 * binding of `thisArg`.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createBind(func, bitmask, thisArg) {
  var isBind = bitmask & WRAP_BIND_FLAG,
      Ctor = _createCtor(func);

  function wrapper() {
    var fn = (this && this !== _root && this instanceof wrapper) ? Ctor : func;
    return fn.apply(isBind ? thisArg : this, arguments);
  }
  return wrapper;
}

var _createBind = createBind;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$3 = Math.max;

/**
 * Creates an array that is the composition of partially applied arguments,
 * placeholders, and provided arguments into a single array of arguments.
 *
 * @private
 * @param {Array} args The provided arguments.
 * @param {Array} partials The arguments to prepend to those provided.
 * @param {Array} holders The `partials` placeholder indexes.
 * @params {boolean} [isCurried] Specify composing for a curried function.
 * @returns {Array} Returns the new array of composed arguments.
 */
function composeArgs(args, partials, holders, isCurried) {
  var argsIndex = -1,
      argsLength = args.length,
      holdersLength = holders.length,
      leftIndex = -1,
      leftLength = partials.length,
      rangeLength = nativeMax$3(argsLength - holdersLength, 0),
      result = Array(leftLength + rangeLength),
      isUncurried = !isCurried;

  while (++leftIndex < leftLength) {
    result[leftIndex] = partials[leftIndex];
  }
  while (++argsIndex < holdersLength) {
    if (isUncurried || argsIndex < argsLength) {
      result[holders[argsIndex]] = args[argsIndex];
    }
  }
  while (rangeLength--) {
    result[leftIndex++] = args[argsIndex++];
  }
  return result;
}

var _composeArgs = composeArgs;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$4 = Math.max;

/**
 * This function is like `composeArgs` except that the arguments composition
 * is tailored for `_.partialRight`.
 *
 * @private
 * @param {Array} args The provided arguments.
 * @param {Array} partials The arguments to append to those provided.
 * @param {Array} holders The `partials` placeholder indexes.
 * @params {boolean} [isCurried] Specify composing for a curried function.
 * @returns {Array} Returns the new array of composed arguments.
 */
function composeArgsRight(args, partials, holders, isCurried) {
  var argsIndex = -1,
      argsLength = args.length,
      holdersIndex = -1,
      holdersLength = holders.length,
      rightIndex = -1,
      rightLength = partials.length,
      rangeLength = nativeMax$4(argsLength - holdersLength, 0),
      result = Array(rangeLength + rightLength),
      isUncurried = !isCurried;

  while (++argsIndex < rangeLength) {
    result[argsIndex] = args[argsIndex];
  }
  var offset = argsIndex;
  while (++rightIndex < rightLength) {
    result[offset + rightIndex] = partials[rightIndex];
  }
  while (++holdersIndex < holdersLength) {
    if (isUncurried || argsIndex < argsLength) {
      result[offset + holders[holdersIndex]] = args[argsIndex++];
    }
  }
  return result;
}

var _composeArgsRight = composeArgsRight;

/**
 * Gets the number of `placeholder` occurrences in `array`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} placeholder The placeholder to search for.
 * @returns {number} Returns the placeholder count.
 */
function countHolders(array, placeholder) {
  var length = array.length,
      result = 0;

  while (length--) {
    if (array[length] === placeholder) {
      ++result;
    }
  }
  return result;
}

var _countHolders = countHolders;

/**
 * The function whose prototype chain sequence wrappers inherit from.
 *
 * @private
 */
function baseLodash() {
  // No operation performed.
}

var _baseLodash = baseLodash;

/** Used as references for the maximum length and index of an array. */
var MAX_ARRAY_LENGTH = 4294967295;

/**
 * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
 *
 * @private
 * @constructor
 * @param {*} value The value to wrap.
 */
function LazyWrapper(value) {
  this.__wrapped__ = value;
  this.__actions__ = [];
  this.__dir__ = 1;
  this.__filtered__ = false;
  this.__iteratees__ = [];
  this.__takeCount__ = MAX_ARRAY_LENGTH;
  this.__views__ = [];
}

// Ensure `LazyWrapper` is an instance of `baseLodash`.
LazyWrapper.prototype = _baseCreate(_baseLodash.prototype);
LazyWrapper.prototype.constructor = LazyWrapper;

var _LazyWrapper = LazyWrapper;

/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */
function noop$1() {
  // No operation performed.
}

var noop_1 = noop$1;

/**
 * Gets metadata for `func`.
 *
 * @private
 * @param {Function} func The function to query.
 * @returns {*} Returns the metadata for `func`.
 */
var getData = !_metaMap ? noop_1 : function(func) {
  return _metaMap.get(func);
};

var _getData = getData;

/** Used to lookup unminified function names. */
var realNames = {};

var _realNames = realNames;

/** Used for built-in method references. */
var objectProto$h = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$f = objectProto$h.hasOwnProperty;

/**
 * Gets the name of `func`.
 *
 * @private
 * @param {Function} func The function to query.
 * @returns {string} Returns the function name.
 */
function getFuncName(func) {
  var result = (func.name + ''),
      array = _realNames[result],
      length = hasOwnProperty$f.call(_realNames, result) ? array.length : 0;

  while (length--) {
    var data = array[length],
        otherFunc = data.func;
    if (otherFunc == null || otherFunc == func) {
      return data.name;
    }
  }
  return result;
}

var _getFuncName = getFuncName;

/**
 * The base constructor for creating `lodash` wrapper objects.
 *
 * @private
 * @param {*} value The value to wrap.
 * @param {boolean} [chainAll] Enable explicit method chain sequences.
 */
function LodashWrapper(value, chainAll) {
  this.__wrapped__ = value;
  this.__actions__ = [];
  this.__chain__ = !!chainAll;
  this.__index__ = 0;
  this.__values__ = undefined;
}

LodashWrapper.prototype = _baseCreate(_baseLodash.prototype);
LodashWrapper.prototype.constructor = LodashWrapper;

var _LodashWrapper = LodashWrapper;

/**
 * Creates a clone of `wrapper`.
 *
 * @private
 * @param {Object} wrapper The wrapper to clone.
 * @returns {Object} Returns the cloned wrapper.
 */
function wrapperClone(wrapper) {
  if (wrapper instanceof _LazyWrapper) {
    return wrapper.clone();
  }
  var result = new _LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
  result.__actions__ = _copyArray(wrapper.__actions__);
  result.__index__  = wrapper.__index__;
  result.__values__ = wrapper.__values__;
  return result;
}

var _wrapperClone = wrapperClone;

/** Used for built-in method references. */
var objectProto$i = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$g = objectProto$i.hasOwnProperty;

/**
 * Creates a `lodash` object which wraps `value` to enable implicit method
 * chain sequences. Methods that operate on and return arrays, collections,
 * and functions can be chained together. Methods that retrieve a single value
 * or may return a primitive value will automatically end the chain sequence
 * and return the unwrapped value. Otherwise, the value must be unwrapped
 * with `_#value`.
 *
 * Explicit chain sequences, which must be unwrapped with `_#value`, may be
 * enabled using `_.chain`.
 *
 * The execution of chained methods is lazy, that is, it's deferred until
 * `_#value` is implicitly or explicitly called.
 *
 * Lazy evaluation allows several methods to support shortcut fusion.
 * Shortcut fusion is an optimization to merge iteratee calls; this avoids
 * the creation of intermediate arrays and can greatly reduce the number of
 * iteratee executions. Sections of a chain sequence qualify for shortcut
 * fusion if the section is applied to an array and iteratees accept only
 * one argument. The heuristic for whether a section qualifies for shortcut
 * fusion is subject to change.
 *
 * Chaining is supported in custom builds as long as the `_#value` method is
 * directly or indirectly included in the build.
 *
 * In addition to lodash methods, wrappers have `Array` and `String` methods.
 *
 * The wrapper `Array` methods are:
 * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
 *
 * The wrapper `String` methods are:
 * `replace` and `split`
 *
 * The wrapper methods that support shortcut fusion are:
 * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
 * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
 * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
 *
 * The chainable wrapper methods are:
 * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
 * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
 * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
 * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
 * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
 * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
 * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
 * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
 * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
 * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
 * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
 * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
 * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
 * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
 * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
 * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
 * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
 * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
 * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
 * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
 * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
 * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
 * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
 * `zipObject`, `zipObjectDeep`, and `zipWith`
 *
 * The wrapper methods that are **not** chainable by default are:
 * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
 * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
 * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
 * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
 * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
 * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
 * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
 * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
 * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
 * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
 * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
 * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
 * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
 * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
 * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
 * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
 * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
 * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
 * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
 * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
 * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
 * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
 * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
 * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
 * `upperFirst`, `value`, and `words`
 *
 * @name _
 * @constructor
 * @category Seq
 * @param {*} value The value to wrap in a `lodash` instance.
 * @returns {Object} Returns the new `lodash` wrapper instance.
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * var wrapped = _([1, 2, 3]);
 *
 * // Returns an unwrapped value.
 * wrapped.reduce(_.add);
 * // => 6
 *
 * // Returns a wrapped value.
 * var squares = wrapped.map(square);
 *
 * _.isArray(squares);
 * // => false
 *
 * _.isArray(squares.value());
 * // => true
 */
function lodash(value) {
  if (isObjectLike_1(value) && !isArray_1(value) && !(value instanceof _LazyWrapper)) {
    if (value instanceof _LodashWrapper) {
      return value;
    }
    if (hasOwnProperty$g.call(value, '__wrapped__')) {
      return _wrapperClone(value);
    }
  }
  return new _LodashWrapper(value);
}

// Ensure wrappers are instances of `baseLodash`.
lodash.prototype = _baseLodash.prototype;
lodash.prototype.constructor = lodash;

var wrapperLodash = lodash;

/**
 * Checks if `func` has a lazy counterpart.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
 *  else `false`.
 */
function isLaziable(func) {
  var funcName = _getFuncName(func),
      other = wrapperLodash[funcName];

  if (typeof other != 'function' || !(funcName in _LazyWrapper.prototype)) {
    return false;
  }
  if (func === other) {
    return true;
  }
  var data = _getData(other);
  return !!data && func === data[0];
}

var _isLaziable = isLaziable;

/**
 * Sets metadata for `func`.
 *
 * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
 * period of time, it will trip its breaker and transition to an identity
 * function to avoid garbage collection pauses in V8. See
 * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
 * for more details.
 *
 * @private
 * @param {Function} func The function to associate metadata with.
 * @param {*} data The metadata.
 * @returns {Function} Returns `func`.
 */
var setData = _shortOut(_baseSetData);

var _setData = setData;

/** Used to match wrap detail comments. */
var reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
    reSplitDetails = /,? & /;

/**
 * Extracts wrapper details from the `source` body comment.
 *
 * @private
 * @param {string} source The source to inspect.
 * @returns {Array} Returns the wrapper details.
 */
function getWrapDetails(source) {
  var match = source.match(reWrapDetails);
  return match ? match[1].split(reSplitDetails) : [];
}

var _getWrapDetails = getWrapDetails;

/** Used to match wrap detail comments. */
var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/;

/**
 * Inserts wrapper `details` in a comment at the top of the `source` body.
 *
 * @private
 * @param {string} source The source to modify.
 * @returns {Array} details The details to insert.
 * @returns {string} Returns the modified source.
 */
function insertWrapDetails(source, details) {
  var length = details.length;
  if (!length) {
    return source;
  }
  var lastIndex = length - 1;
  details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
  details = details.join(length > 2 ? ', ' : ' ');
  return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
}

var _insertWrapDetails = insertWrapDetails;

/**
 * The base implementation of `_.isNaN` without support for number objects.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
 */
function baseIsNaN(value) {
  return value !== value;
}

var _baseIsNaN = baseIsNaN;

/**
 * A specialized version of `_.indexOf` which performs strict equality
 * comparisons of values, i.e. `===`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function strictIndexOf(array, value, fromIndex) {
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

var _strictIndexOf = strictIndexOf;

/**
 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf(array, value, fromIndex) {
  return value === value
    ? _strictIndexOf(array, value, fromIndex)
    : _baseFindIndex(array, _baseIsNaN, fromIndex);
}

var _baseIndexOf = baseIndexOf;

/**
 * A specialized version of `_.includes` for arrays without support for
 * specifying an index to search from.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludes$1(array, value) {
  var length = array == null ? 0 : array.length;
  return !!length && _baseIndexOf(array, value, 0) > -1;
}

var _arrayIncludes = arrayIncludes$1;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$1 = 1,
    WRAP_BIND_KEY_FLAG = 2,
    WRAP_CURRY_FLAG = 8,
    WRAP_CURRY_RIGHT_FLAG = 16,
    WRAP_PARTIAL_FLAG = 32,
    WRAP_PARTIAL_RIGHT_FLAG = 64,
    WRAP_ARY_FLAG = 128,
    WRAP_REARG_FLAG = 256,
    WRAP_FLIP_FLAG = 512;

/** Used to associate wrap methods with their bit flags. */
var wrapFlags = [
  ['ary', WRAP_ARY_FLAG],
  ['bind', WRAP_BIND_FLAG$1],
  ['bindKey', WRAP_BIND_KEY_FLAG],
  ['curry', WRAP_CURRY_FLAG],
  ['curryRight', WRAP_CURRY_RIGHT_FLAG],
  ['flip', WRAP_FLIP_FLAG],
  ['partial', WRAP_PARTIAL_FLAG],
  ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
  ['rearg', WRAP_REARG_FLAG]
];

/**
 * Updates wrapper `details` based on `bitmask` flags.
 *
 * @private
 * @returns {Array} details The details to modify.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @returns {Array} Returns `details`.
 */
function updateWrapDetails(details, bitmask) {
  _arrayEach(wrapFlags, function(pair) {
    var value = '_.' + pair[0];
    if ((bitmask & pair[1]) && !_arrayIncludes(details, value)) {
      details.push(value);
    }
  });
  return details.sort();
}

var _updateWrapDetails = updateWrapDetails;

/**
 * Sets the `toString` method of `wrapper` to mimic the source of `reference`
 * with wrapper details in a comment at the top of the source body.
 *
 * @private
 * @param {Function} wrapper The function to modify.
 * @param {Function} reference The reference function.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @returns {Function} Returns `wrapper`.
 */
function setWrapToString(wrapper, reference, bitmask) {
  var source = (reference + '');
  return _setToString(wrapper, _insertWrapDetails(source, _updateWrapDetails(_getWrapDetails(source), bitmask)));
}

var _setWrapToString = setWrapToString;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$2 = 1,
    WRAP_BIND_KEY_FLAG$1 = 2,
    WRAP_CURRY_BOUND_FLAG = 4,
    WRAP_CURRY_FLAG$1 = 8,
    WRAP_PARTIAL_FLAG$1 = 32,
    WRAP_PARTIAL_RIGHT_FLAG$1 = 64;

/**
 * Creates a function that wraps `func` to continue currying.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {Function} wrapFunc The function to create the `func` wrapper.
 * @param {*} placeholder The placeholder value.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to prepend to those provided to
 *  the new function.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
  var isCurry = bitmask & WRAP_CURRY_FLAG$1,
      newHolders = isCurry ? holders : undefined,
      newHoldersRight = isCurry ? undefined : holders,
      newPartials = isCurry ? partials : undefined,
      newPartialsRight = isCurry ? undefined : partials;

  bitmask |= (isCurry ? WRAP_PARTIAL_FLAG$1 : WRAP_PARTIAL_RIGHT_FLAG$1);
  bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG$1 : WRAP_PARTIAL_FLAG$1);

  if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
    bitmask &= ~(WRAP_BIND_FLAG$2 | WRAP_BIND_KEY_FLAG$1);
  }
  var newData = [
    func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
    newHoldersRight, argPos, ary, arity
  ];

  var result = wrapFunc.apply(undefined, newData);
  if (_isLaziable(func)) {
    _setData(result, newData);
  }
  result.placeholder = placeholder;
  return _setWrapToString(result, func, bitmask);
}

var _createRecurry = createRecurry;

/**
 * Gets the argument placeholder value for `func`.
 *
 * @private
 * @param {Function} func The function to inspect.
 * @returns {*} Returns the placeholder value.
 */
function getHolder(func) {
  var object = func;
  return object.placeholder;
}

var _getHolder = getHolder;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMin$1 = Math.min;

/**
 * Reorder `array` according to the specified indexes where the element at
 * the first index is assigned as the first element, the element at
 * the second index is assigned as the second element, and so on.
 *
 * @private
 * @param {Array} array The array to reorder.
 * @param {Array} indexes The arranged array indexes.
 * @returns {Array} Returns `array`.
 */
function reorder(array, indexes) {
  var arrLength = array.length,
      length = nativeMin$1(indexes.length, arrLength),
      oldArray = _copyArray(array);

  while (length--) {
    var index = indexes[length];
    array[length] = _isIndex(index, arrLength) ? oldArray[index] : undefined;
  }
  return array;
}

var _reorder = reorder;

/** Used as the internal argument placeholder. */
var PLACEHOLDER = '__lodash_placeholder__';

/**
 * Replaces all `placeholder` elements in `array` with an internal placeholder
 * and returns an array of their indexes.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {*} placeholder The placeholder to replace.
 * @returns {Array} Returns the new array of placeholder indexes.
 */
function replaceHolders(array, placeholder) {
  var index = -1,
      length = array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (value === placeholder || value === PLACEHOLDER) {
      array[index] = PLACEHOLDER;
      result[resIndex++] = index;
    }
  }
  return result;
}

var _replaceHolders = replaceHolders;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$3 = 1,
    WRAP_BIND_KEY_FLAG$2 = 2,
    WRAP_CURRY_FLAG$2 = 8,
    WRAP_CURRY_RIGHT_FLAG$1 = 16,
    WRAP_ARY_FLAG$1 = 128,
    WRAP_FLIP_FLAG$1 = 512;

/**
 * Creates a function that wraps `func` to invoke it with optional `this`
 * binding of `thisArg`, partial application, and currying.
 *
 * @private
 * @param {Function|string} func The function or method name to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to prepend to those provided to
 *  the new function.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [partialsRight] The arguments to append to those provided
 *  to the new function.
 * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
  var isAry = bitmask & WRAP_ARY_FLAG$1,
      isBind = bitmask & WRAP_BIND_FLAG$3,
      isBindKey = bitmask & WRAP_BIND_KEY_FLAG$2,
      isCurried = bitmask & (WRAP_CURRY_FLAG$2 | WRAP_CURRY_RIGHT_FLAG$1),
      isFlip = bitmask & WRAP_FLIP_FLAG$1,
      Ctor = isBindKey ? undefined : _createCtor(func);

  function wrapper() {
    var length = arguments.length,
        args = Array(length),
        index = length;

    while (index--) {
      args[index] = arguments[index];
    }
    if (isCurried) {
      var placeholder = _getHolder(wrapper),
          holdersCount = _countHolders(args, placeholder);
    }
    if (partials) {
      args = _composeArgs(args, partials, holders, isCurried);
    }
    if (partialsRight) {
      args = _composeArgsRight(args, partialsRight, holdersRight, isCurried);
    }
    length -= holdersCount;
    if (isCurried && length < arity) {
      var newHolders = _replaceHolders(args, placeholder);
      return _createRecurry(
        func, bitmask, createHybrid, wrapper.placeholder, thisArg,
        args, newHolders, argPos, ary, arity - length
      );
    }
    var thisBinding = isBind ? thisArg : this,
        fn = isBindKey ? thisBinding[func] : func;

    length = args.length;
    if (argPos) {
      args = _reorder(args, argPos);
    } else if (isFlip && length > 1) {
      args.reverse();
    }
    if (isAry && ary < length) {
      args.length = ary;
    }
    if (this && this !== _root && this instanceof wrapper) {
      fn = Ctor || _createCtor(fn);
    }
    return fn.apply(thisBinding, args);
  }
  return wrapper;
}

var _createHybrid = createHybrid;

/**
 * Creates a function that wraps `func` to enable currying.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {number} arity The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createCurry(func, bitmask, arity) {
  var Ctor = _createCtor(func);

  function wrapper() {
    var length = arguments.length,
        args = Array(length),
        index = length,
        placeholder = _getHolder(wrapper);

    while (index--) {
      args[index] = arguments[index];
    }
    var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
      ? []
      : _replaceHolders(args, placeholder);

    length -= holders.length;
    if (length < arity) {
      return _createRecurry(
        func, bitmask, _createHybrid, wrapper.placeholder, undefined,
        args, holders, undefined, undefined, arity - length);
    }
    var fn = (this && this !== _root && this instanceof wrapper) ? Ctor : func;
    return _apply(fn, this, args);
  }
  return wrapper;
}

var _createCurry = createCurry;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$4 = 1;

/**
 * Creates a function that wraps `func` to invoke it with the `this` binding
 * of `thisArg` and `partials` prepended to the arguments it receives.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} partials The arguments to prepend to those provided to
 *  the new function.
 * @returns {Function} Returns the new wrapped function.
 */
function createPartial(func, bitmask, thisArg, partials) {
  var isBind = bitmask & WRAP_BIND_FLAG$4,
      Ctor = _createCtor(func);

  function wrapper() {
    var argsIndex = -1,
        argsLength = arguments.length,
        leftIndex = -1,
        leftLength = partials.length,
        args = Array(leftLength + argsLength),
        fn = (this && this !== _root && this instanceof wrapper) ? Ctor : func;

    while (++leftIndex < leftLength) {
      args[leftIndex] = partials[leftIndex];
    }
    while (argsLength--) {
      args[leftIndex++] = arguments[++argsIndex];
    }
    return _apply(fn, isBind ? thisArg : this, args);
  }
  return wrapper;
}

var _createPartial = createPartial;

/** Used as the internal argument placeholder. */
var PLACEHOLDER$1 = '__lodash_placeholder__';

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$5 = 1,
    WRAP_BIND_KEY_FLAG$3 = 2,
    WRAP_CURRY_BOUND_FLAG$1 = 4,
    WRAP_CURRY_FLAG$3 = 8,
    WRAP_ARY_FLAG$2 = 128,
    WRAP_REARG_FLAG$1 = 256;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMin$2 = Math.min;

/**
 * Merges the function metadata of `source` into `data`.
 *
 * Merging metadata reduces the number of wrappers used to invoke a function.
 * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
 * may be applied regardless of execution order. Methods like `_.ary` and
 * `_.rearg` modify function arguments, making the order in which they are
 * executed important, preventing the merging of metadata. However, we make
 * an exception for a safe combined case where curried functions have `_.ary`
 * and or `_.rearg` applied.
 *
 * @private
 * @param {Array} data The destination metadata.
 * @param {Array} source The source metadata.
 * @returns {Array} Returns `data`.
 */
function mergeData(data, source) {
  var bitmask = data[1],
      srcBitmask = source[1],
      newBitmask = bitmask | srcBitmask,
      isCommon = newBitmask < (WRAP_BIND_FLAG$5 | WRAP_BIND_KEY_FLAG$3 | WRAP_ARY_FLAG$2);

  var isCombo =
    ((srcBitmask == WRAP_ARY_FLAG$2) && (bitmask == WRAP_CURRY_FLAG$3)) ||
    ((srcBitmask == WRAP_ARY_FLAG$2) && (bitmask == WRAP_REARG_FLAG$1) && (data[7].length <= source[8])) ||
    ((srcBitmask == (WRAP_ARY_FLAG$2 | WRAP_REARG_FLAG$1)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG$3));

  // Exit early if metadata can't be merged.
  if (!(isCommon || isCombo)) {
    return data;
  }
  // Use source `thisArg` if available.
  if (srcBitmask & WRAP_BIND_FLAG$5) {
    data[2] = source[2];
    // Set when currying a bound function.
    newBitmask |= bitmask & WRAP_BIND_FLAG$5 ? 0 : WRAP_CURRY_BOUND_FLAG$1;
  }
  // Compose partial arguments.
  var value = source[3];
  if (value) {
    var partials = data[3];
    data[3] = partials ? _composeArgs(partials, value, source[4]) : value;
    data[4] = partials ? _replaceHolders(data[3], PLACEHOLDER$1) : source[4];
  }
  // Compose partial right arguments.
  value = source[5];
  if (value) {
    partials = data[5];
    data[5] = partials ? _composeArgsRight(partials, value, source[6]) : value;
    data[6] = partials ? _replaceHolders(data[5], PLACEHOLDER$1) : source[6];
  }
  // Use source `argPos` if available.
  value = source[7];
  if (value) {
    data[7] = value;
  }
  // Use source `ary` if it's smaller.
  if (srcBitmask & WRAP_ARY_FLAG$2) {
    data[8] = data[8] == null ? source[8] : nativeMin$2(data[8], source[8]);
  }
  // Use source `arity` if one is not provided.
  if (data[9] == null) {
    data[9] = source[9];
  }
  // Use source `func` and merge bitmasks.
  data[0] = source[0];
  data[1] = newBitmask;

  return data;
}

var _mergeData = mergeData;

/** Error message constants. */
var FUNC_ERROR_TEXT$2 = 'Expected a function';

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$6 = 1,
    WRAP_BIND_KEY_FLAG$4 = 2,
    WRAP_CURRY_FLAG$4 = 8,
    WRAP_CURRY_RIGHT_FLAG$2 = 16,
    WRAP_PARTIAL_FLAG$2 = 32,
    WRAP_PARTIAL_RIGHT_FLAG$2 = 64;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$5 = Math.max;

/**
 * Creates a function that either curries or invokes `func` with optional
 * `this` binding and partially applied arguments.
 *
 * @private
 * @param {Function|string} func The function or method name to wrap.
 * @param {number} bitmask The bitmask flags.
 *    1 - `_.bind`
 *    2 - `_.bindKey`
 *    4 - `_.curry` or `_.curryRight` of a bound function
 *    8 - `_.curry`
 *   16 - `_.curryRight`
 *   32 - `_.partial`
 *   64 - `_.partialRight`
 *  128 - `_.rearg`
 *  256 - `_.ary`
 *  512 - `_.flip`
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to be partially applied.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
  var isBindKey = bitmask & WRAP_BIND_KEY_FLAG$4;
  if (!isBindKey && typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$2);
  }
  var length = partials ? partials.length : 0;
  if (!length) {
    bitmask &= ~(WRAP_PARTIAL_FLAG$2 | WRAP_PARTIAL_RIGHT_FLAG$2);
    partials = holders = undefined;
  }
  ary = ary === undefined ? ary : nativeMax$5(toInteger_1(ary), 0);
  arity = arity === undefined ? arity : toInteger_1(arity);
  length -= holders ? holders.length : 0;

  if (bitmask & WRAP_PARTIAL_RIGHT_FLAG$2) {
    var partialsRight = partials,
        holdersRight = holders;

    partials = holders = undefined;
  }
  var data = isBindKey ? undefined : _getData(func);

  var newData = [
    func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
    argPos, ary, arity
  ];

  if (data) {
    _mergeData(newData, data);
  }
  func = newData[0];
  bitmask = newData[1];
  thisArg = newData[2];
  partials = newData[3];
  holders = newData[4];
  arity = newData[9] = newData[9] === undefined
    ? (isBindKey ? 0 : func.length)
    : nativeMax$5(newData[9] - length, 0);

  if (!arity && bitmask & (WRAP_CURRY_FLAG$4 | WRAP_CURRY_RIGHT_FLAG$2)) {
    bitmask &= ~(WRAP_CURRY_FLAG$4 | WRAP_CURRY_RIGHT_FLAG$2);
  }
  if (!bitmask || bitmask == WRAP_BIND_FLAG$6) {
    var result = _createBind(func, bitmask, thisArg);
  } else if (bitmask == WRAP_CURRY_FLAG$4 || bitmask == WRAP_CURRY_RIGHT_FLAG$2) {
    result = _createCurry(func, bitmask, arity);
  } else if ((bitmask == WRAP_PARTIAL_FLAG$2 || bitmask == (WRAP_BIND_FLAG$6 | WRAP_PARTIAL_FLAG$2)) && !holders.length) {
    result = _createPartial(func, bitmask, thisArg, partials);
  } else {
    result = _createHybrid.apply(undefined, newData);
  }
  var setter = data ? _baseSetData : _setData;
  return _setWrapToString(setter(result, newData), func, bitmask);
}

var _createWrap = createWrap;

/** Used to compose bitmasks for function metadata. */
var WRAP_CURRY_FLAG$5 = 8;

/**
 * Creates a function that accepts arguments of `func` and either invokes
 * `func` returning its result, if at least `arity` number of arguments have
 * been provided, or returns a function that accepts the remaining `func`
 * arguments, and so on. The arity of `func` may be specified if `func.length`
 * is not sufficient.
 *
 * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
 * may be used as a placeholder for provided arguments.
 *
 * **Note:** This method doesn't set the "length" property of curried functions.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Function
 * @param {Function} func The function to curry.
 * @param {number} [arity=func.length] The arity of `func`.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Function} Returns the new curried function.
 * @example
 *
 * var abc = function(a, b, c) {
 *   return [a, b, c];
 * };
 *
 * var curried = _.curry(abc);
 *
 * curried(1)(2)(3);
 * // => [1, 2, 3]
 *
 * curried(1, 2)(3);
 * // => [1, 2, 3]
 *
 * curried(1, 2, 3);
 * // => [1, 2, 3]
 *
 * // Curried with placeholders.
 * curried(1)(_, 3)(2);
 * // => [1, 2, 3]
 */
function curry(func, arity, guard) {
  arity = guard ? undefined : arity;
  var result = _createWrap(func, WRAP_CURRY_FLAG$5, undefined, undefined, undefined, undefined, undefined, arity);
  result.placeholder = curry.placeholder;
  return result;
}

// Assign default placeholders.
curry.placeholder = {};

var curry_1 = curry;

/** Built-in value references. */
var spreadableSymbol = _Symbol ? _Symbol.isConcatSpreadable : undefined;

/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */
function isFlattenable(value) {
  return isArray_1(value) || isArguments_1(value) ||
    !!(spreadableSymbol && value && value[spreadableSymbol]);
}

var _isFlattenable = isFlattenable;

/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;

  predicate || (predicate = _isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];
    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten(value, depth - 1, predicate, isStrict, result);
      } else {
        _arrayPush(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

var _baseFlatten = baseFlatten;

/**
 * Flattens `array` a single level deep.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to flatten.
 * @returns {Array} Returns the new flattened array.
 * @example
 *
 * _.flatten([1, [2, [3, [4]], 5]]);
 * // => [1, 2, [3, [4]], 5]
 */
function flatten$1(array) {
  var length = array == null ? 0 : array.length;
  return length ? _baseFlatten(array, 1) : [];
}

var flatten_1 = flatten$1;

/**
 * A specialized version of `baseRest` which flattens the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @returns {Function} Returns the new function.
 */
function flatRest(func) {
  return _setToString(_overRest(func, undefined, flatten_1), func + '');
}

var _flatRest = flatRest;

/** Error message constants. */
var FUNC_ERROR_TEXT$3 = 'Expected a function';

/** Used to compose bitmasks for function metadata. */
var WRAP_CURRY_FLAG$6 = 8,
    WRAP_PARTIAL_FLAG$3 = 32,
    WRAP_ARY_FLAG$3 = 128,
    WRAP_REARG_FLAG$2 = 256;

/**
 * Creates a `_.flow` or `_.flowRight` function.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new flow function.
 */
function createFlow(fromRight) {
  return _flatRest(function(funcs) {
    var length = funcs.length,
        index = length,
        prereq = _LodashWrapper.prototype.thru;

    if (fromRight) {
      funcs.reverse();
    }
    while (index--) {
      var func = funcs[index];
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT$3);
      }
      if (prereq && !wrapper && _getFuncName(func) == 'wrapper') {
        var wrapper = new _LodashWrapper([], true);
      }
    }
    index = wrapper ? index : length;
    while (++index < length) {
      func = funcs[index];

      var funcName = _getFuncName(func),
          data = funcName == 'wrapper' ? _getData(func) : undefined;

      if (data && _isLaziable(data[0]) &&
            data[1] == (WRAP_ARY_FLAG$3 | WRAP_CURRY_FLAG$6 | WRAP_PARTIAL_FLAG$3 | WRAP_REARG_FLAG$2) &&
            !data[4].length && data[9] == 1
          ) {
        wrapper = wrapper[_getFuncName(data[0])].apply(wrapper, data[3]);
      } else {
        wrapper = (func.length == 1 && _isLaziable(func))
          ? wrapper[funcName]()
          : wrapper.thru(func);
      }
    }
    return function() {
      var args = arguments,
          value = args[0];

      if (wrapper && args.length == 1 && isArray_1(value)) {
        return wrapper.plant(value).value();
      }
      var index = 0,
          result = length ? funcs[index].apply(this, args) : value;

      while (++index < length) {
        result = funcs[index].call(this, result);
      }
      return result;
    };
  });
}

var _createFlow = createFlow;

/**
 * This method is like `_.flow` except that it creates a function that
 * invokes the given functions from right to left.
 *
 * @static
 * @since 3.0.0
 * @memberOf _
 * @category Util
 * @param {...(Function|Function[])} [funcs] The functions to invoke.
 * @returns {Function} Returns the new composite function.
 * @see _.flow
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * var addSquare = _.flowRight([square, _.add]);
 * addSquare(1, 2);
 * // => 9
 */
var flowRight = _createFlow(true);

var flowRight_1 = flowRight;

//

var script$1g = {
  name: 'MeDropdownCriterion',
  components: {
    Icon: __vue_component__
  },

  props: {
    hideActions: {
      type: Boolean,
      default: false
    },
    hideEdit: {
      type: Boolean,
      default: false
    },
    hideRemove: {
      type: Boolean,
      default: false
    }
  }

};

/* script */
const __vue_script__$1g = script$1g;

/* template */
var __vue_render__$1g = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "dropdown-criterion" },
    [
      _vm._t("dropdownCriterion"),
      _vm._v(" "),
      !_vm.hideActions
        ? _c(
            "div",
            { staticClass: "actions" },
            [
              !_vm.hideEdit
                ? _c("Icon", {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value: _vm.$toolkit.t("dropdownCriterion.actions.edit"),
                        expression:
                          "$toolkit.t('dropdownCriterion.actions.edit')"
                      }
                    ],
                    staticClass: "action-icon",
                    attrs: {
                      size: 12,
                      icon: "me-icon icon-edit",
                      color: "var(--me-primary-2)"
                    },
                    on: {
                      click: function($event) {
                        return _vm.$emit("edit")
                      }
                    }
                  })
                : _vm._e(),
              _vm._v(" "),
              !_vm.hideRemove
                ? _c("Icon", {
                    directives: [
                      {
                        name: "me-tooltip",
                        rawName: "v-me-tooltip",
                        value: _vm.$toolkit.t(
                          "dropdownCriterion.actions.remove"
                        ),
                        expression:
                          "$toolkit.t('dropdownCriterion.actions.remove')"
                      }
                    ],
                    staticClass: "action-icon",
                    attrs: {
                      size: 12,
                      icon: "me-icon icon-trash",
                      color: "var(--me-primary-2)"
                    },
                    on: {
                      click: function($event) {
                        return _vm.$emit("remove")
                      }
                    }
                  })
                : _vm._e()
            ],
            1
          )
        : _vm._e()
    ],
    2
  )
};
var __vue_staticRenderFns__$1g = [];
__vue_render__$1g._withStripped = true;

  /* style */
  const __vue_inject_styles__$1g = function (inject) {
    if (!inject) return
    inject("data-v-17f6932a_0", { source: ".dropdown-criterion[data-v-17f6932a] {\n  position: relative;\n  width: fit-content;\n}\n.dropdown-criterion:hover .actions[data-v-17f6932a] {\n  opacity: 1;\n}\n.dropdown-criterion .actions[data-v-17f6932a] {\n  position: absolute;\n  display: flex;\n  align-items: center;\n  justify-content: space-around;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100%;\n  opacity: 0;\n  background-color: var(--me-primary-7);\n  border: 1px solid var(--me-primary-2);\n  border-radius: 8px;\n}\n.dropdown-criterion .actions .action-icon[data-v-17f6932a] {\n  margin: 0 8px;\n  cursor: pointer;\n}\n.dropdown-criterion .actions .action-icon[data-v-17f6932a]:not(:last-child)::after {\n  content: \"\";\n  border-right: solid 0.5px var(--me-primary-2);\n  height: 50%;\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n}\n\n/*# sourceMappingURL=DropdownCriterion.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dropdown-criterion/DropdownCriterion.vue","DropdownCriterion.vue"],"names":[],"mappings":"AAwDA;EACA,kBAAA;EACA,kBAAA;ACvDA;AD0DA;EACA,UAAA;ACxDA;AD2DA;EACA,kBAAA;EACA,aAAA;EACA,mBAAA;EACA,6BAAA;EACA,MAAA;EACA,OAAA;EACA,YAAA;EACA,WAAA;EACA,UAAA;EACA,qCAAA;EACA,qCAAA;EACA,kBAAA;ACzDA;AD2DA;EACA,aAAA;EACA,eAAA;ACzDA;AD4DA;EACA,WAAA;EACA,6CAAA;EACA,WAAA;EACA,kBAAA;EACA,QAAA;EACA,SAAA;EACA,gCAAA;AC1DA;;AAEA,gDAAgD","file":"DropdownCriterion.vue","sourcesContent":["<template>\n  <div class=\"dropdown-criterion\">\n    <slot name=\"dropdownCriterion\" />\n\n    <div v-if=\"!hideActions\" class=\"actions\">\n      <Icon\n        v-if=\"!hideEdit\"\n        :size=\"12\"\n        @click=\"$emit('edit')\"\n        v-me-tooltip=\"$toolkit.t('dropdownCriterion.actions.edit')\"\n        icon=\"me-icon icon-edit\"\n        color=\"var(--me-primary-2)\"\n        class=\"action-icon\"\n      />\n\n      <Icon\n        v-if=\"!hideRemove\"\n        :size=\"12\"\n        @click=\"$emit('remove')\"\n        v-me-tooltip=\"$toolkit.t('dropdownCriterion.actions.remove')\"\n        icon=\"me-icon icon-trash\"\n        color=\"var(--me-primary-2)\"\n        class=\"action-icon\"\n      />\n    </div>\n  </div>\n</template>\n\n<script>\nimport Icon from '../Icon.vue'\n\nexport default {\n  name: 'MeDropdownCriterion',\n  components: {\n    Icon\n  },\n\n  props: {\n    hideActions: {\n      type: Boolean,\n      default: false\n    },\n    hideEdit: {\n      type: Boolean,\n      default: false\n    },\n    hideRemove: {\n      type: Boolean,\n      default: false\n    }\n  }\n\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .dropdown-criterion {\n    position: relative;\n    width: fit-content;\n\n    &:hover {\n      .actions {\n        opacity: 1;\n      }\n    }\n    .actions {\n      position: absolute;\n      display: flex;\n      align-items: center;\n      justify-content: space-around;\n      top: 0;\n      left: 0;\n      height: 100%;\n      width: 100%;\n      opacity: 0;\n      background-color: var(--me-primary-7);\n      border: 1px solid var(--me-primary-2);\n      border-radius: 8px;\n\n      .action-icon {\n        margin: 0 8px;\n        cursor: pointer;\n\n        &:not(:last-child) {\n          &::after {\n            content: \"\";\n            border-right: solid 0.5px var(--me-primary-2);\n            height: 50%;\n            position: absolute;\n            top: 50%;\n            left: 50%;\n            transform: translate(-50%, -50%);\n          }\n        }\n      }\n    }\n  }\n</style>\n",".dropdown-criterion {\n  position: relative;\n  width: fit-content;\n}\n.dropdown-criterion:hover .actions {\n  opacity: 1;\n}\n.dropdown-criterion .actions {\n  position: absolute;\n  display: flex;\n  align-items: center;\n  justify-content: space-around;\n  top: 0;\n  left: 0;\n  height: 100%;\n  width: 100%;\n  opacity: 0;\n  background-color: var(--me-primary-7);\n  border: 1px solid var(--me-primary-2);\n  border-radius: 8px;\n}\n.dropdown-criterion .actions .action-icon {\n  margin: 0 8px;\n  cursor: pointer;\n}\n.dropdown-criterion .actions .action-icon:not(:last-child)::after {\n  content: \"\";\n  border-right: solid 0.5px var(--me-primary-2);\n  height: 50%;\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n}\n\n/*# sourceMappingURL=DropdownCriterion.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1g = "data-v-17f6932a";
  /* module identifier */
  const __vue_module_identifier__$1g = undefined;
  /* functional template */
  const __vue_is_functional_template__$1g = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1g, staticRenderFns: __vue_staticRenderFns__$1g },
    __vue_inject_styles__$1g,
    __vue_script__$1g,
    __vue_scope_id__$1g,
    __vue_is_functional_template__$1g,
    __vue_module_identifier__$1g,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//


var script$1h = {
  name: 'MeDateOptions',
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      currentValue: this.value
    }
  },

  watch: {
    value(val) {
      this.currentValue = val;
    }
  }
};

/* script */
const __vue_script__$1h = script$1h;

/* template */
var __vue_render__$1h = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("me-radio-input", {
    attrs: {
      options: _vm.options,
      name: _vm.name,
      hint: _vm.$toolkit.t("dropdownCriterion.dropdownDate.dateOptions.title"),
      "value-field": "id",
      "text-field": "label",
      "disabled-field": "blocked",
      stacked: ""
    },
    on: {
      input: function($event) {
        return _vm.$emit("input", _vm.currentValue)
      }
    },
    model: {
      value: _vm.currentValue,
      callback: function($$v) {
        _vm.currentValue = $$v;
      },
      expression: "currentValue"
    }
  })
};
var __vue_staticRenderFns__$1h = [];
__vue_render__$1h._withStripped = true;

  /* style */
  const __vue_inject_styles__$1h = undefined;
  /* scoped */
  const __vue_scope_id__$1h = undefined;
  /* module identifier */
  const __vue_module_identifier__$1h = undefined;
  /* functional template */
  const __vue_is_functional_template__$1h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1h, staticRenderFns: __vue_staticRenderFns__$1h },
    __vue_inject_styles__$1h,
    __vue_script__$1h,
    __vue_scope_id__$1h,
    __vue_is_functional_template__$1h,
    __vue_module_identifier__$1h,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1i = {
  name: 'MeCustomDateFields',
  props: {
    initialDate: {
      type: [String, Date],
      default: null
    },
    endDate: {
      type: [String, Date],
      default: null
    },
    invalidInitialDateFeedback: {
      type: String,
      default: ''
    },
    invalidEndDateFeedback: {
      type: String,
      default: ''
    },
    validateInitialDate: {
      type: Function,
      default: () => null
    },
    validateEndDate: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      currentInitialDate: this.initialDate ? new Date(this.initialDate) : null,
      currentEndDate: this.endDate ? new Date(this.endDate) : null
    }
  },

  computed: {
    isInitialDateValid() {
      return this.validateInitialDate(this.currentInitialDate, this.currentEndDate)
    },
    isEndDateValid() {
      return this.validateEndDate(this.currentInitialDate, this.currentEndDate)
    }
  },

  watch: {
    initialDate(value) {
      this.currentInitialDate = value ? new Date(value) : null;
    },

    endDate(value) {
      this.currentEndDate = value ? new Date(value) : null;
    }
  }
};

/* script */
const __vue_script__$1i = script$1i;

/* template */
var __vue_render__$1i = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "custom-date-fields" },
    [
      _c(
        "me-form-group",
        {
          staticClass: "form-group",
          attrs: {
            label: _vm.$toolkit.t(
              "dropdownCriterion.dropdownDate.dateOptions.startDate"
            ),
            "label-cols": 3,
            state: _vm.isInitialDateValid,
            "invalid-feedback": _vm.invalidInitialDateFeedback
          }
        },
        [
          _c("me-date-picker", {
            attrs: {
              "popup-style": { zIndex: 1103 },
              state: _vm.isInitialDateValid,
              size: "sm"
            },
            on: {
              input: function($event) {
                return _vm.$emit("changeInitialDate", _vm.currentInitialDate)
              },
              open: function($event) {
                return _vm.$emit("onOpenStartCalendar")
              },
              close: function($event) {
                return _vm.$emit("onCloseStartCalendar")
              }
            },
            model: {
              value: _vm.currentInitialDate,
              callback: function($$v) {
                _vm.currentInitialDate = $$v;
              },
              expression: "currentInitialDate"
            }
          })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "me-form-group",
        {
          staticClass: "form-group",
          attrs: {
            label: _vm.$toolkit.t(
              "dropdownCriterion.dropdownDate.dateOptions.endDate"
            ),
            "label-cols": 3,
            state: _vm.isEndDateValid,
            invalidFeedback: _vm.invalidEndDateFeedback
          }
        },
        [
          _c("me-date-picker", {
            attrs: {
              "popup-style": { zIndex: 1103 },
              state: _vm.isEndDateValid,
              size: "sm"
            },
            on: {
              input: function($event) {
                return _vm.$emit("changeEndDate", _vm.currentEndDate)
              },
              open: function($event) {
                return _vm.$emit("onOpenEndCalendar")
              },
              close: function($event) {
                return _vm.$emit("onCloseEndCalendar")
              }
            },
            model: {
              value: _vm.currentEndDate,
              callback: function($$v) {
                _vm.currentEndDate = $$v;
              },
              expression: "currentEndDate"
            }
          })
        ],
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1i = [];
__vue_render__$1i._withStripped = true;

  /* style */
  const __vue_inject_styles__$1i = undefined;
  /* scoped */
  const __vue_scope_id__$1i = undefined;
  /* module identifier */
  const __vue_module_identifier__$1i = undefined;
  /* functional template */
  const __vue_is_functional_template__$1i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1i, staticRenderFns: __vue_staticRenderFns__$1i },
    __vue_inject_styles__$1i,
    __vue_script__$1i,
    __vue_scope_id__$1i,
    __vue_is_functional_template__$1i,
    __vue_module_identifier__$1i,
    false,
    undefined,
    undefined,
    undefined
  );

//

const calendarKeys = ['startDate', 'endDate'];
const rangeDateEnum = {
  ALL: 'all',
  CUSTOM: 'custom',
  HALF_MONTH: 'half_month',
  ONE_MONTH: 'one_month',
  TWO_MONTHS: 'two_months',
  THREE_MONTHS: 'three_months',
  ONE_YEAR: 'one_year'
};

var script$1j = {
  name: 'MeDropdownDateForm',
  components: {
    DropdownCriterion: __vue_component__$1g,
    DateOptions: __vue_component__$1h,
    CustomDateFields: __vue_component__$1i
  },

  props: {
    name: {
      type: String,
      required: true
    },
    initialDate: {
      type: [String, Date],
      default: null
    },
    endDate: {
      type: [String, Date],
      default: null
    },
    hideAllPeriod: {
      type: Boolean,
      default: false
    },
    hideCustomPeriod: {
      type: Boolean,
      default: false
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    backdrop: {
      type: Boolean,
      default: false
    },
    invalidInitialDateFeedback: {
      type: String,
      default: ''
    },
    invalidEndDateFeedback: {
      type: String,
      default: ''
    },
    validateInitialDate: {
      type: Function,
      default: () => null
    },
    validateEndDate: {
      type: Function,
      default: () => null
    },
    variant: {
      type: String,
      default: 'outline-primary'
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    }
  },

  data() {
    return {
      meDateFilter: Vue.filter('me-date'),
      dropdownLabel: '',
      appliedOptionId: null,
      currentSelectedOptionId: null,
      currentIniDate: this.initialDate ? new Date(this.initialDate) : null,
      currentEndDate: this.endDate ? new Date(this.endDate) : null,
      canCloseDropdown: true,
      calendarsOpenedMap: new Map(calendarKeys.map(key => [key, false]))
    }
  },

  computed: {
    dateOptions() {
      return [
        { id: rangeDateEnum.HALF_MONTH, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastFifteenDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 15)) },
        { id: rangeDateEnum.ONE_MONTH, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastThirtyDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 30)) },
        { id: rangeDateEnum.TWO_MONTHS, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastSixtyDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 60)) },
        { id: rangeDateEnum.THREE_MONTHS, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastNinetyDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 90)) },
        { id: rangeDateEnum.ONE_YEAR, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastTwelveMonths'), value: this.setTimeToZero(this.subtractYears(new Date(), 1)) },
        this.hideAllPeriod ? null : { id: rangeDateEnum.ALL, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.all') },
        this.hideCustomPeriod ? null : { id: rangeDateEnum.CUSTOM, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.specificPeriod') }
      ].filter(option => option)
    },

    hasSomeCalendarOpen() {
      return [...this.calendarsOpenedMap.values()].some(isOpened => isOpened)
    },

    isCustomPeriodSelected() {
      return this.currentSelectedOptionId === rangeDateEnum.CUSTOM
    },

    isAllPeriodSelected() {
      return this.currentSelectedOptionId === rangeDateEnum.ALL
    },

    isPredefinedDateSelected() {
      return !this.isCustomPeriodSelected && !this.isAllPeriodSelected
    },

    isInvalidDates() {
      return this.isInitialDateInvalid || this.isEndDateInvalid
    },

    isInitialDateInvalid() {
      return this.validateInitialDate(this.currentIniDate, this.currentEndDate) === false
    },

    isEndDateInvalid() {
      return this.validateEndDate(this.currentIniDate, this.currentEndDate) === false
    },

    findInDateOptions() {
      return this.finder(this.dateOptions)
    }
  },

  watch: {
    initialDate(val) {
      this.currentIniDate = val ? new Date(val) : null;
      this.resetStates();
    },

    endDate(val) {
      this.currentEndDate = val ? new Date(val) : null;
      this.resetStates();
    }
  },

  created() {
    this.resetStates();
  },

  methods: {
    resetStates() {
      this.appliedOptionId = this.getInitialAppliedOptionId(this.currentIniDate, this.currentEndDate);
      this.currentSelectedOptionId = this.appliedOptionId;
      this.dropdownLabel = this.getDropdownLabel();
    },

    getInitialAppliedOptionId(iniDate, endDate) {
      if (!iniDate && !endDate) return rangeDateEnum.ALL

      if (endDate) return rangeDateEnum.CUSTOM

      const findValue = flowRight_1(this.findInDateOptions('value'), this.setTimeToZero);

      const predefinedDateId = findValue(iniDate)?.id;
      if (predefinedDateId) return predefinedDateId

      return rangeDateEnum.CUSTOM
    },

    getDropdownLabel() {
      if (this.isAllPeriodSelected) {
        return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.allPeriod')
      }

      if (this.currentIniDate && this.currentEndDate) {
        return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.between', this.meDateFilter(this.currentIniDate), this.meDateFilter(this.currentEndDate))
      }

      if (this.currentEndDate) {
        return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.until', this.meDateFilter(this.currentEndDate))
      }

      if (this.isPredefinedDateSelected) {
        return this.findInDateOptions('id')(this.appliedOptionId)?.label
      }

      return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.since', this.meDateFilter(this.currentIniDate))
    },

    finder: curry_1((list, prop, lookingValue) => {
      return list.find(option => option[prop] === lookingValue)
    }),

    updateSelectedDate(value) {
      this.currentSelectedOptionId = value;
    },

    applyFilter() {
      this.setRangeDate();
      this.appliedOptionId = this.currentSelectedOptionId;
      this.dropdownLabel = this.getDropdownLabel();
      this.closeRangeDateForm();
    },

    cancelFilter() {
      this.closeRangeDateForm();
    },

    setRangeDate() {
      if (this.isAllPeriodSelected) {
        this.currentIniDate = null;
        this.currentEndDate = null;
      }

      if (this.isPredefinedDateSelected) {
        this.currentIniDate = new Date(this.findInDateOptions('id')(this.currentSelectedOptionId)?.value);
        this.currentEndDate = null;
      }

      this.$emit('change', { initialDate: this.currentIniDate, endDate: this.currentEndDate });
    },

    onOpenCalendar(calendarKey) {
      this.canCloseDropdown = false;
      this.updateCalendarOpened(calendarKey, true);
    },

    onCloseCalendar(calendarKey) {
      this.updateCalendarOpened(calendarKey, false);
    },

    updateCalendarOpened(calendarKey, isOpened) {
      const calendarsOpenedMapClone = new Map(this.calendarsOpenedMap);
      calendarsOpenedMapClone.set(calendarKey, isOpened);

      this.calendarsOpenedMap = new Map(calendarsOpenedMapClone);
    },

    closeRangeDateForm() {
      this.calendarsOpenedMap = new Map(calendarKeys.map(key => [key, false]));
      this.canCloseDropdown = true;
      document.querySelector('body').click();
    },

    hideRangeDateDropdown(e) {
      if (this.hasSomeCalendarOpen) {
        e.preventDefault();
        return
      }

      if (!this.canCloseDropdown) {
        e.preventDefault();
        this.canCloseDropdown = true;
        return
      }

      this.currentSelectedOptionId = this.appliedOptionId;
    },

    clearRangeDate() {
      this.currentSelectedOptionId = rangeDateEnum.ALL;
      this.applyFilter();
    },

    subtractDays(date, days) {
      return new Date(date).setDate(date.getDate() - days)
    },

    subtractYears(date, years) {
      return new Date(date).setFullYear(date.getFullYear() - years)
    },

    setTimeToZero(date) {
      return new Date(date).setHours(0, 0, 0, 0)
    }
  }
};

/* script */
const __vue_script__$1j = script$1j;

/* template */
var __vue_render__$1j = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("DropdownCriterion", {
    attrs: {
      "hide-actions": _vm.hideActions,
      "hide-remove": _vm.hideAllPeriod || _vm.isAllPeriodSelected
    },
    on: {
      edit: function($event) {
        return _vm.$refs.rangeDateDropDownGrid.show()
      },
      remove: _vm.clearRangeDate
    },
    scopedSlots: _vm._u([
      {
        key: "dropdownCriterion",
        fn: function() {
          return [
            _c(
              "me-drop-down",
              {
                ref: "rangeDateDropDownGrid",
                attrs: {
                  items: [],
                  text: _vm.dropdownLabel,
                  size: _vm.size,
                  backdrop: _vm.backdrop,
                  variant: _vm.variant
                },
                on: { hide: _vm.hideRangeDateDropdown }
              },
              [
                _c(
                  "div",
                  { staticClass: "dropdown-options" },
                  [
                    _c("DateOptions", {
                      attrs: {
                        value: _vm.currentSelectedOptionId,
                        options: _vm.dateOptions,
                        name: _vm.name
                      },
                      on: { input: _vm.updateSelectedDate }
                    }),
                    _vm._v(" "),
                    _c("CustomDateFields", {
                      directives: [
                        {
                          name: "show",
                          rawName: "v-show",
                          value:
                            !_vm.hideCustomPeriod && _vm.isCustomPeriodSelected,
                          expression:
                            "!hideCustomPeriod && isCustomPeriodSelected"
                        }
                      ],
                      attrs: {
                        initialDate: _vm.currentIniDate,
                        endDate: _vm.currentEndDate,
                        validateInitialDate: _vm.validateInitialDate,
                        validateEndDate: _vm.validateEndDate,
                        invalidInitialDateFeedback:
                          _vm.invalidInitialDateFeedback,
                        invalidEndDateFeedback: _vm.invalidEndDateFeedback
                      },
                      on: {
                        changeInitialDate: function(val) {
                          return (_vm.currentIniDate = val)
                        },
                        changeEndDate: function(val) {
                          return (_vm.currentEndDate = val)
                        },
                        onOpenStartCalendar: function($event) {
                          return _vm.onOpenCalendar("startDateCalendar")
                        },
                        onCloseStartCalendar: function($event) {
                          return _vm.onCloseCalendar("startDateCalendar")
                        },
                        onOpenEndCalendar: function($event) {
                          return _vm.onOpenCalendar("endDateCalendar")
                        },
                        onCloseEndCalendar: function($event) {
                          return _vm.onCloseCalendar("endDateCalendar")
                        }
                      }
                    })
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "dropdown-date-form-actions" },
                  [
                    _c(
                      "me-button",
                      {
                        staticClass: "dropdown-date-form-actions__cancel",
                        attrs: { size: "sm", variant: "light-primary" },
                        on: { click: _vm.cancelFilter }
                      },
                      [
                        _vm._v(
                          "\n          " +
                            _vm._s(
                              _vm.$toolkit.t(
                                "dropdownCriterion.dropdownDate.formActions.cancel"
                              )
                            ) +
                            "\n        "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "me-button",
                      {
                        staticClass: "dropdown-date-form-actions__apply",
                        attrs: {
                          disabled:
                            _vm.isCustomPeriodSelected && _vm.isInvalidDates,
                          size: "sm",
                          variant: "primary"
                        },
                        on: { click: _vm.applyFilter }
                      },
                      [
                        _vm._v(
                          "\n          " +
                            _vm._s(
                              _vm.$toolkit.t(
                                "dropdownCriterion.dropdownDate.formActions.apply"
                              )
                            ) +
                            "\n        "
                        )
                      ]
                    )
                  ],
                  1
                )
              ]
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$1j = [];
__vue_render__$1j._withStripped = true;

  /* style */
  const __vue_inject_styles__$1j = function (inject) {
    if (!inject) return
    inject("data-v-d48620f6_0", { source: ".dropdown-options[data-v-d48620f6] {\n  padding: 0 16px;\n}\n.dropdown-date-form-actions[data-v-d48620f6] {\n  border-top: 1px solid var(--me-gray-6);\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 8px;\n  padding: 8px 16px 0 16px;\n}\n.dropdown-date-form-actions__cancel[data-v-d48620f6], .dropdown-date-form-actions__apply[data-v-d48620f6] {\n  border-radius: 8px !important;\n}\n\n/*# sourceMappingURL=DropdownDateForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dropdown-criterion/dropdown-date-form/DropdownDateForm.vue","DropdownDateForm.vue"],"names":[],"mappings":"AAoWA;EACA,eAAA;ACnWA;ADsWA;EACA,sCAAA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,QAAA;EACA,wBAAA;ACnWA;ADqWA;EAEA,6BAAA;ACpWA;;AAEA,+CAA+C","file":"DropdownDateForm.vue","sourcesContent":["<template>\n  <DropdownCriterion\n    @edit=\"$refs.rangeDateDropDownGrid.show()\"\n    @remove=\"clearRangeDate\"\n    :hide-actions=\"hideActions\"\n    :hide-remove=\"hideAllPeriod || isAllPeriodSelected\"\n  >\n    <template v-slot:dropdownCriterion>\n      <me-drop-down\n        ref=\"rangeDateDropDownGrid\"\n        :items=\"[]\"\n        :text=\"dropdownLabel\"\n        :size=\"size\"\n        :backdrop=\"backdrop\"\n        :variant=\"variant\"\n        @hide=\"hideRangeDateDropdown\"\n      >\n        <div class=\"dropdown-options\">\n          <DateOptions\n            :value=\"currentSelectedOptionId\"\n            :options=\"dateOptions\"\n            :name=\"name\"\n            @input=\"updateSelectedDate\"\n          />\n\n          <CustomDateFields\n            v-show=\"!hideCustomPeriod && isCustomPeriodSelected\"\n            :initialDate=\"currentIniDate\"\n            :endDate=\"currentEndDate\"\n            :validateInitialDate=\"validateInitialDate\"\n            :validateEndDate=\"validateEndDate\"\n            :invalidInitialDateFeedback=\"invalidInitialDateFeedback\"\n            :invalidEndDateFeedback=\"invalidEndDateFeedback\"\n            @changeInitialDate=\"(val) => currentIniDate = val\"\n            @changeEndDate=\"(val) => currentEndDate = val\"\n            @onOpenStartCalendar=\"onOpenCalendar('startDateCalendar')\"\n            @onCloseStartCalendar=\"onCloseCalendar('startDateCalendar')\"\n            @onOpenEndCalendar=\"onOpenCalendar('endDateCalendar')\"\n            @onCloseEndCalendar=\"onCloseCalendar('endDateCalendar')\"\n          />\n        </div>\n\n        <div class=\"dropdown-date-form-actions\">\n          <me-button\n            @click=\"cancelFilter\"\n            size=\"sm\"\n            variant=\"light-primary\"\n            class=\"dropdown-date-form-actions__cancel\"\n          >\n            {{ $toolkit.t('dropdownCriterion.dropdownDate.formActions.cancel') }}\n          </me-button>\n\n          <me-button\n            :disabled=\"isCustomPeriodSelected && isInvalidDates\"\n            @click=\"applyFilter\"\n            size=\"sm\"\n            variant=\"primary\"\n            class=\"dropdown-date-form-actions__apply\"\n          >\n            {{ $toolkit.t('dropdownCriterion.dropdownDate.formActions.apply') }}\n          </me-button>\n        </div>\n      </me-drop-down>\n    </template>\n  </DropdownCriterion>\n</template>\n\n<script>\nimport Vue from 'vue'\nimport curry from 'lodash/curry'\nimport flowRight from 'lodash/flowRight'\nimport DropdownCriterion from '../DropdownCriterion.vue'\nimport DateOptions from './DateOptions.vue'\nimport CustomDateFields from './CustomDateFields.vue'\n\nconst calendarKeys = ['startDate', 'endDate']\nconst rangeDateEnum = {\n  ALL: 'all',\n  CUSTOM: 'custom',\n  HALF_MONTH: 'half_month',\n  ONE_MONTH: 'one_month',\n  TWO_MONTHS: 'two_months',\n  THREE_MONTHS: 'three_months',\n  ONE_YEAR: 'one_year'\n}\n\nexport default {\n  name: 'MeDropdownDateForm',\n  components: {\n    DropdownCriterion,\n    DateOptions,\n    CustomDateFields\n  },\n\n  props: {\n    name: {\n      type: String,\n      required: true\n    },\n    initialDate: {\n      type: [String, Date],\n      default: null\n    },\n    endDate: {\n      type: [String, Date],\n      default: null\n    },\n    hideAllPeriod: {\n      type: Boolean,\n      default: false\n    },\n    hideCustomPeriod: {\n      type: Boolean,\n      default: false\n    },\n    hideActions: {\n      type: Boolean,\n      default: false\n    },\n    backdrop: {\n      type: Boolean,\n      default: false\n    },\n    invalidInitialDateFeedback: {\n      type: String,\n      default: ''\n    },\n    invalidEndDateFeedback: {\n      type: String,\n      default: ''\n    },\n    validateInitialDate: {\n      type: Function,\n      default: () => null\n    },\n    validateEndDate: {\n      type: Function,\n      default: () => null\n    },\n    variant: {\n      type: String,\n      default: 'outline-primary'\n    },\n    size: {\n      type: String,\n      default: 'md',\n      validator: value => ['sm', 'md', 'lg'].includes(value)\n    }\n  },\n\n  data() {\n    return {\n      meDateFilter: Vue.filter('me-date'),\n      dropdownLabel: '',\n      appliedOptionId: null,\n      currentSelectedOptionId: null,\n      currentIniDate: this.initialDate ? new Date(this.initialDate) : null,\n      currentEndDate: this.endDate ? new Date(this.endDate) : null,\n      canCloseDropdown: true,\n      calendarsOpenedMap: new Map(calendarKeys.map(key => [key, false]))\n    }\n  },\n\n  computed: {\n    dateOptions() {\n      return [\n        { id: rangeDateEnum.HALF_MONTH, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastFifteenDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 15)) },\n        { id: rangeDateEnum.ONE_MONTH, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastThirtyDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 30)) },\n        { id: rangeDateEnum.TWO_MONTHS, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastSixtyDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 60)) },\n        { id: rangeDateEnum.THREE_MONTHS, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastNinetyDays'), value: this.setTimeToZero(this.subtractDays(new Date(), 90)) },\n        { id: rangeDateEnum.ONE_YEAR, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.lastTwelveMonths'), value: this.setTimeToZero(this.subtractYears(new Date(), 1)) },\n        this.hideAllPeriod ? null : { id: rangeDateEnum.ALL, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.all') },\n        this.hideCustomPeriod ? null : { id: rangeDateEnum.CUSTOM, label: this.$toolkit.t('dropdownCriterion.dropdownDate.dateOptions.specificPeriod') }\n      ].filter(option => option)\n    },\n\n    hasSomeCalendarOpen() {\n      return [...this.calendarsOpenedMap.values()].some(isOpened => isOpened)\n    },\n\n    isCustomPeriodSelected() {\n      return this.currentSelectedOptionId === rangeDateEnum.CUSTOM\n    },\n\n    isAllPeriodSelected() {\n      return this.currentSelectedOptionId === rangeDateEnum.ALL\n    },\n\n    isPredefinedDateSelected() {\n      return !this.isCustomPeriodSelected && !this.isAllPeriodSelected\n    },\n\n    isInvalidDates() {\n      return this.isInitialDateInvalid || this.isEndDateInvalid\n    },\n\n    isInitialDateInvalid() {\n      return this.validateInitialDate(this.currentIniDate, this.currentEndDate) === false\n    },\n\n    isEndDateInvalid() {\n      return this.validateEndDate(this.currentIniDate, this.currentEndDate) === false\n    },\n\n    findInDateOptions() {\n      return this.finder(this.dateOptions)\n    }\n  },\n\n  watch: {\n    initialDate(val) {\n      this.currentIniDate = val ? new Date(val) : null\n      this.resetStates()\n    },\n\n    endDate(val) {\n      this.currentEndDate = val ? new Date(val) : null\n      this.resetStates()\n    }\n  },\n\n  created() {\n    this.resetStates()\n  },\n\n  methods: {\n    resetStates() {\n      this.appliedOptionId = this.getInitialAppliedOptionId(this.currentIniDate, this.currentEndDate)\n      this.currentSelectedOptionId = this.appliedOptionId\n      this.dropdownLabel = this.getDropdownLabel()\n    },\n\n    getInitialAppliedOptionId(iniDate, endDate) {\n      if (!iniDate && !endDate) return rangeDateEnum.ALL\n\n      if (endDate) return rangeDateEnum.CUSTOM\n\n      const findValue = flowRight(this.findInDateOptions('value'), this.setTimeToZero)\n\n      const predefinedDateId = findValue(iniDate)?.id\n      if (predefinedDateId) return predefinedDateId\n\n      return rangeDateEnum.CUSTOM\n    },\n\n    getDropdownLabel() {\n      if (this.isAllPeriodSelected) {\n        return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.allPeriod')\n      }\n\n      if (this.currentIniDate && this.currentEndDate) {\n        return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.between', this.meDateFilter(this.currentIniDate), this.meDateFilter(this.currentEndDate))\n      }\n\n      if (this.currentEndDate) {\n        return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.until', this.meDateFilter(this.currentEndDate))\n      }\n\n      if (this.isPredefinedDateSelected) {\n        return this.findInDateOptions('id')(this.appliedOptionId)?.label\n      }\n\n      return this.$toolkit.t('dropdownCriterion.dropdownDate.dateLabel.since', this.meDateFilter(this.currentIniDate))\n    },\n\n    finder: curry((list, prop, lookingValue) => {\n      return list.find(option => option[prop] === lookingValue)\n    }),\n\n    updateSelectedDate(value) {\n      this.currentSelectedOptionId = value\n    },\n\n    applyFilter() {\n      this.setRangeDate()\n      this.appliedOptionId = this.currentSelectedOptionId\n      this.dropdownLabel = this.getDropdownLabel()\n      this.closeRangeDateForm()\n    },\n\n    cancelFilter() {\n      this.closeRangeDateForm()\n    },\n\n    setRangeDate() {\n      if (this.isAllPeriodSelected) {\n        this.currentIniDate = null\n        this.currentEndDate = null\n      }\n\n      if (this.isPredefinedDateSelected) {\n        this.currentIniDate = new Date(this.findInDateOptions('id')(this.currentSelectedOptionId)?.value)\n        this.currentEndDate = null\n      }\n\n      this.$emit('change', { initialDate: this.currentIniDate, endDate: this.currentEndDate })\n    },\n\n    onOpenCalendar(calendarKey) {\n      this.canCloseDropdown = false\n      this.updateCalendarOpened(calendarKey, true)\n    },\n\n    onCloseCalendar(calendarKey) {\n      this.updateCalendarOpened(calendarKey, false)\n    },\n\n    updateCalendarOpened(calendarKey, isOpened) {\n      const calendarsOpenedMapClone = new Map(this.calendarsOpenedMap)\n      calendarsOpenedMapClone.set(calendarKey, isOpened)\n\n      this.calendarsOpenedMap = new Map(calendarsOpenedMapClone)\n    },\n\n    closeRangeDateForm() {\n      this.calendarsOpenedMap = new Map(calendarKeys.map(key => [key, false]))\n      this.canCloseDropdown = true\n      document.querySelector('body').click()\n    },\n\n    hideRangeDateDropdown(e) {\n      if (this.hasSomeCalendarOpen) {\n        e.preventDefault()\n        return\n      }\n\n      if (!this.canCloseDropdown) {\n        e.preventDefault()\n        this.canCloseDropdown = true\n        return\n      }\n\n      this.currentSelectedOptionId = this.appliedOptionId\n    },\n\n    clearRangeDate() {\n      this.currentSelectedOptionId = rangeDateEnum.ALL\n      this.applyFilter()\n    },\n\n    subtractDays(date, days) {\n      return new Date(date).setDate(date.getDate() - days)\n    },\n\n    subtractYears(date, years) {\n      return new Date(date).setFullYear(date.getFullYear() - years)\n    },\n\n    setTimeToZero(date) {\n      return new Date(date).setHours(0, 0, 0, 0)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .dropdown-options {\n    padding: 0 16px;\n  }\n\n  .dropdown-date-form-actions {\n    border-top: 1px solid var(--me-gray-6);\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    gap: 8px;\n    padding: 8px 16px 0 16px;\n\n    &__cancel,\n    &__apply {\n      border-radius: 8px !important;\n    }\n  }\n</style>\n",".dropdown-options {\n  padding: 0 16px;\n}\n\n.dropdown-date-form-actions {\n  border-top: 1px solid var(--me-gray-6);\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 8px;\n  padding: 8px 16px 0 16px;\n}\n.dropdown-date-form-actions__cancel, .dropdown-date-form-actions__apply {\n  border-radius: 8px !important;\n}\n\n/*# sourceMappingURL=DropdownDateForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1j = "data-v-d48620f6";
  /* module identifier */
  const __vue_module_identifier__$1j = undefined;
  /* functional template */
  const __vue_is_functional_template__$1j = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1j, staticRenderFns: __vue_staticRenderFns__$1j },
    __vue_inject_styles__$1j,
    __vue_script__$1j,
    __vue_scope_id__$1j,
    __vue_is_functional_template__$1j,
    __vue_module_identifier__$1j,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1k = {
  name: 'MeDropdownRadioForm',
  components: {
    DropdownCriterion: __vue_component__$1g
  },

  props: {
    name: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: ''
    },
    initialLabel: {
      type: String,
      default: ''
    },
    valueField: {
      type: String,
      default: 'value'
    },
    textField: {
      type: String,
      default: 'name'
    },
    disabledField: {
      type: String,
      default: 'disabled'
    },
    formTitle: {
      type: String,
      default: ''
    },
    backdrop: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'outline-primary'
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    }
  },

  data() {
    return {
      isActionsVisible: false,
      currentValue: cloneDeep_1(this.value)
    }
  },

  computed: {
    dropdownLabel() {
      return this.items.find(item => item[this.valueField] === this.currentValue)?.[this.textField] || ''
    }
  },

  watch: {
    value(newValue) {
      this.currentValue = newValue;
    }
  },

  methods: {
    onSelectOption(selectedValue) {
      this.$emit('input', selectedValue);
      this.$refs.dropdownRadioForm.hide();
    }
  }
};

/* script */
const __vue_script__$1k = script$1k;

/* template */
var __vue_render__$1k = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("DropdownCriterion", {
    attrs: { "hide-actions": _vm.hideActions, "hide-remove": "" },
    on: {
      edit: function($event) {
        return _vm.$refs.dropdownRadioForm.show()
      }
    },
    scopedSlots: _vm._u([
      {
        key: "dropdownCriterion",
        fn: function() {
          return [
            _c(
              "me-drop-down",
              {
                ref: "dropdownRadioForm",
                attrs: {
                  items: [],
                  size: _vm.size,
                  backdrop: _vm.backdrop,
                  text: _vm.dropdownLabel || _vm.initialLabel,
                  variant: _vm.variant
                }
              },
              [
                _c(
                  "div",
                  { staticClass: "dropdown-options" },
                  [
                    _c("me-radio-input", {
                      attrs: {
                        options: _vm.items,
                        name: _vm.name,
                        valueField: _vm.valueField,
                        textField: _vm.textField,
                        disabledField: _vm.disabledField,
                        hint:
                          _vm.formTitle ||
                          _vm.$toolkit.t(
                            "dropdownCriterion.dropdownRadio.formTitle"
                          ),
                        stacked: ""
                      },
                      on: {
                        input: function() {
                          return _vm.onSelectOption(_vm.currentValue)
                        }
                      },
                      model: {
                        value: _vm.currentValue,
                        callback: function($$v) {
                          _vm.currentValue = $$v;
                        },
                        expression: "currentValue"
                      }
                    })
                  ],
                  1
                )
              ]
            )
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$1k = [];
__vue_render__$1k._withStripped = true;

  /* style */
  const __vue_inject_styles__$1k = function (inject) {
    if (!inject) return
    inject("data-v-7c9c7c6c_0", { source: ".dropdown-options[data-v-7c9c7c6c] {\n  padding: 0 16px;\n  width: max-content;\n  max-width: 248px;\n}\n.dropdown-options .form-group[data-v-7c9c7c6c] {\n  margin-bottom: 0;\n}\n\n/*# sourceMappingURL=DropdownRadioForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dropdown-criterion/dropdown-radio-form/DropdownRadioForm.vue","DropdownRadioForm.vue"],"names":[],"mappings":"AA4HA;EACA,eAAA;EACA,kBAAA;EACA,gBAAA;AC3HA;AD6HA;EACA,gBAAA;AC3HA;;AAEA,gDAAgD","file":"DropdownRadioForm.vue","sourcesContent":["<template>\n  <DropdownCriterion\n    @edit=\"$refs.dropdownRadioForm.show()\"\n    :hide-actions=\"hideActions\"\n    hide-remove\n  >\n    <template v-slot:dropdownCriterion>\n      <me-drop-down\n        ref=\"dropdownRadioForm\"\n        :items=\"[]\"\n        :size=\"size\"\n        :backdrop=\"backdrop\"\n        :text=\"dropdownLabel || initialLabel\"\n        :variant=\"variant\"\n      >\n        <div class=\"dropdown-options\">\n          <me-radio-input\n            v-model=\"currentValue\"\n            :options=\"items\"\n            :name=\"name\"\n            :valueField=\"valueField\"\n            :textField=\"textField\"\n            :disabledField=\"disabledField\"\n            :hint=\"formTitle || $toolkit.t('dropdownCriterion.dropdownRadio.formTitle')\"\n            @input=\"() => onSelectOption(currentValue)\"\n            stacked\n          />\n        </div>\n      </me-drop-down>\n    </template>\n  </DropdownCriterion>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport DropdownCriterion from '../DropdownCriterion.vue'\n\nexport default {\n  name: 'MeDropdownRadioForm',\n  components: {\n    DropdownCriterion\n  },\n\n  props: {\n    name: {\n      type: String,\n      required: true\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    value: {\n      type: String,\n      default: ''\n    },\n    initialLabel: {\n      type: String,\n      default: ''\n    },\n    valueField: {\n      type: String,\n      default: 'value'\n    },\n    textField: {\n      type: String,\n      default: 'name'\n    },\n    disabledField: {\n      type: String,\n      default: 'disabled'\n    },\n    formTitle: {\n      type: String,\n      default: ''\n    },\n    backdrop: {\n      type: Boolean,\n      default: false\n    },\n    variant: {\n      type: String,\n      default: 'outline-primary'\n    },\n    hideActions: {\n      type: Boolean,\n      default: false\n    },\n    size: {\n      type: String,\n      default: 'md',\n      validator: value => ['sm', 'md', 'lg'].includes(value)\n    }\n  },\n\n  data() {\n    return {\n      isActionsVisible: false,\n      currentValue: cloneDeep(this.value)\n    }\n  },\n\n  computed: {\n    dropdownLabel() {\n      return this.items.find(item => item[this.valueField] === this.currentValue)?.[this.textField] || ''\n    }\n  },\n\n  watch: {\n    value(newValue) {\n      this.currentValue = newValue\n    }\n  },\n\n  methods: {\n    onSelectOption(selectedValue) {\n      this.$emit('input', selectedValue)\n      this.$refs.dropdownRadioForm.hide()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .dropdown-options {\n    padding: 0 16px;\n    width: max-content;\n    max-width: 248px;\n\n    .form-group {\n      margin-bottom: 0;\n    }\n  }\n</style>\n",".dropdown-options {\n  padding: 0 16px;\n  width: max-content;\n  max-width: 248px;\n}\n.dropdown-options .form-group {\n  margin-bottom: 0;\n}\n\n/*# sourceMappingURL=DropdownRadioForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1k = "data-v-7c9c7c6c";
  /* module identifier */
  const __vue_module_identifier__$1k = undefined;
  /* functional template */
  const __vue_is_functional_template__$1k = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1k, staticRenderFns: __vue_staticRenderFns__$1k },
    __vue_inject_styles__$1k,
    __vue_script__$1k,
    __vue_scope_id__$1k,
    __vue_is_functional_template__$1k,
    __vue_module_identifier__$1k,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const listSideEnum = {
  RIGHT_SIDE_LIST: 'rightSideList',
  LEFT_SIDE_LIST: 'leftSideList',
  RIGHT_SELECTED_LIST: 'rightSelectedList',
  LEFT_SELECTED_LIST: 'leftSelectedList'
};

var script$1l = {
  name: 'MeDualList',

  components: {
    BTable,
    Button: __vue_component__$6,
    Draggable: __vue_component__$s,
    Icon: __vue_component__,
    TextInput: __vue_component__$p
  },

  props: {
    group: {
      type: String,
      default: 'dualList'
    },
    leftList: {
      type: Array,
      required: true
    },
    rightList: {
      type: Array,
      required: true
    },
    leftTitle: {
      type: String,
      default: ''
    },
    rightTitle: {
      type: String,
      default: ''
    },
    emptyListMessage: {
      type: String,
      default: ''
    },
    searchFieldPlaceholder: {
      type: String,
      default: ''
    },
    leftPlaceholder: {
      type: String,
      default: ''
    },
    rightPlaceholder: {
      type: String,
      default: ''
    },
    selectable: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    selectAndRemoveAll: {
      type: Boolean,
      default: false
    },
    selectAllText: {
      type: String,
      default: ''
    },
    removeAllText: {
      type: String,
      default: ''
    },
    cleanSearchable: {
      type: Boolean,
      default: true
    },
    leftSideRequiredLength: {
      type: Number,
      default: 0
    },
    rightSideRequiredLength: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      listSideEnum,
      leftSearchTerm: '',
      leftSelectedList: [],
      leftSideList: [],
      leftTempList: [],
      rightSearchTerm: '',
      rightSelectedList: [],
      rightSideList: []
    }
  },

  computed: {
    filteredRightSideList() {
      return this.getFilteredList(this.rightSearchTerm, this.rightSideList)
    },

    filteredLeftSideList() {
      return this.getFilteredList(this.leftSearchTerm, this.leftSideList)
    }
  },

  watch: {
    leftList(value) {
      this.leftSideList = cloneDeep_1(value);
    },

    rightList(value) {
      this.rightSideList = cloneDeep_1(value);
    }
  },

  mounted() {
    this.leftSideList = cloneDeep_1(this.leftList);
    this.rightSideList = cloneDeep_1(this.rightList);
  },

  methods: {
    getFilteredList(term, list) {
      if (term) {
        const searchTerm = new RegExp(term, 'i');
        return list.filter(item => item.field.match(searchTerm))
      }
      return list
    },

    clearSearchable(searchTerm) {
      if (this.cleanSearchable) this[searchTerm] = '';
    },

    addItem(item) {
      this.$emit('on-add', item);
      this.clearSearchable('leftSearchTerm');
    },

    removeItem(item) {
      this.$emit('on-remove', item);
      this.clearSearchable('rightSearchTerm');
    },

    onDrag() {
      this.$emit('on-drag', {
        rightList: this.rightSideList,
        leftList: this.leftSideList
      });
    },

    updateListAfterSearchAndDrag({ fromList, toList }, itemData) {
      if (!this.leftSearchTerm && !this.rightSearchTerm) {
        return this.onDrag()
      }

      const { removed } = itemData;
      if (!removed) return

      const itemMoved = removed.element;
      this[fromList] = this[fromList].filter(item => item.id !== itemMoved.id);
      this[toList] = this.removeDuplicate([...this[toList], itemMoved]);

      if (this.cleanSearchable) {
        const searchTerm = fromList === listSideEnum.LEFT_SIDE_LIST ? 'leftSearchTerm' : 'rightSearchTerm';
        this.clearSearchable(searchTerm);
      }

      return this.onDrag()
    },

    removeDuplicate(arrayOfObject) {
      return arrayOfObject.filter((element, index, self) =>
        index === self.findIndex(t => (t.id === element.id)))
    },

    onLeftSideSelected(items) {
      this.leftSelectedList = items;
    },

    onRightSideSelected(items) {
      this.rightSelectedList = items;
    },

    moveSelected(fromList) {
      const direction = fromList === listSideEnum.LEFT_SELECTED_LIST ? 'right' : 'left';
      this.moveList(this[fromList], direction);
    },

    moveAll(fromList) {
      const direction = fromList === listSideEnum.LEFT_SIDE_LIST ? 'right' : 'left';
      this.moveList(this[fromList], direction);
    },

    moveList(list, direction) {
      list.forEach((item) => {
        direction === 'right' ? this.addItem(item) : this.removeItem(item);
      });
    }
  }
};

/* script */
const __vue_script__$1l = script$1l;

/* template */
var __vue_render__$1l = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-dual-list" },
    [
      _c(
        "div",
        {
          staticClass: "list mr-2",
          class: { error: _vm.leftSideList.length < _vm.leftSideRequiredLength }
        },
        [
          _c(
            "div",
            { staticClass: "list-header" },
            [
              _vm.leftTitle || _vm.rightTitle
                ? _c("div", { staticClass: "title" }, [
                    _vm._v("\n        " + _vm._s(_vm.leftTitle) + "\n      ")
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm.selectAndRemoveAll
                ? _c(
                    "Button",
                    {
                      class: { disabled: !_vm.leftSideList.length },
                      attrs: { variant: "light-primary", size: "sm" },
                      on: {
                        click: function($event) {
                          return _vm.moveAll(_vm.listSideEnum.LEFT_SIDE_LIST)
                        }
                      }
                    },
                    [
                      _c("icon", { attrs: { icon: "me-icon-l icon-plus" } }),
                      _vm._v(" " + _vm._s(_vm.selectAllText) + "\n      ")
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "list-left" },
            [
              _vm.searchable
                ? _c(
                    "div",
                    { staticClass: "list-search" },
                    [
                      _c("icon", { attrs: { icon: "me-icon-l icon-search" } }),
                      _vm._v(" "),
                      _c("text-input", {
                        attrs: {
                          placeholder:
                            _vm.leftPlaceholder || _vm.searchFieldPlaceholder
                        },
                        model: {
                          value: _vm.leftSearchTerm,
                          callback: function($$v) {
                            _vm.leftSearchTerm = $$v;
                          },
                          expression: "leftSearchTerm"
                        }
                      })
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              !_vm.selectable
                ? _c(
                    "draggable",
                    {
                      staticClass: "dual-list",
                      class: {
                        "empty-bg":
                          _vm.emptyListMessage && !_vm.leftSideList.length
                      },
                      attrs: {
                        group: _vm.group,
                        list: _vm.filteredLeftSideList,
                        draggable: ".dual-list--items",
                        "ghost-class": "on-drag"
                      },
                      on: {
                        change: function($event) {
                          return _vm.updateListAfterSearchAndDrag(
                            {
                              fromList: _vm.listSideEnum.LEFT_SIDE_LIST,
                              toList: _vm.listSideEnum.RIGHT_SIDE_LIST
                            },
                            $event
                          )
                        }
                      }
                    },
                    [
                      _vm._l(_vm.filteredLeftSideList, function(item) {
                        return _c(
                          "div",
                          { key: item.id, staticClass: "dual-list--items" },
                          [
                            _c("div", { staticClass: "label" }, [
                              _vm._v(
                                "\n            " +
                                  _vm._s(item.field) +
                                  "\n          "
                              )
                            ]),
                            _vm._v(" "),
                            _c(
                              "div",
                              { staticClass: "icons" },
                              [
                                _vm._t("iconsLeft"),
                                _vm._v(" "),
                                _c("icon", {
                                  attrs: { icon: "me-icon-l icon-plus" },
                                  on: {
                                    click: function($event) {
                                      return _vm.addItem(item)
                                    }
                                  }
                                })
                              ],
                              2
                            )
                          ]
                        )
                      }),
                      _vm._v(" "),
                      _vm.emptyListMessage && !_vm.leftSideList.length
                        ? _c("div", { staticClass: "empty-text" }, [
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.emptyListMessage) +
                                "\n        "
                            )
                          ])
                        : _vm._e()
                    ],
                    2
                  )
                : _c("b-table", {
                    class: {
                      "empty-bg":
                        _vm.emptyListMessage && !_vm.leftSideList.length
                    },
                    attrs: {
                      fields: ["field"],
                      items: _vm.filteredLeftSideList,
                      borderless: "",
                      responsive: "",
                      selectable: "",
                      "tbody-class": "without-strip",
                      "thead-class": "hidden_header"
                    },
                    on: { "row-selected": _vm.onLeftSideSelected },
                    scopedSlots: _vm._u([
                      {
                        key: "cell(field)",
                        fn: function(ref) {
                          var item = ref.item;
                          return [
                            _c("div", { staticClass: "selectable-items" }, [
                              _vm._v(
                                "\n            " +
                                  _vm._s(item.field) +
                                  "\n          "
                              )
                            ])
                          ]
                        }
                      }
                    ])
                  })
            ],
            1
          ),
          _vm._v(" "),
          _c("span", { staticClass: "invalid-feedback" }, [
            _vm._v(_vm._s(_vm.$toolkit.t("dualList.invalidFeedback")))
          ])
        ]
      ),
      _vm._v(" "),
      _vm.selectable
        ? _c("div", { staticClass: "actions" }, [
            _c(
              "div",
              { staticClass: "add-action-buttons" },
              [
                _c("icon", {
                  class: { disabled: !_vm.leftSelectedList.length },
                  attrs: { icon: "me-icon-l icon-chevron-right" },
                  on: {
                    click: function($event) {
                      return _vm.moveSelected(
                        _vm.listSideEnum.LEFT_SELECTED_LIST
                      )
                    }
                  }
                }),
                _vm._v(" "),
                _c("icon", {
                  class: { disabled: !_vm.leftSideList.length },
                  attrs: { icon: "me-icon-l icon-chevron-double-right" },
                  on: {
                    click: function($event) {
                      return _vm.moveAll(_vm.listSideEnum.LEFT_SIDE_LIST)
                    }
                  }
                })
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "remove-action-buttons" },
              [
                _c("icon", {
                  class: { disabled: !_vm.rightSelectedList.length },
                  attrs: { icon: "me-icon-l icon-chevron-left" },
                  on: {
                    click: function($event) {
                      return _vm.moveSelected(
                        _vm.listSideEnum.RIGHT_SELECTED_LIST
                      )
                    }
                  }
                }),
                _vm._v(" "),
                _c("icon", {
                  class: { disabled: !_vm.rightSideList.length },
                  attrs: { icon: "me-icon-l icon-chevron-double-left" },
                  on: {
                    click: function($event) {
                      return _vm.moveAll(_vm.listSideEnum.RIGHT_SIDE_LIST)
                    }
                  }
                })
              ],
              1
            )
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.selectAndRemoveAll
        ? _c("icon", { attrs: { icon: "me-icon-l icon-exchange" } })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        {
          staticClass: "list ml-2",
          class: {
            error: _vm.rightSideList.length < _vm.rightSideRequiredLength
          }
        },
        [
          _c(
            "div",
            { staticClass: "list-header" },
            [
              _vm.leftTitle || _vm.rightTitle
                ? _c("div", { staticClass: "title" }, [
                    _vm._v("\n        " + _vm._s(_vm.rightTitle) + "\n      ")
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm.selectAndRemoveAll
                ? _c(
                    "Button",
                    {
                      class: { disabled: !_vm.rightSideList.length },
                      attrs: { variant: "light-primary", size: "sm" },
                      on: {
                        click: function($event) {
                          return _vm.moveAll(_vm.listSideEnum.RIGHT_SIDE_LIST)
                        }
                      }
                    },
                    [
                      _c("icon", { attrs: { icon: "me-icon-l icon-times" } }),
                      _vm._v(" " + _vm._s(_vm.removeAllText) + "\n      ")
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "list-right" },
            [
              _vm.searchable
                ? _c(
                    "div",
                    { staticClass: "list-search" },
                    [
                      _c("icon", { attrs: { icon: "me-icon-l icon-search" } }),
                      _vm._v(" "),
                      _c("text-input", {
                        attrs: {
                          placeholder:
                            _vm.rightPlaceholder || _vm.searchFieldPlaceholder
                        },
                        model: {
                          value: _vm.rightSearchTerm,
                          callback: function($$v) {
                            _vm.rightSearchTerm = $$v;
                          },
                          expression: "rightSearchTerm"
                        }
                      })
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              !_vm.selectable
                ? _c(
                    "draggable",
                    {
                      staticClass: "dual-list",
                      class: {
                        "empty-bg":
                          _vm.emptyListMessage && !_vm.rightSideList.length
                      },
                      attrs: {
                        group: _vm.group,
                        list: _vm.filteredRightSideList,
                        draggable: ".dual-list--items",
                        "ghost-class": "on-drag"
                      },
                      on: {
                        change: function($event) {
                          return _vm.updateListAfterSearchAndDrag(
                            {
                              fromList: _vm.listSideEnum.RIGHT_SIDE_LIST,
                              toList: _vm.listSideEnum.LEFT_SIDE_LIST
                            },
                            $event
                          )
                        }
                      }
                    },
                    [
                      _vm._l(_vm.filteredRightSideList, function(item) {
                        return _c(
                          "div",
                          { key: item.id, staticClass: "dual-list--items" },
                          [
                            _vm._v(
                              "\n          " +
                                _vm._s(item.field) +
                                "\n          "
                            ),
                            _c(
                              "div",
                              { staticClass: "icons" },
                              [
                                _vm._t("iconsRight"),
                                _vm._v(" "),
                                _c("icon", {
                                  attrs: { icon: "me-icon-l icon-times" },
                                  on: {
                                    click: function($event) {
                                      return _vm.removeItem(item)
                                    }
                                  }
                                })
                              ],
                              2
                            )
                          ]
                        )
                      }),
                      _vm._v(" "),
                      _vm.emptyListMessage && !_vm.rightSideList.length
                        ? _c("div", { staticClass: "empty-text" }, [
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.emptyListMessage) +
                                "\n        "
                            )
                          ])
                        : _vm._e()
                    ],
                    2
                  )
                : _c("b-table", {
                    class: {
                      "empty-bg":
                        _vm.emptyListMessage && !_vm.rightSideList.length
                    },
                    attrs: {
                      items: _vm.filteredRightSideList,
                      fields: ["field"],
                      selectable: "",
                      borderless: "",
                      responsive: "",
                      "tbody-class": "without-strip",
                      "thead-class": "hidden_header"
                    },
                    on: { "row-selected": _vm.onRightSideSelected },
                    scopedSlots: _vm._u([
                      {
                        key: "cell(field)",
                        fn: function(ref) {
                          var item = ref.item;
                          return [
                            _c("div", { staticClass: "selectable-items" }, [
                              _vm._v(
                                "\n            " +
                                  _vm._s(item.field) +
                                  "\n          "
                              )
                            ])
                          ]
                        }
                      }
                    ])
                  })
            ],
            1
          ),
          _vm._v(" "),
          _c("span", { staticClass: "invalid-feedback" }, [
            _vm._v(_vm._s(_vm.$toolkit.t("dualList.invalidFeedback")))
          ])
        ]
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1l = [];
__vue_render__$1l._withStripped = true;

  /* style */
  const __vue_inject_styles__$1l = function (inject) {
    if (!inject) return
    inject("data-v-3f0f71f6_0", { source: ".me-dual-list[data-v-3f0f71f6] {\n  display: flex;\n  font-size: 14px;\n}\n.me-dual-list .list[data-v-3f0f71f6] {\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  position: relative;\n  width: 100%;\n}\n.me-dual-list .list.error .list-left[data-v-3f0f71f6],\n.me-dual-list .list.error .list-right[data-v-3f0f71f6] {\n  border: 1px solid var(--me-danger-3);\n}\n.me-dual-list .list.error .invalid-feedback[data-v-3f0f71f6] {\n  display: block;\n}\n.me-dual-list .list-header[data-v-3f0f71f6] {\n  align-items: center;\n  display: flex;\n  font-size: 12px;\n  justify-content: space-between;\n  margin-bottom: 8px;\n}\n.me-dual-list .list-header .title[data-v-3f0f71f6] {\n  font-weight: 600;\n}\n.me-dual-list .list-header .me-button[data-v-3f0f71f6] {\n  font-size: 12px;\n  min-height: 32px;\n  box-shadow: none;\n}\n.me-dual-list .list-search[data-v-3f0f71f6] {\n  border-bottom: 1px solid var(--me-gray-6);\n  position: relative;\n}\n.me-dual-list .list-search[data-v-3f0f71f6]  .form-control {\n  border: none;\n  text-indent: 24px;\n  z-index: 20;\n}\n.me-dual-list .list-search[data-v-3f0f71f6]  .form-control:focus {\n  box-shadow: none;\n  outline: 0;\n}\n.me-dual-list .list-search i[data-v-3f0f71f6] {\n  background: var(--me-white);\n  color: var(--me-gray-3);\n  font-size: 16px;\n  left: 8px;\n  position: absolute;\n  top: 8px;\n  width: 24px;\n  z-index: 21;\n}\n.me-dual-list .list-left .dual-list--items[data-v-3f0f71f6]:hover {\n  background-color: var(--me-primary-7);\n}\n.me-dual-list .list-left .dual-list--items:hover .icons .me-icon-l[data-v-3f0f71f6] {\n  color: var(--me-primary-2);\n  cursor: pointer;\n}\n.me-dual-list .list-left[data-v-3f0f71f6]  .table-responsive tr:hover, .me-dual-list .list-left[data-v-3f0f71f6]  .table-responsive tr.table-active {\n  background-color: var(--me-primary-7);\n}\n.me-dual-list .list-right .dual-list--items[data-v-3f0f71f6]:hover {\n  background-color: var(--me-danger-7);\n}\n.me-dual-list .list-right .dual-list--items:hover .icons .me-icon-l[data-v-3f0f71f6] {\n  color: var(--me-danger-2);\n  cursor: pointer;\n}\n.me-dual-list .list-right[data-v-3f0f71f6]  .table-responsive tr:hover, .me-dual-list .list-right[data-v-3f0f71f6]  .table-responsive tr.table-active {\n  background-color: var(--me-danger-7);\n}\n.me-dual-list .list-left[data-v-3f0f71f6], .me-dual-list .list-right[data-v-3f0f71f6] {\n  background-color: var(--me-white);\n  border-radius: 8px;\n  border: 1px solid #00000014;\n}\n.me-dual-list .list-left .dual-list[data-v-3f0f71f6], .me-dual-list .list-right .dual-list[data-v-3f0f71f6] {\n  position: relative;\n}\n.me-dual-list .list-left .dual-list .label[data-v-3f0f71f6], .me-dual-list .list-right .dual-list .label[data-v-3f0f71f6] {\n  align-items: center;\n  display: flex;\n}\n.me-dual-list .list-left .dual-list--items[data-v-3f0f71f6], .me-dual-list .list-right .dual-list--items[data-v-3f0f71f6] {\n  color: var(--me-gray-1);\n  cursor: grabbing;\n  display: flex;\n  justify-content: space-between;\n  padding: 8px 14px;\n  width: 100%;\n}\n.me-dual-list .list-left .dual-list--items.on-drag[data-v-3f0f71f6], .me-dual-list .list-right .dual-list--items.on-drag[data-v-3f0f71f6] {\n  background-color: var(--me-gray-6);\n  opacity: 0.4;\n}\n.me-dual-list .list-left .dual-list--items .icons[data-v-3f0f71f6], .me-dual-list .list-right .dual-list--items .icons[data-v-3f0f71f6] {\n  display: none;\n}\n.me-dual-list .list-left .dual-list--items .icons *[data-v-3f0f71f6], .me-dual-list .list-right .dual-list--items .icons *[data-v-3f0f71f6] {\n  margin-left: 14px;\n}\n.me-dual-list .list-left .dual-list--items .icons .me-icon[data-v-3f0f71f6], .me-dual-list .list-right .dual-list--items .icons .me-icon[data-v-3f0f71f6] {\n  cursor: pointer;\n}\n.me-dual-list .list-left .dual-list--items:hover .icons[data-v-3f0f71f6], .me-dual-list .list-right .dual-list--items:hover .icons[data-v-3f0f71f6] {\n  display: flex;\n}\n.me-dual-list .list-left[data-v-3f0f71f6]  .table-responsive tr, .me-dual-list .list-right[data-v-3f0f71f6]  .table-responsive tr {\n  background-color: var(--me-white);\n}\n.me-dual-list .list-left[data-v-3f0f71f6]  .table-responsive tr .selectable-items, .me-dual-list .list-right[data-v-3f0f71f6]  .table-responsive tr .selectable-items {\n  color: var(--me-gray-1);\n  cursor: pointer;\n}\n.me-dual-list .list-left[data-v-3f0f71f6]  .table-responsive .hidden_header, .me-dual-list .list-right[data-v-3f0f71f6]  .table-responsive .hidden_header {\n  display: none;\n}\n.me-dual-list .list-left .dual-list[data-v-3f0f71f6],\n.me-dual-list .list-left .table-responsive[data-v-3f0f71f6], .me-dual-list .list-right .dual-list[data-v-3f0f71f6],\n.me-dual-list .list-right .table-responsive[data-v-3f0f71f6] {\n  height: 200px;\n  overflow: auto;\n}\n.me-dual-list .list-left .dual-list[data-v-3f0f71f6]::-webkit-scrollbar,\n.me-dual-list .list-left .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar, .me-dual-list .list-right .dual-list[data-v-3f0f71f6]::-webkit-scrollbar,\n.me-dual-list .list-right .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar {\n  height: 8px;\n  width: 8px;\n}\n.me-dual-list .list-left .dual-list[data-v-3f0f71f6]::-webkit-scrollbar-track,\n.me-dual-list .list-left .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar-track, .me-dual-list .list-right .dual-list[data-v-3f0f71f6]::-webkit-scrollbar-track,\n.me-dual-list .list-right .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar-track {\n  display: none;\n}\n.me-dual-list .list-left .dual-list[data-v-3f0f71f6]::-webkit-scrollbar-thumb,\n.me-dual-list .list-left .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar-thumb, .me-dual-list .list-right .dual-list[data-v-3f0f71f6]::-webkit-scrollbar-thumb,\n.me-dual-list .list-right .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\n.me-dual-list .list-left .dual-list[data-v-3f0f71f6]::-webkit-scrollbar-thumb:hover,\n.me-dual-list .list-left .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar-thumb:hover, .me-dual-list .list-right .dual-list[data-v-3f0f71f6]::-webkit-scrollbar-thumb:hover,\n.me-dual-list .list-right .table-responsive[data-v-3f0f71f6]::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\n.me-dual-list .actions[data-v-3f0f71f6] {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n}\n.me-dual-list .actions div[data-v-3f0f71f6] {\n  display: flex;\n  flex-direction: column;\n}\n.me-dual-list .actions div[data-v-3f0f71f6]:first-child {\n  margin-bottom: 8px;\n}\n.me-dual-list .actions div > .me-icon-l[data-v-3f0f71f6] {\n  background-color: var(--me-primary-7);\n  border-radius: 50px;\n  color: var(--me-primary-2);\n  cursor: pointer;\n  font-size: 18px;\n  margin: 2px;\n  padding: 10px 18px;\n  text-align: center;\n  transition: all 0.2s;\n}\n.me-dual-list .actions div > .me-icon-l.disabled[data-v-3f0f71f6] {\n  cursor: default;\n  opacity: 0.45;\n}\n.me-dual-list .icon-exchange[data-v-3f0f71f6] {\n  align-self: center;\n  color: var(--me-gray-6);\n  font-size: 32px;\n  margin: 0 4px;\n}\n.me-dual-list .empty-bg[data-v-3f0f71f6] {\n  background-color: rgba(240, 240, 240, 0.4);\n  border-radius: 8px;\n}\n.me-dual-list .empty-text[data-v-3f0f71f6] {\n  align-items: center;\n  color: var(--me-gray-3);\n  display: flex;\n  font-style: italic;\n  height: 100%;\n  justify-content: center;\n  position: absolute;\n  top: 0;\n  width: 100%;\n}\n\n/*# sourceMappingURL=DualList.vue.map */", map: {"version":3,"sources":["/app/build/src/components/DualList.vue","DualList.vue"],"names":[],"mappings":"AAoZA;EACA,aAAA;EACA,eAAA;ACnZA;ADqZA;EACA,aAAA;EACA,sBAAA;EACA,YAAA;EACA,kBAAA;EACA,WAAA;ACnZA;ADsZA;;EAEA,oCAAA;ACpZA;ADsZA;EACA,cAAA;ACpZA;ADwZA;EACA,mBAAA;EACA,aAAA;EACA,eAAA;EACA,8BAAA;EACA,kBAAA;ACtZA;ADwZA;EACA,gBAAA;ACtZA;ADyZA;EACA,eAAA;EACA,gBAAA;EACA,gBAAA;ACvZA;AD2ZA;EACA,yCAAA;EACA,kBAAA;ACzZA;AD2ZA;EACA,YAAA;EACA,iBAAA;EACA,WAAA;ACzZA;AD2ZA;EACA,gBAAA;EACA,UAAA;ACzZA;AD6ZA;EACA,2BAAA;EACA,uBAAA;EACA,eAAA;EACA,SAAA;EACA,kBAAA;EACA,QAAA;EACA,WAAA;EACA,WAAA;AC3ZA;ADgaA;EACA,qCAAA;AC9ZA;ADgaA;EACA,0BAAA;EACA,eAAA;AC9ZA;ADmaA;EAEA,qCAAA;AClaA;ADwaA;EACA,oCAAA;ACtaA;ADwaA;EACA,yBAAA;EACA,eAAA;ACtaA;AD2aA;EAEA,oCAAA;AC1aA;AD+aA;EAEA,iCAAA;EACA,kBAAA;EACA,2BAAA;AC9aA;ADgbA;EACA,kBAAA;AC9aA;ADgbA;EACA,mBAAA;EACA,aAAA;AC9aA;ADibA;EACA,uBAAA;EACA,gBAAA;EACA,aAAA;EACA,8BAAA;EACA,iBAAA;EACA,WAAA;AC/aA;ADibA;EACA,kCAAA;EACA,YAAA;AC/aA;ADkbA;EACA,aAAA;AChbA;ADkbA;EACA,iBAAA;AChbA;ADmbA;EACA,eAAA;ACjbA;ADqbA;EACA,aAAA;ACnbA;ADybA;EACA,iCAAA;ACvbA;ADybA;EACA,uBAAA;EACA,eAAA;ACvbA;AD2bA;EACA,aAAA;ACzbA;AD6bA;;;EAEA,aAAA;EACA,cAAA;AC1bA;AD4bA;;;EACA,WAAA;EACA,UAAA;ACxbA;AD0bA;;;EACA,aAAA;ACtbA;ADybA;;;EACA,+BAAA;EACA,mBAAA;ACrbA;ADubA;;;EACA,+BAAA;ACnbA;AD2bA;EACA,aAAA;EACA,sBAAA;EACA,uBAAA;ACzbA;AD2bA;EACA,aAAA;EACA,sBAAA;ACzbA;AD2bA;EACA,kBAAA;ACzbA;AD4bA;EACA,qCAAA;EACA,mBAAA;EACA,0BAAA;EACA,eAAA;EACA,eAAA;EACA,WAAA;EACA,kBAAA;EACA,kBAAA;EACA,oBAAA;AC1bA;AD4bA;EACA,eAAA;EACA,aAAA;AC1bA;ADgcA;EACA,kBAAA;EACA,uBAAA;EACA,eAAA;EACA,aAAA;AC9bA;ADkcA;EACA,0CAAA;EACA,kBAAA;AChcA;ADmcA;EACA,mBAAA;EACA,uBAAA;EACA,aAAA;EACA,kBAAA;EACA,YAAA;EACA,uBAAA;EACA,kBAAA;EACA,MAAA;EACA,WAAA;ACjcA;;AAEA,uCAAuC","file":"DualList.vue","sourcesContent":["<template>\n  <div class=\"me-dual-list\">\n    <div :class=\"{'error': leftSideList.length < leftSideRequiredLength}\" class=\"list mr-2\">\n      <div class=\"list-header\">\n        <div v-if=\"leftTitle || rightTitle\" class=\"title\">\n          {{ leftTitle }}\n        </div>\n\n        <Button\n          v-if=\"selectAndRemoveAll\"\n          :class=\"{ disabled: !leftSideList.length }\"\n          @click=\"moveAll(listSideEnum.LEFT_SIDE_LIST)\"\n          variant=\"light-primary\"\n          size=\"sm\"\n        >\n          <icon icon=\"me-icon-l icon-plus\" /> {{ selectAllText }}\n        </Button>\n      </div>\n      <div class=\"list-left\">\n        <div v-if=\"searchable\" class=\"list-search\">\n          <icon icon=\"me-icon-l icon-search\" />\n          <text-input\n            v-model=\"leftSearchTerm\"\n            :placeholder=\"leftPlaceholder || searchFieldPlaceholder\"\n          />\n        </div>\n\n        <draggable\n          v-if=\"!selectable\"\n          :class=\"{ 'empty-bg': emptyListMessage && !leftSideList.length}\"\n          :group=\"group\"\n          :list=\"filteredLeftSideList\"\n          @change=\"updateListAfterSearchAndDrag({ fromList: listSideEnum.LEFT_SIDE_LIST, toList: listSideEnum.RIGHT_SIDE_LIST }, $event)\"\n          draggable=\".dual-list--items\"\n          ghost-class=\"on-drag\"\n          class=\"dual-list\"\n        >\n          <div\n            v-for=\"item in filteredLeftSideList\"\n            :key=\"item.id\"\n            class=\"dual-list--items\"\n          >\n            <div class=\"label\">\n              {{ item.field }}\n            </div>\n\n            <div class=\"icons\">\n              <slot name=\"iconsLeft\" />\n              <icon @click=\"addItem(item)\" icon=\"me-icon-l icon-plus\" />\n            </div>\n          </div>\n\n          <div v-if=\"emptyListMessage && !leftSideList.length\" class=\"empty-text\">\n            {{ emptyListMessage }}\n          </div>\n        </draggable>\n\n        <b-table\n          v-else\n          :class=\"{ 'empty-bg': emptyListMessage && !leftSideList.length}\"\n          :fields=\"['field']\"\n          :items=\"filteredLeftSideList\"\n          @row-selected=\"onLeftSideSelected\"\n          borderless\n          responsive\n          selectable\n          tbody-class=\"without-strip\"\n          thead-class=\"hidden_header\"\n        >\n          <template #cell(field)=\"{ item }\">\n            <div class=\"selectable-items\">\n              {{ item.field }}\n            </div>\n          </template>\n        </b-table>\n      </div>\n      <span class=\"invalid-feedback\">{{ $toolkit.t('dualList.invalidFeedback') }}</span>\n    </div>\n\n    <div v-if=\"selectable\" class=\"actions\">\n      <div class=\"add-action-buttons\">\n        <icon\n          :class=\"{ disabled: !leftSelectedList.length }\"\n          @click=\"moveSelected(listSideEnum.LEFT_SELECTED_LIST)\"\n          icon=\"me-icon-l icon-chevron-right\"\n        />\n        <icon\n          :class=\"{ disabled: !leftSideList.length }\"\n          @click=\"moveAll(listSideEnum.LEFT_SIDE_LIST)\"\n          icon=\"me-icon-l icon-chevron-double-right\"\n        />\n      </div>\n\n      <div class=\"remove-action-buttons\">\n        <icon\n          :class=\"{ disabled: !rightSelectedList.length }\"\n          @click=\"moveSelected(listSideEnum.RIGHT_SELECTED_LIST)\"\n          icon=\"me-icon-l icon-chevron-left\"\n        />\n        <icon\n          :class=\"{ disabled: !rightSideList.length }\"\n          @click=\"moveAll(listSideEnum.RIGHT_SIDE_LIST)\"\n          icon=\"me-icon-l icon-chevron-double-left\"\n        />\n      </div>\n    </div>\n\n    <icon v-if=\"selectAndRemoveAll\" icon=\"me-icon-l icon-exchange\" />\n\n    <div :class=\"{'error': rightSideList.length < rightSideRequiredLength}\" class=\"list ml-2\">\n      <div class=\"list-header\">\n        <div v-if=\"leftTitle || rightTitle\" class=\"title\">\n          {{ rightTitle }}\n        </div>\n\n        <Button\n          v-if=\"selectAndRemoveAll\"\n          :class=\"{ disabled: !rightSideList.length }\"\n          @click=\"moveAll(listSideEnum.RIGHT_SIDE_LIST)\"\n          variant=\"light-primary\"\n          size=\"sm\"\n        >\n          <icon icon=\"me-icon-l icon-times\" /> {{ removeAllText }}\n        </Button>\n      </div>\n\n      <div class=\"list-right\">\n        <div v-if=\"searchable\" class=\"list-search\">\n          <icon icon=\"me-icon-l icon-search\" />\n          <text-input\n            v-model=\"rightSearchTerm\"\n            :placeholder=\"rightPlaceholder || searchFieldPlaceholder\"\n          />\n        </div>\n\n        <draggable\n          v-if=\"!selectable\"\n          :class=\"{ 'empty-bg': emptyListMessage && !rightSideList.length}\"\n          :group=\"group\"\n          :list=\"filteredRightSideList\"\n          @change=\"updateListAfterSearchAndDrag({ fromList: listSideEnum.RIGHT_SIDE_LIST, toList: listSideEnum.LEFT_SIDE_LIST }, $event)\"\n          draggable=\".dual-list--items\"\n          ghost-class=\"on-drag\"\n          class=\"dual-list\"\n        >\n          <div\n            v-for=\"item in filteredRightSideList\"\n            :key=\"item.id\"\n            class=\"dual-list--items\"\n          >\n            {{ item.field }}\n            <div class=\"icons\">\n              <slot name=\"iconsRight\" />\n              <icon @click=\"removeItem(item)\" icon=\"me-icon-l icon-times\" />\n            </div>\n          </div>\n\n          <div v-if=\"emptyListMessage && !rightSideList.length\" class=\"empty-text\">\n            {{ emptyListMessage }}\n          </div>\n        </draggable>\n\n        <b-table\n          v-else\n          :class=\"{ 'empty-bg': emptyListMessage && !rightSideList.length}\"\n          :items=\"filteredRightSideList\"\n          :fields=\"['field']\"\n          @row-selected=\"onRightSideSelected\"\n          selectable\n          borderless\n          responsive\n          tbody-class=\"without-strip\"\n          thead-class=\"hidden_header\"\n        >\n          <template #cell(field)=\"{ item }\">\n            <div class=\"selectable-items\">\n              {{ item.field }}\n            </div>\n          </template>\n        </b-table>\n      </div>\n      <span class=\"invalid-feedback\">{{ $toolkit.t('dualList.invalidFeedback') }}</span>\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport { BTable } from 'bootstrap-vue'\nimport Button from './Button.vue'\nimport Draggable from './Draggable.vue'\nimport Icon from './Icon.vue'\nimport TextInput from './TextInput.vue'\n\nconst listSideEnum = {\n  RIGHT_SIDE_LIST: 'rightSideList',\n  LEFT_SIDE_LIST: 'leftSideList',\n  RIGHT_SELECTED_LIST: 'rightSelectedList',\n  LEFT_SELECTED_LIST: 'leftSelectedList'\n}\n\nexport default {\n  name: 'MeDualList',\n\n  components: {\n    BTable,\n    Button,\n    Draggable,\n    Icon,\n    TextInput\n  },\n\n  props: {\n    group: {\n      type: String,\n      default: 'dualList'\n    },\n    leftList: {\n      type: Array,\n      required: true\n    },\n    rightList: {\n      type: Array,\n      required: true\n    },\n    leftTitle: {\n      type: String,\n      default: ''\n    },\n    rightTitle: {\n      type: String,\n      default: ''\n    },\n    emptyListMessage: {\n      type: String,\n      default: ''\n    },\n    searchFieldPlaceholder: {\n      type: String,\n      default: ''\n    },\n    leftPlaceholder: {\n      type: String,\n      default: ''\n    },\n    rightPlaceholder: {\n      type: String,\n      default: ''\n    },\n    selectable: {\n      type: Boolean,\n      default: false\n    },\n    searchable: {\n      type: Boolean,\n      default: true\n    },\n    selectAndRemoveAll: {\n      type: Boolean,\n      default: false\n    },\n    selectAllText: {\n      type: String,\n      default: ''\n    },\n    removeAllText: {\n      type: String,\n      default: ''\n    },\n    cleanSearchable: {\n      type: Boolean,\n      default: true\n    },\n    leftSideRequiredLength: {\n      type: Number,\n      default: 0\n    },\n    rightSideRequiredLength: {\n      type: Number,\n      default: 0\n    }\n  },\n\n  data() {\n    return {\n      listSideEnum,\n      leftSearchTerm: '',\n      leftSelectedList: [],\n      leftSideList: [],\n      leftTempList: [],\n      rightSearchTerm: '',\n      rightSelectedList: [],\n      rightSideList: []\n    }\n  },\n\n  computed: {\n    filteredRightSideList() {\n      return this.getFilteredList(this.rightSearchTerm, this.rightSideList)\n    },\n\n    filteredLeftSideList() {\n      return this.getFilteredList(this.leftSearchTerm, this.leftSideList)\n    }\n  },\n\n  watch: {\n    leftList(value) {\n      this.leftSideList = cloneDeep(value)\n    },\n\n    rightList(value) {\n      this.rightSideList = cloneDeep(value)\n    }\n  },\n\n  mounted() {\n    this.leftSideList = cloneDeep(this.leftList)\n    this.rightSideList = cloneDeep(this.rightList)\n  },\n\n  methods: {\n    getFilteredList(term, list) {\n      if (term) {\n        const searchTerm = new RegExp(term, 'i')\n        return list.filter(item => item.field.match(searchTerm))\n      }\n      return list\n    },\n\n    clearSearchable(searchTerm) {\n      if (this.cleanSearchable) this[searchTerm] = ''\n    },\n\n    addItem(item) {\n      this.$emit('on-add', item)\n      this.clearSearchable('leftSearchTerm')\n    },\n\n    removeItem(item) {\n      this.$emit('on-remove', item)\n      this.clearSearchable('rightSearchTerm')\n    },\n\n    onDrag() {\n      this.$emit('on-drag', {\n        rightList: this.rightSideList,\n        leftList: this.leftSideList\n      })\n    },\n\n    updateListAfterSearchAndDrag({ fromList, toList }, itemData) {\n      if (!this.leftSearchTerm && !this.rightSearchTerm) {\n        return this.onDrag()\n      }\n\n      const { removed } = itemData\n      if (!removed) return\n\n      const itemMoved = removed.element\n      this[fromList] = this[fromList].filter(item => item.id !== itemMoved.id)\n      this[toList] = this.removeDuplicate([...this[toList], itemMoved])\n\n      if (this.cleanSearchable) {\n        const searchTerm = fromList === listSideEnum.LEFT_SIDE_LIST ? 'leftSearchTerm' : 'rightSearchTerm'\n        this.clearSearchable(searchTerm)\n      }\n\n      return this.onDrag()\n    },\n\n    removeDuplicate(arrayOfObject) {\n      return arrayOfObject.filter((element, index, self) =>\n        index === self.findIndex(t => (t.id === element.id)))\n    },\n\n    onLeftSideSelected(items) {\n      this.leftSelectedList = items\n    },\n\n    onRightSideSelected(items) {\n      this.rightSelectedList = items\n    },\n\n    moveSelected(fromList) {\n      const direction = fromList === listSideEnum.LEFT_SELECTED_LIST ? 'right' : 'left'\n      this.moveList(this[fromList], direction)\n    },\n\n    moveAll(fromList) {\n      const direction = fromList === listSideEnum.LEFT_SIDE_LIST ? 'right' : 'left'\n      this.moveList(this[fromList], direction)\n    },\n\n    moveList(list, direction) {\n      list.forEach((item) => {\n        direction === 'right' ? this.addItem(item) : this.removeItem(item)\n      })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-dual-list {\n    display: flex;\n    font-size: 14px;\n\n    .list {\n      display: flex;\n      flex-direction: column;\n      flex-grow: 1;\n      position: relative;\n      width: 100%;\n\n      &.error {\n        .list-left,\n        .list-right {\n          border: 1px solid var(--me-danger-3);\n        }\n        .invalid-feedback {\n          display: block;\n        }\n      }\n\n      &-header {\n        align-items: center;\n        display: flex;\n        font-size: 12px;\n        justify-content: space-between;\n        margin-bottom: 8px;\n\n        .title {\n          font-weight: 600;\n        }\n\n        .me-button {\n          font-size: 12px;\n          min-height: 32px;\n          box-shadow: none;\n        }\n      }\n\n      &-search {\n        border-bottom: 1px solid var(--me-gray-6);\n        position: relative;\n\n        ::v-deep .form-control {\n          border: none;\n          text-indent: 24px;\n          z-index: 20;\n\n          &:focus {\n            box-shadow: none;\n            outline: 0;\n          }\n        }\n\n        i {\n          background: var(--me-white);\n          color: var(--me-gray-3);\n          font-size: 16px;\n          left: 8px;\n          position: absolute;\n          top: 8px;\n          width: 24px;\n          z-index: 21;\n        }\n      }\n\n      &-left {\n        .dual-list--items:hover {\n          background-color: var(--me-primary-7);\n\n          .icons .me-icon-l {\n            color: var(--me-primary-2);\n            cursor: pointer;\n          }\n        }\n\n        ::v-deep .table-responsive tr {\n          &:hover,\n          &.table-active {\n            background-color: var(--me-primary-7);\n          }\n        }\n      }\n\n      &-right {\n        .dual-list--items:hover {\n          background-color: var(--me-danger-7);\n\n          .icons .me-icon-l {\n            color: var(--me-danger-2);\n            cursor: pointer;\n          }\n        }\n\n        ::v-deep .table-responsive tr {\n          &:hover,\n          &.table-active {\n            background-color: var(--me-danger-7);\n          }\n        }\n      }\n\n      &-left,\n      &-right {\n        background-color: var(--me-white);\n        border-radius: 8px;\n        border: 1px solid #00000014;\n\n        .dual-list {\n          position: relative;\n\n          .label {\n            align-items: center;\n            display: flex;\n          }\n\n          &--items {\n            color: var(--me-gray-1);\n            cursor: grabbing;\n            display: flex;\n            justify-content: space-between;\n            padding: 8px 14px;\n            width: 100%;\n\n            &.on-drag {\n              background-color: var(--me-gray-6);\n              opacity: .4;\n            }\n\n            .icons {\n              display: none;\n\n              * {\n                margin-left: 14px;\n              }\n\n              .me-icon {\n                cursor: pointer;\n              }\n            }\n\n            &:hover .icons {\n              display: flex;\n            }\n          }\n        }\n\n        ::v-deep .table-responsive {\n          tr {\n            background-color: var(--me-white);\n\n            .selectable-items {\n              color: var(--me-gray-1);\n              cursor: pointer;\n            }\n          }\n\n          .hidden_header {\n            display: none;\n          }\n        }\n\n        .dual-list,\n        .table-responsive {\n          height: 200px;\n          overflow: auto;\n\n          &::-webkit-scrollbar {\n            height: 8px;\n            width: 8px;\n\n            &-track {\n              display: none;\n            }\n\n            &-thumb {\n              background: rgba(0,0,0,.25);\n              border-radius: 15px;\n\n              &:hover {\n                background: rgba(0,0,0,.45)\n              }\n            }\n          }\n        }\n      }\n    }\n\n    .actions {\n      display: flex;\n      flex-direction: column;\n      justify-content: center;\n\n      div {\n        display: flex;\n        flex-direction: column;\n\n        &:first-child {\n          margin-bottom: 8px;\n        }\n\n        > .me-icon-l {\n          background-color: var(--me-primary-7);\n          border-radius: 50px;\n          color: var(--me-primary-2);\n          cursor: pointer;\n          font-size: 18px;\n          margin: 2px;\n          padding: 10px 18px;\n          text-align: center;\n          transition: all 0.2s;\n\n          &.disabled {\n            cursor: default;\n            opacity: 0.45;\n          }\n        }\n      }\n    }\n\n    .icon-exchange {\n      align-self: center;\n      color: var(--me-gray-6);\n      font-size: 32px;\n      margin: 0 4px;\n    }\n\n    .empty {\n      &-bg {\n        background-color: rgba(240, 240, 240, 0.4);\n        border-radius: 8px;\n      }\n\n      &-text {\n        align-items: center;\n        color: var(--me-gray-3);\n        display: flex;\n        font-style: italic;\n        height: 100%;\n        justify-content: center;\n        position: absolute;\n        top: 0;\n        width: 100%;\n      }\n    }\n  }\n</style>\n",".me-dual-list {\n  display: flex;\n  font-size: 14px;\n}\n.me-dual-list .list {\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  position: relative;\n  width: 100%;\n}\n.me-dual-list .list.error .list-left,\n.me-dual-list .list.error .list-right {\n  border: 1px solid var(--me-danger-3);\n}\n.me-dual-list .list.error .invalid-feedback {\n  display: block;\n}\n.me-dual-list .list-header {\n  align-items: center;\n  display: flex;\n  font-size: 12px;\n  justify-content: space-between;\n  margin-bottom: 8px;\n}\n.me-dual-list .list-header .title {\n  font-weight: 600;\n}\n.me-dual-list .list-header .me-button {\n  font-size: 12px;\n  min-height: 32px;\n  box-shadow: none;\n}\n.me-dual-list .list-search {\n  border-bottom: 1px solid var(--me-gray-6);\n  position: relative;\n}\n.me-dual-list .list-search ::v-deep .form-control {\n  border: none;\n  text-indent: 24px;\n  z-index: 20;\n}\n.me-dual-list .list-search ::v-deep .form-control:focus {\n  box-shadow: none;\n  outline: 0;\n}\n.me-dual-list .list-search i {\n  background: var(--me-white);\n  color: var(--me-gray-3);\n  font-size: 16px;\n  left: 8px;\n  position: absolute;\n  top: 8px;\n  width: 24px;\n  z-index: 21;\n}\n.me-dual-list .list-left .dual-list--items:hover {\n  background-color: var(--me-primary-7);\n}\n.me-dual-list .list-left .dual-list--items:hover .icons .me-icon-l {\n  color: var(--me-primary-2);\n  cursor: pointer;\n}\n.me-dual-list .list-left ::v-deep .table-responsive tr:hover, .me-dual-list .list-left ::v-deep .table-responsive tr.table-active {\n  background-color: var(--me-primary-7);\n}\n.me-dual-list .list-right .dual-list--items:hover {\n  background-color: var(--me-danger-7);\n}\n.me-dual-list .list-right .dual-list--items:hover .icons .me-icon-l {\n  color: var(--me-danger-2);\n  cursor: pointer;\n}\n.me-dual-list .list-right ::v-deep .table-responsive tr:hover, .me-dual-list .list-right ::v-deep .table-responsive tr.table-active {\n  background-color: var(--me-danger-7);\n}\n.me-dual-list .list-left, .me-dual-list .list-right {\n  background-color: var(--me-white);\n  border-radius: 8px;\n  border: 1px solid #00000014;\n}\n.me-dual-list .list-left .dual-list, .me-dual-list .list-right .dual-list {\n  position: relative;\n}\n.me-dual-list .list-left .dual-list .label, .me-dual-list .list-right .dual-list .label {\n  align-items: center;\n  display: flex;\n}\n.me-dual-list .list-left .dual-list--items, .me-dual-list .list-right .dual-list--items {\n  color: var(--me-gray-1);\n  cursor: grabbing;\n  display: flex;\n  justify-content: space-between;\n  padding: 8px 14px;\n  width: 100%;\n}\n.me-dual-list .list-left .dual-list--items.on-drag, .me-dual-list .list-right .dual-list--items.on-drag {\n  background-color: var(--me-gray-6);\n  opacity: 0.4;\n}\n.me-dual-list .list-left .dual-list--items .icons, .me-dual-list .list-right .dual-list--items .icons {\n  display: none;\n}\n.me-dual-list .list-left .dual-list--items .icons *, .me-dual-list .list-right .dual-list--items .icons * {\n  margin-left: 14px;\n}\n.me-dual-list .list-left .dual-list--items .icons .me-icon, .me-dual-list .list-right .dual-list--items .icons .me-icon {\n  cursor: pointer;\n}\n.me-dual-list .list-left .dual-list--items:hover .icons, .me-dual-list .list-right .dual-list--items:hover .icons {\n  display: flex;\n}\n.me-dual-list .list-left ::v-deep .table-responsive tr, .me-dual-list .list-right ::v-deep .table-responsive tr {\n  background-color: var(--me-white);\n}\n.me-dual-list .list-left ::v-deep .table-responsive tr .selectable-items, .me-dual-list .list-right ::v-deep .table-responsive tr .selectable-items {\n  color: var(--me-gray-1);\n  cursor: pointer;\n}\n.me-dual-list .list-left ::v-deep .table-responsive .hidden_header, .me-dual-list .list-right ::v-deep .table-responsive .hidden_header {\n  display: none;\n}\n.me-dual-list .list-left .dual-list,\n.me-dual-list .list-left .table-responsive, .me-dual-list .list-right .dual-list,\n.me-dual-list .list-right .table-responsive {\n  height: 200px;\n  overflow: auto;\n}\n.me-dual-list .list-left .dual-list::-webkit-scrollbar,\n.me-dual-list .list-left .table-responsive::-webkit-scrollbar, .me-dual-list .list-right .dual-list::-webkit-scrollbar,\n.me-dual-list .list-right .table-responsive::-webkit-scrollbar {\n  height: 8px;\n  width: 8px;\n}\n.me-dual-list .list-left .dual-list::-webkit-scrollbar-track,\n.me-dual-list .list-left .table-responsive::-webkit-scrollbar-track, .me-dual-list .list-right .dual-list::-webkit-scrollbar-track,\n.me-dual-list .list-right .table-responsive::-webkit-scrollbar-track {\n  display: none;\n}\n.me-dual-list .list-left .dual-list::-webkit-scrollbar-thumb,\n.me-dual-list .list-left .table-responsive::-webkit-scrollbar-thumb, .me-dual-list .list-right .dual-list::-webkit-scrollbar-thumb,\n.me-dual-list .list-right .table-responsive::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\n.me-dual-list .list-left .dual-list::-webkit-scrollbar-thumb:hover,\n.me-dual-list .list-left .table-responsive::-webkit-scrollbar-thumb:hover, .me-dual-list .list-right .dual-list::-webkit-scrollbar-thumb:hover,\n.me-dual-list .list-right .table-responsive::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\n.me-dual-list .actions {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n}\n.me-dual-list .actions div {\n  display: flex;\n  flex-direction: column;\n}\n.me-dual-list .actions div:first-child {\n  margin-bottom: 8px;\n}\n.me-dual-list .actions div > .me-icon-l {\n  background-color: var(--me-primary-7);\n  border-radius: 50px;\n  color: var(--me-primary-2);\n  cursor: pointer;\n  font-size: 18px;\n  margin: 2px;\n  padding: 10px 18px;\n  text-align: center;\n  transition: all 0.2s;\n}\n.me-dual-list .actions div > .me-icon-l.disabled {\n  cursor: default;\n  opacity: 0.45;\n}\n.me-dual-list .icon-exchange {\n  align-self: center;\n  color: var(--me-gray-6);\n  font-size: 32px;\n  margin: 0 4px;\n}\n.me-dual-list .empty-bg {\n  background-color: rgba(240, 240, 240, 0.4);\n  border-radius: 8px;\n}\n.me-dual-list .empty-text {\n  align-items: center;\n  color: var(--me-gray-3);\n  display: flex;\n  font-style: italic;\n  height: 100%;\n  justify-content: center;\n  position: absolute;\n  top: 0;\n  width: 100%;\n}\n\n/*# sourceMappingURL=DualList.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1l = "data-v-3f0f71f6";
  /* module identifier */
  const __vue_module_identifier__$1l = undefined;
  /* functional template */
  const __vue_is_functional_template__$1l = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1l, staticRenderFns: __vue_staticRenderFns__$1l },
    __vue_inject_styles__$1l,
    __vue_script__$1l,
    __vue_scope_id__$1l,
    __vue_is_functional_template__$1l,
    __vue_module_identifier__$1l,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1m = {

  name: 'MeEllipseProgress',
  props: {
    progress: {
      type: Number,
      default: null,
      require: true
    },
    colorFill: {
      type: String,
      default: null
    },
    color: {
      type: String,
      default: null
    },
    emptyColorFill: {
      type: String,
      default: null
    },
    lineMode: {
      type: String,
      default: null
    },
    thickness: {
      type: String,
      default: null
    },
    emptyThickness: {
      type: String,
      default: null
    },
    line: {
      type: String,
      default: null
    },
    size: {
      type: Number,
      default: null,
      require: true
    },
    legend: {
      type: Boolean,
      default: true
    },
    legendClass: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    }
  }
};

/* script */
const __vue_script__$1m = script$1m;

/* template */
var __vue_render__$1m = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("vue-ellipse-progress", {
    attrs: {
      progress: _vm.progress,
      legend: _vm.legend,
      legendClass: _vm.legendClass,
      line: _vm.line,
      color: _vm.color,
      "empty-color-fill": _vm.emptyColorFill,
      thickness: _vm.thickness,
      "empty-thickness": _vm.emptyThickness,
      size: _vm.size
    },
    scopedSlots: _vm._u(
      [
        {
          key: "legend-value",
          fn: function() {
            return [_vm._t("legend-value")]
          },
          proxy: true
        },
        {
          key: "legend-caption",
          fn: function() {
            return [
              _vm._t("legend-caption", [
                _c("div", { staticClass: "content-legend" }, [
                  _vm.icon
                    ? _c("i", { class: "me-icon " + _vm.icon })
                    : _vm._e()
                ])
              ])
            ]
          },
          proxy: true
        },
        {
          key: "default",
          fn: function(ref) {
            var counterTick = ref.counterTick;
            return [_vm._t("default", null, { counterTick: counterTick })]
          }
        }
      ],
      null,
      true
    )
  })
};
var __vue_staticRenderFns__$1m = [];
__vue_render__$1m._withStripped = true;

  /* style */
  const __vue_inject_styles__$1m = function (inject) {
    if (!inject) return
    inject("data-v-6eaba0e5_0", { source: ".content-legend i[data-v-6eaba0e5] {\n  color: rgba(0, 0, 0, 0.24);\n  font-size: 24px;\n}\n\n/*# sourceMappingURL=EllipseProgress.vue.map */", map: {"version":3,"sources":["/app/build/src/components/EllipseProgress.vue","EllipseProgress.vue"],"names":[],"mappings":"AA2FA;EACA,0BAAA;EACA,eAAA;AC1FA;;AAEA,8CAA8C","file":"EllipseProgress.vue","sourcesContent":["<template>\n  <vue-ellipse-progress\n    :progress=\"progress\"\n    :legend=\"legend\"\n    :legendClass=\"legendClass\"\n    :line=\"line\"\n    :color=\"color\"\n    :empty-color-fill=\"emptyColorFill\"\n    :thickness=\"thickness\"\n    :empty-thickness=\"emptyThickness\"\n    :size=\"size\"\n  >\n    <template v-slot:legend-value>\n      <slot name=\"legend-value\" />\n    </template>\n\n    <template v-slot:legend-caption>\n      <slot name=\"legend-caption\">\n        <div class=\"content-legend\">\n          <i v-if=\"icon\" :class=\"`me-icon ${icon}`\" />\n        </div>\n      </slot>\n    </template>\n\n    <template v-slot:default=\"{ counterTick }\">\n      <slot :counterTick=\"counterTick\" />\n    </template>\n  </vue-ellipse-progress>\n</template>\n\n<script>\nexport default {\n\n  name: 'MeEllipseProgress',\n  props: {\n    progress: {\n      type: Number,\n      default: null,\n      require: true\n    },\n    colorFill: {\n      type: String,\n      default: null\n    },\n    color: {\n      type: String,\n      default: null\n    },\n    emptyColorFill: {\n      type: String,\n      default: null\n    },\n    lineMode: {\n      type: String,\n      default: null\n    },\n    thickness: {\n      type: String,\n      default: null\n    },\n    emptyThickness: {\n      type: String,\n      default: null\n    },\n    line: {\n      type: String,\n      default: null\n    },\n    size: {\n      type: Number,\n      default: null,\n      require: true\n    },\n    legend: {\n      type: Boolean,\n      default: true\n    },\n    legendClass: {\n      type: String,\n      default: ''\n    },\n    icon: {\n      type: String,\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .content-legend {\n    i {\n      color: rgba(0, 0, 0, 0.24);\n      font-size: 24px;\n    }\n  }\n</style>\n",".content-legend i {\n  color: rgba(0, 0, 0, 0.24);\n  font-size: 24px;\n}\n\n/*# sourceMappingURL=EllipseProgress.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1m = "data-v-6eaba0e5";
  /* module identifier */
  const __vue_module_identifier__$1m = undefined;
  /* functional template */
  const __vue_is_functional_template__$1m = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1m, staticRenderFns: __vue_staticRenderFns__$1m },
    __vue_inject_styles__$1m,
    __vue_script__$1m,
    __vue_scope_id__$1m,
    __vue_is_functional_template__$1m,
    __vue_module_identifier__$1m,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1n = {
  name: 'MeFab',
  components: {
    Button: __vue_component__$6
  },
  props: {
    opened: {
      type: Boolean
    },
    openedIcon: {
      type: String,
      default: 'icon-times'
    },
    closedIcon: {
      type: String,
      default: 'icon-plus'
    },
    items: {
      type: Array,
      required: true
    },
    mainVariant: {
      type: String,
      default: 'primary'
    },
    subAppearance: {
      type: String,
      default: ''
    },
    subVariant: {
      type: String,
      default: 'primary'
    }
  },
  data() {
    return {
      isOpen: this.opened,
      clazz: `btn-sub btn-${this.subAppearance}`
    }
  },

  computed: {
    fabIcon() {
      return this.isOpen ? `me-icon ${this.openedIcon}` : `me-icon ${this.closedIcon}`
    }
  },

  methods: {
    closeFAB() {
      this.isOpen = false;
    }
  }
};

/* script */
const __vue_script__$1n = script$1n;

/* template */
var __vue_render__$1n = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-fab", class: { opened: _vm.isOpen } }, [
    _c(
      "div",
      { staticClass: "btn-group-fab" },
      [
        _c("Button", {
          staticClass: "btn-main",
          attrs: {
            variant: _vm.mainVariant,
            icon: _vm.fabIcon,
            pill: "",
            "data-cy": "me-fab"
          },
          on: {
            click: function($event) {
              _vm.isOpen = !_vm.isOpen;
            }
          }
        }),
        _vm._v(" "),
        _vm._l(_vm.items, function(item, index) {
          return _c(
            "Button",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: _vm.isOpen,
                  expression: "isOpen"
                }
              ],
              key: index,
              class: _vm.clazz,
              attrs: {
                href: item.href,
                variant: _vm.subVariant,
                target: item.target,
                pill: ""
              },
              on: {
                click: function($event) {
                  item.click ? item.click() : null;
                }
              }
            },
            [_vm._v("\n      " + _vm._s(item.label) + "\n    ")]
          )
        })
      ],
      2
    ),
    _vm._v(" "),
    _c("div", { staticClass: "backdrop", on: { click: _vm.closeFAB } })
  ])
};
var __vue_staticRenderFns__$1n = [];
__vue_render__$1n._withStripped = true;

  /* style */
  const __vue_inject_styles__$1n = function (inject) {
    if (!inject) return
    inject("data-v-ac0d432e_0", { source: "div.me-fab[data-v-ac0d432e] {\n  position: absolute;\n}\ndiv.me-fab .btn-group-fab[data-v-ac0d432e] {\n  bottom: 16px;\n  position: fixed;\n  right: 16px;\n  z-index: 1102;\n}\ndiv.me-fab .btn-group-fab .btn[data-v-ac0d432e] {\n  box-shadow: var(--me-shadow-level-2);\n  position: absolute;\n  right: 0;\n}\ndiv.me-fab .btn-group-fab .btn-main[data-v-ac0d432e] {\n  bottom: 0;\n  height: 56px;\n  width: 56px;\n}\ndiv.me-fab .btn-group-fab .btn-main i[data-v-ac0d432e] {\n  font-size: 30px;\n  vertical-align: middle;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e] {\n  height: 48px;\n  min-width: 148px;\n  width: max-content;\n  padding: 12px 16px;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e]:nth-child(2) {\n  bottom: 76px;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e]:nth-child(3) {\n  bottom: 142px;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e]:nth-child(4) {\n  bottom: 208px;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e]:nth-child(5) {\n  bottom: 270px;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e]:nth-child(6) {\n  bottom: 336PX;\n}\ndiv.me-fab .btn-group-fab .btn-sub[data-v-ac0d432e]:nth-child(7) {\n  bottom: 402px;\n}\ndiv.me-fab .btn-group-fab .btn-default[data-v-ac0d432e] {\n  background-color: var(--me-white);\n  border: none;\n  color: var(--me-primary-2);\n  font-weight: 600;\n}\ndiv.me-fab .btn-group-fab .btn-default[data-v-ac0d432e]:hover {\n  background-color: #e1f1f8;\n}\ndiv.me-fab > div.backdrop[data-v-ac0d432e] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 1100;\n}\ndiv.me-fab.opened > div.backdrop[data-v-ac0d432e] {\n  display: block;\n  opacity: 0.56;\n}\n\n/*# sourceMappingURL=Fab.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Fab.vue","Fab.vue"],"names":[],"mappings":"AAyFA;EACA,kBAAA;ACxFA;AD0FA;EACA,YAAA;EACA,eAAA;EACA,WAAA;EACA,aAAA;ACxFA;AD0FA;EACA,oCAAA;EACA,kBAAA;EACA,QAAA;ACxFA;AD0FA;EACA,SAAA;EACA,YAAA;EACA,WAAA;ACxFA;AD0FA;EACA,eAAA;EACA,sBAAA;ACxFA;AD4FA;EACA,YAAA;EACA,gBAAA;EACA,kBAAA;EACA,kBAAA;AC1FA;AD4FA;EACA,YAAA;AC1FA;AD6FA;EACA,aAAA;AC3FA;AD8FA;EACA,aAAA;AC5FA;AD+FA;EACA,aAAA;AC7FA;ADgGA;EACA,aAAA;AC9FA;ADiGA;EACA,aAAA;AC/FA;ADmGA;EACA,iCAAA;EACA,YAAA;EACA,0BAAA;EACA,gBAAA;ACjGA;ADmGA;EACA,yBAAA;ACjGA;ADuGA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,aAAA;ACrGA;ADyGA;EACA,cAAA;EACA,aAAA;ACvGA;;AAEA,kCAAkC","file":"Fab.vue","sourcesContent":["<template>\n  <div :class=\"{ opened: isOpen }\" class=\"me-fab\">\n    <div class=\"btn-group-fab\">\n      <Button\n        :variant=\"mainVariant\"\n        :icon=\"fabIcon\"\n        @click=\"isOpen = !isOpen\"\n        class=\"btn-main\"\n        pill\n        data-cy=\"me-fab\"\n      />\n\n      <Button\n        v-for=\"(item, index) in items\"\n        v-show=\"isOpen\"\n        :key=\"index\"\n        :href=\"item.href\"\n        :class=\"clazz\"\n        :variant=\"subVariant\"\n        :target=\"item.target\"\n        @click=\"item.click ? item.click() : null\"\n        pill\n      >\n        {{ item.label }}\n      </Button>\n    </div>\n    <div @click=\"closeFAB\" class=\"backdrop\" />\n  </div>\n</template>\n\n<script>\nimport Button from './Button.vue'\n\nexport default {\n  name: 'MeFab',\n  components: {\n    Button\n  },\n  props: {\n    opened: {\n      type: Boolean\n    },\n    openedIcon: {\n      type: String,\n      default: 'icon-times'\n    },\n    closedIcon: {\n      type: String,\n      default: 'icon-plus'\n    },\n    items: {\n      type: Array,\n      required: true\n    },\n    mainVariant: {\n      type: String,\n      default: 'primary'\n    },\n    subAppearance: {\n      type: String,\n      default: ''\n    },\n    subVariant: {\n      type: String,\n      default: 'primary'\n    }\n  },\n  data() {\n    return {\n      isOpen: this.opened,\n      clazz: `btn-sub btn-${this.subAppearance}`\n    }\n  },\n\n  computed: {\n    fabIcon() {\n      return this.isOpen ? `me-icon ${this.openedIcon}` : `me-icon ${this.closedIcon}`\n    }\n  },\n\n  methods: {\n    closeFAB() {\n      this.isOpen = false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-fab {\n    position: absolute;\n\n    .btn-group-fab {\n      bottom: 16px;\n      position: fixed;\n      right: 16px;\n      z-index: 1102;\n\n      .btn {\n        box-shadow: var(--me-shadow-level-2);\n        position: absolute;\n        right: 0;\n\n        &-main {\n          bottom: 0;\n          height: 56px;\n          width: 56px;\n\n          i {\n            font-size: 30px;\n            vertical-align: middle;\n          }\n        }\n\n        &-sub {\n          height: 48px;\n          min-width: 148px;\n          width: max-content;\n          padding: 12px 16px;\n\n          &:nth-child(2) {\n            bottom: 76px;\n          }\n\n          &:nth-child(3) {\n              bottom: 142px;\n          }\n\n          &:nth-child(4) {\n            bottom: 208px;\n          }\n\n          &:nth-child(5) {\n            bottom: 270px;\n          }\n\n          &:nth-child(6) {\n            bottom: 336PX;\n          }\n\n          &:nth-child(7) {\n            bottom: 402px;\n          }\n        }\n\n        &-default {\n          background-color: var(--me-white);\n          border: none;\n          color: var(--me-primary-2);\n          font-weight: 600;\n\n          &:hover {\n            background-color: #e1f1f8;\n          }\n        }\n      }\n    }\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      z-index: 1100;\n    }\n\n    &.opened {\n      > div.backdrop {\n        display: block;\n        opacity: 0.56;\n      }\n    }\n  }\n</style>\n","div.me-fab {\n  position: absolute;\n}\ndiv.me-fab .btn-group-fab {\n  bottom: 16px;\n  position: fixed;\n  right: 16px;\n  z-index: 1102;\n}\ndiv.me-fab .btn-group-fab .btn {\n  box-shadow: var(--me-shadow-level-2);\n  position: absolute;\n  right: 0;\n}\ndiv.me-fab .btn-group-fab .btn-main {\n  bottom: 0;\n  height: 56px;\n  width: 56px;\n}\ndiv.me-fab .btn-group-fab .btn-main i {\n  font-size: 30px;\n  vertical-align: middle;\n}\ndiv.me-fab .btn-group-fab .btn-sub {\n  height: 48px;\n  min-width: 148px;\n  width: max-content;\n  padding: 12px 16px;\n}\ndiv.me-fab .btn-group-fab .btn-sub:nth-child(2) {\n  bottom: 76px;\n}\ndiv.me-fab .btn-group-fab .btn-sub:nth-child(3) {\n  bottom: 142px;\n}\ndiv.me-fab .btn-group-fab .btn-sub:nth-child(4) {\n  bottom: 208px;\n}\ndiv.me-fab .btn-group-fab .btn-sub:nth-child(5) {\n  bottom: 270px;\n}\ndiv.me-fab .btn-group-fab .btn-sub:nth-child(6) {\n  bottom: 336PX;\n}\ndiv.me-fab .btn-group-fab .btn-sub:nth-child(7) {\n  bottom: 402px;\n}\ndiv.me-fab .btn-group-fab .btn-default {\n  background-color: var(--me-white);\n  border: none;\n  color: var(--me-primary-2);\n  font-weight: 600;\n}\ndiv.me-fab .btn-group-fab .btn-default:hover {\n  background-color: #e1f1f8;\n}\ndiv.me-fab > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 1100;\n}\ndiv.me-fab.opened > div.backdrop {\n  display: block;\n  opacity: 0.56;\n}\n\n/*# sourceMappingURL=Fab.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1n = "data-v-ac0d432e";
  /* module identifier */
  const __vue_module_identifier__$1n = undefined;
  /* functional template */
  const __vue_is_functional_template__$1n = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1n, staticRenderFns: __vue_staticRenderFns__$1n },
    __vue_inject_styles__$1n,
    __vue_script__$1n,
    __vue_scope_id__$1n,
    __vue_is_functional_template__$1n,
    __vue_module_identifier__$1n,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1o = {
  name: 'MeFileInput',
  components: {
    BFormFile,
    Button: __vue_component__$6,
    Spinner: __vue_component__$5
  },
  props: {
    accept: {
      type: String,
      default: null
    },
    buttonText: {
      type: String,
      default: ''
    },
    buttonVariant: {
      type: String,
      default: 'secondary'
    },
    clazz: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dropPlaceholder: {
      type: String,
      default: null
    },
    fileMessage: {
      type: String,
      default: ''
    },
    loadFileMessage: {
      type: String,
      default: ''
    },
    loadFileStatus: {
      type: String,
      default: ''
    },
    highlightedLoadFileStatus: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    plusCircle: {
      type: Boolean,
      default: false
    },
    showLoader: {
      type: Boolean,
      default: false
    },
    spinnerVariant: {
      type: String,
      default: 'success'
    },
    state: {
      type: Boolean,
      default: null
    },
    title: {
      type: String,
      default: ''
    },
    value: {
      type: [Object, File],
      default: null
    }
  },
  data() {
    return {
      file: this.value
    }
  },
  computed: {
    uploadedClazz() {
      if (this.successUploadFile) return 'is-valid'
      if (this.errorUploadFile) return 'is-invalid'
      return ''
    },

    errorUploadFile() {
      return this.loadFileStatus === 'errorUpload' ||
        this.loadFileStatus === 'error'
    },

    stateClazz() {
      if (this.state === null) return ''
      return this.state ? 'is-valid' : 'is-invalid'
    },

    successUploadFile() {
      return this.loadFileStatus === 'successUpload' ||
        this.loadFileStatus === 'success'
    },

    uploadFileClazz() {
      if (this.highlightedLoadFileStatus) {
        if (this.successUploadFile) return 'highlight-success'
        if (this.errorUploadFile) return 'highlight-error'
        if (this.showLoader) return 'highlight-loading'
        return 'highlight-attach'
      }
      return ''
    }
  },

  watch: {
    loadFileStatus(value) {
      if (value) this.$emit('doneLoading');
    },

    value(val) {
      this.file = val;
    }
  },

  methods: {
    change(file) {
      this.$emit('fileInputEvent', file);
      this.$emit('input', file);
    },

    uploadFileIcon() {
      if (this.successUploadFile) return 'icon-check-circle is-valid'
      if (this.errorUploadFile) return 'icon-circle-plus is-invalid'
      return 'icon-circle-plus is-invalid'
    },

    reset() {
      this.$refs['file-input'].reset();
    }
  }
};

/* script */
const __vue_script__$1o = script$1o;

/* template */
var __vue_render__$1o = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    [
      _c(
        "div",
        {
          class: [
            "me-input-file",
            _vm.stateClazz,
            _vm.uploadFileClazz,
            _vm.uploadedClazz,
            _vm.disabled ? "disabled" : ""
          ]
        },
        [
          _c(
            "div",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: _vm.showLoader,
                  expression: "showLoader"
                }
              ],
              staticClass: "me-input"
            },
            [
              _c(
                "transition",
                { attrs: { name: "transition" } },
                [
                  !_vm.loadFileStatus
                    ? [
                        _c(
                          "div",
                          { staticClass: "loader-spinner" },
                          [
                            _c("Spinner", {
                              attrs: { variant: _vm.spinnerVariant, fluid: "" }
                            })
                          ],
                          1
                        )
                      ]
                    : [_c("i", { class: "me-icon " + _vm.uploadFileIcon() })]
                ],
                2
              ),
              _vm._v(" "),
              _vm.loadFileMessage && !_vm.errorUploadFile && _vm.state !== false
                ? [
                    _c("p", {
                      directives: [
                        {
                          name: "me-safe-html",
                          rawName: "v-me-safe-html",
                          value: _vm.loadFileMessage,
                          expression: "loadFileMessage"
                        }
                      ],
                      class: _vm.uploadedClazz
                    })
                  ]
                : _vm._e()
            ],
            2
          ),
          _vm._v(" "),
          _c(
            "div",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: !_vm.showLoader,
                  expression: "!showLoader"
                }
              ],
              staticClass: "me-input",
              style:
                _vm.placeholder || _vm.dropPlaceholder
                  ? { "margin-bottom": "calc(1rem + 7.2px)" }
                  : ""
            },
            [
              _vm.title
                ? _c("p", { staticClass: "title" }, [
                    _vm._v("\n        " + _vm._s(_vm.title) + "\n      ")
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm.plusCircle
                ? _c("i", {
                    class: {
                      "me-icon icon-circle-plus": _vm.plusCircle,
                      "me-icon icon-circle-plus is-valid":
                        _vm.stateClazz === "is-valid" || _vm.successUploadFile,
                      "me-icon icon-circle-plus is-invalid":
                        _vm.stateClazz === "is-invalid" || _vm.errorUploadFile,
                      "me-icon icon-check-circle is-valid":
                        _vm.stateClazz === "is-valid" || _vm.successUploadFile
                    }
                  })
                : _vm._e(),
              _vm._v(" "),
              _vm.fileMessage
                ? _c("p", { style: { "padding-bottom": "7.2px" } }, [
                    _vm._v("\n        " + _vm._s(_vm.fileMessage) + "\n      ")
                  ])
                : _vm._e(),
              _vm._v(" "),
              _vm.buttonText
                ? _c(
                    "Button",
                    { class: _vm.clazz, attrs: { variant: _vm.buttonVariant } },
                    [_vm._v("\n        " + _vm._s(_vm.buttonText) + "\n      ")]
                  )
                : _vm._e(),
              _vm._v(" "),
              _c("BFormFile", {
                ref: "file-input",
                attrs: {
                  accept: _vm.accept,
                  "drop-placeholder": _vm.dropPlaceholder,
                  disabled: _vm.disabled,
                  multiple: _vm.multiple,
                  placeholder: _vm.placeholder
                },
                on: { input: _vm.change },
                model: {
                  value: _vm.file,
                  callback: function($$v) {
                    _vm.file = $$v;
                  },
                  expression: "file"
                }
              })
            ],
            1
          )
        ]
      ),
      _vm._v(" "),
      _vm.loadFileMessage && (_vm.errorUploadFile || _vm.state === false)
        ? [
            _c("div", {
              directives: [
                {
                  name: "me-safe-html",
                  rawName: "v-me-safe-html",
                  value: _vm.loadFileMessage,
                  expression: "loadFileMessage"
                }
              ],
              staticClass: "error-feedback"
            })
          ]
        : _vm._e()
    ],
    2
  )
};
var __vue_staticRenderFns__$1o = [];
__vue_render__$1o._withStripped = true;

  /* style */
  const __vue_inject_styles__$1o = function (inject) {
    if (!inject) return
    inject("data-v-78274852_0", { source: ".error-feedback[data-v-78274852] {\n  font-size: 12px;\n  line-height: 16px;\n  margin-top: 4px;\n  color: var(--me-danger-2);\n}\n.me-input-file[data-v-78274852] {\n  border-radius: 4px;\n  border: 1px dashed var(--me-gray-6);\n  font-size: 12px;\n  line-height: 16px;\n  cursor: pointer;\n  min-height: 100px;\n  position: relative;\n  padding: 16px;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n}\n.me-input-file .me-input[data-v-78274852] {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n}\n.me-input-file .me-input .loader-spinner[data-v-78274852] {\n  margin-bottom: 7.2px;\n  width: 25.6px;\n  height: 25.6px;\n}\n.me-input-file .me-input p[data-v-78274852] {\n  text-align: center;\n  margin-bottom: 0;\n}\n.me-input-file .me-input p.title[data-v-78274852] {\n  color: var(--me-gray-2);\n  font-size: 20px;\n  font-weight: 600;\n  text-align: center;\n  padding-bottom: 7.2px;\n}\n.me-input-file .me-icon[data-v-78274852] {\n  font-size: 25.6px;\n  color: var(--me-primary-2);\n  padding: 7.2px 0 15px 0;\n}\n.me-input-file .is-valid[data-v-78274852] {\n  color: var(--me-success-2);\n}\n.me-input-file .is-invalid[data-v-78274852] {\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-input-file.is-valid[data-v-78274852] {\n  color: var(--me-success-2);\n}\n.me-input-file.is-valid[data-v-78274852]  .custom-file-label {\n  color: var(--me-success-2) !important;\n}\n.me-input-file.is-invalid[data-v-78274852] {\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-input-file.is-invalid[data-v-78274852]  .custom-file-label {\n  color: var(--me-danger-2) !important;\n}\n.me-input-file.disabled[data-v-78274852] {\n  background-color: var(--me-gray-6);\n  cursor: default;\n}\n.me-input-file.highlight-success[data-v-78274852] {\n  background: var(--me-success-7);\n  border: 1px solid var(--me-success-6);\n  box-shadow: 0 0 8px 0 var(--me-success-6);\n}\n.me-input-file.highlight-error[data-v-78274852] {\n  background: var(--me-danger-7);\n  border: 1px solid var(--me-danger-2);\n  box-shadow: 0 0 8px 0 var(--me-danger-6);\n}\n.me-input-file.highlight-loading[data-v-78274852] {\n  background: var(--me-primary-7);\n  border: 1px solid var(--me-primary-6);\n  box-shadow: 0 0 8px 0 var(--me-primary-6);\n}\n.me-input-file.highlight-attach[data-v-78274852] {\n  background: #F8F8F8;\n}\n.me-input-file .transition-enter-active[data-v-78274852] {\n  transition: opacity 0.8s;\n}\n.me-input-file .transition-enter[data-v-78274852] {\n  opacity: 0;\n}\n.me-input-file .b-form-file[data-v-78274852] {\n  height: 100%;\n  position: absolute;\n  top: 0;\n  left: 0;\n}\n.me-input-file .b-form-file[data-v-78274852]  .custom-file-label {\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-end;\n  padding: 0;\n  margin: 0;\n  background: none;\n  border: none;\n  color: var(--me-gray-4);\n  font-size: 12px;\n  text-align: center;\n  width: 100%;\n  height: 100%;\n  cursor: inherit;\n}\n.me-input-file .b-form-file[data-v-78274852]  .custom-file-label span {\n  padding-bottom: 16px;\n}\n.me-input-file .b-form-file[data-v-78274852]  .custom-file-label::after {\n  display: none;\n}\n.me-input-file .b-form-file[data-v-78274852]  .dragging {\n  background-color: rgba(0, 0, 0, 0.12);\n}\n\n/*# sourceMappingURL=FileInput.vue.map */", map: {"version":3,"sources":["/app/build/src/components/FileInput.vue","FileInput.vue"],"names":[],"mappings":"AA+PA;EACA,eAAA;EACA,iBAAA;EACA,eAAA;EACA,yBAAA;AC9PA;ADiQA;EACA,kBAAA;EACA,mCAAA;EACA,eAAA;EACA,iBAAA;EACA,eAAA;EACA,iBAAA;EACA,kBAAA;EACA,aAAA;EACA,aAAA;EACA,sBAAA;EACA,mBAAA;EACA,uBAAA;AC9PA;ADgQA;EACA,aAAA;EACA,sBAAA;EACA,mBAAA;EACA,uBAAA;AC9PA;ADgQA;EACA,oBAAA;EACA,aAAA;EACA,cAAA;AC9PA;ADiQA;EACA,kBAAA;EACA,gBAAA;AC/PA;ADkQA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,kBAAA;EACA,qBAAA;AChQA;ADoQA;EACA,iBAAA;EACA,0BAAA;EACA,uBAAA;AClQA;ADqQA;EACA,0BAAA;ACnQA;ADsQA;EACA,yBAAA;EACA,gCAAA;ACpQA;ADuQA;EACA,0BAAA;ACrQA;ADuQA;EACA,qCAAA;ACrQA;ADyQA;EACA,yBAAA;EACA,gCAAA;ACvQA;ADyQA;EACA,oCAAA;ACvQA;AD2QA;EACA,kCAAA;EACA,eAAA;ACzQA;AD6QA;EACA,+BAAA;EACA,qCAAA;EACA,yCAAA;AC3QA;AD8QA;EACA,8BAAA;EACA,oCAAA;EACA,wCAAA;AC5QA;AD+QA;EACA,+BAAA;EACA,qCAAA;EACA,yCAAA;AC7QA;ADgRA;EACA,mBAAA;AC9QA;ADkRA;EACA,wBAAA;AChRA;ADmRA;EACA,UAAA;ACjRA;ADoRA;EACA,YAAA;EACA,kBAAA;EACA,MAAA;EACA,OAAA;AClRA;ADoRA;EACA,aAAA;EACA,sBAAA;EACA,yBAAA;EACA,UAAA;EACA,SAAA;EACA,gBAAA;EACA,YAAA;EACA,uBAAA;EACA,eAAA;EACA,kBAAA;EACA,WAAA;EACA,YAAA;EACA,eAAA;AClRA;ADoRA;EACA,oBAAA;AClRA;ADqRA;EACA,aAAA;ACnRA;ADuRA;EACA,qCAAA;ACrRA;;AAEA,wCAAwC","file":"FileInput.vue","sourcesContent":["<template>\n  <section>\n    <div\n      :class=\"['me-input-file', stateClazz, uploadFileClazz,\n               uploadedClazz, disabled ? 'disabled' : '']\"\n    >\n      <div\n        v-show=\"showLoader\"\n        class=\"me-input\"\n      >\n        <transition name=\"transition\">\n          <template v-if=\"!loadFileStatus\">\n            <div class=\"loader-spinner\">\n              <Spinner\n                :variant=\"spinnerVariant\"\n                fluid\n              />\n            </div>\n          </template>\n\n          <template v-else>\n            <i :class=\"'me-icon ' + uploadFileIcon()\" />\n          </template>\n        </transition>\n\n        <template\n          v-if=\"loadFileMessage && !errorUploadFile && state !== false\"\n        >\n          <p\n            v-me-safe-html=\"loadFileMessage\"\n            :class=\"uploadedClazz\"\n          />\n        </template>\n      </div>\n\n      <div\n        v-show=\"!showLoader\"\n        :style=\"placeholder || dropPlaceholder\n          ? { 'margin-bottom': 'calc(1rem + 7.2px)' } : ''\"\n        class=\"me-input\"\n      >\n        <p\n          v-if=\"title\"\n          class=\"title\"\n        >\n          {{ title }}\n        </p>\n\n        <i\n          v-if=\"plusCircle\"\n          :class=\"{\n            'me-icon icon-circle-plus':\n              plusCircle,\n            'me-icon icon-circle-plus is-valid': stateClazz === 'is-valid' ||\n              successUploadFile,\n            'me-icon icon-circle-plus is-invalid': stateClazz === 'is-invalid' ||\n              errorUploadFile,\n            'me-icon icon-check-circle is-valid': stateClazz === 'is-valid' ||\n              successUploadFile\n          }\"\n        />\n\n        <p\n          v-if=\"fileMessage\"\n          :style=\"{ 'padding-bottom': '7.2px' }\"\n        >\n          {{ fileMessage }}\n        </p>\n\n        <Button\n          v-if=\"buttonText\"\n          :class=\"clazz\"\n          :variant=\"buttonVariant\"\n        >\n          {{ buttonText }}\n        </Button>\n\n        <BFormFile\n          ref=\"file-input\"\n          v-model=\"file\"\n          :accept=\"accept\"\n          :drop-placeholder=\"dropPlaceholder\"\n          :disabled=\"disabled\"\n          :multiple=\"multiple\"\n          :placeholder=\"placeholder\"\n          @input=\"change\"\n        />\n      </div>\n    </div>\n\n    <template\n      v-if=\"loadFileMessage && (errorUploadFile || state === false)\"\n    >\n      <div\n        v-me-safe-html=\"loadFileMessage\"\n        class=\"error-feedback\"\n      />\n    </template>\n  </section>\n</template>\n\n<script>\nimport { BFormFile } from 'bootstrap-vue'\nimport Button from './Button.vue'\nimport Spinner from './Spinner.vue'\n\nexport default {\n  name: 'MeFileInput',\n  components: {\n    BFormFile,\n    Button,\n    Spinner\n  },\n  props: {\n    accept: {\n      type: String,\n      default: null\n    },\n    buttonText: {\n      type: String,\n      default: ''\n    },\n    buttonVariant: {\n      type: String,\n      default: 'secondary'\n    },\n    clazz: {\n      type: String,\n      default: ''\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    dropPlaceholder: {\n      type: String,\n      default: null\n    },\n    fileMessage: {\n      type: String,\n      default: ''\n    },\n    loadFileMessage: {\n      type: String,\n      default: ''\n    },\n    loadFileStatus: {\n      type: String,\n      default: ''\n    },\n    highlightedLoadFileStatus: {\n      type: Boolean,\n      default: false\n    },\n    multiple: {\n      type: Boolean,\n      default: false\n    },\n    placeholder: {\n      type: String,\n      default: null\n    },\n    plusCircle: {\n      type: Boolean,\n      default: false\n    },\n    showLoader: {\n      type: Boolean,\n      default: false\n    },\n    spinnerVariant: {\n      type: String,\n      default: 'success'\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    title: {\n      type: String,\n      default: ''\n    },\n    value: {\n      type: [Object, File],\n      default: null\n    }\n  },\n  data() {\n    return {\n      file: this.value\n    }\n  },\n  computed: {\n    uploadedClazz() {\n      if (this.successUploadFile) return 'is-valid'\n      if (this.errorUploadFile) return 'is-invalid'\n      return ''\n    },\n\n    errorUploadFile() {\n      return this.loadFileStatus === 'errorUpload' ||\n        this.loadFileStatus === 'error'\n    },\n\n    stateClazz() {\n      if (this.state === null) return ''\n      return this.state ? 'is-valid' : 'is-invalid'\n    },\n\n    successUploadFile() {\n      return this.loadFileStatus === 'successUpload' ||\n        this.loadFileStatus === 'success'\n    },\n\n    uploadFileClazz() {\n      if (this.highlightedLoadFileStatus) {\n        if (this.successUploadFile) return 'highlight-success'\n        if (this.errorUploadFile) return 'highlight-error'\n        if (this.showLoader) return 'highlight-loading'\n        return 'highlight-attach'\n      }\n      return ''\n    }\n  },\n\n  watch: {\n    loadFileStatus(value) {\n      if (value) this.$emit('doneLoading')\n    },\n\n    value(val) {\n      this.file = val\n    }\n  },\n\n  methods: {\n    change(file) {\n      this.$emit('fileInputEvent', file)\n      this.$emit('input', file)\n    },\n\n    uploadFileIcon() {\n      if (this.successUploadFile) return 'icon-check-circle is-valid'\n      if (this.errorUploadFile) return 'icon-circle-plus is-invalid'\n      return 'icon-circle-plus is-invalid'\n    },\n\n    reset() {\n      this.$refs['file-input'].reset()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .error-feedback {\n    font-size: 12px;\n    line-height: 16px;\n    margin-top: 4px;\n    color: var(--me-danger-2);\n  }\n\n  .me-input-file {\n    border-radius: 4px;\n    border: 1px dashed var(--me-gray-6);\n    font-size: 12px;\n    line-height: 16px;\n    cursor: pointer;\n    min-height: 100px;\n    position: relative;\n    padding: 16px;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    justify-content: center;\n\n    .me-input {\n      display: flex;\n      flex-direction: column;\n      align-items: center;\n      justify-content: center;\n\n      .loader-spinner {\n        margin-bottom: 7.2px;\n        width: 25.6px;\n        height: 25.6px;\n      }\n\n      p {\n        text-align: center;\n        margin-bottom: 0;\n      }\n\n      p.title {\n        color: var(--me-gray-2);\n        font-size: 20px;\n        font-weight: 600;\n        text-align: center;\n        padding-bottom: 7.2px;\n      }\n    }\n\n    .me-icon {\n      font-size: 25.6px;\n      color: var(--me-primary-2);\n      padding: 7.2px 0 15px 0;\n    }\n\n    .is-valid {\n      color: var(--me-success-2);\n    }\n\n    .is-invalid {\n      color: var(--me-danger-2);\n      border-color: var(--me-danger-2);\n    }\n\n    &.is-valid {\n      color: var(--me-success-2);\n\n      ::v-deep .custom-file-label {\n        color: var(--me-success-2) !important;\n      }\n    }\n\n    &.is-invalid {\n      color: var(--me-danger-2);\n      border-color: var(--me-danger-2);\n\n      ::v-deep .custom-file-label {\n        color: var(--me-danger-2) !important;\n      }\n    }\n\n    &.disabled {\n      background-color: var(--me-gray-6);\n      cursor: default;\n    }\n\n    &.highlight {\n      &-success {\n        background: var(--me-success-7);\n        border: 1px solid var(--me-success-6);\n        box-shadow: 0 0 8px 0 var(--me-success-6);\n      }\n\n      &-error {\n        background: var(--me-danger-7);\n        border: 1px solid var(--me-danger-2);\n        box-shadow: 0 0 8px 0 var(--me-danger-6);\n      }\n\n      &-loading {\n        background: var(--me-primary-7);\n        border: 1px solid var(--me-primary-6);\n        box-shadow: 0 0 8px 0 var(--me-primary-6);\n      }\n\n      &-attach {\n        background: #F8F8F8;\n      }\n    }\n\n    .transition-enter-active {\n      transition: opacity .8s;\n    }\n\n    .transition-enter {\n      opacity: 0;\n    }\n\n    .b-form-file {\n      height: 100%;\n      position: absolute;\n      top: 0;\n      left: 0;\n\n      ::v-deep .custom-file-label {\n        display: flex;\n        flex-direction: column;\n        justify-content: flex-end;\n        padding: 0;\n        margin: 0;\n        background: none;\n        border: none;\n        color: var(--me-gray-4);\n        font-size: 12px;\n        text-align: center;\n        width: 100%;\n        height: 100%;\n        cursor: inherit;\n\n        span {\n          padding-bottom: 16px;\n        }\n\n        &::after {\n          display: none;\n        }\n      }\n\n      ::v-deep .dragging {\n        background-color: rgba(0, 0, 0, 0.12);\n      }\n    }\n  }\n</style>\n",".error-feedback {\n  font-size: 12px;\n  line-height: 16px;\n  margin-top: 4px;\n  color: var(--me-danger-2);\n}\n\n.me-input-file {\n  border-radius: 4px;\n  border: 1px dashed var(--me-gray-6);\n  font-size: 12px;\n  line-height: 16px;\n  cursor: pointer;\n  min-height: 100px;\n  position: relative;\n  padding: 16px;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n}\n.me-input-file .me-input {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n}\n.me-input-file .me-input .loader-spinner {\n  margin-bottom: 7.2px;\n  width: 25.6px;\n  height: 25.6px;\n}\n.me-input-file .me-input p {\n  text-align: center;\n  margin-bottom: 0;\n}\n.me-input-file .me-input p.title {\n  color: var(--me-gray-2);\n  font-size: 20px;\n  font-weight: 600;\n  text-align: center;\n  padding-bottom: 7.2px;\n}\n.me-input-file .me-icon {\n  font-size: 25.6px;\n  color: var(--me-primary-2);\n  padding: 7.2px 0 15px 0;\n}\n.me-input-file .is-valid {\n  color: var(--me-success-2);\n}\n.me-input-file .is-invalid {\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-input-file.is-valid {\n  color: var(--me-success-2);\n}\n.me-input-file.is-valid ::v-deep .custom-file-label {\n  color: var(--me-success-2) !important;\n}\n.me-input-file.is-invalid {\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-input-file.is-invalid ::v-deep .custom-file-label {\n  color: var(--me-danger-2) !important;\n}\n.me-input-file.disabled {\n  background-color: var(--me-gray-6);\n  cursor: default;\n}\n.me-input-file.highlight-success {\n  background: var(--me-success-7);\n  border: 1px solid var(--me-success-6);\n  box-shadow: 0 0 8px 0 var(--me-success-6);\n}\n.me-input-file.highlight-error {\n  background: var(--me-danger-7);\n  border: 1px solid var(--me-danger-2);\n  box-shadow: 0 0 8px 0 var(--me-danger-6);\n}\n.me-input-file.highlight-loading {\n  background: var(--me-primary-7);\n  border: 1px solid var(--me-primary-6);\n  box-shadow: 0 0 8px 0 var(--me-primary-6);\n}\n.me-input-file.highlight-attach {\n  background: #F8F8F8;\n}\n.me-input-file .transition-enter-active {\n  transition: opacity 0.8s;\n}\n.me-input-file .transition-enter {\n  opacity: 0;\n}\n.me-input-file .b-form-file {\n  height: 100%;\n  position: absolute;\n  top: 0;\n  left: 0;\n}\n.me-input-file .b-form-file ::v-deep .custom-file-label {\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-end;\n  padding: 0;\n  margin: 0;\n  background: none;\n  border: none;\n  color: var(--me-gray-4);\n  font-size: 12px;\n  text-align: center;\n  width: 100%;\n  height: 100%;\n  cursor: inherit;\n}\n.me-input-file .b-form-file ::v-deep .custom-file-label span {\n  padding-bottom: 16px;\n}\n.me-input-file .b-form-file ::v-deep .custom-file-label::after {\n  display: none;\n}\n.me-input-file .b-form-file ::v-deep .dragging {\n  background-color: rgba(0, 0, 0, 0.12);\n}\n\n/*# sourceMappingURL=FileInput.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1o = "data-v-78274852";
  /* module identifier */
  const __vue_module_identifier__$1o = undefined;
  /* functional template */
  const __vue_is_functional_template__$1o = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1o, staticRenderFns: __vue_staticRenderFns__$1o },
    __vue_inject_styles__$1o,
    __vue_script__$1o,
    __vue_scope_id__$1o,
    __vue_is_functional_template__$1o,
    __vue_module_identifier__$1o,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1p = {
  name: 'MeFilter',
  components: {
    Button: __vue_component__$6,
    Criteria: __vue_component__$O,
    CriteriaBar: __vue_component__$S
  },
  props: {
    fields: {
      type: Array,
      required: true
    },
    opened: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isOpened: this.opened,
      criteria: []
    }
  },
  computed: {
    isEmpty() {
      return isEmpty_1(this.criteria)
    }
  },
  methods: {
    reset() {
      this.criteria = [];
      this.dispatchEvent();
    },
    refine() {
      this.isOpened = true;
    },
    apply() {
      this.isOpened = false;
      this.dispatchEvent();
    },
    onChange(criteria) {
      this.criteria = criteria;
    },
    onBarChange({ criteria }) {
      this.criteria = criteria;
      this.dispatchEvent();
    },
    dispatchEvent() {
      this.$emit('apply', cloneDeep_1(this.criteria));
    }
  }
};

/* script */
const __vue_script__$1p = script$1p;

/* template */
var __vue_render__$1p = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "me-filter" },
    [
      _vm.isOpened
        ? _c("div", { staticClass: "me-card" }, [
            _c(
              "div",
              { staticClass: "content" },
              [
                _c("Criteria", {
                  attrs: {
                    fields: _vm.fields,
                    criteria: _vm.criteria,
                    condensed: false
                  },
                  on: { change: _vm.onChange }
                }),
                _vm._v(" "),
                _vm.isEmpty
                  ? _c("small", { staticClass: "text-muted" }, [
                      _vm._v(
                        "\n        " +
                          _vm._s(_vm.$toolkit.t("filter.description")) +
                          "\n      "
                      )
                    ])
                  : _vm._e()
              ],
              1
            ),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "footer" },
              [
                _c(
                  "Button",
                  { attrs: { variant: "light" }, on: { click: _vm.reset } },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.$toolkit.t("filter.reset")) +
                        "\n      "
                    )
                  ]
                ),
                _vm._v(" "),
                _c(
                  "Button",
                  { attrs: { variant: "primary" }, on: { click: _vm.apply } },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.$toolkit.t("filter.apply")) +
                        "\n      "
                    )
                  ]
                )
              ],
              1
            )
          ])
        : _c("CriteriaBar", {
            attrs: {
              fields: _vm.fields,
              criteria: _vm.criteria,
              "show-refine": ""
            },
            on: { refine: _vm.refine, change: _vm.onBarChange }
          })
    ],
    1
  )
};
var __vue_staticRenderFns__$1p = [];
__vue_render__$1p._withStripped = true;

  /* style */
  const __vue_inject_styles__$1p = function (inject) {
    if (!inject) return
    inject("data-v-3b50b287_0", { source: ".me-card[data-v-3b50b287] {\n  border: 0;\n  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.24);\n  background-color: white;\n  margin-bottom: 1em;\n}\n.me-card > div[data-v-3b50b287] {\n  border-top: 1px solid rgba(0, 0, 0, 0.1);\n  padding: 15px 30px;\n  position: relative;\n}\n.me-card > div[data-v-3b50b287]:first-child {\n  border: 0;\n}\nsection.me-filter[data-v-3b50b287] {\n  margin-bottom: 1rem;\n}\nsection.me-filter > div.me-card > div.footer[data-v-3b50b287] {\n  text-align: right;\n}\n\n/*# sourceMappingURL=Filter.vue.map */", map: {"version":3,"sources":["Filter.vue","/app/build/src/components/Filter.vue"],"names":[],"mappings":"AAAA;EACE,SAAS;EACT,2CAA2C;EAC3C,uBAAuB;EACvB,kBAAkB;AACpB;AACA;EACE,wCAAwC;EACxC,kBAAkB;EAClB,kBAAkB;AACpB;AACA;EACE,SAAS;AACX;ACqFA;EACA,mBAAA;ADlFA;ACqFA;EACA,iBAAA;ADnFA;;AAEA,qCAAqC","file":"Filter.vue","sourcesContent":[".me-card {\n  border: 0;\n  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.24);\n  background-color: white;\n  margin-bottom: 1em;\n}\n.me-card > div {\n  border-top: 1px solid rgba(0, 0, 0, 0.1);\n  padding: 15px 30px;\n  position: relative;\n}\n.me-card > div:first-child {\n  border: 0;\n}\n\nsection.me-filter {\n  margin-bottom: 1rem;\n}\nsection.me-filter > div.me-card > div.footer {\n  text-align: right;\n}\n\n/*# sourceMappingURL=Filter.vue.map */","<template>\n  <section class=\"me-filter\">\n    <div v-if=\"isOpened\" class=\"me-card\">\n      <div class=\"content\">\n        <Criteria\n          :fields=\"fields\"\n          :criteria=\"criteria\"\n          :condensed=\"false\"\n          @change=\"onChange\"\n        />\n        <small v-if=\"isEmpty\" class=\"text-muted\">\n          {{ $toolkit.t('filter.description') }}\n        </small>\n      </div>\n      <div class=\"footer\">\n        <Button @click=\"reset\" variant=\"light\">\n          {{ $toolkit.t('filter.reset') }}\n        </Button>\n        <Button @click=\"apply\" variant=\"primary\">\n          {{ $toolkit.t('filter.apply') }}\n        </Button>\n      </div>\n    </div>\n    <CriteriaBar\n      v-else\n      :fields=\"fields\"\n      :criteria=\"criteria\"\n      @refine=\"refine\"\n      @change=\"onBarChange\"\n      show-refine\n    />\n  </section>\n</template>\n\n<script>\nimport isEmpty from 'lodash/isEmpty'\nimport cloneDeep from 'lodash/cloneDeep'\nimport Button from './Button.vue'\nimport Criteria from './Criteria.vue'\nimport CriteriaBar from './CriteriaBar.vue'\n\nexport default {\n  name: 'MeFilter',\n  components: {\n    Button,\n    Criteria,\n    CriteriaBar\n  },\n  props: {\n    fields: {\n      type: Array,\n      required: true\n    },\n    opened: {\n      type: Boolean,\n      default: true\n    }\n  },\n  data() {\n    return {\n      isOpened: this.opened,\n      criteria: []\n    }\n  },\n  computed: {\n    isEmpty() {\n      return isEmpty(this.criteria)\n    }\n  },\n  methods: {\n    reset() {\n      this.criteria = []\n      this.dispatchEvent()\n    },\n    refine() {\n      this.isOpened = true\n    },\n    apply() {\n      this.isOpened = false\n      this.dispatchEvent()\n    },\n    onChange(criteria) {\n      this.criteria = criteria\n    },\n    onBarChange({ criteria }) {\n      this.criteria = criteria\n      this.dispatchEvent()\n    },\n    dispatchEvent() {\n      this.$emit('apply', cloneDeep(this.criteria))\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @import \"../scss/card.scss\";\n\n  section.me-filter {\n    margin-bottom: 1rem;\n\n    > div.me-card {\n        > div.footer {\n            text-align: right;\n        }\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1p = "data-v-3b50b287";
  /* module identifier */
  const __vue_module_identifier__$1p = undefined;
  /* functional template */
  const __vue_is_functional_template__$1p = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1p, staticRenderFns: __vue_staticRenderFns__$1p },
    __vue_inject_styles__$1p,
    __vue_script__$1p,
    __vue_scope_id__$1p,
    __vue_is_functional_template__$1p,
    __vue_module_identifier__$1p,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1q = {
  name: 'MeFloatPopover',
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    displayed: {
      type: Boolean,
      required: true
    },
    url: {
      type: String,
      default: ''
    },
    float: {
      type: Boolean,
      default: false
    },
    bottomsec: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maximize: false
    }
  },
  methods: {
    toggleMaximize() {
      this.maximize = !this.maximize;
    },

    closeFloatPopover() {
      this.$emit('close');
    },

    answerFloatPopover() {
      this.$emit('answer');
    }
  }
};

/* script */
const __vue_script__$1q = script$1q;

/* template */
var __vue_render__$1q = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _vm.displayed
      ? _c(
          "div",
          {
            staticClass: "float-popover-wrapper",
            class: [
              "sm",
              "info",
              "bottom-right",
              { "bottom-right-secondary": _vm.bottomsec },
              { fixed: _vm.float },
              { maximize: _vm.maximize }
            ]
          },
          [
            _c("div", { staticClass: "float-popover-content" }, [
              _c("div", { staticClass: "header" }, [
                _c("div", [
                  _c("p", { staticClass: "title" }, [
                    _vm._v(
                      "\n            " + _vm._s(_vm.title) + "\n          "
                    )
                  ])
                ]),
                _vm._v(" "),
                _c("div", { staticClass: "arrow" }, [
                  _vm.maximize
                    ? _c("i", {
                        staticClass: "me-icon icon-chevron-up",
                        on: { click: _vm.toggleMaximize }
                      })
                    : _c("i", {
                        staticClass: "me-icon icon-chevron-down",
                        on: { click: _vm.toggleMaximize }
                      })
                ])
              ]),
              _vm._v(" "),
              !_vm.maximize
                ? _c("div", { staticClass: "body" }, [
                    _c("p", { staticClass: "description" }, [
                      _vm._v(
                        "\n          " + _vm._s(_vm.description) + "\n        "
                      )
                    ])
                  ])
                : _vm._e(),
              _vm._v(" "),
              !_vm.maximize
                ? _c(
                    "div",
                    { staticClass: "footer" },
                    [
                      _c(
                        "me-button",
                        {
                          attrs: { variant: "link" },
                          on: { click: _vm.closeFloatPopover }
                        },
                        [
                          _vm._v(
                            "\n          " +
                              _vm._s(_vm.$toolkit.t("floatPopover.cancel")) +
                              "\n        "
                          )
                        ]
                      ),
                      _vm._v(" "),
                      _c(
                        "me-button",
                        {
                          attrs: {
                            target: _vm.url ? "_blank" : "_self",
                            href: _vm.url,
                            variant: "primary",
                            size: "sm"
                          },
                          on: { click: _vm.answerFloatPopover }
                        },
                        [
                          _vm._v(
                            "\n          " +
                              _vm._s(_vm.$toolkit.t("floatPopover.reply")) +
                              "\n        "
                          )
                        ]
                      )
                    ],
                    1
                  )
                : _vm._e()
            ])
          ]
        )
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$1q = [];
__vue_render__$1q._withStripped = true;

  /* style */
  const __vue_inject_styles__$1q = function (inject) {
    if (!inject) return
    inject("data-v-3a373d4e_0", { source: ".float-popover-wrapper[data-v-3a373d4e] {\n  overflow: hidden;\n  padding: 14px 16px;\n  border-radius: 8px;\n  z-index: 9999;\n}\n.float-popover-wrapper.sm[data-v-3a373d4e] {\n  width: 318px;\n}\n.float-popover-wrapper.info[data-v-3a373d4e] {\n  background-color: var(--me-primary-7);\n}\n.float-popover-wrapper.bottom-right[data-v-3a373d4e] {\n  bottom: 18px;\n  right: 20px;\n}\n.float-popover-wrapper.bottom-right-secondary[data-v-3a373d4e] {\n  bottom: 76px;\n}\n.float-popover-wrapper.fixed[data-v-3a373d4e] {\n  position: fixed;\n}\n.float-popover-wrapper .header[data-v-3a373d4e], .float-popover-wrapper .footer[data-v-3a373d4e] {\n  display: flex;\n}\n.float-popover-wrapper .header[data-v-3a373d4e] {\n  position: relative;\n  display: flex;\n  justify-content: space-between;\n}\n.float-popover-wrapper .header .arrow[data-v-3a373d4e] {\n  color: var(--me-primary-2);\n  cursor: pointer;\n}\n.float-popover-wrapper .header .title[data-v-3a373d4e] {\n  font: 600 16px/22px Open Sans;\n}\n.float-popover-wrapper .header .close[data-v-3a373d4e] {\n  position: absolute;\n  top: 3px;\n  right: 1px;\n  font-size: 20px;\n  color: var(--me-gray-0);\n  cursor: pointer;\n}\n.float-popover-wrapper .footer[data-v-3a373d4e] {\n  justify-content: flex-end;\n}\n.float-popover-wrapper .footer .me-button[data-v-3a373d4e]:nth-child(1) {\n  height: 33px;\n}\n.float-popover-wrapper .body[data-v-3a373d4e] {\n  margin: 15px 0;\n}\n.float-popover-content p[data-v-3a373d4e] {\n  color: var(--me-gray-0);\n  margin: 0;\n}\n.float-popover-content .title[data-v-3a373d4e] {\n  font-size: 16px;\n}\n.float-popover-content .description[data-v-3a373d4e] {\n  font-size: 14px;\n}\n\n/*# sourceMappingURL=FloatPopover.vue.map */", map: {"version":3,"sources":["/app/build/src/components/FloatPopover.vue","FloatPopover.vue"],"names":[],"mappings":"AAyGA;EACA,gBAAA;EACA,kBAAA;EACA,kBAAA;EACA,aAAA;ACxGA;ADyGA;EACA,YAAA;ACvGA;ADyGA;EACA,qCAAA;ACvGA;ADyGA;EACA,YAAA;EACA,WAAA;ACvGA;ADyGA;EACA,YAAA;ACvGA;ADyGA;EACA,eAAA;ACvGA;ADyGA;EACA,aAAA;ACvGA;ADyGA;EACA,kBAAA;EACA,aAAA;EACA,8BAAA;ACvGA;ADwGA;EACA,0BAAA;EACA,eAAA;ACtGA;ADwGA;EACA,6BAAA;ACtGA;ADwGA;EACA,kBAAA;EACA,QAAA;EACA,UAAA;EACA,eAAA;EACA,uBAAA;EACA,eAAA;ACtGA;ADyGA;EACA,yBAAA;ACvGA;ADwGA;EACA,YAAA;ACtGA;ADyGA;EACA,cAAA;ACvGA;AD4GA;EACA,uBAAA;EACA,SAAA;ACzGA;AD2GA;EACA,eAAA;ACzGA;AD2GA;EACA,eAAA;ACzGA;;AAEA,2CAA2C","file":"FloatPopover.vue","sourcesContent":["<template>\n  <div>\n    <div\n      v-if=\"displayed\"\n      :class=\"[\n        'sm',\n        'info',\n        'bottom-right',\n        {'bottom-right-secondary': bottomsec},\n        {'fixed': float},\n        {'maximize': maximize}\n      ]\"\n      class=\"float-popover-wrapper\"\n    >\n      <div class=\"float-popover-content\">\n        <div class=\"header\">\n          <div>\n            <p class=\"title\">\n              {{ title }}\n            </p>\n          </div>\n          <div class=\"arrow\">\n            <i v-if=\"maximize\" @click=\"toggleMaximize\" class=\"me-icon icon-chevron-up\" />\n            <i v-else @click=\"toggleMaximize\" class=\"me-icon icon-chevron-down\" />\n          </div>\n        </div>\n        <div v-if=\"!maximize\" class=\"body\">\n          <p class=\"description\">\n            {{ description }}\n          </p>\n        </div>\n        <div v-if=\"!maximize\" class=\"footer\">\n          <me-button\n            @click=\"closeFloatPopover\"\n            variant=\"link\"\n          >\n            {{ $toolkit.t('floatPopover.cancel') }}\n          </me-button>\n\n          <me-button\n            @click=\"answerFloatPopover\"\n            :target=\"url ? '_blank' : '_self'\"\n            :href=\"url\"\n            variant=\"primary\"\n            size=\"sm\"\n          >\n            {{ $toolkit.t('floatPopover.reply') }}\n          </me-button>\n        </div>\n      </div>\n    </div>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeFloatPopover',\n  props: {\n    title: {\n      type: String,\n      required: true\n    },\n    description: {\n      type: String,\n      required: true\n    },\n    displayed: {\n      type: Boolean,\n      required: true\n    },\n    url: {\n      type: String,\n      default: ''\n    },\n    float: {\n      type: Boolean,\n      default: false\n    },\n    bottomsec: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      maximize: false\n    }\n  },\n  methods: {\n    toggleMaximize() {\n      this.maximize = !this.maximize\n    },\n\n    closeFloatPopover() {\n      this.$emit('close')\n    },\n\n    answerFloatPopover() {\n      this.$emit('answer')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.float-popover-wrapper {\n  overflow: hidden;\n  padding: 14px 16px;\n  border-radius: 8px;\n  z-index: 9999;\n  &.sm {\n    width: 318px;\n  }\n  &.info {\n    background-color: var(--me-primary-7)\n  }\n  &.bottom-right {\n    bottom: 18px;\n    right: 20px;\n  }\n  &.bottom-right-secondary {\n    bottom: 76px;\n  }\n  &.fixed {\n    position: fixed;\n  }\n  .header, .footer {\n    display:flex\n  }\n  .header {\n    position: relative;\n    display: flex;\n    justify-content: space-between;\n    .arrow {\n      color: var(--me-primary-2);\n      cursor: pointer;\n    }\n    .title {\n      font: 600 16px/22px Open Sans;\n    }\n    .close {\n      position: absolute;\n      top: 3px;\n      right: 1px;\n      font-size: 20px;\n      color: var(--me-gray-0);\n      cursor: pointer;\n    }\n  }\n  .footer {\n    justify-content: flex-end;\n    .me-button:nth-child(1) {\n      height: 33px;\n    }\n  }\n  .body {\n    margin: 15px 0\n  }\n}\n\n.float-popover-content {\n  p {\n    color: var(--me-gray-0);\n    margin: 0;\n  }\n  .title {\n    font-size: 16px;\n  }\n  .description {\n    font-size: 14px;\n  }\n}\n</style>\n",".float-popover-wrapper {\n  overflow: hidden;\n  padding: 14px 16px;\n  border-radius: 8px;\n  z-index: 9999;\n}\n.float-popover-wrapper.sm {\n  width: 318px;\n}\n.float-popover-wrapper.info {\n  background-color: var(--me-primary-7);\n}\n.float-popover-wrapper.bottom-right {\n  bottom: 18px;\n  right: 20px;\n}\n.float-popover-wrapper.bottom-right-secondary {\n  bottom: 76px;\n}\n.float-popover-wrapper.fixed {\n  position: fixed;\n}\n.float-popover-wrapper .header, .float-popover-wrapper .footer {\n  display: flex;\n}\n.float-popover-wrapper .header {\n  position: relative;\n  display: flex;\n  justify-content: space-between;\n}\n.float-popover-wrapper .header .arrow {\n  color: var(--me-primary-2);\n  cursor: pointer;\n}\n.float-popover-wrapper .header .title {\n  font: 600 16px/22px Open Sans;\n}\n.float-popover-wrapper .header .close {\n  position: absolute;\n  top: 3px;\n  right: 1px;\n  font-size: 20px;\n  color: var(--me-gray-0);\n  cursor: pointer;\n}\n.float-popover-wrapper .footer {\n  justify-content: flex-end;\n}\n.float-popover-wrapper .footer .me-button:nth-child(1) {\n  height: 33px;\n}\n.float-popover-wrapper .body {\n  margin: 15px 0;\n}\n\n.float-popover-content p {\n  color: var(--me-gray-0);\n  margin: 0;\n}\n.float-popover-content .title {\n  font-size: 16px;\n}\n.float-popover-content .description {\n  font-size: 14px;\n}\n\n/*# sourceMappingURL=FloatPopover.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1q = "data-v-3a373d4e";
  /* module identifier */
  const __vue_module_identifier__$1q = undefined;
  /* functional template */
  const __vue_is_functional_template__$1q = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1q, staticRenderFns: __vue_staticRenderFns__$1q },
    __vue_inject_styles__$1q,
    __vue_script__$1q,
    __vue_scope_id__$1q,
    __vue_is_functional_template__$1q,
    __vue_module_identifier__$1q,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1r = {
  name: 'MeListGroup',
  components: {
    AccordionList: __vue_component__$3,
    DropdownItem: __vue_component__$2
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    caret: {
      type: String,
      default: 'me-icon icon-chevron-down'
    },
    left: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    availableItems() {
      return this.items.filter(d => d.url || d.click || (d.children != null && d.children.length > 0))
    }
  },

  methods: {
    setIconToLight(icon) {
      if (!icon.includes('me-icon-l')) {
        return icon.split(' ')
          .filter(iconClass => !['me-icon-s', 'me-icon'].includes(iconClass))
          .concat(['me-icon-l'])
      }

      return icon
    }
  }
};

/* script */
const __vue_script__$1r = script$1r;

/* template */
var __vue_render__$1r = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "ul",
    { staticClass: "me-list-group", attrs: { "data-cy": "listGroup" } },
    [
      _vm._t("default"),
      _vm._v(" "),
      _vm._l(_vm.availableItems, function(item) {
        return [
          item.children != null && item.children.length > 0
            ? _c(
                "li",
                {
                  key: item.label || item.description,
                  attrs: { "data-cy": "listGroupItem" }
                },
                [
                  _c("AccordionList", {
                    attrs: {
                      click: item.click,
                      label: item.label,
                      description: item.description,
                      items: item.children,
                      opened: item.opened,
                      active: item.active,
                      caret: _vm.caret,
                      left: _vm.left
                    }
                  })
                ],
                1
              )
            : _c("DropdownItem", {
                key: item.label || item.description,
                attrs: {
                  href: item.url,
                  active: item.active,
                  "data-cy": "listGroupItem"
                },
                on: {
                  click: function($event) {
                    item.click ? item.click() : null;
                  }
                },
                scopedSlots: _vm._u(
                  [
                    {
                      key: "dropdown-item",
                      fn: function() {
                        return [
                          _c("span", { staticClass: "list-item" }, [
                            item.icon
                              ? _c("i", {
                                  class: [
                                    _vm.setIconToLight(item.icon.class),
                                    { "icon-left": item.icon.left }
                                  ],
                                  style: { color: item.icon.color }
                                })
                              : _vm._e(),
                            _vm._v(
                              "\n          " +
                                _vm._s(item.label || item.description) +
                                "\n        "
                            )
                          ])
                        ]
                      },
                      proxy: true
                    }
                  ],
                  null,
                  true
                )
              })
        ]
      })
    ],
    2
  )
};
var __vue_staticRenderFns__$1r = [];
__vue_render__$1r._withStripped = true;

  /* style */
  const __vue_inject_styles__$1r = function (inject) {
    if (!inject) return
    inject("data-v-66166716_0", { source: ".me-list-group {\n  display: flex;\n  flex-direction: column;\n  padding: 0;\n  list-style: none;\n  margin: 0px;\n  width: 100%;\n}\n.me-list-group .list-item {\n  color: inherit;\n  text-decoration: none;\n  padding: 0 16px;\n  display: flex;\n}\n.me-list-group .list-item > .me-icon-l {\n  margin-left: 8px;\n  font-size: 16px;\n  line-height: 1;\n  order: 1;\n}\n.me-list-group .list-item > .icon-left {\n  margin-right: 8px;\n  order: 0;\n}\n\n/*# sourceMappingURL=ListGroup.vue.map */", map: {"version":3,"sources":["/app/build/src/components/ListGroup.vue","ListGroup.vue"],"names":[],"mappings":"AAyFA;EACA,aAAA;EACA,sBAAA;EACA,UAAA;EACA,gBAAA;EACA,WAAA;EACA,WAAA;ACxFA;AD0FA;EACA,cAAA;EACA,qBAAA;EACA,eAAA;EACA,aAAA;ACxFA;AD0FA;EACA,gBAAA;EACA,eAAA;EACA,cAAA;EACA,QAAA;ACxFA;AD2FA;EACA,iBAAA;EACA,QAAA;ACzFA;;AAEA,wCAAwC","file":"ListGroup.vue","sourcesContent":["<template>\n  <ul class=\"me-list-group\" data-cy=\"listGroup\">\n    <slot />\n    <template v-for=\"item in availableItems\">\n      <li\n        v-if=\"item.children != null && item.children.length > 0\"\n        :key=\"item.label || item.description\"\n        data-cy=\"listGroupItem\"\n      >\n        <AccordionList\n          :click=\"item.click\"\n          :label=\"item.label\"\n          :description=\"item.description\"\n          :items=\"item.children\"\n          :opened=\"item.opened\"\n          :active=\"item.active\"\n          :caret=\"caret\"\n          :left=\"left\"\n        />\n      </li>\n\n      <DropdownItem\n        v-else\n        :key=\"item.label || item.description\"\n        :href=\"item.url\"\n        :active=\"item.active\"\n        @click=\"item.click ? item.click() : null\"\n        data-cy=\"listGroupItem\"\n      >\n        <template v-slot:dropdown-item>\n          <span class=\"list-item\">\n            <i\n              v-if=\"item.icon\"\n              :class=\"[setIconToLight(item.icon.class), { 'icon-left' : item.icon.left }]\"\n              :style=\"{ color: item.icon.color }\"\n            />\n            {{ item.label || item.description }}\n          </span>\n        </template>\n      </DropdownItem>\n    </template>\n  </ul>\n</template>\n\n<script>\nimport AccordionList from './AccordionList.vue'\nimport DropdownItem from './dropdown/DropdownItem.vue'\n\nexport default {\n  name: 'MeListGroup',\n  components: {\n    AccordionList,\n    DropdownItem\n  },\n  props: {\n    items: {\n      type: Array,\n      default: () => []\n    },\n    caret: {\n      type: String,\n      default: 'me-icon icon-chevron-down'\n    },\n    left: {\n      type: Boolean,\n      default: false\n    }\n  },\n  computed: {\n    availableItems() {\n      return this.items.filter(d => d.url || d.click || (d.children != null && d.children.length > 0))\n    }\n  },\n\n  methods: {\n    setIconToLight(icon) {\n      if (!icon.includes('me-icon-l')) {\n        return icon.split(' ')\n          .filter(iconClass => !['me-icon-s', 'me-icon'].includes(iconClass))\n          .concat(['me-icon-l'])\n      }\n\n      return icon\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\">\n  .me-list-group {\n    display: flex;\n    flex-direction: column;\n    padding: 0;\n    list-style: none;\n    margin: 0px;\n    width: 100%;\n\n    .list-item {\n      color: inherit;\n      text-decoration: none;\n      padding: 0 16px;\n      display: flex;\n\n      > .me-icon-l {\n        margin-left: 8px;\n        font-size: 16px;\n        line-height: 1;\n        order: 1;\n      }\n\n      > .icon-left {\n        margin-right: 8px;\n        order: 0;\n      }\n    }\n  }\n</style>\n",".me-list-group {\n  display: flex;\n  flex-direction: column;\n  padding: 0;\n  list-style: none;\n  margin: 0px;\n  width: 100%;\n}\n.me-list-group .list-item {\n  color: inherit;\n  text-decoration: none;\n  padding: 0 16px;\n  display: flex;\n}\n.me-list-group .list-item > .me-icon-l {\n  margin-left: 8px;\n  font-size: 16px;\n  line-height: 1;\n  order: 1;\n}\n.me-list-group .list-item > .icon-left {\n  margin-right: 8px;\n  order: 0;\n}\n\n/*# sourceMappingURL=ListGroup.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1r = undefined;
  /* module identifier */
  const __vue_module_identifier__$1r = undefined;
  /* functional template */
  const __vue_is_functional_template__$1r = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1r, staticRenderFns: __vue_staticRenderFns__$1r },
    __vue_inject_styles__$1r,
    __vue_script__$1r,
    __vue_scope_id__$1r,
    __vue_is_functional_template__$1r,
    __vue_module_identifier__$1r,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1s = {
  name: 'MeSitemap',
  components: {
    ListGroup: __vue_component__$1r
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    caret: {
      type: String,
      default: 'me-icon icon-chevron-down'
    },
    left: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      search: ''
    }
  },
  computed: {
    filtered() {
      if (this.search.trim() === '') {
        return this.items
      }
      const filteredItems = [];
      this.items.forEach((parentItem) => {
        const childrenFiltered = parentItem.children.filter((childItem) => {
          return childItem.description
            .toLowerCase()
            .includes(this.search.toLowerCase().trim())
        });
        if (childrenFiltered.length) {
          const filteredItem = Object.assign({}, parentItem);
          filteredItem.children = childrenFiltered;
          filteredItems.push(filteredItem);
          if (this.search.trim() !== '') {
            filteredItem.opened = true;
          } else {
            filteredItem.opened = false;
          }
        }
      });
      return filteredItems
    }
  },
  mounted() {
    if (this.$refs.search) {
      this.$refs.search.focus();
    }
  }
};

/* script */
const __vue_script__$1s = script$1s;

/* template */
var __vue_render__$1s = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-site-map" }, [
    _vm.searchable
      ? _c("header", [
          _c("input", {
            directives: [
              {
                name: "model",
                rawName: "v-model",
                value: _vm.search,
                expression: "search"
              }
            ],
            ref: "search",
            staticClass: "form-control form-control-sm rounded-pill",
            attrs: {
              placeholder: _vm.$toolkit.t("sitemap.search"),
              type: "text"
            },
            domProps: { value: _vm.search },
            on: {
              input: function($event) {
                if ($event.target.composing) {
                  return
                }
                _vm.search = $event.target.value;
              }
            }
          })
        ])
      : _vm._e(),
    _vm._v(" "),
    _c(
      "div",
      [
        _c("ListGroup", {
          attrs: { items: _vm.filtered, caret: _vm.caret, left: _vm.left }
        })
      ],
      1
    )
  ])
};
var __vue_staticRenderFns__$1s = [];
__vue_render__$1s._withStripped = true;

  /* style */
  const __vue_inject_styles__$1s = function (inject) {
    if (!inject) return
    inject("data-v-6724efd9_0", { source: "div.me-site-map[data-v-6724efd9] {\n  width: 300px;\n}\ndiv.me-site-map > header[data-v-6724efd9] {\n  margin: 5px;\n}\ndiv.me-site-map > header > input.rounded-pill[data-v-6724efd9] {\n  border-radius: 6px !important;\n}\ndiv.me-site-map > div[data-v-6724efd9] {\n  max-height: 500px;\n  overflow: auto;\n  margin-left: -5px;\n  margin-right: -5px;\n}\ndiv.me-site-map > div[data-v-6724efd9]  div.me-accordion-list > span {\n  cursor: pointer;\n  font-size: 14px;\n  font-weight: 600;\n}\ndiv.me-site-map > div[data-v-6724efd9]  div.me-accordion-list > span > i {\n  font-size: 12px;\n}\ndiv.me-site-map > div[data-v-6724efd9]  div.me-accordion-list .dropdown-item {\n  padding-left: 14px;\n}\ndiv.me-site-map > div[data-v-6724efd9]  div.me-accordion-list .dropdown-item:hover {\n  background-color: var(--me-white);\n}\n\n/*# sourceMappingURL=Sitemap.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Sitemap.vue","Sitemap.vue"],"names":[],"mappings":"AAmFA;EACA,YAAA;AClFA;ADoFA;EACA,WAAA;AClFA;ADoFA;EACA,6BAAA;AClFA;ADsFA;EACA,iBAAA;EACA,cAAA;EACA,iBAAA;EACA,kBAAA;ACpFA;ADuFA;EACA,eAAA;EACA,eAAA;EACA,gBAAA;ACrFA;ADuFA;EACA,eAAA;ACrFA;ADyFA;EACA,kBAAA;ACvFA;ADyFA;EACA,iCAAA;ACvFA;;AAEA,sCAAsC","file":"Sitemap.vue","sourcesContent":["<template>\n  <div class=\"me-site-map\">\n    <header v-if=\"searchable\">\n      <input\n        ref=\"search\"\n        v-model=\"search\"\n        :placeholder=\"$toolkit.t('sitemap.search')\"\n        type=\"text\"\n        class=\"form-control form-control-sm rounded-pill\"\n      >\n    </header>\n    <div>\n      <ListGroup :items=\"filtered\" :caret=\"caret\" :left=\"left\" />\n    </div>\n  </div>\n</template>\n\n<script>\nimport ListGroup from './ListGroup.vue'\n\nexport default {\n  name: 'MeSitemap',\n  components: {\n    ListGroup\n  },\n  props: {\n    items: {\n      type: Array,\n      required: true\n    },\n    searchable: {\n      type: Boolean,\n      default: false\n    },\n    caret: {\n      type: String,\n      default: 'me-icon icon-chevron-down'\n    },\n    left: {\n      type: Boolean,\n      default: false\n    }\n  },\n  data() {\n    return {\n      search: ''\n    }\n  },\n  computed: {\n    filtered() {\n      if (this.search.trim() === '') {\n        return this.items\n      }\n      const filteredItems = []\n      this.items.forEach((parentItem) => {\n        const childrenFiltered = parentItem.children.filter((childItem) => {\n          return childItem.description\n            .toLowerCase()\n            .includes(this.search.toLowerCase().trim())\n        })\n        if (childrenFiltered.length) {\n          const filteredItem = Object.assign({}, parentItem)\n          filteredItem.children = childrenFiltered\n          filteredItems.push(filteredItem)\n          if (this.search.trim() !== '') {\n            filteredItem.opened = true\n          } else {\n            filteredItem.opened = false\n          }\n        }\n      })\n      return filteredItems\n    }\n  },\n  mounted() {\n    if (this.$refs.search) {\n      this.$refs.search.focus()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-site-map {\n    width: 300px;\n\n    > header {\n      margin: 5px;\n\n      > input.rounded-pill {\n        border-radius: 6px !important;\n      }\n    }\n\n    > div {\n      max-height: 500px;\n      overflow: auto;\n      margin-left: -5px;\n      margin-right: -5px;\n\n      ::v-deep div.me-accordion-list {\n         > span {\n          cursor: pointer;\n          font-size: 14px;\n          font-weight: 600;\n\n          > i {\n            font-size: 12px;\n          }\n        }\n\n        .dropdown-item {\n          padding-left: 14px;\n\n          &:hover {\n            background-color: var(--me-white);\n          }\n        }\n      }\n    }\n  }\n</style>\n","div.me-site-map {\n  width: 300px;\n}\ndiv.me-site-map > header {\n  margin: 5px;\n}\ndiv.me-site-map > header > input.rounded-pill {\n  border-radius: 6px !important;\n}\ndiv.me-site-map > div {\n  max-height: 500px;\n  overflow: auto;\n  margin-left: -5px;\n  margin-right: -5px;\n}\ndiv.me-site-map > div ::v-deep div.me-accordion-list > span {\n  cursor: pointer;\n  font-size: 14px;\n  font-weight: 600;\n}\ndiv.me-site-map > div ::v-deep div.me-accordion-list > span > i {\n  font-size: 12px;\n}\ndiv.me-site-map > div ::v-deep div.me-accordion-list .dropdown-item {\n  padding-left: 14px;\n}\ndiv.me-site-map > div ::v-deep div.me-accordion-list .dropdown-item:hover {\n  background-color: var(--me-white);\n}\n\n/*# sourceMappingURL=Sitemap.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1s = "data-v-6724efd9";
  /* module identifier */
  const __vue_module_identifier__$1s = undefined;
  /* functional template */
  const __vue_is_functional_template__$1s = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1s, staticRenderFns: __vue_staticRenderFns__$1s },
    __vue_inject_styles__$1s,
    __vue_script__$1s,
    __vue_scope_id__$1s,
    __vue_is_functional_template__$1s,
    __vue_module_identifier__$1s,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1t = {
  name: 'MeFooter',
  components: {
    Popover: __vue_component__$1,
    Icon: __vue_component__,
    Sitemap: __vue_component__$1s
  },
  props: {
    navItems: {
      type: Array,
      required: true
    },
    siteMap: {
      type: Array,
      required: false,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      siteMapOpened: false,
      internalId: null
    }
  },
  created() {
    this.internalId = new Date().getTime();
  },
  methods: {
    closeSitemap() {
      this.siteMapOpened = false;
    }
  }
};

/* script */
const __vue_script__$1t = script$1t;

/* template */
var __vue_render__$1t = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "footer",
    { staticClass: "me-footer", class: { sitemap: _vm.siteMapOpened } },
    [
      _c(
        "ul",
        { staticClass: "nav-items" },
        _vm._l(_vm.navItems, function(item, index) {
          return _c(
            "li",
            {
              key: index,
              class: { pressed: _vm.siteMapOpened && item.siteMap }
            },
            [
              item.separator
                ? _c("div", { staticClass: "separator" })
                : _vm._e(),
              _vm._v(" "),
              item.url && !item.siteMap
                ? _c(
                    "a",
                    {
                      class: { active: item.active },
                      attrs: { href: item.url }
                    },
                    [
                      _c("Icon", { attrs: { icon: item.icon } }),
                      _vm._v("\n        " + _vm._s(item.label) + "\n      ")
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              item.click && !item.siteMap
                ? _c(
                    "a",
                    {
                      class: { active: item.active },
                      on: { click: item.click }
                    },
                    [
                      _c("Icon", { attrs: { icon: item.icon } }),
                      _vm._v("\n        " + _vm._s(item.label) + "\n      ")
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              item.siteMap
                ? _c(
                    "a",
                    {
                      attrs: { id: "popover-" + _vm.internalId },
                      on: {
                        click: function($event) {
                          item.click ? item.click() : null;
                        }
                      }
                    },
                    [
                      _c("Icon", { attrs: { icon: item.icon } }),
                      _vm._v("\n        " + _vm._s(item.label) + "\n      ")
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              item.siteMap
                ? _c(
                    "Popover",
                    {
                      attrs: {
                        target: "popover-" + _vm.internalId,
                        show: _vm.siteMapOpened,
                        placement: "bottom",
                        triggers: "click"
                      },
                      on: {
                        "update:show": function($event) {
                          _vm.siteMapOpened = $event;
                        }
                      }
                    },
                    [
                      _c("Sitemap", {
                        attrs: {
                          searchable: true,
                          items: _vm.siteMap,
                          caret: "me-icon-s icon-caret-down",
                          left: ""
                        }
                      })
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          )
        }),
        0
      ),
      _vm._v(" "),
      _c("div", { staticClass: "backdrop", on: { click: _vm.closeSitemap } })
    ]
  )
};
var __vue_staticRenderFns__$1t = [];
__vue_render__$1t._withStripped = true;

  /* style */
  const __vue_inject_styles__$1t = function (inject) {
    if (!inject) return
    inject("data-v-27a178dc_0", { source: "footer.me-footer[data-v-27a178dc] {\n  position: relative;\n}\nfooter.me-footer > ul.nav-items[data-v-27a178dc] {\n  display: flex;\n  padding: 0;\n  margin: 0;\n  list-style: none;\n  height: 100%;\n  align-items: center;\n}\nfooter.me-footer > ul.nav-items > li[data-v-27a178dc] {\n  text-align: center;\n  padding: 10px 15px;\n}\nfooter.me-footer > ul.nav-items > li > a[data-v-27a178dc] {\n  position: relative;\n  font-size: 12px;\n  color: var(--me-gray-0) !important;\n  cursor: pointer;\n  opacity: 0.48;\n  transform: opacity;\n  transition-duration: 0.2s;\n  display: block;\n}\nfooter.me-footer > ul.nav-items > li > a > i[data-v-27a178dc] {\n  display: block;\n  font-size: 22px;\n  margin-bottom: 5px;\n  line-height: 1;\n}\nfooter.me-footer > ul.nav-items > li > a[data-v-27a178dc]:hover {\n  text-decoration: none;\n  color: var(--me-primary-2) !important;\n}\nfooter.me-footer > ul.nav-items > li > a.active[data-v-27a178dc], footer.me-footer > ul.nav-items > li > a[data-v-27a178dc]:hover {\n  opacity: 1;\n}\nfooter.me-footer > ul.nav-items > li > a.active[data-v-27a178dc] {\n  border-bottom: 1px solid;\n  color: var(--me-primary-2) !important;\n}\nfooter.me-footer > ul.nav-items > li.pressed[data-v-27a178dc] {\n  background-color: rgba(0, 0, 0, 0.2);\n}\nfooter.me-footer > div.backdrop[data-v-27a178dc] {\n  display: none;\n  position: fixed;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  z-index: 1040;\n  background-color: var(--me-gray-0);\n  opacity: 0;\n  transition: opacity;\n  transition-duration: 0.2s;\n}\nfooter.me-footer.sitemap > div.backdrop[data-v-27a178dc] {\n  display: block;\n  opacity: 0.2;\n}\n\n/*# sourceMappingURL=Footer.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Footer.vue","Footer.vue"],"names":[],"mappings":"AA6FA;EACA,kBAAA;AC5FA;AD8FA;EACA,aAAA;EACA,UAAA;EACA,SAAA;EACA,gBAAA;EACA,YAAA;EACA,mBAAA;AC5FA;AD8FA;EACA,kBAAA;EACA,kBAAA;AC5FA;AD8FA;EACA,kBAAA;EACA,eAAA;EACA,kCAAA;EACA,eAAA;EACA,aAAA;EACA,kBAAA;EACA,yBAAA;EACA,cAAA;AC5FA;AD8FA;EACA,cAAA;EACA,eAAA;EACA,kBAAA;EACA,cAAA;AC5FA;AD+FA;EACA,qBAAA;EACA,qCAAA;AC7FA;ADgGA;EAEA,UAAA;AC/FA;ADkGA;EACA,wBAAA;EACA,qCAAA;AChGA;ADoGA;EACA,oCAAA;AClGA;ADuGA;EACA,aAAA;EACA,eAAA;EACA,MAAA;EACA,QAAA;EACA,SAAA;EACA,OAAA;EACA,aAAA;EACA,kCAAA;EACA,UAAA;EACA,mBAAA;EACA,yBAAA;ACrGA;ADyGA;EACA,cAAA;EACA,YAAA;ACvGA;;AAEA,qCAAqC","file":"Footer.vue","sourcesContent":["<template>\n  <footer\n    :class=\"{ 'sitemap': siteMapOpened }\"\n    class=\"me-footer\"\n  >\n    <ul class=\"nav-items\">\n      <li v-for=\"(item, index) in navItems\" :key=\"index\" :class=\"{ pressed: (siteMapOpened && item.siteMap) }\">\n        <div v-if=\"item.separator\" class=\"separator\" />\n        <a\n          v-if=\"item.url && !item.siteMap\"\n          :href=\"item.url\"\n          :class=\"{ active: item.active }\"\n        >\n          <Icon :icon=\"item.icon\" />\n          {{ item.label }}\n        </a>\n\n        <a\n          v-if=\"item.click && !item.siteMap\"\n          :class=\"{ active: item.active }\"\n          @click=\"item.click\"\n        >\n          <Icon :icon=\"item.icon\" />\n          {{ item.label }}\n        </a>\n\n        <a\n          v-if=\"item.siteMap\"\n          :id=\"'popover-' + internalId\"\n          @click=\"item.click ? item.click() : null\"\n        >\n          <Icon :icon=\"item.icon\" />\n          {{ item.label }}\n        </a>\n\n        <Popover\n          v-if=\"item.siteMap\"\n          :target=\"'popover-' + internalId\"\n          :show.sync=\"siteMapOpened\"\n          placement=\"bottom\"\n          triggers=\"click\"\n        >\n          <Sitemap :searchable=\"true\" :items=\"siteMap\" caret=\"me-icon-s icon-caret-down\" left />\n        </Popover>\n      </li>\n    </ul>\n    <div @click=\"closeSitemap\" class=\"backdrop\" />\n  </footer>\n</template>\n\n<script>\nimport Popover from './Popover.vue'\nimport Icon from './Icon.vue'\nimport Sitemap from './Sitemap.vue'\n\nexport default {\n  name: 'MeFooter',\n  components: {\n    Popover,\n    Icon,\n    Sitemap\n  },\n  props: {\n    navItems: {\n      type: Array,\n      required: true\n    },\n    siteMap: {\n      type: Array,\n      required: false,\n      default() {\n        return []\n      }\n    }\n  },\n  data() {\n    return {\n      siteMapOpened: false,\n      internalId: null\n    }\n  },\n  created() {\n    this.internalId = new Date().getTime()\n  },\n  methods: {\n    closeSitemap() {\n      this.siteMapOpened = false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  footer.me-footer {\n    position: relative;\n\n    > ul.nav-items {\n      display: flex;\n      padding: 0;\n      margin: 0;\n      list-style: none;\n      height: 100%;\n      align-items: center;\n\n      > li {\n        text-align: center;\n        padding: 10px 15px;\n\n        > a {\n          position: relative;\n          font-size: 12px;\n          color: var(--me-gray-0) !important;\n          cursor: pointer;\n          opacity: 0.48;\n          transform: opacity;\n          transition-duration: 0.2s;\n          display: block;\n\n          > i {\n            display: block;\n            font-size: 22px;\n            margin-bottom: 5px;\n            line-height: 1;\n          }\n\n          &:hover {\n            text-decoration: none;\n            color: var(--me-primary-2) !important;\n          }\n\n          &.active,\n          &:hover {\n            opacity: 1;\n          }\n\n          &.active {\n            border-bottom: 1px solid;\n            color: var(--me-primary-2) !important;\n          }\n        }\n\n        &.pressed {\n          background-color: rgba(0, 0, 0, 0.2);\n        }\n      }\n    }\n\n    > div.backdrop {\n      display: none;\n      position: fixed;\n      top: 0;\n      right: 0;\n      bottom: 0;\n      left: 0;\n      z-index: 1040;\n      background-color: var(--me-gray-0);\n      opacity: 0;\n      transition: opacity;\n      transition-duration: 0.2s;\n    }\n\n    &.sitemap {\n      > div.backdrop {\n        display: block;\n        opacity: 0.2;\n      }\n    }\n  }\n</style>\n","footer.me-footer {\n  position: relative;\n}\nfooter.me-footer > ul.nav-items {\n  display: flex;\n  padding: 0;\n  margin: 0;\n  list-style: none;\n  height: 100%;\n  align-items: center;\n}\nfooter.me-footer > ul.nav-items > li {\n  text-align: center;\n  padding: 10px 15px;\n}\nfooter.me-footer > ul.nav-items > li > a {\n  position: relative;\n  font-size: 12px;\n  color: var(--me-gray-0) !important;\n  cursor: pointer;\n  opacity: 0.48;\n  transform: opacity;\n  transition-duration: 0.2s;\n  display: block;\n}\nfooter.me-footer > ul.nav-items > li > a > i {\n  display: block;\n  font-size: 22px;\n  margin-bottom: 5px;\n  line-height: 1;\n}\nfooter.me-footer > ul.nav-items > li > a:hover {\n  text-decoration: none;\n  color: var(--me-primary-2) !important;\n}\nfooter.me-footer > ul.nav-items > li > a.active, footer.me-footer > ul.nav-items > li > a:hover {\n  opacity: 1;\n}\nfooter.me-footer > ul.nav-items > li > a.active {\n  border-bottom: 1px solid;\n  color: var(--me-primary-2) !important;\n}\nfooter.me-footer > ul.nav-items > li.pressed {\n  background-color: rgba(0, 0, 0, 0.2);\n}\nfooter.me-footer > div.backdrop {\n  display: none;\n  position: fixed;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  z-index: 1040;\n  background-color: var(--me-gray-0);\n  opacity: 0;\n  transition: opacity;\n  transition-duration: 0.2s;\n}\nfooter.me-footer.sitemap > div.backdrop {\n  display: block;\n  opacity: 0.2;\n}\n\n/*# sourceMappingURL=Footer.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1t = "data-v-27a178dc";
  /* module identifier */
  const __vue_module_identifier__$1t = undefined;
  /* functional template */
  const __vue_is_functional_template__$1t = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1t, staticRenderFns: __vue_staticRenderFns__$1t },
    __vue_inject_styles__$1t,
    __vue_script__$1t,
    __vue_scope_id__$1t,
    __vue_is_functional_template__$1t,
    __vue_module_identifier__$1t,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//


var script$1u = {
  name: 'MeForehead',
  props: {
    badge: {
      type: Object,
      default: () => ({})
    },
    badgeColor: {
      type: String,
      default: 'var(--me-primary-2)'
    },
    badgeFontColor: {
      type: String,
      default: '#fff'
    },
    logoUrl: {
      type: String,
      default: ''
    },
    optionalIcon: {
      type: String,
      default: ''
    },
    badgeBackground: {
      type: Boolean,
      default: false
    },
    additionalColumn: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$1u = script$1u;

/* template */
var __vue_render__$1u = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "header",
    { staticClass: "me-forehead d-flex", class: { "--logo": !!_vm.logoUrl } },
    [
      _c(
        "div",
        {
          staticClass:
            "me-forehead__badge d-flex flex-column justify-content-around align-items-start",
          style: { backgroundColor: _vm.badgeColor, color: _vm.badgeFontColor }
        },
        [
          _vm._t("badge", [
            _vm.badge.label
              ? _c("span", [_vm._v(_vm._s(_vm.badge.label))])
              : _vm._e(),
            _vm._v(" "),
            _vm.badge.code
              ? _c("h5", [
                  _vm._v("\n        " + _vm._s(_vm.badge.code) + "\n      ")
                ])
              : _vm._e(),
            _vm._v(" "),
            _vm.badge.serial
              ? _c("small", [_vm._v(_vm._s(_vm.badge.serial))])
              : _vm._e()
          ]),
          _vm._v(" "),
          _vm.logoUrl
            ? _c("div", {
                staticClass: "me-forehead__badge__image",
                style: {
                  backgroundImage: "url(" + _vm.logoUrl + ")",
                  borderColor: _vm.badgeColor
                }
              })
            : _vm._e(),
          _vm._v(" "),
          _vm.badgeBackground
            ? _c(
                "div",
                { staticClass: "me-forehead__badge__icon" },
                [
                  _vm._t("badgeBackground", [
                    _vm.badgeBackground
                      ? _c("i", { class: "me-icon-l " + _vm.optionalIcon })
                      : _vm._e()
                  ])
                ],
                2
              )
            : _vm._e()
        ],
        2
      ),
      _vm._v(" "),
      _c("div", { staticClass: "me-forehead__content d-flex w-100" }, [
        _c(
          "div",
          {
            staticClass: "d-flex flex-column justify-content-around flex-grow-1"
          },
          [_vm._t("col-1")],
          2
        ),
        _vm._v(" "),
        _c(
          "div",
          {
            staticClass:
              "me-forehead__content__end d-flex flex-column justify-content-around text-right"
          },
          [_vm._t("col-2")],
          2
        ),
        _vm._v(" "),
        _vm.additionalColumn
          ? _c(
              "div",
              {
                staticClass:
                  "me-forehead__content__end d-flex flex-column justify-content-around text-right additional-column"
              },
              [_vm._t("col-3")],
              2
            )
          : _vm._e()
      ])
    ]
  )
};
var __vue_staticRenderFns__$1u = [];
__vue_render__$1u._withStripped = true;

  /* style */
  const __vue_inject_styles__$1u = function (inject) {
    if (!inject) return
    inject("data-v-bfe95b02_0", { source: ".me-forehead__badge[data-v-bfe95b02] {\n  border-radius: 0 8px 8px 0;\n  color: var(--me-white);\n  min-width: 250px;\n  padding: 10px;\n  position: relative;\n}\n.me-forehead__badge__image[data-v-bfe95b02] {\n  background-color: var(--me-white);\n  background-position: center;\n  background-repeat: no-repeat;\n  background-size: contain;\n  border-radius: 50%;\n  border: 1px solid;\n  height: 50px;\n  left: 100%;\n  position: absolute;\n  top: 50%;\n  transform: translate(-50%, -50%);\n  width: 50px;\n}\n.me-forehead__badge__icon[data-v-bfe95b02] {\n  contain: paint;\n  height: 80px;\n  left: 0;\n  min-width: 250px;\n  position: absolute;\n}\n.me-forehead__badge__icon i[data-v-bfe95b02] {\n  font-size: 64px;\n  color: rgba(0, 0, 0, 0.12);\n  position: absolute;\n  right: -15px;\n  top: -27px;\n}\n.me-forehead__content[data-v-bfe95b02] {\n  padding-left: 1rem;\n}\n.me-forehead__content__end.additional-column[data-v-bfe95b02] {\n  min-width: 150px;\n}\n.me-forehead.--logo .me-forehead__content[data-v-bfe95b02] {\n  padding-left: 2rem;\n}\n\n/*# sourceMappingURL=Forehead.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Forehead.vue","Forehead.vue"],"names":[],"mappings":"AAiFA;EACA,0BAAA;EACA,sBAAA;EACA,gBAAA;EACA,aAAA;EACA,kBAAA;AChFA;ADkFA;EACA,iCAAA;EACA,2BAAA;EACA,4BAAA;EACA,wBAAA;EACA,kBAAA;EACA,iBAAA;EACA,YAAA;EACA,UAAA;EACA,kBAAA;EACA,QAAA;EACA,gCAAA;EACA,WAAA;AChFA;ADmFA;EACA,cAAA;EACA,YAAA;EACA,OAAA;EACA,gBAAA;EACA,kBAAA;ACjFA;ADmFA;EACA,eAAA;EACA,0BAAA;EACA,kBAAA;EACA,YAAA;EACA,UAAA;ACjFA;ADsFA;EACA,kBAAA;ACpFA;ADuFA;EACA,gBAAA;ACrFA;AD2FA;EACA,kBAAA;ACzFA;;AAEA,uCAAuC","file":"Forehead.vue","sourcesContent":["<template>\n  <header :class=\"{'--logo':!!logoUrl}\" class=\"me-forehead d-flex\">\n    <div\n      :style=\"{backgroundColor: badgeColor, color: badgeFontColor}\"\n      class=\"me-forehead__badge d-flex flex-column justify-content-around align-items-start\"\n    >\n      <slot name=\"badge\">\n        <span v-if=\"badge.label\">{{ badge.label }}</span>\n        <h5 v-if=\"badge.code\">\n          {{ badge.code }}\n        </h5>\n        <small v-if=\"badge.serial\">{{ badge.serial }}</small>\n      </slot>\n\n      <div\n        v-if=\"logoUrl\"\n        :style=\"{backgroundImage: `url(${logoUrl})`, borderColor: badgeColor}\"\n        class=\"me-forehead__badge__image\"\n      />\n      <div v-if=\"badgeBackground\" class=\"me-forehead__badge__icon\">\n        <slot name=\"badgeBackground\">\n          <i v-if=\"badgeBackground\" :class=\"`me-icon-l ${optionalIcon}`\" />\n        </slot>\n      </div>\n    </div>\n\n    <div class=\"me-forehead__content d-flex w-100\">\n      <div class=\"d-flex flex-column justify-content-around flex-grow-1\">\n        <slot name=\"col-1\" />\n      </div>\n\n      <div class=\"me-forehead__content__end d-flex flex-column justify-content-around text-right\">\n        <slot name=\"col-2\" />\n      </div>\n\n      <div v-if=\"additionalColumn\" class=\"me-forehead__content__end d-flex flex-column justify-content-around text-right additional-column\">\n        <slot name=\"col-3\" />\n      </div>\n    </div>\n  </header>\n</template>\n\n<script>\n\nexport default {\n  name: 'MeForehead',\n  props: {\n    badge: {\n      type: Object,\n      default: () => ({})\n    },\n    badgeColor: {\n      type: String,\n      default: 'var(--me-primary-2)'\n    },\n    badgeFontColor: {\n      type: String,\n      default: '#fff'\n    },\n    logoUrl: {\n      type: String,\n      default: ''\n    },\n    optionalIcon: {\n      type: String,\n      default: ''\n    },\n    badgeBackground: {\n      type: Boolean,\n      default: false\n    },\n    additionalColumn: {\n      type: Boolean,\n      default: false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-forehead {\n    &__badge {\n      border-radius: 0 8px 8px 0;\n      color: var(--me-white);\n      min-width: 250px;\n      padding: 10px;\n      position: relative;\n\n      &__image {\n        background-color: var(--me-white);\n        background-position: center;\n        background-repeat: no-repeat;\n        background-size: contain;\n        border-radius: 50%;\n        border: 1px solid;\n        height: 50px;\n        left: 100%;\n        position: absolute;\n        top: 50%;\n        transform: translate(-50%, -50%);\n        width: 50px;\n      }\n\n      &__icon {\n        contain: paint;\n        height: 80px;\n        left: 0;\n        min-width: 250px;\n        position: absolute;\n\n        i {\n          font-size: 64px;\n          color: rgba(0, 0, 0, 0.12);\n          position: absolute;\n          right: -15px;\n          top: -27px;\n        }\n      }\n    }\n\n    &__content {\n      padding-left: 1rem;\n\n      &__end {\n        &.additional-column {\n          min-width: 150px;\n        }\n      }\n    }\n\n    &.--logo {\n      .me-forehead__content {\n        padding-left: 2rem;\n      }\n    }\n  }\n</style>\n",".me-forehead__badge {\n  border-radius: 0 8px 8px 0;\n  color: var(--me-white);\n  min-width: 250px;\n  padding: 10px;\n  position: relative;\n}\n.me-forehead__badge__image {\n  background-color: var(--me-white);\n  background-position: center;\n  background-repeat: no-repeat;\n  background-size: contain;\n  border-radius: 50%;\n  border: 1px solid;\n  height: 50px;\n  left: 100%;\n  position: absolute;\n  top: 50%;\n  transform: translate(-50%, -50%);\n  width: 50px;\n}\n.me-forehead__badge__icon {\n  contain: paint;\n  height: 80px;\n  left: 0;\n  min-width: 250px;\n  position: absolute;\n}\n.me-forehead__badge__icon i {\n  font-size: 64px;\n  color: rgba(0, 0, 0, 0.12);\n  position: absolute;\n  right: -15px;\n  top: -27px;\n}\n.me-forehead__content {\n  padding-left: 1rem;\n}\n.me-forehead__content__end.additional-column {\n  min-width: 150px;\n}\n.me-forehead.--logo .me-forehead__content {\n  padding-left: 2rem;\n}\n\n/*# sourceMappingURL=Forehead.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1u = "data-v-bfe95b02";
  /* module identifier */
  const __vue_module_identifier__$1u = undefined;
  /* functional template */
  const __vue_is_functional_template__$1u = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1u, staticRenderFns: __vue_staticRenderFns__$1u },
    __vue_inject_styles__$1u,
    __vue_script__$1u,
    __vue_scope_id__$1u,
    __vue_is_functional_template__$1u,
    __vue_module_identifier__$1u,
    false,
    createInjector,
    undefined,
    undefined
  );

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn(object, iteratee) {
  return object && _baseFor(object, iteratee, keys_1);
}

var _baseForOwn = baseForOwn;

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike_1(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

var _createBaseEach = createBaseEach;

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach = _createBaseEach(_baseForOwn);

var _baseEach = baseEach;

/**
 * The base implementation of `_.map` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function baseMap(collection, iteratee) {
  var index = -1,
      result = isArrayLike_1(collection) ? Array(collection.length) : [];

  _baseEach(collection, function(value, key, collection) {
    result[++index] = iteratee(value, key, collection);
  });
  return result;
}

var _baseMap = baseMap;

/**
 * The base implementation of `_.sortBy` which uses `comparer` to define the
 * sort order of `array` and replaces criteria objects with their corresponding
 * values.
 *
 * @private
 * @param {Array} array The array to sort.
 * @param {Function} comparer The function to define sort order.
 * @returns {Array} Returns `array`.
 */
function baseSortBy(array, comparer) {
  var length = array.length;

  array.sort(comparer);
  while (length--) {
    array[length] = array[length].value;
  }
  return array;
}

var _baseSortBy = baseSortBy;

/**
 * Compares values to sort them in ascending order.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {number} Returns the sort order indicator for `value`.
 */
function compareAscending(value, other) {
  if (value !== other) {
    var valIsDefined = value !== undefined,
        valIsNull = value === null,
        valIsReflexive = value === value,
        valIsSymbol = isSymbol_1(value);

    var othIsDefined = other !== undefined,
        othIsNull = other === null,
        othIsReflexive = other === other,
        othIsSymbol = isSymbol_1(other);

    if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
        (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
        (valIsNull && othIsDefined && othIsReflexive) ||
        (!valIsDefined && othIsReflexive) ||
        !valIsReflexive) {
      return 1;
    }
    if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
        (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
        (othIsNull && valIsDefined && valIsReflexive) ||
        (!othIsDefined && valIsReflexive) ||
        !othIsReflexive) {
      return -1;
    }
  }
  return 0;
}

var _compareAscending = compareAscending;

/**
 * Used by `_.orderBy` to compare multiple properties of a value to another
 * and stable sort them.
 *
 * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
 * specify an order of "desc" for descending or "asc" for ascending sort order
 * of corresponding values.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {boolean[]|string[]} orders The order to sort by for each property.
 * @returns {number} Returns the sort order indicator for `object`.
 */
function compareMultiple(object, other, orders) {
  var index = -1,
      objCriteria = object.criteria,
      othCriteria = other.criteria,
      length = objCriteria.length,
      ordersLength = orders.length;

  while (++index < length) {
    var result = _compareAscending(objCriteria[index], othCriteria[index]);
    if (result) {
      if (index >= ordersLength) {
        return result;
      }
      var order = orders[index];
      return result * (order == 'desc' ? -1 : 1);
    }
  }
  // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
  // that causes it, under certain circumstances, to provide the same value for
  // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
  // for more details.
  //
  // This also ensures a stable sort in V8 and other engines.
  // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
  return object.index - other.index;
}

var _compareMultiple = compareMultiple;

/**
 * The base implementation of `_.orderBy` without param guards.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
 * @param {string[]} orders The sort orders of `iteratees`.
 * @returns {Array} Returns the new sorted array.
 */
function baseOrderBy(collection, iteratees, orders) {
  if (iteratees.length) {
    iteratees = _arrayMap(iteratees, function(iteratee) {
      if (isArray_1(iteratee)) {
        return function(value) {
          return _baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
        }
      }
      return iteratee;
    });
  } else {
    iteratees = [identity_1];
  }

  var index = -1;
  iteratees = _arrayMap(iteratees, _baseUnary(_baseIteratee));

  var result = _baseMap(collection, function(value, key, collection) {
    var criteria = _arrayMap(iteratees, function(iteratee) {
      return iteratee(value);
    });
    return { 'criteria': criteria, 'index': ++index, 'value': value };
  });

  return _baseSortBy(result, function(object, other) {
    return _compareMultiple(object, other, orders);
  });
}

var _baseOrderBy = baseOrderBy;

/**
 * Creates an array of elements, sorted in ascending order by the results of
 * running each element in a collection thru each iteratee. This method
 * performs a stable sort, that is, it preserves the original sort order of
 * equal elements. The iteratees are invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {...(Function|Function[])} [iteratees=[_.identity]]
 *  The iteratees to sort by.
 * @returns {Array} Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 36 },
 *   { 'user': 'fred',   'age': 30 },
 *   { 'user': 'barney', 'age': 34 }
 * ];
 *
 * _.sortBy(users, [function(o) { return o.user; }]);
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
 *
 * _.sortBy(users, ['user', 'age']);
 * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
 */
var sortBy = _baseRest(function(collection, iteratees) {
  if (collection == null) {
    return [];
  }
  var length = iteratees.length;
  if (length > 1 && _isIterateeCall(collection, iteratees[0], iteratees[1])) {
    iteratees = [];
  } else if (length > 2 && _isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
    iteratees = [iteratees[0]];
  }
  return _baseOrderBy(collection, _baseFlatten(iteratees, 1), []);
});

var sortBy_1 = sortBy;

/**
 * Creates a new array concatenating `array` with any additional arrays
 * and/or values.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The array to concatenate.
 * @param {...*} [values] The values to concatenate.
 * @returns {Array} Returns the new concatenated array.
 * @example
 *
 * var array = [1];
 * var other = _.concat(array, 2, [3], [[4]]);
 *
 * console.log(other);
 * // => [1, 2, 3, [4]]
 *
 * console.log(array);
 * // => [1]
 */
function concat$1() {
  var length = arguments.length;
  if (!length) {
    return [];
  }
  var args = Array(length - 1),
      array = arguments[0],
      index = length;

  while (index--) {
    args[index - 1] = arguments[index];
  }
  return _arrayPush(isArray_1(array) ? _copyArray(array) : [array], _baseFlatten(args, 1));
}

var concat_1 = concat$1;

/**
 * The base implementation of `_.filter` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function baseFilter(collection, predicate) {
  var result = [];
  _baseEach(collection, function(value, index, collection) {
    if (predicate(value, index, collection)) {
      result.push(value);
    }
  });
  return result;
}

var _baseFilter = baseFilter;

/**
 * Iterates over elements of `collection`, returning an array of all elements
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * **Note:** Unlike `_.remove`, this method returns a new array.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 * @see _.reject
 * @example
 *
 * var users = [
 *   { 'user': 'barney', 'age': 36, 'active': true },
 *   { 'user': 'fred',   'age': 40, 'active': false }
 * ];
 *
 * _.filter(users, function(o) { return !o.active; });
 * // => objects for ['fred']
 *
 * // The `_.matches` iteratee shorthand.
 * _.filter(users, { 'age': 36, 'active': true });
 * // => objects for ['barney']
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.filter(users, ['active', false]);
 * // => objects for ['fred']
 *
 * // The `_.property` iteratee shorthand.
 * _.filter(users, 'active');
 * // => objects for ['barney']
 *
 * // Combining several predicates using `_.overEvery` or `_.overSome`.
 * _.filter(users, _.overSome([{ 'age': 36 }, ['age', 40]]));
 * // => objects for ['fred', 'barney']
 */
function filter(collection, predicate) {
  var func = isArray_1(collection) ? _arrayFilter : _baseFilter;
  return func(collection, _baseIteratee(predicate));
}

var filter_1 = filter;

/**
 * The base implementation of `_.some` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function baseSome(collection, predicate) {
  var result;

  _baseEach(collection, function(value, index, collection) {
    result = predicate(value, index, collection);
    return !result;
  });
  return !!result;
}

var _baseSome = baseSome;

/**
 * Checks if `predicate` returns truthy for **any** element of `collection`.
 * Iteration is stopped once `predicate` returns truthy. The predicate is
 * invoked with three arguments: (value, index|key, collection).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 * @example
 *
 * _.some([null, 0, 'yes', false], Boolean);
 * // => true
 *
 * var users = [
 *   { 'user': 'barney', 'active': true },
 *   { 'user': 'fred',   'active': false }
 * ];
 *
 * // The `_.matches` iteratee shorthand.
 * _.some(users, { 'user': 'barney', 'active': false });
 * // => false
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.some(users, ['active', false]);
 * // => true
 *
 * // The `_.property` iteratee shorthand.
 * _.some(users, 'active');
 * // => true
 */
function some(collection, predicate, guard) {
  var func = isArray_1(collection) ? _arraySome : _baseSome;
  if (guard && _isIterateeCall(collection, predicate, guard)) {
    predicate = undefined;
  }
  return func(collection, _baseIteratee(predicate));
}

var some_1 = some;

/**
 * A specialized version of `_.every` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if all elements pass the predicate check,
 *  else `false`.
 */
function arrayEvery(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (!predicate(array[index], index, array)) {
      return false;
    }
  }
  return true;
}

var _arrayEvery = arrayEvery;

/**
 * The base implementation of `_.every` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if all elements pass the predicate check,
 *  else `false`
 */
function baseEvery(collection, predicate) {
  var result = true;
  _baseEach(collection, function(value, index, collection) {
    result = !!predicate(value, index, collection);
    return result;
  });
  return result;
}

var _baseEvery = baseEvery;

/**
 * Checks if `predicate` returns truthy for **all** elements of `collection`.
 * Iteration is stopped once `predicate` returns falsey. The predicate is
 * invoked with three arguments: (value, index|key, collection).
 *
 * **Note:** This method returns `true` for
 * [empty collections](https://en.wikipedia.org/wiki/Empty_set) because
 * [everything is true](https://en.wikipedia.org/wiki/Vacuous_truth) of
 * elements of empty collections.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {boolean} Returns `true` if all elements pass the predicate check,
 *  else `false`.
 * @example
 *
 * _.every([true, 1, null, 'yes'], Boolean);
 * // => false
 *
 * var users = [
 *   { 'user': 'barney', 'age': 36, 'active': false },
 *   { 'user': 'fred',   'age': 40, 'active': false }
 * ];
 *
 * // The `_.matches` iteratee shorthand.
 * _.every(users, { 'user': 'barney', 'active': false });
 * // => false
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.every(users, ['active', false]);
 * // => true
 *
 * // The `_.property` iteratee shorthand.
 * _.every(users, 'active');
 * // => false
 */
function every(collection, predicate, guard) {
  var func = isArray_1(collection) ? _arrayEvery : _baseEvery;
  if (guard && _isIterateeCall(collection, predicate, guard)) {
    predicate = undefined;
  }
  return func(collection, _baseIteratee(predicate));
}

var every_1 = every;

/**
 * Checks if `value` is `null` or `undefined`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
 * @example
 *
 * _.isNil(null);
 * // => true
 *
 * _.isNil(void 0);
 * // => true
 *
 * _.isNil(NaN);
 * // => false
 */
function isNil(value) {
  return value == null;
}

var isNil_1 = isNil;

const rnds8Pool = new Uint8Array(256); // # of random values to pre-allocate

let poolPtr = rnds8Pool.length;
function rng() {
  if (poolPtr > rnds8Pool.length - 16) {
    crypto.randomFillSync(rnds8Pool);
    poolPtr = 0;
  }

  return rnds8Pool.slice(poolPtr, poolPtr += 16);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase();
}

var native = {
  randomUUID: crypto.randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return unsafeStringify(rnds);
}

var localStorage$1 = {
  load(key, cb) {
    const data = localStorage.getItem(key);
    if (data) {
      cb(JSON.parse(data));
    } else {
      cb(null);
    }
  },
  save(key, state) {
    localStorage.setItem(key, JSON.stringify(state));
  },
  remove(key) {
    localStorage.removeItem(key);
  }
};

//
var script$1v = {
  name: 'MeOverlay',
  components: {
    BOverlay
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'light',
      validator: (value) => {
        return [
          'transparent',
          'white',
          'light',
          'dark',
          'primary',
          'secondary',
          'success',
          'danger',
          'warning',
          'info'
        ].includes(value)
      }
    },
    opacity: {
      type: [String, Number],
      default: 0.85
    },
    blur: {
      type: String,
      default: '2px'
    },
    rounded: {
      type: [Boolean, String],
      default: false,
      validator: (value) => {
        return [false, true, 'sm', 'lg', 'circle', 'pill', 'top', 'right', 'bottom', 'left'].includes(value)
      }
    },
    spinnerType: {
      type: String,
      default: 'border',
      validator: (value) => {
        return [
          'border',
          'grow'
        ].includes(value)
      }
    },
    spinnerVariant: {
      type: String,
      default: '',
      validator: (value) => {
        return [
          '',
          'primary',
          'success',
          'warning',
          'danger'
        ].includes(value)
      }
    },
    spinnerSmall: {
      type: Boolean,
      default: false
    },
    noFade: {
      type: Boolean,
      default: false
    },
    noWrap: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$1v = script$1v;

/* template */
var __vue_render__$1v = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("b-overlay", {
    attrs: {
      show: _vm.show,
      variant: _vm.variant,
      opacity: _vm.opacity,
      blur: _vm.blur,
      rounded: _vm.rounded,
      "spinner-type": _vm.spinnerType,
      "spinner-small": _vm.spinnerSmall,
      "spinner-variant": _vm.spinnerVariant,
      "no-fade": _vm.noFade,
      "no-wrap": _vm.noWrap
    },
    on: {
      shown: function($event) {
        return _vm.$emit("shown")
      },
      hidden: function($event) {
        return _vm.$emit("hidden")
      },
      click: function($event) {
        return _vm.$emit("click")
      }
    },
    scopedSlots: _vm._u(
      [
        _vm._l(_vm.$scopedSlots, function(_, slot) {
          return {
            key: slot,
            fn: function(scope) {
              return [_vm._t(slot, null, null, scope)]
            }
          }
        })
      ],
      null,
      true
    )
  })
};
var __vue_staticRenderFns__$1v = [];
__vue_render__$1v._withStripped = true;

  /* style */
  const __vue_inject_styles__$1v = undefined;
  /* scoped */
  const __vue_scope_id__$1v = undefined;
  /* module identifier */
  const __vue_module_identifier__$1v = undefined;
  /* functional template */
  const __vue_is_functional_template__$1v = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1v, staticRenderFns: __vue_staticRenderFns__$1v },
    __vue_inject_styles__$1v,
    __vue_script__$1v,
    __vue_scope_id__$1v,
    __vue_is_functional_template__$1v,
    __vue_module_identifier__$1v,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$1w = {
  name: 'MeGridColumnChooser',
  components: {
    Draggable: __vue_component__$s
  },
  props: {
    fields: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      isDragging: false,
      isInactiveContainerDisabled: false,
      actives: filter_1(this.fields, ({ active }) => {
        return isNil_1(active) || active
      }),
      inactives: filter_1(this.fields, ({ active }) => {
        return !isNil_1(active) && !active
      })
    }
  },

  computed: {
    isActiveDragDisabled() {
      return this.actives.length === 1
    }
  },

  methods: {
    onChange(e) {
      if (e.added) return

      this.actives.forEach((item) => {
        item.active = true;
      });

      this.inactives.forEach((item) => {
        item.active = false;
      });

      this.$emit('change', this.actives.concat(this.inactives));
    },

    checkMove(e) {
      const moveFromActive = e.draggedContext.element.active;
      if (moveFromActive && this.actives.length === 1) {
        this.isInactiveContainerDisabled = true;
        return false
      }

      return true
    },

    onStart() {
      this.isDragging = true;
    },

    onEnd() {
      this.isInactiveContainerDisabled = false;
      this.isDragging = false;
    }
  }
};

/* script */
const __vue_script__$1w = script$1w;

/* template */
var __vue_render__$1w = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("section", { staticClass: "me-grid-column-chooser" }, [
    _c("div", { staticClass: "row" }, [
      _c("div", { staticClass: "col-sm" }, [
        _c("h6", [
          _vm._v(
            "\n        " +
              _vm._s(_vm.$toolkit.t("grid.columns.actives")) +
              "\n      "
          )
        ])
      ]),
      _vm._v(" "),
      _c("div", { staticClass: "col-sm" }, [
        _c("h6", [
          _vm._v(
            "\n        " +
              _vm._s(_vm.$toolkit.t("grid.columns.inactives")) +
              "\n      "
          )
        ])
      ])
    ]),
    _vm._v(" "),
    _c("div", { staticClass: "row align-items-stretch" }, [
      _c(
        "div",
        { staticClass: "col-sm" },
        [
          _c(
            "Draggable",
            {
              class: { "drag-active p-1": _vm.isDragging },
              attrs: { move: _vm.checkMove, group: "fields", tag: "ul" },
              on: { change: _vm.onChange, start: _vm.onStart, end: _vm.onEnd },
              model: {
                value: _vm.actives,
                callback: function($$v) {
                  _vm.actives = $$v;
                },
                expression: "actives"
              }
            },
            _vm._l(_vm.actives, function(field) {
              return _c(
                "li",
                {
                  key: field.key,
                  class: { "drag-disable": _vm.isActiveDragDisabled },
                  attrs: { item: field }
                },
                [_c("label", [_vm._v(_vm._s(field.label))])]
              )
            }),
            0
          )
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "col-sm" },
        [
          _c(
            "Draggable",
            {
              class: {
                "drag-active p-1": _vm.isDragging,
                "drag-container-disabled": _vm.isInactiveContainerDisabled
              },
              attrs: { move: _vm.checkMove, group: "fields", tag: "ul" },
              on: { change: _vm.onChange, start: _vm.onStart, end: _vm.onEnd },
              model: {
                value: _vm.inactives,
                callback: function($$v) {
                  _vm.inactives = $$v;
                },
                expression: "inactives"
              }
            },
            _vm._l(_vm.inactives, function(field) {
              return _c("li", { key: field.key, attrs: { item: field } }, [
                _c("label", [_vm._v(_vm._s(field.label))])
              ])
            }),
            0
          )
        ],
        1
      )
    ])
  ])
};
var __vue_staticRenderFns__$1w = [];
__vue_render__$1w._withStripped = true;

  /* style */
  const __vue_inject_styles__$1w = function (inject) {
    if (!inject) return
    inject("data-v-0b5442b4_0", { source: "section.me-grid-column-chooser .drag-active[data-v-0b5442b4] {\n  border: 1px dashed var(--me-gray-6);\n  border-radius: 5px;\n}\nsection.me-grid-column-chooser .drag-container-disabled[data-v-0b5442b4] {\n  border: 1px dashed var(--me-danger-2);\n}\nsection.me-grid-column-chooser ul[data-v-0b5442b4] {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n  min-height: 100px;\n  height: 100%;\n}\nsection.me-grid-column-chooser ul > li[data-v-0b5442b4] {\n  padding: 0.5rem;\n  background: #f9f9f9;\n  margin-bottom: 0.5rem;\n  cursor: grab;\n}\nsection.me-grid-column-chooser ul > li.drag-disable[data-v-0b5442b4] {\n  cursor: not-allowed;\n}\nsection.me-grid-column-chooser ul > li > label[data-v-0b5442b4] {\n  user-select: none;\n  margin: 0;\n}\nsection.me-grid-column-chooser ul > li[data-v-0b5442b4]:hover {\n  background: #e8e8e8;\n}\n\n/*# sourceMappingURL=GridColumnChooser.vue.map */", map: {"version":3,"sources":["/app/build/src/components/GridColumnChooser.vue","GridColumnChooser.vue"],"names":[],"mappings":"AA+HA;EACA,mCAAA;EACA,kBAAA;AC9HA;ADiIA;EACA,qCAAA;AC/HA;ADkIA;EACA,SAAA;EACA,UAAA;EACA,gBAAA;EACA,iBAAA;EACA,YAAA;AChIA;ADkIA;EACA,eAAA;EACA,mBAAA;EACA,qBAAA;EACA,YAAA;AChIA;ADkIA;EACA,mBAAA;AChIA;ADmIA;EACA,iBAAA;EACA,SAAA;ACjIA;ADoIA;EACA,mBAAA;AClIA;;AAEA,gDAAgD","file":"GridColumnChooser.vue","sourcesContent":["<template>\n  <section class=\"me-grid-column-chooser\">\n    <div class=\"row\">\n      <div class=\"col-sm\">\n        <h6>\n          {{ $toolkit.t('grid.columns.actives') }}\n        </h6>\n      </div>\n      <div class=\"col-sm\">\n        <h6>\n          {{ $toolkit.t('grid.columns.inactives') }}\n        </h6>\n      </div>\n    </div>\n\n    <div class=\"row align-items-stretch\">\n      <div class=\"col-sm\">\n        <Draggable\n          v-model=\"actives\"\n          @change=\"onChange\"\n          @start=\"onStart\"\n          @end=\"onEnd\"\n          :move=\"checkMove\"\n          :class=\"{'drag-active p-1': isDragging}\"\n          group=\"fields\"\n          tag=\"ul\"\n        >\n          <li v-for=\"field in actives\" :key=\"field.key\" :item=\"field\" :class=\"{'drag-disable': isActiveDragDisabled }\">\n            <label>{{ field.label }}</label>\n          </li>\n        </Draggable>\n      </div>\n      <div class=\"col-sm\">\n        <Draggable\n          v-model=\"inactives\"\n          @change=\"onChange\"\n          @start=\"onStart\"\n          @end=\"onEnd\"\n          :move=\"checkMove\"\n          :class=\"{'drag-active p-1': isDragging, 'drag-container-disabled': isInactiveContainerDisabled}\"\n          group=\"fields\"\n          tag=\"ul\"\n        >\n          <li v-for=\"field in inactives\" :key=\"field.key\" :item=\"field\">\n            <label>{{ field.label }}</label>\n          </li>\n        </Draggable>\n      </div>\n    </div>\n  </section>\n</template>\n\n<script>\nimport filter from 'lodash/filter'\nimport isNil from 'lodash/isNil'\nimport Draggable from './Draggable.vue'\n\nexport default {\n  name: 'MeGridColumnChooser',\n  components: {\n    Draggable\n  },\n  props: {\n    fields: {\n      type: Array,\n      required: true\n    }\n  },\n\n  data() {\n    return {\n      isDragging: false,\n      isInactiveContainerDisabled: false,\n      actives: filter(this.fields, ({ active }) => {\n        return isNil(active) || active\n      }),\n      inactives: filter(this.fields, ({ active }) => {\n        return !isNil(active) && !active\n      })\n    }\n  },\n\n  computed: {\n    isActiveDragDisabled() {\n      return this.actives.length === 1\n    }\n  },\n\n  methods: {\n    onChange(e) {\n      if (e.added) return\n\n      this.actives.forEach((item) => {\n        item.active = true\n      })\n\n      this.inactives.forEach((item) => {\n        item.active = false\n      })\n\n      this.$emit('change', this.actives.concat(this.inactives))\n    },\n\n    checkMove(e) {\n      const moveFromActive = e.draggedContext.element.active\n      if (moveFromActive && this.actives.length === 1) {\n        this.isInactiveContainerDisabled = true\n        return false\n      }\n\n      return true\n    },\n\n    onStart() {\n      this.isDragging = true\n    },\n\n    onEnd() {\n      this.isInactiveContainerDisabled = false\n      this.isDragging = false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  section.me-grid-column-chooser {\n    .drag-active {\n      border: 1px dashed var(--me-gray-6);\n      border-radius: 5px;\n    }\n\n    .drag-container-disabled {\n      border: 1px dashed var(--me-danger-2);\n    }\n\n    ul {\n      margin: 0;\n      padding: 0;\n      list-style: none;\n      min-height: 100px;\n      height: 100%;\n\n      > li {\n        padding: .5rem;\n        background: #f9f9f9;\n        margin-bottom: .5rem;\n        cursor: grab;\n\n        &.drag-disable {\n          cursor: not-allowed;\n        }\n\n        > label {\n          user-select: none;\n          margin: 0;\n        }\n\n        &:hover {\n          background: #e8e8e8;\n        }\n      }\n    }\n  }\n</style>\n","section.me-grid-column-chooser .drag-active {\n  border: 1px dashed var(--me-gray-6);\n  border-radius: 5px;\n}\nsection.me-grid-column-chooser .drag-container-disabled {\n  border: 1px dashed var(--me-danger-2);\n}\nsection.me-grid-column-chooser ul {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n  min-height: 100px;\n  height: 100%;\n}\nsection.me-grid-column-chooser ul > li {\n  padding: 0.5rem;\n  background: #f9f9f9;\n  margin-bottom: 0.5rem;\n  cursor: grab;\n}\nsection.me-grid-column-chooser ul > li.drag-disable {\n  cursor: not-allowed;\n}\nsection.me-grid-column-chooser ul > li > label {\n  user-select: none;\n  margin: 0;\n}\nsection.me-grid-column-chooser ul > li:hover {\n  background: #e8e8e8;\n}\n\n/*# sourceMappingURL=GridColumnChooser.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1w = "data-v-0b5442b4";
  /* module identifier */
  const __vue_module_identifier__$1w = undefined;
  /* functional template */
  const __vue_is_functional_template__$1w = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1w, staticRenderFns: __vue_staticRenderFns__$1w },
    __vue_inject_styles__$1w,
    __vue_script__$1w,
    __vue_scope_id__$1w,
    __vue_is_functional_template__$1w,
    __vue_module_identifier__$1w,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1x = {
  name: 'MeSkeletonGrid',
  components: {
    BSkeleton,
    BSkeletonTable
  },
  props: {
    card: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Number,
      default: 4
    },
    rows: {
      type: Number,
      default: 5
    }
  }
};

/* script */
const __vue_script__$1x = script$1x;

/* template */
var __vue_render__$1x = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-grid" }, [
    _c(
      "header",
      [_c("BSkeleton", { attrs: { height: "30px", width: "200px" } })],
      1
    ),
    _vm._v(" "),
    _c("main", [
      !_vm.card
        ? _c(
            "div",
            [
              _c("BSkeletonTable", {
                attrs: { columns: _vm.columns, rows: _vm.rows }
              })
            ],
            1
          )
        : _c(
            "section",
            _vm._l(_vm.rows, function(index) {
              return _c(
                "div",
                { key: index },
                [
                  _vm._t("card", [
                    _c("BSkeleton", {
                      attrs: { height: "30px", width: "55%" }
                    }),
                    _vm._v(" "),
                    _c("BSkeleton", {
                      attrs: { height: "20px", width: "65%" }
                    }),
                    _vm._v(" "),
                    _c("BSkeleton", { attrs: { height: "20px", width: "50%" } })
                  ])
                ],
                2
              )
            }),
            0
          )
    ])
  ])
};
var __vue_staticRenderFns__$1x = [];
__vue_render__$1x._withStripped = true;

  /* style */
  const __vue_inject_styles__$1x = function (inject) {
    if (!inject) return
    inject("data-v-c79aaf32_0", { source: ".me-skeleton-grid > header[data-v-c79aaf32] {\n  display: flex;\n  flex-flow: row-reverse;\n  margin-bottom: 10px;\n}\n.me-skeleton-grid > main > section[data-v-c79aaf32] {\n  background-color: var(--me-white);\n}\n.me-skeleton-grid > main > section > div[data-v-c79aaf32] {\n  min-height: 50px;\n  padding: 0.75rem;\n}\n.me-skeleton-grid > main > section > div[data-v-c79aaf32]:hover {\n  background-color: rgba(10, 140, 204, 0.12);\n}\n\n/*# sourceMappingURL=SkeletonGrid.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonGrid.vue","SkeletonGrid.vue"],"names":[],"mappings":"AAkDA;EACA,aAAA;EACA,sBAAA;EACA,mBAAA;ACjDA;ADqDA;EACA,iCAAA;ACnDA;ADqDA;EACA,gBAAA;EACA,gBAAA;ACnDA;ADqDA;EACA,0CAAA;ACnDA;;AAEA,2CAA2C","file":"SkeletonGrid.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-grid\">\n    <header>\n      <BSkeleton height=\"30px\" width=\"200px\" />\n    </header>\n    <main>\n      <div v-if=\"!card\">\n        <BSkeletonTable :columns=\"columns\" :rows=\"rows\" />\n      </div>\n      <section v-else>\n        <div v-for=\"index in rows\" :key=\"index\">\n          <slot name=\"card\">\n            <BSkeleton height=\"30px\" width=\"55%\" />\n            <BSkeleton height=\"20px\" width=\"65%\" />\n            <BSkeleton height=\"20px\" width=\"50%\" />\n          </slot>\n        </div>\n      </section>\n    </main>\n  </div>\n</template>\n\n<script>\nimport { BSkeleton, BSkeletonTable } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonGrid',\n  components: {\n    BSkeleton,\n    BSkeletonTable\n  },\n  props: {\n    card: {\n      type: Boolean,\n      default: false\n    },\n    columns: {\n      type: Number,\n      default: 4\n    },\n    rows: {\n      type: Number,\n      default: 5\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-grid {\n    > header {\n      display: flex;\n      flex-flow: row-reverse;\n      margin-bottom: 10px;\n    }\n\n    > main {\n      > section {\n        background-color: var(--me-white);\n\n        > div {\n          min-height: 50px;\n          padding: .75rem;\n\n          &:hover {\n            background-color: rgba(10, 140, 204, 0.12);\n          }\n        }\n      }\n    }\n  }\n</style>\n",".me-skeleton-grid > header {\n  display: flex;\n  flex-flow: row-reverse;\n  margin-bottom: 10px;\n}\n.me-skeleton-grid > main > section {\n  background-color: var(--me-white);\n}\n.me-skeleton-grid > main > section > div {\n  min-height: 50px;\n  padding: 0.75rem;\n}\n.me-skeleton-grid > main > section > div:hover {\n  background-color: rgba(10, 140, 204, 0.12);\n}\n\n/*# sourceMappingURL=SkeletonGrid.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1x = "data-v-c79aaf32";
  /* module identifier */
  const __vue_module_identifier__$1x = undefined;
  /* functional template */
  const __vue_is_functional_template__$1x = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1x, staticRenderFns: __vue_staticRenderFns__$1x },
    __vue_inject_styles__$1x,
    __vue_script__$1x,
    __vue_scope_id__$1x,
    __vue_is_functional_template__$1x,
    __vue_module_identifier__$1x,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1y = {
  name: 'MeCellContent',
  props: {
    cell: {
      type: Object,
      required: true
    }
  }
};

/* script */
const __vue_script__$1y = script$1y;

/* template */
var __vue_render__$1y = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _vm.cell.field.click
      ? _c(
          "div",
          {
            staticClass: "cell-click",
            on: {
              click: function($event) {
                return _vm.cell.field.click(_vm.cell)
              }
            }
          },
          [_vm._v("\n    " + _vm._s(_vm.cell.value) + "\n  ")]
        )
      : _vm.cell.field.hrefValue
      ? _c(
          "a",
          {
            staticClass: "cell-link",
            attrs: { href: _vm.cell.field.hrefValue(_vm.cell) }
          },
          [_vm._v("\n    " + _vm._s(_vm.cell.value) + "\n  ")]
        )
      : _c("span", { attrs: { "data-cy": "cell-content" } }, [
          _vm._v("\n    " + _vm._s(_vm.cell.value) + "\n  ")
        ])
  ])
};
var __vue_staticRenderFns__$1y = [];
__vue_render__$1y._withStripped = true;

  /* style */
  const __vue_inject_styles__$1y = function (inject) {
    if (!inject) return
    inject("data-v-682e1a14_0", { source: ".cell-link[data-v-682e1a14] {\n  color: var(--me-primary-2);\n  text-decoration: none;\n}\n.cell-link[data-v-682e1a14]:hover {\n  color: var(--me-primary-0);\n  text-decoration: underline;\n}\n.cell-click[data-v-682e1a14] {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=CellContent.vue.map */", map: {"version":3,"sources":["/app/build/src/components/grid/cell/CellContent.vue","CellContent.vue"],"names":[],"mappings":"AAwCA;EACA,0BAAA;EACA,qBAAA;ACvCA;ADyCA;EACA,0BAAA;EACA,0BAAA;ACvCA;AD2CA;EACA,eAAA;ACxCA;;AAEA,0CAA0C","file":"CellContent.vue","sourcesContent":["<template>\n  <div>\n    <div\n      v-if=\"cell.field.click\"\n      @click=\"cell.field.click(cell)\"\n      class=\"cell-click\"\n    >\n      {{ cell.value }}\n    </div>\n\n    <a\n      v-else-if=\"cell.field.hrefValue\"\n      :href=\"cell.field.hrefValue(cell)\"\n      class=\"cell-link\"\n    >\n      {{ cell.value }}\n    </a>\n\n    <span\n      v-else\n      data-cy=\"cell-content\"\n    >\n      {{ cell.value }}\n    </span>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeCellContent',\n  props: {\n    cell: {\n      type: Object,\n      required: true\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.cell-link {\n  color: var(--me-primary-2);\n  text-decoration: none;\n\n  &:hover {\n    color: var(--me-primary-0);\n    text-decoration: underline;\n  }\n}\n\n.cell-click {\n  cursor: pointer;\n}\n</style>\n",".cell-link {\n  color: var(--me-primary-2);\n  text-decoration: none;\n}\n.cell-link:hover {\n  color: var(--me-primary-0);\n  text-decoration: underline;\n}\n\n.cell-click {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=CellContent.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1y = "data-v-682e1a14";
  /* module identifier */
  const __vue_module_identifier__$1y = undefined;
  /* functional template */
  const __vue_is_functional_template__$1y = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1y, staticRenderFns: __vue_staticRenderFns__$1y },
    __vue_inject_styles__$1y,
    __vue_script__$1y,
    __vue_scope_id__$1y,
    __vue_is_functional_template__$1y,
    __vue_module_identifier__$1y,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const DRAG_NOT_ALLOWED_CLASS = 'drag-not-allowed';
const DRAGGING_CLASS = 'tr-grabbing';
const STICKY_COLUMN_LEFT_CLASS = 'me-sticky-column-left';
const STICKY_COLUMN_RIGHT_CLASS = 'me-sticky-column-right';

const gridDensityEnum = {
  LARGE: 'lg',
  MEDIUM: 'md',
  SMALL: 'sm'
};

var script$1z = {
  name: 'MeGrid',
  components: {
    BTable,
    BDropdown,
    BDropdownItem,
    BFormRadio,
    Button: __vue_component__$6,
    DropDown: __vue_component__$8,
    DropdownItem: __vue_component__$2,
    Spinner: __vue_component__$5,
    Overlay: __vue_component__$1v,
    Modal: __vue_component__$f,
    GridColumnChooser: __vue_component__$1w,
    Icon: __vue_component__,
    Checkbox: __vue_component__$l,
    SkeletonGrid: __vue_component__$1x,
    CellContent: __vue_component__$1y
  },
  props: {
    customMenu: {
      type: Array,
      default: () => []
    },
    showColumnsCustomization: {
      type: Boolean,
      default: false
    },
    hideDensityOptions: {
      type: Boolean,
      default: false
    },
    onlyFilterByFields: {
      type: Boolean,
      default: false
    },
    fields: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    actions: {
      type: Array,
      default: () => []
    },
    actionsFixed: {
      type: Boolean,
      default: false
    },
    apiUrl: {
      type: String,
      default: ''
    },
    elementsPerPage: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    provider: {
      type: Function,
      default: null
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectablePosition: {
      type: Number,
      default: 0
    },
    uniqueKey: {
      type: String,
      default: 'id'
    },
    selection: {
      type: Array,
      default: () => []
    },
    stateKey: {
      type: String,
      default: ''
    },
    responsive: {
      type: [Boolean, String],
      default: null
    },
    stateProvider: {
      type: Object,
      default() {
        return localStorage$1
      }
    },
    layout: {
      type: String,
      default: 'grid'
    },
    highlightRow: {
      type: Function,
      default: null
    },
    tbodyRowClass: {
      type: Function,
      default: null
    },
    maxTotalHits: {
      type: Number,
      default: 1000000
    },
    toggle: {
      type: Boolean,
      default: false
    },
    selectRadio: {
      type: Boolean,
      default: false
    },
    stickyHeader: {
      type: [Boolean, String],
      default: false
    },
    resizable: {
      type: Boolean,
      default: false
    },
    colsDraggable: {
      type: [Boolean, Object],
      default: false
    },
    initialPageSize: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      gridDensityEnum,
      headerTooltipTimeout: null,
      selectRadioName: `${v4()}_select-radio`,
      actionOpened: null,
      tableWidthsResize: [],
      isBusy: false,
      showCard: false,
      currentPage: 1,
      currentFields: cloneDeep_1(this.fields),
      pageSize: this.elementsPerPage.find(size => size === this.initialPageSize) || this.elementsPerPage[0],
      hits: this.items.length,
      result: this.items,
      isInitialState: true,
      cached: false,
      filter: null,
      state: null,
      ready: false,
      currentLayout: this.layout,
      currentSelection: cloneDeep_1(this.selection),
      currentSelectionMap: this.selection.map(d => d[this.uniqueKey]),
      breakpoint: {
        sm: 576,
        md: 768,
        lg: 992,
        xl: 1200
      },
      grouping: [
        { type: 'area', label: this.$toolkit.t('analytics.types.area'), icon: 'me-icon icon-chart-area' },
        { type: 'bar', label: this.$toolkit.t('analytics.types.bar'), icon: 'me-icon icon-bars' },
        { type: 'column', label: this.$toolkit.t('analytics.types.column'), icon: 'me-icon icon-chart-bar' },
        { type: 'donut', label: this.$toolkit.t('analytics.types.donut'), icon: 'me-icon icon-dot-circle' },
        { type: 'line', label: this.$toolkit.t('analytics.types.line'), icon: 'me-icon icon-chart-line' },
        { type: 'pie', label: this.$toolkit.t('analytics.types.pie'), icon: 'me-icon icon-chart-pie-alt' }
      ],
      maxHitsLimit: 10000,
      hasEllipsis: false,
      densityClazz: gridDensityEnum.MEDIUM
    }
  },

  computed: {
    actionColumn() {
      return {
        key: '_actions',
        label: '',
        thStyle: {
          width: '40px'
        },
        thClass: this.actionsFixed ? [DRAG_NOT_ALLOWED_CLASS, STICKY_COLUMN_RIGHT_CLASS] : [DRAG_NOT_ALLOWED_CLASS],
        tdClass: (_value, _key, item) => {
          if (!this.actionsFixed) return []

          if (isEqual_1(item, this.actionOpened)) return [STICKY_COLUMN_RIGHT_CLASS, 'me-action-opened']

          return STICKY_COLUMN_RIGHT_CLASS
        }
      }
    },
    filterIncludedFields() {
      return this.onlyFilterByFields ? this.columns.map(({ key }) => key) : []
    },
    showGrid() {
      return !this.isInitialState || !this.lazy
    },
    perPage() {
      return this.currentLayout !== 'table' ? this.pageSize : null
    },
    showHeader() {
      return this.currentLayout !== 'table' && this.showGrid
    },
    searchable() {
      return this.items.length > 0 || this.cached
    },
    columns() {
      let fields = [];

      if (this.showCard) {
        fields.push({
          key: '_card',
          label: ''
        });
      } else {
        fields = concat_1(fields, this.activeFields);
      }

      if (this.selectable) {
        const arrayLeftSide = fields.slice(0, this.selectablePosition);
        const selectableField = {
          key: '_checkbox',
          tdClass: ['check-box-column', STICKY_COLUMN_LEFT_CLASS],
          thClass: [DRAG_NOT_ALLOWED_CLASS, STICKY_COLUMN_LEFT_CLASS]
        };
        const arrayRightSide = fields.slice(this.selectablePosition, fields.length);
        fields = [...arrayLeftSide, selectableField, ...arrayRightSide];
      }

      if (this.actions.length > 0) {
        fields.push(this.actionColumn);
      }
      return fields
    },
    actionIcons() {
      return this.actions.filter((action) => { return action.icon })
    },
    actionItems() {
      return this.actions.filter((action) => { return !action.icon })
    },
    activeFields() {
      return filter_1(this.currentFields, ({ active }) => isNil_1(active) || active)
    },
    lazy() {
      return this.apiUrl !== '' || this.provider !== null
    },
    data() {
      if (this.cached) {
        return this.result
      } else if (this.apiUrl !== '') {
        return this.dataLoader
      } else if (this.provider !== null) {
        return this.dataProvider
      } else {
        return this.items
      }
    },
    hasPreviousPage() {
      return this.currentPage > 1
    },
    selectableItems() {
      return this.result.filter(item => !item.selectDisabled)
    },
    pageChecked() {
      return this.selectableItems.length > 0 && every_1(this.selectableItems, item => this.currentSelectionMap.includes(item[this.uniqueKey]))
    },
    pageIndeterminate() {
      return some_1(this.selectableItems, item => this.currentSelectionMap.includes(item[this.uniqueKey])) &&
      some_1(this.selectableItems, item => !this.currentSelectionMap.includes(item[this.uniqueKey]))
    },
    resizeValue() {
      if (this.resizable && this.responsive === null) {
        return { sizes: this.tableWidthsResize }
      }

      return false
    },
    dragOptions() {
      return {
        queryEl: 'thead tr',
        options: {
          handle: '.handle-drag-area',
          group: this.stateKey ? `cols_drag_${this.stateKey}` : '',
          swapThreshold: 0.7,
          filter: '.drag-not-allowed',
          dragClass: 'dragging-item',
          fallbackClass: 'dragging-item',
          forceFallback: true,
          onEnd: (e) => {
            e.target.classList.remove(DRAGGING_CLASS);
            this.moveColumnsOnDragEnd(e);
          },
          onMove: (e) => {
            e.target.classList.add(DRAGGING_CLASS);
            this.isEnableColumnMove(e);
          },
          onChoose: (e) => {
            e.target.classList.add(DRAGGING_CLASS);
          },
          onUnchoose: (e) => {
            e.target.classList.remove(DRAGGING_CLASS);
          },
          onStart: (e) => {
            e.target.classList.add(DRAGGING_CLASS);
          },
          ...{ ...(this.colsDraggable?.options || {}) }
        }
      }
    }
  },
  watch: {
    items(val) {
      const ids = this.items.map(({ id }) => id);
      this.currentSelection = this.currentSelection.filter(({ id }) => ids.includes(id));
      this.hits = val ? val.length : 0;
      this.result = val;
    },
    stateKey(val) {
      this.stateKey = val;
      this.loadState();
    },
    fields(val) {
      const fields = cloneDeep_1(val || []);
      this.mergeState(this.state, fields);
      this.currentFields = sortBy_1(fields, 'order');
    },
    layout(val) {
      this.currentLayout = val;
    },
    selection(val) {
      this.currentSelection = cloneDeep_1(val || []);
    },
    currentSelection(_val) {
      this.currentSelectionMap = this.currentSelection.map(d => d[this.uniqueKey]);
    },
    responsive(_val) {
      this.calculateViewport();
    },
    pageChecked() {
      this.$emit('pageChecked', this.pageChecked);
    },
    pageIndeterminate() {
      this.$emit('pageIndeterminate', this.pageIndeterminate);
    }
  },
  created() {
    this.loadState();
  },
  mounted() {
    this.calculateViewport();
    window.addEventListener('resize', debounce_1(this.calculateViewport, 500));
  },
  methods: {
    handeTooltip(columnName, show = true) {
      this.$root.$emit('bv::hide::tooltip');
      if (!columnName) return

      this.clearHeaderTooltipTimeout();

      this.headerTooltipTimeout = setTimeout(() => {
        this.$root.$emit('bv::hide::tooltip');
        if (show) {
          this.$root.$emit('bv::show::tooltip', columnName);
        }
      }, 500);
    },
    clearHeaderTooltipTimeout() {
      if (this.headerTooltipTimeout) {
        clearTimeout(this.headerTooltipTimeout);
      }
    },
    setDensity(value) {
      this.densityClazz = value;
      this.$emit('density', value);

      if (this.stateKey) {
        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState());
      }
    },
    isEnableColumnMove(evt) {
      return evt.related.className.indexOf(DRAG_NOT_ALLOWED_CLASS) === -1
    },
    moveColumnsOnDragEnd(evt) {
      const { newIndex, oldIndex } = evt;
      const columnFrom = this.columns[oldIndex];
      const columnTo = this.columns[newIndex];

      const fromIndexInCurrentField = this.currentFields.findIndex(field => field.key === columnFrom.key);
      const toIndexInCurrentField = this.currentFields.findIndex(field => field.key === columnTo.key);
      const fieldsMoved = this.moveColumnPosition(this.currentFields, fromIndexInCurrentField, toIndexInCurrentField);

      this.onColumnChange(fieldsMoved);
    },
    moveColumnPosition(columns, fromIndex, toIndex) {
      if (fromIndex === toIndex) return columns

      const newColumns = [...columns];
      const target = newColumns[fromIndex];
      const inc = toIndex < fromIndex ? -1 : 1;

      for (let i = fromIndex; i !== toIndex; i += inc) {
        newColumns[i] = newColumns[i + inc];
        newColumns[i].order = i;
      }

      newColumns[toIndex] = target;
      newColumns[toIndex].order = toIndex;

      return newColumns
    },
    reset() {
      this.isInitialState = true;
    },
    refresh() {
      this.cached = false;
      this.currentPage = 1;
      this.$refs.table.refresh();
    },
    saveColWidth(widths) {
      if (this.stateKey) {
        this.tableWidthsResize = widths;
        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState());
      }
    },
    setPageSize(size) {
      this.pageSize = size;

      if (this.stateKey) {
        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState());
      }
    },
    dataLoader({ apiUrl, currentPage, perPage }) {
      return fetch(`${apiUrl}?page=${currentPage}&size=${perPage}`)
        .then((response) => {
          return response.json()
        })
        .then((data) => {
          if (Array.isArray(data)) {
            this.cached = true;
            this.hits = data.length;
            this.result = data;
          } else {
            this.hits = data.hits || 0;
            this.result = data.result || [];
          }
          this.isInitialState = false;
          return this.result
        })
    },
    dataProvider(ctx, callback) {
      this.provider({
        page: ctx.currentPage,
        pageSize: ctx.perPage,
        sortBy: ctx.sortBy,
        sortDesc: ctx.sortDesc
      }, ({ hits, result }) => {
        this.hits = hits;
        this.result = result;
        this.isInitialState = false;
        callback(result);
      });
      return null
    },
    handleActionClick(item, row) {
      if (item.click) {
        item.click(row);
      }
    },
    openColumnsDialog() {
      this.$refs.modal.show();
    },
    onColumnChange(fields) {
      this.currentFields = fields;
      this.$emit('fieldChange', fields);
      if (this.stateKey) {
        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState());
        this.loadState();
      }
    },
    resetState() {
      if (this.stateKey) {
        this.currentFields = cloneDeep_1(this.fields || []);
        this.densityClazz = gridDensityEnum.MEDIUM;
        this.stateProvider.remove(`grid_${this.stateKey}`);
      }
    },
    loadState(_cb) {
      this.ready = false;
      if (this.stateKey) {
        this.stateProvider.load(`grid_${this.stateKey}`, (state) => {
          this.pageSize = state?.pageSize || this.pageSize;
          const fields = cloneDeep_1(this.fields || []);
          this.mergeState(state, fields);
          this.currentFields = sortBy_1(fields, 'order');
          this.state = state;
          this.ready = true;
          this.tableWidthsResize = state?.columnSizes || [];
          this.densityClazz = state?.gridDensity || this.densityClazz;
        });
      } else {
        this.ready = true;
      }
    },
    mapState() {
      const state = {
        pageSize: this.perPage,
        fields: [],
        columnSizes: this.tableWidthsResize,
        gridDensity: this.densityClazz
      };
      this.currentFields.forEach((item, index) => {
        state.fields.push({
          key: item.key,
          active: item.active,
          order: index
        });
      });
      return state
    },
    mergeState(state, fields) {
      if (state && state.fields) {
        state.fields.forEach((item) => {
          const field = find_1(fields, {
            key: item.key
          });
          if (field) {
            field.order = item.order;
            field.active = item.active;
          }
        });
      }
    },
    checkPage(checked) {
      this.selectableItems.forEach(item => this.checkItem(checked, item));
      this.dispatchSelected();
    },

    toggleItem(item) {
      if (this.currentSelectionMap.includes(item[this.uniqueKey])) {
        this.checkItem(false, item);
      } else {
        this.checkItem(true, item);
      }
    },

    toggleRadioItem(item) {
      this.currentSelection = [item];
      this.$emit('selected', cloneDeep_1(this.currentSelection));
    },

    checkItem(checked, item) {
      const selection = find_1(this.currentSelection, {
        [this.uniqueKey]: item[this.uniqueKey]
      });

      if (!selection && checked) {
        this.currentSelection.push(item);
      } else if (selection && !checked) {
        this.currentSelection.splice(this.currentSelection.indexOf(selection), 1);
      }

      this.dispatchSelected();
    },
    dispatchSelected: debounce_1(function () {
      this.$emit('selected', cloneDeep_1(this.currentSelection));
    }, 500),
    rowClass(item, type) {
      let customRowClasses = '';
      if (this.tbodyRowClass) {
        customRowClasses = this.tbodyRowClass(item, type);
      }

      if (!this.highlightRow) {
        return customRowClasses
      }

      const isHighlight = this.highlightRow(item, type);
      if (isHighlight) {
        return customRowClasses + ' row-highlight'
      }

      return customRowClasses
    },
    calculateViewport() {
      const type = typeof this.responsive;
      if (type === 'boolean') {
        this.showCard = this.responsive;
      } else if (type === 'string') {
        if (window.innerWidth < this.breakpoint[this.responsive]) {
          this.showCard = true;
        } else {
          this.showCard = false;
        }
      } else if (this.responsive === null) {
        this.showCard = false;
      }
    },
    dispatchGrouping(type, field) {
      this.$emit('grouping', {
        type,
        field
      });
    },
    handleTooltipMouseOver(event) {
      this.hasEllipsis = event.target.offsetWidth < event.target.scrollWidth || event.target.offsetHeight < event.target.scrollHeight;
    }
  }
};

/* script */
const __vue_script__$1z = script$1z;

/* template */
var __vue_render__$1z = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.ready
    ? _c(
        "section",
        { staticClass: "me-grid", class: { responsive: _vm.showCard } },
        [
          _vm.showHeader
            ? _c(
                "header",
                [
                  _c("div", { staticClass: "header" }, [_vm._t("header")], 2),
                  _vm._v(" "),
                  _c("me-pagination", {
                    attrs: {
                      "page-size": _vm.pageSize,
                      searchable: _vm.searchable,
                      hits: _vm.hits,
                      "max-hits": _vm.maxTotalHits,
                      lazy: _vm.lazy,
                      refresh: _vm.refresh,
                      "current-page": _vm.currentPage,
                      filter: _vm.filter,
                      "state-key": _vm.stateKey,
                      "show-coluns-customization": _vm.showColumnsCustomization,
                      "hide-density-options": _vm.hideDensityOptions,
                      "custom-menu": _vm.customMenu,
                      "elements-per-page": _vm.elementsPerPage,
                      density: _vm.densityClazz
                    },
                    on: {
                      "update:currentPage": function($event) {
                        _vm.currentPage = $event;
                      },
                      "update:current-page": function($event) {
                        _vm.currentPage = $event;
                      },
                      "update:filter": function($event) {
                        _vm.filter = $event;
                      },
                      openColumnDialog: _vm.openColumnsDialog,
                      resetState: _vm.resetState,
                      setSize: _vm.setPageSize,
                      density: _vm.setDensity
                    }
                  })
                ],
                1
              )
            : _vm._e(),
          _vm._v(" "),
          _vm._t("content"),
          _vm._v(" "),
          _c(
            "Overlay",
            {
              directives: [
                {
                  name: "show",
                  rawName: "v-show",
                  value: _vm.showGrid,
                  expression: "showGrid"
                }
              ],
              attrs: { show: _vm.isBusy, opacity: "0.6" }
            },
            [
              _c(
                "div",
                {
                  staticClass:
                    "text-center text-primary rounded-pill p-3 bg-white",
                  attrs: { slot: "overlay" },
                  slot: "overlay"
                },
                [
                  _c("Spinner", { staticClass: "align-middle" }),
                  _vm._v(
                    "\n      " +
                      _vm._s(_vm.$toolkit.t("spinner.loading")) +
                      "\n    "
                  )
                ],
                1
              ),
              _vm._v(" "),
              _c(
                "BTable",
                {
                  directives: [
                    {
                      name: "me-resize",
                      rawName: "v-me-resize",
                      value: _vm.resizeValue,
                      expression: "resizeValue"
                    },
                    {
                      name: "me-drag-drop",
                      rawName: "v-me-drag-drop",
                      value: _vm.colsDraggable && _vm.dragOptions,
                      expression: "colsDraggable && dragOptions"
                    }
                  ],
                  ref: "table",
                  class: _vm.densityClazz,
                  attrs: {
                    responsive: _vm.responsive === null,
                    fields: _vm.columns,
                    items: _vm.data,
                    "per-page": _vm.perPage,
                    filter: _vm.filter,
                    "filter-included-fields": _vm.filterIncludedFields,
                    "current-page": _vm.currentPage,
                    "api-url": _vm.apiUrl,
                    "primary-key": _vm.uniqueKey,
                    busy: _vm.isBusy,
                    "tbody-tr-class": _vm.rowClass,
                    "sticky-header": _vm.stickyHeader,
                    "show-empty": "",
                    "data-cy": "grid-table"
                  },
                  on: {
                    "update:busy": function($event) {
                      _vm.isBusy = $event;
                    },
                    colresize: _vm.saveColWidth
                  },
                  scopedSlots: _vm._u(
                    [
                      {
                        key: "head()",
                        fn: function(head) {
                          return [
                            _c(
                              "div",
                              {
                                staticClass: "cell-header",
                                class: {
                                  "handle-drag-area":
                                    _vm.colsDraggable &&
                                    head.column !== _vm.actionColumn.key
                                },
                                on: {
                                  mouseenter: function() {
                                    return _vm.handeTooltip(head.column)
                                  },
                                  mouseleave: function() {
                                    return _vm.handeTooltip(head.column, false)
                                  }
                                }
                              },
                              [
                                _c(
                                  "span",
                                  {
                                    staticClass: "cell-header__label",
                                    attrs: { "data-cy": "gridDragArea" }
                                  },
                                  [
                                    _c("span", {
                                      directives: [
                                        {
                                          name: "me-tooltip",
                                          rawName:
                                            "v-me-tooltip.topright.manual.viewport",
                                          value: {
                                            title: head.field.tooltipMsg,
                                            id: head.column,
                                            customClass:
                                              "me-grid-header-tooltip"
                                          },
                                          expression:
                                            "{\n                title: head.field.tooltipMsg,\n                id: head.column,\n                customClass: 'me-grid-header-tooltip'\n              }",
                                          modifiers: {
                                            topright: true,
                                            manual: true,
                                            viewport: true
                                          }
                                        }
                                      ],
                                      staticClass: "cell-header__label__tooltip"
                                    }),
                                    _vm._v(
                                      "\n            " +
                                        _vm._s(head.label) +
                                        "\n          "
                                    )
                                  ]
                                ),
                                _vm._v(" "),
                                _c(
                                  "div",
                                  { staticClass: "header-actions" },
                                  [
                                    head.field.groupable
                                      ? _c(
                                          "DropDown",
                                          {
                                            staticClass: "aggregations",
                                            attrs: {
                                              boundary: "viewport",
                                              variant: "link",
                                              size: "sm",
                                              "no-caret": ""
                                            },
                                            nativeOn: {
                                              mouseenter: function($event) {
                                                return (function() {
                                                  return _vm.handeTooltip(
                                                    head.column,
                                                    false
                                                  )
                                                })()
                                              },
                                              mouseleave: function($event) {
                                                return (function() {
                                                  return _vm.handeTooltip(
                                                    head.column
                                                  )
                                                })()
                                              }
                                            }
                                          },
                                          [
                                            _c(
                                              "template",
                                              { slot: "button-content" },
                                              [
                                                _c("Icon", {
                                                  attrs: {
                                                    size: 12,
                                                    icon:
                                                      "me-icon-s icon-chart-pie",
                                                    color: "var(--me-gray-5)",
                                                    "hover-color":
                                                      "var(--me-primary-2)"
                                                  }
                                                })
                                              ],
                                              1
                                            ),
                                            _vm._v(" "),
                                            _vm._l(_vm.grouping, function(
                                              item
                                            ) {
                                              return _c("DropdownItem", {
                                                key: item.type,
                                                attrs: {
                                                  label: item.label,
                                                  icon: item.icon
                                                },
                                                on: {
                                                  click: function($event) {
                                                    return _vm.dispatchGrouping(
                                                      item.type,
                                                      head.field
                                                    )
                                                  }
                                                }
                                              })
                                            })
                                          ],
                                          2
                                        )
                                      : _vm._e(),
                                    _vm._v(" "),
                                    head.field.sortable
                                      ? _c(
                                          "div",
                                          {
                                            staticClass: "sort-actions",
                                            on: {
                                              mouseenter: function() {
                                                return _vm.handeTooltip(
                                                  head.column,
                                                  false
                                                )
                                              },
                                              mouseleave: function() {
                                                return _vm.handeTooltip(
                                                  head.column
                                                )
                                              }
                                            }
                                          },
                                          [
                                            _c("Icon", {
                                              staticClass: "sort-none",
                                              attrs: {
                                                size: 12,
                                                icon: "me-icon-s icon-sort"
                                              }
                                            }),
                                            _vm._v(" "),
                                            _c("Icon", {
                                              staticClass: "sort-desc",
                                              attrs: {
                                                size: 12,
                                                icon: "me-icon-s icon-sort-down"
                                              }
                                            }),
                                            _vm._v(" "),
                                            _c("Icon", {
                                              staticClass: "sort-asc",
                                              attrs: {
                                                size: 12,
                                                icon: "me-icon-s icon-sort-up"
                                              }
                                            })
                                          ],
                                          1
                                        )
                                      : _vm._e()
                                  ],
                                  1
                                )
                              ]
                            )
                          ]
                        }
                      },
                      {
                        key: "head(_checkbox)",
                        fn: function() {
                          return [
                            _c(
                              "div",
                              { staticClass: "cell-header" },
                              [
                                _vm.selectRadio
                                  ? _c("div")
                                  : _c("Checkbox", {
                                      attrs: {
                                        checked: _vm.pageChecked,
                                        indeterminate: _vm.pageIndeterminate,
                                        toggle: _vm.toggle
                                      },
                                      on: { change: _vm.checkPage }
                                    })
                              ],
                              1
                            )
                          ]
                        },
                        proxy: true
                      },
                      {
                        key: "cell(_checkbox)",
                        fn: function(row) {
                          return [
                            _vm.selectRadio
                              ? _c("BFormRadio", {
                                  directives: [
                                    {
                                      name: "me-tooltip",
                                      rawName: "v-me-tooltip",
                                      value: row.item.selectTooltip,
                                      expression: "row.item.selectTooltip"
                                    }
                                  ],
                                  attrs: {
                                    checked: _vm.currentSelectionMap[0],
                                    value: row.item[_vm.uniqueKey],
                                    disabled: row.item.selectDisabled,
                                    name: _vm.selectRadioName,
                                    "data-cy": "selectRadio"
                                  },
                                  on: {
                                    change: function($event) {
                                      return _vm.toggleRadioItem(row.item)
                                    }
                                  }
                                })
                              : _c("Checkbox", {
                                  directives: [
                                    {
                                      name: "me-tooltip",
                                      rawName: "v-me-tooltip",
                                      value: row.item.selectTooltip,
                                      expression: "row.item.selectTooltip"
                                    }
                                  ],
                                  attrs: {
                                    disabled: row.item.selectDisabled,
                                    value: row.item[_vm.uniqueKey]
                                  },
                                  on: {
                                    change: function($event) {
                                      return _vm.toggleItem(row.item)
                                    }
                                  },
                                  model: {
                                    value: _vm.currentSelectionMap,
                                    callback: function($$v) {
                                      _vm.currentSelectionMap = $$v;
                                    },
                                    expression: "currentSelectionMap"
                                  }
                                })
                          ]
                        }
                      },
                      {
                        key: "cell()",
                        fn: function(cell) {
                          return [
                            _c("div", { staticClass: "cell-content" }, [
                              _vm.densityClazz !== _vm.gridDensityEnum.LARGE
                                ? _c(
                                    "div",
                                    {
                                      directives: [
                                        {
                                          name: "me-tooltip",
                                          rawName: "v-me-tooltip.hover",
                                          value:
                                            cell.field.truncate &&
                                            _vm.hasEllipsis
                                              ? cell.value
                                              : "",
                                          expression:
                                            "cell.field.truncate && hasEllipsis ? cell.value : ''",
                                          modifiers: { hover: true }
                                        }
                                      ],
                                      class: {
                                        ellipsis: cell.field.truncate,
                                        "no-wrap": cell.field.noWrap
                                      },
                                      on: {
                                        mouseover: _vm.handleTooltipMouseOver
                                      }
                                    },
                                    [
                                      _c("CellContent", {
                                        attrs: { cell: cell }
                                      })
                                    ],
                                    1
                                  )
                                : _c(
                                    "div",
                                    {
                                      directives: [
                                        {
                                          name: "me-tooltip",
                                          rawName: "v-me-tooltip.hover",
                                          value:
                                            cell.field.truncate &&
                                            _vm.hasEllipsis
                                              ? cell.value
                                              : "",
                                          expression:
                                            "cell.field.truncate && hasEllipsis ? cell.value : ''",
                                          modifiers: { hover: true }
                                        }
                                      ],
                                      class: {
                                        "truncate-4": cell.field.truncate,
                                        "no-wrap": cell.field.noWrap
                                      },
                                      on: {
                                        mouseover: _vm.handleTooltipMouseOver
                                      }
                                    },
                                    [
                                      _c("CellContent", {
                                        attrs: { cell: cell }
                                      })
                                    ],
                                    1
                                  )
                            ])
                          ]
                        }
                      },
                      {
                        key: "cell(_card)",
                        fn: function(cell) {
                          return [
                            _vm._t(
                              "card",
                              [
                                _c(
                                  "ul",
                                  { staticClass: "me-grid-card" },
                                  _vm._l(_vm.activeFields, function(field) {
                                    return _c("li", { key: field.key }, [
                                      _c("label", [
                                        _vm._v(_vm._s(field.label) + ":")
                                      ]),
                                      _vm._v(
                                        " " +
                                          _vm._s(cell.item[field.key]) +
                                          "\n            "
                                      )
                                    ])
                                  }),
                                  0
                                )
                              ],
                              { item: cell.item, fields: _vm.activeFields }
                            )
                          ]
                        }
                      },
                      {
                        key: "cell(_actions)",
                        fn: function(row) {
                          return [
                            _vm._t(
                              "actions",
                              [
                                _c(
                                  "div",
                                  {
                                    staticClass: "action-icons",
                                    class: { "to-right": _vm.resizable }
                                  },
                                  [
                                    _vm._l(_vm.actionIcons, function(
                                      action,
                                      index
                                    ) {
                                      return _c(
                                        "a",
                                        {
                                          directives: [
                                            {
                                              name: "me-tooltip",
                                              rawName: "v-me-tooltip",
                                              value: action.tooltip,
                                              expression: "action.tooltip"
                                            }
                                          ],
                                          key: index,
                                          staticClass: "action-icons__link",
                                          class: {
                                            "action-icons__link--disabled":
                                              !action.href && !action.click
                                          },
                                          attrs: {
                                            href: action.click
                                              ? null
                                              : action.href
                                          }
                                        },
                                        [
                                          _c(
                                            "Icon",
                                            {
                                              attrs: { icon: action.icon },
                                              on: {
                                                click: function($event) {
                                                  return _vm.handleActionClick(
                                                    action,
                                                    row
                                                  )
                                                }
                                              }
                                            },
                                            [
                                              _vm._v(
                                                "\n                " +
                                                  _vm._s(action.label) +
                                                  "\n              "
                                              )
                                            ]
                                          )
                                        ],
                                        1
                                      )
                                    }),
                                    _vm._v(" "),
                                    !!_vm.actionItems.length
                                      ? _c(
                                          "BDropdown",
                                          {
                                            attrs: {
                                              variant: "light",
                                              size: "sm",
                                              "no-caret": "",
                                              right: "",
                                              "data-cy": "grid-dropdown"
                                            },
                                            on: {
                                              shown: function() {
                                                return (_vm.actionOpened =
                                                  row.item)
                                              },
                                              hide: function() {
                                                return (_vm.actionOpened = null)
                                              }
                                            }
                                          },
                                          [
                                            _c(
                                              "template",
                                              { slot: "button-content" },
                                              [
                                                _c("Icon", {
                                                  attrs: {
                                                    icon:
                                                      "me-icon icon-ellipsis-v"
                                                  }
                                                })
                                              ],
                                              1
                                            ),
                                            _vm._v(" "),
                                            _vm._l(_vm.actionItems, function(
                                              action,
                                              index
                                            ) {
                                              return _c(
                                                "BDropdownItem",
                                                {
                                                  key: index,
                                                  attrs: { href: action.href },
                                                  on: {
                                                    click: function($event) {
                                                      return _vm.handleActionClick(
                                                        action,
                                                        row
                                                      )
                                                    }
                                                  }
                                                },
                                                [
                                                  _vm._v(
                                                    "\n                " +
                                                      _vm._s(action.label) +
                                                      "\n              "
                                                  )
                                                ]
                                              )
                                            })
                                          ],
                                          2
                                        )
                                      : _vm._e()
                                  ],
                                  2
                                )
                              ],
                              { item: row.item, row: row }
                            )
                          ]
                        }
                      },
                      _vm._l(_vm.$scopedSlots, function(_, slot) {
                        return {
                          key: slot,
                          fn: function(scope) {
                            return [_vm._t(slot, null, null, scope)]
                          }
                        }
                      }),
                      {
                        key: "row-details",
                        fn: function(row) {
                          return [
                            _vm.selectable
                              ? _c(
                                  "div",
                                  { staticClass: "row-details-selectable" },
                                  [_vm._t("row-details", null, null, row)],
                                  2
                                )
                              : _vm._t("row-details", null, null, row)
                          ]
                        }
                      }
                    ],
                    null,
                    true
                  )
                },
                [
                  _vm._v(" "),
                  _vm._v(" "),
                  _vm._v(" "),
                  _vm._v(" "),
                  _vm._v(" "),
                  _vm._v(" "),
                  _c("template", { slot: "empty" }, [
                    _c(
                      "div",
                      { staticClass: "my-2 text-center" },
                      [
                        _c("Icon", {
                          attrs: { icon: "me-icon icon-info-circle" }
                        }),
                        _vm._v(
                          "\n          " +
                            _vm._s(_vm.$toolkit.t("grid.result.empty")) +
                            "\n        "
                        )
                      ],
                      1
                    )
                  ]),
                  _vm._v(" "),
                  _c("template", { slot: "emptyfiltered" }, [
                    _c(
                      "div",
                      { staticClass: "my-2 text-center" },
                      [
                        _c("Icon", { attrs: { icon: "me-icon icon-meh" } }),
                        _vm._v(
                          "\n          " +
                            _vm._s(
                              _vm.$toolkit.t("grid.result.emptySearchResult")
                            ) +
                            "\n        "
                        )
                      ],
                      1
                    )
                  ])
                ],
                2
              )
            ],
            1
          ),
          _vm._v(" "),
          !_vm.showGrid
            ? _c(
                "SkeletonGrid",
                {
                  attrs: {
                    card: _vm.showCard,
                    columns: _vm.columns.length,
                    rows: _vm.perPage
                  }
                },
                [_c("template", { slot: "card" }, [_vm._t("card-loading")], 2)],
                2
              )
            : _vm._e(),
          _vm._v(" "),
          _c(
            "Modal",
            {
              ref: "modal",
              attrs: {
                title: _vm.$toolkit.t("grid.columns.customize"),
                size: "lg",
                scrollable: ""
              },
              scopedSlots: _vm._u(
                [
                  {
                    key: "modal-footer",
                    fn: function(ref) {
                      var close = ref.close;
                      return [
                        _vm._t(
                          "grid-column-chooser-footer",
                          [
                            _c(
                              "Button",
                              {
                                attrs: { variant: "primary" },
                                on: {
                                  click: function($event) {
                                    return close()
                                  }
                                }
                              },
                              [
                                _vm._v(
                                  "\n          " +
                                    _vm._s(_vm.$toolkit.t("modal.close")) +
                                    "\n        "
                                )
                              ]
                            )
                          ],
                          { close: close }
                        )
                      ]
                    }
                  }
                ],
                null,
                true
              )
            },
            [
              _c("GridColumnChooser", {
                attrs: { fields: _vm.currentFields },
                on: { change: _vm.onColumnChange }
              })
            ],
            1
          )
        ],
        2
      )
    : _vm._e()
};
var __vue_staticRenderFns__$1z = [];
__vue_render__$1z._withStripped = true;

  /* style */
  const __vue_inject_styles__$1z = function (inject) {
    if (!inject) return
    inject("data-v-f51572bc_0", { source: ".button__disabled[data-v-f51572bc] {\n  background-color: #efeeef !important;\n  color: #c4c1c1 !important;\n  opacity: 0.65 !important;\n}\nsection.me-grid .sm[data-v-f51572bc]  tbody td {\n  height: 40px;\n  padding-top: 4px;\n  padding-bottom: 4px;\n  vertical-align: middle;\n}\nsection.me-grid .md[data-v-f51572bc]  tbody td {\n  height: 48px;\n  padding-top: 8px;\n  padding-bottom: 8px;\n  vertical-align: middle;\n}\nsection.me-grid .lg[data-v-f51572bc]  tbody td {\n  height: 64px;\n  padding-top: 16px;\n  padding-bottom: 16px;\n  vertical-align: middle;\n}\nsection.me-grid > header[data-v-f51572bc] {\n  background: var(--me-white);\n  border-bottom: 1px solid var(--me-gray-6);\n  display: flex;\n  min-height: 40px;\n  padding: 8px 10px;\n  user-select: none;\n}\nsection.me-grid > header > div.header[data-v-f51572bc] {\n  flex-grow: 1;\n}\nsection.me-grid[data-v-f51572bc]  table .row-highlight {\n  font-weight: 700;\n  position: relative;\n}\nsection.me-grid[data-v-f51572bc]  table .row-highlight:after {\n  content: \"\";\n  position: absolute;\n  width: 4px;\n  top: 0;\n  left: 0;\n  height: 100%;\n  background: var(--me-primary-2);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc], section.me-grid[data-v-f51572bc]  table {\n  background-color: var(--me-white);\n}\nsection.me-grid div.b-table-sticky-header[data-v-f51572bc] {\n  overflow-y: auto;\n}\nsection.me-grid div.b-table-sticky-header[data-v-f51572bc]  table thead {\n  position: sticky;\n  top: 0px;\n  background-color: var(--me-white);\n  z-index: 4;\n  box-shadow: inset 0 -0.5px 0 var(--me-gray-6);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table,\nsection.me-grid[data-v-f51572bc]  table {\n  margin: 0;\n  font-size: 14px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table .tr-grabbing *,\nsection.me-grid[data-v-f51572bc]  table .tr-grabbing * {\n  cursor: grabbing !important;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th {\n  position: relative;\n  border-bottom: 1px solid #ebeef5;\n  border-top: none;\n  color: #474747;\n  font-weight: 400;\n  font-size: 12px;\n  white-space: nowrap;\n  outline: none;\n  vertical-align: middle;\n  padding: 0;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.sortable-ghost, section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.sortable-chosen,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.sortable-ghost,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.sortable-chosen,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.sortable-ghost,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.sortable-chosen,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.sortable-ghost,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.sortable-chosen {\n  background-color: var(--me-primary-7);\n  transition: background-color 0.3s;\n  box-shadow: var(--me-shadow-level-2);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.sortable-ghost.dragging-item, section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.sortable-chosen.dragging-item,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.sortable-ghost.dragging-item,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.sortable-chosen.dragging-item,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.sortable-ghost.dragging-item,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.sortable-chosen.dragging-item,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.sortable-ghost.dragging-item,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.sortable-chosen.dragging-item {\n  opacity: 0.8 !important;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-left, section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-right,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-left,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-right {\n  background-color: var(--me-white);\n  position: sticky;\n  z-index: 2;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-left,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-left {\n  left: 0;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-right,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-right {\n  right: 0;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-right.me-action-opened,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-right.me-action-opened,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th.me-sticky-column-right.me-action-opened,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th.me-sticky-column-right.me-action-opened {\n  z-index: 3;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header {\n  display: flex;\n  align-items: center;\n  padding: 12px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header__label,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header__label,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header__label,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header__label {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  line-height: 16px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header__label__tooltip,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header__label__tooltip,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header__label__tooltip,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header__label__tooltip {\n  width: 1px;\n  margin-right: -4px;\n  height: 12px;\n  display: inline-block;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header.handle-drag-area,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header.handle-drag-area,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header.handle-drag-area,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header.handle-drag-area {\n  cursor: grab;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions {\n  display: flex;\n  flex-grow: 1;\n  justify-content: flex-end;\n  align-items: center;\n  height: 16px;\n  gap: 8px;\n  padding-left: 8px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:not(.opened),\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:not(.opened),\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:not(.opened),\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:not(.opened) {\n  visibility: hidden;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations button,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations button,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations button,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations button {\n  text-decoration: none;\n  padding: 0;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc, section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc {\n  color: var(--me-gray-5);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th:hover > div.cell-header .header-actions > div.aggregations,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th:hover > div.cell-header .header-actions > div.aggregations,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th:hover > div.cell-header .header-actions > div.aggregations,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th:hover > div.cell-header .header-actions > div.aggregations {\n  visibility: visible;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort],\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort],\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort],\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] {\n  cursor: pointer;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort] .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort] .sort-asc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] .sort-asc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort] .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort] .sort-asc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] .sort-asc {\n  display: none;\n  color: var(--me-primary-2);\n  cursor: pointer !important;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort] .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] .sort-none,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort] .sort-none,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort] .sort-none {\n  cursor: pointer !important;\n  display: inline-block;\n  color: var(--me-gray-5);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-asc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-asc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-asc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-asc {\n  display: inline-block;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending] .sort-desc,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending] .sort-desc,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-desc {\n  display: inline-block;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending], section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending],\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending],\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending],\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending],\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending],\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending],\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending] {\n  background: var(--me-primary-7);\n  border-bottom: 1px solid var(--me-primary-3);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending]:hover, section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending]:hover,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending]:hover,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending]:hover,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending]:hover,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending]:hover,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending]:hover,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending]:hover {\n  background: var(--me-primary-7);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-none, section.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending] .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-none,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-none,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort][aria-sort=descending] .sort-none,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-none,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-none {\n  display: none;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen),\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen),\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen),\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen) {\n  background: var(--me-bg-gray);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none,\nsection.me-grid[data-v-f51572bc]  table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none,\nsection.me-grid[data-v-f51572bc]  table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none {\n  color: var(--me-primary-2);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr {\n  transition: 0.2s all ease-in-out;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr .me-sticky-column-left,\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr .me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr .me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr .me-sticky-column-right {\n  transition: 0.2s all ease-in-out;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr:hover:not(.b-table-details), section.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr:hover + .b-table-details, section.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr:hover .me-sticky-column-left, section.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr:hover .me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr:hover:not(.b-table-details),\nsection.me-grid[data-v-f51572bc]  table > tbody > tr:hover + .b-table-details,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr:hover .me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr:hover .me-sticky-column-right {\n  background-color: var(--me-primary-7);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr.b-table-has-details > td,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr.b-table-has-details > td {\n  border-bottom: none;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr.b-table-details,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr.b-table-details {\n  background-color: var(--me-bg-gray);\n  position: relative;\n  z-index: 1;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr.b-table-details table,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr.b-table-details table {\n  background-color: var(--me-bg-gray);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr.b-table-details .row-details-selectable,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr.b-table-details .row-details-selectable {\n  padding-left: 48px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td {\n  border-top: none;\n  border-bottom: 1px solid var(--me-gray-6);\n  position: relative;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td.check-box-column,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td.check-box-column {\n  width: 40px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-left, section.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-right {\n  background-color: var(--me-white);\n  position: sticky;\n  z-index: 2;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-left,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-left {\n  left: 0;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-right,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-right {\n  right: 0;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-right.me-action-opened,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td.me-sticky-column-right.me-action-opened {\n  z-index: 3;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.cell-content,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.cell-content {\n  display: grid;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.cell-content .ellipsis,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.cell-content .ellipsis {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.cell-content .truncate-4,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.cell-content .truncate-4 {\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 4;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.cell-content .no-wrap,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.cell-content .no-wrap {\n  white-space: nowrap;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons {\n  display: inline-flex;\n  align-items: center;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons.to-right,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons.to-right {\n  display: flex;\n  justify-content: flex-end;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link {\n  margin: 0 8px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link > i,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link > i {\n  display: flex;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link > i::before,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link > i::before {\n  padding-right: 4px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link--disabled,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > a.action-icons__link--disabled {\n  color: rgba(0, 0, 0, 0.36);\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > div.dropdown > button,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > div.dropdown > button {\n  padding: 0 8px;\n}\nsection.me-grid > div.table-responsive[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > div.dropdown > button:after,\nsection.me-grid[data-v-f51572bc]  table > tbody > tr > td > div.action-icons > div.dropdown > button:after {\n  content: none;\n}\nsection.me-grid.responsive[data-v-f51572bc]  table.b-table > thead {\n  display: none;\n}\nsection.me-grid.responsive[data-v-f51572bc]  table.b-table > tbody ul.me-grid-card {\n  list-style: none;\n  padding: 0;\n  margin: 0;\n}\nsection.me-grid.responsive[data-v-f51572bc]  table.b-table > tbody ul.me-grid-card > li > label {\n  font-weight: 600;\n}\n@media screen and (max-width: 768px) {\nsection.me-grid.responsive[data-v-f51572bc]  table.b-table td {\n    padding: 0.5rem;\n}\nsection.me-grid.responsive[data-v-f51572bc]  table.b-table td:first-child {\n    width: 14px;\n}\n}\n.me-grid-header-tooltip[data-v-f51572bc] {\n  pointer-events: none;\n}\n\n/*# sourceMappingURL=Grid.vue.map */", map: {"version":3,"sources":["/app/build/src/components/grid/Grid.vue","Grid.vue"],"names":[],"mappings":"AA09BA;EACA,oCAAA;EACA,yBAAA;EACA,wBAAA;ACz9BA;AD69BA;EACA,YAAA;EACA,gBAAA;EACA,mBAAA;EACA,sBAAA;AC19BA;AD+9BA;EACA,YAAA;EACA,gBAAA;EACA,mBAAA;EACA,sBAAA;AC79BA;ADk+BA;EACA,YAAA;EACA,iBAAA;EACA,oBAAA;EACA,sBAAA;ACh+BA;ADo+BA;EACA,2BAAA;EACA,yCAAA;EACA,aAAA;EACA,gBAAA;EACA,iBAAA;EACA,iBAAA;ACl+BA;ADo+BA;EACA,YAAA;ACl+BA;ADs+BA;EACA,gBAAA;EACA,kBAAA;ACp+BA;ADq+BA;EACA,WAAA;EACA,kBAAA;EACA,UAAA;EACA,MAAA;EACA,OAAA;EACA,YAAA;EACA,+BAAA;ACn+BA;ADu+BA;EACA,iCAAA;ACr+BA;ADw+BA;EACA,gBAAA;ACt+BA;AD0+BA;EACA,gBAAA;EACA,QAAA;EACA,iCAAA;EACA,UAAA;EACA,6CAAA;ACx+BA;AD8+BA;;EAEA,SAAA;EACA,eAAA;AC5+BA;AD8+BA;;EACA,2BAAA;AC3+BA;ADi/BA;;;;EACA,kBAAA;EACA,gCAAA;EACA,gBAAA;EACA,cAAA;EACA,gBAAA;EACA,eAAA;EACA,mBAAA;EACA,aAAA;EACA,sBAAA;EACA,UAAA;AC5+BA;AD8+BA;;;;;;;EAEA,qCAAA;EACA,iCAAA;EACA,oCAAA;ACv+BA;ADy+BA;;;;;;;EACA,uBAAA;ACj+BA;AD81BA;;;;;;;EAEA,iCAAA;EACA,gBAAA;EACA,UAAA;ACv1BA;AD01BA;;;;EACA,OAAA;ACr1BA;ADw1BA;;;;EACA,QAAA;ACn1BA;ADq1BA;;;;EACA,UAAA;ACh1BA;AD28BA;;;;EACA,aAAA;EACA,mBAAA;EACA,aAAA;ACt8BA;ADw8BA;;;;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,iBAAA;ACn8BA;ADq8BA;;;;EACA,UAAA;EACA,kBAAA;EACA,YAAA;EACA,qBAAA;ACh8BA;ADo8BA;;;;EACA,YAAA;AC/7BA;ADk8BA;;;;EACA,aAAA;EACA,YAAA;EACA,yBAAA;EACA,mBAAA;EACA,YAAA;EACA,QAAA;EACA,iBAAA;AC77BA;AD+7BA;;;;EACA,kBAAA;AC17BA;AD+7BA;;;;EACA,qBAAA;EACA,UAAA;AC17BA;ADg8BA;;;;;;;;;;;;;;;;;;;;;;;EAGA,uBAAA;AC16BA;ADq7BA;;;;EACA,mBAAA;ACh7BA;ADu7BA;;;;EACA,eAAA;ACl7BA;ADo7BA;;;;;;;;EAEA,aAAA;EACA,0BAAA;EACA,0BAAA;AC56BA;AD+6BA;;;;EACA,0BAAA;EACA,qBAAA;EACA,uBAAA;AC16BA;AD86BA;;;;EACA,qBAAA;ACz6BA;AD86BA;;;;EACA,qBAAA;ACz6BA;AD66BA;;;;;;;EACA,+BAAA;EACA,4CAAA;ACr6BA;ADu6BA;;;;;;;EACA,+BAAA;AC/5BA;ADk6BA;;;;;;;EACA,aAAA;AC15BA;AD85BA;;;;EACA,6BAAA;ACz5BA;AD25BA;;;;EACA,0BAAA;ACt5BA;AD+5BA;;EACA,gCAAA;AC55BA;AD85BA;;;;EAEA,gCAAA;AC15BA;AD65BA;;;;;EAIA,qCAAA;AC15BA;AD85BA;;EACA,mBAAA;AC35BA;AD+5BA;;EACA,mCAAA;EACA,kBAAA;EACA,UAAA;AC55BA;AD85BA;;EACA,mCAAA;AC35BA;AD85BA;;EACA,kBAAA;AC35BA;AD+5BA;;EACA,gBAAA;EACA,yCAAA;EACA,kBAAA;AC55BA;AD85BA;;EACA,WAAA;AC35BA;AD8mBA;;;EAEA,iCAAA;EACA,gBAAA;EACA,UAAA;AC3mBA;AD8mBA;;EACA,OAAA;AC3mBA;AD8mBA;;EACA,QAAA;AC3mBA;AD6mBA;;EACA,UAAA;AC1mBA;AD64BA;;EACA,aAAA;AC14BA;AD44BA;;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;ACz4BA;AD44BA;;EAnUA,gBAAA;EACA,oBAAA;EACA,qBAkUA;EAjUA,4BAAA;EACA,uBAAA;ACrkBA;ADw4BA;;EACA,mBAAA;ACr4BA;ADy4BA;;EACA,oBAAA;EACA,mBAAA;ACt4BA;ADw4BA;;EACA,aAAA;EACA,yBAAA;ACr4BA;ADw4BA;;EACA,aAAA;ACr4BA;ADu4BA;;EACA,aAAA;ACp4BA;ADq4BA;;EACA,kBAAA;ACl4BA;ADs4BA;;EACA,0BAAA;ACn4BA;ADw4BA;;EACA,cAAA;ACr4BA;ADs4BA;;EACA,aAAA;ACn4BA;AD+4BA;EACA,aAAA;AC74BA;ADi5BA;EACA,gBAAA;EACA,UAAA;EACA,SAAA;AC/4BA;ADk5BA;EACA,gBAAA;ACh5BA;ADs5BA;AACA;IACA,eAAA;ACp5BE;ADq5BF;IACA,WAAA;ACn5BE;AACF;AD05BA;EACA,oBAAA;ACv5BA;;AAEA,mCAAmC","file":"Grid.vue","sourcesContent":["<template>\n  <section\n    v-if=\"ready\"\n    :class=\"{ responsive: showCard }\"\n    class=\"me-grid\"\n  >\n    <header v-if=\"showHeader\">\n      <div class=\"header\">\n        <slot name=\"header\" />\n      </div>\n\n      <me-pagination\n        :page-size=\"pageSize\"\n        :searchable=\"searchable\"\n        :hits=\"hits\"\n        :max-hits=\"maxTotalHits\"\n        :lazy=\"lazy\"\n        :refresh=\"refresh\"\n        :current-page.sync=\"currentPage\"\n        :filter.sync=\"filter\"\n        :state-key=\"stateKey\"\n        :show-coluns-customization=\"showColumnsCustomization\"\n        :hide-density-options=\"hideDensityOptions\"\n        :custom-menu=\"customMenu\"\n        :elements-per-page=\"elementsPerPage\"\n        :density=\"densityClazz\"\n        @openColumnDialog=\"openColumnsDialog\"\n        @resetState=\"resetState\"\n        @setSize=\"setPageSize\"\n        @density=\"setDensity\"\n      />\n    </header>\n    <slot name=\"content\" />\n    <Overlay\n      v-show=\"showGrid\"\n      :show=\"isBusy\"\n      opacity=\"0.6\"\n    >\n      <div slot=\"overlay\" class=\"text-center text-primary rounded-pill p-3 bg-white\">\n        <Spinner class=\"align-middle\" />\n        {{ $toolkit.t('spinner.loading') }}\n      </div>\n      <BTable\n        ref=\"table\"\n        v-me-resize=\"resizeValue\"\n        v-me-drag-drop=\"colsDraggable && dragOptions\"\n        :responsive=\"responsive === null\"\n        :fields=\"columns\"\n        :items=\"data\"\n        :per-page=\"perPage\"\n        :filter=\"filter\"\n        :filter-included-fields=\"filterIncludedFields\"\n        :current-page=\"currentPage\"\n        :api-url=\"apiUrl\"\n        :primary-key=\"uniqueKey\"\n        :busy.sync=\"isBusy\"\n        :tbody-tr-class=\"rowClass\"\n        :sticky-header=\"stickyHeader\"\n        :class=\"densityClazz\"\n        @colresize=\"saveColWidth\"\n        show-empty\n        data-cy=\"grid-table\"\n      >\n        <template v-slot:head()=\"head\">\n          <div\n            @mouseenter=\"() => handeTooltip(head.column)\"\n            @mouseleave=\"() => handeTooltip(head.column, false)\"\n            :class=\"{'handle-drag-area': colsDraggable && head.column !== actionColumn.key}\"\n            class=\"cell-header\"\n          >\n            <span\n              data-cy=\"gridDragArea\"\n              class=\"cell-header__label\"\n            >\n              <span\n                v-me-tooltip.topright.manual.viewport=\"{\n                  title: head.field.tooltipMsg,\n                  id: head.column,\n                  customClass: 'me-grid-header-tooltip'\n                }\"\n                class=\"cell-header__label__tooltip\"\n              />\n              {{ head.label }}\n            </span>\n\n            <div class=\"header-actions\">\n              <DropDown\n                v-if=\"head.field.groupable\"\n                @mouseenter.native=\"() => handeTooltip(head.column, false)\"\n                @mouseleave.native=\"() => handeTooltip(head.column)\"\n                class=\"aggregations\"\n                boundary=\"viewport\"\n                variant=\"link\"\n                size=\"sm\"\n                no-caret\n              >\n                <template slot=\"button-content\">\n                  <Icon\n                    :size=\"12\"\n                    icon=\"me-icon-s icon-chart-pie\"\n                    color=\"var(--me-gray-5)\"\n                    hover-color=\"var(--me-primary-2)\"\n                  />\n                </template>\n\n                <DropdownItem\n                  v-for=\"item in grouping\"\n                  :key=\"item.type\"\n                  :label=\"item.label\"\n                  :icon=\"item.icon\"\n                  @click=\"dispatchGrouping(item.type, head.field)\"\n                />\n              </DropDown>\n\n              <div\n                v-if=\"head.field.sortable\"\n                @mouseenter=\"() => handeTooltip(head.column, false)\"\n                @mouseleave=\"() => handeTooltip(head.column)\"\n                class=\"sort-actions\"\n              >\n                <Icon :size=\"12\" icon=\"me-icon-s icon-sort\" class=\"sort-none\" />\n                <Icon :size=\"12\" icon=\"me-icon-s icon-sort-down\" class=\"sort-desc\" />\n                <Icon :size=\"12\" icon=\"me-icon-s icon-sort-up\" class=\"sort-asc\" />\n              </div>\n            </div>\n          </div>\n        </template>\n\n        <template v-slot:head(_checkbox)=\"\">\n          <div class=\"cell-header\">\n            <div v-if=\"selectRadio\" />\n\n            <Checkbox\n              v-else\n              :checked=\"pageChecked\"\n              :indeterminate=\"pageIndeterminate\"\n              :toggle=\"toggle\"\n              @change=\"checkPage\"\n            />\n          </div>\n        </template>\n\n        <template v-slot:cell(_checkbox)=\"row\">\n          <BFormRadio\n            v-if=\"selectRadio\"\n            :checked=\"currentSelectionMap[0]\"\n            :value=\"row.item[uniqueKey]\"\n            :disabled=\"row.item.selectDisabled\"\n            :name=\"selectRadioName\"\n            @change=\"toggleRadioItem(row.item)\"\n            v-me-tooltip=\"row.item.selectTooltip\"\n            data-cy=\"selectRadio\"\n          />\n\n          <Checkbox\n            v-else\n            v-model=\"currentSelectionMap\"\n            :disabled=\"row.item.selectDisabled\"\n            v-me-tooltip=\"row.item.selectTooltip\"\n            :value=\"row.item[uniqueKey]\"\n            @change=\"toggleItem(row.item)\"\n          />\n        </template>\n\n        <template v-slot:cell()=\"cell\">\n          <div class=\"cell-content\">\n            <div\n              v-if=\"densityClazz !== gridDensityEnum.LARGE\"\n              v-me-tooltip.hover=\"cell.field.truncate && hasEllipsis ? cell.value : ''\"\n              :class=\"{'ellipsis': cell.field.truncate, 'no-wrap': cell.field.noWrap}\"\n              @mouseover=\"handleTooltipMouseOver\"\n            >\n              <CellContent :cell=\"cell\" />\n            </div>\n\n            <div\n              v-else\n              v-me-tooltip.hover=\"cell.field.truncate && hasEllipsis ? cell.value : ''\"\n              :class=\"{'truncate-4': cell.field.truncate, 'no-wrap': cell.field.noWrap}\"\n              @mouseover=\"handleTooltipMouseOver\"\n            >\n              <CellContent :cell=\"cell\" />\n            </div>\n          </div>\n        </template>\n\n        <template v-slot:cell(_card)=\"cell\">\n          <slot :item=\"cell.item\" :fields=\"activeFields\" name=\"card\">\n            <ul class=\"me-grid-card\">\n              <li v-for=\"field in activeFields\" :key=\"field.key\">\n                <label>{{ field.label }}:</label> {{ cell.item[field.key] }}\n              </li>\n            </ul>\n          </slot>\n        </template>\n\n        <template v-slot:cell(_actions)=\"row\">\n          <slot\n            :item=\"row.item\"\n            :row=\"row\"\n            name=\"actions\"\n          >\n            <div\n              :class=\"{'to-right': resizable}\"\n              class=\"action-icons\"\n            >\n              <a\n                v-for=\"(action, index) in actionIcons\"\n                :key=\"index\"\n                v-me-tooltip=\"action.tooltip\"\n                :href=\"action.click ? null : action.href\"\n                :class=\"{'action-icons__link--disabled': !action.href && !action.click}\"\n                class=\"action-icons__link\"\n              >\n                <Icon\n                  :icon=\"action.icon\"\n                  @click=\"handleActionClick(action, row)\"\n                >\n                  {{ action.label }}\n                </Icon>\n              </a>\n\n              <BDropdown\n                v-if=\"!!actionItems.length\"\n                @shown=\"() => actionOpened = row.item\"\n                @hide=\"() => actionOpened = null\"\n                variant=\"light\"\n                size=\"sm\"\n                no-caret\n                right\n                data-cy=\"grid-dropdown\"\n              >\n                <template slot=\"button-content\">\n                  <Icon icon=\"me-icon icon-ellipsis-v\" />\n                </template>\n\n                <BDropdownItem\n                  v-for=\"(action, index) in actionItems\"\n                  :key=\"index\"\n                  :href=\"action.href\"\n                  @click=\"handleActionClick(action, row)\"\n                >\n                  {{ action.label }}\n                </BDropdownItem>\n              </BDropdown>\n            </div>\n          </slot>\n        </template>\n\n        <template slot=\"empty\">\n          <div class=\"my-2 text-center\">\n            <Icon icon=\"me-icon icon-info-circle\" />\n            {{ $toolkit.t('grid.result.empty') }}\n          </div>\n        </template>\n\n        <template slot=\"emptyfiltered\">\n          <div class=\"my-2 text-center\">\n            <Icon icon=\"me-icon icon-meh\" />\n            {{ $toolkit.t('grid.result.emptySearchResult') }}\n          </div>\n        </template>\n\n        <template v-for=\"(_, slot) of $scopedSlots\" v-slot:[slot]=\"scope\">\n          <slot :name=\"slot\" v-bind=\"scope\" />\n        </template>\n\n        <template v-slot:row-details=\"row\">\n          <div v-if=\"selectable\" class=\"row-details-selectable\">\n            <slot v-bind=\"row\" name=\"row-details\" />\n          </div>\n\n          <slot v-else v-bind=\"row\" name=\"row-details\" />\n        </template>\n      </BTable>\n    </Overlay>\n    <SkeletonGrid\n      v-if=\"!showGrid\"\n      :card=\"showCard\"\n      :columns=\"columns.length\"\n      :rows=\"perPage\"\n    >\n      <template slot=\"card\">\n        <slot name=\"card-loading\" />\n      </template>\n    </SkeletonGrid>\n    <Modal\n      ref=\"modal\"\n      :title=\"$toolkit.t('grid.columns.customize')\"\n      size=\"lg\"\n      scrollable\n    >\n      <GridColumnChooser :fields=\"currentFields\" @change=\"onColumnChange\" />\n\n      <template slot=\"modal-footer\" slot-scope=\"{ close }\">\n        <slot :close=\"close\" name=\"grid-column-chooser-footer\">\n          <Button @click=\"close()\" variant=\"primary\">\n            {{ $toolkit.t('modal.close') }}\n          </Button>\n        </slot>\n      </template>\n    </Modal>\n  </section>\n</template>\n\n<script>\nimport isEqual from 'lodash/isEqual'\nimport cloneDeep from 'lodash/cloneDeep'\nimport sortBy from 'lodash/sortBy'\nimport concat from 'lodash/concat'\nimport filter from 'lodash/filter'\nimport some from 'lodash/some'\nimport every from 'lodash/every'\nimport isNil from 'lodash/isNil'\nimport find from 'lodash/find'\nimport debounce from 'lodash/debounce'\nimport { v4 as uuidv4 } from 'uuid'\nimport { BTable, BDropdown, BDropdownItem, BFormRadio } from 'bootstrap-vue'\nimport localStorage from '../../state/localStorage'\nimport Button from '../Button.vue'\nimport Spinner from '../Spinner.vue'\nimport Overlay from '../Overlay.vue'\nimport Modal from '../modal/Modal.vue'\nimport GridColumnChooser from '../GridColumnChooser.vue'\nimport Icon from '../Icon.vue'\nimport Checkbox from '../Checkbox.vue'\nimport SkeletonGrid from '../SkeletonGrid.vue'\nimport DropDown from '../dropdown/DropDown.vue'\nimport DropdownItem from '../dropdown/DropdownItem.vue'\nimport CellContent from './cell/CellContent.vue'\n\nconst DRAG_NOT_ALLOWED_CLASS = 'drag-not-allowed'\nconst DRAGGING_CLASS = 'tr-grabbing'\nconst STICKY_COLUMN_LEFT_CLASS = 'me-sticky-column-left'\nconst STICKY_COLUMN_RIGHT_CLASS = 'me-sticky-column-right'\n\nconst gridDensityEnum = {\n  LARGE: 'lg',\n  MEDIUM: 'md',\n  SMALL: 'sm'\n}\n\nexport default {\n  name: 'MeGrid',\n  components: {\n    BTable,\n    BDropdown,\n    BDropdownItem,\n    BFormRadio,\n    Button,\n    DropDown,\n    DropdownItem,\n    Spinner,\n    Overlay,\n    Modal,\n    GridColumnChooser,\n    Icon,\n    Checkbox,\n    SkeletonGrid,\n    CellContent\n  },\n  props: {\n    customMenu: {\n      type: Array,\n      default: () => []\n    },\n    showColumnsCustomization: {\n      type: Boolean,\n      default: false\n    },\n    hideDensityOptions: {\n      type: Boolean,\n      default: false\n    },\n    onlyFilterByFields: {\n      type: Boolean,\n      default: false\n    },\n    fields: {\n      type: Array,\n      required: true\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    actions: {\n      type: Array,\n      default: () => []\n    },\n    actionsFixed: {\n      type: Boolean,\n      default: false\n    },\n    apiUrl: {\n      type: String,\n      default: ''\n    },\n    elementsPerPage: {\n      type: Array,\n      default: () => [10, 20, 50, 100]\n    },\n    provider: {\n      type: Function,\n      default: null\n    },\n    selectable: {\n      type: Boolean,\n      default: false\n    },\n    selectablePosition: {\n      type: Number,\n      default: 0\n    },\n    uniqueKey: {\n      type: String,\n      default: 'id'\n    },\n    selection: {\n      type: Array,\n      default: () => []\n    },\n    stateKey: {\n      type: String,\n      default: ''\n    },\n    responsive: {\n      type: [Boolean, String],\n      default: null\n    },\n    stateProvider: {\n      type: Object,\n      default() {\n        return localStorage\n      }\n    },\n    layout: {\n      type: String,\n      default: 'grid'\n    },\n    highlightRow: {\n      type: Function,\n      default: null\n    },\n    tbodyRowClass: {\n      type: Function,\n      default: null\n    },\n    maxTotalHits: {\n      type: Number,\n      default: 1000000\n    },\n    toggle: {\n      type: Boolean,\n      default: false\n    },\n    selectRadio: {\n      type: Boolean,\n      default: false\n    },\n    stickyHeader: {\n      type: [Boolean, String],\n      default: false\n    },\n    resizable: {\n      type: Boolean,\n      default: false\n    },\n    colsDraggable: {\n      type: [Boolean, Object],\n      default: false\n    },\n    initialPageSize: {\n      type: Number,\n      default: null\n    }\n  },\n  data() {\n    return {\n      gridDensityEnum,\n      headerTooltipTimeout: null,\n      selectRadioName: `${uuidv4()}_select-radio`,\n      actionOpened: null,\n      tableWidthsResize: [],\n      isBusy: false,\n      showCard: false,\n      currentPage: 1,\n      currentFields: cloneDeep(this.fields),\n      pageSize: this.elementsPerPage.find(size => size === this.initialPageSize) || this.elementsPerPage[0],\n      hits: this.items.length,\n      result: this.items,\n      isInitialState: true,\n      cached: false,\n      filter: null,\n      state: null,\n      ready: false,\n      currentLayout: this.layout,\n      currentSelection: cloneDeep(this.selection),\n      currentSelectionMap: this.selection.map(d => d[this.uniqueKey]),\n      breakpoint: {\n        sm: 576,\n        md: 768,\n        lg: 992,\n        xl: 1200\n      },\n      grouping: [\n        { type: 'area', label: this.$toolkit.t('analytics.types.area'), icon: 'me-icon icon-chart-area' },\n        { type: 'bar', label: this.$toolkit.t('analytics.types.bar'), icon: 'me-icon icon-bars' },\n        { type: 'column', label: this.$toolkit.t('analytics.types.column'), icon: 'me-icon icon-chart-bar' },\n        { type: 'donut', label: this.$toolkit.t('analytics.types.donut'), icon: 'me-icon icon-dot-circle' },\n        { type: 'line', label: this.$toolkit.t('analytics.types.line'), icon: 'me-icon icon-chart-line' },\n        { type: 'pie', label: this.$toolkit.t('analytics.types.pie'), icon: 'me-icon icon-chart-pie-alt' }\n      ],\n      maxHitsLimit: 10000,\n      hasEllipsis: false,\n      densityClazz: gridDensityEnum.MEDIUM\n    }\n  },\n\n  computed: {\n    actionColumn() {\n      return {\n        key: '_actions',\n        label: '',\n        thStyle: {\n          width: '40px'\n        },\n        thClass: this.actionsFixed ? [DRAG_NOT_ALLOWED_CLASS, STICKY_COLUMN_RIGHT_CLASS] : [DRAG_NOT_ALLOWED_CLASS],\n        tdClass: (_value, _key, item) => {\n          if (!this.actionsFixed) return []\n\n          if (isEqual(item, this.actionOpened)) return [STICKY_COLUMN_RIGHT_CLASS, 'me-action-opened']\n\n          return STICKY_COLUMN_RIGHT_CLASS\n        }\n      }\n    },\n    filterIncludedFields() {\n      return this.onlyFilterByFields ? this.columns.map(({ key }) => key) : []\n    },\n    showGrid() {\n      return !this.isInitialState || !this.lazy\n    },\n    perPage() {\n      return this.currentLayout !== 'table' ? this.pageSize : null\n    },\n    showHeader() {\n      return this.currentLayout !== 'table' && this.showGrid\n    },\n    searchable() {\n      return this.items.length > 0 || this.cached\n    },\n    columns() {\n      let fields = []\n\n      if (this.showCard) {\n        fields.push({\n          key: '_card',\n          label: ''\n        })\n      } else {\n        fields = concat(fields, this.activeFields)\n      }\n\n      if (this.selectable) {\n        const arrayLeftSide = fields.slice(0, this.selectablePosition)\n        const selectableField = {\n          key: '_checkbox',\n          tdClass: ['check-box-column', STICKY_COLUMN_LEFT_CLASS],\n          thClass: [DRAG_NOT_ALLOWED_CLASS, STICKY_COLUMN_LEFT_CLASS]\n        }\n        const arrayRightSide = fields.slice(this.selectablePosition, fields.length)\n        fields = [...arrayLeftSide, selectableField, ...arrayRightSide]\n      }\n\n      if (this.actions.length > 0) {\n        fields.push(this.actionColumn)\n      }\n      return fields\n    },\n    actionIcons() {\n      return this.actions.filter((action) => { return action.icon })\n    },\n    actionItems() {\n      return this.actions.filter((action) => { return !action.icon })\n    },\n    activeFields() {\n      return filter(this.currentFields, ({ active }) => isNil(active) || active)\n    },\n    lazy() {\n      return this.apiUrl !== '' || this.provider !== null\n    },\n    data() {\n      if (this.cached) {\n        return this.result\n      } else if (this.apiUrl !== '') {\n        return this.dataLoader\n      } else if (this.provider !== null) {\n        return this.dataProvider\n      } else {\n        return this.items\n      }\n    },\n    hasPreviousPage() {\n      return this.currentPage > 1\n    },\n    selectableItems() {\n      return this.result.filter(item => !item.selectDisabled)\n    },\n    pageChecked() {\n      return this.selectableItems.length > 0 && every(this.selectableItems, item => this.currentSelectionMap.includes(item[this.uniqueKey]))\n    },\n    pageIndeterminate() {\n      return some(this.selectableItems, item => this.currentSelectionMap.includes(item[this.uniqueKey])) &&\n      some(this.selectableItems, item => !this.currentSelectionMap.includes(item[this.uniqueKey]))\n    },\n    resizeValue() {\n      if (this.resizable && this.responsive === null) {\n        return { sizes: this.tableWidthsResize }\n      }\n\n      return false\n    },\n    dragOptions() {\n      return {\n        queryEl: 'thead tr',\n        options: {\n          handle: '.handle-drag-area',\n          group: this.stateKey ? `cols_drag_${this.stateKey}` : '',\n          swapThreshold: 0.7,\n          filter: '.drag-not-allowed',\n          dragClass: 'dragging-item',\n          fallbackClass: 'dragging-item',\n          forceFallback: true,\n          onEnd: (e) => {\n            e.target.classList.remove(DRAGGING_CLASS)\n            this.moveColumnsOnDragEnd(e)\n          },\n          onMove: (e) => {\n            e.target.classList.add(DRAGGING_CLASS)\n            this.isEnableColumnMove(e)\n          },\n          onChoose: (e) => {\n            e.target.classList.add(DRAGGING_CLASS)\n          },\n          onUnchoose: (e) => {\n            e.target.classList.remove(DRAGGING_CLASS)\n          },\n          onStart: (e) => {\n            e.target.classList.add(DRAGGING_CLASS)\n          },\n          ...{ ...(this.colsDraggable?.options || {}) }\n        }\n      }\n    }\n  },\n  watch: {\n    items(val) {\n      const ids = this.items.map(({ id }) => id)\n      this.currentSelection = this.currentSelection.filter(({ id }) => ids.includes(id))\n      this.hits = val ? val.length : 0\n      this.result = val\n    },\n    stateKey(val) {\n      this.stateKey = val\n      this.loadState()\n    },\n    fields(val) {\n      const fields = cloneDeep(val || [])\n      this.mergeState(this.state, fields)\n      this.currentFields = sortBy(fields, 'order')\n    },\n    layout(val) {\n      this.currentLayout = val\n    },\n    selection(val) {\n      this.currentSelection = cloneDeep(val || [])\n    },\n    currentSelection(_val) {\n      this.currentSelectionMap = this.currentSelection.map(d => d[this.uniqueKey])\n    },\n    responsive(_val) {\n      this.calculateViewport()\n    },\n    pageChecked() {\n      this.$emit('pageChecked', this.pageChecked)\n    },\n    pageIndeterminate() {\n      this.$emit('pageIndeterminate', this.pageIndeterminate)\n    }\n  },\n  created() {\n    this.loadState()\n  },\n  mounted() {\n    this.calculateViewport()\n    window.addEventListener('resize', debounce(this.calculateViewport, 500))\n  },\n  methods: {\n    handeTooltip(columnName, show = true) {\n      this.$root.$emit('bv::hide::tooltip')\n      if (!columnName) return\n\n      this.clearHeaderTooltipTimeout()\n\n      this.headerTooltipTimeout = setTimeout(() => {\n        this.$root.$emit('bv::hide::tooltip')\n        if (show) {\n          this.$root.$emit('bv::show::tooltip', columnName)\n        }\n      }, 500)\n    },\n    clearHeaderTooltipTimeout() {\n      if (this.headerTooltipTimeout) {\n        clearTimeout(this.headerTooltipTimeout)\n      }\n    },\n    setDensity(value) {\n      this.densityClazz = value\n      this.$emit('density', value)\n\n      if (this.stateKey) {\n        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState())\n      }\n    },\n    isEnableColumnMove(evt) {\n      return evt.related.className.indexOf(DRAG_NOT_ALLOWED_CLASS) === -1\n    },\n    moveColumnsOnDragEnd(evt) {\n      const { newIndex, oldIndex } = evt\n      const columnFrom = this.columns[oldIndex]\n      const columnTo = this.columns[newIndex]\n\n      const fromIndexInCurrentField = this.currentFields.findIndex(field => field.key === columnFrom.key)\n      const toIndexInCurrentField = this.currentFields.findIndex(field => field.key === columnTo.key)\n      const fieldsMoved = this.moveColumnPosition(this.currentFields, fromIndexInCurrentField, toIndexInCurrentField)\n\n      this.onColumnChange(fieldsMoved)\n    },\n    moveColumnPosition(columns, fromIndex, toIndex) {\n      if (fromIndex === toIndex) return columns\n\n      const newColumns = [...columns]\n      const target = newColumns[fromIndex]\n      const inc = toIndex < fromIndex ? -1 : 1\n\n      for (let i = fromIndex; i !== toIndex; i += inc) {\n        newColumns[i] = newColumns[i + inc]\n        newColumns[i].order = i\n      }\n\n      newColumns[toIndex] = target\n      newColumns[toIndex].order = toIndex\n\n      return newColumns\n    },\n    reset() {\n      this.isInitialState = true\n    },\n    refresh() {\n      this.cached = false\n      this.currentPage = 1\n      this.$refs.table.refresh()\n    },\n    saveColWidth(widths) {\n      if (this.stateKey) {\n        this.tableWidthsResize = widths\n        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState())\n      }\n    },\n    setPageSize(size) {\n      this.pageSize = size\n\n      if (this.stateKey) {\n        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState())\n      }\n    },\n    dataLoader({ apiUrl, currentPage, perPage }) {\n      return fetch(`${apiUrl}?page=${currentPage}&size=${perPage}`)\n        .then((response) => {\n          return response.json()\n        })\n        .then((data) => {\n          if (Array.isArray(data)) {\n            this.cached = true\n            this.hits = data.length\n            this.result = data\n          } else {\n            this.hits = data.hits || 0\n            this.result = data.result || []\n          }\n          this.isInitialState = false\n          return this.result\n        })\n    },\n    dataProvider(ctx, callback) {\n      this.provider({\n        page: ctx.currentPage,\n        pageSize: ctx.perPage,\n        sortBy: ctx.sortBy,\n        sortDesc: ctx.sortDesc\n      }, ({ hits, result }) => {\n        this.hits = hits\n        this.result = result\n        this.isInitialState = false\n        callback(result)\n      })\n      return null\n    },\n    handleActionClick(item, row) {\n      if (item.click) {\n        item.click(row)\n      }\n    },\n    openColumnsDialog() {\n      this.$refs.modal.show()\n    },\n    onColumnChange(fields) {\n      this.currentFields = fields\n      this.$emit('fieldChange', fields)\n      if (this.stateKey) {\n        this.stateProvider.save(`grid_${this.stateKey}`, this.mapState())\n        this.loadState()\n      }\n    },\n    resetState() {\n      if (this.stateKey) {\n        this.currentFields = cloneDeep(this.fields || [])\n        this.densityClazz = gridDensityEnum.MEDIUM\n        this.stateProvider.remove(`grid_${this.stateKey}`)\n      }\n    },\n    loadState(_cb) {\n      this.ready = false\n      if (this.stateKey) {\n        this.stateProvider.load(`grid_${this.stateKey}`, (state) => {\n          this.pageSize = state?.pageSize || this.pageSize\n          const fields = cloneDeep(this.fields || [])\n          this.mergeState(state, fields)\n          this.currentFields = sortBy(fields, 'order')\n          this.state = state\n          this.ready = true\n          this.tableWidthsResize = state?.columnSizes || []\n          this.densityClazz = state?.gridDensity || this.densityClazz\n        })\n      } else {\n        this.ready = true\n      }\n    },\n    mapState() {\n      const state = {\n        pageSize: this.perPage,\n        fields: [],\n        columnSizes: this.tableWidthsResize,\n        gridDensity: this.densityClazz\n      }\n      this.currentFields.forEach((item, index) => {\n        state.fields.push({\n          key: item.key,\n          active: item.active,\n          order: index\n        })\n      })\n      return state\n    },\n    mergeState(state, fields) {\n      if (state && state.fields) {\n        state.fields.forEach((item) => {\n          const field = find(fields, {\n            key: item.key\n          })\n          if (field) {\n            field.order = item.order\n            field.active = item.active\n          }\n        })\n      }\n    },\n    checkPage(checked) {\n      this.selectableItems.forEach(item => this.checkItem(checked, item))\n      this.dispatchSelected()\n    },\n\n    toggleItem(item) {\n      if (this.currentSelectionMap.includes(item[this.uniqueKey])) {\n        this.checkItem(false, item)\n      } else {\n        this.checkItem(true, item)\n      }\n    },\n\n    toggleRadioItem(item) {\n      this.currentSelection = [item]\n      this.$emit('selected', cloneDeep(this.currentSelection))\n    },\n\n    checkItem(checked, item) {\n      const selection = find(this.currentSelection, {\n        [this.uniqueKey]: item[this.uniqueKey]\n      })\n\n      if (!selection && checked) {\n        this.currentSelection.push(item)\n      } else if (selection && !checked) {\n        this.currentSelection.splice(this.currentSelection.indexOf(selection), 1)\n      }\n\n      this.dispatchSelected()\n    },\n    dispatchSelected: debounce(function () {\n      this.$emit('selected', cloneDeep(this.currentSelection))\n    }, 500),\n    rowClass(item, type) {\n      let customRowClasses = ''\n      if (this.tbodyRowClass) {\n        customRowClasses = this.tbodyRowClass(item, type)\n      }\n\n      if (!this.highlightRow) {\n        return customRowClasses\n      }\n\n      const isHighlight = this.highlightRow(item, type)\n      if (isHighlight) {\n        return customRowClasses + ' row-highlight'\n      }\n\n      return customRowClasses\n    },\n    calculateViewport() {\n      const type = typeof this.responsive\n      if (type === 'boolean') {\n        this.showCard = this.responsive\n      } else if (type === 'string') {\n        if (window.innerWidth < this.breakpoint[this.responsive]) {\n          this.showCard = true\n        } else {\n          this.showCard = false\n        }\n      } else if (this.responsive === null) {\n        this.showCard = false\n      }\n    },\n    dispatchGrouping(type, field) {\n      this.$emit('grouping', {\n        type,\n        field\n      })\n    },\n    handleTooltipMouseOver(event) {\n      this.hasEllipsis = event.target.offsetWidth < event.target.scrollWidth || event.target.offsetHeight < event.target.scrollHeight\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  @mixin truncate-multiline($numLines: 1) {\n    overflow: hidden;\n    display: -webkit-box;\n    -webkit-line-clamp: $numLines;\n    -webkit-box-orient: vertical;\n    text-overflow: ellipsis;\n  }\n\n  @mixin stickyColumns() {\n    &.me-sticky-column-left,\n    &.me-sticky-column-right {\n      background-color: var(--me-white);\n      position: sticky;\n      z-index: 2;\n    }\n\n    &.me-sticky-column-left {\n      left: 0;\n    }\n\n    &.me-sticky-column-right {\n      right: 0;\n\n      &.me-action-opened {\n        z-index: 3;\n      }\n    }\n  }\n\n  .button__disabled {\n    background-color: #efeeef !important;\n    color: #c4c1c1 !important;\n    opacity: 0.65 !important;\n  }\n  section.me-grid {\n    .sm {\n      ::v-deep tbody td {\n        height: 40px;\n        padding-top: 4px;\n        padding-bottom: 4px;\n        vertical-align: middle;\n      }\n    }\n\n    .md {\n      ::v-deep tbody td {\n        height: 48px;\n        padding-top: 8px;\n        padding-bottom: 8px;\n        vertical-align: middle;\n      }\n    }\n\n    .lg {\n      ::v-deep tbody td {\n        height: 64px;\n        padding-top: 16px;\n        padding-bottom: 16px;\n        vertical-align: middle;\n      }\n    }\n\n    > header {\n      background: var(--me-white);\n      border-bottom: 1px solid var(--me-gray-6);\n      display: flex;\n      min-height: 40px;\n      padding: 8px 10px;\n      user-select: none;\n\n      > div.header {\n        flex-grow: 1;\n      }\n    }\n\n    ::v-deep table .row-highlight {\n      font-weight: 700;\n      position: relative;\n      &:after {\n        content: \"\";\n        position: absolute;\n        width: 4px;\n        top: 0;\n        left: 0;\n        height: 100%;\n        background: var(--me-primary-2);\n      }\n    }\n\n    > div.table-responsive, ::v-deep table {\n      background-color: var(--me-white);\n    }\n\n    div.b-table-sticky-header {\n      overflow-y: auto;\n\n      ::v-deep {\n        table {\n          thead {\n            position: sticky;\n            top: 0px;\n            background-color: var(--me-white);\n            z-index: 4;\n            box-shadow: inset 0 -0.5px 0 var(--me-gray-6);\n          }\n        }\n      }\n    }\n\n    > div.table-responsive ::v-deep table,\n    ::v-deep table {\n      margin: 0;\n      font-size: 14px;\n\n      .tr-grabbing * {\n        cursor:  grabbing !important;\n      }\n\n      > thead,\n      > tfoot {\n        > tr {\n          > th {\n            position: relative;\n            border-bottom: 1px solid #ebeef5;\n            border-top: none;\n            color: #474747;\n            font-weight: 400;\n            font-size: 12px;\n            white-space: nowrap;\n            outline: none;\n            vertical-align: middle;\n            padding: 0;\n\n            &.sortable-ghost,\n            &.sortable-chosen {\n              background-color: var(--me-primary-7);\n              transition: background-color .3s;\n              box-shadow: var(--me-shadow-level-2);\n\n              &.dragging-item {\n                opacity: .8 !important;\n              }\n            }\n\n            @include stickyColumns();\n\n            // Header\n            .cell-header {\n              display: flex;\n              align-items: center;\n              padding: 12px;\n\n              &__label {\n                white-space: nowrap;\n                overflow: hidden;\n                text-overflow: ellipsis;\n                line-height: 16px;\n\n                &__tooltip {\n                  width: 1px;\n                  margin-right: -4px;\n                  height: 12px;\n                  display: inline-block;\n                }\n              }\n\n              &.handle-drag-area {\n                cursor: grab;\n              }\n\n              .header-actions {\n                display: flex;\n                flex-grow: 1;\n                justify-content: flex-end;\n                align-items: center;\n                height: 16px;\n                gap: 8px;\n                padding-left: 8px;\n\n                > div.aggregations:not(.opened) {\n                  visibility: hidden;\n                }\n\n                // Aggregations\n                > div.aggregations {\n                  & button {\n                    text-decoration: none;\n                    padding: 0;\n                  }\n\n                  &.show,\n                  &:hover {\n                    & ~ .sort-actions {\n                      .sort-none,\n                      .sort-desc,\n                      .sort-acc {\n                        color: var(--me-gray-5);\n                      }\n                    }\n                  }\n                }\n              }\n            }\n\n            &:hover {\n              > div.cell-header {\n                .header-actions {\n                  > div.aggregations {\n                    visibility: visible;\n                  }\n                }\n              }\n            }\n\n            // Sorting\n            &[aria-sort] {\n              cursor: pointer;\n\n              .sort-desc,\n              .sort-asc {\n                display: none;\n                color: var(--me-primary-2);\n                cursor: pointer !important;\n              }\n\n              .sort-none {\n                cursor: pointer !important;\n                display: inline-block;\n                color: var(--me-gray-5);\n              }\n\n              &[aria-sort=\"ascending\"] {\n                .sort-asc {\n                  display: inline-block;\n                }\n              }\n\n              &[aria-sort=\"descending\"] {\n                .sort-desc {\n                  display: inline-block;\n                }\n              }\n\n              &[aria-sort=\"ascending\"], &[aria-sort=\"descending\"] {\n                background: var(--me-primary-7);\n                border-bottom: 1px solid var(--me-primary-3);\n\n                &:hover {\n                  background: var(--me-primary-7);\n                }\n\n                .sort-none {\n                  display: none;\n                }\n              }\n\n              &:hover:not(.sortable-chosen) {\n                background: var(--me-bg-gray);\n\n                .sort-none {\n                  color: var(--me-primary-2);\n                }\n              }\n            }\n          }\n        }\n      }\n\n      > tbody {\n        > tr {\n          transition: .2s all ease-in-out;\n\n          .me-sticky-column-left,\n          .me-sticky-column-right {\n            transition: .2s all ease-in-out;\n          }\n\n          &:hover:not(.b-table-details),\n          &:hover + .b-table-details,\n          &:hover .me-sticky-column-left,\n          &:hover .me-sticky-column-right {\n            background-color: var(--me-primary-7);\n          }\n\n          &.b-table-has-details {\n            > td {\n              border-bottom: none;\n            }\n          }\n\n          &.b-table-details {\n            background-color: var(--me-bg-gray);\n            position: relative;\n            z-index: 1;\n\n            table {\n              background-color: var(--me-bg-gray);\n            }\n\n            .row-details-selectable {\n              padding-left: 48px;\n            }\n          }\n\n          > td {\n            border-top: none;\n            border-bottom: 1px solid var(--me-gray-6);\n            position: relative;\n\n            &.check-box-column {\n              width: 40px;\n            }\n\n            @include stickyColumns();\n\n            > div.cell-content {\n              display: grid;\n\n              .ellipsis {\n                white-space: nowrap;\n                overflow: hidden;\n                text-overflow: ellipsis;\n              }\n\n              .truncate-4 {\n                @include truncate-multiline(4)\n              }\n\n              .no-wrap {\n                white-space: nowrap;\n              }\n            }\n\n            > div.action-icons {\n              display: inline-flex;\n              align-items: center;\n\n              &.to-right {\n                display: flex;\n                justify-content: flex-end;\n              }\n\n              > a.action-icons__link {\n                margin: 0 8px;\n\n                > i {\n                  display: flex;\n                  &::before {\n                    padding-right: 4px;\n                  }\n                }\n\n                &--disabled {\n                  color: rgba(0, 0, 0, 0.36);\n                }\n              }\n\n              > div.dropdown {\n                > button {\n                  padding: 0 8px;\n                  &:after {\n                    content: none;\n                  }\n                }\n              }\n            }\n          }\n        }\n      }\n    }\n\n    &.responsive {\n      ::v-deep table.b-table {\n        > thead {\n          display: none;\n        }\n\n        > tbody {\n          ul.me-grid-card {\n            list-style: none;\n            padding: 0;\n            margin: 0;\n\n            > li {\n              > label {\n                font-weight: 600;\n              }\n            }\n          }\n        }\n\n        @media screen and (max-width: \"768px\") {\n          td {\n            padding: 0.5rem;\n            &:first-child {\n              width: 14px;\n            }\n          }\n        }\n      }\n    }\n  }\n\n  .me-grid-header-tooltip {\n    pointer-events: none;\n  }\n\n</style>\n",".button__disabled {\n  background-color: #efeeef !important;\n  color: #c4c1c1 !important;\n  opacity: 0.65 !important;\n}\n\nsection.me-grid .sm ::v-deep tbody td {\n  height: 40px;\n  padding-top: 4px;\n  padding-bottom: 4px;\n  vertical-align: middle;\n}\nsection.me-grid .md ::v-deep tbody td {\n  height: 48px;\n  padding-top: 8px;\n  padding-bottom: 8px;\n  vertical-align: middle;\n}\nsection.me-grid .lg ::v-deep tbody td {\n  height: 64px;\n  padding-top: 16px;\n  padding-bottom: 16px;\n  vertical-align: middle;\n}\nsection.me-grid > header {\n  background: var(--me-white);\n  border-bottom: 1px solid var(--me-gray-6);\n  display: flex;\n  min-height: 40px;\n  padding: 8px 10px;\n  user-select: none;\n}\nsection.me-grid > header > div.header {\n  flex-grow: 1;\n}\nsection.me-grid ::v-deep table .row-highlight {\n  font-weight: 700;\n  position: relative;\n}\nsection.me-grid ::v-deep table .row-highlight:after {\n  content: \"\";\n  position: absolute;\n  width: 4px;\n  top: 0;\n  left: 0;\n  height: 100%;\n  background: var(--me-primary-2);\n}\nsection.me-grid > div.table-responsive, section.me-grid ::v-deep table {\n  background-color: var(--me-white);\n}\nsection.me-grid div.b-table-sticky-header {\n  overflow-y: auto;\n}\nsection.me-grid div.b-table-sticky-header ::v-deep table thead {\n  position: sticky;\n  top: 0px;\n  background-color: var(--me-white);\n  z-index: 4;\n  box-shadow: inset 0 -0.5px 0 var(--me-gray-6);\n}\nsection.me-grid > div.table-responsive ::v-deep table,\nsection.me-grid ::v-deep table {\n  margin: 0;\n  font-size: 14px;\n}\nsection.me-grid > div.table-responsive ::v-deep table .tr-grabbing *,\nsection.me-grid ::v-deep table .tr-grabbing * {\n  cursor: grabbing !important;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th,\nsection.me-grid ::v-deep table > thead > tr > th,\nsection.me-grid ::v-deep table > tfoot > tr > th {\n  position: relative;\n  border-bottom: 1px solid #ebeef5;\n  border-top: none;\n  color: #474747;\n  font-weight: 400;\n  font-size: 12px;\n  white-space: nowrap;\n  outline: none;\n  vertical-align: middle;\n  padding: 0;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th.sortable-ghost, section.me-grid > div.table-responsive ::v-deep table > thead > tr > th.sortable-chosen,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.sortable-ghost,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.sortable-chosen,\nsection.me-grid ::v-deep table > thead > tr > th.sortable-ghost,\nsection.me-grid ::v-deep table > thead > tr > th.sortable-chosen,\nsection.me-grid ::v-deep table > tfoot > tr > th.sortable-ghost,\nsection.me-grid ::v-deep table > tfoot > tr > th.sortable-chosen {\n  background-color: var(--me-primary-7);\n  transition: background-color 0.3s;\n  box-shadow: var(--me-shadow-level-2);\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th.sortable-ghost.dragging-item, section.me-grid > div.table-responsive ::v-deep table > thead > tr > th.sortable-chosen.dragging-item,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.sortable-ghost.dragging-item,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.sortable-chosen.dragging-item,\nsection.me-grid ::v-deep table > thead > tr > th.sortable-ghost.dragging-item,\nsection.me-grid ::v-deep table > thead > tr > th.sortable-chosen.dragging-item,\nsection.me-grid ::v-deep table > tfoot > tr > th.sortable-ghost.dragging-item,\nsection.me-grid ::v-deep table > tfoot > tr > th.sortable-chosen.dragging-item {\n  opacity: 0.8 !important;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th.me-sticky-column-left, section.me-grid > div.table-responsive ::v-deep table > thead > tr > th.me-sticky-column-right,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.me-sticky-column-left,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.me-sticky-column-right,\nsection.me-grid ::v-deep table > thead > tr > th.me-sticky-column-left,\nsection.me-grid ::v-deep table > thead > tr > th.me-sticky-column-right,\nsection.me-grid ::v-deep table > tfoot > tr > th.me-sticky-column-left,\nsection.me-grid ::v-deep table > tfoot > tr > th.me-sticky-column-right {\n  background-color: var(--me-white);\n  position: sticky;\n  z-index: 2;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th.me-sticky-column-left,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.me-sticky-column-left,\nsection.me-grid ::v-deep table > thead > tr > th.me-sticky-column-left,\nsection.me-grid ::v-deep table > tfoot > tr > th.me-sticky-column-left {\n  left: 0;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th.me-sticky-column-right,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.me-sticky-column-right,\nsection.me-grid ::v-deep table > thead > tr > th.me-sticky-column-right,\nsection.me-grid ::v-deep table > tfoot > tr > th.me-sticky-column-right {\n  right: 0;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th.me-sticky-column-right.me-action-opened,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th.me-sticky-column-right.me-action-opened,\nsection.me-grid ::v-deep table > thead > tr > th.me-sticky-column-right.me-action-opened,\nsection.me-grid ::v-deep table > tfoot > tr > th.me-sticky-column-right.me-action-opened {\n  z-index: 3;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header {\n  display: flex;\n  align-items: center;\n  padding: 12px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header__label,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header__label,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header__label,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header__label {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  line-height: 16px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header__label__tooltip,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header__label__tooltip,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header__label__tooltip,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header__label__tooltip {\n  width: 1px;\n  margin-right: -4px;\n  height: 12px;\n  display: inline-block;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header.handle-drag-area,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header.handle-drag-area,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header.handle-drag-area,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header.handle-drag-area {\n  cursor: grab;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions {\n  display: flex;\n  flex-grow: 1;\n  justify-content: flex-end;\n  align-items: center;\n  height: 16px;\n  gap: 8px;\n  padding-left: 8px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:not(.opened),\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:not(.opened),\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:not(.opened),\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:not(.opened) {\n  visibility: hidden;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations button,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations button,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations button,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations button {\n  text-decoration: none;\n  padding: 0;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc, section.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid ::v-deep table > thead > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-none,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-desc,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations.show ~ .sort-actions .sort-acc,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-none,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-desc,\nsection.me-grid ::v-deep table > tfoot > tr > th .cell-header .header-actions > div.aggregations:hover ~ .sort-actions .sort-acc {\n  color: var(--me-gray-5);\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th:hover > div.cell-header .header-actions > div.aggregations,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th:hover > div.cell-header .header-actions > div.aggregations,\nsection.me-grid ::v-deep table > thead > tr > th:hover > div.cell-header .header-actions > div.aggregations,\nsection.me-grid ::v-deep table > tfoot > tr > th:hover > div.cell-header .header-actions > div.aggregations {\n  visibility: visible;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort],\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort],\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort],\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort] {\n  cursor: pointer;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort] .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort] .sort-asc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort] .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort] .sort-asc,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort] .sort-desc,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort] .sort-asc,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort] .sort-desc,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort] .sort-asc {\n  display: none;\n  color: var(--me-primary-2);\n  cursor: pointer !important;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort] .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort] .sort-none,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort] .sort-none,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort] .sort-none {\n  cursor: pointer !important;\n  display: inline-block;\n  color: var(--me-gray-5);\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-asc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-asc,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-asc,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-asc {\n  display: inline-block;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending] .sort-desc,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-desc,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending] .sort-desc,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-desc {\n  display: inline-block;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending], section.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending],\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending],\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending],\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending],\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending],\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending],\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending] {\n  background: var(--me-primary-7);\n  border-bottom: 1px solid var(--me-primary-3);\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending]:hover, section.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending]:hover,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending]:hover,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending]:hover,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending]:hover,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending]:hover,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending]:hover,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending]:hover {\n  background: var(--me-primary-7);\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-none, section.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending] .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-none,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=ascending] .sort-none,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort][aria-sort=descending] .sort-none,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=ascending] .sort-none,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort][aria-sort=descending] .sort-none {\n  display: none;\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen),\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen),\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen),\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen) {\n  background: var(--me-bg-gray);\n}\nsection.me-grid > div.table-responsive ::v-deep table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none,\nsection.me-grid > div.table-responsive ::v-deep table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none,\nsection.me-grid ::v-deep table > thead > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none,\nsection.me-grid ::v-deep table > tfoot > tr > th[aria-sort]:hover:not(.sortable-chosen) .sort-none {\n  color: var(--me-primary-2);\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr,\nsection.me-grid ::v-deep table > tbody > tr {\n  transition: 0.2s all ease-in-out;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr .me-sticky-column-left,\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr .me-sticky-column-right,\nsection.me-grid ::v-deep table > tbody > tr .me-sticky-column-left,\nsection.me-grid ::v-deep table > tbody > tr .me-sticky-column-right {\n  transition: 0.2s all ease-in-out;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr:hover:not(.b-table-details), section.me-grid > div.table-responsive ::v-deep table > tbody > tr:hover + .b-table-details, section.me-grid > div.table-responsive ::v-deep table > tbody > tr:hover .me-sticky-column-left, section.me-grid > div.table-responsive ::v-deep table > tbody > tr:hover .me-sticky-column-right,\nsection.me-grid ::v-deep table > tbody > tr:hover:not(.b-table-details),\nsection.me-grid ::v-deep table > tbody > tr:hover + .b-table-details,\nsection.me-grid ::v-deep table > tbody > tr:hover .me-sticky-column-left,\nsection.me-grid ::v-deep table > tbody > tr:hover .me-sticky-column-right {\n  background-color: var(--me-primary-7);\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr.b-table-has-details > td,\nsection.me-grid ::v-deep table > tbody > tr.b-table-has-details > td {\n  border-bottom: none;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr.b-table-details,\nsection.me-grid ::v-deep table > tbody > tr.b-table-details {\n  background-color: var(--me-bg-gray);\n  position: relative;\n  z-index: 1;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr.b-table-details table,\nsection.me-grid ::v-deep table > tbody > tr.b-table-details table {\n  background-color: var(--me-bg-gray);\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr.b-table-details .row-details-selectable,\nsection.me-grid ::v-deep table > tbody > tr.b-table-details .row-details-selectable {\n  padding-left: 48px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td,\nsection.me-grid ::v-deep table > tbody > tr > td {\n  border-top: none;\n  border-bottom: 1px solid var(--me-gray-6);\n  position: relative;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td.check-box-column,\nsection.me-grid ::v-deep table > tbody > tr > td.check-box-column {\n  width: 40px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td.me-sticky-column-left, section.me-grid > div.table-responsive ::v-deep table > tbody > tr > td.me-sticky-column-right,\nsection.me-grid ::v-deep table > tbody > tr > td.me-sticky-column-left,\nsection.me-grid ::v-deep table > tbody > tr > td.me-sticky-column-right {\n  background-color: var(--me-white);\n  position: sticky;\n  z-index: 2;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td.me-sticky-column-left,\nsection.me-grid ::v-deep table > tbody > tr > td.me-sticky-column-left {\n  left: 0;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td.me-sticky-column-right,\nsection.me-grid ::v-deep table > tbody > tr > td.me-sticky-column-right {\n  right: 0;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td.me-sticky-column-right.me-action-opened,\nsection.me-grid ::v-deep table > tbody > tr > td.me-sticky-column-right.me-action-opened {\n  z-index: 3;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.cell-content,\nsection.me-grid ::v-deep table > tbody > tr > td > div.cell-content {\n  display: grid;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.cell-content .ellipsis,\nsection.me-grid ::v-deep table > tbody > tr > td > div.cell-content .ellipsis {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.cell-content .truncate-4,\nsection.me-grid ::v-deep table > tbody > tr > td > div.cell-content .truncate-4 {\n  overflow: hidden;\n  display: -webkit-box;\n  -webkit-line-clamp: 4;\n  -webkit-box-orient: vertical;\n  text-overflow: ellipsis;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.cell-content .no-wrap,\nsection.me-grid ::v-deep table > tbody > tr > td > div.cell-content .no-wrap {\n  white-space: nowrap;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons {\n  display: inline-flex;\n  align-items: center;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons.to-right,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons.to-right {\n  display: flex;\n  justify-content: flex-end;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link {\n  margin: 0 8px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link > i,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link > i {\n  display: flex;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link > i::before,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link > i::before {\n  padding-right: 4px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link--disabled,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons > a.action-icons__link--disabled {\n  color: rgba(0, 0, 0, 0.36);\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons > div.dropdown > button,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons > div.dropdown > button {\n  padding: 0 8px;\n}\nsection.me-grid > div.table-responsive ::v-deep table > tbody > tr > td > div.action-icons > div.dropdown > button:after,\nsection.me-grid ::v-deep table > tbody > tr > td > div.action-icons > div.dropdown > button:after {\n  content: none;\n}\nsection.me-grid.responsive ::v-deep table.b-table > thead {\n  display: none;\n}\nsection.me-grid.responsive ::v-deep table.b-table > tbody ul.me-grid-card {\n  list-style: none;\n  padding: 0;\n  margin: 0;\n}\nsection.me-grid.responsive ::v-deep table.b-table > tbody ul.me-grid-card > li > label {\n  font-weight: 600;\n}\n@media screen and (max-width: 768px) {\n  section.me-grid.responsive ::v-deep table.b-table td {\n    padding: 0.5rem;\n  }\n  section.me-grid.responsive ::v-deep table.b-table td:first-child {\n    width: 14px;\n  }\n}\n\n.me-grid-header-tooltip {\n  pointer-events: none;\n}\n\n/*# sourceMappingURL=Grid.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1z = "data-v-f51572bc";
  /* module identifier */
  const __vue_module_identifier__$1z = undefined;
  /* functional template */
  const __vue_is_functional_template__$1z = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1z, staticRenderFns: __vue_staticRenderFns__$1z },
    __vue_inject_styles__$1z,
    __vue_script__$1z,
    __vue_scope_id__$1z,
    __vue_is_functional_template__$1z,
    __vue_module_identifier__$1z,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1A = {
  name: 'MeGridModeOptions',
  components: {
    DropDown: __vue_component__$8
  },

  props: {
    items: {
      type: Array,
      required: true,
      validator(itemList) {
        return itemList.every(item => item.label)
      }
    }
  },

  data() {
    return {
      currentItems: [],
      gridModeSelected: {}
    }
  },

  watch: {
    items(values) {
      this.prepareModels(values);
    }
  },

  created() {
    this.prepareModels(this.items);
  },

  methods: {
    prepareModels(items) {
      this.currentItems = items.map(item => ({ ...item, click: selectedItem => this.toggleGridMode(selectedItem, item.click) }));
      this.gridModeSelected = this.currentItems.find(item => item.active) || { ...(this.currentItems[0] || {}), active: true };
    },

    toggleGridMode(selectedItem, cb) {
      this.currentItems = this.currentItems.map(item => item.label === selectedItem.label ? {
        ...item,
        active: true
      } : {
        ...item,
        active: false
      });

      this.gridModeSelected = { ...selectedItem, active: true };

      if (cb) {
        cb(this.gridModeSelected);
      }
    }
  }
};

/* script */
const __vue_script__$1A = script$1A;

/* template */
var __vue_render__$1A = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("DropDown", {
    staticClass: "me-grid-mode-options",
    attrs: {
      items: _vm.currentItems,
      variant: "light-primary",
      "data-cy": "gridMode"
    },
    scopedSlots: _vm._u([
      {
        key: "button-content",
        fn: function() {
          return [
            _c("me-icon", {
              attrs: {
                icon: _vm.gridModeSelected.icon,
                size: 18,
                "data-cy": "gridModeActiveOption"
              }
            })
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$1A = [];
__vue_render__$1A._withStripped = true;

  /* style */
  const __vue_inject_styles__$1A = function (inject) {
    if (!inject) return
    inject("data-v-04934de7_0", { source: ".me-grid-mode-options.me-dropdown[data-v-04934de7]  .dropdown .btn.btn-light-primary {\n  color: var(--me-gray-2);\n}\n\n/*# sourceMappingURL=GridModeOptions.vue.map */", map: {"version":3,"sources":["/app/build/src/components/GridModeOptions.vue","GridModeOptions.vue"],"names":[],"mappings":"AAmFA;EACA,uBAAA;AClFA;;AAEA,8CAA8C","file":"GridModeOptions.vue","sourcesContent":["<template>\n  <DropDown\n    :items=\"currentItems\"\n    variant=\"light-primary\"\n    class=\"me-grid-mode-options\"\n    data-cy=\"gridMode\"\n  >\n    <template v-slot:button-content>\n      <me-icon\n        :icon=\"gridModeSelected.icon\"\n        :size=\"18\"\n        data-cy=\"gridModeActiveOption\"\n      />\n    </template>\n  </DropDown>\n</template>\n\n<script>\nimport DropDown from './../components/dropdown/DropDown.vue'\n\nexport default {\n  name: 'MeGridModeOptions',\n  components: {\n    DropDown\n  },\n\n  props: {\n    items: {\n      type: Array,\n      required: true,\n      validator(itemList) {\n        return itemList.every(item => item.label)\n      }\n    }\n  },\n\n  data() {\n    return {\n      currentItems: [],\n      gridModeSelected: {}\n    }\n  },\n\n  watch: {\n    items(values) {\n      this.prepareModels(values)\n    }\n  },\n\n  created() {\n    this.prepareModels(this.items)\n  },\n\n  methods: {\n    prepareModels(items) {\n      this.currentItems = items.map(item => ({ ...item, click: selectedItem => this.toggleGridMode(selectedItem, item.click) }))\n      this.gridModeSelected = this.currentItems.find(item => item.active) || { ...(this.currentItems[0] || {}), active: true }\n    },\n\n    toggleGridMode(selectedItem, cb) {\n      this.currentItems = this.currentItems.map(item => item.label === selectedItem.label ? {\n        ...item,\n        active: true\n      } : {\n        ...item,\n        active: false\n      })\n\n      this.gridModeSelected = { ...selectedItem, active: true }\n\n      if (cb) {\n        cb(this.gridModeSelected)\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-grid-mode-options {\n    &.me-dropdown {\n      ::v-deep {\n        .dropdown {\n          .btn.btn-light-primary {\n            color: var(--me-gray-2);\n          }\n        }\n      }\n    }\n  }\n\n</style>\n",".me-grid-mode-options.me-dropdown ::v-deep .dropdown .btn.btn-light-primary {\n  color: var(--me-gray-2);\n}\n\n/*# sourceMappingURL=GridModeOptions.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1A = "data-v-04934de7";
  /* module identifier */
  const __vue_module_identifier__$1A = undefined;
  /* functional template */
  const __vue_is_functional_template__$1A = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1A, staticRenderFns: __vue_staticRenderFns__$1A },
    __vue_inject_styles__$1A,
    __vue_script__$1A,
    __vue_scope_id__$1A,
    __vue_is_functional_template__$1A,
    __vue_module_identifier__$1A,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//


var script$1B = {
  name: 'MeGridConfigBadge',
  props: {
    field: {
      type: Object,
      required: true
    },
    searchTerm: {
      type: String,
      default: null
    },
    toggleEnable: {
      type: Boolean,
      required: true
    },
    onToggle: {
      type: Function,
      required: true
    }
  },

  methods: {
    isFieldBlured() {
      return this.searchTerm && !this.field.label.toLowerCase().includes(this.searchTerm.toLowerCase())
    }
  }
};

/* script */
const __vue_script__$1B = script$1B;

/* template */
var __vue_render__$1B = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      key: _vm.field.id,
      staticClass: "grid-config-badge",
      class: {
        active: _vm.field.active,
        disableToggle: !_vm.toggleEnable,
        blured: _vm.isFieldBlured()
      },
      on: {
        click: function() {
          return _vm.onToggle(_vm.field)
        }
      }
    },
    [
      _c(
        "span",
        {
          directives: [
            {
              name: "me-tooltip",
              rawName: "v-me-tooltip",
              value:
                _vm.field.active && !_vm.toggleEnable
                  ? this.$toolkit.t("gridConfig.badge.disabled")
                  : "",
              expression:
                "field.active && !toggleEnable ? this.$toolkit.t('gridConfig.badge.disabled') : ''"
            }
          ]
        },
        [_vm._v("\n    " + _vm._s(_vm.field.label) + "\n  ")]
      ),
      _vm._v(" "),
      _c("i", {
        staticClass: "grid-config-badge__icon-drag me-icon icon-grip-vertical"
      })
    ]
  )
};
var __vue_staticRenderFns__$1B = [];
__vue_render__$1B._withStripped = true;

  /* style */
  const __vue_inject_styles__$1B = function (inject) {
    if (!inject) return
    inject("data-v-6bf1c0e6_0", { source: ".grid-config-badge[data-v-6bf1c0e6] {\n  color: var(--me-gray-2);\n  background-color: var(--me-bg-gray);\n  white-space: nowrap;\n  border-radius: 100px;\n  cursor: pointer;\n  display: inline-block;\n  font-size: 12px;\n  padding: 3px 8px;\n  border: none;\n}\n.grid-config-badge.blured[data-v-6bf1c0e6] {\n  background-color: var(--me-white);\n}\n.grid-config-badge.active[data-v-6bf1c0e6] {\n  color: white;\n}\n.grid-config-badge.active[data-v-6bf1c0e6]:not(.blured) {\n  background-color: var(--me-primary-2);\n}\n.grid-config-badge.active.blured[data-v-6bf1c0e6] {\n  background-color: var(--me-primary-7);\n}\n.grid-config-badge.active.disableToggle[data-v-6bf1c0e6] {\n  cursor: not-allowed;\n}\n.grid-config-badge__icon-drag[data-v-6bf1c0e6] {\n  font-size: 10px;\n  margin-left: 4px;\n  cursor: grab;\n}\n.grid-config-badge[data-v-6bf1c0e6]:not(:last-child) {\n  margin-right: 8px;\n}\n\n/*# sourceMappingURL=GridConfigBadge.vue.map */", map: {"version":3,"sources":["/app/build/src/components/grids-config/GridConfigBadge.vue","GridConfigBadge.vue"],"names":[],"mappings":"AA8CA;EACA,uBAAA;EACA,mCAAA;EACA,mBAAA;EACA,oBAAA;EACA,eAAA;EACA,qBAAA;EACA,eAAA;EACA,gBAAA;EACA,YAAA;AC7CA;AD+CA;EACA,iCAAA;AC7CA;ADgDA;EACA,YAAA;AC9CA;ADiDA;EACA,qCAAA;AC/CA;ADkDA;EACA,qCAAA;AChDA;ADmDA;EACA,mBAAA;ACjDA;ADoDA;EACA,eAAA;EACA,gBAAA;EACA,YAAA;AClDA;ADqDA;EACA,iBAAA;ACnDA;;AAEA,8CAA8C","file":"GridConfigBadge.vue","sourcesContent":["<template>\n  <div\n    :key=\"field.id\"\n    :class=\"{active: field.active, disableToggle: !toggleEnable, blured: isFieldBlured()}\"\n    @click=\"() => onToggle(field)\"\n    class=\"grid-config-badge\"\n  >\n    <span v-me-tooltip=\"field.active && !toggleEnable ? this.$toolkit.t('gridConfig.badge.disabled') : ''\">\n      {{ field.label }}\n    </span>\n    <i class=\"grid-config-badge__icon-drag me-icon icon-grip-vertical\" />\n  </div>\n</template>\n\n<script>\n\nexport default {\n  name: 'MeGridConfigBadge',\n  props: {\n    field: {\n      type: Object,\n      required: true\n    },\n    searchTerm: {\n      type: String,\n      default: null\n    },\n    toggleEnable: {\n      type: Boolean,\n      required: true\n    },\n    onToggle: {\n      type: Function,\n      required: true\n    }\n  },\n\n  methods: {\n    isFieldBlured() {\n      return this.searchTerm && !this.field.label.toLowerCase().includes(this.searchTerm.toLowerCase())\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .grid-config-badge {\n    color: var(--me-gray-2);\n    background-color: var(--me-bg-gray);\n    white-space: nowrap;\n    border-radius: 100px;\n    cursor: pointer;\n    display: inline-block;\n    font-size: 12px;\n    padding: 3px 8px;\n    border: none;\n\n    &.blured {\n      background-color: var(--me-white);\n    }\n\n    &.active {\n      color: white;\n    }\n\n    &.active:not(.blured) {\n      background-color: var(--me-primary-2);\n    }\n\n    &.active.blured {\n      background-color: var(--me-primary-7);\n    }\n\n    &.active.disableToggle {\n      cursor: not-allowed;\n    }\n\n    &__icon-drag {\n      font-size: 10px;\n      margin-left: 4px;\n      cursor: grab;\n    }\n\n    &:not(:last-child) {\n      margin-right: 8px;\n    }\n  }\n</style>\n",".grid-config-badge {\n  color: var(--me-gray-2);\n  background-color: var(--me-bg-gray);\n  white-space: nowrap;\n  border-radius: 100px;\n  cursor: pointer;\n  display: inline-block;\n  font-size: 12px;\n  padding: 3px 8px;\n  border: none;\n}\n.grid-config-badge.blured {\n  background-color: var(--me-white);\n}\n.grid-config-badge.active {\n  color: white;\n}\n.grid-config-badge.active:not(.blured) {\n  background-color: var(--me-primary-2);\n}\n.grid-config-badge.active.blured {\n  background-color: var(--me-primary-7);\n}\n.grid-config-badge.active.disableToggle {\n  cursor: not-allowed;\n}\n.grid-config-badge__icon-drag {\n  font-size: 10px;\n  margin-left: 4px;\n  cursor: grab;\n}\n.grid-config-badge:not(:last-child) {\n  margin-right: 8px;\n}\n\n/*# sourceMappingURL=GridConfigBadge.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1B = "data-v-6bf1c0e6";
  /* module identifier */
  const __vue_module_identifier__$1B = undefined;
  /* functional template */
  const __vue_is_functional_template__$1B = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1B, staticRenderFns: __vue_staticRenderFns__$1B },
    __vue_inject_styles__$1B,
    __vue_script__$1B,
    __vue_scope_id__$1B,
    __vue_is_functional_template__$1B,
    __vue_module_identifier__$1B,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1C = {
  name: 'MeGridConfig',

  components: {
    Draggable: __vue_component__$s,
    TextInput: __vue_component__$p,
    Collapse: __vue_component__$n,
    GridConfigBadge: __vue_component__$1B
  },

  props: {
    grid: {
      type: Object,
      required: true
    },
    isOpened: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      search: ''
    }
  },

  computed: {
    canToggle() {
      return this.grid.fields.filter(field => field.active).length > 1
    },

    fields: {
      get() {
        return this.grid.fields
      },
      set(fieldList) {
        const orderedFieldList = fieldList.map((field, index) => ({
          ...field,
          order: index
        }));
        const gridOrdered = { ...this.grid, fields: orderedFieldList };
        this.$emit('orderGrid', { gridOrdered });
      }
    }
  },

  methods: {
    toggleItem(field) {
      if (!this.canToggle && field.active) return
      const fieldsToggled = this.grid.fields.map(gridField => gridField.key === field.key ? { ...gridField, active: !field.active } : gridField);
      const gridToggled = { ...this.grid, fields: fieldsToggled };
      this.$emit('toggleItem', { gridToggled });
    }
  }
};

/* script */
const __vue_script__$1C = script$1C;

/* template */
var __vue_render__$1C = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Collapse",
    {
      staticClass: "grid-config",
      attrs: {
        title: _vm.grid.description,
        opened: _vm.isOpened,
        "optional-button": ""
      },
      scopedSlots: _vm._u([
        {
          key: "optionalButton",
          fn: function() {
            return [
              _c("TextInput", {
                staticClass: "input-search",
                class: { searching: _vm.search },
                attrs: {
                  placeholder: _vm.$toolkit.t("gridConfig.search"),
                  size: "sm",
                  type: "search"
                },
                model: {
                  value: _vm.search,
                  callback: function($$v) {
                    _vm.search = $$v;
                  },
                  expression: "search"
                }
              })
            ]
          },
          proxy: true
        }
      ])
    },
    [
      _vm._v(" "),
      _c(
        "Draggable",
        {
          model: {
            value: _vm.fields,
            callback: function($$v) {
              _vm.fields = $$v;
            },
            expression: "fields"
          }
        },
        _vm._l(_vm.fields, function(field) {
          return _c("GridConfigBadge", {
            key: field.id,
            staticClass: "d-inline-block mr-1 mb-2",
            attrs: {
              field: field,
              searchTerm: _vm.search,
              toggleEnable: _vm.canToggle,
              onToggle: _vm.toggleItem
            }
          })
        }),
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1C = [];
__vue_render__$1C._withStripped = true;

  /* style */
  const __vue_inject_styles__$1C = function (inject) {
    if (!inject) return
    inject("data-v-08806d9a_0", { source: ".grid-config[data-v-08806d9a]  h5 {\n  margin-right: 16px !important;\n}\n.grid-config .input-search[data-v-08806d9a] {\n  width: 200px;\n}\n\n/*# sourceMappingURL=GridConfig.vue.map */", map: {"version":3,"sources":["/app/build/src/components/grids-config/GridConfig.vue","GridConfig.vue"],"names":[],"mappings":"AAkGA;EACA,6BAAA;ACjGA;ADoGA;EACA,YAAA;AClGA;;AAEA,yCAAyC","file":"GridConfig.vue","sourcesContent":["<template>\n  <Collapse\n    :title=\"grid.description\"\n    :opened=\"isOpened\"\n    optional-button\n    class=\"grid-config\"\n  >\n    <template v-slot:optionalButton>\n      <TextInput\n        v-model=\"search\"\n        :class=\"{'searching': search}\"\n        :placeholder=\"$toolkit.t('gridConfig.search')\"\n        size=\"sm\"\n        type=\"search\"\n        class=\"input-search\"\n      />\n    </template>\n\n    <Draggable v-model=\"fields\">\n      <GridConfigBadge\n        v-for=\"field in fields\"\n        :field=\"field\"\n        :searchTerm=\"search\"\n        :toggleEnable=\"canToggle\"\n        :onToggle=\"toggleItem\"\n        :key=\"field.id\"\n        class=\"d-inline-block mr-1 mb-2\"\n      />\n    </Draggable>\n  </Collapse>\n</template>\n\n<script>\nimport GridConfigBadge from './GridConfigBadge.vue'\nimport TextInput from './../TextInput.vue'\nimport Collapse from './../Collapse.vue'\nimport Draggable from './../Draggable.vue'\n\nexport default {\n  name: 'MeGridConfig',\n\n  components: {\n    Draggable,\n    TextInput,\n    Collapse,\n    GridConfigBadge\n  },\n\n  props: {\n    grid: {\n      type: Object,\n      required: true\n    },\n    isOpened: {\n      type: Boolean,\n      default: true\n    }\n  },\n\n  data() {\n    return {\n      search: ''\n    }\n  },\n\n  computed: {\n    canToggle() {\n      return this.grid.fields.filter(field => field.active).length > 1\n    },\n\n    fields: {\n      get() {\n        return this.grid.fields\n      },\n      set(fieldList) {\n        const orderedFieldList = fieldList.map((field, index) => ({\n          ...field,\n          order: index\n        }))\n        const gridOrdered = { ...this.grid, fields: orderedFieldList }\n        this.$emit('orderGrid', { gridOrdered })\n      }\n    }\n  },\n\n  methods: {\n    toggleItem(field) {\n      if (!this.canToggle && field.active) return\n      const fieldsToggled = this.grid.fields.map(gridField => gridField.key === field.key ? { ...gridField, active: !field.active } : gridField)\n      const gridToggled = { ...this.grid, fields: fieldsToggled }\n      this.$emit('toggleItem', { gridToggled })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .grid-config {\n    ::v-deep h5 {\n      margin-right: 16px !important;\n    }\n\n    .input-search {\n      width: 200px;\n    }\n  }\n\n</style>\n",".grid-config ::v-deep h5 {\n  margin-right: 16px !important;\n}\n.grid-config .input-search {\n  width: 200px;\n}\n\n/*# sourceMappingURL=GridConfig.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1C = "data-v-08806d9a";
  /* module identifier */
  const __vue_module_identifier__$1C = undefined;
  /* functional template */
  const __vue_is_functional_template__$1C = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1C, staticRenderFns: __vue_staticRenderFns__$1C },
    __vue_inject_styles__$1C,
    __vue_script__$1C,
    __vue_scope_id__$1C,
    __vue_is_functional_template__$1C,
    __vue_module_identifier__$1C,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1D = {
  name: 'MeSkeletonGridsConfig',
  components: {
    BSkeleton
  },

  props: {
    numOfGrids: {
      type: Number,
      default: 3
    },
    numOfFields: {
      type: Number,
      default: 10
    }
  },

  methods: {
    randomActive() {
      const max = 4;
      const min = 1;
      return Math.floor(Math.random() * (max - min + 1) + min) === 1
    }
  }
};

/* script */
const __vue_script__$1D = script$1D;

/* template */
var __vue_render__$1D = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-grids-config" }, [
    _c("header", [_c("BSkeleton", { attrs: { width: "140px" } })], 1),
    _vm._v(" "),
    _c("main", [
      _c(
        "div",
        { staticClass: "d-flex flex-column w-100" },
        _vm._l(_vm.numOfGrids, function(u) {
          return _c(
            "div",
            {
              key: u,
              staticClass: "grid-config-container d-flex flex-column w-100 mb-4"
            },
            [
              _c(
                "div",
                { staticClass: "grid-config-title" },
                [_c("BSkeleton", { attrs: { width: "50%" } })],
                1
              ),
              _vm._v(" "),
              _c(
                "div",
                { staticClass: "grid-config-badges" },
                _vm._l(_vm.numOfFields, function(i) {
                  return _c(
                    "div",
                    {
                      key: i,
                      staticClass: "grid-config-badge mr-1",
                      class: { active: _vm.randomActive() }
                    },
                    [
                      _c("BSkeleton", {
                        attrs: { width: "50px", height: "30px" }
                      })
                    ],
                    1
                  )
                }),
                0
              )
            ]
          )
        }),
        0
      )
    ])
  ])
};
var __vue_staticRenderFns__$1D = [];
__vue_render__$1D._withStripped = true;

  /* style */
  const __vue_inject_styles__$1D = function (inject) {
    if (!inject) return
    inject("data-v-5d456c7e_0", { source: ".me-skeleton-grids-config[data-v-5d456c7e] {\n  background: white;\n  border-radius: 6px;\n  box-shadow: var(--me-shadow-level-1);\n  font-size: 12px;\n  padding: 10px 15px 5px 15px;\n}\n.me-skeleton-grids-config > header[data-v-5d456c7e] {\n  margin-bottom: 10px;\n}\n.me-skeleton-grids-config > main[data-v-5d456c7e] {\n  display: flex;\n}\n.me-skeleton-grids-config > main .grid-config-badges[data-v-5d456c7e] {\n  display: flex;\n  flex-wrap: wrap;\n}\n.me-skeleton-grids-config > main .grid-config-badges .grid-config-badge.active[data-v-5d456c7e] {\n  background-color: var(--me-primary-2);\n  border-radius: 5px;\n  height: 30px;\n}\n\n/*# sourceMappingURL=SkeletonGridsConfig.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonGridsConfig.vue","SkeletonGridsConfig.vue"],"names":[],"mappings":"AAqDA;EACA,iBAAA;EACA,kBAAA;EACA,oCAAA;EACA,eAAA;EACA,2BAAA;ACpDA;ADsDA;EACA,mBAAA;ACpDA;ADuDA;EACA,aAAA;ACrDA;ADuDA;EACA,aAAA;EACA,eAAA;ACrDA;ADuDA;EACA,qCAAA;EACA,kBAAA;EACA,YAAA;ACrDA;;AAEA,kDAAkD","file":"SkeletonGridsConfig.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-grids-config\">\n    <header>\n      <BSkeleton width=\"140px\" />\n    </header>\n    <main>\n      <div class=\"d-flex flex-column w-100\">\n        <div v-for=\"u in numOfGrids\" :key=\"u\" class=\"grid-config-container d-flex flex-column w-100 mb-4\">\n          <div class=\"grid-config-title\">\n            <BSkeleton width=\"50%\" />\n          </div>\n          <div class=\"grid-config-badges\">\n            <div v-for=\"i in numOfFields\" :key=\"i\" :class=\"{'active': randomActive()}\" class=\"grid-config-badge mr-1\">\n              <BSkeleton width=\"50px\" height=\"30px\" />\n            </div>\n          </div>\n        </div>\n      </div>\n    </main>\n  </div>\n</template>\n\n<script>\nimport { BSkeleton } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonGridsConfig',\n  components: {\n    BSkeleton\n  },\n\n  props: {\n    numOfGrids: {\n      type: Number,\n      default: 3\n    },\n    numOfFields: {\n      type: Number,\n      default: 10\n    }\n  },\n\n  methods: {\n    randomActive() {\n      const max = 4\n      const min = 1\n      return Math.floor(Math.random() * (max - min + 1) + min) === 1\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-grids-config {\n    background: white;\n    border-radius: 6px;\n    box-shadow: var(--me-shadow-level-1);\n    font-size: 12px;\n    padding: 10px 15px 5px 15px;\n\n    > header {\n      margin-bottom: 10px;\n    }\n\n    > main {\n      display: flex;\n\n      .grid-config-badges {\n        display: flex;\n        flex-wrap: wrap;\n\n        .grid-config-badge.active {\n          background-color: var(--me-primary-2);\n          border-radius: 5px;\n          height: 30px;\n        }\n      }\n    }\n  }\n</style>\n",".me-skeleton-grids-config {\n  background: white;\n  border-radius: 6px;\n  box-shadow: var(--me-shadow-level-1);\n  font-size: 12px;\n  padding: 10px 15px 5px 15px;\n}\n.me-skeleton-grids-config > header {\n  margin-bottom: 10px;\n}\n.me-skeleton-grids-config > main {\n  display: flex;\n}\n.me-skeleton-grids-config > main .grid-config-badges {\n  display: flex;\n  flex-wrap: wrap;\n}\n.me-skeleton-grids-config > main .grid-config-badges .grid-config-badge.active {\n  background-color: var(--me-primary-2);\n  border-radius: 5px;\n  height: 30px;\n}\n\n/*# sourceMappingURL=SkeletonGridsConfig.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1D = "data-v-5d456c7e";
  /* module identifier */
  const __vue_module_identifier__$1D = undefined;
  /* functional template */
  const __vue_is_functional_template__$1D = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1D, staticRenderFns: __vue_staticRenderFns__$1D },
    __vue_inject_styles__$1D,
    __vue_script__$1D,
    __vue_scope_id__$1D,
    __vue_is_functional_template__$1D,
    __vue_module_identifier__$1D,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1E = {
  name: 'MeGridsConfig',
  components: {
    GridConfig: __vue_component__$1C,
    SkeletonGridsConfig: __vue_component__$1D
  },

  props: {
    defaultGrids: {
      type: Array,
      default: () => []
    },
    openedGridsIds: {
      type: Array,
      default: () => []
    },
    apiUrl: {
      type: String,
      default: ''
    },
    provider: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      grids: [],
      isLoading: false
    }
  },

  computed: {
  },

  async mounted() {
    try {
      this.isLoading = true;
      await this.setGrids();
    } catch (error) {
      this.grids = [];
      this.$emit('updatedGridsError', error);
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    async setGrids() {
      if (this.apiUrl || this.provider) {
        this.grids = await this.getGridsConfigSaved();
      } else {
        this.grids = this.defaultGrids;
      }
    },

    async getGridsConfigSaved() {
      let gridsConfigSaved = null;
      if (this.apiUrl) {
        gridsConfigSaved = await this.dataLoader(this.apiUrl);
      }

      if (this.provider) {
        gridsConfigSaved = await this.provider();
      }

      return gridsConfigSaved
    },

    async dataLoader(apiUrl) {
      const response = await fetch(apiUrl);
      return response.json()
    },

    isOpened(grid) {
      if (!this.openedGridsIds.length) return true
      return this.openedGridsIds.includes(grid.id)
    },

    orderGrids({ gridOrdered }) {
      const gridsOrdered = this.grids.map(grid => grid.id === gridOrdered.id ? gridOrdered : grid);
      this.grids = gridsOrdered;
      this.$emit('updatedGrids', gridsOrdered);
    },

    toggleGridItem({ gridToggled }) {
      const gridsToggled = this.grids.map(grid => grid.id === gridToggled.id ? gridToggled : grid);
      this.grids = gridsToggled;
      this.$emit('updatedGrids', gridsToggled);
    }
  }
};

/* script */
const __vue_script__$1E = script$1E;

/* template */
var __vue_render__$1E = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "grids-config" },
    [
      _vm.isLoading
        ? [_c("SkeletonGridsConfig")]
        : _vm._l(_vm.grids, function(grid) {
            return _c("GridConfig", {
              key: grid.id,
              attrs: { grid: grid, isOpened: _vm.isOpened(grid) },
              on: { orderGrid: _vm.orderGrids, toggleItem: _vm.toggleGridItem }
            })
          })
    ],
    2
  )
};
var __vue_staticRenderFns__$1E = [];
__vue_render__$1E._withStripped = true;

  /* style */
  const __vue_inject_styles__$1E = undefined;
  /* scoped */
  const __vue_scope_id__$1E = undefined;
  /* module identifier */
  const __vue_module_identifier__$1E = undefined;
  /* functional template */
  const __vue_is_functional_template__$1E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1E, staticRenderFns: __vue_staticRenderFns__$1E },
    __vue_inject_styles__$1E,
    __vue_script__$1E,
    __vue_scope_id__$1E,
    __vue_is_functional_template__$1E,
    __vue_module_identifier__$1E,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$1F = {
  name: 'MeHeader',
  components: {
    Avatar: __vue_component__$w,
    Badge: __vue_component__$x,
    ListGroup: __vue_component__$1r,
    Sitemap: __vue_component__$1s,
    Icon: __vue_component__,
    Popover: __vue_component__$1,
    Button: __vue_component__$6
  },
  props: {
    brand: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    profileItems: {
      type: Array,
      required: true
    },
    navItems: {
      type: Array,
      default() {
        return []
      }
    },
    siteMap: {
      type: Array,
      default() {
        return []
      }
    },
    fixedTop: {
      type: Boolean,
      default: false
    },
    showStartMenu: {
      type: Boolean,
      default: false
    },
    shadow: {
      type: Boolean,
      default: false
    },
    headerTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      startMenuOpened: false,
      sideMenuOpened: false,
      profileOpened: false,
      siteMapOpened: false,
      internalId: null,
      baseURL: window.location
    }
  },
  computed: {
    userBadgeIcon() {
      return this.user.badge?.icon || ''
    },
    userBadgeVariant() {
      return this.user.badge?.variant || ''
    },
    getBackgroundStyle() {
      let bg = '';

      if (this.brand.background) {
        if (this.brand.background.mainImage && this.brand.background.repeatImage) {
          bg = `url(${this.brand.background.mainImage}) no-repeat, url(${this.brand.background.repeatImage}) repeat-x`;
        } else if (this.brand.background.mainImage && !this.brand.background.repeatImage) {
          bg = `url(${this.brand.background.mainImage}) ${this.brand.background.primaryColor || ''} no-repeat`;
        } else if (!this.brand.background.mainImage && this.brand.background.repeatImage) {
          bg = `url(${this.brand.background.repeatImage}) repeat-x`;
        } else if (!this.brand.background.secondaryColor) {
          bg = this.brand.background.primaryColor;
        } else {
          bg = `linear-gradient(to right, ${this.brand.background.primaryColor}, ${this.brand.background.secondaryColor})`;
        }
      }
      return {
        background: bg,
        borderBottom: `1px solid ${this.brand.iconColor}`
      }
    },
    getIconStyle() {
      let color = '';
      if (this.brand.iconColor) {
        color = `${this.brand.iconColor} !important`;
      }
      return {
        color: color
      }
    },

    getHeaderTitleStyle() {
      return this.getIconStyle.color ? this.getIconStyle : { color: '#fff' }
    }
  },
  created() {
    this.internalId = new Date().getTime();
  },
  methods: {
    getNavItemProps(item = {}) {
      const { url: href, target = '_self' } = item;

      const navItemProps = {
        class: item.active ? 'active' : '',
        style: this.getIconStyle
      };

      if (href) {
        return { ...navItemProps, href, target }
      }

      return navItemProps
    },
    handleNavItemEvent(eventName, item) {
      if (item[eventName]) {
        item[eventName]();
      }
    },
    toggleStartMenu() {
      this.startMenuOpened = !this.startMenuOpened;
    },

    closeStartMenu() {
      this.startMenuOpened = false;
    },

    openProfile() {
      this.profileOpened = true;
    },

    closeProfile() {
      this.profileOpened = false;
    },

    openSideMenu() {
      this.sideMenuOpened = true;
    },

    closeSideMenu() {
      this.sideMenuOpened = false;
    },

    closeSitemap() {
      this.siteMapOpened = false;
    },

    closeAll() {
      this.closeSideMenu();
      this.closeProfile();
      this.closeStartMenu();
      this.closeSitemap();
    },

    loggedUser() {
      return Object.keys(this.user).length
    },

    hasTitle() {
      return this.headerTitle || this.$slots['header-title']
    }
  }
};

/* script */
const __vue_script__$1F = script$1F;

/* template */
var __vue_render__$1F = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "header",
    {
      staticClass: "me-header",
      class: {
        side: _vm.sideMenuOpened,
        profile: _vm.profileOpened,
        start: _vm.startMenuOpened,
        "box-shadow": _vm.shadow,
        "fixed-top": _vm.fixedTop,
        "start-menu": _vm.showStartMenu,
        sitemap: _vm.siteMapOpened
      },
      style: _vm.getBackgroundStyle,
      attrs: { id: "header-" + _vm.internalId, "data-cy": "header" }
    },
    [
      _c("nav", [
        _c(
          "section",
          [
            _vm._t("left", [
              _c("div", { staticClass: "brand" }, [
                _c(
                  "a",
                  {
                    attrs: {
                      href: _vm.brand.link || "#",
                      target: _vm.brand.newTab ? "_blank" : "_self"
                    }
                  },
                  [_c("img", { attrs: { src: _vm.brand.logo, alt: "logo" } })]
                )
              ]),
              _vm._v(" "),
              _vm.hasTitle()
                ? _c(
                    "div",
                    { staticClass: "header-title" },
                    [
                      _vm._t("header-title", [
                        _c("h1", { style: _vm.getHeaderTitleStyle }, [
                          _vm._v(
                            "\n              " +
                              _vm._s(_vm.headerTitle) +
                              "\n            "
                          )
                        ])
                      ])
                    ],
                    2
                  )
                : _vm._e()
            ])
          ],
          2
        ),
        _vm._v(" "),
        _c("section", [_vm._t("center")], 2),
        _vm._v(" "),
        _vm.loggedUser() && !_vm.$slots["header-title"]
          ? _c(
              "section",
              [
                _vm._t("right", [
                  _c(
                    "ul",
                    { staticClass: "nav-items" },
                    _vm._l(_vm.navItems, function(item, index) {
                      return _c(
                        "li",
                        {
                          key: index,
                          class: { pressed: _vm.siteMapOpened && item.siteMap }
                        },
                        [
                          item.separator
                            ? _c(
                                "div",
                                _vm._b(
                                  { staticClass: "separator" },
                                  "div",
                                  _vm.getNavItemProps(),
                                  false
                                )
                              )
                            : _vm._e(),
                          _vm._v(" "),
                          (item.url || item.click) && !item.siteMap
                            ? _c(
                                "a",
                                _vm._b(
                                  {
                                    on: {
                                      click: function($event) {
                                        item.click ? item.click() : null;
                                      },
                                      mouseenter: function($event) {
                                        return _vm.handleNavItemEvent(
                                          "hover",
                                          item
                                        )
                                      },
                                      mouseleave: function($event) {
                                        return _vm.handleNavItemEvent(
                                          "leave",
                                          item
                                        )
                                      }
                                    }
                                  },
                                  "a",
                                  _vm.getNavItemProps(item),
                                  false
                                ),
                                [
                                  _c(
                                    "Icon",
                                    { attrs: { icon: item.icon } },
                                    [
                                      item.badge && item.badge.text
                                        ? [
                                            _c(
                                              "Badge",
                                              {
                                                staticClass: "badge-size",
                                                class: [
                                                  "badge-size-" +
                                                    item.badge.text.toString()
                                                      .length
                                                ],
                                                attrs: { appearance: "danger" }
                                              },
                                              [
                                                item.badge.icon
                                                  ? _c("Icon", {
                                                      attrs: {
                                                        icon: item.badge.icon
                                                      }
                                                    })
                                                  : _c(
                                                      "span",
                                                      {
                                                        staticClass:
                                                          "badge-text"
                                                      },
                                                      [
                                                        _vm._v(
                                                          _vm._s(
                                                            item.badge.text
                                                          )
                                                        )
                                                      ]
                                                    )
                                              ],
                                              1
                                            )
                                          ]
                                        : _vm._e()
                                    ],
                                    2
                                  ),
                                  _vm._v(" "),
                                  _c("span", [
                                    _vm._v(" " + _vm._s(item.label) + " ")
                                  ])
                                ],
                                1
                              )
                            : _vm._e(),
                          _vm._v(" "),
                          item.siteMap
                            ? _c(
                                "a",
                                {
                                  style: _vm.getIconStyle,
                                  attrs: { id: "popover-" + _vm.internalId },
                                  on: {
                                    click: function($event) {
                                      item.click ? item.click() : null;
                                    }
                                  }
                                },
                                [
                                  _c("Icon", { attrs: { icon: item.icon } }),
                                  _vm._v(" "),
                                  _c("span", [
                                    _vm._v(" " + _vm._s(item.label) + " ")
                                  ])
                                ],
                                1
                              )
                            : _vm._e(),
                          _vm._v(" "),
                          item.siteMap
                            ? _c(
                                "Popover",
                                {
                                  attrs: {
                                    target: "popover-" + _vm.internalId,
                                    container: "header-" + _vm.internalId,
                                    show: _vm.siteMapOpened,
                                    placement: "bottom",
                                    triggers: "click"
                                  },
                                  on: {
                                    "update:show": function($event) {
                                      _vm.siteMapOpened = $event;
                                    }
                                  }
                                },
                                [
                                  _c("Sitemap", {
                                    attrs: {
                                      searchable: true,
                                      items: _vm.siteMap,
                                      caret: "me-icon-s icon-caret-down",
                                      left: ""
                                    }
                                  })
                                ],
                                1
                              )
                            : _vm._e()
                        ],
                        1
                      )
                    }),
                    0
                  )
                ])
              ],
              2
            )
          : _vm._e(),
        _vm._v(" "),
        !_vm.loggedUser()
          ? _c(
              "div",
              { staticClass: "not-logged-area" },
              [
                _vm._t("not-logged"),
                _vm._v(" "),
                _c(
                  "Button",
                  {
                    attrs: {
                      href:
                        _vm.baseURL.origin +
                        "/do/Login.mvc/LoginNew?Dest=" +
                        _vm.baseURL.pathname,
                      variant: "outline-light"
                    }
                  },
                  [_vm._v("\n        Login\n      ")]
                )
              ],
              2
            )
          : _vm._e()
      ]),
      _vm._v(" "),
      _vm.loggedUser()
        ? _c(
            "div",
            {
              staticClass: "avatar",
              attrs: { "data-cy": "headerAvatar" },
              on: { click: _vm.openProfile }
            },
            [
              _vm._t("avatar", [
                _c("Avatar", {
                  attrs: {
                    text: _vm.user.acronym,
                    "badge-variant": _vm.userBadgeVariant,
                    size: "48px",
                    "data-jest": "avatar"
                  },
                  scopedSlots: _vm._u(
                    [
                      _vm.userBadgeIcon
                        ? {
                            key: "avatar-badge-content",
                            fn: function() {
                              return [_c("i", { class: _vm.userBadgeIcon })]
                            },
                            proxy: true
                          }
                        : null
                    ],
                    null,
                    true
                  )
                })
              ])
            ],
            2
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.loggedUser()
        ? _c(
            "div",
            {
              staticClass: "profile-card",
              attrs: { "data-cy": "profileCard" }
            },
            [
              _c(
                "div",
                { staticClass: "profile-content" },
                [
                  _vm._t("profile-card", [
                    _c("header", [
                      _c(
                        "div",
                        { staticClass: "header" },
                        [
                          _c("div", { staticClass: "user-content" }, [
                            _c(
                              "div",
                              {
                                staticClass: "user-name",
                                attrs: { "data-cy": "profileCardUserName" }
                              },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(_vm.user.name) +
                                    "\n              "
                                )
                              ]
                            ),
                            _vm._v(" "),
                            _c(
                              "div",
                              {
                                staticClass: "user-role",
                                attrs: { "data-cy": "profileCardUserRole" }
                              },
                              [
                                _vm._v(
                                  "\n                " +
                                    _vm._s(_vm.user.role) +
                                    "\n              "
                                )
                              ]
                            )
                          ]),
                          _vm._v(" "),
                          _c("Avatar", {
                            attrs: {
                              text: _vm.user.acronym,
                              "badge-variant": _vm.userBadgeVariant,
                              size: "48px",
                              "data-jest": "avatar"
                            },
                            nativeOn: {
                              click: function($event) {
                                return _vm.closeProfile($event)
                              }
                            },
                            scopedSlots: _vm._u(
                              [
                                _vm.userBadgeIcon
                                  ? {
                                      key: "avatar-badge-content",
                                      fn: function() {
                                        return [
                                          _c("i", { class: _vm.userBadgeIcon })
                                        ]
                                      },
                                      proxy: true
                                    }
                                  : null
                              ],
                              null,
                              true
                            )
                          })
                        ],
                        1
                      )
                    ])
                  ]),
                  _vm._v(" "),
                  _vm._t("additional-profile-info"),
                  _vm._v(" "),
                  _vm._t("profile-list", [
                    _c("ListGroup", { attrs: { items: _vm.profileItems } })
                  ])
                ],
                2
              )
            ]
          )
        : _vm._e(),
      _vm._v(" "),
      _c(
        "a",
        { staticClass: "side-menu", on: { click: _vm.openSideMenu } },
        [
          _c("Icon", {
            style: _vm.getIconStyle,
            attrs: { icon: "me-icon icon-bars" }
          })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "side-menu" },
        [
          _vm.sideMenuOpened
            ? _vm._t(
                "side-menu",
                [
                  _c("div", { staticClass: "side-content" }, [
                    _c("header", [
                      _c("img", { attrs: { src: _vm.brand.logo, alt: "logo" } })
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      [
                        _c("Sitemap", {
                          attrs: { searchable: true, items: _vm.siteMap }
                        })
                      ],
                      1
                    )
                  ])
                ],
                { close: _vm.closeSideMenu }
              )
            : _vm._e()
        ],
        2
      ),
      _vm._v(" "),
      _c(
        "a",
        { staticClass: "start-menu", on: { click: _vm.toggleStartMenu } },
        [_c("Icon", { attrs: { icon: "me-icon icon-apps" } })],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "start-menu" },
        [
          _vm.showStartMenu
            ? _vm._t(
                "start-menu",
                [
                  _c("div", { staticClass: "start-content" }, [
                    _c("header", { style: _vm.getBackgroundStyle }, [
                      _c(
                        "a",
                        { on: { click: _vm.closeStartMenu } },
                        [_c("Icon", { attrs: { icon: "me-icon icon-apps" } })],
                        1
                      ),
                      _vm._v(" "),
                      _c("img", { attrs: { src: _vm.brand.logo, alt: "logo" } })
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      [
                        _c("Sitemap", {
                          attrs: { searchable: true, items: _vm.siteMap }
                        })
                      ],
                      1
                    )
                  ])
                ],
                { close: _vm.closeStartMenu }
              )
            : _vm._e()
        ],
        2
      ),
      _vm._v(" "),
      _c("div", { staticClass: "backdrop", on: { click: _vm.closeAll } })
    ]
  )
};
var __vue_staticRenderFns__$1F = [];
__vue_render__$1F._withStripped = true;

  /* style */
  const __vue_inject_styles__$1F = function (inject) {
    if (!inject) return
    inject("data-v-83b40f56_0", { source: "header.me-header[data-v-83b40f56] {\n  background-color: var(--me-primary-2);\n  display: flex;\n  position: relative;\n  z-index: 90;\n}\nheader.me-header > nav[data-v-83b40f56] {\n  display: flex;\n  height: 64px;\n  justify-content: space-between;\n  align-items: center;\n  padding-left: 16px;\n  padding-right: 16px;\n  width: 100%;\n}\nheader.me-header > nav .header-title[data-v-83b40f56] {\n  margin-left: 20px;\n  border-left: 1px solid #d5d5d5;\n  padding: 0 0 0 33px;\n}\nheader.me-header > nav .header-title h1[data-v-83b40f56] {\n  margin: 0;\n  padding: 0;\n}\n@media screen and (max-width: 768px) {\nheader.me-header > nav > section[data-v-83b40f56]:first-child {\n    display: none;\n}\n}\nheader.me-header > nav > section[data-v-83b40f56] {\n  display: flex;\n  align-items: center;\n}\nheader.me-header > nav > section > div.brand[data-v-83b40f56] {\n  align-items: center;\n  display: flex;\n  overflow: hidden;\n  padding: 12px 0;\n}\nheader.me-header > nav > section > div.brand img[data-v-83b40f56] {\n  max-height: 40px;\n}\nheader.me-header > nav > section > ul.nav-items[data-v-83b40f56] {\n  align-items: center;\n  display: flex;\n  height: 100%;\n  list-style: none;\n  margin: 0 70px 0 0;\n  padding: 0;\n}\nheader.me-header > nav > section > ul.nav-items > li[data-v-83b40f56] {\n  padding: 0 15px;\n  text-align: center;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56] {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  color: var(--me-white) !important;\n  cursor: pointer;\n  font-size: 12px;\n  position: relative;\n  padding: 12px 0 8px;\n  height: 64px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge {\n  position: absolute;\n  top: -14px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge span.badge {\n  font-size: 10px;\n  padding: 0px 2px;\n  line-height: initial;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge.badge-size .badge-text {\n  font-family: \"Open Sans\", \"Source Sans Pro\", \"Segoe UI\", \"Roboto\", \"Helvetica\", \"Arial\", sans-serif;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge.badge-size-1 {\n  right: -12px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge.badge-size-1 span.badge {\n  padding: 1px 5px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge.badge-size-2 {\n  right: -14px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge.badge-size-3 {\n  right: -16px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]  .me-badge.badge-size-4 {\n  right: -22px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a > i[data-v-83b40f56] {\n  position: relative;\n  font-size: 21px;\n  line-height: 1;\n  margin-bottom: 5px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a[data-v-83b40f56]:hover {\n  text-decoration: none;\n}\nheader.me-header > nav > section > ul.nav-items > li > a.active[data-v-83b40f56]::after {\n  background: currentColor;\n  border-top-left-radius: 3px;\n  border-top-right-radius: 3px;\n  bottom: 0;\n  content: \"\";\n  height: 4px;\n  left: 0;\n  position: absolute;\n  width: 100%;\n}\nheader.me-header > nav > section > ul.nav-items > li > div.separator[data-v-83b40f56] {\n  background: currentColor;\n  color: var(--me-white);\n  opacity: 0.32;\n  height: 40px;\n  width: 1px;\n}\nheader.me-header > nav > section > ul.nav-items > li.pressed[data-v-83b40f56] {\n  background-color: rgba(0, 0, 0, 0.2);\n}\nheader.me-header > div.avatar[data-v-83b40f56] {\n  align-items: center;\n  display: flex;\n  height: 64px;\n  padding: 0 15px;\n  position: absolute;\n  right: 0;\n  top: 0px;\n  transition-duration: 0.3s;\n  transition: all;\n}\nheader.me-header > div.avatar[data-v-83b40f56]:hover {\n  background-color: rgba(0, 0, 0, 0.2);\n  cursor: pointer;\n}\nheader.me-header > div.avatar[data-v-83b40f56]:hover  .me-avatar {\n  border: 2px solid var(--me-white);\n  box-shadow: var(--me-shadow-level-3);\n}\nheader.me-header > div.avatar[data-v-83b40f56]  .me-avatar {\n  background-color: rgba(0, 0, 0, 0.5);\n}\nheader.me-header > div.avatar[data-v-83b40f56]  .me-avatar span:not(.b-avatar-badge) {\n  text-transform: uppercase;\n  font-size: 24px !important;\n}\nheader.me-header > a.side-menu[data-v-83b40f56],\nheader.me-header a.start-menu[data-v-83b40f56] {\n  color: var(--me-white) !important;\n  display: none;\n  font-size: 21px;\n  height: 64px;\n  left: 0;\n  line-height: 64px;\n  position: absolute;\n  text-align: center;\n  top: 0px;\n  transition-duration: 0.2s;\n  transition: background-color;\n  width: 56px;\n}\nheader.me-header > a.side-menu[data-v-83b40f56]:hover, header.me-header > a.side-menu[data-v-83b40f56]:focus, header.me-header > a.side-menu[data-v-83b40f56]:active,\nheader.me-header a.start-menu[data-v-83b40f56]:hover,\nheader.me-header a.start-menu[data-v-83b40f56]:focus,\nheader.me-header a.start-menu[data-v-83b40f56]:active {\n  background-color: rgba(0, 0, 0, 0.1);\n  cursor: pointer;\n}\nheader.me-header > a.start-menu[data-v-83b40f56] {\n  display: none;\n}\nheader.me-header.start-menu[data-v-83b40f56] {\n  padding-left: 50px;\n}\nheader.me-header.start-menu > a.start-menu[data-v-83b40f56] {\n  display: block;\n}\nheader.me-header > div.side-menu[data-v-83b40f56] {\n  background: var(--me-white);\n  bottom: 0;\n  left: -250px;\n  position: fixed;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: left;\n  width: 250px;\n  z-index: 1050;\n}\nheader.me-header > div.side-menu > div.side-content > header[data-v-83b40f56] {\n  align-items: center;\n  background-color: var(--me-primary-2);\n  display: flex;\n  height: 64px;\n  overflow: hidden;\n  padding-left: 15px;\n}\nheader.me-header > div.side-menu > div.side-content > header > img[data-v-83b40f56] {\n  max-height: 50px;\n  max-width: 215px;\n}\nheader.me-header > div.side-menu > div.side-content > div[data-v-83b40f56] {\n  bottom: 0;\n  position: absolute;\n  top: 64px;\n  width: 100%;\n}\nheader.me-header > div.side-menu > div.side-content > div[data-v-83b40f56]  > .me-site-map {\n  width: 100%;\n}\nheader.me-header > div.side-menu > div.side-content > div[data-v-83b40f56]  > .me-site-map > div {\n  bottom: 0;\n  max-height: none;\n  overflow-y: auto;\n  position: absolute;\n  top: 40px;\n  width: 100%;\n}\nheader.me-header div.start-menu[data-v-83b40f56] {\n  background: var(--me-white);\n  bottom: 0;\n  box-shadow: var(--me-shadow-level-2);\n  display: none;\n  left: 0;\n  position: fixed;\n  top: 0;\n  width: 320px;\n  z-index: 1070;\n}\nheader.me-header div.start-menu > div.start-content > header[data-v-83b40f56] {\n  background-color: var(--me-primary-2);\n  border-bottom: 1px solid #eee;\n  height: 64px;\n  position: relative;\n}\nheader.me-header div.start-menu > div.start-content > header > a[data-v-83b40f56] {\n  color: var(--me-white);\n  font-size: 21px;\n  height: 64px;\n  left: 0;\n  line-height: 64px;\n  position: absolute;\n  text-align: center;\n  top: 0px;\n  transition-duration: 0.2s;\n  transition: background-color;\n  width: 50px;\n}\nheader.me-header div.start-menu > div.start-content > header > a[data-v-83b40f56]:hover, header.me-header div.start-menu > div.start-content > header > a[data-v-83b40f56]:focus, header.me-header div.start-menu > div.start-content > header > a[data-v-83b40f56]:active {\n  background-color: rgba(0, 0, 0, 0.1);\n  cursor: pointer;\n}\nheader.me-header div.start-menu > div.start-content > header > img[data-v-83b40f56] {\n  left: 60px;\n  max-height: 50px;\n  max-width: 215px;\n  position: absolute;\n  top: 17px;\n}\nheader.me-header div.start-menu > div.start-content > div[data-v-83b40f56] {\n  bottom: 0;\n  position: absolute;\n  top: 64px;\n  width: 100%;\n}\nheader.me-header div.start-menu > div.start-content > div[data-v-83b40f56]  > .me-site-map {\n  width: 100%;\n}\nheader.me-header div.start-menu > div.start-content > div[data-v-83b40f56]  > .me-site-map > div {\n  bottom: 0;\n  max-height: none;\n  overflow-y: auto;\n  position: absolute;\n  top: 40px;\n  width: 100%;\n}\nheader.me-header > div.backdrop[data-v-83b40f56] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1040;\n}\nheader.me-header > div.profile-card[data-v-83b40f56] {\n  background-color: var(--me-white);\n  display: none;\n  position: absolute;\n  right: 0;\n  top: 0;\n  user-select: none;\n  z-index: 1050;\n  border-bottom-left-radius: 8px;\n  overflow: hidden;\n  width: 256px;\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header[data-v-83b40f56] {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  height: 64px;\n  padding: 8px 16px;\n  gap: 8px;\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header > div.user-content[data-v-83b40f56] {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  width: 168px;\n  height: 48px;\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header > div.user-content > div.user-name[data-v-83b40f56] {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  font-size: 14px;\n  line-height: 22px;\n  color: var(--me-gray-3);\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header > div.user-content > div.user-role[data-v-83b40f56] {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-4);\n}\nheader.me-header > div.profile-card > div.profile-content > header[data-v-83b40f56]  .me-avatar {\n  background-color: var(--me-primary-2);\n  cursor: pointer;\n  position: absolute;\n  right: 15px;\n  top: 8px;\n}\nheader.me-header > div.profile-card > div.profile-content > header[data-v-83b40f56]  .me-avatar span:not(.b-avatar-badge) {\n  text-transform: uppercase;\n  font-size: 24px !important;\n}\nheader.me-header.side > div.side-menu[data-v-83b40f56] {\n  left: 0;\n}\nheader.me-header.side > div.backdrop[data-v-83b40f56] {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.start > div.start-menu[data-v-83b40f56] {\n  display: block;\n}\nheader.me-header.start > div.backdrop[data-v-83b40f56] {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.profile > div.profile-card[data-v-83b40f56] {\n  display: block;\n}\nheader.me-header.profile > div.backdrop[data-v-83b40f56] {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.sitemap > div.backdrop[data-v-83b40f56] {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.fixed-top[data-v-83b40f56] {\n  left: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 1030;\n}\nheader.me-header.box-shadow[data-v-83b40f56] {\n  box-shadow: var(--me-shadow-level-1);\n}\n@media screen and (max-width: 768px) {\nheader.me-header[data-v-83b40f56] {\n    padding-left: 50px;\n}\nheader.me-header > a.side-menu[data-v-83b40f56] {\n    display: block;\n}\nheader.me-header > a.start-menu[data-v-83b40f56] {\n    display: none !important;\n}\nheader.me-header > nav > section ul.nav-items[data-v-83b40f56] {\n    display: none;\n}\n}\nheader.me-header .not-logged-area[data-v-83b40f56] {\n  align-items: center;\n  display: flex;\n}\nheader.me-header .not-logged-area div[data-v-83b40f56], header.me-header .not-logged-area button[data-v-83b40f56], header.me-header .not-logged-area section[data-v-83b40f56] {\n  margin-left: 10px;\n}\nheader.me-header .not-logged-area .btn-outline-light a[data-v-83b40f56] {\n  color: var(--me-white);\n  text-decoration: none;\n}\nheader.me-header .not-logged-area .btn-outline-light:hover a[data-v-83b40f56] {\n  color: var(--me-gray-0);\n}\n\n/*# sourceMappingURL=Header.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Header.vue","Header.vue"],"names":[],"mappings":"AA6XA;EACA,qCAAA;EACA,aAAA;EACA,kBAAA;EACA,WAAA;AC5XA;AD8XA;EACA,aAAA;EACA,YAVA;EAWA,8BAAA;EACA,mBAAA;EACA,kBAAA;EACA,mBAAA;EACA,WAAA;AC5XA;AD8XA;EACA,iBAAA;EACA,8BAAA;EACA,mBAAA;AC5XA;AD8XA;EACA,SAAA;EACA,UAAA;AC5XA;ADgYA;AACA;IACA,aAAA;AC9XE;AACF;ADiYA;EACA,aAAA;EACA,mBAAA;AC/XA;ADiYA;EACA,mBAAA;EACA,aAAA;EACA,gBAAA;EACA,eAAA;AC/XA;ADiYA;EACA,gBAAA;AC/XA;ADmYA;EACA,mBAAA;EACA,aAAA;EACA,YAAA;EACA,gBAAA;EACA,kBAAA;EACA,UAAA;ACjYA;ADmYA;EACA,eAAA;EACA,kBAAA;ACjYA;ADmYA;EACA,aAAA;EACA,sBAAA;EACA,mBAAA;EACA,iCAAA;EACA,eAAA;EACA,eAAA;EACA,kBAAA;EACA,mBAAA;EACA,YAtEA;AC3TA;ADoYA;EACA,kBAAA;EACA,UAAA;AClYA;ADoYA;EACA,eAAA;EACA,gBAAA;EACA,oBAAA;AClYA;ADsYA;EACA,mGAAA;ACpYA;ADuYA;EACA,YAAA;ACrYA;ADuYA;EACA,gBAAA;ACrYA;ADyYA;EACA,YAAA;ACvYA;AD0YA;EACA,YAAA;ACxYA;AD2YA;EACA,YAAA;ACzYA;AD+YA;EACA,kBAAA;EACA,eAAA;EACA,cAAA;EACA,kBAAA;AC7YA;ADgZA;EACA,qBAAA;AC9YA;ADkZA;EACA,wBAAA;EACA,2BAAA;EACA,4BAAA;EACA,SAAA;EACA,WAAA;EACA,WAAA;EACA,OAAA;EACA,kBAAA;EACA,WAAA;AChZA;ADqZA;EACA,wBAAA;EACA,sBAAA;EACA,aAAA;EACA,YAAA;EACA,UAAA;ACnZA;ADsZA;EACA,oCAAA;ACpZA;AD2ZA;EACA,mBAAA;EACA,aAAA;EACA,YA5JA;EA6JA,eAAA;EACA,kBAAA;EACA,QAAA;EACA,QAAA;EACA,yBAAA;EACA,eAAA;ACzZA;AD2ZA;EACA,oCAAA;EACA,eAAA;ACzZA;AD2ZA;EACA,iCAAA;EACA,oCAAA;ACzZA;AD6ZA;EACA,oCAAA;AC3ZA;AD6ZA;EACA,yBAAA;EACA,0BAAA;AC3ZA;ADgaA;;EAEA,iCAAA;EACA,aAAA;EACA,eAAA;EACA,YA7LA;EA8LA,OAAA;EACA,iBA/LA;EAgMA,kBAAA;EACA,kBAAA;EACA,QAAA;EACA,yBAAA;EACA,4BAAA;EACA,WAAA;AC9ZA;ADgaA;;;;EAGA,oCAAA;EACA,eAAA;AC7ZA;ADiaA;EACA,aAAA;AC/ZA;ADkaA;EACA,kBAAA;AChaA;ADkaA;EACA,cAAA;AChaA;ADoaA;EACA,2BAAA;EACA,SAAA;EACA,YAAA;EACA,eAAA;EACA,MAAA;EACA,yBAAA;EACA,gBAAA;EACA,YAAA;EACA,aAAA;AClaA;ADqaA;EACA,mBAAA;EACA,qCAAA;EACA,aAAA;EACA,YA3OA;EA4OA,gBAAA;EACA,kBAAA;ACnaA;ADqaA;EACA,gBAAA;EACA,gBAAA;ACnaA;ADuaA;EACA,SAAA;EACA,kBAAA;EACA,SAxPA;EAyPA,WAAA;ACraA;ADuaA;EACA,WAAA;ACraA;ADuaA;EACA,SAAA;EACA,gBAAA;EACA,gBAAA;EACA,kBAAA;EACA,SAAA;EACA,WAAA;ACraA;AD4aA;EACA,2BAAA;EACA,SAAA;EACA,oCAAA;EACA,aAAA;EACA,OAAA;EACA,eAAA;EACA,MAAA;EACA,YAAA;EACA,aAAA;AC1aA;AD6aA;EACA,qCAAA;EACA,6BAAA;EACA,YA1RA;EA2RA,kBAAA;AC3aA;AD6aA;EACA,sBAAA;EACA,eAAA;EACA,YAhSA;EAiSA,OAAA;EACA,iBAlSA;EAmSA,kBAAA;EACA,kBAAA;EACA,QAAA;EACA,yBAAA;EACA,4BAAA;EACA,WAAA;AC3aA;AD6aA;EAGA,oCAAA;EACA,eAAA;AC7aA;ADibA;EACA,UAAA;EACA,gBAAA;EACA,gBAAA;EACA,kBAAA;EACA,SAAA;AC/aA;ADmbA;EACA,SAAA;EACA,kBAAA;EACA,SA9TA;EA+TA,WAAA;ACjbA;ADmbA;EACA,WAAA;ACjbA;ADmbA;EACA,SAAA;EACA,gBAAA;EACA,gBAAA;EACA,kBAAA;EACA,SAAA;EACA,WAAA;ACjbA;ADwbA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,yBAAA;EACA,mBAAA;EACA,aAAA;ACtbA;ADybA;EACA,iCAAA;EACA,aAAA;EACA,kBAAA;EACA,QAAA;EACA,MAAA;EACA,iBAAA;EACA,aAAA;EACA,8BAAA;EACA,gBAAA;EACA,YAAA;ACvbA;AD2bA;EACA,aAAA;EACA,sBAAA;EACA,uBAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;EACA,YAAA;EACA,iBAAA;EACA,QAAA;ACzbA;AD2bA;EACA,aAAA;EACA,sBAAA;EACA,uBAAA;EACA,YAAA;EACA,YAAA;ACzbA;AD2bA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;ACzbA;AD4bA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;AC1bA;AD+bA;EACA,qCAAA;EACA,eAAA;EACA,kBAAA;EACA,WAAA;EACA,QAAA;AC7bA;AD+bA;EACA,yBAAA;EACA,0BAAA;AC7bA;ADqcA;EACA,OAAA;ACncA;ADscA;EACA,cAAA;EACA,YAAA;ACpcA;ADycA;EACA,cAAA;ACvcA;AD0cA;EACA,cAAA;EACA,YAAA;ACxcA;AD6cA;EACA,cAAA;AC3cA;AD8cA;EACA,cAAA;EACA,YAAA;AC5cA;ADidA;EACA,cAAA;EACA,YAAA;AC/cA;ADmdA;EACA,OAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,aAAA;ACjdA;ADodA;EACA,oCAAA;ACldA;ADqdA;AArdA;IAsdA,kBAAA;ACldE;ADodF;IACA,cAAA;ACldE;ADqdF;IACA,wBAAA;ACndE;ADwdF;IACA,aAAA;ACtdE;AACF;AD2dA;EACA,mBAAA;EACA,aAAA;ACzdA;AD2dA;EACA,iBAAA;ACzdA;AD6dA;EACA,sBAAA;EACA,qBAAA;AC3dA;AD8dA;EACA,uBAAA;AC5dA;;AAEA,qCAAqC","file":"Header.vue","sourcesContent":["<template>\n  <header\n    :id=\"'header-' + internalId\"\n    :class=\"{\n      side: sideMenuOpened,\n      profile: profileOpened,\n      start: startMenuOpened,\n      'box-shadow': shadow,\n      'fixed-top': fixedTop,\n      'start-menu': showStartMenu,\n      'sitemap': siteMapOpened\n    }\"\n    :style=\"getBackgroundStyle\"\n    class=\"me-header\"\n    data-cy=\"header\"\n  >\n    <nav>\n      <section>\n        <slot name=\"left\">\n          <div class=\"brand\">\n            <a\n              :href=\"brand.link || '#'\"\n              :target=\"brand.newTab ? '_blank' : '_self'\"\n            >\n              <img :src=\"brand.logo\" alt=\"logo\">\n            </a>\n          </div>\n\n          <div v-if=\"hasTitle()\" class=\"header-title\">\n            <slot name=\"header-title\">\n              <h1 :style=\"getHeaderTitleStyle\">\n                {{ headerTitle }}\n              </h1>\n            </slot>\n          </div>\n        </slot>\n      </section>\n\n      <section>\n        <slot name=\"center\" />\n      </section>\n\n      <section v-if=\"loggedUser() && !$slots['header-title']\">\n        <slot name=\"right\">\n          <ul class=\"nav-items\">\n            <li v-for=\"(item, index) in navItems\" :key=\"index\" :class=\"{ pressed: (siteMapOpened && item.siteMap) }\">\n              <div\n                v-if=\"item.separator\"\n                v-bind=\"getNavItemProps()\"\n                class=\"separator\"\n              />\n\n              <a\n                v-if=\"(item.url || item.click) && !item.siteMap\"\n                v-bind=\"getNavItemProps(item)\"\n                @click=\"item.click ? item.click() : null\"\n                @mouseenter=\"handleNavItemEvent('hover', item)\"\n                @mouseleave=\"handleNavItemEvent('leave', item)\"\n              >\n                <Icon :icon=\"item.icon\">\n                  <template v-if=\"item.badge && item.badge.text\">\n                    <Badge\n                      :class=\"[`badge-size-${item.badge.text.toString().length}`]\"\n                      class=\"badge-size\"\n                      appearance=\"danger\"\n                    >\n                      <Icon v-if=\"item.badge.icon\" :icon=\"item.badge.icon\" />\n                      <span v-else class=\"badge-text\">{{ item.badge.text }}</span>\n                    </Badge>\n                  </template>\n                </Icon>\n\n                <span> {{ item.label }} </span>\n              </a>\n\n              <a\n                v-if=\"item.siteMap\"\n                :id=\"'popover-' + internalId\"\n                :style=\"getIconStyle\"\n                @click=\"item.click ? item.click() : null\"\n              >\n                <Icon :icon=\"item.icon\" />\n                <span> {{ item.label }} </span>\n              </a>\n              <Popover\n                v-if=\"item.siteMap\"\n                :target=\"'popover-' + internalId\"\n                :container=\"'header-' + internalId\"\n                :show.sync=\"siteMapOpened\"\n                placement=\"bottom\"\n                triggers=\"click\"\n              >\n                <Sitemap :searchable=\"true\" :items=\"siteMap\" caret=\"me-icon-s icon-caret-down\" left />\n              </Popover>\n            </li>\n          </ul>\n        </slot>\n      </section>\n\n      <div v-if=\"!loggedUser()\" class=\"not-logged-area\">\n        <slot name=\"not-logged\" />\n        <Button :href=\"`${baseURL.origin}/do/Login.mvc/LoginNew?Dest=${baseURL.pathname}`\" variant=\"outline-light\">\n          Login\n        </Button>\n      </div>\n    </nav>\n\n    <div v-if=\"loggedUser()\" @click=\"openProfile\" class=\"avatar\" data-cy=\"headerAvatar\">\n      <slot name=\"avatar\">\n        <Avatar\n          :text=\"user.acronym\"\n          :badge-variant=\"userBadgeVariant\"\n          size=\"48px\"\n          data-jest=\"avatar\"\n        >\n          <template v-if=\"userBadgeIcon\" v-slot:avatar-badge-content>\n            <i :class=\"userBadgeIcon\" />\n          </template>\n        </Avatar>\n      </slot>\n    </div>\n    <div v-if=\"loggedUser()\" class=\"profile-card\" data-cy=\"profileCard\">\n      <div class=\"profile-content\">\n        <slot name=\"profile-card\">\n          <header>\n            <div class=\"header\">\n              <div class=\"user-content\">\n                <div class=\"user-name\" data-cy=\"profileCardUserName\">\n                  {{ user.name }}\n                </div>\n                <div class=\"user-role\" data-cy=\"profileCardUserRole\">\n                  {{ user.role }}\n                </div>\n              </div>\n\n              <Avatar\n                :text=\"user.acronym\"\n                :badge-variant=\"userBadgeVariant\"\n                @click.native=\"closeProfile\"\n                size=\"48px\"\n                data-jest=\"avatar\"\n              >\n                <template v-if=\"userBadgeIcon\" v-slot:avatar-badge-content>\n                  <i :class=\"userBadgeIcon\" />\n                </template>\n              </Avatar>\n            </div>\n          </header>\n        </slot>\n\n        <slot name=\"additional-profile-info\" />\n\n        <slot name=\"profile-list\">\n          <ListGroup :items=\"profileItems\" />\n        </slot>\n      </div>\n    </div>\n\n    <a @click=\"openSideMenu\" class=\"side-menu\">\n      <Icon :style=\"getIconStyle\" icon=\"me-icon icon-bars\" />\n    </a>\n    <div class=\"side-menu\">\n      <slot v-if=\"sideMenuOpened\" :close=\"closeSideMenu\" name=\"side-menu\">\n        <div class=\"side-content\">\n          <header>\n            <img :src=\"brand.logo\" alt=\"logo\">\n          </header>\n          <div>\n            <Sitemap :searchable=\"true\" :items=\"siteMap\" />\n          </div>\n        </div>\n      </slot>\n    </div>\n    <a @click=\"toggleStartMenu\" class=\"start-menu\">\n      <Icon icon=\"me-icon icon-apps\" />\n    </a>\n    <div class=\"start-menu\">\n      <slot v-if=\"showStartMenu\" :close=\"closeStartMenu\" name=\"start-menu\">\n        <div class=\"start-content\">\n          <header :style=\"getBackgroundStyle\">\n            <a @click=\"closeStartMenu\">\n              <Icon icon=\"me-icon icon-apps\" />\n            </a>\n            <img :src=\"brand.logo\" alt=\"logo\">\n          </header>\n          <div>\n            <Sitemap :searchable=\"true\" :items=\"siteMap\" />\n          </div>\n        </div>\n      </slot>\n    </div>\n    <div @click=\"closeAll\" class=\"backdrop\" />\n  </header>\n</template>\n\n<script>\nimport Avatar from './Avatar.vue'\nimport Badge from './Badge.vue'\nimport ListGroup from './ListGroup.vue'\nimport Sitemap from './Sitemap.vue'\nimport Icon from './Icon.vue'\nimport Popover from './Popover.vue'\nimport Button from './Button.vue'\n\nexport default {\n  name: 'MeHeader',\n  components: {\n    Avatar,\n    Badge,\n    ListGroup,\n    Sitemap,\n    Icon,\n    Popover,\n    Button\n  },\n  props: {\n    brand: {\n      type: Object,\n      required: true\n    },\n    user: {\n      type: Object,\n      required: true\n    },\n    profileItems: {\n      type: Array,\n      required: true\n    },\n    navItems: {\n      type: Array,\n      default() {\n        return []\n      }\n    },\n    siteMap: {\n      type: Array,\n      default() {\n        return []\n      }\n    },\n    fixedTop: {\n      type: Boolean,\n      default: false\n    },\n    showStartMenu: {\n      type: Boolean,\n      default: false\n    },\n    shadow: {\n      type: Boolean,\n      default: false\n    },\n    headerTitle: {\n      type: String,\n      default: ''\n    }\n  },\n  data() {\n    return {\n      startMenuOpened: false,\n      sideMenuOpened: false,\n      profileOpened: false,\n      siteMapOpened: false,\n      internalId: null,\n      baseURL: window.location\n    }\n  },\n  computed: {\n    userBadgeIcon() {\n      return this.user.badge?.icon || ''\n    },\n    userBadgeVariant() {\n      return this.user.badge?.variant || ''\n    },\n    getBackgroundStyle() {\n      let bg = ''\n\n      if (this.brand.background) {\n        if (this.brand.background.mainImage && this.brand.background.repeatImage) {\n          bg = `url(${this.brand.background.mainImage}) no-repeat, url(${this.brand.background.repeatImage}) repeat-x`\n        } else if (this.brand.background.mainImage && !this.brand.background.repeatImage) {\n          bg = `url(${this.brand.background.mainImage}) ${this.brand.background.primaryColor || ''} no-repeat`\n        } else if (!this.brand.background.mainImage && this.brand.background.repeatImage) {\n          bg = `url(${this.brand.background.repeatImage}) repeat-x`\n        } else if (!this.brand.background.secondaryColor) {\n          bg = this.brand.background.primaryColor\n        } else {\n          bg = `linear-gradient(to right, ${this.brand.background.primaryColor}, ${this.brand.background.secondaryColor})`\n        }\n      }\n      return {\n        background: bg,\n        borderBottom: `1px solid ${this.brand.iconColor}`\n      }\n    },\n    getIconStyle() {\n      let color = ''\n      if (this.brand.iconColor) {\n        color = `${this.brand.iconColor} !important`\n      }\n      return {\n        color: color\n      }\n    },\n\n    getHeaderTitleStyle() {\n      return this.getIconStyle.color ? this.getIconStyle : { color: '#fff' }\n    }\n  },\n  created() {\n    this.internalId = new Date().getTime()\n  },\n  methods: {\n    getNavItemProps(item = {}) {\n      const { url: href, target = '_self' } = item\n\n      const navItemProps = {\n        class: item.active ? 'active' : '',\n        style: this.getIconStyle\n      }\n\n      if (href) {\n        return { ...navItemProps, href, target }\n      }\n\n      return navItemProps\n    },\n    handleNavItemEvent(eventName, item) {\n      if (item[eventName]) {\n        item[eventName]()\n      }\n    },\n    toggleStartMenu() {\n      this.startMenuOpened = !this.startMenuOpened\n    },\n\n    closeStartMenu() {\n      this.startMenuOpened = false\n    },\n\n    openProfile() {\n      this.profileOpened = true\n    },\n\n    closeProfile() {\n      this.profileOpened = false\n    },\n\n    openSideMenu() {\n      this.sideMenuOpened = true\n    },\n\n    closeSideMenu() {\n      this.sideMenuOpened = false\n    },\n\n    closeSitemap() {\n      this.siteMapOpened = false\n    },\n\n    closeAll() {\n      this.closeSideMenu()\n      this.closeProfile()\n      this.closeStartMenu()\n      this.closeSitemap()\n    },\n\n    loggedUser() {\n      return Object.keys(this.user).length\n    },\n\n    hasTitle() {\n      return this.headerTitle || this.$slots['header-title']\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $header-height: 64px;\n\n  header.me-header {\n    background-color: var(--me-primary-2);\n    display: flex;\n    position: relative;\n    z-index: 90;\n\n    > nav {\n      display: flex;\n      height: $header-height;\n      justify-content: space-between;\n      align-items: center;\n      padding-left: 16px;\n      padding-right: 16px;\n      width: 100%;\n\n      .header-title {\n         margin-left: 20px;\n         border-left: 1px solid #d5d5d5;\n         padding: 0 0 0 33px;\n\n         h1 {\n            margin: 0;\n            padding: 0;\n         }\n      }\n\n      @media screen and (max-width: \"768px\") {\n        > section:first-child {\n          display: none;\n        }\n      }\n\n      > section {\n        display: flex;\n        align-items: center;\n\n        > div.brand {\n          align-items: center;\n          display: flex;\n          overflow: hidden;\n          padding: 12px 0;\n\n          img {\n            max-height: 40px;\n          }\n        }\n\n        > ul.nav-items {\n          align-items: center;\n          display: flex;\n          height: 100%;\n          list-style: none;\n          margin: 0 70px 0 0;\n          padding: 0;\n\n          > li {\n            padding: 0 15px;\n            text-align: center;\n\n            > a {\n              display: flex;\n              flex-direction: column;\n              align-items: center;\n              color: var(--me-white) !important;\n              cursor: pointer;\n              font-size: 12px;\n              position: relative;\n              padding: 12px 0 8px;\n              height: $header-height;\n\n              ::v-deep {\n                .me-badge {\n                  position: absolute;\n                  top: -14px;\n\n                  span.badge {\n                    font-size: 10px;\n                    padding: 0px 2px;\n                    line-height: initial;\n                  }\n\n                  &.badge-size {\n                    .badge-text {\n                      font-family: 'Open Sans', 'Source Sans Pro', 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;\n                    }\n\n                    &-1 {\n                      right: -12px;\n\n                      span.badge {\n                        padding: 1px 5px;\n                      }\n                    }\n\n                    &-2 {\n                      right: -14px;\n                    }\n\n                    &-3 {\n                      right: -16px;\n                    }\n\n                    &-4 {\n                      right: -22px;\n                    }\n                  }\n                }\n              }\n\n              > i {\n                position: relative;\n                font-size: 21px;\n                line-height: 1;\n                margin-bottom: 5px;\n              }\n\n              &:hover {\n                text-decoration: none;\n              }\n\n              &.active {\n                &::after {\n                  background: currentColor;\n                  border-top-left-radius: 3px;\n                  border-top-right-radius: 3px;\n                  bottom: 0;\n                  content: \"\";\n                  height: 4px;\n                  left: 0;\n                  position: absolute;\n                  width: 100%;\n                }\n              }\n            }\n\n            > div.separator {\n              background: currentColor;\n              color: var(--me-white);\n              opacity: .32;\n              height: 40px;\n              width: 1px;\n            }\n\n            &.pressed {\n              background-color: rgba(0, 0, 0, 0.2);\n            }\n          }\n        }\n      }\n    }\n\n    > div.avatar {\n      align-items: center;\n      display: flex;\n      height: $header-height;\n      padding: 0 15px;\n      position: absolute;\n      right: 0;\n      top: 0px;\n      transition-duration: 0.3s;\n      transition: all;\n\n      &:hover {\n        background-color: rgba(0, 0, 0, 0.2);\n        cursor: pointer;\n\n        ::v-deep .me-avatar {\n          border: 2px solid var(--me-white);\n          box-shadow: var(--me-shadow-level-3);\n        }\n      }\n\n      ::v-deep .me-avatar {\n        background-color: rgba(0, 0, 0, 0.5);\n\n        span:not(.b-avatar-badge) {\n          text-transform: uppercase;\n          font-size: 24px!important;\n        }\n      }\n    }\n\n    > a.side-menu,\n    a.start-menu {\n      color: var(--me-white) !important;\n      display: none;\n      font-size: 21px;\n      height: $header-height;\n      left: 0;\n      line-height: $header-height;\n      position: absolute;\n      text-align: center;\n      top: 0px;\n      transition-duration: 0.2s;\n      transition: background-color;\n      width: 56px;\n\n      &:hover,\n      &:focus,\n      &:active {\n        background-color: rgba(0, 0, 0, 0.1);\n        cursor: pointer;\n      }\n    }\n\n    > a.start-menu {\n      display: none;\n    }\n\n    &.start-menu {\n      padding-left: 50px;\n\n      > a.start-menu {\n        display: block;\n      }\n    }\n\n    > div.side-menu {\n      background: var(--me-white);\n      bottom: 0;\n      left: -250px;\n      position: fixed;\n      top: 0;\n      transition-duration: 0.2s;\n      transition: left;\n      width: 250px;\n      z-index: 1050;\n\n      > div.side-content {\n        > header {\n          align-items: center;\n          background-color: var(--me-primary-2);\n          display: flex;\n          height: $header-height;\n          overflow: hidden;\n          padding-left: 15px;\n\n          > img {\n            max-height: 50px;\n            max-width: 215px;\n          }\n        }\n\n        > div {\n          bottom: 0;\n          position: absolute;\n          top: $header-height;\n          width: 100%;\n\n          ::v-deep > .me-site-map {\n            width: 100%;\n\n            > div {\n              bottom: 0;\n              max-height: none;\n              overflow-y: auto;\n              position: absolute;\n              top: 40px;\n              width: 100%;\n            }\n          }\n        }\n      }\n    }\n\n    div.start-menu {\n      background: var(--me-white);\n      bottom: 0;\n      box-shadow: var(--me-shadow-level-2);\n      display: none;\n      left: 0;\n      position: fixed;\n      top: 0;\n      width: 320px;\n      z-index: 1070;\n\n      > div.start-content {\n        > header {\n          background-color: var(--me-primary-2);\n          border-bottom: 1px solid #eee;\n          height: $header-height;\n          position: relative;\n\n          > a {\n            color: var(--me-white);\n            font-size: 21px;\n            height: $header-height;\n            left: 0;\n            line-height: $header-height;\n            position: absolute;\n            text-align: center;\n            top: 0px;\n            transition-duration: 0.2s;\n            transition: background-color;\n            width: 50px;\n\n            &:hover,\n            &:focus,\n            &:active {\n              background-color: rgba(0, 0, 0, 0.1);\n              cursor: pointer;\n            }\n          }\n\n          > img {\n            left: 60px;\n            max-height: 50px;\n            max-width: 215px;\n            position: absolute;\n            top: 17px;\n          }\n        }\n\n        > div {\n          bottom: 0;\n          position: absolute;\n          top: $header-height;\n          width: 100%;\n\n          ::v-deep > .me-site-map {\n            width: 100%;\n\n            > div {\n              bottom: 0;\n              max-height: none;\n              overflow-y: auto;\n              position: absolute;\n              top: 40px;\n              width: 100%;\n            }\n          }\n        }\n      }\n    }\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      transition-duration: 0.2s;\n      transition: opacity;\n      z-index: 1040;\n    }\n\n    > div.profile-card {\n      background-color: var(--me-white);\n      display: none;\n      position: absolute;\n      right: 0;\n      top: 0;\n      user-select: none;\n      z-index: 1050;\n      border-bottom-left-radius: 8px;\n      overflow: hidden;\n      width: 256px;\n\n      > div.profile-content {\n        > header {\n          > div.header {\n            display: flex;\n            flex-direction: column;\n            justify-content: center;\n            overflow: hidden;\n            text-overflow: ellipsis;\n            white-space: nowrap;\n            height: 64px;\n            padding: 8px 16px;\n            gap: 8px;\n\n            > div.user-content {\n              display: flex;\n              flex-direction: column;\n              justify-content: center;\n              width: 168px;\n              height: 48px;\n\n              > div.user-name {\n                overflow: hidden;\n                text-overflow: ellipsis;\n                white-space: nowrap;\n                font-size: 14px;\n                line-height: 22px;\n                color: var(--me-gray-3);\n              }\n\n              > div.user-role {\n                overflow: hidden;\n                text-overflow: ellipsis;\n                white-space: nowrap;\n                font-size: 12px;\n                line-height: 16px;\n                color: var(--me-gray-4 );\n              }\n            }\n          }\n\n          ::v-deep .me-avatar {\n            background-color: var(--me-primary-2);\n            cursor: pointer;\n            position: absolute;\n            right: 15px;\n            top: 8px;\n\n            span:not(.b-avatar-badge) {\n              text-transform: uppercase;\n              font-size: 24px!important;\n            }\n          }\n         }\n      }\n    }\n\n    &.side {\n      > div.side-menu {\n        left: 0;\n      }\n\n      > div.backdrop {\n        display: block;\n        opacity: 0.4;\n      }\n    }\n\n    &.start {\n      > div.start-menu {\n        display: block;\n      }\n\n      > div.backdrop {\n        display: block;\n        opacity: 0.4;\n      }\n    }\n\n    &.profile {\n      > div.profile-card {\n        display: block;\n      }\n\n      > div.backdrop {\n        display: block;\n        opacity: 0.4;\n      }\n    }\n\n    &.sitemap {\n      > div.backdrop {\n        display: block;\n        opacity: 0.4;\n      }\n    }\n\n    &.fixed-top {\n      left: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      z-index: 1030;\n    }\n\n    &.box-shadow {\n      box-shadow: var(--me-shadow-level-1);\n    }\n\n    @media screen and (max-width: \"768px\") {\n      padding-left: 50px;\n\n      > a.side-menu {\n        display: block;\n      }\n\n      > a.start-menu {\n        display: none!important;\n      }\n\n      > nav {\n        > section {\n          ul.nav-items {\n            display: none;\n          }\n        }\n      }\n    }\n\n    .not-logged-area {\n      align-items: center;\n      display: flex;\n\n      div, button, section {\n        margin-left: 10px;\n      }\n\n      .btn-outline-light {\n        a {\n          color: var(--me-white);\n          text-decoration: none;\n        }\n\n        &:hover a {\n          color: var(--me-gray-0);\n        }\n      }\n    }\n  }\n</style>\n","header.me-header {\n  background-color: var(--me-primary-2);\n  display: flex;\n  position: relative;\n  z-index: 90;\n}\nheader.me-header > nav {\n  display: flex;\n  height: 64px;\n  justify-content: space-between;\n  align-items: center;\n  padding-left: 16px;\n  padding-right: 16px;\n  width: 100%;\n}\nheader.me-header > nav .header-title {\n  margin-left: 20px;\n  border-left: 1px solid #d5d5d5;\n  padding: 0 0 0 33px;\n}\nheader.me-header > nav .header-title h1 {\n  margin: 0;\n  padding: 0;\n}\n@media screen and (max-width: 768px) {\n  header.me-header > nav > section:first-child {\n    display: none;\n  }\n}\nheader.me-header > nav > section {\n  display: flex;\n  align-items: center;\n}\nheader.me-header > nav > section > div.brand {\n  align-items: center;\n  display: flex;\n  overflow: hidden;\n  padding: 12px 0;\n}\nheader.me-header > nav > section > div.brand img {\n  max-height: 40px;\n}\nheader.me-header > nav > section > ul.nav-items {\n  align-items: center;\n  display: flex;\n  height: 100%;\n  list-style: none;\n  margin: 0 70px 0 0;\n  padding: 0;\n}\nheader.me-header > nav > section > ul.nav-items > li {\n  padding: 0 15px;\n  text-align: center;\n}\nheader.me-header > nav > section > ul.nav-items > li > a {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  color: var(--me-white) !important;\n  cursor: pointer;\n  font-size: 12px;\n  position: relative;\n  padding: 12px 0 8px;\n  height: 64px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge {\n  position: absolute;\n  top: -14px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge span.badge {\n  font-size: 10px;\n  padding: 0px 2px;\n  line-height: initial;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge.badge-size .badge-text {\n  font-family: \"Open Sans\", \"Source Sans Pro\", \"Segoe UI\", \"Roboto\", \"Helvetica\", \"Arial\", sans-serif;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge.badge-size-1 {\n  right: -12px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge.badge-size-1 span.badge {\n  padding: 1px 5px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge.badge-size-2 {\n  right: -14px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge.badge-size-3 {\n  right: -16px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a ::v-deep .me-badge.badge-size-4 {\n  right: -22px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a > i {\n  position: relative;\n  font-size: 21px;\n  line-height: 1;\n  margin-bottom: 5px;\n}\nheader.me-header > nav > section > ul.nav-items > li > a:hover {\n  text-decoration: none;\n}\nheader.me-header > nav > section > ul.nav-items > li > a.active::after {\n  background: currentColor;\n  border-top-left-radius: 3px;\n  border-top-right-radius: 3px;\n  bottom: 0;\n  content: \"\";\n  height: 4px;\n  left: 0;\n  position: absolute;\n  width: 100%;\n}\nheader.me-header > nav > section > ul.nav-items > li > div.separator {\n  background: currentColor;\n  color: var(--me-white);\n  opacity: 0.32;\n  height: 40px;\n  width: 1px;\n}\nheader.me-header > nav > section > ul.nav-items > li.pressed {\n  background-color: rgba(0, 0, 0, 0.2);\n}\nheader.me-header > div.avatar {\n  align-items: center;\n  display: flex;\n  height: 64px;\n  padding: 0 15px;\n  position: absolute;\n  right: 0;\n  top: 0px;\n  transition-duration: 0.3s;\n  transition: all;\n}\nheader.me-header > div.avatar:hover {\n  background-color: rgba(0, 0, 0, 0.2);\n  cursor: pointer;\n}\nheader.me-header > div.avatar:hover ::v-deep .me-avatar {\n  border: 2px solid var(--me-white);\n  box-shadow: var(--me-shadow-level-3);\n}\nheader.me-header > div.avatar ::v-deep .me-avatar {\n  background-color: rgba(0, 0, 0, 0.5);\n}\nheader.me-header > div.avatar ::v-deep .me-avatar span:not(.b-avatar-badge) {\n  text-transform: uppercase;\n  font-size: 24px !important;\n}\nheader.me-header > a.side-menu,\nheader.me-header a.start-menu {\n  color: var(--me-white) !important;\n  display: none;\n  font-size: 21px;\n  height: 64px;\n  left: 0;\n  line-height: 64px;\n  position: absolute;\n  text-align: center;\n  top: 0px;\n  transition-duration: 0.2s;\n  transition: background-color;\n  width: 56px;\n}\nheader.me-header > a.side-menu:hover, header.me-header > a.side-menu:focus, header.me-header > a.side-menu:active,\nheader.me-header a.start-menu:hover,\nheader.me-header a.start-menu:focus,\nheader.me-header a.start-menu:active {\n  background-color: rgba(0, 0, 0, 0.1);\n  cursor: pointer;\n}\nheader.me-header > a.start-menu {\n  display: none;\n}\nheader.me-header.start-menu {\n  padding-left: 50px;\n}\nheader.me-header.start-menu > a.start-menu {\n  display: block;\n}\nheader.me-header > div.side-menu {\n  background: var(--me-white);\n  bottom: 0;\n  left: -250px;\n  position: fixed;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: left;\n  width: 250px;\n  z-index: 1050;\n}\nheader.me-header > div.side-menu > div.side-content > header {\n  align-items: center;\n  background-color: var(--me-primary-2);\n  display: flex;\n  height: 64px;\n  overflow: hidden;\n  padding-left: 15px;\n}\nheader.me-header > div.side-menu > div.side-content > header > img {\n  max-height: 50px;\n  max-width: 215px;\n}\nheader.me-header > div.side-menu > div.side-content > div {\n  bottom: 0;\n  position: absolute;\n  top: 64px;\n  width: 100%;\n}\nheader.me-header > div.side-menu > div.side-content > div ::v-deep > .me-site-map {\n  width: 100%;\n}\nheader.me-header > div.side-menu > div.side-content > div ::v-deep > .me-site-map > div {\n  bottom: 0;\n  max-height: none;\n  overflow-y: auto;\n  position: absolute;\n  top: 40px;\n  width: 100%;\n}\nheader.me-header div.start-menu {\n  background: var(--me-white);\n  bottom: 0;\n  box-shadow: var(--me-shadow-level-2);\n  display: none;\n  left: 0;\n  position: fixed;\n  top: 0;\n  width: 320px;\n  z-index: 1070;\n}\nheader.me-header div.start-menu > div.start-content > header {\n  background-color: var(--me-primary-2);\n  border-bottom: 1px solid #eee;\n  height: 64px;\n  position: relative;\n}\nheader.me-header div.start-menu > div.start-content > header > a {\n  color: var(--me-white);\n  font-size: 21px;\n  height: 64px;\n  left: 0;\n  line-height: 64px;\n  position: absolute;\n  text-align: center;\n  top: 0px;\n  transition-duration: 0.2s;\n  transition: background-color;\n  width: 50px;\n}\nheader.me-header div.start-menu > div.start-content > header > a:hover, header.me-header div.start-menu > div.start-content > header > a:focus, header.me-header div.start-menu > div.start-content > header > a:active {\n  background-color: rgba(0, 0, 0, 0.1);\n  cursor: pointer;\n}\nheader.me-header div.start-menu > div.start-content > header > img {\n  left: 60px;\n  max-height: 50px;\n  max-width: 215px;\n  position: absolute;\n  top: 17px;\n}\nheader.me-header div.start-menu > div.start-content > div {\n  bottom: 0;\n  position: absolute;\n  top: 64px;\n  width: 100%;\n}\nheader.me-header div.start-menu > div.start-content > div ::v-deep > .me-site-map {\n  width: 100%;\n}\nheader.me-header div.start-menu > div.start-content > div ::v-deep > .me-site-map > div {\n  bottom: 0;\n  max-height: none;\n  overflow-y: auto;\n  position: absolute;\n  top: 40px;\n  width: 100%;\n}\nheader.me-header > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1040;\n}\nheader.me-header > div.profile-card {\n  background-color: var(--me-white);\n  display: none;\n  position: absolute;\n  right: 0;\n  top: 0;\n  user-select: none;\n  z-index: 1050;\n  border-bottom-left-radius: 8px;\n  overflow: hidden;\n  width: 256px;\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  height: 64px;\n  padding: 8px 16px;\n  gap: 8px;\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header > div.user-content {\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  width: 168px;\n  height: 48px;\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header > div.user-content > div.user-name {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  font-size: 14px;\n  line-height: 22px;\n  color: var(--me-gray-3);\n}\nheader.me-header > div.profile-card > div.profile-content > header > div.header > div.user-content > div.user-role {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-4);\n}\nheader.me-header > div.profile-card > div.profile-content > header ::v-deep .me-avatar {\n  background-color: var(--me-primary-2);\n  cursor: pointer;\n  position: absolute;\n  right: 15px;\n  top: 8px;\n}\nheader.me-header > div.profile-card > div.profile-content > header ::v-deep .me-avatar span:not(.b-avatar-badge) {\n  text-transform: uppercase;\n  font-size: 24px !important;\n}\nheader.me-header.side > div.side-menu {\n  left: 0;\n}\nheader.me-header.side > div.backdrop {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.start > div.start-menu {\n  display: block;\n}\nheader.me-header.start > div.backdrop {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.profile > div.profile-card {\n  display: block;\n}\nheader.me-header.profile > div.backdrop {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.sitemap > div.backdrop {\n  display: block;\n  opacity: 0.4;\n}\nheader.me-header.fixed-top {\n  left: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 1030;\n}\nheader.me-header.box-shadow {\n  box-shadow: var(--me-shadow-level-1);\n}\n@media screen and (max-width: 768px) {\n  header.me-header {\n    padding-left: 50px;\n  }\n  header.me-header > a.side-menu {\n    display: block;\n  }\n  header.me-header > a.start-menu {\n    display: none !important;\n  }\n  header.me-header > nav > section ul.nav-items {\n    display: none;\n  }\n}\nheader.me-header .not-logged-area {\n  align-items: center;\n  display: flex;\n}\nheader.me-header .not-logged-area div, header.me-header .not-logged-area button, header.me-header .not-logged-area section {\n  margin-left: 10px;\n}\nheader.me-header .not-logged-area .btn-outline-light a {\n  color: var(--me-white);\n  text-decoration: none;\n}\nheader.me-header .not-logged-area .btn-outline-light:hover a {\n  color: var(--me-gray-0);\n}\n\n/*# sourceMappingURL=Header.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1F = "data-v-83b40f56";
  /* module identifier */
  const __vue_module_identifier__$1F = undefined;
  /* functional template */
  const __vue_is_functional_template__$1F = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1F, staticRenderFns: __vue_staticRenderFns__$1F },
    __vue_inject_styles__$1F,
    __vue_script__$1F,
    __vue_scope_id__$1F,
    __vue_is_functional_template__$1F,
    __vue_module_identifier__$1F,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1G = {
  name: 'List',

  components: {
    Icon: __vue_component__
  },

  props: {
    filteredItems: {
      type: Array,
      default: () => []
    }
  }
};

/* script */
const __vue_script__$1G = script$1G;

/* template */
var __vue_render__$1G = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { attrs: { "data-cy": "me-list" } }, [
    _vm.filteredItems.length
      ? _c(
          "ul",
          { staticClass: "list" },
          _vm._l(_vm.filteredItems, function(filteredItem, index) {
            return _c("li", { key: index, staticClass: "list__item" }, [
              _c("div", { staticClass: "list__item__icon-line" }, [
                _c(
                  "div",
                  {
                    staticClass: "list__item__icon-line__icon",
                    class:
                      (filteredItem.border || "rounded-circle") +
                      " " +
                      (filteredItem.variant || "primary")
                  },
                  [
                    _c("Icon", {
                      attrs: {
                        icon: filteredItem.icon,
                        "data-cy": "me-list-icon"
                      }
                    })
                  ],
                  1
                ),
                _vm._v(" "),
                index < _vm.filteredItems.length - 1
                  ? _c("div", { staticClass: "list__item__icon-line__line" })
                  : _vm._e()
              ]),
              _vm._v(" "),
              _c(
                "div",
                {
                  staticClass: "list__item__info",
                  attrs: { "data-cy": "me-list-info" }
                },
                [
                  filteredItem.date
                    ? _c("div", { staticClass: "list__item__info__date" }, [
                        _vm._v(
                          "\n          " +
                            _vm._s(_vm._f("me-datetime")(filteredItem.date)) +
                            "\n        "
                        )
                      ])
                    : _vm._e(),
                  _vm._v(" "),
                  filteredItem.title
                    ? _c("div", { staticClass: "list__item__info__title" }, [
                        _vm._v(
                          "\n          " +
                            _vm._s(filteredItem.title) +
                            "\n        "
                        )
                      ])
                    : _vm._e(),
                  _vm._v(" "),
                  filteredItem.description
                    ? _c(
                        "div",
                        { staticClass: "list__item__info__description" },
                        [
                          _vm._v(
                            "\n          " +
                              _vm._s(filteredItem.description) +
                              "\n        "
                          )
                        ]
                      )
                    : _vm._e(),
                  _vm._v(" "),
                  filteredItem.user
                    ? _c("div", { staticClass: "list__item__info__user" }, [
                        _vm._v(
                          "\n          " +
                            _vm._s(filteredItem.user) +
                            "\n        "
                        )
                      ])
                    : _vm._e()
                ]
              )
            ])
          }),
          0
        )
      : _c(
          "div",
          { staticClass: "text-center" },
          [
            _c("Icon", { attrs: { icon: "me-icon-l icon-info-circle" } }),
            _vm._v(" " + _vm._s(_vm.$toolkit.t("history.empty")) + "\n  ")
          ],
          1
        )
  ])
};
var __vue_staticRenderFns__$1G = [];
__vue_render__$1G._withStripped = true;

  /* style */
  const __vue_inject_styles__$1G = function (inject) {
    if (!inject) return
    inject("data-v-ce78a32e_0", { source: ".list[data-v-ce78a32e] {\n  display: flex;\n  flex-direction: column;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\n.list__item[data-v-ce78a32e] {\n  display: flex;\n}\n.list__item__icon-line[data-v-ce78a32e] {\n  display: flex;\n  flex-direction: column;\n  margin-right: 24px;\n}\n.list__item__icon-line__icon[data-v-ce78a32e] {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 32px;\n  width: 32px;\n}\n.list__item__icon-line__icon.primary[data-v-ce78a32e] {\n  background: var(--me-primary-7);\n  border: 1px solid var(--me-primary-2);\n  color: var(--me-primary-2);\n}\n.list__item__icon-line__icon.success[data-v-ce78a32e] {\n  background: var(--me-success-7);\n  border: 1px solid var(--me-success-2);\n  color: var(--me-success-2);\n}\n.list__item__icon-line__icon.danger[data-v-ce78a32e] {\n  background: var(--me-danger-7);\n  border: 1px solid var(--me-danger-2);\n  color: var(--me-danger-2);\n}\n.list__item__icon-line__icon.warning[data-v-ce78a32e] {\n  background: var(--me-warning-7);\n  border: 1px solid var(--me-warning-2);\n  color: var(--me-warning-2);\n}\n.list__item__icon-line__line[data-v-ce78a32e] {\n  border-left: 1px solid var(--me-gray-6);\n  margin: 4px 0 4px 16px;\n  flex-grow: 1;\n}\n.list__item:last-child .list__item__info[data-v-ce78a32e] {\n  padding-bottom: 0 !important;\n}\n.list__item__info[data-v-ce78a32e] {\n  font-size: 12px;\n  padding-bottom: 32px;\n}\n.list__item__info__date[data-v-ce78a32e] {\n  color: var(--me-gray-4);\n}\n.list__item__info__title[data-v-ce78a32e], .list__item__info__description[data-v-ce78a32e] {\n  font-weight: 600;\n  font-size: 14px;\n}\n.list__item__info__user[data-v-ce78a32e] {\n  color: var(--me-gray-3);\n}\n\n/*# sourceMappingURL=List.vue.map */", map: {"version":3,"sources":["/app/build/src/components/history/List.vue","List.vue"],"names":[],"mappings":"AA4FA;EACA,aAAA;EACA,sBAAA;EACA,gBAAA;EACA,SAAA;EACA,UAAA;AC3FA;AD6FA;EACA,aAAA;AC3FA;AD6FA;EACA,aAAA;EACA,sBAAA;EACA,kBAAA;AC3FA;AD6FA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,YArCA;EAsCA,WAtCA;ACrDA;ADiEA;EAPA,+BAQA;EAPA,qCAAA;EACA,0BAMA;AC7DA;AD4DA;EAPA,+BAQA;EAPA,qCAAA;EACA,0BAMA;ACxDA;ADuDA;EAPA,8BAQA;EAPA,oCAAA;EACA,yBAMA;ACnDA;ADkDA;EAPA,+BAQA;EAPA,qCAAA;EACA,0BAMA;AC9CA;AD6EA;EACA,uCAAA;EACA,sBAAA;EACA,YAAA;AC3EA;ADgFA;EACA,4BAAA;AC9EA;ADiFA;EACA,eAAA;EACA,oBAAA;AC/EA;ADiFA;EACA,uBAAA;AC/EA;ADkFA;EAEA,gBAAA;EACA,eAAA;ACjFA;ADoFA;EACA,uBAAA;AClFA;;AAEA,mCAAmC","file":"List.vue","sourcesContent":["<template>\n  <div data-cy=\"me-list\">\n    <ul\n      v-if=\"filteredItems.length\"\n      class=\"list\"\n    >\n      <li\n        v-for=\"(filteredItem, index) in filteredItems\"\n        :key=\"index\"\n        class=\"list__item\"\n      >\n        <div class=\"list__item__icon-line\">\n          <div\n            :class=\"`${filteredItem.border || 'rounded-circle'} ${filteredItem.variant || 'primary'}`\"\n            class=\"list__item__icon-line__icon\"\n          >\n            <Icon :icon=\"filteredItem.icon\" data-cy=\"me-list-icon\" />\n          </div>\n\n          <div\n            v-if=\"index < filteredItems.length - 1\"\n            class=\"list__item__icon-line__line\"\n          />\n        </div>\n\n        <div class=\"list__item__info\" data-cy=\"me-list-info\">\n          <div v-if=\"filteredItem.date\" class=\"list__item__info__date\">\n            {{ filteredItem.date | me-datetime }}\n          </div>\n\n          <div v-if=\"filteredItem.title\" class=\"list__item__info__title\">\n            {{ filteredItem.title }}\n          </div>\n\n          <div v-if=\"filteredItem.description\" class=\"list__item__info__description\">\n            {{ filteredItem.description }}\n          </div>\n\n          <div v-if=\"filteredItem.user\" class=\"list__item__info__user\">\n            {{ filteredItem.user }}\n          </div>\n        </div>\n      </li>\n    </ul>\n\n    <div\n      v-else\n      class=\"text-center\"\n    >\n      <Icon icon=\"me-icon-l icon-info-circle\" /> {{ $toolkit.t('history.empty') }}\n    </div>\n  </div>\n</template>\n\n<script>\nimport Icon from './../Icon.vue'\n\nexport default {\n  name: 'List',\n\n  components: {\n    Icon\n  },\n\n  props: {\n    filteredItems: {\n      type: Array,\n      default: () => []\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $icon-size: 32px;\n  $icon-middle: $icon-size / 2;\n  $variantList: 'primary', 'success', 'danger', 'warning';\n\n  @mixin colorIcon($darkColor, $brightColor) {\n    background: $brightColor;\n    border: 1px solid $darkColor;\n    color: $darkColor;\n  }\n\n  @mixin variant-style() {\n    @each $variantName in $variantList {\n      &.#{$variantName} {\n        @include colorIcon(var(--me-#{$variantName}-2), var(--me-#{$variantName}-7));\n      }\n    }\n  }\n\n  .list {\n    display: flex;\n    flex-direction: column;\n    list-style: none;\n    margin: 0;\n    padding: 0;\n\n    &__item {\n      display: flex;\n\n      &__icon-line {\n        display: flex;\n        flex-direction: column;\n        margin-right: 24px;\n\n        &__icon {\n          display: flex;\n          align-items: center;\n          justify-content: center;\n          height: $icon-size;\n          width: $icon-size;\n\n          @include variant-style()\n\n        }\n\n        &__line {\n          border-left: 1px solid var(--me-gray-6);\n          margin: 4px 0 4px $icon-middle;\n          flex-grow: 1;\n        }\n\n      }\n\n      &:last-child &__info {\n        padding-bottom: 0 !important;\n      }\n\n      &__info {\n        font-size: 12px;\n        padding-bottom: 32px;\n\n        &__date {\n          color: var(--me-gray-4);\n        }\n\n        &__title,\n        &__description {\n          font-weight: 600;\n          font-size: 14px;\n        }\n\n        &__user {\n          color: var(--me-gray-3);\n        }\n      }\n    }\n  }\n</style>\n",".list {\n  display: flex;\n  flex-direction: column;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\n.list__item {\n  display: flex;\n}\n.list__item__icon-line {\n  display: flex;\n  flex-direction: column;\n  margin-right: 24px;\n}\n.list__item__icon-line__icon {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 32px;\n  width: 32px;\n}\n.list__item__icon-line__icon.primary {\n  background: var(--me-primary-7);\n  border: 1px solid var(--me-primary-2);\n  color: var(--me-primary-2);\n}\n.list__item__icon-line__icon.success {\n  background: var(--me-success-7);\n  border: 1px solid var(--me-success-2);\n  color: var(--me-success-2);\n}\n.list__item__icon-line__icon.danger {\n  background: var(--me-danger-7);\n  border: 1px solid var(--me-danger-2);\n  color: var(--me-danger-2);\n}\n.list__item__icon-line__icon.warning {\n  background: var(--me-warning-7);\n  border: 1px solid var(--me-warning-2);\n  color: var(--me-warning-2);\n}\n.list__item__icon-line__line {\n  border-left: 1px solid var(--me-gray-6);\n  margin: 4px 0 4px 16px;\n  flex-grow: 1;\n}\n.list__item:last-child .list__item__info {\n  padding-bottom: 0 !important;\n}\n.list__item__info {\n  font-size: 12px;\n  padding-bottom: 32px;\n}\n.list__item__info__date {\n  color: var(--me-gray-4);\n}\n.list__item__info__title, .list__item__info__description {\n  font-weight: 600;\n  font-size: 14px;\n}\n.list__item__info__user {\n  color: var(--me-gray-3);\n}\n\n/*# sourceMappingURL=List.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1G = "data-v-ce78a32e";
  /* module identifier */
  const __vue_module_identifier__$1G = undefined;
  /* functional template */
  const __vue_is_functional_template__$1G = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1G, staticRenderFns: __vue_staticRenderFns__$1G },
    __vue_inject_styles__$1G,
    __vue_script__$1G,
    __vue_scope_id__$1G,
    __vue_is_functional_template__$1G,
    __vue_module_identifier__$1G,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1H = {
  name: 'MeHistoryTimeline',

  components: {
    DropdownDateForm: __vue_component__$1j,
    DropDown: __vue_component__$8,
    List: __vue_component__$1G,
    TextInput: __vue_component__$p
  },

  props: {
    title: {
      type: String,
      default: ''
    },

    dateFilter: {
      type: Boolean,
      default: false
    },

    statusFilter: {
      type: Boolean,
      default: false
    },

    searchFilter: {
      type: Boolean,
      default: false
    },

    items: {
      type: Array,
      default: () => []
    },

    initialDate: {
      type: [Date, String],
      default: null
    },

    endDate: {
      type: [Date, String],
      default: null
    },

    invalidInitialDateFeedback: {
      type: String,
      default: ''
    },

    invalidEndDateFeedback: {
      type: String,
      default: ''
    },

    validateInitialDate: {
      type: Function,
      default: () => null
    },

    validateEndDate: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      dropdownDateKey: new Date().getTime().toString() + Math.random().toString(),
      initialDateFilter: this.initialDate || new Date(new Date().setDate(new Date().getDate() - 30)),
      endDateFilter: this.endDate || null,
      statusAll: {
        id: 'all',
        label: this.$toolkit.t('history.all.types'),
        click: this.removeFilter
      },
      selectedStatus: {},
      searchTerm: ''
    }
  },

  computed: {
    filteredItems() {
      return this.items
        .filter(this.filterByStatus)
        .filter(this.filterByInitialDate)
        .filter(this.filterByEndDate)
        .filter(this.filterByTerm)
    },

    statusList() {
      return this.getStatus(this.items)([this.statusAll])
    }
  },

  mounted() {
    this.selectedStatus = { ...this.statusAll };
  },

  methods: {
    filterByStatus(item) {
      return item.status?.id === this.selectedStatus?.id || this.selectedStatus?.id === this.statusAll.id
    },

    filterByInitialDate(item) {
      return this.initialDateFilter && this.dateFilter && item.date ? new Date(item.date).setHours(0, 0, 0, 0) >= this.initialDateFilter.setHours(0, 0, 0, 0) : true
    },

    filterByEndDate(item) {
      return this.endDateFilter && this.dateFilter && item.date ? new Date(item.date).setHours(0, 0, 0, 0) <= this.endDateFilter.setHours(0, 0, 0, 0) : true
    },

    filterByTerm(item) {
      return this.matchTerm(item.description, this.searchTerm) || this.matchTerm(item.user, this.searchTerm) || this.matchTerm(item.title, this.searchTerm)
    },

    getStatus(itemList) {
      return (preDefinedStatus = []) => this.removeDuplicatedStatus([...preDefinedStatus, ...itemList.filter(this.filterValidItemList).map(this.createStatus)])
    },

    removeDuplicatedStatus(statusList) {
      return [...new Map(statusList.map(status => [status.id, status])).values()]
    },

    filterValidItemList(item) {
      return item.status?.id && item.status?.description
    },

    createStatus(item) {
      return {
        id: item.status.id,
        label: item.status.description,
        click: () => this.updateSelectedStatus({
          id: item.status.id,
          label: item.status.description
        })
      }
    },

    removeFilter() {
      this.selectedStatus = { ...this.statusAll };
    },

    updateSelectedStatus(status) {
      this.selectedStatus = { ...status };
    },

    matchTerm(field, term) {
      return field?.match(new RegExp(term, 'i'))
    },

    updateDatesFilter({ initialDate, endDate }) {
      this.initialDateFilter = initialDate;
      this.endDateFilter = endDate;
    }
  }
};

/* script */
const __vue_script__$1H = script$1H;

/* template */
var __vue_render__$1H = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "timeline", attrs: { "data-cy": "me-timeline" } },
    [
      _c("div", { staticClass: "timeline__header" }, [
        _vm.title
          ? _c(
              "div",
              {
                staticClass: "timeline__header__title",
                attrs: { "data-cy": "timeline-title" }
              },
              [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]
            )
          : _vm._e(),
        _vm._v(" "),
        _c(
          "div",
          { staticClass: "timeline__header__filters" },
          [
            _vm.statusFilter && _vm.selectedStatus
              ? _c("DropDown", {
                  staticClass: "mr-2",
                  attrs: {
                    items: _vm.statusList,
                    text: _vm.selectedStatus.label,
                    variant: "outline-primary",
                    size: "sm"
                  }
                })
              : _vm._e(),
            _vm._v(" "),
            _vm.dateFilter
              ? _c("DropdownDateForm", {
                  attrs: {
                    name: _vm.dropdownDateKey,
                    initialDate: _vm.initialDateFilter,
                    validateInitialDate: _vm.validateInitialDate,
                    validateEndDate: _vm.validateEndDate,
                    invalidInitialDateFeedback: _vm.invalidInitialDateFeedback,
                    invalidEndDateFeedback: _vm.invalidEndDateFeedback,
                    "hide-all-period": "",
                    "hide-actions": "",
                    size: "sm"
                  },
                  on: { change: _vm.updateDatesFilter }
                })
              : _vm._e()
          ],
          1
        )
      ]),
      _vm._v(" "),
      _vm.searchFilter
        ? _c("TextInput", {
            staticClass: "my-4",
            attrs: { placeholder: _vm.$toolkit.t("history.search") },
            model: {
              value: _vm.searchTerm,
              callback: function($$v) {
                _vm.searchTerm = $$v;
              },
              expression: "searchTerm"
            }
          })
        : _vm._e(),
      _vm._v(" "),
      _c("List", { attrs: { filteredItems: _vm.filteredItems } })
    ],
    1
  )
};
var __vue_staticRenderFns__$1H = [];
__vue_render__$1H._withStripped = true;

  /* style */
  const __vue_inject_styles__$1H = function (inject) {
    if (!inject) return
    inject("data-v-3849e6d5_0", { source: ".timeline__header[data-v-3849e6d5] {\n  display: flex;\n  justify-content: space-between;\n}\n.timeline__header__title[data-v-3849e6d5] {\n  font-size: 20px;\n  font-weight: 600;\n}\n.timeline__header__filters[data-v-3849e6d5] {\n  display: flex;\n}\n\n/*# sourceMappingURL=Timeline.vue.map */", map: {"version":3,"sources":["/app/build/src/components/history/Timeline.vue","Timeline.vue"],"names":[],"mappings":"AA0NA;EACA,aAAA;EACA,8BAAA;ACzNA;AD2NA;EACA,eAAA;EACA,gBAAA;ACzNA;AD4NA;EACA,aAAA;AC1NA;;AAEA,uCAAuC","file":"Timeline.vue","sourcesContent":["<template>\n  <section class=\"timeline\" data-cy=\"me-timeline\">\n    <div class=\"timeline__header\">\n      <div\n        v-if=\"title\"\n        class=\"timeline__header__title\"\n        data-cy=\"timeline-title\"\n      >\n        {{ title }}\n      </div>\n\n      <div class=\"timeline__header__filters\">\n        <DropDown\n          v-if=\"statusFilter && selectedStatus\"\n          :items=\"statusList\"\n          :text=\"selectedStatus.label\"\n          variant=\"outline-primary\"\n          size=\"sm\"\n          class=\"mr-2\"\n        />\n\n        <DropdownDateForm\n          v-if=\"dateFilter\"\n          :name=\"dropdownDateKey\"\n          :initialDate=\"initialDateFilter\"\n          :validateInitialDate=\"validateInitialDate\"\n          :validateEndDate=\"validateEndDate\"\n          :invalidInitialDateFeedback=\"invalidInitialDateFeedback\"\n          :invalidEndDateFeedback=\"invalidEndDateFeedback\"\n          @change=\"updateDatesFilter\"\n          hide-all-period\n          hide-actions\n          size=\"sm\"\n        />\n      </div>\n    </div>\n\n    <TextInput\n      v-if=\"searchFilter\"\n      v-model=\"searchTerm\"\n      :placeholder=\"$toolkit.t('history.search')\"\n      class=\"my-4\"\n    />\n\n    <List\n      :filteredItems=\"filteredItems\"\n    />\n  </section>\n</template>\n\n<script>\nimport List from './List.vue'\nimport DropdownDateForm from './../dropdown-criterion/dropdown-date-form/DropdownDateForm.vue'\nimport DropDown from './../dropdown/DropDown.vue'\nimport TextInput from './../TextInput.vue'\n\nexport default {\n  name: 'MeHistoryTimeline',\n\n  components: {\n    DropdownDateForm,\n    DropDown,\n    List,\n    TextInput\n  },\n\n  props: {\n    title: {\n      type: String,\n      default: ''\n    },\n\n    dateFilter: {\n      type: Boolean,\n      default: false\n    },\n\n    statusFilter: {\n      type: Boolean,\n      default: false\n    },\n\n    searchFilter: {\n      type: Boolean,\n      default: false\n    },\n\n    items: {\n      type: Array,\n      default: () => []\n    },\n\n    initialDate: {\n      type: [Date, String],\n      default: null\n    },\n\n    endDate: {\n      type: [Date, String],\n      default: null\n    },\n\n    invalidInitialDateFeedback: {\n      type: String,\n      default: ''\n    },\n\n    invalidEndDateFeedback: {\n      type: String,\n      default: ''\n    },\n\n    validateInitialDate: {\n      type: Function,\n      default: () => null\n    },\n\n    validateEndDate: {\n      type: Function,\n      default: () => null\n    }\n  },\n\n  data() {\n    return {\n      dropdownDateKey: new Date().getTime().toString() + Math.random().toString(),\n      initialDateFilter: this.initialDate || new Date(new Date().setDate(new Date().getDate() - 30)),\n      endDateFilter: this.endDate || null,\n      statusAll: {\n        id: 'all',\n        label: this.$toolkit.t('history.all.types'),\n        click: this.removeFilter\n      },\n      selectedStatus: {},\n      searchTerm: ''\n    }\n  },\n\n  computed: {\n    filteredItems() {\n      return this.items\n        .filter(this.filterByStatus)\n        .filter(this.filterByInitialDate)\n        .filter(this.filterByEndDate)\n        .filter(this.filterByTerm)\n    },\n\n    statusList() {\n      return this.getStatus(this.items)([this.statusAll])\n    }\n  },\n\n  mounted() {\n    this.selectedStatus = { ...this.statusAll }\n  },\n\n  methods: {\n    filterByStatus(item) {\n      return item.status?.id === this.selectedStatus?.id || this.selectedStatus?.id === this.statusAll.id\n    },\n\n    filterByInitialDate(item) {\n      return this.initialDateFilter && this.dateFilter && item.date ? new Date(item.date).setHours(0, 0, 0, 0) >= this.initialDateFilter.setHours(0, 0, 0, 0) : true\n    },\n\n    filterByEndDate(item) {\n      return this.endDateFilter && this.dateFilter && item.date ? new Date(item.date).setHours(0, 0, 0, 0) <= this.endDateFilter.setHours(0, 0, 0, 0) : true\n    },\n\n    filterByTerm(item) {\n      return this.matchTerm(item.description, this.searchTerm) || this.matchTerm(item.user, this.searchTerm) || this.matchTerm(item.title, this.searchTerm)\n    },\n\n    getStatus(itemList) {\n      return (preDefinedStatus = []) => this.removeDuplicatedStatus([...preDefinedStatus, ...itemList.filter(this.filterValidItemList).map(this.createStatus)])\n    },\n\n    removeDuplicatedStatus(statusList) {\n      return [...new Map(statusList.map(status => [status.id, status])).values()]\n    },\n\n    filterValidItemList(item) {\n      return item.status?.id && item.status?.description\n    },\n\n    createStatus(item) {\n      return {\n        id: item.status.id,\n        label: item.status.description,\n        click: () => this.updateSelectedStatus({\n          id: item.status.id,\n          label: item.status.description\n        })\n      }\n    },\n\n    removeFilter() {\n      this.selectedStatus = { ...this.statusAll }\n    },\n\n    updateSelectedStatus(status) {\n      this.selectedStatus = { ...status }\n    },\n\n    matchTerm(field, term) {\n      return field?.match(new RegExp(term, 'i'))\n    },\n\n    updateDatesFilter({ initialDate, endDate }) {\n      this.initialDateFilter = initialDate\n      this.endDateFilter = endDate\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.timeline {\n  &__header {\n    display: flex;\n    justify-content: space-between;\n\n    &__title {\n      font-size: 20px;\n      font-weight: 600;\n    }\n\n    &__filters {\n      display: flex;\n    }\n  }\n}\n</style>\n",".timeline__header {\n  display: flex;\n  justify-content: space-between;\n}\n.timeline__header__title {\n  font-size: 20px;\n  font-weight: 600;\n}\n.timeline__header__filters {\n  display: flex;\n}\n\n/*# sourceMappingURL=Timeline.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1H = "data-v-3849e6d5";
  /* module identifier */
  const __vue_module_identifier__$1H = undefined;
  /* functional template */
  const __vue_is_functional_template__$1H = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1H, staticRenderFns: __vue_staticRenderFns__$1H },
    __vue_inject_styles__$1H,
    __vue_script__$1H,
    __vue_scope_id__$1H,
    __vue_is_functional_template__$1H,
    __vue_module_identifier__$1H,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1I = {
  name: 'MeHistoryFlow',

  components: {
    Select: __vue_component__$m
  },

  props: {
    items: {
      type: Array,
      default: () => []
    },

    withFilter: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      elements: [],

      selectedFilterOption: [{
        name: this.$toolkit.t('history.all.types'),
        id: null
      }],

      filterOptions: [{
        name: this.$toolkit.t('history.all.types'),
        id: null
      }]
    }
  },

  mounted() {
    this.elements = this.items;

    this.items.map((x, y) => {
      this.filterOptions.push({
        name: x.statusLabel,
        id: y
      });
    });
  },

  methods: {
    getStatusColor(color) {
      const colorParam = color;

      if (colorParam === 'green') {
        return '#09BD9A'
      }

      if (colorParam === 'red') {
        return '#FF5252'
      }

      return '#DBDDDC'
    }
  }
};

/* script */
const __vue_script__$1I = script$1I;

/* template */
var __vue_render__$1I = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "me-history-flow", attrs: { "data-cy": "me-flow" } },
    [
      _vm.withFilter
        ? _c(
            "div",
            { staticClass: "flow-filter" },
            [
              _c("Select", {
                attrs: { options: _vm.filterOptions, clearable: false },
                model: {
                  value: _vm.selectedFilterOption,
                  callback: function($$v) {
                    _vm.selectedFilterOption = $$v;
                  },
                  expression: "selectedFilterOption"
                }
              })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _c(
        "ul",
        { staticClass: "flow" },
        _vm._l(_vm.elements, function(element, index) {
          return _c("li", { key: index }, [
            _vm.withFilter && !Number.isInteger(_vm.selectedFilterOption.id)
              ? _c(
                  "ul",
                  { attrs: { "data-cy": "flow-all-types" } },
                  [
                    _c("li", [
                      _c("label", [_vm._v(_vm._s(element.firstColumn.label))]),
                      _vm._v(" "),
                      element.firstColumn.valueType === "datetime"
                        ? _c("span", [
                            _vm._v(
                              "\n            " +
                                _vm._s(
                                  _vm._f("me-datetime")(
                                    element.firstColumn.value
                                  )
                                ) +
                                "\n          "
                            )
                          ])
                        : _vm._e(),
                      _vm._v(" "),
                      element.firstColumn.valueType === "string"
                        ? _c("span", [
                            _vm._v(
                              "\n            " +
                                _vm._s(element.firstColumn.value) +
                                "\n          "
                            )
                          ])
                        : _vm._e()
                    ]),
                    _vm._v(" "),
                    _c("li", { staticClass: "flow-status" }, [
                      _c("i", {
                        class: "me-icon " + element.statusIcon,
                        style:
                          "color: " +
                          _vm.getStatusColor(element.statusColor) +
                          ";"
                      }),
                      _vm._v(" "),
                      _c(
                        "span",
                        {
                          style:
                            "color: " +
                            _vm.getStatusColor(element.statusColor) +
                            ";"
                        },
                        [
                          _vm._v(
                            "\n            " +
                              _vm._s(element.statusLabel) +
                              "\n          "
                          )
                        ]
                      )
                    ]),
                    _vm._v(" "),
                    _vm._l(element.otherColumns, function(column, indexColumn) {
                      return _c("li", { key: indexColumn }, [
                        _c("label", [_vm._v(_vm._s(column.label))]),
                        _vm._v(" "),
                        column.valueType === "datetime"
                          ? _c("span", [
                              _vm._v(
                                "\n            " +
                                  _vm._s(_vm._f("me-datetime")(column.value)) +
                                  "\n          "
                              )
                            ])
                          : _vm._e(),
                        _vm._v(" "),
                        column.valueType === "string"
                          ? _c("span", [
                              _vm._v(
                                "\n            " +
                                  _vm._s(column.value) +
                                  "\n          "
                              )
                            ])
                          : _vm._e()
                      ])
                    })
                  ],
                  2
                )
              : _vm.withFilter && _vm.selectedFilterOption.id === index
              ? _c(
                  "ul",
                  { attrs: { "data-cy": "flow-filter" } },
                  [
                    _c("li", [
                      _c("label", [_vm._v(_vm._s(element.firstColumn.label))]),
                      _vm._v(" "),
                      element.firstColumn.valueType === "datetime"
                        ? _c("span", [
                            _vm._v(
                              _vm._s(
                                _vm._f("me-datetime")(element.firstColumn.value)
                              )
                            )
                          ])
                        : _vm._e(),
                      _vm._v(" "),
                      element.firstColumn.valueType === "string"
                        ? _c("span", [
                            _vm._v(_vm._s(element.firstColumn.value))
                          ])
                        : _vm._e()
                    ]),
                    _vm._v(" "),
                    _c("li", { staticClass: "flow-status" }, [
                      _c("i", {
                        class: "me-icon " + element.statusIcon,
                        style:
                          "color: " +
                          _vm.getStatusColor(element.statusColor) +
                          ";"
                      }),
                      _vm._v(" "),
                      _c(
                        "span",
                        {
                          style:
                            "color: " +
                            _vm.getStatusColor(element.statusColor) +
                            ";"
                        },
                        [_vm._v(_vm._s(element.statusLabel))]
                      )
                    ]),
                    _vm._v(" "),
                    _vm._l(element.otherColumns, function(column, indexColumn) {
                      return _c("li", { key: indexColumn }, [
                        _c("label", [_vm._v(_vm._s(column.label))]),
                        _vm._v(" "),
                        column.valueType === "datetime"
                          ? _c("span", [
                              _vm._v(
                                _vm._s(_vm._f("me-datetime")(column.value))
                              )
                            ])
                          : _vm._e(),
                        _vm._v(" "),
                        column.valueType === "string"
                          ? _c("span", [_vm._v(_vm._s(column.value))])
                          : _vm._e()
                      ])
                    })
                  ],
                  2
                )
              : !_vm.withFilter
              ? _c(
                  "ul",
                  { attrs: { "data-cy": "flow-no-filter" } },
                  [
                    _c("li", [
                      _c("label", [_vm._v(_vm._s(element.firstColumn.label))]),
                      _vm._v(" "),
                      element.firstColumn.valueType === "datetime"
                        ? _c("span", [
                            _vm._v(
                              _vm._s(
                                _vm._f("me-datetime")(element.firstColumn.value)
                              )
                            )
                          ])
                        : _vm._e(),
                      _vm._v(" "),
                      element.firstColumn.valueType === "string"
                        ? _c("span", [
                            _vm._v(_vm._s(element.firstColumn.value))
                          ])
                        : _vm._e()
                    ]),
                    _vm._v(" "),
                    _c("li", { staticClass: "flow-status" }, [
                      _c("i", {
                        class: "me-icon " + element.statusIcon,
                        style:
                          "color: " +
                          _vm.getStatusColor(element.statusColor) +
                          ";"
                      }),
                      _vm._v(" "),
                      _c(
                        "span",
                        {
                          style:
                            "color: " +
                            _vm.getStatusColor(element.statusColor) +
                            ";"
                        },
                        [_vm._v(_vm._s(element.statusLabel))]
                      )
                    ]),
                    _vm._v(" "),
                    _vm._l(element.otherColumns, function(column, indexColumn) {
                      return _c("li", { key: indexColumn }, [
                        _c("label", [_vm._v(_vm._s(column.label))]),
                        _vm._v(" "),
                        column.valueType === "datetime"
                          ? _c("span", [
                              _vm._v(
                                _vm._s(_vm._f("me-datetime")(column.value))
                              )
                            ])
                          : _vm._e(),
                        _vm._v(" "),
                        column.valueType === "string"
                          ? _c("span", [_vm._v(_vm._s(column.value))])
                          : _vm._e()
                      ])
                    })
                  ],
                  2
                )
              : _vm._e()
          ])
        }),
        0
      )
    ]
  )
};
var __vue_staticRenderFns__$1I = [];
__vue_render__$1I._withStripped = true;

  /* style */
  const __vue_inject_styles__$1I = function (inject) {
    if (!inject) return
    inject("data-v-bbcea09a_0", { source: ".flow[data-v-bbcea09a] {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\n.flow-filter[data-v-bbcea09a] {\n  margin-bottom: 30px;\n  display: flex;\n  justify-content: flex-end;\n}\n.flow-filter .me-select[data-v-bbcea09a] {\n  min-width: 200px;\n}\n.flow > li > ul[data-v-bbcea09a] {\n  min-height: 80px;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: row;\n  width: 100%;\n  align-items: flex-start;\n  font-size: 14px;\n}\n.flow > li > ul > li[data-v-bbcea09a] {\n  padding: 0 5px;\n}\n.flow > li > ul > li > label[data-v-bbcea09a] {\n  margin: 0;\n  display: block;\n  color: #919191;\n  font-size: 12px;\n}\n.flow > li > ul > li > span[data-v-bbcea09a] {\n  color: rgba(0, 0, 0, 0.87);\n}\n.flow > li > ul > li[data-v-bbcea09a]:not(:last-child) {\n  min-width: 180px;\n  max-width: 180px;\n}\n.flow > li > ul > li.flow-status[data-v-bbcea09a] {\n  display: flex;\n  align-items: center;\n  justify-content: flex-start;\n}\n.flow > li > ul > li.flow-status > span[data-v-bbcea09a] {\n  border: 1px solid;\n  border-radius: 20px;\n  color: #dbdddc;\n  padding: 4px 12px;\n  margin-left: 8px;\n}\n.flow > li > ul > li.flow-status > i[data-v-bbcea09a] {\n  border-radius: 20px;\n  color: #dbdddc;\n  padding: 8px 8px;\n  border: 1px solid;\n  display: table-column;\n}\n.flow > li[data-v-bbcea09a]:not(:last-child) {\n  margin-bottom: 20px;\n}\n.flow > li:not(:last-child) .flow-status i[data-v-bbcea09a]::after {\n  content: \"\";\n  display: block;\n  position: absolute;\n  height: 68px;\n  width: 1px;\n  background: #dbdddc;\n  transform: translateX(7px) translateY(9px);\n}\n\n/*# sourceMappingURL=Flow.vue.map */", map: {"version":3,"sources":["/app/build/src/components/history/Flow.vue","Flow.vue"],"names":[],"mappings":"AAiJA;EACA,gBAAA;EACA,SAAA;EACA,UAAA;AChJA;ADkJA;EACA,mBAAA;EACA,aAAA;EACA,yBAAA;AChJA;ADkJA;EACA,gBAAA;AChJA;ADqJA;EACA,gBAAA;EACA,gBAAA;EACA,SAAA;EACA,UAAA;EACA,aAAA;EACA,mBAAA;EACA,WAAA;EACA,uBAAA;EACA,eAAA;ACnJA;ADqJA;EACA,cAAA;ACnJA;ADqJA;EACA,SAAA;EACA,cAAA;EACA,cAAA;EACA,eAAA;ACnJA;ADsJA;EACA,0BAAA;ACpJA;ADuJA;EACA,gBAAA;EACA,gBAAA;ACrJA;ADwJA;EACA,aAAA;EACA,mBAAA;EACA,2BAAA;ACtJA;ADwJA;EACA,iBAAA;EACA,mBAAA;EACA,cAAA;EACA,iBAAA;EACA,gBAAA;ACtJA;ADyJA;EACA,mBAAA;EACA,cAAA;EACA,gBAAA;EACA,iBAAA;EACA,qBAAA;ACvJA;AD6JA;EACA,mBAAA;AC3JA;AD6JA;EACA,WAAA;EACA,cAAA;EACA,kBAAA;EACA,YAAA;EACA,UAAA;EACA,mBAAA;EACA,0CAAA;AC3JA;;AAEA,mCAAmC","file":"Flow.vue","sourcesContent":["<template>\n  <section class=\"me-history-flow\" data-cy=\"me-flow\">\n    <div v-if=\"withFilter\" class=\"flow-filter\">\n      <Select v-model=\"selectedFilterOption\" :options=\"filterOptions\" :clearable=\"false\" />\n    </div>\n\n    <ul class=\"flow\">\n      <li v-for=\"(element, index) in elements\" :key=\"index\">\n        <ul\n          v-if=\"withFilter && !Number.isInteger(selectedFilterOption.id)\"\n          data-cy=\"flow-all-types\"\n        >\n          <li>\n            <label>{{ element.firstColumn.label }}</label>\n            <span v-if=\"element.firstColumn.valueType === 'datetime'\">\n              {{ element.firstColumn.value | me-datetime }}\n            </span>\n            <span v-if=\"element.firstColumn.valueType === 'string'\">\n              {{ element.firstColumn.value }}\n            </span>\n          </li>\n          <li class=\"flow-status\">\n            <i :class=\"'me-icon ' + element.statusIcon\" :style=\"'color: ' + getStatusColor(element.statusColor) + ';'\" />\n            <span :style=\"'color: ' + getStatusColor(element.statusColor) + ';'\">\n              {{ element.statusLabel }}\n            </span>\n          </li>\n          <li v-for=\"(column, indexColumn) in element.otherColumns\" :key=\"indexColumn\">\n            <label>{{ column.label }}</label>\n            <span v-if=\"column.valueType === 'datetime'\">\n              {{ column.value | me-datetime }}\n            </span>\n            <span v-if=\"column.valueType === 'string'\">\n              {{ column.value }}\n            </span>\n          </li>\n        </ul>\n        <ul\n          v-else-if=\"withFilter && selectedFilterOption.id === index\"\n          data-cy=\"flow-filter\"\n        >\n          <li>\n            <label>{{ element.firstColumn.label }}</label>\n            <span v-if=\"element.firstColumn.valueType === 'datetime'\">{{ element.firstColumn.value | me-datetime }}</span>\n            <span v-if=\"element.firstColumn.valueType === 'string'\">{{ element.firstColumn.value }}</span>\n          </li>\n          <li class=\"flow-status\">\n            <i :class=\"'me-icon ' + element.statusIcon\" :style=\"'color: ' + getStatusColor(element.statusColor) + ';'\" />\n            <span :style=\"'color: ' + getStatusColor(element.statusColor) + ';'\">{{ element.statusLabel }}</span>\n          </li>\n          <li v-for=\"(column, indexColumn) in element.otherColumns\" :key=\"indexColumn\">\n            <label>{{ column.label }}</label>\n            <span v-if=\"column.valueType === 'datetime'\">{{ column.value | me-datetime }}</span>\n            <span v-if=\"column.valueType === 'string'\">{{ column.value }}</span>\n          </li>\n        </ul>\n        <ul v-else-if=\"!withFilter\" data-cy=\"flow-no-filter\">\n          <li>\n            <label>{{ element.firstColumn.label }}</label>\n            <span v-if=\"element.firstColumn.valueType === 'datetime'\">{{ element.firstColumn.value | me-datetime }}</span>\n            <span v-if=\"element.firstColumn.valueType === 'string'\">{{ element.firstColumn.value }}</span>\n          </li>\n          <li class=\"flow-status\">\n            <i :class=\"'me-icon ' + element.statusIcon\" :style=\"'color: ' + getStatusColor(element.statusColor) + ';'\" />\n            <span :style=\"'color: ' + getStatusColor(element.statusColor) + ';'\">{{ element.statusLabel }}</span>\n          </li>\n          <li v-for=\"(column, indexColumn) in element.otherColumns\" :key=\"indexColumn\">\n            <label>{{ column.label }}</label>\n            <span v-if=\"column.valueType === 'datetime'\">{{ column.value | me-datetime }}</span>\n            <span v-if=\"column.valueType === 'string'\">{{ column.value }}</span>\n          </li>\n        </ul>\n      </li>\n    </ul>\n  </section>\n</template>\n\n<script>\nimport Select from './../Select.vue'\n\nexport default {\n  name: 'MeHistoryFlow',\n\n  components: {\n    Select\n  },\n\n  props: {\n    items: {\n      type: Array,\n      default: () => []\n    },\n\n    withFilter: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      elements: [],\n\n      selectedFilterOption: [{\n        name: this.$toolkit.t('history.all.types'),\n        id: null\n      }],\n\n      filterOptions: [{\n        name: this.$toolkit.t('history.all.types'),\n        id: null\n      }]\n    }\n  },\n\n  mounted() {\n    this.elements = this.items\n\n    this.items.map((x, y) => {\n      this.filterOptions.push({\n        name: x.statusLabel,\n        id: y\n      })\n    })\n  },\n\n  methods: {\n    getStatusColor(color) {\n      const colorParam = color\n\n      if (colorParam === 'green') {\n        return '#09BD9A'\n      }\n\n      if (colorParam === 'red') {\n        return '#FF5252'\n      }\n\n      return '#DBDDDC'\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.flow {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n\n  &-filter {\n    margin-bottom: 30px;\n    display: flex;\n    justify-content: flex-end;\n\n    & .me-select {\n      min-width: 200px;\n    }\n  }\n\n  > li {\n    > ul {\n      min-height: 80px;\n      list-style: none;\n      margin: 0;\n      padding: 0;\n      display: flex;\n      flex-direction: row;\n      width: 100%;\n      align-items: flex-start;\n      font-size: 14px;\n\n      > li {\n        padding: 0 5px;\n\n        > label {\n          margin: 0;\n          display: block;\n          color: #919191;\n          font-size: 12px;\n        }\n\n        > span {\n          color: rgba(0,0,0,.87);\n        }\n\n        &:not(:last-child) {\n          min-width: 180px;\n          max-width: 180px;\n        }\n\n        &.flow-status {\n          display: flex;\n          align-items: center;\n          justify-content: flex-start;\n\n          > span {\n            border: 1px solid;\n            border-radius: 20px;\n            color: #dbdddc;\n            padding: 4px 12px;\n            margin-left: 8px;\n          }\n\n          > i {\n            border-radius: 20px;\n            color: #dbdddc;\n            padding: 8px 8px;\n            border: 1px solid;\n            display: table-column;\n          }\n        }\n      }\n    }\n\n    &:not(:last-child) {\n      margin-bottom: 20px;\n\n      .flow-status i::after {\n        content: \"\";\n        display: block;\n        position: absolute;\n        height: 68px;\n        width: 1px;\n        background: #dbdddc;\n        transform: translateX(7px) translateY(9px);\n      }\n    }\n  }\n}\n</style>\n",".flow {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\n.flow-filter {\n  margin-bottom: 30px;\n  display: flex;\n  justify-content: flex-end;\n}\n.flow-filter .me-select {\n  min-width: 200px;\n}\n.flow > li > ul {\n  min-height: 80px;\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: row;\n  width: 100%;\n  align-items: flex-start;\n  font-size: 14px;\n}\n.flow > li > ul > li {\n  padding: 0 5px;\n}\n.flow > li > ul > li > label {\n  margin: 0;\n  display: block;\n  color: #919191;\n  font-size: 12px;\n}\n.flow > li > ul > li > span {\n  color: rgba(0, 0, 0, 0.87);\n}\n.flow > li > ul > li:not(:last-child) {\n  min-width: 180px;\n  max-width: 180px;\n}\n.flow > li > ul > li.flow-status {\n  display: flex;\n  align-items: center;\n  justify-content: flex-start;\n}\n.flow > li > ul > li.flow-status > span {\n  border: 1px solid;\n  border-radius: 20px;\n  color: #dbdddc;\n  padding: 4px 12px;\n  margin-left: 8px;\n}\n.flow > li > ul > li.flow-status > i {\n  border-radius: 20px;\n  color: #dbdddc;\n  padding: 8px 8px;\n  border: 1px solid;\n  display: table-column;\n}\n.flow > li:not(:last-child) {\n  margin-bottom: 20px;\n}\n.flow > li:not(:last-child) .flow-status i::after {\n  content: \"\";\n  display: block;\n  position: absolute;\n  height: 68px;\n  width: 1px;\n  background: #dbdddc;\n  transform: translateX(7px) translateY(9px);\n}\n\n/*# sourceMappingURL=Flow.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1I = "data-v-bbcea09a";
  /* module identifier */
  const __vue_module_identifier__$1I = undefined;
  /* functional template */
  const __vue_is_functional_template__$1I = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1I, staticRenderFns: __vue_staticRenderFns__$1I },
    __vue_inject_styles__$1I,
    __vue_script__$1I,
    __vue_scope_id__$1I,
    __vue_is_functional_template__$1I,
    __vue_module_identifier__$1I,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1J = {
  name: 'MeHistory',

  components: {
    Timeline: __vue_component__$1H,
    Flow: __vue_component__$1I
  },

  props: {
    timeline: {
      type: Boolean,
      default: false
    },

    title: {
      type: String,
      default: ''
    },

    statusFilter: {
      type: Boolean,
      default: false
    },

    dateFilter: {
      type: Boolean,
      default: false
    },

    invalidInitialDateFeedback: {
      type: String,
      default: ''
    },

    invalidEndDateFeedback: {
      type: String,
      default: ''
    },

    validateInitialDate: {
      type: Function,
      default: () => null
    },

    validateEndDate: {
      type: Function,
      default: () => null
    },

    searchFilter: {
      type: Boolean,
      default: false
    },

    items: {
      type: Array,
      default: () => []
    },

    withFilter: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$1J = script$1J;

/* template */
var __vue_render__$1J = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "me-history" },
    [
      _vm.timeline
        ? _c("Timeline", {
            attrs: {
              items: _vm.items,
              title: _vm.title,
              statusFilter: _vm.statusFilter,
              dateFilter: _vm.dateFilter,
              searchFilter: _vm.searchFilter,
              validateInitialDate: _vm.validateInitialDate,
              validateEndDate: _vm.validateEndDate,
              invalidInitialDateFeedback: _vm.invalidInitialDateFeedback,
              invalidEndDateFeedback: _vm.invalidEndDateFeedback
            }
          })
        : _c("Flow", {
            attrs: { items: _vm.items, withFilter: _vm.withFilter }
          })
    ],
    1
  )
};
var __vue_staticRenderFns__$1J = [];
__vue_render__$1J._withStripped = true;

  /* style */
  const __vue_inject_styles__$1J = undefined;
  /* scoped */
  const __vue_scope_id__$1J = undefined;
  /* module identifier */
  const __vue_module_identifier__$1J = undefined;
  /* functional template */
  const __vue_is_functional_template__$1J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1J, staticRenderFns: __vue_staticRenderFns__$1J },
    __vue_inject_styles__$1J,
    __vue_script__$1J,
    __vue_scope_id__$1J,
    __vue_is_functional_template__$1J,
    __vue_module_identifier__$1J,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$1K = {
  name: 'MeInputCounter',
  components: {
    MeNumberInput: __vue_component__$C,
    MeButton: __vue_component__$6
  },

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    disabledAdd: {
      type: Boolean,
      default: false
    },
    disabledSubtract: {
      type: Boolean,
      default: false
    },
    disabledPlaceholder: {
      type: String,
      default: ''
    },
    max: {
      type: Number,
      default: 999999999
    },
    min: {
      type: Number,
      default: -999999999
    },
    precision: {
      type: Number,
      default: 0
    },
    size: {
      type: String,
      default: 'md'
    },
    state: {
      type: Boolean,
      default: null
    },
    value: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      current: this.value,
      focused: false
    }
  },

  computed: {
    disableSub() {
      return this.disabled || this.current === this.min || this.disabledSubtract
    },

    disableAdd() {
      return this.disabled || this.current >= this.max || this.disabledAdd
    }
  },

  watch: {
    value(val) {
      this.current = val;
    }
  },

  methods: {
    add() {
      if (this.current + 1 <= this.max) this.current++;
      this.$emit('add', this.current);
    },

    subtract() {
      if (this.current - 1 >= this.min) this.current--;
      this.$emit('subtract', this.current);
    },

    onFocus() {
      this.focused = true;
      this.$emit('focus', this.current);
    },

    onBlur() {
      this.focused = false;
      this.$emit('blur', this.current);
    },

    onChange() {
      this.$emit('input', this.current);
    }
  }
};

/* script */
const __vue_script__$1K = script$1K;

/* template */
var __vue_render__$1K = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-input-counter",
      attrs: { "data-cy": "me-input-counter" }
    },
    [
      _c(
        "div",
        {
          staticClass: "input-wrapper",
          class: {
            disabled: _vm.disabled,
            focused: _vm.focused,
            "is-invalid": _vm.state === false
          }
        },
        [
          _c("MeButton", {
            attrs: {
              size: _vm.size,
              disabled: _vm.disableSub,
              icon: "me-icon-l icon-minus",
              variant: "primary"
            },
            on: { click: _vm.subtract }
          }),
          _vm._v(" "),
          _c("MeNumberInput", {
            attrs: {
              disabled: _vm.disabled,
              size: _vm.size,
              placeholder: _vm.disabled ? _vm.disabledPlaceholder : "",
              value: _vm.value,
              max: _vm.max,
              min: _vm.min,
              precision: _vm.precision
            },
            on: { input: _vm.onChange, focus: _vm.onFocus, blur: _vm.onBlur },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          }),
          _vm._v(" "),
          _c("MeButton", {
            attrs: {
              size: _vm.size,
              disabled: _vm.disableAdd,
              icon: "me-icon-l icon-plus",
              variant: "primary"
            },
            on: { click: _vm.add }
          })
        ],
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$1K = [];
__vue_render__$1K._withStripped = true;

  /* style */
  const __vue_inject_styles__$1K = function (inject) {
    if (!inject) return
    inject("data-v-0783a6fa_0", { source: ".me-input-counter[data-v-0783a6fa] {\n  width: 100%;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa] {\n  display: flex;\n  flex-wrap: nowrap;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 9px;\n  background-color: var(--me-white);\n}\n.me-input-counter .input-wrapper.focused[data-v-0783a6fa] {\n  border-color: var(--me-primary-2);\n}\n.me-input-counter .input-wrapper.is-invalid[data-v-0783a6fa] {\n  border-color: var(--me-danger-2);\n}\n.me-input-counter .input-wrapper.is-invalid[data-v-0783a6fa]  .me-number-input input {\n  caret-color: auto;\n}\n.me-input-counter .input-wrapper.disabled[data-v-0783a6fa] {\n  border-color: var(--me-gray-4);\n  background-color: var(--me-gray-6);\n}\n.me-input-counter .input-wrapper button[data-v-0783a6fa] {\n  border: none;\n  color: var(--me-gray-3);\n  transition: none;\n}\n.me-input-counter .input-wrapper button[data-v-0783a6fa]:disabled {\n  color: var(--me-gray-4);\n}\n.me-input-counter .input-wrapper button[data-v-0783a6fa]:not(:disabled) {\n  background-color: transparent;\n}\n.me-input-counter .input-wrapper button[data-v-0783a6fa]:first-child {\n  border-right: none;\n  border-top-right-radius: 0;\n  border-bottom-right-radius: 0;\n}\n.me-input-counter .input-wrapper button[data-v-0783a6fa]:last-child {\n  border-left: none;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n}\n.me-input-counter .input-wrapper button.btn-sm[data-v-0783a6fa] {\n  height: 30px;\n}\n.me-input-counter .input-wrapper button.btn-md[data-v-0783a6fa] {\n  height: 38px;\n}\n.me-input-counter .input-wrapper button.btn-lg[data-v-0783a6fa] {\n  height: 46px;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa]  .me-number-input {\n  width: 100%;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa]  .me-number-input .input-icons {\n  display: none;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa]  .me-number-input input {\n  border: none;\n  border-radius: 0;\n  text-align: center;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa]  .me-number-input input.sm {\n  height: 30px;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa]  .me-number-input input.md {\n  height: 38px;\n}\n.me-input-counter .input-wrapper[data-v-0783a6fa]  .me-number-input input.lg {\n  height: 46px;\n}\n\n/*# sourceMappingURL=InputCounter.vue.map */", map: {"version":3,"sources":["/app/build/src/components/InputCounter.vue","InputCounter.vue"],"names":[],"mappings":"AAwJA;EACA,WAAA;ACvJA;ADyJA;EACA,aAAA;EACA,iBAAA;EACA,kCAAA;EACA,kBAAA;EACA,iCAAA;ACvJA;ADyJA;EACA,iCAAA;ACvJA;AD0JA;EACA,gCAAA;ACxJA;AD2JA;EACA,iBAAA;ACzJA;AD8JA;EACA,8BAAA;EACA,kCAAA;AC5JA;AD+JA;EACA,YAAA;EACA,uBAAA;EACA,gBAAA;AC7JA;AD+JA;EACA,uBAAA;AC7JA;ADgKA;EACA,6BAAA;AC9JA;ADiKA;EACA,kBAAA;EACA,0BAAA;EACA,6BAAA;AC/JA;ADkKA;EACA,iBAAA;EACA,yBAAA;EACA,4BAAA;AChKA;ADmKA;EACA,YAAA;ACjKA;ADoKA;EACA,YAAA;AClKA;ADqKA;EACA,YAAA;ACnKA;ADwKA;EACA,WAAA;ACtKA;ADwKA;EACA,aAAA;ACtKA;ADyKA;EACA,YAAA;EACA,gBAAA;EACA,kBAAA;ACvKA;ADyKA;EACA,YAAA;ACvKA;AD0KA;EACA,YAAA;ACxKA;AD2KA;EACA,YAAA;ACzKA;;AAEA,2CAA2C","file":"InputCounter.vue","sourcesContent":["<template>\n  <div\n    class=\"me-input-counter\"\n    data-cy=\"me-input-counter\"\n  >\n    <div\n      :class=\"{\n        'disabled': disabled,\n        'focused': focused,\n        'is-invalid': state === false\n      }\"\n      class=\"input-wrapper\"\n    >\n      <MeButton\n        :size=\"size\"\n        :disabled=\"disableSub\"\n        @click=\"subtract\"\n        icon=\"me-icon-l icon-minus\"\n        variant=\"primary\"\n      />\n\n      <MeNumberInput\n        v-model=\"current\"\n        :disabled=\"disabled\"\n        :size=\"size\"\n        :placeholder=\"disabled ? disabledPlaceholder : ''\"\n        :value=\"value\"\n        :max=\"max\"\n        :min=\"min\"\n        :precision=\"precision\"\n        @input=\"onChange\"\n        @focus=\"onFocus\"\n        @blur=\"onBlur\"\n      />\n\n      <MeButton\n        :size=\"size\"\n        :disabled=\"disableAdd\"\n        @click=\"add\"\n        icon=\"me-icon-l icon-plus\"\n        variant=\"primary\"\n      />\n    </div>\n  </div>\n</template>\n\n<script>\nimport MeNumberInput from './NumberInput.vue'\nimport MeButton from './Button.vue'\n\nexport default {\n  name: 'MeInputCounter',\n  components: {\n    MeNumberInput,\n    MeButton\n  },\n\n  props: {\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    disabledAdd: {\n      type: Boolean,\n      default: false\n    },\n    disabledSubtract: {\n      type: Boolean,\n      default: false\n    },\n    disabledPlaceholder: {\n      type: String,\n      default: ''\n    },\n    max: {\n      type: Number,\n      default: 999999999\n    },\n    min: {\n      type: Number,\n      default: -999999999\n    },\n    precision: {\n      type: Number,\n      default: 0\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    value: {\n      type: Number,\n      default: 0\n    }\n  },\n\n  data() {\n    return {\n      current: this.value,\n      focused: false\n    }\n  },\n\n  computed: {\n    disableSub() {\n      return this.disabled || this.current === this.min || this.disabledSubtract\n    },\n\n    disableAdd() {\n      return this.disabled || this.current >= this.max || this.disabledAdd\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    }\n  },\n\n  methods: {\n    add() {\n      if (this.current + 1 <= this.max) this.current++\n      this.$emit('add', this.current)\n    },\n\n    subtract() {\n      if (this.current - 1 >= this.min) this.current--\n      this.$emit('subtract', this.current)\n    },\n\n    onFocus() {\n      this.focused = true\n      this.$emit('focus', this.current)\n    },\n\n    onBlur() {\n      this.focused = false\n      this.$emit('blur', this.current)\n    },\n\n    onChange() {\n      this.$emit('input', this.current)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-input-counter {\n  width: 100%;\n\n  .input-wrapper {\n    display: flex;\n    flex-wrap: nowrap;\n    border: 1px solid var(--me-gray-6);\n    border-radius: 9px;\n    background-color: var(--me-white);\n\n    &.focused {\n      border-color: var(--me-primary-2);\n    }\n\n    &.is-invalid {\n      border-color: var(--me-danger-2);\n\n      ::v-deep {\n        .me-number-input input {\n          caret-color: auto;\n        }\n      }\n    }\n\n    &.disabled {\n      border-color: var(--me-gray-4);\n      background-color: var(--me-gray-6);\n    }\n\n    button {\n      border: none;\n      color: var(--me-gray-3);\n      transition: none;\n\n      &:disabled {\n        color: var(--me-gray-4);\n      }\n\n      &:not(:disabled) {\n        background-color: transparent;\n      }\n\n      &:first-child {\n        border-right: none;\n        border-top-right-radius: 0;\n        border-bottom-right-radius: 0;\n      }\n\n      &:last-child {\n        border-left: none;\n        border-top-left-radius: 0;\n        border-bottom-left-radius: 0;\n      }\n\n      &.btn-sm {\n        height: 30px;\n      }\n\n      &.btn-md {\n        height: 38px;\n      }\n\n      &.btn-lg {\n        height: 46px;\n      }\n    }\n\n    ::v-deep {\n      .me-number-input {\n        width: 100%;\n\n        .input-icons {\n          display: none;\n        }\n\n        input {\n          border: none;\n          border-radius: 0;\n          text-align: center;\n\n          &.sm {\n            height: 30px;\n          }\n\n          &.md {\n            height: 38px;\n          }\n\n          &.lg {\n            height: 46px;\n          }\n        }\n      }\n    }\n  }\n}\n</style>\n",".me-input-counter {\n  width: 100%;\n}\n.me-input-counter .input-wrapper {\n  display: flex;\n  flex-wrap: nowrap;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 9px;\n  background-color: var(--me-white);\n}\n.me-input-counter .input-wrapper.focused {\n  border-color: var(--me-primary-2);\n}\n.me-input-counter .input-wrapper.is-invalid {\n  border-color: var(--me-danger-2);\n}\n.me-input-counter .input-wrapper.is-invalid ::v-deep .me-number-input input {\n  caret-color: auto;\n}\n.me-input-counter .input-wrapper.disabled {\n  border-color: var(--me-gray-4);\n  background-color: var(--me-gray-6);\n}\n.me-input-counter .input-wrapper button {\n  border: none;\n  color: var(--me-gray-3);\n  transition: none;\n}\n.me-input-counter .input-wrapper button:disabled {\n  color: var(--me-gray-4);\n}\n.me-input-counter .input-wrapper button:not(:disabled) {\n  background-color: transparent;\n}\n.me-input-counter .input-wrapper button:first-child {\n  border-right: none;\n  border-top-right-radius: 0;\n  border-bottom-right-radius: 0;\n}\n.me-input-counter .input-wrapper button:last-child {\n  border-left: none;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n}\n.me-input-counter .input-wrapper button.btn-sm {\n  height: 30px;\n}\n.me-input-counter .input-wrapper button.btn-md {\n  height: 38px;\n}\n.me-input-counter .input-wrapper button.btn-lg {\n  height: 46px;\n}\n.me-input-counter .input-wrapper ::v-deep .me-number-input {\n  width: 100%;\n}\n.me-input-counter .input-wrapper ::v-deep .me-number-input .input-icons {\n  display: none;\n}\n.me-input-counter .input-wrapper ::v-deep .me-number-input input {\n  border: none;\n  border-radius: 0;\n  text-align: center;\n}\n.me-input-counter .input-wrapper ::v-deep .me-number-input input.sm {\n  height: 30px;\n}\n.me-input-counter .input-wrapper ::v-deep .me-number-input input.md {\n  height: 38px;\n}\n.me-input-counter .input-wrapper ::v-deep .me-number-input input.lg {\n  height: 46px;\n}\n\n/*# sourceMappingURL=InputCounter.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1K = "data-v-0783a6fa";
  /* module identifier */
  const __vue_module_identifier__$1K = undefined;
  /* functional template */
  const __vue_is_functional_template__$1K = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1K, staticRenderFns: __vue_staticRenderFns__$1K },
    __vue_inject_styles__$1K,
    __vue_script__$1K,
    __vue_scope_id__$1K,
    __vue_is_functional_template__$1K,
    __vue_module_identifier__$1K,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1L = {
  name: 'MeInputTag',
  components: {
    BFormTags,
    BFormTag,
    BFormInput,
    BInputGroup,
    BInputGroupAppend
  },
  props: {
    separator: {
      type: String,
      default: ',; '
    },
    placeholder: {
      type: String,
      default: ''
    },
    buttonAdd: {
      type: String,
      default: ''
    },
    errorlimitCharacter: {
      type: String,
      default: ''
    },
    errorduplicateTags: {
      type: String,
      default: ''
    },
    characterLimit: {
      type: Number,
      default: null
    },
    textInfo: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      value: [],
      validTags: [],
      invalidTags: [],
      duplicateTags: []
    }
  },
  computed: {
    validatorDuplicate() {
      return this.duplicateTags.length
    },
    validatorLimit() {
      return this.invalidTags.length === 1
    }
  },
  methods: {
    onTagState(valid, invalid, duplicate) {
      this.validTags = valid;
      this.invalidTags = invalid;
      this.duplicateTags = duplicate;
    },
    tagValidator(tag) {
      return tag.length > 0 && tag.length < this.characterLimit
    }
  }
};

/* script */
const __vue_script__$1L = script$1L;

/* template */
var __vue_render__$1L = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _c("BFormTags", {
        staticClass: "me-input-tag",
        attrs: {
          separator: _vm.separator,
          "tag-validator": _vm.tagValidator,
          placeholder: _vm.placeholder,
          "tag-variant": "primary",
          "tag-pills": "",
          "remove-on-delete": ""
        },
        on: { "tag-state": _vm.onTagState },
        scopedSlots: _vm._u([
          {
            key: "default",
            fn: function(ref) {
              var tags = ref.tags;
              var inputAttrs = ref.inputAttrs;
              var inputHandlers = ref.inputHandlers;
              var tagVariant = ref.tagVariant;
              var addTag = ref.addTag;
              var removeTag = ref.removeTag;
              return [
                _c(
                  "BInputGroup",
                  { staticClass: "mb-2" },
                  [
                    _c(
                      "BFormInput",
                      _vm._g(
                        _vm._b(
                          {
                            staticClass: "form-control",
                            attrs: { placeholder: _vm.placeholder }
                          },
                          "BFormInput",
                          inputAttrs,
                          false
                        ),
                        inputHandlers
                      )
                    ),
                    _vm._v(" "),
                    _c(
                      "BInputGroupAppend",
                      [
                        _c(
                          "me-button",
                          {
                            attrs: { variant: "primary", size: "sm" },
                            on: {
                              click: function($event) {
                                return addTag()
                              }
                            }
                          },
                          [
                            _c("i", { staticClass: "me-icon icon-plus" }),
                            _vm._v(" " + _vm._s(_vm.buttonAdd) + "\n          ")
                          ]
                        )
                      ],
                      1
                    )
                  ],
                  1
                ),
                _vm._v(" "),
                _c(
                  "div",
                  {
                    staticClass: "d-inline-block",
                    staticStyle: { "font-size": "1.5rem" }
                  },
                  _vm._l(tags, function(tag) {
                    return _c(
                      "BFormTag",
                      {
                        key: tag,
                        staticClass: "mr-1",
                        attrs: { title: tag, variant: tagVariant },
                        on: {
                          remove: function($event) {
                            return removeTag(tag)
                          }
                        }
                      },
                      [_vm._v("\n          " + _vm._s(tag) + "\n        ")]
                    )
                  }),
                  1
                )
              ]
            }
          }
        ]),
        model: {
          value: _vm.value,
          callback: function($$v) {
            _vm.value = $$v;
          },
          expression: "value"
        }
      }),
      _vm._v(" "),
      _c("span", { staticClass: "text-input" }, [
        _vm._v("\n    " + _vm._s(_vm.textInfo) + "\n  ")
      ]),
      _vm._v(" "),
      _vm.validatorLimit
        ? _c("span", { staticClass: "alert-error" }, [
            _c("i", { staticClass: "me-icon icon-exclamation-triangle" }),
            _vm._v(" " + _vm._s(_vm.errorlimitCharacter) + "\n  ")
          ])
        : _vm._e(),
      _vm._v(" "),
      _vm.validatorDuplicate
        ? _c("span", { staticClass: "alert-error" }, [
            _c("i", { staticClass: "me-icon icon-exclamation-triangle" }),
            _vm._v(
              _vm._s(_vm.errorduplicateTags) +
                " " +
                _vm._s(_vm.duplicateTags) +
                "\n  "
            )
          ])
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$1L = [];
__vue_render__$1L._withStripped = true;

  /* style */
  const __vue_inject_styles__$1L = function (inject) {
    if (!inject) return
    inject("data-v-17691131_0", { source: "[data-v-17691131] div.me-input-tag {\n  font-size: 14px;\n  line-height: 0;\n  margin-top: 10px;\n  padding: 0;\n}\n[data-v-17691131] div.me-input-tag:focus {\n  box-shadow: 0 0 0 0.2rem rgba(10, 140, 204, 0.25);\n}\n[data-v-17691131] div.me-input-tag div.input-group {\n  margin: 0 !important;\n}\n[data-v-17691131] div.me-input-tag div.input-group input {\n  border: none;\n  height: 34px;\n  outline: none;\n}\n[data-v-17691131] div.me-input-tag div.input-group input:focus {\n  box-shadow: none;\n}\n.b-form-tag[data-v-17691131] {\n  background-color: rgba(10, 140, 204, 0.24);\n  color: var(--me-primary-2);\n  font-size: 15px;\n  font-weight: normal;\n  margin: 5px;\n  padding: 0px 5px 6px;\n}\n.b-form-tag .b-form-tag-remove[data-v-17691131] {\n  color: var(--me-primary-2);\n  font-size: 24px;\n}\n.b-form-tags .d-inline-block[data-v-17691131] {\n  max-height: 74px !important;\n  overflow-y: auto;\n  border-top: 1px solid rgba(0, 0, 0, 0.12);\n  width: 100%;\n  margin-top: 5px;\n}\n.b-form-tags .input-group-append[data-v-17691131] {\n  height: 40px;\n  margin-top: -8px;\n}\n.me-input-file[data-v-17691131] {\n  border-radius: 4px;\n  height: 90px;\n}\n.me-checkbox label[data-v-17691131] {\n  color: rgba(0, 0, 0, 0.56);\n  font-size: 14px;\n}\n.text-input[data-v-17691131] {\n  color: var(--me-gray-4);\n  display: block;\n  font-size: 12px;\n  margin-bottom: 0;\n}\nbutton.me-button[data-v-17691131] {\n  font-size: 14px;\n  height: 34px;\n  margin: 11px 2px 0 0;\n}\n.alert-error[data-v-17691131] {\n  color: var(--me-danger-2);\n  display: block;\n  font-size: 12px;\n  margin-top: 5px;\n}\n\n/*# sourceMappingURL=InputTag.vue.map */", map: {"version":3,"sources":["/app/build/src/components/InputTag.vue","InputTag.vue"],"names":[],"mappings":"AA6HA;EACA,eAAA;EACA,cAAA;EACA,gBAAA;EACA,UAAA;AC5HA;AD8HA;EACA,iDAAA;AC5HA;AD+HA;EACA,oBAAA;AC7HA;AD+HA;EACA,YAAA;EACA,YAAA;EACA,aAAA;AC7HA;AD+HA;EACA,gBAAA;AC7HA;ADmIA;EACA,0CAAA;EACA,0BAAA;EACA,eAAA;EACA,mBAAA;EACA,WAAA;EACA,oBAAA;AChIA;ADkIA;EACA,0BAAA;EACA,eAAA;AChIA;ADqIA;EACA,2BAAA;EACA,gBAAA;EACA,yCAAA;EACA,WAAA;EACA,eAAA;AClIA;ADqIA;EACA,YAAA;EACA,gBAAA;ACnIA;ADuIA;EACA,kBAAA;EACA,YAAA;ACpIA;ADwIA;EACA,0BAAA;EACA,eAAA;ACrIA;ADyIA;EACA,uBAAA;EACA,cAAA;EACA,eAAA;EACA,gBAAA;ACtIA;ADyIA;EACA,eAAA;EACA,YAAA;EACA,oBAAA;ACtIA;ADyIA;EACA,yBAAA;EACA,cAAA;EACA,eAAA;EACA,eAAA;ACtIA;;AAEA,uCAAuC","file":"InputTag.vue","sourcesContent":["<template>\n  <div>\n    <BFormTags\n      v-model=\"value\"\n      :separator=\"separator\"\n      :tag-validator=\"tagValidator\"\n      :placeholder=\"placeholder\"\n      @tag-state=\"onTagState\"\n      tag-variant=\"primary\"\n      tag-pills\n      class=\"me-input-tag\"\n      remove-on-delete\n    >\n      <template v-slot=\"{ tags, inputAttrs, inputHandlers, tagVariant, addTag, removeTag }\">\n        <BInputGroup class=\"mb-2\">\n          <BFormInput\n            v-bind=\"inputAttrs\"\n            v-on=\"inputHandlers\"\n            :placeholder=\"placeholder\"\n            class=\"form-control\"\n          />\n          <BInputGroupAppend>\n            <me-button @click=\"addTag()\" variant=\"primary\" size=\"sm\">\n              <i class=\"me-icon icon-plus\" /> {{ buttonAdd }}\n            </me-button>\n          </BInputGroupAppend>\n        </BInputGroup>\n        <div class=\"d-inline-block\" style=\"font-size: 1.5rem;\">\n          <BFormTag\n            v-for=\"tag in tags\"\n            @remove=\"removeTag(tag)\"\n            :key=\"tag\"\n            :title=\"tag\"\n            :variant=\"tagVariant\"\n            class=\"mr-1\"\n          >\n            {{ tag }}\n          </BFormTag>\n        </div>\n      </template>\n    </BFormTags>\n    <span class=\"text-input\">\n      {{ textInfo }}\n    </span>\n    <span v-if=\"validatorLimit\" class=\"alert-error\">\n      <i class=\"me-icon icon-exclamation-triangle\" /> {{ errorlimitCharacter }}\n    </span>\n    <span v-if=\"validatorDuplicate\" class=\"alert-error\">\n      <i class=\"me-icon icon-exclamation-triangle\" />{{ errorduplicateTags }} {{ duplicateTags }}\n    </span>\n  </div>\n</template>\n\n<script>\nimport { BFormTags, BFormTag, BFormInput, BInputGroup, BInputGroupAppend } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeInputTag',\n  components: {\n    BFormTags,\n    BFormTag,\n    BFormInput,\n    BInputGroup,\n    BInputGroupAppend\n  },\n  props: {\n    separator: {\n      type: String,\n      default: ',; '\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    buttonAdd: {\n      type: String,\n      default: ''\n    },\n    errorlimitCharacter: {\n      type: String,\n      default: ''\n    },\n    errorduplicateTags: {\n      type: String,\n      default: ''\n    },\n    characterLimit: {\n      type: Number,\n      default: null\n    },\n    textInfo: {\n      type: String,\n      default: null\n    }\n  },\n  data() {\n    return {\n      value: [],\n      validTags: [],\n      invalidTags: [],\n      duplicateTags: []\n    }\n  },\n  computed: {\n    validatorDuplicate() {\n      return this.duplicateTags.length\n    },\n    validatorLimit() {\n      return this.invalidTags.length === 1\n    }\n  },\n  methods: {\n    onTagState(valid, invalid, duplicate) {\n      this.validTags = valid\n      this.invalidTags = invalid\n      this.duplicateTags = duplicate\n    },\n    tagValidator(tag) {\n      return tag.length > 0 && tag.length < this.characterLimit\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  ::v-deep div.me-input-tag {\n    font-size: 14px;\n    line-height: 0;\n    margin-top: 10px;\n    padding: 0;\n\n    &:focus {\n      box-shadow: 0 0 0 0.2rem rgba(10, 140, 204, 0.25);\n    }\n\n    div.input-group {\n      margin: 0 !important;\n\n      input {\n        border: none;\n        height: 34px;\n        outline: none;\n\n        &:focus {\n          box-shadow: none;\n        }\n      }\n    }\n  }\n\n  .b-form-tag {\n    background-color: rgba(10, 140, 204, 0.24);\n    color: var(--me-primary-2);\n    font-size: 15px;\n    font-weight: normal;\n    margin: 5px;\n    padding: 0px 5px 6px;\n\n    .b-form-tag-remove {\n      color: var(--me-primary-2);\n      font-size: 24px;\n    }\n  }\n\n  .b-form-tags {\n    .d-inline-block {\n      max-height: 74px !important;\n      overflow-y: auto;\n      border-top: 1px solid rgba(0, 0, 0, 0.12);\n      width: 100%;\n      margin-top: 5px;\n    }\n\n    .input-group-append {\n      height: 40px;\n      margin-top: -8px;\n    }\n  }\n\n.me-input-file {\n    border-radius: 4px;\n    height: 90px;\n  }\n\n  .me-checkbox {\n    label {\n      color: rgba(0,0,0,0.56);\n      font-size: 14px;\n    }\n  }\n\n  .text-input {\n    color: var(--me-gray-4);\n    display: block;\n    font-size: 12px;\n    margin-bottom: 0;\n  }\n\n  button.me-button {\n    font-size: 14px;\n    height: 34px;\n    margin: 11px 2px 0 0;\n  }\n\n  .alert-error {\n    color: var(--me-danger-2);\n    display: block;\n    font-size: 12px;\n    margin-top: 5px;\n  }\n</style>\n","::v-deep div.me-input-tag {\n  font-size: 14px;\n  line-height: 0;\n  margin-top: 10px;\n  padding: 0;\n}\n::v-deep div.me-input-tag:focus {\n  box-shadow: 0 0 0 0.2rem rgba(10, 140, 204, 0.25);\n}\n::v-deep div.me-input-tag div.input-group {\n  margin: 0 !important;\n}\n::v-deep div.me-input-tag div.input-group input {\n  border: none;\n  height: 34px;\n  outline: none;\n}\n::v-deep div.me-input-tag div.input-group input:focus {\n  box-shadow: none;\n}\n\n.b-form-tag {\n  background-color: rgba(10, 140, 204, 0.24);\n  color: var(--me-primary-2);\n  font-size: 15px;\n  font-weight: normal;\n  margin: 5px;\n  padding: 0px 5px 6px;\n}\n.b-form-tag .b-form-tag-remove {\n  color: var(--me-primary-2);\n  font-size: 24px;\n}\n\n.b-form-tags .d-inline-block {\n  max-height: 74px !important;\n  overflow-y: auto;\n  border-top: 1px solid rgba(0, 0, 0, 0.12);\n  width: 100%;\n  margin-top: 5px;\n}\n.b-form-tags .input-group-append {\n  height: 40px;\n  margin-top: -8px;\n}\n\n.me-input-file {\n  border-radius: 4px;\n  height: 90px;\n}\n\n.me-checkbox label {\n  color: rgba(0, 0, 0, 0.56);\n  font-size: 14px;\n}\n\n.text-input {\n  color: var(--me-gray-4);\n  display: block;\n  font-size: 12px;\n  margin-bottom: 0;\n}\n\nbutton.me-button {\n  font-size: 14px;\n  height: 34px;\n  margin: 11px 2px 0 0;\n}\n\n.alert-error {\n  color: var(--me-danger-2);\n  display: block;\n  font-size: 12px;\n  margin-top: 5px;\n}\n\n/*# sourceMappingURL=InputTag.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1L = "data-v-17691131";
  /* module identifier */
  const __vue_module_identifier__$1L = undefined;
  /* functional template */
  const __vue_is_functional_template__$1L = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1L, staticRenderFns: __vue_staticRenderFns__$1L },
    __vue_inject_styles__$1L,
    __vue_script__$1L,
    __vue_scope_id__$1L,
    __vue_is_functional_template__$1L,
    __vue_module_identifier__$1L,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1M = {
  name: 'MeLayout',
  props: {
    showHeader: {
      type: Boolean,
      default: true
    },
    showSubHeader: {
      type: Boolean,
      required: false
    },
    showLeft: {
      type: Boolean,
      required: false
    },
    responsive: {
      type: Boolean,
      required: false
    },
    showRight: {
      type: Boolean,
      required: false
    },
    showFooter: {
      type: Boolean,
      required: false
    },
    embedded: {
      type: Boolean,
      required: false
    },
    fixed: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      leftOpened: false
    }
  },
  methods: {
    openLeft() {
      this.leftOpened = true;
    },
    closeAll() {
      this.leftOpened = false;
    }
  }
};

/* script */
const __vue_script__$1M = script$1M;

/* template */
var __vue_render__$1M = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-layout",
      class: {
        header: _vm.showHeader,
        "sub-header": _vm.showSubHeader,
        left: _vm.showLeft,
        "left-opened": _vm.leftOpened,
        responsive: _vm.responsive,
        right: _vm.showRight,
        footer: _vm.showFooter,
        embedded: _vm.embedded,
        fixed: _vm.fixed
      }
    },
    [
      _vm.showHeader
        ? _c(
            "div",
            { staticClass: "header" },
            [
              _c("a", {
                staticClass: "left-menu",
                on: { click: _vm.openLeft }
              }),
              _vm._v(" "),
              _vm._t("header")
            ],
            2
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.showSubHeader
        ? _c("div", { staticClass: "sub-header" }, [_vm._t("sub-header")], 2)
        : _vm._e(),
      _vm._v(" "),
      _c("main", [
        _vm.showLeft
          ? _c("div", { staticClass: "left" }, [
              _c("div", { staticClass: "left-content" }, [_vm._t("left")], 2)
            ])
          : _vm._e(),
        _vm._v(" "),
        _c("div", { staticClass: "content" }, [_vm._t("content")], 2),
        _vm._v(" "),
        _vm.showRight
          ? _c("div", { staticClass: "right" }, [_vm._t("right")], 2)
          : _vm._e()
      ]),
      _vm._v(" "),
      _vm.showFooter
        ? _c("div", { staticClass: "footer" }, [_vm._t("footer")], 2)
        : _vm._e(),
      _vm._v(" "),
      _c("div", { staticClass: "backdrop", on: { click: _vm.closeAll } })
    ]
  )
};
var __vue_staticRenderFns__$1M = [];
__vue_render__$1M._withStripped = true;

  /* style */
  const __vue_inject_styles__$1M = function (inject) {
    if (!inject) return
    inject("data-v-9ea052c6_0", { source: "div.me-layout[data-v-9ea052c6] {\n  background: #f2f3f5;\n  bottom: 0;\n  left: 0;\n  position: absolute;\n  right: 0;\n  top: 0;\n}\ndiv.me-layout > div.backdrop[data-v-9ea052c6] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1038;\n}\ndiv.me-layout > div.header[data-v-9ea052c6] {\n  display: none;\n  left: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 10;\n}\ndiv.me-layout > div.header > a.left-menu[data-v-9ea052c6] {\n  cursor: pointer;\n  display: none;\n  height: 64px;\n  left: 0;\n  position: absolute;\n  top: 0px;\n  width: 50px;\n  z-index: 1;\n}\ndiv.me-layout > div.sub-header[data-v-9ea052c6],\ndiv.me-layout > div.footer[data-v-9ea052c6] {\n  align-items: center;\n  background-color: var(--me-white);\n  display: none;\n  left: 0;\n  padding-left: 16px;\n  padding-right: 16px;\n  position: fixed;\n  right: 0;\n  z-index: 9;\n}\ndiv.me-layout > div.sub-header[data-v-9ea052c6] {\n  height: 54px;\n  border-bottom: 1px solid var(--me-gray-6);\n}\ndiv.me-layout > div.footer[data-v-9ea052c6] {\n  bottom: 0;\n  box-shadow: var(--me-shadow-level-1);\n  height: 65px;\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6],\ndiv.me-layout > main > div.right[data-v-9ea052c6] {\n  background-color: var(--me-white);\n  bottom: 0;\n  box-shadow: var(--me-shadow-level-1);\n  display: none;\n  padding: 0;\n  position: fixed;\n  top: 0;\n  width: 256px;\n  z-index: 8;\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6] {\n  overflow: hidden;\n  left: 0;\n  padding: 0;\n  border-right: 1px solid var(--me-gray-6);\n  width: 256px;\n}\ndiv.me-layout > main > div.left .left-content[data-v-9ea052c6] {\n  width: 256px;\n  height: 100%;\n  min-height: 100%;\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6]:hover {\n  overflow: auto;\n  overflow-x: hidden;\n  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n  scrollbar-width: thin;\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6]:hover::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6]:hover::-webkit-scrollbar-track {\n  border-radius: 15px;\n  background: rgba(0, 0, 0, 0.1);\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6]:hover::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\ndiv.me-layout > main > div.left[data-v-9ea052c6]:hover::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\ndiv.me-layout > main > div.right[data-v-9ea052c6] {\n  right: 0;\n  border-left: 1px solid var(--me-gray-6);\n  overflow: auto;\n}\ndiv.me-layout > main > div.content[data-v-9ea052c6] {\n  background: #f2f3f5;\n}\ndiv.me-layout.header > div.header[data-v-9ea052c6] {\n  display: block;\n}\ndiv.me-layout.header > main[data-v-9ea052c6] {\n  margin-top: 64px;\n}\ndiv.me-layout.header > main > div.left[data-v-9ea052c6],\ndiv.me-layout.header > main > div.right[data-v-9ea052c6] {\n  top: 64px;\n}\ndiv.me-layout.header.fixed > main > div.content[data-v-9ea052c6] {\n  top: 64px;\n}\n@media screen {\ndiv.me-layout.sub-header > div.sub-header[data-v-9ea052c6] {\n    display: flex;\n    top: 0;\n}\ndiv.me-layout.sub-header > main[data-v-9ea052c6] {\n    margin-top: 54px;\n}\ndiv.me-layout.sub-header > main > div.left[data-v-9ea052c6],\ndiv.me-layout.sub-header > main > div.right[data-v-9ea052c6] {\n    top: 54px;\n}\ndiv.me-layout.sub-header.fixed > main > div.content[data-v-9ea052c6] {\n    top: 54px;\n}\ndiv.me-layout.header.sub-header > div.sub-header[data-v-9ea052c6] {\n    display: flex;\n    top: 64px;\n}\ndiv.me-layout.header.sub-header > main[data-v-9ea052c6] {\n    margin-top: 118px;\n}\ndiv.me-layout.header.sub-header > main > div.left[data-v-9ea052c6],\ndiv.me-layout.header.sub-header > main > div.right[data-v-9ea052c6] {\n    top: 118px;\n}\ndiv.me-layout.header.sub-header.fixed > main > div.content[data-v-9ea052c6] {\n    top: 118px;\n}\ndiv.me-layout.footer > div.footer[data-v-9ea052c6] {\n    display: flex;\n    justify-content: center;\n}\ndiv.me-layout.footer > main[data-v-9ea052c6], div.me-layout.footer div.me-layout > main[data-v-9ea052c6] {\n    margin-bottom: 65px;\n}\ndiv.me-layout.footer > main > div.left[data-v-9ea052c6],\ndiv.me-layout.footer > main > div.right[data-v-9ea052c6], div.me-layout.footer div.me-layout > main > div.left[data-v-9ea052c6],\ndiv.me-layout.footer div.me-layout > main > div.right[data-v-9ea052c6] {\n    bottom: 65px;\n}\ndiv.me-layout.footer.fixed > main > div.content[data-v-9ea052c6], div.me-layout.footer div.me-layout.fixed > main > div.content[data-v-9ea052c6] {\n    bottom: 65px;\n}\n}\n@media (min-width: 769px) {\ndiv.me-layout.left > main > div.left[data-v-9ea052c6] {\n    display: block;\n}\ndiv.me-layout.left > main > div.content[data-v-9ea052c6] {\n    margin-left: 256px;\n}\ndiv.me-layout.right > main > div.right[data-v-9ea052c6] {\n    display: block;\n}\ndiv.me-layout.right > main > div.content[data-v-9ea052c6] {\n    margin-right: 256px;\n}\n}\n@media (max-width: 768px) {\ndiv.me-layout.responsive > div.header > a.left-menu[data-v-9ea052c6] {\n    display: block;\n}\ndiv.me-layout.responsive > main > div.left[data-v-9ea052c6] {\n    bottom: 0 !important;\n    display: block;\n    left: -256px;\n    top: 0 !important;\n    transition-duration: 0.2s;\n    transition: left;\n    z-index: 1039;\n}\ndiv.me-layout.left-opened > main > div.left[data-v-9ea052c6] {\n    left: 0;\n}\ndiv.me-layout.left-opened > div.backdrop[data-v-9ea052c6] {\n    display: block;\n    opacity: 0.4;\n}\n}\ndiv.me-layout.embedded > div.header[data-v-9ea052c6] {\n  position: absolute;\n}\ndiv.me-layout.embedded > div.sub-header[data-v-9ea052c6],\ndiv.me-layout.embedded > div.footer[data-v-9ea052c6] {\n  position: absolute;\n}\ndiv.me-layout.embedded > main > div.left[data-v-9ea052c6],\ndiv.me-layout.embedded > main > div.right[data-v-9ea052c6] {\n  position: absolute;\n}\ndiv.me-layout.embedded > div.backdrop[data-v-9ea052c6] {\n  position: absolute;\n}\ndiv.me-layout.embedded.fixed > main > div.content[data-v-9ea052c6] {\n  position: absolute;\n}\ndiv.me-layout.fixed > main > div.content[data-v-9ea052c6] {\n  bottom: 0;\n  left: 0;\n  overflow: auto;\n  position: fixed;\n  right: 0;\n  top: 0;\n}\n\n/*# sourceMappingURL=Layout.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Layout.vue","Layout.vue"],"names":[],"mappings":"AAsGA;EACA,mBAAA;EACA,SAAA;EACA,OAAA;EACA,kBAAA;EACA,QAAA;EACA,MAAA;ACrGA;ADuGA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,yBAAA;EACA,mBAAA;EACA,aAAA;ACrGA;ADwGA;EACA,aAAA;EACA,OAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,WAAA;ACtGA;ADwGA;EACA,eAAA;EACA,aAAA;EACA,YAtCA;EAuCA,OAAA;EACA,kBAAA;EACA,QAAA;EACA,WAAA;EACA,UAAA;ACtGA;AD0GA;;EAEA,mBAAA;EACA,iCAAA;EACA,aAAA;EACA,OAAA;EACA,kBAAA;EACA,mBAAA;EACA,eAAA;EACA,QAAA;EACA,UAAA;ACxGA;AD2GA;EACA,YA1DA;EA2DA,yCAAA;ACzGA;AD4GA;EACA,SAAA;EACA,oCAAA;EACA,YArEA;ACrCA;AD8GA;;EAEA,iCAAA;EACA,SAAA;EACA,oCAAA;EACA,aAAA;EACA,UAAA;EACA,eAAA;EACA,MAAA;EACA,YAhFA;EAiFA,UAAA;AC5GA;AD+GA;EACA,gBAAA;EACA,OAAA;EACA,UAAA;EACA,wCAAA;EACA,YAzFA;ACpBA;AD+GA;EACA,YA5FA;EA6FA,YAAA;EACA,gBAAA;AC7GA;ADgHA;EACA,cAAA;EACA,kBAAA;EACA,gDAAA;EACA,qBAAA;AC9GA;ADgHA;EACA,UAvGA;EAwGA,WAAA;AC9GA;ADgHA;EACA,mBAAA;EACA,8BAAA;AC9GA;ADiHA;EACA,+BAAA;EACA,mBAAA;AC/GA;ADiHA;EACA,+BAAA;AC/GA;ADsHA;EACA,QAAA;EACA,uCAAA;EACA,cAAA;ACpHA;ADuHA;EACA,mBAAA;ACrHA;AD0HA;EACA,cAAA;ACxHA;AD2HA;EACA,gBA9IA;ACqBA;AD0HA;;EAEA,SAjJA;ACyBA;AD8HA;EACA,SAxJA;AC4BA;ADkIA;AAEA;IACA,aAAA;IACA,MAAA;ACjIE;ADoIF;IACA,gBAnKA;ACiCE;ADoIF;;IAEA,SAvKA;ACqCE;ADwIF;IACA,SA9KA;ACwCE;AD6IF;IACA,aAAA;IACA,SA1LA;AC+CE;AD8IF;IACA,iBAAA;AC5IE;AD8IF;;IAEA,UAAA;AC5IE;ADkJF;IACA,UAAA;AChJE;ADuJF;IACA,aAAA;IACA,uBAAA;ACrJE;ADyJF;IACA,mBAxNA;ACiEE;ADyJF;;;IAEA,YA5NA;ACsEE;AD2JF;IACA,YAlOA;ACyEE;AACF;AD+JA;AAGA;IACA,cAAA;AC/JE;ADkKF;IACA,kBA/OA;AC+EE;ADuKF;IACA,cAAA;ACrKE;ADwKF;IACA,mBA3PA;ACqFE;AACF;AD2KA;AAGA;IACA,cAAA;AC3KE;ADgLF;IACA,oBAAA;IACA,cAAA;IACA,YAAA;IACA,iBAAA;IACA,yBAAA;IACA,gBAAA;IACA,aAAA;AC9KE;ADqLF;IACA,OAAA;ACnLE;ADuLF;IACA,cAAA;IACA,YAAA;ACrLE;AACF;AD0LA;EACA,kBAAA;ACxLA;AD2LA;;EAEA,kBAAA;ACzLA;AD6LA;;EAEA,kBAAA;AC3LA;AD+LA;EACA,kBAAA;AC7LA;ADiMA;EACA,kBAAA;AC/LA;ADqMA;EACA,SAAA;EACA,OAAA;EACA,cAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;ACnMA;;AAEA,qCAAqC","file":"Layout.vue","sourcesContent":["<template>\n  <div\n    :class=\"{\n      header: showHeader,\n      'sub-header': showSubHeader,\n      left: showLeft,\n      'left-opened': leftOpened,\n      responsive: responsive,\n      right: showRight,\n      footer: showFooter,\n      embedded: embedded,\n      fixed: fixed\n    }\"\n    class=\"me-layout\"\n  >\n    <div v-if=\"showHeader\" class=\"header\">\n      <a @click=\"openLeft\" class=\"left-menu\" />\n      <slot name=\"header\" />\n    </div>\n    <div v-if=\"showSubHeader\" class=\"sub-header\">\n      <slot name=\"sub-header\" />\n    </div>\n    <main>\n      <div v-if=\"showLeft\" class=\"left\">\n        <div class=\"left-content\">\n          <slot name=\"left\" />\n        </div>\n      </div>\n      <div class=\"content\">\n        <slot name=\"content\" />\n      </div>\n      <div v-if=\"showRight\" class=\"right\">\n        <slot name=\"right\" />\n      </div>\n    </main>\n    <div v-if=\"showFooter\" class=\"footer\">\n      <slot name=\"footer\" />\n    </div>\n    <div @click=\"closeAll\" class=\"backdrop\" />\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeLayout',\n  props: {\n    showHeader: {\n      type: Boolean,\n      default: true\n    },\n    showSubHeader: {\n      type: Boolean,\n      required: false\n    },\n    showLeft: {\n      type: Boolean,\n      required: false\n    },\n    responsive: {\n      type: Boolean,\n      required: false\n    },\n    showRight: {\n      type: Boolean,\n      required: false\n    },\n    showFooter: {\n      type: Boolean,\n      required: false\n    },\n    embedded: {\n      type: Boolean,\n      required: false\n    },\n    fixed: {\n      type: Boolean,\n      required: false\n    }\n  },\n  data() {\n    return {\n      leftOpened: false\n    }\n  },\n  methods: {\n    openLeft() {\n      this.leftOpened = true\n    },\n    closeAll() {\n      this.leftOpened = false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $footer-height: 65px;\n  $header-height: 64px;\n  $side-container-width: 256px;\n  $scroll-width: 6px;\n  $sub-header-height: 54px;\n\n  div.me-layout {\n    background: #f2f3f5;\n    bottom: 0;\n    left: 0;\n    position: absolute;\n    right: 0;\n    top: 0;\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      transition-duration: 0.2s;\n      transition: opacity;\n      z-index: 1038;\n    }\n\n    > div.header {\n      display: none;\n      left: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      z-index: 10;\n\n      > a.left-menu {\n        cursor: pointer;\n        display: none;\n        height: $header-height;\n        left: 0;\n        position: absolute;\n        top: 0px;\n        width: 50px;\n        z-index: 1;\n      }\n    }\n\n    > div.sub-header,\n    > div.footer {\n      align-items: center;\n      background-color: var(--me-white);\n      display: none;\n      left: 0;\n      padding-left: 16px;\n      padding-right: 16px;\n      position: fixed;\n      right: 0;\n      z-index: 9;\n    }\n\n    > div.sub-header {\n      height: $sub-header-height;\n      border-bottom: 1px solid var(--me-gray-6);\n    }\n\n    > div.footer {\n      bottom: 0;\n      box-shadow: var(--me-shadow-level-1);\n      height: $footer-height;\n    }\n\n    > main {\n      > div.left,\n      > div.right {\n        background-color: var(--me-white);\n        bottom: 0;\n        box-shadow: var(--me-shadow-level-1);\n        display: none;\n        padding: 0;\n        position: fixed;\n        top: 0;\n        width: $side-container-width;\n        z-index: 8;\n      }\n\n      > div.left {\n        overflow: hidden;\n        left: 0;\n        padding: 0;\n        border-right: 1px solid var(--me-gray-6);\n        width: $side-container-width;\n\n        .left-content {\n          width: $side-container-width;\n          height: 100%;\n          min-height: 100%;\n        }\n\n        &:hover {\n          overflow: auto;\n          overflow-x: hidden;\n          scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n          scrollbar-width: thin;\n\n          &::-webkit-scrollbar {\n            width: $scroll-width;\n            height: 6px;\n\n            &-track {\n              border-radius: 15px;\n              background: rgba(0, 0, 0, 0.1);\n            }\n\n            &-thumb {\n              background: rgba(0, 0, 0, 0.25);\n              border-radius: 15px;\n\n              &:hover {\n                background: rgba(0, 0, 0, 0.45);\n              }\n            }\n          }\n        }\n      }\n\n      > div.right {\n        right: 0;\n        border-left: 1px solid var(--me-gray-6);\n        overflow: auto;\n      }\n\n      > div.content {\n        background: #f2f3f5;\n      }\n    }\n\n    &.header {\n      > div.header {\n        display: block;\n      }\n\n      > main {\n        margin-top: $header-height;\n        > div.left,\n        > div.right {\n          top: $header-height;\n        }\n      }\n\n      &.fixed {\n        > main {\n          > div.content {\n            top: $header-height;\n          }\n        }\n      }\n    }\n\n    @media screen {\n      &.sub-header {\n        > div.sub-header {\n          display: flex;\n          top: 0;\n        }\n\n        > main {\n          margin-top: $sub-header-height;\n\n          > div.left,\n          > div.right {\n            top: $sub-header-height;\n          }\n        }\n\n        &.fixed {\n          > main {\n            > div.content {\n              top: $sub-header-height;\n            }\n          }\n        }\n      }\n\n      &.header.sub-header {\n        > div.sub-header {\n          display: flex;\n          top: $header-height;\n        }\n\n        > main {\n          margin-top: $header-height + $sub-header-height;\n\n          > div.left,\n          > div.right {\n            top: $header-height + $sub-header-height;\n          }\n        }\n\n        &.fixed {\n          > main {\n            > div.content {\n              top: $header-height + $sub-header-height;\n            }\n          }\n        }\n      }\n\n      &.footer {\n        > div.footer {\n          display: flex;\n          justify-content: center;\n        }\n\n        &, div.me-layout {\n          > main {\n            margin-bottom: $footer-height;\n\n            > div.left,\n            > div.right {\n              bottom: $footer-height;\n            }\n          }\n\n          &.fixed {\n            > main > div.content {\n              bottom: $footer-height;\n            }\n          }\n        }\n      }\n    }\n\n    @media (min-width: \"769px\") {\n      &.left {\n        > main {\n          > div.left {\n            display: block;\n          }\n\n          > div.content {\n            margin-left: $side-container-width;\n          }\n        }\n      }\n\n      &.right {\n        > main {\n          > div.right {\n            display: block;\n          }\n\n          > div.content {\n            margin-right: $side-container-width;\n          }\n        }\n      }\n    }\n\n    @media (max-width: \"768px\") {\n      &.responsive {\n        > div.header {\n          > a.left-menu {\n            display: block;\n          }\n        }\n\n        > main {\n          > div.left {\n            bottom: 0!important;\n            display: block;\n            left: -$side-container-width;\n            top: 0 !important;\n            transition-duration: 0.2s;\n            transition: left;\n            z-index: 1039;\n          }\n        }\n      }\n\n      &.left-opened {\n        > main {\n          > div.left {\n            left: 0;\n          }\n        }\n\n        > div.backdrop {\n          display: block;\n          opacity: 0.4;\n        }\n      }\n    }\n\n    &.embedded {\n      > div.header {\n        position: absolute;\n      }\n\n      > div.sub-header,\n      > div.footer {\n        position: absolute;\n      }\n\n      > main {\n        > div.left,\n        > div.right {\n          position: absolute;\n        }\n      }\n\n      > div.backdrop {\n        position: absolute;\n      }\n\n      &.fixed {\n        > main > div.content {\n          position: absolute;\n        }\n      }\n    }\n\n    &.fixed {\n      > main > div.content {\n        bottom: 0;\n        left: 0;\n        overflow: auto;\n        position: fixed;\n        right: 0;\n        top: 0;\n      }\n    }\n  }\n</style>\n","div.me-layout {\n  background: #f2f3f5;\n  bottom: 0;\n  left: 0;\n  position: absolute;\n  right: 0;\n  top: 0;\n}\ndiv.me-layout > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1038;\n}\ndiv.me-layout > div.header {\n  display: none;\n  left: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  z-index: 10;\n}\ndiv.me-layout > div.header > a.left-menu {\n  cursor: pointer;\n  display: none;\n  height: 64px;\n  left: 0;\n  position: absolute;\n  top: 0px;\n  width: 50px;\n  z-index: 1;\n}\ndiv.me-layout > div.sub-header,\ndiv.me-layout > div.footer {\n  align-items: center;\n  background-color: var(--me-white);\n  display: none;\n  left: 0;\n  padding-left: 16px;\n  padding-right: 16px;\n  position: fixed;\n  right: 0;\n  z-index: 9;\n}\ndiv.me-layout > div.sub-header {\n  height: 54px;\n  border-bottom: 1px solid var(--me-gray-6);\n}\ndiv.me-layout > div.footer {\n  bottom: 0;\n  box-shadow: var(--me-shadow-level-1);\n  height: 65px;\n}\ndiv.me-layout > main > div.left,\ndiv.me-layout > main > div.right {\n  background-color: var(--me-white);\n  bottom: 0;\n  box-shadow: var(--me-shadow-level-1);\n  display: none;\n  padding: 0;\n  position: fixed;\n  top: 0;\n  width: 256px;\n  z-index: 8;\n}\ndiv.me-layout > main > div.left {\n  overflow: hidden;\n  left: 0;\n  padding: 0;\n  border-right: 1px solid var(--me-gray-6);\n  width: 256px;\n}\ndiv.me-layout > main > div.left .left-content {\n  width: 256px;\n  height: 100%;\n  min-height: 100%;\n}\ndiv.me-layout > main > div.left:hover {\n  overflow: auto;\n  overflow-x: hidden;\n  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n  scrollbar-width: thin;\n}\ndiv.me-layout > main > div.left:hover::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\ndiv.me-layout > main > div.left:hover::-webkit-scrollbar-track {\n  border-radius: 15px;\n  background: rgba(0, 0, 0, 0.1);\n}\ndiv.me-layout > main > div.left:hover::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\ndiv.me-layout > main > div.left:hover::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\ndiv.me-layout > main > div.right {\n  right: 0;\n  border-left: 1px solid var(--me-gray-6);\n  overflow: auto;\n}\ndiv.me-layout > main > div.content {\n  background: #f2f3f5;\n}\ndiv.me-layout.header > div.header {\n  display: block;\n}\ndiv.me-layout.header > main {\n  margin-top: 64px;\n}\ndiv.me-layout.header > main > div.left,\ndiv.me-layout.header > main > div.right {\n  top: 64px;\n}\ndiv.me-layout.header.fixed > main > div.content {\n  top: 64px;\n}\n@media screen {\n  div.me-layout.sub-header > div.sub-header {\n    display: flex;\n    top: 0;\n  }\n  div.me-layout.sub-header > main {\n    margin-top: 54px;\n  }\n  div.me-layout.sub-header > main > div.left,\ndiv.me-layout.sub-header > main > div.right {\n    top: 54px;\n  }\n  div.me-layout.sub-header.fixed > main > div.content {\n    top: 54px;\n  }\n  div.me-layout.header.sub-header > div.sub-header {\n    display: flex;\n    top: 64px;\n  }\n  div.me-layout.header.sub-header > main {\n    margin-top: 118px;\n  }\n  div.me-layout.header.sub-header > main > div.left,\ndiv.me-layout.header.sub-header > main > div.right {\n    top: 118px;\n  }\n  div.me-layout.header.sub-header.fixed > main > div.content {\n    top: 118px;\n  }\n  div.me-layout.footer > div.footer {\n    display: flex;\n    justify-content: center;\n  }\n  div.me-layout.footer > main, div.me-layout.footer div.me-layout > main {\n    margin-bottom: 65px;\n  }\n  div.me-layout.footer > main > div.left,\ndiv.me-layout.footer > main > div.right, div.me-layout.footer div.me-layout > main > div.left,\ndiv.me-layout.footer div.me-layout > main > div.right {\n    bottom: 65px;\n  }\n  div.me-layout.footer.fixed > main > div.content, div.me-layout.footer div.me-layout.fixed > main > div.content {\n    bottom: 65px;\n  }\n}\n@media (min-width: 769px) {\n  div.me-layout.left > main > div.left {\n    display: block;\n  }\n  div.me-layout.left > main > div.content {\n    margin-left: 256px;\n  }\n  div.me-layout.right > main > div.right {\n    display: block;\n  }\n  div.me-layout.right > main > div.content {\n    margin-right: 256px;\n  }\n}\n@media (max-width: 768px) {\n  div.me-layout.responsive > div.header > a.left-menu {\n    display: block;\n  }\n  div.me-layout.responsive > main > div.left {\n    bottom: 0 !important;\n    display: block;\n    left: -256px;\n    top: 0 !important;\n    transition-duration: 0.2s;\n    transition: left;\n    z-index: 1039;\n  }\n  div.me-layout.left-opened > main > div.left {\n    left: 0;\n  }\n  div.me-layout.left-opened > div.backdrop {\n    display: block;\n    opacity: 0.4;\n  }\n}\ndiv.me-layout.embedded > div.header {\n  position: absolute;\n}\ndiv.me-layout.embedded > div.sub-header,\ndiv.me-layout.embedded > div.footer {\n  position: absolute;\n}\ndiv.me-layout.embedded > main > div.left,\ndiv.me-layout.embedded > main > div.right {\n  position: absolute;\n}\ndiv.me-layout.embedded > div.backdrop {\n  position: absolute;\n}\ndiv.me-layout.embedded.fixed > main > div.content {\n  position: absolute;\n}\ndiv.me-layout.fixed > main > div.content {\n  bottom: 0;\n  left: 0;\n  overflow: auto;\n  position: fixed;\n  right: 0;\n  top: 0;\n}\n\n/*# sourceMappingURL=Layout.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1M = "data-v-9ea052c6";
  /* module identifier */
  const __vue_module_identifier__$1M = undefined;
  /* functional template */
  const __vue_is_functional_template__$1M = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1M, staticRenderFns: __vue_staticRenderFns__$1M },
    __vue_inject_styles__$1M,
    __vue_script__$1M,
    __vue_scope_id__$1M,
    __vue_is_functional_template__$1M,
    __vue_module_identifier__$1M,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1N = {
  name: 'MeMenuCollapse',
  components: {
    Icon: __vue_component__
  },

  props: {
    opened: {
      type: Boolean,
      default: true
    },
    items: {
      type: Object,
      required: true,
      validator({ header, content }) {
        const hasTitle = !!header?.title;
        const hasLinksList = Array.isArray(content?.links);

        return hasTitle && hasLinksList
      }
    },
    linkButtonsVisibility: {
      type: String,
      default: 'hover',
      validator: value => ['hover', 'always'].includes(value)
    }
  },

  data() {
    return {
      isOpened: this.opened,
      hasEllipsis: false
    }
  },

  computed: {
    header() {
      return this.items.header
    },

    content() {
      return this.items.content
    },

    hasHeaderButtons() {
      return this.header.buttons?.length > 0
    },

    menuTitle() {
      return this.header.title
    },

    links() {
      return this.content.links
    }
  },

  methods: {
    toggleMenu() {
      this.isOpened = !this.isOpened;
    },

    getLinkButtons(link) {
      return link.buttons || this.items.content.defaultButtons || []
    },

    setHasEllipsis(event) {
      this.hasEllipsis = event.target.offsetWidth < event.target.scrollWidth;
    }
  }
};

/* script */
const __vue_script__$1N = script$1N;

/* template */
var __vue_render__$1N = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-menu-collapse" },
    [
      _c("div", { staticClass: "header" }, [
        _c("div", { staticClass: "d-flex align-items-center flex-grow-1" }, [
          _c(
            "div",
            {
              staticClass: "header__icon",
              class: { opened: _vm.isOpened },
              on: { click: _vm.toggleMenu }
            },
            [
              _c("Icon", {
                attrs: {
                  size: 12,
                  icon: "me-icon-l icon-chevron-down",
                  color: "var(--me-gray-4)"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "div",
            { staticClass: "header__title", on: { click: _vm.toggleMenu } },
            [_vm._v("\n        " + _vm._s(_vm.menuTitle) + "\n      ")]
          )
        ]),
        _vm._v(" "),
        _vm.hasHeaderButtons
          ? _c(
              "div",
              { staticClass: "header__buttons" },
              _vm._l(_vm.header.buttons, function(button) {
                return _c(
                  "div",
                  { key: button.label, staticClass: "link-buttons" },
                  [
                    _c(
                      "button",
                      {
                        directives: [
                          {
                            name: "me-tooltip",
                            rawName: "v-me-tooltip",
                            value: button.label,
                            expression: "button.label"
                          }
                        ],
                        on: {
                          click: function() {
                            return button.click && button.click(button)
                          }
                        }
                      },
                      [
                        _c("Icon", {
                          attrs: {
                            icon: button.icon,
                            size: 12,
                            color: button.iconColor || "var(--me-gray-3)",
                            "hover-icon": button.hoverIcon
                          }
                        })
                      ],
                      1
                    )
                  ]
                )
              }),
              0
            )
          : _vm._e()
      ]),
      _vm._v(" "),
      _c("transition", { attrs: { name: "content-transition" } }, [
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.isOpened,
                expression: "isOpened"
              }
            ],
            staticClass: "content"
          },
          [
            _c(
              "ul",
              { staticClass: "links" },
              _vm._l(_vm.links, function(link) {
                return _c(
                  "li",
                  {
                    key: link.linkID,
                    staticClass: "link",
                    class: { active: link.active }
                  },
                  [
                    _c("div", { staticClass: "link-label" }, [
                      link.click
                        ? _c(
                            "button",
                            {
                              directives: [
                                {
                                  name: "me-tooltip",
                                  rawName: "v-me-tooltip",
                                  value: _vm.hasEllipsis ? link.label : "",
                                  expression: "hasEllipsis ? link.label : ''"
                                }
                              ],
                              staticClass: "link-label__action",
                              on: {
                                click: link.click,
                                mouseenter: _vm.setHasEllipsis
                              }
                            },
                            [
                              _vm._v(
                                "\n              " +
                                  _vm._s(link.label) +
                                  "\n            "
                              )
                            ]
                          )
                        : _c(
                            "a",
                            {
                              directives: [
                                {
                                  name: "me-tooltip",
                                  rawName: "v-me-tooltip",
                                  value: _vm.hasEllipsis ? link.label : "",
                                  expression: "hasEllipsis ? link.label : ''"
                                }
                              ],
                              staticClass: "link-label__action",
                              attrs: { href: link.href || "#" },
                              on: { mouseenter: _vm.setHasEllipsis }
                            },
                            [
                              _vm._v(
                                "\n              " +
                                  _vm._s(link.label) +
                                  "\n            "
                              )
                            ]
                          )
                    ]),
                    _vm._v(" "),
                    _vm._l(_vm.getLinkButtons(link), function(button) {
                      return _c(
                        "div",
                        {
                          key: button.label,
                          staticClass: "link-buttons",
                          class: {
                            "link-buttons--visible-always":
                              _vm.linkButtonsVisibility === "always"
                          }
                        },
                        [
                          _c(
                            "button",
                            {
                              directives: [
                                {
                                  name: "me-tooltip",
                                  rawName: "v-me-tooltip.hover.bottom",
                                  value: button.label,
                                  expression: "button.label",
                                  modifiers: { hover: true, bottom: true }
                                }
                              ],
                              on: {
                                click: function() {
                                  return button.click({
                                    button: button,
                                    link: link
                                  })
                                }
                              }
                            },
                            [
                              _c("Icon", {
                                attrs: {
                                  icon: button.icon,
                                  size: 12,
                                  color: button.iconColor || "var(--me-gray-3)",
                                  "hover-icon": button.hoverIcon
                                }
                              })
                            ],
                            1
                          )
                        ]
                      )
                    })
                  ],
                  2
                )
              }),
              0
            )
          ]
        )
      ])
    ],
    1
  )
};
var __vue_staticRenderFns__$1N = [];
__vue_render__$1N._withStripped = true;

  /* style */
  const __vue_inject_styles__$1N = function (inject) {
    if (!inject) return
    inject("data-v-36000c03_0", { source: "*[data-v-36000c03] {\n  margin: 0;\n  padding: 0;\n}\nul[data-v-36000c03] {\n  list-style: none;\n}\na[data-v-36000c03] {\n  text-decoration: none;\n}\n.header[data-v-36000c03] {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  height: 36px;\n  padding: 0 16px;\n  text-transform: uppercase;\n  cursor: pointer;\n}\n.header__icon[data-v-36000c03] {\n  width: 16px;\n  margin-right: 8px;\n}\n.header__icon i[data-v-36000c03] {\n  transition: transform;\n  transition-duration: 0.3s;\n}\n.header__icon.opened i[data-v-36000c03] {\n  transform: rotate(-180deg) !important;\n}\n.header__buttons[data-v-36000c03] {\n  display: flex;\n}\n.header__title[data-v-36000c03] {\n  flex-grow: 1;\n  font-size: 14px;\n  font-weight: 600;\n  color: var(--me-gray-4);\n}\n.content .links .link[data-v-36000c03] {\n  display: flex;\n  align-items: center;\n  height: 36px;\n  padding-left: 40px;\n  padding-right: 16px;\n}\n.content .links .link .link-label[data-v-36000c03] {\n  display: flex;\n  min-width: 0;\n  flex-grow: 1;\n  font-size: 14px;\n}\n.content .links .link .link-label button[data-v-36000c03] {\n  background: none;\n  border: none;\n  text-align: left;\n  width: 100%;\n}\n.content .links .link .link-label button[data-v-36000c03]:focus {\n  outline: none;\n}\n.content .links .link .link-label__action[data-v-36000c03] {\n  color: var(--me-gray-2);\n  flex-grow: 1;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n.content .links .link .link-buttons[data-v-36000c03] {\n  display: none;\n}\n.content .links .link .link-buttons--visible-always[data-v-36000c03] {\n  display: flex;\n}\n.content .links .link.active[data-v-36000c03] {\n  background: var(--me-primary-7);\n}\n.content .links .link.active .link-label__action[data-v-36000c03] {\n  color: var(--me-primary-2);\n}\n.content .links .link.active[data-v-36000c03]:hover {\n  background: var(--me-primary-7);\n}\n.content .links .link[data-v-36000c03]:hover {\n  background: rgba(0, 0, 0, 0.06);\n}\n.content .links .link:hover .link-buttons[data-v-36000c03] {\n  display: flex;\n}\n.link-buttons[data-v-36000c03] {\n  padding-right: 16px;\n}\n.link-buttons[data-v-36000c03]:last-child {\n  padding: 0px;\n}\n.link-buttons button[data-v-36000c03] {\n  background: none;\n  border: none;\n  margin: 0;\n  padding: 0;\n}\n.link-buttons button[data-v-36000c03]:focus {\n  outline: none;\n}\n.content-transition-enter-active[data-v-36000c03], .content-transition-leave-active[data-v-36000c03] {\n  transition: all 0.3s;\n}\n.content-transition-enter[data-v-36000c03], .content-transition-leave-to[data-v-36000c03] {\n  transform: translateY(-10px);\n  opacity: 0;\n}\n\n/*# sourceMappingURL=MenuCollapse.vue.map */", map: {"version":3,"sources":["/app/build/src/components/MenuCollapse.vue","MenuCollapse.vue"],"names":[],"mappings":"AA2LA;EACA,SAAA;EACA,UAAA;AC1LA;AD6LA;EACA,gBAAA;AC1LA;AD6LA;EACA,qBAAA;AC1LA;AD6LA;EACA,aAAA;EACA,mBAAA;EACA,8BAAA;EACA,YApBA;EAqBA,eAAA;EACA,yBAAA;EACA,eAAA;AC1LA;AD4LA;EACA,WAAA;EACA,iBAAA;AC1LA;AD4LA;EACA,qBAAA;EACA,yBAAA;AC1LA;AD6LA;EACA,qCAAA;AC3LA;AD+LA;EACA,aAAA;AC7LA;ADgMA;EACA,YAAA;EACA,eAAA;EACA,gBAAA;EACA,uBAAA;AC9LA;ADoMA;EACA,aAAA;EACA,mBAAA;EACA,YAxDA;EAyDA,kBAAA;EACA,mBAzDA;ACxIA;ADmMA;EACA,aAAA;EACA,YAAA;EACA,YAAA;EACA,eAAA;ACjMA;ADmMA;EACA,gBAAA;EACA,YAAA;EACA,gBAAA;EACA,WAAA;ACjMA;ADmMA;EACA,aAAA;ACjMA;ADqMA;EACA,uBAAA;EACA,YAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;ACnMA;ADuMA;EACA,aAAA;ACrMA;ADuMA;EACA,aAAA;ACrMA;ADyMA;EACA,+BAAA;ACvMA;AD0MA;EACA,0BAAA;ACxMA;AD4MA;EACA,+BAAA;AC1MA;AD8MA;EACA,+BAAA;AC5MA;AD8MA;EACA,aAAA;AC5MA;ADmNA;EACA,mBAAA;AChNA;ADkNA;EACA,YAAA;AChNA;ADmNA;EACA,gBAAA;EACA,YAAA;EACA,SAAA;EACA,UAAA;ACjNA;ADmNA;EACA,aAAA;ACjNA;ADsNA;EACA,oBAAA;ACnNA;ADsNA;EACA,4BAAA;EACA,UAAA;ACnNA;;AAEA,2CAA2C","file":"MenuCollapse.vue","sourcesContent":["<template>\n  <div class=\"me-menu-collapse\">\n    <div class=\"header\">\n      <div class=\"d-flex align-items-center flex-grow-1\">\n        <div\n          :class=\"{ opened: isOpened }\"\n          @click=\"toggleMenu\"\n          class=\"header__icon\"\n        >\n          <Icon\n            :size=\"12\"\n            icon=\"me-icon-l icon-chevron-down\"\n            color=\"var(--me-gray-4)\"\n          />\n        </div>\n\n        <div @click=\"toggleMenu\" class=\"header__title\">\n          {{ menuTitle }}\n        </div>\n      </div>\n\n      <div\n        v-if=\"hasHeaderButtons\"\n        class=\"header__buttons\"\n      >\n        <div\n          v-for=\"button in header.buttons\"\n          :key=\"button.label\"\n          class=\"link-buttons\"\n        >\n          <button\n            @click=\"() => button.click && button.click(button)\"\n            v-me-tooltip=\"button.label\"\n          >\n            <Icon\n              :icon=\"button.icon\"\n              :size=\"12\"\n              :color=\"button.iconColor || 'var(--me-gray-3)'\"\n              :hover-icon=\"button.hoverIcon\"\n            />\n          </button>\n        </div>\n      </div>\n    </div>\n\n    <transition name=\"content-transition\">\n      <div\n        v-show=\"isOpened\"\n        class=\"content\"\n      >\n        <ul class=\"links\">\n          <li\n            v-for=\"link in links\"\n            :key=\"link.linkID\"\n            :class=\"{ active: link.active }\"\n            class=\"link\"\n          >\n            <div class=\"link-label\">\n              <button\n                v-if=\"link.click\"\n                @click=\"link.click\"\n                v-me-tooltip=\"hasEllipsis ? link.label : ''\"\n                @mouseenter=\"setHasEllipsis\"\n                class=\"link-label__action\"\n              >\n                {{ link.label }}\n              </button>\n\n              <a\n                v-else\n                :href=\"link.href || '#'\"\n                v-me-tooltip=\"hasEllipsis ? link.label : ''\"\n                @mouseenter=\"setHasEllipsis\"\n                class=\"link-label__action\"\n              >\n                {{ link.label }}\n              </a>\n            </div>\n\n            <div\n              v-for=\"button in getLinkButtons(link)\"\n              :key=\"button.label\"\n              :class=\"{\n                'link-buttons--visible-always': linkButtonsVisibility === 'always'\n              }\"\n              class=\"link-buttons\"\n            >\n              <button\n                @click=\"() => button.click({ button, link })\"\n                v-me-tooltip.hover.bottom=\"button.label\"\n              >\n                <Icon\n                  :icon=\"button.icon\"\n                  :size=\"12\"\n                  :color=\"button.iconColor || 'var(--me-gray-3)'\"\n                  :hover-icon=\"button.hoverIcon\"\n                />\n              </button>\n            </div>\n          </li>\n        </ul>\n      </div>\n    </transition>\n  </div>\n</template>\n\n<script>\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeMenuCollapse',\n  components: {\n    Icon\n  },\n\n  props: {\n    opened: {\n      type: Boolean,\n      default: true\n    },\n    items: {\n      type: Object,\n      required: true,\n      validator({ header, content }) {\n        const hasTitle = !!header?.title\n        const hasLinksList = Array.isArray(content?.links)\n\n        return hasTitle && hasLinksList\n      }\n    },\n    linkButtonsVisibility: {\n      type: String,\n      default: 'hover',\n      validator: value => ['hover', 'always'].includes(value)\n    }\n  },\n\n  data() {\n    return {\n      isOpened: this.opened,\n      hasEllipsis: false\n    }\n  },\n\n  computed: {\n    header() {\n      return this.items.header\n    },\n\n    content() {\n      return this.items.content\n    },\n\n    hasHeaderButtons() {\n      return this.header.buttons?.length > 0\n    },\n\n    menuTitle() {\n      return this.header.title\n    },\n\n    links() {\n      return this.content.links\n    }\n  },\n\n  methods: {\n    toggleMenu() {\n      this.isOpened = !this.isOpened\n    },\n\n    getLinkButtons(link) {\n      return link.buttons || this.items.content.defaultButtons || []\n    },\n\n    setHasEllipsis(event) {\n      this.hasEllipsis = event.target.offsetWidth < event.target.scrollWidth\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n\n  $row-height: 36px;\n  $row-padding-right: 16px;\n\n  * {\n    margin: 0;\n    padding: 0;\n  }\n\n  ul {\n    list-style: none;\n  }\n\n  a {\n    text-decoration: none;\n  }\n\n  .header {\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    height: $row-height;\n    padding: 0 $row-padding-right;\n    text-transform: uppercase;\n    cursor: pointer;\n\n    &__icon {\n      width: 16px;\n      margin-right: 8px;\n\n      i {\n        transition: transform;\n        transition-duration: 0.3s;\n      }\n\n      &.opened i {\n        transform: rotate(-180deg)!important;\n      }\n    }\n\n    &__buttons {\n      display: flex;\n    }\n\n    &__title {\n      flex-grow: 1;\n      font-size: 14px;\n      font-weight: 600;\n      color: var(--me-gray-4);\n    }\n  }\n\n  .content {\n    .links {\n      .link {\n        display: flex;\n        align-items: center;\n        height: $row-height;\n        padding-left: 40px;\n        padding-right: $row-padding-right;\n\n        .link-label {\n          display: flex;\n          min-width: 0;\n          flex-grow: 1;\n          font-size: 14px;\n\n          button {\n            background: none;\n            border: none;\n            text-align: left;\n            width: 100%;\n\n            &:focus {\n              outline: none;\n            }\n          }\n\n          &__action {\n            color: var(--me-gray-2);\n            flex-grow: 1;\n            overflow: hidden;\n            text-overflow: ellipsis;\n            white-space: nowrap;\n          }\n        }\n\n        .link-buttons {\n          display: none;\n\n          &--visible-always {\n            display: flex;\n          }\n        }\n\n        &.active {\n          background: var(--me-primary-7);\n\n          .link-label {\n            &__action {\n              color: var(--me-primary-2);\n            }\n          }\n\n          &:hover {\n            background: var(--me-primary-7);\n          }\n        }\n\n        &:hover {\n          background: rgba(0, 0, 0, .06);\n\n          .link-buttons {\n            display: flex;\n          }\n        }\n      }\n    }\n  }\n\n  .link-buttons {\n    padding-right: 16px;\n\n    &:last-child {\n      padding: 0px;\n    }\n\n    button {\n      background: none;\n      border: none;\n      margin: 0;\n      padding: 0;\n\n      &:focus {\n        outline: none;\n      }\n    }\n  }\n\n  .content-transition-enter-active, .content-transition-leave-active {\n      transition: all .3s;\n  }\n\n  .content-transition-enter, .content-transition-leave-to {\n    transform: translateY(-10px);\n    opacity: 0;\n  }\n</style>\n","* {\n  margin: 0;\n  padding: 0;\n}\n\nul {\n  list-style: none;\n}\n\na {\n  text-decoration: none;\n}\n\n.header {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  height: 36px;\n  padding: 0 16px;\n  text-transform: uppercase;\n  cursor: pointer;\n}\n.header__icon {\n  width: 16px;\n  margin-right: 8px;\n}\n.header__icon i {\n  transition: transform;\n  transition-duration: 0.3s;\n}\n.header__icon.opened i {\n  transform: rotate(-180deg) !important;\n}\n.header__buttons {\n  display: flex;\n}\n.header__title {\n  flex-grow: 1;\n  font-size: 14px;\n  font-weight: 600;\n  color: var(--me-gray-4);\n}\n\n.content .links .link {\n  display: flex;\n  align-items: center;\n  height: 36px;\n  padding-left: 40px;\n  padding-right: 16px;\n}\n.content .links .link .link-label {\n  display: flex;\n  min-width: 0;\n  flex-grow: 1;\n  font-size: 14px;\n}\n.content .links .link .link-label button {\n  background: none;\n  border: none;\n  text-align: left;\n  width: 100%;\n}\n.content .links .link .link-label button:focus {\n  outline: none;\n}\n.content .links .link .link-label__action {\n  color: var(--me-gray-2);\n  flex-grow: 1;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n.content .links .link .link-buttons {\n  display: none;\n}\n.content .links .link .link-buttons--visible-always {\n  display: flex;\n}\n.content .links .link.active {\n  background: var(--me-primary-7);\n}\n.content .links .link.active .link-label__action {\n  color: var(--me-primary-2);\n}\n.content .links .link.active:hover {\n  background: var(--me-primary-7);\n}\n.content .links .link:hover {\n  background: rgba(0, 0, 0, 0.06);\n}\n.content .links .link:hover .link-buttons {\n  display: flex;\n}\n\n.link-buttons {\n  padding-right: 16px;\n}\n.link-buttons:last-child {\n  padding: 0px;\n}\n.link-buttons button {\n  background: none;\n  border: none;\n  margin: 0;\n  padding: 0;\n}\n.link-buttons button:focus {\n  outline: none;\n}\n\n.content-transition-enter-active, .content-transition-leave-active {\n  transition: all 0.3s;\n}\n\n.content-transition-enter, .content-transition-leave-to {\n  transform: translateY(-10px);\n  opacity: 0;\n}\n\n/*# sourceMappingURL=MenuCollapse.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1N = "data-v-36000c03";
  /* module identifier */
  const __vue_module_identifier__$1N = undefined;
  /* functional template */
  const __vue_is_functional_template__$1N = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1N, staticRenderFns: __vue_staticRenderFns__$1N },
    __vue_inject_styles__$1N,
    __vue_script__$1N,
    __vue_scope_id__$1N,
    __vue_is_functional_template__$1N,
    __vue_module_identifier__$1N,
    false,
    createInjector,
    undefined,
    undefined
  );

/**
 * Creates a slice of `array` with `n` elements taken from the beginning.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to query.
 * @param {number} [n=1] The number of elements to take.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Array} Returns the slice of `array`.
 * @example
 *
 * _.take([1, 2, 3]);
 * // => [1]
 *
 * _.take([1, 2, 3], 2);
 * // => [1, 2]
 *
 * _.take([1, 2, 3], 5);
 * // => [1, 2, 3]
 *
 * _.take([1, 2, 3], 0);
 * // => []
 */
function take(array, n, guard) {
  if (!(array && array.length)) {
    return [];
  }
  n = (guard || n === undefined) ? 1 : toInteger_1(n);
  return _baseSlice(array, 0, n < 0 ? 0 : n);
}

var take_1 = take;

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$1O = {
  name: 'SearchInput',
  props: {
    placeholder: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      term: '',
      isFocused: false
    }
  },
  computed: {
    showCleanButton() {
      return this.term.trim().length > 0
    }
  },
  watch: {
    term(value) {
      this.$emit('input', value);
    }
  },
  methods: {
    resetSearch() {
      this.term = '';
    }
  }
};

/* script */
const __vue_script__$1O = script$1O;

/* template */
var __vue_render__$1O = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "search-input", class: { focus: _vm.isFocused } },
    [
      _c("input", {
        directives: [
          {
            name: "model",
            rawName: "v-model",
            value: _vm.term,
            expression: "term"
          }
        ],
        attrs: {
          placeholder: _vm.placeholder,
          type: "text",
          autocomplete: "off"
        },
        domProps: { value: _vm.term },
        on: {
          focus: function() {
            return (_vm.isFocused = true)
          },
          blur: function() {
            return (_vm.isFocused = false)
          },
          input: function($event) {
            if ($event.target.composing) {
              return
            }
            _vm.term = $event.target.value;
          }
        }
      }),
      _vm._v(" "),
      _vm.showCleanButton
        ? _c("i", {
            staticClass: "me-icon-l me-icon-sm icon-xmark",
            on: { click: _vm.resetSearch }
          })
        : _vm._e(),
      _vm._v(" "),
      _c("i", { staticClass: "me-icon-l me-icon-sm icon-magnifying-glass" })
    ]
  )
};
var __vue_staticRenderFns__$1O = [];
__vue_render__$1O._withStripped = true;

  /* style */
  const __vue_inject_styles__$1O = function (inject) {
    if (!inject) return
    inject("data-v-812f5170_0", { source: ".search-input[data-v-812f5170] {\n  align-items: center;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  display: flex;\n  margin-bottom: 8px;\n  margin-left: 36px;\n  padding: 8px 16px;\n}\n.search-input.focus[data-v-812f5170] {\n  border-color: var(--me-primary-2);\n}\n.search-input input[data-v-812f5170] {\n  border: none;\n  color: var(--me-gray-3);\n  flex-grow: 1;\n  font-size: 12px;\n  min-width: 0px;\n  outline: none;\n}\n.search-input .me-icon-l[data-v-812f5170] {\n  color: var(--me-gray-2);\n  margin-left: 8px;\n}\n.search-input .icon-xmark[data-v-812f5170] {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=SearchInput.vue.map */", map: {"version":3,"sources":["/app/build/src/components/nestedMenu/SearchInput.vue","SearchInput.vue"],"names":[],"mappings":"AAuDA;EACA,mBAAA;EACA,kCAAA;EACA,kBAAA;EACA,aAAA;EACA,kBAAA;EACA,iBAAA;EACA,iBAAA;ACtDA;ADwDA;EACA,iCAAA;ACtDA;ADyDA;EACA,YAAA;EACA,uBAAA;EACA,YAAA;EACA,eAAA;EACA,cAAA;EACA,aAAA;ACvDA;AD0DA;EACA,uBAAA;EACA,gBAAA;ACxDA;AD2DA;EACA,eAAA;ACzDA;;AAEA,0CAA0C","file":"SearchInput.vue","sourcesContent":["<template>\n  <div :class=\"{ focus: isFocused }\" class=\"search-input\">\n    <input\n      v-model=\"term\"\n      :placeholder=\"placeholder\"\n      @focus=\"() => isFocused = true\"\n      @blur=\"() => isFocused = false\"\n      type=\"text\"\n      autocomplete=\"off\"\n    >\n\n    <i\n      v-if=\"showCleanButton\"\n      @click=\"resetSearch\"\n      class=\"me-icon-l me-icon-sm icon-xmark\"\n    />\n\n    <i class=\"me-icon-l me-icon-sm icon-magnifying-glass\" />\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'SearchInput',\n  props: {\n    placeholder: {\n      type: String,\n      default: ''\n    }\n  },\n  data() {\n    return {\n      term: '',\n      isFocused: false\n    }\n  },\n  computed: {\n    showCleanButton() {\n      return this.term.trim().length > 0\n    }\n  },\n  watch: {\n    term(value) {\n      this.$emit('input', value)\n    }\n  },\n  methods: {\n    resetSearch() {\n      this.term = ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.search-input {\n  align-items: center;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  display: flex;\n  margin-bottom: 8px;\n  margin-left: 36px;\n  padding: 8px 16px;\n\n  &.focus {\n    border-color: var(--me-primary-2);\n  }\n\n  input {\n    border: none;\n    color: var(--me-gray-3);\n    flex-grow: 1;\n    font-size: 12px;\n    min-width: 0px;\n    outline: none;\n  }\n\n  .me-icon-l {\n    color: var(--me-gray-2);\n    margin-left: 8px;\n  }\n\n  .icon-xmark {\n    cursor: pointer;\n  }\n}\n</style>\n",".search-input {\n  align-items: center;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  display: flex;\n  margin-bottom: 8px;\n  margin-left: 36px;\n  padding: 8px 16px;\n}\n.search-input.focus {\n  border-color: var(--me-primary-2);\n}\n.search-input input {\n  border: none;\n  color: var(--me-gray-3);\n  flex-grow: 1;\n  font-size: 12px;\n  min-width: 0px;\n  outline: none;\n}\n.search-input .me-icon-l {\n  color: var(--me-gray-2);\n  margin-left: 8px;\n}\n.search-input .icon-xmark {\n  cursor: pointer;\n}\n\n/*# sourceMappingURL=SearchInput.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1O = "data-v-812f5170";
  /* module identifier */
  const __vue_module_identifier__$1O = undefined;
  /* functional template */
  const __vue_is_functional_template__$1O = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1O, staticRenderFns: __vue_staticRenderFns__$1O },
    __vue_inject_styles__$1O,
    __vue_script__$1O,
    __vue_scope_id__$1O,
    __vue_is_functional_template__$1O,
    __vue_module_identifier__$1O,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1P = {
  name: 'MeNestedMenu',
  components: {
    Collapse: __vue_component__$n,
    SearchInput: __vue_component__$1O,
    Icon: __vue_component__
  },

  props: {
    titleMenu: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    searchByItems: {
      type: String,
      default: () => ''
    },
    hasCheckbox: {
      type: Boolean,
      default: false
    },
    opened: {
      type: Boolean,
      default: true
    },
    badgePrecision: {
      type: Number,
      default: 2
    },
    limitStepSize: {
      type: Number,
      default: 5
    },
    initialLimit: {
      type: Number,
      default: null,
      validator: prop => typeof prop === 'number' || prop === null
    },
    provider: {
      type: Function,
      default: null
    },
    linkButtonsVisibility: {
      type: String,
      default: 'hover',
      validator: value => ['hover', 'always'].includes(value)
    }
  },

  data() {
    return {
      listPage: 1,
      hits: null,
      hasEllipsis: false,
      isLoading: false,
      term: '',
      currentItems: cloneDeep_1(this.items),
      currentLimit: this.initialLimit
    }
  },

  computed: {
    currentLinksList() {
      if (this.currentLimit) {
        return take_1(this.filteredLinksList, this.currentLimit)
      }

      return this.filteredLinksList
    },

    filteredLinksList() {
      if (this.term) {
        return this.linksList.filter(item => item.label.toLowerCase().includes(this.term.toLowerCase()))
      }

      return this.linksList
    },

    linksList() {
      return this.currentItems.filter(item => !item.titleMenu).map(item => ({
        ...item,
        count: this.badgeCounter(item)
      }))
    },

    getLinkInTitleMenu() {
      return this.currentItems.find(item => item.titleMenu)
    },

    hasMoreItems() {
      if (!this.currentLimit) return false

      if (this.hits) return this.hits > this.currentLimit

      return this.filteredLinksList.length > this.currentLimit
    }
  },

  watch: {
    items(value) {
      this.currentItems = cloneDeep_1(value);
    }
  },

  created() {
    if (this.provider) {
      this.handleProvider();
    }
  },

  methods: {
    setHasEllipsis(event) {
      this.hasEllipsis = event.target.offsetWidth < event.target.scrollWidth;
    },

    handleProvider() {
      this.isLoading = true;
      this.provider({ page: this.listPage, pageSize: this.limitStepSize }, this.providerCallback);
    },

    providerCallback({ result, hits }) {
      this.currentItems = [...this.currentItems, ...result];
      this.hits = hits;
      this.isLoading = false;
      this.currentLimit += this.limitStepSize;
    },

    handleSeeMore() {
      if (this.provider) {
        this.listPage++;
        return this.handleProvider()
      }

      this.currentLimit += this.limitStepSize;
    },

    handleSearchInput(value) {
      this.term = value;
    },

    badgeCounter(link) {
      const counter = link.count ?? 0;

      if (this.badgePrecision === 0) return counter

      const counterStr = counter.toString();

      if (counterStr.length > this.badgePrecision) {
        const newCounter = '9';
        return newCounter.padEnd(this.badgePrecision, '9') + '+'
      }

      return counter
    },

    handleLinkClick(link, event, linkIndex = -1) {
      if (this.hasCheckbox) {
        this.currentItems = this.currentItems.map((item, index) => linkIndex === index ? { ...item, isChecked: !item.isChecked } : item);
      }

      if (!link.click) return

      if (event.cancelable) {
        event.preventDefault();
      }

      link.click(link);
    }
  }
};

/* script */
const __vue_script__$1P = script$1P;

/* template */
var __vue_render__$1P = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-nested-menu" },
    [
      _c(
        "collapse",
        {
          attrs: {
            title: !_vm.getLinkInTitleMenu ? _vm.titleMenu : "",
            opened: _vm.opened,
            variant: "square"
          },
          scopedSlots: _vm._u(
            [
              _vm.getLinkInTitleMenu
                ? {
                    key: "collapse-info-title",
                    fn: function() {
                      return [
                        _c(
                          "a",
                          {
                            class: { active: _vm.getLinkInTitleMenu.active },
                            attrs: { href: _vm.getLinkInTitleMenu.url },
                            on: {
                              click: function($event) {
                                $event.stopPropagation();
                                return _vm.handleLinkClick(
                                  _vm.getLinkInTitleMenu,
                                  $event
                                )
                              }
                            }
                          },
                          [
                            _c("span", [
                              _vm._v(
                                "\n          " +
                                  _vm._s(_vm.getLinkInTitleMenu.label) +
                                  "\n        "
                              )
                            ])
                          ]
                        ),
                        _vm._v(" "),
                        _vm.getLinkInTitleMenu.buttons
                          ? [
                              _vm._l(_vm.getLinkInTitleMenu.buttons, function(
                                button,
                                i
                              ) {
                                return [
                                  button.icon
                                    ? _c("Icon", {
                                        directives: [
                                          {
                                            name: "me-tooltip",
                                            rawName:
                                              "v-me-tooltip.hover.bottom",
                                            value: button.label,
                                            expression: "button.label",
                                            modifiers: {
                                              hover: true,
                                              bottom: true
                                            }
                                          }
                                        ],
                                        key: i,
                                        staticClass: "link-button",
                                        attrs: {
                                          icon: button.icon,
                                          size: 12,
                                          color:
                                            button.iconColor ||
                                            "var(--me-gray-3)",
                                          "hover-icon": button.hoverIcon
                                        },
                                        on: {
                                          click: function($event) {
                                            $event.stopPropagation();
                                            return (function() {
                                              return (
                                                button.click &&
                                                button.click(
                                                  _vm.getLinkInTitleMenu,
                                                  button
                                                )
                                              )
                                            })()
                                          }
                                        }
                                      })
                                    : _vm._e()
                                ]
                              })
                            ]
                          : _vm._e()
                      ]
                    },
                    proxy: true
                  }
                : null
            ],
            null,
            true
          )
        },
        [
          _vm._v(" "),
          _vm.isLoading && !_vm.currentItems.length
            ? [
                _c(
                  "div",
                  {
                    staticClass: "skeleton",
                    attrs: { "data-jest": "skeleton" }
                  },
                  _vm._l(_vm.limitStepSize, function(skeleton) {
                    return _c("me-skeleton-text", {
                      key: skeleton,
                      attrs: { width: "100px", height: "24px" }
                    })
                  }),
                  1
                )
              ]
            : [
                _vm.currentItems.length
                  ? [
                      _vm.searchByItems
                        ? _c("SearchInput", {
                            attrs: { placeholder: _vm.searchByItems },
                            on: { input: _vm.handleSearchInput }
                          })
                        : _vm._e(),
                      _vm._v(" "),
                      _vm._l(_vm.currentLinksList, function(link, index) {
                        return _c(
                          "div",
                          {
                            key: link.id,
                            staticClass:
                              "nested-links indent-links dynamic-links d-flex flex-column",
                            class: { active: link.active },
                            attrs: { "data-jest": "nested-link" }
                          },
                          [
                            _c(
                              "div",
                              { staticClass: "d-flex align-items-center" },
                              [
                                _vm.hasCheckbox
                                  ? _c("me-checkbox", {
                                      on: {
                                        change: function($event) {
                                          return _vm.handleLinkClick(
                                            link,
                                            $event,
                                            index
                                          )
                                        }
                                      },
                                      model: {
                                        value: link.isChecked,
                                        callback: function($$v) {
                                          _vm.$set(link, "isChecked", $$v);
                                        },
                                        expression: "link.isChecked"
                                      }
                                    })
                                  : _vm._e(),
                                _vm._v(" "),
                                _c(
                                  "a",
                                  {
                                    staticClass: "indent-links link-label",
                                    class: { active: link.active },
                                    attrs: { href: link.url },
                                    on: {
                                      click: function($event) {
                                        return _vm.handleLinkClick(
                                          link,
                                          $event,
                                          index
                                        )
                                      }
                                    }
                                  },
                                  [
                                    _c(
                                      "span",
                                      {
                                        directives: [
                                          {
                                            name: "me-tooltip",
                                            rawName: "v-me-tooltip",
                                            value: _vm.hasEllipsis
                                              ? link.label
                                              : "",
                                            expression:
                                              "hasEllipsis ? link.label : ''"
                                          }
                                        ],
                                        on: { mouseenter: _vm.setHasEllipsis }
                                      },
                                      [
                                        link.icon
                                          ? _c("i", { class: link.icon })
                                          : _vm._e(),
                                        _vm._v(
                                          "\n                " +
                                            _vm._s(link.label) +
                                            "\n              "
                                        )
                                      ]
                                    ),
                                    _vm._v(" "),
                                    link.count
                                      ? _c(
                                          "me-badge",
                                          {
                                            attrs: {
                                              appearance: "light-default",
                                              "data-jest": "badge-counter"
                                            }
                                          },
                                          [
                                            _vm._v(
                                              "\n                " +
                                                _vm._s(link.count) +
                                                "\n              "
                                            )
                                          ]
                                        )
                                      : _vm._e()
                                  ],
                                  1
                                ),
                                _vm._v(" "),
                                link.buttons
                                  ? [
                                      _c(
                                        "div",
                                        { staticClass: "link-buttons" },
                                        [
                                          _vm._l(link.buttons, function(
                                            button,
                                            i
                                          ) {
                                            return [
                                              button.icon
                                                ? _c("Icon", {
                                                    key: i,
                                                    staticClass: "link-button",
                                                    class: {
                                                      "link-button--visible-always":
                                                        _vm.linkButtonsVisibility ===
                                                        "always"
                                                    },
                                                    attrs: {
                                                      tooltip: button.label,
                                                      icon: button.icon,
                                                      size: 12,
                                                      color:
                                                        button.iconColor ||
                                                        "var(--me-gray-3)",
                                                      "hover-icon":
                                                        button.hoverIcon,
                                                      "data-jest":
                                                        "link-button-" + i
                                                    },
                                                    on: {
                                                      click: function($event) {
                                                        $event.stopPropagation();
                                                        return (function() {
                                                          return (
                                                            button.click &&
                                                            button.click(
                                                              link,
                                                              button
                                                            )
                                                          )
                                                        })()
                                                      }
                                                    }
                                                  })
                                                : _vm._e()
                                            ]
                                          })
                                        ],
                                        2
                                      )
                                    ]
                                  : _vm._e()
                              ],
                              2
                            )
                          ]
                        )
                      }),
                      _vm._v(" "),
                      _vm.hasMoreItems
                        ? _c(
                            "div",
                            { staticClass: "see-more" },
                            [
                              _c(
                                "me-button",
                                {
                                  staticClass: "see-more__btn",
                                  attrs: {
                                    busy: _vm.isLoading,
                                    href: "#",
                                    variant: "link",
                                    size: "sm",
                                    "data-jest": "see-more"
                                  },
                                  on: {
                                    click: function($event) {
                                      $event.preventDefault();
                                      return _vm.handleSeeMore($event)
                                    }
                                  }
                                },
                                [
                                  _vm._v(
                                    "\n            " +
                                      _vm._s(
                                        _vm.$toolkit.t("nestedMenu.seeMore")
                                      ) +
                                      "\n          "
                                  )
                                ]
                              )
                            ],
                            1
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      !_vm.currentLinksList.length && _vm.term
                        ? _c(
                            "div",
                            {
                              staticClass: "empty-search",
                              attrs: { "data-jest": "empty-search" }
                            },
                            [
                              _vm._v(
                                "\n          " +
                                  _vm._s(
                                    _vm.$toolkit.t("nestedMenu.emptySearch")
                                  ) +
                                  "\n        "
                              )
                            ]
                          )
                        : _vm._e()
                    ]
                  : [
                      _vm.$slots.links
                        ? _c(
                            "div",
                            { staticClass: "nested-links indent-links" },
                            [_vm._t("links")],
                            2
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      _vm.$slots.listgroup
                        ? _c(
                            "div",
                            { staticClass: "nested-component" },
                            [_vm._t("listgroup")],
                            2
                          )
                        : _vm._e()
                    ]
              ]
        ],
        2
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1P = [];
__vue_render__$1P._withStripped = true;

  /* style */
  const __vue_inject_styles__$1P = function (inject) {
    if (!inject) return
    inject("data-v-6668a3bf_0", { source: "div.me-nested-menu .empty-search[data-v-6668a3bf], div.me-nested-menu .see-more[data-v-6668a3bf] {\n  display: flex;\n  align-items: center;\n  margin-left: 40px;\n  min-height: 36px;\n  font-size: 14px;\n}\n[data-v-6668a3bf] .checkbox-item .me-checkbox {\n  margin-left: 40px;\n  text-indent: 0;\n}\ndiv.me-nested-menu[data-v-6668a3bf]  section.me-collapse > h5 {\n  color: var(--me-gray-2);\n  font-size: 14px;\n  font-weight: 600;\n  letter-spacing: normal;\n  height: 36px;\n  margin: 0;\n  padding-left: 8px;\n  padding-right: 16px;\n}\ndiv.me-nested-menu[data-v-6668a3bf]  section.me-collapse > h5 button {\n  color: var(--me-gray-2);\n  float: none;\n  margin-right: 8px;\n  padding: 0;\n  vertical-align: baseline;\n  z-index: 1;\n}\ndiv.me-nested-menu[data-v-6668a3bf]  section.me-collapse > h5 > a, div.me-nested-menu .nested-links[data-v-6668a3bf]  a {\n  display: flex;\n  align-items: center;\n  min-width: 0;\n  flex-grow: 1;\n  color: var(--me-gray-2);\n  font-size: 14px;\n  text-decoration: none;\n  height: 36px;\n}\ndiv.me-nested-menu[data-v-6668a3bf]  section.me-collapse > h5 > a:hover::after, div.me-nested-menu .nested-links[data-v-6668a3bf]  a:hover::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(0, 0, 0, 0.06);\n}\ndiv.me-nested-menu[data-v-6668a3bf]  section.me-collapse > h5 > a.active, div.me-nested-menu .nested-links[data-v-6668a3bf]  a.active {\n  color: var(--me-primary-2);\n}\ndiv.me-nested-menu[data-v-6668a3bf]  section.me-collapse > h5 > a.active::after, div.me-nested-menu .nested-links[data-v-6668a3bf]  a.active::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(10, 140, 204, 0.12);\n}\ndiv.me-nested-menu .see-more__btn[data-v-6668a3bf] {\n  padding: 0;\n  display: inline;\n  text-decoration: none;\n  box-shadow: none;\n  height: auto;\n}\ndiv.me-nested-menu .skeleton[data-v-6668a3bf] {\n  margin-left: 36px;\n}\ndiv.me-nested-menu .nested-links.indent-links[data-v-6668a3bf] {\n  padding-left: 36px;\n  padding-right: 16px;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label[data-v-6668a3bf] {\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  height: 36px;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label span[data-v-6668a3bf] {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  z-index: 2;\n  margin-right: 4px;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label i[data-v-6668a3bf] {\n  display: inline;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label[data-v-6668a3bf]  .me-badge {\n  float: right;\n  margin-left: auto;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-button[data-v-6668a3bf] {\n  display: none;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-button--visible-always[data-v-6668a3bf] {\n  display: block;\n}\ndiv.me-nested-menu .nested-links.dynamic-links:hover .link-button[data-v-6668a3bf] {\n  display: block;\n}\ndiv.me-nested-menu .link-buttons[data-v-6668a3bf] {\n  display: flex;\n  gap: 16px;\n}\ndiv.me-nested-menu .link-buttons .link-button[data-v-6668a3bf] {\n  cursor: pointer;\n  font-weight: 400;\n  z-index: 1;\n}\ndiv.me-nested-menu .nested-component .me-nested-menu[data-v-6668a3bf] {\n  margin-left: 20px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list {\n  margin-bottom: 8px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > a {\n  color: var(--me-gray-2);\n  font-size: 14px;\n  font-weight: 600;\n  padding-left: 0;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > a > i {\n  float: left;\n  margin: 0px 10px 3px 0;\n  position: relative;\n  z-index: 2;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > a:hover::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 128%;\n  background-color: rgba(0, 0, 0, 0.06);\n  left: -70px;\n  margin-top: -4px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list.opened .me-list-group li {\n  height: 28px;\n  margin: 4px 0;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group {\n  margin-left: 0;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group li.active {\n  color: var(--me-primary-2);\n  cursor: pointer;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group li.active a {\n  color: var(--me-primary-2);\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group li.active:hover {\n  font-weight: 600;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group li.active::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(10, 140, 204, 0.12);\n  margin-top: -28px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group a {\n  color: var(--me-gray-2);\n  display: block;\n  font-size: 14px;\n  height: 28px;\n  overflow: hidden;\n  padding: 3px;\n  text-decoration: none;\n  text-indent: 28px;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  width: 98%;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group a:hover {\n  font-weight: 600;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group a:hover::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(0, 0, 0, 0.06);\n  color: rgba(0, 0, 0, 0.06);\n  margin-top: -24px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group a:hover.active {\n  color: var(--me-primary-2);\n  font-weight: 600;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-accordion-list > .me-list-group a:hover.active::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(10, 140, 204, 0.12);\n  margin-top: -28px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-list-group {\n  margin-left: 9px;\n}\ndiv.me-nested-menu .nested-component[data-v-6668a3bf]  .me-list-group li {\n  padding: 0;\n}\n\n/*# sourceMappingURL=NestedMenu.vue.map */", map: {"version":3,"sources":["/app/build/src/components/nestedMenu/NestedMenu.vue","NestedMenu.vue"],"names":[],"mappings":"AA+UA;EACA,aAAA;EACA,mBAAA;EACA,iBAAA;EACA,gBAAA;EACA,eAAA;AC9UA;ADmVA;EACA,iBAAA;EACA,cAAA;AChVA;ADgWA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,sBAAA;EACA,YAnCA;EAoCA,SAAA;EACA,iBAAA;EACA,mBAAA;AC7VA;AD+VA;EACA,uBAAA;EACA,WAAA;EACA,iBAAA;EACA,UAAA;EACA,wBAAA;EACA,UAAA;AC7VA;ADiWA;EACA,aAAA;EACA,mBAAA;EACA,YAAA;EACA,YAAA;EACA,uBAAA;EACA,eAAA;EACA,qBAAA;EACA,YA1DA;ACrSA;ADkWA;EAzCA,YAAA;EACA,cAAA;EACA,YAtBA;EAuBA,OAAA;EACA,YAAA;EACA,kBAAA;EACA,WAPA;EA4CA,qCAAA;AC1VA;AD8VA;EACA,0BAAA;AC5VA;AD8VA;EAlDA,YAAA;EACA,cAAA;EACA,YAtBA;EAuBA,OAAA;EACA,YAAA;EACA,kBAAA;EACA,WAPA;EAqDA,0CAAA;ACtVA;AD8VA;EACA,UAAA;EACA,eAAA;EACA,qBAAA;EACA,gBAAA;EACA,YAAA;AC5VA;ADoWA;EACA,iBAAA;AClWA;ADsWA;EACA,kBAAA;EACA,mBAAA;ACpWA;ADwWA;EACA,eAAA;EACA,aAAA;EACA,mBAAA;EACA,YA5GA;AC1PA;ADwWA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;EACA,UAAA;EACA,iBAAA;ACtWA;ADyWA;EACA,eAAA;ACvWA;AD0WA;EACA,YAAA;EACA,iBAAA;ACxWA;AD4WA;EACA,aAAA;AC1WA;AD4WA;EACA,cAAA;AC1WA;AD+WA;EACA,cAAA;AC7WA;ADmXA;EACA,aAAA;EACA,SAAA;ACjXA;ADmXA;EACA,eAAA;EACA,gBAAA;EACA,UAAA;ACjXA;ADsXA;EACA,iBAAA;ACpXA;ADuXA;EACA,kBAAA;ACrXA;ADuXA;EACA,uBAAA;EACA,eAAA;EACA,gBAAA;EACA,eAAA;ACrXA;ADuXA;EACA,WAAA;EACA,sBAAA;EACA,kBAAA;EACA,UAAA;ACrXA;ADyXA;EA7JA,YAAA;EACA,cAAA;EACA,YAtBA;EAuBA,OAAA;EACA,YAAA;EACA,kBAAA;EACA,WAwJA;EACA,qCAAA;EACA,WAAA;EACA,gBAAA;ACjXA;ADwXA;EACA,YAAA;EACA,aAAA;ACtXA;AD2XA;EACA,cAAA;ACzXA;AD2XA;EACA,0BAAA;EACA,eAAA;ACzXA;AD2XA;EACA,0BAAA;ACzXA;AD4XA;EACA,gBAAA;AC1XA;AD6XA;EA9LA,YAAA;EACA,cAAA;EACA,YAtBA;EAuBA,OAAA;EACA,YAAA;EACA,kBAAA;EACA,WAPA;EAiMA,0CAAA;EACA,iBAAA;ACrXA;ADyXA;EACA,uBAAA;EACA,cAAA;EACA,eAAA;EACA,YAAA;EACA,gBAAA;EACA,YAAA;EACA,qBAAA;EACA,iBAAA;EACA,uBAAA;EACA,mBAAA;EACA,UAAA;ACvXA;ADyXA;EACA,gBAAA;ACvXA;ADyXA;EArNA,YAAA;EACA,cAAA;EACA,YAtBA;EAuBA,OAAA;EACA,YAAA;EACA,kBAAA;EACA,WAPA;EAwNA,qCAAA;EACA,0BAAA;EACA,iBAAA;ACjXA;ADqXA;EACA,0BAAA;EACA,gBAAA;ACnXA;ADqXA;EAjOA,YAAA;EACA,cAAA;EACA,YAtBA;EAuBA,OAAA;EACA,YAAA;EACA,kBAAA;EACA,WAPA;EAoOA,0CAAA;EACA,iBAAA;AC7WA;ADoXA;EACA,gBAAA;AClXA;ADoXA;EACA,UAAA;AClXA;;AAEA,yCAAyC","file":"NestedMenu.vue","sourcesContent":["<template>\n  <div class=\"me-nested-menu\">\n    <collapse\n      :title=\"!getLinkInTitleMenu ? titleMenu : ''\"\n      :opened=\"opened\"\n      variant=\"square\"\n    >\n      <template #collapse-info-title v-if=\"getLinkInTitleMenu\">\n        <a\n          :href=\"getLinkInTitleMenu.url\"\n          :class=\"{ active: getLinkInTitleMenu.active }\"\n          @click.stop=\"handleLinkClick(getLinkInTitleMenu, $event)\"\n        >\n          <span>\n            {{ getLinkInTitleMenu.label }}\n          </span>\n        </a>\n\n        <template v-if=\"getLinkInTitleMenu.buttons\">\n          <template v-for=\"(button, i) in getLinkInTitleMenu.buttons\">\n            <Icon\n              v-if=\"button.icon\"\n              v-me-tooltip.hover.bottom=\"button.label\"\n              :key=\"i\"\n              :icon=\"button.icon\"\n              :size=\"12\"\n              :color=\"button.iconColor || 'var(--me-gray-3)'\"\n              :hover-icon=\"button.hoverIcon\"\n              @click.stop=\"() => button.click && button.click(getLinkInTitleMenu, button)\"\n              class=\"link-button\"\n            />\n          </template>\n        </template>\n      </template>\n\n      <template v-if=\"isLoading && !currentItems.length \">\n        <div class=\"skeleton\" data-jest=\"skeleton\">\n          <me-skeleton-text\n            v-for=\"skeleton in limitStepSize\"\n            :key=\"skeleton\"\n            width=\"100px\"\n            height=\"24px\"\n          />\n        </div>\n      </template>\n\n      <template v-else>\n        <template v-if=\"currentItems.length\">\n          <SearchInput\n            v-if=\"searchByItems\"\n            :placeholder=\"searchByItems\"\n            @input=\"handleSearchInput\"\n          />\n\n          <div\n            v-for=\"(link, index) in currentLinksList\"\n            :key=\"link.id\"\n            :class=\"{ active: link.active }\"\n            class=\"nested-links indent-links dynamic-links d-flex flex-column\"\n            data-jest=\"nested-link\"\n          >\n            <div class=\"d-flex align-items-center\">\n              <me-checkbox\n                v-if=\"hasCheckbox\"\n                v-model=\"link.isChecked\"\n                @change=\"handleLinkClick(link, $event, index)\"\n              />\n\n              <a\n                :href=\"link.url\"\n                :class=\"{ active: link.active }\"\n                @click=\"handleLinkClick(link, $event, index)\"\n                class=\"indent-links link-label\"\n              >\n\n                <span\n                  v-me-tooltip=\"hasEllipsis ? link.label : ''\"\n                  @mouseenter=\"setHasEllipsis\"\n                >\n                  <i v-if=\"link.icon\" :class=\"link.icon\" />\n                  {{ link.label }}\n                </span>\n\n                <me-badge\n                  v-if=\"link.count\"\n                  appearance=\"light-default\"\n                  data-jest=\"badge-counter\"\n                >\n                  {{ link.count }}\n                </me-badge>\n              </a>\n\n              <template v-if=\"link.buttons\">\n                <div class=\"link-buttons\">\n                  <template v-for=\"(button, i) in link.buttons\">\n                    <Icon\n                      v-if=\"button.icon\"\n                      :tooltip=\"button.label\"\n                      :key=\"i\"\n                      :icon=\"button.icon\"\n                      :size=\"12\"\n                      :color=\"button.iconColor || 'var(--me-gray-3)'\"\n                      :hover-icon=\"button.hoverIcon\"\n                      @click.stop=\"() => button.click && button.click(link, button)\"\n                      :class=\"{\n                        'link-button--visible-always': linkButtonsVisibility === 'always'\n                      }\"\n                      :data-jest=\"`link-button-${i}`\"\n                      class=\"link-button\"\n                    />\n                  </template>\n                </div>\n              </template>\n            </div>\n          </div>\n\n          <div v-if=\"hasMoreItems\" class=\"see-more\">\n            <me-button\n              @click.prevent=\"handleSeeMore\"\n              :busy=\"isLoading\"\n              href=\"#\"\n              variant=\"link\"\n              size=\"sm\"\n              class=\"see-more__btn\"\n              data-jest=\"see-more\"\n            >\n              {{ $toolkit.t('nestedMenu.seeMore') }}\n            </me-button>\n          </div>\n\n          <div v-if=\"!currentLinksList.length && term\" class=\"empty-search\" data-jest=\"empty-search\">\n            {{ $toolkit.t('nestedMenu.emptySearch') }}\n          </div>\n        </template>\n\n        <template v-else>\n          <div v-if=\"$slots.links\" class=\"nested-links indent-links\">\n            <slot name=\"links\" />\n          </div>\n\n          <div v-if=\"$slots.listgroup\" class=\"nested-component\">\n            <slot name=\"listgroup\" />\n          </div>\n        </template>\n      </template>\n    </collapse>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport take from 'lodash/take'\nimport Collapse from '../Collapse.vue'\nimport SearchInput from './SearchInput.vue'\nimport Icon from './../Icon.vue'\n\nexport default {\n  name: 'MeNestedMenu',\n  components: {\n    Collapse,\n    SearchInput,\n    Icon\n  },\n\n  props: {\n    titleMenu: {\n      type: String,\n      default: ''\n    },\n    items: {\n      type: Array,\n      default: () => []\n    },\n    searchByItems: {\n      type: String,\n      default: () => ''\n    },\n    hasCheckbox: {\n      type: Boolean,\n      default: false\n    },\n    opened: {\n      type: Boolean,\n      default: true\n    },\n    badgePrecision: {\n      type: Number,\n      default: 2\n    },\n    limitStepSize: {\n      type: Number,\n      default: 5\n    },\n    initialLimit: {\n      type: Number,\n      default: null,\n      validator: prop => typeof prop === 'number' || prop === null\n    },\n    provider: {\n      type: Function,\n      default: null\n    },\n    linkButtonsVisibility: {\n      type: String,\n      default: 'hover',\n      validator: value => ['hover', 'always'].includes(value)\n    }\n  },\n\n  data() {\n    return {\n      listPage: 1,\n      hits: null,\n      hasEllipsis: false,\n      isLoading: false,\n      term: '',\n      currentItems: cloneDeep(this.items),\n      currentLimit: this.initialLimit\n    }\n  },\n\n  computed: {\n    currentLinksList() {\n      if (this.currentLimit) {\n        return take(this.filteredLinksList, this.currentLimit)\n      }\n\n      return this.filteredLinksList\n    },\n\n    filteredLinksList() {\n      if (this.term) {\n        return this.linksList.filter(item => item.label.toLowerCase().includes(this.term.toLowerCase()))\n      }\n\n      return this.linksList\n    },\n\n    linksList() {\n      return this.currentItems.filter(item => !item.titleMenu).map(item => ({\n        ...item,\n        count: this.badgeCounter(item)\n      }))\n    },\n\n    getLinkInTitleMenu() {\n      return this.currentItems.find(item => item.titleMenu)\n    },\n\n    hasMoreItems() {\n      if (!this.currentLimit) return false\n\n      if (this.hits) return this.hits > this.currentLimit\n\n      return this.filteredLinksList.length > this.currentLimit\n    }\n  },\n\n  watch: {\n    items(value) {\n      this.currentItems = cloneDeep(value)\n    }\n  },\n\n  created() {\n    if (this.provider) {\n      this.handleProvider()\n    }\n  },\n\n  methods: {\n    setHasEllipsis(event) {\n      this.hasEllipsis = event.target.offsetWidth < event.target.scrollWidth\n    },\n\n    handleProvider() {\n      this.isLoading = true\n      this.provider({ page: this.listPage, pageSize: this.limitStepSize }, this.providerCallback)\n    },\n\n    providerCallback({ result, hits }) {\n      this.currentItems = [...this.currentItems, ...result]\n      this.hits = hits\n      this.isLoading = false\n      this.currentLimit += this.limitStepSize\n    },\n\n    handleSeeMore() {\n      if (this.provider) {\n        this.listPage++\n        return this.handleProvider()\n      }\n\n      this.currentLimit += this.limitStepSize\n    },\n\n    handleSearchInput(value) {\n      this.term = value\n    },\n\n    badgeCounter(link) {\n      const counter = link.count ?? 0\n\n      if (this.badgePrecision === 0) return counter\n\n      const counterStr = counter.toString()\n\n      if (counterStr.length > this.badgePrecision) {\n        const newCounter = '9'\n        return newCounter.padEnd(this.badgePrecision, '9') + '+'\n      }\n\n      return counter\n    },\n\n    handleLinkClick(link, event, linkIndex = -1) {\n      if (this.hasCheckbox) {\n        this.currentItems = this.currentItems.map((item, index) => linkIndex === index ? { ...item, isChecked: !item.isChecked } : item)\n      }\n\n      if (!link.click) return\n\n      if (event.cancelable) {\n        event.preventDefault()\n      }\n\n      link.click(link)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $cell-height: 36px;\n\n  %append-links {\n    display: flex;\n    align-items: center;\n    margin-left: 40px;\n    min-height: 36px;\n    font-size: 14px;\n  }\n\n  ::v-deep {\n    .checkbox-item {\n      .me-checkbox {\n        margin-left: 40px;\n        text-indent: 0;\n      }\n    }\n  }\n\n  @mixin menu-link-hover ($heightValue: $cell-height, $widthValue: 100%) {\n    content: ' ';\n    display: block;\n    height: $heightValue;\n    left: 0;\n    padding: 7px;\n    position: absolute;\n    width: $widthValue;\n  }\n\n  div.me-nested-menu {\n    ::v-deep section.me-collapse > h5 {\n      color: var(--me-gray-2);\n      font-size: 14px;\n      font-weight: 600;\n      letter-spacing: normal;\n      height: $cell-height;\n      margin: 0;\n      padding-left: 8px;\n      padding-right: 16px;\n\n      button {\n        color: var(--me-gray-2);\n        float: none;\n        margin-right: 8px;\n        padding: 0;\n        vertical-align: baseline;\n        z-index: 1;\n      }\n    }\n\n    ::v-deep section.me-collapse > h5 > a, .nested-links ::v-deep a {\n      display: flex;\n      align-items: center;\n      min-width: 0;\n      flex-grow: 1;\n      color: var(--me-gray-2);\n      font-size: 14px;\n      text-decoration: none;\n      height: $cell-height;\n\n      &:hover {\n        &::after {\n          @include menu-link-hover($cell-height);\n          background-color: rgba(0, 0, 0, 0.06);\n        }\n      }\n\n      &.active {\n        color: var(--me-primary-2);\n\n        &::after {\n          @include menu-link-hover($cell-height);\n          background-color: rgba(10, 140, 204, 0.12);\n        }\n      }\n    }\n\n    .see-more {\n      @extend %append-links;\n\n      &__btn {\n        padding: 0;\n        display: inline;\n        text-decoration: none;\n        box-shadow: none;\n        height: auto;\n      }\n    }\n\n    .empty-search {\n      @extend %append-links;\n    }\n\n    .skeleton {\n      margin-left: 36px;\n    }\n\n    .nested-links {\n      &.indent-links {\n        padding-left: 36px;\n        padding-right: 16px;\n      }\n\n      &.dynamic-links {\n        .link-label {\n          cursor: pointer;\n          display: flex;\n          align-items: center;\n          height: $cell-height;\n\n          span {\n            overflow: hidden;\n            text-overflow: ellipsis;\n            white-space: nowrap;\n            z-index: 2;\n            margin-right: 4px;\n          }\n\n          i {\n            display: inline;\n          }\n\n          ::v-deep .me-badge {\n            float: right;\n            margin-left: auto;\n          }\n        }\n\n        .link-button {\n          display: none;\n\n          &--visible-always {\n            display: block;\n          }\n        }\n\n        &:hover {\n          .link-button {\n            display: block;\n          }\n        }\n      }\n    }\n\n    .link-buttons {\n      display: flex;\n      gap: 16px;\n\n      .link-button {\n        cursor: pointer;\n        font-weight: 400;\n        z-index: 1;\n      }\n    }\n\n    .nested-component {\n      .me-nested-menu {\n        margin-left: 20px;\n      }\n\n      ::v-deep .me-accordion-list {\n        margin-bottom: 8px;\n\n         > a {\n            color: var(--me-gray-2);\n            font-size: 14px;\n            font-weight: 600;\n            padding-left: 0;\n\n            & > i {\n              float: left;\n              margin: 0px 10px 3px 0;\n              position: relative;\n              z-index: 2;\n            }\n\n            &:hover {\n              &::after {\n                @include menu-link-hover($widthValue: 128%);\n                background-color: rgba(0, 0, 0, 0.06);\n                left: -70px;\n                margin-top: -4px;\n              }\n            }\n        }\n\n        &.opened {\n          .me-list-group {\n            li {\n              height: 28px;\n              margin: 4px 0;\n            }\n          }\n        }\n\n        > .me-list-group {\n            margin-left: 0;\n\n            li.active {\n              color: var(--me-primary-2);\n              cursor: pointer;\n\n              a {\n                color: var(--me-primary-2);\n              }\n\n              &:hover {\n                font-weight: 600;\n              }\n\n              &::after {\n                @include menu-link-hover;\n                background-color: rgba(10, 140, 204, 0.12);\n                margin-top: -28px;\n              }\n            }\n\n            a {\n              color: var(--me-gray-2);\n              display: block;\n              font-size: 14px;\n              height: 28px;\n              overflow: hidden;\n              padding: 3px;\n              text-decoration: none;\n              text-indent: 28px;\n              text-overflow: ellipsis;\n              white-space: nowrap;\n              width: 98%;\n\n              &:hover {\n                font-weight: 600;\n\n                &::after {\n                  @include menu-link-hover;\n                  background-color: rgba(0, 0, 0, 0.06);\n                  color: rgba(0, 0, 0, 0.06);\n                  margin-top: -24px;\n                }\n              }\n\n              &:hover.active {\n                color: var(--me-primary-2);\n                font-weight: 600;\n\n                &::after {\n                  @include menu-link-hover;\n                  background-color: rgba(10, 140, 204, 0.12);\n                  margin-top: -28px;\n                }\n              }\n            }\n        }\n      }\n\n      ::v-deep .me-list-group {\n        margin-left: 9px;\n\n        li {\n          padding: 0;\n        }\n      }\n    }\n  }\n\n</style>\n","div.me-nested-menu .empty-search, div.me-nested-menu .see-more {\n  display: flex;\n  align-items: center;\n  margin-left: 40px;\n  min-height: 36px;\n  font-size: 14px;\n}\n\n::v-deep .checkbox-item .me-checkbox {\n  margin-left: 40px;\n  text-indent: 0;\n}\n\ndiv.me-nested-menu ::v-deep section.me-collapse > h5 {\n  color: var(--me-gray-2);\n  font-size: 14px;\n  font-weight: 600;\n  letter-spacing: normal;\n  height: 36px;\n  margin: 0;\n  padding-left: 8px;\n  padding-right: 16px;\n}\ndiv.me-nested-menu ::v-deep section.me-collapse > h5 button {\n  color: var(--me-gray-2);\n  float: none;\n  margin-right: 8px;\n  padding: 0;\n  vertical-align: baseline;\n  z-index: 1;\n}\ndiv.me-nested-menu ::v-deep section.me-collapse > h5 > a, div.me-nested-menu .nested-links ::v-deep a {\n  display: flex;\n  align-items: center;\n  min-width: 0;\n  flex-grow: 1;\n  color: var(--me-gray-2);\n  font-size: 14px;\n  text-decoration: none;\n  height: 36px;\n}\ndiv.me-nested-menu ::v-deep section.me-collapse > h5 > a:hover::after, div.me-nested-menu .nested-links ::v-deep a:hover::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(0, 0, 0, 0.06);\n}\ndiv.me-nested-menu ::v-deep section.me-collapse > h5 > a.active, div.me-nested-menu .nested-links ::v-deep a.active {\n  color: var(--me-primary-2);\n}\ndiv.me-nested-menu ::v-deep section.me-collapse > h5 > a.active::after, div.me-nested-menu .nested-links ::v-deep a.active::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(10, 140, 204, 0.12);\n}\ndiv.me-nested-menu .see-more__btn {\n  padding: 0;\n  display: inline;\n  text-decoration: none;\n  box-shadow: none;\n  height: auto;\n}\ndiv.me-nested-menu .skeleton {\n  margin-left: 36px;\n}\ndiv.me-nested-menu .nested-links.indent-links {\n  padding-left: 36px;\n  padding-right: 16px;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label {\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  height: 36px;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label span {\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  z-index: 2;\n  margin-right: 4px;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label i {\n  display: inline;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-label ::v-deep .me-badge {\n  float: right;\n  margin-left: auto;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-button {\n  display: none;\n}\ndiv.me-nested-menu .nested-links.dynamic-links .link-button--visible-always {\n  display: block;\n}\ndiv.me-nested-menu .nested-links.dynamic-links:hover .link-button {\n  display: block;\n}\ndiv.me-nested-menu .link-buttons {\n  display: flex;\n  gap: 16px;\n}\ndiv.me-nested-menu .link-buttons .link-button {\n  cursor: pointer;\n  font-weight: 400;\n  z-index: 1;\n}\ndiv.me-nested-menu .nested-component .me-nested-menu {\n  margin-left: 20px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list {\n  margin-bottom: 8px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > a {\n  color: var(--me-gray-2);\n  font-size: 14px;\n  font-weight: 600;\n  padding-left: 0;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > a > i {\n  float: left;\n  margin: 0px 10px 3px 0;\n  position: relative;\n  z-index: 2;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > a:hover::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 128%;\n  background-color: rgba(0, 0, 0, 0.06);\n  left: -70px;\n  margin-top: -4px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list.opened .me-list-group li {\n  height: 28px;\n  margin: 4px 0;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group {\n  margin-left: 0;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group li.active {\n  color: var(--me-primary-2);\n  cursor: pointer;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group li.active a {\n  color: var(--me-primary-2);\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group li.active:hover {\n  font-weight: 600;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group li.active::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(10, 140, 204, 0.12);\n  margin-top: -28px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group a {\n  color: var(--me-gray-2);\n  display: block;\n  font-size: 14px;\n  height: 28px;\n  overflow: hidden;\n  padding: 3px;\n  text-decoration: none;\n  text-indent: 28px;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n  width: 98%;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group a:hover {\n  font-weight: 600;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group a:hover::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(0, 0, 0, 0.06);\n  color: rgba(0, 0, 0, 0.06);\n  margin-top: -24px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group a:hover.active {\n  color: var(--me-primary-2);\n  font-weight: 600;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-accordion-list > .me-list-group a:hover.active::after {\n  content: \" \";\n  display: block;\n  height: 36px;\n  left: 0;\n  padding: 7px;\n  position: absolute;\n  width: 100%;\n  background-color: rgba(10, 140, 204, 0.12);\n  margin-top: -28px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-list-group {\n  margin-left: 9px;\n}\ndiv.me-nested-menu .nested-component ::v-deep .me-list-group li {\n  padding: 0;\n}\n\n/*# sourceMappingURL=NestedMenu.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1P = "data-v-6668a3bf";
  /* module identifier */
  const __vue_module_identifier__$1P = undefined;
  /* functional template */
  const __vue_is_functional_template__$1P = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1P, staticRenderFns: __vue_staticRenderFns__$1P },
    __vue_inject_styles__$1P,
    __vue_script__$1P,
    __vue_scope_id__$1P,
    __vue_is_functional_template__$1P,
    __vue_module_identifier__$1P,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1Q = {
  name: 'MeRadioInput',
  components: {
    BFormGroup,
    BFormRadioGroup,
    BFormRadio
  },

  props: {
    value: {
      type: [String, Object],
      default: null
    },
    hint: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    id: {
      type: [String, Number],
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    valueField: {
      type: String,
      default: 'value'
    },
    textField: {
      type: String,
      default: 'name'
    },
    disabledField: {
      type: String,
      default: 'disabled'
    },
    stacked: {
      type: Boolean,
      default: false
    },
    card: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      current: this.value
    }
  },

  watch: {
    value(val) {
      this.current = val;
    }
  }
};

/* script */
const __vue_script__$1Q = script$1Q;

/* template */
var __vue_render__$1Q = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "b-form-group",
    {
      class: ["me-radio-input", { "with-hint": _vm.hint }],
      attrs: { label: _vm.label }
    },
    [
      _vm.hint
        ? _c("div", { staticClass: "hint" }, [
            _vm._v("\n    " + _vm._s(_vm.hint) + "\n  ")
          ])
        : _vm._e(),
      _vm._v(" "),
      _c(
        "b-form-radio-group",
        {
          class: { "radio-stacked": _vm.stacked, "radio-card": _vm.card },
          attrs: { id: _vm.id, name: _vm.name, stacked: _vm.stacked },
          on: {
            input: function($event) {
              return _vm.$emit("input", _vm.current)
            }
          },
          model: {
            value: _vm.current,
            callback: function($$v) {
              _vm.current = $$v;
            },
            expression: "current"
          }
        },
        _vm._l(_vm.options, function(option) {
          return _c(
            "b-form-radio",
            {
              key: option[_vm.textField],
              staticClass: "radio-option",
              attrs: {
                value: option[_vm.valueField],
                disabled: option[_vm.disabledField]
              }
            },
            [
              option.html
                ? [_c("div", { domProps: { innerHTML: _vm._s(option.html) } })]
                : _c(
                    "div",
                    { staticClass: "radio-text" },
                    [
                      _vm._t(
                        "item",
                        [
                          _vm._t(
                            "text",
                            [
                              _c("span", [
                                _vm._v(
                                  "\n              " +
                                    _vm._s(option[_vm.textField]) +
                                    "\n            "
                                )
                              ])
                            ],
                            null,
                            option
                          ),
                          _vm._v(" "),
                          option.tooltip
                            ? _c("me-icon", {
                                directives: [
                                  {
                                    name: "me-tooltip",
                                    rawName: "v-me-tooltip.hover",
                                    value: option.tooltip,
                                    expression: "option.tooltip",
                                    modifiers: { hover: true }
                                  }
                                ],
                                staticClass: "radio-tooltip",
                                attrs: { icon: "me-icon-s icon-circle-info" }
                              })
                            : _vm._e()
                        ],
                        null,
                        option
                      )
                    ],
                    2
                  )
            ],
            2
          )
        }),
        1
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1Q = [];
__vue_render__$1Q._withStripped = true;

  /* style */
  const __vue_inject_styles__$1Q = function (inject) {
    if (!inject) return
    inject("data-v-638eb2ca_0", { source: ".me-radio-input .hint[data-v-638eb2ca] {\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-4);\n  margin-bottom: 16px;\n}\n.me-radio-input.with-hint[data-v-638eb2ca]  legend {\n  padding-bottom: 0;\n}\n.me-radio-input .radio-option .radio-text[data-v-638eb2ca] {\n  color: var(--me-gray-2);\n  font-size: 14px;\n  cursor: pointer;\n}\n.me-radio-input .radio-option .radio-text .radio-tooltip[data-v-638eb2ca] {\n  padding-left: 8px;\n  cursor: initial;\n}\n.me-radio-input .radio-option[data-v-638eb2ca]  legend {\n  font-size: 12px;\n  padding-bottom: 16px;\n}\n.me-radio-input .radio-option[data-v-638eb2ca]  .custom-control-label::after {\n  cursor: pointer;\n}\n.me-radio-input .radio-option[data-v-638eb2ca]  .custom-control-input[disabled] ~ .custom-control-label .radio-text,\n.me-radio-input .radio-option[data-v-638eb2ca]  .custom-control-input:disabled ~ .custom-control-label .radio-text {\n  color: var(--me-gray-4);\n}\n.me-radio-input .radio-option[data-v-638eb2ca]  .custom-control-input[disabled] ~ .custom-control-label::after,\n.me-radio-input .radio-option[data-v-638eb2ca]  .custom-control-input:disabled ~ .custom-control-label::after {\n  cursor: initial;\n}\n.me-radio-input .radio-card[data-v-638eb2ca] {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 8px;\n}\n.me-radio-input .radio-card .radio-option[data-v-638eb2ca] {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  background-color: var(--me-white);\n  padding-right: 16px;\n  padding-left: calc(1.5rem + 16px);\n  min-height: 64px;\n  margin: 0px;\n  display: inline-flex;\n  align-items: center;\n  flex-grow: 1;\n}\n.me-radio-input .radio-card .radio-option .radio-text[data-v-638eb2ca] {\n  display: flex;\n  min-width: 150px;\n  justify-content: space-between;\n}\n.me-radio-input .radio-card .radio-option[data-v-638eb2ca]  .custom-control-label {\n  width: 100%;\n}\n.me-radio-input .radio-stacked[data-v-638eb2ca]  .custom-radio:not(:last-child) {\n  padding-bottom: 16px;\n}\n.me-radio-input .radio-stacked .radio-option[data-v-638eb2ca] {\n  padding-left: 24px;\n}\n.me-radio-input .radio-stacked.radio-card[data-v-638eb2ca] {\n  flex-direction: column;\n}\n.me-radio-input .radio-stacked.radio-card .radio-option[data-v-638eb2ca] {\n  display: flex;\n  padding: 0;\n  padding-left: 40px;\n  padding-right: 16px;\n}\n.me-radio-input .radio-stacked.radio-card .radio-option[data-v-638eb2ca]:last-child {\n  margin-bottom: 0;\n}\n\n/*# sourceMappingURL=RadioInput.vue.map */", map: {"version":3,"sources":["/app/build/src/components/RadioInput.vue","RadioInput.vue"],"names":[],"mappings":"AA2HA;EACA,eAAA;EACA,iBAAA;EACA,uBAAA;EACA,mBAAA;AC1HA;AD8HA;EACA,iBAAA;AC5HA;ADiIA;EACA,uBAAA;EACA,eAAA;EACA,eAAA;AC/HA;ADiIA;EACA,iBAAA;EACA,eAAA;AC/HA;ADoIA;EACA,eAAA;EACA,oBAAA;AClIA;ADsIA;EACA,eAAA;ACpIA;AD0IA;;EACA,uBAAA;ACvIA;AD0IA;;EACA,eAAA;ACvIA;AD6IA;EACA,aAAA;EACA,eAAA;EACA,QAAA;AC3IA;AD6IA;EACA,kCAAA;EACA,kBAAA;EACA,iCAAA;EACA,mBAAA;EACA,iCAAA;EACA,gBAAA;EACA,WAAA;EACA,oBAAA;EACA,mBAAA;EACA,YAAA;AC3IA;AD6IA;EACA,aAAA;EACA,gBAAA;EACA,8BAAA;AC3IA;AD+IA;EACA,WAAA;AC7IA;ADqJA;EACA,oBAAA;ACnJA;ADsJA;EACA,kBAAA;ACpJA;ADuJA;EACA,sBAAA;ACrJA;ADuJA;EACA,aAAA;EACA,UAAA;EACA,kBAAA;EACA,mBAAA;ACrJA;ADuJA;EACA,gBAAA;ACrJA;;AAEA,yCAAyC","file":"RadioInput.vue","sourcesContent":["<template>\n  <b-form-group\n    :label=\"label\"\n    :class=\"['me-radio-input', { 'with-hint': hint }]\"\n  >\n    <div v-if=\"hint\" class=\"hint\">\n      {{ hint }}\n    </div>\n\n    <b-form-radio-group\n      :id=\"id\"\n      v-model=\"current\"\n      :name=\"name\"\n      @input=\"$emit('input', current)\"\n      :stacked=\"stacked\"\n      :class=\"{'radio-stacked': stacked , 'radio-card': card}\"\n    >\n      <b-form-radio\n        v-for=\"option in options\"\n        :key=\"option[textField]\"\n        :value=\"option[valueField]\"\n        :disabled=\"option[disabledField]\"\n        class=\"radio-option\"\n      >\n        <template v-if=\"option.html\">\n          <div v-html=\"option.html\" />\n        </template>\n\n        <div v-else class=\"radio-text\">\n          <slot v-bind=\"option\" name=\"item\">\n            <slot v-bind=\"option\" name=\"text\">\n              <span>\n                {{ option[textField] }}\n              </span>\n            </slot>\n\n            <me-icon\n              v-if=\"option.tooltip\"\n              v-me-tooltip.hover=\"option.tooltip\"\n              icon=\"me-icon-s icon-circle-info\"\n              class=\"radio-tooltip\"\n            />\n          </slot>\n        </div>\n      </b-form-radio>\n    </b-form-radio-group>\n  </b-form-group>\n</template>\n\n<script>\nimport { BFormGroup, BFormRadioGroup, BFormRadio } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeRadioInput',\n  components: {\n    BFormGroup,\n    BFormRadioGroup,\n    BFormRadio\n  },\n\n  props: {\n    value: {\n      type: [String, Object],\n      default: null\n    },\n    hint: {\n      type: String,\n      default: ''\n    },\n    label: {\n      type: String,\n      default: ''\n    },\n    options: {\n      type: Array,\n      default: () => []\n    },\n    id: {\n      type: [String, Number],\n      default: ''\n    },\n    name: {\n      type: String,\n      required: true\n    },\n    valueField: {\n      type: String,\n      default: 'value'\n    },\n    textField: {\n      type: String,\n      default: 'name'\n    },\n    disabledField: {\n      type: String,\n      default: 'disabled'\n    },\n    stacked: {\n      type: Boolean,\n      default: false\n    },\n    card: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      current: this.value\n    }\n  },\n\n  watch: {\n    value(val) {\n      this.current = val\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-radio-input {\n    .hint {\n      font-size: 12px;\n      line-height: 16px;\n      color: var(--me-gray-4);\n      margin-bottom: 16px;\n    }\n\n    &.with-hint {\n      ::v-deep legend {\n        padding-bottom: 0;\n      }\n    }\n\n    .radio-option {\n      .radio-text {\n        color: var(--me-gray-2);\n        font-size: 14px;\n        cursor: pointer;\n\n        .radio-tooltip {\n          padding-left: 8px;\n          cursor: initial;\n        }\n      }\n\n      ::v-deep {\n        legend {\n          font-size: 12px;\n          padding-bottom: 16px;\n        }\n\n        .custom-control-label {\n          &::after {\n            cursor: pointer;\n          }\n        }\n\n        .custom-control-input[disabled] ~ .custom-control-label,\n        .custom-control-input:disabled ~ .custom-control-label {\n          .radio-text {\n            color: var(--me-gray-4);\n          }\n\n          &::after {\n            cursor: initial;\n          }\n        }\n      }\n    }\n\n    .radio-card {\n      display: flex;\n      flex-wrap: wrap;\n      gap: 8px;\n\n      .radio-option {\n        border: 1px solid var(--me-gray-6);\n        border-radius: 8px;\n        background-color: var(--me-white);\n        padding-right: 16px;\n        padding-left: calc(1.5rem + 16px);\n        min-height: 64px;\n        margin: 0px;\n        display: inline-flex;\n        align-items: center;\n        flex-grow: 1;\n\n        .radio-text {\n          display: flex;\n          min-width: 150px;\n          justify-content: space-between;\n        }\n\n        ::v-deep {\n          .custom-control-label {\n            width: 100%;\n          }\n        }\n\n      }\n    }\n\n    .radio-stacked {\n      ::v-deep .custom-radio:not(:last-child) {\n        padding-bottom: 16px;\n      }\n\n      .radio-option {\n        padding-left: 24px;\n      }\n\n      &.radio-card {\n        flex-direction: column;\n\n        .radio-option {\n          display: flex;\n          padding: 0;\n          padding-left: 40px;\n          padding-right: 16px;\n\n          &:last-child {\n            margin-bottom: 0;\n          }\n        }\n      }\n\n    }\n  }\n\n</style>\n",".me-radio-input .hint {\n  font-size: 12px;\n  line-height: 16px;\n  color: var(--me-gray-4);\n  margin-bottom: 16px;\n}\n.me-radio-input.with-hint ::v-deep legend {\n  padding-bottom: 0;\n}\n.me-radio-input .radio-option .radio-text {\n  color: var(--me-gray-2);\n  font-size: 14px;\n  cursor: pointer;\n}\n.me-radio-input .radio-option .radio-text .radio-tooltip {\n  padding-left: 8px;\n  cursor: initial;\n}\n.me-radio-input .radio-option ::v-deep legend {\n  font-size: 12px;\n  padding-bottom: 16px;\n}\n.me-radio-input .radio-option ::v-deep .custom-control-label::after {\n  cursor: pointer;\n}\n.me-radio-input .radio-option ::v-deep .custom-control-input[disabled] ~ .custom-control-label .radio-text,\n.me-radio-input .radio-option ::v-deep .custom-control-input:disabled ~ .custom-control-label .radio-text {\n  color: var(--me-gray-4);\n}\n.me-radio-input .radio-option ::v-deep .custom-control-input[disabled] ~ .custom-control-label::after,\n.me-radio-input .radio-option ::v-deep .custom-control-input:disabled ~ .custom-control-label::after {\n  cursor: initial;\n}\n.me-radio-input .radio-card {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 8px;\n}\n.me-radio-input .radio-card .radio-option {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  background-color: var(--me-white);\n  padding-right: 16px;\n  padding-left: calc(1.5rem + 16px);\n  min-height: 64px;\n  margin: 0px;\n  display: inline-flex;\n  align-items: center;\n  flex-grow: 1;\n}\n.me-radio-input .radio-card .radio-option .radio-text {\n  display: flex;\n  min-width: 150px;\n  justify-content: space-between;\n}\n.me-radio-input .radio-card .radio-option ::v-deep .custom-control-label {\n  width: 100%;\n}\n.me-radio-input .radio-stacked ::v-deep .custom-radio:not(:last-child) {\n  padding-bottom: 16px;\n}\n.me-radio-input .radio-stacked .radio-option {\n  padding-left: 24px;\n}\n.me-radio-input .radio-stacked.radio-card {\n  flex-direction: column;\n}\n.me-radio-input .radio-stacked.radio-card .radio-option {\n  display: flex;\n  padding: 0;\n  padding-left: 40px;\n  padding-right: 16px;\n}\n.me-radio-input .radio-stacked.radio-card .radio-option:last-child {\n  margin-bottom: 0;\n}\n\n/*# sourceMappingURL=RadioInput.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1Q = "data-v-638eb2ca";
  /* module identifier */
  const __vue_module_identifier__$1Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$1Q = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1Q, staticRenderFns: __vue_staticRenderFns__$1Q },
    __vue_inject_styles__$1Q,
    __vue_script__$1Q,
    __vue_scope_id__$1Q,
    __vue_is_functional_template__$1Q,
    __vue_module_identifier__$1Q,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1R = {
  name: 'MePagination',
  components: {
    BDropdownGroup,
    BDropdownDivider,
    Button: __vue_component__$6,
    Icon: __vue_component__,
    MeDropdown: __vue_component__$8,
    MeDropdownItem: __vue_component__$2,
    MeRadioInput: __vue_component__$1Q
  },
  props: {
    searchable: {
      type: Boolean,
      default: false
    },
    filter: {
      type: String,
      default: ''
    },
    pageSize: {
      type: Number,
      default: 0
    },
    hits: {
      type: Number,
      default: 0
    },
    maxHits: {
      type: Number,
      default: 1000000
    },
    lazy: {
      type: Boolean,
      default: false
    },
    refresh: {
      type: Function,
      default: () => null
    },
    elementsPerPage: {
      type: Array,
      required: false,
      default() {
        return [10, 20, 50, 100]
      }
    },
    stateKey: {
      type: String,
      default: ''
    },
    showColumnsCustomization: {
      type: Boolean,
      default: false
    },
    hideDensityOptions: {
      type: Boolean,
      default: false
    },
    customMenu: {
      type: Array,
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    density: {
      type: String,
      default: 'md'
    }
  },

  data() {
    return {
      currentFilter: this.filter,
      currentDensity: this.density,
      densityOptions: [
        { name: this.$toolkit.t('grid.density.compact'), value: 'sm' },
        { name: this.$toolkit.t('grid.density.default'), value: 'md' },
        { name: this.$toolkit.t('grid.density.spacious'), value: 'lg' }
      ],
      isSearching: false,
      currentPaginationPage: this.currentPage,
      maxHitsLimit: 10000,
      pageSizeOption: `${this.pageSize}`
    }
  },

  computed: {
    densityRadioName() {
      return `${this.stateKey || v4()}_density`
    },

    elementsPerPageRadioName() {
      return `${this.stateKey || v4()}_pageSize`
    },

    pageInfo() {
      let first = 0;
      let last = 0;
      if (this.hits > 0) {
        first = (this.currentPage - 1) * this.pageSize + 1;
        last = first + this.pageSize - 1;
        if (last > this.hits) last = this.hits;
      }
      return first + ' - ' + last
    },

    elementsPerPageRadio() {
      const perPageOptions = this.elementsPerPage.map(size => (
        { name: this.$toolkit.t('grid.paging.perPage', size), value: `${size}` }
      ));

      return perPageOptions
    },

    disableLastPageButton() {
      return this.hits > this.maxHitsLimit
    },

    hasReachedResultsLimit() {
      const first = (this.currentPage - 1) * this.pageSize + 1;
      const last = first + this.pageSize - 1;
      return last >= this.maxHitsLimit && this.disableLastPageButton
    },

    lastPageMessage() {
      return this.disableLastPageButton ? this.$toolkit.t('grid.paging.lastPageMessage') : ''
    },

    hasNextPage() {
      return this.currentPaginationPage < this.totalPages()
    },

    hasPreviousPage() {
      return this.currentPaginationPage > 1
    }
  },

  watch: {
    currentFilter(value) {
      this.$emit('update:filter', value);
    },

    currentPage(value) {
      this.currentPaginationPage = value;
    },

    pageSizeOption(value) {
      this.pageSizeOption = `${value}`;
    },

    density(value) {
      this.currentDensity = value;
    }
  },

  methods: {
    setDensity(densityOptionValue) {
      this.currentDensity = densityOptionValue;
      this.$emit('density', densityOptionValue);
    },

    handleRefresh() {
      this.firstPage();
      this.refresh();
    },

    totalPages() {
      return Math.ceil(this.hits / this.pageSize)
    },

    previousPage() {
      this.currentPaginationPage--;
      this.$emit('update:currentPage', this.currentPaginationPage);
    },

    nextPage() {
      this.currentPaginationPage++;
      this.$emit('update:currentPage', this.currentPaginationPage);
    },

    firstPage() {
      this.currentPaginationPage = 1;
      this.$emit('update:currentPage', this.currentPaginationPage);
    },

    handleSetSize(size) {
      this.firstPage();
      this.pageSizeOption = size;
      this.$emit('setSize', parseInt(size));
    },

    lastPage() {
      this.currentPaginationPage = this.totalPages();
      this.$emit('update:currentPage', this.currentPaginationPage);
    },

    handleClose() {
      this.isSearching = false;
      this.$emit('update:filter', null);
    },

    handleOpen() {
      this.isSearching = true;
      this.$emit('open');
    }
  }
};

/* script */
const __vue_script__$1R = script$1R;

/* template */
var __vue_render__$1R = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "nav",
    { staticClass: "me-pagination" },
    [
      _vm.searchable
        ? _c(
            "div",
            { staticClass: "search" },
            [
              _vm.isSearching
                ? _c("input", {
                    directives: [
                      {
                        name: "model",
                        rawName: "v-model",
                        value: _vm.currentFilter,
                        expression: "currentFilter"
                      }
                    ],
                    staticClass: "form-control form-control-sm rounded-pill",
                    attrs: {
                      placeholder: _vm.$toolkit.t("grid.search"),
                      type: "text"
                    },
                    domProps: { value: _vm.currentFilter },
                    on: {
                      input: function($event) {
                        if ($event.target.composing) {
                          return
                        }
                        _vm.currentFilter = $event.target.value;
                      }
                    }
                  })
                : _vm._e(),
              _vm._v(" "),
              _vm.isSearching
                ? _c("Button", {
                    attrs: {
                      pill: "",
                      variant: "light-secondary",
                      icon: "me-icon icon-times",
                      size: "sm"
                    },
                    on: { click: _vm.handleClose }
                  })
                : _c("Button", {
                    attrs: {
                      pill: "",
                      variant: "light-secondary",
                      icon: "me-icon icon-search",
                      size: "sm"
                    },
                    on: { click: _vm.handleOpen }
                  })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _c("Button", {
        attrs: {
          disabled: !_vm.hasPreviousPage,
          pill: "",
          variant: "light-secondary",
          size: "sm",
          icon: "me-icon icon-chevron-left",
          "data-jest": "btn-previous-page",
          "data-cy": "previous-page"
        },
        on: { click: _vm.previousPage }
      }),
      _vm._v(" "),
      _c("span", [
        _vm._v(
          _vm._s(_vm.pageInfo) + " " + _vm._s(_vm.$toolkit.t("grid.of")) + " "
        ),
        _vm.hits >= _vm.maxHits
          ? _c("span", [
              _vm._v(_vm._s(_vm._f("me-number")(_vm.maxHits, 0)) + "...")
            ])
          : _c("span", [_vm._v(_vm._s(_vm._f("me-number")(_vm.hits, 0)))])
      ]),
      _vm._v(" "),
      !_vm.hasNextPage || _vm.hasReachedResultsLimit
        ? _c("Button", {
            directives: [
              {
                name: "me-tooltip",
                rawName: "v-me-tooltip",
                value: _vm.$toolkit.t("grid.paging.lastPageMessage"),
                expression: "$toolkit.t('grid.paging.lastPageMessage')"
              }
            ],
            staticClass: "button__disabled",
            attrs: {
              pill: "",
              variant: "light-secondary",
              size: "sm",
              icon: "me-icon icon-chevron-right"
            }
          })
        : _c("Button", {
            attrs: {
              pill: "",
              variant: "light-secondary",
              size: "sm",
              icon: "me-icon icon-chevron-right",
              "data-jest": "btn-next-page",
              "data-cy": "next-page"
            },
            on: { click: _vm.nextPage }
          }),
      _vm._v(" "),
      _vm.lazy
        ? _c("Button", {
            staticClass: "refresh",
            attrs: {
              pill: "",
              variant: "light-secondary",
              size: "sm",
              icon: "me-icon icon-sync",
              "data-jest": "btn-refresh",
              "data-cy": "refresh"
            },
            on: { click: _vm.handleRefresh }
          })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "MeDropdown",
        {
          attrs: {
            variant: "light-secondary",
            size: "sm",
            "menu-right": "",
            "no-caret": "",
            pill: "",
            "data-cy": "pag-dropdown"
          }
        },
        [
          _c(
            "template",
            { slot: "button-content" },
            [
              _c("Icon", {
                attrs: {
                  icon: "me-icon icon-ellipsis-v",
                  "data-jest": "btn-more"
                }
              })
            ],
            1
          ),
          _vm._v(" "),
          _vm.customMenu.length > 0
            ? [
                _c(
                  "BDropdownGroup",
                  _vm._l(_vm.customMenu, function(ref, index) {
                    var click = ref.click;
                    var icon = ref.icon;
                    var label = ref.label;
                    return _c("MeDropdownItem", {
                      key: index,
                      attrs: {
                        label: label,
                        icon: icon,
                        "data-jest": "custom-menu-item"
                      },
                      on: {
                        click: function($event) {
                          click && click();
                        }
                      }
                    })
                  }),
                  1
                ),
                _vm._v(" "),
                _c("BDropdownDivider")
              ]
            : _vm._e(),
          _vm._v(" "),
          _vm.stateKey || _vm.showColumnsCustomization
            ? _c(
                "BDropdownGroup",
                { attrs: { header: _vm.$toolkit.t("grid.table") } },
                [
                  _c("MeDropdownItem", {
                    attrs: { label: _vm.$toolkit.t("grid.columns.customize") },
                    on: {
                      click: function($event) {
                        return _vm.$emit("openColumnDialog")
                      }
                    }
                  }),
                  _vm._v(" "),
                  _vm.stateKey
                    ? _c("MeDropdownItem", {
                        attrs: { label: _vm.$toolkit.t("grid.reset") },
                        on: {
                          click: function($event) {
                            return _vm.$emit("resetState")
                          }
                        }
                      })
                    : _vm._e()
                ],
                1
              )
            : _vm._e(),
          _vm._v(" "),
          !_vm.hideDensityOptions
            ? _c(
                "BDropdownGroup",
                { attrs: { header: _vm.$toolkit.t("grid.density.header") } },
                _vm._l(_vm.densityOptions, function(densityOption) {
                  return _c("MeDropdownItem", {
                    key: densityOption.value,
                    on: {
                      click: function() {
                        return _vm.setDensity(densityOption.value)
                      }
                    },
                    scopedSlots: _vm._u(
                      [
                        {
                          key: "dropdown-item",
                          fn: function() {
                            return [
                              _c("MeRadioInput", {
                                staticClass: "pag-radio",
                                attrs: {
                                  options: [densityOption],
                                  name: _vm.densityRadioName,
                                  stacked: ""
                                },
                                model: {
                                  value: _vm.currentDensity,
                                  callback: function($$v) {
                                    _vm.currentDensity = $$v;
                                  },
                                  expression: "currentDensity"
                                }
                              })
                            ]
                          },
                          proxy: true
                        }
                      ],
                      null,
                      true
                    )
                  })
                }),
                1
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.stateKey ||
          _vm.showColumnsCustomization ||
          !_vm.hideDensityOptions
            ? _c("BDropdownDivider")
            : _vm._e(),
          _vm._v(" "),
          _c(
            "BDropdownGroup",
            { attrs: { header: _vm.$toolkit.t("grid.paging.header") } },
            [
              _c("MeDropdownItem", {
                attrs: {
                  label: _vm.$toolkit.t("grid.paging.first"),
                  "data-jest": "btn-first-page",
                  "data-cy": "first-page"
                },
                on: { click: _vm.firstPage }
              }),
              _vm._v(" "),
              _c("MeDropdownItem", {
                directives: [
                  {
                    name: "me-tooltip",
                    rawName: "v-me-tooltip",
                    value: _vm.lastPageMessage,
                    expression: "lastPageMessage"
                  }
                ],
                attrs: {
                  disabled: _vm.disableLastPageButton,
                  label: _vm.$toolkit.t("grid.paging.last"),
                  "data-jest": "btn-last-page"
                },
                on: { click: _vm.lastPage }
              })
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "BDropdownGroup",
            {
              attrs: {
                header: _vm.$toolkit.t("grid.paging.sizes"),
                "data-jest": "page-size"
              }
            },
            _vm._l(_vm.elementsPerPageRadio, function(elementPerPage) {
              return _c("MeDropdownItem", {
                key: elementPerPage.value,
                on: {
                  click: function($event) {
                    return _vm.handleSetSize(elementPerPage.value)
                  }
                },
                scopedSlots: _vm._u(
                  [
                    {
                      key: "dropdown-item",
                      fn: function() {
                        return [
                          _c("MeRadioInput", {
                            staticClass: "pag-radio",
                            attrs: {
                              options: [elementPerPage],
                              name: _vm.elementsPerPageRadioName,
                              stacked: "",
                              "data-cy": "radio-paging"
                            },
                            model: {
                              value: _vm.pageSizeOption,
                              callback: function($$v) {
                                _vm.pageSizeOption = $$v;
                              },
                              expression: "pageSizeOption"
                            }
                          })
                        ]
                      },
                      proxy: true
                    }
                  ],
                  null,
                  true
                )
              })
            }),
            1
          )
        ],
        2
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$1R = [];
__vue_render__$1R._withStripped = true;

  /* style */
  const __vue_inject_styles__$1R = function (inject) {
    if (!inject) return
    inject("data-v-09a8b1f8_0", { source: ".me-pagination[data-v-09a8b1f8] {\n  white-space: nowrap;\n}\n.me-pagination .dropdown-divider[data-v-09a8b1f8] {\n  margin: 0;\n}\n.me-pagination .pag-radio[data-v-09a8b1f8] {\n  margin-bottom: 0;\n}\n.me-pagination .pag-radio[data-v-09a8b1f8]  .radio-option.custom-control {\n  padding-top: 8px;\n  padding-bottom: 8px !important;\n  padding-right: 16px;\n}\n.me-pagination .pag-radio[data-v-09a8b1f8]  .radio-option.custom-control .custom-control-label {\n  left: 16px;\n}\n.me-pagination .pag-radio[data-v-09a8b1f8]  .radio-option.custom-control:hover {\n  background-color: var(--me-primary-7);\n}\n.me-pagination .pag-radio[data-v-09a8b1f8]  .radio-option.custom-control:hover .radio-text {\n  color: var(--me-primary-2) !important;\n}\n.me-pagination > div.search[data-v-09a8b1f8] {\n  display: inline-block;\n}\n.me-pagination > div.search > input[data-v-09a8b1f8] {\n  display: inline;\n  width: 200px;\n  height: 28px;\n  position: relative;\n  top: 1px;\n}\n.me-pagination > button[data-v-09a8b1f8],\n.me-pagination > div.search > button[data-v-09a8b1f8],\n.me-pagination[data-v-09a8b1f8]  div.dropdown > button {\n  width: 32px;\n}\n.me-pagination > button[data-v-09a8b1f8]:after,\n.me-pagination > div.search > button[data-v-09a8b1f8]:after,\n.me-pagination[data-v-09a8b1f8]  div.dropdown > button:after {\n  content: none;\n}\n.me-pagination[data-v-09a8b1f8]  div.dropdown > button {\n  box-shadow: none;\n  padding: 0;\n  font-size: 20px;\n  border-radius: 50px;\n  border: 0px;\n  background: transparent;\n}\n.me-pagination[data-v-09a8b1f8]  div.dropdown > button:hover {\n  background-color: var(--me-bg-gray);\n  border-color: var(--me-bg-gray);\n}\n.me-pagination[data-v-09a8b1f8]  div.dropdown.show > button {\n  background: var(--me-gray-5);\n}\n.me-pagination[data-v-09a8b1f8]  div.dropdown.show li:first-child > .dropdown-header {\n  border-top: none !important;\n}\n.me-pagination > .refresh[data-v-09a8b1f8] {\n  margin: 0 14px;\n}\n.me-pagination > span[data-v-09a8b1f8] {\n  margin: 0 6px;\n  font-size: 14px;\n  color: var(--me-gray-2);\n  line-height: 30px;\n}\n\n/*# sourceMappingURL=Pagination.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Pagination.vue","Pagination.vue"],"names":[],"mappings":"AAsZA;EACA,mBAAA;ACrZA;ADuZA;EACA,SAAA;ACrZA;ADwZA;EACA,gBAAA;ACtZA;ADwZA;EACA,gBAAA;EACA,8BAAA;EACA,mBAAA;ACtZA;ADwZA;EACA,UAAA;ACtZA;ADyZA;EACA,qCAAA;ACvZA;ADyZA;EACA,qCAAA;ACvZA;AD6ZA;EACA,qBAAA;AC3ZA;AD6ZA;EACA,eAAA;EACA,YAAA;EACA,YAAA;EACA,kBAAA;EACA,QAAA;AC3ZA;AD+ZA;;;EAGA,WAAA;AC7ZA;AD8ZA;;;EACA,aAAA;AC1ZA;AD+ZA;EACA,gBAAA;EACA,UAAA;EACA,eAAA;EACA,mBAAA;EACA,WAAA;EACA,uBAAA;AC7ZA;AD+ZA;EACA,mCAAA;EACA,+BAAA;AC7ZA;ADkaA;EACA,4BAAA;AChaA;ADmaA;EACA,2BAAA;ACjaA;ADsaA;EACA,cAAA;ACpaA;ADuaA;EACA,aAAA;EACA,eAAA;EACA,uBAAA;EACA,iBAAA;ACraA;;AAEA,yCAAyC","file":"Pagination.vue","sourcesContent":["<template>\n  <nav class=\"me-pagination\">\n    <div v-if=\"searchable\" class=\"search\">\n      <input\n        v-if=\"isSearching\"\n        v-model=\"currentFilter\"\n        :placeholder=\"$toolkit.t('grid.search')\"\n        class=\"form-control form-control-sm rounded-pill\"\n        type=\"text\"\n      >\n\n      <Button\n        v-if=\"isSearching\"\n        @click=\"handleClose\"\n        pill\n        variant=\"light-secondary\"\n        icon=\"me-icon icon-times\"\n        size=\"sm\"\n      />\n\n      <Button\n        v-else\n        @click=\"handleOpen\"\n        pill\n        variant=\"light-secondary\"\n        icon=\"me-icon icon-search\"\n        size=\"sm\"\n      />\n    </div>\n\n    <Button\n      :disabled=\"!hasPreviousPage\"\n      @click=\"previousPage\"\n      pill\n      variant=\"light-secondary\"\n      size=\"sm\"\n      icon=\"me-icon icon-chevron-left\"\n      data-jest=\"btn-previous-page\"\n      data-cy=\"previous-page\"\n    />\n\n    <span>{{ pageInfo }} {{ $toolkit.t('grid.of') }} <span v-if=\"hits >= maxHits\">{{ maxHits | me-number(0) }}...</span><span v-else>{{ hits | me-number(0) }}</span></span>\n\n    <Button\n      v-if=\"!hasNextPage || hasReachedResultsLimit\"\n      v-me-tooltip=\"$toolkit.t('grid.paging.lastPageMessage')\"\n      pill\n      variant=\"light-secondary\"\n      size=\"sm\"\n      class=\"button__disabled\"\n      icon=\"me-icon icon-chevron-right\"\n    />\n\n    <Button\n      v-else\n      @click=\"nextPage\"\n      pill\n      variant=\"light-secondary\"\n      size=\"sm\"\n      icon=\"me-icon icon-chevron-right\"\n      data-jest=\"btn-next-page\"\n      data-cy=\"next-page\"\n    />\n\n    <Button\n      v-if=\"lazy\"\n      @click=\"handleRefresh\"\n      pill\n      variant=\"light-secondary\"\n      size=\"sm\"\n      class=\"refresh\"\n      icon=\"me-icon icon-sync\"\n      data-jest=\"btn-refresh\"\n      data-cy=\"refresh\"\n    />\n\n    <MeDropdown\n      variant=\"light-secondary\"\n      size=\"sm\"\n      menu-right\n      no-caret\n      pill\n      data-cy=\"pag-dropdown\"\n    >\n      <template slot=\"button-content\">\n        <Icon icon=\"me-icon icon-ellipsis-v\" data-jest=\"btn-more\" />\n      </template>\n\n      <template v-if=\"customMenu.length > 0\">\n        <BDropdownGroup>\n          <MeDropdownItem\n            v-for=\"({ click, icon, label }, index) in customMenu\"\n            :key=\"index\"\n            :label=\"label\"\n            :icon=\"icon\"\n            @click=\"click && click()\"\n            data-jest=\"custom-menu-item\"\n          />\n        </BDropdownGroup>\n\n        <BDropdownDivider />\n      </template>\n\n      <BDropdownGroup\n        v-if=\"stateKey || showColumnsCustomization\"\n        :header=\"$toolkit.t('grid.table')\"\n      >\n        <MeDropdownItem\n          @click=\"$emit('openColumnDialog')\"\n          :label=\"$toolkit.t('grid.columns.customize')\"\n        />\n\n        <MeDropdownItem\n          v-if=\"stateKey\"\n          @click=\"$emit('resetState')\"\n          :label=\"$toolkit.t('grid.reset')\"\n        />\n      </BDropdownGroup>\n\n      <BDropdownGroup\n        v-if=\"!hideDensityOptions\"\n        :header=\"$toolkit.t('grid.density.header')\"\n      >\n        <MeDropdownItem\n          v-for=\"densityOption in densityOptions\"\n          :key=\"densityOption.value\"\n          @click=\"() => setDensity(densityOption.value)\"\n        >\n          <template v-slot:dropdown-item>\n            <MeRadioInput\n              v-model=\"currentDensity\"\n              :options=\"[densityOption]\"\n              :name=\"densityRadioName\"\n              stacked\n              class=\"pag-radio\"\n            />\n          </template>\n        </MeDropdownItem>\n      </BDropdownGroup>\n\n      <BDropdownDivider v-if=\"stateKey || showColumnsCustomization || !hideDensityOptions\" />\n\n      <BDropdownGroup :header=\"$toolkit.t('grid.paging.header')\">\n        <MeDropdownItem\n          :label=\"$toolkit.t('grid.paging.first')\"\n          @click=\"firstPage\"\n          data-jest=\"btn-first-page\"\n          data-cy=\"first-page\"\n        />\n\n        <MeDropdownItem\n          v-me-tooltip=\"lastPageMessage\"\n          :disabled=\"disableLastPageButton\"\n          :label=\"$toolkit.t('grid.paging.last')\"\n          @click=\"lastPage\"\n          data-jest=\"btn-last-page\"\n        />\n      </BDropdownGroup>\n\n      <BDropdownGroup\n        :header=\"$toolkit.t('grid.paging.sizes')\"\n        data-jest=\"page-size\"\n      >\n        <MeDropdownItem\n          v-for=\"elementPerPage in elementsPerPageRadio\"\n          :key=\"elementPerPage.value\"\n          @click=\"handleSetSize(elementPerPage.value)\"\n        >\n          <template v-slot:dropdown-item>\n            <MeRadioInput\n              v-model=\"pageSizeOption\"\n              :options=\"[elementPerPage]\"\n              :name=\"elementsPerPageRadioName\"\n              stacked\n              class=\"pag-radio\"\n              data-cy=\"radio-paging\"\n            />\n          </template>\n        </MeDropdownItem>\n      </BDropdownGroup>\n    </MeDropdown>\n  </nav>\n</template>\n\n<script>\nimport { v4 as uuidv4 } from 'uuid'\nimport { BDropdownGroup, BDropdownDivider } from 'bootstrap-vue'\nimport Button from './Button.vue'\nimport Icon from './Icon.vue'\nimport MeDropdown from './dropdown/DropDown.vue'\nimport MeDropdownItem from './dropdown/DropdownItem.vue'\nimport MeRadioInput from './RadioInput.vue'\n\nexport default {\n  name: 'MePagination',\n  components: {\n    BDropdownGroup,\n    BDropdownDivider,\n    Button,\n    Icon,\n    MeDropdown,\n    MeDropdownItem,\n    MeRadioInput\n  },\n  props: {\n    searchable: {\n      type: Boolean,\n      default: false\n    },\n    filter: {\n      type: String,\n      default: ''\n    },\n    pageSize: {\n      type: Number,\n      default: 0\n    },\n    hits: {\n      type: Number,\n      default: 0\n    },\n    maxHits: {\n      type: Number,\n      default: 1000000\n    },\n    lazy: {\n      type: Boolean,\n      default: false\n    },\n    refresh: {\n      type: Function,\n      default: () => null\n    },\n    elementsPerPage: {\n      type: Array,\n      required: false,\n      default() {\n        return [10, 20, 50, 100]\n      }\n    },\n    stateKey: {\n      type: String,\n      default: ''\n    },\n    showColumnsCustomization: {\n      type: Boolean,\n      default: false\n    },\n    hideDensityOptions: {\n      type: Boolean,\n      default: false\n    },\n    customMenu: {\n      type: Array,\n      default: () => []\n    },\n    currentPage: {\n      type: Number,\n      default: 1\n    },\n    density: {\n      type: String,\n      default: 'md'\n    }\n  },\n\n  data() {\n    return {\n      currentFilter: this.filter,\n      currentDensity: this.density,\n      densityOptions: [\n        { name: this.$toolkit.t('grid.density.compact'), value: 'sm' },\n        { name: this.$toolkit.t('grid.density.default'), value: 'md' },\n        { name: this.$toolkit.t('grid.density.spacious'), value: 'lg' }\n      ],\n      isSearching: false,\n      currentPaginationPage: this.currentPage,\n      maxHitsLimit: 10000,\n      pageSizeOption: `${this.pageSize}`\n    }\n  },\n\n  computed: {\n    densityRadioName() {\n      return `${this.stateKey || uuidv4()}_density`\n    },\n\n    elementsPerPageRadioName() {\n      return `${this.stateKey || uuidv4()}_pageSize`\n    },\n\n    pageInfo() {\n      let first = 0\n      let last = 0\n      if (this.hits > 0) {\n        first = (this.currentPage - 1) * this.pageSize + 1\n        last = first + this.pageSize - 1\n        if (last > this.hits) last = this.hits\n      }\n      return first + ' - ' + last\n    },\n\n    elementsPerPageRadio() {\n      const perPageOptions = this.elementsPerPage.map(size => (\n        { name: this.$toolkit.t('grid.paging.perPage', size), value: `${size}` }\n      ))\n\n      return perPageOptions\n    },\n\n    disableLastPageButton() {\n      return this.hits > this.maxHitsLimit\n    },\n\n    hasReachedResultsLimit() {\n      const first = (this.currentPage - 1) * this.pageSize + 1\n      const last = first + this.pageSize - 1\n      return last >= this.maxHitsLimit && this.disableLastPageButton\n    },\n\n    lastPageMessage() {\n      return this.disableLastPageButton ? this.$toolkit.t('grid.paging.lastPageMessage') : ''\n    },\n\n    hasNextPage() {\n      return this.currentPaginationPage < this.totalPages()\n    },\n\n    hasPreviousPage() {\n      return this.currentPaginationPage > 1\n    }\n  },\n\n  watch: {\n    currentFilter(value) {\n      this.$emit('update:filter', value)\n    },\n\n    currentPage(value) {\n      this.currentPaginationPage = value\n    },\n\n    pageSizeOption(value) {\n      this.pageSizeOption = `${value}`\n    },\n\n    density(value) {\n      this.currentDensity = value\n    }\n  },\n\n  methods: {\n    setDensity(densityOptionValue) {\n      this.currentDensity = densityOptionValue\n      this.$emit('density', densityOptionValue)\n    },\n\n    handleRefresh() {\n      this.firstPage()\n      this.refresh()\n    },\n\n    totalPages() {\n      return Math.ceil(this.hits / this.pageSize)\n    },\n\n    previousPage() {\n      this.currentPaginationPage--\n      this.$emit('update:currentPage', this.currentPaginationPage)\n    },\n\n    nextPage() {\n      this.currentPaginationPage++\n      this.$emit('update:currentPage', this.currentPaginationPage)\n    },\n\n    firstPage() {\n      this.currentPaginationPage = 1\n      this.$emit('update:currentPage', this.currentPaginationPage)\n    },\n\n    handleSetSize(size) {\n      this.firstPage()\n      this.pageSizeOption = size\n      this.$emit('setSize', parseInt(size))\n    },\n\n    lastPage() {\n      this.currentPaginationPage = this.totalPages()\n      this.$emit('update:currentPage', this.currentPaginationPage)\n    },\n\n    handleClose() {\n      this.isSearching = false\n      this.$emit('update:filter', null)\n    },\n\n    handleOpen() {\n      this.isSearching = true\n      this.$emit('open')\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-pagination {\n  white-space: nowrap;\n\n  .dropdown-divider {\n    margin: 0;\n  }\n\n  .pag-radio {\n    margin-bottom: 0;\n\n    ::v-deep .radio-option.custom-control {\n      padding-top: 8px;\n      padding-bottom: 8px !important;\n      padding-right: 16px;\n\n      .custom-control-label {\n        left: 16px;\n      }\n\n      &:hover {\n        background-color: var(--me-primary-7);\n\n        .radio-text {\n          color: var(--me-primary-2) !important;\n        }\n      }\n    }\n  }\n\n  > div.search {\n    display: inline-block;\n\n    > input {\n      display: inline;\n      width: 200px;\n      height: 28px;\n      position: relative;\n      top: 1px;\n    }\n  }\n\n  > button,\n  > div.search > button,\n  ::v-deep div.dropdown > button {\n    width: 32px;\n    &:after {\n      content: none;\n    }\n  }\n\n  ::v-deep div.dropdown {\n    > button {\n      box-shadow: none;\n      padding: 0;\n      font-size: 20px;\n      border-radius: 50px;\n      border: 0px;\n      background: transparent;\n\n      &:hover {\n        background-color: var(--me-bg-gray);\n        border-color: var(--me-bg-gray);\n      }\n    }\n\n    &.show {\n      > button {\n        background: var(--me-gray-5);\n      }\n\n      li:first-child > .dropdown-header {\n        border-top: none !important;\n      }\n    }\n  }\n\n  > .refresh  {\n    margin: 0 14px;\n  }\n\n  > span {\n    margin: 0 6px;\n    font-size: 14px;\n    color:  var(--me-gray-2);\n    line-height: 30px;\n  }\n}\n\n</style>\n",".me-pagination {\n  white-space: nowrap;\n}\n.me-pagination .dropdown-divider {\n  margin: 0;\n}\n.me-pagination .pag-radio {\n  margin-bottom: 0;\n}\n.me-pagination .pag-radio ::v-deep .radio-option.custom-control {\n  padding-top: 8px;\n  padding-bottom: 8px !important;\n  padding-right: 16px;\n}\n.me-pagination .pag-radio ::v-deep .radio-option.custom-control .custom-control-label {\n  left: 16px;\n}\n.me-pagination .pag-radio ::v-deep .radio-option.custom-control:hover {\n  background-color: var(--me-primary-7);\n}\n.me-pagination .pag-radio ::v-deep .radio-option.custom-control:hover .radio-text {\n  color: var(--me-primary-2) !important;\n}\n.me-pagination > div.search {\n  display: inline-block;\n}\n.me-pagination > div.search > input {\n  display: inline;\n  width: 200px;\n  height: 28px;\n  position: relative;\n  top: 1px;\n}\n.me-pagination > button,\n.me-pagination > div.search > button,\n.me-pagination ::v-deep div.dropdown > button {\n  width: 32px;\n}\n.me-pagination > button:after,\n.me-pagination > div.search > button:after,\n.me-pagination ::v-deep div.dropdown > button:after {\n  content: none;\n}\n.me-pagination ::v-deep div.dropdown > button {\n  box-shadow: none;\n  padding: 0;\n  font-size: 20px;\n  border-radius: 50px;\n  border: 0px;\n  background: transparent;\n}\n.me-pagination ::v-deep div.dropdown > button:hover {\n  background-color: var(--me-bg-gray);\n  border-color: var(--me-bg-gray);\n}\n.me-pagination ::v-deep div.dropdown.show > button {\n  background: var(--me-gray-5);\n}\n.me-pagination ::v-deep div.dropdown.show li:first-child > .dropdown-header {\n  border-top: none !important;\n}\n.me-pagination > .refresh {\n  margin: 0 14px;\n}\n.me-pagination > span {\n  margin: 0 6px;\n  font-size: 14px;\n  color: var(--me-gray-2);\n  line-height: 30px;\n}\n\n/*# sourceMappingURL=Pagination.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1R = "data-v-09a8b1f8";
  /* module identifier */
  const __vue_module_identifier__$1R = undefined;
  /* functional template */
  const __vue_is_functional_template__$1R = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1R, staticRenderFns: __vue_staticRenderFns__$1R },
    __vue_inject_styles__$1R,
    __vue_script__$1R,
    __vue_scope_id__$1R,
    __vue_is_functional_template__$1R,
    __vue_module_identifier__$1R,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1S = {
  name: 'MePreview',
  components: {
    Spinner: __vue_component__$5,
    Icon: __vue_component__
  },
  props: {
    urlPreview: {
      type: String,
      default: ''
    },
    previewEnabled: {
      type: Boolean,
      value: true
    },
    contentMaxWidth: {
      type: [String, Number],
      default: '100%'
    }
  },
  data() {
    return {
      isLoading: false
    }
  },
  computed: {
    previewMaxWidth() {
      return `calc(100% - ${this.contentMaxWidth}px)`
    },
    contentWidth() {
      if (!this.previewEnabled) return '100%'
      return (typeof this.contentMaxWidth === 'number') ? `${this.contentMaxWidth}px` : this.contentMaxWidth
    }
  },
  watch: {
    urlPreview() {
      this.isLoading = true;
    }
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleIframeMessage);
  },

  mounted() {
    window.addEventListener('message', this.handleIframeMessage);
  },

  methods: {
    handleIframeMessage(event) {
      this.$emit('iframeEvent', event);
    }
  }
};

/* script */
const __vue_script__$1S = script$1S;

/* template */
var __vue_render__$1S = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-preview" },
    [
      _c(
        "div",
        {
          staticClass: "content",
          style: {
            maxWidth: _vm.contentWidth,
            overflow: _vm.previewEnabled ? "auto" : "visible"
          }
        },
        [_vm._t("default")],
        2
      ),
      _vm._v(" "),
      _c("transition", { attrs: { name: "me-preview-transition" } }, [
        _c(
          "div",
          {
            directives: [
              {
                name: "show",
                rawName: "v-show",
                value: _vm.previewEnabled,
                expression: "previewEnabled"
              }
            ],
            staticClass: "preview",
            style: { "max-width": _vm.previewMaxWidth }
          },
          [
            _vm.urlPreview
              ? [
                  _vm.isLoading
                    ? _vm._t("loading", [
                        _c(
                          "div",
                          { staticClass: "preview-feedback" },
                          [_c("Spinner", { attrs: { variant: "primary" } })],
                          1
                        )
                      ])
                    : _vm._e(),
                  _vm._v(" "),
                  _c("iframe", {
                    directives: [
                      {
                        name: "show",
                        rawName: "v-show",
                        value: !_vm.isLoading,
                        expression: "!isLoading"
                      }
                    ],
                    attrs: { src: _vm.urlPreview },
                    on: {
                      load: function($event) {
                        _vm.isLoading = false;
                      }
                    }
                  })
                ]
              : [
                  _vm._t("empty", [
                    _c("div", { staticClass: "preview-feedback" }, [
                      _c(
                        "p",
                        { staticClass: "no-content" },
                        [
                          _c("Icon", {
                            attrs: { icon: "me-icon-s icon-mail-bulk" }
                          }),
                          _vm._v(
                            "\n              " +
                              _vm._s(_vm.$toolkit.t("preview.noContent")) +
                              "\n            "
                          )
                        ],
                        1
                      )
                    ])
                  ])
                ]
          ],
          2
        )
      ])
    ],
    1
  )
};
var __vue_staticRenderFns__$1S = [];
__vue_render__$1S._withStripped = true;

  /* style */
  const __vue_inject_styles__$1S = function (inject) {
    if (!inject) return
    inject("data-v-458b492e_0", { source: "div.me-preview[data-v-458b492e] {\n  display: flex;\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  top: 0;\n}\ndiv.me-preview > div.content[data-v-458b492e] {\n  flex-grow: 1;\n  width: 100%;\n  transition: all 0.5s;\n}\ndiv.me-preview > div.preview[data-v-458b492e] {\n  flex-grow: 2;\n  overflow: hidden;\n  position: relative;\n}\ndiv.me-preview > div.preview > iframe[data-v-458b492e] {\n  width: 100%;\n  height: 100%;\n  border: 0;\n}\ndiv.me-preview > div.preview > .preview-feedback[data-v-458b492e] {\n  height: 100%;\n  align-items: center;\n  display: flex;\n  justify-content: center;\n}\ndiv.me-preview > div.preview > .preview-feedback > p.no-content[data-v-458b492e] {\n  color: rgba(0, 0, 0, 0.36);\n  font-size: 18px;\n}\ndiv.me-preview > div.preview > .preview-feedback > p.no-content i[data-v-458b492e] {\n  text-align: center;\n  display: block;\n  font-size: 80px;\n  color: #e4e4e4;\n}\n.me-preview-transition-enter-active[data-v-458b492e] {\n  transition: all 0.5s;\n  transition-delay: 0.2s;\n  flex-basis: 0;\n}\n.me-preview-transition-leave-active[data-v-458b492e] {\n  transition: all 0.5s;\n}\n.me-preview-transition-enter[data-v-458b492e] {\n  opacity: 0;\n}\n.me-preview-transition-leave-to[data-v-458b492e] {\n  display: none;\n}\n\n/*# sourceMappingURL=Preview.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Preview.vue","Preview.vue"],"names":[],"mappings":"AAuGA;EACA,aAAA;EACA,kBAAA;EACA,OAAA;EACA,QAAA;EACA,SAAA;EACA,MAAA;ACtGA;ADwGA;EACA,YAAA;EACA,WAAA;EACA,oBAAA;ACtGA;ADyGA;EACA,YAAA;EACA,gBAAA;EACA,kBAAA;ACvGA;ADyGA;EACA,WAAA;EACA,YAAA;EACA,SAAA;ACvGA;AD0GA;EACA,YAAA;EACA,mBAAA;EACA,aAAA;EACA,uBAAA;ACxGA;AD0GA;EACA,0BAAA;EACA,eAAA;ACxGA;AD0GA;EACA,kBAAA;EACA,cAAA;EACA,eAAA;EACA,cAAA;ACxGA;AD+GA;EACA,oBAAA;EACA,sBAAA;EACA,aAAA;AC5GA;AD+GA;EACA,oBAAA;AC5GA;AD+GA;EACA,UAAA;AC5GA;AD+GA;EACA,aAAA;AC5GA;;AAEA,sCAAsC","file":"Preview.vue","sourcesContent":["<template>\n  <div class=\"me-preview\">\n    <div\n      :style=\"{\n        maxWidth: contentWidth,\n        overflow: previewEnabled ? 'auto' : 'visible'\n      }\"\n      class=\"content\"\n    >\n      <slot />\n    </div>\n    <transition name=\"me-preview-transition\">\n      <div\n        v-show=\"previewEnabled\"\n        :style=\"{ 'max-width': previewMaxWidth }\"\n        class=\"preview\"\n      >\n        <template v-if=\"urlPreview\">\n          <slot v-if=\"isLoading\" name=\"loading\">\n            <div class=\"preview-feedback\">\n              <Spinner variant=\"primary\" />\n            </div>\n          </slot>\n          <iframe v-show=\"!isLoading\" :src=\"urlPreview\" @load=\"isLoading = false\" />\n        </template>\n        <template v-else>\n          <slot name=\"empty\">\n            <div class=\"preview-feedback\">\n              <p class=\"no-content\">\n                <Icon icon=\"me-icon-s icon-mail-bulk\" />\n                {{ $toolkit.t('preview.noContent') }}\n              </p>\n            </div>\n          </slot>\n        </template>\n      </div>\n    </transition>\n  </div>\n</template>\n\n<script>\nimport Spinner from './Spinner.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MePreview',\n  components: {\n    Spinner,\n    Icon\n  },\n  props: {\n    urlPreview: {\n      type: String,\n      default: ''\n    },\n    previewEnabled: {\n      type: Boolean,\n      value: true\n    },\n    contentMaxWidth: {\n      type: [String, Number],\n      default: '100%'\n    }\n  },\n  data() {\n    return {\n      isLoading: false\n    }\n  },\n  computed: {\n    previewMaxWidth() {\n      return `calc(100% - ${this.contentMaxWidth}px)`\n    },\n    contentWidth() {\n      if (!this.previewEnabled) return '100%'\n      return (typeof this.contentMaxWidth === 'number') ? `${this.contentMaxWidth}px` : this.contentMaxWidth\n    }\n  },\n  watch: {\n    urlPreview() {\n      this.isLoading = true\n    }\n  },\n\n  beforeDestroy() {\n    window.removeEventListener('message', this.handleIframeMessage)\n  },\n\n  mounted() {\n    window.addEventListener('message', this.handleIframeMessage)\n  },\n\n  methods: {\n    handleIframeMessage(event) {\n      this.$emit('iframeEvent', event)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $transitionTime: .5s;\n\n  div.me-preview {\n    display: flex;\n    position: absolute;\n    left: 0;\n    right: 0;\n    bottom: 0;\n    top: 0;\n\n    > div.content {\n      flex-grow: 1;\n      width: 100%;\n      transition: all $transitionTime;\n    }\n\n    > div.preview {\n      flex-grow: 2;\n      overflow: hidden;\n      position: relative;\n\n      > iframe {\n        width: 100%;\n        height: 100%;\n        border: 0;\n      }\n\n      > .preview-feedback {\n        height: 100%;\n        align-items: center;\n        display: flex;\n        justify-content: center;\n\n        > p.no-content {\n          color: rgba(0, 0, 0, 0.36);\n          font-size: 18px;\n\n          i {\n            text-align: center;\n            display: block;\n            font-size: 80px;\n            color: #e4e4e4;\n          }\n        }\n      }\n    }\n  }\n\n  .me-preview-transition-enter-active {\n    transition: all $transitionTime;\n    transition-delay: .2s;\n    flex-basis: 0;\n  }\n\n  .me-preview-transition-leave-active {\n    transition: all $transitionTime;\n  }\n\n  .me-preview-transition-enter {\n    opacity: 0;\n  }\n\n  .me-preview-transition-leave-to {\n    display: none;\n  }\n</style>\n","div.me-preview {\n  display: flex;\n  position: absolute;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  top: 0;\n}\ndiv.me-preview > div.content {\n  flex-grow: 1;\n  width: 100%;\n  transition: all 0.5s;\n}\ndiv.me-preview > div.preview {\n  flex-grow: 2;\n  overflow: hidden;\n  position: relative;\n}\ndiv.me-preview > div.preview > iframe {\n  width: 100%;\n  height: 100%;\n  border: 0;\n}\ndiv.me-preview > div.preview > .preview-feedback {\n  height: 100%;\n  align-items: center;\n  display: flex;\n  justify-content: center;\n}\ndiv.me-preview > div.preview > .preview-feedback > p.no-content {\n  color: rgba(0, 0, 0, 0.36);\n  font-size: 18px;\n}\ndiv.me-preview > div.preview > .preview-feedback > p.no-content i {\n  text-align: center;\n  display: block;\n  font-size: 80px;\n  color: #e4e4e4;\n}\n\n.me-preview-transition-enter-active {\n  transition: all 0.5s;\n  transition-delay: 0.2s;\n  flex-basis: 0;\n}\n\n.me-preview-transition-leave-active {\n  transition: all 0.5s;\n}\n\n.me-preview-transition-enter {\n  opacity: 0;\n}\n\n.me-preview-transition-leave-to {\n  display: none;\n}\n\n/*# sourceMappingURL=Preview.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1S = "data-v-458b492e";
  /* module identifier */
  const __vue_module_identifier__$1S = undefined;
  /* functional template */
  const __vue_is_functional_template__$1S = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1S, staticRenderFns: __vue_staticRenderFns__$1S },
    __vue_inject_styles__$1S,
    __vue_script__$1S,
    __vue_scope_id__$1S,
    __vue_is_functional_template__$1S,
    __vue_module_identifier__$1S,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1T = {
  name: 'MeQuickSearch',
  components: {
    Button: __vue_component__$6,
    Icon: __vue_component__,
    Spinner: __vue_component__$5
  },
  props: {
    term: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    provider: {
      type: Function,
      required: true
    },
    size: {
      type: String,
      default: 'md'
    }
  },
  data() {
    return {
      currentTerm: this.term,
      opened: false,
      filter: false,
      loadingProvider: false,
      results: []
    }
  },
  computed: {
    shouldOpen() {
      return this.opened && this.hasTerm
    },
    hasTerm() {
      return this.currentTerm
    },
    actionsIndex() {
      return this.opened ? 1041 : 4
    }
  },
  watch: {
    term(val) {
      this.currentTerm = val;
    }
  },
  methods: {
    select(item) {
      this.close();
      this.results = this.$emit('select', item);
      this.clearTerm();
    },
    open() {
      this.opened = true;
    },
    close() {
      this.opened = false;
      this.filter = false;
    },
    showResults() {
      this.open();
      this.filter = true;
      this.loadingProvider = true;
      this.results = [];
      this.callProvider();
    },
    callProvider: debounce_1(function () {
      this.provider(this.currentTerm, ({ results }) => {
        this.results = results;
        this.loadingProvider = false;
      });
    }, 500),
    clearTerm() {
      this.currentTerm = '';
      this.close();
    }
  }
};

/* script */
const __vue_script__$1T = script$1T;

/* template */
var __vue_render__$1T = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-quick-search", class: { opened: _vm.opened } },
    [
      _c("div", { staticClass: "search" }, [
        _c(
          "div",
          { staticClass: "term" },
          [
            _c(
              "div",
              {
                staticClass: "term-search",
                class: _vm.size,
                style: { zIndex: _vm.actionsIndex },
                on: {
                  click: function($event) {
                    $event.stopPropagation();
                    return _vm.showResults($event)
                  }
                }
              },
              [
                _c("Icon", {
                  attrs: {
                    icon: "me-icon-l icon-search",
                    size: "13px",
                    color: "var(--me-gray-3)"
                  }
                })
              ],
              1
            ),
            _vm._v(" "),
            _c("MeTextInput", {
              attrs: { placeholder: _vm.placeholder, size: _vm.size },
              on: { input: _vm.showResults, enter: _vm.showResults },
              model: {
                value: _vm.currentTerm,
                callback: function($$v) {
                  _vm.currentTerm = $$v;
                },
                expression: "currentTerm"
              }
            })
          ],
          1
        ),
        _vm._v(" "),
        _c(
          "div",
          {
            staticClass: "actions",
            class: _vm.size,
            style: { zIndex: _vm.actionsIndex }
          },
          [
            _vm.hasTerm
              ? _c(
                  "Button",
                  {
                    staticClass: "btn-clear-input",
                    attrs: { variant: "link", size: "sm" },
                    on: { click: _vm.clearTerm }
                  },
                  [
                    _c("Icon", {
                      attrs: {
                        icon: "me-icon-l icon-times",
                        size: "13px",
                        color: "var(--me-gray-3)"
                      }
                    })
                  ],
                  1
                )
              : _vm._e()
          ],
          1
        )
      ]),
      _vm._v(" "),
      _vm.shouldOpen
        ? _c("div", { staticClass: "content" }, [
            _c("div", { staticClass: "results" }, [
              _vm.loadingProvider
                ? _c(
                    "span",
                    [
                      _c("Spinner", {
                        staticClass: "align-middle",
                        attrs: { small: "" }
                      }),
                      _vm._v(
                        "\n        " +
                          _vm._s(_vm.$toolkit.t("spinner.loading")) +
                          "\n      "
                      )
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              !_vm.loadingProvider && !_vm.results.length
                ? _c("span", [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.$toolkit.t("grid.result.empty")) +
                        "\n      "
                    )
                  ])
                : _c(
                    "ul",
                    _vm._l(_vm.results, function(item) {
                      return _c(
                        "li",
                        {
                          key: item.id,
                          on: {
                            click: function($event) {
                              return _vm.select(item)
                            }
                          }
                        },
                        [
                          _c("span", {
                            directives: [
                              {
                                name: "me-safe-html",
                                rawName: "v-me-safe-html",
                                value: _vm.$options.filters.highlightText(
                                  item.name,
                                  _vm.currentTerm
                                ),
                                expression:
                                  "$options.filters.highlightText(item.name, currentTerm)"
                              }
                            ]
                          })
                        ]
                      )
                    }),
                    0
                  )
            ])
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("div", { staticClass: "backdrop", on: { click: _vm.close } })
    ]
  )
};
var __vue_staticRenderFns__$1T = [];
__vue_render__$1T._withStripped = true;

  /* style */
  const __vue_inject_styles__$1T = function (inject) {
    if (!inject) return
    inject("data-v-195a29de_0", { source: "div.me-quick-search[data-v-195a29de] {\n  position: relative;\n}\ndiv.me-quick-search[data-v-195a29de]  div.input-icons {\n  display: none;\n}\ndiv.me-quick-search .sm[data-v-195a29de] {\n  height: 32px;\n}\ndiv.me-quick-search .md[data-v-195a29de] {\n  height: 40px;\n}\ndiv.me-quick-search .lg[data-v-195a29de] {\n  height: 48px;\n}\ndiv.me-quick-search > div.search[data-v-195a29de] {\n  align-items: center;\n  background: var(--me-white);\n  display: flex;\n  width: 100%;\n  z-index: 1050;\n}\ndiv.me-quick-search > div.search > div.term[data-v-195a29de] {\n  flex-grow: 2;\n  display: flex;\n  align-items: center;\n}\ndiv.me-quick-search > div.search > div.term > div.term-search[data-v-195a29de] {\n  position: absolute;\n  width: 32px;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\ndiv.me-quick-search > div.search > div.term[data-v-195a29de]  input {\n  padding-left: 40px;\n  padding-right: 40px;\n  background: none;\n  box-shadow: none;\n}\ndiv.me-quick-search > div.search > div.term[data-v-195a29de]  input::placeholder {\n  color: var(--me-gray-3);\n}\ndiv.me-quick-search > div.search > div.term[data-v-195a29de]  input:focus::placeholder {\n  color: transparent;\n}\ndiv.me-quick-search > div.search > div.actions[data-v-195a29de] {\n  white-space: nowrap;\n  position: absolute;\n  right: 0;\n  cursor: pointer;\n}\ndiv.me-quick-search > div.search > div.actions .btn-clear-input[data-v-195a29de] {\n  color: var(--me-gray-2);\n  position: relative;\n  padding: 0;\n  width: 32px;\n  height: 100%;\n}\ndiv.me-quick-search > div.content[data-v-195a29de] {\n  background-color: var(--me-white);\n  border-radius: 0.2rem;\n  box-shadow: var(--me-shadow-level-2);\n  display: none;\n  left: 0;\n  max-height: 200px;\n  overflow-y: auto;\n  padding: 1rem;\n  position: relative;\n  z-index: 1050;\n}\ndiv.me-quick-search > div.content[data-v-195a29de]::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\ndiv.me-quick-search > div.content[data-v-195a29de]::-webkit-scrollbar-track {\n  border-radius: 15px;\n}\ndiv.me-quick-search > div.content[data-v-195a29de]::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\ndiv.me-quick-search > div.content[data-v-195a29de]::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\ndiv.me-quick-search > div.content > div.results > ul[data-v-195a29de] {\n  font-size: 13px;\n  list-style: none;\n  margin-bottom: 0;\n  padding: 0;\n}\ndiv.me-quick-search > div.content > div.results > ul > li[data-v-195a29de] {\n  color: #969696;\n  cursor: pointer;\n  font-size: 13px;\n  line-height: 15px;\n  line-height: 30px;\n  margin-bottom: 10px;\n  padding: 10px;\n  position: relative;\n  width: 100%;\n}\ndiv.me-quick-search > div.content > div.results > ul > li[data-v-195a29de]:not(:last-child) {\n  border-bottom: 1px solid rgba(0, 0, 0, 0.05);\n}\ndiv.me-quick-search > div.content > div.results > ul > li[data-v-195a29de]:hover {\n  background-color: var(--light);\n}\ndiv.me-quick-search > div.content > div.results > ul > li:hover > i.icon-times[data-v-195a29de] {\n  display: block;\n}\ndiv.me-quick-search > div.content > div.results > ul > li span[data-v-195a29de]  b {\n  color: var(--me-gray-0);\n}\ndiv.me-quick-search > div.content > div.results > ul[data-v-195a29de]:not(:last-child) {\n  border-bottom: 1px solid #eee;\n  margin-bottom: 1rem;\n  padding-bottom: 1rem;\n}\ndiv.me-quick-search > div.backdrop[data-v-195a29de] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1040;\n}\ndiv.me-quick-search.opened > div.content[data-v-195a29de] {\n  display: block;\n}\ndiv.me-quick-search.opened > div.backdrop[data-v-195a29de] {\n  display: block;\n  opacity: 0;\n}\n\n/*# sourceMappingURL=QuickSearch.vue.map */", map: {"version":3,"sources":["/app/build/src/components/QuickSearch.vue","QuickSearch.vue"],"names":[],"mappings":"AAkKA;EACA,kBAAA;ACjKA;ADoKA;EACA,aAAA;AClKA;ADsKA;EACA,YAAA;ACpKA;ADuKA;EACA,YAAA;ACrKA;ADwKA;EACA,YAAA;ACtKA;ADyKA;EACA,mBAAA;EACA,2BAAA;EACA,aAAA;EACA,WAAA;EACA,aAAA;ACvKA;ADyKA;EACA,YAAA;EACA,aAAA;EACA,mBAAA;ACvKA;ADyKA;EACA,kBAAA;EACA,WAAA;EACA,eAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;ACvKA;AD0KA;EACA,kBAAA;EACA,mBAAA;EACA,gBAAA;EACA,gBAAA;ACxKA;AD0KA;EACA,uBAAA;ACxKA;AD2KA;EACA,kBAAA;ACzKA;AD8KA;EACA,mBAAA;EACA,kBAAA;EACA,QAAA;EACA,eAAA;AC5KA;AD8KA;EACA,uBAAA;EACA,kBAAA;EACA,UAAA;EACA,WAAA;EACA,YAAA;AC5KA;ADiLA;EACA,iCAAA;EACA,qBAAA;EACA,oCAAA;EACA,aAAA;EACA,OAAA;EACA,iBAAA;EACA,gBAAA;EACA,aAAA;EACA,kBAAA;EACA,aAAA;AC/KA;ADiLA;EACA,UAAA;EACA,WAAA;AC/KA;ADiLA;EACA,mBAAA;AC/KA;ADkLA;EACA,+BAAA;EACA,mBAAA;AChLA;ADkLA;EACA,+BAAA;AChLA;ADsLA;EACA,eAAA;EACA,gBAAA;EACA,gBAAA;EACA,UAAA;ACpLA;ADsLA;EACA,cAAA;EACA,eAAA;EACA,eAAA;EACA,iBAAA;EACA,iBAAA;EACA,mBAAA;EACA,aAAA;EACA,kBAAA;EACA,WAAA;ACpLA;ADsLA;EACA,4CAAA;ACpLA;ADuLA;EACA,8BAAA;ACrLA;ADuLA;EACA,cAAA;ACrLA;ADyLA;EACA,uBAAA;ACvLA;AD2LA;EACA,6BAAA;EACA,mBAAA;EACA,oBAAA;ACzLA;AD+LA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,yBAAA;EACA,mBAAA;EACA,aAAA;AC7LA;ADiMA;EACA,cAAA;AC/LA;ADkMA;EACA,cAAA;EACA,UAAA;AChMA;;AAEA,0CAA0C","file":"QuickSearch.vue","sourcesContent":["<template>\n  <div :class=\"{ opened: opened }\" class=\"me-quick-search\">\n    <div class=\"search\">\n      <div class=\"term\">\n        <div\n          :style=\"{ zIndex: actionsIndex }\"\n          :class=\"size\"\n          @click.stop=\"showResults\"\n          class=\"term-search\"\n        >\n          <Icon\n            icon=\"me-icon-l icon-search\"\n            size=\"13px\"\n            color=\"var(--me-gray-3)\"\n          />\n        </div>\n        <MeTextInput\n          v-model=\"currentTerm\"\n          :placeholder=\"placeholder\"\n          :size=\"size\"\n          @input=\"showResults\"\n          @enter=\"showResults\"\n        />\n      </div>\n      <div\n        :style=\"{ zIndex: actionsIndex }\"\n        :class=\"size\"\n        class=\"actions\"\n      >\n        <Button\n          v-if=\"hasTerm\"\n          @click=\"clearTerm\"\n          variant=\"link\"\n          size=\"sm\"\n          class=\"btn-clear-input\"\n        >\n          <Icon\n            icon=\"me-icon-l icon-times\"\n            size=\"13px\"\n            color=\"var(--me-gray-3)\"\n          />\n        </Button>\n      </div>\n    </div>\n    <div v-if=\"shouldOpen\" class=\"content\">\n      <div class=\"results\">\n        <span v-if=\"loadingProvider\">\n          <Spinner class=\"align-middle\" small />\n          {{ $toolkit.t('spinner.loading') }}\n        </span>\n        <span v-if=\"!loadingProvider && !results.length\">\n          {{ $toolkit.t('grid.result.empty') }}\n        </span>\n\n        <ul v-else>\n          <li\n            v-for=\"item in results\"\n            :key=\"item.id\"\n            @click=\"select(item)\"\n          >\n            <span\n              v-me-safe-html=\"$options.filters.highlightText(item.name, currentTerm)\"\n            />\n          </li>\n        </ul>\n      </div>\n    </div>\n    <div @click=\"close\" class=\"backdrop\" />\n  </div>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport Button from './Button.vue'\nimport Icon from './Icon.vue'\nimport Spinner from './Spinner.vue'\n\nexport default {\n  name: 'MeQuickSearch',\n  components: {\n    Button,\n    Icon,\n    Spinner\n  },\n  props: {\n    term: {\n      type: String,\n      default: null\n    },\n    placeholder: {\n      type: String,\n      default: null\n    },\n    provider: {\n      type: Function,\n      required: true\n    },\n    size: {\n      type: String,\n      default: 'md'\n    }\n  },\n  data() {\n    return {\n      currentTerm: this.term,\n      opened: false,\n      filter: false,\n      loadingProvider: false,\n      results: []\n    }\n  },\n  computed: {\n    shouldOpen() {\n      return this.opened && this.hasTerm\n    },\n    hasTerm() {\n      return this.currentTerm\n    },\n    actionsIndex() {\n      return this.opened ? 1041 : 4\n    }\n  },\n  watch: {\n    term(val) {\n      this.currentTerm = val\n    }\n  },\n  methods: {\n    select(item) {\n      this.close()\n      this.results = this.$emit('select', item)\n      this.clearTerm()\n    },\n    open() {\n      this.opened = true\n    },\n    close() {\n      this.opened = false\n      this.filter = false\n    },\n    showResults() {\n      this.open()\n      this.filter = true\n      this.loadingProvider = true\n      this.results = []\n      this.callProvider()\n    },\n    callProvider: debounce(function () {\n      this.provider(this.currentTerm, ({ results }) => {\n        this.results = results\n        this.loadingProvider = false\n      })\n    }, 500),\n    clearTerm() {\n      this.currentTerm = ''\n      this.close()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-quick-search {\n    position: relative;\n\n    ::v-deep {\n      div.input-icons {\n        display: none;\n      }\n    }\n\n    .sm {\n      height: 32px;\n    }\n\n    .md {\n      height: 40px;\n    }\n\n    .lg {\n      height: 48px;\n    }\n\n    > div.search {\n      align-items: center;\n      background: var(--me-white);\n      display: flex;\n      width: 100%;\n      z-index: 1050;\n\n      > div.term {\n        flex-grow: 2;\n        display: flex;\n        align-items: center;\n\n        > div.term-search {\n          position: absolute;\n          width: 32px;\n          cursor: pointer;\n          display: flex;\n          align-items: center;\n          justify-content: center;\n        }\n\n        ::v-deep input {\n          padding-left: 40px;\n          padding-right: 40px;\n          background: none;\n          box-shadow: none;\n\n          &::placeholder {\n            color: var(--me-gray-3);\n          }\n\n          &:focus::placeholder {\n            color:transparent;\n          }\n        }\n      }\n\n      > div.actions {\n        white-space: nowrap;\n        position: absolute;\n        right: 0;\n        cursor: pointer;\n\n        .btn-clear-input {\n          color: var(--me-gray-2);\n          position: relative;\n          padding: 0;\n          width: 32px;\n          height: 100%;\n        }\n      }\n    }\n\n    > div.content {\n      background-color: var(--me-white);\n      border-radius: 0.2rem;\n      box-shadow: var(--me-shadow-level-2);\n      display: none;\n      left: 0;\n      max-height: 200px;\n      overflow-y: auto;\n      padding: 1rem;\n      position: relative;\n      z-index: 1050;\n\n      &::-webkit-scrollbar {\n        width: 6px;\n        height: 6px;\n\n        &-track {\n          border-radius: 15px;\n        }\n\n        &-thumb {\n          background: rgba(0, 0, 0, 0.25);\n          border-radius: 15px;\n\n          &:hover {\n            background: rgba(0, 0, 0, 0.45);\n          }\n        }\n      }\n\n      > div.results {\n        > ul {\n          font-size: 13px;\n          list-style: none;\n          margin-bottom: 0;\n          padding: 0;\n\n          > li {\n            color: #969696;\n            cursor: pointer;\n            font-size: 13px;\n            line-height: 15px;\n            line-height: 30px;\n            margin-bottom: 10px;\n            padding: 10px;\n            position: relative;\n            width: 100%;\n\n            &:not(:last-child) {\n              border-bottom: 1px solid rgba(0,0,0,.05);\n            }\n\n            &:hover {\n              background-color: var(--light);\n\n              > i.icon-times {\n                display: block;\n              }\n            }\n\n            span ::v-deep b {\n              color: var(--me-gray-0);\n            }\n          }\n\n          &:not(:last-child) {\n            border-bottom: 1px solid #eee;\n            margin-bottom: 1rem;\n            padding-bottom: 1rem;\n          }\n        }\n      }\n    }\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      transition-duration: 0.2s;\n      transition: opacity;\n      z-index: 1040;\n    }\n\n    &.opened {\n      > div.content {\n        display: block\n      }\n\n      > div.backdrop {\n        display: block;\n        opacity: 0;\n      }\n    }\n  }\n</style>\n","div.me-quick-search {\n  position: relative;\n}\ndiv.me-quick-search ::v-deep div.input-icons {\n  display: none;\n}\ndiv.me-quick-search .sm {\n  height: 32px;\n}\ndiv.me-quick-search .md {\n  height: 40px;\n}\ndiv.me-quick-search .lg {\n  height: 48px;\n}\ndiv.me-quick-search > div.search {\n  align-items: center;\n  background: var(--me-white);\n  display: flex;\n  width: 100%;\n  z-index: 1050;\n}\ndiv.me-quick-search > div.search > div.term {\n  flex-grow: 2;\n  display: flex;\n  align-items: center;\n}\ndiv.me-quick-search > div.search > div.term > div.term-search {\n  position: absolute;\n  width: 32px;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\ndiv.me-quick-search > div.search > div.term ::v-deep input {\n  padding-left: 40px;\n  padding-right: 40px;\n  background: none;\n  box-shadow: none;\n}\ndiv.me-quick-search > div.search > div.term ::v-deep input::placeholder {\n  color: var(--me-gray-3);\n}\ndiv.me-quick-search > div.search > div.term ::v-deep input:focus::placeholder {\n  color: transparent;\n}\ndiv.me-quick-search > div.search > div.actions {\n  white-space: nowrap;\n  position: absolute;\n  right: 0;\n  cursor: pointer;\n}\ndiv.me-quick-search > div.search > div.actions .btn-clear-input {\n  color: var(--me-gray-2);\n  position: relative;\n  padding: 0;\n  width: 32px;\n  height: 100%;\n}\ndiv.me-quick-search > div.content {\n  background-color: var(--me-white);\n  border-radius: 0.2rem;\n  box-shadow: var(--me-shadow-level-2);\n  display: none;\n  left: 0;\n  max-height: 200px;\n  overflow-y: auto;\n  padding: 1rem;\n  position: relative;\n  z-index: 1050;\n}\ndiv.me-quick-search > div.content::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\ndiv.me-quick-search > div.content::-webkit-scrollbar-track {\n  border-radius: 15px;\n}\ndiv.me-quick-search > div.content::-webkit-scrollbar-thumb {\n  background: rgba(0, 0, 0, 0.25);\n  border-radius: 15px;\n}\ndiv.me-quick-search > div.content::-webkit-scrollbar-thumb:hover {\n  background: rgba(0, 0, 0, 0.45);\n}\ndiv.me-quick-search > div.content > div.results > ul {\n  font-size: 13px;\n  list-style: none;\n  margin-bottom: 0;\n  padding: 0;\n}\ndiv.me-quick-search > div.content > div.results > ul > li {\n  color: #969696;\n  cursor: pointer;\n  font-size: 13px;\n  line-height: 15px;\n  line-height: 30px;\n  margin-bottom: 10px;\n  padding: 10px;\n  position: relative;\n  width: 100%;\n}\ndiv.me-quick-search > div.content > div.results > ul > li:not(:last-child) {\n  border-bottom: 1px solid rgba(0, 0, 0, 0.05);\n}\ndiv.me-quick-search > div.content > div.results > ul > li:hover {\n  background-color: var(--light);\n}\ndiv.me-quick-search > div.content > div.results > ul > li:hover > i.icon-times {\n  display: block;\n}\ndiv.me-quick-search > div.content > div.results > ul > li span ::v-deep b {\n  color: var(--me-gray-0);\n}\ndiv.me-quick-search > div.content > div.results > ul:not(:last-child) {\n  border-bottom: 1px solid #eee;\n  margin-bottom: 1rem;\n  padding-bottom: 1rem;\n}\ndiv.me-quick-search > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1040;\n}\ndiv.me-quick-search.opened > div.content {\n  display: block;\n}\ndiv.me-quick-search.opened > div.backdrop {\n  display: block;\n  opacity: 0;\n}\n\n/*# sourceMappingURL=QuickSearch.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1T = "data-v-195a29de";
  /* module identifier */
  const __vue_module_identifier__$1T = undefined;
  /* functional template */
  const __vue_is_functional_template__$1T = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1T, staticRenderFns: __vue_staticRenderFns__$1T },
    __vue_inject_styles__$1T,
    __vue_script__$1T,
    __vue_scope_id__$1T,
    __vue_is_functional_template__$1T,
    __vue_module_identifier__$1T,
    false,
    createInjector,
    undefined,
    undefined
  );

/*!
 * vue-range-slider v1.0.3
 * (c) 2016-2020 xwpongithub
 * Released under the MIT License.
 */

// Unsharp text [#166](https://github.com/NightCatSama/vue-slider-component/issues/166)
var roundToDPR = function () {
  var r = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  return function (value) {
    return Math.round(value * r) / r;
  };
}();
var isMobile = function () {
  var userAgentInfo = navigator.userAgent.toLowerCase();
  var agents = ["Android", "iPhone", "SymbianOS", "Windows Phone", "iPad", "iPod"];
  var flag = false;

  for (var v = 0; v < agents.length; v++) {
    if (userAgentInfo.indexOf(agents[v].toLowerCase()) > 0) {
      flag = true;
      break;
    }
  }

  return flag;
}();
function isArray$2(input) {
  if (Array.prototype.isArray) {
    return Array.isArray(input);
  }

  return input instanceof Array || Object.prototype.toString.call(input) === '[object Array]';
}
function isDiff(a, b) {
  if (Object.prototype.toString.call(a) !== Object.prototype.toString.call(b)) {
    return true;
  } else if (isArray$2(a) && a.length === b.length) {
    return a.some(function (v, i) {
      return v !== b[i];
    });
  }

  return a !== b;
}
var elementStyle = document.createElement('div').style;

var vendor = function () {
  var transformNames = {
    webkit: 'webkitTransform',
    Moz: 'MozTransform',
    O: 'OTransform',
    ms: 'msTransform',
    standard: 'transform'
  };

  for (var key in transformNames) {
    if (elementStyle[transformNames[key]] !== undefined) {
      return key;
    }
  }

  return false;
}();

function prefixStyle(style) {
  if (vendor === false) {
    return false;
  }

  if (vendor === 'standard') {
    if (style === 'transitionEnd') {
      return 'transitionend';
    }

    return style;
  }

  return vendor + style.charAt(0).toUpperCase() + style.substr(1);
}
function addEvent(el, type, fn, capture) {
  el.addEventListener(type, fn, {
    passive: false,
    capture: !!capture
  });
}
function removeEvent(el, type, fn, capture) {
  el.removeEventListener(type, fn, {
    passive: false,
    capture: !!capture
  });
}

var transform = prefixStyle('transform');
var transitionDuration = prefixStyle('transitionDuration');
var transitionEnd = prefixStyle('transitionEnd');
var EVENT_TOUCH_START = 'touchstart';
var EVENT_TOUCH_MOVE = 'touchmove';
var EVENT_TOUCH_END = 'touchend';
var EVENT_TOUCH_CANCEL = 'touchcancel';
var EVENT_MOUSE_DOWN = 'mousedown';
var EVENT_MOUSE_MOVE = 'mousemove';
var EVENT_MOUSE_UP = 'mouseup';
var EVENT_MOUSE_LEAVE = 'mouseleave';
var EVENT_KEY_DOWN = 'keydown';
var EVENT_KEY_UP = 'keyup';
var EVENT_RESIZE = 'resize';
var Slider = {
  name: 'vue-range-slider',
  props: {
    // 是否显示组件
    show: {
      type: Boolean,
      default: true
    },
    // 值
    value: {
      type: [String, Number, Array, Object],
      default: 0
    },
    // 最小值
    min: {
      type: Number,
      default: 0
    },
    // 最大值
    max: {
      type: Number,
      default: 100
    },
    // 分段间隔
    step: {
      type: Number,
      default: 1
    },
    // 组件宽度
    width: {
      type: [Number, String],
      default: 'auto'
    },
    // 组件高度
    height: {
      type: [Number, String],
      default: 6
    },
    // 滑块大小
    dotSize: {
      type: Number,
      default: 16
    },
    dotWidth: {
      type: Number,
      required: false
    },
    dotHeight: {
      type: Number,
      required: false
    },
    stopPropagation: {
      type: Boolean,
      default: false
    },
    // 事件类型
    eventType: {
      type: String,
      default: 'auto'
    },
    // 是否实时计算组件布局
    realTime: {
      type: Boolean,
      default: false
    },
    // 是否显示工具提示
    tooltip: {
      type: [String, Boolean],
      default: 'always',
      validator: function validator(val) {
        return ['hover', 'always', false].indexOf(val) > -1;
      }
    },
    // 组件方向
    direction: {
      type: String,
      default: 'horizontal',
      validator: function validator(val) {
        return ['horizontal', 'vertical'].indexOf(val) > -1;
      }
    },
    // 是否反向组件
    reverse: {
      type: Boolean,
      default: false
    },
    // 是否不可用
    disabled: {
      type: [Boolean, Array],
      default: false
    },
    piecewiseLabel: {
      type: Boolean,
      default: false
    },
    piecewise: {
      type: Boolean,
      default: false
    },
    // 进度条是否可拖拽（只限范围模式）
    processDraggable: {
      type: Boolean,
      default: false
    },
    // 是否可点击的
    clickable: {
      type: Boolean,
      default: true
    },
    // 是否固定距离
    fixed: {
      type: Boolean,
      default: false
    },
    // 是否为开发环境（打印错误）
    debug: {
      type: Boolean,
      default: true
    },
    // 最小范围
    minRange: {
      type: Number
    },
    // 最大范围
    maxRange: {
      type: Number
    },
    tooltipMerge: {
      type: Boolean,
      default: true
    },
    // 是否开启初始动画
    startAnimation: {
      type: Boolean,
      default: false
    },
    // 是否在拖拽结束后同步值
    lazy: {
      type: Boolean,
      default: false
    },
    // 在范围模式中，是否允许交叉
    enableCross: {
      type: Boolean,
      default: true
    },
    // 动画速度比
    speed: {
      type: Number,
      default: 0.5
    },
    useKeyboard: {
      type: Boolean,
      default: false
    },
    actionsKeyboard: {
      type: Array,
      default: function _default() {
        return [function (i) {
          return i - 1;
        }, function (i) {
          return i + 1;
        }];
      }
    },
    data: Array,
    formatter: [String, Function],
    mergeFormatter: [String, Function],
    // 工具提示方向
    tooltipDir: [Array, String],
    // 工具提示样式
    tooltipStyle: [Array, Object, Function],
    // 滑块样式
    sliderStyle: [Array, Object, Function],
    // 键盘控制时，算滑块获得焦点时样式
    focusStyle: [Array, Object, Function],
    // 组件禁用状态下样式
    disabledStyle: Object,
    // 进度条样式
    processStyle: Object,
    // 组件背景样式
    bgStyle: Object,
    piecewiseStyle: Object,
    piecewiseActiveStyle: Object,
    disabledDotStyle: [Array, Object, Function],
    labelStyle: Object,
    labelActiveStyle: Object
  },
  data: function data() {
    return {
      currentValue: 0,
      size: 0,
      fixedValue: 0,
      focusSlider: 0,
      currentSlider: 0,
      flag: false,
      processFlag: false,
      processSign: false,
      keydownFlag: false,
      focusFlag: false,
      dragFlag: false,
      crossFlag: false,
      isComponentExists: true,
      isMounted: false
    };
  },
  render: function render(h) {
    var _this = this;

    var sliderConBlocks = []; // dot

    if (this.isRange) {
      var dot0 = h('div', {
        ref: 'dot0',
        staticClass: 'slider-dot',
        class: [this.tooltipStatus, {
          'slider-dot-focus': this.focusFlag && this.focusSlider === 0,
          'slider-dot-dragging': this.flag && this.currentSlider === 0,
          'slider-dot-disabled': !this.boolDisabled && this.disabledArray[0]
        }],
        style: this.dotStyles
      }, [this._t('dot', [h('div', {
        staticClass: 'slider-dot-handle',
        style: [!this.boolDisabled && this.disabledArray[0] ? this.disabledDotStyles[0] : null, this.sliderStyles[0], this.focusFlag && this.focusSlider === 0 ? this.focusStyles[0] : null]
      })], {
        index: 0,
        value: this.val[0],
        disabled: this.disabledArray[0]
      }), h('div', {
        ref: 'tooltip0',
        staticClass: 'slider-tooltip-wrap',
        class: "slider-tooltip-".concat(this.tooltipDirection[0])
      }, [this._t('tooltip', [h('span', {
        staticClass: 'slider-tooltip',
        style: this.tooltipStyles[0]
      }, this.formatter ? this.formatting(this.val[0]) : this.val[0])], {
        value: this.val[0],
        index: 0,
        disabled: !this.boolDisabled && this.disabledArray[0]
      })])]);
      sliderConBlocks.push(dot0);
      var dot1 = h('div', {
        ref: 'dot1',
        staticClass: 'slider-dot',
        class: [this.tooltipStatus, {
          'slider-dot-focus': this.focusFlag && this.focusSlider === 1,
          'slider-dot-dragging': this.flag && this.currentSlider === 1,
          'slider-dot-disabled': !this.boolDisabled && this.disabledArray[1]
        }],
        style: this.dotStyles
      }, [this._t('dot', [h('div', {
        staticClass: 'slider-dot-handle',
        style: [!this.boolDisabled && this.disabledArray[1] ? this.disabledDotStyles[1] : null, this.sliderStyles[1], this.focusFlag && this.focusSlider === 1 ? this.focusStyles[1] : null]
      })], {
        index: 1,
        value: this.val[1],
        disabled: this.disabledArray[1]
      }), h('div', {
        ref: 'tooltip1',
        staticClass: 'slider-tooltip-wrap',
        class: "slider-tooltip-".concat(this.tooltipDirection[1])
      }, [this._t('tooltip', [h('span', {
        staticClass: 'slider-tooltip',
        style: this.tooltipStyles[1]
      }, this.formatter ? this.formatting(this.val[1]) : this.val[1])], {
        value: this.val[1],
        index: 1,
        disabled: !this.boolDisabled && this.disabledArray[1]
      })])]);
      sliderConBlocks.push(dot1);
    } else {
      var dot = h('div', {
        ref: 'dot',
        staticClass: 'slider-dot',
        class: [this.tooltipStatus, {
          'slider-dot-focus': this.focusFlag && this.focusSlider === 0,
          'slider-dot-dragging': this.flag && this.currentSlider === 0
        }],
        style: this.dotStyles
      }, [this._t('dot', [h('div', {
        staticClass: 'slider-dot-handle',
        style: [this.sliderStyles, this.focusFlag && this.focusSlider === 0 ? this.focusStyles : null]
      })], {
        value: this.val,
        disabled: this.boolDisabled
      }), h('div', {
        staticClass: 'slider-tooltip-wrap',
        class: "slider-tooltip-".concat(this.tooltipDirection)
      }, [this._t('tooltip', [h('span', {
        staticClass: 'slider-tooltip',
        style: this.tooltipStyles
      }, this.formatter ? this.formatting(this.val) : this.val)], {
        value: this.val
      })])]);
      sliderConBlocks.push(dot);
    } // piecewise


    var dotWrapLen = this.piecewiseDotWrap.length;
    var ulBlock = h('ul', {
      staticClass: 'slider-piecewise'
    }, this._l(this.piecewiseDotWrap, function (item, i) {
      var piecewiseDot = [];

      if (_this.piecewise) {
        piecewiseDot.push(h('span', {
          staticClass: 'piecewise-dot',
          style: [_this.piecewiseStyle, item.inRange ? _this.piecewiseActiveStyle : null]
        }));
      }

      var piecewiseLabel = [];

      if (_this.piecewiseLabel) {
        piecewiseLabel.push(h('span', {
          staticClass: 'piecewise-label',
          style: [_this.labelStyle, item.inRange ? _this.labelActiveStyle : null]
        }, item.label));
      }

      return h('li', {
        key: i,
        staticClass: 'piecewise-item',
        style: [_this.piecewiseDotStyle, item.style]
      }, [_this._t('piecewise', piecewiseDot, {
        label: item.label,
        index: i,
        first: i === 0,
        last: i === dotWrapLen - 1,
        active: item.inRange
      }), _this._t('label', piecewiseLabel, {
        label: item.label,
        index: i,
        first: i === 0,
        last: i === dotWrapLen - 1,
        active: item.inRange
      })]);
    }));
    sliderConBlocks.push(ulBlock); // process

    var processBlock = h('div', {
      ref: 'process',
      staticClass: 'slider-process',
      class: {
        'slider-process-draggable': this.isRange && this.processDraggable
      },
      style: this.processStyle,
      on: {
        click: function click(e) {
          return _this.processClick(e);
        }
      }
    }, [h('div', {
      ref: 'mergedTooltip',
      staticClass: 'merged-tooltip slider-tooltip-wrap',
      class: "slider-tooltip-".concat(this.isRange ? this.tooltipDirection[0] : this.tooltipDirection),
      style: this.tooltipMergedPosition
    }, [this._t('tooltip', [h('span', {
      staticClass: 'slider-tooltip',
      style: this.tooltipStyles
    }, this.mergeFormatter ? this.mergeFormatting(this.val[0], this.val[1]) : this.formatter ? this.val[0] === this.val[1] ? this.formatting(this.val[0]) : "".concat(this.formatting(this.val[0]), " - ").concat(this.formatting(this.val[1])) : this.val[0] === this.val[1] ? this.val[0] : "".concat(this.val[0], " - ").concat(this.val[1]))], {
      value: this.val,
      merge: true
    })])]);
    sliderConBlocks.push(processBlock); // <input type="range">

    if (!this.isRange && !this.data) {
      sliderConBlocks.push(h('input', {
        staticClass: 'slider-input',
        attrs: {
          type: 'range',
          min: this.min,
          max: this.max
        },
        domProps: {
          value: this.val
        },
        on: {
          input: function input(e) {
            return _this.val = e.target.value;
          }
        }
      }));
    }

    return h('div', {
      ref: 'wrap',
      staticClass: 'vue-range-slider slider-component',
      class: [this.flowDirection, this.disabledClass, this.stateClass, {
        'slider-has-label': this.piecewiseLabel
      }],
      style: [this.wrapStyles, this.boolDisabled ? this.disabledStyle : null],
      directives: [{
        name: 'show',
        value: this.show
      }],
      on: {
        click: function click(e) {
          return _this.wrapClick(e);
        }
      }
    }, [h('div', {
      ref: 'elem',
      staticClass: 'slider',
      style: [this.elemStyles, this.bgStyle],
      attrs: {
        'aria-hidden': true
      }
    }, sliderConBlocks)]);
  },
  computed: {
    val: {
      get: function get() {
        return this.data ? this.isRange ? [this.data[this.currentValue[0]], this.data[this.currentValue[1]]] : this.data[this.currentValue] : this.currentValue;
      },
      set: function set(val) {
        if (this.data) {
          if (this.isRange) {
            var index0 = this.data.indexOf(val[0]);
            var index1 = this.data.indexOf(val[1]);

            if (index0 > -1 && index1 > -1) {
              this.currentValue = [index0, index1];
            }
          } else {
            var index = this.data.indexOf(val);

            if (index > -1) {
              this.currentValue = index;
            }
          }
        } else {
          this.currentValue = val;
        }
      }
    },
    currentIndex: function currentIndex() {
      if (this.isRange) {
        return this.data ? this.currentValue : [this.getIndexByValue(this.currentValue[0]), this.getIndexByValue(this.currentValue[1])];
      } else {
        return this.getIndexByValue(this.currentValue);
      }
    },
    tooltipMergedPosition: function tooltipMergedPosition() {
      if (!this.isMounted) return {};
      var tooltipDirection = this.tooltipDirection[0];
      var dot0 = this.$refs.dot0;

      if (dot0) {
        var style = {};

        if (this.direction === 'vertical') {
          style[tooltipDirection] = "-".concat(this.dotHeightVal / 2 - this.width / 2 + 9, "px");
        } else {
          style[tooltipDirection] = "-".concat(this.dotWidthVal / 2 - this.height / 2 + 9, "px");
          style['left'] = "50%";
        }

        return style;
      }
    },
    tooltipDirection: function tooltipDirection() {
      var dir = this.tooltipDir || (this.direction === 'vertical' ? 'left' : 'top');

      if (isArray$2(dir)) {
        return this.isRange ? dir : dir[1];
      } else {
        return this.isRange ? [dir, dir] : dir;
      }
    },
    piecewiseDotWrap: function piecewiseDotWrap() {
      if (!this.piecewise && !this.piecewiseLabel) {
        return false;
      }

      var arr = [];

      for (var i = 0; i <= this.total; i++) {
        var style = this.direction === 'vertical' ? {
          bottom: "".concat(this.gap * i - this.width / 2, "px"),
          left: 0
        } : {
          left: "".concat(this.gap * i - this.height / 2, "px"),
          top: 0
        };
        var index = this.reverse ? this.total - i : i;
        var label = this.data ? this.data[index] : this.spacing * index + this.min;
        arr.push({
          style: style,
          label: this.formatter ? this.formatting(label) : label,
          inRange: index >= this.indexRange[0] && index <= this.indexRange[1]
        });
      }

      return arr;
    },
    total: function total() {
      if (this.data) {
        return this.data.length - 1;
      } else if (Math.floor((this.maximum - this.minimum) * this.multiple) % (this.step * this.multiple) !== 0) {
        this.printError('Prop[step] is illegal, Please make sure that the step can be divisible');
      }

      return (this.maximum - this.minimum) / this.step;
    },
    piecewiseDotStyle: function piecewiseDotStyle() {
      return this.direction === 'vertical' ? {
        width: "".concat(this.width, "px"),
        height: "".concat(this.width, "px")
      } : {
        width: "".concat(this.height, "px"),
        height: "".concat(this.height, "px")
      };
    },
    dotStyles: function dotStyles() {
      return this.direction === 'vertical' ? {
        width: "".concat(this.dotWidthVal, "px"),
        height: "".concat(this.dotHeightVal, "px"),
        left: "".concat(-(this.dotWidthVal - this.width) / 2, "px")
      } : {
        width: "".concat(this.dotWidthVal, "px"),
        height: "".concat(this.dotHeightVal, "px"),
        top: "".concat(-(this.dotHeightVal - this.height) / 2, "px")
      };
    },
    sliderStyles: function sliderStyles() {
      if (isArray$2(this.sliderStyle)) {
        return this.isRange ? this.sliderStyle : this.sliderStyle[1];
      } else if (typeof this.sliderStyle === 'function') {
        return this.sliderStyle(this.val, this.currentIndex);
      } else {
        return this.isRange ? [this.sliderStyle, this.sliderStyle] : this.sliderStyle;
      }
    },
    tooltipStyles: function tooltipStyles() {
      if (isArray$2(this.tooltipStyle)) {
        return this.isRange ? this.tooltipStyle : this.tooltipStyle[1];
      } else if (typeof this.tooltipStyle === 'function') {
        return this.tooltipStyle(this.val, this.currentIndex);
      } else {
        return this.isRange ? [this.tooltipStyle, this.tooltipStyle] : this.tooltipStyle;
      }
    },
    focusStyles: function focusStyles() {
      if (isArray$2(this.focusStyle)) {
        return this.isRange ? this.focusStyle : this.focusStyle[1];
      } else if (typeof this.focusStyle === 'function') {
        return this.focusStyle(this.val, this.currentIndex);
      } else {
        return this.isRange ? [this.focusStyle, this.focusStyle] : this.focusStyle;
      }
    },
    disabledDotStyles: function disabledDotStyles() {
      var disabledStyle = this.disabledDotStyle;

      if (isArray$2(disabledStyle)) {
        return disabledStyle;
      } else if (typeof disabledStyle === 'function') {
        var style = disabledStyle(this.val, this.currentIndex);
        return isArray$2(style) ? style : [style, style];
      } else if (disabledStyle) {
        return [disabledStyle, disabledStyle];
      } else {
        return [{
          backgroundColor: '#ccc'
        }, {
          backgroundColor: '#ccc'
        }];
      }
    },
    elemStyles: function elemStyles() {
      return this.direction === 'vertical' ? {
        width: "".concat(this.width, "px"),
        height: '100%'
      } : {
        height: "".concat(this.height, "px")
      };
    },
    wrapStyles: function wrapStyles() {
      return this.direction === 'vertical' ? {
        height: typeof this.height === 'number' ? "".concat(this.height, "px") : this.height,
        padding: "".concat(this.dotHeightVal / 2, "px ").concat(this.dotWidthVal / 2, "px")
      } : {
        width: typeof this.width === 'number' ? "".concat(this.width, "px") : this.width,
        padding: "".concat(this.dotHeightVal / 2, "px ").concat(this.dotWidthVal / 2, "px")
      };
    },
    stateClass: function stateClass() {
      return {
        'slider-state-process-drag': this.processFlag,
        'slider-state-drag': this.flag && !this.processFlag && !this.keydownFlag,
        'slider-state-focus': this.focusFlag
      };
    },
    tooltipStatus: function tooltipStatus() {
      return this.tooltip === 'hover' && this.flag ? 'slider-always' : this.tooltip ? "slider-".concat(this.tooltip) : '';
    },
    tooltipClass: function tooltipClass() {
      return ["slider-tooltip-".concat(this.tooltipDirection), 'slider-tooltip'];
    },
    minimum: function minimum() {
      return this.data ? 0 : this.min;
    },
    maximum: function maximum() {
      return this.data ? this.data.length - 1 : this.max;
    },
    multiple: function multiple() {
      var decimals = "".concat(this.step).split('.')[1];
      return decimals ? Math.pow(10, decimals.length) : 1;
    },
    indexRange: function indexRange() {
      return this.isRange ? this.currentIndex : [0, this.currentIndex];
    },
    spacing: function spacing() {
      return this.data ? 1 : this.step;
    },
    gap: function gap() {
      return this.size / this.total;
    },
    dotWidthVal: function dotWidthVal() {
      return typeof this.dotWidth === 'number' ? this.dotWidth : this.dotSize;
    },
    dotHeightVal: function dotHeightVal() {
      return typeof this.dotHeight === 'number' ? this.dotHeight : this.dotSize;
    },
    disabledArray: function disabledArray() {
      return isArray$2(this.disabled) ? this.disabled : [this.disabled, this.disabled];
    },
    boolDisabled: function boolDisabled() {
      return this.disabledArray.every(function (b) {
        return b === true;
      });
    },
    disabledClass: function disabledClass() {
      return this.boolDisabled ? 'slider-disabled' : '';
    },
    flowDirection: function flowDirection() {
      return "slider-".concat(this.direction + (this.reverse ? '-reverse' : ''));
    },
    isRange: function isRange() {
      return isArray$2(this.value);
    },
    isDisabled: function isDisabled() {
      return this.eventType === 'none' ? true : this.boolDisabled;
    },
    isFixed: function isFixed() {
      return this.fixed || this.minRange;
    },
    position: function position() {
      return this.isRange ? [(this.currentValue[0] - this.minimum) / this.spacing * this.gap, (this.currentValue[1] - this.minimum) / this.spacing * this.gap] : (this.currentValue - this.minimum) / this.spacing * this.gap;
    },
    limit: function limit() {
      return this.isRange ? this.isFixed ? [[0, (this.total - this.fixedValue) * this.gap], [this.fixedValue * this.gap, this.size]] : [[0, this.position[1]], [this.position[0], this.size]] : [0, this.size];
    },
    valueLimit: function valueLimit() {
      return this.isRange ? this.isFixed ? [[this.minimum, this.maximum - this.fixedValue * (this.spacing * this.multiple) / this.multiple], [this.minimum + this.fixedValue * (this.spacing * this.multiple) / this.multiple, this.maximum]] : [[this.minimum, this.currentValue[1]], [this.currentValue[0], this.maximum]] : [this.minimum, this.maximum];
    },
    idleSlider: function idleSlider() {
      return this.currentSlider === 0 ? 1 : 0;
    },
    slider: function slider() {
      return this.isRange ? [this.$refs.dot0, this.$refs.dot1] : this.$refs.dot;
    }
  },
  methods: {
    setValue: function setValue(val, noCb, speed) {
      var _this2 = this;

      if (isDiff(this.val, val)) {
        var resetVal = this.limitValue(val);
        this.val = this.isRange ? resetVal.concat() : resetVal;
        this.computedFixedValue();
        this.syncValue(noCb);
      }

      this.$nextTick(function () {
        return _this2.setPosition(speed);
      });
    },
    setIndex: function setIndex(val) {
      if (isArray$2(val) && this.isRange) {
        var value;

        if (this.data) {
          value = [this.data[val[0]], this.data[val[1]]];
        } else {
          value = [this.getValueByIndex(val[0]), this.getValueByIndex(val[1])];
        }

        this.setValue(value);
      } else {
        val = this.getValueByIndex(val);

        if (this.isRange) {
          this.currentSlider = val > (this.currentValue[1] - this.currentValue[0]) / 2 + this.currentValue[0] ? 1 : 0;
        }

        this.setCurrentValue(val);
      }
    },
    wrapClick: function wrapClick(e) {
      if (this.isDisabled || !this.clickable || this.processFlag || this.dragFlag) return false;
      var pos = this.getPos(e);

      if (this.isRange) {
        if (this.disabledArray.every(function (b) {
          return b === false;
        })) {
          this.currentSlider = pos > (this.position[1] - this.position[0]) / 2 + this.position[0] ? 1 : 0;
        } else if (this.disabledArray[0]) {
          if (pos < this.position[0]) return false;
          this.currentSlider = 1;
        } else if (this.disabledArray[1]) {
          if (pos > this.position[1]) return false;
          this.currentSlider = 0;
        }
      }

      if (this.disabledArray[this.currentSlider]) {
        return false;
      }

      this.setValueOnPos(pos);

      if (this.isRange && this.tooltipMerge) {
        var timer = setInterval(this.handleOverlapTooltip, 16.7);
        setTimeout(function () {
          return window.clearInterval(timer);
        }, this.speed * 1000);
      }
    },
    processClick: function processClick(e) {
      if (this.fixed) e.stopPropagation();
    },
    syncValue: function syncValue(noCb) {
      var val = this.isRange ? this.val.concat() : this.val;
      this.$emit('input', val);
      this.keydownFlag && this.$emit('on-keypress', val);
      noCb || this.$emit('slide-end', val);
    },
    getPos: function getPos(e) {
      this.realTime && this.getStaticData();
      return this.direction === 'vertical' ? this.reverse ? e.pageY - this.offset : this.size - (e.pageY - this.offset) : this.reverse ? this.size - (e.pageX - this.offset) : e.pageX - this.offset;
    },
    setValueOnPos: function setValueOnPos(pos, isDrag) {
      var range = this.isRange ? this.limit[this.currentSlider] : this.limit;
      var valueRange = this.isRange ? this.valueLimit[this.currentSlider] : this.valueLimit;
      var index = Math.round(pos / this.gap);

      if (pos >= range[0] && pos <= range[1]) {
        var v = this.getValueByIndex(index);
        this.setTransform(pos);
        this.setCurrentValue(v, isDrag);

        if (this.isRange && (this.fixed || this.isLessRange(pos, index))) {
          this.setTransform(pos + this.fixedValue * this.gap * (this.currentSlider === 0 ? 1 : -1), true);
          this.setCurrentValue((v * this.multiple + this.fixedValue * this.spacing * this.multiple * (this.currentSlider === 0 ? 1 : -1)) / this.multiple, isDrag, true);
        }
      } else {
        var anotherSlider = pos < range[0] ? 0 : 1;
        var currentSlider = anotherSlider === 0 ? 1 : 0;
        this.setTransform(range[anotherSlider]);
        this.setCurrentValue(valueRange[anotherSlider]);

        if (this.isRange && (this.fixed || this.isLessRange(pos, index))) {
          this.setTransform(this.limit[this.idleSlider][anotherSlider], true);
          this.setCurrentValue(this.valueLimit[this.idleSlider][anotherSlider], isDrag, true);
        } else if (this.isRange && (this.enableCross || this.crossFlag) && !this.isFixed && !this.disabledArray[anotherSlider] && this.currentSlider === currentSlider) {
          this.focusSlider = anotherSlider;
          this.currentSlider = anotherSlider;
        }
      }

      this.crossFlag = false;
    },
    setCurrentValue: function setCurrentValue(val, isDrag, isIdleSlider) {
      var slider = isIdleSlider ? this.idleSlider : this.currentSlider;
      if (val < this.minimum || val > this.maximum) return false;

      if (this.isRange) {
        if (isDiff(this.currentValue[slider], val)) {
          this.currentValue.splice(slider, 1, val);

          if (!this.lazy || !this.flag) {
            this.syncValue();
          }
        }
      } else if (isDiff(this.currentValue, val)) {
        this.currentValue = val;

        if (!this.lazy || !this.flag) {
          this.syncValue();
        }
      }

      isDrag || this.setPosition();
    },
    setPosition: function setPosition(speed) {
      this.flag || this.setTransitionTime(speed === undefined ? this.speed : speed);

      if (this.isRange) {
        this.setTransform(this.position[0], this.currentSlider === 1);
        this.setTransform(this.position[1], this.currentSlider === 0);
      } else {
        this.setTransform(this.position);
      }

      this.flag || this.setTransitionTime(0);
    },
    setTransform: function setTransform(val, isIdleSlider) {
      var slider = isIdleSlider ? this.idleSlider : this.currentSlider;
      var value = roundToDPR((this.direction === 'vertical' ? this.dotHeightVal / 2 - val : val - this.dotWidthVal / 2) * (this.reverse ? -1 : 1));
      var translateValue = this.direction === 'vertical' ? "translateY(".concat(value, "px)") : "translateX(".concat(value, "px)");
      var processSize = this.fixed ? "".concat(this.fixedValue * this.gap, "px") : "".concat(slider === 0 ? this.position[1] - val : val - this.position[0], "px");
      var processPos = this.fixed ? "".concat(slider === 0 ? val : val - this.fixedValue * this.gap, "px") : "".concat(slider === 0 ? val : this.position[0], "px");

      if (this.isRange) {
        this.slider[slider].style[transform] = translateValue;

        if (this.direction === 'vertical') {
          this.$refs.process.style.height = processSize;
          this.$refs.process.style[this.reverse ? 'top' : 'bottom'] = processPos;
        } else {
          this.$refs.process.style.width = processSize;
          this.$refs.process.style[this.reverse ? 'right' : 'left'] = processPos;
        }
      } else {
        this.slider.style[transform] = translateValue;

        if (this.direction === 'vertical') {
          this.$refs.process.style.height = "".concat(val, "px");
          this.$refs.process.style[this.reverse ? 'top' : 'bottom'] = 0;
        } else {
          this.$refs.process.style.width = "".concat(val, "px");
          this.$refs.process.style[this.reverse ? 'right' : 'left'] = 0;
        }
      }
    },
    setTransitionTime: function setTransitionTime(time) {
      // In order to avoid browser merge style and modify together
      time || this.$refs.process.offsetWidth;

      if (this.isRange) {
        var sliderLen = this.slider.length;

        for (var i = 0; i < sliderLen; i++) {
          this.slider[i].style[transitionDuration] = "".concat(time, "s");
        }

        this.$refs.process.style[transitionDuration] = "".concat(time, "s");
      } else {
        this.slider.style[transitionDuration] = "".concat(time, "s");
        this.$refs.process.style[transitionDuration] = "".concat(time, "s");
      }
    },
    computedFixedValue: function computedFixedValue() {
      if (!this.isFixed) {
        this.fixedValue = 0;
        return false;
      }

      this.fixedValue = this.currentIndex[1] - this.currentIndex[0];
      this.fixedValue = Math.max(this.fixed ? this.currentIndex[1] - this.currentIndex[0] : 0, this.minRange || 0);
    },
    limitValue: function limitValue(val) {
      var _this3 = this;

      if (this.data) {
        return val;
      }

      var inRange = function inRange(v) {
        if (v < _this3.min) {
          _this3.printError("The value of the slider is ".concat(val, ", the minimum value is ").concat(_this3.min, ", the value of this slider can not be less than the minimum value"));

          return _this3.min;
        } else if (v > _this3.max) {
          _this3.printError("The value of the slider is ".concat(val, ", the maximum value is ").concat(_this3.max, ", the value of this slider can not be greater than the maximum value"));

          return _this3.max;
        }

        return v;
      };

      if (this.isRange) {
        return val.map(function (v) {
          return inRange(v);
        });
      } else {
        return inRange(val);
      }
    },
    getStaticData: function getStaticData() {
      if (this.$refs.elem) {
        this.size = this.direction === 'vertical' ? this.$refs.elem.offsetHeight : this.$refs.elem.offsetWidth;
        this.offset = this.direction === 'vertical' ? this.$refs.elem.getBoundingClientRect().top + window.pageYOffset || document.documentElement.scrollTop : this.$refs.elem.getBoundingClientRect().left;
      }
    },
    handleOverlapTooltip: function handleOverlapTooltip() {
      var isDirectionSame = this.tooltipDirection[0] === this.tooltipDirection[1];

      if (this.isRange && isDirectionSame) {
        var tooltip0 = this.reverse ? this.$refs.tooltip1 : this.$refs.tooltip0;
        var tooltip1 = this.reverse ? this.$refs.tooltip0 : this.$refs.tooltip1;
        var tooltip0Rect = tooltip0.getBoundingClientRect();
        var tooltip1Rect = tooltip1.getBoundingClientRect();
        var tooltip0Right = tooltip0Rect.right;
        var tooltip1Left = tooltip1Rect.left;
        var tooltip0Y = tooltip0Rect.top;
        var tooltip1Y = tooltip1Rect.top + tooltip1Rect.height;
        var horizontalOverlap = this.direction === 'horizontal' && tooltip0Right > tooltip1Left;
        var verticalOverlap = this.direction === 'vertical' && tooltip1Y > tooltip0Y;

        if (horizontalOverlap || verticalOverlap) {
          this.handleDisplayMergedTooltip(true);
        } else {
          this.handleDisplayMergedTooltip(false);
        }
      }
    },
    handleDisplayMergedTooltip: function handleDisplayMergedTooltip(show) {
      var tooltip0 = this.$refs.tooltip0;
      var tooltip1 = this.$refs.tooltip1;
      var mergedTooltip = this.$refs.process.getElementsByClassName('merged-tooltip')[0];

      if (show) {
        tooltip0.style.visibility = 'hidden';
        tooltip1.style.visibility = 'hidden';
        mergedTooltip.style.visibility = 'visible';
      } else {
        tooltip0.style.visibility = 'visible';
        tooltip1.style.visibility = 'visible';
        mergedTooltip.style.visibility = 'hidden';
      }
    },
    isLessRange: function isLessRange(pos, index) {
      if (!this.isRange || !this.minRange && !this.maxRange) {
        return false;
      }

      var diff = this.currentSlider === 0 ? this.currentIndex[1] - index : index - this.currentIndex[0];

      if (this.minRange && diff <= this.minRange) {
        this.fixedValue = this.minRange;
        return true;
      }

      if (this.maxRange && diff >= this.maxRange) {
        this.fixedValue = this.maxRange;
        return true;
      }

      this.computedFixedValue();
      return false;
    },
    getValueByIndex: function getValueByIndex(index) {
      return (this.spacing * this.multiple * index + this.minimum * this.multiple) / this.multiple;
    },
    getIndexByValue: function getIndexByValue(value) {
      return Math.round((value - this.minimum) * this.multiple) / (this.spacing * this.multiple);
    },
    formatting: function formatting(value) {
      return typeof this.formatter === 'string' ? this.formatter.replace(/{value}/, value) : this.formatter(value);
    },
    mergeFormatting: function mergeFormatting(value1, value2) {
      return typeof this.mergeFormatter === 'string' ? this.mergeFormatter.replace(/{(value1|value2)}/g, function (_, key) {
        return key === 'value1' ? value1 : value2;
      }) : this.mergeFormatter(value1, value2);
    },
    _start: function _start(e) {
      var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      var isProcess = arguments.length > 2 ? arguments[2] : undefined;

      if (this.disabledArray[index]) {
        return false;
      }

      if (this.stopPropagation) {
        e.stopPropagation();
      }

      if (this.isRange) {
        this.currentSlider = index;

        if (isProcess) {
          if (!this.processDraggable) {
            return false;
          }

          this.processFlag = true;
          this.processSign = {
            pos: this.position,
            start: this.getPos(e.targetTouches && e.targetTouches[0] ? e.targetTouches[0] : e)
          };
        }

        if (!this.enableCross && this.val[0] === this.val[1]) {
          this.crossFlag = true;
        }
      }

      if (!isProcess && this.useKeyboard) {
        this.focusFlag = true;
        this.focusSlider = index;
      }

      this.flag = true;
      this.$emit('drag-start', this);
    },
    _move: function _move(e) {
      // e.preventDefault() // NOTE: COMMENTED, BREAKS SELECTING THINGS ON PAGE
      if (this.stopPropagation) {
        e.stopPropagation();
      }

      if (!this.flag) return false;
      if (e.targetTouches && e.targetTouches[0]) e = e.targetTouches[0];

      if (this.processFlag) {
        this.currentSlider = 0;
        this.setValueOnPos(this.processSign.pos[0] + this.getPos(e) - this.processSign.start, true);
        this.currentSlider = 1;
        this.setValueOnPos(this.processSign.pos[1] + this.getPos(e) - this.processSign.start, true);
      } else {
        this.dragFlag = true;
        this.setValueOnPos(this.getPos(e), true);
      }

      if (this.isRange && this.tooltipMerge) {
        this.handleOverlapTooltip();
      }
    },
    _end: function _end(e) {
      var _this4 = this;

      if (this.stopPropagation) {
        e.stopPropagation();
      }

      if (this.flag) {
        this.$emit('drag-end', this);

        if (this.lazy && isDiff(this.val, this.value)) {
          this.syncValue();
        }
      } else {
        return false;
      }

      this.flag = false;
      this.$nextTick(function () {
        _this4.crossFlag = false;
        _this4.dragFlag = false;
        _this4.processFlag = false;
      });
      this.setPosition();
    },
    blurSlider: function blurSlider(e) {
      var dot = this.isRange ? this.$refs["dot".concat(this.focusSlider)] : this.$refs.dot;

      if (!dot || dot === e.target || dot.contains(e.target)) {
        return false;
      }

      this.focusFlag = false;
    },
    handleKeydown: function handleKeydown(e) {
      if (!this.useKeyboard) {
        return false;
      }

      var keyCode = e.which || e.keyCode;

      switch (keyCode) {
        case 37:
        case 40:
          e.preventDefault(); // e.stopPropagation()

          this.keydownFlag = true;
          this.flag = true;
          this.changeFocusSlider(this.actionsKeyboard[0]);
          break;

        case 38:
        case 39:
          e.preventDefault(); // e.stopPropagation()

          this.keydownFlag = true;
          this.flag = true;
          this.changeFocusSlider(this.actionsKeyboard[1]);
          break;
      }
    },
    handleKeyup: function handleKeyup() {
      if (this.keydownFlag) {
        this.keydownFlag = false;
        this.flag = false;
      }
    },
    changeFocusSlider: function changeFocusSlider(fn) {
      var _this5 = this;

      if (this.isRange) {
        var arr = this.currentIndex.map(function (index, i) {
          if (i === _this5.focusSlider || _this5.fixed) {
            var val = fn(index);
            var range = _this5.fixed ? _this5.valueLimit[i] : [0, _this5.total];

            if (val <= range[1] && val >= range[0]) {
              return val;
            }
          }

          return index;
        });

        if (arr[0] > arr[1]) {
          this.focusSlider = this.focusSlider === 0 ? 1 : 0;
          arr = arr.reverse();
        }

        this.setIndex(arr);
      } else {
        this.setIndex(fn(this.currentIndex));
      }
    },
    bindEvents: function bindEvents() {
      var me = this;

      this.processStartFn = function (e) {
        me._start(e, 0, true);
      };

      this.dot0StartFn = function (e) {
        me._start(e, 0);
      };

      this.dot1StartFn = function (e) {
        me._start(e, 1);
      };

      if (isMobile) {
        addEvent(this.$refs.process, EVENT_TOUCH_START, this.processStartFn);
        addEvent(document, EVENT_TOUCH_MOVE, this._move);
        addEvent(document, EVENT_TOUCH_END, this._end);
        addEvent(document, EVENT_TOUCH_CANCEL, this._end);

        if (this.isRange) {
          addEvent(this.$refs.dot0, EVENT_TOUCH_START, this.dot0StartFn);
          addEvent(this.$refs.dot1, EVENT_TOUCH_START, this.dot1StartFn);
        } else {
          addEvent(this.$refs.dot, EVENT_TOUCH_START, this._start);
        }
      } else {
        addEvent(this.$refs.process, EVENT_MOUSE_DOWN, this.processStartFn);
        addEvent(document, EVENT_MOUSE_DOWN, this.blurSlider);
        addEvent(document, EVENT_MOUSE_MOVE, this._move);
        addEvent(document, EVENT_MOUSE_UP, this._end);
        addEvent(document, EVENT_MOUSE_LEAVE, this._end);

        if (this.isRange) {
          addEvent(this.$refs.dot0, EVENT_MOUSE_DOWN, this.dot0StartFn);
          addEvent(this.$refs.dot1, EVENT_MOUSE_DOWN, this.dot1StartFn);
        } else {
          addEvent(this.$refs.dot, EVENT_MOUSE_DOWN, this._start);
        }
      }

      addEvent(document, EVENT_KEY_DOWN, this.handleKeydown);
      addEvent(document, EVENT_KEY_UP, this.handleKeyup);
      addEvent(window, EVENT_RESIZE, this.refresh);

      if (this.isRange && this.tooltipMerge) {
        addEvent(this.$refs.dot0, transitionEnd, this.handleOverlapTooltip);
        addEvent(this.$refs.dot1, transitionEnd, this.handleOverlapTooltip);
      }
    },
    unbindEvents: function unbindEvents() {
      if (isMobile) {
        removeEvent(this.$refs.process, EVENT_TOUCH_START, this.processStartFn);
        removeEvent(document, EVENT_TOUCH_MOVE, this._move);
        removeEvent(document, EVENT_TOUCH_END, this._end);
        removeEvent(document, EVENT_TOUCH_CANCEL, this._end);

        if (this.isRange) {
          removeEvent(this.$refs.dot0, EVENT_TOUCH_START, this.dot0StartFn);
          removeEvent(this.$refs.dot1, EVENT_TOUCH_START, this.dot1StartFn);
        } else {
          removeEvent(this.$refs.dot, EVENT_TOUCH_START, this._start);
        }
      } else {
        removeEvent(this.$refs.process, EVENT_MOUSE_DOWN, this.processStartFn);
        removeEvent(document, EVENT_MOUSE_DOWN, this.blurSlider);
        removeEvent(document, EVENT_MOUSE_MOVE, this._move);
        removeEvent(document, EVENT_MOUSE_UP, this._end);
        removeEvent(document, EVENT_MOUSE_LEAVE, this._end);

        if (this.isRange) {
          removeEvent(this.$refs.dot0, EVENT_MOUSE_DOWN, this.dot0StartFn);
          removeEvent(this.$refs.dot1, EVENT_MOUSE_DOWN, this.dot1StartFn);
        } else {
          removeEvent(this.$refs.dot, EVENT_MOUSE_DOWN, this._start);
        }
      }

      removeEvent(document, EVENT_KEY_DOWN, this.handleKeydown);
      removeEvent(document, EVENT_KEY_UP, this.handleKeyup);
      removeEvent(window, EVENT_RESIZE, this.refresh);

      if (this.isRange && this.tooltipMerge) {
        removeEvent(this.$refs.dot0, transitionEnd, this.handleOverlapTooltip);
        removeEvent(this.$refs.dot1, transitionEnd, this.handleOverlapTooltip);
      }
    },
    refresh: function refresh() {
      if (this.$refs.elem) {
        this.getStaticData();
        this.computedFixedValue();
        this.setPosition();
        this.unbindEvents();
        this.bindEvents();
      }
    },
    printError: function printError(msg) {
      if (this.debug) {
        console.error("[VueSlider error]: ".concat(msg));
      }
    }
  },
  mounted: function mounted() {
    var _this6 = this;

    this.isComponentExists = true;

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return this.printError('window or document is undefined, can not be initialization.');
    }

    this.$nextTick(function () {
      _this6.getStaticData();

      _this6.setValue(_this6.limitValue(_this6.value), true, _this6.startAnimation ? _this6.speed : 0);

      _this6.bindEvents();

      if (_this6.isRange && _this6.tooltipMerge && !_this6.startAnimation) {
        _this6.handleOverlapTooltip();
      }
    });
    this.isMounted = true;
  },
  watch: {
    value: function value(val) {
      this.flag || this.setValue(val, true);
    },
    show: function show(bool) {
      if (bool && !this.size) {
        this.$nextTick(this.refresh);
      }
    },
    max: function max(val) {
      if (val < this.min) {
        return this.printError('The maximum value can not be less than the minimum value.');
      }

      var resetVal = this.limitValue(this.val);
      this.setValue(resetVal);
      this.refresh();
    },
    min: function min(val) {
      if (val > this.max) {
        return this.printError('The minimum value can not be greater than the maximum value.');
      }

      var resetVal = this.limitValue(this.val);
      this.setValue(resetVal);
      this.refresh();
    },
    fixed: function fixed() {
      this.computedFixedValue();
    },
    minRange: function minRange() {
      this.computedFixedValue();
    }
  },
  beforeDestroy: function beforeDestroy() {
    this.isComponentExists = false;
    this.unbindEvents();
    this.refresh();
  },
  deactivated: function deactivated() {
    this.isComponentExists = false;
    this.unbindEvents();
    this.refresh();
  }
};

var version$1 = "1.0.3";

Slider.version = version$1;

Slider.install = function (Vue) {
  Vue.component(Slider.name, Slider);
};

if (typeof window !== 'undefined' && window.Vue) {
  window.Vue.use(Slider);
}

//

const tooltipStyle = {
  backgroundColor: 'var(--me-gray-0)',
  borderColor: 'var(--me-gray-0)',
  fontSize: '12px'
};

var script$1U = {
  name: 'MeRangeSlider',
  components: {
    VueRangeSlider: Slider
  },

  props: {
    model: {
      type: [Number, Array],
      required: true
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    enableCross: {
      type: Boolean,
      default: true
    },
    tooltip: {
      type: [String, Boolean],
      default: 'hover'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: true
    },
    dotSize: {
      type: Number,
      default: 16
    },
    showLabel: {
      type: Boolean,
      default: true
    },
    height: {
      type: Number,
      default: 6
    },
    variant: {
      type: String,
      default: 'primary'
    }
  },

  data() {
    return {
      vModel: this.model,
      sliderStyle: null,
      tooltipStyle,
      isDragging: false
    }
  },

  computed: {
    valuesLabel() {
      if (Array.isArray(this.vModel)) {
        return this.vModel.toString().replace(',', ' - ')
      }

      return this.vModel
    },

    variantStyle() {
      const colors = {
        primary: 'var(--me-primary-2)',
        success: 'var(--me-success-2)',
        warning: 'var(--me-warning-2)',
        danger: 'var(--me-danger-2)'
      };

      return colors[this.variant] || colors.primary
    },

    processStyle() {
      return {
        backgroundColor: this.variantStyle
      }
    }
  },

  watch: {
    model(value) {
      this.vModel = value;
    }
  },

  mounted() {
    if (!this.dotSize) {
      this.sliderStyle = {
        border: 'none',
        boxShadow: 'none',
        backgroundColor: 'red'
      };
    }
  },

  methods: {
    setValue(val) {
      this.$refs.vueRangeSlider.setValue(val);
    },

    refresh() {
      this.$refs.vueRangeSlider.refresh();
    }
  }
};

/* script */
const __vue_script__$1U = script$1U;

/* template */
var __vue_render__$1U = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "range-slider", class: "range-slider--" + _vm.variant },
    [
      _c(
        "vue-range-slider",
        _vm._b(
          {
            ref: "vueRangeSlider",
            class: { clickable: _vm.clickable },
            attrs: {
              "process-style": _vm.processStyle,
              "tooltip-style": _vm.tooltipStyle,
              "slider-style": _vm.sliderStyle
            },
            on: {
              "drag-end": function(data) {
                return _vm.$emit("drag-end", data)
              },
              "drag-start": function(data) {
                return _vm.$emit("drag-start", data)
              },
              "slide-end": function(data) {
                return _vm.$emit("slide-end", data)
              }
            },
            scopedSlots: _vm._u(
              [
                _vm._l(_vm.$scopedSlots, function(_, slot) {
                  return {
                    key: slot,
                    fn: function(scope) {
                      return [_vm._t(slot, null, null, scope)]
                    }
                  }
                })
              ],
              null,
              true
            ),
            model: {
              value: _vm.vModel,
              callback: function($$v) {
                _vm.vModel = $$v;
              },
              expression: "vModel"
            }
          },
          "vue-range-slider",
          _vm.$props,
          false
        )
      ),
      _vm._v(" "),
      !_vm.tooltip && _vm.showLabel
        ? _c("div", { staticClass: "slider-values" }, [
            _vm._v("\n    " + _vm._s(_vm.valuesLabel) + "\n  ")
          ])
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$1U = [];
__vue_render__$1U._withStripped = true;

  /* style */
  const __vue_inject_styles__$1U = function (inject) {
    if (!inject) return
    inject("data-v-62f83c23_0", { source: ".range-slider[data-v-62f83c23]  .vue-range-slider .slider-dot-handle {\n  cursor: grab;\n}\n.range-slider[data-v-62f83c23]  .vue-range-slider .slider-dot-handle:active {\n  cursor: grabbing;\n}\n.range-slider[data-v-62f83c23]  .vue-range-slider.clickable .slider {\n  cursor: pointer;\n}\n.range-slider[data-v-62f83c23]  .vue-range-slider.slider-disabled .slider {\n  cursor: not-allowed;\n}\n.range-slider[data-v-62f83c23]  .vue-range-slider.slider-disabled .slider-dot-handle {\n  cursor: not-allowed;\n}\n.range-slider .slider-values[data-v-62f83c23] {\n  text-align: center;\n}\n.range-slider--primary[data-v-62f83c23]  .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-primary-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n.range-slider--success[data-v-62f83c23]  .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-success-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n.range-slider--danger[data-v-62f83c23]  .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-danger-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n.range-slider--warning[data-v-62f83c23]  .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-warning-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n\n/*# sourceMappingURL=RangeSlider.vue.map */", map: {"version":3,"sources":["/app/build/src/components/RangeSlider.vue","RangeSlider.vue"],"names":[],"mappings":"AA0JA;EACA,YAAA;ACzJA;AD2JA;EACA,gBAAA;ACzJA;AD8JA;EACA,eAAA;AC5JA;ADiKA;EACA,mBAAA;AC/JA;ADkKA;EACA,mBAAA;AChKA;ADoKA;EACA,kBAAA;AClKA;ADwKA;EACA,qCASA;EARA,gBAAA;EACA,uBAAA;ACtKA;ADmKA;EACA,qCAaA;EAZA,gBAAA;EACA,uBAAA;ACjKA;AD8JA;EACA,oCAiBA;EAhBA,gBAAA;EACA,uBAAA;AC5JA;ADyJA;EACA,qCAqBA;EApBA,gBAAA;EACA,uBAAA;ACvJA;;AAEA,0CAA0C","file":"RangeSlider.vue","sourcesContent":["<template>\n  <div :class=\"`range-slider--${variant}`\" class=\"range-slider\">\n    <vue-range-slider\n      ref=\"vueRangeSlider\"\n      :class=\"{'clickable': clickable}\"\n      v-model=\"vModel\"\n      v-bind=\"$props\"\n      :process-style=\"processStyle\"\n      :tooltip-style=\"tooltipStyle\"\n      :slider-style=\"sliderStyle\"\n      @drag-end=\"(data) => $emit('drag-end', data)\"\n      @drag-start=\"(data) => $emit('drag-start', data)\"\n      @slide-end=\"(data) => $emit('slide-end', data)\"\n    >\n      <template v-for=\"(_, slot) of $scopedSlots\" v-slot:[slot]=\"scope\">\n        <slot :name=\"slot\" v-bind=\"scope\" />\n      </template>\n    </vue-range-slider>\n\n    <div v-if=\"!tooltip && showLabel\" class=\"slider-values\">\n      {{ valuesLabel }}\n    </div>\n  </div>\n</template>\n\n<script>\nimport VueRangeSlider from 'vue-range-component-fixed'\n\nconst tooltipStyle = {\n  backgroundColor: 'var(--me-gray-0)',\n  borderColor: 'var(--me-gray-0)',\n  fontSize: '12px'\n}\n\nexport default {\n  name: 'MeRangeSlider',\n  components: {\n    VueRangeSlider\n  },\n\n  props: {\n    model: {\n      type: [Number, Array],\n      required: true\n    },\n    min: {\n      type: Number,\n      default: 0\n    },\n    max: {\n      type: Number,\n      default: 100\n    },\n    enableCross: {\n      type: Boolean,\n      default: true\n    },\n    tooltip: {\n      type: [String, Boolean],\n      default: 'hover'\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    clickable: {\n      type: Boolean,\n      default: true\n    },\n    dotSize: {\n      type: Number,\n      default: 16\n    },\n    showLabel: {\n      type: Boolean,\n      default: true\n    },\n    height: {\n      type: Number,\n      default: 6\n    },\n    variant: {\n      type: String,\n      default: 'primary'\n    }\n  },\n\n  data() {\n    return {\n      vModel: this.model,\n      sliderStyle: null,\n      tooltipStyle,\n      isDragging: false\n    }\n  },\n\n  computed: {\n    valuesLabel() {\n      if (Array.isArray(this.vModel)) {\n        return this.vModel.toString().replace(',', ' - ')\n      }\n\n      return this.vModel\n    },\n\n    variantStyle() {\n      const colors = {\n        primary: 'var(--me-primary-2)',\n        success: 'var(--me-success-2)',\n        warning: 'var(--me-warning-2)',\n        danger: 'var(--me-danger-2)'\n      }\n\n      return colors[this.variant] || colors.primary\n    },\n\n    processStyle() {\n      return {\n        backgroundColor: this.variantStyle\n      }\n    }\n  },\n\n  watch: {\n    model(value) {\n      this.vModel = value\n    }\n  },\n\n  mounted() {\n    if (!this.dotSize) {\n      this.sliderStyle = {\n        border: 'none',\n        boxShadow: 'none',\n        backgroundColor: 'red'\n      }\n    }\n  },\n\n  methods: {\n    setValue(val) {\n      this.$refs.vueRangeSlider.setValue(val)\n    },\n\n    refresh() {\n      this.$refs.vueRangeSlider.refresh()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .range-slider {\n    ::v-deep .vue-range-slider {\n      .slider-dot-handle {\n        cursor: grab;\n\n        &:active {\n          cursor: grabbing;\n        }\n      }\n\n      &.clickable {\n        .slider {\n          cursor: pointer;\n        }\n      }\n\n      &.slider-disabled {\n        .slider {\n          cursor: not-allowed;\n        }\n\n        .slider-dot-handle {\n          cursor: not-allowed;\n        }\n      }\n    }\n    .slider-values {\n      text-align: center;\n    }\n\n    @mixin dotDraggingStyle($color) {\n      ::v-deep .vue-range-slider {\n        .slider-dot-dragging {\n          .slider-dot-handle {\n            background-color: $color;\n            box-shadow: none;\n            border: 1px solid white;\n          }\n        }\n      }\n    }\n\n    &--primary {\n      @include dotDraggingStyle(var(--me-primary-0));\n    }\n\n    &--success {\n      @include dotDraggingStyle(var(--me-success-0));\n    }\n\n    &--danger {\n      @include dotDraggingStyle(var(--me-danger-0));\n    }\n\n    &--warning {\n      @include dotDraggingStyle(var(--me-warning-0));\n    }\n  }\n</style>\n",".range-slider ::v-deep .vue-range-slider .slider-dot-handle {\n  cursor: grab;\n}\n.range-slider ::v-deep .vue-range-slider .slider-dot-handle:active {\n  cursor: grabbing;\n}\n.range-slider ::v-deep .vue-range-slider.clickable .slider {\n  cursor: pointer;\n}\n.range-slider ::v-deep .vue-range-slider.slider-disabled .slider {\n  cursor: not-allowed;\n}\n.range-slider ::v-deep .vue-range-slider.slider-disabled .slider-dot-handle {\n  cursor: not-allowed;\n}\n.range-slider .slider-values {\n  text-align: center;\n}\n.range-slider--primary ::v-deep .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-primary-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n.range-slider--success ::v-deep .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-success-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n.range-slider--danger ::v-deep .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-danger-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n.range-slider--warning ::v-deep .vue-range-slider .slider-dot-dragging .slider-dot-handle {\n  background-color: var(--me-warning-0);\n  box-shadow: none;\n  border: 1px solid white;\n}\n\n/*# sourceMappingURL=RangeSlider.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1U = "data-v-62f83c23";
  /* module identifier */
  const __vue_module_identifier__$1U = undefined;
  /* functional template */
  const __vue_is_functional_template__$1U = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1U, staticRenderFns: __vue_staticRenderFns__$1U },
    __vue_inject_styles__$1U,
    __vue_script__$1U,
    __vue_scope_id__$1U,
    __vue_is_functional_template__$1U,
    __vue_module_identifier__$1U,
    false,
    createInjector,
    undefined,
    undefined
  );

/**
 * This function is like `arrayIncludes` except that it accepts a comparator.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @param {Function} comparator The comparator invoked per element.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludesWith(array, value, comparator) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (comparator(value, array[index])) {
      return true;
    }
  }
  return false;
}

var _arrayIncludesWith = arrayIncludesWith;

/** Used as references for various `Number` constants. */
var INFINITY$3 = 1 / 0;

/**
 * Creates a set object of `values`.
 *
 * @private
 * @param {Array} values The values to add to the set.
 * @returns {Object} Returns the new set.
 */
var createSet = !(_Set && (1 / _setToArray(new _Set([,-0]))[1]) == INFINITY$3) ? noop_1 : function(values) {
  return new _Set(values);
};

var _createSet = createSet;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE$1 = 200;

/**
 * The base implementation of `_.uniqBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee] The iteratee invoked per element.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 */
function baseUniq(array, iteratee, comparator) {
  var index = -1,
      includes = _arrayIncludes,
      length = array.length,
      isCommon = true,
      result = [],
      seen = result;

  if (comparator) {
    isCommon = false;
    includes = _arrayIncludesWith;
  }
  else if (length >= LARGE_ARRAY_SIZE$1) {
    var set = iteratee ? null : _createSet(array);
    if (set) {
      return _setToArray(set);
    }
    isCommon = false;
    includes = _cacheHas;
    seen = new _SetCache;
  }
  else {
    seen = iteratee ? [] : result;
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee ? iteratee(value) : value;

    value = (comparator || value !== 0) ? value : 0;
    if (isCommon && computed === computed) {
      var seenIndex = seen.length;
      while (seenIndex--) {
        if (seen[seenIndex] === computed) {
          continue outer;
        }
      }
      if (iteratee) {
        seen.push(computed);
      }
      result.push(value);
    }
    else if (!includes(seen, computed, comparator)) {
      if (seen !== result) {
        seen.push(computed);
      }
      result.push(value);
    }
  }
  return result;
}

var _baseUniq = baseUniq;

/**
 * Creates an array of unique values, in order, from all given arrays using
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {...Array} [arrays] The arrays to inspect.
 * @returns {Array} Returns the new array of combined values.
 * @example
 *
 * _.union([2], [1, 2]);
 * // => [2, 1]
 */
var union = _baseRest(function(arrays) {
  return _baseUniq(_baseFlatten(arrays, 1, isArrayLikeObject_1, true));
});

var union_1 = union;

//

var script$1V = {
  name: 'MeScroll',
  components: {
    Spinner: __vue_component__$5,
    Checkbox: __vue_component__$l
  },
  props: {
    hasSelection: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: 10
    },
    provider: {
      type: Function,
      required: true
    },
    uniqueKey: {
      type: String,
      default: 'id'
    },
    selection: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      busy: false,
      page: 1,
      items: [],
      hits: null,
      padding: 30,
      currentSelection: this.selection.map(d => d[this.uniqueKey]),
      shouldSelectAll: false
    }
  },

  computed: {
    done() {
      return this.page > 1 && this.page > this.totalPages
    },

    totalPages() {
      return Math.ceil(this.hits / this.pageSize)
    }
  },

  watch: {
    selection(val) {
      this.currentSelection = val.map(d => d[this.uniqueKey]);
    },

    shouldSelectAll(value) {
      this.toggleAll(value);
    }
  },

  mounted() {
    this.scroll();
  },

  methods: {
    load() {
      if (!this.done && !this.busy) {
        this.busy = true;
        this.provider({
          page: this.page,
          pageSize: this.pageSize
        }, ({ hits, result }) => {
          this.hits = hits;
          this.items = concat_1(this.items, result);
          this.busy = false;
          this.page++;
          this.scroll();
        });
      }
    },

    scroll() {
      this.$nextTick(() => {
        if (!this.$refs.scroll) return

        const { scrollHeight, scrollTop, offsetHeight } = this.$refs.scroll;
        const distance = scrollHeight - (scrollTop + offsetHeight);
        if (distance <= this.padding) {
          this.load();
        }
      });
    },
    onScroll: debounce_1(function () {
      this.scroll();
    }, 100),

    refresh() {
      this.items = [];
      this.page = 1;
      this.load();
    },

    toggleAll(check) {
      this.items.forEach((item) => {
        item.checked = check;
      });

      let currentSelection = [];
      if (check) {
        currentSelection = union_1(this.currentSelection, this.items.map(d => d[this.uniqueKey]));
      }

      this.currentSelection = currentSelection;
      this.dispatchSelectedDebounce();
    },

    checkItem(item, checked) {
      const key = item[this.uniqueKey];
      const contains = this.currentSelection.includes(key);
      if (checked && !contains) {
        this.currentSelection.push(key);
      } else if (!checked && contains) {
        this.currentSelection.splice(this.currentSelection.indexOf(key), 1);
      }
      this.dispatchSelectedDebounce();
    },

    dispatchSelectedDebounce: debounce_1(function () {
      this.dispatchSelected();
    }, 500),

    dispatchSelected() {
      const selection = this.currentSelection.map((key) => {
        const item = {};
        item[this.uniqueKey] = key;
        return find_1(this.items, item) || item
      });

      this.$emit('selected', cloneDeep_1(selection));
    }
  }
};

/* script */
const __vue_script__$1V = script$1V;

/* template */
var __vue_render__$1V = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      ref: "scroll",
      staticClass: "me-scroll",
      attrs: { "data-cy": "scroll" },
      on: {
        "&scroll": function($event) {
          return _vm.onScroll($event)
        }
      }
    },
    [
      _vm.hasSelection
        ? _vm._t(
            "checkbox",
            [
              _c("Checkbox", {
                attrs: { "data-cy": "toggleAll" },
                model: {
                  value: _vm.shouldSelectAll,
                  callback: function($$v) {
                    _vm.shouldSelectAll = $$v;
                  },
                  expression: "shouldSelectAll"
                }
              })
            ],
            { dataCy: "has-selection" },
            { toggleAll: _vm.toggleAll }
          )
        : _vm._e(),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "me-scroll-content" },
        [
          _vm._l(_vm.items, function(item, index) {
            return _c(
              "div",
              { key: index, attrs: { "data-cy": "item" } },
              [
                _vm._t("item", null, {
                  item: item,
                  uniqueKey: item[_vm.uniqueKey],
                  selection: _vm.currentSelection,
                  checkItem: _vm.checkItem
                })
              ],
              2
            )
          }),
          _vm._v(" "),
          _vm.busy
            ? _vm._t("busy", [
                _c(
                  "div",
                  { staticClass: "loading", attrs: { "data-cy": "loading" } },
                  [
                    _c("Spinner", { staticClass: "align-middle" }),
                    _vm._v(" "),
                    _c("strong", { staticClass: "ml-2" }, [
                      _vm._v(_vm._s(_vm.$toolkit.t("spinner.loading")))
                    ])
                  ],
                  1
                )
              ])
            : _vm._e()
        ],
        2
      )
    ],
    2
  )
};
var __vue_staticRenderFns__$1V = [];
__vue_render__$1V._withStripped = true;

  /* style */
  const __vue_inject_styles__$1V = function (inject) {
    if (!inject) return
    inject("data-v-1ce66731_0", { source: "div.me-scroll[data-v-1ce66731] {\n  bottom: 0;\n  left: 0;\n  overflow: auto;\n  position: absolute;\n  right: 0;\n  top: 0;\n}\ndiv.me-scroll .loading[data-v-1ce66731] {\n  color: var(--me-primary-2);\n  display: flex;\n  justify-content: center;\n  margin-top: 1.5rem;\n  width: 100%;\n}\n\n/*# sourceMappingURL=Scroll.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Scroll.vue","Scroll.vue"],"names":[],"mappings":"AAmMA;EACA,SAAA;EACA,OAAA;EACA,cAAA;EACA,kBAAA;EACA,QAAA;EACA,MAAA;AClMA;ADoMA;EACA,0BAAA;EACA,aAAA;EACA,uBAAA;EACA,kBAAA;EACA,WAAA;AClMA;;AAEA,qCAAqC","file":"Scroll.vue","sourcesContent":["<template>\n  <div\n    ref=\"scroll\"\n    @scroll.passive=\"onScroll\"\n    class=\"me-scroll\"\n    data-cy=\"scroll\"\n  >\n    <slot\n      v-if=\"hasSelection\"\n      v-bind=\"{ toggleAll }\"\n      name=\"checkbox\"\n      data-cy=\"has-selection\"\n    >\n      <Checkbox v-model=\"shouldSelectAll\" data-cy=\"toggleAll\" />\n    </slot>\n\n    <div class=\"me-scroll-content\">\n      <div\n        v-for=\"(item, index) in items\"\n        :key=\"index\"\n        data-cy=\"item\"\n      >\n        <slot\n          :item=\"item\"\n          :uniqueKey=\"item[uniqueKey]\"\n          :selection=\"currentSelection\"\n          :check-item=\"checkItem\"\n          name=\"item\"\n        />\n      </div>\n      <slot v-if=\"busy\" name=\"busy\">\n        <div class=\"loading\" data-cy=\"loading\">\n          <Spinner class=\"align-middle\" />\n          <strong class=\"ml-2\">{{ $toolkit.t('spinner.loading') }}</strong>\n        </div>\n      </slot>\n    </div>\n  </div>\n</template>\n\n<script>\nimport concat from 'lodash/concat'\nimport debounce from 'lodash/debounce'\nimport find from 'lodash/find'\nimport cloneDeep from 'lodash/cloneDeep'\nimport union from 'lodash/union'\nimport Spinner from './Spinner.vue'\nimport Checkbox from './Checkbox.vue'\n\nexport default {\n  name: 'MeScroll',\n  components: {\n    Spinner,\n    Checkbox\n  },\n  props: {\n    hasSelection: {\n      type: Boolean,\n      default: false\n    },\n    pageSize: {\n      type: Number,\n      default: 10\n    },\n    provider: {\n      type: Function,\n      required: true\n    },\n    uniqueKey: {\n      type: String,\n      default: 'id'\n    },\n    selection: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      busy: false,\n      page: 1,\n      items: [],\n      hits: null,\n      padding: 30,\n      currentSelection: this.selection.map(d => d[this.uniqueKey]),\n      shouldSelectAll: false\n    }\n  },\n\n  computed: {\n    done() {\n      return this.page > 1 && this.page > this.totalPages\n    },\n\n    totalPages() {\n      return Math.ceil(this.hits / this.pageSize)\n    }\n  },\n\n  watch: {\n    selection(val) {\n      this.currentSelection = val.map(d => d[this.uniqueKey])\n    },\n\n    shouldSelectAll(value) {\n      this.toggleAll(value)\n    }\n  },\n\n  mounted() {\n    this.scroll()\n  },\n\n  methods: {\n    load() {\n      if (!this.done && !this.busy) {\n        this.busy = true\n        this.provider({\n          page: this.page,\n          pageSize: this.pageSize\n        }, ({ hits, result }) => {\n          this.hits = hits\n          this.items = concat(this.items, result)\n          this.busy = false\n          this.page++\n          this.scroll()\n        })\n      }\n    },\n\n    scroll() {\n      this.$nextTick(() => {\n        if (!this.$refs.scroll) return\n\n        const { scrollHeight, scrollTop, offsetHeight } = this.$refs.scroll\n        const distance = scrollHeight - (scrollTop + offsetHeight)\n        if (distance <= this.padding) {\n          this.load()\n        }\n      })\n    },\n    onScroll: debounce(function () {\n      this.scroll()\n    }, 100),\n\n    refresh() {\n      this.items = []\n      this.page = 1\n      this.load()\n    },\n\n    toggleAll(check) {\n      this.items.forEach((item) => {\n        item.checked = check\n      })\n\n      let currentSelection = []\n      if (check) {\n        currentSelection = union(this.currentSelection, this.items.map(d => d[this.uniqueKey]))\n      }\n\n      this.currentSelection = currentSelection\n      this.dispatchSelectedDebounce()\n    },\n\n    checkItem(item, checked) {\n      const key = item[this.uniqueKey]\n      const contains = this.currentSelection.includes(key)\n      if (checked && !contains) {\n        this.currentSelection.push(key)\n      } else if (!checked && contains) {\n        this.currentSelection.splice(this.currentSelection.indexOf(key), 1)\n      }\n      this.dispatchSelectedDebounce()\n    },\n\n    dispatchSelectedDebounce: debounce(function () {\n      this.dispatchSelected()\n    }, 500),\n\n    dispatchSelected() {\n      const selection = this.currentSelection.map((key) => {\n        const item = {}\n        item[this.uniqueKey] = key\n        return find(this.items, item) || item\n      })\n\n      this.$emit('selected', cloneDeep(selection))\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-scroll {\n    bottom: 0;\n    left: 0;\n    overflow: auto;\n    position: absolute;\n    right: 0;\n    top: 0;\n\n    .loading {\n      color: var(--me-primary-2);\n      display: flex;\n      justify-content: center;\n      margin-top: 1.5rem;\n      width: 100%;\n    }\n  }\n</style>\n","div.me-scroll {\n  bottom: 0;\n  left: 0;\n  overflow: auto;\n  position: absolute;\n  right: 0;\n  top: 0;\n}\ndiv.me-scroll .loading {\n  color: var(--me-primary-2);\n  display: flex;\n  justify-content: center;\n  margin-top: 1.5rem;\n  width: 100%;\n}\n\n/*# sourceMappingURL=Scroll.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1V = "data-v-1ce66731";
  /* module identifier */
  const __vue_module_identifier__$1V = undefined;
  /* functional template */
  const __vue_is_functional_template__$1V = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1V, staticRenderFns: __vue_staticRenderFns__$1V },
    __vue_inject_styles__$1V,
    __vue_script__$1V,
    __vue_scope_id__$1V,
    __vue_is_functional_template__$1V,
    __vue_module_identifier__$1V,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1W = {
  name: 'MeSearch',
  components: {
    Button: __vue_component__$6,
    Criteria: __vue_component__$O,
    TextInput: __vue_component__$p,
    TextInputMultiple: __vue_component__$L,
    CriteriaAdd: __vue_component__$Q,
    Icon: __vue_component__
  },
  props: {
    fields: {
      type: Array,
      required: true
    },
    term: {
      type: String,
      default: null
    },
    itemTermLength: {
      type: Number,
      default: 15
    },
    criteria: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: null
    },
    hideOperators: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    autoFlipSelect: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentTerm: this.multiple ? '' : this.term,
      items: this.term ? [this.term] : [],
      currentCriteria: cloneDeep_1(this.criteria),
      focused: false,
      filter: false,
      history: []
    }
  },
  computed: {
    hasFields() {
      return this.fields && this.fields.length > 0
    },
    hasHistory() {
      return this.history && this.history.length > 0
    },
    opened() {
      return this.focused && (this.filter || this.hasHistory)
    },
    hasTerm() {
      return this.currentTerm || this.items.length
    }
  },
  watch: {
    term(val) {
      if (this.multiple) {
        this.currentTerm = '';
      } else {
        this.currentTerm = val;
      }

      if (!val) {
        this.items = [];
      }
    },
    criteria(val) {
      this.currentCriteria = cloneDeep_1(val || []);
    },
    history(val) {
      localStorage.setItem('search.history', JSON.stringify(val));
      this.history = val;
    },
    currentCriteria(val) {
      this.currentCriteria = val;
      this.$emit('change', { newCriteria: val });
    }
  },
  created() {
    const data = localStorage.getItem('search.history');
    if (data) {
      this.history = JSON.parse(data);
    }
  },
  methods: {
    apply() {
      this.addHistory(this.currentTerm);
      this.close();
      this.$emit('apply', {
        term: this.currentTerm,
        criteria: this.currentCriteria
      });
    },
    applyMultSearch({ items, inserted }) {
      if (inserted) {
        this.addHistory(inserted);
      }
      this.items = items;
      this.close();
      this.$emit('apply', {
        term: [...new Set(items.join(' ').split(' '))].join(' '),
        criteria: this.currentCriteria
      });
    },
    close() {
      this.focused = false;
      this.filter = false;
    },
    showFilter() {
      this.filter = true;
      this.focused = true;
    },
    clearFilter() {
      this.currentCriteria = [];
    },
    addHistory(term) {
      if (term && !this.history.includes(term)) {
        this.history.push(term);
        if (this.history.length > 4) {
          this.history.shift();
        }
      }
    },
    removeHistory(index) {
      this.history.splice(index, 1);
    },
    applyHistory(term) {
      if (this.multiple) {
        this.items = [...new Set([...this.items, term])];
        this.applyMultSearch({ items: this.items, inserted: term });
      } else {
        this.currentTerm = term;
        this.apply();
      }
    },
    clearTerm() {
      this.currentTerm = '';
      this.items = [];
      if (this.multiple) {
        this.applyMultSearch({ items: [] });
      } else {
        this.apply();
      }
    },
    onCriteriaChange(criteria) {
      this.currentCriteria = criteria;
    }
  }
};

/* script */
const __vue_script__$1W = script$1W;

/* template */
var __vue_render__$1W = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    {
      staticClass: "me-search",
      class: { focused: _vm.focused, opened: _vm.opened },
      attrs: { "data-cy": "me-search" }
    },
    [
      _c("div", { staticClass: "search" }, [
        _c(
          "div",
          { staticClass: "term" },
          [
            _c("Icon", {
              attrs: { icon: "me-icon-l icon-magnifying-glass" },
              on: {
                click: function($event) {
                  !_vm.multiple
                    ? _vm.apply()
                    : _vm.applyMultSearch({
                        items: _vm.currentTerm
                          ? _vm.items.concat([_vm.currentTerm])
                          : _vm.items,
                        inserted: _vm.currentTerm
                      });
                }
              }
            }),
            _vm._v(" "),
            !_vm.multiple
              ? _c("TextInput", {
                  attrs: { placeholder: _vm.placeholder },
                  on: {
                    enter: _vm.apply,
                    focus: function($event) {
                      _vm.focused = true;
                    }
                  },
                  model: {
                    value: _vm.currentTerm,
                    callback: function($$v) {
                      _vm.currentTerm = $$v;
                    },
                    expression: "currentTerm"
                  }
                })
              : _c("TextInputMultiple", {
                  attrs: {
                    values: _vm.items,
                    placeholder: _vm.placeholder,
                    bordered: false,
                    "max-item-length": _vm.itemTermLength,
                    maxItems: 3,
                    variant: "primary",
                    autocomplete: "off"
                  },
                  on: {
                    itemAdded: _vm.applyMultSearch,
                    itemRemoved: _vm.applyMultSearch,
                    focus: function($event) {
                      _vm.focused = true;
                    }
                  },
                  model: {
                    value: _vm.currentTerm,
                    callback: function($$v) {
                      _vm.currentTerm = $$v;
                    },
                    expression: "currentTerm"
                  }
                })
          ],
          1
        ),
        _vm._v(" "),
        _c(
          "div",
          { staticClass: "actions", class: { criteria: _vm.hasFields } },
          [
            _vm.currentTerm
              ? _c(
                  "div",
                  {
                    staticClass: "clear-term",
                    attrs: { "data-cy": "clearTerm" },
                    on: { click: _vm.clearTerm }
                  },
                  [
                    _c("me-icon", {
                      attrs: {
                        size: 14,
                        icon: "me-icon-l icon-xmark",
                        variant: "primary"
                      }
                    })
                  ],
                  1
                )
              : _vm._e(),
            _vm._v(" "),
            _vm.hasFields
              ? _c(
                  "Button",
                  {
                    attrs: {
                      variant: "link",
                      size: "md",
                      "prepend-icon": "me-icon-l icon-filter"
                    },
                    on: { click: _vm.showFilter }
                  },
                  [
                    _vm._v(
                      "\n        " +
                        _vm._s(_vm.$toolkit.t("search.filters")) +
                        "\n      "
                    )
                  ]
                )
              : _vm._e()
          ],
          1
        )
      ]),
      _vm._v(" "),
      _vm.opened
        ? _c("div", { staticClass: "content" }, [
            !_vm.filter
              ? _c("div", { staticClass: "history" }, [
                  _vm.hasHistory
                    ? _c(
                        "ul",
                        [
                          _vm._l(_vm.history, function(item, index) {
                            return _c(
                              "li",
                              { key: index },
                              [
                                _c(
                                  "a",
                                  {
                                    on: {
                                      click: function($event) {
                                        return _vm.applyHistory(item)
                                      }
                                    }
                                  },
                                  [
                                    _c("Icon", {
                                      attrs: { icon: "me-icon icon-history" }
                                    }),
                                    _vm._v(
                                      " " + _vm._s(item) + "\n            "
                                    )
                                  ],
                                  1
                                ),
                                _vm._v(" "),
                                _c("Icon", {
                                  attrs: { icon: "me-icon icon-times" },
                                  on: {
                                    click: function($event) {
                                      return _vm.removeHistory(index)
                                    }
                                  }
                                })
                              ],
                              1
                            )
                          })
                        ],
                        2
                      )
                    : _vm._e()
                ])
              : _c(
                  "div",
                  { staticClass: "filter" },
                  [
                    _c("label", { staticClass: "label-title" }, [
                      _vm._v(
                        "\n        " +
                          _vm._s(_vm.$toolkit.t("search.filters")) +
                          "\n      "
                      )
                    ]),
                    _vm._v(" "),
                    _c("Criteria", {
                      staticClass: "mb-4",
                      attrs: {
                        fields: _vm.fields,
                        criteria: _vm.currentCriteria,
                        "hide-operators": _vm.hideOperators,
                        autoFlipSelect: _vm.autoFlipSelect,
                        inline: "",
                        size: "sm"
                      },
                      on: { change: _vm.onCriteriaChange },
                      scopedSlots: _vm._u(
                        [
                          {
                            key: "criteria-add",
                            fn: function(ref) {
                              var filteredFields = ref.fields;
                              var onAdd = ref.onAdd;
                              return [
                                _c("CriteriaAdd", {
                                  attrs: {
                                    fields: filteredFields,
                                    onAdd: onAdd
                                  }
                                })
                              ]
                            }
                          }
                        ],
                        null,
                        false,
                        672401155
                      )
                    }),
                    _vm._v(" "),
                    _c(
                      "div",
                      { staticClass: "footer" },
                      [
                        _c(
                          "Button",
                          {
                            attrs: { variant: "light-primary", size: "sm" },
                            on: { click: _vm.clearFilter }
                          },
                          [
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.$toolkit.t("search.reset")) +
                                "\n        "
                            )
                          ]
                        ),
                        _vm._v(" "),
                        _c(
                          "Button",
                          {
                            attrs: { variant: "primary", size: "sm" },
                            on: {
                              click: function($event) {
                                !_vm.multiple
                                  ? _vm.apply()
                                  : _vm.applyMultSearch({ items: _vm.items });
                              }
                            }
                          },
                          [
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.$toolkit.t("search.apply")) +
                                "\n        "
                            )
                          ]
                        )
                      ],
                      1
                    )
                  ],
                  1
                )
          ])
        : _vm._e(),
      _vm._v(" "),
      _c("div", { staticClass: "backdrop", on: { click: _vm.close } })
    ]
  )
};
var __vue_staticRenderFns__$1W = [];
__vue_render__$1W._withStripped = true;

  /* style */
  const __vue_inject_styles__$1W = function (inject) {
    if (!inject) return
    inject("data-v-d5d9da76_0", { source: "div.me-search[data-v-d5d9da76] {\n  height: 40px;\n  position: relative;\n  width: 100%;\n}\ndiv.me-search > div.search[data-v-d5d9da76] {\n  align-items: center;\n  background-color: var(--me-bg-gray);\n  border-radius: 8px;\n  border: 1px solid transparent;\n  display: flex;\n  height: 40px;\n  left: 0;\n  position: absolute;\n  right: 0;\n  top: 0;\n  width: 100%;\n  z-index: 1030;\n}\ndiv.me-search > div.search > div.term[data-v-d5d9da76] {\n  align-items: center;\n  display: flex;\n  flex-grow: 2;\n}\ndiv.me-search > div.search > div.term > i[data-v-d5d9da76] {\n  color: var(--me-gray-4);\n  cursor: pointer;\n  font-size: 16px;\n  padding-left: 16px;\n}\ndiv.me-search > div.search > div.term > input[data-v-d5d9da76], div.me-search > div.search > div.term[data-v-d5d9da76]  input,\ndiv.me-search > div.search > div.term > .input-multiple-container[data-v-d5d9da76] {\n  border: none;\n  background: none;\n  box-shadow: none;\n  height: 36px;\n  color: var(--me-gray-3);\n}\ndiv.me-search > div.search > div.term > input[data-v-d5d9da76]::placeholder, div.me-search > div.search > div.term[data-v-d5d9da76]  input::placeholder,\ndiv.me-search > div.search > div.term > .input-multiple-container[data-v-d5d9da76]::placeholder {\n  color: var(--me-gray-4);\n}\ndiv.me-search > div.search > div.term > input[data-v-d5d9da76]:focus::placeholder, div.me-search > div.search > div.term[data-v-d5d9da76]  input:focus::placeholder,\ndiv.me-search > div.search > div.term > .input-multiple-container[data-v-d5d9da76]:focus::placeholder {\n  color: transparent;\n}\ndiv.me-search > div.search > div.actions[data-v-d5d9da76] {\n  white-space: nowrap;\n  padding-right: 3px;\n}\ndiv.me-search > div.search > div.actions button.btn-link[data-v-d5d9da76] {\n  box-shadow: none;\n}\ndiv.me-search > div.search > div.actions button.btn-link[data-v-d5d9da76], div.me-search > div.search > div.actions button.btn-link i[data-v-d5d9da76] {\n  color: var(--me-primary-2);\n}\ndiv.me-search > div.search > div.actions .clear-term[data-v-d5d9da76] {\n  width: 32px;\n  height: 32px;\n  display: inline-flex;\n  justify-content: center;\n  align-items: center;\n  cursor: pointer;\n}\ndiv.me-search > div.content[data-v-d5d9da76] {\n  background-color: var(--me-white);\n  border-radius: 0.2rem;\n  box-shadow: var(--me-shadow-level-1);\n  display: none;\n  left: 0;\n  padding: 1rem;\n  position: absolute;\n  right: 0;\n  top: 40px;\n  z-index: 1030;\n}\ndiv.me-search > div.content > div.history > ul[data-v-d5d9da76] {\n  font-size: 13px;\n  list-style: none;\n  margin-bottom: 0;\n  padding: 0;\n}\ndiv.me-search > div.content > div.history > ul > li[data-v-d5d9da76] {\n  color: var(--me-gray-0);\n  line-height: 30px;\n  position: relative;\n}\ndiv.me-search > div.content > div.history > ul > li > a[data-v-d5d9da76] {\n  cursor: pointer;\n  display: block;\n  margin-right: 30px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\ndiv.me-search > div.content > div.history > ul > li > i.icon-times[data-v-d5d9da76] {\n  color: var(--me-gray-2);\n  cursor: pointer;\n  display: none;\n  position: absolute;\n  right: 10px;\n  top: 0;\n}\ndiv.me-search > div.content > div.history > ul > li[data-v-d5d9da76]:hover {\n  background-color: var(--me-bg-gray);\n}\ndiv.me-search > div.content > div.history > ul > li:hover > i.icon-times[data-v-d5d9da76] {\n  display: block;\n}\ndiv.me-search > div.content > div.history > ul[data-v-d5d9da76]:not(:last-child) {\n  border-bottom: 1px solid var(--me-gray-6);\n  margin-bottom: 1rem;\n  padding-bottom: 1rem;\n}\ndiv.me-search > div.content > div.filter[data-v-d5d9da76] {\n  padding: 0 10px;\n}\ndiv.me-search > div.content > div.filter .label-title[data-v-d5d9da76] {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  line-height: 16px;\n}\ndiv.me-search > div.content > div.filter > div.me-criteria[data-v-d5d9da76] {\n  margin-bottom: 1rem;\n}\ndiv.me-search > div.content > div.filter > div.footer[data-v-d5d9da76] {\n  border-top: 1px solid var(--me-gray-6);\n  margin: -1rem;\n  padding: 1rem;\n  text-align: right;\n}\ndiv.me-search > div.backdrop[data-v-d5d9da76] {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1040;\n}\ndiv.me-search.focused div.search[data-v-d5d9da76],\ndiv.me-search.focused div.content[data-v-d5d9da76] {\n  z-index: 1050;\n}\ndiv.me-search.focused > div.backdrop[data-v-d5d9da76] {\n  display: block;\n  opacity: 0.2;\n}\ndiv.me-search.opened > div.search[data-v-d5d9da76] {\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 0;\n}\ndiv.me-search.opened > div.content[data-v-d5d9da76] {\n  border-top-left-radius: 0;\n  border-top-right-radius: 0;\n  display: block;\n}\n\n/*# sourceMappingURL=Search.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Search.vue","Search.vue"],"names":[],"mappings":"AAkSA;EACA,YAAA;EACA,kBAAA;EACA,WAAA;ACjSA;ADmSA;EACA,mBAAA;EACA,mCAAA;EACA,kBAAA;EACA,6BAAA;EACA,aAAA;EACA,YAAA;EACA,OAAA;EACA,kBAAA;EACA,QAAA;EACA,MAAA;EACA,WAAA;EACA,aArBA;AC5QA;ADmSA;EACA,mBAAA;EACA,aAAA;EACA,YAAA;ACjSA;ADmSA;EACA,uBAAA;EACA,eAAA;EACA,eAAA;EACA,kBAAA;ACjSA;ADoSA;;EAEA,YAAA;EACA,gBAAA;EACA,gBAAA;EACA,YAAA;EACA,uBAAA;AClSA;ADoSA;;EACA,uBAAA;ACjSA;ADoSA;;EACA,kBAAA;ACjSA;ADsSA;EACA,mBAAA;EACA,kBAAA;ACpSA;ADsSA;EACA,gBAAA;ACpSA;ADsSA;EACA,0BAAA;ACpSA;ADwSA;EACA,WAAA;EACA,YAAA;EACA,oBAAA;EACA,uBAAA;EACA,mBAAA;EACA,eAAA;ACtSA;AD2SA;EACA,iCAAA;EACA,qBAAA;EACA,oCAAA;EACA,aAAA;EACA,OAAA;EACA,aAAA;EACA,kBAAA;EACA,QAAA;EACA,SAAA;EACA,aAtFA;ACnNA;AD4SA;EACA,eAAA;EACA,gBAAA;EACA,gBAAA;EACA,UAAA;AC1SA;AD4SA;EACA,uBAAA;EACA,iBAAA;EACA,kBAAA;AC1SA;AD4SA;EACA,eAAA;EACA,cAAA;EACA,kBAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;AC1SA;AD6SA;EACA,uBAAA;EACA,eAAA;EACA,aAAA;EACA,kBAAA;EACA,WAAA;EACA,MAAA;AC3SA;AD8SA;EACA,mCAAA;AC5SA;AD8SA;EACA,cAAA;AC5SA;ADiTA;EACA,yCAAA;EACA,mBAAA;EACA,oBAAA;AC/SA;ADoTA;EACA,eAAA;AClTA;ADoTA;EACA,uBAAA;EACA,eAAA;EACA,iBAAA;AClTA;ADqTA;EACA,mBAAA;ACnTA;ADsTA;EACA,sCAAA;EACA,aAAA;EACA,aAAA;EACA,iBAAA;ACpTA;ADyTA;EACA,kCAAA;EACA,SAAA;EACA,aAAA;EACA,OAAA;EACA,UAAA;EACA,eAAA;EACA,QAAA;EACA,MAAA;EACA,yBAAA;EACA,mBAAA;EACA,aAvKA;AChJA;AD2TA;;EAEA,aA5KA;AC7IA;AD4TA;EACA,cAAA;EACA,YAAA;AC1TA;AD+TA;EACA,4BAAA;EACA,6BAAA;AC7TA;ADgUA;EACA,yBAAA;EACA,0BAAA;EACA,cAAA;AC9TA;;AAEA,qCAAqC","file":"Search.vue","sourcesContent":["<template>\n  <div\n    :class=\"{ focused: focused, opened: opened }\"\n    class=\"me-search\"\n    data-cy=\"me-search\"\n  >\n    <div class=\"search\">\n      <div class=\"term\">\n        <Icon\n          @click=\"!multiple ? apply() :\n            applyMultSearch({items: currentTerm ? [...items, currentTerm] : items, inserted: currentTerm})\"\n          icon=\"me-icon-l icon-magnifying-glass\"\n        />\n        <TextInput\n          v-if=\"!multiple\"\n          v-model=\"currentTerm\"\n          :placeholder=\"placeholder\"\n          @enter=\"apply\"\n          @focus=\"focused = true\"\n        />\n        <TextInputMultiple\n          v-else\n          v-model=\"currentTerm\"\n          :values=\"items\"\n          :placeholder=\"placeholder\"\n          :bordered=\"false\"\n          :max-item-length=\"itemTermLength\"\n          :maxItems=\"3\"\n          @itemAdded=\"applyMultSearch\"\n          @itemRemoved=\"applyMultSearch\"\n          @focus=\"focused = true\"\n          variant=\"primary\"\n          autocomplete=\"off\"\n        />\n      </div>\n      <div :class=\"{ criteria: hasFields }\" class=\"actions\">\n        <div\n          v-if=\"currentTerm\"\n          @click=\"clearTerm\"\n          class=\"clear-term\"\n          data-cy=\"clearTerm\"\n        >\n          <me-icon\n            :size=\"14\"\n            icon=\"me-icon-l icon-xmark\"\n            variant=\"primary\"\n          />\n        </div>\n\n        <Button\n          v-if=\"hasFields\"\n          @click=\"showFilter\"\n          variant=\"link\"\n          size=\"md\"\n          prepend-icon=\"me-icon-l icon-filter\"\n        >\n          {{ $toolkit.t('search.filters') }}\n        </Button>\n      </div>\n    </div>\n\n    <div v-if=\"opened\" class=\"content\">\n      <div v-if=\"!filter\" class=\"history\">\n        <ul v-if=\"hasHistory\">\n          <template>\n            <li v-for=\"(item, index) in history\" :key=\"index\">\n              <a @click=\"applyHistory(item)\">\n                <Icon icon=\"me-icon icon-history\" /> {{ item }}\n              </a>\n              <Icon @click=\"removeHistory(index)\" icon=\"me-icon icon-times\" />\n            </li>\n          </template>\n        </ul>\n      </div>\n      <div v-else class=\"filter\">\n        <label class=\"label-title\">\n          {{ $toolkit.t('search.filters') }}\n        </label>\n\n        <Criteria\n          :fields=\"fields\"\n          :criteria=\"currentCriteria\"\n          :hide-operators=\"hideOperators\"\n          @change=\"onCriteriaChange\"\n          :autoFlipSelect=\"autoFlipSelect\"\n          inline\n          size=\"sm\"\n          class=\"mb-4\"\n        >\n          <template v-slot:criteria-add=\"{fields: filteredFields, onAdd}\">\n            <CriteriaAdd\n              :fields=\"filteredFields\"\n              :onAdd=\"onAdd\"\n            />\n          </template>\n        </Criteria>\n\n        <div class=\"footer\">\n          <Button @click=\"clearFilter\" variant=\"light-primary\" size=\"sm\">\n            {{ $toolkit.t('search.reset') }}\n          </Button>\n          <Button @click=\"!multiple ? apply() : applyMultSearch({ items })\" variant=\"primary\" size=\"sm\">\n            {{ $toolkit.t('search.apply') }}\n          </Button>\n        </div>\n      </div>\n    </div>\n    <div @click=\"close\" class=\"backdrop\" />\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport Button from './Button.vue'\nimport Criteria from './Criteria.vue'\nimport TextInput from './TextInput.vue'\nimport CriteriaAdd from './CriteriaAdd.vue'\nimport TextInputMultiple from './inputMultiple/TextInputMultiple.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeSearch',\n  components: {\n    Button,\n    Criteria,\n    TextInput,\n    TextInputMultiple,\n    CriteriaAdd,\n    Icon\n  },\n  props: {\n    fields: {\n      type: Array,\n      required: true\n    },\n    term: {\n      type: String,\n      default: null\n    },\n    itemTermLength: {\n      type: Number,\n      default: 15\n    },\n    criteria: {\n      type: Array,\n      required: true\n    },\n    placeholder: {\n      type: String,\n      default: null\n    },\n    hideOperators: {\n      type: Boolean,\n      default: false\n    },\n    multiple: {\n      type: Boolean,\n      default: false\n    },\n    autoFlipSelect: {\n      type: Boolean,\n      default: true\n    }\n  },\n  data() {\n    return {\n      currentTerm: this.multiple ? '' : this.term,\n      items: this.term ? [this.term] : [],\n      currentCriteria: cloneDeep(this.criteria),\n      focused: false,\n      filter: false,\n      history: []\n    }\n  },\n  computed: {\n    hasFields() {\n      return this.fields && this.fields.length > 0\n    },\n    hasHistory() {\n      return this.history && this.history.length > 0\n    },\n    opened() {\n      return this.focused && (this.filter || this.hasHistory)\n    },\n    hasTerm() {\n      return this.currentTerm || this.items.length\n    }\n  },\n  watch: {\n    term(val) {\n      if (this.multiple) {\n        this.currentTerm = ''\n      } else {\n        this.currentTerm = val\n      }\n\n      if (!val) {\n        this.items = []\n      }\n    },\n    criteria(val) {\n      this.currentCriteria = cloneDeep(val || [])\n    },\n    history(val) {\n      localStorage.setItem('search.history', JSON.stringify(val))\n      this.history = val\n    },\n    currentCriteria(val) {\n      this.currentCriteria = val\n      this.$emit('change', { newCriteria: val })\n    }\n  },\n  created() {\n    const data = localStorage.getItem('search.history')\n    if (data) {\n      this.history = JSON.parse(data)\n    }\n  },\n  methods: {\n    apply() {\n      this.addHistory(this.currentTerm)\n      this.close()\n      this.$emit('apply', {\n        term: this.currentTerm,\n        criteria: this.currentCriteria\n      })\n    },\n    applyMultSearch({ items, inserted }) {\n      if (inserted) {\n        this.addHistory(inserted)\n      }\n      this.items = items\n      this.close()\n      this.$emit('apply', {\n        term: [...new Set(items.join(' ').split(' '))].join(' '),\n        criteria: this.currentCriteria\n      })\n    },\n    close() {\n      this.focused = false\n      this.filter = false\n    },\n    showFilter() {\n      this.filter = true\n      this.focused = true\n    },\n    clearFilter() {\n      this.currentCriteria = []\n    },\n    addHistory(term) {\n      if (term && !this.history.includes(term)) {\n        this.history.push(term)\n        if (this.history.length > 4) {\n          this.history.shift()\n        }\n      }\n    },\n    removeHistory(index) {\n      this.history.splice(index, 1)\n    },\n    applyHistory(term) {\n      if (this.multiple) {\n        this.items = [...new Set([...this.items, term])]\n        this.applyMultSearch({ items: this.items, inserted: term })\n      } else {\n        this.currentTerm = term\n        this.apply()\n      }\n    },\n    clearTerm() {\n      this.currentTerm = ''\n      this.items = []\n      if (this.multiple) {\n        this.applyMultSearch({ items: [] })\n      } else {\n        this.apply()\n      }\n    },\n    onCriteriaChange(criteria) {\n      this.currentCriteria = criteria\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n$zIndexSearch: 1030;\n$zIndexBackdrop: $zIndexSearch + 10;\n$zIndexSearchFocused: $zIndexBackdrop + 10;\n\n  div.me-search {\n    height: 40px;\n    position: relative;\n    width: 100%;\n\n    > div.search {\n      align-items: center;\n      background-color: var(--me-bg-gray);\n      border-radius: 8px;\n      border: 1px solid transparent;\n      display: flex;\n      height: 40px;\n      left: 0;\n      position: absolute;\n      right: 0;\n      top: 0;\n      width: 100%;\n      z-index: $zIndexSearch;\n\n      > div.term {\n        align-items: center;\n        display: flex;\n        flex-grow: 2;\n\n        > i {\n          color: var(--me-gray-4);\n          cursor: pointer;\n          font-size: 16px;\n          padding-left: 16px;\n        }\n\n        > input, ::v-deep input,\n        > .input-multiple-container {\n          border: none;\n          background: none;\n          box-shadow: none;\n          height: 36px;\n          color: var(--me-gray-3);\n\n          &::placeholder {\n            color: var(--me-gray-4);\n          }\n\n          &:focus::placeholder {\n            color:transparent;\n          }\n        }\n      }\n\n      > div.actions {\n        white-space: nowrap;\n        padding-right: 3px;\n\n        button.btn-link {\n          box-shadow: none;\n\n          &, i {\n            color: var(--me-primary-2);\n          }\n        }\n\n        .clear-term {\n          width: 32px;\n          height: 32px;\n          display: inline-flex;\n          justify-content: center;\n          align-items: center;\n          cursor: pointer;\n        }\n      }\n    }\n\n    > div.content {\n      background-color: var(--me-white);\n      border-radius: 0.2rem;\n      box-shadow: var(--me-shadow-level-1);\n      display: none;\n      left: 0;\n      padding: 1rem;\n      position: absolute;\n      right: 0;\n      top: 40px;\n      z-index: $zIndexSearch;\n\n      > div.history {\n        > ul {\n          font-size: 13px;\n          list-style: none;\n          margin-bottom: 0;\n          padding: 0;\n\n          > li {\n            color: var(--me-gray-0);\n            line-height: 30px;\n            position: relative;\n\n            > a {\n              cursor: pointer;\n              display: block;\n              margin-right: 30px;\n              overflow: hidden;\n              text-overflow: ellipsis;\n              white-space: nowrap;\n            }\n\n            > i.icon-times {\n              color: var(--me-gray-2);\n              cursor: pointer;\n              display: none;\n              position: absolute;\n              right: 10px;\n              top: 0;\n            }\n\n            &:hover {\n              background-color: var(--me-bg-gray);\n\n              > i.icon-times {\n                display: block;\n              }\n            }\n          }\n\n          &:not(:last-child) {\n            border-bottom: 1px solid var(--me-gray-6);\n            margin-bottom: 1rem;\n            padding-bottom: 1rem;\n          }\n        }\n      }\n\n      > div.filter {\n        padding: 0 10px;\n\n        .label-title {\n          color: var(--me-gray-4);\n          font-size: 12px;\n          line-height: 16px;\n        }\n\n        > div.me-criteria {\n          margin-bottom: 1rem;\n        }\n\n        > div.footer {\n          border-top: 1px solid var(--me-gray-6);\n          margin: -1rem;\n          padding: 1rem;\n          text-align: right;\n        }\n      }\n    }\n\n    > div.backdrop {\n      background-color: var(--me-gray-0);\n      bottom: 0;\n      display: none;\n      left: 0;\n      opacity: 0;\n      position: fixed;\n      right: 0;\n      top: 0;\n      transition-duration: 0.2s;\n      transition: opacity;\n      z-index: $zIndexBackdrop;\n    }\n\n    &.focused {\n      div.search,\n      div.content {\n        z-index: $zIndexSearchFocused;\n      }\n\n      > div.backdrop {\n        display: block;\n        opacity: 0.2;\n      }\n    }\n\n    &.opened {\n      > div.search {\n        border-bottom-left-radius: 0;\n        border-bottom-right-radius: 0;\n      }\n\n      > div.content {\n        border-top-left-radius: 0;\n        border-top-right-radius: 0;\n        display: block;\n      }\n    }\n  }\n</style>\n","div.me-search {\n  height: 40px;\n  position: relative;\n  width: 100%;\n}\ndiv.me-search > div.search {\n  align-items: center;\n  background-color: var(--me-bg-gray);\n  border-radius: 8px;\n  border: 1px solid transparent;\n  display: flex;\n  height: 40px;\n  left: 0;\n  position: absolute;\n  right: 0;\n  top: 0;\n  width: 100%;\n  z-index: 1030;\n}\ndiv.me-search > div.search > div.term {\n  align-items: center;\n  display: flex;\n  flex-grow: 2;\n}\ndiv.me-search > div.search > div.term > i {\n  color: var(--me-gray-4);\n  cursor: pointer;\n  font-size: 16px;\n  padding-left: 16px;\n}\ndiv.me-search > div.search > div.term > input, div.me-search > div.search > div.term ::v-deep input,\ndiv.me-search > div.search > div.term > .input-multiple-container {\n  border: none;\n  background: none;\n  box-shadow: none;\n  height: 36px;\n  color: var(--me-gray-3);\n}\ndiv.me-search > div.search > div.term > input::placeholder, div.me-search > div.search > div.term ::v-deep input::placeholder,\ndiv.me-search > div.search > div.term > .input-multiple-container::placeholder {\n  color: var(--me-gray-4);\n}\ndiv.me-search > div.search > div.term > input:focus::placeholder, div.me-search > div.search > div.term ::v-deep input:focus::placeholder,\ndiv.me-search > div.search > div.term > .input-multiple-container:focus::placeholder {\n  color: transparent;\n}\ndiv.me-search > div.search > div.actions {\n  white-space: nowrap;\n  padding-right: 3px;\n}\ndiv.me-search > div.search > div.actions button.btn-link {\n  box-shadow: none;\n}\ndiv.me-search > div.search > div.actions button.btn-link, div.me-search > div.search > div.actions button.btn-link i {\n  color: var(--me-primary-2);\n}\ndiv.me-search > div.search > div.actions .clear-term {\n  width: 32px;\n  height: 32px;\n  display: inline-flex;\n  justify-content: center;\n  align-items: center;\n  cursor: pointer;\n}\ndiv.me-search > div.content {\n  background-color: var(--me-white);\n  border-radius: 0.2rem;\n  box-shadow: var(--me-shadow-level-1);\n  display: none;\n  left: 0;\n  padding: 1rem;\n  position: absolute;\n  right: 0;\n  top: 40px;\n  z-index: 1030;\n}\ndiv.me-search > div.content > div.history > ul {\n  font-size: 13px;\n  list-style: none;\n  margin-bottom: 0;\n  padding: 0;\n}\ndiv.me-search > div.content > div.history > ul > li {\n  color: var(--me-gray-0);\n  line-height: 30px;\n  position: relative;\n}\ndiv.me-search > div.content > div.history > ul > li > a {\n  cursor: pointer;\n  display: block;\n  margin-right: 30px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\ndiv.me-search > div.content > div.history > ul > li > i.icon-times {\n  color: var(--me-gray-2);\n  cursor: pointer;\n  display: none;\n  position: absolute;\n  right: 10px;\n  top: 0;\n}\ndiv.me-search > div.content > div.history > ul > li:hover {\n  background-color: var(--me-bg-gray);\n}\ndiv.me-search > div.content > div.history > ul > li:hover > i.icon-times {\n  display: block;\n}\ndiv.me-search > div.content > div.history > ul:not(:last-child) {\n  border-bottom: 1px solid var(--me-gray-6);\n  margin-bottom: 1rem;\n  padding-bottom: 1rem;\n}\ndiv.me-search > div.content > div.filter {\n  padding: 0 10px;\n}\ndiv.me-search > div.content > div.filter .label-title {\n  color: var(--me-gray-4);\n  font-size: 12px;\n  line-height: 16px;\n}\ndiv.me-search > div.content > div.filter > div.me-criteria {\n  margin-bottom: 1rem;\n}\ndiv.me-search > div.content > div.filter > div.footer {\n  border-top: 1px solid var(--me-gray-6);\n  margin: -1rem;\n  padding: 1rem;\n  text-align: right;\n}\ndiv.me-search > div.backdrop {\n  background-color: var(--me-gray-0);\n  bottom: 0;\n  display: none;\n  left: 0;\n  opacity: 0;\n  position: fixed;\n  right: 0;\n  top: 0;\n  transition-duration: 0.2s;\n  transition: opacity;\n  z-index: 1040;\n}\ndiv.me-search.focused div.search,\ndiv.me-search.focused div.content {\n  z-index: 1050;\n}\ndiv.me-search.focused > div.backdrop {\n  display: block;\n  opacity: 0.2;\n}\ndiv.me-search.opened > div.search {\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 0;\n}\ndiv.me-search.opened > div.content {\n  border-top-left-radius: 0;\n  border-top-right-radius: 0;\n  display: block;\n}\n\n/*# sourceMappingURL=Search.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1W = "data-v-d5d9da76";
  /* module identifier */
  const __vue_module_identifier__$1W = undefined;
  /* functional template */
  const __vue_is_functional_template__$1W = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1W, staticRenderFns: __vue_staticRenderFns__$1W },
    __vue_inject_styles__$1W,
    __vue_script__$1W,
    __vue_scope_id__$1W,
    __vue_is_functional_template__$1W,
    __vue_module_identifier__$1W,
    false,
    createInjector,
    undefined,
    undefined
  );

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$6 = Math.max,
    nativeMin$3 = Math.min;

/**
 * This method is like `_.findIndex` except that it iterates over elements
 * of `collection` from right to left.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=array.length-1] The index to search from.
 * @returns {number} Returns the index of the found element, else `-1`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'active': true },
 *   { 'user': 'fred',    'active': false },
 *   { 'user': 'pebbles', 'active': false }
 * ];
 *
 * _.findLastIndex(users, function(o) { return o.user == 'pebbles'; });
 * // => 2
 *
 * // The `_.matches` iteratee shorthand.
 * _.findLastIndex(users, { 'user': 'barney', 'active': true });
 * // => 0
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.findLastIndex(users, ['active', false]);
 * // => 2
 *
 * // The `_.property` iteratee shorthand.
 * _.findLastIndex(users, 'active');
 * // => 0
 */
function findLastIndex(array, predicate, fromIndex) {
  var length = array == null ? 0 : array.length;
  if (!length) {
    return -1;
  }
  var index = length - 1;
  if (fromIndex !== undefined) {
    index = toInteger_1(fromIndex);
    index = fromIndex < 0
      ? nativeMax$6(length + index, 0)
      : nativeMin$3(index, length - 1);
  }
  return _baseFindIndex(array, _baseIteratee(predicate), index, true);
}

var findLastIndex_1 = findLastIndex;

/**
 * This method is like `_.find` except that it iterates over elements of
 * `collection` from right to left.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Collection
 * @param {Array|Object} collection The collection to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=collection.length-1] The index to search from.
 * @returns {*} Returns the matched element, else `undefined`.
 * @example
 *
 * _.findLast([1, 2, 3, 4], function(n) {
 *   return n % 2 == 1;
 * });
 * // => 3
 */
var findLast = _createFind(findLastIndex_1);

var findLast_1 = findLast;

//

var script$1X = {
  name: 'MeSectionTree',

  components: {
    Spinner: __vue_component__$5,
    Icon: __vue_component__
  },

  props: {
    sections: {
      type: Array,
      required: true
    },
    searchPlaceholder: {
      type: String,
      default: ''
    },
    searchProvider: {
      type: Function,
      default: null
    },
    listProvider: {
      type: Function,
      default: null
    },
    searchWithPagination: {
      type: Boolean,
      default: false
    },
    searchPageSize: {
      type: Number,
      default: 10
    },
    selectedItems: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      searchText: '',
      currentSections: this.sections.map(section => this.addMissingParams(section)),
      currentSelectedItems: [...this.selectedItems],
      loadingSearchProvider: false,
      searchResults: [],
      pagination: {
        busy: false,
        page: 1,
        hits: null,
        isLastPage: false
      }
    }
  },

  computed: {
    shouldSearch() {
      return this.searchText?.length > 2
    }
  },

  watch: {
    sections(val) {
      this.currentSections = val.map(section => this.addMissingParams(section));
    },

    selectedItems(val) {
      this.currentSelectedItems = [...val];
      this.currentSections.forEach((section) => {
        if (section.items) {
          section.items.forEach((item) => {
            item.selected = this.isItemSelected(item);
          });
        }
      });
    }
  },

  created() {
    this.currentSections[0].active = true;
  },

  methods: {
    addMissingParams(section) {
      return {
        ...section,
        shouldShowSearch: false,
        active: false,
        selectable: section.selectable !== false,
        loading: false,
        searchText: ''
      }
    },

    isItemSelected(item) {
      return this.currentSelectedItems.some(selectedItem => selectedItem.value === item.value)
    },

    itemHasChildren(item) {
      return item.hasChildren || (item.children && item.children.length)
    },

    showResults() {
      this.pagination = {
        busy: false,
        page: 1,
        hits: null,
        isLastPage: false
      };
      this.filter = true;
      this.loadingSearchProvider = true;
      this.searchResults = [];
      this.callSearchProvider();
    },
    callSearchProvider: debounce_1(function () {
      if (this.shouldSearch) {
        this.searchProvider(this.searchText, ({ results, hits }) => {
          this.loadingSearchProvider = false;
          if (this.searchWithPagination) {
            this.searchResults = concat_1(this.searchResults, results);
            this.pagination.hits = hits;
            this.pagination.page++;
            this.pagination.isLastPage = this.pagination.page > Math.ceil(hits / this.searchPageSize);
            this.pagination.busy = false;
          } else {
            this.searchResults = results;
          }
        }, this.pagination.page, this.searchPageSize);
      }
    }, 500),

    isFirstInactive(index) {
      const lastActive = findLast_1(this.currentSections, section => section.active);

      const lastActiveIndex = this.currentSections.indexOf(lastActive);
      return index === lastActiveIndex + 1
    },

    showSearch(section, index) {
      section.shouldShowSearch = true;
      this.$nextTick(() => {
        document.querySelector(`.section-search-input-${index} input`).focus();
      });
    },

    hideSearch(section) {
      if (!section.searchText) {
        section.shouldShowSearch = false;
      }
    },

    filteredItems(section) {
      if (section.searchText) {
        return section.items.filter(item => item.label.toLowerCase().includes(section.searchText.toLowerCase()))
      }
      return section.items
    },

    openChild(item, index) {
      this.currentSections[index].items
        .filter(sectionItem => sectionItem.opened)
        .forEach((sectionItem) => { sectionItem.opened = false; });

      item.opened = true;

      this.currentSections
        .filter((section, i) => { return section.active && i > index })
        .forEach((section) => {
          section.active = false;
          section.items = [];
        });

      this.currentSections[index + 1].items = [];
      this.currentSections[index + 1].active = true;
      if (this.listProvider) {
        this.currentSections[index + 1].loading = true;
        this.listProvider(item, ({ items }) => {
          this.currentSections[index + 1].items = items;
          this.currentSections[index + 1].loading = false;
          this.focusNextDiv(index + 1);
        });
      } else {
        this.currentSections[index + 1].items = item.children;
        this.focusNextDiv(index + 1);
      }
    },

    focusNextDiv(index) {
      const sectionDiv = document.querySelector(`#section-div-${index}`);
      if (sectionDiv) {
        sectionDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },

    itemSelected($event, item) {
      item.selected = $event;
      if ($event) {
        item.parents = [];
        const previousParents = [];
        this.currentSections
          .filter(section => section.active)
          .every((section) => {
            if (section.items.some(sectionItem => sectionItem.value === item.value)) {
              return false
            }
            const openedItem = section.items.find(sectionItem => sectionItem.opened);
            if (openedItem) {
              openedItem.parents = [...previousParents];
              openedItem.selected = section.selectable;
              item.parents.push(cloneDeep_1(openedItem));
              previousParents.push(cloneDeep_1(openedItem));
              if (!this.isItemSelected(openedItem) && section.selectable) {
                this.currentSelectedItems.push(openedItem);
              }
            }
            return true
          });
        this.currentSelectedItems.push(item);
      } else {
        this.removeItemAndChildrens(item);
      }
      this.$emit('change', this.currentSelectedItems);
      this.$forceUpdate();
    },

    itemOnSearchSelected($event, item) {
      if ($event) {
        if (!this.isItemSelected(item)) {
          this.currentSelectedItems.push(item);
          this.setItemStatusOnSections(item, true);
        }
        item.parents.forEach((parent) => {
          if (!this.isItemSelected(parent)) {
            this.currentSelectedItems.push(parent);
            this.setItemStatusOnSections(parent, true);
          }
        });
      } else {
        this.removeItemAndChildrens(item);
        this.setItemStatusOnSections(item, false);
      }
      this.$emit('change', this.currentSelectedItems);
    },

    removeItemAndChildrens(item) {
      const itemsToRemove = this.currentSelectedItems
        .filter(selectedItem => selectedItem.value === item.value || selectedItem.parents?.some(parent => parent.value === item.value));
      itemsToRemove.forEach((itemToRemove) => {
        const index = this.currentSelectedItems.indexOf(itemToRemove);
        this.currentSelectedItems.splice(index, 1);
        this.setItemStatusOnSections(itemToRemove, false);
      });
      this.$forceUpdate();
    },

    setItemStatusOnSections(item, value) {
      this.currentSections
        .filter(section => section.active)
        .forEach((section) => {
          const selectedItem = section.items.find(sectionItem => sectionItem.value === item.value);
          if (selectedItem) {
            selectedItem.selected = value;
          }
        });
      this.$forceUpdate();
    },

    scroll() {
      const { scrollHeight, scrollTop, offsetHeight } = this.$refs.results;
      const distance = scrollHeight - (scrollTop + offsetHeight);
      if (distance <= 30 && this.searchWithPagination && !this.pagination.busy && !this.pagination.isLastPage) {
        this.pagination.busy = true;
        this.callSearchProvider();
      }
    },

    onScroll: debounce_1(function () {
      this.scroll();
    }, 500)
  }
};

/* script */
const __vue_script__$1X = script$1X;

/* template */
var __vue_render__$1X = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-section-tree" }, [
    _vm.searchProvider
      ? _c(
          "div",
          { staticClass: "section-quick-search" },
          [
            _c("MeTextInput", {
              attrs: { placeholder: _vm.searchPlaceholder, size: "lg" },
              on: { input: _vm.showResults, enter: _vm.showResults },
              model: {
                value: _vm.searchText,
                callback: function($$v) {
                  _vm.searchText = $$v;
                },
                expression: "searchText"
              }
            }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "section-quick-search__actions" },
              [
                !_vm.shouldSearch
                  ? _c("Icon", {
                      attrs: { icon: "me-icon icon-search" },
                      on: { click: _vm.showResults }
                    })
                  : _vm._e(),
                _vm._v(" "),
                _vm.shouldSearch
                  ? _c("Icon", {
                      attrs: { icon: "me-icon icon-times" },
                      on: {
                        click: function($event) {
                          _vm.searchText = "";
                        }
                      }
                    })
                  : _vm._e()
              ],
              1
            ),
            _vm._v(" "),
            _vm.shouldSearch
              ? _c("div", { staticClass: "content" }, [
                  _c(
                    "div",
                    {
                      ref: "results",
                      staticClass: "results",
                      class: { loading: _vm.loadingSearchProvider },
                      on: {
                        "&scroll": function($event) {
                          return _vm.onScroll($event)
                        }
                      }
                    },
                    [
                      _vm.loadingSearchProvider
                        ? _c(
                            "span",
                            [
                              _c("Spinner", {
                                staticClass: "align-middle",
                                attrs: { small: "" }
                              }),
                              _vm._v(
                                "\n          " +
                                  _vm._s(_vm.$toolkit.t("spinner.loading")) +
                                  "\n        "
                              )
                            ],
                            1
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      !_vm.loadingSearchProvider && !_vm.searchResults.length
                        ? _c("span", [
                            _vm._v(
                              "\n          " +
                                _vm._s(_vm.$toolkit.t("grid.result.empty")) +
                                "\n        "
                            )
                          ])
                        : _c(
                            "div",
                            [
                              _vm._l(_vm.searchResults, function(item) {
                                return _c(
                                  "div",
                                  {
                                    key: item.value,
                                    staticClass: "search-result"
                                  },
                                  [
                                    _c("me-checkbox", {
                                      attrs: { disabled: item.disabled },
                                      on: {
                                        change: function($event) {
                                          return _vm.itemOnSearchSelected(
                                            $event,
                                            item
                                          )
                                        }
                                      }
                                    }),
                                    _vm._v(" "),
                                    _c("div", { staticClass: "search-text" }, [
                                      item.parents && item.parents.length
                                        ? _c(
                                            "div",
                                            { staticClass: "search-path" },
                                            _vm._l(item.parents, function(
                                              parent,
                                              index
                                            ) {
                                              return _c(
                                                "div",
                                                {
                                                  key: index,
                                                  staticClass:
                                                    "search-path-parent"
                                                },
                                                [
                                                  _vm._v(
                                                    "\n                  " +
                                                      _vm._s(parent.name) +
                                                      "\n                  "
                                                  ),
                                                  _c("i", {
                                                    staticClass:
                                                      "me-icon icon-chevron-right"
                                                  })
                                                ]
                                              )
                                            }),
                                            0
                                          )
                                        : _vm._e(),
                                      _vm._v(" "),
                                      _c("span", {
                                        directives: [
                                          {
                                            name: "me-safe-html",
                                            rawName: "v-me-safe-html",
                                            value: _vm.$options.filters.highlightText(
                                              item.name,
                                              _vm.searchText
                                            ),
                                            expression:
                                              "$options.filters.highlightText(item.name, searchText)"
                                          }
                                        ]
                                      })
                                    ])
                                  ],
                                  1
                                )
                              }),
                              _vm._v(" "),
                              _vm.pagination.busy
                                ? _c(
                                    "span",
                                    [
                                      _c("Spinner", {
                                        staticClass: "align-middle",
                                        attrs: { small: "" }
                                      }),
                                      _vm._v(
                                        "\n            " +
                                          _vm._s(
                                            _vm.$toolkit.t("spinner.loading")
                                          ) +
                                          "\n          "
                                      )
                                    ],
                                    1
                                  )
                                : _vm._e()
                            ],
                            2
                          )
                    ]
                  )
                ])
              : _vm._e()
          ],
          1
        )
      : _vm._e(),
    _vm._v(" "),
    _c(
      "div",
      { staticClass: "section-selection" },
      _vm._l(_vm.currentSections, function(section, index) {
        return _c(
          "div",
          {
            key: index,
            staticClass: "section-div",
            class: {
              active: section.active,
              "first-inactive": _vm.isFirstInactive(index)
            },
            attrs: { id: "section-div-" + index }
          },
          [
            _c("div", { staticClass: "section-title" }, [
              _vm._v("\n        " + _vm._s(section.title) + "\n        "),
              section.active
                ? _c("div", { staticClass: "section-length" }, [
                    _vm._v(
                      "\n          (" +
                        _vm._s(
                          (section.items ? section.items.length : 0) +
                            _vm.$toolkit.t("sectionTree.results")
                        ) +
                        ")\n        "
                    )
                  ])
                : _vm._e(),
              _vm._v(" "),
              section.active
                ? _c(
                    "div",
                    { staticClass: "section-search" },
                    [
                      section.shouldShowSearch
                        ? _c("me-text-input", {
                            class: "section-search-input-" + index,
                            attrs: { size: "sm", type: "search" },
                            on: {
                              blur: function($event) {
                                return _vm.hideSearch(section)
                              }
                            },
                            model: {
                              value: section.searchText,
                              callback: function($$v) {
                                _vm.$set(section, "searchText", $$v);
                              },
                              expression: "section.searchText"
                            }
                          })
                        : _c("i", {
                            staticClass: "me-icon icon-search",
                            on: {
                              click: function($event) {
                                return _vm.showSearch(section, index)
                              }
                            }
                          })
                    ],
                    1
                  )
                : _vm._e()
            ]),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "section-items" },
              [
                section.loading
                  ? _c(
                      "div",
                      { staticClass: "section-loading" },
                      [
                        _c("Spinner", {
                          staticClass: "align-middle",
                          attrs: { small: "" }
                        }),
                        _vm._v(
                          "\n          " +
                            _vm._s(_vm.$toolkit.t("spinner.loading")) +
                            "\n        "
                        )
                      ],
                      1
                    )
                  : _vm._l(_vm.filteredItems(section), function(item) {
                      return _c(
                        "div",
                        {
                          key: item.value,
                          staticClass: "section-item",
                          class: {
                            selected: item.selected || item.opened,
                            disabled: item.disabled
                          }
                        },
                        [
                          section.selectable
                            ? _c(
                                "div",
                                { staticClass: "section-item-checkbox" },
                                [
                                  _c("me-checkbox", {
                                    attrs: { disabled: item.disabled },
                                    on: {
                                      change: function($event) {
                                        return _vm.itemSelected($event, item)
                                      }
                                    },
                                    model: {
                                      value: item.selected,
                                      callback: function($$v) {
                                        _vm.$set(item, "selected", $$v);
                                      },
                                      expression: "item.selected"
                                    }
                                  })
                                ],
                                1
                              )
                            : _vm._e(),
                          _vm._v(" "),
                          _c(
                            "div",
                            {
                              staticClass: "section-item-title",
                              on: {
                                click: function($event) {
                                  !item.disabled && _vm.itemHasChildren(item)
                                    ? _vm.openChild(item, index)
                                    : null;
                                }
                              }
                            },
                            [
                              _c("span", {
                                directives: [
                                  {
                                    name: "me-safe-html",
                                    rawName: "v-me-safe-html",
                                    value: _vm.$options.filters.highlightText(
                                      item.label || item.value,
                                      section.searchText
                                    ),
                                    expression:
                                      "$options.filters.highlightText((item.label || item.value), section.searchText)"
                                  }
                                ]
                              })
                            ]
                          ),
                          _vm._v(" "),
                          _vm.itemHasChildren(item)
                            ? _c(
                                "div",
                                {
                                  staticClass: "section-item-arrow",
                                  on: {
                                    click: function($event) {
                                      !item.disabled &&
                                        _vm.openChild(item, index);
                                    }
                                  }
                                },
                                [
                                  _c("i", {
                                    staticClass: "me-icon-l",
                                    class: item.opened
                                      ? "icon-chevron-left"
                                      : "icon-chevron-right"
                                  })
                                ]
                              )
                            : _vm._e()
                        ]
                      )
                    })
              ],
              2
            )
          ]
        )
      }),
      0
    )
  ])
};
var __vue_staticRenderFns__$1X = [];
__vue_render__$1X._withStripped = true;

  /* style */
  const __vue_inject_styles__$1X = function (inject) {
    if (!inject) return
    inject("data-v-909113f0_0", { source: ".me-section-tree .section-quick-search[data-v-909113f0] {\n  position: relative;\n}\n.me-section-tree .section-quick-search__actions[data-v-909113f0] {\n  position: absolute;\n  right: 12px;\n  top: 12px;\n}\n.me-section-tree .section-quick-search .content[data-v-909113f0] {\n  align-items: center;\n  background: var(--me-white);\n  border-radius: 8px;\n  box-shadow: var(--me-shadow-level-1);\n  border: 1px solid var(--me-gray-5);\n  left: 0;\n  padding: 0.5rem;\n  position: absolute;\n  right: 0;\n  top: 52px;\n  z-index: 1020;\n}\n.me-section-tree .section-quick-search .content > .results[data-v-909113f0] {\n  max-height: 200px;\n  overflow-y: auto;\n}\n.me-section-tree .section-quick-search .content > .results.loading[data-v-909113f0] {\n  text-align: center;\n}\n.me-section-tree .section-quick-search .content .search-result[data-v-909113f0] {\n  display: flex;\n  padding: 8px;\n  align-items: center;\n}\n.me-section-tree .section-quick-search .content .search-path[data-v-909113f0] {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  display: flex;\n}\n.me-section-tree .section-quick-search .content .search-path-parent[data-v-909113f0] {\n  padding-right: 4px;\n}\n.me-section-tree > .section-selection[data-v-909113f0] {\n  display: flex;\n  width: 100%;\n  height: 328px;\n  margin: 8px 0;\n  padding-bottom: 8px;\n  overflow-x: auto;\n  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n  scrollbar-width: thin;\n}\n.me-section-tree > .section-selection .section-div[data-v-909113f0] {\n  height: 100%;\n  border: 1px solid var(--me-gray-5);\n  border-radius: 5px;\n  min-width: 250px;\n  flex-grow: 1;\n  opacity: 0.4;\n  margin: 0 1px;\n}\n.me-section-tree > .section-selection .section-div.active[data-v-909113f0] {\n  opacity: 1;\n}\n.me-section-tree > .section-selection .section-div.first-inactive[data-v-909113f0] {\n  opacity: 0.7;\n}\n.me-section-tree > .section-selection .section-title[data-v-909113f0] {\n  display: flex;\n  color: var(--me-gray-3);\n  font-weight: bold;\n  padding: 16px;\n}\n.me-section-tree > .section-selection .section-length[data-v-909113f0] {\n  margin: 0 8px;\n  color: var(--me-gray-4);\n  font-weight: normal;\n}\n.me-section-tree > .section-selection .section-search[data-v-909113f0] {\n  margin-left: auto;\n}\n.me-section-tree > .section-selection .section-search i[data-v-909113f0] {\n  cursor: pointer;\n}\n.me-section-tree > .section-selection .section-loading[data-v-909113f0] {\n  text-align: center;\n}\n.me-section-tree > .section-selection .section-items[data-v-909113f0] {\n  overflow: auto;\n  height: calc(100% - 60px);\n}\n.me-section-tree > .section-selection .section-item[data-v-909113f0] {\n  display: flex;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding: 12px;\n  font-size: 14px;\n  color: var(--me-gray-3);\n}\n.me-section-tree > .section-selection .section-item[data-v-909113f0]:hover, .me-section-tree > .section-selection .section-item.selected[data-v-909113f0] {\n  background: var(--me-primary-7);\n  color: var(--me-primary-4);\n}\n.me-section-tree > .section-selection .section-item.disabled[data-v-909113f0] {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  pointer-events: none;\n}\n.me-section-tree > .section-selection .section-item-title[data-v-909113f0] {\n  flex-grow: 1;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.me-section-tree > .section-selection .section-item-arrow[data-v-909113f0] {\n  padding: 0 8px;\n  font-size: 20px;\n  position: relative;\n}\n.me-section-tree > .section-selection .section-item-arrow[data-v-909113f0]:hover {\n  cursor: pointer;\n}\n.me-section-tree > .section-selection .section-item-arrow > i[data-v-909113f0] {\n  top: -2px;\n  position: absolute;\n}\n[data-v-909113f0]::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n[data-v-909113f0]::-webkit-scrollbar-thumb {\n  background: var(--me-gray-4);\n  border-radius: 15px;\n  transition: opacity 1s;\n}\n[data-v-909113f0]::-webkit-scrollbar-thumb:hover {\n  background: var(--me-gray-3);\n}\n\n/*# sourceMappingURL=SectionTree.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SectionTree.vue","SectionTree.vue"],"names":[],"mappings":"AAkYA;EACA,kBAAA;ACjYA;ADmYA;EACA,kBAAA;EACA,WAAA;EACA,SAAA;ACjYA;ADoYA;EACA,mBAAA;EACA,2BAAA;EACA,kBAAA;EACA,oCAAA;EACA,kCAAA;EACA,OAAA;EACA,eAAA;EACA,kBAAA;EACA,QAAA;EACA,SAAA;EACA,aAAA;AClYA;ADmYA;EACA,iBAAA;EACA,gBAAA;ACjYA;ADkYA;EACA,kBAAA;AChYA;ADoYA;EACA,aAAA;EACA,YAAA;EACA,mBAAA;AClYA;ADqYA;EACA,eAAA;EACA,uBAAA;EACA,aAAA;ACnYA;ADqYA;EACA,kBAAA;ACnYA;ADwYA;EACA,aAAA;EACA,WAAA;EACA,aAAA;EACA,aAAA;EACA,mBAAA;EACA,gBAAA;EACA,gDAAA;EACA,qBAAA;ACtYA;ADyYA;EACA,YAAA;EACA,kCAAA;EACA,kBAAA;EACA,gBAAA;EACA,YAAA;EACA,YAAA;EACA,aAAA;ACvYA;ADyYA;EACA,UAAA;ACvYA;AD0YA;EACA,YAAA;ACxYA;AD4YA;EACA,aAAA;EACA,uBAAA;EACA,iBAAA;EACA,aAAA;AC1YA;AD6YA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;AC3YA;AD8YA;EACA,iBAAA;AC5YA;AD6YA;EACA,eAAA;AC3YA;AD+YA;EACA,kBAAA;AC7YA;ADgZA;EACA,cAAA;EACA,yBAAA;AC9YA;ADiZA;EACA,aAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,aAAA;EACA,eAAA;EACA,uBAAA;AC/YA;ADiZA;EACA,+BAAA;EACA,0BAAA;AC/YA;ADkZA;EACA,kCAAA;EACA,uBAAA;EACA,oBAAA;AChZA;ADmZA;EACA,YAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;ACjZA;ADoZA;EACA,cAAA;EACA,eAAA;EACA,kBAAA;AClZA;ADmZA;EACA,eAAA;ACjZA;ADoZA;EACA,SAAA;EACA,kBAAA;AClZA;AD0ZA;EACA,UAAA;EACA,WAAA;ACvZA;AD0ZA;EACA,4BAAA;EACA,mBAAA;EACA,sBAAA;ACvZA;ADwZA;EACA,4BAAA;ACtZA;;AAEA,0CAA0C","file":"SectionTree.vue","sourcesContent":["<template>\n  <div class=\"me-section-tree\">\n    <div v-if=\"searchProvider\" class=\"section-quick-search\">\n      <MeTextInput\n        v-model=\"searchText\"\n        :placeholder=\"searchPlaceholder\"\n        @input=\"showResults\"\n        @enter=\"showResults\"\n        size=\"lg\"\n      />\n      <div class=\"section-quick-search__actions\">\n        <Icon v-if=\"!shouldSearch\" @click=\"showResults\" icon=\"me-icon icon-search\" />\n        <Icon v-if=\"shouldSearch\" @click=\"searchText = ''\" icon=\"me-icon icon-times\" />\n      </div>\n      <div v-if=\"shouldSearch\" class=\"content\">\n        <div ref=\"results\" :class=\"{loading: loadingSearchProvider}\" @scroll.passive=\"onScroll\" class=\"results\">\n          <span v-if=\"loadingSearchProvider\">\n            <Spinner class=\"align-middle\" small />\n            {{ $toolkit.t('spinner.loading') }}\n          </span>\n          <span v-if=\"!loadingSearchProvider && !searchResults.length\">\n            {{ $toolkit.t('grid.result.empty') }}\n          </span>\n\n          <div v-else>\n            <div\n              v-for=\"item in searchResults\"\n              :key=\"item.value\"\n              class=\"search-result\"\n            >\n              <me-checkbox @change=\"itemOnSearchSelected($event, item)\" :disabled=\"item.disabled\" />\n              <div class=\"search-text\">\n                <div v-if=\"item.parents && item.parents.length\" class=\"search-path\">\n                  <div v-for=\"(parent, index) in item.parents\" :key=\"index\" class=\"search-path-parent\">\n                    {{ parent.name }}\n                    <i class=\"me-icon icon-chevron-right\" />\n                  </div>\n                </div>\n                <span\n                  v-me-safe-html=\"$options.filters.highlightText(item.name, searchText)\"\n                />\n              </div>\n            </div>\n            <span v-if=\"pagination.busy\">\n              <Spinner class=\"align-middle\" small />\n              {{ $toolkit.t('spinner.loading') }}\n            </span>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div class=\"section-selection\">\n      <div :id=\"`section-div-${index}`\" v-for=\"(section, index) in currentSections\" :key=\"index\" :class=\"{active: section.active, 'first-inactive': isFirstInactive(index)}\" class=\"section-div\">\n        <div class=\"section-title\">\n          {{ section.title }}\n          <div v-if=\"section.active\" class=\"section-length\">\n            ({{ (section.items ? section.items.length : 0) + $toolkit.t('sectionTree.results') }})\n          </div>\n          <div v-if=\"section.active\" class=\"section-search\">\n            <me-text-input\n              v-model=\"section.searchText\"\n              v-if=\"section.shouldShowSearch\"\n              @blur=\"hideSearch(section)\"\n              :class=\"`section-search-input-${index}`\"\n              size=\"sm\"\n              type=\"search\"\n            />\n            <i v-else @click=\"showSearch(section, index)\" class=\"me-icon icon-search\" />\n          </div>\n        </div>\n        <div class=\"section-items\">\n          <div v-if=\"section.loading\" class=\"section-loading\">\n            <Spinner class=\"align-middle\" small />\n            {{ $toolkit.t('spinner.loading') }}\n          </div>\n          <div v-else v-for=\"item in filteredItems(section)\" :key=\"item.value\" :class=\"{selected: item.selected || item.opened, disabled: item.disabled}\" class=\"section-item\">\n            <div v-if=\"section.selectable\" class=\"section-item-checkbox\">\n              <me-checkbox @change=\"itemSelected($event, item)\" v-model=\"item.selected\" :disabled=\"item.disabled\" />\n            </div>\n            <div @click=\"!item.disabled && itemHasChildren(item) ? openChild(item, index) : null\" class=\"section-item-title\">\n              <span\n                v-me-safe-html=\"$options.filters.highlightText((item.label || item.value), section.searchText)\"\n              />\n            </div>\n            <div v-if=\"itemHasChildren(item)\" @click=\"!item.disabled && openChild(item, index)\" class=\"section-item-arrow\">\n              <i :class=\"item.opened ? 'icon-chevron-left' : 'icon-chevron-right'\" class=\"me-icon-l\" />\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport cloneDeep from 'lodash/cloneDeep'\nimport findLast from 'lodash/findLast'\nimport concat from 'lodash/concat'\nimport Spinner from './Spinner.vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeSectionTree',\n\n  components: {\n    Spinner,\n    Icon\n  },\n\n  props: {\n    sections: {\n      type: Array,\n      required: true\n    },\n    searchPlaceholder: {\n      type: String,\n      default: ''\n    },\n    searchProvider: {\n      type: Function,\n      default: null\n    },\n    listProvider: {\n      type: Function,\n      default: null\n    },\n    searchWithPagination: {\n      type: Boolean,\n      default: false\n    },\n    searchPageSize: {\n      type: Number,\n      default: 10\n    },\n    selectedItems: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      searchText: '',\n      currentSections: this.sections.map(section => this.addMissingParams(section)),\n      currentSelectedItems: [...this.selectedItems],\n      loadingSearchProvider: false,\n      searchResults: [],\n      pagination: {\n        busy: false,\n        page: 1,\n        hits: null,\n        isLastPage: false\n      }\n    }\n  },\n\n  computed: {\n    shouldSearch() {\n      return this.searchText?.length > 2\n    }\n  },\n\n  watch: {\n    sections(val) {\n      this.currentSections = val.map(section => this.addMissingParams(section))\n    },\n\n    selectedItems(val) {\n      this.currentSelectedItems = [...val]\n      this.currentSections.forEach((section) => {\n        if (section.items) {\n          section.items.forEach((item) => {\n            item.selected = this.isItemSelected(item)\n          })\n        }\n      })\n    }\n  },\n\n  created() {\n    this.currentSections[0].active = true\n  },\n\n  methods: {\n    addMissingParams(section) {\n      return {\n        ...section,\n        shouldShowSearch: false,\n        active: false,\n        selectable: section.selectable !== false,\n        loading: false,\n        searchText: ''\n      }\n    },\n\n    isItemSelected(item) {\n      return this.currentSelectedItems.some(selectedItem => selectedItem.value === item.value)\n    },\n\n    itemHasChildren(item) {\n      return item.hasChildren || (item.children && item.children.length)\n    },\n\n    showResults() {\n      this.pagination = {\n        busy: false,\n        page: 1,\n        hits: null,\n        isLastPage: false\n      }\n      this.filter = true\n      this.loadingSearchProvider = true\n      this.searchResults = []\n      this.callSearchProvider()\n    },\n    callSearchProvider: debounce(function () {\n      if (this.shouldSearch) {\n        this.searchProvider(this.searchText, ({ results, hits }) => {\n          this.loadingSearchProvider = false\n          if (this.searchWithPagination) {\n            this.searchResults = concat(this.searchResults, results)\n            this.pagination.hits = hits\n            this.pagination.page++\n            this.pagination.isLastPage = this.pagination.page > Math.ceil(hits / this.searchPageSize)\n            this.pagination.busy = false\n          } else {\n            this.searchResults = results\n          }\n        }, this.pagination.page, this.searchPageSize)\n      }\n    }, 500),\n\n    isFirstInactive(index) {\n      const lastActive = findLast(this.currentSections, section => section.active)\n\n      const lastActiveIndex = this.currentSections.indexOf(lastActive)\n      return index === lastActiveIndex + 1\n    },\n\n    showSearch(section, index) {\n      section.shouldShowSearch = true\n      this.$nextTick(() => {\n        document.querySelector(`.section-search-input-${index} input`).focus()\n      })\n    },\n\n    hideSearch(section) {\n      if (!section.searchText) {\n        section.shouldShowSearch = false\n      }\n    },\n\n    filteredItems(section) {\n      if (section.searchText) {\n        return section.items.filter(item => item.label.toLowerCase().includes(section.searchText.toLowerCase()))\n      }\n      return section.items\n    },\n\n    openChild(item, index) {\n      this.currentSections[index].items\n        .filter(sectionItem => sectionItem.opened)\n        .forEach((sectionItem) => { sectionItem.opened = false })\n\n      item.opened = true\n\n      this.currentSections\n        .filter((section, i) => { return section.active && i > index })\n        .forEach((section) => {\n          section.active = false\n          section.items = []\n        })\n\n      this.currentSections[index + 1].items = []\n      this.currentSections[index + 1].active = true\n      if (this.listProvider) {\n        this.currentSections[index + 1].loading = true\n        this.listProvider(item, ({ items }) => {\n          this.currentSections[index + 1].items = items\n          this.currentSections[index + 1].loading = false\n          this.focusNextDiv(index + 1)\n        })\n      } else {\n        this.currentSections[index + 1].items = item.children\n        this.focusNextDiv(index + 1)\n      }\n    },\n\n    focusNextDiv(index) {\n      const sectionDiv = document.querySelector(`#section-div-${index}`)\n      if (sectionDiv) {\n        sectionDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })\n      }\n    },\n\n    itemSelected($event, item) {\n      item.selected = $event\n      if ($event) {\n        item.parents = []\n        const previousParents = []\n        this.currentSections\n          .filter(section => section.active)\n          .every((section) => {\n            if (section.items.some(sectionItem => sectionItem.value === item.value)) {\n              return false\n            }\n            const openedItem = section.items.find(sectionItem => sectionItem.opened)\n            if (openedItem) {\n              openedItem.parents = [...previousParents]\n              openedItem.selected = section.selectable\n              item.parents.push(cloneDeep(openedItem))\n              previousParents.push(cloneDeep(openedItem))\n              if (!this.isItemSelected(openedItem) && section.selectable) {\n                this.currentSelectedItems.push(openedItem)\n              }\n            }\n            return true\n          })\n        this.currentSelectedItems.push(item)\n      } else {\n        this.removeItemAndChildrens(item)\n      }\n      this.$emit('change', this.currentSelectedItems)\n      this.$forceUpdate()\n    },\n\n    itemOnSearchSelected($event, item) {\n      if ($event) {\n        if (!this.isItemSelected(item)) {\n          this.currentSelectedItems.push(item)\n          this.setItemStatusOnSections(item, true)\n        }\n        item.parents.forEach((parent) => {\n          if (!this.isItemSelected(parent)) {\n            this.currentSelectedItems.push(parent)\n            this.setItemStatusOnSections(parent, true)\n          }\n        })\n      } else {\n        this.removeItemAndChildrens(item)\n        this.setItemStatusOnSections(item, false)\n      }\n      this.$emit('change', this.currentSelectedItems)\n    },\n\n    removeItemAndChildrens(item) {\n      const itemsToRemove = this.currentSelectedItems\n        .filter(selectedItem => selectedItem.value === item.value || selectedItem.parents?.some(parent => parent.value === item.value))\n      itemsToRemove.forEach((itemToRemove) => {\n        const index = this.currentSelectedItems.indexOf(itemToRemove)\n        this.currentSelectedItems.splice(index, 1)\n        this.setItemStatusOnSections(itemToRemove, false)\n      })\n      this.$forceUpdate()\n    },\n\n    setItemStatusOnSections(item, value) {\n      this.currentSections\n        .filter(section => section.active)\n        .forEach((section) => {\n          const selectedItem = section.items.find(sectionItem => sectionItem.value === item.value)\n          if (selectedItem) {\n            selectedItem.selected = value\n          }\n        })\n      this.$forceUpdate()\n    },\n\n    scroll() {\n      const { scrollHeight, scrollTop, offsetHeight } = this.$refs.results\n      const distance = scrollHeight - (scrollTop + offsetHeight)\n      if (distance <= 30 && this.searchWithPagination && !this.pagination.busy && !this.pagination.isLastPage) {\n        this.pagination.busy = true\n        this.callSearchProvider()\n      }\n    },\n\n    onScroll: debounce(function () {\n      this.scroll()\n    }, 500)\n  }\n}\n</script>\n<style lang=\"scss\" scoped>\n.me-section-tree {\n  .section-quick-search {\n    position: relative;\n\n    &__actions {\n      position: absolute;\n      right: 12px;\n      top: 12px;\n    }\n\n    .content {\n      align-items: center;\n      background: var(--me-white);\n      border-radius: 8px;\n      box-shadow: var(--me-shadow-level-1);\n      border: 1px solid var(--me-gray-5);\n      left: 0;\n      padding: 0.5rem;\n      position: absolute;\n      right: 0;\n      top: 52px;\n      z-index: 1020;\n      > .results {\n        max-height: 200px;\n        overflow-y: auto;\n        &.loading {\n          text-align: center;\n        }\n      }\n\n      .search-result {\n        display: flex;\n        padding: 8px;\n        align-items: center;\n      }\n\n      .search-path {\n        font-size: 12px;\n        color: var(--me-gray-4);\n        display: flex;\n\n        &-parent {\n          padding-right: 4px;\n        }\n      }\n    }\n  }\n  > .section-selection {\n    display: flex;\n    width: 100%;\n    height: 328px;\n    margin: 8px 0;\n    padding-bottom: 8px;\n    overflow-x: auto;\n    scrollbar-color: rgba(0,0,0,0.25) transparent;\n    scrollbar-width: thin;\n\n    .section {\n      &-div {\n        height: 100%;\n        border: 1px solid var(--me-gray-5);\n        border-radius: 5px;\n        min-width: 250px;\n        flex-grow: 1;\n        opacity: 0.4;\n        margin: 0 1px;\n\n        &.active {\n          opacity: 1;\n        }\n\n        &.first-inactive {\n          opacity: 0.7;\n        }\n      }\n\n      &-title {\n        display: flex;\n        color: var(--me-gray-3);\n        font-weight: bold;\n        padding: 16px;\n      }\n\n      &-length {\n        margin: 0 8px;\n        color: var(--me-gray-4);\n        font-weight: normal;\n      }\n\n      &-search {\n        margin-left: auto;\n        i {\n          cursor: pointer;\n        }\n      }\n\n      &-loading {\n        text-align: center;\n      }\n\n      &-items {\n        overflow: auto;\n        height: calc(100% - 60px);\n      }\n\n      &-item {\n        display: flex;\n        white-space: nowrap;\n        overflow: hidden;\n        text-overflow: ellipsis;\n        padding: 12px;\n        font-size: 14px;\n        color: var(--me-gray-3);\n\n        &:hover, &.selected {\n          background: var(--me-primary-7);\n          color: var(--me-primary-4);\n        }\n\n        &.disabled {\n          background-color: var(--me-gray-6);\n          color: var(--me-gray-4);\n          pointer-events: none;\n        }\n\n        &-title {\n          flex-grow: 1;\n          white-space: nowrap;\n          overflow: hidden;\n          text-overflow: ellipsis\n        }\n\n        &-arrow {\n          padding: 0 8px;\n          font-size: 20px;\n          position: relative;\n          &:hover {\n            cursor: pointer;\n          }\n\n          > i {\n            top: -2px;\n            position: absolute;\n          }\n        }\n      }\n    }\n  }\n}\n\n::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n\n::-webkit-scrollbar-thumb {\n  background: var(--me-gray-4);\n  border-radius: 15px;\n  transition: opacity 1s;\n  &:hover {\n    background: var(--me-gray-3);\n  }\n}\n\n</style>\n",".me-section-tree .section-quick-search {\n  position: relative;\n}\n.me-section-tree .section-quick-search__actions {\n  position: absolute;\n  right: 12px;\n  top: 12px;\n}\n.me-section-tree .section-quick-search .content {\n  align-items: center;\n  background: var(--me-white);\n  border-radius: 8px;\n  box-shadow: var(--me-shadow-level-1);\n  border: 1px solid var(--me-gray-5);\n  left: 0;\n  padding: 0.5rem;\n  position: absolute;\n  right: 0;\n  top: 52px;\n  z-index: 1020;\n}\n.me-section-tree .section-quick-search .content > .results {\n  max-height: 200px;\n  overflow-y: auto;\n}\n.me-section-tree .section-quick-search .content > .results.loading {\n  text-align: center;\n}\n.me-section-tree .section-quick-search .content .search-result {\n  display: flex;\n  padding: 8px;\n  align-items: center;\n}\n.me-section-tree .section-quick-search .content .search-path {\n  font-size: 12px;\n  color: var(--me-gray-4);\n  display: flex;\n}\n.me-section-tree .section-quick-search .content .search-path-parent {\n  padding-right: 4px;\n}\n.me-section-tree > .section-selection {\n  display: flex;\n  width: 100%;\n  height: 328px;\n  margin: 8px 0;\n  padding-bottom: 8px;\n  overflow-x: auto;\n  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;\n  scrollbar-width: thin;\n}\n.me-section-tree > .section-selection .section-div {\n  height: 100%;\n  border: 1px solid var(--me-gray-5);\n  border-radius: 5px;\n  min-width: 250px;\n  flex-grow: 1;\n  opacity: 0.4;\n  margin: 0 1px;\n}\n.me-section-tree > .section-selection .section-div.active {\n  opacity: 1;\n}\n.me-section-tree > .section-selection .section-div.first-inactive {\n  opacity: 0.7;\n}\n.me-section-tree > .section-selection .section-title {\n  display: flex;\n  color: var(--me-gray-3);\n  font-weight: bold;\n  padding: 16px;\n}\n.me-section-tree > .section-selection .section-length {\n  margin: 0 8px;\n  color: var(--me-gray-4);\n  font-weight: normal;\n}\n.me-section-tree > .section-selection .section-search {\n  margin-left: auto;\n}\n.me-section-tree > .section-selection .section-search i {\n  cursor: pointer;\n}\n.me-section-tree > .section-selection .section-loading {\n  text-align: center;\n}\n.me-section-tree > .section-selection .section-items {\n  overflow: auto;\n  height: calc(100% - 60px);\n}\n.me-section-tree > .section-selection .section-item {\n  display: flex;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  padding: 12px;\n  font-size: 14px;\n  color: var(--me-gray-3);\n}\n.me-section-tree > .section-selection .section-item:hover, .me-section-tree > .section-selection .section-item.selected {\n  background: var(--me-primary-7);\n  color: var(--me-primary-4);\n}\n.me-section-tree > .section-selection .section-item.disabled {\n  background-color: var(--me-gray-6);\n  color: var(--me-gray-4);\n  pointer-events: none;\n}\n.me-section-tree > .section-selection .section-item-title {\n  flex-grow: 1;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n.me-section-tree > .section-selection .section-item-arrow {\n  padding: 0 8px;\n  font-size: 20px;\n  position: relative;\n}\n.me-section-tree > .section-selection .section-item-arrow:hover {\n  cursor: pointer;\n}\n.me-section-tree > .section-selection .section-item-arrow > i {\n  top: -2px;\n  position: absolute;\n}\n\n::-webkit-scrollbar {\n  width: 6px;\n  height: 6px;\n}\n\n::-webkit-scrollbar-thumb {\n  background: var(--me-gray-4);\n  border-radius: 15px;\n  transition: opacity 1s;\n}\n::-webkit-scrollbar-thumb:hover {\n  background: var(--me-gray-3);\n}\n\n/*# sourceMappingURL=SectionTree.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1X = "data-v-909113f0";
  /* module identifier */
  const __vue_module_identifier__$1X = undefined;
  /* functional template */
  const __vue_is_functional_template__$1X = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1X, staticRenderFns: __vue_staticRenderFns__$1X },
    __vue_inject_styles__$1X,
    __vue_script__$1X,
    __vue_scope_id__$1X,
    __vue_is_functional_template__$1X,
    __vue_module_identifier__$1X,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1Y = {
  name: 'MeSidebar',
  components: {
    BSidebar,
    Spinner: __vue_component__$5
  },
  props: {
    id: {
      type: String,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    zIndex: {
      type: Number,
      default: 9999
    },
    title: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: null
    },
    bgVariant: {
      type: String,
      default: 'light'
    },
    textvariant: {
      type: String,
      default: null
    },
    width: {
      type: String,
      default: '510px'
    },
    backdrop: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$1Y = script$1Y;

/* template */
var __vue_render__$1Y = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BSidebar",
    {
      attrs: {
        id: _vm.id,
        title: _vm.title,
        variant: _vm.variant,
        "bg-variant": _vm.bgVariant,
        "text-variant": _vm.textvariant,
        width: _vm.width,
        visible: _vm.visible,
        "no-header": !_vm.showHeader,
        "z-index": _vm.zIndex,
        backdrop: _vm.backdrop,
        right: ""
      },
      on: {
        shown: function($event) {
          return _vm.$emit("shown")
        },
        hidden: function($event) {
          return _vm.$emit("hidden")
        }
      }
    },
    [
      _c(
        "div",
        [
          _vm.busy
            ? _c("div", { staticClass: "me-sidebar__overlay" }, [
                _c(
                  "div",
                  { staticClass: "me-sidebar__overlay__inner" },
                  [
                    _vm._t("overlay", [
                      _c(
                        "div",
                        { staticClass: "text-center" },
                        [
                          _c("Spinner"),
                          _vm._v(" "),
                          _c("p", [
                            _vm._v(_vm._s(_vm.$toolkit.t("spinner.loading")))
                          ])
                        ],
                        1
                      )
                    ])
                  ],
                  2
                )
              ])
            : _vm._t("default")
        ],
        2
      )
    ]
  )
};
var __vue_staticRenderFns__$1Y = [];
__vue_render__$1Y._withStripped = true;

  /* style */
  const __vue_inject_styles__$1Y = function (inject) {
    if (!inject) return
    inject("data-v-e2cab7d6_0", { source: "div.me-sidebar__overlay[data-v-e2cab7d6] {\n  background-color: var(--me-primary-2);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: fixed;\n  inset: 0;\n  color: var(--me-white);\n}\ndiv.me-sidebar__overlay__inner[data-v-e2cab7d6] {\n  padding: 0 2rem;\n}\n[data-v-e2cab7d6] .b-sidebar .b-sidebar-header button.close {\n  outline: none;\n}\n[data-v-e2cab7d6] .b-sidebar .b-sidebar-body {\n  padding: 16px;\n}\n\n/*# sourceMappingURL=Sidebar.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Sidebar.vue","Sidebar.vue"],"names":[],"mappings":"AA6FA;EACA,qCAAA;EACA,aAAA;EACA,mBAAA;EACA,uBAAA;EACA,eAAA;EACA,QAAA;EACA,sBAAA;AC5FA;AD8FA;EACA,eAAA;AC5FA;ADmGA;EACA,aAAA;AChGA;ADoGA;EACA,aAAA;AClGA;;AAEA,sCAAsC","file":"Sidebar.vue","sourcesContent":["<template>\n  <BSidebar\n    :id=\"id\"\n    :title=\"title\"\n    :variant=\"variant\"\n    :bg-variant=\"bgVariant\"\n    :text-variant=\"textvariant\"\n    :width=\"width\"\n    :visible=\"visible\"\n    :no-header=\"!showHeader\"\n    :z-index=\"zIndex\"\n    :backdrop=\"backdrop\"\n    @shown=\"$emit('shown')\"\n    @hidden=\"$emit('hidden')\"\n    right\n  >\n    <div>\n      <div v-if=\"busy\" class=\"me-sidebar__overlay\">\n        <div class=\"me-sidebar__overlay__inner\">\n          <slot name=\"overlay\">\n            <div class=\"text-center\">\n              <Spinner />\n              <p>{{ $toolkit.t('spinner.loading') }}</p>\n            </div>\n          </slot>\n        </div>\n      </div>\n      <slot v-else name=\"default\" />\n    </div>\n  </BSidebar>\n</template>\n\n<script>\nimport { BSidebar } from 'bootstrap-vue'\nimport Spinner from './Spinner.vue'\n\nexport default {\n  name: 'MeSidebar',\n  components: {\n    BSidebar,\n    Spinner\n  },\n  props: {\n    id: {\n      type: String,\n      default: null\n    },\n    visible: {\n      type: Boolean,\n      default: false\n    },\n    showHeader: {\n      type: Boolean,\n      default: true\n    },\n    zIndex: {\n      type: Number,\n      default: 9999\n    },\n    title: {\n      type: String,\n      default: null\n    },\n    variant: {\n      type: String,\n      default: null\n    },\n    bgVariant: {\n      type: String,\n      default: 'light'\n    },\n    textvariant: {\n      type: String,\n      default: null\n    },\n    width: {\n      type: String,\n      default: '510px'\n    },\n    backdrop: {\n      type: Boolean,\n      default: false\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-sidebar {\n    &__overlay {\n      background-color: var(--me-primary-2);\n      display: flex;\n      align-items: center;\n      justify-content: center;\n      position: fixed;\n      inset: 0;\n      color: var(--me-white);\n\n      &__inner {\n        padding: 0 2rem;\n      }\n    }\n  }\n\n  ::v-deep .b-sidebar {\n      .b-sidebar-header {\n        button.close {\n          outline: none;\n        }\n      }\n\n      .b-sidebar-body {\n        padding: 16px;\n      }\n  }\n\n</style>\n","div.me-sidebar__overlay {\n  background-color: var(--me-primary-2);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: fixed;\n  inset: 0;\n  color: var(--me-white);\n}\ndiv.me-sidebar__overlay__inner {\n  padding: 0 2rem;\n}\n\n::v-deep .b-sidebar .b-sidebar-header button.close {\n  outline: none;\n}\n::v-deep .b-sidebar .b-sidebar-body {\n  padding: 16px;\n}\n\n/*# sourceMappingURL=Sidebar.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1Y = "data-v-e2cab7d6";
  /* module identifier */
  const __vue_module_identifier__$1Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$1Y = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1Y, staticRenderFns: __vue_staticRenderFns__$1Y },
    __vue_inject_styles__$1Y,
    __vue_script__$1Y,
    __vue_scope_id__$1Y,
    __vue_is_functional_template__$1Y,
    __vue_module_identifier__$1Y,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1Z = {
  name: 'MeSidebarMenu',
  components: {
    MeNestedMenu: __vue_component__$1P
  },
  props: {
    title: {
      type: String,
      required: true
    },
    links: {
      type: Array,
      default: () => []
    },
    group: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      linksModel: this.getLinksModel(),
      groupModel: this.getItemsModel()
    }
  },

  watch: {
    links() {
      this.linksModel = this.getLinksModel();
    },
    group() {
      this.groupModel = this.getItemsModel();
    }
  },

  methods: {
    createItemModel(item, menuIndex) {
      item.items = item.items.map((link, linkIndex) => ({
        ...link,
        class: link.class ?? '',
        icon: link.icon ?? '',
        active: link.active ?? false,
        opened: item.opened ?? true,
        click: () => this.handleClick({ menuIndex, linkIndex, link })
      }));

      return item
    },
    createMenuModel(item) {
      const parentItem = {
        titleMenu: true,
        label: item.title,
        icon: item.icon ?? '',
        active: item.active ?? false,
        click: item.click
      };

      item.items.splice(0, 0, parentItem);

      return item
    },
    getLinksModel() {
      return this.links.map((link, index) => ({
        ...link,
        icon: link.icon ?? '',
        active: link.active ?? false,
        click: () => this.handleLinkClick({ index, link })
      }))
    },
    getItemsModel() {
      return this.group
        .map(this.createMenuModel)
        .map(this.createItemModel)
    },
    inactiveGroupLinks() {
      this.groupModel.forEach((links) => {
        links.items.forEach((link) => {
          link.active = false;
        });
      });
    },
    inactiveLinks() {
      this.linksModel.forEach((item) => {
        item.active = false;
      });
    },
    handleLinkClick({ index, link }) {
      this.linksModel.forEach((item, itemIndex) => {
        const isLinkClicked = itemIndex === index;

        item.active = isLinkClicked;
      });

      if (link.click) {
        link.click();
      }

      this.inactiveGroupLinks();
    },
    handleClick({ menuIndex, linkIndex, link }) {
      this.groupModel.forEach((links, mIndex) => {
        links.items.forEach((link, lIndex) => {
          const isLinkClicked = (mIndex === menuIndex && lIndex === linkIndex);

          link.active = isLinkClicked;
        });
      });

      if (link.click) {
        link.click();
      }

      this.inactiveLinks();
    }
  }
};

/* script */
const __vue_script__$1Z = script$1Z;

/* template */
var __vue_render__$1Z = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "sidebar-menu" },
    [
      _c("me-nested-menu", {
        attrs: { "title-menu": _vm.title },
        scopedSlots: _vm._u([
          {
            key: "links",
            fn: function() {
              return _vm._l(_vm.linksModel, function(link, index) {
                return _c(
                  "a",
                  {
                    key: index,
                    class: [link.active ? "active" : "", link.class],
                    attrs: { href: "#", "data-jest": "link" },
                    on: {
                      click: function($event) {
                        $event.preventDefault();
                        return link.click($event)
                      }
                    }
                  },
                  [
                    _vm._v("\n        " + _vm._s(link.label) + "\n        "),
                    link.icon
                      ? _c("i", { staticClass: "link-icon", class: link.icon })
                      : _vm._e()
                  ]
                )
              })
            },
            proxy: true
          },
          {
            key: "listgroup",
            fn: function() {
              return _vm._l(_vm.groupModel, function(item) {
                return _c("me-nested-menu", {
                  key: item.title,
                  attrs: {
                    "title-menu": item.title,
                    items: item.items,
                    opened: item.opened
                  }
                })
              })
            },
            proxy: true
          }
        ])
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$1Z = [];
__vue_render__$1Z._withStripped = true;

  /* style */
  const __vue_inject_styles__$1Z = function (inject) {
    if (!inject) return
    inject("data-v-23dafc4a_0", { source: ".link-icon[data-v-23dafc4a] {\n  color: #FCBD6E;\n  margin-left: 8px;\n  text-indent: 0;\n}\n.sidebar-menu[data-v-23dafc4a] > div.me-nested-menu > section.me-collapse > h5 {\n  color: var(--me-gray-4) !important;\n  text-transform: uppercase;\n}\n.sidebar-menu[data-v-23dafc4a] > div.me-nested-menu > section.me-collapse > h5 .me-icon-l {\n  color: var(--me-gray-4);\n}\n[data-v-23dafc4a] .me-nested-menu div.nested-component div.me-nested-menu {\n  margin-left: 0;\n  padding-left: 0;\n}\n\n/*# sourceMappingURL=SidebarMenu.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SidebarMenu.vue","SidebarMenu.vue"],"names":[],"mappings":"AAwJA;EACA,cAAA;EACA,gBAAA;EACA,cAAA;ACvJA;AD0JA;EAKA,kCAAA;EACA,yBAAA;AC3JA;ADsJA;EACA,uBAAA;ACpJA;AD2JA;EACA,cAAA;EACA,eAAA;ACxJA;;AAEA,0CAA0C","file":"SidebarMenu.vue","sourcesContent":["<template>\n  <div class=\"sidebar-menu\">\n    <me-nested-menu :title-menu=\"title\">\n      <template #links>\n        <a\n          v-for=\"(link, index) in linksModel\"\n          :key=\"index\"\n          :class=\"[ link.active ? 'active' : '', link.class ]\"\n          @click.prevent=\"link.click\"\n          href=\"#\"\n          data-jest=\"link\"\n        >\n          {{ link.label }}\n          <i v-if=\"link.icon\" :class=\"link.icon\" class=\"link-icon\" />\n        </a>\n      </template>\n\n      <template #listgroup>\n        <me-nested-menu\n          v-for=\"item in groupModel\"\n          :title-menu=\"item.title\"\n          :items=\"item.items\"\n          :key=\"item.title\"\n          :opened=\"item.opened\"\n        />\n      </template>\n    </me-nested-menu>\n  </div>\n</template>\n\n<script>\nimport MeNestedMenu from './nestedMenu/NestedMenu.vue'\n\nexport default {\n  name: 'MeSidebarMenu',\n  components: {\n    MeNestedMenu\n  },\n  props: {\n    title: {\n      type: String,\n      required: true\n    },\n    links: {\n      type: Array,\n      default: () => []\n    },\n    group: {\n      type: Array,\n      default: () => []\n    }\n  },\n  data() {\n    return {\n      linksModel: this.getLinksModel(),\n      groupModel: this.getItemsModel()\n    }\n  },\n\n  watch: {\n    links() {\n      this.linksModel = this.getLinksModel()\n    },\n    group() {\n      this.groupModel = this.getItemsModel()\n    }\n  },\n\n  methods: {\n    createItemModel(item, menuIndex) {\n      item.items = item.items.map((link, linkIndex) => ({\n        ...link,\n        class: link.class ?? '',\n        icon: link.icon ?? '',\n        active: link.active ?? false,\n        opened: item.opened ?? true,\n        click: () => this.handleClick({ menuIndex, linkIndex, link })\n      }))\n\n      return item\n    },\n    createMenuModel(item) {\n      const parentItem = {\n        titleMenu: true,\n        label: item.title,\n        icon: item.icon ?? '',\n        active: item.active ?? false,\n        click: item.click\n      }\n\n      item.items.splice(0, 0, parentItem)\n\n      return item\n    },\n    getLinksModel() {\n      return this.links.map((link, index) => ({\n        ...link,\n        icon: link.icon ?? '',\n        active: link.active ?? false,\n        click: () => this.handleLinkClick({ index, link })\n      }))\n    },\n    getItemsModel() {\n      return this.group\n        .map(this.createMenuModel)\n        .map(this.createItemModel)\n    },\n    inactiveGroupLinks() {\n      this.groupModel.forEach((links) => {\n        links.items.forEach((link) => {\n          link.active = false\n        })\n      })\n    },\n    inactiveLinks() {\n      this.linksModel.forEach((item) => {\n        item.active = false\n      })\n    },\n    handleLinkClick({ index, link }) {\n      this.linksModel.forEach((item, itemIndex) => {\n        const isLinkClicked = itemIndex === index\n\n        item.active = isLinkClicked\n      })\n\n      if (link.click) {\n        link.click()\n      }\n\n      this.inactiveGroupLinks()\n    },\n    handleClick({ menuIndex, linkIndex, link }) {\n      this.groupModel.forEach((links, mIndex) => {\n        links.items.forEach((link, lIndex) => {\n          const isLinkClicked = (mIndex === menuIndex && lIndex === linkIndex)\n\n          link.active = isLinkClicked\n        })\n      })\n\n      if (link.click) {\n        link.click()\n      }\n\n      this.inactiveLinks()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.link-icon {\n  color: #FCBD6E;\n  margin-left: 8px;\n  text-indent: 0;\n}\n\n.sidebar-menu::v-deep > div.me-nested-menu > section.me-collapse > h5 {\n  .me-icon-l {\n    color: var(--me-gray-4);\n  }\n\n  color: var(--me-gray-4) !important;\n  text-transform: uppercase;\n}\n\n::v-deep .me-nested-menu div.nested-component div.me-nested-menu {\n  margin-left: 0;\n  padding-left: 0;\n}\n</style>\n",".link-icon {\n  color: #FCBD6E;\n  margin-left: 8px;\n  text-indent: 0;\n}\n\n.sidebar-menu::v-deep > div.me-nested-menu > section.me-collapse > h5 {\n  color: var(--me-gray-4) !important;\n  text-transform: uppercase;\n}\n.sidebar-menu::v-deep > div.me-nested-menu > section.me-collapse > h5 .me-icon-l {\n  color: var(--me-gray-4);\n}\n\n::v-deep .me-nested-menu div.nested-component div.me-nested-menu {\n  margin-left: 0;\n  padding-left: 0;\n}\n\n/*# sourceMappingURL=SidebarMenu.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1Z = "data-v-23dafc4a";
  /* module identifier */
  const __vue_module_identifier__$1Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$1Z = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1Z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1Z, staticRenderFns: __vue_staticRenderFns__$1Z },
    __vue_inject_styles__$1Z,
    __vue_script__$1Z,
    __vue_scope_id__$1Z,
    __vue_is_functional_template__$1Z,
    __vue_module_identifier__$1Z,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1_ = {
  name: 'MeSkeletonAddressCard',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$1_ = script$1_;

/* template */
var __vue_render__$1_ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-address-card" }, [
    _c(
      "header",
      [
        _c("BSkeleton", { attrs: { height: "15px", width: "60px" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "25px", width: "60%" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "15px", width: "20%" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "20px", width: "80%" } })
      ],
      1
    ),
    _vm._v(" "),
    _c(
      "main",
      [
        _c("BSkeleton", { attrs: { height: "20px", width: "100px" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "20px", width: "150px" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "20px", width: "130px" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "20px", width: "130px" } })
      ],
      1
    )
  ])
};
var __vue_staticRenderFns__$1_ = [];
__vue_render__$1_._withStripped = true;

  /* style */
  const __vue_inject_styles__$1_ = function (inject) {
    if (!inject) return
    inject("data-v-6769829e_0", { source: ".me-skeleton-address-card[data-v-6769829e] {\n  background-color: var(--me-white);\n  border-radius: 8px;\n  box-shadow: var(--me-shadow-level-1);\n  padding: 10px 15px;\n}\n.me-skeleton-address-card > main[data-v-6769829e] {\n  padding-left: 15px;\n}\n\n/*# sourceMappingURL=SkeletonAddressCard.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonAddressCard.vue","SkeletonAddressCard.vue"],"names":[],"mappings":"AA6BA;EACA,iCAAA;EACA,kBAAA;EACA,oCAAA;EACA,kBAAA;AC5BA;AD8BA;EACA,kBAAA;AC5BA;;AAEA,kDAAkD","file":"SkeletonAddressCard.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-address-card\">\n    <header>\n      <BSkeleton height=\"15px\" width=\"60px\" />\n      <BSkeleton height=\"25px\" width=\"60%\" />\n      <BSkeleton height=\"15px\" width=\"20%\" />\n      <BSkeleton height=\"20px\" width=\"80%\" />\n    </header>\n    <main>\n      <BSkeleton height=\"20px\" width=\"100px\" />\n      <BSkeleton height=\"20px\" width=\"150px\" />\n      <BSkeleton height=\"20px\" width=\"130px\" />\n      <BSkeleton height=\"20px\" width=\"130px\" />\n    </main>\n  </div>\n</template>\n\n<script>\nimport { BSkeleton } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonAddressCard',\n  components: {\n    BSkeleton\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-address-card {\n    background-color: var(--me-white);\n    border-radius: 8px;\n    box-shadow: var(--me-shadow-level-1);\n    padding: 10px 15px;\n\n    > main {\n      padding-left: 15px;\n    }\n  }\n</style>\n",".me-skeleton-address-card {\n  background-color: var(--me-white);\n  border-radius: 8px;\n  box-shadow: var(--me-shadow-level-1);\n  padding: 10px 15px;\n}\n.me-skeleton-address-card > main {\n  padding-left: 15px;\n}\n\n/*# sourceMappingURL=SkeletonAddressCard.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1_ = "data-v-6769829e";
  /* module identifier */
  const __vue_module_identifier__$1_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$1_ = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1_ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1_, staticRenderFns: __vue_staticRenderFns__$1_ },
    __vue_inject_styles__$1_,
    __vue_script__$1_,
    __vue_scope_id__$1_,
    __vue_is_functional_template__$1_,
    __vue_module_identifier__$1_,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$1$ = {
  name: 'MeSkeletonButtonBar',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$1$ = script$1$;

/* template */
var __vue_render__$1$ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-button-bar" }, [
    _c(
      "div",
      { staticClass: "button-bar-actions" },
      [
        _c("BSkeleton", { attrs: { type: "button" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { type: "button" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { type: "button" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { type: "button" } })
      ],
      1
    ),
    _vm._v(" "),
    _c(
      "div",
      { staticClass: "button-bar-options" },
      [_c("BSkeleton", { attrs: { type: "button" } })],
      1
    )
  ])
};
var __vue_staticRenderFns__$1$ = [];
__vue_render__$1$._withStripped = true;

  /* style */
  const __vue_inject_styles__$1$ = function (inject) {
    if (!inject) return
    inject("data-v-c3622a4a_0", { source: ".me-skeleton-button-bar[data-v-c3622a4a] {\n  display: flex;\n}\n.me-skeleton-button-bar > .button-bar-actions[data-v-c3622a4a], .me-skeleton-button-bar > .button-bar-options[data-v-c3622a4a] {\n  margin: 0 -5px;\n  display: flex;\n  flex-grow: 1;\n}\n.me-skeleton-button-bar > .button-bar-actions > div[data-v-c3622a4a], .me-skeleton-button-bar > .button-bar-options > div[data-v-c3622a4a] {\n  margin: 0 5px;\n}\n.me-skeleton-button-bar > .button-bar-options[data-v-c3622a4a] {\n  flex-direction: column;\n  flex-wrap: wrap-reverse;\n}\n\n/*# sourceMappingURL=SkeletonButtonBar.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonButtonBar.vue","SkeletonButtonBar.vue"],"names":[],"mappings":"AA0BA;EACA,aAAA;ACzBA;AD2BA;EACA,cAAA;EACA,aAAA;EACA,YAAA;ACzBA;AD2BA;EACA,aAAA;ACzBA;AD6BA;EACA,sBAAA;EACA,uBAAA;AC3BA;;AAEA,gDAAgD","file":"SkeletonButtonBar.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-button-bar\">\n    <div class=\"button-bar-actions\">\n      <BSkeleton type=\"button\" />\n      <BSkeleton type=\"button\" />\n      <BSkeleton type=\"button\" />\n      <BSkeleton type=\"button\" />\n    </div>\n    <div class=\"button-bar-options\">\n      <BSkeleton type=\"button\" />\n    </div>\n  </div>\n</template>\n\n<script>\nimport { BSkeleton } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonButtonBar',\n  components: {\n    BSkeleton\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-button-bar {\n    display: flex;\n\n    > .button-bar-actions, >.button-bar-options {\n      margin: 0 -5px;\n      display: flex;\n      flex-grow: 1;\n\n      > div {\n        margin: 0 5px;\n      }\n    }\n\n    > .button-bar-options {\n      flex-direction: column;\n      flex-wrap: wrap-reverse;\n    }\n  }\n</style>\n",".me-skeleton-button-bar {\n  display: flex;\n}\n.me-skeleton-button-bar > .button-bar-actions, .me-skeleton-button-bar > .button-bar-options {\n  margin: 0 -5px;\n  display: flex;\n  flex-grow: 1;\n}\n.me-skeleton-button-bar > .button-bar-actions > div, .me-skeleton-button-bar > .button-bar-options > div {\n  margin: 0 5px;\n}\n.me-skeleton-button-bar > .button-bar-options {\n  flex-direction: column;\n  flex-wrap: wrap-reverse;\n}\n\n/*# sourceMappingURL=SkeletonButtonBar.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1$ = "data-v-c3622a4a";
  /* module identifier */
  const __vue_module_identifier__$1$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$1$ = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1$ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1$, staticRenderFns: __vue_staticRenderFns__$1$ },
    __vue_inject_styles__$1$,
    __vue_script__$1$,
    __vue_scope_id__$1$,
    __vue_is_functional_template__$1$,
    __vue_module_identifier__$1$,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$20 = {
  name: 'MeSkeletonTable',
  components: {
    BSkeletonTable
  },
  props: {
    columns: {
      type: Number,
      default: 4
    },
    rows: {
      type: Number,
      default: 5
    }
  }
};

/* script */
const __vue_script__$20 = script$20;

/* template */
var __vue_render__$20 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-skeleton-table" },
    [
      _c("div"),
      _vm._v(" "),
      _c("BSkeletonTable", {
        attrs: {
          columns: _vm.columns,
          rows: _vm.rows,
          "table-props": { bordered: true, striped: true }
        }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$20 = [];
__vue_render__$20._withStripped = true;

  /* style */
  const __vue_inject_styles__$20 = function (inject) {
    if (!inject) return
    inject("data-v-5d46723f_0", { source: ".me-skeleton-table[data-v-5d46723f] {\n  position: relative;\n  margin-bottom: -1rem;\n}\n.me-skeleton-table > div[data-v-5d46723f] {\n  background-image: linear-gradient(to bottom, rgba(255, 0, 0, 0), white);\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  z-index: 1;\n}\n\n/*# sourceMappingURL=SkeletonTable.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonTable.vue","SkeletonTable.vue"],"names":[],"mappings":"AAiCA;EACA,kBAAA;EACA,oBAAA;AChCA;ADkCA;EACA,uEAAA;EACA,kBAAA;EACA,MAAA;EACA,OAAA;EACA,QAAA;EACA,SAAA;EACA,UAAA;AChCA;;AAEA,4CAA4C","file":"SkeletonTable.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-table\">\n    <div />\n    <BSkeletonTable\n      :columns=\"columns\"\n      :rows=\"rows\"\n      :table-props=\"{ bordered: true, striped: true }\"\n    />\n  </div>\n</template>\n\n<script>\nimport { BSkeletonTable } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonTable',\n  components: {\n    BSkeletonTable\n  },\n  props: {\n    columns: {\n      type: Number,\n      default: 4\n    },\n    rows: {\n      type: Number,\n      default: 5\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-table {\n    position: relative;\n    margin-bottom: -1rem;\n\n    > div {\n      background-image: linear-gradient(to bottom, rgba(255,0,0,0), rgb(255, 255, 255));\n      position: absolute;\n      top: 0;\n      left: 0;\n      right: 0;\n      bottom: 0;\n      z-index: 1;\n    }\n  }\n</style>\n",".me-skeleton-table {\n  position: relative;\n  margin-bottom: -1rem;\n}\n.me-skeleton-table > div {\n  background-image: linear-gradient(to bottom, rgba(255, 0, 0, 0), white);\n  position: absolute;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  z-index: 1;\n}\n\n/*# sourceMappingURL=SkeletonTable.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$20 = "data-v-5d46723f";
  /* module identifier */
  const __vue_module_identifier__$20 = undefined;
  /* functional template */
  const __vue_is_functional_template__$20 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$20 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$20, staticRenderFns: __vue_staticRenderFns__$20 },
    __vue_inject_styles__$20,
    __vue_script__$20,
    __vue_scope_id__$20,
    __vue_is_functional_template__$20,
    __vue_module_identifier__$20,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$21 = {
  name: 'MeSkeletonForehead',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$21 = script$21;

/* template */
var __vue_render__$21 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-forehead" }, [
    _c(
      "div",
      { staticClass: "forehead-badge" },
      [
        _c("BSkeleton", { attrs: { height: "20px", width: "60%" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { height: "30px", width: "85%" } })
      ],
      1
    ),
    _vm._v(" "),
    _c("div", { staticClass: "forehead-content" }, [
      _c(
        "div",
        { staticClass: "left" },
        [
          _c("BSkeleton", { attrs: { height: "30px", width: "100%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "30%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "50%" } })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "right" },
        [
          _c("BSkeleton", { attrs: { height: "30px", width: "50%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "35%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "85%" } })
        ],
        1
      )
    ])
  ])
};
var __vue_staticRenderFns__$21 = [];
__vue_render__$21._withStripped = true;

  /* style */
  const __vue_inject_styles__$21 = function (inject) {
    if (!inject) return
    inject("data-v-3a2ca4c4_0", { source: ".me-skeleton-forehead[data-v-3a2ca4c4] {\n  display: flex;\n}\n.me-skeleton-forehead > .forehead-badge[data-v-3a2ca4c4] {\n  background-color: var(--me-primary-2);\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 10px;\n  border-top-left-radius: 0;\n  border-top-right-radius: 10px;\n  box-sizing: border-box;\n  color: var(--me-white);\n  font-size: 18px;\n  height: 90px;\n  padding: 16px;\n  position: relative;\n  width: 200px;\n}\n.me-skeleton-forehead > .forehead-content[data-v-3a2ca4c4] {\n  display: flex;\n  flex-grow: 1;\n  font-size: 12px;\n  margin: 0 -10px;\n  padding-left: 15px;\n}\n.me-skeleton-forehead > .forehead-content > .left[data-v-3a2ca4c4], .me-skeleton-forehead > .forehead-content > .right[data-v-3a2ca4c4] {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  margin: 0 10px;\n}\n.me-skeleton-forehead > .forehead-content > .left[data-v-3a2ca4c4] {\n  flex-grow: 2;\n}\n.me-skeleton-forehead > .forehead-content > .right[data-v-3a2ca4c4] {\n  flex-grow: 1;\n  flex-wrap: wrap-reverse;\n}\n\n/*# sourceMappingURL=SkeletonForehead.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonForehead.vue","SkeletonForehead.vue"],"names":[],"mappings":"AAiCA;EACA,aAAA;AChCA;ADkCA;EACA,qCAAA;EACA,4BAAA;EACA,gCAAA;EACA,yBAAA;EACA,6BAAA;EACA,sBAAA;EACA,sBAAA;EACA,eAAA;EACA,YAAA;EACA,aAAA;EACA,kBAAA;EACA,YAAA;AChCA;ADmCA;EACA,aAAA;EACA,YAAA;EACA,eAAA;EACA,eAAA;EACA,kBAAA;ACjCA;ADmCA;EACA,aAAA;EACA,sBAAA;EACA,8BAAA;EACA,cAAA;ACjCA;ADoCA;EACA,YAAA;AClCA;ADqCA;EACA,YAAA;EACA,uBAAA;ACnCA;;AAEA,+CAA+C","file":"SkeletonForehead.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-forehead\">\n    <div class=\"forehead-badge\">\n      <BSkeleton height=\"20px\" width=\"60%\" />\n      <BSkeleton height=\"30px\" width=\"85%\" />\n    </div>\n    <div class=\"forehead-content\">\n      <div class=\"left\">\n        <BSkeleton height=\"30px\" width=\"100%\" />\n        <BSkeleton width=\"30%\" />\n        <BSkeleton width=\"50%\" />\n      </div>\n      <div class=\"right\">\n        <BSkeleton height=\"30px\" width=\"50%\" />\n        <BSkeleton width=\"35%\" />\n        <BSkeleton width=\"85%\" />\n      </div>\n    </div>\n  </div>\n</template>\n\n<script>\nimport { BSkeleton } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonForehead',\n  components: {\n    BSkeleton\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-forehead {\n    display: flex;\n\n    > .forehead-badge {\n      background-color: var(--me-primary-2);\n      border-bottom-left-radius: 0;\n      border-bottom-right-radius: 10px;\n      border-top-left-radius: 0;\n      border-top-right-radius: 10px;\n      box-sizing: border-box;\n      color: var(--me-white);\n      font-size: 18px;\n      height: 90px;\n      padding: 16px;\n      position: relative;\n      width: 200px;\n    }\n\n    > .forehead-content {\n      display: flex;\n      flex-grow: 1;\n      font-size: 12px;\n      margin: 0 -10px;\n      padding-left: 15px;\n\n      > .left, >.right {\n        display: flex;\n        flex-direction: column;\n        justify-content: space-between;\n        margin: 0 10px;\n      }\n\n      > .left {\n        flex-grow: 2;\n      }\n\n      > .right {\n        flex-grow: 1;\n        flex-wrap: wrap-reverse;\n      }\n    }\n  }\n</style>\n",".me-skeleton-forehead {\n  display: flex;\n}\n.me-skeleton-forehead > .forehead-badge {\n  background-color: var(--me-primary-2);\n  border-bottom-left-radius: 0;\n  border-bottom-right-radius: 10px;\n  border-top-left-radius: 0;\n  border-top-right-radius: 10px;\n  box-sizing: border-box;\n  color: var(--me-white);\n  font-size: 18px;\n  height: 90px;\n  padding: 16px;\n  position: relative;\n  width: 200px;\n}\n.me-skeleton-forehead > .forehead-content {\n  display: flex;\n  flex-grow: 1;\n  font-size: 12px;\n  margin: 0 -10px;\n  padding-left: 15px;\n}\n.me-skeleton-forehead > .forehead-content > .left, .me-skeleton-forehead > .forehead-content > .right {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n  margin: 0 10px;\n}\n.me-skeleton-forehead > .forehead-content > .left {\n  flex-grow: 2;\n}\n.me-skeleton-forehead > .forehead-content > .right {\n  flex-grow: 1;\n  flex-wrap: wrap-reverse;\n}\n\n/*# sourceMappingURL=SkeletonForehead.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$21 = "data-v-3a2ca4c4";
  /* module identifier */
  const __vue_module_identifier__$21 = undefined;
  /* functional template */
  const __vue_is_functional_template__$21 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$21 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$21, staticRenderFns: __vue_staticRenderFns__$21 },
    __vue_inject_styles__$21,
    __vue_script__$21,
    __vue_scope_id__$21,
    __vue_is_functional_template__$21,
    __vue_module_identifier__$21,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$22 = {
  name: 'MeSkeletonSupplierCard',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$22 = script$22;

/* template */
var __vue_render__$22 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-supplier-card" }, [
    _c(
      "header",
      [
        _c("BSkeleton", { attrs: { width: "140px" } }),
        _vm._v(" "),
        _c("BSkeleton", { attrs: { width: "40%" } })
      ],
      1
    ),
    _vm._v(" "),
    _c("main", [
      _c(
        "div",
        [
          _c("BSkeleton", { attrs: { width: "50%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "30%" } })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        [
          _c("BSkeleton", { attrs: { width: "60%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "20%" } })
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        [
          _c("BSkeleton", { attrs: { width: "70%" } }),
          _vm._v(" "),
          _c("BSkeleton", { attrs: { width: "40%" } })
        ],
        1
      )
    ])
  ])
};
var __vue_staticRenderFns__$22 = [];
__vue_render__$22._withStripped = true;

  /* style */
  const __vue_inject_styles__$22 = function (inject) {
    if (!inject) return
    inject("data-v-8ac9a92e_0", { source: ".me-skeleton-supplier-card[data-v-8ac9a92e] {\n  background: white;\n  border-radius: 6px;\n  box-shadow: var(--me-shadow-level-1);\n  font-size: 12px;\n  padding: 10px 15px 5px 15px;\n}\n.me-skeleton-supplier-card > header[data-v-8ac9a92e] {\n  margin-bottom: 10px;\n}\n.me-skeleton-supplier-card > main[data-v-8ac9a92e] {\n  display: flex;\n  margin: 0 -10px;\n}\n.me-skeleton-supplier-card > main > div[data-v-8ac9a92e] {\n  flex-grow: 1;\n  margin: 0 10px;\n}\n\n/*# sourceMappingURL=SkeletonSupplierCard.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonSupplierCard.vue","SkeletonSupplierCard.vue"],"names":[],"mappings":"AAmCA;EACA,iBAAA;EACA,kBAAA;EACA,oCAAA;EACA,eAAA;EACA,2BAAA;AClCA;ADoCA;EACA,mBAAA;AClCA;ADqCA;EACA,aAAA;EACA,eAAA;ACnCA;ADqCA;EACA,YAAA;EACA,cAAA;ACnCA;;AAEA,mDAAmD","file":"SkeletonSupplierCard.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-supplier-card\">\n    <header>\n      <BSkeleton width=\"140px\" />\n      <BSkeleton width=\"40%\" />\n    </header>\n    <main>\n      <div>\n        <BSkeleton width=\"50%\" />\n        <BSkeleton width=\"30%\" />\n      </div>\n      <div>\n        <BSkeleton width=\"60%\" />\n        <BSkeleton width=\"20%\" />\n      </div>\n      <div>\n        <BSkeleton width=\"70%\" />\n        <BSkeleton width=\"40%\" />\n      </div>\n    </main>\n  </div>\n</template>\n\n<script>\nimport { BSkeleton } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeSkeletonSupplierCard',\n  components: {\n    BSkeleton\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-supplier-card {\n    background: white;\n    border-radius: 6px;\n    box-shadow: var(--me-shadow-level-1);\n    font-size: 12px;\n    padding: 10px 15px 5px 15px;\n\n    > header {\n      margin-bottom: 10px;\n    }\n\n    > main {\n      display: flex;\n      margin: 0 -10px;\n\n      > div {\n        flex-grow: 1;\n        margin: 0 10px;\n      }\n    }\n  }\n</style>\n",".me-skeleton-supplier-card {\n  background: white;\n  border-radius: 6px;\n  box-shadow: var(--me-shadow-level-1);\n  font-size: 12px;\n  padding: 10px 15px 5px 15px;\n}\n.me-skeleton-supplier-card > header {\n  margin-bottom: 10px;\n}\n.me-skeleton-supplier-card > main {\n  display: flex;\n  margin: 0 -10px;\n}\n.me-skeleton-supplier-card > main > div {\n  flex-grow: 1;\n  margin: 0 10px;\n}\n\n/*# sourceMappingURL=SkeletonSupplierCard.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$22 = "data-v-8ac9a92e";
  /* module identifier */
  const __vue_module_identifier__$22 = undefined;
  /* functional template */
  const __vue_is_functional_template__$22 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$22 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$22, staticRenderFns: __vue_staticRenderFns__$22 },
    __vue_inject_styles__$22,
    __vue_script__$22,
    __vue_scope_id__$22,
    __vue_is_functional_template__$22,
    __vue_module_identifier__$22,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$23 = {
  name: 'MeSkeletonDocument',
  components: {
    SkeletonTable: __vue_component__$20,
    SkeletonAddressCard: __vue_component__$1_,
    SkeletonButtonBar: __vue_component__$1$,
    SkeletonForehead: __vue_component__$21,
    SkeletonSupplierCard: __vue_component__$22
  }
};

/* script */
const __vue_script__$23 = script$23;

/* template */
var __vue_render__$23 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-skeleton-document" }, [
    _c("header", [_c("SkeletonButtonBar")], 1),
    _vm._v(" "),
    _c("main", [
      _c("div", { staticClass: "document-paper" }, [
        _c("header", [_c("SkeletonForehead")], 1),
        _vm._v(" "),
        _c(
          "main",
          [
            _c(
              "div",
              { staticClass: "document-supplier" },
              [_c("SkeletonSupplierCard")],
              1
            ),
            _vm._v(" "),
            _c("div", { staticClass: "document-addresses" }, [
              _c("div", [_c("SkeletonAddressCard")], 1),
              _vm._v(" "),
              _c("div", [_c("SkeletonAddressCard")], 1),
              _vm._v(" "),
              _c("div", [_c("SkeletonAddressCard")], 1)
            ]),
            _vm._v(" "),
            _c("SkeletonTable", { attrs: { rows: 5, columns: 4 } })
          ],
          1
        )
      ])
    ])
  ])
};
var __vue_staticRenderFns__$23 = [];
__vue_render__$23._withStripped = true;

  /* style */
  const __vue_inject_styles__$23 = function (inject) {
    if (!inject) return
    inject("data-v-7bfe89ce_0", { source: ".me-skeleton-document > header[data-v-7bfe89ce] {\n  background: var(--me-white);\n  border-bottom: 1px solid #eee;\n  display: flex;\n  flex-flow: column;\n  height: 50px;\n  justify-content: center;\n  padding: 0 10px;\n}\n.me-skeleton-document > main[data-v-7bfe89ce] {\n  background-color: whitesmoke;\n  padding: 10px;\n}\n.me-skeleton-document > main > .document-paper[data-v-7bfe89ce] {\n  background: var(--me-white);\n  border-top: 1px solid #eee;\n  box-shadow: var(--me-shadow-level-2);\n  margin-bottom: 15px;\n  padding: 20px;\n}\n.me-skeleton-document > main > .document-paper > header[data-v-7bfe89ce] {\n  margin: 0 0 20px -20px;\n}\n.me-skeleton-document > main > .document-paper > main div.document-supplier[data-v-7bfe89ce] {\n  margin-bottom: 1rem;\n}\n.me-skeleton-document > main > .document-paper > main div.document-addresses[data-v-7bfe89ce] {\n  display: flex;\n  margin: 0 -10px 1rem -10px;\n}\n.me-skeleton-document > main > .document-paper > main div.document-addresses > div[data-v-7bfe89ce] {\n  flex-grow: 1;\n  margin: 0 10px;\n}\n\n/*# sourceMappingURL=SkeletonDocument.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SkeletonDocument.vue","SkeletonDocument.vue"],"names":[],"mappings":"AAwDA;EACA,2BAAA;EACA,6BAAA;EACA,aAAA;EACA,iBAAA;EACA,YAAA;EACA,uBAAA;EACA,eAAA;ACvDA;AD0DA;EACA,4BAAA;EACA,aAAA;ACxDA;AD0DA;EACA,2BAAA;EACA,0BAAA;EACA,oCAAA;EACA,mBAAA;EACA,aAAA;ACxDA;AD0DA;EACA,sBAAA;ACxDA;AD4DA;EACA,mBAAA;AC1DA;AD6DA;EACA,aAAA;EACA,0BAAA;AC3DA;AD6DA;EACA,YAAA;EACA,cAAA;AC3DA;;AAEA,+CAA+C","file":"SkeletonDocument.vue","sourcesContent":["<template>\n  <div class=\"me-skeleton-document\">\n    <header>\n      <SkeletonButtonBar />\n    </header>\n    <main>\n      <div class=\"document-paper\">\n        <header>\n          <SkeletonForehead />\n        </header>\n        <main>\n          <div class=\"document-supplier\">\n            <SkeletonSupplierCard />\n          </div>\n          <div class=\"document-addresses\">\n            <div>\n              <SkeletonAddressCard />\n            </div>\n            <div>\n              <SkeletonAddressCard />\n            </div>\n            <div>\n              <SkeletonAddressCard />\n            </div>\n          </div>\n          <SkeletonTable\n            :rows=\"5\"\n            :columns=\"4\"\n          />\n        </main>\n      </div>\n    </main>\n  </div>\n</template>\n\n<script>\nimport SkeletonTable from './SkeletonTable.vue'\nimport SkeletonAddressCard from './SkeletonAddressCard.vue'\nimport SkeletonButtonBar from './SkeletonButtonBar.vue'\nimport SkeletonForehead from './SkeletonForehead.vue'\nimport SkeletonSupplierCard from './SkeletonSupplierCard.vue'\n\nexport default {\n  name: 'MeSkeletonDocument',\n  components: {\n    SkeletonTable,\n    SkeletonAddressCard,\n    SkeletonButtonBar,\n    SkeletonForehead,\n    SkeletonSupplierCard\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-skeleton-document {\n    > header {\n      background: var(--me-white);\n      border-bottom: 1px solid #eee;\n      display: flex;\n      flex-flow: column;\n      height: 50px;\n      justify-content: center;\n      padding: 0 10px;\n    }\n\n    > main {\n      background-color: whitesmoke;\n      padding: 10px;\n\n      > .document-paper {\n        background: var(--me-white);\n        border-top: 1px solid #eee;\n        box-shadow: var(--me-shadow-level-2);\n        margin-bottom: 15px;\n        padding: 20px;\n\n        > header {\n          margin: 0 0 20px -20px;\n        }\n\n        > main {\n          div.document-supplier {\n            margin-bottom: 1rem;\n          }\n\n          div.document-addresses {\n            display: flex;\n            margin: 0 -10px 1rem -10px;\n\n            > div {\n              flex-grow: 1;\n              margin: 0 10px;\n            }\n          }\n        }\n      }\n    }\n  }\n</style>\n",".me-skeleton-document > header {\n  background: var(--me-white);\n  border-bottom: 1px solid #eee;\n  display: flex;\n  flex-flow: column;\n  height: 50px;\n  justify-content: center;\n  padding: 0 10px;\n}\n.me-skeleton-document > main {\n  background-color: whitesmoke;\n  padding: 10px;\n}\n.me-skeleton-document > main > .document-paper {\n  background: var(--me-white);\n  border-top: 1px solid #eee;\n  box-shadow: var(--me-shadow-level-2);\n  margin-bottom: 15px;\n  padding: 20px;\n}\n.me-skeleton-document > main > .document-paper > header {\n  margin: 0 0 20px -20px;\n}\n.me-skeleton-document > main > .document-paper > main div.document-supplier {\n  margin-bottom: 1rem;\n}\n.me-skeleton-document > main > .document-paper > main div.document-addresses {\n  display: flex;\n  margin: 0 -10px 1rem -10px;\n}\n.me-skeleton-document > main > .document-paper > main div.document-addresses > div {\n  flex-grow: 1;\n  margin: 0 10px;\n}\n\n/*# sourceMappingURL=SkeletonDocument.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$23 = "data-v-7bfe89ce";
  /* module identifier */
  const __vue_module_identifier__$23 = undefined;
  /* functional template */
  const __vue_is_functional_template__$23 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$23 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$23, staticRenderFns: __vue_staticRenderFns__$23 },
    __vue_inject_styles__$23,
    __vue_script__$23,
    __vue_scope_id__$23,
    __vue_is_functional_template__$23,
    __vue_module_identifier__$23,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$24 = {
  name: 'MeSkeletonLoadCard',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$24 = script$24;

/* template */
var __vue_render__$24 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-skeleton-load-card" },
    [
      _c("BSkeleton", {
        staticStyle: { margin: "10px" },
        attrs: { width: "200px", height: "150px", type: "button" }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$24 = [];
__vue_render__$24._withStripped = true;

  /* style */
  const __vue_inject_styles__$24 = undefined;
  /* scoped */
  const __vue_scope_id__$24 = undefined;
  /* module identifier */
  const __vue_module_identifier__$24 = undefined;
  /* functional template */
  const __vue_is_functional_template__$24 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$24 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$24, staticRenderFns: __vue_staticRenderFns__$24 },
    __vue_inject_styles__$24,
    __vue_script__$24,
    __vue_scope_id__$24,
    __vue_is_functional_template__$24,
    __vue_module_identifier__$24,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$25 = {
  name: 'MeSkeletonLoadHistory',
  components: {
    BSkeleton
  }
};

/* script */
const __vue_script__$25 = script$25;

/* template */
var __vue_render__$25 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-skeleton-load-history" },
    [
      _c("BSkeleton", {
        staticStyle: { margin: "2px 0" },
        attrs: { width: "100%", height: "98px", type: "button" }
      })
    ],
    1
  )
};
var __vue_staticRenderFns__$25 = [];
__vue_render__$25._withStripped = true;

  /* style */
  const __vue_inject_styles__$25 = undefined;
  /* scoped */
  const __vue_scope_id__$25 = undefined;
  /* module identifier */
  const __vue_module_identifier__$25 = undefined;
  /* functional template */
  const __vue_is_functional_template__$25 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$25 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$25, staticRenderFns: __vue_staticRenderFns__$25 },
    __vue_inject_styles__$25,
    __vue_script__$25,
    __vue_scope_id__$25,
    __vue_is_functional_template__$25,
    __vue_module_identifier__$25,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$26 = {
  name: 'MeSkeletonText',
  components: {
    BSkeleton
  },
  props: {
    width: {
      type: String,
      default: null
    },
    height: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: null
    }
  }
};

/* script */
const __vue_script__$26 = script$26;

/* template */
var __vue_render__$26 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("BSkeleton", {
    attrs: { width: _vm.width, height: _vm.height, variant: _vm.variant }
  })
};
var __vue_staticRenderFns__$26 = [];
__vue_render__$26._withStripped = true;

  /* style */
  const __vue_inject_styles__$26 = undefined;
  /* scoped */
  const __vue_scope_id__$26 = undefined;
  /* module identifier */
  const __vue_module_identifier__$26 = undefined;
  /* functional template */
  const __vue_is_functional_template__$26 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$26 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$26, staticRenderFns: __vue_staticRenderFns__$26 },
    __vue_inject_styles__$26,
    __vue_script__$26,
    __vue_scope_id__$26,
    __vue_is_functional_template__$26,
    __vue_module_identifier__$26,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$27 = {
  name: 'MeSortInput',
  components: {
    Icon: __vue_component__,
    Button: __vue_component__$6,
    Select: __vue_component__$m,
    Draggable: __vue_component__$s
  },
  props: {
    value: {
      type: Array,
      default: null
    },
    fields: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      showInput: false,
      sort: []
    }
  },
  watch: {
    value(val) {
      this.sort = this.prepareModel(val);
    }
  },
  created() {
    this.sort = this.prepareModel(this.value);
  },
  methods: {
    prepareModel(items) {
      return (items || []).map((d) => {
        const field = find_1(this.fields, { name: d.field.name });
        return {
          field: field || d.field,
          direction: d.direction
        }
      })
    },
    add() {
      this.showInput = true;
    },
    onAdd(field) {
      this.sort.push({
        field: field,
        direction: 'ASC'
      });
      this.showInput = false;
      this.dispatchChange();
    },
    toggle(index) {
      this.sort[index].direction = this.sort[index].direction === 'ASC' ? 'DESC' : 'ASC';
      this.dispatchChange();
    },
    remove(index) {
      this.sort.splice(index, 1);
      this.dispatchChange();
    },
    dispatchChange() {
      this.$emit('input', cloneDeep_1(this.sort));
    }
  }
};

/* script */
const __vue_script__$27 = script$27;

/* template */
var __vue_render__$27 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { staticClass: "me-sort-input" }, [
    _c(
      "div",
      { staticClass: "items" },
      [
        _vm.sort.length > 0
          ? _c(
              "Draggable",
              {
                attrs: { tag: "ul" },
                on: { change: _vm.dispatchChange },
                model: {
                  value: _vm.sort,
                  callback: function($$v) {
                    _vm.sort = $$v;
                  },
                  expression: "sort"
                }
              },
              _vm._l(_vm.sort, function(item, index) {
                return _c("li", { key: index }, [
                  _c(
                    "span",
                    { staticClass: "badge badge-light" },
                    [
                      _c(
                        "span",
                        {
                          on: {
                            click: function($event) {
                              return _vm.toggle(index)
                            }
                          }
                        },
                        [
                          item.direction == "ASC"
                            ? _c("Icon", {
                                attrs: { icon: "me-icon-s icon-caret-up" }
                              })
                            : _c("Icon", {
                                attrs: { icon: "me-icon-s icon-caret-down" }
                              }),
                          _vm._v(
                            "\n            " +
                              _vm._s(item.field.label || item.field.name) +
                              "\n          "
                          )
                        ],
                        1
                      ),
                      _vm._v(" "),
                      _c("Icon", {
                        attrs: { icon: "me-icon icon-times" },
                        on: {
                          click: function($event) {
                            return _vm.remove(index)
                          }
                        }
                      })
                    ],
                    1
                  )
                ])
              }),
              0
            )
          : _vm._e()
      ],
      1
    ),
    _vm._v(" "),
    _c(
      "div",
      { staticClass: "controls" },
      [
        _vm.showInput
          ? _c("Select", {
              attrs: {
                options: _vm.fields,
                placeholder: _vm.$toolkit.t("sort.field.placeholder"),
                clearable: false,
                label: "label"
              },
              on: { input: _vm.onAdd }
            })
          : _c(
              "Button",
              {
                attrs: { variant: "light", size: "sm" },
                on: { click: _vm.add }
              },
              [
                _vm._v(
                  "\n      " +
                    _vm._s(_vm.$toolkit.t("sort.field.add")) +
                    "\n    "
                )
              ]
            )
      ],
      1
    )
  ])
};
var __vue_staticRenderFns__$27 = [];
__vue_render__$27._withStripped = true;

  /* style */
  const __vue_inject_styles__$27 = function (inject) {
    if (!inject) return
    inject("data-v-60951dba_0", { source: "div.me-sort-input[data-v-60951dba] {\n  display: flex;\n}\ndiv.me-sort-input > div.items[data-v-60951dba] {\n  margin: -2px;\n}\ndiv.me-sort-input > div.controls[data-v-60951dba] {\n  min-width: 190px;\n  white-space: nowrap;\n  margin-left: 2px;\n}\ndiv.me-sort-input > div > ul[data-v-60951dba] {\n  padding: 0;\n  margin: 0;\n  list-style: none;\n}\ndiv.me-sort-input > div > ul > li[data-v-60951dba] {\n  float: left;\n  margin: 2px;\n}\ndiv.me-sort-input > div > ul > li .badge[data-v-60951dba] {\n  background-color: #ccc;\n  color: var(--me-gray-0);\n  padding: 0.55rem;\n}\ndiv.me-sort-input > div > ul > li .badge > span[data-v-60951dba] {\n  cursor: pointer;\n}\ndiv.me-sort-input > div > ul > li .badge > span[data-v-60951dba]:hover {\n  color: var(--me-gray-2);\n}\ndiv.me-sort-input > div > ul > li .badge > i[data-v-60951dba] {\n  cursor: pointer;\n  margin-left: 10px;\n}\ndiv.me-sort-input > div.me-select[data-v-60951dba] {\n  display: inline-block;\n  width: 200px;\n}\n\n/*# sourceMappingURL=SortInput.vue.map */", map: {"version":3,"sources":["/app/build/src/components/SortInput.vue","SortInput.vue"],"names":[],"mappings":"AAkHA;EACA,aAAA;ACjHA;ADoHA;EACA,YAAA;AClHA;ADqHA;EACA,gBAAA;EACA,mBAAA;EACA,gBAAA;ACnHA;ADsHA;EACA,UAAA;EACA,SAAA;EACA,gBAAA;ACpHA;ADsHA;EACA,WAAA;EACA,WAAA;ACpHA;ADsHA;EACA,sBAAA;EACA,uBAAA;EACA,gBAAA;ACpHA;ADsHA;EACA,eAAA;ACpHA;ADsHA;EACA,uBAAA;ACpHA;ADwHA;EACA,eAAA;EACA,iBAAA;ACtHA;AD6HA;EACA,qBAAA;EACA,YAAA;AC3HA;;AAEA,wCAAwC","file":"SortInput.vue","sourcesContent":["<template>\n  <div class=\"me-sort-input\">\n    <div class=\"items\">\n      <Draggable v-if=\"sort.length > 0\" v-model=\"sort\" @change=\"dispatchChange\" tag=\"ul\">\n        <li v-for=\"(item, index) in sort\" :key=\"index\">\n          <span class=\"badge badge-light\">\n            <span @click=\"toggle(index)\">\n              <Icon v-if=\"item.direction=='ASC'\" icon=\"me-icon-s icon-caret-up\" />\n              <Icon v-else icon=\"me-icon-s icon-caret-down\" />\n              {{ item.field.label || item.field.name }}\n            </span>\n            <Icon @click=\"remove(index)\" icon=\"me-icon icon-times\" />\n          </span>\n        </li>\n      </Draggable>\n    </div>\n    <div class=\"controls\">\n      <Select\n        v-if=\"showInput\"\n        :options=\"fields\"\n        :placeholder=\"$toolkit.t('sort.field.placeholder')\"\n        :clearable=\"false\"\n        @input=\"onAdd\"\n        label=\"label\"\n      />\n      <Button\n        v-else\n        @click=\"add\"\n        variant=\"light\"\n        size=\"sm\"\n      >\n        {{ $toolkit.t('sort.field.add') }}\n      </Button>\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport find from 'lodash/find'\nimport Icon from './Icon.vue'\nimport Button from './Button.vue'\nimport Select from './Select.vue'\nimport Draggable from './Draggable.vue'\n\nexport default {\n  name: 'MeSortInput',\n  components: {\n    Icon,\n    Button,\n    Select,\n    Draggable\n  },\n  props: {\n    value: {\n      type: Array,\n      default: null\n    },\n    fields: {\n      type: Array,\n      required: true\n    }\n  },\n  data() {\n    return {\n      showInput: false,\n      sort: []\n    }\n  },\n  watch: {\n    value(val) {\n      this.sort = this.prepareModel(val)\n    }\n  },\n  created() {\n    this.sort = this.prepareModel(this.value)\n  },\n  methods: {\n    prepareModel(items) {\n      return (items || []).map((d) => {\n        const field = find(this.fields, { name: d.field.name })\n        return {\n          field: field || d.field,\n          direction: d.direction\n        }\n      })\n    },\n    add() {\n      this.showInput = true\n    },\n    onAdd(field) {\n      this.sort.push({\n        field: field,\n        direction: 'ASC'\n      })\n      this.showInput = false\n      this.dispatchChange()\n    },\n    toggle(index) {\n      this.sort[index].direction = this.sort[index].direction === 'ASC' ? 'DESC' : 'ASC'\n      this.dispatchChange()\n    },\n    remove(index) {\n      this.sort.splice(index, 1)\n      this.dispatchChange()\n    },\n    dispatchChange() {\n      this.$emit('input', cloneDeep(this.sort))\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-sort-input {\n    display: flex;\n\n    > div {\n      &.items {\n        margin: -2px;\n      }\n\n      &.controls {\n        min-width: 190px;\n        white-space: nowrap;\n        margin-left: 2px;\n      }\n\n      > ul {\n        padding: 0;\n        margin: 0;\n        list-style: none;\n\n        > li {\n            float: left;\n            margin: 2px;\n\n            .badge {\n              background-color: #ccc;\n              color: var(--me-gray-0);\n              padding: .55rem;\n\n              > span {\n                cursor: pointer;\n\n                &:hover {\n                  color: var(--me-gray-2);\n                }\n              }\n\n              > i {\n                cursor: pointer;\n                margin-left: 10px;\n              }\n            }\n        }\n      }\n    }\n\n    > div.me-select {\n      display: inline-block;\n      width: 200px;\n    }\n  }\n</style>\n","div.me-sort-input {\n  display: flex;\n}\ndiv.me-sort-input > div.items {\n  margin: -2px;\n}\ndiv.me-sort-input > div.controls {\n  min-width: 190px;\n  white-space: nowrap;\n  margin-left: 2px;\n}\ndiv.me-sort-input > div > ul {\n  padding: 0;\n  margin: 0;\n  list-style: none;\n}\ndiv.me-sort-input > div > ul > li {\n  float: left;\n  margin: 2px;\n}\ndiv.me-sort-input > div > ul > li .badge {\n  background-color: #ccc;\n  color: var(--me-gray-0);\n  padding: 0.55rem;\n}\ndiv.me-sort-input > div > ul > li .badge > span {\n  cursor: pointer;\n}\ndiv.me-sort-input > div > ul > li .badge > span:hover {\n  color: var(--me-gray-2);\n}\ndiv.me-sort-input > div > ul > li .badge > i {\n  cursor: pointer;\n  margin-left: 10px;\n}\ndiv.me-sort-input > div.me-select {\n  display: inline-block;\n  width: 200px;\n}\n\n/*# sourceMappingURL=SortInput.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$27 = "data-v-60951dba";
  /* module identifier */
  const __vue_module_identifier__$27 = undefined;
  /* functional template */
  const __vue_is_functional_template__$27 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$27 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$27, staticRenderFns: __vue_staticRenderFns__$27 },
    __vue_inject_styles__$27,
    __vue_script__$27,
    __vue_scope_id__$27,
    __vue_is_functional_template__$27,
    __vue_module_identifier__$27,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$28 = {
  name: 'MeSteps',
  props: {
    stepsQuantity: {
      type: Number,
      required: true
    },
    activeUntil: {
      type: Number,
      default: 0
    },
    activePass: {
      type: Number,
      default: 0
    },
    stepType: {
      type: String,
      default: 'default',
      validator: value => ['default', 'step-bar'].includes(value)
    },
    variant: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'success', 'danger', 'warning'].includes(value)
    }
  }
};

/* script */
const __vue_script__$28 = script$28;

/* template */
var __vue_render__$28 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", [
    _vm.stepType === "default"
      ? _c(
          "div",
          { staticClass: "load-steps d-flex align-items-center" },
          [
            _vm._l(_vm.stepsQuantity, function(step, index) {
              return [
                _c(
                  "div",
                  {
                    key: step,
                    staticClass: "load-steps__outer-circle",
                    class: {
                      active: index <= _vm.activeUntil,
                      activepass: index <= _vm.activePass - 1
                    }
                  },
                  [
                    _c(
                      "div",
                      {
                        staticClass: "load-steps__inner-circle",
                        class: { active: index <= _vm.activeUntil }
                      },
                      [
                        index <= _vm.activePass - 1
                          ? _c("i", { staticClass: "me-icon icon-check" })
                          : _vm._e()
                      ]
                    )
                  ]
                ),
                _vm._v(" "),
                _c("div", {
                  key: step * (_vm.stepsQuantity + 1),
                  staticClass: "load-steps__progress",
                  class: {}
                })
              ]
            })
          ],
          2
        )
      : _vm._e(),
    _vm._v(" "),
    _vm.stepType === "step-bar"
      ? _c(
          "ul",
          { staticClass: "step-bar" },
          _vm._l(_vm.stepsQuantity, function(step, index) {
            return _c(
              "li",
              {
                key: step,
                staticClass: "step-bar__item",
                class: { "step-bar__item--last": step === _vm.stepsQuantity }
              },
              [
                _c("div", {
                  staticClass: "step-bar__item__circle",
                  class: [
                    index <= _vm.activeUntil
                      ? "step-bar__item__circle--" + _vm.variant
                      : ""
                  ]
                }),
                _vm._v(" "),
                step < _vm.stepsQuantity
                  ? _c("div", {
                      staticClass: "step-bar__item__progress-bar",
                      class: [
                        index <= _vm.activeUntil
                          ? "step-bar__item__progress-bar--" + _vm.variant
                          : ""
                      ]
                    })
                  : _vm._e()
              ]
            )
          }),
          0
        )
      : _vm._e()
  ])
};
var __vue_staticRenderFns__$28 = [];
__vue_render__$28._withStripped = true;

  /* style */
  const __vue_inject_styles__$28 = function (inject) {
    if (!inject) return
    inject("data-v-dcc78bd6_0", { source: ".load-steps__outer-circle[data-v-dcc78bd6] {\n  background-color: rgba(10, 140, 204, 0.16);\n  border-radius: 50%;\n  height: 0px;\n  width: 0px;\n  position: relative;\n}\n.load-steps__outer-circle.active[data-v-dcc78bd6] {\n  height: 28px;\n  width: 28px;\n}\n.load-steps__outer-circle.active.activepass[data-v-dcc78bd6] {\n  border: none;\n  background: none;\n  width: 14px;\n  height: 14px;\n}\n.load-steps__outer-circle.active.activepass + .load-steps__progress[data-v-dcc78bd6] {\n  background-image: linear-gradient(to right, #42ca7d, #1273a2);\n}\n.load-steps__outer-circle.active.activepass .load-steps__inner-circle[data-v-dcc78bd6] {\n  background-color: var(--me-success-2);\n}\n.load-steps__outer-circle.active.activepass .load-steps__inner-circle i[data-v-dcc78bd6] {\n  color: var(--me-white);\n  font-size: 10px;\n  position: absolute;\n  left: 2px;\n}\n.load-steps__inner-circle[data-v-dcc78bd6] {\n  position: absolute;\n  background-color: white;\n  border: 1px solid var(--me-gray-2);\n  border-radius: 50%;\n  height: 15px;\n  width: 15px;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n}\n.load-steps__inner-circle.active[data-v-dcc78bd6] {\n  background-color: var(--me-primary-2);\n  border: none;\n}\n.load-steps__progress[data-v-dcc78bd6] {\n  width: 100px;\n  height: 1px;\n  background-color: var(--me-gray-2);\n}\n.load-steps__progress[data-v-dcc78bd6]:last-child {\n  display: none;\n}\n@media screen and (max-width: 767px) {\n.load-steps__progress[data-v-dcc78bd6] {\n    width: 60px;\n}\n}\n.step-bar[data-v-dcc78bd6] {\n  list-style: none;\n  display: flex;\n  width: 100%;\n  margin: 0;\n  padding: 0;\n}\n.step-bar__item[data-v-dcc78bd6] {\n  display: flex;\n  align-items: center;\n  flex-grow: 1;\n}\n.step-bar__item--last[data-v-dcc78bd6] {\n  flex-grow: 0;\n}\n.step-bar__item__circle[data-v-dcc78bd6] {\n  height: 16px;\n  width: 16px;\n  border-radius: 8px;\n  border: 4px solid var(--me-gray-6);\n  background-color: transparent;\n}\n.step-bar__item__circle--primary[data-v-dcc78bd6] {\n  background-color: var(--me-primary-2);\n}\n.step-bar__item__circle--success[data-v-dcc78bd6] {\n  background-color: var(--me-success-2);\n}\n.step-bar__item__circle--danger[data-v-dcc78bd6] {\n  background-color: var(--me-danger-2);\n}\n.step-bar__item__circle--warning[data-v-dcc78bd6] {\n  background-color: var(--me-warning-2);\n}\n.step-bar__item__progress-bar[data-v-dcc78bd6] {\n  height: 2px;\n  flex-grow: 1;\n  background-color: var(--me-gray-6);\n}\n.step-bar__item__progress-bar--primary[data-v-dcc78bd6] {\n  background-color: var(--me-primary-2);\n}\n.step-bar__item__progress-bar--success[data-v-dcc78bd6] {\n  background-color: var(--me-success-2);\n}\n.step-bar__item__progress-bar--danger[data-v-dcc78bd6] {\n  background-color: var(--me-danger-2);\n}\n.step-bar__item__progress-bar--warning[data-v-dcc78bd6] {\n  background-color: var(--me-warning-2);\n}\n\n/*# sourceMappingURL=Steps.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Steps.vue","Steps.vue"],"names":[],"mappings":"AA+FA;EACA,0CAAA;EACA,kBAAA;EACA,WAAA;EACA,UAAA;EACA,kBAAA;AC9FA;ADgGA;EACA,YAAA;EACA,WAAA;AC9FA;ADgGA;EACA,YAAA;EACA,gBAAA;EACA,WAAA;EACA,YAAA;AC9FA;ADgGA;EACA,6DAAA;AC9FA;ADiGA;EACA,qCAAA;AC/FA;ADiGA;EACA,sBAAA;EACA,eAAA;EACA,kBAAA;EACA,SAAA;AC/FA;ADsGA;EACA,kBAAA;EACA,uBAAA;EACA,kCAAA;EACA,kBAAA;EACA,YAAA;EACA,WAAA;EACA,QAAA;EACA,SAAA;EACA,gCAAA;ACpGA;ADsGA;EACA,qCAAA;EACA,YAAA;ACpGA;ADwGA;EACA,YAAA;EACA,WAAA;EACA,kCAAA;ACtGA;ADwGA;EACA,aAAA;ACtGA;ADyGA;AATA;IAUA,WAAA;ACtGE;AACF;AD0GA;EACA,gBAAA;EACA,aAAA;EACA,WAAA;EACA,SAAA;EACA,UAAA;ACvGA;ADyGA;EACA,aAAA;EACA,mBAAA;EACA,YAAA;ACvGA;ADyGA;EACA,YAAA;ACvGA;AD0GA;EACA,YAAA;EACA,WAAA;EACA,kBAAA;EACA,kCAAA;EACA,6BAAA;ACxGA;ADSA;EACA,qCAAA;ACPA;ADMA;EACA,qCAAA;ACJA;ADGA;EACA,oCAAA;ACDA;ADAA;EACA,qCAAA;ACEA;ADgGA;EACA,WAAA;EACA,YAAA;EACA,kCAAA;AC9FA;ADRA;EACA,qCAAA;ACUA;ADXA;EACA,qCAAA;ACaA;ADdA;EACA,oCAAA;ACgBA;ADjBA;EACA,qCAAA;ACmBA;;AAEA,oCAAoC","file":"Steps.vue","sourcesContent":["<template>\n  <div>\n    <div\n      v-if=\"stepType === 'default'\"\n      class=\"load-steps d-flex align-items-center\"\n    >\n      <template v-for=\"(step, index) in stepsQuantity\">\n        <div\n          :key=\"step\"\n          :class=\"{\n            active: index <= activeUntil,\n            activepass: index <= activePass - 1\n          }\"\n          class=\"load-steps__outer-circle\"\n        >\n          <div\n            :class=\"{ active: index <= activeUntil }\"\n            class=\"load-steps__inner-circle\"\n          >\n            <i v-if=\"index <= activePass - 1\" class=\"me-icon icon-check\" />\n          </div>\n        </div>\n\n        <div\n          :key=\"step * (stepsQuantity + 1)\"\n          :class=\"{}\"\n          class=\"load-steps__progress\"\n        />\n      </template>\n    </div>\n\n    <ul v-if=\"stepType === 'step-bar'\" class=\"step-bar\">\n      <li\n        v-for=\"(step, index) in stepsQuantity\"\n        :key=\"step\"\n        :class=\"{'step-bar__item--last': step === stepsQuantity}\"\n        class=\"step-bar__item\"\n      >\n        <div\n          :class=\"[index <= activeUntil ? `step-bar__item__circle--${variant}` : '']\"\n          class=\"step-bar__item__circle\"\n        />\n\n        <div\n          v-if=\"step < stepsQuantity\"\n          :class=\"[index <= activeUntil ? `step-bar__item__progress-bar--${variant}` : '' ]\"\n          class=\"step-bar__item__progress-bar\"\n        />\n      </li>\n    </ul>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeSteps',\n  props: {\n    stepsQuantity: {\n      type: Number,\n      required: true\n    },\n    activeUntil: {\n      type: Number,\n      default: 0\n    },\n    activePass: {\n      type: Number,\n      default: 0\n    },\n    stepType: {\n      type: String,\n      default: 'default',\n      validator: value => ['default', 'step-bar'].includes(value)\n    },\n    variant: {\n      type: String,\n      default: 'primary',\n      validator: value => ['primary', 'success', 'danger', 'warning'].includes(value)\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  $variantList: 'primary', 'success', 'danger', 'warning';\n\n  @mixin variant-background() {\n    @each $variantName in $variantList {\n      &--#{$variantName} {\n        background-color: var(--me-#{$variantName}-2)\n      }\n    }\n  }\n\n.load-steps {\n  &__outer-circle {\n    background-color: rgba(10, 140, 204, 0.16);\n    border-radius: 50%;\n    height: 0px;\n    width: 0px;\n    position: relative;\n\n    &.active {\n      height: 28px;\n      width: 28px;\n\n      &.activepass {\n        border: none;\n        background: none;\n        width: 14px;\n        height: 14px;\n\n        + .load-steps__progress {\n          background-image: linear-gradient(to right, #42ca7d, #1273a2);\n        }\n\n        .load-steps__inner-circle {\n          background-color: var(--me-success-2);\n\n          i {\n            color: var(--me-white);\n            font-size: 10px;\n            position: absolute;\n            left: 2px;\n          }\n        }\n      }\n    }\n  }\n\n  &__inner-circle {\n    position: absolute;\n    background-color: white;\n    border: 1px solid var(--me-gray-2);\n    border-radius: 50%;\n    height: 15px;\n    width: 15px;\n    top: 50%;\n    left: 50%;\n    transform: translate(-50%, -50%);\n\n    &.active {\n      background-color: var(--me-primary-2);\n      border: none;\n    }\n  }\n\n  &__progress {\n    width: 100px;\n    height: 1px;\n    background-color: var(--me-gray-2);\n\n    &:last-child {\n      display: none;\n    }\n\n    @media screen and (max-width: \"767px\") {\n      width: 60px;\n    }\n  }\n}\n\n.step-bar {\n  list-style: none;\n  display: flex;\n  width: 100%;\n  margin: 0;\n  padding: 0;\n\n  &__item {\n    display: flex;\n    align-items: center;\n    flex-grow: 1;\n\n    &--last{\n      flex-grow: 0;\n    }\n\n    &__circle {\n      height: 16px;\n      width: 16px;\n      border-radius: 8px;\n      border: 4px solid var(--me-gray-6);\n      background-color: transparent;\n      @include variant-background();\n    }\n\n    &__progress-bar {\n      height: 2px;\n      flex-grow: 1;\n      background-color: var(--me-gray-6);\n      @include variant-background();\n    }\n  }\n}\n</style>\n",".load-steps__outer-circle {\n  background-color: rgba(10, 140, 204, 0.16);\n  border-radius: 50%;\n  height: 0px;\n  width: 0px;\n  position: relative;\n}\n.load-steps__outer-circle.active {\n  height: 28px;\n  width: 28px;\n}\n.load-steps__outer-circle.active.activepass {\n  border: none;\n  background: none;\n  width: 14px;\n  height: 14px;\n}\n.load-steps__outer-circle.active.activepass + .load-steps__progress {\n  background-image: linear-gradient(to right, #42ca7d, #1273a2);\n}\n.load-steps__outer-circle.active.activepass .load-steps__inner-circle {\n  background-color: var(--me-success-2);\n}\n.load-steps__outer-circle.active.activepass .load-steps__inner-circle i {\n  color: var(--me-white);\n  font-size: 10px;\n  position: absolute;\n  left: 2px;\n}\n.load-steps__inner-circle {\n  position: absolute;\n  background-color: white;\n  border: 1px solid var(--me-gray-2);\n  border-radius: 50%;\n  height: 15px;\n  width: 15px;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n}\n.load-steps__inner-circle.active {\n  background-color: var(--me-primary-2);\n  border: none;\n}\n.load-steps__progress {\n  width: 100px;\n  height: 1px;\n  background-color: var(--me-gray-2);\n}\n.load-steps__progress:last-child {\n  display: none;\n}\n@media screen and (max-width: 767px) {\n  .load-steps__progress {\n    width: 60px;\n  }\n}\n\n.step-bar {\n  list-style: none;\n  display: flex;\n  width: 100%;\n  margin: 0;\n  padding: 0;\n}\n.step-bar__item {\n  display: flex;\n  align-items: center;\n  flex-grow: 1;\n}\n.step-bar__item--last {\n  flex-grow: 0;\n}\n.step-bar__item__circle {\n  height: 16px;\n  width: 16px;\n  border-radius: 8px;\n  border: 4px solid var(--me-gray-6);\n  background-color: transparent;\n}\n.step-bar__item__circle--primary {\n  background-color: var(--me-primary-2);\n}\n.step-bar__item__circle--success {\n  background-color: var(--me-success-2);\n}\n.step-bar__item__circle--danger {\n  background-color: var(--me-danger-2);\n}\n.step-bar__item__circle--warning {\n  background-color: var(--me-warning-2);\n}\n.step-bar__item__progress-bar {\n  height: 2px;\n  flex-grow: 1;\n  background-color: var(--me-gray-6);\n}\n.step-bar__item__progress-bar--primary {\n  background-color: var(--me-primary-2);\n}\n.step-bar__item__progress-bar--success {\n  background-color: var(--me-success-2);\n}\n.step-bar__item__progress-bar--danger {\n  background-color: var(--me-danger-2);\n}\n.step-bar__item__progress-bar--warning {\n  background-color: var(--me-warning-2);\n}\n\n/*# sourceMappingURL=Steps.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$28 = "data-v-dcc78bd6";
  /* module identifier */
  const __vue_module_identifier__$28 = undefined;
  /* functional template */
  const __vue_is_functional_template__$28 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$28 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$28, staticRenderFns: __vue_staticRenderFns__$28 },
    __vue_inject_styles__$28,
    __vue_script__$28,
    __vue_scope_id__$28,
    __vue_is_functional_template__$28,
    __vue_module_identifier__$28,
    false,
    createInjector,
    undefined,
    undefined
  );

const createActionsValidator = (propValue) => {
  if (!propValue) return true

  const isLabelValid = propValue.label;
  const isClickValid = !propValue.click || typeof propValue.click === 'function';

  if (propValue.items?.length) {
    const isItemsValid = propValue.items && Array.isArray(propValue.items) && propValue.items.length;
    return isLabelValid && isItemsValid && isClickValid
  }

  return isLabelValid && isClickValid
};

const buttonBarValidator = (propValue) => {
  if (!propValue) return true

  const isActionsValid = propValue.actions && Array.isArray(propValue.actions) && propValue.actions.length;
  const isOptionsValid = !propValue.options || (propValue.options && Array.isArray(propValue.options));
  return isActionsValid && isOptionsValid
};

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$29 = {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    click: {
      type: Function,
      default: null
    },
    href: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    busy: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    target: {
      type: String,
      default: '_self'
    }
  },

  methods: {
    handleClick() {
      if (this.href) return null
      if (!this.click) return null
      return this.click()
    }
  }
};

/* script */
const __vue_script__$29 = script$29;

/* template */
var __vue_render__$29 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    [
      _vm.items && _vm.items.length
        ? _c("me-drop-down", {
            attrs: {
              items: _vm.items,
              "split-href": _vm.href,
              split: !!(_vm.href || _vm.click),
              variant: "primary",
              "split-variant": "primary",
              block: "",
              "data-cy": "subheaderCreateActions"
            },
            on: { click: _vm.handleClick },
            scopedSlots: _vm._u(
              [
                {
                  key: "button-content",
                  fn: function() {
                    return [
                      _c("me-icon", {
                        staticStyle: { "margin-right": "8px" },
                        attrs: { icon: "me-icon-l icon-plus" }
                      }),
                      _vm._v(" "),
                      _c(
                        "span",
                        {
                          directives: [
                            {
                              name: "me-tooltip",
                              rawName: "v-me-tooltip.ellipsis",
                              value: _vm.label,
                              expression: "label",
                              modifiers: { ellipsis: true }
                            }
                          ],
                          staticClass: "create-action-title"
                        },
                        [_vm._v("\n        " + _vm._s(_vm.label) + "\n      ")]
                      )
                    ]
                  },
                  proxy: true
                }
              ],
              null,
              false,
              3705517189
            )
          })
        : _c(
            "me-button",
            {
              directives: [
                {
                  name: "me-tooltip",
                  rawName: "v-me-tooltip.hover.responsive",
                  value: _vm.label,
                  expression: "label",
                  modifiers: { hover: true, responsive: true }
                }
              ],
              staticClass: "add-new-btn",
              attrs: {
                href: _vm.href,
                target: _vm.target,
                disabled: _vm.disabled,
                busy: _vm.busy,
                "prepend-icon": "me-icon-l icon-plus",
                variant: "primary"
              },
              on: { click: _vm.handleClick }
            },
            [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]
          )
    ],
    1
  )
};
var __vue_staticRenderFns__$29 = [];
__vue_render__$29._withStripped = true;

  /* style */
  const __vue_inject_styles__$29 = function (inject) {
    if (!inject) return
    inject("data-v-d1337a38_0", { source: ".add-new-btn[data-v-d1337a38] {\n  width: 192px;\n  min-width: 192px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n/*# sourceMappingURL=CreateActions.vue.map */", map: {"version":3,"sources":["/app/build/src/components/subheader/mode/index/CreateActions.vue","CreateActions.vue"],"names":[],"mappings":"AAyFA;EACA,YAAA;EACA,gBAAA;EACA,gBAAA;EACA,uBAAA;EACA,mBAAA;ACxFA;;AAEA,4CAA4C","file":"CreateActions.vue","sourcesContent":["<template>\n  <div>\n    <me-drop-down\n      v-if=\"items && items.length\"\n      :items=\"items\"\n      :split-href=\"href\"\n      :split=\"!!(href || click)\"\n      @click=\"handleClick\"\n      variant=\"primary\"\n      split-variant=\"primary\"\n      block\n      data-cy=\"subheaderCreateActions\"\n    >\n      <template v-slot:button-content>\n        <me-icon\n          icon=\"me-icon-l icon-plus\"\n          style=\"margin-right: 8px\"\n        />\n\n        <span\n          v-me-tooltip.ellipsis=\"label\"\n          class=\"create-action-title\"\n        >\n          {{ label }}\n        </span>\n      </template>\n    </me-drop-down>\n\n    <me-button\n      v-else\n      :href=\"href\"\n      :target=\"target\"\n      :disabled=\"disabled\"\n      :busy=\"busy\"\n      @click=\"handleClick\"\n      v-me-tooltip.hover.responsive=\"label\"\n      prepend-icon=\"me-icon-l icon-plus\"\n      variant=\"primary\"\n      class=\"add-new-btn\"\n    >\n      {{ label }}\n    </me-button>\n  </div>\n</template>\n\n<script>\nexport default {\n  props: {\n    items: {\n      type: Array,\n      default: () => []\n    },\n    click: {\n      type: Function,\n      default: null\n    },\n    href: {\n      type: String,\n      default: null\n    },\n    label: {\n      type: String,\n      default: null\n    },\n    busy: {\n      type: Boolean,\n      default: false\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    target: {\n      type: String,\n      default: '_self'\n    }\n  },\n\n  methods: {\n    handleClick() {\n      if (this.href) return null\n      if (!this.click) return null\n      return this.click()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .add-new-btn {\n    width: 192px;\n    min-width: 192px;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n  }\n</style>\n",".add-new-btn {\n  width: 192px;\n  min-width: 192px;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n/*# sourceMappingURL=CreateActions.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$29 = "data-v-d1337a38";
  /* module identifier */
  const __vue_module_identifier__$29 = undefined;
  /* functional template */
  const __vue_is_functional_template__$29 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$29 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$29, staticRenderFns: __vue_staticRenderFns__$29 },
    __vue_inject_styles__$29,
    __vue_script__$29,
    __vue_scope_id__$29,
    __vue_is_functional_template__$29,
    __vue_module_identifier__$29,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$2a = {
  name: 'MeSubheaderIndex',
  components: {
    CreateActions: __vue_component__$29,
    GridModeOptions: __vue_component__$1A
  },

  props: {
    toggleSidebar: {
      type: Object,
      default: null
    },
    createActions: {
      type: Object,
      default: null,
      validator: createActionsValidator
    },
    buttonBar: {
      type: Object,
      default: null,
      validator: buttonBarValidator
    },
    search: {
      type: Object,
      default: null
    },
    gridMode: {
      type: Array,
      default: () => []
    },
    toggleChart: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      chartOption: null,
      currentGridMode: []
    }
  },

  watch: {
    gridMode() {
      this.currentGridMode = this.getGridModeOption();
    },

    toggleChart() {
      this.chartOption = cloneDeep_1(this.toggleChart);
    }
  },

  created() {
    this.chartOption = cloneDeep_1(this.toggleChart);
    this.currentGridMode = this.getGridModeOption();
  },

  methods: {
    getGridModeOption() {
      return this.gridMode.map(item => ({ ...item, click: () => this.handleGridModeClick(item.click, item.click) }))
    },

    handleGridModeClick(selectedOption, cb) {
      this.toggleGridModeOptions(selectedOption);
      if (cb) {
        cb(selectedOption);
      }
    },

    toggleGridModeOptions(selectedOption) {
      return this.currentGridMode.map(item => item.label === selectedOption.label ? {
        ...item,
        active: true
      } : {
        ...item,
        active: false
      })
    },

    toggleChartOption() {
      this.chartOption = { ...this.chartOption, active: !this.chartOption.active };

      if (this.chartOption.click) {
        this.chartOption.click(this.chartOption.active);
      }
    },

    onApplySearch(data) {
      if (this.search?.onApply) {
        this.search.onApply(data);
      }
    },

    onChangeSearch(data) {
      if (this.search?.onChange) {
        this.search.onChange(data);
      }
    }
  }
};

/* script */
const __vue_script__$2a = script$2a;

/* template */
var __vue_render__$2a = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "subheader-index", attrs: { "data-cy": "subheaderIndex" } },
    [
      _vm.toggleSidebar
        ? _c("me-icon", {
            staticClass: "toggle-sidebar subheader-index__action",
            attrs: {
              size: 20,
              variant: "primary",
              icon: "me-icon-l icon-bars"
            },
            on: {
              click: function($event) {
                _vm.toggleSidebar.click ? _vm.toggleSidebar.click() : null;
              }
            }
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.createActions
        ? _c(
            "div",
            { staticClass: "create-actions subheader-index__action" },
            [
              _c("CreateActions", {
                attrs: {
                  items: _vm.createActions.items,
                  href: _vm.createActions.href,
                  click: _vm.createActions.click,
                  label: _vm.createActions.label,
                  target: _vm.createActions.target,
                  disabled: _vm.createActions.disabled,
                  busy: _vm.createActions.busy
                }
              })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.buttonBar
        ? _c(
            "div",
            { staticClass: "button-bar subheader-index__action" },
            [
              _c("me-button-bar", {
                attrs: {
                  actions: _vm.buttonBar.actions,
                  options: _vm.buttonBar.options,
                  "max-size": 4
                }
              })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.search
        ? _c(
            "div",
            { staticClass: "search-input subheader-index__action" },
            [
              _c("me-search", {
                attrs: {
                  term: _vm.search.term || "",
                  fields: _vm.search.fields || [],
                  criteria: _vm.search.criteria || [],
                  placeholder: _vm.search.placeholder,
                  "hide-operators": ""
                },
                on: { apply: _vm.onApplySearch, change: _vm.onChangeSearch }
              })
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      _vm.chartOption
        ? _c("me-icon", {
            staticClass: "toggle-chart subheader-index__action",
            class: { "toggle-chart__active": _vm.chartOption.active },
            attrs: {
              size: 18,
              icon: "me-icon-l icon-chart-column",
              "data-cy": "toggleChartOption"
            },
            on: { click: _vm.toggleChartOption }
          })
        : _vm._e(),
      _vm._v(" "),
      _vm.currentGridMode.length
        ? _c("GridModeOptions", {
            staticClass: "subheader-index__action",
            attrs: { items: _vm.currentGridMode }
          })
        : _vm._e()
    ],
    1
  )
};
var __vue_staticRenderFns__$2a = [];
__vue_render__$2a._withStripped = true;

  /* style */
  const __vue_inject_styles__$2a = function (inject) {
    if (!inject) return
    inject("data-v-3866ea68_0", { source: ".subheader-index[data-v-3866ea68] {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n}\n.subheader-index__action[data-v-3866ea68] {\n  margin-right: 16px;\n}\n.subheader-index__action[data-v-3866ea68]:last-child {\n  margin-right: 0;\n}\n.subheader-index .toggle-sidebar[data-v-3866ea68] {\n  cursor: pointer;\n}\n@media screen and (max-width: 768px) {\n.subheader-index .toggle-sidebar[data-v-3866ea68] {\n    display: none;\n}\n}\n.subheader-index .create-actions[data-v-3866ea68] {\n  width: 192px;\n  min-width: 192px;\n}\n.subheader-index .create-actions[data-v-3866ea68]  .btn {\n  overflow: hidden;\n}\n.subheader-index .create-actions .create-action-title[data-v-3866ea68] {\n  white-space: nowrap;\n  text-overflow: ellipsis;\n  overflow: hidden;\n}\n.subheader-index .button-bar[data-v-3866ea68] {\n  flex-grow: 1;\n  min-width: 1px;\n}\n.subheader-index .search-input[data-v-3866ea68] {\n  width: 30vw;\n  max-width: 415px;\n  min-width: 300px;\n  margin-left: auto;\n}\n.subheader-index .toggle-chart[data-v-3866ea68] {\n  cursor: pointer;\n  height: 40px;\n  width: 40px;\n  min-width: 40px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  border-radius: 8px;\n  color: var(--me-gray-2);\n}\n.subheader-index .toggle-chart[data-v-3866ea68]:hover:not(.toggle-chart__active) {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n}\n.subheader-index .toggle-chart__active[data-v-3866ea68] {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n}\n\n/*# sourceMappingURL=SubheaderIndex.vue.map */", map: {"version":3,"sources":["/app/build/src/components/subheader/mode/index/SubheaderIndex.vue","SubheaderIndex.vue"],"names":[],"mappings":"AA2KA;EACA,aAAA;EACA,mBAAA;EACA,WAAA;EACA,YAAA;AC1KA;AD4KA;EACA,kBAAA;AC1KA;AD4KA;EACA,eAAA;AC1KA;AD8KA;EACA,eAAA;AC5KA;AD8KA;AAHA;IAIA,aAAA;AC3KE;AACF;AD8KA;EACA,YAAA;EACA,gBAAA;AC5KA;AD+KA;EACA,gBAAA;AC7KA;ADiLA;EACA,mBAAA;EACA,uBAAA;EACA,gBAAA;AC/KA;ADmLA;EACA,YAAA;EACA,cAAA;ACjLA;ADoLA;EACA,WAAA;EACA,gBAAA;EACA,gBAAA;EACA,iBAAA;AClLA;ADqLA;EACA,eAAA;EACA,YAAA;EACA,WAAA;EACA,eAAA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,kBAAA;EACA,uBAAA;ACnLA;ADqLA;EACA,qCAAA;EACA,0BAAA;ACnLA;ADsLA;EACA,qCAAA;EACA,sBAAA;ACpLA;;AAEA,6CAA6C","file":"SubheaderIndex.vue","sourcesContent":["<template>\n  <div class=\"subheader-index\" data-cy=\"subheaderIndex\">\n    <me-icon\n      v-if=\"toggleSidebar\"\n      @click=\"toggleSidebar.click ? toggleSidebar.click() : null\"\n      :size=\"20\"\n      variant=\"primary\"\n      icon=\"me-icon-l icon-bars\"\n      class=\"toggle-sidebar subheader-index__action\"\n    />\n\n    <div v-if=\"createActions\" class=\"create-actions subheader-index__action\">\n      <CreateActions\n        :items=\"createActions.items\"\n        :href=\"createActions.href\"\n        :click=\"createActions.click\"\n        :label=\"createActions.label\"\n        :target=\"createActions.target\"\n        :disabled=\"createActions.disabled\"\n        :busy=\"createActions.busy\"\n      />\n    </div>\n\n    <div v-if=\"buttonBar\" class=\"button-bar subheader-index__action\">\n      <me-button-bar\n        :actions=\"buttonBar.actions\"\n        :options=\"buttonBar.options\"\n        :max-size=\"4\"\n      />\n    </div>\n\n    <div v-if=\"search\" class=\"search-input subheader-index__action\">\n      <me-search\n        :term=\"search.term || ''\"\n        :fields=\"search.fields || []\"\n        :criteria=\"search.criteria || []\"\n        :placeholder=\"search.placeholder\"\n        @apply=\"onApplySearch\"\n        @change=\"onChangeSearch\"\n        hide-operators\n      />\n    </div>\n\n    <me-icon\n      v-if=\"chartOption\"\n      :class=\"{'toggle-chart__active': chartOption.active}\"\n      @click=\"toggleChartOption\"\n      :size=\"18\"\n      class=\"toggle-chart subheader-index__action\"\n      icon=\"me-icon-l icon-chart-column\"\n      data-cy=\"toggleChartOption\"\n    />\n\n    <GridModeOptions\n      v-if=\"currentGridMode.length\"\n      :items=\"currentGridMode\"\n      class=\"subheader-index__action\"\n    />\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport { createActionsValidator, buttonBarValidator } from './../../propsValidators'\nimport CreateActions from './CreateActions.vue'\nimport GridModeOptions from './../../../GridModeOptions.vue'\n\nexport default {\n  name: 'MeSubheaderIndex',\n  components: {\n    CreateActions,\n    GridModeOptions\n  },\n\n  props: {\n    toggleSidebar: {\n      type: Object,\n      default: null\n    },\n    createActions: {\n      type: Object,\n      default: null,\n      validator: createActionsValidator\n    },\n    buttonBar: {\n      type: Object,\n      default: null,\n      validator: buttonBarValidator\n    },\n    search: {\n      type: Object,\n      default: null\n    },\n    gridMode: {\n      type: Array,\n      default: () => []\n    },\n    toggleChart: {\n      type: Object,\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      chartOption: null,\n      currentGridMode: []\n    }\n  },\n\n  watch: {\n    gridMode() {\n      this.currentGridMode = this.getGridModeOption()\n    },\n\n    toggleChart() {\n      this.chartOption = cloneDeep(this.toggleChart)\n    }\n  },\n\n  created() {\n    this.chartOption = cloneDeep(this.toggleChart)\n    this.currentGridMode = this.getGridModeOption()\n  },\n\n  methods: {\n    getGridModeOption() {\n      return this.gridMode.map(item => ({ ...item, click: () => this.handleGridModeClick(item.click, item.click) }))\n    },\n\n    handleGridModeClick(selectedOption, cb) {\n      this.toggleGridModeOptions(selectedOption)\n      if (cb) {\n        cb(selectedOption)\n      }\n    },\n\n    toggleGridModeOptions(selectedOption) {\n      return this.currentGridMode.map(item => item.label === selectedOption.label ? {\n        ...item,\n        active: true\n      } : {\n        ...item,\n        active: false\n      })\n    },\n\n    toggleChartOption() {\n      this.chartOption = { ...this.chartOption, active: !this.chartOption.active }\n\n      if (this.chartOption.click) {\n        this.chartOption.click(this.chartOption.active)\n      }\n    },\n\n    onApplySearch(data) {\n      if (this.search?.onApply) {\n        this.search.onApply(data)\n      }\n    },\n\n    onChangeSearch(data) {\n      if (this.search?.onChange) {\n        this.search.onChange(data)\n      }\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .subheader-index {\n    display: flex;\n    align-items: center;\n    width: 100%;\n    height: 100%;\n\n    &__action {\n      margin-right: 16px;\n\n      &:last-child {\n        margin-right: 0;\n      }\n    }\n\n    .toggle-sidebar {\n      cursor: pointer;\n\n      @media screen and (max-width: \"768px\") {\n        display: none;\n      }\n    }\n\n    .create-actions {\n      width: 192px;\n      min-width: 192px;\n\n      ::v-deep {\n        .btn {\n          overflow: hidden;\n        }\n      }\n\n      .create-action-title {\n        white-space: nowrap;\n        text-overflow: ellipsis;\n        overflow: hidden;\n      }\n    }\n\n    .button-bar {\n      flex-grow: 1;\n      min-width: 1px;\n    }\n\n    .search-input {\n      width: 30vw;\n      max-width: 415px;\n      min-width: 300px;\n      margin-left: auto;\n    }\n\n    .toggle-chart {\n      cursor: pointer;\n      height: 40px;\n      width: 40px;\n      min-width: 40px;\n      display: flex;\n      justify-content: center;\n      align-items: center;\n      border-radius: 8px;\n      color: var(--me-gray-2);\n\n      &:hover:not(.toggle-chart__active) {\n        background-color: var(--me-primary-7);\n        color: var(--me-primary-1);\n      }\n\n      &__active {\n        background-color: var(--me-primary-1);\n        color: var(--me-white);\n      }\n    }\n  }\n</style>\n",".subheader-index {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n}\n.subheader-index__action {\n  margin-right: 16px;\n}\n.subheader-index__action:last-child {\n  margin-right: 0;\n}\n.subheader-index .toggle-sidebar {\n  cursor: pointer;\n}\n@media screen and (max-width: 768px) {\n  .subheader-index .toggle-sidebar {\n    display: none;\n  }\n}\n.subheader-index .create-actions {\n  width: 192px;\n  min-width: 192px;\n}\n.subheader-index .create-actions ::v-deep .btn {\n  overflow: hidden;\n}\n.subheader-index .create-actions .create-action-title {\n  white-space: nowrap;\n  text-overflow: ellipsis;\n  overflow: hidden;\n}\n.subheader-index .button-bar {\n  flex-grow: 1;\n  min-width: 1px;\n}\n.subheader-index .search-input {\n  width: 30vw;\n  max-width: 415px;\n  min-width: 300px;\n  margin-left: auto;\n}\n.subheader-index .toggle-chart {\n  cursor: pointer;\n  height: 40px;\n  width: 40px;\n  min-width: 40px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  border-radius: 8px;\n  color: var(--me-gray-2);\n}\n.subheader-index .toggle-chart:hover:not(.toggle-chart__active) {\n  background-color: var(--me-primary-7);\n  color: var(--me-primary-1);\n}\n.subheader-index .toggle-chart__active {\n  background-color: var(--me-primary-1);\n  color: var(--me-white);\n}\n\n/*# sourceMappingURL=SubheaderIndex.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2a = "data-v-3866ea68";
  /* module identifier */
  const __vue_module_identifier__$2a = undefined;
  /* functional template */
  const __vue_is_functional_template__$2a = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2a, staticRenderFns: __vue_staticRenderFns__$2a },
    __vue_inject_styles__$2a,
    __vue_script__$2a,
    __vue_scope_id__$2a,
    __vue_is_functional_template__$2a,
    __vue_module_identifier__$2a,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

const wizardModes = {
  WIZARD: 1,
  EXPRESS: 2
};

var script$2b = {
  props: {
    wizard: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      wizardModeSelected: {
        id: wizardModes.WIZARD,
        icon: 'me-icon-l icon-hat-wizard',
        label: 'Wizard',
        active: true
      },
      wizardModeOptions: [
        {
          id: wizardModes.WIZARD,
          icon: 'me-icon-l icon-hat-wizard',
          label: 'Wizard',
          click: this.toggleWizardMode,
          active: true
        },
        {
          id: wizardModes.EXPRESS,
          icon: 'me-icon-l icon-stopwatch',
          label: 'Express',
          click: this.toggleWizardMode,
          active: false
        }
      ]
    }
  },

  methods: {
    toggleWizardMode(selectedMode) {
      this.wizardModeOptions = this.wizardModeOptions.map(item => item.label === selectedMode?.label ? {
        ...item,
        active: true
      } : {
        ...item,
        active: false
      });

      this.wizardModeSelected = selectedMode;

      if (this.wizard?.click) {
        this.wizard.click(selectedMode);
      }
    }
  }
};

/* script */
const __vue_script__$2b = script$2b;

/* template */
var __vue_render__$2b = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("me-drop-down", {
    attrs: {
      items: _vm.wizardModeOptions,
      variant: "light-secondary",
      "data-cy": "wizardActions"
    },
    scopedSlots: _vm._u([
      {
        key: "button-content",
        fn: function() {
          return [
            _c("me-icon", {
              staticStyle: { "margin-right": "8px" },
              attrs: { icon: _vm.wizardModeSelected.icon }
            }),
            _vm._v(" "),
            _c("span", { attrs: { "data-cy": "wizardActiveAction" } }, [
              _vm._v(
                "\n      " + _vm._s(_vm.wizardModeSelected.label) + "\n    "
              )
            ])
          ]
        },
        proxy: true
      }
    ])
  })
};
var __vue_staticRenderFns__$2b = [];
__vue_render__$2b._withStripped = true;

  /* style */
  const __vue_inject_styles__$2b = undefined;
  /* scoped */
  const __vue_scope_id__$2b = undefined;
  /* module identifier */
  const __vue_module_identifier__$2b = undefined;
  /* functional template */
  const __vue_is_functional_template__$2b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2b, staticRenderFns: __vue_staticRenderFns__$2b },
    __vue_inject_styles__$2b,
    __vue_script__$2b,
    __vue_scope_id__$2b,
    __vue_is_functional_template__$2b,
    __vue_module_identifier__$2b,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$2c = {
  name: 'MeSubheaderForm',
  components: {
    WizardActions: __vue_component__$2b
  },

  props: {
    cancel: {
      type: Object,
      default: null
    },
    back: {
      type: Object,
      default: null
    },
    next: {
      type: Object,
      default: null
    },
    wizard: {
      type: Object,
      default: null
    }
  }

};

/* script */
const __vue_script__$2c = script$2c;

/* template */
var __vue_render__$2c = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "subheader-form", attrs: { "data-cy": "subheaderForm" } },
    [
      _c(
        "div",
        { staticClass: "left-actions" },
        [
          _vm.cancel
            ? _c(
                "me-button",
                {
                  staticClass: "subheader-action",
                  attrs: {
                    "prepend-icon":
                      _vm.cancel.prependIcon || "me-icon-l icon-xmark",
                    "append-icon": _vm.cancel.appendIcon || "",
                    disabled: _vm.cancel.disabled,
                    variant: "light-primary"
                  },
                  on: { click: _vm.cancel.click }
                },
                [
                  _vm._v(
                    "\n      " +
                      _vm._s(
                        _vm.cancel.label ||
                          _vm.$toolkit.t("subheader.formMode.cancel")
                      ) +
                      "\n    "
                  )
                ]
              )
            : _vm._e()
        ],
        1
      ),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "right-actions" },
        [
          _vm.wizard
            ? _c("WizardActions", {
                staticClass: "subheader-action",
                attrs: { wizard: _vm.wizard }
              })
            : _vm._e(),
          _vm._v(" "),
          _vm.back
            ? _c(
                "me-button",
                {
                  staticClass: "subheader-action",
                  attrs: {
                    "prepend-icon":
                      _vm.back.prependIcon || "me-icon-l icon-arrow-left",
                    "append-icon": _vm.back.appendIcon || "",
                    disabled: _vm.back.disabled,
                    variant: "light-primary"
                  },
                  on: { click: _vm.back.click }
                },
                [
                  _vm._v(
                    "\n      " +
                      _vm._s(
                        _vm.back.label ||
                          _vm.$toolkit.t("subheader.formMode.back")
                      ) +
                      "\n    "
                  )
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.next
            ? _c(
                "me-button",
                {
                  staticClass: "subheader-action",
                  attrs: {
                    "prepend-icon": _vm.next.prependIcon || "",
                    "append-icon":
                      _vm.next.appendIcon || "me-icon-l icon-arrow-right",
                    disabled: _vm.next.disabled,
                    variant: "primary"
                  },
                  on: { click: _vm.next.click }
                },
                [
                  _vm._v(
                    "\n      " +
                      _vm._s(
                        _vm.next.label ||
                          _vm.$toolkit.t("subheader.formMode.next")
                      ) +
                      "\n    "
                  )
                ]
              )
            : _vm._e()
        ],
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$2c = [];
__vue_render__$2c._withStripped = true;

  /* style */
  const __vue_inject_styles__$2c = function (inject) {
    if (!inject) return
    inject("data-v-ba0d3368_0", { source: ".subheader-form[data-v-ba0d3368] {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n}\n.subheader-form .subheader-action[data-v-ba0d3368] {\n  margin-right: 32px;\n}\n.subheader-form .subheader-action[data-v-ba0d3368]:last-child {\n  margin: 0;\n}\n.subheader-form .right-actions[data-v-ba0d3368] {\n  display: flex;\n  align-items: center;\n  margin-left: auto;\n}\n\n/*# sourceMappingURL=SubheaderForm.vue.map */", map: {"version":3,"sources":["/app/build/src/components/subheader/mode/form/SubheaderForm.vue","SubheaderForm.vue"],"names":[],"mappings":"AAiFA;EACA,aAAA;EACA,mBAAA;EACA,WAAA;EACA,YAAA;AChFA;ADkFA;EACA,kBAAA;AChFA;ADkFA;EACA,SAAA;AChFA;ADoFA;EACA,aAAA;EACA,mBAAA;EACA,iBAAA;AClFA;;AAEA,4CAA4C","file":"SubheaderForm.vue","sourcesContent":["<template>\n  <div\n    class=\"subheader-form\"\n    data-cy=\"subheaderForm\"\n  >\n    <div class=\"left-actions\">\n      <me-button\n        v-if=\"cancel\"\n        :prepend-icon=\"cancel.prependIcon || 'me-icon-l icon-xmark'\"\n        :append-icon=\"cancel.appendIcon || ''\"\n        :disabled=\"cancel.disabled\"\n        @click=\"cancel.click\"\n        class=\"subheader-action\"\n        variant=\"light-primary\"\n      >\n        {{ cancel.label || $toolkit.t('subheader.formMode.cancel') }}\n      </me-button>\n    </div>\n\n    <div class=\"right-actions\">\n      <WizardActions v-if=\"wizard\" :wizard=\"wizard\" class=\"subheader-action\" />\n\n      <me-button\n        v-if=\"back\"\n        :prepend-icon=\"back.prependIcon || 'me-icon-l icon-arrow-left'\"\n        :append-icon=\"back.appendIcon || ''\"\n        :disabled=\"back.disabled\"\n        @click=\"back.click\"\n        class=\"subheader-action\"\n        variant=\"light-primary\"\n      >\n        {{ back.label || $toolkit.t('subheader.formMode.back') }}\n      </me-button>\n\n      <me-button\n        v-if=\"next\"\n        :prepend-icon=\"next.prependIcon || ''\"\n        :append-icon=\"next.appendIcon || 'me-icon-l icon-arrow-right'\"\n        :disabled=\"next.disabled\"\n        @click=\"next.click\"\n        class=\"subheader-action\"\n        variant=\"primary\"\n      >\n        {{ next.label || $toolkit.t('subheader.formMode.next') }}\n      </me-button>\n    </div>\n  </div>\n</template>\n\n<script>\nimport WizardActions from './WizardActions.vue'\n\nexport default {\n  name: 'MeSubheaderForm',\n  components: {\n    WizardActions\n  },\n\n  props: {\n    cancel: {\n      type: Object,\n      default: null\n    },\n    back: {\n      type: Object,\n      default: null\n    },\n    next: {\n      type: Object,\n      default: null\n    },\n    wizard: {\n      type: Object,\n      default: null\n    }\n  }\n\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .subheader-form {\n    display: flex;\n    align-items: center;\n    width: 100%;\n    height: 100%;\n\n    .subheader-action {\n      margin-right: 32px;\n\n      &:last-child {\n        margin: 0;\n      }\n    }\n\n    .right-actions {\n      display: flex;\n      align-items: center;\n      margin-left: auto;\n    }\n  }\n</style>\n",".subheader-form {\n  display: flex;\n  align-items: center;\n  width: 100%;\n  height: 100%;\n}\n.subheader-form .subheader-action {\n  margin-right: 32px;\n}\n.subheader-form .subheader-action:last-child {\n  margin: 0;\n}\n.subheader-form .right-actions {\n  display: flex;\n  align-items: center;\n  margin-left: auto;\n}\n\n/*# sourceMappingURL=SubheaderForm.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2c = "data-v-ba0d3368";
  /* module identifier */
  const __vue_module_identifier__$2c = undefined;
  /* functional template */
  const __vue_is_functional_template__$2c = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2c, staticRenderFns: __vue_staticRenderFns__$2c },
    __vue_inject_styles__$2c,
    __vue_script__$2c,
    __vue_scope_id__$2c,
    __vue_is_functional_template__$2c,
    __vue_module_identifier__$2c,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$2d = {
  name: 'MeTab',
  components: {
    BTab,
    Icon: __vue_component__
  },
  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: null
    },
    active: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    titleLinkClass: {
      type: [Array, Object, String],
      default: ''
    },
    titleItemClass: {
      type: [Array, Object, String],
      default: ''
    }
  }
};

/* script */
const __vue_script__$2d = script$2d;

/* template */
var __vue_render__$2d = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BTab",
    {
      staticClass: "me-tabs-item",
      attrs: {
        title: _vm.title,
        active: _vm.active,
        disabled: _vm.disabled,
        "title-link-class": _vm.titleLinkClass,
        "title-item-class": _vm.titleItemClass
      },
      scopedSlots: _vm._u([
        {
          key: "title",
          fn: function() {
            return [
              _vm.icon ? _c("Icon", { attrs: { icon: _vm.icon } }) : _vm._e(),
              _vm._v(" " + _vm._s(_vm.title) + "\n  ")
            ]
          },
          proxy: true
        }
      ])
    },
    [_vm._v(" "), _vm._t("default")],
    2
  )
};
var __vue_staticRenderFns__$2d = [];
__vue_render__$2d._withStripped = true;

  /* style */
  const __vue_inject_styles__$2d = function (inject) {
    if (!inject) return
    inject("data-v-435fc5d6_0", { source: "div.me-tabs-item[data-v-435fc5d6] {\n  margin-top: 0.5rem;\n}\n\n/*# sourceMappingURL=Tab.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Tab.vue","Tab.vue"],"names":[],"mappings":"AAwDA;EACA,kBAAA;ACvDA;;AAEA,kCAAkC","file":"Tab.vue","sourcesContent":["<template>\n  <BTab\n    :title=\"title\"\n    :active=\"active\"\n    :disabled=\"disabled\"\n    :title-link-class=\"titleLinkClass\"\n    :title-item-class=\"titleItemClass\"\n    class=\"me-tabs-item\"\n  >\n    <template v-slot:title>\n      <Icon v-if=\"icon\" :icon=\"icon\" /> {{ title }}\n    </template>\n    <slot />\n  </BTab>\n</template>\n\n<script>\nimport { BTab } from 'bootstrap-vue'\nimport Icon from './Icon.vue'\n\nexport default {\n  name: 'MeTab',\n  components: {\n    BTab,\n    Icon\n  },\n  props: {\n    title: {\n      type: String,\n      required: true\n    },\n    icon: {\n      type: String,\n      default: null\n    },\n    active: {\n      type: Boolean,\n      default: false\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    titleLinkClass: {\n      type: [Array, Object, String],\n      default: ''\n    },\n    titleItemClass: {\n      type: [Array, Object, String],\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-tabs-item {\n    margin-top: 0.5rem;\n  }\n</style>\n","div.me-tabs-item {\n  margin-top: 0.5rem;\n}\n\n/*# sourceMappingURL=Tab.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2d = "data-v-435fc5d6";
  /* module identifier */
  const __vue_module_identifier__$2d = undefined;
  /* functional template */
  const __vue_is_functional_template__$2d = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2d, staticRenderFns: __vue_staticRenderFns__$2d },
    __vue_inject_styles__$2d,
    __vue_script__$2d,
    __vue_scope_id__$2d,
    __vue_is_functional_template__$2d,
    __vue_module_identifier__$2d,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$2e = {
  name: 'MeTabs',
  components: {
    BTabs
  },
  props: {
    value: {
      type: Number,
      default: 0
    },
    pills: {
      type: Boolean,
      default: false
    },
    card: {
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: false
    },
    activeNavItemClass: {
      type: [Array, Object, String],
      default: ''
    },
    activeTabClass: {
      type: [Array, Object, String],
      default: ''
    },
    contentClass: {
      type: [Array, Object, String],
      default: ''
    }
  }
};

/* script */
const __vue_script__$2e = script$2e;

/* template */
var __vue_render__$2e = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "BTabs",
    {
      staticClass: "me-tabs",
      class: { card: _vm.card },
      attrs: {
        value: _vm.value,
        pills: _vm.pills,
        card: _vm.card,
        lazy: _vm.lazy,
        "active-nav-item-class": _vm.activeNavItemClass,
        "active-tab-class": _vm.activeTabClass,
        "content-class": _vm.contentClass
      },
      on: {
        input: function($event) {
          return _vm.$emit("input", $event)
        }
      }
    },
    [_vm._t("default")],
    2
  )
};
var __vue_staticRenderFns__$2e = [];
__vue_render__$2e._withStripped = true;

  /* style */
  const __vue_inject_styles__$2e = function (inject) {
    if (!inject) return
    inject("data-v-01852069_0", { source: "div.me-tabs.card[data-v-01852069] {\n  border: 1px solid var(--me-gray-6);\n}\n[data-v-01852069] .nav-pills .nav-item .nav-link.active {\n  color: var(--me-white);\n}\n[data-v-01852069] .nav-tabs:not(.card-header-tabs) {\n  border-bottom: 1px solid var(--me-gray-6);\n}\n[data-v-01852069] .nav-item.show {\n  border: none;\n}\n[data-v-01852069] .nav-item .nav-link {\n  color: var(--me-gray-2);\n  border: none;\n  font-size: 14px;\n  padding: 13px 24px;\n}\n[data-v-01852069] .nav-item .nav-link:focus {\n  border: none;\n}\n[data-v-01852069] .nav-item .nav-link.disabled {\n  color: var(--me-gray-4);\n}\n[data-v-01852069] .nav-item .nav-link.active {\n  color: var(--me-primary-2);\n  border-bottom: 2px solid var(--me-primary-2);\n}\n[data-v-01852069] .nav-item .nav-link.active:hover {\n  border-top: none;\n  border-right: none;\n  border-left: none;\n}\n[data-v-01852069] .nav-item .nav-link[class=nav-link]:hover {\n  border: none;\n}\n\n/*# sourceMappingURL=Tabs.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Tabs.vue","Tabs.vue"],"names":[],"mappings":"AA4DA;EACA,kCAAA;AC3DA;ADgEA;EACA,sBAAA;AC7DA;ADiEA;EACA,yCAAA;AC9DA;ADkEA;EACA,YAAA;AC/DA;ADkEA;EACA,uBAAA;EACA,YAAA;EACA,eAAA;EACA,kBAAA;AChEA;ADkEA;EACA,YAAA;AChEA;ADmEA;EACA,uBAAA;ACjEA;ADoEA;EACA,0BAAA;EACA,4CAAA;AClEA;ADoEA;EACA,gBAAA;EACA,kBAAA;EACA,iBAAA;AClEA;ADsEA;EACA,YAAA;ACpEA;;AAEA,mCAAmC","file":"Tabs.vue","sourcesContent":["<template>\n  <BTabs\n    :value=\"value\"\n    :class=\"{ card: card }\"\n    :pills=\"pills\"\n    :card=\"card\"\n    :lazy=\"lazy\"\n    @input=\"$emit('input', $event)\"\n    :active-nav-item-class=\"activeNavItemClass\"\n    :active-tab-class=\"activeTabClass\"\n    :content-class=\"contentClass\"\n    class=\"me-tabs\"\n  >\n    <slot />\n  </BTabs>\n</template>\n\n<script>\nimport { BTabs } from 'bootstrap-vue'\n\nexport default {\n  name: 'MeTabs',\n  components: {\n    BTabs\n  },\n  props: {\n    value: {\n      type: Number,\n      default: 0\n    },\n    pills: {\n      type: Boolean,\n      default: false\n    },\n    card: {\n      type: Boolean,\n      default: false\n    },\n    lazy: {\n      type: Boolean,\n      default: false\n    },\n    activeNavItemClass: {\n      type: [Array, Object, String],\n      default: ''\n    },\n    activeTabClass: {\n      type: [Array, Object, String],\n      default: ''\n    },\n    contentClass: {\n      type: [Array, Object, String],\n      default: ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  div.me-tabs {\n    &.card {\n      border: 1px solid var(--me-gray-6);\n    }\n  }\n\n  ::v-deep .nav-pills {\n    .nav-item .nav-link.active {\n      color: var(--me-white);\n    }\n  }\n\n  ::v-deep .nav-tabs:not(.card-header-tabs) {\n    border-bottom: 1px solid var(--me-gray-6);\n  }\n\n  ::v-deep .nav-item {\n    &.show {\n      border: none;\n    }\n\n    .nav-link {\n      color: var(--me-gray-2);\n      border: none;\n      font-size: 14px;\n      padding: 13px 24px;\n\n      &:focus {\n        border: none;\n      }\n\n      &.disabled {\n        color: var(--me-gray-4);\n      }\n\n      &.active {\n        color: var(--me-primary-2);\n        border-bottom: 2px solid var(--me-primary-2);\n\n        &:hover {\n          border-top: none;\n          border-right: none;\n          border-left: none;\n        }\n      }\n\n      &[class=\"nav-link\"]:hover {\n        border: none;\n      }\n    }\n  }\n</style>\n","div.me-tabs.card {\n  border: 1px solid var(--me-gray-6);\n}\n\n::v-deep .nav-pills .nav-item .nav-link.active {\n  color: var(--me-white);\n}\n\n::v-deep .nav-tabs:not(.card-header-tabs) {\n  border-bottom: 1px solid var(--me-gray-6);\n}\n\n::v-deep .nav-item.show {\n  border: none;\n}\n::v-deep .nav-item .nav-link {\n  color: var(--me-gray-2);\n  border: none;\n  font-size: 14px;\n  padding: 13px 24px;\n}\n::v-deep .nav-item .nav-link:focus {\n  border: none;\n}\n::v-deep .nav-item .nav-link.disabled {\n  color: var(--me-gray-4);\n}\n::v-deep .nav-item .nav-link.active {\n  color: var(--me-primary-2);\n  border-bottom: 2px solid var(--me-primary-2);\n}\n::v-deep .nav-item .nav-link.active:hover {\n  border-top: none;\n  border-right: none;\n  border-left: none;\n}\n::v-deep .nav-item .nav-link[class=nav-link]:hover {\n  border: none;\n}\n\n/*# sourceMappingURL=Tabs.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2e = "data-v-01852069";
  /* module identifier */
  const __vue_module_identifier__$2e = undefined;
  /* functional template */
  const __vue_is_functional_template__$2e = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2e, staticRenderFns: __vue_staticRenderFns__$2e },
    __vue_inject_styles__$2e,
    __vue_script__$2e,
    __vue_scope_id__$2e,
    __vue_is_functional_template__$2e,
    __vue_module_identifier__$2e,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const toastIcons = {
  primary: {
    icon: 'me-icon-l icon-circle-info',
    color: 'var(--me-primary-2)'
  },
  danger: {
    icon: 'me-icon-l icon-triangle-exclamation',
    color: 'var(--me-danger-2)'
  },
  success: {
    icon: 'me-icon-l icon-circle-check',
    color: 'var(--me-success-0)'
  },
  warning: {
    icon: 'me-icon-l icon-circle-exclamation',
    color: 'var(--me-warning-0 )'
  }
};

var script$2f = {
  name: 'MeToast',
  components: {
    BToast,
    Icon: __vue_component__,
    MeButton: __vue_component__$6
  },

  model: {
    prop: 'visible'
  },

  props: {
    noAutoHide: {
      type: Boolean,
      default: false
    },
    content: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: null
    },
    inPlace: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      default: 'default'
    },
    variant: {
      type: String,
      default: 'primary'
    },
    visible: {
      type: Boolean,
      default: false
    },
    autoHideDelay: {
      type: Number,
      default: 5000
    },
    action: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      model: this.visible
    }
  },

  computed: {
    toastIcon() {
      return toastIcons[this.variant] || toastIcons.primary
    }
  },

  watch: {
    visible(val) {
      this.model = val;
    }
  },

  methods: {
    show() {
      this.model = true;
    }
  }
};

/* script */
const __vue_script__$2f = script$2f;

/* template */
var __vue_render__$2f = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "b-toast",
    {
      attrs: {
        id: _vm.id,
        toaster: "b-toaster-" + _vm.position,
        variant: _vm.variant,
        "auto-hide-delay": _vm.autoHideDelay,
        "no-auto-hide": _vm.noAutoHide,
        static: _vm.inPlace,
        "no-close-button": "",
        solid: "",
        "toast-class": "me-toast",
        "data-cy": "b-toast"
      },
      on: {
        show: function($event) {
          return _vm.$emit("show")
        },
        hide: function($event) {
          return _vm.$emit("hide")
        }
      },
      model: {
        value: _vm.model,
        callback: function($$v) {
          _vm.model = $$v;
        },
        expression: "model"
      }
    },
    [
      _vm._t("default", [
        _c(
          "div",
          { staticClass: "me-toast-container" },
          [
            _c("Icon", {
              attrs: {
                size: 18,
                icon: _vm.toastIcon.icon,
                color: _vm.toastIcon.color
              }
            }),
            _vm._v(" "),
            _c(
              "div",
              { staticClass: "main-content" },
              [
                _c(
                  "div",
                  { staticClass: "content-header" },
                  [
                    _vm._t("content", [
                      _c("div", { staticClass: "text-content" }, [
                        _vm._v(
                          "\n              " +
                            _vm._s(_vm.content) +
                            "\n            "
                        )
                      ])
                    ]),
                    _vm._v(" "),
                    _c("Icon", {
                      attrs: {
                        size: 18,
                        icon: "me-icon-l icon-xmark",
                        color: "var(--me-gray-2)",
                        "data-cy": "toast-dismiss"
                      },
                      on: {
                        click: function($event) {
                          _vm.model = false;
                        }
                      }
                    })
                  ],
                  2
                ),
                _vm._v(" "),
                _vm._t("description", [
                  _vm.description
                    ? _c("div", { staticClass: "text-description" }, [
                        _vm._v(
                          "\n            " +
                            _vm._s(_vm.description) +
                            "\n          "
                        )
                      ])
                    : _vm._e()
                ])
              ],
              2
            )
          ],
          1
        ),
        _vm._v(" "),
        _vm.action
          ? _c(
              "div",
              { staticClass: "me-toast-footer" },
              [
                _c(
                  "me-button",
                  {
                    attrs: {
                      size: "sm",
                      variant: "outline-secondary",
                      "data-cy": "toast-action"
                    },
                    on: { click: _vm.action.click }
                  },
                  [_vm._v("\n        " + _vm._s(_vm.action.label) + "\n      ")]
                )
              ],
              1
            )
          : _vm._e()
      ])
    ],
    2
  )
};
var __vue_staticRenderFns__$2f = [];
__vue_render__$2f._withStripped = true;

  /* style */
  const __vue_inject_styles__$2f = function (inject) {
    if (!inject) return
    inject("data-v-5494cf7c_0", { source: ".b-toast-primary[data-v-5494cf7c]  .me-toast, .b-toast-secondary[data-v-5494cf7c]  .me-toast, .b-toast-success[data-v-5494cf7c]  .me-toast, .b-toast-danger[data-v-5494cf7c]  .me-toast, .b-toast-warning[data-v-5494cf7c]  .me-toast {\n  border: none;\n  color: var(--me-gray-2);\n}\n.b-toast-primary[data-v-5494cf7c]  .me-toast {\n  background: var(me-primary-7);\n}\n.b-toast-secondary[data-v-5494cf7c]  .me-toast {\n  background: var(--me-gray-2);\n}\n.b-toast-success[data-v-5494cf7c]  .me-toast {\n  background: var(--me-success-7);\n}\n.b-toast-danger[data-v-5494cf7c]  .me-toast {\n  background: var(--me-danger-7);\n}\n.b-toast-warning[data-v-5494cf7c]  .me-toast {\n  background: var(--me-warning-7);\n}\n[data-v-5494cf7c] div.me-toast {\n  box-shadow: var(--me-shadow-level-2);\n  max-width: 348px;\n  border-radius: 8px;\n}\n[data-v-5494cf7c] div.me-toast .toast-body {\n  display: flex;\n  flex-direction: column;\n  padding: 16px;\n  gap: 12px;\n}\n@media screen and (max-width: 480px) {\n[data-v-5494cf7c] div.me-toast {\n    min-width: 328px;\n}\n}\n.me-toast-container[data-v-5494cf7c] {\n  display: flex;\n  gap: 8px;\n}\n.me-toast-container .main-content[data-v-5494cf7c] {\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  gap: 6px;\n}\n.me-toast-container .main-content .content-header[data-v-5494cf7c] {\n  display: flex;\n  justify-content: space-between;\n  gap: 10px;\n}\n.me-toast-container .main-content .content-header .text-content[data-v-5494cf7c] {\n  font-weight: 600;\n  align-self: center;\n}\n.me-toast-container .main-content .text-description[data-v-5494cf7c] {\n  font-size: 12px;\n}\n.me-toast-footer[data-v-5494cf7c] {\n  display: flex;\n  justify-content: flex-end;\n}\n\n/*# sourceMappingURL=Toast.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Toast.vue","Toast.vue"],"names":[],"mappings":"AAgLA;EACA,YAAA;EACA,uBAAA;AC/KA;ADmLA;EACA,6BAAA;ACjLA;ADoLA;EACA,4BAAA;AClLA;ADqLA;EACA,+BAAA;ACnLA;ADsLA;EACA,8BAAA;ACpLA;ADuLA;EACA,+BAAA;ACrLA;ADyLA;EACA,oCAAA;EACA,gBAAA;EACA,kBAAA;ACtLA;ADwLA;EACA,aAAA;EACA,sBAAA;EACA,aAAA;EACA,SAAA;ACtLA;ADyLA;AAZA;IAaA,gBAAA;ACtLE;AACF;ADyLA;EACA,aAAA;EACA,QAAA;ACtLA;ADwLA;EACA,aAAA;EACA,sBAAA;EACA,YAAA;EACA,QAAA;ACtLA;ADwLA;EACA,aAAA;EACA,8BAAA;EACA,SAAA;ACtLA;ADwLA;EACA,gBAAA;EACA,kBAAA;ACtLA;AD0LA;EACA,eAAA;ACxLA;AD6LA;EACA,aAAA;EACA,yBAAA;AC1LA;;AAEA,oCAAoC","file":"Toast.vue","sourcesContent":["<template>\n  <b-toast\n    :id=\"id\"\n    v-model=\"model\"\n    :toaster=\"`b-toaster-${position}`\"\n    :variant=\"variant\"\n    :auto-hide-delay=\"autoHideDelay\"\n    :no-auto-hide=\"noAutoHide\"\n    :static=\"inPlace\"\n    @show=\"$emit('show')\"\n    @hide=\"$emit('hide')\"\n    no-close-button\n    solid\n    toast-class=\"me-toast\"\n    data-cy=\"b-toast\"\n  >\n    <slot>\n      <div class=\"me-toast-container\">\n        <Icon\n          :size=\"18\"\n          :icon=\"toastIcon.icon\"\n          :color=\"toastIcon.color\"\n        />\n\n        <div class=\"main-content\">\n          <div class=\"content-header\">\n            <slot name=\"content\">\n              <div class=\"text-content\">\n                {{ content }}\n              </div>\n            </slot>\n\n            <Icon\n              :size=\"18\"\n              @click=\"model = false\"\n              icon=\"me-icon-l icon-xmark\"\n              color=\"var(--me-gray-2)\"\n              data-cy=\"toast-dismiss\"\n            />\n          </div>\n\n          <slot name=\"description\">\n            <div v-if=\"description\" class=\"text-description\">\n              {{ description }}\n            </div>\n          </slot>\n        </div>\n      </div>\n\n      <div v-if=\"action\" class=\"me-toast-footer\">\n        <me-button\n          @click=\"action.click\"\n          size=\"sm\"\n          variant=\"outline-secondary\"\n          data-cy=\"toast-action\"\n        >\n          {{ action.label }}\n        </me-button>\n      </div>\n    </slot>\n  </b-toast>\n</template>\n\n<script>\nimport { BToast } from 'bootstrap-vue'\nimport Icon from './Icon.vue'\nimport MeButton from './Button.vue'\n\nconst toastIcons = {\n  primary: {\n    icon: 'me-icon-l icon-circle-info',\n    color: 'var(--me-primary-2)'\n  },\n  danger: {\n    icon: 'me-icon-l icon-triangle-exclamation',\n    color: 'var(--me-danger-2)'\n  },\n  success: {\n    icon: 'me-icon-l icon-circle-check',\n    color: 'var(--me-success-0)'\n  },\n  warning: {\n    icon: 'me-icon-l icon-circle-exclamation',\n    color: 'var(--me-warning-0 )'\n  }\n}\n\nexport default {\n  name: 'MeToast',\n  components: {\n    BToast,\n    Icon,\n    MeButton\n  },\n\n  model: {\n    prop: 'visible'\n  },\n\n  props: {\n    noAutoHide: {\n      type: Boolean,\n      default: false\n    },\n    content: {\n      type: String,\n      default: ''\n    },\n    description: {\n      type: String,\n      default: ''\n    },\n    id: {\n      type: String,\n      default: null\n    },\n    inPlace: {\n      type: Boolean,\n      default: false\n    },\n    position: {\n      type: String,\n      default: 'default'\n    },\n    variant: {\n      type: String,\n      default: 'primary'\n    },\n    visible: {\n      type: Boolean,\n      default: false\n    },\n    autoHideDelay: {\n      type: Number,\n      default: 5000\n    },\n    action: {\n      type: Object,\n      default: null\n    }\n  },\n\n  data() {\n    return {\n      model: this.visible\n    }\n  },\n\n  computed: {\n    toastIcon() {\n      return toastIcons[this.variant] || toastIcons.primary\n    }\n  },\n\n  watch: {\n    visible(val) {\n      this.model = val\n    }\n  },\n\n  methods: {\n    show() {\n      this.model = true\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n\n.b-toast {\n  &-primary,\n  &-secondary,\n  &-success,\n  &-danger,\n  &-warning {\n    ::v-deep .me-toast {\n      border: none;\n      color: var(--me-gray-2);\n    }\n  }\n\n  &-primary ::v-deep .me-toast {\n    background: var(me-primary-7);\n  }\n\n  &-secondary ::v-deep .me-toast {\n    background: var(--me-gray-2);\n  }\n\n  &-success ::v-deep .me-toast {\n    background: var(--me-success-7);\n  }\n\n  &-danger ::v-deep .me-toast {\n    background: var(--me-danger-7);\n  }\n\n  &-warning ::v-deep .me-toast {\n    background: var(--me-warning-7);\n  }\n}\n\n::v-deep div.me-toast {\n  box-shadow: var(--me-shadow-level-2);\n  max-width: 348px;\n  border-radius: 8px;\n\n  .toast-body {\n    display: flex;\n    flex-direction: column;\n    padding: 16px;\n    gap: 12px;\n  }\n\n  @media screen and (max-width: 480px) {\n    min-width: 328px;\n  }\n}\n\n.me-toast-container {\n  display: flex;\n  gap: 8px;\n\n  .main-content {\n    display: flex;\n    flex-direction: column;\n    flex-grow: 1;\n    gap: 6px;\n\n    .content-header {\n      display: flex;\n      justify-content: space-between;\n      gap: 10px;\n\n      .text-content {\n        font-weight: 600;\n        align-self: center;\n      }\n    }\n\n    .text-description {\n      font-size: 12px;\n    }\n  }\n}\n\n.me-toast-footer {\n  display: flex;\n  justify-content: flex-end;\n}\n</style>\n",".b-toast-primary ::v-deep .me-toast, .b-toast-secondary ::v-deep .me-toast, .b-toast-success ::v-deep .me-toast, .b-toast-danger ::v-deep .me-toast, .b-toast-warning ::v-deep .me-toast {\n  border: none;\n  color: var(--me-gray-2);\n}\n.b-toast-primary ::v-deep .me-toast {\n  background: var(me-primary-7);\n}\n.b-toast-secondary ::v-deep .me-toast {\n  background: var(--me-gray-2);\n}\n.b-toast-success ::v-deep .me-toast {\n  background: var(--me-success-7);\n}\n.b-toast-danger ::v-deep .me-toast {\n  background: var(--me-danger-7);\n}\n.b-toast-warning ::v-deep .me-toast {\n  background: var(--me-warning-7);\n}\n\n::v-deep div.me-toast {\n  box-shadow: var(--me-shadow-level-2);\n  max-width: 348px;\n  border-radius: 8px;\n}\n::v-deep div.me-toast .toast-body {\n  display: flex;\n  flex-direction: column;\n  padding: 16px;\n  gap: 12px;\n}\n@media screen and (max-width: 480px) {\n  ::v-deep div.me-toast {\n    min-width: 328px;\n  }\n}\n\n.me-toast-container {\n  display: flex;\n  gap: 8px;\n}\n.me-toast-container .main-content {\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  gap: 6px;\n}\n.me-toast-container .main-content .content-header {\n  display: flex;\n  justify-content: space-between;\n  gap: 10px;\n}\n.me-toast-container .main-content .content-header .text-content {\n  font-weight: 600;\n  align-self: center;\n}\n.me-toast-container .main-content .text-description {\n  font-size: 12px;\n}\n\n.me-toast-footer {\n  display: flex;\n  justify-content: flex-end;\n}\n\n/*# sourceMappingURL=Toast.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2f = "data-v-5494cf7c";
  /* module identifier */
  const __vue_module_identifier__$2f = undefined;
  /* functional template */
  const __vue_is_functional_template__$2f = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2f, staticRenderFns: __vue_staticRenderFns__$2f },
    __vue_inject_styles__$2f,
    __vue_script__$2f,
    __vue_scope_id__$2f,
    __vue_is_functional_template__$2f,
    __vue_module_identifier__$2f,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$2g = {
  name: 'MeTreeMenu',

  props: {
    name: {
      type: String,
      required: true
    },
    childs: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      opened: false,
      openedSubchild: '',
      currentChilds: [],
      childsBacktrace: []
    }
  },

  methods: {
    openTreeMenu() {
      this.opened = true;
      this.currentChilds = [...this.childs];
      this.openedSubchild = '';
      this.childsBacktrace = [];
      const position = (this.$refs['tree-menu'].parentElement || this.$refs['tree-menu']).getBoundingClientRect();
      this.$refs['tree-menu-sidebar'].style.left = `${position.right + 2}px`;
      this.$refs['tree-menu-sidebar'].style.top = `${position.top}px`;
      this.$refs['tree-menu-sidebar'].style.height = `${position.height}px`;

      this.$refs['tree-menu-overlay'].style.left = `${position.right + 2}px`;
      this.$refs['tree-menu-overlay'].style.top = `${position.top}px`;
      this.$refs['tree-menu-overlay'].style.height = `${position.height}px`;
      this.$refs['tree-menu-overlay'].style.right = `${position.left}px`;
      document.body.style.overflow = 'hidden';
    },

    closeTreeMenu() {
      this.opened = false;
      this.$refs['tree-menu-sidebar'].style.removeProperty('left');
      document.body.style.overflow = 'auto';
    },

    toggleTreeMenu() {
      this.opened ? this.closeTreeMenu() : this.openTreeMenu();
    },
    /* istanbul ignore next */
    doClick(item) {
      if (item?.click) {
        item.click();
      }

      if (item?.childs?.length && !item.hasChildren) {
        this.handleChildClick(item);
      } else {
        this.$emit('click', item);
      }
    },
    /* istanbul ignore next */
    handleChildClick(item) {
      this.childsBacktrace.push(this.currentChilds);
      this.openedSubchild = item.name;
      this.currentChilds = item.childs;
    },
    /* istanbul ignore next */
    backSubchild() {
      this.currentChilds = this.childsBacktrace.pop();
      let openedSubchildBacktrace;
      this.childsBacktrace.forEach((items) => {
        openedSubchildBacktrace = items.find((item) => { return item.childs === this.currentChilds });
      });
      this.openedSubchild = openedSubchildBacktrace ? openedSubchildBacktrace.name : '';
    }
  }
};

/* script */
const __vue_script__$2g = script$2g;

/* template */
var __vue_render__$2g = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("div", { ref: "tree-menu", staticClass: "me-tree-menu" }, [
    _c("div", { staticClass: "tree-menu-outer" }, [
      _c(
        "div",
        {
          ref: "tree-menu-sidebar",
          staticClass: "tree-menu-sidebar",
          class: { opened: _vm.opened }
        },
        [
          _vm.openedSubchild
            ? _c(
                "div",
                {
                  staticClass: "section-header subsection",
                  on: { click: _vm.backSubchild }
                },
                [
                  _c("i", { staticClass: "me-icon icon-arrow-left" }),
                  _vm._v("\n        " + _vm._s(_vm.openedSubchild) + "\n      ")
                ]
              )
            : _vm._e(),
          _vm._v(" "),
          _vm.currentChilds.length > 0
            ? _vm._l(_vm.currentChilds, function(child, index) {
                return _c("div", { key: index }, [
                  child.section
                    ? _c(
                        "div",
                        {
                          directives: [
                            {
                              name: "me-tooltip",
                              rawName: "v-me-tooltip.ellipsis.hover",
                              value: child.section,
                              expression: "child.section",
                              modifiers: { ellipsis: true, hover: true }
                            }
                          ],
                          staticClass: "section-header"
                        },
                        [
                          _vm._v(
                            "\n            " +
                              _vm._s(child.section) +
                              "\n          "
                          )
                        ]
                      )
                    : _c(
                        "div",
                        {
                          staticClass: "section-item",
                          class: {
                            "has-child": child.childs || child.hasChildren
                          },
                          on: {
                            click: function($event) {
                              return _vm.doClick(child)
                            }
                          }
                        },
                        [
                          _c(
                            "div",
                            {
                              directives: [
                                {
                                  name: "me-tooltip",
                                  rawName: "v-me-tooltip",
                                  value:
                                    child.name.length > 30 ? child.name : "",
                                  expression:
                                    "child.name.length > 30 ? child.name : ''"
                                }
                              ],
                              staticClass: "option-item"
                            },
                            [
                              _vm._v(
                                "\n              " +
                                  _vm._s(
                                    _vm._f("me-cut-text")(child.name, 30)
                                  ) +
                                  "\n            "
                              )
                            ]
                          ),
                          _vm._v(" "),
                          (child.childs && child.childs.length) ||
                          child.hasChildren
                            ? _c("i", {
                                staticClass: "me-icon icon-chevron-right"
                              })
                            : _vm._e()
                        ]
                      )
                ])
              })
            : [
                _c(
                  "div",
                  { staticClass: "section-header" },
                  [_vm._t("title")],
                  2
                ),
                _vm._v(" "),
                _c(
                  "div",
                  { staticClass: "section-content" },
                  [_vm._t("content", null, null, { close: _vm.closeTreeMenu })],
                  2
                )
              ]
        ],
        2
      ),
      _vm._v(" "),
      _c("div", {
        ref: "tree-menu-overlay",
        staticClass: "tree-menu-overlay",
        class: { opened: _vm.opened },
        on: { click: _vm.closeTreeMenu }
      })
    ]),
    _vm._v(" "),
    _c(
      "div",
      {
        staticClass: "tree-menu-link",
        class: { active: _vm.opened },
        on: { click: _vm.toggleTreeMenu }
      },
      [_vm._v("\n    " + _vm._s(_vm.name) + "\n  ")]
    )
  ])
};
var __vue_staticRenderFns__$2g = [];
__vue_render__$2g._withStripped = true;

  /* style */
  const __vue_inject_styles__$2g = function (inject) {
    if (!inject) return
    inject("data-v-1fa9e83c_0", { source: "@charset \"UTF-8\";\n.tree-menu-link[data-v-1fa9e83c] {\n  padding: 2px 4px;\n  display: flex;\n  justify-content: space-between;\n  font-weight: 400;\n  align-items: center;\n  font-size: 14px;\n  color: #4a4a4a;\n  text-indent: 26px;\n}\n.tree-menu-link[data-v-1fa9e83c]:hover {\n  background: rgba(0, 0, 0, 0.06);\n  cursor: pointer;\n}\n.tree-menu-link.active[data-v-1fa9e83c] {\n  background: #0A8CCC1F;\n}\n.tree-menu-link[data-v-1fa9e83c]:after {\n  font-family: \"ME Icons Light\";\n  content: \"\";\n  font-size: 22px;\n  color: #0000007A;\n}\n.section-header[data-v-1fa9e83c] {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  color: #999;\n  font-size: 14px;\n  border-bottom: 1px solid #eee;\n  width: calc(100% - 32px);\n  margin: 16px;\n}\n.section-header.subsection[data-v-1fa9e83c] {\n  cursor: pointer;\n}\n.section-content[data-v-1fa9e83c],\n.section-item[data-v-1fa9e83c] {\n  padding: 8px 26px 8px 16px;\n}\n.section-item[data-v-1fa9e83c] {\n  display: flex;\n  justify-content: space-between;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  width: 100%;\n  font-size: 14px;\n}\n.section-item[data-v-1fa9e83c]:hover {\n  cursor: pointer;\n  background: rgba(10, 140, 204, 0.12157);\n}\n.section-item.has-child[data-v-1fa9e83c] {\n  display: flex;\n  justify-content: space-between;\n  position: relative;\n}\n.section-item.has-child > div[data-v-1fa9e83c] {\n  flex-grow: 1;\n}\n.section-item.has-child i[data-v-1fa9e83c] {\n  font-size: 22px;\n  color: var(--me-gray-2);\n  position: absolute;\n  right: 16px;\n  top: 2px;\n}\n.tree-menu-outer[data-v-1fa9e83c] {\n  position: fixed;\n  top: 0;\n  left: 0;\n  bottom: 0;\n  right: 0;\n  height: 100%;\n  width: 0;\n  z-index: -1;\n  overflow: visible;\n}\n.tree-menu-overlay[data-v-1fa9e83c], .tree-menu-sidebar[data-v-1fa9e83c] {\n  left: 250px;\n  visibility: hidden;\n  transition: all 0.3s;\n}\n.tree-menu-overlay[data-v-1fa9e83c] {\n  position: fixed;\n  background: var(--me-gray-0);\n  z-index: -1;\n  opacity: 0;\n}\n.tree-menu-sidebar[data-v-1fa9e83c] {\n  left: 0;\n  position: absolute;\n  width: 300px;\n  background: white;\n  overflow: auto;\n  opacity: 0;\n  height: 100vh;\n  padding-bottom: 15px;\n}\n.tree-menu-sidebar.opened[data-v-1fa9e83c] {\n  visibility: visible;\n  opacity: 1;\n}\n.tree-menu-overlay.opened[data-v-1fa9e83c] {\n  visibility: visible;\n  opacity: 0.5;\n}\n\n/*# sourceMappingURL=TreeMenu.vue.map */", map: {"version":3,"sources":["TreeMenu.vue","/app/build/src/components/TreeMenu.vue"],"names":[],"mappings":"AAAA,gBAAgB;ACgIhB;EACA,gBAAA;EACA,aAAA;EACA,8BAAA;EACA,gBAAA;EACA,mBAAA;EACA,eAAA;EACA,cAAA;EACA,iBAAA;AD9HA;ACgIA;EACA,+BAAA;EACA,eAAA;AD9HA;ACiIA;EACA,qBAAA;AD/HA;ACkIA;EACA,6BAAA;EACA,YAAA;EACA,eAAA;EACA,gBAAA;ADhIA;ACoIA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,WAAA;EACA,eAAA;EACA,6BAAA;EACA,wBAAA;EACA,YAAA;ADjIA;ACmIA;EACA,eAAA;ADjIA;ACqIA;;EAEA,0BAAA;ADlIA;ACqIA;EACA,aAAA;EACA,8BAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,WAAA;EACA,eAAA;ADlIA;ACoIA;EACA,eAAA;EACA,uCAAA;ADlIA;ACqIA;EACA,aAAA;EACA,8BAAA;EACA,kBAAA;ADnIA;ACqIA;EACA,YAAA;ADnIA;ACsIA;EACA,eAAA;EACA,uBAAA;EACA,kBAAA;EACA,WAAA;EACA,QAAA;ADpIA;ACyIA;EACA,eAAA;EACA,MAAA;EACA,OAAA;EACA,SAAA;EACA,QAAA;EACA,YAAA;EACA,QAAA;EACA,WAAA;EACA,iBAAA;ADtIA;AC0IA;EAEA,WAAA;EACA,kBAAA;EACA,oBAAA;ADxIA;AC2IA;EACA,eAAA;EACA,4BAAA;EACA,WAAA;EACA,UAAA;ADzIA;AC4IA;EACA,OAAA;EACA,kBAAA;EACA,YAAA;EACA,iBAAA;EACA,cAAA;EACA,UAAA;EACA,aAAA;EACA,oBAAA;AD1IA;AC6IA;EACA,mBAAA;EACA,UAAA;AD3IA;AC8IA;EACA,mBAAA;EACA,YAAA;AD5IA;;AAEA,uCAAuC","file":"TreeMenu.vue","sourcesContent":["@charset \"UTF-8\";\n.tree-menu-link {\n  padding: 2px 4px;\n  display: flex;\n  justify-content: space-between;\n  font-weight: 400;\n  align-items: center;\n  font-size: 14px;\n  color: #4a4a4a;\n  text-indent: 26px;\n}\n.tree-menu-link:hover {\n  background: rgba(0, 0, 0, 0.06);\n  cursor: pointer;\n}\n.tree-menu-link.active {\n  background: #0A8CCC1F;\n}\n.tree-menu-link:after {\n  font-family: \"ME Icons Light\";\n  content: \"\";\n  font-size: 22px;\n  color: #0000007A;\n}\n\n.section-header {\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  color: #999;\n  font-size: 14px;\n  border-bottom: 1px solid #eee;\n  width: calc(100% - 32px);\n  margin: 16px;\n}\n.section-header.subsection {\n  cursor: pointer;\n}\n\n.section-content,\n.section-item {\n  padding: 8px 26px 8px 16px;\n}\n\n.section-item {\n  display: flex;\n  justify-content: space-between;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  width: 100%;\n  font-size: 14px;\n}\n.section-item:hover {\n  cursor: pointer;\n  background: rgba(10, 140, 204, 0.12157);\n}\n.section-item.has-child {\n  display: flex;\n  justify-content: space-between;\n  position: relative;\n}\n.section-item.has-child > div {\n  flex-grow: 1;\n}\n.section-item.has-child i {\n  font-size: 22px;\n  color: var(--me-gray-2);\n  position: absolute;\n  right: 16px;\n  top: 2px;\n}\n\n.tree-menu-outer {\n  position: fixed;\n  top: 0;\n  left: 0;\n  bottom: 0;\n  right: 0;\n  height: 100%;\n  width: 0;\n  z-index: -1;\n  overflow: visible;\n}\n\n.tree-menu-overlay, .tree-menu-sidebar {\n  left: 250px;\n  visibility: hidden;\n  transition: all 0.3s;\n}\n.tree-menu-overlay {\n  position: fixed;\n  background: var(--me-gray-0);\n  z-index: -1;\n  opacity: 0;\n}\n.tree-menu-sidebar {\n  left: 0;\n  position: absolute;\n  width: 300px;\n  background: white;\n  overflow: auto;\n  opacity: 0;\n  height: 100vh;\n  padding-bottom: 15px;\n}\n.tree-menu-sidebar.opened {\n  visibility: visible;\n  opacity: 1;\n}\n.tree-menu-overlay.opened {\n  visibility: visible;\n  opacity: 0.5;\n}\n\n/*# sourceMappingURL=TreeMenu.vue.map */","<template>\n  <div ref=\"tree-menu\" class=\"me-tree-menu\">\n    <div class=\"tree-menu-outer\">\n      <div ref=\"tree-menu-sidebar\" :class=\"{ opened }\" class=\"tree-menu-sidebar\">\n        <div v-if=\"openedSubchild\" @click=\"backSubchild\" class=\"section-header subsection\">\n          <i class=\"me-icon icon-arrow-left\" />\n          {{ openedSubchild }}\n        </div>\n\n        <template v-if=\"currentChilds.length > 0\">\n          <div v-for=\"(child, index) in currentChilds\" :key=\"index\">\n            <div v-if=\"child.section\" v-me-tooltip.ellipsis.hover=\"child.section\" class=\"section-header\">\n              {{ child.section }}\n            </div>\n            <div v-else @click=\"doClick(child)\" :class=\"{'has-child': child.childs || child.hasChildren}\" class=\"section-item\">\n              <div v-me-tooltip=\"child.name.length > 30 ? child.name : ''\" class=\"option-item\">\n                {{ child.name | me-cut-text(30) }}\n              </div>\n              <i\n                v-if=\"(child.childs && child.childs.length) || child.hasChildren\"\n                class=\"me-icon icon-chevron-right\"\n              />\n            </div>\n          </div>\n        </template>\n        <template v-else>\n          <div class=\"section-header\">\n            <slot name=\"title\" />\n          </div>\n\n          <div class=\"section-content\">\n            <slot v-bind=\"{ close: closeTreeMenu }\" name=\"content\" />\n          </div>\n        </template>\n      </div>\n\n      <div ref=\"tree-menu-overlay\" :class=\"{ opened }\" @click=\"closeTreeMenu\" class=\"tree-menu-overlay\" />\n    </div>\n\n    <div @click=\"toggleTreeMenu\" :class=\"{ active: opened }\" class=\"tree-menu-link\">\n      {{ name }}\n    </div>\n  </div>\n</template>\n\n<script>\nexport default {\n  name: 'MeTreeMenu',\n\n  props: {\n    name: {\n      type: String,\n      required: true\n    },\n    childs: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      opened: false,\n      openedSubchild: '',\n      currentChilds: [],\n      childsBacktrace: []\n    }\n  },\n\n  methods: {\n    openTreeMenu() {\n      this.opened = true\n      this.currentChilds = [...this.childs]\n      this.openedSubchild = ''\n      this.childsBacktrace = []\n      const position = (this.$refs['tree-menu'].parentElement || this.$refs['tree-menu']).getBoundingClientRect()\n      this.$refs['tree-menu-sidebar'].style.left = `${position.right + 2}px`\n      this.$refs['tree-menu-sidebar'].style.top = `${position.top}px`\n      this.$refs['tree-menu-sidebar'].style.height = `${position.height}px`\n\n      this.$refs['tree-menu-overlay'].style.left = `${position.right + 2}px`\n      this.$refs['tree-menu-overlay'].style.top = `${position.top}px`\n      this.$refs['tree-menu-overlay'].style.height = `${position.height}px`\n      this.$refs['tree-menu-overlay'].style.right = `${position.left}px`\n      document.body.style.overflow = 'hidden'\n    },\n\n    closeTreeMenu() {\n      this.opened = false\n      this.$refs['tree-menu-sidebar'].style.removeProperty('left')\n      document.body.style.overflow = 'auto'\n    },\n\n    toggleTreeMenu() {\n      this.opened ? this.closeTreeMenu() : this.openTreeMenu()\n    },\n    /* istanbul ignore next */\n    doClick(item) {\n      if (item?.click) {\n        item.click()\n      }\n\n      if (item?.childs?.length && !item.hasChildren) {\n        this.handleChildClick(item)\n      } else {\n        this.$emit('click', item)\n      }\n    },\n    /* istanbul ignore next */\n    handleChildClick(item) {\n      this.childsBacktrace.push(this.currentChilds)\n      this.openedSubchild = item.name\n      this.currentChilds = item.childs\n    },\n    /* istanbul ignore next */\n    backSubchild() {\n      this.currentChilds = this.childsBacktrace.pop()\n      let openedSubchildBacktrace\n      this.childsBacktrace.forEach((items) => {\n        openedSubchildBacktrace = items.find((item) => { return item.childs === this.currentChilds })\n      })\n      this.openedSubchild = openedSubchildBacktrace ? openedSubchildBacktrace.name : ''\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .tree-menu-link {\n    padding: 2px 4px;\n    display: flex;\n    justify-content: space-between;\n    font-weight: 400;\n    align-items: center;\n    font-size: 14px;\n    color: #4a4a4a;\n    text-indent: 26px;\n\n    &:hover {\n      background: rgba(0, 0, 0, 0.06);\n      cursor: pointer;\n    }\n\n    &.active {\n      background: #0A8CCC1F;\n    }\n\n    &:after {\n      font-family: 'ME Icons Light';\n      content: \"\\f2a6\";\n      font-size: 22px;\n      color: #0000007A;\n    }\n  }\n\n  .section-header {\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    color: #999;\n    font-size: 14px;\n    border-bottom: 1px solid #eee;\n    width: calc(100% - 32px);\n    margin: 16px;\n\n    &.subsection {\n     cursor: pointer;\n    }\n  }\n\n  .section-content,\n  .section-item {\n    padding: 8px 26px 8px 16px;\n  }\n\n  .section-item {\n    display: flex;\n    justify-content: space-between;\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    width: 100%;\n    font-size: 14px;\n\n    &:hover {\n      cursor: pointer;\n      background: rgba(10,140,204,0.12157);\n    }\n\n    &.has-child {\n      display: flex;\n      justify-content: space-between;\n      position: relative;\n\n      & > div {\n        flex-grow: 1;\n      }\n\n      i {\n        font-size: 22px;\n        color: var(--me-gray-2);\n        position: absolute;\n        right: 16px;\n        top: 2px;\n      }\n    }\n  }\n\n  .tree-menu-outer {\n    position: fixed;\n    top: 0;\n    left: 0;\n    bottom: 0;\n    right: 0;\n    height: 100%;\n    width: 0;\n    z-index: -1;\n    overflow: visible;\n  }\n\n  .tree-menu {\n     &-overlay,\n     &-sidebar {\n      left: 250px;\n      visibility: hidden;\n      transition: all 0.3s;\n    }\n\n    &-overlay {\n      position: fixed;\n      background: var(--me-gray-0);\n      z-index: -1;\n      opacity: 0;\n    }\n\n    &-sidebar {\n      left: 0;\n      position: absolute;\n      width: 300px;\n      background: white;\n      overflow: auto;\n      opacity: 0;\n      height: 100vh;\n      padding-bottom: 15px;\n    }\n\n    &-sidebar.opened {\n      visibility: visible;\n      opacity: 1;\n    }\n\n    &-overlay.opened {\n      visibility: visible;\n      opacity: 0.5;\n    }\n  }\n</style>\n"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2g = "data-v-1fa9e83c";
  /* module identifier */
  const __vue_module_identifier__$2g = undefined;
  /* functional template */
  const __vue_is_functional_template__$2g = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2g, staticRenderFns: __vue_staticRenderFns__$2g },
    __vue_inject_styles__$2g,
    __vue_script__$2g,
    __vue_scope_id__$2g,
    __vue_is_functional_template__$2g,
    __vue_module_identifier__$2g,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//


var script$2h = {
  name: 'MeTreeMenuProvider',
  props: {
    name: {
      type: String,
      required: true
    },
    childs: {
      type: Array,
      required: true
    },
    provider: {
      type: Function,
      required: true
    }
  },

  methods: {
    handleClickProvider(itemHasChildren) {
      this.provider(itemHasChildren, this.callbackSetChildrens);
    },

    callbackSetChildrens(group) {
      this.$refs.meTreeMenuProvider.handleChildClick(group);
    },

    closeTreeMenuProvider() {
      this.$refs.meTreeMenuProvider.closeTreeMenu();
    }
  }
};

/* script */
const __vue_script__$2h = script$2h;

/* template */
var __vue_render__$2h = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c("me-tree-menu", {
    ref: "meTreeMenuProvider",
    attrs: { name: _vm.name, childs: _vm.childs },
    on: { click: _vm.handleClickProvider }
  })
};
var __vue_staticRenderFns__$2h = [];
__vue_render__$2h._withStripped = true;

  /* style */
  const __vue_inject_styles__$2h = undefined;
  /* scoped */
  const __vue_scope_id__$2h = undefined;
  /* module identifier */
  const __vue_module_identifier__$2h = undefined;
  /* functional template */
  const __vue_is_functional_template__$2h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2h, staticRenderFns: __vue_staticRenderFns__$2h },
    __vue_inject_styles__$2h,
    __vue_script__$2h,
    __vue_scope_id__$2h,
    __vue_is_functional_template__$2h,
    __vue_module_identifier__$2h,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$2i = {
  name: 'MeUpload',
  components: {
    Attachment: __vue_component__$v,
    FileInput: __vue_component__$1o
  },
  props: {
    handler: {
      type: Function,
      default: null,
      required: true
    },
    placeholder: {
      type: String,
      default: null
    },
    dropPlaceholder: {
      type: String,
      default: null
    },
    canDeleteFile: {
      type: Boolean,
      default: false
    },
    files: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      currentFiles: cloneDeep_1(this.files)
    }
  },

  watch: {
    files(values) {
      this.currentFiles = cloneDeep_1(values);
    }
  },

  methods: {
    change(filesUploaded) {
      filesUploaded.forEach(this.pushNewFile);
      this.$refs['file-input'].reset();
    },

    pushNewFile(file) {
      const data = {
        done: false,
        fail: false,
        file: file,
        data: {
          ext: file.name.split('.').pop()
        },
        result: {}
      };

      this.currentFiles.push(data);
      this.upload(data);
    },

    upload(data) {
      this.handler({
        file: data.file,
        done(result) {
          data.result = result;
          data.done = true;
        },
        error(result) {
          data.result = result;
          data.fail = true;
        }
      });
    },

    deleteFile(fileId) {
      const fileDeleted = this.currentFiles.find(file => file.result.id === fileId);
      this.currentFiles = this.currentFiles.filter(file => file.result.id !== fileId);

      this.$emit('delete-file', { fileId, files: this.currentFiles, fileDeleted });
    }
  }
};

/* script */
const __vue_script__$2i = script$2i;

/* template */
var __vue_render__$2i = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-upload" },
    [
      _c("FileInput", {
        ref: "file-input",
        attrs: {
          placeholder: _vm.placeholder,
          "drop-placeholder": _vm.dropPlaceholder,
          "plus-circle": "",
          multiple: ""
        },
        on: { input: _vm.change }
      }),
      _vm._v(" "),
      _c(
        "div",
        { staticClass: "me-upload__files" },
        [
          _vm._l(_vm.currentFiles, function(item, index) {
            return _c(
              "div",
              {
                key: index,
                staticClass: "me-upload__file-uploaded",
                attrs: { "data-cy": "upload-files" }
              },
              [
                item.done
                  ? _vm._t(
                      "file",
                      [
                        _c("Attachment", {
                          attrs: {
                            "file-name": item.file.name,
                            "file-size": item.file.size,
                            "file-extension": item.data.ext,
                            "file-id": item.result.id,
                            canDeleteFile: _vm.canDeleteFile,
                            "data-cy": "upload-success"
                          },
                          on: { "delete-file": _vm.deleteFile }
                        })
                      ],
                      { file: item.file, result: item.result }
                    )
                  : item.fail
                  ? _vm._t(
                      "error",
                      [
                        _c("Attachment", {
                          attrs: {
                            "file-name": item.file.name,
                            "file-size": item.file.size,
                            "file-extension": item.data.ext,
                            "file-id": item.result.id,
                            feedback: item.result.message,
                            canDeleteFile: _vm.canDeleteFile,
                            "data-cy": "upload-error"
                          },
                          on: { "delete-file": _vm.deleteFile }
                        })
                      ],
                      { file: item.file, result: item.result }
                    )
                  : _vm._t(
                      "loading",
                      [
                        _c("Attachment", {
                          attrs: {
                            "file-name": item.file.name,
                            "file-size": item.file.size,
                            "file-extension": item.data.ext,
                            canDeleteFile: _vm.canDeleteFile,
                            busy: "",
                            "data-cy": "upload-loading"
                          }
                        })
                      ],
                      { file: item.file }
                    )
              ],
              2
            )
          }),
          _vm._v(" "),
          _c("div"),
          _vm._v(" "),
          _c("div"),
          _vm._v(" "),
          _c("div"),
          _vm._v(" "),
          _c("div")
        ],
        2
      )
    ],
    1
  )
};
var __vue_staticRenderFns__$2i = [];
__vue_render__$2i._withStripped = true;

  /* style */
  const __vue_inject_styles__$2i = function (inject) {
    if (!inject) return
    inject("data-v-3a0c4152_0", { source: ".me-upload__files[data-v-3a0c4152] {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));\n  margin: 0 -10px;\n}\n.me-upload__file-uploaded[data-v-3a0c4152] {\n  margin: 8px;\n}\n\n/*# sourceMappingURL=Upload.vue.map */", map: {"version":3,"sources":["/app/build/src/components/Upload.vue","Upload.vue"],"names":[],"mappings":"AA2JA;EACA,aAAA;EACA,2DAAA;EACA,eAAA;AC1JA;AD6JA;EACA,WAAA;AC3JA;;AAEA,qCAAqC","file":"Upload.vue","sourcesContent":["<template>\n  <div class=\"me-upload\">\n    <FileInput\n      ref=\"file-input\"\n      @input=\"change\"\n      :placeholder=\"placeholder\"\n      :drop-placeholder=\"dropPlaceholder\"\n      plus-circle\n      multiple\n    />\n    <div class=\"me-upload__files\">\n      <div\n        v-for=\"(item, index) in currentFiles\"\n        :key=\"index\"\n        class=\"me-upload__file-uploaded\"\n        data-cy=\"upload-files\"\n      >\n        <slot v-if=\"item.done\" :file=\"item.file\" :result=\"item.result\" name=\"file\">\n          <Attachment\n            :file-name=\"item.file.name\"\n            :file-size=\"item.file.size\"\n            :file-extension=\"item.data.ext\"\n            :file-id=\"item.result.id\"\n            :canDeleteFile=\"canDeleteFile\"\n            @delete-file=\"deleteFile\"\n            data-cy=\"upload-success\"\n          />\n        </slot>\n\n        <slot v-else-if=\"item.fail\" :file=\"item.file\" :result=\"item.result\" name=\"error\">\n          <Attachment\n            :file-name=\"item.file.name\"\n            :file-size=\"item.file.size\"\n            :file-extension=\"item.data.ext\"\n            :file-id=\"item.result.id\"\n            :feedback=\"item.result.message\"\n            :canDeleteFile=\"canDeleteFile\"\n            @delete-file=\"deleteFile\"\n            data-cy=\"upload-error\"\n          />\n        </slot>\n\n        <slot v-else :file=\"item.file\" name=\"loading\">\n          <Attachment\n            :file-name=\"item.file.name\"\n            :file-size=\"item.file.size\"\n            :file-extension=\"item.data.ext\"\n            :canDeleteFile=\"canDeleteFile\"\n            busy\n            data-cy=\"upload-loading\"\n          />\n        </slot>\n      </div>\n      <div />\n      <div />\n      <div />\n      <div />\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport Attachment from './Attachment.vue'\nimport FileInput from './FileInput.vue'\n\nexport default {\n  name: 'MeUpload',\n  components: {\n    Attachment,\n    FileInput\n  },\n  props: {\n    handler: {\n      type: Function,\n      default: null,\n      required: true\n    },\n    placeholder: {\n      type: String,\n      default: null\n    },\n    dropPlaceholder: {\n      type: String,\n      default: null\n    },\n    canDeleteFile: {\n      type: Boolean,\n      default: false\n    },\n    files: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      currentFiles: cloneDeep(this.files)\n    }\n  },\n\n  watch: {\n    files(values) {\n      this.currentFiles = cloneDeep(values)\n    }\n  },\n\n  methods: {\n    change(filesUploaded) {\n      filesUploaded.forEach(this.pushNewFile)\n      this.$refs['file-input'].reset()\n    },\n\n    pushNewFile(file) {\n      const data = {\n        done: false,\n        fail: false,\n        file: file,\n        data: {\n          ext: file.name.split('.').pop()\n        },\n        result: {}\n      }\n\n      this.currentFiles.push(data)\n      this.upload(data)\n    },\n\n    upload(data) {\n      this.handler({\n        file: data.file,\n        done(result) {\n          data.result = result\n          data.done = true\n        },\n        error(result) {\n          data.result = result\n          data.fail = true\n        }\n      })\n    },\n\n    deleteFile(fileId) {\n      const fileDeleted = this.currentFiles.find(file => file.result.id === fileId)\n      this.currentFiles = this.currentFiles.filter(file => file.result.id !== fileId)\n\n      this.$emit('delete-file', { fileId, files: this.currentFiles, fileDeleted })\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-upload {\n    &__files {\n      display: grid;\n      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));\n      margin: 0 -10px;\n    }\n\n    &__file-uploaded {\n      margin: 8px;\n    }\n  }\n</style>\n",".me-upload__files {\n  display: grid;\n  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));\n  margin: 0 -10px;\n}\n.me-upload__file-uploaded {\n  margin: 8px;\n}\n\n/*# sourceMappingURL=Upload.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2i = "data-v-3a0c4152";
  /* module identifier */
  const __vue_module_identifier__$2i = undefined;
  /* functional template */
  const __vue_is_functional_template__$2i = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2i, staticRenderFns: __vue_staticRenderFns__$2i },
    __vue_inject_styles__$2i,
    __vue_script__$2i,
    __vue_scope_id__$2i,
    __vue_is_functional_template__$2i,
    __vue_module_identifier__$2i,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$2j = {
  name: 'MeUnitPicker',
  components: {
    MeSelect: __vue_component__$m,
    MeNumberInput: __vue_component__$C
  },

  props: {
    autoSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputValue: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: 999999999
    },
    min: {
      type: Number,
      default: -999999999
    },
    minus: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    precision: {
      type: Number,
      default: 2
    },
    size: {
      type: String,
      default: 'md'
    },
    selectLabel: {
      type: String,
      default: 'name'
    },
    selectedValue: {
      type: [Array, Object, Number, String],
      default: null
    },
    state: {
      type: Boolean,
      default: null
    },
    useLocale: {
      type: [Boolean, String],
      default: false
    },
    appendPickerToBody: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      current: this.inputValue,
      selected: cloneDeep_1(this.selectedValue),
      focused: false
    }
  },

  watch: {
    inputValue(value) {
      this.current = value;
    },

    selectedValue(value) {
      this.selected = cloneDeep_1(value);
    }
  },

  methods: {
    onChange() {
      this.$emit(
        'input',
        { value: this.current, selectValue: this.selected }
      );
    },

    onFocus() {
      this.focused = true;
      this.$emit(
        'focus',
        { value: this.current, selectValue: this.selected }
      );
    },

    onBlur() {
      this.focused = false;
      this.$emit(
        'blur',
        { value: this.current, selectValue: this.selected }
      );
    }
  }
};

/* script */
const __vue_script__$2j = script$2j;

/* template */
var __vue_render__$2j = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "div",
    { staticClass: "me-unit-picker", attrs: { "data-cy": "me-unit-picker" } },
    [
      _c(
        "div",
        {
          staticClass: "input-wrapper",
          class: {
            "with-value": _vm.current,
            focused: _vm.focused,
            "no-valid": _vm.state,
            "is-invalid": _vm.state === false,
            disabled: _vm.disabled
          }
        },
        [
          _c("MeNumberInput", {
            attrs: {
              size: _vm.size,
              placeholder: _vm.placeholder,
              disabled: _vm.disabled,
              state: _vm.state,
              minus: _vm.minus,
              min: _vm.min,
              max: _vm.max,
              precision: _vm.precision,
              useLocale: _vm.useLocale
            },
            on: { input: _vm.onChange, focus: _vm.onFocus, blur: _vm.onBlur },
            model: {
              value: _vm.current,
              callback: function($$v) {
                _vm.current = $$v;
              },
              expression: "current"
            }
          }),
          _vm._v(" "),
          _c("MeSelect", {
            attrs: {
              disabled: _vm.disabled,
              size: _vm.size,
              options: _vm.options,
              clearable: false,
              autoSelect: _vm.autoSelect,
              appendToBody: _vm.appendPickerToBody,
              label: _vm.selectLabel
            },
            on: { input: _vm.onChange },
            model: {
              value: _vm.selected,
              callback: function($$v) {
                _vm.selected = $$v;
              },
              expression: "selected"
            }
          })
        ],
        1
      )
    ]
  )
};
var __vue_staticRenderFns__$2j = [];
__vue_render__$2j._withStripped = true;

  /* style */
  const __vue_inject_styles__$2j = function (inject) {
    if (!inject) return
    inject("data-v-539ab254_0", { source: ".me-unit-picker[data-v-539ab254] {\n  width: 100%;\n}\n.me-unit-picker .input-wrapper[data-v-539ab254] {\n  display: flex;\n  flex-wrap: nowrap;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\n.me-unit-picker .input-wrapper.disabled[data-v-539ab254] {\n  border-color: var(--me-gray-4);\n  background-color: var(--me-gray-6);\n}\n.me-unit-picker .input-wrapper.is-invalid[data-v-539ab254] {\n  border-color: var(--me-danger-2);\n}\n.me-unit-picker .input-wrapper.is-invalid[data-v-539ab254]  .me-number-input .form-control:focus {\n  caret-color: auto;\n}\n.me-unit-picker .input-wrapper.no-valid[data-v-539ab254]  .me-number-input > .input-icons {\n  display: none;\n}\n.me-unit-picker .input-wrapper.focused[data-v-539ab254]:not(.is-invalid) {\n  border: 1px solid var(--me-primary-2);\n}\n.me-unit-picker .input-wrapper.with-value[data-v-539ab254]  .me-select .vs__dropdown-toggle .selected-option, .me-unit-picker .input-wrapper.focused[data-v-539ab254]  .me-select .vs__dropdown-toggle .selected-option {\n  color: var(--me-gray-2);\n}\n.me-unit-picker[data-v-539ab254]  .me-number-input {\n  width: 100%;\n}\n.me-unit-picker[data-v-539ab254]  .me-number-input .form-control {\n  border: none;\n  border-top-right-radius: 0;\n  border-bottom-right-radius: 0;\n  color: var(--me-gray-2);\n}\n.me-unit-picker[data-v-539ab254]  .me-number-input .form-control.is-invalid {\n  border-color: var(--me-gray-6);\n}\n.me-unit-picker[data-v-539ab254]  .me-number-input .form-control.is-valid {\n  padding-right: 8px;\n}\n.me-unit-picker[data-v-539ab254]  .me-number-input .form-control:focus {\n  caret-color: var(--me-primary-5);\n}\n.me-unit-picker[data-v-539ab254]  .me-number-input .form-control:disabled {\n  border: none;\n  color: var(--me-gray-4);\n}\n.me-unit-picker[data-v-539ab254]  .me-select .vs__dropdown-toggle {\n  border: none;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n  background-color: var(--me-bg-gray);\n}\n.me-unit-picker[data-v-539ab254]  .me-select .vs__dropdown-toggle .selected-option {\n  text-align: center;\n  color: var(--me-gray-3);\n}\n.me-unit-picker[data-v-539ab254]  .me-select .vs--open .vs__dropdown-toggle {\n  border: none;\n}\n.me-unit-picker[data-v-539ab254]  .me-select .vs--disabled .selected-option {\n  color: var(--me-gray-4);\n}\n\n/*# sourceMappingURL=UnitPicker.vue.map */", map: {"version":3,"sources":["/app/build/src/components/UnitPicker.vue","UnitPicker.vue"],"names":[],"mappings":"AAuKA;EACA,WAAA;ACtKA;ADwKA;EACA,aAAA;EACA,iBAAA;EACA,kCAAA;EACA,kBAAA;ACtKA;ADwKA;EACA,8BAAA;EACA,kCAAA;ACtKA;ADyKA;EACA,gCAAA;ACvKA;ADyKA;EACA,iBAAA;ACvKA;AD4KA;EACA,aAAA;AC1KA;AD8KA;EACA,qCAAA;AC5KA;ADiLA;EACA,uBAAA;AC/KA;ADsLA;EACA,WAAA;ACpLA;ADsLA;EACA,YAAA;EACA,0BAAA;EACA,6BAAA;EACA,uBAAA;ACpLA;ADsLA;EACA,8BAAA;ACpLA;ADuLA;EACA,kBAAA;ACrLA;ADwLA;EACA,gCAAA;ACtLA;ADyLA;EACA,YAAA;EACA,uBAAA;ACvLA;AD6LA;EACA,YAAA;EACA,yBAAA;EACA,4BAAA;EACA,mCAAA;AC3LA;AD6LA;EACA,kBAAA;EACA,uBAAA;AC3LA;ADgMA;EACA,YAAA;AC9LA;ADkMA;EACA,uBAAA;AChMA;;AAEA,yCAAyC","file":"UnitPicker.vue","sourcesContent":["<template>\n  <div\n    class=\"me-unit-picker\"\n    data-cy=\"me-unit-picker\"\n  >\n    <div\n      :class=\"{\n        'with-value': current,\n        'focused': focused,\n        'no-valid': state,\n        'is-invalid': state === false,\n        'disabled': disabled\n      }\"\n      class=\"input-wrapper\"\n    >\n      <MeNumberInput\n        v-model=\"current\"\n        :size=\"size\"\n        :placeholder=\"placeholder\"\n        :disabled=\"disabled\"\n        :state=\"state\"\n        :minus=\"minus\"\n        :min=\"min\"\n        :max=\"max\"\n        :precision=\"precision\"\n        :useLocale=\"useLocale\"\n        @input=\"onChange\"\n        @focus=\"onFocus\"\n        @blur=\"onBlur\"\n      />\n\n      <MeSelect\n        v-model=\"selected\"\n        :disabled=\"disabled\"\n        :size=\"size\"\n        :options=\"options\"\n        :clearable=\"false\"\n        :autoSelect=\"autoSelect\"\n        :appendToBody=\"appendPickerToBody\"\n        :label=\"selectLabel\"\n        @input=\"onChange\"\n      />\n    </div>\n  </div>\n</template>\n\n<script>\nimport cloneDeep from 'lodash/cloneDeep'\nimport MeSelect from './Select.vue'\nimport MeNumberInput from './NumberInput.vue'\n\nexport default {\n  name: 'MeUnitPicker',\n  components: {\n    MeSelect,\n    MeNumberInput\n  },\n\n  props: {\n    autoSelect: {\n      type: Boolean,\n      default: false\n    },\n    disabled: {\n      type: Boolean,\n      default: false\n    },\n    inputValue: {\n      type: Number,\n      default: null\n    },\n    max: {\n      type: Number,\n      default: 999999999\n    },\n    min: {\n      type: Number,\n      default: -999999999\n    },\n    minus: {\n      type: Boolean,\n      default: false\n    },\n    options: {\n      type: Array,\n      required: true\n    },\n    placeholder: {\n      type: String,\n      default: ''\n    },\n    precision: {\n      type: Number,\n      default: 2\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    selectLabel: {\n      type: String,\n      default: 'name'\n    },\n    selectedValue: {\n      type: [Array, Object, Number, String],\n      default: null\n    },\n    state: {\n      type: Boolean,\n      default: null\n    },\n    useLocale: {\n      type: [Boolean, String],\n      default: false\n    },\n    appendPickerToBody: {\n      type: Boolean,\n      default: false\n    }\n  },\n\n  data() {\n    return {\n      current: this.inputValue,\n      selected: cloneDeep(this.selectedValue),\n      focused: false\n    }\n  },\n\n  watch: {\n    inputValue(value) {\n      this.current = value\n    },\n\n    selectedValue(value) {\n      this.selected = cloneDeep(value)\n    }\n  },\n\n  methods: {\n    onChange() {\n      this.$emit(\n        'input',\n        { value: this.current, selectValue: this.selected }\n      )\n    },\n\n    onFocus() {\n      this.focused = true\n      this.$emit(\n        'focus',\n        { value: this.current, selectValue: this.selected }\n      )\n    },\n\n    onBlur() {\n      this.focused = false\n      this.$emit(\n        'blur',\n        { value: this.current, selectValue: this.selected }\n      )\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n.me-unit-picker {\n  width: 100%;\n\n  .input-wrapper {\n    display: flex;\n    flex-wrap: nowrap;\n    border: 1px solid var(--me-gray-6);\n    border-radius: 8px;\n\n    &.disabled {\n      border-color: var(--me-gray-4);\n      background-color: var(--me-gray-6);\n    }\n\n    &.is-invalid {\n      border-color: var(--me-danger-2);\n\n      ::v-deep .me-number-input .form-control:focus {\n        caret-color: auto;\n      }\n    }\n\n    &.no-valid {\n      ::v-deep .me-number-input > .input-icons {\n        display: none;\n      }\n    }\n\n    &.focused:not(.is-invalid) {\n      border: 1px solid var(--me-primary-2);\n    }\n\n    &.with-value, &.focused {\n      ::v-deep {\n        .me-select .vs__dropdown-toggle .selected-option {\n          color: var(--me-gray-2);\n        }\n      }\n    }\n  }\n\n  ::v-deep {\n    .me-number-input {\n      width: 100%;\n\n      .form-control {\n        border: none;\n        border-top-right-radius: 0;\n        border-bottom-right-radius: 0;\n        color: var(--me-gray-2);\n\n        &.is-invalid {\n          border-color: var(--me-gray-6);\n        }\n\n        &.is-valid {\n          padding-right: 8px;\n        }\n\n        &:focus {\n          caret-color: var(--me-primary-5);\n        }\n\n        &:disabled {\n          border: none;\n          color: var(--me-gray-4);\n        }\n      }\n    }\n\n    .me-select {\n      .vs__dropdown-toggle {\n        border: none;\n        border-top-left-radius: 0;\n        border-bottom-left-radius: 0;\n        background-color: var(--me-bg-gray);\n\n        .selected-option {\n          text-align: center;\n          color: var(--me-gray-3);\n        }\n      }\n\n      .vs--open {\n        .vs__dropdown-toggle {\n          border: none;\n        }\n      }\n\n      .vs--disabled .selected-option{\n        color: var(--me-gray-4);\n      }\n    }\n  }\n}\n</style>\n",".me-unit-picker {\n  width: 100%;\n}\n.me-unit-picker .input-wrapper {\n  display: flex;\n  flex-wrap: nowrap;\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n}\n.me-unit-picker .input-wrapper.disabled {\n  border-color: var(--me-gray-4);\n  background-color: var(--me-gray-6);\n}\n.me-unit-picker .input-wrapper.is-invalid {\n  border-color: var(--me-danger-2);\n}\n.me-unit-picker .input-wrapper.is-invalid ::v-deep .me-number-input .form-control:focus {\n  caret-color: auto;\n}\n.me-unit-picker .input-wrapper.no-valid ::v-deep .me-number-input > .input-icons {\n  display: none;\n}\n.me-unit-picker .input-wrapper.focused:not(.is-invalid) {\n  border: 1px solid var(--me-primary-2);\n}\n.me-unit-picker .input-wrapper.with-value ::v-deep .me-select .vs__dropdown-toggle .selected-option, .me-unit-picker .input-wrapper.focused ::v-deep .me-select .vs__dropdown-toggle .selected-option {\n  color: var(--me-gray-2);\n}\n.me-unit-picker ::v-deep .me-number-input {\n  width: 100%;\n}\n.me-unit-picker ::v-deep .me-number-input .form-control {\n  border: none;\n  border-top-right-radius: 0;\n  border-bottom-right-radius: 0;\n  color: var(--me-gray-2);\n}\n.me-unit-picker ::v-deep .me-number-input .form-control.is-invalid {\n  border-color: var(--me-gray-6);\n}\n.me-unit-picker ::v-deep .me-number-input .form-control.is-valid {\n  padding-right: 8px;\n}\n.me-unit-picker ::v-deep .me-number-input .form-control:focus {\n  caret-color: var(--me-primary-5);\n}\n.me-unit-picker ::v-deep .me-number-input .form-control:disabled {\n  border: none;\n  color: var(--me-gray-4);\n}\n.me-unit-picker ::v-deep .me-select .vs__dropdown-toggle {\n  border: none;\n  border-top-left-radius: 0;\n  border-bottom-left-radius: 0;\n  background-color: var(--me-bg-gray);\n}\n.me-unit-picker ::v-deep .me-select .vs__dropdown-toggle .selected-option {\n  text-align: center;\n  color: var(--me-gray-3);\n}\n.me-unit-picker ::v-deep .me-select .vs--open .vs__dropdown-toggle {\n  border: none;\n}\n.me-unit-picker ::v-deep .me-select .vs--disabled .selected-option {\n  color: var(--me-gray-4);\n}\n\n/*# sourceMappingURL=UnitPicker.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2j = "data-v-539ab254";
  /* module identifier */
  const __vue_module_identifier__$2j = undefined;
  /* functional template */
  const __vue_is_functional_template__$2j = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2j, staticRenderFns: __vue_staticRenderFns__$2j },
    __vue_inject_styles__$2j,
    __vue_script__$2j,
    __vue_scope_id__$2j,
    __vue_is_functional_template__$2j,
    __vue_module_identifier__$2j,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

const defaultTabValues = {
  title: '',
  active: false,
  invalid: false,
  disabled: false,
  onlyWizard: false
};

var script$2k = {
  name: 'MeWizardBar',

  props: {
    tabs: {
      type: Array,
      default: () => []
    },
    express: {
      type: Boolean,
      default: false
    },
    beforeTabChange: {
      type: Function,
      default: ({ _prevTab, _nextTab }) => true
    }
  },

  data() {
    return {
      currentTabs: this.prepareModel(this.tabs)
    }
  },

  watch: {
    tabs(value) {
      this.currentTabs = this.prepareModel(value);
    },

    express(value) {
      if (!value && this.getActiveTab(this.currentTabs).disabled) {
        const firstNotDisabledTab = this.currentTabs.find(tab => !tab.disabled);
        this.currentTabs = this.toggleActiveTabs(this.currentTabs, firstNotDisabledTab);
        this.$emit('changeTabs', this.currentTabs);
      }
    }
  },

  methods: {
    prepareModel() {
      return this.setOnlyOneActiveTab(this.tabs)
        .map((tab, index) => {
          const {
            key,
            title,
            active,
            invalid,
            disabled,
            onlyWizard
          } = { ...defaultTabValues, ...tab, key: tab.key || index };

          return { key, title, active, invalid, disabled, onlyWizard }
        })
    },

    getModeTabs(tabs) {
      if (this.express) {
        return tabs.filter(tab => !tab.onlyWizard)
      }

      return tabs
    },

    setOnlyOneActiveTab(tabs) {
      const activeTab = this.getActiveTab(tabs);
      return this.toggleActiveTabs(tabs, activeTab)
    },

    getActiveTab(tabs) {
      if (!tabs.length) return {}
      return tabs.find(tab => tab.active) || tabs[0]
    },

    toggleActiveTabs(tabs, tabToActive) {
      return tabs.map(tab => tab.key === tabToActive.key
        ? this.setTabActive(tab)
        : this.setTabInative(tab)
      )
    },

    setTabActive(tab) {
      return {
        ...tab,
        active: true
      }
    },

    setTabInative(tab) {
      return {
        ...tab,
        active: false
      }
    },

    handleTabClick(tabToActive) {
      const activeTab = this.getActiveTab(this.currentTabs);

      if (!this.beforeTabChange({ prevTab: activeTab, nextTab: tabToActive })) return

      this.currentTabs = this.toggleActiveTabs(this.currentTabs, tabToActive);

      this.$emit('click', tabToActive);
      this.$emit('changeTabs', this.currentTabs);
    }
  }
};

/* script */
const __vue_script__$2k = script$2k;

/* template */
var __vue_render__$2k = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "section",
    { staticClass: "me-wizard-bar" },
    [
      _c(
        "div",
        {
          staticClass: "wizard-items",
          class: { "express-mode": _vm.express },
          attrs: { "data-cy": "wizard-items" }
        },
        [
          _vm._l(_vm.getModeTabs(_vm.currentTabs), function(tab, index) {
            return [
              _c(
                "div",
                {
                  key: tab.key,
                  staticClass: "wizard-item",
                  class: {
                    "tab-disabled": tab.disabled,
                    "tab-invalid": tab.invalid
                  },
                  attrs: { "data-cy": "wizard-item" },
                  on: {
                    click: function() {
                      return _vm.handleTabClick(tab)
                    }
                  }
                },
                [
                  _c(
                    "div",
                    { staticClass: "contents" },
                    [
                      !_vm.express
                        ? _c(
                            "div",
                            {
                              staticClass: "number",
                              class: {
                                selected: tab.active && !_vm.express,
                                invalid: tab.invalid,
                                circle: !_vm.express
                              },
                              attrs: { "data-cy": "circle" }
                            },
                            [
                              _vm._v(
                                "\n            " +
                                  _vm._s(index + 1) +
                                  "\n          "
                              )
                            ]
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      _c(
                        "p",
                        { staticClass: "title-content" },
                        [
                          _vm._t(
                            tab.title,
                            [
                              _vm._t(
                                "title",
                                [
                                  _c(
                                    "span",
                                    {
                                      directives: [
                                        {
                                          name: "me-tooltip",
                                          rawName: "v-me-tooltip.ellipsis",
                                          value: tab.title,
                                          expression: "tab.title",
                                          modifiers: { ellipsis: true }
                                        }
                                      ],
                                      staticClass: "title",
                                      attrs: { "data-cy": "title" }
                                    },
                                    [
                                      _vm._v(
                                        "\n                  " +
                                          _vm._s(tab.title) +
                                          "\n                "
                                      )
                                    ]
                                  )
                                ],
                                { tab: tab }
                              )
                            ],
                            { tab: tab }
                          )
                        ],
                        2
                      ),
                      _vm._v(" "),
                      tab.invalid
                        ? _c("me-icon", {
                            staticClass: "invalid-icon",
                            attrs: { icon: "me-icon-s icon-circle-exclamation" }
                          })
                        : _vm._e()
                    ],
                    1
                  ),
                  _vm._v(" "),
                  _c("div", {
                    staticClass: "item-border",
                    class: {
                      "border-disabled": tab.disabled,
                      "border-invalid": tab.invalid
                    }
                  })
                ]
              ),
              _vm._v(" "),
              _vm.express ? _vm._t(tab.key) : _vm._e()
            ]
          })
        ],
        2
      ),
      _vm._v(" "),
      !_vm.express
        ? [
            _vm._l(_vm.getModeTabs(_vm.currentTabs), function(tab) {
              return [tab.active ? _vm._t(tab.key) : _vm._e()]
            })
          ]
        : _vm._e()
    ],
    2
  )
};
var __vue_staticRenderFns__$2k = [];
__vue_render__$2k._withStripped = true;

  /* style */
  const __vue_inject_styles__$2k = function (inject) {
    if (!inject) return
    inject("data-v-34f481bc_0", { source: ".me-wizard-bar .wizard-items[data-v-34f481bc] {\n  display: flex;\n}\n.me-wizard-bar .wizard-items .wizard-item[data-v-34f481bc] {\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  color: var(--me-primary-2);\n  margin-left: 24px;\n  margin-bottom: 24px !important;\n  cursor: pointer;\n  min-width: 0;\n}\n.me-wizard-bar .wizard-items .wizard-item .title-content[data-v-34f481bc] {\n  min-width: 0;\n}\n.me-wizard-bar .wizard-items .wizard-item .title[data-v-34f481bc] {\n  display: block;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  font-weight: 600;\n  font-size: 14px;\n}\n.me-wizard-bar .wizard-items .wizard-item .number[data-v-34f481bc] {\n  font-weight: 600;\n  font-size: 12px;\n}\n.me-wizard-bar .wizard-items .wizard-item .item-border[data-v-34f481bc] {\n  width: 100%;\n  height: 4px;\n  background-color: var(--me-primary-2);\n  border-radius: 8px;\n}\n.me-wizard-bar .wizard-items .wizard-item .item-border.border-invalid[data-v-34f481bc] {\n  background-color: var(--me-danger-2);\n}\n.me-wizard-bar .wizard-items .wizard-item .item-border.border-disabled[data-v-34f481bc] {\n  background-color: var(--me-gray-6);\n}\n.me-wizard-bar .wizard-items .wizard-item .contents[data-v-34f481bc] {\n  display: flex;\n  flex-basis: 100%;\n}\n.me-wizard-bar .wizard-items .wizard-item[data-v-34f481bc]:first-child {\n  margin-left: 0;\n}\n.me-wizard-bar .wizard-items .wizard-item .circle[data-v-34f481bc] {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  min-width: 24px;\n  height: 24px;\n  border: 1px solid var(--me-primary-2);\n  border-radius: 50%;\n  margin-right: 8px;\n}\n.me-wizard-bar .wizard-items .wizard-item .circle.selected[data-v-34f481bc] {\n  color: var(--me-white);\n  background: var(--me-primary-2);\n}\n.me-wizard-bar .wizard-items .wizard-item .circle.invalid[data-v-34f481bc] {\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n  background: var(--me-white);\n}\n.me-wizard-bar .wizard-items .wizard-item .circle.selected.invalid[data-v-34f481bc] {\n  color: var(--me-white);\n  border-color: var(--me-danger-2);\n  background: var(--me-danger-2);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-disabled[data-v-34f481bc] {\n  border-color: var(--me-gray-6);\n  color: var(--me-gray-5);\n  pointer-events: none;\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-disabled .circle[data-v-34f481bc] {\n  color: var(--me-gray-5);\n  border-color: var(--me-gray-5);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-disabled .title[data-v-34f481bc] {\n  color: var(--me-gray-5);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-invalid[data-v-34f481bc] {\n  display: flex;\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-invalid .invalid-icon[data-v-34f481bc] {\n  margin-left: auto;\n}\n.me-wizard-bar .wizard-items.express-mode[data-v-34f481bc] {\n  display: flex;\n  flex-direction: column;\n}\n.me-wizard-bar .wizard-items.express-mode .wizard-item[data-v-34f481bc] {\n  pointer-events: none;\n  margin-left: 0px !important;\n  margin-top: 24px;\n}\n.me-wizard-bar .wizard-items.express-mode .wizard-item[data-v-34f481bc]:first-child {\n  margin-top: 0px;\n}\n\n/*# sourceMappingURL=WizardBar.vue.map */", map: {"version":3,"sources":["/app/build/src/components/wizardBar/WizardBar.vue","WizardBar.vue"],"names":[],"mappings":"AA0LA;EACA,aAAA;ACzLA;AD2LA;EACA,aAAA;EACA,sBAAA;EACA,YAAA;EACA,0BAAA;EACA,iBAAA;EACA,8BAAA;EACA,eAAA;EACA,YAAA;ACzLA;AD2LA;EACA,YAAA;ACzLA;AD4LA;EACA,cAAA;EACA,mBAAA;EACA,gBAAA;EACA,uBAAA;EACA,gBAAA;EACA,eAAA;AC1LA;AD6LA;EACA,gBAAA;EACA,eAAA;AC3LA;AD8LA;EACA,WAAA;EACA,WAAA;EACA,qCAAA;EACA,kBAAA;AC5LA;AD8LA;EACA,oCAAA;AC5LA;AD+LA;EACA,kCAAA;AC7LA;ADiMA;EACA,aAAA;EACA,gBAAA;AC/LA;ADkMA;EACA,cAAA;AChMA;ADmMA;EACA,aAAA;EACA,uBAAA;EACA,mBAAA;EACA,eAAA;EACA,YAAA;EACA,qCAAA;EACA,kBAAA;EACA,iBAAA;ACjMA;ADmMA;EACA,sBAAA;EACA,+BAAA;ACjMA;ADoMA;EACA,yBAAA;EACA,gCAAA;EACA,2BAAA;AClMA;ADqMA;EACA,sBAAA;EACA,gCAAA;EACA,8BAAA;ACnMA;ADuMA;EACA,8BAAA;EACA,uBAAA;EACA,oBAAA;ACrMA;ADuMA;EACA,uBAAA;EACA,8BAAA;ACrMA;ADwMA;EACA,uBAAA;ACtMA;AD0MA;EACA,aAAA;EACA,yBAAA;EACA,gCAAA;ACxMA;AD0MA;EACA,iBAAA;ACxMA;AD6MA;EACA,aAAA;EACA,sBAAA;AC3MA;AD6MA;EACA,oBAAA;EACA,2BAAA;EACA,gBAAA;AC3MA;AD6MA;EACA,eAAA;AC3MA;;AAEA,wCAAwC","file":"WizardBar.vue","sourcesContent":["<template>\n  <section class=\"me-wizard-bar\">\n    <div :class=\"{'express-mode': express}\" class=\"wizard-items\" data-cy=\"wizard-items\">\n      <template v-for=\"(tab, index) in getModeTabs(currentTabs)\">\n        <div\n          :key=\"tab.key\"\n          :class=\"{\n            'tab-disabled': tab.disabled,\n            'tab-invalid' : tab.invalid\n          }\"\n          @click=\"() => handleTabClick(tab)\"\n          class=\"wizard-item\"\n          data-cy=\"wizard-item\"\n        >\n          <div class=\"contents\">\n            <div\n              :class=\"{ selected: tab.active && !express, invalid: tab.invalid,\n                        circle: !express}\"\n              v-if=\"!express\"\n              class=\"number\"\n              data-cy=\"circle\"\n            >\n              {{ index + 1 }}\n            </div>\n\n            <p class=\"title-content\">\n              <slot :tab=\"tab\" :name=\"tab.title\">\n                <slot :tab=\"tab\" name=\"title\">\n                  <span\n                    v-me-tooltip.ellipsis=\"tab.title\"\n                    class=\"title\"\n                    data-cy=\"title\"\n                  >\n                    {{ tab.title }}\n                  </span>\n                </slot>\n              </slot>\n            </p>\n            <me-icon\n              v-if=\"tab.invalid\"\n              icon=\"me-icon-s icon-circle-exclamation\"\n              class=\"invalid-icon\"\n            />\n          </div>\n\n          <div\n            :class=\"{\n              'border-disabled': tab.disabled,\n              'border-invalid' : tab.invalid\n            }\"\n            class=\"item-border\"\n          />\n        </div>\n\n        <slot :name=\"tab.key\" v-if=\"express\" />\n      </template>\n    </div>\n\n    <template v-if=\"!express\">\n      <template v-for=\"tab in getModeTabs(currentTabs)\">\n        <slot v-if=\"tab.active\" :name=\"tab.key\" />\n      </template>\n    </template>\n  </section>\n</template>\n\n<script>\nconst defaultTabValues = {\n  title: '',\n  active: false,\n  invalid: false,\n  disabled: false,\n  onlyWizard: false\n}\n\nexport default {\n  name: 'MeWizardBar',\n\n  props: {\n    tabs: {\n      type: Array,\n      default: () => []\n    },\n    express: {\n      type: Boolean,\n      default: false\n    },\n    beforeTabChange: {\n      type: Function,\n      default: ({ _prevTab, _nextTab }) => true\n    }\n  },\n\n  data() {\n    return {\n      currentTabs: this.prepareModel(this.tabs)\n    }\n  },\n\n  watch: {\n    tabs(value) {\n      this.currentTabs = this.prepareModel(value)\n    },\n\n    express(value) {\n      if (!value && this.getActiveTab(this.currentTabs).disabled) {\n        const firstNotDisabledTab = this.currentTabs.find(tab => !tab.disabled)\n        this.currentTabs = this.toggleActiveTabs(this.currentTabs, firstNotDisabledTab)\n        this.$emit('changeTabs', this.currentTabs)\n      }\n    }\n  },\n\n  methods: {\n    prepareModel() {\n      return this.setOnlyOneActiveTab(this.tabs)\n        .map((tab, index) => {\n          const {\n            key,\n            title,\n            active,\n            invalid,\n            disabled,\n            onlyWizard\n          } = { ...defaultTabValues, ...tab, key: tab.key || index }\n\n          return { key, title, active, invalid, disabled, onlyWizard }\n        })\n    },\n\n    getModeTabs(tabs) {\n      if (this.express) {\n        return tabs.filter(tab => !tab.onlyWizard)\n      }\n\n      return tabs\n    },\n\n    setOnlyOneActiveTab(tabs) {\n      const activeTab = this.getActiveTab(tabs)\n      return this.toggleActiveTabs(tabs, activeTab)\n    },\n\n    getActiveTab(tabs) {\n      if (!tabs.length) return {}\n      return tabs.find(tab => tab.active) || tabs[0]\n    },\n\n    toggleActiveTabs(tabs, tabToActive) {\n      return tabs.map(tab => tab.key === tabToActive.key\n        ? this.setTabActive(tab)\n        : this.setTabInative(tab)\n      )\n    },\n\n    setTabActive(tab) {\n      return {\n        ...tab,\n        active: true\n      }\n    },\n\n    setTabInative(tab) {\n      return {\n        ...tab,\n        active: false\n      }\n    },\n\n    handleTabClick(tabToActive) {\n      const activeTab = this.getActiveTab(this.currentTabs)\n\n      if (!this.beforeTabChange({ prevTab: activeTab, nextTab: tabToActive })) return\n\n      this.currentTabs = this.toggleActiveTabs(this.currentTabs, tabToActive)\n\n      this.$emit('click', tabToActive)\n      this.$emit('changeTabs', this.currentTabs)\n    }\n  }\n}\n\n</script>\n\n<style lang=\"scss\" scoped>\n.me-wizard-bar {\n  .wizard-items {\n    display: flex;\n\n    .wizard-item {\n      display: flex;\n      flex-direction: column;\n      flex-grow: 1;\n      color: var(--me-primary-2);\n      margin-left: 24px;\n      margin-bottom: 24px !important;\n      cursor: pointer;\n      min-width: 0;\n\n    .title-content {\n      min-width: 0;\n    }\n\n    .title {\n      display: block;\n      white-space: nowrap;\n      overflow: hidden;\n      text-overflow: ellipsis;\n      font-weight: 600;\n      font-size: 14px;\n    }\n\n    .number {\n      font-weight: 600;\n      font-size: 12px;\n    }\n\n    .item-border {\n      width: 100%;\n      height: 4px;\n      background-color: var(--me-primary-2);\n      border-radius: 8px;\n\n      &.border-invalid {\n        background-color: var(--me-danger-2);\n      }\n\n      &.border-disabled {\n        background-color: var(--me-gray-6);\n      }\n    }\n\n    .contents{\n      display: flex;\n      flex-basis: 100%;\n    }\n\n      &:first-child {\n        margin-left: 0;\n      }\n\n      .circle {\n        display: flex;\n        justify-content: center;\n        align-items: center;\n        min-width: 24px;\n        height: 24px;\n        border: 1px solid var(--me-primary-2);\n        border-radius: 50%;\n        margin-right: 8px;\n\n        &.selected {\n          color: var(--me-white);\n          background: var(--me-primary-2);\n        }\n\n        &.invalid {\n          color: var(--me-danger-2);\n          border-color: var(--me-danger-2);\n          background: var(--me-white);\n        }\n\n        &.selected.invalid {\n          color: var(--me-white);\n          border-color: var(--me-danger-2);\n          background: var(--me-danger-2);\n        }\n      }\n\n      &.tab-disabled {\n        border-color: var(--me-gray-6);\n        color: var(--me-gray-5);\n        pointer-events: none;\n\n        .circle {\n          color: var(--me-gray-5);\n          border-color: var(--me-gray-5);\n        }\n\n        .title {\n          color: var(--me-gray-5);\n        }\n      }\n\n      &.tab-invalid {\n        display: flex;\n        color: var(--me-danger-2);\n        border-color: var(--me-danger-2);\n\n        .invalid-icon {\n          margin-left: auto;\n        }\n      }\n    }\n\n    &.express-mode {\n      display: flex;\n      flex-direction: column;\n\n      .wizard-item {\n        pointer-events: none;\n        margin-left: 0px !important;\n        margin-top: 24px;\n\n        &:first-child {\n          margin-top: 0px;\n        }\n      }\n    }\n  }\n}\n\n</style>\n",".me-wizard-bar .wizard-items {\n  display: flex;\n}\n.me-wizard-bar .wizard-items .wizard-item {\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n  color: var(--me-primary-2);\n  margin-left: 24px;\n  margin-bottom: 24px !important;\n  cursor: pointer;\n  min-width: 0;\n}\n.me-wizard-bar .wizard-items .wizard-item .title-content {\n  min-width: 0;\n}\n.me-wizard-bar .wizard-items .wizard-item .title {\n  display: block;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  font-weight: 600;\n  font-size: 14px;\n}\n.me-wizard-bar .wizard-items .wizard-item .number {\n  font-weight: 600;\n  font-size: 12px;\n}\n.me-wizard-bar .wizard-items .wizard-item .item-border {\n  width: 100%;\n  height: 4px;\n  background-color: var(--me-primary-2);\n  border-radius: 8px;\n}\n.me-wizard-bar .wizard-items .wizard-item .item-border.border-invalid {\n  background-color: var(--me-danger-2);\n}\n.me-wizard-bar .wizard-items .wizard-item .item-border.border-disabled {\n  background-color: var(--me-gray-6);\n}\n.me-wizard-bar .wizard-items .wizard-item .contents {\n  display: flex;\n  flex-basis: 100%;\n}\n.me-wizard-bar .wizard-items .wizard-item:first-child {\n  margin-left: 0;\n}\n.me-wizard-bar .wizard-items .wizard-item .circle {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  min-width: 24px;\n  height: 24px;\n  border: 1px solid var(--me-primary-2);\n  border-radius: 50%;\n  margin-right: 8px;\n}\n.me-wizard-bar .wizard-items .wizard-item .circle.selected {\n  color: var(--me-white);\n  background: var(--me-primary-2);\n}\n.me-wizard-bar .wizard-items .wizard-item .circle.invalid {\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n  background: var(--me-white);\n}\n.me-wizard-bar .wizard-items .wizard-item .circle.selected.invalid {\n  color: var(--me-white);\n  border-color: var(--me-danger-2);\n  background: var(--me-danger-2);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-disabled {\n  border-color: var(--me-gray-6);\n  color: var(--me-gray-5);\n  pointer-events: none;\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-disabled .circle {\n  color: var(--me-gray-5);\n  border-color: var(--me-gray-5);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-disabled .title {\n  color: var(--me-gray-5);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-invalid {\n  display: flex;\n  color: var(--me-danger-2);\n  border-color: var(--me-danger-2);\n}\n.me-wizard-bar .wizard-items .wizard-item.tab-invalid .invalid-icon {\n  margin-left: auto;\n}\n.me-wizard-bar .wizard-items.express-mode {\n  display: flex;\n  flex-direction: column;\n}\n.me-wizard-bar .wizard-items.express-mode .wizard-item {\n  pointer-events: none;\n  margin-left: 0px !important;\n  margin-top: 24px;\n}\n.me-wizard-bar .wizard-items.express-mode .wizard-item:first-child {\n  margin-top: 0px;\n}\n\n/*# sourceMappingURL=WizardBar.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2k = "data-v-34f481bc";
  /* module identifier */
  const __vue_module_identifier__$2k = undefined;
  /* functional template */
  const __vue_is_functional_template__$2k = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2k, staticRenderFns: __vue_staticRenderFns__$2k },
    __vue_inject_styles__$2k,
    __vue_script__$2k,
    __vue_scope_id__$2k,
    __vue_is_functional_template__$2k,
    __vue_module_identifier__$2k,
    false,
    createInjector,
    undefined,
    undefined
  );

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  AccordionList: __vue_component__$3,
  Alert: __vue_component__$4,
  Analytics: __vue_component__$t,
  Anchor: __vue_component__$u,
  Attachment: __vue_component__$v,
  Avatar: __vue_component__$w,
  Badge: __vue_component__$x,
  BoxCard: __vue_component__$y,
  BoxCardGrid: __vue_component__$z,
  Breadcrumb: __vue_component__$a,
  BreadcrumbItem: __vue_component__$9,
  Button: __vue_component__$6,
  ButtonBar: __vue_component__$G,
  Card: __vue_component__$H,
  Carousel: __vue_component__$I,
  Chart: __vue_component__$g,
  Checkbox: __vue_component__$l,
  Collapse: __vue_component__$n,
  Criteria: __vue_component__$O,
  CriteriaBar: __vue_component__$S,
  CriteriaInput: __vue_component__$T,
  Dashboard: __vue_component__$1c,
  DatePicker: __vue_component__$B,
  DatePickerMultiple: __vue_component__$M,
  DocHeader: __vue_component__$1d,
  Download: __vue_component__$1f,
  DropDown: __vue_component__$8,
  DropdownGroup: __vue_component__$7,
  DropdownItem: __vue_component__$2,
  DropdownDateForm: __vue_component__$1j,
  DropdownRadioForm: __vue_component__$1k,
  DualList: __vue_component__$1l,
  EllipseProgress: __vue_component__$1m,
  Fab: __vue_component__$1n,
  FileInput: __vue_component__$1o,
  Filter: __vue_component__$1p,
  FilterActions: __vue_component__$P,
  FloatPopover: __vue_component__$1q,
  Footer: __vue_component__$1t,
  Forehead: __vue_component__$1u,
  Form: __vue_component__$h,
  FormGroup: __vue_component__$i,
  FormRow: __vue_component__$j,
  Grid: __vue_component__$1z,
  GridModeOptions: __vue_component__$1A,
  GridsConfig: __vue_component__$1E,
  Header: __vue_component__$1F,
  History: __vue_component__$1J,
  Icon: __vue_component__,
  Input: __vue_component__$E,
  InputCounter: __vue_component__$1K,
  InputTag: __vue_component__$1L,
  Layout: __vue_component__$1M,
  ListGroup: __vue_component__$1r,
  MenuCollapse: __vue_component__$1N,
  Modal: __vue_component__$f,
  NestedMenu: __vue_component__$1P,
  NumberInput: __vue_component__$C,
  Overlay: __vue_component__$1v,
  Pagination: __vue_component__$1R,
  Popover: __vue_component__$1,
  Preview: __vue_component__$1S,
  QuickSearch: __vue_component__$1T,
  RadioInput: __vue_component__$1Q,
  RangeSlider: __vue_component__$1U,
  Scroll: __vue_component__$1V,
  Search: __vue_component__$1W,
  SectionTree: __vue_component__$1X,
  Select: __vue_component__$m,
  Sidebar: __vue_component__$1Y,
  SidebarMenu: __vue_component__$1Z,
  Sitemap: __vue_component__$1s,
  SkeletonAddressCard: __vue_component__$1_,
  SkeletonButtonBar: __vue_component__$1$,
  SkeletonDocument: __vue_component__$23,
  SkeletonForehead: __vue_component__$21,
  SkeletonGrid: __vue_component__$1x,
  SkeletonGridsConfig: __vue_component__$1D,
  SkeletonLoadCard: __vue_component__$24,
  SkeletonLoadHistory: __vue_component__$25,
  SkeletonSupplierCard: __vue_component__$22,
  SkeletonTable: __vue_component__$20,
  SkeletonText: __vue_component__$26,
  SortInput: __vue_component__$27,
  Spinner: __vue_component__$5,
  Steps: __vue_component__$28,
  SubheaderIndex: __vue_component__$2a,
  SubheaderForm: __vue_component__$2c,
  Tab: __vue_component__$2d,
  Tabs: __vue_component__$2e,
  TextArea: __vue_component__$D,
  TextInput: __vue_component__$p,
  TextInputMultiple: __vue_component__$L,
  Toast: __vue_component__$2f,
  TreeMenu: __vue_component__$2g,
  TreeMenuProvider: __vue_component__$2h,
  UnitPicker: __vue_component__$2j,
  Upload: __vue_component__$2i,
  WidgetCardIndicator: __vue_component__$X,
  WidgetCardLink: __vue_component__$_,
  WidgetCardButton: __vue_component__$Z,
  WidgetListButton: __vue_component__$15,
  WidgetListCard: __vue_component__$11,
  WidgetListLink: __vue_component__$14,
  WizardBar: __vue_component__$2k
});

var date = {
  install(Vue, _options) {
    Vue.filter('me-date', (...args) => {
      return new Date(args.shift()).toLocaleDateString(Vue.prototype.$toolkit.locale)
    });
  }
};

var datetime = {
  install(Vue, _options) {
    Vue.filter('me-datetime', (...args) => {
      return new Date(args.shift()).toLocaleString(Vue.prototype.$toolkit.locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    });
  }
};

var datetimeseconds = {
  install(Vue, _options) {
    Vue.filter('me-datetime-seconds', (...args) => {
      return new Date(args.shift()).toLocaleString(Vue.prototype.$toolkit.locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    });
  }
};

var time = {
  install(Vue, _options) {
    Vue.filter('me-time', (...args) => {
      const value = args.shift();
      return new Date(value).toLocaleTimeString(Vue.prototype.$toolkit.locale)
    });
  }
};

var number = {
  install(Vue, _options) {
    Vue.filter('me-number', (value, digits = 2) => {
      let formattedValue;

      if (Number.isInteger(value)) {
        formattedValue = value.toFixed(digits);
      } else {
        formattedValue = value;
      }

      return Number(formattedValue).toLocaleString(Vue.prototype.$toolkit.locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      })
    });
  }
};

var cnpj = {
  install(Vue, _options) {
    Vue.filter('me-cnpjmask', (...args) => {
      const value = args.shift();
      return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    });
  }
};

var cpf = {
  install(Vue, _options) {
    Vue.filter('me-cpfmask', (...args) => {
      const value = args.shift();
      return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    });
  }
};

var rut = {
  install(Vue, _options) {
    Vue.filter('me-rutmask', (...args) => {
      const value = args.shift();
      return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
    });
  }
};

var cutText = {
  install(Vue, _options) {
    Vue.filter('me-cut-text', (...args) => {
      const textToCut = args.shift();
      const characterLimitNumber = args.shift();
      if (textToCut.length > characterLimitNumber) {
        return `${textToCut.substr(0, characterLimitNumber)}...`
      }

      return textToCut
    });
  }
};

var highlightText = {
  install(Vue, _options) {
    Vue.filter('highlightText', (originalText, term) => {
      if (!term) {
        return originalText
      }
      return originalText.replace(new RegExp(term, 'gi'), (match) => {
        return '<b>' + match + '</b>'
      })
    });
  }
};

var filters = /*#__PURE__*/Object.freeze({
  __proto__: null,
  date: date,
  datetime: datetime,
  datetimeseconds: datetimeseconds,
  time: time,
  number: number,
  cnpj: cnpj,
  cpf: cpf,
  rut: rut,
  cutText: cutText,
  highlightText: highlightText
});

var tooltip = {
  install(Vue, _options) {
    const addShowDelayModifier = (binding = {}) => {
      const hasDelayProp = binding.value?.delay;
      const hasDelayModifier = Object.keys(binding.modifiers || {}).some(key => key.startsWith('ds'));
      const hasClickModifier = Object.keys(binding.modifiers || {}).some(key => key === 'click');

      if (hasDelayProp || hasDelayModifier || hasClickModifier) return binding.modifiers

      return {
        ds500: true,
        ...(binding.modifiers || {})
      }
    };

    const hasTooltip = (el, options) => {
      const hasTooltipOnTruncate = ((options.responsive || options.ellipsis) && el.offsetWidth < el.scrollWidth);
      const alwaysShowTooltip = !options.ellipsis && !options.responsive;

      return hasTooltipOnTruncate || alwaysShowTooltip
    };

    const onUpdate = (el, binding, vnode) => {
      const { modifiers: { ellipsis, responsive } } = binding;

      if (hasTooltip(el, { ellipsis, responsive })) {
        VBTooltip.componentUpdated(el, binding, vnode);
      } else {
        VBTooltip.unbind(el);
      }
    };

    Vue.directive('me-tooltip', {
      bind(el, binding, vnode) {
        const bindingUpdated = { ...binding, modifiers: addShowDelayModifier(binding) };
        const { modifiers: { ellipsis, responsive } } = bindingUpdated;

        Vue.nextTick(() => {
          if (hasTooltip(el, { ellipsis, responsive })) {
            VBTooltip.bind(el, bindingUpdated, vnode);
          }
        });

        if (bindingUpdated.modifiers.responsive) {
          new ResizeObserver(debounce_1(() => { onUpdate(el, bindingUpdated, vnode); }, 300)).observe(el);
        }
      },

      componentUpdated(el, binding, vnode) {
        Vue.nextTick(() => {
          const bindingUpdated = { ...binding, modifiers: addShowDelayModifier(binding) };
          onUpdate(el, bindingUpdated, vnode);
        });
      },

      unbind(el) {
        VBTooltip.unbind(el);
      }
    });
  }
};

var modal = {
  install(Vue, _options) {
    Vue.directive('me-modal', VBModal);
  }
};

var toast = {
  install(Vue, options) {
    Vue.directive('me-toast', {
      bind(el, binding, vnode) {
        el.addEventListener('click', ({ currentTarget }) => {
          const target = binding.value || keys_1(binding.modifiers).reverse()[0];
          vnode.context.$root.$emit('bv::show::toast', target, currentTarget);
        });
      }
    });
  }
};

var toggle = {
  install(Vue, _options) {
    Vue.directive('me-toggle', VBToggle);
  }
};

var vueTheMask = createCommonjsModule(function (module, exports) {
(function(e,t){module.exports=t();})(commonjsGlobal,function(){return function(e){function t(r){if(n[r])return n[r].exports;var a=n[r]={i:r,l:!1,exports:{}};return e[r].call(a.exports,a,a.exports,t),a.l=!0,a.exports}var n={};return t.m=e,t.c=n,t.i=function(e){return e},t.d=function(e,n,r){t.o(e,n)||Object.defineProperty(e,n,{configurable:!1,enumerable:!0,get:r});},t.n=function(e){var n=e&&e.__esModule?function(){return e.default}:function(){return e};return t.d(n,"a",n),n},t.o=function(e,t){return Object.prototype.hasOwnProperty.call(e,t)},t.p=".",t(t.s=10)}([function(e,t){e.exports={"#":{pattern:/\d/},X:{pattern:/[0-9a-zA-Z]/},S:{pattern:/[a-zA-Z]/},A:{pattern:/[a-zA-Z]/,transform:function(e){return e.toLocaleUpperCase()}},a:{pattern:/[a-zA-Z]/,transform:function(e){return e.toLocaleLowerCase()}},"!":{escape:!0}};},function(e,t,n){function r(e){var t=document.createEvent("Event");return t.initEvent(e,!0,!0),t}var a=n(2),o=n(0),i=n.n(o);t.a=function(e,t){var o=t.value;if((Array.isArray(o)||"string"==typeof o)&&(o={mask:o,tokens:i.a}),"INPUT"!==e.tagName.toLocaleUpperCase()){var u=e.getElementsByTagName("input");if(1!==u.length)throw new Error("v-mask directive requires 1 input, found "+u.length);e=u[0];}e.oninput=function(t){if(t.isTrusted){var i=e.selectionEnd,u=e.value[i-1];for(e.value=n.i(a.a)(e.value,o.mask,!0,o.tokens);i<e.value.length&&e.value.charAt(i-1)!==u;)i++;e===document.activeElement&&(e.setSelectionRange(i,i),setTimeout(function(){e.setSelectionRange(i,i);},0)),e.dispatchEvent(r("input"));}};var s=n.i(a.a)(e.value,o.mask,!0,o.tokens);s!==e.value&&(e.value=s,e.dispatchEvent(r("input")));};},function(e,t,n){var r=n(6),a=n(5);t.a=function(e,t){var o=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],i=arguments[3];return Array.isArray(t)?n.i(a.a)(r.a,t,i)(e,t,o,i):n.i(r.a)(e,t,o,i)};},function(e,t,n){function r(e){e.component(s.a.name,s.a),e.directive("mask",i.a);}Object.defineProperty(t,"__esModule",{value:!0});var a=n(0),o=n.n(a),i=n(1),u=n(7),s=n.n(u);n.d(t,"TheMask",function(){return s.a}),n.d(t,"mask",function(){return i.a}),n.d(t,"tokens",function(){return o.a}),n.d(t,"version",function(){return c});var c="0.11.1";t.default=r,"undefined"!=typeof window&&window.Vue&&window.Vue.use(r);},function(e,t,n){Object.defineProperty(t,"__esModule",{value:!0});var r=n(1),a=n(0),o=n.n(a),i=n(2);t.default={name:"TheMask",props:{value:[String,Number],mask:{type:[String,Array],required:!0},masked:{type:Boolean,default:!1},tokens:{type:Object,default:function(){return o.a}}},directives:{mask:r.a},data:function(){return {lastValue:null,display:this.value}},watch:{value:function(e){e!==this.lastValue&&(this.display=e);},masked:function(){this.refresh(this.display);}},computed:{config:function(){return {mask:this.mask,tokens:this.tokens,masked:this.masked}}},methods:{onInput:function(e){e.isTrusted||this.refresh(e.target.value);},refresh:function(e){this.display=e;var e=n.i(i.a)(e,this.mask,this.masked,this.tokens);e!==this.lastValue&&(this.lastValue=e,this.$emit("input",e));}}};},function(e,t,n){function r(e,t,n){return t=t.sort(function(e,t){return e.length-t.length}),function(r,a){for(var o=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],i=0;i<t.length;){var u=t[i];i++;var s=t[i];if(!(s&&e(r,s,!0,n).length>u.length))return e(r,u,o,n)}return ""}}t.a=r;},function(e,t,n){function r(e,t){var n=!(arguments.length>2&&void 0!==arguments[2])||arguments[2],r=arguments[3];e=e||"",t=t||"";for(var a=0,o=0,i="";a<t.length&&o<e.length;){var u=t[a],s=r[u],c=e[o];s&&!s.escape?(s.pattern.test(c)&&(i+=s.transform?s.transform(c):c,a++),o++):(s&&s.escape&&(a++,u=t[a]),n&&(i+=u),c===u&&o++,a++);}for(var f="";a<t.length&&n;){var u=t[a];if(r[u]){f="";break}f+=u,a++;}return i+f}t.a=r;},function(e,t,n){var r=n(8)(n(4),n(9),null,null);e.exports=r.exports;},function(e,t){e.exports=function(e,t,n,r){var a,o=e=e||{},i=typeof e.default;"object"!==i&&"function"!==i||(a=e,o=e.default);var u="function"==typeof o?o.options:o;if(t&&(u.render=t.render,u.staticRenderFns=t.staticRenderFns),n&&(u._scopeId=n),r){var s=u.computed||(u.computed={});Object.keys(r).forEach(function(e){var t=r[e];s[e]=function(){return t};});}return {esModule:a,exports:o,options:u}};},function(e,t){e.exports={render:function(){var e=this,t=e.$createElement;return (e._self._c||t)("input",{directives:[{name:"mask",rawName:"v-mask",value:e.config,expression:"config"}],attrs:{type:"text"},domProps:{value:e.display},on:{input:e.onInput}})},staticRenderFns:[]};},function(e,t,n){e.exports=n(3);}])});
});

unwrapExports(vueTheMask);
var vueTheMask_1 = vueTheMask.mask;
var vueTheMask_2 = vueTheMask.VueTheMask;

var mask = {
  install(Vue, _options) {
    Vue.directive('me-mask', vueTheMask_1);
  }
};

var resize = {
  install(Vue) {
    const MIN_WIDTH_ALLOWED_TO_RESIZE = 800;
    const MIN_TH_WIDTH = 92;
    const DBL_CLICK_INTERVAL = 200;
    const EXTRA_PX_TO_AVOID_TRUNCATE = 5;
    const EXTRA_PX_MAX_COL = 30;
    let isMoving = false;
    const tableInitialStylesMap = new Map();

    const DATA_ATTRIBUTES = {
      meTableResize: {
        get: 'data-me-table-resize',
        set: 'meTableResize'
      },
      resize: {
        get: 'data-resize',
        set: 'resize',
        defaultValue: 'resize'
      },
      resizeCol: {
        get: 'data-resize-col',
        set: 'resizeCol'
      },
      resizeHeader: {
        get: 'data-resize-header',
        set: 'resizeHeader',
        defaultValue: 'resizeHeader'
      }
    };

    function destroyResize(el) {
      const table = getTableEl(el);
      const thead = table.querySelector('thead');

      const tableResizeAttrValue = table.getAttribute(DATA_ATTRIBUTES.meTableResize.get);
      if (!tableResizeAttrValue) return

      applyCss(el, getTableInitialStyle(tableResizeAttrValue));
      tableInitialStylesMap.delete(tableResizeAttrValue);

      const ths = thead.querySelectorAll('th');
      destroyGrips(ths);

      if (el.resizeObs?.disconnect && typeof el.resizeObs.disconnect === 'function') {
        el.resizeObs.disconnect();
        el.resizeObs = null;
      }
    }

    function generateTableIdentifier() {
      const allTablesWithResize = document.querySelectorAll(`[${DATA_ATTRIBUTES.meTableResize.get}]`);

      let allTableResizeAttrValue = [0];
      allTablesWithResize.forEach((tablesWithResize) => {
        const attrValue = Math.max(1 + Number(tablesWithResize.getAttribute(DATA_ATTRIBUTES.meTableResize.get)), 0);
        allTableResizeAttrValue = [...allTableResizeAttrValue, attrValue];
      });

      return String(Math.max(...allTableResizeAttrValue))
    }

    function saveTableStyle(key, style) {
      tableInitialStylesMap.set(key, style);
    }

    function getTableInitialStyle(key) {
      return tableInitialStylesMap.get(key) || {}
    }

    function getTableHeight(el) {
      return `${el.getBoundingClientRect().height.toFixed(2)}px`
    }

    function setDataAttribute({ el, attr, attrValue }) {
      el.dataset[attr] = attrValue;
    }

    function getTableEl(el) {
      return el.tagName === 'TABLE' ? el : el.querySelector('table')
    }

    function getGripElem(th) {
      return th.querySelector(`[${DATA_ATTRIBUTES.resize.get}="${DATA_ATTRIBUTES.resize.defaultValue}"]`)
    }

    function isSelectable(el) {
      return el.querySelectorAll('input[type="checkbox"]').length > 0 || el.querySelectorAll('input[type="radio"]').length > 0
    }

    function getGripHeaderElem(th) {
      return th.querySelector(`[${DATA_ATTRIBUTES.resizeHeader.get}="${DATA_ATTRIBUTES.resizeHeader.defaultValue}"]`)
    }

    function setPreDefinedColWidth(cols, widths) {
      cols.forEach((col, index) => {
        if (widths[index] && !(index === 0 && isSelectable(col))) {
          col.style.width = widths[index];
        }
      });
    }

    function getColsWidths(node) {
      const widths = [];
      const thead = node.elm.querySelector('thead');
      const cols = thead.querySelectorAll('th');
      cols.forEach((col) => {
        widths.push(`${col.getBoundingClientRect().width.toFixed(2)}px`);
      });
      return widths
    }

    function getTextWidth(text, font) {
      const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
      const context = canvas.getContext('2d');
      context.font = font;
      const metrics = context.measureText(text);
      return metrics.width
    }

    function getCssStyle(element, prop) {
      return window.getComputedStyle(element, null).getPropertyValue(prop)
    }

    function getCanvasFont(el = document.body) {
      const fontWeight = getCssStyle(el, 'font-weight') || 'bolder';
      const fontSize = getCssStyle(el, 'font-size') || '16px';
      const fontFamily = getCssStyle(el, 'font-family') || 'Times New Roman';

      return `${fontWeight} ${fontSize} ${fontFamily}`
    }

    function getElementSize(el) {
      const fontSize = getTextWidth(el.innerText, getCanvasFont(el));
      const paddingLeft = cutPx(window.getComputedStyle(el, null).getPropertyValue('padding-left'));
      const paddingRight = cutPx(window.getComputedStyle(el, null).getPropertyValue('padding-right'));
      return fontSize + paddingLeft + paddingRight
    }

    function destroyGrips(tableThs) {
      tableThs.forEach((th) => {
        const grip = getGripElem(th);
        if (getGripElem(th)) {
          th.removeChild(grip);
        }

        const gripHeader = getGripHeaderElem(th);
        if (gripHeader) {
          th.removeChild(gripHeader);
        }
      });
    }

    function applyCss(element, style) {
      for (const property in style) {
        element.style[property] = style[property];
      }
    }

    function setTableStyle(el) {
      applyCss(el, {
        width: 'max-content',
        minWidth: '100%',
        tableLayout: 'fixed'
      });
    }

    function setResizeStyle(el) {
      applyCss(el, {
        top: 0,
        right: 0,
        bottom: 0,
        width: '5px',
        position: 'absolute',
        'z-index': 1
      });
    }

    function setResizeHeaderStyle(el) {
      applyCss(el, {
        top: '50%',
        right: '0px',
        height: '30%',
        width: '1px',
        position: 'absolute',
        backgroundColor: 'var(--me-gray-6)',
        zIndex: 1,
        transform: 'translate(0px, -50%)'
      });
    }

    function setResizeHoverStyle(el) {
      applyCss(el, {
        'border-right': '1px solid var(--me-gray-6)',
        cursor: 'col-resize',
        'user-select': 'none'
      });
    }

    function removeResizeHoverStyle(el) {
      applyCss(el, {
        'border-right': '0px',
        cursor: 'initial',
        'user-select': 'initial'
      });
    }

    function addThsKeys(ths) {
      ths.forEach((th, index) => {
        setDataAttribute({
          el: th,
          attr: DATA_ATTRIBUTES.resizeCol.set,
          attrValue: `resizeCol_${index}`
        });
      });
    }

    function getHasColumnsChanged(ths1, ths2) {
      if (!ths1 || !ths2) return true
      if (ths1.length || ths2.length) return true // TODO rever isso, pq para rodar o loop abaixo, ths1 precisa ter length

      for (let i = 0; i < ths1.length; i++) {
        if (!ths1[i]?.dataset || !ths2[i]?.dataset) return true
        if (ths1[i].dataset[DATA_ATTRIBUTES.resizeCol.set] !== ths2[i].dataset[DATA_ATTRIBUTES.resizeCol.set]) return true
      }

      return false
    }

    const cutPx = str => +str.replace('px', '');

    function initializeGrip(thIndex, gripHeight, tableThs, tableVNode, tbody) {
      let movingThIndex = null;
      let maxColSize = null;

      function mouseUp() {
        isMoving = false;
        const th = tableThs[movingThIndex];
        const grip = getGripElem(th);

        applyCss(grip, { width: '5px ' });

        document.body.style.cursor = '';
        document.removeEventListener('mousemove', mouseMove);
        document.removeEventListener('mouseup', mouseUp);

        setTimeout(() => {
          removeResizeHoverStyle(grip);
          tableVNode.child.$emit('colresize', getColsWidths(tableVNode));
        }, DBL_CLICK_INTERVAL);
      }

      function mouseMove(e) {
        if (e.movementX === 0) return

        const th = tableThs[movingThIndex];
        const nextTh = tableThs[movingThIndex + 1];

        const isMininumColumnSize = cutPx(th.style.width) !== th.offsetWidth || cutPx(nextTh.style.width) !== nextTh.offsetWidth;

        if (isMininumColumnSize) {
          th.style.width = `${th.offsetWidth}px`;
          nextTh.style.width = `${nextTh.offsetWidth}px`;
        }

        const newWidthValue = cutPx(th.style.width) + e.movementX;
        if (e.movementX > 0 && newWidthValue > Math.max(maxColSize + EXTRA_PX_MAX_COL, MIN_WIDTH_ALLOWED_TO_RESIZE)) return

        const hasScrollTable = tableVNode.elm?.offsetWidth < tableVNode.elm?.scrollWidth;
        if (e.movementX > 0 || (e.movementX < 0 && !hasScrollTable)) {
          nextTh.style.width = `${cutPx(nextTh.style.width) - e.movementX}px`;
        }

        th.style.width = `${cutPx(th.style.width) + e.movementX}px`;

        if (e.movementX < 0) {
          th.style.maxWidth = `${Math.max(cutPx(th.style.width), MIN_TH_WIDTH)}px`;
        }

        if (e.movementX > 0) {
          th.style.maxWidth = th.style.width;
        }

        document.body.style.cursor = 'col-resize';
      }

      function mouseDown() {
        isMoving = true;
        movingThIndex = thIndex;

        const tds = Array.from(tbody.querySelectorAll(`tr td:nth-child(${thIndex + 1})`));
        maxColSize = null;
        const fontSizes = tds.map(getElementSize);
        maxColSize = Math.max(...fontSizes);

        applyCss(grip, { width: '100%' });

        document.addEventListener('mousemove', mouseMove);
        document.addEventListener('mouseup', mouseUp);
      }

      function dblClick() {
        const th = tableThs[thIndex];
        th.style.maxWidth = 'none';
        th.style.width = 'fit-content';
        th.style.width = `${Math.min((th.offsetWidth + EXTRA_PX_TO_AVOID_TRUNCATE))}px`;

        const tableScrollDifWidth = tableVNode.elm?.scrollWidth - tableVNode.elm?.offsetWidth;
        if (tableScrollDifWidth === EXTRA_PX_TO_AVOID_TRUNCATE) {
          th.style.width = `${Math.min((th.offsetWidth - EXTRA_PX_TO_AVOID_TRUNCATE))}px`;
        }

        th.style.maxWidth = th.style.width;
      }

      const grip = document.createElement('div');
      setDataAttribute({
        el: grip,
        attr: DATA_ATTRIBUTES.resize.set,
        attrValue: DATA_ATTRIBUTES.resize.defaultValue
      });

      const gripHeader = document.createElement('div');
      setDataAttribute({
        el: gripHeader,
        attr: DATA_ATTRIBUTES.resizeHeader.set,
        attrValue: DATA_ATTRIBUTES.resizeHeader.defaultValue
      });

      setResizeStyle(grip);
      setResizeHeaderStyle(gripHeader);

      applyCss(grip, { height: gripHeight });

      grip.addEventListener('click', e => e.stopPropagation());
      grip.addEventListener('dblclick', dblClick);

      grip.addEventListener('mouseenter', (_e) => {
        if (isMoving) return
        setResizeHoverStyle(grip);
      });

      grip.addEventListener('mouseleave', (_e) => {
        if (isMoving) return
        removeResizeHoverStyle(grip);
      });

      grip.addEventListener('mousedown', mouseDown);

      const th = tableThs[thIndex];
      th.appendChild(gripHeader);
      th.appendChild(grip);
    }

    Vue.directive('me-resize', {
      inserted(el, binding, vnode) {
        // eslint-disable-next-line no-prototype-builtins
        if (binding.hasOwnProperty('value') && !binding.value) return

        const table = getTableEl(el);
        const tableResizeAttrValue = generateTableIdentifier();
        setDataAttribute({
          el: table,
          attr: DATA_ATTRIBUTES.meTableResize.set,
          attrValue: tableResizeAttrValue
        });

        saveTableStyle(tableResizeAttrValue, {
          width: table.style.width,
          minWidth: table.style.minWidth,
          tableLayout: table.style.tableLayout
        });

        const thead = el.querySelector('thead');
        const tbody = el.querySelector('tbody');
        const tableThs = thead.querySelectorAll('th');

        addThsKeys(tableThs);
        const height = getTableHeight(el);

        if (Array.isArray(binding.value?.sizes) && binding.value?.sizes?.length === tableThs.length) {
          setTableStyle(table);
          setPreDefinedColWidth(tableThs, binding.value?.sizes);
        }

        tableThs.forEach((th, index) => {
          if ((index === 0 && isSelectable(th)) || index + 1 >= tableThs.length) return

          if (cutPx(th.style.width) !== th.offsetWidth) {
            th.style.width = `${th.offsetWidth}px`;
          }

          initializeGrip(index, height, tableThs, vnode, tbody);
        });

        el.resizeObs = new ResizeObserver((entries) => {
          const height = entries[0].contentRect.height.toFixed(2);
          entries[0].target.querySelectorAll(`[${DATA_ATTRIBUTES.resize.get}="${DATA_ATTRIBUTES.resize.defaultValue}"]`)
            .forEach((grip) => {
              applyCss(grip, { height: `${height}px` });
            });
        });

        el.tableThs = tableThs;

        el.resizeObs.observe(el);
        setTableStyle(table);
      },

      componentUpdated(el, binding, vnode) {
        Vue.nextTick(() => {
          // eslint-disable-next-line no-prototype-builtins
          if (binding.hasOwnProperty('value') && !binding.value) {
            destroyResize(el);
            return
          }

          const table = getTableEl(el);
          let tableResizeAttrValue = table.getAttribute(DATA_ATTRIBUTES.meTableResize.get);
          if (!tableResizeAttrValue) {
            tableResizeAttrValue = generateTableIdentifier();
            setDataAttribute({
              el: table,
              attr: DATA_ATTRIBUTES.meTableResize.set,
              attrValue: tableResizeAttrValue
            });

            saveTableStyle(tableResizeAttrValue, {
              width: table.style.width,
              minWidth: table.style.minWidth,
              tableLayout: table.style.tableLayout
            });

            setTableStyle(table);
          }

          const thead = table.querySelector('thead');
          const tbody = el.querySelector('tbody');
          const updatedThs = thead.querySelectorAll('th');

          const hasColumnsChanged = getHasColumnsChanged(updatedThs, el.tableThs);
          if (hasColumnsChanged) {
            destroyGrips(updatedThs);
            addThsKeys(updatedThs);
          }

          if (hasColumnsChanged && Array.isArray(binding.value?.sizes) && binding.value.sizes?.length === updatedThs.length) {
            setPreDefinedColWidth(updatedThs, binding.value?.sizes);
          }

          const height = getTableHeight(el);

          updatedThs.forEach((th, index) => {
            if ((index === 0 && isSelectable(th)) || index + 1 >= updatedThs.length) return

            if (cutPx(th.style.width) !== th.offsetWidth) {
              th.style.width = `${th.offsetWidth}px`;
            }

            const currentGrip = getGripElem(th);
            if (currentGrip) {
              applyCss(currentGrip, { height });
            } else {
              initializeGrip(index, height, updatedThs, vnode, tbody);
            }
          });

          el.tableThs = updatedThs;

          if (!el.resizeObs) {
            el.resizeObs = new ResizeObserver((entries) => {
              const height = entries[0].contentRect.height.toFixed(2);
              entries[0].target.querySelectorAll(`[${DATA_ATTRIBUTES.resize.get}="${DATA_ATTRIBUTES.resize.defaultValue}"]`)
                .forEach((grip) => {
                  applyCss(grip, { height: `${height}px` });
                });
            });

            el.resizeObs.observe(el);
          }
        });
      },

      unbind(el) {
        if (el.resizeObs?.disconnect && typeof el.resizeObs.disconnect === 'function') {
          el.resizeObs.disconnect();
          el.resizeObs = null;
        }

        const table = getTableEl(el);
        const tableResizeAttrValue = table.getAttribute(DATA_ATTRIBUTES.meTableResize.get);
        if (tableResizeAttrValue) {
          tableInitialStylesMap.delete(tableResizeAttrValue);
        }
      }
    });
  }
};

/**!
 * Sortable 1.15.0
 * @author	RubaXa   <trash@rubaxa.org>
 * @author	owenm    <owen23355@gmail.com>
 * @license MIT
 */
function ownKeys$N(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys$N(Object(source), true).forEach(function (key) {
        _defineProperty$10(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys$N(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _typeof$6(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof$6 = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof$6 = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof$6(obj);
}

function _defineProperty$10(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$3() {
  _extends$3 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$3.apply(this, arguments);
}

function _objectWithoutPropertiesLoose$2(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties$2(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$2(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var version$2 = "1.15.0";

function userAgent$2(pattern) {
  if (typeof window !== 'undefined' && window.navigator) {
    return !! /*@__PURE__*/navigator.userAgent.match(pattern);
  }
}

var IE11OrLess$1 = userAgent$2(/(?:Trident.*rv[ :]?11\.|msie|iemobile|Windows Phone)/i);
var Edge$1 = userAgent$2(/Edge/i);
var FireFox$1 = userAgent$2(/firefox/i);
var Safari$1 = userAgent$2(/safari/i) && !userAgent$2(/chrome/i) && !userAgent$2(/android/i);
var IOS$1 = userAgent$2(/iP(ad|od|hone)/i);
var ChromeForAndroid$1 = userAgent$2(/chrome/i) && userAgent$2(/android/i);

var captureMode$1 = {
  capture: false,
  passive: false
};

function on$1(el, event, fn) {
  el.addEventListener(event, fn, !IE11OrLess$1 && captureMode$1);
}

function off$1(el, event, fn) {
  el.removeEventListener(event, fn, !IE11OrLess$1 && captureMode$1);
}

function matches$2(
/**HTMLElement*/
el,
/**String*/
selector) {
  if (!selector) return;
  selector[0] === '>' && (selector = selector.substring(1));

  if (el) {
    try {
      if (el.matches) {
        return el.matches(selector);
      } else if (el.msMatchesSelector) {
        return el.msMatchesSelector(selector);
      } else if (el.webkitMatchesSelector) {
        return el.webkitMatchesSelector(selector);
      }
    } catch (_) {
      return false;
    }
  }

  return false;
}

function getParentOrHost$1(el) {
  return el.host && el !== document && el.host.nodeType ? el.host : el.parentNode;
}

function closest$2(
/**HTMLElement*/
el,
/**String*/
selector,
/**HTMLElement*/
ctx, includeCTX) {
  if (el) {
    ctx = ctx || document;

    do {
      if (selector != null && (selector[0] === '>' ? el.parentNode === ctx && matches$2(el, selector) : matches$2(el, selector)) || includeCTX && el === ctx) {
        return el;
      }

      if (el === ctx) break;
      /* jshint boss:true */
    } while (el = getParentOrHost$1(el));
  }

  return null;
}

var R_SPACE$1 = /\s+/g;

function toggleClass$1(el, name, state) {
  if (el && name) {
    if (el.classList) {
      el.classList[state ? 'add' : 'remove'](name);
    } else {
      var className = (' ' + el.className + ' ').replace(R_SPACE$1, ' ').replace(' ' + name + ' ', ' ');
      el.className = (className + (state ? ' ' + name : '')).replace(R_SPACE$1, ' ');
    }
  }
}

function css$1(el, prop, val) {
  var style = el && el.style;

  if (style) {
    if (val === void 0) {
      if (document.defaultView && document.defaultView.getComputedStyle) {
        val = document.defaultView.getComputedStyle(el, '');
      } else if (el.currentStyle) {
        val = el.currentStyle;
      }

      return prop === void 0 ? val : val[prop];
    } else {
      if (!(prop in style) && prop.indexOf('webkit') === -1) {
        prop = '-webkit-' + prop;
      }

      style[prop] = val + (typeof val === 'string' ? '' : 'px');
    }
  }
}

function matrix$1(el, selfOnly) {
  var appliedTransforms = '';

  if (typeof el === 'string') {
    appliedTransforms = el;
  } else {
    do {
      var transform = css$1(el, 'transform');

      if (transform && transform !== 'none') {
        appliedTransforms = transform + ' ' + appliedTransforms;
      }
      /* jshint boss:true */

    } while (!selfOnly && (el = el.parentNode));
  }

  var matrixFn = window.DOMMatrix || window.WebKitCSSMatrix || window.CSSMatrix || window.MSCSSMatrix;
  /*jshint -W056 */

  return matrixFn && new matrixFn(appliedTransforms);
}

function find$3(ctx, tagName, iterator) {
  if (ctx) {
    var list = ctx.getElementsByTagName(tagName),
        i = 0,
        n = list.length;

    if (iterator) {
      for (; i < n; i++) {
        iterator(list[i], i);
      }
    }

    return list;
  }

  return [];
}

function getWindowScrollingElement$1() {
  var scrollingElement = document.scrollingElement;

  if (scrollingElement) {
    return scrollingElement;
  } else {
    return document.documentElement;
  }
}
/**
 * Returns the "bounding client rect" of given element
 * @param  {HTMLElement} el                       The element whose boundingClientRect is wanted
 * @param  {[Boolean]} relativeToContainingBlock  Whether the rect should be relative to the containing block of (including) the container
 * @param  {[Boolean]} relativeToNonStaticParent  Whether the rect should be relative to the relative parent of (including) the contaienr
 * @param  {[Boolean]} undoScale                  Whether the container's scale() should be undone
 * @param  {[HTMLElement]} container              The parent the element will be placed in
 * @return {Object}                               The boundingClientRect of el, with specified adjustments
 */


function getRect$1(el, relativeToContainingBlock, relativeToNonStaticParent, undoScale, container) {
  if (!el.getBoundingClientRect && el !== window) return;
  var elRect, top, left, bottom, right, height, width;

  if (el !== window && el.parentNode && el !== getWindowScrollingElement$1()) {
    elRect = el.getBoundingClientRect();
    top = elRect.top;
    left = elRect.left;
    bottom = elRect.bottom;
    right = elRect.right;
    height = elRect.height;
    width = elRect.width;
  } else {
    top = 0;
    left = 0;
    bottom = window.innerHeight;
    right = window.innerWidth;
    height = window.innerHeight;
    width = window.innerWidth;
  }

  if ((relativeToContainingBlock || relativeToNonStaticParent) && el !== window) {
    // Adjust for translate()
    container = container || el.parentNode; // solves #1123 (see: https://stackoverflow.com/a/37953806/6088312)
    // Not needed on <= IE11

    if (!IE11OrLess$1) {
      do {
        if (container && container.getBoundingClientRect && (css$1(container, 'transform') !== 'none' || relativeToNonStaticParent && css$1(container, 'position') !== 'static')) {
          var containerRect = container.getBoundingClientRect(); // Set relative to edges of padding box of container

          top -= containerRect.top + parseInt(css$1(container, 'border-top-width'));
          left -= containerRect.left + parseInt(css$1(container, 'border-left-width'));
          bottom = top + elRect.height;
          right = left + elRect.width;
          break;
        }
        /* jshint boss:true */

      } while (container = container.parentNode);
    }
  }

  if (undoScale && el !== window) {
    // Adjust for scale()
    var elMatrix = matrix$1(container || el),
        scaleX = elMatrix && elMatrix.a,
        scaleY = elMatrix && elMatrix.d;

    if (elMatrix) {
      top /= scaleY;
      left /= scaleX;
      width /= scaleX;
      height /= scaleY;
      bottom = top + height;
      right = left + width;
    }
  }

  return {
    top: top,
    left: left,
    bottom: bottom,
    right: right,
    width: width,
    height: height
  };
}
/**
 * Checks if a side of an element is scrolled past a side of its parents
 * @param  {HTMLElement}  el           The element who's side being scrolled out of view is in question
 * @param  {String}       elSide       Side of the element in question ('top', 'left', 'right', 'bottom')
 * @param  {String}       parentSide   Side of the parent in question ('top', 'left', 'right', 'bottom')
 * @return {HTMLElement}               The parent scroll element that the el's side is scrolled past, or null if there is no such element
 */


function isScrolledPast$1(el, elSide, parentSide) {
  var parent = getParentAutoScrollElement$1(el, true),
      elSideVal = getRect$1(el)[elSide];
  /* jshint boss:true */

  while (parent) {
    var parentSideVal = getRect$1(parent)[parentSide],
        visible = void 0;

    if (parentSide === 'top' || parentSide === 'left') {
      visible = elSideVal >= parentSideVal;
    } else {
      visible = elSideVal <= parentSideVal;
    }

    if (!visible) return parent;
    if (parent === getWindowScrollingElement$1()) break;
    parent = getParentAutoScrollElement$1(parent, false);
  }

  return false;
}
/**
 * Gets nth child of el, ignoring hidden children, sortable's elements (does not ignore clone if it's visible)
 * and non-draggable elements
 * @param  {HTMLElement} el       The parent element
 * @param  {Number} childNum      The index of the child
 * @param  {Object} options       Parent Sortable's options
 * @return {HTMLElement}          The child at index childNum, or null if not found
 */


function getChild$1(el, childNum, options, includeDragEl) {
  var currentChild = 0,
      i = 0,
      children = el.children;

  while (i < children.length) {
    if (children[i].style.display !== 'none' && children[i] !== Sortable$1.ghost && (includeDragEl || children[i] !== Sortable$1.dragged) && closest$2(children[i], options.draggable, el, false)) {
      if (currentChild === childNum) {
        return children[i];
      }

      currentChild++;
    }

    i++;
  }

  return null;
}
/**
 * Gets the last child in the el, ignoring ghostEl or invisible elements (clones)
 * @param  {HTMLElement} el       Parent element
 * @param  {selector} selector    Any other elements that should be ignored
 * @return {HTMLElement}          The last child, ignoring ghostEl
 */


function lastChild$1(el, selector) {
  var last = el.lastElementChild;

  while (last && (last === Sortable$1.ghost || css$1(last, 'display') === 'none' || selector && !matches$2(last, selector))) {
    last = last.previousElementSibling;
  }

  return last || null;
}
/**
 * Returns the index of an element within its parent for a selected set of
 * elements
 * @param  {HTMLElement} el
 * @param  {selector} selector
 * @return {number}
 */


function index$1(el, selector) {
  var index = 0;

  if (!el || !el.parentNode) {
    return -1;
  }
  /* jshint boss:true */


  while (el = el.previousElementSibling) {
    if (el.nodeName.toUpperCase() !== 'TEMPLATE' && el !== Sortable$1.clone && (!selector || matches$2(el, selector))) {
      index++;
    }
  }

  return index;
}
/**
 * Returns the scroll offset of the given element, added with all the scroll offsets of parent elements.
 * The value is returned in real pixels.
 * @param  {HTMLElement} el
 * @return {Array}             Offsets in the format of [left, top]
 */


function getRelativeScrollOffset$1(el) {
  var offsetLeft = 0,
      offsetTop = 0,
      winScroller = getWindowScrollingElement$1();

  if (el) {
    do {
      var elMatrix = matrix$1(el),
          scaleX = elMatrix.a,
          scaleY = elMatrix.d;
      offsetLeft += el.scrollLeft * scaleX;
      offsetTop += el.scrollTop * scaleY;
    } while (el !== winScroller && (el = el.parentNode));
  }

  return [offsetLeft, offsetTop];
}
/**
 * Returns the index of the object within the given array
 * @param  {Array} arr   Array that may or may not hold the object
 * @param  {Object} obj  An object that has a key-value pair unique to and identical to a key-value pair in the object you want to find
 * @return {Number}      The index of the object in the array, or -1
 */


function indexOfObject$1(arr, obj) {
  for (var i in arr) {
    if (!arr.hasOwnProperty(i)) continue;

    for (var key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] === arr[i][key]) return Number(i);
    }
  }

  return -1;
}

function getParentAutoScrollElement$1(el, includeSelf) {
  // skip to window
  if (!el || !el.getBoundingClientRect) return getWindowScrollingElement$1();
  var elem = el;
  var gotSelf = false;

  do {
    // we don't need to get elem css if it isn't even overflowing in the first place (performance)
    if (elem.clientWidth < elem.scrollWidth || elem.clientHeight < elem.scrollHeight) {
      var elemCSS = css$1(elem);

      if (elem.clientWidth < elem.scrollWidth && (elemCSS.overflowX == 'auto' || elemCSS.overflowX == 'scroll') || elem.clientHeight < elem.scrollHeight && (elemCSS.overflowY == 'auto' || elemCSS.overflowY == 'scroll')) {
        if (!elem.getBoundingClientRect || elem === document.body) return getWindowScrollingElement$1();
        if (gotSelf || includeSelf) return elem;
        gotSelf = true;
      }
    }
    /* jshint boss:true */

  } while (elem = elem.parentNode);

  return getWindowScrollingElement$1();
}

function extend$1(dst, src) {
  if (dst && src) {
    for (var key in src) {
      if (src.hasOwnProperty(key)) {
        dst[key] = src[key];
      }
    }
  }

  return dst;
}

function isRectEqual$1(rect1, rect2) {
  return Math.round(rect1.top) === Math.round(rect2.top) && Math.round(rect1.left) === Math.round(rect2.left) && Math.round(rect1.height) === Math.round(rect2.height) && Math.round(rect1.width) === Math.round(rect2.width);
}

var _throttleTimeout$1;

function throttle$1(callback, ms) {
  return function () {
    if (!_throttleTimeout$1) {
      var args = arguments,
          _this = this;

      if (args.length === 1) {
        callback.call(_this, args[0]);
      } else {
        callback.apply(_this, args);
      }

      _throttleTimeout$1 = setTimeout(function () {
        _throttleTimeout$1 = void 0;
      }, ms);
    }
  };
}

function cancelThrottle$1() {
  clearTimeout(_throttleTimeout$1);
  _throttleTimeout$1 = void 0;
}

function scrollBy$1(el, x, y) {
  el.scrollLeft += x;
  el.scrollTop += y;
}

function clone$2(el) {
  var Polymer = window.Polymer;
  var $ = window.jQuery || window.Zepto;

  if (Polymer && Polymer.dom) {
    return Polymer.dom(el).cloneNode(true);
  } else if ($) {
    return $(el).clone(true)[0];
  } else {
    return el.cloneNode(true);
  }
}

var expando$1 = 'Sortable' + new Date().getTime();

function AnimationStateManager$1() {
  var animationStates = [],
      animationCallbackId;
  return {
    captureAnimationState: function captureAnimationState() {
      animationStates = [];
      if (!this.options.animation) return;
      var children = [].slice.call(this.el.children);
      children.forEach(function (child) {
        if (css$1(child, 'display') === 'none' || child === Sortable$1.ghost) return;
        animationStates.push({
          target: child,
          rect: getRect$1(child)
        });

        var fromRect = _objectSpread2$1({}, animationStates[animationStates.length - 1].rect); // If animating: compensate for current animation


        if (child.thisAnimationDuration) {
          var childMatrix = matrix$1(child, true);

          if (childMatrix) {
            fromRect.top -= childMatrix.f;
            fromRect.left -= childMatrix.e;
          }
        }

        child.fromRect = fromRect;
      });
    },
    addAnimationState: function addAnimationState(state) {
      animationStates.push(state);
    },
    removeAnimationState: function removeAnimationState(target) {
      animationStates.splice(indexOfObject$1(animationStates, {
        target: target
      }), 1);
    },
    animateAll: function animateAll(callback) {
      var _this = this;

      if (!this.options.animation) {
        clearTimeout(animationCallbackId);
        if (typeof callback === 'function') callback();
        return;
      }

      var animating = false,
          animationTime = 0;
      animationStates.forEach(function (state) {
        var time = 0,
            target = state.target,
            fromRect = target.fromRect,
            toRect = getRect$1(target),
            prevFromRect = target.prevFromRect,
            prevToRect = target.prevToRect,
            animatingRect = state.rect,
            targetMatrix = matrix$1(target, true);

        if (targetMatrix) {
          // Compensate for current animation
          toRect.top -= targetMatrix.f;
          toRect.left -= targetMatrix.e;
        }

        target.toRect = toRect;

        if (target.thisAnimationDuration) {
          // Could also check if animatingRect is between fromRect and toRect
          if (isRectEqual$1(prevFromRect, toRect) && !isRectEqual$1(fromRect, toRect) && // Make sure animatingRect is on line between toRect & fromRect
          (animatingRect.top - toRect.top) / (animatingRect.left - toRect.left) === (fromRect.top - toRect.top) / (fromRect.left - toRect.left)) {
            // If returning to same place as started from animation and on same axis
            time = calculateRealTime$1(animatingRect, prevFromRect, prevToRect, _this.options);
          }
        } // if fromRect != toRect: animate


        if (!isRectEqual$1(toRect, fromRect)) {
          target.prevFromRect = fromRect;
          target.prevToRect = toRect;

          if (!time) {
            time = _this.options.animation;
          }

          _this.animate(target, animatingRect, toRect, time);
        }

        if (time) {
          animating = true;
          animationTime = Math.max(animationTime, time);
          clearTimeout(target.animationResetTimer);
          target.animationResetTimer = setTimeout(function () {
            target.animationTime = 0;
            target.prevFromRect = null;
            target.fromRect = null;
            target.prevToRect = null;
            target.thisAnimationDuration = null;
          }, time);
          target.thisAnimationDuration = time;
        }
      });
      clearTimeout(animationCallbackId);

      if (!animating) {
        if (typeof callback === 'function') callback();
      } else {
        animationCallbackId = setTimeout(function () {
          if (typeof callback === 'function') callback();
        }, animationTime);
      }

      animationStates = [];
    },
    animate: function animate(target, currentRect, toRect, duration) {
      if (duration) {
        css$1(target, 'transition', '');
        css$1(target, 'transform', '');
        var elMatrix = matrix$1(this.el),
            scaleX = elMatrix && elMatrix.a,
            scaleY = elMatrix && elMatrix.d,
            translateX = (currentRect.left - toRect.left) / (scaleX || 1),
            translateY = (currentRect.top - toRect.top) / (scaleY || 1);
        target.animatingX = !!translateX;
        target.animatingY = !!translateY;
        css$1(target, 'transform', 'translate3d(' + translateX + 'px,' + translateY + 'px,0)');
        this.forRepaintDummy = repaint$1(target); // repaint

        css$1(target, 'transition', 'transform ' + duration + 'ms' + (this.options.easing ? ' ' + this.options.easing : ''));
        css$1(target, 'transform', 'translate3d(0,0,0)');
        typeof target.animated === 'number' && clearTimeout(target.animated);
        target.animated = setTimeout(function () {
          css$1(target, 'transition', '');
          css$1(target, 'transform', '');
          target.animated = false;
          target.animatingX = false;
          target.animatingY = false;
        }, duration);
      }
    }
  };
}

function repaint$1(target) {
  return target.offsetWidth;
}

function calculateRealTime$1(animatingRect, fromRect, toRect, options) {
  return Math.sqrt(Math.pow(fromRect.top - animatingRect.top, 2) + Math.pow(fromRect.left - animatingRect.left, 2)) / Math.sqrt(Math.pow(fromRect.top - toRect.top, 2) + Math.pow(fromRect.left - toRect.left, 2)) * options.animation;
}

var plugins$1 = [];
var defaults$1 = {
  initializeByDefault: true
};
var PluginManager$1 = {
  mount: function mount(plugin) {
    // Set default static properties
    for (var option in defaults$1) {
      if (defaults$1.hasOwnProperty(option) && !(option in plugin)) {
        plugin[option] = defaults$1[option];
      }
    }

    plugins$1.forEach(function (p) {
      if (p.pluginName === plugin.pluginName) {
        throw "Sortable: Cannot mount plugin ".concat(plugin.pluginName, " more than once");
      }
    });
    plugins$1.push(plugin);
  },
  pluginEvent: function pluginEvent(eventName, sortable, evt) {
    var _this = this;

    this.eventCanceled = false;

    evt.cancel = function () {
      _this.eventCanceled = true;
    };

    var eventNameGlobal = eventName + 'Global';
    plugins$1.forEach(function (plugin) {
      if (!sortable[plugin.pluginName]) return; // Fire global events if it exists in this sortable

      if (sortable[plugin.pluginName][eventNameGlobal]) {
        sortable[plugin.pluginName][eventNameGlobal](_objectSpread2$1({
          sortable: sortable
        }, evt));
      } // Only fire plugin event if plugin is enabled in this sortable,
      // and plugin has event defined


      if (sortable.options[plugin.pluginName] && sortable[plugin.pluginName][eventName]) {
        sortable[plugin.pluginName][eventName](_objectSpread2$1({
          sortable: sortable
        }, evt));
      }
    });
  },
  initializePlugins: function initializePlugins(sortable, el, defaults, options) {
    plugins$1.forEach(function (plugin) {
      var pluginName = plugin.pluginName;
      if (!sortable.options[pluginName] && !plugin.initializeByDefault) return;
      var initialized = new plugin(sortable, el, sortable.options);
      initialized.sortable = sortable;
      initialized.options = sortable.options;
      sortable[pluginName] = initialized; // Add default options from plugin

      _extends$3(defaults, initialized.defaults);
    });

    for (var option in sortable.options) {
      if (!sortable.options.hasOwnProperty(option)) continue;
      var modified = this.modifyOption(sortable, option, sortable.options[option]);

      if (typeof modified !== 'undefined') {
        sortable.options[option] = modified;
      }
    }
  },
  getEventProperties: function getEventProperties(name, sortable) {
    var eventProperties = {};
    plugins$1.forEach(function (plugin) {
      if (typeof plugin.eventProperties !== 'function') return;

      _extends$3(eventProperties, plugin.eventProperties.call(sortable[plugin.pluginName], name));
    });
    return eventProperties;
  },
  modifyOption: function modifyOption(sortable, name, value) {
    var modifiedValue;
    plugins$1.forEach(function (plugin) {
      // Plugin must exist on the Sortable
      if (!sortable[plugin.pluginName]) return; // If static option listener exists for this option, call in the context of the Sortable's instance of this plugin

      if (plugin.optionListeners && typeof plugin.optionListeners[name] === 'function') {
        modifiedValue = plugin.optionListeners[name].call(sortable[plugin.pluginName], value);
      }
    });
    return modifiedValue;
  }
};

function dispatchEvent$1(_ref) {
  var sortable = _ref.sortable,
      rootEl = _ref.rootEl,
      name = _ref.name,
      targetEl = _ref.targetEl,
      cloneEl = _ref.cloneEl,
      toEl = _ref.toEl,
      fromEl = _ref.fromEl,
      oldIndex = _ref.oldIndex,
      newIndex = _ref.newIndex,
      oldDraggableIndex = _ref.oldDraggableIndex,
      newDraggableIndex = _ref.newDraggableIndex,
      originalEvent = _ref.originalEvent,
      putSortable = _ref.putSortable,
      extraEventProperties = _ref.extraEventProperties;
  sortable = sortable || rootEl && rootEl[expando$1];
  if (!sortable) return;
  var evt,
      options = sortable.options,
      onName = 'on' + name.charAt(0).toUpperCase() + name.substr(1); // Support for new CustomEvent feature

  if (window.CustomEvent && !IE11OrLess$1 && !Edge$1) {
    evt = new CustomEvent(name, {
      bubbles: true,
      cancelable: true
    });
  } else {
    evt = document.createEvent('Event');
    evt.initEvent(name, true, true);
  }

  evt.to = toEl || rootEl;
  evt.from = fromEl || rootEl;
  evt.item = targetEl || rootEl;
  evt.clone = cloneEl;
  evt.oldIndex = oldIndex;
  evt.newIndex = newIndex;
  evt.oldDraggableIndex = oldDraggableIndex;
  evt.newDraggableIndex = newDraggableIndex;
  evt.originalEvent = originalEvent;
  evt.pullMode = putSortable ? putSortable.lastPutMode : undefined;

  var allEventProperties = _objectSpread2$1(_objectSpread2$1({}, extraEventProperties), PluginManager$1.getEventProperties(name, sortable));

  for (var option in allEventProperties) {
    evt[option] = allEventProperties[option];
  }

  if (rootEl) {
    rootEl.dispatchEvent(evt);
  }

  if (options[onName]) {
    options[onName].call(sortable, evt);
  }
}

var _excluded = ["evt"];

var pluginEvent$1 = function pluginEvent(eventName, sortable) {
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      originalEvent = _ref.evt,
      data = _objectWithoutProperties$2(_ref, _excluded);

  PluginManager$1.pluginEvent.bind(Sortable$1)(eventName, sortable, _objectSpread2$1({
    dragEl: dragEl$1,
    parentEl: parentEl$1,
    ghostEl: ghostEl$1,
    rootEl: rootEl$1,
    nextEl: nextEl$1,
    lastDownEl: lastDownEl$1,
    cloneEl: cloneEl$1,
    cloneHidden: cloneHidden$1,
    dragStarted: moved$1,
    putSortable: putSortable$1,
    activeSortable: Sortable$1.active,
    originalEvent: originalEvent,
    oldIndex: oldIndex$1,
    oldDraggableIndex: oldDraggableIndex$1,
    newIndex: newIndex$1,
    newDraggableIndex: newDraggableIndex$1,
    hideGhostForTarget: _hideGhostForTarget$1,
    unhideGhostForTarget: _unhideGhostForTarget$1,
    cloneNowHidden: function cloneNowHidden() {
      cloneHidden$1 = true;
    },
    cloneNowShown: function cloneNowShown() {
      cloneHidden$1 = false;
    },
    dispatchSortableEvent: function dispatchSortableEvent(name) {
      _dispatchEvent$1({
        sortable: sortable,
        name: name,
        originalEvent: originalEvent
      });
    }
  }, data));
};

function _dispatchEvent$1(info) {
  dispatchEvent$1(_objectSpread2$1({
    putSortable: putSortable$1,
    cloneEl: cloneEl$1,
    targetEl: dragEl$1,
    rootEl: rootEl$1,
    oldIndex: oldIndex$1,
    oldDraggableIndex: oldDraggableIndex$1,
    newIndex: newIndex$1,
    newDraggableIndex: newDraggableIndex$1
  }, info));
}

var dragEl$1,
    parentEl$1,
    ghostEl$1,
    rootEl$1,
    nextEl$1,
    lastDownEl$1,
    cloneEl$1,
    cloneHidden$1,
    oldIndex$1,
    newIndex$1,
    oldDraggableIndex$1,
    newDraggableIndex$1,
    activeGroup$1,
    putSortable$1,
    awaitingDragStarted$1 = false,
    ignoreNextClick$1 = false,
    sortables$1 = [],
    tapEvt$1,
    touchEvt$2,
    lastDx$1,
    lastDy$1,
    tapDistanceLeft$1,
    tapDistanceTop$1,
    moved$1,
    lastTarget$1,
    lastDirection$1,
    pastFirstInvertThresh$1 = false,
    isCircumstantialInvert$1 = false,
    targetMoveDistance$1,
    // For positioning ghost absolutely
ghostRelativeParent$1,
    ghostRelativeParentInitialScroll$1 = [],
    // (left, top)
_silent$1 = false,
    savedInputChecked$1 = [];
/** @const */

var documentExists$1 = typeof document !== 'undefined',
    PositionGhostAbsolutely$1 = IOS$1,
    CSSFloatProperty$1 = Edge$1 || IE11OrLess$1 ? 'cssFloat' : 'float',
    // This will not pass for IE9, because IE9 DnD only works on anchors
supportDraggable$1 = documentExists$1 && !ChromeForAndroid$1 && !IOS$1 && 'draggable' in document.createElement('div'),
    supportCssPointerEvents$1 = function () {
  if (!documentExists$1) return; // false when <= IE11

  if (IE11OrLess$1) {
    return false;
  }

  var el = document.createElement('x');
  el.style.cssText = 'pointer-events:auto';
  return el.style.pointerEvents === 'auto';
}(),
    _detectDirection$1 = function _detectDirection(el, options) {
  var elCSS = css$1(el),
      elWidth = parseInt(elCSS.width) - parseInt(elCSS.paddingLeft) - parseInt(elCSS.paddingRight) - parseInt(elCSS.borderLeftWidth) - parseInt(elCSS.borderRightWidth),
      child1 = getChild$1(el, 0, options),
      child2 = getChild$1(el, 1, options),
      firstChildCSS = child1 && css$1(child1),
      secondChildCSS = child2 && css$1(child2),
      firstChildWidth = firstChildCSS && parseInt(firstChildCSS.marginLeft) + parseInt(firstChildCSS.marginRight) + getRect$1(child1).width,
      secondChildWidth = secondChildCSS && parseInt(secondChildCSS.marginLeft) + parseInt(secondChildCSS.marginRight) + getRect$1(child2).width;

  if (elCSS.display === 'flex') {
    return elCSS.flexDirection === 'column' || elCSS.flexDirection === 'column-reverse' ? 'vertical' : 'horizontal';
  }

  if (elCSS.display === 'grid') {
    return elCSS.gridTemplateColumns.split(' ').length <= 1 ? 'vertical' : 'horizontal';
  }

  if (child1 && firstChildCSS["float"] && firstChildCSS["float"] !== 'none') {
    var touchingSideChild2 = firstChildCSS["float"] === 'left' ? 'left' : 'right';
    return child2 && (secondChildCSS.clear === 'both' || secondChildCSS.clear === touchingSideChild2) ? 'vertical' : 'horizontal';
  }

  return child1 && (firstChildCSS.display === 'block' || firstChildCSS.display === 'flex' || firstChildCSS.display === 'table' || firstChildCSS.display === 'grid' || firstChildWidth >= elWidth && elCSS[CSSFloatProperty$1] === 'none' || child2 && elCSS[CSSFloatProperty$1] === 'none' && firstChildWidth + secondChildWidth > elWidth) ? 'vertical' : 'horizontal';
},
    _dragElInRowColumn$1 = function _dragElInRowColumn(dragRect, targetRect, vertical) {
  var dragElS1Opp = vertical ? dragRect.left : dragRect.top,
      dragElS2Opp = vertical ? dragRect.right : dragRect.bottom,
      dragElOppLength = vertical ? dragRect.width : dragRect.height,
      targetS1Opp = vertical ? targetRect.left : targetRect.top,
      targetS2Opp = vertical ? targetRect.right : targetRect.bottom,
      targetOppLength = vertical ? targetRect.width : targetRect.height;
  return dragElS1Opp === targetS1Opp || dragElS2Opp === targetS2Opp || dragElS1Opp + dragElOppLength / 2 === targetS1Opp + targetOppLength / 2;
},

/**
 * Detects first nearest empty sortable to X and Y position using emptyInsertThreshold.
 * @param  {Number} x      X position
 * @param  {Number} y      Y position
 * @return {HTMLElement}   Element of the first found nearest Sortable
 */
_detectNearestEmptySortable$1 = function _detectNearestEmptySortable(x, y) {
  var ret;
  sortables$1.some(function (sortable) {
    var threshold = sortable[expando$1].options.emptyInsertThreshold;
    if (!threshold || lastChild$1(sortable)) return;
    var rect = getRect$1(sortable),
        insideHorizontally = x >= rect.left - threshold && x <= rect.right + threshold,
        insideVertically = y >= rect.top - threshold && y <= rect.bottom + threshold;

    if (insideHorizontally && insideVertically) {
      return ret = sortable;
    }
  });
  return ret;
},
    _prepareGroup$1 = function _prepareGroup(options) {
  function toFn(value, pull) {
    return function (to, from, dragEl, evt) {
      var sameGroup = to.options.group.name && from.options.group.name && to.options.group.name === from.options.group.name;

      if (value == null && (pull || sameGroup)) {
        // Default pull value
        // Default pull and put value if same group
        return true;
      } else if (value == null || value === false) {
        return false;
      } else if (pull && value === 'clone') {
        return value;
      } else if (typeof value === 'function') {
        return toFn(value(to, from, dragEl, evt), pull)(to, from, dragEl, evt);
      } else {
        var otherGroup = (pull ? to : from).options.group.name;
        return value === true || typeof value === 'string' && value === otherGroup || value.join && value.indexOf(otherGroup) > -1;
      }
    };
  }

  var group = {};
  var originalGroup = options.group;

  if (!originalGroup || _typeof$6(originalGroup) != 'object') {
    originalGroup = {
      name: originalGroup
    };
  }

  group.name = originalGroup.name;
  group.checkPull = toFn(originalGroup.pull, true);
  group.checkPut = toFn(originalGroup.put);
  group.revertClone = originalGroup.revertClone;
  options.group = group;
},
    _hideGhostForTarget$1 = function _hideGhostForTarget() {
  if (!supportCssPointerEvents$1 && ghostEl$1) {
    css$1(ghostEl$1, 'display', 'none');
  }
},
    _unhideGhostForTarget$1 = function _unhideGhostForTarget() {
  if (!supportCssPointerEvents$1 && ghostEl$1) {
    css$1(ghostEl$1, 'display', '');
  }
}; // #1184 fix - Prevent click event on fallback if dragged but item not changed position


if (documentExists$1 && !ChromeForAndroid$1) {
  document.addEventListener('click', function (evt) {
    if (ignoreNextClick$1) {
      evt.preventDefault();
      evt.stopPropagation && evt.stopPropagation();
      evt.stopImmediatePropagation && evt.stopImmediatePropagation();
      ignoreNextClick$1 = false;
      return false;
    }
  }, true);
}

var nearestEmptyInsertDetectEvent$1 = function nearestEmptyInsertDetectEvent(evt) {
  if (dragEl$1) {
    evt = evt.touches ? evt.touches[0] : evt;

    var nearest = _detectNearestEmptySortable$1(evt.clientX, evt.clientY);

    if (nearest) {
      // Create imitation event
      var event = {};

      for (var i in evt) {
        if (evt.hasOwnProperty(i)) {
          event[i] = evt[i];
        }
      }

      event.target = event.rootEl = nearest;
      event.preventDefault = void 0;
      event.stopPropagation = void 0;

      nearest[expando$1]._onDragOver(event);
    }
  }
};

var _checkOutsideTargetEl$1 = function _checkOutsideTargetEl(evt) {
  if (dragEl$1) {
    dragEl$1.parentNode[expando$1]._isOutsideThisEl(evt.target);
  }
};
/**
 * @class  Sortable
 * @param  {HTMLElement}  el
 * @param  {Object}       [options]
 */


function Sortable$1(el, options) {
  if (!(el && el.nodeType && el.nodeType === 1)) {
    throw "Sortable: `el` must be an HTMLElement, not ".concat({}.toString.call(el));
  }

  this.el = el; // root element

  this.options = options = _extends$3({}, options); // Export instance

  el[expando$1] = this;
  var defaults = {
    group: null,
    sort: true,
    disabled: false,
    store: null,
    handle: null,
    draggable: /^[uo]l$/i.test(el.nodeName) ? '>li' : '>*',
    swapThreshold: 1,
    // percentage; 0 <= x <= 1
    invertSwap: false,
    // invert always
    invertedSwapThreshold: null,
    // will be set to same as swapThreshold if default
    removeCloneOnHide: true,
    direction: function direction() {
      return _detectDirection$1(el, this.options);
    },
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    ignore: 'a, img',
    filter: null,
    preventOnFilter: true,
    animation: 0,
    easing: null,
    setData: function setData(dataTransfer, dragEl) {
      dataTransfer.setData('Text', dragEl.textContent);
    },
    dropBubble: false,
    dragoverBubble: false,
    dataIdAttr: 'data-id',
    delay: 0,
    delayOnTouchOnly: false,
    touchStartThreshold: (Number.parseInt ? Number : window).parseInt(window.devicePixelRatio, 10) || 1,
    forceFallback: false,
    fallbackClass: 'sortable-fallback',
    fallbackOnBody: false,
    fallbackTolerance: 0,
    fallbackOffset: {
      x: 0,
      y: 0
    },
    supportPointer: Sortable$1.supportPointer !== false && 'PointerEvent' in window && !Safari$1,
    emptyInsertThreshold: 5
  };
  PluginManager$1.initializePlugins(this, el, defaults); // Set default options

  for (var name in defaults) {
    !(name in options) && (options[name] = defaults[name]);
  }

  _prepareGroup$1(options); // Bind all private methods


  for (var fn in this) {
    if (fn.charAt(0) === '_' && typeof this[fn] === 'function') {
      this[fn] = this[fn].bind(this);
    }
  } // Setup drag mode


  this.nativeDraggable = options.forceFallback ? false : supportDraggable$1;

  if (this.nativeDraggable) {
    // Touch start threshold cannot be greater than the native dragstart threshold
    this.options.touchStartThreshold = 1;
  } // Bind events


  if (options.supportPointer) {
    on$1(el, 'pointerdown', this._onTapStart);
  } else {
    on$1(el, 'mousedown', this._onTapStart);
    on$1(el, 'touchstart', this._onTapStart);
  }

  if (this.nativeDraggable) {
    on$1(el, 'dragover', this);
    on$1(el, 'dragenter', this);
  }

  sortables$1.push(this.el); // Restore sorting

  options.store && options.store.get && this.sort(options.store.get(this) || []); // Add animation state manager

  _extends$3(this, AnimationStateManager$1());
}

Sortable$1.prototype =
/** @lends Sortable.prototype */
{
  constructor: Sortable$1,
  _isOutsideThisEl: function _isOutsideThisEl(target) {
    if (!this.el.contains(target) && target !== this.el) {
      lastTarget$1 = null;
    }
  },
  _getDirection: function _getDirection(evt, target) {
    return typeof this.options.direction === 'function' ? this.options.direction.call(this, evt, target, dragEl$1) : this.options.direction;
  },
  _onTapStart: function _onTapStart(
  /** Event|TouchEvent */
  evt) {
    if (!evt.cancelable) return;

    var _this = this,
        el = this.el,
        options = this.options,
        preventOnFilter = options.preventOnFilter,
        type = evt.type,
        touch = evt.touches && evt.touches[0] || evt.pointerType && evt.pointerType === 'touch' && evt,
        target = (touch || evt).target,
        originalTarget = evt.target.shadowRoot && (evt.path && evt.path[0] || evt.composedPath && evt.composedPath()[0]) || target,
        filter = options.filter;

    _saveInputCheckedState$1(el); // Don't trigger start event when an element is been dragged, otherwise the evt.oldindex always wrong when set option.group.


    if (dragEl$1) {
      return;
    }

    if (/mousedown|pointerdown/.test(type) && evt.button !== 0 || options.disabled) {
      return; // only left button and enabled
    } // cancel dnd if original target is content editable


    if (originalTarget.isContentEditable) {
      return;
    } // Safari ignores further event handling after mousedown


    if (!this.nativeDraggable && Safari$1 && target && target.tagName.toUpperCase() === 'SELECT') {
      return;
    }

    target = closest$2(target, options.draggable, el, false);

    if (target && target.animated) {
      return;
    }

    if (lastDownEl$1 === target) {
      // Ignoring duplicate `down`
      return;
    } // Get the index of the dragged element within its parent


    oldIndex$1 = index$1(target);
    oldDraggableIndex$1 = index$1(target, options.draggable); // Check filter

    if (typeof filter === 'function') {
      if (filter.call(this, evt, target, this)) {
        _dispatchEvent$1({
          sortable: _this,
          rootEl: originalTarget,
          name: 'filter',
          targetEl: target,
          toEl: el,
          fromEl: el
        });

        pluginEvent$1('filter', _this, {
          evt: evt
        });
        preventOnFilter && evt.cancelable && evt.preventDefault();
        return; // cancel dnd
      }
    } else if (filter) {
      filter = filter.split(',').some(function (criteria) {
        criteria = closest$2(originalTarget, criteria.trim(), el, false);

        if (criteria) {
          _dispatchEvent$1({
            sortable: _this,
            rootEl: criteria,
            name: 'filter',
            targetEl: target,
            fromEl: el,
            toEl: el
          });

          pluginEvent$1('filter', _this, {
            evt: evt
          });
          return true;
        }
      });

      if (filter) {
        preventOnFilter && evt.cancelable && evt.preventDefault();
        return; // cancel dnd
      }
    }

    if (options.handle && !closest$2(originalTarget, options.handle, el, false)) {
      return;
    } // Prepare `dragstart`


    this._prepareDragStart(evt, touch, target);
  },
  _prepareDragStart: function _prepareDragStart(
  /** Event */
  evt,
  /** Touch */
  touch,
  /** HTMLElement */
  target) {
    var _this = this,
        el = _this.el,
        options = _this.options,
        ownerDocument = el.ownerDocument,
        dragStartFn;

    if (target && !dragEl$1 && target.parentNode === el) {
      var dragRect = getRect$1(target);
      rootEl$1 = el;
      dragEl$1 = target;
      parentEl$1 = dragEl$1.parentNode;
      nextEl$1 = dragEl$1.nextSibling;
      lastDownEl$1 = target;
      activeGroup$1 = options.group;
      Sortable$1.dragged = dragEl$1;
      tapEvt$1 = {
        target: dragEl$1,
        clientX: (touch || evt).clientX,
        clientY: (touch || evt).clientY
      };
      tapDistanceLeft$1 = tapEvt$1.clientX - dragRect.left;
      tapDistanceTop$1 = tapEvt$1.clientY - dragRect.top;
      this._lastX = (touch || evt).clientX;
      this._lastY = (touch || evt).clientY;
      dragEl$1.style['will-change'] = 'all';

      dragStartFn = function dragStartFn() {
        pluginEvent$1('delayEnded', _this, {
          evt: evt
        });

        if (Sortable$1.eventCanceled) {
          _this._onDrop();

          return;
        } // Delayed drag has been triggered
        // we can re-enable the events: touchmove/mousemove


        _this._disableDelayedDragEvents();

        if (!FireFox$1 && _this.nativeDraggable) {
          dragEl$1.draggable = true;
        } // Bind the events: dragstart/dragend


        _this._triggerDragStart(evt, touch); // Drag start event


        _dispatchEvent$1({
          sortable: _this,
          name: 'choose',
          originalEvent: evt
        }); // Chosen item


        toggleClass$1(dragEl$1, options.chosenClass, true);
      }; // Disable "draggable"


      options.ignore.split(',').forEach(function (criteria) {
        find$3(dragEl$1, criteria.trim(), _disableDraggable$1);
      });
      on$1(ownerDocument, 'dragover', nearestEmptyInsertDetectEvent$1);
      on$1(ownerDocument, 'mousemove', nearestEmptyInsertDetectEvent$1);
      on$1(ownerDocument, 'touchmove', nearestEmptyInsertDetectEvent$1);
      on$1(ownerDocument, 'mouseup', _this._onDrop);
      on$1(ownerDocument, 'touchend', _this._onDrop);
      on$1(ownerDocument, 'touchcancel', _this._onDrop); // Make dragEl draggable (must be before delay for FireFox)

      if (FireFox$1 && this.nativeDraggable) {
        this.options.touchStartThreshold = 4;
        dragEl$1.draggable = true;
      }

      pluginEvent$1('delayStart', this, {
        evt: evt
      }); // Delay is impossible for native DnD in Edge or IE

      if (options.delay && (!options.delayOnTouchOnly || touch) && (!this.nativeDraggable || !(Edge$1 || IE11OrLess$1))) {
        if (Sortable$1.eventCanceled) {
          this._onDrop();

          return;
        } // If the user moves the pointer or let go the click or touch
        // before the delay has been reached:
        // disable the delayed drag


        on$1(ownerDocument, 'mouseup', _this._disableDelayedDrag);
        on$1(ownerDocument, 'touchend', _this._disableDelayedDrag);
        on$1(ownerDocument, 'touchcancel', _this._disableDelayedDrag);
        on$1(ownerDocument, 'mousemove', _this._delayedDragTouchMoveHandler);
        on$1(ownerDocument, 'touchmove', _this._delayedDragTouchMoveHandler);
        options.supportPointer && on$1(ownerDocument, 'pointermove', _this._delayedDragTouchMoveHandler);
        _this._dragStartTimer = setTimeout(dragStartFn, options.delay);
      } else {
        dragStartFn();
      }
    }
  },
  _delayedDragTouchMoveHandler: function _delayedDragTouchMoveHandler(
  /** TouchEvent|PointerEvent **/
  e) {
    var touch = e.touches ? e.touches[0] : e;

    if (Math.max(Math.abs(touch.clientX - this._lastX), Math.abs(touch.clientY - this._lastY)) >= Math.floor(this.options.touchStartThreshold / (this.nativeDraggable && window.devicePixelRatio || 1))) {
      this._disableDelayedDrag();
    }
  },
  _disableDelayedDrag: function _disableDelayedDrag() {
    dragEl$1 && _disableDraggable$1(dragEl$1);
    clearTimeout(this._dragStartTimer);

    this._disableDelayedDragEvents();
  },
  _disableDelayedDragEvents: function _disableDelayedDragEvents() {
    var ownerDocument = this.el.ownerDocument;
    off$1(ownerDocument, 'mouseup', this._disableDelayedDrag);
    off$1(ownerDocument, 'touchend', this._disableDelayedDrag);
    off$1(ownerDocument, 'touchcancel', this._disableDelayedDrag);
    off$1(ownerDocument, 'mousemove', this._delayedDragTouchMoveHandler);
    off$1(ownerDocument, 'touchmove', this._delayedDragTouchMoveHandler);
    off$1(ownerDocument, 'pointermove', this._delayedDragTouchMoveHandler);
  },
  _triggerDragStart: function _triggerDragStart(
  /** Event */
  evt,
  /** Touch */
  touch) {
    touch = touch || evt.pointerType == 'touch' && evt;

    if (!this.nativeDraggable || touch) {
      if (this.options.supportPointer) {
        on$1(document, 'pointermove', this._onTouchMove);
      } else if (touch) {
        on$1(document, 'touchmove', this._onTouchMove);
      } else {
        on$1(document, 'mousemove', this._onTouchMove);
      }
    } else {
      on$1(dragEl$1, 'dragend', this);
      on$1(rootEl$1, 'dragstart', this._onDragStart);
    }

    try {
      if (document.selection) {
        // Timeout neccessary for IE9
        _nextTick$1(function () {
          document.selection.empty();
        });
      } else {
        window.getSelection().removeAllRanges();
      }
    } catch (err) {}
  },
  _dragStarted: function _dragStarted(fallback, evt) {

    awaitingDragStarted$1 = false;

    if (rootEl$1 && dragEl$1) {
      pluginEvent$1('dragStarted', this, {
        evt: evt
      });

      if (this.nativeDraggable) {
        on$1(document, 'dragover', _checkOutsideTargetEl$1);
      }

      var options = this.options; // Apply effect

      !fallback && toggleClass$1(dragEl$1, options.dragClass, false);
      toggleClass$1(dragEl$1, options.ghostClass, true);
      Sortable$1.active = this;
      fallback && this._appendGhost(); // Drag start event

      _dispatchEvent$1({
        sortable: this,
        name: 'start',
        originalEvent: evt
      });
    } else {
      this._nulling();
    }
  },
  _emulateDragOver: function _emulateDragOver() {
    if (touchEvt$2) {
      this._lastX = touchEvt$2.clientX;
      this._lastY = touchEvt$2.clientY;

      _hideGhostForTarget$1();

      var target = document.elementFromPoint(touchEvt$2.clientX, touchEvt$2.clientY);
      var parent = target;

      while (target && target.shadowRoot) {
        target = target.shadowRoot.elementFromPoint(touchEvt$2.clientX, touchEvt$2.clientY);
        if (target === parent) break;
        parent = target;
      }

      dragEl$1.parentNode[expando$1]._isOutsideThisEl(target);

      if (parent) {
        do {
          if (parent[expando$1]) {
            var inserted = void 0;
            inserted = parent[expando$1]._onDragOver({
              clientX: touchEvt$2.clientX,
              clientY: touchEvt$2.clientY,
              target: target,
              rootEl: parent
            });

            if (inserted && !this.options.dragoverBubble) {
              break;
            }
          }

          target = parent; // store last element
        }
        /* jshint boss:true */
        while (parent = parent.parentNode);
      }

      _unhideGhostForTarget$1();
    }
  },
  _onTouchMove: function _onTouchMove(
  /**TouchEvent*/
  evt) {
    if (tapEvt$1) {
      var options = this.options,
          fallbackTolerance = options.fallbackTolerance,
          fallbackOffset = options.fallbackOffset,
          touch = evt.touches ? evt.touches[0] : evt,
          ghostMatrix = ghostEl$1 && matrix$1(ghostEl$1, true),
          scaleX = ghostEl$1 && ghostMatrix && ghostMatrix.a,
          scaleY = ghostEl$1 && ghostMatrix && ghostMatrix.d,
          relativeScrollOffset = PositionGhostAbsolutely$1 && ghostRelativeParent$1 && getRelativeScrollOffset$1(ghostRelativeParent$1),
          dx = (touch.clientX - tapEvt$1.clientX + fallbackOffset.x) / (scaleX || 1) + (relativeScrollOffset ? relativeScrollOffset[0] - ghostRelativeParentInitialScroll$1[0] : 0) / (scaleX || 1),
          dy = (touch.clientY - tapEvt$1.clientY + fallbackOffset.y) / (scaleY || 1) + (relativeScrollOffset ? relativeScrollOffset[1] - ghostRelativeParentInitialScroll$1[1] : 0) / (scaleY || 1); // only set the status to dragging, when we are actually dragging

      if (!Sortable$1.active && !awaitingDragStarted$1) {
        if (fallbackTolerance && Math.max(Math.abs(touch.clientX - this._lastX), Math.abs(touch.clientY - this._lastY)) < fallbackTolerance) {
          return;
        }

        this._onDragStart(evt, true);
      }

      if (ghostEl$1) {
        if (ghostMatrix) {
          ghostMatrix.e += dx - (lastDx$1 || 0);
          ghostMatrix.f += dy - (lastDy$1 || 0);
        } else {
          ghostMatrix = {
            a: 1,
            b: 0,
            c: 0,
            d: 1,
            e: dx,
            f: dy
          };
        }

        var cssMatrix = "matrix(".concat(ghostMatrix.a, ",").concat(ghostMatrix.b, ",").concat(ghostMatrix.c, ",").concat(ghostMatrix.d, ",").concat(ghostMatrix.e, ",").concat(ghostMatrix.f, ")");
        css$1(ghostEl$1, 'webkitTransform', cssMatrix);
        css$1(ghostEl$1, 'mozTransform', cssMatrix);
        css$1(ghostEl$1, 'msTransform', cssMatrix);
        css$1(ghostEl$1, 'transform', cssMatrix);
        lastDx$1 = dx;
        lastDy$1 = dy;
        touchEvt$2 = touch;
      }

      evt.cancelable && evt.preventDefault();
    }
  },
  _appendGhost: function _appendGhost() {
    // Bug if using scale(): https://stackoverflow.com/questions/2637058
    // Not being adjusted for
    if (!ghostEl$1) {
      var container = this.options.fallbackOnBody ? document.body : rootEl$1,
          rect = getRect$1(dragEl$1, true, PositionGhostAbsolutely$1, true, container),
          options = this.options; // Position absolutely

      if (PositionGhostAbsolutely$1) {
        // Get relatively positioned parent
        ghostRelativeParent$1 = container;

        while (css$1(ghostRelativeParent$1, 'position') === 'static' && css$1(ghostRelativeParent$1, 'transform') === 'none' && ghostRelativeParent$1 !== document) {
          ghostRelativeParent$1 = ghostRelativeParent$1.parentNode;
        }

        if (ghostRelativeParent$1 !== document.body && ghostRelativeParent$1 !== document.documentElement) {
          if (ghostRelativeParent$1 === document) ghostRelativeParent$1 = getWindowScrollingElement$1();
          rect.top += ghostRelativeParent$1.scrollTop;
          rect.left += ghostRelativeParent$1.scrollLeft;
        } else {
          ghostRelativeParent$1 = getWindowScrollingElement$1();
        }

        ghostRelativeParentInitialScroll$1 = getRelativeScrollOffset$1(ghostRelativeParent$1);
      }

      ghostEl$1 = dragEl$1.cloneNode(true);
      toggleClass$1(ghostEl$1, options.ghostClass, false);
      toggleClass$1(ghostEl$1, options.fallbackClass, true);
      toggleClass$1(ghostEl$1, options.dragClass, true);
      css$1(ghostEl$1, 'transition', '');
      css$1(ghostEl$1, 'transform', '');
      css$1(ghostEl$1, 'box-sizing', 'border-box');
      css$1(ghostEl$1, 'margin', 0);
      css$1(ghostEl$1, 'top', rect.top);
      css$1(ghostEl$1, 'left', rect.left);
      css$1(ghostEl$1, 'width', rect.width);
      css$1(ghostEl$1, 'height', rect.height);
      css$1(ghostEl$1, 'opacity', '0.8');
      css$1(ghostEl$1, 'position', PositionGhostAbsolutely$1 ? 'absolute' : 'fixed');
      css$1(ghostEl$1, 'zIndex', '100000');
      css$1(ghostEl$1, 'pointerEvents', 'none');
      Sortable$1.ghost = ghostEl$1;
      container.appendChild(ghostEl$1); // Set transform-origin

      css$1(ghostEl$1, 'transform-origin', tapDistanceLeft$1 / parseInt(ghostEl$1.style.width) * 100 + '% ' + tapDistanceTop$1 / parseInt(ghostEl$1.style.height) * 100 + '%');
    }
  },
  _onDragStart: function _onDragStart(
  /**Event*/
  evt,
  /**boolean*/
  fallback) {
    var _this = this;

    var dataTransfer = evt.dataTransfer;
    var options = _this.options;
    pluginEvent$1('dragStart', this, {
      evt: evt
    });

    if (Sortable$1.eventCanceled) {
      this._onDrop();

      return;
    }

    pluginEvent$1('setupClone', this);

    if (!Sortable$1.eventCanceled) {
      cloneEl$1 = clone$2(dragEl$1);
      cloneEl$1.removeAttribute("id");
      cloneEl$1.draggable = false;
      cloneEl$1.style['will-change'] = '';

      this._hideClone();

      toggleClass$1(cloneEl$1, this.options.chosenClass, false);
      Sortable$1.clone = cloneEl$1;
    } // #1143: IFrame support workaround


    _this.cloneId = _nextTick$1(function () {
      pluginEvent$1('clone', _this);
      if (Sortable$1.eventCanceled) return;

      if (!_this.options.removeCloneOnHide) {
        rootEl$1.insertBefore(cloneEl$1, dragEl$1);
      }

      _this._hideClone();

      _dispatchEvent$1({
        sortable: _this,
        name: 'clone'
      });
    });
    !fallback && toggleClass$1(dragEl$1, options.dragClass, true); // Set proper drop events

    if (fallback) {
      ignoreNextClick$1 = true;
      _this._loopId = setInterval(_this._emulateDragOver, 50);
    } else {
      // Undo what was set in _prepareDragStart before drag started
      off$1(document, 'mouseup', _this._onDrop);
      off$1(document, 'touchend', _this._onDrop);
      off$1(document, 'touchcancel', _this._onDrop);

      if (dataTransfer) {
        dataTransfer.effectAllowed = 'move';
        options.setData && options.setData.call(_this, dataTransfer, dragEl$1);
      }

      on$1(document, 'drop', _this); // #1276 fix:

      css$1(dragEl$1, 'transform', 'translateZ(0)');
    }

    awaitingDragStarted$1 = true;
    _this._dragStartId = _nextTick$1(_this._dragStarted.bind(_this, fallback, evt));
    on$1(document, 'selectstart', _this);
    moved$1 = true;

    if (Safari$1) {
      css$1(document.body, 'user-select', 'none');
    }
  },
  // Returns true - if no further action is needed (either inserted or another condition)
  _onDragOver: function _onDragOver(
  /**Event*/
  evt) {
    var el = this.el,
        target = evt.target,
        dragRect,
        targetRect,
        revert,
        options = this.options,
        group = options.group,
        activeSortable = Sortable$1.active,
        isOwner = activeGroup$1 === group,
        canSort = options.sort,
        fromSortable = putSortable$1 || activeSortable,
        vertical,
        _this = this,
        completedFired = false;

    if (_silent$1) return;

    function dragOverEvent(name, extra) {
      pluginEvent$1(name, _this, _objectSpread2$1({
        evt: evt,
        isOwner: isOwner,
        axis: vertical ? 'vertical' : 'horizontal',
        revert: revert,
        dragRect: dragRect,
        targetRect: targetRect,
        canSort: canSort,
        fromSortable: fromSortable,
        target: target,
        completed: completed,
        onMove: function onMove(target, after) {
          return _onMove$1(rootEl$1, el, dragEl$1, dragRect, target, getRect$1(target), evt, after);
        },
        changed: changed
      }, extra));
    } // Capture animation state


    function capture() {
      dragOverEvent('dragOverAnimationCapture');

      _this.captureAnimationState();

      if (_this !== fromSortable) {
        fromSortable.captureAnimationState();
      }
    } // Return invocation when dragEl is inserted (or completed)


    function completed(insertion) {
      dragOverEvent('dragOverCompleted', {
        insertion: insertion
      });

      if (insertion) {
        // Clones must be hidden before folding animation to capture dragRectAbsolute properly
        if (isOwner) {
          activeSortable._hideClone();
        } else {
          activeSortable._showClone(_this);
        }

        if (_this !== fromSortable) {
          // Set ghost class to new sortable's ghost class
          toggleClass$1(dragEl$1, putSortable$1 ? putSortable$1.options.ghostClass : activeSortable.options.ghostClass, false);
          toggleClass$1(dragEl$1, options.ghostClass, true);
        }

        if (putSortable$1 !== _this && _this !== Sortable$1.active) {
          putSortable$1 = _this;
        } else if (_this === Sortable$1.active && putSortable$1) {
          putSortable$1 = null;
        } // Animation


        if (fromSortable === _this) {
          _this._ignoreWhileAnimating = target;
        }

        _this.animateAll(function () {
          dragOverEvent('dragOverAnimationComplete');
          _this._ignoreWhileAnimating = null;
        });

        if (_this !== fromSortable) {
          fromSortable.animateAll();
          fromSortable._ignoreWhileAnimating = null;
        }
      } // Null lastTarget if it is not inside a previously swapped element


      if (target === dragEl$1 && !dragEl$1.animated || target === el && !target.animated) {
        lastTarget$1 = null;
      } // no bubbling and not fallback


      if (!options.dragoverBubble && !evt.rootEl && target !== document) {
        dragEl$1.parentNode[expando$1]._isOutsideThisEl(evt.target); // Do not detect for empty insert if already inserted


        !insertion && nearestEmptyInsertDetectEvent$1(evt);
      }

      !options.dragoverBubble && evt.stopPropagation && evt.stopPropagation();
      return completedFired = true;
    } // Call when dragEl has been inserted


    function changed() {
      newIndex$1 = index$1(dragEl$1);
      newDraggableIndex$1 = index$1(dragEl$1, options.draggable);

      _dispatchEvent$1({
        sortable: _this,
        name: 'change',
        toEl: el,
        newIndex: newIndex$1,
        newDraggableIndex: newDraggableIndex$1,
        originalEvent: evt
      });
    }

    if (evt.preventDefault !== void 0) {
      evt.cancelable && evt.preventDefault();
    }

    target = closest$2(target, options.draggable, el, true);
    dragOverEvent('dragOver');
    if (Sortable$1.eventCanceled) return completedFired;

    if (dragEl$1.contains(evt.target) || target.animated && target.animatingX && target.animatingY || _this._ignoreWhileAnimating === target) {
      return completed(false);
    }

    ignoreNextClick$1 = false;

    if (activeSortable && !options.disabled && (isOwner ? canSort || (revert = parentEl$1 !== rootEl$1) // Reverting item into the original list
    : putSortable$1 === this || (this.lastPutMode = activeGroup$1.checkPull(this, activeSortable, dragEl$1, evt)) && group.checkPut(this, activeSortable, dragEl$1, evt))) {
      vertical = this._getDirection(evt, target) === 'vertical';
      dragRect = getRect$1(dragEl$1);
      dragOverEvent('dragOverValid');
      if (Sortable$1.eventCanceled) return completedFired;

      if (revert) {
        parentEl$1 = rootEl$1; // actualization

        capture();

        this._hideClone();

        dragOverEvent('revert');

        if (!Sortable$1.eventCanceled) {
          if (nextEl$1) {
            rootEl$1.insertBefore(dragEl$1, nextEl$1);
          } else {
            rootEl$1.appendChild(dragEl$1);
          }
        }

        return completed(true);
      }

      var elLastChild = lastChild$1(el, options.draggable);

      if (!elLastChild || _ghostIsLast$1(evt, vertical, this) && !elLastChild.animated) {
        // Insert to end of list
        // If already at end of list: Do not insert
        if (elLastChild === dragEl$1) {
          return completed(false);
        } // if there is a last element, it is the target


        if (elLastChild && el === evt.target) {
          target = elLastChild;
        }

        if (target) {
          targetRect = getRect$1(target);
        }

        if (_onMove$1(rootEl$1, el, dragEl$1, dragRect, target, targetRect, evt, !!target) !== false) {
          capture();

          if (elLastChild && elLastChild.nextSibling) {
            // the last draggable element is not the last node
            el.insertBefore(dragEl$1, elLastChild.nextSibling);
          } else {
            el.appendChild(dragEl$1);
          }

          parentEl$1 = el; // actualization

          changed();
          return completed(true);
        }
      } else if (elLastChild && _ghostIsFirst(evt, vertical, this)) {
        // Insert to start of list
        var firstChild = getChild$1(el, 0, options, true);

        if (firstChild === dragEl$1) {
          return completed(false);
        }

        target = firstChild;
        targetRect = getRect$1(target);

        if (_onMove$1(rootEl$1, el, dragEl$1, dragRect, target, targetRect, evt, false) !== false) {
          capture();
          el.insertBefore(dragEl$1, firstChild);
          parentEl$1 = el; // actualization

          changed();
          return completed(true);
        }
      } else if (target.parentNode === el) {
        targetRect = getRect$1(target);
        var direction = 0,
            targetBeforeFirstSwap,
            differentLevel = dragEl$1.parentNode !== el,
            differentRowCol = !_dragElInRowColumn$1(dragEl$1.animated && dragEl$1.toRect || dragRect, target.animated && target.toRect || targetRect, vertical),
            side1 = vertical ? 'top' : 'left',
            scrolledPastTop = isScrolledPast$1(target, 'top', 'top') || isScrolledPast$1(dragEl$1, 'top', 'top'),
            scrollBefore = scrolledPastTop ? scrolledPastTop.scrollTop : void 0;

        if (lastTarget$1 !== target) {
          targetBeforeFirstSwap = targetRect[side1];
          pastFirstInvertThresh$1 = false;
          isCircumstantialInvert$1 = !differentRowCol && options.invertSwap || differentLevel;
        }

        direction = _getSwapDirection$1(evt, target, targetRect, vertical, differentRowCol ? 1 : options.swapThreshold, options.invertedSwapThreshold == null ? options.swapThreshold : options.invertedSwapThreshold, isCircumstantialInvert$1, lastTarget$1 === target);
        var sibling;

        if (direction !== 0) {
          // Check if target is beside dragEl in respective direction (ignoring hidden elements)
          var dragIndex = index$1(dragEl$1);

          do {
            dragIndex -= direction;
            sibling = parentEl$1.children[dragIndex];
          } while (sibling && (css$1(sibling, 'display') === 'none' || sibling === ghostEl$1));
        } // If dragEl is already beside target: Do not insert


        if (direction === 0 || sibling === target) {
          return completed(false);
        }

        lastTarget$1 = target;
        lastDirection$1 = direction;
        var nextSibling = target.nextElementSibling,
            after = false;
        after = direction === 1;

        var moveVector = _onMove$1(rootEl$1, el, dragEl$1, dragRect, target, targetRect, evt, after);

        if (moveVector !== false) {
          if (moveVector === 1 || moveVector === -1) {
            after = moveVector === 1;
          }

          _silent$1 = true;
          setTimeout(_unsilent$1, 30);
          capture();

          if (after && !nextSibling) {
            el.appendChild(dragEl$1);
          } else {
            target.parentNode.insertBefore(dragEl$1, after ? nextSibling : target);
          } // Undo chrome's scroll adjustment (has no effect on other browsers)


          if (scrolledPastTop) {
            scrollBy$1(scrolledPastTop, 0, scrollBefore - scrolledPastTop.scrollTop);
          }

          parentEl$1 = dragEl$1.parentNode; // actualization
          // must be done before animation

          if (targetBeforeFirstSwap !== undefined && !isCircumstantialInvert$1) {
            targetMoveDistance$1 = Math.abs(targetBeforeFirstSwap - getRect$1(target)[side1]);
          }

          changed();
          return completed(true);
        }
      }

      if (el.contains(dragEl$1)) {
        return completed(false);
      }
    }

    return false;
  },
  _ignoreWhileAnimating: null,
  _offMoveEvents: function _offMoveEvents() {
    off$1(document, 'mousemove', this._onTouchMove);
    off$1(document, 'touchmove', this._onTouchMove);
    off$1(document, 'pointermove', this._onTouchMove);
    off$1(document, 'dragover', nearestEmptyInsertDetectEvent$1);
    off$1(document, 'mousemove', nearestEmptyInsertDetectEvent$1);
    off$1(document, 'touchmove', nearestEmptyInsertDetectEvent$1);
  },
  _offUpEvents: function _offUpEvents() {
    var ownerDocument = this.el.ownerDocument;
    off$1(ownerDocument, 'mouseup', this._onDrop);
    off$1(ownerDocument, 'touchend', this._onDrop);
    off$1(ownerDocument, 'pointerup', this._onDrop);
    off$1(ownerDocument, 'touchcancel', this._onDrop);
    off$1(document, 'selectstart', this);
  },
  _onDrop: function _onDrop(
  /**Event*/
  evt) {
    var el = this.el,
        options = this.options; // Get the index of the dragged element within its parent

    newIndex$1 = index$1(dragEl$1);
    newDraggableIndex$1 = index$1(dragEl$1, options.draggable);
    pluginEvent$1('drop', this, {
      evt: evt
    });
    parentEl$1 = dragEl$1 && dragEl$1.parentNode; // Get again after plugin event

    newIndex$1 = index$1(dragEl$1);
    newDraggableIndex$1 = index$1(dragEl$1, options.draggable);

    if (Sortable$1.eventCanceled) {
      this._nulling();

      return;
    }

    awaitingDragStarted$1 = false;
    isCircumstantialInvert$1 = false;
    pastFirstInvertThresh$1 = false;
    clearInterval(this._loopId);
    clearTimeout(this._dragStartTimer);

    _cancelNextTick$1(this.cloneId);

    _cancelNextTick$1(this._dragStartId); // Unbind events


    if (this.nativeDraggable) {
      off$1(document, 'drop', this);
      off$1(el, 'dragstart', this._onDragStart);
    }

    this._offMoveEvents();

    this._offUpEvents();

    if (Safari$1) {
      css$1(document.body, 'user-select', '');
    }

    css$1(dragEl$1, 'transform', '');

    if (evt) {
      if (moved$1) {
        evt.cancelable && evt.preventDefault();
        !options.dropBubble && evt.stopPropagation();
      }

      ghostEl$1 && ghostEl$1.parentNode && ghostEl$1.parentNode.removeChild(ghostEl$1);

      if (rootEl$1 === parentEl$1 || putSortable$1 && putSortable$1.lastPutMode !== 'clone') {
        // Remove clone(s)
        cloneEl$1 && cloneEl$1.parentNode && cloneEl$1.parentNode.removeChild(cloneEl$1);
      }

      if (dragEl$1) {
        if (this.nativeDraggable) {
          off$1(dragEl$1, 'dragend', this);
        }

        _disableDraggable$1(dragEl$1);

        dragEl$1.style['will-change'] = ''; // Remove classes
        // ghostClass is added in dragStarted

        if (moved$1 && !awaitingDragStarted$1) {
          toggleClass$1(dragEl$1, putSortable$1 ? putSortable$1.options.ghostClass : this.options.ghostClass, false);
        }

        toggleClass$1(dragEl$1, this.options.chosenClass, false); // Drag stop event

        _dispatchEvent$1({
          sortable: this,
          name: 'unchoose',
          toEl: parentEl$1,
          newIndex: null,
          newDraggableIndex: null,
          originalEvent: evt
        });

        if (rootEl$1 !== parentEl$1) {
          if (newIndex$1 >= 0) {
            // Add event
            _dispatchEvent$1({
              rootEl: parentEl$1,
              name: 'add',
              toEl: parentEl$1,
              fromEl: rootEl$1,
              originalEvent: evt
            }); // Remove event


            _dispatchEvent$1({
              sortable: this,
              name: 'remove',
              toEl: parentEl$1,
              originalEvent: evt
            }); // drag from one list and drop into another


            _dispatchEvent$1({
              rootEl: parentEl$1,
              name: 'sort',
              toEl: parentEl$1,
              fromEl: rootEl$1,
              originalEvent: evt
            });

            _dispatchEvent$1({
              sortable: this,
              name: 'sort',
              toEl: parentEl$1,
              originalEvent: evt
            });
          }

          putSortable$1 && putSortable$1.save();
        } else {
          if (newIndex$1 !== oldIndex$1) {
            if (newIndex$1 >= 0) {
              // drag & drop within the same list
              _dispatchEvent$1({
                sortable: this,
                name: 'update',
                toEl: parentEl$1,
                originalEvent: evt
              });

              _dispatchEvent$1({
                sortable: this,
                name: 'sort',
                toEl: parentEl$1,
                originalEvent: evt
              });
            }
          }
        }

        if (Sortable$1.active) {
          /* jshint eqnull:true */
          if (newIndex$1 == null || newIndex$1 === -1) {
            newIndex$1 = oldIndex$1;
            newDraggableIndex$1 = oldDraggableIndex$1;
          }

          _dispatchEvent$1({
            sortable: this,
            name: 'end',
            toEl: parentEl$1,
            originalEvent: evt
          }); // Save sorting


          this.save();
        }
      }
    }

    this._nulling();
  },
  _nulling: function _nulling() {
    pluginEvent$1('nulling', this);
    rootEl$1 = dragEl$1 = parentEl$1 = ghostEl$1 = nextEl$1 = cloneEl$1 = lastDownEl$1 = cloneHidden$1 = tapEvt$1 = touchEvt$2 = moved$1 = newIndex$1 = newDraggableIndex$1 = oldIndex$1 = oldDraggableIndex$1 = lastTarget$1 = lastDirection$1 = putSortable$1 = activeGroup$1 = Sortable$1.dragged = Sortable$1.ghost = Sortable$1.clone = Sortable$1.active = null;
    savedInputChecked$1.forEach(function (el) {
      el.checked = true;
    });
    savedInputChecked$1.length = lastDx$1 = lastDy$1 = 0;
  },
  handleEvent: function handleEvent(
  /**Event*/
  evt) {
    switch (evt.type) {
      case 'drop':
      case 'dragend':
        this._onDrop(evt);

        break;

      case 'dragenter':
      case 'dragover':
        if (dragEl$1) {
          this._onDragOver(evt);

          _globalDragOver$1(evt);
        }

        break;

      case 'selectstart':
        evt.preventDefault();
        break;
    }
  },

  /**
   * Serializes the item into an array of string.
   * @returns {String[]}
   */
  toArray: function toArray() {
    var order = [],
        el,
        children = this.el.children,
        i = 0,
        n = children.length,
        options = this.options;

    for (; i < n; i++) {
      el = children[i];

      if (closest$2(el, options.draggable, this.el, false)) {
        order.push(el.getAttribute(options.dataIdAttr) || _generateId$1(el));
      }
    }

    return order;
  },

  /**
   * Sorts the elements according to the array.
   * @param  {String[]}  order  order of the items
   */
  sort: function sort(order, useAnimation) {
    var items = {},
        rootEl = this.el;
    this.toArray().forEach(function (id, i) {
      var el = rootEl.children[i];

      if (closest$2(el, this.options.draggable, rootEl, false)) {
        items[id] = el;
      }
    }, this);
    useAnimation && this.captureAnimationState();
    order.forEach(function (id) {
      if (items[id]) {
        rootEl.removeChild(items[id]);
        rootEl.appendChild(items[id]);
      }
    });
    useAnimation && this.animateAll();
  },

  /**
   * Save the current sorting
   */
  save: function save() {
    var store = this.options.store;
    store && store.set && store.set(this);
  },

  /**
   * For each element in the set, get the first element that matches the selector by testing the element itself and traversing up through its ancestors in the DOM tree.
   * @param   {HTMLElement}  el
   * @param   {String}       [selector]  default: `options.draggable`
   * @returns {HTMLElement|null}
   */
  closest: function closest$1(el, selector) {
    return closest$2(el, selector || this.options.draggable, this.el, false);
  },

  /**
   * Set/get option
   * @param   {string} name
   * @param   {*}      [value]
   * @returns {*}
   */
  option: function option(name, value) {
    var options = this.options;

    if (value === void 0) {
      return options[name];
    } else {
      var modifiedValue = PluginManager$1.modifyOption(this, name, value);

      if (typeof modifiedValue !== 'undefined') {
        options[name] = modifiedValue;
      } else {
        options[name] = value;
      }

      if (name === 'group') {
        _prepareGroup$1(options);
      }
    }
  },

  /**
   * Destroy
   */
  destroy: function destroy() {
    pluginEvent$1('destroy', this);
    var el = this.el;
    el[expando$1] = null;
    off$1(el, 'mousedown', this._onTapStart);
    off$1(el, 'touchstart', this._onTapStart);
    off$1(el, 'pointerdown', this._onTapStart);

    if (this.nativeDraggable) {
      off$1(el, 'dragover', this);
      off$1(el, 'dragenter', this);
    } // Remove draggable attributes


    Array.prototype.forEach.call(el.querySelectorAll('[draggable]'), function (el) {
      el.removeAttribute('draggable');
    });

    this._onDrop();

    this._disableDelayedDragEvents();

    sortables$1.splice(sortables$1.indexOf(this.el), 1);
    this.el = el = null;
  },
  _hideClone: function _hideClone() {
    if (!cloneHidden$1) {
      pluginEvent$1('hideClone', this);
      if (Sortable$1.eventCanceled) return;
      css$1(cloneEl$1, 'display', 'none');

      if (this.options.removeCloneOnHide && cloneEl$1.parentNode) {
        cloneEl$1.parentNode.removeChild(cloneEl$1);
      }

      cloneHidden$1 = true;
    }
  },
  _showClone: function _showClone(putSortable) {
    if (putSortable.lastPutMode !== 'clone') {
      this._hideClone();

      return;
    }

    if (cloneHidden$1) {
      pluginEvent$1('showClone', this);
      if (Sortable$1.eventCanceled) return; // show clone at dragEl or original position

      if (dragEl$1.parentNode == rootEl$1 && !this.options.group.revertClone) {
        rootEl$1.insertBefore(cloneEl$1, dragEl$1);
      } else if (nextEl$1) {
        rootEl$1.insertBefore(cloneEl$1, nextEl$1);
      } else {
        rootEl$1.appendChild(cloneEl$1);
      }

      if (this.options.group.revertClone) {
        this.animate(dragEl$1, cloneEl$1);
      }

      css$1(cloneEl$1, 'display', '');
      cloneHidden$1 = false;
    }
  }
};

function _globalDragOver$1(
/**Event*/
evt) {
  if (evt.dataTransfer) {
    evt.dataTransfer.dropEffect = 'move';
  }

  evt.cancelable && evt.preventDefault();
}

function _onMove$1(fromEl, toEl, dragEl, dragRect, targetEl, targetRect, originalEvent, willInsertAfter) {
  var evt,
      sortable = fromEl[expando$1],
      onMoveFn = sortable.options.onMove,
      retVal; // Support for new CustomEvent feature

  if (window.CustomEvent && !IE11OrLess$1 && !Edge$1) {
    evt = new CustomEvent('move', {
      bubbles: true,
      cancelable: true
    });
  } else {
    evt = document.createEvent('Event');
    evt.initEvent('move', true, true);
  }

  evt.to = toEl;
  evt.from = fromEl;
  evt.dragged = dragEl;
  evt.draggedRect = dragRect;
  evt.related = targetEl || toEl;
  evt.relatedRect = targetRect || getRect$1(toEl);
  evt.willInsertAfter = willInsertAfter;
  evt.originalEvent = originalEvent;
  fromEl.dispatchEvent(evt);

  if (onMoveFn) {
    retVal = onMoveFn.call(sortable, evt, originalEvent);
  }

  return retVal;
}

function _disableDraggable$1(el) {
  el.draggable = false;
}

function _unsilent$1() {
  _silent$1 = false;
}

function _ghostIsFirst(evt, vertical, sortable) {
  var rect = getRect$1(getChild$1(sortable.el, 0, sortable.options, true));
  var spacer = 10;
  return vertical ? evt.clientX < rect.left - spacer || evt.clientY < rect.top && evt.clientX < rect.right : evt.clientY < rect.top - spacer || evt.clientY < rect.bottom && evt.clientX < rect.left;
}

function _ghostIsLast$1(evt, vertical, sortable) {
  var rect = getRect$1(lastChild$1(sortable.el, sortable.options.draggable));
  var spacer = 10;
  return vertical ? evt.clientX > rect.right + spacer || evt.clientX <= rect.right && evt.clientY > rect.bottom && evt.clientX >= rect.left : evt.clientX > rect.right && evt.clientY > rect.top || evt.clientX <= rect.right && evt.clientY > rect.bottom + spacer;
}

function _getSwapDirection$1(evt, target, targetRect, vertical, swapThreshold, invertedSwapThreshold, invertSwap, isLastTarget) {
  var mouseOnAxis = vertical ? evt.clientY : evt.clientX,
      targetLength = vertical ? targetRect.height : targetRect.width,
      targetS1 = vertical ? targetRect.top : targetRect.left,
      targetS2 = vertical ? targetRect.bottom : targetRect.right,
      invert = false;

  if (!invertSwap) {
    // Never invert or create dragEl shadow when target movemenet causes mouse to move past the end of regular swapThreshold
    if (isLastTarget && targetMoveDistance$1 < targetLength * swapThreshold) {
      // multiplied only by swapThreshold because mouse will already be inside target by (1 - threshold) * targetLength / 2
      // check if past first invert threshold on side opposite of lastDirection
      if (!pastFirstInvertThresh$1 && (lastDirection$1 === 1 ? mouseOnAxis > targetS1 + targetLength * invertedSwapThreshold / 2 : mouseOnAxis < targetS2 - targetLength * invertedSwapThreshold / 2)) {
        // past first invert threshold, do not restrict inverted threshold to dragEl shadow
        pastFirstInvertThresh$1 = true;
      }

      if (!pastFirstInvertThresh$1) {
        // dragEl shadow (target move distance shadow)
        if (lastDirection$1 === 1 ? mouseOnAxis < targetS1 + targetMoveDistance$1 // over dragEl shadow
        : mouseOnAxis > targetS2 - targetMoveDistance$1) {
          return -lastDirection$1;
        }
      } else {
        invert = true;
      }
    } else {
      // Regular
      if (mouseOnAxis > targetS1 + targetLength * (1 - swapThreshold) / 2 && mouseOnAxis < targetS2 - targetLength * (1 - swapThreshold) / 2) {
        return _getInsertDirection$1(target);
      }
    }
  }

  invert = invert || invertSwap;

  if (invert) {
    // Invert of regular
    if (mouseOnAxis < targetS1 + targetLength * invertedSwapThreshold / 2 || mouseOnAxis > targetS2 - targetLength * invertedSwapThreshold / 2) {
      return mouseOnAxis > targetS1 + targetLength / 2 ? 1 : -1;
    }
  }

  return 0;
}
/**
 * Gets the direction dragEl must be swapped relative to target in order to make it
 * seem that dragEl has been "inserted" into that element's position
 * @param  {HTMLElement} target       The target whose position dragEl is being inserted at
 * @return {Number}                   Direction dragEl must be swapped
 */


function _getInsertDirection$1(target) {
  if (index$1(dragEl$1) < index$1(target)) {
    return 1;
  } else {
    return -1;
  }
}
/**
 * Generate id
 * @param   {HTMLElement} el
 * @returns {String}
 * @private
 */


function _generateId$1(el) {
  var str = el.tagName + el.className + el.src + el.href + el.textContent,
      i = str.length,
      sum = 0;

  while (i--) {
    sum += str.charCodeAt(i);
  }

  return sum.toString(36);
}

function _saveInputCheckedState$1(root) {
  savedInputChecked$1.length = 0;
  var inputs = root.getElementsByTagName('input');
  var idx = inputs.length;

  while (idx--) {
    var el = inputs[idx];
    el.checked && savedInputChecked$1.push(el);
  }
}

function _nextTick$1(fn) {
  return setTimeout(fn, 0);
}

function _cancelNextTick$1(id) {
  return clearTimeout(id);
} // Fixed #973:


if (documentExists$1) {
  on$1(document, 'touchmove', function (evt) {
    if ((Sortable$1.active || awaitingDragStarted$1) && evt.cancelable) {
      evt.preventDefault();
    }
  });
} // Export utils


Sortable$1.utils = {
  on: on$1,
  off: off$1,
  css: css$1,
  find: find$3,
  is: function is(el, selector) {
    return !!closest$2(el, selector, el, false);
  },
  extend: extend$1,
  throttle: throttle$1,
  closest: closest$2,
  toggleClass: toggleClass$1,
  clone: clone$2,
  index: index$1,
  nextTick: _nextTick$1,
  cancelNextTick: _cancelNextTick$1,
  detectDirection: _detectDirection$1,
  getChild: getChild$1
};
/**
 * Get the Sortable instance of an element
 * @param  {HTMLElement} element The element
 * @return {Sortable|undefined}         The instance of Sortable
 */

Sortable$1.get = function (element) {
  return element[expando$1];
};
/**
 * Mount a plugin to Sortable
 * @param  {...SortablePlugin|SortablePlugin[]} plugins       Plugins being mounted
 */


Sortable$1.mount = function () {
  for (var _len = arguments.length, plugins = new Array(_len), _key = 0; _key < _len; _key++) {
    plugins[_key] = arguments[_key];
  }

  if (plugins[0].constructor === Array) plugins = plugins[0];
  plugins.forEach(function (plugin) {
    if (!plugin.prototype || !plugin.prototype.constructor) {
      throw "Sortable: Mounted plugin must be a constructor function, not ".concat({}.toString.call(plugin));
    }

    if (plugin.utils) Sortable$1.utils = _objectSpread2$1(_objectSpread2$1({}, Sortable$1.utils), plugin.utils);
    PluginManager$1.mount(plugin);
  });
};
/**
 * Create sortable instance
 * @param {HTMLElement}  el
 * @param {Object}      [options]
 */


Sortable$1.create = function (el, options) {
  return new Sortable$1(el, options);
}; // Export


Sortable$1.version = version$2;

var autoScrolls$1 = [],
    scrollEl$1,
    scrollRootEl$1,
    scrolling$1 = false,
    lastAutoScrollX$1,
    lastAutoScrollY$1,
    touchEvt$1$1,
    pointerElemChangedInterval$1;

function AutoScrollPlugin$1() {
  function AutoScroll() {
    this.defaults = {
      scroll: true,
      forceAutoScrollFallback: false,
      scrollSensitivity: 30,
      scrollSpeed: 10,
      bubbleScroll: true
    }; // Bind all private methods

    for (var fn in this) {
      if (fn.charAt(0) === '_' && typeof this[fn] === 'function') {
        this[fn] = this[fn].bind(this);
      }
    }
  }

  AutoScroll.prototype = {
    dragStarted: function dragStarted(_ref) {
      var originalEvent = _ref.originalEvent;

      if (this.sortable.nativeDraggable) {
        on$1(document, 'dragover', this._handleAutoScroll);
      } else {
        if (this.options.supportPointer) {
          on$1(document, 'pointermove', this._handleFallbackAutoScroll);
        } else if (originalEvent.touches) {
          on$1(document, 'touchmove', this._handleFallbackAutoScroll);
        } else {
          on$1(document, 'mousemove', this._handleFallbackAutoScroll);
        }
      }
    },
    dragOverCompleted: function dragOverCompleted(_ref2) {
      var originalEvent = _ref2.originalEvent;

      // For when bubbling is canceled and using fallback (fallback 'touchmove' always reached)
      if (!this.options.dragOverBubble && !originalEvent.rootEl) {
        this._handleAutoScroll(originalEvent);
      }
    },
    drop: function drop() {
      if (this.sortable.nativeDraggable) {
        off$1(document, 'dragover', this._handleAutoScroll);
      } else {
        off$1(document, 'pointermove', this._handleFallbackAutoScroll);
        off$1(document, 'touchmove', this._handleFallbackAutoScroll);
        off$1(document, 'mousemove', this._handleFallbackAutoScroll);
      }

      clearPointerElemChangedInterval$1();
      clearAutoScrolls$1();
      cancelThrottle$1();
    },
    nulling: function nulling() {
      touchEvt$1$1 = scrollRootEl$1 = scrollEl$1 = scrolling$1 = pointerElemChangedInterval$1 = lastAutoScrollX$1 = lastAutoScrollY$1 = null;
      autoScrolls$1.length = 0;
    },
    _handleFallbackAutoScroll: function _handleFallbackAutoScroll(evt) {
      this._handleAutoScroll(evt, true);
    },
    _handleAutoScroll: function _handleAutoScroll(evt, fallback) {
      var _this = this;

      var x = (evt.touches ? evt.touches[0] : evt).clientX,
          y = (evt.touches ? evt.touches[0] : evt).clientY,
          elem = document.elementFromPoint(x, y);
      touchEvt$1$1 = evt; // IE does not seem to have native autoscroll,
      // Edge's autoscroll seems too conditional,
      // MACOS Safari does not have autoscroll,
      // Firefox and Chrome are good

      if (fallback || this.options.forceAutoScrollFallback || Edge$1 || IE11OrLess$1 || Safari$1) {
        autoScroll$1(evt, this.options, elem, fallback); // Listener for pointer element change

        var ogElemScroller = getParentAutoScrollElement$1(elem, true);

        if (scrolling$1 && (!pointerElemChangedInterval$1 || x !== lastAutoScrollX$1 || y !== lastAutoScrollY$1)) {
          pointerElemChangedInterval$1 && clearPointerElemChangedInterval$1(); // Detect for pointer elem change, emulating native DnD behaviour

          pointerElemChangedInterval$1 = setInterval(function () {
            var newElem = getParentAutoScrollElement$1(document.elementFromPoint(x, y), true);

            if (newElem !== ogElemScroller) {
              ogElemScroller = newElem;
              clearAutoScrolls$1();
            }

            autoScroll$1(evt, _this.options, newElem, fallback);
          }, 10);
          lastAutoScrollX$1 = x;
          lastAutoScrollY$1 = y;
        }
      } else {
        // if DnD is enabled (and browser has good autoscrolling), first autoscroll will already scroll, so get parent autoscroll of first autoscroll
        if (!this.options.bubbleScroll || getParentAutoScrollElement$1(elem, true) === getWindowScrollingElement$1()) {
          clearAutoScrolls$1();
          return;
        }

        autoScroll$1(evt, this.options, getParentAutoScrollElement$1(elem, false), false);
      }
    }
  };
  return _extends$3(AutoScroll, {
    pluginName: 'scroll',
    initializeByDefault: true
  });
}

function clearAutoScrolls$1() {
  autoScrolls$1.forEach(function (autoScroll) {
    clearInterval(autoScroll.pid);
  });
  autoScrolls$1 = [];
}

function clearPointerElemChangedInterval$1() {
  clearInterval(pointerElemChangedInterval$1);
}

var autoScroll$1 = throttle$1(function (evt, options, rootEl, isFallback) {
  // Bug: https://bugzilla.mozilla.org/show_bug.cgi?id=505521
  if (!options.scroll) return;
  var x = (evt.touches ? evt.touches[0] : evt).clientX,
      y = (evt.touches ? evt.touches[0] : evt).clientY,
      sens = options.scrollSensitivity,
      speed = options.scrollSpeed,
      winScroller = getWindowScrollingElement$1();
  var scrollThisInstance = false,
      scrollCustomFn; // New scroll root, set scrollEl

  if (scrollRootEl$1 !== rootEl) {
    scrollRootEl$1 = rootEl;
    clearAutoScrolls$1();
    scrollEl$1 = options.scroll;
    scrollCustomFn = options.scrollFn;

    if (scrollEl$1 === true) {
      scrollEl$1 = getParentAutoScrollElement$1(rootEl, true);
    }
  }

  var layersOut = 0;
  var currentParent = scrollEl$1;

  do {
    var el = currentParent,
        rect = getRect$1(el),
        top = rect.top,
        bottom = rect.bottom,
        left = rect.left,
        right = rect.right,
        width = rect.width,
        height = rect.height,
        canScrollX = void 0,
        canScrollY = void 0,
        scrollWidth = el.scrollWidth,
        scrollHeight = el.scrollHeight,
        elCSS = css$1(el),
        scrollPosX = el.scrollLeft,
        scrollPosY = el.scrollTop;

    if (el === winScroller) {
      canScrollX = width < scrollWidth && (elCSS.overflowX === 'auto' || elCSS.overflowX === 'scroll' || elCSS.overflowX === 'visible');
      canScrollY = height < scrollHeight && (elCSS.overflowY === 'auto' || elCSS.overflowY === 'scroll' || elCSS.overflowY === 'visible');
    } else {
      canScrollX = width < scrollWidth && (elCSS.overflowX === 'auto' || elCSS.overflowX === 'scroll');
      canScrollY = height < scrollHeight && (elCSS.overflowY === 'auto' || elCSS.overflowY === 'scroll');
    }

    var vx = canScrollX && (Math.abs(right - x) <= sens && scrollPosX + width < scrollWidth) - (Math.abs(left - x) <= sens && !!scrollPosX);
    var vy = canScrollY && (Math.abs(bottom - y) <= sens && scrollPosY + height < scrollHeight) - (Math.abs(top - y) <= sens && !!scrollPosY);

    if (!autoScrolls$1[layersOut]) {
      for (var i = 0; i <= layersOut; i++) {
        if (!autoScrolls$1[i]) {
          autoScrolls$1[i] = {};
        }
      }
    }

    if (autoScrolls$1[layersOut].vx != vx || autoScrolls$1[layersOut].vy != vy || autoScrolls$1[layersOut].el !== el) {
      autoScrolls$1[layersOut].el = el;
      autoScrolls$1[layersOut].vx = vx;
      autoScrolls$1[layersOut].vy = vy;
      clearInterval(autoScrolls$1[layersOut].pid);

      if (vx != 0 || vy != 0) {
        scrollThisInstance = true;
        /* jshint loopfunc:true */

        autoScrolls$1[layersOut].pid = setInterval(function () {
          // emulate drag over during autoscroll (fallback), emulating native DnD behaviour
          if (isFallback && this.layer === 0) {
            Sortable$1.active._onTouchMove(touchEvt$1$1); // To move ghost if it is positioned absolutely

          }

          var scrollOffsetY = autoScrolls$1[this.layer].vy ? autoScrolls$1[this.layer].vy * speed : 0;
          var scrollOffsetX = autoScrolls$1[this.layer].vx ? autoScrolls$1[this.layer].vx * speed : 0;

          if (typeof scrollCustomFn === 'function') {
            if (scrollCustomFn.call(Sortable$1.dragged.parentNode[expando$1], scrollOffsetX, scrollOffsetY, evt, touchEvt$1$1, autoScrolls$1[this.layer].el) !== 'continue') {
              return;
            }
          }

          scrollBy$1(autoScrolls$1[this.layer].el, scrollOffsetX, scrollOffsetY);
        }.bind({
          layer: layersOut
        }), 24);
      }
    }

    layersOut++;
  } while (options.bubbleScroll && currentParent !== winScroller && (currentParent = getParentAutoScrollElement$1(currentParent, false)));

  scrolling$1 = scrollThisInstance; // in case another function catches scrolling as false in between when it is not
}, 30);

var drop$1 = function drop(_ref) {
  var originalEvent = _ref.originalEvent,
      putSortable = _ref.putSortable,
      dragEl = _ref.dragEl,
      activeSortable = _ref.activeSortable,
      dispatchSortableEvent = _ref.dispatchSortableEvent,
      hideGhostForTarget = _ref.hideGhostForTarget,
      unhideGhostForTarget = _ref.unhideGhostForTarget;
  if (!originalEvent) return;
  var toSortable = putSortable || activeSortable;
  hideGhostForTarget();
  var touch = originalEvent.changedTouches && originalEvent.changedTouches.length ? originalEvent.changedTouches[0] : originalEvent;
  var target = document.elementFromPoint(touch.clientX, touch.clientY);
  unhideGhostForTarget();

  if (toSortable && !toSortable.el.contains(target)) {
    dispatchSortableEvent('spill');
    this.onSpill({
      dragEl: dragEl,
      putSortable: putSortable
    });
  }
};

function Revert$1() {}

Revert$1.prototype = {
  startIndex: null,
  dragStart: function dragStart(_ref2) {
    var oldDraggableIndex = _ref2.oldDraggableIndex;
    this.startIndex = oldDraggableIndex;
  },
  onSpill: function onSpill(_ref3) {
    var dragEl = _ref3.dragEl,
        putSortable = _ref3.putSortable;
    this.sortable.captureAnimationState();

    if (putSortable) {
      putSortable.captureAnimationState();
    }

    var nextSibling = getChild$1(this.sortable.el, this.startIndex, this.options);

    if (nextSibling) {
      this.sortable.el.insertBefore(dragEl, nextSibling);
    } else {
      this.sortable.el.appendChild(dragEl);
    }

    this.sortable.animateAll();

    if (putSortable) {
      putSortable.animateAll();
    }
  },
  drop: drop$1
};

_extends$3(Revert$1, {
  pluginName: 'revertOnSpill'
});

function Remove$1() {}

Remove$1.prototype = {
  onSpill: function onSpill(_ref4) {
    var dragEl = _ref4.dragEl,
        putSortable = _ref4.putSortable;
    var parentSortable = putSortable || this.sortable;
    parentSortable.captureAnimationState();
    dragEl.parentNode && dragEl.parentNode.removeChild(dragEl);
    parentSortable.animateAll();
  },
  drop: drop$1
};

_extends$3(Remove$1, {
  pluginName: 'removeOnSpill'
});

Sortable$1.mount(new AutoScrollPlugin$1());
Sortable$1.mount(Remove$1, Revert$1);

/* eslint-disable no-prototype-builtins */

var dragDrop = {
  install(Vue, _options) {
    const createSortable = (el, sortableOptions) => {
      Sortable$1.create(el, { ...sortableOptions });
    };

    const setDraggable = (el, binding) => {
      const dragElement = el.querySelector(binding.value.queryEl) || el;
      const sortableOptions = binding.value?.options || {};
      createSortable(dragElement, sortableOptions);
    };

    Vue.directive('me-drag-drop', {
      bind(el, binding) {
        if (binding.hasOwnProperty('value') && !binding.value) return

        setDraggable(el, binding);
        el.isDraggable = true;
      },

      componentUpdated(el, binding, vnode) {
        if (binding.hasOwnProperty('value') && !binding.value && el.isDraggable) {
          el.isDraggable = false;
          return
        }

        if (binding.hasOwnProperty('value') && !binding.value) return

        if (!el.isDraggable) {
          setDraggable(el, binding);
          el.isDraggable = true;
        }
      }
    });
  }
};

/*! @license DOMPurify 3.0.1 | (c) Cure53 and other contributors | Released under the Apache license 2.0 and Mozilla Public License 2.0 | github.com/cure53/DOMPurify/blob/3.0.1/LICENSE */

function _typeof$7(obj) {
  "@babel/helpers - typeof";

  return _typeof$7 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof$7(obj);
}

function _setPrototypeOf$2(o, p) {
  _setPrototypeOf$2 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf$2(o, p);
}

function _isNativeReflectConstruct$2() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct$1(Parent, args, Class) {
  if (_isNativeReflectConstruct$2()) {
    _construct$1 = Reflect.construct;
  } else {
    _construct$1 = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf$2(instance, Class.prototype);
      return instance;
    };
  }

  return _construct$1.apply(null, arguments);
}

function _slicedToArray$2(arr, i) {
  return _arrayWithHoles$2(arr) || _iterableToArrayLimit$2(arr, i) || _unsupportedIterableToArray$5(arr, i) || _nonIterableRest$2();
}

function _toConsumableArray$6(arr) {
  return _arrayWithoutHoles$6(arr) || _iterableToArray$6(arr) || _unsupportedIterableToArray$5(arr) || _nonIterableSpread$6();
}

function _arrayWithoutHoles$6(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$5(arr);
}

function _arrayWithHoles$2(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray$6(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit$2(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray$5(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$5(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$5(o, minLen);
}

function _arrayLikeToArray$5(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread$6() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest$2() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _createForOfIteratorHelper(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];

  if (!it) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray$5(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;

      var F = function () {};

      return {
        s: F,
        n: function () {
          if (i >= o.length) return {
            done: true
          };
          return {
            done: false,
            value: o[i++]
          };
        },
        e: function (e) {
          throw e;
        },
        f: F
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  var normalCompletion = true,
      didErr = false,
      err;
  return {
    s: function () {
      it = it.call(o);
    },
    n: function () {
      var step = it.next();
      normalCompletion = step.done;
      return step;
    },
    e: function (e) {
      didErr = true;
      err = e;
    },
    f: function () {
      try {
        if (!normalCompletion && it.return != null) it.return();
      } finally {
        if (didErr) throw err;
      }
    }
  };
}

var entries = Object.entries,
    setPrototypeOf = Object.setPrototypeOf,
    isFrozen = Object.isFrozen,
    getPrototypeOf = Object.getPrototypeOf,
    getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
var freeze$2 = Object.freeze,
    seal = Object.seal,
    create$1 = Object.create; // eslint-disable-line import/no-mutable-exports

var _ref = typeof Reflect !== 'undefined' && Reflect,
    apply$1 = _ref.apply,
    construct = _ref.construct;

if (!apply$1) {
  apply$1 = function apply(fun, thisValue, args) {
    return fun.apply(thisValue, args);
  };
}

if (!freeze$2) {
  freeze$2 = function freeze(x) {
    return x;
  };
}

if (!seal) {
  seal = function seal(x) {
    return x;
  };
}

if (!construct) {
  construct = function construct(Func, args) {
    return _construct$1(Func, _toConsumableArray$6(args));
  };
}

var arrayForEach = unapply(Array.prototype.forEach);
var arrayPop = unapply(Array.prototype.pop);
var arrayPush$1 = unapply(Array.prototype.push);
var stringToLowerCase = unapply(String.prototype.toLowerCase);
var stringToString = unapply(String.prototype.toString);
var stringMatch = unapply(String.prototype.match);
var stringReplace = unapply(String.prototype.replace);
var stringIndexOf = unapply(String.prototype.indexOf);
var stringTrim = unapply(String.prototype.trim);
var regExpTest = unapply(RegExp.prototype.test);
var typeErrorCreate = unconstruct(TypeError);
function unapply(func) {
  return function (thisArg) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return apply$1(func, thisArg, args);
  };
}
function unconstruct(func) {
  return function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return construct(func, args);
  };
}
/* Add properties to a lookup table */

function addToSet(set, array, transformCaseFunc) {
  transformCaseFunc = transformCaseFunc ? transformCaseFunc : stringToLowerCase;

  if (setPrototypeOf) {
    // Make 'in' and truthy checks like Boolean(set.constructor)
    // independent of any properties defined on Object.prototype.
    // Prevent prototype setters from intercepting set as a this value.
    setPrototypeOf(set, null);
  }

  var l = array.length;

  while (l--) {
    var element = array[l];

    if (typeof element === 'string') {
      var lcElement = transformCaseFunc(element);

      if (lcElement !== element) {
        // Config presets (e.g. tags.js, attrs.js) are immutable.
        if (!isFrozen(array)) {
          array[l] = lcElement;
        }

        element = lcElement;
      }
    }

    set[element] = true;
  }

  return set;
}
/* Shallow clone an object */

function clone$3(object) {
  var newObject = create$1(null);

  var _iterator = _createForOfIteratorHelper(entries(object)),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _step$value = _slicedToArray$2(_step.value, 2),
          property = _step$value[0],
          value = _step$value[1];

      newObject[property] = value;
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return newObject;
}
/* This method automatically checks if the prop is function
 * or getter and behaves accordingly. */

function lookupGetter(object, prop) {
  while (object !== null) {
    var desc = getOwnPropertyDescriptor(object, prop);

    if (desc) {
      if (desc.get) {
        return unapply(desc.get);
      }

      if (typeof desc.value === 'function') {
        return unapply(desc.value);
      }
    }

    object = getPrototypeOf(object);
  }

  function fallbackValue(element) {
    console.warn('fallback value for', element);
    return null;
  }

  return fallbackValue;
}

var html$1 = freeze$2(['a', 'abbr', 'acronym', 'address', 'area', 'article', 'aside', 'audio', 'b', 'bdi', 'bdo', 'big', 'blink', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'center', 'cite', 'code', 'col', 'colgroup', 'content', 'data', 'datalist', 'dd', 'decorator', 'del', 'details', 'dfn', 'dialog', 'dir', 'div', 'dl', 'dt', 'element', 'em', 'fieldset', 'figcaption', 'figure', 'font', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'img', 'input', 'ins', 'kbd', 'label', 'legend', 'li', 'main', 'map', 'mark', 'marquee', 'menu', 'menuitem', 'meter', 'nav', 'nobr', 'ol', 'optgroup', 'option', 'output', 'p', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'section', 'select', 'shadow', 'small', 'source', 'spacer', 'span', 'strike', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead', 'time', 'tr', 'track', 'tt', 'u', 'ul', 'var', 'video', 'wbr']); // SVG

var svg$1 = freeze$2(['svg', 'a', 'altglyph', 'altglyphdef', 'altglyphitem', 'animatecolor', 'animatemotion', 'animatetransform', 'circle', 'clippath', 'defs', 'desc', 'ellipse', 'filter', 'font', 'g', 'glyph', 'glyphref', 'hkern', 'image', 'line', 'lineargradient', 'marker', 'mask', 'metadata', 'mpath', 'path', 'pattern', 'polygon', 'polyline', 'radialgradient', 'rect', 'stop', 'style', 'switch', 'symbol', 'text', 'textpath', 'title', 'tref', 'tspan', 'view', 'vkern']);
var svgFilters = freeze$2(['feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite', 'feConvolveMatrix', 'feDiffuseLighting', 'feDisplacementMap', 'feDistantLight', 'feFlood', 'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR', 'feGaussianBlur', 'feImage', 'feMerge', 'feMergeNode', 'feMorphology', 'feOffset', 'fePointLight', 'feSpecularLighting', 'feSpotLight', 'feTile', 'feTurbulence']); // List of SVG elements that are disallowed by default.
// We still need to know them so that we can do namespace
// checks properly in case one wants to add them to
// allow-list.

var svgDisallowed = freeze$2(['animate', 'color-profile', 'cursor', 'discard', 'fedropshadow', 'font-face', 'font-face-format', 'font-face-name', 'font-face-src', 'font-face-uri', 'foreignobject', 'hatch', 'hatchpath', 'mesh', 'meshgradient', 'meshpatch', 'meshrow', 'missing-glyph', 'script', 'set', 'solidcolor', 'unknown', 'use']);
var mathMl$1 = freeze$2(['math', 'menclose', 'merror', 'mfenced', 'mfrac', 'mglyph', 'mi', 'mlabeledtr', 'mmultiscripts', 'mn', 'mo', 'mover', 'mpadded', 'mphantom', 'mroot', 'mrow', 'ms', 'mspace', 'msqrt', 'mstyle', 'msub', 'msup', 'msubsup', 'mtable', 'mtd', 'mtext', 'mtr', 'munder', 'munderover']); // Similarly to SVG, we want to know all MathML elements,
// even those that we disallow by default.

var mathMlDisallowed = freeze$2(['maction', 'maligngroup', 'malignmark', 'mlongdiv', 'mscarries', 'mscarry', 'msgroup', 'mstack', 'msline', 'msrow', 'semantics', 'annotation', 'annotation-xml', 'mprescripts', 'none']);
var text = freeze$2(['#text']);

var html = freeze$2(['accept', 'action', 'align', 'alt', 'autocapitalize', 'autocomplete', 'autopictureinpicture', 'autoplay', 'background', 'bgcolor', 'border', 'capture', 'cellpadding', 'cellspacing', 'checked', 'cite', 'class', 'clear', 'color', 'cols', 'colspan', 'controls', 'controlslist', 'coords', 'crossorigin', 'datetime', 'decoding', 'default', 'dir', 'disabled', 'disablepictureinpicture', 'disableremoteplayback', 'download', 'draggable', 'enctype', 'enterkeyhint', 'face', 'for', 'headers', 'height', 'hidden', 'high', 'href', 'hreflang', 'id', 'inputmode', 'integrity', 'ismap', 'kind', 'label', 'lang', 'list', 'loading', 'loop', 'low', 'max', 'maxlength', 'media', 'method', 'min', 'minlength', 'multiple', 'muted', 'name', 'nonce', 'noshade', 'novalidate', 'nowrap', 'open', 'optimum', 'pattern', 'placeholder', 'playsinline', 'poster', 'preload', 'pubdate', 'radiogroup', 'readonly', 'rel', 'required', 'rev', 'reversed', 'role', 'rows', 'rowspan', 'spellcheck', 'scope', 'selected', 'shape', 'size', 'sizes', 'span', 'srclang', 'start', 'src', 'srcset', 'step', 'style', 'summary', 'tabindex', 'title', 'translate', 'type', 'usemap', 'valign', 'value', 'width', 'xmlns', 'slot']);
var svg = freeze$2(['accent-height', 'accumulate', 'additive', 'alignment-baseline', 'ascent', 'attributename', 'attributetype', 'azimuth', 'basefrequency', 'baseline-shift', 'begin', 'bias', 'by', 'class', 'clip', 'clippathunits', 'clip-path', 'clip-rule', 'color', 'color-interpolation', 'color-interpolation-filters', 'color-profile', 'color-rendering', 'cx', 'cy', 'd', 'dx', 'dy', 'diffuseconstant', 'direction', 'display', 'divisor', 'dur', 'edgemode', 'elevation', 'end', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'filterunits', 'flood-color', 'flood-opacity', 'font-family', 'font-size', 'font-size-adjust', 'font-stretch', 'font-style', 'font-variant', 'font-weight', 'fx', 'fy', 'g1', 'g2', 'glyph-name', 'glyphref', 'gradientunits', 'gradienttransform', 'height', 'href', 'id', 'image-rendering', 'in', 'in2', 'k', 'k1', 'k2', 'k3', 'k4', 'kerning', 'keypoints', 'keysplines', 'keytimes', 'lang', 'lengthadjust', 'letter-spacing', 'kernelmatrix', 'kernelunitlength', 'lighting-color', 'local', 'marker-end', 'marker-mid', 'marker-start', 'markerheight', 'markerunits', 'markerwidth', 'maskcontentunits', 'maskunits', 'max', 'mask', 'media', 'method', 'mode', 'min', 'name', 'numoctaves', 'offset', 'operator', 'opacity', 'order', 'orient', 'orientation', 'origin', 'overflow', 'paint-order', 'path', 'pathlength', 'patterncontentunits', 'patterntransform', 'patternunits', 'points', 'preservealpha', 'preserveaspectratio', 'primitiveunits', 'r', 'rx', 'ry', 'radius', 'refx', 'refy', 'repeatcount', 'repeatdur', 'restart', 'result', 'rotate', 'scale', 'seed', 'shape-rendering', 'specularconstant', 'specularexponent', 'spreadmethod', 'startoffset', 'stddeviation', 'stitchtiles', 'stop-color', 'stop-opacity', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke', 'stroke-width', 'style', 'surfacescale', 'systemlanguage', 'tabindex', 'targetx', 'targety', 'transform', 'transform-origin', 'text-anchor', 'text-decoration', 'text-rendering', 'textlength', 'type', 'u1', 'u2', 'unicode', 'values', 'viewbox', 'visibility', 'version', 'vert-adv-y', 'vert-origin-x', 'vert-origin-y', 'width', 'word-spacing', 'wrap', 'writing-mode', 'xchannelselector', 'ychannelselector', 'x', 'x1', 'x2', 'xmlns', 'y', 'y1', 'y2', 'z', 'zoomandpan']);
var mathMl = freeze$2(['accent', 'accentunder', 'align', 'bevelled', 'close', 'columnsalign', 'columnlines', 'columnspan', 'denomalign', 'depth', 'dir', 'display', 'displaystyle', 'encoding', 'fence', 'frame', 'height', 'href', 'id', 'largeop', 'length', 'linethickness', 'lspace', 'lquote', 'mathbackground', 'mathcolor', 'mathsize', 'mathvariant', 'maxsize', 'minsize', 'movablelimits', 'notation', 'numalign', 'open', 'rowalign', 'rowlines', 'rowspacing', 'rowspan', 'rspace', 'rquote', 'scriptlevel', 'scriptminsize', 'scriptsizemultiplier', 'selection', 'separator', 'separators', 'stretchy', 'subscriptshift', 'supscriptshift', 'symmetric', 'voffset', 'width', 'xmlns']);
var xml = freeze$2(['xlink:href', 'xml:id', 'xlink:title', 'xml:space', 'xmlns:xlink']);

var MUSTACHE_EXPR = seal(/\{\{[\w\W]*|[\w\W]*\}\}/gm); // Specify template detection regex for SAFE_FOR_TEMPLATES mode

var ERB_EXPR = seal(/<%[\w\W]*|[\w\W]*%>/gm);
var TMPLIT_EXPR = seal(/\${[\w\W]*}/gm);
var DATA_ATTR = seal(/^data-[\-\w.\u00B7-\uFFFF]/); // eslint-disable-line no-useless-escape

var ARIA_ATTR = seal(/^aria-[\-\w]+$/); // eslint-disable-line no-useless-escape

var IS_ALLOWED_URI = seal(/^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i // eslint-disable-line no-useless-escape
);
var IS_SCRIPT_OR_DATA = seal(/^(?:\w+script|data):/i);
var ATTR_WHITESPACE = seal(/[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g // eslint-disable-line no-control-regex
);
var DOCTYPE_NAME = seal(/^html$/i);

var getGlobal = function getGlobal() {
  return typeof window === 'undefined' ? null : window;
};
/**
 * Creates a no-op policy for internal use only.
 * Don't export this function outside this module!
 * @param {?TrustedTypePolicyFactory} trustedTypes The policy factory.
 * @param {Document} document The document object (to determine policy name suffix)
 * @return {?TrustedTypePolicy} The policy created (or null, if Trusted Types
 * are not supported).
 */


var _createTrustedTypesPolicy = function _createTrustedTypesPolicy(trustedTypes, document) {
  if (_typeof$7(trustedTypes) !== 'object' || typeof trustedTypes.createPolicy !== 'function') {
    return null;
  } // Allow the callers to control the unique policy name
  // by adding a data-tt-policy-suffix to the script element with the DOMPurify.
  // Policy creation with duplicate names throws in Trusted Types.


  var suffix = null;
  var ATTR_NAME = 'data-tt-policy-suffix';

  if (document.currentScript && document.currentScript.hasAttribute(ATTR_NAME)) {
    suffix = document.currentScript.getAttribute(ATTR_NAME);
  }

  var policyName = 'dompurify' + (suffix ? '#' + suffix : '');

  try {
    return trustedTypes.createPolicy(policyName, {
      createHTML: function createHTML(html) {
        return html;
      },
      createScriptURL: function createScriptURL(scriptUrl) {
        return scriptUrl;
      }
    });
  } catch (_) {
    // Policy creation failed (most likely another DOMPurify script has
    // already run). Skip creating the policy, as this will only cause errors
    // if TT are enforced.
    console.warn('TrustedTypes policy ' + policyName + ' could not be created.');
    return null;
  }
};

function createDOMPurify() {
  var window = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getGlobal();

  var DOMPurify = function DOMPurify(root) {
    return createDOMPurify(root);
  };
  /**
   * Version label, exposed for easier checks
   * if DOMPurify is up to date or not
   */


  DOMPurify.version = '3.0.1';
  /**
   * Array of elements that DOMPurify removed during sanitation.
   * Empty if nothing was removed.
   */

  DOMPurify.removed = [];

  if (!window || !window.document || window.document.nodeType !== 9) {
    // Not running in a browser, provide a factory function
    // so that you can pass your own Window
    DOMPurify.isSupported = false;
    return DOMPurify;
  }

  var originalDocument = window.document;
  var document = window.document;
  var DocumentFragment = window.DocumentFragment,
      HTMLTemplateElement = window.HTMLTemplateElement,
      Node = window.Node,
      Element = window.Element,
      NodeFilter = window.NodeFilter,
      _window$NamedNodeMap = window.NamedNodeMap,
      NamedNodeMap = _window$NamedNodeMap === void 0 ? window.NamedNodeMap || window.MozNamedAttrMap : _window$NamedNodeMap,
      HTMLFormElement = window.HTMLFormElement,
      DOMParser = window.DOMParser,
      trustedTypes = window.trustedTypes;
  var ElementPrototype = Element.prototype;
  var cloneNode = lookupGetter(ElementPrototype, 'cloneNode');
  var getNextSibling = lookupGetter(ElementPrototype, 'nextSibling');
  var getChildNodes = lookupGetter(ElementPrototype, 'childNodes');
  var getParentNode = lookupGetter(ElementPrototype, 'parentNode'); // As per issue #47, the web-components registry is inherited by a
  // new document created via createHTMLDocument. As per the spec
  // (http://w3c.github.io/webcomponents/spec/custom/#creating-and-passing-registries)
  // a new empty registry is used when creating a template contents owner
  // document, so we use that as our parent document to ensure nothing
  // is inherited.

  if (typeof HTMLTemplateElement === 'function') {
    var template = document.createElement('template');

    if (template.content && template.content.ownerDocument) {
      document = template.content.ownerDocument;
    }
  }

  var trustedTypesPolicy = _createTrustedTypesPolicy(trustedTypes, originalDocument);

  var emptyHTML = trustedTypesPolicy ? trustedTypesPolicy.createHTML('') : '';
  var _document = document,
      implementation = _document.implementation,
      createNodeIterator = _document.createNodeIterator,
      createDocumentFragment = _document.createDocumentFragment,
      getElementsByTagName = _document.getElementsByTagName;
  var importNode = originalDocument.importNode;
  var hooks = {};
  /**
   * Expose whether this browser supports running the full DOMPurify.
   */

  DOMPurify.isSupported = typeof entries === 'function' && typeof getParentNode === 'function' && implementation && typeof implementation.createHTMLDocument !== 'undefined';
  var MUSTACHE_EXPR$1 = MUSTACHE_EXPR,
      ERB_EXPR$1 = ERB_EXPR,
      TMPLIT_EXPR$1 = TMPLIT_EXPR,
      DATA_ATTR$1 = DATA_ATTR,
      ARIA_ATTR$1 = ARIA_ATTR,
      IS_SCRIPT_OR_DATA$1 = IS_SCRIPT_OR_DATA,
      ATTR_WHITESPACE$1 = ATTR_WHITESPACE;
  var IS_ALLOWED_URI$1 = IS_ALLOWED_URI;
  /**
   * We consider the elements and attributes below to be safe. Ideally
   * don't add any new ones but feel free to remove unwanted ones.
   */

  /* allowed element names */

  var ALLOWED_TAGS = null;
  var DEFAULT_ALLOWED_TAGS = addToSet({}, [].concat(_toConsumableArray$6(html$1), _toConsumableArray$6(svg$1), _toConsumableArray$6(svgFilters), _toConsumableArray$6(mathMl$1), _toConsumableArray$6(text)));
  /* Allowed attribute names */

  var ALLOWED_ATTR = null;
  var DEFAULT_ALLOWED_ATTR = addToSet({}, [].concat(_toConsumableArray$6(html), _toConsumableArray$6(svg), _toConsumableArray$6(mathMl), _toConsumableArray$6(xml)));
  /*
   * Configure how DOMPUrify should handle custom elements and their attributes as well as customized built-in elements.
   * @property {RegExp|Function|null} tagNameCheck one of [null, regexPattern, predicate]. Default: `null` (disallow any custom elements)
   * @property {RegExp|Function|null} attributeNameCheck one of [null, regexPattern, predicate]. Default: `null` (disallow any attributes not on the allow list)
   * @property {boolean} allowCustomizedBuiltInElements allow custom elements derived from built-ins if they pass CUSTOM_ELEMENT_HANDLING.tagNameCheck. Default: `false`.
   */

  var CUSTOM_ELEMENT_HANDLING = Object.seal(Object.create(null, {
    tagNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    attributeNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    allowCustomizedBuiltInElements: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: false
    }
  }));
  /* Explicitly forbidden tags (overrides ALLOWED_TAGS/ADD_TAGS) */

  var FORBID_TAGS = null;
  /* Explicitly forbidden attributes (overrides ALLOWED_ATTR/ADD_ATTR) */

  var FORBID_ATTR = null;
  /* Decide if ARIA attributes are okay */

  var ALLOW_ARIA_ATTR = true;
  /* Decide if custom data attributes are okay */

  var ALLOW_DATA_ATTR = true;
  /* Decide if unknown protocols are okay */

  var ALLOW_UNKNOWN_PROTOCOLS = false;
  /* Decide if self-closing tags in attributes are allowed.
   * Usually removed due to a mXSS issue in jQuery 3.0 */

  var ALLOW_SELF_CLOSE_IN_ATTR = true;
  /* Output should be safe for common template engines.
   * This means, DOMPurify removes data attributes, mustaches and ERB
   */

  var SAFE_FOR_TEMPLATES = false;
  /* Decide if document with <html>... should be returned */

  var WHOLE_DOCUMENT = false;
  /* Track whether config is already set on this instance of DOMPurify. */

  var SET_CONFIG = false;
  /* Decide if all elements (e.g. style, script) must be children of
   * document.body. By default, browsers might move them to document.head */

  var FORCE_BODY = false;
  /* Decide if a DOM `HTMLBodyElement` should be returned, instead of a html
   * string (or a TrustedHTML object if Trusted Types are supported).
   * If `WHOLE_DOCUMENT` is enabled a `HTMLHtmlElement` will be returned instead
   */

  var RETURN_DOM = false;
  /* Decide if a DOM `DocumentFragment` should be returned, instead of a html
   * string  (or a TrustedHTML object if Trusted Types are supported) */

  var RETURN_DOM_FRAGMENT = false;
  /* Try to return a Trusted Type object instead of a string, return a string in
   * case Trusted Types are not supported  */

  var RETURN_TRUSTED_TYPE = false;
  /* Output should be free from DOM clobbering attacks?
   * This sanitizes markups named with colliding, clobberable built-in DOM APIs.
   */

  var SANITIZE_DOM = true;
  /* Achieve full DOM Clobbering protection by isolating the namespace of named
   * properties and JS variables, mitigating attacks that abuse the HTML/DOM spec rules.
   *
   * HTML/DOM spec rules that enable DOM Clobbering:
   *   - Named Access on Window (§7.3.3)
   *   - DOM Tree Accessors (§3.1.5)
   *   - Form Element Parent-Child Relations (§4.10.3)
   *   - Iframe srcdoc / Nested WindowProxies (§4.8.5)
   *   - HTMLCollection (§4.2.10.2)
   *
   * Namespace isolation is implemented by prefixing `id` and `name` attributes
   * with a constant string, i.e., `user-content-`
   */

  var SANITIZE_NAMED_PROPS = false;
  var SANITIZE_NAMED_PROPS_PREFIX = 'user-content-';
  /* Keep element content when removing element? */

  var KEEP_CONTENT = true;
  /* If a `Node` is passed to sanitize(), then performs sanitization in-place instead
   * of importing it into a new Document and returning a sanitized copy */

  var IN_PLACE = false;
  /* Allow usage of profiles like html, svg and mathMl */

  var USE_PROFILES = {};
  /* Tags to ignore content of when KEEP_CONTENT is true */

  var FORBID_CONTENTS = null;
  var DEFAULT_FORBID_CONTENTS = addToSet({}, ['annotation-xml', 'audio', 'colgroup', 'desc', 'foreignobject', 'head', 'iframe', 'math', 'mi', 'mn', 'mo', 'ms', 'mtext', 'noembed', 'noframes', 'noscript', 'plaintext', 'script', 'style', 'svg', 'template', 'thead', 'title', 'video', 'xmp']);
  /* Tags that are safe for data: URIs */

  var DATA_URI_TAGS = null;
  var DEFAULT_DATA_URI_TAGS = addToSet({}, ['audio', 'video', 'img', 'source', 'image', 'track']);
  /* Attributes safe for values like "javascript:" */

  var URI_SAFE_ATTRIBUTES = null;
  var DEFAULT_URI_SAFE_ATTRIBUTES = addToSet({}, ['alt', 'class', 'for', 'id', 'label', 'name', 'pattern', 'placeholder', 'role', 'summary', 'title', 'value', 'style', 'xmlns']);
  var MATHML_NAMESPACE = 'http://www.w3.org/1998/Math/MathML';
  var SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  var HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
  /* Document namespace */

  var NAMESPACE = HTML_NAMESPACE;
  var IS_EMPTY_INPUT = false;
  /* Allowed XHTML+XML namespaces */

  var ALLOWED_NAMESPACES = null;
  var DEFAULT_ALLOWED_NAMESPACES = addToSet({}, [MATHML_NAMESPACE, SVG_NAMESPACE, HTML_NAMESPACE], stringToString);
  /* Parsing of strict XHTML documents */

  var PARSER_MEDIA_TYPE;
  var SUPPORTED_PARSER_MEDIA_TYPES = ['application/xhtml+xml', 'text/html'];
  var DEFAULT_PARSER_MEDIA_TYPE = 'text/html';
  var transformCaseFunc;
  /* Keep a reference to config to pass to hooks */

  var CONFIG = null;
  /* Ideally, do not touch anything below this line */

  /* ______________________________________________ */

  var formElement = document.createElement('form');

  var isRegexOrFunction = function isRegexOrFunction(testValue) {
    return testValue instanceof RegExp || testValue instanceof Function;
  };
  /**
   * _parseConfig
   *
   * @param  {Object} cfg optional config literal
   */
  // eslint-disable-next-line complexity


  var _parseConfig = function _parseConfig(cfg) {
    if (CONFIG && CONFIG === cfg) {
      return;
    }
    /* Shield configuration object from tampering */


    if (!cfg || _typeof$7(cfg) !== 'object') {
      cfg = {};
    }
    /* Shield configuration object from prototype pollution */


    cfg = clone$3(cfg);
    PARSER_MEDIA_TYPE = // eslint-disable-next-line unicorn/prefer-includes
    SUPPORTED_PARSER_MEDIA_TYPES.indexOf(cfg.PARSER_MEDIA_TYPE) === -1 ? PARSER_MEDIA_TYPE = DEFAULT_PARSER_MEDIA_TYPE : PARSER_MEDIA_TYPE = cfg.PARSER_MEDIA_TYPE; // HTML tags and attributes are not case-sensitive, converting to lowercase. Keeping XHTML as is.

    transformCaseFunc = PARSER_MEDIA_TYPE === 'application/xhtml+xml' ? stringToString : stringToLowerCase;
    /* Set configuration parameters */

    ALLOWED_TAGS = 'ALLOWED_TAGS' in cfg ? addToSet({}, cfg.ALLOWED_TAGS, transformCaseFunc) : DEFAULT_ALLOWED_TAGS;
    ALLOWED_ATTR = 'ALLOWED_ATTR' in cfg ? addToSet({}, cfg.ALLOWED_ATTR, transformCaseFunc) : DEFAULT_ALLOWED_ATTR;
    ALLOWED_NAMESPACES = 'ALLOWED_NAMESPACES' in cfg ? addToSet({}, cfg.ALLOWED_NAMESPACES, stringToString) : DEFAULT_ALLOWED_NAMESPACES;
    URI_SAFE_ATTRIBUTES = 'ADD_URI_SAFE_ATTR' in cfg ? addToSet(clone$3(DEFAULT_URI_SAFE_ATTRIBUTES), // eslint-disable-line indent
    cfg.ADD_URI_SAFE_ATTR, // eslint-disable-line indent
    transformCaseFunc // eslint-disable-line indent
    ) // eslint-disable-line indent
    : DEFAULT_URI_SAFE_ATTRIBUTES;
    DATA_URI_TAGS = 'ADD_DATA_URI_TAGS' in cfg ? addToSet(clone$3(DEFAULT_DATA_URI_TAGS), // eslint-disable-line indent
    cfg.ADD_DATA_URI_TAGS, // eslint-disable-line indent
    transformCaseFunc // eslint-disable-line indent
    ) // eslint-disable-line indent
    : DEFAULT_DATA_URI_TAGS;
    FORBID_CONTENTS = 'FORBID_CONTENTS' in cfg ? addToSet({}, cfg.FORBID_CONTENTS, transformCaseFunc) : DEFAULT_FORBID_CONTENTS;
    FORBID_TAGS = 'FORBID_TAGS' in cfg ? addToSet({}, cfg.FORBID_TAGS, transformCaseFunc) : {};
    FORBID_ATTR = 'FORBID_ATTR' in cfg ? addToSet({}, cfg.FORBID_ATTR, transformCaseFunc) : {};
    USE_PROFILES = 'USE_PROFILES' in cfg ? cfg.USE_PROFILES : false;
    ALLOW_ARIA_ATTR = cfg.ALLOW_ARIA_ATTR !== false; // Default true

    ALLOW_DATA_ATTR = cfg.ALLOW_DATA_ATTR !== false; // Default true

    ALLOW_UNKNOWN_PROTOCOLS = cfg.ALLOW_UNKNOWN_PROTOCOLS || false; // Default false

    ALLOW_SELF_CLOSE_IN_ATTR = cfg.ALLOW_SELF_CLOSE_IN_ATTR !== false; // Default true

    SAFE_FOR_TEMPLATES = cfg.SAFE_FOR_TEMPLATES || false; // Default false

    WHOLE_DOCUMENT = cfg.WHOLE_DOCUMENT || false; // Default false

    RETURN_DOM = cfg.RETURN_DOM || false; // Default false

    RETURN_DOM_FRAGMENT = cfg.RETURN_DOM_FRAGMENT || false; // Default false

    RETURN_TRUSTED_TYPE = cfg.RETURN_TRUSTED_TYPE || false; // Default false

    FORCE_BODY = cfg.FORCE_BODY || false; // Default false

    SANITIZE_DOM = cfg.SANITIZE_DOM !== false; // Default true

    SANITIZE_NAMED_PROPS = cfg.SANITIZE_NAMED_PROPS || false; // Default false

    KEEP_CONTENT = cfg.KEEP_CONTENT !== false; // Default true

    IN_PLACE = cfg.IN_PLACE || false; // Default false

    IS_ALLOWED_URI$1 = cfg.ALLOWED_URI_REGEXP || IS_ALLOWED_URI$1;
    NAMESPACE = cfg.NAMESPACE || HTML_NAMESPACE;
    CUSTOM_ELEMENT_HANDLING = cfg.CUSTOM_ELEMENT_HANDLING || {};

    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.tagNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck;
    }

    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.attributeNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck;
    }

    if (cfg.CUSTOM_ELEMENT_HANDLING && typeof cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements === 'boolean') {
      CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements = cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements;
    }

    if (SAFE_FOR_TEMPLATES) {
      ALLOW_DATA_ATTR = false;
    }

    if (RETURN_DOM_FRAGMENT) {
      RETURN_DOM = true;
    }
    /* Parse profile info */


    if (USE_PROFILES) {
      ALLOWED_TAGS = addToSet({}, _toConsumableArray$6(text));
      ALLOWED_ATTR = [];

      if (USE_PROFILES.html === true) {
        addToSet(ALLOWED_TAGS, html$1);
        addToSet(ALLOWED_ATTR, html);
      }

      if (USE_PROFILES.svg === true) {
        addToSet(ALLOWED_TAGS, svg$1);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }

      if (USE_PROFILES.svgFilters === true) {
        addToSet(ALLOWED_TAGS, svgFilters);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }

      if (USE_PROFILES.mathMl === true) {
        addToSet(ALLOWED_TAGS, mathMl$1);
        addToSet(ALLOWED_ATTR, mathMl);
        addToSet(ALLOWED_ATTR, xml);
      }
    }
    /* Merge configuration parameters */


    if (cfg.ADD_TAGS) {
      if (ALLOWED_TAGS === DEFAULT_ALLOWED_TAGS) {
        ALLOWED_TAGS = clone$3(ALLOWED_TAGS);
      }

      addToSet(ALLOWED_TAGS, cfg.ADD_TAGS, transformCaseFunc);
    }

    if (cfg.ADD_ATTR) {
      if (ALLOWED_ATTR === DEFAULT_ALLOWED_ATTR) {
        ALLOWED_ATTR = clone$3(ALLOWED_ATTR);
      }

      addToSet(ALLOWED_ATTR, cfg.ADD_ATTR, transformCaseFunc);
    }

    if (cfg.ADD_URI_SAFE_ATTR) {
      addToSet(URI_SAFE_ATTRIBUTES, cfg.ADD_URI_SAFE_ATTR, transformCaseFunc);
    }

    if (cfg.FORBID_CONTENTS) {
      if (FORBID_CONTENTS === DEFAULT_FORBID_CONTENTS) {
        FORBID_CONTENTS = clone$3(FORBID_CONTENTS);
      }

      addToSet(FORBID_CONTENTS, cfg.FORBID_CONTENTS, transformCaseFunc);
    }
    /* Add #text in case KEEP_CONTENT is set to true */


    if (KEEP_CONTENT) {
      ALLOWED_TAGS['#text'] = true;
    }
    /* Add html, head and body to ALLOWED_TAGS in case WHOLE_DOCUMENT is true */


    if (WHOLE_DOCUMENT) {
      addToSet(ALLOWED_TAGS, ['html', 'head', 'body']);
    }
    /* Add tbody to ALLOWED_TAGS in case tables are permitted, see #286, #365 */


    if (ALLOWED_TAGS.table) {
      addToSet(ALLOWED_TAGS, ['tbody']);
      delete FORBID_TAGS.tbody;
    } // Prevent further manipulation of configuration.
    // Not available in IE8, Safari 5, etc.


    if (freeze$2) {
      freeze$2(cfg);
    }

    CONFIG = cfg;
  };

  var MATHML_TEXT_INTEGRATION_POINTS = addToSet({}, ['mi', 'mo', 'mn', 'ms', 'mtext']);
  var HTML_INTEGRATION_POINTS = addToSet({}, ['foreignobject', 'desc', 'title', 'annotation-xml']); // Certain elements are allowed in both SVG and HTML
  // namespace. We need to specify them explicitly
  // so that they don't get erroneously deleted from
  // HTML namespace.

  var COMMON_SVG_AND_HTML_ELEMENTS = addToSet({}, ['title', 'style', 'font', 'a', 'script']);
  /* Keep track of all possible SVG and MathML tags
   * so that we can perform the namespace checks
   * correctly. */

  var ALL_SVG_TAGS = addToSet({}, svg$1);
  addToSet(ALL_SVG_TAGS, svgFilters);
  addToSet(ALL_SVG_TAGS, svgDisallowed);
  var ALL_MATHML_TAGS = addToSet({}, mathMl$1);
  addToSet(ALL_MATHML_TAGS, mathMlDisallowed);
  /**
   *
   *
   * @param  {Element} element a DOM element whose namespace is being checked
   * @returns {boolean} Return false if the element has a
   *  namespace that a spec-compliant parser would never
   *  return. Return true otherwise.
   */

  var _checkValidNamespace = function _checkValidNamespace(element) {
    var parent = getParentNode(element); // In JSDOM, if we're inside shadow DOM, then parentNode
    // can be null. We just simulate parent in this case.

    if (!parent || !parent.tagName) {
      parent = {
        namespaceURI: NAMESPACE,
        tagName: 'template'
      };
    }

    var tagName = stringToLowerCase(element.tagName);
    var parentTagName = stringToLowerCase(parent.tagName);

    if (!ALLOWED_NAMESPACES[element.namespaceURI]) {
      return false;
    }

    if (element.namespaceURI === SVG_NAMESPACE) {
      // The only way to switch from HTML namespace to SVG
      // is via <svg>. If it happens via any other tag, then
      // it should be killed.
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === 'svg';
      } // The only way to switch from MathML to SVG is via`
      // svg if parent is either <annotation-xml> or MathML
      // text integration points.


      if (parent.namespaceURI === MATHML_NAMESPACE) {
        return tagName === 'svg' && (parentTagName === 'annotation-xml' || MATHML_TEXT_INTEGRATION_POINTS[parentTagName]);
      } // We only allow elements that are defined in SVG
      // spec. All others are disallowed in SVG namespace.


      return Boolean(ALL_SVG_TAGS[tagName]);
    }

    if (element.namespaceURI === MATHML_NAMESPACE) {
      // The only way to switch from HTML namespace to MathML
      // is via <math>. If it happens via any other tag, then
      // it should be killed.
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === 'math';
      } // The only way to switch from SVG to MathML is via
      // <math> and HTML integration points


      if (parent.namespaceURI === SVG_NAMESPACE) {
        return tagName === 'math' && HTML_INTEGRATION_POINTS[parentTagName];
      } // We only allow elements that are defined in MathML
      // spec. All others are disallowed in MathML namespace.


      return Boolean(ALL_MATHML_TAGS[tagName]);
    }

    if (element.namespaceURI === HTML_NAMESPACE) {
      // The only way to switch from SVG to HTML is via
      // HTML integration points, and from MathML to HTML
      // is via MathML text integration points
      if (parent.namespaceURI === SVG_NAMESPACE && !HTML_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }

      if (parent.namespaceURI === MATHML_NAMESPACE && !MATHML_TEXT_INTEGRATION_POINTS[parentTagName]) {
        return false;
      } // We disallow tags that are specific for MathML
      // or SVG and should never appear in HTML namespace


      return !ALL_MATHML_TAGS[tagName] && (COMMON_SVG_AND_HTML_ELEMENTS[tagName] || !ALL_SVG_TAGS[tagName]);
    } // For XHTML and XML documents that support custom namespaces


    if (PARSER_MEDIA_TYPE === 'application/xhtml+xml' && ALLOWED_NAMESPACES[element.namespaceURI]) {
      return true;
    } // The code should never reach this place (this means
    // that the element somehow got namespace that is not
    // HTML, SVG, MathML or allowed via ALLOWED_NAMESPACES).
    // Return false just in case.


    return false;
  };
  /**
   * _forceRemove
   *
   * @param  {Node} node a DOM node
   */


  var _forceRemove = function _forceRemove(node) {
    arrayPush$1(DOMPurify.removed, {
      element: node
    });

    try {
      // eslint-disable-next-line unicorn/prefer-dom-node-remove
      node.parentNode.removeChild(node);
    } catch (_) {
      node.remove();
    }
  };
  /**
   * _removeAttribute
   *
   * @param  {String} name an Attribute name
   * @param  {Node} node a DOM node
   */


  var _removeAttribute = function _removeAttribute(name, node) {
    try {
      arrayPush$1(DOMPurify.removed, {
        attribute: node.getAttributeNode(name),
        from: node
      });
    } catch (_) {
      arrayPush$1(DOMPurify.removed, {
        attribute: null,
        from: node
      });
    }

    node.removeAttribute(name); // We void attribute values for unremovable "is"" attributes

    if (name === 'is' && !ALLOWED_ATTR[name]) {
      if (RETURN_DOM || RETURN_DOM_FRAGMENT) {
        try {
          _forceRemove(node);
        } catch (_) {}
      } else {
        try {
          node.setAttribute(name, '');
        } catch (_) {}
      }
    }
  };
  /**
   * _initDocument
   *
   * @param  {String} dirty a string of dirty markup
   * @return {Document} a DOM, filled with the dirty markup
   */


  var _initDocument = function _initDocument(dirty) {
    /* Create a HTML document */
    var doc;
    var leadingWhitespace;

    if (FORCE_BODY) {
      dirty = '<remove></remove>' + dirty;
    } else {
      /* If FORCE_BODY isn't used, leading whitespace needs to be preserved manually */
      var matches = stringMatch(dirty, /^[\r\n\t ]+/);
      leadingWhitespace = matches && matches[0];
    }

    if (PARSER_MEDIA_TYPE === 'application/xhtml+xml' && NAMESPACE === HTML_NAMESPACE) {
      // Root of XHTML doc must contain xmlns declaration (see https://www.w3.org/TR/xhtml1/normative.html#strict)
      dirty = '<html xmlns="http://www.w3.org/1999/xhtml"><head></head><body>' + dirty + '</body></html>';
    }

    var dirtyPayload = trustedTypesPolicy ? trustedTypesPolicy.createHTML(dirty) : dirty;
    /*
     * Use the DOMParser API by default, fallback later if needs be
     * DOMParser not work for svg when has multiple root element.
     */

    if (NAMESPACE === HTML_NAMESPACE) {
      try {
        doc = new DOMParser().parseFromString(dirtyPayload, PARSER_MEDIA_TYPE);
      } catch (_) {}
    }
    /* Use createHTMLDocument in case DOMParser is not available */


    if (!doc || !doc.documentElement) {
      doc = implementation.createDocument(NAMESPACE, 'template', null);

      try {
        doc.documentElement.innerHTML = IS_EMPTY_INPUT ? emptyHTML : dirtyPayload;
      } catch (_) {// Syntax error if dirtyPayload is invalid xml
      }
    }

    var body = doc.body || doc.documentElement;

    if (dirty && leadingWhitespace) {
      body.insertBefore(document.createTextNode(leadingWhitespace), body.childNodes[0] || null);
    }
    /* Work on whole document or just its body */


    if (NAMESPACE === HTML_NAMESPACE) {
      return getElementsByTagName.call(doc, WHOLE_DOCUMENT ? 'html' : 'body')[0];
    }

    return WHOLE_DOCUMENT ? doc.documentElement : body;
  };
  /**
   * _createIterator
   *
   * @param  {Document} root document/fragment to create iterator for
   * @return {Iterator} iterator instance
   */


  var _createIterator = function _createIterator(root) {
    return createNodeIterator.call(root.ownerDocument || root, root, // eslint-disable-next-line no-bitwise
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT, null, false);
  };
  /**
   * _isClobbered
   *
   * @param  {Node} elm element to check for clobbering attacks
   * @return {Boolean} true if clobbered, false if safe
   */


  var _isClobbered = function _isClobbered(elm) {
    return elm instanceof HTMLFormElement && (typeof elm.nodeName !== 'string' || typeof elm.textContent !== 'string' || typeof elm.removeChild !== 'function' || !(elm.attributes instanceof NamedNodeMap) || typeof elm.removeAttribute !== 'function' || typeof elm.setAttribute !== 'function' || typeof elm.namespaceURI !== 'string' || typeof elm.insertBefore !== 'function' || typeof elm.hasChildNodes !== 'function');
  };
  /**
   * _isNode
   *
   * @param  {Node} obj object to check whether it's a DOM node
   * @return {Boolean} true is object is a DOM node
   */


  var _isNode = function _isNode(object) {
    return _typeof$7(Node) === 'object' ? object instanceof Node : object && _typeof$7(object) === 'object' && typeof object.nodeType === 'number' && typeof object.nodeName === 'string';
  };
  /**
   * _executeHook
   * Execute user configurable hooks
   *
   * @param  {String} entryPoint  Name of the hook's entry point
   * @param  {Node} currentNode node to work on with the hook
   * @param  {Object} data additional hook parameters
   */


  var _executeHook = function _executeHook(entryPoint, currentNode, data) {
    if (!hooks[entryPoint]) {
      return;
    }

    arrayForEach(hooks[entryPoint], function (hook) {
      hook.call(DOMPurify, currentNode, data, CONFIG);
    });
  };
  /**
   * _sanitizeElements
   *
   * @protect nodeName
   * @protect textContent
   * @protect removeChild
   *
   * @param   {Node} currentNode to check for permission to exist
   * @return  {Boolean} true if node was killed, false if left alive
   */


  var _sanitizeElements = function _sanitizeElements(currentNode) {
    var content;
    /* Execute a hook if present */

    _executeHook('beforeSanitizeElements', currentNode, null);
    /* Check if element is clobbered or can clobber */


    if (_isClobbered(currentNode)) {
      _forceRemove(currentNode);

      return true;
    }
    /* Now let's check the element's type and name */


    var tagName = transformCaseFunc(currentNode.nodeName);
    /* Execute a hook if present */

    _executeHook('uponSanitizeElement', currentNode, {
      tagName: tagName,
      allowedTags: ALLOWED_TAGS
    });
    /* Detect mXSS attempts abusing namespace confusion */


    if (currentNode.hasChildNodes() && !_isNode(currentNode.firstElementChild) && (!_isNode(currentNode.content) || !_isNode(currentNode.content.firstElementChild)) && regExpTest(/<[/\w]/g, currentNode.innerHTML) && regExpTest(/<[/\w]/g, currentNode.textContent)) {
      _forceRemove(currentNode);

      return true;
    }
    /* Remove element if anything forbids its presence */


    if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
      /* Check if we have a custom element to handle */
      if (!FORBID_TAGS[tagName] && _basicCustomElementTest(tagName)) {
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, tagName)) return false;
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(tagName)) return false;
      }
      /* Keep content except for bad-listed elements */


      if (KEEP_CONTENT && !FORBID_CONTENTS[tagName]) {
        var parentNode = getParentNode(currentNode) || currentNode.parentNode;
        var childNodes = getChildNodes(currentNode) || currentNode.childNodes;

        if (childNodes && parentNode) {
          var childCount = childNodes.length;

          for (var i = childCount - 1; i >= 0; --i) {
            parentNode.insertBefore(cloneNode(childNodes[i], true), getNextSibling(currentNode));
          }
        }
      }

      _forceRemove(currentNode);

      return true;
    }
    /* Check whether element has a valid namespace */


    if (currentNode instanceof Element && !_checkValidNamespace(currentNode)) {
      _forceRemove(currentNode);

      return true;
    }
    /* Make sure that older browsers don't get noscript mXSS */


    if ((tagName === 'noscript' || tagName === 'noembed') && regExpTest(/<\/no(script|embed)/i, currentNode.innerHTML)) {
      _forceRemove(currentNode);

      return true;
    }
    /* Sanitize element content to be template-safe */


    if (SAFE_FOR_TEMPLATES && currentNode.nodeType === 3) {
      /* Get the element's text content */
      content = currentNode.textContent;
      content = stringReplace(content, MUSTACHE_EXPR$1, ' ');
      content = stringReplace(content, ERB_EXPR$1, ' ');
      content = stringReplace(content, TMPLIT_EXPR$1, ' ');

      if (currentNode.textContent !== content) {
        arrayPush$1(DOMPurify.removed, {
          element: currentNode.cloneNode()
        });
        currentNode.textContent = content;
      }
    }
    /* Execute a hook if present */


    _executeHook('afterSanitizeElements', currentNode, null);

    return false;
  };
  /**
   * _isValidAttribute
   *
   * @param  {string} lcTag Lowercase tag name of containing element.
   * @param  {string} lcName Lowercase attribute name.
   * @param  {string} value Attribute value.
   * @return {Boolean} Returns true if `value` is valid, otherwise false.
   */
  // eslint-disable-next-line complexity


  var _isValidAttribute = function _isValidAttribute(lcTag, lcName, value) {
    /* Make sure attribute cannot clobber */
    if (SANITIZE_DOM && (lcName === 'id' || lcName === 'name') && (value in document || value in formElement)) {
      return false;
    }
    /* Allow valid data-* attributes: At least one character after "-"
        (https://html.spec.whatwg.org/multipage/dom.html#embedding-custom-non-visible-data-with-the-data-*-attributes)
        XML-compatible (https://html.spec.whatwg.org/multipage/infrastructure.html#xml-compatible and http://www.w3.org/TR/xml/#d0e804)
        We don't need to check the value; it's always URI safe. */


    if (ALLOW_DATA_ATTR && !FORBID_ATTR[lcName] && regExpTest(DATA_ATTR$1, lcName)) ; else if (ALLOW_ARIA_ATTR && regExpTest(ARIA_ATTR$1, lcName)) ; else if (!ALLOWED_ATTR[lcName] || FORBID_ATTR[lcName]) {
      if ( // First condition does a very basic check if a) it's basically a valid custom element tagname AND
      // b) if the tagName passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
      // and c) if the attribute name passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.attributeNameCheck
      _basicCustomElementTest(lcTag) && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, lcTag) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(lcTag)) && (CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.attributeNameCheck, lcName) || CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.attributeNameCheck(lcName)) || // Alternative, second condition checks if it's an `is`-attribute, AND
      // the value passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
      lcName === 'is' && CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, value) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(value))) ; else {
        return false;
      }
      /* Check value is safe. First, is attr inert? If so, is safe */

    } else if (URI_SAFE_ATTRIBUTES[lcName]) ; else if (regExpTest(IS_ALLOWED_URI$1, stringReplace(value, ATTR_WHITESPACE$1, ''))) ; else if ((lcName === 'src' || lcName === 'xlink:href' || lcName === 'href') && lcTag !== 'script' && stringIndexOf(value, 'data:') === 0 && DATA_URI_TAGS[lcTag]) ; else if (ALLOW_UNKNOWN_PROTOCOLS && !regExpTest(IS_SCRIPT_OR_DATA$1, stringReplace(value, ATTR_WHITESPACE$1, ''))) ; else if (!value) ; else {
      return false;
    }

    return true;
  };
  /**
   * _basicCustomElementCheck
   * checks if at least one dash is included in tagName, and it's not the first char
   * for more sophisticated checking see https://github.com/sindresorhus/validate-element-name
   * @param {string} tagName name of the tag of the node to sanitize
   */


  var _basicCustomElementTest = function _basicCustomElementTest(tagName) {
    return tagName.indexOf('-') > 0;
  };
  /**
   * _sanitizeAttributes
   *
   * @protect attributes
   * @protect nodeName
   * @protect removeAttribute
   * @protect setAttribute
   *
   * @param  {Node} currentNode to sanitize
   */


  var _sanitizeAttributes = function _sanitizeAttributes(currentNode) {
    var attr;
    var value;
    var lcName;
    var l;
    /* Execute a hook if present */

    _executeHook('beforeSanitizeAttributes', currentNode, null);

    var attributes = currentNode.attributes;
    /* Check if we have attributes; if not we might have a text node */

    if (!attributes) {
      return;
    }

    var hookEvent = {
      attrName: '',
      attrValue: '',
      keepAttr: true,
      allowedAttributes: ALLOWED_ATTR
    };
    l = attributes.length;
    /* Go backwards over all attributes; safely remove bad ones */

    while (l--) {
      attr = attributes[l];
      var _attr = attr,
          name = _attr.name,
          namespaceURI = _attr.namespaceURI;
      value = name === 'value' ? attr.value : stringTrim(attr.value);
      lcName = transformCaseFunc(name);
      /* Execute a hook if present */

      hookEvent.attrName = lcName;
      hookEvent.attrValue = value;
      hookEvent.keepAttr = true;
      hookEvent.forceKeepAttr = undefined; // Allows developers to see this is a property they can set

      _executeHook('uponSanitizeAttribute', currentNode, hookEvent);

      value = hookEvent.attrValue;
      /* Did the hooks approve of the attribute? */

      if (hookEvent.forceKeepAttr) {
        continue;
      }
      /* Remove attribute */


      _removeAttribute(name, currentNode);
      /* Did the hooks approve of the attribute? */


      if (!hookEvent.keepAttr) {
        continue;
      }
      /* Work around a security issue in jQuery 3.0 */


      if (!ALLOW_SELF_CLOSE_IN_ATTR && regExpTest(/\/>/i, value)) {
        _removeAttribute(name, currentNode);

        continue;
      }
      /* Sanitize attribute content to be template-safe */


      if (SAFE_FOR_TEMPLATES) {
        value = stringReplace(value, MUSTACHE_EXPR$1, ' ');
        value = stringReplace(value, ERB_EXPR$1, ' ');
        value = stringReplace(value, TMPLIT_EXPR$1, ' ');
      }
      /* Is `value` valid for this attribute? */


      var lcTag = transformCaseFunc(currentNode.nodeName);

      if (!_isValidAttribute(lcTag, lcName, value)) {
        continue;
      }
      /* Full DOM Clobbering protection via namespace isolation,
       * Prefix id and name attributes with `user-content-`
       */


      if (SANITIZE_NAMED_PROPS && (lcName === 'id' || lcName === 'name')) {
        // Remove the attribute with this value
        _removeAttribute(name, currentNode); // Prefix the value and later re-create the attribute with the sanitized value


        value = SANITIZE_NAMED_PROPS_PREFIX + value;
      }
      /* Handle attributes that require Trusted Types */


      if (trustedTypesPolicy && _typeof$7(trustedTypes) === 'object' && typeof trustedTypes.getAttributeType === 'function') {
        if (namespaceURI) ; else {
          switch (trustedTypes.getAttributeType(lcTag, lcName)) {
            case 'TrustedHTML':
              value = trustedTypesPolicy.createHTML(value);
              break;

            case 'TrustedScriptURL':
              value = trustedTypesPolicy.createScriptURL(value);
              break;
          }
        }
      }
      /* Handle invalid data-* attribute set by try-catching it */


      try {
        if (namespaceURI) {
          currentNode.setAttributeNS(namespaceURI, name, value);
        } else {
          /* Fallback to setAttribute() for browser-unrecognized namespaces e.g. "x-schema". */
          currentNode.setAttribute(name, value);
        }

        arrayPop(DOMPurify.removed);
      } catch (_) {}
    }
    /* Execute a hook if present */


    _executeHook('afterSanitizeAttributes', currentNode, null);
  };
  /**
   * _sanitizeShadowDOM
   *
   * @param  {DocumentFragment} fragment to iterate over recursively
   */


  var _sanitizeShadowDOM = function _sanitizeShadowDOM(fragment) {
    var shadowNode;

    var shadowIterator = _createIterator(fragment);
    /* Execute a hook if present */


    _executeHook('beforeSanitizeShadowDOM', fragment, null);

    while (shadowNode = shadowIterator.nextNode()) {
      /* Execute a hook if present */
      _executeHook('uponSanitizeShadowNode', shadowNode, null);
      /* Sanitize tags and elements */


      if (_sanitizeElements(shadowNode)) {
        continue;
      }
      /* Deep shadow DOM detected */


      if (shadowNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(shadowNode.content);
      }
      /* Check attributes, sanitize if necessary */


      _sanitizeAttributes(shadowNode);
    }
    /* Execute a hook if present */


    _executeHook('afterSanitizeShadowDOM', fragment, null);
  };
  /**
   * Sanitize
   * Public method providing core sanitation functionality
   *
   * @param {String|Node} dirty string or DOM node
   * @param {Object} configuration object
   */
  // eslint-disable-next-line complexity


  DOMPurify.sanitize = function (dirty) {
    var cfg = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var body;
    var importedNode;
    var currentNode;
    var returnNode;
    /* Make sure we have a string to sanitize.
      DO NOT return early, as this will return the wrong type if
      the user has requested a DOM object rather than a string */

    IS_EMPTY_INPUT = !dirty;

    if (IS_EMPTY_INPUT) {
      dirty = '<!-->';
    }
    /* Stringify, in case dirty is an object */


    if (typeof dirty !== 'string' && !_isNode(dirty)) {
      // eslint-disable-next-line no-negated-condition
      if (typeof dirty.toString !== 'function') {
        throw typeErrorCreate('toString is not a function');
      } else {
        dirty = dirty.toString();

        if (typeof dirty !== 'string') {
          throw typeErrorCreate('dirty is not a string, aborting');
        }
      }
    }
    /* Return dirty HTML if DOMPurify cannot run */


    if (!DOMPurify.isSupported) {
      return dirty;
    }
    /* Assign config vars */


    if (!SET_CONFIG) {
      _parseConfig(cfg);
    }
    /* Clean up removed elements */


    DOMPurify.removed = [];
    /* Check if dirty is correctly typed for IN_PLACE */

    if (typeof dirty === 'string') {
      IN_PLACE = false;
    }

    if (IN_PLACE) {
      /* Do some early pre-sanitization to avoid unsafe root nodes */
      if (dirty.nodeName) {
        var tagName = transformCaseFunc(dirty.nodeName);

        if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
          throw typeErrorCreate('root node is forbidden and cannot be sanitized in-place');
        }
      }
    } else if (dirty instanceof Node) {
      /* If dirty is a DOM element, append to an empty document to avoid
         elements being stripped by the parser */
      body = _initDocument('<!---->');
      importedNode = body.ownerDocument.importNode(dirty, true);

      if (importedNode.nodeType === 1 && importedNode.nodeName === 'BODY') {
        /* Node is already a body, use as is */
        body = importedNode;
      } else if (importedNode.nodeName === 'HTML') {
        body = importedNode;
      } else {
        // eslint-disable-next-line unicorn/prefer-dom-node-append
        body.appendChild(importedNode);
      }
    } else {
      /* Exit directly if we have nothing to do */
      if (!RETURN_DOM && !SAFE_FOR_TEMPLATES && !WHOLE_DOCUMENT && // eslint-disable-next-line unicorn/prefer-includes
      dirty.indexOf('<') === -1) {
        return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(dirty) : dirty;
      }
      /* Initialize the document to work on */


      body = _initDocument(dirty);
      /* Check we have a DOM node from the data */

      if (!body) {
        return RETURN_DOM ? null : RETURN_TRUSTED_TYPE ? emptyHTML : '';
      }
    }
    /* Remove first element node (ours) if FORCE_BODY is set */


    if (body && FORCE_BODY) {
      _forceRemove(body.firstChild);
    }
    /* Get node iterator */


    var nodeIterator = _createIterator(IN_PLACE ? dirty : body);
    /* Now start iterating over the created document */


    while (currentNode = nodeIterator.nextNode()) {
      /* Sanitize tags and elements */
      if (_sanitizeElements(currentNode)) {
        continue;
      }
      /* Shadow DOM detected, sanitize it */


      if (currentNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(currentNode.content);
      }
      /* Check attributes, sanitize if necessary */


      _sanitizeAttributes(currentNode);
    }
    /* If we sanitized `dirty` in-place, return it. */


    if (IN_PLACE) {
      return dirty;
    }
    /* Return sanitized string or DOM */


    if (RETURN_DOM) {
      if (RETURN_DOM_FRAGMENT) {
        returnNode = createDocumentFragment.call(body.ownerDocument);

        while (body.firstChild) {
          // eslint-disable-next-line unicorn/prefer-dom-node-append
          returnNode.appendChild(body.firstChild);
        }
      } else {
        returnNode = body;
      }

      if (ALLOWED_ATTR.shadowroot || ALLOWED_ATTR.shadowrootmod) {
        /*
          AdoptNode() is not used because internal state is not reset
          (e.g. the past names map of a HTMLFormElement), this is safe
          in theory but we would rather not risk another attack vector.
          The state that is cloned by importNode() is explicitly defined
          by the specs.
        */
        returnNode = importNode.call(originalDocument, returnNode, true);
      }

      return returnNode;
    }

    var serializedHTML = WHOLE_DOCUMENT ? body.outerHTML : body.innerHTML;
    /* Serialize doctype if allowed */

    if (WHOLE_DOCUMENT && ALLOWED_TAGS['!doctype'] && body.ownerDocument && body.ownerDocument.doctype && body.ownerDocument.doctype.name && regExpTest(DOCTYPE_NAME, body.ownerDocument.doctype.name)) {
      serializedHTML = '<!DOCTYPE ' + body.ownerDocument.doctype.name + '>\n' + serializedHTML;
    }
    /* Sanitize final string template-safe */


    if (SAFE_FOR_TEMPLATES) {
      serializedHTML = stringReplace(serializedHTML, MUSTACHE_EXPR$1, ' ');
      serializedHTML = stringReplace(serializedHTML, ERB_EXPR$1, ' ');
      serializedHTML = stringReplace(serializedHTML, TMPLIT_EXPR$1, ' ');
    }

    return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(serializedHTML) : serializedHTML;
  };
  /**
   * Public method to set the configuration once
   * setConfig
   *
   * @param {Object} cfg configuration object
   */


  DOMPurify.setConfig = function (cfg) {
    _parseConfig(cfg);

    SET_CONFIG = true;
  };
  /**
   * Public method to remove the configuration
   * clearConfig
   *
   */


  DOMPurify.clearConfig = function () {
    CONFIG = null;
    SET_CONFIG = false;
  };
  /**
   * Public method to check if an attribute value is valid.
   * Uses last set config, if any. Otherwise, uses config defaults.
   * isValidAttribute
   *
   * @param  {string} tag Tag name of containing element.
   * @param  {string} attr Attribute name.
   * @param  {string} value Attribute value.
   * @return {Boolean} Returns true if `value` is valid. Otherwise, returns false.
   */


  DOMPurify.isValidAttribute = function (tag, attr, value) {
    /* Initialize shared config vars if necessary. */
    if (!CONFIG) {
      _parseConfig({});
    }

    var lcTag = transformCaseFunc(tag);
    var lcName = transformCaseFunc(attr);
    return _isValidAttribute(lcTag, lcName, value);
  };
  /**
   * AddHook
   * Public method to add DOMPurify hooks
   *
   * @param {String} entryPoint entry point for the hook to add
   * @param {Function} hookFunction function to execute
   */


  DOMPurify.addHook = function (entryPoint, hookFunction) {
    if (typeof hookFunction !== 'function') {
      return;
    }

    hooks[entryPoint] = hooks[entryPoint] || [];
    arrayPush$1(hooks[entryPoint], hookFunction);
  };
  /**
   * RemoveHook
   * Public method to remove a DOMPurify hook at a given entryPoint
   * (pops it from the stack of hooks if more are present)
   *
   * @param {String} entryPoint entry point for the hook to remove
   * @return {Function} removed(popped) hook
   */


  DOMPurify.removeHook = function (entryPoint) {
    if (hooks[entryPoint]) {
      return arrayPop(hooks[entryPoint]);
    }
  };
  /**
   * RemoveHooks
   * Public method to remove all DOMPurify hooks at a given entryPoint
   *
   * @param  {String} entryPoint entry point for the hooks to remove
   */


  DOMPurify.removeHooks = function (entryPoint) {
    if (hooks[entryPoint]) {
      hooks[entryPoint] = [];
    }
  };
  /**
   * RemoveAllHooks
   * Public method to remove all DOMPurify hooks
   *
   */


  DOMPurify.removeAllHooks = function () {
    hooks = {};
  };

  return DOMPurify;
}

var purify = createDOMPurify();

var safeHtml = {
  install(Vue, _options) {
    function getSanitizedValue(value = '') {
      if (purify?.sanitize) {
        return purify.sanitize(value)
      }
      return value || ''
    }

    Vue.directive('me-safe-html', {
      bind(el, binding, _vnode) {
        if (typeof el.innerHTML === 'string') {
          el.innerHTML = getSanitizedValue(binding.value);
        } else {
          el.data.domProps = { innerHTML: getSanitizedValue(binding.value) };
        }
      },

      componentUpdated(el, binding, vnode) {
        if (typeof el.innerHTML === 'string') {
          el.innerHTML = getSanitizedValue(binding.value);
        } else {
          el.data.domProps = { innerHTML: getSanitizedValue(binding.value) };
        }
      }
    });
  }
};

var directives = /*#__PURE__*/Object.freeze({
  __proto__: null,
  tooltip: tooltip,
  modal: modal,
  toast: toast,
  toggle: toggle,
  mask: mask,
  resize: resize,
  dragDrop: dragDrop,
  safeHtml: safeHtml
});

var toast$1 = {
  install(Vue, _options) {
    const ToastOnDemand = Vue.extend({
      name: 'MeToastOnDemand',
      extends: __vue_component__$2f,
      mounted() {
        this.show();
      }
    });

    const createToast = (options) => {
      const { content, variant, position, autoHideDelay, action, description } = options;
      return new ToastOnDemand({
        propsData: {
          content,
          variant,
          position,
          autoHideDelay,
          action,
          description
        }
      })
    };

    const show = (options = {}) => {
      const div = document.createElement('div');
      document.body.appendChild(div);

      const toast = createToast(options);
      toast.$mount(div);
    };

    const error = (options = {}) => {
      show({
        ...options,
        variant: 'danger'
      });
    };

    const success = (options = {}) => {
      show({
        ...options,
        variant: 'success'
      });
    };

    Vue.prototype.$meToast = {
      show,
      error,
      success
    };
  }
};

var vval = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.patchChildren = patchChildren;
exports.h = h;

function isUndef(v) {
  return v === null || v === undefined;
}

function isDef(v) {
  return v !== null && v !== undefined;
}

function sameVval(oldVval, vval) {
  return vval.tag === oldVval.tag && vval.key === oldVval.key;
}

function createVm(vval) {
  var Vm = vval.tag;
  vval.vm = new Vm({
    data: vval.args
  });
}

function updateVval(vval) {
  var keys = Object.keys(vval.args);

  for (var i = 0; i < keys.length; i++) {
    keys.forEach(function (k) {
      vval.vm[k] = vval.args[k];
    });
  }
}

function createKeyToOldIdx(children, beginIdx, endIdx) {
  var i, key;
  var map = {};

  for (i = beginIdx; i <= endIdx; ++i) {
    key = children[i].key;
    if (isDef(key)) map[key] = i;
  }

  return map;
}

function updateChildren(oldCh, newCh) {
  var oldStartIdx = 0;
  var newStartIdx = 0;
  var oldEndIdx = oldCh.length - 1;
  var oldStartVval = oldCh[0];
  var oldEndVval = oldCh[oldEndIdx];
  var newEndIdx = newCh.length - 1;
  var newStartVval = newCh[0];
  var newEndVval = newCh[newEndIdx];
  var oldKeyToIdx, idxInOld, elmToMove;

  while (oldStartIdx <= oldEndIdx && newStartIdx <= newEndIdx) {
    if (isUndef(oldStartVval)) {
      oldStartVval = oldCh[++oldStartIdx];
    } else if (isUndef(oldEndVval)) {
      oldEndVval = oldCh[--oldEndIdx];
    } else if (sameVval(oldStartVval, newStartVval)) {
      patchVval(oldStartVval, newStartVval);
      oldStartVval = oldCh[++oldStartIdx];
      newStartVval = newCh[++newStartIdx];
    } else if (sameVval(oldEndVval, newEndVval)) {
      patchVval(oldEndVval, newEndVval);
      oldEndVval = oldCh[--oldEndIdx];
      newEndVval = newCh[--newEndIdx];
    } else if (sameVval(oldStartVval, newEndVval)) {
      patchVval(oldStartVval, newEndVval);
      oldStartVval = oldCh[++oldStartIdx];
      newEndVval = newCh[--newEndIdx];
    } else if (sameVval(oldEndVval, newStartVval)) {
      patchVval(oldEndVval, newStartVval);
      oldEndVval = oldCh[--oldEndIdx];
      newStartVval = newCh[++newStartIdx];
    } else {
      if (isUndef(oldKeyToIdx)) oldKeyToIdx = createKeyToOldIdx(oldCh, oldStartIdx, oldEndIdx);
      idxInOld = isDef(newStartVval.key) ? oldKeyToIdx[newStartVval.key] : null;

      if (isUndef(idxInOld)) {
        createVm(newStartVval);
        newStartVval = newCh[++newStartIdx];
      } else {
        elmToMove = oldCh[idxInOld];

        if (sameVval(elmToMove, newStartVval)) {
          patchVval(elmToMove, newStartVval);
          oldCh[idxInOld] = undefined;
          newStartVval = newCh[++newStartIdx];
        } else {
          createVm(newStartVval);
          newStartVval = newCh[++newStartIdx];
        }
      }
    }
  }

  if (oldStartIdx > oldEndIdx) {
    addVvals(newCh, newStartIdx, newEndIdx);
  } else if (newStartIdx > newEndIdx) {
    removeVvals(oldCh, oldStartIdx, oldEndIdx);
  }
}

function addVvals(vvals, startIdx, endIdx) {
  for (; startIdx <= endIdx; ++startIdx) {
    createVm(vvals[startIdx]);
  }
}

function removeVvals(vvals, startIdx, endIdx) {
  for (; startIdx <= endIdx; ++startIdx) {
    var ch = vvals[startIdx];

    if (isDef(ch)) {
      ch.vm.$destroy();
      ch.vm = null;
    }
  }
}

function patchVval(oldVval, vval) {
  if (oldVval === vval) {
    return;
  }

  vval.vm = oldVval.vm;
  updateVval(vval);
}

function patchChildren(oldCh, ch) {
  if (isDef(oldCh) && isDef(ch)) {
    if (oldCh !== ch) updateChildren(oldCh, ch);
  } else if (isDef(ch)) {
    addVvals(ch, 0, ch.length - 1);
  } else if (isDef(oldCh)) {
    removeVvals(oldCh, 0, oldCh.length - 1);
  }
}

function h(tag, key, args) {
  return {
    tag: tag,
    key: key,
    args: args
  };
}
});

unwrapExports(vval);
var vval_1 = vval.patchChildren;
var vval_2 = vval.h;

var lib = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Vuelidate = Vuelidate;
Object.defineProperty(exports, "withParams", {
  enumerable: true,
  get: function get() {
    return params.withParams;
  }
});
exports.default = exports.validationMixin = void 0;





function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance"); }

function _iterableToArray(iter) { if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var NIL = function NIL() {
  return null;
};

var buildFromKeys = function buildFromKeys(keys, fn, keyFn) {
  return keys.reduce(function (build, key) {
    build[keyFn ? keyFn(key) : key] = fn(key);
    return build;
  }, {});
};

function isFunction(val) {
  return typeof val === 'function';
}

function isObject(val) {
  return val !== null && (_typeof(val) === 'object' || isFunction(val));
}

function isPromise(object) {
  return isObject(object) && isFunction(object.then);
}

var getPath = function getPath(ctx, obj, path, fallback) {
  if (typeof path === 'function') {
    return path.call(ctx, obj, fallback);
  }

  path = Array.isArray(path) ? path : path.split('.');

  for (var i = 0; i < path.length; i++) {
    if (obj && _typeof(obj) === 'object') {
      obj = obj[path[i]];
    } else {
      return fallback;
    }
  }

  return typeof obj === 'undefined' ? fallback : obj;
};

var __isVuelidateAsyncVm = '__isVuelidateAsyncVm';

function makePendingAsyncVm(Vue, promise) {
  var asyncVm = new Vue({
    data: {
      p: true,
      v: false
    }
  });
  promise.then(function (value) {
    asyncVm.p = false;
    asyncVm.v = value;
  }, function (error) {
    asyncVm.p = false;
    asyncVm.v = false;
    throw error;
  });
  asyncVm[__isVuelidateAsyncVm] = true;
  return asyncVm;
}

var validationGetters = {
  $invalid: function $invalid() {
    var _this = this;

    var proxy = this.proxy;
    return this.nestedKeys.some(function (nested) {
      return _this.refProxy(nested).$invalid;
    }) || this.ruleKeys.some(function (rule) {
      return !proxy[rule];
    });
  },
  $dirty: function $dirty() {
    var _this2 = this;

    if (this.dirty) {
      return true;
    }

    if (this.nestedKeys.length === 0) {
      return false;
    }

    return this.nestedKeys.every(function (key) {
      return _this2.refProxy(key).$dirty;
    });
  },
  $anyDirty: function $anyDirty() {
    var _this3 = this;

    if (this.dirty) {
      return true;
    }

    if (this.nestedKeys.length === 0) {
      return false;
    }

    return this.nestedKeys.some(function (key) {
      return _this3.refProxy(key).$anyDirty;
    });
  },
  $error: function $error() {
    return this.$dirty && !this.$pending && this.$invalid;
  },
  $anyError: function $anyError() {
    var _this4 = this;

    if (this.$error) return true;
    return this.nestedKeys.some(function (key) {
      return _this4.refProxy(key).$anyError;
    });
  },
  $pending: function $pending() {
    var _this5 = this;

    return this.ruleKeys.some(function (key) {
      return _this5.getRef(key).$pending;
    }) || this.nestedKeys.some(function (key) {
      return _this5.refProxy(key).$pending;
    });
  },
  $params: function $params() {
    var _this6 = this;

    var vals = this.validations;
    return _objectSpread({}, buildFromKeys(this.nestedKeys, function (key) {
      return vals[key] && vals[key].$params || null;
    }), {}, buildFromKeys(this.ruleKeys, function (key) {
      return _this6.getRef(key).$params;
    }));
  }
};

function setDirtyRecursive(newState) {
  this.dirty = newState;
  var proxy = this.proxy;
  var method = newState ? '$touch' : '$reset';
  this.nestedKeys.forEach(function (key) {
    proxy[key][method]();
  });
}

var validationMethods = {
  $touch: function $touch() {
    setDirtyRecursive.call(this, true);
  },
  $reset: function $reset() {
    setDirtyRecursive.call(this, false);
  },
  $flattenParams: function $flattenParams() {
    var proxy = this.proxy;
    var params = [];

    for (var key in this.$params) {
      if (this.isNested(key)) {
        var childParams = proxy[key].$flattenParams();

        for (var j = 0; j < childParams.length; j++) {
          childParams[j].path.unshift(key);
        }

        params = params.concat(childParams);
      } else {
        params.push({
          path: [],
          name: key,
          params: this.$params[key]
        });
      }
    }

    return params;
  }
};
var getterNames = Object.keys(validationGetters);
var methodNames = Object.keys(validationMethods);
var _cachedComponent = null;

var getComponent = function getComponent(Vue) {
  if (_cachedComponent) {
    return _cachedComponent;
  }

  var VBase = Vue.extend({
    computed: {
      refs: function refs() {
        var oldVval = this._vval;
        this._vval = this.children;
        (0, vval.patchChildren)(oldVval, this._vval);
        var refs = {};

        this._vval.forEach(function (c) {
          refs[c.key] = c.vm;
        });

        return refs;
      }
    },
    beforeCreate: function beforeCreate() {
      this._vval = null;
    },
    beforeDestroy: function beforeDestroy() {
      if (this._vval) {
        (0, vval.patchChildren)(this._vval);
        this._vval = null;
      }
    },
    methods: {
      getModel: function getModel() {
        return this.lazyModel ? this.lazyModel(this.prop) : this.model;
      },
      getModelKey: function getModelKey(key) {
        var model = this.getModel();

        if (model) {
          return model[key];
        }
      },
      hasIter: function hasIter() {
        return false;
      }
    }
  });
  var ValidationRule = VBase.extend({
    data: function data() {
      return {
        rule: null,
        lazyModel: null,
        model: null,
        lazyParentModel: null,
        rootModel: null
      };
    },
    methods: {
      runRule: function runRule(parent) {
        var model = this.getModel();
        (0, params.pushParams)();
        var rawOutput = this.rule.call(this.rootModel, model, parent);
        var output = isPromise(rawOutput) ? makePendingAsyncVm(Vue, rawOutput) : rawOutput;
        var rawParams = (0, params.popParams)();
        var params$1 = rawParams && rawParams.$sub ? rawParams.$sub.length > 1 ? rawParams : rawParams.$sub[0] : null;
        return {
          output: output,
          params: params$1
        };
      }
    },
    computed: {
      run: function run() {
        var _this7 = this;

        var parent = this.lazyParentModel();

        var isArrayDependant = Array.isArray(parent) && parent.__ob__;

        if (isArrayDependant) {
          var arrayDep = parent.__ob__.dep;
          arrayDep.depend();
          var target = arrayDep.constructor.target;

          if (!this._indirectWatcher) {
            var Watcher = target.constructor;
            this._indirectWatcher = new Watcher(this, function () {
              return _this7.runRule(parent);
            }, null, {
              lazy: true
            });
          }

          var model = this.getModel();

          if (!this._indirectWatcher.dirty && this._lastModel === model) {
            this._indirectWatcher.depend();

            return target.value;
          }

          this._lastModel = model;

          this._indirectWatcher.evaluate();

          this._indirectWatcher.depend();
        } else if (this._indirectWatcher) {
          this._indirectWatcher.teardown();

          this._indirectWatcher = null;
        }

        return this._indirectWatcher ? this._indirectWatcher.value : this.runRule(parent);
      },
      $params: function $params() {
        return this.run.params;
      },
      proxy: function proxy() {
        var output = this.run.output;

        if (output[__isVuelidateAsyncVm]) {
          return !!output.v;
        }

        return !!output;
      },
      $pending: function $pending() {
        var output = this.run.output;

        if (output[__isVuelidateAsyncVm]) {
          return output.p;
        }

        return false;
      }
    },
    destroyed: function destroyed() {
      if (this._indirectWatcher) {
        this._indirectWatcher.teardown();

        this._indirectWatcher = null;
      }
    }
  });
  var Validation = VBase.extend({
    data: function data() {
      return {
        dirty: false,
        validations: null,
        lazyModel: null,
        model: null,
        prop: null,
        lazyParentModel: null,
        rootModel: null
      };
    },
    methods: _objectSpread({}, validationMethods, {
      refProxy: function refProxy(key) {
        return this.getRef(key).proxy;
      },
      getRef: function getRef(key) {
        return this.refs[key];
      },
      isNested: function isNested(key) {
        return typeof this.validations[key] !== 'function';
      }
    }),
    computed: _objectSpread({}, validationGetters, {
      nestedKeys: function nestedKeys() {
        return this.keys.filter(this.isNested);
      },
      ruleKeys: function ruleKeys() {
        var _this8 = this;

        return this.keys.filter(function (k) {
          return !_this8.isNested(k);
        });
      },
      keys: function keys() {
        return Object.keys(this.validations).filter(function (k) {
          return k !== '$params';
        });
      },
      proxy: function proxy() {
        var _this9 = this;

        var keyDefs = buildFromKeys(this.keys, function (key) {
          return {
            enumerable: true,
            configurable: true,
            get: function get() {
              return _this9.refProxy(key);
            }
          };
        });
        var getterDefs = buildFromKeys(getterNames, function (key) {
          return {
            enumerable: true,
            configurable: true,
            get: function get() {
              return _this9[key];
            }
          };
        });
        var methodDefs = buildFromKeys(methodNames, function (key) {
          return {
            enumerable: false,
            configurable: true,
            get: function get() {
              return _this9[key];
            }
          };
        });
        var iterDefs = this.hasIter() ? {
          $iter: {
            enumerable: true,
            value: Object.defineProperties({}, _objectSpread({}, keyDefs))
          }
        } : {};
        return Object.defineProperties({}, _objectSpread({}, keyDefs, {}, iterDefs, {
          $model: {
            enumerable: true,
            get: function get() {
              var parent = _this9.lazyParentModel();

              if (parent != null) {
                return parent[_this9.prop];
              } else {
                return null;
              }
            },
            set: function set(value) {
              var parent = _this9.lazyParentModel();

              if (parent != null) {
                parent[_this9.prop] = value;

                _this9.$touch();
              }
            }
          }
        }, getterDefs, {}, methodDefs));
      },
      children: function children() {
        var _this10 = this;

        return [].concat(_toConsumableArray(this.nestedKeys.map(function (key) {
          return renderNested(_this10, key);
        })), _toConsumableArray(this.ruleKeys.map(function (key) {
          return renderRule(_this10, key);
        }))).filter(Boolean);
      }
    })
  });
  var GroupValidation = Validation.extend({
    methods: {
      isNested: function isNested(key) {
        return typeof this.validations[key]() !== 'undefined';
      },
      getRef: function getRef(key) {
        var vm = this;
        return {
          get proxy() {
            return vm.validations[key]() || false;
          }

        };
      }
    }
  });
  var EachValidation = Validation.extend({
    computed: {
      keys: function keys() {
        var model = this.getModel();

        if (isObject(model)) {
          return Object.keys(model);
        } else {
          return [];
        }
      },
      tracker: function tracker() {
        var _this11 = this;

        var trackBy = this.validations.$trackBy;
        return trackBy ? function (key) {
          return "".concat(getPath(_this11.rootModel, _this11.getModelKey(key), trackBy));
        } : function (x) {
          return "".concat(x);
        };
      },
      getModelLazy: function getModelLazy() {
        var _this12 = this;

        return function () {
          return _this12.getModel();
        };
      },
      children: function children() {
        var _this13 = this;

        var def = this.validations;
        var model = this.getModel();

        var validations = _objectSpread({}, def);

        delete validations['$trackBy'];
        var usedTracks = {};
        return this.keys.map(function (key) {
          var track = _this13.tracker(key);

          if (usedTracks.hasOwnProperty(track)) {
            return null;
          }

          usedTracks[track] = true;
          return (0, vval.h)(Validation, track, {
            validations: validations,
            prop: key,
            lazyParentModel: _this13.getModelLazy,
            model: model[key],
            rootModel: _this13.rootModel
          });
        }).filter(Boolean);
      }
    },
    methods: {
      isNested: function isNested() {
        return true;
      },
      getRef: function getRef(key) {
        return this.refs[this.tracker(key)];
      },
      hasIter: function hasIter() {
        return true;
      }
    }
  });

  var renderNested = function renderNested(vm, key) {
    if (key === '$each') {
      return (0, vval.h)(EachValidation, key, {
        validations: vm.validations[key],
        lazyParentModel: vm.lazyParentModel,
        prop: key,
        lazyModel: vm.getModel,
        rootModel: vm.rootModel
      });
    }

    var validations = vm.validations[key];

    if (Array.isArray(validations)) {
      var root = vm.rootModel;
      var refVals = buildFromKeys(validations, function (path) {
        return function () {
          return getPath(root, root.$v, path);
        };
      }, function (v) {
        return Array.isArray(v) ? v.join('.') : v;
      });
      return (0, vval.h)(GroupValidation, key, {
        validations: refVals,
        lazyParentModel: NIL,
        prop: key,
        lazyModel: NIL,
        rootModel: root
      });
    }

    return (0, vval.h)(Validation, key, {
      validations: validations,
      lazyParentModel: vm.getModel,
      prop: key,
      lazyModel: vm.getModelKey,
      rootModel: vm.rootModel
    });
  };

  var renderRule = function renderRule(vm, key) {
    return (0, vval.h)(ValidationRule, key, {
      rule: vm.validations[key],
      lazyParentModel: vm.lazyParentModel,
      lazyModel: vm.getModel,
      rootModel: vm.rootModel
    });
  };

  _cachedComponent = {
    VBase: VBase,
    Validation: Validation
  };
  return _cachedComponent;
};

var _cachedVue = null;

function getVue(rootVm) {
  if (_cachedVue) return _cachedVue;
  var Vue = rootVm.constructor;

  while (Vue.super) {
    Vue = Vue.super;
  }

  _cachedVue = Vue;
  return Vue;
}

var validateModel = function validateModel(model, validations) {
  var Vue = getVue(model);

  var _getComponent = getComponent(Vue),
      Validation = _getComponent.Validation,
      VBase = _getComponent.VBase;

  var root = new VBase({
    computed: {
      children: function children() {
        var vals = typeof validations === 'function' ? validations.call(model) : validations;
        return [(0, vval.h)(Validation, '$v', {
          validations: vals,
          lazyParentModel: NIL,
          prop: '$v',
          model: model,
          rootModel: model
        })];
      }
    }
  });
  return root;
};

var validationMixin = {
  data: function data() {
    var vals = this.$options.validations;

    if (vals) {
      this._vuelidate = validateModel(this, vals);
    }

    return {};
  },
  beforeCreate: function beforeCreate() {
    var options = this.$options;
    var vals = options.validations;
    if (!vals) return;
    if (!options.computed) options.computed = {};
    if (options.computed.$v) return;

    options.computed.$v = function () {
      return this._vuelidate ? this._vuelidate.refs.$v.proxy : null;
    };
  },
  beforeDestroy: function beforeDestroy() {
    if (this._vuelidate) {
      this._vuelidate.$destroy();

      this._vuelidate = null;
    }
  }
};
exports.validationMixin = validationMixin;

function Vuelidate(Vue) {
  Vue.mixin(validationMixin);
}

var _default = Vuelidate;
exports.default = _default;
});

var Vuelidate = unwrapExports(lib);
var lib_1 = lib.Vuelidate;
var lib_2 = lib.validationMixin;

var vuelidate = {
  install(Vue, _options) {
    Vue.use(Vuelidate);
  }
};

//

var script$2l = {
  name: 'MeConfirmModal',
  components: {
    Modal: __vue_component__$f,
    TextInput: __vue_component__$p,
    Button: __vue_component__$6
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    code: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md'
    },
    cancelTitle: {
      type: String,
      default: ''
    },
    okTitle: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      codeTyped: ''
    }
  },

  computed: {
    confirmed() {
      return this.code === this.codeTyped
    },

    variant() {
      if (this.confirmed) {
        return 'danger'
      }

      return 'outline-secondary'
    }
  },

  methods: {
    show() {
      this.$refs.modal.show();
    }
  }
};

/* script */
const __vue_script__$2l = script$2l;

/* template */
var __vue_render__$2l = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Modal",
    {
      ref: "modal",
      attrs: {
        title: _vm.title || _vm.$toolkit.t("modal.confirmation.title"),
        size: _vm.size
      },
      on: {
        hide: function($event) {
          return _vm.$emit("hide", $event)
        }
      },
      scopedSlots: _vm._u(
        [
          _vm.code
            ? {
                key: "modal-footer",
                fn: function(ref) {
                  var ok = ref.ok;
                  return [
                    _c(
                      "Button",
                      {
                        attrs: {
                          variant: _vm.variant,
                          disabled: !_vm.confirmed
                        },
                        on: {
                          click: function($event) {
                            return ok()
                          }
                        }
                      },
                      [
                        _vm._v(
                          "\n      " +
                            _vm._s(_vm.$toolkit.t("modal.confirm")) +
                            "\n    "
                        )
                      ]
                    )
                  ]
                }
              }
            : {
                key: "modal-footer",
                fn: function(ref) {
                  var ok = ref.ok;
                  var cancel = ref.cancel;
                  return [
                    _c(
                      "Button",
                      {
                        attrs: { variant: "light-primary" },
                        on: {
                          click: function($event) {
                            return cancel()
                          }
                        }
                      },
                      [
                        _vm._v(
                          "\n      " +
                            _vm._s(
                              _vm.cancelTitle || _vm.$toolkit.t("modal.no")
                            ) +
                            "\n    "
                        )
                      ]
                    ),
                    _vm._v(" "),
                    _c(
                      "Button",
                      {
                        attrs: { variant: "primary" },
                        on: {
                          click: function($event) {
                            return ok()
                          }
                        }
                      },
                      [
                        _vm._v(
                          "\n      " +
                            _vm._s(_vm.okTitle || _vm.$toolkit.t("modal.yes")) +
                            "\n    "
                        )
                      ]
                    )
                  ]
                }
              }
        ],
        null,
        true
      )
    },
    [
      _c("div", { staticClass: "me-modal-confirm" }, [
        _vm.code
          ? _c(
              "div",
              [
                _c("p", { staticClass: "text-danger" }, [
                  _vm._v(
                    "\n        " +
                      _vm._s(
                        _vm.message ||
                          _vm.$toolkit.t("modal.confirmation.message")
                      ) +
                      "\n      "
                  )
                ]),
                _vm._v(" "),
                _c("p", [
                  _vm._v(
                    "\n        " +
                      _vm._s(_vm.$toolkit.t("modal.confirmation.prevent")) +
                      "\n      "
                  )
                ]),
                _vm._v(" "),
                _c("p", {
                  directives: [
                    {
                      name: "me-safe-html",
                      rawName: "v-me-safe-html",
                      value: _vm.$toolkit.t(
                        "modal.confirmation.code",
                        _vm.code
                      ),
                      expression: "$toolkit.t('modal.confirmation.code', code)"
                    }
                  ]
                }),
                _vm._v(" "),
                _c("TextInput", {
                  model: {
                    value: _vm.codeTyped,
                    callback: function($$v) {
                      _vm.codeTyped = $$v;
                    },
                    expression: "codeTyped"
                  }
                })
              ],
              1
            )
          : _c("p", [
              _vm._v(
                "\n      " +
                  _vm._s(
                    _vm.message || _vm.$toolkit.t("modal.confirmation.message")
                  ) +
                  "\n    "
              )
            ])
      ])
    ]
  )
};
var __vue_staticRenderFns__$2l = [];
__vue_render__$2l._withStripped = true;

  /* style */
  const __vue_inject_styles__$2l = function (inject) {
    if (!inject) return
    inject("data-v-3ec7996d_0", { source: ".me-modal-confirm[data-v-3ec7996d] {\n  font-size: 14px;\n}\n.me-modal-confirm .text-danger[data-v-3ec7996d] {\n  font-weight: 600;\n}\n.me-modal-confirm code[data-v-3ec7996d] {\n  padding: 2px 4px;\n  color: #c0341d;\n  background-color: #fbe5e1;\n  border-radius: 4px;\n}\n\n/*# sourceMappingURL=ConfirmModal.vue.map */", map: {"version":3,"sources":["/app/build/src/components/ConfirmModal.vue","ConfirmModal.vue"],"names":[],"mappings":"AAiHA;EACA,eAAA;AChHA;ADkHA;EACA,gBAAA;AChHA;ADmHA;EACA,gBAAA;EACA,cAAA;EACA,yBAAA;EACA,kBAAA;ACjHA;;AAEA,2CAA2C","file":"ConfirmModal.vue","sourcesContent":["<template>\n  <Modal\n    ref=\"modal\"\n    :title=\"title || $toolkit.t('modal.confirmation.title')\"\n    :size=\"size\"\n    @hide=\"$emit('hide', $event)\"\n  >\n    <div class=\"me-modal-confirm\">\n      <div v-if=\"code\">\n        <p class=\"text-danger\">\n          {{ message || $toolkit.t('modal.confirmation.message') }}\n        </p>\n\n        <p>\n          {{ $toolkit.t('modal.confirmation.prevent') }}\n        </p>\n\n        <p v-me-safe-html=\"$toolkit.t('modal.confirmation.code', code)\" />\n        <TextInput v-model=\"codeTyped\" />\n      </div>\n\n      <p v-else>\n        {{ message || $toolkit.t('modal.confirmation.message') }}\n      </p>\n    </div>\n\n    <template v-if=\"code\" v-slot:modal-footer=\"{ ok }\">\n      <Button :variant=\"variant\" :disabled=\"!confirmed\" @click=\"ok()\">\n        {{ $toolkit.t('modal.confirm') }}\n      </Button>\n    </template>\n\n    <template v-else v-slot:modal-footer=\"{ ok, cancel }\">\n      <Button @click=\"cancel()\" variant=\"light-primary\">\n        {{ cancelTitle || $toolkit.t('modal.no') }}\n      </Button>\n\n      <Button @click=\"ok()\" variant=\"primary\">\n        {{ okTitle || $toolkit.t('modal.yes') }}\n      </Button>\n    </template>\n  </Modal>\n</template>\n\n<script>\nimport Modal from './modal/Modal.vue'\nimport TextInput from './TextInput.vue'\nimport Button from './Button.vue'\n\nexport default {\n  name: 'MeConfirmModal',\n  components: {\n    Modal,\n    TextInput,\n    Button\n  },\n\n  props: {\n    title: {\n      type: String,\n      default: ''\n    },\n    message: {\n      type: String,\n      default: ''\n    },\n    code: {\n      type: String,\n      default: ''\n    },\n    size: {\n      type: String,\n      default: 'md'\n    },\n    cancelTitle: {\n      type: String,\n      default: ''\n    },\n    okTitle: {\n      type: String,\n      default: ''\n    }\n  },\n\n  data() {\n    return {\n      codeTyped: ''\n    }\n  },\n\n  computed: {\n    confirmed() {\n      return this.code === this.codeTyped\n    },\n\n    variant() {\n      if (this.confirmed) {\n        return 'danger'\n      }\n\n      return 'outline-secondary'\n    }\n  },\n\n  methods: {\n    show() {\n      this.$refs.modal.show()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n  .me-modal-confirm {\n      font-size: 14px;\n\n      .text-danger {\n          font-weight: 600;\n      }\n\n      code {\n          padding: 2px 4px;\n          color: #c0341d;\n          background-color: #fbe5e1;\n          border-radius: 4px;\n      }\n  }\n</style>\n",".me-modal-confirm {\n  font-size: 14px;\n}\n.me-modal-confirm .text-danger {\n  font-weight: 600;\n}\n.me-modal-confirm code {\n  padding: 2px 4px;\n  color: #c0341d;\n  background-color: #fbe5e1;\n  border-radius: 4px;\n}\n\n/*# sourceMappingURL=ConfirmModal.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2l = "data-v-3ec7996d";
  /* module identifier */
  const __vue_module_identifier__$2l = undefined;
  /* functional template */
  const __vue_is_functional_template__$2l = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2l, staticRenderFns: __vue_staticRenderFns__$2l },
    __vue_inject_styles__$2l,
    __vue_script__$2l,
    __vue_scope_id__$2l,
    __vue_is_functional_template__$2l,
    __vue_module_identifier__$2l,
    false,
    createInjector,
    undefined,
    undefined
  );

const confirmModalService = (Vue) => {
  const defaultOptions = {
    title: '',
    message: '',
    code: '',
    size: 'md',
    cancelTitle: '',
    okTitle: ''
  };

  const confirmModal = () => {
    return Vue.extend({
      name: 'MeConfirmDialog',
      extends: __vue_component__$2l,
      mounted() {
        this.show();
      }
    })
  };

  const createModalInstance = (options) => {
    const { title, message, code, size, cancelTitle, okTitle } = { ...defaultOptions, ...options };
    const ConfirmDialog = confirmModal();

    return new ConfirmDialog({
      propsData: {
        title,
        message,
        code,
        size,
        cancelTitle,
        okTitle
      }
    })
  };

  return (options = {}) => {
    return new Promise((resolve) => {
      const div = document.createElement('div');
      document.body.appendChild(div);

      const dialog = createModalInstance(options);
      dialog.$mount(div);
      dialog.$on('hide', ({ trigger }) => {
        resolve(trigger === 'ok');
      });
    })
  }
};

//

const sidebarTypesEnum = {
  LIST: 'list',
  CARDS: 'cards',
  BUTTONS: 'buttons'
};

const widgetsSizes = {
  [widgetTypesEnum.LIST_LINK]: {
    w: 8,
    h: 7
  },
  [widgetTypesEnum.LIST_CARD]: {
    w: 16,
    h: 4
  },
  [widgetTypesEnum.LIST_BUTTON]: {
    w: 16,
    h: 3
  },
  [widgetTypesEnum.CARD_INDICATOR]: {
    w: 8,
    h: 3
  },
  [widgetTypesEnum.CARD_LINK]: {
    w: 8,
    h: 3
  }
};

const widgetsIds = {
  [widgetTypesEnum.LIST_LINK]: '1',
  [widgetTypesEnum.LIST_CARD]: '2',
  [widgetTypesEnum.LIST_BUTTON]: '3',
  [widgetTypesEnum.CARD_INDICATOR]: '4',
  [widgetTypesEnum.CARD_LINK]: '5'
};

const mobileWidth = 768;

var script$2m = {
  name: 'MeCreateWidgetModal',
  components: {
    Modal: __vue_component__$f,
    Dashboard: __vue_component__$1c
  },

  props: {
    indicatorFilters: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isMobile: false,
      sidebarTypesEnum,
      selectedWidget: null,
      widgetTypeSelected: {
        value: sidebarTypesEnum.LIST,
        label: this.$toolkit.t('dashboard.newWidgetModal.sidebar.types.list')
      },
      widgetTypeList: [
        {
          value: sidebarTypesEnum.LIST,
          label: this.$toolkit.t('dashboard.newWidgetModal.sidebar.types.list')
        },
        {
          value: sidebarTypesEnum.CARDS,
          label: this.$toolkit.t('dashboard.newWidgetModal.sidebar.types.cards')
        }
      ]
    }
  },

  computed: {
    activeTab() {
      return this.widgetTypeList.findIndex(type => type.value === this.widgetTypeSelected.value) || 0
    },

    widgetListLink() {
      return {
        ...widgetsSizes[widgetTypesEnum.LIST_LINK],
        x: 0,
        y: 0,
        i: widgetsIds[widgetTypesEnum.LIST_LINK],
        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.LIST_LINK],
        type: widgetTypesEnum.LIST_LINK,
        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.label'),
        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.LIST_LINK], list: [sidebarTypesEnum.LIST] }),
        items: [
          {
            id: 'list-link-0',
            type: widgetSubtypesEnum.INDICATOR,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.indicator'),
            value: 99,
            badgeValue: 0
          },
          {
            id: 'list-link-1',
            type: widgetSubtypesEnum.LINK,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.customLink')
          },
          {
            id: 'list-link-2',
            type: widgetSubtypesEnum.LINK,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.customLink')
          },
          {
            id: 'list-link-3',
            type: widgetSubtypesEnum.INDICATOR,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.indicator'),
            value: 99,
            badgeValue: 0
          },
          {
            id: 'list-link-4',
            type: widgetSubtypesEnum.INDICATOR,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.customLink'),
            value: 99,
            badgeValue: 0
          }
        ]
      }
    },

    widgetListCard() {
      return {
        ...widgetsSizes[widgetTypesEnum.LIST_CARD],
        x: widgetsSizes[widgetTypesEnum.LIST_LINK].w,
        y: 0,
        type: widgetTypesEnum.LIST_CARD,
        i: widgetsIds[widgetTypesEnum.LIST_CARD],
        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.LIST_CARD],
        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.label'),
        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.LIST_CARD], list: [sidebarTypesEnum.LIST] }),
        items: [
          {
            type: widgetSubtypesEnum.INDICATOR,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.items.indicator'),
            variant: 'primary',
            value: 99,
            badgeValue: 99
          },
          {
            type: widgetSubtypesEnum.INDICATOR,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.items.indicator'),
            value: 99,
            badgeValue: 99
          },
          {
            type: widgetSubtypesEnum.LINK,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.items.customLink')
          }
        ]
      }
    },

    widgetListButton() {
      return {
        ...widgetsSizes[widgetTypesEnum.LIST_BUTTON],
        x: widgetsSizes[widgetTypesEnum.LIST_LINK].w,
        y: widgetsSizes[widgetTypesEnum.LIST_CARD].h,
        i: widgetsIds[widgetTypesEnum.LIST_BUTTON],
        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.LIST_BUTTON],
        type: widgetTypesEnum.LIST_BUTTON,
        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.label'),
        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.LIST_BUTTON], list: [sidebarTypesEnum.LIST] }),
        items: [
          {
            type: widgetButtonTypesEnum.SIMPLE,
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.simple')
          },
          {
            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.multiple.title'),
            type: widgetButtonTypesEnum.MULTIPLE,
            links: [
              {
                label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.multiple.linkA')
              },
              {
                label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.multiple.linkB')
              }
            ]
          }
        ]
      }
    },

    widgetCardIndicator() {
      return {
        ...widgetsSizes[widgetTypesEnum.CARD_INDICATOR],
        x: 0,
        y: 0,
        i: widgetsIds[widgetTypesEnum.CARD_INDICATOR],
        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.CARD_INDICATOR],
        type: widgetTypesEnum.CARD_INDICATOR,
        value: 99,
        badgeValue: 99,
        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.cardIndicator.label'),
        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.CARD_INDICATOR], list: [sidebarTypesEnum.CARDS] })
      }
    },

    widgetCardLink() {
      return {
        ...widgetsSizes[widgetTypesEnum.CARD_LINK],
        x: widgetsSizes[widgetTypesEnum.CARD_INDICATOR].w,
        y: 0,
        i: widgetsIds[widgetTypesEnum.CARD_LINK],
        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.CARD_LINK],
        type: widgetTypesEnum.CARD_LINK,
        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.cardLink.label'),
        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.CARD_LINK], list: [sidebarTypesEnum.CARDS] })
      }
    },

    widgets() {
      return {
        [sidebarTypesEnum.LIST]: [
          this.widgetListLink,
          this.widgetListCard,
          this.widgetListButton
        ],
        [sidebarTypesEnum.CARDS]: [
          this.widgetCardIndicator,
          this.widgetCardLink
        ]
      }
    },

    footerActions() {
      return [
        {
          label: this.$toolkit.t('dashboard.newWidgetModal.footer.next'),
          click: close => this.goToNextStep(close),
          disabled: !this.selectedWidget
        },
        {
          label: this.$toolkit.t('dashboard.newWidgetModal.footer.cancel'),
          click: close => close()
        }
      ]
    }
  },

  mounted() {
    this.isMobile = window.innerWidth <= mobileWidth;
  },

  methods: {
    show() {
      window.addEventListener('resize', this.setIsMobile);
      this.$refs.modal.show();
    },

    hide() {
      window.removeEventListener('resize', this.setIsMobile);
    },

    setIsMobile: debounce_1(function () {
      this.isMobile = window.innerWidth <= mobileWidth;
    }, 500),

    setWidgetTypeSelected(selected) {
      this.widgetTypeSelected = selected;
    },

    changeTab(selectedTabIndex) {
      this.widgetTypeSelected = this.widgetTypeList[selectedTabIndex];
    },

    setSelectedWidget({ i, list }) {
      this.selectedWidget = this.widgets[list].find(widget => widget.i === i);
    },

    goToNextStep(cb) {
      this.$emit('next', this.selectedWidget);
      cb();
    }
  }
};

/* script */
const __vue_script__$2m = script$2m;

/* template */
var __vue_render__$2m = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "Modal",
    {
      ref: "modal",
      attrs: {
        buttons: _vm.footerActions,
        title: _vm.$toolkit.t("dashboard.newWidgetModal.title"),
        size: "xl",
        scrollable: ""
      },
      on: { hide: _vm.hide }
    },
    [
      _c("div", { attrs: { "data-cy": "selectWidgetModal" } }, [
        _c("p", [
          _vm._v(_vm._s(_vm.$toolkit.t("dashboard.newWidgetModal.subTitle")))
        ]),
        _vm._v(" "),
        _c(
          "div",
          { staticClass: "new-widget-content" },
          [
            _vm.isMobile
              ? _c(
                  "me-tabs",
                  {
                    staticClass: "w-100",
                    attrs: { value: _vm.activeTab },
                    on: { input: _vm.changeTab }
                  },
                  _vm._l(_vm.widgetTypeList, function(widgetType, index) {
                    return _c(
                      "me-tab",
                      {
                        key: widgetType.value,
                        staticClass: "widget-type-tab",
                        attrs: { title: widgetType.label }
                      },
                      [
                        _c("div", { staticClass: "content-cards__title" }, [
                          _vm._v(
                            "\n            " +
                              _vm._s(
                                _vm.$toolkit.t(
                                  "dashboard.newWidgetModal.content.title"
                                )
                              ) +
                              "\n          "
                          )
                        ]),
                        _vm._v(" "),
                        _vm.widgets[_vm.widgetTypeSelected.value] &&
                        _vm.activeTab === index
                          ? _c("Dashboard", {
                              key: _vm.widgetTypeSelected.value,
                              attrs: {
                                widgets:
                                  _vm.widgets[_vm.widgetTypeSelected.value],
                                "is-responsive": ""
                              }
                            })
                          : _vm._e()
                      ],
                      1
                    )
                  }),
                  1
                )
              : _vm._e(),
            _vm._v(" "),
            !_vm.isMobile
              ? [
                  _c("div", { staticClass: "content-sidebar" }, [
                    _c("span", { staticClass: "sidebar-title" }, [
                      _vm._v(
                        _vm._s(
                          _vm.$toolkit.t(
                            "dashboard.newWidgetModal.sidebar.types.title"
                          )
                        )
                      )
                    ]),
                    _vm._v(" "),
                    _c(
                      "ul",
                      { staticClass: "sidebar-list" },
                      _vm._l(_vm.widgetTypeList, function(widgetType) {
                        return _c(
                          "li",
                          {
                            key: widgetType.value,
                            staticClass: "sidebar-list-item",
                            class: {
                              "sidebar-list-item--active":
                                widgetType.value ===
                                _vm.widgetTypeSelected.value
                            },
                            attrs: {
                              "data-cy": "sidebarItem_" + widgetType.value
                            },
                            on: {
                              click: function() {
                                return _vm.setWidgetTypeSelected(widgetType)
                              }
                            }
                          },
                          [
                            _vm._v(
                              "\n              " +
                                _vm._s(widgetType.label) +
                                "\n            "
                            )
                          ]
                        )
                      }),
                      0
                    )
                  ]),
                  _vm._v(" "),
                  _c("div", { staticClass: "content-cards" }, [
                    _c("div", { staticClass: "content-cards__title" }, [
                      _vm._v(
                        "\n            " +
                          _vm._s(
                            _vm.$toolkit.t(
                              "dashboard.newWidgetModal.content.title"
                            )
                          ) +
                          "\n          "
                      )
                    ]),
                    _vm._v(" "),
                    _c(
                      "div",
                      {
                        staticClass: "overflow-auto",
                        staticStyle: { height: "100%", width: "100%" }
                      },
                      [
                        _vm.widgets[_vm.widgetTypeSelected.value]
                          ? _c("Dashboard", {
                              key: _vm.widgetTypeSelected.value,
                              attrs: {
                                widgets:
                                  _vm.widgets[_vm.widgetTypeSelected.value],
                                "is-responsive": ""
                              }
                            })
                          : _vm._e()
                      ],
                      1
                    )
                  ])
                ]
              : _vm._e()
          ],
          2
        )
      ])
    ]
  )
};
var __vue_staticRenderFns__$2m = [];
__vue_render__$2m._withStripped = true;

  /* style */
  const __vue_inject_styles__$2m = function (inject) {
    if (!inject) return
    inject("data-v-1c03c32a_0", { source: ".new-widget-content[data-v-1c03c32a] {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  display: flex;\n  overflow: hidden;\n}\n.new-widget-content .widget-type-tab[data-v-1c03c32a] {\n  width: 100%;\n  max-height: 50vh;\n  overflow: auto;\n  margin-top: 16px;\n}\n.new-widget-content .content-sidebar[data-v-1c03c32a] {\n  display: flex;\n  flex-direction: column;\n  border-right: 1px solid var(--me-gray-6);\n  padding: 8px 0;\n  flex-grow: 1;\n  min-width: 256px;\n}\n.new-widget-content .content-sidebar .sidebar-title[data-v-1c03c32a] {\n  padding: 0 16px;\n  height: 36px;\n  display: flex;\n  align-items: center;\n  text-transform: capitalize;\n  font-weight: 600;\n  font-size: 14px;\n  color: var(--me-gray-4);\n}\n.new-widget-content .content-sidebar .sidebar-list[data-v-1c03c32a] {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\n.new-widget-content .content-sidebar .sidebar-list .sidebar-list-item[data-v-1c03c32a] {\n  height: 36px;\n  display: flex;\n  align-items: center;\n  padding: 0 16px 0 40px;\n  cursor: pointer;\n}\n.new-widget-content .content-sidebar .sidebar-list .sidebar-list-item--active[data-v-1c03c32a], .new-widget-content .content-sidebar .sidebar-list .sidebar-list-item[data-v-1c03c32a]:hover {\n  background-color: var(--me-primary-7);\n}\n.new-widget-content .content-sidebar .sidebar-list .sidebar-list-item--active[data-v-1c03c32a] {\n  color: var(--me-primary-2);\n  font-weight: 600;\n}\n.new-widget-content .content-cards[data-v-1c03c32a] {\n  background-color: var(--me-bg-gray);\n  flex-grow: 3;\n  padding: 24px;\n  padding-left: 8px;\n}\n.new-widget-content .content-cards__title[data-v-1c03c32a] {\n  padding-left: 16px;\n  font-weight: 600;\n}\n\n/*# sourceMappingURL=SelectWidgetModal.vue.map */", map: {"version":3,"sources":["/app/build/src/components/dashboard/create/SelectWidgetModal.vue","SelectWidgetModal.vue"],"names":[],"mappings":"AAgXA;EACA,kCAAA;EACA,kBAAA;EACA,aAAA;EACA,gBAAA;AC/WA;ADiXA;EACA,WAAA;EACA,gBAAA;EACA,cAAA;EACA,gBAAA;AC/WA;ADkXA;EACA,aAAA;EACA,sBAAA;EACA,wCAAA;EACA,cAAA;EACA,YAAA;EACA,gBAAA;AChXA;ADkXA;EACA,eAAA;EACA,YAzBA;EA0BA,aAAA;EACA,mBAAA;EACA,0BAAA;EACA,gBAAA;EACA,eAAA;EACA,uBAAA;AChXA;ADmXA;EACA,gBAAA;EACA,SAAA;EACA,UAAA;ACjXA;ADmXA;EACA,YAxCA;EAyCA,aAAA;EACA,mBAAA;EACA,sBAAA;EACA,eAAA;ACjXA;ADmXA;EAEA,qCAAA;AClXA;ADqXA;EACA,0BAAA;EACA,gBAAA;ACnXA;ADyXA;EACA,mCAAA;EACA,YAAA;EACA,aAAA;EACA,iBAAA;ACvXA;ADyXA;EACA,kBAAA;EACA,gBAAA;ACvXA;;AAEA,gDAAgD","file":"SelectWidgetModal.vue","sourcesContent":["<template>\n  <Modal\n    ref=\"modal\"\n    :buttons=\"footerActions\"\n    :title=\"$toolkit.t('dashboard.newWidgetModal.title')\"\n    @hide=\"hide\"\n    size=\"xl\"\n    scrollable\n  >\n    <div data-cy=\"selectWidgetModal\">\n      <p>{{ $toolkit.t('dashboard.newWidgetModal.subTitle') }}</p>\n\n      <div class=\"new-widget-content\">\n        <me-tabs\n          v-if=\"isMobile\"\n          :value=\"activeTab\"\n          @input=\"changeTab\"\n          class=\"w-100\"\n        >\n          <me-tab\n            v-for=\"(widgetType, index) in widgetTypeList\"\n            :key=\"widgetType.value\"\n            :title=\"widgetType.label\"\n            class=\"widget-type-tab\"\n          >\n            <div class=\"content-cards__title\">\n              {{ $toolkit.t('dashboard.newWidgetModal.content.title') }}\n            </div>\n\n            <Dashboard\n              v-if=\"widgets[widgetTypeSelected.value] && activeTab === index\"\n              :key=\"widgetTypeSelected.value\"\n              :widgets=\"widgets[widgetTypeSelected.value]\"\n              is-responsive\n            />\n          </me-tab>\n        </me-tabs>\n\n        <template v-if=\"!isMobile\">\n          <div class=\"content-sidebar\">\n            <span class=\"sidebar-title\">{{ $toolkit.t('dashboard.newWidgetModal.sidebar.types.title') }}</span>\n            <ul class=\"sidebar-list\">\n              <li\n                v-for=\"widgetType in widgetTypeList\"\n                :key=\"widgetType.value\"\n                @click=\"() => setWidgetTypeSelected(widgetType)\"\n                :class=\"{'sidebar-list-item--active': widgetType.value === widgetTypeSelected.value }\"\n                :data-cy=\"`sidebarItem_${widgetType.value}`\"\n                class=\"sidebar-list-item\"\n              >\n                {{ widgetType.label }}\n              </li>\n            </ul>\n          </div>\n\n          <div class=\"content-cards\">\n            <div class=\"content-cards__title\">\n              {{ $toolkit.t('dashboard.newWidgetModal.content.title') }}\n            </div>\n\n            <div style=\"height: 100%; width: 100%\" class=\"overflow-auto\">\n              <Dashboard\n                v-if=\"widgets[widgetTypeSelected.value]\"\n                :key=\"widgetTypeSelected.value\"\n                :widgets=\"widgets[widgetTypeSelected.value]\"\n                is-responsive\n              />\n            </div>\n          </div>\n        </template>\n      </div>\n    </div>\n  </Modal>\n</template>\n\n<script>\nimport debounce from 'lodash/debounce'\nimport Modal from '../../modal/Modal.vue'\nimport Dashboard from '../Dashboard.vue'\nimport { widgetTypesEnum, widgetSubtypesEnum, widgetButtonTypesEnum } from '../enums/widgetTypesEnum.js'\n\nconst sidebarTypesEnum = {\n  LIST: 'list',\n  CARDS: 'cards',\n  BUTTONS: 'buttons'\n}\n\nconst widgetsSizes = {\n  [widgetTypesEnum.LIST_LINK]: {\n    w: 8,\n    h: 7\n  },\n  [widgetTypesEnum.LIST_CARD]: {\n    w: 16,\n    h: 4\n  },\n  [widgetTypesEnum.LIST_BUTTON]: {\n    w: 16,\n    h: 3\n  },\n  [widgetTypesEnum.CARD_INDICATOR]: {\n    w: 8,\n    h: 3\n  },\n  [widgetTypesEnum.CARD_LINK]: {\n    w: 8,\n    h: 3\n  }\n}\n\nconst widgetsIds = {\n  [widgetTypesEnum.LIST_LINK]: '1',\n  [widgetTypesEnum.LIST_CARD]: '2',\n  [widgetTypesEnum.LIST_BUTTON]: '3',\n  [widgetTypesEnum.CARD_INDICATOR]: '4',\n  [widgetTypesEnum.CARD_LINK]: '5'\n}\n\nconst mobileWidth = 768\n\nexport default {\n  name: 'MeCreateWidgetModal',\n  components: {\n    Modal,\n    Dashboard\n  },\n\n  props: {\n    indicatorFilters: {\n      type: Array,\n      default: () => []\n    }\n  },\n\n  data() {\n    return {\n      isMobile: false,\n      sidebarTypesEnum,\n      selectedWidget: null,\n      widgetTypeSelected: {\n        value: sidebarTypesEnum.LIST,\n        label: this.$toolkit.t('dashboard.newWidgetModal.sidebar.types.list')\n      },\n      widgetTypeList: [\n        {\n          value: sidebarTypesEnum.LIST,\n          label: this.$toolkit.t('dashboard.newWidgetModal.sidebar.types.list')\n        },\n        {\n          value: sidebarTypesEnum.CARDS,\n          label: this.$toolkit.t('dashboard.newWidgetModal.sidebar.types.cards')\n        }\n      ]\n    }\n  },\n\n  computed: {\n    activeTab() {\n      return this.widgetTypeList.findIndex(type => type.value === this.widgetTypeSelected.value) || 0\n    },\n\n    widgetListLink() {\n      return {\n        ...widgetsSizes[widgetTypesEnum.LIST_LINK],\n        x: 0,\n        y: 0,\n        i: widgetsIds[widgetTypesEnum.LIST_LINK],\n        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.LIST_LINK],\n        type: widgetTypesEnum.LIST_LINK,\n        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.label'),\n        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.LIST_LINK], list: [sidebarTypesEnum.LIST] }),\n        items: [\n          {\n            id: 'list-link-0',\n            type: widgetSubtypesEnum.INDICATOR,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.indicator'),\n            value: 99,\n            badgeValue: 0\n          },\n          {\n            id: 'list-link-1',\n            type: widgetSubtypesEnum.LINK,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.customLink')\n          },\n          {\n            id: 'list-link-2',\n            type: widgetSubtypesEnum.LINK,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.customLink')\n          },\n          {\n            id: 'list-link-3',\n            type: widgetSubtypesEnum.INDICATOR,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.indicator'),\n            value: 99,\n            badgeValue: 0\n          },\n          {\n            id: 'list-link-4',\n            type: widgetSubtypesEnum.INDICATOR,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listLink.items.customLink'),\n            value: 99,\n            badgeValue: 0\n          }\n        ]\n      }\n    },\n\n    widgetListCard() {\n      return {\n        ...widgetsSizes[widgetTypesEnum.LIST_CARD],\n        x: widgetsSizes[widgetTypesEnum.LIST_LINK].w,\n        y: 0,\n        type: widgetTypesEnum.LIST_CARD,\n        i: widgetsIds[widgetTypesEnum.LIST_CARD],\n        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.LIST_CARD],\n        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.label'),\n        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.LIST_CARD], list: [sidebarTypesEnum.LIST] }),\n        items: [\n          {\n            type: widgetSubtypesEnum.INDICATOR,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.items.indicator'),\n            variant: 'primary',\n            value: 99,\n            badgeValue: 99\n          },\n          {\n            type: widgetSubtypesEnum.INDICATOR,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.items.indicator'),\n            value: 99,\n            badgeValue: 99\n          },\n          {\n            type: widgetSubtypesEnum.LINK,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listCard.items.customLink')\n          }\n        ]\n      }\n    },\n\n    widgetListButton() {\n      return {\n        ...widgetsSizes[widgetTypesEnum.LIST_BUTTON],\n        x: widgetsSizes[widgetTypesEnum.LIST_LINK].w,\n        y: widgetsSizes[widgetTypesEnum.LIST_CARD].h,\n        i: widgetsIds[widgetTypesEnum.LIST_BUTTON],\n        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.LIST_BUTTON],\n        type: widgetTypesEnum.LIST_BUTTON,\n        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.label'),\n        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.LIST_BUTTON], list: [sidebarTypesEnum.LIST] }),\n        items: [\n          {\n            type: widgetButtonTypesEnum.SIMPLE,\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.simple')\n          },\n          {\n            label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.multiple.title'),\n            type: widgetButtonTypesEnum.MULTIPLE,\n            links: [\n              {\n                label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.multiple.linkA')\n              },\n              {\n                label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.listButton.items.multiple.linkB')\n              }\n            ]\n          }\n        ]\n      }\n    },\n\n    widgetCardIndicator() {\n      return {\n        ...widgetsSizes[widgetTypesEnum.CARD_INDICATOR],\n        x: 0,\n        y: 0,\n        i: widgetsIds[widgetTypesEnum.CARD_INDICATOR],\n        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.CARD_INDICATOR],\n        type: widgetTypesEnum.CARD_INDICATOR,\n        value: 99,\n        badgeValue: 99,\n        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.cardIndicator.label'),\n        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.CARD_INDICATOR], list: [sidebarTypesEnum.CARDS] })\n      }\n    },\n\n    widgetCardLink() {\n      return {\n        ...widgetsSizes[widgetTypesEnum.CARD_LINK],\n        x: widgetsSizes[widgetTypesEnum.CARD_INDICATOR].w,\n        y: 0,\n        i: widgetsIds[widgetTypesEnum.CARD_LINK],\n        active: this.selectedWidget?.i === widgetsIds[widgetTypesEnum.CARD_LINK],\n        type: widgetTypesEnum.CARD_LINK,\n        label: this.$toolkit.t('dashboard.newWidgetModal.content.widgets.cardLink.label'),\n        click: () => this.setSelectedWidget({ i: widgetsIds[widgetTypesEnum.CARD_LINK], list: [sidebarTypesEnum.CARDS] })\n      }\n    },\n\n    widgets() {\n      return {\n        [sidebarTypesEnum.LIST]: [\n          this.widgetListLink,\n          this.widgetListCard,\n          this.widgetListButton\n        ],\n        [sidebarTypesEnum.CARDS]: [\n          this.widgetCardIndicator,\n          this.widgetCardLink\n        ]\n      }\n    },\n\n    footerActions() {\n      return [\n        {\n          label: this.$toolkit.t('dashboard.newWidgetModal.footer.next'),\n          click: close => this.goToNextStep(close),\n          disabled: !this.selectedWidget\n        },\n        {\n          label: this.$toolkit.t('dashboard.newWidgetModal.footer.cancel'),\n          click: close => close()\n        }\n      ]\n    }\n  },\n\n  mounted() {\n    this.isMobile = window.innerWidth <= mobileWidth\n  },\n\n  methods: {\n    show() {\n      window.addEventListener('resize', this.setIsMobile)\n      this.$refs.modal.show()\n    },\n\n    hide() {\n      window.removeEventListener('resize', this.setIsMobile)\n    },\n\n    setIsMobile: debounce(function () {\n      this.isMobile = window.innerWidth <= mobileWidth\n    }, 500),\n\n    setWidgetTypeSelected(selected) {\n      this.widgetTypeSelected = selected\n    },\n\n    changeTab(selectedTabIndex) {\n      this.widgetTypeSelected = this.widgetTypeList[selectedTabIndex]\n    },\n\n    setSelectedWidget({ i, list }) {\n      this.selectedWidget = this.widgets[list].find(widget => widget.i === i)\n    },\n\n    goToNextStep(cb) {\n      this.$emit('next', this.selectedWidget)\n      cb()\n    }\n  }\n}\n</script>\n\n<style lang=\"scss\" scoped>\n$sidebar-item-height: 36px;\n\n  .new-widget-content {\n    border: 1px solid var(--me-gray-6);\n    border-radius: 8px;\n    display: flex;\n    overflow: hidden;\n\n    .widget-type-tab {\n      width: 100%;\n      max-height: 50vh;\n      overflow: auto;\n      margin-top: 16px;\n    }\n\n    .content-sidebar {\n      display: flex;\n      flex-direction: column;\n      border-right: 1px solid var(--me-gray-6);\n      padding: 8px 0;\n      flex-grow: 1;\n      min-width: 256px;\n\n      .sidebar-title {\n        padding: 0 16px;\n        height: $sidebar-item-height;\n        display: flex;\n        align-items: center;\n        text-transform: capitalize;\n        font-weight: 600;\n        font-size: 14px;\n        color: var(--me-gray-4);\n      }\n\n      .sidebar-list {\n        list-style: none;\n        margin: 0;\n        padding: 0;\n\n        .sidebar-list-item {\n          height: $sidebar-item-height;\n          display: flex;\n          align-items: center;\n          padding: 0 16px 0 40px;\n          cursor: pointer;\n\n          &--active,\n          &:hover {\n            background-color: var(--me-primary-7);\n          }\n\n          &--active {\n            color: var(--me-primary-2);\n            font-weight: 600;\n          }\n        }\n      }\n    }\n\n    .content-cards {\n      background-color: var(--me-bg-gray);\n      flex-grow: 3;\n      padding: 24px;\n      padding-left: 8px;\n\n      &__title {\n        padding-left: 16px;\n        font-weight: 600;\n      }\n    }\n  }\n\n</style>\n",".new-widget-content {\n  border: 1px solid var(--me-gray-6);\n  border-radius: 8px;\n  display: flex;\n  overflow: hidden;\n}\n.new-widget-content .widget-type-tab {\n  width: 100%;\n  max-height: 50vh;\n  overflow: auto;\n  margin-top: 16px;\n}\n.new-widget-content .content-sidebar {\n  display: flex;\n  flex-direction: column;\n  border-right: 1px solid var(--me-gray-6);\n  padding: 8px 0;\n  flex-grow: 1;\n  min-width: 256px;\n}\n.new-widget-content .content-sidebar .sidebar-title {\n  padding: 0 16px;\n  height: 36px;\n  display: flex;\n  align-items: center;\n  text-transform: capitalize;\n  font-weight: 600;\n  font-size: 14px;\n  color: var(--me-gray-4);\n}\n.new-widget-content .content-sidebar .sidebar-list {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n}\n.new-widget-content .content-sidebar .sidebar-list .sidebar-list-item {\n  height: 36px;\n  display: flex;\n  align-items: center;\n  padding: 0 16px 0 40px;\n  cursor: pointer;\n}\n.new-widget-content .content-sidebar .sidebar-list .sidebar-list-item--active, .new-widget-content .content-sidebar .sidebar-list .sidebar-list-item:hover {\n  background-color: var(--me-primary-7);\n}\n.new-widget-content .content-sidebar .sidebar-list .sidebar-list-item--active {\n  color: var(--me-primary-2);\n  font-weight: 600;\n}\n.new-widget-content .content-cards {\n  background-color: var(--me-bg-gray);\n  flex-grow: 3;\n  padding: 24px;\n  padding-left: 8px;\n}\n.new-widget-content .content-cards__title {\n  padding-left: 16px;\n  font-weight: 600;\n}\n\n/*# sourceMappingURL=SelectWidgetModal.vue.map */"]}, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$2m = "data-v-1c03c32a";
  /* module identifier */
  const __vue_module_identifier__$2m = undefined;
  /* functional template */
  const __vue_is_functional_template__$2m = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2m, staticRenderFns: __vue_staticRenderFns__$2m },
    __vue_inject_styles__$2m,
    __vue_script__$2m,
    __vue_scope_id__$2m,
    __vue_is_functional_template__$2m,
    __vue_module_identifier__$2m,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$2n = {
  name: 'MeCreateWidgetModal',

  props: {
    indicatorFilters: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      stepModal: __vue_component__$2m,
      modalEvents: { next: this.setNextStep },
      modalProps: this.indicatorFilters
    }
  },

  methods: {
    show() {
      this.$refs.modal.show();
    },

    setNextStep(selectedWidget) {
      if (!selectedWidget) return

      this.modalProps = {
        visible: true,
        widgetType: selectedWidget.type,
        indicatorFilters: this.indicatorFilters,
        scrollable: true
      };

      this.modalEvents = {
        save: (form, close) => this.$emit('save', { ...form, type: selectedWidget.type }, close)
      };

      this.stepModal = __vue_component__$1a;
    }
  }
};

/* script */
const __vue_script__$2n = script$2n;

/* template */
var __vue_render__$2n = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    _vm.stepModal,
    _vm._g(
      _vm._b({ ref: "modal", tag: "div" }, "div", _vm.modalProps, false),
      _vm.modalEvents
    )
  )
};
var __vue_staticRenderFns__$2n = [];
__vue_render__$2n._withStripped = true;

  /* style */
  const __vue_inject_styles__$2n = undefined;
  /* scoped */
  const __vue_scope_id__$2n = undefined;
  /* module identifier */
  const __vue_module_identifier__$2n = undefined;
  /* functional template */
  const __vue_is_functional_template__$2n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2n, staticRenderFns: __vue_staticRenderFns__$2n },
    __vue_inject_styles__$2n,
    __vue_script__$2n,
    __vue_scope_id__$2n,
    __vue_is_functional_template__$2n,
    __vue_module_identifier__$2n,
    false,
    undefined,
    undefined,
    undefined
  );

const createWidgetModalService = (Vue) => {
  const defaultOptions = {
    indicatorFilters: {}
  };

  const createWidgetModal = () => {
    return Vue.extend({
      name: 'MeCreateWidgetModal',
      extends: __vue_component__$2n,
      mounted() {
        this.show();
      }
    })
  };

  const createModalInstance = (options) => {
    const { indicatorFilters } = { ...defaultOptions, ...options };
    const CreateWidgetModal = createWidgetModal();

    return new CreateWidgetModal({
      propsData: {
        indicatorFilters: indicatorFilters
      }
    })
  };

  return (options = {}) => {
    return new Promise((resolve) => {
      const div = document.createElement('div');
      document.body.appendChild(div);

      const dialog = createModalInstance(options);
      dialog.$mount(div);
      dialog.$on('save', (widget, hide) => {
        resolve({ widget, hide });
      });
    })
  }
};

var modal$1 = {
  install(Vue, _options) {
    const confirmModal = confirmModalService(Vue);
    const createWidgetModal = createWidgetModalService(Vue);

    Vue.prototype.$meModal = {
      confirm: confirmModal,
      newWidget: createWidgetModal
    };
  }
};

var download = {
  install(Vue, _options) {
    const DownloadComponent = Vue.extend({
      name: 'MeDownload',
      extends: __vue_component__$1f
    });

    const createDownload = (options) => {
      const { label, fileName } = options;
      return new DownloadComponent({
        propsData: {
          label,
          fileName,
          visible: true
        }
      })
    };

    const setLabel = (ctx, label) => {
      ctx.label = label;
    };

    const setError = (ctx, error) => {
      ctx.error = error;
    };

    const downloadURL = (url) => {
      location.href = url;
    };

    const start = (options = {}, downloadFn) => {
      const div = document.createElement('div');
      document.body.appendChild(div);

      const downloadInstance = createDownload(options);

      downloadInstance.$on('update:visible', (value) => {
        downloadInstance.visible = value;
      });

      downloadInstance.$mount(div);
      downloadFn({
        hide: downloadInstance.hide,
        setLabel: label => setLabel(downloadInstance, label),
        setError: error => setError(downloadInstance, error),
        downloadURL
      });
    };

    Vue.prototype.$meDownload = {
      start
    };
  }
};

var vueEllipseProgress_umd_min = createCommonjsModule(function (module, exports) {
(function(t,e){module.exports=e();})("undefined"!==typeof self?self:commonjsGlobal,(function(){return function(t){var e={};function r(n){if(e[n])return e[n].exports;var i=e[n]={i:n,l:!1,exports:{}};return t[n].call(i.exports,i,i.exports,r),i.l=!0,i.exports}return r.m=t,r.c=e,r.d=function(t,e,n){r.o(t,e)||Object.defineProperty(t,e,{enumerable:!0,get:n});},r.r=function(t){"undefined"!==typeof Symbol&&Symbol.toStringTag&&Object.defineProperty(t,Symbol.toStringTag,{value:"Module"}),Object.defineProperty(t,"__esModule",{value:!0});},r.t=function(t,e){if(1&e&&(t=r(t)),8&e)return t;if(4&e&&"object"===typeof t&&t&&t.__esModule)return t;var n=Object.create(null);if(r.r(n),Object.defineProperty(n,"default",{enumerable:!0,value:t}),2&e&&"string"!=typeof t)for(var i in t)r.d(n,i,function(e){return t[e]}.bind(null,i));return n},r.n=function(t){var e=t&&t.__esModule?function(){return t["default"]}:function(){return t};return r.d(e,"a",e),e},r.o=function(t,e){return Object.prototype.hasOwnProperty.call(t,e)},r.p="",r(r.s="fb15")}({"00ee":function(t,e,r){var n=r("b622"),i=n("toStringTag"),o={};o[i]="z",t.exports="[object z]"===String(o);},"01f3":function(t,e,r){var n=r("6d40"),i=r.n(n);i.a;},"02d4":function(t,e,r){var n=r("24fb");e=n(!1),e.push([t.i,"@-webkit-keyframes ep-dot--init__loop{0%{-webkit-transform:rotate(var(--ep-dot-start));transform:rotate(var(--ep-dot-start))}33%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}66%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}to{-webkit-transform:rotate(var(--ep-dot-loop-end));transform:rotate(var(--ep-dot-loop-end))}}@keyframes ep-dot--init__loop{0%{-webkit-transform:rotate(var(--ep-dot-start));transform:rotate(var(--ep-dot-start))}33%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}66%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}to{-webkit-transform:rotate(var(--ep-dot-loop-end));transform:rotate(var(--ep-dot-loop-end))}}@-webkit-keyframes ep-dot--init__reverse{0%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}50%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}to{-webkit-transform:rotate(var(--ep-dot-end));transform:rotate(var(--ep-dot-end))}}@keyframes ep-dot--init__reverse{0%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}50%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}to{-webkit-transform:rotate(var(--ep-dot-end));transform:rotate(var(--ep-dot-end))}}@-webkit-keyframes ep-dot--init__bounce{0%{opacity:0}90%{opacity:0}to{opacity:1}}@keyframes ep-dot--init__bounce{0%{opacity:0}90%{opacity:0}to{opacity:1}}@-webkit-keyframes ep-dot--init__disabled{0%{opacity:0}90%{opacity:0}to{opacity:1}}@keyframes ep-dot--init__disabled{0%{opacity:0}90%{opacity:0}to{opacity:1}}.ep-circle--progress{-webkit-animation-timing-function:ease-in-out;animation-timing-function:ease-in-out}.ep-circle--progress.animation__default{-webkit-animation-name:ep-progress--init__default;animation-name:ep-progress--init__default}.ep-circle--progress.animation__rs{-webkit-animation-name:ep-progress--init__rs;animation-name:ep-progress--init__rs}.ep-circle--progress.animation__bounce{-webkit-animation-name:ep-progress--init__bounce;animation-name:ep-progress--init__bounce}.ep-circle--progress.animation__reverse{-webkit-animation-name:ep-progress--init__reverse;animation-name:ep-progress--init__reverse}.ep-circle--progress.animation__loop{-webkit-animation-name:ep-progress--init__loop;animation-name:ep-progress--init__loop}.ep-circle--loading.animation__loading{-webkit-animation-name:ep-progress--loading,ep-progress--loading__rotation;animation-name:ep-progress--loading,ep-progress--loading__rotation;-webkit-animation-iteration-count:infinite!important;animation-iteration-count:infinite!important;-webkit-animation-duration:2s,1s!important;animation-duration:2s,1s!important;-webkit-animation-timing-function:ease-in-out,linear;animation-timing-function:ease-in-out,linear}.ep-half-circle--loading.animation__loading{-webkit-animation-name:ep-half-progress--loading;animation-name:ep-half-progress--loading;-webkit-animation-iteration-count:infinite!important;animation-iteration-count:infinite!important;-webkit-animation-duration:2s!important;animation-duration:2s!important;-webkit-animation-timing-function:ease-in-out;animation-timing-function:ease-in-out}.ep-circle--empty.ep-circle--nodata,.ep-half-circle--empty.ep-circle--nodata{opacity:.5}.ep-circle--progress__dot-container{-webkit-animation-timing-function:ease-in-out;animation-timing-function:ease-in-out}.ep-circle--progress__dot-container.animation__rs{-webkit-animation-name:ep-dot--init__rs;animation-name:ep-dot--init__rs}.ep-circle--progress__dot-container.animation__bounce{-webkit-animation-fill-mode:forwards;animation-fill-mode:forwards;-webkit-animation-name:ep-dot--init__disabled;animation-name:ep-dot--init__disabled}.ep-circle--progress__dot-container.animation__reverse{-webkit-animation-name:ep-dot--init__reverse;animation-name:ep-dot--init__reverse}.ep-circle--progress__dot-container.animation__loop{-webkit-animation-name:ep-dot--init__loop;animation-name:ep-dot--init__loop}.ep-circle--progress__dot-container.ep-half-circle-progress__dot.animation__bounce,.ep-circle--progress__dot-container.ep-half-circle-progress__dot.animation__loop{-webkit-animation-fill-mode:forwards;animation-fill-mode:forwards;-webkit-animation-name:ep-dot--init__disabled;animation-name:ep-dot--init__disabled}@-webkit-keyframes ep-progress--init__default{0%{stroke-dashoffset:var(--ep-circumference)}to{stroke-dashoffset:var(--ep-stroke-offset)}}@keyframes ep-progress--init__default{0%{stroke-dashoffset:var(--ep-circumference)}to{stroke-dashoffset:var(--ep-stroke-offset)}}@-webkit-keyframes ep-progress--init__rs{0%{stroke-dashoffset:var(--ep-circumference)}50%{stroke-dashoffset:0}to{stroke-dashoffset:var(--ep-stroke-offset)}}@keyframes ep-progress--init__rs{0%{stroke-dashoffset:var(--ep-circumference)}50%{stroke-dashoffset:0}to{stroke-dashoffset:var(--ep-stroke-offset)}}@-webkit-keyframes ep-progress--init__bounce{0%{-webkit-animation-timing-function:linear;animation-timing-function:linear;stroke-dashoffset:var(--ep-circumference)}33%{stroke-dashoffset:var(--ep-bounce-out-stroke-offset)}66%{stroke-dashoffset:var(--ep-bounce-in-stroke-offset)}to{stroke-dashoffset:var(--ep-stroke-offset)}}@keyframes ep-progress--init__bounce{0%{-webkit-animation-timing-function:linear;animation-timing-function:linear;stroke-dashoffset:var(--ep-circumference)}33%{stroke-dashoffset:var(--ep-bounce-out-stroke-offset)}66%{stroke-dashoffset:var(--ep-bounce-in-stroke-offset)}to{stroke-dashoffset:var(--ep-stroke-offset)}}@-webkit-keyframes ep-progress--init__reverse{0%{stroke-dashoffset:var(--ep-circumference)}50%{stroke-dashoffset:var(--ep-double-circumference)}to{stroke-dashoffset:var(--ep-reverse-stroke-offset)}}@keyframes ep-progress--init__reverse{0%{stroke-dashoffset:var(--ep-circumference)}50%{stroke-dashoffset:var(--ep-double-circumference)}to{stroke-dashoffset:var(--ep-reverse-stroke-offset)}}@-webkit-keyframes ep-progress--init__loop{0%{stroke-dashoffset:var(--ep-circumference)}33%{stroke-dashoffset:0}66%{stroke-dashoffset:var(--ep-negative-circumference)}to{stroke-dashoffset:var(--ep-loop-stroke-offset)}}@keyframes ep-progress--init__loop{0%{stroke-dashoffset:var(--ep-circumference)}33%{stroke-dashoffset:0}66%{stroke-dashoffset:var(--ep-negative-circumference)}to{stroke-dashoffset:var(--ep-loop-stroke-offset)}}@-webkit-keyframes ep-progress--loading{0%{opacity:.5;stroke-dashoffset:var(--ep-circumference)}50%{opacity:.8;stroke-dashoffset:var(--ep-loading-stroke-offset)}to{opacity:.5;stroke-dashoffset:var(--ep-circumference)}}@keyframes ep-progress--loading{0%{opacity:.5;stroke-dashoffset:var(--ep-circumference)}50%{opacity:.8;stroke-dashoffset:var(--ep-loading-stroke-offset)}to{opacity:.5;stroke-dashoffset:var(--ep-circumference)}}@-webkit-keyframes ep-half-progress--loading{0%{opacity:.5;stroke-dashoffset:var(--ep-circumference)}50%{opacity:.8;stroke-dashoffset:0}to{opacity:.5;stroke-dashoffset:var(--ep-circumference)}}@keyframes ep-half-progress--loading{0%{opacity:.5;stroke-dashoffset:var(--ep-circumference)}50%{opacity:.8;stroke-dashoffset:0}to{opacity:.5;stroke-dashoffset:var(--ep-circumference)}}@-webkit-keyframes ep-progress--loading__rotation{to{-webkit-transform:rotate(1turn);transform:rotate(1turn)}}@keyframes ep-progress--loading__rotation{to{-webkit-transform:rotate(1turn);transform:rotate(1turn)}}@-webkit-keyframes ep-dot--init__rs{0%{-webkit-transform:rotate(var(--ep-dot-start));transform:rotate(var(--ep-dot-start))}50%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}to{-webkit-transform:rotate(var(--ep-dot-end));transform:rotate(var(--ep-dot-end))}}@keyframes ep-dot--init__rs{0%{-webkit-transform:rotate(var(--ep-dot-start));transform:rotate(var(--ep-dot-start))}50%{-webkit-transform:rotate(var(--ep-dot-360));transform:rotate(var(--ep-dot-360))}to{-webkit-transform:rotate(var(--ep-dot-end));transform:rotate(var(--ep-dot-end))}}.ep-svg-container{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;-webkit-box-align:center;-ms-flex-align:center;align-items:center;position:absolute}.ep-svg-container.ep-reverse{-webkit-transform:scaleX(-1);transform:scaleX(-1)}g.ep-circle--container{-webkit-transition:inherit;-o-transition:inherit;transition:inherit;-webkit-transform-origin:50% 50%;transform-origin:50% 50%}",""]),t.exports=e;},"0366":function(t,e,r){var n=r("1c0b");t.exports=function(t,e,r){if(n(t),void 0===e)return t;switch(r){case 0:return function(){return t.call(e)};case 1:return function(r){return t.call(e,r)};case 2:return function(r,n){return t.call(e,r,n)};case 3:return function(r,n,i){return t.call(e,r,n,i)}}return function(){return t.apply(e,arguments)}};},"054f":function(t,e,r){var n=r("a231"),i=r.n(n);i.a;},"057f":function(t,e,r){var n=r("fc6a"),i=r("241c").f,o={}.toString,a="object"==typeof window&&window&&Object.getOwnPropertyNames?Object.getOwnPropertyNames(window):[],s=function(t){try{return i(t)}catch(e){return a.slice()}};t.exports.f=function(t){return a&&"[object Window]"==o.call(t)?s(t):i(n(t))};},"06cf":function(t,e,r){var n=r("83ab"),i=r("d1e7"),o=r("5c6c"),a=r("fc6a"),s=r("c04e"),c=r("5135"),u=r("0cfb"),f=Object.getOwnPropertyDescriptor;e.f=n?f:function(t,e){if(t=a(t),e=s(e,!0),u)try{return f(t,e)}catch(r){}if(c(t,e))return o(!i.f.call(t,e),t[e])};},"0cfb":function(t,e,r){var n=r("83ab"),i=r("d039"),o=r("cc12");t.exports=!n&&!i((function(){return 7!=Object.defineProperty(o("div"),"a",{get:function(){return 7}}).a}));},1148:function(t,e,r){var n=r("a691"),i=r("1d80");t.exports="".repeat||function(t){var e=String(i(this)),r="",o=n(t);if(o<0||o==1/0)throw RangeError("Wrong number of repetitions");for(;o>0;(o>>>=1)&&(e+=e))1&o&&(r+=e);return r};},"11ae":function(t,e,r){var n=r("24fb");e=n(!1),e.push([t.i,".ep-container[data-v-59d6a78d]{display:inline-block;overflow:hidden}.ep-content[data-v-59d6a78d]{max-width:inherit;display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-pack:center;-ms-flex-pack:center;justify-content:center;-webkit-box-align:center;-ms-flex-align:center;align-items:center;position:relative;height:100%;width:100%}.ep-content[data-v-59d6a78d],.ep-legend--container[data-v-59d6a78d]{-webkit-transition:inherit;-o-transition:inherit;transition:inherit}.ep-legend--container[data-v-59d6a78d]{position:absolute;text-align:center}.ep-legend--value[data-v-59d6a78d]{-webkit-transition:.3s;-o-transition:.3s;transition:.3s;text-align:center;opacity:1}.ep-hidden[data-v-59d6a78d]{opacity:0}svg.ep-svg[data-v-59d6a78d]{-webkit-transition:inherit;-o-transition:inherit;transition:inherit;-webkit-transform-origin:50% 50%;transform-origin:50% 50%}",""]),t.exports=e;},1276:function(t,e,r){var n=r("d784"),i=r("44e7"),o=r("825a"),a=r("1d80"),s=r("4840"),c=r("8aa5"),u=r("50c4"),f=r("14c3"),l=r("9263"),p=r("d039"),d=[].push,h=Math.min,v=4294967295,m=!p((function(){return !RegExp(v,"y")}));n("split",2,(function(t,e,r){var n;return n="c"=="abbc".split(/(b)*/)[1]||4!="test".split(/(?:)/,-1).length||2!="ab".split(/(?:ab)*/).length||4!=".".split(/(.?)(.?)/).length||".".split(/()()/).length>1||"".split(/.?/).length?function(t,r){var n=String(a(this)),o=void 0===r?v:r>>>0;if(0===o)return [];if(void 0===t)return [n];if(!i(t))return e.call(n,t,o);var s,c,u,f=[],p=(t.ignoreCase?"i":"")+(t.multiline?"m":"")+(t.unicode?"u":"")+(t.sticky?"y":""),h=0,m=new RegExp(t.source,p+"g");while(s=l.call(m,n)){if(c=m.lastIndex,c>h&&(f.push(n.slice(h,s.index)),s.length>1&&s.index<n.length&&d.apply(f,s.slice(1)),u=s[0].length,h=c,f.length>=o))break;m.lastIndex===s.index&&m.lastIndex++;}return h===n.length?!u&&m.test("")||f.push(""):f.push(n.slice(h)),f.length>o?f.slice(0,o):f}:"0".split(void 0,0).length?function(t,r){return void 0===t&&0===r?[]:e.call(this,t,r)}:e,[function(e,r){var i=a(this),o=void 0==e?void 0:e[t];return void 0!==o?o.call(e,i,r):n.call(String(i),e,r)},function(t,i){var a=r(n,t,this,i,n!==e);if(a.done)return a.value;var l=o(t),p=String(this),d=s(l,RegExp),g=l.unicode,y=(l.ignoreCase?"i":"")+(l.multiline?"m":"")+(l.unicode?"u":"")+(m?"y":"g"),b=new d(m?l:"^(?:"+l.source+")",y),x=void 0===i?v:i>>>0;if(0===x)return [];if(0===p.length)return null===f(b,p)?[p]:[];var k=0,w=0,_=[];while(w<p.length){b.lastIndex=m?w:0;var S,E=f(b,m?p:p.slice(w));if(null===E||(S=h(u(b.lastIndex+(m?0:w)),p.length))===k)w=c(p,w,g);else {if(_.push(p.slice(k,w)),_.length===x)return _;for(var T=1;T<=E.length-1;T++)if(_.push(E[T]),_.length===x)return _;w=k=S;}}return _.push(p.slice(k)),_}]}),!m);},"129f":function(t,e){t.exports=Object.is||function(t,e){return t===e?0!==t||1/t===1/e:t!=t&&e!=e};},"13d5":function(t,e,r){var n=r("23e7"),i=r("d58f").left,o=r("a640"),a=r("ae40"),s=o("reduce"),c=a("reduce",{1:0});n({target:"Array",proto:!0,forced:!s||!c},{reduce:function(t){return i(this,t,arguments.length,arguments.length>1?arguments[1]:void 0)}});},"14c3":function(t,e,r){var n=r("c6b6"),i=r("9263");t.exports=function(t,e){var r=t.exec;if("function"===typeof r){var o=r.call(t,e);if("object"!==typeof o)throw TypeError("RegExp exec method returned something other than an Object or null");return o}if("RegExp"!==n(t))throw TypeError("RegExp#exec called on incompatible receiver");return i.call(t,e)};},"159b":function(t,e,r){var n=r("da84"),i=r("fdbc"),o=r("17c2"),a=r("9112");for(var s in i){var c=n[s],u=c&&c.prototype;if(u&&u.forEach!==o)try{a(u,"forEach",o);}catch(f){u.forEach=o;}}},"17c2":function(t,e,r){var n=r("b727").forEach,i=r("a640"),o=r("ae40"),a=i("forEach"),s=o("forEach");t.exports=a&&s?[].forEach:function(t){return n(this,t,arguments.length>1?arguments[1]:void 0)};},"19aa":function(t,e){t.exports=function(t,e,r){if(!(t instanceof e))throw TypeError("Incorrect "+(r?r+" ":"")+"invocation");return t};},"1be4":function(t,e,r){var n=r("d066");t.exports=n("document","documentElement");},"1c0b":function(t,e){t.exports=function(t){if("function"!=typeof t)throw TypeError(String(t)+" is not a function");return t};},"1c7e":function(t,e,r){var n=r("b622"),i=n("iterator"),o=!1;try{var a=0,s={next:function(){return {done:!!a++}},return:function(){o=!0;}};s[i]=function(){return this},Array.from(s,(function(){throw 2}));}catch(c){}t.exports=function(t,e){if(!e&&!o)return !1;var r=!1;try{var n={};n[i]=function(){return {next:function(){return {done:r=!0}}}},t(n);}catch(c){}return r};},"1cdc":function(t,e,r){var n=r("342f");t.exports=/(iphone|ipod|ipad).*applewebkit/i.test(n);},"1d1a":function(t,e,r){var n=r("24fb");e=n(!1),e.push([t.i,"g.ep-half-circle[data-v-5b37ebc3]{-webkit-transform-origin:50% 50%;transform-origin:50% 50%}",""]),t.exports=e;},"1d80":function(t,e){t.exports=function(t){if(void 0==t)throw TypeError("Can't call method on "+t);return t};},"1dde":function(t,e,r){var n=r("d039"),i=r("b622"),o=r("2d00"),a=i("species");t.exports=function(t){return o>=51||!n((function(){var e=[],r=e.constructor={};return r[a]=function(){return {foo:1}},1!==e[t](Boolean).foo}))};},2266:function(t,e,r){var n=r("825a"),i=r("e95a"),o=r("50c4"),a=r("0366"),s=r("35a1"),c=r("9bdd"),u=function(t,e){this.stopped=t,this.result=e;},f=t.exports=function(t,e,r,f,l){var p,d,h,v,m,g,y,b=a(e,r,f?2:1);if(l)p=t;else {if(d=s(t),"function"!=typeof d)throw TypeError("Target is not iterable");if(i(d)){for(h=0,v=o(t.length);v>h;h++)if(m=f?b(n(y=t[h])[0],y[1]):b(t[h]),m&&m instanceof u)return m;return new u(!1)}p=d.call(t);}g=p.next;while(!(y=g.call(p)).done)if(m=c(p,b,y.value,f),"object"==typeof m&&m&&m instanceof u)return m;return new u(!1)};f.stop=function(t){return new u(!0,t)};},"23cb":function(t,e,r){var n=r("a691"),i=Math.max,o=Math.min;t.exports=function(t,e){var r=n(t);return r<0?i(r+e,0):o(r,e)};},"23e7":function(t,e,r){var n=r("da84"),i=r("06cf").f,o=r("9112"),a=r("6eeb"),s=r("ce4e"),c=r("e893"),u=r("94ca");t.exports=function(t,e){var r,f,l,p,d,h,v=t.target,m=t.global,g=t.stat;if(f=m?n:g?n[v]||s(v,{}):(n[v]||{}).prototype,f)for(l in e){if(d=e[l],t.noTargetGet?(h=i(f,l),p=h&&h.value):p=f[l],r=u(m?l:v+(g?".":"#")+l,t.forced),!r&&void 0!==p){if(typeof d===typeof p)continue;c(d,p);}(t.sham||p&&p.sham)&&o(d,"sham",!0),a(f,l,d,t);}};},"241c":function(t,e,r){var n=r("ca84"),i=r("7839"),o=i.concat("length","prototype");e.f=Object.getOwnPropertyNames||function(t){return n(t,o)};},2474:function(t,e,r){var n=r("d07f"),i=r.n(n);i.a;},"24fb":function(t,e,r){function n(t,e){var r=t[1]||"",n=t[3];if(!n)return r;if(e&&"function"===typeof btoa){var o=i(n),a=n.sources.map((function(t){return "/*# sourceURL=".concat(n.sourceRoot||"").concat(t," */")}));return [r].concat(a).concat([o]).join("\n")}return [r].join("\n")}function i(t){var e=btoa(unescape(encodeURIComponent(JSON.stringify(t)))),r="sourceMappingURL=data:application/json;charset=utf-8;base64,".concat(e);return "/*# ".concat(r," */")}t.exports=function(t){var e=[];return e.toString=function(){return this.map((function(e){var r=n(e,t);return e[2]?"@media ".concat(e[2]," {").concat(r,"}"):r})).join("")},e.i=function(t,r,n){"string"===typeof t&&(t=[[null,t,""]]);var i={};if(n)for(var o=0;o<this.length;o++){var a=this[o][0];null!=a&&(i[a]=!0);}for(var s=0;s<t.length;s++){var c=[].concat(t[s]);n&&i[c[0]]||(r&&(c[2]?c[2]="".concat(r," and ").concat(c[2]):c[2]=r),e.push(c));}},e};},2532:function(t,e,r){var n=r("23e7"),i=r("5a34"),o=r("1d80"),a=r("ab13");n({target:"String",proto:!0,forced:!a("includes")},{includes:function(t){return !!~String(o(this)).indexOf(i(t),arguments.length>1?arguments[1]:void 0)}});},"25f0":function(t,e,r){var n=r("6eeb"),i=r("825a"),o=r("d039"),a=r("ad6d"),s="toString",c=RegExp.prototype,u=c[s],f=o((function(){return "/a/b"!=u.call({source:"a",flags:"b"})})),l=u.name!=s;(f||l)&&n(RegExp.prototype,s,(function(){var t=i(this),e=String(t.source),r=t.flags,n=String(void 0===r&&t instanceof RegExp&&!("flags"in c)?a.call(t):r);return "/"+e+"/"+n}),{unsafe:!0});},2626:function(t,e,r){var n=r("d066"),i=r("9bf2"),o=r("b622"),a=r("83ab"),s=o("species");t.exports=function(t){var e=n(t),r=i.f;a&&e&&!e[s]&&r(e,s,{configurable:!0,get:function(){return this}});};},"273a":function(t,e,r){var n=r("d30c"),i=r.n(n);i.a;},"28ab":function(t,e,r){var n=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("div",{staticClass:"ep-container",style:{width:t.size+"px",height:t.size+"px"}},[r("div",{staticClass:"ep-content"},[t._l(t.circlesData,(function(e,n){return r("circle-container",t._b({key:n,attrs:{multiple:t.isMultiple,index:n,globalThickness:t.thickness,globalGap:t.gap,globalDot:t.dot}},"circle-container",e,!1))})),r("div",{staticClass:"ep-legend--container",style:{maxWidth:t.size+"px"}},[t.legend&&!t.isMultiple?r("div",{staticClass:"ep-legend--value",class:[t.legendClass,{"ep-hidden":t.shouldHideLegendValue}],style:{fontSize:t.fontSize,color:t.fontColor}},[r("counter",{attrs:{value:t.legendVal,animation:t.animation,loading:t.loading},scopedSlots:t._u([{key:"default",fn:function(e){var n=e.counterTick;return [t.$scopedSlots.default?t._t("default",null,{counterTick:n}):t._e(),t.legendFormatter?r("span",[t.isHTML?r("span",{domProps:{innerHTML:t._s(t.legendFormatter(n))}}):r("span",[t._v(t._s(t.legendFormatter(n)))])]):t.$scopedSlots.default?t._e():r("span",[t._v(t._s(n.currentFormattedValue))])]}}],null,!0)}),t._t("legend-value")],2):t._e(),t._t("legend-caption")],2)],2)])},i=[];r("d81d"),r("d3b7"),r("25f0"),r("498a"),r("a4d3"),r("4de4"),r("4160"),r("e439"),r("dbb4"),r("b64b"),r("159b");function o(t,e,r){return e in t?Object.defineProperty(t,e,{value:r,enumerable:!0,configurable:!0,writable:!0}):t[e]=r,t}function a(t,e){var r=Object.keys(t);if(Object.getOwnPropertySymbols){var n=Object.getOwnPropertySymbols(t);e&&(n=n.filter((function(e){return Object.getOwnPropertyDescriptor(t,e).enumerable}))),r.push.apply(r,n);}return r}function s(t){for(var e=1;e<arguments.length;e++){var r=null!=arguments[e]?arguments[e]:{};e%2?a(Object(r),!0).forEach((function(e){o(t,e,r[e]);})):Object.getOwnPropertyDescriptors?Object.defineProperties(t,Object.getOwnPropertyDescriptors(r)):a(Object(r)).forEach((function(e){Object.defineProperty(t,e,Object.getOwnPropertyDescriptor(r,e));}));}return t}r("a9e3"),r("9129");var c=function(t){return void 0!==t&&""!==t&&null!==t&&!Number.isNaN(parseFloat(t))},u=function(t){return !!c(t)&&parseFloat(t)};r("a623"),r("caad"),r("45fc"),r("ac1f"),r("5319"),r("1276"),r("2ca0"),r("e01a"),r("d28b"),r("e260"),r("3ca3"),r("ddb0");function f(t){return f="function"===typeof Symbol&&"symbol"===typeof Symbol.iterator?function(t){return typeof t}:function(t){return t&&"function"===typeof Symbol&&t.constructor===Symbol&&t!==Symbol.prototype?"symbol":typeof t},f(t)}var l=function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:"transparent";return {type:[String,Object],required:!1,default:t,validator:function(t){return !("string"!==typeof t||!t)||!("object"!==f(t)||!t.colors)&&t.colors.every((function(t){return t.color&&t.offset}))}}},p={data:{type:Array,required:!1,default:function(){return []}},progress:{type:Number,require:!0,validator:function(t){return t>=-100&&t<=100}},legendValue:{type:[Number,String],required:!1,validator:function(t){return !Number.isNaN(parseFloat(t.toString().replace(",",".")))}},size:{type:Number,required:!1,default:200,validator:function(t){return t>=0}},thickness:{type:[Number,String],required:!1,default:"5%",validator:function(t){return parseFloat(t)>=0}},emptyThickness:{type:[Number,String],required:!1,default:"5%",validator:function(t){return parseFloat(t)>=0}},line:{type:String,required:!1,default:"round",validator:function(t){return ["round","butt","square"].includes(t)}},lineMode:{type:String,required:!1,default:"normal",validator:function(t){var e=t.split(" "),r=["normal","out","out-over","in","in-over","top","bottom"].includes(e[0]),n=!e[1]||!Number.isNaN(parseFloat(e[1]));return r&&n}},color:l("#3f79ff"),emptyColor:l("#e6e9f0"),colorFill:l(),emptyColorFill:l(),fontSize:{type:String,required:!1},fontColor:{type:String,required:!1},animation:{type:String,required:!1,default:"default 1000 400",validator:function(t){var e=t.split(" "),r=["default","rs","loop","reverse","bounce"].some((function(t){return t===e[0]})),n=!e[1]||parseFloat(e[1])>=0,i=!e[2]||parseFloat(e[2])>=0;return r&&n&&i}},legend:{type:Boolean,required:!1,default:!0},legendClass:{type:String,required:!1},angle:{type:[String,Number],required:!1,default:-90},loading:{type:Boolean,required:!1,default:!1},noData:{type:Boolean,required:!1,default:!1},dash:{type:String,required:!1,default:"",validator:function(t){if(t.startsWith("strict")){var e=t.split(" ");return parseFloat(e[1])>=0&&parseFloat(e[2])>=0}return !0}},half:{type:Boolean,required:!1,default:!1},gap:{type:Number,required:!1,default:0,validator:function(t){return !Number.isNaN(parseInt(t,10))}},determinate:{type:Boolean,required:!1,default:!1},dot:{type:[String,Number,Object],required:!1,default:0,validator:function(t){return "object"===f(t)?void 0!==t.size&&!Number.isNaN(parseFloat(t.size)):!Number.isNaN(parseFloat(t))}},reverse:{type:Boolean,required:!1,default:!1}},d={};for(var h in p)d[h]={type:p[h].type,default:p[h].default};var v=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("div",{staticClass:"ep-svg-container",class:{"ep-reverse":t.reverse}},[r("svg",{staticClass:"ep-svg",attrs:{height:t.size,width:t.size,xmlns:"http://www.w3.org/2000/svg"}},[r("g",{staticClass:"ep-circle--container"},[r("defs",[t.isColorGradient?r("gradient",{attrs:{color:t.color,type:"progress",id:t._uid}}):t._e(),t.isColorFillGradient?r("gradient",{attrs:{color:t.colorFill,type:"progress-fill",id:t._uid}}):t._e(),t.isEmptyColorGradient?r("gradient",{attrs:{color:t.emptyColor,type:"empty",id:t._uid}}):t._e(),t.isEmptyColorFillGradient?r("gradient",{attrs:{color:t.emptyColorFill,type:"empty-fill",id:t._uid}}):t._e()],1),r(t.circleType,t._b({tag:"component",attrs:{id:t._uid}},"component",t.$props,!1))],1)]),t.dot?r("circle-dot",t._b({attrs:{id:t._uid}},"circle-dot",t.$props,!1)):t._e()],1)},m=[],g=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r(t.gradientComponent,{tag:"component",attrs:{id:"ep-"+t.type+"-gradient-"+t.id,x1:"0%",y1:"100%",x2:"0%",y2:"0%","area-hidden":"true"}},t._l(t.color.colors,(function(e,n){return r("stop",{key:n,attrs:{offset:e.offset+"%","stop-color":""+e.color,"stop-opacity":""+(t.isValidNumber(e.opacity)?e.opacity:1)}})})),1)},y=[],b={name:"Gradient",props:{color:{type:Object,required:!0},type:{type:String,required:!0},id:{type:Number,required:!0}},methods:{isValidNumber:function(t){return c(t)}},computed:{gradientComponent:function(){return this.color.radial?"radialGradient":"linearGradient"}}},x=b;function k(t,e,r,n,i,o,a,s){var c,u="function"===typeof t?t.options:t;if(e&&(u.render=e,u.staticRenderFns=r,u._compiled=!0),n&&(u.functional=!0),o&&(u._scopeId="data-v-"+o),a?(c=function(t){t=t||this.$vnode&&this.$vnode.ssrContext||this.parent&&this.parent.$vnode&&this.parent.$vnode.ssrContext,t||"undefined"===typeof __VUE_SSR_CONTEXT__||(t=__VUE_SSR_CONTEXT__),i&&i.call(this,t),t&&t._registeredComponents&&t._registeredComponents.add(a);},u._ssrRegister=c):i&&(c=s?function(){i.call(this,(u.functional?this.parent:this).$root.$options.shadowRoot);}:i),c)if(u.functional){u._injectStyles=c;var f=u.render;u.render=function(t,e){return c.call(e),f(t,e)};}else {var l=u.beforeCreate;u.beforeCreate=l?[].concat(l,c):[c];}return {exports:t,options:u}}var w=k(x,g,y,!1,null,null,null),_=w.exports,S=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("g",{staticClass:"ep-half-circle",style:{transitionDuration:t.styles.transitionDuration,transitionTimingFunction:t.styles.transitionTimingFunction,transform:"rotate("+t.computedAngle+"deg)"}},[r("path",{staticClass:"ep-half-circle--empty",class:{"ep-circle--nodata":!t.dataIsAvailable},style:{transitionDuration:t.animationDuration,transitionTimingFunction:t.styles.transitionTimingFunction},attrs:{"stroke-width":t.computedEmptyThickness,fill:t.computedColorFill,stroke:t.computedEmptyColor,d:t.emptyPath,"stroke-linecap":t.line,"stroke-dasharray":t.emptyDasharray}}),r("fade-in-transition",[t.isLoading?r("g",[r("g",{style:{opacity:""+(t.loading?1:.45)}},[r("path",{staticClass:"ep-half-circle--loading animation__loading",style:{transitionTimingFunction:t.styles.transitionTimingFunction,transformOrigin:t.styles.transformOrigin,"--ep-loading-stroke-offset":t.styles["--ep-loading-stroke-offset"],"--ep-circumference":t.styles["--ep-circumference"],"--ep-negative-circumference":t.styles["--ep-negative-circumference"]},attrs:{"stroke-width":t.computedThickness,d:t.path,fill:t.computedColorFill,stroke:t.computedColor,"stroke-dasharray":t.circumference,"stroke-linecap":t.line}})])]):t._e()]),r("path",{staticClass:"ep-half-circle--progress ep-circle--progress",class:t.animationClass,style:t.styles,attrs:{"stroke-width":t.computedThickness,d:t.path,fill:t.computedColorFill,stroke:t.computedColor,"stroke-dasharray":t.circumference,"stroke-linecap":t.line}})],1)},E=[];r("99af"),r("13d5"),r("e6cf"),r("2532"),r("4795"),r("96cf");function T(t,e,r,n,i,o,a){try{var s=t[o](a),c=s.value;}catch(u){return void r(u)}s.done?e(c):Promise.resolve(c).then(n,i);}function O(t){return function(){var e=this,r=arguments;return new Promise((function(n,i){var o=t.apply(e,r);function a(t){T(o,n,i,a,s,"next",t);}function s(t){T(o,n,i,a,s,"throw",t);}a(void 0);}))}}var C=function(t){var e=t.trim().split(" ");return {mode:e[0],offset:u(e[1])||0}},j=function(t){var e=t.trim().split(" ");return {type:e[0],duration:c(e[1])?parseFloat(e[1]):1e3,delay:c(e[2])?parseFloat(e[2]):400}},A=function(t){var e=t.trim().split(" "),r="strict"===e[0];return r?{count:parseInt(e[1],10),spacing:parseFloat(e[2])}:t},P=function(t){var e=0,r="white",n={};if("object"!==f(t)){var i=t.toString().trim().split(" ");e=c(i[0])?i[0]:0,r=i[1]||"white";}else e=t.size||0,n=t;return s(s({},n),{},{size:e,color:r})},R=function(){var t=arguments.length>0&&void 0!==arguments[0]?arguments[0]:400;return new Promise((function(e){return setTimeout((function(){return e()}),t)}))},F={name:"CircleMixin",props:s(s({},d),{},{multiple:{type:Boolean,required:!0},id:{type:Number,required:!0},index:{type:Number,required:!0},globalThickness:{type:[Number,String],required:!1,default:"5%"},globalGap:{type:Number,required:!1},globalDot:{type:[Number,String,Object],required:!1}}),data:function(){return {isInitialized:!1}},computed:{computedProgress:function(){return parseFloat(this.progress||0)},progressOffset:function(){var t=this.circumference-this.computedProgress/100*this.circumference;return Math.abs(this.circumference-t)<1?this.circumference-.5:t},radius:function(){var t=this.parsedLineMode.offset;if(this.multiple)return this.baseRadius-this.previousCirclesThickness;switch(this.parsedLineMode.mode){case"normal":return this.normalLineModeRadius;case"in":return this.emptyRadius-(this.computedEmptyThickness/2+this.computedThickness/2+t);case"out-over":return this.computedEmptyThickness<=this.computedThickness?this.baseRadius:this.emptyRadius-this.computedEmptyThickness/2+this.computedThickness/2;case"bottom":return this.emptyRadius-this.computedEmptyThickness/2;case"top":return this.emptyRadius+this.computedEmptyThickness/2;default:return this.baseRadius}},emptyRadius:function(){var t=this.parsedLineMode.offset;if(this.multiple)return this.baseRadius-this.previousCirclesThickness;switch(this.parsedLineMode.mode){case"normal":return this.normalLineModeRadius;case"in":var e=this.computedThickness/2+this.computedEmptyThickness+t;return this.dotSize/2>e?this.emptyBaseRadius-(this.dotSize/2-e):this.emptyBaseRadius;case"in-over":return this.dotToThicknessDifference>0?this.emptyBaseRadius-this.dotToThicknessDifference/2:this.emptyBaseRadius;case"out":return this.baseRadius-(this.computedThickness/2+this.computedEmptyThickness/2+t);case"out-over":return this.computedEmptyThickness<=this.computedThickness?this.baseRadius-this.computedThickness/2+this.computedEmptyThickness/2:this.emptyBaseRadius;case"bottom":return this.computedEmptyThickness<this.thicknessWithDot/2?this.emptyBaseRadius-(this.thicknessWithDot/2-this.computedEmptyThickness):this.emptyBaseRadius;case"top":return this.emptyBaseRadius-this.thicknessWithDot/2;default:return this.emptyBaseRadius}},baseRadius:function(){return this.size/2-this.thicknessWithDot/2},emptyBaseRadius:function(){return this.size/2-this.computedEmptyThickness/2},normalLineModeRadius:function(){return this.thicknessWithDot<this.computedEmptyThickness?this.emptyBaseRadius:this.baseRadius},parsedLineMode:function(){return C(this.lineMode)},parsedAnimation:function(){return j(this.animation)},parsedDash:function(){return A(this.dash)},dataIsAvailable:function(){return c(this.computedProgress)&&!this.noData},animationClass:function(){return ["animation__".concat(!this.loading&&this.dataIsAvailable&&this.isInitialized?this.parsedAnimation.type:"none")]},animationDuration:function(){return "".concat(this.parsedAnimation.duration,"ms")},computedColor:function(){return Array.isArray(this.color.colors)?"url(#ep-progress-gradient-".concat(this.id,")"):this.color},computedEmptyColor:function(){return Array.isArray(this.emptyColor.colors)?"url(#ep-empty-gradient-".concat(this.id,")"):this.emptyColor},computedColorFill:function(){return Array.isArray(this.colorFill.colors)?"url(#ep-progress-fill-gradient-".concat(this.id,")"):this.colorFill},computedEmptyColorFill:function(){return Array.isArray(this.emptyColorFill.colors)?"url(#ep-empty-fill-gradient-".concat(this.id,")"):this.emptyColorFill},computedThickness:function(){return this.calculateThickness(this.thickness.toString())},thicknessWithDot:function(){return this.computedThickness<this.dotSize?this.dotSize:this.computedThickness},computedGlobalThickness:function(){return this.calculateThickness(this.globalThickness)},computedEmptyThickness:function(){return this.calculateThickness(this.emptyThickness)},computedAngle:function(){return c(this.angle)?this.angle:-90},transformOrigin:function(){return "50% 50%"},emptyDasharray:function(){return this.parsedDash.count&&this.parsedDash.spacing?"".concat(2*Math.PI*this.emptyRadius*this.getDashPercent(),",\n              ").concat(2*Math.PI*this.emptyRadius*this.getDashSpacingPercent()).trim():this.parsedDash},strokeDashOffset:function(){return this.dataIsAvailable&&!this.loading&&this.isInitialized?this.progressOffset:this.circumference},previousCirclesThickness:function(){if(0===this.index)return 0;for(var t=c(this.gap)?this.gap:this.globalGap,e=[],r=0;r<this.index;r++){var n=this.data[r],i=n.dot?this.calculateThickness(P(n.dot).size):this.globalDotSize,o=c(n.thickness)?this.calculateThickness(n.thickness):this.computedGlobalThickness,a=c(n.gap)?n.gap:this.globalGap,s=Math.max(i,o);e.push(r>0?s+a:s);}return e.reduce((function(t,e){return t+e}))+t},parsedDot:function(){return P(this.dot)},dotSize:function(){return this.calculateThickness(this.parsedDot.size)},dotColor:function(){return this.parsedDot.color},dotToThicknessDifference:function(){return this.dotSize-this.computedThickness},globalDotSize:function(){return this.calculateThickness(P(this.globalDot).size)},styles:function(){return {transition:"".concat(this.animationDuration,", opacity 0.3s"),strokeDashoffset:this.strokeDashOffset,transitionTimingFunction:"ease-in-out",transformOrigin:this.transformOrigin,opacity:this.loading||!this.dataIsAvailable?0:1,"--ep-circumference":this.circumference,"--ep-negative-circumference":this.getNegativeCircumference(),"--ep-double-circumference":this.getDoubleCircumference(),"--ep-stroke-offset":this.progressOffset,"--ep-loop-stroke-offset":this.getLoopOffset(),"--ep-bounce-out-stroke-offset":this.getBounceOutOffset(),"--ep-bounce-in-stroke-offset":this.getBounceInOffset(),"--ep-reverse-stroke-offset":this.getReverseOffset(),"--ep-loading-stroke-offset":.2*this.circumference,"animation-duration":this.animationDuration}},isLoading:function(){return (this.determinate||this.loading)&&this.dataIsAvailable}},methods:{calculateThickness:function(t){var e=parseFloat(t);switch(!0){case t.toString().includes("%"):return e*this.size/100;default:return e}},getDashSpacingPercent:function(){return this.parsedDash.spacing/this.parsedDash.count},getDashPercent:function(){return (1-this.parsedDash.spacing)/this.parsedDash.count},getNegativeCircumference:function(){return -1*this.circumference},getDoubleCircumference:function(){return 2*this.circumference},getLoopOffset:function(){return this.getNegativeCircumference()-(this.circumference-this.progressOffset)},getReverseOffset:function(){return this.getDoubleCircumference()+this.progressOffset},getBounceOutOffset:function(){return this.progressOffset<100?0:this.progressOffset-100},getBounceInOffset:function(){return this.circumference-this.progressOffset<100?this.progressOffset:this.progressOffset+100}},mounted:function(){var t=this;return O(regeneratorRuntime.mark((function e(){return regeneratorRuntime.wrap((function(e){while(1)switch(e.prev=e.next){case 0:if(t.loading){e.next=3;break}return e.next=3,R(t.parsedAnimation.delay);case 3:t.isInitialized=!0;case 4:case"end":return e.stop()}}),e)})))()}},I=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("transition",{attrs:{mode:"out-in",name:"fade",appear:""}},[t._t("default")],2)},N=[],D={name:"FadeInTransition"},L=D,M=(r("53c8"),k(L,I,N,!1,null,"873ef638",null)),z=M.exports,q={name:"HalfCircleProgress",components:{FadeInTransition:z},mixins:[F],computed:{circumference:function(){return 2*this.radius*Math.PI/2},path:function(){return " M ".concat(this.position,", ").concat(this.size/2," a ").concat(this.radius,",").concat(this.radius," 0 1,1 ").concat(2*this.radius,",0")},emptyPath:function(){return " M ".concat(this.emptyPosition,", ").concat(this.size/2," a ").concat(this.emptyRadius,",").concat(this.emptyRadius," 0 1,1 ").concat(2*this.emptyRadius,",0")},position:function(){return this.size/2-this.radius},emptyPosition:function(){return this.size/2-this.emptyRadius}}},V=q,G=(r("054f"),k(V,S,E,!1,null,"5b37ebc3",null)),$=G.exports,B=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("g",{staticClass:"ep-circle",style:{transitionDuration:t.styles.transitionDuration,transitionTimingFunction:t.styles.transitionTimingFunction,transform:"rotate("+t.computedAngle+"deg)"}},[r("circle",{staticClass:"ep-circle--empty",class:{"ep-circle--nodata":!t.dataIsAvailable},style:{transitionDuration:t.animationDuration,transitionTimingFunction:t.styles.transitionTimingFunction},attrs:{r:t.emptyRadius,cx:t.position,cy:t.position,stroke:t.computedEmptyColor,"stroke-dasharray":t.emptyDasharray,fill:t.computedEmptyColorFill,"stroke-width":t.computedEmptyThickness}}),r("fade-in-transition",[t.isLoading?r("g",[r("g",{staticClass:"ep-circle--loading__container",style:{opacity:""+(t.loading?1:.45)}},[r("circle",{staticClass:"ep-circle--loading animation__loading",style:{transitionTimingFunction:t.styles.transitionTimingFunction,transformOrigin:t.styles.transformOrigin,"--ep-loading-stroke-offset":t.styles["--ep-loading-stroke-offset"],"--ep-circumference":t.styles["--ep-circumference"]},attrs:{r:t.radius,cx:t.position,cy:t.position,fill:"transparent",stroke:t.computedColor,"stroke-width":t.computedThickness,"stroke-linecap":t.line,"stroke-dasharray":t.circumference}})])]):t._e()]),r("circle",{staticClass:"ep-circle--progress",class:t.animationClass,style:t.styles,attrs:{r:t.radius,cx:t.position,cy:t.position,fill:t.computedColorFill,stroke:t.computedColor,"stroke-width":t.computedThickness,"stroke-linecap":t.line,"stroke-dasharray":t.circumference}})],1)},U=[],W={name:"CircleProgress",components:{FadeInTransition:z},mixins:[F],computed:{position:function(){return this.size/2},circumference:function(){return 2*this.radius*Math.PI}}},H=W,X=(r("273a"),k(H,B,U,!1,null,"7d0a0fbb",null)),Y=X.exports,K=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("div",{staticClass:"ep-circle--progress__dot-container",class:t.dotContainerClasses,style:t.dotContainerStyle},[r("div",[r("span",{staticClass:"ep-circle--progress__dot",class:{"ep-hidden":t.isHidden},style:t.dotStyle})])])},J=[],Q={props:s({},d),name:"CircleDot",mixins:[F],computed:{dotContainerSize:function(){return 2*this.radius+this.dotSize},dotContainerRotation:function(){return this.isInitialized&&!this.loading&&this.dataIsAvailable?this.dotEnd:this.dotStart},dotContainerFullRotationDeg:function(){return this.half?180:360},dotContainerStyle:function(){return s({width:"".concat(this.dotContainerSize,"px"),height:"".concat(this.dotContainerSize,"px"),transform:"rotate(".concat(this.dotContainerRotation,"deg)"),transitionDuration:this.loading||!this.dataIsAvailable?"0s":this.animationDuration,transitionTimingFunction:"ease-in-out","animation-duration":this.animationDuration,"--ep-dot-start":"".concat(this.dotStart,"deg"),"--ep-dot-end":"".concat(this.dotEnd,"deg"),"--ep-dot-360":"".concat(this.dotStart+this.dotContainerFullRotationDeg,"deg")},this.dotContainerAnimationStyle)},dotContainerClasses:function(){return [this.animationClass,!this.half||"ep-half-circle-progress__dot"]},dotContainerAnimationStyle:function(){var t={loop:{opacity:this.half?0:1,"--ep-dot-loop-end":"".concat(this.dotStart+this.dotContainerFullRotationDeg+this.dotEnd,"deg")},bounce:{opacity:0,"animation-duration":"".concat(this.parsedAnimation.duration+500,"ms")}};return t[this.parsedAnimation.type]},dotStyle:function(){return s(s({borderRadius:"".concat(this.dotSize/2,"px"),width:"".concat(this.dotSize,"px"),backgroundColor:this.dotColor},this.dot),{},{transitionDuration:this.loading||!this.dataIsAvailable?"0s":this.animationDuration,height:"".concat(this.dotSize,"px")})},dotStart:function(){return this.half?this.angle-90:this.angle+90},dotEnd:function(){var t=this.calculateProgress();return this.dotStart+t*this.dotContainerFullRotationDeg/100},isHidden:function(){return !this.isInitialized||this.loading||!this.dataIsAvailable}},methods:{calculateProgress:function(){return this.half&&this.computedProgress<0?this.computedProgress-100:this.computedProgress}}},Z=Q,tt=(r("2474"),k(Z,K,J,!1,null,"38ca167f",null)),et=tt.exports,rt={name:"EpCircleContainer",components:{CircleDot:et,CircleProgress:Y,HalfCircleProgress:$,Gradient:_},props:s(s({},d),{},{index:{type:Number,required:!0},multiple:{type:Boolean,required:!0},globalThickness:{type:[Number,String],required:!1,default:"5%"},globalGap:{type:Number,required:!1},globalDot:{type:[Number,String,Object],required:!1}}),computed:{circleType:function(){return this.half?"half-circle-progress":"circle-progress"},isColorGradient:function(){return Array.isArray(this.color.colors)},isColorFillGradient:function(){return Array.isArray(this.colorFill.colors)},isEmptyColorGradient:function(){return Array.isArray(this.emptyColor.colors)},isEmptyColorFillGradient:function(){return Array.isArray(this.emptyColorFill.colors)}}},nt=rt,it=(r("c49e"),k(nt,v,m,!1,null,null,null)),ot=it.exports,at=function(){var t=this,e=t.$createElement,r=t._self._c||e;return r("span",{staticClass:"ep-legend--value__counter"},[t._t("default",null,{counterTick:t.counterProps}),t.$scopedSlots.default?t._e():r("span",[t._v(t._s(t.formattedValue))])],2)},st=[],ct=(r("b680"),r("841c"),{name:"Counter",props:{value:{type:[Number,String],required:!0},animation:{type:String,required:!0},loading:{type:Boolean,required:!0}},data:function(){return {start:0,startTime:0,currentValue:0,raf:null,previousCountStepValue:0}},watch:{value:function(){this.start=this.currentValue,this.reset(),this.raf=requestAnimationFrame(this.count);}},computed:{end:function(){return parseFloat(this.value.toString().replace(",","."))},difference:function(){return Math.abs(this.end-this.start)},oneStepDifference:function(){return 0===this.duration?this.difference:this.difference/this.duration},delimiter:function(){return this.value.toString().search(",")>=0?",":"."},formattedValue:function(){return this.currentValue.toFixed(this.countDecimals()).replace(".",this.delimiter)},delay:function(){return j(this.animation).delay},duration:function(){return j(this.animation).duration},counterProps:function(){return {currentValue:parseFloat(this.formattedValue),currentFormattedValue:this.formattedValue,currentRawValue:this.currentValue,duration:this.duration,previousCountStepValue:this.previousCountStepValue,start:this.start,end:this.end,difference:this.difference,oneStepDifference:this.oneStepDifference,startTime:this.startTime,elapsed:0}}},methods:{countDecimals:function(){return this.value%1===0?0:this.value.toString().split(this.delimiter)[1].length},count:function(t){this.startTime||(this.startTime=t);var e=t-this.startTime;this.end>=this.start?this.countUp(e):this.countDown(e),e<this.duration&&this.difference>.1&&(cancelAnimationFrame(this.raf),this.raf=requestAnimationFrame(this.count)),e>=this.duration&&(this.currentValue=this.end,this.reset());},countDown:function(t){var e=Math.min(this.oneStepDifference*(t||1),this.difference);this.currentValue-=e-this.previousCountStepValue,this.previousCountStepValue=e;},countUp:function(t){var e=Math.min(this.oneStepDifference*(t||1),this.difference);this.currentValue+=e-this.previousCountStepValue,this.previousCountStepValue=e;},reset:function(){this.startTime=0,this.previousCountStepValue=0,cancelAnimationFrame(this.raf);}},mounted:function(){var t=this;this.loading?this.raf=requestAnimationFrame(this.count):setTimeout((function(){t.raf=requestAnimationFrame(t.count);}),this.delay);}}),ut=ct,ft=k(ut,at,st,!1,null,null,null),lt=ft.exports,pt={name:"VueEllipseProgress",components:{Counter:lt,CircleContainer:ot},props:s(s({},p),{},{legendFormatter:{type:Function,required:!1}}),data:function(){return {counterTick:{}}},computed:{legendVal:function(){return this.loading||this.noData?0:this.legendValue?this.legendValue:u(this.progress)||0},shouldHideLegendValue:function(){return !this.isDataAvailable||this.loading},isDataAvailable:function(){return c(this.progress)&&!this.noData},isMultiple:function(){return this.data.length>1},isHTML:function(){return /<[a-z/][\s\S]*>/i.test(this.legendFormatter({currentValue:0}).toString().trim())},circlesData:function(){var t=this;return this.isMultiple?this.data.map((function(e){return s(s(s({},t.$props),e),{},{emptyThickness:c(e.thickness)?e.thickness:t.$props.thickness})})):[this.$props]}}},dt=pt,ht=(r("01f3"),k(dt,n,i,!1,null,"59d6a78d",null));e["a"]=ht.exports;},"2ca0":function(t,e,r){var n=r("23e7"),i=r("06cf").f,o=r("50c4"),a=r("5a34"),s=r("1d80"),c=r("ab13"),u=r("c430"),f="".startsWith,l=Math.min,p=c("startsWith"),d=!u&&!p&&!!function(){var t=i(String.prototype,"startsWith");return t&&!t.writable}();n({target:"String",proto:!0,forced:!d&&!p},{startsWith:function(t){var e=String(s(this));a(t);var r=o(l(arguments.length>1?arguments[1]:void 0,e.length)),n=String(t);return f?f.call(e,n,r):e.slice(r,r+n.length)===n}});},"2cf4":function(t,e,r){var n,i,o,a=r("da84"),s=r("d039"),c=r("c6b6"),u=r("0366"),f=r("1be4"),l=r("cc12"),p=r("1cdc"),d=a.location,h=a.setImmediate,v=a.clearImmediate,m=a.process,g=a.MessageChannel,y=a.Dispatch,b=0,x={},k="onreadystatechange",w=function(t){if(x.hasOwnProperty(t)){var e=x[t];delete x[t],e();}},_=function(t){return function(){w(t);}},S=function(t){w(t.data);},E=function(t){a.postMessage(t+"",d.protocol+"//"+d.host);};h&&v||(h=function(t){var e=[],r=1;while(arguments.length>r)e.push(arguments[r++]);return x[++b]=function(){("function"==typeof t?t:Function(t)).apply(void 0,e);},n(b),b},v=function(t){delete x[t];},"process"==c(m)?n=function(t){m.nextTick(_(t));}:y&&y.now?n=function(t){y.now(_(t));}:g&&!p?(i=new g,o=i.port2,i.port1.onmessage=S,n=u(o.postMessage,o,1)):!a.addEventListener||"function"!=typeof postMessage||a.importScripts||s(E)||"file:"===d.protocol?n=k in l("script")?function(t){f.appendChild(l("script"))[k]=function(){f.removeChild(this),w(t);};}:function(t){setTimeout(_(t),0);}:(n=E,a.addEventListener("message",S,!1))),t.exports={set:h,clear:v};},"2d00":function(t,e,r){var n,i,o=r("da84"),a=r("342f"),s=o.process,c=s&&s.versions,u=c&&c.v8;u?(n=u.split("."),i=n[0]+n[1]):a&&(n=a.match(/Edge\/(\d+)/),(!n||n[1]>=74)&&(n=a.match(/Chrome\/(\d+)/),n&&(i=n[1]))),t.exports=i&&+i;},"342f":function(t,e,r){var n=r("d066");t.exports=n("navigator","userAgent")||"";},"35a1":function(t,e,r){var n=r("f5df"),i=r("3f8c"),o=r("b622"),a=o("iterator");t.exports=function(t){if(void 0!=t)return t[a]||t["@@iterator"]||i[n(t)]};},"37e8":function(t,e,r){var n=r("83ab"),i=r("9bf2"),o=r("825a"),a=r("df75");t.exports=n?Object.defineProperties:function(t,e){o(t);var r,n=a(e),s=n.length,c=0;while(s>c)i.f(t,r=n[c++],e[r]);return t};},"3bbe":function(t,e,r){var n=r("861d");t.exports=function(t){if(!n(t)&&null!==t)throw TypeError("Can't set "+String(t)+" as a prototype");return t};},"3ca3":function(t,e,r){var n=r("6547").charAt,i=r("69f3"),o=r("7dd0"),a="String Iterator",s=i.set,c=i.getterFor(a);o(String,"String",(function(t){s(this,{type:a,string:String(t),index:0});}),(function(){var t,e=c(this),r=e.string,i=e.index;return i>=r.length?{value:void 0,done:!0}:(t=n(r,i),e.index+=t.length,{value:t,done:!1})}));},"3f8c":function(t,e){t.exports={};},"403e":function(t,e,r){var n=r("648b");"string"===typeof n&&(n=[[t.i,n,""]]),n.locals&&(t.exports=n.locals);var i=r("499e").default;i("3d3d86fd",n,!0,{sourceMap:!1,shadowMode:!1});},"408a":function(t,e,r){var n=r("c6b6");t.exports=function(t){if("number"!=typeof t&&"Number"!=n(t))throw TypeError("Incorrect invocation");return +t};},4160:function(t,e,r){var n=r("23e7"),i=r("17c2");n({target:"Array",proto:!0,forced:[].forEach!=i},{forEach:i});},"428f":function(t,e,r){var n=r("da84");t.exports=n;},"44ad":function(t,e,r){var n=r("d039"),i=r("c6b6"),o="".split;t.exports=n((function(){return !Object("z").propertyIsEnumerable(0)}))?function(t){return "String"==i(t)?o.call(t,""):Object(t)}:Object;},"44d2":function(t,e,r){var n=r("b622"),i=r("7c73"),o=r("9bf2"),a=n("unscopables"),s=Array.prototype;void 0==s[a]&&o.f(s,a,{configurable:!0,value:i(null)}),t.exports=function(t){s[a][t]=!0;};},"44de":function(t,e,r){var n=r("da84");t.exports=function(t,e){var r=n.console;r&&r.error&&(1===arguments.length?r.error(t):r.error(t,e));};},"44e7":function(t,e,r){var n=r("861d"),i=r("c6b6"),o=r("b622"),a=o("match");t.exports=function(t){var e;return n(t)&&(void 0!==(e=t[a])?!!e:"RegExp"==i(t))};},"45fc":function(t,e,r){var n=r("23e7"),i=r("b727").some,o=r("a640"),a=r("ae40"),s=o("some"),c=a("some");n({target:"Array",proto:!0,forced:!s||!c},{some:function(t){return i(this,t,arguments.length>1?arguments[1]:void 0)}});},4795:function(t,e,r){var n=r("23e7"),i=r("da84"),o=r("342f"),a=[].slice,s=/MSIE .\./.test(o),c=function(t){return function(e,r){var n=arguments.length>2,i=n?a.call(arguments,2):void 0;return t(n?function(){("function"==typeof e?e:Function(e)).apply(this,i);}:e,r)}};n({global:!0,bind:!0,forced:s},{setTimeout:c(i.setTimeout),setInterval:c(i.setInterval)});},4840:function(t,e,r){var n=r("825a"),i=r("1c0b"),o=r("b622"),a=o("species");t.exports=function(t,e){var r,o=n(t).constructor;return void 0===o||void 0==(r=n(o)[a])?e:i(r)};},4930:function(t,e,r){var n=r("d039");t.exports=!!Object.getOwnPropertySymbols&&!n((function(){return !String(Symbol())}));},"498a":function(t,e,r){var n=r("23e7"),i=r("58a8").trim,o=r("c8d2");n({target:"String",proto:!0,forced:o("trim")},{trim:function(){return i(this)}});},"499e":function(t,e,r){function n(t,e){for(var r=[],n={},i=0;i<e.length;i++){var o=e[i],a=o[0],s=o[1],c=o[2],u=o[3],f={id:t+":"+i,css:s,media:c,sourceMap:u};n[a]?n[a].parts.push(f):r.push(n[a]={id:a,parts:[f]});}return r}r.r(e),r.d(e,"default",(function(){return h}));var i="undefined"!==typeof document;if("undefined"!==typeof DEBUG&&DEBUG&&!i)throw new Error("vue-style-loader cannot be used in a non-browser environment. Use { target: 'node' } in your Webpack config to indicate a server-rendering environment.");var o={},a=i&&(document.head||document.getElementsByTagName("head")[0]),s=null,c=0,u=!1,f=function(){},l=null,p="data-vue-ssr-id",d="undefined"!==typeof navigator&&/msie [6-9]\b/.test(navigator.userAgent.toLowerCase());function h(t,e,r,i){u=r,l=i||{};var a=n(t,e);return v(a),function(e){for(var r=[],i=0;i<a.length;i++){var s=a[i],c=o[s.id];c.refs--,r.push(c);}e?(a=n(t,e),v(a)):a=[];for(i=0;i<r.length;i++){c=r[i];if(0===c.refs){for(var u=0;u<c.parts.length;u++)c.parts[u]();delete o[c.id];}}}}function v(t){for(var e=0;e<t.length;e++){var r=t[e],n=o[r.id];if(n){n.refs++;for(var i=0;i<n.parts.length;i++)n.parts[i](r.parts[i]);for(;i<r.parts.length;i++)n.parts.push(g(r.parts[i]));n.parts.length>r.parts.length&&(n.parts.length=r.parts.length);}else {var a=[];for(i=0;i<r.parts.length;i++)a.push(g(r.parts[i]));o[r.id]={id:r.id,refs:1,parts:a};}}}function m(){var t=document.createElement("style");return t.type="text/css",a.appendChild(t),t}function g(t){var e,r,n=document.querySelector("style["+p+'~="'+t.id+'"]');if(n){if(u)return f;n.parentNode.removeChild(n);}if(d){var i=c++;n=s||(s=m()),e=b.bind(null,n,i,!1),r=b.bind(null,n,i,!0);}else n=m(),e=x.bind(null,n),r=function(){n.parentNode.removeChild(n);};return e(t),function(n){if(n){if(n.css===t.css&&n.media===t.media&&n.sourceMap===t.sourceMap)return;e(t=n);}else r();}}var y=function(){var t=[];return function(e,r){return t[e]=r,t.filter(Boolean).join("\n")}}();function b(t,e,r,n){var i=r?"":n.css;if(t.styleSheet)t.styleSheet.cssText=y(e,i);else {var o=document.createTextNode(i),a=t.childNodes;a[e]&&t.removeChild(a[e]),a.length?t.insertBefore(o,a[e]):t.appendChild(o);}}function x(t,e){var r=e.css,n=e.media,i=e.sourceMap;if(n&&t.setAttribute("media",n),l.ssrId&&t.setAttribute(p,e.id),i&&(r+="\n/*# sourceURL="+i.sources[0]+" */",r+="\n/*# sourceMappingURL=data:application/json;base64,"+btoa(unescape(encodeURIComponent(JSON.stringify(i))))+" */"),t.styleSheet)t.styleSheet.cssText=r;else {while(t.firstChild)t.removeChild(t.firstChild);t.appendChild(document.createTextNode(r));}}},"4d64":function(t,e,r){var n=r("fc6a"),i=r("50c4"),o=r("23cb"),a=function(t){return function(e,r,a){var s,c=n(e),u=i(c.length),f=o(a,u);if(t&&r!=r){while(u>f)if(s=c[f++],s!=s)return !0}else for(;u>f;f++)if((t||f in c)&&c[f]===r)return t||f||0;return !t&&-1}};t.exports={includes:a(!0),indexOf:a(!1)};},"4de4":function(t,e,r){var n=r("23e7"),i=r("b727").filter,o=r("1dde"),a=r("ae40"),s=o("filter"),c=a("filter");n({target:"Array",proto:!0,forced:!s||!c},{filter:function(t){return i(this,t,arguments.length>1?arguments[1]:void 0)}});},"50c4":function(t,e,r){var n=r("a691"),i=Math.min;t.exports=function(t){return t>0?i(n(t),9007199254740991):0};},5135:function(t,e){var r={}.hasOwnProperty;t.exports=function(t,e){return r.call(t,e)};},5319:function(t,e,r){var n=r("d784"),i=r("825a"),o=r("7b0b"),a=r("50c4"),s=r("a691"),c=r("1d80"),u=r("8aa5"),f=r("14c3"),l=Math.max,p=Math.min,d=Math.floor,h=/\$([$&'`]|\d\d?|<[^>]*>)/g,v=/\$([$&'`]|\d\d?)/g,m=function(t){return void 0===t?t:String(t)};n("replace",2,(function(t,e,r,n){var g=n.REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE,y=n.REPLACE_KEEPS_$0,b=g?"$":"$0";return [function(r,n){var i=c(this),o=void 0==r?void 0:r[t];return void 0!==o?o.call(r,i,n):e.call(String(i),r,n)},function(t,n){if(!g&&y||"string"===typeof n&&-1===n.indexOf(b)){var o=r(e,t,this,n);if(o.done)return o.value}var c=i(t),d=String(this),h="function"===typeof n;h||(n=String(n));var v=c.global;if(v){var k=c.unicode;c.lastIndex=0;}var w=[];while(1){var _=f(c,d);if(null===_)break;if(w.push(_),!v)break;var S=String(_[0]);""===S&&(c.lastIndex=u(d,a(c.lastIndex),k));}for(var E="",T=0,O=0;O<w.length;O++){_=w[O];for(var C=String(_[0]),j=l(p(s(_.index),d.length),0),A=[],P=1;P<_.length;P++)A.push(m(_[P]));var R=_.groups;if(h){var F=[C].concat(A,j,d);void 0!==R&&F.push(R);var I=String(n.apply(void 0,F));}else I=x(C,d,j,A,R,n);j>=T&&(E+=d.slice(T,j)+I,T=j+C.length);}return E+d.slice(T)}];function x(t,r,n,i,a,s){var c=n+t.length,u=i.length,f=v;return void 0!==a&&(a=o(a),f=h),e.call(s,f,(function(e,o){var s;switch(o.charAt(0)){case"$":return "$";case"&":return t;case"`":return r.slice(0,n);case"'":return r.slice(c);case"<":s=a[o.slice(1,-1)];break;default:var f=+o;if(0===f)return e;if(f>u){var l=d(f/10);return 0===l?e:l<=u?void 0===i[l-1]?o.charAt(1):i[l-1]+o.charAt(1):e}s=i[f-1];}return void 0===s?"":s}))}}));},"53c8":function(t,e,r){var n=r("403e"),i=r.n(n);i.a;},5692:function(t,e,r){var n=r("c430"),i=r("c6cd");(t.exports=function(t,e){return i[t]||(i[t]=void 0!==e?e:{})})("versions",[]).push({version:"3.6.5",mode:n?"pure":"global",copyright:"© 2020 Denis Pushkarev (zloirock.ru)"});},"56ef":function(t,e,r){var n=r("d066"),i=r("241c"),o=r("7418"),a=r("825a");t.exports=n("Reflect","ownKeys")||function(t){var e=i.f(a(t)),r=o.f;return r?e.concat(r(t)):e};},5899:function(t,e){t.exports="\t\n\v\f\r                　\u2028\u2029\ufeff";},"58a8":function(t,e,r){var n=r("1d80"),i=r("5899"),o="["+i+"]",a=RegExp("^"+o+o+"*"),s=RegExp(o+o+"*$"),c=function(t){return function(e){var r=String(n(e));return 1&t&&(r=r.replace(a,"")),2&t&&(r=r.replace(s,"")),r}};t.exports={start:c(1),end:c(2),trim:c(3)};},"5a34":function(t,e,r){var n=r("44e7");t.exports=function(t){if(n(t))throw TypeError("The method doesn't accept regular expressions");return t};},"5c6c":function(t,e){t.exports=function(t,e){return {enumerable:!(1&t),configurable:!(2&t),writable:!(4&t),value:e}};},"648b":function(t,e,r){var n=r("24fb");e=n(!1),e.push([t.i,".fade-enter-active[data-v-873ef638],.fade-leave-active[data-v-873ef638]{-webkit-transition:opacity .3s;-o-transition:opacity .3s;transition:opacity .3s}.fade-enter[data-v-873ef638],.fade-leave-active[data-v-873ef638]{-webkit-transition:.3s;-o-transition:.3s;transition:.3s;opacity:0}",""]),t.exports=e;},6547:function(t,e,r){var n=r("a691"),i=r("1d80"),o=function(t){return function(e,r){var o,a,s=String(i(e)),c=n(r),u=s.length;return c<0||c>=u?t?"":void 0:(o=s.charCodeAt(c),o<55296||o>56319||c+1===u||(a=s.charCodeAt(c+1))<56320||a>57343?t?s.charAt(c):o:t?s.slice(c,c+2):a-56320+(o-55296<<10)+65536)}};t.exports={codeAt:o(!1),charAt:o(!0)};},"65f0":function(t,e,r){var n=r("861d"),i=r("e8b5"),o=r("b622"),a=o("species");t.exports=function(t,e){var r;return i(t)&&(r=t.constructor,"function"!=typeof r||r!==Array&&!i(r.prototype)?n(r)&&(r=r[a],null===r&&(r=void 0)):r=void 0),new(void 0===r?Array:r)(0===e?0:e)};},"69f3":function(t,e,r){var n,i,o,a=r("7f9a"),s=r("da84"),c=r("861d"),u=r("9112"),f=r("5135"),l=r("f772"),p=r("d012"),d=s.WeakMap,h=function(t){return o(t)?i(t):n(t,{})},v=function(t){return function(e){var r;if(!c(e)||(r=i(e)).type!==t)throw TypeError("Incompatible receiver, "+t+" required");return r}};if(a){var m=new d,g=m.get,y=m.has,b=m.set;n=function(t,e){return b.call(m,t,e),e},i=function(t){return g.call(m,t)||{}},o=function(t){return y.call(m,t)};}else {var x=l("state");p[x]=!0,n=function(t,e){return u(t,x,e),e},i=function(t){return f(t,x)?t[x]:{}},o=function(t){return f(t,x)};}t.exports={set:n,get:i,has:o,enforce:h,getterFor:v};},"6d40":function(t,e,r){var n=r("11ae");"string"===typeof n&&(n=[[t.i,n,""]]),n.locals&&(t.exports=n.locals);var i=r("499e").default;i("78b49b6e",n,!0,{sourceMap:!1,shadowMode:!1});},"6eeb":function(t,e,r){var n=r("da84"),i=r("9112"),o=r("5135"),a=r("ce4e"),s=r("8925"),c=r("69f3"),u=c.get,f=c.enforce,l=String(String).split("String");(t.exports=function(t,e,r,s){var c=!!s&&!!s.unsafe,u=!!s&&!!s.enumerable,p=!!s&&!!s.noTargetGet;"function"==typeof r&&("string"!=typeof e||o(r,"name")||i(r,"name",e),f(r).source=l.join("string"==typeof e?e:"")),t!==n?(c?!p&&t[e]&&(u=!0):delete t[e],u?t[e]=r:i(t,e,r)):u?t[e]=r:a(e,r);})(Function.prototype,"toString",(function(){return "function"==typeof this&&u(this).source||s(this)}));},7156:function(t,e,r){var n=r("861d"),i=r("d2bb");t.exports=function(t,e,r){var o,a;return i&&"function"==typeof(o=e.constructor)&&o!==r&&n(a=o.prototype)&&a!==r.prototype&&i(t,a),t};},7418:function(t,e){e.f=Object.getOwnPropertySymbols;},"746f":function(t,e,r){var n=r("428f"),i=r("5135"),o=r("e538"),a=r("9bf2").f;t.exports=function(t){var e=n.Symbol||(n.Symbol={});i(e,t)||a(e,t,{value:o.f(t)});};},7839:function(t,e){t.exports=["constructor","hasOwnProperty","isPrototypeOf","propertyIsEnumerable","toLocaleString","toString","valueOf"];},"7b0b":function(t,e,r){var n=r("1d80");t.exports=function(t){return Object(n(t))};},"7c73":function(t,e,r){var n,i=r("825a"),o=r("37e8"),a=r("7839"),s=r("d012"),c=r("1be4"),u=r("cc12"),f=r("f772"),l=">",p="<",d="prototype",h="script",v=f("IE_PROTO"),m=function(){},g=function(t){return p+h+l+t+p+"/"+h+l},y=function(t){t.write(g("")),t.close();var e=t.parentWindow.Object;return t=null,e},b=function(){var t,e=u("iframe"),r="java"+h+":";return e.style.display="none",c.appendChild(e),e.src=String(r),t=e.contentWindow.document,t.open(),t.write(g("document.F=Object")),t.close(),t.F},x=function(){try{n=document.domain&&new ActiveXObject("htmlfile");}catch(e){}x=n?y(n):b();var t=a.length;while(t--)delete x[d][a[t]];return x()};s[v]=!0,t.exports=Object.create||function(t,e){var r;return null!==t?(m[d]=i(t),r=new m,m[d]=null,r[v]=t):r=x(),void 0===e?r:o(r,e)};},"7dd0":function(t,e,r){var n=r("23e7"),i=r("9ed3"),o=r("e163"),a=r("d2bb"),s=r("d44e"),c=r("9112"),u=r("6eeb"),f=r("b622"),l=r("c430"),p=r("3f8c"),d=r("ae93"),h=d.IteratorPrototype,v=d.BUGGY_SAFARI_ITERATORS,m=f("iterator"),g="keys",y="values",b="entries",x=function(){return this};t.exports=function(t,e,r,f,d,k,w){i(r,e,f);var _,S,E,T=function(t){if(t===d&&P)return P;if(!v&&t in j)return j[t];switch(t){case g:return function(){return new r(this,t)};case y:return function(){return new r(this,t)};case b:return function(){return new r(this,t)}}return function(){return new r(this)}},O=e+" Iterator",C=!1,j=t.prototype,A=j[m]||j["@@iterator"]||d&&j[d],P=!v&&A||T(d),R="Array"==e&&j.entries||A;if(R&&(_=o(R.call(new t)),h!==Object.prototype&&_.next&&(l||o(_)===h||(a?a(_,h):"function"!=typeof _[m]&&c(_,m,x)),s(_,O,!0,!0),l&&(p[O]=x))),d==y&&A&&A.name!==y&&(C=!0,P=function(){return A.call(this)}),l&&!w||j[m]===P||c(j,m,P),p[e]=P,d)if(S={values:T(y),keys:k?P:T(g),entries:T(b)},w)for(E in S)(v||C||!(E in j))&&u(j,E,S[E]);else n({target:e,proto:!0,forced:v||C},S);return S};},"7f9a":function(t,e,r){var n=r("da84"),i=r("8925"),o=n.WeakMap;t.exports="function"===typeof o&&/native code/.test(i(o));},"825a":function(t,e,r){var n=r("861d");t.exports=function(t){if(!n(t))throw TypeError(String(t)+" is not an object");return t};},"83ab":function(t,e,r){var n=r("d039");t.exports=!n((function(){return 7!=Object.defineProperty({},1,{get:function(){return 7}})[1]}));},8418:function(t,e,r){var n=r("c04e"),i=r("9bf2"),o=r("5c6c");t.exports=function(t,e,r){var a=n(e);a in t?i.f(t,a,o(0,r)):t[a]=r;};},"841c":function(t,e,r){var n=r("d784"),i=r("825a"),o=r("1d80"),a=r("129f"),s=r("14c3");n("search",1,(function(t,e,r){return [function(e){var r=o(this),n=void 0==e?void 0:e[t];return void 0!==n?n.call(e,r):new RegExp(e)[t](String(r))},function(t){var n=r(e,t,this);if(n.done)return n.value;var o=i(t),c=String(this),u=o.lastIndex;a(u,0)||(o.lastIndex=0);var f=s(o,c);return a(o.lastIndex,u)||(o.lastIndex=u),null===f?-1:f.index}]}));},"861d":function(t,e){t.exports=function(t){return "object"===typeof t?null!==t:"function"===typeof t};},8875:function(t,e,r){var n,i,o;(function(r,a){i=[],n=a,o="function"===typeof n?n.apply(e,i):n,void 0===o||(t.exports=o);})("undefined"!==typeof self&&self,(function(){function t(){var e=Object.getOwnPropertyDescriptor(document,"currentScript");if(!e&&"currentScript"in document&&document.currentScript)return document.currentScript;if(e&&e.get!==t&&document.currentScript)return document.currentScript;try{throw new Error}catch(d){var r,n,i,o=/.*at [^(]*\((.*):(.+):(.+)\)$/gi,a=/@([^@]*):(\d+):(\d+)\s*$/gi,s=o.exec(d.stack)||a.exec(d.stack),c=s&&s[1]||!1,u=s&&s[2]||!1,f=document.location.href.replace(document.location.hash,""),l=document.getElementsByTagName("script");c===f&&(r=document.documentElement.outerHTML,n=new RegExp("(?:[^\\n]+?\\n){0,"+(u-2)+"}[^<]*<script>([\\d\\D]*?)<\\/script>[\\d\\D]*","i"),i=r.replace(n,"$1").trim());for(var p=0;p<l.length;p++){if("interactive"===l[p].readyState)return l[p];if(l[p].src===c)return l[p];if(c===f&&l[p].innerHTML&&l[p].innerHTML.trim()===i)return l[p]}return null}}return t}));},8925:function(t,e,r){var n=r("c6cd"),i=Function.toString;"function"!=typeof n.inspectSource&&(n.inspectSource=function(t){return i.call(t)}),t.exports=n.inspectSource;},"8aa5":function(t,e,r){var n=r("6547").charAt;t.exports=function(t,e,r){return e+(r?n(t,e).length:1)};},"90e3":function(t,e){var r=0,n=Math.random();t.exports=function(t){return "Symbol("+String(void 0===t?"":t)+")_"+(++r+n).toString(36)};},9112:function(t,e,r){var n=r("83ab"),i=r("9bf2"),o=r("5c6c");t.exports=n?function(t,e,r){return i.f(t,e,o(1,r))}:function(t,e,r){return t[e]=r,t};},9129:function(t,e,r){var n=r("23e7");n({target:"Number",stat:!0},{isNaN:function(t){return t!=t}});},9263:function(t,e,r){var n=r("ad6d"),i=r("9f7f"),o=RegExp.prototype.exec,a=String.prototype.replace,s=o,c=function(){var t=/a/,e=/b*/g;return o.call(t,"a"),o.call(e,"a"),0!==t.lastIndex||0!==e.lastIndex}(),u=i.UNSUPPORTED_Y||i.BROKEN_CARET,f=void 0!==/()??/.exec("")[1],l=c||f||u;l&&(s=function(t){var e,r,i,s,l=this,p=u&&l.sticky,d=n.call(l),h=l.source,v=0,m=t;return p&&(d=d.replace("y",""),-1===d.indexOf("g")&&(d+="g"),m=String(t).slice(l.lastIndex),l.lastIndex>0&&(!l.multiline||l.multiline&&"\n"!==t[l.lastIndex-1])&&(h="(?: "+h+")",m=" "+m,v++),r=new RegExp("^(?:"+h+")",d)),f&&(r=new RegExp("^"+h+"$(?!\\s)",d)),c&&(e=l.lastIndex),i=o.call(p?r:l,m),p?i?(i.input=i.input.slice(v),i[0]=i[0].slice(v),i.index=l.lastIndex,l.lastIndex+=i[0].length):l.lastIndex=0:c&&i&&(l.lastIndex=l.global?i.index+i[0].length:e),f&&i&&i.length>1&&a.call(i[0],r,(function(){for(s=1;s<arguments.length-2;s++)void 0===arguments[s]&&(i[s]=void 0);})),i}),t.exports=s;},"94ca":function(t,e,r){var n=r("d039"),i=/#|\.prototype\./,o=function(t,e){var r=s[a(t)];return r==u||r!=c&&("function"==typeof e?n(e):!!e)},a=o.normalize=function(t){return String(t).replace(i,".").toLowerCase()},s=o.data={},c=o.NATIVE="N",u=o.POLYFILL="P";t.exports=o;},"96cf":function(t,e,r){var n=function(t){var e,r=Object.prototype,n=r.hasOwnProperty,i="function"===typeof Symbol?Symbol:{},o=i.iterator||"@@iterator",a=i.asyncIterator||"@@asyncIterator",s=i.toStringTag||"@@toStringTag";function c(t,e,r){return Object.defineProperty(t,e,{value:r,enumerable:!0,configurable:!0,writable:!0}),t[e]}try{c({},"");}catch(R){c=function(t,e,r){return t[e]=r};}function u(t,e,r,n){var i=e&&e.prototype instanceof m?e:m,o=Object.create(i.prototype),a=new j(n||[]);return o._invoke=E(t,r,a),o}function f(t,e,r){try{return {type:"normal",arg:t.call(e,r)}}catch(R){return {type:"throw",arg:R}}}t.wrap=u;var l="suspendedStart",p="suspendedYield",d="executing",h="completed",v={};function m(){}function g(){}function y(){}var b={};b[o]=function(){return this};var x=Object.getPrototypeOf,k=x&&x(x(A([])));k&&k!==r&&n.call(k,o)&&(b=k);var w=y.prototype=m.prototype=Object.create(b);function _(t){["next","throw","return"].forEach((function(e){c(t,e,(function(t){return this._invoke(e,t)}));}));}function S(t,e){function r(i,o,a,s){var c=f(t[i],t,o);if("throw"!==c.type){var u=c.arg,l=u.value;return l&&"object"===typeof l&&n.call(l,"__await")?e.resolve(l.__await).then((function(t){r("next",t,a,s);}),(function(t){r("throw",t,a,s);})):e.resolve(l).then((function(t){u.value=t,a(u);}),(function(t){return r("throw",t,a,s)}))}s(c.arg);}var i;function o(t,n){function o(){return new e((function(e,i){r(t,n,e,i);}))}return i=i?i.then(o,o):o()}this._invoke=o;}function E(t,e,r){var n=l;return function(i,o){if(n===d)throw new Error("Generator is already running");if(n===h){if("throw"===i)throw o;return P()}r.method=i,r.arg=o;while(1){var a=r.delegate;if(a){var s=T(a,r);if(s){if(s===v)continue;return s}}if("next"===r.method)r.sent=r._sent=r.arg;else if("throw"===r.method){if(n===l)throw n=h,r.arg;r.dispatchException(r.arg);}else "return"===r.method&&r.abrupt("return",r.arg);n=d;var c=f(t,e,r);if("normal"===c.type){if(n=r.done?h:p,c.arg===v)continue;return {value:c.arg,done:r.done}}"throw"===c.type&&(n=h,r.method="throw",r.arg=c.arg);}}}function T(t,r){var n=t.iterator[r.method];if(n===e){if(r.delegate=null,"throw"===r.method){if(t.iterator["return"]&&(r.method="return",r.arg=e,T(t,r),"throw"===r.method))return v;r.method="throw",r.arg=new TypeError("The iterator does not provide a 'throw' method");}return v}var i=f(n,t.iterator,r.arg);if("throw"===i.type)return r.method="throw",r.arg=i.arg,r.delegate=null,v;var o=i.arg;return o?o.done?(r[t.resultName]=o.value,r.next=t.nextLoc,"return"!==r.method&&(r.method="next",r.arg=e),r.delegate=null,v):o:(r.method="throw",r.arg=new TypeError("iterator result is not an object"),r.delegate=null,v)}function O(t){var e={tryLoc:t[0]};1 in t&&(e.catchLoc=t[1]),2 in t&&(e.finallyLoc=t[2],e.afterLoc=t[3]),this.tryEntries.push(e);}function C(t){var e=t.completion||{};e.type="normal",delete e.arg,t.completion=e;}function j(t){this.tryEntries=[{tryLoc:"root"}],t.forEach(O,this),this.reset(!0);}function A(t){if(t){var r=t[o];if(r)return r.call(t);if("function"===typeof t.next)return t;if(!isNaN(t.length)){var i=-1,a=function r(){while(++i<t.length)if(n.call(t,i))return r.value=t[i],r.done=!1,r;return r.value=e,r.done=!0,r};return a.next=a}}return {next:P}}function P(){return {value:e,done:!0}}return g.prototype=w.constructor=y,y.constructor=g,g.displayName=c(y,s,"GeneratorFunction"),t.isGeneratorFunction=function(t){var e="function"===typeof t&&t.constructor;return !!e&&(e===g||"GeneratorFunction"===(e.displayName||e.name))},t.mark=function(t){return Object.setPrototypeOf?Object.setPrototypeOf(t,y):(t.__proto__=y,c(t,s,"GeneratorFunction")),t.prototype=Object.create(w),t},t.awrap=function(t){return {__await:t}},_(S.prototype),S.prototype[a]=function(){return this},t.AsyncIterator=S,t.async=function(e,r,n,i,o){void 0===o&&(o=Promise);var a=new S(u(e,r,n,i),o);return t.isGeneratorFunction(r)?a:a.next().then((function(t){return t.done?t.value:a.next()}))},_(w),c(w,s,"Generator"),w[o]=function(){return this},w.toString=function(){return "[object Generator]"},t.keys=function(t){var e=[];for(var r in t)e.push(r);return e.reverse(),function r(){while(e.length){var n=e.pop();if(n in t)return r.value=n,r.done=!1,r}return r.done=!0,r}},t.values=A,j.prototype={constructor:j,reset:function(t){if(this.prev=0,this.next=0,this.sent=this._sent=e,this.done=!1,this.delegate=null,this.method="next",this.arg=e,this.tryEntries.forEach(C),!t)for(var r in this)"t"===r.charAt(0)&&n.call(this,r)&&!isNaN(+r.slice(1))&&(this[r]=e);},stop:function(){this.done=!0;var t=this.tryEntries[0],e=t.completion;if("throw"===e.type)throw e.arg;return this.rval},dispatchException:function(t){if(this.done)throw t;var r=this;function i(n,i){return s.type="throw",s.arg=t,r.next=n,i&&(r.method="next",r.arg=e),!!i}for(var o=this.tryEntries.length-1;o>=0;--o){var a=this.tryEntries[o],s=a.completion;if("root"===a.tryLoc)return i("end");if(a.tryLoc<=this.prev){var c=n.call(a,"catchLoc"),u=n.call(a,"finallyLoc");if(c&&u){if(this.prev<a.catchLoc)return i(a.catchLoc,!0);if(this.prev<a.finallyLoc)return i(a.finallyLoc)}else if(c){if(this.prev<a.catchLoc)return i(a.catchLoc,!0)}else {if(!u)throw new Error("try statement without catch or finally");if(this.prev<a.finallyLoc)return i(a.finallyLoc)}}}},abrupt:function(t,e){for(var r=this.tryEntries.length-1;r>=0;--r){var i=this.tryEntries[r];if(i.tryLoc<=this.prev&&n.call(i,"finallyLoc")&&this.prev<i.finallyLoc){var o=i;break}}o&&("break"===t||"continue"===t)&&o.tryLoc<=e&&e<=o.finallyLoc&&(o=null);var a=o?o.completion:{};return a.type=t,a.arg=e,o?(this.method="next",this.next=o.finallyLoc,v):this.complete(a)},complete:function(t,e){if("throw"===t.type)throw t.arg;return "break"===t.type||"continue"===t.type?this.next=t.arg:"return"===t.type?(this.rval=this.arg=t.arg,this.method="return",this.next="end"):"normal"===t.type&&e&&(this.next=e),v},finish:function(t){for(var e=this.tryEntries.length-1;e>=0;--e){var r=this.tryEntries[e];if(r.finallyLoc===t)return this.complete(r.completion,r.afterLoc),C(r),v}},catch:function(t){for(var e=this.tryEntries.length-1;e>=0;--e){var r=this.tryEntries[e];if(r.tryLoc===t){var n=r.completion;if("throw"===n.type){var i=n.arg;C(r);}return i}}throw new Error("illegal catch attempt")},delegateYield:function(t,r,n){return this.delegate={iterator:A(t),resultName:r,nextLoc:n},"next"===this.method&&(this.arg=e),v}},t}(t.exports);try{regeneratorRuntime=n;}catch(i){Function("r","regeneratorRuntime = r")(n);}},"99af":function(t,e,r){var n=r("23e7"),i=r("d039"),o=r("e8b5"),a=r("861d"),s=r("7b0b"),c=r("50c4"),u=r("8418"),f=r("65f0"),l=r("1dde"),p=r("b622"),d=r("2d00"),h=p("isConcatSpreadable"),v=9007199254740991,m="Maximum allowed index exceeded",g=d>=51||!i((function(){var t=[];return t[h]=!1,t.concat()[0]!==t})),y=l("concat"),b=function(t){if(!a(t))return !1;var e=t[h];return void 0!==e?!!e:o(t)},x=!g||!y;n({target:"Array",proto:!0,forced:x},{concat:function(t){var e,r,n,i,o,a=s(this),l=f(a,0),p=0;for(e=-1,n=arguments.length;e<n;e++)if(o=-1===e?a:arguments[e],b(o)){if(i=c(o.length),p+i>v)throw TypeError(m);for(r=0;r<i;r++,p++)r in o&&u(l,p,o[r]);}else {if(p>=v)throw TypeError(m);u(l,p++,o);}return l.length=p,l}});},"9bdd":function(t,e,r){var n=r("825a");t.exports=function(t,e,r,i){try{return i?e(n(r)[0],r[1]):e(r)}catch(a){var o=t["return"];throw void 0!==o&&n(o.call(t)),a}};},"9bf2":function(t,e,r){var n=r("83ab"),i=r("0cfb"),o=r("825a"),a=r("c04e"),s=Object.defineProperty;e.f=n?s:function(t,e,r){if(o(t),e=a(e,!0),o(r),i)try{return s(t,e,r)}catch(n){}if("get"in r||"set"in r)throw TypeError("Accessors not supported");return "value"in r&&(t[e]=r.value),t};},"9ed3":function(t,e,r){var n=r("ae93").IteratorPrototype,i=r("7c73"),o=r("5c6c"),a=r("d44e"),s=r("3f8c"),c=function(){return this};t.exports=function(t,e,r){var u=e+" Iterator";return t.prototype=i(n,{next:o(1,r)}),a(t,u,!1,!0),s[u]=c,t};},"9f7f":function(t,e,r){var n=r("d039");function i(t,e){return RegExp(t,e)}e.UNSUPPORTED_Y=n((function(){var t=i("a","y");return t.lastIndex=2,null!=t.exec("abcd")})),e.BROKEN_CARET=n((function(){var t=i("^r","gy");return t.lastIndex=2,null!=t.exec("str")}));},a231:function(t,e,r){var n=r("1d1a");"string"===typeof n&&(n=[[t.i,n,""]]),n.locals&&(t.exports=n.locals);var i=r("499e").default;i("1ef95f13",n,!0,{sourceMap:!1,shadowMode:!1});},a4d3:function(t,e,r){var n=r("23e7"),i=r("da84"),o=r("d066"),a=r("c430"),s=r("83ab"),c=r("4930"),u=r("fdbf"),f=r("d039"),l=r("5135"),p=r("e8b5"),d=r("861d"),h=r("825a"),v=r("7b0b"),m=r("fc6a"),g=r("c04e"),y=r("5c6c"),b=r("7c73"),x=r("df75"),k=r("241c"),w=r("057f"),_=r("7418"),S=r("06cf"),E=r("9bf2"),T=r("d1e7"),O=r("9112"),C=r("6eeb"),j=r("5692"),A=r("f772"),P=r("d012"),R=r("90e3"),F=r("b622"),I=r("e538"),N=r("746f"),D=r("d44e"),L=r("69f3"),M=r("b727").forEach,z=A("hidden"),q="Symbol",V="prototype",G=F("toPrimitive"),$=L.set,B=L.getterFor(q),U=Object[V],W=i.Symbol,H=o("JSON","stringify"),X=S.f,Y=E.f,K=w.f,J=T.f,Q=j("symbols"),Z=j("op-symbols"),tt=j("string-to-symbol-registry"),et=j("symbol-to-string-registry"),rt=j("wks"),nt=i.QObject,it=!nt||!nt[V]||!nt[V].findChild,ot=s&&f((function(){return 7!=b(Y({},"a",{get:function(){return Y(this,"a",{value:7}).a}})).a}))?function(t,e,r){var n=X(U,e);n&&delete U[e],Y(t,e,r),n&&t!==U&&Y(U,e,n);}:Y,at=function(t,e){var r=Q[t]=b(W[V]);return $(r,{type:q,tag:t,description:e}),s||(r.description=e),r},st=u?function(t){return "symbol"==typeof t}:function(t){return Object(t)instanceof W},ct=function(t,e,r){t===U&&ct(Z,e,r),h(t);var n=g(e,!0);return h(r),l(Q,n)?(r.enumerable?(l(t,z)&&t[z][n]&&(t[z][n]=!1),r=b(r,{enumerable:y(0,!1)})):(l(t,z)||Y(t,z,y(1,{})),t[z][n]=!0),ot(t,n,r)):Y(t,n,r)},ut=function(t,e){h(t);var r=m(e),n=x(r).concat(ht(r));return M(n,(function(e){s&&!lt.call(r,e)||ct(t,e,r[e]);})),t},ft=function(t,e){return void 0===e?b(t):ut(b(t),e)},lt=function(t){var e=g(t,!0),r=J.call(this,e);return !(this===U&&l(Q,e)&&!l(Z,e))&&(!(r||!l(this,e)||!l(Q,e)||l(this,z)&&this[z][e])||r)},pt=function(t,e){var r=m(t),n=g(e,!0);if(r!==U||!l(Q,n)||l(Z,n)){var i=X(r,n);return !i||!l(Q,n)||l(r,z)&&r[z][n]||(i.enumerable=!0),i}},dt=function(t){var e=K(m(t)),r=[];return M(e,(function(t){l(Q,t)||l(P,t)||r.push(t);})),r},ht=function(t){var e=t===U,r=K(e?Z:m(t)),n=[];return M(r,(function(t){!l(Q,t)||e&&!l(U,t)||n.push(Q[t]);})),n};if(c||(W=function(){if(this instanceof W)throw TypeError("Symbol is not a constructor");var t=arguments.length&&void 0!==arguments[0]?String(arguments[0]):void 0,e=R(t),r=function(t){this===U&&r.call(Z,t),l(this,z)&&l(this[z],e)&&(this[z][e]=!1),ot(this,e,y(1,t));};return s&&it&&ot(U,e,{configurable:!0,set:r}),at(e,t)},C(W[V],"toString",(function(){return B(this).tag})),C(W,"withoutSetter",(function(t){return at(R(t),t)})),T.f=lt,E.f=ct,S.f=pt,k.f=w.f=dt,_.f=ht,I.f=function(t){return at(F(t),t)},s&&(Y(W[V],"description",{configurable:!0,get:function(){return B(this).description}}),a||C(U,"propertyIsEnumerable",lt,{unsafe:!0}))),n({global:!0,wrap:!0,forced:!c,sham:!c},{Symbol:W}),M(x(rt),(function(t){N(t);})),n({target:q,stat:!0,forced:!c},{for:function(t){var e=String(t);if(l(tt,e))return tt[e];var r=W(e);return tt[e]=r,et[r]=e,r},keyFor:function(t){if(!st(t))throw TypeError(t+" is not a symbol");if(l(et,t))return et[t]},useSetter:function(){it=!0;},useSimple:function(){it=!1;}}),n({target:"Object",stat:!0,forced:!c,sham:!s},{create:ft,defineProperty:ct,defineProperties:ut,getOwnPropertyDescriptor:pt}),n({target:"Object",stat:!0,forced:!c},{getOwnPropertyNames:dt,getOwnPropertySymbols:ht}),n({target:"Object",stat:!0,forced:f((function(){_.f(1);}))},{getOwnPropertySymbols:function(t){return _.f(v(t))}}),H){var vt=!c||f((function(){var t=W();return "[null]"!=H([t])||"{}"!=H({a:t})||"{}"!=H(Object(t))}));n({target:"JSON",stat:!0,forced:vt},{stringify:function(t,e,r){var n,i=[t],o=1;while(arguments.length>o)i.push(arguments[o++]);if(n=e,(d(e)||void 0!==t)&&!st(t))return p(e)||(e=function(t,e){if("function"==typeof n&&(e=n.call(this,t,e)),!st(e))return e}),i[1]=e,H.apply(null,i)}});}W[V][G]||O(W[V],G,W[V].valueOf),D(W,q),P[z]=!0;},a623:function(t,e,r){var n=r("23e7"),i=r("b727").every,o=r("a640"),a=r("ae40"),s=o("every"),c=a("every");n({target:"Array",proto:!0,forced:!s||!c},{every:function(t){return i(this,t,arguments.length>1?arguments[1]:void 0)}});},a640:function(t,e,r){var n=r("d039");t.exports=function(t,e){var r=[][t];return !!r&&n((function(){r.call(null,e||function(){throw 1},1);}))};},a691:function(t,e){var r=Math.ceil,n=Math.floor;t.exports=function(t){return isNaN(t=+t)?0:(t>0?n:r)(t)};},a91a:function(t,e,r){var n=r("24fb");e=n(!1),e.push([t.i,".ep-circle--progress__dot-container[data-v-38ca167f]{position:absolute;-webkit-transform-origin:center center;transform-origin:center center}.ep-circle--progress__dot-container.hidden[data-v-38ca167f]{-webkit-transition-duration:0s;-o-transition-duration:0s;transition-duration:0s}.ep-circle--progress__dot-container>div[data-v-38ca167f]{position:relative}.ep-circle--progress__dot[data-v-38ca167f]{-webkit-transition-duration:.2s;-o-transition-duration:.2s;transition-duration:.2s;-webkit-box-sizing:border-box;box-sizing:border-box;position:absolute;margin:auto;right:0;left:0}.ep-circle--progress__dot.ep-hidden[data-v-38ca167f]{-webkit-transform:scale(0);transform:scale(0)}",""]),t.exports=e;},a9e3:function(t,e,r){var n=r("83ab"),i=r("da84"),o=r("94ca"),a=r("6eeb"),s=r("5135"),c=r("c6b6"),u=r("7156"),f=r("c04e"),l=r("d039"),p=r("7c73"),d=r("241c").f,h=r("06cf").f,v=r("9bf2").f,m=r("58a8").trim,g="Number",y=i[g],b=y.prototype,x=c(p(b))==g,k=function(t){var e,r,n,i,o,a,s,c,u=f(t,!1);if("string"==typeof u&&u.length>2)if(u=m(u),e=u.charCodeAt(0),43===e||45===e){if(r=u.charCodeAt(2),88===r||120===r)return NaN}else if(48===e){switch(u.charCodeAt(1)){case 66:case 98:n=2,i=49;break;case 79:case 111:n=8,i=55;break;default:return +u}for(o=u.slice(2),a=o.length,s=0;s<a;s++)if(c=o.charCodeAt(s),c<48||c>i)return NaN;return parseInt(o,n)}return +u};if(o(g,!y(" 0o1")||!y("0b1")||y("+0x1"))){for(var w,_=function(t){var e=arguments.length<1?0:t,r=this;return r instanceof _&&(x?l((function(){b.valueOf.call(r);})):c(r)!=g)?u(new y(k(e)),r,_):k(e)},S=n?d(y):"MAX_VALUE,MIN_VALUE,NaN,NEGATIVE_INFINITY,POSITIVE_INFINITY,EPSILON,isFinite,isInteger,isNaN,isSafeInteger,MAX_SAFE_INTEGER,MIN_SAFE_INTEGER,parseFloat,parseInt,isInteger".split(","),E=0;S.length>E;E++)s(y,w=S[E])&&!s(_,w)&&v(_,w,h(y,w));_.prototype=b,b.constructor=_,a(i,g,_);}},ab13:function(t,e,r){var n=r("b622"),i=n("match");t.exports=function(t){var e=/./;try{"/./"[t](e);}catch(r){try{return e[i]=!1,"/./"[t](e)}catch(n){}}return !1};},ac1f:function(t,e,r){var n=r("23e7"),i=r("9263");n({target:"RegExp",proto:!0,forced:/./.exec!==i},{exec:i});},ad6d:function(t,e,r){var n=r("825a");t.exports=function(){var t=n(this),e="";return t.global&&(e+="g"),t.ignoreCase&&(e+="i"),t.multiline&&(e+="m"),t.dotAll&&(e+="s"),t.unicode&&(e+="u"),t.sticky&&(e+="y"),e};},ae40:function(t,e,r){var n=r("83ab"),i=r("d039"),o=r("5135"),a=Object.defineProperty,s={},c=function(t){throw t};t.exports=function(t,e){if(o(s,t))return s[t];e||(e={});var r=[][t],u=!!o(e,"ACCESSORS")&&e.ACCESSORS,f=o(e,0)?e[0]:c,l=o(e,1)?e[1]:void 0;return s[t]=!!r&&!i((function(){if(u&&!n)return !0;var t={length:-1};u?a(t,1,{enumerable:!0,get:c}):t[1]=1,r.call(t,f,l);}))};},ae93:function(t,e,r){var n,i,o,a=r("e163"),s=r("9112"),c=r("5135"),u=r("b622"),f=r("c430"),l=u("iterator"),p=!1,d=function(){return this};[].keys&&(o=[].keys(),"next"in o?(i=a(a(o)),i!==Object.prototype&&(n=i)):p=!0),void 0==n&&(n={}),f||c(n,l)||s(n,l,d),t.exports={IteratorPrototype:n,BUGGY_SAFARI_ITERATORS:p};},b041:function(t,e,r){var n=r("00ee"),i=r("f5df");t.exports=n?{}.toString:function(){return "[object "+i(this)+"]"};},b575:function(t,e,r){var n,i,o,a,s,c,u,f,l=r("da84"),p=r("06cf").f,d=r("c6b6"),h=r("2cf4").set,v=r("1cdc"),m=l.MutationObserver||l.WebKitMutationObserver,g=l.process,y=l.Promise,b="process"==d(g),x=p(l,"queueMicrotask"),k=x&&x.value;k||(n=function(){var t,e;b&&(t=g.domain)&&t.exit();while(i){e=i.fn,i=i.next;try{e();}catch(r){throw i?a():o=void 0,r}}o=void 0,t&&t.enter();},b?a=function(){g.nextTick(n);}:m&&!v?(s=!0,c=document.createTextNode(""),new m(n).observe(c,{characterData:!0}),a=function(){c.data=s=!s;}):y&&y.resolve?(u=y.resolve(void 0),f=u.then,a=function(){f.call(u,n);}):a=function(){h.call(l,n);}),t.exports=k||function(t){var e={fn:t,next:void 0};o&&(o.next=e),i||(i=e,a()),o=e;};},b622:function(t,e,r){var n=r("da84"),i=r("5692"),o=r("5135"),a=r("90e3"),s=r("4930"),c=r("fdbf"),u=i("wks"),f=n.Symbol,l=c?f:f&&f.withoutSetter||a;t.exports=function(t){return o(u,t)||(s&&o(f,t)?u[t]=f[t]:u[t]=l("Symbol."+t)),u[t]};},b64b:function(t,e,r){var n=r("23e7"),i=r("7b0b"),o=r("df75"),a=r("d039"),s=a((function(){o(1);}));n({target:"Object",stat:!0,forced:s},{keys:function(t){return o(i(t))}});},b680:function(t,e,r){var n=r("23e7"),i=r("a691"),o=r("408a"),a=r("1148"),s=r("d039"),c=1..toFixed,u=Math.floor,f=function(t,e,r){return 0===e?r:e%2===1?f(t,e-1,r*t):f(t*t,e/2,r)},l=function(t){var e=0,r=t;while(r>=4096)e+=12,r/=4096;while(r>=2)e+=1,r/=2;return e},p=c&&("0.000"!==8e-5.toFixed(3)||"1"!==.9.toFixed(0)||"1.25"!==1.255.toFixed(2)||"1000000000000000128"!==(0xde0b6b3a7640080).toFixed(0))||!s((function(){c.call({});}));n({target:"Number",proto:!0,forced:p},{toFixed:function(t){var e,r,n,s,c=o(this),p=i(t),d=[0,0,0,0,0,0],h="",v="0",m=function(t,e){var r=-1,n=e;while(++r<6)n+=t*d[r],d[r]=n%1e7,n=u(n/1e7);},g=function(t){var e=6,r=0;while(--e>=0)r+=d[e],d[e]=u(r/t),r=r%t*1e7;},y=function(){var t=6,e="";while(--t>=0)if(""!==e||0===t||0!==d[t]){var r=String(d[t]);e=""===e?r:e+a.call("0",7-r.length)+r;}return e};if(p<0||p>20)throw RangeError("Incorrect fraction digits");if(c!=c)return "NaN";if(c<=-1e21||c>=1e21)return String(c);if(c<0&&(h="-",c=-c),c>1e-21)if(e=l(c*f(2,69,1))-69,r=e<0?c*f(2,-e,1):c/f(2,e,1),r*=4503599627370496,e=52-e,e>0){m(0,r),n=p;while(n>=7)m(1e7,0),n-=7;m(f(10,n,1),0),n=e-1;while(n>=23)g(1<<23),n-=23;g(1<<n),m(1,1),g(2),v=y();}else m(0,r),m(1<<-e,0),v=y()+a.call("0",p);return p>0?(s=v.length,v=h+(s<=p?"0."+a.call("0",p-s)+v:v.slice(0,s-p)+"."+v.slice(s-p))):v=h+v,v}});},b727:function(t,e,r){var n=r("0366"),i=r("44ad"),o=r("7b0b"),a=r("50c4"),s=r("65f0"),c=[].push,u=function(t){var e=1==t,r=2==t,u=3==t,f=4==t,l=6==t,p=5==t||l;return function(d,h,v,m){for(var g,y,b=o(d),x=i(b),k=n(h,v,3),w=a(x.length),_=0,S=m||s,E=e?S(d,w):r?S(d,0):void 0;w>_;_++)if((p||_ in x)&&(g=x[_],y=k(g,_,b),t))if(e)E[_]=y;else if(y)switch(t){case 3:return !0;case 5:return g;case 6:return _;case 2:c.call(E,g);}else if(f)return !1;return l?-1:u||f?f:E}};t.exports={forEach:u(0),map:u(1),filter:u(2),some:u(3),every:u(4),find:u(5),findIndex:u(6)};},c04e:function(t,e,r){var n=r("861d");t.exports=function(t,e){if(!n(t))return t;var r,i;if(e&&"function"==typeof(r=t.toString)&&!n(i=r.call(t)))return i;if("function"==typeof(r=t.valueOf)&&!n(i=r.call(t)))return i;if(!e&&"function"==typeof(r=t.toString)&&!n(i=r.call(t)))return i;throw TypeError("Can't convert object to primitive value")};},c430:function(t,e){t.exports=!1;},c49e:function(t,e,r){var n=r("fec1"),i=r.n(n);i.a;},c6b6:function(t,e){var r={}.toString;t.exports=function(t){return r.call(t).slice(8,-1)};},c6cd:function(t,e,r){var n=r("da84"),i=r("ce4e"),o="__core-js_shared__",a=n[o]||i(o,{});t.exports=a;},c8ba:function(t,e){var r;r=function(){return this}();try{r=r||new Function("return this")();}catch(n){"object"===typeof window&&(r=window);}t.exports=r;},c8d2:function(t,e,r){var n=r("d039"),i=r("5899"),o="​᠎";t.exports=function(t){return n((function(){return !!i[t]()||o[t]()!=o||i[t].name!==t}))};},ca84:function(t,e,r){var n=r("5135"),i=r("fc6a"),o=r("4d64").indexOf,a=r("d012");t.exports=function(t,e){var r,s=i(t),c=0,u=[];for(r in s)!n(a,r)&&n(s,r)&&u.push(r);while(e.length>c)n(s,r=e[c++])&&(~o(u,r)||u.push(r));return u};},caad:function(t,e,r){var n=r("23e7"),i=r("4d64").includes,o=r("44d2"),a=r("ae40"),s=a("indexOf",{ACCESSORS:!0,1:0});n({target:"Array",proto:!0,forced:!s},{includes:function(t){return i(this,t,arguments.length>1?arguments[1]:void 0)}}),o("includes");},cc12:function(t,e,r){var n=r("da84"),i=r("861d"),o=n.document,a=i(o)&&i(o.createElement);t.exports=function(t){return a?o.createElement(t):{}};},cdf9:function(t,e,r){var n=r("825a"),i=r("861d"),o=r("f069");t.exports=function(t,e){if(n(t),i(e)&&e.constructor===t)return e;var r=o.f(t),a=r.resolve;return a(e),r.promise};},ce4e:function(t,e,r){var n=r("da84"),i=r("9112");t.exports=function(t,e){try{i(n,t,e);}catch(r){n[t]=e;}return e};},d012:function(t,e){t.exports={};},d039:function(t,e){t.exports=function(t){try{return !!t()}catch(e){return !0}};},d066:function(t,e,r){var n=r("428f"),i=r("da84"),o=function(t){return "function"==typeof t?t:void 0};t.exports=function(t,e){return arguments.length<2?o(n[t])||o(i[t]):n[t]&&n[t][e]||i[t]&&i[t][e]};},d07f:function(t,e,r){var n=r("a91a");"string"===typeof n&&(n=[[t.i,n,""]]),n.locals&&(t.exports=n.locals);var i=r("499e").default;i("a2fae2cc",n,!0,{sourceMap:!1,shadowMode:!1});},d1e7:function(t,e,r){var n={}.propertyIsEnumerable,i=Object.getOwnPropertyDescriptor,o=i&&!n.call({1:2},1);e.f=o?function(t){var e=i(this,t);return !!e&&e.enumerable}:n;},d28b:function(t,e,r){var n=r("746f");n("iterator");},d2bb:function(t,e,r){var n=r("825a"),i=r("3bbe");t.exports=Object.setPrototypeOf||("__proto__"in{}?function(){var t,e=!1,r={};try{t=Object.getOwnPropertyDescriptor(Object.prototype,"__proto__").set,t.call(r,[]),e=r instanceof Array;}catch(o){}return function(r,o){return n(r),i(o),e?t.call(r,o):r.__proto__=o,r}}():void 0);},d30c:function(t,e,r){var n=r("d5a4");"string"===typeof n&&(n=[[t.i,n,""]]),n.locals&&(t.exports=n.locals);var i=r("499e").default;i("3d0aec29",n,!0,{sourceMap:!1,shadowMode:!1});},d3b7:function(t,e,r){var n=r("00ee"),i=r("6eeb"),o=r("b041");n||i(Object.prototype,"toString",o,{unsafe:!0});},d44e:function(t,e,r){var n=r("9bf2").f,i=r("5135"),o=r("b622"),a=o("toStringTag");t.exports=function(t,e,r){t&&!i(t=r?t:t.prototype,a)&&n(t,a,{configurable:!0,value:e});};},d58f:function(t,e,r){var n=r("1c0b"),i=r("7b0b"),o=r("44ad"),a=r("50c4"),s=function(t){return function(e,r,s,c){n(r);var u=i(e),f=o(u),l=a(u.length),p=t?l-1:0,d=t?-1:1;if(s<2)while(1){if(p in f){c=f[p],p+=d;break}if(p+=d,t?p<0:l<=p)throw TypeError("Reduce of empty array with no initial value")}for(;t?p>=0:l>p;p+=d)p in f&&(c=r(c,f[p],p,u));return c}};t.exports={left:s(!1),right:s(!0)};},d5a4:function(t,e,r){var n=r("24fb");e=n(!1),e.push([t.i,".ep-circle[data-v-7d0a0fbb]{-webkit-transform-origin:50% 50%;transform-origin:50% 50%}",""]),t.exports=e;},d784:function(t,e,r){r("ac1f");var n=r("6eeb"),i=r("d039"),o=r("b622"),a=r("9263"),s=r("9112"),c=o("species"),u=!i((function(){var t=/./;return t.exec=function(){var t=[];return t.groups={a:"7"},t},"7"!=="".replace(t,"$<a>")})),f=function(){return "$0"==="a".replace(/./,"$0")}(),l=o("replace"),p=function(){return !!/./[l]&&""===/./[l]("a","$0")}(),d=!i((function(){var t=/(?:)/,e=t.exec;t.exec=function(){return e.apply(this,arguments)};var r="ab".split(t);return 2!==r.length||"a"!==r[0]||"b"!==r[1]}));t.exports=function(t,e,r,l){var h=o(t),v=!i((function(){var e={};return e[h]=function(){return 7},7!=""[t](e)})),m=v&&!i((function(){var e=!1,r=/a/;return "split"===t&&(r={},r.constructor={},r.constructor[c]=function(){return r},r.flags="",r[h]=/./[h]),r.exec=function(){return e=!0,null},r[h](""),!e}));if(!v||!m||"replace"===t&&(!u||!f||p)||"split"===t&&!d){var g=/./[h],y=r(h,""[t],(function(t,e,r,n,i){return e.exec===a?v&&!i?{done:!0,value:g.call(e,r,n)}:{done:!0,value:t.call(r,e,n)}:{done:!1}}),{REPLACE_KEEPS_$0:f,REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE:p}),b=y[0],x=y[1];n(String.prototype,t,b),n(RegExp.prototype,h,2==e?function(t,e){return x.call(t,this,e)}:function(t){return x.call(t,this)});}l&&s(RegExp.prototype[h],"sham",!0);};},d81d:function(t,e,r){var n=r("23e7"),i=r("b727").map,o=r("1dde"),a=r("ae40"),s=o("map"),c=a("map");n({target:"Array",proto:!0,forced:!s||!c},{map:function(t){return i(this,t,arguments.length>1?arguments[1]:void 0)}});},da84:function(t,e,r){(function(e){var r=function(t){return t&&t.Math==Math&&t};t.exports=r("object"==typeof globalThis&&globalThis)||r("object"==typeof window&&window)||r("object"==typeof self&&self)||r("object"==typeof e&&e)||Function("return this")();}).call(this,r("c8ba"));},dbb4:function(t,e,r){var n=r("23e7"),i=r("83ab"),o=r("56ef"),a=r("fc6a"),s=r("06cf"),c=r("8418");n({target:"Object",stat:!0,sham:!i},{getOwnPropertyDescriptors:function(t){var e,r,n=a(t),i=s.f,u=o(n),f={},l=0;while(u.length>l)r=i(n,e=u[l++]),void 0!==r&&c(f,e,r);return f}});},ddb0:function(t,e,r){var n=r("da84"),i=r("fdbc"),o=r("e260"),a=r("9112"),s=r("b622"),c=s("iterator"),u=s("toStringTag"),f=o.values;for(var l in i){var p=n[l],d=p&&p.prototype;if(d){if(d[c]!==f)try{a(d,c,f);}catch(v){d[c]=f;}if(d[u]||a(d,u,l),i[l])for(var h in o)if(d[h]!==o[h])try{a(d,h,o[h]);}catch(v){d[h]=o[h];}}}},df75:function(t,e,r){var n=r("ca84"),i=r("7839");t.exports=Object.keys||function(t){return n(t,i)};},e01a:function(t,e,r){var n=r("23e7"),i=r("83ab"),o=r("da84"),a=r("5135"),s=r("861d"),c=r("9bf2").f,u=r("e893"),f=o.Symbol;if(i&&"function"==typeof f&&(!("description"in f.prototype)||void 0!==f().description)){var l={},p=function(){var t=arguments.length<1||void 0===arguments[0]?void 0:String(arguments[0]),e=this instanceof p?new f(t):void 0===t?f():f(t);return ""===t&&(l[e]=!0),e};u(p,f);var d=p.prototype=f.prototype;d.constructor=p;var h=d.toString,v="Symbol(test)"==String(f("test")),m=/^Symbol\((.*)\)[^)]+$/;c(d,"description",{configurable:!0,get:function(){var t=s(this)?this.valueOf():this,e=h.call(t);if(a(l,t))return "";var r=v?e.slice(7,-1):e.replace(m,"$1");return ""===r?void 0:r}}),n({global:!0,forced:!0},{Symbol:p});}},e163:function(t,e,r){var n=r("5135"),i=r("7b0b"),o=r("f772"),a=r("e177"),s=o("IE_PROTO"),c=Object.prototype;t.exports=a?Object.getPrototypeOf:function(t){return t=i(t),n(t,s)?t[s]:"function"==typeof t.constructor&&t instanceof t.constructor?t.constructor.prototype:t instanceof Object?c:null};},e177:function(t,e,r){var n=r("d039");t.exports=!n((function(){function t(){}return t.prototype.constructor=null,Object.getPrototypeOf(new t)!==t.prototype}));},e260:function(t,e,r){var n=r("fc6a"),i=r("44d2"),o=r("3f8c"),a=r("69f3"),s=r("7dd0"),c="Array Iterator",u=a.set,f=a.getterFor(c);t.exports=s(Array,"Array",(function(t,e){u(this,{type:c,target:n(t),index:0,kind:e});}),(function(){var t=f(this),e=t.target,r=t.kind,n=t.index++;return !e||n>=e.length?(t.target=void 0,{value:void 0,done:!0}):"keys"==r?{value:n,done:!1}:"values"==r?{value:e[n],done:!1}:{value:[n,e[n]],done:!1}}),"values"),o.Arguments=o.Array,i("keys"),i("values"),i("entries");},e2cc:function(t,e,r){var n=r("6eeb");t.exports=function(t,e,r){for(var i in e)n(t,i,e[i],r);return t};},e439:function(t,e,r){var n=r("23e7"),i=r("d039"),o=r("fc6a"),a=r("06cf").f,s=r("83ab"),c=i((function(){a(1);})),u=!s||c;n({target:"Object",stat:!0,forced:u,sham:!s},{getOwnPropertyDescriptor:function(t,e){return a(o(t),e)}});},e538:function(t,e,r){var n=r("b622");e.f=n;},e667:function(t,e){t.exports=function(t){try{return {error:!1,value:t()}}catch(e){return {error:!0,value:e}}};},e6cf:function(t,e,r){var n,i,o,a,s=r("23e7"),c=r("c430"),u=r("da84"),f=r("d066"),l=r("fea9"),p=r("6eeb"),d=r("e2cc"),h=r("d44e"),v=r("2626"),m=r("861d"),g=r("1c0b"),y=r("19aa"),b=r("c6b6"),x=r("8925"),k=r("2266"),w=r("1c7e"),_=r("4840"),S=r("2cf4").set,E=r("b575"),T=r("cdf9"),O=r("44de"),C=r("f069"),j=r("e667"),A=r("69f3"),P=r("94ca"),R=r("b622"),F=r("2d00"),I=R("species"),N="Promise",D=A.get,L=A.set,M=A.getterFor(N),z=l,q=u.TypeError,V=u.document,G=u.process,$=f("fetch"),B=C.f,U=B,W="process"==b(G),H=!!(V&&V.createEvent&&u.dispatchEvent),X="unhandledrejection",Y="rejectionhandled",K=0,J=1,Q=2,Z=1,tt=2,et=P(N,(function(){var t=x(z)!==String(z);if(!t){if(66===F)return !0;if(!W&&"function"!=typeof PromiseRejectionEvent)return !0}if(c&&!z.prototype["finally"])return !0;if(F>=51&&/native code/.test(z))return !1;var e=z.resolve(1),r=function(t){t((function(){}),(function(){}));},n=e.constructor={};return n[I]=r,!(e.then((function(){}))instanceof r)})),rt=et||!w((function(t){z.all(t)["catch"]((function(){}));})),nt=function(t){var e;return !(!m(t)||"function"!=typeof(e=t.then))&&e},it=function(t,e,r){if(!e.notified){e.notified=!0;var n=e.reactions;E((function(){var i=e.value,o=e.state==J,a=0;while(n.length>a){var s,c,u,f=n[a++],l=o?f.ok:f.fail,p=f.resolve,d=f.reject,h=f.domain;try{l?(o||(e.rejection===tt&&ct(t,e),e.rejection=Z),!0===l?s=i:(h&&h.enter(),s=l(i),h&&(h.exit(),u=!0)),s===f.promise?d(q("Promise-chain cycle")):(c=nt(s))?c.call(s,p,d):p(s)):d(i);}catch(v){h&&!u&&h.exit(),d(v);}}e.reactions=[],e.notified=!1,r&&!e.rejection&&at(t,e);}));}},ot=function(t,e,r){var n,i;H?(n=V.createEvent("Event"),n.promise=e,n.reason=r,n.initEvent(t,!1,!0),u.dispatchEvent(n)):n={promise:e,reason:r},(i=u["on"+t])?i(n):t===X&&O("Unhandled promise rejection",r);},at=function(t,e){S.call(u,(function(){var r,n=e.value,i=st(e);if(i&&(r=j((function(){W?G.emit("unhandledRejection",n,t):ot(X,t,n);})),e.rejection=W||st(e)?tt:Z,r.error))throw r.value}));},st=function(t){return t.rejection!==Z&&!t.parent},ct=function(t,e){S.call(u,(function(){W?G.emit("rejectionHandled",t):ot(Y,t,e.value);}));},ut=function(t,e,r,n){return function(i){t(e,r,i,n);}},ft=function(t,e,r,n){e.done||(e.done=!0,n&&(e=n),e.value=r,e.state=Q,it(t,e,!0));},lt=function(t,e,r,n){if(!e.done){e.done=!0,n&&(e=n);try{if(t===r)throw q("Promise can't be resolved itself");var i=nt(r);i?E((function(){var n={done:!1};try{i.call(r,ut(lt,t,n,e),ut(ft,t,n,e));}catch(o){ft(t,n,o,e);}})):(e.value=r,e.state=J,it(t,e,!1));}catch(o){ft(t,{done:!1},o,e);}}};et&&(z=function(t){y(this,z,N),g(t),n.call(this);var e=D(this);try{t(ut(lt,this,e),ut(ft,this,e));}catch(r){ft(this,e,r);}},n=function(t){L(this,{type:N,done:!1,notified:!1,parent:!1,reactions:[],rejection:!1,state:K,value:void 0});},n.prototype=d(z.prototype,{then:function(t,e){var r=M(this),n=B(_(this,z));return n.ok="function"!=typeof t||t,n.fail="function"==typeof e&&e,n.domain=W?G.domain:void 0,r.parent=!0,r.reactions.push(n),r.state!=K&&it(this,r,!1),n.promise},catch:function(t){return this.then(void 0,t)}}),i=function(){var t=new n,e=D(t);this.promise=t,this.resolve=ut(lt,t,e),this.reject=ut(ft,t,e);},C.f=B=function(t){return t===z||t===o?new i(t):U(t)},c||"function"!=typeof l||(a=l.prototype.then,p(l.prototype,"then",(function(t,e){var r=this;return new z((function(t,e){a.call(r,t,e);})).then(t,e)}),{unsafe:!0}),"function"==typeof $&&s({global:!0,enumerable:!0,forced:!0},{fetch:function(t){return T(z,$.apply(u,arguments))}}))),s({global:!0,wrap:!0,forced:et},{Promise:z}),h(z,N,!1,!0),v(N),o=f(N),s({target:N,stat:!0,forced:et},{reject:function(t){var e=B(this);return e.reject.call(void 0,t),e.promise}}),s({target:N,stat:!0,forced:c||et},{resolve:function(t){return T(c&&this===o?z:this,t)}}),s({target:N,stat:!0,forced:rt},{all:function(t){var e=this,r=B(e),n=r.resolve,i=r.reject,o=j((function(){var r=g(e.resolve),o=[],a=0,s=1;k(t,(function(t){var c=a++,u=!1;o.push(void 0),s++,r.call(e,t).then((function(t){u||(u=!0,o[c]=t,--s||n(o));}),i);})),--s||n(o);}));return o.error&&i(o.value),r.promise},race:function(t){var e=this,r=B(e),n=r.reject,i=j((function(){var i=g(e.resolve);k(t,(function(t){i.call(e,t).then(r.resolve,n);}));}));return i.error&&n(i.value),r.promise}});},e893:function(t,e,r){var n=r("5135"),i=r("56ef"),o=r("06cf"),a=r("9bf2");t.exports=function(t,e){for(var r=i(e),s=a.f,c=o.f,u=0;u<r.length;u++){var f=r[u];n(t,f)||s(t,f,c(e,f));}};},e8b5:function(t,e,r){var n=r("c6b6");t.exports=Array.isArray||function(t){return "Array"==n(t)};},e95a:function(t,e,r){var n=r("b622"),i=r("3f8c"),o=n("iterator"),a=Array.prototype;t.exports=function(t){return void 0!==t&&(i.Array===t||a[o]===t)};},f069:function(t,e,r){var n=r("1c0b"),i=function(t){var e,r;this.promise=new t((function(t,n){if(void 0!==e||void 0!==r)throw TypeError("Bad Promise constructor");e=t,r=n;})),this.resolve=n(e),this.reject=n(r);};t.exports.f=function(t){return new i(t)};},f5df:function(t,e,r){var n=r("00ee"),i=r("c6b6"),o=r("b622"),a=o("toStringTag"),s="Arguments"==i(function(){return arguments}()),c=function(t,e){try{return t[e]}catch(r){}};t.exports=n?i:function(t){var e,r,n;return void 0===t?"Undefined":null===t?"Null":"string"==typeof(r=c(e=Object(t),a))?r:s?i(e):"Object"==(n=i(e))&&"function"==typeof e.callee?"Arguments":n};},f772:function(t,e,r){var n=r("5692"),i=r("90e3"),o=n("keys");t.exports=function(t){return o[t]||(o[t]=i(t))};},fb15:function(t,e,r){if(r.r(e),"undefined"!==typeof window){var n=window.document.currentScript,i=r("8875");n=i(),"currentScript"in document||Object.defineProperty(document,"currentScript",{get:i});var o=n&&n.src.match(/(.+\/)[^/]+\.js(\?.*)?$/);o&&(r.p=o[1]);}var a=r("fe83");e["default"]=a["a"];},fc6a:function(t,e,r){var n=r("44ad"),i=r("1d80");t.exports=function(t){return n(i(t))};},fdbc:function(t,e){t.exports={CSSRuleList:0,CSSStyleDeclaration:0,CSSValueList:0,ClientRectList:0,DOMRectList:0,DOMStringList:0,DOMTokenList:1,DataTransferItemList:0,FileList:0,HTMLAllCollection:0,HTMLCollection:0,HTMLFormElement:0,HTMLSelectElement:0,MediaList:0,MimeTypeArray:0,NamedNodeMap:0,NodeList:1,PaintRequestList:0,Plugin:0,PluginArray:0,SVGLengthList:0,SVGNumberList:0,SVGPathSegList:0,SVGPointList:0,SVGStringList:0,SVGTransformList:0,SourceBufferList:0,StyleSheetList:0,TextTrackCueList:0,TextTrackList:0,TouchList:0};},fdbf:function(t,e,r){var n=r("4930");t.exports=n&&!Symbol.sham&&"symbol"==typeof Symbol.iterator;},fe83:function(t,e,r){(function(t){var n=r("28ab"),i=function(t){var e=arguments.length>1&&void 0!==arguments[1]?arguments[1]:"vue-ellipse-progress";return t.component(e,n["a"])},o=null;"undefined"!==typeof window?o=window.Vue:"undefined"!==typeof t&&(o=t.Vue),o&&o.use({install:i}),e["a"]={install:i};}).call(this,r("c8ba"));},fea9:function(t,e,r){var n=r("da84");t.exports=n.Promise;},fec1:function(t,e,r){var n=r("02d4");"string"===typeof n&&(n=[[t.i,n,""]]),n.locals&&(t.exports=n.locals);var i=r("499e").default;i("ec09b49c",n,!0,{sourceMap:!1,shadowMode:!1});}})}));
});

var VueEllipseProgress = unwrapExports(vueEllipseProgress_umd_min);

var progress = {
  install(Vue, _options) {
    Vue.use(VueEllipseProgress);
  }
};

var plugins$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  toast: toast$1,
  vuelidate: vuelidate,
  modal: modal$1,
  download: download,
  progress: progress
});

/* eslint-disable import/namespace */

const locales$1 = {
  'en-US': require('./locales/en-US.json'),
  'es-ES': require('./locales/es-ES.json'),
  'es-MX': require('./locales/es-MX.json'),
  'pt-BR': require('./locales/pt-BR.json'),
  'pt-PT': require('./locales/pt-PT.json'),
  'fr-FR': require('./locales/fr-FR.json')
};

class Toolkit {
  get locale() {
    return config.locale
  }

  set locale(value) {
    config.locale = value;
  }

  t(term, ...args) {
    const locale = locales$1[config.locale] || locales$1['en-US'];
    return get_1(locale, term, term).replace(/{(\d+)}/g, (_match, number) => {
      if (typeof args[number] !== 'undefined') {
        return args[number]
      } else {
        return '{' + number + '}'
      }
    })
  }

  static install(Vue, options = {}) {
    const _map = (module) => {
      return values_1(module).filter(d => d)
    };

    merge_1(config, options);
    _map(components).forEach(d => Vue.component(d.name, d));
    _map(filters).forEach(d => d.install(Vue, options));
    _map(directives).forEach(d => d.install(Vue, options));
    _map(plugins$2).forEach(d => d.install(Vue, options));
    Vue.prototype.$toolkit = new Toolkit();
  }
}

export default Toolkit;
//# sourceMappingURL=me-toolkit.esm.js.map
